/*!
 *
 * Bryntum Scheduler 4.0.8
 *
 * Copyright(c) 2021 Bryntum AB
 * https://bryntum.com/contact
 * https://bryntum.com/license
 *
 */
const productName = 'scheduler';

/**
 * @module Core/helper/BrowserHelper
 */

/**
 * Static helper class that does browser/feature detection.
 * @internal
 */
class BrowserHelper {
  static cacheFlags(platform = navigator.platform, userAgent = navigator.userAgent) {
    const me = this; // os

    me._isLinux = Boolean(platform.match(/Linux/));
    me._isMac = Boolean(platform.match(/Mac/));
    me._isWindows = Boolean(platform.match(/Win32/)); // browser

    me._isIE11 = Boolean(userAgent.match(/rv:11/));
    me._edgeVersion = me.getVersion(userAgent, /Edge?\/(\d+)\./);
    me._isEdge = me._edgeVersion > 0 && me._edgeVersion < 80;

    if (!me._isEdge) {
      me._edgeVersion = 0;
    } // Edge user agent contain webkit too

    me._isWebkit = Boolean(userAgent.match(/WebKit/)) && !me._isEdge;
    me._firefoxVersion = me.getVersion(userAgent, /Firefox\/(\d+)\./);
    me._isFirefox = me._firefoxVersion > 0;
    me._chromeVersion = !me._isEdge ? me.getVersion(userAgent, /Chrom(?:e|ium)\/(\d+)\./) : 0;
    me._isChrome = me._chromeVersion > 0;
    me._isSafari = Boolean(userAgent.match(/Safari/)) && !me._isChrome && !me._isEdge;
    me._isMobileSafari = Boolean(userAgent.match(/Mobile.*Safari/));
    me._isAndroid = Boolean(userAgent.match(/Android/g));

    try {
      document.querySelector(':scope'); // Scoped queries are not supported for custom element polyfill in firefox
      // https://app.assembla.com/spaces/bryntum/tickets/6781

      me.supportsQueryScope = !me._isFirefox;
    } catch (e) {
      me.supportsQueryScope = false;
    }

    me._supportsPassive = false;

    try {
      // If the browser asks the options object to yield its passive
      // property, we know it supports the object form options object
      // and passive listeners.
      document.addEventListener('__notvalid__', null, {
        get passive() {
          me._supportsPassive = true;
        }

      });
    } catch (e) {} //document.addEventListener("DOMContentLoaded", me.onDocumentReady);

  } // NOTE: Not allowed with CSP, moved to Grid#render()
  // /**
  //  * Feature and browser detection which requires the document to be loaded.
  //  * @private
  //  */
  // static onDocumentReady() {
  //     // Firefox includes a part of the Y scroller jutting up which is not needed.
  //     // Also need to "mitre" the horizontal scrollbar to leave the gap at the end.
  //     if (BrowserHelper.isFirefox) {
  //         CSSHelper.insertRule(`.b-virtual-scroller {height: ${DomHelper.scrollBarWidth}px;}`);
  //         CSSHelper.insertRule(`.b-virtual-scrollers {padding-right: ${DomHelper.scrollBarWidth}px;}`);
  //     }
  // }

  /**
   * Yields `true` if the current browser supports CSS style `position:sticky`
   * @property {Boolean}
   * @readonly
   */

  static get supportsSticky() {
    // TODO: remove this when IE11 support is dropped. That's the only non-compliant browser.
    return !this._isIE11;
  }
  /**
   * Returns matched version for userAgent
   * @param String versionRe version match regular expression
   * @returns {Number} matched version
   * @readonly
   * @private
   */

  static getVersion(userAgent, versionRe) {
    const match = userAgent.match(versionRe);
    return match ? parseInt(match[1]) : 0;
  }
  /**
   * Tries to determine if the user is using a touch device
   * @property {Boolean}
   * @readonly
   */

  static get isTouchDevice() {
    if ('_isTouchDevice' in this) return this._isTouchDevice;
    return 'ontouchstart' in window || // edge tends to always have this with a value 2
    !this.isEdge && navigator.maxTouchPoints > 0 || // but if env is actually touchable, then window has this class present
    this.isEdge && window.TouchEvent || navigator.msMaxTouchPoints > 0;
  }

  static get supportsPointerEvents() {
    return Boolean(window.PointerEvent || window.MSPointerEvent);
  } // Since touch screen detection is unreliable we should allow client to configure it, or detect first touch

  static set isTouchDevice(value) {
    this._isTouchDevice = value;
  }
  /**
   * Checks if platform is mac
   * @property {Boolean}
   * @readonly
   */

  static get isMac() {
    return this._isMac;
  }
  /**
   * Checks if platform is windows
   * @property {Boolean}
   * @readonly
   */

  static get isWindows() {
    return this._isWindows;
  }
  /**
   * Checks if platform is linux
   * @property {Boolean}
   * @readonly
   */

  static get isLinux() {
    return this._isLinux;
  }
  /**
   * Checks if browser is IE11
   * @property {Boolean}
   * @readonly
   */

  static get isIE11() {
    return this._isIE11;
  }
  /**
   * Checks if browser is Edge
   * @property {Boolean}
   * @readonly
   */

  static get isEdge() {
    return this._isEdge;
  }
  /**
   * Returns the major Edge versionor 0 for other browsers
   * @property {Number}
   * @readonly
   */

  static get edgeVersion() {
    return this._edgeVersion;
  }
  /**
   * Checks if browser is Webkit
   * @property {Boolean}
   * @readonly
   */

  static get isWebkit() {
    return this._isWebkit;
  }
  /**
   * Checks if browser is Chrome
   * @property {Boolean}
   * @readonly
   */

  static get isChrome() {
    return this._isChrome;
  }
  /**
   * Returns the major Chrome version, or 0 for other browsers
   * @property {Number}
   * @readonly
   */

  static get chromeVersion() {
    return this._chromeVersion;
  }
  /**
   * Checks if browser is Firefox
   * @property {Boolean}
   * @readonly
   */

  static get isFirefox() {
    return this._isFirefox;
  }
  /**
   * Find the major Firefox version (or 0 for other browsers).
   * @property {Number}
   * @readonly
   */

  static get firefoxVersion() {
    return this._firefoxVersion;
  }
  /**
   * Checks if browser is Safari
   * @property {Boolean}
   * @readonly
   */

  static get isSafari() {
    return this._isSafari;
  }
  /**
   * Checks if browser is mobile Safari
   * @property {Boolean}
   * @readonly
   */

  static get isMobileSafari() {
    return this._isMobileSafari;
  }

  static get isAndroid() {
    return this._isAndroid;
  }
  /**
   * Returns `true` if the browser supports passive event listeners.
   * @property {Boolean}
   */

  static get supportsPassive() {
    return this._supportsPassive;
  } // https://developer.mozilla.org/en-US/docs/Web/API/Web_Storage_API/Using_the_Web_Storage_API

  static get storageAvailable() {
    let storage, x;

    try {
      storage = localStorage;
      x = '__storage_test__';
      storage.setItem(x, x);
      storage.removeItem(x);
      return true;
    } catch (e) {
      return e instanceof DOMException && ( // everything except Firefox
      e.code === 22 || // Firefox
      e.code === 1014 || // test name field too, because code might not be present
      // everything except Firefox
      e.name === 'QuotaExceededError' || // Firefox
      e.name === 'NS_ERROR_DOM_QUOTA_REACHED') && // acknowledge QuotaExceededError only if there's something already stored
      storage.length !== 0;
    }
  }

  static setLocalStorageItem(key, value) {
    this.storageAvailable && localStorage.setItem(key, value);
  }

  static getLocalStorageItem(key) {
    return this.storageAvailable && localStorage.getItem(key);
  }

  static removeLocalStorageItem(key) {
    this.storageAvailable && localStorage.removeItem(key);
  }
  /**
   * Returns parameter value from search string by parameter name.
   * @param {String} paramName search parameter name
   * @param {*} [defaultValue] default value if parameter not found
   * @param {String} [search] search string. Defaults to `document.location.search`
   */

  static searchParam(paramName, defaultValue = null, search = document.location.search) {
    const re = new RegExp(`[?&]${paramName}=([^&]*)`),
          match = search.match(re);
    return match && match[1] || defaultValue;
  }

  static getCookie(name) {
    const nameEq = encodeURIComponent(name) + '=',
          cookieItems = document.cookie.split(';');

    for (let i = 0; i < cookieItems.length; i++) {
      let c = cookieItems[i];

      while (c.charAt(0) === ' ') {
        c = c.substring(1, c.length);
      }

      if (c.indexOf(nameEq) === 0) {
        return decodeURIComponent(c.substring(nameEq.length, c.length));
      }
    }

    return '';
  }
  /**
   * Triggers a download of a file with the specified name / URL.
   * @param {String} filename The filename of the file to be downloaded
   * @param {String} [url] The URL where the file is to be downloaded from
   */

  static download(filename, url) {
    const a = document.createElement('a');
    a.download = filename;
    a.href = url || filename;
    a.style.cssText = 'display:none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }
  /**
   * Triggers a download of a Blob with the specified name.
   * @param {Blob} blob The Blob to be downloaded
   * @param {String} filename The filename of the file to be downloaded
   */

  static downloadBlob(blob, filename) {
    const url = window.URL.createObjectURL(blob);
    this.download(filename, url);
    window.URL.revokeObjectURL(url);
  }

  static get queryString() {
    // new URLSearchParams throws in salesforce
    // https://github.com/salesforce/lwc/issues/1812
    const params = new URL(window.location.href).searchParams;
    return Object.fromEntries(params.entries());
  }

  static get isBrowserEnv() {
    return typeof window !== 'undefined';
  }

  static get global() {
    return typeof window !== 'undefined' ? window // eslint-disable-next-line no-undef
    : typeof global !== 'undefined' ? global : globalThis || {};
  }

  static copyToClipboard(code) {
    let success = true;
    const textArea = document.createElement('textarea');
    textArea.value = code;
    textArea.style.height = textArea.style.width = 0;
    document.body.appendChild(textArea);
    textArea.select();

    try {
      document.execCommand('copy');
    } catch (e) {
      success = false;
    }

    textArea.remove();
    return success;
  }

}

if (BrowserHelper.isBrowserEnv) {
  BrowserHelper.cacheFlags();
}

BrowserHelper._$name = 'BrowserHelper';

/**
 * @module Core/helper/VersionHelper
 */

let isSiesta = false;

try {
  isSiesta = Boolean(BrowserHelper.global.parent && BrowserHelper.global.parent.Siesta || typeof process !== 'undefined' && (BrowserHelper.global.StartTest || BrowserHelper.global.Siesta));
} catch (e) {}
/**
 * Helper for version handling
 * @private
 * @example
 *
 * VersionHelper.setVersion('grid', '1.5');
 *
 * if (VersionHelper.getVersion('grid').isNewerThan('1.0')) {
 *   ...
 * }
 */

class VersionHelper {
  /**
   * Set version for specified product
   * @private
   * @param {String} product
   * @param {String} version
   */
  static setVersion(product, version) {
    product = product.toLowerCase();
    VH[product] = {
      version,

      isNewerThan(otherVersion) {
        return otherVersion < version;
      },

      isOlderThan(otherVersion) {
        return otherVersion > version;
      }

    };
    let bundleFor = ''; // Var productName is only defined in bundles, it is internal to bundle so not available on window. Used to
    // tell importing combinations of grid/scheduler/gantt bundles apart from loading same bundle twice
    // eslint-disable-next-line no-undef

    if (typeof productName !== 'undefined') {
      // eslint-disable-next-line no-undef
      bundleFor = productName;
    } // Set "global" flag to detect bundle being loaded twice

    const globalKey = `${bundleFor}.${product}${version.replace(/\./g, '-')}`;

    if (BrowserHelper.isBrowserEnv) {
      if (window.bryntum[globalKey] === true) {
        if (isSiesta) {
          window.BUNDLE_EXCEPTION = true;
        } else {
          throw new Error('Bundle included twice, check cache-busters and file types (.js)');
        }
      } else {
        window.bryntum[globalKey] = true;
      }
    }
  }
  /**
   * Get (previously set) version for specified product
   * @private
   * @param {String} product
   */

  static getVersion(product) {
    product = product.toLowerCase();

    if (!VH[product]) {
      throw new Error('No version specified! Please check that you import VersionHelper right into the class from where you call `deprecate` function.');
    }

    return VH[product].version;
  }
  /**
   * Checks the passed product against the passed version using the passed test.
   * @param {String} product The name of the product to test the version of
   * @param {String} version The version to test against
   * @param {String} test The test operator, `<=`, `<`, `=`, `>` or `>=`.
   * @returns {Boolean} `true` if the test passes.
   * @internal
   */

  static checkVersion(product, version, test) {
    const productVersion = VH.getVersion(product);
    let result;

    switch (test) {
      case '<':
        result = productVersion < version;
        break;

      case '<=':
        result = productVersion <= version;
        break;

      case '=':
        result = productVersion === version;
        break;

      case '>=':
        result = productVersion >= version;
        break;

      case '>':
        result = productVersion > version;
        break;
    }

    return result;
  }
  /**
   * Based on a comparison of current product version and the passed version this method either outputs a console.warn
   * or throws an error.
   * @param {String} product The name of the product
   * @param {String} invalidAsOfVersion The version where the offending code is invalid (when any compatibility layer
   * is actually removed).
   * @param {String} message Required! A helpful warning message to show to the developer using a deprecated API.
   * @internal
   */

  static deprecate(product, invalidAsOfVersion, message) {
    const justWarn = VH.checkVersion(product, invalidAsOfVersion, '<');

    if (justWarn) {
      // During the grace period (until the next major release following the deprecated code), just show a console warning
      console.warn(`Deprecation warning: You are using a deprecated API which will change in v${invalidAsOfVersion}. ${message}`);
    } else {
      throw new Error(`Deprecated API use. ${message}`);
    }
  }

  static get isTestEnv() {
    return isSiesta;
  }

  static get isDebug() {
    // eslint-disable-next-line no-unreachable
    return false;
  }

}
const VH = VersionHelper;

if (BrowserHelper.isBrowserEnv) {
  (window.bryntum || (window.bryntum = {})).getVersion = VH.getVersion.bind(VH);
  window.bryntum.checkVersion = VH.checkVersion.bind(VH);
  window.bryntum.deprecate = VH.deprecate.bind(VH);
}

VersionHelper._$name = 'VersionHelper';

// IMPORTANT - adding imports here can create problems for Base class
/**
 * @module Core/helper/StringHelper
 */

let charsToEncode, entitiesToDecode, htmlEncodeRe, htmlDecodeRe;

const camelLettersRe = /([a-z])([A-Z])/g,
      escapeRegExpRe = /[.*+?^${}()|[\]\\]/g,
      // same as NPM escape-string-regexp
htmlRe = /[&<]/,
      idRe = /(^[^a-z]+|[^\w]+)/gi,
      whiteSpaceRe = /\s+/,
      htmlDecoder = (m, captured) => entitiesToDecode[captured.toLowerCase()] || String.fromCharCode(parseInt(captured.substr(2), 10)),
      htmlEncoder = (m, captured) => charsToEncode[captured],
      replaceCamelLetters = (all, g1, g2) => {
  return g1 + '-' + g2.toLowerCase();
},
      replaceNonIdChar = c => {
  if (c) {
    return `_x${c.charCodeAt(0).toString(16)}`;
  }

  return '__blank__';
},
      hyphenateCache = {};
/**
 * Helper for string manipulation.
 */

class StringHelper {
  /**
   * Capitalizes the first letter of a string, "myString" -> "MyString".
   * @param {String} string The string to capitalize
   * @returns {String} The capitalized string or the value of `string` if falsy.
   * @category String formatting
   */
  static capitalize(string) {
    return string && string[0].toUpperCase() + string.substr(1);
  }
  /**
   * Capitalizes the first letter of a string, "myString" -> "MyString".
   * If the parameter is falsy, `null` is returned.
   * @param {String} string The string to capitalize
   * @returns {String} The capitalized string or `null` if `string` is falsy.
   * @deprecated 4.0 Use {@link #function-capitalize} instead.
   * @category String formatting
   */

  static capitalizeFirstLetter(string) {
    VersionHelper.deprecate('Core', '5.0.0', 'Deprecated in favor of `capitalize()`, will be removed in 5.0');

    if (!string) {
      return null;
    }

    return string[0].toUpperCase() + string.substr(1);
  }
  /**
   * This method decodes HTML entities and returns the original HTML.
   *
   * See also {@link #function-encodeHtml-static}.
   * @param {String} text
   * @returns {String}
   * @category HTML
   */

  static decodeHtml(text) {
    return text ? String(text).replace(htmlDecodeRe, htmlDecoder) : text;
  }
  /**
   * This method encodes HTML entities and returns a string that can be placed in the document and produce the
   * original text rather than be interpreted as HTML. Using this method with user-entered values prevents those
   * values from executing as HTML (i.e., a cross-site scripting or "XSS" security issue).
   *
   * See also {@link #function-decodeHtml-static}.
   * @param {String} html
   * @returns {String}
   * @category HTML
   */

  static encodeHtml(html) {
    return html ? String(html).replace(htmlEncodeRe, htmlEncoder) : html;
  }
  /**
   * Returns `true` if the provided `text` contains special HTML characters.
   * @param {String} text
   * @returns {Boolean}
   * @category HTML
   */

  static isHtml(text) {
    return typeof text === 'string' && htmlRe.test(text || '');
  }
  /**
   * Makes the first letter of a string lowercase, "MyString" -> "myString".
   * If the parameter is falsy, `null` is returned.
   * @param {String} string The string to lowercase.
   * @returns {String} The lowercased string or `null` if `string` is falsy.
   * @deprecated 4.0 Use {@link #function-uncapitalize} instead.
   * @category String formatting
   */

  static lowercaseFirstLetter(string) {
    VersionHelper.deprecate('Core', '5.0.0', 'Deprecated in favor of `uncapitalize()`, will be removed in 5.0');

    if (!string) {
      return null;
    }

    return string[0].toLowerCase() + string.substr(1);
  }
  /**
   * Makes the first letter of a string lowercase, "MyString" -> "myString".
   * @param {String} string The string to un-capitalize.
   * @returns {String} The un-capitalized string or the value of `string` if falsy.
   * @category String formatting
   */

  static uncapitalize(string) {
    return string && string[0].toLowerCase() + string.substr(1);
  }
  /**
   * Converts the passed camelCased string to a hyphen-separated string. eg "minWidth" -> "min-width"
   * @param {String} string The string to convert.
   * @return {String} The string with adjoining lower and upper case letters
   * separated by hyphens and converted to lower case.
   * @category String formatting
   */

  static hyphenate(string) {
    // Cached since it is used heavily with DomHelper.sync()
    const cached = hyphenateCache[string];

    if (cached) {
      return cached;
    }

    return hyphenateCache[string] = string.replace(camelLettersRe, replaceCamelLetters);
  }
  /**
   * Initializes HTML entities used by {@link #function-encodeHtml-static} and {@link #function-decodeHtml-static}.
   * @param {Object} [mappings] An object whose keys are characters that should be encoded and values are the HTML
   * entity for the character.
   * @private
   */

  static initHtmlEntities(mappings) {
    mappings = mappings || {
      '&': '&amp;',
      '>': '&gt;',
      '<': '&lt;',
      '"': '&quot;',
      "'": '&#39;'
    };
    const chars = Object.keys(mappings); // Maps '<' to '&lt;'

    charsToEncode = mappings; // Inverts the mapping so we can convert '&lt;' to '<'

    entitiesToDecode = chars.reduce((prev, val) => {
      prev[mappings[val]] = val;
      return prev;
    }, {}); // Creates a regex char set like /([<&>])/g to match the characters that need to be encoded (escaping any of
    // the regex charset special chars '[', ']' and '-'):

    htmlEncodeRe = new RegExp(`([${chars.map(c => '[-]'.includes(c) ? '\\' + c : c).join('')}])`, 'g'); // Creates a regex like /(&lt;|&amp;|&gt;)/ig to match encoded entities... good news is that (valid) HTML
    // entities do not contain any regex special characters:

    htmlDecodeRe = new RegExp(`(${Object.values(mappings).join('|')}|&#[0-9]+;)`, 'ig');
  }
  /**
   * Parses JSON inside a try-catch block. Returns null if the string could not be parsed.
   *
   * @param {String} string String to parse
   * @returns {Object} Resulting object or null if parse failed
   * @category JSON
   */

  static safeJsonParse(string) {
    let parsed = null;

    try {
      parsed = JSON.parse(string);
    } catch (e) {}

    return parsed;
  }
  /**
   * Stringifies an object inside a try-catch block. Returns null if an exception is encountered.
   *
   * See [JSON.stringify on MDN](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/JSON/stringify)
   * for more information on the arguments.
   *
   * @param {Object} object The object to stringify
   * @param {Function|String[]|Number[]} [replacer] A function or array of string/number used to determine properties
   * to include in the JSON string
   * @param {String|Number} [space] Number of spaces to indent or string used as whitespace
   * @returns {Object} Resulting object or null if stringify failed
   * @category JSON
   */

  static safeJsonStringify(object, replacer = null, space = null) {
    let result = null;

    try {
      result = JSON.stringify(object, replacer, space);
    } catch (e) {}

    return result;
  }
  /**
   * Creates an alphanumeric identifier from any passed string. Encodes spaces and non-alpha characters.
   * @param {String} inString The string from which to strip non-identifier characters.
   * @return {String}
   * @category Misc
   */

  static createId(inString) {
    return String(inString).replace(idRe, replaceNonIdChar);
  }

  static escapeRegExp(string, flags) {
    // $& means the whole matched string
    let ret = string.replace(escapeRegExpRe, '\\$&');

    if (flags !== undefined) {
      ret = new RegExp(ret, flags);
    }

    return ret;
  }
  /**
   * Joins all given paths together using the separator as a delimiter and normalizes the resulting path.
   * @param paths {Array} array of paths to join
   * @param pathSeparator [{String}] path separator. Default value is '/'
   * @return {String}
   * @category Misc
   */

  static joinPaths(paths, pathSeparator = '/') {
    return paths.join(pathSeparator).replace(new RegExp('\\' + pathSeparator + '+', 'g'), pathSeparator);
  }
  /**
   * Returns the provided string split on whitespace. If the string is empty or consists of only whitespace, the
   * returned array will be empty. If `str` is not a string, it is simply returned. This allows `null` or already
   * split strings (arrays) to be passed through.
   *
   * For example:
   * ```
   *  console.log(StringHelper.split(' abc def xyz   '));
   *  > ['abc', 'def', 'xyz']
   *  console.log(StringHelper.split(''));
   *  > []
   * ```
   * Compare to the standard `split()` method:
   * ```
   *  console.log(' abc def xyz   '.split(/\s+/));
   *  > ['', 'abc', 'def', 'xyz', '']
   *  console.log(''.split(/\s+/));
   *  > ['']
   * ```
   * @param {String} str
   * @param {String|RegExp} delimiter
   * @returns {String[]}
   * @category Misc
   */

  static split(str, delimiter = whiteSpaceRe) {
    let ret = str;

    if (typeof ret === 'string') {
      ret = str.trim(); // w/o trim() whitespace on the ends will give us '' in the array

      ret = ret ? ret.split(delimiter) : []; // also ''.split() = ['']
    }

    return ret;
  }
  /**
   * This is a tagged template function that performs HTML encoding on replacement values to avoid XSS (Cross-Site
   * Scripting) attacks.
   *
   * For example:
   *
   * ```javascript
   *  eventRenderer(eventRecord) {
   *      return StringHelper.xss`<span class="${eventRecord.attrib}">${eventRecord.name}</span>`;
   *  }
   * ```
   */

  static xss(strings, ...values) {
    const buf = [];
    let i = values.length;
    buf[i] = strings[i];

    while (i-- > 0) {
      buf[i] = strings[i] + StringHelper.encodeHtml(values[i]);
    }

    return buf.join('');
  }

}
StringHelper.initHtmlEntities();
StringHelper._$name = 'StringHelper';

// IMPORTANT - adding imports here can create problems for Base class
const {
  toString
} = Object.prototype,
      {
  isFrozen
} = Object,
      typeNameRe = /\[object ([^\]]+)]/,
      typeCache = {},
      emptyObject = Object.freeze({});
/**
 * @module Core/helper/util/Objects
 */
// Static methods are not displayed in derived class documentation. Therefore, since this is an internal class, the
// work around is to copy method documentation to ObjectHelper (the public interface). Also tried making ObjectHelper
// a singleton.

/**
 * Helper for low-level Object manipulation.
 *
 * While documented on {@link Core.helper.ObjectHelper}, the following static methods are implemented by this class:
 *
 * - `{@link Core.helper.ObjectHelper#function-assign-static}`
 * - `{@link Core.helper.ObjectHelper#function-assignIf-static}`
 * - `{@link Core.helper.ObjectHelper#function-clone-static}`
 * - `{@link Core.helper.ObjectHelper#function-createTruthyKeys-static}`
 * - `{@link Core.helper.ObjectHelper#function-getPath-static}`
 * - `{@link Core.helper.ObjectHelper#function-getTruthyKeys-static}`
 * - `{@link Core.helper.ObjectHelper#function-getTruthyValues-static}`
 * - `{@link Core.helper.ObjectHelper#function-isEmpty-static}`
 * - `{@link Core.helper.ObjectHelper#function-isObject-static}`
 * - `{@link Core.helper.ObjectHelper#function-merge-static}`
 * - `{@link Core.helper.ObjectHelper#function-setPath-static}`
 * - `{@link Core.helper.ObjectHelper#function-typeOf-static}`
 * @internal
 */

class Objects {
  static assign(dest, ...sources) {
    for (let source, key, i = 0; i < sources.length; i++) {
      source = sources[i];

      if (source) {
        for (key in source) {
          dest[key] = source[key];
        }
      }
    }

    return dest;
  }

  static assignIf(dest, ...sources) {
    for (let source, key, i = 0; i < sources.length; i++) {
      source = sources[i];

      if (source) {
        for (key in source) {
          if (!(key in dest) || dest[key] === undefined) {
            dest[key] = source[key];
          }
        }
      }
    }

    return dest;
  }

  static clone(value, handler) {
    let cloned = value,
        key;

    if (value && typeof value === 'object') {
      if (O.isObject(value)) {
        cloned = {};

        for (key in value) {
          cloned[key] = O.clone(value[key]);
        }
      } else if (Array.isArray(value)) {
        cloned = []; // Loop backwards to:
        //  1. read source.length once
        //  2. get result array sized on first pass (avoid growing)

        for (key = value.length; key-- > 0;)
        /* empty */
        {
          cloned[key] = O.clone(value[key]);
        }
      } else if (O.isDate(value)) {
        cloned = new Date(value.getTime());
      } else if (handler) {
        // Allow other types to be handled (e.g., DOM nodes)
        cloned = handler(value);
      }
    }

    return cloned;
  }

  static createTruthyKeys(source) {
    const keys = StringHelper.split(source),
          result = keys && {};

    if (keys) {
      for (const key of keys) {
        // StringHelper.split won't return empty keys if passed a string, but we
        // could have been passed a String[]
        if (key) {
          result[key] = true;
        }
      }
    }

    return result;
  }
  /**
   * Returns value for a given path in the object
   * @param {Object} object Object to check path on
   * @param {String} path Dot-separated path, e.g. 'object.childObject.someKey'
   * @returns {*} Value associated with passed key
   */

  static getPath(object, path) {
    return path.split('.').reduce((result, key) => {
      return (result || emptyObject)[key];
    }, object);
  }
  /**
   * Returns value for a given path in the object, placing a passed default value in at the
   * leaf property and filling in undefined properties all the way down.
   * @param {Object} object Object to get path value for.
   * @param {String|Number|String[]|Number[]} path Dot-separated path, e.g. 'firstChild.childObject.someKey',
   * or the key path as an array, e.g. ['firstChild', 'childObject', 'someKey'].
   * @param {*} [defaultValue] Optionally the value to put in as the `someKey` property.
   * @returns {*} Value at the leaf position of the path.
   */

  static getPathDefault(object, path, defaultValue) {
    const keys = Array.isArray(path) ? path : typeof path === 'string' ? path.split('.') : [path],
          length = keys.length - 1;
    return keys.reduce((result, key, index) => {
      if (defaultValue && !(key in result)) {
        // Can't use emptyObject here, we are creating a node in the object tree
        result[key] = index === length ? defaultValue : {};
      }

      return (result || emptyObject)[key];
    }, object);
  }
  /**
   * Determines if the specified path exists
   * @param {Object} object Object to check path on
   * @param {String} path Dot-separated path, e.g. 'object.childObject.someKey'
   * @returns {Boolean}
   */

  static hasPath(object, path) {
    return path.split('.').every(key => {
      if (key in object) {
        object = object[key];
        return true;
      }

      return false;
    });
  }

  static getTruthyKeys(source) {
    const keys = Object.keys(source);

    for (let i = keys.length; i-- > 0;)
    /* empty */
    {
      if (!source[keys[i]]) {
        keys.splice(i, 1);
      }
    }

    return keys;
  }

  static getTruthyValues(source) {
    const keys = Object.keys(source);

    for (let i = keys.length; i-- > 0;)
    /* empty */
    {
      if (source[keys[i]]) {
        keys[i] = source[keys[i]];
      } else {
        keys.splice(i, 1);
      }
    }

    return keys;
  }

  static isClass(object) {
    var _object$prototype;

    if (typeof object === 'function' && ((_object$prototype = object.prototype) === null || _object$prototype === void 0 ? void 0 : _object$prototype.constructor) === object) {
      // TODO find a way to differentiate function(){} from class{}
      return true;
    }

    return false;
  }

  static isDate(object) {
    // A couple quick rejections but only sure way is typeOf:
    return Boolean(object && object.getUTCDate) && O.typeOf(object) === 'date';
  }

  static isPromise(object) {
    if (Promise && Promise.resolve) {
      return Promise.resolve(object) === object;
    } else {
      throw new Error('Promise not supported in your environment');
    }
  }

  static isEmpty(object) {
    if (object && typeof object === 'object') {
      // noinspection LoopStatementThatDoesntLoopJS
      for (const p in object) {
        // eslint-disable-line no-unused-vars
        return false;
      }
    }

    return true;
  }

  static isObject(value) {
    const C = value && value.constructor;
    return Boolean(C // Most things have a .constructor property
    ? // An in-frame instance of Object
    C === Object || // Detect cross-frame objects, but exclude instance of custom classes named Object. typeOf(value) is
    // "object" even for instances of a class and typeOf(C) is "function" for all constructors. We'll have
    // to settle for relying on the fact that getPrototypeOf(Object.prototype) === null.
    // NOTE: this issue does come up in Scheduler unit tests at least.
    // NOTE: IE11 does not have C.name === 'Object' for cross-frame objects! So instead of that check we
    //  do a presence check for a method unlikely to be found elsewhere.
    C.getPrototypeOf && C.prototype && !Object.getPrototypeOf(C.prototype) : // Since all classes have a constructor property, an object w/o one is likely from Object.create(null). Of
    // course, primitive types do not have ".constructor"
    value && typeof value === 'object');
  }

  static merge(dest, ...sources) {
    for (let destValue, source, key, value, i = 0; i < sources.length; i++) {
      source = sources[i];

      if (source) {
        for (key in source) {
          value = source[key];

          if (value && O.isObject(value)) {
            destValue = dest[key];

            if (destValue && O.isObject(destValue)) {
              if (isFrozen(destValue)) {
                dest[key] = destValue = O.clone(destValue);
              }

              O.merge(destValue, value);
            } else {
              dest[key] = isFrozen(value) ? value : O.clone(value);
            }
          } else {
            dest[key] = value;
          }
        }
      }
    }

    return dest;
  }
  /**
   * Sets value for a given path in the object
   * @param {Object} object Target object
   * @param {String} path Dot-separated path, e.g. 'object.childObject.someKey'
   * @param {*} value Value for a given path
   * @returns {Object} Returns passed object
   */

  static setPath(object, path, value) {
    path.split('.').reduce((result, key, index, array) => {
      const isLast = index === array.length - 1;

      if (isLast) {
        return result[key] = value;
      } else if (!(result[key] instanceof Object)) {
        result[key] = {};
      }

      return result[key];
    }, object);
    return object;
  }

  static typeOf(value) {
    let type = typeof value,
        match,
        trueType; // If not atomic type, we handle date or null

    if (type === 'object') {
      if (value === null) {
        type = 'null';
      } else {
        trueType = toString.call(value);

        if (!(type = typeCache[trueType])) {
          match = typeNameRe.exec(trueType);
          typeCache[trueType] = type = match ? match[1].toLowerCase() : trueType;
        }
      }
    } // NaN is the only value that is !== to itself
    else if (value !== value) {
        // eslint-disable-line no-self-compare
        type = 'nan';
      }

    return type;
  }

}
const O = Objects;
Objects._$name = 'Objects';

//  ObjectHelper -> DateHelper -> LocaleManager -> Base -> us

/**
 * @module Core/Config
 */

const {
  defineProperty,
  getOwnPropertyDescriptor
} = Reflect,
      {
  hasOwnProperty: hasOwnProperty$1,
  toString: toString$1
} = Object.prototype,
      instancePropertiesSymbol = Symbol('instanceProperties'),
      configuringSymbol = Symbol('configuring'),
      lazyConfigValues = Symbol('lazyConfigValues'),
      DATE_TYPE = toString$1.call(new Date()),
      whitespace = /\s+/,
      createClsProps = (result, cls) => {
  result[cls] = 1;
  return result;
};
/**
 * This class holds the description of a config property. Only one instance of this class is needed for each config
 * name (e.g., "text"). If config options are supplied, however, they also contribute to the cached instance.
 *
 * Instances should always be retrieved by calling `Config.get()`.
 *
 * The **Configs** of this class correspond to `options` that can be supplied to the `get()` method. These affect the
 * behavior of the config property in some way, as descried by their respective documentation.
 *
 * This class is not used directly.
 *
 * ## The Setter
 * The primary functionality provided by `Config` is its standard setter. This setter function ensures consistent
 * behavior when modifying config properties.
 *
 * The standard setter algorithm is as follows (using the `'text'` config for illustration):
 *
 *  - If the class defines a `changeText()` method, call it passing the new value and the current value:
 *    `changeText(newText, oldText)`.<br>
 *    Then:
 *    * If `changeText()` exits without returning a value (i.e., `undefined`), exit and do nothing further. The
 *      assumption is that the changer method has done all that is required.
 *    * Otherwise, the return value of `changeText()` replaces the incoming value passed to the setter.
 *  - If the new value (or the value returned by `changeText()`) is `!==` to the current value:
 *    * Update the stored config value in `this._text`.
 *    * If the class defines an `updateText()` method, call it passing the new value and the previous value.
 *      `updateText(newText, oldText)`
 *    * If the class defines an `onConfigChange()` method, call it passing an object with the following properties:
 *        - `name` - The config's name
 *        - `value` - The new value
 *        - `was` - The previous value
 *        - `config` - The `Config` instance.
 *
 * NOTE: unlike `changeText()` and `updateText()`, the name of the `onConfigChange()` method is unaffected by the
 * config's name.
 *
 * @internal
 */

class Config {
  /**
   * Returns the `Config` instance for the given `name` and `options`.
   * @param {String} name The name of the config (e.g., 'text' for the text config).
   * @param {Object} [options] Config behavior options.
   * @returns {Core.Config}
   * @internal
   */
  static get(name, options) {
    const {
      cache
    } = this,
          baseCfg = cache[name] || (cache[name] = new Config(name));
    let cfg = baseCfg,
        key;

    if (options) {
      key = Config.makeCacheKey(name, options);

      if (!(cfg = key && cache[key])) {
        cfg = baseCfg.extend(options);

        if (key) {
          cache[key] = cfg;
        }
      }
    }

    return cfg;
  }

  constructor(name) {
    const me = this,
          cap = name[0].toUpperCase() + name.substr(1);
    me.base = me; // so extend()ed configs have a link to the base definition

    me.name = name;
    me.field = '_' + name;
    me.capName = cap;
    me.changer = 'change' + cap;
    me.initializing = 'initializing' + cap;
    me.updater = 'update' + cap;
  }
  /**
   * The descriptor to use with `Reflect.defineProperty()` for defining this config's getter and setter.
   * @property {Object}
   * @private
   */

  get descriptor() {
    let descriptor = this._descriptor;

    if (!descriptor || !hasOwnProperty$1.call(this, '_descriptor')) {
      // lazily make the descriptor
      this._descriptor = descriptor = this.makeDescriptor();
    }

    return descriptor;
  }
  /**
   * The descriptor to use with `Reflect.defineProperty()` for defining this config's initter.
   * @property {Object}
   * @private
   */

  get initDescriptor() {
    let descriptor = this._initDescriptor;

    if (!descriptor || !hasOwnProperty$1.call(this, '_initDescriptor')) {
      // lazily make the descriptor
      this._initDescriptor = descriptor = this.makeInitter();
    }

    return descriptor;
  }
  /**
   * This method compares two values for semantic equality. By default, this is based on the `===` operator. This
   * is often overridden for configs that accept `Date` or array values.
   * @param {*} value1
   * @param {*} value2
   * @returns {Boolean}
   * @internal
   */

  equal(value1, value2) {
    return value1 === value2;
  }
  /**
   * Extends this config with a given additional set of options. These objects are just prototype extensions of this
   * instance.
   * @param {Object} options
   * @returns {Core.Config}
   * @internal
   */

  extend(options) {
    const cfg = Object.assign(Object.create(this), options),
          {
      equal,
      merge
    } = options,
          {
      equalityMethods
    } = Config;

    if (typeof equal === 'string') {
      if (equal.endsWith('[]')) {
        cfg.equal = Config.makeArrayEquals(equalityMethods[equal.substr(0, equal.length - 2)]);
      } else {
        cfg.equal = equalityMethods[equal];
      }
    }

    if (typeof merge === 'string') {
      // Base uses { merge : 'replace' } for defaultConfig properties
      cfg.merge = Config.mergeMethods[merge];
    }

    return cfg;
  }
  /**
   * Defines the property on a given target object via `Reflect.defineProperty()`. If the object has its own getter,
   * it will be preserved. It is invalid to define a setter.
   * @param {Object} target
   * @internal
   */

  define(target) {
    const existing = getOwnPropertyDescriptor(target, this.name);
    let descriptor = this.descriptor;

    if (existing && existing.get) {
      descriptor = Object.assign({}, descriptor);
      descriptor.get = existing.get;
    }

    defineProperty(target, this.name, descriptor);
  }
  /**
   * Defines the property initter on the `target`. This is a property getter/setter that propagates the configured
   * value when the property is read.
   * @param {Object} target
   * @param {*} value
   * @internal
   */

  defineInitter(target, value) {
    const {
      name
    } = this,
          properties = target[instancePropertiesSymbol];
    let lazyValues, prop; // If there is an existing property with a getter/setter, *not* a value
    // defined on the object for this config we must call it in our injected getter/setter.

    if (!properties[name] && (
    /* assign */
    prop = getOwnPropertyDescriptor(target, name)) && !('value' in prop)) {
      properties[name] = prop;
    } // Set up a temporary instance property which will pull in the value from the initialConfig if the getter
    // is called first.

    defineProperty(target, name, this.initDescriptor);

    if (this.lazy) {
      lazyValues = target[lazyConfigValues] || (target[lazyConfigValues] = {
        $counter_: 0
      });
      lazyValues[name] = value;
      ++lazyValues.$counter_;
    }
  }
  /**
   * Returns an equality function for arrays of a base type, for example `'date'`.
   * @param {Function} [fn] The function to use to compare array elements. By default, operator `===` is used.
   * @returns {Function}
   * @private
   */

  static makeArrayEquals(fn) {
    return (value1, value2) => {
      let i,
          equal = value1 && value2 && value1.length === (i = value2.length);

      if (equal && Array.isArray(value1) && Array.isArray(value2)) {
        if (fn) {
          while (equal && i-- > 0) {
            equal = fn(value1[i], value2[i]);
          }
        } else {
          while (equal && i-- > 0) {
            equal = value1[i] === value2[i];
          }
        }
      } else {
        equal = fn ? fn(value1, value2) : value1 === value2;
      }

      return equal;
    };
  }
  /**
   * Returns the key to use in the Config `cache`.
   * @param {String} name The name of the config property.
   * @param {Object} options The config property options.
   * @returns {String}
   * @private
   */

  static makeCacheKey(name, options) {
    const keys = Object.keys(options).sort();

    for (let key, type, value, i = keys.length; i-- > 0;)
    /* empty */
    {
      value = options[key = keys[i]];

      if (value == null && value === false) {
        keys.splice(i, 1);
      } else {
        type = typeof value;

        if (type === 'function') {
          return null;
        }

        if (type === 'string') {
          keys[i] = `${key}:"${value}"`;
        } else if (type === 'number') {
          keys[i] = `${key}:${value}`;
        } // that leaves bool and object, but there are no (valid) config options that are objects... so ignore

      }
    }

    return keys.length ? `${name}>${keys.join('|')}` : name; // eg: 'text>render|merge:v => v|bar'
  }
  /**
   * Creates and returns a property descriptor for this config suitable to be passed to `Reflect.defineProperty()`.
   * @returns {Object}
   * @private
   */

  makeDescriptor() {
    const config = this,
          {
      base,
      field,
      changer,
      updater,
      name
    } = config;

    if (base !== config && base.equal === config.equal) {
      // At present only the equal option affects the setter, so all configs can share the
      // descriptor of the base-most config definition unless their equality test fns differ.
      return base.descriptor;
    }

    return {
      get() {
        return this[field];
      },

      set(value) {
        const me = this;
        let was = me[field]; // Resolve values starting with 'up.' by traversing owners to find it

        if (typeof value === 'string' && value.startsWith('up.')) {
          const ownerValue = me.resolveProperty(value.substr(3)); // TODO: Ignoring functions for now, we need to handle scope to make those work

          if (typeof ownerValue !== 'function') {
            value = ownerValue;
          }
        } // If the "changeTitle()" fellow falls off the end, it must have changed all the needful things.
        // Otherwise, it returned the final config value (it may have changed it instead, for example, making
        // an instance from a config object).

        if (me[changer]) {
          value = me[changer](value, was);

          if (value === undefined) {
            return;
          }

          was = me[field]; // in case it was modified by the changer fn...
        } // inline the default equal() for better perf:

        if (config.equal === equal ? was !== value : !config.equal(was, value)) {
          me[field] = value; // Check for a "syncTitle()" method and call it if present.

          if (me[updater]) {
            me[updater](value, was);
          }

          if (!me.onConfigChange.$nullFn) {
            me.onConfigChange({
              name,
              value,
              was,
              config
            });
          }
        }
      }

    };
  }
  /**
   * Creates and returns a property descriptor for this config's initter suitable to pass to
   * `Reflect.defineProperty()`.
   * @returns {Object}
   * @private
   */

  makeInitter() {
    const config = this;

    if (config !== config.base) {
      if (config.lazy) {
        return config.makeLazyInitter();
      } // At present no other options affect the setter, so all configs can share the descriptor of the base-most
      // config definition.

      return config.base.initDescriptor;
    }

    return config.makeBasicInitter();
  }

  makeBasicInitter() {
    const config = this,
          {
      initializing,
      name
    } = config;
    return {
      configurable: true,

      get() {
        const me = this;
        config.removeInitter(me); // Set the value from the configuration.

        me[initializing] = true;
        me[name] = me[configuringSymbol][name];
        me[initializing] = false; // The property has been *pulled* from the configuration.
        // Prevent the setting loop in configure from setting it again.

        me.configDone[name] = true; // Finally, allow the prototype getter to return the value.

        return me[name];
      },

      set(value) {
        config.removeInitter(this); // The config has been set (some internal code may have called the setter)
        // so prevent it from being called again and overwritten with data from initialConfig.

        this.configDone[name] = true; // Set the property normally (Any prototype setter will be invoked)

        this[name] = value;
      }

    };
  }

  makeLazyInitter() {
    const config = this,
          {
      initializing,
      name
    } = config;
    return {
      configurable: true,

      get() {
        const me = this,
              value = me[lazyConfigValues][name];
        config.removeInitter(me); // Set the value from the lazy config object.

        me[initializing] = true;
        me[name] = value;
        me[initializing] = false; // Finally, allow the prototype getter to return the value.

        return me[name];
      },

      set(value) {
        config.removeInitter(this); // Set the property normally (Any prototype setter will be invoked)

        this[name] = value;
      }

    };
  }
  /**
   * Removes the property initter and restores the instance to its original form.
   * @param {Object} instance
   * @private
   */

  removeInitter(instance) {
    const {
      name
    } = this,
          instanceProperty = instance[instancePropertiesSymbol][name]; // If we took over from an instance property, replace it

    if (instanceProperty) {
      defineProperty(instance, name, instanceProperty);
    } // Otherwise just delete the instance property who's getter we are in.
    else {
        delete instance[name];
      }

    if (this.lazy) {
      if (! --instance[lazyConfigValues].$counter_) {
        delete instance[lazyConfigValues];
      }
    }
  }
  /**
   * This method combines (merges) two config values. This is called in two cases:
   *
   *  - When a derived class specifies the value of a config defined in a super class.
   *  - When a value is specified in the instance config object.
   *
   * @param {*} newValue In the case of derived classes, this is the config value of the derived class. In the case
   * of the instance config, this is the instance config value.
   * @param {*} currentValue In the case of derived classes, this is the config value of the super class. In the case
   * of the instance config, this is the class config value.
   * @param {Object} meta The class meta object from which the `newValue` is coming. This parameter is `null` if the
   * `newValue` is from an instance configuration.
   * @returns {*}
   * @internal
   */

  merge(newValue, currentValue) {
    if (currentValue && newValue && Objects.isObject(currentValue) && Objects.isObject(newValue)) {
      newValue = Objects.merge(Objects.clone(currentValue), newValue);
    }

    return newValue;
  }

}
const {
  prototype
} = Config,
      {
  equal
} = prototype;
Config.symbols = {
  configuring: configuringSymbol,
  instanceProperties: instancePropertiesSymbol
};
/**
 * This object holds `Config` instances keyed by their name. For example:
 * ```
 *  Config.cache = {
 *      disabled : Config.get('disabled'),
 *      text     : Config.get('text'),
 *      title    : Config.get('title')
 *  };
 * ```
 * @member {Object} cache
 * @static
 * @private
 */

Config.cache = Object.create(null); // object w/no properties not even inherited ones

/**
 * This object holds config value equality methods. By default, the `===` operator is used to compare config values for
 * semantic equality. When an `equal` option is specified as a string, that string is used as a key into this object.
 *
 * All equality methods in this object have the same signature as the {@link #function-equal equal()} method.
 *
 * This object has the following merge methods:
 *
 * - `array` : Compares arrays of values using `===` on each element.
 * - `date` : Compares values of `Date` type.
 * - `strict` : The default equal algorithm based on `===` operator.
 * @member {Object} equalityMethods
 * @static
 * @private
 */

Config.equalityMethods = {
  array: Config.makeArrayEquals(),

  date(value1, value2) {
    if (value1 === value2) {
      return true;
    } // see DateHelper.isDate() but cannot import due to circularity

    if (value1 && value2 && toString$1.call(value1) === DATE_TYPE && toString$1.call(value2) === DATE_TYPE) {
      // https://jsbench.me/ltkb3vk0ji/1 - getTime is >2x faster vs valueOf/Number/op+
      return value1.getTime() === value2.getTime();
    }

    return false;
  },

  strict: Config.equal = equal
};
/**
 * This object holds config value merge methods. By default, {@link Core.helper.ObjectHelper#function-merge-static} is
 * used to merge object's by their properties. Config merge methods are used to combine config values from derived
 * classes with config values from super classes, as well as instance config values with those of the class.
 *
 * All merge methods in this object have the same signature as the {@link #function-merge merge()} method.
 *
 * This object has the following merge methods:
 *
 * - `distinct`  : Combines arrays of values ensuring that no value is duplicated.
 * - `merge`     : The default merge algorithm for `configurable()` properties, based on
 *   {@link Core.helper.ObjectHelper#function-merge-static}.
 * - `objects`   : The same as to `merge` except this method promotes `true` to an empty object.
 * - 'classList' : Incoming strings are propted to an object where the string is a property name with a truthy value.
 * - `replace`   : Always returns `newValue` to replace the super class value with the derived class value, or the
 *   class value with the instance value.
 * @member {Object} mergeMethods
 * @static
 * @private
 */

Config.mergeMethods = {
  distinct(newValue, oldValue) {
    const ret = oldValue ? oldValue.slice() : [];

    if (newValue != null) {
      (Array.isArray(newValue) ? newValue : [newValue]).forEach(v => !ret.includes(v) && ret.push(v));
    }

    return ret;
  },

  merge: Config.merge = prototype.merge,

  classList(newValue, oldValue) {
    // 'foo bar' -> { foo : 1, bar : 1 }
    if (typeof newValue === 'string') {
      if (!newValue.length) {
        return oldValue;
      }

      newValue = newValue.split(whitespace).reduce(createClsProps, {});
    }

    return Config.merge(newValue, oldValue);
  },

  objects(newValue, oldValue) {
    return newValue === true ? oldValue || {} : Config.merge(newValue, oldValue);
  },

  replace(newValue) {
    return newValue;
  }

};
Object.assign(prototype, {
  _descriptor: null,
  _initDescriptor: null,

  /**
   * A function that compares values for equality. This test is used to determine if the `update` method should be
   * called when the setter is invoked.
   *
   * To handle `Date` values:
   * ```
   *  class Foo extends Base {
   *      static get configurable() {
   *          return {
   *              date : {
   *                  $config : {
   *                      equal : 'date'
   *                  },
   *
   *                  value : null
   *              }
   *          }
   *      }
   *
   *      updateDate(date) {
   *          // date has changed
   *      }
   *  }
   * ```
   *
   * Also useful for some configs:
   * ```
   *  class Foo extends Base {
   *      static get configurable() {
   *          return {
   *              bar : {
   *                  $config : {
   *                      equal : ObjectHelper.isEqual
   *                  },
   *
   *                  value : null
   *              }
   *          }
   *      }
   *
   *      updateBar(value) {
   *          // value has changed
   *      }
   *  }
   * ```
   * @config {Function} equal
   * @internal
   */

  /**
   * Indicates that this config property should not automatically initialize during construction, but instead should
   * wait for the first explicit use of the property getter.
   * @config {Boolean}
   * @default
   * @internal
   */
  lazy: false,

  /**
   * Indicates that this config property should automatically be set to `null` on destroy.
   * @config {Boolean}
   * @default
   * @internal
   */
  nullify: false,

  /**
   * Indicates that this config participates in rendering. This has does not affect the behavior of the property
   * directly, but allows classes that perform rendering to detect which config changes will affect the rendered
   * result.
   * @config {Boolean}
   * @default
   * @internal
   */
  render: false // TODO FUTURES
  // clear   (bool - set to null during cleanup)
  // destroy (bool - destroy value during cleanup)
  // evented (bool - fire event on change / after updater)

});
Config._$name = 'Config';

/**
 * @module Core/Base
 */

class MetaClass {
  constructor(options) {
    options && Object.assign(this, options);
  }

  getInherited(name) {
    var _this$super;

    return this[name] || (this[name] = Object.create(((_this$super = this.super) === null || _this$super === void 0 ? void 0 : _this$super.getInherited(name)) || null));
  }

}

const  // Using Object.getPrototypeOf instead of Reflect.getPrototypeOf because:
// 1. The are almost the same, according to the MDN difference is handling getPrototypeOf('string')
// 2. It allows to pass security check in SalesForce environment
{
  getPrototypeOf
} = Object,
      {
  defineProperty: defineProperty$1
} = Reflect,
      {
  hasOwnProperty: hasOwnProperty$2
} = Object.prototype,
      metaSymbol = Symbol('classMetaData'),
      mixinTagSymbol = Symbol('mixinTag'),
      originalConfigSymbol = Symbol('originalConfig'),
      configuringSymbol$1 = Config.symbols.configuring,
      instancePropertiesSymbol$1 = Config.symbols.instanceProperties,
      defaultConfigOptions = {
  merge: 'replace',
  simple: true
},
      emptyFn = () => {},
      newMeta = o => new MetaClass(o),
      setupNames = {
  /* foo : 'setupFoo' */
};
/**
 * Base class for all configurable classes.
 *
 * Subclasses do not have to implement a constructor with its restriction of having to call super()
 * before there is a `this` reference. Subclasses instead implement a `construct` method which is
 * called by the `Base` constructor. This may call its `super` implementation at any time.
 *
 * The `Base` constructor applies all configs to properties of the new instance. The instance
 * will have been configured after the `super.construct(config)` is called.
 *
 * See the Class System documentation in the guides for more information.
 *
 * @abstract
 */

class Base {
  static get isBase() {
    return true;
  }

  get isBase() {
    return true;
  } // defaultConfig & properties made private to not spam all other classes

  /**
   * A class property getter to add additional, special class properties.
   *
   * For example, a class adds a `declarable` class property like so:
   * ```
   *  class Something extends Base {
   *      static get declarable() {
   *          return ['extra'];
   *      }
   *
   *      static setupExtra(cls, meta) {
   *          // use cls.extra
   *      }
   *  }
   * ```
   * A derived class can then specify this property like so:
   * ```
   *  class Derived extends Something {
   *      static get extra() {
   *          // return extra information
   *      }
   *  }
   * ```
   * When the `Derived` class is initialized, the `setupExtra()` method is called and `Derived` is passed as the
   * argument. It is also the `this` pointer, but the parameter is minifiable. The second argument passed is the
   * `$meta` object for the class.
   *
   * Classes are initialized at the first occurrence of the following:
   *
   * - An instance is created
   * - The class `$meta` property is accessed
   *
   * @member {String[]} declarable
   * @static
   * @category Configuration
   * @internal
   */

  static get declarable() {
    return ['declarable',
    /**
     * A class property getter for the configuration properties of the class, which can be overridden by
     * configurations passed at construction time.
     *
     * Unlike a normal `static` property, this property is only ever used for the class that defines it (as in,
     * `hasOwnProperty`). It is retrieved for all classes in a class hierarchy, to gather their configs
     * individually and then combine them with those of derived classes.
     *
     * For example, a `Label` might declare a `text` config like so:
     * ```javascript
     *  class Label extends Base {
     *      static get configurable() {
     *          return {
     *              text : null
     *          };
     *      }
     *  }
     * ```
     * The `text` config is automatically inherited by classes derived from Label. By implementing
     * `get configurable()`, derived classes can change the default value of inherited configs, or define new
     * configs, or both.
     *
     * When a config property is declared in this way, the class author can also implement either of two
     * special methods that will be called when the config property is assigned a new value:
     *
     *  - `changeText()`
     *  - `updateText()`
     *
     * In the example above, the `Label` class could implement a `changeText()` method, an `updateText()`
     * method, or both. The generated property setter ensures these methods will be called when the `text`
     * property is assigned.
     *
     * The generated setter (for `text` in this example) performs the following steps:
     *
     *  - If the class defines a `changeText()` method, call it passing the new value and the current value:
     *    `changeText(newText, oldText)`.<br>
     *    Then:
     *    * If `changeText()` exits without returning a value (i.e., `undefined`), exit and do nothing
     *      further. The assumption is that the changer method has done all that is required.
     *    * Otherwise, the return value of `changeText()` replaces the incoming value passed to the setter.
     *  - If the new value (or the value returned by `changeText()`) is `!==` to the current value:
     *    * Update the stored config value in `this._text`.
     *    * If the class defines an `updateText()` method, call it passing the new value and the previous value.
     *      `updateText(newText, oldText)`
     *
     * #### Resolving a value from an owner
     * By specifying a value starting with `'up.'` for a config, the config system will resolve that value by
     * examining the ownership hierarchy. It will walk up the hierarchy looking for a property matching the name
     * (or dot separated path) after 'up.'. If one is found, the value will be read and used as the initial
     * value.
     *
     * ```javascript
     * class Parent extends Base {
     *     static get configurable() {
     *         return [
     *           'importantValue'
     *         ]
     *     }
     * }
     *
     * class Child extends Base {
     *     static get configurable() {
     *         return [
     *           'value'
     *         ]
     *     }
     * }
     *
     * const parent = new Parent({
     *     importantValue : 123
     * });
     *
     * const child = new Child({
     *     owner : parent,
     *     // Will be resolved from the owner
     *     value : 'up.importantValue'
     * });
     *
     * console.log(child.value); // logs 123
     * ```
      * Please note that this is for now a one way one time binding, the value will only be read initially and
     * not kept up to date on later changes.
     *
     * #### Value Merging
     * When a config property value is an object, the value declared by the base class is merged with values
     * declared by derived classes and the value passed to the constructor.
     * ```javascript
     *  class Example extends Base {
     *      static get configurable() {
     *          return {
     *              config : {
     *                  foo : 1,
     *                  bar : 2
     *              }
     *          };
     *      }
     *  }
     *
     *  class Example2 extends Example {
     *      static get configurable() {
     *          return {
     *              config : {
     *                  bar : 42,
     *                  zip : 'abc'
     *              }
     *          };
     *      }
     *  }
     *
     *  let ex = new Example2({
     *      config : {
     *          zip : 'xyz'
     *      }
     *  });
     * ```
     * The result of the merge would set `config` to:
     * ```javascript
     *  ex.foo = {
     *      foo : 1,    // from Example
     *      bar : 42,   // from Example2
     *      zip : 'xyz' // from constructor
     *  }
     * ```
     *
     * #### Config Options
     * Some config properties require additional options such as declarative information about the config that
     * may be useful to automate some operation. Consider a `Button`. It could declare that its `text` config
     * affects the rendered HTML by applying a `render` property to the config definition. Its base class could
     * then examine the config definition to find this property.
     *
     * To support this, config options ca be declared like so:
     * ```javascript
     *  class Button extends Widget {
     *      static get configurable() {
     *          return {
     *              text : {
     *                  value   : null,
     *                  $config : {
     *                      render : true
     *                  }
     *              }
     *          };
     *      }
     *  }
     * ```
     * The `$config` property can alternatively be just the names of the options that should be enabled (set
     * to `true`).
     *
     * For example, the following is equivalent to the above:
     * ```javascript
     *  class Button extends Widget {
     *      static get configurable() {
     *          return {
     *              text : {
     *                  value   : null,
     *                  $config : 'render'
     *              }
     *          };
     *  }
     * ```
     *
     * #### Default Value
     * It is common to set a config to a `null` value to take advantage of internal optimizations for `null`
     * values. In most cases the fact that this produces `undefined` as the actual initial value of the config
     * is acceptable. When this is not acceptable, a config can be declared like so:
     * ```javascript
     *  class Widget {
     *      static get configurable() {
     *          return {
     *              disabled : {
     *                  $config : null,
     *                  value   : null,
     *                  default : false
     *              }
     *          };
     *  }
     * ```
     * The `default` property above determines the value of the config while still gaining the benefits of
     * minimal processing due to the `null` value of the `value` property.
     * @member {Object} configurable
     * @static
     * @category Configuration
     * @internal
     */
    'configurable',
    /**
     * A class property getter for the default configuration of the class, which can be overridden by
     * configurations passed at construction time.
     *
     * Unlike a normal `static` property, this property is only ever used for the class that defines it (as in,
     * `hasOwnProperty`). It is retrieved for all classes in a class hierarchy, to gather their configs
     * individually and then combine them with those of derived classes.
     *
     * For example, a `Store` might declare its `url` config like so:
     * ```
     *  class Store extends Base {
     *      static get defaultConfig() {
     *          return {
     *              url : null
     *          };
     *      }
     *  }
     * ```
     * The `url` config is automatically inherited by classes derived from Store. By implementing
     * `get defaultConfig()`, derived classes can change the default value of inherited configs, or define new
     * configs, or both. When defining new configs, however, `configurable` is preferred.
     *
     * Config properties introduced to a class by this declaration do not participate in value merging and do
     * not get a generated setter. Config properties introduced by a base class using `configurable` can be
     * set to a different value using `defaultConfig` and in doing so, the values will be merged as appropriate
     * for `configurable`.
     *
     * @member {Object} defaultConfig
     * @static
     * @category Configuration
     * @internal
     */
    'defaultConfig',
    /**
     * A class property getter for the default values of internal properties for this class.
     * @member {Object} properties
     * @static
     * @category Configuration
     * @internal
     */
    'properties',
    /**
     * A class property getter for properties that will be applied to the class prototype.
     * @member {Object} prototypeProperties
     * @static
     * @category Configuration
     * @internal
     */
    'prototypeProperties'];
  }
  /**
   * Base constructor, passes arguments to {@link #function-construct}.
   * @param {...Object} [args] Usually called with a config object, but accepts any params
   * @function constructor
   * @category Lifecycle
   */

  constructor(...args) {
    const me = this,
          C = me.constructor;

    if (me.$meta.class !== C) {
      // This will happen only once for each class. We need to call the C.$meta getter which puts $meta on our
      // prototype. Since that alone would be optimized away (and would generate IDE and lint warnings), we call
      // emptyFn and simply pass the value.
      emptyFn(C.$meta);
    } // Allow subclasses to have a pseudo constructor with "this" already set:

    me.construct(...args);
    me.afterConstruct();
  }
  /**
   * Base implementation applies configuration.
   *
   * Subclasses need only implement this if they have to initialize instance specific
   * properties required by the class. Often a `construct` method is
   * unnecessary. All initialization of incoming configuration properties can be
   * done in a `set propName` implementation.
   * @param {...Object} [args] Usually called with a config object, but accepts any params
   * @category Lifecycle
   */

  construct(...args) {
    // Passing null to base construct means bypass the config system and stack creation (to gain performance)
    if (args[0] !== null) {
      this.configure(...args);
    }

    this.afterConfigure();
  }
  /**
   * Destroys the provided objects by calling their {@link #function-destroy} method.
   * Skips empty values or objects that are already destroyed.
   *
   * ```javascript
   * Base.destroy(myButton, toolbar1, helloWorldMessageBox);
   * ```
   * @param {...Object} [args] Objects to be destroyed
   * @category Lifecycle
   */

  static destroy(...args) {
    const shredder = object => {
      if (object !== null && object !== void 0 && object.destroy) {
        object.destroy();
      } else if (Array.isArray(object)) {
        object.forEach(shredder);
      }
    };

    shredder(args);
  }
  /**
   * Destroys this object. This is primarily accomplished by calling {@link #function-doDestroy}, however, prior to
   * calling `doDestroy`, {@link #property-isDestroying} is set to `true`. After {@link #function-doDestroy} returns,
   * {@link #property-isDestroyed} is set to `true`.
   *
   * Do not override this method in subclasses. To provide class-specific cleanup, implement {@link #function-doDestroy}
   * instead.
   * @category Lifecycle
   */

  destroy() {
    const me = this; // Let everyone know the object is going inert:

    me.isDestroying = true; // Make calling destroy() harmless:

    me.destroy = emptyFn;
    me.doDestroy();
    Object.setPrototypeOf(me, null); // Clear all remaining instance properties.

    for (const key in me) {
      if (key !== 'destroy' && key !== 'isDestroying') {
        delete me[key];
      }
    }

    delete me[originalConfigSymbol]; // Let everyone know the object is inert:

    me.isDestroyed = true;
  }
  /**
   * This method is required to help `unused` getters to survive production build process. Some tools, like angular,
   * will remove `unused` code in production build, making our side-effected getters behind, breaking code heavily.
   * @internal
   * @param getter Getter to evaluate
   */

  _thisIsAUsedExpression(getter) {}

  static get $$name() {
    return hasOwnProperty$2.call(this, '$name') && this.$name || // _$name is filled by webpack for every class (cls._$name = '...')
    hasOwnProperty$2.call(this, '_$name') && this._$name || this.name;
  }

  get $$name() {
    return this.constructor.$$name;
  }
  /**
   * Base implementation so that all subclasses and mixins may safely call super.startConfigure.
   *
   * This is called by the Base class before setting configuration properties, but after
   * the active initial getters have been set, so all configurations are available.
   *
   * This method allows all classes in the hierarchy to force some configs to be evaluated before others.
   * @internal
   * @category Lifecycle
   * @params {Object} config The configuration object use to set the initial state.
   */

  startConfigure(config) {}
  /**
   * Base implementation so that all subclasses and mixins may safely call super.finishConfigure.
   *
   * This is called by the Base class before exiting the {@link #function-configure} method.
   *
   * At this point, all configs have been applied, but the `isConfiguring` property is still set.
   *
   * This method allows all classes in the hierarchy to inject functionality
   * into the config phase.
   * @internal
   * @category Lifecycle
   * @params {Object} config The configuration object use to set the initial state.
   */

  finishConfigure(config) {}
  /**
   * Base implementation so that all subclasses and mixins may safely call `super.afterConfigure`. This is called by the Base class after the {@link #function-configure} method has been
   * called. At this point, all configs have been applied.
   *
   * This method allows all classes in the hierarchy to inject functionality
   * either before or after the super.afterConstruct();
   * @internal
   * @category Lifecycle
   */

  afterConfigure() {}
  /**
   * Base implementation so that all subclasses and mixins may safely call super.afterConstruct.
   *
   * This is called by the Base class after the {@link #function-construct} method has been
   * called.
   *
   * At this point, all configs have been applied.
   *
   * This method allows all classes in the hierarchy to inject functionality
   * either before or after the super.afterConstruct();
   * @internal
   * @function afterConstructor
   * @category Lifecycle
   */

  afterConstruct() {}
  /**
   * Provides a way of calling callbacks which may have been specified as the _name_ of a function
   * and optionally adds scope resolution.
   *
   * For example, if the callback is specified as a string, then if it is prefixed with `'this.'`
   * then the function is resolved in this object. This is useful when configuring listeners
   * at the class level.
   *
   * If the callback name is prefixed with `'up.'` then the ownership hierarchy is queried
   * using the `owner` property until an object with the named function is present, then the
   * named function is called upon that object.
   * @param {String|Function} fn The function to call, or the name of the function to call.
   * @param {Object} thisObject The `this` object of the function.
   * @param {Object[]} args The argument list to pass.
   */

  callback(fn, thisObject, args) {
    // Maintainer: do not make args ...args. This method may acquire more arguments
    if (thisObject === 'this') {
      thisObject = this;
    }

    const {
      handler,
      thisObj
    } = this.resolveCallback(fn, thisObject);
    return args ? handler.apply(thisObj, args) : handler.call(thisObj);
  }

  resolveProperty(propertyPath) {
    let thisObj = this;

    while (thisObj) {
      if (Objects.hasPath(thisObj, propertyPath)) {
        return Objects.getPath(thisObj, propertyPath);
      }

      thisObj = thisObj.owner;
    }

    return undefined;
  }
  /**
   * Provides a way of locating callbacks which may have been specified as the _name_ of a function
   * and optionally adds scope resolution.
   *
   * For example, if the callback is specified as a string, then if it is prefixed with `'this.'`
   * then the function is resolved in this object. This is useful when configuring listeners
   * at the class level.
   *
   * If the callback name is prefixed with `'up.'` then the ownership hierarchy is queried
   * using the `owner` property until an object with the named function is present, then the
   * named function is called upon that object.
   * @param {String|Function} handler The function to call, or the name of the function to call.
   * @param {Object} thisObj The `this` object of the function.
   * @param {Boolean} [enforceCallability = true] Pass `false` if the function may not exist, and a null return value is acceptable.
   * @returns {Object} `{ handler, thisObj }`
   */

  resolveCallback(handler, thisObj = this, enforceCallability = true) {
    // It's a string, we find it in its own thisObj
    if (handler.substr) {
      if (handler.startsWith('up.')) {
        handler = handler.substr(3); // Empty loop until we find the function owner

        for (thisObj = this.owner; thisObj && !thisObj[handler]; thisObj = thisObj.owner);

        if (!thisObj) {
          return;
        }
      } else if (handler.startsWith('this.')) {
        handler = handler.substr(5);
        thisObj = this;
      }

      if (!thisObj || !(thisObj instanceof Object)) {
        return;
      }

      if (typeof thisObj[handler] !== 'function') {
        return;
      }

      handler = thisObj[handler];
    }

    return {
      handler,
      thisObj
    };
  }

  bindCallback(inHandler, inThisObj = this) {
    if (inHandler) {
      const {
        handler,
        thisObj
      } = this.resolveCallback(inHandler, inThisObj);

      if (handler) {
        return handler.bind(thisObj);
      }
    }
  }
  /**
   * Delays the execution of the passed function by the passed time quantum, or if the time is omitted
   * or not a number, delays until the next animation frame. Note that this will use
   * {@link Core.mixin.Delayable#function-setTimeout} || {@link Core.mixin.Delayable#function-requestAnimationFrame}
   * if this class mixes in `Delayable`, otherwise it uses the global methods. The function will
   * be called using `this` object as its execution scope.
   * @param {Function} fn The function to call on a delay.
   * @param {Number} [by] The number of milliseconds to delay.
   * @private
   */

  delay(fn, by) {
    // Force scope on the fn if we are not a Delayable
    fn = this.setTimeout ? fn : fn.bind(this);
    const invoker = this.setTimeout ? this : window;
    invoker[typeof by === 'number' ? 'setTimeout' : 'requestAnimationFrame'](fn, by);
  }
  /**
   * Classes implement this method to provide custom cleanup logic before calling `super.doDestroy()`. The general
   * pattern is as follows:
   *
   * ```javascript
   *  class Foo extends Base {
   *      doDestroy() {
   *          // perform custom cleanup
   *
   *          super.doDestroy();
   *      }
   *  }
   * ```
   *
   * This method is called by {@link #function-destroy} which also prevents multiple calls from reaching `doDestroy`.
   * Prior to calling `doDestroy`, {@link #property-isDestroying} is set to `true`. Upon return, the object is fully
   * destructed and {@link #property-isDestroyed} is set to `true`.
   *
   * Do not call this method directly. Instead call {@link #function-destroy}.
   * @category Lifecycle
   */

  doDestroy() {
    const {
      nullify
    } = this.$meta;

    if (nullify) {
      for (let i = 0; i < nullify.length; ++i) {
        this[nullify[i].name] = null;
      }
    }
  }
  /**
   * Destroys the named properties if they have been initialized, and if they have a `destroy` method.
   * Deletes the property from this object. For example:
   *
   *      this.destroyProperties('store', 'resourceStore', 'eventStore', 'dependencyStore', 'assignmentStore');
   *
   * @param {String} properties The names of the properties to destroy.
   * @internal
   * @category Lifecycle
   */

  destroyProperties(...properties) {
    const me = this;
    let key, propertyValue;

    for (key of properties) {
      // If the value has *not* been pulled in from the configuration object yet
      // we must not try to access it, as that will cause the property to be initialized.
      if (key in me && (!me[configuringSymbol$1] || !me[configuringSymbol$1][key])) {
        propertyValue = me[key];

        if (propertyValue && propertyValue.destroy) {
          propertyValue.destroy();
        }

        delete me[key];
      }
    }
  }
  /**
   * Called by the Base constructor to apply configs to this instance. This must not be called.
   * @param {Object} config The configuration object from which instance properties are initialized.
   * @private
   * @category Lifecycle
   */

  configure(config = {}) {
    const me = this,
          {
      beforeConfigure
    } = config,
          configs = me.$meta.configs,
          fullConfig = me.getDefaultConfiguration();
    let cfg, key, value;
    me.initialConfig = config; // Important flag for setters to know whether they are being called during
    // configuration when this object is not fully alive, or whether it's being reconfgured.

    me.isConfiguring = true; // Assign any instance properties declared by the class.

    Object.assign(me, me.getProperties()); // Apply configuration to default from class definition. This is safe because it's either chained from or a
    // fork of the class values.

    for (key in config) {
      value = config[key];
      cfg = configs[key];
      fullConfig[key] = cfg ? cfg.merge(value, fullConfig[key], null) : value;
    }

    if (beforeConfigure) {
      delete fullConfig.beforeConfigure; // noinspection JSValidateTypes

      beforeConfigure(me, fullConfig);
    } // Cache me.config for use by get config.

    me.setConfig(me[originalConfigSymbol] = fullConfig, true);
    me.isConfiguring = false;
  }
  /**
   * Returns the value of the specified config property. This is a method to allow
   * property getters to be explicitly called in a way that does not get optimized out.
   *
   * The following triggers the getter call, but optimizers will remove it:
   *
   *      inst.foo;   // also raises "expression has no side-effects" warning
   *
   * Instead, do the following to trigger a getter:
   *
   *      inst.getConfig('foo');
   *
   * @param {String} name
   * @internal
   * @category Configuration
   */

  getConfig(name) {
    return this[name];
  }
  /**
   * Sets configuration options this object with all the properties passed in the parameter object.
   * Timing is taken care of. If the setter of one config is called first, and references
   * the value of another config which has not yet been set, that config will be set just
   * in time, and the *new* value will be used.
   * @param {Object} config An object containing configurations to change.
   * @category Configuration
   */

  setConfig(config, isConstructing) {
    const me = this,
          wasConfiguring = me[configuringSymbol$1],
          configDone = wasConfiguring ? me.configDone : me.configDone = {},
          configs = me.$meta.configs;
    let cfg, key;
    me[instancePropertiesSymbol$1] = {}; // Cache configuration for use by injected property initializers.

    me[configuringSymbol$1] = wasConfiguring ? Object.setPrototypeOf(Object.assign({}, config), wasConfiguring) : config; // For each incoming non-null configuration, create a temporary getter which will
    // pull the value in from the initialConfig so that it doesn't matter in
    // which order properties are set. You can access any property at any time.

    for (key in config) {
      // Don't default null configs in unless it's a direct property of the
      // the passed configuration. When used at construct time, defaultConfigs
      // will be prototype-chained onto the config.
      if (config[key] != null || hasOwnProperty$2.call(config, key)) {
        cfg = configs[key] || Config.get(key);
        cfg.defineInitter(me, config[key]);

        if (!isConstructing) {
          configDone[key] = false;
        } else if (cfg.lazy) {
          configDone[key] = true;
        }
      } else {
        configDone[key] = true;
      }
    }

    if (isConstructing) {
      me.startConfigure(config);
    } // Set all our properties from the config object.
    // If one of the properties needs to access a property that has not
    // yet been set, the above temporary property will pull it through.
    // Can't use Object.assign because that only uses own properties.
    // config value blocks are prototype chained subclass->superclass

    for (key in config) {
      // Only push the value through if the property initializer is still present.
      // If it gets triggered to pull the configuration value in, it deleted itself.
      if (!configDone[key]) {
        me[key] = config[key];
      }
    }

    if (wasConfiguring) {
      me[configuringSymbol$1] = wasConfiguring;
    } else {
      delete me[configuringSymbol$1];
    }

    if (isConstructing) {
      me.finishConfigure(config);
    }

    return me;
  }
  /**
   * Ensures that the specified config is initialized if it is needed. If there is a config value specified, and it
   * was initialized by this call, this method returns `true`. If there was a config value specified, and it was
   * already initialized, this method returns `false`. If there was no value specified for the given config, this
   * method returns `null`.
   *
   * This is different than just reading the property because some property getters exist that do not actually just
   * read the config value back, but instead produce some result. Reading such properties to incidentally trigger a
   * possible config initializer can lead to incorrect results. For example, the Combo items config.
   *
   * @param {String} name The name of the config property.
   * @returns {Boolean}
   * @internal
   */

  triggerConfig(name) {
    const {
      configDone
    } = this,
          config = this[configuringSymbol$1],
          triggered = config && (config[name] != null || hasOwnProperty$2.call(config, name)) ? !configDone[name] : null;

    if (triggered) {
      this.getConfig(name);
    }

    return triggered;
  }

  onConfigChange() {} // declared above because lint/IDE get angry about not declaring the args...

  /**
   * This method is called when any config changes.
   * @param {Object} info Object containing information regarding the config change.
   * @param {String} info.name The name of the config that changed.
   * @param {*} info.value The new value of the config.
   * @param {*} info.was The previous value of the config.
   * @param {Core.Config} info.config The `Config` object for the changed config property.
   * @method onConfigChange
   * @internal
   * @category Configuration
   */

  /**
   * Returns a *copy* of the full configuration which was used to configure this object.
   * @property {Object}
   * @category Configuration
   * @readonly
   */

  get config() {
    const result = {},
          myConfig = this[originalConfigSymbol]; // The configuration was created as a prototype chain of the class hierarchy's
    // defaultConfig values hanging off a copy of the initialConfig object, so
    // we must loop and copy since Object.assign only copies own properties.

    for (const key in myConfig) {
      result[key] = myConfig[key];
    }

    return result;
  }
  /**
   * Registers this class type with its Factory
   */

  static initClass() {
    return this.$meta.class;
  }
  /**
   * The class's {@link #property-$meta-static meta} object.
   * @property {Object}
   * @name $meta
   * @internal
   * @category Misc
   */

  /**
   * An object owned by this class that does not share properties with its super class.
   *
   * This object may contain other properties which are added as needed and are not documented here.
   *
   * @property {Object} $meta The class meta object.
   * @property {Function} $meta.class The class constructor that owns the meta object.
   * @property {Object} $meta.super The `$meta` object for the super class. This is `null` for `Base`.
   * @property {Object} $meta.config The object holding the default configuration values for this class.
   * @property {Object} $meta.configs An object keyed by config name that holds the defined configs for the class.
   * The value of each property is a {@link Core/Config} instance.
   * @property {Boolean} $meta.forkConfigs This will be `true` if the default configuration values for this class
   * (in the `config` property of the meta object) must be forked to avoid object sharing, or if the object can be
   * passed to `Object.create()` for efficiency.
   * @property {Array} $meta.hierarchy The array of classes in the ancestry of this class. This will start with
   * `Base` at index 0 and ends with this class.
   * @property {Array} $meta.properties The array of classes that define a "static get properties()" getter.
   * @internal
   * @static
   * @category Misc
   */

  static get $meta() {
    const me = this;
    let meta = me[metaSymbol];

    if (!hasOwnProperty$2.call(me, metaSymbol)) {
      me[metaSymbol] = meta = newMeta();
      meta.class = me;
      me.setupClass(meta);
    }

    return meta;
  }
  /**
   * This optional class method is called when a class is mixed in using the {@link #function-mixin-static mixin()}
   * method.
   * @internal
   */

  static onClassMixedIn() {// empty
  }
  /**
   * Returns the merge of the `baseConfig` and `config` config objects based on the configs defined by this class.
   * @param {Object} baseConfig The base config or defaults.
   * @param {...Object} configs One or more config objects that takes priority over `baseConfig`.
   * @returns {Object}
   * @internal
   */

  static mergeConfigs(baseConfig, ...configs) {
    const classConfigs = this.$meta.configs,
          result = Objects.clone(baseConfig) || {};
    let config, i, key, value;

    for (i = 0; i < configs.length; ++i) {
      config = configs[i];

      if (config) {
        for (key in config) {
          value = config[key];

          if (classConfigs[key]) {
            value = classConfigs[key].merge(value, result[key]);
          } else if (result[key] && value) {
            value = Config.merge(value, result[key]);
          }

          result[key] = value;
        }
      }
    }

    return result;
  }
  /**
   * Applies one or more `mixins` to this class and returns the produced class constructor.
   *
   * For example, instead of writing this:
   * ```
   *  class A extends Delayable(Events(Localizable(Base))) {
   *      // ...
   *  }
   * ```
   *
   * Using this method, one would write this:
   * ```
   *  class A extends Base.mixin(Localizable, Events, Delayable) {
   *      // ...
   *  }
   * ```
   * If one of the mixins specified has already been mixed into the class, it will be ignored and not mixed in a
   * second time.
   * @param {...Function} mixins
   * @returns {Function}
   */

  static mixin(...mixins) {
    // Starting w/the first class C = this
    let C = this,
        i; // wrap each class C using mixins[i] to produce the next class

    for (i = 0; i < mixins.length; ++i) {
      const mixin = mixins[i],
            // Grab or create a unique Symbol for this mixin so we can tell if we've already mixed it in
      tag = mixin[mixinTagSymbol] || (mixin[mixinTagSymbol] = Symbol('mixinTag'));

      if (C[tag]) {
        continue;
      }

      C = mixin(C);
      C[tag] = true; // properties on the constructor are inherited to subclass constructors...

      if (hasOwnProperty$2.call(C, 'onClassMixedIn')) {
        C.onClassMixedIn();
      }
    }

    return C;
  }
  /**
   * This method is called only once for any class. This can occur when the first instance is created or when the
   * `$meta` object is first requested.
   * @param {Object} meta The `$meta` object for the class.
   * @internal
   * @category Misc
   */

  static setupClass(meta) {
    var _base$nullify;

    const cls = meta.class,
          // Trigger setupClass on the super class (if it has yet to happen):
    base = getPrototypeOf(cls).$meta,
          name = cls.$$name,
          names = base.names,
          proto = cls.prototype;
    defineProperty$1(proto, '$meta', {
      value: meta
    });
    Object.assign(meta, {
      super: base,
      config: Object.create(base.config),
      configs: Object.create(base.configs),
      declarables: base.declarables,
      forkConfigs: base.forkConfigs,
      hierarchy: Object.freeze([...base.hierarchy, cls]),
      names: names.includes(name) ? names : Object.freeze([...names, name]),
      properties: base.properties,
      nullify: (_base$nullify = base.nullify) === null || _base$nullify === void 0 ? void 0 : _base$nullify.slice()
    });

    if (names !== meta.names) {
      const is = 'is' + name;

      if (!hasOwnProperty$2.call(proto, is)) {
        defineProperty$1(proto, is, {
          value: true
        });
      }

      if (!hasOwnProperty$2.call(cls, is)) {
        defineProperty$1(cls, is, {
          value: true
        });
      }
    } // NOTE: we always use meta.declarables because setupDeclarable() can replace the array on the meta object
    // when new declarable properties are added...

    for (let decl, setupName, i = 0; i < meta.declarables.length; ++i) {
      decl = meta.declarables[i];

      if (hasOwnProperty$2.call(cls, decl)) {
        setupName = setupNames[decl] || (setupNames[decl] = `setup${StringHelper.capitalize(decl)}`);
        cls[setupName](cls, meta);
      }
    }
    /*  Add slash to the front of this line to enable the diagnostic block:

    /**/

  }
  /**
   * This method is called as part of `setupClass()`. It will process the `configurable()` return object and the
   * `defaultConfig` return object.
   * @param {Object} meta The `meta` object for this class.
   * @param {Object} configs The config definition object.
   * @param {Boolean} simple `true` when processing `defaultConfig` and `false` when processing `configurable`.
   * @private
   * @category Configuration
   */

  static setupConfigs(meta, configs, simple) {
    const classConfigValues = meta.config,
          classConfigs = meta.configs,
          proto = meta.class.prototype;
    let cfg, nullify, options, value, wasNullify;

    for (const name in configs) {
      value = configs[name];

      if (simple) {
        // Using "defaultConfig"
        if (!(cfg = classConfigs[name])) {
          cfg = Config.get(name, defaultConfigOptions);
        } else {
          // The property may be declared in a base class using configurable(), so it may have special
          // merge processing:
          value = cfg.merge(value, classConfigValues[name], meta);
        }
        /*  Add slash to the front of this line to enable the diagnostic block:

        /**/

      } else {
        // Using "configurable"
        options = null;

        if (value && typeof value === 'object' && '$config' in value) {
          options = value.$config;

          if (options && !Objects.isObject(options)) {
            options = Objects.createTruthyKeys(options);
          }

          if ('default' in value) {
            defineProperty$1(proto, Config.get(name).field, {
              configurable: true,
              writable: true,
              // or else "this._value = x" will fail
              value: value.default
            });
          }

          value = value.value;
        }

        if (!(cfg = classConfigs[name])) {
          cfg = Config.get(name, options);
          cfg.define(meta.class.prototype);
          wasNullify = false;
        } else {
          wasNullify = cfg.nullify;

          if (options) {
            // Defined by a base class, but maybe being adjusted by derived.
            cfg = cfg.extend(options); // In the future, we may need to redefine the property here if options affect the descriptor (such
            // as event firing)
          }

          value = cfg.merge(value, classConfigValues[name], meta);
        }

        if (cfg.nullify && !wasNullify) {
          (nullify || (nullify = meta.nullify || (meta.nullify = []))).push(cfg);
        }
      } // If any default properties are *mutable* Objects or Array we need to clone them.
      // so that instances do not share configured values.

      if (value && (Objects.isObject(value) || Array.isArray(value)) && !Object.isFrozen(value)) {
        meta.forkConfigs = true;
      }

      classConfigs[name] = cfg;
      classConfigValues[name] = value;
    }
  }

  static setupConfigurable(cls, meta) {
    cls.setupConfigs(meta, cls.configurable, false);
  }

  static setupDefaultConfig(cls, meta) {
    cls.setupConfigs(meta, cls.defaultConfig, true);
  }

  static setupDeclarable(cls, meta) {
    const declarable = cls.declarable;
    let all = meta.declarables,
        forked,
        i;

    for (i = 0; i < declarable.length; ++i) {
      if (!all.includes(declarable[i])) {
        if (!forked) {
          meta.declarables = forked = all = all.slice();
        }

        all.push(declarable[i]);
      }
    }
  }

  static setupProperties(cls, meta) {
    meta.properties = meta.super.properties.slice();
    meta.properties.push(cls);
    Object.freeze(meta.properties);
  }

  static setupPrototypeProperties(cls) {
    Object.assign(cls.prototype, cls.prototypeProperties);
  }
  /**
   * Gets the full {@link #property-defaultConfig-static} block for this object's entire inheritance chain
   * all the way up to but not including {@link Core.Base}
   * @return {Object} All default config values for this class.
   * @private
   * @category Configuration
   */

  getDefaultConfiguration() {
    return this.constructor.getDefaultConfiguration();
  }
  /**
   * Gets the full {@link #property-defaultConfig-static} block for the entire inheritance chain for this class
   * all the way up to but not including {@link Core.Base}
   * @return {Object} All default config values for this class.
   * @private
   * @category Configuration
   */

  static getDefaultConfiguration() {
    const meta = this.$meta,
          config = meta.forkConfigs ? Base.fork(meta.config) : Object.create(meta.config);

    if (VersionHelper.isTestEnv && BrowserHelper.isBrowserEnv && config.testConfig && BrowserHelper.global.__applyTestConfigs) {
      for (const o in config.testConfig) {
        config[o] = config.testConfig[o];
      }
    }

    return config;
  }

  static fork(obj) {
    let ret = obj,
        key,
        value;

    if (obj && Objects.isObject(obj) && !Object.isFrozen(obj)) {
      ret = Object.create(obj);

      for (key in obj) {
        value = obj[key];

        if (value) {
          if (Objects.isObject(value)) {
            ret[key] = Base.fork(value);
          } else if (Array.isArray(value)) {
            ret[key] = value.slice();
          }
        }
      }
    }

    return ret;
  }
  /**
   * Gets the full {@link #property-properties-static} block for this class's entire inheritance chain
   * all the way up to but not including {@link Core.Base}
   * @return {Object} All default config values for this class.
   * @private
   * @category Configuration
   */

  getProperties() {
    const // The meta.properties array is an array of classes that define "static get properties()"
    hierarchy = this.$meta.properties,
          result = {}; // TODO: if properties block does not change this could be cached? would speed up loading of big data sets
    //  into grid. The problem is that this call creates new (i.e., non-shared) instances of objects, arrays and
    //  Map objects that apply to each instance. These would all need to be cloned so it may be better to just
    //  call the properties getter each time and let it create new instances of whatever types.
    //for (cls of hierarchy) { // for-of transpiles badly and this is called a lot

    for (let i = 0; i < hierarchy.length; i++) {
      // Gather the class result in top-down order so that subclass properties override superclass properties
      Object.assign(result, hierarchy[i].properties);
    }

    return result;
  }

  static get superclass() {
    return getPrototypeOf(this);
  }
  /**
   * Used by the Widget and GridFeatureManager class internally. Returns the class hierarchy of this object
   * starting from the `topClass` class (which defaults to `Base`).
   *
   * For example `classHierarchy(Widget)` on a Combo would yield `[Widget, Field, TextField, PickerField, Combo]`
   * @param {Function} [topClass] The topmost class constructor to start from.
   * @returns {Function[]} The class hierarchy of this instance.
   * @private
   * @category Configuration
   */

  classHierarchy(topClass) {
    const hierarchy = this.$meta.hierarchy,
          index = topClass ? hierarchy.indexOf(topClass) : 0;
    return index > 0 ? hierarchy.slice(index) : hierarchy;
  }
  /**
   * Checks if an obj is of type using object's $$name property and doing string comparison of the property with the
   * type parameter.
   *
   * @param {String} type
   * @return {Boolean}
   */

  static isOfTypeName(type) {
    return this.$meta.names.includes(type);
  }
  /**
   * Removes all event listeners that were registered with the given `name`.
   * @param {String} name The name of the event listeners to be removed.
   * @category Events
   */

  detachListeners(name) {
    let detachers = this.$detachers;
    detachers = detachers && detachers[name];

    if (detachers) {
      while (detachers.length) {
        detachers.pop()();
      }
    }
  }
  /**
   * Tracks a detacher function for the specified listener name.
   * @param {String} name The name assigned to the associated listeners.
   * @param {Function} detacher The detacher function.
   * @private
   */

  trackDetacher(name, detacher) {
    const detachers = this.$detachers || (this.$detachers = {}),
          bucket = detachers[name] || (detachers[name] = []);
    bucket.push(detacher);
  }
  /**
   * Removes all detacher functions for the specified `Events` object. This is called
   * by the `removeAllListeners` method on that object which is typically called by its
   * `destroy` invocation.
   * @param {Core.mixin.Events} eventer The `Events` instance to untrack.
   * @private
   */

  untrackDetachers(eventer) {
    const detachers = this.$detachers;

    if (detachers) {
      for (const name in detachers) {
        const bucket = detachers[name];

        for (let i = bucket.length; i-- > 0;)
        /* empty */
        {
          if (bucket[i].eventer === eventer) {
            bucket.splice(i, 1);
          }
        }
      }
    }
  }

}
const proto = Base.prototype; // Informs the standard config setter there is no need to call this fn:

proto.onConfigChange.$nullFn = true;
Base[metaSymbol] = proto.$meta = newMeta({
  class: Base,
  config: Object.freeze({}),
  configs: Object.create(null),
  declarables: Base.declarable,
  forkConfigs: false,
  hierarchy: Object.freeze([Base]),
  names: Object.freeze(['Base']),
  nullify: null,
  properties: Object.freeze([]),
  super: null
}); // Avoid some object shape changes:

Object.assign(proto, {
  $detachers: null,

  /**
   * This property is set to `true` by {@link #function-destroy} after the {@link #function-doDestroy} method returns.
   * It is also one of the few properties that remains on the object after returning from `destroy()`. This property
   * is often checked in code paths that may encounter a destroyed object (like some event handlers) or in the
   * destruction path during cleanup.
   * @member {Boolean}
   * @readonly
   */
  isDestroyed: false,

  /**
   * This property is set to `true` on entry to the {@link #function-destroy} method. It remains on the objects after
   * returning from `destroy()`. If {@link #property-isDestroyed} is `true`, this property will also be `true`, so
   * there is no need to test for both (for example, `comp.isDestroying || comp.isDestroyed`).
   * @member {Boolean}
   * @readonly
   */
  isDestroying: false
});
VersionHelper.setVersion('core', '0.0.0');
Base._$name = 'Base';

/**
 * @module Core/helper/ArrayHelper
 */

/**
 * Helper with useful functions for handling Arrays
 * @internal
 */
class ArrayHelper {
  static clean(array) {
    return array.reduce((res, item) => {
      if (item !== null && item !== undefined && !(Array.isArray(item) && item.length === 0) && item !== '') res.push(item);
      return res;
    }, []);
  }
  /**
   * Remove one or more items from an array
   * @param {Array} array Array to remove from
   * @param {Object} items One or more items to remove
   * @returns {boolean} Returns true if any item was removed
   */

  static remove(array, ...items) {
    let index,
        item,
        removed = false;

    for (let i = 0; i < items.length; i++) {
      item = items[i];

      if ((index = array.indexOf(item)) !== -1) {
        array.splice(index, 1);
        removed = true;
      }
    }

    return removed;
  }
  /*
   * Calculates the insertion index of a passed object into the passed Array according
   * to the passed comparator function. Note that the passed Array *MUST* already be ordered.
   * @param {Object} item The item to calculate the insertion index for.
   * @param {Array} The array into which the item is to be inserted.
   * @param {Function} comparatorFn The comparison function. Must return -1 or 0 or 1.
   * @param {Object} comparatorFn.lhs The left object to compare.
   * @param {Object} comparatorFn.rhs The right object to compare.
   * @param {Number} index The possible correct index to try first before a binary
   * search is instigated.
   */

  static findInsertionIndex(item, array, comparatorFn = this.lexicalCompare, index) {
    const len = array.length;
    let beforeCheck, afterCheck;

    if (index < len) {
      beforeCheck = index > 0 ? comparatorFn(array[index - 1], item) : 0;
      afterCheck = index < len - 1 ? comparatorFn(item, array[index]) : 0;

      if (beforeCheck < 1 && afterCheck < 1) {
        return index;
      }
    }

    return this.binarySearch(array, item, comparatorFn);
  }
  /**
   * Similar to the native Array.find call, but this finds the *last* element in the array for which
   * the passed function returns a truthy value.
   * @param {Object[]} array The array to find in.
   * @param {Function} fn The testing function.
   * @param {Object} [thisObj] The scope (`this` reference) in which to call the function.
   */

  static findLast(array, fn, thisObj) {
    for (let {
      length
    } = array, i = length - 1; i >= 0; i--) {
      if (fn.call(thisObj, array[i], i, array)) {
        return array[i];
      }
    }
  }
  /**
   * This method returns the index that a given item would be inserted into the
   * given (sorted) `array`. Note that the given `item` may or may not be in the
   * array. This method will return the index of where the item *should* be.
   *
   * For example:
   *
   *      var array = [ 'A', 'D', 'G', 'K', 'O', 'R', 'X' ];
   *      var index = ArrayHelper.binarySearch(array, 'E');
   *
   *      console.log('index: ' + index);
   *      // logs "index: 2"
   *
   *      array.splice(index, 0, 'E');
   *
   *      console.log('array : ' + array.join(''));
   *      // logs "array: ADEGKORX"
   *
   * @param {Object[]} array The array to search.
   * @param {Object} item The item that you want to insert into the `array`.
   * @param {Number} [begin=0] The first index in the `array` to consider.
   * @param {Number} [end=array.length] The index that marks the end of the range
   * to consider. The item at this index is *not* considered.
   * @param {Function} [compareFn] The comparison function that matches the sort
   * order of the `array`. The default `compareFn` compares items using less-than
   * and greater-than operators.
   * @return {Number} The index for the given item in the given array based on
   * the passed `compareFn`.
   */

  static binarySearch(array, item, begin = 0, end = array.length, compareFn = this.lexicalCompare) {
    const length = array.length;
    let middle, comparison;

    if (begin instanceof Function) {
      compareFn = begin;
      begin = 0;
    } else if (end instanceof Function) {
      compareFn = end;
      end = length;
    }

    --end;

    while (begin <= end) {
      middle = begin + end >> 1;
      comparison = compareFn(item, array[middle]);

      if (comparison >= 0) {
        begin = middle + 1;
      } else if (comparison < 0) {
        end = middle - 1;
      }
    }

    return begin;
  }

  magnitudeCompare(lhs, rhs) {
    return lhs < rhs ? -1 : lhs > rhs ? 1 : 0;
  }

  lexicalCompare(lhs, rhs) {
    lhs = String(lhs);
    rhs = String(rhs);
    return lhs < rhs ? -1 : lhs > rhs ? 1 : 0;
  }
  /**
   * Similar to Array.prototype.fill(), but constructs a new array with the specified item count and fills it with
   * clones of the supplied item.
   * @param {Number} count Number of entries to create
   * @param {Object|Array} itemOrArray Item or array of items to clone (uses object spread to create shallow clone)
   * @param {Function} [fn] An optional function that is called for each item added, to allow processing
   * @returns {Array} A new populated array
   */

  static fill(count, itemOrArray = {}, fn = null) {
    const result = [],
          items = Array.isArray(itemOrArray) ? itemOrArray : [itemOrArray];

    for (let i = 0; i < count; i++) {
      for (const item of items) {
        // Using object spread here forces us to use more babel plugins and will make
        // react_typescript demo very difficult to setup
        const processedItem = Object.assign({}, item);

        if (fn) {
          fn(processedItem, i);
        }

        result.push(processedItem);
      }
    }

    return result;
  }
  /**
   * Populates an array with the return value from `fn`.
   * @param {Number} count Number of entries to create
   * @param {Function} fn A function that is called `count` times, return value is added to array
   * @param {Number} fn.index Current index in the array
   * @returns {Array} A new populated array
   */

  static populate(count, fn) {
    const items = [];

    for (let i = 0; i < count; i++) {
      items.push(fn(i));
    }

    return items;
  }
  /**
   * Pushes `item` on to the `array` if not already included
   * @param {Array}  array Array to push to
   * @param {Object} item Item to push if not already included
   */

  static include(array, item) {
    if (!array.includes(item)) {
      array.push(item);
    }
  }
  /**
   * Returns a new array with the unique items from the supplied array.
   * @param {Array} array Input array
   * @returns {Array} New array with unique items
   */

  static unique(array) {
    return [...new Set(array)];
  } // Wanted to create an indexer on Stores, based on this. But turns out Proxy cannot be transpiled/polyfill for IE11
  // Keeping it for future reference

  static allowNegative(array) {
    // From https://github.com/sindresorhus/negative-array
    return new Proxy(array, {
      get(target, name, receiver) {
        if (typeof name !== 'string') {
          return Reflect.get(target, name, receiver);
        }

        const index = Number(name);

        if (Number.isNaN(index)) {
          return Reflect.get(target, name, receiver);
        }

        return target[index < 0 ? target.length + index : index];
      },

      set(target, name, value, receiver) {
        if (typeof name !== 'string') {
          return Reflect.set(target, name, value, receiver);
        }

        const index = Number(name);

        if (Number.isNaN(index)) {
          return Reflect.set(target, name, value, receiver);
        }

        target[index < 0 ? target.length + index : index] = value;
        return true;
      }

    });
  }

  static delta(a, b, useRelativeNaming = false) {
    // Nicer syntax but about 40% slower (an extra iteration)
    // const
    //     onlyInA = a.filter(item => !b.includes(item)),
    //     onlyInB = b.filter(item => !a.includes(item)),
    //     inBoth  = a.filter(item => b.includes(item));
    // Quick bailout for nonexisting target array
    if (!b) {
      return useRelativeNaming ? {
        toAdd: a,
        toRemove: [],
        toKeep: []
      } : {
        onlyInA: a,
        onlyInB: [],
        inBoth: []
      };
    }

    const onlyInA = [],
          onlyInB = [],
          inBoth = [];

    for (let i = 0; i < a.length; i++) {
      const item = a[i];

      if (b.includes(item)) {
        inBoth.push(item);
      } else {
        onlyInA.push(item);
      }
    }

    for (let i = 0; i < b.length; i++) {
      const item = b[i];

      if (!inBoth.includes(item)) {
        onlyInB.push(item);
      }
    }

    if (useRelativeNaming) {
      return {
        toAdd: onlyInA,
        toRemove: onlyInB,
        toKeep: inBoth
      };
    }

    return {
      onlyInA,
      onlyInB,
      inBoth
    };
  }

}
ArrayHelper._$name = 'ArrayHelper';

/**
 * @module Core/helper/FunctionHelper
 */

const {
  hasOwnProperty: hasOwnProperty$3
} = Object.prototype;
/**
 * Provides functionality for working with functions
 * @internal
 */

class FunctionHelper {
  /**
   * Inserts a function after the specified `method` is called on an `object`. To remove this hook, invoke the
   * function returned by this method.
   * ```
   *  class A {
   *      method() {
   *          console.log('method');
   *      }
   *  }
   *
   *  let instance = new A();
   *
   *  let detach = FunctionHelper.after(instance, 'method', () => { console.log('after') });
   *
   *  instance.method();
   *  > method
   *  > after
   *
   *  detach();
   *  instance.method();
   *  > method
   * ```
   * The value returned by the original method is passed as the first argument to `fn` followed by all the arguments
   * passed by the caller.
   *
   * If `fn` returns a value (not `undefined`), that value is returned from the method call instead of the value
   * returned by the original method.
   * ```
   *  class A {
   *      method(x) {
   *          console.log('method', x);
   *          return x * 2
   *      }
   *  }
   *
   *  let instance = new A();
   *
   *  let detach = FunctionHelper.after(instance, 'method', (ret, x) => {
   *      console.log('after', ret, x);
   *      return x / 2;
   *  });
   *
   *  console.log(instance.method(50));
   *  > method 50
   *  > after 100 50
   *  > 25
   *
   *  detach();
   *  console.log(instance.method(50));
   *  > method 50
   *  > 100
   * ```
   *
   * @param {Object} object The object to hook.
   * @param {String} method The name of the method on `object` to hook.
   * @param {Function|String} fn The function or method name (on `thisObj`) to call after `method`.
   * @param {Object} [thisObj] The `this` pointer value for calling `fn`.
   * @param {Object} [options] Additional options
   * @param {Boolean} [options.return=true] Specify `false` to not include the return value of the hooked method as
   * the first argument to `fn`.
   * @returns {Function} The function to call to remove the hook.
   */
  static after(object, method, fn, thisObj, options) {
    const named = typeof fn === 'string',
          withReturn = (options === null || options === void 0 ? void 0 : options.return) !== false,
          hook = (...args) => {
      const // if object.destroy() occurs, our hook will be removed, so this fn won't be called in that case
      origResult = hook.$nextHook.call(object, ...args),
            hookResult = thisObj !== null && thisObj !== void 0 && thisObj.isDestroyed ? undefined : withReturn ? named ? thisObj[fn](origResult, ...args) : fn.call(thisObj, origResult, ...args) : named ? thisObj[fn](...args) : fn.call(thisObj, ...args);
      return hookResult === undefined ? origResult : hookResult;
    };

    return FunctionHelper.hookMethod(object, method, hook);
  }
  /**
   * Inserts a function before the specified `method` is called on an `object`. To remove this hook, invoke the
   * function returned by this method.
   * ```
   *  class A {
   *      method() {
   *          console.log('method');
   *      }
   *  }
   *
   *  let instance = new A();
   *
   *  let detach = FunctionHelper.before(instance, 'method', () => { console.log('before') });
   *
   *  instance.method();
   *  > before
   *  > method
   *
   *  detach();
   *  instance.method();
   *  > method
   * ```
   * If `fn` returns `false`, the original method is not invoked and `false` is returned to the caller.
   * ```
   *  class A {
   *      method(x) {
   *          console.log('method', x);
   *          return x * 2;
   *      }
   *  }
   *
   *  let instance = new A();
   *
   *  let detach = FunctionHelper.before(instance, 'method', x => {
   *      console.log('before', x);
   *      return false;
   *  });
   *
   *  console.log(instance.method(50));
   *  > before 50
   *  > false
   *
   *  detach();
   *  console.log(instance.method(50));
   *  > method 50
   *  > 100
   * ```
   *
   * @param {Object} object The object to hook.
   * @param {String} method The name of the method on `object` to hook.
   * @param {Function|String} fn The function or method name (on `thisObj`) to call before `method`.
   * @param {Object} [thisObj] The `this` pointer value for calling `fn`.
   * @returns {Function} The function to call to remove the hook.
   */

  static before(object, method, fn, thisObj) {
    const named = typeof fn === 'string',
          hook = (...args) => {
      const ret = thisObj !== null && thisObj !== void 0 && thisObj.isDestroyed ? 0 : named ? thisObj[fn](...args) : fn.call(thisObj, ...args);
      return ret === false ? ret : hook.$nextHook.call(object, ...args);
    };

    return FunctionHelper.hookMethod(object, method, hook);
  }

  static curry(func) {
    return function curried(...args) {
      if (args.length >= func.length) {
        return func.apply(this, args);
      } else {
        return function (...args2) {
          return curried.apply(this, args.concat(args2));
        };
      }
    };
  }

  static bindAll(obj) {
    for (let key in obj) {
      if (typeof obj[key] === 'function') {
        obj[key] = obj[key].bind(obj);
      }
    }
  }
  /**
   * Returns a function which calls the passed `interceptor` function first, and the passed `original` after
   * as long as the `interceptor` does not return `false`.
   * @param {Function} original The function to call second.
   * @param {Function} interceptor The function to call first.
   * @param {Object} [thisObj] The `this` reference when the functions are called.
   * @returns The return value from the `original` function **if it was called**, else `false`.
   */

  static createInterceptor(original, interceptor, thisObj) {
    return (...args) => {
      if (interceptor.call(thisObj, ...args) !== false) {
        return original.call(thisObj, ...args);
      }

      return false;
    };
  }
  /**
   * Returns a function which calls the passed `sequence` function after calling
   * the passed `original`.
   * @param {Function} original The function to call first.
   * @param {Function} sequence The function to call second.
   * @param {Object} [thisObj] The `this` reference when the functions are called.
   * @returns The value returned from the sequence if it returned a value, else the return
   * value from the original function.
   */

  static createSequence(original, sequence, thisObj) {
    return (...args) => {
      const origResult = original.call(thisObj, ...args),
            sequenceResult = sequence.call(thisObj, ...args);
      return sequenceResult === undefined ? origResult : sequenceResult;
    };
  }
  /**
   * Create a "debounced" function which will call on the "leading edge" of a timer period.
   * When first invoked will call immediately, but invocations after that inside its buffer
   * period will be rejected, and *one* invocation will be made after the buffer period has expired.
   *
   * This is useful for responding immediately to a first mousemove, but from then on, only
   * calling the action function on a regular timer while the mouse continues to move.
   *
   * @param {Function} fn The function to call.
   * @param {Number} buffer The milliseconds to wait after each execution before another execution takes place.
   * @param {Object} [thisObj] `this` reference for the function.
   * @param {Array} [extraArgs] The argument list to append to those passed to the function.
   * @param {Function} [alt] A function to call when the invocation is rejected due to buffer time not having expired.
   */

  static createThrottled(fn, buffer, thisObj, extraArgs, alt) {
    let lastCallTime = 0,
        callArgs,
        timerId,
        result;

    const invoke = () => {
      timerId = 0;
      lastCallTime = performance.now();
      callArgs.push.apply(callArgs, extraArgs);
      fn.apply(thisObj, callArgs);
    };

    result = function (...args) {
      let elapsed = performance.now() - lastCallTime;
      callArgs = args; // If it's been more then the buffer period since we invoked, we can call it now

      if (elapsed >= buffer) {
        clearTimeout(timerId);
        invoke();
      } // Otherwise, kick off a timer for the requested period.
      else {
          if (!timerId) {
            timerId = setTimeout(invoke, buffer - elapsed);
          }

          if (alt) {
            callArgs.push.apply(callArgs, extraArgs);
            alt.apply(thisObj, callArgs);
          }
        }
    };

    result.cancel = () => clearTimeout(timerId);

    return result;
  }
  /**
   * Create a "debounced" function which will call on the "trailing edge" of a timer period.
   * When first invoked will wait until the buffer period has expired to call the function, and
   * more calls within that time will restart the timer.
   *
   * This is useful for responding to keystrokes, but deferring action until the user pauses typing.
   *
   * @param {Function} fn The function to call.
   * @param {Number} buffer The milliseconds to wait after each execution before another execution takes place.
   * @param {Object} [thisObj] `this` reference for the function.
   * @param {Array} [args] The argument list to append to those passed to the function.
   */

  static createBuffered(fn, buffer, thisObj, args) {
    let callArgs, timerId, result;

    const invoke = () => {
      timerId = 0;
      result.isPending = false;
      callArgs.push.apply(callArgs, args);
      fn.apply(thisObj, callArgs);
    };

    result = function (...args) {
      callArgs = args; // Cancel any impending invocation. It's pushed out for <buffer> ms from each call

      if (timerId) {
        clearTimeout(timerId);
      }

      result.isPending = true;
      timerId = setTimeout(invoke, buffer);
    };

    result.cancel = () => {
      result.isPending = false;
      clearTimeout(timerId);
    };

    return result;
  }

  static hookMethod(object, method, hook) {
    hook.$nextHook = object[method];
    object[method] = hook;
    return () => {
      // Object will have no hooks on the instance if it is destroyed (perhaps other reasons too)
      if (hasOwnProperty$3.call(object, method)) {
        let f = object[method],
            next;

        if (f === hook) {
          var _Object$getPrototypeO;

          // When this is the outermost hook, we may be the last hook. If $nextHook is found on the object's
          // prototype, simply delete the slot to expose it. Otherwise, there's another hook, so make it the
          // outermost.
          if (((_Object$getPrototypeO = Object.getPrototypeOf(object)) === null || _Object$getPrototypeO === void 0 ? void 0 : _Object$getPrototypeO[method]) === hook.$nextHook) {
            delete object[method];
          } else {
            object[method] = hook.$nextHook;
          }
        } else {
          // Not being the outermost hook means we have outer hooks that should chain to the one we want to
          // remove. Be cautious because the object could be destroyed.
          for (; next = (_f = f) === null || _f === void 0 ? void 0 : _f.$nextHook; f = next) {
            var _f;

            if (next === hook) {
              f.$nextHook = hook.$nextHook;
              break;
            }
          }
        }
      }
    };
  }

  static returnTrue() {
    return true;
  }

  static animate(duration, fn, thisObj, easing = 'linear') {
    let cancel = false;
    const result = new Promise(resolve => {
      const start = performance.now(),
            iterate = () => {
        const progress = Math.min((performance.now() - start) / duration, 1),
              delayable = thisObj && thisObj.setTimeout ? thisObj : window;

        if (!cancel) {
          if (fn.call(thisObj, this.easingFunctions[easing](progress)) === false) {
            resolve();
          }
        }

        if (cancel || progress === 1) {
          // Push resolution into the next animation frame so that
          // this frame completes before the resolution handler runs.
          delayable.requestAnimationFrame(() => resolve());
        } else {
          delayable.requestAnimationFrame(iterate);
        }
      };

      iterate();
    });

    result.cancel = () => {
      cancel = true;
      return false;
    };

    return result;
  }

}
/* eslint-disable */

const half = 0.5,
      e1 = 1.70158,
      e2 = 7.5625,
      e3 = 1.525,
      e4 = 2 / 2.75,
      e5 = 2.25 / 2.75,
      e6 = 1 / 2.75,
      e7 = 1.5 / 2.75,
      e8 = 2.5 / 2.75,
      e9 = 2.625 / 2.75,
      e10 = 0.75,
      e11 = 0.9375,
      e12 = 0.984375;
FunctionHelper.easingFunctions = {
  linear: t => t,
  easeInQuad: t => Math.pow(t, 2),
  easeOutQuad: t => -(Math.pow(t - 1, 2) - 1),
  easeInOutQuad: t => (t /= half) < 1 ? half * Math.pow(t, 2) : -half * ((t -= 2) * t - 2),
  easeInCubic: t => Math.pow(t, 3),
  easeOutCubic: t => Math.pow(t - 1, 3) + 1,
  easeInOutCubic: t => (t /= half) < 1 ? half * Math.pow(t, 3) : half * (Math.pow(t - 2, 3) + 2),
  easeInQuart: t => Math.pow(t, 4),
  easeOutQuart: t => -(Math.pow(t - 1, 4) - 1),
  easeInOutQuart: t => (t /= half) < 1 ? half * Math.pow(t, 4) : -half * ((t -= 2) * Math.pow(t, 3) - 2),
  easeInQuint: t => Math.pow(t, 5),
  easeOutQuint: t => Math.pow(t - 1, 5) + 1,
  easeInOutQuint: t => (t /= half) < 1 ? half * Math.pow(t, 5) : half * (Math.pow(t - 2, 5) + 2),
  easeInSine: t => -Math.cos(t * (Math.PI / 2)) + 1,
  easeOutSine: t => Math.sin(t * (Math.PI / 2)),
  easeInOutSine: t => -half * (Math.cos(Math.PI * t) - 1),
  easeInExpo: t => t === 0 ? 0 : Math.pow(2, 10 * (t - 1)),
  easeOutExpo: t => t === 1 ? 1 : -Math.pow(2, -10 * t) + 1,
  easeInOutExpo: t => t === 0 ? 0 : t === 1 ? 1 : (t /= half) < 1 ? half * Math.pow(2, 10 * (t - 1)) : half * (-Math.pow(2, -10 * --t) + 2),
  easeInCirc: t => -(Math.sqrt(1 - t * t) - 1),
  easeOutCirc: t => Math.sqrt(1 - Math.pow(t - 1, 2)),
  easeInOutCirc: t => (t /= half) < 1 ? -half * (Math.sqrt(1 - t * t) - 1) : half * (Math.sqrt(1 - (t -= 2) * t) + 1),
  easeOutBounce: t => t < e6 ? e2 * t * t : t < e4 ? e2 * (t -= e7) * t + e10 : t < e8 ? e2 * (t -= e5) * t + e11 : e2 * (t -= e9) * t + e12,
  easeInBack: t => t * t * ((e1 + 1) * t - e1),
  easeOutBack: t => (t = t - 1) * t * ((e1 + 1) * t + e1) + 1,
  easeInOutBack: t => {
    let s = 1.70158;
    return (t /= half) < 1 ? half * (t * t * (((s *= e3) + 1) * t - s)) : half * ((t -= 2) * t * (((s *= e3) + 1) * t + s) + 2);
  },
  elastic: t => -1 * Math.pow(4, -8 * t) * Math.sin((t * 6 - 1) * (2 * Math.PI) / 2) + 1,
  swingFromTo: t => {
    let s = 1.70158;
    return (t /= half) < 1 ? half * (t * t * (((s *= e3) + 1) * t - s)) : half * ((t -= 2) * t * (((s *= e3) + 1) * t + s) + 2);
  },
  swingFrom: t => t * t * ((e1 + 1) * t - e1),
  swingTo: t => (t -= 1) * t * ((e1 + 1) * t + e1) + 1,
  bounce: t => t < e6 ? e2 * t * t : t < e4 ? e2 * (t -= e7) * t + e10 : t < e8 ? e2 * (t -= e5) * t + e11 : e2 * (t -= e9) * t + e12,
  bouncePast: t => t < e6 ? e2 * t * t : t < e4 ? 2 - (e2 * (t -= e7) * t + e10) : t < e8 ? 2 - (e2 * (t -= e5) * t + e11) : 2 - (e2 * (t -= e9) * t + e12),
  easeFromTo: t => (t /= half) < 1 ? half * Math.pow(t, 4) : -half * ((t -= 2) * Math.pow(t, 3) - 2),
  easeFrom: t => Math.pow(t, 4),
  easeTo: t => Math.pow(t, 0.25)
};
FunctionHelper._$name = 'FunctionHelper';

/**
 * @module Core/mixin/Events
 */

const // Used by the config system to flatten configs from the class hierrchy.
// In this case, a pure merge is not wanted. Listener definitions from
// every class level are collected up into an array.
// addListener iterates any passed array, adding each element.
{
  isArray
} = Array,
      // Used to distinguish event names from listener options in addListener object config.
specialProperties = {
  thisObj: 1,
  detachable: 1,
  once: 1,
  detacher: 1,
  prio: 1,
  args: 1,
  expires: 1,
  name: 1,
  _oldDestructor: 1
},
      priorityComparator = (a, b) => b.prio - a.prio;
/**
 * Mix this into another class to enable event handling.
 *
 * ## Basic usage
 * Listeners can be added either through config:
 *
 * ```javascript
 * let button = new Button({
 *   listeners: {
 *     click: () => {},
 *     press: () => {},
 *     ...
 *   }
 * });
 * ```
 *
 * *NOTE*: Do not reuse listeners config object, use new every time:
 * ```javascript
 * // wrong
 * let config = { click : () => {} }
 * new Button({
 *     listeners : config
 * })
 * new Button({
 *     listeners : config
 * })
 * // right
 * new Button({
 *     listeners : { click : () => {} }
 * })
 * new Button({
 *     listeners : { click : () => {} }
 * })
 * ```
 *
 * Or by calling on()/addListener():
 *
 * ```javascript
 * let button = new Button();
 *
 * button.addListener('press', () => {});
 * // on is an alias for addListener
 * button.on('click', () => {});
 * ```
 *
 * This style also accepts multiple listeners in same way as when using config:
 *
 * ```javascript
 * button.on({
 *   click: () => {},
 *   press: () => {},
 *   ...
 * });
 * ```
 *
 * ## Options
 * ### Once
 * Listeners can be configured to automatically deregister after first trigger by specifing config option `once`:
 *
 * ```javascript
 * button.on({
 *   click: () => {},
 *   once: true
 * });
 * ```
 *
 * ### Priority
 * Specifying priority affects the order in which listeners are called when triggering an event. Higher prios will be
 * called before lower. Default value is 0.
 *
 * ```javascript
 * button.on({
 *   click: this.onClick,
 *   prio: 1
 * });
 * ```
 *
 * ### This reference
 * If desired, you can specify thisObj when configuring listeners. There is no need if you are using arrow functions as
 * listeners, but might be handy in other cases. Of course, you can also use bind to set `this` reference.
 *
 * ```javascript
 * button.on({
 *   click: this.onClick,
 *   thisObj: this
 * });
 *
 * // or
 *
 * button.on({
 *   click: this.onClick.bind(this)
 * });
 * ```
 *
 * ### Detacher
 * A convenient way of unregistering events is to use a detacher, a function returned when adding listeners that you
 * call later to deregister them. As of version 1.0, detachable defaults to true.
 *
 * ```javascript
 * let detacher = button.on({
 *   click: () => {},
 *   press: () => {},
 *   detachable: true
 * });
 *
 * // when you want to detach, for example in destroy()
 * detacher();
 * ```
 *
 * ### Auto detaching
 * When listeners are bound to a class instance using `thisObj`, the `thisObj`'s `doDestroy` method
 * is overridden to remove the listeners before calling the overridden doDestroy.
 *
 * ```javascript
 * class MyClass extends Base {
 *   construct() {
 *     let button = new Button({
 *       listeners: {
 *         click: () => {},
 *         thisObj: this
 *       }
 *     });
 *   }
 *
 *   doDestroy() {
 *     // clean up stuff
 *   }
 * }
 *
 * let myObj = new MyClass();
 * // clean up, also removes listeners
 * myObj.destroy();
 * ```
 *
 * ### On-functions
 * When mixing Events into another class it can be configured to call on-functions when events are triggered.
 * On-functions are functions named 'onEventname', for example 'onClick', 'onPress' declared on the class triggering
 * the event.
 *
 * ```javascript
 * // mix Events in with on-functions activated
 * let button = new Button({
 *   callOnFunctions: true,
 *
 *   onClick: () => {}
 * });
 *
 * // or add a getter in class declaration
 * ```
 *
 * ### Catching all events
 * By specifying a listener for {@link #event-catchAll catchAll} a function can be notified when any event is triggered:
 *
 * ```javascript
 * const button = new Button({
 *    listeners : {
 *        catchAll(event) {
 *            // All events on the button will pass through here
 *        }
 *    }
 * });
 * ```
 *
 * @mixin
 */

var Events = (Target => class Events extends (Target || Base) {
  static get $name() {
    return 'Events';
  } //region Events

  /**
   * Fires before an object is destroyed.
   * @event beforeDestroy
   * @param {Object} source The Object that is being destroyed.
   */

  /**
   * Fires when an object is destroyed.
   * @event destroy
   * @param {Object} source The Object that is being destroyed.
   */

  /**
   * Fires when any other event is fired from the object.
   *
   * **Note**: `catchAll` is fired for both public and private events. Please rely on the public events only.
   * @event catchAll
   * @param {Object} event The Object that contains event details
   * @param {String} event.type The type of the event which is caught by the listener
   */
  //endregion

  static get declarable() {
    return [
    /**
     * The list of deprecated events as an object, where `key` is an event name which is deprecated and
     * `value` is an object which contains values for
     * {@link Core.helper.VersionHelper#function-deprecate-static VersionHelper}:
     * - product {String} The name of the product;
     * - invalidAsOfVersion {String} The version where the offending code is invalid (when any compatibility
     *   layer is actually removed);
     * - message {String} Warning message to show to the developer using a deprecated API;
     *
     * For example:
     *
     * ```javascript
     * return {
     *     click : {
     *         product            : 'Grid',
     *         invalidAsOfVersion : '1.0.0',
     *         message            : 'click is deprecated!'
     *     }
     * }
     * ```
     *
     * @name deprecatedEvents
     * @returns {Object}
     * @static
     * @internal
     */
    'deprecatedEvents'];
  }

  static setupDeprecatedEvents(cls, meta) {
    const all = meta.getInherited('deprecatedEvents'),
          add = cls.deprecatedEvents;

    for (const eventName in add) {
      // Event names are case-insensitive so build our map using toLowerCase'd names (but keep true case too):
      all[eventName.toLowerCase()] = all[eventName] = add[eventName];
    }
  } //region Config

  static get configurable() {
    return {
      /**
       * The listener set for this object.
       *
       * Listeners can be specified in target class config and they will be merged with any
       * listeners specified in the instantiation config. Class listeners will be fired first:
       * ```
       * class MyStore extends Store({
       *     static get configurable() {
       *         listeners : {
       *             myCustomEvent() {
       *             },
       *             load : {
       *                 prio : 10000,
       *                 fn() { // this load listener handles things first }
       *             }
       *         }
       *     }
       * });
       *
       * let store = new MyStore({
       *   listeners: {
       *     load: () => { // This load listener runs after the class's },
       *     ...
       *   }
       * });
       * ```
       * @config {Object}
       * @category Common
       */
      listeners: {
        value: null,
        $config: {
          merge(newValue, currentValue) {
            if (newValue !== null) {
              if (!newValue) {
                return currentValue;
              }

              if (currentValue) {
                newValue = newValue ? [newValue] : [];
                newValue.push[isArray(currentValue) ? 'apply' : 'call'](newValue, currentValue);
              }
            }

            return newValue;
          }

        }
      }
    };
  }

  destroy() {
    this.trigger('beforeDestroy');
    super.destroy();
  } //endregion
  //region Init

  construct(config, ...args) {
    var _config;

    // Configured listeners use this as the thisObj
    if (this.configuredListeners
    /* assignment */
    = (_config = config) === null || _config === void 0 ? void 0 : _config.listeners) {
      config = { ...config
      };
      delete config.listeners;
    }

    super.construct(config, ...args); // Apply configured listeners after construction.
    // Note that some classes invoke this during parts of their construction.
    // Store invokes this prior to setting data so that observers are notified of data load.

    this.processConfiguredListeners();
  }

  processConfiguredListeners() {
    // This can only happen once
    if (this.configuredListeners) {
      const me = this,
            {
        isConfiguring
      } = me; // If called from config ingestion during configuration, listeners must be added
      // so temporarily clear the isConfiguring flag.

      me.isConfiguring = false;
      me.listeners = me.configuredListeners;
      me.configuredListeners = null;
      me.isConfiguring = isConfiguring;
    }
  }
  /**
   * Auto detaches listeners registered from start, if set as detachable
   * @internal
   */

  doDestroy() {
    this.trigger('destroy');
    this.removeAllListeners();
    super.doDestroy();
  }

  static setupClass(meta) {
    super.setupClass(meta);
    ['onListen', 'onUnlisten'].forEach(fn => Events.prototype[fn].$nullFn = true);
  } //endregion
  //region Listeners

  /**
   * Adds an event listener. This method accepts parameters in the following format:
   *
   * ```javascript
   *  myObject.on({
   *      thisObj    : this,          // The this reference for the handlers
   *      eventname2 : 'functionName' // Resolved at invocation time using the thisObj,
   *      otherevent : {
   *          fn      : 'handlerFnName',
   *          once    : true          // Just this handler is auto-removed on fire
   *      },
   *      yetanother  : {
   *          fn      : 'yetAnotherHandler',
   *          args    : [ currentState1, currentState2 ] // Capture info to be passed to handler
   *      },
   *      prio        : 100           // Higher prio listeners are called before lower
   *  });
   * ```
   *
   * When listeners have a `thisObj` option, they are linked to the lifecycle of that object.
   * When it is destroyed, those listeners are removed.
   *
   * A simpler signature may be used when only adding a listener for one event and no extra options
   * (such as `once` or `delay`) are required:
   *
   * ```javascript
   * myObject.on('click', myController.handleClicks, myController);
   * ```
   *
   * The args in this simple case are `eventName`, `handler` and `thisObj`
   *
   * @param {Object|String} config An object containing listener definitions.
   * @param {Object} [config.thisObj] The `this` reference for all listeners.
   * (May be overridden if a handler is specified in object form)
   * @param {Boolean} [config.once] Specify as `true` to remove the listener as soon as it is invoked.
   * @param {Number|Object} [config.expires] The listener only waits for a specified time before
   * being removed. The value may be a number or an object containing an expiry handler.
   * @param {Number} [config.expires.delay] How long to wait for the event for.
   * @param {String|Function} [config.expires.alt] The function to call when the listener expires
   * **without having been triggered**.
   * @param {Object[]} [config.args] An array of arguments to be passed to the handler before the event object.
   * @param {Number} [config.prio] The priority for all listeners; higher priority listeners are called before lower.
   * @param {Object|Function} [thisObj] Default `this` reference for all listeners in the config object.
   * @param {Object} [oldThisObj] The `this` reference if the old signature starting with a string event name is used..
   * @returns {Function} Returns a detacher function unless configured with `detachable: false`. Call detacher to remove listeners
   */

  addListener(config, thisObj, oldThisObj) {
    if (isArray(config)) {
      for (let i = 0, {
        length
      } = config; i < length; i++) {
        this.addListener(config[i], thisObj);
      }

      return;
    }

    const me = this,
          deprecatedEvents = me.$meta.getInherited('deprecatedEvents');

    if (typeof config === 'string') {
      // arguments[2] is thisObj if (eventname, handler, thisObj) form called.
      // Note that the other side of the if compares to undefined, so this will work.
      return me.addListener({
        [config]: thisObj,
        detachable: thisObj.detachable !== false,
        thisObj: oldThisObj
      });
    } else {
      // Capture the default thisObj.
      thisObj = config.thisObj !== undefined ? config.thisObj : thisObj;

      for (const key in config) {
        if (!specialProperties[key]) {
          // comparing should be case insensitive
          const // comparing should be case insensitive
          eventName = key.toLowerCase(),
                deprecatedEvent = deprecatedEvents === null || deprecatedEvents === void 0 ? void 0 : deprecatedEvents[eventName],
                events = me.eventListeners || (me.eventListeners = {}),
                listenerSpec = config[key],
                expires = listenerSpec.expires || config.expires,
                listener = {
            fn: typeof listenerSpec === 'object' ? listenerSpec.fn : listenerSpec,
            thisObj: listenerSpec.thisObj !== undefined ? listenerSpec.thisObj : thisObj,
            args: listenerSpec.args || config.args,
            prio: listenerSpec.prio !== undefined ? listenerSpec.prio : config.prio !== undefined ? config.prio : 0,
            once: listenerSpec.once !== undefined ? listenerSpec.once : config.once !== undefined ? config.once : false,
            catchAll: key === 'catchAll'
          };

          if (deprecatedEvent) {
            const {
              product,
              invalidAsOfVersion,
              message
            } = deprecatedEvent;
            VersionHelper.deprecate(product, invalidAsOfVersion, message);
          }

          if (expires) {
            // Extract expires : { delay : 100, alt : 'onExpireFn' }
            const delayable = me.isDelayable ? me : window,
                  {
              alt
            } = expires,
                  delay = alt ? expires.delay : expires;
            delayable[typeof delay === 'number' ? 'setTimeout' : 'requestAnimationFrame'](() => {
              me.un(eventName, listener); // If we make it here and the handler has not been called, invoke the alt handler

              if (alt && !listener.called) {
                me.callback(alt, thisObj);
              }
            }, delay);
          }

          let listeners = events[eventName] || (events[eventName] = []);

          if (listeners.$firing) {
            events[eventName] = listeners = listeners.slice();
          } // Insert listener directly in prio order

          listeners.splice(ArrayHelper.findInsertionIndex(listener, listeners, priorityComparator, listeners.length), 0, listener);

          if (!me.onListen.$nullFn && listeners.length < 2) {
            me.onListen(eventName);
          }
        }
      }

      if (config.relayAll) {
        me.relayAll(config.relayAll);
      } // Hook into the thisObj's destruction sequence to remove these listeners.
      // Pass the default thisObj in for use when it comes to destruction time.

      if (thisObj) {
        me.attachAutoDetacher(config, thisObj);
      }

      const detachable = config.detachable !== false,
            name = config.name,
            destroy = config.expires || detachable || name ? () => {
        // drop listeners if not destroyed yet
        if (!me.isDestroyed) {
          me.removeListener(config, thisObj);
        }
      } : null;

      if (destroy) {
        var _thisObj;

        destroy.eventer = me;
        destroy.listenerName = name;

        if (name && (_thisObj = thisObj) !== null && _thisObj !== void 0 && _thisObj.trackDetacher) {
          thisObj.trackDetacher(name, destroy);
        }

        if (config.expires) {
          me.delay(destroy, config.expires);
        }

        if (detachable) {
          return destroy;
        }
      }
    }
  }
  /**
   * Alias for {@link #function-addListener}. Adds an event listener. This method accepts parameters in the following format:
   *
   * ```javascript
   *  myObject.on({
   *      thisObj    : this,          // The this reference for the handlers
   *      eventname2 : 'functionName' // Resolved at invocation time using the thisObj,
   *      otherevent : {
   *          fn      : 'handlerFnName',
   *          once    : true          // Just this handler is auto-removed on fire
   *      },
   *      yetanother  : {
   *          fn      : 'yetAnotherHandler',
   *          args    : [ currentState1, currentState2 ] // Capture info to be passed to handler
   *      },
   *      prio        : 100           // Higher prio listeners are called before lower
   *  });
   * ```
   *
   * When listeners have a `thisObj` option, they are linked to the lifecycle of that object.
   * When it is destroyed, those listeners are removed.
   *
   * A simpler signature may be used when only adding a listener for one event and no extra options
   * (such as `once` or `delay`) are required:
   *
   * ```javascript
   * myObject.on('click', myController.handleClicks, myController);
   * ```
   *
   * The args in this simple case are `eventName`, `handler` and `thisObj`
   *
   * @param {Object|String} config An object containing listener definitions.
   * @param {Object} [config.thisObj] The `this` reference for all listeners.
   * (May be overridden if a handler is specified in object form)
   * @param {Boolean} [config.once] Specify as `true` to remove the listener as soon as it is invoked.
   * @param {Number|Object} [config.expires] The listener only waits for a specified time before
   * being removed. The value may be a number or an object containing an expiry handler.
   * @param {Number} [config.expires.delay] How long to wait for the event for.
   * @param {String|Function} [config.expires.alt] The function to call when the listener expires
   * **without having been triggered**.
   * @param {Object[]} [config.args] An array of arguments to be passed to the handler before the event object.
   * @param {Object|Function} [thisObj] Default `this` reference for all listeners in the config object.
   * @param {Object} [oldThisObj] The `this` reference if the old signature starting with a string event name is used..
   * @returns {Function} Returns a detacher function unless configured with `detachable: false`. Call detacher to remove listeners
   */

  on(...args) {
    return this.addListener(...args);
  }
  /**
   * Shorthand for {@link #function-removeListener}
   * @param {Object} config Listeners
   * @param {Object} [thisObj] `this` reference for all listeners
   */

  un(...args) {
    this.removeListener(...args);
  }

  get listeners() {
    return this.eventListeners;
  }

  changeListeners(listeners) {
    // If we are receiving class listeners, add them early, and they do not become
    // the configured listeners, and are not removed by setting listeners during the lifecycle.
    if (this.isConfiguring) {
      if (listeners) {
        this.on(listeners, this);
      }
    } // Setting listeners after config time clears the old set and adds the new.
    // This will initially happen at the tail end of the constructor when config
    // listeners are set.
    else {
        // Configured listeners use this as the thisObj by default.
        // Flatten using Objects.assign because it may have been part of
        // a prototype chained default configuration of another object.
        // eg: the tooltip config of a Widget.
        if (listeners && !('thisObj' in listeners)) {
          listeners = Objects.assign({
            thisObj: this
          }, listeners);
        }

        return listeners;
      }
  }

  updateListeners(listeners, oldListeners) {
    // Only configured listeners get here. Class listeners are added by changeListeners.
    oldListeners && this.un(oldListeners);
    listeners && this.on(listeners);
  }
  /**
   * Removes an event listener. Same API signature as {@link #function-addListener}
   * @param {Object} config Listeners
   * @param {Object} thisObj `this` reference for all listeners
   */

  removeListener(config, thisObj = config.thisObj, arg2) {
    const me = this;

    if (typeof config === 'string') {
      return me.removeListener({
        [config]: thisObj
      }, arg2);
    }

    Object.entries(config).forEach(([eventName, listenerToRemove]) => {
      if (!specialProperties[eventName]) {
        eventName = eventName.toLowerCase();
        const eventListeners = me.eventListeners,
              index = me.findListener(eventName, listenerToRemove, thisObj);

        if (index >= 0) {
          let listeners = eventListeners[eventName];

          if (listeners.length > 1) {
            if (listeners.$firing) {
              eventListeners[eventName] = listeners = listeners.slice();
            } // NOTE: we cannot untrack any detachers here because we may only be
            // removing some of its listeners

            listeners.splice(index, 1);
          } else {
            delete eventListeners[eventName];

            if (!me.onUnlisten.$nullFn) {
              me.onUnlisten(eventName);
            }
          }
        }
      }
    });

    if (config.thisObj && !config.thisObj.isDestroyed) {
      me.detachAutoDetacher(config);
    }
  }

  findListener(eventName, listenerToFind, defaultThisObj) {
    var _this$eventListeners;

    const eventListeners = (_this$eventListeners = this.eventListeners) === null || _this$eventListeners === void 0 ? void 0 : _this$eventListeners[eventName],
          fn = listenerToFind.fn || listenerToFind,
          thisObj = listenerToFind.thisObj || defaultThisObj;

    if (eventListeners) {
      for (let listenerEntry, i = 0, {
        length
      } = eventListeners; i < length; i++) {
        listenerEntry = eventListeners[i];

        if (listenerEntry.fn === fn && listenerEntry.thisObj === thisObj) {
          return i;
        }
      }
    }

    return -1;
  }
  /**
   * Check if any listener is registered for the specified eventName
   * @param {String} eventName
   * @returns {Boolean} `true` if listener is registered, otherwise `false`
   */

  hasListener(eventName) {
    var _this$eventListeners2;

    return Boolean((_this$eventListeners2 = this.eventListeners) === null || _this$eventListeners2 === void 0 ? void 0 : _this$eventListeners2[eventName === null || eventName === void 0 ? void 0 : eventName.toLowerCase()]);
  }
  /**
   * Relays all events through another object that also implements Events mixin. Adds a prefix to the event name
   * before relaying, for example add -> storeAdd
   * ```
   * // Relay all events from store through grid, will make it possible to listen for store events prefixed on grid:
   * 'storeLoad', 'storeChange', 'storeRemoveAll' etc.
   * store.relayAll(grid, 'store');
   *
   * grid.on('storeLoad', () => console.log('Store loaded');
   * ```
   * @param {Core.mixin.Events} through Object to relay the events through, needs to mix Events mixin in
   * @param {String} prefix Prefix to add to event name
   * @param {Boolean} [transformCase] Specify false to prevent making first letter of event name uppercase
   */

  relayAll(through, prefix, transformCase = true) {
    if (!this.relayAllTargets) {
      this.relayAllTargets = [];
    }

    const relayAllTargets = this.relayAllTargets;
    through.on('beforeDestroy', ({
      source
    }) => {
      if (source === through) {
        const configs = relayAllTargets.filter(r => r.through === through);
        configs.forEach(config => ArrayHelper.remove(relayAllTargets, config));
      }
    });
    relayAllTargets.push({
      through,
      prefix,
      transformCase
    });
  }
  /**
   * Removes all listeners registered to this object
   */

  removeAllListeners() {
    const listeners = this.eventListeners;
    let i, thisObj;

    for (const event in listeners) {
      const bucket = listeners[event]; // We iterate backwards since we call removeListener which will splice out of
      // this array as we go...

      for (i = bucket.length; i-- > 0;)
      /* empty */
      {
        var _thisObj2;

        const cfg = bucket[i];
        this.removeListener(event, cfg);
        thisObj = cfg.thisObj;

        if ((_thisObj2 = thisObj) !== null && _thisObj2 !== void 0 && _thisObj2.untrackDetachers) {
          thisObj.untrackDetachers(this);
        }
      }
    }
  }

  relayEvents(source, eventNames, prefix = '') {
    const listenerConfig = {
      detachable: true
    };
    eventNames.forEach(eventName => {
      listenerConfig[eventName] = (event, ...params) => {
        return this.trigger(prefix + event.type, event, ...params);
      };
    });
    return source.on(listenerConfig);
  }
  /**
   * This method is called when the first listener for an event is added.
   * @param {String} eventName
   * @internal
   */

  onListen() {}
  /**
   * This method is called when the last listener for an event is removed.
   * @param {String} eventName
   * @internal
   */

  onUnlisten() {}
  /**
   * Internal function used to hook destroy() calls when using thisObj
   * @private
   */

  attachAutoDetacher(config, thisObj) {
    const target = config.thisObj || thisObj,
          // If it's a Bryntum Base subclass, hook doDestroy, otherwise, destroy
    destructorName = 'doDestroy' in target ? 'doDestroy' : 'destroy';

    if (destructorName in target) {
      let {
        $autoDetachers
      } = target;

      if (!$autoDetachers) {
        target[destructorName] = FunctionHelper.createInterceptor(target[destructorName], () => {
          // Remove listeners first, so that they do not fire during destruction.
          // The observable being listened to by the thisObj may already have
          // been destroyed in a clean up sequence
          for (let i = 0; i < $autoDetachers.length; i++) {
            const {
              dispatcher,
              config
            } = $autoDetachers[i];

            if (!dispatcher.isDestroyed) {
              dispatcher.removeListener(config, target);
            }
          }
        }, target);
        target.$autoDetachers = $autoDetachers = [];
      }

      $autoDetachers.push({
        config,
        dispatcher: this
      });
    } else {
      target[destructorName] = () => {
        this.removeListener(config);
      };
    }
  }
  /**
   * Internal function used restore hooked destroy() calls when using thisObj
   * @private
   */

  detachAutoDetacher(config) {
    const target = config.thisObj,
          destructorName = 'doDestroy' in target ? 'doDestroy' : 'destroy';

    if (config._oldDestructor) {
      target[destructorName] = config._oldDestructor;
    }
  } //endregion
  //region Promise based workflow
  // experimental, used in tests to support async/await workflow

  await(eventName, options = {
    checkLog: true,
    resetLog: true,
    args: null
  }) {
    const me = this;

    if (options === false) {
      options = {
        checkLog: false
      };
    }

    const {
      args
    } = options;
    return new Promise(resolve => {
      var _me$_triggered;

      // check if previously triggered?
      if (options.checkLog && (_me$_triggered = me._triggered) !== null && _me$_triggered !== void 0 && _me$_triggered[eventName]) {
        // resolve immediately, no params though...
        resolve(); // reset log to be able to await again

        if (options.resetLog) {
          me.clearLog(eventName);
        }
      } // This branch will listen for events until catches one with specific arguments

      if (args) {
        const detacher = me.on({
          [eventName]: (...params) => {
            const argsOk = Object.keys(args).every(key => {
              return key in params[0] && params[0][key] === args[key];
            });

            if (argsOk) {
              // resolve when event is fired with required arguments
              resolve(...params); // reset log to be able to await again

              if (options.resetLog) {
                me.clearLog(eventName);
              }

              detacher();
            }
          },
          prio: -10000 // Let others do their stuff first

        });
      } else {
        me.on({
          [eventName]: (...params) => {
            // resolve when event is caught
            resolve(...params); // reset log to be able to await again

            if (options.resetLog) {
              me.clearLog(eventName);
            }
          },
          prio: -10000,
          // Let others do their stuff first
          once: true // promises can only be resolved once anyway

        });
      }
    });
  }

  clearLog(eventName) {
    if (this._triggered) {
      if (eventName) {
        delete this._triggered[eventName];
      } else {
        this._triggered = {};
      }
    }
  } //endregion
  //region Trigger

  /**
   * Wraps a function with event triggering. First triggers before[eventName] with the supplied eventObject. If it is
   * not prevented (by returning false) the supplied fn is called with the eventObject as first argument and any
   * optional arguments after that. It then triggers [eventName] with the eventObject (that might have been altered in
   * fn). Finally it returns the return value from the fn.
   * @param {String} eventName Event name, used to trigger 'beforeEventName' and 'eventName'
   * @param {Object} eventObject Passed when triggering events
   * @param {Function} fn Function to call if before is not prevented
   * @param {Array} args Arguments to pass to fn
   * @param {Boolean} quiet Specify true to not trigger events
   * @returns {*} Result returned from calling fn, or false if prevented
   * @internal
   */

  callPreventable(eventName, eventObject, fn, args = [], quiet = false) {
    const me = this;

    if (!quiet && me.trigger('before' + StringHelper.capitalize(eventName), eventObject) === false) {
      return false;
    }

    const result = fn(eventObject, ...args);

    if (!quiet) {
      me.trigger(eventName, eventObject);
    }

    return result;
  }
  /**
   * Triggers an event, calling all registered listeners with the supplied arguments. Returning false from any listener
   * makes function return false.
   * @param {String} eventName Event name for which to trigger listeners
   * @param {Object} [param] Single parameter passed on to listeners, source property will be added to it (this)
   * @param {Boolean} [param.bubbles] Pass as `true` to indicate that the event will bubble up the widget
   * ownership hierarchy. For example up a Menu->parent Menu tree, or a Field->Container tree.
   * @returns {Boolean} Returns false if any listener returned false, otherwise true
   */

  trigger(eventName, param) {
    var _me$eventListeners, _me$eventListeners2;

    // TODO: should returning false from a listener really prevent other listeners from executing?
    const me = this,
          name = eventName.toLowerCase(),
          {
      eventsSuspended,
      relayAllTargets,
      callOnFunctions,
      owner
    } = me;
    let listeners = (_me$eventListeners = me.eventListeners) === null || _me$eventListeners === void 0 ? void 0 : _me$eventListeners[name]; // log trigger, used by experimental promise support to resolve immediately when needed

    if (!me._triggered) {
      me._triggered = {};
    }

    me._triggered[eventName] = true;

    if (eventsSuspended) {
      if (eventsSuspended.shouldQueue) {
        eventsSuspended.queue.push(arguments);
      }

      return true;
    } // Include catchall listener for all events.
    // Do not push the catchAll listeners onto the events own listener array.

    if ((_me$eventListeners2 = me.eventListeners) !== null && _me$eventListeners2 !== void 0 && _me$eventListeners2.catchall) {
      (listeners = listeners ? listeners.slice() : []).push(...me.eventListeners.catchall); // The catchAll listeners must honour their prio settings.

      listeners.sort(priorityComparator);
    }

    if (!listeners && !relayAllTargets && !callOnFunctions) {
      return true;
    } // default to include source : this in param

    if (param) {
      if (!('source' in param)) {
        if (Object.isExtensible(param)) {
          param.source = me;
        } else {
          param = Object.setPrototypeOf({
            source: me
          }, param);
        }
      }
    } else {
      param = {
        source: me
      };
    } // Lowercased event name should be the "type" property in keeping with DOM events.

    if (param.type !== name) {
      // Create instance property because "type" is read only
      if (param.constructor !== Object) {
        Reflect.defineProperty(param, 'type', {
          get: () => name
        });
      } else {
        param.type = name;
      }
    }

    if (callOnFunctions) {
      const fnName = 'on' + StringHelper.capitalize(eventName);

      if (fnName in me) {
        me.callback(me[fnName], me, [param]); // See if the called function was injected into the instance
        // masking an implementation in the prototype.
        // we must call the class's implementation after the injected one.
        // unless it's an injected chained function, in which case it will have been called above.
        // Note: The handler may have resulted in destruction.

        if (!me.isDestroyed && Object.prototype.hasOwnProperty.call(me, fnName) && (!me.pluginFunctionChain || !me.pluginFunctionChain[fnName])) {
          const myProto = Object.getPrototypeOf(me);

          if (fnName in myProto) {
            myProto[fnName].call(me, param); // A handler may have resulted in destruction.

            if (me.isDestroyed) {
              return;
            }
          }
        }
      }
    }

    if (listeners) {
      let i = 0,
          listener,
          handler,
          ret; // Let add/removeListener know that we're using the array to protect against a situation where an event
      // listener changes the listeners when triggering the event.

      listeners.$firing = true; // If any listener resulted in our destruction, abort.

      for (i; i < listeners.length && !me.isDestroyed && ret !== false; i++) {
        listener = listeners[i];
        let thisObj = listener.thisObj; // Listeners that have thisObj are auto removed when thisObj is destroyed. If thisObj is destroyed from
        // a listener we might still end up here, since listeners are sliced and not affected by the removal

        if (!thisObj || !thisObj.isDestroyed) {
          // Flag for the expiry timer
          listener.called = true;

          if (listener.once) {
            me.removeListener(name, listener);
          }

          if (typeof listener.fn === 'string') {
            if (thisObj) {
              handler = thisObj[listener.fn];
            } // keep looking for the callback in the hierarchy

            if (!handler) {
              const result = me.resolveCallback(listener.fn);
              handler = result.handler;
              thisObj = result.thisObj;
            }
          } else {
            handler = listener.fn;
          } // If listener was declared with args, send our param after them

          if (listener.args) {
            ret = handler.call(thisObj || me, ...listener.args, param);
          } else {
            ret = handler.call(thisObj || me, param);
          }
        }
      }

      listeners.$firing = false;

      if (ret === false) {
        return ret;
      }
    } // relay all?

    if (relayAllTargets) {
      relayAllTargets.forEach(config => {
        let name = eventName;
        if (config.transformCase) name = StringHelper.capitalize(name);
        if (config.prefix) name = config.prefix + name;
        if (config.through.trigger(name, param) === false) return false;
      });
    } // Use DOM standard event property name to indicate that the event
    // bubbles up the owner axis.
    // False from any handler cancels the bubble.
    // The owner prop was cached above in case any handlers destroyed this object.
    // Must also avoid owner if any handlers destroyed the owner.

    if (param.bubbles && owner && !owner.isDestroyed) {
      return owner.trigger(eventName, param);
    }

    return true;
  }
  /**
   * Prevents events from being triggered until {@link #function-resumeEvents()} is called. Optionally queues events that are triggered while
   * suspended. Multiple calls stack to require matching calls to `resumeEvents()` before actually resuming.
   * @param {Boolean} queue Specify true to queue events triggered while suspended
   */

  suspendEvents(queue = false) {
    const eventsSuspended = this.eventsSuspended || (this.eventsSuspended = {
      shouldQueue: queue,
      queue: [],
      count: 0
    });
    eventsSuspended.count++;
  }
  /**
   * Resume event triggering after a call to {@link #function-suspendEvents()}. If any triggered events were queued they will be triggered.
   * @returns `true` if events have been resumed (multiple calls to suspend require an equal number of resume calls to resume).
   */

  resumeEvents() {
    const suspended = this.eventsSuspended;

    if (suspended) {
      if (--suspended.count === 0) {
        this.eventsSuspended = null;

        if (suspended.shouldQueue) {
          for (const queued of suspended.queue) {
            this.trigger(...queued);
          }
        }
      }
    }

    return !Boolean(this.eventsSuspended);
  } //endregion

});

/**
 * @module Core/helper/AjaxHelper
 */

const paramValueRegExp = /^(\w+)=(.*)$/,
      // For CodePens, adjust URLs to be absolute
resourceRoot = BrowserHelper.queryString.__resourceRoot || '',
      parseParams = function (paramString) {
  const result = {},
        params = paramString.split('&'); // loop through each 'filter={"field":"name","operator":"=","value":"Sweden","caseSensitive":true}' string
  // So we cannot use .split('=')

  for (const nameValuePair of params) {
    const [match, name, value] = paramValueRegExp.exec(nameValuePair),
          decodedName = decodeURIComponent(name),
          decodedValue = decodeURIComponent(value);

    if (match) {
      let paramValue = result[decodedName];

      if (paramValue) {
        if (!Array.isArray(paramValue)) {
          paramValue = result[decodedName] = [paramValue];
        }

        paramValue.push(decodedValue);
      } else {
        result[decodedName] = decodedValue;
      }
    }
  }

  return result;
};
/**
 * Simplifies Ajax requests. Uses fetch & promises.
 *
 * ```javascript
 * AjaxHelper.get('some-url').then(response => {
 *     // process request response here
 * });
 * ```
 *
 * Uploading file to server via FormData interface.
 * Please visit <https://developer.mozilla.org/en-US/docs/Web/API/FormData> for details.
 *
 * ```javascript
 * const formData = new FormData();
 * formData.append('file', 'fileNameToUpload');
 * AjaxHelper.post('file-upload-url', formData).then(response => {
 *     // process request response here
 * });
 * ```
 *
 */

class AjaxHelper {
  /**
   * Make a request (using GET) to the specified url.
   * @param {String} url Url
   * @param {Object} [options] The options for the `fetch` API. Please see <https://developer.mozilla.org/en-US/docs/Web/API/WindowOrWorkerGlobalScope/fetch> for details
   * @param {Object} [options.queryParams] A key-value pair Object containing the params to add to the query string
   * @param {Object} [options.headers] Any headers you want to add to your request, contained within a `Headers object or an object literal with ByteString values
   * @param {Object} [options.body] Any body that you want to add to your request: this can be a Blob, BufferSource, FormData, URLSearchParams, or USVString object. Note that a request using the GET or HEAD method cannot have a body.
   * @param {Object} [options.mode] The mode you want to use for the request, e.g., cors, no-cors, or same-origin.
   * @param {Object} [options.credentials] The request credentials you want to use for the request: omit, same-origin, or include. To automatically send cookies for the current domain, this option must be provided
   * @param {Object} [options.parseJson] Specify `true` to parses the response and attach the resulting object to the `Response` object as `parsedJson`
   * @returns {Promise} The fetch Promise, which can be aborted by calling a special `abort` method
   */
  static get(url, options) {
    return this.fetch(url, options);
  }
  /**
   * POST data to the specified URL.
   * @param {String} url The URL
   * @param {String|Object|FormData} payload The data to post. If an object is supplied, it will be stringified
   * @param {Object} options The options for the `fetch` API. Please see <https://developer.mozilla.org/en-US/docs/Web/API/WindowOrWorkerGlobalScope/fetch> for details
   * @param {Object} [options.queryParams] A key-value pair Object containing the params to add to the query string
   * @param {Object} [options.headers] Any headers you want to add to your request, contained within a `Headers object or an object literal with ByteString values
   * @param {Object} [options.body] Any body that you want to add to your request: this can be a Blob, BufferSource, FormData, URLSearchParams, or USVString object. Note that a request using the GET or HEAD method cannot have a body.
   * @param {Object} [options.mode] The mode you want to use for the request, e.g., cors, no-cors, or same-origin.
   * @param {Object} [options.credentials] The request credentials you want to use for the request: omit, same-origin, or include. To automatically send cookies for the current domain, this option must be provided
   * @param {Object} [options.parseJson] Specify `true` to parses the response and attach the resulting object to the `Response` object as `parsedJson`
   * @returns {Promise} The fetch Promise, which can be aborted by calling a special `abort` method
   */

  static post(url, payload, options = {}) {
    if (!(payload instanceof FormData) && !(typeof payload === 'string')) {
      payload = JSON.stringify(payload);
      options.headers = options.headers || {};
      options.headers['Content-Type'] = options.headers['Content-Type'] || 'application/json';
    }

    return this.fetch(url, Object.assign({
      method: 'POST',
      body: payload
    }, options));
  }
  /**
   * Fetch the specified resource using the `fetch` API.
   * @param {String} url object to fetch
   * @param {Object} options The options for the `fetch` API. Please see <https://developer.mozilla.org/en-US/docs/Web/API/WindowOrWorkerGlobalScope/fetch> for details
   * @param {Object} [options.method] The request method, e.g., GET, POST
   * @param {Object} [options.queryParams] A key-value pair Object containing the params to add to the query string
   * @param {Object} [options.headers] Any headers you want to add to your request, contained within a `Headers object or an object literal with ByteString values
   * @param {Object} [options.body] Any body that you want to add to your request: this can be a Blob, BufferSource, FormData, URLSearchParams, or USVString object. Note that a request using the GET or HEAD method cannot have a body.
   * @param {Object} [options.mode] The mode you want to use for the request, e.g., cors, no-cors, or same-origin.
   * @param {Object} [options.credentials] The request credentials you want to use for the request: omit, same-origin, or include. To automatically send cookies for the current domain, this option must be provided
   * @param {Object} [options.parseJson] Specify `true` to parses the response and attach the resulting object to the `Response` object as `parsedJson`
   * @returns {Promise} The fetch Promise, which can be aborted by calling a special `abort` method
   */

  static fetch(url, options = {}) {
    const controller = new AbortController(); // Need to assign AbortController individually, because IE11 polyfill of the Fetch API deletes 'signal' property on fetchOptions object

    Object.assign(options, {
      abortController: controller,
      signal: controller.signal
    });

    if (!('credentials' in options)) {
      options.credentials = 'include';
    }

    if (options.queryParams) {
      const params = Object.entries(options.queryParams);

      if (params.length) {
        url += (url.includes('?') ? '&' : '?') + params.map(([param, value]) => `${param}=${encodeURIComponent(value)}`).join('&');
      }
    } // Promise that will be resolved either when network request is finished or when json is parsed

    const promise = new Promise((resolve, reject) => {
      fetch(resourceRoot + url, options).then(response => {
        if (options.parseJson) {
          response.json().then(json => {
            response.parsedJson = json;
            resolve(response);
          }).catch(error => {
            response.parsedJson = null;
            response.error = error;
            reject(response);
          });
        } else {
          resolve(response);
        }
      }).catch(error => {
        error.stack = promise.stack; // Unless calling code aborted the request, reject this operation

        if (error.name !== 'AbortError') {
          reject(error);
        }
      });
    });
    promise.stack = new Error().stack;

    promise.abort = function () {
      controller.abort();
    };

    return promise;
  }
  /**
   * Registers the passed URL to return the passed mocked up Fetch Response object to the
   * AjaxHelper's promise resolve function.
   * @param {String} url The url to return mock data for
   * @param {Object|Function} response A mocked up Fetch Response object which must contain
   * at least a `responseText` property, or a function to which the `url` and a `params` object
   * and the `Fetch` `options` object is passed which returns that.
   * @param {String} response.responseText The data to return.
   * @param {Boolean} [response.synchronous] resolve the Promise immediately
   * @param {Number} [response.delay=100] resolve the Promise after this number of milliseconds.
   */

  static mockUrl(url, response) {
    const me = this;
    (me.mockAjaxMap || (me.mockAjaxMap = {}))[url] = response; // Inject the override into the AjaxHelper instance

    if (!AjaxHelper.originalFetch) {
      AjaxHelper.originalFetch = AjaxHelper.fetch;
    }

    AjaxHelper.fetch = me.mockAjaxFetch.bind(me);
  }

  static mockAjaxFetch(url, options) {
    let urlAndParams = url.split('?'),
        result = this.mockAjaxMap[urlAndParams[0]],
        parsedJson = null;

    if (result) {
      if (typeof result === 'function') {
        result = result(urlAndParams[0], urlAndParams[1] && parseParams(urlAndParams[1]), options);
      }

      try {
        parsedJson = options.parseJson && JSON.parse(result.responseText);
      } catch (error) {
        parsedJson = null;
        result.error = error;
      }

      result = Object.assign({
        status: 200,
        ok: true,
        headers: new Headers(),
        statusText: 'OK',
        url: url,
        parsedJson: parsedJson,
        text: () => new Promise(resolve => {
          resolve(result.responseText);
        }),
        json: () => new Promise(resolve => {
          resolve(parsedJson);
        })
      }, result);
      return new Promise(function (resolve, reject) {
        if (result.synchronous) {
          resolve(result);
        } else {
          setTimeout(function () {
            resolve(result);
          }, 'delay' in result ? result.delay : 100);
        }
      });
    } else {
      return AjaxHelper.originalFetch(url, options);
    }
  }

}
AjaxHelper._$name = 'AjaxHelper';

/**
 * @module Core/localization/LocaleHelper
 */

/**
 * Provides locale management methods.
 */
class LocaleHelper {
  /**
   * Merges all properties of provided locales into new locale.
   * Locales are merged in order they provided and locales wich go later replace same properties of previous locales.
   * @param {...Object} locales Locales to merge
   * @return {Object} Merged locale
   */
  static mergeLocales(...locales) {
    const result = {};
    locales.forEach(locale => {
      Object.keys(locale).forEach(key => {
        if (typeof locale[key] === 'object') {
          result[key] = { ...result[key],
            ...locale[key]
          };
        } else {
          result[key] = locale[key];
        }
      });
    });
    return result;
  }
  /**
   * Removes all properties from `locale` that are present in the provided `trimLocale`.
   * @param {Object} locale locales to merge
   * @param {Object} trimLocale locales to merge
   */

  static trimLocale(locale, trimLocale) {
    const remove = (key, subKey) => {
      if (!locale[key]) {
        throw new Error(`Key "${key}" doesn't exist in locale`);
      }

      if (subKey) {
        if (!locale[key][subKey]) {
          throw new Error(`SubKey "${key}.${subKey}" doesn't exist in locale`);
        }

        delete locale[key][subKey];
      } else {
        delete locale[key];
      }
    };

    Object.keys(trimLocale).forEach(key => {
      if (Array.isArray(trimLocale[key])) {
        trimLocale[key].forEach(subKey => remove(key, subKey));
      } else {
        remove(key);
      }
    });
  }
  /**
   * Put the locale under `window.bryntum.locales` to make sure it can be discovered automatically
   * @param {String} localeName Locale name
   * @param {Object} config Locale config
   */

  static publishLocale(localeName, config) {
    const bryntum = window.bryntum = window.bryntum || {},
          locales = bryntum.locales = bryntum.locales || {}; // Avoid registering locales twice

    locales[localeName] = !locales[localeName] ? config : this.mergeLocales(locales[localeName], config);
  }

}
LocaleHelper._$name = 'LocaleHelper';

/**
 * Singleton that handles switching locale. Locales can be included on page with `<script type="module">` tags or
 * loaded using ajax. When using script tags the first locale loaded is used per default, if another should be used as
 * default specify it on the script tag for the grid (see example below).
 *
 * ```
 * // Using Ecma 6 modules
 * &lt;script type="module" src="./Core/localization/SvSE.js"&gt;
 *
 * // Specify default when using scripts
 * &lt;script src="build/locales/grid.locale.SvSE.js"&gt;
 * &lt;script data-default-locale="En" src="build/grid-all.js"&gt;
 *
 * import LocaleManager from 'Core/localization/LocaleManager.js';
 * // Set locale using method
 * LocaleManager.applyLocale('SvSE');
 * // Or set locale using property
 * LocaleManager.locale = LocaleManager.locales.SvSE;
 * ```
 *
 * @demo Grid/localization
 * @singleton
 */

class LocaleManager extends Events(Base) {
  static get defaultConfig() {
    return {
      locales: {},
      // Enable strict locale checking by default for tests
      throwOnMissingLocale: VersionHelper.isTestEnv
    };
  }

  construct(...args) {
    const me = this;
    super.construct(...args);
    const scriptTag = document.querySelector('script[data-default-locale]');

    if (scriptTag) {
      me.defaultLocaleName = scriptTag.dataset.defaultLocale;
    }

    if (BrowserHelper.isBrowserEnv && window.bryntum && window.bryntum.locales) {
      Object.keys(window.bryntum.locales).forEach(localeName => {
        // keeping this check in case some client tries to use an old locale
        if (!localeName.startsWith('moment')) {
          const locale = window.bryntum.locales[localeName];

          if (locale.extends) {
            me.extendLocale(locale.extends, locale);
          } else if (locale.localeName) {
            me.registerLocale(locale.localeName, {
              desc: locale.localeDesc,
              locale: locale
            });
          }
        }
      });

      if (!me.locale) {
        // English locale is built in, no need to apply it here since it will be applied anyway
        if (me.defaultLocaleName !== 'En') {
          // No locale applied, use default or first found
          me.applyLocale(me.defaultLocaleName || Object.keys(me.locales)[0]);
        }
      }
    }
  }

  set locales(localeConfigs) {
    this._locales = localeConfigs;
  }
  /**
   * Get currently registered locales.
   * Returns an object with locale names (`localeName`) as properties.
   *
   * Example:
   * ```
   * const englishLocale = LocaleManager.locales.En;
   * ```
   *
   * this returns registered English locale object.
   * ```
   * {
   *   "desc": "English",
   *   "locale": {
   *     "localeName": "En",
   *     "localeDesc": "English",
   *
   *     ... (localization goes here)
   *
   *   }
   * }
   * ```
   * @readonly
   * @property {Object}
   */

  get locales() {
    return this._locales;
  }
  /**
   * Get/set currently used locale. Set a name of a locale to have it applied, or give a locale configuration to
   * have it registered and then applied
   * @property {String|Object}
   */

  set locale(locale) {
    if (typeof locale === 'string') {
      this.applyLocale(locale);
    } else {
      if (!locale.locale) {
        locale = {
          locale,
          localeName: locale.localeName || 'custom'
        };
      }

      this.registerLocale(locale.localeName, locale);
      this.applyLocale(locale.localeName);
    }
  }

  get locale() {
    return this._locale;
  }
  /**
   * Register a locale to make it available for applying.
   * Registered locales are available in {@link Core.localization.LocaleManager#property-locales-static}.
   * @param {String} name Name of locale (for example `En` or `SvSE`)
   * @param {Object} config Object with localized properties
   */

  registerLocale(name, config) {
    const me = this,
          isDefault = me.defaultLocaleName === name,
          isCurrent = me.locale && me.locale.localeName === name,
          isFirst = Object.keys(me.locales).length === 0; // Avoid registering the same locale twice and merge configs

    if (!me.locales[name]) {
      me.locales[name] = config;
    } else {
      me.locales[name].locale = LocaleHelper.mergeLocales(me.locales[name].locale, config.locale);
    } // if no default locale specified, use the first one. otherwise apply the default when it is registered
    // also reapply if current locale is registered again (first grid, then scheduler etc).

    if (isDefault || !me.defaultLocaleName && (isFirst || isCurrent)) {
      me.internalApplyLocale(me.locales[name]);
    }
  }
  /**
   * Extend an already loaded locale to add additional translations.
   * @param {String} name Name of locale (for example `En` or `SvSE`)
   * @param {Object} config Object with localized properties
   */

  extendLocale(name, config) {
    var _this$locale;

    const locale = this.locales[name];

    if (!locale) {
      return false;
    }

    locale.locale = LocaleHelper.mergeLocales(locale.locale, config);
    delete locale.locale.extends; // If current loaded locale is the same then apply to reflect changes

    if (((_this$locale = this.locale) === null || _this$locale === void 0 ? void 0 : _this$locale.localeName) === name) {
      this.applyLocale(name);
    }

    return true;
  }

  internalApplyLocale(localeConfig) {
    this._locale = localeConfig.locale;
    this.trigger('locale', localeConfig);
  }
  /**
   * Apply a locale. Locale must be defined in {@link Core.localization.LocaleManager#property-locales-static}.
   * If it is not loaded it will be loaded using AjaxHelper {@link Core.helper.AjaxHelper#function-get-static} request and then applied.
   * @param {String} name Name of locale to apply (for example `En` or `SvSE`)
   * @returns {boolean|Promise}
   */

  applyLocale(name, forceApply = false, ignoreError = false) {
    const me = this,
          localeConfig = me.locales[name];

    if (localeConfig && localeConfig.locale && me._locale === localeConfig.locale && !forceApply) {
      // no need to apply same locale again
      return true;
    } // ignoreError is used in examples where one example might have defined a locale not available in another

    if (!localeConfig) {
      if (ignoreError) return true;
      throw new Error(`Locale ${name} not registered`);
    }

    function internalApply() {
      me.internalApplyLocale(localeConfig);
    }

    if (!localeConfig.locale) {
      return new Promise((resolve, reject) => {
        me.loadLocale(localeConfig.path).then(response => {
          response.text().then(text => {
            const // eslint-disable-next-line no-new-func
            parseLocale = new Function(text);
            parseLocale();

            if (BrowserHelper.isBrowserEnv) {
              localeConfig.locale = window.bryntum.locales[name];
            }

            internalApply();
            resolve(localeConfig);
          });
        }).catch(response => reject(response));
      });
    }

    internalApply();
    return true;
  }
  /**
   * Loads a locale using AjaxHelper {@link Core.helper.AjaxHelper#function-get-static} request.
   * @private
   * @param {String} path Path to locale file
   * @returns {Promise}
   */

  loadLocale(path) {
    return AjaxHelper.get(path);
  }
  /**
   * Specifies if {@link Core.localization.Localizable#function-L-static Localizable.L()} function would throw error if no localization found in runtime
   * @property {Boolean}
   * @default false
   */

  set throwOnMissingLocale(value) {
    this._throwOnMissingLocale = value;
  }

  get throwOnMissingLocale() {
    return this._throwOnMissingLocale;
  }

}

const LocaleManagerSingleton = new LocaleManager();

/**
 * @module Core/localization/Localizable
 */

const ObjectProto = Object.getPrototypeOf(Object),
      localeRe = /L{.*?}/g,
      escape = txt => txt.replace(/{(\d+)}/gm, '[[$1]]'),
      unescape = txt => txt.replace(/\[\[(\d+)]]/gm, '{$1}');
/**
 * Mixin that provides localization functionality to a class.
 *
 * ```
 * // Get localized string
 * grid.L('foo');
 * grid.L('L{foo});
 * ```
 *
 * @mixin
 */

var Localizable = (Target => class Localizable extends (Target || Base) {
  static get $name() {
    return 'Localizable';
  }

  static get configurable() {
    return {
      /**
       * A class translations of which are used for translating this entity.
       * This is often used when translations of an item are defined on its container class.
       * For example:
       *
       * ```js
       * // Toolbar class that has some predefined items
       * class MyToolbar extends Toolbar {
       *
       *     static get $name() {
       *         return 'MyToolbar';
       *     }
       *
       *     static get defaultConfig() {
       *         return {
       *             // this specifies default configs for the items
       *             defaults : {
       *                 // will tell items to use the toolbar locale
       *                 localeClass : this
       *             },
       *
       *             items : [
       *                 // The toolbar has 2 buttons and translation for their texts will be searched in
       *                 // the toolbar locales
       *                 { text : 'Agree' },
       *                 { text : 'Disagree' }
       *             ]
       *         };
       *     }
       *
       *    ...
       * }
       * ```
       * So if one makes a locale for the `MyToolbar` class that will include `Agree` and `Disagree` string translations:
       * ```js
       *     ...
       *     MyToolbar : {
       *         Agree    : 'Yes, I agree',
       *         Disagree : 'No, I do not agree'
       *     }
       * ```
       * They will be used for the toolbar buttons and the button captions will say `Yes, I agree` and `No, I do not agree`.
       *
       * @config {Core.Base}
       */
      localeClass: null,

      /**
       * List of properties which values should be translated automatically upon a locale applying.
       * In case there is a need to localize not typical value (not a String value or a field with re-defined setter/getter),
       * you could use 'localeKey' meta configuration.
       * Example:
       * ```js
       *  static get configurable() {
       *     return {
       *          localizableProperties : ['width'],
       *
       *          width : {
       *              value   : '54em', // default value here
       *              $config : {
       *                  localeKey : 'L{editorWidth}' // name of the property that will be used in localization file
       *              }
       *          }
       *      };
       *  }
       * ```
       * @config {String[]}
       */
      localizableProperties: {
        value: [],
        $config: {
          merge: 'distinct'
        }
      }
    };
  }

  static clsName(cls) {
    var _cls$prototype, _cls$prototype2;

    return typeof cls === 'string' ? cls : cls === ObjectProto ? 'Object' : cls.$$name || cls.name || ((_cls$prototype = cls.prototype) === null || _cls$prototype === void 0 ? void 0 : _cls$prototype.$$name) || ((_cls$prototype2 = cls.prototype) === null || _cls$prototype2 === void 0 ? void 0 : _cls$prototype2.name);
  }

  static parseLocaleString(text) {
    var _text;

    const matches = [];
    let m; // Parse locale text in case it's wrapped with L{foo}

    if ((_text = text) !== null && _text !== void 0 && _text.includes('L{')) {
      const re = /L{(.*?)}/g; // Escape fix for {1}, {2} etc. in locale str

      text = escape(text);

      while ((m = re.exec(text)) != null) {
        // Support for parsing class namespace L{Class.foo}
        const classMatch = /((.*?)\.)?(.+)/g.exec(m[1]);
        matches.push({
          match: unescape(m[0]),
          localeKey: unescape(classMatch[3]),
          localeClass: classMatch[2]
        });
      }
    }

    return matches.length > 0 ? matches : [{
      match: text,
      localeKey: text,
      localeClass: undefined
    }];
  }

  construct(config = {}, ...args) {
    // Base class applies configs.
    super.construct(config, ...args);
    LocaleManagerSingleton.on('locale', this.updateLocalization, this);
    this.updateLocalization();
  }

  get localeClass() {
    return this._localeClass || null;
  }

  localizeProperty(property) {
    var _me$$meta$configs$pro;

    const me = this,
          currentValue = Objects.getPath(me, property),
          localeKey = (_me$$meta$configs$pro = me.$meta.configs[property]) === null || _me$$meta$configs$pro === void 0 ? void 0 : _me$$meta$configs$pro.localeKey; // check if localeKey is defined and try to translate it

    if (localeKey) {
      const localizedValue = Localizable.localize(localeKey, null, me.localeClass || me); // if a user set value directly in class definition, his value has a prio

      if (localizedValue && !(property in me.initialConfig)) {
        Objects.setPath(me, property, localizedValue);
      }
    } else if (typeof currentValue === 'string') {
      me.originalLocales = me.originalLocales || {};
      let propertyValue = Objects.getPath(me.originalLocales, property); // If we haven't saved original values yet let's do that

      if (propertyValue === undefined) {
        Objects.setPath(me.originalLocales, property, currentValue);
        propertyValue = currentValue;
      } // Doing localization from the original values

      if (propertyValue) {
        Objects.setPath(me, property, me.optionalL(propertyValue));
      }
    }
  }
  /**
   * Method that is triggered when applying a locale to the instance
   * (happens on the instance construction steps and when switching to another locale).
   *
   * The method can be overridden to dynamically translate the instance when locale is switched.
   * When overriding the method please make sure you call `super.updateLocalization()`.
   */

  updateLocalization() {
    var _this$localizableProp;

    (_this$localizableProp = this.localizableProperties) === null || _this$localizableProp === void 0 ? void 0 : _this$localizableProp.forEach(this.localizeProperty, this);
  }

  static getTranslation(text, templateData, localeCls) {
    const {
      locale
    } = LocaleManagerSingleton;
    let result = null,
        clsName,
        cls;

    if (locale) {
      // Iterate over all found localization entries
      for (const {
        match,
        localeKey,
        localeClass
      } of this.parseLocaleString(text)) {
        const translate = clsName => {
          var _locale$clsName;

          const translation = (_locale$clsName = locale[clsName]) === null || _locale$clsName === void 0 ? void 0 : _locale$clsName[localeKey];

          if (translation) {
            if (typeof translation === 'function') {
              result = templateData != null ? translation(templateData) : translation;
            } else if (typeof translation === 'object' || text === match) {
              result = translation;
            } else {
              result = (result || text).replace(match, translation);
            }
          }

          return translation;
        }; // Translate order
        // 1. Try to translate for current class
        // 2. Try to translate by Class hierarchy traversing prototypes
        // 3. Try to translate if Class is in {Class.foo} format

        let success = false;

        for (cls = localeCls; cls && (clsName = Localizable.clsName(cls)); cls = Object.getPrototypeOf(cls)) {
          if (success = translate(clsName)) {
            break;
          } else if (typeof cls === 'string') {
            break;
          }
        }

        if (!success && localeClass) {
          translate(localeClass);
        }
      }
    }

    return result;
  }
  /**
   * Get localized string, returns `null` if no localized string found.
   * @param {String} text String key
   * @param {Object} [templateData] Data to supply to template if localized string is a function
   * @returns {String}
   * @internal
   */

  static localize(text, templateData = undefined, ...localeClasses) {
    var _localeClasses;

    // In case this static method is called directly third argument is not provided
    // just fallback to searching locales for the class itself
    if (((_localeClasses = localeClasses) === null || _localeClasses === void 0 ? void 0 : _localeClasses.length) === 0) {
      localeClasses = [this];
    }

    let translation = null;
    localeClasses.some(cls => {
      translation = Localizable.getTranslation(text, templateData, cls);
      return translation != null;
    });
    return translation;
  }
  /**
   * Get localized string, returns value of `text` if no localized string found.
   *
   * If {@link Core.localization.LocaleManager#property-throwOnMissingLocale-static LocaleManager.throwOnMissingLocale}
   * is `true` then calls to `L()` will throw `Localization is not found for 'text' in 'ClassName'` exception when no
   * localization is found.
   *
   * @param {String} text String key
   * @param {Object} [templateData] Data to supply to template if localized string is a function
   * @static
   * @returns {String}
   */

  static L(text, templateData = undefined, ...localeClasses) {
    var _localeClasses2;

    // In case this static method is called directly third argument is not provided
    // just fallback to searching locales for the class itself
    if (((_localeClasses2 = localeClasses) === null || _localeClasses2 === void 0 ? void 0 : _localeClasses2.length) === 0) {
      localeClasses = [this];
    }

    const translation = this.localize(text, templateData, ...localeClasses); // Throw error if not localized anв text matches `L{foo}`

    if (LocaleManagerSingleton.throwOnMissingLocale && LocaleManagerSingleton.locale && translation == null) {
      throw new Error(`Localization is not found for '${text}' in '${localeClasses.map(cls => Localizable.clsName(cls)).join(', ')}'. ${LocaleManagerSingleton.locale.localeName ? `Locale : ${LocaleManagerSingleton.locale.localeName}` : ''}`);
    }

    return translation !== null && translation !== void 0 ? translation : text;
  }
  /**
   * Convenience function that can be called directly on the class that mixes Localizable in
   *
   * ```javascript
   * button.text = grid.L('L{group}');
   * ```
   *
   * @param {String} text String key
   * @param {Object} [templateData] Data to supply to template if localized string is a function
   * @returns {String}
   * @category Misc
   */

  L(text, templateData) {
    const {
      localeClass,
      constructor
    } = this; // If we have a different class set as translations provider
    // pass it first and use the class being translated as a fallback provider

    if (localeClass && Localizable.clsName(localeClass) !== Localizable.clsName(constructor)) {
      return Localizable.L(text, templateData, localeClass, constructor);
    } else {
      return Localizable.L(text, templateData, constructor);
    }
  }
  /**
   * Convenience function to get an optional translation. The difference compared to `L()` is that it wont throw
   * an error when the translation is missing even if configured with `throwOnMissingLocale`
   *
   * ```javascript
   * button.text = grid.optionalL('L{group}');
   * ```
   *
   * @param {String} text String key
   * @param {Object} [templateData] Data to supply to template if localized string is a function
   * @returns {String}
   * @static
   * @category Misc
   */

  static optionalL(text, templateData = undefined, ...localeClasses) {
    var _localeClasses3;

    const shouldThrow = LocaleManagerSingleton.throwOnMissingLocale;
    LocaleManagerSingleton.throwOnMissingLocale = shouldThrow && localeRe.test(text); // In case this static method is called directly third argument is not provided
    // just fallback to searching locales for the class itself

    if (((_localeClasses3 = localeClasses) === null || _localeClasses3 === void 0 ? void 0 : _localeClasses3.length) === 0) {
      localeClasses = [this];
    }

    const result = Localizable.L(text, templateData, ...localeClasses);
    LocaleManagerSingleton.throwOnMissingLocale = shouldThrow;
    return result;
  }
  /**
   * Convenience function to get an optional translation. The difference compared to `L()` is that it wont throw
   * an error when the translation is missing even if configured with `throwOnMissingLocale`
   *
   * ```javascript
   * button.text = grid.optionalL('L{group}');
   * ```
   *
   * @param {String} text String key
   * @param {Object} [templateData] Data to supply to template if localized string is a function
   * @returns {String}
   * @category Misc
   * @internal
   */

  optionalL(text, templateData) {
    const shouldThrow = LocaleManagerSingleton.throwOnMissingLocale;
    LocaleManagerSingleton.throwOnMissingLocale = shouldThrow && localeRe.test(text);
    const result = this.L(text, templateData);
    LocaleManagerSingleton.throwOnMissingLocale = shouldThrow;
    return result;
  }
  /**
   * Get the global LocaleManager
   * @property {Core.localization.LocaleManager}
   * @category Misc
   * @readonly
   */

  get localeManager() {
    return LocaleManagerSingleton;
  }

});

const {
  toString: toString$2
} = Object.prototype,
      DATE_TYPE$1 = toString$2.call(new Date()),
      // Left-to-right unicode mark (https://www.codetable.net/decimal/8206)
LTRSymbolRegexp = new RegExp('\u200E', 'g'),
      defaultValue = (value, defValue) => isNaN(value) || value == null ? defValue : value,
      enOrdinalSuffix = number => {
  const hasSpecialCase = ['11', '12', '13'].find(n => number.endsWith(n));
  let suffix = 'th';

  if (!hasSpecialCase) {
    const lastDigit = number[number.length - 1];
    suffix = {
      1: 'st',
      2: 'nd',
      3: 'rd'
    }[lastDigit] || 'th';
  }

  return number + suffix;
},
      useIntlFormat = (name, options, date) => {
  const formatter = intlFormatterCache[name] || (intlFormatterCache[name] = new Intl.DateTimeFormat(locale, options));
  let result = formatter.format(date); // IE11 inserts direction control characters to the string
  // This removes the characters

  if (BrowserHelper.isIE11) {
    result = result.replace(LTRSymbolRegexp, '');
  }

  return result;
},
      formatTime = (name, options, date, isShort = false) => {
  let strTime = useIntlFormat(name, options, date); // remove "0" from time when has AM/PM (from 01:00 PM to 1:00 PM): https://github.com/bryntum/support/issues/1483

  if (/am|pm/i.test(strTime)) {
    // remove first character only if is 0
    strTime = strTime.replace(/^0/, ''); // if isShort is true, remove minutes if is :00

    if (isShort) {
      strTime = strTime.replace(/:00/, '');
    }
  }

  return strTime;
},
      getDayDiff = (end, start) => Math.floor((end.getTime() - start.getTime() - (end.getTimezoneOffset() - start.getTimezoneOffset()) * validConversions.minute.millisecond) / validConversions.day.millisecond) + 1,
      normalizeDay = day => day >= 0 ? day : day + 7; // These vars are set when changing locale

let locale = 'en-US',
    ordinalSuffix = enOrdinalSuffix,
    // Used to cache used formats, to not have to parse format string each time
formatCache = {},
    intlFormatterCache = {},
    parserCache = {};

const DEFAULT_YEAR = 2020,
      // 2020 is the year that has no issues in Safari and Edge, see: https://github.com/bryntum/support/issues/554
DEFAULT_MONTH = 0,
      DEFAULT_DAY = 1,
      formats = {
  // 1, 2, ... 11, 12
  M: date => date.getMonth() + 1,
  //date.toLocaleDateString(locale, { month : 'numeric' }),
  // 1st, 2nd, 3rd, 4th, ... 11th, 12th
  Mo: date => ordinalSuffix(formats.M(date).toString()),
  // 01, 02, ...
  MM: date => (date.getMonth() + 1).toString().padStart(2, '0'),
  //date.toLocaleDateString(locale, { month : '2-digit' }),
  // Jan, Feb, ...
  MMM: date => useIntlFormat('MMM', {
    month: 'short'
  }, date),
  // January, February, ...
  MMMM: date => useIntlFormat('MMMM', {
    month: 'long'
  }, date),
  // 1, 2, ...
  Q: date => Math.ceil((date.getMonth() + 1) / 3),
  // 1st, 2nd, ...
  Qo: date => ordinalSuffix(formats.Q(date).toString()),
  // 1, 2, ...
  D: date => date.getDate(),
  //date.toLocaleDateString(locale, { day : 'numeric' }),
  // 1st, 2nd, ...
  Do: date => ordinalSuffix(formats.D(date).toString()),
  // 01, 02, ...
  DD: date => date.getDate().toString().padStart(2, '0'),
  //date.toLocaleDateString(locale, { day : '2-digit' }),
  // 1, 2, ..., 365, 365
  DDD: date => Math.ceil((new Date(date.getFullYear(), date.getMonth(), date.getDate(), 12, 0, 0) - new Date(date.getFullYear(), 0, 0, 12, 0, 0)) / validConversions.day.millisecond),
  // 1st, 2nd, ...
  DDDo: date => ordinalSuffix(formats.DDD(date).toString()),
  // 001, 002, ...
  DDDD: date => formats.DDD(date).toString().padStart(3, '0'),
  // 0, 1, ..., 6
  d: date => date.getDay(),
  // 0th, 1st, ...
  do: date => ordinalSuffix(date.getDay().toString()),
  // S, M, ...
  d1: date => formats.ddd(date).substring(0, 1),
  // Su, Mo, ...
  dd: date => formats.ddd(date).substring(0, 2),
  // Sun, Mon, ...
  ddd: date => useIntlFormat('ddd', {
    weekday: 'short'
  }, date),
  // Sunday, Monday, ...
  dddd: date => useIntlFormat('dddd', {
    weekday: 'long'
  }, date),
  e: date => date.getDay(),
  E: date => date.getDay() + 1,
  // ISO week, 1, 2, ...
  W: date => DateHelper.getWeekNumber(date)[1],
  Wo: date => ordinalSuffix(formats.W(date).toString()),
  WW: date => formats.W(date).toString().padStart(2, '0'),
  // 1979, 2018
  Y: date => date.getFullYear(),
  //date.toLocaleDateString(locale, { year : 'numeric' }),
  // 79, 18
  YY: date => date.getFullYear() % 100,
  //date.toLocaleDateString(locale, { year : '2-digit' }),
  // 1979, 2018
  YYYY: date => date.getFullYear(),
  //date.toLocaleDateString(locale, { year : 'numeric' }),
  // AM, PM
  A: date => date.getHours() < 12 ? 'AM' : 'PM',
  a: date => date.getHours() < 12 ? 'am' : 'pm',
  // 0, 1, ... 23
  H: date => date.getHours(),
  // 00, 01, ...
  HH: date => date.getHours().toString().padStart(2, '0'),
  // 1, 2, ... 12
  h: date => date.getHours() % 12 || 12,
  // 01, 02, ...
  hh: date => formats.h(date).toString().padStart(2, '0'),
  // 1, 2, ... 24
  k: date => date.getHours() + 1,
  // 01, 02, ...
  kk: date => formats.k(date).toString().padStart(2, '0'),
  // Locale specific (0 -> 24 or 1 AM -> 12 PM)
  K: date => formatTime('K', {
    hour: 'numeric'
  }, date),
  // Locale specific (00 -> 24 or 1 AM -> 12 PM)
  KK: date => formatTime('KK', {
    hour: '2-digit'
  }, date),
  // 0, 1, ... 59
  m: date => date.getMinutes(),
  //date.toLocaleTimeString(locale, { minute : 'numeric' }),
  // 00, 01, ...
  mm: date => formats.m(date).toString().padStart(2, '0'),
  // 0, 1, ... 59
  s: date => date.getSeconds(),
  //date.toLocaleTimeString(locale, { second : 'numeric' }),
  // 00, 01, ...
  ss: date => formats.s(date).toString().padStart(2, '0'),
  // 0, 1, ... 9
  S: date => date.getMilliseconds().toString().substr(0, 1),
  // 00, 01, ... 99
  SS: date => date.getMilliseconds().toString().substr(0, 2).padEnd(2, '0'),
  // 000, 001, ... 999
  SSS: date => date.getMilliseconds().toString().substr(0, 3).padEnd(3, '0'),
  z: date => useIntlFormat('z', {
    timeZoneName: 'short'
  }, date),
  zz: date => useIntlFormat('zz', {
    timeZoneName: 'long'
  }, date),
  Z: date => DH.getGMTOffset(date),
  LT: date => formatTime('LT', {
    hour: '2-digit',
    minute: '2-digit'
  }, date),
  // if minutes is 0, doesn't show it
  LST: date => formatTime('LST', {
    hour: 'numeric',
    minute: '2-digit'
  }, date, true),
  LTS: date => formatTime('LTS', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  }, date),
  L: date => useIntlFormat('L', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }, date),
  l: date => useIntlFormat('l', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric'
  }, date),
  LL: date => useIntlFormat('LL', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  }, date),
  ll: date => useIntlFormat('ll', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  }, date),
  LLL: date => useIntlFormat('LLL', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  }, date),
  lll: date => useIntlFormat('lll', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  }, date),
  LLLL: date => useIntlFormat('LLLL', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    weekday: 'long'
  }, date),
  llll: date => useIntlFormat('llll', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    weekday: 'short'
  }, date)
},
      // Want longest keys first, to not stop match at L of LTS etc.
formatKeys = Object.keys(formats).sort((a, b) => b.length - a.length),
      formatRegexp = `^(?:${formatKeys.join('|')})`,
      // return empty object, meaning value cannot be processed to a valuable date part
emptyFn$1 = () => ({}),
      isNumber = str => numberRegex.test(str),
      parseMilliseconds = str => isNumber(str) && {
  milliseconds: parseInt(str.padEnd(3, '0').substring(0, 3))
},
      parsers = {
  YYYY: str => ({
    year: parseInt(str)
  }),
  Y: str => ({
    year: parseInt(str)
  }),
  YY: str => {
    const year = parseInt(str);
    return {
      year: year + (year > 1968 ? 1900 : 2000)
    };
  },
  MM: str => ({
    month: parseInt(str) - 1
  }),
  Mo: str => ({
    month: parseInt(str) - 1
  }),
  DD: str => ({
    date: parseInt(str)
  }),
  M: str => ({
    month: parseInt(str) - 1
  }),
  D: str => ({
    date: parseInt(str)
  }),
  Do: str => ({
    date: parseInt(str)
  }),
  DDD: emptyFn$1,
  MMM: emptyFn$1,
  MMMM: emptyFn$1,
  DDDo: emptyFn$1,
  DDDD: emptyFn$1,
  d: emptyFn$1,
  do: emptyFn$1,
  d1: emptyFn$1,
  dd: emptyFn$1,
  ddd: emptyFn$1,
  dddd: emptyFn$1,
  Q: emptyFn$1,
  Qo: emptyFn$1,
  W: emptyFn$1,
  Wo: emptyFn$1,
  WW: emptyFn$1,
  e: emptyFn$1,
  E: emptyFn$1,
  HH: str => ({
    hours: parseInt(str)
  }),
  hh: str => ({
    hours: parseInt(str)
  }),
  mm: str => ({
    minutes: parseInt(str)
  }),
  H: str => ({
    hours: parseInt(str)
  }),
  m: str => ({
    minutes: parseInt(str)
  }),
  ss: str => ({
    seconds: parseInt(str)
  }),
  s: str => ({
    seconds: parseInt(str)
  }),
  S: parseMilliseconds,
  SS: parseMilliseconds,
  SSS: parseMilliseconds,
  A: str => ({
    amPm: str.toLowerCase()
  }),
  a: str => ({
    amPm: str.toLowerCase()
  }),
  L: 'MM/DD/YYYY',
  LT: 'HH:mm A',
  // Can either be Z (=UTC, 0) or +-HH:MM
  Z: str => {
    if (!str || !timeZoneRegEx.test(str) && str !== 'Z') {
      return null;
    }

    let timeZone = 0; // If string being parsed is more "detailed" than the format specified we can have more chars left,
    // thus check the last (for example HH:mmZ with input HH:mm:ssZ -> ssZ)

    if (str !== 'Z') {
      const matches = timeZoneRegEx.exec(str); // If timezone regexp matches, sting has time zone offset like '+02:00'

      if (matches) {
        const sign = matches[1] === '+' ? 1 : -1,
              hours = parseInt(matches[2]) || 0,
              minutes = parseInt(matches[3]) || 0;
        timeZone = sign * (hours * 60 + minutes);
      } // otherwise we just return current time zone, because there's a Z key in the input
      else {
          timeZone = -1 * new Date().getTimezoneOffset();
        }
    }

    return {
      timeZone
    };
  }
},
      parserKeys = Object.keys(parsers).sort((a, b) => b.length - a.length),
      parserRegexp = new RegExp(`(${parserKeys.join('|')})`),
      // Following regexp includes all formats that should be handled by Date class
localeStrRegExp = new RegExp('(l|LL|ll|LLL|lll|LLLL|llll)'),
      //    ISODateRegExp             = new RegExp('YYYY-MM-DD[T ]HH:mm:ss(.s+)?Z'),
// Some validConversions are negative to show that it's not an exact conversion, just an estimate.
validConversions = {
  // The units below assume:
  // 30 days in a month, 91 days for a quarter and 365 for a year
  // 52 weeks per year, 4 per month, 13 per quarter
  year: {
    year: 1,
    quarter: 4,
    month: 12,
    week: 52,
    day: 365,
    hour: 24 * 365,
    minute: 1440 * 365,
    second: 86400 * 365,
    millisecond: 86400000 * 365
  },
  quarter: {
    year: 1 / 4,
    quarter: 1,
    month: 3,
    week: 4,
    day: 91,
    hour: 24 * 91,
    minute: 1440 * 91,
    second: 86400 * 91,
    millisecond: 86400000 * 91
  },
  month: {
    year: 1 / 12,
    quarter: 1 / 3,
    month: 1,
    week: 4,
    day: -30,
    hour: -24 * 30,
    minute: -1440 * 30,
    second: -86400 * 30,
    millisecond: -86400000 * 30
  },
  week: {
    year: -1 / 52,
    quarter: -1 / 13,
    month: -1 / 4,
    day: 7,
    hour: 168,
    minute: 10080,
    second: 604800,
    millisecond: 604800000
  },
  day: {
    year: -1 / 365,
    quarter: -1 / 91,
    month: -1 / 30,
    week: 1 / 7,
    hour: 24,
    minute: 1440,
    second: 86400,
    millisecond: 86400000
  },
  hour: {
    year: -1 / (365 * 24),
    quarter: -1 / (91 * 24),
    month: -1 / (30 * 24),
    week: 1 / 168,
    day: 1 / 24,
    minute: 60,
    second: 3600,
    millisecond: 3600000
  },
  minute: {
    year: -1 / (365 * 1440),
    quarter: -1 / (91 * 1440),
    month: -1 / (30 * 1440),
    week: 1 / 10080,
    day: 1 / 1440,
    hour: 1 / 60,
    second: 60,
    millisecond: 60000
  },
  second: {
    year: -1 / (365 * 86400),
    quarter: -1 / (91 * 86400),
    month: -1 / (30 * 86400),
    week: 1 / 604800,
    day: 1 / 86400,
    hour: 1 / 3600,
    minute: 1 / 60,
    millisecond: 1000
  },
  millisecond: {
    year: -1 / (365 * 86400000),
    quarter: -1 / (91 * 86400000),
    month: -1 / (30 * 86400000),
    week: 1 / 604800000,
    day: 1 / 86400000,
    hour: 1 / 3600000,
    minute: 1 / 60000,
    second: 1 / 1000
  }
},
      normalizedUnits = {
  ms: 'millisecond',
  milliseconds: 'millisecond',
  s: 'second',
  seconds: 'second',
  m: 'minute',
  minutes: 'minute',
  h: 'hour',
  hours: 'hour',
  d: 'day',
  days: 'day',
  w: 'week',
  weeks: 'week',
  M: 'month',
  months: 'month',
  q: 'quarter',
  quarters: 'quarter',
  y: 'year',
  years: 'year'
},
      withDecimalsDurationRegex = /^\s*([-+]?\d+(?:[.,]\d*)?|[-+]?(?:[.,]\d+))\s*([^\s]+)?/i,
      noDecimalsDurationRegex = /^\s*([-+]?\d+)(?![.,])\s*([^\s]+)?/i,
      canonicalUnitNames = ['millisecond', 'second', 'minute', 'hour', 'day', 'week', 'month', 'quarter', 'year'],
      canonicalUnitAbbreviations = [['mil'], ['s', 'sec'], ['m', 'min'], ['h', 'hr'], ['d'], ['w', 'wk'], ['mo', 'mon', 'mnt'], ['q', 'quar', 'qrt'], ['y', 'yr']],
      deltaUnits = ['year', 'month', 'week', 'day', 'hour', 'minute', 'second', 'millisecond'],
      // Used when creating a date from an object, to fill in any blanks
dateProperties = ['milliseconds', 'seconds', 'minutes', 'hours', 'date', 'month', 'year'],
      // TODO: Should we provide special number parsing?
parseNumber = n => {
  const result = parseFloat(n);
  return isNaN(result) ? null : result;
},
      numberRegex = /^[0-9]+$/,
      timeZoneRegEx = /([+-])(\d\d):*(\d\d)*$/,
      unitMagnitudes = {
  millisecond: 0,
  second: 1,
  minute: 2,
  hour: 3,
  day: 4,
  week: 5,
  month: 6,
  quarter: 7,
  year: 8
},
      snapFns = {
  round(number, step = 1) {
    return Math.round(number / step) * step;
  },

  floor(number, step = 1) {
    return Math.floor(number / step) * step;
  },

  ceil(number, step = 1) {
    return Math.ceil(number / step) * step;
  }

},
      keyCache = {};
/**
 * @module Core/helper/DateHelper
 */

/**
 * Helps with date manipulation, comparison, parsing, formatting etc.
 *
 * ## Parsing strings
 * Use `DateHelper.parse()` to parse strings into dates. It accepts a date string and a format specifier.
 * The format specifier is string built up using the following tokens:
 *
 * | Unit        | Token | Description                |
 * |-------------|-------|----------------------------|
 * | Year        | YYYY  | 2018                       |
 * |             | YY    | < 68 -> 2000, > 68 -> 1900 |
 * | Month       | MM    | 01 - 12                    |
 * | Date        | DD    | 01 - 31                    |
 * | Hour        | HH    | 00 - 23 or 1 - 12          |
 * | Minute      | mm    | 00 - 59                    |
 * | Second      | ss    | 00 - 59                    |
 * | Millisecond | S     | 0 - 9                      |
 * |             | SS    | 00 - 99                    |
 * |             | SSS   | 000 - 999                  |
 * | AM/PM       | A     | AM or PM                   |
 * |             | a     | am or pm                   |
 * | TimeZone    | Z     | Z for UTC or +-HH:mm       |
 * | Predefined  | L     | Long date, MM/DD/YYYY      |
 * |             | LT    | Long time, HH:mm A         |
 *
 * Default parse format is: `'YYYY-MM-DDTHH:mm:ss.SSSZ'` see {@link #property-defaultParseFormat-static}
 *
 * For example:
 * ```
 * DateHelper.parse('2018-11-06', 'YYYY-MM-DD');
 * DateHelper.parse('13:14', 'HH:mm');
 * DateHelper.parse('6/11/18', 'DD/MM/YY');
 * ```
 *
 * ## Formatting dates
 * Use `DateHelper.format()` to create a string from a date using a format specifier. The format specifier is similar to
 * that used when parsing strings. It can use the following tokens (input used for output below is
 * `new Date(2018,8,9,18,7,8,145)`):
 *
 * | Unit                  | Token | Description & output                  |
 * |-----------------------|-------|---------------------------------------|
 * | Year                  | YYYY  | 2018                                  |
 * |                       | YY    | 18                                    |
 * |                       | Y     | 2018                                  |
 * | Quarter               | Q     | 3                                     |
 * |                       | Qo    | 3rd                                   |
 * | Month                 | MMMM  | September                             |
 * |                       | MMM   | Sep                                   |
 * |                       | MM    | 09                                    |
 * |                       | Mo    | 9th                                   |
 * |                       | M     | 9                                     |
 * | Week (iso)            | WW    | 36 (2 digit)                          |
 * |                       | Wo    | 36th                                  |
 * |                       | W     | 36                                    |
 * | Date                  | DDDD  | Day of year, 3 digits                 |
 * |                       | DDDo  | Day of year, ordinal                  |
 * |                       | DDD   | Day of year                           |
 * |                       | DD    | 09                                    |
 * |                       | Do    | 9th                                   |
 * |                       | D     | 9                                     |
 * | Weekday               | dddd  | Sunday                                |
 * |                       | ddd   | Sun                                   |
 * |                       | dd    | Su                                    |
 * |                       | do    | 0th                                   |
 * |                       | d     | 0                                     |
 * | Hour                  | HH    | 18 (00 - 23)                          |
 * |                       | H     | 18 (0 - 23)                           |
 * |                       | hh    | 06 (00 - 12)                          |
 * |                       | h     | 6 (0 - 12)                            |
 * |                       | KK    | 19 (01 - 24)                          |
 * |                       | K     | 19 (1 - 24)                           |
 * |                       | kk    | 06 or 18, locale determines           |
 * |                       | k     | 6 or 18, locale determines            |
 * | Minute                | mm    | 07                                    |
 * |                       | m     | 7                                     |
 * | Second                | ss    | 08                                    |
 * |                       | s     | 8                                     |
 * | Millisecond           | S     | 1                                     |
 * |                       | SS    | 14                                    |
 * |                       | SSS   | 145                                   |
 * | AM/PM                 | A     | AM or PM                              |
 * |                       | a     | am or pm                              |
 * | Predefined            | LT    | H: 2-digit (2d), m: 2d                |
 * | (uses browser locale) | LTS   | H: 2d, m: 2d, s : 2d                  |
 * |                       | LST   | Depends on 12 or 24 hour clock        |
 * |                       |       | 12h, H : 1d, m : 0 or 2d              |
 * |                       |       | 24h, H : 2d, m : 2d                   |
 * |                       | L     | Y: numeric (n), M : 2d, D : 2d        |
 * |                       | l     | Y: n, M : n, D : n                    |
 * |                       | LL    | Y: n, M : long (l), D : n             |
 * |                       | ll    | Y: n, M : short (s), D : n            |
 * |                       | LLL   | Y: n, M : l, D : n, H: n, m: 2d       |
 * |                       | lll   | Y: n, M : s, D : n, H: n, m: 2d       |
 * |                       | LLLL  | Y: n, M : l, D : n, H: n, m: 2d, d: l |
 * |                       | llll  | Y: n, M : s, D : n, H: n, m: 2d, d: s |
 *
 *
 * Default format is: `'YYYY-MM-DDTHH:mm:ssZ'` see {@link #property-defaultFormat-static}
 *
 * For example:
 *
 * ```javascript
 * DateHelper.format(new Date(2018,10,6), 'YYYY-MM-DD'); // 2018-11-06
 * DateHelper.format(new Date(2018,10,6), 'M/D/YY'); // 11/6/18
 * ```
 *
 * Arbitrary text can be embedded in the format string by wrapping it with {}:
 *
 * ```javascript
 * DateHelper.format(new Date(2019, 7, 16), '{It is }dddd{, yay!}') -> It is Friday, yay!
 * ```
 *
 * ## Unit names
 * Many DateHelper functions (for example add, as, set) accepts a unit among their params. The following units are
 * available:
 *
 * | Unit        | Aliases                       |
 * |-------------|-------------------------------|
 * | millisecond | millisecond, milliseconds, ms |
 * | second      | second, seconds, s            |
 * | minute      | minute, minutes, m            |
 * | hour        | hour, hours, h                |
 * | day         | day, days, d                  |
 * | week        | week, weeks, w                |
 * | month       | month, months, M              |
 * | quarter     | quarter, quarters, q          |
 * | year        | year, years, y                |
 *
 * For example:
 * ```javascript
 * DateHelper.add(date, 2, 'days');
 * DateHelper.as('hour', 7200, 'seconds');
 * ```
 */

class DateHelper extends Localizable() {
  static get $name() {
    return 'DateHelper';
  } //region Parse & format

  /**
   * Get/set the default format used by `format()` and `parse()`. Defaults to `'YYYY-MM-DDTHH:mm:ssZ'`
   * (~ISO 8601 Date and time, `'1962-06-17T09:21:34Z'`).
   * @member {String}
   */

  static set defaultFormat(format) {
    DH._defaultFormat = format;
  }

  static get defaultFormat() {
    return DH._defaultFormat || 'YYYY-MM-DDTHH:mm:ssZ';
  }
  /**
   * Get/set the default format used by `parse()`. Defaults to `'YYYY-MM-DDTHH:mm:ss.SSSZ'` or {@link #property-defaultFormat-static}
   * (~ISO 8601 Date and time, `'1962-06-17T09:21:34.123Z'`).
   * @member {String}
   */

  static set defaultParseFormat(parseFormat) {
    this._defaultParseFormat = parseFormat;
  }

  static get defaultParseFormat() {
    return this._defaultParseFormat || this._defaultFormat || 'YYYY-MM-DDTHH:mm:ss.SSSZ';
  }

  static buildParser(format) {
    // Split input format by regexp, which includes predefined patterns. Normally format would have some
    // splitters, like 'YYYY-MM-DD' or 'D/M YYYY' so output will contain matched patterns as well as splitters
    // which would serve as anchors. E.g. provided format is 'D/M!YYYY' and input is `11/6!2019` algorithm would work like:
    // 1. split format by regexp                // ['', 'D', '/', 'M', '!', 'YYYY', '']
    // 2. find splitters                        // ['/', '!']
    // 3. split input by seps, step by step     // ['11', ['6', ['2019']]]
    // Inputs like 'YYYYY' (5*Y) means 'YYYY' + 'Y', because it matches patterns from longer to shorter,
    // but if few patterns describe same unit the last one is applied, for example
    // DH.parse('20182015', 'YYYYY') equals to new Date(2015, 0, 0)
    const parts = format.split(parserRegexp),
          parser = []; // if length of the parts array is 1 - there are no regexps in the input string. thus - no parsers
    // do same if there are patterns matching locale strings (l, ll, LLLL etc.)
    // returning empty array to use new Date() as parser

    if (parts.length === 1 || localeStrRegExp.test(format)) {
      return [];
    } else {
      parts.reduce((prev, curr, index, array) => {
        // ignore first and last empty string
        if (index !== 0 || curr !== '') {
          // if current element matches parser regexp store it as a parser
          if (parserRegexp.test(curr)) {
            const localeParsers = this.localize('L{parsers}') || {},
                  fn = localeParsers[curr] || parsers[curr]; // Z should be last element in the string that matches regexp. Last array element is always either
            // an empty string (if format ends with Z) or splitter (everything that doesn't match regexp after Z)
            // If there is a pattern after Z, then Z index will be lower than length - 2

            if (curr === 'Z' && index < array.length - 2) {
              throw new Error(`Invalid format ${format} TimeZone (Z) must be last token`);
            } // If fn is a string, we found an alias (L, LLL, l etc.).
            // Need to build parsers from mapped format and merge with existing

            if (typeof fn === 'string') {
              // we are going to merge nested parsers with current, some cleanup required:
              // 1. last element is no longer last
              // 2. need to pass last parser to the next step
              const nestedParsers = DH.buildParser(fn),
                    lastItem = nestedParsers.pop();
              delete lastItem.last; // elevate nested parsers

              parser.push(...nestedParsers);
              prev = lastItem;
            } else {
              prev.pattern = curr;
              prev.fn = parsers[curr];
            }
          } // if it doesn't match - we've found a splitter
          else {
              prev.splitter = curr;
              parser.push(prev);
              prev = {};
            }
        } else if (Object.prototype.hasOwnProperty.call(prev, 'pattern')) {
          parser.push(prev);
        }

        return prev;
      }, {});
    }

    parser[parser.length - 1].last = true;
    return parser;
  }
  /**
   * A utility function to create a sortable string key for the passed date or ms timestamp using the `'YYYY-MM-DD'`
   * format.
   * @param {Number|Date} ms The Date instance or ms timestamp to generate a key for
   * @returns {String} Date/timestamp as a string with `'YYYY-MM-DD'` format
   * @internal
   */

  static makeKey(ms) {
    // Convert Date to ms timestamp
    if (ms.getTime) {
      ms = ms.getTime();
    } // Cache holds ms -> YYYY-MM-DD

    const cached = keyCache[ms];

    if (cached) {
      return cached;
    }

    return keyCache[ms] = DH.format(new Date(ms), 'YYYY-MM-DD');
  }
  /**
   * A utility function to parse a sortable string to a date date using the `'YYYY-MM-DD'` format.
   * @param {String} key The string to return a date for.
   * @internal
   */

  static parseKey(key) {
    return DH.parse(key, 'YYYY-MM-DD');
  }
  /**
   * Returns a date created from the supplied string using the specified format. Will try to create even if format
   * is left out, by first using the default format (see {@link #property-defaultFormat-static}, by default
   * `YYYY-MM-DDTHH:mm:ssZ`) and then using `new Date(dateString)`.
   * Supported tokens:
   *
   * | Unit        | Token | Description                |
   * |-------------|-------|----------------------------|
   * | Year        | YYYY  | 2018                       |
   * |             | YY    | < 68 -> 2000, > 68 -> 1900 |
   * | Month       | MM    | 01 - 12                    |
   * | Date        | DD    | 01 - 31                    |
   * | Hour        | HH    | 00 - 23 or 1 - 12          |
   * | Minute      | mm    | 00 - 59                    |
   * | Second      | ss    | 00 - 59                    |
   * | Millisecond | S     | 0 - 9                      |
   * |             | SS    | 00 - 99                    |
   * |             | SSS   | 000 - 999                  |
   * | AM/PM       | A     | AM or PM                   |
   * |             | a     | am or pm                   |
   * | TimeZone    | Z     | Z for UTC or +-HH:mm       |
   * | Predefined  | L     | Long date, MM/DD/YYYY      |
   * |             | LT    | Long time, HH:mm A         |
   *
   * Predefined formats and functions used to parse tokens can be localized, see for example the swedish locale SvSE.js
   *
   * NOTE: If no date parameters are provided then `Jan 01 2020` is used as a default date
   *
   * @param {String} dateString Date string
   * @param {String} format Date format (or {@link #property-defaultParseFormat-static} if left out)
   * @returns {Date}
   * @category Parse & format
   */

  static parse(dateString, format = DH.defaultParseFormat) {
    if (dateString instanceof Date) {
      return dateString;
    }

    if (typeof dateString !== 'string' || !dateString) {
      return null;
    } // // For ISO 8601 native is faster, but not very forgiving
    // if (format === defaultFormat) {
    //     const dt = new Date(dateString);
    //     if (!isNaN(dt)) {
    //         return dt;
    //     }
    // }

    const config = {
      year: null,
      month: null,
      date: null,
      hours: null,
      minutes: null,
      seconds: null,
      milliseconds: null
    }; // Milliseconds parser is the same for S, SS, SSS

    format = format.replace(/S+/gm, 'SSS');
    let parser = parserCache[format],
        result;

    if (!parser) {
      parser = parserCache[format] = DH.buildParser(format);
    } // Each parser knows its pattern and splitter. It looks for splitter in the
    // input string, takes first substring and tries to process it. Remaining string
    // is passed to the next parser.

    parser.reduce((dateString, parser) => {
      if (parser.last) {
        Object.assign(config, parser.fn(dateString));
      } else {
        let splitAt; // ISO 8601 says that T symbol can be replaced with a space

        if (parser.splitter === 'T' && dateString.indexOf('T') === -1) {
          splitAt = dateString.indexOf(' ');
        } else {
          // If splitter specified find its position, otherwise try to determine pattern length
          splitAt = parser.splitter !== '' ? dateString.indexOf(parser.splitter) : parser.pattern && parser.pattern.length || -1;
        }

        let part, rest; // If splitter is not found in the current string we may be dealing with
        // 1. partial input - in that case we just feed all string to current parser and move on
        // 2. time zone (ssZ - splitter is empty string) and pattern is not specified, see comment below
        // 3. parse milliseconds before Z

        if (splitAt === -1 || parser.pattern === 'SSS' && dateString.match(/^\d+Z$/)) {
          // NOTE: parentheses are required here as + and - signs hold valuable information
          // with parentheses we get array like ['00','+','01:00'], omitting them we won't get
          // regexp match in result, loosing information
          const chunks = dateString.split(/([Z\-+])/); // If splitter is not found in the string, we may be dealing with string that contains info about TZ.
          // For instance, if format contains Z as last arg which is not separated (normally it is not indeed),
          // like 'YYYY-MM-DD HH:mm:ssZ', then second to last parser will have string that it cannot just parse, like
          // '2010-01-01 10:00:00'        -> '00'
          // '2010-01-01 10:00:00Z'       -> '00Z'
          // '2010-01-01 10:00:00-01'     -> '00-01'
          // '2010-01-01 10:00:00+01:30'  -> '00+01:30'
          // this cannot be processed by date parsers, so we need to process that additionally. So we
          // split string by symbols that can be found around timezone info: Z,-,+

          if (chunks.length === 1) {
            part = dateString;
            rest = '';
          } else {
            part = chunks[0];
            rest = `${chunks[1]}${chunks[2]}`;
          }
        } else {
          part = dateString.substring(0, splitAt) || dateString;
          rest = dateString.substring(splitAt + parser.splitter.length);
        }

        if (parser.fn) {
          // Run parser and add result to config on successful parse otherwise continue parsing
          const res = parser.fn(part);

          if (res) {
            Object.assign(config, res);
          } else {
            rest = part + rest;
          }
        }

        return rest;
      }
    }, dateString); // If year is specified date has to be greater than 0

    if (config.year && !config.date) {
      config.date = 1;
    }

    const date = DH.create(config);

    if (date) {
      result = date;
    } else {
      // Last resort, try if native passing can do it
      result = new Date(dateString);
    }

    return result;
  }
  /**
   * Creates a date from a date definition object. The object can have the following properties:
   * - year
   * - month
   * - date (day in month)
   * - hours
   * - minutes
   * - seconds
   * - milliseconds
   * - amPm : 'am' or 'pm', implies 12 hour clock
   * - timeZone : offset from UTC in minutes
   * @param {Object} definition
   * @returns {Date}
   * @category Parse & format
   */

  static create(definition) {
    // Shallow clone to not alter input
    const def = Object.assign({}, definition);
    let invalid = isNaN(def.year),
        useUTC = false; // Not much validation yet, only considered invalid if all properties are null

    if (!invalid) {
      let allNull = true;
      dateProperties.forEach(property => {
        if (!(property in def) || isNaN(def[property])) {
          def[property] = 0;
        }

        allNull = allNull && def[property] === null;
      });
      invalid = allNull;
    }

    if (def.amPm === 'pm') {
      def.hours = def.hours % 12 + 12;
    }

    if ('timeZone' in def) {
      useUTC = true;
      def.minutes -= def.timeZone;
    }

    if (invalid) {
      return null;
    }

    const args = [defaultValue(def.year, DEFAULT_YEAR), defaultValue(def.month, DEFAULT_MONTH), defaultValue(def.date, DEFAULT_DAY), def.hours, def.minutes, def.seconds, def.milliseconds];
    return useUTC ? new Date(Date.UTC(...args)) : new Date(...args);
  }

  static toUTC(date) {
    return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), date.getUTCHours(), date.getUTCMinutes(), date.getUTCSeconds(), date.getUTCMilliseconds()));
  }
  /**
   * Converts a date to string with the specified format. Formats heavily inspired by https://momentjs.com.
   * Available formats (input used for output below is `new Date(2018,8,9,18,7,8,145)`):
   *
   * | Unit                  | Token | Description & output                  |
   * |-----------------------|-------|---------------------------------------|
   * | Year                  | YYYY  | 2018                                  |
   * |                       | YY    | 18                                    |
   * |                       | Y     | 2018                                  |
   * | Quarter               | Q     | 3                                     |
   * |                       | Qo    | 3rd                                   |
   * | Month                 | MMMM  | September                             |
   * |                       | MMM   | Sep                                   |
   * |                       | MM    | 09                                    |
   * |                       | Mo    | 9th                                   |
   * |                       | M     | 9                                     |
   * | Week (iso)            | WW    | 36 (2 digit)                          |
   * |                       | Wo    | 36th                                  |
   * |                       | W     | 36                                    |
   * | Date                  | DDDD  | Day of year, 3 digits                 |
   * |                       | DDDo  | Day of year, ordinal                  |
   * |                       | DDD   | Day of year                           |
   * |                       | DD    | 09                                    |
   * |                       | Do    | 9th                                   |
   * |                       | D     | 9                                     |
   * | Weekday               | dddd  | Sunday                                |
   * |                       | ddd   | Sun                                   |
   * |                       | dd    | Su                                    |
   * |                       | do    | 0th                                   |
   * |                       | d     | 0                                     |
   * | Hour                  | HH    | 18 (00 - 23)                          |
   * |                       | H     | 18 (0 - 23)                           |
   * |                       | hh    | 06 (00 - 12)                          |
   * |                       | h     | 6 (0 - 12)                            |
   * |                       | KK    | 19 (01 - 24)                          |
   * |                       | K     | 19 (1 - 24)                           |
   * |                       | kk    | 06 or 18, locale determines           |
   * |                       | k     | 6 or 18, locale determines            |
   * | Minute                | mm    | 07                                    |
   * |                       | m     | 7                                     |
   * | Second                | ss    | 08                                    |
   * |                       | s     | 8                                     |
   * | Millisecond           | S     | 1                                     |
   * |                       | SS    | 14                                    |
   * |                       | SSS   | 145                                   |
   * | AM/PM                 | A     | AM or PM                              |
   * |                       | a     | am or pm                              |
   * | Predefined            | LT    | H: 2-digit (2d), m: 2d                |
   * | (uses browser locale) | LTS   | H: 2d, m: 2d, s : 2d                  |
   * |                       | LST   | Depends on 12 or 24 hour clock        |
   * |                       |       | 12h, H : 1d, m : 0 or 2d              |
   * |                       |       | 24h, H : 2d, m : 2d                   |
   * |                       | L     | Y: numeric (n), M : 2d, D : 2d        |
   * |                       | l     | Y: n, M : n, D : n                    |
   * |                       | LL    | Y: n, M : long (l), D : n             |
   * |                       | ll    | Y: n, M : short (s), D : n            |
   * |                       | LLL   | Y: n, M : l, D : n, H: n, m: 2d       |
   * |                       | lll   | Y: n, M : s, D : n, H: n, m: 2d       |
   * |                       | LLLL  | Y: n, M : l, D : n, H: n, m: 2d, d: l |
   * |                       | llll  | Y: n, M : s, D : n, H: n, m: 2d, d: s |
   *
   * Some examples:
   *
   * ```
   * DateHelper.format(new Date(2019, 7, 16), 'dddd') -> Friday
   * DateHelper.format(new Date(2019, 7, 16, 14, 27), 'HH:mm') --> 14:27
   * DateHelper.format(new Date(2019, 7, 16, 14, 27), 'L HH') --> 2019-07-16 14
   * ```
   *
   * Arbitrary text can be embedded in the format string by wrapping it with {}:
   *
   * ```
   * DateHelper.format(new Date(2019, 7, 16), '{It is }dddd{, yay!}') -> It is Friday, yay!
   * ```
   *
   * @param {Date} date Date
   * @param {String} format Desired format (uses `defaultFormat` if left out)
   * @returns {String} Formatted string
   * @category Parse & format
   */

  static format(date, format = DH.defaultFormat) {
    // Bail out if no date or invalid date
    if (!date || isNaN(date)) {
      return null;
    }

    let formatter = formatCache[format],
        output = '';

    if (!formatter) {
      formatter = formatCache[format] = []; // Build formatter array with the steps needed to format the date

      for (let i = 0; i < format.length; i++) {
        // Matches a predefined format?
        const formatMatch = format.substr(i).match(formatRegexp),
              predefined = formatMatch === null || formatMatch === void 0 ? void 0 : formatMatch[0];

        if (predefined) {
          const localeFormats = this.localize('L{formats}') || {},
                fn = localeFormats[predefined] || formats[predefined];
          formatter.push(fn);
          i += predefined.length - 1;
        } // Start of text block? Append it
        else if (format[i] === '{') {
            // Find closing brace
            const index = format.indexOf('}', i + 1); // No closing brace, grab rest of string

            if (index === -1) {
              formatter.push(format.substr(i + 1));
              i = format.length;
            } // Closing brace found
            else {
                formatter.push(format.substring(i + 1, index)); // Carry on after closing brace

                i = index;
              }
          } // Otherwise append to output (for example - / : etc)
          else {
              formatter.push(format[i]);
            }
      }
    }

    formatter.forEach(step => {
      if (typeof step === 'string') {
        output += step;
      } else {
        output += step(date);
      }
    }); // MS inserts a Left-to-right control char between localized date parts, remove it to have the expected string
    // output. Otherwise comparision with typed strings will fail

    if (BrowserHelper.isEdge || BrowserHelper.isIE11) {
      output = output.replace(/\u200E/g, '');
    }

    return output;
  }
  /**
   * Converts the specified amount of desired unit into milliseconds. Can be called by only specifying a unit as the
   * first argument, it then uses amount = 1. For example: asMilliseconds('hour') == asMilliseconds(1, 'hour')
   * @param {Number|String} amount Amount, what of is decided by specifying unit (also takes a unit which implies an amount of 1)
   * @param {String} unit Time unit (s, hour, months etc.)
   * @returns {Number}
   * @category Parse & format
   */

  static asMilliseconds(amount, unit = null) {
    if (typeof amount === 'string') {
      unit = amount;
      amount = 1;
    }

    return DH.as('millisecond', amount, unit);
  }
  /**
   * Converts the passed Date to an accurate number of months passed since the epoch start.
   * @param {Date} time The Date to find the month value of.
   * @returns {Number} The number of months since the system time epoch start. May be a fractional value.
   */

  static asMonths(time) {
    const monthLength = DH.as('ms', DH.daysInMonth(time), 'day'),
          fraction = (time.valueOf() - DH.startOf(time, 'month').valueOf()) / monthLength;
    return time.getYear() * 12 + time.getMonth() + fraction;
  }

  static monthsToDate(months) {
    const intMonths = Math.floor(months),
          fraction = months - intMonths,
          result = new Date(0, intMonths),
          msInMonth = DH.as('ms', DH.daysInMonth(result), 'days');
    result.setTime(result.getTime() + fraction * msInMonth);
    return result;
  }
  /**
   * Converts a millisecond time delta to a human readable form. For example `1000 * 60 * 60 * 50`
   * milliseconds would be rendered as "2 days, 2 hours"
   * @param {Number} delta The millisecond delta value.
   * @param {Object} [options] Formatting options.
   * @param {Boolean} [options.abbrev] Pass `true` to use abbreviated unit names, eg "2d, 2h" for the above example.
   * @param {String} [options.precision] The minimum precision unit.
   * @param {Boolean} [options.asString] Pass `false` to return the result as an array, eg ['2d', '2h'] for the above example.
   * @category Parse & format
   */

  static formatDelta(delta, options) {
    let abbrev, unitName;

    if (typeof options === 'boolean') {
      abbrev = options;
    } else if (options) {
      abbrev = options.abbrev;
    }

    const deltaObj = this.getDelta(delta, options),
          result = [],
          sep = abbrev ? '' : ' ';

    for (unitName in deltaObj) {
      result.push(`${deltaObj[unitName]}${sep}${unitName}`);
    }

    return (options === null || options === void 0 ? void 0 : options.asString) === false ? result : result.join(', ');
  }
  /**
   * Converts a millisecond time delta to an object structure. For example `1000 * 60 * 60 * 50`
   * milliseconds the result would be as:
   * {
   *     day: 2,
   *     hour: 2
   * }
   *
   * @param {Number} delta The millisecond delta value.
   * @param {Object} [options] Formatting options.
   * @param {Boolean} [options.abbrev] Pass `true` to use abbreviated unit names, eg "{ d: 2, h: 2 }" for the above example.
   * @param {String} [options.precision] The minimum precision unit.
   * @returns {Object} The object with the values for each unit.
   */

  static getDelta(delta, options) {
    let abbrev, d, done, precision, unitName;

    if (typeof options === 'boolean') {
      abbrev = options;
    } else if (options) {
      abbrev = options.abbrev;
      precision = DH.normalizeUnit(options.precision);
    }

    const result = {},
          getUnit = abbrev ? DH.getShortNameOfUnit : DH.getLocalizedNameOfUnit; // Loop downwards through the magnitude of units from year -> ms

    for (unitName of deltaUnits) {
      d = DH.as(unitName, delta);
      done = precision === unitName;
      d = Math[done ? 'round' : 'floor'](d); // If there's a non-zero integer quantity of this unit, add it to result
      // and subtract from the delta, then go round to next unit down.

      if (d || done && !result.length) {
        result[getUnit.call(DH, unitName, d !== 1)] = d; // TODO 0 is plural in EN but...?

        delta -= DH.as('ms', d, unitName);
      }

      if (done || !delta) {
        break;
      }
    }

    return result;
  }
  /**
   * Converts the specified amount of one unit (fromUnit) into an amount of another unit (toUnit)
   * @param {String} toUnit The name of units to convert to, eg: `'ms'`
   * @param {Number|String} amount The time to convert. Either the magnitude number form
   * or a duration string such as '2d'.
   * @param {string} [fromUnit] If the amount was passed as a number, the units to use to convert from.
   * @returns {Number}
   * @category Parse & format
   */

  static as(toUnit, amount, fromUnit = 'ms') {
    // Allow DH.as('ms', '2d')
    if (typeof amount === 'string') {
      amount = DH.parseDuration(amount);
    } // Allow DH.as('ms', myDurationObject)

    if (typeof amount === 'object') {
      fromUnit = amount.unit;
      amount = amount.magnitude;
    }

    if (toUnit === fromUnit) {
      return amount;
    }

    toUnit = DH.normalizeUnit(toUnit);
    fromUnit = DH.normalizeUnit(fromUnit); // validConversions[][] can be negative to signal that conversion is not exact, ignore sign here

    if (unitMagnitudes[fromUnit] > unitMagnitudes[toUnit]) {
      return amount * Math.abs(validConversions[fromUnit][toUnit]);
    } else {
      return amount / Math.abs(validConversions[toUnit][fromUnit]);
    }
  }

  static formatContainsHourInfo(format) {
    const stripEscapeRe = /(\\.)/g,
          hourInfoRe = /([HhKkmSsAa]|LT|L{3,}|l{3,})/;
    return hourInfoRe.test(format.replace(stripEscapeRe, ''));
  }
  /**
   * Returns true for 24 hour format.
   * @param {String} format Date format
   * @returns {Boolean}
   * @category Parse & format
   */

  static is24HourFormat(format) {
    return DH.format(DH.getTime(13, 0, 0), format).includes('13');
  } //endregion
  //region Manipulate

  /**
   * Add days, hours etc. to a date. Always clones the date, original will be left unaffected.
   * @param {Date|String} date Original date
   * @param {Number} amount Amount of days, hours etc
   * @param {String} unit Unit for amount
   * @returns {Date} New calculated date
   * @category Manipulate
   */

  static add(date, amount, unit = 'ms') {
    let d;

    if (typeof date === 'string') {
      d = DH.parse(date);
    } else {
      d = new Date(date.getTime());
    }

    if (!unit || amount === 0) return d;
    unit = DH.normalizeUnit(unit);

    switch (unit) {
      case 'millisecond':
        d = new Date(d.getTime() + amount);
        break;

      case 'second':
        d = new Date(d.getTime() + amount * 1000);
        break;

      case 'minute':
        d = new Date(d.getTime() + amount * 60000);
        break;

      case 'hour':
        d = new Date(d.getTime() + amount * 3600000);
        break;

      case 'day':
        // Integer value added, do calendar calculation to correctly handle DST etc.
        if (amount % 1 === 0) {
          d.setDate(d.getDate() + amount);
        } // No browsers support fractional values for dates any longer, do time based calculation
        else {
            d = new Date(d.getTime() + amount * 86400000);
          } // When crossing DST in Brazil, we expect hours to end up the same

        if (d.getHours() === 23 && date.getHours() === 0) {
          d = DH.add(d, 1, 'hour');
        }

        break;

      case 'week':
        d.setDate(d.getDate() + amount * 7);
        break;

      case 'month':
        {
          let day = d.getDate();

          if (day > 28) {
            day = Math.min(day, DH.getLastDateOfMonth(DH.add(DH.getFirstDateOfMonth(d), amount, 'month')).getDate());
          }

          d.setDate(day);
          d.setMonth(d.getMonth() + amount);
          break;
        }

      case 'quarter':
        d = DH.add(d, amount * 3, 'month');
        break;

      case 'year':
        d.setFullYear(d.getFullYear() + amount);
        break;
    }

    return d;
  }
  /**
   * Calculates the difference between two dates, in the specified unit.
   * @param {Date} start First date
   * @param {Date} end Second date
   * @param {String} unit Unit to calculate difference in
   * @param {Boolean} fractional Specify false to round result
   * @returns {Number} Difference in the specified unit
   * @category Manipulate
   */

  static diff(start, end, unit = 'ms', fractional = true) {
    unit = DH.normalizeUnit(unit);
    if (!start || !end) return 0;
    let amount;

    switch (unit) {
      case 'year':
        amount = DH.diff(start, end, 'month') / 12;
        break;

      case 'quarter':
        amount = DH.diff(start, end, 'month') / 3;
        break;

      case 'month':
        amount = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
        break;

      case 'week':
        amount = DH.diff(start, end, 'day') / 7;
        break;

      case 'day':
        {
          const dstDiff = start.getTimezoneOffset() - end.getTimezoneOffset();
          amount = (end - start + dstDiff * 60 * 1000) / 86400000;
          break;
        }

      case 'hour':
        amount = (end - start) / 3600000;
        break;

      case 'minute':
        amount = (end - start) / 60000;
        break;

      case 'second':
        amount = (end - start) / 1000;
        break;

      case 'millisecond':
        amount = end - start;
        break;
    }

    return fractional ? amount : Math.round(amount);
  }
  /**
   * Sets the date to the start of the specified unit, by default returning a clone of the date instead of changing it
   * inplace.
   * @param {Date} date Original date
   * @param {String} unit Start of this unit, 'day', 'month' etc.
   * @param {Boolean} clone Manipulate a copy of the date
   * @returns {Date} Manipulated date
   * @category Manipulate
   */

  static startOf(date, unit = 'day', clone = true) {
    if (!date) {
      return null;
    }

    unit = DH.normalizeUnit(unit);
    if (clone) date = DH.clone(date);
    const toSet = {},
          index = unitMagnitudes[unit];

    for (let i = 0; i < index; i++) {
      const currentUnit = canonicalUnitNames[i]; // Week and quarter are not part of the date

      if (currentUnit !== 'quarter' && currentUnit !== 'week') {
        toSet[currentUnit] = currentUnit === 'day' ? 1 : 0; // day is 1 based
      }
    } // Special handling of quarter and week

    if (unit === 'quarter') {
      toSet.month = (DH.get(date, 'quarter') - 1) * 3;
    } else if (unit === 'week') {
      const delta = date.getDay() - DH.weekStartDay;
      toSet.day = date.getDate() - delta;
    }

    return DH.set(date, toSet);
  }
  /**
   * Returns the end point of the passed date, that is 00:00:00 of the day after the passed date.
   * @param {Date} date The date to return the end point of.
   */

  static endOf(date) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1);
  }
  /**
   * Creates a clone of the specified date
   * @param {Date} date Original date
   * @returns {Date} Cloned date
   * @category Manipulate
   */

  static clone(date) {
    return new Date(date.getTime());
  }
  /**
   * Removes time from a date (same as calling #startOf(date, 'day'))
   * @param {Date} date Date to remove time from
   * @param {Boolean} clone Manipulate a copy of the date
   * @returns {Date} Manipulated date
   * @category Manipulate
   */

  static clearTime(date, clone = true) {
    return DH.startOf(date, 'day', clone);
  }

  static midnight(date, inclusive) {
    let ret = DH.clearTime(date);

    if (inclusive && ret < date) {
      ret = DH.add(ret, 1, 'd');
    }

    return ret;
  }
  /**
   * Returns the elapsed milliseconds from the start of the specified date.
   * @param {Date} date Date to remove date from.
   * @returns {Number} The elapsed milliseconds from the start of the specified date
   * @category Manipulate
   */

  static getTimeOfDay(date) {
    return date.getHours() * validConversions.hour.millisecond + date.getMinutes() * validConversions.minute.millisecond + date.getSeconds() * validConversions.second.millisecond + date.getMilliseconds();
  }
  /**
   * Sets a part of a date (in place)
   * @param {Date} date Date to manipulate
   * @param {String|Object} unit Part of date to set, for example 'minute'. Or an object like { second: 1, minute: 1 }
   * @param {Number} amount Value to set
   * @returns {Date} Modified date
   * @category Manipulate
   */

  static set(date, unit, amount) {
    if (!unit) {
      return date;
    }

    if (typeof unit === 'string') {
      switch (DH.normalizeUnit(unit)) {
        case 'millisecond':
          // Setting value to 0 when it is 0 at DST crossing messes it up
          if (amount !== 0 || date.getMilliseconds() > 0) {
            date.setMilliseconds(amount);
          }

          break;

        case 'second':
          // Setting value to 0 when it is 0 at DST crossing messes it up
          if (amount !== 0 || date.getSeconds() > 0) {
            date.setSeconds(amount);
          }

          break;

        case 'minute':
          // Setting value to 0 when it is 0 at DST crossing messes it up
          if (amount !== 0 || date.getMinutes() > 0) {
            date.setMinutes(amount);
          }

          break;

        case 'hour':
          date.setHours(amount);
          break;

        case 'day':
        case 'date':
          date.setDate(amount);
          break;

        case 'week':
          throw new Error('week not implemented');

        case 'month':
          date.setMonth(amount);
          break;

        case 'quarter':
          // Setting quarter = first day of first month of that quarter
          date.setDate(1);
          date.setMonth((amount - 1) * 3);
          break;

        case 'year':
          date.setFullYear(amount);
          break;
      }
    } else {
      Object.entries(unit) // Make sure smallest unit goes first, to not change month before changing day
      .sort((a, b) => unitMagnitudes[a[0]] - unitMagnitudes[b[0]]).forEach(([unit, amount]) => {
        DH.set(date, unit, amount);
      });
    }

    return date;
  }

  static setDateToMidday(date, clone = true) {
    return DH.set(DH.clearTime(date, clone), 'hour', 12);
  }
  /**
   * Constrains the date within a min and a max date
   * @param {Date} date The date to constrain
   * @param {Date} [min] Min date
   * @param {Date} [max] Max date
   * @return {Date} The constrained date
   * @category Manipulate
   */

  static constrain(date, min, max) {
    if (min != null) {
      date = DH.max(date, min);
    }

    return max == null ? date : DH.min(date, max);
  }
  /**
   * Returns time with default year, month, and day (Jan 1, 2020)
   * @param {Number|Date} hours Hours value or the full date to extract the time of.
   * @param {Number} minutes Minutes value
   * @param {Number} seconds Seconds value
   * @param {Number} ms Milliseconds value
   * @returns {Date} A new default date with the time extracted from the given date or from the time values provided individually.
   * @category Manipulate
   */

  static getTime(hours, minutes = 0, seconds = 0, ms = 0) {
    if (hours instanceof Date) {
      ms = hours.getMilliseconds();
      seconds = hours.getSeconds();
      minutes = hours.getMinutes();
      hours = hours.getHours();
    }

    return new Date(DEFAULT_YEAR, DEFAULT_MONTH, DEFAULT_DAY, hours, minutes, seconds, ms);
  }
  /**
   * Copies hours, minutes, seconds, milliseconds from one date to another
   *
   * @param {Date} targetDate The target date
   * @param {Date} sourceDate The source date
   * @return {Date} The adjusted target date
   * @category Manipulate
   * @static
   */

  static copyTimeValues(targetDate, sourceDate) {
    targetDate.setHours(sourceDate.getHours());
    targetDate.setMinutes(sourceDate.getMinutes());
    targetDate.setSeconds(sourceDate.getSeconds());
    targetDate.setMilliseconds(sourceDate.getMilliseconds());
    return targetDate;
  } //endregion
  //region Comparison

  static isDST(date) {
    const year = date.getFullYear(),
          jan = new Date(year, 0, 1),
          jul = new Date(year, 6, 1);
    return date.getTimezoneOffset() < Math.max(jan.getTimezoneOffset(), jul.getTimezoneOffset());
  }
  /**
   * Determines if a date precedes another.
   * @param {Date} first First date
   * @param {Date} second Second date
   * @returns {Boolean} true if first precedes second, otherwise false
   * @category Comparison
   */

  static isBefore(first, second) {
    return first < second;
  }
  /**
   * Determines if a date succeeds another.
   * @param {Date} first First date
   * @param {Date} second Second date
   * @returns {Boolean} true if first succeeds second, otherwise false
   * @category Comparison
   */

  static isAfter(first, second) {
    return first > second;
  }
  /**
   * Checks if two dates are equal.
   * @param {Date} first First date
   * @param {Date} second Second date
   * @param {String} unit If not given, the comparison will be done up to a millisecond
   * @returns {Boolean} true if the dates are equal
   * @category Comparison
   */

  static isEqual(first, second, unit = null) {
    if (unit === null) {
      // https://jsbench.me/3jk2bom2r3/1
      // https://jsbench.me/ltkb3vk0ji/1 (more flavors) - getTime is >2x faster vs valueOf/Number/op+
      return first && second && first.getTime() === second.getTime();
    }

    return DH.startOf(first, unit) - DH.startOf(second, unit) === 0;
  }
  /**
   * Compares two dates using the specified precision
   * @param {Date} first
   * @param {Date} second
   * @param {String} unit
   * @returns {Number} 0 = equal, -1 first before second, 1 first after second
   * @category Comparison
   */

  static compare(first, second, unit = null) {
    // Unit specified, cut the rest out
    if (unit) {
      first = DH.startOf(first, unit);
      second = DH.startOf(second, unit);
    } // Comparision on ms level

    if (DH.isBefore(first, second)) return -1;
    if (DH.isAfter(first, second)) return 1;
    return 0;
  }
  /**
   * Checks if date is the start of specified unit
   * @param {Date} date
   * @param {String} unit
   * @returns {Boolean}
   * @category Comparison
   */

  static isStartOf(date, unit) {
    return DH.isEqual(date, DH.startOf(date, unit));
  }
  /**
   * Checks if this date is >= start and < end.
   * @param {Date} date The source date
   * @param {Date} start Start date
   * @param {Date} end End date
   * @return {Boolean} true if this date falls on or between the given start and end dates.
   * @category Comparison
   */

  static betweenLesser(date, start, end) {
    //return start <= date && date < end;
    return start.getTime() <= date.getTime() && date.getTime() < end.getTime();
  }
  /**
   * Checks if this date is >= start and <= end.
   * @param {Date} date The source date
   * @param {Date} start Start date
   * @param {Date} end End date
   * @return {Boolean} true if this date falls on or between the given start and end dates.
   * @category Comparison
   */

  static betweenLesserEqual(date, start, end) {
    return start.getTime() <= date.getTime() && date.getTime() <= end.getTime();
  }
  /**
   * Returns true if dates intersect
   * @param {Date} date1Start
   * @param {Date} date1End
   * @param {Date} date2Start
   * @param {Date} date2End
   * @return {Boolean} Returns true if dates intersect
   * @category Comparison
   */

  static intersectSpans(date1Start, date1End, date2Start, date2End) {
    return DH.betweenLesser(date1Start, date2Start, date2End) || DH.betweenLesser(date2Start, date1Start, date1End);
  }
  /**
   * Returns 1 if first param is a greater unit than second param, -1 if the opposite is true or 0 if they're equal
   * @param {String} unit1 The 1st unit
   * @param {String} unit2 The 2nd unit
   * @category Comparison
   */

  static compareUnits(unit1, unit2) {
    return Math.sign(unitMagnitudes[unit1] - unitMagnitudes[unit2]);
  }
  /**
   * Returns true if the first time span completely 'covers' the second time span.
   * @example
   * DateHelper.timeSpanContains(new Date(2010, 1, 2), new Date(2010, 1, 5), new Date(2010, 1, 3), new Date(2010, 1, 4)) ==> true
   * DateHelper.timeSpanContains(new Date(2010, 1, 2), new Date(2010, 1, 5), new Date(2010, 1, 3), new Date(2010, 1, 6)) ==> false
   * @param {Date} spanStart The start date for initial time span
   * @param {Date} spanEnd The end date for initial time span
   * @param {Date} otherSpanStart The start date for the 2nd time span
   * @param {Date} otherSpanEnd The end date for the 2nd time span
   * @return {Boolean}
   * @category Comparison
   */

  static timeSpanContains(spanStart, spanEnd, otherSpanStart, otherSpanEnd) {
    return otherSpanStart - spanStart >= 0 && spanEnd - otherSpanEnd >= 0;
  } //endregion
  //region Query

  /**
   * Get the first day of week, 0-6 (Sunday-Saturday).
   * This is determined by the current locale's `DateHelper.weekStartDay` parameter.
   * @property {Number}
   * @readonly
   */

  static get weekStartDay() {
    // Defaults to 0, should not need to happen in real world scenarios when a locale is always loaded
    return this.localize('L{weekStartDay}') || 0;
  }
  /**
   * Get the specified part of a date
   * @param {Date} date
   * @param {String} unit Part of date, hour, minute etc.
   * @category Query
   */

  static get(date, unit) {
    switch (DH.normalizeUnit(unit)) {
      case 'millisecond':
        return date.getMilliseconds();

      case 'second':
        return date.getSeconds();

      case 'minute':
        return date.getMinutes();

      case 'hour':
        return date.getHours();

      case 'date':
      case 'day':
        // Scheduler has a lot of calculations expecting this to work
        return date.getDate();

      case 'week':
        return formats.W(date);

      case 'month':
        return date.getMonth();

      case 'quarter':
        return Math.floor(date.getMonth() / 3) + 1;

      case 'year':
        return date.getFullYear();
    }

    return null;
  }
  /**
   * Get number of days in the current month for the supplied date
   * @param {Date} date Date which month should be checked
   * @returns {Number} Days in month
   * @category Query
   */

  static daysInMonth(date) {
    const d = DH.clone(date);
    d.setDate(1);
    d.setMonth(date.getMonth() + 1);
    d.setDate(0);
    return d.getDate();
  }
  /**
   * Get the first date of the month for the supplied date
   * @param {Date} date
   * @returns {Date}
   * @category Query
   */

  static getFirstDateOfMonth(date) {
    return new Date(date.getFullYear(), date.getMonth(), 1);
  }
  /**
   * Get the last date of the month for the supplied date
   * @param {Date} date
   * @returns {Date}
   * @category Query
   */

  static getLastDateOfMonth(date) {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0);
  }
  /**
   * Get the earliest of two dates
   * @param {Date} first
   * @param {Date} second
   * @returns {Date} Earliest date
   * @category Query
   */

  static min(first, second) {
    return first.getTime() < second.getTime() ? first : second;
  }
  /**
   * Get the latest of two dates
   * @param {Date} first
   * @param {Date} second
   * @returns {Date} Latest date
   * @category Query
   */

  static max(first, second) {
    return first.getTime() > second.getTime() ? first : second;
  }
  /**
   * Get an incremented date. Incrementation based on specified unit and optional amount
   * @param {Date} date
   * @param {String} unit
   * @param {Number} [increment]
   * @param {Number} [weekStartDay] Will default to what is set in locale
   * @returns {Date}
   * @category Query
   */

  static getNext(date, unit, increment = 1, weekStartDay = DH.weekStartDay) {
    if (unit === 'week') {
      let dt = DH.clone(date);
      const day = dt.getDay();
      dt = DH.startOf(dt, 'day');
      dt = DH.add(dt, weekStartDay - day + 7 * (increment - (weekStartDay <= day ? 0 : 1)), 'day'); // For south american timezones, midnight does not exist on DST transitions, adjust...

      if (dt.getDay() !== weekStartDay) DH.add(dt, 1, 'hour');
      return dt;
    }

    return DH.startOf(DH.add(date, increment, unit), unit);
  }
  /**
   * Checks if date object is valid.
   *
   * For example:
   *
   * ```javascript
   * date = new Date('foo')
   * date instanceof Date // true
   * date.toString() // "Invalid Date"
   * isNaN(date) // true
   * DateHelper.isValidDate(date) // false
   *
   * date = new Date()
   * date instanceof Date // true
   * date.toString() // "Mon Jan 13 2020 18:27:38 GMT+0300 (GMT+03:00)"
   * isNaN(date) // false
   * DateHelper.isValidDate(date) // true
   * ```
   *
   * @param {Date} date
   * @returns {boolean}
   */

  static isValidDate(date) {
    return DH.isDate(date) && !isNaN(date);
  }
  /**
   * Checks if value is a date object. Allows to recognize date object even from another context,
   * like the top frame when used in an iframe.
   *
   * @param {*} value
   * @returns {boolean}
   */

  static isDate(value) {
    // see https://jsbench.me/s7kb49w83j/1 (cannot use instanceof cross-frame):
    return value && toString$2.call(value) === DATE_TYPE$1;
  }
  /**
   * Get the start of the next day
   * @param {Date} date
   * @param {Boolean} clone
   * @param {Boolean} noNeedToClearTime
   * @returns {Date}
   * @category Query
   */

  static getStartOfNextDay(date, clone, noNeedToClearTime) {
    let nextDay = DH.add(noNeedToClearTime ? date : DH.clearTime(date, clone), 1, 'day'); // DST case

    if (nextDay.getDate() === date.getDate()) {
      const offsetNextDay = DH.add(DH.clearTime(date, clone), 2, 'day').getTimezoneOffset(),
            offsetDate = date.getTimezoneOffset();
      nextDay = DH.add(nextDay, offsetDate - offsetNextDay, 'minute');
    }

    return nextDay;
  }
  /**
   * Get the end of previous day
   * @param {Date} date
   * @param {Boolean} noNeedToClearTime
   * @returns {Date}
   * @category Query
   */

  static getEndOfPreviousDay(date, noNeedToClearTime) {
    const dateOnly = noNeedToClearTime ? date : DH.clearTime(date, true); // dates are different

    if (dateOnly - date) {
      return dateOnly;
    } else {
      return DH.add(dateOnly, -1, 'day');
    }
  }
  /**
   * Get week number for the date.
   * @param {Date} date date
   * @param {Number} weekStartDay first day of week, 0-6 (Sunday-Saturday). Defaults to the {@link #property-weekStartDay-static}.
   * @returns {Number[]} year and week number
   * @category Query
   */

  static getWeekNumber(date, weekStartDay = DateHelper.weekStartDay) {
    const jan01 = new Date(date.getFullYear(), 0, 1),
          dec31 = new Date(date.getFullYear(), 11, 31),
          firstDay = normalizeDay(jan01.getDay() - weekStartDay),
          lastDay = normalizeDay(dec31.getDay() - weekStartDay),
          dayNumber = getDayDiff(date, jan01);
    let weekNumber; // Check if the year starts before the middle of a week

    if (firstDay < 4) {
      weekNumber = Math.floor((dayNumber + firstDay - 1) / 7) + 1;
    } else {
      weekNumber = Math.floor((dayNumber + firstDay - 1) / 7);
    }

    if (weekNumber) {
      let year = date.getFullYear(); // Might be week 1 of next year if the year ends before day 3 (0 based)

      if (weekNumber === 53 && lastDay < 3) {
        year++;
        weekNumber = 1;
      }

      return [year, weekNumber];
    } // We're in week zero which is the last week of the previous year, so ask what
    // week encapsulates 31 Dec in the previous year.

    const lastWeekOfLastYear = DateHelper.getWeekNumber(new Date(date.getFullYear() - 1, 11, 31))[1];
    return [date.getFullYear() - 1, lastWeekOfLastYear];
  } //endregion
  //region Unit helpers

  /**
   * Turns (10, 'day') into '10 days' etc.
   * @param {Number} count Amount of unit
   * @param {String} unit Unit, will be normalizes (days, d -> day etc.)
   * @returns {String}
   * @category Unit helpers
   */

  static formatCount(count, unit) {
    unit = DH.normalizeUnit(unit);
    if (count !== 1) unit += 's';
    return count + ' ' + unit;
  }
  /**
   * Get the ratio between two units ( year, month -> 1/12 )
   * @param {String} baseUnit
   * @param {String} unit
   * @param {Boolean} acceptEstimate If true, process negative values of validConversions. Defaults to false.
   * @returns {Number} Ratio
   * @category Unit helpers
   */

  static getUnitToBaseUnitRatio(baseUnit, unit, acceptEstimate = false) {
    baseUnit = DH.normalizeUnit(baseUnit);
    unit = DH.normalizeUnit(unit);
    if (baseUnit === unit) return 1; // Some validConversions have negative sign to signal that it is not an exact conversion.
    // Ignore those here unless acceptEstimate is provided.

    if (validConversions[baseUnit] && validConversions[baseUnit][unit] && (acceptEstimate || validConversions[baseUnit][unit] > 0)) {
      return 1 / DH.as(unit, 1, baseUnit);
    }

    if (validConversions[unit] && validConversions[unit][baseUnit] && (acceptEstimate || validConversions[unit][baseUnit] > 0)) {
      return DH.as(baseUnit, 1, unit);
    }

    return -1;
  }
  /**
   * Returns a localized abbreviated form of the name of the duration unit.
   * For example in the `EN` locale, for `"qrt"` it will return `"q"`.
   * @param {String} unit Duration unit
   * @return {String}
   * @category Unit helpers
   */

  static getShortNameOfUnit(unit) {
    // Convert abbreviations to the canonical name.
    // See locale file and the applyLocale method below.
    unit = DH.parseTimeUnit(unit); // unitLookup is keyed by eg 'DAY', 'day', 'MILLISECOND', 'millisecond' etc

    return DH.unitLookup[unit].abbrev;
  }
  /**
   * Returns a localized full name of the duration unit.
   * For for example in the `EN` locale, for `"d"` it will return either
   * `"day"` or `"days"`, depending from the `plural` argument
   * @static
   * @param {String} unit Time unit
   * @param {Boolean} plural Whether to return a plural name or singular
   * @return {String}
   * @category Unit helpers
   */

  static getLocalizedNameOfUnit(unit, plural) {
    // Normalize to not have to have translations for each variation used in code
    unit = DH.normalizeUnit(unit); // Convert abbreviations to the canonical name.
    // See locale file and the applyLocale method below.

    unit = DH.parseTimeUnit(unit); // unitLookup is keyed by eg 'DAY', 'day', 'MILLISECOND', 'millisecond' etc

    return DH.unitLookup[unit][plural ? 'plural' : 'single'];
  }
  /**
   * Normalizes a unit for easier usage in conditionals. For example year, years, y -> year
   * @param {String} unit
   * @returns {String}
   * @category Unit helpers
   */

  static normalizeUnit(unit) {
    if (!unit) {
      return null;
    }

    const unitLower = unit.toLowerCase();

    if (unitLower === 'date') {
      return unitLower;
    }

    return canonicalUnitNames.includes(unitLower) // Already valid
    ? unitLower // Trying specified case first, since we have both "M" for month and "m" for minute
    : normalizedUnits[unit] || normalizedUnits[unitLower];
  }

  static getUnitByName(name) {
    // Allow either a canonical name to be passed, or, if that fails, parse it as a localized name or abbreviation.
    return DH.normalizeUnit(name) || DH.normalizeUnit(DH.parseTimeUnit(name));
  }
  /**
   * Returns a duration of the timeframe in the given unit.
   * @param {Date} start The start date of the timeframe
   * @param {Date} end The end date of the timeframe
   * @param {String} unit Duration unit
   * @return {Number} The duration in the units
   * @category Unit helpers
   * @ignore
   */

  static getDurationInUnit(start, end, unit, doNotRound) {
    return DH.diff(start, end, unit, doNotRound);
  }
  /**
   * Checks if two date units align
   * @private
   * @param {String} majorUnit
   * @param {String} minorUnit
   * @returns {Boolean}
   * @category Unit helpers
   */

  static doesUnitsAlign(majorUnit, minorUnit) {
    // TODO: probably needs some fleshing out to be generally useful, otherwise move to TimeAxisViewModel?
    // Maybe also use getUnitToBaseUnitRatio() for assertion?
    return !(majorUnit !== minorUnit && minorUnit === 'week');
  }

  static getSmallerUnit(unit) {
    return canonicalUnitNames[unitMagnitudes[DH.normalizeUnit(unit)] - 1] || null;
  }

  static getLargerUnit(unit) {
    return canonicalUnitNames[unitMagnitudes[DH.normalizeUnit(unit)] + 1] || null;
  }
  /**
   *
   * Rounds the passed Date value to the nearest `increment` value.
   *
   * Optionally may round relative to a certain base time point.
   *
   * For example `DH.round(new Date('2020-01-01T09:35), '30 min', new Date('2020-01-01T09:15))`
   * would round to 9:45 because that's the nearest integer number of 30 minute increments
   * from the base.
   *
   * Note that `base` is ignored when rounding to weeks. The configured {@link #property-weekStartDay-static}
   * dictates what the base of a week is.
   *
   * @param {Date} time The time to round
   * @param {String|Number} increment A millisecond value by which to round the time.
   * May be specified in string form eg: `'15 minutes'`
   * @param {Date} [base] The "start" from which to apply the rounding.
   */

  static round(time, increment, base) {
    return DH.snap('round', time, increment, base);
  }
  /**
   *
   * Floor the passed Date value to the nearest `increment` value.
   *
   * Optionally may floor relative to a certain base time point.
   *
   * For example `DH.floor(new Date('2020-01-01T09:35), '30 min', new Date('2020-01-01T09:15))`
   * would floor to 9:15 because that's the closest lower integer number of 30 minute increments
   * from the base.
   *
   * Note that `base` is ignored when flooring to weeks. The configured {@link #property-weekStartDay-static}
   * dictates what the base of a week is.
   *
   * @param {Date} time The time to floor
   * @param {String|Number} increment A millisecond value by which to floor the time.
   * May be specified in string form eg: `'15 minutes'`
   * @param {Date} [base] The "start" from which to apply the flooring.
   */

  static floor(time, increment, base) {
    return DH.snap('floor', time, increment, base);
  }
  /**
   *
   * Ceils the passed Date value to the nearest `increment` value.
   *
   * Optionally may ceil relative to a certain base time point.
   *
   * For example `DH.ceil(new Date('2020-01-01T09:35), '30 min', new Date('2020-01-01T09:15))`
   * would ceil to 9:45 because that's the closest higher integer number of 30 minute increments
   * from the base.
   *
   * Note that `base` is ignored when ceiling to weeks. The configured {@link #property-weekStartDay-static}
   * dictates what the base of a week is.
   *
   * @param {Date} time The time to ceil
   * @param {String|Number} increment A millisecond value by which to ceil the time.
   * May be specified in string form eg: `'15 minutes'`
   * @param {Date} [base] The "start" from which to apply the ceiling.
   */

  static ceil(time, increment, base) {
    return DH.snap('ceil', time, increment, base);
  }
  /**
   * Implementation for round, floor and ceil.
   * @internal
   */

  static snap(operation, time, increment, base) {
    const snapFn = snapFns[operation];

    if (typeof increment === 'string') {
      increment = DH.parseDuration(increment); // increment is in weeks, months, quarters or years, then it can't be handled
      // using millisecond arithmetic.

      switch (increment.unit) {
        case 'week':
          {
            const {
              weekStartDay
            } = DH,
                  weekDay = time.getDay(); // weekStartDay gives our base
            // Our base is the start of the week

            base = DH.add(DH.clearTime(time), weekDay >= weekStartDay ? weekStartDay - weekDay : -(weekDay - weekStartDay + 7), 'day');
            return DH[operation](time, `${increment.magnitude * 7} days`, base);
          }

        case 'month':
          {
            // Express the time as a number of months from epoch start.
            // May be a fraction, eg the 15th will be 0.5 through a month.
            time = DH.asMonths(time);
            let resultMonths; // Snap the month count in the way requested

            if (base) {
              base = DH.asMonths(base);
              resultMonths = time + snapFn(time - base, increment.magnitude);
            } else {
              resultMonths = snapFn(time, increment.magnitude);
            } // Convert resulting month value back to a date

            return DH.monthsToDate(resultMonths);
          }

        case 'quarter':
          return DH[operation](time, `${increment.magnitude * 3} months`, base);

        case 'year':
          return DH[operation](time, `${increment.magnitude * 12} months`, base);
      } // Convert to a millisecond value

      increment = DH.as('ms', increment.magnitude, increment.unit);
    } // It's a simple round to milliseconds

    if (base) {
      const tzChange = DH.as('ms', base.getTimezoneOffset() - time.getTimezoneOffset(), 'ms');
      return new Date(base.valueOf() + snapFn(DH.diff(base, time, 'ms') + tzChange, increment));
    } else {
      const offset = time.getTimezoneOffset() * 60 * 1000; // Assuming current TZ is GMT+3
      // new Date(2000, 0, 1) / 86400000      -> 10956.875
      // new Date(2000, 0, 1, 3) / 86400000   -> 10957
      // Before calculation we need to align time value of the current timezone to GMT+0
      // And after calculate we need to adjust time back

      return new Date(snapFn(time.valueOf() - offset, increment) + offset);
    }
  } //endregion
  //region Date picker format

  /**
   * Parses a typed duration value according to locale rules.
   *
   * The value is taken to be a string consisting of the numeric magnitude and the units.
   * The units may be a recognised unit abbreviation of this locale or the full local unit name.
   *
   * For example: "2d", "2 d", "2 day", "2 days" will be turned into `{ magnitude : 2, unit : "day" }`
   *
   * **NOTE:** Doesn't work with complex values like "2 days, 2 hours"
   *
   * @param {String} value The value to parse.
   * @param {Boolean} [allowDecimals=true] Decimals are allowed in the magnitude.
   * @param {String} [defaultUnit] Default unit to use if only magnitude passed.
   * @returns {Object} If successfully parsed, the result contains two properties,
   * `magnitude` being a number, and `unit` being the canonical unit name, *NOT*
   * a localized name. If parsing was unsuccessful, `null` is returned.
   * @category Parse & format
   */

  static parseDuration(value, allowDecimals = true, defaultUnit) {
    const durationRegEx = allowDecimals ? withDecimalsDurationRegex : noDecimalsDurationRegex,
          match = durationRegEx.exec(value);

    if (value == null || !match) {
      return null;
    }

    const magnitude = parseNumber(match[1]),
          unit = DH.parseTimeUnit(match[2]) || defaultUnit;

    if (!unit) {
      return null;
    }

    return {
      magnitude,
      unit
    };
  }
  /**
   * Parses a typed unit name, for example `"ms"` or `"hr"` or `"yr"` into the
   * canonical form of the unit name which may be passed to {@link #function-add-static}
   * or {@link #function-diff-static}
   * @param {*} unitName
   * @category Parse & format
   */

  static parseTimeUnit(unitName) {
    // NOTE: In case you get a crash here when running tests, it is caused by missing locale. Build locales
    // using `scripts/build.js locales` to resolve.
    const unitMatch = unitName == null ? null : DH.durationRegEx.exec(unitName.toLowerCase());

    if (!unitMatch) {
      return null;
    } // See which group in the unitAbbrRegEx matched match[2]

    for (let unitOrdinal = 0; unitOrdinal < canonicalUnitNames.length; unitOrdinal++) {
      if (unitMatch[unitOrdinal + 1]) {
        return canonicalUnitNames[unitOrdinal];
      }
    }
  } //endregion
  //region Internal

  static getGMTOffset(date = new Date()) {
    if (!date) {
      return;
    }

    const offsetInMinutes = date.getTimezoneOffset(); // return 'Z' for UTC

    if (!offsetInMinutes) return 'Z';
    return (offsetInMinutes > 0 ? '-' : '+') + Math.abs(Math.trunc(offsetInMinutes / 60)).toString().padStart(2, '0') + ':' + Math.abs(offsetInMinutes % 60).toString().padStart(2, '0');
  }

  static fillDayNames() {
    const tempDate = new Date('2000-01-01T12:00:00'),
          dayNames = DH._dayNames || [],
          dayShortNames = DH._dayShortNames || [];
    dayNames.length = 0;
    dayShortNames.length = 0;

    for (let day = 2; day < 9; day++) {
      tempDate.setDate(day);
      dayNames.push(DH.format(tempDate, 'dddd'));
      dayShortNames.push(DH.format(tempDate, 'ddd'));
    }

    DH._dayNames = dayNames;
    DH._dayShortNames = dayShortNames;
  }

  static getDayNames() {
    return DH._dayNames;
  }

  static getDayName(day) {
    return DH._dayNames[day];
  }

  static getDayShortNames() {
    return DH._dayShortNames;
  }

  static getDayShortName(day) {
    return DH._dayShortNames[day];
  }

  static fillMonthNames() {
    const tempDate = new Date('2000-01-15T12:00:00'),
          monthNames = DH._monthNames || [],
          monthShortNames = DH._monthShortNames || [];
    monthNames.length = 0;
    monthShortNames.length = 0;

    for (let month = 0; month < 12; month++) {
      tempDate.setMonth(month);
      monthNames.push(DH.format(tempDate, 'MMMM'));
      monthShortNames.push(DH.format(tempDate, 'MMM'));
    }

    DH._monthNames = monthNames;
    DH._monthShortNames = monthShortNames;
  }

  static getMonthShortNames() {
    return DH._monthShortNames;
  }

  static getMonthShortName(month) {
    return DH._monthShortNames[month];
  }

  static getMonthNames() {
    return DH._monthNames;
  }

  static getMonthName(month) {
    return DH._monthNames[month];
  }

  static set locale(name) {
    locale = name;
  }

  static get locale() {
    return locale;
  }

  static setupDurationRegEx(unitNames = [], unitAbbreviations = []) {
    const me = this,
          unitLookup = {};
    let unitAbbrRegEx = '';

    for (let i = 0; i < unitAbbreviations.length; i++) {
      const // for example ['s', 'sec']
      abbreviations = unitAbbreviations[i],
            // for example { single : 'second', plural : 'seconds', abbrev : 's' }
      unitNamesCfg = unitNames[i];
      unitNamesCfg.canonicalUnitName = canonicalUnitNames[i]; // Create a unitLookup object keyed by unit full names
      // both lower and upper case to be able to look up plurals or abbreviations
      // also always include english names, since those are used in sources

      unitLookup[unitNamesCfg.single] = unitLookup[unitNamesCfg.single.toUpperCase()] = unitLookup[unitNamesCfg.canonicalUnitName] = unitLookup[unitNamesCfg.canonicalUnitName.toUpperCase()] = unitNamesCfg;
      unitAbbrRegEx += `${i ? '|' : ''}(`;

      for (let j = 0; j < abbreviations.length; j++) {
        unitAbbrRegEx += `${abbreviations[j]}|`;
      }

      locale = me.localize('L{locale}') || 'en-US';

      if (locale !== 'en-US') {
        // Add canonical values to be able to parse durations specified in configs
        const canonicalAbbreviations = canonicalUnitAbbreviations[i];

        for (let j = 0; j < canonicalAbbreviations.length; j++) {
          unitAbbrRegEx += `${canonicalAbbreviations[j]}|`;
        }
      }

      unitAbbrRegEx += `${unitNamesCfg.single}|${unitNamesCfg.plural}|${unitNamesCfg.canonicalUnitName}|${unitNamesCfg.canonicalUnitName}s)`;
    }

    me.unitLookup = unitLookup;
    me.durationRegEx = new RegExp(`^(?:${unitAbbrRegEx})$`);
  }

  static applyLocale() {
    const me = this,
          unitAbbreviations = me.localize('L{unitAbbreviations}') || [],
          unitNames = me.unitNames = me.localize('L{unitNames}'); // This happens when applying an incomplete locale, as done in Localizable.t.js.
    // Invalid usecase, but return to prevent a crash in that test.

    if (unitNames === 'unitNames') {
      return;
    }

    locale = me.localize('L{locale}') || 'en-US';

    if (locale === 'en-US') {
      // TODO: Include in En locale instead?
      ordinalSuffix = enOrdinalSuffix;
    } else {
      ordinalSuffix = me.localize('L{ordinalSuffix}') || ordinalSuffix;
    }

    formatCache = {};
    parserCache = {};
    intlFormatterCache = {};
    DH.setupDurationRegEx(unitNames, unitAbbreviations); // rebuild day/month names cache

    DH.fillDayNames();
    DH.fillMonthNames();
  } //endregion

}
const DH = DateHelper;
DH.useIntlFormat = useIntlFormat; // to use on tests

/*  */
// Update when changing locale

LocaleManagerSingleton.on({
  locale: 'applyLocale',
  prio: 1000,
  thisObj: DH
}); // Apply default locale

if (LocaleManagerSingleton.locale) {
  DH.applyLocale();
}

DateHelper._$name = 'DateHelper';

const {
  hasOwnProperty: hasOwnProperty$4
} = Object.prototype;
/**
 * @module Core/helper/ObjectHelper
 */

const typeOf = value => {
  const baseType = typeof value; // If not atomic type, we handle date or null

  if (baseType === 'object') {
    if (value === null) {
      return 'null';
    }

    if (Object.prototype.toString.call(value) === '[object Date]') {
      return 'date';
    }
  }

  return baseType;
}; // Detect if browser has bad implementation of toFixed()

const toFixedFix = 1.005.toFixed(2) === '1.01' ? null : function (number, fractionDigits) {
  const split = number.toString().split('.'),
        newNumber = +(!split[1] ? split[0] : split.join('.') + '1');
  return number.toFixed.call(newNumber, fractionDigits);
};
/**
 * Helper for Object manipulation.
 */

class ObjectHelper extends Objects {
  // These methods are inherited from Objects (an internal class) but need to be documented here for public use.
  // This is primarily because static methods, while inherited by JavaScript classes, are not displayed in derived
  // classes in the docs.

  /**
   * Copies all enumerable properties from the supplied source objects to `dest`. Unlike `Object.assign`, this copy
   * also includes inherited properties.
   * @param {Object} dest The destination object.
   * @param {...Object} sources The source objects.
   * @returns {Object} The `dest` object.
   * @method assign
   * @static
   */

  /**
   * Copies all enumerable properties from the supplied source objects to `dest`, only including properties that does
   * not already exist on `dest`. Unlike `Object.assign`, this copy also includes inherited properties.
   * @param {Object} dest The destination object.
   * @param {...Object} sources The source objects.
   * @returns {Object} The `dest` object.
   * @method assignIf
   * @static
   */

  /**
   * Creates a deep copy of the `value`. Simple objects ({@link #function-isObject-static}, arrays and `Date` objects
   * are cloned. The enumerable properties of simple objects and the elements of arrays are cloned recursively.
   * @param {*} value The value to clone.
   * @param {Function} [handler] An optional function to call for values of types other than simple object, array or
   * `Date`. This function should return the clone of the `value` passed to it. It is only called for truthy values
   * whose `typeof` equals `'object'`.
   * @param {*} handler.value The value to clone.
   * @returns {*} The cloned value.
   * @method clone
   * @static
   */

  /**
   * Converts a list of names (either a space separated string or an array), into an object with those properties
   * assigned truthy values. The converse of {@link #function-getTruthyKeys-static}.
   * @param {String|String[]} source The list of names to convert to object form.
   * @method createTruthyKeys
   * @static
   */

  /**
   * Gathers the names of properties which have truthy values into an array.
   *
   * This is useful when gathering CSS class names for complex element production.
   * Instead of appending to an array or string which may already contain the
   * name, and instead of contending with space separation and concatenation
   * and conditional execution, just set the properties of an object:
   *
   *     cls = {
   *         [this.selectedCls] : this.isSelected(thing),
   *         [this.dirtyCls] : this.isDirty(thing)
   *     };
   *
   * @param {Object} source Source of keys to gather into an array.
   * @returns {String[]} The keys which had a truthy value.
   * @method getTruthyKeys
   * @static
   */

  /**
   * Gathers the values of properties which are truthy into an array.
   * @param {Object} source Source of values to gather into an array.
   * @returns {String[]} The truthy values from the passed object.
   * @method getTruthyValues
   * @static
   */

  /**
   * Tests whether a passed object has any enumerable properties.
   * @param {Object} object
   * @returns {Boolean} `true` if the passed object has no enumerable properties.
   * @method isEmpty
   * @static
   */

  /**
   * Returns `true` if the `value` is a simple `Object`.
   * @param {Object} value
   * @returns {Boolean} `true` if the `value` is a simple `Object`.
   * @method isObject
   * @static
   */

  /**
   * Copies all enumerable properties from the supplied source objects to `dest`, recursing when the properties of
   * both the source and `dest` are objects.
   * ```
   *  const o = {
   *      a : 1,
   *      b : {
   *          c : 2
   *      }
   *  };
   *  const o2 = {
   *      b : {
   *          d : 3
   *      }
   *  }
   *
   *  console.log(merge(o, o2));
   *
   *  > { a : 1, b : { c : 2, d : 3 } }
   * ```
   * @param {Object} dest The destination object.
   * @param {...Object} sources The source objects.
   * @returns {Object} The `dest` object.
   * @method merge
   * @static
   */

  /**
   * Returns the specific type of the given `value`. Unlike the `typeof` operator, this function returns the text
   * from the `Object.prototype.toString` result allowing `Date`, `Array`, `RegExp`, and others to be differentiated.
   * ```
   *  console.log(typeOf(null));
   *  > null
   *
   *  console.log(typeOf({}));
   *  > object
   *
   *  console.log(typeOf([]));
   *  > array
   *
   *  console.log(typeOf(new Date()));
   *  > date
   *
   *  console.log(typeOf(NaN));
   *  > nan
   *
   *  console.log(typeOf(/a/));
   *  > regexp
   * ```
   * @param {*} value
   * @returns {String}
   * @method typeOf
   * @static
   */

  /**
   * Returns value for a given path in the object
   * @param {Object} object Object to check path on
   * @param {String} path Dot-separated path, e.g. 'object.childObject.someKey'
   * @returns {*} Value associated with passed key
   * @method getPath
   * @static
   */

  /**
   * Sets value for a given path in the object
   * @param {Object} object Target object
   * @param {String} path Dot-separated path, e.g. 'object.childObject.someKey'
   * @param {*} value Value for a given path
   * @returns {Object} Returns passed object
   * @method setPath
   * @static
   */

  /**
   * Creates a new object where key is a property in array item (`ref` by default) or index in the array and value is array item.
   *
   * From:
   * ```
   * [
   *     {
   *          text : 'foo',
   *          ref : 'fooItem'
   *     },
   *     {
   *          text : 'bar'
   *     }
   * ]
   * ```
   *
   * To:
   * ```
   * {
   *     fooItem : {
   *         text : 'foo',
   *         ref  : 'fooItem'
   *     },
   *     1 : {
   *         text : 'bar'
   *     }
   * }
   * ```
   *
   * @param {Object[]} arrayOfItems Array to transform.
   * @param {String} [prop] Property to read the key from. `ref` by default.
   * @returns {Object} namedItems
   */
  static transformArrayToNamedObject(arrayOfItems, prop = 'ref') {
    const namedItems = {};
    arrayOfItems.forEach((item, index) => {
      const // 0 is valid value, but empty string in not valid
      key = item[prop] != null && item[prop].toString().length ? item[prop] : index;
      namedItems[key] = item;
    });
    return namedItems;
  }
  /**
   * Creates a new array from object values and saves key in a property (`ref` by default) of each item.
   *
   * From:
   * ```
   * {
   *     fooItem : {
   *         text : 'foo'
   *     },
   *     1 : {
   *         text : 'bar'
   *     },
   *     barItem : false // will be ignored
   * }
   * ```
   *
   * To:
   * ```
   * [
   *     {
   *          text : 'foo',
   *          ref : 'fooItem'
   *     },
   *     {
   *          text : 'bar',
   *          ref : 1
   *     }
   * ]
   * ```
   *
   * @param {Object} namedItems Object to transform.
   * @param {String} [prop] Property to save the key to. `ref` by default.
   * @returns {Object[]} arrayOfItems
   */

  static transformNamedObjectToArray(namedItems, prop = 'ref') {
    return Object.keys(namedItems).filter(key => namedItems[key]).map(key => {
      const item = namedItems[key];
      item[prop] = key;
      return item;
    });
  }
  /**
   * Checks if two values are equal. Basically === but special handling of dates.
   * @param {*} a First value
   * @param {*} b Second value
   * @returns {*} true if values are equal, otherwise false
   */

  static isEqual(a, b, useIsDeeply = false) {
    // Eliminate null vs undefined mismatch
    if (a === null && b !== null || a === undefined && b !== undefined || b === null && a !== null || b === undefined && a !== undefined) {
      return false;
    } // Covers undefined === undefined and null === null, since mismatches are elminated above

    if (a == null && b == null) {
      return true;
    } // The same instance should equal itself.

    if (a === b) {
      return true;
    }

    const typeA = typeof a,
          typeB = typeof b;

    if (typeA === typeB) {
      switch (typeA) {
        case 'number':
        case 'string':
        case 'boolean':
          return a === b;
      }

      switch (true) {
        case a instanceof Date && b instanceof Date:
          // faster than calling DateHelper.isEqual
          // https://jsbench.me/3jk2bom2r3/1
          return a.getTime() === b.getTime();

        case Array.isArray(a) && Array.isArray(b):
          return a.length === b.length ? a.every((v, idx) => OH.isEqual(v, b[idx], useIsDeeply)) : false;

        case typeA === 'object' && a.constructor.prototype === b.constructor.prototype:
          return useIsDeeply ? OH.isDeeplyEqual(a, b, useIsDeeply) : JSON.stringify(a) === JSON.stringify(b);
      }
    }

    return String(a) === String(b);
  }
  /**
   * Checks if two objects are deeply equal
   * @param {Object} a
   * @param {Object} b
   * @param {Object} [options] Additional comparison options
   * @param {Object} [options.ignore] Map of property names to ignore when comparing
   * @param {Function} [options.shouldEvaluate] Function used to evaluate if a property should be compared or not.
   * Return false to prevent comparision
   * @param {Function} [options.evaluate] Function used to evaluate equality. Return `true`/`false` as evaluation
   * result or anything else to let `isEqual` handle the comparision
   * @returns {Boolean}
   */

  static isDeeplyEqual(a, b, options = {}) {
    // TODO : When syncing to DOM, it first checks the outermost config for equality with the previously used one
    //  and then drills downs for each task. If lets say the third task was changed, the two prior have already been
    //  determined to be equal before drilling down. That check could be persisted on the incoming object (a) to
    //  skip comparing it again on the more detailed level. Since (a) in this case is regenerated on ech draw there
    //  is no risk of polluting the next render
    // Same object, equal :)
    if (a === b) {
      return true;
    } // Nothing to compare, not equal

    if (!a || !b) {
      return false;
    } // Property names excluding ignored

    const aKeys = OH.keys(a, options.ignore),
          bKeys = OH.keys(b, options.ignore); // Property count differs, not equal

    if (aKeys.length !== bKeys.length) {
      return false;
    }

    for (let i = 0; i < aKeys.length; i++) {
      const aKey = aKeys[i],
            bKey = bKeys[i]; // Property name differs, not equal

      if (aKey !== bKey) {
        return false;
      }

      const aVal = a[aKey],
            bVal = b[bKey]; // Allow caller to determine if property values should be evaluated or not
      // TODO: Not currently used

      if (options.shouldEvaluate) {
        if (options.shouldEvaluate(aKey, {
          value: aVal,
          object: a
        }, {
          value: bVal,
          object: b
        }) === false) {
          continue;
        }
      } // Allow caller to determine equality of properties

      if (options.evaluate) {
        const result = options.evaluate(aKey, {
          value: aVal,
          object: a
        }, {
          value: bVal,
          object: b
        }); // Not equal

        if (result === false) {
          return false;
        } // Equal, skip isEqual call below

        if (result === true) {
          continue;
        }
      } // Values differ, not equal (also digs deeper)

      if (!OH.isEqual(aVal, bVal, options)) {
        return false;
      }
    } // Found to be equal

    return true;
  }
  /**
   * Checks if value B is partially equal to value A.
   * @param {*} a First value
   * @param {*} b Second value
   * @returns {Boolean} true if values are partially equal, false otherwise
   */

  static isPartial(a, b) {
    a = String(a).toLowerCase();
    b = String(b).toLowerCase();
    return a.indexOf(b) != -1;
  }
  /**
   * Checks if value a is smaller than value b.
   * @param {*} a First value
   * @param {*} b Second value
   * @returns {Boolean} true if a < b
   */

  static isLessThan(a, b) {
    if (a instanceof Date && b instanceof Date) {
      return DateHelper.isBefore(a, b);
    }

    return a < b;
  }
  /**
   * Checks if value a is bigger than value b.
   * @param {*} a First value
   * @param {*} b Second value
   * @returns {Boolean} true if a > b
   */

  static isMoreThan(a, b) {
    if (a instanceof Date && b instanceof Date) {
      return DateHelper.isAfter(a, b);
    }

    return a > b;
  }
  /**
   * Used by the Base class to make deep copies of defaultConfig blocks
   * @private
   */

  static fork(obj) {
    var ret, key, value;

    if (obj && obj.constructor === Object) {
      ret = Object.setPrototypeOf({}, obj);

      for (key in obj) {
        value = obj[key];

        if (value) {
          if (value.constructor === Object) {
            ret[key] = OH.fork(value);
          } else if (value instanceof Array) {
            ret[key] = value.slice();
          }
        }
      }
    } else {
      ret = obj;
    }

    return ret;
  }
  /**
   * Copies the named properties from the `source` parameter into the `dest` parameter.
   * @param {Object} dest The destination into which properties are copied.
   * @param {Object} source The source from which properties are copied.
   * @param {String[]} props The list of property names.
   * @returns The `dest` object.
   */

  static copyProperties(dest, source, props) {
    let prop, i;

    for (i = 0; i < props.length; i++) {
      prop = props[i];

      if (prop in source) {
        dest[prop] = source[prop];
      }
    }

    return dest;
  }
  /**
   * Copies the named properties from the `source` parameter into the `dest` parameter
   * unless the property already exists in the `dest`.
   * @param {Object} dest The destination into which properties are copied.
   * @param {Object} source The source from which properties are copied.
   * @param {String[]} props The list of property names.
   * @returns The `dest` object.
   */

  static copyPropertiesIf(dest, source, props) {
    if (source) {
      for (const prop of props) {
        if (!(prop in dest)) {
          dest[prop] = source[prop];
        }
      }
    }

    return dest;
  }
  /**
   * Returns an array containing all enumerable property names from every prototype level for the object.
   * @param {Object} object Object to retrieve property names from
   * @param {Object} [ignore] Optional map of names to ignore
   * @returns {String[]} All keys from every prototype level.
   */

  static keys(object, ignore = null) {
    const result = [];

    for (const p in object) {
      if (!ignore || !ignore[p]) {
        result.push(p);
      }
    }

    return result;
  }
  /**
   * Returns an array of a given object's properties names including properties in
   * all superclasses, in the same order as we get with a normal loop.
   * @param {Object} object An object which may have properties in a prototype
   * chain, such as a configuration (These are chained because of inheritance).
   * @returns {String[]} The property names.
   */

  static allKeys(object) {
    const result = [];

    for (const k in object) {
      result.push(k);
    }

    return result;
  }
  /**
   * Checks if a given path exists in an object
   * @param {Object} object Object to check path on
   * @param {String} path Dot-separated path, e.g. 'object.childObject.someKey'
   * @returns {Boolean} Returns `true` if path exists or `false` if it does not
   */

  static pathExists(object, path) {
    const properties = path.split('.');
    return properties.every(property => {
      if (!(property in object)) {
        return false;
      }

      object = object[property];
      return true;
    });
  }
  /**
   * Creates a simple single level key-value object from complex deep object.
   * @param {Object} object Object to extract path and values from
   * @returns {Object} Key-value object where key is a path to the corresponding value
   * @internal
   *
   * ```javascript
   * // converts deep object
   * {
   *     foo : {
   *         bar : {
   *             test : 1
   *         }
   *     }
   * }
   * // into a single level object
   * {
   *     'foo.bar.test' : 1
   * }
   * ```
   */

  static pathifyKeys(object) {
    const result = {};

    for (const key in object) {
      if (Object.hasOwnProperty.call(object, key)) {
        if (object[key] instanceof Object) {
          const paths = this.pathifyKeys(object[key]);

          for (const path in paths) {
            result[`${key}.${path}`] = paths[path];
          }
        } else {
          result[key] = object[key];
        }
      }
    }

    return result;
  }
  /**
   * Removes value for a given path in the object. Doesn't cleanup empty objects.
   * @param {Object} object
   * @param {String} path Dot-separated path, e.g. `obj.child.someKey`
   * @internal
   */

  static deletePath(object, path) {
    path.split('.').reduce((result, key, index, array) => {
      if (result == null) {
        return null;
      } else if (Object.hasOwnProperty.call(result, key)) {
        if (index === array.length - 1) {
          delete result[key];
        } else {
          return result[key];
        }
      }
    }, object);
  }

  static coerce(from, to) {
    var fromType = typeOf(from),
        toType = typeOf(to),
        isString = typeof from === 'string';

    if (fromType !== toType) {
      switch (toType) {
        case 'string':
          return String(from);

        case 'number':
          return Number(from);

        case 'boolean':
          // See http://ecma262-5.com/ELS5_HTML.htm#Section_11.9.3 as to why '0'.
          // TL;DR => ('0' == 0), so if given string '0', we must return boolean false.
          return isString && (!from || from === 'false' || from === '0') ? false : Boolean(from);

        case 'null':
          return isString && (!from || from === 'null') ? null : false;

        case 'undefined':
          return isString && (!from || from === 'undefined') ? undefined : false;

        case 'date':
          return isString && isNaN(from) ? DateHelper.parse(from) : Date(Number(from));
      }
    }

    return from;
  }

  static wrapProperty(object, propertyName, newGetter, newSetter, deep = true) {
    const newProperty = {};
    let proto = Object.getPrototypeOf(object),
        existingProperty = Object.getOwnPropertyDescriptor(proto, propertyName);

    while (!existingProperty && proto && deep) {
      proto = Object.getPrototypeOf(proto);

      if (proto) {
        existingProperty = Object.getOwnPropertyDescriptor(proto, propertyName);
      }
    }

    if (existingProperty) {
      if (existingProperty.set) {
        newProperty.set = v => {
          existingProperty.set.call(object, v); // Must invoke the getter in case "v" has been transformed.

          newSetter && newSetter.call(object, existingProperty.get.call(object));
        };
      } else {
        newProperty.set = newSetter;
      }

      if (existingProperty.get) {
        newProperty.get = () => {
          let result = existingProperty.get.call(object);

          if (newGetter) {
            result = newGetter.call(object, result);
          }

          return result;
        };
      } else {
        newProperty.get = newGetter;
      }
    } else {
      newProperty.set = v => {
        object[`_${propertyName}`] = v;
        newSetter && newSetter.call(object, v);
      };

      newProperty.get = () => {
        let result = object[`_${propertyName}`];

        if (newGetter) {
          result = newGetter.call(object, result);
        }

        return result;
      };
    }

    Object.defineProperty(object, propertyName, newProperty);
  }
  /**
   * Finds a property descriptor for the passed object from all inheritance levels.
   * @param {Object} object The Object whose property to find.
   * @param {String} propertyName The name of the property to find.
   * @returns {Object} An ECMA property descriptor is the property was found, otherwise `null`
   */

  static getPropertyDescriptor(object, propertyName) {
    let result = null;

    for (let o = object; o && !result && !hasOwnProperty$4.call(o, 'isBase'); o = Object.getPrototypeOf(o)) {
      result = Object.getOwnPropertyDescriptor(o, propertyName);
    }

    return result;
  }
  /**
   * Changes the passed object and removes all null and undefined properties from it
   * @param {Object} object Target object
   * @param {Boolean} [keepNull] Pass true to only remove undefined properties
   * @returns {Object} Passed object
   */

  static cleanupProperties(object, keepNull = false) {
    Object.entries(object).forEach(([key, value]) => {
      if (keepNull) {
        value === undefined && delete object[key];
      } else {
        value == null && delete object[key];
      }
    });
    return object;
  }
  /**
   * Changes the passed object and removes all properties from it.
   * Used while mutating when need to keep reference to the object but replace its properties.
   * @param {Object} object Target object
   * @returns {Object} Passed object
   */

  static removeAllProperties(obj) {
    Object.keys(obj).forEach(key => delete obj[key]);
    return obj;
  }
  /**
   * Checks that the supplied value is of the specified type. Throws if it is not
   * @param {Object} value Value to check type of
   * @param {String} type Expected type
   * @param {String} name Name of the value, used in error message
   */

  static assertType(value, type, name) {
    // eslint-disable-next-line valid-typeof
    if (value != null && typeof value !== type) {
      throw new Error(`Incorrect type "${typeof value}" for ${name}, expected "${type}"`);
    }
  }
  /**
   * Checks that the supplied value is a number.  Throws if it is not
   * @param {Object} value Value to check type of
   * @param {String} name Name of the value, used in error message
   */

  static assertNumber(value, name) {
    const asNumber = Number(value);

    if (typeof value !== 'number' || isNaN(asNumber)) {
      throw new Error(`Incorrect type for ${name}, got "${value}" (expected a Number)`);
    }
  }
  /**
   * Checks that the supplied value is a boolean.  Throws if it is not
   * @param {Object} value Value to check type of
   * @param {String} name Name of the value, used in error message
   */

  static assertBoolean(value, name) {
    OH.assertType(value, 'boolean', name);
  }
  /**
   * Number.toFixed(), with polyfill for browsers that needs it
   * @param {Number} number
   * @param {Number} digits
   * @returns {String} A fixed point string representation of the passed number.
   */

  static toFixed(number, digits) {
    if (toFixedFix) {
      return toFixedFix(number, digits);
    }

    return number.toFixed(digits);
  }
  /**
   * Round the passed number to closest passed step value.
   * @param {Number} number The number to round.
   * @param {Number} [step] The step value to round to.
   * @returns {Number} The number rounded to the closest step.
   */

  static roundTo(number, step = 1) {
    return Math.round(number / step) * step;
  }
  /**
   * Round the passed number to the passed number of decimals.
   * @param {Number} number The number to round.
   * @param {Number} digits The number of decimal places to round to.
   * @returns {Number} The number rounded to the passed number of decimal places.
   */

  static round(number, digits) {
    // Undefined or null means do not round. NOT round to no decimals.
    if (digits == null) {
      return number;
    }

    const factor = 10 ** digits;
    return Math.round(number * factor) / factor;
  }
  /**
   * Check if passed object is a Promise which contains `then` method.
   * Used to fix problems with detecting promises with `obj instanceof Promise` in code when Promise class is replaced with any other implementation.
   * Related to this issue https://github.com/bryntum/support/issues/791
   * @param obj Promise object to check
   * @returns {Boolean} truthy value if object is Promise
   * @internal
   */

  static isPromise(obj) {
    return typeof (obj === null || obj === void 0 ? void 0 : obj.then) === 'function';
  }
  /**
   * Returns a non-null entry from a Map for a given key path. This enables a specified
   * defaultValue to be added "just in time" which is returned if the key is not already present.
   * @param {Map} map The Map to find the key in (and potentially add to).
   * @param {String|Number|String[]|Number[]} path Dot-separated path, e.g. 'firstChild.childObject.someKey',
   * or the key path as an array, e.g. ['firstChild', 'childObject', 'someKey'].
   * @param {Object} [defaultValue] Optionally the value to insert if the key is not found.
   */

  static getMapPath(map, path, defaultValue) {
    const keyPath = Array.isArray(path) ? path : typeof path === 'string' ? path.split('.') : [path],
          simpleKey = keyPath.length === 1,
          topKey = keyPath[0],
          topValue = map.has(topKey) ? map.get(topKey) : map.set(topKey, simpleKey ? defaultValue : {}).get(topKey); // If it was a simple key, we are done.

    if (simpleKey) {
      return topValue;
    } // Go down the property path on the top Object, filling entries in until the leaf.

    return OH.getPathDefault(topValue, keyPath.slice(1), defaultValue);
  }

}
const OH = ObjectHelper;
ObjectHelper._$name = 'ObjectHelper';

/**
 * @module Core/helper/util/Rectangle
 */
const allBorders = ['border-top-width', 'border-right-width', 'border-bottom-width', 'border-left-width'],
      allPaddings = ['padding-top', 'padding-right', 'padding-bottom', 'padding-left'],
      borderNames = {
  t: 'border-top-width',
  r: 'border-right-width',
  b: 'border-bottom-width',
  l: 'border-left-width'
},
      paddingNames = {
  t: 'padding-top',
  r: 'padding-right',
  b: 'padding-bottom',
  l: 'padding-left'
},
      zeroBased = Object.freeze({
  x: 0,
  y: 0
}),
      alignSpecRe = /^([trblc])(\d*)-([trblc])(\d*)$/i,
      alignPointRe = /^([trblc])(\d*)$/i,
      edgeNames = ['top', 'right', 'bottom', 'left'],
      edgeIndices = {
  t: 0,
  r: 1,
  b: 2,
  l: 3
},
      defaultAlignments = ['b-t', 'l-r', 't-b', 'r-l'],
      zeroOffsets = Object.freeze([0, 0]),
      matchDimensions = ['width', 'height']; // Parse a l0-r0 (That's how Menus align to their owning MenuItem) slign spec.

function parseAlign(alignSpec) {
  const parts = alignSpecRe.exec(alignSpec),
        myOffset = parseInt(parts[2] || 50),
        targetOffset = parseInt(parts[4] || 50); // Comments assume the Menu's alignSpec of l0-r0 is used.

  return {
    myAlignmentPoint: parts[1] + myOffset,
    // l0
    myEdge: parts[1],
    // l
    myOffset,
    // 0
    targetAlignmentPoint: parts[3] + targetOffset,
    // r0
    targetEdge: parts[3],
    // r
    targetOffset,
    // 0
    startZone: edgeIndices[parts[3]] // 1 - start trying zone 1 in TRBL order

  };
} // Takes a result from the above function and flips edges for the axisLock config

function flipAlign(align) {
  return `${edgeNames[(edgeIndices[align.myEdge] + 2) % 4][0]}${align.myOffset}-${edgeNames[(edgeIndices[align.targetEdge] + 2) % 4][0]}${align.targetOffset}`;
}

function createOffsets(offset) {
  if (offset == null) {
    return zeroOffsets;
  } else if (typeof offset === 'number') {
    return [offset, offset];
  }

  return offset;
}
/**
 * Encapsulates rectangular areas for comparison, intersection etc.
 *
 * Note that the `right` and `bottom` properties are *exclusive*.
 *
 */

class Rectangle {
  /**
   * Returns the Rectangle in document based coordinates of the passed element.
   *
   * *Note:* If the element passed is the `document` or `window` the `window`'s
   * rectangle is returned which is always at `[0, 0]` and encompasses the
   * browser's entire document viewport.
   * @param {HTMLElement} element The element to calculate the Rectangle for.
   * @param {HTMLElement} [relativeTo] Optionally, a parent element in whose space to calculate the Rectangle.
   * @param {Boolean} [ignorePageScroll=false] Use browser viewport based coordinates.
   * @return {Core.helper.util.Rectangle} The Rectangle in document based (or, optionally viewport based) coordinates. Relative to the _relativeTo_ parameter if passed.
   */
  static from(element, relativeTo, ignorePageScroll) {
    var _element$namespaceURI;

    if (element instanceof Rectangle) {
      return element;
    }

    if (ignorePageScroll === undefined && typeof relativeTo === 'boolean') {
      ignorePageScroll = relativeTo;
      relativeTo = null;
    }

    if (relativeTo) {
      // TODO: nige should figure out if there is a better solution
      let {
        scrollLeft,
        scrollTop
      } = relativeTo;

      if ((BrowserHelper.isEdge || BrowserHelper.isSafari) && relativeTo === document.body) {
        scrollLeft = scrollTop = 0;
      }

      relativeTo = Rectangle.from(relativeTo).translate(-scrollLeft, -scrollTop);
    } else {
      relativeTo = zeroBased;
    } // Viewport is denoted by requesting window or document.
    // document.body may overflow the viewport, so this must not be evaluated as the viewport.

    const isViewport = element === document || element === window,
          viewRect = isViewport ? new Rectangle(0, 0, window.innerWidth, window.innerHeight) : element.getBoundingClientRect(),
          scrollOffset = ignorePageScroll || isViewport ? [0, 0] : [window.pageXOffset, window.pageYOffset];
    let scaleX = 0,
        scaleY = 0; // scaleX/Y is required to adjust scaled SVG element bounding rect

    if (BrowserHelper.isIE11 || BrowserHelper.isEdge && (_element$namespaceURI = element.namespaceURI) !== null && _element$namespaceURI !== void 0 && _element$namespaceURI.match(/svg/)) {
      var _element$dataset, _element$dataset2;

      scaleX = ((_element$dataset = element.dataset) === null || _element$dataset === void 0 ? void 0 : _element$dataset.scaleX) || 0;
      scaleY = ((_element$dataset2 = element.dataset) === null || _element$dataset2 === void 0 ? void 0 : _element$dataset2.scaleY) || 0;
    }

    return new Rectangle(viewRect.left + scrollOffset[0] - relativeTo.x + scaleX / 2, viewRect.top + scrollOffset[1] - relativeTo.y + scaleY / 2, viewRect.width - scaleX, viewRect.height - scaleY);
  }
  /**
   * Returns the Rectangle in viewport coordinates of the passed element.
   *
   * *Note:* If the element passed is the `document` or `window` the `window`'s rectangle is returned which is always
   * at `[0, 0]` and encompasses the browser's entire document viewport.
   * @param {HTMLElement} element The element to calculate the Rectangle for.
   * @param {HTMLElement} [relativeTo] Optionally, a parent element in whose space to calculate the Rectangle.
   * @return {Core.helper.util.Rectangle} The Rectangle in viewport based coordinates. Relative to the _relativeTo_
   * parameter if provided.
   */

  static fromScreen(element, relativeTo) {
    return Rectangle.from(element, relativeTo,
    /* ignorePageScroll = */
    true);
  }
  /**
   * Returns the inner Rectangle (within border) in document based coordinates
   * of the passed element.
   * @param {HTMLElement} The element to calculate the Rectangle for.
   * @param {HTMLElement} [relativeTo] Optionally, a parent element in whose space to calculate the Rectangle.
   * @param {Boolean} [ignorePageScroll] Use browser viewport based coordinates.
   * @return {Core.helper.util.Rectangle} The Rectangle in document based (or, optionally viewport based) coordinates. Relative to the _relativeTo_ parameter if passed.
   */

  static inner(element, relativeTo, ignorePageScroll = false) {
    const result = this.from(element, relativeTo, ignorePageScroll); // Can only ask for the following styles if element is in the document.

    if (document.body.contains(element)) {
      const borders = DomHelper.getStyleValue(element, allBorders);
      result.x += parseFloat(borders[borderNames.l]);
      result.y += parseFloat(borders[borderNames.t]);
      result.right -= parseFloat(borders[borderNames.r]);
      result.bottom -= parseFloat(borders[borderNames.b]);
    }

    return result;
  }
  /**
   * Returns the content Rectangle (within border and padding) in document based coordinates
   * of the passed element.
   * @param {HTMLElement} The element to calculate the Rectangle for.
   * @param {HTMLElement} [relativeTo] Optionally, a parent element in whose space to calculate the Rectangle.
   * @param {Boolean} [ignorePageScroll] Use browser viewport based coordinates.
   * @return {Core.helper.util.Rectangle} The Rectangle in document based (or, optionally viewport based) coordinates. Relative to the _relativeTo_ parameter if passed.
   */

  static content(element, relativeTo, ignorePageScroll = false) {
    const result = this.from(element, relativeTo, ignorePageScroll); // Can only ask for the following styles if element is in the document.

    if (document.body.contains(element)) {
      const borders = DomHelper.getStyleValue(element, allBorders),
            padding = DomHelper.getStyleValue(element, allPaddings);
      result.x += parseFloat(borders[borderNames.l]) + parseFloat(padding[paddingNames.l]);
      result.y += parseFloat(borders[borderNames.t]) + parseFloat(padding[paddingNames.t]);
      result.right -= parseFloat(borders[borderNames.r]) + parseFloat(padding[paddingNames.r]);
      result.bottom -= parseFloat(borders[borderNames.b]) + parseFloat(padding[paddingNames.b]);
    }

    return result;
  }
  /**
   * Returns the client Rectangle (within border and padding and scrollbars) in document based coordinates
   * of the passed element.
   * @param {HTMLElement} element The element to calculate the Rectangle for.
   * @param {HTMLElement} [relativeTo] Optionally, a parent element in whose space to calculate the Rectangle.
   * @param {Boolean} [ignorePageScroll] Use browser viewport based coordinates.
   * @return {Core.helper.util.Rectangle} The Rectangle in document based (or, optionally viewport based) coordinates. Relative to the _relativeTo_ parameter if passed.
   */

  static client(element, relativeTo, ignorePageScroll = false) {
    const result = this.content(element, relativeTo, ignorePageScroll),
          scrollbarWidth = DomHelper.scrollBarWidth;
    let padding;

    if (scrollbarWidth) {
      // Capture width taken by any vertical scrollbar.
      // If there is a vertical scrollbar, shrink the box.
      // TODO: We may have to shrink from the left in RTL mode.
      if (element.scrollHeight > element.clientHeight && DomHelper.getStyleValue(element, 'overflow-y') !== 'hidden') {
        padding = parseFloat(DomHelper.getStyleValue(element, 'padding-right'));
        result.right += padding - Math.max(padding, scrollbarWidth);
      } // Capture height taken by any horizontal scrollbar.
      // If there is a horizontal scrollbar, shrink the box.

      if (element.scrollWidth > element.clientWidth && DomHelper.getStyleValue(element, 'overflow-x') !== 'hidden') {
        padding = parseFloat(DomHelper.getStyleValue(element, 'padding-bottom'));
        result.bottom += padding - Math.max(padding, scrollbarWidth);
      }
    } // The client region excluding any scrollbars.

    return result;
  }
  /**
   * Returns a new rectangle created as the union of all supplied rectangles.
   * @param {Core.helper.util.Rectangle[]} rectangles
   * @return {Core.helper.util.Rectangle}
   */

  static union(...rectangles) {
    let {
      x,
      y,
      right,
      bottom
    } = rectangles[0],
        current;

    if (rectangles.length > 1) {
      for (let i = 1; i < rectangles.length; i++) {
        current = rectangles[i];

        if (current.x < x) {
          x = current.x;
        }

        if (current.y < y) {
          y = current.y;
        }

        if (current.right > right) {
          right = current.right;
        }

        if (current.bottom > bottom) {
          bottom = current.bottom;
        }
      }
    }

    return new Rectangle(x, y, right - x, bottom - y);
  }
  /**
   * Rounds this Rectangle to the pixel resolution of the current display or to the nearest
   * passed unit which defaults to the current display's [`devicePixelRatio`](https://developer.mozilla.org/en-US/docs/Web/API/Window/devicePixelRatio).
   * @param {Number} [devicePixelRatio] device pixel ratio which defaults to `window.devicePixelRatio`
   */

  roundPx(devicePixelRatio = window.devicePixelRatio || 1) {
    const me = this;
    me._x = DomHelper.roundPx(me._x, devicePixelRatio);
    me._y = DomHelper.roundPx(me._y, devicePixelRatio);
    me._width = DomHelper.roundPx(me._width, devicePixelRatio);
    me._height = DomHelper.roundPx(me._height, devicePixelRatio);
    return me;
  } // This class doesn't extend Base and extending doesn't seem to be
  // the way to go. Instead we duplicate smallest piece of logic here

  static get $$name() {
    return hasOwnProperty.call(this, '$name') && this.$name || // _$name is filled by webpack for every class (cls._$name = '...')
    hasOwnProperty.call(this, '_$name') && this._$name || this.name;
  }

  get $$name() {
    return this.constructor.$$name;
  }
  /**
   * Constructs a Rectangle
   * @param {Number} x The X coordinate
   * @param {Number} y The Y coordinate
   * @param {Number} width The width
   * @param {Number} height The height
   */

  constructor(x, y, width, height) {
    ObjectHelper.assertNumber(x, 'Rectangle.x');
    ObjectHelper.assertNumber(y, 'Rectangle.y');
    ObjectHelper.assertNumber(width, 'Rectangle.width');
    ObjectHelper.assertNumber(height, 'Rectangle.height');
    const me = this;
    me._x = x;
    me._y = y;
    me._width = width;
    me._height = height;
  }
  /**
   * Creates a copy of this Rectangle.
   */

  clone() {
    const me = this,
          result = new Rectangle(me.x, me.y, me.width, me.height);
    result.isAlignRectangle = me.isAlignRectangle;
    result.minHeight = me.minHeight;
    result.minWidth = me.minWidth;
    return result;
  }
  /**
   * Returns `true` if this Rectangle wholly contains the passed rectangle.
   *
   * Note that a {@link Core.helper.util.Point} may be passed.
   * @param {Core.helper.util.Rectangle} The Rectangle to test for containment within this Rectangle
   * @return {Boolean} `true` if the other Rectangle is wholly contained within this Rectangle
   */

  contains(other) {
    const me = this;

    if (other instanceof Rectangle) {
      return other._x >= me._x && other._y >= me._y && other.right <= me.right && other.bottom <= me.bottom;
    } else {
      return false;
    }
  }
  /**
   * Checks if this Rectangle intersects the passed Rectangle
   * @param {Core.helper.util.Rectangle} other The Rectangle to intersect with this.
   * @param {Boolean} useBoolean Specify `true` to return a boolean value instead of constructing a new Rectangle
   * @param {Boolean} allowZeroDimensions `true` to consider zero-width or zero-hight rectangles as intersecting if coordinates indicate the intersection
   * @return {Core.helper.util.Rectangle|Boolean} Returns the intersection Rectangle or `false` if there is no intersection.
   */

  intersect(other, useBoolean = false, allowZeroDimensions = false) {
    const me = this,
          y = Math.max(me.y, other.y),
          r = Math.min(me.right, other.right),
          b = Math.min(me.bottom, other.bottom),
          x = Math.max(me.x, other.x),
          intersect = allowZeroDimensions ? b >= y && r >= x : b > y && r > x;

    if (intersect) {
      return useBoolean ? true : new Rectangle(x, y, r - x, b - y);
    } else {
      return false;
    }
  }

  equals(other, round = false) {
    const processor = round ? x => Math.round(x) : x => x;
    return other instanceof Rectangle && processor(other.x) === processor(this.x) && processor(other.y) === processor(this.y) && processor(other.width) === processor(this.width) && processor(other.height) === processor(this.height);
  }
  /**
   * Translates this Rectangle by the passed vector. Size is maintained.
   * @param {Number} x The X translation vector.
   * @param {Number} y The Y translation vector.
   * @returns This Rectangle;
   */

  translate(x, y) {
    this._x += x || 0;
    this._y += y || 0;
    return this;
  }
  /**
   * Moves this Rectangle to the passed `x`, `y` position. Size is maintained.
   * @param {Number} x The new X position.
   * @param {Number} y The new Y position.
   * @returns This Rectangle;
   */

  moveTo(x, y) {
    if (x != null) {
      this._x = x;
    }

    if (y != null) {
      this._y = y;
    }

    return this;
  }
  /**
   * Returns the vector which would translate this Rectangle (or Point) to the same position as the other Rectangle (or point)
   * @param {Core.helper.util.Rectangle|Core.helper.util.Point} other The Rectangle or Point to calculate the delta to.
   */

  getDelta(other) {
    return [other.x - this.x, other.y - this.y];
  }
  /**
   * The center point of this rectangle.
   * @property {Core.helper.util.Point}
   */

  get center() {
    return new Rectangle.Point(this.x + this.width / 2, this.y + this.height / 2);
  }
  /**
   * Get/sets the X coordinate of the Rectangle. Note that this does *not* translate the
   * Rectangle. The requested {@link #property-width} will change.
   * @property {Number}
   */

  set x(x) {
    const xDelta = x - this._x;
    this._x = x;
    this._width -= xDelta;
  }

  get x() {
    return this._x;
  }
  /**
   * Alias for x. To match DOMRect.
   * @property {Number}
   */

  set left(x) {
    this.x = x;
  }

  get left() {
    return this.x;
  }
  /**
   * Alias for y. To match DOMRect.
   * @property {Number}
   */

  set top(y) {
    this.y = y;
  }

  get top() {
    return this.y;
  }
  /**
   * Get/sets the Y coordinate of the Rectangle. Note that this does *not* translate the
   * Rectangle. The requested {@link #property-height} will change.
   * @property {Number}
   */

  set y(y) {
    const yDelta = y - this._y;
    this._y = y;
    this._height -= yDelta;
  }

  get y() {
    return this._y;
  }
  /**
   * Get/sets the width of the Rectangle. Note that the requested {@link #property-right} will change.
   * @property {Number}
   */

  set width(width) {
    this._width = width;
  }

  get width() {
    return this._width;
  }
  /**
   * Get/sets the height of the Rectangle. Note that the requested {@link #property-bottom} will change.
   * @property {Number}
   */

  set height(height) {
    this._height = height;
  }

  get height() {
    return this._height;
  }
  /**
   * Get/sets the right edge of the Rectangle. Note that the requested {@link #property-width} will change.
   *
   * The right edge value is exclusive of the calculated rectangle width. So x=0 and right=10
   * means a width of 10.
   * @property {Number}
   */

  set right(right) {
    this._width = right - this._x;
  }

  get right() {
    return this._x + this._width;
  }
  /**
   * Get/sets the bottom edge of the Rectangle. Note that the requested {@link #property-height} will change.
   *
   * The bottom edge value is exclusive of the calculated rectangle height. So y=0 and bottom=10
   * means a height of 10.
   * @property {Number}
   */

  set bottom(bottom) {
    this._height = bottom - this._y;
  }

  get bottom() {
    return this._y + this._height;
  }

  get area() {
    return this.width * this.height;
  }

  set minWidth(minWidth) {
    const me = this;

    if (isNaN(minWidth)) {
      me._minWidth = null;
    } else {
      me._minWidth = Number(minWidth); // If this is being used as an alignment calculation rectangle, minWidth has a different meaning.
      // It does not mean that the width has to be at least this value. It means that under constraint,
      // it is willing to shrink down to that value before falling back to another align position.

      if (!me.isAlignRectangle) {
        me.width = Math.max(me.width, me._minWidth);
      }
    }
  }

  get minWidth() {
    return this._minWidth;
  }

  set minHeight(minHeight) {
    const me = this;

    if (isNaN(minHeight)) {
      me._minHeight = null;
    } else {
      me._minHeight = Number(minHeight); // If this is being used as an alignment calculation rectangle, minHeight has a different meaning.
      // It does not mean that the height has to be at least this value. It means that under constraint,
      // it is willing to shrink down to that value before falling back to another align position.

      if (!me.isAlignRectangle) {
        me.height = Math.max(me.height, me._minHeight);
      }
    }
  }

  get minHeight() {
    return this._minHeight;
  }
  /**
   * Modifies the bounds of this Rectangle by the specified deltas.
   * @param {Number} x How much to *add* to the x position.
   * @param {Number} y  How much to *add* to the y position.
   * @param {Number} width  How much to add to the width.
   * @param {Number} height  How much to add to the height.
   * @returns This Rectangle
   */

  adjust(x, y, width, height) {
    const me = this;
    me.x += x;
    me.y += y;
    me.width += width;
    me.height += height;
    return me;
  }
  /**
   * Modifies the bounds of this rectangle by moving them by the specified amount in all directions.
   * @param {Number} amount How much to inflate
   * @returns {Core.helper.util.Rectangle} This Rectangle
   */

  inflate(amount) {
    return this.adjust(-amount, -amount, amount, amount);
  }
  /**
   * Attempts constrain this Rectangle into the passed Rectangle. If the `strict` parameter is `true`
   * then this method will return `false` if constraint could not be acheived.
   *
   * If this Rectangle has a `minHeight` or `minWidth` property, size will be adjusted while attempting to constrain.
   *
   * Right and bottom are adjusted first leaving the top and bottom sides to "win" in the case that this Rectangle overflows
   * the constrainTo Rectangle.
   * @param {Core.helper.util.Rectangle} constrainTo The Rectangle to constrain this Rectangle into if possible.
   * @param {Boolean} strict Pass `true` to return false, and leave this Rectangle unchanged if constraint
   * could not be achieved.
   * @returns This Rectangle. If `strict` is true, and constraining was not successful, `false`.
   */

  constrainTo(constrainTo, strict) {
    const me = this,
          originalHeight = me.height,
          originalY = me.y,
          minWidth = me.minWidth || me.width,
          minHeight = me.minHeight || me.height;

    if (me.height >= constrainTo.height) {
      // If we're strict, fail if we could *never* fit into available height.
      if (strict && minHeight > constrainTo.height) {
        return false;
      } // If we are >= constrain height, we will have to be at top edge of constrainTo

      me._y = constrainTo.y;
      me.height = constrainTo.height;
    }

    if (me.width >= constrainTo.width) {
      // If we're strict, fail if we could *never* fit into available width.
      if (strict && minWidth > constrainTo.width) {
        // Could not be constrained; undo any previous attempt with height
        me.y = originalY;
        me.height = originalHeight;
        return false;
      } // If we are >= constrain width, we will have to be at left edge of constrainTo

      me._x = constrainTo.x;
      me.width = constrainTo.width;
    }

    let overflow = me.bottom - constrainTo.bottom; // Overflowing the bottom side, translate upwards.

    if (overflow > 0) {
      me.translate(0, -overflow);
    }

    overflow = me.right - constrainTo.right; // Overflowing the right side, translate leftwards.

    if (overflow > 0) {
      me.translate(-overflow);
    }

    overflow = constrainTo.y - me.y; // Now, after possible translation upwards, we overflow the top, translate downwards.

    if (overflow > 0) {
      me.translate(0, overflow);
    }

    overflow = constrainTo.x - me.x; // Now, after possible translation leftwards, we overflow the left, translate rightwards.

    if (overflow > 0) {
      me.translate(overflow);
    }

    return me;
  }
  /**
   * Returns a cloned version of this Rectangle aligned to a target Rectangle, or element or {@link Core.widget.Widget}.
   * @param {Object} spec Alignment specification.
   * @param {HTMLElement|Core.widget.Widget|Core.helper.util.Rectangle} spec.target The Widget or element or Rectangle to align to.
   * @param {Number[]} [spec.anchorSize] The `[width, height]` of the anchor pointer when in `top` position. The
   * width is the baseline length, and the height is the height of the arrow. If passed, the anchor position
   * will be calculated to be at the centre of the overlap of the two aligned edges and returned in the `anchor`
   * property of the resulting Rectangle:
   *
   *     {
   *         edge: 'top',         // or 'right' or 'bottom' or 'left'
   *         x/y: offset          // dimension to translate and value to translate by.
   *     }
   *
   * @param {Object} [spec.anchorPosition] an `{ x: n, y: n }` anchor translation to be used *if the requested alignment
   * succeeds without violating constraints*. If a fallback alignment is used, the anchor will be centered in the
   * overlap of the aligned edges as usual.
   * @param {Boolean} [spec.overlap] True to allow this to overlap the target.
   * @param {String} spec.align The edge alignment specification string, specifying two points to bring together.
   * The form is `[trblc][n]-[trblc][n]. The `n` is a percentage offset
   * along that edge which defines the alignment point. This is not valid for alignment point `c`.
   * For example `t0-b0' would align this Rectangle's top left corner with the bottom left corner of the `target`.
   * @param {HTMLElement|Core.widget.Widget|Core.helper.util.Rectangle} [spec.constrainTo] The Widget or Element or Rectangle to constrain to.
   * If the requested alignment cannot be constrained (it will first shrink the resulting Rectangle according
   * to the `minWidth` and `minHeight` properties of this rectangle), then it will try aligning at other edges
   * (honouring the `axisLock` option), and pick the fallback alignment which results in the shortest translation.
   * @param {Boolean} [spec.axisLock] Specify as a truthy value to fall back to aligning against the opposite
   * edge first if the requested alignment cannot be constrained into the `constrainTo` option. If specified
   * as `'flexible'`, then fallback will continue searching for solutions on the remaining two sides.
   * @param {Number|Number[]} [spec.offset] The 'x' and 'y' offset values to create an extra margin round the target
   * to offset the aligned widget further from the target. May be configured as -ve to move the aligned widget
   * towards the target - for example producing the effect of the anchor pointer piercing the target.
   * @param {Number} [spec.constrainPadding] The amount of pixels to pad from the constraining element
   * @returns {Core.helper.util.Rectangle} A new Rectangle aligned as requested if possible, but if the requested position violates
   * the `constrainTo` Rectangle, the shortest translation from the requested position which obeys constraints will be used.
   */

  alignTo(spec) {
    // The target and constrainTo may be passed as HtmlElements or Widgets.
    // If so, extract the Rectangles without mutating the incoming spec.
    let result = this.clone(),
        target = spec.target,
        constrainTo = spec.constrainTo,
        calculatedAnchorPosition,
        zone,
        resultZone,
        constrainingToViewport;

    if (target && !(target instanceof Rectangle)) {
      target = Rectangle.from(target.element ? target.element : target);
    }

    if (constrainTo && !(constrainTo instanceof Rectangle)) {
      // Viewport is denoted by requesting window or document.
      // document.body may overflow the viewport, so this must not be evaluated as the viewport.
      constrainingToViewport = constrainTo === window || constrainTo === document; // When rectangle is constrained to some element on the page other than window/document, page scroll
      // should not be taken into account

      const ignorePageScroll = 'ignorePageScroll' in spec ? spec.ignorePageScroll : !constrainingToViewport;
      constrainTo = Rectangle.from(constrainTo.element ? constrainTo.element : constrainTo, null, ignorePageScroll);

      if (spec.constrainPadding) {
        constrainTo.inflate(-spec.constrainPadding);
      }
    }

    const me = this,
          targetOffsets = createOffsets(spec.offset),
          {
      align,
      axisLock,
      anchorSize,
      anchorPosition
    } = spec,
          alignSpec = parseAlign(align),
          position = spec.position || (target && target.$$name === 'Point' ? target : null),
          targetConstrainRect = constrainTo && constrainTo.clone(),
          constraintZones = [],
          zoneOrder = [{
      zone: zone = alignSpec.startZone,
      align
    }],
          matchDimension = spec.matchSize && matchDimensions[alignSpec.startZone & 1],
          originalSize = me[matchDimension]; // Match the size of the edge we are aligning against

    if (matchDimension && axisLock) {
      result[matchDimension] = target[matchDimension];
    } // Ensure we will fit before trying

    if (constrainTo) {
      result.constrainTo(constrainTo);
    } // If we are aligning edge-to-edge, then plan our fallback strategy when we are constrained.

    if (constrainTo && alignSpec.startZone != null) {
      // Create the list of zone numbers and alignments to try in the preferred order.
      //
      // In the case of axisLock, go through the zones by each axis.
      // So if they asked for t-b, which is zone 2,
      // the array will be [2, 0, 3, 1] (t-b, b-t, r-l, l-r)
      if (axisLock) {
        // First axis flip has to maintain the offset along that axis.
        // so align: l0-r0 has to flip to align: r0-l0. See submenu flipping when
        // constrained to the edge. It flips sides but maintains vertical position.
        zoneOrder.push({
          zone: zone = (zone + 2) % 4,
          align: flipAlign(alignSpec)
        }); // Only try the other axis is axisLock is 'flexible'

        if (axisLock === 'flexible') {
          zoneOrder.push({
            zone: zone = (alignSpec.startZone + 1) % 4,
            align: defaultAlignments[zone]
          });
          zoneOrder.push({
            zone: zone = (zone + 2) % 4,
            align: defaultAlignments[zone]
          });
        }
      } // Go through the zones in order from the requested start.
      // So if they asked for t-b, which is zone 2,
      // the array will be [2, 3, 0, 1] (t-b, r-l, b-t, l-r)
      else {
          for (let i = 1; i < 4; i++) {
            zoneOrder.push({
              zone: zone = (zone + 1) % 4,
              align: defaultAlignments[zone]
            });
          }
        }
    } // Allow them to pass anchorPosition: {x: 10} to indicate that after a fully successful,
    // unconstrained align, the anchor should be 10px from the start.

    if (anchorPosition) {
      const pos = alignSpec.startZone & 1 ? 'y' : 'x';
      calculatedAnchorPosition = {
        [pos]: anchorPosition[pos],
        edge: edgeNames[(alignSpec.startZone + 2) % 4]
      };
    } // Keep the target within reach. If it's way outside, pull it back so that it's only just outside);

    if (targetConstrainRect && target) {
      targetConstrainRect.adjust(-target.width, -target.height, target.width, target.height);
      target.constrainTo(targetConstrainRect);
    } // As part of fallback process when fitting within constraints, result may shrink to our minima

    result.minWidth = me.minWidth;
    result.minHeight = me.minHeight; // We're being commanded to try to align at a position

    if (position) {
      result.moveTo(position.x, position.y);

      if (constrainTo) {
        result.constrainTo(constrainTo);
      }
    } // We're aligning to a Target Rectangle within a ConstrainTo Rectangle, taking into account
    // a possible anchor pointer, or x/y offsets. Here's the situation:
    //
    //                             <-- ConstrainTo Rectangle -->
    //  +-----------------------------------+--------------------+-------------------------+
    //  |                                   |                    |                         |
    //  |                                   |                    |                         |
    //  |                                   |                    |                         |
    //  |-----------------------------------+--------------------+-------------------------+
    //  |                                   |          ▼         |                         |
    //  |                                   | +----------------+ |                         |
    //  |                                   | |                | |                         |
    //  |                                   | |                | |                         |
    //  |                                   |▶|     Target     |◀|                         |
    //  |                                   | |                | |                         |
    //  |                                   | |                | |                         |
    //  |                                   | +----------------+ |                         |
    //  |                                   |          ▲         |                         |
    //  +-----------------------------------+--------------------+-------------------------|
    //  |                                   |                    |                         |
    //  |                                   |                    |                         |
    //  |                                   |                    |                         |
    //  +-----------------------------------+--------------------+-------------------------+
    //
    // Which results in the four possible constraint zones above, which we index in standard CSS order.
    //
    // Top    = 0
    // Right  = 1
    // Bottom = 2
    // Left   = 3
    //
    // If the initially requested alignment is not within the constrainTo rectangle
    // then, calculate these four, and then loop through them, beginning at the requested one,
    // quitting when we find a position which does not violate constraints. This includes
    // shrinking the aligning Rectangle towards its minima to attempt a fit.
    //
    // The final fallback, if there is no position which does not violate constraints
    // is to position in whichever of the four rectangles has the largest area shrinking overflowing
    // dimensions down to minima if specified.
    //
    else {
        // Offsets: If we are using an anchor to move away from the target, use anchor height in both dimensions.
        // It's rotated so that "height" always has the same meaning. It's the height of the arrow.
        const centerAligned = alignSpec.myEdge === 'c' || alignSpec.targetEdge === 'c',
              offsets = anchorSize && !centerAligned ? [anchorSize[1] + targetOffsets[0], anchorSize[1] + targetOffsets[1]] : targetOffsets,
              targetPoint = target.getAlignmentPoint(alignSpec.targetAlignmentPoint, offsets),
              myPoint = result.getAlignmentPoint(alignSpec.myAlignmentPoint);
        result.translate(targetPoint[0] - myPoint[0], targetPoint[1] - myPoint[1]); // If an overlapping position was requested, then we are *not* trying out those four zones.
        // We just respect constraint, and that's it.

        const overlap = result.intersect(target, true); // If we are aligned over our target, we just obey that within any constraint.
        // No complex edge alignment attempts to fall back to.

        if (overlap) {
          if (constrainTo) {
            result.constrainTo(constrainTo);
          }

          resultZone = alignSpec.startZone;
          result.translate(...offsets);
        } // Aligned to outside of our target, and we need to be constrained
        else if (constrainTo && !constrainTo.contains(result)) {
            let requestedResult = result.clone(),
                solutions = [],
                zone,
                largestZone; // Any configured anchorPosition becomes invalid now that we're having to move the resulting zone
            // to some unpredictable new place where it fits. It will have to be calculated based upon where
            // we end up aligning.

            calculatedAnchorPosition = null; // Calculate the four constraint zones illustrated above.
            // Top

            constraintZones[0] = zone = constrainTo.clone();
            zone.bottom = target.y - offsets[1]; // Right

            constraintZones[1] = zone = constrainTo.clone();
            zone.x = target.right + offsets[0]; // Bottom

            constraintZones[2] = zone = constrainTo.clone();
            zone.y = target.bottom + offsets[1]; // Left

            constraintZones[3] = zone = constrainTo.clone();
            zone.right = target.x - offsets[0]; // Start from the preferred edge and see if we are able to constrain to within each rectangle

            for (let i = 0; i < zoneOrder.length; i++) {
              // Revert to incoming dimension for fallback out of axisLock
              if (matchDimension && i == 2) {
                result[matchDimension] = originalSize;
              }

              zone = constraintZones[resultZone = zoneOrder[i].zone]; // Perform unconstrained alignment at the calculated alignment for the zone

              result = result.alignTo({
                target: target,
                offsets: offsets,
                align: zoneOrder[i].align
              }); // If we are able to strictly constrain into this area, then it's one of the possible solutions.
              // We choose the solution which result in the shortest translation from the initial position.

              if (result.constrainTo(zone, true)) {
                solutions.push({
                  result: result,
                  zone: resultZone
                }); // If this successful constraint is at the requested alignment, or at a fallback
                // alignment which has used min size constraints, then that's the correct solution.
                // If there's no size compromising, we have to pick the shortest translation.

                if (!largestZone || result.width < me.width || result.height < me.height) {
                  result.align = zoneOrder[i].align;
                  break;
                }
              } // Cache the largest zone we find in case we need the final fallback.

              if (!largestZone || zone.area > largestZone.area) {
                const r = result.clone(); // And just move the result clone into the edge zone

                switch (resultZone) {
                  // Top
                  case 0:
                    r.moveTo(null, zone.bottom - r.height);
                    break;
                  // Right

                  case 1:
                    r.moveTo(zone.left);
                    break;
                  // Bottom

                  case 2:
                    r.moveTo(null, zone.top);
                    break;
                  // Left

                  case 3:
                    r.moveTo(zone.right - r.width);
                    break;
                }

                largestZone = {
                  area: zone.area,
                  result: r,
                  zone: resultZone
                };
              }
            } // The loop found at least one solution

            if (solutions.length) {
              // Multiple fallbacks with no axisLock.
              // Use the solution which resulted in the shortest translation distance from the requested alignment.
              if (solutions.length > 1 && !axisLock) {
                solutions.sort((s1, s2) => {
                  const s1TranslationDistance = Math.sqrt((requestedResult.x - s1.result.x) ** 2 + (requestedResult.y - s1.result.y) ** 2),
                        s2TranslationDistance = Math.sqrt((requestedResult.x - s2.result.x) ** 2 + (requestedResult.y - s2.result.y) ** 2);
                  return s1TranslationDistance - s2TranslationDistance;
                });
              } // Initial success, or axisLock. Use first successful solution.

              result = solutions[0].result;
              resultZone = solutions[0].zone;
            } // No solutions found - use the largest rectangle.
            else {
                result = largestZone.result;
                resultZone = largestZone.zone; // When we are constraining to the viewport, we must still must be constrained,
                // even after we've given up making it align *and* constrain.

                if (constrainingToViewport) {
                  result.constrainTo(constrainTo);
                }
              }
          } else {
            resultZone = alignSpec.startZone;
          }

        result.zone = resultZone; // If they included an anchor, calculate its position along its edge.
        // TODO: Handle the edge overlap being less than anchor width.

        if (anchorSize && !overlap) {
          // If we were passed an anchorPosition, and it has remnained valid (meaning the requested
          // alignment succeeded with no constraint), then anchorPosition will be set. If not,
          // we have to calculate it based upon the aligned edge.
          if (!calculatedAnchorPosition) {
            let isLeftOrRight = resultZone & 1,
                start = isLeftOrRight ? 'y' : 'x',
                end = isLeftOrRight ? 'bottom' : 'right',
                startValue = Math.max(target[start], result[start]),
                endValue = Math.min(target[end], result[end]),
                anchorStart = startValue + (endValue - startValue) / 2 - anchorSize[0] / 2,
                anchorEnd = anchorStart + anchorSize[0];

            if (anchorEnd > result[end]) {
              anchorStart -= anchorEnd - result[end];
            }

            if (anchorStart < result[start]) {
              anchorStart += result[start] - anchorStart;
            } // Return an anchor property which will have an x or y property and an edge name onto which the
            // arrow should be aligned.

            calculatedAnchorPosition = {
              [start]: anchorStart - result[start],
              edge: edgeNames[(resultZone + 2) % 4]
            };
          }

          result.anchor = calculatedAnchorPosition;
        }
      }

    return result;
  }
  /**
   * Returns the `[x, y]` position of the specified anchor point of this Rectangle in <edge><offset> format.
   * for example passing "t50" will return the centre point of the top edge, passing "r0" will return the start
   * position of the right edge (the top right corner).
   *
   * Note that the offset defaults to 50, so "t" means the centre of the top edge.
   * @param {String} alignmentPoint The alignment point to calculate. Must match the RegExp `[trbl]\d*`
   * @param {Number[]} margins The `[x, y]` margins to add from the left/right, top/bottom edge.
   */

  getAlignmentPoint(alignmentPoint, margins = zeroOffsets) {
    const me = this,
          parts = alignPointRe.exec(alignmentPoint),
          edge = parts[1].toLowerCase(),
          edgeOffset = Math.min(Math.max(parseInt(parts[2] || 50), 0), 100) / 100;

    switch (edge) {
      case 't':
        return [me.x + me.width * edgeOffset, me.y - margins[1]];

      case 'r':
        return [me.right + margins[0], me.y + me.height * edgeOffset];

      case 'b':
        return [me.x + me.width * edgeOffset, me.bottom + margins[1]];

      case 'l':
        return [me.x - margins[0], me.y + me.height * edgeOffset];

      case 'c':
        {
          const center = me.center;
          return [center.x + margins[0], center.y + margins[1]];
        }
    }
  }
  /**
   * Highlights this Rectangle using the highlighting effect of {@link Core.helper.DomHelper}
   * on a transient element which encapsulates the region's area.
   */

  highlight() {
    const me = this,
          highlightElement = DomHelper.createElement({
      parent: document.body,
      style: `position:absolute;z-index:9999999;pointer-events:none;
                            left:${me.x}px;top:${me.y}px;width:${me.width}px;height:${me.height}px`
    });
    return DomHelper.highlight(highlightElement).then(() => highlightElement.remove());
  }

}
Rectangle._$name = 'Rectangle';

/**
 * @module Core/helper/util/DomClassList
 */

/**
 * This class encapsulates a list of CSS classes which can be set as the `className`
 * on an `HTMLElement`.
 *
 * Properties names set on this class equate to *adding* a class if the property's value
 * is _truthy_, or removing a class if the value is _falsy_.
 *
 * ```javascript
 * const myClassList = new DomClassList('b-test-button');
 *
 * myClassList.add('test-class');
 * myClassList.important = 1;
 *
 * myHtmlElement.className = myClassList; // Sets it to "b-test-button test-class important"
 * ```
 */

class DomClassList {
  static change(cls, add, remove, as = 'string') {
    remove = DomClassList.normalize(remove, 'object');
    const after = DomClassList.normalize(cls, 'array').filter(c => !remove[c]);

    if (add) {
      add = DomClassList.normalize(add, 'array');

      for (let i = 0; i < add.length; ++i) {
        if (!after.includes(add[i])) {
          after.push(add[i]);
        }
      }
    }

    return DomClassList.normalize(after, as);
  }
  /**
   *
   * @param {String|String[]|Object|HTMLElement} cls
   * @param {String} as Pass `'object'` to return an object with the class names as its
   * keys (all keys will have a value of `true`), or pass `'array'` to return an array
   * of class names, or pass `'string'` (the default) to return a space-separated string
   * of class names,
   * @returns {String|String[]|Object}
   * @private
   */

  static normalize(cls, as = 'string') {
    cls = cls || ''; // promote null to '' to avoid typeof snag

    const type = typeof cls,
          asArray = as === 'array',
          asObject = as === 'object',
          asString = !asArray && !asObject;
    let isString = type === 'string',
        c,
        ret;

    if (type === 'object') {
      if (cls.nodeType === 1 && typeof cls.getAttribute === 'function') {
        cls = cls.getAttribute('class'); // cannot use className for SVG el's

        isString = true;
      } else if (!Array.isArray(cls)) {
        cls = Objects.getTruthyKeys(cls);
      }
    }

    if (isString) {
      if (asString) {
        return cls;
      }

      cls = cls.split(' ');
    } // ret is now an array

    if (asArray) {
      ret = cls;
    } else if (asString) {
      ret = cls.join(' ');
    } else {
      ret = Object.create(null);

      for (c of cls) {
        ret[c] = true;
      }
    }

    return ret;
  }
  /**
   * Initializes a new DomClassList.
   * @param {String} classes CSS classes to create with
   * @function constructor
   */

  constructor(...classes) {
    if (typeof classes[0] === 'object') {
      Objects.assign(this, classes[0]);
    } else {
      this.process(1, classes);
    } // String value needs recalculating

    this[dirtySymbol] = true;
  }
  /**
   * Returns a clone of this DomClassList with all the same keys set.
   * @returns {Core.helper.util.DomClassList} A clone of this DomClassList.
   */

  clone() {
    return new DomClassList(this);
  }
  /**
   * Returns a Boolean value, indicating whether this ClassList has the specified CSS class name.
   * @param {String} className CSS class name to check
   * @return {Boolean} true if this ClassList contains the passed CSS class name, false otherwise
   */

  contains(className) {
    if (typeof className === 'string' && className) {
      return Boolean(this[className]);
    }

    return false;
  } // An instance of this class may be assigned directly to an element's className
  // it will be coerced to a string value using this method.

  toString() {
    // Adding space at the end if there is content to make concatenation code simpler in renderers.
    return this.length ? `${this.value} ` : '';
  }

  toJSON() {
    return this.toString();
  }
  /**
   * Analogous to string.trim, returns the string value of this `DomClassList` with no trailing space.
   * @returns {String} A concatenated string value of all the class names in this `DomClassList`
   * separated by spaces.
   */

  trim() {
    return this.value;
  }
  /**
   * Compares this ClassList to another ClassList (or class name string of space separated classes).
   * If the *same class names, regardless of order* are present, the two are considered equal.
   *
   * So `new DomClassList('foo bar bletch').isEqual('bletch bar foo')` would return `true`
   * @param {Core.helper.util.DomClassList|String} other The `DomClassList` or string of classes to compare to.
   * @returns {Boolean} `true` if the two contain the same class names.
   */

  isEqual(other) {
    if (typeof other === 'string') {
      testClassList.value = other;
      other = testClassList;
    }

    if (this.length === other.length) {
      const otherClasses = Objects.getTruthyKeys(other);

      for (let i = 0, len = otherClasses.length; i < len; i++) {
        if (!this[otherClasses[i]]) {
          return false;
        }
      }

      return true;
    }

    return false;
  }
  /**
   * Get/set string value.
   * Class names separated with space.
   * @property {String}
   */

  get value() {
    const me = this;

    if (me[dirtySymbol]) {
      const keys = Objects.getTruthyKeys(me);
      me[lengthSymbol] = keys.length;
      me[valueSymbol] = keys.join(' ');
      me[dirtySymbol] = false;
    }

    return me[valueSymbol];
  }

  set value(value) {
    const me = this,
          keys = Object.keys(me),
          len = keys.length;

    for (let i = 0; i < len; i++) {
      delete me[keys[i]];
    }

    if (value) {
      me.process(1, [value]);
    } else {
      // String value needs recalculating
      me[dirtySymbol] = true;
    }
  }
  /**
   * Returns string values as an array.
   * @readonly
   * @property {String[]}
   */

  get values() {
    return Objects.getTruthyKeys(this);
  }

  get length() {
    // Maintainer: We MUST access the value getter to force
    // the value to be calculated if it's currently dirty.
    return this.value ? this[lengthSymbol] : 0;
  }

  process(value, classes) {
    for (let i = 0, {
      length
    } = classes; i < length; i++) {
      const splitClasses = parseCls(classes[i]);

      for (let j = 0, {
        length
      } = splitClasses; j < length; j++) {
        if (splitClasses[j]) {
          this[splitClasses[j]] = value;
        }
      }
    } // String value needs recalculating

    this[dirtySymbol] = true;
    return this;
  }
  /**
   * Adds/removes class names according to the passed object's properties.
   *
   * Properties with truthy values are added.
   * Properties with false values are removed.
   * @param {Object} classList Object containing properties to set/clear
   */

  assign(classList) {
    for (const cls in classList) {
      if (Boolean(this[cls]) !== Boolean(classList[cls])) {
        this[cls] = classList[cls]; // String value needs recalculating

        this[dirtySymbol] = true;
      }
    }

    return this;
  }
  /**
   * Add CSS class(es)
   * ```
   * myClassList.add('bold', 'small');
   * ```
   * @param {String} classes CSS classes to add
   */

  add(...classes) {
    return this.process(1, classes);
  }
  /**
   * Remove CSS class(es)
   * ```
   * myClassList.remove('bold', 'small');
   * ```
   * @param {String} classes CSS classes to remove
   */

  remove(...classes) {
    return this.process(0, classes);
  }
  /**
   * Analogous to the `String#split` method, but with no delimiter
   * parameter. This method returns an array containing the individual
   * CSS class names set.
   * @returns {String[]} The individual class names in this `DomClassList`
   */

  split() {
    return Objects.getTruthyKeys(this);
  }

  forEach(fn) {
    return Objects.getTruthyKeys(this).forEach(fn);
  } // To gain some speed in DomHelper.sync(), faster than instanceof etc

  get isDomClassList() {
    return true;
  }

}

const whiteSpaceRe$1 = /\s+/,
      valueSymbol = Symbol('value'),
      lengthSymbol = Symbol('length'),
      dirtySymbol = Symbol('dirty'),
      testClassList = new DomClassList(),
      emptyArray = Object.freeze([]),
      parseCls = cls => {
  if (cls !== null && cls !== void 0 && cls.isDomClassList) {
    return cls.values;
  }

  if (cls instanceof DOMTokenList) {
    return Array.from(cls);
  }

  if (typeof cls === 'object') {
    return Objects.getTruthyKeys(cls);
  }

  if (cls !== null && cls !== void 0 && cls.split) {
    return cls.split(whiteSpaceRe$1);
  }

  if (Array.isArray(cls)) {
    return cls;
  }

  return emptyArray;
};

DomClassList._$name = 'DomClassList';

/* eslint-disable standard/no-callback-literal */
//import './util/Point.js';
// TODO import VersionHelper from './VersionHelper.js';
// Gives circular dependencies which I could not solve, called from global scope instead
//import GlobalEvents from '../GlobalEvents.js';
// https://app.assembla.com/spaces/bryntum/tickets/7903-rendering-fails
// HACK: this value is required to calculate width if it was configured relative to font size (em) but no element is set

const DEFAULT_FONT_SIZE = 14,
      t0t0 = {
  align: 't0-t0'
},
      // eslint-disable-next-line no-undef
ELEMENT_NODE = Node.ELEMENT_NODE,
      // eslint-disable-next-line no-undef
TEXT_NODE = Node.TEXT_NODE; // We only do the measurement once, if the value is null

let scrollBarWidth = null,
    idCounter = 0,
    themeInfo = null,
    root;

const // Transform matrix parse Regex. CSS transform computed style looks like this:
// matrix(scaleX(), skewY(), skewX(), scaleY(), translateX(), translateY())
// or
// matrix3d(scaleX(), skewY(), 0, 0, skewX(), scaleY(), 0, 0, 0, 0, 1, 0, translateX(), translateY())
// This is more reliable than using the style literal which may include
// relative styles such as "translateX(-20em)", or not include the translation at all if it's from a CSS rule.
// Use a const so as to only compile RexExp once
// Extract repeating number regexp to simplify next expressions. Available values are: https://developer.mozilla.org/en-US/docs/Web/CSS/number
numberRe = /[+-]?\d*\.?\d+[eE]?-?\d*/g,
      // -2.4492935982947064e-16 should be supported
numberReSrc = numberRe.source,
      translateMatrix2dRe = new RegExp(`matrix\\((?:${numberReSrc}),\\s?(?:${numberReSrc}),\\s?(?:${numberReSrc}),\\s?(?:${numberReSrc}),\\s?(${numberReSrc}),\\s?(${numberReSrc})`),
      translateMatrix3dRe = new RegExp(`matrix3d\\((?:-?\\d*),\\s?(?:-?\\d*),\\s?(?:-?\\d*),\\s?(?:-?\\d*),\\s?(?:-?\\d*),\\s?(?:-?\\d*),\\s?(?:-?\\d*),\\s?(?:-?\\d*),\\s?(?:-?\\d*),\\s?(?:-?\\d*),\\s?(?:-?\\d*),\\s?(?:-?\\d*),\\s?(-?\\d*),\\s?(-?\\d*)`),
      translateMatrixRe = new RegExp(`(?:${translateMatrix2dRe.source})|(?:${translateMatrix3dRe.source})`),
      pxTtranslateXRe = new RegExp(`translate(3d|X)?\\((${numberReSrc})px(?:,\\s?(${numberReSrc})px)?`),
      pxTtranslateYRe = new RegExp(`translate(3d|Y)?\\((${numberReSrc})px(?:,\\s?(${numberReSrc})px)?`),
      domIdRe = /^[^a-z]+|[^\w:.-]+/gi,
      whiteSpaceRe$2 = /\s+/,
      semicolonRe = /\s*;\s*/,
      colonRe = /\s*:\s*/,
      elementPropKey = '$bryntum',
      // DomHelper#createElement properties which require special processing.
// All other configs such as id and type are applied directly to the element.
elementCreateProperties = {
  tag: 1,
  html: 1,
  for: 1,
  children: 1,
  tooltip: 1,
  style: 1,
  dataset: 1,
  parent: 1,
  nextSibling: 1,
  ns: 1,
  reference: 1,
  className: 1,
  unmatched: 1,
  // Used by syncId approach
  _element: 1,
  // Used by sync to assign used element back to the config, for usage by the caller
  onlyChildren: 1,
  // Used by sync to not touch the target element itself,
  elementData: 1,
  listeners: 1,
  compareHtml: 1,
  // Sync,
  syncOptions: 1 // Sync

},
      styleIgnoreProperties = {
  length: 1,
  parentRule: 1,
  style: 1
},
      styleDimensionProperties = {
  width: 1,
  height: 1,
  top: 1,
  left: 1,
  minWidth: 1,
  minHeight: 1,
  maxWidth: 1,
  maxHeight: 1,
  fontSize: 1,
  'min-width': 1,
  'min-height': 1,
  'max-width': 1,
  'max-height': 1,
  'font-size': 1
},
      nativeFocusableTags = {
  BUTTON: 1,
  IFRAME: 1,
  EMBED: 1,
  INPUT: 1,
  OBJECT: 1,
  SELECT: 1,
  TEXTAREA: 1,
  HTML: BrowserHelper.isIE11 ? 1 : 0,
  BODY: BrowserHelper.isIE11 ? 0 : 1
},
      win = window,
      doc = document,
      emptyObject$1 = {},
      emptyArray$1 = [],
      arraySlice = Array.prototype.slice,
      immediatePromise = Promise.resolve(),
      fontProps = ['font-size', 'font-size-adjust', 'font-style', 'font-weight', 'font-family', 'font-kerning', 'font-stretch', 'line-height', 'text-transform', 'text-decoration', 'letter-spacing', 'word-break'],
      isHiddenWidget = e => e._hidden,
      parentNode = el => el.parentNode || el.host,
      isVisible = e => {
  const style = e.ownerDocument.defaultView.getComputedStyle(e);
  return style.getPropertyValue('display') !== 'none' && style.getPropertyValue('visibility') !== 'hidden';
},
      getRootNode = document.documentElement.getRootNode ? el => el.getRootNode() : el => {
  while (el.parentNode) el = el.parentNode;

  return el;
},
      // Both Shadow Root Element and Anchor <a> Element have `host` property.
// https://developer.mozilla.org/en-US/docs/Web/API/ShadowRoot/host
// https://developer.mozilla.org/en-US/docs/Web/API/HTMLHyperlinkElementUtils/host
// Need make sure `host` is an Element.
isShadowRoot = el => el.host && el.host instanceof Element,
      // Check whether the element has an offsetParent.
// Nodes such as SVG which do not expose such a property must have an ancestor which has an offsetParent.
hasLayout = el => el && (el === document.body || ('offsetParent' in el ? el.offsetParent : hasLayout(el.parentNode))),
      // Check for node being in document.
// If part of shadow DOM, see if the root's host is in the DOM
isInDocument = el => el && (document.body.contains(el) || (root = getRootNode(el)) && isShadowRoot(root) && isInDocument(root.host)),
      elementOrConfigToElement = elementOrConfig => {
  if (elementOrConfig instanceof Node) {
    return elementOrConfig;
  }

  if (typeof elementOrConfig === 'string') {
    return DH$1.createElementFromTemplate(elementOrConfig);
  }

  return DH$1.createElement(elementOrConfig);
};
let templateElement, htmlParser;
/**
 * @module Core/helper/DomHelper
 */

/**
 * Helps with dom querying and manipulation.
 * ```
 * DomHelper.createElement({
 *   tag: 'div',
 *   className: 'parent',
 *   style: 'background: red',
 *   children: [
 *      { tag: 'div', className: 'child' },
 *      { tag: 'div', className: 'child' }
 *   ]
 * });
 * ```
 */

class DomHelper {
  /**
   * Returns `true` if the passed element is focusable either programatically or through pointer gestures.
   * @param {HTMLElement} element The element to test.
   */
  static isFocusable(element, skipAccessibilityCheck = false) {
    if (!skipAccessibilityCheck) {
      // If element is hidden or in a hidden Widget, it's not focusable.
      if (!DH$1.isVisible(element) || DH$1.Widget.fromElement(element, isHiddenWidget)) {
        return false;
      }
    }

    const nodeName = element.nodeName;
    /*
     * An element is focusable if:
     *   - It is natively focusable, or
     *   - It is an anchor or link with href attribute, or
     *   - It has a tabIndex, or
     *   - It is an editing host (contenteditable="true")
     */

    return nativeFocusableTags[nodeName] || (nodeName === 'A' || nodeName === 'LINK') && !!element.href || element.getAttribute('tabIndex') != null || element.contentEditable === 'true';
  }
  /**
   * Returns `true` if the passed element is currently visible in the browser viewport, i.e. user can find it on screen
   * @param {HTMLElement} element The element to test.
   * @param {Boolean} whole Whether to check that whole element is visible, not just part of it.
   */

  static isInView(el, whole = true) {
    let elRect = Rectangle.from(el),
        inView = true;
    const fullHeight = elRect.height,
          fullWidth = elRect.width;

    while (inView && el.parentElement) {
      el = el.parentElement;
      elRect = elRect.intersect(Rectangle.from(el));
      inView = elRect && (!whole || elRect.height >= fullHeight && elRect.width >= fullWidth);
    }

    return inView;
  }
  /**
   * Returns `true` if the passed element is deeply visible. Meaning it is not hidden using `display`
   * or `visibility` and no ancestor node is hidden.
   * @param {HTMLElement} element The element to test.
   * @returns {Boolean} `true` if deeply visible.
   */

  static isVisible(element) {
    const document = element.ownerDocument; // Use the parentNode function so that we can traverse upwards through shadow DOM
    // to correctly ascertain visibility of nodes in web components.

    for (; element; element = parentNode(element)) {
      // Visible if we've reached top of the owning document without finding a hidden Element.
      if (element === document) {
        return true;
      } // Must not evaluate a shadow DOM's root fragment.

      if (element.nodeType === 1 && !isVisible(element)) {
        return false;
      }
    } // We get here if the node is detached.

    return false;
  }

  static roundPx(px, devicePixelRatio = window.devicePixelRatio || 1) {
    const multiplier = 1 / devicePixelRatio;
    return Math.round(px / multiplier) * multiplier;
  }
  /**
   * Returns true if element has opened shadow root
   * @param {HTMLElement} element Element to check
   * @returns {Boolean}
   */

  static isCustomElement(element) {
    return element && element.shadowRoot;
  }
  /**
   * Resolves element from point, checking shadow DOM if requried
   * @param {Number} x
   * @param {Number} y
   * @returns {HTMLElement}
   */

  static elementFromPoint(x, y) {
    let el = document.elementFromPoint(x, y); // Try to check shadow dom if it exists

    if (DH$1.isCustomElement(el)) {
      el = el.shadowRoot.elementFromPoint(x, y) || el;
    }

    return el;
  }
  /**
   * Returns active element checking shadow dom too
   * @readonly
   * @property {HTMLElement}
   */

  static get activeElement() {
    let el = document.activeElement;

    while (el.shadowRoot) {
      el = el.shadowRoot.activeElement;
    }

    return el;
  }
  /**
   * Returns the `id` of the passed element. Generates a unique `id` if the element does not have one.
   * @param {HTMLElement} element The element to return the `id` of.
   */

  static getId(element) {
    return element.id || (element.id = 'b-element-' + ++idCounter);
  }
  /**
   * Returns common widget/node ancestor for from/to arguments
   * @param {Widget/HTMLElement} from
   * @param {Widget/HTMLElement} to
   * @returns {Widget/HTMLElement}
   * @internal
   */

  static getCommonAncestor(from, to) {
    if (from === to) {
      return from;
    }

    while (from && !((_from = (_from2 = from)[from.isWidget ? 'owns' : 'contains']) !== null && _from !== void 0 && _from.call(_from2, to) || from === to)) {
      var _from, _from2;

      from = from.owner || from.parentNode;
    }

    return from;
  } //region Internal

  /**
   * Internal convenience fn to allow specifying either an element or a CSS selector to retrieve one
   * @private
   * @param {String|HTMLElement} elementOrSelector element or selector to lookup in DOM
   * @returns {HTMLElement}
   */

  static getElement(elementOrSelector) {
    // also used for SVG elements, so need to use more basic class, that is also returned by querySelector
    if (elementOrSelector instanceof Element) {
      return elementOrSelector;
    }

    return doc.querySelector(elementOrSelector);
  }
  /**
   * Sets attributes passed as object to given element
   * @private
   * @param {String|Element} elementOrSelector
   * @param {Object} attributes
   */

  static setAttributes(elementOrSelector, attributes) {
    const element = DH$1.getElement(elementOrSelector);
    Object.entries(attributes).forEach(([key, value]) => element.setAttribute(key, value));
  }
  /**
   * Sets a CSS [length](https://developer.mozilla.org/en-US/docs/Web/CSS/length) style value.
   * @param {String|HTMLElement} element The element to set the style in, or, if just the result is required,
   * the style magnitude to return with units added.
   * @param {String} [style] The name of a style property which specifies a [length](https://developer.mozilla.org/en-US/docs/Web/CSS/length)
   * @param {Number|String} [value] The magnitude. If a number is used, the value will be set in `px` units.
   * @returns {String} The style value string.
   */

  static setLength(element, style, value) {
    if (arguments.length === 1) {
      return typeof element === 'number' ? `${element}px` : element;
    } else {
      element = DH$1.getElement(element);
      return element.style[style] = typeof value === 'number' ? `${value}px` : value;
    }
  } //endregion
  //region Children, going down...

  /**
   * Gets the first direct child of `element` that matches `selector.
   * @param {HTMLElement} element Parent element
   * @param {String} selector CSS selector
   * @returns {HTMLElement}
   * @category Query children
   */

  static getChild(element, selector) {
    // TODO: Only IE11 doesn't support :scope
    if (BrowserHelper.supportsQueryScope) {
      selector = ':scope>' + selector;
    } else {
      const elId = element.id || (element.id = 'b-element-' + ++idCounter);
      selector = `#${elId} > ${selector}`;
    }

    return element.querySelector(selector);
  }
  /**
   * Checks if `element` has any child that matches `selector`.
   * @param {HTMLElement} element Parent element
   * @param {String} selector CSS selector
   * @returns {Boolean} true if any child matches selector
   * @category Query children
   */

  static hasChild(element, selector) {
    return DH$1.getChild(element, selector) != null;
  }
  /**
   * Returns all child elements (not necessarily direct children) that matches `selector`.
   *
   * If `selector` starts with `'>'` or `'# '`, then all components of the `selector` must match inside of `element`.
   * Where supported, `:scope` is prepended to the selector (and if `#` was used, it is removed). In other cases,
   * the `id` of the `element` is used to restrict the `selector`. If necessary, the `element` may be assigned a
   * generated `id` to enable this.
   *
   * These are equivalent:
   *
   *      DomHelper.children(el, '# .foo .bar');
   *
   *      el.querySelectorAll(':scope .foo .bar');
   *
   * These are also equivalent:
   *
   *      DomHelper.children(el, '> .foo .bar');
   *
   *      el.querySelectorAll(':scope > .foo .bar');
   *
   * @param {HTMLElement} element The parent element
   * @param {String} selector The CSS selector
   * @returns {HTMLElement[]} Matched elements, somewhere below `element`
   * @category Query children
   */

  static children(element, selector) {
    // a '#' could be '#id' but '# ' (hash and space) is not a valid selector...
    if (selector[0] === '>' || selector.startsWith('# ')) {
      if (selector[0] === '#') {
        selector = selector.substr(2);
      }

      if (BrowserHelper.supportsQueryScope) {
        selector = ':scope ' + selector;
      } else {
        const elId = element.id || (element.id = 'b-element-' + ++idCounter);
        selector = `#${elId} ${selector}`;
      }
    }

    return Array.from(element.querySelectorAll(selector));
  }
  /**
   * Looks at the specified `element` and all of its children for the one that first matches `selector.
   * @param {HTMLElement} element Parent element
   * @param {String} selector CSS selector
   * @returns {HTMLElement} Matched element, either element or an element below it
   * @category Query children
   */

  static down(element, selector) {
    if (!element) {
      return null;
    }

    if (element.matches && element.matches(selector)) {
      return element;
    }

    if (BrowserHelper.supportsQueryScope) {
      selector = ':scope ' + selector;
    } else {
      const elId = element.id || (element.id = 'b-element-' + ++idCounter);
      selector = `#${elId} ${selector}`;
    }

    return element.querySelector(selector);
  }
  /**
   * Checks if childElement is a descendant of parentElement (contained in it or a sub element)
   * @param {HTMLElement} parentElement Parent element
   * @param {HTMLElement} childElement Child element, at any level below parent
   * @returns {Boolean}
   * @category Query children
   */

  static isDescendant(parentElement, childElement) {
    // In case of IE11 and parentElement is <html>, HTMLDocument#contains is not supported - fallback to body
    if (!parentElement.contains) {
      parentElement = parentElement.body;
    }

    return parentElement.contains(childElement);
  }
  /**
   * Returns the specified element of the given `event`. If the `event` is an `Element`, it is returned. Otherwise,
   * the `eventName` argument is used to retrieve the desired element property from `event` (this defaults to the
   * `'target'` property).
   * @param {Event|Element} event
   * @param {String} [elementName]
   * @returns {Element}
   */

  static getEventElement(event, elementName = 'target') {
    return DH$1.isElement(event) ? event : event[elementName];
  }
  /**
   * Returns `true` if the provided value is _likely_ a DOM element. If the element can be assured to be from the
   * same document, `instanceof Element` is more reliable.
   * @param {*} value
   * @returns {Boolean}
   */

  static isElement(value) {
    return (value === null || value === void 0 ? void 0 : value.nodeType) === 1 && DH$1.isNode(value);
  }
  /**
   * Returns `true` if the provided value is _likely_ a DOM node. If the node can be assured to be from the same
   * document, `instanceof Node` is more reliable.
   * @param {*} value
   * @returns {Boolean}
   */

  static isNode(value) {
    // cannot use instanceof across frames. Using it here won't help since we'd need the same logic if it were
    // false... meaning we'd have the same chances of a false-positive.
    return Boolean(value) && typeof value.nodeType === 'number' && !ObjectHelper.isObject(value);
  }
  /**
   * Iterates over each result returned from `element.querySelectorAll(selector)`. First turns it into an array to
   * work in IE. Can also be called with only two arguments, in which case the first argument is used as selector and
   * document is used as the element.
   * @param {HTMLElement} element Parent element
   * @param {String} selector CSS selector
   * @param {Function} fn Function called for each found element
   * @category Query children
   */

  static forEachSelector(element, selector, fn) {
    if (typeof element === 'string') {
      fn = selector;
      selector = element;
      element = doc;
    }

    DH$1.children(element, selector).forEach(fn);
  }
  /**
   * Iterates over the direct child elements of the specified element. First turns it into an array to
   * work in IE.
   * @param {HTMLElement} element Parent element
   * @param {Function} fn Function called for each child element
   * @category Query children
   */

  static forEachChild(element, fn) {
    Array.from(element.children).forEach(fn);
  }
  /**
   * Removes each element returned from `element.querySelectorAll(selector)`.
   * @param {HTMLElement} element
   * @param {String} selector
   * @category Query children
   */

  static removeEachSelector(element, selector) {
    DH$1.forEachSelector(element, selector, child => child.remove());
  }

  static removeClsGlobally(element, ...classes) {
    classes.forEach(cls => DH$1.forEachSelector(element, '.' + cls, child => child.classList.remove(cls)));
  } //endregion
  //region Parents, going up...

  /**
   * Looks at the specified element and all of its parents for the one that first matches selector.
   * @param {HTMLElement} element Element
   * @param {String} selector CSS selector
   * @returns {HTMLElement} Matched element, either the passed in element or an element above it
   * @category Query parents
   */

  static up(element, selector) {
    return element && element.closest(selector);
  }

  static getAncestor(element, possibleAncestorParents, outerElement = null) {
    let found = false,
        ancestor,
        parent = element;
    if (!Array.isArray(possibleAncestorParents)) possibleAncestorParents = [possibleAncestorParents];

    while (parent = parent.parentElement) {
      if (possibleAncestorParents.includes(parent)) {
        found = true;
        break;
      }

      if (outerElement && parent === outerElement) break;
      ancestor = parent;
    }

    if (!found) return null;
    return ancestor || element;
  }
  /**
   * Retrieves all parents to the specified element.
   * @param {HTMLElement} element Element
   * @returns {HTMLElement[]} All parent elements, bottom up
   * @category Query parents
   */

  static getParents(element) {
    const parents = [];

    while (element.parentElement) {
      parents.push(element.parentElement);
      element = element.parentElement;
    }

    return parents;
  } //endregion
  //region Creation

  /**
   * Converts the passed id to an id valid for usage as id on a DOM element.
   * @param {String} id
   * @returns {String}
   */

  static makeValidId(id) {
    if (id == null) return null;
    return String(id).replace(domIdRe, '');
  }
  /**
   * Creates an Element. Example usage:
   * @example
   * DomHelper.createElement({
   *   tag         : 'table', // defaults to 'div'
   *   cellSpacing : 0,
   *   className   : 'nacho',
   *   html        : 'I am a nacho',
   *   children    : [ { tag: 'tr', ... }, myDomElement ],
   *   parent      : myExistingElement // Or its id
   *   style       : 'font-weight: bold;color: red',
   *   dataset     : { index: 0, size: 10 },
   *   tooltip     : 'Yay!',
   *   ns          : 'http://www.w3.org/1999/xhtml'
   * });
   * @param {Object} config Element config, as in example
   * @param {Boolean|Object} options An object specifying creation options. If this is a boolean value, it is
   * understood to be the `returnAll` option. Passing a boolean is deprecated in favor of the `options` object.
   * @param {Boolean} options.returnAll Specify true to return all elements & child elements
   * created as an array.
   * @returns {HTMLElement|HTMLElement[]|Object} Single element or array of elements `returnAll` was set to true.
   * If any elements had a `reference` property, this will be an object containing a reference to
   * all those elements, keyed by the reference name.
   * @category Creation
   */

  static createElement(config = {}, options, ...args) {
    let returnAll = options,
        refOwner,
        refs,
        syncIdField;

    if (typeof returnAll === 'boolean') {
      refs = args[0];
      syncIdField = args[1]; // TODO There are many internal calls that will need refactoring to the object form before we can do this:
      // VersionHelper.deprecate('Core', '4.0.0',
      //     'DomHelper.createElement should be given an options object not the returnAll boolean');

      options = {
        returnAll,
        refs,
        syncIdField
      };
    } else if (options) {
      refOwner = options.refOwner;
      refs = options.refs;
      returnAll = options.returnAll;
      syncIdField = options.syncIdField;
    }

    if (typeof config.parent === 'string') {
      config.parent = document.getElementById(config.parent);
    }

    if (config.syncOptions) {
      syncIdField = config.syncOptions.syncIdField || syncIdField;
    } // nextSibling implies a parent

    const parent = config.parent || config.nextSibling && config.nextSibling.parentNode,
          {
      reference
    } = config;
    let element;

    if (config.ns) {
      element = doc.createElementNS(config.ns, config.tag || 'svg');
    } else {
      element = doc.createElement(config.tag || 'div');
    }

    if (config.html != null) {
      if (config.html instanceof DocumentFragment) {
        element.appendChild(config.html);
      } else {
        element.innerHTML = config.html;
      }
    }

    if (config.tooltip) {
      DH$1.Widget.attachTooltip(element, config.tooltip);
    }

    if (config.style) {
      DH$1.applyStyle(element, config.style);
    }

    if (config.dataset) {
      Object.assign(element.dataset, config.dataset);
    }

    if (config.for) {
      element.htmlFor = config.for;
    }

    if (parent) {
      parent.insertBefore(element, config.nextSibling);
    }

    if (refOwner) {
      // Tag each element created by the refOwner's id to enable DomSync
      element.$refOwnerId = refOwner.id;
    }

    if (reference) {
      // SalesForce platform does not allow custom attributes, but existing code
      // uses querySelector('[reference]'), so bypass it when we can:
      if (refOwner) {
        refOwner.byRef[element.$reference = reference] = element;
        refOwner.attachRef(reference, element, config);
      } else {
        // TODO fixup callers to do the above
        if (!refs) {
          options = Object.assign({}, options);
          options.refs = refs = {};
        }

        refs[reference] = element;
        element.setAttribute('data-reference', reference);
      }
    } // Attach custom data to the element, not visible

    if (config.elementData) {
      element.elementData = config.elementData;
    }

    const className = config.className || config.class; // matches DomSync

    if (className) {
      element.setAttribute('class', DomClassList.normalize(className));
    } // Handle things like id, className, type, rel, cellSpacing, href etc which just get assigned.

    for (const prop of Object.keys(config)) {
      if (prop && !elementCreateProperties[prop]) {
        if (config.ns) {
          element.setAttribute(prop, config[prop]); //element.setAttributeNS(config.ns, prop, config[prop]);
        } else {
          element[prop] = config[prop];
        }
      }
    } // if returnAll is true, use array

    if (returnAll === true) {
      options.returnAll = returnAll = [element];
    } // if it already is an array, add to it (we are probably a child)
    else if (Array.isArray(returnAll)) {
        returnAll.push(element);
      }

    if (config.children) {
      if (syncIdField) {
        // Map syncId -> child element to avoid querying dom later on
        element.syncIdMap = {};
      }

      config.children.forEach(child => {
        // Skip null children, convenient to allow those for usage with Array.map()
        if (child) {
          // Append string children as text nodes
          if (typeof child === 'string') {
            const textNode = document.createTextNode(child);

            if (refOwner) {
              textNode.$refOwnerId = refOwner.id;
            }

            element.appendChild(textNode);
          } // Just append Elements directly.
          else if (isNaN(child.nodeType)) {
              child.parent = element;

              if (!child.ns && config.ns) {
                child.ns = config.ns;
              }

              const childElement = DH$1.createElement(child, options),
                    syncId = child.dataset && child.dataset[syncIdField]; // syncId is used with DomHelper.sync to match elements. Populate a map here to make finding them faster

              if (syncId != null) {
                element.syncIdMap[syncId] = childElement;
              } // Do not want to alter the initial config

              delete child.parent;
            } else {
              element.appendChild(child);
            }
        }
      });
    } // Store used config, to be able to compare on sync to determine if changed without hitting dom

    element.lastDomConfig = config; // Link element to config for easy retrieval

    config._element = element; // If references were used, return them in an object
    // If returnAll was specified, return the array
    // By default, return the root element

    return refs || returnAll || element;
  }
  /**
   * Create element(s) from a template (html string). Note that
   * `textNode`s are discarded unless the `raw` option is passed
   * as `true`.
   *
   * If the template has a single root element, then the single element will be returned
   * unless the `array` option is passed as `true`.
   *
   * If there are multiple elements, then an Array will be returned.
   *
   * @param {String} template The HTML string from which to create DOM content
   * @param {Object} [options] An object containing properties to modify how the DOM is created and returned.
   * @param {Boolean} [options.array] `true` to return an array even if there's only one resulting element.
   * @param {Boolean} [options.raw] Return all child nodes, including text nodes.
   * @param {Boolean} [options.fragment] Return a DocumentFragment.
   * @private
   */

  static createElementFromTemplate(template, options = emptyObject$1) {
    const {
      array,
      raw,
      fragment
    } = options;
    let result; // Use template by preference if it exists. It's faster on most supported platforms
    // https://jsperf.com/domparser-vs-template/

    if (DH$1.supportsTemplate) {
      (templateElement || (templateElement = doc.createElement('template'))).innerHTML = template;
      result = templateElement.content;

      if (fragment) {
        // The template is reused, so therefore is its fragment.
        // If we release the fragment to a caller, it must be a clone.
        return result.cloneNode(true);
      }
    } else {
      (htmlParser || (htmlParser = new DOMParser())).parseFromString(template, 'text/html');
      result = htmlParser.parseFromString(template, 'text/html').body; // We must return a DocumentFragment.
      // myElement.append(fragment) inserts the contents of the fragment, not the fragment itself.

      if (fragment) {
        // Empty string results in *no document.body* on IE!
        const nodes = result ? result.childNodes : emptyArray$1;
        result = document.createDocumentFragment();

        while (nodes.length) {
          result.appendChild(nodes[0]);
        }

        return result;
      } // Happens with empty template in IE11
      else if (!result) {
          result = {
            children: [],
            childNodes: []
          };
        }
    } // Raw means all child nodes are returned

    if (raw) {
      result = result.childNodes;
    } // Otherwise, only element nodes
    else {
        result = result.children;
      }

    return result.length === 1 && !array ? result[0] : arraySlice.call(result);
  }
  /**
   * Inserts an `element` at first position in `into`.
   * @param {HTMLElement} into Parent element
   * @param {HTMLElement} element Element to insert, or an element config passed on to createElement()
   * @returns {HTMLElement}
   * @category Creation
   */

  static insertFirst(into, element) {
    if (element && element.nodeType !== ELEMENT_NODE && element.tag) {
      element = DH$1.createElement(element);
    }

    return into.insertBefore(element, into.firstElementChild);
  }
  /**
   * Inserts a `element` before `beforeElement` in `into`.
   * @param {HTMLElement} into Parent element
   * @param {HTMLElement} element Element to insert, or an element config passed on to createElement()
   * @param {HTMLElement} beforeElement Element before which passed element should be inserted
   * @returns {HTMLElement}
   * @category Creation
   */

  static insertBefore(into, element, beforeElement) {
    if (element && element.nodeType !== ELEMENT_NODE && element.tag) {
      element = DH$1.createElement(element);
    }

    return beforeElement ? into.insertBefore(element, beforeElement) : DH$1.insertFirst(into, element);
  }

  static insertAt(parentElement, newElement, index) {
    const siblings = Array.from(parentElement.children);

    if (index >= siblings.length) {
      return DH$1.append(parentElement, newElement);
    }

    const beforeElement = siblings[index];
    return DH$1.insertBefore(parentElement, newElement, beforeElement);
  }
  /**
   * Appends element to parentElement.
   * @param {HTMLElement} parentElement Parent element
   * @param {HTMLElement|Object|String} elementOrConfig Element to insert, or an element config passed on to createElement(), or an html string passed to createElementFromTemplate
   * @returns {HTMLElement}
   * @category Creation
   */

  static append(parentElement, elementOrConfig) {
    if (elementOrConfig.forEach) {
      // Ensure all elements of an Array are HTMLElements.
      // The other implementor of forEach is a NodeList which needs no conversion.
      if (Array.isArray(elementOrConfig)) {
        elementOrConfig = elementOrConfig.map(elementOrConfig => elementOrConfigToElement(elementOrConfig));
      }

      if (parentElement.append) {
        parentElement.append(...elementOrConfig);
      } else {
        const docFrag = document.createDocumentFragment();
        elementOrConfig.forEach(function (child) {
          docFrag.appendChild(child);
        });
        parentElement.appendChild(docFrag);
      }

      return elementOrConfig;
    } else {
      return parentElement.appendChild(elementOrConfigToElement(elementOrConfig));
    }
  } //endregion
  //region Get position

  /**
   * Returns the element's `transform translateX` value in pixels.
   * @param {HTMLElement} element
   * @returns {Number} X transform
   * @category Position, get
   */

  static getTranslateX(element) {
    const transformStyle = element.style.transform;
    let matches = pxTtranslateXRe.exec(transformStyle); // Use inline transform style if it contains "translate(npx, npx" or "translate3d(npx, npx" or "translateX(npx"

    if (matches) {
      return parseInt(matches[2]);
    } else {
      // If the inline style is the matrix() form, then use that, otherwise, use computedStyle
      matches = translateMatrixRe.exec(transformStyle) || translateMatrixRe.exec(DH$1.getStyleValue(DH$1.getElement(element), 'transform'));
      return matches ? parseInt(matches[1] || matches[3]) : 0;
    }
  }
  /**
   * Returns the element's `transform translateY` value in pixels.
   * @param {HTMLElement} element
   * @returns {Number} Y coordinate
   * @category Position, get
   */

  static getTranslateY(element) {
    const transformStyle = element.style.transform;
    let matches = pxTtranslateYRe.exec(transformStyle); // Use inline transform style if it contains "translate(npx, npx" or "translate3d(npx, npx" or "translateY(npx"

    if (matches) {
      // If it was translateY(npx), use first item in the parens.
      const y = parseInt(matches[matches[1] === 'Y' ? 2 : 3]); // FF will strip `translate(x, 0)` -> `translate(x)`, so need to check for isNaN also

      return isNaN(y) ? 0 : y;
    } else {
      // If the inline style is the matrix() form, then use that, otherwise, use computedStyle
      matches = translateMatrixRe.exec(transformStyle) || translateMatrixRe.exec(DH$1.getStyleValue(DH$1.getElement(element), 'transform'));
      return matches ? parseInt(matches[2] || matches[4]) : 0;
    }
  }
  /**
   * Gets both X and Y coordinates as an array [x, y]
   * @param {HTMLElement} element
   * @returns {Number[]} [x, y]
   * @category Position, get
   */

  static getTranslateXY(element) {
    return [DH$1.getTranslateX(element), DH$1.getTranslateY(element)];
  }
  /**
   * Get elements X offset within a containing element
   * @param {HTMLElement} element
   * @param {HTMLElement} container
   * @returns {Number} X offset
   * @category Position, get
   */

  static getOffsetX(element, container = null) {
    return container ? element.getBoundingClientRect().left - container.getBoundingClientRect().left : element.offsetLeft;
  }
  /**
   * Get elements Y offset within a containing element
   * @param {HTMLElement} element
   * @param {HTMLElement} container
   * @returns {Number} Y offset
   * @category Position, get
   */

  static getOffsetY(element, container = null) {
    return container ? element.getBoundingClientRect().top - container.getBoundingClientRect().top : element.offsetTop;
  }
  /**
   * Gets elemnts X and Y offset within containing element as an array [x, y]
   * @param {HTMLElement} element
   * @param {HTMLElement} container
   * @returns {Number[]} [x, y]
   * @category Position, get
   */

  static getOffsetXY(element, container = null) {
    return [DH$1.getOffsetX(element, container), DH$1.getOffsetY(element, container)];
  }
  /**
   * Focus element without scrolling the element into view.
   * @param {HTMLElement} element
   */

  static focusWithoutScrolling(element) {
    function resetScroll(scrollHierarchy) {
      scrollHierarchy.forEach(({
        element,
        scrollLeft,
        scrollTop
      }) => {
        // Check first to avoid triggering unnecessary `scroll` events
        if (element.scrollLeft !== scrollLeft) {
          element.scrollLeft = scrollLeft;
        }

        if (element.scrollTop !== scrollTop) {
          element.scrollTop = scrollTop;
        }
      });
    } // Check browsers which do support focusOptions
    // https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/focus

    const preventScrollSupported = BrowserHelper.chromeVersion >= 64 || BrowserHelper.firefoxVersion >= 68;

    if (preventScrollSupported) {
      element.focus({
        preventScroll: true
      });
    } else {
      // Examine every parentNode of the target and cache the scrollLeft and scrollTop,
      // and restore all values after the focus has taken place
      const parents = DH$1.getParents(element),
            scrollHierarchy = parents.map(parent => ({
        element: parent,
        scrollLeft: parent.scrollLeft,
        scrollTop: parent.scrollTop
      }));
      element.focus(); // Reset in sync on IE, async in others

      if (BrowserHelper.isIE11) {
        resetScroll(scrollHierarchy);
      } else {
        setTimeout(() => resetScroll(scrollHierarchy), 0);
      }
    }
  }
  /**
   * Get elements X position on page
   * @param {HTMLElement} element
   * @returns {Number}
   * @category Position, get
   */

  static getPageX(element) {
    return element.getBoundingClientRect().left + win.pageXOffset; // no window.scrollX in IE11
  }
  /**
   * Get elements Y position on page
   * @param {HTMLElement} element
   * @returns {Number}
   * @category Position, get
   */

  static getPageY(element) {
    return element.getBoundingClientRect().top + win.pageYOffset; // no window.scrollY in IE11
  }
  /**
   * Returns extremal (min/max) size (height/width) of the element in pixels
   * @param {HTMLElement} element
   * @param {String} style minWidth/minHeight/maxWidth/maxHeight
   * @returns {number}
   * @internal
   */

  static getExtremalSizePX(element, style) {
    const prop = StringHelper.hyphenate(style),
          measure = prop.split('-')[1];
    let value = DH$1.getStyleValue(element, prop);

    if (/%/.test(value)) {
      // Element might be detached from DOM
      if (element.parentElement) {
        value = parseInt(DH$1.getStyleValue(element.parentElement, measure), 10);
      } else {
        value = NaN;
      }
    } else {
      value = parseInt(value, 10);
    }

    return value;
  } //endregion
  //region Set position

  /**
   * Set element's `X` translation in pixels.
   * @param {HTMLElement} element
   * @param {Number} x The value by which the element should be translated from its default position.
   * @category Position, set
   */

  static setTranslateX(element, x) {
    const t = DH$1.getStyleValue(element, 'transform').split(/,\s*/); // Avoid blurry text on non-retina displays

    x = DomHelper.roundPx(x);

    if (t.length > 1) {
      t[t[0].startsWith('matrix3d') ? 12 : 4] = x;
      element.style.transform = t.join(',');
    } else {
      element.style.transform = `translateX(${x}px)`;
    }
  }
  /**
   * Set element's `Y` translation in pixels.
   * @param {HTMLElement} element
   * @param {Number} y  The value by which the element should be translated from its default position.
   * @category Position, set
   */

  static setTranslateY(element, y) {
    const t = DH$1.getStyleValue(element, 'transform').split(/,\s*/); // Avoid blurry text on non-retina displays

    y = DomHelper.roundPx(y);

    if (t.length > 1) {
      t[t[0].startsWith('matrix3d') ? 13 : 5] = y;
      element.style.transform = t.join(',') + ')';
    } else {
      element.style.transform = `translateY(${y}px)`;
    }
  }
  /**
   * Set element's style `top`.
   * @param {HTMLElement} element
   * @param {Number|String} y The top position. If numeric, `'px'` is used as the unit.
   * @category Position, set
   */

  static setTop(element, y) {
    DH$1.setLength(element, 'top', y);
  }
  /**
   * Set element's style `left`.
   * @param {HTMLElement} element
   * @param {Number|String} x The top position. If numeric, `'px'` is used as the unit.
   * @category Position, set
   */

  static setLeft(element, x) {
    DH$1.setLength(element, 'left', x);
  }
  /**
   * Set elements `X` and `Y` translation in pixels.
   * @param {HTMLElement} element
   * @param {Number} [x] The `X translation.
   * @param {Number} [y] The `Y translation.
   * @category Position, set
   */

  static setTranslateXY(element, x, y) {
    if (x == null) {
      return DH$1.setTranslateY(element, y);
    }

    if (y == null) {
      return DH$1.setTranslateX(element, x);
    } // Avoid blurry text on non-retina displays

    x = DomHelper.roundPx(x);
    y = DomHelper.roundPx(y);
    const t = DH$1.getStyleValue(element, 'transform').split(/,\s*/),
          is3d = t[0].startsWith('matrix3d');

    if (t.length > 1) {
      t[is3d ? 12 : 4] = x;
      t[is3d ? 13 : 5] = y;
      element.style.transform = t.join(',') + ')';
    } else {
      element.style.transform = `translate(${x}px, ${y}px)`;
    }
  }
  /**
   * Increase `X` translation
   * @param {HTMLElement} element
   * @param {Number} x The number of pixels by which to increase the element's `X` translation.
   * @category Position, set
   */

  static addTranslateX(element, x) {
    DH$1.setTranslateX(element, DH$1.getTranslateX(element) + x);
  }
  /**
   * Increase `Y` position
   * @param {HTMLElement} element
   * @param {Number} y The number of pixels by which to increase the element's `Y` translation.
   * @category Position, set
   */

  static addTranslateY(element, y) {
    DH$1.setTranslateY(element, DH$1.getTranslateY(element) + y);
  }
  /**
   * Increase X position
   * @param {HTMLElement} element
   * @param {Number} x
   * @category Position, set
   */

  static addLeft(element, x) {
    DH$1.setLeft(element, DH$1.getOffsetX(element) + x);
  }
  /**
   * Increase Y position
   * @param {HTMLElement} element
   * @param {Number} y
   * @category Position, set
   */

  static addTop(element, y) {
    DH$1.setTop(element, DH$1.getOffsetY(element) + y);
  }
  /**
   * Align the passed element with the passed target according to the align spec.
   * @param {HTMLElement} element The element to align.
   * @param {HTMLElement|Core.helper.util.Rectangle} target The target element or rectangle to align to
   * @param {Object} alignSpec See {@link Core.helper.util.Rectangle#function-alignTo} Defaults to `{ align : 't0-t0' }`
   * @param {Boolean} [round] Round the calculated Rectangles (for example if dealing with scrolling which
   * is integer based).
   */

  static alignTo(element, target, alignSpec = t0t0, round) {
    target = target instanceof Rectangle ? target : Rectangle.from(target, true);
    const elXY = DH$1.getTranslateXY(element),
          elRect = Rectangle.from(element, true);

    if (round) {
      elRect.roundPx();
      target.roundPx();
    }

    const targetRect = elRect.alignTo(Object.assign(alignSpec, {
      target
    }));
    DH$1.setTranslateXY(element, elXY[0] + targetRect.x - elRect.x, elXY[1] + targetRect.y - elRect.y);
  } //endregion
  //region Styles & CSS

  /**
   * Returns a style value or values for the passed element.
   * @param {HTMLElement} element The element to read styles from
   * @param {String|String[]} propName The property or properties to read
   * @param {Boolean} [inline=false] Pass as `true` to read the element's inline style.
   * Note that this could return inaccurate results if CSS rules apply to this element.
   * @return {String|Object} The value or an object containing the values keyed by the requested property name.
   * @category CSS
   */

  static getStyleValue(element, propName, inline, pseudo) {
    const styles = inline ? element.style : element.ownerDocument.defaultView.getComputedStyle(element, pseudo);

    if (Array.isArray(propName)) {
      const result = {};

      for (const prop of propName) {
        result[prop] = styles.getPropertyValue(StringHelper.hyphenate(prop));
      }

      return result;
    } // Use the elements owning view to get the computed style.
    // Ensure the property name asked for is hyphenated.
    // getPropertyValue doesn't work with camelCase

    return styles.getPropertyValue(StringHelper.hyphenate(propName));
  }
  /**
   * Returns an object with the parse style values for the top, right, bottom, and left
   * components of the given edge style.
   *
   * The return value is an object with `top`, `right`, `bottom`, and `left` properties
   * for the respective components of the edge style, as well as `width` (the sum of
   * `left` and `right`) and `height` (the sum of `top` and `bottom`).
   *
   * @param {HTMLElement} element
   * @param {String} edgeStyle The element's desired edge style such as 'padding', 'margin',
   * or 'border'.
   * @param {String} [edges='trbl'] A string with one character codes for each edge. Only
   * those edges will be populated in the returned object. By default, all edges will be
   * populated.
   * @returns {Object}
   */

  static getEdgeSize(element, edgeStyle, edges) {
    const suffix = edgeStyle === 'border' ? '-width' : '',
          ret = {
      raw: {}
    };

    for (const edge of ['top', 'right', 'bottom', 'left']) {
      if (!edges || edges.includes(edge[0])) {
        // This produces px units even if the provided style is em or other (i.e.,
        // getComputedStyle normalizes this):
        ret[edge] = parseFloat(ret.raw[edge] = DomHelper.getStyleValue(element, `${edgeStyle}-${edge}${suffix}`));
      }
    } // These may not even be requested (based on "edges") but conditional code here
    // would be wasted since the caller would still need to know not to use them...
    // Replace NaN with 0 to keep calculations correct if they only asked for one side.

    ret.width = (ret.left || 0) + (ret.right || 0);
    ret.height = (ret.top || 0) + (ret.bottom || 0);
    return ret;
  }
  /**
   * Splits a style string up into object form. For example `'font-weight:bold;font-size:150%'`
   * would convert to
   *
   * ```javascript
   * {
   *     font-weight : 'bold',
   *     font-size : '150%'
   * }
   * ```
   * @param {String} style A DOM style string
   * @returns {Object} the style declaration in object form.
   */

  static parseStyle(style) {
    if (typeof style === 'string') {
      const styles = style.split(semicolonRe);
      style = {};

      for (let i = 0, {
        length
      } = styles; i < length; i++) {
        const propVal = styles[i].split(colonRe);
        style[propVal[0]] = propVal[1];
      }
    }

    return style || {};
  }
  /**
   * Applies specified style to the passed element. Style can be an object or a string.
   * @param {HTMLElement} element Target element
   * @param {String|Object} style Style to apply, 'border: 1px solid black' or { border: '1px solid black' }
   * @param {Boolean} [overwrite] Specify `true` to replace style instead of applying changes
   * @category CSS
   */

  static applyStyle(element, style, overwrite = false) {
    if (typeof style === 'string') {
      if (overwrite) {
        // Only assign if either end has any styles, do not want to add empty `style` tag on element
        if (style.length || element.style.cssText.length) {
          element.style.cssText = style;
        }
      } else {
        // Add style so as not to delete configs in style such as width, height, flex etc.
        // If a style is already there, the newest, appended one will take precedence.
        element.style.cssText += style;
      }
    } else {
      if (overwrite) {
        element.style.cssText = ''; //element.removeAttribute('style');
      } // Has a sub-style block?

      if (style.style) {
        if (typeof style.style === 'string') {
          element.style.cssText = style.style;
        } else {
          style = Object.assign({}, style.style, style);
        }
      } // Prototype chained objects may be passed, so use direct loop.

      for (const key in style) {
        // Ignore readonly properties of the CSSStyleDeclaration object:
        // https://developer.mozilla.org/en-US/docs/Web/API/CSSStyleDeclaration
        // Also ignores sub-style blocks, which are applied above
        if (!styleIgnoreProperties[key]) {
          // Append 'px' for numeric dimensions
          if (styleDimensionProperties[key] && typeof style[key] == 'number') {
            element.style[StringHelper.hyphenate(key)] = style[key] + 'px';
          } else {
            element.style[StringHelper.hyphenate(key)] = style[key];
          }
        }
      }
    }
  }

  static getCSSText(style) {
    if (typeof style === 'string') {
      return style;
    }

    let cssText = '';

    for (const key in style) {
      // Ignore readonly properties of the CSSStyleDeclaration object:
      // https://developer.mozilla.org/en-US/docs/Web/API/CSSStyleDeclaration
      if (!styleIgnoreProperties[key]) {
        cssText += `${StringHelper.hyphenate(key)}:${style[key]};`;
      }
    }

    return cssText;
  } // For IE11, it doesn't support adding/removing multiple classes at once

  /**
   * Add multiple classes to elements classList. Helper for IE11 which does not support it directly
   * @param {HTMLElement} element
   * @param {String[]} classes
   * @category CSS
   */

  static addClasses(element, classes) {
    for (let i = 0; i < classes.length; i++) {
      element.classList.add(classes[i]);
    }
  }
  /**
   * Remove multiple classes from elements classList. Helper for IE11 which does not support it directly
   * @param {HTMLElement} element
   * @param {String[]} classes
   * @category CSS
   */

  static removeClasses(element, classes) {
    for (let i = 0; i < classes.length; i++) {
      element.classList.remove(classes[i]);
    }
  }
  /**
   * Toggle multiple classes in elements classList. Helper for IE11 which does not support toggling with force or for
   * multiple classes at once.
   * @param {HTMLElement} element
   * @param {String[]} classes
   * @param {Boolean} [force] Specify true to add classes, false to remove. Leave blank to toggle
   * @category CSS
   */

  static toggleClasses(element, classes, force = null) {
    if (!Array.isArray(classes)) {
      classes = [classes];
    }

    if (force === true) {
      DH$1.addClasses(element, classes);
    } else if (force === false) {
      DH$1.removeClasses(element, classes);
    } else {
      classes.forEach(cls => element.classList.toggle(cls));
    }
  }
  /**
   * Adds a CSS class to an element during the specified duration
   * @param {HTMLElement} element Target element
   * @param {String} cls CSS class to add temporarily
   * @param {Number} duration Duration in ms, 0 means cls will not be applied
   * @param {Core.mixin.Delayable} delayable The delayable to tie the setTimeout call to
   * @category CSS
   */

  static addTemporaryClass(element, cls, duration, delayable = window) {
    if (duration > 0) {
      element.classList.add(cls);
      delayable.setTimeout(() => element.classList.remove(cls), duration);
    }
  }
  /**
   * Reads computed style from the element and returns transition duration for a given property in milliseconds
   * @param {HTMLElement} element Target DOM element
   * @param {String} property Animatied property name
   * @returns {Number} Duration in ms
   * @internal
   */

  static getPropertyTransitionDuration(element, property) {
    const style = window.getComputedStyle(element),
          properties = style.transitionProperty.split(', '),
          durations = style.transitionDuration.split(', '),
          index = properties.indexOf(property);
    let result;

    if (index !== -1) {
      // get floating value of transition duration in seconds and convert into milliseconds
      result = parseFloat(durations[index]) * 1000;
    }

    return result;
  }
  /**
   * Reads computed style from the element and returns the animation duration for any
   * attached animation in milliseconds
   * @param {HTMLElement} element Target DOM element
   * @returns {Number} Duration in ms
   * @internal
   */

  static getAnimationDuration(element) {
    return parseFloat(DH$1.getStyleValue(element, 'animation-duration')) * 1000;
  } //endregion
  //region Effects

  /**
   * Highlights the passed element or Rectangle according to the theme's highlighting rules.
   * Usually an animated framing effect.
   * @param {HTMLElement|Core.helper.util.Rectangle} element The element or Rectangle to highlight.
   */

  static highlight(element, delayable = window) {
    if (element instanceof Rectangle) {
      return element.highlight();
    }

    return new Promise(resolve => {
      delayable.setTimeout(() => {
        element.classList.add('b-fx-highlight');
        delayable.setTimeout(() => {
          element.classList.remove('b-fx-highlight');
          resolve();
        }, 1000);
      }, 0);
    });
  } //endregion
  //region Measuring / Scrollbar

  /**
   * Measures the scrollbar width using a hidden div. Caches result
   * @property {Number}
   * @readonly
   */

  static get scrollBarWidth() {
    // Ensure the measurement is only done once, when the value is null and body is available
    if (scrollBarWidth === null && doc.body) {
      const element = DH$1.createElement({
        parent: doc.body,
        style: 'position:absolute;top:-999px;width:100px;height:100px;overflow:scroll'
      });
      scrollBarWidth = element.offsetWidth - element.clientWidth;
      element.remove();
    }

    return scrollBarWidth;
  }

  static get scrollBarPadElement() {
    return {
      className: 'b-yscroll-pad',
      children: [{
        className: 'b-yscroll-pad-sizer'
      }]
    };
  }
  /**
   * Resets DomHelper.scrollBarWidth cache, triggering a new measurement next time it is read
   */

  static resetScrollBarWidth() {
    scrollBarWidth = null;
  }
  /**
   * Measures the text width using a hidden div
   * @param {String} text
   * @param {HTMLElement} sourceElement
   * @returns {Number} width
   * @category Measure
   */

  static measureText(text, sourceElement, useHTML = false, parentElement = undefined) {
    const offScreenDiv = DH$1.getMeasureElement(sourceElement, parentElement);
    offScreenDiv[useHTML ? 'innerHTML' : 'innerText'] = text;
    const result = offScreenDiv.clientWidth;
    offScreenDiv.className = '';
    return result;
  }
  /**
   * Measures a relative size, such as a size specified in `em` units for the passed element.
   * @param {String} size The CSS size value to measure.
   * @param {HTMLElement} sourceElement
   * @returns {Number} size The size in pixels of the passed relative measurement.
   * @category Measure
   */

  static measureSize(size, sourceElement) {
    if (!size) {
      return 0;
    }

    if (typeof size === 'number') {
      return size;
    }

    if (!size.length) {
      return 0;
    }

    if (/^\d+(px)?$/.test(size)) {
      return parseInt(size);
    }

    if (sourceElement) {
      const offScreenDiv = DH$1.getMeasureElement(sourceElement);
      offScreenDiv.innerHTML = '';
      offScreenDiv.style.width = DH$1.setLength(size); //const result = BrowserHelper.isIE11 ?  offScreenDiv.offsetWidth : offScreenDiv.clientWidth;

      const result = offScreenDiv.offsetWidth;
      offScreenDiv.style.width = offScreenDiv.className = '';
      return result;
    }

    if (/^\d+em$/.test(size)) {
      return parseInt(size) * DEFAULT_FONT_SIZE;
    }

    return isNaN(size) ? 0 : parseInt(size);
  } // parentElement allows measurement to happen inside a specific element, allowing scoped css rules to match

  static getMeasureElement(sourceElement, parentElement = doc.body) {
    const sourceElementStyle = win.getComputedStyle(sourceElement),
          offScreenDiv = parentElement.offScreenDiv = parentElement.offScreenDiv || DH$1.createElement({
      parent: parentElement,
      style: 'position:fixed;top:-10000px;left:-10000px;visibility:hidden;contain:strict',
      className: 'b-measure-element',
      children: [{
        style: 'white-space:nowrap;display:inline-block;will-change:contents;width:auto;contain:none'
      }]
    }, true)[1];
    fontProps.forEach(prop => {
      if (offScreenDiv.style[prop] !== sourceElementStyle[prop]) {
        offScreenDiv.style[prop] = sourceElementStyle[prop];
      }
    });
    offScreenDiv.className = sourceElement.className; // In case the measure element was moved/removed, re-add it

    if (offScreenDiv.parentElement.parentElement !== parentElement) {
      parentElement.appendChild(offScreenDiv.parentElement);
    }

    return offScreenDiv;
  } //endregion
  //region Sync

  /**
   * Sync one source element attributes, children etc. to a target element. Source element can be specified as a html
   * string or an actual HTMLElement.
   *
   * NOTE: This function is superseded by {@link Core/helper/DomSync#function-sync-static DomSync.sync()}, which works
   * with DOM configs. For most usecases, use it instead.
   *
   * @param {String|HTMLElement} sourceElement Source "element" to copy from
   * @param {HTMLElement} targetElement Target element to apply to, can also be specified as part of the config object
   * @returns {HTMLElement} Returns the updated targetElement (which is also updated in place)
   */

  static sync(sourceElement, targetElement) {
    if (typeof sourceElement === 'string') {
      if (sourceElement === '') {
        targetElement.innerHTML = '';
        return;
      } else {
        sourceElement = DH$1.createElementFromTemplate(sourceElement);
      }
    }

    DH$1.performSync(sourceElement, targetElement);
    return targetElement;
  } // Internal helper used for recursive syncing

  static performSync(sourceElement, targetElement) {
    // Syncing identical elements is a no-op
    if (sourceElement.outerHTML !== targetElement.outerHTML) {
      DH$1.syncAttributes(sourceElement, targetElement);
      DH$1.syncContent(sourceElement, targetElement);
      DH$1.syncChildren(sourceElement, targetElement);
      return true;
    }

    return false;
  } // Attributes as map { attr : value, ... }, either from an html element or from a config

  static getSyncAttributes(element) {
    const attributes = {},
          // Attribute names, simplifies comparisons and calls to set/removeAttribute
    names = []; // Extract from element

    for (let i = 0; i < element.attributes.length; i++) {
      const attr = element.attributes[i];

      if (attr.specified) {
        const name = attr.name.toLowerCase();
        attributes[name] = attr.value;
        names.push(name);
      }
    }

    return {
      attributes,
      names
    };
  }
  /**
   * Syncs attributes from sourceElement to targetElement.
   * @private
   * @param {HTMLElement} sourceElement
   * @param {HTMLElement} targetElement
   */

  static syncAttributes(sourceElement, targetElement) {
    const // Extract attributes from elements (sourceElement might be a config)
    {
      attributes: sourceAttributes,
      names: sourceNames
    } = DH$1.getSyncAttributes(sourceElement),
          {
      attributes: targetAttributes,
      names: targetNames
    } = DH$1.getSyncAttributes(targetElement),
          // Used to ignore data-xx attributes when we will be setting entire dataset
    hasDataset = sourceNames.includes('dataset'),
          // Intersect arrays to determine what needs adding, removing and syncing
    toAdd = sourceNames.filter(attr => !targetNames.includes(attr)),
          toRemove = targetNames.filter(attr => !sourceNames.includes(attr) && (!hasDataset || !attr.startsWith('data-'))),
          toSync = sourceNames.filter(attr => targetNames.includes(attr));

    if (toAdd.length > 0) {
      for (let i = 0; i < toAdd.length; i++) {
        const attr = toAdd[i]; // Style requires special handling

        if (attr === 'style') {
          DH$1.applyStyle(targetElement, sourceAttributes.style, true);
        } // So does dataset
        else if (attr === 'dataset') {
            Object.assign(targetElement.dataset, sourceAttributes.dataset);
          } // Other attributes are set using setAttribute (since it calls toString() DomClassList works fine)
          else {
              targetElement.setAttribute(attr, sourceAttributes[attr]);
            }
      }
    }

    if (toRemove.length > 0) {
      for (let i = 0; i < toRemove.length; i++) {
        targetElement.removeAttribute(toRemove[i]);
      }
    }

    if (toSync.length > 0) {
      for (let i = 0; i < toSync.length; i++) {
        const attr = toSync[i]; // Set all attributes that has changed, with special handling for style

        if (attr === 'style') {
          // TODO: Check for changes?
          DH$1.applyStyle(targetElement, sourceAttributes.style, true);
        } // And dataset
        else if (attr === 'dataset') {
            // TODO: Any cost to assigning same values?
            Object.assign(targetElement.dataset, sourceAttributes.dataset);
          } // And class, which might be a DomClassList or an config for a DomClassList
          else if (attr === 'class' && (sourceAttributes.class.isDomClassList || typeof sourceAttributes.class === 'object')) {
              let classList;

              if (sourceAttributes.class.isDomClassList) {
                classList = sourceAttributes.class;
              } else {
                // TODO : Reuse a single DomClassList?
                classList = new DomClassList(sourceAttributes.class);
              }

              if (!classList.isEqual(targetAttributes.class)) {
                targetElement.setAttribute('class', classList);
              }
            } else if (targetAttributes[attr] !== sourceAttributes[attr]) {
              targetElement.setAttribute(attr, sourceAttributes[attr]);
            }
      }
    }
  }
  /**
   * Sync content (innerText) from sourceElement to targetElement
   * @private
   * @param {HTMLElement} sourceElement
   * @param {HTMLElement} targetElement
   */

  static syncContent(sourceElement, targetElement) {
    if (sourceElement.childElementCount === 0) {
      targetElement.innerText = sourceElement.innerText;
    }
  }

  static setInnerText(targetElement, text) {
    // setting firstChild.data is faster than innerText (and innerHTML),
    // but in some cases the inner node is lost and needs to be recreated
    const firstChild = targetElement.firstChild;

    if (firstChild) {
      firstChild.data = text;
    } else {
      // textContent is supposed to be faster than innerText, since it does not trigger layout
      targetElement.textContent = text;
    }
  }
  /**
   * Sync traversing children
   * @private
   * @param {HTMLElement} sourceElement Source element
   * @param {HTMLElement} targetElement Target element
   * @param {String} syncIdField Field in dataset to use for element re-usage
   */

  static syncChildren(sourceElement, targetElement) {
    const me = this,
          sourceNodes = arraySlice.call(sourceElement.childNodes),
          targetNodes = arraySlice.call(targetElement.childNodes);

    while (sourceNodes.length) {
      const sourceNode = sourceNodes.shift(),
            targetNode = targetNodes.shift(); // only textNodes and elements allowed (no comments)

      if (sourceNode && sourceNode.nodeType !== TEXT_NODE && sourceNode.nodeType !== ELEMENT_NODE) {
        throw new Error(`Source node type ${sourceNode.nodeType} not supported by DomHelper.sync()`);
      }

      if (targetNode && targetNode.nodeType !== TEXT_NODE && targetNode.nodeType !== ELEMENT_NODE) {
        throw new Error(`Target node type ${targetNode.nodeType} not supported by DomHelper.sync()`);
      }

      if (!targetNode) {
        // out of target nodes, add to target
        targetElement.appendChild(sourceNode);
      } else {
        // match node
        if (sourceNode.nodeType === targetNode.nodeType) {
          // same type of node, take action depending on which type
          if (sourceNode.nodeType === TEXT_NODE) {
            // text
            targetNode.data = sourceNode.data;
          } else {
            if (sourceNode.tagName === targetNode.tagName) {
              me.performSync(sourceNode, targetNode);
            } else {
              // new tag, remove targetNode and insert new element
              targetElement.insertBefore(sourceNode, targetNode);
              targetNode.remove();
            }
          }
        } // Trying to set text node as element, use it as innerText
        // (we get this in FF with store mutations and List)
        else if (sourceNode.nodeType === TEXT_NODE && targetNode.nodeType === ELEMENT_NODE) {
            targetElement.innerText = sourceNode.data.trim();
          } else {
            throw new Error('Currently no support for transforming nodeType');
          }
      }
    } // Out of source nodes, remove remaining target nodes

    targetNodes.forEach(targetNode => {
      targetNode.remove();
    });
  }
  /**
   * Synchronizes the passed element's `classList` with the class names
   * passed in either Array or String format or Object. Avoiding mutating an element's
   * `classList` or `className` can avoid browser style recalculations.
   * @param {HTMLElement} element The element whose class list to synchronize.
   * @param {String[]|String|Object} newClasses The incoming class names to set on the element.
   * @category CSS
   */

  static syncClassList(element, newClasses) {
    const classList = element.classList,
          isString = typeof newClasses === 'string',
          newClsArray = isString ? newClasses.trim().split(whiteSpaceRe$2) : Array.isArray(newClasses) ? newClasses : ObjectHelper.getTruthyKeys(newClasses),
          classCount = newClsArray.length;
    let changed = classList.length !== classCount,
        i; // If the incoming and existing class lists are the same length
    // then check that each contains the same names. As soon as
    // we find a non-matching name, we know we have to update the
    // className.

    for (i = 0; !changed && i < classCount; i++) {
      changed = !classList.contains(newClsArray[i]);
    }

    if (changed) {
      element.className = isString ? newClasses : newClsArray.join(' ');
    }
  }
  /**
   * Changes the theme to the passed theme name if possible.
   *
   * Theme names are case insensitive. The `href` used is all lower case.
   *
   * To use this method, the `<link rel="stylesheet">` _must_ use the default,
   * Bryntum-supplied CSS files where the `href` end with `<themeName>.css`, so that
   * it can be found in the document, and switched out for a new link with
   * the a modified `href`. The new `href` will use the same path, just
   * with the `themeName` portion subsituted for the new name.
   *
   * If no `<link>` with that name pattern can be found, an error will be thrown.
   *
   * If you use this method, you  must ensure that the theme files are
   * all accessible on your server.
   *
   * Because this is an asynchronous operation, a `Promise` is returned.
   * The theme change event is passed to the success function. If the
   * theme was not changed, because the theme name passed is the current theme,
   * nothing is passed to the success function.
   *
   * The theme change event contains two properties:
   *
   *  - `prev` The previous Theme name.
   *  - `theme` The new Theme name.
   *
   * @param {String} newThemeName
   * @returns {Promise} A promise who's success callback receives the theme change
   * event if the theme in fact changed. If the theme `href` could not be loaded,
   * the failure callback is called, passing the error event caught.
   */

  static setTheme(newThemeName) {
    newThemeName = newThemeName.toLowerCase();
    const oldThemeName = DH$1.themeInfo.name.toLowerCase(),
          oldThemeLink = document.head.querySelector('#bryntum-theme:not([data-loading])') || document.head.querySelector(`[href*="${oldThemeName}.css"]:not([data-loading])`); // Remove any links currently loading

    DomHelper.removeEachSelector(document.head, '#bryntum-theme[data-loading]'); // Theme link href ends with <themeName>.css also there could be a query - css?11111...

    if (!(oldThemeLink !== null && oldThemeLink !== void 0 && oldThemeLink.href.includes(`${oldThemeName}.css`))) {
      throw new Error(`Theme link for ${oldThemeName} not found`);
    } // Do not reapply same theme

    if (oldThemeLink.href.includes(`${newThemeName}.css`)) {
      return immediatePromise;
    }

    return new Promise((resolve, reject) => {
      const newThemeLink = document.createElement('link'),
            nextSibling = oldThemeLink.nextSibling,
            oldThemeName = DH$1.themeInfo.name.toLowerCase(),
            themeEvent = {
        theme: newThemeName,
        prev: oldThemeName
      },
            onThemeLoad = () => {
        delete newThemeLink.dataset.loading;
        themeInfo = null;
        oldThemeLink.remove();
        window.bryntum.GlobalEvents.trigger('theme', themeEvent);
        resolve(themeEvent);
      },
            onThemeLoadError = e => {
        delete newThemeLink.dataset.loading;
        reject(e);
      };

      newThemeLink.rel = 'stylesheet';
      newThemeLink.id = 'bryntum-theme';
      newThemeLink.addEventListener('load', onThemeLoad);
      newThemeLink.addEventListener('error', onThemeLoadError);
      newThemeLink.dataset.loading = 'true';
      newThemeLink.href = oldThemeLink.href.replace(oldThemeName, newThemeName);
      nextSibling.parentNode.insertBefore(newThemeLink, nextSibling);
    });
  }
  /**
   * A theme information object about the current theme.
   *
   * Currently this has only one property:
   *
   *   - `name` The current theme name.
   * @property {Object}
   * @readonly
   */

  static get themeInfo() {
    if (!themeInfo) {
      const // The content it creates for 'b-theme-info' is described in corresponding theme in Core/resources/sass/themes
      // for example in Core/resources/sass/themes/material.scss
      // ```
      // .b-theme-info:before {
      //     content : '{"name":"Material"}';
      // }
      // ```
      testDiv = DH$1.createElement({
        parent: document.body,
        className: 'b-theme-info'
      }),
            // Need to be a pseudo element for Edge to report content correctly
      themeData = DH$1.getStyleValue(testDiv, 'content', false, ':before');

      if (themeData) {
        // themeData could be invalid JSON string in case there is no content rule
        try {
          themeInfo = JSON.parse(themeData.replace(/^["']|["']$|\\/g, ''));
        } catch (e) {
          themeInfo = null;
        }
      }

      testDiv.remove();
    }

    return themeInfo;
  } //endregion

  static isOrphaned(element) {
    return element !== document.body && element !== document.documentElement && element.nodeType === Node.ELEMENT_NODE && element.offsetParent === null;
  }

  static async loadScript(url) {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = url;
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

}
const DH$1 = DomHelper;
let clearTouchTimer;

const clearTouchEvent = () => DH$1.isTouchEvent = false,
      setTouchEvent = () => {
  DH$1.isTouchEvent = true; // Jump round the click delay

  clearTimeout(clearTouchTimer);
  clearTouchTimer = setTimeout(clearTouchEvent, 400);
}; // Set event type flags so that mousedown and click handlers can know whether a touch gesture was used.
// This is used. This must stay until we have a unified DOM event system which handles both touch and mouse events.

doc.addEventListener('touchstart', setTouchEvent, true);
doc.addEventListener('touchend', setTouchEvent, true);
DH$1.supportsTemplate = 'content' in doc.createElement('template');
DH$1.elementPropKey = elementPropKey;
DH$1.numberRe = numberRe; //region Polyfills
// TODO: include babels polyfills instead of keeping own?

if (!('children' in Node.prototype)) {
  const elementFilter = node => node.nodeType === 1;

  Object.defineProperty(Node.prototype, 'children', {
    get: function () {
      return Array.prototype.filter.call(this.childNodes, elementFilter);
    }
  });
}

if (!Element.prototype.matches) {
  Element.prototype.matches = Element.prototype.matchesSelector || Element.prototype.mozMatchesSelector || Element.prototype.msMatchesSelector || Element.prototype.oMatchesSelector || Element.prototype.webkitMatchesSelector || function (s) {
    const matches = (this.document || this.ownerDocument).querySelectorAll(s);
    let i = matches.length;

    while (--i >= 0 && matches.item(i) !== this) {}

    return i > -1;
  };
}

if (win.Element && !Element.prototype.closest) {
  Node.prototype.closest = Element.prototype.closest = function (s) {
    var el = this;
    if (!doc.documentElement.contains(el)) return null;

    do {
      if (el.matches(s)) return el;
      el = el.parentElement || el.parentNode;
    } while (el !== null && el.nodeType === 1);

    return null;
  };
} else {
  // It's crazy that closest is not already on the Node interface!
  // Note that some Node types (eg DocumentFragment) do not have a parentNode.
  Node.prototype.closest = function (selector) {
    var _this$parentNode;

    return (_this$parentNode = this.parentNode) === null || _this$parentNode === void 0 ? void 0 : _this$parentNode.closest(selector);
  };
} // from MDN (public domain): https://developer.mozilla.org/en-US/docs/Web/API/ChildNode/remove

(function (arr) {
  arr.forEach(function (item) {
    if (Object.prototype.hasOwnProperty.call(item, 'remove')) {
      return;
    }

    Object.defineProperty(item, 'remove', {
      configurable: true,
      enumerable: true,
      writable: true,
      value: function remove() {
        this.parentNode && this.parentNode.removeChild(this);
      }
    });
  });
})([Element.prototype, CharacterData.prototype, DocumentType.prototype]); // IE11 polyfill

if (!SVGElement.prototype.contains) {
  SVGElement.prototype.contains = function (node) {
    do {
      if (this === node) {
        return true;
      }

      node = node.parentNode;
    } while (node);

    return false;
  };
} // IE11 polyfill for Event constructors

if (typeof win.CustomEvent !== 'function') {
  let evt, constructor;

  win.CustomEvent = constructor = function (event, params = {
    bubbles: false,
    cancelable: false,
    detail: undefined
  }) {
    evt = doc.createEvent('CustomEvent');
    evt.initCustomEvent(event, params.bubbles, params.cancelable, params.detail);
    return evt;
  };

  constructor.prototype = win.Event.prototype;

  win.MouseEvent = constructor = function (event, params = {
    bubbles: false,
    cancelable: false,
    detail: undefined
  }) {
    evt = doc.createEvent('MouseEvents');
    evt.initMouseEvent(event, params.bubbles, params.cancelable, doc.defaultView || win, params.detail, params.screenX, params.screenY, params.clientX, params.clientY, false, false, false, false, 0, document);
    return evt;
  };

  constructor.prototype = win.Event.prototype;

  win.KeyboardEvent = constructor = function (event, params = {
    bubbles: false,
    cancelable: false,
    detail: undefined
  }) {
    const modifiers = `${params.shiftKey ? 'Shift ' : ''}${params.ctrlKey ? 'Control' : ''}`;
    evt = doc.createEvent('KeyboardEvent');
    evt.initKeyboardEvent(event, params.bubbles, params.cancelable, doc.defaultView || win, params.key, params.location, modifiers, false, '');
    return evt;
  };

  constructor.prototype = win.Event.prototype;
} //endregion
// https://gist.github.com/brettz9/4093766

if (!Object.getOwnPropertyDescriptor(SVGElement.prototype, 'dataset') || !Object.getOwnPropertyDescriptor(SVGElement.prototype, 'dataset').get) {
  var propDescriptor = {
    enumerable: true,
    get: function () {

      var i,
          that = this,
          map = {},
          attrVal,
          attrName,
          propName,
          attribute,
          attributes = this.attributes,
          attsLength = attributes.length,
          toUpperCase = function (n0) {
        return n0.charAt(1).toUpperCase();
      },
          getter = function () {
        return this;
      },
          setter = function (attrName, value) {
        return typeof value !== 'undefined' ? this.setAttribute(attrName, value) : this.removeAttribute(attrName);
      };

      for (i = 0; i < attsLength; i++) {
        attribute = attributes[i]; // Fix: This test really should allow any XML Name without
        //         colons (and non-uppercase for XHTML)

        if (attribute && attribute.name && /^data-\w[\w-]*$/.test(attribute.name)) {
          attrVal = attribute.value;
          attrName = attribute.name; // Change to CamelCase

          propName = attrName.substr(5).replace(/-./g, toUpperCase);
          Object.defineProperty(map, propName, {
            enumerable: this.enumerable,
            get: getter.bind(attrVal || ''),
            set: setter.bind(that, attrName)
          });
        }
      }

      return map;
    }
  }; // FF enumerates over element's dataset, but not
  // SVGElement.prototype.dataset; IE9 iterates over both

  Object.defineProperty(SVGElement.prototype, 'dataset', propDescriptor);
} // Polyfill to allow an array to be passed to classList.add/remove

const nativeAdd = DOMTokenList.prototype.add,
      nativeRemove = DOMTokenList.prototype.remove;

DOMTokenList.prototype.add = function (cls) {
  if (Array.isArray(cls)) {
    cls.forEach(c => nativeAdd.call(this, c));
  } else {
    nativeAdd.call(this, ...arguments);
  }
};

DOMTokenList.prototype.remove = function (cls) {
  if (Array.isArray(cls)) {
    cls.forEach(c => nativeRemove.call(this, c));
  } else {
    nativeRemove.call(this, ...arguments);
  }
}; // CTRL/+ and  CTRL/- zoom gestures must invalidate the scrollbar width.
// Window resize is triggered by this operation on Blink (Chrome & Edge), Firefox and Safari.

window.addEventListener('resize', () => scrollBarWidth = null);
DomHelper._$name = 'DomHelper';

/**
 * @module Core/GlobalEvents
 */

const GlobalEvents = (window.bryntum || (window.bryntum = {})).GlobalEvents = new class extends Base.mixin(Events) {
  suspendFocusEvents() {
    focusEventsSuspended = true;
  }

  resumeFocusEvents() {
    focusEventsSuspended = false;
  }

  get lastInteractionType() {
    return lastInteractionType;
  }

}(),
      isTopVisibleModal = w => w.isVisible && w.isTopModal;
/**
 * Fired after the theme is changed
 * @event theme
 * @param {Core.GlobalEvents} source
 * @param {String} theme The new theme name
 */

let globaltouchStart,
    focusEventsSuspended = false,
    lastInteractionType,
    detacher;

function createWidgetEvent(eventName, target, relatedTarget, fromWidget, toWidget, backwards, options) {
  const result = new CustomEvent(eventName, options); // Workaround for Salesforce. They use strict mode and define non-configurable property `target`. We use this
  // CustomEvent as a syntetic one, feels fine to use non-standard handle for target.

  Object.defineProperty(result, '_target', {
    get() {
      return target;
    }

  });
  Object.defineProperty(result, 'relatedTarget', {
    get() {
      return relatedTarget;
    }

  });
  result.fromWidget = fromWidget;
  result.toWidget = toWidget;
  result.backwards = backwards;
  return result;
}

function getCommonAncestor(from, to) {
  return DomHelper.getCommonAncestor(from, to);
} // This is imported by EventHelper and that makes the call to set up the listeners
// `detach` argument is required to not setup more listeners than we need to. In case of salesforce we include floatroot
// inside the webcomponent element and thus don't need default listeners on document. In regular webcomponents demo we
// don't need to do it, because with multiple components on one page that would force us to make more complex lookups.

function setupFocusListeners(element = document, EventHelper, detach = false) {
  const listeners = {
    element,

    touchstart(touchstart) {
      if (!globaltouchStart && touchstart.changedTouches.length === 1) {
        globaltouchStart = touchstart.changedTouches[0];
      } else {
        globaltouchStart = null;
      }
    },

    // Just this one has to be passive: false so that we are allowed to preventDefault
    // if we are part of a contextmenu longpres emulation. Otherwise the gesture will
    // proceed to cause a mousedown event.
    touchend: {
      handler: event => {
        if (globaltouchStart) {
          // If the touchstart was used to synthesize a contextmenu event
          // stop the touch gesture processing right now.
          // Also prevent the conversion of the touch into  click.
          if (globaltouchStart.identifier === EventHelper.contextMenuTouchId) {
            event.stopImmediatePropagation();
            event.preventDefault();
          } else if (event.changedTouches.length === 1 && event.changedTouches[0].identifier === globaltouchStart.identifier) {
            GlobalEvents.trigger('globaltap', {
              event
            });
          }

          globaltouchStart = null;
        }
      },
      passive: false
    },
    mousedown: {
      handler: event => {
        lastInteractionType = 'mouse';

        if (!globaltouchStart) {
          GlobalEvents.trigger('globaltap', {
            event
          });
        }
      },
      passive: false
    },

    keydown() {
      lastInteractionType = 'key';
    },

    keypress() {
      lastInteractionType = 'key';
    },

    focusin(focusin) {
      // https://app.assembla.com/spaces/bryntum/tickets/5503
      // Caused by the browser scrolling a focused element into view. The browser will do *whatever it takes*
      // to scroll a focused element so that as much of it is in view as possible. Its first point of scrolling will
      // be the float containing element. That must never scroll.
      // TODO: Remove when https://www.w3.org/TR/css-overflow-3/#valdef-overflow-clip is supported.
      GlobalEvents.Widget.floatRoot.scrollTop = GlobalEvents.Widget.floatRoot.scrollLeft = 0;

      if (focusEventsSuspended) {
        return;
      }

      const fromElement = !focusin.relatedTarget ? null : focusin.relatedTarget instanceof HTMLElement ? focusin.relatedTarget : document.body,
            toElement = focusin.target || document.body,
            fromWidget = GlobalEvents.Widget.fromElement(fromElement),
            toWidget = GlobalEvents.Widget.fromElement(toElement),
            commonAncestor = getCommonAncestor(fromWidget, toWidget),
            // Flag if the fromElement is DOCUMENT_POSITION_FOLLOWING toElement
      backwards = !!(fromElement && toElement.compareDocumentPosition(fromElement) & 4),
            topVisibleModal = GlobalEvents.Widget.query(isTopVisibleModal),
            currentFocus = DomHelper.activeElement; // If there is a topmost modal, and the focus is moving to somewhere *not* a descendant of that
      // modal then we enforce modality and sweep focus back into the modal. By default the Container
      // class will yield the first focusable descendant widget's focusEl as its focusEl, so
      // that will be out of the box behaviour for Popups.

      if (topVisibleModal && (!toWidget || !topVisibleModal.owns(toWidget))) {
        topVisibleModal.focus();
        return;
      }

      let event = createWidgetEvent('focusout', fromElement, focusin.target, fromWidget, toWidget, backwards); // Bubble focusout event up the "from" side of the tree

      for (let target = fromWidget; target && target !== commonAncestor; target = target.owner) {
        if (!target.isDestroying && target.onFocusOut) {
          target.onFocusOut(event); // It is possible for focusout handlers to refocus themselves (editor's invalidAction='block'), so
          // check if the focus is still where it was when we started unless we are in a document
          // loss of focus situation (no target)

          if (focusin.target && currentFocus !== DomHelper.activeElement) {
            // If the focus has moved, that movement would have kicked off a nested sequence of focusin/out
            // notifications, so everyone has already been notified... no more to do here.
            return;
          }
        }
      } // Focus is moving upwards to the ancestor widget.
      // Its focus method might delegate focus to a focusable descendant.

      if (commonAncestor && focusin.target === commonAncestor.element) {
        // If one of the handlers above has not moved focus onwards
        // and the common ancestor is a container which delegates
        // focus inwards to a descendant, then give it the opportunity to do that.
        if (DomHelper.activeElement === toElement && commonAncestor.focusElement && commonAncestor.focusElement !== commonAncestor.element) {
          // Wait until out of the focusin handler to move focus on.
          commonAncestor.setTimeout(() => commonAncestor.focus && commonAncestor.focus(), 0);
        }
      } // Focus is moving between two branches of a subtree.
      // Bubble focusin event up the "to" side of the tree
      else {
          event = createWidgetEvent('focusin', toElement, fromElement, fromWidget, toWidget, backwards);

          for (let target = toWidget; target && target !== commonAncestor; target = target.owner) {
            if (!target.isDestroying) {
              target.onFocusIn && target.onFocusIn(event);
            }
          }
        } // Fire element focusmove event. Grid navigation will use  this when cells are focusable.

      const commonAncestorEl = getCommonAncestor(fromElement && fromElement.nodeType === 1 ? fromElement : null, toElement) || toElement.parentNode;
      event = createWidgetEvent('focusmove', toElement, fromElement, fromWidget, toWidget, backwards, {
        bubbles: true
      });
      commonAncestorEl.dispatchEvent(event);
    },

    focusout(focusout) {
      if (focusEventsSuspended) {
        return;
      }

      if (!focusout.relatedTarget || !GlobalEvents.Widget.fromElement(focusout.relatedTarget)) {
        // When switching between tabs in Salesforce app `relatedTarget` of the focusout event might be not an instance of
        // HTMLElement.
        const target = focusout.relatedTarget && focusout.relatedTarget instanceof HTMLElement ? focusout.relatedTarget : null;
        listeners.focusin({
          target,
          relatedTarget: focusout.target
        });
      }
    },

    capture: true,
    passive: true
  }; // detach previous listeners

  if (detach && detacher) {
    detacher();
  }

  detacher = EventHelper.on(listeners);
}

/**
 * @module Core/mixin/InstancePlugin
 */

function getDescriptor(me, fnName) {
  const property = Object.getOwnPropertyDescriptor(Object.getPrototypeOf(me), fnName);
  return property && (property.get || property.set) ? property : null;
}
/**
 * Base class for plugins. Published functions will be available from the other class. `this` in published functions is
 * referenced to the plugin, access the other class using `this.client`.
 *
 * Observe that plugin doesn't apply itself on class level but instead on instance level. Plugin is its own instance
 * that can have own functions and data that is not exposed to target class.
 *
 * Functions can be published in four ways:
 *
 * * `assign` (when function is not already available on target)
 * * `before` (when function is already available on target, will be called before original function)
 * * `after` (when function is already available on target, will be called after original function)
 * * `override` (replaces function on target, but old function can be reached)
 *
 * To configure which functions get published and in what way, specify `pluginConfig` getter on plugin:
 *
 * ```
 * class Sort extends InstancePlugin {
 *   static get pluginConfig {
 *      return {
 *          before   : ['init'],
 *          after    : ['destroy', 'onElementClick'],
 *          override : ['render']
 *      };
 *   }
 * }
 * ```
 * @extends Core/Base
 * @mixes Core/localization/Localizable
 * @mixes Core/mixin/Events
 */

class InstancePlugin extends Base.mixin(Events, Localizable) {
  //region Config
  static get configurable() {
    return {
      clientListeners: null,

      /**
       * Get/set the plugin/feature `disabled` state.
       * @property {Boolean}
       * @name disabled
       * @category Common
       */

      /**
       * The plugin/feature `disabled` state.
       *
       * For a feature that is **off** by default that you want to enable later during runtime,
       * configure it with `disabled : true`.
       * ```javascript
       * const grid = new Grid({
       *      featureName : {
       *          disabled : true // on and disabled, can be enabled later
       *      }
       * });
       *
       * // enable the feature
       * grid.featureName.disabled = false;
       * ```
       *
       * If the feature is **off** by default, and you want to include and enable the feature, configure it as `true`:
       * ```javascript
       * const grid = new Grid({
       *      featureName : true // on and enabled, can be disabled later
       * });
       *
       * // disable the feature
       * grid.featureName.disabled = true;
       * ```
       *
       * If the feature is **on** by default, but you want to turn it **off**, configure it as `false`:
       * ```javascript
       * const grid = new Grid({
       *      featureName : false // turned off, not included at all
       * });
       * ```
       *
       * If the feature is **enabled** by default and you have no need of reconfiguring it,
       * you can omit the feature configuration.
       *
       * @config {Boolean}
       * @default
       * @category Common
       */
      disabled: false
    };
  } //endregion
  //region Init

  /**
   * Call from another instance to add plugins to it.
   * @example
   * InstancePlugin.initPlugins(this, Search, Stripe);
   * @param plugInto Instance to mix into (usually this)
   * @param plugins Classes to plug in
   * @internal
   */

  static initPlugins(plugInto, ...plugins) {
    const property = plugInto.plugins || (plugInto.plugins = {});

    for (const PluginClass of plugins) {
      property[PluginClass.$$name] = new PluginClass(plugInto);
    }
  }
  /**
   * The Widget which was passed into the constructor,
   * which is the Widget we are providing extra services for.
   * @property {Core.widget.Widget}
   * @readonly
   * @category Misc
   */

  get client() {
    return this._client;
  }

  set client(client) {
    this._client = client;
  } // We can act as an owner of a widget, so must be able to participate in focus reversion

  getFocusRevertTarget() {
    var _this$client;

    return (_this$client = this.client) === null || _this$client === void 0 ? void 0 : _this$client.getFocusRevertTarget();
  }

  construct(...args) {
    const me = this;
    let [plugInto, config] = args,
        listeners; // When called with one argument (a config object), grab the "client" from the config object.

    if (args.length === 1) {
      if (ObjectHelper.isObject(plugInto)) {
        config = plugInto;
        plugInto = config.client;
      }
    } // Two args, so client is the first. Ensure the config doesn't contain a client property.
    else {
        config = ObjectHelper.assign({}, config);
        delete config.client;
      }

    me.client = plugInto;
    super.construct(config);
    me.applyPluginConfig(plugInto);
    listeners = me.clientListeners;

    if (listeners) {
      listeners = ObjectHelper.assign({}, listeners);
      listeners.thisObj = me;
      plugInto.on(listeners);
    }
  }
  /**
   * Applies config as found in plugInto.pluginConfig, or published all if no config found.
   * @private
   * @param plugInto Target instance to plug into
   */

  applyPluginConfig(plugInto) {
    const me = this,
          config = me.pluginConfig || me.constructor.pluginConfig;

    if (config) {
      const {
        assign,
        chain,
        after,
        before,
        override
      } = config;
      assign && me.applyAssign(plugInto, assign);
      (chain || after) && me.applyChain(plugInto, chain || after);
      before && me.applyChain(plugInto, before, false);
      override && me.applyOverride(plugInto, override);
    }
  }
  /**
   * Applies assigning for specified functions.
   * @private
   * @param plugInto
   * @param fnNames
   */

  applyAssign(plugInto, fnNames) {
    fnNames.forEach(fnName => this.assign(plugInto, fnName));
  }
  /**
   * Applies chaining for specified functions.
   * @private
   * @param plugInto
   * @param functions
   * @param after
   */

  applyChain(plugInto, functions, after = true) {
    if (Array.isArray(functions)) {
      for (const fnName of functions) {
        this.chain(plugInto, fnName, fnName, after);
      }
    } else {
      for (const intoName in functions) {
        this.chain(plugInto, intoName, functions[intoName], after);
      }
    }
  }
  /**
   * Applies override for specified functions.
   * @private
   * @param plugInto
   * @param fnNames
   */

  applyOverride(plugInto, fnNames) {
    const me = this;

    if (!me.overridden) {
      me.overridden = {};
    }

    fnNames.forEach(fnName => {
      if (!me[fnName]) {
        throw new Error(`Trying to chain fn ${plugInto.$$name}#${fnName}, but plugin fn ${me.$$name}#${fnName} does not exist`);
      } // override

      if (typeof plugInto[fnName] === 'function') {
        me.overridden[fnName] = plugInto[fnName].bind(plugInto);
      }

      plugInto[fnName] = me[fnName].bind(me);
    });
  }
  /**
   * Assigns specified functions.
   * @private
   * @param plugInto
   * @param fnName
   */

  assign(plugInto, fnName) {
    const me = this,
          property = getDescriptor(me, fnName);

    if (property) {
      // getter/setter, define corresponding property on target
      Object.defineProperty(plugInto, fnName, {
        configurable: true,
        enumerable: true,
        get: property.get && property.get.bind(me),
        set: property.set && property.set.bind(me)
      });
    } else {
      plugInto[fnName] = me[fnName].bind(me);
    }
  } //endregion
  //region Chaining

  /**
   * Chains functions. When the function is called on the target class all functions in the chain will be called in
   * the order they where added.
   * @private
   * @param plugInto
   * @param intoName
   * @param hookName
   * @param after
   */

  chain(plugInto, intoName, hookName, after = true) {
    const me = this,
          chains = plugInto.pluginFunctionChain || (plugInto.pluginFunctionChain = {}),
          hookFn = me[hookName] && me[hookName].bind(me),
          // Grab the fn so that we won't need our this pointer to call it. This is due
    // to this instance possibly being destroyed by the time a chain call is made.
    functionChainRunner = me.functionChainRunner;

    if (!hookFn) {
      throw new Error(`Trying to chain fn ${plugInto.$$name}#${hookName}, but plugin fn ${me.$$name}#${hookName} does not exist`);
    }

    if (!chains[intoName]) {
      let intoFn = plugInto[intoName];

      if (intoFn) {
        intoFn = intoFn.bind(plugInto);
        intoFn.$this = plugInto;
      }

      chains[intoName] = intoFn ? [intoFn] : [];

      plugInto[intoName] = (...params) => functionChainRunner(chains[intoName], params);
    }

    hookFn.$this = me;
    chains[intoName][after ? 'push' : 'unshift'](hookFn);
  }
  /**
   * Used to run multiple plugged in functions with the same name, see chain above. Returning false from a
   * function will abort chain.
   * @private
   * @param {Array} chain
   * @param {Array} params
   * @returns value returned from last function in chain (or false if any returns false)
   */

  functionChainRunner(chain, params) {
    // NOTE: even though we are an instance method, we must not use our "this" pointer
    // since our instance may be destroyed. We cope with that by receiving parameters
    // for everything we need (so we're just a pure function).
    let fn, i, returnValue; // changed from for..of to try and fix Edge problems

    for (i = 0; i < chain.length; i++) {
      fn = chain[i]; // Feature hooks remain in place even after GridBase loops and destroys its
      // features, so skip over any destroyed features on the chain. In particular,
      // bindStore hooks will be called when GridBase sets store to null.

      if (!fn.$this.isDestroyed) {
        returnValue = fn(...params);

        if (returnValue === false) {
          break;
        }
      }
    }

    return returnValue;
  } //endregion

  /**
   * Called when disabling/enabling the plugin. By default removes the cls of the plugin from its client.
   * Override in subclasses to take any other actions necessary.
   */

  doDisable(disable) {
    const me = this,
          {
      constructor
    } = me,
          cls = 'featureClass' in constructor ? constructor.featureClass : `b-${constructor.$$name.toLowerCase()}`; // Some features do not use a cls

    if (cls) {
      var _me$client, _me$client$element;

      (_me$client = me.client) === null || _me$client === void 0 ? void 0 : (_me$client$element = _me$client.element) === null || _me$client$element === void 0 ? void 0 : _me$client$element.classList[disable ? 'remove' : 'add'](cls);
    }

    if (!me.isConfiguring) {
      if (disable) {
        /**
         * Fired when the plugin/feature is disabled.
         * @event disable
         * @param {Core.mixin.InstancePlugin} source
         */
        me.trigger('disable');
      } else {
        /**
         * Fired when the plugin/feature is enabled.
         * @event enable
         * @param {Core.mixin.InstancePlugin} source
         */
        me.trigger('enable');
      }
    }
  }

  updateDisabled(disabled) {
    this.doDisable(disabled);
  }

  throwOverrideIsMissing(data) {
    throw new Error(`Trying to override fn ${data.plugIntoName}#${data.fnName}, but plugin fn ${data.pluginName}#${data.fnName} does not exist`);
  }

}
InstancePlugin._$name = 'InstancePlugin';

/**
 * @module Core/mixin/Pluggable
 */

/**
 * Enables using plugins for a class by specifying property plugins as an array of plugin classes. If only a single plugin
 * is used, just give the plugin class instead of an array. This class isn't required for using plugins, just makes it
 * easier. Without mixin you can otherwise use `InstancePlugin.initPlugins(this, PluginClass)`.
 *
 * @example
 * new Store({
 *   plugins: [PluginClass, ...]
 * });
 *
 * @mixin
 */

var Pluggable = (Target => class Pluggable extends (Target || Base) {
  static get $name() {
    return 'Pluggable';
  }
  /**
   * Specify plugins (an array of classes) in config
   * @config {Function[]} plugins
   * @category Misc
   */

  /**
   * Map of applied plugins
   * @property {Object}
   * @readonly
   * @category Misc
   */

  get plugins() {
    if (!this._plugins) {
      this._plugins = {};
    }

    return this._plugins;
  }

  set plugins(plugins) {
    if (plugins) {
      if (!Array.isArray(plugins)) plugins = [plugins];
      InstancePlugin.initPlugins(this, ...plugins);
    }

    this.initPlugins();
  }
  /**
   * Template method which may be implemented in subclasses to initialize any plugins.
   * This method is empty in the `Pluggable` base class.
   * @internal
   */

  initPlugins() {}
  /**
   * Adds plugins to an instance.
   * @param {Function[]} plugins The plugins to add
   */

  addPlugins(...plugins) {
    InstancePlugin.initPlugins(this, ...plugins);
  }
  /**
   * Checks if instance has plugin.
   * @param {String|Function} pluginClassOrName Plugin or name to check for
   * @returns {Boolean}
   */

  hasPlugin(pluginClassOrName) {
    return this.getPlugin(pluginClassOrName) != null;
  }
  /**
   * Get a plugin instance.
   * @param {String|Function} pluginClassOrName
   * @returns {Object}
   */

  getPlugin(pluginClassOrName) {
    if (typeof pluginClassOrName === 'function') pluginClassOrName = pluginClassOrName.$$name;
    return this.plugins && this.plugins[pluginClassOrName];
  } // This does not need a className on Widgets.
  // Each *Class* which doesn't need 'b-' + constructor.name.toLowerCase() automatically adding
  // to the Widget it's mixed in to should implement thus.

  get widgetClass() {}

});

/**
 * @module Core/mixin/State
 */

/**
 * Mixin that simplifies handling state for an ui component. Classes that uses this mixin must implement `getState()` and
 * `applyState(state)`.
 *
 * ```
 * class MyUIComponent extends State() {
 *   getState() {
 *     return {
 *       this.text,
 *       this.size
 *     }
 *   }
 *
 *   applyState(state) {
 *      this.text = state.text;
 *      this.size = state.size;
 *   }
 * }
 * ```
 *
 * @demo Grid/state
 *
 * @mixin
 */

var State = (Target => class State extends (Target || Base) {
  static get $name() {
    return 'State';
  } //getState() {}
  //applyState(State) {}

  /**
   * Gets or sets a component's state
   * @property {Object}
   */

  get state() {
    this._state = this.getState();
    return this._state;
  }

  set state(state) {
    this._state = state;
    this.applyState(state);
  } // This does not need a className on Widgets.
  // Each *Class* which doesn't need 'b-' + constructor.name.toLowerCase() automatically adding
  // to the Widget it's mixed in to should implement thus.

  get widgetClass() {}

});

/**
 * @module Core/mixin/Identifiable
 */

const idCounts = Object.create(null),
      idTypes = {
  string: 1,
  number: 1
};
/**
 * A mixin which provides identifier services such as auto-creation of `id`s and registration and
 * lookup of instances by `id`.
 * @mixin
 * @internal
 */

var Identifiable = (Target => class Identifiable extends (Target || Base) {
  static get $name() {
    return 'Identifiable';
  }

  static get declarable() {
    return ['identifiable'];
  }

  static get configurable() {
    return {
      /**
       * The id of this object.  If not specified one will be generated. Also used for lookups through the
       * static `getById` of the class which mixes this in. An example being {@link Core.widget.Widget}.
       *
       * For a {@link Core.widget.Widget Widget}, this is assigned as the `id` of the DOM
       * {@link Core.widget.Widget#config-element element} and must be unique across all elements
       * in the page's `document`.
       * @config {String}
       * @category Common
       */
      id: ''
    };
  }

  static setupIdentifiable(cls, meta) {
    const {
      identifiable
    } = cls;
    identifiable.idMap = Object.create(null);
    Reflect.defineProperty(cls, 'identifiable', {
      get() {
        return identifiable;
      }

    });
  }

  doDestroy() {
    this.constructor.unregisterInstance(this);
    super.doDestroy();
  }

  changeId(id) {
    return (this.hasGeneratedId
    /* assignment */
    = !id) ? this.generateAutoId() : id;
  }

  updateId(id, oldId) {
    const me = this,
          C = me.constructor;
    oldId && C.unregisterInstance(me, oldId);
    C.registerInstance(me, id);
  }
  /**
   * This method generates an id for this instance.
   * @returns {String}
   * @internal
   */

  generateAutoId() {
    return this.constructor.generateId(`b-${this.$$name.toLowerCase()}-`);
  }
  /**
   * Generate a new id, using an internal counter and a prefix.
   * @param {String} prefix Id prefix
   * @returns {String} Generated id
   */

  static generateId(prefix = 'generatedId') {
    // This produces "b-foo-1, b-foo-2, ..." for each prefix independently of the others. In other words, it makes
    // id's more stable since the counter is on a per-class basis.
    return prefix + (idCounts[prefix] = (idCounts[prefix] || 0) + 1);
  }

  static registerInstance(instance, instanceId = instance.id) {
    const {
      idMap
    } = this.identifiable; // Code editor sets `disableThrow` to not get conflicts when loading the same module again

    if (instanceId in idMap && !this.disableThrow) {
      throw new Error('Id ' + instanceId + ' already in use');
    }

    idMap[instanceId] = instance;
  }
  /**
   * Unregister Identifiable instance, normally done on destruction
   * @param {Object} instance Object to unregister
   * @param {String} id The id of the instance to unregister.
   */

  static unregisterInstance(instance, id = instance.id) {
    const {
      idMap
    } = this.identifiable; // ID may be passed, for example if the instance is destroyed and can no longer yield an id.

    if (idTypes[typeof instance]) {
      delete idMap[instance];
    } // Have to check for identity in case another instance by the same id has been created.
    // Allow that to be overridden. Stores have always just evicted the previous owner of their IDs
    else if (idMap[id] === instance) {
        delete idMap[id];
      }
  }

  static getById(id) {
    const idMap = this.identifiable.idMap;

    if (idMap) {
      return idMap[id];
    }
  }

  static get registeredInstances() {
    const idMap = this.identifiable.idMap;
    return idMap ? Object.values(idMap) : [];
  }

});

/**
 * @module Core/data/stm/mixin/ModelStm
 */
const STM_PROP = Symbol('STM_PROP'),
      unrecordedFields = {
  // This field's value is a by product of node insertion and must not be recorded here.
  // It's the node insertion operation which is recorded by STM.
  parentIndex: 1
};
/**
 * Mixin making a model compatible with {@link Core/data/stm/StateTrackingManager}
 *
 * @mixin
 */

var ModelStm = (Target => class ModelStm extends (Target || Base) {
  static get $name() {
    return 'ModelStm';
  }

  static get defaultConfig() {
    return {
      stm: null
    };
  }

  joinStore(store) {
    super.joinStore && super.joinStore(store);

    if (!this.stm) {
      this.stm = store.stm;
    }
  }

  unJoinStore(store) {
    if (this.stm === store.stm) {
      this.stm = null;
    }

    super.unJoinStore && super.unJoinStore(store);
  }
  /**
   * Reference to STM manager, if used
   * @member {Core.data.stm.StateTrackingManager}
   * @category Misc
   */

  get stm() {
    return this[STM_PROP];
  }

  set stm(stm) {
    this[STM_PROP] = stm;
  }
  /**
   * Overridden to collect initial data for the fields about to be changed.
   * The method is called from within {@link Core/data/Model#function-set} method.
   *
   * @private
   */

  beforeSet(field, value, silent, fromRelationUpdate) {
    const preResult = super.beforeSet ? super.beforeSet(field, value, silent, fromRelationUpdate) : [];
    const stm = this.stm;

    if (stm && !stm.disabled && !unrecordedFields[field]) {
      let newDataCandidate, oldDataCandidate;

      if (typeof field == 'object') {
        [newDataCandidate, oldDataCandidate] = Object.keys(field).reduce((data, fName) => {
          data[0][fName] = field[fName];
          data[1][fName] = this.get(fName);
          return data;
        }, [{}, {}]);
      } else {
        newDataCandidate = {
          [field]: value
        };
        oldDataCandidate = {
          [field]: this.get(field)
        };
      }

      preResult.push([newDataCandidate, oldDataCandidate]);
    }

    return preResult;
  }
  /**
   * Overridden to store initial data of the changed fields and to notify STM
   * manager about the change action if anything has been changed in result.
   *
   * The method is called from within {@link Core/data/Model#function-set} method.
   *
   * @private
   */

  afterSet(field, value, silent, fromRelationUpdate, beforeResult, wasSet) {
    const stm = this.stm;

    if (stm && !stm.disabled && !unrecordedFields[field]) {
      const preResult = beforeResult.pop();

      if (wasSet) {
        let [newDataCandidate, oldDataCandidate] = preResult;
        let [newData, oldData] = Object.keys(wasSet).reduce((data, fName) => {
          data[0][fName] = newDataCandidate[fName];
          data[1][fName] = oldDataCandidate[fName];
          return data;
        }, [{}, {}]);
        stm.onModelUpdate(this, newData, oldData);
      }
    }

    super.afterSet && super.afterSet(field, value, silent, fromRelationUpdate, beforeResult, wasSet);
  }
  /**
   * Called from {@link Core/data/mixin/TreeNode#function-insertChild} to obtain inserted
   * records initial parents and parent index, to be able to restore the state back upon undo.
   *
   * @param {Core.data.Model[]} childRecords
   * @return {Array} Array of results from this call and any of super calls if any.
   *               This result is consumed by {@link #function-afterInsertChild} which pops
   *               from the result array to take only results of this method call and leave
   *               results from super calls untouched.
   *
   * @private
   */

  beforeInsertChild(childRecords) {
    const preResult = super.beforeInsertChild ? super.beforeInsertChild(childRecords) : [];
    const stm = this.stm;

    if (stm && !stm.disabled) {
      preResult.push(childRecords.reduce((result, childRecord) => {
        // We are interested only in records from the same root node.
        // Removing (which is done before insertion) of the records
        // from another root (and store) should
        // be handled by that store STM instance.
        if (childRecord.root === this.root) {
          result.set(childRecord, {
            parent: childRecord.parent,
            index: childRecord.parent ? childRecord.parentIndex : undefined
          });
        }

        return result;
      }, new Map()));
    }

    return preResult;
  }
  /**
   * Called from {@link Core/data/mixin/TreeNode#function-insertChild} to notify {@link Core/data/stm/StateTrackingManager}
   * about children insertion. Provides it with all necessary context information collected
   * in {@link #function-beforeInsertChild} required to undo/redo the action.
   *
   * @private
   */

  afterInsertChild(index, childRecords, beforeResult, inserted) {
    const stm = this.stm;

    if (stm && !stm.disabled) {
      const context = beforeResult.pop();

      if (inserted) {
        stm.onModelInsertChild(this, index, inserted, context);
      }
    }

    super.afterInsertChild && super.afterInsertChild(index, childRecords, beforeResult, inserted);
  }
  /**
   * Called from {@link Core/data/mixin/TreeNode#function-removeChild} to obtain removed
   * records initial parent index, to be able to restore the state back upon undo.
   *
   * @param {Core.data.Model[]} childRecords
   * @param {Boolean} isMove
   * @return {Array} Array of results from this call and any of super calls if any.
   *               This result is consumed by {@link #function-afterRemoveChild} which pops
   *               from the result array to take only results of this method call and leave
   *               results from super calls untouched.
   *
   * @private
   */

  beforeRemoveChild(childRecords, isMove) {
    const preResult = super.beforeRemoveChild ? super.beforeRemoveChild(childRecords, isMove) : [];
    const stm = this.stm; // If it's move then InsertChildAction will handle this case

    if (stm && !stm.disabled && !isMove) {
      // Child records we receive are guaranteed to be direct children
      // of this node, see Core/data/mixin/TreeNode#removeChild method
      // Here we interested in the original index for each child removed,
      // we collect it and store for future use in RemoveChildAction
      preResult.push(childRecords.reduce((result, childRecord) => {
        result.set(childRecord, childRecord.parentIndex);
        return result;
      }, new Map()));
    }

    return preResult;
  }
  /**
   * Called from {@link Core/data/mixin/TreeNode#function-removeChild} to notify {@link Core/data/stm/StateTrackingManager}
   * about children removing. Provides it with all necessary context information collected
   * in {@link #function-beforeRemoveChild} required to undo/redo the action.
   *
   * @private
   */

  afterRemoveChild(childRecords, beforeResult, isMove) {
    const stm = this.stm; // If it's move then InsertChildAction will handle this case

    if (stm && !stm.disabled && !isMove) {
      const context = beforeResult.pop();

      if (childRecords && childRecords.length) {
        stm.onModelRemoveChild(this, childRecords, context);
      }
    }

    super.afterRemoveChild && super.afterRemoveChild(childRecords, beforeResult, isMove);
  }

});

/**
 * @module Core/data/mixin/TreeNode
 */

/**
 * Mixin for Model with tree node related functionality. This class is mixed into the {@link Core/data/Model} class.
 *
 * ## Adding and removing child nodes
 * ```
 * const parent = store.getById(1),
 *
 * firstBorn = parent.insertChild({
 *      name : 'Child node'
 *  }, parent.children[0]); // Insert a child at a specific place in the children array
 *
 * parent.removeChild(parent.children[0]); // Removes a child node
 * parent.appendChild({ name : 'New child node' }); // Appends a child node
 * ```
 *
 * @mixin
 */

var TreeNode = (Target => class TreeNode extends (Target || Base) {
  static get $name() {
    return 'TreeNode';
  }
  /**
   * This static configuration option allows you to control whether an empty parent task should be converted into a
   * leaf. Enable/disable it for a whole class:
   *
   * ```javascript
   * Model.convertEmptyParentToLeaf = false;
   * ```
   *
   * By specifying `true`, all empty parents will be considered leafs. Can also be assigned a configuration object
   * with with the following Boolean properties to customize the behaviour:
   *
   * * `onLoad` - Apply the transformation on load to any parents without children (`children : []`)
   * * `onRemove` - Apply the transformation when all children have been removed from a parent
   *
   * ```javascript
   * Model.convertEmptyParentToLeaf = {
   *     onLoad   : false,
   *     onRemove : true
   * }
   * ```
   *
   * @property {Boolean|Object}
   * @default false
   * @category Parent & children
   */

  static set convertEmptyParentToLeaf(value) {
    if (value === true) {
      value = {
        onLoad: true,
        onRemove: true
      };
    } else if (value === false) {
      value = {
        onLoad: false,
        onRemove: false
      };
    }

    this._convertEmptyParentToLeaf = value;
  }

  static get convertEmptyParentToLeaf() {
    return this._convertEmptyParentToLeaf || {
      onLoad: false,
      onRemove: false
    };
  }
  /**
   * This is a read-only property providing access to the parent node.
   * @member {Core.data.Model} parent
   * @readonly
   * @category Parent & children
   */

  /**
   * This is a read-only field provided in server synchronization packets to specify
   * which record id is the parent of the record.
   * @readonly
   * @field {String|Number} parentId
   * @category Tree
   */

  /**
   * This is a read-only field provided in server synchronization packets to specify
   * which position the node takes in the parent's children array.
   * This index is set on load and gets updated automatically after row reordering, sorting, etc.
   * To save the order, need to persist the field on the server and when data is fetched to be loaded,
   * need to sort by this field.
   * @readonly
   * @field {Number} parentIndex
   * @category Tree
   */

  ingestChildren(childRecord, stores = this.stores) {
    const {
      inProcessChildren,
      constructor: MyClass
    } = this,
          store = stores[0];

    if (childRecord === true) {
      if (inProcessChildren) {
        return true;
      }

      return [];
    }

    if (childRecord) {
      if (!Array.isArray(childRecord)) {
        childRecord = [childRecord];
      }

      const len = childRecord.length,
            result = [];

      for (let i = 0, child; i < len; i++) {
        child = childRecord[i];
        child = child.isModel ? child : store ? store.createRecord(child) : new MyClass(child, null, null, true);
        child = store ? store.processRecord(child) : child;
        result.push(child);
      }

      return result;
    }
  }
  /**
   * Child nodes. To allow loading children on demand, specify `children : true` in your data.
   * @member {Core.data.Model[]} children
   * @category Parent & children
   */

  /**
   * Called during creation to also turn any children into Models joined to the same stores as this model
   * @internal
   * @category Parent & children
   */

  processChildren(stores = this.stores) {
    const me = this,
          {
      meta
    } = me;
    me.inProcessChildren = true;
    const children = me.ingestChildren(me.data[me.constructor.childrenField], stores);

    if (children) {
      if (children.length) {
        meta.isLeaf = false; // We are processing a remote load

        if (me.children === true) {
          me.children = [];
        }

        me.appendChild(children);
      } // Flagged for load on demand
      else if (children === true) {
          meta.isLeaf = false;
          me.children = true;
        } // Empty child array, flag is leaf if configured to do so
        else if (!me.isRoot) {
            meta.isLeaf = me.constructor.convertEmptyParentToLeaf.onLoad;
          }
    }

    me.inProcessChildren = false;
  }
  /**
   * This method returns `true` if this record has all expanded ancestors and is therefore
   * eligible for inclusion in a UI.
   * @param {Core.data.Store} store
   * @readonly
   * @category Parent & children
   * @returns {Boolean}
   */

  ancestorsExpanded(store) {
    const {
      parent
    } = this;
    return !parent || parent.isExpanded(store) && parent.ancestorsExpanded(store);
  }
  /**
   * Used by stores to assess the record's collapsed/expanded state in that store.
   * @param {Core.data.Store} store
   * @category Parent & children
   * @returns {Boolean}
   */

  isExpanded(store) {
    const mapMeta = this.instanceMeta(store.id); // Default initial expanded/collapsed state when in the store
    // to the record's original expanded property.

    if (!Object.prototype.hasOwnProperty.call(mapMeta, 'collapsed')) {
      mapMeta.collapsed = !this.expanded;
    }

    return !mapMeta.collapsed;
  } // A read-only property. It provides the initial state upon load
  // The UI's expanded/collapsed state is in the store's meta map.

  get expanded() {
    return this.data.expanded;
  }
  /**
   * Depth in the tree at which this node exists. First visual level of nodes are at level 0, their direct children at
   * level 1 and so on.
   * @property {Number}
   * @readonly
   * @category Parent & children
   */

  get childLevel() {
    let node = this,
        ret = -1;

    while (node && !node.isRoot) {
      ++ret;
      node = node.parent;
    }

    return ret;
  }
  /**
   * Is a leaf node in a tree structure?
   * @property {Boolean}
   * @readonly
   * @category Parent & children
   */

  get isLeaf() {
    return this.meta.isLeaf !== false && !this.isRoot;
  }
  /**
   * Is a parent node in a tree structure?
   * @property {Boolean}
   * @readonly
   * @category Parent & children
   */

  get isParent() {
    return !this.isLeaf;
  }
  /**
   * Returns true for parent nodes with children loaded (there might still be no children)
   * @property {Boolean}
   * @readonly
   * @category Parent & children
   */

  get isLoaded() {
    return this.isParent && Array.isArray(this.children);
  }
  /**
   * Count all children (including sub-children) for a node (in its `firstStore´)
   * @member {Number}
   * @category Parent & children
   */

  get descendantCount() {
    return this.getDescendantCount();
  }
  /**
   * Count visible (expanded) children (including sub-children) for a node (in its `firstStore`)
   * @member {Number}
   * @category Parent & children
   */

  get visibleDescendantCount() {
    return this.getDescendantCount(true);
  }
  /**
   * Count visible (expanded)/all children for this node, optionally specifying for which store.
   * @param {Boolean} [onlyVisible] Specify `true` to only count visible (expanded) children.
   * @param {Core.data.Store} [store] A Store to which this node belongs
   * @returns {Number}
   * @category Parent & children
   */

  getDescendantCount(onlyVisible = false, store = this.firstStore) {
    const children = this.children;

    if (!children || !Array.isArray(children) || onlyVisible && !this.isExpanded(store)) {
      return 0;
    }

    return children.reduce((count, child) => count + child.getDescendantCount(onlyVisible), children.length);
  }
  /**
   * Retrieve all children (by traversing sub nodes)
   * @property {Core.data.Model[]}
   * @category Parent & children
   */

  get allChildren() {
    const children = this.children;
    if (!children) return [];
    return children.reduce((all, child) => {
      all.push(child); // push.apply is faster than push with array spread:
      // https://jsperf.com/push-apply-vs-push-with-array-spread/1

      all.push.apply(all, child.allChildren);
      return all;
    }, []);
  }
  /**
   * Get the first child of this node
   * @property {Core.data.Model}
   * @readonly
   * @category Parent & children
   */

  get firstChild() {
    const children = this.children;
    return children && children.length && children[0] || null;
  }
  /**
   * Get the last child of this node
   * @property {Core.data.Model}
   * @readonly
   * @category Parent & children
   */

  get lastChild() {
    const children = this.children;
    return children && children.length && children[children.length - 1] || null;
  }
  /**
   * Returns count of all preceding sibling nodes (including their children).
   * @property {Number}
   * @category Parent & children
   */

  get previousSiblingsTotalCount() {
    let task = this.previousSibling,
        count = this.parentIndex;

    while (task) {
      count += task.descendantCount;
      task = task.previousSibling;
    }

    return count;
  }

  get root() {
    return this.parent && this.parent.root || this;
  }
  /**
   * Reading this property returns the id of the parent node, if this record is a child of a node.
   *
   * Setting this property appends this record to the record with the passed
   * id **in the same store that this record is already in**.
   *
   * Note that setting this property is **only valid if this record is already
   * part of a tree store**.
   *
   * This is not intended for general use. This is for when a server responds to a record
   * mutation and the server decides to move a record to a new parent. If a `parentId` property
   * is passed in the response data for a record, that record will be moved.
   * @property {Number|String}
   * @category Parent & children
   */

  get parentId() {
    return this.parent && !this.parent.isAutoRoot ? this.parent.id : null;
  }

  set parentId(parentId) {
    const me = this,
          {
      parent
    } = me,
          newParent = parentId && me.firstStore.getById(parentId); // Handle exact equality of parent.
    // Also handle one being null and the other being undefined meaning no change.

    if (!(newParent === parent || !parent && !newParent)) {
      // If we are batching, we do not trigger a change immediately.
      // endBatch will set the field which will set the property again.
      if (me.isBatchUpdating) {
        me.meta.batchChanges.parentId = parentId;
      } else {
        if (newParent) {
          newParent.appendChild(me);
        } else {
          me.parent.removeChild(me);
        }
      }
    }
  }

  static set parentIdField(parentIdField) {
    // Maintainer: the "this" references in here reference two different contexts.
    // Outside of the property definition, it's the Model Class.
    // In the getter and setter, it's the record instance.
    this._parentIdField = parentIdField;
    Object.defineProperty(this.prototype, parentIdField, {
      set: function (parentId) {
        // no arrow functions here, need `this` to change to instance
        // noinspection JSPotentiallyInvalidUsageOfClassThis
        this.parentId = parentId;
      },
      get: function () {
        // no arrow functions here, need `this` to change to instance
        // noinspection JSPotentiallyInvalidUsageOfClassThis
        return this.parentId;
      }
    });
  }

  static get parentIdField() {
    return this._parentIdField || 'parentId';
  }
  /**
   * Traverses all child nodes recursively calling the passed function
   * on a target node **before** iterating the child nodes.
   * @param {Function} fn
   * @category Parent & children
   */

  traverse(fn, skipSelf = false) {
    const {
      children
    } = this;

    if (!skipSelf) {
      fn.call(this, this);
    } // Simply testing whether there is non-zero children length
    // is 10x faster than using this.isLoaded

    for (let i = 0, l = children && children.length; i < l; i++) {
      children[i].traverse(fn);
    }
  }
  /**
   * Traverses all child nodes recursively calling the passed function
   * on child nodes of a target **before** calling it it on the node.
   * @param {Function} fn
   * @param {Boolean} [skipSelf] True to skip this node in the traversal
   * @category Parent & children
   */

  traverseBefore(fn, skipSelf = false) {
    const {
      children
    } = this; // Simply testing whether there is non-zero children length
    // is 10x faster than using this.isLoaded

    for (let i = 0, l = children && children.length; i < l; i++) {
      children[i].traverse(fn);
    }

    if (!skipSelf) {
      fn.call(this, this);
    }
  }
  /**
   * Traverses child nodes recursively while fn returns true
   * @param {Function} fn
   * @param {Boolean} [skipSelf] True to skip this node in the traversal
   * @category Parent & children
   * @returns {Boolean}
   */

  traverseWhile(fn, skipSelf = false) {
    const me = this;
    let goOn = true;

    if (!skipSelf) {
      goOn = fn.call(me, me) !== false;
    }

    if (goOn && me.isLoaded) {
      goOn = me.children.every(child => child.traverseWhile(fn));
    }

    return goOn;
  }
  /**
   * Bubbles up from this node, calling the specified function with each node.
   *
   * @param {Function} fn The function to call for each node
   * @param {Boolean} [skipSelf] True to skip this node in the traversal
   * @category Parent & children
   */

  bubble(fn, skipSelf = false) {
    let me = this;

    if (!skipSelf) {
      fn.call(me, me);
    }

    while (me.parent) {
      me = me.parent;
      fn.call(me, me);
    }
  }
  /**
   * Bubbles up from this node, calling the specified function with each node,
   * while the function returns true.
   *
   * @param {Function} fn The function to call for each node
   * @param {Boolean} [skipSelf] True to skip this node in the traversal
   * @category Parent & children
   * @return {Boolean}
   */

  bubbleWhile(fn, skipSelf = false) {
    let me = this,
        goOn = true;

    if (!skipSelf) {
      goOn = fn.call(me, me);
    }

    while (goOn && me.parent) {
      me = me.parent;
      goOn = fn.call(me, me);
    }

    return goOn;
  }
  /**
   * Checks if this model contain another model as one of it's descendants
   *
   * @param {Core.data.Model|String|Number} childOrId child node or id
   * @category Parent & children
   * @returns {Boolean}
   */

  contains(childOrId) {
    if (childOrId && typeof childOrId === 'object') {
      childOrId = childOrId.id;
    }

    return !this.traverseWhile(node => node.id != childOrId);
  }

  getTopParent(all) {
    let result;

    if (all) {
      result = [];
      this.bubbleWhile(t => {
        result.push(t);
        return t.parent && !t.parent.isRoot;
      });
    } else {
      result = null;
      this.bubbleWhile(t => {
        if (!t.parent) {
          result = t;
        }

        return t.parent && !t.parent.isRoot;
      });
    }

    return result;
  }
  /**
   * Append a child record(s) to any current children.
   * @param {Core.data.Model|Core.data.Model[]} childRecord Record or array of records to append
   * @param {Boolean} [silent] Pass `true` to not trigger events during append
   * @returns {Core.data.Model|Core.data.Model[]}
   * @category Parent & children
   */

  appendChild(childRecord, silent = false) {
    return this.insertChild(childRecord, null, silent);
  }
  /**
   * Insert a child record(s) before an existing child record.
   * @param {Core.data.Model|Core.data.Model[]} childRecord Record or array of records to insert
   * @param {Core.data.Model} [before] Optional record to insert before, leave out to append to the end
   * @param {Boolean} [silent] Pass `true` to not trigger events during append
   * @returns {Core.data.Model|Core.data.Model[]}
   * @category Parent & children
   */

  insertChild(childRecord, beforeRecord = null, silent = false) {
    // Handle deprecated signature
    if (typeof childRecord === 'number') {
      const index = childRecord;
      childRecord = beforeRecord;
      beforeRecord = this.children[index];
    }

    const me = this,
          wasLeaf = me.isLeaf,
          returnArray = Array.isArray(childRecord);
    if (!Array.isArray(childRecord)) childRecord = [childRecord];

    if (!silent) {
      if (!me.stores.every(s => s.trigger('beforeAdd', {
        records: childRecord,
        parent: me
      }) !== false)) {
        return null;
      }
    } // This call makes child record an array containing Models

    childRecord = me.ingestChildren(childRecord); // NOTE: see comment in Model::set() about before/in/after calls approach.

    const index = beforeRecord ? beforeRecord.parentIndex : me.children ? me.children.length : 0,
          preResult = me.beforeInsertChild ? me.beforeInsertChild(childRecord) : undefined,
          inserted = me.internalAppendInsert(childRecord, beforeRecord, silent); // Turn into a parent if not already one

    if (wasLeaf && inserted.length) {
      me.meta.isLeaf = false;
    } // If we've transitioned to being a branch node, signal a change event
    // so that the UI updates.
    // Not if it's due to root node loading. StoreTree#onNodeAddChild
    // for the rootNode will fire a store refresh.

    if (me.isLeaf !== wasLeaf && !me.root.isLoading && !silent) {
      me.stores.forEach(s => {
        const changes = {
          isLeaf: {
            value: false,
            oldValue: true
          }
        };
        s.trigger('update', {
          record: me,
          changes
        });
        s.trigger('change', {
          action: 'update',
          record: me,
          changes
        });
      });
    }

    me.afterInsertChild && me.afterInsertChild(index, childRecord, preResult, inserted);
    return returnArray || !inserted ? inserted : inserted[0];
  }

  tryInsertChild() {
    return this.insertChild(...arguments);
  }

  internalAppendInsert(recordsToInsert, beforeRecord, silent) {
    const me = this,
          {
      stores,
      root
    } = me,
          {
      firstStore: rootStore
    } = root,
          {
      parentIdField
    } = me.constructor,
          parentId = me.isAutoRoot ? null : me.id,
          isMove = {};
    let isNoop, start, i, newRecordsCloned; // The reference node must be one of our children. If not, fall back to an append.

    if (beforeRecord && beforeRecord.parent !== me) {
      beforeRecord = null;
    } // If the records starting at insertAt or (insertAt - 1), are the same sequence
    // that we are being asked to add, this is a no-op.

    if (me.children) {
      const children = me.children,
            insertAt = beforeRecord ? beforeRecord.parentIndex : children.length;

      if (children[start = insertAt] === recordsToInsert[0] || children[start = insertAt - 1] === recordsToInsert[0]) {
        for (isNoop = true, i = 0; isNoop && i < recordsToInsert.length; i++) {
          if (recordsToInsert[i] !== children[start + i]) {
            isNoop = false;
          }
        }
      }
    } // Fulfill the contract of appendChild/insertChild even if we did not have to do anything.
    // Callers must be able to correctly postprocess the returned value as an array.

    if (isNoop) {
      return recordsToInsert;
    } // Remove incoming child nodes from any current parent.

    for (i = 0; i < recordsToInsert.length; i++) {
      const newRecord = recordsToInsert[i],
            oldParent = newRecord.parent; // Store added should not be modified for adds
      // caused by moving.

      isMove[newRecord.id] = newRecord.root === root; // Check if any descendants of the added node are moves.

      rootStore && newRecord.traverse(r => {
        if (r.root === root) {
          isMove[r.id] = true;
        }
      }); // If the new record has a parent, remove from that parent.
      // This operation may be vetoed by listeners.
      // If it is vetoed, then remove from the newRecords and do not
      // set the parent property

      if (oldParent && oldParent.removeChild(newRecord, isMove[newRecord.id]) === false) {
        if (!newRecordsCloned) {
          recordsToInsert = recordsToInsert.slice();
          newRecordsCloned = true;
        }

        recordsToInsert.splice(i--, 1);
      } else {
        newRecord.parent = me; // Set parentId directly to data, record.parentId uses a getter to return record.parent.id

        newRecord.data[parentIdField] = parentId;

        if (oldParent) {
          newRecord.meta.oldParentId = oldParent.id;
        }
      }
    } // Still records to insert after beforeRemove listeners may have vetoed some

    if (recordsToInsert.length) {
      // Add to the children
      const insertAt = me.addToChildren(me.children || (me.children = []), beforeRecord, recordsToInsert); // When filter is used add to the unfiltered children

      me.addToChildren(me.unfilteredChildren, beforeRecord, recordsToInsert, true);
      stores.forEach(store => {
        if (!store.isChained) {
          recordsToInsert.forEach(record => {
            // Initialize context for newly added records
            record.joinStore(store);
          }); // Add to store (will also add any child records and trigger events)

          store.onNodeAddChild(me, recordsToInsert, insertAt, isMove, silent);
          recordsToInsert.forEach(record => {
            // If we are in the recursive inclusion of children at construction
            // time, or in a store load, that must not be a data modification.
            // Otherwise, we have to signal a change
            if (record.meta.oldParentId != null && !(me.inProcessChildren || me.isLoading)) {
              const toSet = {
                [parentIdField]: parentId,
                [me.getDataSource('parentIndex')]: record.parentIndex
              },
                    wasSet = {},
                    {
                modified,
                oldParentId
              } = record.meta,
                    oldParentIndex = modified.parentIndex;
              delete record.meta.oldParentId;

              if (me.id !== oldParentId) {
                wasSet[parentIdField] = {
                  value: parentId,
                  oldValue: oldParentId
                };
              }

              if (record.parentIndex !== oldParentIndex) {
                wasSet.parentIndex = {
                  value: record.parentIndex,
                  oldValue: oldParentIndex
                };
              } // Changing back to its original value

              if (modified[parentIdField] === me.id) {
                Reflect.deleteProperty(modified, parentIdField);
              } // Cache its original value
              else if (!(parentIdField in modified)) {
                  modified[parentIdField] = oldParentId;
                }

              record.afterChange(toSet, wasSet);
            }
          });
        }
      });
    }

    return recordsToInsert;
  }
  /**
   * Remove a child record. Only direct children of this node can be removed, others are ignored.
   * @param {Core.data.Model|Core.data.Model[]} childRecords The record(s) to remove.
   * @param {Boolean} [isMove] Pass `true` if the record is being moved within the same store.
   * @param {Boolean} [silent] Pass `true` to not trigger events during remove.
   * @category Parent & children
   */

  removeChild(childRecords, isMove = false, silent = false) {
    const me = this,
          wasLeaf = me.isLeaf,
          {
      children,
      stores
    } = me;

    if (!Array.isArray(childRecords)) {
      childRecords = [childRecords];
    }

    childRecords = childRecords.filter(r => r.parent === me);

    if (!silent) {
      // Allow store listeners to veto the beforeRemove event
      for (const store of stores) {
        if (!store.isChained) {
          if (store.trigger('beforeRemove', {
            parent: me,
            records: childRecords,
            isMove
          }) === false) {
            return false;
          }
        }
      }
    }

    const preResult = me.beforeRemoveChild ? me.beforeRemoveChild(childRecords, isMove) : undefined;

    for (const childRecord of childRecords) {
      const {
        parentIdField
      } = childRecord.constructor,
            {
        modified
      } = childRecord.meta,
            oldParentId = childRecord.parent ? childRecord.parent.id : null; // Cache its original value

      if (!(parentIdField in modified)) {
        modified[parentIdField] = oldParentId;
      }

      const index = me.removeFromChildren(children, childRecord);
      me.removeFromChildren(me.unfilteredChildren, childRecord, true);
      stores.forEach(store => {
        if (!store.isChained) {
          store.onNodeRemoveChild(me, [childRecord], index, {
            isMove,
            silent
          });
        }
      }); // No need to clean up the node parent info and other meta data in case it is "move" operation. The info will be updated after "insert" operation.

      if (!isMove) {
        childRecord.parent = childRecord.parentIndex = childRecord.unfilteredIndex = childRecord.nextSibling = childRecord.previousSibling = null; // Reset parentId in data, record.parentId uses a getter to return record.parent.id

        childRecord.data[parentIdField] = null;
      }
    } // Convert emptied parent into leaf if configured to do so

    if (!children.length && me.constructor.convertEmptyParentToLeaf.onRemove && !me.isRoot) {
      me.meta.isLeaf = true;
    } // If we've transitioned to being a leaf node, signal a change event
    // so that the UI updates

    if (me.isLeaf !== wasLeaf && !silent) {
      me.stores.forEach(s => {
        const changes = {
          isLeaf: {
            value: true,
            oldValue: false
          }
        };
        s.trigger('update', {
          record: me,
          changes
        });
        s.trigger('change', {
          action: 'update',
          record: me,
          changes
        });
      });
    }

    me.afterRemoveChild && me.afterRemoveChild(childRecords, preResult, isMove);
  }

  clearParentId() {
    const me = this;
    Reflect.deleteProperty(me.data, me.parentIdField);
    Reflect.deleteProperty(me.originalData, me.parentIdField);

    if (me.meta.modified) {
      Reflect.deleteProperty(me.meta.modified, me.parentIdField);
    }
  }

  clearChildren(silent = false) {
    const me = this,
          {
      children,
      stores
    } = me;

    if (children) {
      me.children = [];
      stores.forEach(store => {
        if (!store.isChained) {
          // unfiltered:true to unregister children on filtered stores
          store.onNodeRemoveChild(me, children, 0, {
            unfiltered: true,
            silent
          });
        }
      }); // clear unfilteredChildren (must be after the above loop)

      if (me.unfilteredChildren) {
        me.unfilteredChildren = [];
      }
    }
  }
  /**
   * Removes all records from the rootNode
   * @private
   */

  clear() {
    const me = this,
          {
      stores
    } = me,
          children = me.children && me.children.slice(); // Only allow for root node and if data is present

    if (!me.isRoot || !children) {
      return;
    }

    for (const store of stores) {
      if (!store.isChained) {
        if (store.trigger('beforeRemove', {
          parent: me,
          records: children,
          isMove: false,
          removingAll: true
        }) === false) {
          return false;
        }
      }
    }

    me.children.length = 0;
    stores.forEach(store => {
      children.forEach(child => {
        if (child.stores.includes(store)) {
          // this will drill down the child, unregistering whole branch
          child.unJoinStore(store);
        }

        child.parent = child.parentIndex = child.nextSibling = child.previousSibling = null;
      });
      store.storage.suspendEvents();
      store.storage.clear();
      store.storage.resumeEvents();
      store.added.clear();
      store.modified.clear();
      store.trigger('removeAll');
      store.trigger('change', {
        action: 'removeall'
      });
    });
  }

  updateChildrenIndices(children, unfiltered = false, silent = false) {
    const indexName = unfiltered ? 'unfilteredIndex' : 'parentIndex';
    let previousSibling = null;

    for (let i = 0; i < children.length; i++) {
      const child = children[i],
            oldValue = child[indexName];

      if (indexName === 'parentIndex') {
        // Record should not be considered modified by initial assignment of parentIndex
        if (oldValue === undefined || silent) {
          child.setData('parentIndex', i);
        } // Check to avoid pointless beforeUpdates from inSet
        else if (oldValue !== i) {
            // Silent set, do not want to trigger events from updated indices
            child.set('parentIndex', i, true);
          }
      } else {
        child[indexName] = i;
      }

      if (!unfiltered) {
        child.previousSibling = previousSibling;

        if (previousSibling) {
          previousSibling.nextSibling = child;
        } // Last child never has a nextSibling

        if (i === children.length - 1) {
          child.nextSibling = null;
        }

        previousSibling = child;
      }
    }
  }

  addToChildren(children, beforeRecord, newRecords, unfiltered = false) {
    if (children) {
      const indexName = unfiltered ? 'unfilteredIndex' : 'parentIndex',
            index = beforeRecord ? beforeRecord[indexName] : children.length;
      children.splice(index, 0, ...newRecords);
      this.updateChildrenIndices(children, unfiltered);
      return index;
    }
  }

  removeFromChildren(children, childRecord, unfiltered = false) {
    if (children) {
      const indexName = unfiltered ? 'unfilteredIndex' : 'parentIndex',
            index = childRecord[indexName];

      if (index > -1) {
        children.splice(index, 1);
        this.updateChildrenIndices(children, unfiltered);
      }

      return index;
    }
  }

});

/**
 * @module Core/mixin/Factoryable
 */

const {
  defineProperty: defineProperty$2
} = Reflect,
      ownerSymbol = Symbol('owner'),
      typeSplitRe = /[\s,]+/;
/**
 * This mixin is applied to base classes of a type that will be dynamically created by type name aliases.
 * ```
 *  class Layout extends Base.mixin(Factoryable) {
 *      static get factoryable() {
 *          return {
 *              defaultType : 'default'
 *          };
 *      }
 *
 *      static get type() {
 *          return 'default';
 *      }
 *  }
 *
 *  class Fit extends Layout {
 *      static get type() {
 *          return 'fit';
 *      }
 *  }
 * ```
 *
 * Once a family of classes has been defined, instances are created using the `create()` method:
 * ```
 *  const layout = Layout.create(config);
 * ```
 *
 * In the above example, `config` can be a type name (such as "fit") or a config object with a `type` property that
 * holds the type name.
 *
 * Factories can also extend other factories. For example, one factory creates objects that are useful across a wide
 * range of consumers, and a second factory creates objects for a more specialized consumer. If that specialized
 * consumer can also consume objects from the first factory, then the second factory can specify this relationship:
 * ```
 *  class General extends Base.mixin(Factoryable) {
 *      ...
 *  }
 *
 *  class Specialized extends Base.mixin(Factoryable) {
 *      static get factoryable() {
 *          return {
 *              extends : General,
 *              ...
 *          };
 *      }
 *  }
 * ```
 * The `extends` factoryable option can be either a class that mixes in `Factoryable` or an array of such classes.
 * @mixin
 * @internal
 */

var Factoryable = (Target => class Factoryable extends (Target || Base) {
  static get $name() {
    return 'Factoryable';
  }

  static get declarable() {
    return [
    /**
     * This property getter returns options that control the factory process. This property getter must be
     * defined by the class that mixes in `Factoryable` in order to initialize the factory properly.
     * ```
     *  static get factoryable() {
     *      return {
     *          defaultType : 'default'
     *      };
     *  }
     * ```
     * If there are no special options to provide, this method can return nothing (`undefined`):
     * ```
     *  static get factoryable() {
     *      // initialize the factory with all default options
     *  }
     * ```
     * @static
     * @member {Object} factoryable
     * @property {Boolean} [factoryable.caseless=true] Specify `false` to use case-sensitive type names. The
     * default is to ignore case.
     * @property {String} [factoryable.defaultType=null] The default type to create when a config object has
     * no `typeKey` property.
     * @property {Function|Function[]} [factoryable.extends] One or more classes that mix in `Factoryable` to
     * use for resolving type names when a type name is not found in this factory.
     * @property {String} [factoryable.typeKey='type'] The name of the proprerty in a config object that holds
     * the type name.
     * @internal
     */
    'factoryable',
    /**
     * One or more additional type name aliases for this class. This can be useful for renaming and maintaining
     * a previous type name.
     * ```
     *  class Fit extends Layout {
     *      static get type() {
     *          return 'fit';
     *      }
     *
     *      static get alias() {
     *          return 'fill';  // deprecated type name (now known as 'fit')
     *      }
     *  }
     * ```
     * @static
     * @member {String|String[]} alias
     * @internal
     */
    'alias',
    /**
     * The (canonical) type name for this class by which instances can be created using the static
     * {@link #function-create-static create()} method.
     * @static
     * @member {String} type
     */
    'type'];
  }
  /**
   * Registers a class (`cls`) associated with the given `type`.
   * @param {String|String[]} type A string, array of strings or a comma-separated string containing the type names
   * for the specified `cls` class.
   * @param {Function} cls The class (constructor function)
   * @param {Boolean} [replace] Pass `true` to overwrite existing registered types. Otherwise, this method will throw
   * an exception if the `type` is already registered with this factory.
   * @internal
   */

  static register(type, cls, replace = false) {
    const {
      factoryable
    } = this.initClass(),
          {
      caseless,
      registry
    } = factoryable,
          types = StringHelper.split(type, typeSplitRe); // if type is a string[] it will just be returned

    for (let lower, name, i = 0; i < types.length; ++i) {
      name = types[i];
      lower = caseless ? name.toLowerCase() : name;

      if (!replace && lower in registry) {
        throw new Error(`Type "${name}" already registered with ${factoryable.class.name} factory`);
      } // Ensure class being registered is initialized. (initClass returns the class)

      registry[name] = registry[lower] = cls.initClass();
    }
  }
  /**
   * Returns 'true` if the passed instance is of the passed type or of a derived class.
   * @param {Object} instance The object to test.
   * @param {String} type The type to test against
   */

  static isA(instance, type) {
    return this.isType(instance, type, true);
  }
  /**
   * Returns 'true` if the passed instance is of the passed type.
   * @param {Object} instance The object to test.
   * @param {String} type The type to test against
   * @param {Boolean} [deep] Pass `true` to return `true` if the class is a subclass of the passed type.
   */

  static isType(instance, type, deep) {
    const {
      factoryable
    } = this,
          {
      caseless,
      registry
    } = factoryable,
          typeCls = registry[caseless ? type.toLowerCase() : type]; // If the type to be tested against maps to a class, see if the instance is an instanceof that

    if (typeCls) {
      if (deep) {
        return instance instanceof typeCls;
      }

      return instance.constructor === typeCls;
    }

    return false;
  }

  static setupAlias(cls) {
    cls.register(cls.alias, cls);
  }

  static setupFactoryable(cls, meta) {
    const superClass = meta.super.class;
    let {
      factoryable
    } = cls;
    factoryable = {
      caseless: true,
      defaultType: null,
      extends: superClass.factoryable ? [superClass] : null,
      typeKey: 'type',
      ...factoryable
    };
    factoryable.class = cls;
    factoryable.registry = Object.create(null);

    if (factoryable.extends && !Array.isArray(factoryable.extends)) {
      factoryable.extends = [factoryable.extends];
    } // Replace the class/static getter with a new one that returns the complete factoryable object:

    defineProperty$2(cls, 'factoryable', {
      get() {
        return factoryable;
      }

    });
  }

  static setupType(cls) {
    const {
      type
    } = cls;
    cls.register(type, cls); // Copy the static type property onto the prototype as a readonly property:

    defineProperty$2(cls.prototype, 'type', {
      value: type
    });
  }
  /**
   * Creates an instance from this factory, given the type name or a config object.
   * @param {String|Object} config The type name string or config object.
   * @param {String|Function|Object} [options] Creation options (for details see {@link #function-reconfigure-static}).
   * @returns {Object}
   */

  static create(config, options) {
    return this.reconfigure(null, config, options);
  }
  /**
   * Reconfigures an optional existing instance based on the provided config and returns the correctly configured
   * instance. This will be the `existingInstance` if the `config` does not specify a different type.
   *
   * If `config` is `null` (or simply falsy), this method will destroy the `existingInstance` (if any) and return
   * `null`.
   *
   * If there is no `existingInstance`, the config must specify a type. That is, it must be a string (the type name)
   * or an object containing a `type` property, the `defaultType` must be provided or the factory itself must have
   * a `defaultType` specified (see {@link #property-factoryable-static}).
   *
   * When an `existingInstance` is provided and a type is specified, the instance will be reconfigured via `setConfig`
   * if it is of that type. Otherwise, the `existingInstance` is destroyed (if it is owned by the `options.owner`)
   * and a new instance of the correct type is created.
   *
   * @param {Object} existingInstance The instance to reconfigure. This can be `null`.
   *
   * @param {String|Object} config The type name string or config object.
   *
   * @param {String|Function|Object} [options] Additional options to control the reconfiguration process. If this
   * value is a string or a class constructor, it treated as `options.type`. If this value is an class instance, it
   * is used as the `options.owner`. If this is a function, it is treated as `options.setup`. NOTE: functions declared
   * using the `function` keyword are equivalent to class constructors. Use an arrow function or a class method to
   * avoid this when a `setup` function is intended.
   *
   * @param {String|Function} [options.type] The default type to use if the `config` object does not specify a type.
   *
   * @param {Object} [options.owner] The owner of any created instances. If the `existingInstance` is being replaced,
   * this value determines if the instance will be destroyed.
   *
   * @param {Object} [options.defaults] A config object of default values to use when creating a new instance.
   *
   * @param {Function|String} [options.setup] A function or the name of a method (on the `options.owner`) to call
   * prior to creating a new instance. It is passed the config object that will be used to create the instance. If a
   * truthy value is returned, that value is passed to the constructor instead of the provided config object.
   *
   * @param {Function|String} [options.cleanup] A function or the name of a method (on the `options.owner`) to call
   * prior to destroying the `existingInstance`. The `existingInstance` is passed as the sole argument.
   *
   * @returns {Object} The reconfigured instance (either `existingInstance` or a new instance of the desired type)
   */

  static reconfigure(existingInstance, config, options) {
    const me = this,
          {
      factoryable
    } = me,
          {
      typeKey
    } = factoryable;
    let type = config,
        defaultType = options,
        cleanup,
        defaults,
        mergeType,
        owner,
        prepared,
        setup,
        t; // Pick apart the options and set the vars accordingly

    if (options && !ObjectHelper.isClass(options)) {
      // if (options is not the defaultType)
      defaultType = null;
      t = typeof options;

      if (t === 'function') {
        setup = options;
      } else if (t === 'string') {
        defaultType = options;
      } else if (ObjectHelper.isObject(options)) {
        // TODO revert to
        //  ({ cleanup, defaults, owner, setup, type : defaultType } = options);
        //  after this issue is fixed https://github.com/bryntum/bryntum-suite/issues/1457
        cleanup = options.cleanup;
        defaults = options.defaults;
        owner = options.owner;
        setup = options.setup;
        defaultType = options.type;
      } else {
        owner = options;
      }
    } // Figure out config... it's either a type (string), a config object or the actual instance.

    if (typeof type === 'string') {
      config = {};
    } else if (config) {
      if (!ObjectHelper.isObject(config)) {
        // If we are being given an instance (not a config object), discard or destroy the existingInstance
        if (owner && config !== existingInstance && (existingInstance === null || existingInstance === void 0 ? void 0 : existingInstance[ownerSymbol]) === owner) {
          var _cleanup;

          typeof cleanup === 'string' ? owner[cleanup](existingInstance) : (_cleanup = cleanup) === null || _cleanup === void 0 ? void 0 : _cleanup(existingInstance);
          existingInstance.destroy();
        }

        return config;
      }

      type = config[typeKey];
    }

    type = type && me.resolveType(type); // We've got our orders... make it so...

    if (existingInstance) {
      // We can have a type-less config object when reconfiguring an existing instance, but if we have a type,
      // the existingInstance must be of that type. If !config that means we are nulling out.
      if (config && (!type || existingInstance.constructor === type)) {
        if (typeKey in config) {
          config = ObjectHelper.assign({}, config);
          delete config[typeKey]; // so "type" won't be processed as a config property
        }

        existingInstance.setConfig(config);
        return existingInstance;
      }

      if (owner && existingInstance[ownerSymbol] === owner) {
        var _cleanup2;

        typeof cleanup === 'string' ? owner[cleanup](existingInstance) : (_cleanup2 = cleanup) === null || _cleanup2 === void 0 ? void 0 : _cleanup2(existingInstance);
        existingInstance.destroy();
      }
    }

    if (config) {
      if (defaults) {
        if (!(mergeType = type)) {
          if (!(mergeType = defaults[typeKey] || defaultType || factoryable.defaultType)) {
            throw new Error(`No default mergeType defined for ${factoryable.class.name} factory`);
          }

          mergeType = me.resolveType(mergeType);
        }

        if (mergeType) {
          // Allow the merge fn of each config to perform the task:
          config = mergeType.mergeConfigs(defaults, config);
        }
      }

      if (setup) {
        prepared = typeof setup === 'string' ? owner[setup](config, type, defaults) : setup(config, type, defaults);

        if (prepared === null) {
          return prepared;
        }

        config = prepared || config;
      }

      if (!type) {
        // One more check on config[typeKey] since the setup() function may have added it...
        if (!(type = config[typeKey] || defaultType || factoryable.defaultType)) {
          throw new Error(`No default type defined for ${factoryable.class.name} factory`);
        }

        type = me.resolveType(type);
      }

      if (defaults && !mergeType) {
        config = type.mergeConfigs(defaults, config);
      }

      if (typeKey in config) {
        config = ObjectHelper.assign({}, config);
        delete config[typeKey]; // so "type" won't be processed as a config property
      }

      config = new type(config);

      if (owner) {
        config[ownerSymbol] = owner;
      }
    }

    return config || null;
  }
  /**
   * This method returns the constructor of the class registered for the given type name.
   * @param {String} type The type name to look up.
   * @param {Boolean} [optional] Pass `true` to return `null` if `type` is not found instead of throwing an exception.
   * @returns {Function}
   * @private
   */

  static resolveType(type, optional) {
    if (typeof type !== 'string') {
      return type;
    }

    const {
      factoryable
    } = this,
          bases = factoryable.extends;
    let result = factoryable.registry[factoryable.caseless ? type.toLowerCase() : type],
        i;

    for (i = 0; !result && bases && i < bases.length; ++i) {
      // Pass optional=true to base factory so the error is our own should the lookup fail:
      result = bases[i].resolveType(type,
      /* optional = */
      true);
    }

    if (!result && !optional) {
      throw new Error(`Invalid type name "${type}" passed to ${factoryable.class.name} factory`);
    }

    return result;
  }

});

/**
 * @module Core/data/field/DataField
 */

/**
 * This is the base class for Model field classes. A field class defines how to handle the data for a particular type
 * of field. Many of these behaviors can be configured on individual field instances.
 *
 * @extends Core/Base
 */

class DataField extends Base.mixin(Factoryable) {
  static get type() {
    return 'auto';
  }

  static get factoryable() {
    return {
      defaultType: 'auto'
    };
  }

  static get prototypeProperties() {
    return {
      /**
       * The name of the field.
       * @config {String} name
       */

      /**
       * A function that compares two values and returns a value < 0 if the first is less than the second, or 0
       * if the values are equal, or a value > 0 if the first is greater than the second.
       * @config {Function}
       * @default
       */
      compare: null,

      /**
       * A function that compares two objects or records using the `compare` function on the properties of each
       * objects based on the `name` of this field.
       * @config {Function}
       * @default
       * @internal
       */
      compareItems: null,

      /**
       * The property in a record's data object that contains the field's value.
       * Defaults to the field's `name`.
       * @config {String}
       * @default
       */
      dataSource: null,

      /**
       * The default value to assign to this field in a record if no value is provided.
       * @config {*} defaultValue
       */

      /**
       * Setting to `false` indicates that `null` is not a valid value.
       * @config {Boolean}
       * @default
       */
      nullable: true,

      /**
       * The value to return from {@link #function-print} for a `null` or `undefined` value.
       * @config {String}
       * @default
       */
      nullText: null,

      /**
       * The value to replace `null` when the field is not `nullable`.
       * @config {*}
       * @default
       */
      nullValue: undefined,

      /**
       * Set to `false` to exclude this field when saving records to a server.
       * @config {Boolean}
       * @default
       */
      persist: true,

      /**
       * Set to `true` for the field's set accessor to ignore attempts to set this field.
       * @config {Boolean}
       * @default
       */
      readOnly: false
    };
  }
  /**
   * The class that first defined this field. Derived classes that override a field do not change this property.
   * @member {Core.data.Model} definedBy
   * @private
   * @readonly
   */

  /**
   * The class that most specifically defined this field. Derived classes that override a field set this property to
   * themselves.
   * @member {Core.data.Model} owner
   * @private
   * @readonly
   */
  // NOTE: Since we create lots of instances, they have no life cycle (they are not destroyed) and are readonly after
  // creation, this class does not use configurable.

  construct(config) {
    const me = this;

    if (config) {
      me.name = config.name; // assign name first for diagnostic reasons

      Object.assign(me, config);
    }

    if (me.compare) {
      // We wrap in this way to allow compareItems() to be used as an array sorter fn (which gets no "this"):
      me.compareItems = (itemA, itemB) => me.compare(itemA === null || itemA === void 0 ? void 0 : itemA[me.name], itemB === null || itemB === void 0 ? void 0 : itemB[me.name]);
    }
  }
  /**
   * This method transforms a data value into the desired form for storage in the record's data object.
   * @method convert
   * @param {*} value The value to convert for storage in a record.
   * @returns {*} The converted value.
   */

  /**
   * This method transforms a data value into the desired form for transmitting to a server.
   * @method serialize
   * @param {*} value The value to serialize
   * @param {Core.data.Model} record The record that contains the value being serialized.
   * @returns {*} The serialized value.
   */

  /**
   * Create getter and setter functions for the specified field name under the specified key.
   * @internal
   */

  defineAccessor(target, force) {
    const {
      name
    } = this;

    if (force || !(name in target)) {
      Reflect.defineProperty(target, name, {
        configurable: true,
        // To allow removing it later
        enumerable: true,

        // no arrow functions here, need `this` to change to instance
        get() {
          return this.get(name);
        },

        // no arrow functions here, need `this` to change to instance
        set(value) {
          // Since the accessor is defined on a base class, we dip into the fields map for the actual
          // calling class to get the correct field definition
          const field = this.$meta.fields.map[name]; // Only set if field is read/write. Privately, we use setData to set its value

          if (!(field && field.readOnly)) {
            this.set(name, value);
          }
        }

      });
    }
  }
  /**
   * Compares two values for this field and returns `true` if they are equal, and `false` if not.
   * @param {*} first The first value to compare for equality.
   * @param {*} second The second value to compare for equality.
   * @returns {Boolean} `true` if `first` and `second` are equal.
   */

  isEqual(first, second) {
    return ObjectHelper.isEqual(first, second);
  }
  /**
   * Returns the given field value as a `String`. If `value` is `null` or `undefined`, the value specified by
   * {@link #config-nullText} is returned.
   * @param {*} value The value to convert to a string.
   * @returns {String}
   */

  print(value) {
    return value == null ? this.nullText : this.printValue(value);
  }
  /**
   * Returns the given, non-null field value as a `String`.
   * @param {*} value The value to convert to a string (will not be `null` or `undefined`).
   * @returns {String}
   * @protected
   */

  printValue(value) {
    return String(value);
  }

}
DataField._$name = 'DataField';

/**
 * @module Core/data/field/BooleanDataField
 */

/**
 * This field class handles field of type `Boolean`.
 * ```
 * class Person extends Model {
 *     static get fields() {
 *         return [
 *             'name',
 *             { name : 'active', type : 'boolean' }
 *         ];
 *     }
 * }
 * ```
 * When a field is declared as a `'boolean'`, non-null values are promoted to `Boolean` type. This is seldom required,
 * but can be useful if a field value is received as a number but should be treated as a boolean.
 * @extends Core/data/field/DataField
 * @classtype boolean
 */

class BooleanDataField extends DataField {
  static get type() {
    return 'boolean';
  }

  static get alias() {
    return 'bool';
  }

  static get prototypeProperties() {
    return {
      /**
       * The value to replace `null` when the field is not `nullable`.
       * @config {Boolean}
       * @default
       */
      nullValue: false
    };
  }

  convert(value) {
    return value == null ? this.nullable ? value : this.nullValue : Boolean(value);
  }

}
BooleanDataField.initClass();
BooleanDataField._$name = 'BooleanDataField';

/**
 * @module Core/data/field/DateDataField
 */

/**
 * This field class handles field of type `Date`.
 * ```
 * class Person extends Model {
 *     static get fields() {
 *         return [
 *             'name',
 *             { name : 'birthday', type : 'date', format : 'YYYY-MM-DD' },
 *             { name : 'age', readOnly : true }
 *         ];
 *     }
 * }
 * ```
 * When a field is declared as a `'date'`, non-null values are promoted to `Date` type. This is frequently needed due
 * to how date types are serialized to JSON strings.
 *
 * Date fields can have a special `defaultValue` of `'now'` which will convert to the current date/time.
 * @extends Core/data/field/DataField
 * @classtype date
 */

class DateDataField extends DataField {
  static get type() {
    return 'date';
  }

  static get prototypeProperties() {
    return {
      /**
       * The format of the date field. *Deprecated*: use {@link #config-format} instead.
       *
       * @config {String} dateFormat
       * @deprecated Use {@link #config-format} instead.
       */

      /**
       * The format of the date field.
       *
       * See {@link Core.helper.DateHelper DateHelper} for details.
       * @config {String} format
       * @default DateHelper.defaultFormat
       */
      format: null
    };
  }

  get dateFormat() {
    return this.format;
  }

  set dateFormat(v) {
    VersionHelper.deprecate('Core', '5.0.0', `DateDataField "${this.name}": dateFormat config has been renamed to "format"`);
    this.format = v;
  }

  convert(value) {
    if (value == null) {
      if (!this.nullable) {
        value = this.nullValue;
      }
    } else if (value === 'now') {
      value = new Date();
    } else if (!(value instanceof Date)) {
      // Use configured format, if null/undefined use DateHelpers default format
      value = DateHelper.parse(value, this.format || DateHelper.defaultParseFormat); // if parsing has failed, we would like to return `undefined` to indicate the "absence" of data
      // instead of `null` (presence of "empty" data)

      value = value || undefined;
    }

    return value;
  }

  serialize(value) {
    if (value instanceof Date) {
      // Use configured format or DateHelpers default one
      value = DateHelper.format(value, this.format || DateHelper.defaultFormat);
    }

    return value;
  }

  printValue(value) {
    return DateHelper.format(value, this.format || DateHelper.defaultFormat);
  }

}
DateDataField.initClass();
DateDataField._$name = 'DateDataField';

/**
 * @module Core/data/field/IntegerDataField
 */

/**
 * This field class handles field of type `Number` with no decimal digits.
 * ```
 * class Person extends Model {
 *     static get fields() {
 *         return [
 *             'name',
 *             { name : 'age', type : 'int' }
 *         ];
 *     }
 * }
 * ```
 * When a field is declared as a `'int'`, non-null values are promoted to `Number` type and decimals are removed using
 * a specified `rounding`. This field type can be useful if a field value is received as a string but should be stored
 * as a number or has a fractional component that must be rounded or truncated.
 * @extends Core/data/field/DataField
 * @classtype integer
 */

class IntegerDataField extends DataField {
  static get type() {
    return 'integer';
  }

  static get alias() {
    return 'int';
  }

  static get prototypeProperties() {
    return {
      /**
       * The value to replace `null` when the field is not `nullable`.
       * @config {Number}
       * @default
       */
      nullValue: 0,

      /**
       * The `Math` method to use to ensure fractional component is removed.
       * This can be `'round'` (the default value), `'floor'` or `'ceil'`.
       * @config {String}
       * @default
       */
      rounding: 'round'
    };
  }

  convert(value) {
    return value == null ? this.nullable ? value : this.nullValue : Math[this.rounding](Number(value));
  }

}
IntegerDataField.initClass();
IntegerDataField._$name = 'IntegerDataField';

/**
 * @module Core/data/field/ModelDataField
 */

/**
 * This field class handles fields that hold other records.
 * ```
 * class Person extends Model {
 *     static get fields() {
 *         return [
 *             'name',
 *             { name : 'address', type : 'model' }
 *         ];
 *     }
 * }
 * ```
 * @internal
 * @extends Core/data/field/DataField
 * @classtype model
 */

class ModelDataField extends DataField {
  static get type() {
    return 'model';
  }

  static get prototypeProperties() {
    return {
      complexMapping: true
    };
  }

}
ModelDataField.initClass();
ModelDataField._$name = 'ModelDataField';

/**
 * @module Core/data/field/NumberDataField
 */

/**
 * This field class handles field of type `Number`.
 * ```
 * class Person extends Model {
 *     static get fields() {
 *         return [
 *             'name',
 *             { name : 'age', type : 'number' }
 *         ];
 *     }
 * }
 * ```
 * When a field is declared as a `'number'`, non-null values are promoted to `Number` type. This is seldom required, but
 * can be useful if a field value is received as a string but should be treated as a number.
 * @extends Core/data/field/DataField
 * @classtype number
 */

class NumberDataField extends DataField {
  static get type() {
    return 'number';
  }

  static get alias() {
    return 'float';
  }

  static get prototypeProperties() {
    return {
      /**
       * The value to replace `null` when the field is not `nullable`.
       * @config {Number}
       * @default
       */
      nullValue: 0,

      /**
       * The numeric precision of this field. Values are rounded to the specified number of digits. If `null`,
       * the default, no rounding is performed.
       * @config {Number}
       * @default
       */
      precision: null
    };
  }

  convert(value) {
    if (value == null) {
      return this.nullable ? value : this.nullValue;
    }

    value = Number(value);
    let scale = this.precision;

    if (scale) {
      scale = 10 ** scale;
      value = Math.round(value * scale) / scale;
    } else if (scale === 0) {
      value = Math.round(value);
    }

    return value;
  }

}
NumberDataField.initClass();
NumberDataField._$name = 'NumberDataField';

/**
 * @module Core/data/field/ObjectDataField
 */

/**
 * This field class handles fields that hold an object.
 * ```
 * class Person extends Model {
 *     static get fields() {
 *         return [
 *             'name',
 *             { name : 'address', type : 'object' }
 *         ];
 *     }
 * }
 * ```
 * @internal
 * @extends Core/data/field/DataField
 * @classtype object
 */

class ObjectDataField extends DataField {
  static get type() {
    return 'object';
  }

  static get prototypeProperties() {
    return {
      complexMapping: true
    };
  }

}
ObjectDataField.initClass();
ObjectDataField._$name = 'ObjectDataField';

/**
 * @module Core/data/field/StringDataField
 */

/**
 * This field class handles field of type `String`.
 * ```
 * class Person extends Model {
 *     static get fields() {
 *         return [
 *             { name : 'name', type : 'string' }
 *         ];
 *     }
 * }
 * ```
 * When a field is declared as a `'string'`, non-null values are promoted to `String` type. This is seldom required, but
 * can be useful if a field value is received as a number but should be treated as a string.
 * @extends Core/data/field/DataField
 * @classtype string
 */

class StringDataField extends DataField {
  static get type() {
    return 'string';
  }

  static get prototypeProperties() {
    return {
      /**
       * The value to replace `null` when the field is not `nullable`.
       * @config {String}
       * @default
       */
      nullValue: ''
    };
  }

  convert(value) {
    return value == null ? this.nullable ? value : this.nullValue : String(value);
  }

}
StringDataField.initClass();
StringDataField._$name = 'StringDataField';

const {
  defineProperty: defineProperty$3
} = Reflect,
      {
  hasOwnProperty: hasOwnProperty$5
} = Object.prototype,
      _undefined = undefined,
      internalProps = {
  children: 1,
  data: 1,
  meta: 1
},
      abbreviationFields = ['name', 'title', 'text', 'label', 'description'];
/**
 * @module Core/data/Model
 */

/**
 * A Model is a definition for a record in a store. It defines which fields the data contains and exposes an interface
 * to access and manipulate that data.
 *
 * Models are created from json objects, the input json is stored in `Model#data`. By default it stores a shallow copy of
 * the raw json, but for records in stores configured with `useRawData: true` it stores the supplied json object as is.
 *
 * ## Defining Fields
 * A Model can either define its fields explicitly (see {@link #property-fields-static}) or have them created from its
 * data (see {@link #property-autoExposeFields-static}). This snippet shows a model with 4 fields defined explicitly:
 *
 * ```javascript
 * class Person extends Model {
 *     static get fields() {
 *         return [
 *             'name',
 *             { name : 'birthday', type : 'date', format : 'YYYY-MM-DD' },
 *             { name : 'shoeSize', type : 'number', defaultValue : 11 },
 *             { name : 'age', readOnly : true }
 *         ];
 *     }
 * }
 * ```
 *
 * The first field (name) has an unspecified type, which means the field's value is held as received with no conversion
 * applied. The second field (birthday) is defined to be a date, which will make the model parse any supplied value into
 * an actual date. The parsing is handled by {@link Core/helper/DateHelper#function-parse-static DateHelper.parse()}
 * using the specified `format`, or if no format is specified using
 * {@link Core/helper/DateHelper#property-defaultFormat-static DateHelper.defaultFormat}.
 *
 * The set of standard field types is as follows:
 *
 *  - {@link Core.data.field.BooleanDataField `boolean`}
 *  - {@link Core.data.field.DateDataField `date`}
 *  - {@link Core.data.field.IntegerDataField `integer`}
 *  - {@link Core.data.field.NumberDataField `number`}
 *  - {@link Core.data.field.StringDataField `string`}
 *
 * You can also set a `defaultValue` that will be used if the data does not contain a value for the field:
 *
 * ```javascript
 * { name : 'shoeSize', type : 'number', defaultValue : 11 }
 * ```
 *
 * To create a record from a Model, supply data to its constructor:
 *
 * ```javascript
 * let guy = new Person({
 *   id       : 1,
 *   name     : 'Dude',
 *   birthday : '2014-09-01'
 * });
 * ```
 *
 * If no id is specified, a temporary id will be generated.
 *
 * Please avoid using reserved names for your fields (such as `parent`, `children` and others that are used as Model properties) to
 * avoid possible data collisions and bugs.
 *
 * ## Persisting Fields
 * By default all fields are persisted. If you don't want particular field to get saved to the server, configure it with
 * `persist: false`. In this case field will not be among changes which are sent by
 * {@link Core/data/AjaxStore#function-commit store.commit()}, otherwise its behavior doesn't change.
 *
 * ```javascript
 * class Person extends Model {
 *     static get fields() {
 *         return [
 *             'name',
 *             { name : 'age', persist : false }
 *         ];
 *     }
 * }
 * ```
 *
 * ## The `idField`
 * By default Model expects its id to be stored in a field named "id". The name of the field can be customized by
 * setting {@link #property-idField-static}:
 *
 * ```javascript
 * class Person extends Model {
 *     static get fields() {
 *         return [
 *             'name',
 *             { name : 'age', persist : false },
 *             { name : 'personId' },
 *             { name : 'birthday', type : 'date' }
 *         ];
 *     }
 * }
 *
 * // Id drawn from 'id' property by default; use custom field here
 * Person.idField = 'personId';
 *
 * let girl = new Person({
 *    personId : 2,
 *    name     : 'Lady',
 *    birthday : '2011-11-05'
 * });
 * ```
 *
 * ## Getting and Setting Values
 * Fields are used to generate getters and setters on the records. Use them to access or modify values (they are
 * reactive):
 *
 * ```javascript
 * console.log(guy.name);
 * girl.birthday = new Date(2011,10,6);
 * ```
 *
 * NOTE: In an application with multiple different models you should subclass Model, since the prototype is decorated
 * with getters and setters. Otherwise you might get unforeseen collisions.
 *
 * ## Field Data Mapping
 * By default fields are mapped to data using their name. If you for example have a "name" field it expects data to be
 * `{ name: 'Some name' }`. If you need to map it to some other property, specify `dataSource` in your field definition:
 *
 * ```javascript
 * class Person extends Model {
 *     static get fields {
 *         return [
 *             { name : 'name', dataSource : 'TheName' }
 *         ];
 *     }
 * }
 *
 * // This is now OK:
 * let dude = new Person({ TheName : 'Manfred' });
 * console.log(dude.name); // --> Manfred
 * ```
 * ## Field Inheritance
 * Fields declared in a derived model class are added to those from its superclass. If a field declared by a derived
 * class has also been declared by its super class, the field properties of the super class are merged with those of
 * the derived class.
 *
 * For example:
 * ```javascript
 *  class Person extends Model {
 *      static get fields() {
 *          return [
 *              'name',
 *              { name : 'birthday', type : 'date', format : 'YYYY-MM-DD' }
 *          ];
 *      }
 *  }
 *
 *  class User extends Person {
 *      static get fields() {
 *          return [
 *              { name : 'birthday', dataSource : 'dob' },
 *              { name : 'lastLogin', type : 'date' }
 *          ];
 *      }
 *  }
 * ```
 * In the above, the `Person` model declares the `birthday` field as a `date` with a specified `format`. The `User`
 * model extends `Person` and also declares the `birthday` field. This redeclared field only specifies `dataSource`, so
 * all of the other fields are preserved from `Person`. The `User` model also adds a `lastLogin` field.
 *
 * The `User` from above could have been declared like so to achieve the same `fields`:
 * ```javascript
 *  class User extends Model {
 *      static get fields() {
 *          return [
 *              'name',
 *              { name : 'birthday', type : 'date', format : 'YYYY-MM-DD', dataSource : 'dob' },
 *              { name : 'lastLogin', type : 'date' }
 *          ];
 *      }
 *  }
 * ```
 *
 * ## Override default values
 *
 * In case you need to define default value for a specific field, or override an existing default value, you can
 * define a new or re-define an existing field definition in {@link #property-fields-static} static getter:
 *
 * ```javascript
 * class Person extends Model {
 *     static get fields() {
 *         return [
 *             { name : 'username', defaultValue : 'New person' },
 *             { name : 'birthdate', type : 'date' }
 *         ];
 *     }
 * }
 *
 * class Bot extends Person {
 *     static get fields() {
 *         return [
 *             { name : 'username', defaultValue : 'Bot' } // default value of 'username' field is overridden
 *         ];
 *     }
 * }
 * ```
 *
 * ## Tree API
 * This class mixes in the {@link Core/data/mixin/TreeNode TreeNode} mixin which provides an API for tree related functionality (only relevant if your
 * store is configured to be a {@link Core/data/Store#config-tree tree}).
 *
 * @mixes Core/data/mixin/TreeNode
 * @mixes Core/data/stm/mixin/ModelStm
 */

class Model extends Base.mixin(ModelStm, TreeNode) {
  static get $name() {
    return 'Model';
  }

  static get declarable() {
    return [
    /**
     * Array of defined fields for this model class. Subclasses add new fields by implementing this static
     * getter:
     *
     * ```javascript
     * // Model defining two fields
     * class Person extends Model {
     *     static get fields() {
     *         return [
     *             { name : 'username', defaultValue : 'New person' },
     *             { name : 'birthdate', type : 'date' }
     *         ];
     *     }
     * }
     *
     * // Subclass overriding one of the fields
     * class Bot extends Person {
     *     static get fields() {
     *         return [
     *             // Default value of 'username' field is overridden, any other setting from the parents
     *             // definition is preserved
     *             { name : 'username', defaultValue : 'Bot' }
     *         ];
     *     }
     * }
     * ```
     *
     * Fields in a subclass are merged with those from the parent class, making it easy to override mappings,
     * formats etc.
     *
     * @member {Object[]|Core.data.field.DataField[]} fields
     * @readonly
     * @static
     * @category Fields
     */
    'fields'];
  }

  static get fields() {
    return [// The index of this item in its parent (respects filtering)
    {
      name: 'parentIndex',
      type: 'number',
      persist: false
    }];
  }
  /**
   * Template static getter which is supposed to be overridden to define default field values for the Model class.
   * Overrides `defaultValue` config specified by the {@link #property-fields-static} getter.
   * Returns a named object where key is a field name and value is a default value for the field.
   *
   * NOTE: This is a legacy way of defining default values, we recommend using {@link #property-fields-static} moving
   * forward.
   *
   * ```javascript
   * class Person extends Model {
   *     static get fields() {
   *         return [
   *             { name : 'username', defaultValue : 'New person' }
   *         ];
   *     }
   * }
   *
   * class Bot extends Person {
   *     static get defaults() {
   *         return {
   *             username : 'Bot' // default value of 'username' field is overridden
   *         };
   *     }
   * }
   * ```
   *
   * @member {Object} defaults
   * @static
   * @category Fields
   */
  // TODO: deprecate in 5.0 in favor of static fields getter
  // static get defaults() {
  //     return {};
  // }

  /**
   * The name of the data field which provides the ID of instances of this Model.
   * @property {String}
   * @category Fields
   */

  static set idField(idField) {
    this._assignedIdField = true;
    this._idField = idField;
  }

  static get idField() {
    return this._idField;
  }
  /**
   * The name of the data field which holds children of this Model when used in a tree structure
   * ```javascript
   * MyModel.childrenField = 'kids';
   * const parent = new MyModel({
   *   name : 'Dad',
   *   kids : [
   *     { name : 'Daughter' },
   *     { name : 'Son' }
   *   ]
   * });
   * ```
   * @property {String}
   * @category Fields
   */

  static set childrenField(childrenField) {
    this._childrenField = childrenField;
  }

  static get childrenField() {
    return this._childrenField || 'children';
  }
  /**
   * Returns index path to this node. This is the index of each node in the node path
   * starting from the topmost parent. (only relevant when its part of a tree store).
   * @returns {Number[]} The index of each node in the path from the topmost parent to this node.
   * @category Parent & children
   * @private
   */

  get indexPath() {
    const indices = [];
    let node = this,
        depth = node.childLevel;

    for (node = this; node && !node.isRoot; node = node.parent) {
      indices[depth--] = node.parentIndex + 1;
    }

    return indices;
  }
  /**
   * Unique identifier for the record. Might be mapped to another dataSource using idField, but always exposed as
   * record.id. Will get a generated value if none is specified in records data.
   * @member {String|Number} id
   * @category Identification
   */
  //region Init

  /**
   * Constructs a new record from the supplied data.
   * @param {Object} [data] Raw data
   * @param {Core.data.Store} [store] Data store
   * @param {Object} [meta] Meta data
   * @function constructor
   * @category Lifecycle
   */

  construct(data = {}, store = null, meta = null, skipExpose = false) {
    const me = this,
          stores = store ? Array.isArray(store) ? store : [store] : [],
          {
      constructor,
      fieldMap
    } = me; // null passed to Base construct inhibits config processing.

    let configs = null;
    store = stores[0];
    me.meta = {
      modified: {},
      ...constructor.metaConfig,
      ...meta
    }; // Should apply configs?

    if (constructor.applyConfigs) {
      // Extract from data and combine with defaultConfigs
      for (const key in me.$meta.config) {
        if (!configs) {
          // if (first config)
          configs = {};

          if (!me.useRawData || !me.useRawData.enabled) {
            // Shallow copy of data to not mutate incoming object
            data = { ...data
            };
          }
        }

        if (key in data) {
          // Use as config
          configs[key] = data[key]; // Remove from data

          delete data[key];
        }
      }
    }

    super.construct(configs); // make getters/setters for fields, needs to be done before processing data to make sure defaults are available

    if (!skipExpose) {
      constructor.exposeProperties(data);
    } // It's only valid to do this once, on construction of the first instance

    if (!hasOwnProperty$5.call(constructor, 'idFieldProcessed')) {
      // idField can be overridden from meta, or from the store if we have not had an idField set programmatically
      // and if we have not had an id field defined above the base Model class level.
      let overriddenIdField = me.meta.idField;

      if (!overriddenIdField) {
        // Might have been set to Model after construction but before load
        if (constructor._assignedIdField) {
          overriddenIdField = constructor.idField;
        } // idField on store was deprecated, but should still work to not break code
        // TODO: Remove in 3.0? Or reintroduce it...
        else if (store) {
            overriddenIdField = store.idField;
          }
      } // If it's overridden to something different than we already have, replace the 'id' field in the fieldMap

      if (overriddenIdField && overriddenIdField !== fieldMap.id.dataSource) {
        constructor.addField({
          name: 'id',
          dataSource: overriddenIdField
        });
      }

      constructor.idFieldProcessed = true;
    } // assign internalId, unique among all records

    me._internalId = Model._internalIdCounter++; // relation code expects store to be available for relation lookup, but actual join done below

    me.stores = [];
    me.unjoinedStores = []; // Superclass constructors may set this in their own way before this is called.

    if (!me.originalData) {
      me.originalData = data;
    }

    me.data = constructor.processData(data, false, store, me); // Consider undefined and null as missing id and generate one

    if (me.id == null) {
      // Assign a generated id silently, record should not be considered modified
      me.setData('id', me.generateId(store));
    }

    if (me.data[constructor.childrenField]) {
      me.processChildren(stores);
    }

    me.generation = 0;
  }
  /**
   * Compares this Model instance to the passed instance. If they are of the same type, and all fields
   * (except, obviously, `id`) are equal, this returns `true`.
   * @param {Core.data.Model} other The record to compare this record with.
   * @returns {Boolean} `true` if the other is of the same class and has all fields equal.
   */

  equals(other) {
    if (other instanceof this.constructor) {
      for (let fields = this.$meta.fields.defs, i = 0, {
        length
      } = fields; i < length; i++) {
        const field = fields[i],
              {
          name
        } = field;

        if (name !== 'id' && !field.isEqual(this[name], other[name])) {
          return false;
        }
      }

      return true;
    }

    return false;
  }

  get subclass() {
    return new this.constructor(Object.setPrototypeOf({
      id: _undefined
    }, this.data), this.stores[0], null, true);
  }
  /**
   * Processes raw data, converting values and setting defaults.
   * @private
   * @param {Object} data Raw data
   * @param {Boolean} [ignoreDefaults] Ignore setting default values, used when updating
   * @returns {Object} Processed data
   * @category Fields
   */

  static processData(data, ignoreDefaults = false, store, record) {
    const {
      fieldMap,
      defaultValues
    } = this,
          {
      useRawData = {
        enabled: false
      }
    } = store || {},
          // Store configured with useRawData uses the supplied data object, polluting it. When not configured with
    // useRawData it instead makes a shallow copy.
    processed = useRawData.enabled ? data : { ...data
    };
    let fieldName;
    ignoreDefaults = ignoreDefaults || useRawData.disableDefaultValue;

    if (!ignoreDefaults) {
      for (fieldName in defaultValues) {
        if (processed[fieldName] === _undefined) {
          processed[fieldName] = defaultValues[fieldName];
        }
      }
    }

    if (!useRawData.disableTypeConversion) {
      // Convert field types which need converting
      for (fieldName in fieldMap) {
        const field = fieldMap[fieldName],
              {
          name,
          dataSource
        } = field,
              // Value might have been supplied either using mapped dataSource (when loading JSON etc. for example
        // event.myStartDate) or as field name (from internal code, for example event.startDate). If [name]
        // exists but not [dataSource], use it.
        hasSource = dataSource !== name,
              sourceExists = hasSource && ObjectHelper.pathExists(data, dataSource),
              useNameForValue = name in data && (!hasSource || !sourceExists),
              convert = !useRawData.disableTypeConversion && field.convert; // Only action field definitions which have a convert function or remap data

        if (useNameForValue || convert) {
          // When ignoringDefaults, do not convert unspecified values
          if (!ignoreDefaults || useNameForValue || sourceExists) {
            const value = useNameForValue ? processed[name] : ObjectHelper.getPath(processed, dataSource);
            ObjectHelper.setPath(processed, dataSource, convert ? field.convert(value, record) : value); // Remove [startDate] from internal data holder, only keeping [myStartDate]

            if (hasSource) {
              delete processed[name];
            }
          }
        }
      }
    }

    return processed;
  }

  static setupClass(meta) {
    super.setupClass(meta);

    if (!meta.fields) {
      // Normally setupFields will only run when a Model defines a fields getter, but we want to always run it:
      this.setupFields(this, meta);
    }
  }

  static setupFields(cls, meta) {
    var _base$defs$slice, _base$exposed, _base$ordinals, _base$map, _base$sources;

    const classFields = hasOwnProperty$5.call(cls, 'fields') && cls.fields,
          base = meta.super.fields,
          fieldsInfo = meta.fields = {
      defs: (_base$defs$slice = base === null || base === void 0 ? void 0 : base.defs.slice()) !== null && _base$defs$slice !== void 0 ? _base$defs$slice : [],
      // Set to true when an instance's data object is run through exposeProperties
      exposedData: false,
      // These objects are all keyed by field name:
      defaults: base ? { ...base.defaults
      } : {},
      // value=field.defaultValue
      exposed: Object.create((_base$exposed = base === null || base === void 0 ? void 0 : base.exposed) !== null && _base$exposed !== void 0 ? _base$exposed : null),
      // value=true if we've done defineProperty
      ordinals: Object.create((_base$ordinals = base === null || base === void 0 ? void 0 : base.ordinals) !== null && _base$ordinals !== void 0 ? _base$ordinals : null),
      // value=index in the defs array
      map: Object.create((_base$map = base === null || base === void 0 ? void 0 : base.map) !== null && _base$map !== void 0 ? _base$map : null),
      // value=definition object
      sources: Object.create((_base$sources = base === null || base === void 0 ? void 0 : base.sources) !== null && _base$sources !== void 0 ? _base$sources : null) // value=source definition object

    }; // We use Object.create(null) as the base for these maps because some models declare "constructor" as a field
    // NOTE: instead of chaining the defaults, we copy them so the defaults object can be used with Object.assign
    // in other contexts (since it does not copy inherited properties from the prototype chain)
    // Clone the superclass's defaults, and override that with our own defaults.
    // As we find fields with a defaultValue, more defaults may be added

    if (hasOwnProperty$5.call(cls, 'defaults')) {
      Object.assign(fieldsInfo.defaults, cls.defaults);
    } // Hook up our field maps with the class hierarchy's fieldMaps.
    // We need to be able to look up field definitions by the name, or by the dataSource property name
    // If the idField is overridden at this level, create a new field

    if (hasOwnProperty$5.call(cls, 'idField')) {
      cls.addField({
        name: 'id',
        dataSource: cls.idField
      });
      fieldsInfo.exposed[cls.idField] = true;
    } // Process fields defined in the class definition

    if (classFields !== null && classFields !== void 0 && classFields.length) {
      classFields.map(cls.addField, cls);
    }

    cls.exposeRelations();
  }

  static get defaultValues() {
    return this.$meta.fields.defaults;
  }
  /**
   * An object containing all the _defined_ fields for this Model class. This will include all superclass's
   * defined fields through its prototype chain. So be aware that `Object.keys` and `Object.entries` will only
   * access this class's defined fields.
   * @property {Object}
   * @static
   * @readonly
   * @category Fields
   */

  static get fieldMap() {
    return this.$meta.fields.map;
  }
  /**
   * Same as {@link #property-fieldMap-static}.
   * @property {Object}
   * @readonly
   * @category Fields
   */

  get fieldMap() {
    return this.$meta.fields.map;
  }

  static get fieldDataSourceMap() {
    return this.$meta.fields.sources;
  }
  /**
   * Makes getters and setters for fields (from definitions and data). Called once when class is defined and once when
   * data is loaded first time.
   * @internal
   * @param {Object} [data] Raw data
   * @category Fields
   */

  static exposeProperties(data) {
    const me = this,
          fieldsInfo = me.$meta.fields; // Process the raw data properties and expose them as fields unless the property name
    // has already been used by the "dataSource" of a defined field.

    if (data && me.autoExposeFields && !fieldsInfo.exposedData) {
      let dataProperty, fieldDef, type;

      for (dataProperty in data) {
        if (!fieldsInfo.exposed[dataProperty]) {
          type = typeof data[dataProperty]; // Create a field definition in our fieldMap with the flag that it's from data

          fieldDef = {
            name: dataProperty,
            dataSource: dataProperty,
            fromData: true
          };

          if (type === 'boolean' || type === 'number') {
            fieldDef.type = type;
          }

          me.addField(fieldDef);
        }
      }

      fieldsInfo.exposedData = true;
    }

    me.exposeRelations();
  }
  /**
   * Add a field definition in addition to those predefined in `fields`.
   * @param {String|Object} fieldDef A field name or definition
   */

  static addField(fieldDef) {
    if (fieldDef == null) {
      return;
    }

    if (typeof fieldDef === 'string') {
      fieldDef = {
        name: fieldDef
      };
    }

    const me = this.initClass(),
          fieldsInfo = me.$meta.fields,
          {
      ordinals
    } = fieldsInfo,
          propertiesExposed = fieldsInfo.exposed,
          {
      name
    } = fieldDef,
          existing = fieldsInfo.map[name],
          dataSource = fieldDef.dataSource || (fieldDef.dataSource = name);
    let field, key;

    if (!existing || fieldDef.type && fieldDef.type !== existing.type) {
      field = DataField.create(fieldDef);
      field.definedBy = existing ? existing.definedBy : me;
      field.ordinal = existing ? existing.ordinal : ordinals[name] = fieldsInfo.defs.length;
    } else {
      field = Object.create(existing);

      for (key in fieldDef) {
        if (key !== 'type') {
          field[key] = fieldDef[key];
        }
      }
    }

    field.owner = me;
    fieldsInfo.defs[field.ordinal] = field;
    fieldsInfo.map[name] = field;

    if (!fieldsInfo.sources[dataSource]) {
      fieldsInfo.sources[dataSource] = field;
    } // With complex mapping avoid exposing object as model field

    if (dataSource.includes('.')) {
      field.complexMapping = true;
    }

    if (field.complexMapping) {
      // model fields have this set on their prototype...
      propertiesExposed[dataSource.split('.')[0]] = true;
    } else {
      // When iterating through the raw data, if autoExposeFields is set
      // We do not need to create properties for raw property names we've processed here
      propertiesExposed[dataSource] = true;
    } // Maintain an object of defaultValues for fields.

    if ('defaultValue' in field) {
      fieldsInfo.defaults[dataSource] = field.defaultValue;
    } // Create a property on this Model's prototype, named for the defined field name
    // which reads the correct property out of the raw data object.

    if (!internalProps[name]) {
      field.defineAccessor(me.prototype);
    }

    return field;
  }
  /**
   * Remove a field definition by name.
   * @param {String} fieldName Field name
   */

  static removeField(fieldName) {
    const me = this.initClass(),
          fieldsInfo = me.$meta.fields,
          definition = fieldsInfo.map[fieldName],
          {
      ordinals
    } = fieldsInfo,
          index = ordinals[fieldName];

    if (definition) {
      fieldsInfo.defs.splice(index, 1);
      delete ordinals[fieldName];
      delete fieldsInfo.defaults[fieldName];
      delete fieldsInfo.exposed[fieldName];
      delete fieldsInfo.map[fieldName];
      delete fieldsInfo.sources[definition.dataSource];

      for (const name in ordinals) {
        if (ordinals[name] > index) {
          --ordinals[name];
        }
      } // Note: if field was exposed by superclass, this won't do anything...

      delete me.prototype[fieldName];
    }
  }
  /**
   * Makes getters and setters for related records. Populates a Model#relation array with the relations, to allow it
   * to be modified later when assigning stores.
   * @internal
   * @category Relations
   */

  static exposeRelations() {
    const me = this;

    if (hasOwnProperty$5.call(me, 'relationsExposed')) {
      return;
    }

    if (me.relationConfig) {
      me.relationsExposed = true;
      me.relations = [];
      me.relationConfig.forEach(relation => {
        me.relations.push(relation);
        const name = relation.relationName; // getter and setter for related object

        if (!Reflect.ownKeys(me.prototype).includes(name)) {
          defineProperty$3(me.prototype, name, {
            enumerable: true,
            get: function () {
              // noinspection JSPotentiallyInvalidUsageOfClassThis
              return this.getForeign(name);
            },
            set: function (value) {
              // noinspection JSPotentiallyInvalidUsageOfClassThis
              this.setForeign(name, value, relation);
            }
          });
        }
      });
    }
  } //endregion
  //region Fields

  /**
   * Flag checked from Store when loading data that determines if fields found in first records should be exposed in
   * same way as predefined fields.
   * @property {Boolean}
   * @category Fields
   */

  static get autoExposeFields() {
    return true;
  }
  /**
   * Convenience getter to get field definitions from class.
   * @property {Core.data.field.DataField[]}
   * @readonly
   * @category Fields
   */

  get fields() {
    return this.$meta.fields.defs;
  }
  /**
   * Convenience function to get the definition for a field from class.
   * @param {String} fieldName Field name
   * @returns {Core.data.field.DataField}
   * @category Fields
   */

  getFieldDefinition(fieldName) {
    return this.$meta.fields.map[fieldName];
  }

  getFieldDefinitionFromDataSource(dataSource) {
    return this.$meta.fields.sources[dataSource];
  }
  /**
   * Get the names of all fields in data.
   * @property {String[]}
   * @readonly
   * @category Fields
   */

  get fieldNames() {
    return Object.keys(this.data);
  }
  /**
   * Get the definition for a field by name. Caches results.
   * @param {String} fieldName Field name
   * @returns {Core.data.field.DataField} Field definition or null if none found
   * @category Fields
   */

  static getFieldDefinition(fieldName) {
    return this.$meta.fields.map[fieldName];
  }
  /**
   * Returns dataSource configuration for a given field name
   * @param {String} fieldName
   * @returns {String} Field `dataSource` mapping
   * @internal
   */

  static getFieldDataSource(fieldName) {
    return this.getFieldDefinition(fieldName).dataSource;
  }
  /**
   * Get the data source used by specified field. Returns the fieldName if no data source specified.
   * @param {String} fieldName Field name
   * @returns {String}
   * @category Fields
   */

  getDataSource(fieldName) {
    const def = this.constructor.getFieldDefinition(fieldName);
    return (def === null || def === void 0 ? void 0 : def.dataSource) || (def === null || def === void 0 ? void 0 : def.name);
  }
  /**
   * Processes input to a field, converting to expected type.
   * @param {String} fieldName Field dataSource
   * @param {*} value Value to process
   * @returns {*} Converted value
   * @category Fields
   */

  static processField(fieldName, value, record) {
    const field = this.fieldMap[fieldName];
    return field !== null && field !== void 0 && field.convert ? field.convert(value, record) : value;
  } //endregion
  //region Relations

  /**
   * Initializes model relations. Called from store when adding a record.
   * @private
   * @category Relations
   */

  initRelations() {
    const me = this,
          relations = me.constructor.relations;
    if (!relations) return; // TODO: feels strange to have to look at the store for relation config but didn't figure out anything better.
    // TODO: because other option would be to store it on each model instance, not better...

    me.stores.forEach(store => {
      var _store$modelRelations;

      if (!store.modelRelations) {
        store.initRelations();
      } // TODO: not at all tested for multiple stores, can't imagine it works as is

      const relatedRecords = [];
      (_store$modelRelations = store.modelRelations) === null || _store$modelRelations === void 0 ? void 0 : _store$modelRelations.forEach(config => {
        relatedRecords.push({
          related: me.initRelation(config),
          config
        });
      });
      store.updateRecordRelationCache(me, relatedRecords);
    });
  }
  /**
   * Initializes/updates a single relation.
   * @param config Relation config
   * @returns {Core.data.Model} Related record
   * @private
   * @category Relations
   */

  initRelation(config) {
    const me = this,
          keyValue = me.get(config.fieldName),
          foreign = keyValue !== _undefined && typeof config.store !== 'string' && config.store.getById(keyValue),
          placeHolder = {
      id: keyValue,
      placeHolder: true
    };

    if (!me.meta.relationCache) {
      me.meta.relationCache = {};
    } // apparently scheduler tests expect cache to work without matched related record, thus the placeholder

    me.meta.relationCache[config.relationName] = foreign || (keyValue != null ? placeHolder : null);
    return foreign;
  }

  removeRelation(config) {
    // (have to check for existence before deleting to work in Safari)
    if (this.meta.relationCache[config.relationName]) {
      delete this.meta.relationCache[config.relationName];

      if (config.nullFieldOnRemove) {
        // Setting to null silently, to not trigger additional relation behaviour
        this.setData(config.fieldName, null);
      }
    }
  }

  getForeign(name) {
    var _this$meta$relationCa;

    return (_this$meta$relationCa = this.meta.relationCache) === null || _this$meta$relationCa === void 0 ? void 0 : _this$meta$relationCa[name];
  }

  setForeign(name, value, config) {
    const id = Model.asId(value);
    return this.set(config.fieldName, id);
  } //endregion
  //region Get/set values, data handling

  /**
   * Get value for specified field name. You can also use the generated getters if loading through a Store.
   * If model is currently in batch operation this will return updated batch values which are not applied to Model
   * until endBatch() is called.
   * @param {String} fieldName Field name to get value from
   * @returns {*} Fields value
   * @category Fields
   */

  get(fieldName) {
    const me = this,
          batchChanges = me.meta.batchChanges,
          {
      fieldMap
    } = me,
          recData = me.data;
    let field = fieldMap[fieldName];
    const dataSource = field ? field.dataSource : fieldName; // When changes are batched, they get stored by field name, not dataSource

    if (batchChanges && fieldName in batchChanges) {
      return batchChanges[fieldName];
    } // If changes are not batched, it should be resolved from the data using dataSource

    if (dataSource) {
      var _field;

      // Getting property of nested record?
      if (!field && fieldName.includes('.')) {
        const nestedName = fieldName.split('.')[0];
        field = fieldMap[nestedName];
      }

      if ((_field = field) !== null && _field !== void 0 && _field.complexMapping) {
        return ObjectHelper.getPath(recData, dataSource);
      }

      return dataSource in recData ? recData[dataSource] : recData[fieldName];
    }
  }
  /**
   * Internal function used to update a records underlying data block (record.data) while still respecting field
   * mappings. Needed in cases where a field needs setting without triggering any associated behaviour and it has a
   * dataSource with a different name.
   *
   * For example:
   * ```javascript
   * // startDate mapped to data.beginDate
   * { name : 'startDate', dataSource : 'beginDate' }
   *
   * // Some parts of our code needs to update the data block without triggering any of the behaviour associated with
   * // calling set. This would then not update "beginDate":
   * record.data.startDate = xx;
   *
   * // But this would
   * record.setData('startDate', xx);
   * ```
   * @internal
   * @category Editing
   */

  setData(toSet, value) {
    const {
      data,
      fieldMap
    } = this; // Two separate paths for performance reasons
    // setData('name', 'Quicksilver');

    if (typeof toSet === 'string') {
      var _field$dataSource;

      const field = fieldMap[toSet],
            dataSource = (_field$dataSource = field === null || field === void 0 ? void 0 : field.dataSource) !== null && _field$dataSource !== void 0 ? _field$dataSource : toSet;

      if (field !== null && field !== void 0 && field.complexMapping) {
        ObjectHelper.setPath(data, dataSource, value);
      } else {
        data[dataSource] = value;
      }
    } // setData({ name : 'Magneto', power : 'Magnetism' });
    else {
        const keys = Object.keys(toSet);

        for (let i = 0; i < keys.length; i++) {
          var _field$dataSource2;

          const fieldName = keys[i],
                field = fieldMap[fieldName],
                dataSource = (_field$dataSource2 = field === null || field === void 0 ? void 0 : field.dataSource) !== null && _field$dataSource2 !== void 0 ? _field$dataSource2 : fieldName;

          if (dataSource) {
            if (field !== null && field !== void 0 && field.complexMapping) {
              ObjectHelper.setPath(data, dataSource, toSet[fieldName]);
            } else {
              data[dataSource] = toSet[fieldName];
            }
          }
        }
      }
  }
  /**
   * Returns raw data from the encapsulated data object for the passed field name
   * @param {String} fieldName The field to get data for.
   * @returns {*} The raw data value for the field.
   */

  getData(fieldName) {
    var _field$dataSource3;

    const field = this.fieldMap[fieldName],
          dataSource = (_field$dataSource3 = field === null || field === void 0 ? void 0 : field.dataSource) !== null && _field$dataSource3 !== void 0 ? _field$dataSource3 : fieldName;

    if (dataSource) {
      if (field !== null && field !== void 0 && field.complexMapping) {
        return ObjectHelper.getPath(this.data, dataSource);
      }

      return this.data[dataSource];
    }
  }
  /**
   * Silently updates record's id with no flagging the property as modified.
   * Triggers onModelChange event for changed id.
   * @param {String|Number} value id value
   * @private
   */

  syncId(value) {
    const oldValue = this.id;

    if (oldValue !== value) {
      this.setData('id', value);
      const data = {
        id: {
          value,
          oldValue
        }
      };
      this.afterChange(data, data);
    }
  }
  /**
   * Set value for the specified field. You can also use the generated setters if loading through a Store.
   *
   * Setting a single field, supplying name and value:
   *
   * ```javascript
   * record.set('name', 'Clark');
   * ```
   *
   * Setting multiple fields, supplying an object:
   *
   * ```javascript
   * record.set({
   *    name : 'Clark',
   *    city : 'Metropolis'
   * });
   * ```
   *
   * @param {String|Object} field The field to set value for, or an object with multiple values to set in one call
   * @param {*} value Value to set
   * @param {Boolean} [silent] Set to true to not trigger events. If event is recurring, occurrences won't be updated automatically.
   * @fires Store#idChange
   * @fires Store#update
   * @fires Store#change
   * @category Editing
   */

  set(field, value, silent = false, fromRelationUpdate = false, skipAccessors = false) {
    const me = this; // We use beforeSet/inSet/afterSet approach here because mixin interested in overriding set() method
    // like STM, for example, might be mixed before Model class or after. In general I have no control over this.
    // STM mixed before, so the only option to wrap set() method body is actually to call
    // beforeSet()/afterSet().

    if (me.isBatchUpdating) {
      me.inBatchSet(field, value);
      return null;
    } else {
      var _me$afterSet;

      const preResult = me.beforeSet ? me.beforeSet(field, value, silent, fromRelationUpdate) : _undefined,
            wasSet = me.inSet(field, value, silent, fromRelationUpdate, skipAccessors);
      (_me$afterSet = me.afterSet) === null || _me$afterSet === void 0 ? void 0 : _me$afterSet.call(me, field, value, silent, fromRelationUpdate, preResult, wasSet);
      return wasSet;
    }
  }

  fieldToKeys(field, value) {
    if (typeof field !== 'string') {
      // will get in trouble when setting same field on multiple models without this
      return ObjectHelper.assign({}, field);
    }

    return {
      [field]: value
    };
  }

  inBatchSet(field, value) {
    const {
      meta,
      constructor
    } = this;
    let changed = false;

    if (typeof field !== 'string') {
      Object.keys(this.fieldToKeys(field, value)).forEach(key => {
        value = constructor.processField(key, field[key], this); // Store batch changes

        if (meta.batchChanges[key] !== value) {
          meta.batchChanges[key] = value;
          changed = true;
        }
      });
    } else {
      // Minor optimization for engine writing back a lot of changes
      if (meta.batchChanges[field] !== value) {
        meta.batchChanges[field] = value;
        changed = true;
      }
    } // Callers need to be able to detect changes

    if (changed) {
      this.generation++;
    }
  }

  inSet(field, value, silent, fromRelationUpdate, skipAccessors = false) {
    const me = this,
          {
      data,
      meta,
      fieldMap,
      constructor
    } = me,
          {
      prototype: myProto,
      childrenField
    } = constructor,
          wasSet = {},
          toSet = me.fieldToKeys(field, value),
          keys = Object.keys(toSet);
    let changed = false; // Give a chance to cancel action before records updated.

    if (!silent && !me.triggerBeforeUpdate(toSet)) {
      return null;
    }

    for (let i = 0; i < keys.length; i++) {
      var _field$dataSource4;

      const key = keys[i]; // Currently not allowed to set children in a TreeNode this way, will be ignored

      if (key === childrenField) {
        continue;
      }

      const field = fieldMap[key],
            cmp = field || ObjectHelper,
            readOnly = field === null || field === void 0 ? void 0 : field.readOnly,
            mapping = (_field$dataSource4 = field === null || field === void 0 ? void 0 : field.dataSource) !== null && _field$dataSource4 !== void 0 ? _field$dataSource4 : key,
            useProp = !skipAccessors && !field && key in myProto,
            oldValue = useProp ? me[mapping] : field !== null && field !== void 0 && field.complexMapping ? ObjectHelper.getPath(data, mapping) : data[mapping],
            value = constructor.processField(key, toSet[key], me),
            val = toSet[key] = {
        value
      },
            relation = me.getRelationConfig(key);

      if (!readOnly && !cmp.isEqual(oldValue, value)) {
        // Indicate to observers that data has changed.
        me.generation++;
        val.oldValue = oldValue;
        changed = true; // Update `modified` state which is used in sync request

        if (cmp.isEqual(me.meta.modified[key], value)) {
          // Remove changes if values are the same
          Reflect.deleteProperty(meta.modified, key);
        } else if (!me.ignoreBag) {
          // Private flag in engine, speeds initial commit up by not recording changes
          // Cache its original value
          if (!(key in meta.modified)) {
            meta.modified[key] = oldValue;
          }

          if (val.oldValue === _undefined) {
            Reflect.deleteProperty(val, 'oldValue');
          }
        } // The wasSet object keys must be the field *name*, not its dataSource.

        wasSet[key] = val;
        me.applyValue(useProp, mapping, value, skipAccessors, field); // changing foreign key

        if (relation && !fromRelationUpdate) {
          me.initRelation(relation);
          me.stores.forEach(store => store.cacheRelatedRecord(me, value, relation.relationName, val.oldValue));
        }
      }
    }

    if (changed) {
      me.afterChange(toSet, wasSet, silent, fromRelationUpdate, skipAccessors);
    }

    return changed ? wasSet : null;
  } // Provided as a hook for SchedulingEngine to do what needs to be done which ever way a field value is changed

  applyValue(useProp, key, value, skipAccessors, field) {
    var _field2;

    // If we don't have a field, but we have a property defined
    // eg, the fullDuration property defined in TaskModel, then
    // use the property
    if (useProp) {
      this[key] = value;
      return;
    } // Might be setting value of nested object

    if (!field && key.includes('.')) {
      const nestedName = key.split('.')[0];
      field = this.constructor.fieldMap[nestedName];
    } // Use complex mapping?

    if ((_field2 = field) !== null && _field2 !== void 0 && _field2.complexMapping) {
      ObjectHelper.setPath(this.data, key, value);
    } // Otherwise, push the value through into the data.
    else {
        this.data[key] = value;
      }
  } // skipAccessors argument is used in the engine override

  afterChange(toSet, wasSet, silent, fromRelationUpdate, skipAccessors) {
    this.stores.forEach(store => {
      store.onModelChange(this, toSet, wasSet, silent, fromRelationUpdate);
    });
  }

  get isPersistable() {
    return true;
  }
  /**
   * True if this model has any uncommitted changes.
   * @property {Boolean}
   * @readonly
   * @category Editing
   */

  get isModified() {
    return Boolean(this.meta.modified && Object.keys(this.meta.modified).length > 0);
  } // TODO: Make this the behaviour of isModified?

  get hasPersistableChanges() {
    return this.isPersistable && !ObjectHelper.isEmpty(this.rawModificationData);
  }
  /**
   * Returns true if this model has uncommitted changes for the provided field.
   * @param {String} fieldName Field name
   * @returns {Boolean} True if the field is changed
   */

  isFieldModified(fieldName) {
    return this.isModified && this.meta.modified[fieldName];
  }
  /**
   * Returns field value that should be persisted, or `undefined` if field is configured with `persist: false`.
   * @param {String|Core.data.field.DataField} nameOrField Name of the field to get value for, or its field definition
   * @private
   * @category Fields
   */

  getFieldPersistentValue(nameOrField) {
    const field = typeof nameOrField === 'string' ? this.getFieldDefinition(nameOrField) : nameOrField,
          name = (field === null || field === void 0 ? void 0 : field.name) || nameOrField;
    let result;

    if (!field || field.persist) {
      result = this[name]; // if serialize function is provided we use it to prepare the persistent value

      if (field !== null && field !== void 0 && field.serialize) {
        result = field.serialize(result, this);
      }
    }

    return result;
  }
  /**
   * Get a map of the modified fields in form of an object. The field *names* are used as the property names
   * in the returned object.
   * @property {Object}
   * @readonly
   * @category Editing
   */

  get modifications() {
    const data = this.rawModifications;

    if (data && Object.keys(data).length) {
      data[this.constructor.idField] = this.id;
    }

    return data;
  }

  get rawModifications() {
    const me = this,
          data = {};

    if (!me.isModified) {
      return null;
    }

    let keySet = false;
    Object.keys(me.meta.modified).forEach(key => {
      // TODO: isModified will report record as modified even if a modification wont be persisted here. Should it?
      const value = me.getFieldPersistentValue(key);

      if (value !== _undefined) {
        data[key] = value;
        keySet = true;
      }
    });
    return keySet ? data : null;
  }
  /**
   * Get a map of the modified fields in form of an object. The field *dataSources* are used as the property names
   * in the returned object.
   * @property {Object}
   * @readonly
   * @category Editing
   */

  get modificationData() {
    const data = this.rawModificationData; // If there are some persistable field changes, append record id

    if (data && Object.keys(data).length) {
      ObjectHelper.setPath(data, this.constructor.getFieldDefinition(this.constructor.idField).dataSource, this.id);
    }

    return data;
  }

  get rawModificationData() {
    const me = this,
          {
      fieldMap
    } = me.constructor,
          data = {};

    if (!me.isModified) {
      return null;
    }

    let keySet = false;
    Object.keys(me.meta.modified).forEach(fieldName => {
      // TODO: isModified will report record as modified even if a modification wont be persisted here. Should it?
      const field = fieldMap[fieldName]; // No field definition means there's no original dataSource to update

      if (field !== null && field !== void 0 && field.persist) {
        const value = me.getFieldPersistentValue(fieldName);

        if (value !== _undefined) {
          ObjectHelper.setPath(data, field.dataSource, value);
          keySet = true;
        }
      }
    });
    return keySet ? data : null;
  }
  /**
   * Get persistable data in form of an object.
   * @property {Object}
   * @internal
   * @readonly
   * @category Editing
   */

  get persistableData() {
    const me = this,
          data = {};
    me.fields.forEach(field => {
      const value = me.getFieldPersistentValue(field);

      if (value !== _undefined) {
        if (field !== null && field !== void 0 && field.complexMapping) {
          ObjectHelper.setPath(data, field.dataSource, value);
        } else {
          data[field.dataSource] = value;
        }
      }
    });
    return data;
  }
  /**
   * True if this models changes are currently being committed.
   * @property {boolean}
   * @category Editing
   */

  get isCommitting() {
    return Boolean(this.meta.committing);
  }
  /**
   * Clear stored changes, used on commit. Does not revert changes.
   * @privateparam {Boolean} [removeFromStoreChanges] Update related stores modified collection or not
   * @privateparam {Boolean} [includeDescendants] Set true to clear store descendants
   * @category Editing
   */

  clearChanges(removeFromStoreChanges = true, includeDescendants = true) {
    const me = this,
          {
      meta
    } = me;
    meta.modified = {};
    meta.committing = false;

    if (removeFromStoreChanges) {
      me.stores.forEach(store => {
        store.modified.remove(me);
        store.added.remove(me);

        if (includeDescendants) {
          const descendants = store.collectDescendants(me).all;
          store.added.remove(descendants);
          store.modified.remove(descendants);
        }
      });
    }
  }
  /**
   * Reverts changes in this back to their original values.
   * @category Editing
   */

  revertChanges() {
    this.set(this.meta.modified);
  } //endregion
  //region Id

  /**
   * Gets the records internalId. It is assigned during creation, guaranteed to be globally unique among models.
   * @property {Number}
   * @category Identification
   */

  get internalId() {
    return this._internalId;
  }
  /**
   * Returns true if the record is new and has not been persisted (and received a proper id).
   * @property {Boolean}
   * @readonly
   * @category Identification
   */

  get isPhantom() {
    return this.id === '' || this.id == null || this.hasGeneratedId;
  }

  get isModel() {
    return true;
  }
  /**
   * Checks if record has a generated id. New records are assigned a generated id (starting with _generated), which should be
   * replaced on commit.
   * @property {Boolean}
   * @category Identification
   */

  get hasGeneratedId() {
    return typeof this.id === 'string' && this.id.startsWith('_generated');
  }

  static generateId(text = this.$$name) {
    if (!this.generatedIdIndex) {
      this.generatedIdIndex = 0;
    }

    return `_generated${text}${++this.generatedIdIndex}`;
  }
  /**
   * Generates id for new record which starts with _generated.
   * @category Identification
   */

  generateId() {
    return this.constructor.generateId();
  }
  /**
   * Gets the id of specified model or the value if passed string/Number.
   * @param {Core.data.Model|String|Number} model
   * @returns {String|Number} id
   * @category Identification
   */

  static asId(model) {
    return model !== null && model !== void 0 && model.isModel ? model.id : model;
  } //endregion
  //region JSON

  /**
   * Get the records data as a json string.
   *
   * ```javascript
   * const record = new Model({
   *   title    : 'Hello',
   *   children : [
   *     ...
   *   ]
   * });
   *
   * const jsonString = record.json;
   *
   * //jsonString:
   * '{"title":"Hello","children":[...]}'
   * ```
   *
   * @member {String}
   * @category JSON
   */

  get json() {
    return StringHelper.safeJsonStringify(this); // calls our toJSON() method
  }
  /**
   * Used by `JSON.stringify()` to correctly convert this record to json.
   *
   * In most cases no point in calling it directly.
   *
   * ```
   * // This will call `toJSON()`
   * const json = JSON.stringify(record);
   * ```
   *
   * If called manually, the resulting object is a clone of `record.data` + the data of any children:
   *
   * ```
   * const record = new Model({
   *   title    : 'Hello',
   *   children : [
   *     ...
   *   ]
   * });
   *
   * const jsonObject = record.toJSON();
   *
   * // jsonObject:
   * {
   *   title : 'Hello',
   *   children : [
   *       ...
   *   ]
   * }
   * ```
   *
   * @returns {Object}
   * @category JSON
   */

  toJSON() {
    const {
      children
    } = this,
          jsonData = this.persistableData;

    if (children) {
      jsonData[this.constructor.childrenField] = children.map(c => c.toJSON());
    }

    return jsonData;
  }
  /**
   * Represent the record as a string, by default as a JSON string. Tries to use an abbreviated version of the objects
   * data, using id + name/title/text/label/description. If no such field exists, the full data is used.
   *
   * ```javascript
   * const record = new Model({ id : 1, name : 'Steve Rogers', alias : 'Captain America' });
   * console.log(record.toString()); // logs { "id" : 1, "name" : "Steve Rogers" }
   * ```
   *
   * @returns {string}
   */

  toString() {
    const me = this,
          nameField = abbreviationFields.find(field => field in me.constructor.fieldMap),
          data = nameField ? {
      [me.constructor.idField]: me.id,
      [nameField]: me[nameField]
    } : me.data;
    return StringHelper.safeJsonStringify(data);
  } //endregion
  //region Batch

  /**
   * True if this Model is currently batching its changes.
   * @property {Boolean}
   * @readonly
   * @category Editing
   */

  get isBatchUpdating() {
    return Boolean(this.batching);
  }
  /**
   * Begin a batch, which stores changes and commits them when the batch ends.
   * Prevents events from being fired during batch.
   * ```
   * record.beginBatch();
   * record.name = 'Mr Smith';
   * record.team = 'Golden Knights';
   * record.endBatch();
   * ```
   * Please note that you can also set multiple fields in a single call using {@link #function-set}, which in many
   * cases can replace using a batch:
   * ```
   * record.set({
   *   name : 'Mr Smith',
   *   team : 'Golden Knights'
   * });
   * ```
   * @category Editing
   */

  beginBatch() {
    const me = this;

    if (!me.batching) {
      me.batching = 0;
      me.meta.batchChanges = {};
    }

    me.batching++;
  }
  /**
   * End a batch, triggering events if data has changed.
   * @param {Boolean} [silent] Specify `true` to not trigger events. If event is recurring, occurrences won't be updated automatically.
   * @category Editing
  */

  endBatch(silent = false, skipAccessors = false) {
    const me = this,
          {
      parentIdField
    } = me.constructor;

    if (!me.batching) {
      return;
    }

    me.batching--;

    if (me.batching > 0) {
      return;
    } // Set pending batch changes

    if (!ObjectHelper.isEmpty(me.meta.batchChanges)) {
      const batchChanges = { ...me.meta.batchChanges
      };
      me.meta.batchChanges = null; // Move to its new parent before applying the other changes.

      if (batchChanges[parentIdField]) {
        me.parentId = batchChanges[parentIdField];
        delete batchChanges[parentIdField];
      }

      me.set(batchChanges, _undefined, silent, false, skipAccessors);
      me.cancelBatch();
    }
  }
  /**
   * Cancels current batch operation. Any changes during the batch are discarded.
   * @category Editing
   */

  cancelBatch() {
    this.batching = null;
    this.meta.batchChanges = null;
  } //endregion
  //region Events

  /**
   * Triggers beforeUpdate event for each store and checks if changes can be made from event return value.
   * @param {Object} changes Data changes
   * @returns {Boolean} returns true if data changes are accepted
   * @private
   */

  triggerBeforeUpdate(changes) {
    return !this.stores.some(s => {
      if (s.trigger('beforeUpdate', {
        record: this,
        changes
      }) === false) {
        return true;
      }
    });
  } //endregion
  //region Additional functionality

  /**
   * Makes a copy of this model, assigning the specified id or a generated id and also allowing you to pass field values to
   * the created copy.
   *
   * ```
   * const record = new Model({ name : 'Super model', hairColor : 'Brown' });
   * const clone = record.copy({ name : 'Super model clone' });
   * ```
   * @param {Number|String|Object} [newId] The id for the copied instance, or any field values to apply
   * (overriding the values from the source record). If no id provided, one will be auto-generated
   * @param {Number|String} [newId.id] DEPRECATED: Id to set, leave out to use generated id or specify false to also copy id
   * @param {Boolean} [newId.deep] DEPRECATED: true to also clone children (use separate argument)
   * @param {Boolean} [deep] True to also clone children
   * @returns {Core.data.Model} Copy of this model
   * @category Editing
   */

  copy(newId = null, params) {
    const me = this,
          data = { ...me.data
    },
          idField = me.constructor.idField;
    let deep, id; // Chrono model is adding more logic to copy and that logic should be manageable from arguments. So there is
    // option to pass object as a last argument to switch method behavior. Used internally only, shouldn't be public.

    deep = ObjectHelper.isObject(params) ? params.deep : params; // We do not include the id from this record

    if (newId !== false) {
      delete data[idField];
    }

    if (newId && typeof newId === 'object') {
      id = newId.id;

      if ('deep' in newId) {
        deep = newId.deep;
        VersionHelper.deprecate('Grid', '5.0.0', '`deep` attribute deprecated, in favor of a separate second parameter'); // Only use id once to avoid collisions

        delete newId.id;
        delete newId.deep;
      } else {
        Object.assign(data, newId);
      }
    } else {
      id = newId;
    } // Iterate over instance children, because data may not reflect actual children state

    if (deep && me.children) {
      data.children = me.children.map(child => child.copy(undefined, params));
    } else {
      delete data.children;
      delete data.expanded;
    } // No id dataSource value in the data copy.
    // We can use the value from the 'id' property, but as a fallback, generate the id.

    if (data[idField] == null) {
      data[idField] = id || me.generateId(me.firstStore);
    }

    const copy = new me.constructor(data); // Store original record internal id to lookup from copy later

    copy.originalInternalId = me.internalId;
    return copy;
  }
  /**
   * Removes this record from all stores (and in a tree structure, also from its parent if it has one).
   * @param {Boolean} [silent] Specify `true` to not trigger events. If event is recurring, occurrences won't be updated automatically.
   * @category Editing
   */

  remove(silent = false) {
    const me = this,
          {
      parent
    } = this; // Remove from parent if we're in a tree structure.
    // This informs the owning store(s)

    if (parent) {
      parent.removeChild(me);
    } // Store handles remove
    else if (me.stores.length) {
        // Not sure what should happen if you try to remove a special row (group row for example), bailing out
        if (!me.isSpecialRow) {
          me.stores.forEach(s => s.remove(me, silent, false, true));
        }
      }
  }
  /**
   * Get the first store that this model is assigned to.
   * @property {Core.data.Store}
   * @readonly
   * @category Misc
   */

  get firstStore() {
    return this.stores.length > 0 && this.stores[0];
  }
  /**
   * Get a relation config by name, from the first store.
   * @param {String} name
   * @returns {Object}
   * @private
   * @category Relations
   */

  getRelationConfig(name) {
    var _this$firstStore, _this$firstStore$mode;

    // using first store for relations, might have to revise later..
    return (_this$firstStore = this.firstStore) === null || _this$firstStore === void 0 ? void 0 : (_this$firstStore$mode = _this$firstStore.modelRelations) === null || _this$firstStore$mode === void 0 ? void 0 : _this$firstStore$mode.find(r => r.fieldName === name);
  } //endregion
  //region Validation

  /**
   * Check if record has valid data. Default implementation returns true, override in your model to do actual validation.
   * @property {Boolean}
   * @category Editing
   */

  get isValid() {
    return true;
  } //endregion
  //region Store

  /**
   * Joins this record and any children to specified store, if not already joined.
   * @internal
   * @param {Core.data.Store} store Store to join
   * @category Misc
   */

  joinStore(store) {
    const me = this,
          {
      stores,
      unjoinedStores
    } = me;

    if (!stores.includes(store)) {
      super.joinStore && super.joinStore(store);
      store.register(me);
      stores.push(store);

      if (unjoinedStores.includes(store)) {
        unjoinedStores.splice(unjoinedStores.indexOf(store), 1);
      }

      me.isLoaded && me.children.forEach(child => child.joinStore(store));
      me.initRelations();

      if (store.tree && !me.isRoot) {
        me.instanceMeta(store.id).collapsed = !me.expanded;
      }
    }
  }
  /**
   * Unjoins this record and any children from specified store, if already joined.
   * @internal
   * @param {Core.data.Store} store Store to join
   * @param {Boolean} [isReplacing] `true` if this record is being replaced
   * @category Misc
   */

  unJoinStore(store, isReplacing = false) {
    const me = this,
          {
      stores,
      unjoinedStores
    } = me;

    if (stores.includes(store)) {
      var _me$children, _me$children$forEach;

      store.unregister(me); // me.children may === true...

      (_me$children = me.children) === null || _me$children === void 0 ? void 0 : (_me$children$forEach = _me$children.forEach) === null || _me$children$forEach === void 0 ? void 0 : _me$children$forEach.call(_me$children, child => child.unJoinStore(store, isReplacing));
      stores.splice(stores.indexOf(store), 1); // keep the cord to allow removed records to reach the store when needed

      unjoinedStores.push(store);
      super.unJoinStore && super.unJoinStore(store, isReplacing); // remove from relation cache

      store.uncacheRelatedRecord(me);
    }
  }
  /**
   * Returns true if this record is contained in the specified store, or in any store if store param is omitted.
   * @internal
   * @param {Core.data.Store} store Store to join
   * @returns {Boolean}
   * @category Misc
   */

  isPartOfStore(store) {
    if (store) {
      return store.includes(this);
    }

    return this.stores.length > 0;
  }
  /**
   * Returns true if this record is not part of any store.
   * @property {Boolean}
   * @readonly
   * @internal
   */

  get isRemoved() {
    return !this.isPartOfStore();
  } //endregion
  //region Per instance meta

  /**
   * Used to set per external instance meta data. For example useful when using a record in multiple grids to store some state
   * per grid.
   * @param {String|Object} instanceOrId External instance id or the instance itself, if it has id property
   * @private
   * @category Misc
   */

  instanceMeta(instanceOrId) {
    const {
      meta
    } = this,
          id = instanceOrId.id || instanceOrId;

    if (!meta.map) {
      meta.map = {};
    }

    return meta.map[id] || (meta.map[id] = {});
  }

  get isSpecialRow() {
    return this.meta.specialRow;
  } //endregion

}
Model._idField = 'id';
Model._internalIdCounter = 1;
Model._assignedIdField = false;
Model.exposeProperties();
Model._$name = 'Model';

/**
 * @module Core/data/Duration
 */

/**
 * Class which represents a duration object.
 * ```javascript
 * {
 *    unit      : String,
 *    magnitude : Number
 * }
 * ```
 */

class Duration {
  /**
   * Duration constructor.
   * @param {Number} magnitude Duration magnitude value
   * @param {String} unit Duration unit value
   */
  constructor(magnitude, unit) {
    if (typeof magnitude === 'number') {
      this._magnitude = magnitude;
      this._unit = unit;
    } else {
      if (typeof magnitude === 'string') {
        magnitude = DateHelper.parseDuration(magnitude);
      }

      if (typeof magnitude === 'object') {
        Object.assign(this, magnitude);
      }
    }
  }
  /**
   * Get/Set numeric magnitude `value`.
   * @property {Number}
   */

  get magnitude() {
    return this._magnitude;
  }

  set magnitude(value) {
    this._magnitude = typeof value === 'number' && value;
  }
  /**
   * Get/Set duration unit to use with the current magnitude value.
   * Valid values are:
   * - "millisecond" - Milliseconds
   * - "second" - Seconds
   * - "minute" - Minutes
   * - "hour" - Hours
   * - "day" - Days
   * - "week" - Weeks
   * - "month" - Months
   * - "quarter" - Quarters
   * - "year"- Years
   *
   * @property {String}
   */

  get unit() {
    return this._unit;
  }

  set unit(value) {
    this._unit = DateHelper.parseTimeUnit(value);
  }

  get isValid() {
    return this._magnitude != null && this._unit;
  }
  /**
   * The `milliseconds` property is a read only property which returns the number of milliseconds in this Duration
   * @property {Number}
   * @readonly
   */

  get milliseconds() {
    // There's no smaller time unit in the Date class than milliseconds, so round any divided values
    return this.isValid ? Math.round(DateHelper.asMilliseconds(this._magnitude, this._unit)) : 0;
  }
  /**
   * Returns truthy value if this Duration equals the passed value.
   * @param {Core.data.Duration} value
   * @return {Boolean}
   */

  isEqual(value) {
    return Boolean(value && this.milliseconds === value.milliseconds);
  }

  toString(useAbbreviation) {
    const me = this,
          abbreviationFn = useAbbreviation ? 'getShortNameOfUnit' : 'getLocalizedNameOfUnit';
    return me.isValid ? `${me._magnitude} ${DateHelper[abbreviationFn](me._unit, me._magnitude !== 1)}` : '';
  }

  toJSON() {
    return this.toString();
  }

  valueOf() {
    return this.milliseconds;
  }

}
Duration._$name = 'Duration';

/**
 * @module Core/util/CollectionFilter
 */

/**
 * A class which encapsulates a single filter operation which may be applied to any object to decide whether to
 * include or exclude it from a set.
 *
 * A CollectionFilter generally has at least three main properties:
 *
 * * `property` - The name of a property in candidate objects from which to extract the value to test
 * * `value` - The value which  this filter uses to test against.
 * * `operator` - The comparison operator, eg: `'='` or `'>'` etc.
 *
 * Given these three essential values, further configurations may affect how the filter is applied:
 *
 * * `caseSensitive` - If configured as `false`, string comparisons are case insensitive.
 * * `convert` - A function which, when passed the extracted value from the candidate object, returns the value to test.
 *
 * A filter may also be configured with a single `filterBy` property. This function is just passed the raw
 * candidate object and must return `true` or `false`.
 *
 * A CollectionFilter may be configured to encapsulate a single filtering function by passing that function as the sole
 * parameter to the constructor:
 *
 *     new CollectionFilter(candidate => candidate.title.contains('search string'));
 *
 */

class CollectionFilter extends Base.mixin(Identifiable) {
  static get defaultConfig() {
    return {
      /**
       * The name of a property of candidate objects which yields the value to compare against this CollectionFilter's {@link #config-value}.
       * @config {String}
       */
      property: null,

      /**
       * The value against which to compare the {@link #config-property} of candidate objects.
       * @config {*}
       */
      value: null,

      /**
       * The operator to use when comparing a candidate object's {@link #config-property} with this CollectionFilter's {@link #config-value}.
       * May be: `'='`, `'!='`, `'>'`, `'>='`, `'<'`, `'<='`, `'*'`, `'startsWith'`, `'endsWith'`
       * @config {String}
       */
      operator: null,

      /**
       * May be used in place of the {@link #config-property}, {@link #config-value} and {@link #config-property} configs. A function which
       * accepts a candidate object and returns `true` or `false`
       * @config {Function}
       */
      filterBy: null,

      /**
       * A function which accepts a value extracted from a candidate object using the {@link #config-property} name, and
       * returns the value which the filter should use to compare against its {@link #config-value}.
       * @config {Function}
       */
      convert: null,

      /**
       * Configure as `false` to have string comparisons case insensitive.
       * @config {Boolean}
       */
      caseSensitive: true,

      /**
       * The `id` of this Filter for when used by a {@link Core.util.Collection} Collection.
       * By default the `id` is the {@link #config-property} value.
       * @config {String}
       */
      id: null,
      // Type is required to process the Date value in State API. Store doesn't always know about field type to
      // process filter value, when it applies it from the state, e.g. when you don't declare model field as `date`
      // type but provide a Date instance there. When DateColumn is used to shows this field, it could add date
      // filters to the store. When store is applying state it cannot just infer type, because model doesn't
      // declare it. Only column knows. So to properly process the Date instance for the filter State API would
      // have to process the field additionally, checking model field type and column type. So it is simpler to
      // make Filter to put this information. That way when filter is instantiated by the store, it can gracefully
      // handle value processing, converting date string to the Date instance.
      // Date is the only known value type so far which requires this processing.
      type: null
    };
  }

  construct(config) {
    if (typeof config === 'function') {
      config = {
        filterBy: config
      };
    } // If Filter is created without a type (yet everything except applying state) create one

    if (!config.type) {
      if (DateHelper.isDate(config.value)) {
        config.type = 'date';
      } else if (config.value instanceof Duration) {
        config.type = 'duration';
      }
    } // If type already exist, it means we are applying state and should process value
    else {
        if (config.type === 'date') {
          config.value = new Date(config.value);
        } else if (config.type === 'duration') {
          config.value = new Duration(config.value);
        }
      }

    super.construct(config);
  }
  /**
   * When in a Collection (A Collection holds its Filters in a Collection), we need an id.
   * @property {String}
   * @private
   */

  get id() {
    return this._id || (this._id = this.property || CollectionFilter.generateId('b-filter'));
  }

  set id(id) {
    this._id = id;
  }

  onChange(propertyChanged) {
    const me = this; // Inform any owner (eg a Store), that it has to reassess its CollectionFilters

    if (!me.isConfiguring && me.owner && !me.owner.isConfiguring && me.owner.onFilterChanged) {
      me.owner.onFilterChanged(me, propertyChanged);
    }
  }

  get filterBy() {
    return this._filterBy || this.defaultFilterBy;
  }
  /**
   * May be used in place of the {@link #config-property}, {@link #config-value} and {@link #config-property} configs. A function which
   * accepts a candidate object and returns `true` or `false`
   * @type {Function}
   */

  set filterBy(filterBy) {
    this._filterBy = filterBy;
  }

  defaultFilterBy(candidate) {
    return this[this.operator](this.convert(candidate[this.property]));
  }
  /**
   * The name of a property of candidate objects which yields the value to compare against this CollectionFilter's {@link #config-value}.
   * @type {String}
   */

  set property(property) {
    this._property = property; // Signal to owner about filter change

    this.onChange('property');
  }

  get property() {
    return this._property;
  }
  /**
   * The value against which to compare the {@link #config-property} of candidate objects.
   * @type {*}
   */

  set value(value) {
    var _value$valueOf, _value;

    this._value = value;
    value = (_value$valueOf = (_value = value) === null || _value === void 0 ? void 0 : _value.valueOf()) !== null && _value$valueOf !== void 0 ? _value$valueOf : value; // Filter value is a processed value to be used by the comparators. Useful when value is object, like Duration field

    this._filterValue = !this.caseSensitive && typeof value === 'string' ? value.toLowerCase() : value; // Signal to owner about filter change

    this.onChange('value');
  }

  get value() {
    return this._value;
  }

  get filterValue() {
    return this._filterValue;
  }
  /**
   * The operator to use when comparing a candidate object's {@link #config-property} with this CollectionFilter's {@link #config-value}.
   * May be: `'='`, `'!='`, `'>'`, `'>='`, `'<'`, `'<='`, `'*'`, `'startsWith'`, `'endsWith'`
   * @type {String}
   */

  set operator(operator) {
    this._operator = operator; // Signal to owner about filter change

    this.onChange('operator');
  }

  get operator() {
    return this._operator || (typeof this.filterValue === 'string' ? '*' : '=');
  }

  convert(value) {
    // This is a workaround for filterbar feature: it always converts input value to string. When date is typed,
    // it is converted into string, and Date.valueOf() would return number. So if we are matching date against string
    // type, we should not convert it.
    if (!(typeof this.filterValue === 'string' && value instanceof Date)) {
      var _value$valueOf2, _value2;

      // if value is a complex type, try to access `value` property to get primitive value
      value = (_value$valueOf2 = (_value2 = value) === null || _value2 === void 0 ? void 0 : _value2.valueOf()) !== null && _value$valueOf2 !== void 0 ? _value$valueOf2 : value;
    }

    value = !this.caseSensitive && typeof value === 'string' ? value.toLowerCase() : value;
    return value;
  }

  filter(candidate) {
    return this.filterBy(candidate);
  }

  startsWith(v) {
    return String(v).startsWith(this.filterValue);
  }

  endsWith(v) {
    return String(v).endsWith(this.filterValue);
  }

  '='(v) {
    return ObjectHelper.isEqual(v, this.filterValue);
  }

  '!='(v) {
    return !ObjectHelper.isEqual(v, this.filterValue);
  }

  '>'(v) {
    return ObjectHelper.isMoreThan(v, this.filterValue);
  }

  '>='(v) {
    return ObjectHelper.isMoreThan(v, this.filterValue) || ObjectHelper.isEqual(v, this.filterValue);
  }

  '<'(v) {
    return ObjectHelper.isLessThan(v, this.filterValue);
  }

  '<='(v) {
    return ObjectHelper.isLessThan(v, this.filterValue) || ObjectHelper.isEqual(v, this.filterValue);
  }

  '*'(v) {
    return ObjectHelper.isPartial(v, this.filterValue);
  } // Accepts an array or a Collection

  static generateFiltersFunction(filters) {
    if (!filters || !filters.length && !filters.count) {
      return FunctionHelper.returnTrue;
    }

    return function (candidate) {
      let match = true;

      for (const filter of filters) {
        // Skip disabled filters
        if (!filter.disabled) {
          match = filter.filter(candidate);
        }

        if (!match) {
          break;
        }
      }

      return match;
    };
  }

}
CollectionFilter._$name = 'CollectionFilter';

/**
 * @module Core/helper/IdHelper
 */

/**
 * IdHelper provides unique ID generation.
 *
 * This class is not intended for application use, it is used internally by the Bryntum infrastructure.
 * @internal
 */
class IdHelper {
  /**
   * Generate a new id, using IdHelpers internal counter and a prefix
   * @param {String} prefix Id prefix
   * @returns {String} Generated id
   */
  static generateId(prefix = 'generatedId') {
    // This produces "b-foo-1, b-foo-2, ..." for each prefix independently of the others. In other words, it makes
    // id's more stable since the counter is on a per-class basis.
    return prefix + (IdHelper.idCounts[prefix] = (IdHelper.idCounts[prefix] || 0) + 1);
  }

}
IdHelper.idCounts = {};
IdHelper._$name = 'IdHelper';

/**
 * @module Core/util/CollectionSorter
 */

/**
 * A class which encapsulates a single sorter operation which may be applied to any object to decide whether to
 * include or exclude it from a set.
 *
 * A CollectionSorter generally has two properties:
 *
 * * `property` - The name of a property in collection objects by which to sort
 * * `direction` - The sort direction, `'ASC'` or `'DESC'`.
 *
 * It may also be configured with just a {@link #config-sortFn} function which returns the desired comparison
 * result when passed two objects to compare. Note that this does *not* require or use the
 * {@link #config-property} config. Two collection items are passed for comparison.
 *
 * Further configurations may affect how the sorter is applied:
 *
 * * `convert` - A function which, when passed the {@link #config-property} value from
 * a collection object, returns the value to sort by.
 *
 * A CollectionSorter may be configured to encapsulate a {@link #config-sortFn} by passing that function as the sole
 * parameter to the constructor:
 *
 *     new CollectionSorter((lhs, rhs) => {
 *         lhs = lhs.customerDetails.companyName.toLowerCase();
 *         rhs = rhs.customerDetails.companyName.toLowerCase();
 *
 *         if (lhs < rhs) {
 *             return -1;
 *         }
 *         else if (lhs > rhs) {
 *             return 1;
 *         }
 *         else {
 *             return 0;
 *         }
 *     });
 *
 */

class CollectionSorter extends Base {
  static get defaultConfig() {
    return {
      /**
       * The name of a property of collection objects which yields the value to sort by.
       * @config {String}
       */
      property: null,

      /**
       * The direction to sort in, `'ASC'` or `'DESC'`
       * @config {String}
       * @default
       */
      direction: 'ASC',

      /**
       * A function which takes the place of using {@link #config-property} and {@link #config-direction}.
       * The function is passed two objects from the collection to compare and must return the comparison result.
       * @config {Function}
       */
      sortFn: null,

      /**
       * When using {@link #config-property}, this may be specified as a function which takes the raw
       * property value and returns the value to actually sort by.
       * @config {Function}
       */
      convert: null,

      /**
       * The `id` of this Sorter for when used by a {@link Core.util.Collection} Collection.
       * By default the `id` is the {@link #config-property} value.
       * @config {String}
       */
      id: null,

      /**
       * Use `localeCompare()` when sorting, which lets the browser sort in a locale specific order. Set to `true`,
       * a locale string or a locale config to enable.
       *
       * Enabling this has big negative impact on sorting
       * performance. For more info on `localeCompare()`, see [MDN](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/localeCompare).
       *
       * ```javascript
       * collection.addSorter({ field: 'name', useLocaleSort : 'sv-SE' });
       * ```
       *
       * @config {Boolean|String|Object}
       * @default false
       */
      useLocaleSort: null
    };
  }

  construct(config) {
    if (typeof config === 'function') {
      config = {
        sortFn: config
      };
    }

    super.construct(config);
  }
  /**
   * When in a Collection (A Collection holds its Sorters in a Collection), we need an id.
   * @property {String}
   * @private
   */

  get id() {
    return this._id || (this._id = this.property || IdHelper.generateId('b-sorter'));
  }

  set id(id) {
    this._id = id;
  }

  set sortFn(sortFn) {
    this._sortFn = sortFn;
  }

  get sortFn() {
    if (this._sortFn) {
      return this._sortFn;
    }

    return this.defaultSortFn;
  }
  /**
   * Default sortFn used when no sortFn specified. Uses the {@link #config-property},
   * {@link #config-direction}, and {@link #config-convert}.
   * @private
  */

  defaultSortFn(lhs, rhs) {
    const me = this,
          {
      convert,
      property,
      useLocaleSort
    } = me,
          multiplier = me.direction.toLowerCase() === 'desc' ? -1 : 1;
    lhs = lhs[property];
    rhs = rhs[property];

    if (convert) {
      lhs = convert(lhs);
      rhs = convert(rhs);
    }

    if (useLocaleSort && lhs != null && rhs != null) {
      // Use systems locale
      if (useLocaleSort === true) {
        return String(lhs).localeCompare(rhs);
      } // Use specified locale

      if (typeof useLocaleSort === 'string') {
        return String(lhs).localeCompare(rhs, useLocaleSort);
      } // Use locale config

      if (typeof useLocaleSort === 'object') {
        return String(lhs).localeCompare(rhs, useLocaleSort.locale, useLocaleSort);
      }
    }

    return (lhs > rhs ? 1 : lhs < rhs ? -1 : 0) * multiplier;
  }

  static generateSortFunction(sorters, tieBreaker) {
    const items = sorters.isCollection ? sorters.values : sorters,
          n = items.length;
    return (lhs, rhs) => {
      let comp, i;

      for (i = 0; i < n; ++i) {
        comp = items[i].sortFn(lhs, rhs);

        if (comp) {
          return comp;
        }
      }

      return tieBreaker ? tieBreaker(lhs, rhs) : 0;
    };
  }

}
CollectionSorter._$name = 'CollectionSorter';

/**
 * @module Core/util/Collection
 */

const return0 = () => 0,
      reverseNumericSortFn = (a, b) => b - a,
      filteredIndicesProperty = Symbol('filteredIndicesProperty'),
      emptyArray$2 = Object.freeze([]),
      sortEvent = Object.freeze({
  action: 'sort',
  added: emptyArray$2,
  removed: emptyArray$2,
  replaced: emptyArray$2
}),
      filterEvent = Object.freeze({
  action: 'filter',
  added: emptyArray$2,
  removed: emptyArray$2,
  replaced: emptyArray$2
}),
      keyTypes = {
  string: 1,
  number: 1
}; // Adds a single item to a single index using the specified key

function addItemToIndex(item, index, key) {
  // Unique holds a single entry
  if (index.unique !== false) {
    index.set(key, item);
  } // Non-unique index holds a Set
  else {
      let set = index.get(key); // Add a set if this is the first entry

      if (!set) {
        set = new Set();
        index.set(key, set);
      } // Add entry to the set

      set.add(item);
    }
} // Removes a single item from a single index using the specified key

function removeItemFromIndex(item, index, key) {
  // Unique holds single entry, remove it
  if (index.unique !== false) {
    index.delete(key);
  } else if (index.has(key)) {
    // Remove from set
    index.get(key).delete(item); // Remove turned empty set

    if (!index.get(key).size) {
      index.delete(key);
    }
  }
} // Used to fully build the indices, normal and filtered (if used). Better to do full builds for performance reasons
// when assigning new datasets. For other CRUD operations, indices are kept up to date elsewhere

function doRebuildIndices(values, indices, keyProps, indexCount) {
  for (let i = 0; i < values.length; i++) {
    const item = values[i];

    for (let j = 0; j < indexCount; j++) {
      const keyProp = keyProps[j],
            key = item[keyProp],
            index = indices[keyProp];
      addItemToIndex(item, index, key);
    }
  }
}
/**
 * A class which encapsulates a {@link #function-get keyed},
 * {@link #function-addFilter filterable}, {@link #function-addSorter sortable}
 * collection of objects. Entries may not be atomic data types such as `string` or `number`.
 *
 * The entries are keyed by their `id` which is determined by interrogating the {@link #config-idProperty}.
 *
 * To filter a Collection, add a {@link Core.util.CollectionFilter CollectionFilter}
 * using the {@link #function-addFilter} method. A Filter config object may be specified here
 * which will be promoted to a CollectionFilter instance.
 *
 * To sort a Collection, add a {@link Core.util.CollectionSorter CollectionSorter}
 * using the {@link #function-addSorter} method. A Sorter config object may be specified here
 * which will be promoted to a CollectionSorter instance.
 */

class Collection extends Base.mixin(Events) {
  //region Config
  static get configurable() {
    return {
      /**
       * Specify the name of the property of added objects which provides the lookup key
       * @config {String}
       * @default
       */
      idProperty: 'id',

      /**
       * Specify the names or index configs of properties which are to be indexed for fast lookup.
       *
       * Index configs use the format `{ property : string, unique : boolean }`. Unique indices stores one index
       * per entry, non-unique stores a `Set`. If left out, `unique` defaults to `true`
       *
       * @config {String[]|Object[]}
       */
      extraKeys: null,

      /**
       * Automatically apply filters on item add.
       * @config {Boolean}
       * @default
       */
      autoFilter: true,

      /**
       * A {@link Core.util.CollectionSorter Sorter}, or Sorter config object, or
       * an array of these, to use to sort this Collection.
       * @config {Object[]}
       * @default
       */
      sorters: {
        $config: ['lazy'],
        value: []
      }
    };
  }

  get isCollection() {
    return true;
  } //endregion
  //region Init & destroy

  construct(config) {
    /**
     * A counter which is incremented whenever the Collection is mutated in a meaningful way.
     *
     * If a {@link #function-splice} call results in no net replacement, removal or addition,
     * then the `generation` will not be incremented.
     * @property {Number}
     * @readonly
     */
    this.generation = 0;
    this._values = [];
    super.construct(config);
  }

  doDestroy() {
    var _me$_sorters;

    super.doDestroy();
    const me = this;
    me._values.length = 0;

    if (me.isFiltered) {
      me._filteredValues.length = 0;
      me.filters.destroy();
    } // eslint-disable-next-line no-unused-expressions

    (_me$_sorters = me._sorters) === null || _me$_sorters === void 0 ? void 0 : _me$_sorters.destroy();
  } //endregion
  //region "CRUD"

  /**
   * Clears this collection.
   */

  clear() {
    const me = this,
          removed = me._values.slice();

    if (me.totalCount) {
      me._values.length = 0;

      if (me._filteredValues) {
        me._filteredValues.length = 0;
      }

      me._indicesInvalid = true; // Indicate to observers that data has changed.

      me.generation++;
      me.trigger('change', {
        action: 'clear',
        removed
      });
    }
  }
  /**
   * Replaces the internal `values` array with the passed `values`, or `filteredValues` array with the passed `filteredValues`.
   * If `filteredValues` are not passed explicitly, but storage is filtered, decides internally `values` or `filteredValues` should
   * be replaced by passed `values`.
   *
   * Note that this takes ownership of the array, and the array must not be mutated by outside code.
   *
   * This is an internal utility method, not designed for use by application code.
   *
   * @param {Object} params Values and parameters to replace
   * @param {Object[]} params.values The new `values` array
   * @param {Object[]} [params.filteredValues] The new `filteredValues` array. Applicable only when storage is filtered.
   * @param {Boolean} [params.silent=false] If true, `change` event will not be fired
   * @param {Boolean} [params.isNewDataset=false] If true, `values` is a new dataset
   * @fires change
   * @internal
   */

  replaceValues({
    values,
    filteredValues,
    silent = false,
    isNewDataset = false
  }) {
    const me = this;
    let replacedValues, replacedFilteredValues; // The isNewDataset flag is passed by store#loadData to indicate that it's
    // a new data load, and that local filters can be applied.
    // Other use cases are for purely local updates of an existing dataset such as
    // refreshing the visible data with a values array containing group headers.

    if (me.isFiltered && !isNewDataset) {
      const filteredPassed = Boolean(filteredValues); // If `filteredValues` are missing, take `values` as a source of filtered values

      if (!filteredPassed) {
        filteredValues = values.slice();
        values = null;
      } // otherwise check if non-filtered values are passed together with filtered, and replace them too
      else if (values) {
          replacedValues = me._values;
          me._values = values.slice();
        }

      replacedFilteredValues = me._filteredValues;
      me._filteredValues = filteredValues.slice();
    } else {
      replacedValues = me._values;
      me._values = values.slice();
      filteredValues = null;

      if (me.isFiltered && isNewDataset && me.autoFilter) {
        me._filterFunction = null;
        me._filteredValues = me._values.filter(me.filterFunction);
      } else if (me._filteredValues) {
        me._filteredValues.length = 0;
      }
    }

    me._indicesInvalid = true; // Indicate to observers that data has changed.

    me.generation++;

    if (!silent) {
      me.trigger('change', {
        action: 'replaceValues',
        replacedValues,
        replacedFilteredValues,
        values,
        filteredValues
      });
    }
  }

  set values(values) {
    // Want a full rebuild for new dataset, less costly than doing it per item
    this._indicesInvalid = true;
    this.splice(0, this._values.length, values);
  }
  /**
   * The set of values of this Collection. If this Collection {@link #property-isFiltered},
   * this yields the filtered data set.
   *
   * Setting this property replaces the data set.
   * @property {Object[]}
   */

  get values() {
    return this.isFiltered ? this._filteredValues : this._values;
  }
  /**
   * Iterator that allows you to do `for (const item of collection)`
   */

  [Symbol.iterator]() {
    return this.values[Symbol.iterator]();
  }
  /**
   * Executes the passed function for each item in this Collection, passing in the item,
   * ths index, and the full item array.
   * @param {Function} fn The function to execute.
   * @param {Boolean} [ignoreFilters=false] Pass `true` to include all items, bypassing filters.
   */

  forEach(fn, ignoreFilters = false) {
    (this.isFiltered && !ignoreFilters ? this._filteredValues : this._values).forEach(fn);
  }
  /**
   * Extracts ths content of this Collection into an array based upon the passed
   * value extraction function.
   * @param {Function} fn A function, which, when passed an item, returns a value to place into the resulting array.
   * @param {Boolean} [ignoreFilters=false] Pass `true` to process an item even if it is filtered out.
   * @returns {Object[]} An array of values extracted from this Collection.
   */

  map(fn, ignoreFilters = false) {
    return (this.isFiltered && !ignoreFilters ? this._filteredValues : this._values).map(fn);
  }
  /**
   * Returns the first item in this Collection which elicits a *truthy* return value from the passed function.
   * @param {Function} fn A function, which, when passed an item, returns `true` to select it as the item to return.
   * @param {Boolean} [ignoreFilters=false] Pass `true` to include filtered out items.
   * @returns {Object} The matched item, or `undefined`.
   */

  find(fn, ignoreFilters = false) {
    return (this.isFiltered && !ignoreFilters ? this._filteredValues : this._values).find(fn);
  }

  get first() {
    return this.values[0];
  }

  get last() {
    return this.values[this.count - 1];
  }
  /**
   * The set of all values of this Collection regardless of filters applied.
   * @readonly
   * @property {Object[]}
   */

  get allValues() {
    return this._values;
  }
  /**
   * Adds items to this Collection. Multiple new items may be passed.
   *
   * By default, new items are appended to the existing values.
   *
   * Any {@link #property-sorters} {@link #property-sorters} present are re-run.
   *
   * Any {@link #property-filters} {@link #property-filters} present are re-run.
   *
   * *Note that if application functionality requires add and remove, the
   * {@link #function-splice} operation is preferred as it performs both
   * operations in an atomic manner*
   * @param  {...Object} items The item(s) to add.
   */

  add(...items) {
    if (items.length === 1) {
      this.splice(this._values.length, null, ...items);
    } else {
      this.splice(this._values.length, null, items);
    }
  }
  /**
   * Removes items from this Collection. Multiple items may be passed.
   *
   * Any {@link #property-sorters} {@link #property-sorters} present are re-run.
   *
   * Any {@link #property-filters} {@link #property-filters} present are re-run.
   *
   * *Note that if application functionality requires add and remove, the
   * {@link #function-splice} operation is preferred as it performs both
   * operations in an atomic manner*
   * @param  {...Object} items The item(s) to remove.
   */

  remove(...items) {
    if (items.length === 1) {
      this.splice(0, ...items);
    } else {
      this.splice(0, items);
    }
  }
  /**
   * Moves an individual item, or a block of items to another location.
   * @param {Object|Object[]} items The item/items to move.
   * @param {Object} [beforeItem] the item to insert the first item before. If omitted, the `item`
   * is moved to the end of the Collection.
   * @returns {Number} The new index of the `item`.
   */

  move(items, beforeItem) {
    if (!Array.isArray(items)) {
      items = [items];
    } // Handle the case of move(myItem, myItem). It's a no-op

    while (items.length && items[0] === beforeItem) {
      items.shift();
    }

    if (!items.length) {
      return;
    }

    const me = this,
          {
      _values
    } = me,
          itemIndex = me.indexOf(items[0], true); // move(record, followingrecord) is a no-op

    if (items.length === 1 && _values[itemIndex + 1] === beforeItem) {
      return;
    } // Silently remove the items that are to be inserted before the "beforeItem".

    me.suspendEvents();
    me.remove(items);
    me.resumeEvents();
    const beforeIndex = beforeItem ? me.indexOf(beforeItem, true) : _values.length;

    if (beforeIndex === -1) {
      throw new Error('Collection move beforeItem parameter must be present in Collection');
    }

    _values.splice(beforeIndex, 0, ...items);

    me._indicesInvalid = 1;
    me.trigger('change', {
      action: 'move',
      items,
      from: itemIndex,
      to: beforeIndex
    });
    return beforeIndex;
  }
  /**
   * The core data set mutation method. Removes and adds at the same time. Analogous
   * to the `Array` `splice` method.
   *
   * Note that if items that are specified for removal are also in the `toAdd` array,
   * then those items are *not* removed then appended. They remain in the same position
   * relative to all remaning items.
   *
   * @param {Number} index Index at which to remove a block of items. Only valid if the
   * second, `toRemove` argument is a number.
   * @param {Object[]|Number} [toRemove] Either the number of items to remove starting
   * at the passed `index`, or an array of items to remove (If an array is passed, the `index` is ignored).
   * @param  {Object[]|Object} [toAdd] An item, or an array of items to add.
   */

  splice(index = 0, toRemove, ...toAdd) {
    const me = this,
          idProperty = me.idProperty,
          values = me._values,
          newIds = {},
          removed = [],
          replaced = [],
          oldCount = me.totalCount;
    let added, mutated; // Create an "newIds" map of the new items so remove ops know if it's really a replace
    // {
    //     1234 : true
    // }
    // And an "added" array of the items that need adding (there was not already an entry for the id)
    //

    if (toAdd) {
      if (toAdd.length === 1 && Array.isArray(toAdd[0])) {
        toAdd = toAdd[0];
      } // Check for replacements if we contain any data

      if (oldCount && toAdd.length) {
        // Only risk rebuilding the indices if we are adding
        const idIndex = me.indices[idProperty];
        added = [];

        for (let i = 0; i < toAdd.length; i++) {
          const newItem = toAdd[i],
                id = newItem[idProperty],
                existingItem = idIndex.get(id),
                existingIndex = existingItem ? values.indexOf(existingItem) : -1; // Register incoming id so that removal leaves it be

          newIds[id] = true; // Incoming id is already present.
          // Replace it in place.

          if (existingIndex !== -1) {
            // If incoming is the same object, it's a no-op
            if (values[existingIndex] !== newItem) {
              replaced.push([values[existingIndex], newItem]);
              values[existingIndex] = newItem;
            }
          } else {
            added.push(newItem);
          }
        }
      } // Empty Collection, we simply add what we're passed
      else {
          added = toAdd;
        }
    }

    if (toRemove) {
      // We're removing a chunk starting at index
      if (typeof toRemove === 'number') {
        for (let removeIndex = index; toRemove; --toRemove) {
          const id = values[removeIndex][idProperty]; // If the entry here is being replaced, skip the insertion index past it

          if (newIds[id]) {
            index++;
            removeIndex++;
          } // If the id is not among incoming items, remove it
          else {
              removed.push(values[removeIndex]);
              values.splice(removeIndex, 1);
              mutated = true;
            }
        }
      } // We are removing an item/items
      else {
          let contiguous = added.length === 0,
              lastIdx;

          if (!Array.isArray(toRemove)) {
            toRemove = [toRemove];
          } // Create array of index points to remove.
          // They must be in reverse order so that removal leaves following remove indices stable

          const removeIndices = toRemove.reduce((result, item) => {
            const isNumeric = typeof item === 'number',
                  idx = isNumeric ? item : me.indexOf(item, true); // Drop out of contiguous mode if we find a non-contiguous record, or a remove *index*

            if (contiguous && (lastIdx != null && idx !== lastIdx + 1 || isNumeric)) {
              contiguous = false;
            } // Do not include indices out of range in our removeIndices

            if (idx >= 0 && idx < oldCount) {
              result.push(idx);
            }

            lastIdx = idx;
            return result;
          }, []).sort(reverseNumericSortFn); // If it's a pure remove of contiguous items with no adds, fast track it.

          if (contiguous) {
            // If reduced to zero by being asked to remove items we do not contain
            // then this is a no-op
            if (removeIndices.length) {
              removed.push.apply(removed, toRemove);
              values.splice(removeIndices[removeIndices.length - 1], removeIndices.length);
              mutated = true;
            }
          } else {
            // Loop through removeIndices splicing each index out of the values
            // unless there's an incoming identical id.
            for (let i = 0; i < removeIndices.length; i++) {
              const removeIndex = removeIndices[i];

              if (removeIndex !== -1) {
                const id = values[removeIndex][idProperty]; // If the id is not among incoming items, remove it

                if (!newIds[id]) {
                  removed.unshift(values[removeIndex]);
                  values.splice(removeIndex, 1);
                  mutated = true;
                }
              }
            }
          }
        } // Update indices only if they have been used

      if (removed.length && !me._indicesInvalid) {
        removed.forEach(me.removeFromIndices, me);
      }
    } // If we collected genuinely new entries, insert them at the splice index

    if (added.length) {
      values.splice(Math.min(index, values.length), 0, ...added);
      mutated = true; // Update indices only if they have been used

      if (!me._indicesInvalid) {
        added.forEach(me.addToIndices, me);
      }
    } // Update indices only if they have been used

    if (replaced.length && !me._indicesInvalid) {
      replaced.forEach(rep => {
        me.removeFromIndices(rep[0]);
        me.addToIndices(rep[1]);
      });
    } // If we either added or removed items, or we did an in-place replace operation
    // then inform all interested parties.

    if (mutated || replaced.length) {
      // Ensure order of values matches the sorters
      if (me.isSorted) {
        me.onSortersChanged();
      } // The sort will also recreate the filteredValues so that it can be in correct sort order
      else if (me.isFiltered) {
          if (me.autoFilter) {
            me.onFiltersChanged();
          } else {
            me._filteredValues.push(...added);
          }
        } // Indicate to observers that data has changed.

      me.generation++;
      /**
       * Fired when items are added, replace or removed
       * @event change
       * @param {String} action The underlying operation which caused data change.
       * May be `'splice'` (meaning an atomic add/remove operation, `'sort'` or `'filter'`)
       * @param {Core.util.Collection} source This Collection.
       * @param {Object[]} removed An array of removed items.
       * @param {Object[]} added An array of added items.
       * @param {Object[]} replaced An array of replacements, each entry of which contains `[oldValue, newValue]`.
       * @param {Number} oldCount The number of items in the full, untiltered collection prior to the splice operation.
       */

      me.trigger('change', {
        action: 'splice',
        removed,
        added,
        replaced,
        oldCount
      });
    } else {
      /**
       * Fired when a {@link #function-splice} operation is requested but the operation
       * is a no-op and has caused no change to this Collection's dataset. The splice
       * method's parameters are passed for reference.
       * @event noChange
       * @param {Number} index Index at which to remove a block of items.
       * @param {Object[]|Number} [toRemove] Either the number of items to remove starting
       * at the passed `index`, or an array of items to remove (If an array is passed, the `index` is ignored).
       * @param  {Object[]|Object} [toAdd] An item, or an array of items to add.
       */
      me.trigger('noChange', {
        index,
        toRemove,
        toAdd
      });
    }
  }
  /**
   * Change the id of an existing member by mutating its {@link #config-idProperty}.
   * @param {String|Number|Object} item The item or id of the item to change.
   * @param {String|Number} newId The id to set in the existing member.
   */

  changeId(item, newId) {
    const me = this,
          {
      idProperty
    } = me,
          oldId = keyTypes[typeof item] ? item : item[idProperty],
          member = me.get(oldId);

    if (member) {
      const existingMember = me.get(newId);

      if (existingMember && member !== existingMember) {
        throw new Error(`Attempt to set item ${oldId} to already existing member's id ${newId}`);
      }

      me.removeIndexEntry(item, idProperty, oldId);
      me.addIndexEntry(item, idProperty, newId); // Last on purpose, onItemMutation would fail to find the item if its id was changed prior to the call

      member[idProperty] = newId;
    }
  }
  /**
   * Returns the item with the passed `id`. By default, filtered are honoured, and
   * if the item with the requested `id` is filtered out, nothing will be returned.
   *
   * To return the item even if it has been filtered out, pass the second parameter as `true`.
   * @param {*} id The `id` to find.
   * @param {Boolean} [ignoreFilters=false] Pass `true` to return an item even if it is filtered out.
   * @returns {Object} The found item, or `undefined`.
   */

  get(id, ignoreFilters = false) {
    return this.getBy(this.idProperty, id, ignoreFilters);
  }

  getAt(index, ignoreFilters = false) {
    if (this.isFiltered && !ignoreFilters) {
      return this._filteredValues[index];
    } else {
      return this._values[index];
    }
  }
  /**
   * Returns the item with passed property name equal to the passed value. By default,
   * filtered are honoured, and if the item with the requested `id` is filtered out,
   * nothing will be returned.
   *
   * To return the item even if it has been filtered out, pass the third parameter as `true`.
   * @param {String} propertyName The property to test.
   * @param {*} value The value to find.
   * @param {Boolean} [ignoreFilters=false] Pass `true` to return an item even if it is filtered out.
   * @returns {Object} The found item, or `undefined`.
   */

  getBy(propertyName, value, ignoreFilters = false) {
    return this.findItem(propertyName, value, this.isFiltered && ignoreFilters);
  }
  /**
   * The number of items in this collection. Note that this honours filtering.
   * See {@link #property-totalCount};
   * @property {Number}
   * @readonly
   */

  get count() {
    return this.values.length;
  }
  /**
   * The number of items in this collection regardless of filtering.
   * @property {Number}
   * @readonly
   */

  get totalCount() {
    return this._values.length;
  }
  /**
   * The property name used to extract item `id`s from added objects.
   * @member {String} idProperty
   */

  updateIdProperty(idProperty) {
    this.addIndex({
      property: idProperty,
      unique: true
    });
  } //endregion
  //region Sorting

  /**
   * The Collection of {@link Core.util.CollectionSorter Sorters} for this Collection.
   * @member {Core.util.Collection} sorters
   */

  changeSorters(sorters) {
    return new Collection({
      values: Array.isArray(sorters) ? sorters : [sorters],
      listeners: {
        change: 'onSortersChanged',
        thisObj: this
      }
    });
  }
  /**
   * Adds a Sorter to the Collection of Sorters which are operating on this Collection.
   *
   * A Sorter may be an specified as an instantiated {@link Core.util.CollectionSorter
   * CollectionSorter}, or a config object for a CollectionSorter of the form
   *
   *     {
   *         property  : 'age',
   *         direction : 'desc'
   *     }
   *
   * Note that by default, a Sorter *replaces* a Sorter with the same `property` to make
   * it easy to change existing Sorters. A Sorter's `id` is its `property` by default. You
   * can avoid this and add multiple Sorters for one property by configuring Sorters with `id`s.
   *
   * A Sorter may also be specified as a function which compares two objects eg:
   *
   *     (lhs, rhs) => lhs.customerDetails.age - rhs.customerDetails.age
   *
   * @param {Object} sorter A Sorter of Sorter configuration object to add to the Collection
   * of Sorters operating on this Collection.
   * @returns {Core.util.CollectionSorter} The resulting Sorter to make it easy to remove Sorters.
   */

  addSorter(sorter) {
    const result = sorter instanceof CollectionSorter ? sorter : new CollectionSorter(sorter);
    this.sorters.add(result);
    return result;
  }
  /**
   * A flag which is `true` if this Collection has active {@link #property-sorters}.
   * @property {Boolean}
   * @readonly
   */

  get isSorted() {
    var _this$_sorters;

    return Boolean((_this$_sorters = this._sorters) === null || _this$_sorters === void 0 ? void 0 : _this$_sorters.count);
  }

  onSortersChanged() {
    const me = this;
    delete me._sortFunction;

    me._values.sort(me.sortFunction);

    me.trigger('change', sortEvent);
  }
  /**
   * A sorter function which encapsulates the {@link Core.util.CollectionSorter Sorters}
   * for this Collection.
   * @property {Function}
   * @readonly
   */

  get sortFunction() {
    if (!this._sortFunction) {
      if (this.isSorted) {
        this._sortFunction = CollectionSorter.generateSortFunction(this.sorters.values);
      } else {
        this._sortFunction = return0;
      }
    }

    return this._sortFunction;
  } //endregion
  //region Filtering

  /**
   * The Collection of {@link Core.util.CollectionFilter Filters} for this Collection.
   * @property {Core.util.Collection}
   * @readonly
   */

  get filters() {
    if (!this._filters) {
      this._filters = new Collection({
        listeners: {
          change: 'onFiltersChanged',
          thisObj: this
        }
      });
    }

    return this._filters;
  }
  /**
   * Adds a Filter to the Collection of Filters which are operating on this Collection.
   *
   * A Filter may be an specified as an instantiated {@link Core.util.CollectionFilter
   * CollectionFilter}, or a config object for a CollectionFilter of the form
   *
   *     {
   *         property : 'age',
   *         operator : '>=',
   *         value    : 21
   *     }
   *
   * Note that by default, a Filter *replaces* a Filter with the same `property` to make
   * it easy to change existing Filters. A Filter's `id` is its `property` by default. You
   * can avoid this and add multiple Filters for one property by configuring Filters with `id`s.
   *
   * A Filter may also be specified as a function which filters candidate objects eg:
   *
   *     candidate => candidate.customerDetails.age >= 21
   *
   * @param {Object} filter A Filter or Filter configuration object to add to the Collection
   * of Filters operating on this Collection.
   * @returns {Core.util.CollectionFilter} The resulting Filter to make it easy to remove Filters.
   */

  addFilter(filter) {
    const result = filter instanceof CollectionFilter ? filter : new CollectionFilter(filter);
    this.filters.add(result);
    return result;
  }
  /**
   * A flag which is `true` if this Collection has active {@link #property-filters}.
   * @property {Boolean}
   * @readonly
   */

  get isFiltered() {
    return Boolean(this._filters && this._filters.count);
  }

  onFiltersChanged() {
    const me = this;
    me._filterFunction = null;
    me._filteredValues = me._values.filter(me.filterFunction);
    me._indicesInvalid = true;
    me.trigger('change', filterEvent);
  }
  /**
   * A filter function which encapsulates the {@link Core.util.CollectionFilter Filters}
   * for this Collection.
   * @property {Function}
   * @readonly
   */

  get filterFunction() {
    if (!this._filterFunction) {
      if (this.isFiltered) {
        this._filterFunction = CollectionFilter.generateFiltersFunction(this.filters.values);
      } else {
        this._filterFunction = FunctionHelper.returnTrue;
      }
    }

    return this._filterFunction;
  } //endregion
  //region Indexing

  changeExtraKeys(extraKeys) {
    const keys = !Array.isArray(extraKeys) ? [extraKeys] : extraKeys; // Normalize to always be an array of index configs

    return keys.map(config => {
      if (typeof config === 'string') {
        return {
          property: config,
          unique: true
        };
      }

      return config;
    });
  }

  updateExtraKeys(extraKeys) {
    for (let i = 0; i < extraKeys.length; i++) {
      this.addIndex(extraKeys[i]);
    }
  }
  /**
   * Adds a lookup index for the passed property name or index config. The index is built lazily when an index is
   * searched
   * @internal
   * @param {Object} indexConfig An index config
   * @param {String} indexConfig.property The property name to add an index for
   * @param {Boolean} [indexConfig.unique] Specify `false` to allow multiple entries of the same index, turning
   *   entries into sets
   */

  addIndex(indexConfig) {
    // Combo without valueField used in some tests -> addIndex(undefined). Safeguarding here
    if (indexConfig) {
      (this._indices || (this._indices = {}))[indexConfig.property] = new Map(); // Piggyback the index config

      Object.assign(this._indices[indexConfig.property], indexConfig); // Indices need a rebuild now.

      this._indicesInvalid = true;
      /**
       * this.indices is keyed by the property name, and contains the keys linked to an item in the _values array.
       * So collection.add({id : foo, name : 'Nige'}, {id : 'bar', name : 'Faye'}) where collection has had an index
       * added for the "name" property would result in:
       *
       * {
       *     id : Map({
       *         foo : nige,
       *         bar : faye
       *     }),
       *     name : Map({
       *         Nige : nige,
       *         Faye : faye
       *     })
       * }
       */
    }
  }
  /**
   * Return the index of the item with the specified key having the specified value.
   *
   * By default, filtering is taken into account and this returns the index in the filtered dataset if present. To
   * bypass this, pass the third parameter as `true`.
   *
   * Only useful for indices configured with `unique: true`.
   *
   * @param {String} propertyName The name of the property to test.
   * @param {*} value The value to test for.
   * @param {Boolean} [ignoreFilters=false] Pass `true` to return the index in
   * the original data set if the item is filtered out.
   * @returns {Number} The index of the item or `-1` if not found for unique indices
   */

  findIndex(propertyName, value, ignoreFilters = false) {
    const item = this.findItem(propertyName, value, ignoreFilters);

    if (!item) {
      return -1;
    }

    const values = this.isFiltered && !ignoreFilters ? this._filteredValues : this._values;
    return values.indexOf(item);
  }
  /**
   * Return the item with the specified key having the specified value.
   *
   * By default, filtering is taken into account. To bypass this, pass the third parameter as `true`.
   *
   * For indices configured with `unique: false`, a Set of items will be returned.
   *
   * @param {String} propertyName The name of the property to test.
   * @param {*} value The value to test for.
   * @param {Boolean} [ignoreFilters=false] Pass `true` to return the index in
   * the original data set if the item is filtered out.
   * @returns {Object|Set} The found item or Set of items or null
   */

  findItem(propertyName, value, ignoreFilters = false) {
    const me = this,
          {
      isFiltered
    } = me,
          index = isFiltered && !ignoreFilters ? me.indices[filteredIndicesProperty][propertyName] : me.indices[propertyName];

    if (index) {
      var _index$get;

      // If the key is numeric, cast it to a number and find.
      // Store's idRegister is an object which treats numeric keys as strings
      // but Maps are more picky, so we have to work round that.
      const itemIndex = (_index$get = index.get(value)) !== null && _index$get !== void 0 ? _index$get : typeof value === 'string' && !isNaN(value) && index.get(Number(value)) || null;

      if (itemIndex != null) {
        return itemIndex;
      }
    } else {
      // Search the filtered values if we are filtered and not ignoring filters
      const values = isFiltered && !ignoreFilters ? me._filteredValues : me._values,
            count = values.length;

      for (let i = 0; i < count; i++) {
        const item = values[i];

        if (item[propertyName] == value) {
          return item;
        }
      }
    }

    return null;
  }
  /**
   * Returns the index of the item with the same `id` as the passed item.
   *
   * By default, filtering is honoured, so if the item in question has been added, but is currently filtered out of
   * visibility, `-1` will be returned.
   *
   * To find the index in the master, unfiltered dataset, pass the second parameter as `true`;
   * @param {Object|String|Number} item The item to find, or an `id` to find.
   * @param {Boolean} [ignoreFilters=false] Pass `true` to find the index in the master, unfiltered data set.
   * @returns {Number} The index of the item, or `-1` if not found.
   */

  indexOf(item, ignoreFilters = false) {
    return this.findIndex(this.idProperty, keyTypes[typeof item] ? item : item[this.idProperty], ignoreFilters);
  }
  /**
   * Returns `true` if this Collection includes an item with the same `id` as the passed item.
   *
   * By default, filtering is honoured, so if the item in question has been added,
   * but is currently filtered out of visibility, `false` will be returned.
   *
   * To query inclusion in the master, unfiltered dataset, pass the second parameter as `true`;
   * @param {Object|String|Number} item The item to find, or an `id` to find.
   * @param {Boolean} [ignoreFilters=false] Pass `true` to find the index in the master, unfiltered data set.
   * @returns {Boolean} True if the passed item is found.
   */

  includes(item, ignoreFilters = false) {
    if (Array.isArray(item)) {
      return item.some(item => this.includes(item));
    }

    return Boolean(this.findItem(this.idProperty, keyTypes[typeof item] ? item : item[this.idProperty], ignoreFilters));
  }

  get indices() {
    if (this._indicesInvalid) {
      this.rebuildIndices();
    }

    return this._indices;
  }
  /**
   * Called when the Collection is mutated and the indices have been flagged as invalid.
   *
   * Rebuilds the indices object to allow lookup by keys.
   * @internal
   */

  rebuildIndices() {
    const me = this,
          isFiltered = me.isFiltered,
          indices = me._indices || (me._indices = {}),
          keyProps = Object.keys(indices),
          indexCount = keyProps.length,
          values = me._values;
    let filteredIndices;

    if (isFiltered) {
      filteredIndices = indices[filteredIndicesProperty] = {};
    } // First, clear indices.

    for (let i = 0; i < indexCount; i++) {
      const index = indices[keyProps[i]];
      index.clear();

      if (isFiltered) {
        let filteredIndex = filteredIndices[keyProps[i]];

        if (filteredIndex) {
          filteredIndex.clear();
        } else {
          filteredIndex = filteredIndices[keyProps[i]] = new Map(); // Piggyback config

          filteredIndex.unique = index.unique;
        }
      }
    }

    doRebuildIndices(values, indices, keyProps, indexCount); // Create a parallel lookup structure into the _filteredValues

    if (isFiltered) {
      doRebuildIndices(me._filteredValues, filteredIndices, keyProps, indexCount);
    }

    me._indicesInvalid = false;
  } // Returns an array with [indices] or [indices, filteredIndices] if filtering is used

  getIndices(propertyName) {
    const indices = [this.indices[propertyName]];

    if (this.isFiltered) {
      indices.push(this.indices[filteredIndicesProperty][propertyName]);
    }

    return indices;
  }
  /**
   * Add an item to all indices
   * @param {*} item Item already available in the Collection
   * @private
   */

  addToIndices(item) {
    Object.keys(this.indices).forEach(propertyName => {
      this.addIndexEntry(item, propertyName, item[propertyName]);
    });
  }
  /**
   * Remove an item from all indices
   * @param {*} item Item already available in the Collection
   * @private
   */

  removeFromIndices(item) {
    Object.keys(this.indices).forEach(propertyName => {
      this.removeIndexEntry(item, propertyName, item[propertyName]);
    });
  }
  /**
   * Remove an entry from an index, and if filtering is used also from the filtered index.
   * @param {*} item Item already available in the Collection
   * @param {String} propertyName Property of the item, will be matched with configured indices
   * @param {*} oldValue Value to remove
   * @private
   */

  removeIndexEntry(item, propertyName, oldValue) {
    this.getIndices(propertyName).forEach(index => removeItemFromIndex(item, index, oldValue));
  }
  /**
   * Add a new entry to an index, and if filtering is used also to the filtered index.
   * @param {*} item Item already available in the Collection
   * @param {String} propertyName Property of the item, will be matched with configured indices
   * @param {*} value Value to store
   * @private
   */

  addIndexEntry(item, propertyName, value) {
    this.getIndices(propertyName).forEach(index => addItemToIndex(item, index, value));
  }
  /**
   * Call externally to update indices on item mutation (from Store)
   * @param {*} item Item already available in the Collection
   * @param {Object} wasSet Uses the `wasSet` format from Store, `{ field : { oldValue, newValue } }`
   * @internal
   */

  onItemMutation(item, wasSet) {
    // Iterate over changes if we have extra indices defined, keeping those indices up to date
    if (this.extraKeys && !this._indicesInvalid) {
      Object.keys(wasSet).forEach(propertyName => {
        if (this.indices[propertyName]) {
          const {
            value,
            oldValue
          } = wasSet[propertyName];
          this.removeIndexEntry(item, propertyName, oldValue);
          this.addIndexEntry(item, propertyName, value);
        }
      });
    }
  } //endregion

} // These are used by Bag for the same purpose
Collection._$name = 'Collection';

const nonPrimitives = new WeakMap(),
      safeIndexKey = value => {
  if (value && typeof value === 'object') {
    let substitute = nonPrimitives.get(value);

    if (substitute === undefined) {
      substitute = Symbol('bscik'); // Bryntum safe collection index key

      nonPrimitives.set(value, substitute);
    }

    value = substitute;
  }

  return value;
};
/**
 * @module Core/util/Bag
 */

/**
 * A simple collection class to contain unique, keyed items.
 */

class Bag {
  constructor(config) {
    const me = this;
    me.generation = 0;
    me.items = new Set();
    me.idMap = {};
    me.idProperty = 'id';

    if (config) {
      if (config.idProperty) {
        me.idProperty = config.idProperty;
      }

      if (config.values) {
        me.values = config.values;
      }
    }
  }
  /**
   * Returns the item with the passed `id`.
   *
   * @param {*} id The `id` to find.
   * @returns {Object} The found item, or `undefined`.
   */

  get(key) {
    return this.idMap[safeIndexKey(key)];
  }
  /**
   * The number of items in this Bag.
   * @property {Number}
   * @readonly
   */

  get count() {
    return this.items.size;
  }
  /**
   * Adds the passed item(s) to this Bag. Existing items with the same ID
   * will be replaced.
   * @param {Object|Object[]} toAdd Item(s) to add.
   */

  add(...toAdd) {
    if (toAdd.length === 1 && Array.isArray(toAdd[0])) {
      toAdd = toAdd[0];
    }

    const me = this,
          {
      items,
      idMap,
      idProperty
    } = me,
          len = toAdd.length;

    for (let i = 0; i < len; i++) {
      const item = toAdd[i],
            key = keyTypes[typeof item] ? item : safeIndexKey(item[idProperty]),
            existingItem = idMap[key]; // Not already present

      if (existingItem == null) {
        idMap[key] = item;
        items.add(item);
        me.generation++;
      } // Already present, replace it if different. No generational change.
      // IDs rule.
      else if (existingItem !== item) {
          idMap[key] = item;
          items.delete(existingItem);
          items.add(item);
        }
    }
  }
  /**
   * Removes the passed item(s) from this Bag.
   * @param {Object|Object[]} toRemove Item(s) to remove.
   */

  remove(toRemove) {
    if (!Array.isArray(toRemove)) {
      toRemove = [toRemove];
    }

    const {
      items,
      idMap,
      idProperty
    } = this,
          len = toRemove.length;

    for (let i = 0; i < len; i++) {
      const item = toRemove[i],
            key = keyTypes[typeof item] ? item : safeIndexKey(item[idProperty]),
            existingItem = idMap[key]; // Found natch, so delete it

      if (existingItem != null) {
        items.delete(existingItem);
        delete idMap[key];
        this.generation++;
      }
    }
  }

  clear() {
    this.items.clear();
    this.idMap = {};
    this.generation++;
  }
  /**
   * Change the id of an existing member by mutating its idProperty.
   * @param {String|Number|Object} item The item or id of the item to change.
   * @param {String|Number} newId The id to set in the existing member.
   */

  changeId(item, newId) {
    const me = this,
          {
      idMap,
      idProperty
    } = me,
          oldId = keyTypes[typeof item] ? item : safeIndexKey(item[idProperty]),
          member = me.get(oldId);

    if (member) {
      const existingMember = me.get(newId);

      if (existingMember && member !== existingMember) {
        throw new Error(`Attempt to set item ${oldId} to already existing member's id ${newId}`);
      }

      member[idProperty] = newId;
      delete idMap[oldId];
      idMap[newId] = member;
    }
  }
  /**
   * Returns `true` if this Collection includes an item with the same `id` as the passed item.
   *
   * @param {Object|String|Number} item The item to find, or an `id` to find.
   * @returns {Boolean} True if the passed item is found.
   */

  includes(item) {
    const key = keyTypes[typeof item] ? item : safeIndexKey(item[this.idProperty]);
    return Boolean(this.idMap[key]);
  }
  /**
   * Extracts the content of this Bag into an array based upon the passed
   * value extraction function.
   * @param {Function} fn A function, which, when passed an item, returns a value to place into the resulting array.
   * @param {Object} [thisObj] The `this` reference when the function is called.
   * @returns {Object[]} An array of values extracted from this Bag.
   */

  map(fn, thisObj) {
    const {
      items
    } = this,
          result = new Array(items.size); // Set has no map

    let i = 0;
    items.forEach(item => {
      result[i] = fn.call(thisObj, item, i++, items);
    });
    return result;
  }
  /**
   * Executes the passed function for each item in this Bag, passing in the item.
   * @param {Function} fn The function to execute.
   * @param {Object} [thisObj] The `this` reference when the function is called.
   */

  forEach(fn, thisObj) {
    return this.items.forEach(fn, thisObj);
  }
  /**
   * Returns the first item in this Bag which elicits a *truthy* return value from the passed funtion.
   * @param {Function} fn A function, which, when passed an item, returns `true` to select it as the item to return.
   * @returns {Object} The matched item, or `undefined`.
   */

  find(fn) {
    for (const div of this.items) {
      if (fn(div)) {
        return div;
      }
    }

    return undefined;
  }
  /**
   * Iterator that allows you to do for (let item of bag)
   */

  [Symbol.iterator]() {
    return this.items[Symbol.iterator]();
  }

  indexOf(item) {
    return this.values.indexOf(item);
  }
  /**
   * The set of values of this Bag.
   *
   * Setting this property replaces the data set.
   * @property {Object[]}
   */

  get values() {
    return [...this.items];
  }

  set values(values) {
    if (!Array.isArray(values)) {
      values = [values];
    }

    this.clear();
    this.add.apply(this, values);
    this.generation++;
  }
  /**
   * Sort the values of this Bag using the passed comparison function.
   *
   * Setting this property replaces the data set.
   * @param {Function} fn Comparison function which returns -ve, 0, or +ve
   */

  sort(fn) {
    this.values = this.values.sort(fn);
  }

  some(fn, thisObj) {
    return this.values.some(fn, thisObj);
  }

}
Bag._$name = 'Bag';

/**
 * @module Core/data/StoreBag
 */

class StoreBag extends Bag {
  add(...toAdd) {
    if (toAdd.length === 1 && Array.isArray(toAdd[0])) {
      toAdd = toAdd[0];
    } // ignore unpersistable records

    return super.add(...toAdd.filter(record => record.isPersistable));
  }

}
StoreBag._$name = 'StoreBag';

/**
 * @module Core/data/mixin/StoreCRUD
 */

/**
 * Mixin for Store that handles modifying records (add, remove etc).
 *
 * @example
 * // add new record to store
 * store.add({ id: 1, team: 'FC Krasnodar' });
 *
 * // remove a record from store, using id
 * store.remove(1);
 *
 * @mixin
 */

var StoreCRUD = (Target => class StoreCRUD extends (Target || Base) {
  static get $name() {
    return 'StoreCRUD';
  } //region Config

  static get defaultConfig() {
    return {
      /**
       * Commit changes automatically
       * @config {Boolean}
       * @default
       * @category Common
       */
      autoCommit: false,
      suspendCount: 0
    };
  } //endregion
  //region Events

  /**
   * Fired after removing all records
   * @event removeAll
   * @param {Core.data.Store} source This Store
   */

  /**
   * Fired before committing changes. Return false from handler to abort commit
   * @event beforeCommit
   * @param {Core.data.Store} source This Store
   * @param {Object} changes Modification data
   */

  /**
   * Fired after committing changes
   * @event commit
   * @param {Core.data.Store} source This Store
   * @param {Object} changes Modification data
   */

  /**
   * Fired before records are removed from this store by the {@link #function-remove} or {@link #function-removeAll}.
   * Also fired when removing a child record in a tree store using {@link Core.data.mixin.TreeNode#function-removeChild}.
   * The remove may be vetoed by returning `false` from a handler.
   * @event beforeRemove
   * @param {Core.data.Store} source This Store
   * @param {Core.data.Model[]} records The records which are to be removed.
   * @param {Core.data.Model} parent The record from which children are being removed when using a tree store
   * @param {Boolean} isMove Set to `true` if the child node is being removed by
   * {@link Core.data.mixin.TreeNode#function-appendChild appendChild} to be moved
   * _within the same tree_.
   * @param {Boolean} removingAll Set to `true` if the operation is removing the store's entire data set.
   */

  /**
   * Fired before records are added to this store by the {@link #function-add} or {@link #function-insert}. In a tree
   * store, also fired by {@link Core.data.mixin.TreeNode#function-appendChild} and
   * {@link Core.data.mixin.TreeNode#function-insertChild}. The add or insert may be vetoed by returning `false`
   * from a handler.
   * @event beforeAdd
   * @param {Core.data.Store} source This Store
   * @param {Core.data.Model[]} records The records which are to be added
   * @param {Core.data.Model} parent The parent node when using a tree store
   * @preventable
   */

  /**
   * Fired after adding/inserting record(s). If the record was added to a parent, the `isChild` flag is set on the
   * event. If it was inserted, event contains `index`
   * @event add
   * @param {Core.data.Store} source This Store
   * @param {Core.data.Model[]} records Added records. In case of tree store, if branch is added, only branch root
   * is returned
   * @param {Core.data.Model[]} [allRecords] Flat list of all added records. In case of tree store, if branch is
   * added, all new records are returned, not only branch root
   * @param {Core.data.Model} [parent] If due to an {@link Core.data.mixin.TreeNode#function-appendChild appendChild}
   * call, this is the parent node added to.
   * @param {Number} [index] Insertion point in the store's {@link Core.data.Store#config-storage Collection}.
   * @param {Number} [oldIndex] Not used for tree stores. The index of the first record moved.
   * @param {Boolean} [isChild] Flag which is set to true if the records are added to a parent record
   * @param {Boolean} [isExpand] Flag which is set to true if records are added to the store by expanding parent
   * @param {Object} [isMove] An object keyed by the ids of the records which were moved from another
   * position in the store, or from another parent node in the store. The ids of moved records will be
   * property names with a value `true`.
   */

  /**
   * Fired when a record has been removed
   * @event remove
   * @param {Core.data.Store} source This Store
   * @param {Core.data.Model[]} records Removed records. In case of tree store, if branch is removed, only branch root
   * is returned
   * @param {Core.data.Model[]} [allRecords] Flat list of all removed records. In case of tree store, if branch is
   * removed, all removed records are returned, not only branch root
   * @param {Core.data.Model} [parent] If due to a {@link Core.data.mixin.TreeNode#function-removeChild removeChild}
   * call, this is the parent node removed from.
   * @param {Number} [index] Visible index at which record was removed. In case record is removed from the collapsed
   * branch -1 is returned.
   * @param {Boolean} [isChild] Flag which is set to true if the record is added to a parent record
   * @param {Boolean} [isCollapse] Flag which is set to true if records are removed from the store by collapsing parent
   * @param {Boolean} [isMove] Passed as `true` if the remove was part of a move operation within this Store.
   */
  //endregion
  //region Add, insert & remove

  /**
   * Removes a record from this store.
   * @param {String|String[]|Number|Number[]|Core.data.Model|Core.data.Model[]} records Record/array of records (or record ids) to remove
   * @param {Boolean} [silent] Specify `true` to suppress events/autoCommit
   * @returns {Core.data.Model[]} Removed records
   * @fires beforeRemove
   * @fires remove
   * @fires change
   * @category CRUD
   */

  remove(records, silent = false) {
    const me = this,
          {
      storage
    } = me;

    if (records instanceof Set) {
      records = Array.from(records);
    } // Ensure we have an array of records in case we are passed IDs

    records = (Array.isArray(records) ? records : [records]).reduce((result, r) => {
      r = me.getById(r);

      if (r) {
        result.push(r);
      }

      return result;
    }, []);

    if (records.length) {
      // Flag that can be checked in linked store to determine if removal is ongoing
      me.isRemoving = true;

      if (me.tree) {
        // In case it's a set of records from different parents, group them by parent
        const removeChildArgs = records.reduce((result, child) => {
          const parent = child.parent;

          if (parent) {
            if (!result[parent.id]) {
              result[parent.id] = [parent, []];
            }

            result[parent.id][1].push(child);
          }

          return result;
        }, {}); // Remove the records each from their correct parent

        for (const argBlock of Object.values(removeChildArgs)) {
          argBlock[0].removeChild(argBlock[1], false, silent);
        }

        me.isRemoving = false;
        return records;
      } // Ensure that records in collapsed groups are removed and events fired.
      else if (me.isGrouped) {
          const oldCount = storage.count,
                recordsInCollapsedGroups = [],
                changedGroupParents = new Set(); // Collect any records which are in collapsed groups which will not be in the storage
          // Collection, and so will not cause a store change.
          // If we find some, we must fire the event which the store will react to and update itself.

          for (const rec of records) {
            const {
              groupParent
            } = rec.instanceMeta(me);

            if (groupParent && groupParent.meta.collapsed) {
              recordsInCollapsedGroups.push(rec);
            } // Collect group header records which change so they can announce this to cause UI updates.

            ArrayHelper.remove(groupParent.groupChildren, rec);
            groupParent.meta.childCount--;
            changedGroupParents.add(groupParent);
          } // The changed group parents must announce their changes, otherwise the group headers
          // in a UI will not refresh to reflect the change.

          for (const groupParent of changedGroupParents) {
            if (groupParent.groupChildren.length > 0) {
              me.onModelChange(groupParent, {}, {});
            }
          } // The store must react as if these records in collapsed groups have been removed from storage

          if (recordsInCollapsedGroups.length) {
            storage.trigger('change', {
              action: 'splice',
              removed: recordsInCollapsedGroups,
              added: [],
              replaced: [],
              oldCount
            });
          }
        } // Give chance to veto or take action before records disappear.

      if (!records.length || !silent && me.trigger('beforeRemove', {
        records
      }) === false) {
        me.isRemoving = false;
        return null;
      }

      if (silent) {
        me.suspendEvents();
      }

      storage.remove(records);

      if (silent) {
        me.resumeEvents();
      }

      if (me.autoCommit) {
        me.doAutoCommit();
      }

      me.isRemoving = false;
    }

    return records;
  }
  /**
   * Clears store data. Used by removeAll, separate function for using with chained stores.
   * @private
   * @category CRUD
   */

  clear(removing = true) {
    const me = this,
          {
      storage
    } = me;

    if (me.storage.totalCount) {
      // Give chance to veto or take action before records disappear.
      if (removing && me.trigger('beforeRemove', {
        records: storage.allValues,
        removingAll: true
      }) === false) {
        return null;
      }

      if (!removing) {
        // If !removing, we suspend events, and Store#onDataChange won't get to
        // do all this stuff.
        const allRecords = me.registeredRecords;

        for (let i = allRecords.length - 1, rec; i >= 0; i--) {
          rec = allRecords[i];

          if (rec && !rec.isDestroyed) {
            rec.unJoinStore(me);
          }
        }

        me.removed.clear();
      }

      if (!removing) storage.suspendEvents(); // Clearing the storage will trigger 'removeAll' and 'change' if `removing`

      storage.clear();
      if (!removing) storage.resumeEvents();
      me.added.clear();
      me.modified.clear();
    }
  }
  /**
   * Removes all records from the store.
   * @param {Boolean} [silent] Specify `true` to suppress events
   * @returns {Boolean} `true` unless the action was prevented, in which case it returns `false`
   * @fires beforeRemove
   * @fires removeAll
   * @fires change
   * @category CRUD
   */

  removeAll(silent = false) {
    const me = this,
          storage = me.storage;
    let result;
    me.isRemoving = true; // No reaction to the storage Collection's change event.

    if (silent) {
      storage.suspendEvents(); // If silent, the storage Collection won't fire the event we react to
      // to unjoin, and we allow the removing flag in remove() to be true,
      // so *it* will not do the unJoin, so if silent, so do it here.

      const allRecords = me.registeredRecords;

      for (let i = allRecords.length - 1, rec; i >= 0; i--) {
        rec = allRecords[i];

        if (rec && !rec.isDestroyed) {
          rec.unJoinStore(me);
        }
      }
    }

    if (me.tree) {
      result = me.rootNode.clear() !== false;
    } else {
      result = me.clear() !== null;
    }

    if (silent) {
      storage.resumeEvents();
    }

    me.isRemoving = false;
    return result;
  }
  /**
   * Add records to store.
   * @param {Core.data.Model|Core.data.Model[]|Object|Object[]} records Array of records/data or a single record/data to add to store
   * @param {Boolean} [silent] Specify `true` to suppress events
   * @returns {Core.data.Model[]} Added records
   * @fires add
   * @fires change
   * @category CRUD
   */

  add(records, silent = false) {
    const me = this,
          storage = me.storage;

    if (!Array.isArray(records)) {
      records = [records];
    } else if (!records.length) {
      // Adding zero records, bail out
      return;
    }

    me.tree = me.tree || Boolean(me.autoTree && records[0].children);

    if (me.tree) {
      const // Map and not Object to allow keys to keep their type
      parentIdMap = new Map(),
            added = [];
      records.forEach(node => {
        const parentId = node[me.modelClass.parentIdField];

        if (!parentIdMap.has(parentId)) {
          parentIdMap.set(parentId, []);
        }

        parentIdMap.get(parentId).push(node);
      });
      parentIdMap.forEach((nodes, parentId) => {
        const parentNode = parentId == null ? me.rootNode : me.getById(parentId);

        if (!parentNode) {
          throw new Error(`Parent node with id ${parentId} not found, cannot add children.`);
        }

        added.push(...parentNode.appendChild(nodes, silent));
      });
      return added;
    } // Give chance to cancel action before records added.

    if (!silent) {
      if (me.trigger('beforeAdd', {
        records
      }) === false) {
        return null;
      }
    }

    me.tree = me.tree || Boolean(me.autoTree && records[0].children);

    if (me.tree) {
      return me.rootNode.appendChild(records);
    }

    const added = me.processRecords(records);

    if (silent) {
      me.suspendEvents();
    }

    storage.add(added);

    if (silent) {
      me.resumeEvents();
    }

    if (me.autoCommit) {
      me.doAutoCommit();
    }

    return added;
  }

  processRecords(records, onRecordCreated) {
    return records.map(data => {
      const record = this.processRecord(data.isModel ? data : this.createRecord(data));
      onRecordCreated === null || onRecordCreated === void 0 ? void 0 : onRecordCreated.call(this, record);
      return record;
    });
  }
  /**
   * Insert records to store.
   * @param {Number} index Index to insert at
   * @param {Core.data.Model|Core.data.Model[]|Object|Object[]} records Array of records/data or a single record/data to insert to store
   * @param {Boolean} [silent] Specify `true` to suppress events
   * @returns {Core.data.Model[]} Inserted records
   * @fires add
   * @fires change
   * @category CRUD
   */

  insert(index, records, silent = false) {
    const me = this,
          storage = me.storage,
          insertBefore = me.getAt(index),
          _records = storage.values,
          removeIndices = [];
    if (!Array.isArray(records)) records = [records]; // Give chance to cancel action before records added.

    if (me.trigger('beforeAdd', {
      records
    }) === false) {
      return null;
    }

    let isNoop, start, i; // If the records starting at index or (index - 1), are the same sequence
    // that we are being asked to add, this is a no-op.

    if (_records[start = index] === records[0] || _records[start = index - 1] === records[0]) {
      for (isNoop = true, i = 0; isNoop && i < records.length; i++) {
        if (records[i] !== _records[start + i]) {
          isNoop = false;
        }
      }
    }

    if (isNoop) {
      return null;
    }

    const added = me.processRecords(records, record => {
      const removedAtIndex = storage.indexOf(record);

      if (record.children && record.children.length && me.autoTree) {
        me.tree = true;
      } // already in store, do some cleanup

      if (removedAtIndex > -1) {
        if (removedAtIndex < index && insertBefore) index--;
        removeIndices.push(removedAtIndex);
      } // Store previous index to be able to determine that it is a move, since Collection does not handle that

      record.meta.previousIndex = removedAtIndex;
    });

    if (me.tree) {
      const root = me.rootNode;
      return root.insertChild(records, root.children && root.children[index]);
    } // Silently remove them, so that they will be inserted into place.
    // Collection is stable by default, and inserting an existing item
    // is a no-op.

    me.suspendEvents();
    me.storage.remove(removeIndices);
    me.resumeEvents();

    if (silent) {
      me.suspendEvents();
    }

    storage.splice(index, 0, ...added);

    if (silent) {
      me.resumeEvents();
    }

    if (me.autoCommit) {
      me.doAutoCommit();
    }

    return added;
  }
  /**
   * Moves a record, or block of records to another location.
   * @param {Core.data.Model|Core.data.Model[]} records The record(s) to move.
   * @param {Core.data.Model} beforeRecord the record to insert the first record(s) before.
   * @category CRUD
   */

  move(records, beforeRecord) {
    this.storage.move(records, beforeRecord);
  } //endregion
  //region Update multiple

  setMultiple(filterFn, field, value) {
    const me = this,
          records = [],
          changes = [];
    me.forEach(r => {
      if (filterFn(r)) {
        changes.push(r.set(field, value, true));
        records.push(r);
      }
    }); // TODO: should consolidate with update, make it take an array instead? to only have to listen for one event outside of store?

    me.trigger('updateMultiple', {
      records,
      all: me.records.length === records.length
    });
    me.trigger('change', {
      action: 'updatemultiple',
      records,
      all: me.records.length === records.length
    });
    if (me.reapplyFilterOnUpdate && me.isFiltered) me.filter();
  }

  setAll(field, value) {
    const me = this,
          changes = [];
    me.forEach(r => {
      changes.push(r.set(field, value, true));
    });
    me.trigger('updateMultiple', {
      records: me.records,
      all: true
    });
    me.trigger('change', {
      action: 'updatemultiple',
      records: me.records,
      all: true
    });
    if (me.reapplyFilterOnUpdate && me.isFiltered) me.filter();
  } //endregion
  //region Commit

  /**
   * Accepts all changes, resets the modification tracking:
   * * Clears change tracking for all records
   * * Clears added
   * * Clears modified
   * * Clears removed
   * Leaves the store in an "unmodified" state.
   * @internal
   */

  acceptChanges() {
    const me = this; // Clear record change tracking

    me.added.forEach(r => r.clearChanges(false));
    me.modified.forEach(r => r.clearChanges(false)); // Clear store change tracking

    me.added.clear();
    me.modified.clear();
    me.removed.clear();
  }
  /**
   * Commits changes, per default only returns changes and resets tracking.
   * @param {Boolean} [silent] Specify `true` to suppress events
   * @returns {Object} Changes, see {@link #property-changes}
   * @fires beforeCommit
   * @fires commit
   * @category CRUD
   */

  commit(silent = false) {
    // resets stores tracking of changed records, doesn't store changes, only returns them
    const {
      changes
    } = this;
    return this.callPreventable('commit', {
      changes
    }, () => {
      this.acceptChanges();
      return changes;
    }, [], silent);
  }
  /**
   * Discards changes in the store.
   * @fires change
   * @category CRUD
   * @private
   */
  // TODO: Need to think of its name. `reject` is a candidate.

  clearChanges() {
    const me = this,
          {
      changes
    } = me;
    me.remove(me.added.values, true);
    me.modified.forEach(r => r.clearChanges(false)); // TODO: removed records should be restored

    me.added.clear();
    me.modified.clear();
    me.removed.clear();
    me.trigger('change', {
      action: 'clearchanges',
      changes
    });
  }
  /**
   * Get uncommitted changes as an object of added/modified/removed arrays of records.
   *
   * ```javascript
   * // Format:
   * {
   *      added: [], // array of Core.data.Model
   *      modified: [], // array of Core.data.Model
   *      removed: [] // array of Core.data.Model
   * }
   * ```
   *
   * @type {Object}
   * @property {Object} changes
   * @property {Core.data.Model[]} changes.added Records that have been added
   * @property {Core.data.Model[]} changes.modified Records that have changes to persistable fields
   * @property {Core.data.Model[]} changes.removed Records that have been removed
   * @readonly
   * @category Records
   */

  get changes() {
    const me = this,
          // Only include modified records that has persistable changes
    modified = me.modified.values.filter(r => r.rawModifications);
    return me.added.count || modified.length || me.removed.count ? {
      // Slicing to have changes intact when triggering commit
      added: me.added.values.slice(),
      modified,
      removed: me.removed.values.slice()
    } : null;
  }
  /**
   * Setting autoCommit to true automatically commits changes to records.
   * @property {Boolean}
   * @category Records
   */

  get autoCommit() {
    return this._autoCommit;
  }

  set autoCommit(auto) {
    this._autoCommit = auto;

    if (auto && this.changes) {
      this.commit();
    }
  }
  /**
   * Suspends automatic commits upon store changes. Can be called multiple times (it uses an internal counter).
   * @category Records
   */

  suspendAutoCommit() {
    this.suspendCount++;
  }
  /**
   * Resumes automatic commits upon store changes. Will trigger commit if the internal counter is 0.
   * @category Records
   */

  resumeAutoCommit(doCommit = true) {
    this.suspendCount--;

    if (this.autoCommit && doCommit) {
      this.doAutoCommit();
    }
  }

  doAutoCommit() {
    if (this.suspendCount <= 0) {
      this.commit();
    }
  } //endregion
  //region Changes from other store

  /**
   * Applies changes from another store to this store. Useful if cloning records in one store to display in a
   * grid in a popup etc. to reflect back changes.
   * @param {Core.data.Store} otherStore
   * @category CRUD
   */

  applyChangesFromStore(otherStore) {
    const me = this,
          changes = otherStore.changes;
    if (!changes) return;

    if (changes.added) {
      me.add(changes.added);
    }

    if (changes.removed) {
      // Remove using id, otherwise indexOf in remove fn won't yield correct result
      me.remove(changes.removed.map(r => r.id));
    }

    if (changes.modified) {
      changes.modified.forEach(record => {
        const localRecord = me.getById(record.id);
        localRecord.set(record.modifications);
      });
    }
  } //endregion

});

/**
 * @module Core/data/mixin/StoreFilter
 */

/**
 * Mixin for Store that handles filtering.
 * Filters are instances of {@link Core.util.CollectionFilter CollectionFilter} class.
 *
 * - Adding a filter for the same property will replace the current one (unless a unique {@link Core.util.CollectionFilter#config-id Id} is specified),
 * but will not clear any other filters.
 * - Adding a filter through the {@link #function-filterBy} function is ultimate.
 * It will clear all the property based filters and replace the current filterBy function if present.
 * - Removing records from the store does not remove filters!
 * The filters will be reapplied if {@link #config-reapplyFilterOnAdd}/{@link #config-reapplyFilterOnUpdate} are true and you add new records or update current.
 *
 * ```
 * // Add a filter
 * store.filter({
 *     property : 'score',
 *     value    : 10,
 *     operator : '>'
 * });
 *
 * // Add filter as a function
 * store.filter(record => record.score > 10);
 *
 * // Add named filter as a function
 * store.filter({
 *     id : 'my filter',
 *     filterBy : record => record.score > 10
 * });
 *
 * // Replace any filter set with new filters
 * store.filter({
 *     filters : {
 *         property : 'score',
 *         value    : 10,
 *         operator : '>'
 *     },
 *     replace : true
 * });
 *
 * // Remove this one specific filter, leaving any possible others in place.
 * // A filter's id defaults to the property name that it's filtering on.
 * store.removeFilter('score');
 *
 * // Reapply filters without firing an event.
 * // Use if making multiple data mutations with the
 * // intention of updating UIs when all finished.
 * store.filter({
 *     silent : true
 * });
 * ```
 *
 * @mixin
 */

var StoreFilter = (Target => class StoreFilter extends (Target || Base) {
  static get $name() {
    return 'StoreFilter';
  } //region Config

  static get defaultConfig() {
    return {
      /**
       * Specify one or more {@link Core.util.CollectionFilter CollectionFilter} config object to apply initially
       * @config {Object|Object[]}
       * @category Filtering
       */
      filters: null,

      /**
       * Specify true to reapply filters when a record is added to the store.
       * @config {Boolean}
       * @default
       * @category Filtering
       */
      reapplyFilterOnAdd: false,

      /**
       * Specify true to reapply filters when a record is updated in the store.
       * @config {Boolean}
       * @default
       * @category Filtering
       */
      reapplyFilterOnUpdate: false
    };
  } //endregion
  //region Events

  /**
   * Fired after applying filters to the store
   * @event filter
   * @param {Core.data.Store} source This Store
   * @param {Core.util.Collection} filters Filters used by this Store
   * @param {Core.data.Model[]} records Filtered records
   */
  //endregion
  //region Properties

  set reapplyFilterOnAdd(enable) {
    this.storage.autoFilter = enable;
  }

  get reapplyFilterOnAdd() {
    return this.storage.autoFilter;
  }
  /**
   * Currently applied filters. A collection of {@link Core.util.CollectionFilter} instances.
   * @type {Core.util.Collection}
   * @readonly
   * @category Sort, group & filter
   */

  set filters(filters) {
    const me = this,
          collection = me.filters;
    collection.clear(); // Invalidate the filtersFunction so that it has to be recalculated upon next access

    me._filtersFunction = null; // If we are being asked to filter, parse the filters.

    if (filters) {
      if (filters.constructor.name === 'Object') {
        for (const f of Object.entries(filters)) {
          // Entry keys are either a field name with its value being the filter value
          // or, there may be one filterBy property which specifies a filering function.
          if (f[0] === 'filterBy' && typeof f[1] === 'function') {
            collection.add(new CollectionFilter({
              filterBy: f[1]
            }));
          } else {
            collection.add(new CollectionFilter(f[1].constructor.name === 'Object' ? Object.assign({
              property: f[0]
            }, f[1]) : {
              property: f[0],
              value: f[1]
            }));
          }
        }
      } else if (Array.isArray(filters)) {
        // Make sure we are adding CollectionFilters
        collection.add(...filters.map(filterConfig => {
          if (filterConfig instanceof CollectionFilter) {
            return filterConfig;
          }

          return new CollectionFilter(filterConfig);
        }));
      } else if (filters.isCollection) {
        // Use supplied collection? Opting to use items from it currently
        collection.add(...filters.values);
      } else {
        collection.add(new CollectionFilter({
          filterBy: filters
        }));
      }

      collection.forEach(item => item.owner = me);
    }
  }

  get filters() {
    return this._filters || (this._filters = new Collection({
      extraKeys: ['property']
    }));
  }

  set filtersFunction(filtersFunction) {
    this._filtersFunction = filtersFunction;
  }

  get filtersFunction() {
    const me = this,
          {
      filters,
      isGrouped
    } = me;

    if (!me._filtersFunction) {
      if (filters.count) {
        const generatedFilterFunction = CollectionFilter.generateFiltersFunction(filters);

        me._filtersFunction = candidate => {
          // A group record is filtered in if it has passing groupChildren.
          if (isGrouped && candidate.isSpecialRow) {
            return candidate.groupChildren.some(generatedFilterFunction);
          } else {
            return generatedFilterFunction(candidate);
          }
        };
      } else {
        me._filtersFunction = FunctionHelper.returnTrue;
      }
    }

    return me._filtersFunction;
  }
  /**
   * Check if store is filtered
   * @property {Boolean}
   * @readonly
   * @category Sort, group & filter
   */

  get isFiltered() {
    return this.filters.values.some(filter => !filter.disabled);
  } //endregion

  traverseFilter(record) {
    const me = this,
          hitsCurrent = !record.isRoot && me.filtersFunction(record),
          children = record.unfilteredChildren || record.children; // leaf, bail out

    if (!children || !children.length) {
      return hitsCurrent;
    }

    if (!record.unfilteredChildren) {
      record.unfilteredChildren = record.children.slice();
    }

    record.children = record.unfilteredChildren.filter(r => {
      return me.traverseFilter(r);
    }); // unfilteredIndex must be set for child elements

    record.updateChildrenIndices(record.unfilteredChildren, true); // parentIndex must be set for visible child elements

    record.updateChildrenIndices(record.children, false);
    return hitsCurrent || Boolean(record.children.length);
  }

  traverseClearFilter(record) {
    const me = this;

    if (record.unfilteredChildren) {
      record.children = record.unfilteredChildren.slice();
      record.unfilteredChildren = null;
    }

    if (record.children) {
      record.children.forEach(r => me.traverseClearFilter(r));
    }
  } // TODO: Get rid of this.
  // The Filter feature of a Grid pokes around in the Store to ask this question.

  get latestFilterField() {
    return this.filters.last ? this.filters.last.property : null;
  }
  /**
   * Adds a single filter to the {@link #config-filters} collection. By default, filters are reevaluated
   * and a Store change event fired.
   *
   * If the `silent` parameter is passed as `true`, multiple filters can be added without causing data changes.
   *
   * When the filters are as required, call {@link #function-filter} with no parameters
   * to apply the filters to the store.
   *
   * @param {Object|Function} newFilters A {@link Core.util.CollectionFilter filter} config,
   * or a function to use for filtering.
   * @param {Boolean} [silent] Pass `true` to *not* refilter the store immediately. Such as when
   * adding multiple filters.
   * @returns {Core.util.CollectionFilter} The Filter instance that was added.
   * @category Sort, group & filter
   */

  addFilter(filter, silent = false) {
    filter = filter instanceof CollectionFilter ? filter : new CollectionFilter(filter); // We want notification upon change of field, value or operator

    filter.owner = this; // Collection will replace any already existing filter on the field, unless it has id specified

    this.filters.add(filter);

    if (!silent) {
      this.filter();
    }

    return filter;
  }
  /**
   * Filters the store by **adding** the specified filter(s) to the existing filters collection applied to this Store.
   * If a filter has an {@link Core.util.CollectionFilter#config-id id} specified,
   * or a {@link Core.util.CollectionFilter#config-property property} specified,
   * it will search for corresponding filter(s) in the existing filters first and replace it with a new filter.
   * **It will not remove other filters applied to the store!**
   *
   * To **add** a new filter:
   * ```
   * // Filter using simple object
   * store.filter({
   *     property : 'age',
   *     operator : '>',
   *     value    : 90
   * });
   *
   * // Filter using function
   * store.filter(r => r.age < 90);
   *
   * // Filter using a named filter as a function
   * store.filter({
   *     id : 'my-filter',
   *     filterBy : record => record.score > 10
   * });
   * ```
   *
   * To **remove** a specific filter, but keep other filters applied
   * ```
   * // Remove by filter `id` or `property`. Filter `id` defaults to the `property` name.
   * store.removeFilter('age');
   * store.removeFilter('my-filter');
   * ```
   *
   * To **replace** all existing filters with a new filter
   * ```
   * // Remove all filters and filter using simple object
   * store.filter({
   *     filters : {
   *         property : 'age',
   *         operator : '<',
   *         value    : 90
   *     },
   *     replace : true
   * });
   *
   * // Remove all filters and filter using function
   * store.filter({
   *     filters : r => r.age > 90,
   *     replace : true
   * });
   *
   * // Remove all filters and filter using a named filter as a function
   * store.filter({
   *     filters : {
   *         id : 'my-filter',
   *         filterBy : record => record.score > 10
   *     },
   *     replace : true
   * });
   * ```
   *
   * Basically filters replacing is an equivalent of having two sequenced calls:
   * {@link #function-clearFilters clearFilters} and {@link #function-filter filter}.
   *
   * Call without arguments to reapply filters.
   * ```
   * // Re-filter the store
   * store.filter();
   * ```
   *
   * @param {Object|Object[]|Function} newFilters
   *        A {@link Core.util.CollectionFilter filter} config,
   *        or an array of {@link Core.util.CollectionFilter filter} configs,
   *        or a function to use for filtering,
   *        or a special object like: ```{ replace : true, filters : newFilters }```
   * @param {Boolean} [newFilters.replace]
   *        A flag, indicating whether or not the previous filters should be removed.
   * @param {Object|Object[]|Function} [newFilters.filters]
   *        If `newFilters` is an object and `replace` property is defined in the `newFilters`,
   *        it means that special object is used and real filter configuration must be nested down to this `filters` property.
   *        It can be:
   *        A {@link Core.util.CollectionFilter filter} config,
   *        or an array of {@link Core.util.CollectionFilter filter} configs,
   *        or a function to use for filtering.
   * @fires filter
   * @fires change
   * @category Sort, group & filter
   */

  filter(newFilters) {
    const me = this,
          {
      filters
    } = me;
    let silent = false;

    if (newFilters) {
      let fieldType = typeof newFilters;

      if (fieldType === 'object') {
        if ('silent' in newFilters || 'replace' in newFilters) {
          silent = newFilters.silent;

          if (newFilters.replace) {
            filters.clear();
          }

          newFilters = newFilters.filters;
          fieldType = typeof newFilters;
        }
      } // If it was just a config object containing no filters, this will be null

      if (newFilters) {
        // We will not be informed about Filter mutations while configuring.
        me.isConfiguring = true; // If we provide array of objects looking like :
        //  {
        //      property  : 'fieldName',
        //      value     : 'someValue',
        //      [operator : '>']
        //  }
        //  or ...
        //  {
        //      property : 'fieldName',
        //      filterBy : function (value, record) {
        //          return value > 50;
        //      }
        //  }

        if (Array.isArray(newFilters)) {
          newFilters.forEach(f => me.addFilter(f, true), me);
        } else if (fieldType === 'function') {
          me.addFilter(new CollectionFilter(newFilters), true);
        } // Old signature of fieldname, value with implicit equality test.
        // Not documented, but still tested.
        else if (fieldType === 'string') {
            me.addFilter({
              property: newFilters,
              value: arguments[1]
            }, true);
          } // An object-based filter definition
          else {
              me.addFilter(newFilters, true);
            } // Open up to recieving Filter mutation notifications again

        me.isConfiguring = false; // We added a disabled filter to either no filters, or all disabled filters, so no change.

        if (!me.isFiltered) {
          return;
        }
      }
    } // Invalidate the filtersFunction so that it has to be recalculated upon next access

    me.filtersFunction = null; // Implemented here for local filtering, and AjaxStore implements for remote.

    me.performFilter(silent);
  }
  /**
   * Perform filtering according to the {@link #property-filters} Collection.
   * This is the internal implementation which is overridden in {@link Core.data.AjaxStore} and
   * must not be overridden.
   * @private
   */

  performFilter(silent) {
    const me = this,
          {
      storage,
      filters,
      rootNode
    } = me,
          oldCount = me.count; // Private event, did not need it documented or preventable right now

    me.trigger('beforeFilter', {
      filters
    });

    if (me.tree) {
      if (me.isFiltered) {
        me.traverseFilter(rootNode);
      } else {
        me.traverseClearFilter(rootNode);
      }

      storage.replaceValues({
        values: me.collectDescendants(rootNode).visible,
        silent: true
      });
    } else {
      if (me.isFiltered) {
        storage.addFilter({
          id: 'primary-filter',
          filterBy: me.filtersFunction
        });
      } else {
        storage.filters.clear();
      }
    }

    me.afterPerformFilter(silent ? null : {
      action: 'filter',
      filters,
      oldCount,
      records: me.storage.values
    });
  }

  afterPerformFilter(event) {
    this.resetRelationCache();

    if (event) {
      this.triggerFilterEvent(event);
    }
  }

  get filtered() {
    return this.storage.isFiltered;
  } // Used from filter() and StoreCRUD when reapplying filters

  triggerFilterEvent(event) {
    this.trigger('filter', event); // Only fire these events if it's a local filter.
    // If we are configured with filterParamName, the loadData will fire them.

    if (!this.remoteFilter) {
      this.trigger('refresh', event);
      this.trigger('change', event);
    }
  }
  /**
   * *Adds* a function used to filter the store. Alias for calling `filter(fn)`. Return `true` from the function to
   * include record in filtered set
   *
   * ```javascript
   * store.filterBy(record => record.age > 25 && record.name.startsWith('A'));
   * ```
   *
   * @param {Function} fn Function used to test records
   * @category Sort, group & filter
   */

  filterBy(fn) {
    this.filter(fn);
  }
  /**
   * Removes the passed filter, or the filter by the passed ID from the {@link #config-filters} collection.
   * By default, filters are reevaluated and a Store change event fired.
   *
   * If the `silent` parameter is passed as `true`, multiple filters can be removed without causing data changes.
   *
   * When the filters are as required, call {@link #function-filter} with no parameters
   * to apply the filters to the store.
   *
   * @param {String|Core.util.CollectionFilter} idOrInstance Filter to remove, or ID of the filter to remove. By default,
   * filtera are reevaluated and a change event fired.
   * @param {Boolean} [silent] Pass `true` to *not* refilter the store immediately. Such as when
   * removing multiple filters.
   * @returns {Core.util.CollectionFilter} The Filter instance that was removed.
   *
   *```javascript
   *  // Only view top priority events
   *  myEventStore.filter({
   *      id       : 'priorityFilter',
   *      property : 'priority',
   *      value    : 1,
   *      operator : '='
   *  });
   *
   * // That individual filter can be removed like this
   *  myEventStore.removeFilter('priorityFilter');
   *
   * // Add named filter as a function
   * store.filter({
   *     id : 'my filter',
   *     filterBy : record => record.score > 10
   * });
   *
   * // Remove named filter function
   * store.removeFilter('my filter');
   *```
   * @category Sort, group & filter
   */

  removeFilter(idOrInstance, silent = false) {
    const me = this,
          filter = idOrInstance instanceof CollectionFilter ? idOrInstance : me.filters.get(idOrInstance); // If we have such a filter, remove it.

    if (filter) {
      me.filters.remove(filter); // Invalidate the filtersFunction so that it has to be recalculated upon next access

      me._filtersFunction = null;

      if (!silent) {
        me.filter();
      }

      return filter;
    }
  }
  /**
   * Removes all filters from the store.
   * @category Sort, group & filter
   */

  clearFilters() {
    this.filters.clear();
    this.filter();
  }

  convertFilterToString(field) {
    const filter = this.filters.getBy('property', field);
    return filter && !filter.filterBy ? String(filter) : '';
  }

});

/**
 * @module Core/data/mixin/StoreGroup
 */

const resortActions = {
  add: 1,
  replace: 1
};
/**
 * Mixin for Store that handles grouping.
 *
 * ```javascript
 * // simple grouper
 * store.group('city');
 *
 * // grouper as object, descending order
 * store.group({ field : 'city', ascending : false });
 *
 * // using custom sorting function
 * store.group({
 *     field : 'city',
 *     fn : (recordA, recordB) => {
 *         // apply custom logic, for example:
 *         return recordA.city.length < recordB.city.length ? -1 : 1;
 *     }
 * });
 * ```
 *
 * Currently grouping is not supported when using pagination, the underlying store cannot group data that is split into pages.
 *
 * @mixin
 */

var StoreGroup = (Target => class StoreGroup extends (Target || Base) {
  static get $name() {
    return 'StoreGroup';
  } //region Config

  static get defaultConfig() {
    return {
      /**
       * Initial groupers, specify to have store grouped automatically after initially setting data
       * @config {Object[]}
       * @category Common
       */
      groupers: null
    };
  } //endregion
  //region Init

  construct(config) {
    super.construct(config); // For handling record mutation, *not* add/remove of records.
    // Sorts dataset if necessary.

    this.on('change', this.onDataChanged);
  }
  /**
   * Currently used groupers
   * @property {Object[]}
   * @category Sort, group & filter
   */

  get groupers() {
    return this._groupers;
  }

  set groupers(groupers) {
    const me = this;

    if (groupers && groupers.length) {
      me._groupers = groupers;
    } else if (me.groupers) {
      delete me._groupers;
      me.groupRecords.forEach(r => {
        if (r.meta.collapsed) {
          me.includeGroupRecords(r);
        }
      });
      me.storage.replaceValues({
        values: me.removeHeadersAndFooters(me.storage._values),
        filteredValues: me.storage.isFiltered ? me.removeHeadersAndFooters(me.storage._filteredValues) : null,
        silent: true
      });
      me.group(null, null, null, false);

      if ('groupRecords' in me) {
        delete me.groupRecords;
      }
    } // Need to clear the id map so it gets rebuilt next time its accessed

    this._idMap = null;
  }

  storeCollapsedGroups() {
    const me = this;
    me.collapsedGroupsHash = {}; // Remember which groups are collapsed

    if (me.groupRecords && me.groupRecords.length) {
      me.groupRecords.forEach(rec => {
        if (me.expand(rec)) {
          me.collapsedGroupsHash[rec.id] = true;
        }
      });
      return true;
    }

    return false;
  }

  restoreCollapsedGroups() {
    const me = this,
          {
      collapsedGroupsHash
    } = me;
    collapsedGroupsHash && Object.keys(collapsedGroupsHash).forEach(id => me.collapse(me.getById(id)));
  }

  onDataChange({
    source: storage,
    action,
    removed
  }) {
    const me = this,
          {
      groupers
    } = me; // Only do grouping transformations if we have groupers to apply.
    // In stores which never use grouping, this code is superfluous and will reduce performance.
    // The else side will simply replace the ungrouped data with itself.

    if (groupers) {
      const records = storage.values; // When records are added or removed, re-evaluate the group records
      // so that when the events are fired by the super call, the group
      // records are in place.

      if (groupers.length) {
        if (action === 'splice' && removed && removed.length) {
          // Remember which groups are collapsed
          if (me.storeCollapsedGroups()) {
            me.storage.replaceValues({
              values: me.removeHeadersAndFooters(records),
              silent: true
            });
          }

          me.storage.replaceValues({
            values: me.prepareGroupRecords(records),
            silent: true
          }); // Re-collapse the groups

          me.restoreCollapsedGroups();
        }
      } // Remove all group headers and footers
      else {
          storage.replaceValues({
            values: me.removeHeadersAndFooters(records),
            silent: true
          });
        }
    }

    super.onDataChange && super.onDataChange(...arguments);
  } // private function that collapses on the data level
  // TODO: make public and trigger events for grid to react to?

  collapse(groupRecord) {
    if (groupRecord && !groupRecord.meta.collapsed) {
      this.excludeGroupRecords(groupRecord);
      groupRecord.meta.collapsed = true;
      return true;
    }

    return false;
  } // private function that expands on the data level
  // TODO: make public and trigger events for grid to react to?

  expand(groupRecord) {
    if (groupRecord && groupRecord.meta.collapsed) {
      this.includeGroupRecords(groupRecord);
      groupRecord.meta.collapsed = false;
      return true;
    }

    return false;
  }

  removeHeadersAndFooters(records) {
    const me = this;
    return records.filter(r => {
      if (r.isSpecialRow) {
        me.unregister(r);
        return false;
      } else {
        return true;
      }
    }, true);
  }

  prepareGroupRecords(records) {
    const me = this;
    records = me.removeHeadersAndFooters(records);

    if (!me.isGrouped) {
      return records;
    }

    const groupedRecords = [],
          field = me.groupers[0].field,
          groupRecords = [];
    let curGroup = null,
        curGroupRecord = null,
        childCount = 0;

    function addFooter() {
      const val = curGroupRecord.meta.groupRowFor,
            id = `group-footer-${typeof val === 'number' ? val : StringHelper.createId(val)}`,
            footer = me.getById(id) || new me.modelClass({
        id
      }, me, {
        specialRow: true,
        groupFooterFor: val,
        groupRecord: curGroupRecord
      });
      me.register(footer);
      footer.groupChildren = curGroupRecord.groupChildren;
      groupRecords.push(footer);
      groupedRecords.push(footer);
      me.allRecords.push(footer);
      curGroupRecord.groupChildren.push(footer);
      childCount++;
      return footer;
    }

    records.forEach(record => {
      const val = record[field] == undefined ? '__novalue__' : record[field],
            id = `group-header-${typeof val === 'number' ? val : StringHelper.createId(val)}`; // A group header or footer record of an empty group.
      // Remove from the data

      if (record.groupChildren && !record.groupChildren.length) {
        me.unregister(record);
        return;
      }

      if (!ObjectHelper.isEqual(val, curGroup)) {
        if (curGroupRecord) {
          // also add group footer? used by GroupSummary feature
          if (me.useGroupFooters) {
            addFooter();
          }

          curGroupRecord.meta.childCount = childCount;
        }

        curGroupRecord = me.getById(id) || new me.modelClass({
          id
        }, me, {
          specialRow: true,
          groupRowFor: val,
          groupField: field
        });
        me.register(curGroupRecord);
        curGroupRecord.groupChildren = [];
        groupedRecords.push(curGroupRecord);
        me.allRecords.push(curGroupRecord);
        groupRecords.push(curGroupRecord);
        curGroup = val;
        childCount = 0;
      }

      record.instanceMeta(me.id).groupParent = curGroupRecord;
      groupedRecords.push(record);
      curGroupRecord.groupChildren.push(record);
      childCount++;
    }); // misses for last group without this

    if (curGroupRecord) {
      // footer for last group
      if (me.useGroupFooters) {
        addFooter();
      }

      curGroupRecord.meta.childCount = childCount;
    }

    me.groupRecords = groupRecords;
    return groupedRecords;
  } //endregion
  //region Group and ungroup

  /**
   * Is store currently grouped?
   * @property {Boolean}
   * @readonly
   * @category Sort, group & filter
   */

  get isGrouped() {
    return Boolean(this.groupers && this.groupers.length);
  }
  /**
   * Group records, either by replacing current sorters or by adding to them.
   * A grouper can specify a **_custom sorting function_** which will be called with arguments (recordA, recordB).
   * Works in the same way as a standard array sorter, except that returning `null` triggers the stores
   * normal sorting routine. Grouped store **must** always be sorted by the same field.
   *
   * ```javascript
   * // simple grouper
   * store.group('city');
   *
   * // grouper as object, descending order
   * store.group({ field : 'city', ascending : false });
   *
   * // using custom sorting function
   * store.group({
   *     field : 'city',
   *     fn : (recordA, recordB) => {
   *         // apply custom logic, for example:
   *         return recordA.city.length < recordB.city.length ? -1 : 1;
   *     }
   * });
   * ```
   *
   * @param {String|Object} field Field to group by.
   * Can also be a config containing a field to group by and a custom sorting function called `fn`.
   * @param {Boolean} [ascending] Sort order of the group titles
   * @param {Boolean} [add] Add a grouper (true) or use only this grouper (false)
   * @param {Boolean} [performSort] Trigger sort directly, which does the actual grouping
   * @param {Boolean} [silent] Set as true to not fire events
   * @category Sort, group & filter
   * @fires group
   * @fires refresh
   */

  group(field, ascending, add = false, performSort = true, silent = false) {
    const me = this;
    let newGrouper;

    if (add) {
      me.groupers.push(newGrouper = {
        field: field,
        ascending: ascending,
        complexMapping: field.includes('.')
      });
    } else if (field) {
      if (ascending == undefined) {
        ascending = me.groupInfo && me.groupInfo.field === field ? !me.groupInfo.ascending : true;
      }

      me.groupInfo = newGrouper = {
        field: field,
        ascending: ascending,
        complexMapping: field.includes('.')
      };
      me.groupers = [me.groupInfo];
    }

    if (newGrouper) {
      const {
        prototype
      } = me.modelClass; // Create a getter for complex field names like "get resource.city"

      if (newGrouper.complexMapping && !Object.prototype.hasOwnProperty.call(prototype, field)) {
        Object.defineProperty(prototype, field, {
          get() {
            return ObjectHelper.getPath(this, field);
          }

        });
      }
    } // as far as the store is concerned, grouping is just more sorting. so trigger sort

    if (performSort !== false) {
      me.sort(null, null, false, true);
    }

    if (!silent) {
      /**
       * Fired when grouping changes
       * @event group
       * @param {Core.data.Store} source This Store
       * @param {Object[]} groupers Applied groupers
       * @param {Core.data.Model[]} records Grouped records
       */
      me.trigger('group', {
        isGrouped: me.isGrouped,
        groupers: me.groupers,
        records: me.storage.values
      });
      me.trigger('refresh', {
        action: 'group',
        isGrouped: me.isGrouped,
        groupers: me.groupers,
        records: me.storage.values
      });
    }
  } // Internal since UI does not support multi grouping yet

  /**
   * Add a grouping level (a grouper).
   * @param {String} field Field to group by
   * @param {Boolean} ascending Group direction
   * @category Sort, group & filter
   * @internal
   */

  addGrouper(field, ascending = true) {
    this.group(field, ascending, true);
  } // Internal since UI does not support multi grouping yet

  /**
   * Removes a grouping level (a grouper)
   * @param {String} field Grouper to remove
   * @category Sort, group & filter
   * @internal
   */

  removeGrouper(field) {
    const me = this,
          index = me.groupers.findIndex(grouper => grouper.field === field);

    if (index > -1) {
      me.groupers.splice(index, 1);

      if (!me.groupers.length) {
        me.clearGroupers();
      } else {
        me.group();
      }
    }
  }
  /**
   * Removes all groupers, turning store grouping off.
   * @category Sort, group & filter
   */

  clearGroupers() {
    this.groupers = null;
  } //endregion
  //region Get and check

  /**
   * Check if a record belongs to a certain group (only for the first grouping level)
   * @param {Core.data.Model} record The Record
   * @param {*} groupValue The group value
   * @returns {Boolean} True if the record belongs to the group, otherwise false
   * @category Sort, group & filter
   */

  isRecordInGroup(record, groupValue) {
    if (!this.isGrouped) return null;
    const me = this,
          groupField = me.groupers[0] && me.groupers[0].field;
    return record[groupField] === groupValue && !record.isSpecialRow;
  }

  isInCollapsedGroup(record) {
    const parentGroupRec = record.instanceMeta(this).groupParent;
    return parentGroupRec && parentGroupRec.meta.collapsed;
  }
  /**
   * Returns all records in the group with specified groupValue.
   * @param {*} groupValue
   * @returns {Core.data.Model[]} Records in specified group or null if store not grouped
   * @category Sort, group & filter
   */

  getGroupRecords(groupValue) {
    const me = this;
    if (!me.isGrouped) return null;
    return me.storage.values.filter(record => me.isRecordInGroup(record, groupValue));
  }
  /**
   * Get all group titles.
   * @returns {String[]} Group titles
   * @category Sort, group & filter
   */

  getGroupTitles() {
    const me = this;
    if (!me.isGrouped) return null;
    return me.getDistinctValues(me.groupers[0] && me.groupers[0].field);
  } //endregion

  onDataChanged({
    changes,
    action
  }) {
    if (this.isGrouped && ( // If an action flagged as requiring resort is performed...
    !changes && resortActions[action] || // ...or if the group field has changes...
    changes && this.groupers.some(grouper => grouper.field in changes))) {
      // ...then re-sort
      this.sort();
    }
  }
  /**
   * Adds or removes records in a group from storage. Used when expanding/collapsing groups.
   * @private
   * @param {Core.data.Model} groupRecord Group which records should be added or removed
   * @param {Boolean} include Include (true) or exclude (false) records
   * @category Grouping
   */

  internalIncludeExcludeGroupRecords(groupRecord, include) {
    const me = this,
          index = me.indexOf(groupRecord),
          mapId = me.id; // Skip if group record is not found, otherwise it removes records from wrong position

    if (index === -1) {
      return;
    } // Prevent removing from already collapsed and vice versa

    if (groupRecord.meta.collapsed && !include || !groupRecord.meta.collapsed && include) {
      return;
    }

    groupRecord.groupChildren.forEach(child => child.instanceMeta(mapId).hiddenByCollapse = !include);

    if (include) {
      // Avoid adding record duplicates which may already have been reinserted by clearing filters
      const groupChildren = groupRecord.groupChildren.filter(r => !me.isAvailable(r));
      me.storage.values.splice(index + 1, 0, ...groupChildren);
    } else {
      me.storage.values.splice(index + 1, groupRecord.groupChildren.length);
    }

    me.storage._indicesInvalid = true;
    me._idMap = null;
  }
  /**
   * Removes records in a group from storage. Used when collapsing a group.
   * @private
   * @param groupRecord Group which records should be removed
   * @category Grouping
   */

  excludeGroupRecords(groupRecord) {
    this.internalIncludeExcludeGroupRecords(groupRecord, false);
  }
  /**
   * Adds records in a group to storage. Used when expanding a group.
   * @private
   * @param groupRecord Group which records should be added
   * @category Grouping
   */

  includeGroupRecords(groupRecord) {
    this.internalIncludeExcludeGroupRecords(groupRecord, true);
  }
  /**
   * Collects all group headers + children, whether expanded or not
   * @private
   * @param {Boolean} allRecords True to include filtered out records
   * @param {Boolean} includeHeaders True to also include group headers
   * @returns {Core.data.Model[]}
   */

  collectGroupRecords(allRecords, includeHeaders = true) {
    const records = allRecords ? this.storage.allValues : this.storage.values;
    return records.reduce((records, record) => {
      if (record.isSpecialRow) {
        if (includeHeaders) {
          records.push(record);
        }

        if ('groupRowFor' in record.meta) {
          records.push.apply(records, record.groupChildren);
        }
      }

      return records;
    }, []);
  }

});

/**
 * @module Core/data/mixin/StoreProxy
 */

/**
 * Object-like interaction with a Store by using a Proxy. To enable, configure the store with `objectify : true`.
 *
 * ```javascript
 * const store = new Store({
 *    objectify : true,
 *    data      : [
 *        { id : 'batman', name : 'Bruce' }
 *    ]
 * });
 * ```
 *
 * Access records using their ids as Store properties:
 * ```javascript
 * console.log(store.batman.name); // logs Bruce
 * ```
 *
 * Add records by assigning properties to the Store:
 * ```javascript
 * store.superman = { name : 'Clark' }; // Id will be 'superman'
 * ```
 *
 * Remove records by removing their property:
 * ```javascript
 * delete store.batman;
 * ```
 *
 * Check if a certain id existing in the store by using `in`:
 * ```javascript
 * console.log('superman' in store): // logs true
 * ```
 *
 * Please note that this approach:
 * * Will affect performance slightly, not recommended for larger datasets.
 * * Uses native Proxy, which cannot be polyfilled. Not possible to get it working in IE11.
 * * Preserves predefined Store properties, records cannot use ids that match those.
 * * Might have other limitations preventing the use of it in some scenarios where a normal Store can be used.
 *
 * @mixin
 * @private
 */

var StoreProxy = (Target => class StoreProxy extends (Target || Base) {
  static get configurable() {
    return {
      /**
       * Allow object like interaction with the Store. For example:
       *
       * ```javascript
       * const store = new Store({
       *    objectify : true,
       *    data      : [
       *        { id : 'batman', name : 'Bruce' }
       *    ]
       * });
       *
       * // retreive using id as property
       * const record = store.batman;
       *
       * // add as property
       * store.superman = { name : 'Clark' };
       *
       * // delete to remove
       * delete store.batman;
       * ``
       *
       * @config {Boolean}
       * @default false
       * @private
       */
      objectify: null
    };
  }

  initProxy() {
    if (!BrowserHelper.global.Proxy) {
      throw new Error('Proxy not supported');
    }

    return new Proxy(this, {
      // Support getting records using `store[id/index]
      get(target, property) {
        // Stores own properties take precedence
        if (property in target) {
          return target[property];
        } // Then ids

        let record = target.getById(property); // And finally index

        if (!record && !isNaN(parseInt(property))) {
          record = target.getAt(parseInt(property));
        }

        return record;
      },

      // Support adding/replacing records using `store.id = { ...data }`
      set(target, property, value) {
        // Pass through when using names of existing properties or when destroyed/ing
        if (property in target || target.isDestroying) {
          target[property] = value;
        } // Otherwise add/replace a record
        else {
            target.add({
              [target.modelClass.idField]: property,
              ...value
            });
          }

        return true;
      },

      // Support deleting records using `delete store.id`
      deleteProperty(target, property) {
        // Properties are deleted on destroy
        if (target.isDestroying) {
          delete target[property];
          return true;
        }

        return Boolean(target.remove(property).length);
      },

      // Support `id in store`
      has(target, property) {
        // Actual property
        if (property in target) {
          return true;
        } // Threat { ... } as JSON representation of a record (likely from toString())

        if (property.startsWith('{') && property.endsWith('}')) {
          const data = StringHelper.safeJsonParse(property);
          property = data === null || data === void 0 ? void 0 : data.id;
        }

        return target.includes(property);
      }

    });
  }

});

/**
 * @module Core/data/mixin/StoreRelation
 */

/**
 * Mixin for Store that handles relations with other stores (actually defined on model).
 *
 * @mixin
 * @private
 */

var StoreRelation = (Target => class StoreRelation extends (Target || Base) {
  static get $name() {
    return 'StoreRelation';
  } //region Init

  /**
   * Initialized relations, called from constructor
   * @private
   */

  initRelations(reset) {
    const me = this,
          relations = me.modelClass.relations;

    if (reset && me.modelRelations) {
      // reset will reinit all relations, stop listening for store events on existing ones
      me.modelRelations.forEach(relation => {
        relation.storeDetacher && relation.storeDetacher();
      });
    }

    if ((!me.modelRelations || me.modelRelations.length === 0 || reset) && relations) {
      me.modelRelations = []; // foreignKeys is filled when model exposes its properties

      relations && relations.forEach(modelRelationConfig => {
        const config = Object.assign({}, modelRelationConfig),
              relatedStore = typeof config.store === 'string' ? me[config.store] : config.store;
        config.dependentStore = me;
        me.modelRelations.push(config);

        if (relatedStore) {
          config.storeProperty = config.store;
          config.store = relatedStore; // repeated from initRelationStores, needed if stored is assigned late

          const dependentStoreConfigs = relatedStore.dependentStoreConfigs; // Add link to dependent store

          if (dependentStoreConfigs.has(me)) {
            const dependentConfigs = dependentStoreConfigs.get(me); // Remove existing config on reset

            if (reset) {
              const existingConfig = dependentConfigs.find(c => c.relationName === config.relationName);

              if (existingConfig) {
                ArrayHelper.remove(dependentConfigs, existingConfig);
              }
            }

            dependentConfigs.push(config);
          } else {
            dependentStoreConfigs.set(me, [config]);
          } // if foreign key specifies collectionName the related store should also be configured

          if (config.collectionName) {
            relatedStore.initRelationCollection(config, me);
          }

          if (relatedStore.count > 0) {
            relatedStore.updateDependentStores('dataset', relatedStore.records);
          }
        }
      });
    }
  }
  /**
   * Called from other end of an relation when this store should hold a collection of related records.
   * @private
   * @param config
   * @param collectionStore
   */

  initRelationCollection(config, collectionStore) {
    // TODO: parts of this should move to model?
    const me = this,
          name = config.collectionName;

    if (!me.collectionStores) {
      me.collectionStores = {};
    }

    me.collectionStores[name] = {
      store: collectionStore,
      config: config
    };

    if (!me[name + 'Store']) {
      me[name + 'Store'] = collectionStore;
    }

    if (me.count > 0) {
      me.initModelRelationCollection(name, me.records);
    }
  }

  initModelRelationCollection(name, records) {
    const me = this; // add collection getter to each model

    records.forEach(record => {
      // Needs to work in trees also, if not a tree traverse just calls fn on self
      record.traverse(node => {
        // Add/replace $relatedAssignments (or similar) if assignments already exists on target
        const useName = name in node ? `$related${StringHelper.capitalize(name)}` : name;
        Object.defineProperty(node, useName, {
          enumerable: true,
          configurable: true,
          get: function () {
            return me.getCollection(this, name);
          },
          set: function (value) {
            return me.setCollection(this, name, value);
          }
        });
      });
    });
  } //TODO: Do diff update, this is called on filtering and will be heavy with lots of records

  /**
   * Updates relationCache for all records.
   * @private
   */

  resetRelationCache() {
    this.relationCache = {};
    this.forEach(record => record.initRelations());
  }
  /**
   * Caches related records from related store on the local store.
   * @private
   * @param record Local record
   * @param relations Relations to related store
   */

  updateRecordRelationCache(record, relations) {
    const me = this;
    relations && relations.forEach(relation => {
      // use related records id, or if called before "binding" is complete use foreign key
      const foreignId = relation.related ? relation.related.id : record.get(relation.config.fieldName); // cache on that id, removing previously cached value if any

      foreignId !== undefined && me.cacheRelatedRecord(record, foreignId, relation.config.relationName, foreignId);
    });
  } //endregion
  //region Getters

  /**
   * Returns records the relation cache. Same result as if retrieving the collection on the dependent store, but
   * without the need of accessing that store.
   * @internal
   * @param {String} name
   * @param {Core.data.Model|String|Number} recordOrId
   * @returns {Array}
   */

  getRelationCollection(name, recordOrId) {
    const id = Model.asId(recordOrId);
    return this.relationCache[name] && this.relationCache[name][id] || [];
  }
  /**
   * Returns records from a collection of related records. Not to be called directly, called from Model getter.
   * TODO: Move to Model?
   * @private
   * @param {Core.data.Model} record
   * @param {String} name
   * @returns {Array}
   */

  getCollection(record, name) {
    const {
      config,
      store
    } = this.collectionStores[name];
    return store.relationCache[config.relationName] && store.relationCache[config.relationName][record.id] || [];
  }
  /**
   * Sets a collection of related records. Will updated the related store and trigger events from it. Not to be called
   * directly, called from Model setter.
   * @private
   */

  setCollection(model, name, records) {
    const {
      config,
      store
    } = this.collectionStores[name];
    if (!store.relationCache[config.relationName]) store.relationCache[config.relationName] = {};
    const old = (store.relationCache[config.relationName][model.id] || []).slice(),
          added = [],
          removed = [];
    store.suspendEvents(); // Remove any related records not in the new collection

    old.forEach(record => {
      if (!records.includes(record)) {
        record[config.fieldName] = null;
        store.remove(record);
        removed.push(record);
      }
    }); // Add records from the new collection not already in store

    records.forEach(record => {
      if (record instanceof Model) {
        if (!record.stores.includes(store)) {
          store.add(record);
          added.push(record);
        }
      } else {
        [record] = store.add(record);
        added.push(record);
      } // Init relation

      record[config.fieldName] = model.id;
    });
    store.resumeEvents();

    if (removed.length) {
      store.trigger('remove', {
        records: removed
      });
      store.trigger('change', {
        action: 'remove',
        records: removed
      });
    }

    if (added.length) {
      store.trigger('add', {
        records: added
      });
      store.trigger('change', {
        action: 'add',
        records: added
      });
    }
  } //endregion
  //region Caching

  /**
   * Adds a record to relation cache, optionally removing it if already there.
   * @private
   * @param record
   * @param id
   * @param name
   * @param uncacheId
   */

  cacheRelatedRecord(record, id, name, uncacheId = null) {
    const me = this,
          cache = me.relationCache[name] || (me.relationCache[name] = {});

    if (uncacheId !== null) {
      me.uncacheRelatedRecord(record, name, uncacheId);
    }

    if (id != null) {
      // Only include of not already in relation cache, which might happen when removing and re-adding the same instance
      ArrayHelper.include(cache[id] || (cache[id] = []), record);
    }
  }
  /**
   * Removes a record from relation cache, for a specific relation (specifiy relation name and id) or for all relations
   * @private
   * @param record Record to remove from cache
   * @param name Optional, relation name
   * @param id Optional, id
   */

  uncacheRelatedRecord(record, name = null, id = null) {
    const me = this;

    function remove(relationName, relatedId) {
      const cache = me.relationCache[relationName],
            oldCache = cache && cache[relatedId]; // When unjoining a record from a filtered store the relationCache will also be filtered
      // and might give us nothing, in which case we have nothing to clean up and bail out

      if (oldCache) {
        const uncacheIndex = oldCache.indexOf(record);
        uncacheIndex >= 0 && oldCache.splice(uncacheIndex, 1);

        if (oldCache.length === 0) {
          delete cache[relatedId];
        }
      }
    }

    if (id != null) {
      remove(name, id);
    } else {
      if (record.meta.relationCache) {
        Object.entries(record.meta.relationCache).forEach(([relationName, relatedRecord]) => {
          const relatedId = relatedRecord && relatedRecord.id;
          remove(relationName, relatedId);
        });
      }
    }
  }
  /**
   * Updates related stores when store is cleared, a record is removed or added.
   * @private
   * @param {String} action
   * @param {Core.data.Model[]} records
   */

  updateDependentStores(action, records) {
    this.dependentStoreConfigs.forEach(configs => {
      configs.forEach(config => {
        const dependentStore = config.dependentStore,
              cache = dependentStore.relationCache[config.relationName];

        if (action === 'dataset') {
          config.collectionName && this.initModelRelationCollection(config.collectionName, records);
          dependentStore.forEach(record => {
            const foreign = record.initRelation(config);
            foreign && dependentStore.cacheRelatedRecord(record, foreign.id, config.relationName, foreign.id);
          });
          return;
        }

        if (action === 'removeall') {
          dependentStore.forEach(record => {
            record.removeRelation(config);
          });
          delete dependentStore.relationCache[config.relationName];
          return;
        }

        if (action === 'add') {
          config.collectionName && this.initModelRelationCollection(config.collectionName, records);
        }

        if (action === 'add' || action === 'remove') {
          records.forEach(record => {
            const dependentRecords = cache && cache[record.id];

            switch (action) {
              case 'remove':
                // removing related record removes from cache on model and store
                if (dependentRecords) {
                  dependentRecords.forEach(dependentRecord => dependentRecord.removeRelation(config)); // Altered to not delete on self, simplifies taking actions on related records after remove if relation still lives
                  //delete cache[relatedRecord.id];
                } // TODO: Should removing related set foreign key to null? (removing Team sets Player.teamId to null)

                break;

              case 'add':
                // adding a new record in related store checks if any foreign keys match the new id,
                // and if so it sets up the relation
                dependentStore.forEach(dependentRecord => {
                  if (dependentRecord.get(config.fieldName) == record.id) {
                    dependentRecord.initRelation(config);
                    dependentStore.cacheRelatedRecord(dependentRecord, record.id, config.relationName);
                  }
                });
                break;
            }
          });
        }
      });
    });
  }
  /**
   * Updates relation cache and foreign key value when a related objects id is changed.
   * @private
   */

  updateDependentRecordIds(oldValue, value) {
    this.dependentStoreConfigs && this.dependentStoreConfigs.forEach(configs => {
      configs.forEach(config => {
        const dependentStore = config.dependentStore,
              cache = dependentStore.relationCache[config.relationName],
              localRecords = cache && cache[oldValue] && cache[oldValue].slice();
        localRecords && localRecords.forEach(localRecord => {
          // First update cache
          dependentStore.cacheRelatedRecord(localRecord, value, config.relationName, oldValue); // Then update & announce, otherwise relations wont be up to date in listeners

          localRecord.set(config.fieldName, value, false, true);
        });
      });
    });
  } //endregion

});

/**
 * @module Core/data/mixin/StoreSum
 */

/**
 * Mixin for Store that handles summaries.
 *
 * @mixin
 */

var StoreSum = (Target => class StoreSum extends (Target || Base) {
  static get $name() {
    return 'StoreSum';
  }
  /**
   * Returns sum calculated by adding value of specified field for specified records. Defaults to using all records
   * in store
   * @param {String} field Field to summarize by
   * @param {Core.data.Model[]} records Records to summarize, uses all records if unspecified.
   * @returns {Number}
   * @category Sum
   */

  sum(field, records = this.storage.values) {
    if (!records) return 0;
    return records.reduce((sum, record) => {
      if (record.isSpecialRow) return sum;
      const v = Number(record[field]);
      return isNaN(v) ? sum : sum + v;
    }, 0);
  }
  /**
   * Returns min value for the specified field, can be used with Date or Number values. Defaults to look through all records in store
   * @param {String} field Field to find min value for
   * @param {Core.data.Model[]} records Records to process, uses all records if unspecified
   * @returns {Number|Date}
   * @category Sum
   */

  min(field, records = this.storage.values) {
    if (!records || !records.length) return 0;
    return records.reduce((min, record) => {
      var _record$field;

      const type = typeof ((_record$field = record[field]) === null || _record$field === void 0 ? void 0 : _record$field.valueOf());
      if (type === 'number' && record[field] < min) min = record[field];
      return min;
    }, records[0][field]);
  }
  /**
   * Returns max value for the specified field, can be used with Date or Number values. Defaults to look through all records in store
   * @param {String} field Field to find max value for
   * @param {Core.data.Model[]} records Records to process, uses all records if unspecified
   * @returns {Number|Date}
   * @category Sum
   */

  max(field, records = this.storage.values) {
    if (!records || !records.length) return 0;
    return records.reduce((max, record) => {
      var _record$field2;

      const type = typeof ((_record$field2 = record[field]) === null || _record$field2 === void 0 ? void 0 : _record$field2.valueOf());
      if (type === 'number' && record[field] > max) max = record[field];
      return max;
    }, records[0][field]);
  }
  /**
   * Returns the average value for the specified field. Defaults to look through all records in store
   * @param {String} field Field to calculate average value for
   * @param {Core.data.Model[]} records Records to process, uses all records if unspecified
   * @returns {Number}
   * @category Sum
   */

  average(field, records = this.storage.values) {
    if (!records || !records.length) return 0;
    let count = 0;
    const sum = records.reduce((sum, record) => {
      if (record.isSpecialRow) return sum;
      const v = parseFloat(record[field]);

      if (!isNaN(v)) {
        count++;
        return sum + v;
      } else {
        return sum;
      }
    }, 0);
    return count > 0 ? sum / count : 0;
  }
  /**
   * Returns sum by adding value of specified field for records in the group with the specified groupValue.
   * @param {*} groupValue The group to summarize
   * @param {String} field Field to summarize by
   * @returns {Number} Sum or null if store not grouped
   * @category Sum
   */

  groupSum(groupValue, field) {
    return this.sum(field, this.getGroupRecords(groupValue));
  }

});

/**
 * @module Core/data/mixin/StoreSearch
 */

const stringFound = (value, find) => String(value).toLowerCase().indexOf(find) !== -1,
      comparisons = {
  string: stringFound,
  number: stringFound,
  boolean: stringFound,
  date: (value, find) => {
    if (value instanceof Date && find instanceof Date) {
      return String(value) === String(find);
    }

    return String(value.getMonth() + 1).indexOf(find) !== -1 || String(value.getDate()).indexOf(find) !== -1 || String(value.getFullYear()).indexOf(find) !== -1;
  }
};
/**
 * Mixin for Store that handles searching (multiple records) and finding (single record).
 *
 * @example
 * // find all records that has a field containing the string john
 * let hits = store.search('john');
 *
 * @mixin
 */

var StoreSearch = (Target => class StoreSearch extends (Target || Base) {
  static get $name() {
    return 'StoreSearch';
  } //region Search (multiple hits)

  /**
   * Find all hits matching the specified input
   * @param {String} find Value to search for
   * @param {Object[]} fields Fields to search value in
   * @returns {Object[]} Array of hits, in the format { index: x, data: record }
   * @category Search
   */

  search(find, fields = null) {
    const records = this.storage.values,
          len = records.length,
          found = [];

    if (find == null) {
      return null;
    }

    if (typeof find === 'string') {
      find = String(find).toLowerCase();
    }

    let i, record, value, valueType, comparison;

    for (i = 0; i < len; i++) {
      record = records[i];

      for (const key of fields || record.fieldNames) {
        value = record[key];
        valueType = value instanceof Date ? 'date' : typeof value;
        comparison = comparisons[valueType];

        if (value && comparison && comparison(value, find)) {
          found.push({
            index: i,
            data: record,
            field: key,
            id: record.id
          });
        }
      }
    }

    return found;
  }
  /**
   * Find all hits in a column
   * @param {String} field The store field to search in
   * @param {*} value Value to search for
   * @returns {*} Array of hits, in the format { index: x, data: record }
   * @category Search
   */

  findByField(field, value) {
    let records = this.storage.values,
        i,
        len = records.length,
        record,
        found = [],
        fieldValue;

    if (value !== null && value !== undefined) {
      value = String(value).toLowerCase();
    }

    for (i = 0; i < len; i++) {
      record = records[i];
      fieldValue = record[field];
      const type = fieldValue instanceof Date ? 'date' : typeof fieldValue,
            comparison = {
        date: () => Boolean(fieldValue) && fieldValue.toLocaleString().includes(value),
        string: () => Boolean(fieldValue) && fieldValue.toLowerCase().includes(value),
        number: () => typeof fieldValue === 'number' && fieldValue.toString().includes(value),
        object: () => fieldValue === value,
        // typeof null === object
        undefined: () => fieldValue === value
      };

      if ((value === null || value === undefined) && fieldValue === value || value && comparison[type]()) {
        found.push({
          id: record.id,
          index: i,
          data: record
        });
      }
    }

    return found;
  } //endregion
  //region Find (single hit)

  /**
   * Finds the first record for which the specified function returns true
   * @param {Function} fn Comparison function, called with record as parameter
   * @param {Boolean} [searchAllRecords] True to ignore any applied filters when searching
   * @returns {Core.data.Model} Record or undefined if none found
   *
   * @example
   * store.find(record => record.color === 'blue');
   * @category Search
   */

  find(fn, searchAllRecords = false) {
    let records = searchAllRecords ? this.storage.allValues : this.storage.values;

    if (this.isGrouped) {
      records = this.collectGroupRecords(searchAllRecords, false);
    }

    return records.find(fn);
  }
  /**
   * Finds the first record for which the specified field has the specified value
   * @param {String} fieldName Field name
   * @param {*} value Value to find
   * @param {Boolean} [searchAllRecords] True to ignore any applied filters when searching
   * @returns {Core.data.Model} Record or undefined if none found
   * @category Search
   */

  findRecord(fieldName, value, searchAllRecords = false) {
    const matchFn = r => ObjectHelper.isEqual(r[fieldName], value);

    if (this.tree) {
      return this.query(matchFn)[0];
    }

    return (searchAllRecords ? this.storage.allValues : this.storage.values).find(matchFn);
  }
  /**
   * Searches the Store records using the passed function.
   * @param {Function} fn A function that is called for each record. Return true to indicate a match
   * @param {Boolean} [searchAllRecords] True to ignore any applied filters when searching
   * @returns {Core.data.Model[]} An array of the matching Records
   * @category Search
   */

  query(fn, searchAllRecords = false) {
    let records = searchAllRecords ? this.storage.allValues : this.storage.values;

    if (this.isTree) {
      const matches = [];
      this.traverse(node => {
        if (fn(node)) {
          matches.push(node);
        }
      });
      return matches;
    } else if (this.isGrouped) {
      records = this.collectGroupRecords(searchAllRecords, false);
    }

    return records.filter(fn);
  } //endregion
  //region Others

  /**
   * Returns true if the supplied function returns true for any record in the store
   * @param {Function} fn A function that should return true to indicate a match
   * @param {Boolean} [searchAllRecords] True to ignore any applied filters when searching
   * @returns {Boolean}
   *
   * @example
   * store.some(record => record.age > 95); // true if any record has age > 95
   * @category Search
   */

  some(fn, searchAllRecords = false) {
    let records = searchAllRecords ? this.storage.allValues : this.storage.values;

    if (this.isGrouped) {
      records = this.collectGroupRecords(searchAllRecords, false);
    }

    return records.some(fn);
  } //endregion

});

/**
 * @module Core/data/mixin/StoreSort
 */

/**
 * Mixin for Store that handles simple sorting as well as multi-level sorting.
 *
 * ```javascript
 * // single sorter
 * store.sort('age');
 *
 * // single sorter as object, descending order
 * store.sort({ field : 'age', ascending : false });
 *
 * // multiple sorters
 * store.sort(['age', 'name']);
 *
 * // using custom sorting function
 * store.sort({
 *     fn : (recordA, recordB) => {
 *         // apply custom logic, for example:
 *         return recordA.name.length < recordB.name.length ? -1 : 1;
 *     }
 * });
 *
 * // using locale specific sort (slow)
 * store.sort({ field : 'name', useLocaleSort : 'sv-SE' });
 * ```
 *
 * @mixin
 */

var StoreSort = (Target => class StoreSort extends (Target || Base) {
  static get $name() {
    return 'StoreSort';
  } //region Config

  static get defaultConfig() {
    return {
      /**
       * Initial sorters, format is [{ field: 'name', ascending: false }, ...]
       * @config {Object[]|String[]}
       * @category Common
       */
      sorters: [],

      /**
       * Use `localeCompare()` when sorting, which lets the browser sort in a locale specific order. Set to `true`,
       * a locale string or a locale config to enable.
       *
       * Enabling this has big negative impact on sorting
       * performance. For more info on `localeCompare()`, see [MDN](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/localeCompare).
       *
       * Examples:
       *
       * ```javascript
       * const store = new Store({
       *     // Swedish sorting
       *     useLocaleSort : 'sv-SE'
       * });
       *
       * const store = new Store({
       *     // Swedish sorting with custom casing order
       *     useLocaleSort : {
       *         locale    : 'sv-SE',
       *         caseFirst : 'upper'
       *     }
       * });
       * ```
       *
       * Can also be configured on a per sorter basis:
       *
       * ```javascript
       * store.sort({ field: 'name', useLocaleSort : 'sv-SE' });
       * ```
       *
       * @config {Boolean|String|Object}
       * @default false
       * @category Advanced
       */
      useLocaleSort: null
    };
  } //endregion
  //region Events

  /**
   * Fired before sorting
   * @event beforeSort
   * @param {Core.data.Store} source This Store
   * @param {Object[]} sorters Sorter configs
   * @param {Core.data.Model[]} records Records to sort
   */

  /**
   * Fired after sorting
   * @event sort
   * @param {Core.data.Store} source This Store
   * @param {Object[]} sorters Sorter configs
   * @param {Core.data.Model[]} records Sorted records
   */
  //endregion
  //region Properties

  /**
   * Currently applied sorters
   * @member {Object[]} sorters
   * @readonly
   * @category Sort, group & filter
   */

  /**
   * Is store sorted?
   * true
   * @property {Boolean}
   * @readonly
   * @category Sort, group & filter
   */

  get isSorted() {
    return Boolean(this.sorters.length) || this.isGrouped;
  } //endregion
  //region Add & remove sorters

  /**
   * Sort records, either by replacing current sorters or by adding to them.
   * A sorter can specify a **_custom sorting function_** which will be called with arguments (recordA, recordB).
   * Works in the same way as a standard array sorter, except that returning `null` triggers the stores
   * normal sorting routine.
   *
   * ```javascript
   * // single sorter
   * store.sort('age');
   *
   * // single sorter as object, descending order
   * store.sort({ field : 'age', ascending : false });
   *
   * // multiple sorters
   * store.sort(['age', 'name']);
   *
   * // using custom sorting function
   * store.sort((recordA, recordB) => {
   *     // apply custom logic, for example:
   *     return recordA.name.length < recordB.name.length ? -1 : 1;
   * });
   *
   * // using locale specific sort (slow)
   * store.sort({ field : 'name', useLocaleSort : 'sv-SE' });
   * ```
   *
   * @param {String|Array|Object|Function} field Field to sort by.
   * Can also be an array of sorters, or a sorting function, or a {@link Core.util.CollectionSorter sorter} config.
   * @param {Boolean} [ascending] Sort order.
   * Applicable when the `field` is a string (if not specified and already sorted by the field, reverts direction),
   * or an object and `ascending` property is not specified for the object. `true` by default.
   * Not applicable when `field` is a function. `ascending` is always `true` in this case.
   * @param {Boolean} [add] If `true`, adds a sorter to the sorters collection.
   * Not applicable when `field` is an array. In this case always replaces active sorters.
   * @param {Boolean} [silent] Set as true to not fire events
   * @category Sort, group & filter
   * @fires beforeSort
   * @fires sort
   * @fires refresh
   */

  sort(field, ascending, add = false, silent = false) {
    const me = this,
          records = me.allRecords,
          currentSorters = me.sorters ? me.sorters.slice() : [];
    let currentDir = null,
        curSort;

    if (field) {
      if (Array.isArray(field)) {
        // array of strings make fields always be sorted ascending
        me.sorters = field.map(sorter => me.normalizeSorterConfig(sorter, typeof sorter === 'string' ? true : ascending));
      } else {
        const sorter = me.normalizeSorterConfig(field, ascending);

        if (add) {
          curSort = me.getCurrentSorterByField(sorter.field); // Field already among sorters? change sort direction instead of adding new sorter

          if (curSort) {
            currentDir = curSort.ascending;
            curSort.ascending = sorter.ascending;
          } else {
            me.sorters.push(sorter);
          }
        } else {
          me.sorters = [sorter];
        }
      }
    }

    if (!silent && me.trigger('beforeSort', {
      sorters: me.sorters,
      records,
      currentSorters
    }) === false) {
      // Restore sorters
      me.sorters = currentSorters; // Restore sorting direction if toggled

      if (currentDir !== null) {
        curSort.ascending = currentDir;
      }

      return;
    }

    return me.performSort(silent);
  }

  normalizeSorterConfig(field, ascending) {
    const me = this,
          sorter = {
      ascending
    };

    if (typeof field === 'object') {
      var _field$ascending;

      Object.assign(sorter, field);

      if (field.fn) {
        delete sorter.fn;
        sorter.sortFn = field.fn;
      }

      sorter.ascending = (_field$ascending = field.ascending) !== null && _field$ascending !== void 0 ? _field$ascending : ascending;
    } else if (typeof field === 'function') {
      sorter.sortFn = field;
    } else {
      sorter.field = field;
    } // sort in opposite direction if not specified and already sorted, default to sorting ascending

    if (sorter.ascending == null) {
      const curSort = me.getCurrentSorterByField(sorter.field);
      sorter.ascending = curSort ? !curSort.ascending : true;
    }

    if (sorter.sortFn == null) {
      var _me$modelClass, _me$modelClass$$meta$;

      const compareItems = (_me$modelClass = me.modelClass) === null || _me$modelClass === void 0 ? void 0 : (_me$modelClass$$meta$ = _me$modelClass.$meta.fields.map[sorter.field]) === null || _me$modelClass$$meta$ === void 0 ? void 0 : _me$modelClass$$meta$.compareItems;

      if (compareItems) {
        // These sorters will be ignored by AjaxStore when remoting...
        sorter.sortFn = compareItems;
      }
    }

    return sorter;
  }

  getCurrentSorterByField(field) {
    return typeof field === 'string' && this.sorters.find(s => s.field === field) || null;
  }
  /**
   * Add a sorting level (a sorter).
   * @param {String|Array|Object|Function} field Field to sort by. Can also be an array of sorters, or a sorting
   * function, or a {@link Core.util.CollectionSorter sorter} config.
   * @param {Boolean} ascending Sort order (used only if field specified as string)
   * @category Sort, group & filter
   */

  addSorter(field, ascending = true) {
    this.sort(field, ascending, true);
  }
  /**
   * Remove a sorting level (a sorter)
   * @param {String|Function} field Stop sorting by this field (or sorter function)
   * @category Sort, group & filter
   */

  removeSorter(field) {
    const sorterIndex = this.sorters.findIndex(sorter => sorter.field === field || sorter.sortFn === field);

    if (sorterIndex > -1) {
      this.sorters.splice(sorterIndex, 1);
      this.sort();
    }
  }
  /**
   * Removes all sorters, turning store sorting off.
   * @category Sort, group & filter
   */

  clearSorters() {
    const me = this;
    me.sorters.length = 0;
    me.sort();
  } //region
  //region Sorting logic

  /**
   * Creates a function used with Array#sort when sorting the store. Override to use your own custom sorting logic.
   * @param {Object[]} sorters An array of sorter config objects
   * @returns {Function}
   * @category Sort, group & filter
   */

  createSorterFn(sorters) {
    const storeLocaleSort = this.useLocaleSort;
    return (lhs, rhs) => {
      // Cannot use `for ... of` here, breaks a test in IE11 were this fn is called from transpiled test code
      // which leads to iterator symbol mismatch
      for (let i = 0; i < sorters.length; i++) {
        const sorter = sorters[i],
              {
          field,
          ascending = true,
          useLocaleSort = storeLocaleSort
        } = sorter,
              fn = sorter.fn || sorter.sortFn,
              direction = ascending ? 1 : -1;

        if (fn) {
          const val = fn(lhs, rhs);

          if (val !== null) {
            return val * direction;
          }
        }

        const lhsValue = lhs[field],
              rhsValue = rhs[field];

        if (lhsValue === rhsValue) {
          continue;
        }

        if (lhsValue == null) {
          return -direction;
        }

        if (rhsValue == null) {
          return direction;
        }

        if (useLocaleSort) {
          // Use systems locale
          if (useLocaleSort === true) {
            return String(lhsValue).localeCompare(rhsValue) * direction;
          } // Use specified locale

          if (typeof useLocaleSort === 'string') {
            return String(lhsValue).localeCompare(rhsValue, useLocaleSort) * direction;
          } // Use locale config

          if (typeof useLocaleSort === 'object') {
            return String(lhsValue).localeCompare(rhsValue, useLocaleSort.locale, useLocaleSort) * direction;
          }
        }

        if (lhsValue > rhsValue) {
          return direction;
        }

        if (lhsValue < rhsValue) {
          return -direction;
        }
      }

      return 0;
    };
  }
  /**
   * Perform sorting according to the {@link #config-sorters} configured.
   * This is the internal implementation which is overridden in {@link Core.data.AjaxStore} and
   * must not be overridden.
   * @private
   * @category Sort, group & filter
   */

  performSort(silent) {
    const me = this,
          {
      rootNode,
      storage,
      sorters
    } = me,
          sorter = me.createSorterFn(me.isGrouped ? me.groupers.concat(sorters) : sorters); // Temporarily remove group headers, will be re-added after sort

    if (me.isGrouped) {
      // Remember which groups are collapsed
      if (me.storeCollapsedGroups()) {
        storage.replaceValues({
          values: me.removeHeadersAndFooters(storage.values),
          silent: true
        });
      }
    }

    if (me.tree) {
      rootNode.traverse(node => {
        if (node.isLoaded && node.isParent) {
          node.children.sort(sorter); // Since child nodes change order their parentIndex needs to be updated.
          // Update is silent, records wont be considered modified because of the sort

          node.updateChildrenIndices(node.children, false, true);
        }
      });
      storage.replaceValues({
        values: me.collectDescendants(rootNode).visible,
        silent: true
      });
    } else {
      storage.replaceValues({
        values: storage.values.sort(sorter),
        silent: true
      });
    }

    me.afterPerformSort(silent);
  }

  afterPerformSort(silent) {
    const me = this,
          {
      storage
    } = me;
    me._idMap = null; // Apply grouping

    if (me.isGrouped) {
      storage.replaceValues({
        values: me.prepareGroupRecords(storage.values),
        silent: true
      }); // Re-collapse the groups

      me.restoreCollapsedGroups();
    }

    if (!silent) {
      const event = {
        action: 'sort',
        sorters: me.sorters,
        records: me.allRecords
      };
      me.trigger('sort', event); // Only fire this event if it's a local sort.
      // If we are configured with sortParamName, the loadData will fire it.

      if (!me.sortParamName) {
        me.trigger('refresh', event);
      }
    }
  } //endregion

});

/**
 * @module Core/data/mixin/StoreChained
 */

const returnTrue = () => true;
/**
 * A chained Store contains a subset of records from a master store. Which records to include is determined by a
 * filtering function, {@link #config-chainedFilterFn}.
 *
 * @example
 * masterStore.makeChained(record => record.percent < 10);
 *
 * // or
 *
 * new Store({
 *   masterStore     : masterStore,
 *   chainedFilterFn : record => record.percent < 10
 * });
 *
 * @mixin
 */

var StoreChained = (Target => class StoreChained extends (Target || Base) {
  static get $name() {
    return 'StoreChained';
  } //region Config

  static get defaultConfig() {
    return {
      /**
       * Function used to filter records in the masterStore into a chained store. If not provided,
       * all records from the masterStore will be included in the chained store.
       * @config {Function}
       * @category Chained store
       */
      chainedFilterFn: null,

      /**
       * Array of fields that should trigger filtering of chained store when the fields are updated.
       * @config {String[]}
       * @category Chained store
       */
      chainedFields: null,

      /**
       * Master store that a chained store gets its records from.
       * @config {Core.data.Store}
       * @category Chained store
       */
      masterStore: null,

      /**
       * Method names calls to which should be relayed to master store.
       * @config {String[]}
       * @category Chained store
       */
      doRelayToMaster: ['add', 'remove', 'insert'],

      /**
       * Method names calls to which shouldn't be relayed to master store.
       * @config {String}
       * @category Chained store
       */
      dontRelayToMaster: [],

      /**
       * Flag showing whether to keep added/removed uncommitted records when filling the store from master.
       * @config {Boolean}
       * @category Chained store
       */
      keepUncommittedChanges: false,

      /**
       * If true, collapsed records in original tree will be excluded from the chained store.
       * @config {Boolean}
       * @category Chained store
       * @internal
       */
      excludeCollapsedRecords: true
    };
  } //endregion

  construct(config) {
    super.construct(config);
    const me = this,
          {
      masterStore
    } = me;

    if (masterStore) {
      me.methodNamesToRelay.forEach(fnName => me[fnName] = (...params) => me.relayToMaster(fnName, params));

      me.removeAll = (...params) => {
        masterStore.remove(me.getRange(), ...params);
      }; // TODO: prevent other functions?

      masterStore.on({
        // HACK to have chained stores react early in a async events scenario (with engine). Could be turned
        // into a config, but this way one does not have to think about it
        changePreCommit: me.onMasterDataChangedPreCommit,
        change: me.onMasterDataChanged,
        prio: 1,
        thisObj: me
      });

      if (!masterStore.chainedStores) {
        masterStore.chainedStores = [];
      }

      masterStore.chainedStores.push(me);
      me.fillFromMaster();
    }
  } //region Properties

  /**
   * Is this a chained store?
   * @property {Boolean}
   * @readonly
   * @category Store
   */

  get isChained() {
    return Boolean(this.masterStore);
  }

  set chainedFilterFn(chainedFilterFn) {
    this._chainedFilterFn = this.thisObj ? chainedFilterFn.bind(this.thisObj) : chainedFilterFn;
  }

  get chainedFilterFn() {
    return this._chainedFilterFn || returnTrue;
  }

  get methodNamesToRelay() {
    const doIsArray = Array.isArray(this.doRelayToMaster),
          dontIsArray = Array.isArray(this.dontRelayToMaster);
    return doIsArray && this.doRelayToMaster.filter(name => !dontIsArray || !this.dontRelayToMaster.includes(name)) || [];
  } //endregion
  //region Internal

  updateChainedStores() {
    if (this.chainedStores) {
      this.chainedStores.forEach(store => store.fillFromMaster());
    }
  }
  /**
   * Updates records available in a chained store by filtering the master store records using
   * {@link #config-chainedFilterFn}
   */

  fillFromMaster() {
    const me = this,
          {
      masterStore,
      isTree
    } = me;

    if (!me.isChained) {
      throw new Error('fillFromMaster only allowed on chained store');
    }

    if (me.keepUncommittedChanges) {
      if (isTree) {
        throw new Error('Cannot use keepUncommittedChanges on a chained tree store');
      }

      me.data = [].concat(me.added.values.filter(r => !me.removed.includes(r)), masterStore.allRecords.filter(r => !me.removed.includes(r) && !me.added.includes(r) && me.chainedFilterFn(r)));
    } else {
      let records = masterStore.allRecords.filter(me.chainedFilterFn);

      if (isTree) {
        // All nodes will be registered
        me.idRegister = {};
        me.internalIdRegister = {}; // *all* owned records have to join, as they would have done if they'd all gone through
        // the appendChild route for this store.

        records.forEach(r => {
          if (r.stores.includes(me)) {
            me.register(r);
          } else {
            r.joinStore(me);
          }
        }); // We exclude collapsed records by default. It's used in Columns Store.
        // Because grid columns is a tree store when subgrid columns is just a chained store of the columns store.
        // And we don't need to include collapsed column.
        // If we need to show collapsed nodes in Combo we need to chain tree store and set `excludeCollapsedRecords` to `false`.

        if (me.excludeCollapsedRecords) {
          const children = me.getChildren(me.rootNode);
          records = me.doIncludeExclude(children, true);
        }
      }

      me.data = records;
    }
  }
  /**
   * Commits changes back to master.
   * - the records deleted from chained store and present in master will be deleted from master
   * - the records added to chained store and missing in master will added to master
   * Internally calls {Store#function-commit commit()}.
   * @returns {Object} Changes, see Store#changes
   * @internal
   */

  commitToMaster() {
    const me = this,
          master = me.masterStore;

    if (!me.isChained) {
      throw new Error('commitToMaster only allowed on chained store');
    }

    master.beginBatch();
    master.remove(me.removed.values);
    master.add(me.added.values);
    master.endBatch();
    return me.commit();
  }
  /**
   * Relays some function calls to the master store
   * @private
   */

  relayToMaster(fnName, params) {
    return this.masterStore[fnName](...params);
  } // HACK, when used with engine the chained store will catch events early (sync) and prevent late (async) listeners

  onMasterDataChangedPreCommit(event) {
    this.onMasterDataChanged(event);
    this.$masterEventhandled = true;
  }
  /**
   * Handles changes in master stores data. Updates the chained store accordingly
   * @private
   */

  onMasterDataChanged({
    action,
    changes,
    $handled
  }) {
    var _this$chainedFields;

    // Handled early in engine store (above), bail out
    if (this.$masterEventhandled) {
      this.$masterEventhandled = false;
      return;
    } // if a field not defined in chainedFields is changed, ignore the change.
    // there is no need to refilter the store in such cases, the change will be available anyhow since data is
    // shared

    if (action !== 'update' || (_this$chainedFields = this.chainedFields) !== null && _this$chainedFields !== void 0 && _this$chainedFields.some(field => field in changes)) {
      this.fillFromMaster();
    }
  } //endregion

});

/**
 * @module Core/data/mixin/StoreState
 */

/**
 * Mixin for Store that handles store state.
 *  * sorters
 *  * groupers
 *  * filters
 * @mixin
 */

var StoreState = (Target => class StoreState extends (Target || Base) {
  static get $name() {
    return 'StoreState';
  }
  /**
   * Get store state. Used by State-plugin to serialize state
   * @private
   * @returns {{ sorters, groupers }}
   */

  getState() {
    const {
      sorters,
      groupers,
      filters
    } = this,
          state = {};

    if (sorters !== null && sorters !== void 0 && sorters.length) {
      state.sorters = sorters.map(sorter => {
        const clone = ObjectHelper.cleanupProperties(ObjectHelper.clone(sorter)); // Remove sorting fns

        delete clone.fn;
        delete clone.sortFn;
        return clone;
      });
    }

    if (groupers !== null && groupers !== void 0 && groupers.length) {
      state.groupers = groupers.slice();
    }

    if (filters !== null && filters !== void 0 && filters.values.length) {
      state.filters = filters.values.map(filter => {
        const clone = ObjectHelper.cleanupProperties(ObjectHelper.clone(filter.config)); // Apply value, needed because of filterable fn reusing instance (so not in config)

        clone.value = filter.value; // Remove default value, no need to store that in state

        if (clone.caseSensitive) {
          delete clone.caseSensitive;
        } // Remove filtering fns
        // delete clone.filterBy; // Cannot remove it, required by export feature

        return clone;
      });
    }

    return state;
  }
  /**
   * Apply store state. Used by State-plugin to restore a previously serialized state
   * @private
   * @param {{ sorters, groupers }} state
   */

  applyState(state) {
    const me = this,
          {
      sorters,
      groupers,
      filters
    } = state;

    if (sorters) {
      me.sorters = sorters.slice();
    }

    if (groupers) {
      me.groupers = groupers.slice();
    }

    if (sorters || groupers) {
      me.sort();
    }

    if (filters) {
      me.filters = filters.slice();
      me.filter();
    }
  }

});

/**
 * @module Core/data/mixin/StoreTree
 */

const emptyArray$3 = Object.freeze([]);
/**
 * Mixin for store with tree related functionality. To learn more about working with tree nodes please see the
 * {@link Core/data/mixin/TreeNode} class and [this guide](#guides/data/treedata.md).
 * @mixin
 */

var StoreTree = (Target => class StoreTree extends (Target || Base) {
  static get $name() {
    return 'StoreTree';
  }

  static get configurable() {
    return {
      /**
       * Set to `true` to on load transform a flat dataset with raw objects containing `parentId` into the format
       * expected for tree data.
       *
       * Example input format:
       *
       * ```javascript
       * [
       *   { id : 1, name : 'Parent' },
       *   { id : 2, name : 'Child', parentId : 1 }
       * ]
       * ```
       *
       * Will be transformed into:
       *
       * ```javascript
       * [
       *   {
       *     id : 1,
       *     name : 'Parent',
       *     children : [
       *       { id : 2, name : 'Child', parentId : 1 }
       *     ]
       *   }
       * ]
       * ```
       *
       * @config {Boolean}
       * @category Tree
       */
      transformFlatData: null
    };
  } //region Getters

  /**
   * True if this Store is configured to handle tree data (with `tree : true`) or if this is a
   * {@link Core.data.Store#function-makeChained chained store} and the master store is a tree store.
   * @property {Boolean}
   * @readonly
   * @category Tree
   */

  get isTree() {
    return this.tree || this.masterStore && this.masterStore.tree;
  }
  /**
   * Returns all leaf records in a tree store
   * @property {Core.data.Model[]}
   * @category Tree
   */

  get leaves() {
    const me = this,
          result = [];

    if (me.isTree) {
      me.traverse(record => {
        if (record.isLeaf) {
          result.push(record);
        }
      });
      return result;
    } else {
      me.allRecords.forEach(r => {
        if (r.isLeaf) {
          result.push(r);
        }

        r.traverse(record => {
          if (record.isLeaf) {
            result.push(record);
          }
        }, true);
      });
    }

    return result;
  } //endregion
  //region Children

  /**
   * Loads children for a parent node that uses load on demand (when expanding it). Base implementation does nothing,
   * either use AjaxStore which implements it, create your own subclass with an implementation or listen for
   * `toggleNode` and insert records when you have them available.
   * @param {Core.data.Model} parentRecord
   * @returns {Promise} A Promise which will be resolved if the load succeeds, and rejected if the load is
   * @category Tree
   */

  async loadChildren(parentRecord) {}
  /**
   * Called from Model when adding children. Not to be called directly, use Model#appendChild() instead.
   * @internal
   * @param {Core.data.mixin.TreeNode} parent
   * @param {Core.data.mixin.TreeNode[]} children
   * @param {Number} index
   * @param {Object} isMove
   * @param {Boolean} [silent]
   * @fires add
   * @fires change
   * @category Tree
   */

  onNodeAddChild(parent, children, index, isMove, silent = false) {
    const me = this,
          isRootLoad = parent === me.rootNode && parent.isLoading,
          {
      storage
    } = me,
          toAddToUI = [],
          toAdd = [],
          previousSibling = children[0].previousSibling;
    let storeInsertionPoint;
    me.collectDescendants(children, toAddToUI, toAdd, {
      inCollapsedBranch: !(parent.isExpanded(me) && parent.ancestorsExpanded(me))
    }); // Keep CRUD caches up to date unless it's a root load

    if (!isRootLoad && toAdd.length) {
      for (const record of toAdd) {
        // Only considered an add if not modified or moved
        if (!me.modified.includes(record) && !isMove[record.id]) {
          // If was removed, remove from `removed` list
          if (me.removed.includes(record)) {
            me.removed.remove(record);
          } // Else add to `added` list
          else {
              me.added.add(record);
            }
        }
      }
    } // Root node inserted first

    if (isRootLoad && me.rootVisible) {
      toAddToUI.unshift(parent);
      toAdd.unshift(parent);
    }

    if (toAddToUI.length) {
      // Calculate the insertion point into the flat store.
      // If the new node is the first, then it goes after the parent node.
      if (index === 0 || !previousSibling) {
        storeInsertionPoint = storage.indexOf(parent);
      } // Otherwise it has to go after the previous visible node which has
      // to be calculated. See indexOfPreviousVisibleNode for explanation.
      else {
          storeInsertionPoint = storage.indexOf(previousSibling) + previousSibling.getDescendantCount(true, me);
        } // Insert added child nodes at correct location in storage.
      // We must not react to change - we fire the events here.

      storage.suspendEvents();
      me.storage.splice(++storeInsertionPoint, 0, toAddToUI);
      storage.resumeEvents();
    } // Since we do not pass through Store#onDataChange we have to handle relations manually here. And since they are
    // not tied to flat part of store, use all children

    me.updateDependentStores('add', children); // If it's a root level set data op, then signal 'dataset'

    if (isRootLoad && toAddToUI.length) {
      var _me$afterLoadData;

      // If we have initial sorters, perform a silent sort before triggering `dataset`
      // NOTE: Records in toAddToUI will be in the original order, not affected by the sort
      if (me.sorters.length) {
        me.sort(null, null, false, true);
      }

      (_me$afterLoadData = me.afterLoadData) === null || _me$afterLoadData === void 0 ? void 0 : _me$afterLoadData.call(me);

      if (!silent) {
        const event = {
          action: 'dataset',
          data: me._data,
          records: toAddToUI
        };
        me.trigger('refresh', event);
        me.trigger('change', event);
      }
    } // Else, continue as before to signal a bizarre "isChild" add.
    else if (!silent) {
        const event = {
          action: 'add',
          parent,
          isChild: true,
          isMove,
          records: children,
          allRecords: toAdd,
          index: storeInsertionPoint
        };
        me.trigger('add', event);
        me.trigger('change', event);
      }
  }

  onNodeRemoveChild(parent, children, index, flags = {
    isMove: false,
    silent: false,
    unfiltered: false
  }) {
    const me = this,
          {
      storage
    } = me,
          toRemoveFromUI = [],
          toRemove = [],
          {
      isMove,
      silent,
      unfiltered
    } = flags,
          removeUnfiltered = unfiltered && me.isFiltered,
          childrenToRemove = removeUnfiltered && parent.unfilteredChildren ? parent.unfilteredChildren : children;
    me.collectDescendants(childrenToRemove, toRemoveFromUI, toRemove, {
      inCollapsedBranch: !(parent.isExpanded(me) && parent.ancestorsExpanded(me)),
      unfiltered: removeUnfiltered
    }); // test StoreTree.t.js should fail if the next line replaces the above line
    // me.collectDescendants(children, toRemoveFromUI, toRemove, { inCollapsedBranch : !(parent.isExpanded(me) && parent.ancestorsExpanded(me)) });

    if (!isMove) {
      // Unjoin is recursive, use flat children array
      for (const record of children) {
        record.unJoinStore(me);
      } // Keep CRUD caches up to date

      if (toRemove.length) {
        for (const record of toRemove) {
          if (record.stores.includes(me)) {
            record.unJoinStore(me);
          } // If was newly added, remove from `added` list

          if (me.added.includes(record)) {
            me.added.remove(record);
          } // Else add to `removed` list
          else {
              me.removed.add(record);
            }
        }

        me.modified.remove(toRemove);
      }
    } // Remove removed child nodes at correct location in storage

    if (toRemoveFromUI.length) {
      index = storage.indexOf(toRemoveFromUI[0]); // We must not react to change - we fire the events here.

      if (index > -1) {
        storage.suspendEvents();
        storage.splice(index, toRemoveFromUI.length);
        storage.resumeEvents();
      }
    } else {
      // If nothing is removed from UI (storage) return -1, showing that removed node was in the collapsed branch
      index = -1;
    }

    if (!silent) {
      const event = {
        action: 'remove',
        parent,
        isChild: true,
        isMove,
        records: children,
        allRecords: toRemove,
        index
      };
      me.trigger('remove', event);
      me.trigger('change', event);
    }
  }

  collectDescendants(node, visible = [], all = [], flags = {
    inCollapsedBranch: false,
    unfiltered: false
  }) {
    const me = this,
          {
      inCollapsedBranch,
      unfiltered
    } = flags,
          children = Array.isArray(node) ? node : me.getChildren(node, unfiltered);

    if (children) {
      for (let i = 0, len = children.length, child; i < len; i++) {
        child = children[i];

        if (!inCollapsedBranch) {
          visible.push(child);
        }

        all.push(child);
        me.collectDescendants(child, visible, all, {
          inCollapsedBranch: inCollapsedBranch || !child.isExpanded(me),
          unfiltered
        });
      }
    }

    return {
      visible,
      all
    };
  }
  /**
   * Returns the children of the passed branch node which this store owns. By default, this
   * is the entire `children` array.
   *
   * **If this store {@link Core.data.mixin.StoreChained#property-isChained isChained}**, then
   * this returns only the subset of children which are filtered into this store by the
   * {@link Core.data.mixin.StoreChained#config-chainedFilterFn chainedFilterFn}.
   * @param {Core.data.Model} parent The node to return the children of.
   * @returns {Core.data.Model[]}
   * @category Tree
   */

  getChildren(parent, unfiltered = false) {
    // In case of chained store we need to apply chainedFilterFn to all records
    const children = (unfiltered || this.isChained) && parent.unfilteredChildren || parent.children;
    return children && children.length ? this.isChained ? children.filter(this.chainedFilterFn) : children : emptyArray$3;
  }
  /**
   * Includes or excludes all records beneath parentRecord in storage. Used when expanding or collapsing
   * nodes.
   * @private
   * @param parentRecord Parent record
   * @param include Include (true) or exclude (false)
   * @category Tree
   */

  internalToggleTreeSubRecords(parentRecord, include) {
    const me = this,
          {
      storage
    } = me,
          index = storage.indexOf(parentRecord),
          children = me.doIncludeExclude(me.getChildren(parentRecord), include); // If we expanded a node which is yet to load children, the collected children
    // array will be empty, so do not broadcast any change event.
    // If we are collapsing a record which isn't visible (because parent is collapsed) we won't get an index,
    // which is fine since it is already removed from processedRecords

    if (children.length && index !== false) {
      // We must not react to change - we fire the events here with a flag
      // to tell responders that it's due to an expoand or collapse.
      storage.suspendEvents();

      if (include) {
        storage.splice(index + 1, 0, ...children);
        const event = {
          action: 'add',
          isExpand: true,
          records: children,
          index: index + 1
        };
        me.trigger('add', event);
        me.trigger('change', event);
      } else {
        storage.splice(index + 1, children.length);
        const event = {
          action: 'remove',
          isCollapse: true,
          records: children,
          index: index + 1
        };
        me.trigger('remove', event);
        me.trigger('change', event);
      }

      storage.resumeEvents();
    }
  }

  doIncludeExclude(children, include, result = []) {
    const me = this,
          childCount = children && children.length;

    for (let i = 0; i < childCount; i++) {
      const child = children[i]; // Only consider child nodes who we own.
      // If we are a chained store, skip nodes that are not ours.

      if (!me.isChained || me.chainedFilterFn(child)) {
        const mapMeta = child.instanceMeta(me.id);

        if (include) {
          // if including subrecords, add those who are not hidden by a collapsed sub parent
          result.push(child);
        } else if (!mapMeta.hidden) {
          result.push(child);
        }

        mapMeta.hidden = !include;

        if (child.isExpanded(me)) {
          me.doIncludeExclude(me.getChildren(child), include, result);
        }
      }
    }

    return result;
  }
  /**
   * Collapse an expanded record or expand a collapsed. Optionally forcing a certain state.
   * @param {String|Number|Core.data.Model} idOrRecord Record (the record itself) or id of a record to toggle
   * @param {Boolean} [collapse] Force collapse (true) or expand (false)
   * @returns {Promise}
   * @async
   * @category Tree
   */

  async toggleCollapse(idOrRecord, collapse) {
    const me = this,
          record = me.getById(idOrRecord),
          meta = record.instanceMeta(me);

    if (collapse === undefined) {
      collapse = !meta.collapsed;
    } // Reject if we're in the middle of loading children, or it's a leaf, or it's a no-op

    if (!meta.isLoadingChildren && !record.isLeaf && record.isExpanded(me) === collapse) {
      me.trigger('beforeToggleNode', {
        record,
        collapse
      });
      meta.collapsed = collapse;

      if (meta.collapsed) {
        me.onNodeCollapse(record);
        return true;
      } else {
        me.onNodeExpand(record);
        let success = true; // Children not yet loaded, ask store for them.
        // It will append them. Appending to a node which
        // is expandded will insert the children into the UI.

        if (!record.isLoaded) {
          meta.isLoadingChildren = true;

          try {
            await me.loadChildren(record);
          } catch (exception) {
            // Revert to being collapsed
            meta.collapsed = true;
            success = false;
            me.trigger('loadChildrenException', {
              record,
              exception
            });
          } finally {
            meta.isLoadingChildren = false;
          }
        }

        return success;
      }
    }
  }
  /**
   * Remove all records beneath parentRecord from storage.
   * @private
   * @param parentRecord Parent record
   * @category Tree
   */

  onNodeCollapse(parentRecord) {
    // We don't care about collapse if it's inside a collapsed subtree
    if (parentRecord.ancestorsExpanded(this)) {
      return this.internalToggleTreeSubRecords(parentRecord, false);
    }
  }
  /**
   * Add all records beneath parentRecord from storage.
   * @private
   * @param parentRecord Parent record
   * @category Tree
   */

  onNodeExpand(parentRecord) {
    // We don't care about expand if it's inside a collapsed subtree
    if (parentRecord.ancestorsExpanded(this)) {
      return this.internalToggleTreeSubRecords(parentRecord, true);
    }
  } //endregion
  //region Transform flat data

  /**
   * Transforms flat data containing parent ids into tree data
   * @param {Object[]} data Flat raw data
   * @returns {Object[]} Tree data
   * @private
   */

  transformToTree(data) {
    const {
      parentIdField,
      idField,
      childrenField
    } = this.modelClass,
          parents = new Map(),
          transformed = [];

    for (const node of data) {
      const parentId = node[parentIdField]; // Child, find parent

      if (parentId != null) {
        // Detected parents are kept in a map to no have to iterate to find them each time
        let parent = parents.get(parentId); // Parent not in map, find it and add it (also adds unmatched parents, to not search for
        // them again)

        if (parent === undefined) {
          var _data$find;

          parent = (_data$find = data.find(node => node[idField] === parentId)) !== null && _data$find !== void 0 ? _data$find : null;
          parents.set(parentId, parent);
        } // Parent found, add node as child of it

        if (parent) {
          if (!parent[childrenField]) {
            parent[childrenField] = [];
          }

          parent[childrenField].push(node);
        }
      } // Parent, keep
      else {
          transformed.push(node);
        }
    }

    return transformed;
  } //endregion

});

/**
 * @module Core/helper/WalkHelper
 */

/**
 * Tree walking helper
 * @internal
 */
class WalkHelper {
  /**
   * Pre-walks any hierarchical data structure
   *
   * @param data Walking starting point
   * @param {Function} childrenFn Function to return `data` children entries in an array
   *                   or null if no children exists for the entry
   * @param {Function} fn Function to call on each entry
   */
  static preWalk(data, childrenFn, fn) {
    const walkStack = [data];
    let node, children;

    while (walkStack.length) {
      node = walkStack.pop();
      fn(node);
      children = childrenFn(node);

      if (children) {
        walkStack.push.apply(walkStack, children.slice().reverse());
      }
    }
  }
  /**
   * Pre-walks any hierarchical data structure, passing along a link to the parent node
   *
   * @param data Walking starting point
   * @param {Function} childrenFn Function to return `data` children entries in an array
   *                   or null if no children exists for the entry
   * @param {Function} fn Function to call on each entry, called with `parent` and `node`
   */

  static preWalkWithParent(data, childrenFn, fn) {
    const walkStack = [{
      node: data,
      parent: null
    }];

    while (walkStack.length) {
      const {
        parent,
        node
      } = walkStack.pop();
      fn(parent, node);
      const children = childrenFn(node);

      if (children) {
        walkStack.push(...children.slice().reverse().map(child => ({
          node: child,
          parent: node
        })));
      }
    }
  }
  /**
   * Pre-walk unordered.
   *
   * Like {@link #function-preWalk-static preWalk} but doesn't reverses children before walk,
   * thus children will be walked last child first - first child last
   *
   * @param data Walking starting point
   * @param {Function} childrenFn Function to return `data` children entries in an array
   *                   or null if no children exists for the entry
   * @param {Function} fn Function to call on each entry
   */

  static preWalkUnordered(data, childrenFn, fn) {
    let walkStack = [data],
        node,
        children;

    while (walkStack.length) {
      node = walkStack[walkStack.length - 1];
      fn(node);
      children = childrenFn(node);

      if (children) {
        walkStack.splice(walkStack.length - 1, 1, ...children);
      } else {
        walkStack.length = walkStack.length - 1;
      }
    }
  }
  /**
   * Post-walks any hierarchical data structure
   *
   * @param data Walking starting point
   * @param {Function} childrenFn Function to return `data` children entries in an array
   *                   or null if no children exists for the entry
   * @param {Function} fn Function to call on each entry
   */

  static postWalk(data, childrenFn, fn) {
    let visited = new Map(),
        walkStack = [data],
        node,
        children;

    while (walkStack.length) {
      node = walkStack[walkStack.length - 1];

      if (visited.has(node)) {
        fn(node);
        walkStack.pop();
      } else {
        children = childrenFn(node);

        if (children) {
          walkStack = walkStack.concat(children.slice().reverse());
        }

        visited.set(node, node);
      }
    }
  }
  /**
   * Pre/Post-walks any hierarchical data structure calling inFn each node when it walks in,
   * and outFn when it walks out.
   *
   * @param data Walking starting point
   * @param {Function} childrenFn Function to return `data` children entries in an array
   *                   or null if no children exists for the entry
   * @param {Function} inFn  Function to call on each entry upon enter
   * @param {Function} outFn Function to call on each entry upon exit
   */

  static prePostWalk(data, childrenFn, inFn, outFn) {
    let visited = new Map(),
        walkStack = [data],
        node,
        children;

    while (walkStack.length) {
      node = walkStack[walkStack.length - 1];

      if (visited.has(node)) {
        outFn(node);
        walkStack.pop();
      } else {
        inFn(node);
        children = childrenFn(node);

        if (children) {
          walkStack = walkStack.concat(children.slice().reverse());
        }

        visited.set(node, node);
      }
    }
  }

}
WalkHelper._$name = 'WalkHelper';

/**
 * @module Core/data/mixin/StoreSync
 */

/**
 * Mixin that allows Store to sync a new dataset with its existing records, instead of fully replacing everything.
 * Configure Store with `syncDataOnLoad: true` to activate the functionality. Sync is performed when a new dataset
 * is loaded, either by directly assigning it to `store.data` or by loading it using Ajax (if using an AjaxStore).
 *
 * ```javascript
 * const store = new Store({
 *   syncDataOnLoad : true,
 *   data           : [
 *     { id : 1, name : 'Saitama' },
 *     { id : 2, name : 'Genos' },
 *     { id : 3, name : 'Mumen Rider' }
 *   ]
 * });
 *
 * // Sync a new dataset by assigning to data:
 * store.data = [
 *   { id : 1, name : 'Caped Baldy' },
 *   { id : 4, name : 'Horse-Bone' }
 * ];
 *
 *  // Result : Record 1 updated, record 2 & 3 removed, record 4 added
 * ```
 *
 * For more details, please see {@link #config-syncDataOnLoad}.
 *
 * @mixin
 */

var StoreSync = (Target => class StoreSync extends (Target || Base) {
  static get $name() {
    return 'StoreSync';
  }

  static get defaultConfig() {
    return {
      /**
       * Configure with `true` to sync loaded data instead of replacing existing with a new dataset.
       *
       * By default (or when configured with `false`) assigning to `store.data` replaces the entire dataset
       * with a new one, creating all new records:
       *
       * ```javascript
       * store.data = [ { id : 1, name : 'Saitama' } ];
       *
       * const first = store.first;
       *
       * store.data = [ { id : 1, name : 'One-Punch man' } ];
       *
       * store.first !== first;
       * ```
       *
       * When configured with `true` the new dataset is instead synced against the old, figuring out what was
       * added, removed and updated:
       *
       * ```javascript
       * store.data = [ { id : 1, name : 'Saitama' } ];
       *
       * const first = store.first;
       *
       * store.data = [ { id : 1, name : 'One-Punch man' } ];
       *
       * store.first === first;
       * ```
       *
       * After the sync, any configured sorters, groupers and filters will be reapplied.
       *
       * The sync operation has a configurable threshold, above which the operation will be treated as a
       * batch/refresh and only trigger a single `refresh` event. If threshold is not reached, individual events
       * will be triggered (single `add`, `remove` and possible multiple `update`). To enable the threshold,
       * supply a config object with a `threshold` property instead of `true`:
       *
       * ```javascript
       * const store = new Store({
       *     syncDataOnLoad : {
       *         threshold : '20%'
       *     }
       * });
       * ```
       *
       * `threshold` accepts numbers or strings. A numeric threshold means number of affected records, while a
       * string is used as a percentage of the whole dataset (appending `%` is optional). By default no threshold
       * is used.
       *
       * @config {Boolean|Object}
       * @default false
       */
      syncDataOnLoad: null
    };
  }
  /**
   * Syncs a new dataset against the already loaded one, only applying changes.
   * Not intended to be called directly, please configure store with `syncDataOnLoad: true` and assign to
   * `store.data` as usual instead.
   *
   * ```
   * const store = new Store({
   *    syncDataOnLoad : true,
   *    data : [
   *        // initial data
   *    ]
   * });
   *
   * store.data = [ // new data ]; //  Difference between initial data and new data will be applied
   * ```
   *
   * @param {Object[]} data New dataset
   * @private
   */

  syncDataset(data) {
    const me = this,
          {
      storage
    } = me,
          {
      toAdd,
      toRemove,
      updated
    } = me.tree ? me.syncTreeDataset(data) : me.syncFlatDataset(data);
    let {
      threshold
    } = me.syncDataOnLoad,
        surpassed = false; // Check if threshold is surpassed

    if (threshold) {
      // Any string is treated as a percentage
      if (typeof threshold === 'string') {
        threshold = parseInt(threshold, 10) / 100 * me.count;
      }

      surpassed = toAdd.length + toRemove.length + updated.length > threshold;
    }

    if (me.tree) {
      // Flat data is spliced into/out of the collection, but in a tree it has to be added/removed from store
      // to end up on correct parents
      if (toAdd.length) {
        // Add all new nodes in one go, will be added to correct parent using `parentId`. Triggering multiple times
        const added = me.add(toAdd, surpassed); // parentId was tucked on in syncTreeDataset() to allow the single flat add above, clean it out

        added.forEach(node => node.clearParentId());
      } // Remove in one go, removing from each parent. Triggering multiple times

      me.remove(toRemove, surpassed);
    } else {
      if (surpassed) {
        storage.suspendEvents();
      } // Add and remove, will trigger if below threshold/no threshold

      storage.splice(me.count - toRemove.length, toRemove, toAdd);

      if (surpassed) {
        storage.resumeEvents();
      }
    } // Trigger updates if using threshold, but have not surpassed it. If threshold is not used, the updates
    // are triggered when data is set (avoiding another iteration)

    if (threshold && !surpassed) {
      updated.forEach(({
        record,
        toSet,
        wasSet
      }) => me.onModelChange(record, toSet, wasSet));
    } // Clear change-tracking

    me.acceptChanges();
    const event = {
      added: toAdd,
      removed: toRemove,
      updated,
      thresholdSurpassed: surpassed
    }; // Trigger `batch` if threshold is surpassed, more similar to a batch than a full `dataset`

    if (surpassed) {
      this.trigger('refresh', {
        action: 'batch',
        data: data,
        records: storage.values,
        syncInfo: event
      });
    }

    if (me.isFiltered) {
      // Announced filter
      me.filter();
    }

    if (me.isGrouped) {
      // Announced sort
      me.group();
    } else if (me.isSorted) {
      // Announced sort
      me.sort();
    }

    me.trigger('loadSync', event);
  } // Used by syncDataset()

  syncFlatDataset(data) {
    if (!data) {
      return;
    }

    const me = this,
          idField = me.modelClass.idField,
          toRemove = [],
          toAdd = [],
          updated = [],
          usedIds = {};
    let {
      threshold
    } = me.syncDataOnLoad,
        hitCount = 0;
    data.forEach(rawData => {
      const id = rawData[idField],
            record = me.getById(id); // Record exists, might be an update

      if (record) {
        // Update silently if using threshold, otherwise trigger away
        const wasSet = record.set(rawData, null, Boolean(threshold));

        if (wasSet) {
          updated.push({
            record,
            wasSet,
            toSet: rawData
          });
        }

        hitCount++;
      } // Does not exist, add
      else {
          toAdd.push(me.processRecord(me.createRecord(rawData)));
        }

      usedIds[id] = 1;
    }); // Check removals, unless all records were visited above

    if (hitCount < me.allCount) {
      me.forEach(record => {
        if (!usedIds[record.id]) {
          toRemove.push(record);
        }
      });
    }

    return {
      toAdd,
      toRemove,
      updated
    };
  } // Used by syncDataset()

  syncTreeDataset(data) {
    if (!data) {
      return;
    }

    const me = this,
          {
      idField,
      parentIdField
    } = me.modelClass,
          toRemove = [],
          toAdd = [],
          updated = [],
          usedIds = {};
    const {
      threshold
    } = me.syncDataOnLoad;
    WalkHelper.preWalkWithParent({
      isRoot: true,
      children: data
    }, n => n.children, (parent, rawData) => {
      if (parent) {
        const id = rawData[idField],
              node = me.getById(id); // Record exists, might be an update

        if (node) {
          // Update silently if using threshold, otherwise trigger away
          const wasSet = node.set(rawData, null, Boolean(threshold));

          if (wasSet) {
            updated.push({
              record: node,
              wasSet,
              toSet: rawData
            });
          }
        } // Does not exist, add
        else {
            rawData[parentIdField] = parent[idField];
            toAdd.push(rawData);
          }

        usedIds[id] = 1;
      }
    });
    me.traverse(node => {
      if (!usedIds[node.id]) {
        toRemove.push(node);
      }
    });
    return {
      toAdd,
      toRemove,
      updated
    };
  }

});

/**
 * @module Core/data/stm/mixin/StoreStm
 */
const STM_PROP$1 = Symbol('STM_PROP');
/**
 * Store mixin to make it compatible with {@link Core.data.stm.StateTrackingManager}.
 * @mixin
 */

var StoreStm = (Target => class StoreStm extends (Target || Base) {
  static get $name() {
    return 'StoreStm';
  }

  static get defaultConfig() {
    return {
      /**
       * Reference to STM manager
       *
       * @config {Core.data.stm.StateTrackingManager}
       * @default
       * @category Advanced
       */
      stm: null
    };
  }

  get stm() {
    return this[STM_PROP$1];
  }

  set stm(stm) {
    const me = this;

    if (me.stm != stm) {
      if (me.stm && me.stm.hasStore(me)) {
        me.stm.removeStore(me);
      }

      me[STM_PROP$1] = stm;

      if (me.stm && !me.stm.hasStore(me)) {
        me.stm.addStore(me);
      }
    }
  } // Overridden to notify STM about flat add action

  add(records, silent = false) {
    let result;
    const stm = this.stm; // Tree adding is routed via rootNode.appendChild() it has it's own
    // STM override thus if the store is tree we ignore the action

    if (!this.tree && stm && !stm.disabled) {
      // Flat adding here only, the only data needed to undo/redo the action
      // is the list of records added.
      result = super.add(records, silent); // If adding wasn't vetoed and something has been added then
      // notifying the STM about the fact.

      if (result && result.length) {
        stm.onStoreModelAdd(this, result, silent);
      }
    } else {
      result = super.add(records, silent);
    }

    return result;
  } // Overridden to notify STM about flat insert action

  insert(index, records, silent = false) {
    let result;
    const stm = this.stm; // Tree inserting is routed via rootNode.insertChild() it has it's own
    // STM override thus if the store is tree we ignore the action

    if (!this.tree && stm && !stm.disabled) {
      // Flat inserting here only, the only data needed to undo/redo the action is:
      // - the list of record inserted
      // - index they are inserted at
      // - index they have been at if they are part of this store already and are moved
      // Here we are getting indexes of records which are in this store already
      // not all records might be from this store, some might be new or from another store
      const context = (Array.isArray(records) ? records : [records]).reduce((context, r) => {
        const index = r instanceof Model ? this.indexOf(r) : undefined;

        if (index !== undefined && index !== -1) {
          context.set(r, index);
        }

        return context;
      }, new Map()); // Result here is the array of Models inserted or undefined,
      // and it might be different from `records` we received as argument.

      result = super.insert(index, records); // Here we check if anything has been actually inserted.
      // The insertion action might be vetoed by event handler or something

      if (result && result.length) {
        // We can't rely on `index` we've got as argument since `result` might
        // differ from records.
        index = this.indexOf(result[0]); // Notifying STM manager about the insertion action providing all
        // the required data to undo/redo.

        stm.onStoreModelInsert(this, index, result, context, silent);
      }
    } else {
      result = super.insert(index, records, silent);
    }

    return result;
  } // Overridden to notify STM about flat removing action

  remove(recordsOrIds, silent = false, fromRemoveChild) {
    let result;
    const stm = this.stm; // Tree removing is routed via rootNode.removeChild() it has it's own
    // STM override thus if the store is tree we ignore the action

    if (!this.tree && stm && !stm.disabled) {
      // Flat removing here only, the only date needed to undo/redo the actions is:
      // - the list of records removed
      // - their original index to re-insert them back correctly
      const recordsOrIdsNormalized = (Array.isArray(recordsOrIds) ? recordsOrIds : [recordsOrIds]).map(r => this.getById(r)).filter(r => !!r);
      const context = recordsOrIdsNormalized.reduce((context, r) => {
        const index = this.indexOf(r);

        if (index !== undefined && index != -1) {
          context.set(r, index);
        }

        return context;
      }, new Map()); // Calling original store method

      result = super.remove(recordsOrIds, silent, fromRemoveChild); // Here we check if anything has been actually removed.
      // The removing action might be vetoed by event handler or something

      if (result && result.length) {
        stm.onStoreModelRemove(this, result, context, silent);
      }
    } else {
      result = super.remove(recordsOrIds, silent, fromRemoveChild);
    }

    return result;
  } // Overridden to notify STM about flat clear action

  removeAll(silent) {
    const stm = this.stm;
    let result;

    if (stm && !stm.disabled) {
      // Here we are to detect if anything has been removed
      // the only way is to check if store has anything before removing all
      // and has nothing after.
      const {
        tree,
        rootNode,
        allRecords
      } = this,
            wasNotEmpty = allRecords.length,
            // need to store children/records before super method call, otherwise those would report empty list
      records = tree ? rootNode.children.slice() : allRecords.slice();
      result = super.removeAll(silent); // The trick here is to distinguish tree and flat case
      // For the flat case it's simple we just store all records
      // For the tree we are to store root node children only
      // Upon restoring store.add() will do the right thing for the flat case and tree case regardless.

      if (wasNotEmpty && this.count === 0) {
        stm.onStoreRemoveAll(this, records, silent);
      }
    } else {
      result = super.removeAll(silent);
    }

    return result;
  }

});

/* eslint-disable no-unused-expressions */
/**
 * The Store represents a data container which holds flat data or tree structures. An item in the Store is often called a ´record´ and it is simply an instance of the
 * {@link Core.data.Model} (or any subclass thereof). Typically you load data into a store to display it in a Grid or a ComboBox. The Store is the backing data component for any component that is showing data in a list style UI.
 *
 * * {@link Grid.view.Grid}
 * * {@link Grid.view.TreeGrid}
 * * {@link Core.widget.List}
 * * {@link Core.widget.Combo}
 *
 * ## Data format
 * Data is store in a JSON array the Store offers an API to edit, filter, group and sort the records.
 *
 * ## Store with flat data
 * To create a flat store simply provide an array of objects that describe your records
 *
 * ```javascript
 * const store = new Store({
 *   data : [
 *     { id : 1, name : 'ABBA', country : 'Sweden' },
 *     { id : 2, name : 'Beatles', country : 'UK' }
 *   ]
 * });
 *
 * // retrieve record by id
 * const beatles = store.getById(2);
 * ```
 *
 * ## Store with tree data
 * To create a tree store use `children` property for descendant records
 *
 * ```javascript
 * const store = new Store({
 *   tree: true,
 *   data : [
 *     { id : 1, name : 'ABBA', country : 'Sweden', children: [
 *       { id: 2, name: 'Agnetha' },
 *       { id: 3, name: 'Bjorn' },
 *       { id: 4, name: 'Benny' },
 *       { id: 5, name: 'Anni-Frid' }
 *     ]},
 *   ]
 * });
 *
 * // retrieve record by id
 * let benny = store.getById(4);
 * ```
 *
 * Optionally a tree store can consume a flat dataset with nodes that have a `parentId` property. By configuring the
 * store with `tree : true` and `transformFlatData : true`, the flat data is transformed into tree data:
 *
 * ```javascript
 * const store = new Store({
 *   tree              : true,
 *   transformFlatData : true,
 *   data              : [
 *     { id : 1, name : 'ABBA', country : 'Sweden' },
 *     { id : 2, name: 'Agnetha', parentId : 1 },
 *     { id : 3, name: 'Bjorn', parentId : 1 },
 *     { id : 4, name: 'Benny', parentId : 1 },
 *     { id : 5, name: 'Anni-Frid', parentId : 1 }
 *   ]
 * });
 * ```
 *
 * ### Retrieving and consuming JSON
 * For both flat stores or tree stores it is possible to retrieve the data of all records in JSON format:
 *
 * ```javascript
 * const jsonString = store.json;
 *
 * // or
 *
 * const jsonArray = store.toJSON();
 * ```
 *
 * To plug the JSON data back in later:
 *
 * ```javascript
 * store.data = JSON.parse(jsonString);
 *
 * // or
 *
 * store.data = jsonArray;
 * ```
 *
 * ## Sharing stores
 * You cannot directly share a Store between widgets, but the data in a Store can be shared. There are two different
 * approaches depending on your needs, sharing data and chaining stores:
 *
 * ### Shared data
 * To create 2 widgets that share data, you can create 2 separate stores and pass records of the first store as the
 * dataset of the second store.
 *
 * ```javascript
 * let combo1 = new Combo({
 *     appendTo : document.body,
 *     store    : new Store({
 *         data : [
 *             { id : 1, name : 'ABBA', country : 'Sweden' },
 *             { id : 2, name : 'Beatles', country : 'UK' }
 *         ]
 *     }),
 *     valueField   : 'id',
 *     displayField : 'name'
 * });
 *
 * let combo2 = new Combo({
 *     appendTo : document.body,
 *     store    : new Store({
 *         data : combo1.store.records
 *     }),
 *     valueField   : 'id',
 *     displayField : 'name'
 * });
 *
 * combo1.store.first.name = 'foo';
 * combo2.store.first.name; // "foo"
 * ```
 *
 * ### Chained stores
 * Another more powerful option to share data between widgets is to create {@link Core.data.mixin.StoreChained chained stores}.
 * The easiest way to create a chained store is to call {@link #function-chain} function.
 *
 * ```javascript
 * let combo1 = new Combo({
 *     appendTo : document.body,
 *     store    : new Store({
 *         data : [
 *             { id : 1, name : 'ABBA', country : 'Sweden' },
 *             { id : 2, name : 'Beatles', country : 'UK' }
 *         ]
 *     }),
 *     valueField   : 'id',
 *     displayField : 'name'
 * });
 *
 * let combo2 = new Combo({
 *     appendTo : document.body,
 *     store    : combo1.store.chain(),
 *     valueField   : 'id',
 *     displayField : 'name'
 * });
 *
 * combo1.store.first.name = 'foo';
 * combo2.store.first.name; // "foo"
 * ```
 *
 * A chained store can optionally be created with a filtering function, to only contain a subset of the records from
 * the main store. In addition, the chained store will reflect record removals/additions to the master store, something
 * the shared data approach will not.
 *
 * @mixes Core/mixin/Events
 * @mixes Core/data/mixin/StoreFilter
 * @mixes Core/data/mixin/StoreCRUD
 * @mixes Core/data/mixin/StoreSum
 * @mixes Core/data/mixin/StoreSearch
 * @mixes Core/data/mixin/StoreSort
 * @mixes Core/data/mixin/StoreGroup
 * @mixes Core/data/mixin/StoreChained
 * @mixes Core/data/mixin/StoreState
 * @mixes Core/data/mixin/StoreTree
 * @mixes Core/data/stm/mixin/StoreStm
 * @mixes Core/data/mixin/StoreSync
 *
 * @extends Core/Base
 */

class Store extends Base.mixin(Identifiable, Events, Pluggable, State, StoreFilter, StoreCRUD, StoreRelation, // Private
StoreSum, StoreSearch, StoreSort, StoreGroup, StoreChained, StoreState, StoreTree, StoreStm, StoreSync, StoreProxy // Private for now, thus not mentioned in @mixes block above
) {
  //region Config & properties
  static get properties() {
    return {
      relationCache: {},
      dependentStoreConfigs: new Map()
    };
  }

  static get configurable() {
    return {
      /**
       * Store's unique identifier. When set the store is added to a store map accessible through Store#getStore(id)
       * @config {String|Number}
       * @category Common
       */
      id: true,

      /**
       * Class used to represent records
       * @config {Core.data.Model}
       * @default
       * @category Common
       * @typings { new(data: object): Model }
       */
      modelClass: Model
    };
  }

  static get defaultConfig() {
    return {
      /**
       * An array of field definitions used to create a Model (modelClass) subclass. Optional. If the Model
       * already has fields defined, these fields will extend those.
       * @config {Object[]}
       * @category Common
       */
      fields: null,

      /**
       * Automatically detect from set data if used as tree store or flat store
       * @config {Boolean}
       * @default
       * @category Tree
       */
      autoTree: true,

      /**
       * Raw data to load initially
       * @config {Object[]}
       * @category Common
       */
      data: null,

      /**
       * `true` to act as a tree store.
       * @config {Boolean}
       * @category Tree
       */
      tree: false,
      callOnFunctions: true,

      /**
       * A {@link Core.util.Collection Collection}, or Collection config object
       * to use to contain this Store's constituent records.
       * @config {Core.util.Collection|Object}
       */
      storage: null,

      /**
       * Retools the loaded data objects instead of making shallow copies of them. This increases performance but
       * pollutes the incoming data and does not allow remapping of fields (dataSource).
       *
       * Also allows disabling certain steps in data loading, to further improve performance. Either accepts an
       * object with the params described below or `true` which equals `disableDuplicateIdCheck` and
       * `disableTypeConversion`.
       *
       * ```javascript
       * // No duplicate id checking, no type conversions
       * new Store({ useRawData : true });
       *
       * new Store({
       *   // No type conversions only
       *   useRawData : {
       *     disableTypeConversion : true
       *   }
       * });
       * ```
       *
       * @config {Boolean|Object}
       * @param {Boolean} [disableDuplicateIdCheck] Data must not contain duplicate ids, check is bypassed.
       * @param {Boolean} [disableDefaultValue] Default values will not be applied to record fields.
       * @param {Boolean} [disableTypeConversion] No type conversions will be performed on record data.
       * @category Advanced
       */
      useRawData: false,

      /**
       * Specify `false` to prevent loading records without ids, a good practise to enforce when syncing with a
       * backend. By default Store allows loading records without ids, in which case a generated id will be
       * assigned.
       * @config {Boolean}
       * @default true
       * @category Advanced
       */
      allowNoId: true,

      /**
       * Prevent dynamically subclassing the modelClass. It does so by default to not pollute it when exposing
       * properties. Should rarely need to be used.
       * @config {Boolean}
       * @default false
       * @private
       * @category Advanced
       */
      preventSubClassingModel: null
    };
  }

  static get identifiable() {
    return {};
  }
  /**
   * Class used to represent records. Defaults to class Model.
   * @property {Core.data.Model}
   * @name modelClass
   * @category Records
   * @typings { new(data: object): Model }
   */
  //endregion
  //region Events

  /**
   * Fired when the id of a record has changed
   * @event idChange
   * @param {Core.data.Store} source This Store
   * @param {Core.data.Model} record Modified record
   * @param {String|Number} oldValue Old id
   * @param {String|Number} value New id
   */

  /**
   * Fired before record is modified in this store.
   * Modification may be vetoed by returning `false` from a handler.
   * @event beforeUpdate
   * @param {Core.data.Store} source This Store
   * @param {Core.data.Model} record Modified record
   * @param {Object} changes Modification data
   */

  /**
   * Fired when a record is modified
   * @event update
   * @param {Core.data.Store} source This Store
   * @param {Core.data.Model} record Modified record
   * @param {Object} changes Modification data
   */

  /**
   * Fired when the root node is set
   * @event rootChange
   * @param {Core.data.Store} source This Store
   * @param {Core.data.Model} oldRoot The old root node.
   * @param {Core.data.Model} rootNode The new root node.
   */

  /**
   * Data in the store was changed. This is a catch-all event which is fired for all changes
   * which take place to the store's data.
   *
   * This includes mutation of individual records, adding and removal of records, as well as
   * setting a new data payload using the {@link #property-data} property, sorting, filtering,
   * and calling {@link Core.data.mixin.StoreCRUD#function-removeAll}.
   *
   * Simple databound widgets may use to the `change` event to refresh their UI without having to add multiple
   * listeners to the {@link #event-update}, {@link Core.data.mixin.StoreCRUD#event-add}, {@link Core.data.mixin.StoreCRUD#event-remove}, {@link #event-refresh}
   * and {@link Core.data.mixin.StoreCRUD#event-removeAll} events.
   *
   * A more complex databound widget such as a grid may use the more granular events to perform less
   * destructive updates more appropriate to each type of change. The properties will depend upon the value of the `action` property.
   * @event change
   * @param {Core.data.Store} source This Store.
   * @param {String} action Name of action which triggered the change. May be one of:
   * * `'remove'`
   * * `'removeAll'`
   * * `'add'`
   * * `'updatemultiple'`
   * * `'clearchanges'`
   * * `'filter'`
   * * `'sort'`
   * * `'update'`
   * * `'dataset'`
   * * `'replace'`
   */

  /**
   * Data in the store has completely changed, such as by a filter, or sort or load operation.
   * @event refresh
   * @param {Core.data.Store} source This Store.
   * @param {Boolean} batch Flag set to `true` when the refresh is triggered by ending a batch
   * @param {String} action Name of action which triggered the change. May be one of:
   * * `'dataset'`
   * * `'sort'`
   * * `'filter'`
   * * `'create'`
   * * `'update'`
   * * `'delete'`
   * * `'group'`.
   */
  //endregion

  /* break doc comment from next method */
  //region Init

  constructor(...args) {
    super(...args); // When using a Proxy, the Proxy is returned instead of the actual Store

    if (this.objectify) {
      return this.initProxy();
    }
  }

  construct(config = {}) {
    const me = this;
    Object.assign(me, {
      added: new StoreBag(),
      removed: new StoreBag(),
      modified: new StoreBag(),
      idRegister: {},
      internalIdRegister: {}
    });

    if (config.storeId) {
      // avoid changing id when setting storeId:
      config = ObjectHelper.assign({
        id: config.storeId
      }, config); // We cannot remove storeId since it can also be inherited and if so, it will override the id above:
      // delete config.storeId;
    }

    super.construct(config);
    me.initRelations();
  }

  doDestroy() {
    const me = this,
          allRecords = me.registeredRecords;

    for (let i = allRecords.length - 1, rec; i >= 0; i--) {
      var _rec;

      rec = allRecords[i];

      if (!((_rec = rec) !== null && _rec !== void 0 && _rec.isDestroyed)) {
        rec.unJoinStore(me);
      }
    }

    me.storage.destroy(); // Remove from STM if added there

    if (me.stm) {
      me.stm.removeStore(me);
    } // Events superclass fires destroy event.

    super.doDestroy(); //TODO abort any ongoing loads
  }
  /**
   * Stops this store from firing events until {@link #function-endBatch} is called. Multiple calls to `beginBatch`
   * stack up, and will require an equal number of `endBatch` calls to resume events.
   *
   * Upon call of {@link #function-endBatch}, a {@link #event-refresh} event is triggered to allow UIs to
   * update themselves based upon the new state of the store.
   *
   * This is extremely useful when making a large number of changes to a store. It is important not to trigger
   * too many UI updates for performance reasons. Batching the changes ensures that UIs attached to this
   * store are only updated once at the end of the updates.
   */

  beginBatch() {
    this.suspendEvents();
  }
  /**
   * Ends event suspension started by {@link #function-beginBatch}. Multiple calls to {@link #function-beginBatch}
   * stack up, and will require an equal number of `endBatch` calls to resume events.
   *
   * Upon call of `endBatch`, a {@link #event-refresh} event with `action: batch` is triggered to allow UIs to update
   * themselves based upon the new state of the store.
   *
   * This is extremely useful when making a large number of changes to a store. It is important not to trigger
   * too many UI updates for performance reasons. Batching the changes ensures that UIs attached to this
   * store are only updated once at the end of the updates.
   */

  endBatch() {
    if (this.resumeEvents()) {
      this.trigger('refresh', {
        action: 'batch',
        data: this.storage.values,
        records: this.storage.values
      });
    }
  }

  set storage(storage) {
    const me = this;

    if (storage !== null && storage !== void 0 && storage.isCollection) {
      me._storage = storage;
    } else {
      me._storage = new Collection(storage);
    }

    me._storage.autoFilter = me.reapplyFilterOnAdd; // Join all the constituent records to this Store

    for (const r of me._storage) {
      r.joinStore(me);
    }

    me._storage.on({
      change: 'onDataChange',
      thisObj: me
    });
  }

  get storage() {
    if (!this._storage) {
      this.storage = {};
    }

    return this._storage;
  }
  /**
   * Returns all records (not filtered) from the store.
   * @property {Core.data.Model[]}
   * @readonly
   * @category Records
   */

  get allRecords() {
    const me = this;

    if (me.isTree) {
      const result = me.collectDescendants(me.rootNode, undefined, undefined, {
        unfiltered: true
      }).all;

      if (me.rootVisible) {
        result.unshift(me.rootNode);
      }

      return result;
    } else {
      return me.isGrouped ? me.collectGroupRecords() : me.storage.allValues;
    }
  }
  /**
   * Responds to mutations of the underlying storage Collection
   * @param {Object} event
   * @protected
   */

  onDataChange({
    source: storage,
    action,
    added,
    removed,
    replaced,
    oldCount,
    items,
    from,
    to
  }) {
    const me = this,
          addedCount = added === null || added === void 0 ? void 0 : added.length,
          removedCount = removed === null || removed === void 0 ? void 0 : removed.length;
    let filtersWereReapplied;
    me._idMap = null;

    if (addedCount) {
      added.forEach(added => {
        added.joinStore(me);
      });
    }

    replaced === null || replaced === void 0 ? void 0 : replaced.forEach(([oldRecord, newRecord]) => {
      oldRecord.unJoinStore(me, true);
      newRecord.joinStore(me);
    }); // Allow mixins to mutate the storage before firing events.
    // StoreGroup does this to introduce group records into the mix.

    super.onDataChange(...arguments); // Join/unjoin incoming/outgoing records unless its as a result of TreeNode operations.
    // If we are a tree, joining is done when nodes are added/removed
    // as child nodes of a joined parent.

    if (!me.isTree) {
      if (addedCount) {
        for (const record of added) {
          // If was removed, remove from `removed` list
          if (me.removed.includes(record)) {
            me.removed.remove(record);
          } // Else add to `added` list
          else {
              me.added.add(record);
            }
        } // Re-evaluate the current *local* filter set silently so that the
        // information we are broadcasting below is up to date.

        filtersWereReapplied = !me.remoteFilter && me.filtered && me.reapplyFilterOnAdd;

        if (filtersWereReapplied) {
          me.filter({
            silent: true
          });
        }
      }

      if (removedCount) {
        for (const record of removed) {
          record.unJoinStore(me); // If was newly added, remove from `added` list

          if (me.added.includes(record)) {
            me.added.remove(record);
          } // Else add to `removed` list
          // (unless it's StateTrackingManager reverting the record insertion)
          else if (!record._undoingInsertion) {
              me.removed.add(record);
            }
        }

        me.modified.remove(removed); // Re-evaluate the current *local* filter set silently so that the
        // information we are broadcasting below is up to date.

        filtersWereReapplied = !me.remoteFilter && me.filtered;

        if (filtersWereReapplied) {
          me.filter({
            silent: true
          });
        }
      }
    }

    switch (action) {
      case 'clear':
        // Clear our own relationCache, since we will be empty
        me.relationCache = {}; // Signal to stores that depend on us

        me.updateDependentStores('removeall');
        me.trigger('removeAll');
        me.trigger('change', {
          action: 'removeall'
        });
        break;

      case 'splice':
        if (addedCount) {
          me.updateDependentStores('add', added);
          const // Collection does not handle moves, figure out if and where a record was moved from by checking
          // previous index value stored in meta
          oldIndex = added.reduce((lowest, record) => {
            const {
              previousIndex
            } = record.meta;
            if (previousIndex > -1 && previousIndex < lowest) lowest = previousIndex;
            return lowest;
          }, added[0].meta.previousIndex),
                index = storage.indexOf(added[0], !storage.autoFilter),
                params = {
            records: added,
            index
          }; // Only include param oldIndex when used

          if (oldIndex > -1) {
            params.oldIndex = oldIndex;
          }

          me.trigger('add', params);
          me.trigger('change', Object.assign({
            action: 'add'
          }, params));

          if (filtersWereReapplied) {
            me.triggerFilterEvent({
              action: 'filter',
              filters: me.filters,
              oldCount,
              records: me.storage.allValues
            });
          }
        }

        if (removed.length) {
          me.updateDependentStores('remove', removed);
          me.trigger('remove', {
            records: removed
          });
          me.trigger('change', {
            action: 'remove',
            records: removed
          });
        }

        if (replaced.length) {
          // TODO: Remove in 2.2 if no problems til then
          // me.trigger('updateMultiple', {
          //     records : removed,
          //     all     : me.records.length === replaced.length
          // });
          me.trigger('replace', {
            records: replaced,
            all: me.records.length === replaced.length
          });
          me.trigger('change', {
            action: 'replace',
            replaced,
            all: me.records.length === replaced.length
          });
        }

        break;

      case 'filter':
        // Reapply grouping/sorting to make sure unfiltered records get sorted correctly
        if (me.isGrouped) {
          me.group(null, null, null, null, true);
        } else if (me.isSorted) {
          me.performSort(true);
        }

        break;

      case 'move':
        // silently update parentIndex of records affected
        for (let allRecords = me.storage.allValues, i = Math.min(from, to); i <= Math.max(from, to); i++) {
          allRecords[i].setData('parentIndex', i);
        }
        /**
         * Fired when a block of records has been moved within this Store
         * @event move
         * @param {Core.data.Store} source This Store
         * @param {Core.data.Model[]} records The records moved.
         * @param {Number} from The index from which the record was removed.
         * @param {Number} to The index at which the record was inserted.
         */

        me.trigger('move', {
          record: items[0],
          records: items,
          from,
          to
        }); // The move was in real data. If we are filtered, the
        // filtered set has to be refreshed.

        if (me.isFiltered) {
          me.performFilter();
        }

        me.trigger('change', {
          action,
          record: items[0],
          records: items,
          from,
          to
        });
        break;
    }
  }

  onDataReplaced(action, data) {
    var _me$afterLoadData;

    const me = this;
    me.storage.allValues.forEach(r => {
      r.joinStore(me);
    }); // Need to update group records info (headers and footers)

    me.prepareGroupRecords(me.storage.values); // Restore collapsed state

    me.restoreCollapsedGroups(); // The three operations below, filter, store and sort, all are passed
    // the "silent" parameter meaning they do not fire their own events.
    // The 'refresh' and 'change' events after are used to update UIs.

    if (!me.remoteFilter && me.isFiltered) {
      me.filter({
        silent: true
      });
    } // TODO: groupers must just be promoted to be the primary sorters.

    if (me.isGrouped) {
      me.group(null, null, false, !me.sorters.length, true);
    }

    if (!me.sortParamName && me.sorters.length) {
      me.sort(null, null, false, true);
    } // Check for duplicate ids, unless user guarantees data validity

    if (!me.useRawData.disableDuplicateIdCheck) {
      const idMap = me.idMap;

      if (Object.keys(idMap).length < me.storage.values.length) {
        // idMap has fewer entries than expected, a duplicate id was used. pick idMap apart to find out which
        const collisions = [];
        me.storage.values.forEach(r => idMap[r.id] ? delete idMap[r.id] : collisions.push(r));
        throw new Error(`Id collision on ${collisions.map(r => r.id)}`);
      }
    }

    const event = {
      action,
      data,
      records: me.storage.values
    };
    me.updateDependentStores(action, event.records); // Allow subclasses to postprocess a new dataset

    (_me$afterLoadData = me.afterLoadData) === null || _me$afterLoadData === void 0 ? void 0 : _me$afterLoadData.call(me);
    me.trigger('refresh', event);
    me.trigger('change', event);
  }
  /**
   * This is called from Model after mutating any fields so that Stores can take any actions necessary
   * at that point, and distribute mutation event information through events.
   * @param {Core.data.Model} record The record which has just changed
   * @param {Object} toSet A map of the field names and values that were passed to be set
   * @param {Object} wasSet A map of the fields that were set. Each property is a field name, and
   * the property value is an object containing two properties: `oldValue` and `value` eg:
   * ```javascript
   *     {
   *         name {
   *             oldValue : 'Rigel',
   *             value : 'Nigel'
   *         }
   *     }
   *
   * @param {Boolean} silent Do not trigger events
   * @param {Boolean} fromRelationUpdate Update caused by a change in related model
   * @private
   */

  onModelChange(record, toSet, wasSet, silent, fromRelationUpdate) {
    const me = this,
          idField = record.constructor.idField,
          event = {
      record,
      changes: wasSet,
      // Cannot use isBatching, since change is triggered when batching has reached 0
      // (but before it is set to null)
      batch: record.batching != null,
      fromRelationUpdate
    },
          committable = record.ignoreBag ? false : me.updateModifiedBagForRecord(record); // Inform underlying collection of the changes, allowing it to keep any indices up to date

    this.storage.onItemMutation(record, wasSet);

    if (!silent) {
      if (idField in wasSet) {
        const {
          oldValue,
          value
        } = toSet[idField];
        me.updateDependentRecordIds(oldValue, value);
        me.onRecordIdChange({
          record,
          oldValue,
          value
        });
        me.trigger('idChange', {
          store: me,
          record,
          oldValue,
          value
        });
      }

      me.onUpdateRecord(record, wasSet);
      me.trigger('update', event);
      me.trigger('change', Object.assign({
        action: 'update'
      }, event));
    }

    if (me.autoCommit && committable) {
      me.doAutoCommit();
    }
  }

  updateModifiedBagForRecord(record) {
    const me = this;
    let addedToBag = false; // Add or remove from our modified Bag

    if (record.isModified) {
      if (!me.modified.includes(record) && !me.added.includes(record) && record.isPartOfStore(me) && !record.isAutoRoot) {
        // When we add a new model first time and the model is not persistable (for example when the model is not valid),
        // it is not added to the "added" collection (StoreBag), but only joined to the store.
        // So if the record is not added neither to "modified" nor "added",
        // need to check if this record is phantom. If so, add it to the "added", otherwise to the "modified".
        if (record.isPhantom) {
          me.added.add(record);
        } else {
          me.modified.add(record);
        }

        addedToBag = true;
      }
    } else {
      me.modified.remove(record);
    }

    return addedToBag;
  }

  get idMap() {
    const me = this,
          processedRecords = me.storage.values,
          needsRebuild = !me._idMap,
          idMap = me._idMap || (me._idMap = {});

    if (needsRebuild) {
      for (let record, index = 0, visibleIndex = 0; index < processedRecords.length; index++) {
        record = processedRecords[index];
        idMap[record.id] = {
          index,
          visibleIndex,
          record
        };

        if (!record.isSpecialRow) {
          visibleIndex++;
        }
      }
    }

    return idMap;
  }

  changeModelClass(ClassDef) {
    const fields = this.fields; // noinspection JSRedeclarationOfBlockScope

    let ClassDefEx = null; // Ensure our modelClass is exchanged for an extended of modelClass decorated with any configured fields.

    if (fields !== null && fields !== void 0 && fields.length) {
      ClassDefEx = class extends ClassDef {
        static get fields() {
          return fields;
        }

      };
    } // If we expose properties on Model we will pollute all other models, use internal subclass instead
    else if (!this.preventSubClassingModel) {
        ClassDefEx = class extends ClassDef {};
      } else {
        ClassDefEx = ClassDef;
      } // Need to properly expose relations on this new subclass

    ClassDefEx.initClass();
    return ClassDefEx;
  } //endregion
  //region Store id & map
  // Deprecated.
  // TODO: Remove in 2.0 when all references have been removed from Scheduler and Gantt

  set storeId(storeId) {
    this.id = storeId;
  }

  get storeId() {
    return this.id;
  }

  changeId(id, oldId) {
    return super.changeId(id !== true && id, oldId);
  }

  updateId(id, oldId) {
    // Store code always did just evict any other instance by that ID.
    // TODO: Should we tighten this up?
    const duplicate = Store.getById(id);
    duplicate && Store.unregisterInstance(duplicate);
    super.updateId(id, oldId);
  }

  generateAutoId() {
    return Store.generateId(`store-`);
  }

  get tree() {
    return this._tree;
  }

  set tree(tree) {
    this._tree = tree;

    if (tree && !this.rootNode) {
      this.rootNode = this.buildRootNode();
      this.rootNode.isAutoRoot = true;
    }
  } // a hook to build a customized root node

  buildRootNode() {
    return {};
  }
  /**
   * Get a store from the store map by id.
   * @param {String|Number|Object[]} id The id of the store to retrieve, or an array of objects
   * from which to create the contents of a new Store.
   * @returns {Core.data.Store} The store with the specified id
   */

  static getStore(id, storeClass) {
    if (id instanceof Store) {
      return id;
    }

    if (this.getById(id)) {
      return this.getById(id);
    }

    if (Array.isArray(id)) {
      let storeModel;
      const storeData = id.map(item => {
        if (item instanceof Model) {
          storeModel = item.constructor;
        } else if (typeof item === 'string') {
          item = {
            text: item
          };
        }

        return item;
      });
      id = {
        autoCreated: true,
        data: storeData,
        modelClass: storeModel || class extends Model {},
        allowNoId: true // String items have no id and are not guaranteed to be unique

      };

      if (!storeClass) {
        storeClass = Store;
      }
    }

    if (storeClass) {
      return new storeClass(id);
    }
  }
  /**
   * Get all registered stores
   * @property {Core.data.Store[]}
   */

  static get stores() {
    return Store.registeredInstances;
  } //endregion
  //region Data

  /**
   * The invisible root node of this tree.
   * @property {Core.data.Model}
   * @readonly
   * @category Tree
   */

  get rootNode() {
    return this.masterStore ? this.masterStore.rootNode : this._rootNode;
  }

  set rootNode(rootNode) {
    var _rootNode$children;

    const me = this,
          oldRoot = me._rootNode; // No change

    if (rootNode === oldRoot) {
      return;
    }

    if (oldRoot) {
      me.clear(false);
    }

    if (rootNode instanceof Model) {
      // We insist that the rootNode is expanded otherwise no children will be added
      rootNode.instanceMeta(me).collapsed = false;
      me._rootNode = rootNode;
    } else {
      me._rootNode = rootNode = new me.modelClass(Object.assign({
        expanded: true,
        [me.modelClass.idField]: `${me.id}-rootNode`
      }, rootNode), me, null, true);
    }

    me._tree = true;
    rootNode.isRoot = true;
    rootNode.joinStore(me); // If there are nodes to be inserted into the flat storage
    // then onNodeAddChild knows how to do that and what events
    // to fire based upon rootNode.isLoading.

    if ((_rootNode$children = rootNode.children) !== null && _rootNode$children !== void 0 && _rootNode$children.length || me.rootVisible) {
      rootNode.isLoading = true;
      me.onNodeAddChild(rootNode, rootNode.children || [], 0);
      rootNode.isLoading = false;
    }

    me.trigger('rootChange', {
      oldRoot,
      rootNode
    });
  }
  /**
   * Sets data in the store, called on initialization if data is in config otherwise call it yourself after
   * ajax call etc. Can also be used to get the raw original data.
   * @property {Object[]}
   * @fires refresh
   * @fires change
   * @category Records
   */

  set data(data) {
    this.setStoreData(data);
  } // For overridability in engine

  setStoreData(data) {
    var _data;

    const me = this,
          {
      idField,
      childrenField,
      parentIdField
    } = me.modelClass; // Make sure that if the plugins have not been processed yet, we call
    // the temporary property getter which configuration injects to
    // process plugins at this point. Some plugins are required to
    // operate on incoming data.

    me.getConfig('plugins'); // In case data is loaded during configuration before configuredListeners have been processed

    me.processConfiguredListeners(); // Allow data as a "named object", using keys as ids

    if (data && !Array.isArray(data)) {
      data = ObjectHelper.transformNamedObjectToArray(data, idField);
    } // Convert to being a tree store if any of the new rows have a children property

    me.tree = !me.isChained && (me.tree || Boolean(me.autoTree && ((_data = data) === null || _data === void 0 ? void 0 : _data.some(r => r[childrenField])))); // Always load a new dataset initially

    if (!me.syncDataOnLoad || !me._data) {
      me._data = data; // This means load the root node

      if (me.tree) {
        if (me.transformFlatData) {
          let hasParentId = false,
              shouldTransform = true; // Configured to transform flat data into tree data, make sure that we have:
          // - raw data without children defined
          // - parentIds

          for (const node of data) {
            if (node.isModel || node[childrenField]) {
              shouldTransform = false;
              break;
            }

            if (node[parentIdField] != null) {
              hasParentId = true;
            }
          }

          if (shouldTransform && hasParentId) {
            data = me.transformToTree(data);
          }
        }

        const root = me.rootNode; // Should signal stores to clear which clears UIs

        root.clearChildren(true);
        root.isLoading = true; // Append child will detect that this is a dataset operation and trigger sort + events needed

        root.appendChild(data);
        me.updateDependentStores('dataset', [root]);
        root.isLoading = false;

        if (data.length === 0) {
          me.trigger('refresh', {
            action: 'dataset',
            data: [],
            records: []
          });
        } // we must re-apply filters for the filtered tree store
        else if (me.isFiltered) {
            me.filter();
          }
      } else {
        me.loadData(data);
      } // loading the store discards all tracked changes

      me.added.clear();
      me.removed.clear();
      me.modified.clear();
    } // Sync dataset if configured to do so
    else {
        me.syncDataset(data);
      }
  }

  loadData(data, action = 'dataset') {
    const me = this,
          {
      storage,
      allowNoId
    } = me,
          idField = me.modelClass.fieldMap.id.dataSource; // Having any of groups collapsed at the time of data reloading using the same dataset
    // makes new data look differ comparing to the data which is already in the store.
    // So here we expand all groups and save the state to collapse them later.
    // This might be expensive, but it helps to prevent Id collision failure for now.

    me.storeCollapsedGroups(); // Need to unregister all groups

    me.removeHeadersAndFooters(me.storage.values);
    me._idMap = null;

    if (data) {
      const isRaw = !(data[0] instanceof Model);

      if (isRaw) {
        me.modelClass.exposeProperties(data[0]);
        const count = data.length,
              records = new Array(count);

        for (let i = 0; i < count; i++) {
          const recordData = data[i];

          if (!allowNoId && recordData[idField] == null) {
            throw new Error(`Id required but not found on row ${i}`);
          }

          records[i] = me.processRecord(me.createRecord(recordData, true), true);
          records[i].setData('parentIndex', i);
        }

        if (me.allCount) {
          // clear without marking as removed
          me.clear(false);
        } // Allow Collection's own filters to work on the Collection by
        // passing the isNewDataset param as true.
        // The storage Collection may have been set up with its own filters
        // while we are doing remote filtering. An example is ComboBox
        // with filterSelected: true. Records which are in the selection are
        // filtered out of visibility using a filter directly in the Combobox's
        // Store's Collection.

        storage.replaceValues({
          values: records,
          isNewDataset: true,
          silent: true
        });
      } else {
        if (me.allCount) {
          // clear without marking as removed
          me.clear(false);
        }

        storage.replaceValues({
          values: data.slice(),
          isNewDataset: true,
          silent: true
        });
      }

      me.onDataReplaced(action, data);
    } else {
      if (me.allCount) {
        // clear without marking as removed
        me.clear(false);
      }

      me._data = null;
    }
  }

  get data() {
    return this._data;
  }
  /**
   * Creates an array of records from this store from the `start` to the `end' - 1
   * @param {Number} [start] The index of the first record to return
   * @param {Number} [end] The index *after* the last record to return `(start + length)`
   * @return {Core.data.Model[]} The requested records.
   * @category Records
   */

  getRange(start, end, all = true) {
    return (all ? this.storage.allValues : this.storage.values).slice(start, end);
  }
  /**
   * Creates a model instance, used internally when data is set/added. Override this in a subclass to do your own custom
   * conversion from data to record.
   * @param {Object} data Json data
   * @param {Boolean} [skipExpose] Supply true when batch setting to not expose properties multiple times
   * @category Records
   */

  createRecord(data, skipExpose = false) {
    return new this.modelClass(data, this, null, skipExpose);
  }

  processRecord(record, isDataset = false) {
    return record;
  }

  refreshData() {
    this.filter();
    this.sort();
  }

  onRecordIdChange({
    record,
    oldValue,
    value
  }) {
    const me = this,
          idMap = me._idMap,
          {
      idRegister
    } = me;
    me.storage._indicesInvalid = true;

    if (idMap) {
      delete idMap[oldValue];
      idMap[value] = record;
    }

    me.added.changeId(oldValue, value);
    me.removed.changeId(oldValue, value);
    me.modified.changeId(oldValue, value);
    delete idRegister[oldValue];
    idRegister[value] = record;
    record.index = me.storage.indexOf(record);
  }

  onUpdateRecord(record, changes) {
    const {
      internalId
    } = changes,
          {
      internalIdRegister
    } = this;

    if (internalId) {
      this.storage._indicesInvalid = true;
      delete internalIdRegister[internalId.oldValue];
      internalIdRegister[internalId.value] = record;
    } // Reapply filters when records change?

    if (this.reapplyFilterOnUpdate && this.isFiltered) {
      this.filter();
    }
  }

  get useRawData() {
    return this._useRawData;
  }

  set useRawData(options) {
    if (options === true) {
      this._useRawData = {
        enabled: true,
        disableDuplicateIdCheck: true,
        disableTypeConversion: true,
        disableDefaultValue: false
      };
    } else {
      this._useRawData = options ? Object.assign(options, {
        enabled: true
      }) : {
        enabled: false
      };
    }
  } //endregion
  //region Count

  /**
   * Number of records in the store
   * @param {Boolean} [countProcessed] Count processed (true) or real records (false)
   * @returns {Number} Record count
   * @category Records
   */

  getCount(countProcessed = true) {
    return countProcessed ? this.count : this.originalCount;
  }
  /**
   * Record count, for data records. Not including records added for group headers etc.
   * @property {Number}
   * @readonly
   * @category Records
   */

  get originalCount() {
    return this.storage.totalCount;
  }
  /**
   * Record count, including records added for group headers etc.
   * @property {Number}
   * @readonly
   * @category Records
   */

  get count() {
    return this.storage.count;
  }
  /**
   * Returns the complete dataset size regardless of tree node collapsing or filtering
   * @property {Number}
   * @readonly
   * @category Records
   */

  get allCount() {
    return this.isTree ? this.rootNode.descendantCount : this.storage.totalCount;
  } //endregion
  //region Get record(s)
  // TODO: Implement immutable tag for docs #1206

  /**
   * Returns all "visible" records.
   * **Note:** The returned value **may not** be mutated!
   * @property {Core.data.Model[]}
   * @readonly
   * @immutable
   * @category Records
   */

  get records() {
    return this.storage.values;
  }
  /**
   * Get the first record in the store.
   * @property {Core.data.Model}
   * @readonly
   * @category Records
   */

  get first() {
    return this.storage.values[0];
  }
  /**
   * Get the last record in the store.
   * @property {Core.data.Model}
   * @readonly
   * @category Records
   */

  get last() {
    return this.storage.values[this.storage.values.length - 1];
  }
  /**
   * Get the record at the specified index
   * @param {Number} index Index for the record
   * @returns {Core.data.Model} Record at the specified index
   * @category Records
   */

  getAt(index, all = false) {
    // all means include filtered out records
    return this.storage.getAt(index, all);
  } // These are called by Model#join and Model#unjoin
  // register a record as a findable member keyed by id and internalId

  register(record) {
    const me = this,
          // Test for duplicate IDs on register only when a tree store.
    // loadData does it in the case of a non-tree
    existingRec = me.isTree && me.idRegister[record.id];

    if (existingRec && existingRec !== record) {
      throw new Error(`Id collision on ${record.id}`);
    }

    me.idRegister[record.id] = record;
    me.internalIdRegister[record.internalId] = record;
  }

  unregister(record) {
    delete this.idRegister[record.id];
    delete this.internalIdRegister[record.internalId];
  }

  get registeredRecords() {
    return Object.values(this.idRegister);
  }
  /**
   * Get a record by id. Find the record even if filtered out, part of collapsed group or collapsed node
   * @param {Core.data.Model|String|Number} id Id of record to return.
   * @returns {Core.data.Model} A record with the specified id
   * @category Records
   */

  getById(id) {
    // In case `id` is a record, we use its ID to try to find the record in the store,
    // because if the record is removed from the store it shouldn't be found.
    // if (id instanceof Model) {
    //     id = id.id;
    // }
    if (id !== null && id !== void 0 && id.isModel) {
      return id;
    } //return this.tree ? this.idRegister[id] : this.storage.get(id);

    return this.idRegister[id];
  }
  /**
   * Checks if a record is available, in the sense that it is not filtered out,
   * hidden in a collapsed group or in a collapsed node.
   * @param {Core.data.Model|String|Number} recordOrId Record to check
   * @returns {Boolean}
   * @category Records
   */

  isAvailable(recordOrId) {
    const record = this.getById(recordOrId);
    return record && this.storage.includes(record) || false;
  }
  /**
   * Get a record by internalId.
   * @param {Number} internalId The internalId of the record to return
   * @returns {Core.data.Model} A record with the specified internalId
   * @category Records
   */

  getByInternalId(internalId) {
    return this.internalIdRegister[internalId];
  }
  /**
   * Checks if the specified record is contained in the store
   * @param {Core.data.Model|String|Number} recordOrId Record, or `id` of record
   * @returns {Boolean}
   * @category Records
   */

  includes(recordOrId) {
    if (this.isTree) {
      return this.idRegister[Model.asId(recordOrId)] != null;
    }

    return this.indexOf(recordOrId) > -1;
  } //endregion
  //region Get index

  /**
   * Returns the index of the specified record/id, or `-1` if not found.
   * @param {Core.data.Model|String|Number} recordOrId Record, or `id` of record to return the index of.
   * @param {Boolean} [visibleRecords] Pass `true` to find the visible index.
   * as opposed to the dataset index. This omits group header records.
   * @returns {Number} Index for the record/id, or `-1` if not found.
   * @category Records
   */

  indexOf(recordOrId, visibleRecords = false) {
    const id = Model.asId(recordOrId);

    if (id == null) {
      return -1;
    } // When a tree, indexOf is always in the visible records - filtering is different in trees.

    if (this.isTree) {
      return this.storage.indexOf(id);
    }

    const found = this.idMap[id];
    return found ? found[visibleRecords ? 'visibleIndex' : 'index'] : -1;
  }

  allIndexOf(recordOrId) {
    if (this.isTree) {
      const record = this.getById(recordOrId);
      let result = -1; // Use the tree structure to get the index in tree walk order

      if (record) {
        record.bubble(n => {
          if (n.parent) {
            result += n.parentIndex + 1;
          } else if (n === this.rootNode && this.rootVisible) {
            result += 1;
          }
        });
      }

      return result;
    } else {
      return this.storage.indexOf(recordOrId, true);
    }
  } //endregion
  //region Get values

  /**
   * Gets distinct values for the specified field.
   * @param {String} field Field to extract values for
   * @returns {Array} Array of values
   * @category Values
   */

  getDistinctValues(field) {
    const me = this,
          values = [],
          keys = {};
    let value;
    me.forEach(r => {
      if (!r.isSpecialRow && !r.isRoot) {
        value = r.get(field);

        if (!keys[value]) {
          values.push(value);
          keys[value] = 1;
        }
      }
    });
    return values;
  }
  /**
   * Counts how many times specified value appears in the store
   * @param {String} field Field to look in
   * @param {*} value Value to look for
   * @returns {Number} Found count
   * @category Values
   */

  getValueCount(field, value) {
    let count = 0;
    this.forEach(r => {
      if (ObjectHelper.isEqual(r.get(field), value)) count++;
    });
    return count;
  } //endregion
  //region JSON & console

  /**
   * Retrieve or set the data of all records as a JSON string
   *
   * ```javascript
   * const store = new Store({
   *     data : [
   *         { id : 1, name : 'Superman' },
   *         { id : 2, name : 'Batman' }
   *     ]
   * });
   *
   * const jsonString = store.json;
   *
   * //jsonString:
   * '[{"id":1,"name":"Superman"},{"id":2,"name":"Batman"}]
   * ```
   *
   * @property {String}
   */

  set json(json) {
    if (typeof json === 'string') {
      json = StringHelper.safeJsonParse(json);
    }

    this.data = json;
  }

  get json() {
    return StringHelper.safeJsonStringify(this);
  }
  /**
   * Pretty printed version of {@link #property-json}
   * @readonly
   * @property {String}
   */

  get formattedJSON() {
    return StringHelper.safeJsonStringify(this, null, 4);
  }
  /**
   * Retrieve the data of all records as an array of JSON objects
   *
   * ```javascript
   * const store = new Store({
   *     data : [
   *         { id : 1, name : 'Superman' },
   *         { id : 2, name : 'Batman' }
   *     ]
   * });
   *
   * const jsonArray = store.toJSON();
   *
   * //jsonArray:
   * [{id:1,name:"Superman"},{id:2,name:"Batman"}]
   * ```
   *
   * @returns {Object[]}
   */

  toJSON() {
    // extract entire structure.
    // If we're a tree, then that consists of the payload of the rootNode.
    return (this.isTree ? this.rootNode.children : this).map(record => record.toJSON());
  } //endregion
  //region Iteration & traversing

  /**
   * Iterates over all normal records in store. Omits group header and footer records
   * if this store is grouped.
   * @param {Function} fn A function that is called for each record. Returning false from that function cancels iteration
   * @param {Object} [thisObj] `this` reference for the function
   * @category Iteration
   */

  forEach(fn, thisObj = this) {
    const callback = (r, i) => {
      if (!r.isRoot && !r.isSpecialRow) {
        return fn.call(thisObj, r, i);
      }
    };

    if (this.isTree) {
      this.rootNode.traverseWhile(callback);
    } else {
      // native forEach cannot be aborted by returning false, have to loop "manually"
      const records = this.storage.values;

      for (let i = 0; i < records.length; i++) {
        if (callback(records[i], i) === false) {
          return;
        }
      }
    }
  }
  /**
   * Equivalent to Array.map(). Creates a new array with the results of calling a provided function on every record
   * @param {Function} fn
   * @returns {Array}
   * @category Iteration
   */

  map(fn, thisObj = this) {
    return this.storage.values.map(fn, thisObj);
  }
  /**
   * Equivalent to Array.reduce(). Applies a function against an accumulator and each record (from left to right) to
   * reduce it to a single value.
   * @param {Function} fn
   * @param {*} initialValue
   * @returns {*}
   * @category Iteration
   */

  reduce(fn, initialValue = [], thisObj = this) {
    if (thisObj !== this) {
      fn = fn.bind(thisObj);
    }

    return this.storage.values.reduce(fn, initialValue, thisObj);
  }
  /**
   * Iterator that allows you to do for (let record of store)
   * @category Iteration
   */

  [Symbol.iterator]() {
    return this.storage.values[Symbol.iterator]();
  }
  /**
   * Traverse all tree nodes
   * @param {Function} fn The function to call on visiting each node.
   * @param {Core.data.Model} [topNode=this.rootNode] The top node to start the traverse at.
   * @param {Boolean} [skipTopNode] Pass true to not call `fn` on the top node, but proceed directly to its children.
   * @category Traverse
   */

  traverse(fn, topNode = this.rootNode, skipTopNode = topNode === this.rootNode) {
    const me = this;

    if (me.isTree) {
      // Allow store.traverse(fn, true) to start from rootNode
      if (typeof topNode === 'boolean') {
        skipTopNode = topNode;
        topNode = me.rootNode;
      }

      if (me.isChained) {
        const passedFn = fn;

        fn = node => {
          if (me.chainedFilterFn(node)) {
            passedFn(node);
          }
        };
      }

      topNode.traverse(fn, skipTopNode);
    } else {
      for (const record of me.storage) {
        record.traverse(fn);
      }
    }
  }
  /**
   * Traverse all tree nodes while the passed `fn` returns true
   * @param {Function} fn The function to call on visiting each node. Returning `false` from it stops the traverse.
   * @param {Core.data.Model} [topNode=this.rootNode] The top node to start the traverse at.
   * @param {Boolean} [skipTopNode] Pass true to not call `fn` on the top node, but proceed directly to its children.
   * @category Traverse
   */

  traverseWhile(fn, topNode = this.rootNode, skipTopNode = topNode === this.rootNode) {
    const me = this;

    if (me.isTree) {
      // Allow store.traverse(fn, true) to start from rootNode
      if (typeof topNode === 'boolean') {
        skipTopNode = topNode;
        topNode = me.rootNode;
      }

      if (me.isChained) {
        const passedFn = fn;

        fn = node => {
          if (me.chainedFilterFn(node)) {
            passedFn(node);
          }
        };
      }

      topNode.traverseWhile(fn, skipTopNode);
    } else {
      for (const record of me.storage) {
        if (record.traverse(fn) === false) {
          break;
        }
      }
    }
  }
  /**
   * Finds the next record.
   * @param {Core.data.Model|String|Number} recordOrId Current record or its id
   * @param {Boolean} [wrap=false] Wrap at start/end or stop there
   * @param {Boolean} [skipSpecialRows=false] True to not return specialRows like group headers
   * @returns {Core.data.Model} Next record or null if current is the last one
   * @category Traverse
   */

  getNext(recordOrId, wrap = false, skipSpecialRows = false) {
    const me = this,
          records = me.storage.values;
    let idx = me.indexOf(recordOrId);

    if (idx >= records.length - 1) {
      if (wrap) {
        idx = -1;
      } else {
        return null;
      }
    }

    const record = records[idx + 1]; // Skip the result if it's a specialRow and we are told to skip them

    if (skipSpecialRows && record && record.isSpecialRow) {
      return me.getNext(records[idx + 1], wrap, true);
    }

    return record;
  }
  /**
   * Finds the previous record.
   * @param {Core.data.Model|String|Number} recordOrId Current record or its id
   * @param {Boolean} [wrap=false] Wrap at start/end or stop there
   * @param {Boolean} [skipSpecialRows=false] True to not return specialRows like group headers
   * @returns {Core.data.Model} Previous record or null if current is the last one
   * @category Traverse
   */

  getPrev(recordOrId, wrap = false, skipSpecialRows = false) {
    const me = this,
          records = me.storage.values;
    let idx = me.indexOf(recordOrId);

    if (idx === 0) {
      if (wrap) {
        idx = records.length;
      } else {
        return null;
      }
    }

    const record = records[idx - 1]; // Skip the result if it's a specialRow and we are told to skip them

    if (skipSpecialRows && record && record.isSpecialRow && idx > 0) {
      return me.getPrev(records[idx - 1], wrap, true);
    }

    return record;
  }
  /**
   * Gets the next or the previous record. Optionally wraps from first -> last and vice versa
   * @param {String|Core.data.Model} recordOrId Record or records id
   * @param {Boolean} next Next (true) or previous (false)
   * @param {Boolean} wrap Wrap at start/end or stop there
   * @param {Boolean} [skipSpecialRows=false] True to not return specialRows like group headers
   * @returns {Core.data.Model}
   * @category Traverse
   * @internal
   */

  getAdjacent(recordOrId, next = true, wrap = false, skipSpecialRows = false) {
    return next ? this.getNext(recordOrId, wrap, skipSpecialRows) : this.getPrev(recordOrId, wrap, skipSpecialRows);
  }
  /**
   * Finds the next record among leaves (in a tree structure)
   * @param {Core.data.Model|String|Number} recordOrId Current record or its id
   * @param {Boolean} [wrap] Wrap at start/end or stop there
   * @returns {Core.data.Model} Next record or null if current is the last one
   * @category Traverse
   * @internal
   */

  getNextLeaf(recordOrId, wrap = false) {
    const me = this,
          records = me.leaves,
          record = me.getById(recordOrId);
    let idx = records.indexOf(record);

    if (idx >= records.length - 1) {
      if (wrap) {
        idx = -1;
      } else {
        return null;
      }
    }

    return records[idx + 1];
  }
  /**
   * Finds the previous record among leaves (in a tree structure)
   * @param {Core.data.Model|String|Number} recordOrId Current record or its id
   * @param {Boolean} [wrap] Wrap at start/end or stop there
   * @returns {Core.data.Model} Previous record or null if current is the last one
   * @category Traverse
   * @internal
   */

  getPrevLeaf(recordOrId, wrap = false) {
    const me = this,
          records = me.leaves,
          record = me.getById(recordOrId);
    let idx = records.indexOf(record);

    if (idx === 0) {
      if (wrap) {
        idx = records.length;
      } else {
        return null;
      }
    }

    return records[idx - 1];
  }
  /**
   * Gets the next or the previous record among leaves (in a tree structure). Optionally wraps from first -> last and
   * vice versa
   * @param {String|Core.data.Model} recordOrId Record or record id
   * @param {Boolean} [next] Next (true) or previous (false)
   * @param {Boolean} [wrap] Wrap at start/end or stop there
   * @returns {Core.data.Model}
   * @category Traverse
   * @internal
   */

  getAdjacentLeaf(recordOrId, next = true, wrap = false) {
    return next ? this.getNextLeaf(recordOrId, wrap) : this.getPrevLeaf(recordOrId, wrap);
  } //endregion
  //region Chained store

  /**
   * Creates a chained store, a new Store instance that contains a subset of the records from current store.
   * Which records is determined by a filtering function, which is reapplied when data in the base store changes.
   *
   * If this store is a {@link Core.data.mixin.StoreTree#property-isTree tree} store, then the resulting
   * chained store will be a tree store sharing the same root node, but only child nodes which pass
   * the `chainedFilterFn` will be considered when iterating the tree through the methods such as
   * {@link #function-traverse} or {@link #function-forEach}.
   *
   * @param {Function} chainedFilterFn Function called for each records that determines if it should be included
   * (return true) or not (return false). Defaults to including all records (fn always returning true)
   * @param {String[]} chainedFields Array of fields that trigger filtering when they are updated
   * @param {Object} config Additional chained store configuration
   * @returns {Core.data.Store}
   * @example
   * let oldies = store.makeChained(record => record.age > 40);
   */

  makeChained(chainedFilterFn = () => true, chainedFields, config) {
    return new this.constructor(Object.assign(config || {}, {
      tree: false,
      autoTree: false,
      masterStore: this,
      modelClass: this.modelClass,
      chainedFilterFn,
      chainedFields
    }));
  }
  /**
   * Alias for {@link #function-makeChained}
   * @param {Function} chainedFilterFn Function called for each records that determines if it should be included
   * (return true) or not (return false). Defaults to including all records (fn always returning true)
   * @param {String[]} chainedFields Array of fields that trigger filtering when they are updated
   * @param {Object} config Additional chained store configuration
   * @returns {Core.data.Store}
   */

  chain() {
    return this.makeChained(...arguments);
  } //endregion

}
Store.storeMap = {};
Store._$name = 'Store';

/**
 * @module Core/data/AjaxStore
 */

const processParamEntry = (paramArray, entry) => {
  if (Array.isArray(entry[1])) {
    entry[1].forEach(value => paramArray.push(encodeURIComponent(entry[0]) + '=' + encodeURIComponent(value)));
  } else {
    paramArray.push(encodeURIComponent(entry[0]) + '=' + encodeURIComponent(entry[1]));
  }

  return paramArray;
},
      immediatePromise$1 = Promise.resolve();
/**
 * Store that uses the Fetch API to read data from a remote server, and optionally sends synchronization
 * requests to the server containing information about locally created, modified and deleted records.
 *
 * ## Create
 * Posts array of JSON data for newly added records to {@link #config-createUrl}, expects response containing an array of JSON objects
 * in same order with id set (uses Model#idField as id).
 *
 * ## Read
 * Reads array of JSON data from the data packet returned from the {@link #config-readUrl}. Unique id for each row is required.
 * By default looks in field 'id' but can be configured by setting {@link Core.data.Model#property-idField-static}.
 *
 * ## Update
 * Posts array of JSON data for newly modified records to {@link #config-updateUrl}.
 *
 * ## Delete
 * Posts to {@link #config-deleteUrl} with removed records ids (for example id=1,4,7).
 *
 * ```javascript
 * new AjaxStore({
 *   createUrl  : 'php/create',
 *   readUrl    : 'php/read',
 *   updateUrl  : 'php/update',
 *   deleteUrl  : 'php/delete',
 *   modelClass : Customer
 * });
 * ```
 *
 * ## Pagination
 * Configuring an `AjaxStore` with {@link #config-pageParamName} or {@link #config-pageStartParamName} means that the store requests **pages**
 * of data from the remote source, sending the configured {@link #config-pageParamName} or {@link #config-pageStartParamName} to request the page
 * along with the {@link #config-pageSizeParamName}.
 *
 * If `pageParamName` is set, that is passed with the requested page number **(one based)**, along with the {@link #config-pageSizeParamName}.
 *
 * If `pageStartParamName` is set, that is passed with the requested page starting record index **(zero based)**, along with the {@link #config-pageSizeParamName}.
 *
 * ## Remote filtering
 * To specify that filtering is the responsibility of the server, configure the store with
 * `{@link #config-filterParamName}: 'nameOfFilterParameter'`
 *
 * When this is set, any {@link Core.data.mixin.StoreFilter#function-filter} operation causes the store to
 * reload itself, encoding the filters as JSON representations in the {@link #config-filterParamName} HTTP
 * parameter.
 *
 * The filters will look like this:
 * ```javascript
 * {
 *     "field": "country",
 *     "operator": "=",
 *     "value": "sweden",
 *     "caseSensitive": false
 * }
 * ```
 *
 * The encoding may be overridden by configuring an implementation of {@link #function-encodeFilterParams}
 * into the store which returns the value for the {@link #config-filterParamName} when passed an _Iterable_ of filters.
 *
 * ## Remote sorting
 * To specify that sorting is the responsibility of the server, configure the store with
 * `{@link #config-sortParamName}: 'nameOfSortParameter'`
 *
 * When this is set, any {@link Core.data.mixin.StoreSort#function-sort} operation causes the store to
 * reload itself, encoding the sorters as JSON representations in the {@link #config-sortParamName} HTTP
 * parameter.
 *
 * The sorters will look like this:
 * ```javascript
 * {
 *     "field": "name",
 *     "ascending": true
 * }
 * ```
 *
 * The encoding may be overridden by configuring an implementation of {@link #function-encodeSorterParams}
 * into the store which returns the value for the {@link #config-sortParamName} when passed an _Iterable_ of sorters.
 *
 * @extends Core/data/Store
 */

class AjaxStore extends Store {
  // region Events

  /**
   * Fired when a remote request fails, either at the network level, or the server returns a failure, or an invalid response.
   *
   * Note that when a {@link #function-commit} fails, more than one exception event will be triggered. The individual operation,
   * `create`, `update` or `delete` will trigger their own `exception` event, but the encapsulating commit operation will also
   * trigger an `exception` event when all the operations have finished, so if exceptions are going to be handled gracefully,
   * the event's `action` property must be examined, and the constituent operations of the event must be examined.
   * @event exception
   * @param {Core.data.Store} source This Store
   * @param {Boolean} exception `true`
   * @param {String} action Action that failed, `'create'`, `'read'`, `'update'` or `'delete'`. May also be fired
   * with '`commit'` to indicate the failure of an aggregated `create`, `update` and `delete` operation. In this case,
   * the event will contain a property for each operation of the commit named `'create'`, `'update'` and `'delete'`,
   * each containing the individual `exception` events.
   * @param {String} exceptionType The type of failure, `'network'` or `'server'`
   * @param {Response} response the `Response` object
   * @param {Object} json The decoded response object *if the exceptionType is `'server'`*
   */

  /**
   * Fired after committing added records
   * @event commitAdded
   * @param {Core.data.Store} source This Store
   */

  /**
   * Fired after committing modified records
   * @event commitModified
   * @param {Core.data.Store} source This Store
   */

  /**
   * Fired on successful load
   * @event load
   * @param {Core.data.Store} source This Store
   * @param {Object[]} data Data loaded
   * @param {Response} response the `Response` object
   * @param {Object} json The decoded response object.
   */

  /**
   * Fired on successful load of remote child nodes for a tree node.
   * @event loadChildren
   * @param {Core.data.Store} source This Store
   * @param {Object[]} data Data loaded
   * @param {Object} json The decoded response object.
   */

  /**
   * Fired after committing removed records
   * @event commitRemoved
   * @param {Core.data.Store} source This Store
   */

  /**
   * Fired before loading starts. Allows altering parameters and is cancelable
   * @event beforeLoad
   * @preventable
   * @param {Core.data.Store} source This Store
   * @param {Object} params An object containing property/name pairs which are the parameters.
   * This may be mutated to affect the parameters used in the Ajax request.
   */

  /**
   * Fired before loading of remote child nodes of a tree node starts. Allows altering parameters and is cancelable
   * @event beforeLoadChildren
   * @preventable
   * @param {Core.data.Store} source This Store
   * @param {Object} params An object containing property/name pairs which are the parameters.
   * This may be mutated to affect the parameters used in the Ajax request.
   */

  /**
   * When the store {@link #property-isPaged is paged}, this is fired before loading a page and is cancelable
   * @event beforeLoadPage
   * @preventable
   * @param {Core.data.Store} source This Store
   * @param {Object} params An object containing property/name pairs which are the parameters.
   * This may be mutated to affect the parameters used in the Ajax request.
   */

  /**
   * Fired when loading is beginning. This is not cancelable. Parameters in the event may still be
   * mutated at this stage.
   * @event loadStart
   * @param {Core.data.Store} source This Store
   * @param {Object} params An object containing property/name pairs which are the parameters.
   * This may be mutated to affect the parameters used in the Ajax request.
   */

  /**
   * Fired when loading of remote child nodes into a tree node is beginning. This is not cancelable. Parameters in the event may still be
   * mutated at this stage.
   * @event loadChildrenStart
   * @param {Core.data.Store} source This Store
   * @param {Object} params An object containing property/name pairs which are the parameters.
   * This may be mutated to affect the parameters used in the Ajax request.
   */

  /**
   * Fired before any remote request is initiated.
   * @event beforeRequest
   * @param {Core.data.Store} source This Store
   * @param {Object} params An object containing key/value pairs that are passed on the requests query string
   * @param {Object} body The body of the request to be posted to the server.
   * @param {String} action Action that is making the request, `'create'`, `'read'`, `'update'` or `'delete'`
   */

  /**
   * Fired after any remote request has finished whether successfully or unsuccessfully.
   * @event afterRequest
   * @param {Boolean} exception `true`. *Only present if the request triggered an exception.*
   * @param {String} action Action that has finished, `'create'`, `'read'`, `'update'` or `'delete'`
   * @param {String} exceptionType The type of failure, `'network'` or `'server'`. *Only present if the request triggered an exception.*
   * @param {Response} response The `Response` object
   * @param {Object} json The decoded response object if there was no `'network'` exception.
   */
  // endregion
  //region Config
  static get defaultConfig() {
    return {
      /**
       * An object containing the HTTP headers to add to each server request issued by this Store.
       * @config {Object}
       * @default
       * @category Remote
       */
      headers: null,

      /**
       * An object containing key/value pairs that are passed on the requests query string. You can set the params dynamically in a {@link #event-beforeRequest} listener.
       * @config {Object}
       * @default
       * @category Remote
       */
      params: null,

      /**
       * An object containing the Fetch options to pass to each server request issued by this Store. Use this to control if credentials are sent
       * and other options, read more at [MDN](https://developer.mozilla.org/en-US/docs/Web/API/Fetch_API/Using_Fetch).
       * @config {Object}
       * @default
       * @category Remote
       */
      fetchOptions: null,

      /**
       * Specify `true` to send payloads as form data, `false` to send as regular JSON.
       * @config {Boolean}
       * @default
       * @category Remote
       */
      sendAsFormData: false,

      /**
       * Specify `true` to send all model fields when committing modified records (as opposed to just the modified fields)
       * @config {Boolean}
       * @default
       * @category Remote
       */
      writeAllFields: null,

      /**
       * The name of the HTTP parameter passed to this Store's {@link #config-readUrl} to indicate the node `id` to
       * load when loading child nodes on demand if the node being expanded was created with data containing `children: true`.
       * @config {String}
       * @default
       * @category Remote
       */
      parentIdParamName: 'id',

      /**
       * The optional property name in JSON responses from the server that contains a boolean
       * success/fail status.
       * ```
       * {
       *   "responseMeta" : {
       *   {
       *     "success" : true,
       *     "count" : 100
       *   },
       *   // The property name used here should match that of 'reponseDataProperty'
       *   "data" : [
       *     ...
       *   ]
       * }
       * ```
       *
       * The store would be configured with:
       * ```javascript
       *  {
       *      ...
       *      successDataProperty : 'responseMeta.success',
       *      responseTotalProperty : 'responseMeta.count'
       *      ...
       *  }
       *
       * ```
       * @config {String}
       * @default
       * @category Remote
       */
      responseSuccessProperty: 'success',

      /**
       * The property name in JSON responses from the server that contains the data for the records
       * ```
       * {
       *   "success" : true,
       *   // The property name used here should match that of 'reponseDataProperty'
       *   "data" : [
       *     ...
       *   ]
       * }
       * ```
       * @config {String}
       * @default
       * @category Remote
       */
      responseDataProperty: 'data',

      /**
       * The property name in JSON responses from the server that contains the dataset total size
       * **when this store {@link #property-isPaged is paged}**
       * ```
       * {
       *   "success" : true,
       *   // The property name used here should match that of 'reponseDataProperty'
       *   "data" : [
       *     ...
       *   ],
       *   // The property name used here should match that of 'responseTotalProperty'
       *   "total" : 65535
       * }
       * ```
       * @config {String}
       * @default
       * @category Remote
       */
      responseTotalProperty: 'total',

      /**
       * The name of the HTTP parameter to use to pass any encoded filters when loading data from the server and a filtered response is required.
       *
       * **Note:** When this is set, filters must be defined using a field name, an operator and a value
       * to compare, **not** a comparison function.
       * @config {String}
       * @category Remote
       */
      filterParamName: null,

      /**
       * Set this flag to true if you are filtering remote using restful URLs (e.g. https://nominatim.openstreetmap.org/search/paris?format=json)
       *
       * **Note:** When this is set, the filter string is appended to the readUrl.
       * @config {Boolean}
       */
      restfulFilter: false,

      /**
       * The name of the HTTP parameter to use to pass any encoded sorters when loading data from the server and a sorted response is required.
       *
       * **Note:** When this is set, sorters must be defined using a field name and an ascending flag,
       * **not** a sort function.
       * @config {String}
       * @category Remote
       */
      sortParamName: null,

      /**
       * The name of the HTTP parameter to use when requesting pages of data using the **one based** page number required.
       * @config {String}
       * @category Paging
       */
      pageParamName: null,

      /**
       * The name of the HTTP parameter to use when requesting pages of data using the **zero based** index of the required page's starting record.
       * @config {String}
       * @category Paging
       */
      pageStartParamName: null,

      /**
       * The name of the HTTP parameter to use when requesting pages of data using the **zero based** index of the required page's starting record.
       * @config {String}
       * @default
       * @category Paging
       */
      pageSizeParamName: 'pageSize',

      /**
       * When paging of data is requested by setting _either_ the {@link #config-pageParamName} _or_ the {@link #config-pageStartParamName},
       * this is the value to send in the {@link #config-pageSizeParamName}
       * @config {Number}
       * @default
       * @category Paging
       */
      pageSize: 50,

      /**
       * Set to ´true´ to use restful {@link #config-httpMethods}
       * @config {Boolean}
       * @default
       * @category Remote
       */
      useRestfulMethods: false,

      /**
       * The HTTP methods to use for CRUD requests when {@link #config-useRestfulMethods} is enabled.
       *
       * ```
       * new AjaxStore({
       *    useRestfulMethods : true,
       *    httpMethods : {
       *        create : 'POST',
       *        read : 'POST',
       *        update : 'PATCH',
       *        delete : 'DELETE'
       *    }
       * });
       *
       * ```
       * @config {Object}
       * @default
       * @category Remote
       */
      httpMethods: {
        create: 'POST',
        read: 'GET',
        update: 'PUT',
        delete: 'DELETE'
      }
    };
  }
  /**
   * Url to post newly created records to.
   *
   * The response must be in the form:
   *
   *     {
   *         "success": true,
   *         "data": [{
   *             "id": 0, "name": "General Motors"
   *         }, {
   *             "id": 1, "name": "Apple"
   *         }]
   *     }
   *
   * Just the array of data may be returned, however that precludes the
   * orderly handling of errors encountered at the server.
   *
   * If the server encountered an error, the packet would look like this:
   *
   *     {
   *         "success": false,
   *         "message": "Some kind of database error"
   *     }
   *
   * And that packet would be available in the {@link #event-exception} handler
   * in the `response` property of the event.
   *
   * The `success` property may be omitted, it defaults to `true`.
   *
   * @config {String} createUrl
   * @category CRUD
   */

  /**
   * Url to read data from.
   *
   * The response must be in the form:
   *
   *     {
   *         "success": true,
   *         "data": [{
   *             "id": 0, "name": "General Motors"
   *         }, {
   *             "id": 1, "name": "Apple"
   *         }]
   *     }
   *
   * If the store {@link #property-isPaged is paged}, the total dataset size must be returned
   * in the {@link #config-responseTotalProperty} property:
   *
   *     {
   *         "success": true,
   *         "data": [{
   *             "id": 0, "name": "General Motors"
   *         }, {
   *             "id": 1, "name": "Apple"
   *         }],
   *         "total": 65535
   *     }
   *
   * Just the array of data may be returned, however that precludes the
   * orderly handling of errors encountered at the server.
   *
   * If the server encountered an error, the packet would look like this:
   *
   *     {
   *         "success": false,
   *         "message": "Some kind of database error"
   *     }
   *
   * And that packet would be available in the {@link #event-exception} handler
   * in the `response` property of the event.
   *
   * The `success` property may be omitted, it defaults to `true`.
   *
   * @config {String} readUrl
   * @category CRUD
   */

  /**
   * Url to post record modifications to.
   *
   * The response must be in the form:
   *
   *     {
   *         "success": true,
   *         "data": [{
   *             "id": 0, "name": "General Motors"
   *         }, {
   *             "id": 1, "name": "Apple"
   *         }]
   *     }
   *
   * Just the array of data may be returned, however that precludes the
   * orderly handling of errors encountered at the server.
   *
   * If the server encountered an error, the packet would look like this:
   *
   *     {
   *         "success": false,
   *         "message": "Some kind of database error"
   *     }
   *
   * And that packet would be available in the {@link #event-exception} handler
   * in the `response` property of the event.
   *
   * The `success` property may be omitted, it defaults to `true`.
   *
   * @config {String} updateUrl
   * @category CRUD
   */

  /**
   * Url for deleting records.
   *
   * The response must be in the form:
   *
   *     {
   *         "success": true
   *     }
   *
   * If the server encountered an error, the packet would look like this:
   *
   *     {
   *         "success": false,
   *         "message": "Some kind of database error"
   *     }
   *
   * And that packet would be available in the {@link #event-exception} handler
   * in the `response` property of the event.
   *
   * The `success` property may be omitted, it defaults to `true`.
   *
   * @config {String} deleteUrl
   * @category CRUD
   */

  /**
   * True to initiate a load when the store is instantiated
   * @config {Boolean} autoLoad
   * @category Common
   */
  //endregion

  afterConstruct(config) {
    super.afterConstruct(config);

    if (this.autoLoad) {
      this.load().catch(() => {});
    }
  }
  /**
   * Returns true if the Store is currently loading
   * @property {Boolean}
   * @readonly
   * @category CRUD
   */

  get isLoading() {
    return this._isLoading;
  }
  /**
   * Returns true if the Store is currently committing
   * @property {Boolean}
   * @readonly
   * @category CRUD
   */

  get isCommitting() {
    return Boolean(this.commitPromise);
  }

  set pageParamName(pageParamName) {
    if (this.tree) {
      throw new Error('Paging cannot be supported for tree stores');
    }

    if (this.pageStartParamName) {
      throw new Error('Configs pageStartParamName and pageParamName are mutually exclusive');
    }

    this._pageParamName = pageParamName;
  }

  get pageParamName() {
    return this._pageParamName;
  }

  set pageStartParamName(pageStartParamName) {
    if (this.tree) {
      throw new Error('Paging cannot be supported for tree stores');
    }

    if (this.pageParamName) {
      throw new Error('Configs pageParamName and pageStartParamName are mutually exclusive');
    }

    this._pageStartParamName = pageStartParamName;
  }

  get pageStartParamName() {
    return this._pageStartParamName;
  }
  /**
   * Yields true if this Store is loaded page by page. This yields `true` if either of the {@link #config-pageParamName}
   * of {@link #config-pageStartParamName} configs are set.
   * @property {Boolean}
   * @readonly
   * @category Paging
   */

  get isPaged() {
    return this.pageParamName || this.pageStartParamName;
  }
  /**
   * Yields the complete dataset size. If the store is {@link #property-isPaged is paged} this is the value
   * returned in the last loaded data block in the {@link #config-responseTotalProperty} property. Otherwise it is
   * the number of records in the store's underlying storage collection.
   * @property {Number}
   * @readonly
   * @category Paging
   */

  get allCount() {
    return 'remoteTotal' in this ? this.remoteTotal : super.allCount;
  }
  /**
   * **If the store {@link #property-isPaged is paged}**, yields the highest page number in the dataset as calculated from the {@link #config-responseTotalProperty}
   * returned in the last page data block loaded.
   * @property {Number}
   * @readonly
   * @category Paging
   */

  get lastPage() {
    if (this.isPaged) {
      return Math.floor((this.allCount + this.pageSize - 1) / this.pageSize);
    }
  }

  buildQueryString(url, ...paramObjects) {
    const hasParamsInUrl = url.includes('?'),
          queryString = Object.entries(Object.assign({}, ...paramObjects)).reduce(processParamEntry, []).join('&');
    return queryString ? (hasParamsInUrl ? '&' : '?') + queryString : '';
  }

  performSort(silent) {
    const me = this;

    if (me.sortParamName) {
      const {
        groupRecords
      } = me,
            // Current collapsed state for groups
      collapsed = {}; // Temporarily remove group headers, will be re-added after sort

      if (me.isGrouped && groupRecords && groupRecords.length) {
        groupRecords.forEach(r => {
          if (r.meta.collapsed) {
            me.includeGroupRecords(r);
            collapsed[r.id] = true;
          }
        });
      } // TODO: Should we cache lastParams from the load call and use them here?

      return me.internalLoad({}, '', event => {
        me.data = event.data;
        me.afterPerformSort(silent, collapsed);
      });
    } else {
      super.performSort(silent);
    }
  }

  performFilter(silent) {
    const me = this; // For remote filtering, the dataset cannot be preserved. The size may be completely different.
    // This is a reload operation.

    if (me.remoteFilter) {
      const oldCount = me.count,
            {
        filters
      } = me; // load should default to page 1

      me.currentPage = 1; // TODO: Should we cache lastParams from the load call and use them here?

      return me.internalLoad({}, '', event => {
        me.data = event.data;
        me.afterPerformFilter(silent ? null : {
          action: 'filter',
          filters,
          oldCount,
          records: me.storage.values
        });
      });
    } else {
      super.performFilter(silent);
    }
  }
  /**
   * A provided function which creates an array of values for the {#config-filterParamName} to pass
   * any filters to the server upon load.
   *
   * By default, this creates a JSON string containing the following properties:
   *
   * ```javascript
   *    [{
   *        field         : <theFieldName>
   *        operator      : May be: `'='`, `'!='`, `'>'`, `'>='`, `'<'`, `'<='`, `'*'`, `'startsWith'`, `'endsWith'`
   *        value         : The value to compare
   *        caseSensitive : true for case sensitive comparisons
   *    }]
   * ```
   * @param {Core.util.CollectionFilter[]} filters The filters to encode.
   */

  encodeFilterParams(filters) {
    const result = [];

    for (const {
      property,
      operator,
      value,
      caseSensitive
    } of filters) {
      result.push({
        field: property,
        operator,
        value,
        caseSensitive
      });
    }

    return JSON.stringify(result);
  }
  /**
   * A provided function which creates an array of values for the {#config-sortParamName} to pass
   * any sorters to the server upon load.
   *
   * By default, this creates a JSON string containing the following properties:
   *
   * ```javascript
   *    [{
   *        field     : <theFieldName>
   *        ascending : true/false
   *    }]
   * ```
   *
   * @param {Object[]} sorters The sorters to encode.
   */

  encodeSorterParams(sorters) {
    return JSON.stringify(sorters.filter(sorter => !sorter.sortFn).map(sorter => sorter));
  }

  buildReadUrl() {
    const {
      readUrl
    } = this;

    if (this.restfulFilter) {
      const url = readUrl.endsWith('/') ? readUrl : readUrl + '/';
      return url + this.filters.first.value;
    } else {
      return readUrl;
    }
  }

  internalLoad(params, eventName, successFn) {
    const me = this,
          allParams = ObjectHelper.assign({}, me.params, params),
          url = me.buildReadUrl(),
          event = {
      action: 'read' + eventName,
      params: allParams,
      url
    };
    return url ? new Promise((resolve, reject) => {
      if (me.trigger('beforeLoad' + eventName, event) === false) {
        return reject(false); // eslint-disable-line
      }

      me._isLoading = true; // This may look redundant, but it allows for two levels of event listening.
      // Granular, where the observer observes only the events of interest, and
      // catch-all, where the observer is interested in all requests.

      me.trigger(`load${eventName}Start`, event);
      me.trigger('beforeRequest', event); // Add filter information to the request parameters

      if (me.filterParamName && me.isFiltered) {
        allParams[me.filterParamName] = me.encodeFilterParams(me.filters.values);
      } // Add sorter information to the request parameters.
      // isSorted includes grouping in its evaluation.

      if (me.sortParamName && me.isSorted) {
        allParams[me.sortParamName] = me.encodeSorterParams(me.groupers ? me.groupers.concat(me.sorters) : me.sorters);
      } // Ensure our next page is passed to the server in the params if not already set.
      // Ensure our page size is always passed.

      if (me.isPaged) {
        if (!(allParams[me.pageParamName] || allParams[me.pageStartParamName])) {
          const page = Math.min(me.currentPage || 1, me.allCount ? me.lastPage : Infinity);

          if (me.pageParamName) {
            allParams[me.pageParamName] = page;
          } else {
            allParams[me.pageStartParamName] = (page - 1) * me.pageSize;
          }
        }

        allParams[me.pageSizeParamName] = me.pageSize;
      }

      const options = {
        headers: me.headers,
        parseJson: true
      };

      if (me.useRestfulMethods) {
        options.method = me.httpMethods.read;
      }

      AjaxHelper.get(event.url + me.buildQueryString(event.url, allParams), ObjectHelper.assign(options, me.fetchOptions)).then(async response => {
        const data = response.parsedJson,
              isArray = Array.isArray(data),
              success = isArray || data && ObjectHelper.getPath(data, me.responseSuccessProperty) !== false,
              remoteTotal = isArray ? null : ObjectHelper.getPath(data, me.responseTotalProperty);
        me._isLoading = false;
        event.response = response;
        event.json = data;

        if (success) {
          if (remoteTotal != null) {
            me.remoteTotal = parseInt(remoteTotal, 10);
          } // If we are issuing paged requests, work out what page we are on based
          // on the requested page and the size of the dataset declared.

          if (me.isPaged) {
            if (me.remoteTotal >= 0) {
              const requestedPage = me.pageParamName ? allParams[me.pageParamName] : allParams[me.pageStartParamName] / me.pageSize + 1;
              me.currentPage = Math.min(requestedPage, me.lastPage);
            } else {
              throw new Error('A paged store must receive its responseTotalProperty in each data packet');
            }
          }

          event.data = isArray ? data : ObjectHelper.getPath(data, me.responseDataProperty);
          await successFn(event);
          me.trigger('load' + eventName, event);
          resolve(event);
        } else {
          event.exception = true;
          event.exceptionType = 'server';
          me.trigger('exception', event);
          reject(event);
        } // finally

        me.trigger('afterRequest', event);
      }).catch(responseOrError => {
        me._isLoading = false;
        event.exception = true;

        if (responseOrError instanceof Response) {
          event.exceptionType = responseOrError.ok ? 'server' : 'network';
          event.response = responseOrError;
          event.error = responseOrError.error;
        } else {
          event.exceptionType = 'server';
          event.error = responseOrError;
        }

        me.trigger('exception', event);
        reject(event); // finally

        me.trigger('afterRequest', event);
      });
    }) : null;
  }
  /**
   * Load data from the {@link #config-readUrl}.
   * @param {Object} params A hash of parameters to append to querystring (will also append Store#params)
   * @returns {Promise} A Promise which will be resolved if the load succeeds, and rejected if the load is
   * vetoed by a {@link #event-beforeLoad} handler, or if an {@link #event-exception} is detected.
   * The resolved function is passed the event object passed to any event handlers.
   * The rejected function is passed the {@link #event-exception} event if an exception occurred,
   * or `false` if the load was vetoed by a {@link #event-beforeLoad} handler.
   * @fires beforeLoad
   * @fires loadStart
   * @fires beforeRequest
   * @fires load
   * @fires exception
   * @fires afterRequest
   * @category CRUD
   * @async
   * @returns {Promise} A promise which is resolved when the Ajax request completes and has been processed.
   */

  async load(params) {
    const me = this;

    if (me.isPaged) {
      return me.loadPage((me.currentPage || 0) + 1, params);
    } else {
      return me.internalLoad(params, '', event => {
        // The set Data setter will trigger the refresh event with { action: 'dataset' }
        me.data = event.data;
      });
    }
  }
  /**
   * Loads children into specified parent record. Parent records id is sent as a param (param name configured with
   * {@link #config-parentIdParamName}.
   * @param {Core.data.Model} parentRecord Parent record
   * @returns {Promise} A Promise which will be resolved if the load succeeds, and rejected if the load is
   * vetoed by a {@link #event-beforeLoadChildren} handler, or if an {@link #event-exception} is detected.
   * The resolved function is passed the event object passed to any event handlers.
   * The rejected function is passed the {@link #event-exception} event if an exception occurred,
   * or `false` if the load was vetoed by a {@link #event-beforeLoadChildren} handler.
   * @fires beforeLoadChildren
   * @fires loadChildrenStart
   * @fires beforeRequest
   * @fires loadChildren
   * @fires exception
   * @fires afterRequest
   * @category CRUD
   * @async
   * @returns {Promise} A promise which is resolved when the Ajax request completes and has been processed.
   */

  async loadChildren(parentRecord) {
    const me = this;
    return me.internalLoad({
      [me.parentIdParamName]: parentRecord.id
    }, 'Children', event => {
      event.parentRecord = parentRecord; // Append received children

      parentRecord.data[parentRecord.constructor.childrenField] = event.data;
      parentRecord.processChildren(parentRecord.stores);
    });
  }
  /**
   * Loads a page of data from the {@link #config-readUrl}.
   * @param {Number} page The *one based* page number to load.
   * @param {Object} params A hash of parameters to append to querystring (will also append Store#params)
   * @returns {Promise} A Promise which will be resolved if the load succeeds, and rejected if the load is
   * vetoed by a {@link #event-beforeLoadPage} handler, or if an {@link #event-exception} is detected.
   * The resolved function is passed the event object passed to any event handlers.
   * The rejected function is passed the {@link #event-exception} event if an exception occurred,
   * or `false` if the load was vetoed by a {@link #event-beforeLoadPage} handler.
   * @fires beforeLoadPage
   * @fires loadPageStart
   * @fires beforeRequest
   * @fires loadPage
   * @fires exception
   * @fires afterRequest
   * @category CRUD
   * @async
   * @returns {Promise} A promise which is resolved when the Ajax request completes and has been processed.
   */

  async loadPage(page, params) {
    if (this.allCount) {
      page = Math.min(page, this.lastPage);
    }

    const me = this,
          pageParam = me.pageParamName ? {
      [me.pageParamName]: page
    } : {
      [me.pageStartParamName]: (page - 1) * me.pageSize
    };
    pageParam[me.pageSizeParamName] = me.pageSize;
    return me.internalLoad(ObjectHelper.assign(pageParam, params), 'Page', event => {
      // We go directly to loadPage because paging a tree store is unsupportable.
      // loadPage will trigger the refresh event with { action: 'pageLoad' }
      me.loadData(event.data, 'pageLoad');
    });
  }
  /**
   * If this store {@link #property-isPaged is paged}, and is not already at the {@link #property-lastPage}
   * then this will load the next page of data.
   * @fires beforeLoadPage
   * @fires loadPageStart
   * @fires beforeRequest
   * @fires loadPage
   * @fires exception
   * @fires afterRequest
   * @category CRUD
   * @async
   * @returns {Promise} A promise which is resolved when the Ajax request completes and has been processed.
   */

  async nextPage(params) {
    return this.isPaged && this.currentPage !== this.lastPage ? this.loadPage(this.currentPage + 1, params) : immediatePromise$1;
  }
  /**
   * If this store {@link #property-isPaged is paged}, and is not already at the first page
   * then this will load the previous page of data.
   * @fires beforeLoadPage
   * @fires loadPageStart
   * @fires beforeRequest
   * @fires loadPage
   * @fires exception
   * @fires afterRequest
   * @category CRUD
   * @async
   * @returns {Promise} A promise which is resolved when the Ajax request completes and has been processed.
   */

  async previousPage(params) {
    return this.isPaged && this.currentPage !== 1 ? this.loadPage(this.currentPage - 1, params) : immediatePromise$1;
  }
  /**
   * Commits all changes (added, modified and removed) using corresponding urls ({@link #config-createUrl},
   * {@link #config-updateUrl} and {@link #config-deleteUrl})
   * @fires beforeCommit
   * @returns {Promise} A Promise which is resolved only if all pending changes (Create, Update and Delete) successfully resolve.
   * Both the resolve and reject functions are passed a `commitState` object which is stored the {@link #event-afterRequest}
   * event for each request. Each event contains the `exception`, `request` and `response` properties eg:
   *
   *     {
   *          success: true,                  // If *all* commits succeeded
   *          changes: {
   *              added: [records...],
   *              modified: [records...],
   *              removed: [records...],
   *          },
   *          added: {
   *              source: theStore,
   *              exception: true,            // Only if the add request triggered an exception
   *              exceptionType: 'server'/'network', // Only if the add request triggered an exception
   *              response: Response,
   *              json: parsedResponseObject
   *          },
   *          modified: {},                   // Same format as added
   *          removed: {}                     // Same format as added
   *     }
   *
   * If there were no pending changes, the resolve and reject functions are passed no parameters.
   *
   * Returns `false` if a commit operation is already in progress.
   * The resolved function is passed the event object passed to any event handlers.
   * The rejected function is passed the {@link #event-exception} event if an exception occurred,
   * @category CRUD
   * @async
   * @returns {Promise} A promise which is resolved when all the Ajax requests complete and have been processed.
   */

  async commit() {
    const me = this,
          {
      changes
    } = me,
          allPromises = []; // not allowing additional commits while in progress
    // TODO: should queue

    if (me.commitPromise) {
      return false;
    } // No outstanding changes, return a Promise that resolves immediately.

    if (!changes) {
      // Special handling of modified. If only non-persistable fields have been modified, it wont count among
      // `changes`, but still needs to be cleared
      if (me.modified.count) {
        me.modified.forEach(record => record.clearChanges(false));
        me.modified.clear();
      }

      return immediatePromise$1;
    } // Flag all affected records as being committed

    [...changes.added, ...changes.modified, ...changes.removed].forEach(record => record.meta.committing = true);

    if (me.trigger('beforeCommit', {
      changes
    }) !== false) {
      const commitState = {
        action: 'commit',
        exception: false,
        changes
      };
      let p = me.commitRemoved(commitState);

      if (p) {
        allPromises.push(p);
      }

      p = me.commitAdded(commitState);

      if (p) {
        allPromises.push(p);
      }

      p = me.commitModified(commitState);

      if (p) {
        allPromises.push(p);
      } // If there were no urls configured, behave as a local store

      if (!allPromises.length) {
        me.modified.forEach(r => r.clearChanges(false));
        me.modified.clear();
        me.added.forEach(r => r.clearChanges(false));
        me.added.clear();
        me.removed.clear();
        me.trigger('commit', {
          changes
        });
        return immediatePromise$1;
      } // The Promises from the commit methods all resolve whether the request
      // succeeded or not. They each contribute their afterrequest event to the
      // commitState which can be used to detect overall success or failure
      // and granular inspection of which operations succeeded or failed.
      // If there's only one operation, wait for it.
      // If there's more than one operation, we have to wait for allPromises to resolve.

      p = allPromises.length === 1 ? allPromises[0] : Promise.all(allPromises);
      return me.commitPromise = new Promise((resolve, reject) => {
        p.then(() => {
          me.commitPromise = null;

          if (commitState.exception) {
            me.trigger('exception', commitState);
            reject(commitState);
          } else {
            me.trigger('commit', {
              changes
            });
            resolve(commitState);
          }
        }).catch(() => {
          me.commitPromise = null;
          reject(commitState);
        });
      });
    }
  } // Performs background autocommit with reject checking

  doAutoCommit() {
    if (this.suspendCount <= 0) {
      this.commit().catch(commitState => {
        const {
          response
        } = commitState; // Skip throw if response is `ok` or `status` is 500 or 404 and request is successfully parsed
        // These errors are notified by API events

        if (!(response && (response.ok && response.parsedJson || [500, 404].includes(response.status)))) {
          throw commitState;
        }
      });
    }
  } // TODO: need a way to abort commits

  /**
   * Commits added records by posting to {@link #config-createUrl}.
   * Server should return a JSON object with a 'success' property indicating whether the operation was succesful.
   * @param {Object} commitState An object into which is added a `delete` property being the {@link #event-afterRequest} event.
   * @returns {Promise} If there are added records, a Promise which will be resolved whether the commit
   * succeeds or fails. The resulting event is placed into the `add` property of the passed `commitState`
   * parameter. If there are no added records, `null` is returned.
   * The resolved function is passed the event object passed to any event handlers.
   * @private
   * @fires beforeRequest
   * @fires commitAdded
   * @fires refresh
   * @fires exception
   * @fires afterRequest
   * @returns {Promise} A promise which is resolved when the Ajax request completes and has been processed.
   * @async
   */

  commitAdded(commitState) {
    const me = this,
          added = me.added,
          event = {
      action: 'create',
      params: me.params
    };
    return added.count && me.createUrl ? new Promise(resolve => {
      const toAdd = added.values.map(r => r.persistableData);
      commitState.create = event;
      let dataToSend = event.body = {
        data: toAdd
      };
      me.trigger('beforeRequest', event);

      if (me.sendAsFormData) {
        const formData = new FormData();
        formData.append('data', JSON.stringify(toAdd));
        dataToSend = formData;
      }

      const options = {
        headers: me.headers,
        parseJson: true
      };

      if (me.useRestfulMethods) {
        options.method = me.httpMethods.create;
      }

      AjaxHelper.post(me.createUrl + me.buildQueryString(me.createUrl, me.params), dataToSend, ObjectHelper.assign(options, me.fetchOptions)).then(response => {
        const data = response.parsedJson,
              isArray = Array.isArray(data),
              success = isArray || data && data.success !== false;
        commitState.response = response;
        event.json = data;
        event.response = response;

        if (success) {
          // Copy updated fields and updated ID back into records.
          // This also calls clearChanges on each record.
          me.processReturnedData(added.values, isArray ? data : data[me.responseDataProperty]); // Clear down added records cache

          added.clear();
          me.trigger('commitAdded'); // We must signal a full refresh because any number of records could have recieved any number of field updates
          // back from the server, so a refresh is more efficient than picking through the received updates.

          me.trigger('refresh', event);
          resolve(commitState);
        } else {
          // Clear committing flag
          added.forEach(r => r.meta.committing = false);
          commitState.exception = event.exception = true;
          commitState.exceptionType = event.exceptionType = 'server';
          me.trigger('exception', event);
          resolve(commitState);
        } // finally

        me.trigger('afterRequest', event);
      }).catch(responseOrError => {
        // Clear committing flag
        added.forEach(r => r.meta.committing = false);
        commitState.exception = event.exception = true;

        if (responseOrError instanceof Response) {
          commitState.response = responseOrError;
          event.exceptionType = responseOrError.ok ? 'server' : 'network';
          event.response = responseOrError;
          event.error = responseOrError.error;
        } else {
          event.exceptionType = 'server';
          event.error = responseOrError;
        }

        me.trigger('exception', event);
        resolve(commitState); // finally

        me.trigger('afterRequest', event);
      });
    }) : null;
  }
  /**
   * Commits modified records by posting to {@link #config-updateUrl}.
   * Server should return a JSON object with a 'success' property indicating whether the operation was succesful.
   * @param {Object} commitState An object into which is added a `delete` property being the {@link #event-afterRequest} event.
   * @returns {Promise} If there are added records, a Promise which will be resolved whether the commit
   * succeeds or fails. The resulting event is placed into the `update` property of the passed `commitState`
   * parameter. If there are no added records, `null` is returned.
   * The resolved function is passed the event object passed to any event handlers.
   * @private
   * @fires beforeRequest
   * @fires commitModified
   * @fires refresh
   * @fires exception
   * @fires afterRequest
   * @returns {Promise} A promise which is resolved when the Ajax request completes and has been processed.
   * @async
   */

  commitModified(commitState) {
    const me = this,
          // Only include persistable changes
    {
      modified
    } = me.changes,
          event = {
      action: 'update',
      params: me.params
    },
          result = modified.length && me.updateUrl ? new Promise(resolve => {
      // Use the record's modificationData, not modifications.
      // modifications returns a map using *field names*
      // The server will expect a map using the original dataSource properties.
      const modifications = modified.map(r => me.writeAllFields ? r.persistableData : r.modificationData).filter(el => !ObjectHelper.isEmpty(el)); // Check if modifications are empty and don't make request

      if (modifications.length === 0) {
        me.modified.clear();
        modified.forEach(r => r.meta.committing = false);
        resolve();
        return;
      }

      commitState.update = event;
      let dataToSend = event.body = {
        data: modifications
      };
      me.trigger('beforeRequest', event);

      if (me.sendAsFormData) {
        const formData = new FormData();
        formData.append('data', JSON.stringify(modifications));
        dataToSend = formData;
      }

      const options = {
        headers: me.headers,
        parseJson: true
      };

      if (me.useRestfulMethods) {
        options.method = me.httpMethods.update;
      }

      AjaxHelper.post(me.updateUrl + me.buildQueryString(me.updateUrl, me.params), dataToSend, ObjectHelper.assign(options, me.fetchOptions)).then(response => {
        const data = response.parsedJson,
              isArray = Array.isArray(data),
              success = isArray || data && data.success !== false;
        commitState.response = response;
        event.json = data;
        event.response = response;

        if (success) {
          // Copy updated fields and updated ID back into records.
          // This also calls clearChanges on each record.
          me.processReturnedData(modified, isArray ? data : data[me.responseDataProperty], true); // Clear down modified records cache

          me.modified.clear();
          me.trigger('commitModified'); // We must signal a full refresh because any number of records could have recieved any number of field updates
          // back from the server, so a refresh is more efficient than picking through the received updates.

          me.trigger('refresh', event);
          resolve(commitState);
        } else {
          // Clear committing flag
          modified.forEach(r => r.meta.committing = false);
          commitState.exception = event.exception = true;
          event.exceptionType = 'server';
          me.trigger('exception', event);
          resolve(commitState);
        } // finally

        me.trigger('afterRequest', event);
      }).catch(responseOrError => {
        // Clear committing flag
        modified.forEach(r => r.meta.committing = false);
        commitState.exception = event.exception = true;

        if (responseOrError instanceof Response) {
          commitState.response = responseOrError;
          event.exceptionType = responseOrError.ok ? 'server' : 'network';
          event.response = responseOrError;
          event.error = responseOrError.error;
        } else {
          event.exceptionType = 'server';
          event.error = responseOrError;
        }

        me.trigger('exception', event);
        resolve(commitState); // finally

        me.trigger('afterRequest', event);
      });
    }) : null; // Also clear non-persistable changes

    if (!modified.length && me.modified.count) {
      me.modified.clear();
    }

    return result;
  }

  processReturnedData(localRecords, returnedData, isUpdating = false) {
    const me = this,
          Model = me.modelClass,
          idDataSource = Model.fieldMap.id.dataSource;
    returnedData.forEach((recData, i) => {
      const record = localRecords[i]; // Must clear changed state before syncId goes through store.onModelChange

      record.clearChanges(false); // Using syncId to update record's id with no flagging the property as modified.

      record.syncId(recData[idDataSource]); // When updating, only want to apply the actual changes and not reapply defaults. When adding, also
      // apply the defaults

      Object.assign(localRecords[i].data, Model.processData(recData, isUpdating));
    });
  }
  /**
   * Commits removed records by posting to {@link #config-deleteUrl}.
   * Server should return a JSON object with a 'success' property indicating whether the operation was successful.
   * @param {Object} commitState An object into which is added a `delete` property being the {@link #event-afterRequest} event.
   * @returns {Promise} If there are added records, a Promise which will be resolved whether the commit
   * succeeds or fails. The resulting event is placed into the `delete` property of the passed `commitState`
   * parameter. If there are no added records, `null` is returned.
   * The resolved function is passed the event object passed to any event handlers.
   * @private
   * @fires beforerequest
   * @fires commitremoved
   * @fires refresh
   * @fires exception
   * @fires afterrequest
   * @returns {Promise} A promise which is resolved when the Ajax request completes and has been processed.
   * @async
   */

  commitRemoved(commitState) {
    const me = this,
          removed = me.removed,
          event = {
      action: 'delete',
      params: me.params
    };
    return removed.count && me.deleteUrl ? new Promise(resolve => {
      commitState.delete = event;
      let dataToSend = event.body = {
        ids: removed.map(r => r.id)
      };
      me.trigger('beforeRequest', event);

      if (me.sendAsFormData) {
        const formData = new FormData();
        formData.append('id', JSON.stringify(dataToSend.ids));
        dataToSend = formData;
      }

      const options = {
        headers: me.headers,
        parseJson: true
      };

      if (me.useRestfulMethods) {
        options.method = me.httpMethods.delete;
      }

      AjaxHelper.post(me.deleteUrl + me.buildQueryString(me.deleteUrl, me.params), dataToSend, ObjectHelper.assign(options, me.fetchOptions)).then(response => {
        const data = response.parsedJson,
              isArray = Array.isArray(data),
              success = isArray || data && data.success !== false;
        commitState.response = response;
        event.json = data;
        event.response = response;

        if (success) {
          removed.forEach(record => record.meta.committing = false); // In case used by other store etc.

          removed.clear();
          me.trigger('commitRemoved');
          me.trigger('refresh', event);
          resolve(commitState);
        } else {
          // Clear committing flag
          removed.forEach(r => r.meta.committing = false);
          commitState.exception = event.exception = true;
          event.exceptionType = 'server';
          me.trigger('exception', event);
          resolve(commitState);
        } // finally

        me.trigger('afterRequest', event);
      }).catch(responseOrError => {
        // Clear committing flag
        removed.forEach(r => r.meta.committing = false);
        commitState.exception = event.exception = true;

        if (responseOrError instanceof Response) {
          commitState.response = responseOrError;
          event.exceptionType = responseOrError.ok ? 'server' : 'network';
          event.response = responseOrError;
          event.error = responseOrError.error;
        } else {
          event.exceptionType = 'server';
          event.error = responseOrError;
        }

        me.trigger('exception', event);
        resolve(commitState); // finally

        me.trigger('afterRequest', event);
      });
    }) : null;
  }

  get remoteFilter() {
    return Boolean(this.filterParamName || this.restfulFilter);
  }

}
AjaxStore._$name = 'AjaxStore';

/**
 * @module Core/data/DomDataStore
 */

/**
 * Stores data on a dom element (by setting element._domData).
 * Instead of using HTML5:s element.dataset, which turned out to be slow.
 * @internal
 */
class DomDataStore {
  /**
   * Get data that is connected to specified element.
   * @param {HTMLElement} element DOM element
   * @param {String} [key] The name of the property in the element data to return
   * @returns {Object} Data
   */
  static get(element, key) {
    const result = element._domData || (element._domData = {});
    return key != null ? result[key] : result;
  }
  /**
   * Remove data connected to specified element.
   * @param {HTMLElement} element DOM element
   * @param {String} key The name of the property in the element data to remove.
   */

  static remove(element, key) {
    const {
      _domData
    } = element,
          was = _domData === null || _domData === void 0 ? void 0 : _domData[key];
    _domData === null || _domData === void 0 ? true : delete _domData[key];
    return was;
  }
  /**
   * Set data connected to specified element (completely replacing any existing).
   * To update data, use DomDataStore#assign instead.
   * @param {HTMLElement} element DOM element
   * @param {Object|String} data Data object to set, or property name to set
   * @param {*} [value] If the previous parameter was a property name, this is the value to set.
   */

  static set(element, data, value) {
    if (arguments.length === 3) {
      (element._domData || (element._domData = {}))[data] = value;
    } else {
      element._domData = data;
    }
  }
  /**
   * Updates data connected to specified element.
   * @param {HTMLElement} element DOM element
   * @param {Object} data Data to assign
   */

  static assign(element, data) {
    Object.assign(element._domData || (element._domData = {}), data);
  }

}
DomDataStore._$name = 'DomDataStore';

/**
 * @module Core/data/stm/state/StateBase
 */
const throwAbstractMethodCall = () => {
  throw new Error('Abstract method call!');
};
const throwInvalidMethodCall = () => {
  throw new Error('Method cannot be called at this state!');
};
/**
 * Abstract class for STM states
 *
 * Every on* method should return a state for the STM which it should switch to
 * or throw an exception that this call at this state is illegal.
 *
 * Interface of this class mirrors interface of {@link Core.data.stm.StateTrackingManager}.
 *
 * @abstract
 */

class StateBase extends Base {
  canUndo(stm) {
    throwAbstractMethodCall();
  }

  canRedo(stm) {
    throwAbstractMethodCall();
  }

  onUndo(stm) {
    throwAbstractMethodCall();
  }

  onRedo(stm) {
    throwAbstractMethodCall();
  }

  onStartTransaction(stm) {
    throwAbstractMethodCall();
  }

  onStopTransaction(stm) {
    throwAbstractMethodCall();
  }

  onStopTransactionDelayed(stm) {
    throwAbstractMethodCall();
  }

  onRejectTransaction(stm) {
    throwAbstractMethodCall();
  }

  onEnable(stm) {
    throwAbstractMethodCall();
  }

  onDisable(stm) {
    throwAbstractMethodCall();
  }

  onAutoRecordOn(stm) {
    throwAbstractMethodCall();
  }

  onAutoRecordOff(stm) {
    throwAbstractMethodCall();
  }

  onResetQueue(stm) {
    throwAbstractMethodCall();
  }

  onModelUpdate(stm) {
    throwAbstractMethodCall();
  }

  onStoreModelAdd(stm) {
    throwAbstractMethodCall();
  }

  onStoreModelInsert(stm) {
    throwAbstractMethodCall();
  }

  onStoreModelRemove(stm) {
    throwAbstractMethodCall();
  }

  onStoreModelRemoveAll(stm) {
    throwAbstractMethodCall();
  }

  onModelInsertChild(stm) {
    throwAbstractMethodCall();
  }

  onModelRemoveChild(stm) {
    throwAbstractMethodCall();
  }

}
StateBase._$name = 'StateBase';

/**
 * @module Core/data/stm/Props.js
 * @internal
 */
const STATE_PROP = Symbol('STATE_PROP');
const STORES_PROP = Symbol('STORES_PROP');
const QUEUE_PROP = Symbol('QUEUE_PROP');
const POS_PROP = Symbol('POS_PROP');
const TRANSACTION_PROP = Symbol('TRANSACTION_PROP');
const TRANSACTION_TIMER_PROP = Symbol('TRANSACTION_TIMER_PROP');
const AUTO_RECORD_PROP = Symbol('AUTO_RECORD_PROP');
const PROPS = Object.freeze([STATE_PROP, STORES_PROP, QUEUE_PROP, POS_PROP, TRANSACTION_PROP, TRANSACTION_TIMER_PROP, AUTO_RECORD_PROP]);

/**
 * @module Core/data/stm/state/Registry
 *
 * Provides map of registered STM states.
 *
 * Needed to remove states circular dependency.
 *
 * @internal
 */

const registry = new Map();
/**
 * Registers STM state class with the given name.
 *
 * @private
 *
 * @param {string} name
 * @param {Core.data.stm.state.StateBase} state
 */

const registerStmState = (name, state) => {
  registry.set(name, state);
};
/**
 * Resolves STM state class with the given name.
 *
 * @private
 *
 * @param {string} name
 * @return {Core.data.stm.state.StateBase} state
 */

const resolveStmState = state => {
  if (typeof state === 'string') {
    state = registry.get(state);
  }

  return state;
}; // UMD/module compatible export
// NOTE: the most compatible way of exporting is:
//       import registry from './Registry.js';
//       { registerStmState, resolveStmState } = registry;
//          or
//       registry.registerStmState(...);

var Registry = {
  registerStmState,
  resolveStmState
};

const resetQueue = (stm, options) => {
  const {
    undo,
    redo
  } = options;
  let newProps;

  if (undo && !redo) {
    newProps = {
      [QUEUE_PROP]: stm[QUEUE_PROP].slice(stm.position),
      [POS_PROP]: 0
    };
  } else if (redo && !undo) {
    newProps = {
      [QUEUE_PROP]: stm[QUEUE_PROP].slice(0, stm.position)
    };
  } else {
    newProps = {
      [QUEUE_PROP]: [],
      [POS_PROP]: 0
    };
  }
  return [newProps, () => {
    stm.notifyStoresAboutQueueReset(options);
  }];
};

/**
 * @module Core/data/stm/state/DisabledState
 */
/**
 * STM disabled state class.
 *
 * @internal
 */

class DisabledStateClass extends StateBase {
  canUndo() {
    return false;
  }

  canRedo() {
    return false;
  }

  onUndo() {
    throwInvalidMethodCall();
  }

  onRedo() {
    throwInvalidMethodCall();
  }

  onEnable(stm) {
    return stm.autoRecord ? 'autoreadystate' : 'readystate';
  }

  onDisable() {
    throwInvalidMethodCall();
  }

  onAutoRecordOn() {
    return {
      [AUTO_RECORD_PROP]: true
    };
  }

  onAutoRecordOff() {
    return {
      [AUTO_RECORD_PROP]: false
    };
  }

  onStartTransaction() {
    throwInvalidMethodCall();
  }

  onStopTransaction() {
    throwInvalidMethodCall();
  }

  onStopTransactionDelayed() {
    throwInvalidMethodCall();
  }

  onRejectTransaction() {
    throwInvalidMethodCall();
  }

  onResetQueue(stm, options) {
    return resetQueue(stm, options);
  }

  onModelUpdate() {}

  onModelInsertChild() {}

  onModelRemoveChild() {}

  onStoreModelAdd() {}

  onStoreModelInsert() {}

  onStoreModelRemove() {}

  onStoreRemoveAll() {}

}
/**
 * STM disabled state.
 *
 * @internal
 */

const DisabledState = new DisabledStateClass();
Registry.registerStmState('disabledstate', DisabledState);

/**
 * @module Core/data/stm/Transaction
 */
const ACTION_QUEUE_PROP = Symbol('ACTION_QUEUE_PROP');
/**
 * STM transaction class, holds list of actions constituting a transaction.
 *
 * A transaction can be undone and redone. Upon undo all the actions being held
 * are undone in reverse order. Upon redo all the actions being held are redone
 * in forward order.
 */

class Transaction extends Base {
  get defaultConfig() {
    return {
      /**
       * Transaction title
       *
       * @config {String}
       * @default
       */
      title: null
    };
  }

  construct(...args) {
    this[ACTION_QUEUE_PROP] = [];
    super.construct(...args);
  }
  /**
   * Gets transaction's actions queue
   *
   * @property {Core.data.stm.action.ActionBase[]}
   */

  get queue() {
    return this[ACTION_QUEUE_PROP].slice(0);
  }
  /**
   * Gets transaction's actions queue length
   *
   * @property {Number}
   */

  get length() {
    return this[ACTION_QUEUE_PROP].length;
  }
  /**
   * Adds an action to the transaction.
   *
   * @param {Core.data.stm.action.ActionBase|Object} action
   */

  addAction(action) {
    this[ACTION_QUEUE_PROP].push(action);
  }
  /**
   * Undoes actions held
   */

  undo() {
    const queue = this[ACTION_QUEUE_PROP];

    for (let i = queue.length - 1; i >= 0; --i) {
      queue[i].undo();
    }
  }
  /**
   * Redoes actions held
   */

  redo() {
    const queue = this[ACTION_QUEUE_PROP];

    for (let i = 0, len = queue.length; i < len; ++i) {
      queue[i].redo();
    }
  }

}
Transaction._$name = 'Transaction';

/**
 * @module Core/data/stm/state/ReadyState
 */
/**
 * STM ready state class.
 *
 * @internal
 */

class ReadyStateClass extends StateBase {
  canUndo(stm) {
    //      v
    // |*|*|
    return 0 < stm.position && stm.position <= stm.length;
  }

  canRedo(stm) {
    //  v
    // |*|*|
    return 0 <= stm.position && stm.position < stm.length;
  }

  onUndo(stm, steps) {
    const queue = stm[QUEUE_PROP];
    let curPos = stm.position,
        newPos = Math.max(0, curPos - steps);

    const next = () => {
      stm.notifyStoresAboutStateRestoringStart();

      while (curPos != newPos) {
        queue[--curPos].undo();
      }

      return [stm.autoRecord ? 'autoreadystate' : 'readystate', () => {
        stm.notifyStoresAboutStateRestoringStop();
      }];
    };

    return [{
      [STATE_PROP]: 'restoringstate',
      [POS_PROP]: newPos
    }, next];
  }

  onRedo(stm, steps) {
    const queue = stm[QUEUE_PROP];
    let curPos = stm.position,
        newPos = Math.min(queue.length, curPos + steps);

    const next = () => {
      stm.notifyStoresAboutStateRestoringStart();

      do {
        queue[curPos++].redo();
      } while (curPos != newPos);

      return [stm.autoRecord ? 'autoreadystate' : 'readystate', () => {
        stm.notifyStoresAboutStateRestoringStop();
      }];
    };

    return [{
      [STATE_PROP]: 'restoringstate',
      [POS_PROP]: newPos
    }, next];
  }

  onEnable() {
    throwInvalidMethodCall();
  }

  onDisable() {
    return 'disabledstate';
  }

  onAutoRecordOn() {
    return {
      [STATE_PROP]: 'autoreadystate',
      [AUTO_RECORD_PROP]: true
    };
  }

  onAutoRecordOff() {
    throwInvalidMethodCall();
  }

  onStartTransaction(stm, title) {
    const transaction = new Transaction({
      title: title
    });
    return [{
      [STATE_PROP]: 'recordingstate',
      [TRANSACTION_PROP]: transaction
    }, () => {
      stm.notifyStoresAboutStateRecordingStart(transaction);
    }];
  }

  onStopTransaction() {
    throwInvalidMethodCall();
  }

  onStopTransactionDelayed() {
    throwInvalidMethodCall();
  }

  onRejectTransaction() {
    throwInvalidMethodCall();
  }

  onResetQueue(stm, options) {
    return resetQueue(stm, options);
  }

  onModelUpdate() {}

  onModelInsertChild() {}

  onModelRemoveChild() {}

  onStoreModelAdd() {}

  onStoreModelInsert() {}

  onStoreModelRemove() {}

  onStoreRemoveAll() {}

}
/**
 * STM ready state.
 *
 * @internal
 */

let ReadyState = new ReadyStateClass();
Registry.registerStmState('readystate', ReadyState);

/**
 * @module Core/data/stm/state/RecordingState
 */
/**
 * STM recording state class.
 *
 * @internal
 */

class RecordingStateClass extends StateBase {
  canUndo() {
    return false;
  }

  canRedo() {
    return false;
  }

  onEnable() {}

  onDisable(stm) {
    const transaction = stm[TRANSACTION_PROP];
    stm.notifyStoresAboutStateRecordingStop(transaction, {
      disabled: true
    });
    return {
      [STATE_PROP]: 'disabledstate',
      [TRANSACTION_PROP]: null
    };
  }

  onAutoRecordOn(stm) {
    return [{
      [STATE_PROP]: 'autorecordingstate',
      [AUTO_RECORD_PROP]: true
    }, () => {
      stm.stopTransactionDelayed();
    }];
  }

  onAutoRecordOff() {
    throwInvalidMethodCall();
  }

  onStartTransaction() {
    throwInvalidMethodCall();
  }

  onStopTransaction(stm, title) {
    const transaction = stm[TRANSACTION_PROP];
    let position = stm[POS_PROP];
    let queue = stm[QUEUE_PROP];

    if (transaction.length) {
      if (!transaction.title && !title && stm.getTransactionTitle) {
        transaction.title = stm.getTransactionTitle(transaction);
      } else if (title) {
        transaction.title = title;
      }

      queue[position] = transaction;
      queue.length = ++position;
    }

    return [{
      [STATE_PROP]: 'readystate',
      [POS_PROP]: position,
      [TRANSACTION_PROP]: null
    }, () => {
      stm.notifyStoresAboutStateRecordingStop(transaction, {
        stop: true
      });
    }];
  }

  onRejectTransaction(stm) {
    const transaction = stm[TRANSACTION_PROP];
    return [{
      [STATE_PROP]: 'restoringstate',
      [TRANSACTION_PROP]: null
    }, () => {
      if (transaction.length) {
        transaction.undo();
      }

      return ['readystate', () => {
        stm.notifyStoresAboutStateRecordingStop(transaction, {
          rejected: true
        });
      }];
    }];
  }

  onStopTransactionDelayed() {
    throwInvalidMethodCall();
  }

  onQueueReset() {
    throwInvalidMethodCall();
  }

  onModelUpdate(stm, model, newData, oldData) {
    const transaction = stm[TRANSACTION_PROP];
    transaction.addAction(stm.makeModelUpdateAction(model, newData, oldData));
  }

  onModelInsertChild(stm, parentModel, index, childModel, previousParent, previousIndex) {
    const transaction = stm[TRANSACTION_PROP];
    transaction.addAction(stm.makeModelInsertChildAction(parentModel, index, childModel, previousParent, previousIndex));
  }

  onModelRemoveChild(stm, parentModel, childModels, context) {
    const transaction = stm[TRANSACTION_PROP];
    transaction.addAction(stm.makeModelRemoveChildAction(parentModel, childModels, context));
  }

  onStoreModelAdd(stm, store, models, silent) {
    const transaction = stm[TRANSACTION_PROP];
    transaction.addAction(stm.makeStoreModelAddAction(store, models, silent));
  }

  onStoreModelInsert(stm, store, index, models, context, silent) {
    const transaction = stm[TRANSACTION_PROP];
    transaction.addAction(stm.makeStoreModelInsertAction(store, index, models, context, silent));
  }

  onStoreModelRemove(stm, store, models, context, silent) {
    const transaction = stm[TRANSACTION_PROP];
    transaction.addAction(stm.makeStoreModelRemoveAction(store, models, context, silent));
  }

  onStoreRemoveAll(stm, store, allRecords, silent) {
    const transaction = stm[TRANSACTION_PROP];
    transaction.addAction(stm.makeStoreRemoveAllAction(store, allRecords, silent));
  }

}
/**
 * STM recording state.
 *
 * @internal
 */

const RecordingState = new RecordingStateClass();
Registry.registerStmState('recordingstate', RecordingState);

/**
 * @module Core/data/stm/state/RestoringState
 */
/**
 * STM restoring state class.
 *
 * @internal
 */

class RestoringStateClass extends StateBase {
  canUndo() {
    return false;
  }

  canRedo() {
    return false;
  }

  onUndo() {
    throwInvalidMethodCall();
  }

  onRedo() {
    throwInvalidMethodCall();
  }

  onEnable() {
    throwInvalidMethodCall();
  }

  onDisable() {
    throwInvalidMethodCall();
  }

  onAutoRecordOn() {
    return {
      [AUTO_RECORD_PROP]: true
    };
  }

  onAutoRecordOff() {
    return {
      [AUTO_RECORD_PROP]: false
    };
  }

  onStartTransaction() {
    throwInvalidMethodCall();
  }

  onStopTransaction() {
    throwInvalidMethodCall();
  }

  onStopTransactionDelayed() {
    throwInvalidMethodCall();
  }

  onRejectTransaction() {
    throwInvalidMethodCall();
  }

  onQueueReset() {
    throwInvalidMethodCall();
  }

  onModelUpdate() {}

  onModelInsertChild() {}

  onModelRemoveChild() {}

  onStoreModelAdd() {}

  onStoreModelInsert() {}

  onStoreModelRemove() {}

  onStoreRemoveAll() {}

}
/**
 * STM restoring state.
 *
 * @internal
 */

const RestoringState = new RestoringStateClass();
Registry.registerStmState('restoringstate', RestoringState);

/**
 * @module Core/data/stm/state/AutoReadyState
 */
/**
 * STM transaction autostart ready state class.
 *
 * @internal
 */

class AutoReadyStateClass extends ReadyStateClass {
  onAutoRecordOn() {
    throwInvalidMethodCall();
  }

  onAutoRecordOff() {
    return {
      [STATE_PROP]: 'readystate',
      [AUTO_RECORD_PROP]: false
    };
  }

  onStartTransaction(stm, title) {
    const transaction = new Transaction({
      title: title
    });
    return [{
      [STATE_PROP]: 'autorecordingstate',
      [TRANSACTION_PROP]: transaction
    }, () => {
      stm.notifyStoresAboutStateRecordingStart(transaction);
      stm.stopTransactionDelayed();
    }];
  }

  onModelUpdate(stm, model, newData, oldData) {
    stm.startTransaction();
    stm.onModelUpdate(model, newData, oldData);
  }

  onModelInsertChild(stm, parentModel, index, childModels, context) {
    stm.startTransaction();
    stm.onModelInsertChild(parentModel, index, childModels, context);
  }

  onModelRemoveChild(stm, parentModel, childModels, context) {
    stm.startTransaction();
    stm.onModelRemoveChild(parentModel, childModels, context);
  }

  onStoreModelAdd(stm, store, models, silent) {
    stm.startTransaction();
    stm.onStoreModelAdd(store, models, silent);
  }

  onStoreModelInsert(stm, store, index, models, context, silent) {
    stm.startTransaction();
    stm.onStoreModelInsert(store, index, models, silent);
  }

  onStoreModelRemove(stm, store, models, context, silent) {
    stm.startTransaction();
    stm.onStoreModelRemove(store, models, context, silent);
  }

  onStoreRemoveAll(stm, store, allRecords, silent) {
    stm.startTransaction();
    stm.onStoreRemoveAll(store, allRecords, silent);
  }

}
/**
 * STM transaction autostart ready state.
 *
 * @internal
 */

const AutoReadyState = new AutoReadyStateClass();
Registry.registerStmState('autoreadystate', AutoReadyState);

const {
  defineProperty: defineProperty$4
} = Reflect;
/**
 * @module Core/mixin/Delayable
 */
// Global timeout collections for tests

let globalDelays = null;

if (VersionHelper.isTestEnv) {
  const bryntum = BrowserHelper.global.bryntum || (BrowserHelper.global.bryntum = {});
  globalDelays = bryntum.globalDelays = {
    timeouts: new Map(),
    intervals: new Map(),
    animationFrames: new Map(),

    isEmpty(includeIntervals = false) {
      return globalDelays.timeouts.size + globalDelays.animationFrames.size + (includeIntervals ? globalDelays.intervals.size : 0) === 0;
    }

  };
}

const
/**
 * Creates and returns a function that will call the user-supplied `fn`.
 *
 * @param {Core.mixin.Delayable} me
 * @param {Function} fn The user function to call when the timer fires.
 * @param {Function} wrapFn The function the user will call to start the timer.
 * @param {Object} options The invoke options.
 * @param {Array} [options.appendArgs] The argument list to append to those passed to the function.
 * @param {Object} [options.thisObj] The `this` reference for `fn`.
 * @returns {Function}
 * @private
 */
makeInvoker = (me, fn, wrapFn, options) => {
  const named = typeof fn === 'string',
        appendArgs = options && options.appendArgs || [],
        // The invoker fn is intended to be wired directly to native setTimeout/requestAnimationFrame/etc and so
  // it does not receive any arguments worth passing on to the user's fn. Those come from the original call
  // to the wrapFn.
  invoker = () => {
    wrapFn.timerId = null;
    wrapFn.lastCallTime = performance.now(); // Grab args now and null the stored args out (to avoid leaks):

    const args = wrapFn.args;
    wrapFn.args = null;

    if (named) {
      me[fn](...args, ...appendArgs);
    } else {
      fn.call(me, ...args, ...appendArgs);
    }

    wrapFn.called = true;
    ++wrapFn.calls;
  };

  if (options) {
    me = options.thisObj || me;
  } // We put most everything as properties on the wrapFn so that it can all be inspected in the debugger (unlike
  // closure variables) and expected in tests.

  wrapFn.lastCallTime = -9e9; // performance.now() = 0 at start...

  wrapFn.calls = 0;
  wrapFn.invoker = invoker;
  invoker.wrapFn = wrapFn;
  return invoker;
},

/**
 * Decorates the supported `wrapFn` with additional methods and an `isPending` readonly
 * property. These decorations are available to user code to help manage the scheduling
 * behavior of the buffered function.
 *
 * @param {Core.mixin.Delayable} me
 * @param {Function} wrapFn The function the user will call to start the timer.
 * @param {String} cancelFn The name of the function that will cancel a timer.
 * @returns {Function} The `wrapFn` is returned.
 * @private
 */
decorateWrapFn = (me, wrapFn, cancelFn = 'clearTimeout') => {
  wrapFn.cancel = () => {
    if (wrapFn.isPending) {
      me[cancelFn](wrapFn.timerId); // avoid leaks and cleanup:

      wrapFn.args = wrapFn.timerId = null;
    }
  };

  wrapFn.flush = () => {
    if (wrapFn.isPending) {
      me[cancelFn](wrapFn.timerId);
      wrapFn.timerId = null; // we don't call cancel() since it also sets args=null

      wrapFn.invoker();
    }
  };

  wrapFn.now = (...args) => {
    wrapFn.cancel();
    wrapFn.args = args;
    wrapFn.invoker();
  };

  wrapFn.immediate = false;
  wrapFn.timerId = null;
  defineProperty$4(wrapFn, 'isPending', {
    get() {
      return wrapFn.timerId !== null;
    }

  });
  return wrapFn;
};
/**
 * Tracks setTimeout, setInterval and requestAnimationFrame calls and clears them on destroy.
 *
 * @example
 * someClass.setTimeout(() => console.log('hi'), 200);
 * someClass.setInterval(() => console.log('annoy'), 100);
 * // can also use named timeouts for easier tracking
 * someClass.setTimeout(() => console.log('named'), 300, 'named');
 * someClass.clearTimeout('named');
 *
 * @mixin
 */

var Delayable = (Target => class Delayable extends (Target || Base) {
  static get $name() {
    return 'Delayable';
  }

  static get declarable() {
    return [
    /**
     * This class property returns an object that specifies methods to wrap with configurable timer behaviors.
     *
     * It is used like so:
     * ```
     *  class Foo extends Delayable() {
     *      static get delayable() {
     *          return {
     *              expensiveMethod : 500
     *          };
     *      }
     *
     *      expensiveMethod() {
     *          this.things();
     *          this.moreThings();
     *          this.evenMoreThings();
     *      }
     *  }
     * ```
     * With the above in place, consider:
     * ```
     *  let instance = new Foo();
     *
     *  instance.expensiveMethod();
     * ```
     * Instead of the above code immediately calling the `expensiveMethod()`, it will start a timer that will
     * invoke the method 500ms later. Because `expensiveMethod()` is an instance method, each instance of `Foo`
     * will have its own timer.
     *
     * NOTE: Only instance methods are currently supported (i.e., only non-`static` methods).
     *
     * #### Options
     * The value of each key configures how the method will be scheduled. If the value is a number, it is
     * promoted to a config object of `type='buffer'` as in the following:
     * ```
     *  class Foo extends Delayable() {
     *      static get delayable() {
     *          return {
     *              expensiveMethod : {
     *                  type  : 'buffer',
     *                  delay : 500
     *              }
     *          };
     *      }
     *  });
     * ```
     * The `type` property of the config object must be one of three values. Other options can be provided
     * depending on the `type`:
     *
     *  - `buffer`<br>
     *    Other options:
     *     - `delay` (Number) : The number of milliseconds to wait before calling the underlying method. A
     *       value of 0 is equivalent to setting `immediate: true`.
     *     - `immediate` (Boolean) : Set to `true` to call immediately (effectively disabling the buffer).
     *  - `raf` (short for "request animation frame")<br>
     *    Other options:
     *     - `cancelOutstanding` (Boolean) : Set to `true` to cancel any pending animation frame requests and
     *       schedule a new one on each call.
     *     - `immediate` (Boolean) : Set to `true` to call immediately.
     *  - `throttle`<br>
     *    Other options:
     *     - `delay` (Number) : The number of milliseconds to wait after each execution before another
     *       execution takes place. A value of 0 is equivalent to setting `immediate: true`.
     *     - `immediate` (Boolean) : Set to `true` to call immediately (effectively disabling the throttle).
     *
     * While `immediate: true` can be specified at the class level, it is more typical to set it on the
     * instance's method as described below.
     *
     * #### Delayable Method API
     * Delayable methods have a consistent API to manage their scheduling. This API is added to the methods
     * themselves.
     *
     * For example:
     * ```
     *  let instance = new Foo();
     *
     *  instance.expensiveMethod();         // schedule a call in 500ms
     *  instance.expensiveMethod.isPending; // true
     *  instance.expensiveMethod.cancel();
     *  instance.expensiveMethod.flush();
     *  instance.expensiveMethod.now();
     *
     *  instance.expensiveMethod.delay = 10;
     *  instance.expensiveMethod();         // schedule a call in 10ms
     * ```
     *
     * ##### `isPending` (Boolean, readonly)
     * This boolean property will be `true` if a call has been scheduled, and false otherwise.
     *
     * ##### `cancel()`
     * Cancels a pending call if one has been scheduled. Otherwise this method does nothing.
     *
     * ##### `flush()`
     * Cancels the timer and causes the pending call to execute immediately. If there is no pending call, this
     * method does nothing.
     *
     * ##### `now()`
     * Cancels the timer (if one is pending) and executes the method immediately. If there is no pending call,
     * this method will still call the underlying method.
     *
     * @static
     * @member {Object} delayable
     * @internal
     */
    'delayable'];
  }

  doDestroy() {
    const me = this;

    if (me.timeoutIds) {
      me.timeoutIds.forEach((fn, id) => {
        if (typeof fn === 'function') {
          fn();
        }

        clearTimeout(id);

        if (globalDelays) {
          globalDelays.timeouts.delete(id);
        }
      });
      me.timeoutIds = null;
    }

    if (me.timeoutMap) {
      me.timeoutMap.forEach((name, id) => clearTimeout(id));
      me.timeoutMap = null;
    }

    if (me.intervalIds) {
      me.intervalIds.forEach(id => {
        clearInterval(id);

        if (globalDelays) {
          globalDelays.intervals.delete(id);
        }
      });
      me.intervalIds = null;
    }

    if (me.animationFrameIds) {
      me.animationFrameIds.forEach(id => {
        cancelAnimationFrame(id);

        if (globalDelays) {
          globalDelays.animationFrames.delete(id);
        }
      });
      me.animationFrameIds = null;
    }

    super.doDestroy();
  }
  /**
   * Check if a named timeout is active
   * @param name
   * @internal
   */

  hasTimeout(name) {
    return !!(this.timeoutMap && this.timeoutMap.has(name));
  }
  /**
   * Same as native setTimeout, but will be cleared automatically on destroy. If a propertyName is supplied it will
   * be used to store the timeout id.
   * @param {Object} timeoutSpec An object containing the details about that function, and the time delay.
   * @param {Function|String} timeoutSpec.fn The function to call, or name of function in this object to call. Used as the `name` parameter if a string.
   * @param {Number} timeoutSpec.delay The milliseconds to delay the call by.
   * @param {Object[]} timeoutSpec.args The arguments to pass.
   * @param {String} [timeoutSpec.name] The name under which to register the timer. Defaults to `fn.name`.
   * @param {Boolean} [timeoutSpec.runOnDestroy] Pass `true` if this function should be executed if the Delayable instance is destroyed while function is scheduled.
   * @param {Boolean} [timeoutSpec.cancelOutstanding] Pass `true` to cancel any outstanding invocation of the passed function.
   * @returns {Number}
   * @internal
   */

  setTimeout({
    fn,
    delay,
    name,
    runOnDestroy,
    cancelOutstanding,
    args
  }) {
    if (arguments.length > 1 || typeof arguments[0] === 'function') {
      [fn, delay, name, runOnDestroy] = arguments;
    }

    if (typeof fn === 'string') {
      name = fn;
    } else if (!name) {
      name = fn.name;
    }

    if (cancelOutstanding) {
      this.clearTimeout(name);
    }

    const me = this,
          timeoutIds = me.timeoutIds || (me.timeoutIds = new Map()),
          timeoutMap = me.timeoutMap || (me.timeoutMap = new Map()),
          timeoutId = setTimeout(() => {
      if (typeof fn === 'string') {
        fn = me[name];
      } // Cleanup before invocation in case fn throws

      timeoutIds && timeoutIds.delete(timeoutId);
      timeoutMap && timeoutMap.delete(name);
      globalDelays && globalDelays.timeouts.delete(timeoutId);
      fn.apply(me, args);
    }, delay);
    timeoutIds.set(timeoutId, runOnDestroy ? fn : true);

    if (globalDelays) {
      globalDelays.timeouts.set(timeoutId, {
        fn,
        delay,
        name
      });
    }

    if (name) {
      timeoutMap.set(name, timeoutId);
    }

    return timeoutId;
  }
  /**
   * clearTimeout wrapper, either call with timeout id as normal clearTimeout or with timeout name (if you specified
   * a name to setTimeout())
   * property to null.
   * @param {Number|String} idOrName timeout id or name
   * @internal
   */

  clearTimeout(idOrName) {
    let id = idOrName;

    if (typeof id === 'string') {
      if (this.timeoutMap) {
        id = this.timeoutMap.get(idOrName);
        this.timeoutMap.delete(idOrName);
      } else {
        return;
      }
    }

    clearTimeout(id);
    this.timeoutIds && this.timeoutIds.delete(id);
    globalDelays && globalDelays.timeouts.delete(id);
  }
  /**
   * clearInterval wrapper
   * @param {Number} id
   * @internal
   */

  clearInterval(id) {
    clearInterval(id);
    this.intervalIds && this.intervalIds.delete(id);
    globalDelays && globalDelays.intervals.delete(id);
  }
  /**
   * Same as native setInterval, but will be cleared automatically on destroy
   * @param fn
   * @param delay
   * @returns {Number}
   * @internal
   */

  setInterval(fn, delay) {
    const intervalId = setInterval(fn, delay);
    (this.intervalIds || (this.intervalIds = new Set())).add(intervalId);
    globalDelays && globalDelays.intervals.set(intervalId, {
      fn,
      delay
    });
    return intervalId;
  }
  /**
   * Relays to native requestAnimationFrame and adds to tracking to have call automatically canceled on destroy.
   * @param {Function} fn
   * @param {Object[]} [extraArgs] The argument list to append to those passed to the function.
   * @param {Object} [thisObj] `this` reference for the function.
   * @returns {Number}
   * @internal
   */

  requestAnimationFrame(fn, extraArgs = [], thisObj = this) {
    const animationFrameIds = this.animationFrameIds || (this.animationFrameIds = new Set()),
          frameId = requestAnimationFrame(() => {
      globalDelays && globalDelays.animationFrames.delete(frameId);
      animationFrameIds.delete(frameId);
      return fn.apply(thisObj, extraArgs);
    });
    animationFrameIds.add(frameId);
    globalDelays && globalDelays.animationFrames.set(frameId, {
      fn,
      extraArgs,
      thisObj
    });
    return frameId;
  }
  /**
   * Creates a function which will execute once, on the next animation frame. However many time it is
   * called in one event run, it will only be scheduled to run once.
   * @param {Function|String} fn The function to call, or name of function in this object to call.
   * @param {Object[]} [args] The argument list to append to those passed to the function.
   * @param {Object} [thisObj] `this` reference for the function.
   * @param {Boolean} [cancelOutstanding] Cancel any outstanding queued invocation upon call.
   * @internal
   */

  createOnFrame(fn, args = [], thisObj = this, cancelOutstanding) {
    let rafId;

    const result = (...callArgs) => {
      // Cancel if outstanding if requested
      if (rafId && cancelOutstanding) {
        this.cancelAnimationFrame(rafId);
        rafId = null;
      }

      if (!rafId) {
        rafId = this.requestAnimationFrame(() => {
          if (typeof fn === 'string') {
            fn = thisObj[fn];
          }

          rafId = null;
          callArgs.push(...args);
          fn.apply(thisObj, callArgs);
        });
      }
    };

    result.cancel = () => this.cancelAnimationFrame(rafId);

    return result;
  }
  /**
   * Relays to native cancelAnimationFrame and removes from tracking.
   * @param {Number} handle
   * @internal
   */

  cancelAnimationFrame(handle) {
    cancelAnimationFrame(handle);
    this.animationFrameIds && this.animationFrameIds.delete(handle);
    globalDelays && globalDelays.animationFrames.delete(handle);
  }

  async nextAnimationFrame() {
    return new Promise(resolve => this.requestAnimationFrame(resolve));
  }
  /**
   * Wraps a function with another function that delays it specified amount of time, repeated calls to the wrapper
   * resets delay.
   * @param {Function|String} fn The function to call. If this is a string, it is looked up as a method on `this`
   * instance (or `options.thisObj` instead, if provided).
   * @param {Object|Number} options The delay in milliseconds or an options object.
   * @param {Number} options.delay The delay in milliseconds.
   * @param {Array} [options.appendArgs] The argument list to append to those passed to the function.
   * @param {Object} [options.thisObj] The `this` reference for the function.
   * @returns {Function} Wrapped function to call.
   * @internal
   */

  buffer(fn, options) {
    let delay = options;

    if (options && typeof options !== 'number') {
      // if (config object)
      delay = options.delay;
    } else {
      options = null;
    }

    const bufferWrapFn = (...params) => {
      const {
        delay
      } = bufferWrapFn;
      bufferWrapFn.cancel();
      bufferWrapFn.called = false;
      bufferWrapFn.args = params; // If delay=0, the buffer has been disabled so always call immediately.

      if (bufferWrapFn.immediate || !delay) {
        invoker();
      } else {
        bufferWrapFn.timerId = this.setTimeout(invoker, delay);
      }
    },
          invoker = makeInvoker(this, fn, bufferWrapFn, options);

    bufferWrapFn.delay = delay;
    return decorateWrapFn(this, bufferWrapFn);
  }
  /**
   * Returns a function that when called will schedule a call to `fn` on the next animation frame.
   *
   * @param {Function|String} fn The function to call. If this is a string, it is looked up as a method on `this`
   * instance (or `options.thisObj` instead, if provided).
   * @param {Boolean|Object} [options] An options object or the `cancelOutstanding` boolean property of it.
   * @param {Boolean} [options.cancelOutstanding] Pass `true` to cancel any pending animation frame requests and
   * schedule a new one on each call to the returned function.
   * @param {Array} [options.appendArgs] The argument list to append to those passed to the function.
   * @param {Object} [options.thisObj] The `this` reference for the function.
   * @returns {Function}
   * @internal
   */

  raf(fn, options) {
    // NOTE: This method is only intended for use with `delayable`. It has a signature that is compatible
    // with `buffer()` and `throttle()`. The name is 'raf' to make the following aesthetically pleasing:
    //
    //  class Foo extends Delayable() {
    //      static get delayable() {
    //          return {
    //              bar : 'raf'
    //          };
    //      }
    //  }
    let cancelOutstanding = options;

    if (options && typeof options !== 'boolean') {
      // if (config object)
      cancelOutstanding = options.cancelOutstanding;
    } else {
      options = null;
    }

    const rafWrapFn = (...params) => {
      // Reschedule the frame on each call if requested
      if (rafWrapFn.cancelOutstanding) {
        rafWrapFn.cancel();
      }

      rafWrapFn.called = false;
      rafWrapFn.args = params;

      if (rafWrapFn.immediate) {
        invoker();
      } else if (!rafWrapFn.isPending) {
        rafWrapFn.timerId = this.requestAnimationFrame(invoker);
      }
    },
          invoker = makeInvoker(this, fn, rafWrapFn, options);

    rafWrapFn.cancelOutstanding = cancelOutstanding;
    return decorateWrapFn(this, rafWrapFn, 'cancelAnimationFrame');
  }
  /**
   * Create a "debounced" function which will call on the "leading edge" of a timer period.
   * When first invoked will call immediately, but invocations after that inside its buffer
   * period will be rejected, and *one* invocation will be made after the buffer period has expired.
   *
   * This is useful for responding immediately to a first mousemove, but from then on, only
   * calling the action function on a regular timer while the mouse continues to move.
   *
   * @param {Function|String} fn The function to call. If this is a string, it is looked up as a method on `this`
   * instance (or `options.thisObj` instead, if provided).
   * @param {Number|Object} options The milliseconds to wait after each execution before another execution takes place
   * or a object containing options.
   * @param {Object} [options.thisObj] `this` reference for the function.
   * @param {Array} [options.appendArgs] The argument list to append to those passed to the function.
   * @param {Function|String} [options.throttled] A function to call when the invocation is delayed due to buffer
   * time not having expired. If this is a string, it is looked up as a method on `this` instance (or `options.thisObj`
   * instead, if provided). When called, the same arguments are passed as would have been passed to `fn`, including
   * any `options.appendArgs`.
   * @internal
   */

  throttle(fn, options) {
    let delay = options,
        throttled;

    if (options && typeof options !== 'number') {
      // if (config object)
      delay = options.delay;
      throttled = options.throttled;
    } else {
      options = null;
    }

    const me = this,
          throttleWrapFn = (...args) => {
      const {
        delay
      } = throttleWrapFn,
            elapsed = performance.now() - throttleWrapFn.lastCallTime;
      throttleWrapFn.args = args; // If it's been more then the delay period since we invoked, we can call it now.
      // Setting delay=0 effectively disables the throttle (which is the goal)

      if (throttleWrapFn.immediate || elapsed >= delay) {
        me.clearTimeout(throttleWrapFn.timerId);
        invoker();
      } else {
        // Kick off a timer for the requested period.
        if (!throttleWrapFn.isPending) {
          throttleWrapFn.timerId = me.setTimeout(invoker, delay - elapsed);
          throttleWrapFn.called = false;
        }

        if (throttled) {
          // Args have to be stored on the wrapFn for the invoker to pick them up:
          throttled.wrapFn.args = args;
          throttled();
        }
      }
    },
          invoker = makeInvoker(me, fn, throttleWrapFn, options);

    throttleWrapFn.delay = delay;

    if (throttled) {
      // Make an invoker for this callback to handle thisObj and typeof=string etc (pass a dud wrapFn):
      throttled = makeInvoker(me, throttled, () => {}, options);
    }

    return decorateWrapFn(me, throttleWrapFn);
  }

  static setupDelayable(cls) {
    cls.setupDelayableMethods(cls.delayable);
  }
  /**
   * This method initializes the `delayable` methods on this class.
   * @param {Object} delayable The `delayable` property.
   * @param {Function} [cls] This parameter will be used internally to process static methods.
   * @private
   */

  static setupDelayableMethods(delayable, cls = null) {
    const me = this,
          statics = delayable.static,
          target = cls || me.prototype;

    if (statics) {
      // TODO me.setupDelayableMethods(statics, me);
      delete delayable.static;
    }

    for (const name in delayable) {
      let options = delayable[name];
      const implName = name + 'Now',
            type = typeof options;
      target[implName] = target[name];

      if (type === 'number') {
        options = {
          type: 'buffer',
          delay: options
        };
      } else if (type === 'string') {
        options = {
          type: options
        };
      } // For instance methods, we place a getter on the prototype. When the method is first accessed from the
      // prototype, we create an instance-specific version by calling this.buffer()/throttle() (based on the type
      // desired) and set that as the instance-level property.

      defineProperty$4(target, name, {
        get() {
          const value = this[options.type]((...params) => {
            this[implName](...params);
          }, options);
          defineProperty$4(this, name, {
            value
          });
          return value;
        }

      });
    }
  } // This does not need a className on Widgets.
  // Each *Class* which doesn't need 'b-' + constructor.name.toLowerCase() automatically adding
  // to the Widget it's mixed in to should implement thus.

  get widgetClass() {}

});

/**
 * @module Core/data/stm/state/AutoRecordingState
 */
/**
 * STM recording state class.
 *
 * @internal
 */

class AutoRecordingStateClass extends RecordingStateClass.mixin(Delayable) {
  onDisable(stm) {
    const transaction = stm[TRANSACTION_PROP];
    const timer = stm[TRANSACTION_TIMER_PROP];

    if (timer) {
      this.clearTimeout(timer);
    }

    stm.notifyStoresAboutStateRecordingStop(transaction, {
      disabled: true
    });
    return {
      [STATE_PROP]: 'disabledstate',
      [TRANSACTION_PROP]: null,
      [TRANSACTION_TIMER_PROP]: null
    };
  }

  onAutoRecordOn(stm) {
    throwInvalidMethodCall();
  }

  onAutoRecordOff(stm) {
    const timer = stm[TRANSACTION_TIMER_PROP];

    if (timer) {
      this.clearTimeout(timer);
    }

    return {
      [STATE_PROP]: 'recordingstate',
      [AUTO_RECORD_PROP]: false,
      [TRANSACTION_TIMER_PROP]: null
    };
  }

  onStopTransaction(stm, title) {
    const transaction = stm[TRANSACTION_PROP];
    const timer = stm[TRANSACTION_TIMER_PROP];
    let position = stm[POS_PROP];
    let queue = stm[QUEUE_PROP];

    if (timer) {
      this.clearTimeout(timer);
    }

    if (transaction.length) {
      if (!transaction.title && !title && stm.getTransactionTitle) {
        transaction.title = stm.getTransactionTitle(transaction);
      } else if (title) {
        transaction.title = title;
      }

      queue[position] = transaction;
      queue.length = ++position;
    }

    return [{
      [STATE_PROP]: 'autoreadystate',
      [POS_PROP]: position,
      [TRANSACTION_PROP]: null,
      [TRANSACTION_TIMER_PROP]: null
    }, () => {
      stm.notifyStoresAboutStateRecordingStop(transaction, {
        stop: true
      });
    }];
  }

  onStopTransactionDelayed(stm) {
    let timer = stm[TRANSACTION_TIMER_PROP];

    if (timer) {
      this.clearTimeout(timer);
    }

    timer = this.setTimeout(() => {
      stm.stopTransaction();
    }, stm.autoRecordTransactionStopTimeout);
    return {
      [STATE_PROP]: AutoRecordingState,
      [TRANSACTION_TIMER_PROP]: timer
    };
  }

  onRejectTransaction(stm) {
    const transaction = stm[TRANSACTION_PROP],
          timer = stm[TRANSACTION_TIMER_PROP];

    if (timer) {
      this.clearTimeout(timer);
    }

    return [{
      [STATE_PROP]: 'restoringstate',
      [TRANSACTION_PROP]: null,
      [TRANSACTION_TIMER_PROP]: null
    }, () => {
      if (transaction.length) {
        transaction.undo();
      }

      return ['autoreadystate', () => {
        stm.notifyStoresAboutStateRecordingStop(transaction, {
          rejected: true
        });
      }];
    }];
  }

  onModelUpdate(stm, ...rest) {
    super.onModelUpdate(stm, ...rest);
    stm.stopTransactionDelayed();
  }

  onModelInsertChild(stm, ...rest) {
    super.onModelInsertChild(stm, ...rest);
    stm.stopTransactionDelayed();
  }

  onModelRemoveChild(stm, ...rest) {
    super.onModelRemoveChild(stm, ...rest);
    stm.stopTransactionDelayed();
  }

  onStoreModelAdd(stm, ...rest) {
    super.onStoreModelAdd(stm, ...rest);
    stm.stopTransactionDelayed();
  }

  onStoreModelInsert(stm, ...rest) {
    super.onStoreModelInsert(stm, ...rest);
    stm.stopTransactionDelayed();
  }

  onStoreModelRemove(stm, ...rest) {
    super.onStoreModelRemove(stm, ...rest);
    stm.stopTransactionDelayed();
  }

  onStoreRemoveAll(stm, ...rest) {
    super.onStoreRemoveAll(stm, ...rest);
    stm.stopTransactionDelayed();
  }

}
/**
 * STM recording state.
 *
 * @internal
 */

const AutoRecordingState = new AutoRecordingStateClass();
Registry.registerStmState('autorecordingstate', AutoRecordingState);

/**
 * @module Core/data/stm/action/ActionBase
 */

const throwAbstractMethodCall$1 = () => {
  throw new Error('Abstract method call!');
};
/**
 * Base class for STM actions.
 *
 * @abstract
 */

class ActionBase extends Base {
  /**
   * Gets the type of the action (stringified class name).
   * @readonly
   * @property {String}
   */
  get type() {
    return this.constructor.name;
  }
  /**
   * Undoes an action
   */

  undo() {
    throwAbstractMethodCall$1();
  }
  /**
   * Redoes an action
   */

  redo() {
    throwAbstractMethodCall$1();
  }

}
ActionBase._$name = 'ActionBase';

/**
 * @module Core/data/stm/action/UpdateAction
 */
const MODEL_PROP = Symbol('MODEL_PROP');
const NEW_DATA_PROP = Symbol('NEW_DATA_PROP');
const OLD_DATA_PROP = Symbol('OLD_DATA_PROP');
/**
 * Action to record the fact that a model has been updated.
 * @extends Core/data/stm/action/ActionBase
 */

class UpdateAction extends ActionBase {
  static get defaultConfig() {
    return {
      /**
       * Reference to a model which has been updated.
       *
       * @config {Core.data.Model}
       * @default
       */
      model: undefined,

      /**
       * Map of updated properties with new values.
       *
       * @config {Object}
       * @default
       */
      newData: undefined,

      /**
       * Map of updated properties with old values.
       *
       * @config {Object}
       * @default
       */
      oldData: undefined
    };
  }

  get type() {
    return 'UpdateAction';
  }

  get model() {
    return this[MODEL_PROP];
  }

  set model(value) {
    this[MODEL_PROP] = value;
  }

  get newData() {
    return this[NEW_DATA_PROP];
  }

  set newData(value) {
    this[NEW_DATA_PROP] = Object.assign({}, value);
  }

  get oldData() {
    return this[OLD_DATA_PROP];
  }

  set oldData(value) {
    this[OLD_DATA_PROP] = Object.assign({}, value);
  }

  undo() {
    // it seems STM has to use `model.set()` because of `model.inSet` overrides or smth
    // w/o this call, just with `Object.assign()` below, the view is not refreshed
    // Since invoking accessor will just forward change to the engine, we need to pass `skipAccessors = true`
    // to this call to make this change on data level. Engine will be updated below.
    // Covered by TaskEdit.t `autoSync` subtest
    this.model.set(this.oldData, null, null, null, true); // in the same time, engine needs the setters to be activated, since there's some
    // additional logic (for example, invalidate dispatcher)
    // This will make change in the engine

    if (this.model.$) {
      Object.assign(this.model, this.oldData);
    }
  }

  redo() {
    this.model.set(this.newData, null, null, null, true); // see comments above

    if (this.model.$) {
      Object.assign(this.model, this.newData);
    }
  }

}
UpdateAction._$name = 'UpdateAction';

/**
 * @module Core/data/stm/action/InsertChildAction
 */
const PARENT_MODEL_PROP = Symbol('PARENT_MODEL_PROP');
const CHILD_MODELS_PROP = Symbol('CHILD_MODELS_PROP');
const INSERT_INDEX_PROP = Symbol('INSERT_INDEX_PROP');
const CONTEXT_PROP = Symbol('CONTEXT_PROP');
/**
 * Action to record the fact of adding a children models into a parent model.
 * @extends Core/data/stm/action/ActionBase
 */

class InsertChildAction extends ActionBase {
  static get defaultConfig() {
    return {
      /**
       * Reference to a parent model a child model has been added to.
       *
       * @config {Core.data.Model}
       * @default
       */
      parentModel: undefined,

      /**
       * Children models inserted.
       *
       * @config {Core.data.Model[]}
       * @default
       */
      childModels: undefined,

      /**
       * Index a children models are inserted at
       *
       * @config {Number}
       * @default
       */
      insertIndex: undefined,

      /**
       * Map having children models as keys and values containing previous parent
       * of each model and index at the previous parent.
       *
       * @config {Object}
       * @default
       */
      context: undefined
    };
  }

  get type() {
    return 'InsertChildAction';
  }

  get parentModel() {
    return this[PARENT_MODEL_PROP];
  }

  set parentModel(model) {
    this[PARENT_MODEL_PROP] = model;
  }

  get childModels() {
    return this[CHILD_MODELS_PROP];
  }

  set childModels(models) {
    this[CHILD_MODELS_PROP] = models.slice(0);
  }

  get insertIndex() {
    return this[INSERT_INDEX_PROP];
  }

  set insertIndex(index) {
    this[INSERT_INDEX_PROP] = index;
  }

  get context() {
    return this[CONTEXT_PROP];
  }

  set context(ctx) {
    this[CONTEXT_PROP] = ctx;
  }

  undo() {
    const {
      parentModel,
      context,
      childModels
    } = this; // Let's sort models by parent index such that models with lesser index
    // were inserted back first, thus making valid parent index of models following.

    childModels.sort((lhs, rhs) => {
      const {
        lhsParent,
        lhsIndex
      } = context.get(lhs) || {},
            {
        rhsParent,
        rhsIndex
      } = context.get(rhs) || {};
      return lhsParent && lhsParent === rhsParent ? lhsIndex - rhsIndex : 0;
    }); // Now let's re-insert records back to where they were or remove them
    // if they weren't anywhere

    childModels.forEach(m => {
      const {
        parent,
        index
      } = context.get(m) || {};

      if (parent) {
        // If we move within same parent then index must be adjusted
        if (parent === parentModel) {
          let adjustedIndex; // we need to increment index if the node was moved up

          adjustedIndex = index > m.parentIndex ? index + 1 : index; // null index if we need to move to the end

          adjustedIndex = index === parent.children.length - 1 ? null : adjustedIndex; // if insertBefore is undefined the node will be appended to the end

          const insertBefore = parent.children[adjustedIndex];
          parent.insertChild(m, insertBefore);
        } else {
          // Insert at previous index
          parent.insertChild(m, parent.children[index]);
        }
      } else {
        // Just remove
        parentModel.removeChild(m);
      }
    });
  }

  redo() {
    const {
      parentModel,
      insertIndex,
      childModels
    } = this,
          insertBefore = parentModel.children[insertIndex];
    parentModel.insertChild(childModels, insertBefore);
  }

}
InsertChildAction._$name = 'InsertChildAction';

/**
 * @module Core/data/stm/action/RemoveChildAction
 */
const PARENT_MODEL_PROP$1 = Symbol('PARENT_MODEL_PROP');
const CHILD_MODELS_PROP$1 = Symbol('CHILD_MODELS_PROP');
const CONTEXT_PROP$1 = Symbol('CONTEXT_PROP');
/**
 * Action to record store remove child operation.
 * @extends Core/data/stm/action/ActionBase
 */

class RemoveChildAction extends ActionBase {
  static get defaultConfig() {
    return {
      /**
       * Reference to a parent model a child model has been removed to.
       *
       * @config {Core.data.Model}
       * @default
       */
      parentModel: undefined,

      /**
       * Children models removed.
       *
       * @config {Core.data.Model[]}
       * @default
       */
      childModels: undefined,

      /**
       * Map having children models as keys and values containing previous parent
       * index at the parent.
       *
       * @config {Object}
       * @default
       */
      context: undefined
    };
  }

  get type() {
    return 'RemoveChildAction';
  }

  get parentModel() {
    return this[PARENT_MODEL_PROP$1];
  }

  set parentModel(model) {
    this[PARENT_MODEL_PROP$1] = model;
  }

  get childModels() {
    return this[CHILD_MODELS_PROP$1];
  }

  set childModels(models) {
    this[CHILD_MODELS_PROP$1] = models.slice(0);
  }

  get context() {
    return this[CONTEXT_PROP$1];
  }

  set context(ctx) {
    this[CONTEXT_PROP$1] = ctx;
  }

  undo() {
    const {
      parentModel,
      context,
      childModels
    } = this; // Let's sort models by parent index such that models with lesser index
    // were inserted back first, thus making valid parent index of models following.

    childModels.sort((lhs, rhs) => {
      const lhsIndex = context.get(lhs),
            rhsIndex = context.get(rhs);
      return lhsIndex - rhsIndex;
    }); // Now let's re-insert records back to where they were

    childModels.forEach(m => {
      parentModel.insertChild(context.get(m), m);
    });
  }

  redo() {
    this.parentModel.removeChild(this.childModels);
  }

}
RemoveChildAction._$name = 'RemoveChildAction';

/**
 * @module Core/data/stm/action/AddAction
 */

const STORE_PROP = Symbol('STORE_PROP');
const MODEL_LIST_PROP = Symbol('MODEL_LIST_PROP');
/**
 * Action to record the fact of models adding to a store.
 * @extends Core/data/stm/action/ActionBase
 */

class AddAction extends ActionBase {
  static get defaultConfig() {
    return {
      /**
       * Reference to a store models have been added into.
       *
       * @config {Core.data.Store}
       * @default
       */
      store: undefined,

      /**
       * List of models added into the store.
       *
       * @config {Core.data.Model[]}
       * @default
       */
      modelList: undefined,

      /**
       * Flag showing if undo/redo should be done silently i.e. with events suppressed
       *
       * @config {Boolean}
       * @default
       */
      silent: false
    };
  }

  get type() {
    return 'AddAction';
  }

  get store() {
    return this[STORE_PROP];
  }

  set store(store) {
    this[STORE_PROP] = store;
  }

  get modelList() {
    return this[MODEL_LIST_PROP];
  }

  set modelList(list) {
    this[MODEL_LIST_PROP] = list.slice(0);
  }

  undo() {
    const me = this;
    me.store.remove(me.modelList, me.silent);
  }

  redo() {
    const me = this;
    me.store.add(me.modelList, me.silent);
  }

}
AddAction._$name = 'AddAction';

/**
 * @module Core/data/stm/action/InsertAction
 */

const STORE_PROP$1 = Symbol('STORE_PROP');
const MODEL_LIST_PROP$1 = Symbol('MODEL_LIST_PROP');
const INSERT_INDEX_PROP$1 = Symbol('INSERT_INDEX_PROP');
const CONTEXT_PROP$2 = Symbol('CONTEXT_PROP');
/**
 * Action to record the fact of models inserting into a store.
 * @extends Core/data/stm/action/ActionBase
 */

class InsertAction extends ActionBase {
  static get defaultConfig() {
    return {
      /**
       * Reference to a store models have been inserted into.
       *
       * @config {Core.data.Store}
       * @default
       */
      store: undefined,

      /**
       * List of models inserted into the store.
       *
       * @config {Core.data.Model[]}
       * @default
       */
      modelList: undefined,

      /**
       * Index the models have been inserted at.
       *
       * @config {Number}
       * @default
       */
      insertIndex: undefined,

      /**
       * Models move context (if models has been moved), if any.
       * Map this {@link Core/data/Model} instances as keys and their
       * previous index as values
       *
       * @config {Map}
       * @default
       */
      context: undefined,

      /**
       * Flag showing if undo/redo should be done silently i.e. with events suppressed
       *
       * @config {Boolean}
       * @default
       */
      silent: false
    };
  }

  get type() {
    return 'InsertAction';
  }

  get store() {
    return this[STORE_PROP$1];
  }

  set store(store) {
    this[STORE_PROP$1] = store;
  }

  get modelList() {
    return this[MODEL_LIST_PROP$1];
  }

  set modelList(list) {
    this[MODEL_LIST_PROP$1] = list.slice(0);
  }

  get insertIndex() {
    return this[INSERT_INDEX_PROP$1];
  }

  set insertIndex(index) {
    this[INSERT_INDEX_PROP$1] = index;
  }

  get context() {
    return this[CONTEXT_PROP$2];
  }

  set context(context) {
    this[CONTEXT_PROP$2] = context;
  }

  undo() {
    const {
      store,
      modelList,
      context,
      silent
    } = this; // Let's sort models by index such that models with lesser index
    // were inserted back first, thus making valid index of models following.

    modelList.sort((lhs, rhs) => {
      const lhsIndex = context.get(lhs),
            rhsIndex = context.get(rhs);
      return lhsIndex !== undefined && rhsIndex !== undefined ? lhsIndex - rhsIndex : 0;
    });
    modelList.forEach(m => {
      const index = context.get(m); // Flag the inserted record that we undo to skip adding it to "store.removed"

      m._undoingInsertion = true;

      if (index !== undefined) {
        // Insert at previous index
        store.insert(index, m, silent);
      } else {
        // Just remove
        store.remove(m, silent);
      }

      m._undoingInsertion = false;
    });
  }

  redo() {
    const me = this;
    me.store.insert(me.insertIndex, me.modelList, me.silent);
  }

}
InsertAction._$name = 'InsertAction';

/**
 * @module Core/data/stm/action/RemoveAction
 */
const STORE_PROP$2 = Symbol('STORE_PROP');
const MODEL_LIST_PROP$2 = Symbol('MODEL_LIST_PROP');
const CONTEXT_PROP$3 = Symbol('CONTEXT_PROP');
/**
 * Action to record the fact of models removed from a store.
 * @extends Core/data/stm/action/ActionBase
 */

class RemoveAction extends ActionBase {
  static get defaultConfig() {
    return {
      /**
       * Reference to a store models have been removed from.
       *
       * @config {Core.data.Store}
       * @default
       */
      store: undefined,

      /**
       * List of models removed from the store.
       *
       * @config {Core.data.Model[]}
       * @default
       */
      modelList: undefined,

      /**
       * Models removing context.
       *
       * @config {Object}
       * @default
       */
      context: undefined,

      /**
       * Flag showing if undo/redo should be done silently i.e. with events suppressed
       *
       * @config {Boolean}
       * @default
       */
      silent: false
    };
  }

  get type() {
    return 'RemoveAction';
  }

  get store() {
    return this[STORE_PROP$2];
  }

  set store(store) {
    this[STORE_PROP$2] = store;
  }

  get modelList() {
    return this[MODEL_LIST_PROP$2];
  }

  set modelList(list) {
    this[MODEL_LIST_PROP$2] = list.slice(0);
  }

  get context() {
    return this[CONTEXT_PROP$3];
  }

  set context(context) {
    this[CONTEXT_PROP$3] = context;
  }

  undo() {
    const {
      store,
      context,
      modelList,
      silent
    } = this; // Let's sort models by index such that models with lesser index
    // were inserted back first, thus making valid index of models following.

    modelList.sort((lhs, rhs) => {
      const lhsIndex = context.get(lhs),
            rhsIndex = context.get(rhs); // Here, in contrast to InsertAction, index is always present

      return lhsIndex - rhsIndex;
    });
    modelList.forEach(m => {
      const index = context.get(m); // Insert at previous index

      store.insert(index, m, silent);
    });
  }

  redo() {
    const me = this;
    me.store.remove(me.modelList, me.silent);
  }

}
RemoveAction._$name = 'RemoveAction';

/**
 * @module Core/data/stm/action/RemoveAllAction
 */
const STORE_PROP$3 = Symbol('STORE_PROP');
const ALL_RECORDS_PROP = Symbol('ALL_RECORDS_PROP');
/**
 * Action to record store remove all operation.
 * @extends Core/data/stm/action/ActionBase
 */

class RemoveAllAction extends ActionBase {
  static get defaultConfig() {
    return {
      /**
       * Reference to a store cleared.
       *
       * @config {Core.data.Store}
       * @default
       */
      store: undefined,

      /**
       * All store records removed
       *
       * @config {Core.data.Model[]}
       * @default
       */
      allRecords: undefined,

      /**
       * Flag showing if undo/redo should be done silently i.e. with events suppressed
       *
       * @config {Boolean}
       * @default
       */
      silent: false
    };
  }

  get type() {
    return 'RemoveAllAction';
  }

  get store() {
    return this[STORE_PROP$3];
  }

  set store(store) {
    this[STORE_PROP$3] = store;
  }

  get allRecords() {
    return this[ALL_RECORDS_PROP];
  }

  set allRecords(records) {
    this[ALL_RECORDS_PROP] = records.slice(0);
  }

  undo() {
    const {
      store,
      allRecords,
      silent
    } = this;
    store.add(allRecords, silent);
  }

  redo() {
    const me = this;
    me.store.removeAll(me.silent);
  }

}
RemoveAllAction._$name = 'RemoveAllAction';

/**
 * @module Core/data/stm/StateTrackingManager
 */
const makeModelUpdateAction = (model, newData, oldData) => {
  return new UpdateAction({
    model,
    newData,
    oldData
  });
};
const makeModelInsertChildAction = (parentModel, insertIndex, childModels, context) => {
  return new InsertChildAction({
    parentModel,
    childModels,
    insertIndex,
    context
  });
};
const makeModelRemoveChildAction = (parentModel, childModels, context) => {
  return new RemoveChildAction({
    parentModel,
    childModels,
    context
  });
};
const makeStoreModelAddAction = (store, modelList, silent) => {
  return new AddAction({
    store,
    modelList,
    silent
  });
};
const makeStoreModelInsertAction = (store, insertIndex, modelList, context, silent) => {
  return new InsertAction({
    store,
    insertIndex,
    modelList,
    context,
    silent
  });
};
const makeStoreModelRemoveAction = (store, modelList, context, silent) => {
  return new RemoveAction({
    store,
    modelList,
    context,
    silent
  });
};
const makeStoreRemoveAllAction = (store, allRecords, silent) => {
  return new RemoveAllAction({
    store,
    allRecords,
    silent
  });
};

const stateTransition = (stm, event, ...args) => {
  const newState = event.call(stm[STATE_PROP], stm, ...args);

  if (typeof newState === 'string') {
    stm[STATE_PROP] = Registry.resolveStmState(newState);
  } else if (newState instanceof StateBase) {
    stm[STATE_PROP] = newState;
  } else if (Array.isArray(newState)) {
    const [state, next] = newState;

    if (typeof state === 'string') {
      stm[STATE_PROP] = Registry.resolveStmState(state);
    } else if (state instanceof StateBase) {
      stm[STATE_PROP] = state;
    } else if (state && typeof state === 'object') {
      stm = Object.assign(stm, state);
      stm[STATE_PROP] = Registry.resolveStmState(stm[STATE_PROP]);
    }

    if (typeof next === 'function') {
      stateTransition(stm, next, ...args);
    }
  } else if (newState && typeof newState === 'object') {
    stm = Object.assign(stm, newState);
    stm[STATE_PROP] = Registry.resolveStmState(stm[STATE_PROP]);
  }
};
/**
 * When enabled tracks state of every store registered via {@link #function-addStore}.
 * Use {@link #function-undo} / {@link #function-redo} method calls to restore state to a particular
 * point in time
 *
 * ```javascript
 * stm = new StateTrackingManager({
 *     autoRecord : true,
 *     listeners  : {
 *        'recordingstop' : () => {
 *            // your custom code to update undo/redo GUI controls
 *            updateUndoRedoControls();
 *        },
 *        'restoringstop' : ({ stm }) => {
 *            // your custom code to update undo/redo GUI controls
 *            updateUndoRedoControls();
 *        }
 *    },
 *    getTransactionTitle : (transaction) => {
 *        // your custom code to analyze the transaction and return custom transaction title
 *        const lastAction = transaction.queue[transaction.queue.length - 1];
 *
 *        if (lastAction instanceof AddAction) {
 *            let title = 'Add new record';
 *        }
 *
 *        return title;
 *    }
 * });
 *
 * stm.addStore(userStore);
 * stm.addStore(companyStore);
 * stm.addStore(otherStore);
 *
 * stm.enable();
 * ```
 *
 * **Note:** STM currently does not support undoing the added and saved records.
 * Therefore it's recommended to {@link #function-resetQueue reset the queue}
 * each time a tracked store(s) loads from or saves its changes to the server.
 * If Crud Manager is used it can be done like this:
 * ```javascript
 * crudManager.on({
 *     requestDone() {
 *         stm.resetQueue();
 *     }
 * });
 * ```
 * and in case individual stores are used:
 * ```javascript
 * ajaxStore.on({
 *     afterRequest({ exception }) {
 *         if (!exception) {
 *             stm.resetQueue();
 *         }
 *     }
 * });
 * ```
 *
 * @mixes Core/mixin/Events
 * @extends Core/Base
 */

class StateTrackingManager extends Events(Base) {
  static get defaultConfig() {
    return {
      /**
       * Default manager disabled state
       *
       * @config {Boolean}
       * @default
       */
      disabled: true,

      /**
       * Whether to start transaction recording automatically in case the Manager is enabled.
       *
       * In the auto recording mode, the manager waits for the first change in any store being managed and starts a transaction, i.e.
       * records any changes in its monitored stores. The transaction lasts for {@link #config-autoRecordTransactionStopTimeout} and
       * afterwards creates one undo/redo step, including all changes in the stores during that period of time.
       *
       * In non auto recording mode you have to call {@link #function-startTransaction} / {@link #function-stopTransaction} to start and end
       * a transaction.
       *
       * @config {Boolean}
       * @default
       */
      autoRecord: false,

      /**
       * The transaction duration (in ms) for the auto recording mode {@link #config-autoRecord}
       *
       * @config {Number}
       * @default
       */
      autoRecordTransactionStopTimeout: 100,

      /**
       * Store model update action factory
       *
       * @config {Function}
       * @default
       * @private
       */
      makeModelUpdateAction: makeModelUpdateAction,

      /**
       * Store insert child model action factory.
       *
       * @config {Function}
       * @default
       * @private
       */
      makeModelInsertChildAction: makeModelInsertChildAction,

      /**
       * Store remove child model action factory.
       *
       * @config {Function}
       * @default
       * @private
       */
      makeModelRemoveChildAction: makeModelRemoveChildAction,

      /**
       * Store add model action factory.
       *
       * @config {Function}
       * @default
       * @private
       */
      makeStoreModelAddAction: makeStoreModelAddAction,

      /**
       * Store insert model action factory.
       *
       * @config {Function}
       * @default
       * @private
       */
      makeStoreModelInsertAction: makeStoreModelInsertAction,

      /**
       * Store remove model action factory.
       *
       * @config {Function}
       * @default
       * @private
       */
      makeStoreModelRemoveAction: makeStoreModelRemoveAction,

      /**
       * Store remove all models action factory.
       *
       * @config {Function}
       * @default
       * @private
       */
      makeStoreRemoveAllAction: makeStoreRemoveAllAction,

      /**
       * Function to create a transaction title if none is provided.
       *
       * The function receives a transaction and should return a title.
       *
       * @config {Function}
       * @default
       */
      getTransactionTitle: null
    };
  }

  construct(...args) {
    const me = this;
    Object.assign(me, {
      [STATE_PROP]: ReadyState,
      [STORES_PROP]: [],
      [QUEUE_PROP]: [],
      [POS_PROP]: 0,
      [TRANSACTION_PROP]: null,
      [TRANSACTION_TIMER_PROP]: null,
      [AUTO_RECORD_PROP]: false
    });
    super.construct(...args);
  } // TODO: This destroys the current state, which makes successive tests fail. Not sure what should be done
  // doDestroy() {
  //     super.doDestroy();
  //     this.destroyProperties(STATE_PROP);
  // }

  /**
   * Gets current state of the manager
   *
   * @property {Core.data.stm.state.StateBase}
   */

  get state() {
    return this[STATE_PROP];
  }
  /**
   * Gets current undo/redo queue position
   *
   * @property {Number}
   */

  get position() {
    return this[POS_PROP];
  }
  /**
   * Gets current undo/redo queue length
   *
   * @property {Number}
   */

  get length() {
    return this[QUEUE_PROP].length;
  }
  /**
   * Gets all the stores registered in STM
   *
   * @property {Core.data.Store[]}
   */

  get stores() {
    return Array.from(this[STORES_PROP]);
  }
  /**
   * Checks if a store has been added to the manager
   *
   * @param  {Core.data.Store} store
   * @return {Boolean}
   */

  hasStore(store) {
    return this[STORES_PROP].includes(store);
  }
  /**
   * Adds a store to the manager
   *
   * @param {Core.data.Store} store
   */

  addStore(store) {
    if (!this.hasStore(store)) {
      this[STORES_PROP].push(store);
      store.stm = this;
    }
  }
  /**
   * Removes a store from the manager
   *
   * @param {Core.data.Store} store
   */

  removeStore(store) {
    if (this.hasStore(store)) {
      this[STORES_PROP] = this[STORES_PROP].filter(s => s !== store);
      store.stm = null;
    }
  }
  /**
   * Calls `fn` for each store registered in STM.
   *
   * @param {Function} fn (store, id) => ...
   */

  forEachStore(fn) {
    this[STORES_PROP].forEach(s => fn(s, s.id));
  }
  /**
   * Get/set manager disabled state
   *
   * @property {Boolean}
   */

  get disabled() {
    return this.state === DisabledState;
  }

  set disabled(val) {
    const me = this;

    if (me.disabled != val) {
      if (val) {
        stateTransition(me, me.state.onDisable, me);
      } else {
        stateTransition(me, me.state.onEnable, me);
      }

      me.trigger('stmDisabled', {
        disabled: val
      });
      /**
       * Fired when the disabled state of the STM changes
       *
       * @event disabled
       * @param {Core.data.stm.StateTrackingManager} source
       * @param {Boolean} disabled The current disabled state of the STM
       */

      me.trigger('disabled', {
        disabled: val
      });
    }
  }
  /**
   * Enables manager
   */

  enable() {
    this.disabled = false;
  }
  /**
   * Disables manager
   */

  disable() {
    this.disabled = true;
  }
  /**
   * Checks manager ready state
   * @readonly
   * @property {Boolean}
   */

  get isReady() {
    return this.state === ReadyState || this.state === AutoReadyState;
  }
  /**
   * Checks manager recording state
   * @readonly
   * @property {Boolean}
   */

  get isRecording() {
    return this.state === RecordingState || this.state === AutoRecordingState;
  }
  /**
   * Gets/sets manager auto record option
   *
   * @property {Boolean}
   */

  get autoRecord() {
    return this[AUTO_RECORD_PROP];
  }

  set autoRecord(value) {
    const me = this;

    if (me.autoRecord != value) {
      if (value) {
        stateTransition(me, me.state.onAutoRecordOn, me);
      } else {
        stateTransition(me, me.state.onAutoRecordOff, me);
      }
    }
  }
  /**
   * Starts undo/redo recording transaction.
   *
   * @param {String} [title=nul]
   */

  startTransaction(title = null) {
    stateTransition(this, this.state.onStartTransaction, title);
  }
  /**
   * Stops undo/redo recording transaction
   *
   * @param {String} [title=null]
   */

  stopTransaction(title = null) {
    stateTransition(this, this.state.onStopTransaction, title);
  }
  /**
   * Stops undo/redo recording transaction after {@link #config-autoRecordTransactionStopTimeout} delay.
   *
   * @private
   */

  stopTransactionDelayed() {
    stateTransition(this, this.state.onStopTransactionDelayed);
  }
  /**
   * Rejects currently recorded transaction.
   */

  rejectTransaction() {
    stateTransition(this, this.state.onRejectTransaction);
  }
  /**
   * Gets currently recording STM transaction.
   * @readonly
   * @property {Core.data.stm.Transaction}
   */

  get transaction() {
    return this[TRANSACTION_PROP];
  }
  /**
   * Gets titles of all recorded undo/redo transactions
   * @readonly
   * @property {String[]}
   */

  get queue() {
    return this[QUEUE_PROP].map(t => t.title);
  }
  /**
   * Gets manager restoring state.
   * @readonly
   * @property {Boolean}
   */

  get isRestoring() {
    return this.state === RestoringState;
  }
  /**
   * Checks if the manager can undo.
   *
   * @property {Boolean}
   */

  get canUndo() {
    return this.state.canUndo(this);
  }
  /**
   * Checks if the manager can redo.
   *
   * @property {Boolean}
   */

  get canRedo() {
    return this.state.canRedo(this);
  }
  /**
   * Undoes current undo/redo transaction.
   * @param {Number} [steps=1]
   */

  undo(steps = 1) {
    stateTransition(this, this.state.onUndo, steps);
  }
  /**
   * Undoes all transactions.
   */

  undoAll() {
    this.undo(this.length);
  }
  /**
   * Redoes current undo/redo transaction.
   *
   * @param {Number} [steps=1]
   */

  redo(steps = 1) {
    stateTransition(this, this.state.onRedo, steps);
  }
  /**
   * Redoes all transactions.
   */

  redoAll() {
    this.redo(this.length);
  }
  /**
   * Resets undo/redo queue.
   */

  resetQueue(
  /* private */
  options = {
    undo: true,
    redo: true
  }) {
    stateTransition(this, this.state.onResetQueue, options);
  }
  /**
   * Resets undo queue.
   */

  resetUndoQueue() {
    this.resetQueue({
      undo: true
    });
  }
  /**
   * Resets redo queue.
   */

  resetRedoQueue() {
    this.resetQueue({
      redo: true
    });
  }

  notifyStoresAboutStateRecordingStart(transaction) {
    this.forEachStore(store => {
      store.onStmRecordingStart && store.onStmRecordingStart(this, transaction);
    });
    /**
     * Fired upon state recording operation starts.
     *
     * @event recordingStart
     * @param {Core.data.stm.StateTrackingManager} stm
     * @param {Core.data.stm.Transaction} transaction
     */

    this.trigger('recordingStart', {
      stm: this,
      transaction
    });
  }

  notifyStoresAboutStateRecordingStop(transaction, reason) {
    this.forEachStore(store => {
      store.onStmRecordingStop && store.onStmRecordingStop(this, transaction, reason);
    });
    /**
     * Fired upon state recording operation stops.
     *
     * @event recordingStop
     * @param {Core.data.stm.StateTrackingManager} stm
     * @param {Core.data.stm.Transaction} transaction
     * @param {Object} reason Transaction stop reason
     * @param {Boolean} reason.stop Transaction recording has been stopped in a normal way.
     * @param {Boolean} reason.disabled Transaction recording has been stopped due to STM has been disabled.
     * @param {Boolean} reason.rejected Transaction recording has been stopped due to transaction has been rejected.
     */

    this.trigger('recordingStop', {
      stm: this,
      transaction,
      reason
    });
  }

  notifyStoresAboutStateRestoringStart() {
    this.forEachStore(store => {
      store.onStmRestoringStart && store.onStmRestoringStart(this);
    });
    /**
     * Fired upon state restoration operation starts.
     *
     * @event restoringStart
     * @param {Core.data.stm.StateTrackingManager} stm
     */

    this.trigger('restoringStart', {
      stm: this
    });
  }

  notifyStoresAboutStateRestoringStop() {
    this.forEachStore(store => {
      store.onStmRestoringStop && store.onStmRestoringStop(this);
    });
    /**
     * Fired upon state restoration operation stops.
     *
     * @event restoringStop
     * @param {Core.data.stm.StateTrackingManager} stm
     */

    this.trigger('restoringStop', {
      stm: this
    });
  }

  notifyStoresAboutQueueReset(options) {
    this.forEachStore(store => {
      store.onStmQueueReset && store.onStmQueueReset(this, options);
    });
    /**
     * Fired upon state undo/redo queue reset.
     *
     * @event queueReset
     * @param {Core.data.stm.StateTrackingManager} stm
     */

    this.trigger('queueReset', {
      stm: this,
      options
    });
  }
  /**
   * Method to call from model STM mixin upon model update
   *
   * @param {Core.data.Model} model
   * @param {Object} newData
   * @param {Object} oldData
   *
   * @private
   */

  onModelUpdate(model, newData, oldData) {
    stateTransition(this, this.state.onModelUpdate, model, newData, oldData);
  }
  /**
   * Method to call from model STM mixin upon tree model child insertion
   *
   * @param {Core.data.Model} parentModel Parent model
   * @param {Number} index Insertion index
   * @param {Core.data.Model[]} childModels Array of models inserted
   * @param {Map} context Map with inserted models as keys and objects with previous parent,
   *                      and index at previous parent.
   *
   * @private
   */

  onModelInsertChild(parentModel, index, childModels, context) {
    stateTransition(this, this.state.onModelInsertChild, parentModel, index, childModels, context);
  }
  /**
   * Method to call from model STM mixin upon tree model child removal
   *
   * @param {Core.data.Model} parentModel
   * @param {Core.data.Model[]} childModels
   * @param {Map} context
   *
   * @private
   */

  onModelRemoveChild(parentModel, childModels, context) {
    stateTransition(this, this.state.onModelRemoveChild, parentModel, childModels, context);
  }
  /**
   * Method to call from store STM mixin upon store models adding
   *
   * @param {Core.data.Store} store
   * @param {Core.data.Model[]} models
   * @param {Boolean} silent
   *
   * @private
   */

  onStoreModelAdd(store, models, silent) {
    stateTransition(this, this.state.onStoreModelAdd, store, models, silent);
  }
  /**
   * Method to call from store STM mixin upon store models insertion
   *
   * @param {Core.data.Store} store
   * @param {Number} index
   * @param {Core.data.Model[]} models
   * @param {Map} context
   * @param {Boolean} silent
   *
   * @private
   */

  onStoreModelInsert(store, index, models, context, silent) {
    stateTransition(this, this.state.onStoreModelInsert, store, index, models, context, silent);
  }
  /**
   * Method to call from store STM mixin upon store models removal
   *
   * @param {Core.data.Store} store
   * @param {Core.data.Model[]} models
   * @param {Object} context
   * @param {Boolean} silent
   *
   * @private
   */

  onStoreModelRemove(store, models, context, silent) {
    stateTransition(this, this.state.onStoreModelRemove, store, models, context, silent);
  }
  /**
   * Method to call from store STM mixin upon store clear
   *
   * @param {Core.data.Store} store
   * @param {Core.data.Model[]} allRecords
   * @param {Boolean} silent
   *
   * @private
   */

  onStoreRemoveAll(store, allRecords, silent) {
    stateTransition(this, this.state.onStoreRemoveAll, store, allRecords, silent);
  }

}
StateTrackingManager._$name = 'StateTrackingManager';

/**
 * @module Core/helper/AsyncHelper
 */

/**
 * A helper class to make asynchronous tasks `await` friendly.
 */
class AsyncHelper {
  /**
   * Returns a promise that resolves on next animation frame.
   * ```
   *  async method() {
   *      // do work
   *      await AsyncHelper.animationFrame();
   *      // do more work
   *  }
   * ```
   * @returns {Promise}
   */
  static animationFrame() {
    return new Promise(resolve => {
      requestAnimationFrame(resolve);
    });
  }
  /**
   * Returns a promise that resolves after a specified number of milliseconds.
   * ```
   *  async method() {
   *      await AsyncHelper.sleep(10);
   *      // ...
   *  }
   * ```
   * @param {Number} millis The number of milliseconds to sleep.
   * @returns {Promise}
   */

  static sleep(millis) {
    return new Promise(resolve => {
      setTimeout(() => {
        resolve();
      }, millis);
    });
  }
  /**
   * Returns a promise that resolves as soon as possible, allowing the browser to minimally process other messages.
   * This is the shortest possible delay the browser offers, so be aware that it does not necessarily allow the
   * browser to paint or reflow if used in a long loop. It does, however, allow other async methods to execute.
   * ```
   *  async method() {
   *      await AsyncHelper.yield();
   *      // ...
   *  }
   * ```
   * @returns {Promise}
   */

  static yield() {
    return Promise.resolve();
  }

}
AsyncHelper._$name = 'AsyncHelper';

/**
 * @module Core/helper/CSSHelper
 */

/**
 * Provides methods to add and manipulate CSS style rules.
 *
 * Note that this class is incompatible with [CSP](https://developer.mozilla.org/en-US/docs/Web/HTTP/CSP)
 *
 * ```
 * this.criticalRule = CSSHelper.insertRule(`#${this.id} .b-sch-event.critical {background-color:${this.criticalColor}}`);
 * ```
 */

class CSSHelper {
  /**
   * Inserts a CSS style rule based upon the passed text
   * @param {String} cssText The text of the rule including selector and rule body just as it would
   * be specified in a CSS file.
   * @returns {CSSRule} The resulting CSS Rule object if the add was successful.
   */
  static insertRule(cssText) {
    const {
      styleSheet
    } = this,
          oldCount = styleSheet.cssRules.length;
    styleSheet.insertRule(cssText, 0); // Only return element zero if the add was successful.

    if (styleSheet.cssRules.length > oldCount) {
      return styleSheet.cssRules[0];
    }
  }
  /**
   * Looks up the first rule which matched the passed selector.
   * @param {String|Function} selector Either the selector string to exactly match or a function which
   * when passed a required selector, returns `true`.
   * @returns {CSSRule} The first matching CSS Rule object if any found.
   */

  static findRule(selector) {
    let result,
        isFn = typeof selector === 'function'; // Array#find will stop when the function returns true, stop when the inner
    // find call yields a value from the search string.
    // Array#find better: to http://www.andygup.net/fastest-way-to-find-an-item-in-a-javascript-array/

    Array.prototype.find.call(document.head.querySelectorAll('link[rel=stylesheet],style[type*=css]'), element => {
      result = Array.prototype.find.call(element.sheet.rules || element.sheet.cssRules, r => {
        return isFn ? selector(r) : r.selectorText === selector;
      });

      if (result) {
        return true;
      }
    });
    return result;
  }

  static get styleSheet() {
    if (!this._stylesheet) {
      this._stylesheet = DomHelper.createElement({
        tag: 'style',
        id: 'bryntum-private-styles',
        type: 'text/css',
        parent: document.head
      }).sheet;
    }

    return this._stylesheet;
  }

}
CSSHelper._$name = 'CSSHelper';

/**
 * @module Core/helper/DomSync
 */

const arraySlice$1 = Array.prototype.slice,
      htmlRe$1 = /[&<]/,
      // tests if setInnerText is equivalent to innerHTML
// Used in sync to give ObjectHelper.isDeeplyEqual() some domain knowledge
syncEqualityEvaluator = {
  // Attributes used during creation that should not be compared
  ignore: {
    _element: 1,
    parent: 1,
    elementData: 1,
    ns: 1
  },

  // Function to evaluate 'compareHtml' property instead of 'html' for DocumentFragments
  evaluate(property, a, b) {
    if (property === 'html' && typeof a.value !== 'string' && `compareHtml` in a.object) {
      // DocumentFragment, compare separately supplied html
      return a.object.compareHtml === b.object.compareHtml;
    }
  }

},
      // Attributes to ignore on sync
syncIgnoreAttributes = {
  tag: 1,
  html: 1,
  text: 1,
  children: 1,
  tooltip: 1,
  parent: 1,
  nextSibling: 1,
  ns: 1,
  reference: 1,
  _element: 1,
  elementData: 1,
  retainElement: 1,
  compareHtml: 1,
  syncOptions: 1,
  listeners: 1,
  isReleased: 1
},
      isClass = {
  class: 1,
  className: 1,
  classname: 1
},
      emptyArray$4 = Object.freeze([]);
/**
 * A utility class for syncing DOM config objects to DOM elements. Syncing compares the new config with the previously
 * used for that element, only applying the difference. Very much like a virtual DOM approach on a per element basis
 * (element + its children).
 *
 * Usage example:
 *
 * ```javascript
 * DomSync.sync({
 *     domConfig: {
 *         className : 'b-outer',
 *         children : [
 *             {
 *                 className : 'b-child',
 *                 html      : 'Child 1',
 *                 dataset   : {
 *                     custom : true
 *                 }
 *             },
 *             {
 *                 className : 'b-child',
 *                 html      : 'Child 2',
 *                 style     : {
 *                     fontWeight : 'bold',
 *                     color      : 'blue'
 *                 }
 *             }
 *         ]
 *     },
 *     targetElement : target
 * });
 * ```
 */

class DomSync {
  /**
   * Sync a DOM config to a target element
   * @param {Object} options Options object
   * @param {Object} options.domConfig A DOM config object
   * @param {HTMLElement} options.targetElement Target element to apply to
   * @param {Boolean} options.strict Specify `true` to limit synchronization to only the values set by previous calls.
   * Styles and classes placed directly on the DOM elements by other means will not be affected.
   * @param {String} [options.syncIdField] Field in dataset to use to match elements for re-usage
   * @param {String|String[]} [options.affected] The references affected by a partial sync.
   * @param {Function} [options.callback] A function that will be called on element re-usage, creation and similar
   * @param {Boolean} [options.configEquality] A function that will be called to compare an incoming config to
   * the last config applied to the `targetElement`. Defaults to {@link Core.helper.ObjectHelper#function-isDeeplyEqual-static isDeeplyEqual}.
   * @returns {HTMLElement} Returns the updated target element (which is also updated in place)
   */
  static sync(options) {
    const refOwner = options.refOwner,
          refsWas = refOwner && refOwner.byRef;
    let affected = options.affected,
        i,
        ref,
        targetNode,
        lastDomConfig;

    if (typeof affected === 'string') {
      affected = [affected];
    }

    if (refOwner) {
      // We always rebuild the byRef map on each call
      refOwner.byRef = {};

      if (affected) {
        // We need to preserve all previously rendered refs that are not going to be affected by this partial
        // update...
        for (ref in refsWas) {
          if (!affected.includes(ref)) {
            refOwner.byRef[ref] = refsWas[ref];
          }
        }
      }

      options = Object.assign({
        refsWas
      }, options);
    } // performSync() returns false if nothing was done because the configs were equal...

    if (this.performSync(options, options.targetElement) && refOwner) {
      if (!affected) {
        affected = Object.keys(refsWas);
      }

      for (i = 0; i < affected.length; ++i) {
        ref = affected[i];

        if (!(ref in refOwner.byRef)) {
          targetNode = refsWas[ref]; // If we are doing a partial update, the list of affected refs may contain refs that were not
          // rendered last time...

          if (targetNode) {
            lastDomConfig = targetNode.lastDomConfig;
            targetNode.remove();
            refOwner.detachRef(ref, targetNode, lastDomConfig);
          }
        }
      }
    }

    return options.targetElement;
  }

  static performSync(options, targetElement) {
    const {
      domConfig,
      callback
    } = options,
          configIsEqual = options.configEquality ? options.configEquality(domConfig, targetElement.lastDomConfig, syncEqualityEvaluator) : ObjectHelper.isDeeplyEqual(domConfig, targetElement.lastDomConfig, syncEqualityEvaluator);

    if (!configIsEqual) {
      if (domConfig) {
        // Sync without affecting the containing element?
        if (!domConfig.onlyChildren) {
          this.syncAttributes(domConfig, targetElement, options);
          this.syncContent(domConfig, targetElement);
        }

        this.syncChildren(options, targetElement); // Link the element for easy retrieval later

        domConfig._element = targetElement;
      } // Allow null to clear html
      else {
          targetElement.innerHTML = null;
          targetElement.syncIdMap = null;
        } // Cache the config on the target for future comparison

      targetElement.lastDomConfig = domConfig;
      return true;
    } else {
      // Maintain link to element (deep)
      this.relinkElements(domConfig, targetElement); // Sync took no action, notify the world

      callback && callback({
        action: 'none',
        domConfig,
        targetElement
      });
    }

    return false;
  } // Called from sync when there is no change to elements, to set up link between new config and existing element.
  // Plucks the element from the last applied config, no need to hit DOM so is cheap

  static relinkElements(domConfig, targetElement) {
    // Hands off when retaining element (it is for some reason taken out of the normal rendering flow, for example
    // by dragging it)
    if (!domConfig.retainElement) {
      domConfig._element = targetElement; // Since there was no change detected, there is a 1 to 1 ratio between new config and last config, should be
      // safe to do a straight mapping

      domConfig.children && domConfig.children.forEach((childDomConfig, i) => {
        // Skip null entries, allowed for convenience, neat with map.
        // Also skip text nodes
        if (childDomConfig && typeof childDomConfig !== 'string') {
          this.relinkElements(childDomConfig, targetElement.lastDomConfig.children[i]._element);
        }
      });
    }
  } //region Attributes

  static syncDataset(domConfig, targetElement) {
    const {
      lastDomConfig
    } = targetElement,
          sameConfig = domConfig === lastDomConfig,
          source = Object.keys(domConfig.dataset),
          target = lastDomConfig && lastDomConfig.dataset && Object.keys(lastDomConfig.dataset),
          delta = ArrayHelper.delta(source, target); // New attributes in dataset

    for (let i = 0; i < delta.onlyInA.length; i++) {
      const attr = delta.onlyInA[i],
            value = domConfig.dataset[attr]; // Prevent `data-property="undefined"`

      if (value !== undefined) {
        targetElement.setAttribute(`data-${StringHelper.hyphenate(attr)}`, value);
      }
    } // Might have changed

    for (let i = 0; i < delta.inBoth.length; i++) {
      const attr = delta.inBoth[i]; // Intentional != since dataset is always string but want numbers to match
      // noinspection EqualityComparisonWithCoercionJS

      if (sameConfig || domConfig.dataset[attr] != lastDomConfig.dataset[attr]) {
        targetElement.setAttribute(`data-${StringHelper.hyphenate(attr)}`, domConfig.dataset[attr]);
      }
    } // Removed

    for (let i = 0; i < delta.onlyInB.length; i++) {
      targetElement.removeAttribute(`data-${StringHelper.hyphenate(delta.onlyInB[i])}`);
    }
  }

  static syncClassList(domConfig, targetElement, lastDomConfig) {
    let cls = domConfig.className || domConfig.class,
        c,
        currentClasses,
        i,
        k,
        keep,
        last;

    if (lastDomConfig) {
      // NOTE: The following reads the DOM to determine classes that may have been added by other means. This
      //  diff is only enabled when "strict" is used (see our callers)
      currentClasses = DomClassList.normalize(targetElement, 'array');
      cls = DomClassList.normalize(cls, 'object');
      last = DomClassList.normalize(lastDomConfig.className || lastDomConfig.class, 'object');
      keep = [];

      for (i = 0, k = currentClasses.length; i < k; ++i) {
        c = currentClasses[i]; // We want to keep classes not in cls if we didn't add them last time

        if (cls[c] || !(c in last)) {
          last[c] = 1;
          keep.push(c);
        }
      }

      for (c in cls) {
        if (!last[c]) {
          keep.push(c);
        }
      }

      cls = keep.join(' ');
    } else {
      cls = DomClassList.normalize(cls); // to string
    }

    targetElement.setAttribute('class', cls);
  } // Attributes as map { attr : value, ... }

  static getSyncAttributes(domConfig) {
    const attributes = {},
          // Attribute names, simplifies comparisons and calls to set/removeAttribute
    names = []; // On a first sync, there are no domConfig on the target element yet

    if (domConfig) {
      Object.keys(domConfig).forEach(attr => {
        if (!syncIgnoreAttributes[attr]) {
          const name = attr.toLowerCase();
          attributes[name] = domConfig[attr];
          names.push(name);
        }
      });
    }

    return {
      attributes,
      names
    };
  }

  static syncAttributes(domConfig, targetElement, options) {
    const {
      lastDomConfig
    } = targetElement,
          // If the same config has come through, due to configEquality, we must update all attrs.
    sameConfig = domConfig === lastDomConfig,
          sourceSyncAttrs = this.getSyncAttributes(domConfig),
          // Extract attributes from elements (sourceElement might be a config)
    {
      attributes: sourceAttributes,
      names: sourceNames
    } = sourceSyncAttrs,
          {
      attributes: targetAttributes,
      names: targetNames
    } = sameConfig ? sourceSyncAttrs : this.getSyncAttributes(lastDomConfig),
          // Intersect arrays to determine what needs adding, removing and syncing
    {
      onlyInA: toAdd,
      onlyInB: toRemove,
      inBoth: toSync
    } = sameConfig ? {
      onlyInA: emptyArray$4,
      onlyInB: emptyArray$4,
      inBoth: sourceNames
    } : ArrayHelper.delta(sourceNames, targetNames); // Add new attributes

    for (let i = 0; i < toAdd.length; i++) {
      const attr = toAdd[i],
            sourceAttr = sourceAttributes[attr]; // Style requires special handling

      if (attr === 'style') {
        // TODO: Do diff style apply also instead of this replace
        DomHelper.applyStyle(targetElement, sourceAttr, true);
      } // So does dataset
      else if (attr === 'dataset') {
          this.syncDataset(domConfig, targetElement);
        } // And class, which might be an object
        else if (isClass[attr]) {
            this.syncClassList(domConfig, targetElement);
          } // Other attributes are set using setAttribute (since it calls toString() DomClassList works fine),
          // unless they are undefined in which case they are ignored to not get `href="undefined"` etc
          else if (sourceAttr !== undefined) {
              targetElement.setAttribute(attr, sourceAttr);
            }
    } // Removed no longer used attributes

    for (let i = 0; i < toRemove.length; i++) {
      targetElement.removeAttribute(toRemove[i]);
    } // TODO: toAdd and toSync are growing very alike, consider merging
    // Sync values for all other attributes

    for (let i = 0; i < toSync.length; i++) {
      const attr = toSync[i],
            sourceAttr = sourceAttributes[attr],
            targetAttr = targetAttributes[attr]; // Attribute value null means remove attribute

      if (sourceAttr == null) {
        targetElement.removeAttribute(attr);
      } // Set all attributes that has changed, with special handling for style.
      else if (attr === 'style') {
          if (options.strict) {
            if (sameConfig) {
              this.syncStyles(targetElement, sourceAttr);
            } else if (!ObjectHelper.isEqual(sourceAttr, targetAttr, true)) {
              this.syncStyles(targetElement, sourceAttr, targetAttr);
            }
          } else if (sameConfig || !ObjectHelper.isEqual(sourceAttr, targetAttr, true)) {
            DomHelper.applyStyle(targetElement, sourceAttr, true);
          }
        } // And dataset
        else if (attr === 'dataset') {
            this.syncDataset(domConfig, targetElement);
          } // And class, which might be an object
          else if (isClass[attr]) {
              this.syncClassList(domConfig, targetElement, options.strict && targetElement.lastDomConfig);
            } else if (sameConfig || sourceAttr !== targetAttr) {
              targetElement.setAttribute(attr, sourceAttr);
            }
    }
  }

  static syncStyles(targetElement, sourceAttr, targetAttr) {
    let styles, key, value;

    if (!targetAttr) {
      styles = sourceAttr;
    } else {
      styles = {};

      if (sourceAttr) {
        for (key in sourceAttr) {
          value = sourceAttr[key];

          if (targetAttr[key] !== value) {
            styles[key] = value;
          }
        }
      }

      for (key in targetAttr) {
        if (!(key in sourceAttr)) {
          styles[key] = '';
        }
      }
    }

    DomHelper.applyStyle(targetElement, styles);
  } //endregion
  //region Content

  static syncContent(domConfig, targetElement) {
    const {
      html,
      text
    } = domConfig,
          content = text !== null && text !== void 0 ? text : html; // elementData holds custom data that we want to attach to the element (not visible in dom)

    if (domConfig.elementData) {
      targetElement.elementData = domConfig.elementData;
    } // Apply html from config

    if (content instanceof DocumentFragment) {
      // If given a DocumentFragment, replace content with it
      if (targetElement.childNodes.length === 1 && targetElement.childElementCount === 0 && content.childNodes.length === 1 && content.childElementCount === 0) {
        // Syncing a textNode to a textNode? Use shortcut
        DomHelper.setInnerText(targetElement, content.firstChild.data);
      } else {
        targetElement.innerHTML = '';
        targetElement.appendChild(content);
      }
    } else if (html != null && htmlRe$1.test(html)) {
      targetElement.innerHTML = String(html); // convert numbers to strings
    } else if (content != null) {
      DomHelper.setInnerText(targetElement, String(content));
    }
  }

  static insertTextNode(text, targetElement, callback, refOwner, beforeElement = null) {
    const newNode = document.createTextNode(text);
    targetElement.insertBefore(newNode, beforeElement);

    if (refOwner) {
      newNode.$refOwnerId = refOwner.id;
    }

    callback && callback({
      action: 'newNode',
      domConfig: text,
      targetElement: newNode
    });
  }

  static insertElement(domConfig, targetElement, targetNode, refOwner, syncIdMap, syncId, options) {
    // Create a new element
    const newElement = options.ns ? document.createElementNS(options.ns, domConfig.tag || 'svg') : document.createElement(domConfig.tag || 'div'); // Insert (or append if no targetNode)

    targetElement.insertBefore(newElement, targetNode); // Sync to it

    this.performSync(options, newElement);

    if (syncId != null) {
      syncIdMap[syncId] = newElement;
    }

    if (refOwner) {
      newElement.$refOwnerId = refOwner.id;

      if (syncId) {
        newElement.$reference = syncId;
        refOwner.attachRef(syncId, newElement, domConfig);
      }
    }

    options.callback && options.callback({
      action: 'newElement',
      domConfig,
      targetElement: newElement,
      syncId
    });
  } //endregion
  //region Children

  static syncChildren(options, targetElement) {
    var _domConfig$html;

    let {
      domConfig,
      syncIdField,
      callback,
      releaseThreshold,
      configEquality,
      ns,
      refOwner,
      refsWas,
      strict
    } = options; // Having specified html or text replaces all inner content, no point in syncing

    if ((_domConfig$html = domConfig.html) !== null && _domConfig$html !== void 0 ? _domConfig$html : domConfig.text) {
      return;
    }

    const me = this,
          // Always repopulate the map, since elements might get used by other syncId below
    newSyncIdMap = refOwner ? refOwner.byRef : {},
          sourceConfigs = arraySlice$1.call(domConfig.children || []),
          targetNodes = arraySlice$1.call(targetElement.childNodes),
          syncIdMap = refsWas || targetElement.syncIdMap || {},
          syncOptions = domConfig.syncOptions || {},
          releasedIdMap = targetElement.releasedIdMap || {}; // Each level can optionally specify its own syncIdField, strict and callback, if left out parent levels will be used

    syncIdField = syncOptions.syncIdField || syncIdField;
    strict = syncOptions.strict || strict;
    callback = syncOptions.callback || callback;
    configEquality = syncOptions.configEquality || configEquality; // Make sure releaseThreshold 0 is respected...

    releaseThreshold = 'releaseThreshold' in syncOptions ? syncOptions.releaseThreshold : releaseThreshold;
    let cleanupNodes = null,
        syncId;

    if (syncIdField) {
      targetElement.syncIdMap = newSyncIdMap;
    } // Settings to use in all syncs below

    const syncChildOptions = {
      refOwner,
      refsWas,
      strict,
      syncIdField,
      releaseThreshold,
      callback,
      configEquality
    };

    while (sourceConfigs.length) {
      const sourceConfig = sourceConfigs.shift();
      syncId = null; // Allowing null, convenient when using Array.map() to generate children

      if (!sourceConfig) {
        continue;
      }

      const isTextNode = typeof sourceConfig === 'string'; // Used in all syncs

      syncChildOptions.domConfig = sourceConfig;
      syncChildOptions.ns = sourceConfig.ns || ns;

      if (!isTextNode) {
        // If syncIdField was supplied, we should first try to reuse element with
        // matching "id"
        if (refOwner) {
          syncId = sourceConfig.reference;
        } else if (syncIdField && sourceConfig.dataset) {
          syncId = sourceConfig.dataset[syncIdField];
        } // We have an id to look for

        if (syncId != null && !sourceConfig.unmatched) {
          // Find any matching element, either in use or previously released
          const syncTargetElement = syncIdMap[syncId] || releasedIdMap[syncId];

          if (syncTargetElement) {
            const {
              lastDomConfig
            } = syncTargetElement;

            if ( // Ignore if flagged with `retainElement` (for example during dragging)
            !syncTargetElement.retainElement && // Otherwise sync with the matched element
            me.performSync(syncChildOptions, syncTargetElement)) {
              // Sync took some action, notify the world
              callback && callback({
                action: 'reuseOwnElement',
                domConfig: sourceConfig,
                lastDomConfig,
                targetElement: syncTargetElement,
                syncId
              });
            } // Since it wont sync above when flagged to be retained, we need to apply the flag here

            if (sourceConfig.retainElement) {
              syncTargetElement.retainElement = true; // Normally linked in performSync(), but for retained elements that fn is not called

              sourceConfig._element = syncTargetElement;
            } // Cache the element on the syncIdMap for faster retrieval later

            newSyncIdMap[syncId] = syncTargetElement; // Remove target from targetElements & release tracking, no-one else is allowed to sync with it

            ArrayHelper.remove(targetNodes, syncTargetElement);
            delete releasedIdMap[syncId];
            syncTargetElement.isReleased = false;

            if (strict || syncTargetElement.parentNode !== targetElement) {
              targetElement.insertBefore(syncTargetElement, targetNodes[0] || null);
            }
          } else if (strict) {
            this.insertElement(sourceConfig, targetElement, targetNodes[0] || null, refOwner, newSyncIdMap, syncId, syncChildOptions);
          } else {
            // No match, move to end of queue to not steal some one else's element
            sourceConfigs.push(sourceConfig); // Also flag as unmatched to know that when we reach this element again

            sourceConfig.unmatched = true;
          } // Node handled, carry on with next one

          continue;
        } // Avoid polluting the config object when done

        if (sourceConfig.unmatched) {
          delete sourceConfig.unmatched;
        }
      } // Skip over any retained elements

      let beforeNode = null,
          targetNode = null,
          cleanupNode;

      while (targetNodes.length && !targetNode) {
        cleanupNode = targetNodes.shift();

        if (refOwner) {
          // When syncing for a refOwner, foreign elements are skipped.
          if (cleanupNode.$refOwnerId !== refOwner.id) {
            continue;
          }

          if (cleanupNode.$reference) {
            // In refOwner mode we always pass strict:true, so this won't happen... but if it did, the
            // idea is that ref els do not get cleaned up until the end of the sync process.
            if (!strict) {
              continue;
            } // Since we want to maintain DOM order, this ref el marks the spot where to insert. We also
            // don't want to put it into cleanupNodes (see above). We cannot reuse ref els.

            beforeNode = cleanupNode;
            break;
          } // The element is owned by this refOwner and not assigned a reference...
          // We can reuse it

          targetNode = cleanupNode;
        } else if (!cleanupNode.retainElement) {
          targetNode = cleanupNode;
        }

        if (!targetNode) {
          (cleanupNodes || (cleanupNodes = [])).push(cleanupNode);
        }
      }

      if (beforeNode || !targetNode) {
        if (isTextNode) {
          this.insertTextNode(sourceConfig, targetElement, callback, refOwner, beforeNode);
        } else {
          // Will append if beforeNode === null
          this.insertElement(sourceConfig, targetElement, beforeNode, refOwner, newSyncIdMap, syncId, syncChildOptions);
        }
      } // We have targets left
      else {
          // Matching element tag, sync it
          if (!isTextNode && targetNode.nodeType === Node.ELEMENT_NODE && (sourceConfig.tag || 'div').toLowerCase() === targetNode.tagName.toLowerCase()) {
            var _lastDomConfig$datase;

            const {
              lastDomConfig
            } = targetNode,
                  result = me.performSync(syncChildOptions, targetNode); // Remove reused element from release tracking

            if (syncIdField && lastDomConfig !== null && lastDomConfig !== void 0 && (_lastDomConfig$datase = lastDomConfig.dataset) !== null && _lastDomConfig$datase !== void 0 && _lastDomConfig$datase[syncIdField]) {
              delete releasedIdMap[lastDomConfig.dataset[syncIdField]];
            }

            if (syncId != null) {
              newSyncIdMap[syncId] = targetNode;
            }

            targetNode.isReleased = false; // Only use callback if sync succeeded (anything changed)

            result && callback && callback({
              action: 'reuseElement',
              domConfig: sourceConfig,
              lastDomConfig,
              targetElement: targetNode,
              syncId
            });
          } // Text node to text node, change text :)
          else if (isTextNode && targetNode.nodeType === Node.TEXT_NODE) {
              targetNode.data = sourceConfig; // Not using callback for updating text of node, have no usecase for it currently
            } // Not matching, replace it
            else {
                if (isTextNode) {
                  this.insertTextNode(sourceConfig, targetElement, callback, refOwner, targetNode);
                } else {
                  // Will insert
                  this.insertElement(sourceConfig, targetElement, targetNode, refOwner, newSyncIdMap, syncId, syncChildOptions);
                }

                targetNode.remove();
              }
        }
    } // while (sourceConfigs.length)
    // Out of source nodes, remove remaining target nodes

    const numTargets = targetNodes.length;

    if (numTargets) {
      if (refOwner) {
        // Any remaining nodes that belong to this refOwner need to be cleaned up. If
        // they have an assigned reference, however, they will be handled at the very
        // end of the sync process since those elements can move in the node hierarchy.
        for (let i = 0; i < numTargets; ++i) {
          const targetNode = targetNodes[i];

          if (targetNode.$refOwnerId === refOwner.id && !targetNode.$reference) {
            (cleanupNodes || (cleanupNodes = [])).push(targetNode);
          }
        }
      } else if (cleanupNodes) {
        cleanupNodes.push(...targetNodes);
      } else {
        cleanupNodes = targetNodes;
      }
    }

    if (cleanupNodes) {
      this.syncChildrenCleanup(targetElement, cleanupNodes, newSyncIdMap, callback, refOwner, releaseThreshold, syncIdField);
    }
  }

  static syncChildrenCleanup(targetElement, cleanupNodes, newSyncIdMap, callback, refOwner, releaseThreshold, syncIdField) {
    let releaseCount = 0,
        ref;

    for (const targetNode of cleanupNodes) {
      const {
        lastDomConfig
      } = targetNode; // Element might be retained, hands off (for example while dragging)

      if (!targetNode.retainElement) {
        // When using syncId to reuse elements, "release" left over elements instead of removing them, up to a
        // limit specified as releaseThreshold, above which elements are removed instead
        if (!refOwner && syncIdField && (releaseThreshold == null || releaseCount < releaseThreshold)) {
          // Prevent releasing already released element
          if (!targetNode.isReleased) {
            targetNode.className = 'b-released';
            targetNode.isReleased = true; // Store released element in syncIdMap, to facilitate reusing it for self later

            if (lastDomConfig !== null && lastDomConfig !== void 0 && lastDomConfig.dataset) {
              if (!targetElement.releasedIdMap) {
                targetElement.releasedIdMap = {};
              }

              targetElement.releasedIdMap[lastDomConfig.dataset[syncIdField]] = targetNode;
            }

            callback && callback({
              action: 'releaseElement',
              domConfig: lastDomConfig,
              lastDomConfig,
              targetElement: targetNode
            }); // Done after callback on purpose, to allow checking old className

            if (lastDomConfig) {
              // Make sure lastDomConfig differs even from the same domConfig applied again
              // Do not want to discard it completely since it is needed for diff when reused later
              lastDomConfig.isReleased = true; // To force reapply of classes on reuse

              if (lastDomConfig.className) {
                lastDomConfig.className = 'b-released';
              } // Same for style
              // (for elements positioned using style, when moved in a non DomSync way, aka EventDrag)

              if (lastDomConfig.style) {
                lastDomConfig.style = null;
              }
            }
          }

          releaseCount++;
        } // In normal sync mode, remove left overs
        else {
            targetNode.remove();

            if (refOwner) {
              ref = targetNode.$reference;

              if (ref) {
                refOwner.detachRef(ref, targetNode, lastDomConfig);
              }
            } // Remove from "release tracking"

            if (targetElement.releasedIdMap && syncIdField && lastDomConfig !== null && lastDomConfig !== void 0 && lastDomConfig.dataset) {
              delete targetElement.releasedIdMap[lastDomConfig.dataset[syncIdField]];
            }

            callback && callback({
              action: 'removeElement',
              domConfig: targetNode.lastDomConfig,
              lastDomConfig: targetNode.lastDomConfig,
              targetElement: targetNode
            });
          }
      } else if (syncIdField) {
        // Keep retained element in map
        if (lastDomConfig) {
          newSyncIdMap[targetNode.dataset[syncIdField]] = targetNode;
        }
      }
    }
  }
  /**
   * Remove a child element without syncing, for example when dragging an element to some other parent.
   * Removes it both from DOM and the parent elements syncMap
   * @param {HTMLElement} parentElement
   * @param {HTMLElement} childElement
   */

  static removeChild(parentElement, childElement) {
    if (parentElement.contains(childElement)) {
      const syncIdMap = parentElement.syncIdMap;

      if (syncIdMap) {
        const index = Object.values(syncIdMap).indexOf(childElement);

        if (index > -1) {
          delete syncIdMap[Object.keys(syncIdMap)[index]];
        }
      }

      parentElement.removeChild(childElement);
    }
  }
  /**
   * Adds a child element without syncing, making it properly available for later syncs. Useful for example
   * when dragging and dropping an element from some other parent.
   * @param {HTMLElement} parentElement
   * @param {HTMLElement} childElement
   * @param {String|Number} syncId
   */

  static addChild(parentElement, childElement, syncId) {
    parentElement.appendChild(childElement);
    parentElement.syncIdMap[syncId] = childElement;
  }
  /**
   * Get a child element using a dot separated syncIdMap path.
   *
   * ```javascript
   * DomSync.getChild(eventWrap, 'event.percentBar');
   * ```
   *
   * @param {HTMLElement} element "root" element, under which the path starts
   * @param {String} path Dot '.' separated path of syncIdMap entries
   */

  static getChild(element, path) {
    const syncIds = path.split('.');

    for (const id of syncIds) {
      element = element.syncIdMap[id];
    }

    return element;
  } //endregion

}
DomSync._$name = 'DomSync';

/**
 * @module Core/helper/mixin/DragHelperContainer
 */

/**
 * Mixin for DragHelper that handles dragging elements between containers (or rearranging within)
 *
 * @mixin
 * @private
 */

var DragHelperContainer = (Target => class DragHelperContainer extends (Target || Base) {
  static get $name() {
    return 'DragHelperContainer';
  } //region Init

  /**
   * Initialize container drag mode.
   * @private
   */

  initContainerDrag() {
    const me = this; //use container drag as default mode

    if (!me.mode) me.mode = 'container';
    if (me.mode === 'container' && !me.containers) throw new Error('Container drag mode must specify containers');
  } //endregion
  //region Grab, update, finish

  /**
   * Grab an element which can be dragged between containers.
   * @private
   * @param event
   * @returns {Boolean}
   */

  grabContainerDrag(event) {
    const me = this; // allow specified selectors to prevent drag

    if (!me.ignoreSelector || !DomHelper.up(event.target, me.ignoreSelector)) {
      // go up from "handle" to draggable element
      const element = DomHelper.getAncestor(event.target, me.containers, me.outerElement);

      if (element) {
        const box = element.getBoundingClientRect();
        me.context = {
          element,
          valid: true,
          action: 'container',
          offsetX: event.pageX - box.left,
          offsetY: event.pageY - box.top,
          originalPosition: {
            parent: element.parentElement,
            prev: element.previousElementSibling,
            next: element.nextElementSibling
          }
        };
      }

      return true;
    }

    return false;
  }
  /**
   * Starts dragging, called when mouse moves first time after grabbing
   * @private
   * @param event
   */

  startContainerDrag(event) {
    const {
      context
    } = this,
          {
      element: dragElement
    } = context,
          div = dragElement.cloneNode(true),
          box = dragElement.getBoundingClientRect(); // init drag proxy

    div.classList.add(this.dragProxyCls);
    div.classList.add(this.draggingCls);
    document.body.appendChild(div);
    context.dragProxy = div; // Always set the proxy element width manually, drag target could be sized with flex or % width

    div.style.width = box.width + 'px';
    div.style.height = box.height + 'px';
    DomHelper.setTranslateXY(context.dragProxy, box.left, box.top); // style dragged element

    context.dragging = dragElement;
    dragElement.classList.add(this.dropPlaceholderCls);
  }
  /**
   * Move the placeholder element into its new position on valid drag.
   * @private
   * @param event
   */

  updateContainerDrag(event) {
    const me = this,
          context = me.context;
    if (!context.started || !context.targetElement) return;
    const containerElement = DomHelper.getAncestor(context.targetElement, me.containers, 'b-grid'),
          willLoseFocus = context.dragging && context.dragging.contains(DomHelper.activeElement);

    if (containerElement && DomHelper.isDescendant(context.element, containerElement)) {
      // dragging over part of self, do nothing
      return;
    } // The dragging element contains focus, and moving it within the DOM
    // will cause focus loss which might affect an encapsulating autoClose Popup.
    // Prevent focus loss handling during the DOM move.

    if (willLoseFocus) {
      GlobalEvents.suspendFocusEvents();
    }

    if (containerElement && context.valid) {
      me.moveNextTo(containerElement, event);
    } else {
      // dragged outside of containers, revert position
      me.revertPosition();
    }

    if (willLoseFocus) {
      GlobalEvents.resumeFocusEvents();
    }

    event.preventDefault();
  }
  /**
   * Finalize drag, fire drop.
   * @private
   * @param event
   * @fires drop
   */

  finishContainerDrag(event) {
    const me = this,
          context = me.context,
          // extracting variables to make code more readable
    {
      dragging,
      dragProxy,
      valid,
      draggedTo,
      insertBefore,
      originalPosition
    } = context;

    if (dragging) {
      // needs to have a valid target
      context.valid = valid && draggedTo && ( // no drop on self or parent
      dragging !== insertBefore || originalPosition.parent !== draggedTo);

      context.finalize = (valid = context.valid) => {
        // revert if invalid (and context still exists, might have been aborted from outside)
        if (!valid && this.context) {
          me.revertPosition();
        }

        dragging.classList.remove(me.dropPlaceholderCls);
        dragProxy.remove();
        me.reset();
      }; // allow async finalization by setting async to true on context in drop handler,
      // requires implementer to call context.finalize later to finish the drop

      context.async = false;
      me.trigger('drop', {
        context,
        event
      });

      if (!context.async) {
        // finalize immediately
        context.finalize();
      } else {
        context.awaitingFinalization = true;
      }
    }
  }
  /**
   * Aborts a drag operation.
   * @private
   * @param {Boolean} [invalid]
   * @param {Object} [event]
   * @param {Boolean} [silent]
   */

  abortContainerDrag(invalid = false, event = null, silent = false) {
    const me = this,
          context = me.context;

    if (context.dragging) {
      context.dragging.classList.remove(me.dropPlaceholderCls);
      context.dragProxy.remove();
      me.context = {};
    }

    if (!silent) {
      me.trigger(invalid ? 'drop' : 'abort', {
        context,
        event
      });
    }
  } //endregion
  //region Helpers

  /**
   * Starts a drag operation by creating a proxy and storing which element is being dragged.
   * @private
   */
  // startDrag() {
  //     const context = this.context,
  //           div      = context.element.cloneNode(true);
  //
  //     // init drag proxy
  //     div.classList.add('b-drag-proxy');
  //     document.body.appendChild(div);
  //     context.dragProxy = div;
  //
  //     // style dragged element
  //     context.dragging = context.element;
  //     context.dragging.classList.add('myClass');
  // }

  /**
   * Updates the drag proxy position.
   * @private
   * @param event
   */

  updateContainerProxy(event) {
    const me = this,
          context = me.context,
          proxy = context.dragProxy;
    let newX = event.pageX - context.offsetX,
        newY = event.pageY - context.offsetY;

    if (typeof me.minX === 'number') {
      newX = Math.max(me.minX, newX);
    }

    if (typeof me.maxX === 'number') {
      newX = Math.min(me.maxX - proxy.offsetWidth, newX);
    }

    if (typeof me.minY === 'number') {
      newY = Math.max(me.minY, newY);
    }

    if (typeof me.maxY === 'number') {
      newY = Math.min(me.maxY - proxy.offsetHeight, newY);
    }

    if (me.lockX) {
      DomHelper.setTranslateY(proxy, newY);
    } else if (me.lockY) {
      DomHelper.setTranslateX(proxy, newX);
    } else {
      DomHelper.setTranslateXY(proxy, newX, newY);
    }

    let targetElement;

    if (event.type === 'touchmove') {
      const touch = event.changedTouches[0];
      targetElement = DomHelper.elementFromPoint(touch.clientX, touch.clientY);
    } else {
      targetElement = event.target;
    }

    context.targetElement = targetElement;
  }
  /**
   * Positions element being dragged in relation to targetElement.
   * @private
   * @param targetElement
   * @param event
   */

  moveNextTo(targetElement, event) {
    const context = this.context,
          dragElement = context.dragging,
          parent = targetElement.parentElement;

    if (targetElement !== dragElement) {
      // dragged over a container and not over self, calculate where to insert
      const centerX = targetElement.getBoundingClientRect().left + targetElement.offsetWidth / 2;

      if (event.pageX < centerX) {
        // dragged left of target center, insert before
        parent.insertBefore(dragElement, targetElement);
        context.insertBefore = targetElement;
      } else {
        // dragged right of target center, insert after
        if (targetElement.nextElementSibling) {
          // check that not dragged to the immediate left of self. in such case, position should not change
          if (targetElement.nextElementSibling !== dragElement) {
            context.insertBefore = targetElement.nextElementSibling;
            parent.insertBefore(dragElement, targetElement.nextElementSibling);
          } else if (!context.insertBefore && dragElement.parentElement.lastElementChild !== dragElement) {
            // dragged left initially, should stay in place (checked in finishContainerDrag)
            // TODO: or flag as invalid drag? since no change...
            context.insertBefore = targetElement.nextElementSibling;
          }
        } else {
          parent.appendChild(dragElement);
          context.insertBefore = null;
        }
      }

      context.draggedTo = parent;
    }
  }
  /**
   * Moves element being dragged back to its original position.
   * @private
   */

  revertPosition() {
    const context = this.context,
          original = context.originalPosition; // revert to correct location

    if (original.next) {
      const isNoop = original.next && original.next.previousSibling === context.dragging || !original.next && context.dragging === original.parent.lastChild;

      if (!isNoop) {
        original.parent.insertBefore(context.dragging, original.next);
      }
    } else {
      original.parent.appendChild(context.dragging);
    } // no target container

    context.draggedTo = null;
  } //endregion

});

/**
 * @module Core/helper/mixin/DragHelperTranslate
 */

const noScroll = {
  pageXOffset: 0,
  pageYOffset: 0
};
/**
 * Mixin for DragHelper that handles repositioning (translating) an element within its container
 *
 * @mixin
 * @private
 */

var DragHelperTranslate = (Target => class DragHelperTranslate extends Delayable(Target || Base) {
  static get $name() {
    return 'DragHelperTranslate';
  } //region Init

  /**
   * Initialize translation drag mode.
   * @private
   */

  initTranslateDrag() {
    const me = this;
    me.mode = me.mode || 'translateXY';

    if (!me.isElementDraggable && me.targetSelector) {
      me.isElementDraggable = element => DomHelper.up(element, me.targetSelector);
    }
  } //endregion
  //region Grab, update, finish

  /**
   * Grab an element which can be moved using translation.
   * @private
   * @param event
   * @returns {Boolean}
   */

  grabTranslateDrag(event) {
    const me = this,
          element = me.getTarget(event);

    if (element) {
      me.context = {
        valid: true,
        action: me.mode,
        // translateX or translateXY...
        element,
        startPageX: event.pageX,
        startPageY: event.pageY,
        startClientX: event.clientX,
        startClientY: event.clientY
      };
      return true;
    }

    return false;
  }

  getTarget(event) {
    return DomHelper.up(event.target, this.targetSelector);
  }
  /**
   * Start translating, called on first mouse move after dragging
   * @private
   * @param event
   */

  startTranslateDrag(event) {
    const me = this,
          context = me.context,
          {
      relatedElements
    } = context;
    let element = context.dragProxy || context.element;

    if (element && !context.started) {
      const grabbed = element,
            grabbedParent = element.parentElement,
            // When cloning an element to be dragged, we place it in BODY by default
      dragWithin = me.dragWithin = me.dragWithin || me.cloneTarget && document.body,
            outerElement = me.outerElement;

      if (me.cloneTarget) {
        const offsetX = DomHelper.getOffsetX(element, dragWithin),
              offsetY = DomHelper.getOffsetY(element, dragWithin),
              offsetWidth = element.offsetWidth,
              offsetheight = element.offsetHeight;
        element = me.createProxy(element); // Match the grabbed element's size and position.

        DomHelper.setTranslateXY(element, offsetX, offsetY);
        element.style.width = `${offsetWidth}px`;
        element.style.height = `${offsetheight}px`;
        element.classList.add(me.dragProxyCls);
        dragWithin.appendChild(element);
        grabbed.classList.add('b-drag-original');

        if (me.hideOriginalElement) {
          grabbed.classList.add('b-hidden');
        }
      }

      Object.assign(me.context, {
        // The element which we're moving, could be a cloned version of grabbed, or the grabbed element itself
        element,
        // The original element upon which the mousedown event triggered a drag operation
        grabbed,
        // The parent of the original element where the pointerdown was detected - to be able to restore after an invalid drop
        grabbedParent,
        // The next sibling of the original element where the pointerdown was detected - to be able to restore after an invalid drop
        grabbedNextSibling: element.nextElementSibling,
        // elements position within parent element
        elementStartX: DomHelper.getTranslateX(element),
        elementStartY: DomHelper.getTranslateY(element),
        elementX: DomHelper.getOffsetX(element, dragWithin || outerElement),
        elementY: DomHelper.getOffsetY(element, dragWithin || outerElement),
        scrollX: 0,
        scrollY: 0,
        scrollManagerElementContainsDragProxy: !me.cloneTarget || dragWithin === outerElement
      });
      element.classList.add(me.draggingCls);

      if (dragWithin) {
        context.parentElement = element.parentElement;

        if (dragWithin !== element.parentElement) {
          dragWithin.appendChild(element);
        }

        me.updateTranslateProxy(event);
      }

      if (relatedElements) {
        relatedElements.forEach(r => {
          r.classList.add(me.draggingCls);
        });
      }
    }
  }
  /**
   * Limit translation to outer bounds and specified constraints
   * @private
   * @param element
   * @param x
   * @param y
   * @returns {{constrainedX: *, constrainedY: *}}
   */

  applyConstraints(element, x, y) {
    const me = this,
          dragWithin = me.dragWithin,
          {
      pageXOffset,
      pageYOffset
    } = dragWithin === document.body ? window : noScroll; // limit to outer elements edges

    if (dragWithin && me.constrain) {
      if (x < 0) x = 0;
      if (x + element.offsetWidth > dragWithin.scrollWidth) x = dragWithin.scrollWidth - element.offsetWidth;
      if (y < 0) y = 0;
      if (y + element.offsetHeight > dragWithin.scrollHeight) y = dragWithin.scrollHeight - element.offsetHeight;
    } // limit horizontally

    if (typeof me.minX === 'number') {
      x = Math.max(me.minX + pageXOffset, x);
    }

    if (typeof me.maxX === 'number') {
      x = Math.min(me.maxX + pageXOffset, x);
    } // limit vertically

    if (typeof me.minY === 'number') {
      y = Math.max(me.minY + pageYOffset, y);
    }

    if (typeof me.maxY === 'number') {
      y = Math.min(me.maxY + pageYOffset, y);
    }

    return {
      constrainedX: x,
      constrainedY: y
    };
  }
  /**
   * Update elements translation on mouse move.
   * @private
   * @param event
   */

  updateTranslateProxy(event, scrollManagerConfig) {
    const me = this,
          {
      mode,
      lockX,
      lockY
    } = me,
          context = me.context,
          element = context.dragProxy || context.element,
          {
      relatedElements,
      relatedElDragFromPos
    } = context; // If we are cloning the dragged element outside of the element(s) monitored by the ScrollManager, then no need to take the scrollManager scroll values into account
    // since it is only relevant when dragProxy is inside the Grid (where scroll manager operates).

    if (context.scrollManagerElementContainsDragProxy && scrollManagerConfig) {
      context.scrollX = scrollManagerConfig.scrollRelativeLeft;
      context.scrollY = scrollManagerConfig.scrollRelativeTop;
    }

    context.pageX = event.pageX;
    context.pageY = event.pageY;
    context.clientX = event.clientX;
    context.clientY = event.clientY;
    const newX = context.elementStartX + event.pageX - context.startPageX + context.scrollX,
          newY = context.elementStartY + event.pageY - context.startPageY + context.scrollY,
          {
      constrainedX,
      constrainedY
    } = me.applyConstraints(element, newX, newY);

    if (mode === 'translateXY' && !(lockX || lockY)) {
      DomHelper.setTranslateXY(element, constrainedX, constrainedY);
    } else if (mode === 'translateX' || lockY) {
      DomHelper.setTranslateX(element, constrainedX);
    } else if (mode === 'translateY' || lockX) {
      DomHelper.setTranslateY(element, constrainedY);
    }

    if (relatedElements) {
      const deltaX = !lockX && mode !== 'translateY' ? constrainedX - context.elementStartX : 0,
            deltaY = !lockY && mode !== 'translateX' ? constrainedY - context.elementStartY : 0;
      relatedElements.forEach((r, i) => {
        const [x, y] = relatedElDragFromPos[i];
        DomHelper.setTranslateXY(r, x + deltaX, y + deltaY);
      });
    }

    context.newX = constrainedX;
    context.newY = constrainedY;
  }
  /**
   * Finalize drag, fire drop.
   * @private
   * @param event
   * @fires drop
   */

  finishTranslateDrag(event) {
    const me = this,
          context = me.context,
          xChanged = context.newX !== context.elementStartX,
          yChanged = context.newY !== context.elementStartY,
          element = context.dragProxy || context.element,
          {
      relatedElements
    } = context;

    function cleanUp() {
      element.classList.remove(me.invalidCls);
      element.classList.remove(me.draggingCls);

      if (relatedElements) {
        relatedElements.forEach(r => {
          r.classList.remove(me.invalidCls);
          r.classList.remove(me.draggingCls);
        });
      } // If we're currently aborting, the b-hidden CSS class will be removed after animation is complete

      if (!element.classList.contains('b-aborting')) {
        if (me.hideOriginalElement) {
          context.grabbed.classList.remove('b-hidden');
        }

        context.grabbed.classList.remove('b-drag-original');
      }
    }

    if (!me.ignoreSamePositionDrop || me.mode !== 'translateY' && xChanged || me.mode !== 'translateX' && yChanged) {
      if (context.valid === false) {
        me.abortTranslateDrag(true, event);
      } else {
        const targetRect = Rectangle.from(me.dragWithin || me.outerElement);

        if (!me.minX && event.pageX < targetRect.left || !me.maxX && event.pageX > targetRect.right || !me.minY && event.pageY < targetRect.top || !me.maxY && event.pageY > targetRect.bottom) {
          // revert location when dropped outside allowed element
          context.valid = false;
          me.abortTranslateDrag(true, event);
        } else {
          context.finalize = (valid = context.valid) => {
            if (context.asyncCleanup) {
              cleanUp();
            } // abort if invalid (and context still exists, might have been aborted from outside)

            if (!valid && me.context) {
              // abort if flagged as invalid, without triggering abort or drop again
              me.abortTranslateDrag(true, null, true);
            } else if (me.cloneTarget || context.dragProxy) {
              element.remove();
            }

            me.reset();
          }; // allow async finalization by setting async to true on context in drop handler,
          // requires implementer to call context.finalize later to finish the drop

          context.async = false;
          me.trigger('drop', {
            context,
            event
          });

          if (!context.async) {
            // finalize immediately
            context.finalize();
          } else {
            context.awaitingFinalization = true;
          }
        }
      }

      if (!context.asyncCleanup) {
        cleanUp();
      }
    } else {
      // no change, abort but not as invalid
      me.abortTranslateDrag(false, event);
    }
  }
  /**
   * Abort translation
   * @private
   * @param invalid
   * @fires abort
   */

  abortTranslateDrag(invalid = false, event = null, silent = false) {
    const me = this,
          context = me.context,
          {
      relatedElements,
      relatedElStartPos,
      grabbed
    } = context,
          element = context.dragProxy || context.element,
          resetEl = me.cloneTarget && me.hideOriginalElement ? grabbed : element;
    let {
      elementStartX,
      elementStartY
    } = context;

    if (element && context.started) {
      // Put the dragged element back where it was
      if (!me.cloneTarget && me.dragWithin && me.dragWithin !== context.grabbedParent) {
        context.grabbedParent.insertBefore(element, context.grabbedNextSibling);
      }

      grabbed.classList.remove('b-hidden'); // Align the now visible grabbed element with the clone, so that it looks like it's
      // sliding back into place when the clone is removed

      if (me.cloneTarget) {
        if (me.hideOriginalElement) {
          [elementStartX, elementStartY] = DomHelper.getTranslateXY(grabbed);
          DomHelper.alignTo(grabbed, element); // The getBoundingClientRect is important. The aligning above must be processed
          // by a forced synchronous layout *before* the b-aborting class is added below.

          resetEl.getBoundingClientRect();
        }

        element.remove();
      } // animated restore of position.

      resetEl.classList.add('b-aborting'); // Move the elements back to their original positions.

      if (me.mode === 'translateXY' && !(me.lockX || me.lockY)) {
        DomHelper.setTranslateXY(resetEl, elementStartX, elementStartY);
        relatedElements && relatedElements.forEach((element, i) => {
          element.classList.add('b-aborting');
          DomHelper.setTranslateXY(element, relatedElStartPos[i][0], relatedElStartPos[i][1]);
        });
      }

      if (me.mode === 'translateX' || me.lockY) {
        DomHelper.setTranslateX(resetEl, elementStartX);
        relatedElements && relatedElements.forEach((element, i) => {
          element.classList.add('b-aborting');
          DomHelper.setTranslateX(element, relatedElStartPos[i][0]);
        });
      }

      if (me.mode === 'translateY' || me.lockX) {
        DomHelper.setTranslateY(resetEl, elementStartY);
        relatedElements && relatedElements.forEach((element, i) => {
          element.classList.add('b-aborting');
          DomHelper.setTranslateY(element, relatedElStartPos[i][1]);
        });
      }

      me.setTimeout(() => {
        resetEl.classList.remove('b-aborting');
        grabbed.classList.remove('b-dragging');
        grabbed.classList.remove('b-drag-original');

        if (!me.cloneTarget) {
          relatedElements && relatedElements.forEach((element, i) => {
            element.classList.remove('b-aborting');
          });
          element.classList.remove('b-aborting');
          element.classList.remove(me.draggingCls);
          element.classList.remove(me.invalidCls);
        }
      }, me.transitionDuration, undefined, true);

      if (!silent) {
        me.trigger(invalid ? 'drop' : 'abort', {
          context,
          event
        });
      }
    }

    me.reset();
  } //endregion

});

/**
 * @module Core/helper/util/Point
 */

/**
 * Encapsulates an X,Y coordinate point.
 * @extends Core/helper/util/Rectangle
 */

class Point extends Rectangle {
  /**
   * Creates a new Point encapsulating the event's page position.
   * @param {Event} event
   * @return {Core.helper.util.Point}
   * @typings ignore
   */
  static from(event) {
    const touchPoints = event.changedTouches;
    return new Point(touchPoints ? touchPoints[0].screenX : event.screenX, touchPoints ? touchPoints[0].screenY : event.pageY);
  }
  /**
   * Constructs a Point
   * @param x The X coordinate
   * @param y The Y coordinate
   */

  constructor(x, y) {
    super(x, y, 0, 0);
  }
  /**
   * Coerces this Point to be within the passed Rectangle. Translates it into the bounds.
   * @param {Core.helper.util.Rectangle} into The Rectangle into which to coerce this Point.
   */

  constrain(into) {
    this.x = Math.min(Math.max(this.x, into.x), into.right - 1);
    this.y = Math.min(Math.max(this.y, into.y), into.bottom - 1);
    return this;
  }

  toArray() {
    return [this.x, this.y];
  }

} // The Rectangle class uses the Point class, but cannot import it.
// TODO: find a better way of getting a reference to the Point class in Rectangle.
// #8224 - Gantt angular demo doesn't work in production

Object.getPrototypeOf(Point).Point = Point;
Point._$name = 'Point';

/**
 * @module Core/helper/EventHelper
 */

const touchProperties = ['clientX', 'clientY', 'pageX', 'pageY', 'screenX', 'screenY'],
      isOption = {
  element: 1,
  thisObj: 1,
  once: 1,
  delegate: 1,
  delay: 1,
  capture: 1,
  passive: 1,
  throttled: 1,
  autoDetach: 1,
  expires: 1
},
      ctrlKeyProp = {
  get: () => true
},
      normalizedKeyNames = {
  Spacebar: 'Space',
  Del: 'Delete',
  Esc: 'Escape',
  Left: 'ArrowLeft',
  Up: 'ArrowUp',
  Right: 'ArrowRight',
  Down: 'ArrowDown'
};

function getAccessorFromPrototype(target, property) {
  let descriptor, accessor;

  while ((target = Object.getPrototypeOf(target)) && !accessor) {
    descriptor = Object.getOwnPropertyDescriptor(target, property);
    accessor = descriptor && descriptor.get;
  }

  return accessor;
}
/**
 * Utility methods for dealing with Events, normalizing Touch/Pointer/Mouse events.
 */

class EventHelper {
  static normalizeEvent(event) {
    return ObjectHelper.copyPropertiesIf(event, event.changedTouches[0] || event.touches[0], touchProperties);
  }
  /**
   * Returns the `[x, y]` coordinates of the event in the viewport coordinate system.
   * @param {Event} event The event
   * @return {Number[]} The coordinate.
   */

  static getXY(event) {
    if (event.touches) {
      event = event.touches[0];
    }

    return [event.clientX, event.clientY];
  }
  /**
   * Returns the pixel distance between two mouse/touch/pointer events.
   * @param {Event} event1 The first event.
   * @param {Event} event2 The second event.
   * @return {Number} The distance in pixels between the two events.
   */

  static getDistanceBetween(event1, event2) {
    const xy1 = EH.getXY(event1),
          xy2 = EH.getXY(event2); // No point in moving this to Point. We are dealing only with number values here.

    return Math.sqrt(Math.pow(xy1[0] - xy2[0], 2) + Math.pow(xy1[1] - xy2[1], 2));
  }
  /**
   * Returns a {@link Core.helper.util.Point} which encapsulates the `pageX/Y` position of the event.
   * May be used in {@link Core.helper.util.Rectangle} events.
   * @param {Event} event A browser mouse/touch/pointer event.
   * @return {Core.helper.util.Point} The page point.
   */

  static getPagePoint(event) {
    return new Rectangle.Point(event.pageX, event.pageY);
  }
  /**
   * Returns a {@link Core.helper.util.Point} which encapsulates the `clientX/Y` position of the event.
   * May be used in {@link Core.helper.util.Rectangle} events.
   * @param {Event} event A browser mouse/touch/pointer event.
   * @return {Core.helper.util.Point} The page point.
   */

  static getClientPoint(event) {
    return new Rectangle.Point(event.clientX, event.clientY);
  }
  /**
   * Add a listener or listeners to an element
   * @param {HTMLElement} element The element to add a listener/listeners to.
   * @param {String|Object} eventName Either a string, being the name of the event to listen for,
   * or an options object containing event names and options as keys. See the options parameter
   * for details, or the {@link #function-on-static} method for details.
   * @param {Function} [handler] If the second parameter is a string event name, this is the handler function.
   * @param {Object} [options] If the second parameter is a string event name, this is the options.
   * @param {HTMLElement} options.element The element to add the listener to.
   * @param {Object} options.thisObj The default `this` reference for all handlers added in this call.
   * @param {Boolean} [options.autoDetach=true] The listeners are automatically removed when the `thisObj` is destroyed.
   * @param {String} [options.delegate] A CSS selector string which only fires the handler when the event takes place in a matching element.
   * @param {Boolean} [options.once] Specify as `true` to have the listener(s) removed upon first invocation.
   * @param {Number} [options.delay] The number of milliseconds to delay the handler call after the event fires:
   * @param {Number|Object} [options.expires] The listener only waits for a specified time before
   * being removed. The value may be a number or an object containing an expiry handler.
   * @param {Number} [options.expires.delay] How long to wait for the event for.
   * @param {String|Function} [options.expires.alt] The function to call when the listener expires
   * **without having been triggered**.
   * @returns {Function} A detacher function which removes all the listeners when called.
   */

  static addListener(element, eventName, handler, options) {
    if (element.nodeType) {
      // All separate params, element, eventname and handler
      if (typeof eventName === 'string') {
        options = Object.assign({
          element,
          [eventName]: handler
        }, options);
      } // element, options
      else {
          options = Object.assign({
            element
          }, eventName);
        }
    } // Just an options object passed
    else {
        options = element;
      }

    return EH.on(options);
  }
  /**
   * Adds a listener or listeners to an element.
   * all property names other than the options listed below are taken to be event names,
   * and the values as handler specs.
   *
   * A handler spec is usually a function reference or the name of a function in the `thisObj`
   * option.
   *
   * But a handler spec may also be an options object containing a `handler` property which is
   * the function or function name, and local options, including `element` and `thisObj`
   * which override the top level options.
   *
   *  Usage example
   *
   * ```javascript
   * construct(config) {
   *     super.construct(config);
   *
   *     // Add auto detaching event handlers to this Widget's reference elements
   *     EventHelper.on({
   *         element : this.iconElement,
   *         click   : '_handleIconClick',
   *         thisObj : this,
   *         contextmenu : {
   *             element : document,
   *             handler : '_handleDocumentContextMenu'
   *         }
   *     });
   * }
   *```
   *
   * The `click` handler on the `iconElement` calls `this._handleIconClick`.
   *
   * The `contextmenu` handler is added to the `document` element, but the `thisObj`
   * is defaulted in from the top `options` and calls `this._handleDocumentContextMenu`.
   *
   * Note that on touch devices, `dblclick` and `contextmenu` events are synthesized.
   * Synthesized events contain a `browserEvent` property containing the final triggering
   * event of the gesture. For example a synthesized `dblclick` event would contain a
   * `browserEvent` property which is the last `touchend` event. A synthetic `contextmenu`
   * event will contain a `browserEvent` property which the longstanding `touchstart` event.
   *
   * @param {Object} options The full listener specification.
   * @param {HTMLElement} options.element The element to add the listener to.
   * @param {Object} options.thisObj The default `this` reference for all handlers added in this call.
   * @param {Boolean} [options.autoDetach=true] The listeners are automatically removed when the `thisObj` is destroyed.
   * @param {String} [options.delegate] A CSS selector string which only fires the handler when the event takes place in a matching element.
   * @param {Boolean} [options.once] Specify as `true` to have the listener(s) removed upon first invocation.
   * @param {Number} [options.delay] The number of milliseconds to delay the handler call after the event fires.
   * @param {Number} [options.throttled] For rapidly repeating events (Such as `wheel` or `scroll` or `mousemove`)
   * this is the number of milliseconds to delay subsequent handler calls after first invocation which happens immediately.
   * @param {Number|Object} [options.expires] The listener only waits for a specified time before
   * being removed. The value may be a number or an object containing an expiry handler.
   * @param {Number} [options.expires.delay] How long to wait for the event for.
   * @param {String|Function} [options.expires.alt] The function to call when the listener expires
   * **without having been triggered**.
   * @returns {Function} A detacher function which removes all the listeners when called.
   */

  static on(options) {
    const element = options.element,
          thisObj = options.thisObj,
          handlerDetails = [];

    for (const eventName in options) {
      // Only treat it as an event name if it's not a supported option
      if (!isOption[eventName]) {
        let handlerSpec = options[eventName];

        if (typeof handlerSpec !== 'object') {
          handlerSpec = {
            handler: handlerSpec
          };
        }

        const targetElement = handlerSpec.element || element; // If we need to convert taphold to an emulated contextmenu, add a wrapping function
        // in addition to the contextmenu listener. Platforms may support mouse *and* touch.

        if (BrowserHelper.isTouchDevice && !BrowserHelper.isAndroid) {
          if (eventName === 'contextmenu') {
            handlerDetails.push(EH.addElementListener(targetElement, 'touchstart', {
              handler: EH.createContextMenuWrapper(handlerSpec.handler, handlerSpec.thisObj || thisObj)
            }, options));
          }
        } // Keep track of the real handlers added.
        // addElementLister returns [ element, eventName, addedfunction, capture ]

        handlerDetails.push(EH.addElementListener(targetElement, eventName, handlerSpec, options));
      }
    }

    const detacher = () => {
      for (let handlerSpec, i = 0; i < handlerDetails.length; i++) {
        handlerSpec = handlerDetails[i];
        EH.removeEventListener(handlerSpec[0], handlerSpec[1], handlerSpec[2]);
      }

      handlerDetails.length = 0;
    }; // { autoDetach : true, thisObj : scheduler } means remove all listeners when the scheduler dies.

    if (thisObj && options.autoDetach !== false) {
      thisObj.doDestroy = FunctionHelper.createInterceptor(thisObj.doDestroy, detacher, thisObj);
    }

    return detacher;
  }
  /**
   * Used internally to add a single event handler to an element.
   * @param {HTMLElement} element The element to add the handler to.
   * @param {String} eventName The name of the event to add a handler for.
   * @param {Function|String|Object} handlerSpec Either a function to call, or
   * the name of a function to call in the `thisObj`, or an object containing
   * the handler local options.
   * @param {Function|String} [handlerSpec.handler] Either a function to call, or
   * the name of a function to call in the `thisObj`.
   * @param {HTMLElement} [handlerSpec.element] Optionally a local element for the listener.
   * @param {Object} [handlerSpec.thisObj] A local `this` specification for the handler.
   * @param {Object} defaults The `options` parameter from the {@link #function-addListener-static} call.
   * @private
   */

  static addElementListener(element, eventName, handlerSpec, defaults) {
    const handler = EH.createHandler(element, eventName, handlerSpec, defaults),
          handlerHasPassive = ('passive' in handlerSpec),
          expires = handlerSpec.expires || defaults.expires;
    let options = handlerSpec.capture || defaults.capture; // If we are passed the passive option and the browser supports it, then convert
    // The capture option into the object options form.

    if ((handlerHasPassive || 'passive' in defaults) && BrowserHelper.supportsPassive) {
      options = {
        capture: !!options,
        passive: handlerHasPassive ? handlerSpec.passive : defaults.passive
      };
    }

    element.addEventListener(eventName, handler, options);

    if (expires) {
      // Extract expires : { delay : 100, alt : 'onExpireFn' }
      const thisObj = handlerSpec.thisObj || defaults.thisObj,
            delayable = thisObj !== null && thisObj !== void 0 && thisObj.isDelayable ? thisObj : window,
            {
        alt
      } = expires,
            delay = alt ? expires.delay : expires,
            {
        spec
      } = handler; // expires is not applied with other options in createHandler(), store it here

      spec.expires = expires;
      spec.timerId = delayable[typeof delay === 'number' ? 'setTimeout' : 'requestAnimationFrame'](() => {
        spec.timerId = null;
        EH.removeEventListener(element, eventName, handler); // If we make it here and the handler has not been called, invoke the alt handler

        if (alt && !handler.called) {
          (typeof alt === 'string' ? thisObj[alt] : alt).call(thisObj);
        }
      }, delay, `listener-timer-${performance.now()}`);
    }

    return [element, eventName, handler, options];
  }

  static fixEvent(event) {
    var _event$target, _event$target2, _event$target3, _event$relatedTarget;

    const {
      type
    } = event; // Normalize key names

    if (type.startsWith('key')) {
      const normalizedKeyName = normalizedKeyNames[event.key];

      if (normalizedKeyName) {
        Object.defineProperty(event, 'key', {
          get: () => normalizedKeyName
        });
      } // Polyfill the code property for SPACE because it is not set for synthetic events.

      if (event.key === ' ' && !event.code) {
        Object.defineProperty(event, 'code', {
          get: () => 'Space'
        });
      }
    } // Sync OSX's meta key with the ctrl key. This will only happen on Mac platform.
    // It's read-only, so define a local property to return true for ctrlKey.

    if (event.metaKey && !event.ctrlKey) {
      Object.defineProperty(event, 'ctrlKey', ctrlKeyProp);
    } // When we listen to event on document and get event which bubbled from shadow dom, reading its target would
    // return shadow root element. We need actual element which started the event

    if ((_event$target = event.target) !== null && _event$target !== void 0 && _event$target.shadowRoot && event.composedPath && !BrowserHelper.isIE11 && !BrowserHelper.isEdge) {
      const targetElement = event.composedPath()[0],
            originalTarget = event.target; // Can there be an event which actually originated from custom element, not its shadow dom?

      if (event.target !== targetElement) {
        Object.defineProperty(event, 'target', {
          get: () => targetElement,
          configurable: true
        }); // Save original target just in case

        Object.defineProperty(event, 'originalTarget', {
          get: () => originalTarget,
          configurable: true
        });
      }
    } // Chrome 78 has a bug where moving out of the left edge of an element can report a mousemove
    // with that element as the target, but offsetX as -1 and moving out the right edge can report
    // that the element is the target but an offset of the offsetWidth. Patch the event until they fix it.
    // https://bugs.chromium.org/p/chromium/issues/detail?id=1010528
    // Tested in EventHelper.js

    if (BrowserHelper.isChrome && event.target && 'offsetX' in event) {
      // Wrap reading `offsetX/Y` until this property is actually accessed in the code to avoid forced reflow.
      // To avoid getting infinite loop, property descriptor is looked up in the prototype chain. Should be
      // better than forcing DOM reflow on every single event.
      if (!Object.getOwnPropertyDescriptor(event, 'offsetX')) {
        Object.defineProperty(event, 'offsetX', {
          get: () => {
            const accessor = getAccessorFromPrototype(event, 'offsetX'),
                  offsetX = accessor.call(event),
                  {
              target
            } = event,
                  {
              offsetWidth
            } = target,
                  x = Math.min(Math.max(offsetX, 0), offsetWidth - 1);

            if (offsetX < 0 || offsetX >= offsetWidth) {
              return x;
            }

            return offsetX;
          }
        });
      }

      if (!Object.getOwnPropertyDescriptor(event, 'offsetY')) {
        Object.defineProperty(event, 'offsetY', {
          get: () => {
            const accessor = getAccessorFromPrototype(event, 'offsetY'),
                  offsetY = accessor.call(event),
                  {
              target
            } = event,
                  {
              offsetHeight
            } = target,
                  y = Math.min(Math.max(offsetY, 0), offsetHeight - 1);

            if (offsetY < 0 || offsetY >= offsetHeight) {
              return y;
            }

            return offsetY;
          }
        });
      }
    } // Firefox has a bug where it can report that the target is the #document when mouse is over a pseudo element

    if (((_event$target2 = event.target) === null || _event$target2 === void 0 ? void 0 : _event$target2.nodeType) === 9 && 'clientX' in event) {
      const targetElement = DomHelper.elementFromPoint(event.clientX, event.clientY);
      Object.defineProperty(event, 'target', {
        get: () => targetElement
      });
    } // Firefox has a bug where it can report a textNode as an event target/relatedTarget.
    // We standardize this to report the parentElement.

    if (((_event$target3 = event.target) === null || _event$target3 === void 0 ? void 0 : _event$target3.nodeType) === 3) {
      const targetElement = event.target.parentElement;
      Object.defineProperty(event, 'target', {
        get: () => targetElement
      });
    }

    if (((_event$relatedTarget = event.relatedTarget) === null || _event$relatedTarget === void 0 ? void 0 : _event$relatedTarget.nodeType) === 3) {
      const relatedTargetElement = event.target.parentElement;
      Object.defineProperty(event, 'relatedTarget', {
        get: () => relatedTargetElement
      });
    } // If it's a touch event, move the positional details
    // of touches[0] up to the event.

    if (type.startsWith('touch') && event.touches.length) {
      this.normalizeEvent(event);
    }

    return event;
  }

  static createHandler(element, eventName, handlerSpec, defaults) {
    const delay = handlerSpec.delay || defaults.delay,
          throttled = handlerSpec.throttled || defaults.throttled,
          block = handlerSpec.block || defaults.block,
          once = handlerSpec.once || defaults.once,
          thisObj = handlerSpec.thisObj || defaults.thisObj,
          capture = handlerSpec.capture || defaults.capture,
          delegate = handlerSpec.delegate || defaults.delegate,
          wrappedFn = handlerSpec.handler; //Capture initial conditions in case of destruction of thisObj.
    // Destruction completely wipes the object.
    // Innermost level of wrapping which calls the user's handler.
    // Normalize the event cross-browser, and attempt to normalize touch events.

    let handler = (event, ...args) => {
      // Flag for the expiry timer
      handler.called = true; // When playing a demo using DemoBot, only handle synthetic events

      if (EH.playingDemo && event.isTrusted) {
        return;
      } // If the thisObj is already destroyed, we cannot call the function.
      // If in dev mode, warn the developer with a JS error.

      if (thisObj && thisObj.isDestroyed) {
        // eslint-disable-next-line
        return;
      } // Fix up events to handle various browser inconsistencies

      event = EH.fixEvent(event); // delegate: '.b-field-trigger' only fires when click is in a matching el.
      // currentTarget becomes the delegate.

      if (delegate) {
        // Maintainer: In Edge event.target can be an empty object for transitionend events
        const delegatedTarget = event.target instanceof HTMLElement && event.target.closest(delegate);

        if (!delegatedTarget) {
          return;
        } // Allow this to be redefined as it bubbles through listeners up the parentNode axis
        // which might have their own delegate settings.

        Object.defineProperty(event, 'currentTarget', {
          get: () => delegatedTarget,
          configurable: true
        });
      }

      (typeof wrappedFn === 'string' ? thisObj[wrappedFn] : wrappedFn).call(thisObj, event, ...args);
    }; // Allow events to be blocked for a certain time

    if (block) {
      const wrappedFn = handler;
      let lastCallTime, lastTarget;

      handler = (e, ...args) => {
        const now = performance.now();

        if (!lastCallTime || e.target !== lastTarget || now - lastCallTime > block) {
          lastTarget = e.target;
          lastCallTime = now;
          wrappedFn(e, ...args);
        }
      };
    } // Go through options, each creates a new handler by wrapping the previous handler to implement the options.
    // Right now, we have delay. Note that it may be zero, so test != null

    if (delay != null) {
      const wrappedFn = handler,
            delayable = thisObj && thisObj.setTimeout ? thisObj : window;

      handler = (...args) => {
        delayable.setTimeout(() => {
          wrappedFn(...args);
        }, delay);
      };
    } // If they specified the throttled option, wrap the handler in a createdThrottled
    // version. Allow the called to specify an alt function to call when the event
    // fires before the buffer time has expired.

    if (throttled != null) {
      let alt,
          buffer = throttled;

      if (throttled.buffer) {
        alt = e => throttled.alt.call(EH, EH.fixEvent(e));

        buffer = throttled.buffer;
      }

      if (thisObj && thisObj.isDelayable) {
        handler = thisObj.throttle(handler, {
          delay: buffer,
          throttled: alt
        });
      } else {
        handler = FunctionHelper.createThrottled(handler, buffer, thisObj, null, alt);
      }
    } // This must always be the last option processed so that it is the outermost handler
    // which is the one added to the element and is called immediately so that the
    // handler is removed immediately.
    // TODO: Use the native once option when all browsers support it. Only IE11 doesn't.

    if (once) {
      const wrappedFn = handler;

      handler = (...args) => {
        //element.removeEventListener(eventName, handler, capture);
        EH.removeEventListener(element, eventName, handler);
        wrappedFn(...args);
      };
    } // Only autoDetach here if there's a local thisObj is in the handlerSpec for this one listener.
    // If it's in the defaults, then the "on" method will handle it.

    if (handlerSpec.thisObj && handlerSpec.autoDetach !== false) {
      thisObj.doDestroy = FunctionHelper.createInterceptor(thisObj.doDestroy, () => EH.removeEventListener(element, eventName, handler), thisObj);
    }

    handler.spec = {
      delay,
      throttled,
      block,
      once,
      thisObj,
      capture,
      delegate
    };
    return handler;
  }

  static removeEventListener(element, eventName, handler) {
    const {
      expires,
      timerId,
      thisObj,
      capture
    } = handler.spec; // Cancel outstanding expires.alt() call when removing the listener

    if (expires !== null && expires !== void 0 && expires.alt && timerId) {
      const delayable = thisObj !== null && thisObj !== void 0 && thisObj.isDelayable ? thisObj : window;
      delayable[typeof expires.delay === 'number' ? 'clearTimeout' : 'cancelAnimationFrame'](timerId);
    }

    element.removeEventListener(eventName, handler, capture);
  }

  static onTransitionEnd({
    element,
    property,
    handler,
    mode = 'transition',
    duration = DomHelper[`get${mode === 'transition' ? 'Property' : ''}${StringHelper.capitalize(mode)}Duration`](element, property),
    thisObj = window
  }) {
    let timerId;

    const timerSource = thisObj.isDelayable ? thisObj : window,
          doCallback = () => {
      detacher();

      if (!thisObj.isDestroyed) {
        if (thisObj.callback) {
          thisObj.callback(handler, thisObj);
        } else {
          handler.call(thisObj);
        }
      }
    },
          detacher = EH.on({
      element,

      [`${mode}end`]({
        propertyName,
        target
      }) {
        if (propertyName === property && target === element) {
          if (timerId) {
            timerSource.clearTimeout(timerId);
            timerId = null;
          }

          doCallback();
        }
      }

    }); // If the transition has not signalled its end within duration + 20 milliseconds
    // then give up on it. Remove the listener and call the handler.

    if (duration != null) {
      timerId = timerSource.setTimeout(doCallback, duration + 20);
    }

    return detacher;
  }
  /**
   * Private function to wrap the passed function. The returned wrapper function to be used as
   * a `touchstart` handler which will call the passed function passing a fabricated `contextmenu`
   * event if there's no `touchend` or `touchmove` after a default of 400ms.
   * @param {String|Function} handler The handler to call.
   * @param {Object} thisObj The owner of the function.
   * @private
   */

  static createContextMenuWrapper(handler, me) {
    return event => {
      // Only attempt conversion to contextmenu if it's a single touch start.
      if (event.touches.length === 1) {
        const tapholdStartTouch = event.touches[0],
              // Dispatch a synthetic "contextmenu" event from the touchpoint in <longPressTime> milliseconds.
        tapholdTimer = setTimeout(() => {
          // Remove the gesture cancelling listeners
          touchMoveRemover();
          const contextmenuEvent = new MouseEvent('contextmenu', tapholdStartTouch);
          Object.defineProperty(contextmenuEvent, 'target', {
            get() {
              return tapholdStartTouch.target;
            }

          });

          if (typeof handler === 'string') {
            handler = me[handler];
          }

          contextmenuEvent.browserEvent = event; // Call the wrapped handler passing the fabricated contextmenu event

          handler.call(me, contextmenuEvent);
          EH.contextMenuTouchId = tapholdStartTouch.identifier;
        }, EH.longPressTime),
              // This is what gets called if the user moves their touchpoint,
        // or releases the touch before <longPressTime>ms is up
        cancelTapholdTimer = () => {
          EH.contextMenuTouchId = null;
          touchMoveRemover();
          clearTimeout(tapholdTimer);
        },
              // Touchmove or touchend before that timer fires cancels the timer and removes these listeners.
        touchMoveRemover = EH.on({
          element: document,
          touchmove: cancelTapholdTimer,
          touchend: cancelTapholdTimer,
          pointermove: cancelTapholdTimer,
          pointerup: cancelTapholdTimer,
          capture: true
        });
      }
    };
  }
  /**
   * Private function to wrap the passed function. The returned wrapper function to be used as
   * a `touchend` handler which will call the passed function passing a fabricated `dblclick`
   * event if there is a `click` within 300ms.
   * @param {String|Function} handler The handler to call.
   * @param {Object} thisObj The owner of the function.
   * @private
   */

  static createDblClickWrapper(element, handler, me) {
    let startId, secondListenerDetacher, tapholdTimer;
    return () => {
      if (!secondListenerDetacher) {
        secondListenerDetacher = EH.on({
          element,
          // We only get here if a touchstart arrives within 300ms of a click
          touchstart: secondStart => {
            startId = secondStart.changedTouches[0].identifier; // Prevent zoom

            secondStart.preventDefault();
          },
          touchend: secondClick => {
            if (secondClick.changedTouches[0].identifier === startId) {
              secondClick.preventDefault();
              clearTimeout(tapholdTimer);
              startId = secondListenerDetacher = null;
              const targetRect = Rectangle.from(secondClick.changedTouches[0].target, null, true),
                    offsetX = secondClick.changedTouches[0].pageX - targetRect.x,
                    offsetY = secondClick.changedTouches[0].pageY - targetRect.y,
                    dblclickEventConfig = Object.assign({
                browserEvent: secondClick
              }, secondClick),
                    dblclickEvent = new MouseEvent('dblclick', dblclickEventConfig);
              Object.defineProperty(dblclickEvent, 'target', {
                get() {
                  return secondClick.target;
                }

              });
              Object.defineProperty(dblclickEvent, 'offsetX', {
                get() {
                  return offsetX;
                }

              });
              Object.defineProperty(dblclickEvent, 'offsetY', {
                get() {
                  return offsetY;
                }

              });

              if (typeof handler === 'string') {
                handler = me[handler];
              } // Call the wrapped handler passing the fabricated dblclick event

              handler.call(me, dblclickEvent);
            }
          },
          once: true
        }); // Cancel the second listener is there's no second click within <dblClickTime> milliseconds.

        tapholdTimer = setTimeout(() => {
          secondListenerDetacher();
          startId = secondListenerDetacher = null;
        }, EH.dblClickTime);
      }
    };
  }

  static lockComposedPath(event) {
    if (event.composedPath) {
      event.composedPath = (path => () => path)(event.composedPath());
    }
  }

}
const EH = EventHelper;
/**
 * The time in milliseconds for a `taphold` gesture to trigger a `contextmenu` event.
 * @member {Number} [longPressTime=500]
 * @readonly
 * @static
 */

EH.longPressTime = 500;
/**
 * The time in milliseconds within which a second touch tap event triggers a `dblclick` event.
 * @member {Number} [dblClickTime=300]
 * @readonly
 * @static
 */

EH.dblClickTime = 300; // Flag body if last user action used keyboard, used for focus styling etc.

EH.on({
  element: document,

  mousedown() {
    if (!DomHelper.isTouchEvent) {
      DomHelper.usingKeyboard = false;
      document.body.classList.remove('b-using-keyboard');
    }
  },

  touchmove() {
    DomHelper.usingKeyboard = false;
    document.body.classList.remove('b-using-keyboard');
  },

  keydown() {
    DomHelper.usingKeyboard = true;
    document.body.classList.add('b-using-keyboard');
  }

}); // When dragging on a touch device, we need to prevent scrolling from happening.
// Dragging only starts on a touchmove event, by which time it's too late to preventDefault
// on the touchstart event which started it.
// To do this we need a capturing, non-passive touchmove listener at the document level so we can preventDefault.
// This is in lieu of a functioning touch-action style on iOS Safari. When that's fixed, this will not be needed.

if (BrowserHelper.isTouchDevice) {
  EH.on({
    element: document,
    touchmove: event => {
      // If we're touching a b-dragging event, then stop any panning by preventing default.
      if (event.target.closest('.b-dragging')) {
        event.preventDefault();
      }
    },
    passive: false,
    capture: true
  });
} // Set up the GlobalEvents listeners for global focus events

setupFocusListeners(document, EventHelper);
EventHelper._$name = 'EventHelper';

//TODO: add pointer events support

/**
 * @module Core/helper/DragHelper
 */

const documentListeners = {
  down: 'onMouseDown',
  move: 'onMouseMove',
  up: 'onMouseUp',
  docclick: 'onDocumentClick',
  touchstart: 'onTouchStart',
  touchmove: 'onTouchMove',
  touchend: 'onTouchEnd',
  keydown: 'onKeyDown'
};
/**
 * Helps with drag and drop. Supports two modes of dragging:
 * * `container` - moving/rearranging elements within and between specified containers
 * * `translateXY` - repositioning an element withing its container
 *
 * Usage examples:
 * ```
 * // dragging element between containers
 * let dragHelper = new DragHelper({
 *   mode       : 'container',
 *   containers : [ container1, container2 ]
 * });
 *
 * // dragging element within container
 * let dragHelper = new DragHelper({
 *   mode    : 'translateXY',
 *   targetSelector: 'div.moveable'
 * });
 * ```
 * In the various Drag event handlers, you will have access to the raw DOM event and some useful `context` of the drag operation:
 *
 * ```
 *  myDrag.on({
 *      drag : ({event , context}) {
 *            // The element which we're moving, could be a cloned version of grabbed, or the grabbed element itself
 *           const element = context.element;
 *
 *           // The original mousedown element upon which triggered the drag operation
 *           const grabbed = context.grabbed;
 *
 *           // The target under the current mouse / pointer / touch position
 *           const target = context.target;
 *       }
 *  });
 * ```
 *
 * Simple drag drop with a drop target specified:
 * ```javascript
 * export default class MyDrag extends DragHelper {
 *      static get defaultConfig() {
 *          return {
 *              // Don't drag the actual cell element, clone it
 *              cloneTarget        : true,
 *              mode               : 'translateXY',
 *              // Only allow drops on DOM elements with 'yourDropTarget' CSS class specified
 *              dropTargetSelector : '.yourDropTarget',
 *
 *              // Only allow dragging elements with the 'draggable' CSS class
 *              targetSelector : '.draggable'
 *          };
 *      }
 *
 *      construct(config) {
 *          const me = this;
 *
 *          super.construct(config);
 *
 *          me.on({
 *              dragstart : me.onDragStart
 *          });
 *      }
 *
 *      onDragStart({ event, context }) {
 *          const target = context.target;
 *
 *          // Here you identify what you are dragging (an image of a user, grid row in an order table etc) and map it to something in your
 *          // data model. You can store your data on the context object which is available to you in all drag-related events
 *          context.userId = target.dataset.userId;
 *      }
 *
 *      onEquipmentDrop({ context, event }) {
 *          const me = this;
 *
 *          if (context.valid) {
 *              const userId   = context.userId,
 *                    droppedOnTarget = context.target;
 *
 *              console.log(`You dropped user ${userStore.getById(userId).name} on ${droppedOnTarget}`, droppedOnTarget);
 *
 *              // Dropped on a scheduled event, display toast
 *              WidgetHelper.toast(`You dropped user ${userStore.getById(userId).name} on ${droppedOnTarget}`);
 *
 *              // tell the drag helper the operation is finished
 *              me.context.finalize();
 *          }
 *      }
 *  };
 * ```
 *
 * @mixes Core/mixin/Events
 * @extends Core/Base
 */

class DragHelper extends Base.mixin(Events, DragHelperContainer, DragHelperTranslate) {
  //region Config
  static get defaultConfig() {
    return {
      /**
       * Drag proxy CSS class
       * @config {String}
       * @default
       * @private
       */
      dragProxyCls: 'b-drag-proxy',

      /**
       * CSS class added when drag is invalid
       * @config {String}
       * @default
       */
      invalidCls: 'b-drag-invalid',

      /**
       * CSS class added to the source element in Container drag
       * @config {String}
       * @default
       * @private
       */
      draggingCls: 'b-dragging',

      /**
       * CSS class added to the source element in Container drag
       * @config {String}
       * @default
       * @private
       */
      dropPlaceholderCls: 'b-drop-placeholder',

      /**
       * The amount of pixels to move mouse before it counts as a drag operation
       * @config {Number}
       * @default
       */
      dragThreshold: 5,

      /**
       * The outer element where the drag helper will operate (attach events to it and use as outer limit when looking for ancestors)
       * @config {HTMLElement}
       * @default
       */
      outerElement: document.body,

      /**
       * Outer element that limits where element can be dragged
       * @config {HTMLElement}
       * @default
       */
      dragWithin: null,

      /**
       * Constrain translate drag to dragWithin elements bounds (set to false to allow it to "overlap" edges)
       * @config {Boolean}
       * @default
       */
      constrain: true,

      /**
       * Smallest allowed x when dragging horizontally.
       * @config {Number}
       * @default
       */
      minX: null,

      /**
       * Largest allowed x when dragging horizontally.
       * @config {Number}
       * @default
       */
      maxX: null,

      /**
       * Smallest allowed y when dragging horizontally.
       * @config {Number}
       * @default
       */
      minY: null,

      /**
       * Largest allowed y when dragging horizontally.
       * @config {Number}
       * @default
       */
      maxY: null,

      /**
       * Enabled dragging, specify mode:
       * <table>
       * <tr><td>container<td>Allows reordering elements within one and/or between multiple containers
       * <tr><td>translateXY<td>Allows dragging within a parent container
       * </table>
       * @config {String}
       * @default
       */
      mode: null,

      /**
       * A function that determines if dragging an element is allowed. Gets called with the element as argument,
       * return true to allow dragging or false to prevent.
       * @config {Function}
       * @default
       */
      isElementDraggable: null,

      /**
       * A CSS selector used to determine if dragging an element is allowed.
       * @config {String}
       * @default
       */
      targetSelector: null,

      /**
       * A CSS selector used to determine if a drop is allowed at the current position.
       * @config {String}
       * @default
       */
      dropTargetSelector: null,

      /**
       * Set to true to clone the dragged target, and not move the actual target DOM node.
       * @config {Boolean}
       * @default
       */
      cloneTarget: false,

      /**
       * Set to true to hide the original element while dragging (applicable when `cloneTarget` is true).
       * @config {Boolean}
       * @default
       */
      hideOriginalElement: false,

      /**
       * Containers whose elements can be rearranged (and moved between the containers). Used when
       * mode is set to "container".
       * @config {HTMLElement[]}
       * @default
       */
      containers: null,

      /**
       * A CSS selector used to exclude elements when using container mode
       * @config {String}
       * @default
       */
      ignoreSelector: null,
      startEvent: null,

      /**
       * Configure as `true` to disallow dragging in the `X` axis. The dragged element will only move vertically.
       * @config {Boolean}
       * @default
       */
      lockX: false,

      /**
       * Configure as `true` to disallow dragging in the `Y` axis. The dragged element will only move horizontally.
       * @config {Boolean}
       * @default
       */
      lockY: false,

      /**
       * The amount of milliseconds to wait after a touchstart, before a drag gesture will be allowed to start.
       * @config {Number}
       * @default
       */
      touchStartDelay: 300,
      // For the abort animation
      transitionDuration: 300,
      clickSwallowDuration: 50,
      ignoreSamePositionDrop: true,
      testConfig: {
        transitionDuration: 10,
        // In Edge synthetic click events are fired asynchronously and can take considerable
        // amount of time. Setting larger click swallow timeout to make sure Edge has time to fire click event.
        // Caught by test EventDrag4.t in Edge
        clickSwallowDuration: BrowserHelper.isEdge ? 300 : 50
      }
    };
  } //endregion
  //region Events

  /**
   * Fired before dragging starts, return false to prevent the drag operation.
   * @preventable
   * @event beforeDragStart
   * @param {Core.helper.DragHelper} source
   * @param {Object} context
   * @param {MouseEvent|TouchEvent} event
   */

  /**
   * Fired when dragging starts.
   * @event dragStart
   * @param {Core.helper.DragHelper} source
   * @param {Object} context
   * @param {MouseEvent|TouchEvent} event
   */

  /**
   * Fired while dragging, you can signal that the drop is valid or invalid by setting `context.valid = false;`
   * @event drag
   * @param {Core.helper.DragHelper} source
   * @param {Object} context
   * @param {Boolean} context.valid Set this to true or false to indicate whether the drop position is valid.
   * @param {MouseEvent} event
   */
  //endregion
  //region Init

  /**
   * Initializes a new DragHelper.
   * @param {Object} config Configuration object, accepts options specified under Configs above
   * @example
   * new DragHelper({
   *   containers: [div1, div2],
   *   isElementDraggable: element => element.className.contains('handle'),
   *   outerElement: topParent,
   *   listeners: {
   *     drop: onDrop,
   *     thisObj: this
   *   }
   * });
   * @function constructor
   */

  construct(config) {
    const me = this;
    super.construct(config);
    me.initListeners();

    if (me.mode === 'container') {
      me.initContainerDrag();
    } else if (me.mode.startsWith('translate')) {
      me.initTranslateDrag();
    }

    me.initialMode = me.mode;
  }

  doDestroy() {
    // Abort dragging
    this.abort(true);
    super.doDestroy();
  }

  get listenersTarget() {
    return document;
  }
  /**
   * Initialize listener
   * @private
   */

  initListeners() {
    const dragStartListeners = {
      element: this.outerElement,
      mousedown: documentListeners.down,
      thisObj: this
    };

    if (BrowserHelper.isTouchDevice) {
      dragStartListeners.touchstart = documentListeners.touchstart;
    } // These will be autoDetached upon destroy

    EventHelper.on(dragStartListeners);
  } //endregion
  //region Events

  /**
   * Fires after drop. For valid drops, it exposes `context.async` which you can set to true to signal that additional
   * processing is needed before finalizing the drop (such as showing some dialog). When that operation is done, call
   * `context.finalize(true/false)` with a boolean that determines the outcome of the drop.
   * @event drop
   * @param {Core.helper.DragHelper} dragHelper
   * @param {Object} context
   */

  onPointerDown(event) {
    let me = this,
        handled = false,
        isTouch = ('touches' in event); // If a drag is ongoing already, finalize it and don't proceed with new drag (happens if user does mouseup
    // outside browser window). Also handles the edge case of trying to start a new drag while previous is awaiting
    // finalization, in which case it just bails out.

    if (me.context) {
      if (!me.context.awaitingFinalization) {
        me.onMouseUp(event);
        me.reset();
      }

      return;
    }

    me.startEvent = event;
    if (me.isElementDraggable && !me.isElementDraggable(event.target, event)) return;

    if (me.mode) {
      if (!handled && me.mode === 'container') handled = me.grabContainerDrag(event);
      if (!handled && me.mode.startsWith('translate')) handled = me.grabTranslateDrag(event);
    }

    if (handled) {
      const dragListeners = {
        element: me.listenersTarget,
        thisObj: me,
        keydown: documentListeners.keydown
      };

      if (isTouch) {
        dragListeners.touchmove = {
          handler: documentListeners.touchmove,
          passive: false // We need to be able to preventDefault on the touchmove

        }; // Touch desktops don't fire touchend event when touch has ended, instead pointerup is fired
        // iOS do fire touchend

        dragListeners.touchend = dragListeners.pointerup = documentListeners.touchend;
      } else {
        dragListeners.mousemove = documentListeners.move;
        dragListeners.mouseup = documentListeners.up;
      } // A listener detacher is returned;

      me.removeListeners = EventHelper.on(dragListeners);

      if (me.dragWithin && me.dragWithin !== me.outerElement) {
        const box = me.dragWithin.getBoundingClientRect();
        me.minY = box.top;
        me.maxY = box.bottom;
        me.minX = box.left;
        me.maxX = box.right;
      }
    }
  }
  /**
   * @param event
   * @private
   */

  onTouchStart(event) {
    const me = this; // only allowing one finger for now...

    if (event.touches.length === 1) {
      me.touchStartTimer = me.setTimeout(() => {
        me.touchStartTimer = null;
      }, me.touchStartDelay, 'touchStartDelay');
      me.onPointerDown(event);
    }
  }
  /**
   * Grab draggable element on mouse down.
   * @private
   * @param event
   */

  onMouseDown(event) {
    // only dragging with left mouse button
    if (event.button === 0) {
      this.onPointerDown(event);
    }
  }

  internalMove(event) {
    const me = this,
          context = me.context,
          distance = EventHelper.getDistanceBetween(me.startEvent, event),
          abortTouchDrag = me.touchStartTimer && distance > me.dragThreshold;

    if (abortTouchDrag) {
      me.abort(true);
      return;
    }

    if (!me.touchStartTimer && context && context.element && // Only target Elements, not text nodes
    event.target && event.target.nodeType === Node.ELEMENT_NODE && (context.started || distance >= me.dragThreshold)) {
      if (!context.started) {
        // triggers beforeDragStart, dragStart. returning false from beforeDragStart aborts drag
        if (me.callPreventable('dragStart', {
          context,
          event
        }, () => {
          if (me.mode !== 'container') {
            if (me.lockY) {
              me.mode = 'translateX';
            } else if (me.lockX) {
              me.mode = 'translateY';
            } else {
              me.mode = me.initialMode;
            }
          }

          const direction = me.lockX ? 'vertical' : me.lockY ? 'horizontal' : 'both';

          if (context.action.startsWith('translate')) {
            me.startTranslateDrag(event);
          } else if (context.action === 'container') {
            me.startContainerDrag(event);
          }

          context.started = true; // Now that the drag drop is confirmed to be starting, activate the configured scrollManager if present

          if (me.scrollManager) {
            me.scrollManager.startMonitoring({
              direction: direction,
              element: me.dragWithin || me.outerElement,
              callback: config => {
                if (me.context.element && me.lastMouseMoveEvent) {
                  // Indicate that this is a 'fake' mousemove event as a result of the scrolling
                  me.lastMouseMoveEvent.isScroll = true;
                  me.update(me.lastMouseMoveEvent, config);
                }
              },
              thisObj: me
            });
          } // Global informational class for when DragHelper is dragging

          document.body.classList.add('b-draghelper-active');
        }) === false) {
          return me.abort();
        }
      } // to prevent view drag (scroll) on ipad

      if (event.type === 'touchmove') {
        event.preventDefault();
        event.stopImmediatePropagation();
      }

      me.update(event);
    }
  }

  onTouchMove(event) {
    this.internalMove(event);
  }
  /**
   * Move drag element with mouse.
   * @param event
   * @fires beforeDragStart
   * @fires dragStart
   * @private
   */

  onMouseMove(event) {
    this.internalMove(event);
  }
  /**
   * Updates drag, called when an element is grabbed and mouse moves
   * @private
   * @fires drag
   */

  update(event, scrollManagerConfig) {
    const me = this,
          context = me.context,
          draggingElement = context.dragProxy || context.element; // two different modes used

    let target = event.target,
        scrollingPageElement = document.scrollingElement || document.body; // "pointer-events:none" touchmove has no effect for the touchmove event target, meaning we cannot know
    // what's under the cursor as easily in touch devices

    if (event.type === 'touchmove') {
      const touch = event.changedTouches[0];
      target = DomHelper.elementFromPoint(touch.clientX + scrollingPageElement.scrollLeft, touch.clientY + scrollingPageElement.scrollTop);
    }

    context.target = target;

    if (me.dropTargetSelector) {
      context.valid = Boolean(target.closest(me.dropTargetSelector));
    } else {
      // assume valid drop location
      context.valid = true;
    } // Move the drag proxy or dragged element before triggering the drag event

    if (context.action) {
      if (context.action === 'container') {
        me.updateContainerProxy(event, scrollManagerConfig);
      }

      if (context.action.startsWith('translate')) {
        me.updateTranslateProxy(event, scrollManagerConfig);
      }
    } // Allow external code to validate the context before updating a container drag

    me.trigger('drag', {
      context,
      event
    }); // Move the placeholder element into its new place.
    // This will see the new state of context if mutated by a drag listener.

    if (context.action === 'container') {
      me.updateContainerDrag(event, scrollManagerConfig);
    } // change to toggle with force when not supporting IE11 any longer

    draggingElement.classList[context.valid ? 'remove' : 'add'](me.invalidCls);
    if (event) me.lastMouseMoveEvent = event;
  }
  /**
   * Abort dragging
   * @fires abort
   */

  abort(silent = false) {
    const me = this,
          context = me.context;
    me.scrollManager && me.scrollManager.stopMonitoring(me.dragWithin || me.outerElement);

    if (context) {
      // Force a synchronous layout so that transitions from this point will work.
      context.element.getBoundingClientRect(); // Aborted drag not considered valid

      context.valid = false;

      if (context.action === 'container') {
        me.abortContainerDrag(undefined, undefined, silent);
      } else {
        me.abortTranslateDrag(undefined, undefined, silent);
      }
    }

    me.reset();
  } // Empty class implementation. If listeners *are* added, the detacher is added
  // as an instance property. So this is always callable.

  removeListeners() {} // Called when a drag operation is completed, or aborted
  // Removes DOM listeners and resets context

  reset(silent) {
    document.body.classList.remove('b-draghelper-active');
    this.removeListeners();
    /**
     * Fired when a drag operation is completed or aborted
     * @event reset
     * @private
     * @param {Core.helper.DragHelper} dragHelper
     */

    if (!silent) {
      this.trigger('reset');
    }

    this.context = this.lastMouseMoveEvent = null;
  }

  onTouchEnd(event) {
    this.onMouseUp(event);
  }
  /**
   * This is a capture listener, only added during drag, which prevents a click gesture
   * propagating from the terminating mouseup gesture
   * @param {MouseEvent} event
   * @private
   */

  onDocumentClick(event) {
    event.stopPropagation();
  }
  /**
   * Drop on mouse up (if dropped on valid target).
   * @param event
   * @private
   */

  onMouseUp(event) {
    const me = this,
          context = me.context;
    me.removeListeners();

    if (context) {
      me.scrollManager && me.scrollManager.stopMonitoring(me.dragWithin || me.outerElement);

      if (context.action === 'container') {
        me.finishContainerDrag(event);
      } else if (context.started && context.action.startsWith('translate')) {
        me.finishTranslateDrag(event);
      }

      if (context.started) {
        // Prevent the impending document click from the mouseup event from propagating
        // into a click on our element.
        EventHelper.on({
          element: document,
          thisObj: me,
          click: documentListeners.docclick,
          capture: true,
          expires: me.clickSwallowDuration,
          // In case a click did not ensue, remove the listener
          once: true
        });
      } else {
        me.reset(true);
      }
    }
  }
  /**
   * Cancel on ESC key
   * @param event
   * @private
   */

  onKeyDown(event) {
    if (event.key === 'Escape') this.abort();
  }
  /**
   * Creates the proxy element to be dragged, when using {@link #config-cloneTarget}. Clones the original element by default
   */

  createProxy(element) {
    const clone = element.cloneNode(true);
    clone.removeAttribute('id');
    return clone;
  } //endregion

}
DragHelper._$name = 'DragHelper';

/**
 * @module Core/helper/util/RandomGenerator
 */

/**
 * Generates pseudo random numbers from predefined sequence of 100 numbers
 */
class RandomGenerator {
  constructor() {
    this.random100 = [46, 2, 36, 46, 54, 59, 18, 20, 71, 55, 88, 98, 13, 61, 61, 40, 2, 15, 3, 32, 51, 45, 64, 25, 81, 85, 54, 13, 57, 49, 64, 22, 81, 94, 0, 62, 17, 7, 11, 2, 33, 99, 85, 26, 83, 83, 96, 26, 20, 89, 91, 38, 26, 13, 11, 79, 32, 30, 5, 51, 70, 7, 5, 56, 58, 77, 37, 89, 40, 80, 78, 59, 26, 36, 8, 51, 60, 23, 86, 5, 11, 96, 64, 94, 87, 64, 4, 78, 17, 85, 35, 0, 90, 86, 23, 55, 53, 9, 35, 59, 29, 2, 64, 42, 8, 49, 43, 73, 6, 53, 38, 9, 39, 31, 32, 40, 49, 13, 78, 68, 20, 99, 24, 78, 35, 91, 73, 46, 67, 76, 89, 69, 30, 69, 25, 3, 4, 55, 1, 65, 66, 76, 83, 19, 67, 1, 95, 24, 54, 45, 56, 40, 67, 92, 72, 4, 69, 8, 47, 50, 27, 2, 38, 9, 14, 83, 12, 14, 62, 95, 22, 47, 35, 18, 38, 14, 86, 64, 68, 61, 52, 69, 39, 93, 20, 73, 32, 52, 74, 6, 56, 68, 99, 29, 24, 92, 40, 67, 6, 72, 31, 41, 91, 53, 80, 55, 33, 97, 97, 99, 18, 20, 5, 27, 82, 84, 61, 78, 27, 67, 7, 42, 75, 95, 91, 25, 63, 21, 70, 36, 46, 0, 1, 45, 84, 6, 86, 15, 10, 62, 96, 94, 10, 23, 93, 83, 94, 47, 5, 29, 29, 52, 51, 37, 77, 96, 43, 72, 43, 14, 54, 14, 72, 52, 4, 39, 15, 26, 68, 28, 25, 76, 60, 50, 22, 40, 72, 74, 68, 58, 8, 48, 40, 62, 52, 24, 9, 26, 47, 44, 49, 96, 7, 77, 90, 45, 76, 47, 5, 86, 1, 36, 18, 42, 19, 90, 34, 23, 70, 32, 69, 79, 0, 99, 57, 80, 72, 21, 19, 72, 85, 68, 4, 40, 86, 62, 0, 63, 4, 11, 69, 31, 78, 31, 21, 78, 29, 84, 13, 53, 57, 10, 26, 50, 24, 30, 90, 42, 51, 96, 93, 21, 99, 23, 81, 0, 89, 43, 86, 63, 93, 19, 54, 71, 92, 36, 4, 95, 37, 99, 60, 29, 23, 50, 68, 95, 57, 95, 77, 53, 99, 78, 75, 12, 92, 47, 23, 14, 0, 41, 98, 11, 34, 64, 26, 90, 50, 23, 38, 31, 74, 76, 16, 76, 66, 23, 22, 72, 48, 50, 20, 36, 37, 58, 5, 43, 49, 64, 81, 30, 8, 21, 98, 75, 60, 17, 50, 42, 27, 38, 90, 74, 45, 68, 67, 27, 31, 15, 58, 76, 41, 99, 23, 98, 53, 98, 56, 19, 79, 2, 4, 38, 96, 24, 65, 51, 43, 42, 41, 60, 46, 7, 90, 65, 3, 27, 63, 99, 51, 44, 86, 1, 54, 40, 15, 74, 3, 81, 51, 63, 87, 79, 84, 72, 22, 38, 96, 95, 33, 41, 21, 99, 21, 69, 7, 49, 40, 52, 41, 6, 91, 19, 76, 40, 54, 17, 33, 11, 11, 0, 1, 32, 94, 33, 13, 18, 45, 7, 85, 61, 42, 54, 45, 72, 78, 96, 17, 9, 80, 87, 41, 96, 66, 0, 8, 59, 18, 21, 2, 28, 64, 75, 97, 32, 80, 86, 97, 97, 55, 2, 73, 75, 11, 89, 67, 58, 70, 76, 12, 46, 64, 17, 22, 97, 25, 35, 93, 57, 82, 46, 57, 61, 31, 74, 27, 4, 32, 85, 53, 86, 53, 53, 42, 5, 28, 50, 65, 63, 70, 61, 73, 37, 13, 80, 7, 34, 22, 3, 26, 6, 62, 78, 12, 56, 87, 41, 58, 64, 31, 27, 45, 35, 18, 66, 62, 43, 89, 69, 94, 93, 33, 74, 2, 43, 85, 37, 82, 41, 74, 9, 15, 44, 33, 42, 65, 19, 1, 49, 78, 12, 29, 9, 78, 7, 55, 12, 45, 40, 33, 16, 86, 14, 52, 16, 73, 76, 0, 98, 75, 91, 78, 46, 99, 95, 90, 69, 78, 45, 62, 55, 37, 88, 49, 77, 27, 83, 38, 73, 39, 1, 75, 40, 65, 83, 54, 95, 7, 73, 4, 30, 26, 36, 89, 21, 5, 95, 11, 14, 87, 45, 36, 21, 77, 55, 5, 66, 51, 98, 48, 62, 74, 58, 23, 82, 30, 28, 19, 53, 89, 76, 98, 8, 34, 70, 28, 54, 16, 52, 35, 93, 54, 54, 72, 49, 18, 93, 72, 90, 71, 73, 15, 60, 38, 80, 76, 53, 70, 39, 69, 25, 5, 31, 61, 46, 6, 54, 34, 31, 52, 33, 36, 79, 76, 44, 29, 28, 38, 1, 66, 2, 90, 91, 1, 76, 78, 31, 55, 37, 71, 2, 3, 38, 85, 0, 95, 42, 2, 39, 57, 87, 61, 77, 98, 2, 24, 80, 48, 27, 47, 71, 15, 7, 49, 60, 86, 3, 2, 29, 38, 54, 36, 59, 83, 27, 47, 9, 36, 42, 8, 73, 85, 9, 16, 73, 60, 39, 12, 43, 25, 23, 29, 28, 47, 40, 77, 20, 89, 22, 30, 41, 59, 96, 19, 56, 20, 76, 73, 39, 46, 72, 40, 47, 37, 52, 29, 79, 37, 39, 50, 41, 87, 66, 17, 75, 31, 45, 26, 88, 70, 11, 90, 40, 74, 9, 32, 65, 72, 61, 6, 93, 54, 15, 84, 22, 99, 47, 10, 96, 4, 84, 19, 85, 73, 45, 25, 16, 8, 94, 99, 39, 28, 26, 68, 87, 48, 1, 65, 86, 46, 86, 7, 60, 82, 45, 75, 38, 56, 41, 35, 30, 86, 91, 97, 85, 45, 5, 14, 69, 85, 96, 37, 18, 26, 16, 38, 16, 1, 44, 94, 85, 58, 60, 20, 5, 47, 52, 41, 50, 71, 43, 42, 67, 64, 38, 65, 83, 99, 78, 96, 33, 20, 98, 24, 6, 2, 25, 16, 16, 44, 63, 24, 68, 56, 49, 91, 15, 59, 99, 27, 43, 34, 28, 36, 45, 1, 10, 19, 54, 26, 75, 17, 88, 96, 63, 24, 71, 93, 72, 97, 66, 87, 18, 86];
    this.randomCache = {};
    this.rndIndex = 0;
  }
  /**
   * Returns next pseudo random integer number from sequence between 0 and max parameter value (99 is maximum value)
   * @param {Number} max max value
   * @returns {Number}
   */

  nextRandom(max) {
    let randomCache = this.randomCache,
        randomNumbers;

    if (randomCache[max]) {
      randomNumbers = randomCache[max];
    } else {
      randomNumbers = this.random100.filter(num => num < max);
      randomCache[max] = randomNumbers;
    }

    return randomNumbers[this.rndIndex++ % randomNumbers.length];
  }
  /**
   * Resets sequence to initial number
   */

  reset() {
    this.rndIndex = 0;
  }
  /**
   * Returns pseudo random array element
   * @param {Array} array input array
   * @returns {*}
   */

  fromArray(array) {
    return array[this.nextRandom(array.length)];
  }

}
RandomGenerator._$name = 'RandomGenerator';

const documentListeners$1 = {
  down: 'onMouseDown',
  move: 'onMouseMove',
  up: 'onMouseUp',
  docclick: 'onDocumentClick',
  touchstart: 'onTouchStart',
  touchmove: 'onTouchMove',
  touchend: 'onTouchEnd',
  keydown: 'onKeyDown'
};
/**
 * @module Core/helper/ResizeHelper
 */

/**
 * Handles resizing of elements using handles. Handles can be actual elements or virtual handles specified as a border
 * area on elements left and right edges.
 *
 * ```
 * // enable resizing all elements with class 'resizable'
 * let resizer = new ResizeHelper({
 *   targetSelector: '.resizable'
 * });
 * ```
 *
 * @mixes Core/mixin/Events
 * @internal
 */

class ResizeHelper extends Events(Base) {
  //region Config
  static get defaultConfig() {
    return {
      /**
       * CSS class added when resizing
       * @config {String}
       * @default
       */
      resizingCls: 'b-resizing',

      /**
       * The amount of pixels to move mouse before it counts as a drag operation
       * @config {Number}
       * @default
       */
      dragThreshold: 5,

      /**
       * Resizing handle size
       * @config {Number}
       * @default
       */
      handleSize: 10,

      /**
       * Automatically shrink virtual handles when available space < handleSize. The virtual handles will
       * decrease towards width/height 1, reserving space between opposite handles to for example leave room for
       * dragging. To configure reserved space, see {@link #config-reservedSpace}.
       * @config {Boolean}
       * @default false
       */
      dynamicHandleSize: null,
      //

      /**
       * Room in px to leave unoccupied by handles when shrinking them dynamically (see
       * {@link #config-dynamicHandleSize}).
       * @config {Number}
       * @default
       */
      reservedSpace: 10,

      /**
       * Resizing handle size on touch devices
       * @config {Number}
       * @default
       */
      touchHandleSize: 30,

      /**
       * Minimum width when resizing
       * @config {Number}
       * @default
       */
      minWidth: 1,

      /**
       * Max width when resizing.
       * @config {Number}
       * @default
       */
      maxWidth: 0,

      /**
       * Minimum height when resizing
       * @config {Number}
       * @default
       */
      minHeight: 1,

      /**
       * Max height when resizing
       * @config {Number}
       * @default
       */
      maxHeight: 0,
      // outerElement, attach events to it and use as outer limit when looking for ancestors
      outerElement: document.body,

      /**
       * Optional scroller used to read scroll position. If unspecified, the outer element will be used.
       * @config {Core.helper.util.Scroller}
       */
      scroller: null,

      /**
       * Assign a function to determine if a hovered element can be resized or not
       * @config {Function}
       * @default
       */
      allowResize: null,

      /**
       * Outer element that limits where element can be dragged
       * @config {HTMLElement}
       * @default
       */
      dragWithin: null,

      /**
       * A function that determines if dragging an element is allowed. Gets called with the element as argument,
       * return true to allow dragging or false to prevent.
       * @config {Function}
       * @default
       */
      isElementResizable: null,

      /**
       * A CSS selector used to determine if resizing an element is allowed.
       * @config {String}
       * @default
       */
      targetSelector: null,

      /**
       * Use left handle when resizing. Only applies when `direction` is 'horizontal'
       * @config {Boolean}
       * @default
       */
      leftHandle: true,

      /**
       * Use right handle when resizing. Only applies when `direction` is 'horizontal'
       * @config {Boolean}
       * @default
       */
      rightHandle: true,

      /**
       * Use top handle when resizing. Only applies when `direction` is 'vertical'
       * @config {Boolean}
       * @default
       */
      topHandle: true,

      /**
       * Use bottom handle when resizing. Only applies when `direction` is 'vertical'
       * @config {Boolean}
       * @default
       */
      bottomHandle: true,

      /**
       * A CSS selector used to determine where handles should be "displayed" when resizing. Defaults to
       * targetSelector if unspecified
       * @config {String}
       * @default
       */
      handleSelector: null,

      /**
       * A CSS selector used to determine which inner element contains handles.
       * @config {String}
       * @default
       */
      handleContainerSelector: null,
      startEvent: null,

      /*
       * Optional config object, used by EventResize feature: it appends proxy and has to start resizing immediately
       * @config {Object}
       * @private
       */
      grab: null,

      /**
       * CSS class added when the resize state is invalid
       * @config {String}
       * @default
       */
      invalidCls: 'b-resize-invalid',
      // A number that controls whether or not the element is wide enough for it to make sense to show resize handles
      // e.g. handle width is 10px, so doesn't make sense to show them unless handles on both sides fit
      handleVisibilityThreshold: null,
      // Private config that disables translation when resizing left edge. Useful for example in cases when element
      // being resized is part of a flex layout
      skipTranslate: false,

      /**
       * Direction to resize in, either 'horizontal' or 'vertical'
       * @config {String}
       * @default
       */
      direction: 'horizontal',
      clickSwallowDuration: 50
    };
  } //endregion
  //region Events

  /**
   * Fired while dragging
   * @event resizing
   * @param {Core.helper.ResizeHelper} source
   * @param {Object} context Resize context
   * @param {MouseEvent} event Browser event
   */

  /**
   * Fired when dragging starts.
   * @event resizeStart
   * @param {Core.helper.ResizeHelper} source
   * @param {Object} context Resize context
   * @param {MouseEvent|TouchEvent} event Browser event
   */

  /**
   * Fires after resize, and allows for asynchronous finalization by setting 'async' to `true` on the context object.
   * @event resize
   * @param {Core.helper.ResizeHelper} source
   * @param {Object} context Context about the resize operation. Set 'async' to `true` to indicate asynchronous validation of the resize flow (for showing a confirmation dialog etc)
   */

  /**
   * Fires when a resize is canceled (width is unchanged)
   * @event cancel
   * @param {Core.helper.ResizeHelper} source
   * @param {Object} context Resize context
   * @param {MouseEvent|TouchEvent} event Browser event
   */
  //endregion
  //region Init

  construct(config) {
    const me = this;
    super.construct(config); // Larger grabbable zones on touch devices

    if (!me.handleSelector && BrowserHelper.isTouchDevice) {
      me.handleSize = me.touchHandleSize;
    }

    me.handleVisibilityThreshold = me.handleVisibilityThreshold || 2 * me.handleSize;
    me.initListeners();
    me.initResize();
  }

  doDestroy() {
    this.abort(true);
    super.doDestroy();
  }
  /**
   * Initializes resizing
   * @private
   */

  initResize() {
    const me = this;

    if (!me.isElementResizable && me.targetSelector) {
      me.isElementResizable = element => DomHelper.up(element, me.targetSelector);
    }

    if (me.grab) {
      const {
        edge,
        element,
        event
      } = me.grab;
      me.startEvent = event;
      const cursorOffset = me.getCursorOffsetToElementEdge(event, element, edge); // emulates mousedown & grabResize

      me.context = {
        element,
        edge,
        valid: true,
        async: false,
        elementStartX: DomHelper.getTranslateX(element) || element.offsetLeft,
        // extract x from translate
        elementStartY: DomHelper.getTranslateY(element) || element.offsetTop,
        // extract x from translate
        newX: DomHelper.getTranslateX(element) || element.offsetLeft,
        // No change yet on start, but info must be present
        newY: DomHelper.getTranslateY(element) || element.offsetTop,
        // No change yet on start, but info must be present
        elementWidth: element.offsetWidth,
        elementHeight: element.offsetHeight,
        cursorOffset,
        startX: event.clientX + cursorOffset.x + me.scrollLeft,
        startY: event.clientY + cursorOffset.y + me.scrollTop,
        finalize: () => {
          var _me$reset;

          return (_me$reset = me.reset) === null || _me$reset === void 0 ? void 0 : _me$reset.call(me);
        }
      };
      element.classList.add(me.resizingCls);
      me.internalStartResize(me.isTouch);
    }
  }
  /**
   * Initialize listeners
   * @private
   */

  initListeners() {
    const me = this,
          dragStartListeners = {
      element: me.outerElement,
      mousedown: documentListeners$1.down,
      thisObj: me
    };

    if (BrowserHelper.isTouchDevice) {
      dragStartListeners.touchstart = documentListeners$1.touchstart;
    } else if (!me.handleSelector) {
      dragStartListeners.mousemove = {
        handler: documentListeners$1.move,
        // Filter events for checkResizeHandles so we only get called if the mouse
        // is over one of our targets.
        delegate: me.targetSelector
      }; // We need to clean up when we exit one of our targets

      dragStartListeners.mouseleave = {
        handler: 'onMouseLeaveTarget',
        delegate: me.targetSelector,
        capture: true
      };
    } // These will be autoDetached upon destroy

    me.removeListeners = EventHelper.on(dragStartListeners);
  }

  removeListeners() {} //endregion
  //region Scroll helpers

  get scrollLeft() {
    if (this.scroller) {
      return this.scroller.x;
    }

    return this.outerElement.scrollLeft;
  }

  get scrollTop() {
    if (this.scroller) {
      return this.scroller.y;
    }

    return this.outerElement.scrollTop;
  } //endregion
  //region Events

  internalStartResize(isTouch) {
    const me = this,
          dragListeners = {
      element: document,
      keydown: documentListeners$1.keydown,
      thisObj: me
    };

    if (isTouch) {
      dragListeners.touchmove = documentListeners$1.touchmove; // Touch desktops don't fire touchend event when touch has ended, instead pointerup is fired
      // iOS do fire touchend

      dragListeners.touchend = dragListeners.pointerup = documentListeners$1.touchend;
    } else {
      dragListeners.mousemove = documentListeners$1.move;
      dragListeners.mouseup = documentListeners$1.up;
    } // A listener detacher is returned;

    me.removeDragListeners = EventHelper.on(dragListeners);
  } // Empty class implementation. If listeners *are* added, the detacher is added
  // as an instance property. So this is always callable.

  removeDragListeners() {}

  reset() {
    var _this$removeDragListe;

    (_this$removeDragListe = this.removeDragListeners) === null || _this$removeDragListe === void 0 ? void 0 : _this$removeDragListe.call(this);
    this.context = null;
  }

  onPointerDown(isTouch, event) {
    const me = this;
    me.startEvent = event;

    if (!me.isElementResizable || me.isElementResizable(event.target, event)) {
      if (me.grabResizeHandle(isTouch, event)) {
        // Stop event if resize handle was grabbed (resize started)
        event.stopImmediatePropagation();
        me.internalStartResize(isTouch);
      }
    }
  }

  onTouchStart(event) {
    // only allowing one finger for now...
    if (event.touches.length > 1) {
      return;
    }

    this.onPointerDown(true, event);
  }
  /**
   * Grab draggable element on mouse down.
   * @private
   * @param event
   */

  onMouseDown(event) {
    // only dragging with left mouse button
    if (event.button !== 0) {
      return;
    }

    this.onPointerDown(false, event);
  }

  internalMove(isTouch, event) {
    const me = this,
          context = me.context;

    if (context && context.element && (context.started || EventHelper.getDistanceBetween(me.startEvent, event) >= me.dragThreshold)) {
      if (!context.started) {
        me.scrollManager && me.scrollManager.startMonitoring({
          direction: me.direction,
          // TODO Update this then when we add support for vertical resizing too
          element: me.dragWithin || me.outerElement,
          callback: config => me.context && me.context.element && me.lastMouseMoveEvent && me.update(me.lastMouseMoveEvent, config),
          thisObj: me
        });
        me.trigger('resizeStart', {
          context,
          event
        });
        context.started = true;
      }

      me.update(event);
    } // If a mousemove, and we are using zones, and not handles, we have to
    // programatically check whether we are over a handle, and add/remove
    // classes to change the mouse cursor to resize.
    // If we are using handles, their CSS will set the mouse cursor.
    else if (!isTouch && !me.handleSelector) {
        me.checkResizeHandles(event);
      }
  }

  onTouchMove(event) {
    this.internalMove(true, event);
  }
  /**
   * Move grabbed element with mouse.
   * @param event
   * @fires resizestart
   * @private
   */

  onMouseMove(event) {
    this.internalMove(false, event);
  }

  onPointerUp(isTouch, event) {
    var _me$removeDragListene;

    const me = this,
          context = me.context;
    (_me$removeDragListene = me.removeDragListeners) === null || _me$removeDragListene === void 0 ? void 0 : _me$removeDragListene.call(me);

    if (context) {
      me.scrollManager && me.scrollManager.stopMonitoring(me.dragWithin || me.outerElement);

      if (context.started) {
        // Prevent the impending document click from the mouseup event from propagating
        // into a click on our element.
        EventHelper.on({
          element: document,
          thisObj: me,
          click: documentListeners$1.docclick,
          expires: me.clickSwallowDuration,
          // In case a click did not ensue, remove the listener
          capture: true,
          once: true
        });
      }

      me.finishResize(event);
    } else {
      var _me$reset2;

      (_me$reset2 = me.reset) === null || _me$reset2 === void 0 ? void 0 : _me$reset2.call(me);
    }
  }

  onTouchEnd(event) {
    this.onPointerUp(true, event);
  }
  /**
   * Drop on mouse up (if dropped on valid target).
   * @param event
   * @private
   */

  onMouseUp(event) {
    this.onPointerUp(false, event);
  }
  /**
   * This is a capture listener, only added during drag, which prevents a click gesture
   * propagating from the terminating mouseup geature
   * @param {MouseEvent} event
   * @private
   */

  onDocumentClick(event) {
    event.stopPropagation();
  }
  /**
   * Cancel on ESC key
   * @param event
   * @private
   */

  onKeyDown(event) {
    if (event.key === 'Escape') {
      this.abort();
    }
  } //endregion
  //region Grab, update, finish

  /**
   * Updates resize, called when an element is grabbed and mouse moves
   * @private
   * @fires resizing
   */

  update(event) {
    const me = this,
          context = me.context,
          parentRectangle = Rectangle.from(me.outerElement); // Calculate the current pointer X. Do not allow overflowing either edge

    context.currentX = Math.max(Math.min(event.clientX + context.cursorOffset.x, parentRectangle.right), parentRectangle.x) + me.scrollLeft;
    context.currentY = Math.max(Math.min(event.clientY + context.cursorOffset.y, parentRectangle.bottom), parentRectangle.y) + me.scrollTop;
    me.updateResize(event);
    me.trigger('resizing', {
      context,
      event
    });
    context.element.classList[context.valid === false ? 'add' : 'remove'](me.invalidCls); // When IE11 support is dropped
    // context.grabbed.classList.toggle(me.invalidCls, context.valid === false);

    if (event) {
      me.lastMouseMoveEvent = event;
    }
  }
  /**
   * Abort dragging
   */

  abort(silent = false) {
    const me = this;
    me.scrollManager && me.scrollManager.stopMonitoring(me.dragWithin || me.outerElement);

    if (me.context) {
      me.abortResize(null, silent);
    } else if (!me.isDestroyed) {
      me.reset();
    }
  }
  /**
   * Starts resizing, updates ResizeHelper#context with relevant info.
   * @private
   * @param {Boolean} isTouch
   * @param {MouseEvent} event
   * @returns {Boolean} True if handled, false if not
   */

  grabResizeHandle(isTouch, event) {
    const me = this;

    if (me.allowResize && !me.allowResize(event.target, event)) {
      return false;
    }

    const handleSelector = me.handleSelector,
          coordsFrom = event.type === 'touchstart' ? event.changedTouches[0] : event,
          clientX = coordsFrom.clientX,
          clientY = coordsFrom.clientY,
          // go up from "handle" to resizable element
    element = me.targetSelector ? DomHelper.up(event.target, me.targetSelector) : event.target;

    if (element) {
      let edge; // Calculate which edge to resize
      // If there's a handle selector, see if it's anchored on the left or the right

      if (handleSelector) {
        if (event.target.matches(handleSelector)) {
          if (me.direction === 'horizontal') {
            if (event.pageX < DomHelper.getPageX(element) + element.offsetWidth / 2) {
              edge = 'left';
            } else {
              edge = 'right';
            }
          } else {
            if (event.pageY < DomHelper.getPageY(element) + element.offsetHeight / 2) {
              edge = 'top';
            } else {
              edge = 'bottom';
            }
          }
        } else {
          return false;
        }
      } // If we're not using handles, but just active zones
      // then test whether the event position is in an active resize zone.
      else {
          if (me.direction === 'horizontal') {
            if (me.overLeftHandle(event, element)) {
              edge = 'left';
            } else if (me.overRightHandle(event, element)) {
              edge = 'right';
            }
          } else {
            if (me.overTopHandle(event, element)) {
              edge = 'top';
            } else if (me.overBottomHandle(event, element)) {
              edge = 'bottom';
            }
          }

          if (!edge) {
            me.context = null; // not over an edge, abort

            return false;
          }
        } // If resizing is initiated by a touch, we must preventDefault on the touchstart
      // so that scrolling is not invoked when dragging. This is in lieu of a functioning
      // touch-action style on iOS Safari. When that's fixed, this will not be needed.

      if (event.type === 'touchstart') {
        event.preventDefault();
      }

      const cursorOffset = me.getCursorOffsetToElementEdge(coordsFrom, element, edge);

      if (me.trigger('beforeResizeStart', {
        element,
        event
      }) !== false) {
        // store initial size
        me.context = {
          element,
          edge,
          valid: true,
          async: false,
          direction: me.direction,
          isTouch: isTouch,
          elementStartX: DomHelper.getTranslateX(element) || element.offsetLeft,
          // extract x from translate
          elementStartY: DomHelper.getTranslateY(element) || element.offsetTop,
          // extract y from translate
          newX: DomHelper.getTranslateX(element) || element.offsetLeft,
          // No change yet on start, but info must be present
          newY: DomHelper.getTranslateY(element) || element.offsetTop,
          // No change yet on start, but info must be present
          elementWidth: element.offsetWidth,
          elementHeight: element.offsetHeight,
          cursorOffset,
          startX: clientX + cursorOffset.x + me.scrollLeft,
          startY: clientY + cursorOffset.y + me.scrollTop,
          finalize: () => {
            var _me$reset3;

            return (_me$reset3 = me.reset) === null || _me$reset3 === void 0 ? void 0 : _me$reset3.call(me);
          }
        };
        element.classList.add(me.resizingCls);
        return true;
      }
    }

    return false;
  }

  getCursorOffsetToElementEdge(event, element, edge) {
    const rectEl = Rectangle.from(element);
    let x = 0,
        y = 0;

    switch (edge) {
      case 'left':
        x = rectEl.x - event.clientX; // negative

        break;

      case 'right':
        x = rectEl.x + rectEl.width - event.clientX; // positive

        break;

      case 'top':
        y = rectEl.y - event.clientY; // negative

        break;

      case 'bottom':
        y = rectEl.y + rectEl.height - event.clientY; // positive

        break;
    }

    return {
      x,
      y
    };
  }
  /**
   * Check if mouse is over a resize handle (virtual). If so, highlight.
   * @private
   * @param event
   */

  checkResizeHandles(event) {
    const me = this,
          target = me.targetSelector ? DomHelper.up(event.target, me.targetSelector) : event.target; // mouse over a target element and allowed to resize?

    if (target && (!me.allowResize || me.allowResize(event.target, event))) {
      me.currentElement = me.handleContainerSelector ? DomHelper.up(event.target, me.handleContainerSelector) : event.target;

      if (me.currentElement) {
        let over = false;

        if (me.direction === 'horizontal') {
          over = me.overLeftHandle(event, target) || me.overRightHandle(event, target);
        } else {
          over = me.overTopHandle(event, target) || me.overBottomHandle(event, target);
        }

        if (over) {
          me.highlightHandle(); // over handle
        } else {
          me.unHighlightHandle(); // not over handle
        }
      }
    } else if (me.currentElement) {
      me.unHighlightHandle(); // outside element
    }
  }

  onMouseLeaveTarget(event) {
    const me = this;
    me.currentElement = me.handleContainerSelector ? DomHelper.up(event.target, me.handleContainerSelector) : event.target;

    if (me.currentElement) {
      me.unHighlightHandle();
    }
  }
  /**
   * Updates size of target (on mouse move).
   * @private
   * @param event
   */

  updateResize(event) {
    const me = this,
          context = me.context; // flip which edge is being dragged depending on whether we're to the right or left of the mousedown

    if (me.allowEdgeSwitch) {
      if (me.direction === 'horizontal') {
        context.edge = context.currentX > context.startX ? 'right' : 'left';
      } else {
        context.edge = context.currentY > context.startY ? 'bottom' : 'top';
      }
    }

    const // limit to outerElement if set
    deltaX = context.currentX - context.startX,
          deltaY = context.currentY - context.startY,
          minWidth = DomHelper.getExtremalSizePX(context.element, 'minWidth') || me.minWidth,
          maxWidth = DomHelper.getExtremalSizePX(context.element, 'maxWidth') || me.maxWidth,
          minHeight = DomHelper.getExtremalSizePX(context.element, 'minHeight') || me.minHeight,
          maxHeight = DomHelper.getExtremalSizePX(context.element, 'maxHeight') || me.maxHeight,
          // dragging right edge right increases width, dragging left edge right decreases width
    sign = context.edge === 'right' || context.edge === 'bottom' ? 1 : -1,
          // new width, not allowed to go below minWidth
    newWidth = context.elementWidth + deltaX * sign,
          newHeight = context.elementHeight + deltaY * sign;
    let width = Math.max(minWidth, newWidth),
        height = Math.max(minHeight, newHeight);

    if (maxWidth > 0) {
      width = Math.min(width, maxWidth);
    }

    if (maxHeight > 0) {
      height = Math.min(height, maxHeight);
    } // remove flex when resizing

    if (context.element.style.flex) {
      context.element.style.flex = '';
    }

    if (me.direction === 'horizontal') {
      context.element.style.width = Math.abs(width) + 'px';
      context.newWidth = width; // when dragging left edge, also update position (so that right edge remains in place)

      if (context.edge === 'left' || width < 0) {
        context.newX = Math.max(Math.min(context.elementStartX + context.elementWidth - me.minWidth, context.elementStartX + deltaX), 0);

        if (!me.skipTranslate) {
          DomHelper.setTranslateX(context.element, context.newX);
        }
      } // When dragging the right edge and we're allowed to flip the drag from left to right
      // through the start point (eg drag event creation) the element must be at its initial X position
      else if (context.edge === 'right' && me.allowEdgeSwitch && !me.skipTranslate) {
          DomHelper.setTranslateX(context.element, context.elementStartX);
        }
    } else {
      context.element.style.height = Math.abs(height) + 'px';
      context.newHeight = height; // when dragging top edge, also update position (so that bottom edge remains in place)

      if (context.edge === 'top' || height < 0) {
        context.newY = Math.max(Math.min(context.elementStartY + context.elementHeight - me.minHeight, context.elementStartY + deltaY), 0);

        if (!me.skipTranslate) {
          DomHelper.setTranslateY(context.element, context.newY);
        }
      } // When dragging the bottom edge and we're allowed to flip the drag from top to bottom
      // through the start point (eg drag event creation) the element must be at its initial Y position
      else if (context.edge === 'bottom' && me.allowEdgeSwitch && !me.skipTranslate) {
          DomHelper.setTranslateY(context.element, context.elementStartY);
        }
    }
  }
  /**
   * Finalizes resize, fires drop.
   * @private
   * @param event
   * @fires resize
   * @fires cancel
   */

  finishResize(event) {
    const me = this,
          context = me.context,
          eventObject = {
      context,
      event
    };
    context.element.classList.remove(me.resizingCls);

    if (context.started) {
      let changed = false;

      if (me.direction === 'horizontal') {
        changed = context.newWidth && context.newWidth !== context.elementWidth;
      } else {
        changed = context.newHeight && context.newHeight !== context.elementHeight;
      }

      me.trigger(changed ? 'resize' : 'cancel', eventObject);

      if (!context.async) {
        context.finalize();
      }
    } else {
      var _me$reset4;

      (_me$reset4 = me.reset) === null || _me$reset4 === void 0 ? void 0 : _me$reset4.call(me);
    }
  }
  /**
   * Abort resizing
   * @private
   * @fires cancel
   */

  abortResize(event = null, silent = false) {
    const me = this,
          context = me.context;
    context.element.classList.remove(me.resizingCls);

    if (me.direction === 'horizontal') {
      DomHelper.setTranslateX(context.element, context.elementStartX);
      context.element.style.width = context.elementWidth + 'px';
    } else {
      DomHelper.setTranslateY(context.element, context.elementStartY);
      context.element.style.height = context.elementHeight + 'px';
    }

    !silent && me.trigger('cancel', {
      context,
      event
    });

    if (!me.isDestroyed) {
      me.reset();
    }
  } //endregion
  //region Handles
  // /**
  //  * Constrain resize to outerElements bounds
  //  * @private
  //  * @param x
  //  * @returns {*}
  //  */
  // constrainResize(x) {
  //     const me = this;
  //
  //     if (me.outerElement) {
  //         const box = me.outerElement.getBoundingClientRect();
  //         if (x < box.left) x = box.left;
  //         if (x > box.right) x = box.right;
  //     }
  //
  //     return x;
  // }

  /**
   * Highlights handles (applies css that changes cursor).
   * @private
   */

  highlightHandle() {
    const me = this,
          target = me.targetSelector ? DomHelper.up(me.currentElement, me.targetSelector) : me.currentElement; // over a handle, add cls to change cursor

    me.currentElement.classList.add('b-resize-handle');
    target.classList.add('b-over-resize-handle');
  }
  /**
   * Unhighlight handles (removes css).
   * @private
   */

  unHighlightHandle() {
    const me = this,
          target = me.targetSelector ? DomHelper.up(me.currentElement, me.targetSelector) : me.currentElement;
    target && target.classList.remove('b-over-resize-handle');
    me.currentElement.classList.remove('b-resize-handle');
    me.currentElement = null;
  }

  overAnyHandle(event, target) {
    return this.overStartHandle(event, target) || this.overEndHandle(event, target);
  }

  overStartHandle(event, target) {
    return this.direction === 'horizontal' ? this.overLeftHandle(event, target) : this.overTopHandle(event, target);
  }

  overEndHandle(event, target) {
    return this.direction === 'horizontal' ? this.overRightHandle(event, target) : this.overBottomHandle(event, target);
  }

  getDynamicHandleSize(opposite, offsetWidth) {
    const handleCount = opposite ? 2 : 1,
          {
      handleSize
    } = this; // Shrink handle size when configured to do so, preserving reserved space between handles

    if (this.dynamicHandleSize && handleSize * handleCount > offsetWidth - this.reservedSpace) {
      return Math.max(Math.floor((offsetWidth - this.reservedSpace) / handleCount), 0);
    }

    return handleSize;
  }
  /**
   * Check if over left handle (virtual).
   * @private
   * @param {MouseEvent} event MouseEvent
   * @param {HTMLElement} target The current target element
   * @returns {Boolean} Returns true if mouse is over left handle, otherwise false
   */

  overLeftHandle(event, target) {
    const me = this,
          {
      offsetWidth
    } = target;

    if (me.leftHandle && (offsetWidth >= me.handleVisibilityThreshold || me.dynamicHandleSize)) {
      const leftHandle = Rectangle.from(target);
      leftHandle.width = me.getDynamicHandleSize(me.rightHandle, offsetWidth);
      return leftHandle.width > 0 && leftHandle.contains(EventHelper.getPagePoint(event));
    }

    return false;
  }
  /**
   * Check if over right handle (virtual).
   * @private
   * @param {MouseEvent} event MouseEvent
   * @param {HTMLElement} target The current target element
   * @returns {Boolean} Returns true if mouse is over left handle, otherwise false
   */

  overRightHandle(event, target) {
    const me = this,
          {
      offsetWidth
    } = target;

    if (me.rightHandle && (offsetWidth >= me.handleVisibilityThreshold || me.dynamicHandleSize)) {
      const rightHandle = Rectangle.from(target);
      rightHandle.x = rightHandle.right - me.getDynamicHandleSize(me.leftHandle, offsetWidth);
      return rightHandle.width > 0 && rightHandle.contains(EventHelper.getPagePoint(event));
    }

    return false;
  }
  /**
   * Check if over top handle (virtual).
   * @private
   * @param {MouseEvent} event MouseEvent
   * @param {HTMLElement} target The current target element
   * @returns {Boolean} Returns true if mouse is over top handle, otherwise false
   */

  overTopHandle(event, target) {
    const me = this,
          {
      offsetHeight
    } = target;

    if (me.topHandle && (offsetHeight >= me.handleVisibilityThreshold || me.dynamicHandleSize)) {
      const topHandle = Rectangle.from(target);
      topHandle.height = me.getDynamicHandleSize(me.bottomHandle, offsetHeight);
      return topHandle.height > 0 && topHandle.contains(EventHelper.getPagePoint(event));
    }

    return false;
  }
  /**
   * Check if over bottom handle (virtual).
   * @private
   * @param {MouseEvent} event MouseEvent
   * @param {HTMLElement} target The current target element
   * @returns {Boolean} Returns true if mouse is over bottom handle, otherwise false
   */

  overBottomHandle(event, target) {
    const me = this,
          {
      offsetHeight
    } = target;

    if (me.bottomHandle && (offsetHeight >= me.handleVisibilityThreshold || me.dynamicHandleSize)) {
      const bottomHandle = Rectangle.from(target);
      bottomHandle.y = bottomHandle.bottom - me.getDynamicHandleSize(me.bottomHandle, offsetHeight);
      return bottomHandle.height > 0 && bottomHandle.contains(EventHelper.getPagePoint(event));
    }

    return false;
  } //endregion

}
ResizeHelper._$name = 'ResizeHelper';

/* globals ResizeObserver: true */
let resizeFireTimer = null;

const resizedQueue = [],
      isAbsolutelyPositioned = n => {
  var _n$ownerDocument$defa;

  return n.nodeType === 1 && ((_n$ownerDocument$defa = n.ownerDocument.defaultView) === null || _n$ownerDocument$defa === void 0 ? void 0 : _n$ownerDocument$defa.getComputedStyle(n).getPropertyValue('position')) === 'absolute';
};
/**
 * @module Core/helper/ResizeMonitor
 */

/**
 * Allows size monitoring of elements (or optionally a Window instance).
 *
 * ```
 * ResizeMonitor.addResizeListener(
 *   myElement,
 *   element => {
 *      console.log(element, ' changed size');
 *   }
 * );
 * ```
 *
 * @internal
 */

class ResizeMonitor {
  /**
   * Adds a resize listener to the passed element which is called when the element
   * is resized by layout.
   * @param {HTMLElement} element The element to listen for resizing.
   * @param {Function} handler The handling function. Will be passed the element.
   */
  static addResizeListener(element, handler) {
    const me = this;

    if (element === document || element === window) {
      element = document.body;
    }

    if (!element.classList.contains('b-resize-monitored')) {
      element.classList.add('b-resize-monitored');
      element._bResizemonitor = {
        handlers: []
      };
    } // If we're looking at the document, or body, use a window resize listener.

    if (element === document.body) {
      if (!me.hasWindowResizeListener) {
        // Throttle the reaction to window resize to only fire once every 100ms
        window.addEventListener('resize', me.onWindowResize);
        me.hasWindowResizeListener = true;
      }
    } // Regular element - use ResizeObserver by preference
    else if (window.ResizeObserver) {
        if (!me.resizeObserver) {
          me.resizeObserver = new ResizeObserver(me.onElementResize);
        }

        me.resizeObserver.observe(element);
      } // Polyfill ResizeObesrver
      else {
          element.classList.add('b-no-resizeobserver');
          const [monitors, expand, shrink] = DomHelper.createElement({
            parent: element,
            className: 'b-resize-monitors',
            children: [{
              className: 'b-resize-monitor-expand'
            }, {
              className: 'b-resize-monitor-shrink'
            }]
          }, true);
          expand.scrollLeft = expand.scrollTop = shrink.scrollLeft = shrink.scrollTop = 1000000;
          expand.addEventListener('scroll', me.onSizeMonitorScroll, true);
          shrink.addEventListener('scroll', me.onSizeMonitorScroll, true); // Also need to fake a resize-scroll on DOM mutation

          (handler.targetMutationMonitor = new MutationObserver(m => {
            const changedNodes = [];

            for (const mr of m) {
              if (mr.type === 'childList') {
                changedNodes.push.apply(changedNodes, mr.removedNodes);
                changedNodes.push.apply(changedNodes, mr.addedNodes);
              }
            } // If the changed nodes were absolutely positioned, then they won't
            // cause a resize, so return

            if (changedNodes.length > 0 && changedNodes.every(isAbsolutelyPositioned)) {
              return;
            } // HACK!
            // Although expand should be `b-resize-monitor-expand` element, id Edge 18 it might not be the case.
            // SimpleEventEditor.t.js in Scheduler shows this when you dragcreate event. At some point real expand element
            // get removed from DOM and we get smth like `b-sch-event-content` here, which is totally different element.
            // Reference is the same, it passes === check! So this is a sanity check to not trigger resizes when element
            // is incorrect.

            if ((BrowserHelper.isEdge || BrowserHelper.isIE11) && !expand.classList.contains('b-resize-monitor-expand')) {
              return;
            } // We only want the size monitor listener to trigger, so this event must NOT bubble
            // to any application or other framework listeners.

            expand.dispatchEvent(new CustomEvent('scroll', {
              bubbles: false
            }));
          })).observe(element, {
            childList: true,
            subtree: true
          }); // store reference for easier cleanup later

          handler.monitorElement = monitors;
        }

    element._bResizemonitor.handlers.push(handler);
  }
  /**
   * Removes a resize listener from the passed element.
   * @param {HTMLElement} element The element to listen for resizing.
   * @param {Function} handler The handling function to remove.
   */

  static removeResizeListener(element, handler) {
    if (element) {
      if (element === document || element === window) {
        element = document.body;
      }

      const resizeMonitor = element._bResizemonitor;
      let listenerCount = 0;

      if (resizeMonitor && resizeMonitor.handlers) {
        ArrayHelper.remove(resizeMonitor.handlers, handler); // See if we should unobserve the element

        listenerCount = resizeMonitor.handlers.length;
      } // Down to no listeners.

      if (!listenerCount) {
        element.classList.remove('b-resize-monitored');

        if (this.resizeObserver) {
          this.resizeObserver.unobserve(element);
        } // Remove the polyfill resize listeners
        else {
            // remove any added elements
            if (handler.monitorElement) {
              handler.monitorElement.remove();
              handler.monitorElement = null;
            } // remove the DOM mutation observer

            if (handler.targetMutationMonitor) {
              handler.targetMutationMonitor.disconnect();
            }
          }
      }
    }
  }

  static onElementResize(entries) {
    for (const resizeObserverEntry of entries) {
      const resizedElement = resizeObserverEntry.target,
            resizeMonitor = resizedElement._bResizemonitor,
            newRect = resizeObserverEntry.contentRect || resizedElement.getBoundingClientRect();

      if (resizedElement.offsetParent) {
        if (!resizeMonitor.rectangle || newRect.width !== resizeMonitor.rectangle.width || newRect.height !== resizeMonitor.rectangle.height) {
          const oldRect = resizeMonitor.rectangle;
          resizeMonitor.rectangle = newRect;

          for (const resizeHandler of resizeMonitor.handlers) {
            resizeHandler(resizedElement, oldRect, newRect);
          }
        }
      }
    }
  }

  static onSizeMonitorScroll(e) {
    var _document$body;

    // If no body exists or the element has gone, ignore the event; the listener will be removed automatically.
    if ((_document$body = document.body) !== null && _document$body !== void 0 && _document$body.contains(e.target)) {
      e.stopImmediatePropagation();
      const monitorNode = e.target.parentNode,
            resizedElement = monitorNode.parentNode,
            resizeMonitor = resizedElement._bResizemonitor,
            newRect = resizedElement.getBoundingClientRect();

      if (!resizeMonitor.rectangle || newRect.width !== resizeMonitor.rectangle.width || newRect.height !== resizeMonitor.rectangle.height) {
        resizedQueue.push([resizedElement, resizeMonitor.rectangle, newRect]);
        resizeMonitor.rectangle = newRect;

        if (!resizeFireTimer) {
          resizeFireTimer = requestAnimationFrame(ResizeMonitor.fireResizeEvents);
        }
      }

      monitorNode.firstChild.scrollLeft = monitorNode.firstChild.scrollTop = monitorNode.childNodes[1].scrollTop = monitorNode.childNodes[1].scrollLeft = 1000000;
    }
  }

  static onWindowResize(e) {
    const resizedElement = document.body,
          resizeMonitor = resizedElement._bResizemonitor,
          oldRect = resizeMonitor.rectangle;
    resizeMonitor.rectangle = document.documentElement.getBoundingClientRect();

    for (const resizeHandler of resizeMonitor.handlers) {
      resizeHandler(resizedElement, oldRect, resizeMonitor.rectangle);
    }
  }

  static fireResizeEvents() {
    for (const resizedEntry of resizedQueue) {
      for (const resizeHandler of resizedEntry[0]._bResizemonitor.handlers) {
        // Checking offsetParent to avoid resizing of elements which are not visible or exist in DOM
        if (resizedEntry[0].offsetParent) {
          resizeHandler.apply(this, resizedEntry);
        }
      }
    }

    resizeFireTimer = null;
    resizedQueue.length = 0;
  }

}
ResizeMonitor._$name = 'ResizeMonitor';

/**
 * @module Core/helper/TemplateHelper
 */
const tagSpaceRe = />[ \t\r\n]+</g,
      multiSpaceRe = /\s\s+/g;
/**
 * Helper for template strings, use as tag function
 * @example
 * TemplateHelper.tpl`...`
 * @internal
 */

class TemplateHelper {
  /**
   * Tag function for template literals that does some basic cleanup
   * @private
   */
  static tpl(strings, ...values) {
    let count = 0,
        output = values.map((val, i) => {
      // TODO: pad output nicely instead of just trimming away
      let ret = strings[i];

      if (Array.isArray(val)) {
        ret += val.reduce((ack, item) => ack += (typeof item === 'string' ? item.trim() : item) + '\n', '');
      } else {
        if (val === undefined) {
          val = '';
        }

        ret += typeof val === 'string' ? val.trim() : val;
      }

      count++;
      return ret;
    }).join('');

    if (count <= strings.length) {
      output += strings[strings.length - 1];
    } // Excise unnecessary *soft* whitespace textNodes. We cannot use \s because that
    // matches non-breaking spaces which need to be preserved.
    // Also reduce any usage of multiple spaces to single, since they have no value in html

    return output.replace(tagSpaceRe, '><').replace(multiSpaceRe, ' ').trim();
  }
  /**
   * Tag function for template literals that does some basic cleanup. Version for docs that do not remove blank space,
   * to keep code snippets formatting intact.
   * @private
   */

  static docsTpl(strings, ...values) {
    let count = 0,
        output = values.map((val, i) => {
      // TODO: pad output nicely instead of just trimming away
      let str = strings[i],
          ret = str;

      if (Array.isArray(val)) {
        ret += val.reduce((ack, item) => ack += (typeof item === 'string' ? item.trim() : item) + '\n', '');
      } else {
        if (val === undefined) {
          val = '';
        }

        ret += typeof val === 'string' ? val.trim() : val;
      }

      count++;
      return ret;
    }).join('');

    if (count <= strings.length) {
      output += strings[strings.length - 1];
    } // Excise unnecessary *soft* whitespace textNodes. We cannot use \s because that
    // matches non-breaking spaces which need to be preserved.

    return output.replace(tagSpaceRe, '><').trim();
  }

  static repeat(times) {
    return function (...args) {
      return TemplateHelper.tpl(...args).repeat(times);
    };
  }

  static loop(times) {}

}
TemplateHelper._$name = 'TemplateHelper';

/**
 * @module Core/helper/util/Scroller
 */

const scrollLiterals = {
  true: 'auto',
  false: 'hidden',
  'hidden-scroll': 'auto'
},
      scrollerCls = 'b-widget-scroller',
      defaultScrollOptions = {
  block: 'nearest'
},
      immediatePromise$2 = Promise.resolve(),
      scrollPromise = element => new Promise(resolve => EventHelper.on({
  element,
  scroll: resolve,
  once: true
})),
      xAxis = {
  x: 1
};
/**
 * Encapsulates scroll functionality for a Widget. All requests for scrolling and scrolling information
 * must go through a Widget's {@link Core.widget.Widget#config-scrollable} property.
 * @mixes Core/mixin/Events
 * @mixes Core/mixin/Delayable
 * @extends Core/Base
 */

class Scroller extends Delayable(Events(Base)) {
  static get configurable() {
    return {
      /**
       * The widget which is to scroll.
       * @config {HTMLElement}
       */
      widget: null,

      /**
       * The element which is to scroll. Defaults to the
       * {@link Core.widget.Widget#property-overflowElement} of the configured
       * {@link #config-widget}
       * @config {HTMLElement}
       */
      element: null,

      /**
       * How to handle overflowing in the `X` axis.
       * May be:
       * * `'auto'`
       * * `'visible'`
       * * `'hidden'`
       * * `'scroll'`
       * * `'hidden-scroll'` Meaning scrollable from the UI but with no scrollbar,
       * for example a grid header. Only on platforms which support this feature.
       * * `true` - meaning `'auto'`
       * * `false` - meaning `'hidden'`
       * @config {String|Boolean}
       */
      overflowX: null,

      /**
       * How to handle overflowing in the `Y` axis.
       * May be:
       * * `'auto'`
       * * `'visible'`
       * * `'hidden'`
       * * `'scroll'`
       * * `'hidden-scroll'` Meaning scrollable from the UI but with no scrollbar.
       * Only on platforms which support this feature.
       * * `true` - meaning `'auto'`
       * * `false` - meaning `'hidden'`
       * @config {String|Boolean}
       */
      overflowY: null,

      /**
       * If configured as `true`, the {@link #config-element} is not scrolled
       * but is translated using CSS transform when controlled by this class's API.
       * Scroll events are fired when the element is translated.
       * @default
       * @config {Boolean}
       */
      translate: null,
      x: 0,
      y: 0
    };
  }

  static get delayable() {
    return {
      onScrollEnd: {
        type: 'buffer',
        delay: 100
      }
    };
  }
  /**
   * Fired when scrolling happens on this Scroller's element. The event object is a native `scroll` event
   * with the described extra properties injected.
   * @event scroll
   * @param {Core.widget.Widget} widget The owning Widget which has been scrolled.
   * @param {Core.helper.util.Scroller} source This Scroller
   */

  /**
   * Fired when scrolling finished on this Scroller's element. The event object is the last native `scroll` event
   * fires by the element with the described extra properties injected.
   * @event scrollend
   * @param {Core.widget.Widget} widget The owning Widget which has been scrolled.
   * @param {Core.helper.util.Scroller} source This Scroller
   */

  /**
   * The `overflow-x` setting for the widget. `true` means `'auto'`.
   * @member {Boolean|String} overflowX
   */

  /**
   * The `overflow-y` setting for the widget. `true` means `'auto'`.
   * @member {Boolean|String} overflowY
   */

  syncOverflowState() {
    const {
      element
    } = this,
          {
      classList
    } = element; // We use classes to indicate presence of overflow. This carries no rules by default.
    // Widget SASS may or may not attach rules or use these to select elements.

    if (BrowserHelper.isEdge) {
      // Edge rounds widths and may falsely report a horizontal overflow
      classList[element.scrollWidth - element.clientWidth > 1 ? 'add' : 'remove']('b-horizontal-overflow');
    } else {
      classList[element.scrollWidth > element.clientWidth ? 'add' : 'remove']('b-horizontal-overflow');
    }

    classList[element.scrollHeight > element.clientHeight ? 'add' : 'remove']('b-vertical-overflow');
  }
  /**
   * Returns `true` if there is a *space-consuming* scrollbar controlling scroll in the specified axis.
   * @param {*} axis The axis to check scrollbar for. Note that this is subtly different to asking
   * whether an axis *has any* overflow.
   */

  hasScrollbar(axis = 'y') {
    const {
      element
    } = this;

    if (element && DomHelper.scrollBarWidth) {
      const vertical = axis === 'y',
            dimension = vertical ? 'Width' : 'Height',
            clientSize = element[`client${dimension}`],
            borderSize = parseInt(DomHelper.getStyleValue(element, `border${vertical ? 'Left' : 'Top'}Width`)) + parseInt(DomHelper.getStyleValue(element, `border${vertical ? 'Right' : 'Bottom'}Width`)),
            difference = element[`offset${dimension}`] - borderSize - clientSize; // If the difference between the content width and the client width is
      // scrollBarWidth, then we have a scrollbar

      return Math.abs(difference - DomHelper.scrollBarWidth) < 2;
    }
  }
  /**
   * Partners this Scroller with the passed scroller in order to sync the scrolling position in the passed axes
   * @param {Core.helper.util.Scroller} otherScroller
   * @param {String|Object} [axes='x'] `'x'` or `'y'` or `{x: true/false, y: true/false}` axes to sync
   */

  addPartner(otherScroller, axes = xAxis) {
    const me = this;

    if (typeof axes === 'string') {
      axes = {
        [axes]: 1
      };
    }

    if (!me.partners) {
      me.partners = {};
    }

    me.partners[otherScroller.id] = {
      scroller: otherScroller,
      axes
    }; // Initial sync of the other axis to match our current state

    if (axes.x) {
      otherScroller.x = me.x;
    }

    if (axes.y) {
      otherScroller.y = me.y;
    } // It's a mutual relationship - the other scroller partners with us.

    if (!otherScroller.isPartneredWith(me)) {
      otherScroller.addPartner(me, axes);
    }
  }
  /**
   * Breaks the link between this Scroller and the passed Scroller set up by the
   * {@link #function-addPartner} method.
   * @param {Core.helper.util.Scroller} otherScroller The Scroller to unlink from.
   */

  removePartner(otherScroller) {
    if (this.isPartneredWith(otherScroller)) {
      delete this.partners[otherScroller.id];
      otherScroller.removePartner(this);
    }
  }

  isPartneredWith(otherScroller) {
    return Boolean(this.partners && this.partners[otherScroller.id]);
  }
  /**
   * Scrolls the passed element or {@link Core.helper.util.Rectangle} into view according to the passed options.
   * @param {HTMLElement|Core.helper.util.Rectangle} element The element or a Rectangle in document space to scroll into view.
   * @param {Object} [options] How to scroll.
   * @param {String} [options.block] How far to scroll the element: `start/end/center/nearest`.
   * @param {Number} [options.edgeOffset] edgeOffset A margin around the element or rectangle to bring into view.
   * @param {Object|Boolean|Number} [options.animate] Set to `true` to animate the scroll by 300ms,
   * or the number of milliseconds to animate over, or an animation config object.
   * @param {Number} [options.animate.duration] The number of milliseconds to animate over.
   * @param {String} [options.animate.easing] The name of an easing function.
   * @param {Boolean|Function} [options.highlight] Set to `true` to highlight the element when it is in view.
   * May be a function which is called passing the element, to provide customized highlighting.
   * @param {Boolean} [options.focus] Set to `true` to focus the element when it is in view.
   * @param {Boolean} [options.x] Pass as `false` to disable scrolling in the `X` axis.
   * @param {Boolean} [options.y] Pass as `false` to disable scrolling in the `Y` axis.
   * @returns {Promise} A promise which is resolved when the element has been scrolled into view.
   */

  scrollIntoView(element, options = defaultScrollOptions) {
    const me = this,
          isRectangle = element instanceof Rectangle,
          originalRect = isRectangle ? element : Rectangle.from(element),
          {
      xDelta,
      yDelta
    } = me.getDeltaTo(element, options),
          result = me.scrollBy(xDelta, yDelta, options);

    if (options.highlight || options.focus) {
      result.then(() => {
        if (isRectangle) {
          element = originalRect.translate(-xDelta, -yDelta);
        }

        if (options.highlight) {
          // Not coercible to a number means its a function or name of a function
          if (isNaN(options.highlight)) {
            (me.widget || me).callback(options.highlight, null, [element]);
          } // Otherwise, it's truthy or falsy
          else {
              DomHelper.highlight(element, me);
            }
        }

        if (options.focus) {
          element.focus();
        }
      });
    }

    return result;
  }
  /**
   * Scrolls by the passed deltas according to the passed options.
   * @param {Number} [xDelta=0] How far to scroll in the X axis.
   * @param {Number} [yDelta=0] How far to scroll in the Y axis.
   * @param {Object|Boolean} [options] How to scroll. May be passed as `true` to animate.
   * @param {Boolean} [options.silent] Set to `true` to suspend `scroll` events during scrolling.
   * @param {Object|Boolean|Number} [options.animate] Set to `true` to animate the scroll by 300ms,
   * or the number of milliseconds to animate over, or an animation config object.
   * @param {Number} [options.animate.duration] The number of milliseconds to animate over.
   * @param {String} [options.animate.easing] The name of an easing function.
   * @returns {Promise} A promise which is resolved when the scrolling has finished.
   */

  scrollBy(xDelta = 0, yDelta = 0, options = defaultScrollOptions) {
    const me = this,
          animate = typeof options === 'object' ? options.animate : options,
          absX = Math.abs(xDelta),
          absY = Math.abs(yDelta);

    if (me.scrollAnimation) {
      me.scrollAnimation.cancel();
      me.scrollAnimation = null;
    } // Only set the flag if there is going to be scrolling done.
    // It is cleared by the scrollEnd handler, so there must be scrolling.

    if (xDelta || yDelta) {
      me.silent = options.silent;
    }

    let duration = animate && (typeof animate === 'number' ? animate : typeof animate.duration === 'number' ? animate.duration : 300); // Only go through animation if there is significant scrolling to do.

    if (duration && (absX > 10 || absY > 10)) {
      const {
        x,
        y
      } = me;
      let lastX = x,
          lastY = y; // For small distances, constrain duration

      if (Math.max(absX, absY) < 50) {
        duration = Math.min(duration, 500);
      }

      me.scrollAnimation = FunctionHelper.animate(duration, progress => {
        const isEnd = progress === 1;

        if (xDelta) {
          // If the user, or another process has changed the position since last time, abort.
          // Unless called with the force option to proceed regardless.
          if (me.x !== lastX && !options.force) {
            return me.scrollAnimation && me.scrollAnimation.cancel();
          }

          me.x = Math.max(x + (isEnd ? xDelta : Math.round(xDelta * progress)), 0);
        }

        if (yDelta) {
          // If the user, or another process has changed the position since last time, abort.
          // Unless called with the force option to proceed regardless.
          if (me.y !== lastY && !options.force) {
            return me.scrollAnimation && me.scrollAnimation.cancel();
          }

          me.y = Math.max(y + (isEnd ? yDelta : Math.round(yDelta * progress)), 0);
        } // Store actual position from DOM

        lastX = me.x;
        lastY = me.y;
      }, me, animate.easing);
      me.element.classList.add('b-scrolling');
      me.scrollAnimation.then(() => {
        if (!me.isDestroyed) {
          me.element.classList.remove('b-scrolling');
          me.scrollAnimation = null;
        }
      });
      return me.scrollAnimation;
    } else {
      if (xDelta | yDelta) {
        me.x += xDelta;
        me.y += yDelta;
        return scrollPromise(me.element);
      } else {
        return immediatePromise$2;
      }
    }
  }
  /**
   * Scrolls to the passed position according to the passed options.
   * @param {Number} [toX=0] Where to scroll to in the X axis.
   * @param {Number} [toY=0] Where to scroll to in the Y axis.
   * @param {Object|Boolean} [options] How to scroll. May be passed as `true` to animate.
   * @param {Object|Boolean|Number} [options.animate] Set to `true` to animate the scroll by 300ms,
   * or the number of milliseconds to animate over, or an animation config object.
   * @param {Number} [options.animate.duration] The number of milliseconds to animate over.
   * @param {String} [options.animate.easing] The name of an easing function.
   * @returns {Promise} A promise which is resolved when the scrolling has finished.
   */

  scrollTo(toX, toY, options) {
    const {
      x,
      y
    } = this,
          xDelta = toX == null ? 0 : toX - x,
          yDelta = toY == null ? 0 : toY - y;
    return this.scrollBy(xDelta, yDelta, options);
  }

  doDestroy() {
    const me = this;

    if (me._element) {
      me._element.removeEventListener('scroll', me.scrollHandler);

      me.wheelListenerRemover && me.wheelListenerRemover();
    }

    if (me.scrollAnimation) {
      me.scrollAnimation.cancel();
    }

    Object.values(me.partners || {}).forEach(({
      scroller
    }) => scroller.removePartner(me));
    super.doDestroy();
  }
  /**
   * Respond to style changes to monitor scroll *when this Scroller is in `translate: true` mode.*
   * @param {Object[]} mutations The ElementMutation records.
   * @private
   */

  onElMutation(mutations) {
    const me = this,
          [x, y] = DomHelper.getTranslateXY(me.element); // If the mutation was due to a change in the translateX/Y styles, this is
    // a scroll event, so inform observers and partners

    if (me._x !== -x || me.y !== -y) {
      const scrollEvent = new CustomEvent('scroll', {
        bubbles: true
      });
      Object.defineProperty(scrollEvent, 'target', {
        get: () => me.element
      });
      me.onScroll(scrollEvent);
    }
  }

  onScroll(e) {
    const me = this;

    if (!me.widget || !me.widget.isDestroyed) {
      // Don't read the value until we have to. The x & y getters will check this flag
      me.positionDirty = true;

      if (!me.element.classList.contains('b-scrolling')) {
        me.element.classList.add('b-scrolling');
      }

      e.widget = me.widget; // If we have the scroll silent flag, do not fire the event.

      if (!me.silent) {
        me.trigger('scroll', e);
      } // Keep partners in sync

      me.syncPartners(); // If this scroll impulse was from a controlling partner, clear that now

      me.controllingPartner = null; // Buffered method will fire in 100ms, unless another scroll event comes round.
      // In which case execution will be pushed out by another 100ms.

      me.onScrollEnd(e);
    }
  }

  syncPartners() {
    const me = this; // Keep partners in sync

    if (me.partners) {
      Object.values(me.partners).forEach(({
        axes,
        scroller
      }) => {
        // Don't feed back to the one who's just told us to scroll here.
        if (scroller !== me.controllingPartner) {
          scroller.sync(me, axes);
        }
      });
    }
  }

  onScrollEnd(e) {
    if (this.silent) {
      this.silent = false;
    }

    this.trigger('scrollEnd', e); // Controlling partner is required for scrollable not to change its partners on scroll. This method is buffered
    // and landing here essentially means that no scrolling has occurred during the onScrollEnd buffer
    // time. We can safely cleanup controlling partner here.
    // https://github.com/bryntum/support/issues/1095

    this.controllingPartner = null;
    this.element.classList.remove('b-scrolling');
  }
  /**
   * Returns the xDelta and yDelta values in an object from the current scroll position to the
   * passed element or Rectangle.
   * @param {HTMLElement|Core.helper.util.Rectangle} element The element or a Rectangle to calculate deltas for.
   * @param {Object} [options] How to scroll.
   * @param {String} [options.block] How far to scroll the element: `start/end/center/nearest`.
   * @param {Number} [options.edgeOffset] A margin around the element or rectangle to bring into view.
   * @param {Boolean} [options.x] Pass as `false` to disable scrolling in the `X` axis.
   * @param {Boolean} [options.y] Pass as `false` to disable scrolling in the `Y` axis.
   * @returns {Object} `{ xDelta, yDelta }`
   * @internal
   */

  getDeltaTo(element, options) {
    const me = this; // scroller may belong to a collapsed subgrid widget

    if (!me.viewport) {
      return {
        xDelta: 0,
        yDelta: 0
      };
    }

    const elementRect = element instanceof Rectangle ? element : Rectangle.from(element),
          block = options.block || 'nearest',
          scrollerRect = me.viewport,
          edgeOffset = options.edgeOffset || 0,
          // Only include the offset round the target is the viewport is big enough to accommodate it.
    xOffset = scrollerRect.width >= elementRect.width + edgeOffset * 2 ? edgeOffset : 0,
          yOffset = scrollerRect.height >= elementRect.height + edgeOffset * 2 ? edgeOffset : 0,
          elRect = elementRect.clone().adjust(-xOffset, -yOffset, xOffset, yOffset).constrainTo(new Rectangle(scrollerRect.x - me.x, scrollerRect.y - me.y, me.scrollWidth, me.scrollHeight)),
          targetRect = elRect.clone();
    let xDelta = 0,
        yDelta = 0;

    if (block === 'start') {
      targetRect.moveTo(scrollerRect.x, scrollerRect.y);
      xDelta = elRect.x - targetRect.x;
      yDelta = elRect.y - targetRect.y;
    } else if (block === 'end') {
      targetRect.translate(scrollerRect.right - targetRect.right, scrollerRect.bottom - targetRect.bottom);
      xDelta = elRect.x - targetRect.x;
      yDelta = elRect.y - targetRect.y;
    } else {
      // Calculate deltas unless the above has done that for non-fitting target
      if (block === 'center') {
        const center = scrollerRect.center;
        targetRect.moveTo(center.x - targetRect.width / 2, center.y - targetRect.height / 2);
        xDelta = xDelta || elRect.x - targetRect.x;
        yDelta = yDelta || elRect.y - targetRect.y;
      } // Use "nearest"
      else {
          // Can't fit width in, scroll what is possible into view so that start is visible.
          if (targetRect.width > scrollerRect.width) {
            xDelta = targetRect.x - scrollerRect.x;
          } // If it's *possible* to scroll to nearest x, calculate the delta
          else {
              if (targetRect.right > scrollerRect.right) {
                xDelta = targetRect.right - scrollerRect.right;
              } else if (targetRect.x < scrollerRect.x) {
                xDelta = targetRect.x - scrollerRect.x;
              }
            } // Can't fit height in, scroll what is possible into view so that start is visible.

          if (targetRect.height > scrollerRect.height) {
            yDelta = targetRect.y - scrollerRect.y;
          } // If it's *possible* to scroll to nearest y, calculate the delta
          else {
              if (targetRect.bottom > scrollerRect.bottom) {
                yDelta = targetRect.bottom - scrollerRect.bottom;
              } else if (targetRect.y < scrollerRect.y) {
                yDelta = targetRect.y - scrollerRect.y;
              }
            }
        }
    } // Do not allow deltas which would produce -ve scrolling or scrolling past the maxX/Y

    return {
      // When calculating how much delta is necessary to scroll the targetRect to the center
      // constrain that to what is *possible*. If what you are trying to scroll into the
      // center is hard against the right edge of the scroll range, then it cannot scroll
      // to the center, and the result must reflect that even though scroll is self limiting.
      // This is because highlighting the requested "element", if that element is in fact
      // a Rectangle, uses a temporary element placed at the requested region which
      // MUST match where the actual scroll has moved the requested region.
      xDelta: options.x === false ? 0 : Math.round(Math.max(Math.min(xDelta, me.maxX - me.x), -me.x)),
      yDelta: options.y === false ? 0 : Math.round(Math.max(Math.min(yDelta, me.maxY - me.y), -me.y))
    };
  }
  /**
   * A {@link Core.helper.util.Rectangle Rectangle} describing the bounds of the scrolling viewport.
   * @property {Core.helper.util.Rectangle}
   */

  get viewport() {
    return Rectangle.client(this.element);
  }

  updateElement(element, oldElement) {
    const me = this,
          scrollHandler = me.scrollHandler || (me.scrollHandler = me.onScroll.bind(me));

    if (oldElement) {
      if (me.translate) {
        me.mutationObserver && me.mutationObserver.disconnect(oldElement);
      } else {
        oldElement.removeEventListener('scroll', scrollHandler);
        oldElement.classList.remove(scrollerCls);
      }
    }

    if (me.translate) {
      if (!me.mutationObserver) {
        me.mutationObserver = new MutationObserver(mutations => me.onElMutation(mutations));
      }

      me.mutationObserver.observe(element, {
        attributes: true
      });
      me._x = me._y = 0;

      if (document.contains(element)) {
        const [x, y] = DomHelper.getTranslateXY(element);
        me._x = -x;
        me._y = -y;
      }
    } else {
      element.addEventListener('scroll', scrollHandler);
      element.classList.add(scrollerCls);
    } // Ensure the overflow configs, which are unable to process themselves
    // in the absence of the element get applied to the newly arrived element.

    if (me.positionDirty) {
      me.updateOverflowX(me.overflowX);
      me.updateOverflowY(me.overflowY);
    } // Apply initially configured scroll position if we have non-zero positions

    if (me.isConfiguring) {
      me._x && me.updateX(me._x);
      me._y && me.updateY(me._y);
    }

    me.positionDirty = true;
  }
  /**
   * The horizontal scroll position of the widget.
   * @property {Number}
   */

  get x() {
    const me = this,
          {
      element
    } = me;

    if (element && me.positionDirty) {
      if (me.translate) {
        const [x, y] = DomHelper.getTranslateXY(element);
        me._x = -x;
        me._y = -y;
      } else {
        me._x = element.scrollLeft;
        me._y = element.scrollTop;
      }

      me.positionDirty = false;
    }

    return me._x;
  }

  changeX(x) {
    // Only process initial X if we were configured to start at non-zero
    if (!this.isConfiguring || x) {
      return x;
    }

    this._x = x;
  }

  updateX(x) {
    const {
      element,
      widget
    } = this; // When element is outside of DOM, this can have no effect

    if (element && !(widget !== null && widget !== void 0 && widget.isConfiguring)) {
      this.trigger('scrollStart', {
        x
      });

      if (this.translate) {
        DomHelper.setTranslateX(element, -x);
      } else {
        element.scrollLeft = x;
      }
    } // The scroll position will need to be read before we can return it.
    // Do not read it back now, we may not have our element, or if we do,
    // that would cause a forced synchronous layout.

    this.positionDirty = true;
  }

  sync(controllingPartner, axes) {
    const {
      x,
      y
    } = axes;

    if (x != null) {
      if (this.x !== controllingPartner.x) {
        // Only set controlling partner when scroll will actually change. This helps to increase stability of
        // state restoring API.
        this.controllingPartner = controllingPartner;
        this.x = controllingPartner.x;
      }
    }

    if (y != null) {
      if (this.y !== controllingPartner.y) {
        this.controllingPartner = controllingPartner;
        this.y = controllingPartner.y;
      }
    }
  }
  /**
   * The vertical scroll position of the widget.
   * @property {Number}
   */

  get y() {
    const me = this,
          {
      element
    } = me;

    if (element && me.positionDirty) {
      if (me.translate) {
        const [x, y] = DomHelper.getTranslateXY(element);
        me._x = -x;
        me._y = -y;
      } else {
        me._x = element.scrollLeft;
        me._y = element.scrollTop;
      }

      me.positionDirty = false;
    }

    return me._y;
  }

  changeY(y) {
    // Only process initial Y if we were configured to start at non-zero
    if (!this.isConfiguring || y) {
      return y;
    }

    this._y = y;
  }

  updateY(y) {
    const {
      element,
      widget
    } = this; // When element is outside of DOM, this can have no effect

    if (element && !(widget !== null && widget !== void 0 && widget.isConfiguring)) {
      this.trigger('scrollStart', {
        y
      });

      if (this.translate) {
        DomHelper.setTranslateY(element, -y);
      } else {
        element.scrollTop = y;
      }
    } // The scroll position will need to be read before we can return it.
    // Do not read it back now, we may not have our element, or if we do,
    // that would cause a forced synchronous layout.

    this.positionDirty = true;
  }
  /**
   * The maximum `X` scrollable position of the widget.
   * @property {Number}
   * @readonly
   */

  get maxX() {
    return this.scrollWidth - this.clientWidth;
  }
  /**
   * The maximum `Y` scrollable position of the widget.
   * @property {Number}
   * @readonly
   */

  get maxY() {
    return this.scrollHeight - this.clientHeight;
  }

  updateOverflowX(overflowX) {
    const {
      element,
      translate
    } = this;

    if (element) {
      // Scroll, but without showing scrollbars.
      // For example a grid header. Only works on platforms which
      // support suppression of scrollbars through CSS.
      if (overflowX === 'hidden-scroll' && !translate) {
        element.classList.add('b-hide-scroll'); // Adds a wheel listener if needed - there is scrollbar width
        // and we don't already have one.

        this.enableWheel();
      }

      if (!translate) {
        element.style.overflowX = scrollLiterals[overflowX] || overflowX;
      }
    }

    this.positionDirty = !this.isConfiguring;
  }

  updateOverflowY(overflowY) {
    const {
      element,
      translate
    } = this;

    if (element) {
      // Scroll, but without showing scrollbars.
      // For example a grid header. Only works on platforms which
      // support suppression of scrollbars through CSS.
      if (overflowY === 'hidden-scroll' && !translate) {
        element.classList.add('b-hide-scroll'); // Adds a wheel listener if needed - there is scrollbar width
        // and we don't already have one.

        this.enableWheel();
      }

      if (!translate) {
        element.style.overflowY = scrollLiterals[overflowY] || overflowY;
      }
    }

    this.positionDirty = !this.isConfiguring;
  }

  enableWheel() {
    if (!BrowserHelper.isChrome && !BrowserHelper.isSafari && !this.wheelListenerRemover) {
      this.wheelListenerRemover = EventHelper.on({
        element: this.element,
        wheel: 'onWheel',
        thisObj: this
      });
    }
  }

  onWheel(e) {
    if (Math.abs(e.deltaX) > Math.abs(e.deltaY) && this.overflowX !== false) {
      this.x += e.deltaX; // The gesture must not also scroll any scrollable ancestor

      e.stopPropagation();
    } else if (this.overflowY !== false) {
      this.y += e.deltaY; // The gesture must not also scroll any scrollable ancestor

      e.stopPropagation();
    }
  }
  /**
   * The horizontal scroll range of the widget.
   * @property {Number}
   * @readonly
   */

  get scrollWidth() {
    return this.element ? this.element.scrollWidth : 0;
  }

  set scrollWidth(scrollWidth) {
    const me = this;
    let stretcher = me.widthStretcher; // "Unsetting" scrollWidth removes the stretcher

    if (stretcher && scrollWidth == null) {
      stretcher.remove();
      me.widthStretcher = null;
    } else if (scrollWidth) {
      if (!stretcher) {
        stretcher = me.widthStretcher = DomHelper.createElement({
          className: 'b-scroller-stretcher'
        });
      }

      stretcher.style.transform = `translateX(${scrollWidth - 1}px)`;

      if (me.element && me.element.lastChild !== stretcher) {
        me.element.appendChild(stretcher);
      }
    }

    me.syncOverflowState();
  }

  get scrollHeight() {
    return this.element ? this.element.scrollHeight : 0;
  }
  /**
   * The vertical scroll range of the widget. May be set to larger than the actual data
   * height to enable virtual scrolling. This is how the grid extends its scroll range
   * while only rendering a small subset of the dataset.
   * @property {Number}
   */

  set scrollHeight(scrollHeight) {
    const me = this,
          stretcher = me.stretcher || (me.stretcher = DomHelper.createElement({
      className: 'b-scroller-stretcher'
    }));
    stretcher.style.transform = `translateY(${scrollHeight - 1}px)`;

    if (me.element && me.element.lastChild !== stretcher) {
      me.element.appendChild(stretcher);
    }
  }
  /**
   * The client width of the widget.
   * @property {Number}
   * @readonly
   */

  get clientWidth() {
    return this.element ? this.element.clientWidth : 0;
  }
  /**
   * The client height of the widget.
   * @property {Number}
   * @readonly
   */

  get clientHeight() {
    return this.element ? this.element.clientHeight : 0;
  }
  /**
   * The unique ID of this Scroller
   * @property {String}
   * @readonly
   */

  get id() {
    if (!this._id) {
      if (this.widget) {
        this._id = `${this.widget.id}-scroller`;
      } else {
        this._id = IdHelper.generateId('scroller-');
      }
    }

    return this._id;
  }

}
Scroller._$name = 'Scroller';

const localeName = 'En',
      localeDesc = 'English',
      locale$1 = {
  localeName,
  localeDesc,
  // Translations for common words and phrases which are used by all classes.
  Object: {
    Yes: 'Yes',
    No: 'No',
    Cancel: 'Cancel'
  },
  //region Widgets
  Combo: {
    noResults: 'No results'
  },
  FilePicker: {
    file: 'File'
  },
  Field: {
    // native input ValidityState statuses
    badInput: 'Invalid field value',
    patternMismatch: 'Value should match a specific pattern',
    rangeOverflow: value => `Value must be less than or equal to ${value.max}`,
    rangeUnderflow: value => `Value must be greater than or equal to ${value.min}`,
    stepMismatch: 'Value should fit the step',
    tooLong: 'Value should be shorter',
    tooShort: 'Value should be longer',
    typeMismatch: 'Value is required to be in a special format',
    valueMissing: 'This field is required',
    invalidValue: 'Invalid field value',
    minimumValueViolation: 'Minimum value violation',
    maximumValueViolation: 'Maximum value violation',
    fieldRequired: 'This field is required',
    validateFilter: 'Value must be selected from the list'
  },
  DateField: {
    invalidDate: 'Invalid date input'
  },
  NumberFormat: {
    locale: 'en-US',
    currency: 'USD'
  },
  DurationField: {
    invalidUnit: 'Invalid unit'
  },
  TimeField: {
    invalidTime: 'Invalid time input'
  },
  List: {
    loading: 'Loading...'
  },
  PagingToolbar: {
    firstPage: 'Go to first page',
    prevPage: 'Go to previous page',
    page: 'Page',
    nextPage: 'Go to next page',
    lastPage: 'Go to last page',
    reload: 'Reload current page',
    noRecords: 'No records to display',
    pageCountTemplate: data => `of ${data.lastPage}`,
    summaryTemplate: data => `Displaying records ${data.start} - ${data.end} of ${data.allCount}`
  },
  //endregion
  //region Others
  DateHelper: {
    locale: 'en-US',
    weekStartDay: 0,
    unitNames: [{
      single: 'millisecond',
      plural: 'ms',
      abbrev: 'ms'
    }, {
      single: 'second',
      plural: 'seconds',
      abbrev: 's'
    }, {
      single: 'minute',
      plural: 'minutes',
      abbrev: 'min'
    }, {
      single: 'hour',
      plural: 'hours',
      abbrev: 'h'
    }, {
      single: 'day',
      plural: 'days',
      abbrev: 'd'
    }, {
      single: 'week',
      plural: 'weeks',
      abbrev: 'w'
    }, {
      single: 'month',
      plural: 'months',
      abbrev: 'mon'
    }, {
      single: 'quarter',
      plural: 'quarters',
      abbrev: 'q'
    }, {
      single: 'year',
      plural: 'years',
      abbrev: 'yr'
    }],
    // Used to build a RegExp for parsing time units.
    // The full names from above are added into the generated Regexp.
    // So you may type "2 w" or "2 wk" or "2 week" or "2 weeks" into a DurationField.
    // When generating its display value though, it uses the full localized names above.
    unitAbbreviations: [['mil'], ['s', 'sec'], ['m', 'min'], ['h', 'hr'], ['d'], ['w', 'wk'], ['mo', 'mon', 'mnt'], ['q', 'quar', 'qrt'], ['y', 'yr']],
    parsers: {
      L: 'MM/DD/YYYY',
      LT: 'HH:mm A'
    },
    ordinalSuffix: number => {
      const hasSpecialCase = ['11', '12', '13'].find(n => number.endsWith(n));
      let suffix = 'th';

      if (!hasSpecialCase) {
        const lastDigit = number[number.length - 1];
        suffix = {
          1: 'st',
          2: 'nd',
          3: 'rd'
        }[lastDigit] || 'th';
      }

      return number + suffix;
    }
  } //endregion

};

LocaleManagerSingleton.registerLocale(localeName, {
  desc: localeDesc,
  locale: locale$1
});

/**
 * @module Core/helper/util/Promissory
 */

/**
 * Encapsulates a Promise and provides `resolve()` and `reject()` methods.
 *
 * For example:
 * ```
 *  load() {
 *      this.loading = new Promissory();
 *      this.store.load();
 *
 *      return this.loading.promise;
 *  }
 *
 *  onStoreLoad(store, err) {
 *      if (err) {
 *          this.loading.resolve(this);
 *      }
 *      else {
 *          this.loading.reject(err);
 *      }
 *  }
 *
 * ```
 * @private
 */
class Promissory {
  constructor(fn) {
    this.promise = new Promise((resolve, reject) => {
      this.resolve = resolve;
      this.reject = reject;
    });

    if (fn) {
      fn(this);
    }
  }

}
Promissory._$name = 'Promissory';

const {
  defineProperty: defineProperty$5
} = Reflect;
/**
 * @module Core/widget/Renderable
 */

/**
 * This class provides element rendering and automatic synchronization based on config property changes.
 *```
 *  class Label extends Renderable {
 *      static get configurable() {
 *          return {
 *              text : {
 *                  value: null,
 *
 *                  $config : 'render'
 *              }
 *          };
 *      }
 *
 *      renderDom() {
 *          return {
 *              tag: 'label',
 *              children: [
 *                  this.text
 *              ]
 *          };
 *      }
 *  }
 *```
 * A class can opt to not specify its render configs. In this case, these will be determined automatically, but with
 * some additional cost. This may not be worth considering if only a few instances are ever likely.
 *
 * @private
 */

class Renderable extends Base.mixin(Localizable, Events, Delayable, Identifiable) {
  static get configurable() {
    return {
      /**
       * The top-level DOM element for this object. This element is produced from the {@link #function-renderDom}
       * method of the derived class. As configs used by `renderDom` change, a synchronization of the DOM is
       * scheduled by calling {@link #function-refreshDom}. This call is made automatically by this class, making
       * it rarely necessary to call `refreshDom` directly.
       * @config {HTMLElement}
       * @category DOM
       */
      element: null
    };
  }

  static get delayable() {
    return {
      refreshDom: 'raf'
    };
  }

  static get identifiable() {
    return {};
  }
  /**
   * Returns the Set of configs defined as `render: true`, that is, the configs that determine the rendering (via
   * `renderDom`). If no such configs are defined, this method returns `null`.
   *
   * @returns {Set}
   * @private
   */

  static get renderConfigs() {
    const me = this,
          meta = me.$meta;
    let renderConfigs = meta.renderConfigs,
        configs,
        name;

    if (renderConfigs === undefined) {
      // if (first time for this class)
      renderConfigs = null;
      configs = meta.configs;

      for (name in configs) {
        if (configs[name].render) {
          (renderConfigs || (renderConfigs = new Set())).add(name);
        }
      } // Put $renderConfigs on the prototype so that onConfigChange is as simple as possible:

      meta.renderConfigs = me.prototype.$renderConfigs = renderConfigs;
    }

    return renderConfigs;
  } //region Init

  construct(...args) {
    this.$iid = ++Renderable.$idSeed;
    this.byRef = {};
    super.construct(...args);

    if (this.onDraw !== Renderable.prototype.onDraw) {
      this.initDrawable();
    }
  }

  startConfigure(config) {
    this.element = this.renderContext.renderDom(); // calls changeElement()

    super.startConfigure(config);
  } //endregion
  //region Configs

  get element() {
    // NOTE: We can replace the getter of a config using defineConfig()...
    // Asking for the primary el is a good sign that we need to sync the DOM:
    this.refreshDom.flush();
    return this._element;
  }
  /**
   * This is called when the `element` config is assigned (via the setter).
   * @param {HTMLElement} element The new element being assigned.
   * @param {HTMLElement} oldElement The old element (previously assigned) or `null`.
   * @returns {HTMLElement}
   * @private
   */

  changeElement(element, oldElement) {
    const me = this;

    if (oldElement) {
      oldElement.remove();
    }

    if (element) {
      element.id = me.id;
      element = DomHelper.createElement(element, {
        refOwner: me
      });
    }

    return element;
  }

  updateId(id, oldId) {
    const me = this;

    if (oldId) {
      const element = me.element;
      element.id = id;
      me.fixRefOwnerId(element, id, oldId);
    }
  } //endregion
  //region Misc

  /**
   * Returns the `classList` of this instance's `element`.
   * @property {DOMTokenList}
   */

  get classes() {
    return this.element.classList;
  }
  /**
   * This method fixes the element's `$refOwnerId` when this instance's `id` is changing.
   * @param {HTMLElement} el The element to fix.
   * @param {String} id The new id being assigned.
   * @param {String} oldId The old id (previously assigned).
   * @private
   */

  fixRefOwnerId(el, id, oldId) {
    if (el.$refOwnerId === oldId) {
      el.$refOwnerId = id;
      const ref = el.$reference;

      if (ref) {
        el.id = `${id}-${ref}`;
      }

      for (const c of el.childNodes) {
        this.fixRefOwnerId(c, id, oldId);
      }
    }
  }

  onConfigChange({
    name
  }) {
    // The $renderConfigs Set is either on our prototype (due to renderConfigs getter) or on our instance (due to
    // renderContext getter):
    if (!this.isConfiguring && this.$renderConfigs.has(name)) {
      this.refreshDom();
    }
  } //endregion
  //region Rendering

  /**
   * This method is called by `DomHelper.createElement` and `DomSync.sync` as new reference elements are created.
   * @param {String} name The name of the element, i.e., the value of its `reference` attribute.
   * @param {HTMLElement} el The element instance
   * @param {Object} domConfig The DOM config object.
   * @private
   */

  attachRef(name, el, domConfig) {
    const me = this,
          key = '_' + name,
          {
      listeners
    } = domConfig;

    if (!(key in me)) {
      defineProperty$5(me, name, {
        get() {
          // Asking for a ref el is a good sign that we need to sync the DOM:
          me.refreshDom.flush();
          return me[key];
        },

        set(el) {
          me[key] = el; // Key elements contain owner pointer (Not supported on IE SVG).
          // if (el && el.dataset) {
          //     el.dataset.ownerCmp = me.id;
          // }
        }

      });
    }

    el.id = `${me.id}-${name}`;
    me[name] = el;

    if (listeners) {
      domConfig.listeners = {
        on: listeners,
        un: EventHelper.on(Object.assign({
          element: el,
          thisObj: me
        }, listeners))
      };
    }
  }
  /**
   * This method is called by `DomSync.sync` as reference elements are removed from the DOM.
   * @param {String} name The name of the element, i.e., the value of its `reference` attribute.
   * @param {HTMLElement} el The element instance
   * @param {Object} domConfig The DOM config object.
   * @private
   */

  detachRef(name, el, domConfig) {
    if (domConfig.listeners) {
      domConfig.listeners.un();
      domConfig.listeners = null;
    }

    this[name] = null;
  }
  /**
   * This method returns a {@link Core.helper.DomHelper#function-createElement-static} config object that describes
   * the desired elements for this instance.
   *
   * This method is called to produce the initial DOM structure and again as necessary to generate the DOM for the
   * current state. The DOM produced by these subsequent calls is then passed through
   * {@link Core.helper.DomSync#function-sync-static DomSync.sync()} to update the DOM.
   * @returns {Object}
   */

  renderDom() {
    // abstract method provided by derived class
    return {};
  }
  /**
   * This property is the object to use when calling the `renderDom` method. It may evaluate to this instance (if
   * the class declares its render configs) or a helper object to track config usage during the `renderDom` call.
   * @property {Object}
   * @private
   */

  get renderContext() {
    const me = this,
          meta = me.$meta,
          C = me.constructor;
    let renderConfigs = meta.renderConfigs || C.renderConfigs,
        context = me; // If the class author did not declare any configs as render:true, then we make a proxy-like object that can
    // detect getter calls to build that Set. Since the getters may not all trigger on any given rendering, we
    // cannot share this work across instances since they may take different control paths.

    if (!renderConfigs) {
      context = Object.create(me);
      renderConfigs = new Set();

      for (const name in meta.configs) {
        defineProperty$5(context, name, {
          get() {
            renderConfigs.add(name);
            return me[name];
          }

        });
      } // In order to be substitutable for the Renderable, we need the same method name... we just need to run
      // renderDom w/ our context as "this":

      context.renderDom = () => {
        return me.renderDom.call(context);
      };

      me.$renderConfigs = renderConfigs;
    } // Replace this getter with the actual context so we don't get called again:

    defineProperty$5(me, 'renderContext', {
      value: context
    });
    return context;
  }
  /**
   * This method synchronized the DOM produced by {@link #function-renderDom} with what was previously produced and
   * updates the elements accordingly.
   *
   * This method is buffered such that calls to it do not immediately execute. To perform the refresh immediately,
   * do this:
   *```
   *  instance.refreshDom.now();
   *```
   * To flush any potential updates to the DOM (and do nothing if there are none), do this:
   *```
   *  instance.refreshDom.flush();
   *```
   * To determine if there are updates to the DOM pending, do this:
   *```
   *  if (instance.refreshDom.isPending) {
   *      ...
   *  }
   *```
   */

  refreshDom() {
    DomSync.sync({
      targetElement: this.element,
      domConfig: this.renderContext.renderDom(),
      refOwner: this,
      // This limits the sync() to only removing the classes and styles added by previous renderings. This
      // allows dynamically added styles and classes to be preserved:
      strict: true
    });
  } //endregion

  onListen(eventName) {
    // Only add the b-drawable-el when someone first listens for the draw event since it may never happen...
    if (eventName === 'draw') {
      this.initDrawable();
    }
  }

  initDrawable() {
    const me = this;

    if (!me.intersector) {
      let el = Object.values(me.byRef).find(el => el.classList.contains('b-drawable'));

      if (!el) {
        el = me.element;
        el.classList.add('b-drawable');
      } // TODO Not supported in IE11 even w/polyfills at the moment

      me.intersector = true; // me.intersector = new IntersectionObserver(entries => {
      //     if (!me.isDestroyed) {
      //         entries.forEach(ent => {
      //             if (ent.isIntersecting) {
      //                 me.onDraw({
      //                     count : ++me.drawCounter
      //                 });
      //             }
      //         });
      //     }
      // });
      //
      // me.intersector.observe(el);

      EventHelper.on({
        element: DomHelper.createElement({
          parent: el,
          class: 'b-drawable-el'
        }),

        animationend({
          animationName
        }) {
          if (animationName === 'b-drawable-anim') {
            me.onDraw({
              count: ++me.drawCounter
            });
          }
        }

      });
    }
  }

  onDraw(ev) {
    this.trigger('draw', ev);
  }

}
Renderable.$idSeed = 0;
Object.assign(Renderable.prototype, {
  drawCounter: 0,
  hasGeneratedId: false,
  intersector: null
});
Renderable._$name = 'Renderable';

/**
 * @module Core/widget/Mask
 */

/**
 * Masks a target element (document.body if none is specified). Call static methods for ease of use or make instance
 * for reusability.
 *
 * @example
 * Mask.mask('hello');
 * Mask.unmask();
 *
 * @externalexample widget/Mask.js
 *
  * @example
 * // Using progress by calling static method
 * let mask = Mask.mask({
 *   text:'The task is in progress',
 *   progress: 0,
 *   maxProgress: 100
 * }, document.body);
 * let timer = setInterval(()=>{
 *   mask.progress += 5;
 *   if(mask.progress >= mask.maxProgress) {
 *     Mask.unmask();
 *     clearInterval(timer)
 *   }
 * },100)
 *
 * @example
 * // Using progress to mask a Bryntum component
 * scheduler.mask({
 *  text:'Loading in progress',
 *   progress: 0,
 *   maxProgress: 100
 * })
 * let timer = setInterval(()=>{
 *   scheduler.masked.progress += 5;
 *   if(scheduler.masked.progress >= scheduler.masked.maxProgress) {
 *     scheduler.unmask();
 *     clearInterval(timer)
 *   }
 * },100)
 *
 */

class Mask extends Renderable {
  //region Config
  static get $name() {
    return 'Mask';
  } // Factoryable type name

  static get type() {
    return 'mask';
  }

  static get configurable() {
    return {
      /**
       * Set this config to trigger an automatic close after the desired delay:
       * ```
       *  mask.autoClose = 2000;
       * ```
       * If the mask has an `owner`, its `onMaskAutoClosing` method is called when the close starts and its
       * `onMaskAutoClose` method is called when the close finishes.
       * @config {Number}
       * @private
       */
      autoClose: null,

      /**
       * The portion of the {@link #config-target} element to be covered by this mask. By default, the mask fully
       * covers the `target`. In some cases, however, it may be desired to only cover the `'body'` (for example,
       * in a grid).
       *
       * This config is set in conjunction with `owner` which implements the method `syncMaskCover`.
       *
       * @config {String}
       * @private
       */
      cover: null,

      /**
       * The icon to show next to the text. Defaults to showing a spinner
       * @config {String}
       * @default
       */
      icon: 'b-icon b-icon-spinner',
      errorDefaults: {
        icon: 'b-icon b-icon-warning',
        autoClose: 3000,
        showDelay: 0
      },

      /**
       * The maximum value of the progress indicator
       * @property {Number}
       */
      maxProgress: null,

      /**
       * Mode: bright, bright-blur, dark or dark-blur
       * @config {String}
       * @default
       */
      mode: 'dark',

      /**
       * Number expressing the progress
       * @property {Number}
       */
      progress: null,
      // The owner is involved in the following features:
      //
      // - The `autoClose` timer calls `onMaskAutoClose`.
      // - The `cover` config calls `syncMaskCover`.
      // - If the `target` is a string, that string names the property of the `owner` that holds the
      //   `HTMLElement` reference.

      /**
       * The owning widget of this mask. This is required if `target` is a string.
       *
       * @config {Object|Core.widget.Widget}
       */
      owner: null,

      /**
       * The element to be masked. If this config is a string, that string is the name of the property of the
       * `owner` that holds the `HTMLElement` that is the actual target of the mask.
       *
       * NOTE: In prior releases, this used to be specified as the `element` config, but that is now, as with
       * `Widget`, the primary element of the mask.
       *
       * @config {String|HTMLElement}
       */
      target: null,

      /**
       * The text (or HTML) to show in mask
       * @config {String}
       */
      text: null,
      // TODO - perhaps a better way to deal w/multiple reasons to mask something would be a mask FIFO behind
      //  the scenes so that only one mask is visible at a time. That would take a bit of tinkering in the
      //  hide/show mechanism but feels like a more natural way to go. In which case, this config would go away
      //  and multiple masks would simply cooperate. https://github.com/bryntum/support/issues/190
      type: null,

      /**
       * The number of milliseconds to delay before making the mask visible. If set, the mask will have an
       * initial `opacity` of 0 but will function in all other ways as a normal mask. Setting this delay can
       * reduce flicker in cases where load operations are typically short (for example, a second or less).
       *
       * @config {Number}
       */
      showDelay: null,
      useTransition: false
    };
  }

  static get delayable() {
    return {
      deferredClose: 0,
      delayedShow: 0
    };
  } //endregion
  //region Init

  construct(config) {
    if (config) {
      let el = config.element,
          cfg; // Upgrade config -> cfg
      // Treat config as readonly, cfg is lazily copied and writable

      if (el) {
        VersionHelper.deprecate('Core', '4.0.0', 'Mask "element" config has been renamed to "target"');
        config = cfg = Object.assign({}, config);
        delete cfg.element;
        cfg.target = el;
      }

      el = config.target;

      if (typeof el === 'string') {
        config = cfg = cfg || Object.assign({}, config);
        cfg.target = config.owner[el]; // must supply "owner" in this case
      }
    }

    super.construct(config);

    if (!this.target) {
      this.target = document.body;
    }

    this.show();
  }

  doDestroy() {
    const me = this,
          {
      element
    } = me;

    if (element) {
      me.element = null;

      if (me.mode.endsWith('blur')) {
        DomHelper.forEachChild(element, child => {
          child.classList.remove(`b-masked-${me.mode}`);
        });
      }

      me.target.classList.remove('b-masked');
      me.target[me.maskName] = null;
    }

    super.doDestroy();
  }

  get maskElement() {
    // TODO log on use of deprecated property?
    return this.element;
  }

  get maskName() {
    const type = this.type;
    return `mask${typeof type === 'string' ? type.trim() : ''}`;
  }

  set error(value) {
    this.setConfig(this.errorDefaults);
    this.text = value;
  }

  renderDom() {
    const me = this,
          {
      maxProgress
    } = me;
    return {
      class: {
        'b-mask': 1,
        'b-delayed-show': me.showDelay,
        'b-widget': 1,
        [`b-mask-${me.mode}`]: 1,
        'b-progress': maxProgress,
        'b-prevent-transitions': !me.useTransition
      },
      children: [{
        reference: 'maskContent',
        class: 'b-mask-content b-drawable',
        children: [maxProgress ? {
          reference: 'progressElement',
          class: 'b-mask-progress-bar',
          style: {
            width: `${Math.max(0, Math.min(100, Math.round(me.progress / maxProgress * 100)))}%`
          }
        } : null, {
          reference: 'maskText',
          class: 'b-mask-text',
          html: (me.icon ? `<i class="b-mask-icon ${me.icon}"></i>` : '') + me.text
        }]
      }]
    };
  } //endregion
  //region Static

  static mergeConfigs(...sources) {
    const ret = {};

    for (const src of sources) {
      if (typeof src === 'string') {
        ret.text = src;
      } else {
        ObjectHelper.assign(ret, src); // not Object.assign!
      }
    }

    return ret;
  }
  /**
   * Shows a mask with the specified message
   * @param {String|Object} text Message
   * @param {HTMLElement} target The element to mask
   * @returns {Core.widget.Mask}
   */

  static mask(text, target = document.body) {
    return new Mask(typeof text !== 'string' ? Object.assign({
      target
    }, text) : {
      target,
      text
    });
  }
  /**
   * Unmask
   * @param {HTMLElement} element Element to unmask
   * @returns {Promise} A promise which is resolved when the mask is gone
   */

  static unmask(element = document.body) {
    return element.mask && element.mask.close();
  } //endregion
  //region Config

  updateAutoClose(delay) {
    const me = this;
    me.deferredClose.cancel();

    if (delay) {
      me.deferredClose.delay = delay;
      me.deferredClose();
    }
  }

  updateCover() {
    const owner = this.owner;

    if (owner && owner.syncMaskCover) {
      owner.syncMaskCover(this);
    }
  }

  updateShowDelay(delay) {
    const delayedShow = this.delayedShow;
    delayedShow.delay = delay;

    if (!delay) {
      delayedShow.flush();
    }
  } //endregion
  //region Show & hide

  deferredClose() {
    const owner = this.owner;
    this.close().then(() => {
      if (owner && owner.onMaskAutoClose) {
        owner.onMaskAutoClose(this);
      }
    });

    if (owner && owner.onMaskAutoClosing) {
      owner.onMaskAutoClosing(this);
    }
  }

  delayedShow() {
    this.classes.remove('b-delayed-show');
  }
  /**
   * Show mask
   */

  show() {
    const me = this,
          {
      element,
      target,
      hiding,
      maskName
    } = me; // We don't do this because we may want to show but automatically close after a
    // brief delay. The order of applying those configs should not be an issue. In
    // other words, to stop the deferredClose, you must set autoClose to falsy.
    // me.deferredClose.cancel();

    if (hiding) {
      // Resolving seems much better than the only other options:
      //  1. Never settling
      //  2. Rejecting
      hiding.resolve(); // This will be nulled out as the promise resolves but that is a race condition
      // compared to the next hide() call.

      me.hiding = null;
      me.clearTimeout('hide');
    }

    if (me.showDelay) {
      element.classList.add('b-delayed-show');
      me.delayedShow();
    }

    element.classList.add('b-visible');
    element.classList.remove('b-hidden');
    target.classList.add('b-masked');

    if (!target[maskName]) {
      target[maskName] = me;
      target.appendChild(element);
    }

    me.shown = true; // blur has to blur child elements

    if (me.mode.endsWith('blur')) {
      DomHelper.forEachChild(target, child => {
        if (child !== element) {
          child.classList.add(`b-masked-${me.mode}`);
        }
      });
    }
  }
  /**
   * Hide mask
   * @returns {Promise} A promise which is resolved when the mask is hidden, or immediately if already hidden
   */

  hide() {
    const me = this,
          {
      target,
      element
    } = me;
    let hiding = me.hiding;

    if (!hiding) {
      if (!me.shown) {
        return Promise.resolve();
      }

      me.hiding = hiding = new Promissory();
      me.shown = false;
      element.classList.remove('b-visible');
      element.classList.add('b-hidden');
      target.classList.remove('b-masked');

      if (me.mode.endsWith('blur')) {
        DomHelper.forEachChild(target, child => {
          if (child !== element) {
            child.classList.remove(`b-masked-${me.mode}`);
          }
        });
      }

      hiding.promise = hiding.promise.then(() => {
        if (me.hiding === hiding) {
          me.hiding = null;
        }
      }); // TODO: use AnimationHelper when available

      me.setTimeout(() => hiding.resolve(), 500, 'hide');
    }

    return hiding.promise;
  }
  /**
   * Close mask (removes it)
   * @returns {Promise} A promise which is resolved when the mask is closed
   */

  close() {
    return this.hide().then(() => {
      this.destroy();
    });
  } //endregion

}
Mask._$name = 'Mask';

/**
 * @module Core/widget/Widget
 */

const isTransparent = /transparent|rgba\(0,\s*0,\s*0,\s*0\)/,
      textInputTypes = {
  INPUT: 1,
  TEXTAREA: 1
},
      mergeAnim = (value, was) => {
  // The show/hideAnimation objects can only have one animation property, but it is fine to merge if they have
  // the same property.
  return value && was && was[ObjectHelper.keys(value)[0]] ? Config.merge(value, was) : value;
},
      // Need braces here. MUST NOT return false
widgetTriggerPaint = w => {
  w.isVisible && w.triggerPaint();
},
      negationPseudo = /^:not\((.+)\)$/,
      isScaled = w => w.scale != null,
      {
  hasOwnProperty: hasOwnProperty$6
} = Object.prototype,
      parseDuration = d => parseFloat(d) * (d.endsWith('ms') ? 1 : 1000),
      immediatePromise$3 = Promise.resolve(),
      alignedClass = ['b-aligned-above', 'b-aligned-right', 'b-aligned-below', 'b-aligned-left'];
/**
 * Base class for other widgets. The Widget base class simply encapsulates an element, and may optionally contain some
 * specified {@link #config-html}.
 *
 * Subclasses should at override the {@link #function-template} member function to return an HTML string to create their
 * own encapsulating element and internal structure.
 *
 * ## Resolving properties
 *
 * Values for a Widgets properties can be resolved from the ownership hierarchy. For example a text field in a toolbar
 * can get its initial value from a property on the container owning the toolbar. This is achieved by prefixing the
 * desired property name with 'up.':
 *
 * ```javascript
 * const grid = new Grid((
 *   tbar : [
 *     {
 *         type  : 'numberfield',
 *         // Fields value will be retrieved from the grids rowHeight property
 *         value : 'up.rowHeight'
 *     }
 *   ]
 * });
 * ```
 *
 * Please note that this is for now a one way one time binding, the value will only be read initially and not kept up to
 * date on later changes.
 *
 * @mixes Core/mixin/Events
 * @mixes Core/localization/Localizable
 * @extends Core/Base
 * @classType widget
 */

class Widget extends Base.mixin(Localizable, Events, Delayable, Identifiable, Factoryable) {
  //region Config

  /**
   * Class name getter.
   * Used when original ES6 class name is minified or mangled during production build.
   * Should be overriden in each class which extends Widget or it descendants.
   *
   * ```javascript
   * class MyNewClass extends Widget {
   *     static get $name() {
   *        return 'MyNewClass';
   *     }
   * }
   * ```
   *
   * @static
   * @member {String} $name
   */
  static get $name() {
    return 'Widget';
  } // Factoryable type name

  static get type() {
    return 'widget';
  }

  static get configurable() {
    return {
      /**
       * Get this widget's encapsulating HTMLElement, which is created along with the widget but added to DOM at
       * render time.
       * @member {HTMLElement} element
       * @readonly
       * @category DOM
       */

      /**
       * A {@link Core.helper.DomHelper#function-createElement-static} config object or HTML string from which to
       * create the Widget's element.
       * @private
       * @config {Object|String}
       * @category DOM
       */
      element: true,
      callOnFunctions: true,

      /**
       * Get/set widgets id
       * @member {String} id
       * @category DOM
       */

      /**
       * Widget id, if not specified one will be generated. Also used for lookups through Widget.getById
       * @config {String}
       * @category DOM
       */
      id: '',

      /**
       * Html to display initially
       * @config {String}
       * @category DOM
       */
      html: null,

      /**
       * Set HTML content safely, without disturbing sibling elements which may have been
       * added to the {@link #property-contentElement} by plugins and features.
       * When specifying html, this widget's element will also have the {@link #config-htmlCls}
       * added to its classList, to allow targeted styling.
       * @member {String} content
       * @category DOM
       */

      /**
       * The HTML content that coexists with sibling elements which may have been added to the
       * {@link #property-contentElement} by plugins and features.
       * When specifying html, this widget's element will also have the {@link #config-htmlCls}
       * class added to its classList, to allow targeted styling.
       * @config {String} content
       * @category DOM
       */
      content: null,

      /**
       * true if no id was set, will use generated id instead (widget1, ...). Toggle automatically on creation
       * @default false
       * @type {Boolean}
       * @private
       * @category Misc
       */
      hasGeneratedId: null,

      /**
       * Custom CSS classes to add to element.
       * May be specified as a space separated string, or as an object in which property names
       * with truthy values are used as the class names:
       * ```javascript
       *  cls : {
       *      'b-my-class'     : 1,
       *      [this.extraCls]  : 1,
       *      [this.activeCls] : this.isActive
       *  }
       *  ```
       *
       * @config {String|Object}
       * @category CSS
       */
      cls: {
        $config: {
          merge: 'classList'
        },
        value: null
      },

      /**
       * Custom CSS classes to add to the {@link #property-contentElement}.
       * May be specified as a space separated string, or as an object in which property names
       * with truthy values are used as the class names:
       * ```javascript
       *  cls : {
       *      'b-my-class'     : 1,
       *      [this.extraCls]  : 1,
       *      [this.activeCls] : this.isActive
       *  }
       *  ```
       *
       * @config {String|Object}
       * @category CSS
       */
      contentElementCls: {
        $config: {
          merge: 'classList'
        },
        value: null
      },

      /**
       * The CSS class(es) to add when HTML content is being applied to this widget.
       * @config {String|Object}
       * @category CSS
       */
      htmlCls: {
        $config: {
          merge: 'classList'
        },
        value: {
          'b-html': 1
        }
      },

      /**
       * Custom style spec to add to element
       * @config {String}
       * @category CSS
       */
      style: null,

      /**
       * Get/set element's disabled state
       * @member {Boolean} disabled
       * @category Misc
       */

      /**
       * Disabled or enabled
       * @default false
       * @config {Boolean}
       * @category Misc
       */
      disabled: null,

      /**
       * Get/set element's readOnly state. This is only valid if the widget is an input
       * field, __or contains input fields at any depth__. Updating this property will trigger
       * a {@link #event-readOnly} event.
       *
       * All descendant input fields follow the widget's setting. If a descendant
       * widget has a readOnly config, that is set.
       * @member {Boolean} readOnly
       * @category Misc
       */

      /**
       * Whether this widget is read-only.  This is only valid if the widget is an input
       * field, __or contains input fields at any depth__.
       *
       * All descendant input fields follow the widget's setting. If a descendant
       * widget has a readOnly config, that is set.
       * @default false
       * @config {Boolean}
       * @category Misc
       */
      readOnly: null,

      /**
       * Element (or element id) to adopt as this Widget's encapsulating element. The widget's
       * content will be placed inside this element.
       *
       * If this widget has not been configured with an id, it will adopt the id of the element
       * in order to preserve CSS rules which may apply to the id.
       * @config {HTMLElement|String}
       * @default
       * @category DOM
       */
      adopt: null,

      /**
       * Element (or element id) to append this widgets element to
       * @config {HTMLElement|String}
       * @default
       * @category DOM
       */
      appendTo: null,

      /**
       * Element (or element id) to insert this widget before. If provided, {@link #config-appendTo} config is ignored.
       * @config {HTMLElement|String}
       * @category DOM
       */
      insertBefore: null,

      /**
       * Element (or element id) to append this widget element to, as a first child. If provided, {@link #config-appendTo} config is ignored.
       * @config {HTMLElement|String}
       * @category DOM
       */
      insertFirst: null,

      /**
       * Object to apply to elements dataset (each key will be used as a data-attribute on the element)
       * @config {Object}
       * @category DOM
       */
      dataset: null,

      /**
       * Tooltip for the widget, either as a string or as a Tooltip config object.
       *
       * By default, the Widget will use a single, shared instance to display its tootip as configured,
       * reconfiguring it to the specification before showing it. Therefore, it may not be permanently
       * mutated by doing things such as adding fixed event listeners.
       *
       * To have this Widget *own* its own `Tooltip` instance, add the property `newInstance : true`
       * to the configuration. In this case, the widget's {@link #property-owner} will be this Widget.
       * @config {String|Object}
       * @category Misc
       */
      tooltip: {
        $config: ['nullify'],
        value: null
      },

      /**
       * Prevent tooltip from being displayed on touch devices. Useful for example for buttons that display a
       * menu on click etc, since the tooltip would be displayed at the same time.
       * @config {Boolean}
       * @default false
       * @category Misc
       */
      preventTooltipOnTouch: null,

      /**
       * Specify true to have widget monitoring its own resize.
       * @config {Boolean}
       * @default false
       * @category Misc
       */
      monitorResize: {
        $config: ['lazy', 'nullify'],
        value: null
      },

      /**
       * Set to `true` to apply the default mask to the widget. Alternatively, this can be the mask message or a
       * {@link Core.widget.Mask} config object.
       * @config {Boolean|String|Object|Core.widget.Mask}
       * @category Misc
       */
      masked: null,

      /**
       * This config object contains the defaults for the {@link Core.widget.Mask} created for the
       * {@link #config-masked} config. Any properties specified in the `masked` config will override these
       * values.
       * @config {Object|Core.widget.Mask}
       * @default
       * @category Misc
       */
      maskDefaults: {
        target: 'element'
      },
      cache: {},

      /**
       * Set to `true` to move the widget out of the document flow and position it
       * absolutely in browser viewport space.
       * @config {Boolean}
       * @default
       * @category Float & align
       */
      floating: null,

      /**
       * Set to `true` when a widget is rendered into another widget's  {@link #property-contentElement}, but must not
       * participate in the standard layout of that widget, and must be positioned relatively to that
       * widget's {@link #property-contentElement}.
       *
       * {@link Core.widget.Editor Editor}s are positioned widgets.
       * @config {Boolean}
       * @default
       * @category Float & align
       */
      positioned: null,

      /**
       * Only valid if this Widget is {@link #config-floating}
       * Set to `true` to be able to drag a widget freely on the page. Or set to an object with a ´handleSelector´ property which controls when a drag
       * should start.
       *
       * ```javascript
       *
       * draggable : {
       *     handleSelector : ':not(button)'
       * }
       *
       * ```
       *
       * @config {Boolean|Object}
       * @default false
       * @category Float & align
       */
      draggable: null,

      /**
       * _Only valid if this Widget is {@link #config-floating}._
       *
       * How to align this element with its target when {@link #function-showBy} is called
       * passing a simple element as an align target.
       *
       * Either a full alignment config object as passed to {@link #function-showBy}, or for simple
       * cases, the edge alignment string to use.
       *
       * When using a simple string, the format is `'[trbl]n-[trbl]n'` and it specifies our edge and
       * the target edge plus optional offsets from 0 to 100 along the edges to align to.
       *
       * See the {@link #function-showBy} function for more details about using the object form.
       *
       * Once set, this is stored internally in object form.
       * @config {Object|String}
       * @category Float & align
       */
      align: 't-b',
      // NOTE: this config changes meaning in tools and grid columns, so we don't get too clever here (such as
      //  config merge fn that promotes strings to objects)

      /**
       * Only valid if this Widget is {@link #config-floating}
       * Set to `true` to centre the Widget in browser viewport space.
       * @config {Boolean}
       * @default
       * @category Float & align
       */
      centered: null,

      /**
       * Only valid if this Widget is {@link #config-floating} and being shown through {@link #function-showBy}.
       * Element, Widget or Rectangle to which this Widget is constrained.
       * @config {HTMLElement|Core.widget.Widget|Core.helper.util.Rectangle}
       * @default document.body
       * @category Float & align
       */
      constrainTo: undefined,

      /**
       * Only valid if this Widget is {@link #config-floating} and being shown through {@link #function-showBy}.
       * `true` to show a connector arrow pointing to the align target.
       * @config {Boolean}
       * @default false
       * @category Float & align
       */
      anchor: null,

      /**
       * The owning Widget of this Widget. If this Widget is directly contained, this will be the containing Widget.
       * If there is a `forElement`, this config will be that element's encapsulating Widget.
       *
       * If this Widget is floating, this config must be specified by the developer.
       * @config {Core.widget.Widget}
       * @category Float & align
       */
      owner: null,

      /**
       * Defines what to do if document is scrolled while Widget is visible (only relevant when floating is set to true).
       * Valid values: ´null´: do nothing, ´hide´: hide the widget or ´realign´: realign to the target if possible.
       * @config {String}
       * @default
       * @category Float & align
       */
      scrollAction: null,

      /**
       * Only valid if this Widget is {@link #config-floating}. An object which defined which CSS style
       * property should be animated upon hide, and how it should be animated eg:
       *
       * ```javascript
       * {
       *    opacity: {
       *        to : 0,
       *        duration: '10s',
       *        delay: '0s'
       *    }
       * }
       * ```
       *
       * Set to `'false'` to disable animation.
       *
       * @config {Boolean|Object}
       * @category Float & align
       */
      hideAnimation: {
        $config: {
          merge: mergeAnim
        },
        value: null
      },

      /**
       * Only valid if this Widget is {@link #config-floating}. An object which defined which CSS style
       * property should be animated upon show, and how it should be animated eg:
       *
       * ```javascript
       * {
       *    opacity: {
       *        to : 1,
       *        duration: '10s',
       *        delay: '0s'
       *    }
       * }
       * ```
       *
       * Set to `'false'` to disable animation.
       *
       * @config {Boolean|Object}
       * @category Float & align
       */
      showAnimation: {
        $config: {
          merge: mergeAnim
        },
        value: null
      },

      /**
       * Only valid if this Widget is {@link #config-floating}. The x position for the widget.
       *
       * @config {Number}
       * @default
       * @category Float & align
       */
      x: null,

      /**
       * Only valid if this Widget is {@link #config-floating}. The y position for the widget.
       *
       * @config {Number}
       * @default
       * @category Float & align
       */
      y: null,

      /**
       * Accessor to the {@link Core.helper.util.Scroller} which can be used
       * to both set and read scroll information.
       * @member {Core.helper.util.Scroller} scrollable
       * @category Scrolling
       */

      /**
       * Specifies whether (and optionally in which axes) a Widget may scroll. `true` means this widget
       * may scroll in both axes. May be an object containing boolean `overflowX` and `overflowY` properties which are applied
       * to CSS style properties `overflowX` and `overflowY`. If they are boolean, they are translated
       * to CSS overflow properties thus:
       *
       * *`true` -> `'auto'`
       * *`false` -> `'hidden'`
       *
       * After initialization, this property yields a {@link Core.helper.util.Scroller} which may be used
       * to both set and read scroll information.
       *
       * A Widget uses its `get overflowElement` property to select which element is to be scrollable.
       * By default, in the base `Widget` class, this is the Widget's encapsulating element. Subclasses
       * may implement `get overflowElement` to scroll inner elements.
       * @config {Boolean|Object|Core.helper.util.Scroller}
       * @default false
       * @category Scrolling
       */
      scrollable: {
        $config: ['nullify'],
        value: null
      },

      /**
       * The class to instantiate to use as the {@link #config-scrollable}. Defaults to {@link Core.helper.util.Scroller}.
       * @internal
       * @config {Core.helper.util.Scroller}
       * @category Scrolling
       */
      scrollerClass: Scroller,

      /**
       * The name of the property to set when a single value is to be applied to this Widget. Such as when used
       * in a grid WidgetColumn, this is the property to which the column's `field` is applied.
       * @config {String}
       * @default 'html'
       * @category Misc
       */
      defaultBindProperty: 'html',

      /**
       * Event that should be considered the default action of the widget. When that event is triggered the
       * widget is also expected to trigger an `action` event. Purpose is to allow reacting to most widgets in
       * a coherent way.
       * @private
       * @config {String}
       * @category Misc
       */
      defaultAction: null,

      /**
       * Widget's width, used to set element style.width. Either specify a valid width string or a number, which
       * will get 'px' appended. We recommend using CSS as the primary way to control width, but in some cases
       * this config is convenient.
       * @config {String|Number}
       * @category Layout
       */
      width: null,

      /**
       * Widget's height, used to set element style.height. Either specify a valid height string or a number, which
       * will get 'px' appended. We recommend using CSS as the primary way to control height, but in some cases
       * this config is convenient.
       * @config {String|Number}
       * @category Layout
       */
      height: null,

      /**
       * The element's maxHeight. Can be either a String or a Number (which will have 'px' appended). Note that
       * like {@link #config-height}, _reading_ the value will return the numeric value in pixels.
       * @config {String|Number}
       * @category Layout
       */
      maxHeight: null,

      /**
       * The elements maxWidth. Can be either a String or a Number (which will have 'px' appended). Note that
       * like {@link #config-width}, _reading_ the value will return the numeric value in pixels.
       * @config {String|Number}
       * @category Layout
       */
      maxWidth: null,

      /**
       * The elements minWidth. Can be either a String or a Number (which will have 'px' appended). Note that
       * like {@link #config-width}, _reading_ the value will return the numeric value in pixels.
       * @config {String|Number}
       * @category Layout
       */
      minWidth: null,

      /**
       * The element's minHeight. Can be either a String or a Number (which will have 'px' appended). Note that
       * like {@link #config-height}, _reading_ the value will return the numeric value in pixels.
       * @config {String|Number}
       * @category Layout
       */
      minHeight: null,
      // not public, only used by us in docs
      scaleToFitWidth: null,
      allowGrowWidth: true,
      // only used if scaleToFitWidth is true

      /**
       * Get element's margin property. This may be configured as a single number or a `TRBL` format string.
       * numeric-only values are interpreted as pixels.
       * @member {Number|String} margin
       * @category Layout
       */

      /**
       * Widget's margin. This may be configured as a single number or a `TRBL` format string.
       * numeric-only values are interpreted as pixels.
       * @config {Number|String}
       * @category Layout
       */
      margin: null,

      /**
       * Get element's flex property. This may be configured as a single number or a format string:
       *
       *      <flex-grow> <flex-shrink> <flex-basis>
       *
       * Numeric-only values are interpreted as the `flex-grow` value.
       * @member {Number|String} flex
       * @category Layout
       */

      /**
       * When this widget is a child of a {@link Core.widget.Container}, it will by default be participating in a
       * flexbox layout. This config allows you to set this widget's
       * <a href="https://developer.mozilla.org/en-US/docs/Web/CSS/flex">flex</a> style.
       * This may be configured as a single number or a `<flex-grow> <flex-shrink> <flex-basis>` format string.
       * numeric-only values are interpreted as the `flex-grow` value.
       * @config {Number|String}
       * @category Layout
       */
      flex: null,

      /**
       * A widgets weight determines its position among siblings when added to a {@link Core.widget.Container}.
       * Higher weights go further down.
       * @config {Number}
       * @category Layout
       */
      weight: null,

      /**
       * Get/set this widget's `align-self` flexbox setting. This may be set to modify how this widget is aligned
       * within the cross axis of a flexbox layout container.
       * @member {String} alignSelf
       * @category Layout
       */

      /**
       * When this widget is a child of a {@link Core.widget.Container}, it will by default be participating in a
       * flexbox layout. This config allows you to set this widget's
       * <a href="https://developer.mozilla.org/en-US/docs/Web/CSS/align-self">align-self</a> style.
       * @config {String}
       * @category Layout
       */
      alignSelf: null,

      /**
       * Configure as `true` to have the component display a translucent ripple when its
       * {@link #property-focusElement}, or {@link #property-element} is tapped *if the
       * current theme supports ripples*. Out of the box, only the Material theme supports ripples.
       *
       * This may also be a config object containing the following properties:
       *
       *  - `delegate  ` Optional. A CSS selector to filter which child elements trigger ripples. By default
       * the ripple is clipped to the triggering element.
       *  - `color     ` Optional, default = `#000`. A CSS color name or specification.
       *  - `radius    ` Optional, default is 100. The ending radius of the ripple.
       * Note that it will be clipped by the target element by default.
       *  - `clip      ` A string which describes how to clip the ripple if it is not to be clipped to the default
       * element. Either the property of the widget to use as the clipping element, or a selector to
       * allow clipping to the closest matching ancestor to the target element.
       *
       * eg:
       *```
       *    columns  : [{}...],
       *    ripple   : {
       *        color : 'red',
       *        clip  : '.b-grid-row'
       *    },
       *    ...
       *```
       * @config {Boolean|Object}
       * @category Misc
       */
      ripple: null,

      /**
       * A title to display for the widget. Only in effect when inside a container that uses it (such as TabPanel)
       * @default
       * @config {String}
       * @category DOM
       */
      title: null,
      localizableProperties: ['title'],
      // Set this flag to require element to have a size to be considered visible
      requireSize: false,

      /**
       * An identifier by which this widget will be registered in the {@link Core.widget.Container#property-widgetMap}
       * of all ancestor containers.
       *
       * If omitted, this widget will be registered using its {@link #config-id}. In most cases `ref` is
       * preferable over `id` since `id` is required to be globally unique while `ref` is not.
       *
       * The `ref` value is also added to the elements dataset, to allow targeting it using CSS etc.
       * @default
       * @config {String}
       * @category Misc
       */
      ref: null,

      /**
       * Get/set the widget hidden state.
       *
       * Note: `hidden : false` does *not* mean that this widget is definitely visible.
       * To ascertain visibilty, use the {@link #property-isVisible} property.
       * @member {Boolean} hidden
       * @category Visibility
       */

      /**
       * Configure with true to make widget initially hidden.
       * @default false
       * @config {Boolean}
       * @category Visibility
       */
      hidden: null,

      /**
       * Text alignment: 'left', 'center' or 'right'. Applied by adding a `b-text-align-xx` class to the widgets
       * element. Blank by default, which does not add any alignment class.
       * @config {String}
       */
      textAlign: null,
      // When adding our scroll listeners to hide/realign, we ignore events
      // happening too quickly as a result of the show/align action
      ignoreScrollDuration: 500,

      /**
       * The tag name of this Widget's root element
       * @config {String}
       * @default
       */
      tag: 'div'
    };
  }
  /**
   * Call once per class for custom widgets to have them register with the `Widget` class, allowing them to be created
   * by type.
   *
   * For example:
   * ```javascript
   * class MyWidget extends Widget {
   *   static get type() {
   *     return 'mywidget';
   *   }
   * }
   * MyWidget.initClass();
   * ```
   * @method initClass
   * @static
   */

  static get factoryable() {
    return {
      defaultType: 'widget'
    };
  }

  static get identifiable() {
    return {};
  } //endregion
  //region Init & destroy

  construct(config = {}, ...args) {
    const me = this; // Flag so we know when our dimensions have been constrained during alignment

    me.alignConstrained = 0;
    me.byRef = Object.create(null);
    me.callRealign = me.realign.bind(me);
    me.doHideOrRealign = me.doHideOrRealign.bind(me);
    me.initialRender = true;
    me._isUserAction = false; // Base class applies configs.

    super.construct(config, args);
    me.finalizeInit();
  }

  isType(type, deep) {
    return Widget.isType(this, type, deep);
  }

  startConfigure(config) {
    const me = this,
          {
      adopt,
      element
    } = me; // Adopt the preexisting element as our element before configuration proceeds.

    if (adopt) {
      const adoptElement = typeof adopt === 'string' ? document.getElementById(adopt) : adopt,
            previousHolder = Widget.fromElement(adoptElement); // If we are taking it over from a previous iteration, destroy the previous holder. This is not officially
      // supported, but CodeEditor relies on it working

      if (previousHolder && previousHolder.adopt && previousHolder !== me) {
        const previousHolderAdopt = typeof previousHolder.adopt === 'string' ? document.getElementById(previousHolder.adopt) : previousHolder.adopt;

        if (previousHolderAdopt === adoptElement) {
          previousHolder.destroy();
        }
      } // On destroy, leave these

      me.preExistingElements = Array.from(adoptElement.childNodes);
      me.adoptedElementClassName = adoptElement.className;
      me.adoptedElementCssText = adoptElement.style.cssText; // Adopt the host element's id if we don't have one so that we do not override
      // it and invalidate any ad-based CSS rules.

      if (adoptElement.id && !config.id) {
        me.id = element.id = adoptElement.id;
      }

      DomHelper.syncAttributes(element, adoptElement);

      for (let i = 0, {
        length
      } = element.childNodes; i < length; i++) {
        adoptElement.appendChild(element.childNodes[0]);
      }

      me._element = adoptElement;
      delete me._contentRange;
    } // Mutually exclusive with scaleToFitWidth.
    // Observe container element before the cascade down to descendants.
    // Outer elements are expected to fire resize first.
    // It's a lazy config, so this is the time to flush it through to begin monitoring.

    if (!me.scaleToFitWidth) {
      me.getConfig('monitorResize');
    }

    super.startConfigure(config);
  }

  updateTitle(title) {
    if (this.titleElement) {
      this.titleElement.innerHTML = title;
    }
  }
  /**
   * This method is called by `DomHelper.createElement` and `DomSync.sync` as new reference elements are created.
   * @param {String} name The name of the element, i.e., the value of its `reference` attribute.
   * @param {HTMLElement} el The element instance
   * @param {Object} [domConfig] The DOM config object.
   * @private
   */

  attachRef(name, el, domConfig) {
    const me = this,
          listeners = domConfig && domConfig.listeners;
    me[name] = me.byRef[name] = el; // Key elements contain owner pointer if data is supported (Not on IE SVG).

    el.dataset && (el.dataset.ownerCmp = me.id);

    if (listeners) {
      domConfig.listeners = {
        on: listeners,
        un: EventHelper.on(Object.assign({
          element: el,
          thisObj: me
        }, listeners))
      };
    }
  }
  /**
   * This method is called by `DomSync.sync` as reference elements are removed from the DOM.
   * @param {String} name The name of the element, i.e., the value of its `reference` attribute.
   * @param {HTMLElement} el The element instance
   * @param {Object} domConfig The DOM config object.
   * @private
   */

  detachRef(name, el, domConfig) {
    if (domConfig.listeners) {
      domConfig.listeners.un();
      domConfig.listeners = null;
    }

    this[name] = null;
    delete this.byRef[name];
  }

  changeElement(element) {
    const me = this;
    let el, i;

    if (typeof element === 'string') {
      element = DomHelper.createElementFromTemplate(element);
    } else if (ObjectHelper.isObject(element)) {
      element = DomHelper.createElement(element, {
        refOwner: me
      });
    } else if (element.nodeType !== 1) {
      element = DomHelper.createElementFromTemplate(me.template(me));
    }

    me._element = element;
    const classList = element.classList;
    element.id = me.id; // Add the hierarchy, eg ['b-combo b-pickerfield b-textfield b-widget']
    // TODO: apply not needed when IE11 not supported, classList.add takes multiple classes
    //classList.add(...me.widgetClassList);

    element.className += ' ' + me.widgetClassList.join(' ');

    if (me.contentElementCls) {
      me.contentElement.className += ' ' + me.contentElementCls;
    }

    if (me._hidden) {
      classList.add('b-hidden');
    }

    if (me._readOnly) {
      classList.add('b-readonly');
    } // The environmental classes only need to be added to orphan Widgets.
    // If we have a parent container, that will have them.

    if (!me.parent) {
      DomHelper.addClasses(element, Widget.outerCls);
    }

    const namedElements = element.querySelectorAll('[data-reference]');

    for (i = 0; i < namedElements.length; ++i) {
      el = namedElements[i];
      me.attachRef(el.getAttribute('data-reference'), el);
      el.removeAttribute('data-reference');
    }

    return element;
  }

  set parent(parent) {
    this._parent = parent;
    this._element && DomHelper[parent ? 'removeClasses' : 'addClasses'](this._element, Widget.outerCls);
  }

  get parent() {
    return this._parent;
  }

  get constrainTo() {
    const ret = this._constrainTo;
    return ret === undefined ? window : ret;
  }

  updateCentered(value) {
    const {
      element,
      _anchorElement
    } = this;

    if (value && !this.floating && !this.positioned) {
      throw new Error('`centered` is only relevant when a Widget is `floating` or `positioned`');
    }

    if (value) {
      element.classList.add('b-centered');
      element.style.transform = element.style.left = element.style.top = '';
      _anchorElement === null || _anchorElement === void 0 ? void 0 : _anchorElement.classList.add('b-hide-display');
      element.classList.remove('b-anchored');
    } else {
      element.classList.remove('b-centered');
    }
  }
  /**
   * The child element into which content should be placed. This means where {@link #config-html} should be put,
   * or, for {@link Core.widget.Container Container}s, where child items should be rendered.
   * @property {HTMLElement}
   * @readonly
   * @category DOM
   */

  get contentElement() {
    return this.element;
  }

  get contentRange() {
    const me = this,
          {
      contentElement
    } = me,
          contentRange = me._contentRange || (me._contentRange = BrowserHelper.isIE11 ? document.createRange() : new Range()); // Initialize the contentRange if it's collapsed.
    // It gets collapsed if the widget's element is removed from the DOM.

    if (contentRange.collapsed) {
      contentRange.setStart(contentElement, me.contentRangeStartOffset || 0);
      contentRange.setEnd(contentElement, me.contentRangeEndOffset || contentElement.childNodes.length);
    }

    return contentRange;
  }

  updateId(id, oldId) {
    super.updateId(id, oldId);

    if (oldId) {
      const {
        element
      } = this,
            refEls = element.querySelectorAll(`[data-owner-cmp="${oldId}"]`);

      for (let i = 0, {
        length
      } = refEls; i < length; i++) {
        refEls[i].dataset.ownerCmp = id;
      }

      element.id = id;
    }
  }
  /**
   * Get/set widgets elements style. The setter accepts a cssText string or a style config object, the getter always
   * returns a CSSStyleDeclaration
   * @property {String|Object|CSSStyleDeclaration}
   * @category DOM
   */

  get style() {
    return this.element ? this.element.ownerDocument.defaultView.getComputedStyle(this.element) : this._style;
  }

  updateStyle(style) {
    this.element && DomHelper.applyStyle(this.element, style);
  }
  /**
   * Get widgets elements dataset or assign to it
   * @property {Object}
   * @category DOM
   */

  get dataset() {
    return this.element.dataset;
  }

  get dragGhost() {
    return this.constructor._dragGhost || (this.constructor._dragGhost = DomHelper.createElement({
      // Safari won't allow dragging an empty node
      html: '\xa0',
      style: 'position:absolute;top:-10000em;left:-10000em'
    }));
  }

  changeDataset(dataset) {
    Object.assign(this.dataset, dataset);
  }
  /**
   * Called by the Base constructor after all configs have been applied.
   * @internal
   * @category Lifecycle
   */

  finalizeInit() {
    if (this.insertBefore || this.appendTo || this.insertFirst || this.adopt) {
      this.render();
    }
  }

  doDestroy() {
    const me = this,
          {
      preExistingElements,
      element
    } = me;
    me.unmask();

    if (element) {
      var _me$dragEventDetacher, _me$dragOverEventDeta;

      // If we get destroyed very quickly after a call to show,
      // we must kill the timers which add the realign listeners.
      me.clearTimeout(me.scrollListenerTimeout);
      me.clearTimeout(me.resizeListenerTimeout); // Remove listeners which are only added during the visible phase.
      // In its own method because it's called on hide and destroy.

      me.removeTransientListeners();

      if (me.floating || me.positioned) {
        me.hide();
      } else {
        me.revertFocus();
      }

      ResizeMonitor.removeResizeListener(element.parentElement, me.onParentElementResize);
      ResizeMonitor.removeResizeListener(element, me.onElementResize); // Remove elements *which we own* on destroy,

      if (me.adopt) {
        for (let nodes = Array.from(element.childNodes), i = 0, {
          length
        } = nodes; i < length; i++) {
          const el = nodes[i]; // If it's not preexisting, and not the floatRoot, remove it

          if (!preExistingElements.includes(el) && el !== Widget.floatRoot) {
            el.remove();
          }
        }

        element.className = me.adoptedElementClassName;
        element.style.cssText = me.adoptedElementCssText;
      } else {
        element.remove();
      }

      (_me$dragEventDetacher = me.dragEventDetacher) === null || _me$dragEventDetacher === void 0 ? void 0 : _me$dragEventDetacher.call(me);
      (_me$dragOverEventDeta = me.dragOverEventDetacher) === null || _me$dragOverEventDeta === void 0 ? void 0 : _me$dragOverEventDeta.call(me);
      me.dragGhost.remove();
    }

    super.doDestroy();
  } //endregion
  //region Render

  render(appendToElement, triggerPaint = true) {
    const me = this,
          {
      element
    } = me;
    me.emptyCache();

    if (me.syncElement && me.currentElement) {
      DomHelper.sync(element, me.currentElement);
    } else {
      // Use passed element to insert this widget directly
      if (appendToElement) {
        me.appendTo = typeof appendToElement === 'string' ? document.getElementById(appendToElement) : appendToElement;
        me.appendTo.appendChild(element);
      } // If nothing was passed directly, try to use configs preferring `insertBefore` first
      else if (me.insertBefore) {
          me.insertBefore = typeof me.insertBefore === 'string' ? document.getElementById(me.insertBefore) : me.insertBefore;
          me.insertBefore.parentElement.insertBefore(element, me.insertBefore);
        } else if (me.insertFirst) {
          me.insertFirst = typeof me.insertFirst === 'string' ? document.getElementById(me.insertFirst) : me.insertFirst; // Try to insert as a first child

          if (me.insertFirst.firstChild) {
            me.insertFirst.insertBefore(element, me.insertFirst.firstChild);
          } // insertBefore will throw exceptions if firstChild element not exists. Add as only child then.
          else {
              me.insertFirst.appendChild(element);
            }
        } else if (me.appendTo) {
          me.appendTo = typeof me.appendTo === 'string' ? document.getElementById(me.appendTo) : me.appendTo;
          me.appendTo.appendChild(element);
        }

      me.currentElement = element;
    } // The environmental classes only need to be added to a naked Widget.
    // If we are inside a Widget's element, that will have them.

    if (Widget.fromElement(element.parentElement)) {
      DomHelper.removeClasses(element, Widget.outerCls);
    }

    me.rendered = true;

    if (triggerPaint) {
      me.getConfig('contentRange');
      me.triggerPaint();
    }
  }
  /**
   * A function which, when passed an instance of this Widget, produces a valid HTML string which is compiled
   * to create the encapsulating element for this Widget, and its own internal DOM structure.
   *
   * Note that this just creates the DOM structure that *this* Widget owns. If it contains child widgets
   * (Such as for example a grid), this is not included. The template creates own structure.
   *
   * Certain elements within the generated element can be identified as special elements with a `reference="name"`
   * property. These will be extracted from the element upon creation and injected as the named property into
   * the Widget. For example, a {@link Core.widget.TextField} will have an `input` property which is its
   * `<input>` element.
   * @param {Core.widget.Widget} me The widget for which to produce the initial HTML structure.
   * @internal
   */

  template(me) {
    return `<${me.tag} class="${me.html ? me.htmlCls : ''}" ${me.name ? `data-name="${me.name}"` : ''}>${me.html || ''}</${me.tag}>`;
  } //endregion
  //region floating

  /**
   * If this Widget is {@link #config-floating} or {@link #config-positioned}, and visible,
   * aligns the widget according to the passed specification. To stop aligning, call this method without arguments.
   * For details, see the {@link #function-showBy} method.
   * @param {Object} [spec] Alignment options. May be an object as processed by the {@link #function-showBy} method,
   * or an `HTMLElement` to align to using this Widget's {@link #config-align} configuration.
   * @category Float & align
   */

  alignTo(spec) {
    var _anchor;

    const me = this,
          {
      lastAlignSpec,
      element
    } = me;

    if (!spec) {
      if (lastAlignSpec) {
        element.classList.remove(alignedClass[lastAlignSpec.zone]);
      }

      me.removeTransientListeners();
      me.anchor = me.lastAlignSpec = null;
      return;
    } // You can "alignTo" an element or a Widget or a Point, and allow our align config.
    // property to specify how.

    if (spec.nodeType === 1 || spec instanceof Widget || spec.$$name === 'Point') {
      spec = {
        target: spec
      };
    } // Release size constraints so we can align from scratch each time.

    me.releaseSizeConstraints();
    const {
      scrollable,
      constrainTo
    } = me,
          scale = me.scale || 1,
          passedTarget = spec.target,
          target = passedTarget && (passedTarget instanceof Rectangle ? passedTarget : passedTarget.element || passedTarget),
          myPosition = Rectangle.from(element, me.positioned ? element.offsetParent : null, true),
          aligningToElement = target && target.nodeType === 1;
    spec = ObjectHelper.assign({
      constrainTo,
      align: 'b-t',
      // we can end up w/o a value for this if an object replaces a string
      axisLock: me.axisLock,
      anchor: me.anchor
    }, me.align, spec); // As part of fallback process when fitting within constraints, this may shrink to minima specified
    // either on the align spec or the widget itself.

    const minWidth = spec.minWidth || me.minWidth,
          minHeight = spec.minHeight || me.minHeight; // Minima have a different meaning in an alignRectangle.
    // It means that the rectangle is willing to shrink down
    // to that size during constraint, *not* that it can never
    // be smaller than that size.

    myPosition.isAlignRectangle = true;
    minWidth && (myPosition.minWidth = minWidth * scale);
    minHeight && (myPosition.minHeight = minHeight * scale); // This is used by the realign call which may be called either when a global scroll is detected
    // or the constraining element is resized.

    me.lastAlignSpec = spec;

    if (aligningToElement && target.offsetParent) {
      // Translate the element into a browser viewport based Rectangle. Rectangle
      // Doesn't have the knowledge that we do to make this decision. Floating
      // alignment all takes place within browser viewport space, not document space.
      // Don't destroy the spec which was cached above with the element in it.
      spec = Object.setPrototypeOf({}, spec);
      spec.target = Rectangle.from(target, me.positioned ? element.offsetParent : null, !me.positioned); // Force the target to have an area so that intersect works.

      spec.target.height = Math.max(spec.target.height, 1);
      spec.target.width = Math.max(spec.target.width, 1); // Handle the target being clipped by the bounds of various elements.
      // For example in a grid, the SubGrid element will clip the left and right
      // but the main grid bodyContainer will clip the top and bottom.

      const clippedBy = me.clippedBy;

      if (clippedBy) {
        const clippedTarget = spec.target.intersect(clippedBy); // If there is an intersecting Rectangle with the forElement, align

        if (clippedTarget) {
          spec.target = clippedTarget;
        } // This is the case where the target is scrolled or positioned out of view.
        else {
            const result = me.hide(); // The hide method clears this flag.
            // Only this hide invocation must complete with the
            // targetHidden flag as true

            me.lastAlignSpec.targetHidden = true;
            return result;
          }
      } // This is the element which determines our position.
      // This is used in doHideOrRealign to see if a scroll event
      // will have affected our position.

      me.anchoredTo = target;
    } else {
      me.anchoredTo = null;
    }

    if (spec.anchor) {
      spec.anchorSize = me.anchorSize;
      element.appendChild(me.anchorElement);
    } // If we're positioned, the constrainTo must be a Rectangle in our offsetParent's coordinate space

    if (constrainTo && !constrainTo.isRectangle && me.positioned) {
      const isViewport = constrainTo === document || constrainTo === window;
      spec.constrainTo = Rectangle.from(isViewport ? document.body : spec.constrainTo, element.offsetParent);
    } // Flag to prevent infinite loop when setting html from a beforeAlign listener

    me.isAligning = true; // Allow outside world to modify the suggested position

    me.trigger('beforeAlign', spec);
    me.isAligning = false; // Calculate the best position WRT target rectangle, our rectangle, a constrainTo rectangle
    // and the rectangle of an anchor pointer.

    const result = myPosition.alignTo(spec); // May change if constraint changes our shape, and we have to go round again

    let {
      align,
      anchor,
      x,
      y,
      width,
      height,
      bottom,
      right
    } = result; // Which zone, T=0, R=1, B=2, L=3 the result is in

    me.lastAlignSpec.zone = result.zone; // If the alignment specified that we must constrain a dimension in order to
    // fit within our constrainTo, then obey that. If we own a Scroller, then
    // inform it that we do now need to scroll that dimension.
    // These conditions are released upon each alignment call because conditions may change.

    if (height !== myPosition.height) {
      if (!('configuredHeight' in me)) {
        me.configuredHeight = element.style.height;
      }

      me.height = height / scale;
      me.alignConstrained = me.alignConstrained | 1;

      if (scrollable) {
        scrollable.overflowY = true;
      }
    }

    if (width !== myPosition.width) {
      if (!('configuredWidth' in me)) {
        me.configuredWidth = element.style.width;
      }

      me.width = width / scale;
      me.alignConstrained = me.alignConstrained | 2;

      if (scrollable) {
        scrollable.overflowX = scrollable.clientWidth > scrollable.scrollWidth;
      }
    } // If either dimension has been constrained, we may have changed shape
    // due to text wrapping/overflowing, so we have to realign at the
    // successful align setting.

    if (align && me.alignConstrained) {
      spec.align = align;
      const newResult = Rectangle.from(element, me.positioned ? element.offsetParent : null, true).alignTo(spec);
      anchor = newResult.anchor;
      x = newResult.x;
      y = newResult.y;
      width = newResult.width;
      height = newResult.height;
      bottom = newResult.bottom;
      right = newResult.right;
    }

    me.setXY(x, y); // Class indicates which edge of the target this is aligned to: 0, 1, 2, or 3 (TRBL)

    if (isFinite(result.zone)) {
      element.classList.add(alignedClass[result.zone]);
    } // If we asked it to also calculate our anchor position, position our anchor.
    // If we're not edge-to-edge aligned with our target, we cannot anchor.

    if ((_anchor = anchor) !== null && _anchor !== void 0 && _anchor.edge) {
      const {
        edge
      } = anchor,
            {
        anchorElement
      } = me,
            elRect = Rectangle.from(element, null, true),
            colorMatchPoint = [],
            stylePointerEvents = element.style.pointerEvents; // Enable pointerEvents to make discoverable by elementFromPoint()

      element.style.pointerEvents = 'all'; // Make the anchor color match the color of the closest adjacent element

      if (edge === 'top' || edge === 'bottom') {
        colorMatchPoint[0] = elRect.x + anchor.x;
        colorMatchPoint[1] = edge === 'top' ? elRect.y + 1 : bottom - 1;
      } else {
        colorMatchPoint[0] = edge === 'left' ? elRect.x + 1 : right - 1;
        colorMatchPoint[1] = elRect.y + anchor.y;
      }

      const colourSource = DomHelper.elementFromPoint(...colorMatchPoint);
      element.style.pointerEvents = stylePointerEvents || null; // If  it's off the edge of the screen, we won't be able to read it.
      // But that's fine, the anchor will be off the edge in that case.

      if (colourSource) {
        const fillColour = DomHelper.getStyleValue(colourSource, 'background-color');

        if (fillColour.match(isTransparent)) {
          me.anchorPathElement.setAttribute('fill', me.defaultAnchorBackgroundColor);
        } else {
          me.anchorPathElement.setAttribute('fill', fillColour);
        }
      }

      anchorElement.classList.remove('b-hide-display');
      anchorElement.style.transform = '';
      anchorElement.className = `b-anchor b-anchor-${edge}`; // Anchor's position needs boosting if we are scaled down

      anchor.x && (anchor.x /= scale);
      anchor.y && (anchor.y /= scale);
      DomHelper.setTranslateXY(anchorElement, anchor.x, anchor.y);
      element.classList.add('b-anchored');
    } else if (me._anchorElement) {
      me.anchorElement.classList.add('b-hide-display');
      element.classList.remove('b-anchored');
    } // If we are to hide on scroll, we still need to know if the element we are
    // aligned to moves. If we have not been *explicitly* aligned to an element,
    // Use the element at our display position. For example, when a context menu
    // is shown on a grid header, then is the grid header is moved by a scroll
    // event, then we must hide.

    if (me.scrollAction === 'hide' && !aligningToElement) {
      // Our element is over the X, Y point now,
      // elementFromPoint must "see through" it.
      element.style.pointerEvents = 'none';
      me.anchoredTo = DomHelper.elementFromPoint(x, y);
      element.style.pointerEvents = '';
    } // If we're aligning to an element, then listen for scrolls so that we can remain aligned.
    // Scrolls can be instigated with no mousedown, so transient floating Widgets can be put
    // out of alignment by scrolls.

    if ((me.scrollAction === 'realign' && aligningToElement || me.scrollAction === 'hide') && !me.documentScrollListener) {
      // Firefox requires a longer timeout to not autohide as the result of a scroll event firing during Menu show
      me.clearTimeout(me.scrollListenerTimeout);
      me.scrollListenerTimeout = me.setTimeout(() => {
        me.documentScrollListener = true;
        document.addEventListener('scroll', me.doHideOrRealign, true);
      }, me.scrollAction === 'hide' ? me.ignoreScrollDuration : 0);
    } // If alignment specified monitorResize add a resize listener to the target so we can stay aligned.

    if (aligningToElement && spec.monitorResize && !me.targetResizeListener) {
      ResizeMonitor.addResizeListener(target, me.callRealign);
      me.targetResizeListener = true;
    } // Don't try to listen for window resizes to try realigning on Android.
    // That just means the keyboard has been shown.

    if (!BrowserHelper.isAndroid) {
      if (!me.constrainListeners && !(constrainTo instanceof Rectangle)) {
        // Always observe for changes to window size since aligned things
        // will possibly be out of place after a window resize
        me.clearTimeout(me.resizeListenerTimeout);
        me.resizeListenerTimeout = me.setTimeout(() => {
          me.constrainListeners = true;
          ResizeMonitor.addResizeListener(constrainTo || window, me.callRealign);
        }, me.ignoreScrollDuration);
      }
    }
  }

  realign(el) {
    const me = this;

    if (me.isVisible && (me.floating || me.positioned) && me.lastAlignSpec) {
      const target = me.lastAlignSpec.target; // If there was a DOM mutation which caused our target to become not layed out, or the target
      // is outside of the passed element's rectangle (passed from scroll handler), we must hide

      if (target && target.nodeType === 1) {
        // It might occur that target gets detached from DOM in IE11
        if (BrowserHelper.isIE11 && !isInDocument(target)) {
          return me.hide();
        }

        const targetWidget = Widget.fromElement(target),
              targetRect = Rectangle.from(target),
              targetIsVisible = (!targetWidget || targetWidget.isVisible) && DomHelper.isVisible(target); // We can align to an element which has a zero dimension, but the
        // intersection check requires that both dimensions are non-zero.

        targetRect.minHeight = targetRect.minWidth = 1;

        if (target !== document.body && !targetIsVisible || el && el.nodeType === 1 && el.contains(target) && !Rectangle.from(el).intersect(targetRect)) {
          return me.hide();
        }
      }

      DomHelper.addTemporaryClass(me.element, 'b-realigning', 300);
      me.alignTo(me.lastAlignSpec);
    }
  }

  releaseSizeConstraints() {
    const me = this,
          scroller = me.scrollable; // Release constraints so we can align from scratch each time.

    if (me.alignConstrained & 1) {
      me.height = me.configuredHeight;

      if (scroller) {
        scroller.overflowY = scroller.config.overflowY;
      }
    }

    if (me.alignConstrained & 2) {
      me.width = me.configuredWidth;

      if (scroller) {
        scroller.overflowX = scroller.config.overflowX;
      }
    }

    me.alignConstrained = 0;
  }
  /**
   * Only valid for {@link #config-floating} Widgets. Moves to the front of the visual stacking order.
   * @category Float & align
   */

  toFront() {
    const {
      element
    } = this,
          parent = this.floating ? Widget.floatRoot : this.positioned ? element === null || element === void 0 ? void 0 : element.parentNode : null;

    if (element && element.nextSibling) {
      const r = this._toFrontRange || (this._toFrontRange = document.createRange()); // Instead of moving this element (which may contain focus and therefore should not be moved),
      // Lasso all following nodes using a range and insert them before this.
      // If it contained focus, appending it triggers a focusOut event which will not be expected.

      r.setStartBefore(element.nextSibling);
      r.setEndAfter(parent.lastElementChild);
      parent.insertBefore(r.extractContents(), element);
    }
  } //endregion
  //region Getters/setters

  updateRef(ref) {
    this.element.dataset.ref = ref;
  }

  set clippedBy(clippedBy) {
    this._clippedBy = Array.isArray(clippedBy) ? clippedBy : clippedBy ? [clippedBy] : null;
  }

  get clippedBy() {
    const clippedBy = this._clippedBy;

    if ((clippedBy === null || clippedBy === void 0 ? void 0 : clippedBy.length) > 0) {
      let result = Rectangle.from(clippedBy === null || clippedBy === void 0 ? void 0 : clippedBy[0], null, true),
          i;

      for (i = 1; i < clippedBy.length; i++) {
        result = result.intersect(Rectangle.from(clippedBy === null || clippedBy === void 0 ? void 0 : clippedBy[i], null, true));
      }

      return result;
    }
  }
  /**
   * The child element which scrolls if any. This means the element used by the {@link #config-scrollable}.
   * @property {HTMLElement}
   * @readonly
   * @category DOM
   */

  get overflowElement() {
    return this.contentElement;
  }

  get maxHeightElement() {
    return this.element;
  }

  changeAlign(align) {
    return typeof align === 'string' ? {
      align
    } : align;
  }

  changeScrollable(scrollable, oldScrollable) {
    if (typeof scrollable === 'boolean') {
      scrollable = {
        overflowX: scrollable,
        overflowY: scrollable
      };
    }

    if (scrollable) {
      scrollable.element = this.overflowElement;
      scrollable.widget = this;

      if (!scrollable.isScroller) {
        scrollable = oldScrollable ? oldScrollable.setConfig(scrollable) : new this.scrollerClass(scrollable);
      } // Keep overflow indicator classes in sync

      scrollable.syncOverflowState();
    } // Destroy the old sceroller if the scroller is being nulled.
    else {
        oldScrollable === null || oldScrollable === void 0 ? void 0 : oldScrollable.destroy();
      }

    return scrollable;
  }
  /**
   * Get/set HTML to display. When specifying HTML, this widget's element will also have the
   * {@link #config-htmlCls} added to its classList, to allow targeted styling.
   * @property {String}
   * @category DOM
   */

  get html() {
    // Maintainer, we cannot use a ternary here, we need the this.initializingElement test to shortcut
    // to the true case to return the _html property to avoid infinite loops.
    if (this.initializingElement || !this.element) {
      return this.content || this._html;
    }

    return this.contentElement.innerHTML;
  }

  updateHtml(html) {
    const me = this,
          isClearing = html == null,
          {
      element,
      contentElement,
      htmlCls
    } = me;

    if (element) {
      const anchorEl = contentElement === element && me._anchorElement; // Flag class that we are an HTML carrying element

      if (htmlCls) {
        element.classList[isClearing ? 'remove' : 'add'](htmlCls.value);
      } // Setting innerHTML destroys the anchorElement in some browsers
      // so we must temporarily remove it to preserve it.
      // Only if the contentElement is the main element.

      if (anchorEl) {
        me.element.removeChild(anchorEl);
      }

      if (html && typeof html === 'object') {
        DomSync.sync({
          domConfig: html,
          targetElement: me.contentElement,
          onlyChildren: true
        });
      } else {
        me.contentElement.innerHTML = isClearing ? '' : html;
      }

      if (anchorEl) {
        element.appendChild(anchorEl);
      }

      if (me.floating || me.positioned) {
        // Must realign because content change might change dimensions
        if (!me.isAligning) {
          me.realign();
        }
      }
    }
  }

  updateContent(html) {
    const me = this,
          isClearing = html == null,
          {
      element,
      htmlCls
    } = me;

    if (element) {
      const {
        contentRange
      } = me; // Flag class that we are an HTML carrying element

      if (htmlCls) {
        element.classList[isClearing ? 'remove' : 'add'](htmlCls.value);
      } // Only works if we are in the DOM

      if (isInDocument(element)) {
        // Replace the contents of our content range with the new content
        contentRange.deleteContents();

        if (!isClearing) {
          contentRange.insertNode(DomHelper.createElementFromTemplate(html, {
            fragment: true
          }));
        }
      } else {
        me.contentElement.innerHTML = html;
      } // Cache in case it gets collapsed

      me.contentRangeStartOffset = contentRange.startOffset;
      me.contentRangeEndOffset = contentRange.endOffset; // Must realign because content change might change dimensions

      if ((me.floating || me.positioned) && !me.isAligning) {
        me.realign();
      }
    }
  }

  onThemeChange() {
    var _this$anchorElement;

    // If we have a *visible* anchor element, then a theme change may
    // invalidate it's size or this.defaultAnchorBackgroundColor, so a
    // run through realign (and get anchorSize) will fix that.
    if ((_this$anchorElement = this.anchorElement) !== null && _this$anchorElement !== void 0 && _this$anchorElement.offsetParent) {
      this._anchorSize = null;
      this.realign();
    }
  }
  /**
   * Returns an `[x, y]` array containing the width and height of the anchor arrow used when
   * aligning this Widget to another Widget or element.
   *
   * The height is the height of the arrow when pointing upwards, the width is the width
   * of the baseline.
   * @property {Number[]}
   * @category Float & align
   */

  get anchorSize() {
    const me = this;
    let result = this._anchorSize;

    if (!result) {
      // TODO: Move the anchoring scheme to the Panel class when we have it.
      // These values will be in the SASS and styled into the SVG through the Panel's theme.
      const borderWidth = parseFloat(DomHelper.getStyleValue(me.element, 'border-top-width')),
            borderColour = DomHelper.getStyleValue(me.element, 'border-top-color'),
            anchorElement = me.anchorElement,
            {
        className
      } = anchorElement,
            svgEl = anchorElement.firstElementChild,
            pathElement = me.anchorPathElement = svgEl.lastElementChild,
            hidden = me._hidden; // In case we are measuring after the size has been invalidated (such as via theme change)
      // and the widget is shown and aligned left or right. We must measure it in top alignment
      // so as to get the dimensions the right way round.

      anchorElement.className = 'b-anchor b-anchor-top';
      let backgroundColour = DomHelper.getStyleValue(me.contentElement, 'background-color'); // If the background colour comes through from the outer element, use that.

      if (backgroundColour.match(isTransparent)) {
        backgroundColour = DomHelper.getStyleValue(me.element, 'background-color');
      }

      me.defaultAnchorBackgroundColor = backgroundColour;
      result = anchorElement.getBoundingClientRect();
      const [width, height] = result = me._anchorSize = [result.width, result.height]; // Restore orientation

      anchorElement.className = className;
      svgEl.setAttribute('height', height + borderWidth);
      svgEl.setAttribute('width', width);
      pathElement.setAttribute('d', `M0,${height}L${width / 2},0.5L${width},${height}`);

      if (borderWidth) {
        pathElement.setAttribute('stroke-width', borderWidth);
        pathElement.setAttribute('stroke', borderColour);
      }

      result[1] -= borderWidth;

      if (hidden) {
        me.element.classList.add('b-hidden');
      }

      if (!me.themeChangeListener) {
        me.themeChangeListener = GlobalEvents.on({
          theme: 'onThemeChange',
          thisObj: me
        });
      } // Reset to default in case it has been positioned by a coloured header

      me.anchorPathElement.setAttribute('fill', me.defaultAnchorBackgroundColor);
    }

    return result;
  }

  get anchorElement() {
    let result = this._anchorElement;

    if (!result) {
      // Using the filter hides the SVG element when positioned inside a Widget.
      const useFilter = this.floating && !(BrowserHelper.isIE11 || BrowserHelper.isEdge);
      result = this._anchorElement = DomHelper.createElement({
        parent: this.element,
        className: 'b-anchor b-anchor-top',
        children: [{
          tag: 'svg',
          ns: 'http://www.w3.org/2000/svg',
          version: '1.1',
          class: 'b-pointer-el',
          children: [useFilter ? {
            tag: 'defs',
            children: [{
              tag: 'filter',
              id: 'shadow-filter',
              children: [{
                tag: 'feDropShadow',
                dx: 0,
                dy: -1,
                stdDeviation: 2,
                'flood-opacity': 0.05
              }]
            }]
          } : null, {
            tag: 'path',
            [useFilter ? 'filter' : '']: 'url(#shadow-filter)'
          }]
        }]
      });
    }

    return result;
  }

  updateAnchor(anchor) {
    if (this._anchorElement) {
      this._anchorElement.classList[anchor ? 'remove' : 'add']('b-hide-display');
    }
  }

  updateDraggable(draggable) {
    const me = this,
          {
      element
    } = me;

    if (!BrowserHelper.isFirefox) {
      element.setAttribute('draggable', Boolean(draggable));
    }

    if (draggable) {
      me.dragEventDetacher = EventHelper.addListener({
        element,
        dragstart: 'onWidgetDragStart',
        dragend: 'onWidgetDragEnd',
        thisObj: me
      }); // Fix for FF draggable bug https://bugzilla.mozilla.org/show_bug.cgi?id=1189486

      if (BrowserHelper.isFirefox) {
        me.ffDragDetacher = EventHelper.on({
          element,

          mousedown(e) {
            if (!DomHelper.up(e.target, '.b-field-inner')) {
              element.setAttribute('draggable', 'true');
            }
          },

          // Only needed for automatic listener removal on the destroy of the thisObj
          thisObj: me
        });
      }
    } else {
      var _me$dragEventDetacher2, _me$dragOverEventDeta2, _me$ffDragDetacher;

      (_me$dragEventDetacher2 = me.dragEventDetacher) === null || _me$dragEventDetacher2 === void 0 ? void 0 : _me$dragEventDetacher2.call(me);
      (_me$dragOverEventDeta2 = me.dragOverEventDetacher) === null || _me$dragOverEventDeta2 === void 0 ? void 0 : _me$dragOverEventDeta2.call(me);
      (_me$ffDragDetacher = me.ffDragDetacher) === null || _me$ffDragDetacher === void 0 ? void 0 : _me$ffDragDetacher.call(me);
    }
  }

  onWidgetDragStart(e) {
    const me = this,
          {
      element
    } = me;

    if (!me.validateDragStartEvent(e)) {
      return;
    } // Opt out of auto-alignment on scroll or DOM mutation.

    me.removeTransientListeners();
    const dragStartX = e.clientX,
          dragStartY = e.clientY,
          scrollingPageElement = document.scrollingElement || document.body,
          widgetX = me.getXY()[0],
          widgetY = me.getXY()[1],
          dragListeners = {
      element: document.body,
      // Position using direct DOM access, do not go though the setXY method which clears
      // any centered config. User dragging only moves this show of the widget. Upon next
      // neutral show (with no extra positioning info), a centered widget will show centered again.
      dragover: event => {
        // Centered adds positioning rules, it can't be centered during drag.
        element.classList.remove('b-centered');
        DomHelper.setTranslateXY(element, widgetX + event.clientX - dragStartX - scrollingPageElement.scrollLeft, widgetY + event.clientY - dragStartY - scrollingPageElement.scrollTop);
      }
    }; // Stop viewport panning on drag on touch devices

    if (BrowserHelper.isTouchDevice) {
      dragListeners.touchmove = e => e.preventDefault();
    }

    document.body.appendChild(me.dragGhost);
    me.setDragImage(e); // Prevent special cursor from being shown

    e.dataTransfer.effectAllowed = 'none';
    me.dragOverEventDetacher = EventHelper.addListener(dragListeners); // Various app events (Such as resize or visible child count change) can
    // cause a request to realign, so opt out of anchoring and alignedness
    // until we are next hidden.

    me.alignTo();
  }
  /**
   * Validates a `dragstart` event with respect to the target element. Dragging is not normally
   * initiated when the target is interactive such as an input field or its label, or a button.
   * This may be overridden to provide custom drag start validation.
   * @param {DragEvent} e The `dragstart` event to validate.
   * @returns {Boolean} Return `true` if the drag is to be allowed.
   * @internal
   */

  validateDragStartEvent(e) {
    const me = this,
          {
      element
    } = me,
          actualTarget = DomHelper.elementFromPoint(e.clientX, e.clientY),
          // Can't be resolved from the event :/
    {
      handleSelector
    } = me.draggable;

    if (handleSelector) {
      const blacklist = negationPseudo.exec(handleSelector)[1]; // Extract the content of :not()
      // If the selector was :not(), then if we are a descendant of a matching element, it's a no-drag

      if (blacklist) {
        if (actualTarget.closest(`#${element.id} ${blacklist}`)) {
          e.preventDefault();
          return false;
        }
      } // If we are not the descendant of a matching element, it's a no-drag
      else if (!actualTarget.closest(`#${element.id} ${handleSelector}`)) {
          e.preventDefault();
          return false;
        }
    }

    return true;
  }

  setDragImage(e) {
    if (e.dataTransfer.setDragImage) {
      // Firefox requires this to be called before setDragImage
      e.dataTransfer.setData('application/node type', ''); // Override the default HTML5 drag ghost and just drag an empty node.
      // The large offset will cause it to be displayed offscreen on platforms
      // which will not hide drag images (iOS)

      e.dataTransfer.setDragImage(this.dragGhost, -9999, -9999);
    }
  }

  onWidgetDragEnd() {
    this.dragGhost.remove();
    this.dragOverEventDetacher();

    if (BrowserHelper.isFirefox) {
      this.element.removeAttribute('draggable');
    }
  }

  changeFloating(value) {
    // Coerce all to boolean so that we have a true/false value
    return Boolean(value);
  }

  changePositioned(value) {
    // Coerce all to boolean so that we have a true/false value
    return Boolean(value);
  }

  updatePositioned(positioned) {
    this.element.classList[positioned ? 'add' : 'remove']('b-positioned');
  }

  getXY() {
    return [DomHelper.getPageX(this.element), DomHelper.getPageY(this.element)];
  }
  /**
   * Moves this Widget to the x,y position. Both arguments can be omitted to just set one value.
   *
   * *For {@link #config-floating} Widgets, this is a position in the browser viewport.*
   * *For {@link #config-positioned} Widgets, this is a position in the element it was rendered into.*
   *
   * @param {Number} [x]
   * @param {Number} [y]
   * @category Float & align
   */

  setXY(x, y) {
    const me = this;

    if (me.floating || me.positioned) {
      if (x != null) {
        me._x = x;
        me.centered = false;
      }

      if (y != null) {
        me._y = y;
        me.centered = false;
      }

      DomHelper.setTranslateXY(me.element, me._x || 0, me._y || 0);
    }
  }
  /**
   * Moves this Widget to the desired x position. *Only valid for {@link #config-floating} Widgets.
   * @property {Number} x
   * @type {Number}
   * @category Float & align
   */

  get x() {
    return this.getXY()[0];
  }

  changeX(x) {
    this.setXY(x);
  }
  /**
   * Moves this Widget to the desired y position. *Only valid for {@link #config-floating} Widgets.
   * @property {Number} y
   * @type {Number}
   * @category Float & align
   */

  get y() {
    return this.getXY()[1];
  }

  changeY(y) {
    this.setXY(null, y);
  }
  /**
   * Get elements offsetWidth or sets its style.width, or specified width if element not created yet.
   * @property {Number|String}
   * @category Layout
   */

  get width() {
    const me = this,
          element = me.element;

    if (me.monitorResize) {
      // If the width is invalid, read it now.
      if (me._width == null) {
        me._width = element.offsetWidth;
      } // Usually this will be set in onInternalResize

      return me._width;
    } // No monitoring, we have to measure;

    return element.offsetWidth;
  }

  changeWidth(width) {
    DomHelper.setLength(this.element, 'width', width); // Invalidate the width, so it will be read from the DOM if a read is requested before the resize event

    this._width = null; // Setting width explicitly should reset flex, since it's not flexed anymore

    this._flex = null;
    this.element.style.flex = '';
  }
  /**
   * Get/set elements maxWidth. Getter returns max-width from elements style, which is always a string. Setter accepts
   * either a String or a Number (which will have 'px' appended). Note that like {@link #config-width},
   * _reading_ the value will return the numeric value in pixels.
   * @property {String|Number}
   * @category Layout
   */

  get maxWidth() {
    return DomHelper.measureSize(this.element.style.maxWidth, this.element);
  }

  updateMaxWidth(maxWidth) {
    DomHelper.setLength(this.element, 'maxWidth', maxWidth);
  }
  /**
   * Get/set elements minWidth. Getter returns min-width from elements style, which is always a string. Setter accepts
   * either a String or a Number (which will have 'px' appended). Note that like {@link #config-width},
   * _reading_ the value will return the numeric value in pixels.
   * @property {String|Number}
   * @category Layout
   */

  get minWidth() {
    return DomHelper.measureSize(this.element.style.minWidth, this.element);
  }

  updateMinWidth(minWidth) {
    DomHelper.setLength(this.element, 'minWidth', minWidth);
  }

  updateFlex(flex) {
    // Default grow to the same as flex and basis to 0.
    if (typeof flex === 'number' || !isNaN(flex)) {
      flex = `${flex} ${flex}`;
    }

    this.element.style.flex = flex;
    this.element.style.width = BrowserHelper.isIE11 ? '' : null;
  }

  updateAlignSelf(alignSelf) {
    this.element.style.alignSelf = alignSelf;
  }

  updateMargin(margin) {
    // Convert eg 1 to "1px 1px 1px 1px" or "0 8px" to "0px 8px 0px 8px"
    this.element.style.margin = this.parseTRBL(margin).join(' ');
  }

  updateTextAlign(align, oldAlign) {
    oldAlign && this.element.classList.remove(`b-text-align-${oldAlign}`);
    this.element.classList.add(`b-text-align-${align}`);
  }
  /**
   * Get element's offsetHeight or sets its style.height, or specified height if element no created yet.
   * @property {Number|String}
   * @category Layout
   */

  get height() {
    const me = this,
          element = me.element;

    if (me.monitorResize) {
      // If the height is invalid, read it now.
      if (me._height == null) {
        me._height = element.offsetHeight;
      } // Usually this will be set in onInternalResize

      return me._height;
    } // No monitoring, we have to measure;

    return element.offsetHeight;
  }

  changeHeight(height) {
    DomHelper.setLength(this.element, 'height', height); // Invalidate the height, so it will be read from the DOM if a read is requested before the resize event

    this._height = null;
  }
  /**
   * Get/set element's maxHeight. Getter returns max-height from elements style, which is always a string. Setter
   * accepts either a String or a Number (which will have 'px' appended). Note that like {@link #config-height},
   * _reading_ the value will return the numeric value in pixels.
   * @property {String|Number}
   * @category Layout
   */

  get maxHeight() {
    return DomHelper.measureSize(this.maxHeightElement.style.maxHeight, this.element);
  }

  updateMaxHeight(maxHeight) {
    const me = this,
          {
      element
    } = me;
    DomHelper.setLength(me.maxHeightElement, 'maxHeight', maxHeight); // Flexbox does not handle overflow correctly when a vertically
    // flexed item is constrained by the maxHeight of its containing el.
    // So we have to propagate the maxHeight from the encapsulating element
    // onto the contentElement.

    if (BrowserHelper.isIE11 && me.contentElement !== element) {
      if (maxHeight == null) {
        ResizeMonitor.removeResizeListener(element, me.boundMaxHeightBugHandler);
      } else {
        ResizeMonitor.addResizeListener(element, me.boundMaxHeightBugHandler || (me.boundMaxHeightBugHandler = me.fixIEMaxHeightBug.bind(me)));

        if (document.body.contains(element)) {
          me.fixIEMaxHeightBug();
        }
      }
    }
  }

  fixIEMaxHeightBug() {
    const {
      element,
      contentElement
    } = this; // If this widget is flexed vertically, the maxHeight of the encapsulating element must be
    // propapagated onto the contentElement.

    if (DomHelper.getStyleValue(element, 'flex-direction') === 'column') {
      // Release previous height constraint
      contentElement.style.maxHeight = '';
      let availableHeight = Rectangle.client(element).height;
      const siblings = element.childNodes,
            len = siblings.length; // In case there are height-consuming siblings of the contentElement.
      // For example a header/footer. The heights of these must be subtracted.

      for (let i = 0; i < len; i++) {
        const sibling = siblings[i];

        if (sibling !== contentElement && !sibling.classList.contains('b-resize-monitors')) {
          availableHeight -= sibling.offsetHeight;
        }
      } // Apply the adjusted height constraint to the contentElement

      this.contentElement.style.maxHeight = `${availableHeight}px`;
    }
  }
  /**
   * Get/set element's minHeight. Getter returns min-height from elements style, which is always a string. Setter
   * accepts either a String or a Number (which will have 'px' appended). Note that like {@link #config-height},
   * _reading_ the value will return the numeric value in pixels.
   * @property {String|Number}
   * @category Layout
   */

  get minHeight() {
    return DomHelper.measureSize(this.element.style.minHeight, this.element);
  }

  updateMinHeight(minHeight) {
    DomHelper.setLength(this.element, 'minHeight', minHeight);
  }

  updateDisabled(disabled) {
    const me = this,
          el = me.element,
          focusEl = me.focusElement;
    me.trigger('beforeUpdateDisabled', {
      disabled
    });
    disabled && me.revertFocus();

    if (el) {
      el.classList[disabled ? 'add' : 'remove']('b-disabled');

      if (focusEl) {
        focusEl.disabled = disabled;
      }
    }

    me.onDisabled(disabled);
  }
  /**
   * Called when disabled state is changed.
   * Override in subclass that needs special handling when being disabled.
   * @param {Boolean} disabled current state
   * @private
   */

  onDisabled(disabled) {}
  /**
   * Disable the widget
   */

  disable() {
    this.disabled = true;
  }
  /**
   * Enable the widget
   */

  enable() {
    this.disabled = false;
  }
  /**
   * Get/set a tooltip on the widget. Accepts a string or tooltip config (specify true (or 'true') to use placeholder
   * as tooltip). When using a string it will configure the tooltip with `textContent: true` which enforces a default
   * max width.
   *
   * By default, this uses a singleton Tooltip instance which may be accessed from the
   * `{@link Core.widget.Widget Widget}` class under the name `Widget.tooltip`.
   * This is configured according to the config object on pointer over.
   *
   * To request a separate instance be created just for this widget, add `newInstance : true`
   * to the configuration.
   *
   * @property {String|Object}
   * @category Misc
   */

  get tooltip() {
    if (this._tooltip) {
      return this._tooltip;
    } else {
      const {
        tooltip
      } = Widget; // If the shared tooltip is currently in use by us, return it.
      // If it's not in use by us, we don't have a tooltip.

      if (tooltip && tooltip.activeTarget === this._element && tooltip.isVisible) {
        return tooltip;
      }
    }
  } //noinspection JSAnnotator

  changeTooltip(tooltip, oldTooltip) {
    const me = this;

    if (tooltip) {
      if (!(me.preventTooltipOnTouch && BrowserHelper.isTouchDevice)) {
        if (tooltip.constructor.name !== 'Object') {
          tooltip = {
            html: typeof tooltip === 'string' ? tooltip : me.placeholder,
            textContent: true
          };
        } // We have to explicitly request a new instance to avoid spam Tooltip instances.
        // If there is an incoming oldTooptip, then we own a newInstance.

        if (oldTooltip || tooltip.newInstance) {
          tooltip.type = 'tooltip';
          if (!tooltip.forElement) tooltip.forElement = me.element;
          if (!('showOnHover' in tooltip) && !tooltip.forSelector) tooltip.showOnHover = true;
          if (!('autoClose' in tooltip)) tooltip.autoClose = true;
          tooltip = Widget.reconfigure(oldTooltip, tooltip, me);
        } // The default is that tooltip content and configs from tipConfig
        else {
            me.element.dataset.btip = true;
            me.tipConfig = tooltip; // We do not set our property if we are sharing the singleton

            return;
          }
      }
    } else {
      // If there is an incoming oldTooptip, then we own a newInstance.
      // Only destroy it if it's being set to null. Empty string
      // just means clear its content.
      if (oldTooltip) {
        if (tooltip == null) {
          oldTooltip.destroy();
        } else {
          // We do not update the property if we are just clearing its content
          oldTooltip.html = null;
          return;
        }
      } // We are sharing, so just clear the btip
      else {
          delete me.element.dataset.btip;
        }
    }

    return tooltip;
  }
  /**
   * Determines visibility by checking if the Widget is hidden, or any ancestor is hidden and that it has an
   * element which is visible in the DOM
   * @property {Boolean}
   * @category Visibility
   * @readonly
   */

  get isVisible() {
    const me = this,
          {
      element
    } = me; // If we are hidden, or destroying, or any ancestors are hidden, we're not visible

    return Boolean(element && !me._hidden && !me.isDestroying && isInDocument(element) && (!me.requireSize || hasLayout(element)) && (!me.owner || me.owner.isVisible));
  }
  /**
   * Focuses this widget if it has a focusable element.
   */

  focus() {
    if (this.isFocusable) {
      DomHelper.focusWithoutScrolling(this.focusElement);
    }
  }
  /**
   * Get this widget's primary focus holding element if this widget is itself focusable, or contains focusable widgets.
   * @property {HTMLElement}
   * @readonly
   * @category DOM
   */

  get focusElement() {// Override in widgets which are focusable.
  }

  get isFocusable() {
    const focusElement = this.focusElement; // We are only focusable if the focusEl is deeply visible, that means
    // it must have layout - an offsetParent. Body does not have offsetParent.

    return focusElement && this.isVisible && !this.disabled && (focusElement === document.body || focusElement.offsetParent);
  }
  /**
   * Shows this widget
   * @category Visibility
   * @async
   * @returns {Promise} A promise which is resolved when the widget is shown
   */

  async show() {
    return new Promise(resolve => {
      const me = this,
            {
        showAnimation,
        element
      } = me,
            {
        floatRoot
      } = Widget,
            {
        style
      } = element;
      let styleProp, animProps;
      /**
       * Triggered before a widget is shown. Return `false` to prevent the action.
       * @preventable
       * @event beforeShow
       * @param {Core.widget.Widget} source The widget being shown.
       */

      if (!me.isVisible && me.trigger('beforeShow') !== false && (!me.internalBeforeShow || me.internalBeforeShow() !== false)) {
        // Cancel any current hide/show animation
        me.cancelHideShowAnimation(); // Centered config value takes precedence over x and y configs.
        // This also ensures that widgets configured with centered: true
        // and draggable : true will show in the center on next show after
        // being dragged by the user which is the intuitive UX.

        me.updateCentered(me._centered);

        if (me.floating) {
          if (!floatRoot.contains(element)) {
            // Replace this Widget's DOM into the container if it's already rendered
            if (me.rendered) {
              floatRoot.appendChild(me.element);
            } else {
              // Pass triggerPaint as false. The calls will not propagate
              // anyway since we are still hidden.
              me.render(floatRoot, false);
            }
          } // Because we are outside of any owner's element, we need to see if they're scaled so
          // that we match. See scaled examples with tooltips in API docs guides section.

          if (style.transform.includes('scale')) {
            me.scale = null;
            style.transform = style.transformOrigin = '';
          }

          const scaledAncestor = me.closest(isScaled);

          if (scaledAncestor) {
            const {
              scale
            } = scaledAncestor; // Our scale is the same while we are visible and owned by that scaled ancestor.
            // Now floating descendants will follow suit.

            me.scale = scale;
            style.transform = `scale(${scale})`;
            style.transformOrigin = `0 0`;
          }
        }

        me._hidden = false;
        element.classList.remove('b-hidden');

        if (showAnimation) {
          styleProp = Object.keys(showAnimation)[0];
          animProps = showAnimation[styleProp];
          me.currentAnimation = {
            styleProp,
            resolve
          };
          me.isAnimating = true; // afterHideShowAnimate will always be called even if the transition aborts

          me.currentAnimation.detacher = EventHelper.onTransitionEnd({
            element,
            property: styleProp,
            duration: parseDuration(animProps.duration) + 20,
            handler: 'afterHideShowAnimate',
            thisObj: me
          }); // Setting transition style initial value before showing,
          // then reading the style to ensure transition will animate

          style[styleProp] = animProps.from;
          DomHelper.getStyleValue(element, styleProp);
          style.transition = `${styleProp} ${animProps.duration} ease ${animProps.delay}`;
          style[styleProp] = animProps.to;
        }

        me.afterShow(!showAnimation && resolve); // Note: popups can be dismissed in the process of focusin/out if an editor has invalidAction='block',
        // so we may be hidden at this point...
        // If we're not being called from showBy, do default aligning unless its centered

        if (!me.inShowBy && me.floating && me.forElement && !me.centered && !me.hidden) {
          me.alignTo(me.forElement);
        }
      } else {
        resolve();
      }
    });
  }
  /**
   * Show aligned to another target element or {@link Core.widget.Widget} or {@link Core.helper.util.Rectangle}
   * @param {Object|HTMLElement} spec Alignment specification, or the element to align to using the configured
   * {@link #config-align}.
   * @param {HTMLElement|Core.widget.Widget|Core.helper.util.Rectangle} spec.target The Widget or Element or Rectangle to align to.
   * @param {Boolean} [spec.anchor] True to show a pointer arrow connecting to the target. Defaults to false.
   * @param {Boolean} [spec.overlap] True to allow this to overlap the target.
   * @param {String} spec.align The alignment specification string, `[trbl]n-[trbl]n`. Defaults to this instance's
   * {@link #config-align} setting.
   * @param {HTMLElement|Core.widget.Widget|Core.helper.util.Rectangle} [spec.constrainTo] The Widget or Element or Rectangle to constrain to.
   * If the requested alignment cannot be constrained (it will first shrink the resulting Rectangle according
   * to the `minWidth` and `minHeight` properties of this spec, or the Widget), then it will try aligning at other edges
   * (honouring the `axisLock` option), and pick the fallback alignment which results in the shortest translation.
   * @param {Number} [spec.minHeight] The minimum height this widget may be compressed to when constraining within the `constrainTo` option.
   * @param {Number} [spec.minWidth] The minimum width this widget may be compressed to when constraining within the `constrainTo` option.
   * @param {Boolean} [spec.axisLock] Specify as `true` to fall back to aligning against the opposite
   * edge if the requested alignment cannot be constrained into the `constrainTo` option. Specify as
   * `'flexible'` to allow continuation to try the other edges if a solution cannot be found on the originally requested axis.
   * @param {Boolean} [spec.matchSize] __Only honored when `axisLock` is enabled and alignment succeeds on the requested axis.__
   * Specify as `true` to have this widget's size along the aligned edge match the size of the target's edge.
   * For example, a combobox's dropdown should match the width of the combobox.
   * @param {Number|Number[]} [spec.offset] The offset to create an extra margin round the target
   * to offset the aligned widget further from the target. May be configured as -ve to move the aligned widget
   * towards the target - for example producing the effect of the anchor pointer piercing the target.
   * @param {Boolean} [spec.monitorResize] Configure as `true` to monitor the element being aligned to for
   * resizing while visible to correct alignment.
   * @category Float & align
   * @async
   * @returns {Promise} A promise which is resolved when the widget is shown
   */

  async showBy(spec) {
    const me = this;
    let result;

    if (me.isVisible) {
      DomHelper.addTemporaryClass(me.element, 'b-realigning', 300);
    } else {
      // Prevent show from rerouting here.
      me.inShowBy = true;
      result = me.show();
      me.inShowBy = false;
    }

    if (me.isVisible) {
      me.alignTo(spec);
    }

    return result || immediatePromise$3;
  }
  /**
   * Show this widget anchored to a coordinate
   * @param {Number|Number[]} x The x position (or an array with [x,y] values) to show by
   * @param {Number} [y] The y position to show by
   * @param {Object} [options] See {@link #function-showBy} for reference
   * @category Float & align
   * @async
   * @returns {Promise} A promise which is resolved when the widget is shown
   */

  async showByPoint(x, y, options) {
    const xy = arguments.length === 1 ? x : [x, y];
    return this.showBy(Object.assign({
      position: new Point(xy[0] + 1, xy[1] + 1),
      // Override any matchSize that we might have in our align config.
      // Otherwise we are going to be 1px wide/high
      matchSize: false,
      align: 't-b'
    }, options));
  }

  afterShow(resolveFn) {
    var _me$parent, _me$parent$onChildSho;

    const me = this; // This method is injected when maxHeight is set on IE11.
    // Flexbox does not handle overflow correctly when a vertically
    // flexed item is constrained by the maxHeight of its containing el.
    // So we have to propagate the maxHeight from the encapsulating element
    // onto the contentElement.

    if (me.boundMaxHeightBugHandler) {
      me.boundMaxHeightBugHandler();
    }

    me.trigger('show'); // Cache our preferred anchoredness in case it's overridden by a drag.

    me._configuredAnchorState = me.anchor; // Keep any owning container informed about visibility state.
    // It may not be a Container. SubGrid class is still a Widget
    // which contains grid headers.

    (_me$parent = me.parent) === null || _me$parent === void 0 ? void 0 : (_me$parent$onChildSho = _me$parent.onChildShow) === null || _me$parent$onChildSho === void 0 ? void 0 : _me$parent$onChildSho.call(_me$parent);
    me.triggerPaint();
    resolveFn && resolveFn();
  }

  triggerPaint() {
    const me = this,
          {
      element
    } = me,
          firstPaint = !me.isPainted;

    if (me.isVisible) {
      if (firstPaint) {
        // Not for public use, only used in docs
        if (me.scaleToFitWidth && !me.monitorResize) {
          me.onParentElementResize = me.onParentElementResize.bind(me);
          ResizeMonitor.addResizeListener(element.parentElement, me.onParentElementResize);
          me.updateScale();
        }
      } // Trigger paint only on immediate children.
      // Each one will call this recursively.
      // paint is triggered in a bottom up manner.

      me.eachWidget(widgetTriggerPaint, false);
      /**
       * Triggered when a widget which had been in a non-visible state for any reason
       * achieves visibility.
       *
       * A non-visible state *might* mean the widget is hidden and has just been shown.
       *
       * But this event will also fire on widgets when a non-visible (unrendered, or hidden)
       * ancestor achieves visibility, for example a {@link Core.widget.Popup Popup} being shown.
       * @event paint
       * @param {Core.widget.Widget} source The widget being painted.
       * @param {Boolean} firstPaint `true` if this is the first paint.
       */

      me.trigger('paint', {
        firstPaint
      });

      if (firstPaint) {
        // On first paint, we should announce our size immediately.
        // When the real event comes along, onElementResize will reject it because the size will be the same.
        if (me.monitorResize && !me.scaleToFitWidth) {
          ResizeMonitor.onElementResize([{
            target: element
          }]);
        }
      }

      me.isPainted = true;
    }
  }

  cancelHideShowAnimation() {
    const {
      currentAnimation,
      element
    } = this,
          {
      style
    } = element;

    if (currentAnimation) {
      this.isAnimating = false;
      element.classList.remove('b-hiding');
      currentAnimation.detacher();
      style.transition = style[currentAnimation.styleProp] = '';
      this.currentAnimation = null;
    }
  }

  afterHideShowAnimate() {
    // If menu is destroyed too soon in Edge, this method will be invoked for destroyed element
    if (this.isDestroyed || !this.currentAnimation) {
      return;
    }

    const me = this,
          {
      element
    } = me,
          {
      style
    } = element,
          {
      styleProp,
      resolve
    } = me.currentAnimation;
    element.classList.remove('b-hiding');
    me.isAnimating = false;
    me.currentAnimation = null;
    style.transition = style[styleProp] = ''; // Element must be fully hidden after the animation effect finishes

    if (me._hidden) {
      me.afterHideAnimation();
    }

    resolve();
  }
  /**
   * Hide widget
   * @param {Boolean} animate Pass `true` (default) to animate the hide action
   * @category Visibility
   * @returns {Promise} A promise which is resolved when the widget has been hidden
   */

  hide(animate = true) {
    return new Promise(resolve => {
      const me = this,
            {
        element
      } = me,
            {
        style
      } = element,
            hideAnimation = animate && me.hideAnimation; // If we get hidden very quickly after a call to show,
      // we must kill the timers which add the realign listeners.

      me.clearTimeout(me.scrollListenerTimeout);
      me.clearTimeout(me.resizeListenerTimeout); // replaced check for isVisible with _hidden, need to hide a component not yet in view in EventEditor

      if (!me._hidden && me.trigger('beforeHide') !== false) {
        me._hidden = true;

        if (!element) {
          resolve();
          return;
        }

        me.revertFocus(); // Safari and FireFox do not fire focusOut when an element hides, so in case the focus
        // reversion did not find a target, we will not get the call to onFocusOut
        // below to perform the forced focus reversion, so we must do it before hide.

        if ((BrowserHelper.isSafari || BrowserHelper.isFirefox) && element.contains(document.activeElement)) {
          me.revertFocus(true);
        } // Focus exit causes close if autoClose: true, and if closeAction: 'hide'
        // that might destroy us, so exit now if that happens.

        if (me.isDestroyed) {
          resolve();
          return;
        } // Cancel any current hide/show animation

        me.cancelHideShowAnimation();

        if (hideAnimation) {
          const styleProp = Object.keys(hideAnimation)[0],
                animProps = hideAnimation[styleProp]; // Make sure we are not already at the final value of the hide animation (i.e. calling hide() directly after show())

          if (Number(getComputedStyle(me.element)[styleProp]) !== animProps.to) {
            me.currentAnimation = {
              styleProp,
              resolve
            }; // Element must behave as though it were not there during
            // the animated hide. This means pointer-events:none

            element.classList.add('b-hiding');
            me.isAnimating = true; // afterHideShowAnimate will always be called even if the transition aborts

            me.currentAnimation.detacher = EventHelper.onTransitionEnd({
              element,
              property: styleProp,
              duration: parseDuration(animProps.duration) + 20,
              handler: 'afterHideShowAnimate',
              thisObj: me
            }); // Setting transition style initial value before showing,
            // then reading the style to ensure transition will animate

            style[styleProp] = animProps.from;
            DomHelper.getStyleValue(element, styleProp);
            style.transition = `${styleProp} ${animProps.duration} ease ${animProps.delay}`;
            style[styleProp] = animProps.to;
          } else {
            element.classList.add('b-hidden');
          }
        } else {
          element.classList.add('b-hidden');
        } // only supply resolve function if not using animation

        me.afterHide(!hideAnimation && resolve, hideAnimation);
      }
    });
  }

  doHideOrRealign(event) {
    var _event$target;

    const me = this,
          {
      anchoredTo,
      lastAlignSpec
    } = me,
          target = lastAlignSpec === null || lastAlignSpec === void 0 ? void 0 : lastAlignSpec.target,
          position = lastAlignSpec === null || lastAlignSpec === void 0 ? void 0 : lastAlignSpec.position,
          activeEl = DomHelper.activeElement,
          [x, y] = me.getXY(); // Scroll is inside this widget, no action

    if (me.element.contains(event.target)) {
      return;
    } // If it's a synthesized scroll event (such as from our ResizeMonitor polyfill), ignore it
    // If we're scrolling because a focused textual input field which we contain is being shifted into view,
    // we must not reposition - we'll just move with the document content.
    // event.target might be missing with LockerService enabled

    if (!event.isTrusted || ((_event$target = event.target) === null || _event$target === void 0 ? void 0 : _event$target.nodeType) === 9 && me.element.contains(activeEl) && textInputTypes[activeEl] && window.innerHeight < document.body.offsetHeight) {
      return;
    } // Perform the realignment. If the scroll did not in fact cause us to become unaligned, then we don't have to hide.

    me.realign(event.target); // Might destroy on hide in realign, so check for isDestroyed.

    if (!me.isDestroyed && me.scrollAction === 'hide') {
      const [newX, newY] = me.getXY(),
            moved = newX !== x || newY !== y; // If the scroll caused our position to become invalid, and we either don't know what element
      // we're anchored to (or not anchored to one at all), or the element we're anchored to has been
      // affected by the scroll, we must hide.
      // target might be missing with LockerService enabled

      if ((moved || (target === null || target === void 0 ? void 0 : target.$$name) === 'Point' || position) && (!anchoredTo || event.target && DomHelper.isDescendant(event.target, anchoredTo))) {
        me.hide();
      }
    }
  }

  afterHide(resolveFn = null, hideAnimation = this.hideAnimation) {
    var _me$parent2, _me$parent2$onChildHi;

    const me = this,
          {
      lastAlignSpec
    } = me; // The flag must be cleared on a normal hide.
    // It's set after the hide() call in alignTo
    // if the target is outside our clippedTo region.

    if (lastAlignSpec) {
      lastAlignSpec.targetHidden = null;
    } // If a drag caused us to lose our anchor, restore it upon hide.

    me._anchor = me._configuredAnchorState; // Remove listeners which are only added during the visible phase.
    // In its own method because it's called on hide and destroy.

    me.removeTransientListeners(); // Postprocessing to be done after the hideAnimation finishes.
    // If there's no animation, we call it immediately.
    // We set the element to be hidden here, after any animation completes.
    // We also remove floating Widgets from the DOM when they are hidden.

    if (!hideAnimation) {
      me.afterHideAnimation();
    }

    me.trigger('hide'); // Keep any owning container informed about visibility state.
    // It may not be a Container. SubGrid class is still a Widget
    // which contains grid headers.

    (_me$parent2 = me.parent) === null || _me$parent2 === void 0 ? void 0 : (_me$parent2$onChildHi = _me$parent2.onChildHide) === null || _me$parent2$onChildHi === void 0 ? void 0 : _me$parent2$onChildHi.call(_me$parent2);
    resolveFn && resolveFn();
  }

  removeTransientListeners() {
    const me = this;
    me.clearTimeout(me.resizeListenerTimeout);
    me.clearTimeout(me.scrollListenerTimeout);

    if (me.documentScrollListener) {
      document.removeEventListener('scroll', me.doHideOrRealign, true);
      me.documentScrollListener = false;
    }

    if (me.targetResizeListener) {
      ResizeMonitor.removeResizeListener(me.lastAlignSpec.target, me.callRealign);
      me.targetResizeListener = false;
    }

    if (me.constrainListeners) {
      ResizeMonitor.removeResizeListener(me.lastAlignSpec.constrainTo || window, me.callRealign);
      me.constrainMutationMonitor && me.constrainMutationMonitor.disconnect();
      me.constrainListeners = false;
    }
  }

  afterHideAnimation() {
    const me = this,
          {
      element
    } = me;

    if (me.floating && Widget.floatRoot.contains(element)) {
      element.remove();
    } else {
      element.classList.add('b-hidden');
    } // Reset anchor to its default colour after hide

    if (me.defaultAnchorBackgroundColor) {
      // Reset to default in case it has been positioned by a coloured header
      me.anchorPathElement.setAttribute('fill', me.defaultAnchorBackgroundColor);
    }
  }

  changeHidden(value) {
    const me = this;
    let ret;

    if (me.isConfiguring) {
      ret = Boolean(value);
      me.element.classList[value ? 'add' : 'remove']('b-hidden');
    } else {
      // These methods are async but set _hidden when they get past the before event, so don't set ret and
      // the setter won't set _hidden automatically.
      me.trigger('beforeChangeHidden', {
        hidden: value
      });

      if (value) {
        me.hide();
      } else {
        me.show();
      }
    }

    return ret;
  }
  /**
   * Get id assigned by user (not generated id)
   * @returns {String}
   * @readonly
   * @private
   * @category Misc
   */

  get assignedId() {
    return this.hasGeneratedId ? null : this.id;
  }
  /**
   * Get the owning Widget of this Widget. If this Widget is directly contained, then the containing
   * Widget is returned. If this Widget is floating, the configured `owner` property is returned.
   * If there is a `forElement`, that element's encapsulating Widget is returned.
   * @property {Core.widget.Widget}
   * @readonly
   * @category Misc
   */

  get owner() {
    var _this$forElement, _this$element;

    const owningEl = ((_this$forElement = this.forElement) === null || _this$forElement === void 0 ? void 0 : _this$forElement.nodeType) === 1 ? this.forElement : (_this$element = this.element) === null || _this$element === void 0 ? void 0 : _this$element.parentNode;
    return this.parent || this._owner || (owningEl === null || owningEl === void 0 ? void 0 : owningEl.closest('.b-widget')) && Widget.fromElement(owningEl);
  }
  /**
   * Get this Widget's previous sibling in the parent {@link Core.widget.Container Container}, or, if not
   * in a Container, the previous sibling widget in the same _parentElement_.
   * @property {Core.widget.Widget}
   * @readonly
   * @category Misc
   */

  get previousSibling() {
    return this.getSibling(-1);
  }
  /**
   * Get this Widget's next sibling in the parent {@link Core.widget.Container Container}, or, if not
   * in a Container, the next sibling widget in the same _parentElement_.
   * @property {Core.widget.Widget}
   * @readonly
   * @category Misc
   */

  get nextSibling() {
    return this.getSibling(1);
  }

  getSibling(increment) {
    const me = this,
          {
      parent
    } = me,
          siblings = parent ? parent.childItems : Array.from(me.element.parentElement.querySelectorAll('.b-widget'));
    return parent ? siblings[siblings.indexOf(me) + increment] : Widget.fromElement(siblings[siblings.indexOf(me.element) + increment]);
  }
  /**
   * Looks up the {@link #property-owner} axis to find an ancestor which matches the passed selector.
   * The selector may be a widget type identifier, such as `'grid'`, or a function which will return
   * `true` when passed the desired ancestore.
   * @param {String|Function} selector A Type identifier or selection function.
   * @param {Boolean} [deep] When using a string identifier, pass `true` if all superclasses should be included, ie if a `Grid` should match `'widget'`.
   * @param {Number|String|Core.widget.Widget} [limit] how many steps to step up before aborting the search, or a selector to stop at or the topmost ancestor to consider.
   */

  up() {
    const {
      owner
    } = this;

    if (owner) {
      return owner.closest(...arguments);
    }
  }
  /**
   * Starts with this Widget, then Looks up the {@link #property-owner} axis to find an ancestor which matches the passed selector.
   * The selector may be a widget type identifier, such as `'grid'`, or a function which will return
   * `true` when passed the desired ancestore.
   * @param {String|Function} selector A Type identifier or selection function.
   * @param {Boolean} [deep] When using a string identifier, pass `true` if all superclasses should be included, ie if a `Grid` should match `'widget'`.
   * @param {Number|String|Core.widget.Widget} [limit] how many steps to step up before aborting the search, or a selector to stop at or the topmost ancestor to consider.
   */

  closest(selector, deep, limit) {
    const limitType = typeof limit,
          numericLimit = limitType === 'number',
          selectorLimit = limitType === 'string';

    for (let result = this, steps = 1; result; result = result.owner, steps++) {
      if (Widget.widgetMatches(result, selector, deep)) {
        return result;
      }

      if (numericLimit && steps >= limit) {
        return;
      } else if (selectorLimit && Widget.widgetMatches(result, limit, deep)) {
        return;
      } else if (result === limit) {
        return;
      }
    }
  }
  /**
   * Returns `true` if this Widget owns the passed Element, Event or Widget. This is based on the widget hierarchy,
   * not DOM containment. So an element in a `Combo`'s dropdown list will be owned by the `Combo`.
   * @param {HTMLElement|Event|Core.widget.Widget} target The element event or Widget to test for being
   * within the ownership tree of this Widget.
   */

  owns(target) {
    if (target) {
      // Passed an event, grab its target
      if (target.eventPhase) {
        target = target.target;
      } // We were passed an HTMLElement

      if (target.nodeType === 1) {
        if (this.element.contains(target)) {
          return true;
        }

        target = Widget.fromElement(target);
      }

      while (target) {
        if (target === this) {
          return true;
        }

        target = target.owner;
      }
    }

    return false;
  }
  /**
   * Iterate over all ancestors of this widget.
   *
   * *Note*: Due to this method aborting when the function returns `false`, beware of using short form arrow
   * functions. If the expression executed evaluates to `false`, iteration will terminate.
   * @param {Function} fn Function to execute for all ancestors. Terminate iteration by returning `false`.
   * @returns {Boolean} Returns `true` if iteration was not aborted by a step returning `false`
   */

  eachAncestor(fn) {
    let ancestor = this.owner;

    while (ancestor) {
      if (fn(ancestor) === false) {
        return false;
      }

      ancestor = ancestor.owner;
    }

    return true;
  }

  get readOnly() {
    return this._readOnly || false;
  }

  changeMonitorResize(monitorResize, oldMonitorResize) {
    // They are mutually exclusive. scaleToFitWidth disables monitorResize
    const result = this.scaleToFitWidth ? false : Boolean(monitorResize); // null and undefined both mean false. Avoid going through the updater if no change.

    if (result !== Boolean(oldMonitorResize)) {
      return result;
    }
  }

  updateMonitorResize(monitorResize) {
    const me = this;

    if (!hasOwnProperty$6.call(me, 'onElementResize')) {
      me.onElementResize = me.onElementResize.bind(me);
    }

    ResizeMonitor[monitorResize ? 'addResizeListener' : 'removeResizeListener'](me.element, me.onElementResize);
  }

  changeReadOnly(readOnly) {
    readOnly = Boolean(readOnly); // It starts as undefined, so if false is passed, that's a no-change.

    if (Boolean(this._readOnly) !== readOnly) {
      return readOnly;
    }
  }

  updateReadOnly(readOnly) {
    var _this$element2;

    // Can be called from the element initialization because of the way Panel is set up.
    // tbar and bbar are instantiated, and their elements added to the gathered element
    // config object, but that can have consequences which can lead here.
    // eslint-disable-next-line no-unused-expressions
    (_this$element2 = this.element) === null || _this$element2 === void 0 ? void 0 : _this$element2.classList[readOnly ? 'add' : 'remove']('b-readonly'); // Do not update  children at configure time.
    // Container will sync its items.

    if (!this.isConfiguring) {
      // Implemented at this level because Widgets can own a descendant tree without being
      // a Container. For example Combos own a ChipView and a List. Buttons own a Menu etc.
      this.eachWidget(widget => {
        if (!('_originalReadOnly' in widget)) {
          // Store initial readOnly/disabled value.
          // the config getter copies the properties in a loop
          // so execute once and cache the value.
          widget._originalReadOnly = widget.config.readOnly || false;
        } // Set if truthy, otherwise reset to initial value

        widget.readOnly = readOnly || widget._originalReadOnly;
      }, false);
      /**
       * Fired when a Widget's read only state is toggled
       * @event readOnly
       * @param {Boolean} readOnly Read only or not
       */

      this.trigger('readOnly', {
        readOnly
      });
    }
  }
  /**
   * Iterate over all widgets owned by this widget and any descendants.
   *
   * *Note*: Due to this method aborting when the function returns `false`, beware of using short form arrow
   * functions. If the expression executed evaluates to `false`, iteration will terminate.
   * @param {Function} fn A function to execute upon all descendant widgets.
   * Iteration terminates if this function returns `false`.
   * @param {Boolean} [deep=true] Pass as `false` to only consider immediate child widgets.
   * @returns {Boolean} Returns `true` if iteration was not aborted by a step returning `false`
   */

  eachWidget(fn, deep = true) {
    const widgets = this.childItems,
          length = widgets ? widgets.length : 0;

    for (let i = 0; i < length; i++) {
      const widget = widgets[i]; // Abort if a call returns false

      if (fn(widget) === false) {
        return false;
      }

      if (deep && widget.eachWidget) {
        // Abort if a deep call returns false
        if (widget.eachWidget(fn, deep) === false) {
          return false;
        }
      }
    }

    return true;
  }
  /**
   * Returns an array of all descendant widgets which the passed
   * filter function returns `true` for.
   * @param {Function} filter A function which, when passed a widget,
   * returns `true` to include the widget in the results.
   * @returns {Core.widget.Widget[]} All matching descendant widgets.
   */

  queryAll(filter) {
    const result = [];
    this.eachWidget(w => {
      if (filter(w)) {
        result.push(w);
      }
    });
    return result;
  }
  /**
   * Returns the first descendant widgets which the passed
   * filter function returns `true` for.
   * @param {Function} filter A function which, when passed a widget,
   * returns `true` to return the widget as the sole result.
   * @returns {Core.widget.Widget} The first matching descendant widget.
   */

  query(filter) {
    let result = null;
    this.eachWidget(w => {
      if (filter(w)) {
        result = w;
        return false;
      }
    });
    return result;
  }
  /**
   * Get a widget by ref, starts on self and traverses up the owner hierarchy checking `widgetMap` at each level.
   * Not checking the top level widgetMap right away to have some acceptance for duplicate refs.
   * @param {String} ref ref to find
   * @returns {Core.widget.Widget}
   * @internal
   */

  getWidgetByRef(ref) {
    var _this$widgetMap, _this$owner;

    if (ref instanceof Widget) {
      return ref;
    }

    return (this === null || this === void 0 ? void 0 : (_this$widgetMap = this.widgetMap) === null || _this$widgetMap === void 0 ? void 0 : _this$widgetMap[ref]) || (this === null || this === void 0 ? void 0 : (_this$owner = this.owner) === null || _this$owner === void 0 ? void 0 : _this$owner.getWidgetByRef(ref));
  }

  onFocusIn(e) {
    const me = this,
          {
      element
    } = me;
    me.containsFocus = true;
    me.focusInEvent = e;
    element.classList.add('b-contains-focus');

    if (element.contains(e._target) && me.onInternalKeyDown && !me.keyDownListenerRemover) {
      me.keyDownListenerRemover = EventHelper.on({
        element,
        keydown: 'onInternalKeyDown',
        thisObj: me
      });
    }
    /**
     * Fired when focus enters this Widget.
     * @event focusIn
     * @param {Core.widget.Widget} source - This Widget
     * @param {HTMLElement} fromElement The element which lost focus.
     * @param {HTMLElement} toElement The element which gained focus.
     * @param {Core.widget.Widget} fromWidget The widget which lost focus.
     * @param {Core.widget.Widget} toWidget The widget which gained focus.
     * @param {Boolean} backwards `true` if the `toElement` is before the `fromElement` in document order.
     */

    me.trigger('focusin', e);
  }

  onFocusOut(e) {
    const me = this;

    if (me.keyDownListenerRemover) {
      me.keyDownListenerRemover();
      me.keyDownListenerRemover = null;
    }

    if (!me.isDestroyed) {
      // Focus to nowhere, focus a close relation
      if (!e.relatedTarget) {
        me.revertFocus(!me.isVisible);
      }

      me.containsFocus = false;
      me.element.classList.remove('b-contains-focus');
      /**
       * Fired when focus exits this Widget's ownership tree. This is different from a `blur` event.
       * focus moving from within this Widget's ownership tree, even if there are floating widgets
       * will not trigger this event. This is when focus exits this widget completely.
       * @event focusOut
       * @param {Core.widget.Widget} source - This Widget
       * @param {HTMLElement} fromElement The element which lost focus.
       * @param {HTMLElement} toElement The element which gained focus.
       * @param {Core.widget.Widget} fromWidget The widget which lost focus.
       * @param {Core.widget.Widget} toWidget The widget which gained focus.
       * @param {Boolean} backwards `true` if the `toElement` is before the `fromElement` in document order.
       */

      me.trigger('focusout', e);
    }
  }
  /**
   * If this Widget contains focus, focus is reverted to the source from which it entered if possible,
   * or to a close relative if not.
   * @param {Boolean} force Pass as `true` to move focus to the previously focused item, or the
   * closest possible relatve even if this widget does not contain focus.
   */

  revertFocus(force) {
    let target = this.focusInEvent && this.focusInEvent.relatedTarget;

    if (force || this.containsFocus && target && target.nodeType === 1 && this.element.contains(DomHelper.activeElement)) {
      if (!target || !DomHelper.isFocusable(target)) {
        target = this.getFocusRevertTarget();
      }

      if (target && DomHelper.isFocusable(target)) {
        DomHelper.focusWithoutScrolling(target);
      }
    }
  }
  /**
   * This method finds a close sibling (or parent, or parent's sibling etc recursively) to which focus
   * can be directed in the case of revertFocus not having a focusable element from our focusInEvent.
   *
   * This can hapen when the "from" component is destroyed or hidden. We should endeavour to prevent
   * focus escaping to `document.body` for accessibility and ease of use, and keep focus close.
   * @internal
   */

  getFocusRevertTarget() {
    const me = this,
          {
      owner,
      focusInEvent
    } = me,
          searchDirection = focusInEvent ? focusInEvent.backwards ? 1 : -1 : -1;
    let target = focusInEvent && focusInEvent.relatedTarget;
    const toComponent = target && Widget.fromElement(target); // If the from element is now not focusable, for example an Editor which hid
    // itself on focus leave, then we have to find a sibling/parent/parent's sibling
    // to take focus. Anything is better than flipping to document.body.

    if (owner && !owner.isDestroyed && (!target || !DomHelper.isFocusable(target) || toComponent && !toComponent.isFocusable)) {
      target = null; // If this widget can have siblings, then find the closest
      // (in the direction focus arrived from) focusable sibling.

      if (owner.eachWidget) {
        const siblings = []; // Collect focusable siblings.
        // With this included so we can find ourselves.

        owner.eachWidget(w => {
          if (w === me || w.isFocusable) {
            siblings.push(w);
          }
        }, false);

        if (siblings.length > 1) {
          const myIndex = siblings.indexOf(me);
          target = siblings[myIndex + searchDirection] || siblings[myIndex - searchDirection];
        }
      } // No focusable siblings found to take focus, try the owner

      if (!target && owner.isFocusable) {
        target = owner;
      } // If non of the above found any related focusable widget,
      // Go through these steps for the owner.

      target = target ? target.focusElement : owner.getFocusRevertTarget();
    }

    return target;
  }
  /**
   * Used by the Widget class internally to create CSS classes based on this Widget's
   * inheritance chain to allow styling from each level to apply.
   *
   * For example Combo would yield `"["b-widget", "b-field", "b-textfield", "b-pickerfield", "b-combo"]"`
   *
   * May be implemented in subclasses to add or remove classes from the super.widgetClassList
   * @returns {String[]} The css class list named using the class name.
   * @internal
   * @category DOM
   */

  get widgetClassList() {
    const me = this;
    const myCls = me.cls,
          result = me.classHierarchy(Widget).reduce((target, cls) => {
      const widgetClassProperty = Reflect.getOwnPropertyDescriptor(cls.prototype, 'widgetClass');
      let widgetClass; // If the Class has its own get widgetClass, call it upon this instance.

      if (widgetClassProperty && widgetClassProperty.get) {
        widgetClass = widgetClassProperty.get.call(me);
      } else {
        // All built in widgets should define $name to be safer from minification/obfuscation, but user
        // created might not so fall back to actual name. UMD files use a _$name property
        // which the Base $$name getter uses as a fallback.
        const name = hasOwnProperty$6.call(cls, '$$name') || hasOwnProperty$6.call(cls, '$name') || hasOwnProperty$6.call(cls, '_$name') ? cls.$$name : cls.name; // Throw error in case of an obfuscated name or an autogenerated name.
        // These should never be released without a meaningful $name getter.

        if (name.length < 3 || name.includes('$')) {
          // class.$name comes from parent API class which has it
          console.warn(`Class "${name}" which extends "${cls.$name}" should have "$name" static getter with no less than 3 chars.`);
        }

        widgetClass = `b-${name.toLowerCase()}`;
      }

      if (widgetClass) {
        // The result is used as a DOM classList. Must not contain spaces.
        target.push(...widgetClass.split(' '));
      }

      return target;
    }, (myCls === null || myCls === void 0 ? void 0 : myCls.values) || []);

    if (me.floating) {
      result.push('b-floating');
    }

    if (BrowserHelper.isTouchDevice) {
      result.push('b-touch');
    }

    return result;
  }

  changeCls(cls) {
    var _cls;

    if (!((_cls = cls) !== null && _cls !== void 0 && _cls.isDomClassList)) {
      cls = new DomClassList(cls);
    }

    return cls;
  }

  changeContentElementCls(cls) {
    var _cls2;

    if (!((_cls2 = cls) !== null && _cls2 !== void 0 && _cls2.isDomClassList)) {
      cls = new DomClassList(cls);
    }

    return cls;
  }

  changeHtmlCls(cls) {
    var _cls3;

    if (!((_cls3 = cls) !== null && _cls3 !== void 0 && _cls3.isDomClassList)) {
      cls = new DomClassList(cls);
    }

    return cls;
  } //endregion
  //region Cache

  /**
   * Gets dom elements in the view. Caches the results for faster future calls.
   * @param {String} query CSS selector
   * @param {Boolean} children true to fetch multiple elements
   * @param {HTMLElement} element Element to use as root for the query, defaults to the views outermost element
   * @returns {HTMLElement|HTMLElement[]|null} A single element or an array of elements (if parameter children is set to true)
   * @internal
   * @category DOM
   */

  fromCache(query, children = false, element = this.element) {
    if (!element) return null;
    const me = this;

    if (!me.cache[query]) {
      me.cache[query] = children ? DomHelper.children(element, query) : DomHelper.down(element, query);
    }

    return me.cache[query];
  }
  /**
   * Clear caches, forces all calls to fromCache to requery dom. Called on render/rerender.
   * @internal
   * @category DOM
   */

  emptyCache() {
    this.cache = {};
  } //endregion
  //region Mask

  changeMasked(mask, maskInstance) {
    if (mask === true || mask === '') {
      // empty string don't render well, so promote it to an &nbsp; as well
      mask = '\xA0'; // nbsp
    }

    if (maskInstance) {
      if (typeof mask === 'string') {
        maskInstance.text = mask;
        mask = maskInstance;
      } else if (mask) {
        maskInstance.setConfig(mask);
        mask = maskInstance;
      } else {
        maskInstance.destroy();
      }
    } else if (mask) {
      const Mask = Widget.resolveType('mask');
      mask = Mask.mergeConfigs(this.maskDefaults, mask);
      mask.owner = this;
      mask = Mask.mask(mask);
    }

    return mask || null;
  }

  onMaskAutoClose(mask) {
    if (mask.isDestroyed && mask === this.masked) {
      this.masked = null;
    }
  }
  /**
   * Mask the widget, showing the specified message
   * @param {String|Object} msg Mask message (or a {@link Core.widget.Mask} config object
   * @returns {Core.widget.Mask}
   */

  mask(msg) {
    this.masked = msg;
    return this.masked;
  }
  /**
   * Unmask the widget
   */

  unmask() {
    this.masked = null;
  } //endregion
  //region Monitor resize

  onInternalResize(element, width, height, oldWidth, oldHeight) {
    this._width = element.offsetWidth;
    this._height = element.offsetHeight;
  }

  onElementResize(resizedElement) {
    const me = this,
          {
      element,
      scrollable
    } = me,
          oldWidth = me._width,
          oldHeight = me._height,
          newWidth = element.offsetWidth,
          newHeight = element.offsetHeight;

    if (me.floating) {
      me.onFloatingWidgetResize(...arguments);
    }

    if (scrollable) {
      var _scrollable$syncOverf;

      (_scrollable$syncOverf = scrollable.syncOverflowState) === null || _scrollable$syncOverf === void 0 ? void 0 : _scrollable$syncOverf.call(scrollable);
    }

    if (!me.suspendResizeMonitor && (oldWidth !== newWidth || oldHeight !== newHeight)) {
      me.onInternalResize(element, newWidth, newHeight, oldWidth, oldHeight);
      /**
       * Fired when the encapsulating element of a Widget resizes *only when {@link #config-monitorResize} is `true`*.
       * @event resize
       * @param {Core.widget.Widget} source - This Widget
       * @param {Number} width The new width
       * @param {Number} height The new height
       * @param {Number} oldWidth The old width
       * @param {Number} oldHeight The old height
       */

      me.trigger('resize', {
        width: newWidth,
        height: newHeight,
        oldWidth,
        oldHeight
      });
    }
  }

  onFloatingWidgetResize(resizedElement, lastRect, myRect) {
    const me = this,
          {
      lastAlignSpec,
      constrainTo
    } = me; // If this Popup changes size while we are aligned and we are aligned to
    // a target (not a position), then we might need to realign.

    if (me.isVisible && lastAlignSpec && lastAlignSpec.target) {
      const heightChange = !lastRect || myRect.height !== lastRect.height,
            widthChange = !lastRect || myRect.width !== lastRect.width,
            failsConstraint = constrainTo && !Rectangle.from(constrainTo).contains(Rectangle.from(me.element, null, true)); // Only realign if:
      // the height has changed and we are not aligned below, or
      // the width has changed and we are not aligned to the right.

      if (heightChange && lastAlignSpec.zone !== 2 || widthChange && lastAlignSpec.zone !== 1 || failsConstraint) {
        // Must move to next AF because in Chrome, the resize monitor might fire
        // before the element is painted and the anchor color matching
        // scheme cannot work in that case.
        me.requestAnimationFrame(() => me.realign());
      }
    }
  }

  updateScale() {
    const me = this,
          element = me.element,
          parentElement = element.parentElement; // this could be placed elsewhere but want to keep it contained to not spam other code,
    // since this is a very specific use case in our docs

    if (!me.configuredWidth) {
      me.configuredWidth = me.width;
    } // TODO: handle autoHeight, but seems it assigns height to late with current setup
    // We are scaling to fit inside the width, so ensure that we are not the cause of a scrollbar
    // in our current, unscaled state by hiding while we measure the parent's offsetWidth which
    // we are going to scale to.

    element.style.display = 'none';
    const rect = Rectangle.client(parentElement),
          scale = rect.width / me.configuredWidth,
          adjustedScale = me.scale = me.allowGrowWidth ? Math.min(scale, 1) : scale;
    element.style.transform = `scale(${adjustedScale})`;
    element.style.transformOrigin = 'top left';
    element.style.display = '';

    if (me.allowGrowWidth && scale > 1) {
      // increase width
      me.width = me.configuredWidth * scale;
    }
  }

  onParentElementResize(event) {
    this.updateScale();
  } //endregion

  /**
   * Returns a `TRBL` array of values parse from the passed specification. This can be used to parse`
   * a value list for `margin` or `padding` or `border-width` etc - any CSS value which takes a `TRBL` value.
   * @param {Number|String|String[]} values The `TRBL` value
   * @param {String} [units=px] The units to add to values which are specified as numeric.
   * @internal
   */

  parseTRBL(values, units = 'px') {
    values = values || 0;

    if (typeof values === 'number') {
      return [`${values}${units}`, `${values}${units}`, `${values}${units}`, `${values}${units}`];
    }

    const parts = values.split(' '),
          len = parts.length;

    if (len === 1) {
      parts[1] = parts[2] = parts[3] = parts[0];
    } else if (len === 2) {
      parts[2] = parts[0];
      parts[3] = parts[1];
    } else if (len === 3) {
      parts[3] = parts[1];
    }

    return [isFinite(parts[0]) ? `${parts[0]}${units}` : parts[0], isFinite(parts[1]) ? `${parts[1]}${units}` : parts[2], isFinite(parts[2]) ? `${parts[2]}${units}` : parts[3], isFinite(parts[3]) ? `${parts[3]}${units}` : parts[4]];
  }

  static get floatRoot() {
    const me = this;

    if (!me._floatRoot) {
      // Reuse any existing floatRoot. There might be one if using multiple product bundles
      me._floatRoot = document.querySelector('.b-float-root');
    }

    if (!me._floatRoot) {
      var _DomHelper$themeInfo;

      const {
        outerCls
      } = me,
            themeName = (_DomHelper$themeInfo = DomHelper.themeInfo) === null || _DomHelper$themeInfo === void 0 ? void 0 : _DomHelper$themeInfo.name; // When outside of our examples, the body element doesn't get the theme class.
      // The floatRoot must carry it for floating items to be themed.

      if (themeName) {
        outerCls.push(`b-theme-${themeName.toLowerCase()}`);
      }

      me._floatRoot = DomHelper.createElement({
        className: `b-float-root ${outerCls.join(' ')}`,
        parent: document.body
      }); // Make float root immune to keyboard-caused size changes

      if (BrowserHelper.isAndroid) {
        me._floatRoot.style.height = `${screen.height}px`;
        EventHelper.on({
          element: window,
          orientationchange: () => me._floatRoot.style.height = `${screen.height}px`
        });
      } // Keep floatRoot up to date with the theme

      GlobalEvents.on({
        theme: ({
          theme,
          prev
        }) => {
          me.floatRoot.classList.add(`b-theme-${theme.toLowerCase()}`);
          me.floatRoot.classList.remove(`b-theme-${prev.toLowerCase()}`);
        }
      });
    } else if (!document.body.contains(me._floatRoot)) {
      // Reattach floatRoot if it was detached
      document.body.appendChild(me._floatRoot);
    }

    return me._floatRoot;
  } // CSS classes describing outer-most Widgets to provide styling / behavioral CSS style rules

  static get outerCls() {
    const result = ['b-outer'];

    if (BrowserHelper.isTouchDevice) {
      result.push('b-touch-events');
    }

    if (DomHelper.scrollBarWidth) {
      result.push('b-visible-scrollbar');
    } else {
      result.push('b-overlay-scrollbar');
    }

    if (BrowserHelper.isChrome) {
      result.push('b-chrome');
    } else if (BrowserHelper.isSafari) {
      result.push('b-safari');
    } else if (BrowserHelper.isFirefox) {
      result.push('b-firefox');
    } else if (BrowserHelper.isIE11) {
      result.push('b-ie');
    } else if (BrowserHelper.isEdge) {
      result.push('b-edge');
    } // So that we don't get the polyfill styles applied if we have ResizeMonitor available.
    // The polyfill styles can break certain elements styling.

    if (!window.ResizeObserver) {
      result.push('b-no-resizeobserver');
    }

    return result;
  }

  get isAnimating() {
    return this._animating;
  }

  set isAnimating(value) {
    const me = this;

    if (value !== me.isAnimating) {
      me.element.classList[value ? 'add' : 'remove']('b-animating');
      me._animating = value;
    }
  }
  /**
   * Analogous to document.querySelector, finds the first Bryntum widget matching the passed
   * selector. Right now, only class name (lowercased) selector strings, or
   * a filter function which returns `true` for required object are allowed:
   * ```
   * bryntum.query('grid').destroy();
   * ```
   * @param {String|Function} selector A lowercased class name, or a filter function.
   * @param {Boolean} [deep] Specify `true` to search the prototype chain (requires supplying a string `selector`). For
   *   example 'widget' would then find a Grid
   * @return {Core.widget.Widget} The first matched widget if any.
   */

  static query(selector, deep = false) {
    const {
      idMap
    } = Widget.identifiable;

    for (const id in idMap) {
      if (Widget.widgetMatches(idMap[id], selector, deep)) {
        return idMap[id];
      }
    }

    return null;
  }
  /**
   * Analogous to document.querySelectorAll, finds all Bryntum widgets matching the passed
   * selector. Right now, only registered widget `type` strings, or a filter function which
   * returns `true` for required object are allowed:
   * ```
   * let allFields = bryntum.queryAll('field', true);
   * ```
   * @param {String|Function} selector A lowercased class name, or a filter function.
   * @param {Boolean} [deep] Specify `true` to search the prototype chain (requires supplying a string `selector`). For
   *   example 'widget' would then find a Grid
   * @return {Core.widget.Widget[]} The first matched widgets if any - an empty array will be returned
   * if no matches are found.
   */

  static queryAll(selector, deep = false) {
    const {
      idMap
    } = Widget.identifiable,
          result = [];

    for (const id in idMap) {
      if (Widget.widgetMatches(idMap[id], selector, deep)) {
        result.push(idMap[id]);
      }
    }

    return result;
  }
  /**
   * Returns the Widget which owns the passed element (or event).
   * @param {HTMLElement|Event} element The element or event to start from
   * @param {String|Function} [type] The type of Widget to scan upwards for. The lowercase
   * class name. Or a filter function which returns `true` for the required Widget.
   * @param {HTMLElement|Number} [limit] The number of components to traverse upwards to find a
   * match of the type parameter, or the element to stop at.
   * @return {Core.widget.Widget} The found Widget or null.
   */

  static fromElement(element, type, limit) {
    const typeOfType = typeof type; // Event passed

    if (element && element instanceof Event) {
      element = element.target;
    }

    if (typeOfType === 'number' || type && type.nodeType === 1) {
      limit = type;
      type = null;
    }

    let target = element,
        depth = 0,
        topmost,
        cmpId,
        cmp;

    if (typeof limit !== 'number') {
      topmost = limit;
      limit = Number.MAX_VALUE;
    }

    if (typeOfType === 'string') {
      type = type.toLowerCase();
    }

    while (target && target.nodeType === 1 && depth < limit && target !== topmost) {
      cmpId = target.dataset && target.dataset.ownerCmp || target.id;

      if (cmpId) {
        cmp = Widget.getById(cmpId);

        if (cmp) {
          if (type) {
            if (typeOfType === 'function') {
              if (type(cmp)) {
                return cmp;
              }
            } else if (Widget.widgetMatches(cmp, type, true)) {
              return cmp;
            }
          } else {
            return cmp;
          }
        } // Increment depth on every *Widget* found

        depth++;
      }

      target = target.parentNode;
    }

    return null;
  } // NOTE: Not named `triggerChange` to not conflict with existing fn on Field

  /**
   * Triggers a 'change' event with the supplied params. After triggering it also calls `onFieldChange()` on each
   * ancestor the implements that function, supplying the same set of params.
   * @param {Object} params Event params, used both for triggering and notifying ancestors
   * @param {Boolean} [trigger] `false` to not trigger, only notifying ancestors
   * @internal
   */

  triggerFieldChange(params, trigger = true) {
    if (trigger) {
      this.trigger('change', params);
    }

    this.eachAncestor(ancestor => {
      if (ancestor.onFieldChange) {
        ancestor.onFieldChange(params);
      }
    });
  }

  static widgetMatches(candidate, selector, deep) {
    if (selector === '*') {
      return true;
    }

    if (typeof selector === 'function') {
      return selector(candidate);
    }

    return Widget.isType(candidate, selector, deep);
  }
  /**
   * Attached a tooltip to the specified element.
   * @example
   * Widget.attachTooltip(element, {
   *   text: 'Useful information goes here'
   * });
   * @param {HTMLElement} element Element to attach tooltip for
   * @param {Object|String} configOrText Tooltip config or tooltip string, see example and source
   * @returns {HTMLElement}
   * @category Popups
   */

  static attachTooltip(element, configOrText) {
    if (typeof configOrText === 'string') configOrText = {
      html: configOrText
    }; // TODO: refactor this
    // eslint-disable-next-line no-new

    Widget.create(Object.assign({
      forElement: element
    }, configOrText), 'tooltip');
    return element;
  }

} // Register this widget type with its Factory

Widget.initClass();
Widget.register('mask', Mask); // These low level classes must not import Widget because that would cause circularity.
// Instead Widget injects a reference to itself into them.

DomHelper.Widget = Widget;
GlobalEvents.Widget = Widget; // We use the same map to track instances by ID

Mask.identifiable.idMap = Widget.identifiable.idMap; // Simplify querying widgets by exposing fns in bryntum ns

(window.bryntum || (window.bryntum = {})).get = Widget.getById.bind(Widget);
window.bryntum.Widget = Widget;
window.bryntum.query = Widget.query;
window.bryntum.queryAll = Widget.queryAll;
window.bryntum.fromElement = Widget.fromElement;
Widget._$name = 'Widget';

/**
 * @module Core/widget/Toast
 */
/**
 * Basic toast. Toasts are stacked on top of each other
 * @example
 * // simplest possible
 * Toast.show('Just toasting');
 *
 * // with config
 * Toast.show({
 *   html: 'Well toasted',
 *   showProgress: false
 * });
 *
 * // as instance (instance is also returned from Toast.show()
 * let toast = new Toast({
 *   html: 'Not going away',
 *   timeout: 0
 * });
 *
 * toast.show();
 *
 * @classType toast
 * @externalexample widget/Toast.js
 */

class Toast extends Widget {
  static get $name() {
    return 'Toast';
  } // Factoryable type name

  static get type() {
    return 'toast';
  }

  static get defaultConfig() {
    return {
      testConfig: {
        timeout: 100,
        destroyTimeout: 1
      },
      floating: true,

      /**
       * Timeout (in ms) until the toast is automatically dismissed. Set to 0 to never hide.
       * @config {Number}
       * @default
       */
      timeout: 2500,
      // How long to wait after hide before destruction
      destroyTimeout: 200,

      /**
       * Show a progress bar indicating the time remaining until the toast is dismissed.
       * @config {Boolean}
       * @default
       */
      showProgress: true,

      /**
       * Toast color (should have match in toast.scss or your custom styling).
       * Valid values in Bryntum themes are:
       * * b-amber
       * * b-blue
       * * b-dark-gray
       * * b-deep-orange
       * * b-gray
       * * b-green
       * * b-indigo
       * * b-lime
       * * b-light-gray
       * * b-light-green
       * * b-orange
       * * b-purple
       * * b-red
       * * b-teal
       * * b-white
       * * b-yellow
       *
       * ```
       * new Toast({
       *    color : 'b-blue'
       * });
       * ```
       *
       * @config {String}
       */
      color: null,
      bottomMargin: 20
    };
  }

  doDestroy() {
    const index = Toast.toasts.indexOf(this);

    if (index > -1) {
      Toast.toasts.splice(index, 1);
    }

    super.doDestroy();
  } // Toasts must not create their element until show.

  set element(element) {}

  get element() {
    // Toasts must not create their element until show.
    if (!this._element && !this.isConfiguring) {
      const me = this,
            topToast = Toast.toasts.length && Toast.toasts[0].element,
            bottom = topToast ? topToast._nextBottom : me.bottomMargin,
            element = super.element = DomHelper.createElement({
        parent: me.appendTo || Widget.floatRoot,
        children: me.showProgress ? [{
          className: 'b-toast-progress',
          style: `animation-duration:${me.timeout / 1000}s;`
        }] : null,
        html: me._html,
        className: me.color || '',
        style: me._style || ''
      });
      EventHelper.on({
        element,
        click: 'hide',
        thisObj: me
      });
      Toast.toasts.unshift(me);
      element._nextBottom = bottom + element.offsetHeight + me.bottomMargin; // Transitioned style props must be set dynamically

      element.style.cssText += `;bottom:${bottom}px;transform:translateY(0)`;
    }

    return super.element;
  }
  /**
   * Show the toast
   */

  show() {
    super.show();

    if (this.timeout > 0) {
      this.hideTimeout = this.setTimeout('hide', this.timeout);
    }
  }
  /**
   * Hide the toast
   */

  hide() {
    const me = this;

    if (!me.destroyTimer) {
      me.element.classList.add('b-toast-hide');
      me.element.style.bottom = '';
      me.destroyTimer = me.setTimeout('destroy', me.destroyTimeout);
    }
  }
  /**
   * Hide all visible toasts
   */

  static hideAll() {
    Toast.toasts.reverse().forEach(toast => toast.hide());
  }
  /**
   * Easiest way to show a toast
   * @param {String|Object} msgOrConfig Message or toast config object
   * @returns {Core.widget.Toast}
   * @example
   * Toast.show('Hi');
   * @example
   * Toast.show({
   *   html   : 'Read quickly, please',
   *   timeout: 1000
   * });
   */

  static show(msgOrConfig) {
    const toast = new Toast(typeof msgOrConfig === 'string' ? {
      html: msgOrConfig
    } : msgOrConfig);
    toast.show();
    return toast;
  }

}
Toast.toasts = []; // Register this widget type with its Factory

Toast.initClass();
Toast._$name = 'Toast';

/**
 * @module Core/helper/WidgetHelper
 */

/**
 * Helper for creating widgets.
 */

class WidgetHelper {
  //region Querying

  /**
   * Returns the widget with the specified id.
   * @param {String} Id of widget to find
   * @returns {Core.widget.Widget} The widget if any
   * @category Querying
   */
  static getById(id) {
    return Widget.getById(id);
  }
  /**
   * Returns the Widget which owns the passed element (or event).
   * @param {HTMLElement|Event} element The element or event to start from
   * @param {String|Function} [type] The type of Widget to scan upwards for. The lowercase
   * class name. Or a filter function which returns `true` for the required Widget.
   * @param {HTMLElement|Number} [limit] The number of components to traverse upwards to find a
   * match of the type parameter, or the element to stop at.
   * @returns {Core.widget.Widget} The found Widget or null.
   * @category Querying
   * @typings any
   */

  static fromElement(element, type, limit) {
    return Widget.fromElement(element, type, limit);
  } //endregion
  //region Widgets

  /**
   * Create a widget.
   * @example
   * WidgetHelper.createWidget({
   *   type: 'button',
   *   icon: 'user',
   *   text: 'Edit user'
   * });
   * @param {Object} config Widget config
   * @returns {Object} The widget
   * @category Widgets
   */

  static createWidget(config = {}) {
    return config.isWidget ? config : Widget.create(config);
  }
  /**
   * Appends a widget (array of widgets) to the DOM tree. If config is empty, widgets are appended to the DOM. To
   * append widget to certain position you can pass HTMLElement or its id as config, or as a config, that will be
   * applied to all passed widgets.
   *
   * Usage:
   *
   * ```javascript
   * // Will append button as last item to element with id 'container'
   * let [button] = WidgetHelper.append({ type : 'button' }, 'container');
   *
   * // Same as above, but will add two buttons
   * let [button1, button2] = WidgetHelper.append([
   *     { type : 'button' },
   *     { type : 'button' }
   *     ], { appendTo : 'container' });
   *
   * // Will append two buttons before element with id 'someElement'. Order will be preserved and all widgets will have
   * // additional class 'my-cls'
   * let [button1, button2] = WidgetHelper.append([
   *     { type : 'button' },
   *     { type : 'button' }
   *     ], {
   *         insertBefore : 'someElement',
   *         cls          : 'my-cls'
   *     });
   * ```
   *
   * @param {Object|Object[]} widget Widget config or array of such configs
   * @param {HTMLElement|String|Object} [config] Element (or element id) to which to append the widget or config to apply to all passed widgets
   * @returns {Core.widget.Widget[]} Array or widgets
   * @category Widgets
   */

  static append(widget, config) {
    widget = Array.isArray(widget) && widget || [widget];

    if (config instanceof HTMLElement || typeof config === 'string') {
      config = {
        appendTo: config
      };
    } // We want to fix position to insert into to keep order of passed widgets

    if (config.insertFirst) {
      const target = typeof config.insertFirst === 'string' ? document.getElementById(config.insertFirst) : config.insertFirst;

      if (target.firstChild) {
        config.insertBefore = target.firstChild;
      } else {
        config.appendTo = target;
      }
    }

    return widget.map(item => Widget.create(ObjectHelper.assign({}, config || {}, item)));
  } //endregion
  //region Popups

  /**
   * Shows a popup (~tooltip) containing widgets connected to specified element.
   * @example
   * WidgetHelper.openPopup(element, {
   *   position: 'bottom center',
   *   items: [
   *      { widgetConfig }
   *   ]
   * });
   * @param {HTMLElement} element Element to connect popup to
   * @param {Object} config Config object, or string to use as html in popup
   * @returns {*|{close, widgets}}
   * @category Popups
   */

  static openPopup(element, config) {
    return Widget.create(ObjectHelper.assign({
      forElement: element
    }, typeof config === 'string' ? {
      html: config
    } : config), 'popup');
  }
  /**
   * Shows a context menu connected to the specified element.
   * @example
   * WidgetHelper.showContextMenu(element, {
   *   items: [
   *      { id: 'addItem', icon: 'add', text: 'Add' },
   *      ...
   *   ],
   *   onItem: item => alert('Clicked ' + item.text)
   * });
   * @param {HTMLElement|Number[]} element Element (or a coordinate) to show the context menu for
   * @param {Object} config Context menu config, see example
   * @returns {*|{close}}
   * @category Popups
   */

  static showContextMenu(element, config) {
    const me = this;

    if (me.currentContextMenu) {
      me.currentContextMenu.destroy();
    }

    if (element instanceof HTMLElement) {
      config.forElement = element;
    } else if (Array.isArray(element)) {
      config.forElement = {
        target: new Point(...element)
      };
    } else if (element instanceof Point) {
      config.forElement = {
        target: element
      };
    }

    me.currentContextMenu = Widget.create(config, 'menu');
    me.currentContextMenu.on('destroy', () => {
      me.currentContextMenu = null;
    });
    return me.currentContextMenu;
  }
  /**
   * Attached a tooltip to the specified element.
   * @example
   * WidgetHelper.attachTooltip(element, {
   *   text: 'Useful information goes here'
   * });
   * @param {HTMLElement} element Element to attach tooltip for
   * @param {String|Object} configOrText Tooltip config or tooltip string, see example and source
   * @returns {Object}
   * @category Popups
   */

  static attachTooltip(element, configOrText) {
    return Widget.attachTooltip(element, configOrText);
  }
  /**
   * Checks if element has tooltip attached
   *
   * @param {HTMLElement} element Element to check
   * @return {Boolean}
   * @category Popups
   */

  static hasTooltipAttached(element) {
    return Widget.resolveType('tooltip').hasTooltipAttached(element);
  }
  /**
   * Destroys any tooltip attached to an element, removes it from the DOM and unregisters any tip related listeners
   * on the element.
   *
   * @param {HTMLElement} element Element to remove tooltip from
   * @category Popups
   */

  static destroyTooltipAttached(element) {
    return Widget.resolveType('tooltip').destroyTooltipAttached(element);
  } //endregion
  //region Mask

  /**
   * Masks the specified element, showing a message in the mask.
   * @param {HTMLElement} element Element to mask
   * @param {String} msg Message to show in the mask
   * @category Mask
   */

  static mask(element, msg = 'Loading') {
    if (element) {
      // Config object normalization
      if (element instanceof HTMLElement) {
        element = {
          target: element,
          text: msg
        };
      }

      return Mask.mask(element, element.target);
    }
  }
  /**
   * Unmask the specified element.
   * @param {HTMLElement} element
   * @category Mask
   */

  static unmask(element, close = true) {
    if (element.mask) {
      if (close) {
        element.mask.close();
      } else {
        element.mask.hide();
      }
    }
  } //endregion
  //region Toast

  /**
   * Show a toast
   * @param {String} msg message to show in the toast
   * @category Mask
   */

  static toast(msg) {
    return Toast.show(msg);
  } //endregion

}
WidgetHelper._$name = 'WidgetHelper';

class DataGenerator {
  //region Random
  static reset() {
    this.rnd.reset();
    this.rndTime.reset();
    this.rndRating.reset();
  } //endregion
  //region Generate data

  static *generate(count, randomHeight = false, initialId = 1) {
    const me = this,
          rnd = me.rnd,
          rndTime = me.rndTime,
          rndRating = me.rndRating,
          firstNames = me.firstNames,
          surNames = me.surNames,
          teams = me.teams,
          foods = me.foods,
          colors = me.colors,
          cities = me.cities;

    for (let i = 0; i < count; i++) {
      const firstName = rnd.fromArray(firstNames),
            surName = rnd.fromArray(surNames),
            name = `${firstName} ${String.fromCharCode(65 + i % 25)} ${surName}`,
            startDay = rnd.nextRandom(60) + 1,
            start = new Date(2019, 0, startDay),
            finish = new Date(2019, 0, startDay + rnd.nextRandom(30) + 2),
            //DateHelper.add(start, rnd.nextRandom(30) + 2, 'days'),
      row = {
        id: i + initialId,
        title: 'Row ' + i,
        name: name,
        firstName: firstName,
        surName: surName,
        city: rnd.fromArray(cities),
        team: rnd.fromArray(cities) + ' ' + rnd.fromArray(teams),
        age: 10 + rnd.nextRandom(80),
        food: rnd.fromArray(foods),
        color: rnd.fromArray(colors),
        score: rnd.nextRandom(100) * 10,
        rank: rnd.nextRandom(100) + 1,
        start: start,
        finish: finish,
        time: DateHelper.getTime(rndTime.nextRandom(24), rndTime.nextRandom(12) * 5),
        percent: rnd.nextRandom(100),
        done: rnd.nextRandom(100) < 50,
        rating: rndRating.nextRandom(5),
        relatedTo: Math.min(count - 1, i + initialId + rnd.nextRandom(10))
      };

      if (randomHeight) {
        row.rowHeight = rnd.nextRandom(randomHeight === true ? 20 : randomHeight) * 5 + 20;
      }

      yield row;
    }
  } // Param reset defaults to true to ensure we get the same dataset on consecutive calls. Without it, code editor
  // reloading modules yields different data each time

  static generateData(count, randomHeight = false, initialId = 1, reset = true) {
    if (reset) this.reset();
    const rows = [],
          number = DataGenerator.overrideRowCount ? DataGenerator.overrideRowCount : count,
          generator = this.generate(number, randomHeight, initialId);

    for (let i = 0; i < number; i++) {
      rows.push(generator.next().value);
    }

    return rows;
  } //endregion

}
Object.assign(DataGenerator, {
  rnd: new RandomGenerator(),
  rndTime: new RandomGenerator(),
  rndRating: new RandomGenerator(),
  cities: ['Stockholm', 'Barcelona', 'Paris', 'Dubai', 'New York', 'San Francisco', 'Washington', 'Moscow'],
  firstNames: ['Mike', 'Linda', 'Don', 'Karen', 'Doug', 'Jenny', 'Daniel', 'Melissa', 'John', 'Jane', 'Theo', 'Lisa', 'Adam', 'Mary', 'Barbara', 'James', 'David'],
  surNames: ['McGregor', 'Ewans', 'Scott', 'Smith', 'Johnson', 'Adams', 'Williams', 'Brown', 'Jones', 'Miller', 'Davis', 'More', 'Wilson', 'Taylor', 'Anderson', 'Thomas', 'Jackson'],
  teams: ['Lions', 'Eagles', 'Tigers', 'Horses', 'Dogs', 'Cats', 'Panthers', 'Rats', 'Ducks', 'Cougars', 'Hens', 'Roosters'],
  foods: ['Pancake', 'Burger', 'Fish n chips', 'Carbonara', 'Taco', 'Salad', 'Bolognese', 'Mac n cheese', 'Waffles'],
  colors: ['Blue', 'Green', 'Red', 'Yellow', 'Pink', 'Purple', 'Orange', 'Teal', 'Black']
});
DataGenerator._$name = 'DataGenerator';

const knownProps = ['action', 'target', 'to', 'deltaX', 'deltaY', 'x', 'y', 'text'];
class DemoBot extends Events(Delayable()) {
  static get defaultConfig() {
    return {
      repeat: true,
      outerElement: document.body,
      callOnFunctions: true
    };
  } // expects an outer element (grid.element/scheduler.element) and an array of steps similar to chain steps in siesta

  construct(config) {
    super.construct(config);
    const me = this;

    if (me.widget) {
      me.outerElement = me.widget.element;
      me.widget.playingDemo = true;
    }

    EventHelper.playingDemo = true;
    Object.assign(me, {
      prevTarget: null,
      currentStep: 0,
      mouse: DomHelper.createElement({
        parent: me.outerElement,
        tag: 'div',
        className: 'simulated-mouse'
      }),
      timeoutId: null,
      innerIntervalId: null,
      mouseOutElements: []
    });
    me.intervalId = me.setInterval(me.nextStep.bind(me), 1000);
    me.outerElement.classList.add('b-playing-demo');
    me.outerElement.addEventListener('click', event => {
      if (event.isTrusted) {
        me.abort();
      }
    });
  }

  doDestroy() {
    this.abort();
  } // stops the bot

  abort(atEnd = false) {
    const me = this;
    me.mouse.style.top = '-100px';
    me.clearInterval(me.intervalId);
    me.timeoutId && me.clearTimeout(me.timeoutId);
    me.innerIntervalId && me.clearInterval(me.innerIntervalId);
    me.outerElement.classList.remove('b-playing-demo');

    if (me.widget) {
      me.widget.playingDemo = false;
    }

    EventHelper.playingDemo = false;
    me.trigger(atEnd ? 'done' : 'abort');
  } // triggers a synthetic event

  triggerEvent(element, type, data) {
    if (!element) return null;
    let event;

    if (type.startsWith('mouse')) {
      const box = this.mouse.getBoundingClientRect();
      event = new MouseEvent(type, Object.assign({
        view: window,
        bubbles: true,
        cancelable: true,
        clientX: box.left,
        clientY: box.top
      }, data || {}));
    } else {
      event = document.createEvent('Event');
      event.initEvent(type, true, false);
    }

    element.dispatchEvent(event);
    return event;
  } // moves mouse to target in 10 steps, with animated transition between steps

  handleMouseMove(step, target) {
    const me = this,
          mouse = me.mouse;
    mouse.classList.add('quick');
    if (me.mouseDown) mouse.classList.add('drag');
    const mouseBox = Rectangle.from(mouse, me.outerElement),
          x = mouseBox.x,
          y = mouseBox.y;
    let deltaX = 0,
        deltaY = 0;

    if (step.to) {
      if (typeof step.to === 'string') {
        const toElement = me.outerElement.querySelector(step.to);

        if (toElement) {
          const rect = Rectangle.from(toElement, me.outerElement),
                toX = rect.x + rect.width / 2,
                toY = rect.y + rect.height / 2;
          deltaX = (toX - x) / 10;
          deltaY = (toY - y) / 10;
        }
      } else if (step.to.x) {
        deltaX = (step.to.x - x) / 10;
      } else {
        deltaX = step.to[0] / 10;
        deltaY = step.to[1] / 10;
      }
    } else if (step.deltaX) {
      deltaX = step.deltaX / 10;
    } else if (step.x) {
      deltaX = (step.x - x) / 10;
    }

    if (step.deltaY) {
      deltaY = step.deltaY / 10;
    }

    let i = 0;
    me.innerIntervalId = me.setInterval(() => {
      // Only move mouse if in view and not scrolling
      if (me.shouldPause) {
        return;
      }

      if (i++ === 9) {
        clearInterval(me.innerIntervalId);

        if (step.then) {
          step.then();
        }
      }

      const mouseX = x + deltaX * i,
            mouseY = y + deltaY * i; // Move mouse there also

      mouse.style.left = mouseX + 'px';
      mouse.style.top = mouseY + 'px';
      const mouseBounds = mouse.getBoundingClientRect(),
            clientX = mouseBounds.left,
            clientY = mouseBounds.top,
            eventTarget = DomHelper.elementFromPoint(clientX, clientY);

      if (eventTarget !== me.prevTarget) {
        if (me.prevTarget) {
          me.mouseOutElements.push(me.prevTarget);

          if (!DomHelper.isDescendant(me.mouseOutElements[0], eventTarget)) {
            me.mouseOutElements.forEach(element => me.triggerEvent(element, 'mouseout'));
            me.mouseOutElements.length = 0;
          }
        }

        me.prevTarget = eventTarget;
        me.triggerEvent(eventTarget, 'mouseover');
      }

      me.triggerEvent(eventTarget, step.action, {
        clientX,
        clientY
      });
    }, 50);
  } // target can be a string selector, a function or blank to use last target or outerElement if first time

  getTarget(step) {
    const me = this,
          target = step.target;

    if (!target) {
      return me.prevTarget || me.outerElement;
    }

    if (typeof target === 'function') {
      return target(step);
    }

    return document.querySelector(target);
  } // action can be a function, a string or extracted from a property by scanning for unknown names

  normalizeStep(step) {
    if (step.action) {
      if (typeof step.action === 'function') {
        return step.action(step);
      }

      return step;
    }

    if (typeof step === 'function') {
      step();
      return step;
    } // try to find action among properties

    for (const prop in step) {
      if (Object.hasOwnProperty.call(step, prop) && !knownProps.includes(prop)) {
        step.action = prop.toLowerCase();
        step.to = step[prop];
      }
    }

    if (!step.target && (typeof step.to === 'string' || typeof step.to === 'function')) step.target = step.to;
    return step;
  }

  get isScrolling() {
    const me = this,
          box = me.outerElement.getBoundingClientRect(),
          scrolled = me.lastTop && box.top !== me.lastTop;
    me.lastTop = box.top;
    return scrolled;
  }

  get isInView() {
    const box = this.outerElement.getBoundingClientRect();
    return box.top < window.innerHeight && box.bottom > 0;
  }

  get shouldPause() {
    return !this.isInView || this.isScrolling || document.hidden || !document.hasFocus();
  } // process the next step

  nextStep() {
    const me = this; // Only perform step if in view and not scrolling

    if (me.shouldPause) {
      return;
    }

    if (me.currentStep === me.steps.length) {
      if (me.repeat) {
        me.currentStep = 0;
      } else {
        return me.abort(true);
      }
    } // First step, signal to let demo initialize stuff

    if (me.currentStep === 0) {
      me.trigger('initialize');
    }

    const mouse = me.mouse,
          step = me.normalizeStep(me.steps[me.currentStep++]),
          target = me.getTarget(step),
          action = step.action;

    if (target && action) {
      mouse.className = 'simulated-mouse';

      if (action === 'mousemove') {
        me.handleMouseMove(step, target);
      } else {
        // First move mouse into position
        if (target !== me.prevTarget) {
          const rect = Rectangle.from(target, me.outerElement);
          mouse.style.left = rect.x + rect.width / 2 + 'px';
          mouse.style.top = rect.y + rect.height / 2 + 'px';
        }

        if (action === 'mousedown') {
          me.mouseDown = true;
        }

        if (action === 'mouseup') {
          me.mouseDown = false;
        } // Then trigger action

        me.timeoutId = me.setTimeout(() => {
          me.prevTarget = target; // Animate click etc.

          mouse.classList.add(action);

          if (action === 'type') {
            const field = Widget.fromElement(target),
                  parts = step.text.split('|');
            field.value = parts[parts.length === 1 || field.value != parts[0] ? 0 : 1];
          } else {
            me.triggerEvent(target, action);
          }
        }, action === 'type' ? 100 : 550);
      }
    }
  }

}
DemoBot._$name = 'DemoBot';

const hasOwnProperty$7 = Object.prototype.hasOwnProperty;
let cacheKey = null;

function setParser(me, parser) {
  Object.defineProperty(me, 'parser', {
    value: parser
  });
  return parser;
}

class Default {
  constructor(formatter) {
    this.formatter = formatter;
  }

  format(value) {
    return this.formatter.defaultFormat(value);
  }

  parse(value, strict) {
    return this.formatter.defaultParse(value, strict);
  }

  resolvedOptions() {
    return null;
  }

} // This class does not extend Core.Base because instances are not reconfigurable (making
// setConfig harmful) nor destroyable. Instead, they get frozen and cached according to
// their "config" definition.

/**
 * Abstract base class for formatters.
 * @private
 */

class Formatter {
  static get(format) {
    if (format == null) {
      return this.NULL;
    }

    const key = typeof format === 'string' ? format : JSON.stringify(format),
          cache = this.cache;
    let fmt = cache.get(key);

    if (!fmt) {
      cacheKey = key; // this is grabbed by our constructor below...

      fmt = new this(format);
      cache.set(key, fmt);
    }

    return fmt;
  }

  static get cache() {
    return hasOwnProperty$7.call(this, '_cache') && this._cache || (this._cache = new Map());
  }

  static get NULL() {
    return hasOwnProperty$7.call(this, '_null') ? this._null : this._null = new this(null);
  }

  constructor(config) {
    const me = this; // This is done in a funny way so as not to complicate the derived constructor's
    // desire to maintain a single argument signature, as well as it's calling of
    // Object.freeze() to ensure immutability in dev mode.

    me.cacheKey = cacheKey;
    cacheKey = null;
    me.initialize();

    if (config === null) {
      me.formatter = new Default(me);
    } else {
      me.configure(config); // Bring locale and other defaulted options back onto this object:

      for (const [key, value] of Object.entries(me.resolvedOptions())) {
        // For some reason (locale-specific perhaps), resolvedOptions returns
        // with 'undefined' in some keys (e.g., min/maximumFractionDigits) when
        // we specified 0.
        //
        // The second check is to only bring back values that we understand.
        if (value != null && key in me.defaults) {
          me[key] = value;
        }
      }
    }
  }

  get parser() {
    // Replace this property w/the actual instance:
    return setParser(this, new this.constructor.Parser(this));
  }

  defaultFormat(value) {
    return value == null ? value : String(value);
  }

  defaultParse(value) {
    return value;
  }

  format(value) {
    return value == null ? value : this.formatter.format(value);
  }

  parse(value, strict) {
    return value == null ? value : this.parser.parse(value, strict);
  }

  parseStrict(value) {
    return this.parse(value, true);
  }

  resolvedOptions() {
    return this.formatter.resolvedOptions();
  }

}
Formatter._$name = 'Formatter';

/**
 * @module Core/helper/util/Fullscreen
 */

/**
 * Encapsulates the functionality related to switching cross-browser to full screen view and back.
 */

class Fullscreen {
  static init() {
    const fnNames = ['fullscreenEnabled', 'requestFullscreen', 'exitFullscreen', 'fullscreenElement'],
          // turns fnNames into function calls to prefixed functions, fullscreenEnabled -> document.mozFullscreenEnabled
    prefixFn = prefix => fnNames.map(fn => {
      let result = prefix + StringHelper.capitalize(fn); // fullscreenEnabled in Firefox is called fullScreenEnabled

      if (prefix === 'moz') {
        result = result.replace('screen', 'Screen'); // #6555 - Crash when clicking full screen button twice
        // firefox doesn't support exitFullScreen method

        if ('mozCancelFullScreen' in document && fn === 'exitFullscreen') {
          result = 'mozCancelFullScreen';
        }
      }

      return result;
    });

    this.functions = 'fullscreenEnabled' in document && fnNames || 'webkitFullscreenEnabled' in document && prefixFn('webkit') || 'mozFullScreenEnabled' in document && prefixFn('moz') || 'msFullscreenEnabled' in document && prefixFn('ms') || [];

    const eventNames = ['fullscreenchange', 'fullscreenerror'],
          msEventNames = ['MSFullscreenChange', 'MSFullscreenError'],
          prefixEvt = prefix => eventNames.map(eventName => prefix + StringHelper.capitalize(eventName));

    this.events = 'fullscreenEnabled' in document && eventNames || 'webkitFullscreenEnabled' in document && prefixEvt('webkit') || 'mozFullscreenEnabled' in document && prefixEvt('moz') || 'msFullscreenEnabled' in document && msEventNames || [];
  }
  /**
   * True if the fullscreen mode is supported and enabled, false otherwise
   * @property {Boolean}
   */

  static get enabled() {
    return document[this.functions[0]];
  }
  /**
   * Request entering the fullscreen mode.
   * @param {HTMLElement} element Element to be displayed fullscreen
   */

  static request(element) {
    return element[this.functions[1]]();
  }
  /**
   * Exit the previously entered fullscreen mode.
   */

  static exit() {
    return document[this.functions[2]]();
  }
  /**
   * True if fullscreen mode is currently active, false otherwise
   * @property {Boolean}
   */

  static get isFullscreen() {
    return !!document[this.functions[3]];
  }
  /**
   * Installs the passed listener to fullscreenchange event
   * @param {Function} fn The listener to install
   */

  static onFullscreenChange(fn) {
    document.addEventListener(this.events[0], fn);
  }
  /**
   * Uninstalls the passed listener from fullscreenchange event
   * @param {Function} fn
   */

  static unFullscreenChange(fn) {
    document.removeEventListener(this.events[0], fn);
  }

}
Fullscreen.init();
Fullscreen._$name = 'Fullscreen';

/**
 * @module Core/helper/util/NumberFormat
 */

const escapeRegExp = StringHelper.escapeRegExp,
      digitsRe = /[\d+-]/g,
      // We cannot pass locale=null:
newFormatter = (locale, config) => new Intl.NumberFormat(locale || undefined, config),
      numFormatRe = /^(?:([$])\s*)?(?:(\d+)>)?\d+(,\d+)?(?:\.((\d*)(?:#*)|[*]))?(?:\s*([%])?)?$/;

class NumberParser {
  constructor(formatter) {
    const me = this,
          locale = formatter.locale,
          // We need a formatter for this locale that has decimals and grouping:
    numFmt = newFormatter(locale, {
      maximumFractionDigits: 3
    }),
          currency = formatter.is.currency ? me._decodeStyle(locale, {
      style: 'currency',
      currency: formatter.currency,
      currencyDisplay: formatter.currencyDisplay
    }) : null,
          percent = formatter.is.percent ? me._decodeStyle(locale, {
      style: 'percent'
    }) : null,
          decimal = numFmt.format(1.2).replace(digitsRe, '')[0],
          grouper = numFmt.format(1e9).replace(digitsRe, '')[0];
    Object.assign(me, {
      currency,
      decimal,
      formatter,
      grouper,
      percent
    });
    me.decimal = decimal;
    me.decimalRe = escapeRegExp(decimal, 'g');
    me.grouper = grouper; // The stripRe removes whitespace, currency text, percent text and grouping chars:

    me.stripRe = new RegExp(`(?:\\s+|${escapeRegExp(grouper)})` + (currency ? `|(?:${escapeRegExp(currency.text)})` : '') + (percent ? `|(?:${escapeRegExp(percent.text)})` : ''), 'g');
  }

  decimalPlaces(value) {
    value = value.replace(this.stripRe, '');
    const dot = value.indexOf(this.decimal) + 1;
    return dot && value.length - dot;
  }

  parse(value, strict) {
    if (typeof value === 'string') {
      value = value.replace(this.stripRe, '').replace(this.decimalRe, '.');
      value = strict ? Number(value) : parseFloat(value);

      if (this.formatter.is.percent) {
        value /= 100;
      }
    } // else, a number is already parsed but could be null...

    return value;
  }

  _decodeStyle(locale, fmtDef) {
    const fmt = newFormatter(locale, fmtDef),
          decFmt = newFormatter(locale, Object.assign(fmt.resolvedOptions(), {
      style: 'decimal'
    })),
          zero = fmt.format(0),
          // = '0%' or '$0.00' in en-US
    zeroDec = decFmt.format(0); // = '0' or '0.00' in en-US

    return {
      suffix: zero.startsWith(zeroDec),
      text: zero.replace(zeroDec, '').trim()
    };
  }

}
/**
 * This class is an enhancement to `Intl.NumberFormat` that has a more flexible
 * constructor as well as other features such as `parse()`.
 *
 * All constructor forms take a single argument. The most common is to pass a format
 * {@link #config-template} string:
 *```
 *  const formatter = new NumberFormat('9,999.99##');
 *```
 * The above is equivalent to:
 *```
 *  const formatter = new Intl.NumberFormat({
 *      useGrouping           : true,
 *      minimumFractionDigits : 2,
 *      maximumFractionDigits : 4
 *  });
 *```
 * The `formatter` created above is used as follows (in the `en-US` locale):
 *```
 *  console.log(formatter.format(12345.54321));
 *  console.log(formatter.format(42));
 *
 *  // 12,345.5432
 *  // 42.00
 *```
 * When a format template is insufficient, a config object can be provided, similar to
 * `Intl.NumberFormat`'s `options` parameter. While all options from `Intl.NumberFormat`
 * are valid properties for this class's config object, additional properties are
 * supported.
 *
 * For example:
 *```
 *  new NumberFormat({
 *      locale      : 'en-US',
 *      template    : '$9,999',
 *      currency    : 'USD',
 *      significant : 5
 *  });
 *```
 * The `locale` option takes the place of the first positional parameter to the
 * `Intl.NumberFormat` constructor. The `template` config is the same string that can be
 * passed by itself.
 *
 * The shorthand properties `fraction`, `integer`, and `significant` set the standard
 * options `minimumFractionDigits`, `maximumFractionDigits`, `minimumIntegerDigits`,
 * `minimumSignificantDigits`, and `maximumSignificantDigits`.
 *
 * NOTE: Instances of `NumberFormat` are immutable after construction.
 *
 * For details about `Intl.NumberFormat` see [MDN](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/NumberFormat).
 */

class NumberFormat extends Formatter {
  static get $name() {
    return 'NumberFormat';
  }

  initialize() {
    this._as = {// cacheKey : cachedInstance
    };
    this.is = {
      decimal: false,
      currency: false,
      percent: false,
      null: true,
      from: null
    };
  }

  get truncator() {
    const scale = this.maximumFractionDigits;
    return scale == null ? null : this.as({
      style: 'decimal',
      maximumFractionDigits: Math.min(20, scale + 1)
    }, 'truncator');
  }

  configure(options) {
    const me = this;

    if (typeof options !== 'string') {
      Object.assign(me, options);
    } else {
      me.template = options;
    } // Do not remove. Assertion strings for Localization sanity check.
    // 'L{locale}'
    // 'L{currency}'

    const config = {},
          loc = me.locale ? LocaleManagerSingleton.locales[me.locale] : LocaleManagerSingleton.locale,
          defaults = loc && loc.NumberFormat,
          template = me.template;

    if (defaults) {
      for (const key in defaults) {
        if (me[key] == null && typeof defaults[key] !== 'function') {
          me[key] = defaults[key];
        }
      }
    }

    if (template) {
      const match = numFormatRe.exec(template),
            m2 = match[2],
            m4 = match[4];
      me.useGrouping = !!match[3];
      me.style = match[1] ? 'currency' : match[6] ? 'percent' : 'decimal';

      if (m2) {
        me.integer = +m2;
      }

      if (m4 === '*') {
        me.fraction = [0, 20];
      } else if (m4 != null) {
        me.fraction = [match[5].length, m4.length];
      }
    }

    me._minMax('fraction', true, true);

    me._minMax('integer', true, false);

    me._minMax('significant', false, true);

    for (const key in me.defaults) {
      if (me[key] != null) {
        config[key] = me[key];
      }
    }

    me.is.from = me.from && me.from.is;
    me.is[me.style] = !(me.is.null = false);
    me.formatter = newFormatter(me.locale, config);
  }
  /**
   * Creates a derived `NumberFormat` from this instance, with a different `style`. This is useful for processing
   * currency and percentage styles without the symbols being injected in the formatting.
   *
   * @param {String|Object} change The new style (if a string) or a set of properties to update.
   * @param {String} [cacheAs] A key by which to cache this derived formatter.
   * @returns {Core.helper.util.NumberFormat}
   */

  as(change, cacheAs = null) {
    const config = this.resolvedOptions() || {
      template: '9.*'
    },
          cache = this._as;
    let ret = cacheAs && cache[cacheAs];

    if (!ret) {
      if (typeof change === 'string') {
        config.style = change;
      } else {
        Object.assign(config, change);
      }

      config.from = this;
      ret = new NumberFormat(config);
    }

    if (cacheAs) {
      cache[cacheAs] = ret;
    }

    return ret;
  }

  defaultParse(value, strict) {
    return value == null ? value : strict ? Number(value) : parseFloat(value);
  }
  /**
   * Returns the given `value` formatted in accordance with the specified locale and
   * formatting options.
   *
   * @param {Number} value
   * @returns {String}
   */

  format(value) {
    if (typeof value === 'string') {
      const v = Number(value);
      value = isNaN(v) ? this.parse(value) : v;
    }

    return super.format(value);
  } // The parse() method is inherited but the base class implementation
  // cannot properly document the parameter and return types:

  /**
   * Returns a `Number` parsed from the given, formatted `value`, in accordance with the
   * specified locale and formatting options.
   *
   * If the `value` cannot be parsed, `NaN` is returned.
   *
   * Pass `strict` as `true` to require all text to convert. In essence, the default is
   * in line with JavaScript's `parseFloat` while `strict=true` behaves like the `Number`
   * constructor:
   *```
   *  parseFloat('1.2xx');  // = 1.2
   *  Number('1.2xx')       // = NaN
   *```
   * @method parse
   * @param {String} value
   * @param {Boolean} [strict=false]
   * @returns {Number}
   */

  /**
   * Returns a `Number` parsed from the given, formatted `value`, in accordance with the
   * specified locale and formatting options.
   *
   * If the `value` cannot be parsed, `NaN` is returned.
   *
   * This method simply passes the `value` to `parse()` and passes `true` for the second
   * argument.
   *
   * @method parseStrict
   * @param {String} value
   * @returns {Number}
   */

  /**
   * Returns the given `Number` rounded in accordance with the specified locale and
   * formatting options.
   *
   * @param {Number|String} value
   * @returns {Number}
   */

  round(value) {
    return this.parse(this.format(value));
  }
  /**
   * Returns the given `Number` truncated to the `maximumFractionDigits` in accordance
   * with the specified locale and formatting options.
   *
   * @param {Number|String} value
   * @returns {Number}
   */

  truncate(value) {
    const me = this,
          scale = me.maximumFractionDigits,
          {
      truncator
    } = me;
    let v = me.parse(value),
        dot;

    if (truncator) {
      v = truncator.format(v);
      dot = v.indexOf(truncator.parser.decimal);

      if (dot > -1 && v.length - dot - 1 > scale) {
        v = v.substr(0, dot + scale + 1);
      }

      v = truncator.parse(v);
    }

    return v;
  }

  resolvedOptions() {
    const options = super.resolvedOptions();

    for (const key in options) {
      // For some reason, on TeamCity, tests get undefined for some properties...
      // maybe a locale issue?
      if (options[key] === undefined) {
        options[key] = this[key];
      }
    }

    return options;
  }
  /**
   * Expands the provided shorthand into the "minimum*Digits" and "maximum*Digits".
   * @param {String} name
   * @param {Boolean} setMin
   * @param {Boolean} setMax
   * @private
   */

  _minMax(name, setMin, setMax) {
    const me = this,
          value = me[name];

    if (value != null) {
      const capName = StringHelper.capitalize(name),
            max = `maximum${capName}Digits`,
            min = `minimum${capName}Digits`;

      if (typeof value === 'number') {
        if (setMin) {
          me[min] = value;
        }

        if (setMax) {
          me[max] = value;
        }
      } else {
        me[min] = value[0];
        me[max] = value[1];
      }
    }
  }

}
NumberFormat.Parser = NumberParser;
Object.assign(NumberFormat.prototype, {
  // This object holds only those properties that Intl.NumberFormat accepts in its
  // "options" parameter. Only these options will be copied from the NumberFormat
  // and passed to the Intl.NumberFormat constructor and only these will be copied
  // back from its resolvedOptions:
  defaults: {
    /**
     * The formatting style.
     *
     * Valid values are: `'decimal'` (the default), `'currency'`, and `'percent'`.
     * @config {String}
     * @default
     */
    style: 'decimal',

    /**
     * The currency to use when using `style: 'currency'`. For example, `'USD'` (US dollar)
     * or `'EUR'` for the euro.
     *
     * If not provided, the {@link Core.localization.LocaleManager} default will be used.
     * @config {Boolean}
     */
    currency: null,

    /**
     * The format in which to display the currency value when using `style: 'currency'`.
     *
     * Valid values are: `'symbol'` (the default), `'code'`, and `'name'`.
     * @config {String}
     * @default
     */
    currencyDisplay: 'symbol',

    /**
     * The name of the locale. For example, `'en-US'`. This config is the same as the
     * first argument to the `Intl.NumberFormat` constructor.
     *
     * Defaults to the browser's default locale.
     * @config {String}
     */
    locale: null,

    /**
     * The maximum number of digits following the decimal.
     *
     * This is more convenient to specify using the {@link #config-fraction} config.
     * @config {Number}
     */
    maximumFractionDigits: null,

    /**
     * The minimum number of digits following the decimal.
     *
     * This is more convenient to specify using the {@link #config-fraction} config.
     * @config {Number}
     */
    minimumFractionDigits: null,

    /**
     * The minimum number of digits preceding the decimal.
     *
     * This is more convenient to specify using the {@link #config-integer} config.
     * @config {Number}
     */
    minimumIntegerDigits: null,

    /**
     * The maximum number of significant digits.
     *
     * This is more convenient to specify using the {@link #config-significant} config.
     * @config {Number}
     */
    maximumSignificantDigits: null,

    /**
     * The minimum number of significant digits.
     *
     * This is more convenient to specify using the {@link #config-significant} config.
     * @config {Number}
     */
    minimumSignificantDigits: null,

    /**
     * Specify `false` to disable thousands separators.
     * @config {Boolean}
     * @default
     */
    useGrouping: true
  },

  /**
   * Specifies the `minimumFractionDigits` and `minimumFractionDigits` in a compact
   * way. If this value is a `Number`, it sets both the minimum and maximum to that
   * value. If this value is an array, `[0]` sets the minimum and `[1]` sets the
   * maximum.
   * @config {Number|Number[]}
   */
  fraction: null,
  from: null,

  /**
   * An alias for `minimumIntegerDigits`.
   * @config {Number}
   */
  integer: null,

  /**
   * Specifies the `minimumSignificantDigits` and `minimumSignificantDigits` in a compact
   * format. If this value is a `Number`, it sets only the maximum to that value. If this
   * value is an array, `[0]` sets the minimum and `[1]` sets the maximum.
   *
   * If this value (or `minimumSignificantDigits` or `minimumSignificantDigits`) is set,
   * `integer` (and `minimumIntegerDigits`) and `fraction` (and `minimumFractionDigits`
   * and `minimumFractionDigits`) are ignored.
   *
   * @config {Number|Number[]}
   */
  significant: null,

  /**
   * A format template consisting of the following parts:
   *```
   *  [$] [\d+:] \d+ [,\d+] [.\d* [#*] | *] [%]
   *```
   * If the template begins with a `'$'`, the formatter's `style` option is set to
   * `'currency'`. If the template ends with `'%'`, `style` is set to `'percent'`.
   * It is invalid to include both characters. When using `'$'`, the `currency` symbol
   * defaults to what is provided by the {@link Core.localization.LocaleManager}.
   *
   * To set the `minimumIntegerDigits`, the desired minimum comes before the first
   * digits in the template and is followed by a `'>'` (greater-than). For example:
   *```
   *  5>9,999.00
   *```
   * The above sets `minimumIntegerDigits` to 5.
   *
   * The `useGrouping` option is enabled if there is a `','` (comma) present and is
   * disabled otherwise.
   *
   * If there is a `'.'` (decimal) present, it may be followed by either of:
   *
   *  - Zero or more digits which may then be followed by zero or more `'#'` characters.
   *    The number of digits determines the `minimumFractionDigits`, while the total
   *    number of digits and `'#'`s determines the `maximumFractionDigits`.
   *  - A single `'*'` (asterisk) indicating any number of fractional digits (no minimum
   *    or maximum).
   *
   * @config {String}
   */
  template: null
});
Object.assign(NumberFormat.prototype, NumberFormat.prototype.defaults); // TODO is this the "right" way to do this?

Formatter.number = (format, value) => NumberFormat.format(format, value);

NumberFormat._$name = 'NumberFormat';

/**
 * @module Core/mixin/Finalizable
 */

/**
 * This mixin provides an asynchronous completion mechanism. This allows a process to coordinate its async actions
 * (such as Ajax requests or user interaction) with cleanup.
 *
 * Consider a context tracking helper class, for example:
 * ```
 *  class Context extends Base.mixin(Finalizable) {
 *      // ...
 *
 *      async finish() {
 *          this.owner.trigger('finish', {
 *              context : this
 *          });
 *
 *          // Wait for any scheduled finalizer to run...
 *          await this.finalize();
 *      }
 *
 *      doFinalize() {
 *          this.destroy();
 *      }
 *  }
 * ```
 *
 * When the `finish` event is processed, the receiver can register a promise for whatever processing it would like to
 * perform:
 * ```
 *  class Foo {
 *      onFinish({ context }) {
 *          context.finalizer = this.askUser(context);
 *      }
 *
 *      async askUser(context) {
 *          //
 *      }
 *  }
 * ```
 * @mixin
 * @internal
 */

var Finalizable = (Target => class Finalizable extends (Target || Base) {
  static get $name() {
    return 'Finalizable';
  }

  construct(...args) {
    super.construct(...args);
    /**
     * This property can be set any time prior to calling {@link #function-finalize} (i.e., when the
     * {@link #property-isFinalizing} property goes to `true`). When set, this instance will `await` this
     * promise before completing the finalization process by calling {@link #function-doFinalize}.
     * @member {Promise} finalizer
     */

    this.finalizer = null;
    /**
     * This property holds the `Promise` that will resolve when {@link #function-finalize} has completed. It is
     * set when {@link #function-finalize} is called and cleared on return.
     * @member {Boolean} finalizing
     * @readonly
     * @private
     */

    this.finalizing = null;
    /**
     * This property is `true` once the instance completes the {@link #function-finalize} method.
     * @member {Boolean} isFinalized
     * @readonly
     */

    this.isFinalized = false;
    /**
     * This property is set to `true` when {@link #function-finalize} is called.
     * @member {Boolean} isFinalizing
     * @readonly
     */

    this.isFinalizing = false;
  }
  /**
   * This template method is called at the end of {@link #function-finalize}. By default it calls `destroy()`, but
   * can be replaced by the derived class. This can be useful if it is not the `Finalizable` instance that awaits
   * the {@link #function-finalize} method.
   */

  doFinalize() {
    this.destroy();
  }
  /**
   * This method is called (typically by this instance or its owner) to cleanup this instance while possibly first
   * waiting for the {@link #property-finalizer} promise to settle. Once settled, the {@link #function-doFinalize}
   * method is called.
   * @async
   */

  finalize() {
    const me = this;
    let ret = me.finalizing;

    if (!ret && !me.isFinalized) {
      me.isFinalizing = true; // note: we never clear this flag (use isFinalized to know if we're done)
      // If we are called during finalization, we want to return the same promise, so we cache it away.

      me.finalizing = ret = me._awaitFinalizer();
    }

    return ret;
  }

  async _awaitFinalizer() {
    const me = this;

    try {
      await me.finalizer;
    } finally {
      // be sure we clean up even if an exception is thrown by the finalizer...
      me.finalizing = null;
      me.isFinalized = true;
      me.doFinalize();
    }
  }

});

/**
 * @module Core/util/drag/DragContext
 */

const ABORTED = Symbol('dragAbort'),
      // Drag has been aborted
INIT = Symbol('dragInit'),
      // Button is down but insufficient movement to start the drag
DRAGGING = Symbol('dragDrag'),
      // Button is down and movement has started a drag
DROPPED = Symbol('dragDrop'),
      // Button has been released and drop has occurred
lockDirections = {
  x: 'horizontal',
  y: 'vertical'
};
/**
 * This class is created during drag operations of {@link Core/mixin/Draggable}. It holds the state of an ongoing drag
 * operation.
 * @extends Core/Base
 * @internal
 */

class DragContext extends Base.mixin(Finalizable, Delayable) {
  static get configurable() {
    return {
      /**
       * The element that will have the {@link Core.mixin.Draggable#property-draggingItemCls}. This element is
       * determined by the {@link Core.mixin.Draggable#config-dragItemSelector}.
       * @config {HTMLElement}
       */
      itemElement: null,

      /**
       * The `ScrollManager` instance to use for scrolling while dragging.
       * @config {Grid.util.ScrollManager}
       * @private
       */
      scrollManager: null,

      /**
       * The source of the drag operation.
       * @config {Core.mixin.Draggable}
       * @default
       * @readonly
       */
      source: null,

      /**
       * The current target element of the drag.
       * @member {Core.mixin.Droppable}
       * @readonly
       */
      target: null,

      /**
       * The current target element of the drag.
       * @member {HTMLElement}
       * @private
       */
      targetElement: null,

      /**
       * The minimum distance from the touchstart/mousedown/pointerdown that must be moved to actually start a
       * drag operation.
       * @config {Number}
       * @default
       * @readonly
       */
      threshold: 5,

      /**
       * The minimum amount of time a touch must be maintained before it will initiate a drag. Movement prior to
       * this time will cancel the drag in order to allow touch scrolling.
       * @config {Number}
       * @default
       */
      touchStartDelay: 300
    };
  }
  /**
   * The current DOM event being processed.
   * @member {Event} event
   * @readonly
   */

  construct(...args) {
    super.construct(...args);
    const me = this,
          {
      event
    } = me;
    Object.assign(me, {
      /**
       * This property holds the `altKey` state of the most recent event.
       * @member {Boolean}
       */
      altKey: null,

      /**
       * An array of functions to call when cleaning up the context instance.
       * @member {Function[]}
       * @private
       */
      cleaners: [],

      /**
       * This property holds the `ctrlKey` state of the most recent event.
       * @member {Boolean}
       */
      ctrlKey: null,

      /**
       * Container for data associated with the drag. Data items are added by the {@link Core.mixin.Draggable}
       * when the drag starts.
       * @member {Map}
       * @private
       */
      data: new Map(),

      /**
       * The element from which the drag operation started.
       * @member {HTMLElement}
       * @readonly
       */
      element: event.target,

      /**
       * The event that completed the drag (a `mouseup`, `pointerup` or `touchend`).
       * @member {Event}
       * @readonly
       */
      endEvent: null,

      /**
       * The most recent `mousemove`, `pointermove` or `touchmove` event.
       * @member {Event}
       * @private
       */
      lastMoveEvent: null,

      /**
       * This property holds the `metaKey` state of the most recent event.
       * @member {Boolean}
       */
      metaKey: null,

      /**
       * The scroll actions reported by the {@link #config-scrollManager}.
       * @member {Object}
       * @private
       */
      scrollerAction: null,

      /**
       * This property holds the `shiftKey` state of the most recent event.
       * @member {Boolean}
       */
      shiftKey: null,

      /**
       * This property holds the current state of the drag process.
       *
       * This will be one of the following values:
       *
       *  - `DragContext.STATE.ABORTED` - The drag has been aborted.
       *  - `DragContext.STATE.INIT` - The button is down but there is insufficient movement to start the drag.
       *  - `DragContext.STATE.DRAGGING` - The button is down and movement has started the drag.
       *  - `DragContext.STATE.DROPPED` - The button has been released and drop has occurred.
       *
       * @member {Symbol}
       * @readonly
       * @internal
       */
      state: INIT,

      /**
       * The event that started the drag operation.
       * @member {Event}
       * @readonly
       */
      startEvent: event,

      /**
       * The timer that fires when a touch pointermove is allowed to start the drag. A touch pointermove event
       * prior to this will `abort()` the drag to allow touch scrolling.
       * @member {Number}
       * @private
       */
      touchStartTimer: null,

      /**
       * Stores the value from writes to the {@link #property-valid} property.
       * @member {Boolean}
       * @private
       */
      _valid: true
    });

    if ('touches' in event) {
      me.touchStartTimer = me.setTimeout(() => me.touchStartTimer = null, me.touchStartDelay, 'touchStartDelay');
    }
  }

  doDestroy() {
    const me = this,
          {
      source,
      target
    } = me;
    me.cleanup();

    if ((target === null || target === void 0 ? void 0 : target.dropping) === me) {
      target.dropping = null;
    }

    if ((source === null || source === void 0 ? void 0 : source.dragging) === me) {
      source.dragging = null;
    }

    super.doDestroy();
  }
  /**
   * This property is `true` if the {@link #function-abort} method was called and `false` otherwise. This
   * is typically because the user pressed the ESC key, however, a drag can be aborted for other reasons.
   * @property {Boolean}
   * @readonly
   */

  get aborted() {
    return this.state === ABORTED;
  }
  /**
   * Returns `true` if the drag has completed either by mouse/pointerup or the {@link #function-abort} method.
   * @property {Boolean}
   * @readonly
   */

  get completed() {
    return this.aborted || this.endEvent !== null;
  }
  /**
   * This property is set to `true` if the drag {@link #config-threshold} is reached and the drag operation
   * becomes active.
   * @property {Boolean}
   * @readonly
   */

  get started() {
    return this.state !== INIT && !this.aborted;
  }
  /**
   * This property is `true` when the drag is in a valid drop state. This can be set to `false` to indicate the drop
   * is invalid. Setting to `true` does not ensure that the property will be `true` when next read due to other factors
   * that are required to make the drop valid. For example, setting `valid = true` will still return `false` if called
   * before the drag {@link #config-threshold} has not been reached or if the {@link #function-abort} method has been
   * called.
   * @property {Boolean}
   */

  get valid() {
    return this.started && this.targetElement != null && this._valid;
  }

  set valid(v) {
    this._valid = v;
  } //region Data Access

  /**
   * Retrieves a data item from the drag source. This method can only be called after the drag has completed.
   * @param {String|String[]} name The name of the data item.
   * @returns {*}
   * @async
   */

  async get(name) {
    if (this.aborted) {
      throw new Error('Data is not available on aborted drag');
    }

    if (!this.completed) {
      throw new Error('Data is not available until drag completion');
    }

    if (Array.isArray(name)) {
      return Promise.all(name.map(s => this.get(s)));
    }

    let value = this.data.get(name);

    if (typeof value === 'function') {
      value = await value();
      this.data.set(name, value);
    }

    return value;
  }
  /**
   * Returns `true` if the named data item is present.
   * @param {String} name The name of the data item.
   * @returns {Boolean}
   */

  has(name) {
    return this.data.has(name);
  }
  /**
   * Retrieves a data item from the drag source if it is available. This will return `true` for an item that was
   * {@link #function-set} using a renderer function.
   * @param {String|String[]} name The name of the data item.
   * @returns {*}
   */

  peek(name) {
    if (this.aborted) {
      throw new Error('Data is not available on aborted drag');
    }

    if (Array.isArray(name)) {
      return name.map(s => this.peek(s));
    }

    let value = this.data.get(name);

    if (typeof value === 'function') {
      value = true;
    }

    return value;
  }
  /**
   * Sets a data item for the drag. If a function is passed, it is called to render the data only if that data is
   * actually requested via the {@link #function-get} method. A data renderer function can be `async`.
   * @param {String} name The name of the data item.
   * @param {*} value The value of the data item.
   */

  set(name, value) {
    this.data.set(name, value);
  } //endregion
  //region Configs

  changeTarget(target, was) {
    if (target !== was) {
      const me = this;
      me._target = target;

      if (was) {
        was.dropping = null;
      }

      if (target) {
        target.dropping = me; // calls dragEnter/Leave on the target

        if (target.dropping !== me) {
          // if (target did not accept us)
          target = null;
          me.valid = false;
        }
      }

      me._target = was;
    }

    return target;
  }

  updateTarget(target, was) {
    const me = this;
    was && me.source.dragLeaveTarget(me, was);

    if (target) {
      me.valid = true; // Always give the target an initial dragMove since feedback/indicators will need to update on move and
      // the lack of a move on entry will just make that a special case.

      target.dragMove(me);
      me.source.dragEnterTarget(me);
    }
  }

  updateTargetElement(targetElement) {
    const target = targetElement === null || targetElement === void 0 ? void 0 : targetElement.closest('.b-droppable'); // The selected drop target must *contain* the event target element.
    // Otherwise we can get a hit on borders,

    if (target && target.compareDocumentPosition(targetElement) & Node.DOCUMENT_POSITION_CONTAINED_BY) {
      // The following runs updateTarget and informs the target:
      this.target = DomDataStore.get(target, 'droppable') || null;
    } // Must trigger a dragleave if target is now null
    else {
        this.target = null;
      }
  } //endregion
  //region Operations

  /**
   * Aborts the drag. After calling this method, {@link #property-aborted} will be `true`, {@link #property-valid}
   * will be `false` and {@link #property-completed} will be `true`.
   */

  abort() {
    const me = this,
          {
      element,
      source
    } = me; // Force a synchronous layout so that transitions from this point will work.

    element === null || element === void 0 ? void 0 : element.getBoundingClientRect();

    if (me.state !== DROPPED) {
      me.state = ABORTED;
      me.cleanup();
    }

    source === null || source === void 0 ? void 0 : source.endDrag(me);
  }

  begin() {
    const me = this,
          {
      source
    } = me;

    if (source.beforeDrag(me) !== false) {
      source.dragging = me;
    }
  }

  cleanup() {
    let cleaner;

    while (cleaner = this.cleaners.pop()) {
      cleaner();
    }
  }

  end(event) {
    const me = this,
          {
      lastMoveEvent: lastEvent,
      target
    } = me,
          {
      dragSwallowClickTime
    } = me.source;
    me.event = me.endEvent = event;
    me.syncFlags();

    if (me.started) {
      if ((lastEvent === null || lastEvent === void 0 ? void 0 : lastEvent.clientX) !== event.clientX || (lastEvent === null || lastEvent === void 0 ? void 0 : lastEvent.clientY) !== event.clientY || (lastEvent === null || lastEvent === void 0 ? void 0 : lastEvent.target) !== event.target) {
        // Make sure the drop event is preceded by a move to the final position...
        me.track();
      }

      if (dragSwallowClickTime) {
        // Prevent the impending document click from the mouseup event from propagating
        // into a click on our element.
        EventHelper.on({
          element: document,
          capture: true,
          expires: dragSwallowClickTime,
          // In case a click did not ensue, remove the listener
          once: true,

          click(event) {
            event.stopPropagation();
          }

        });
      }

      me.state = DROPPED;

      if (target !== me.source) {
        // If we are the target and not also the source, we need to call dropDrop() now. If we are
        // the source, dragDrop() will be called by Draggable.
        target === null || target === void 0 ? void 0 : target.dragDrop(me);
      }
    }
  }

  fakeKey(event, down) {
    const me = this,
          {
      lastMoveEvent
    } = me;

    if (lastMoveEvent && me.element) {
      let changed; // Indicate that this is a 'fake' mousemove event as a result of the keydown

      lastMoveEvent.isKey = true;

      if (event.key === 'Alt') {
        if (me.altKey !== down) {
          me.altKey = down;
          changed = true;
        }
      } else if (event.key === 'Control') {
        if (me.ctrlKey !== down) {
          me.ctrlKey = down;
          changed = true;
        }
      }

      if (changed) {
        me.event = lastMoveEvent;
        me.track();
      }
    }
  }

  keyDown(event) {
    if (!this.completed) {
      if (event.key === 'Escape') {
        this.abort();
      } else if (this.isDragToggleKey(event.key)) {
        this.fakeKey(event, true);
      }
    }
  }

  keyUp(event) {
    if (!this.completed && this.isDragToggleKey(event.key)) {
      this.fakeKey(event, false);
    }
  }

  getDistance(event) {
    return EventHelper.getDistanceBetween(this.startEvent, event);
  }

  isDragToggleKey(key) {
    return key === 'Control' || key === 'Alt';
  }

  move(event) {
    const me = this,
          {
      target
    } = event,
          distance = me.getDistance(event),
          significant = distance >= me.threshold;
    me.syncFlags();

    if (me.touchStartTimer) {
      if (significant) {
        me.abort();
      }

      return;
    }

    if (target && target.nodeType === Node.ELEMENT_NODE) {
      if (significant && !me.started) {
        me.event = event; // triggers beforeDragStart, dragStart. returning false from beforeDragStart aborts drag

        if (me.start() === false) {
          me.abort();
          return;
        }
      }

      if (me.started) {
        me.lastMoveEvent = me.event = event; // to prevent view drag (scroll) on ipad

        if (event.type === 'touchmove') {
          event.preventDefault();
          event.stopImmediatePropagation();
        }

        me.track();
      }
    }
  }

  start() {
    const me = this,
          {
      scrollManager,
      source
    } = me,
          {
      draggingBodyCls: activeCls,
      dragLock
    } = source,
          element =
    /* source.dragWithin || */
    source.dragRootElement;
    me.state = DRAGGING; // Now that the drag drop is confirmed to be starting, activate the configured scrollManager if present

    if (scrollManager) {
      scrollManager.startMonitoring({
        thisObj: source,
        direction: lockDirections[dragLock] || dragLock || 'both',
        element,

        callback(scrollerAction) {
          const {
            lastMoveEvent
          } = me;

          if (lastMoveEvent && me.element) {
            // Indicate that this is a 'fake' mousemove event as a result of the scrolling
            lastMoveEvent.isScroll = true;
            me.event = lastMoveEvent;
            me.scrollerAction = scrollerAction;
            me.track();
            me.scrollerAction = null;
          }
        }

      });
      me.cleaners.push(() => scrollManager.stopMonitoring(element));
    } // Global informational class for when DragHelper is dragging

    document.body.classList.add(activeCls);
    me.cleaners.push(() => document.body.classList.remove(activeCls));

    if (source.startDrag(me) === false) {
      me.cleanup();
      return false;
    }
  }

  syncFlags() {
    const me = this,
          {
      event
    } = me;
    me.altKey = event.altKey;
    me.ctrlKey = event.ctrlKey;
    me.metaKey = event.metaKey;
    me.shiftKey = event.shiftKey;
  }

  track() {
    const me = this,
          {
      event,
      source,
      target
    } = me;
    let targetElement = event.target,
        touch; // NOTE: we cannot syncFlags here since we are called to pass along keydown events as movement updates
    // "pointer-events:none" touchmove has no effect for the touchmove event target, meaning we cannot know
    // what's under the cursor as easily in touch devices

    if (event.type === 'touchmove') {
      touch = event.changedTouches[0]; // From MDN:
      //  * clientX/Y - the coordinates of the touch point relative to the left/top edge of the browser viewport,
      //    not including any scroll offset.
      //  * The elementFromPoint() method returns the topmost Element at the specified coordinates (relative to
      //    the viewport).

      targetElement = DomHelper.elementFromPoint(touch.clientX, touch.clientY);
    }

    me.targetElement = targetElement; // when we change elements, update me.target

    if (target === me.target) {
      // if we have a target and that did not change due to setting targetElement, we need to inform the target
      // of the drag move
      target === null || target === void 0 ? void 0 : target.dragMove(me);
    }

    source.trackDrag(me);
  } //endregion

}
DragContext.prototype.STATE = DragContext.STATE = Object.freeze({
  ABORTED,
  INIT,
  DRAGGING,
  DROPPED
});
DragContext._$name = 'DragContext';

/**
 * @module Core/util/drag/DragProxy
 */

/**
 * Drag proxies are helper classes that represent the object being dragged in some visual way. This is an abstract
 * base with which particular drag proxy classes (such as, {@link Core.util.drag.DragTipProxy} are registered.
 *
 * Derived classes the various template methods of this class to manage their particular form of visual feedback.
 * @extends Core/Base
 * @internal
 */

class DragProxy extends Base.mixin(Factoryable) {
  static get type() {
    return 'default';
  }

  static get configurable() {
    return {
      /**
       * The currently active `DragContext`. This context will be active prior to be passed to the proxy. This
       * config is set by {@link #function-dragStart} and cleared by {@link #function-dragEnd}.
       * @config {Core.util.drag.DragContext}
       */
      dragging: null
    };
  }

  static get factoryable() {
    return {
      defaultType: DragProxy
    };
  }
  /**
   * The `Draggable` instance that owns this drag proxy.
   * @member {Core.mixin.Draggable} owner
   * @readonly
   */
  //region Configs

  updateDragging(drag, was) {
    if (was) {
      this.close(was);
    }

    if (drag) {
      this.open(drag);
    }
  } //endregion
  //region Operations

  /**
   * This template method is called when {@link #config-dragging} is reset to `null`.
   * @param {Core.util.drag.DragContext} drag The drag instance.
   */

  close(drag) {// template
  }
  /**
   * This template method is called when {@link #config-dragging} is set to a non-`null` value.
   * @param {Core.util.drag.DragContext} drag The drag instance.
   */

  open(drag) {// template
  } //endregion
  //region Drag Processing

  /**
   * This template method is called by the `Draggable` instance when the drag officially starts.
    * This sets the {@link #config-dragging} config to `drag`, which triggers the call to {@link #function-open}.
   * @param {Core.util.drag.DragContext} drag The drag instance.
   */

  dragStart(drag) {
    this.dragging = drag;
  }
  /**
   * This template method is called by the `Draggable` instance as drag movement occurs.
   * @param {Core.util.drag.DragContext} drag The drag instance.
   */

  dragMove(drag) {// template
  }
  /**
   * This template method is called by the `Draggable` instance when the drag completes.
   *
   * This sets the {@link #config-dragging} config to `null`, which triggers the call to {@link #function-close}.
   * @param {Core.util.drag.DragContext} drag The drag instance.
   */

  dragEnd(drag) {
    this.dragging = null;
  } //endregion

}
DragProxy.initClass();
DragProxy._$name = 'DragProxy';

/**
 * @module Core/mixin/Draggable
 */

/**
 * Mix this into another class to enable drag/drop support.
 *
 * To use a draggable, it must be associated with an element that contains draggable content:
 *
 * ```javascript
 *  let draggable = new MyDraggable({
 *      dragRootElement : someElement
 *  });
 * ```
 *
 * Once the `dragRootElement` is assigned, any element inside that root is a candidate for dragging. To limit the
 * allowed element, set the {@link #config-dragSelector} config.
 *
 * ```javascript
 *  let draggable = new MyDraggable({
 *      dragRootElement : someElement,
 *      dragSelector    : '.drag-this'
 *  });
 * ```
 * @mixin
 * @internal
 */

var Draggable = (Target => class Draggable extends (Target || Base) {
  static get $name() {
    return 'Draggable';
  } //region Configs

  static get configurable() {
    return {
      /**
       * The current `DragContext`. This is created immediately on pointerdown but does not become active until
       * some movement occurs. This {@link #config-dragThreshold threshold} is configurable.
       * @member {Core.util.drag.DragContext}
       * @readonly
       */
      dragging: null,

      /**
       * A CSS selector to use to ascend from the {@link #config-dragRootElement} to find the element that will
       * gain the {@link #property-draggingCls} and {@link #property-draggingStartedCls} CSS classes.
       * @config {String}
       */
      draggingClsSelector: null,

      /**
       * The listeners to add to the `document` during a drag.
       * @config {Object}
       * @private
       */
      dragDocumentListeners: {
        element: document,
        keydown: 'onDragKeyDown',
        keyup: 'onDragKeyUp',
        // On mobile, a long-press will (sometimes) trigger a context menu, so we suppress it:
        contextmenu: 'onDragContextMenu',
        // We don't use pointermove/up because they get snared in the "touch-action" vs "pan-x/y" trap and we
        // cannot prevent panning (aka scrolling) in response to move events if we go that way:
        mousemove: 'onDragPointerMove',
        mouseup: 'onDragPointerUp',
        // Touch desktops don't fire touchend event when touch has ended, instead pointerup is fired. iOS does
        // fire touchend:
        pointerup: 'onDragPointerUp',
        touchend: 'onDragPointerUp',
        touchmove: {
          handler: 'onDragPointerMove',
          passive: false // We need to be able to preventDefault on the touchmove

        }
      },

      /**
       * A CSS selector to use to ascend from the drag element to find the element that will gain the
       * {@link #property-draggingItemCls} CSS class. If not supplied, the drag element will gain this CSS
       * class.
       * @config {String}
       */
      dragItemSelector: null,

      /**
       * Configure as `'x'` to lock dragging to the `X` axis (the drag will only move horizontally) or `'y'`
       * to lock dragging to the `Y` axis (the drag will only move vertically).
       * @config {String}
       */
      dragLock: null,

      /**
       * The minimum distance a drag must move to be considered a drop and not
       * {@link Core.util.drag.DragContext#property-aborted aborted}.
       * @config {Number}
       * @default
       */
      dragMinDistance: 1,

      /**
       * The {@link Core.util.drag.DragProxy drag proxy} is a helper object that can be used to display feedback
       * during a drag.
       * @config {Object|Core.util.drag.DragProxy}
       */
      dragProxy: {
        $config: ['lazy', 'nullify'],
        value: null
      },

      /**
       * The outer element where dragging will operate (attach events to it and use as root limit when looking
       * for ancestors).
       * @config {HTMLElement}
       */
      dragRootElement: {
        $config: 'nullify',
        value: null
      },

      /**
       * Set to `true` to allow a drag to drop on to the same element from which the drag started.
       * @config {Boolean}
       * @default
       */
      dragSameTargetDrop: false,

      /**
       * A CSS selector used to determine which element(s) can be dragged.
       * @config {String}
       * @default
       */
      dragSelector: null,

      /**
       * The number of milliseconds after a pointerup to ignore click events on the document. This
       * is used to avoid the "up" event itself generating a `click` on the target.
       * @config {Number}
       * @default
       */
      dragSwallowClickTime: 50,

      /**
       * The amount of pixels to move pointer/mouse before it counts as a drag operation.
       * @config {Number}
       * @default
       */
      dragThreshold: 5,

      /**
       * The number of milliseconds that must elapse after a `touchstart` event before it is considered a drag. If
       * movement occurs before this time, the drag is aborted. This is to allow touch swipes and scroll gestures.
       * @config {Number}
       * @default
       */
      dragTouchStartDelay: 300,

      /**
       * The CSS selector to use to identify the closest valid target from the event target.
       * @config {String}
       */
      dropTargetSelector: null,
      testConfig: {
        // In Edge synthetic click events are fired asynchronously and can take considerable
        // amount of time. Setting larger click swallow timeout to make sure Edge has time to fire click event.
        // Caught by test EventDrag4.t in Edge
        dragSwallowClickTime: BrowserHelper.isEdge ? 300 : 50
      }
    };
  }

  static get properties() {
    return {
      /**
       * The CSS class to add to the {@link #config-dragRootElement} (or {@link #config-draggingClsSelector} from
       * there) as soon as the pointerdown event occurs.
       * @member {String}
       * @readonly
       */
      draggingCls: 'b-draggable-active',

      /**
       * The CSS class to add to the `body` element as soon as the {@link #config-dragThreshold} is reached and
       * an actual drag is in progress.
       * @member {String}
       * @readonly
       */
      draggingBodyCls: 'b-draghelper-active',
      // match DragHelper since we need the same treatment

      /**
       * The CSS class to add to the element being dragged as soon as the pointerdown event occurs.
       * @member {String}
       * @readonly
       */
      draggingItemCls: 'b-dragging-item',

      /**
       * The CSS class to add to the {@link #config-dragRootElement} (or {@link #config-draggingClsSelector} from
       * there) as soon as the {@link #config-dragThreshold} is reached and an actual drag is in progress.
       * @member {String}
       * @readonly
       */
      draggingStartedCls: 'b-draggable-started',

      /**
       * The CSS class that is added to the {@link #config-dragRootElement}, i.e., `'b-draggable'`.
       * @property {String}
       * @readonly
       */
      draggableCls: 'b-draggable'
    };
  } //endregion
  //region Drag Processing
  // These template methods are implemented by derived classes as desired. There is only one overlap with Droppable's
  // template methods (dragDrop) so that a class can easily mixin both Draggable and Droppable and always distinguish
  // whether it is acting as the source, the target, or both.

  /**
   * This template method is called when the drag operation starts. This occurs when the {@link #config-dragThreshold}
   * has been reached.
   * @param {Core.util.drag.DragContext} drag
   */

  dragStart(drag) {// template
  }
  /**
   * This template method is called as the drag moves. This occurs on each mouse/pointer/touchmove event.
   * @param {Core.util.drag.DragContext} drag
   */

  dragOver(drag) {// template
  }
  /**
   * This template method is called when the drag enters a {@link Core.mixin.Droppable target}.
   * @param {Core.util.drag.DragContext} drag
   */

  dragEnterTarget(drag) {// template
  }
  /**
   * This template method is called when the drag leaves a {@link Core.mixin.Droppable target}.
   * @param {Core.util.drag.DragContext} drag
   * @param {Core.mixin.Droppable} oldTarget The previous value of `drag.target`.
   */

  dragLeaveTarget(drag, oldTarget) {// template
  }
  /**
   * This template method is called when the drag operation completes. This occurs on the pointerup event.
   *
   * This method is not called if the drag is {@link Core.util.drag.DragContext#property-aborted aborted}.
   * @param {Core.util.drag.DragContext} drag
   */

  dragDrop(drag) {// template
  }
  /**
   * This template method is called when the drag operation completes. This occurs on the pointerup event or perhaps
   * a keypress event.
   *
   * This method is always called, even if the drag is {@link Core.util.drag.DragContext#property-aborted aborted}.
   * @param {Core.util.drag.DragContext} drag
   */

  dragEnd(drag) {// template
  } //endregion
  //region Drag Management
  // These methods are called by the DragContext and generally manage element updates (adding/removing classes) or
  // event firing. In most cases these methods then call a corresponding Drag Processing template method intended
  // for derived classes to implement.

  get activeDrag() {
    const {
      dragging: drag
    } = this;
    return drag !== null && drag !== void 0 && drag.started && !drag.completed ? drag : null;
  }
  /**
   * Return the `Events` instance from which drag events are fired.
   * @internal
   * @property {Core.mixin.Events}
   */

  get dragEventer() {
    return this.callPreventable ? this : null; // simple Events feature detector
  }

  get draggingClassElement() {
    const {
      draggingClsSelector,
      dragRootElement
    } = this;
    return draggingClsSelector ? dragRootElement === null || dragRootElement === void 0 ? void 0 : dragRootElement.closest(draggingClsSelector) : dragRootElement;
  }

  beforeDrag(drag) {
    const {
      dragSelector
    } = this;
    return !dragSelector || drag.element.matches(dragSelector);
  }

  beginDrag(drag) {
    const {
      draggingCls,
      draggingClassElement
    } = this;

    if (draggingCls && draggingClassElement) {
      draggingClassElement.classList.add(draggingCls);
      drag.cleaners.push(() => draggingClassElement.classList.remove(draggingCls));
    }
  }

  async endDrag(drag) {
    const me = this,
          {
      dragEventer,
      dragProxy
    } = me;
    me.dragEnd(drag);

    if (drag.valid) {
      // The implementation may be async. If so, any Promise must always be
      // propagated back to a point which may have to await it
      await me.dragDrop(drag);
    }

    dragProxy === null || dragProxy === void 0 ? void 0 : dragProxy.dragEnd(drag);
    /**
     * This event is fired when a drag gesture is completed. This event is fired if the drag was
     * {@link Core.util.drag.DragContext#property-aborted}.
     * @event dragCancel
     * @param {Core.mixin.Draggable} source The draggable instance that fired the event.
     * @param {Core.util.drag.DragContext} drag The drag context.
     * @param {Event} event The browser event.
     */

    /**
     * This event is fired when a drag gesture is completed. This event is not fired if the drag was
     * {@link Core.util.drag.DragContext#property-aborted}.
     * @event drop
     * @param {Core.mixin.Draggable} source The draggable instance that fired the event.
     * @param {Core.util.drag.DragContext} drag The drag context.
     * @param {Event} event The browser event.
     */

    dragEventer === null || dragEventer === void 0 ? void 0 : dragEventer.trigger(drag.valid ? 'drop' : 'dragCancel', {
      drag,
      event: drag.event
    }); // The drag context could have registered finalizers added by the above methods or event. If so, we need to
    // wait for finalization of the drag before we clear our "dragging" config.

    me.finalizeDrag(drag);
  }

  async finalizeDrag(drag) {
    await drag.finalize(); // The doFinalize() method of DragContext is called by the above await... which nulls our "dragging" config
  }

  moveDrag(drag) {
    if (this.dragOver(drag) !== false) {
      const {
        dragEventer,
        dragProxy
      } = this;
      dragProxy === null || dragProxy === void 0 ? void 0 : dragProxy.dragMove(drag);
      /**
       * This event is fired as a drag gesture progresses due to cursor movement.
       * @event drag
       * @param {Core.mixin.Draggable} source The draggable instance that fired the event.
       * @param {Core.util.drag.DragContext} drag The drag context.
       * @param {Event} event The browser event.
       */

      dragEventer === null || dragEventer === void 0 ? void 0 : dragEventer.trigger('drag', {
        drag,
        event: drag.event
      });
    }
  }

  setupDragContext(event) {
    const me = this,
          {
      dragItemSelector
    } = me,
          {
      target
    } = event;
    return {
      event,
      itemElement: dragItemSelector ? target.closest(dragItemSelector) : target,
      touchStartDelay: me.dragTouchStartDelay,
      source: me,
      threshold: me.dragThreshold
    };
  }

  startDrag(drag) {
    const {
      draggingStartedCls,
      draggingClassElement,
      draggingItemCls,
      dragEventer,
      dragProxy
    } = this,
          {
      itemElement
    } = drag;
    /**
     * This event is fired prior to starting a drag gesture. This does not occur immediately after the user
     * performs the pointer/mousedown/touchstart but only after the {@link #config-dragThreshold} amount of
     * movement has taken place.
     *
     * The drag is canceled if a listener returns `false`.
     * @event beforeDragStart
     * @param {Core.mixin.Draggable} source The draggable instance that fired the event.
     * @param {Core.util.drag.DragContext} drag The drag context.
     * @param {Event} event The browser event.
     * @preventable
     */

    if ((dragEventer === null || dragEventer === void 0 ? void 0 : dragEventer.trigger('beforeDragStart', {
      drag,
      event: drag.event
    })) === false) {
      return false;
    }

    if (draggingStartedCls && draggingClassElement) {
      draggingClassElement.classList.add(draggingStartedCls);
      drag.cleaners.push(() => draggingClassElement.classList.remove(draggingStartedCls));
    }

    if (draggingItemCls && itemElement) {
      itemElement.classList.add(draggingItemCls);
      drag.cleaners.push(() => itemElement.classList.remove(draggingItemCls));
    }

    dragProxy === null || dragProxy === void 0 ? void 0 : dragProxy.dragStart(drag);
    this.dragStart(drag);
    /**
     * This event is fired when a drag gesture has started. This does not occur immediately after the user
     * performs the pointer/mousedown/touchstart but only after the {@link #config-dragThreshold} amount of
     * movement has taken place.
     * @event dragStart
     * @param {Core.mixin.Draggable} source The draggable instance that fired the event.
     * @param {Core.util.drag.DragContext} drag The drag context.
     * @param {Event} event The browser event.
     */

    dragEventer === null || dragEventer === void 0 ? void 0 : dragEventer.trigger('dragStart', {
      drag,
      event: drag.event
    });
  }

  trackDrag(drag) {
    var _drag$targetElement;

    const {
      dropTargetSelector
    } = this;
    drag.valid = !(dropTargetSelector && !((_drag$targetElement = drag.targetElement) !== null && _drag$targetElement !== void 0 && _drag$targetElement.closest(dropTargetSelector)));
    this.moveDrag(drag);
  } //endregion
  //region Configs

  updateDragging(drag) {
    const me = this;

    if (drag) {
      const listeners = ObjectHelper.assign({
        thisObj: me
      }, me.dragDocumentListeners); // Only listen for the events related to how the drag was initiated:

      if ('touches' in drag.startEvent) {
        delete listeners.mousemove;
        delete listeners.mouseup;
      } else {
        delete listeners.contextmenu;
        delete listeners.touchmove;
        delete listeners.touchend;
        delete listeners.pointerup;
      }

      drag.cleaners.push(EventHelper.on(listeners));
      me.beginDrag(drag);
    }
  }

  changeDragProxy(config, existing) {
    return DragProxy.reconfigure(existing, config, {
      owner: this,
      defaults: {
        owner: this
      }
    });
  }

  updateDragRootElement(rootEl, was) {
    var _me$_dragRootDetacher;

    const me = this,
          {
      draggableCls
    } = me;
    was === null || was === void 0 ? void 0 : was.classList.remove(draggableCls);
    (_me$_dragRootDetacher = me._dragRootDetacher) === null || _me$_dragRootDetacher === void 0 ? void 0 : _me$_dragRootDetacher.call(me);

    if (rootEl) {
      rootEl.classList.add(draggableCls);
      me._dragRootDetacher = EventHelper.on({
        thisObj: me,
        element: rootEl,
        mousedown: 'onDragMouseDown',
        // We have touchstart listener in place since Siesta/Chrome can send these events even on non-touch
        // devices:
        touchstart: 'onDragTouchStart'
      });
    }
  } //endregion
  //region Events

  onDragContextMenu(event) {
    event.preventDefault();
  }

  onDragKeyDown(event) {
    this.dragging.keyDown(event);
  }

  onDragKeyUp(event) {
    this.dragging.keyUp(event);
  }
  /**
   * Grab draggable element on mouse down.
   * @param {Event} event
   * @private
   */

  onDragMouseDown(event) {
    // only dragging with left mouse button
    if (event.button === 0) {
      this.onDragPointerDown(event);
    }
  }
  /**
   * Grab draggable element on pointerdown.
   * @param {Event} event
   * @private
   */

  onDragPointerDown(event) {
    let {
      dragging: drag
    } = this; // If a drag is ongoing already, finalize it and don't proceed with new drag (happens if user does pointerup
    // outside browser window). Also handles the edge case of trying to start a new drag while previous is awaiting
    // finalization, in which case it just bails out.

    if (!drag) {
      drag = new DragContext(this.setupDragContext(event));
      drag.begin();
    } else if (!drag.isFinalizing) {
      drag.abort();
    }
  }

  onDragPointerMove(event) {
    const {
      dragging: drag
    } = this;

    if (drag && !drag.completed) {
      drag === null || drag === void 0 ? void 0 : drag.move(event);
    }
  }

  onDragPointerUp(event) {
    const {
      dragging: drag
    } = this;

    if (drag && !drag.completed) {
      drag.end(event);
      this.endDrag(drag);
    }
  }
  /**
   * @param {Event} event
   * @private
   */

  onDragTouchStart(event) {
    // only allowing one finger for now...
    if (event.touches.length === 1) {
      this.onDragPointerDown(event);
    }
  } //endregion

});

/**
 * @module Core/mixin/Droppable
 */

/**
 * Mix this into another class to enable drop support and receive drops from {@link Core.mixin.Draggable draggables}.
 *
 * There are 4 basic methods that a droppable implements. These methods are called as drag operations occur:
 *
 * ```javascript
 *  class MyDroppable extends Base.mixin(Droppable) {
 *      dragEnter(drag) {
 *          // a drag has entered the drop zone... create some type of drop indicator perhaps
 *      }
 *
 *      dragMove(drag) {
 *          // a drag has changed position... update drop indicators
 *      }
 *
 *      dragDrop(drag) {
 *          // drop has occurred... process data from the drag context
 *      }
 *
 *      dragLeave(drag) {
 *          // the drag has left the drop zone... cleanup indicators
 *      }
 *  }
 * ```
 *
 * Instances of `Droppable` are associated with an element to receive drag operations:
 *
 * ```javascript
 *  let target = new MyDroppable({
 *      dropRootElement : someElement
 *  });
 * ```
 * @mixin
 * @internal
 */

var Droppable = (Target => class Droppable extends (Target || Base) {
  static get $name() {
    return 'Droppable';
  } //region Configs

  static get configurable() {
    return {
      /**
       * The current `DragContext`. This is set when a drag enters this target. Changing this config causes the
       * {@link #function-dragEnter} and {@link #function-dragLeave} methods to be called. If `dragEnter` returns
       * `false` for a drag, this value will be set to `null`.
       * @member {Core.util.drag.DragContext}
       * @readonly
       */
      dropping: null,

      /**
       * Set this config to the element where drops should be received. When set, the `b-droppable` CSS class is
       * added to the element and the `Droppable` instance is associated with that element so that it can be
       * found by {@link Core.mixin.Draggable draggables}.
       * @config {HTMLElement}
       */
      dropRootElement: {
        $config: 'nullify',
        value: null
      }
    };
  }
  /**
   * Returns the CSS class that is added to the {@link #config-dropRootElement}, i.e., `'b-droppable'`.
   * @property {String}
   * @readonly
   */

  get droppableCls() {
    return 'b-droppable';
  } //endregion
  //region Drop Management

  /**
   * This method is called when a drag enters this droppable's `dropRootElement`. In many cases, this method is used
   * to create some sort of drop indicator to provide user feedback.
   *
   * If this method does not return `false`, the {@link #property-dropping} config will retain the given `drag` context
   * which was set prior to this method being called.
   *
   * If this method returns `false`, the drop will not be accepted. Neither {@link #function-dragDrop} nor
   * {@link #function-dragLeave} will be called for this drop. If the drag leaves this target and re-enters, this
   * method will be called again. While `dropping` will already be updated before this method is called, it will be
   * reset to `null` in this case.
   *
   * @param {Core.util.drag.DragContext} drag
   * @returns {Boolean}
   */

  dragEnter(drag) {// template
  }
  /**
   * This method is called when the drag that was previously announced via {@link #function-dragEnter} moves to a new
   * position. This is typically where drop indicators are updated to reflect the new position.
   * @param {Core.util.drag.DragContext} drag
   */

  dragMove(drag) {// template
  }
  /**
   * This method is called when the drag that was previously announced via {@link #function-dragEnter} has ended with
   * a drop. In addition to any cleanup (since {@link #function-dragLeave} will not be called), this method handles
   * any updates associated with the data from the drag context and the position of the drop.
   * @param {Core.util.drag.DragContext} drag
   */

  dragDrop(drag) {// template
  }
  /**
   * This method is called when the drag that was previously announced via {@link #function-dragEnter} leaves this
   * droppable's `dropRootElement`. This is the time to cleanup anything created by `dragEnter`.
   * @param {Core.util.drag.DragContext} drag
   */

  dragLeave(drag) {// template
  } //endregion
  //region Configs

  changeDropping(dropping, was) {
    if (dropping !== was) {
      const me = this;

      if (was) {
        if (was.aborted || !was.completed) {
          me.dragLeave(was);
        }
      }

      if (dropping) {
        me._dropping = dropping; // update config value early in case dragEnter et al refer to it

        if (me.dragEnter(dropping) === false) {
          dropping = null;
        }

        me._dropping = was; // restore the value so that updateDropping is called as it should be
      }
    }

    return dropping;
  }

  updateDropRootElement(rootEl, was) {
    const {
      droppableCls
    } = this;

    if (was) {
      was.classList.remove(droppableCls);

      if (DomDataStore.get(was, 'droppable') === this) {
        DomDataStore.remove(was, 'droppable');
      }
    }

    if (rootEl) {
      rootEl.classList.add(droppableCls);
      DomDataStore.set(rootEl, 'droppable', this);
    }
  } //endregion

});

/**
 * @module Core/util/DynamicObject
 */

const PENDING = Symbol('pendingCreate');
/**
 * This class is used to manage dynamic creation and configuration of individual properties of an object. This pattern
 * is used to allow the names of an object to each represent a dynamically instantiated object. For example, the
 * `features` config of Calendar is defined like so:
 *
 * ```javascript
 *  class Calendar extends ... {
 *      static get configurable() {
 *          return {
 *              features : {
 *                  drag : {
 *                      // configs for Drag feature
 *                  }
 *              }
 *          }
 *      }
 *  }
 * ```
 * This class is used to manage the `features` objects in the above case. The `drag` property value is promoted from
 * the config object defined by the class and user instance on first request. Like config properties themselves, these
 * features may access other features during their initialization. These accesses are trapped by this class to ensure
 * the config object is promoted to an instantiated instance.
 *
 * A {@link #config-factory} is provided to this object to allow it to create instances from names like `'drag'`.
 * @internal
 */

class DynamicObject extends Base {
  static get prototypeProperties() {
    return {
      /**
       * Optional function to call as instances are created. Each new instance is passed to this function.
       * @param {Object} instance The newly created instance.
       * @param {String} key The property name in the dynamic object by which the new instance may be referenced.
       * @config {Function}
       */
      created: null,

      /**
       * The {@link Core.mixin.Factoryable factory} to use to create instances.
       * @config {Object}
       */
      factory: null,

      /**
       * By default, the name of the member is used for the type. Set this config to `true` to also allow the
       * config object for a property to contain a `type` property. Set this to `false` to ignore the name of the
       * member and rely on the {@link #config-factory} to process the config object.
       * @config {Boolean|String}
       * @default
       */
      inferType: 'name',

      /**
       * The owning object to pass along to the instances as the `ownerName` property.
       * @config {Object}
       */
      owner: null,

      /**
       * The property name by which to store the `owner` on each instance.
       * @config {String}
       */
      ownerName: null,

      /**
       * Set to `false` to prevent using a `Proxy` even if that JavaScript platform feature is available. Using
       * a `Proxy` is ideal because it allows for all forms of access to the dynamic properties to be handled
       * instead of only those that have predefined configuration values.
       * @config {Boolean}
       * @private
       */
      proxyable: typeof Proxy !== 'undefined',

      /**
       * Optional function that will be passed a config object prior to instantiating an object. This function
       * can either modify the passed object or return a new object.
       * @param {Object} config The config object used to create the object
       * @param {String} name The property name in the Dynamic object by which the new instance may be referenced.
       * @config {Function}
       */
      setup: null
    };
  }

  static get properties() {
    return {
      /**
       * Holds config objects for each defined object. These are used to hold class and instance config values
       * and use them to create instances on first request, or when `flush()` is called. Further, if the instance
       * is initially assigned instead of retrieved, these values act as the defaults for the instance and are
       * combined with those provided in the assignment.
       * @member {Object} defaults
       * @private
       */
      defaults: {},

      /**
       * This object holds the actual instances that are retrieved by the dynamic accessor or `Proxy`.
       * @member {Object} instances
       * @private
       */
      instances: {},

      /**
       * The object that contains the dynamic accessors for each instance. This object is not used when using a
       * `Proxy`.
       * @member {Object} object
       * @private
       */
      object: Object.create({})
    };
  }
  /**
   * Returns the `Proxy` instance used to manage dynamic assignments. If the JavaScript platform does not support the
   * `Proxy` class, this will be `null`.
   * @property {Proxy}
   * @private
   */

  get proxy() {
    const me = this;
    let proxy = null;

    if (me.proxyable) {
      proxy = new Proxy(me.instances, {
        get(o, name) {
          return me.get(name);
        },

        set(o, name, value) {
          me.set(name, value);
          return true;
        },

        deleteProperty(o, name) {
          me.set(name, null);
          return true;
        }

      });
    } // Replace our getter with the result for quicker future use.

    Reflect.defineProperty(me, 'proxy', {
      configurable: true,
      // allow destroy() to delete it
      value: proxy
    });
    return proxy;
  }
  /**
   * Returns the object that contains the dynamic properties. This may be a `Proxy` instance or an object with getter
   * and setter accessors.
   * @property {Object}
   * @internal
   */

  get target() {
    return this.proxy || this.object;
  }
  /**
   * This method establishes the initial definition of a dynamic property. When using a `Proxy`, this method simply
   * needs to cache away the initial config for use by the getter. When `Proxy` is unavailable, this method will
   * also defined a getter/setter to intercept access to the dynamic property.
   * @param {String} name The name of the dynamic property.
   * @param {Object} config The initial config object for the dynamic property.
   * @private
   */

  define(name, config) {
    this.instances[name] = PENDING;
    this.setDefaults(name, config); // We provide get/set accessors on our `object` so that we can create the instance on first use and provide
    // the correct defaults. The setter also handles reassignment and reconfiguration. These accessors are
    // placed on the prototype of `object` so that we can add/remove the same accessors from `object` itself.
    // This allows users of the object to see the correct number of "own" keys for enumeration.

    this.defineProp(name, true);
  }
  /**
   * Define the get/set accessors for `name` on our `object` or its prototype.
   * @param {String} name
   * @param {Boolean} [base] Pass `true` to indicate the property should be defined on the prototype.
   * @private
   */

  defineProp(name, base) {
    const me = this,
          {
      object
    } = me;

    if (!me.proxy) {
      Reflect.defineProperty(base ? Object.getPrototypeOf(object) : object, name, {
        configurable: !base,
        enumerable: true,

        get() {
          return me.get(name);
        },

        set(value) {
          return me.set(name, value);
        }

      });
    }
  }
  /**
   * Ensures that all defined members are touched to trigger their creation.
   * @internal
   */

  flush() {
    // Iterate the defaults object to loop over all the defined items:
    for (const name in this.defaults) {
      this.get(name);
    }
  }
  /**
   * Returns (lazily creating as necessary) the value of a dynamic property given its name.
   * @param {String} name
   * @returns {Object}
   * @private
   */

  get(name) {
    const {
      defaults,
      instances
    } = this;

    if (instances[name] === PENDING) {
      this.set(name, PENDING);
    } // Return null for defined instances that have been nulled out and undefined otherwise:

    return instances[name] || defaults[name] && null;
  }
  /**
   * Sets the value of a dynamic property given its name and value.
   * @param {String} name
   * @param {Object} value
   * @private
   */

  set(name, value) {
    const me = this,
          {
      defaults,
      factory,
      instances,
      setup
    } = me,
          was = instances[name] === PENDING ? null : instances[name],
          instance = factory.reconfigure(was, value === PENDING ? {} : value || null, {
      defaults: defaults[name] || me.setDefaults(name, {}),
      owner: me.owner,
      setup: setup && ((config, type, defaults) => setup(config, name, type, defaults))
    });

    if (instance !== was) {
      instances[name] = instance;

      if (instance) {
        var _me$created;

        me.defineProp(name);
        (_me$created = me.created) === null || _me$created === void 0 ? void 0 : _me$created.call(me, instance, name);
      } else {
        delete me.object[name];
        delete instances[name];
      }
    }
  }
  /**
   * Stores the default config values for use in the factory reconfiguration process.
   * @param {String} name The name and default type of the dynamic property. Depending on {@link #config-inferType},
   * this may not be overridden by a type property in the config object.
   * @param {Object} config The config object.
   * @returns {Object}
   * @private
   */

  setDefaults(name, config) {
    const {
      defaults,
      factory,
      inferType,
      instances,
      owner,
      ownerName
    } = this,
          {
      typeKey
    } = factory.factoryable;
    config = config === true ? {} : ObjectHelper.assign({}, config); // copy props even from prototype

    if (inferType === 'name' || inferType === true && !config[typeKey]) {
      config[typeKey] = name;
    } // Store this object as the "owner" on the config object so instances can access their creating object:

    if (ownerName) {
      config[ownerName] = owner;
    }

    config.beforeConfigure = instance => {
      // Ensure the feature is injected into the features object before initialization so that it is
      // available to call chains from its initialization.
      instances[name] = instance;
    };

    return defaults[name] = config;
  }
  /**
   * Updates the members of `object` based on the provided configuration.
   * @param {Object} members The configuration for the instances of `object`.
   * @internal
   */

  update(members) {
    const me = this;
    let name, config;

    if (members) {
      // We prime the features so that if any configuration code accesses a feature, it will self initialize,
      // but if not, they will remain in a primed state until afterConfigure.
      for (name in members) {
        config = members[name];

        if (me.defaults[name]) {
          // if (already defined)
          me.set(name, config);
        } else if (config) {
          me.define(name, config);
        }
      } // NOTE: we leave alone any existing features that were not present in the given object. To remove a
      // feature, you must set it to null or set all features to null.

    } else {
      for (name in me.instances) {
        me.set(name, null);
      }
    }
  }

}
DynamicObject.initClass();
DynamicObject._$name = 'DynamicObject';

/**
 * @module Core/mixin/Featureable
 */

/**
 * This mixin provides management of a set of features that can be manipulated via the `features` config.
 *
 * The first step in using `Featureable` is to define the family of features using `Factoryable` to declare a base
 * class for features to extend:
 * ```
 *  class SuperWidgetFeature extends InstancePlugin.mixin(Factoryable) {
 *      static get factoryable() {
 *          //
 *      }
 *  }
 * ```
 *
 * The various feature classes extend the `SuperWidgetFeature` base class and call `initClass()` to register themselves:
 * ```
 *  export default class AmazingSuperWidgetFeature extends SuperWidgetFeature {
 *      static get type() {
 *          return 'amazing';
 *      }
 *  }
 *
 *  AmazingSuperWidgetFeature.initClass();
 * ```
 *
 * A class that supports these features via `Featureable` is declared like so:
 * ```
 *  class SuperWidget extends Widget.mixin(Featureable) {
 *      static get featureable() {
 *          return {
 *              factory : SuperWidgetFeature
 *          };
 *      }
 *
 *      static get configurable() {
 *          return {
 *              // Declare the default features. These can be disabled by setting them to a falsy value. Using
 *              // configurable(), the value defined by this class is merged with values defined by derived classes
 *              // and ultimately the instance.
 *              features : {
 *                  amazing : {
 *                      ...
 *                  }
 *              }
 *          };
 *      }
 *  }
 *```
 * @mixin
 * @internal
 */

var Featureable = (Target => class Featureable extends (Target || Base) {
  static get $name() {
    return 'Featureable';
  }

  static get configurable() {
    return {
      /**
       * Specifies the features to create and associated with the instance. The keys of this object are the names
       * of features. The values are config objects for those feature instances.
       *
       * After construction, this property can be used to access the feature instances and even reconfigure them.
       *
       * For example:
       * ```
       *  instance.features.amazing = {
       *      // reconfigure this feature
       *  }
       * ```
       * This can also be done in bulk:
       * ```
       *  instance.features = {
       *      amazing : {
       *          // reconfigure this feature
       *      },
       *      // reconfigure other features
       *  }
       * ```
       * @config {Object}
       */
      features: null
    };
  }

  static get declarable() {
    return [
    /**
     * This property getter returns options that control feature management for the derived class. This
     * property getter must be defined by the class that mixes in `Featureable` in order to initialize the
     * class properly.
     * ```
     *  class SuperWidget extends Widget.mixin(Featureable) {
     *      static get featureable() {
     *          return {
     *              factory : SuperWidgetFeature
     *          };
     *      }
     *      ...
     *  }
     * ```
     * @static
     * @member {Object} featureable
     * @property {Core.mixin.Factoryable} featureable.factory The factoryable class (not one of its instances)
     * that will be used to create feature instances.
     * @property {String} [featureable.ownerName='client'] The config or property to assign on each feature as
     * a reference to its creator, the `Featureable` instance.
     * @internal
     */
    'featureable'];
  }

  static setupFeatureable(cls) {
    const featureable = {
      ownerName: 'client',
      ...cls.featureable
    };
    featureable.factory.initClass(); // Replace the class/static getter with a new one that returns the complete featureable object:

    Reflect.defineProperty(cls, 'featureable', {
      get() {
        return featureable;
      }

    });
  }

  afterConfigure() {
    var _this$$features;

    super.afterConfigure();
    (_this$$features = this.$features) === null || _this$$features === void 0 ? void 0 : _this$$features.flush();
  }

  doDestroy() {
    this.features = null;
    super.doDestroy();
  }
  /**
   * Returns `true` if the specified feature is active for this instance and `false` otherwise.
   * @param {String} name The feature name
   * @returns {Boolean}
   */

  hasFeature(name) {
    var _this$features;

    return Boolean((_this$features = this.features) === null || _this$features === void 0 ? void 0 : _this$features[name]);
  }

  changeFeatures(features) {
    const me = this,
          {
      featureable
    } = me.constructor,
          manager = me.$features || (me.$features = new DynamicObject({
      factory: featureable.factory,
      owner: me,
      ownerName: featureable.ownerName
    }));
    manager.update(features); // Normally we wait for afterConfigure to create all the features, but if we are being called after that
    // point in the life cycle, we need to flush them out now. We still use the same delay technique in case the
    // user adds multiple features dynamically.

    if (!me.isConfiguring) {
      manager.flush();
    }

    return manager.target;
  } // This does not need a className on Widgets.
  // Each *Class* which doesn't need 'b-' + constructor.name.toLowerCase() automatically adding
  // to the Widget it's mixed in to should implement thus.

  get widgetClass() {}

});

/* eslint-disable no-unused-expressions */

/**
 * @module Core/mixin/Hoverable
 */

const EDGES = {
  e: 'b-hover-edge',
  t: 'b-hover-top',
  r: 'b-hover-right',
  b: 'b-hover-bottom',
  l: 'b-hover-left'
},
      EDGE_CLASSES = {
  [EDGES.e]: 1,
  [EDGES.t]: 1,
  [EDGES.r]: 1,
  [EDGES.b]: 1,
  [EDGES.l]: 1
},
      ZONES = {
  t: [EDGES.e, EDGES.t],
  r: [EDGES.e, EDGES.r],
  b: [EDGES.e, EDGES.b],
  l: [EDGES.e, EDGES.l],
  tr: [EDGES.e, EDGES.t, EDGES.r],
  bl: [EDGES.e, EDGES.b, EDGES.l],
  tl: [EDGES.e, EDGES.t, EDGES.l],
  br: [EDGES.e, EDGES.b, EDGES.r]
};
/**
 * This mixin provides mouse hover tracking.
 * ```
 *  class Tracker extends Base.mixin(Hoverable) {
 *      hoverEnter(leaving) {
 *          // this.hoverTarget has been entered from "leaving"
 *          // this.hoverTarget will never be null, but leaving may be null
 *      }
 *
 *      hoverLeave(leaving) {
 *          // this.hoverTarget has been entered from "leaving"
 *          // this.hoverTarget may be null, but leaving will never be null
 *      }
 *
 *      hoverMove(event) {
 *          // called when a mousemove is made within a hover target
 *          // this.hoverTarget will never be null
 *      }
 *  }
 *
 *  let tracker = new Tracker({
 *      hoverRootElement : document.body,
 *      hoverSelector    : '.hoverable'
 *  });
 * ```
 * @mixin
 * @internal
 */

var Hoverable = (Target => class Hoverable extends Target.mixin(Delayable) {
  static get $name() {
    return 'Hoverable';
  } //region Configs

  static get configurable() {
    return {
      /**
       * A CSS class to add to the {@link #config-hoverTarget target} element.
       * @config {String}
       */
      hoverCls: null,

      /**
       * A CSS class to add to the {@link #config-hoverTarget target} element to enable CSS animations. This class
       * is added after calling {@link #function-hoverEnter}.
       * @config {String}
       */
      hoverAnimationCls: null,

      /**
       * A CSS class to add to the {@link #config-hoverRootElement root} element.
       * @config {String}
       */
      hoverRootCls: null,

      /**
       * A CSS class to add to the {@link #config-hoverRootElement root} element when there is an active
       * {@link #config-hoverTarget target}.
       * @config {String}
       */
      hoverRootActiveCls: null,

      /**
       * The number of milliseconds to delay notification of changes in the {@link #config-hoverTarget}.
       * @config {Number}
       */
      hoverDelay: null,

      /**
       * The current element that the cursor is inside as determined by `mouseover` and `mouseout`. Changes in
       * this config trigger re-evaluation of the {@link #config-hoverSelector} to determine if there is a
       * {@link #config-hoverTarget}.
       * @config {HTMLElement}
       * @private
       */
      hoverElement: null,

      /**
       * An element to ignore. Mouse entry into this element will not trigger a change in either of the
       * {@link #config-hoverElement} or {@link #config-hoverTarget} values.
       * @config {HTMLElement}
       */
      hoverIgnoreElement: null,

      /**
       * This property is a string containing one character for each edge that is hoverable. For example, a
       * value of "tb" indicates that the top and bottom edges are hoverable.
       * @config {String}
       */
      hoverEdges: null,

      /**
       * When {@link #config-hoverEdges} is used, this value determines the size (in pixels) of the edge. When
       * the cursor is within this number of pixels of an edge listed in `hoverEdges`, the appropriate CSS class
       * is added to the {@link #config-hoverTarget}:
       *
       *  - `b-hover-top`
       *  - `b-hover-right`
       *  - `b-hover-bottom`
       *  - `b-hover-left`
       *
       * Depending on the values of `hoverEdges`, it is possible to have at most two of these classes present at
       * any one time (when the cursor is in a corner).
       * @config {Number}
       * @default
       */
      hoverEdgeSize: 10,

      /**
       * The outer element where hover tracking will operate (attach events to it and use as root limit when
       * looking for ancestors).
       *
       * A common choice for this will be `document.body`.
       * @config {HTMLElement}
       */
      hoverRootElement: {
        $config: 'nullify',
        value: null
      },

      /**
       * A selector for the [closest](https://developer.mozilla.org/en-US/docs/Web/API/Element/closest) API to
       * determine the actual element of interest. This selector is used to process changes to the
       * {@link #config-hoverElement} to determine the {@link #config-hoverTarget}.
       * @config {String}
       */
      hoverSelector: null,

      /**
       * The currently active hover target. This will be the same as {@link #config-hoverElement} unless there is
       * a {@link #config-hoverSelector}.
       * @config {HTMLElement}
       */
      hoverTarget: {
        $config: 'nullify',
        value: null
      },

      /**
       * Set to `true` to include tracking of `mousemove` events for the active {@link #config-hoverTarget}. This
       * is required for the {@link #function-hoverMove} method to be called.
       * @config {Boolean}
       * @default false
       */
      hoverTrack: null,

      /**
       * A string value containing one character per active edge (e.g., "tr").
       * @config {String}
       * @private
       */
      hoverZone: null
    };
  }

  static get delayable() {
    return {
      setHoverTarget: 0
    };
  } //endregion
  //region State Handling

  /**
   * This method is called when the cursor enters the {@link #config-hoverTarget}. The `hoverTarget` will not be
   * `null`.
   * @param {HTMLElement} leaving The element that was previously the `hoverTarget`. This value may be null.
   */

  hoverEnter(leaving) {// template
  }
  /**
   * This method should return true if the given `element` should be ignored. By default, this is `true` if the
   * `element` is contained inside the {@link #config-hoverIgnoreElement}.
   * @param {HTMLElement} element
   * @returns {Boolean}
   * @protected
   */

  hoverIgnore(element) {
    var _this$hoverIgnoreElem;

    return (_this$hoverIgnoreElem = this.hoverIgnoreElement) === null || _this$hoverIgnoreElem === void 0 ? void 0 : _this$hoverIgnoreElem.contains(element);
  }
  /**
   * This method is called when the cursor leaves the {@link #config-hoverTarget}. The `hoverTarget` may be `null`
   * or refer to the new `hoverTarget`
   * @param {HTMLElement} leaving The element that was previously the `hoverTarget`. This value will not be null.
   */

  hoverLeave(leaving) {// template
  }
  /**
   * This method is called when the mouse moves within a {@link #config-hoverTarget}, but only if enabled by the
   * {@link #config-hoverTrack} config.
   * @param {Event} event
   */

  hoverMove(event) {// template
  } //endregion
  //region Events

  onHoverMouseMove(event) {
    const me = this,
          {
      hoverEdges,
      hoverEdgeSize,
      hoverTarget
    } = me;

    if (hoverTarget) {
      if (hoverEdges) {
        const {
          top,
          left,
          width,
          height,
          right,
          bottom
        } = hoverTarget.getBoundingClientRect(),
              {
          clientX,
          clientY
        } = event,
              centerX = left + width / 2,
              centerY = top + height / 2,
              t = clientY < (hoverEdgeSize ? top + hoverEdgeSize : centerY),
              r = clientX >= (hoverEdgeSize ? right - hoverEdgeSize : centerX),
              b = clientY >= (hoverEdgeSize ? bottom - hoverEdgeSize : centerY),
              l = clientX < (hoverEdgeSize ? left + hoverEdgeSize : centerX),
              tb = t || b ? t ? 't' : 'b' : '',
              rl = r || l ? r ? 'r' : 'l' : '';
        me.hoverZone = (hoverEdges.includes(tb) ? tb : '') + (hoverEdges.includes(rl) ? rl : '');
      }

      me.hoverEvent = event;
      me.hoverTrack && me.hoverMove(event);
    }
  }

  onHoverMouseOver(event) {
    this.hoverEvent = event;
    this.hoverElement = event.target;
  }

  onHoverMouseOut(event) {
    this.hoverEvent = event;
    this.hoverElement = event.relatedTarget;
  } //endregion
  //region Configs
  // hoverDelay

  updateHoverDelay(delay) {
    this.setHoverTarget.delay = delay;
  } // hoverEdges

  changeHoverEdges(edges) {
    return edges === true ? 'trbl' : (edges || '').replace('v', 'tb').replace('h', 'lr');
  }

  updateHoverEdges() {
    this.syncHoverListeners();
  } // hoverElement

  changeHoverElement(element) {
    if (!this.hoverIgnore(element)) {
      return element;
    }
  }

  updateHoverElement(hoverEl) {
    const {
      hoverSelector
    } = this;

    if (hoverSelector) {
      var _hoverEl;

      hoverEl = (_hoverEl = hoverEl) === null || _hoverEl === void 0 ? void 0 : _hoverEl.closest(hoverSelector);
    }

    this.setHoverTarget(hoverEl); // this may be delayed
  } // hoverRootElement

  updateHoverRootElement(rootEl, was) {
    const {
      hoverRootCls
    } = this;

    if (hoverRootCls) {
      was === null || was === void 0 ? void 0 : was.classList.remove(hoverRootCls);
      rootEl === null || rootEl === void 0 ? void 0 : rootEl.classList.add(hoverRootCls);
    }

    this.syncHoverListeners();
  } // hoverTarget

  changeHoverTarget(hoverEl, was) {
    if (was) {
      this.hoverZone = null;
    }

    return hoverEl;
  }

  updateHoverTarget(hoverEl, was) {
    const me = this,
          {
      hoverCls,
      hoverAnimationCls,
      hoverRootActiveCls,
      hoverRootElement
    } = me;

    if (hoverRootActiveCls) {
      hoverRootElement === null || hoverRootElement === void 0 ? void 0 : hoverRootElement.classList[hoverEl ? 'add' : 'remove'](hoverRootActiveCls);
    }

    if (was) {
      hoverCls && was.classList.remove(hoverCls);
      hoverAnimationCls && was.classList.remove(hoverAnimationCls);
      me.hoverLeave(was);
    }

    if (hoverEl) {
      hoverCls && hoverEl.classList.add(hoverCls);
      me.hoverEnter(was);

      if (me.hoverTrack) {
        me.hoverMove(me.hoverEvent);
      }

      if (hoverAnimationCls) {
        hoverEl.getBoundingClientRect(); // force layout so next change starts animation

        hoverEl.classList.add(hoverAnimationCls);
      }
    }
  } // hoverTrack

  updateHoverTrack() {
    this.syncHoverListeners();
  } // hoverZone

  updateHoverZone(zone) {
    const {
      hoverAnimationCls,
      hoverTarget
    } = this;

    if (hoverTarget) {
      const {
        className
      } = hoverTarget,
            cls = DomClassList.change(className,
      /* add= */
      zone ? ZONES[zone] : null,
      /* remove= */
      EDGE_CLASSES);

      if (className !== cls) {
        hoverTarget.className = cls;

        if (zone && hoverAnimationCls) {
          hoverTarget.classList.remove(hoverAnimationCls);
          hoverTarget.getBoundingClientRect(); // force layout so next change starts animation

          hoverTarget.classList.add(hoverAnimationCls);
        }
      }
    }
  } //endregion
  //region Misc

  setHoverTarget(target) {
    // this method runs later based on the hoverDelay
    this.hoverTarget = target;
  }

  syncHoverListeners() {
    var _me$_hoverRootDetache;

    const me = this,
          element = me.hoverRootElement,
          listeners = {
      element,
      thisObj: me,
      mouseover: 'onHoverMouseOver',
      mouseout: 'onHoverMouseOut'
    };

    if (me.hoverTrack || me.hoverEdges) {
      listeners.mousemove = 'onHoverMouseMove';
    }

    (_me$_hoverRootDetache = me._hoverRootDetacher) === null || _me$_hoverRootDetache === void 0 ? void 0 : _me$_hoverRootDetache.call(me);
    me._hoverRootDetacher = element && EventHelper.on(listeners);
  } //endregion

});

/**
 * @module Core/mixin/Override
 */

const excludedPropNames = {
  constructor: 1,
  prototype: 1,
  name: 1,
  length: 1,
  arguments: 1,
  caller: 1,
  callee: 1,
  __proto__: 1
};
/**
 * Simplifies overriding class methods by allowing methods from another class to be used as overrides.
 * Overrides are defined as own classes. They must at a minimum contain a static getter named targetClass, which
 * should return the class to override. Apply the override by calling {@link #function-apply-static apply()}.
 *
 * @example
 * class TemplateColumnOverride {
 *   static get target() {
 *     return {
 *             class: TemplateColumn,
 *             product: 'grid',
 *             minVersion: '1.0',
 *             maxVersion: '1.5'
 *     }
 *   }
 *
 *   renderer(renderData) {
 *       // call overridden function (optional)
 *       const value = this._overridden.renderer.call(this, renderData);
 *
 *       return 'HELLO' + value;
 *   }
 * }
 * Override.apply(TemplateColumnOverride);
 */

class Override {
  /**
   * Apply override. We strongly suggest that you at least specify a maxVersion for your overrides.
   * ```
   * class OriginalOverride {
   *     static get target() {
   *         return {
   *             class: Original,
   *             product: 'grid',
   *             minVersion: '1.0',
   *             maxVersion: '1.5'
   *         }
   *     }
   * }
   * ```
   * @param {Object} override An override class definition
   */
  static apply(override) {
    if (!override.target) throw new Error('Override must specify what it overrides, using static getter target');
    if (!override.target.class) throw new Error('Override must specify which class it overrides, using target.class');
    if (!this.shouldApplyOverride(override)) return false;
    const staticKeys = Object.getOwnPropertyNames(override),
          instanceKeys = Object.getOwnPropertyNames(override.prototype);
    staticKeys.splice(staticKeys.indexOf('target'), 1);
    this.internalOverrideAll(override.target.class, staticKeys, override);
    this.internalOverrideAll(override.target.class.prototype, instanceKeys, override.prototype);
    return true;
  }

  static internalOverrideAll(targetClass, properties, overrideDefinition) {
    Reflect.ownKeys(overrideDefinition).forEach(key => {
      if (properties.includes(key) && !excludedPropNames[key]) {
        const desc = Object.getOwnPropertyDescriptor(overrideDefinition, key);
        let currentTargetClass = targetClass;
        let targetProperty = null; // Walk up the prototype chain to find fn, needed for mixin overrides applied to class that has them
        // mixed in

        while (!targetProperty && currentTargetClass) {
          targetProperty = Object.getOwnPropertyDescriptor(currentTargetClass, key);

          if (!targetProperty) {
            currentTargetClass = Object.getPrototypeOf(currentTargetClass);
          }
        }

        if (targetProperty) {
          this.internalOverride(currentTargetClass, key, desc, targetProperty);
        }
      }
    });
  }

  static internalOverride(target, key, desc, targetDesc) {
    const overrides = target._overridden = target._overridden || {};
    overrides[key] = target[key];

    if (targetDesc.get) {
      Object.defineProperty(target, key, {
        enumerable: false,
        configurable: true,
        get: desc.get
      });
    } else {
      target[key] = desc.value;
    }
  }
  /**
   * Checks versions if an override should be applied. Specify version in your overrides target config
   * @param override
   * @returns {Boolean}
   * @example
   * class OriginalOverride {
   *     static get target() {
   *         return {
   *             class: Original,
   *             product: 'grid',
   *             minVersion: '1.0',
   *             maxVersion: '1.5'
   *         }
   *     }
   * }
   * @private
   */

  static shouldApplyOverride(override) {
    const config = override.target; // not using versioning, allow override

    if (!config.maxVersion && !config.minVersion) return true; // must specify product to be able to lookup versions

    if (!config.product) throw new Error('Override must specify product when using versioning'); // override is for older version, disallow

    if (config.maxVersion && VersionHelper[config.product].isNewerThan(config.maxVersion)) {
      return false;
    } // override is for newer version, disallow

    if (config.minVersion && VersionHelper[config.product].isOlderThan(config.minVersion)) {
      return false;
    } // override is for current version, allow

    return true;
  }

}
Override._$name = 'Override';

/**
 * @module Core/util/ClickRepeater
 */

/**
 * A helper class, which, when applied to an {@link #config-element} means that a mousedown
 * and hold on that element will, after a configured {@link #config-delay}, begin
 * autorepeating `click` events on that element, starting at a rate of
 * {@link #config-startRate} clicks per second, and over {@link #config-accelerateDuration}
 * milliseconds, accelerate to firing clicks at {@link #config-endRate} times per second.
 *
 * An example of this is used by the {@link Core.widget.NumberField NumberField}'s
 * spinner triggers.
 */

class ClickRepeater extends Delayable(Base) {
  static get defaultConfig() {
    return {
      /**
       * The element on which to fire autorepeating `click` events when the mouse is held down.
       * @config {HTMLElement}
       */
      element: null,

      /**
       * A query selector which specifies subtargets of this ClickRepeater's {@link #config-element}
       * which act as the click auto repeat event targets.
       * @config {String}
       * @default
       */
      delegate: null,

      /**
       * How long in milliSeconds to pause before starting the click repeats.
       * @config {Number}
       * @default
       */
      delay: 500,

      /**
       * Clicks per second to start firing after the initial {@link #config-delay}
       * @config {Number}
       * @default
       */
      startRate: 2,

      /**
       * Clicks per second to fire at top speed, after accelerating over the {@link #config-accelerateDuration}
       * @config {Number}
       * @default
       */
      endRate: 20,

      /**
       * How long in milliseconds to accelerate from the
       * {@link #config-startRate} to the {@link #config-startRate}.
       * @config {Number}
       * @default
       */
      accelerateDuration: 4000
    };
  }

  doDestroy() {
    this.mousedownRemover && this.mousedownRemover();
    super.doDestroy();
  }

  set element(element) {
    this._element = element;
    this.mousedownRemover = EventHelper.on({
      element,
      mousedown: 'onMouseDown',
      thisObj: this
    });
  }

  get element() {
    return this._element;
  }

  onMouseDown(e) {
    const me = this,
          target = me.delegate ? e.target.closest(me.delegate) : me.element;

    if (target) {
      me.triggerEvent = e;
      EventHelper.lockComposedPath(me.triggerEvent);
      me.activeListenerRemover = EventHelper.on({
        mouseup: {
          element: document,
          handler: 'onMouseUp'
        },
        mousemove: {
          element: target,
          handler: 'onTargetMouseMove'
        },
        mouseleave: {
          element: target,
          handler: 'onTargetMouseLeave'
        },
        mouseenter: {
          element: target,
          handler: 'onTargetMouseEnter'
        },
        thisObj: me
      });
      me.setTimeout('startAutoRepeat', me.delay);
    }
  }

  onMouseUp(e) {
    const me = this;
    me.acceleration && me.acceleration.cancel();
    me.activeListenerRemover && me.activeListenerRemover();
    me.clearTimeout('startAutoRepeat');
    me.clearTimeout(me.repeatTimer);
  }

  onTargetMouseLeave() {
    this.clearInterval(this.repeatTimer);
  }

  onTargetMouseEnter(e) {
    this.triggerEvent = e;
    this.fireClick();
  }

  onTargetMouseMove(e) {
    this.triggerEvent = e;
  }

  startAutoRepeat() {
    const me = this;
    me.interval = me.startInterval = 1000 / me.startRate;
    me.accelerationDelta = me.startInterval - 1000 / me.endRate; // Begin animating the interval from 1000/me.startRate to 1000/me.endRate

    me.fireClick();
    me.acceleration = FunctionHelper.animate(me.accelerateDuration, me.nextTick, me, 'easeOutQuad');
  }

  nextTick(progress) {
    this.interval = this.startInterval - this.accelerationDelta * progress;
  }

  fireClick() {
    const me = this,
          clickEvent = new MouseEvent('click', me.triggerEvent);
    me.triggerEvent.target.dispatchEvent(clickEvent);
    me.repeatTimer = me.setTimeout(me.fireClick, me.interval);
  }

}
ClickRepeater._$name = 'ClickRepeater';

/**
 * @module Core/widget/Tool
 */

/**
 * Base class for tools.
 *
 * May be configured with a `cls` and a `handler` which is a function (or name of a function)
 * in the owning Panel.
 * @extends Core/widget/Widget
 *
 * @classType tool
 */

class Tool extends Widget {
  static get $name() {
    return 'Tool';
  } // Factoryable type name

  static get type() {
    return 'tool';
  } // Align is a simple string at this level

  static get configurable() {
    return {
      align: {
        value: null,
        $config: {
          merge: 'replace'
        }
      }
    };
  }

  changeAlign(align) {
    return align;
  }

  template() {
    return `<button class="b-icon b-align-${this.align || 'end'}"></button>`;
  }

  construct(config) {
    super.construct(config);
    EventHelper.on({
      element: this.element,
      click: 'onClick',
      thisObj: this
    });
  }

  get focusElement() {
    return this.element;
  }

  onClick(e) {
    const {
      handler,
      panel
    } = this;

    if (panel.trigger('toolclick', {
      domEvent: e,
      tool: this
    }) !== false) {
      handler && this.callback(handler, panel, [e]);
    }
  }

  onInternalKeyDown(keyEvent) {
    const keyName = keyEvent.key.trim() || keyEvent.code; // Don't allow key invocations to bubble and trigger owning
    // widget's key handlers.

    if (keyName === 'Enter') {
      keyEvent.cancelBubble = true;
      keyEvent.stopPropagation();
    }
  }

  get panel() {
    return this.parent;
  }

} // Register this widget type with its Factory

Tool.initClass();
Tool._$name = 'Tool';

/**
 * @module Core/widget/layout/Layout
 */

/**
  * A helper class used by {@link Core.widget.Container Container}s which renders child widgets to their
  * {@link Core.widget.Widget#property-contentElement}. It also adds the Container's
  * {@link Core.widget.Container#config-itemCls} class to child items.
  *
  * Subclasses may modify the way child widgets are rendered, or may offer APIs for manipulating the child widgets.
  *
  * The {@link Core.widget.layout.Card Card} layout class offers slide-in, slide-out animation of multiple
  * child widgets. {@link Core.widget.TabPanel} uses Card layout.
  */

class Layout extends Events(Base) {
  static get configurable() {
    return {
      /**
       * The CSS class which should be added to the owning {@link Core.widget.Container Container}'s
       * {@link Core.widget.Widget#property-contentElement}.
       * @config {String}
       */
      containerCls: 'b-auto-container',

      /**
       * The CSS class which should be added to the encapsulating element of child items.
       * @config {String}
       */
      itemCls: null
    };
  }

  static getLayout(layout, owner) {
    if (layout instanceof Layout) {
      layout.owner = this;
      return layout;
    }

    const config = {
      owner
    };

    if (typeof layout === 'string') {
      config.type = layout;
    } else {
      ObjectHelper.assign(config, layout);
    }

    return new layoutClasses[config.type || 'default'](config);
  }

  static registerLayout(cls, name = cls.$$name.toLowerCase()) {
    layoutClasses[name] = cls;
  }

  onChildAdd(item) {}

  onChildRemove(item) {}

  renderChildren() {
    const {
      owner,
      containerCls,
      itemCls
    } = this,
          {
      contentElement,
      items
    } = owner,
          ownerItemCls = owner.itemCls,
          itemCount = items && items.length;
    contentElement.classList.add('b-content-element');

    if (containerCls) {
      contentElement.classList.add(containerCls);
    } // Need to check that container has widgets, for example TabPanel can have no tabs

    if (itemCount) {
      owner.textContent = false;

      for (let i = 0; i < itemCount; i++) {
        const item = items[i],
              {
          element
        } = item;
        element.dataset.itemIndex = i;

        if (itemCls) {
          element.classList.add(itemCls);
        }

        if (ownerItemCls) {
          element.classList.add(ownerItemCls);
        } // If instantiated by the app developer, external to Container#createWidget
        // a widget will have the b-outer class. Remove that if it' contained.

        element.classList.remove('b-outer'); // Only trigger paint if the owner is itself painted, otherwise
        // the outermost Container will cascade the paint signal down.

        item.render(contentElement, Boolean(owner.isPainted));
      }
    }

    this.syncChildCount();
  }

  removeChild(child) {
    const {
      element
    } = child,
          {
      owner,
      itemCls
    } = this,
          {
      contentElement
    } = owner,
          ownerItemCls = owner.itemCls;
    contentElement.removeChild(element);
    delete element.dataset.itemIndex;

    if (itemCls) {
      element.classList.remove(itemCls);
    }

    if (ownerItemCls) {
      element.classList.remove(ownerItemCls);
    }

    this.fixChildIndices();
    this.syncChildCount();
  }

  appendChild(child) {
    const {
      element
    } = child,
          childIndex = this.owner.indexOfChild(child),
          {
      owner,
      itemCls
    } = this,
          {
      contentElement
    } = owner,
          ownerItemCls = owner.itemCls;
    element.dataset.itemIndex = childIndex;
    owner.textContent = false;

    if (itemCls) {
      element.classList.add(itemCls);
    }

    if (ownerItemCls) {
      element.classList.add(ownerItemCls);
    }

    child.render(contentElement, Boolean(owner.isPainted));
    this.syncChildCount();
  }

  insertChild(toAdd, childIndex) {
    const {
      element
    } = toAdd,
          {
      owner,
      itemCls
    } = this,
          {
      contentElement
    } = owner,
          nextSibling = contentElement.querySelector(`[data-item-index="${childIndex}"]`),
          ownerItemCls = owner.itemCls;
    owner.textContent = false;

    if (itemCls) {
      element.classList.add(itemCls);
    }

    if (ownerItemCls) {
      element.classList.add(ownerItemCls);
    }

    contentElement.insertBefore(element, nextSibling);
    toAdd.render(null, Boolean(owner.isPainted));
    this.fixChildIndices();
    this.syncChildCount();
  }

  fixChildIndices() {
    this.owner.items.forEach((child, index) => {
      child.element.dataset.itemIndex = index;
    });
  }

  syncChildCount() {
    const {
      owner
    } = this,
          {
      length
    } = owner.items; // Special CSS conditions may apply if there's only a single child.

    owner.contentElement.classList[length === 1 ? 'add' : 'remove']('b-single-child');
  }
  /**
   * The owning Widget
   * @property {String} owner
   * @readonly
   */

}
const layoutClasses = {
  default: Layout
};
Layout._$name = 'Layout';

let lastTouchTime = 0;

const hasRipple = w => w.ripple,
      Ripple = window.Ripple = new class Ripple extends Widget {
  static get defaultConfig() {
    return {
      id: 'bryntum-ripple',
      old_element: {
        children: [{
          className: 'b-ripple-inner',
          reference: 'rippleElement'
        }]
      },
      element: {
        children: [{
          tag: 'svg',
          class: 'b-ripple-inner',
          reference: 'rippleElement',
          ns: 'http://www.w3.org/2000/svg',
          version: '1.1',
          viewBox: '0 0 100 100',
          children: [{
            reference: 'circleElement',
            tag: 'circle',
            cx: '0',
            cy: '0',
            r: 10
          }]
        }]
      },
      floating: true,
      hideAnimation: false,
      showAnimation: false,
      scrollAction: 'realign',
      color: 'rgba(0,0,0,.3)',
      startRadius: 10,
      radius: 100
    };
  }

  static get $name() {
    return 'Ripple';
  }

  afterConstruct() {
    super.afterConstruct();
    EventHelper.on({
      element: document,
      mousedown: 'onRippleControllingEvent',
      thisObj: this,
      capture: true,
      once: true
    });
  }

  onRippleControllingEvent(event) {
    const me = this;
    me.show();
    const rippleAnimation = DomHelper.getStyleValue(me.circleElement, 'animationName');
    me.hide();
    me.listenerDetacher && me.listenerDetacher(); // If our theme supports ripples, add our listeners

    if (rippleAnimation && rippleAnimation !== 'none') {
      me.listenerDetacher = EventHelper.on({
        // Trap all mousedowns and see if the encapsulating Component is configured to ripple
        mousedown: {
          element: document,
          capture: true,
          handler: 'onDocumentMousedown'
        },
        touchstart: {
          element: document,
          capture: true,
          handler: 'onDocumentTouchStart'
        },
        // Hide at the end of the ripple
        animationend: {
          element: me.circleElement,
          handler: 'onAnimationEnd'
        },
        thisObj: me
      }); // If this is the first mousedown, start listening to theme changes and trigger ripple manually

      if (event.type === 'mousedown') {
        me.onDocumentMousedown(event);
        GlobalEvents.on({
          theme: 'onRippleControllingEvent',
          thisObj: this
        });
      }
    }
  }

  onDocumentTouchStart(event) {
    lastTouchTime = performance.now();
    this.handleTriggerEvent(event);
  }

  onDocumentMousedown(event) {
    // We need to prevent a touchend->mousedown simulated mousedown from triggering a ripple.
    // https://developer.mozilla.org/en-US/docs/Web/API/Touch_events/Supporting_both_TouchEvent_and_MouseEvent
    if (performance.now() - lastTouchTime > 200) {
      this.handleTriggerEvent(event);
    }
  }

  handleTriggerEvent(event) {
    const targetWidget = Widget.fromElement(event.target, hasRipple);

    if (targetWidget) {
      const rippleCfg = targetWidget.ripple,
            target = rippleCfg.delegate ? event.target.closest(rippleCfg.delegate) : targetWidget.focusElement || targetWidget.element;

      if (target) {
        const ripple = ObjectHelper.assign({
          event,
          target,
          radius: this.radius
        }, rippleCfg); // The clip option is specified as a string property name or delegate

        if (typeof ripple.clip === 'string') {
          ripple.clip = targetWidget[ripple.clip] || event.target.closest(ripple.clip); // Not inside an instance of the clip delegate, then no ripple

          if (!ripple.clip) {
            return;
          }
        }

        this.ripple(ripple);
      }
    }
  }

  ripple({
    event,
    point = EventHelper.getClientPoint(event),
    target = event.target,
    clip = target,
    radius = this.radius,
    color = this.color
  }) {
    this.clip = clip;
    clip = Rectangle.from(clip, null, true);
    const me = this,
          centreDelta = clip.getDelta(point),
          rippleStyle = me.rippleElement.style,
          circleElement = me.circleElement;
    me.hide();
    me.alignTo(clip);
    rippleStyle.transform = `translateX(${centreDelta[0]}px) translateY(${centreDelta[1]}px)`;
    rippleStyle.height = rippleStyle.width = `${radius}px`;
    circleElement.setAttribute('r', radius);
    circleElement.setAttribute('fill', color);
    me.show(); // Push binding to the scroll position out until the next animation frame.
    // This is in case the calling code is going to cause a scroll.
    // Mousedown is a focusing gesture which may cause a scroll
    // to fire as the target element moves into view.

    me.requestAnimationFrame(() => {
      document.addEventListener('scroll', me.callRealign, true);
      me.documentScrollListener = true;
    });
  }

  alignTo(clip) {
    if (clip.nodeType === 1) {
      clip = Rectangle.from(clip, null, true);
    }

    const me = this,
          {
      x,
      y,
      width,
      height
    } = clip;
    me.x = x;
    me.y = y;
    me.height = height;
    me.width = width;
  } // When fully expanded, it's all over.

  onAnimationEnd(event) {
    if (event.animationName === 'b-ripple-expand') {
      this.hide();
    }
  }

  realign() {
    if (this.isVisible) {
      this.alignTo(this.clip);
    }
  }

}();

/**
 * @module Core/widget/Container
 */

const emptyObject$2 = Object.freeze({}),
      emptyArray$5 = Object.freeze([]),
      returnWeight = i => i.weight,
      // Allowing string weights (used by subgrids for backwards compatibility)
sortByWeight = ({
  weight: a
}, {
  weight: b
}) => {
  if (typeof a === 'string' || typeof b === 'string') {
    return String(a).localeCompare(String(b));
  } // Items without weight sort last

  return (a !== null && a !== void 0 ? a : Number.MAX_SAFE_INTEGER) - (b !== null && b !== void 0 ? b : Number.MAX_SAFE_INTEGER);
},
      isNotHidden = w => !w.hidden,
      stylesToCheck = ['display', 'flex-direction'];
/**
 * Widget that can contain other widgets. Layout is flexbox by default, see the {@link #config-layout} config.
 *
 * ```javascript
 * // create a container with two widgets
 * let container = new Container({
 *     items : [
 *         { type : 'text', label : 'Name' },
 *         { type : 'number', label : 'Score' }
 *     ]
 * });
 * ```
 *
 * Containers can have child widgets added, or removed during their lifecycle to accommodate business needs.
 *
 * For example:
 *
 *  ```javascript
 *  myTaskPopup.on({
 *      beforeShow() {
 *          if (task.type === task.MASTER) {
 *              // Insert the childTask multiselect before the masterTask field
 *              myPopyup.insert(childTaskMultiselect, masterTaskField)
 *
 *              // We don't need this for master tasks
 *              myPopup.remove(masterTaskField);
 *          }
 *          else {
 *              // Insert the masterTask combo before the childTask multiselect
 *              myPopyup.insert(masterTaskField, childTaskMultiselect)
 *
 *              // We don't need this for child tasks
 *              myPopup.remove(childTaskMultiselect);
 *          }
 *      }
 *  });
 * ```
 *
 * @extends Core/widget/Widget
 * @classType container
 * @externalexample widget/Container.js
 */

class Container extends Widget {
  static get $name() {
    return 'Container';
  } // Factoryable type name

  static get type() {
    return 'container';
  }

  static get configurable() {
    return {
      /**
       * An object containing typed child widget config objects or Widgets. May also be specified
       * as an array.
       *
       * If configured as an Object, the property names are used as the child component's
       * {@link Core.widget.Widget#config-ref ref} name, and the value is the child component's config object.
       *
       * ```javascript
       *  new Panel({
       *      title    : 'Test Panel',
       *      floating : true,
       *      centered : true,
       *      width    : 600,
       *      height   : 400,
       *      layout   : 'fit',
       *      items    : {
       *          tabs : {
       *              type : 'tabpanel',
       *              items : {
       *                  general : {
       *                      title : 'General',
       *                      html  : 'General content'
       *                  },
       *                  details : {
       *                      title : 'Details',
       *                      html  : 'Details content'
       *                  }
       *              }
       *          }
       *      }
       *  }).show();
       * ```
       *
       * The order of the child widgets is determined by the order they are defined in `items`, but can also be
       * affected by configuring a {@link Core.widget.Widget#config-weight} on one or more widgets.
       *
       * @config {Object|Object[]|Core.widget.Widget[]}
       */
      items: null,

      /**
       * An array of {@link #config-items child item} _config objects_ which is to be converted into
       * instances only when this Container is rendered, rather than eagerly at construct time.
       *
       * _This is mutually exclusive with the {@link #config-items} config._
       *
       * @config {Object|Object[]|Core.widget.Widget[]}
       */
      lazyItems: {
        $config: ['lazy'],
        value: null
      },

      /**
       * A config object containing default settings to apply to all child widgets.
       * @config {Object}
       */
      defaults: null,
      defaultType: 'widget',

      /**
       * The CSS style properties to apply to the {@link Core.widget.Widget#property-contentElement}.
       *
       * By default, a Container's {@link Core.widget.Widget#property-contentElement} uses flexbox layout, so this
       * config may contain the following properties:
       *
       * - <a href="https://developer.mozilla.org/en-US/docs/Web/CSS/flex-direction">flexDirection</a> default '`row`'
       * - <a href="https://developer.mozilla.org/en-US/docs/Web/CSS/flex-wrap">flexWrap</a>
       * - <a href="https://developer.mozilla.org/en-US/docs/Web/CSS/flex-flow">flexFlow</a>
       * - <a href="https://developer.mozilla.org/en-US/docs/Web/CSS/justify-content">justifyContent</a>
       * - <a href="https://developer.mozilla.org/en-US/docs/Web/CSS/align-items">alignItems</a>
       * - <a href="https://developer.mozilla.org/en-US/docs/Web/CSS/align-content">alignContent</a>
       * - <a href="https://developer.mozilla.org/en-US/docs/Web/CSS/place-content">placeContent</a>
       * @config {Object}
       */
      layoutStyle: null,

      /**
       * An optional CSS class to add to child items of this container.
       * @config {String}
       */
      itemCls: null,

      /**
       * The short name of a helper class which manages rendering and styling of child items.
       *
       * By default, the only special processing that is applied is that the Container class's
       * {@link #config-itemCls} is added to child items.
       *
       * Containers use CSS flexbox in its default configuration to arrange child items. You may either use the
       * {@link #config-layoutStyle} configuration to tune how child items are layed out, or use one of the built
       * in helper classes which include:
       *
       *  - `card` Child items are displayed one at a time, size to fit the
       *  {@link Core.widget.Widget#property-contentElement} and are slid in from the side when activated.
       * @config {String}
       */
      layout: 'default',
      // TODO: deprecate namedItems, use items instead

      /**
       * An object containing named config objects which may be referenced by name in any {@link #config-items}
       * object. For example, a specialized {@link Core.widget.Menu Menu} subclass may have a `namedItems` default
       * value defined like this:
       *
       * ```javascript
       *  namedItems : {
       *      removeRow : {
       *          text : 'Remove row',
       *          onItem() {
       *              this.ownerGrid.remove(this.ownerGrid.selectedRecord);
       *          }
       *      }
       *  }
       * ```
       *
       * Then whenever that subclass is instantiated and configured with an {@link #config-items} object, the
       * items may be configured like this:
       *
       * ```javascript
       *  items : {
       *      removeRow : true,   // The referenced namedItem will be applied to this
       *      otherItemRef : {
       *          text : 'Option 2',
       *          onItem() {
       *          }
       *      }
       * }
       * ```
       * @config {Object}
       */
      namedItems: null,

      /**
       * Specify `true` for a container used to show text markup. It will apply the CSS class `b-text-content`
       * which specifies a default max-width that makes long text more readable.
       *
       * This CSS class is automatically removed if the container adds/defines child Widgets.
       * @config {Boolean}
       * @default
       */
      textContent: true,

      /**
       * Record whose values will be used to populate fields in the container.
       * @config {Object}
       * @internal
       */
      record: null,
      // NOTE: internal since record is not documented

      /**
       * Update assigned {@link #config-record} automatically on field changes
       * @config {Boolean}
       * @internal
       */
      autoUpdateRecord: null,

      /**
       * Specify `true` to make this container hide when it has no visible children (Either empty
       * or all children hidden).
       *
       * Container will show itself when there are visible children, ie: hidden children are
       * shown, or new visible childre are added.
       * @config {Boolean}
       * @default
       */
      hideWhenEmpty: null,
      contentElMutationObserver: {
        $config: ['lazy', 'nullify'],
        value: true
      }
    };
  }
  /**
   * Sets multiple flexbox settings which affect how child widgets are arranged.
   *
   * By default, Containers use flexbox layout, so this property may contain the following properties:
   *
   * - <a href="https://developer.mozilla.org/en-US/docs/Web/CSS/flex-direction">flexDirection</a> default '`row`'
   * - <a href="https://developer.mozilla.org/en-US/docs/Web/CSS/flex-wrap">flexWrap</a>
   * - <a href="https://developer.mozilla.org/en-US/docs/Web/CSS/flex-flow">flexFlow</a>
   * - <a href="https://developer.mozilla.org/en-US/docs/Web/CSS/justify-content">justifyContent</a>
   * - <a href="https://developer.mozilla.org/en-US/docs/Web/CSS/align-items">alignItems</a>
   * - <a href="https://developer.mozilla.org/en-US/docs/Web/CSS/align-content">alignContent</a>
   * - <a href="https://developer.mozilla.org/en-US/docs/Web/CSS/place-content">placeContent</a>
   * @member {Object} layoutStyle
   * @category Layout
   */

  /**
   * The {@link Core.data.Model record} to be applied to the fields contained in this Container. Any descendant
   * widgets of this Container with a `name` property will have its value set to the value of that named property of
   * the record. If no record is passed, the widget has its value set to `null`.
   * @member {Core.data.Model} record
   */

  startConfigure(config) {
    // Set a flag so that code can test for presence of items.
    // Widgets which render child widgets outside of the Container scheme
    // can set this flag (eg Panels with tools and tbar).
    const items = config.items || config.lazyItems;
    this.hasItems = Boolean(items && (Array.isArray(items) ? items : Object.keys(items)).length);
    super.startConfigure(config);
  }
  /**
   * Removes the passed child/children from this Container.
   * @param  {...Core.widget.Widget} toRemove The child or children to remove.
   * @returns {Core.widget.Widget|Core.widget.Widget[]} All the removed items. An array if multiple items
   * were removed, otherwise, just the item removed.
   */

  remove(...toRemove) {
    let returnArray = true;

    if (toRemove.length === 1) {
      if (Array.isArray(toRemove[0])) {
        toRemove = toRemove[0];
      } else {
        returnArray = false;
      }
    }

    const me = this,
          {
      _items
    } = me,
          result = [];

    for (let i = 0; i < toRemove.length; i++) {
      const childToRemove = toRemove[i];

      if (_items.includes(childToRemove)) {
        _items.remove(childToRemove);

        me.layout.removeChild(childToRemove);
        result.push(childToRemove);
        me.onChildRemove(childToRemove);
      }
    }

    return returnArray ? result : result[0];
  }
  /**
   * Removes all children from this Container.
   * @returns {Core.widget.Widget[]} All the removed items.
   */

  removeAll() {
    return this.remove(this.items);
  }
  /**
   * Appends the passed widget/widgets to this Container.
   *
   * If the widgets specify a `weight`, they are inserted at the correct index compared to the existing items weights.
   *
   * @param  {...Core.widget.Widget} toAdd The child or children to add.
   * @returns {Core.widget.Widget|Core.widget.Widget[]} All the added widgets. An array if multiple items
   * were added, otherwise, just the item added.
   */

  add(...toAdd) {
    let returnArray = true;

    if (toAdd.length === 1) {
      if (Array.isArray(toAdd[0])) {
        toAdd = toAdd[0];
      } else {
        returnArray = false;
      }
    } // Force creation of our items Bag

    if (!this.items) {
      this.items = [];
    }

    const me = this,
          {
      _items
    } = me,
          result = [];

    for (let i = 0; i < toAdd.length; i++) {
      let childToAdd = toAdd[i]; // Items with weight are inserted at correct index

      if (childToAdd.weight != null) {
        // Cannot use cached _items, weights might be unordered in set being added
        const index = ArrayHelper.findInsertionIndex(childToAdd, me._items.values, sortByWeight);
        result.push(me.insert(childToAdd, index));
      } // Those without are appended
      else {
          if (!(childToAdd instanceof Widget)) {
            childToAdd = me.createWidget(childToAdd);
          } else {
            childToAdd.parent = me;
          }

          if (!_items.includes(childToAdd)) {
            _items.add(childToAdd);

            me.onChildAdd(childToAdd);
            me.layout.appendChild(childToAdd);
            result.push(childToAdd);
          }
        }
    }

    return returnArray ? result : result[0];
  }
  /**
   * Inserts the passed widget into this Container at the specified position.
   * @param  {Core.widget.Widget} toAdd The child to insert.
   * @param {Number|Core.widget.Widget} The index to insert at or the existing child to insert before.
   * @returns {Core.widget.Widget} The added widget.
   */

  insert(toAdd, index) {
    // Force creation of our items Bag
    if (!this.items) {
      this.items = [];
    }

    const me = this,
          {
      _items
    } = me;

    if (toAdd instanceof Widget) {
      toAdd.parent = me;
    } else {
      toAdd = me.createWidget(toAdd);
    }

    if (_items.includes(index)) {
      index = me.indexOfChild(index);
    }

    index = Math.min(index, _items.count);
    const newValues = _items.values;
    newValues.splice(index, 0, toAdd);
    _items.values = newValues; // Register inserted item

    me.onChildAdd(toAdd);
    me.layout.insertChild(toAdd, index);
    return toAdd;
  }

  indexOfChild(child) {
    return this.items.indexOf(child);
  }

  changeLazyItems(lazyItems) {
    this.items = lazyItems;
    this.layout.renderChildren();
  }

  changeItems(items, oldItems) {
    const me = this,
          newItems = [],
          result = new Bag();

    if (Array.isArray(items)) {
      me.processItemsArray(items, newItems);
    } else if (items) {
      me.processItemsObject(items, me.namedItems, newItems);
    } // Allow child items to have a weight to establish their order

    if (newItems.some(returnWeight)) {
      newItems.sort(sortByWeight);
    }

    result.add(newItems); // Remove previous child payload if any

    if (oldItems) {
      oldItems.forEach(w => {
        me.remove(w); // Destroy outgoing-only widgets which we created.

        if (!result.includes(w) && w._createdBy === me) {
          w.destroy();
        }
      });
    }

    return result;
  }

  updateItems(items, oldItems) {
    const me = this;
    let index = 0;
    items.forEach(function (item) {
      me.onChildAdd(item); // If this is *change* to items

      if (oldItems) {
        me.layout.insertChild(item, index);
      }

      index++;
    });
  }

  updateHideWhenEmpty() {
    this.syncChildCount();
  }
  /**
   * A property, which, when *read*, yields an array of the child items of this container in rendered order.
   *
   * This property may also be *set* to change the child items of the container. Just as in the
   * {@link #config-items initial items configuration}, the new value may either be an array of
   * Widgets/Widget configs or an object.
   *
   * If specified as an Object, the property names are used as the child Widget's
   * {@link Core.widget.Widget#config-ref ref} name, and the value is the child Widget/Widget config.
   *
   * When setting this, any items which are *only* in the outgoing child items which were created
   * by this container from raw config objects are destroyed.
   *
   * Usage patterns:
   *
   * ```javascript
   * myContainer.items = {
   *     name : {
   *         type  : 'textfield',
   *         label : 'User name'
   *     },
   *     age : {
   *         type  : 'numberfield',
   *         label : 'User age'
   *     }
   * };
   * ```
   *
   * or
   *
   * ```javascript
   * myContainer.items = [{
   *     ref   : 'name',
   *     type  : 'textfield',
   *     label : 'User name'
   * },
   *     ref   : 'age',
   *     type  : 'numberfield',
   *     label : 'User age'
   * }];
   * ```
   * @property {Core.widget.Widget[]|Object}
   */

  get items() {
    // If we are being asked for items, ingest lazyItems.
    this.getConfig('lazyItems'); // The documented API for items is an Array.
    // Internal code should access _items

    if (!this._items) {
      if (this.initializingItems) {
        // This is a created array. User may mutate it.
        return [];
      }

      this.items = emptyArray$5;
    } // This is the Collection's array. User may not mutate it.

    return this._items.values;
  }

  processItemsArray(items, result) {
    const len = items.length;
    let i, item;

    for (i = 0; i < len; i++) {
      item = items[i];

      if (item instanceof Widget) {
        item.parent = this;
        DomHelper.removeClasses(item.element, Widget.outerCls);
      } else {
        item = this.createWidget(item);
      } // If the widget creation function returns null, nothing to add

      if (item) {
        result.push(item);
      }
    }
  }

  processItemsObject(items, namedItems = emptyObject$2, result) {
    let item, ref;

    for (ref in items) {
      item = items[ref]; // It might come in as itemRef : false

      if (item) {
        // If this class or instance has a "namedItems" object
        // named by this ref, then use it as the basis for the item
        if (ref in namedItems) {
          item = typeof item === 'object' ? ObjectHelper.merge(ObjectHelper.clone(namedItems[ref]), item) : namedItems[ref];
        } // Allow namedItems to be overridden with itemKey : false to indicate unavailability of an item

        if (item) {
          if (item instanceof Widget) {
            item.parent = this;
          } else {
            if (item instanceof Object) {
              item.ref = ref;
            }

            item = this.createWidget(item);
          } // If the widget creation function returns null, nothing to add

          if (item) {
            item.ref = ref;
            result.push(item);
          }
        }
      }
    }
  }

  onChildAdd(item) {
    const ref = item.ref || item.id; // Don't just assign the property across since the default value is undefined
    // which means false. Only set to true if we are readOnly

    if (this.readOnly) {
      item.readOnly = true;
    } // Keep layout informed of child item state

    this.layout.onChildAdd(item);

    if (ref) {
      for (let current = this; current; current = current.parent) {
        // Silently add the descendant to the ancestor's widgetMap without kicking off
        // the ancestor's items processing by directly accessing the widgetMap property.
        current.addDescendant(item, ref);
      }
    }

    this.syncChildCount();
  }

  onChildHide() {
    this.syncChildCount();
  }

  onChildShow() {
    this.syncChildCount();
  }

  syncChildCount() {
    // If called during configuration, the element may not be available because
    // some non-item widgets (eg, Tools) instantiate and declare themselves through
    // onChildAdd before the element is set.
    // But also there may be no items because of     lazyItems.
    // So do the initial sync at render time.
    if (!this.isConfiguring) {
      const me = this,
            {
        childItems
      } = me,
            {
        length
      } = childItems,
            {
        length: visibleLength
      } = childItems.filter(isNotHidden);

      if (me.hideWhenEmpty) {
        const shouldHide = Boolean(!visibleLength);

        if (Boolean(me._hidden) !== shouldHide) {
          me.hidden = shouldHide;
        }
      } // Keep hasItems property up to date. It's used by the isFocusable getter.

      me.hasItems = Boolean(length);
      me.contentElement.classList[visibleLength ? 'remove' : 'add']('b-no-visible-children'); // Reevaluate whether we should have the b-text-content class

      me.updateTextContent(me._textContent);
    }
  }

  syncFlexDirection() {
    const me = this,
          {
      contentElement
    } = me,
          classList = new DomClassList(contentElement.className),
          styles = DomHelper.getStyleValue(contentElement, stylesToCheck); // We might not be flexing at all any more.

    classList['b-flex-row'] = classList['b-flex-column'] = 0; // If we are, add a flag class to indicate direction

    if (styles.display === 'flex') {
      classList[`b-flex-${styles['flex-direction']}`] = 1;
    } // Will only mutate the DOM if there are changes to apply.
    // We don't wang to cause an infinite loop through our MutationObserver.

    DomHelper.syncClassList(contentElement, classList);
  }

  addDescendant(item, ref) {
    const widgetMap = this._widgetMap || (this._widgetMap = {});

    if (!widgetMap[ref]) {
      widgetMap[ref] = item;
    }
  }

  onChildRemove(item) {
    const ref = item.ref || item.id;

    if (ref) {
      for (let current = this; current; current = current.parent) {
        if (current.widgetMap[ref] === item) {
          delete current.widgetMap[ref];
        }
      }
    } // Keep layout informed of child item state

    this.layout.onChildRemove(item);
    this.syncChildCount();
  }
  /**
   * An object which contains a map of descendant widgets keyed by their {@link Core.widget.Widget#config-ref ref}.
   * All descendant widgets will be available in the `widgetMap`.
   * @property {Object}
   * @readonly
   * @typings any
   */

  get widgetMap() {
    if (!this._widgetMap) {
      this._widgetMap = {};
    } // Force evaluation of the configured items by the getter
    // so that configs are promoted to widgets and the widgetMap
    // is created, and if there are widgets, populated.

    if (!this.initializingItems) {
      this.getConfig('items');
    }

    return this._widgetMap;
  }

  changeRecord(record) {
    // The config system's non-change vetoing must be bypassed.
    // The record might have changed, or the destination fields may be out of sync.
    this._record = record == null ? emptyObject$2 : null;
    return record;
  }

  updateRecord(record) {
    /**
     * Fired before this container will load record values into its child fields. This is useful if you
     * want to modify the UI before data is loaded (e.g. set some input field to be readonly)
     * @event beforeSetRecord
     * @param {Core.widget.Container} source The container
     * @param {Core.data.Model} record The record
     */
    this.trigger('beforeSetRecord', {
      record
    });
    this.setValues(record, true, !this.$highlight);
  }

  setRecord(record, highlightChanges) {
    this.$highlight = highlightChanges;
    this.record = record;
    this.$highlight = false;
  }
  /**
   * A function called by descendant widgets after they trigger their 'change' event, in reaction to field changes.
   * By default implements the functionality for the `autoUpdateRecord` config.
   *
   * @param {Object} params Normally the event params used when triggering the 'change' event
   * @internal
   */

  onFieldChange({
    source,
    userAction
  }) {
    // When configured with `autoUpdateRecord`, changes from descendant fields/widgets are applied to the loaded
    // record using the fields `name`. Only changes from valid fields will be applied
    if (this.autoUpdateRecord) {
      const {
        record
      } = this,
            {
        name,
        isValid,
        value
      } = source;

      if (record && userAction && name && isValid) {
        record[name] = value;
      }
    }
  }

  getValues(filterFn) {
    const me = this,
          widgets = me.queryAll(w => w.name),
          len = widgets.length,
          result = {};

    for (let i = 0; i < len; i++) {
      const widget = widgets[i],
            name = widget.name;

      if (!filterFn || filterFn(widget)) {
        result[name] = widget.value;
      }
    }

    return result;
  }

  get hasNoChildren() {
    // If we have *uningested* lazyItems, use them to find our items length.
    // In that case we must not cause ingestion of the lazyItems by referencing this.items.
    // If we have items, then only those that are visible have any bearing.
    // Popup also has to consult this property to decide on its CSS classes.
    const {
      lazyItems
    } = this.initialConfig,
          lazyItemsArray = lazyItems && (Array.isArray(lazyItems) ? lazyItems : Object.values(lazyItems)),
          {
      length
    } = (lazyItemsArray && !this._items ? lazyItemsArray : this.items).filter(isNotHidden);
    return !length;
  }

  updateTextContent(textContent) {
    // Add the text content class if we have no visible immediate item children.
    this.contentElement.classList[textContent && this.hasNoChildren ? 'add' : 'remove']('b-text-content'); // Depending on CSS settings around how this widget handles text content and widthing
    // we *may* have to realign. Realign only goes ahead if we're visible and floating.

    if (this.rendered) {
      this.realign();
    }
  }

  updateLayoutStyle(layoutStyle) {
    DomHelper.applyStyle(this.contentElement, layoutStyle);
  }

  onPaint() {
    // Bring the lazy config into existence now that we have a layout.
    this.getConfig('contentElMutationObserver');
  }

  changeContentElMutationObserver(contentElMutationObserver, oldContentElMutationObserver) {
    const me = this,
          {
      element,
      contentElement
    } = me;

    if (oldContentElMutationObserver) {
      // Clear the queue. Any remaining notifications will be undeliverable.
      oldContentElMutationObserver.takeRecords();
      oldContentElMutationObserver.disconnect();
    } // We need to monitor our contentElement for attribute changes.
    // Changes to the inline style, or the classList/className will trigger this.
    // At that point we can sync our layout flag classes.

    if (contentElMutationObserver) {
      contentElMutationObserver = new MutationObserver(mutations => me.onContentElMutation(mutations));
      contentElMutationObserver.observe(contentElement, {
        attributes: true
      }); // If our contentElement is an inner element (eg Panel, Toolbar)
      // then styling might apply from classes on the outer element
      // so monitor that too.

      if (contentElement !== element) {
        contentElMutationObserver.observe(element, {
          attributes: true
        });
      }

      me.syncFlexDirection();
    }

    return contentElMutationObserver;
  }

  onContentElMutation(mutations) {
    // We can only do it if we are visible, otherwise the computed style won't work
    // and the classes will be removed.
    if (this.isVisible) {
      this.syncFlexDirection();
    }
  }

  changeLayout(layout) {
    // TODO: Layouts to be Factoryable
    return Layout.getLayout(layout, this);
  } // Items to iterate over

  get childItems() {
    return this.items;
  }
  /**
   * Returns a directly contained widget by id
   * @param {String} id The widget id
   * @returns {Core.widget.Widget}
   */

  getWidgetById(id) {
    return this.widgetMap[id];
  }
  /**
   * This function is called prior to creating widgets, override it in subclasses to allow containers to modify the
   * configuration of each widget. When adding a widget to a container hierarchy each parent containers
   * `processWidgetConfig` will be called. Returning false from the function prevents the widget from being added at
   * all.
   */

  processWidgetConfig(widget) {}
  /**
   * This method combines container {@link #config-defaults}
   * @param {Object} widgetConfig
   * @param {String|Function} [type] The type of widget described by `widgetConfig`.
   * @returns {Object}
   * @internal
   */

  setupWidgetConfig(widgetConfig, type) {
    const me = this; // A string becomes the defaultType (see below) with the html set to the string.

    if (typeof widgetConfig === 'string') {
      widgetConfig = {
        html: widgetConfig
      };
    } // An element is encapsulated by a Widget
    else if (widgetConfig.nodeType === 1) {
        widgetConfig = {
          element: widgetConfig,
          id: widgetConfig.id
        };
      }

    if (typeof type === 'string' || !type && (type
    /* assignment */
    = widgetConfig.type)) {
      type = Widget.resolveType(type, true);
    } // widgetConfig = ObjectHelper.assign({}, me.defaults, widgetConfig, { parent : me });

    widgetConfig = (type || Widget).mergeConfigs(me.defaults, widgetConfig, {
      parent: me
    });

    for (let ancestor = widgetConfig.parent; ancestor; ancestor = ancestor.parent) {
      if (ancestor.processWidgetConfig(widgetConfig) === false) {
        return null;
      }
    }

    if (me.trigger('beforeWidgetCreate', {
      widgetConfig
    }) === false) {
      return null;
    }

    return widgetConfig;
  }
  /**
   * This function converts a Widget config object into a Widget.
   * @param {Object} widget A Widget config object.
   * @internal
   */

  createWidget(widget) {
    const result = Widget.create(this.setupWidgetConfig(widget), this.defaultType); // If we created a widget from a config object, then upon items replacement
    // we must destroy outgoing widgets.

    result && (result._createdBy = this);
    return result;
  } // Reapply defaults, not used during config

  updateDefaults(defaults, oldDefaults) {
    if (!this.isConfiguring) {
      const entries = Object.entries(defaults);
      this.eachWidget(widget => {
        entries.forEach(([prop, value]) => {
          // Apply defaults only if current value matches the old default
          if (widget[prop] === oldDefaults[prop]) {
            widget[prop] = value;
          }
        });
      }, false);
    }
  }

  render() {
    // Pull in lazyItems at last second
    this.getConfig('lazyItems');
    this.layout.renderChildren(); // If called during configuration, the element may not have been available because
    // some non-item widgets (eg, Tools) instantiate and declare themselves through
    // onChildAdd before the element is set.
    // But also there may have been no items because of lazyItems.
    // So do the initial sync at render time.

    this.syncChildCount();
    super.render(...arguments);
  }

  get focusElement() {
    // Find first focusable descendant widget that is not our close tool.
    const firstFocusable = this.query(this.defaultFocus || (w => w.isFocusable && w.ref !== 'close'));

    if (firstFocusable) {
      return firstFocusable.focusElement;
    }

    return super.focusElement;
  }

  doDestroy() {
    const {
      _items
    } = this;
    _items && _items.forEach(widget => widget.destroy && widget.destroy());
    super.doDestroy();
  }
  /**
   * Returns `true` if all contained fields are valid, otherwise `false`
   * @property {Boolean}
   */

  get isValid() {
    let valid = true;
    this.eachWidget(widget => {
      // Touch each widget so that they all update their invalid state. This is important for required fields
      // since they don't initially complain if they start as empty.
      if ('isValid' in widget && !widget.isValid) {
        valid = false;
      }
    }, true);
    return valid;
  }
  /**
   * Retrieves or sets all values from/to contained fields.
   * Accepts and returns a map, using {@link Core.widget.Field#config-name name},
   * {@link Core.widget.Widget#config-ref ref} or {@link Core.widget.Widget#config-id id} (in that order) as keys.
   *
   * ```javascript
   * container.values = {
   *     firstName : 'Clark',
   *     surname : 'Kent'
   * };
   * ```
   *
   * @property {Object}
   */

  get values() {
    const values = {};
    this.eachWidget(widget => {
      if ('value' in widget) {
        values[widget.name || widget.ref || widget.id] = widget.value;
      }
    }, true);
    return values;
  }

  set values(values) {
    this.setValues(values);
  }
  /**
   * Returns `true` if currently setting values. Allows fields change highlighting to distinguishing between initially
   * setting values and later on changing values.
   * @property {Boolean}
   */

  get isSettingValues() {
    var _this$parent;

    // Fields query their parent, pass the question up in case containers are nested
    return Boolean(this._isSettingValues || ((_this$parent = this.parent) === null || _this$parent === void 0 ? void 0 : _this$parent.isSettingValues));
  }

  setValues(values, onlyName = false, preventHighlight = true) {
    // Flag checked by Field to determine if it should highlight change or not
    this._isSettingValues = {
      preventHighlight
    };
    this.eachWidget(widget => {
      const hec = widget.highlightExternalChange,
            key = onlyName ? widget.name : widget.name || widget.ref || widget.id;

      if ('value' in widget && key) {
        if (preventHighlight) {
          // Don't want a field highlight on mass change
          widget.highlightExternalChange = false;
        } // Setting to null when value not matched clears field

        widget.value = values && key in values ? values[key] : null;
        widget.highlightExternalChange = hec;
      }
    }, true);
    this._isSettingValues = false;
  }

} // Register this widget type with its Factory

Container.initClass();
Container._$name = 'Container';

/**
 * @module Core/widget/mixin/Badge
 */

/**
 * Mixin that allows a widget to display a badge (mostly done as css)
 *
 * @example
 * // show badge
 * button.badge = 5;
 *
 * // hide badge
 * button.badge = null;
 *
 * @externalexample widget/Badge.js
 *
 * @mixin
 */

var Badge = (Target => class Badge extends (Target || Base) {
  static get $name() {
    return 'Badge';
  }

  static get configurable() {
    return {
      /**
       * Get/sets and display badge, set to null or empty string to hide.
       * @property {String}
       * @name badge
       */

      /**
       * Initial text to show in badge.
       * @config {String} badge
       */
      badge: null
    };
  }

  updateBadge(badge) {
    const {
      element
    } = this;

    if (element) {
      if (badge != null && badge !== '') {
        element.dataset.badge = badge;
        element.classList.add('b-badge');
      } else {
        if (element.dataset.badge) {
          delete element.dataset.badge;
        }

        element.classList.remove('b-badge');
      }
    }
  } // Each *Class* which doesn't need 'b-' + constructor.name.toLowerCase() automatically adding
  // to the Widget it's mixed in to should implement thus.

  get widgetClass() {
    // If we don't have a badge, our classList doesn't include "b-badge"
    if (this.badge) {
      return 'b-badge';
    }
  }

});

//TODO: A toggling widget's focusElement should be an opacity:0 input type=checkbox which covers the clickable area.

/**
 * @module Core/widget/Button
 */

const bIcon = /^b-icon-/,
      bFa = /^b-fa-/;
/**
 * Button widget, wraps and styles a regular <code>&lt;button&gt;</code> element. Can display text and icon and allows specifying button color.
 *
 * @extends Core/widget/Widget
 * @mixes Core/widget/mixin/Badge
 *
 * @example
 * // button with text and icon
 * let button = new Button({
 *   icon: 'b-fa-plus-circle',
 *   text: 'Add',
 *   color: 'green',
 *   onClick: () => {}
 * });
 *
 * @classType button
 * @externalexample widget/Button.js
 */

class Button extends Badge(Widget) {
  //region Config
  static get $name() {
    return 'Button';
  } // Factoryable type name

  static get type() {
    return 'button';
  }

  static get configurable() {
    return {
      /**
       * Get/set the Button icon
       * @property {String}
       * @name icon
       */

      /**
       * Button icon class.
       *
       * All [Font Awesome](https://fontawesome.com/cheatsheet) icons may also be specified as `'b-fa-' + iconName`.
       *
       * Otherwise this is a developer-defined CSS class string which results in the desired icon.
       * @config {String}
       */
      icon: null,

      /**
       * Get/set the Button pressed icon
       * @property {String}
       * @name pressedIcon
       */

      /**
       * Icon class for the buttons pressed state. Only applies to toggle buttons
       *
       * All [Font Awesome](https://fontawesome.com/cheatsheet) icons may also be specified as `'b-fa-' + iconName`.
       *
       * Otherwise this is a developer-defined CSS class string which results in the desired icon.
       *
       * ```
       * new Button({
       *    // Icon for unpressed button
       *    icon        : 'b-fa-wine-glass',
       *
       *    // Icon for pressed button
       *    pressedIcon : 'b-fa-wine-glass-alt',
       *
       *    // Only applies to toggle buttons
       *    toggleable  : true
       * });
       * ```
       *
       * @config {String}
       */
      pressedIcon: null,

      /**
       * Get/Set the Button icon alignment.
       * May be `'start'` or `'end'`. Defaults to `'start'`
       * @property {String}
       * @name iconAlign
       */

      /**
       * Button icon alignment. May be `'start'` or `'end'`. Defaults to `'start'`
       * @config {String}
       */
      iconAlign: 'start',

      /**
       * Get/set text displayed on the button.
       * @property {String}
       * @name text
       */

      /**
       * The button's text.
       * @config {String}
       */
      text: '',

      /**
       * Button color (should have match in button.scss or your custom styling). Valid values in Bryntum themes
       * are:
       * * b-amber
       * * b-blue
       * * b-dark-gray
       * * b-deep-orange
       * * b-gray
       * * b-green
       * * b-indigo
       * * b-lime
       * * b-light-gray
       * * b-light-green
       * * b-orange
       * * b-purple
       * * b-red
       * * b-teal
       * * b-white
       * * b-yellow
       * Combine with specifying `b-raised` for raised/filled style (theme dependent).
       *
       * ```
       * new Button({
       *    color : 'b-teal b-raised'
       * });
       * ```
       *
       * @config {String}
       */
      color: null,

      /**
       * Enabled toggling of the button (stays pressed when pressed).
       * @config {Boolean}
       * @default
       */
      toggleable: false,

      /**
       * Get/set button pressed state
       * @property {Boolean}
       * @name pressed
       */

      /**
       * Initially pressed or not. Only applies with `toggleable = true`.
       * ```
       * const toggleButton = new Button({
       *    toggleable : true,
       *    text : 'Enable cool action'
       * });
       * ```
       * @config {Boolean}
       * @default
       */
      pressed: false,

      /**
       * Indicate that this button is part of a group where only one button can be pressed. Assigning a value
       * also sets `toggleable` to `true`.
       * ```
       * const yesButton = new Button({
       *    toggleGroup : 'yesno',
       *    text        : 'Yes'
       * });
       *
       * const noButton = new Button({
       *    toggleGroup : 'yesno',
       *    text        : 'No'
       * });
       * ```
       * @config {String}
       */
      toggleGroup: null,
      ripple: {
        radius: 75
      },
      defaultBindProperty: 'text',
      localizableProperties: ['text'],

      /**
       * Returns the instantiated menu widget as configured by {@link #config-menu}.
       * @property {Core.widget.Widget}
       * @name menu
       */

      /**
       * A submenu configuration object, or an array of MenuItem configuration
       * objects from which to create a submenu which is shown when this button is pressed.
       *
       * Note that this does not have to be a Menu. The `type` config can be used
       * to specify any widget as the submenu.
       *
       * May also be specified as a fully instantiated {@link Core.widget.Widget#config-floating floating Widget}
       * such as a {@link Core.widget.Popup Popup}.
       * @config {Object|Object[]|Core.widget.Widget}
       */
      menu: {
        $config: ['lazy', 'nullify'],
        value: null
      },
      menuDefaults: {
        type: 'menu',
        autoShow: false,
        autoClose: true,
        floating: true,
        scrollAction: 'realign',
        align: 't0-b0'
      },

      /**
       * If provided, turns the button into a link
       * @config {String}
       */
      href: null,

      /**
       * The `target` attribute for the {@link #config-href} config
       * @config {String}
       */
      target: null,
      onMenuHideTimeout: 300,
      testConfig: {
        onMenuHideTimeout: 0
      }
    };
  }

  template() {
    const me = this,
          tag = me.href ? 'a' : 'button';
    return TemplateHelper.tpl`
            <${tag} ${tag === 'a' ? `href="${me.href}"` : ''} ${tag === 'a' && me.target ? `target="${me.target}"` : ''} class="${me.color || ''} ${me.toggleable && me.pressed ? 'b-pressed' : ''}" ${me.toggleGroup ? `data-group="${me.toggleGroup}"` : ''}>
                <label data-reference="label">\xa0</label>
            </${tag}>
        `;
  } //endregion
  //region Construct/Destroy

  construct(config = {}, ...args) {
    if (config.toggleGroup || config.menu) {
      config.toggleable = true;
    }

    super.construct(config, ...args);
    EventHelper.on({
      element: this.element,
      click: 'onInternalClick',
      thisObj: this
    });
  } //endregion

  /**
   * Iterate over all widgets owned by this widget and any descendants.
   *
   * *Note*: Due to this method aborting when the function returns `false`, beware of using short form arrow
   * functions. If the expression executed evaluates to `false`, iteration will terminate.
   *
   * _Due to the {@link #config-menu} config being a lazy config and only being converted to be a
   * `Menu` instance just before it's shown, the menu will not be part of the iteration before
   * it has been shown once_.
   * @function eachWidget
   * @param {Function} fn A function to execute upon all descendant widgets.
   * Iteration terminates if this function returns `false`.
   * @param {Boolean} [deep=true] Pass as `false` to only consider immediate child widgets.
   * @returns {Boolean} Returns `true` if iteration was not aborted by a step returning `false`
   */

  get childItems() {
    if (this._menu) {
      return [this.menu];
    }
  }

  onFocusOut(e) {
    super.onFocusOut(e);

    if (this.menu) {
      this.menu.hide();
    }
  } //region Getters/Setters

  get focusElement() {
    return this.element;
  }

  changeText(text) {
    return text == null ? '' : text;
  }

  updateText(text) {
    const {
      element,
      label
    } = this;
    label.innerText = text;
    element.classList[String(text).length ? 'add' : 'remove']('b-text');
  }

  get menuContainerElement() {
    return document.body;
  }

  changeMenu(menu, oldMenu) {
    const me = this;

    if (menu) {
      // In case the reason for the hide is a mousedown
      // on this button, and it's not hiding because we are being destroyed,
      // wait until after any impending click handler to sync our state with the visibility.
      const onHide = () => !me.isDestroying && me.setTimeout(() => me.toggle(false), me.onMenuHideTimeout);

      if (menu.isWidget) {
        menu.forElement = me.element;
        menu.on('hide', onHide);
      } else {
        // This covers both Array and Object which are valid items config formats.
        // menu could be { itemRef : { text : 'sub item 1 } }. But if it has
        // child items or html property in it, it's the main config
        if (typeof menu === 'object' && !('type' in menu || 'items' in menu || 'widgets' in menu || 'html' in menu)) {
          menu = {
            lazyItems: menu
          };
        }

        menu = Widget.reconfigure(oldMenu, menu ? Objects.merge({
          constrainTo: me.menuContainerElement,
          forElement: me.element,
          owner: me,
          onHide
        }, me.menuDefaults, menu) : null, me);
      }
    } else {
      if (oldMenu) {
        oldMenu.destroy();
      }
    }

    return menu;
  }

  updateMenu(menu) {
    // We are toggleable if there's a menu.
    // Pressed means menu visible, not pressed means menu hidden.
    this.toggleable = Boolean(menu);
  }

  updatePressed(pressed) {
    const me = this;

    if (pressed && me.toggleGroup) {
      DomHelper.forEachSelector(`button[data-group=${me.toggleGroup}]`, btnEl => {
        if (btnEl !== me.element) {
          Widget.getById(btnEl.id).toggle(false);
        }
      });
    }

    if (me.element) {
      me.element.classList[pressed ? 'add' : 'remove']('b-pressed');
    }
  }

  updateIcon() {
    this.syncIconCls();
  }

  updatePressedIcon() {
    this.syncIconCls();
  }

  updateIconAlign(iconAlign, oldAlign) {
    const classList = this.element.classList;

    if (oldAlign) {
      classList.remove(`b-icon-align-${oldAlign}`);
    }

    if (this.icon) {
      classList.add(`b-icon-align-${iconAlign}`);
    }
  } //endregion
  //region Events

  /**
   * Triggers events when user clicks button
   * @fires click
   * @fires action
   * @private
   */

  onInternalClick(event) {
    const me = this,
          bryntumEvent = {
      event
    };

    if (me.toggleable) {
      // Clicking the pressed button in a toggle group should do nothing
      if (me.toggleGroup && me.pressed) {
        return;
      }

      me.toggle(!me.pressed); // Edge case in dragfromgrid demo, where toggling mode destroys the Scheduler and thus destroys the toolbar
      // and the button in it

      if (me.isDestroyed) {
        return;
      }
    }
    /**
     * User clicked button
     * @event click
     * @property {Core.widget.Button} button - Clicked button
     * @property {Event} event - DOM event
     */

    me.trigger('click', bryntumEvent);
    /**
     * User performed the default action (clicked the button)
     * @event action
     * @property {Core.widget.Button} button - Clicked button
     * @property {Event} event - DOM event
     */
    // A handler may have resulted in destruction.

    if (!me.isDestroyed) {
      me.trigger('action', bryntumEvent);
    } // since Widget has Events mixed in configured with 'callOnFunctions' this will also call onClick and onAction

    if (!this.href) {
      // stop the event since it has been handled
      event.preventDefault();
      event.stopPropagation();
    }
  } //endregion
  //region Toggle

  /**
   * Toggle button state (only use with toggleable = true)
   * @param {Boolean} pressed Specify to force a certain toggle state
   * @fires toggle
   */

  toggle(pressed = null) {
    const me = this,
          {
      menu
    } = me;

    if (!me.toggleable) {
      return;
    }

    if (pressed === null) {
      pressed = !me.pressed;
    }

    me.pressed = pressed; // For handlers from the code below to detect and avoid recursion

    me.toggling = true;

    if (menu) {
      menu.minWidth = me.width;
      menu[pressed ? 'show' : 'hide']();
    }

    me.syncIconCls();
    /**
     * Button state was toggled
     * @event toggle
     * @property {Core.widget.Button} button - Button
     * @property {Boolean} pressed - New pressed state
     */

    me.trigger('toggle', {
      pressed
    });
    me.toggling = false;
  } //endregion
  //region Other

  syncIconCls() {
    const me = this;
    let iconEl = me._iconEl;

    if (me.icon) {
      if (!iconEl) {
        iconEl = me._iconEl = DomHelper.createElement({
          tag: 'i',
          nextSibling: me.element.firstChild
        });
      }

      const iconCls = me.pressed && me.pressedIcon ? me.pressedIcon : me.icon;
      me._iconEl.className = iconCls; // if it's a supplied icon class b-fa-xxx or b-icon-xxx, add extra class
      // so user doesn't have to; they can just specify the icon

      if (bIcon.test(iconCls)) {
        iconEl.classList.add('b-icon');
      } else if (bFa.test(iconCls)) {
        iconEl.classList.add('b-fa');
      }
    } else {
      iconEl && iconEl.remove();
    }
  } //endregion

} // Register this widget type with its Factory

Button.initClass();
Button._$name = 'Button';

/**
 * @module Core/widget/Toolbar
 */

/**
 * A container widget that can contain Buttons or other widgets, and is docked to the bottom or top of
 * a {@link Core.widget.Panel Panel}.
 *
 * @extends Core/widget/Container
 * @classType toolbar
 * @externalexample widget/Toolbar.js
 */

class Toolbar extends Container {
  static get $name() {
    return 'Toolbar';
  } // Factoryable type name

  static get type() {
    return 'toolbar';
  }

  static get configurable() {
    return {
      defaultType: 'button',

      /**
       * Custom CSS class to add to toolbar widgets
       * @config {String}
       * @category CSS
       */
      widgetCls: null,
      layout: 'default'
    };
  }

  createWidget(widget) {
    if (widget === '->') {
      widget = {
        type: 'widget',
        cls: 'b-toolbar-fill'
      };
    } else if (widget === '|') {
      widget = {
        type: 'widget',
        cls: 'b-toolbar-separator'
      };
    } else if (typeof widget === 'string') {
      widget = {
        type: 'widget',
        cls: 'b-toolbar-text',
        html: widget
      };
    }

    const result = super.createWidget(widget);

    if (this.widgetCls) {
      result.element.classList.add(this.widgetCls);
    }

    return result;
  }

} // Register this widget type with its Factory

Toolbar.initClass();
Toolbar._$name = 'Toolbar';

/**
 * @module Core/widget/Panel
 */

const acceptNode = e => !e.classList.contains('b-focus-trap') && DomHelper.isFocusable(e) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP,
      byWeight = (l, r) => (l.weight || 0) - (r.weight || 0),
      byWeightReverse = (l, r) => (r.weight || 0) - (l.weight || 0); // https://github.com/webcomponents/webcomponentsjs/issues/556
// Work around Internet Explorer wanting a function instead of an object.
// IE also *requires* this argument where other browsers don't.

acceptNode.acceptNode = acceptNode;
/**
 * Panel widget. A general pupose container which may be used to contain child {@link Core.widget.Container#config-items}
 * or {@link Core.widget.Widget#config-html}.
 *
 * Also may dock a {@link #config-header} and {@link #config-footer} either at top/bottom or left/right
 *
 *
 * @example
 * let panel = new Panel({
 *   title   : 'A Test Panel',
 *   items : [
 *     { type : 'text', placeholder: 'Text' },
 *   ],
 *   bbar : [{
 *     text : 'Proceed',
 *     onClick : () => {
 *       alert('Proceeding!');
 *     }
 *   }]
 * });
 *
 * @classType panel
 *
 * @extends Core/widget/Container
 * @externalexample widget/Panel.js
 */

class Panel extends Container {
  //region Config
  static get $name() {
    return 'Panel';
  } // Factoryable type name

  static get type() {
    return 'panel';
  }

  static get configurable() {
    return {
      localizableProperties: ['title'],

      /**
       * By default, tabbing within a Panel is not contained, ie you can TAB out of the Panel
       * forwards or backwards.
       * Configure this as `true` to disallow tabbing out of the Panel, and make tabbing circular within this Panel.
       * @config {Boolean}
       * @default false
       */
      trapFocus: null,

      /**
       * A title to display in the header. Causes creation and docking of a header
       * to the top if no header is configured.
       *
       * If specified, overrides any title configured within the {@link #config-header} configuration.
       * @default
       * @config {String}
       */
      title: null,

      /**
       * Config object of a header. May contain a `title`, `titleAlign`, `dock`
       * and a `cls` property. If passed as a string, the value is used
       * as the title, dock is defaulted to `'top', and titleAlign` is defaulted to `start`.
       *
       * The `dock` property may be `top`, `right`, `bottom` or `left`.
       * The `titleAlign` property may be `start`, `center` or `end`.
       * @default
       * @config {Object|String}
       */
      header: null,

      /**
       * Config object of a footer. May contain a `dock`
       * and a `cls` property.
       *
       * The `dock` property may be `top`, `right`, `bottom` or `left`.
       * @default
       * @config {Object|String}
       */
      footer: null,

      /**
       * The tools to add either before or after the title in the Panel header.
       * Each property name is the reference by which an instantiated Tool Widget
       * may be retrieved from the live `{@link #property-tools}` property.
       * Each tool may have the following properties:
       * * `cls` The CSS class to apply.
       * * `handler` A method in the field to call upon click
       * * `align` `'start'` or `'end'` which end of the header the tool should go.
       * * `weight` (Optional) Heigher weighted tools gravitate towards the centre.
       * @config {Object}
       */
      tools: {
        $config: {
          nullify: true
        },
        value: null
      },

      /**
       * A Config object representing the configuration of a {@link Core.widget.Toolbar},
       * or array of config objects representing the child items of a Toolbar.
       *
       * This creates a toolbar docked to the top of the panel immediately below the header.
       * @config {Object[]|Object}
       */
      tbar: {
        $config: {
          nullify: true,
          merge: 'objects'
        },
        value: null
      },

      /**
       * A Config object representing the configuration of a {@link Core.widget.Toolbar},
       * or array of config objects representing the child items of a Toolbar.
       *
       * This creates a toolbar docked to the bottom of the panel immediately above the footer.
       * @config {Object[]|Object}
       */
      bbar: {
        $config: {
          nullify: true,
          merge: 'objects'
        },
        value: null
      }
    };
  } //endregion

  /**
   * The tool Widgets as specified by the {@link #config-tools} configuration
   * (and the {@link Core.widget.Popup#config-closable} configuration in the Popup subclass).
   * Each is a {@link Core.widget.Widget Widget} instance which may be hidden, shown and observed and styled just like any other widget.
   * @member {Object} tools
   */

  /**
   * Get toolbar {@link Core.widget.Toolbar} docked to the top of the panel
   * @member {Core.widget.Toolbar} tbar
   * @readonly
   * @typings Toolbar
   */

  /**
   * Get toolbar {@link Core.widget.Toolbar} docked to the bottom of the panel
   * @member {Core.widget.Toolbar} bbar
   * @readonly
   * @typings Toolbar
   */

  /**
   * A header {@link #config-tools tool} has been clicked.
   * @event toolClick
   * @param {Core.widget.Tool} source - This Panel.
   * @param {Core.widget.Tool} tool - The tool which is being clicked.
   */
  //region Init & destroy

  set element(element) {
    const me = this,
          headerFromTitle = me.title && (!me.parent || !me.parent.suppressChildHeaders),
          {
      tools,
      footer,
      tbar
    } = me,
          // If tools exist, we need to create the header infrastructure
    header = me.header || (tools || headerFromTitle ? {} : null),
          bbar = me.bbar || me.buttons,
          children = [{
      reference: 'topFocusTrap',
      className: 'b-focus-trap',
      tabIndex: 0
    }],
          startTools = [],
          endTools = [],
          result = {
      children
    };

    if (me.hasItems && me.focusable !== false || me.focusable) {
      result.tabIndex = 0;
    }

    if (header) {
      const title = typeof header === 'string' ? header : me.title || header.title,
            titleAlign = header.titleAlign || 'start',
            headerClass = me.classHierarchy(Panel).reduce((prev, cls) => {
        if (cls.$$name !== 'Grid') {
          prev[`b-${cls.$$name.toLowerCase()}-header`] = 1;
        }

        return prev;
      }, typeof header.cls === 'string' ? {
        [header.cls]: 1
      } : header.cls || {}),
            headerChildren = [{
        reference: 'titleElement',
        className: `b-header-title b-align-${titleAlign}`,
        html: title
      }];

      for (const toolRef in tools) {
        const tool = tools[toolRef];

        if (tool.align === 'start') {
          startTools.unshift(tool);
        } else {
          endTools.push(tool);
        }
      } // The tools at each end are sorted "gravitationally".
      // Higher weight sorts towards the center which is the title element.

      startTools.sort(byWeight);
      endTools.sort(byWeightReverse);
      headerChildren.unshift(...startTools.map(t => t.element));
      headerChildren.push(...endTools.map(t => t.element)); // Hide the header if no title and all tools are hidden

      headerClass['b-hide-display'] = !title && (!tools || !Object.values(tools).some(t => !t.hidden));
      headerClass[`b-dock-${header.dock || 'top'}`] = 1;
      children.push({
        tag: 'header',
        reference: 'headerElement',
        className: headerClass,
        children: headerChildren
      });
    }

    if (tbar) {
      tbar.layout.renderChildren();
      children.push(tbar.element);
    }

    children.push(me.bodyConfig);

    if (bbar) {
      bbar.layout.renderChildren();
      children.push(bbar.element);
    }

    if (footer) {
      children.push({
        tag: 'footer',
        reference: 'footerElement',
        className: `b-dock-${footer.dock || 'bottom'} ${footer.cls || ''}`
      });
    }

    children.push({
      reference: 'bottomFocusTrap',
      className: 'b-focus-trap',
      tabIndex: 0
    });
    super.element = result;
  }

  set bodyConfig(bodyConfig) {
    this._bodyConfig = bodyConfig;
  }

  get bodyConfig() {
    const {
      html
    } = this,
          result = ObjectHelper.merge({}, this._bodyConfig, {
      reference: 'bodyElement',
      className: this.classHierarchy(Panel).reduce((prev, cls) => {
        prev[`b-${cls.$$name.toLowerCase()}-content`] = 1;
        return prev;
      }, {})
    }); // See updateHtml. Allow for object-based HTML content

    if (html) {
      if (typeof html === 'object') {
        ObjectHelper.merge(result, html);
      } else if (typeof html === 'string') {
        result.html = html;
      }
    }

    return result;
  }

  get element() {
    return super.element;
  }

  changeTbar(tbar, oldTbar) {
    if (tbar) {
      if (Array.isArray(tbar)) {
        tbar = {
          items: tbar
        };
      }

      if (tbar.isWidget) {
        tbar.parent = this;
      } else {
        tbar = Toolbar.reconfigure(oldTbar, Toolbar.mergeConfigs({
          type: 'toolbar',
          parent: this
        }, tbar));
      } // Allow Panel subclasses with tbar configured into them to have tbar configured away

      if (tbar) {
        this.hasItems = this.hasItems || tbar.hasItems;
        DomHelper.addClasses(tbar.element, ['b-top-toolbar', `b-dock-${tbar.dock || 'top'}`]);
      }
    } else if (oldTbar) {
      oldTbar.destroy();
    }

    return tbar;
  }

  changeBbar(bbar, oldTbar) {
    if (bbar) {
      if (Array.isArray(bbar)) {
        bbar = {
          items: bbar
        };
      }

      if (bbar.isWidget) {
        bbar.parent = this;
      } else {
        bbar = Toolbar.reconfigure(oldTbar, Toolbar.mergeConfigs({
          type: 'toolbar',
          parent: this
        }, bbar));
      } // Allow Panel subclasses with bbar configured into them to have bbar configured away

      if (bbar) {
        this.hasItems = this.hasItems || bbar.hasItems;
        DomHelper.addClasses(bbar.element, ['b-bottom-toolbar', `b-dock-${bbar.dock || 'bottom'}`]);
      }
    } else if (oldTbar) {
      oldTbar.destroy();
    }

    return bbar;
  }

  updateTitle(title) {
    const {
      tools
    } = this;

    if (this.headerElement) {
      this.titleElement.innerHTML = title;
      this.headerElement.classList[!title && (!tools || !Object.values(tools).some(t => !t.hidden)) ? 'add' : 'remove']('b-hide-display');
    }
  } // Override to iterate docked Toolbars in the correct order around contained widgets.

  get childItems() {
    const me = this,
          items = me.items ? me.items.slice() : [];

    if (me.tools) {
      items.unshift(...Object.values(me.tools));
    }

    if (me.tbar) {
      items.unshift(me.tbar);
    }

    if (me.bbar) {
      items.push(me.bbar);
    }

    return items;
  }

  changeTools(tools, oldTools) {
    const me = this,
          myTools = {};

    if (tools) {
      for (const toolRef in tools) {
        // Allow excluding tools by configuring them as `false`
        if (tools[toolRef]) {
          const tool = myTools[toolRef] = Panel.create(ObjectHelper.assign({
            type: 'tool',
            ref: toolRef,
            parent: me
          }, tools[toolRef]), me.defaultToolType || 'tool');
          me.onChildAdd(tool);
        }
      }
    } else if (oldTools) {
      for (const t of Object.values(oldTools)) {
        t.destroy();
      }
    }

    return myTools;
  }

  updateTrapFocus(trapFocus) {
    const me = this;
    me.element.classList[trapFocus ? 'add' : 'remove']('b-focus-trapped');

    if (trapFocus) {
      me.focusTrapListener = EventHelper.on({
        element: me.element,
        focusin: 'onFocusTrapped',
        delegate: '.b-focus-trap',
        thisObj: me
      }); // Create a TreeWalker which visits focusable elements.

      if (!me.treeWalker) {
        me.treeWalker = this.setupTreeWalker(me.element, NodeFilter.SHOW_ELEMENT, acceptNode, false);
      }
    } else {
      if (me.focusTrapListener) {
        me.focusTrapListener();
        me.focusTrapListener = null;
      }
    }
  }

  setupTreeWalker(root, whatToShow, filter, entityReferenceExpansion) {
    return document.createTreeWalker(root, whatToShow, filter, entityReferenceExpansion);
  }

  onFocusTrapped(e) {
    const me = this,
          treeWalker = me.treeWalker; // The only way of focusing these invisible elements is by TABbing to them.
    // If we hit the bottom one, wrap to the top.

    if (e.target === me.bottomFocusTrap) {
      treeWalker.currentNode = me.topFocusTrap;
      treeWalker.nextNode();
    } // If we hit the top one, wrap to the bottom.
    else {
        treeWalker.currentNode = me.bottomFocusTrap;
        treeWalker.previousNode();
      }

    me.requestAnimationFrame(() => treeWalker.currentNode.focus());
  }

  get focusElement() {
    // Either use our Containerness to yield the focus element of
    // a descendant or fall back to the encapsulating element.
    return this.hasItems && (super.focusElement || this.element);
  }

  get contentElement() {
    return this.element && this.bodyElement;
  }

  get widgetClassList() {
    const me = this,
          result = super.widgetClassList,
          header = me.header;

    if (header || me.title || me.tools) {
      result.push(`b-panel-has-header b-header-dock-${header && header.dock || 'top'}`);
    }

    if (me.tbar) {
      result.push(`b-panel-has-top-toolbar`);
    }

    if (me.bbar) {
      result.push(`b-panel-has-bottom-toolbar`);
    }

    return result;
  } //endregion

} // Register this widget type with its Factory

Panel.initClass();
Panel._$name = 'Panel';

/**
 * @module Core/widget/Popup
 */

/**
 * Popup widget, used as base class for Menu but can also be used as is to contain widgets or html
 *
 * @example
 * let popup = new Popup({
 *   forElement : document.querySelector('button'),
 *   items      : [
 *     { type : 'text', placeholder: 'Text' },
 *     { type: 'button', text: 'Okay', style: 'width: 100%', color: 'b-orange'}
 *   ]
 * });
 *
 * @classType popup
 * @externalexample widget/Popup.js
 *
 * @extends Core/widget/Panel
 */

class Popup extends Panel {
  //region Config
  static get $name() {
    return 'Popup';
  } // Factoryable type name

  static get type() {
    return 'popup';
  }

  static get configurable() {
    return {
      /**
       * Auto show flag for Popup.
       * If truthy then Popup is shown automatically upon hover.
       * @config {Boolean}
       * @default
       */
      autoShow: true,

      /**
       * By default a Popup is transient, and will {@link #function-close} when the user clicks or
       * taps outside its owned widgets and when focus moves outside its owned widgets.
       *
       * **Note**: {@link #config-modal Modal} popups won't {@link #function-close} when focus moves outside even if autoClose is `true`.
       *
       * Configure as `false` to make a Popup non-transient.
       * @config {Boolean}
       * @default
       */
      autoClose: true,

      /**
       * Show popup when user clicks the element that it is anchored to. Cannot be combined with showOnHover
       * @config {Boolean}
       * @default
       */
      showOnClick: false,

      /**
       * DOM element to attach popup.
       * @config {HTMLElement}
       */
      forElement: null,
      monitorResize: true,
      floating: true,
      hidden: true,
      axisLock: true,
      // Flip edges if align violates constrainTo
      hideAnimation: BrowserHelper.isIE11 ? null : {
        opacity: {
          from: 1,
          to: 0,
          duration: '.3s',
          delay: '0s'
        }
      },
      showAnimation: BrowserHelper.isIE11 ? null : {
        opacity: {
          from: 0,
          to: 1,
          duration: '.4s',
          delay: '0s'
        }
      },
      testConfig: {
        hideAnimation: null,
        showAnimation: null
      },

      /**
       * The action to take when calling the {@link #function-close} method.
       * By default, the popup is hidden.
       *
       * This may be set to `'destroy'` to destroy the popup upon close.
       * @config {String}
       * @default
       */
      closeAction: 'hide',

      /**
       * By default, tabbing within a Popup is circular - that is it does not exit.
       * Configure this as `false` to allow tabbing out of the Popup.
       * @config {Boolean}
       * @default
       */
      trapFocus: true,

      /**
       * By default a Popup is focused when it is shown.
       * Configure this as `false` to prevent automatic focus on show.
       * @config {Boolean}
       * @default
       */
      focusOnToFront: true,

      /**
       * Show a tool in the header to close this Popup, and allow `ESC` close it.
       * The tool is available in the {@link #property-tools} object
       * under the name `close`. It uses the CSS class `b-popup-close` to apply a
       * default close icon. This may be customized with your own CSS rules.
       * @config {Boolean}
       * @default
       */
      closable: null,

      /**
       * Optionally show an opaque mask below this Popup when shown.
       * Configure this as `true` to show the mask.
       *
       * When a Popup is modal, it defaults to being {@link Core.widget.Widget#config-centered centered}.
       * Also it won't {@link #function-close} when focus moves outside even if {@link #config-autoClose} is `true`.
       *
       * May also be an object containing the following properties:
       * * `closeOnMaskTap` Specify as `true` to {@link #function-close} when mask is tapped.
       * The default action is to focus the popup.
       *
       * Usage:
       * ```javascript
       * new Popup({
       *     title  : 'I am modal',
       *     modal  : {
       *         closeOnMaskTap : true
       *     },
       *     height : 100,
       *     width  : 200
       * });
       * ```
       *
       * @config {Boolean}
       * @default
       */
      modal: null,
      tools: {
        close: {
          cls: 'b-popup-close',
          handler: 'close',
          weight: -1000,
          hidden: true // shown when closable set to true

        }
      },
      highlightReturnedFocus: true
    };
  } //endregion
  //region Init & destroy

  finalizeInit() {
    const me = this;
    me.anchoredTo = me.forElement;
    me.initialAnchor = me.anchor;

    if (me.forElement && me.showOnClick) {
      // disable autoShow if not enabled by config
      if (!me.initialConfig.autoShow) {
        me.autoShow = false;
      }

      EventHelper.on({
        element: me.forElement,
        click: 'onElementUserAction',
        thisObj: me
      });
    }

    super.finalizeInit();

    if (me.autoShow) {
      if (me.autoShow === true) {
        me.show();
      } else {
        me.setTimeout(() => me.show(), me.autoShow);
      }
    }
  }

  doDestroy() {
    this.syncModalMask();
    super.doDestroy();
  } //endregion
  //region Show/hide

  /**
   * Performs the configured {@link #config-closeAction} upon this popup.
   * By default, the popup hides. The {@link #config-closeAction} may be
   * configured as `'destroy'`.
   * @fires beforeclose If popup is not hidden
   */

  close() {
    const me = this;
    /**
     * Fired when the {@link #function-close} method is called and the popup is not hidden.
     * May be vetoed by returning `false` from a handler.
     * @event beforeClose
     * @param {Core.widget.Popup} source - This Popup
     */

    if (!me._hidden && me.trigger('beforeClose') !== false || // we should destroy it even if it's hidden just omit beforeclose event
    me._hidden && me.closeAction === 'destroy') {
      // Focus moves unrelated to where the user's attention is upon this gesture.
      // Go into the keyboard mode where the focused widget gets a rendition so that
      // it is obvious where focus now is.
      // Must jump over EventHelper's global mousedown listener which will remove this class.
      if (me.containsFocus && me.highlightReturnedFocus) {
        me.setTimeout(() => document.body.classList.add('b-using-keyboard'), 0);
      }

      return me[me.closeAction]();
    }
  } //endregion
  //region Events

  onInternalKeyDown(event) {
    // close on escape key
    if (event.key === 'Escape') {
      event.stopImmediatePropagation();
      this.close(true);
    }
  }

  onDocumentMouseDown({
    event
  }) {
    const me = this;

    if (me.modal && event.target === Popup.modalMask) {
      event.preventDefault();

      if (me.modal.closeOnMaskTap) {
        me.close();
      } else if (!me.containsFocus) {
        me.focus();
      }
    } // in case of outside click and if popup is focused, focusout will trigger closing
    else if (!me.owns(event.target) && me.autoClose && !me.containsFocus) {
        me.close();
      }
  }

  get isTopModal() {
    return DomHelper.isVisible(Popup.modalMask) && this.element.previousElementSibling === Popup.modalMask;
  }

  onFocusIn(e) {
    const activeEl = DomHelper.activeElement;
    super.onFocusIn(e); // No event handler has moved focus, and target is outermost el
    // then delegate to the focusElement which for a Container
    // is found by finding the first visible, focusable descendant widget.

    if (DomHelper.activeElement === activeEl && e.target === this.element) {
      this.focus();
    }
  }

  onFocusOut(e) {
    if (!this.modal && this.autoClose) {
      this.close();
    }

    super.onFocusOut(e);
  }

  onShow() {
    const me = this;

    if (me.autoClose) {
      me.addDocumentMouseDownListener();
    } // TODO: It's the floating "toFront" operation that should handle
    // focusing based on config focusOnToFront.

    if (me.focusOnToFront) {
      me.focus();
    }

    super.onShow && super.onShow(); // Insert the modal mask below this Popup if needed

    me.syncModalMask();
  }

  addDocumentMouseDownListener() {
    if (!this.mouseDownRemover) {
      this.mouseDownRemover = GlobalEvents.on({
        globaltap: 'onDocumentMouseDown',
        thisObj: this
      });
    }
  }

  syncModalMask() {
    const me = this,
          {
      modal
    } = me; // Note the difference between Popup.modalMask and this.modalMask.
    // this.modalMask syncs the position of the element in the DOM
    // to be below this element. Popup.modalMask just returns the element.

    if (modal && me.isVisible) {
      // If we have not been explicitly positioned, a modal is centered.
      if (!me._x && !me._y) {
        me.centered = true;
      }

      me.modalMask.classList.remove('b-hide-display');
      me.element.classList.add('b-modal');
    } else {
      me.element.classList.remove('b-modal');
      const remainingModals = me.constructor.floatRoot.querySelectorAll('.b-modal'),
            topModal = remainingModals.length ? Popup.fromElement(remainingModals[remainingModals.length - 1], 'popup') : null; // If there are any other visible modals, drop the mask to just below the new topmost

      if (topModal) {
        topModal.syncModalMask();
      } else {
        Popup.modalMask.classList.add('b-hide-display');
      }
    }
  }

  onHide() {
    const me = this;

    if (me.mouseDownRemover) {
      me.mouseDownRemover();
      me.mouseDownRemover = null;
    }

    super.onHide && super.onHide(); // Insert the modal mask below the topmost Popup if needed, else hide it

    me.syncModalMask();
  }

  onElementUserAction() {
    this.show();
  } //endregion

  updateClosable(closable) {
    this.tools.close[closable ? 'show' : 'hide'](); // Ensure headerElement is shown if needed and hidden if not

    if (!this.title) {
      this.headerElement.classList[Object.values(this.tools).some(t => !t.hidden) ? 'remove' : 'add']('b-hide-display');
    }
  }

  updateTextContent(textContent) {
    // Popup has extra CSS responsibilities at the top level.
    // The CSS needs to know whether it should impose a max-width.
    this.element.classList[textContent && this.hasNoChildren ? 'add' : 'remove']('b-text-popup');
    super.updateTextContent(textContent);
  }
  /**
   * Returns the modal mask element for this Popup correctly positioned just below this Popup.
   * @internal
   */

  get modalMask() {
    const {
      modalMask
    } = Popup;

    if (modalMask.nextElementSibling !== this.element) {
      Popup.floatRoot.insertBefore(modalMask, this.element);
    }

    return modalMask;
  }
  /**
   * Returns the modal mask element. It does NOT guarantee its placement in the DOM relative
   * to any Popup. To get the modal mask for a particular Popup, use the instance property.
   * @internal
   */

  static get modalMask() {
    return this._modalMask || (this._modalMask = DomHelper.createElement({
      className: 'b-modal-mask b-hide-display'
    }));
  }

} // Register this widget type with its Factory

Popup.initClass();
Popup._$name = 'Popup';

const realignTransitions = {
  left: true,
  right: true,
  top: true,
  bottom: true,
  transform: true
},
      isBoolean = {
  true: 1,
  false: 1
};
/**
 * @module Core/widget/Tooltip
 */

/**
 * Tooltip. Easiest way of assigning a tooltip to a widget is by setting {@link Core.widget.Widget#config-tooltip}, see example below.
 *
 * ```javascript
 * new Button {
 *     text    : 'Hover me',
 *     tooltip : 'Click me and you wont believe what happens next'
 * });
 * ```
 *
 * By default, tooltips of widgets use a singleton Tooltip instance which may be accessed from the
 * `{@link Core.widget.Widget Widget}` class under the name `Widget.tooltip`.
 * This is configured according to the config object on pointer over.
 *
 * To request a separate instance be created just for this widget, add `newInstance : true`
 * to the configuration:
 *
 * ```javascript
 * new Button {
 *     text    : 'Hover me',
 *     tooltip : {
 *         html        : 'Click me and you wont believe what happens next',
 *         newInstance : true
 *     }
 * });
 * ```
 *
 * You can ask for the singleton instance to display configured tips for your own DOM structure using
 * `data-btip` element attributes:
 *
 * ```html
 * <button class="my-button" data-btip="Contextual help for my button" data-btip-scroll-action="realign">Hover me</button>
 * ```
 *
 * ## Showing async content
 * To load remote content into a simple tooltip, just load your data in the `beforeShow` listener (but ensure that the {@link #property-activeTarget} is the same when the data arrives)
 *
 * ```javascript
 * new Tooltip({
 *     listeners : {
 *         beforeShow : ({ source : tip }) => tip.html = AjaxHelper.get('someurl').then(response => response.text());
 *     }
 * });
 * ```
 *
 * If you have multiple targets that should show a tooltip when hovered over, look at {@link #config-forSelector} and {@link #config-getHtml}.
 *
 * ```javascript
 * new Tooltip({
 *     forSelector : '.myCssClass',
 *     getHtml     : ({ source : tip }) => AjaxHelper.fetch('data').then(response => response.text())
 * });
 * ```
 *
 * @extends Core/widget/Popup
 * @classType tooltip
 * @externalexample widget/Tooltip.js
 */

class Tooltip extends Popup {
  //region Default config
  static get $name() {
    return 'Tooltip';
  }

  static get listenersTarget() {
    return document.body;
  } // Factoryable type name

  static get type() {
    return 'tooltip';
  }
  /**
   * Triggered before tooltip widget is shown. Return `false` to prevent the action.
   * @preventable
   * @event beforeShow
   * @param {Core.widget.Tooltip} source The Tooltip
   * @param {Event} source.triggeredByEvent The event that triggered this Tooltip to show.
   */

  static get configurable() {
    return {
      /**
       * Horizontal offset from mouse when {@link #config-anchorToTarget} is `false`
       * @config {Number}
       * @default
       */
      mouseOffsetX: 15,

      /**
       * Vertical offset from mouse when {@link #config-anchorToTarget} is `false`
       * @config {Number}
       * @default
       */
      mouseOffsetY: 15,
      floating: true,

      /**
       * A method called to update the tooltip's content when the cursor is moved over a target. It receives one argument containing context about the tooltip and show operation.
       * The function should return a string, or a Promise yielding a string.
       * @param {Object} context
       * @param {Core.widget.Tooltip} context.tip The tooltip instance
       * @param {HTMLElement} context.element The Element for which the Tooltip is monitoring mouse movement
       * @param {HTMLElement} context.activeTarget The target element that triggered the show
       * @param {Event} context.event The raw DOM event
       * @return {String|Promise}
       * @config {Function}
       */
      getHtml: null,

      /**
       * DOM element to attach tooltip to. By default, the mouse entering this element will kick off a timer
       * (see {@link #config-hoverDelay}) to show itself.
       *
       * If the {@link #config-forSelector} is specified, then mouse entering matching elements within the `forElement`
       * will trigger the show timer to start.
       *
       * Note that when moving from matching element to matching element within the `forElement`, the tooltip
       * will remain visible for {@link #config-hideDelay} milliseconds after exiting one element, so that rapidly
       * entering another matching element will not cause hide+show flicker. To prevent this behaviour configure
       * with `hideDelay: 0`.
       * @config {HTMLElement}
       */
      forElement: null,
      trackMouse: null,

      /**
       * By default, a tooltip displays static content. In the Scheduler however, there are
       * plenty of uses cases when the tip content is based on the current mouse position (dragging events, resizing events, schedule hover tip, drag creation of events etc). Set
       * to `true` to update contents on mouse movement.
       * @config {Boolean}
       * @private
       */
      updateContentOnMouseMove: false,

      /**
       * A CSS selector which targets child elements of the {@link #config-forElement} that should produce a tooltip when hovered over.
       * @config {String}
       */
      forSelector: null,

      /**
       * By default, when moving rapidly from target to target, if, when mouseing over
       * a new target, the tip is still visible, the tooltip does not hide, it remains
       * visible, but updates its content however it is configured to do so.
       *
       * Configure `hideOnDelegateChange : true` to have the tip hide, and then trigger
       * a new show delay upon entry of a new target while still visible.
       * @config {Boolean}
       * @default false
       */
      hideOnDelegateChange: null,

      /**
       * Set to true to anchor tooltip to the triggering target. If set to `false`, the tooltip
       * will align to the mouse position. When set to `false`, it will also set `anchor: false`
       * to hide anchor arrow.
       * @config {Boolean}
       * @default true
       */
      anchorToTarget: true,

      /**
       * Show on hover
       * @config {Boolean}
       * @default
       */
      showOnHover: false,

      /**
       * The amount of time to hover before showing
       * @config {Number}
       * @default
       */
      hoverDelay: 500,

      /**
       * Show immediately when created
       * @config {Boolean}
       * @default
       */
      autoShow: false,

      /**
       * The time (in milliseconds) that the Tooltip should stay visible for when it shows over its
       * target. If the tooltip is anchored to its target, then moving the mouse during this time
       * resets the timer so that the tooltip will remain visible.
       *
       * Defaults to `0` which means the Tooltip will persist until the mouse leaves the target.
       * @config {Number}
       * @default
       */
      dismissDelay: 0,

      /**
       * The time (in milliseconds) for which the Tooltip remains visible when the mouse leaves the target.
       *
       * May be configured as `false` to persist visible after the mouse exits the target element. Configure it
       * as 0 to always retrigger `hoverDelay` even when moving mouse inside `fromElement`
       * @config {Number}
       * @default
       */
      hideDelay: 500,

      /**
       * The message to show while an async tooltip is fetching its content.
       * @config {String}
       * @default
       */
      loadingMsg: 'Loading...',

      /**
       * Keep the tooltip open if user hovers it
       * @config {Boolean}
       * @default
       */
      allowOver: false,
      anchor: true,
      align: 'b-t',
      axisLock: true,
      testConfig: {
        hideDelay: 100,
        hoverDelay: 100,
        showAnimation: null,
        hideAnimation: null
      }
    };
  } //endregion
  //region Events

  /**
   * Triggered when a mouseover event is detected on a potential target element.
   * Return false to prevent the action
   * @event pointerOver
   * @param {Core.widget.Tooltip} sourceThe tooltip instance.
   * @param {Event} event The mouseover event.
   */
  //endregion
  //region Properties

  /**
   * The HTML element that triggered this Tooltip to show
   * @member {HTMLElement} activeTarget
   */
  //endregion
  //region Init & destroy

  afterConstruct() {
    const me = this,
          {
      forSelector
    } = me;

    if (forSelector) {
      me.showOnHover = true;

      if (!me.forElement) {
        me.trackMouse = true;
        me.forElement = Tooltip.listenersTarget;
      }
    }

    super.afterConstruct(); // There's a triggering element, and we're showing on hover, add the mouse listeners

    if (me.forElement && me.showOnHover) {
      me.pointerOverOutDetacher = EventHelper.on({
        element: me.forElement,
        mouseover: 'internalOnPointerOver',
        mouseout: 'internalOnPointerOut',
        thisObj: me
      });
    }

    if (me.autoShow) {
      me.show();
    }

    if (me.allowOver) {
      EventHelper.on({
        element: me.element,
        mouseenter: 'onOwnElementMouseEnter',
        mouseleave: 'internalOnPointerOut',
        thisObj: me
      });
    }
  }

  doDestroy() {
    this.pointerOverOutDetacher && this.pointerOverOutDetacher();
    super.doDestroy();
  }

  set focusOnToFront(focusOnToFront) {
    super.focusOnToFront = focusOnToFront;
  }

  get focusOnToFront() {
    // Transient things like tooltips should not focus when invoked by pointer events
    return super.focusOnToFront && DomHelper.usingKeyboard;
  }

  get focusElement() {
    const result = super.focusElement;

    if (result !== this.element) {
      return result;
    }
  }

  get trackMouse() {
    // We do not track the mouse if we are anchored to (aligned to) the target
    return this._trackMouse && !this.anchorToTarget;
  }

  updateAnchorToTarget(anchorToTarget) {
    if (!anchorToTarget) {
      this.anchor = false;
    }
  } //endregion
  //region Hovering, show and hide

  onDocumentMouseDown({
    event
  }) {
    const me = this,
          {
      triggeredByEvent
    } = me; // If it's a tap that is caused by the touch that was converted into a mousover we should not hide.
    // That is if it's a touch and at the same place and within 500ms

    if (triggeredByEvent && DomHelper.isTouchEvent) {
      if (event.pageX === triggeredByEvent.pageX && event.pageY === triggeredByEvent.pageY && me.activeTarget.contains(event.target) && performance.now() - triggeredByEvent.timeStamp < 500) {
        return;
      }
    }

    me.abortDelayedShow();
    super.onDocumentMouseDown({
      event
    });
  }

  internalOnPointerOver(event) {
    const me = this,
          forElement = me.forElement,
          forSelector = me.forSelector,
          activeTarget = me.activeTarget;
    let newTarget;

    if (me.disabled) {
      return;
    } // If the mouse moves over this tooltip, it is theoretically a mouseout of its
    // forElement, but allowOver lets us tolerate this ane remain visible.

    if (me.allowOver && me.element.contains(event.target)) {
      return;
    } // There's been a mouseover. If we have a forSelector, we have to check
    // if it's an enter of a matching child

    if (forSelector) {
      // Moving inside a forSelector matching element
      if (activeTarget && activeTarget.contains(event.target) && activeTarget.contains(event.relatedTarget)) {
        return;
      }

      newTarget = event.target.closest(forSelector); // Mouseovers while within a target do nothing

      if (newTarget && event.relatedTarget && event.relatedTarget.closest(forSelector) === newTarget) {
        return;
      }
    } // There's no forSelector, so check if we moved from outside the target
    else if (!forElement.contains(event.relatedTarget)) {
        newTarget = forElement;
      } // Mouseover caused by moving from child to child inside the target
      else {
          return;
        } // If pointer entered the target or a forSelector child, then show.

    if (newTarget) {
      me.handleForElementOver(event, newTarget);
    } // If over a non-forSelector child, behave as in forElement out
    else if (activeTarget) {
        me.handleForElementOut();
      }
  } // Handle a transitioned reposition when the activeTarget moved beneath the pointer.
  // When it comes to an end, if the mouseout has not hidden, then realign at the new position
  // if the activeTarget is still beneath the pointer.

  onTransitionEnd(event) {
    const me = this,
          currOver = Tooltip.currentOverElement;

    if (realignTransitions[event.propertyName]) {
      // Don't realign if the mouse is over this, and is allowed to be over this
      // If user is interacting with this Toolltip, they won't expect it to move.
      if (me.allowOver && me.element.contains(currOver)) {
        return;
      } // If we are still visible, and mouse is still over the activeTarget, realign

      if (me.activeTarget && me.activeTarget.contains(currOver) && !me.trackMouse) {
        me.realign();
      }
    }
  }

  async handleForElementOver(event, newTarget) {
    const me = this; // Vetoed, then behave as is a targetout

    if (me.trigger('pointerOver', {
      event,
      target: newTarget
    }) === false) {
      me.internalOnPointerOut(event);
    } else {
      me.triggeredByEvent = event;
      me.abortDelayedHide(); // Abort any in-flight animated hide, and show immediately.
      // This is needed when allowOver is false (which is the default), and mouseovering
      // hides, but that immediately causes the mouse to be over another delegate
      // which jumps right in here.

      if (!me.hideOnDelegateChange && me.element.classList.contains('b-hiding')) {
        me.element.classList.remove('b-hiding');
        me.show(false);
      } // We are over a new target. If we are still visible, we
      // do not want to hide to avoid flickering. But if there is a
      // beforeshow listener which may mutate us, we still have to
      // consult it. If it returns a veto, then we do in fact hide.
      // Under normal circumstances we just alignTo the new target.
      // We must handle the post show tasks like starting the dismiss timer etc.

      if (me.isVisible && !me.hideOnDelegateChange) {
        me.updateActiveTarget(newTarget);

        if (me.trigger('beforeShow') === false) {
          return me.hide();
        }

        const result = me.updateContent(); // Edge case, we have no loadingMsg meaning we're not visible until content has arrived

        if (ObjectHelper.isPromise(result) && !me.loadingMsg) {
          await result;
        } // updateContent may have yielded empty content which hides us

        if (!me.hidden) {
          me.alignTo({
            target: me.anchorToTarget ? newTarget : new Point(me.triggeredByEvent.pageX - window.pageXOffset + me.mouseOffsetX, me.triggeredByEvent.pageY - window.pageYOffset + me.mouseOffsetY),
            overlap: !(me.anchorToTarget && me.anchor)
          });
          me.trigger('show');
          me.afterShowByTarget();
        }
      } else {
        if (me.hideOnDelegateChange) {
          // Hide with no animation if we are configured to hide between delegates
          me.hide(false);
        }

        me.updateActiveTarget(newTarget);
        me.delayShow(newTarget);
      }
    }
  }

  async delayShow(target) {
    const me = this; // Caught in an animation - cancel it

    if (me.currentAnimation) {
      me.cancelHideShowAnimation();
      me._hidden = true;
    }

    if (!me.isVisible && !me.hasTimeout('show')) {
      // Allow hoverDelay:0 or rapid movement from delegate to delegate to show immediately
      if (!me.hoverDelay || me.forSelector && Date.now() - me.lastHidden < me.quickShowInterval) {
        const result = me.updateContent(); // Edge case, we have no loadingMsg meaning we're not visible until content has arrived

        if (ObjectHelper.isPromise(result) && !me.loadingMsg) {
          await result;
        }

        me.showByTarget(target);
      } else {
        // If a mouse down happens during the delay period, we cancel the show
        me.addDocumentMouseDownListener(); // If we're not going to anchor to the hovered element, then we need to keep track
        // of mousemoves until the show happens so we can show where the mouse currently is.

        if (!me.listeningForMouseMove && !me.anchorToTarget) {
          me.mouseMoveRemover = EventHelper.on({
            element: Tooltip.listenersTarget,
            mousemove: 'onMouseMove',
            thisObj: me
          });
        } // If a tap event triggered, do not wait. Show immediately.

        me.setTimeout(async () => {
          if (!DomHelper.isOrphaned(target)) {
            const result = me.updateContent(); // Edge case, we have no loadingMsg meaning we're not visible until content has arrived

            if (ObjectHelper.isPromise(result) && !me.loadingMsg) {
              await result;
            }

            me.showByTarget(target);
          }
        }, !me.triggeredByEvent || me.triggeredByEvent.type === 'mouseover' ? me.hoverDelay : 0, 'show');
      }
    } else if (me.isVisible) {
      me.showByTarget(target);
    }
  }

  updateContent() {
    const me = this;

    if (me.getHtml) {
      const result = me.getHtml({
        tip: me,
        element: me.element,
        activeTarget: me.activeTarget,
        event: me.triggeredByEvent
      });
      me.html = result;
      return result;
    }
  } // There are 3 possible scenarios:
  // - Static content
  // - Remote content being loaded (meaning we (possibly) set a loading message as the `html`
  // - Tooltip acts as a Container

  get hasContent() {
    return Boolean(this._html !== '' && typeof this.html === 'string' && this.html.length || this.items.length);
  }

  internalBeforeShow() {
    // In case we update content on mouse move, need to show empty tooltip first
    return this.updateContentOnMouseMove || this.hasContent;
  }
  /**
   * Shows a spinner and a message to indicate an async flow is ongoing
   * @param {String} message The message, defaults to {@link #config-loadingMsg}
   */

  showAsyncMessage(message = this.optionalL(this.loadingMsg)) {
    // TODO: Load masking should ideally use a general Widget ability.
    if (message) {
      this.html = `
                <div class="b-tooltip-loading">
                    <i class="b-icon b-icon-spinner"></i>
                    ${message}
                </div>
            `;
    }
  }

  showByTarget(target) {
    const me = this;

    if (me.mouseMoveRemover) {
      me.mouseMoveRemover();
      me.mouseMoveRemover = null;
    } // Show by the correct thing.
    // If we are not anchored to the target, then it's the current pointer event.
    // Otherwise it's the activeTarget.

    me.showBy({
      target: me.anchorToTarget ? target : new Point(me.triggeredByEvent.pageX - window.pageXOffset + me.mouseOffsetX, me.triggeredByEvent.pageY - window.pageYOffset + me.mouseOffsetY),
      overlap: !(me.anchorToTarget && me.anchor)
    });
  }

  afterShowByTarget() {
    const me = this,
          dismissDelay = me.dismissDelay;
    me.abortDelayedShow();

    if (dismissDelay) {
      me.setTimeout('hide', dismissDelay);
    }

    me.toFront();
  }

  updateActiveTarget(newTarget) {
    const me = this,
          lastTarget = me.activeTarget;
    me.activeTarget = newTarget;

    if (!me.isConfiguring) {
      me.trigger('overtarget', {
        newTarget,
        lastTarget
      });
    }
  }

  internalOnPointerOut(event) {
    const me = this,
          toElement = event.relatedTarget; // Edge case: If there is no space to fit the tooltip, and as a result of showing the tooltip - the mouse is over the tooltip
    // Make sure we don't end up in an infinite hide/show loop

    if (me.allowOver && me.element.contains(toElement)) {
      return;
    } // If we were in an allowOver situation and exited
    // into the activeTarget, do nothing; in this situation
    // the tip is treated as if it were part of the target.

    if (me.element.contains(event.target) && me.activeTarget && me.activeTarget.contains(toElement)) {
      return;
    } // We have exited the active target

    if (me.activeTarget && !me.activeTarget.contains(event.relatedTarget)) {
      me.handleForElementOut();
    }
  }

  handleForElementOut() {
    // Separated from onTargetOut so that subclasses can handle target out in any way.
    const me = this,
          {
      hideDelay
    } = me; // Allow outside world to veto the hide

    if (me.trigger('pointerOut') === false) {
      me.activeTarget = null;
      return;
    }

    me.abortDelayedShow(); // Even if there is a hide timer, it's a *dismiss* timer which hides the tip
    // after a hover time. We begin a new delay on target out.

    if (me.isVisible && hideDelay !== false) {
      me.abortDelayedHide();

      if (hideDelay > 0) {
        me.setTimeout('hide', hideDelay);
      } else {
        // Hide immediately when configured with `hideDelay: 0`. Used by async cell tooltips that always should
        // retrigger `hoverDelay`, to not spam the backend
        me.hide();
      }
    }
  }

  show() {
    const me = this; // If we know what element to show it by, and we are anchoring to it
    // and there's no ambiguity with a selector for sub elements,
    // then show it by our forElement (Unless we're being called from showBy)

    if (me.forElement && me.anchorToTarget && !me.forSelector && !me.inShowBy) {
      me.showByTarget(me.forElement);
    } // All we can do is the basic Widget show.
    else {
        if (me.inShowBy) {
          me.y = -10000;
        }

        super.show();
      }

    me.afterShowByTarget(); // If we've shown, and are tracking the mouse and not anchored to (aligned to) the target, track the mouse

    if (!me.mouseMoveRemover && !me._hidden && (me.trackMouse || me.updateContentOnMouseMove)) {
      me.mouseMoveRemover = EventHelper.on({
        element: Tooltip.listenersTarget,
        [BrowserHelper.supportsPointerEvents ? 'pointermove' : 'touchmove']: 'onMouseMove',
        thisObj: me
      });
    }

    if (me.forElement && !me.transitionEndDetacher && !me._hidden) {
      me.transitionEndDetacher = EventHelper.on({
        element: me.forElement,
        transitionend: 'onTransitionEnd',
        thisObj: me
      });
    }
  }

  hide(...args) {
    const me = this; // If we are asked to hide, we must always abort any impending show.

    me.abortDelayedShow(); // But if we are in fact visible, go ahead and hide

    if (me.isVisible) {
      me.abortDelayedHide();
      super.hide(...args);
      me.lastHidden = Date.now();
      me.activeTarget = null;

      if (me.mouseMoveRemover) {
        me.mouseMoveRemover();
        me.mouseMoveRemover = null;
      }

      if (me.transitionEndDetacher) {
        me.transitionEndDetacher();
        me.transitionEndDetacher = null;
      }

      me.listeningForMouseMove = false;
    }
  }

  abortDelayedShow() {
    const me = this;

    if (me.hasTimeout('show')) {
      // This listener is addded in delayShow to make a mousedown abort,
      // So we must remove it here because it's only removed in onHide.
      if (me.mouseDownRemover) {
        me.mouseDownRemover();
        me.mouseDownRemover = null;
      }

      me.clearTimeout('show');

      if (me.mouseMoveRemover) {
        me.mouseMoveRemover();
        me.mouseMoveRemover = null;
      }

      if (me.transitionEndDetacher) {
        me.transitionEndDetacher();
        me.transitionEndDetacher = null;
      }
    }
  }
  /**
   * Stops both timers which may hide this tooltip, the one which counts down from mouseout
   * and the one which counts down from mouseover show for dismissDelay ms
   * @private
   */

  abortDelayedHide() {
    this.clearTimeout('hide');
  }

  realign(el) {
    const me = this,
          spec = me.lastAlignSpec,
          clippedBy = me.clippedBy; // If we are hidden because our align target scrolled, or otherwise
    // moved out of its clipping boundaries, then check if it's moved back in.
    // For example EventDrag might move the element outside of the scheduler
    // SubGrid, which will cause the tip to hide, but then moving it back in
    // must reshow it.

    if (!me.isConfiguring && clippedBy && !me.isVisible && spec && spec.targetHidden) {
      const target = Rectangle.from(spec.target, me.positioned ? me.element.offsetParent : null, !me.positioned),
            clippedTarget = target.intersect(clippedBy); // If there is an intersecting Rectangle with the forElement, align

      if (clippedTarget) {
        me.show();
        spec.targetHidden = false;
      }
    }

    super.realign(el);
  }

  alignTo(spec) {
    const me = this;

    if (!me.isVisible) {
      return;
    } // If mouse pointer is over this, do not attempt
    // to call the getHtml method.

    if (spec && !(me.triggeredByEvent && me.element.contains(me.triggeredByEvent.target))) {
      if (spec.nodeType === 1) {
        spec = {
          target: spec
        };
      }
    }

    super.alignTo(spec);
  } //endregion
  //region Tooltip contents

  /**
   * Get/set the HTML to display. When specifying HTML, this widget's element will also have `b-html` added to its
   * classList, to allow targeted styling. To create async tooltip and show the {@link #config-loadingMsg}, see code below:
   * For example:
   *
   * ```javascript
   * new Tooltip({
   *     listeners : {
   *         beforeShow : ({ source : tip }) => {
   *             tip.showAsyncMessage();
   *             AjaxHelper.get('someurl').then(response => tip.html = 'Done!');
   *         }
   *     }
   * });
   * ```
   *
   * @property {String}
   * @name html
   * @category DOM
   */

  changeHtml(htmlOrPromise) {
    const me = this;

    if (htmlOrPromise === false) {
      VersionHelper.deprecate('Grid', '5.0.0', 'Setting false to indicate loading is deprecated, in favor of `showAsyncMessage`. Please see https://bryntum.com/docs/grid/#guides/upgrades/3.0.1.md for more information');
      this.showAsyncMessage();
      return;
    }

    if (ObjectHelper.isPromise(htmlOrPromise)) {
      me.showAsyncMessage();
      htmlOrPromise.target = me.activeTarget;
      htmlOrPromise.then(html => {
        // Cursor might have exited the element while loading
        if (htmlOrPromise.target === me.activeTarget) {
          me.html = html;
        }
      });
      return;
    } // Allow objects to pass through, to be used with DomSync

    if (typeof htmlOrPromise !== 'object') {
      // Stringify in case a number was passed in
      htmlOrPromise = htmlOrPromise != null ? me.optionalL(String(htmlOrPromise)) : '';
    }

    return htmlOrPromise;
  }

  updateHtml(value, was) {
    const me = this;
    let empty = value === ''; // As setting empty string as content should hide the tooltip, we don't want to actually update the
    // element innerHTML with blank space during the hide transition, we check _html for emptiness in hasContent

    if (!empty) {
      super.updateHtml(value, was);

      if (me.hasContent) {
        if (me.isVisible) {
          me.realign();
        }

        if (!ObjectHelper.isPromise(value)) {
          me.trigger('innerHtmlUpdate');
        }
      } else {
        empty = true;
      }
    }

    if (empty) {
      // Hide empty tooltips
      me.hide();
    }
  } //endregion
  //region Events

  /**
   * Mouse move event listener which updates tooltip
   * @private
   */

  onMouseMove(event) {
    const me = this,
          {
      forElement
    } = me,
          x = event.pageX - window.pageXOffset + me.mouseOffsetX,
          y = event.pageY - window.pageYOffset + me.mouseOffsetY,
          // If we are trackMouse: true
    // we must keep out of the way of the mouse by continuing
    // to track if we are on the way out due to a hide timer.
    isHiding = me.hasTimeout('hide'),
          target = event.target; // MouseMove is listened for during the hover show timer wait phase if anchorToTarget is false
    // so that when the timer fires, it can show near the most recent pointer position.
    // It's also listened for after show when we are not anchored to the target and so tracking the mouse.

    /**
     * The DOM event that triggered this tooltip to show
     * @member {Event} triggeredByEvent
     * @readonly
     */

    me.triggeredByEvent = event; // Check that we are still valid to be visible, and if so, track the mouse.

    if (!me._hidden) {
      let hideVetoed;
      const isElementOut = me.forSelector && me.activeTarget && !isHiding && target.nodeType === 1 && !target.matches(me.forSelector) && !(me.allowOver && me.element.contains(target)),
            // We need an element we can ask the "contains" question about our target.
      // If we are using window, we need to step down to the documentElement.
      containingElement = forElement !== null && forElement !== void 0 && forElement.document ? forElement.document.documentElement : forElement,
            // If the forElement is a ShadowRoot, it won't implement contains
      // but it does implement compareDocumentPosition.
      forElementContainsTarget = containingElement && (containingElement.contains ? containingElement.contains(target) : containingElement.compareDocumentPosition(target) & 16); // Check whether the element we are over is still a valid delegate matching the forSelector,
      // or it's the tip element, and we're allowOver. If not, we have to hide.
      // nodeType check is for FF on Linux, event.target is sometimes a text node

      if (isElementOut) {
        hideVetoed = me.handleForElementOut();
      } // If we are not hiding due to moving mouse outside our forElement (or hide being vetoed), tooltip stays visible and optionally realigns based on trackMouse setting.

      if (hideVetoed || !isHiding || forElementContainsTarget) {
        // Mousemoves restart the dismiss timer.
        if (me.dismissDelay && !isHiding) {
          me.setTimeout('hide', me.dismissDelay);
        }

        if (me.updateContentOnMouseMove && me.getHtml) {
          me.html = me.getHtml({
            tip: me,
            element: me.element,
            forElement: me.activeTarget,
            event: event
          });
        } // If we're not anchoring to the target, track the mouse

        if (me.trackMouse) {
          me.alignTo({
            position: new Point(x, y),
            ignorePageScroll: true
          });
        }
      }
    }
  }

  onOwnElementMouseEnter(event) {
    this.abortDelayedHide();
  } //endregion

} // In some demos this code may execute when body does not yet exist (scripttag)

if (!Tooltip.listenersTarget) {
  document.addEventListener('DOMContentLoaded', setupMouseEnterListener);
} else {
  setupMouseEnterListener();
}

function setupMouseEnterListener() {
  EventHelper.on({
    element: Tooltip.listenersTarget,
    mouseenter: event => Tooltip.currentOverElement = event.target,
    capture: true
  });
}
/**
 * Updated dynamically with the current element that the mouse is over. For use when showing a Tooltip
 * from code which is not triggered by a pointer event so that a tooltip can be positioned.
 * @member {HTMLElement} currentOverElement
 * @readonly
 * @static
 */
// Register this widget type with its Factory

Tooltip.initClass();
let globalInstance;

function createGlobalTooltipInstance() {
  globalInstance = new Tooltip({
    id: 'bryntum-tooltip',
    forElement: Tooltip.listenersTarget,
    forSelector: '[data-btip]',
    header: true,
    // So that we can set a title
    resetCfg: {},
    listeners: {
      // Reconfigure on pointerOver
      pointerOver({
        source: me,
        target
      }) {
        // Revert last pointerOver config set to initial setting.
        for (const key in me.resetCfg) {
          if (key === 'listeners') {
            me.un(me.resetCfg[key].set);
          } // Do not reset HTML to "". It causes an unwanted inter-delegate hide.
          // hideOnDelegateChange defaults to false.
          else if (key !== 'html') {
              me[key] = me.resetCfg[key].was;
            }
        }

        me.resetCfg = {};
        const forComponent = Widget.getById(target.id); // If it's a component's tooltip, configure from the component,
        // Otherwise gather from the dataset

        const config = (forComponent === null || forComponent === void 0 ? void 0 : forComponent.tipConfig) || me.gatherDataConfigs(target.dataset);

        for (const key in config) {
          me.resetCfg[key] = {
            set: config[key],
            was: me[key]
          };

          if (key === 'listeners') {
            me.on(config[key]);
          } else {
            me[key] = config[key];
          }
        }

        me.headerElement.style.display = me.title ? '' : 'none';
      }

    },

    gatherDataConfigs(dataset) {
      const me = this,
            config = {};

      for (const key in dataset) {
        if (key.startsWith('btip')) {
          if (key.length > 4) {
            const configProp = StringHelper.uncapitalize(key.substr(4)); // Snip off "btip" prefix to convert to property name
            // If we have a config by the name, set it

            if (configProp in me.getDefaultConfiguration()) {
              const value = dataset[key]; // gather the found config value

              config[configProp] = isBoolean[value] ? value === 'true' : isNaN(value) ? value : parseInt(value, 10);
            }
          } else {
            config.html = dataset[key];
          }
        }
      }

      return config;
    }

  });
}

Object.defineProperty(Widget, 'tooltip', {
  get() {
    if (!globalInstance) {
      clearTimeout(timeoutId);
      createGlobalTooltipInstance();
    }

    return globalInstance;
  }

});
let timeoutId;
function createTooltip(delay = 0) {
  if (timeoutId) {
    clearTimeout(timeoutId);
  }

  if (delay === 0) {
    createGlobalTooltipInstance();
  } else {
    timeoutId = setTimeout(createGlobalTooltipInstance, delay);
  }
} // In some demos this code may execute when body does not yet exist (UMD)

if (!Tooltip.listenersTarget) {
  document.addEventListener('DOMContentLoaded', () => createTooltip(VersionHelper.isTestEnv ? 0 : 1000));
} else {
  // Create the singleton after app startup (Unless in test mode when mouse will be grabbed immediately)
  createTooltip(VersionHelper.isTestEnv ? 0 : 1000);
}

Tooltip._$name = 'Tooltip';

/* eslint-disable no-unused-expressions */

/**
 * @module Core/util/drag/DragTipProxy
 */

/**
 * This drag proxy manages a {@link #config-tooltip} (or derived class) and aligns the tooltip to the current drag
 * position adjusted by the {@link #config-align} config.
 * @extends Core/util/drag/DragProxy
 * @classtype tip
 * @internal
 */

class DragTipProxy extends DragProxy {
  static get type() {
    return 'tip';
  }

  static get configurable() {
    return {
      /**
       * Controls how the tooltip will be aligned to the current drag position.
       *
       * See {@link Core.helper.util.Rectangle#function-alignTo} for details.
       * @config {String}
       * @default
       */
      align: 't10-b50',

      /**
       * The number of pixels to offset from the drag position.
       * @config {Number}
       * @default
       */
      offset: 20,

      /**
       * The tooltip to be shown, hidden and repositioned to track the drag position.
       * @config {Core.widget.Tooltip}
       */
      tooltip: {
        $config: ['lazy', 'nullify'],
        value: {
          type: 'tooltip'
        }
      }
    };
  }

  open() {
    this.getConfig('tooltip'); // trigger creation
  }

  close() {
    var _this$tooltip;

    (_this$tooltip = this.tooltip) === null || _this$tooltip === void 0 ? void 0 : _this$tooltip.hide();
  }

  dragMove(drag) {
    const {
      offset,
      tooltip
    } = this,
          {
      event
    } = drag;

    if (tooltip) {
      if (!tooltip.isVisible) {
        tooltip.show();
      }

      tooltip.alignTo({
        align: this.align,
        target: new Rectangle(event.clientX - offset, event.clientY - offset, offset * 2, offset * 2)
      });
    }
  }

  changeTooltip(config, existing) {
    return Widget.reconfigure(existing, config,
    /* owner = */
    this);
  }

}
DragTipProxy.initClass();
DragTipProxy._$name = 'DragTipProxy';

/**
 * @module Core/util/Month
 */

const sundayAndSaturday = {
  0: true,
  6: true
};
/**
 * A class which encapsulates a calendar view of a month, and offers information about
 * the weeks and days within that calendar view.
 * ```
 *   // December 2018 using Monday as week start
 *   const m = new Month({
 *       date         : '2018-12-01',
 *       weekStartDay : 1
 *   });
 *
 *   m.eachWeek((week, dates) => console.log(dates.map(d => d.getDate())))
 * ```
 */

class Month extends Events(Base) {
  static get configurable() {
    return {
      /**
       * The date which the month should encapsulate. May be a `Date` object, or a
       * `YYYY-MM-DD` format string.
       *
       * Mutating a passed `Date` after initializing a `Month` object has no effect on
       * the `Month` object.
       * @config {Date|String}
       */
      date: {
        $config: {
          equal: 'date'
        },
        value: DateHelper.clearTime(new Date())
      },
      month: null,
      year: null,

      /**
       * Week start day override. Defaults to the locale's {@link Core.helper.DateHelper#property-weekStartDay-static}.
       * @config {Number}
       */
      weekStartDay: DateHelper.weekStartDay,

      /**
       * Configure as `true` to have the visibleDayColumnIndex and visibleColumnCount properties
       * respect the configured {@link #config-nonWorkingDays}.
       * @config {Boolean}
       */
      hideNonWorkingDays: null,

      /**
       * defaults to `{0 : true, 6 : true}`. Specifies which *canonical* day numbers are non working days.
       * @config {Object}
       */
      nonWorkingDays: sundayAndSaturday,

      /**
       * Configure as `true` to always have the month encapsulate six weeks.
       * This is useful for UIs which must be a fixed height.
       * @config {Boolean}
       */
      sixWeeks: null
    };
  } //region events

  /**
   * Fired when setting the {@link #config-date} property causes the encapsulated date to change
   * in **any** way, date, week, month or year.
   * @event dateChange
   * @param {Core.util.Month} source The Month which triggered the event.
   * @param {Date} newDate The new encapsulated date value.
   * @param {Date} oldDate The previous encapsulated date value.
   * @param {Number} changes An object which contains properties which indicate what part of the date changed.
   * @param {Boolean} changes.d True if the date changed in any way.
   * @param {Boolean} changes.w True if the week changed (including same week in a different year).
   * @param {Boolean} changes.m True if the month changed (including same month in a different year).
   * @param {Boolean} changes.y True if the year changed.
   */

  /**
   * Fired when setting the {@link #config-date} property causes a change of week. Note that
   * weeks are calculated in the ISO standard form such that if there are fewer than four
   * days in the first week of a year, then that week is owned by the previous year.
   *
   * The {@link #config-weekStartDay} is honoured when making this calculation and this is a
   * locale-specific value which defaults to the ISO standard of 1 (Monday) in provided European
   * locales and 0 (Sunday) in the provided US English locale.
   * @event weekChange
   * @param {Core.util.Month} source The Month which triggered the event.
   * @param {Date} newDate The new encapsulated date value.
   * @param {Date} oldDate The previous encapsulated date value.
   * @param {Number} changes An object which contains properties which indicate what part of the date changed.
   * @param {Boolean} changes.d True if the date changed in any way.
   * @param {Boolean} changes.w True if the week changed (including same week in a different year).
   * @param {Boolean} changes.m True if the month changed (including same month in a different year).
   * @param {Boolean} changes.y True if the year changed.
   */

  /**
   * Fired when setting the {@link #config-date} property causes a change of month. This
   * will fire when changing to the same month in a different year.
   * @event monthChange
   * @param {Core.util.Month} source The Month which triggered the event.
   * @param {Date} newDate The new encapsulated date value.
   * @param {Date} oldDate The previous encapsulated date value.
   * @param {Number} changes An object which contains properties which indicate what part of the date changed.
   * @param {Boolean} changes.d True if the date changed in any way.
   * @param {Boolean} changes.w True if the week changed (including same week in a different year).
   * @param {Boolean} changes.m True if the month changed (including same month in a different year).
   * @param {Boolean} changes.y True if the year changed.
  */

  /**
   * Fired when setting the {@link #config-date} property causes a change of year.
   * @event yearChange
   * @param {Core.util.Month} source The Month which triggered the event.
   * @param {Date} newDate The new encapsulated date value.
   * @param {Date} oldDate The previous encapsulated date value.
   * @param {Number} changes An object which contains properties which indicate what part of the date changed.
   * @param {Boolean} changes.d True if the date changed in any way.
   * @param {Boolean} changes.w True if the week changed (including same week in a different year).
   * @param {Boolean} changes.m True if the month changed (including same month in a different year).
   * @param {Boolean} changes.y True if the year changed.
   */
  //endRegion

  /**
   * For use when this Month's `weekStartDay` is non-zero.
   *
   * An array to map the days of the week 0-6 of this Calendar to the canonical day numbers
   * used by the Javascript [Date](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date) object.
   * @member {Number[]} canonicalDayNumbers
   * @readonly
   */

  /**
   * An array to map a canonical day number to a *visible* column index.
   * For example, if we have `weekStartDay` as Monday which is 1, and non working days as
   * Wednesday, and `hideNonWorkingDays : true`, then the calendar would look like
   *
   *```
   * ┌────┬────┬────┬────┬────┬────┐
   * | Mo | Tu | Th | Fr | Sa | Su |
   * └────┴────┴────┴────┴────┴────┘
   *```
   *
   * So we'd need this array: `[ 5, 0, 1, undefined, 2, 3, 4]`
   * @member {Number[]} visibleDayColumnIndex
   * @readonly
   */

  /**
   * An array to map a canonical day number to a 0-6 column index.
   * For example, if we have `weekStartDay` as Monday which is 1, then the calendar would look like
   *
   *```
   * ┌────┬────┬────┬────┬────┬────┬────┐
   * | Mo | Tu | We | Th | Fr | Sa | Su |
   * └────┴────┴────┴────┴────┴────┴────┘
   *```
   *
   * So we'd need this array: `[ 6, 0, 1, 2, 3, 4, 5]`
   * @member {Number[]} dayColumnIndex
   * @readonly
   */

  /**
   * The number of visible days in the week as defined by the `nonWorkingDays` and
   * `hideNonWorkingDays` options.
   * @member {Number} weekLength
   * @readonly
   */

  configure(config) {
    super.configure(config); // The set is rejected during configuration because everything else has to be set up.

    if (config.date) {
      this.date = config.date;
    }
  }

  changeDate(date) {
    // Date has to be set after we know everything else
    if (this.isConfiguring) {
      return;
    }

    date = typeof date === 'string' ? DateHelper.parse(date, 'YYYY-MM-DD') : new Date(date);

    if (isNaN(date)) {
      throw new Error('Month date ingestion must be passed a Date, or a valid YYYY-MM-DD date string');
    }

    return date;
  }

  updateDate(newDate, oldDate) {
    const me = this,
          {
      dayColumnIndex
    } = me,
          monthStart = DateHelper.getFirstDateOfMonth(newDate),
          monthEnd = DateHelper.getLastDateOfMonth(monthStart),
          startWeekDay = dayColumnIndex[monthStart.getDay()],
          endWeekDay = dayColumnIndex[monthEnd.getDay()],
          yearChanged = !oldDate || newDate.getFullYear() !== oldDate.getFullYear(),
          // Collect changes as bitwise flags if we have any listeners:
    // 0001 = date has changed.
    // 0010 = week has changed.
    // 0100 = month has changed.
    // 1000 = year has changed.
    // We need this because 10/1/2010 -> 10/1/2011 must fire a dateChange
    // and a monthChange in addition to the yearChange.
    // And 10/1/2010 -> 10/2/2010 must fire a dateChange in addition to the monthChange.
    changes = me.eventListeners && (oldDate ? newDate.getDate() !== oldDate.getDate() | (me.getWeekId(newDate) !== me.getWeekId(oldDate)) << 1 | (newDate.getMonth() !== oldDate.getMonth()) << 2 | yearChanged << 3 : 15); // Keep our properties in sync with reality.
    // Access the private properties directly to avoid recursion.

    me._year = newDate.getFullYear();
    me._month = newDate.getMonth(); // These comments assume ISO standard of Monday as week start day.
    //
    // This is the date of month that is the beginning of the first week row.
    // So this may be -ve. Eg: for Dec 2018, Monday 26th Nov is the first
    // cell on the calendar which is the -4th of December. Note that the 0th
    // of December was 31st of November, so needs -4 to get back to the 26th.

    me.startDayOfMonth = 1 - startWeekDay; // This is the date of month that is the end of the last week row.
    // So this may be > month end. Eg: for Dec 2018, Sunday 6th Jan is the last
    // cell on the calendar which is the 37th of December.

    me.endDayOfMonth = monthEnd.getDate() + (6 - endWeekDay);

    if (me.sixWeeks) {
      me.endDayOfMonth += (6 - me.weekCount) * 7;
    } // Calculate the start point of where we calculate weeks from if we need to.
    // It's either the first "weekStartDay" in this year if this year's
    // first week is last year's, and so should work out as zero,
    // or the "weekStartDay" of the week before, so that dates in the first week
    // the Math.floor(DH.diff(weekBase, date, 'day') / 7) calculates as 1.

    if (!me.weekBase || yearChanged) {
      me.calculateWeekBase();
    }

    if (changes) {
      const event = {
        newDate,
        oldDate,
        changes: {
          d: true,
          w: Boolean(changes & 2),
          m: Boolean(changes & 12),
          y: Boolean(changes & 8)
        }
      }; // If either date, month or year changes, we fire a dateChange

      me.trigger('dateChange', event); // If the week has changed, fire a weekChange

      if (changes & 2) {
        me.trigger('weekChange', event);
      } // If month or year changed, we fire a monthChange

      if (changes & 12) {
        me.trigger('monthChange', event);
      } // If the year has changed, fire a yearChange

      if (changes & 8) {
        me.trigger('yearChange', event);
      }
    }
  }

  calculateWeekBase() {
    const me = this,
          {
      dayColumnIndex
    } = me,
          jan1 = new Date(me.year, 0, 1),
          dec31 = new Date(me.year, 11, 31),
          january = me.month ? me.getOtherMonth(jan1) : me; // First 7 days are in last week of previous year if the year
    // starts after our 4th day of week.

    if (me.dayColumnIndex[jan1.getDay()] > 3) {
      // Week base is calculated from the year start
      me.weekBase = january.startDate;
    } // First 7 days are in week 1 of this year
    else {
        // Week base is the start of week before
        me.weekBase = new Date(me.year, 0, january.startDayOfMonth - 7);
      }

    const dec31Week = Math.floor(DateHelper.diff(me.weekBase, dec31, 'day') / 7); // Our year only has a 53rd week if 53rd week ends after our week's 3rd day

    me.has53weeks = dec31Week === 53 && dayColumnIndex[dec31.getDay()] > 2;
  }
  /**
   * Returns the week start date, based on the configured {@link #config-weekStartDay} of the
   * passed week.
   * @param {Number| Number[]} week The week number in the current year, or an array containing
   * `[year, weekOfYear]` for any year.
   *
   * Week numbers greater than the number of weeks in the year just wrap into the following year.
   */

  getWeekStart(week) {
    // Week number n of current year
    if (typeof week === 'number') {
      return DateHelper.add(this.weekBase, Math.max(week, 1) * 7, 'day');
    } // Week n of year nnnn

    const me = this,
          [year, weekOfYear] = week; // nnnn is our year, so we know it

    if (year === me.year) {
      return me.getWeekStart(weekOfYear);
    }

    return me.getOtherMonth(new Date(year, 0, 1)).getWeekStart(weekOfYear);
  }

  getOtherMonth(date) {
    const me = this,
          result = me === otherMonth ? new Month(null) : otherMonth;
    result.configure({
      weekBase: null,
      weekStartDay: me.weekStartDay,
      nonWorkingDays: me.nonWorkingDays,
      hideNonWorkingDays: me.hideNonWorkingDays,
      sixWeeks: me.sixWeeks
    });
    result.date = date;

    if (result.date !== date) {
      // in this case, the date config ignores changes w/=== getTime so we have to force the update because we
      // also cleared weekBase above
      result.updateDate(result.date, result.date);
    }

    return result;
  }

  changeYear(year) {
    const newDate = new Date(this.date);
    newDate.setFullYear(year); // changeDate rejects non-changes, otherwise a change event will be emitted

    this.date = newDate;
  }

  changeMonth(month) {
    const newDate = new Date(this.date);
    newDate.setMonth(month); // changeDate rejects non-changes, otherwise a change event will be emitted

    this.date = newDate;
  }

  changeWeekStartDay(weekStartDay) {
    return weekStartDay == null ? DateHelper.weekStartDay : weekStartDay;
  }

  updateWeekStartDay() {
    const me = this;
    me.updateDayNumbers();
    me.calculateWeekBase();

    if (!me.isConfiguring) {
      me.updateDate(me.date, me.date);
    }
  }

  updateNonWorkingDays() {
    this.updateDayNumbers();
  }

  updateHideNonWorkingDays() {
    this.updateDayNumbers();
  }

  updateSixWeeks() {
    if (!this.isConfiguring) {
      this.updateDate(this.date);
    }
  }
  /**
   * The number of days in the calendar for this month. This will always be
   * a multiple of 7, because this represents the number of calendar cells
   * occupied by this month.
   * @property {Number}
   * @readonly
   */

  get dayCount() {
    // So for the example month, Dec 2018 has 42 days, from Mon 26th Nov (-4th Dec) 2018
    // to Sun 6th Jan (37th Dec) 2019
    return this.endDayOfMonth + 1 - this.startDayOfMonth;
  }
  /**
   * The number of weeks in the calendar for this month.
   * @property {Number}
   * @readonly
   */

  get weekCount() {
    return this.dayCount / 7;
  }
  /**
   * The date of the first cell in the calendar view of this month.
   * @property {Date}
   * @readonly
   */

  get startDate() {
    const me = this;

    if (me.year != null && me.month != null && me.startDayOfMonth != null) {
      return new Date(me.year, me.month, me.startDayOfMonth);
    }
  }
  /**
   * The date of the last cell in the calendar view of this month.
   * @property {Date}
   * @readonly
   */

  get endDate() {
    const me = this;

    if (me.year != null && me.month != null && me.startDayOfMonth != null) {
      return new Date(me.year, me.month, me.endDayOfMonth);
    }
  }
  /**
   * Iterates through all calendar cells in this month, calling the passed function for each date.
   * @param {Function} fn The function to call.
   * @param {Date} fn.date The date for the cell.
   */

  eachDay(fn) {
    for (let dayOfMonth = this.startDayOfMonth; dayOfMonth <= this.endDayOfMonth; dayOfMonth++) {
      fn(new Date(this.year, this.month, dayOfMonth));
    }
  }
  /**
   * Iterates through all weeks in this month, calling the passed function
   * for each week.
   * @param {Function} fn The function to call.
   * @param {Number[]} fn.week An array containing `[year, weekNumber]`
   * @param {Date[]} fn.dates The dates for the week.
   */

  eachWeek(fn) {
    const me = this,
          {
      weekCount
    } = me;

    for (let dayOfMonth = me.startDayOfMonth, week = 0; week < weekCount; week++) {
      const weekDates = [],
            weekOfYear = me.getWeekNumber(new Date(me.year, me.month, dayOfMonth));

      for (let day = 0; day < 7; day++, dayOfMonth++) {
        weekDates.push(new Date(me.year, me.month, dayOfMonth));
      }

      fn(weekOfYear, weekDates);
    }
  }
  /**
   * Returns the week of the year for the passed date. This returns an array containing *two* values,
   * the year **and** the week number are returned.
   *
   * The week number is calculated according to ISO rules, meaning that if the first week of the year
   * contains less than four days, it is considered to be the last week of the preceding year.
   *
   * The configured {@link #config-weekStartDay} is honoured in this calculation. So if the weekStartday
   * is **NOT** the ISO standard of `1`, (Monday), then the weeks do not coincide with ISO weeks.
   * @param {Date} date The date to calculate the week for.
   * @returns {Number[]} A numeric array: `[year, week]`
   */

  getWeekNumber(date) {
    const me = this;
    date = DateHelper.clearTime(date); // If it's a date in another year, use our otherMonth to find the answer.

    if (date.getFullYear() !== me.year) {
      return me.getOtherMonth(new Date(date.getFullYear(), 0, 1)).getWeekNumber(date);
    }

    let weekNo = Math.floor(DateHelper.diff(me.weekBase, date, 'day') / 7),
        year = date.getFullYear(); // No week 0. It's the last week of last year

    if (!weekNo) {
      // Week is the week of last year's 31st Dec
      return me.getOtherMonth(new Date(me.year - 1, 0, 1)).getWeekNumber(new Date(me.year, 0, 0));
    } // Only week 53 if year ends before our week's 5th day
    else if (weekNo === 53 && !me.has53weeks) {
        weekNo = 1;
        year++;
      } // 54 wraps round to 2 of next year
      else if (weekNo > 53) {
          weekNo = weekNo % 52;
        } // Return array of year and week number

    return [year, weekNo];
  }

  getWeekId(date) {
    const week = this.getWeekNumber(date);
    return week[0] * 100 + week[1];
  }

  getCellData(date) {
    const me = this,
          day = date.getDay(),
          visibleColumnIndex = me.visibleDayColumnIndex[day],
          isNonWorking = me.nonWorkingDays[day],
          isHiddenDay = me.hideNonWorkingDays && isNonWorking; // Automatically move to required month

    if (date < me.startDate || date > me.endDate) {
      me.month = date.getMonth();
    }

    return {
      day,
      visibleColumnIndex,
      isNonWorking,
      week: me.getWeekNumber(date),
      key: DateHelper.format(date, 'YYYY-MM-DD'),
      columnIndex: me.dayColumnIndex[day],
      date: new Date(date),
      isOtherMonth: Math.sign(date.getMonth() + date.getFullYear() * 12 - (me.month + me.year * 12)),
      visible: !isHiddenDay && date >= me.startDate && date < DateHelper.add(me.endDate, 1, 'day'),
      tomorrow: DateHelper.add(date, 1, 'day'),
      isRowStart: visibleColumnIndex === 0,
      isRowEnd: visibleColumnIndex === me.visibleColumnCount - 1
    };
  }

  updateDayNumbers() {
    const me = this,
          {
      weekStartDay,
      nonWorkingDays,
      hideNonWorkingDays
    } = me,
          dayColumnIndex = me.dayColumnIndex = [],
          canonicalDayNumbers = me.canonicalDayNumbers = [],
          visibleDayColumnIndex = me.visibleDayColumnIndex = []; // So, if they set weekStartDay to 1 meaning Monday which is ISO standard, we will
    // have mapping of internal day number to canonical day number (as used by Date class)
    // and to abbreviated day name like this:
    // canonicalDayNumbers = [1, 2, 3, 4, 5, 6, 0] // Use for translation from our day number to Date class's day number
    //
    // Also, we need a map from canonical day number to *visible* cell index.
    // for example, if we have weekStartDay as Monday which is 1, and non working days as
    // Wednesday, and hideNonWorkingDays:true, then the calendar would look like
    // +----+----+----+----+----+----+
    // | Mo | Tu | Th | Fr | Sa | Su |
    // +----+----+----+----+----+----+
    //
    // So we'd need this array
    // [ 5, 0, 1, undefined, 2, 3, 4]
    // Or think of it as this map:
    // {
    //      1 : 0,
    //      2 : 1,
    //      4 : 2,
    //      5 : 3,
    //      6 : 4,
    //      0 : 5
    // }
    // To be able to ascertain the cell index directly from the canonical day number.
    //
    // We also need a logical column map which would be
    // +----+----+----+----+----+----+----+
    // | Mo | Tu | We | Th | Fr | Sa | Su |
    // +----+----+----+----+----+----+----+
    //
    // So we'd need this array
    // [ 6, 0, 1, 2, 3, 4, 5]
    // Or think of it as this map:
    // {
    //      1 : 0,
    //      2 : 1,
    //      3 : 2
    //      4 : 3,
    //      5 : 4,
    //      6 : 5,
    //      0 : 6
    // }
    // We use this to cache the number of visible columns so that cell renderers can tell whether
    // they are on the last visible column.

    let visibleColumnIndex = 0;

    for (let columnIndex = 0; columnIndex < 7; columnIndex++) {
      const canonicalDay = (weekStartDay + columnIndex) % 7;
      canonicalDayNumbers[columnIndex] = canonicalDay;
      dayColumnIndex[canonicalDay] = columnIndex; // If this day is going to have visible representation, we need to
      // map it to a columnIndex;

      if (!hideNonWorkingDays || !nonWorkingDays[canonicalDay]) {
        visibleDayColumnIndex[canonicalDay] = visibleColumnIndex++;
      }
    }

    me.visibleColumnCount = visibleColumnIndex;
    me.weekLength = hideNonWorkingDays ? 7 - ObjectHelper.keys(nonWorkingDays).length : 7;
  }

} // Instance needed for internal tasks

const otherMonth = new Month(null);
Month._$name = 'Month';

/**
 * @module Core/util/Parser
 */
// Tools. Maybe move it to memoization module.

let nextObjectIdentity = 0;
const objectIdentityMap = new WeakMap();

const argsToCacheKey = (...args) => args.map(arg => {
  let result;

  if (arg && typeof arg == 'object' || typeof arg == 'function') {
    result = objectIdentityMap.get(arg);

    if (result === undefined) {
      result = ++nextObjectIdentity;
      objectIdentityMap.set(arg, result);
    }
  } else {
    result = String(arg);
  }

  return result;
}).join('-');
/**
 * Generic memoization function. Wraps `fn` into higher order function which caches `fn` result
 * using stringified arguments as the cache key.
 *
 * @param {Function} fn function to memoize
 */

const memo = fn => {
  const mlist = new Map();
  return (...args) => {
    const mkey = argsToCacheKey(args);
    let result = mlist.get(mkey);

    if (result === undefined) {
      result = fn(...args);
      mlist.set(mkey, result);
    }

    return result;
  };
};
/**
 * Specific memoization function caches `fn` calls. `fn` should recieve 2 arguments, the first one
 * is a string, and the second one is a callback which should be called by `fn` with some result.
 * The function returned wraps `fn` and it's callback such that `fn` would be called only once
 * with a particular first argument, other time callback will be called instantly with the result cached.
 *
 * @param {Function} fn function to memoize
 */

const memoCps = fn => {
  const table = new Map(),
        entryContinuations = entry => entry[0],
        entryResults = entry => entry[1],
        pushContinuation = (entry, cont) => entryContinuations(entry).push(cont),
        pushResult = (entry, result) => entryResults(entry).push(result),
        isResultSubsumed = (entry, result) => entryResults(entry).some(r => ObjectHelper.isEqual(r, result)),
        makeEntry = () => [[], []],
        isEmptyEntry = entry => !entryResults(entry).length && !entryContinuations(entry).length,
        tableRef = str => {
    let entry = table.get(str);

    if (entry === undefined) {
      entry = makeEntry();
      table.set(str, entry);
    }

    return entry;
  };

  return (str, cont) => {
    const entry = tableRef(str);

    if (isEmptyEntry(entry)) {
      pushContinuation(entry, cont);
      fn(str, result => {
        if (!isResultSubsumed(entry, result)) {
          pushResult(entry, result);
          entryContinuations(entry).forEach(cont => cont(result));
        }
      });
    } else {
      pushContinuation(entry, cont);
      entryResults(entry).forEach(result => cont(result));
    }
  };
}; // End of tools

const SUCCESS = Symbol('success');
const FAILURE = Symbol('failure');
/**
 * Successfull parsing result. Represented as array with 3 items:
 * - SUCCESS symbol which can be checked with {@link #function-isSuccess} function.
 * - Parsed payload
 * - Rest string left to parse
 *
 * @typedef {Array} SuccessResult
 */

/**
 * Creates successull parsing result with parsed `val` and unparsed `rest`
 *
 * @param {String} val Parsed value
 * @param {String} rest Unparsed rest
 * @return {SuccessResult}
 */

const success = (val, rest) => [SUCCESS, val, rest];
/**
 * Failure parsing result. Represented as array with 2 items:
 * - FAILURE symbol which can be checked with {@link #function-isSuccess} function
 * - Rest string left to parse
 *
 * @typedef {Array} FailureResult
 */

/**
 * Creates failed parsing result with unparsed `rest`
 *
 * @param {String} rest Unparsed rest
 * @return {FailureResult}
 */

const failure = rest => [FAILURE, rest];
/**
 * Checks if the given parsing `result` is successfull
 *
 * @param {SuccessfullResult|FailureResult} result
 */

const isSuccess = result => result.length && result[0] === SUCCESS;
/**
 * Resolves parser when needed. Parser should be resolved if it's defined as a function
 * with no arguments which returns the actual parser function with more then one argument.
 *
 * @param {Function} p Parser factory
 * @return {Function} Combinable parser function
 */

const resolveParser = p => typeof p === 'function' && !p.length ? p() : p;
/**
 * Returns combinable parser which always return successfull parsing result with `val`
 * as parsed result and string parsed as `rest`.
 *
 * @param {*} val Succefull parsing result parsed payload
 * @return {Function} Combinable parser function
 *
 * @example
 * const sp = succeed('Ok');
 * sp('My string', (r) => console.dir(r)) // Will output successfull parsing result with `Ok` payload and `My string` rest.
 */

const succeed = memo(val => memoCps((str, cont) => cont(success(val, str))));
/**
 * Returns combinable parser which succeeds if string parsed starts with `match`. The parsing
 * result will contain `match` as parsed result and rest of the string characters,
 * the ones after `match` as the unparsed rest.
 *
 * @param {String} match String to match
 * @return {Function} Combinable parser function
 *
 * @example
 * const mp = string('My');
 * mp('My string', (r) => console.dir(r)); // Will output successfull parsing result with `My` payload and `string` rest.
 */

const string = memo(match => memoCps((str, cont) => {
  const len = Math.min(match.length, str.length),
        head = str.substr(0, len),
        tail = str.substr(len);
  cont(head === match ? success(head, tail) : failure(tail));
}));
/**
 * Binds parser or parser factory with a `fn` function which should recieve one string argument
 * and return a combinable parser function.
 *
 * @param {Function} p Combinable parser function or combinable parser factory which can be
 *                     resolved using with {@link #function-resolveParser}.
 * @param {Function} fn A function recieving one string argument and returning combinable parser function.
 *
 * @internal
 */

const bind = (p, fn) => (str, cont) => resolveParser(p)(str, result => {
  if (isSuccess(result)) {
    const [, val, rest] = result;
    fn(val)(rest, cont);
  } else {
    cont(result);
  }
});
/**
 * Combines several combined parser functions or combinable parser factories in sequence such that second starts after first succeeds
 * third after second etc, if first fails then second will not be called and so on.
 *
 * @param {...Function} parsers Combinable parser function or combinable parser factory which can be
 *                      resolved using with {@link #function-resolveParser}.
 * @returns {Function} Combinable parser function
 *
 * @example
 * const ab = seq(string('a'), string('b'));
 * ab('abc', (r) => console.dir(r)); // Will output successfull parsing result with `ab` as parsed payload and `c` as the rest.
 */

const seq = memo((...parsers) => {
  const seq2 = memo((a, b) => memoCps(bind(a, x => bind(b, y => succeed([].concat(x, y))))));
  return parsers.reduce(seq2, succeed([]));
});
/**
 * Combines several combined parser functions or combinable parser factories in alteration such that successfull parsing result will be passed into
 * a callback if one of those parsers succeeds.
 *
 * @param {...Function} parsers Combinable parser function or combinable parser factory which can be
 *                      resolved using with {@link #function-resolveParser}.
 * @returns {Function} Combinable parser function
 *
 * @example
 * const aorb = alt(string('a'), string('b'));
 * aorb('abc', (r) => console.dir(r)); // Will output successfull parsing result with `a` as parsed payload and `bc` as the rest.
 * aorb('bbc', (r) => console.dir(r)); // Will output successfull parsing result with `b` as parsed payload and `bc` as the rest.
 */

const alt = memo((...parsers) => memoCps((str, cont) => parsers.forEach(p => resolveParser(p)(str, cont))));
/**
 * Creates combinable parser which succeeds if string to parse starts from a substring which succeeds for the regular expression
 * `pattern` the parser is created with.
 *
 * @param {String} pattern Regular expression pattern
 * @return {Function} Combinable parser function
 *
 * @example
 * const rp = regexp('a+');
 * rp('aaabb', (r) => console.dir(r)); // Will output successfull parsing result with `aaa` as parsed payload and `bb` as the rest.
 */

const regexp = memo(pattern => (str, cont) => {
  const rexp = new RegExp(`^${pattern}`),
        match = rexp.exec(str);

  if (match) {
    const head = match[0],
          tail = str.substr(head.length);
    cont(success(head, tail));
  } else {
    cont(failure(str));
  }
});
/**
 * Creates reducing combinable parser function which should be used to create semantic actions
 * on parsed results.
 *
 * @param {Function} p Combinable parser function or combinable parser factory which can be
 *                     resolved using with {@link #function-resolveParser}.
 * @param {Function} fn Semantic action function should be the same arity as the successfull result arity
 *                      of `p` parser.
 * @return {Function} Combinable parser function
 *
 * @example
 * const nump = red(
 *     regexp('\d'),
 *     Number
 * );
 *
 * const plusp = string('+');
 *
 * const sump = red(
 *     seq(nump, plusp, nump),
 *     (a, _, b) => a + b
 * );
 *
 * sump('7+8', (r) => console.dir(r)); // Will return successfull parsing result with `15` as parsing payload and `` as rest.
 */

const red = memo((p, fn) => bind(p, (...val) => succeed(fn(...[].concat.apply([], val)))));
/**
 * Runs combinable parsing function returning totaly parsed results only, i.e. such results which have
 * parsed the `str` string completely.
 *
 * @param {Function} body Combinable parser function
 * @param {String} str String to parse
 * @return {SuccessfullResult[]} All totaly parsed results possible for the given parsing function.
 */

const runParser = (body, str) => {
  const results = [];
  body(str, result => {
    if (isSuccess(result)) {
      const [,, left] = result;

      if (left === '') {
        results.push(result);
      }
    }
  });
  return results;
};
/**
 * Helper function for combinable parser definition supplements combinable parser function
 * returning a higher order function which when called with 2 arguments (string to parse and
 * a callback function) behaves exactly like parser function, but when called with 1 argument
 * it wraps call to parser function with {@link function-runParser} thus returning array of
 * totaly parsed results.
 *
 * @param {Function} body Combinable parser function
 */

const defineParser = body => (str, cont) => cont ? resolveParser(body)(str, cont) : runParser(resolveParser(body), str); //Combines exports in an object such that it was possible to export parser utilities
// in UMD/module bundles.

var Parser = {
  memo,
  memoCps,
  success,
  failure,
  isSuccess,
  resolveParser,
  succeed,
  string,
  bind,
  seq,
  alt,
  regexp,
  red,
  runParser,
  defineParser
};

let timeoutId$1;
function createTooltip$1(delay) {
  if (timeoutId$1) {
    clearTimeout(timeoutId$1);
  }

  if (delay === undefined) {
    delay = VersionHelper.isTestEnv ? 0 : 1000;
  }

  if (delay === 0) ; else {
    // eslint-disable-next-line
    timeoutId$1 = setTimeout(() => Field.errorTip, delay);
  }
}
/**
 * @module Core/widget/Field
 */

const byWeight$1 = (l, r) => (l.weight || 0) - (r.weight || 0),
      byWeightReverse$1 = (l, r) => (r.weight || 0) - (l.weight || 0);
/**
 * Base class for {@link Core.widget.TextField TextField} and {@link Core.widget.NumberField NumberField}. Not to be used directly.
 * All subclasses can be used as editors for the {@link Grid.column.Column Column}. The most popular are:
 * - {@link Core.widget.TextField TextField}
 * - {@link Core.widget.NumberField NumberField}
 * - {@link Core.widget.DateField DateField}
 * - {@link Core.widget.TimeField TimeField}
 * - {@link Core.widget.Combo Combo}
 *
 * @extends Core/widget/Widget
 * @mixes Core/widget/mixin/Badge
 * @abstract
 */

class Field extends Badge(Widget) {
  //region Config
  static get $name() {
    return 'Field';
  } // Factoryable type name

  static get type() {
    return 'field';
  }

  static get configurable() {
    return {
      /**
       * Text to display in empty field.
       * @config {String} placeholder
       */
      placeholder: null,

      /**
       * Gets or sets the value. The returned type will depend upon the Field subclass.
       *
       * `TextField` returns a `String`.
       *
       * `NumberField` returns a `Number`.
       *
       * `DateField` and `TimeField` return a `Date` object, and `null` if the field is empty.
       *
       * `Combo` will return a `String` if configured with `items` as a simple string array.
       * Otherwise it will return the {@link Core.widget.Combo#config-valueField} value from the
       * selected record, or `null` if no selection has been made.
       * @property {*}
       * @name value
       */

      /**
       * Default value
       * @config {String}
       */
      value: '',

      /**
       * Name of the field which is used as a key to get/set values from/to the field.
       * Used prior to {@link Core.widget.Widget#config-ref ref} and {@link Core.widget.Widget#config-id id} in
       * {@link Core.widget.Container#property-values Container.values}.
       *
       * The config is useful when the field is used in EventEditor or TaskEditor to load/save values automatically.
       * @config {String}
       */
      name: null,

      /**
       * Get/set fields label. Please note that the Field needs to have a label specified from start for this to
       * work, otherwise no element is created.
       * @property {String}
       * @name label
       */

      /**
       * Label, prepended to field
       * @config {String}
       * @category Label
       */
      label: null,

      /**
       * CSS class name or class names to add to any configured {@link #config-label}
       * @config {String|Object}
       * @category Label
       */
      labelCls: null,

      /**
       * The labels to add either before or after the input field.
       * Each label may have the following properties:
       * * `html` The label text.
       * * `align` `'start'` or `'end'` which end of the field the label should go.
       * @config {Object}
       * @category Label
       */
      labels: null,

      /**
       * The width to apply to the `<label>` element. If a number is specified, `px` will be used.
       * @config {String|Number}
       * @localizable
       * @category Label
       */
      labelWidth: {
        value: null,
        $config: {
          localeKey: 'L{labelWidth}'
        }
      },

      /**
       * Configure as `true` to indicate that a `null` field value is to be marked as invalid.
       * @config {Boolean}
       * @default false
       */
      required: null,

      /**
       * Show a trigger to clear field, and allow `ESC` key to clear field if this field is
       * not {@link #config-readOnly}. The trigger is available in the {@link #property-triggers} object
       * under the name `clear`. May also be an object which configures the `clear` {@link #property-triggers trigger}.
       * @config {Boolean|Object}
       * @default false
       */
      clearable: null,

      /**
       * If this field is not {@link #config-readOnly}, then setting this option means that pressing
       * the `ESCAPE` key after editing the field will revert the field to the value it had when
       * the user focused the field. If the field is _not_ changed from when focused, the {@link #config-clearable}
       * behaviour will be activated.
       * @config {Boolean}
       * @default false
       */
      revertOnEscape: null,

      /**
       * An optional string to display inside the input field as an overlay. This can be useful for displaying
       * a field's units.
       *
       * This config is ignored if {@link #config-hintHtml} is set.
       *
       * For example:
       * ```javascript
       *  {
       *      type  : 'numberfield',
       *      label : 'Temperature',
       *      hint  : '°C'
       *  }
       * ```
       *
       * This config can be set to a function to dynamically generate the `hint` text:
       * ```javascript
       *  {
       *      type  : 'numberfield',
       *      label : 'Duration',
       *      hint  : ({ value }) => (value === 1) ? 'Day' : 'Days'
       *  }
       * ```
       *
       * The function is passed an object with the following properties:
       *
       *  - `source` A reference to the field instance.
       *  - `value` The current value of the field.
       *
       * A `hint` function will be called when the field changes value.
       *
       * @config {String|Function}
       * @category Label
       */
      hint: null,

      /**
       * This config is similar to {@link #config-hint} except that this config is used to display HTML content.
       * Since this can allow malicious content to be executed, be sure not to include user-entered data or to
       * encode such data (see {@link Core.helper.StringHelper#function-encodeHtml-static}).
       *
       * If this config is set, {@link #config-hint} is ignored.
       *
       * For example:
       * ```javascript
       *  {
       *      type     : 'numberfield',
       *      label    : 'Temperature',
       *      hintHtml : '<i>°C</i>'
       *  }
       * ```
       *
       * This config can be set to a function to dynamically generate the `hintHtml` text:
       * ```javascript
       *  {
       *      type     : 'numberfield',
       *      label    : 'Duration',
       *      hintHtml : ({ value }) => (value === 1) ? '<i>Day</i>' : '<i>Days</i>'
       *  }
       * ```
       *
       * The function is passed an object with the following properties:
       *
       *  - `source` A reference to the field instance.
       *  - `value` The current value of the field.
       *
       * A `hintHtml` function will be called when the field changes value.
       *
       * @config {String|Function}
       * @category Label
       */
      hintHtml: null,

      /**
       * The width to apply to the `<input>` element. If a number is specified, `px` will be used.
       * @config {String|Number}
       * @category Input element
       */
      inputWidth: null,

      /**
       * The delay in milliseconds to wait after the last keystroke before triggering a change event.
       * Set to 0 to not trigger change events from keystrokes (listen for input event instead to have
       * immediate feedback, change will still be triggered on blur)
       * @config {Number}
       * @default
       */
      keyStrokeChangeDelay: 0,

      /**
       * Makes the field unmodifiable by user action. The input area is not editable, and triggers
       * are unresponsive.
       *
       * This is a wider-acting setting than {@link #config-editable} which *only* sets the
       * `readOnly` attribute of the `<input>` field.
       *
       * PickerFields such as `Combo` and `DateField` can be `editable : false`, but still
       * modifiable through the UI.
       * @config {Boolean}
       */
      readOnly: null,

      /**
       * Set to false to prevent user from editing the field. For TextFields it is basically the same as setting
       * {@link #config-readOnly}, but for PickerFields there is a distinction where it allows you to pick a value
       * but not to type one in the field.
       *
       * PickerFields such as `Combo` and `DateField` can be `editable : false`, but still
       * modifiable through the UI.
       * @config {Boolean}
       * @default true
       */
      editable: true,
      defaultAction: 'change',

      /**
       * The trigger Widgets as specified by the {@link #config-triggers} configuration and the
       * {@link #config-clearable} configuration. Each is a {@link Core.widget.Widget Widget} instance which may
       * be hidden, shown and observed and styled just like any other widget.
       * @property {Object}
       * @name triggers
       */

      /**
       * The triggers to add either before or after the input field. Each property name is the reference by which
       * an instantiated Trigger Widget may be retrieved from the live `{@link #property-triggers}` property.
       *
       * Each trigger may have the following properties:
       * * `cls` The CSS class to apply.
       * * `handler` A method in the field to call upon click
       * * `align` `'start'` or `'end'` which end of the field the trigger should go.
       * * `weight` (Optional) Higher weighted triggers gravitate towards the input field.
       *
       * ```javascript
       * const textField = new TextField({
       *   triggers : {
       *       check : {
       *           cls : 'b-fa b-fa-check',
       *           handler() {
       *               ...
       *           }
       *       },
       *       ...
       *   }
       * })
       * ```
       *
       * @config {Object}
       */
      triggers: null,

      /**
       * Specify `false` to prevent field from being highlighted when on external value changes
       * @config {Boolean}
       */
      highlightExternalChange: true,
      localizableProperties: ['label', 'title', 'placeholder', 'labelWidth'],
      autoSelect: false,

      /**
       * Sets the native `autocomplete` property of the underlying input element. For more information, please refer to
       * [MDN](https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autocomplete)
       * @config {String}
       * @default
       * @category Input element
       */
      autoComplete: 'off',

      /**
       * Sets custom attributes of the underlying input element. For more information, please refer to
       * [MDN](https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes)
       * @config {Object}
       * @default
       * @category Input element
       */
      inputAttributes: null,

      /**
       * Sets the `type` attribute of the underlying input element.
       * @config {String}
       * @private
       * @category Input element
       */
      inputType: null,

      /**
       * Text alignment for the input field.
       * @config {String}
       * @category Input element
       */
      inputAlign: null,

      /**
       * A list of property names to be set in the underlying input element from properties
       * by the same name in this Field object if the value is not `== null`.
       * @private
       * @category Input element
       */
      attributes: {
        value: ['placeholder', 'autoComplete', 'minLength', 'maxLength', 'pattern', 'tabIndex', 'min', 'max'],
        $config: {
          merge: 'distinct'
        }
      },
      nullValue: {
        $config: null,
        value: null,
        default: null // store _value=null on prototype

      },
      updatedClsDuration: 500,
      inputReadOnly: false,
      testConfig: {
        updatedClsDuration: 10
      }
    };
  }

  static get delayable() {
    return {
      highlightChanged: 'raf'
    };
  }

  doDestroy() {
    var _me$inputListenerRemo, _me$keyListenerRemove;

    const me = this,
          {
      triggers
    } = me,
          errorTip = Field._errorTip;
    (_me$inputListenerRemo = me.inputListenerRemover) === null || _me$inputListenerRemo === void 0 ? void 0 : _me$inputListenerRemo.call(me);
    (_me$keyListenerRemove = me.keyListenerRemover) === null || _me$keyListenerRemove === void 0 ? void 0 : _me$keyListenerRemove.call(me);
    super.doDestroy();

    if (triggers) {
      for (const t of Object.values(triggers)) {
        t.destroy();
      }
    } // The errorTip references this field, hide it when we die.

    if ((errorTip === null || errorTip === void 0 ? void 0 : errorTip.field) === me) {
      errorTip.hide();
    }
  }

  get invalidValueError() {
    return 'L{invalidValue}';
  }
  /**
   * A singleton error tooltip which activates on hover of invalid fields.
   * before show, it gets a reference to the field and interrogates its
   * active error list to display as the tip content.
   * @member {Core.widget.Tooltip}
   * @readonly
   */

  get errorTip() {
    return this.constructor.errorTip;
  }
  /**
   * A singleton error tooltip which activates on hover of invalid fields.
   * before show, it gets a reference to the field and interrogates its
   * active error list to display as the tip content.
   * @member {Core.widget.Tooltip}
   * @readonly
   * @static
   */

  static get errorTip() {
    return Field._errorTip || (Field._errorTip = new Tooltip({
      id: 'bryntum-field-errortip',
      cls: 'b-field-error-tip',
      forSelector: '.b-field.b-invalid .b-field-inner',
      align: 'l-r',
      scrollAction: 'realign',

      onBeforeShow() {
        const tip = this,
              field = Widget.fromElement(tip.activeTarget);

        if (field) {
          const errors = field.getErrors();

          if (errors) {
            tip.html = errors.join('<br>');
            tip.field = field;
            return true;
          }
        } // Veto show

        return false;
      }

    }));
  } //endregion
  //region Event

  /**
   * Fired when the user types into this field.
   * @event input
   * @param {Core.widget.Field} source This field.
   * @param {String} value - This field's value
   * @param {Event} event - The triggering DOM event.
   */

  /**
   * Fired when this field's value changes.
   * @event change
   * @param {String} value - This field's value
   * @param {String} oldValue - This field's previous value
   * @param {Boolean} valid - True if this field is in a valid state.
   * @param {Event} [event] - The triggering DOM event if any.
   * @param {Boolean} userAction - Triggered by user taking an action (`true`) or by setting a value (`false`)
   * @param {Core.widget.Field} source - This Field
   */

  /**
   * User performed default action (typed into this field).
   * @event action
   * @param {String} value - This field's value
   * @param {String} oldValue - This field's previous value
   * @param {Boolean} valid - True if this field is in a valid state.
   * @param {Event} [event] - The triggering DOM event if any.
   * @param {Boolean} userAction - Triggered by user taking an action (`true`) or by setting a value (`false`)
   * @param {Core.widget.Field} source - This Field
   */

  /**
   * Fired when this field is {@link #function-clear cleared}.
   *
   * This will be triggered when a user clicks this field's clear {@link #property-triggers trigger}
   * @event clear
   * @param {Core.widget.Field} source - This Field
   */

  /**
   * User clicked one of this field's {@link #property-triggers}
   * @event trigger
   * @param {Core.widget.Field} source This field
   * @param {Core.widget.Widget} trigger The trigger activated by click or touch tap.
   */
  //endregion
  //region Init

  construct(config) {
    super.construct(config);
    const me = this; // Instantiate error tip
    // In some demos this code may execute when body does not yet exist (happens in UMD bundles)
    // Create the singleton if DOM is ready

    if (Tooltip.listenersTarget) {
      createTooltip$1();
    } // wait till DOM is ready and then initialize Field.errorTip
    else {
        document.addEventListener('DOMContentLoaded', createTooltip$1);
      }

    if (me.keyStrokeChangeDelay) {
      me.changeOnKeyStroke = me.buffer(me.internalOnChange, me.keyStrokeChangeDelay);
    }
  }

  onFocusIn(e) {
    this.valueOnFocus = ObjectHelper.clone(this.value);
    super.onFocusIn(e);
  }

  onFocusOut(e) {
    super.onFocusOut(e); // Required field not flagged with error initially, flag on blur instead for better appearance

    this.syncRequired(); // Check field consistency on blur

    this.onEditComplete();
  }
  /**
   * Template function which may be implemented by subclasses to synchronize
   * input state and validity state upon completion of the edit.
   * @internal
   */

  onEditComplete() {}

  set element(element) {
    const me = this,
          value = me.initialConfig.value,
          innerElements = me.innerElements,
          children = [{
      className: 'b-field-inner',
      reference: 'inputWrap',
      children: innerElements
    }],
          startTriggers = [],
          endTriggers = [];
    let hasStartTrigger;
    const labels = me.labels || [];

    if (me.label) {
      labels.push({
        reference: 'labelElement',
        className: me.labelCls,
        html: me.label
      });
    }

    if (labels) {
      labels.forEach(label => {
        const entry = Object.assign({
          tag: 'label',
          htmlFor: `${me.id}-input`,
          className: `b-align-${label.align || 'start'}`
        }, label);

        if (!label.align || label.align === 'start') {
          children.unshift(entry);
        } else {
          children.push(entry);
        }
      });
    } // Force evaluation of clearable so triggers include the clearable trigger.

    me.getConfig('clearable');

    for (const triggerRef in me.triggers) {
      const trigger = me.triggers[triggerRef];

      if (trigger.align === 'start') {
        startTriggers.unshift(trigger);
        hasStartTrigger = true;
      } else {
        endTriggers.push(trigger);
      }
    } // The triggers at each end are sorted "gavitationally".
    // Higher weight sorts towards the center which is the input element.

    startTriggers.sort(byWeight$1);
    endTriggers.sort(byWeightReverse$1);
    innerElements.unshift(...startTriggers.map(t => t.element));
    innerElements.push(...endTriggers.map(t => t.element));
    super.element = {
      children,
      className: {
        'b-has-label': labels.length,
        'b-has-start-trigger': hasStartTrigger,
        'b-readonly': me.readOnly
      }
    }; // Value must be injected into the input element after it has been constructed, not in the
    // initial template, otherwise the caret position will not be as expected.

    if (value != null) {
      me.value = value;
    }

    me.syncEmpty();
    me.updateInputReadOnly(me.inputReadOnly);
    me.syncInvalid();
    const keyEventElement = me.input || me.focusElement;

    if (keyEventElement) {
      me.keyListenerRemover = EventHelper.on({
        element: keyEventElement,
        thisObj: me,
        keydown: 'internalOnKeyEvent',
        keypress: 'internalOnKeyEvent',
        keyup: 'internalOnKeyEvent'
      });
    }
  }

  get element() {
    return super.element;
  } // Subclasses may implement this.
  // Needed because Checkbox produces an array of two elements as its innerElements

  get innerElements() {
    return [this.setInputAttributes(this.inputElement)];
  }

  get inputElement() {
    const me = this,
          config = {
      tag: 'input',
      reference: 'input',
      class: me.inputCls || '',
      value: me._value || '',
      name: me.name || me.id,
      id: `${me.id}-input`
    };

    if (me.inputType) {
      config.type = me.inputType;
    }

    return config;
  }
  /**
   * Sets the inputAttributes and the attributes on the passed input field (or config for an input field
   * which it promoted to be an element).
   * For use by subclass implementations of `get innerElements` which return an array of child
   * configs or elements which should be children of the `inputWrap` element.
   * @param {HTMLElement|Object} inputField The element or DomHelper config to have attributes set.
   * @returns {HTMLElement} The element with the attributes set
   * @private
   */

  setInputAttributes(inputField) {
    const me = this;

    if (!(inputField instanceof HTMLElement)) {
      const {
        reference
      } = inputField;
      inputField = DomHelper.createElement(inputField);

      if (reference) {
        inputField = inputField[reference];
      }
    } // Propagate config values listed in the attributes property from this into the input element.

    me.attributes.forEach(attr => {
      const value = me[attr];

      if (value != null) {
        inputField.setAttribute(attr, value);
      }
    }); // Add custom inputAttributes provided

    me.inputAttributes && Object.entries(me.inputAttributes).forEach(([attr, value]) => {
      if (value != null) {
        inputField.setAttribute(attr, value);
      }
    });
    return inputField;
  } //endregion
  //region Focus & select

  get caretPos() {
    return this.textSelection[0];
  }

  set caretPos(value) {
    this.textSelection = value;
  }

  get focusElement() {
    return this.input;
  }

  get textSelection() {
    const input = this.input;
    let ret;

    try {
      ret = [input.selectionStart, input.selectionEnd, input.selectionDirection];
    } catch (e) {
      // ignore (some input types cannot do this)
      ret = input.value;
      ret = ret ? ret.length : 0;
      ret = [ret, ret];
    }

    return ret;
  }

  set textSelection(value) {
    if (typeof value === 'number') {
      this.select(value, value);
    } else {
      this.select(...value);
    }
  }

  get hasTextSelection() {
    const [selectionStart, selectionEnd] = this.textSelection;
    return selectionStart && selectionEnd - selectionStart > 0;
  }
  /**
   * Returns the input value for this field's input element that will be present if
   * the event carrying the given text is allowed to proceed.
   * @param {String} text
   * @returns {String}
   * @private
   */

  getAfterValue(text) {
    const [begin, end] = this.textSelection,
          value = this.input.value;
    return `${value.substr(0, begin)}${text}${value.substr(end || begin)}`;
  }
  /**
   * Selects the field contents. Optionally may be passed a start and end.
   * NOTE: This method is async for IE11
   * @param {Number} [start] The start index from which to select the input.
   * @param {Number} [end] The index at which to end the selection of the input.
   */

  select(start, end) {
    // Use focusElement which is the input field in this class
    // but allows subclasses to use other elements.
    // See, for example, TextAreaField
    const input = this.focusElement;

    if (input.value.length) {
      if (arguments.length === 0) {
        this.selectAll();
        return;
      } // Only allowed to select range in certain element / input types

      if (!this.supportsTextSelection) {
        return; // throw new Error('Trying to select text on an invalid element type');
      }

      if (BrowserHelper.isIE11) {
        // this.clearTimeout(this.selectTimeout);
        // HACK: IE focus processing is async and we can't select text until field is focused
        // Getting flaky exception in IE, something timing related. Found no way to detect it so using this
        // workaround for now
        input.focus();
        input.setSelectionRange(start, end); //
        // this.selectTimeout = this.setTimeout(() => {
        //     input && input.setSelectionRange(start, end);
        // }, 10);
      } else {
        input.setSelectionRange(start, end);
      }
    }
  }

  moveCaretToEnd() {
    const input = this.input;

    if (input.createTextRange) {
      const range = input.createTextRange();
      range.collapse(false);
      range.select();
    } else if (this.supportsTextSelection) {
      // Move caret to the end if possible
      this.select(input.value.length, input.value.length);
    }
  }

  selectAll() {
    this.focusElement.select();
  } // called on value changes to update styling of empty vs non-empty field

  syncEmpty() {
    const {
      isEmptyInput,
      isEmpty,
      element,
      clearIcon
    } = this,
          empty = isEmptyInput && isEmpty;

    if (element) {
      if (clearIcon) {
        // IE11...
        clearIcon.classList[empty ? 'add' : 'remove']('b-icon-hidden');
      } // IE11...

      element.classList[empty ? 'add' : 'remove']('b-empty');
    }
  }

  updateHint() {
    this.syncHint();
  }

  updateHintHtml() {
    this.syncHint();
  }

  syncHint() {
    const me = this,
          {
      input,
      hint,
      hintHtml
    } = me,
          parent = input === null || input === void 0 ? void 0 : input.parentElement;

    if (input) {
      let hintValue = hintHtml || hint;
      const hintElement = me.hintElement || hintValue && (me.hintElement = DomHelper.createElement({
        parent,
        className: 'b-field-hint',
        nextSibling: input.nextSibling,
        children: [{
          className: 'b-field-hint-content'
        }]
      }));

      if (hintElement) {
        if (typeof hintValue === 'function') {
          hintValue = hintValue({
            source: me,
            value: me.value
          });
        }

        hintElement.firstChild[hintHtml ? 'innerHTML' : 'textContent'] = hintValue || '';
      }

      me.element.classList[hintValue ? 'remove' : 'add']('b-field-no-hint');
    }
  }

  syncInvalid() {
    this.updatingInvalid = true;
    const {
      isValid,
      element,
      inputWrap
    } = this; // errorTip needs Tooltip.listenersTarget to be there
    // otherwise it doesn't setup listeners and cannot notice we mouseove an invalid field

    const errorTip = Tooltip.listenersTarget && this.errorTip;

    if (element) {
      element.classList[isValid ? 'remove' : 'add']('b-invalid'); // We achieved validity, so ensure the error tip is hidden

      if (isValid) {
        if (errorTip !== null && errorTip !== void 0 && errorTip.isVisible && errorTip.field === this) {
          errorTip.hide();
        }
      } // If the mouse is over, the tip should spring into view
      else if (errorTip && inputWrap.contains(Tooltip.currentOverElement)) {
          // Already shown by this field's inputWrap, just update content.
          if (errorTip.activeTarget === inputWrap && errorTip.isVisible) {
            errorTip.onBeforeShow();
          } else {
            errorTip.activeTarget = inputWrap;
            errorTip.showBy(inputWrap);
          }
        }
    }

    this.updatingInvalid = false;
  } //endregion
  //region Getters/setters

  updateEditable() {
    this.syncInputReadOnly();
  }

  syncInputReadOnly() {
    this.getConfig('readOnly'); // make sure our config is initialized...
    // but since the readOnly getter conflates disabled into it, we ultimately have to look at _readOnly:

    this.inputReadOnly = this._readOnly || this.editable === false;
  }

  updateInputReadOnly(readOnly) {
    const me = this,
          {
      input,
      inputListenerRemover
    } = me; // Editable refers *ONLY* to the readOnly state of the <input> field within the field.
    // It does *NOT* imply that the field is not modifiable by user interaction.
    // For example, a Combo or DateField may be not editable, but may still be set through the UI.
    // It is the readOnly config which disables user interaction from modifying the field.

    if (input) {
      input.readOnly = readOnly ? 'readOnly' : null;

      if (readOnly) {
        me.inputListenerRemover = inputListenerRemover === null || inputListenerRemover === void 0 ? void 0 : inputListenerRemover();
      } else if (!inputListenerRemover) {
        me.inputListenerRemover = EventHelper.on({
          element: input,
          thisObj: me,
          focus: 'internalOnInputFocus',
          change: 'internalOnChange',
          input: 'internalOnInput'
        });
      }
    }
  }

  changeReadOnly(value) {
    return Boolean(value);
  }

  updateReadOnly(readOnly) {
    var _this$element;

    // We can be called during element creation, so "this.element" may not yet be available
    (_this$element = this.element) === null || _this$element === void 0 ? void 0 : _this$element.classList[readOnly ? 'add' : 'remove']('b-readonly');
    this.syncInputReadOnly();
  }

  updateClearable(clearable) {
    const me = this;
    me.getConfig('triggers');
    me.triggers = {
      clear: clearable && ObjectHelper.assign({
        cls: 'b-icon-remove',
        weight: 1000,

        handler() {
          me._isUserAction = true;
          me.clear();
          me._isUserAction = false;
        }

      }, clearable) || null
    };
  }

  changeTriggers(triggers) {
    const me = this,
          manager = me.$triggers || (me.$triggers = new DynamicObject({
      factory: Field.Trigger,
      inferType: false,
      // the name of a trigger in the triggers object is not its type
      owner: me,

      setup(config, name) {
        config.reference = config.ref = name;
        config.parent = me;
      }

    }));

    if (me.stepTriggers === false && (triggers.back || triggers.forward)) {
      triggers = ObjectHelper.assign({}, triggers);
      delete triggers.back;
      delete triggers.forward;
    }

    manager.update(triggers);
    manager.flush();
    return manager.target;
  }

  updateLabelWidth(newValue) {
    if (this.labelElement) {
      this.labelElement.style.flex = `0 0 ${DomHelper.setLength(newValue)}`; // If there's a label width, the input must conform with it, and not try to expand to 100%

      this.inputWrap.style.flexBasis = newValue == null ? '' : 0;
    }
  }

  updateInputWidth(newValue) {
    this.input.style.width = DomHelper.setLength(newValue); // requires to make "Should support inputWidth with label" test green

    if (BrowserHelper.isIE11) {
      this.input.parentElement.style.flexBasis = this.input.style.width;
    }

    this.element.classList.add('b-has-width');
  }

  updateInputAlign(newValue) {
    this.input.style.textAlign = newValue;
  }

  changeLabel(label) {
    return label == null ? '' : label;
  }

  updateLabel(label) {
    // since value is used in template it is not certain that element is available
    // TODO: move the code from template here instead
    if (this.labelElement) {
      // using innerHTML since we sometimes use icons as label
      this.labelElement.innerHTML = label;
    }
  }
  /**
   * Returns true if the field value is valid
   * @type {Boolean}
   * @readonly
   */

  get isValid() {
    const me = this; // Disabled fields are considered valid

    if (!me.disabled) {
      me.syncRequired();

      if (me.errors && Object.keys(me.errors).length) {
        return false;
      }

      const validity = me.validity;

      if (validity) {
        return validity.valid;
      }
    }

    return true;
  }
  /**
   * Returns `true` if this field is empty. That is, if it would violate the {@link #config-required}
   * setting.
   *
   * This may have different definitions in subclasses from simple text fields.
   * @type {boolean}
   * @readonly
   */

  get isEmpty() {
    return this.value == null || this.value === '';
  }
  /**
   * Returns true if the field's input is empty
   * @type {boolean}
   * @readonly
   */

  get isEmptyInput() {
    return !this.input || this.input.value == null || this.input.value === '';
  }
  /**
   * Returns the DOM `ValidityState` for this widget's input element, or `null` if there
   * isn't one.
   * @returns {ValidityState}
   * @private
   */

  get validity() {
    const input = this.input;
    return input && input.validity;
  }

  changeValue(value, was) {
    if (value == null) {
      value = this.nullValue;
    } // In cases of arrays, for example, we need to return "was" to pass the === check in the setter to convince
    // the config that, in fact, no change has occurred.

    if (this.hasChanged(was, value)) {
      return value;
    } // When loading a record into a form, an empty value might be loaded into a field, which is not detected as a
    // change. In this scenario it should still be flagged as invalid

    if (value === '') {
      this.syncRequired();
    }

    return was;
  }

  updateValue(value, oldValue) {
    const me = this; // Do not flag with error if configured empty, looks ugly to have fields start red

    if (!me.isConfiguring) {
      me.syncRequired(); // Do not trigger change event during configuration phase
      // or during keyboard input

      if (!me.inputting) {
        // trigger change event, signaling that origin is from set operation,
        // makes it easier to ignore such events in applications that set value on load etc
        me.triggerChange();
      }
    } // lastValue is used for IE to check if a change event should be triggered when pressing ENTER

    if (!me.inputting) {
      me._lastValue = value;
    }

    me.syncInputFieldValue();
  }
  /**
   * Compare's this field's value with its previous value. May be overridden in subclasses
   * which have more complex value types. See, for example, {@link Core.widget.DurationField}.
   * @param {*} oldValue
   * @param {*} newValue
   * @private
   */

  hasChanged(oldValue, newValue) {
    return newValue !== oldValue;
  }
  /**
   * Called by the base Field class's `set value` to sync the state of the UI with the field's value.
   *
   * Relies upon the class implementation of `get inputValue` to return a string representation of
   * the value for user consumption and editing.
   * @private
   */

  syncInputFieldValue(skipHighlight = false) {
    const me = this,
          {
      input,
      parent
    } = me; // If we are updating from internalOnInput, we must not update the input field

    if (input && !me.inputting) {
      // Subclasses may implement their own read only inputValue property.
      me.input[me.inputValueAttr] = me.inputValue; // TODO : This highlighting logic needs extracting/clarifying
      // If it's being manipulated from the outside, highlight it

      if (!me.isConfiguring && !me.containsFocus && me.highlightExternalChange) {
        input.classList.remove('b-field-updated');
        me.clearTimeout('removeUpdatedCls');

        if (!skipHighlight && (!parent || !parent.isSettingValues || !parent._isSettingValues.preventHighlight)) {
          me.highlightChanged();
        }
      }
    }

    me.syncEmpty();
    me.syncInvalid();
  }

  highlightChanged() {
    this.input.classList.add('b-field-updated');
    this.setTimeout('removeUpdatedCls', this.updatedClsDuration);
  }

  removeUpdatedCls() {
    this.input.classList.remove('b-field-updated');
  }
  /**
   * A String representation of the value of this field for {@link #function-syncInputFieldValue} to use
   * as the input element's value.
   *
   * Subclasses may override this to create string representations.
   *
   * For example, {@link Core.widget.DateField}'s implementation will format the field date
   * value according to its configured {@link Core.widget.DateField#config-format}. And {@link Core.widget.Combo}'s
   * implementation will return the {@link Core.widget.Combo#config-displayField} of the selected record.
   * @internal
   * @readOnly
   */

  get inputValue() {
    // Do not use the _value property. If called during configuration, this
    // will import the configured value from the config object.
    return this.value == null ? '' : this.value;
  }

  get inputValueAttr() {
    return 'value';
  }

  get supportsTextSelection() {
    const input = this.focusElement; // Text selection using setSelectionRange is allowed in Chrome for certain elements. Edge supports it even for input[type=number]

    return input && (input.tagName.toLowerCase() === 'textarea' || input.type && (/text|search|password|tel|url/.test(input.type) || BrowserHelper.isEdge));
  } //endregion
  //region Events

  internalOnInputFocus() {
    const length = this.input.value.length; // Help IE to set caret at the end like the other browsers

    if (BrowserHelper.isIE11 && length && !this.autoSelect) {
      this.select(length, length);
    }
  }
  /**
   * Trigger event when fields input changes
   * @fires change
   * @private
   */

  internalOnChange(event) {
    const me = this; // Don't trigger change if we enter invalid value or if value has not changed (for IE when pressing ENTER)

    if (me.isValid && me.hasChanged(me._lastValue, me.value)) {
      me.triggerChange(event, true);
      me._lastValue = me.value;
    }
  }

  triggerChange(event, userAction = Boolean(this._isUserAction)) {
    const me = this,
          {
      value,
      _lastValue: oldValue,
      // TODO: The `internalOnChange`-path excludes invalid changes, while the `set value`-path includes them
      isValid: valid
    } = me;
    me.syncHint(); // trigger change event, signaling that origin is from user

    me.triggerFieldChange({
      value,
      oldValue,
      event,
      userAction,
      valid
    }); // per default Field triggers action event on change, but might be reconfigured in subclasses (such as Combo)

    if (me.defaultAction === 'change') {
      me.trigger('action', {
        value,
        oldValue,
        event,
        userAction,
        valid
      });
    } // since Widget has Events mixed in configured with 'callOnFunctions' this will also call onClick and onAction

  }
  /**
   * Trigger event when user inputs into field
   * @fires input
   * @param event
   * @private
   */

  internalOnInput(event) {
    const me = this; // Keep the value synced with the inputValue at all times.

    me.inputting = true;
    me.value = me.input.value;
    me.inputting = false;
    me.trigger('input', {
      value: me.value,
      event
    });
    me.changeOnKeyStroke && me.changeOnKeyStroke(event); // since Widget has Events mixed in configured with 'callOnFunctions' this will also call onInput
  }

  internalOnKeyEvent(event) {
    const me = this,
          {
      value
    } = me;
    let stopEvent = false;

    if (event.type === 'keydown') {
      if (event.key === 'Escape' && !(me.readOnly || me.disabled)) {
        // We can be started with an initialValue which takes precedence over the valueOnFocus.
        // Because in some situations focus can move out and back in after a change which needs
        // to be revertable. For example in cell editing.
        const initialValue = 'initialValue' in me ? me.initialValue : me.valueOnFocus,
              valueChanged = me.hasChanged(initialValue, value),
              needsInputSync = me.input.value !== String(me.inputValue); // We revert on escape if we are configured to do so AND:
        // We are in an invalid state, or the value has changed, or the displayed value doesn't match the field value.

        if (me.revertOnEscape && (!me.isValid || valueChanged || needsInputSync)) {
          if (valueChanged) {
            me.value = initialValue;
          }

          if (needsInputSync) {
            me.syncInputFieldValue(true);
          }

          me.clearError();
          stopEvent = valueChanged && me.isValid;
        } else if (me.clearable && (value || me.input.value)) {
          me.clear();
        }
      } // #5730 - IE11 doesn't trigger "change" event by Enter click
      else if (event.key === 'Enter' && BrowserHelper.isIE11) {
          me.internalOnChange(event);
        }
    } // The above processing might have destructive consequences.

    if (!me.isDestroyed) {
      // If the keystroke had the effect of changing the field, prevent other handlers
      // which may mask that effect. Such as ESC exiting some UI context. Keep it contained.
      if (stopEvent) {
        event.stopImmediatePropagation();
      }

      me.trigger(event.type, {
        event
      });
    }
  }
  /**
   * Clears the value of this Field, and triggers the {@link #event-clear} event.
   */

  clear() {
    this.value = this.nullValue;
    this.trigger('clear');
  }
  /**
   * Called when disabled state is changed.
   * Used to add or remove 'b-invalid' class for the invalid field based on current disabled state.
   * @private
   */

  onDisabled() {
    this.syncInvalid();
  } //endregion
  //region Error

  syncRequired() {
    var _me$parent;

    const me = this; // Empty valid if any ancestor Container is setting values

    if (!me.isConfiguring && me.required && me.isEmpty && !((_me$parent = me.parent) !== null && _me$parent !== void 0 && _me$parent.isSettingValues)) {
      me.setError('L{fieldRequired}', me.updatingInvalid);
    } else {
      me.clearError('L{fieldRequired}', me.updatingInvalid);
    }
  }
  /**
   * Adds an error message to the list of errors on this field.
   * By default the field's valid/invalid state is updated; pass
   * `false` as the second parameter to disable that if multiple
   * changes are being made to the error state.
   * @param {String} error A locale string, or message to use as an error message.
   * @param {Boolean} [silent=false] Pass as `true` to skip updating the field's valid/invalid state.
   */

  setError(error, silent) {
    // Error messages are deduped by using them as the property names in an object.
    (this.errors || (this.errors = {}))[this.optionalL(error)] = 1;

    if (!silent) {
      this.syncInvalid();
    }
  }
  /**
   * Removes an error message from the list of errors on this field.
   * By default the field's valid/invalid state is updated; pass
   * `false` as the second parameter to disable that if multiple
   * changes are being made to the error state.
   * @param {String} [error] A locale string, or message to remove. If not passed, all errors are cleared.
   * @param {Boolean} [silent=false] Pass as `true` to skip updating the field's valid/invalid state.
   */

  clearError(error, silent) {
    if (this.errors) {
      if (error) {
        delete this.errors[this.optionalL(error)];
      } else {
        this.errors = {};
      }
    }

    if (!silent) {
      this.syncInvalid();
    }
  }
  /**
   * Returns an array of error messages as set by {@link #function-setError}, or
   * `undefined` if there are currently no errors.
   * @return {String[]} The errors for this field, or `undefined` if there are no errors.
   */

  getErrors() {
    const me = this;

    if (!me.isValid) {
      const validity = me.validity,
            // See possible state names: https://developer.mozilla.org/en-US/docs/Web/API/ValidityState
      stateName = ObjectHelper.allKeys(validity).find(key => key !== 'valid' && key !== 'customError' && validity[key]),
            errorKeys = me.errors && Object.keys(me.errors);
      let errors;

      if (errorKeys !== null && errorKeys !== void 0 && errorKeys.length) {
        errors = errorKeys;
      } // If custom error message was set using https://developer.mozilla.org/en-US/docs/Web/API/HTMLObjectElement/setCustomValidity
      else if (validity.customError) {
          errors = [me.input.validationMessage];
        } // If invalid state found, translate it
        else if (stateName) {
            // Do not remove. Assertion strings for Localization sanity check.
            // 'L{foo}'
            // 'L{badInput}'
            // 'L{patternMismatch}'
            // 'L{rangeOverflow}'
            // 'L{rangeUnderflow}'
            // 'L{stepMismatch}'
            // 'L{tooLong}'
            // 'L{tooShort}'
            // 'L{typeMismatch}'
            // 'L{valueMissing}'
            errors = [me.L(stateName, {
              // In case min/max limits are present they will be used in the translation
              min: me.min,
              max: me.max
            })];
          } // If built-in state is 'valid' but me.isValid is false, show our invalid message
          else {
              errors = [me.L(me.invalidValueError)];
            }

      if (errors && errors.length > 0) {
        return errors;
      }
    }
  } //endregion

}
/**
 * Base class for field triggers May be configured with a `cls` and a `handler` which is a function (or name of a function)
 * in the owning Field.
 * @classtype trigger
 * @extends Core/widget/Widget
 */

Field.Trigger = class FieldTrigger extends Widget {
  static get $name() {
    return 'FieldTrigger';
  }

  static get factoryable() {
    return {
      defaultType: Field.Trigger,
      extends: null
    };
  } // Factoryable type name

  static get type() {
    return 'trigger';
  } // Align is a simple string at this level

  static get configurable() {
    return {
      align: null
    };
  }

  template() {
    return `<div class="b-icon b-align-${this.align || 'end'}"></div>`;
  }

  construct(config) {
    super.construct(config);
    EventHelper.on({
      element: this.element,
      click: {
        handler: 'onClick',
        thisObj: this
      },
      mousedown: {
        handler: 'onMousedown',
        thisObj: this
      }
    });
  }

  changeAlign(align) {
    return align; // Widget base class converts to an object
  }

  onClick(e) {
    const me = this,
          field = me.field,
          handler = typeof me.handler === 'function' ? me.handler : field[me.handler];

    if (field.disabled || field.readOnly) {
      return;
    }

    if (handler && field.trigger('trigger', {
      trigger: me
    }) !== false) {
      handler.call(field, e);
    }
  }

  onMousedown(e) {
    const field = this.field,
          isKeyEvent = ('key' in e); // If it's a touch tap on the trigger of an editable, then
    // avoid the keyboard by setting the field to not be editable
    // before focusing the field. Reset to be editable after focusing
    // has happened. Keyboard will not appear.

    if (!isKeyEvent && DomHelper.isTouchEvent) {
      if (field.editable) {
        field.editable = false;
        field.setTimeout(() => field.editable = true, 500);
      }
    }

    e.preventDefault();

    if (DomHelper.activeElement !== field.input) {
      field.focus();
    }
  }

  get field() {
    return this.parent;
  }

};
Field.SpinTrigger = class SpinTrigger extends Field.Trigger {
  static get $name() {
    return 'SpinTrigger';
  } // Factoryable type name

  static get type() {
    return 'spintrigger';
  }

  static get configurable() {
    return {
      repeat: {
        $config: ['nullify'],
        value: true
      }
    };
  }

  template() {
    return `<div class="b-icon b-align-${this.align || 'end'}">
                    <div data-reference="upButton" class="b-icon b-spin-up"></div>
                    <div data-reference="downButton" class="b-icon b-spin-down"></div>
                </div>`;
  }

  updateRepeat(repeat) {
    const me = this;

    if (repeat) {
      me.clickRepeater = new ClickRepeater(Object.assign({
        element: me.element
      }, repeat));
    } else if (me.clickrepeater) {
      me.clickRepeater.destroy();
      me.clickrepeater = null;
    }
  }

  onClick(e) {
    const me = this,
          {
      field
    } = me;

    if (field.disabled || field.readOnly) {
      return;
    }

    if (e.target === me.upButton) {
      field.doSpinUp(e.shiftKey);
    } else if (e.target === me.downButton) {
      field.doSpinDown(e.shiftKey);
    }
  }

}; // Register trigger widgets type with their Factory

Field.Trigger.initClass();
Field.SpinTrigger.initClass();
Widget.register(Field.Trigger, 'trigger');
Widget.register(Field.SpinTrigger, 'spintrigger');
Field._$name = 'Field';

/**
 * @module Core/widget/TextField
 */

/**
 * Textfield widget. Wraps native &lt;input type="text"&gt;
 *
 * This field can be used as an {@link Grid.column.Column#config-editor editor} for the {@link Grid.column.Column Column}.
 * It is used as the default editor for the {@link Grid.column.Column Column}, {@link Grid.column.TemplateColumn TemplateColumn},
 * {@link Grid.column.TreeColumn TreeColumn}, and for other columns if another editor is not specified explicitly,
 * or disabled by setting `false` value.
 *
 * @extends Core/widget/Field
 *
 * @example
 * let textField = new TextField({
 *   placeholder: 'Enter some text'
 * });
 *
 * @classType textfield
 * @externalexample widget/TextField.js
 */

class TextField extends Field {
  // Factoryable type name
  static get type() {
    return 'textfield';
  } // Factoryable type alias

  static get alias() {
    return 'text';
  }

  static get configurable() {
    return {
      /**
       * The tab index of the input field
       * @config {Number} tabIndex
       * @category Input element
       */

      /**
       * The min number of characters for the input field
       * @config {Number} minLength
       * @category Field
       */

      /**
       * The max number of characters for the input field
       * @config {Number} maxLength
       * @category Field
       */
      nullValue: ''
    };
  }

  static get $name() {
    return 'TextField';
  }

  construct(config) {
    if (config.inputType === 'hidden') {
      config.hidden = true;
    }

    super.construct(...arguments);
  }

  updatePlaceholder(value) {
    if (this.input) {
      this.input.placeholder = value !== null && value !== void 0 ? value : '';
    }
  }

} // Register this widget type with its Factory

TextField.initClass();
TextField._$name = 'TextField';

/**
 * @module Core/widget/PickerField
 */

/**
 * Base class used for {@link Core.widget.Combo Combo}, {@link Core.widget.DateField DateField}, and {@link Core.widget.TimeField TimeField}.
 * Displays a picker ({@link Core.widget.List List}, {@link Core.widget.DatePicker DatePicker}) anchored to the field.
 * Not intended to be used directly.
 *
 * This field's subclasses can be used as editors for the {@link Grid.column.Column Column}.
 *
 * When focused by means of *touch* tapping on the trigger element (eg, the down arrow on a Combo)
 * on a tablet, the keyboard will not be shown by default to allow for interaction with the dropdown.
 *
 * A second tap on the input area will then show the keyboard if required.
 *
 * @extends Core/widget/TextField
 * @abstract
 */

class PickerField extends TextField {
  //region Config
  static get $name() {
    return 'PickerField';
  } // Factoryable type name

  static get type() {
    return 'pickerfield';
  }

  static get configurable() {
    return {
      /**
       * User can edit text in text field (otherwise only pick from attached picker)
       * @config {Boolean}
       * @default
       */
      editable: true,

      /**
       * The name of the element property to which the picker should size and align itself.
       * @config {String}
       * @default element
       */
      pickerAlignElement: 'inputWrap',
      // Does not get set, but prevents PickerFields inheriting value:'' from Field.
      value: null,
      triggers: {
        expand: {
          cls: 'bars'
        }
      },

      /**
       * By default PickerFiled's picker is transient, and will {@link #function-hidePicker} when the user clicks or
       * taps outside or when focus moves outside picker.
       *
       * Configure as `false` to make picker non-transient.
       * @config {Boolean}
       * @default
       */
      autoClose: true,

      /**
       * Configure as `true` to have the picker expand upon focus enter.
       * @config {Boolean}
       */
      autoExpand: null,

      /**
       * Returns the instantiated picker widget as configured by {@link #config-picker}.
       * @property {Core.widget.Widget}
       * @name picker
       */

      /**
       * A config object which is merged into the generated picker configuration to allow specific use cases
       * to override behaviour. For example:
       *
       *     picker: {
       *         align: {
       *             anchor: true
       *         }
       *     }
       *
       * @config {Object}
       */
      picker: {
        value: {
          floating: true
        },
        $config: ['lazy', 'nullify']
      },
      inputType: 'text',
      // We need to realign the picker if we resize (eg a multiSelect Combo's ChipView wrapping)
      monitorResize: true,
      nullValue: null
    };
  } //endregion
  //region Init & destroy

  doDestroy() {
    var _this$globalTapListen;

    // Remove touch keyboard showing listener if we added it
    (_this$globalTapListen = this.globalTapListener) === null || _this$globalTapListen === void 0 ? void 0 : _this$globalTapListen.call(this);
    super.doDestroy();
  }

  updateEditable(editable) {
    const me = this,
          element = me.element;
    super.updateEditable(...arguments);

    if (editable === false) {
      var _me$globalTapListener;

      (_me$globalTapListener = me.globalTapListener) === null || _me$globalTapListener === void 0 ? void 0 : _me$globalTapListener.call(me);
      element.classList.add('b-not-editable');
      EventHelper.on({
        element: me.input,
        click: 'onTriggerClick',
        thisObj: me
      });
    } else {
      // In case the field was temporarily set to readOnly="true" to prevent
      // the intrusive keyboard (This happens when tapping the trigger
      // and when focused by the container in response to a touch tap),
      // allow a subsequent touch tap to show the keyboard.
      me.globalTapListener = GlobalEvents.on({
        globaltap: 'showKeyboard',
        thisObj: me
      });
    }
  } //endregion
  //region Picker

  /* TODO
  changePicker(picker, was) {
      if (was) {
          was.hide();
          was.destroy();
      }
       if (picker && !(picker instanceof Widget)) {
          picker = this.createPicker(picker);
      }
       return picker;
  }
   createPicker() {
      throw new Error('createPicker must be implemented in PickerField subclass implementations');
  }
  */

  changePicker(picker, oldPicker) {
    throw new Error('changePicker(oldPicker, newPicker) must be implemented in PickerField subclass implementations');
  }
  /**
   * Iterate over all widgets owned by this widget and any descendants.
   *
   * *Note*: Due to this method aborting when the function returns `false`, beware of using short form arrow
   * functions. If the expression executed evaluates to `false`, iteration will terminate.
   *
   * _Due to the {@link #config-picker} config being a lazy config and only being converted to be a
   * `List` instance just before it's shown, the picker will not be part of the iteration before
   * it has been shown once_.
   * @function eachWidget
   * @param {Function} fn A function to execute upon all descendant widgets.
   * Iteration terminates if this function returns `false`.
   * @param {Boolean} [deep=true] Pass as `false` to only consider immediate child widgets.
   * @returns {Boolean} Returns `true` if iteration was not aborted by a step returning `false`
   */

  get childItems() {
    if (this._picker) {
      return [this.picker];
    }
  } //endregion
  //region Events

  /**
   * Check if field value is valid
   * @internal
   */

  onEditComplete() {
    super.onEditComplete();
    this.autoClosePicker();
  }

  onElementResize(resizedElement) {
    const me = this; // If the field changes size while the picker is visible, the picker
    // must be kept in alignment. For example a multiSelect: true
    // ComboBox with a wrapped ChipView.

    if (me.pickerVisible) {
      // Push realignment out to the next AF, because this picker itself may move in
      // response to the element resize, and the picker must realign *after* that happens.
      // For example a multiSelect: true ComboBox with a wrapped ChipView inside
      // a Popup that is aligned *above* an element. When the ChipView gains or
      // loses height, the Popup must realign first, and then the List must align to the
      // new position of the ComboBox.
      me.picker.requestAnimationFrame(me.picker.realign, null, me.picker);
    }

    super.onElementResize(resizedElement);
  }
  /**
   * Allows using arrow keys to open/close list. Relays other keypresses to list if open.
   * @private
   */

  internalOnKeyEvent(event) {
    const me = this;
    let callSuper = true;

    if (event.type === 'keydown' && !me.disabled) {
      if (me.pickerVisible) {
        const {
          picker
        } = me;

        if (event.key === 'Escape') {
          event.stopImmediatePropagation();
          me.hidePicker(); // EC has multiple effects. First stage is hide the picker.
          // If we do this, then the superclass's ESC handling must
          // not be called.

          callSuper = false;
        } else if (picker.onInternalKeyDown) {
          // if picker is visible, give it a shot at the event
          picker.onInternalKeyDown(event);
        } else if (event.key === 'ArrowDown') {
          if (picker.focusable) {
            picker.focus();
          }
        }
      } else if (event.key === 'ArrowDown') {
        // navigator should not react to initial DOWN keypress that triggers the List to show
        event.stopImmediatePropagation(); // If we not prevent default handler page might scroll. Siesta cannot reproduce this behavior, have to
        // leave it untested.
        // https://github.com/bryntum/support/issues/885

        event.preventDefault();
        me.onTriggerClick(event);
      }
    }

    if (callSuper) {
      super.internalOnKeyEvent(event);
    }
  }

  onFocusIn(e) {
    super.onFocusIn(e);

    if (this.autoExpand) {
      // IE triggers an input event on focus.
      // If expand is configured for focus, minChars should be zero.
      this.minChars = 0;
      this.onTriggerClick(e);
    }
  }

  onFocusOut(e) {
    this.autoClosePicker();
    super.onFocusOut(e);
  }
  /**
   * User clicked trigger icon, toggle list.
   * @private
   */

  onTriggerClick(event) {
    if (!this.disabled) {
      // Pass focus flag as true if invoked by a key event
      this.togglePicker('key' in event);
    }
  }
  /**
   * User clicked on an editable input field. If it's a touch event
   * ensure that the keyboard is shown.
   * @private
   */

  showKeyboard({
    event
  }) {
    const input = this.input;

    if (DomHelper.isTouchEvent && DomHelper.activeElement === input && event.target === input) {
      GlobalEvents.suspendFocusEvents();
      input.blur();
      input.focus();
      GlobalEvents.resumeFocusEvents();
    }
  } //endregion
  //region Toggle picker

  /**
   * Toggle picker display
   */

  togglePicker(focusPicker) {
    if (this.pickerVisible) {
      this.hidePicker();
    } else {
      this.showPicker(focusPicker);
    }
  }
  /**
   * Show picker
   */

  showPicker(focusPicker) {
    const me = this,
          picker = me.picker;

    if (!me.pickerHideShowListenersAdded) {
      picker.on({
        show: 'onPickerShow',
        hide: 'onPickerHide',
        thisObj: me
      });
      me.pickerHideShowListenersAdded = true;
    }

    picker.autoClose = me.autoClose;
    picker.show(); // Not been vetoed

    if (picker.isVisible) {
      if (focusPicker) {
        me.focusPicker();
      }
    }
  }

  onPickerShow() {
    const me = this;
    me.pickerVisible = true;
    me.element.classList.add('b-open');
    me.trigger('togglePicker', {
      show: true
    });
    me.pickerTapOutRemover = GlobalEvents.on({
      globaltap: 'onPickerTapOut',
      thisObj: me
    });
    me.pickerKeyDownRemover = EventHelper.on({
      element: me.picker.element,
      keydown: 'onPickerKeyDown',
      thisObj: me
    });
  }

  onPickerHide() {
    const me = this;
    me.pickerVisible = false;
    me.element.classList.remove('b-open');
    me.trigger('togglePicker', {
      show: false
    });
    me.pickerTapOutRemover && me.pickerTapOutRemover();
    me.pickerKeyDownRemover && me.pickerKeyDownRemover();
  }

  onPickerTapOut({
    event
  }) {
    if (!this.owns(event.target)) {
      this.autoClosePicker();
    }
  }

  onPickerKeyDown(event) {
    if (event.key === 'Tab') {
      const activeEl = DomHelper.activeElement,
            forwardedEvent = new KeyboardEvent('keydown', event); // Offer our own element a shot at the TAB event.
      // Some widgets or plugins may actively navigate.

      this.input.dispatchEvent(forwardedEvent); // Somebody might preventDefault on the synthesized event. We must honour that.
      // For example if we are the field for a cell Editor, and it started an edit on the adjacent cell.

      if (forwardedEvent.defaultPrevented) {
        event.preventDefault();
      } // No listener intervened, point the TAB event at the input,
      // and user agent default navigation will proceed.

      if (DomHelper.activeElement === activeEl) {
        this.input.focus();
      } // Some listener *did* navigate, prevent user agent default.
      else {
          event.preventDefault();
        } // If listeners have not destroyed us, close our picker.

      if (!this.isDestroyed) {
        this.hidePicker();
      }
    }
  } //endregion
  //region Visibility

  autoClosePicker() {
    if (this.autoClose) {
      this.hidePicker();
    }
  }
  /**
   * Hide picker
   */

  hidePicker() {
    if (this.pickerVisible) {
      this.picker.hide();
    }
  }

  focusPicker() {}

  focus() {
    const input = this.input; // If we are focusing an editable PickerField from a touch event, temporarily
    // set it to readOnly to prevent the showing of the intrusive keyboard.
    // It's more likely that a user on a touch device will interact with the picker
    // rather than the input field.
    // A second touch tap on an already focused input will show the keyboard;
    // see the showKeyboard method.

    if (DomHelper.isTouchEvent && this.editable) {
      input.readOnly = true;
      this.setTimeout(() => input.readOnly = false, 500);
    }

    super.focus();
  } //endregion

}
PickerField._$name = 'PickerField';

/**
 * @module Core/helper/util/Navigator
 */

/**
 * A helper class which allows keyboard navigation within the {@link #config-target} element.
 * @private
 */

class Navigator extends Events(Base) {
  static get configurable() {
    return {
      /**
       * The owning Widget which is using this Navigator.
       * @config {Core.widget.Widget}
       */
      ownerCmp: null,

      /**
       * The encapsulating element in which navigation takes place.
       * @config {HTMLElement}
       */
      target: null,

      /**
       * The element which provides key events for navigation. Optional. Defaults to the {@link #config-target} element.
       * @config {HTMLElement}
       */
      keyEventTarget: null,

      /**
       * An optional key event processor which may preprocess the key event. Returning `null` prevents processing of the event.
       * @config {Function}
       */
      processEvent: null,

      /**
       * A query selector which identifies descendant elements within the {@link #config-target} which are navigable.
       * @config {String}
       */
      itemSelector: null,

      /**
       * The currently focused element within the {@link #config-target}.
       * @config {HTMLElement}
       */
      activeItem: null,

      /**
       * A CSS class name to add to focused elements.
       * @config {String}
       * @default
       */
      focusCls: 'b-active',

      /**
       * An object containing key definitions keys by the key name eg:
       *
       * ```javascript
       *  keys : {
       *      "CTRL+SPACE" : 'onCtrlSpace',
       *      ENTER        : 'onEnterKey'
       *  }```
       *
       * The {@link #config-ownerCmp} is used as the `this` reference and to resolve string method names.
       *
       * Modified key names must be created prepending one or more `'CTRL+'`, `'SHIFT+'`, `'ALT+'`
       * in that order, for example `"CTRL+SHIFT+ENTER" : 'showMenu'`
       * @config {Object}
       * @default
       */
      keys: null,

      /**
       * Configure as `true` to also navigate when the `CTRL` modifier key is used along with
       * navigation keys.
       * @config {Boolean}
       * @default false
       */
      allowCtrlKey: null,

      /**
       * Configure as `true` to also navigate when the `SHIFT` modifier key is used along with
       * navigation keys.
       * @config {Boolean}
       * @default false
       */
      allowShiftKey: null,
      scrollDuration: 50,

      /**
       * Configure as, or set to `true` to disable the processing of keys.
       * @config {Boolean}
       */
      disabled: null,
      testConfig: {
        scrollDuration: 1
      }
    };
  }

  updateOwnerCmp(ownerCmp) {
    if (!this.itemSelector) {
      this.itemSelector = `.${this.ownerCmp.itemCls}`;
    }
  }

  onKeyDown(keyEvent) {
    const me = this,
          {
      ownerCmp,
      itemSelector,
      activeItem,
      itemsTabbable
    } = me,
          {
      target
    } = keyEvent,
          firstItem = me.target.querySelector(itemSelector),
          // Not all key events have 'key'
    keyName = (keyEvent.key || '').trim() || keyEvent.code,
          composedKeyName = `${keyEvent.ctrlKey ? 'CTRL+' : ''}${keyEvent.shiftKey ? 'SHIFT+' : ''}${keyEvent.altKey ? 'ALT+' : ''}${keyName}`,
          validTarget = target.matches(itemSelector) || target === me.keyEventTarget; // Process the key gesture if there are items and we are visible.
    // Also, if key emanated from a valid target element (Not an owned positioned Widget).

    if (!me.disabled && firstItem && me.target.offsetParent && validTarget) {
      // Only set navigation key names if we are *not* using tabbing.
      if (!itemsTabbable) {
        // Detect whether the navigable items flow inline or downwards.
        if (me.inlineFlow == null) {
          const itemContainer = firstItem.parentNode,
                itemPositionStyle = DomHelper.getStyleValue(firstItem, 'position'),
                itemDisplayStyle = DomHelper.getStyleValue(firstItem, 'display'),
                itemFloatStyle = DomHelper.getStyleValue(firstItem, 'float'); // This is how we know that RIGHT and LEFT arrow should be used for next and previous.
          // If inlineFlow is false, we use UP and DOWN. Consider tabs in a tab bar.

          me.inlineFlow = itemPositionStyle === 'absolute' || itemDisplayStyle === 'inline' || itemDisplayStyle === 'inline-block' || itemFloatStyle === 'left' || itemFloatStyle === 'right' || DomHelper.getStyleValue(itemContainer, 'display') === 'flex' && DomHelper.getStyleValue(itemContainer, 'flex-direction') === 'row';
        }

        if (!me.prevKey) {
          if (me.inlineFlow) {
            me.prevKey = 'ArrowLeft';
            me.nextKey = 'ArrowRight';
          } else {
            me.prevKey = 'ArrowUp';
            me.nextKey = 'ArrowDown';
          }
        }
      } // Feed the key event through our configured processor, process the event that returns if any.

      if (me.processEvent) {
        keyEvent = me.processEvent.call(ownerCmp, keyEvent);

        if (!keyEvent) {
          return;
        }
      }

      if (activeItem) {
        switch (keyName) {
          case me.prevKey:
            if (me.disabled || keyEvent.ctrlKey && !me.allowCtrlKey) {
              return;
            }

            if (keyEvent.shiftKey && !me.allowShiftKey) {
              return;
            }

            keyEvent.preventDefault();
            me.navigatePrevious(keyEvent);
            break;

          case me.nextKey:
            if (me.disabled || keyEvent.ctrlKey && !me.allowCtrlKey) {
              return;
            }

            if (keyEvent.shiftKey && !me.allowShiftKey) {
              return;
            }

            keyEvent.preventDefault();
            me.navigateNext(keyEvent);
            break;

          default:
            {
              const keyHandler = me.keys && me.keys[composedKeyName];

              if (keyHandler && !me.disabled) {
                me.callback(keyHandler, me.thisObj || ownerCmp, [keyEvent]);
              } // Note that even if this.disabled, the ownerCmp will expect
              // to have its key down hander invoked.
              else if (ownerCmp.onInternalKeyDown) {
                  ownerCmp.onInternalKeyDown(keyEvent);
                } // One of the handlers moved focus.
              // This KeyDown should not act upon the new target.
              // For example popped up a Popup and focused its "close" Tool.

              if (DomHelper.activeElement !== target) {
                keyEvent.preventDefault();
              } // No navigation must take place when items are tabbable.

              if (itemsTabbable) {
                return;
              }
            }
        }
      } else {
        if (itemsTabbable) {
          return;
        }

        me.activeItem = me.previousActiveItem || me.getDefaultNavigationItem(keyEvent);
      }

      if (me.activeItem !== activeItem) {
        /**
         * Fired when a user gesture causes the active item to change _or become `null`_.
         * @event navigate
         * @param {Event} event The browser event which instigated navigation. May be a click or key or focus move event.
         * @param {HTMLElement} item The newly active item, or `null` if focus moved out.
         * @param {HTMLElement} oldItem The previously active item, or `null` if focus is moving in.
         */
        me.triggerNavigate(keyEvent);
      }
    }
  }

  getDefaultNavigationItem(keyEvent) {
    const {
      target,
      itemSelector,
      prevKey,
      nextKey
    } = this; // Navigating backwards from after the component, we default to last item

    if (target.compareDocumentPosition(keyEvent.target) & 4 && keyEvent.key === prevKey) {
      return target.querySelector(`${itemSelector}:last-of-type`);
    } // Navigating forwards from before the component we default to the first item

    if (target.compareDocumentPosition(keyEvent.target) & 2 && keyEvent.key === nextKey) {
      return target.querySelector(`${itemSelector}`);
    }
  }

  navigatePrevious(keyEvent) {
    const me = this,
          previous = me.previous;
    keyEvent.preventDefault();

    if (previous) {
      me.ownerCmp.scrollable.scrollIntoView(previous, {
        animate: me.scrollDuration
      }).then(() => {
        me.activeItem = previous;
        me.triggerNavigate(keyEvent);
      });
    }
  }

  navigateNext(keyEvent) {
    const me = this,
          next = me.next;
    keyEvent.preventDefault();

    if (next) {
      me.ownerCmp.scrollable.scrollIntoView(next, {
        animate: me.scrollDuration
      }).then(() => {
        me.activeItem = next;
        me.triggerNavigate(keyEvent);
      });
    }
  }

  get owner() {
    return this.ownerCmp;
  }

  get previous() {
    return this.getAdjacent(-1);
  }

  get next() {
    return this.getAdjacent(1);
  }
  /**
   * Returns the next or previous navigable element starting from the passed `from` element,
   * navigating in the passed direction.
   * @param {HTMLElement} [from] The start point. Defaults to the current {@link #config-activeItem}
   * @param {Number} [direction=1] The direction. -1 for backwards, else forwards.
   */

  getAdjacent(direction = 1, from = this.activeItem) {
    const treeWalker = this.treeWalker;
    treeWalker.currentNode = from;
    treeWalker[direction < 0 ? 'previousNode' : 'nextNode']();

    if (treeWalker.currentNode !== this.activeItem) {
      return treeWalker.currentNode;
    }
  }

  onTargetFocusIn(focusInEvent) {
    const me = this,
          {
      target
    } = focusInEvent,
          {
      itemsTabbable,
      skipScrollIntoView
    } = me; // Ignore navigating to a focus trap. It will bounce back in

    if (!target.matches('.b-focus-trap')) {
      if (target.matches(me.itemSelector)) {
        // We may need to know this in downstream code.
        // for example set activeItem must not scrollintoView on click
        me.skipScrollIntoView = true;
        me.activeItem = target;
        me.skipScrollIntoView = skipScrollIntoView; // If we are using natural, TAB based navigation, trigger the navigate event after it really happens.

        if (me.activeItem && itemsTabbable) {
          me.triggerNavigate(focusInEvent);
        }
      } // Upward focusing from within means a SHIFT+TAB, so go to previous sibling
      // PATCH: IE11 may receive event which related target is not an instance of HTMLElement
      else {
          const toWidget = bryntum.fromElement(focusInEvent.target); // If we are focusing into an owned widget (such as an Editor), ignore. It will revert focus
          // when it hides.

          if (!toWidget || !me.owner.owns(toWidget)) {
            var _focusInEvent$related, _focusInEvent$related2;

            if (!itemsTabbable && (_focusInEvent$related = focusInEvent.relatedTarget) !== null && _focusInEvent$related !== void 0 && (_focusInEvent$related2 = _focusInEvent$related.matches) !== null && _focusInEvent$related2 !== void 0 && _focusInEvent$related2.call(_focusInEvent$related, me.itemSelector)) {
              var _me$owner$previousSib;

              (_me$owner$previousSib = me.owner.previousSibling) === null || _me$owner$previousSib === void 0 ? void 0 : _me$owner$previousSib.focus();
            }
          }
        }
    }
  }

  onTargetFocusOut(focusOutEvent) {
    const me = this,
          {
      relatedTarget
    } = focusOutEvent; // Ignore navigating to a focus trap. It will bounce back in

    if (!(relatedTarget !== null && relatedTarget !== void 0 && relatedTarget.matches('.b-focus-trap'))) {
      if (!relatedTarget || !me.target.contains(relatedTarget) || !relatedTarget.matches(me.itemSelector)) {
        if (me.activeItem) {
          me.activeItem = null; // If we are using natural, TAB based navigation, trigger the navigate event after it really happens.

          if (me.itemsTabbable) {
            me.triggerNavigate(focusOutEvent);
          }
        }
      }
    }
  }

  onTargetMouseDown(mousedownEvent) {
    // Mousedown is the focus gesture.
    // This holds true even on touch platforms
    // where the mousedown event is synthesized -
    // preventing default prevents focus on the upcoming touchend.
    if (this.ownerCmp.itemsFocusable === false) {
      this.onFocusGesture(mousedownEvent);
    } else {
      const target = mousedownEvent.target.closest(this.itemSelector);

      if (target) {
        mousedownEvent.preventDefault();
        DomHelper.focusWithoutScrolling(target);
      }
    }
  }

  onTargetClick(clickEvent) {
    const me = this,
          {
      skipScrollIntoView
    } = me;

    if (me.disabled) {
      return;
    } // We may need to know this in downstream code.
    // for example set activeItem must not scrollintoView on click

    me.skipScrollIntoView = true; // ownerCmp's preprocessing of any navigate event.

    if (me.processEvent) {
      clickEvent = me.processEvent.call(me.ownerCmp, clickEvent);
    }

    if (clickEvent) {
      me.activeItem = clickEvent.target.closest(me.itemSelector);
      me.triggerNavigate(clickEvent);
    }

    me.skipScrollIntoView = skipScrollIntoView;
  } // We have to prevent focus moving from eg, an input field when we mousedown
  // or touchtap a non focusable item when ownerCmp has itemsFocusable: false.
  // If the event was not on an item, we must allow it through to allow Lists
  // to contain other widgets.

  onFocusGesture(event) {
    if (event.target === this.ownerCmp.contentElement || event.target.closest(this.itemSelector) && this.ownerCmp.itemsFocusable === false) {
      event.preventDefault();
    }
  }

  acceptNode(node) {
    return node.offsetParent && node.matches && node.matches(this.itemSelector) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP;
  }

  changeActiveItem(activeItem) {
    if (activeItem != null) {
      // List and Menu's getItem API allows number, or node or record or record id to be passed
      if (this.ownerCmp.getItem) {
        activeItem = this.ownerCmp.getItem(activeItem);
      }
    }

    return activeItem;
  }

  updateActiveItem(activeItem, oldActiveItem) {
    const me = this,
          {
      ownerCmp
    } = me,
          isActive = oldActiveItem && me.target.contains(oldActiveItem);

    if (isActive) {
      me.previousActiveItem = oldActiveItem;
    } // This may be set to null on focusout of the target element.
    // Cannot use truthiness test because index zero may be passed.

    if (activeItem != null) {
      // If the user was able to click the event, they will not expect it to attempt to scroll.
      if (!me.skipScrollIntoView) {
        ownerCmp.scrollable.scrollIntoView(activeItem, {
          block: 'nearest',
          // If we are being called in response to focus movement, it will already be the document.activeElement
          // so in that case, behave is if we were in non-focusing mode, and just add the focused class.
          focus: DomHelper.isFocusable(activeItem) && activeItem !== DomHelper.activeElement,
          silent: me.scrollSilently
        });
      } // No change in active item, do nothing after we've ensured it's fully in view.

      if (activeItem === oldActiveItem && isActive) {
        return;
      }

      if (oldActiveItem) {
        oldActiveItem.classList.remove(me.focusCls);
      }

      activeItem.classList.add(me.focusCls);
      me._activeItem = activeItem;
    } else {
      if (oldActiveItem) {
        oldActiveItem.classList.remove(me.focusCls); // If we are clearing the activeItem, and it's focused, keep focus
        // close by actively reverting it.

        if (oldActiveItem === DomHelper.activeElement) {
          ownerCmp.revertFocus();
        }
      }

      me._activeItem = null;
    }
  }

  updateTarget(target, oldTarget) {
    const me = this,
          listeners = {
      element: target,
      thisObj: me,
      focusin: 'onTargetFocusIn',
      focusout: 'onTargetFocusOut'
    };

    if (!me.itemsTabbable) {
      // We don't need pointer events if items are tabbable
      listeners.mousedown = 'onTargetMouseDown';
      listeners.click = 'onTargetClick';
    }

    if (!Object.prototype.hasOwnProperty.call(me, 'acceptNode')) {
      me.acceptNode = me.acceptNode.bind(me); // https://github.com/webcomponents/webcomponentsjs/issues/556
      // Work around Internet Explorer wanting a function instead of an object.
      // IE also *requires* this argument where other browsers don't.

      me.acceptNode.acceptNode = me.acceptNode;
    }

    EventHelper.on(listeners); // This Navigator object acts as the filter for the TreeWalker. We must implement acceptNode(node)

    me.treeWalker = me.setupTreeWalker(target, NodeFilter.SHOW_ELEMENT, me.acceptNode, false); // If we were not configured with an outside key event provider (think the input field providing UP/DOWN keys for the dropdown)
    // then use the target element as the source.

    if (!me.keyEventTarget) {
      me.keyEventTarget = target;
    } // If the activeItem gets removed, we must know, and deactivate.

    (me.targetMutationMonitor = new MutationObserver(me.onTargetChildListChange.bind(me))).observe(target, {
      childList: true,
      subtree: true
    });
  }

  setupTreeWalker(root, whatToShow, filter, entityReferenceExpansion) {
    // This Navigator object acts as the filter for the TreeWalker. We must implement acceptNode(node)
    return document.createTreeWalker(root, whatToShow, filter, entityReferenceExpansion);
  }

  onTargetChildListChange() {
    const activeItem = this.activeItem; // On DOM mutation, if the activeItem got changed, pull the one with the same ID out again.

    if (activeItem) {
      if (this.target.contains(activeItem)) {
        activeItem.classList.add(this.focusCls);
      } else {
        // Passing undefined results in the config setter assuming no-change
        // So we must use null to clear.
        this.activeItem = this.target.querySelector(`${this.itemSelector}[data-id="${activeItem.dataset.id}"]`) || null;
      }
    }
  }

  changeKeyEventTarget(keyEventTarget) {
    this._keyEventTarget = keyEventTarget;
    EventHelper.on({
      element: keyEventTarget,
      keydown: 'onKeyDown',
      thisObj: this
    });
  }

  triggerNavigate(event, item = this.activeItem) {
    var _this$ownerCmp;

    const navEvent = {
      event,
      item,
      oldItem: this.previousActiveItem
    };
    this.trigger('navigate', navEvent);
    (_this$ownerCmp = this.ownerCmp) === null || _this$ownerCmp === void 0 ? void 0 : _this$ownerCmp.trigger('navigate', navEvent);
  }

}
Navigator._$name = 'Navigator';

/**
 * @module Core/widget/List
 */

const itemRange = document.createRange();
/**
 * Displays a list of items which the user can navigate using the keyboard and select using either pointer gestures or the keyboard.
 * @extends Core/widget/Widget
 *
 * @classType list
 * @externalexample widget/List.js
 */

class List extends Widget {
  //region Config
  static get $name() {
    return 'List';
  } // Factoryable type name

  static get type() {
    return 'list';
  }

  static get configurable() {
    return {
      itemCls: 'b-list-item',
      selectedCls: 'b-selected',
      selectIconCls: 'b-icon-check',

      /**
       * An array of Objects which are converted into records and used to create this
       * List's {@link #config-store}
       * @config {Object[]}
       */
      items: null,

      /**
       * A {@link Core.data.Store} which provides the records which map to List items. Each record is passed through the
       * {@link #config-itemTpl} to produce the DOM structure of the List. May be generated from an array of {@link #config-items}.
       * @config {Object|Core.data.Store}
       */
      store: null,
      navigator: true,
      scrollable: {
        x: false,
        y: true
      },
      itemsFocusable: true,

      /**
       * Configure as `true` to allow multi select and add checkboxes to the items
       * @config {Boolean}
       * @default
       */
      multiSelect: false,
      // If Cmd/Ctrl is pressed when clicking, toggle selected state for all items
      toggleAllIfCtrlPressed: false,

      /**
       * Template function which, when passed a record, returns the textual HTML for that item. Defaults to a
       * function returning the value of records `text` field
       * @config {Function} itemTpl
       */
      itemTpl: record => record.text,

      /**
       * A {@link Core.util.Collection Collection}, or Collection config object
       * to use to contain this List's selected records.
       * @config {Core.util.Collection|Object}
       */
      selected: {},

      /**
       * Configure as `true` to activate items on mouseover. This is used by the Combo
       * field when using a List as its dropdown.
       * @config {Boolean}
       */
      activateOnMouseover: null
    };
  } //endregion
  //region Events

  /**
   * User activated an item in the list either by pointer or keyboard.
   * The active record, list item index, and the triggering event are passed.
   * @event item
   * @property {Object} record - Activated record
   * @property {Number} index - List item index
   * @property {Event} event - Triggering event
   */
  //endregion

  construct(config, ...args) {
    const me = this; // We can be created from a raw array. It becomes our items which we translate to a Store.

    if (Array.isArray(config)) {
      config = {
        items: config
      };
    }

    super.construct(config, ...args);
    const {
      element
    } = me,
          {
      classList
    } = element;

    if (me.multiSelect) {
      classList.add('b-multiselect');
    }

    if (me.store && me.store.count) {
      me.refresh();
    } else {
      classList.add('b-empty');
    }

    EventHelper.on({
      element,
      delegate: me.itemSelector,
      mouseover: 'onMouseOver',
      click: 'onClick',
      thisObj: me
    });
  }

  doDestroy() {
    var _this$navigator;

    this.detachListeners('store');
    (_this$navigator = this.navigator) === null || _this$navigator === void 0 ? void 0 : _this$navigator.destroy();
    super.doDestroy();
  }

  onFocusIn(e) {
    const listItem = e._target.classList.contains(this.itemCls) ? e._target.dataset.id : 0;
    this.restoreActiveItem(this.navigator.previousActiveItem || this.getItem(listItem));
    super.onFocusIn(e);
  }

  contentTpl() {
    return TemplateHelper.tpl`${this.store.records.map((record, i) => this.itemWrapperTpl(record, i))}`;
  }

  itemWrapperTpl(record, i) {
    return TemplateHelper.tpl`<div class="${this.getItemClasses(record, i)}" data-index="${i}" data-id="${record.id}" ${this.itemsFocusable ? 'tabindex="-1"' : ''}>
            ${this.itemContentTpl(record, i)}
            </div>`;
  }

  itemContentTpl(record, i) {
    return `${this.itemIconTpl(record, i)}${this.itemTpl(record, i)}`;
  }

  itemIconTpl(record, i) {
    return this.multiSelect ? `<div class="b-selected-icon b-icon ${this.selectIconCls}"></div>` : '';
  }

  getItemClasses(record) {
    var _me$_navigator;

    const me = this,
          activeItem = (_me$_navigator = me._navigator) === null || _me$_navigator === void 0 ? void 0 : _me$_navigator.activeItem,
          isActive = (activeItem === null || activeItem === void 0 ? void 0 : activeItem.dataset.id) == record.id,
          isSelected = me.selected.includes(record);
    return `${me.itemCls} ${record.cls || ''} ${isSelected ? me.selectedCls : ''} ${isActive ? me.navigator.focusCls : ''}`;
  }

  onBeforeStoreLoad() {
    this.mask(this.L('L{loading}'));
  }

  onAfterStoreRequest() {
    this.unmask();
  }

  onStoreChange({
    source: store,
    action,
    records,
    record
  }) {
    switch (action) {
      case 'remove':
        this.selected.remove(records);
        break;

      case 'clear':
        this.selected.clear();
        break;

      case 'update':
        this.refreshItem(record);
        return;
    }

    this.refresh();
  }

  onStoreRefresh() {
    this.refresh();
  }

  refresh() {
    const me = this;

    if (me.isVisible) {
      const {
        activeItem
      } = me.navigator; // Ensure that the next call to refresh when not visible queues a refresh

      me.paintListener = null; // IMPORTANT! DO NOT INLINE!
      // This is a fix for issue: https://github.com/bryntum/support/issues/2171
      // Long story short: DomHelper returns document-fragment and first time we insert node, nothing is actually
      // inserted if (and only) fragment is not stored as a variable.

      const node = DomHelper.createElementFromTemplate(me.contentTpl(), {
        fragment: true
      });
      me.clearItems().insertNode(node); // The item elements will be all new, so restore the active item.
      // Navigator will reacquire it by its id.

      if (activeItem) {
        me.restoreActiveItem(activeItem);
      }

      me.refreshCount = (me.refreshCount || 0) + 1;
      me.element.classList[me.store.count > 0 ? 'remove' : 'add']('b-empty');
    } // Only queue a refresh if not already queued
    else if (!me.paintListener) {
        me.paintListener = me.on({
          paint: 'refresh',
          thisObj: me,
          once: true
        });
      }
  }

  clearItems() {
    const me = this,
          firstItem = me.contentElement.querySelector(me.itemSelector),
          lastChild = me.contentElement.lastChild;

    if (firstItem) {
      // Can't use :last-of-type when other elements of same type may be present.
      // Have to pluck the last element of the NodeList.
      const items = me.contentElement.querySelectorAll(`${me.itemSelector}:not(.b-mask)`);
      itemRange.setStartBefore(firstItem);
      itemRange.setEndAfter(items[items.length - 1]);
      itemRange.deleteContents();
    } else {
      // Allow a static set of elements to be at the top of the list
      if (lastChild) {
        itemRange.setStartAfter(lastChild);
        itemRange.setEndAfter(lastChild);
      } else {
        itemRange.setStart(me.contentElement, 0);
        itemRange.setEnd(me.contentElement, 0);
      }
    }

    return itemRange;
  }

  refreshItem(...records) {
    for (const record of records) {
      const item = this.getItem(record); // Maybe a record which is filtered out announces a change.
      // There will be no item.

      if (item) {
        const index = this.store.indexOf(record),
              newItem = DomHelper.createElementFromTemplate(this.itemWrapperTpl(record, index));
        DomHelper.sync(newItem, item);
      }
    }
  }

  getItem(recordOrId) {
    // Cannot use truthiness test because index zero may be passed.
    if (recordOrId != null) {
      if (typeof recordOrId === 'number') {
        return this.contentElement.querySelector(`[data-index="${recordOrId}"]`);
      } else if (recordOrId.nodeType === 1) {
        recordOrId = recordOrId.dataset.id;
      }

      if (recordOrId.id != null) {
        recordOrId = recordOrId.id;
      }

      return this.contentElement.querySelector(`[data-id="${recordOrId}"]`);
    }
  }

  getRecord(dom) {
    if (dom.target) {
      dom = dom.target;
    }

    dom = dom.closest(this.itemSelector);
    return this.store.getAt(parseInt(dom.dataset.index));
  } //region getters/setters

  /**
   * May be *set* as an array of Objects which are converted into records and used to create this
   * List's {@link #config-store}
   * @member {Object[]} items
   */

  updateItems(items) {
    const me = this;

    if (me.store && me.store.autoCreated) {
      me.store.destroy();
    }

    me.store = Store.getStore(items);
  }

  updateSelected(selected) {
    if (!(selected && selected instanceof Collection)) {
      selected = new Collection(selected);
    }

    this._selected = selected;
    selected.on({
      change: 'onSelectionChange',
      thisObj: this
    });
  }

  get itemSelector() {
    return `.${this.itemCls}`;
  }

  get selected() {
    return this._selected;
  }
  /**
   * Get the backing store, a {@link Core.data.Store} holding the records used to generate list items
   * @property {Core.data.Store}
   * @readonly
   */

  get store() {
    // Ensure any configured items is processed into a store before we try to return it.
    this._thisIsAUsedExpression(this.items);

    return this._store;
  }

  changeStore(store) {
    const me = this;

    if (!(store instanceof Store)) {
      store = new Store(store);
    }

    me.detachListeners('store');

    if (store) {
      const storeListeners = {
        name: 'store',
        change: 'onStoreChange',
        refresh: 'onStoreRefresh',
        thisObj: me
      };

      if (store.readUrl) {
        storeListeners.beforeLoad = 'onBeforeStoreLoad';
        storeListeners.afterRequest = 'onAfterStoreRequest';
      }

      store.on(storeListeners);
    }

    return store;
  }

  updateStore() {
    this.refresh();
  }

  changeNavigator(navigator) {
    const me = this,
          {
      element
    } = me;

    if (element) {
      navigator = new (navigator.class || Navigator)(Object.assign({
        ownerCmp: me,
        target: element,
        keyEventTarget: element
      }, navigator)); // This widget is responsible for relaying keyboard events into the navigator
      // So it must be able to receive focus.

      if (navigator.keyEventTarget === element) {
        element.tabIndex = 0;
      }
    }

    return navigator;
  }

  get minAlignHeight() {
    const lastItem = this.element.lastElementChild,
          minHeight = this.minHeight; // No minHeight specified, always defer to the items height

    if (minHeight != null) {
      return this.store.count ? Math.min(lastItem.offsetTop + lastItem.offsetHeight, minHeight) : 0;
    }
  } //endregion
  //region Hide/Show

  alignTo(...args) {
    // When aligning, if the items total height is less than minHeight, use that.
    super.minHeight = this.minAlignHeight;
    super.alignTo(...args);
  }

  hide(...args) {
    this.navigator.activeItem = null;
    super.hide(...args);
  }

  show() {
    const {
      refreshCount
    } = this,
          {
      previousActiveItem
    } = this.navigator; // Restore the configured minHeight

    super.minHeight = this._minHeight;
    super.show(); // If no refresh on paint took place, we need to restore the activeItem.
    // Refresh does that itself.

    if (this.refreshCount === refreshCount) {
      this.restoreActiveItem(previousActiveItem, true);
    }
  }
  /**
   * Sets the passed record as the current focused record for keyboard navigation and selection purposes.
   * @param {Core.data.Model} activeItem The item to highlight as the active ("focused") item.
   * @param {String|boolean} [block='nearest'] The block scroll specification to scroll
   * the item into view. Defaults to `'nearest'` which means a minimal scroll, only if
   * the item is actually out of view. Passing `true` is for when the List is aligned
   * to a picker and the selected item should be scrolled to be close to the picker,
   * however that is aligned. See `Combo.showPicker` for usage.
   * @internal
   */

  restoreActiveItem(activeItem = this.navigator.previousActiveItem, block = 'nearest') {
    const me = this,
          {
      navigator,
      lastAlignSpec
    } = me; // Zero may be passed to select first item, so cannot use thruthiness.
    // This won't mean *clear* the active item if any. Falsy means
    // nothing to restore so leave it as is.

    if (activeItem != null) {
      if (typeof block !== 'string') {
        const alignZone = lastAlignSpec ? lastAlignSpec.zone : 1; // Scroll item into view appropriately, meaning closest to any align target.
        // Align zones are T,R,B,L => 0,1,2,3
        // If 1 or 3 (right or left), align nearest.
        // if 0 (above) align end.
        // else, we're below so align start.

        block = alignZone & 1 ? 'nearest' : alignZone ? 'start' : 'end';
      }

      activeItem = me.getItem(activeItem);

      if (activeItem) {
        // If we are aligned to a target, first, scroll the required item to the optimal
        // position to avoid Navigator's default minimal scroll to block: 'nearest'.
        // If we are aligned below a combo, move the point of interest to the top.
        // If we are aligned above a combo, move the point of interest to the bottom.
        me.scrollable.scrollIntoView(activeItem, {
          block
        });
        navigator.activeItem = activeItem;
      }
    }
  } //endregion
  //region Events

  /**
   * Focuses list items on hover.
   * @private
   */

  onMouseOver(event) {
    const me = this,
          itemElement = event.currentTarget; // Activate soon in case they're moving fast over items.

    if (!DomHelper.isTouchEvent && itemElement && me.navigator && me.activateOnMouseover) {
      me.setTimeout({
        fn: 'handleMouseOver',
        delay: 30,
        args: [itemElement],
        cancelOutstanding: true
      });
    }
  }

  handleMouseOver(itemElement) {
    this.navigator.activeItem = itemElement;
  }
  /**
   * Selects list items on click.
   * @private
   */

  onClick(event) {
    const itemElement = event.target.closest(this.itemSelector);

    if (itemElement) {
      this.onItemClick(itemElement, event);
    }
  }
  /**
   * Key events which are not navigation are delegated up to here by the Navigator
   * @private
   */

  onInternalKeyDown(event) {
    const me = this,
          active = me.navigator.activeItem;

    switch (event.key) {
      case ' ':
        // If it's an input field that's providing events, allow it to process space.
        // Otherwise, it falls through and is processed the same as ENTER to make a click.
        if (event.target.nodeName.toUpperCase() === 'INPUT' && !event.target.readOnly) {
          break; // eslint-disable-line
        }

      case 'Enter':
        // eslint-disable-line
        if (active) {
          this.onItemClick(active, event); // Stop the keydown from bubbling.
          // And stop it from creating a keypress event.
          // No further action should be taken after item selection.

          event.stopImmediatePropagation();
          event.preventDefault();
        }

    }
  } //endregion

  onItemClick(item, event) {
    const me = this,
          index = parseInt(item.dataset.index);
    let record = me.store.getAt(index);
    me._isUserAction = true;
    me.trigger('item', {
      item,
      record,
      index,
      event,
      userAction: true
    }); // Clicking on any element with the data-noselect attribute means no selection

    if (!item.contains(event.target.closest('[data-noselect]'))) {
      const selected = me.selected,
            isSelected = selected.includes(record);

      if (me.multiSelect) {
        if (me.toggleAllIfCtrlPressed && event.ctrlKey) {
          record = me.store.getRange();
        }

        selected[isSelected ? 'remove' : 'add'](record);
      } else {
        selected.splice(0, selected.count, record);
      }
    }

    me.lastClicked = record;
    me._isUserAction = false;
  }
  /**
   * Handles items being added or removed from the selected Collection
   * @param {Object} changeEvent
   * @private
   */

  onSelectionChange({
    action,
    removed,
    added,
    replaced
  }) {
    const me = this,
          {
      selectedCls,
      unselectedCls
    } = me;
    let record, item;

    if (action === 'clear') {
      for (item of me.element.querySelectorAll(`.${selectedCls}`)) {
        item.classList.remove(selectedCls);
        unselectedCls && item.classList.remove(unselectedCls);
      }
    } else {
      for (record of removed) {
        item = me.getItem(record);
        item && item.classList.remove(selectedCls);
      }

      for (record of added) {
        item = me.getItem(record);
        item && item.classList.add(selectedCls);
      }
    }
  }

} // Register this widget type with its Factory

List.initClass();
List.prototype.navigatorClass = Navigator;
List._$name = 'List';

/**
 * @module Core/widget/ChipView
 */

/**
 * Displays an inline series of Chips which may be navigated to, selected and deleted.
 * @extends Core/widget/List
 *
 * @classType chipview
 */

class ChipView extends List {
  //region Config
  static get $name() {
    return 'ChipView';
  } // Factoryable type name

  static get type() {
    return 'chipview';
  }

  static get configurable() {
    return {
      itemCls: 'b-chip',

      /**
       * Configure as `true` to display a clickable close icon after the {@link Core.widget.List#config-itemTpl}.
       * When tapped, the configured {@link #config-closeHandler} is called passing the
       * associated record.
       *
       * Chips may also be selected using the `LEFT` and `RIGHT` arrows (And the `Shift` key to
       * do multiple, contiguous election). Pressing the `DELETE` or `BACKSPACE` key passes the
       * selected records to the {@link #config-closeHandler}
       * @config {Boolean}
       * @default
       */
      closable: true,

      /**
       * A template function, which, when passed a record, returns the markup which
       * encapsulates a chip's icon to be placed before the {@link Core.widget.List#config-itemTpl}.
       * @config {Function}
       */
      iconTpl: null,

      /**
       * If {@link #config-closable} is `true`, this is the name of a callback function
       * to handle what the "close" action means.
       * @config {String|Function}
       */
      closeHandler: null
    };
  }

  itemContentTpl(record, i) {
    const me = this;
    return TemplateHelper.tpl`${me.iconTpl ? this.iconTpl(record) : ''}
            ${me.itemTpl(record, i)}
            ${me.closable ? '<div class="b-icon b-close-icon b-icon-clear" data-noselect></div>' : ''}`;
  }

  onInternalKeyDown(event) {
    if (this.closable && this.selected.count && (event.key === 'Delete' || event.key === 'Backspace')) {
      this.callback(this.closeHandler, this.owner, [this.selected.values, {
        isKeyEvent: true
      }]);
    } else {
      super.onInternalKeyDown(event);
    }
  }

  updateClosable(closable) {
    this.element.classList[closable ? 'add' : 'remove']('b-chips-closable');
    this.navigator.disabled = !closable;

    if (!this.isConfiguring) {
      this.refresh();
    }
  }

  onClick(event) {
    const me = this,
          item = event.target.closest(`.${me.itemCls}`);

    if (me.closable && event.target.classList.contains('b-close-icon')) {
      const record = me.store.getAt(parseInt(item.dataset.index));
      me.callback(me.closeHandler, me.owner, [[record]]);
    } else {
      super.onClick(event);
    }
  }

} // Register this widget type with its Factory

ChipView.initClass();
ChipView._$name = 'ChipView';

const errorFieldRequired = 'L{Field.fieldRequired}',
      errorValidateFilter = 'L{Field.validateFilter}';
/**
 * @module Core/widget/Combo
 */

/**
 * Combo (dropdown) widget. Consists of a text field with a trigger icon, which displays a List. Can be
 * populated from a Store.
 *
 * This field can be used as an {@link Grid.column.Column#config-editor editor} for the {@link Grid.column.Column Column}.
 *
 * @extends Core/widget/PickerField
 *
 * @example
 * // items as strings
 * let combo = new Combo({
 *   items: ['Small', 'Smaller', 'Really small', 'Tiny', 'Super tiny', '?'],
 *   placeholder: 'Pick size of diamond for ring'
 * });
 *
 * @example
 * // items as configs
 * let combo = new Combo({
 *   items: [{ value: 'a', text: 'First' }, { value: 'z', text: 'Last' }]
 * });
 *
 * @example
 * // items from store
 * let combo = new Combo({
 *   store: memberStore,
 *   valueField: 'id',
 *   displayField: 'name'
 * });
 *
 * @classType combo
 * @externalexample widget/Combo.js
 */

class Combo extends PickerField {
  // Do not remove. Assertion strings for Localization sanity check.
  // 'L{Field.fieldRequired}'
  // 'L{Field.validateFilter}'
  //region Config
  static get $name() {
    return 'Combo';
  } // Factoryable type name

  static get type() {
    return 'combo';
  } // Factoryable type aliases

  static get alias() {
    return 'combobox,dropdown';
  }

  static get configurable() {
    return {
      /**
       * Optionally a {@link Core.util.CollectionFilter Filter} config object which
       * the combo should use for filtering using the typed value.
       * This may use a `filterBy` property to test its `value` against any field
       * in the passed record.
       * ```javascript
       * {
       *     type          : 'combo',
       *     store         : myStore,
       *     primaryFilter : {
       *         filterBy(record) {
       *             if (this.value == null) {
       *                 return true;
       *             }
       *             const value = this.value.toLowerCase();
       *
       *             // Match typed value with forename or surname
       *             return record.forename.toLowerCase().startsWith(value) || record.surname.toLowerCase().startsWith(value);
       *         }
       *     }
       * }
       * ```
       * @config {Object}
       */
      primaryFilter: {},
      picker: {
        type: 'list',
        floating: true,
        scrollAction: 'realign',
        itemsFocusable: false,
        activateOnMouseover: true,
        align: {
          align: 't0-b0',
          axisLock: true
        },
        maxHeight: 324,
        scrollable: {
          overflowY: true
        },
        autoShow: false,
        focusOnHover: false
      },

      /**
       * Configure as `true` to allow selection of multiple values from the dropdown list.
       *
       * Each value is displayed as a "Chip" to the left of the input area. Chips may be
       * selected using the `LEFT` and `RIGHT` arrow keys and deleted using the `DELETE` key
       * to remove values from the field. There is also a clickable close icon in each chip.
       *
       * @config {Boolean}
       * @default false
       */
      multiSelect: null,

      /**
       * Rows to display in the dropdown (list items).
       *
       * If an object, the property names provide the {@link #config-value} for the Combo, and
       * the property values provide the displayed test in the list and input area eg:
       *
       *     items : {
       *         small  : 'Small',
       *         medium : 'Medium',
       *         large  : 'Large'
       *     }
       *
       * If an array, each entry may be
       *  - an object containing properties which must include
       * the {@link #config-valueField} and {@link #config-displayField} which populates the dropdown
       * with text and provides the corresponding field value.
       *  - An array whos first value provides the {@link #config-value} for the Combo and whos
       * second value provides the displayed test in the list and input area.
       *  - An array of values where the {@link #config-valueField} and {@link #config-displayField} are the same.
       *
       * eg:
       *
       *     items : [
       *         {value : 'small',  text : 'Small'},
       *         {value : 'medium', text : 'Medium'},
       *         {value : 'large',  text : 'Large'},
       *     ]
       *
       * or
       *
       *     items : [
       *         ['small',  'Small'],
       *         ['medium', 'Medium'],
       *         ['large',  'Large'],
       *     ]
       *
       * or
       *
       *     items : [ 'Small', 'Medium', 'Large' ]
       *
       * @config {Object[]|String[]|Object}
       */
      items: null,

      /**
       * Get/set store to display items from. Also accepts a Store config object.
       * @property {Core.data.Store|Object}
       * @name store
       */

      /**
       * A store used to populate items.
       * @config {Core.data.Store}
       */
      store: null,

      /**
       * Field used for item value when populating from store. Setting this to `null` will
       * yield the selected record as the Combo's {@link #property-value}.
       * @config {String}
       */
      valueField: undefined,

      /**
       * Field used for item text when populating from store
       * @config {String}
       */
      displayField: 'text',

      /**
       * Width of picker, defaults to this combo's {@link #config-pickerAlignElement} width
       * @config {Number}
       */
      pickerWidth: null,

      /**
       * The minimum string length to trigger the filtering, only relevant when {@link #config-editable} is `true`.
       *
       * This defaults to `1` in the case of local filtering, but `4` if the
       * {@link #config-filterParamName} is set to cause remote dropdown loading.
       *
       * @config {Number}
       * @default
       */
      minChars: null,
      selected: null,

      /**
       * Template for rendering list items contents
       * @config {Function}
       */
      listItemTpl: null,

      /**
       * Template function that can be used to customize the displayed value
       * @param {Core.data.Model} record The record to provide a textual value for.
       * @param {Core.widget.Combo} combo A reference to this Combo.
       * @config {Function}
       */
      displayValueRenderer: null,

      /**
       * CSS class to add to picker
       * @config {String}
       */
      listCls: null,
      triggers: {
        expand: {
          cls: 'b-icon-picker',
          handler: 'onTriggerClick'
        }
      },

      /**
       * If the dropdown is to be populated with a filtered query to a remote server, specify the
       * name of the parameter to pass the typed string here. By default, the string is simply sent
       * as the value of the parameter. For special encoding, configure the combo with {@link #config-encodeFilterParams}
       * @config {String}
       */
      filterParamName: null,

      /**
       * A function which creates an array of values for the {#config-filterParamName} to pass
       * any filters to the server upon load.
       *
       * The default behaviour is just to set the parameter value to the filter's `value`,
       * but the filter can be fully encoded for example:
       *
       * ```javascript
       *    {
       *        encodeFilterParams(filters) {
       *            const result = [];
       *
       *            for (const { property, operator, value, caseSensitive } of filters) {
       *                result.push(JSON.stringify({
       *                    field : property,
       *                    operator,
       *                    value,
       *                    caseSensitive
       *                }));
       *           }
       *        return result;
       *    }
       * ```
       * @config {Function}
       */
      encodeFilterParams: filters => filters.map(f => f.value),

      /**
       * If `false`, filtering will be triggered once you exceed {@link #config-minChars}. To filter only when
       * hitting Enter key, set this to `true`;
       * @config {Boolean}
       */
      filterOnEnter: false,

      /**
       * Configure as `true` to hide the expand trigger. This is automatically set to `true` if
       * remote filtering is enabled by setting the {@link #config-filterParamName} config.
       * @config {Boolean}
       * @default false
       */
      hideTrigger: null,

      /**
       * This implies that the picker will display an anchor pointer, but also means that the picker will align closer
       * to the input field so that the pointer pierces the {@link #config-pickerAlignElement}
       * @config {Boolean}
       * @default false
       */
      overlayAnchor: null,

      /**
       * The delay in milliseconds to wait after the last keystroke before filtering the list.
       * @config {Number}
       * @default
       */
      keyStrokeFilterDelay: null,
      defaultAction: 'select',

      /**
       * How to query the store upon click of the expand trigger. Specify one of these values:
       *
       *  - `'all'` - Clear the filter and display the whole dataset in the dropdown.
       *  - `'last'` - Filter the dataset using the last filter value.
       *  - `null`/any other - Use the value in the input field to filter the dataset.
       *
       * @config {String}
       * @default
       */
      triggerAction: 'all',

      /**
       * The name of an operator type as implemented in {@link Core.util.CollectionFilter#config-operator}
       * to use when filtering the dropdown list based upon the typed value.
       *
       * This defaults to `'startsWith'`, but the `'*'` operator may be used to match all
       * values which _contain_ the typed value.
       *
       * Not used when {@link #config-filterParamName} is set to cause remote dropdown loading.
       * The exact filtering operation is up to the server.
       *
       * @config {String}
       */
      filterOperator: 'startsWith',

      /**
       * Configure as `true` to force case matching when filtering the dropdown list based upon the typed value.
       *
       * @config {Boolean}
       * @default false
       */
      caseSensitive: false,

      /**
       * By default, the picker is hidden on selection in single select mode, and
       * remains to allow more selections when {@link #config-multiSelect} is `true`.
       * Setting this to a `Boolean` value can override that default.
       * @config {Boolean}
       */
      hidePickerOnSelect: null,

      /**
       * A config object to configure the {@link Core.widget.ChipView} to display the
       * selected value set when {@link #config-multiSelect} is `true.
       *
       * For example the {@link Core.widget.List#config-itemTpl} or
       * {@link Core.widget.ChipView#config-iconTpl} might be configured to display
       * richer chips for selected items.
       * @config {Object}
       */
      chipView: {
        $config: ['lazy', 'nullify'],
        value: {
          type: 'combochipview'
        }
      },
      chipStore: {
        $config: ['lazy', 'nullify'],
        value: {}
      },

      /**
       * When {@link #config-multiSelect} is `true`, you may configure `filterSelected` as
       * `true` to hide items in the dropdown when they are added to the selection.
       * It will appear as if the requested item has "moved" into the field's
       * {@link #config-chipView ChipView}.
       *
       * @config {Boolean}
       * @default false
       */
      filterSelected: null,

      /**
       * Text to display in the drop down when there are no items in the underlying store
       * @config {String}
       * @default
       */
      emptyText: null,

      /**
       * Get/sets combo value, selects corresponding item in the list
       * Setting null clears the field.
       *
       * If {@link #config-multiSelect} is `true`, then multiple values may be passed as an array.
       * If the values are records, these become the selected record set held by {@link #property-valueCollection},
       * and the `value` yielded by this field is an array of all the {@link #config-valueField}s from the records.
       * @fires select
       * @fires action
       * @property {Object}
       * @name value
       */

      /**
       * The initial value of this Combo box. In single select mode (default) it's a simple string value, for
       * {@link #config-multiSelect} mode, it should be an array of record ids.
       * @config {String|Number[]|String[]}
       * @default
       */
      value: null,

      /**
       * `true` to cause the field to be in an invalid state while the typed filter string does not match a record in the store.
       * @config {Boolean}
       * @default
       */
      validateFilter: true,

      /**
       * `true` to clear value typed to a multiselect combo when picker is collapsed
       * @config {Boolean}
       * @default
       */
      clearTextOnPickerHide: true
    };
  }
  /**
   * A constant value for the {@link #config-triggerAction} config to indicate that clicking the trigger should
   * filter the dataset using the last filter query string, *not* the input field value.
   * @member {String} queryLast
   * @readonly
   * @static
   */

  static get queryLast() {
    return 'last';
  } //endregion

  construct(config) {
    super.construct(...arguments); // Ensure there will always be a store created if Combo is created without items/store

    if (!this.store) {
      this.items = [];
    }

    if (this.filterOnEnter) {
      this.hideTrigger = true;
    }
  }

  startConfigure(config) {
    this.usingRecordAsValue = config.valueField === null;
    super.startConfigure(...arguments);
  }

  afterConfigure() {
    super.afterConfigure();

    if (!('_value' in this)) {
      this._value = this.valueField === this.displayField ? '' : null;
    }
  }

  get childItems() {
    const {
      _chipView,
      _picker
    } = this,
          result = [];

    if (_chipView) {
      result.push(_chipView);
    }

    if (_picker) {
      result.push(_picker);
    }

    return result;
  }

  changeChipStore(chipStore) {
    if (chipStore && !(chipStore instanceof Store)) {
      chipStore = new Store(Store.mergeConfigs({
        storage: this.valueCollection
      }, chipStore));
    }

    return chipStore;
  }

  updateChipStore(store, was) {
    was === null || was === void 0 ? void 0 : was.destroy();
  }

  changeChipView(chipView, oldChipView) {
    const me = this;
    me.element.classList[chipView ? 'add' : 'remove']('b-uses-chipview');

    if (chipView) {
      const {
        input
      } = me;

      if (!me.chipStore) {
        me.chipStore = {};
      }

      return ComboChipView.reconfigure(oldChipView, chipView, {
        defaults: {
          parent: me,
          insertBefore: input,
          store: me.chipStore,
          closable: !me.readOnly,
          navigator: {
            class: ComboChipNavigator,
            keyEventTarget: input
          }
        }
      });
    }

    oldChipView === null || oldChipView === void 0 ? void 0 : oldChipView.destroy();
  }

  updateChipView(chipView) {
    var _me$_chipViewEventDet;

    const me = this;
    me._chipViewEventDetacher = (_me$_chipViewEventDet = me._chipViewEventDetacher) === null || _me$_chipViewEventDet === void 0 ? void 0 : _me$_chipViewEventDet.call(me);
    me.chipStore = chipView === null || chipView === void 0 ? void 0 : chipView.store;

    if (chipView) {
      // Insert the input field
      chipView.element.appendChild(me.input); // Focus must flow into our field from the ChipView

      me._chipViewEventDetacher = EventHelper.on({
        element: chipView.element,
        mousedown: 'onChipViewMousedown',
        thisObj: me
      });
    }
  }

  updateMultiSelect(multiSelect, oldMultiSelect) {
    const me = this,
          {
      input,
      element
    } = me,
          fixValue = !me.isConfiguring;
    let {
      value
    } = me;
    element.classList[multiSelect ? 'add' : 'remove']('b-multiselect');

    if (multiSelect) {
      const {
        chipView
      } = me,
            {
        parentNode
      } = input,
            chipViewEl = chipView === null || chipView === void 0 ? void 0 : chipView.element; // If the input's parentNode is not the chipView's element, we need to restore that DOM arrangement

      if (chipViewEl && chipViewEl !== parentNode) {
        // This is where the chipView is created in the DOM but it may have been removed.
        parentNode.insertBefore(chipViewEl, input);
        chipViewEl.appendChild(input);
        me.chipView.refresh();
      }

      input.value = '';

      if (fixValue && !Array.isArray(value)) {
        value = value ? [value] : null;
      }
    } // else when !multiSelect, if the input's parentNode is the chipView's element, we need to put things back the
    // other way
    else {
        // avoid triggering lazy config if !multiSelect
        const chipView = me._chipView,
              {
          parentNode
        } = input,
              chipViewEl = chipView === null || chipView === void 0 ? void 0 : chipView.element;

        if (chipViewEl === parentNode) {
          // Put the input back in its proper place
          chipViewEl.parentNode.insertBefore(input, chipViewEl); // When no longer multiSelect, remove the chipView from the DOM. We do not destroy it because derived
          // classes or the instance config may have specified chipView config options. If we destroyed it, we could
          // not then properly recreate it.

          chipViewEl.remove();
          element.classList.remove('b-uses-chipview');
        }

        if (fixValue && typeof value !== 'string') {
          value = value && value.length ? value[0] : null;
        }
      }

    if (fixValue) {
      me.value = value;
    }
  }

  onChipViewMousedown(mousedownEvent) {
    mousedownEvent.preventDefault();

    if (!this.containsFocus) {
      this.focus();
    }
  }

  onChipClose(records, options = {}) {
    // Do not clean value collection if input field is not empty - probably text is selected
    // and user just wants to remove it, not the picked values
    if (options.isKeyEvent && this.input.value === '' || !options.isKeyEvent) {
      this._isUserAction = true;
      this.valueCollection.remove(records);
      this._isUserAction = false;
    }
  }

  updateFilterParamName(filterParamName) {
    if (this.hideTrigger !== false) {
      this.hideTrigger = Boolean(filterParamName);
    }
  }

  updateHideTrigger(hideTrigger) {
    this.element.classList[hideTrigger ? 'add' : 'remove']('b-hide-trigger');
  } //region Getters/setters

  updateKeyStrokeFilterDelay(delay) {
    const me = this;

    if (delay) {
      me.filterList = me.buffer(me.doFilter, delay);
    }
  }

  updateReadOnly(readOnly) {
    super.updateReadOnly(...arguments); // Disable closing (removing) chips when we are read-only.

    this._chipView && (this._chipView.closable = !readOnly);
  }

  get minChars() {
    const minChars = this._minChars;

    if (minChars != null) {
      return minChars;
    } // If it's not actually set, default differently for remote filtering.

    return this.remoteFilter ? 4 : 1;
  }

  get items() {
    return this.store.allRecords;
  }
  /**
   * Prepares items to work in attached menu (converts strings to items)
   * @private
   */

  changeItems(items) {
    const me = this;

    if (items == null) {
      if (me.store && !me.store.isItemStore) {
        return;
      }

      items = [];
    }

    if (items instanceof Store) {
      me.store = items;
      return;
    }

    const displayField = me.displayField;
    let itemModel,
        valueField = me.valueField,
        storeData;

    if (Array.isArray(items)) {
      storeData = items.map(item => {
        let result = item;

        if (item instanceof Model) {
          itemModel = item.constructor;

          if (!valueField) {
            me.valueField = valueField = itemModel.idField;
          }
        } else {
          if (typeof item === 'string' || typeof item === 'number') {
            if (!valueField) {
              me.valueField = valueField = me.displayField;
            }

            result = {
              [valueField]: item,
              [displayField]: item
            };
          } else {
            if (!valueField) {
              me.valueField = valueField = 'value';
            }

            if (Array.isArray(item)) {
              result = {
                [valueField]: item[0],
                [displayField]: item[1]
              };
            }
          }
        }

        if (result.selected) {
          me.value = result;
        }

        return result;
      });
    } // Must be a value -> text map
    else {
        if (!valueField) {
          me.valueField = valueField = 'value';
        }

        storeData = [];
        Object.entries(items).forEach(([key, value]) => {
          storeData.push({
            [valueField]: key,
            [displayField]: value
          });
        });
      } // Allow reconfiguring with a new set of items

    if (me.store) {
      me.store.data = storeData;
    } else {
      const valueFieldDefinition = valueField === displayField ? {
        name: 'value',
        dataSource: displayField
      } : valueField;
      me.store = new Store({
        isItemStore: true,
        data: storeData,
        idField: valueField,
        modelClass: itemModel || class extends Model {
          static get idField() {
            // Need to use instance var and not rely on closure for cases where valueField changes
            // (like first assigning [] and then ['a'] without configured valueField
            return me.valueField;
          }

          static set idField(idField) {
            super.idField = idField;
          }

          static get fields() {
            return [valueFieldDefinition, displayField];
          }

        }
      });
    }
  }

  get value() {
    const {
      valueCollection,
      valueField
    } = this;

    if (valueField == null) {
      return this.multiSelect ? valueCollection.values.slice() : valueCollection.first;
    }

    return this.multiSelect ? valueCollection.map(r => r[valueField]) : valueCollection.count ? valueCollection.first[valueField] : this._value;
  }

  set value(value) {
    super.value = value;
  }

  changeValue(value, oldValue) {
    const me = this;

    if (value === oldValue) {
      // Sync without highlight
      me.syncInputFieldValue(true);
      return;
    }

    if (!me.multiSelect && Array.isArray(value) && value.length > 1) {
      throw new Error('Multiple values cannot be set to a non-multiSelect Combo');
    } // This forces promotion of the items config into a Store if it has not already been injected

    me.triggerConfig('items');
    const {
      valueField,
      displayField,
      store,
      valueCollection
    } = me; // It's a remote filter store, so we have to do a filter down to just match(es)
    // and add the result to the valueCollection

    if (me.remoteFilter) {
      // The null case will drop through and be processed as for local just by clearing the valueCollection
      if (value != null) {
        if (typeof value === 'object') {
          this.store.data = [value];
          this.valueCollection.splice(0, this.valueCollection.count, this.store.first);
        } else {
          const wasConfiguring = me.isConfiguring;
          me.primaryFilter.setConfig({
            value,
            disabled: false
          });
          store.performFilter(true).then(() => {
            const {
              isConfiguring
            } = me; // Carry the wasConfiguring flag from the set value call frame so that
            // if it's the configuring set value, we do not now fire the change event.

            me.isConfiguring = wasConfiguring;
            valueCollection.splice(0, valueCollection.count, store.allRecords);
            me.isConfiguring = isConfiguring;
          });
          return;
        }
      }
    } // Else, if it's a *locally* filtered store, then
    // unfilter it so we can do the value lookup.
    else if (store.filtered) {
        me.primaryFilter.disabled = true;
        store.filter();
      }

    let record;

    if (value != null) {
      // If value is set as an array, make sure to slice it to not mutate original array below
      const arrayPassed = Array.isArray(value),
            values = arrayPassed ? value.slice() : [value];

      for (let i = 0, len = values.length; i < len; i++) {
        let currentValue = values[i];

        if (currentValue instanceof Model) {
          // The required record value may not yet be in the store. Add it if not.
          if (!store.storage.includes(currentValue)) {
            store.add(currentValue);
          }
        } else {
          const isObject = ObjectHelper.isObject(currentValue); // If they passed a data object, match the valueField

          if (isObject) {
            currentValue = currentValue[valueField];
          } // Use the Store Collection's extra indices to quickly find a match

          record = store.storage.getBy(displayField, currentValue) || store.storage.getBy(valueField, currentValue);

          if (record) {
            // If the incoming value was a matched object, use it to update the record
            if (isObject) {
              record.set(values[i]);
            }

            values[i] = record;
          } else {
            values.splice(i, 1);
            len--;
            i--;
          }
        }
      } // Remove all old values, add new values in one shot.

      const vcGen = valueCollection.generation;
      valueCollection.splice(0, valueCollection.count, values); // If no change has fed through to onValueCollectionChange, just ensure the input matches

      if (valueCollection.generation === vcGen) {
        me.syncInputFieldValue();
      } // If we got no matches, onValueCollectionChange will set the _value to null.
      // Tests specify that the _value should be set to the incoming unmatched value.
      // Handle the case that an array was passed.

      if (!values.length) {
        me._value = arrayPassed ? values[0] || null : value;
      }
    } else {
      if (valueCollection.count) {
        valueCollection.clear();
      } else {
        const oldValue = me._value; // Cache the value for use by our change handler next time, and also so that
        // when get value yields null, the fallback to ._value will be correct

        me._value = null;
        me.syncInputFieldValue();
        me.syncEmpty();

        if (!me.isConfiguring) {
          me.triggerFieldChange({
            value,
            oldValue,
            userAction: me._isUserAction,
            valid: me.isValid
          });
        }
      }
    }

    me._lastValue = me.value; // TODO ???
  }

  hasChanged(oldValue, newValue) {
    if (this.multiSelect) {
      return !ObjectHelper.isEqual(oldValue, newValue);
    }

    return super.hasChanged(...arguments);
  }

  onComboStoreChange({
    action
  }) {
    // Local filter-on-type is happening, do not sync the input value.
    if (action !== 'filter') {
      this.syncInputFieldValue(true);
    }
  }

  syncInputFieldValue(skipHighlight) {
    // We only sync the input's value if we are not using the chip view (DependencyField).
    // If we are multiselecting, our value is represented by a ChipView.
    // The ChipView automatically syncs itself with our valueCollection.
    if (!this.usesChipView) {
      super.syncInputFieldValue(skipHighlight);
    }
  }

  get usesChipView() {
    return Boolean(this.multiSelect && this._chipView);
  }
  /**
   * Returns `true` if this field has no selected records.
   * @property {Boolean}
   * @readonly
   */

  get isEmpty() {
    return this.valueCollection.count === 0;
  }

  get inputValue() {
    // This must be evaluated first, and NOT moved to be directly used as the
    // second expression in the ternary. If called during configuration, this
    // will import the configured value from the config object and ensure the
    // value is matched against the store, and that the "selected" property is set.
    const me = this;
    let result = me.value;
    result = me.selected ? me.selected[me.displayField] : result;

    if (me.displayValueRenderer) {
      result = me.callback(me.displayValueRenderer, me, [me.selected, me]);
    }

    return result == null ? '' : result;
  }
  /**
   * A {@link Core.util.Collection Collection} which holds the currently seleted records
   * from the store which dictates this field's value.
   *
   * Usually, this will contain one record, the record selected.
   *
   * When {@link #config-multiSelect} is `true`, there may be several records selected.
   * @type {Core.util.Collection}
   * @property valueCollection
   * @readonly
   */

  get valueCollection() {
    let valueCollection = this._valueCollection;

    if (!valueCollection) {
      this._valueCollection = valueCollection = new Collection({
        listeners: {
          noChange: 'onValueCollectionNoChange',
          change: 'onValueCollectionChange',
          prio: -1000,
          // The ChipView must react to changes first.
          thisObj: this
        }
      });
    }

    return valueCollection;
  }

  changePrimaryFilter(primaryFilter) {
    if (!(primaryFilter instanceof CollectionFilter)) {
      if (typeof primaryFilter === 'function') {
        primaryFilter = {
          filterBy: primaryFilter
        };
      } // This is the filter that performs filtering on typing.

      primaryFilter = new CollectionFilter({
        // Need an id to replace any existing combo filter on the store.
        // Dodge pre-commit hook by quoting property:
        'id': 'primary',
        // eslint-disable-line quote-props
        disabled: true,
        property: this.displayField,
        operator: this.filterOperator,
        caseSensitive: this.caseSensitive,
        ...primaryFilter
      });
    }

    return primaryFilter;
  }

  changeStore(store) {
    if (Array.isArray(store)) {
      this.items = store;
      return;
    }

    if (store) {
      if (typeof store === 'string') {
        store = Store.getStore(store);
      } else if (ObjectHelper.isObject(store)) {
        store = new (store.readUrl ? AjaxStore : Store)(store);
      }

      const me = this,
            remoteFilter = me.remoteFilter || store.restfulFilter,
            {
        filterParamName
      } = me,
            storeFilters = [];

      if (remoteFilter && filterParamName) {
        store.filterParamName = filterParamName;

        if (me.encodeFilterParams) {
          store.encodeFilterParams = me.encodeFilterParams;
        }
      } // If no value field provided, read it off of the Store's modelClass
      // Unless we want to use full record as the value

      if (!me.valueField && !me.usingRecordAsValue) {
        me.valueField = store.modelClass.idField;
      }

      storeFilters.push(me.primaryFilter);

      if (remoteFilter) {
        if (me.filterSelected) {
          store.storage.autoFilter = true;
          store.storage.addFilter({
            id: `${me.id}-selected-filter`,
            filterBy: r => !me.valueCollection.includes(r)
          });
        }
      } else if (me.filterSelected) {
        me.selectedItemsFilter = r => !me.valueCollection.includes(r);

        storeFilters.push(me.selectedItemsFilter);
        store.reapplyFilterOnAdd = true;
      }

      store.filter(storeFilters);
    }

    return store;
  }

  updateStore(store) {
    const me = this,
          {
      _picker
    } = me;
    let storeListeners;

    if (_picker) {
      _picker.store = store;
    } // Allow fast lookup by value or displayed value

    store.storage.addIndex(me.displayField);
    store.storage.addIndex(me.valueField);

    if (me.remoteFilter) {
      storeListeners = {
        filter: 'onRemoteFilter'
      };
    }

    if (me.displayValueRenderer) {
      (storeListeners || (storeListeners = {})).change = 'onComboStoreChange';
      me.syncInputFieldValue();
    }

    me.detachListeners('store');

    if (storeListeners) {
      storeListeners.name = 'store';
      storeListeners.thisObj = me;
      store.on(storeListeners);
    }
  }

  get remoteFilter() {
    var _this$_store;

    // Don't use "this.store" since we need to run during changeStore()
    return Boolean(this.filterParamName || ((_this$_store = this._store) === null || _this$_store === void 0 ? void 0 : _this$_store.restfulFilter));
  }
  /**
   * Get selected record.
   * @property {Core.data.Model[]}
   * @readonly
   */

  get record() {
    return this.selected;
  }
  /**
   * Get the selected record(s).
   * @property {Core.data.Model[]}
   * @readonly
   */

  get records() {
    return this.valueCollection.values.slice();
  }

  get selected() {
    return this.valueCollection.first;
  } //endregion
  //region Value handling

  /**
   * Check if field value is valid
   * @internal
   */

  onEditComplete() {
    const me = this,
          selectionCount = me.valueCollection.count;
    super.onEditComplete(); // Ensure the input area matches the selected value

    if (selectionCount) {
      me.clearError(errorValidateFilter);
      me.syncInputFieldValue();
    }

    if (me.required && !selectionCount) {
      me.setError(errorFieldRequired);
    }
  } //endregion
  //region Events

  /**
   * User clicked trigger icon, toggle list.
   * @private
   */

  onTriggerClick() {
    var _me$triggerAction;

    const me = this; // Bail out if enter key is required to trigger a filter

    if (me.remoteFilter && me.filterOnEnter) {
      return;
    }

    if (me.pickerVisible) {
      me.hidePicker();
    } else {
      if (!me.readOnly && !me.disabled) {
        switch ((_me$triggerAction = me.triggerAction) === null || _me$triggerAction === void 0 ? void 0 : _me$triggerAction.toLowerCase()) {
          case 'all':
            me.doFilter(null);
            break;

          case 'last':
            me.doFilter(me.lastQuery);
            break;

          default:
            me.doFilter(me.input.value);
        }
      }
    }
  }
  /**
   * User types into input field in editable combo, show list and filter it.
   * @private
   */

  internalOnInput(event) {
    const me = this,
          value = me.input.value,
          inputLen = value.length; // IE11 triggers input event on focus for some reason, ignoring it if not editable

    if (!me.editable) {
      return;
    }

    me.syncEmpty();
    me.syncInputWidth();
    me.inputting = true;

    if (inputLen >= me.minChars && !me.filterOnEnter) {
      me.filterList(value);
    } else {
      // During typing, the field is invalid
      if (me.validateFilter && !me.remoteFilter) {
        me[inputLen ? 'setError' : 'clearError'](errorValidateFilter);
      }

      me.hidePicker();
    }

    me.inputting = false;
    /**
     * User typed into the field. Please note that the value attached to this event is the raw input field value and
     * not the combos value
     * @event input
     * @param {Core.widget.Combo} source - The combo
     * @param {String} value - Raw input value
     */

    me.trigger('input', {
      value,
      event
    });
  }

  syncInputWidth() {
    const me = this;

    if (me.usesChipView) {
      const input = me.input,
            // padding on the input el won't change, so cache the measurement:
      inputPadding = me._inputPadding || (me._inputPadding = DomHelper.getEdgeSize(input, 'padding', 'lr')),
            value = input.value || '',
            width = // +'W' to avoid text getting clipped or horizontal scrolling
      DomHelper.measureText(value + 'W', input, false, me.element) + inputPadding.width; // Normally the input is given "flex: 1 1 0px" so it will fill the space
      // so we just need to adjust the flex-basis to ensure the input is at least
      // as long as the text. Since it is also flex-shrink, it will not become
      // any larger than one "row".

      input.style.flex = `1 1 ${Math.ceil(width)}px`;
    }
  } // This is potentially a buffered function to respond to keystrokes in a buffered manner.
  // This only becomes useful as a saving when using remote querying where each filter is an Ajax request.

  filterList(queryString) {
    this.doFilter(queryString);
  }

  doFilter(queryString) {
    const me = this,
          {
      store
    } = me,
          {
      _picker: picker
    } = me,
          disableFilter = queryString == null || queryString === '';
    me.lastQuery = queryString;
    me.primaryFilter.setConfig({
      value: queryString,
      disabled: disableFilter
    });

    if (me.remoteFilter) {
      store.clear(true);
    } // Force the lazy config to create picker since the List needs to add its beforeLoad listener
    // showing a mask in case of remote filtering

    me.getConfig('picker');
    store.filter();

    if (picker !== null && picker !== void 0 && picker.isVisible) {
      // If aligned above, filtering will change its height so will need realigning
      if (picker.lastAlignSpec.zone === 0) {
        picker.realign();
      }
    } else {
      me.showPicker();
    }

    if (store.count) {
      // If we are filtering, activate the first match
      if (!disableFilter) {
        me.picker.navigator.activeItem = 0;
      }
    } // If we were actively *locally* filtering on a string but there were no matches
    // and we are validateFilter: true, then mark as invalid even though we
    // may have an underlying valid selected value.
    else if (!me.remoteFilter && !disableFilter && me.validateFilter) {
        me.setError(errorValidateFilter);
      }
  }

  onRemoteFilter() {
    const me = this,
          picker = me._picker; // If we are filtering, activate the first match

    if (me.store.count) {
      if (picker) {
        picker.navigator.activeItem = 0;
      }
    } // Invalid if no matches after filtering
    else {
        if (me.validateFilter) {
          me.setError(errorValidateFilter);
        }
      } // If we have selection, evict selected items from the newly remote-filtered list

    if (me.filterSelected && me.valueCollection.count) {
      me.store.storage.onFiltersChanged(); // Store does not react to Collection filtering yet because it does its own filtering and
      // then fires its own event. So we have to refresh the picker to hide the selected items.

      if (picker) {
        picker.refresh();
      }
    }
  }
  /**
   * This reacts to our {@link #property-valueCollection} being mutated in any way.
   * The `change`, `select` and `action` events are fired here.
   *
   * This could happen in four ways:
   *
   *  - User selected or deselected an item in the dropdown list.
   *  - `set value` changes the content.
   *  - The {@link #config-multiSelect} Chip view (which uses this in its store) deletes a record.
   *  - The application programmatically mutates the {@link #property-valueCollection}.
   *
   * @private
   */

  onValueCollectionChange({
    source: valueCollection
  }) {
    var _me$hidePickerOnSelec;

    const me = this,
          {
      multiSelect,
      _picker
    } = me,
          hidePicker = (_me$hidePickerOnSelec = me.hidePickerOnSelect) !== null && _me$hidePickerOnSelec !== void 0 ? _me$hidePickerOnSelec : !multiSelect,
          record = multiSelect ? valueCollection.values.slice() : valueCollection.first,
          records = valueCollection.values.slice(),
          isUserAction = me._isUserAction || (_picker === null || _picker === void 0 ? void 0 : _picker._isUserAction) || hidePicker && me.pickerVisible || false,
          oldValue = me._value;

    if (hidePicker) {
      me.hidePicker();
    }

    if (!valueCollection.count && me.required) {
      me.setError(errorFieldRequired);
    } else {
      me.clearError(errorFieldRequired);
      me.clearError(errorValidateFilter);
    }

    if (me.validateFilter && record) {
      me.clearError(errorValidateFilter);
    } // Re-evaluate *local* filtering so that selected items are filtered out of the dropdown.
    // For remote filtering, we programatically add a filter to the store's storage

    if (me.filterSelected) {
      if (me.remoteFilter) {
        me.store.storage.onFiltersChanged(); // Store does not react to Collection filtering yet because it does its own filtering and
        // then fires its own event. So we have to refresh the picker to hide the selected items.

        if (me._picker) {
          me._picker.refresh();
        }
      } else {
        me.store.filter();
      }
    } // Clear the cached value so that there's no fallback when we read back the value below

    me._value = null; // Cache the value for use by our change handler next time, and also so that
    // if we just cleared the valueCollection, the fallback to ._value will be correct

    const value = me.cacheCurrentValue(me.value);
    me.syncInputFieldValue();
    me.syncEmpty();

    if (!me.isConfiguring) {
      me.triggerFieldChange({
        value,
        oldValue,
        userAction: isUserAction,
        valid: me.isValid
      });
      /**
       * An item in the list was selected
       * @event select
       * @property {Combo} combo - Combo
       * @property {Core.data.Model} record - Selected record
       * @property {Core.data.Model} records - Selected records as an array if {@link #config-multiSelect} is `true`
       * @property {Boolean} userAction - `true` if the value change is due to user interaction.
       */

      me.trigger('select', {
        record,
        records,
        userAction: isUserAction
      });
      /**
       * Th default action was performed (an item in the list was selected)
       * @event action
       * @property {Combo} combo - Combo
       * @property {*} value - The {@link #valueField} of the selected record
       * @property {Core.data.Model} record - Selected record
       * @property {Core.data.Model} records - Selected records as an array if {@link #config-multiSelect} is `true`
       * @property {Boolean} userAction - `true` if the value change is due to user interaction.
       */

      if (me.defaultAction === 'select') {
        me.trigger('action', {
          value,
          record,
          records,
          userAction: isUserAction
        });
      }
    }
  } // Caching a copy of current value, which can be changed by subclasses (see AssignmentField for reference)

  cacheCurrentValue(v) {
    return this._value = v;
  }
  /**
   * This listens for when a record from the list is selected, but is already part of
   * the selection and so the {@link #property-valueCollection} rejects that as a no-op.
   * At this point, the user will still expect the picker to hide.
   * @param {Object} event The noChange event containing the splice parameters
   * @private
   */

  onValueCollectionNoChange({
    toAdd
  }) {
    if (!this.multiSelect && toAdd.length && this.pickerVisible) {
      this.picker.hide();
    }
  } //endregion
  //region Picker

  showPicker() {
    const me = this,
          {
      picker
    } = me;

    if (me.readOnly) {
      return;
    }

    picker.multiSelect = me.multiSelect;
    super.showPicker(); // Once we have access to the anchor size, overlay the anchor pointer over the target if configured to do so.

    if (me.overlayAnchor && !picker.align.offset) {
      picker.align.offset = -picker.anchorSize[1];
      picker.realign();
    } // Activate and make visible the *single* selected item.
    // Not valid if multiselect because the user's interest will not be focused on any one item.

    if (!me.multiSelect && me.selected) {
      picker.restoreActiveItem(me.selected, true);
    }

    me.input.focus();
  }
  /**
   * Creates default picker widget
   *
   * @internal
   */

  changePicker(picker, oldPicker) {
    var _picker2;

    const me = this,
          pickerWidth = me.pickerWidth || ((_picker2 = picker) === null || _picker2 === void 0 ? void 0 : _picker2.width);
    picker = List.reconfigure(oldPicker, picker ? List.mergeConfigs({
      owner: me,
      store: me.store,
      selected: me.valueCollection,
      multiSelect: me.multiSelect,
      cls: me.listCls,
      itemTpl: me.listItemTpl || (item => item[me.displayField]),
      forElement: me[me.pickerAlignElement],
      align: {
        matchSize: pickerWidth == null,
        anchor: me.overlayAnchor,
        target: me[me.pickerAlignElement],
        // Reasonable minimal height to fit few combo items below the combo.
        // When height is not enough, list will appear on top. That works for windows higher than 280px,
        // worrying about shorter windows sounds overkill.
        // We cannot use relative measures here, each combo list item is ~40px high
        minHeight: Math.min(3, me.store.count) * 40
      },
      width: pickerWidth,
      navigator: {
        keyEventTarget: me.input
      }
    }, picker) : null, me);
    picker && (picker.element.dataset.emptyText = me.emptyText || me.L('L{noResults}'));
    return picker;
  }

  onPickerHide() {
    const me = this;
    super.onPickerHide(); // https://app.assembla.com/spaces/bryntum/tickets/7736

    if (me.multiSelect && me.clearTextOnPickerHide) {
      me.input.value = '';
    }
  } //endregion

  internalOnKeyEvent(keyEvent) {
    const me = this,
          value = me.input.value,
          inputLen = value.length;
    super.internalOnKeyEvent(...arguments);

    if (keyEvent.type === 'keydown' && keyEvent.key === 'Enter' && me.filterOnEnter && inputLen >= me.minChars) {
      keyEvent.stopPropagation();
      me.filterList(value);
    }
  }

}

class ComboChipView extends ChipView {
  static get $name() {
    return 'ComboChipView';
  }

  static get type() {
    return 'combochipview';
  }

  static get defaultConfig() {
    return {
      closeHandler: 'up.onChipClose',
      itemsFocusable: false,
      multiSelect: true,

      itemTpl(record) {
        return record[this.owner.displayField];
      },

      scrollable: {
        overflowY: 'auto'
      }
    };
  }

}

class ComboChipNavigator extends Navigator {
  static get defaultConfig() {
    return {
      allowShiftKey: true
    };
  }

  onTargetClick(clickEvent) {
    const item = clickEvent.target.closest(this.itemSelector);

    if (item && !clickEvent.shiftKey && !item.contains(clickEvent.target.closest('[data-noselect]'))) {
      this.ownerCmp.selected.clear();
    } // Our own updateActiveItem also selects because on superclass *key* navigation
    // (which is async on scroll end), it sets activeItem, and we select at that time.
    // So set a flag which disables this.

    this.inClickHandler = true;
    this.activeItem = item;
    this.inClickHandler = false;
  }

  onKeyDown(keyEvent) {
    // ENTER does not toggle selectedness in a ChipView.
    // ChipView's selection is bound to navigation.
    if (keyEvent.key !== 'Enter') {
      super.onKeyDown(keyEvent);
    }
  }

  updateActiveItem(activeItem, oldActiveItem) {
    const chipView = this.ownerCmp;
    super.updateActiveItem(activeItem, oldActiveItem); // Selection simply follows navigation in a ChipView

    if (activeItem && !this.inClickHandler) {
      chipView.selected.add(chipView.getRecord(activeItem));
    }
  }

  navigatePrevious(keyEvent) {
    const chipView = this.ownerCmp;

    if (chipView.navigator.activeItem && !keyEvent.shiftKey) {
      chipView.selected.clear();
    }

    if (this.previous) {
      super.navigatePrevious(keyEvent);
    } else {
      this.activeItem = null;
    }
  }

  navigateNext(keyEvent) {
    const chipView = this.ownerCmp; // SHIFT+navigate preserves selection

    if (chipView.navigator.activeItem && !keyEvent.shiftKey) {
      chipView.selected.clear();
    }

    if (this.next) {
      super.navigateNext(keyEvent);
    } else {
      this.activeItem = null;
    }
  }

} // Register this widget type with its Factory

Combo.initClass();
ComboChipView.initClass();
Combo._$name = 'Combo';

/**
 * @module Core/widget/BooleanCombo
 * Boolean combo, a combo box with two options corresponding to true or false.
 *
 * This field can be used as an {@link Grid.column.Column#config-editor editor} for the {@link Grid.column.Column Column}.
 *
 * @classType booleancombo
 * @extends Core/widget/Combo
 */

class BooleanCombo extends Combo {
  static get $name() {
    return 'BooleanCombo';
  }

  static get type() {
    return 'booleancombo';
  } //region Config

  static get configurable() {
    return {
      /**
       * Positive option value
       *
       * @config {*}
       */
      positiveValue: true,

      /**
       * Positive option display value
       *
       * @config {String}
       */
      positiveText: null,

      /**
       * Negative option value
       *
       * @config {*}
       */
      negativeValue: false,

      /**
       * False option display value
       *
       * @config {String}
       */
      negativeText: null,
      store: {
        value: [],
        $config: 'lazy'
      },

      /**
       * Default value
       *
       * @config {*}
       */
      value: false
    };
  } //endregion

  changeStore(store, oldStore) {
    const me = this; // We must call super.changeStore() in order to deduce valueField. We also cannot just pass an array since it
    // will convert to a store and call back here (infinite recursion).

    return super.changeStore(new Store({
      data: [{
        id: me.positiveValue,
        text: me.positiveText || me.L('L{Object.Yes}')
      }, {
        id: me.negativeValue,
        text: me.negativeText || me.L('L{Object.No}')
      }]
    }), oldStore);
  }

} // Register this widget type with its Factory

BooleanCombo.initClass();
BooleanCombo._$name = 'BooleanCombo';

/**
 * @module Core/widget/ButtonGroup
 */

/**
 * A specialized container that holds buttons, displaying them in a horizontal group with borders adjusted to make them
 * stick together.
 *
 * Trying to add other widgets than buttons will throw an exception.
 *
 * ```javascript
 * new ButtonGroup({
 *   items : [
 *       { icon : 'b-fa b-fa-kiwi-bird' },
 *       { icon : 'b-fa b-fa-kiwi-otter' },
 *       { icon : 'b-fa b-fa-kiwi-rabbit' },
 *       ...
 *   ]
 * });
 * ```
 *
 * @externalexample widget/ButtonGroup.js
 * @classType buttonGroup
 * @extends Core/widget/Container
 */

class ButtonGroup extends Container {
  /**
   * Fires when a button is clicked
   * @event click
   * @property {Core.widget.Button} source - Clicked button
   * @property {Event} event - DOM event
   */

  /**
   * Fired after user performed the default action (clicked the button)
   * @event action
   * @property {Core.widget.Button} source - Clicked button
   * @property {Event} event - DOM event
   */
  static get $name() {
    return 'ButtonGroup';
  } // Factoryable type name

  static get type() {
    return 'buttongroup';
  }

  static get configurable() {
    return {
      defaultType: 'button',

      /**
       * Custom CSS class to add to element. When using raised buttons (cls 'b-raised' on the buttons), the group
       * will look nicer if you also set that cls on the group.
       *
       * ```
       * new ButtonGroup({
       *   cls : 'b-raised,
       *   items : [
       *       { icon : 'b-fa b-fa-unicorn', cls : 'b-raised' },
       *       ...
       *   ]
       * });
       * ```
       *
       * @config {String}
       * @category CSS
       */
      cls: null,

      /**
       * An array of Buttons or typed Button config objects.
       * @config {Object[]|Core.widget.Button[]}
       */
      items: null,

      /**
       * Default color to apply to all contained buttons, see {@link Core.widget.Button#config-color Button#color}.
       * Individual buttons can override the default.
       * @config {String}
       */
      color: null,

      /**
       * Set to `true` to turn the ButtonGroup into a toggle group, assigning a generated value to each contained
       * buttons {@link Core.widget.Button#config-toggleGroup toggleGroup config}. Individual buttons can
       * override the default.
       * @config {Boolean}
       */
      toggleGroup: null,
      valueSeparator: ',',
      columns: null
    };
  }

  updateReadOnly(readOnly) {
    this.element.style.pointerEvents = readOnly ? 'none' : '';
  }

  onChildAdd(item) {
    super.onChildAdd(item);
    item.on({
      click: 'onItemClick',
      thisObj: this,
      // This needs to run before the 'click' event is relayed by this button group, in such listener
      // the `value` must already be updated
      prio: 10000
    });
  }

  onChildRemove(item) {
    item.un({
      click: 'onItemClick',
      thisObj: this
    });
    super.onChildAdd(item);
  }

  onItemClick() {
    // reset cached value to revalidate next time it's requested
    this._value = null;
  }

  createWidget(widget) {
    const me = this,
          type = me.constructor.resolveType(widget.type || 'button');

    if (type.isButton) {
      if (me.color && !widget.color) {
        widget.color = me.color;
      }

      if (me.toggleGroup && !widget.toggleGroup) {
        if (typeof me.toggleGroup === 'boolean') {
          me.toggleGroup = ButtonGroup.generateId('toggleGroup');
        }

        widget.toggleGroup = me.toggleGroup;
      }
    }

    if (me.columns) {
      widget.width = `${100 / me.columns}%`;
    }

    widget = super.createWidget(widget);
    me.relayEvents(widget, ['click', 'action']);
    return widget;
  }

  get value() {
    // if we don't have cached value
    // let's calculate it based on item values
    if (!this._value) {
      const values = []; // collect pressed item values

      this.items.forEach(w => {
        if (w.pressed && w.value !== undefined) values.push(w.value);
      }); // build a string

      this._value = values.join(this.valueSeparator);
    }

    return this._value;
  }

  set value(value) {
    if (!Array.isArray(value)) {
      if (value === undefined || value === null) {
        value = [];
      } else if (typeof value == 'string') {
        value = value.split(this.valueSeparator);
      } else {
        value = [value];
      }
    }

    this._value = value; // reflect value on items

    this.items.forEach(w => {
      if (w.value !== undefined) {
        w.pressed = value.indexOf(w.value) > -1;
      }
    });
  }

  get disabled() {
    return super.disabled;
  }

  set disabled(state) {
    super.disabled = state;
    this.items.forEach(w => w.disabled = state);
  }

  get widgetClassList() {
    const classList = super.widgetClassList; // if the buttons should be shown in rows

    if (this.columns) classList.push('b-columned');
    return classList;
  }

} // Register this widget type with its Factory

ButtonGroup.initClass();
ButtonGroup._$name = 'ButtonGroup';

/**
 * @module Core/widget/CalendarPanel
 */

/**
 * A Panel which can display a month of date cells.
 *
 * This is a base class for UI widgets which need to make use of a calendar layout
 * and should not be used directly.
 * @extends Core/widget/Panel
 */

class CalendarPanel extends Panel {
  static get $name() {
    return 'CalendarPanel';
  } // Factoryable type name

  static get type() {
    return 'calendarpanel';
  }

  static get configurable() {
    return {
      textContent: false,

      /**
       * Gets or sets the date that orientates the panel to display a particular month.
       * Changing this causes the content to be refreshed.
       * @property {Date}
       * @name date
       */

      /**
       * The date which this CalendarPanel encapsulates.
       * @config {Date|String}
       */
      date: {
        $config: {
          equal: 'date'
        },
        value: null
      },

      /**
       * A {@link Core.util.Month} Month utility object which encapsulates this Panel's month
       * and provides contextual information and navigation services.
       * @config {Core.util.Month|Object}
       */
      month: {},
      year: null,

      /**
       * The week start day, 0 meaning Sunday, 6 meaning Saturday.
       * Defaults to the locale's week start day.
       * @config {Number}
       */
      weekStartDay: null,

      /**
       * Configure as `true` to always show a six week calendar.
       * @config {Boolean}
       * @default
       */
      sixWeeks: true,

      /**
       * Configure as `true` to show a week number column at the start of the calendar block.
       * @deprecated 4.0.0 Use {@link #config-showWeekColumn} instead.
       * @config {Boolean}
       */
      showWeekNumber: null,

      /**
       * Configure as `true` to show a week number column at the start of the calendar block.
       * @config {Boolean}
       */
      showWeekColumn: null,

      /**
       * Either an array of `Date` objects which are to be disabled, or
       * a function, which, when passed a `Date` returns `true` if the
       * date is disabled.
       * @config {Function|Date[]}
       */
      disabledDates: null,

      /**
       * A function which creates content in, and may mutate a day header element.
       * The following parameters are passed:
       *  - cell [HTMLElement](https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement) The header element.
       *  - day [Number](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Number) The day number conforming to the specified {@link #config-weekStartDay}. Will be in the range 0 to 6.
       *  - weekDay [Number](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Number) The canonial day number where Monday is 0 and Sunday is.
       * @config {Function}
       */
      headerRenderer: null,

      /**
       * A function which creates content in, and may mutate the week cell element at the start of a week row.
       * The following parameters are passed:
       *  - cell [HTMLElement](https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement) The header element.
       *  - week [Number[]](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Number) An array containing `[year, weekNumber]`.
       * @config {Function}
       */
      weekRenderer: null,

      /**
       * A function which creates content in, and may mutate a day cell element.
       * The following parameters are passed:
       *  - cell [HTMLElement](https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement) The header element.
       *  - date [Date](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date) The date for the cell.
       *  - cellIndex [Number[]](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Number) The cell index, 0 to 6.
       * @config {Function}
       */
      cellRenderer: null,

      /**
       * Configure as `true` to render weekends as {@link #config-disabledDates}.
       * @config {Boolean}
       */
      disableWeekends: null,
      hideNonWorkingDays: null,
      hideNonWorkingDaysCls: 'b-hide-nonworking-days',

      /**
       * An object with number properties (0-6) whose values define the non-working days.
       * @config {Object}
       * @default
       */
      nonWorkingDays: {
        0: true,
        6: true
      },

      /**
       * A config object to create a tooltip which will show on hover of a date cell
       * including disabled, weekend, and "other month" cells.
       *
       * It is the developer's responsibility to hook the `beforeshow` event
       * to either veto the show by returning `false` or provide contextual
       * content for the date.
       *
       * The tip instance will be primed with a `currentDate` property.
       * @config {Object}
       */
      tip: null,
      dayCellCls: 'b-calendar-cell',

      /**
       * The class name to add to disabled calendar cells.
       * @config {String}
       * @private
       */
      disabledCls: 'b-disabled-date',

      /**
       * The class name to add to calendar cells which are in the previous or next month.
       * @config {String}
       * @private
       */
      otherMonthCls: 'b-other-month',

      /**
       * The class name to add to calendar cells which are weekend dates.
       * @config {String}
       * @private
       */
      weekendCls: 'b-weekend',

      /**
       * The class name to add to the calendar cell which contains today's date.
       * @config {String}
       * @private
       */
      todayCls: 'b-today',

      /**
       * The class name to add to calendar cells which are {@link #config-nonWorkingDays}.
       * @config {String}
       * @private
       */
      nonWorkingDayCls: 'b-nonworking-day',

      /**
       * The {@link Core.helper.DateHelper DateHelper} format string to format the day names.
       * @config {String}
       * @default
       */
      dayNameFormat: 'ddd',

      /**
       * By default, week rows flex to share available Panel height equally.
       *
       * Set this config if the available height is too small, and the cell height needs
       * to be larger to show events.
       *
       * Setting this config causes the month grid to become scrollable in the `Y` axis.
       * @config {Number}
       */
      minRowHeight: {
        $config: ['lazy'],
        value: null
      },

      /**
       * By default, day cells flex to share available Panel width equally.
       *
       * Set this config if the available width is too small, and the cell width needs
       * to be larger to show events.
       *
       * Setting this config causes the month grid to become scrollable in the `X` axis.
       * @config {Number}
       */
      minColumnWidth: {
        $config: ['lazy'],
        value: null
      }
    };
  }

  construct(config) {
    super.construct(config);

    if (!this.refreshCount) {
      this.refresh();
    }
  }

  onPaint({
    firstPaint
  }) {
    // Invoke the lazy configs when we first hit the visible DOM
    if (firstPaint) {
      // The cell structure must exist for the configs to apply to.
      if (!this.refreshCount) {
        this.refresh();
      }

      this.getConfig('minColumnWidth');
      this.getConfig('minRowHeight');
    }
  }

  get overflowElement() {
    return this.weeksElement;
  }

  doDestroy() {
    this.tip && this.tip.destroy();
    super.doDestroy();
  }

  changeMinRowHeight(minRowHeight) {
    // Fall back to 75 on platforms that do not support CSS vars
    const minValue = parseInt(DomHelper.getStyleValue(this.element, '--min-row-height'), 10) || 75;
    return minRowHeight == null ? minRowHeight : Math.max(parseInt(minRowHeight) || 0, minValue);
  }

  updateMinRowHeight(minRowHeight) {
    this.weekElements.forEach(w => DomHelper.setLength(w, 'flexBasis', minRowHeight));
    this.scrollable = {
      overflowY: minRowHeight ? 'auto' : false
    };
  }

  changeMinColumnWidth(minColumnWidth) {
    // Fall back to 75 on platforms that do not support CSS vars
    const minValue = parseInt(DomHelper.getStyleValue(this.element, '--min-column-width'), 10) || 75;
    return minColumnWidth == null ? minColumnWidth : Math.max(parseInt(minColumnWidth) || 0, minValue);
  }

  updateMinColumnWidth(minColumnWidth) {
    const me = this;
    me.weekdayCells.forEach(c => DomHelper.setLength(c, 'minWidth', minColumnWidth));
    me.cellElements.forEach(c => c.matches(`.${me.dayCellCls}`) && DomHelper.setLength(c, 'minWidth', minColumnWidth));
    me.scrollable = {
      overflowX: minColumnWidth ? 'auto' : false
    };
    me.overflowElement.classList[minColumnWidth ? 'add' : 'remove']('b-min-columnwidth');
  }

  getDateFromEvent(domEvent) {
    const element = (domEvent.nodeType === 1 ? domEvent : domEvent.target).closest(`#${this.id} [data-date]`);

    if (element) {
      return DateHelper.parseKey(element.dataset.date);
    }
  }

  changeTip(tip, oldTip) {
    const listeners = {
      pointerOver: 'onTipOverCell',
      thisObj: this
    };

    if (oldTip) {
      oldTip.un(listeners);
    }

    if (tip) {
      if (!(tip instanceof Widget)) {
        tip = Widget.create(Object.assign({
          owner: this,
          type: 'tooltip',
          id: `${this.id}-cell-tip`,
          forElement: this.bodyElement,
          forSelector: `.${this.dayCellCls}`
        }, tip));
      }

      tip.on(listeners);
    }

    return tip;
  }

  changeElement(element) {
    const me = this;
    element = super.changeElement(...arguments);
    me.updateHideNonWorkingDays(me.hideNonWorkingDays);
    me.weekElements = Array.from(element.querySelectorAll('.b-calendar-week'));
    me.weekDayElements = Array.from(element.querySelectorAll('.b-calendar-days'));
    me.cellElements = [];

    for (let i = 0, {
      length
    } = me.weekDayElements; i < length; i++) {
      me.cellElements.push(me.weekDayElements[i].previousSibling, ...me.weekDayElements[i].children);
    }

    return element;
  }

  changeDate(date) {
    date = typeof date === 'string' ? DateHelper.parse(date) : new Date(date);

    if (isNaN(date)) {
      throw new Error('CalendarPanel date ingestion must be passed a Date, or a YYYY-MM-DD date string');
    }

    return DateHelper.clearTime(date);
  }
  /**
   * The date which this CalendarPanel encapsulates. Setting this causes the
   * content to be refreshed.
   * @property {Date}
   */

  updateDate(value) {
    // We respond to Month change events to update the UI
    this.month.date = value;
  }

  updateDayNameFormat() {
    // 4th June 2000 was a Sunday
    const d = new Date('2000-06-04T12:00:00');
    this.shortDayNames = []; // Collect local shortDayNames in default order.

    for (let date = 4; date < 11; date++) {
      d.setDate(date);
      this.shortDayNames.push(DateHelper.format(d, this.dayNameFormat));
    }
  }

  get weekStartDay() {
    // This trick allows our weekStartDay to float w/the locale even if the locale changes
    return typeof this._weekStartDay === 'number' ? this._weekStartDay : DateHelper.weekStartDay;
  }
  /**
   * Set to 0 for Sunday (the default), 1 for Monday etc.
   *
   * Set to `null` to use the default value from {@link Core.helper.DateHelper DateHelper}.
   */

  updateWeekStartDay(weekStartDay) {
    const me = this;

    if (me._month) {
      me.month.weekStartDay = weekStartDay;
      me.dayNames = []; // So, if they set weekStartDay to 1 meaning Monday which is ISO standard, we will
      // dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

      for (let i = 0; i < 7; i++) {
        me.dayNames[i] = me.shortDayNames[me.canonicalDayNumbers[i]];
      }

      if (me.refreshCount) {
        me.refresh();
      }
    }
  }

  updateHideNonWorkingDays(hideNonWorkingDays) {
    var _this$scrollable;

    this.contentElement.classList[hideNonWorkingDays ? 'add' : 'remove'](this.hideNonWorkingDaysCls);
    (_this$scrollable = this.scrollable) === null || _this$scrollable === void 0 ? void 0 : _this$scrollable.syncOverflowState();

    if (this._month) {
      this.month.hideNonWorkingDays = hideNonWorkingDays;
    }
  }

  changeNonWorkingDays(nonWorkingDays) {
    return ObjectHelper.assign({}, nonWorkingDays);
  }

  updateNonWorkingDays(nonWorkingDays) {
    if (this._month) {
      var _this$scrollable2;

      this.month.nonWorkingDays = nonWorkingDays;
      this.refresh();
      (_this$scrollable2 = this.scrollable) === null || _this$scrollable2 === void 0 ? void 0 : _this$scrollable2.syncOverflowState();
    }
  }

  get visibleDayColumnIndex() {
    return this.month.visibleDayColumnIndex;
  }

  get dayColumnIndex() {
    return this.month.dayColumnIndex;
  }

  get canonicalDayNumbers() {
    return this.month.canonicalDayNumbers;
  }

  get visibleColumnCount() {
    return this.month.visibleColumnCount;
  }

  get weekLength() {
    return this.month.weekLength;
  }
  /**
   * The date of the first day cell in this panel.
   * Note that this may *not* be the first of this panel's current month.
   * @property {Date}
   * @readonly
   */

  get startDate() {
    return this.month.startDate;
  }

  get duration() {
    // The endDate is "exclusive" because it means 00:00:00 of that day.
    return DateHelper.diff(this.month.startDate, this.month.endDate, 'day') + 1;
  }
  /**
   * The end date of this view. Note that in terms of full days, this is exclusive,
   * ie: 2020-01-012 to 2020-01-08 is *seven* days. The end is 00:00:00 on the 8th.
   *
   * Note that this may *not* be the last date of this panel's current month.
   * @property {Date}
   * @readonly
   */

  get endDate() {
    const {
      endDate
    } = this.month;

    if (endDate) {
      return DateHelper.add(endDate, 1, 'day');
    }
  }

  changeMonth(month, currentMonth) {
    const me = this;

    if (!(month instanceof Month)) {
      // Setting month to a number when we already have a Month means
      // just updating the month number of our Month
      if (typeof month === 'number') {
        if (currentMonth) {
          currentMonth.month = month;
          return;
        }

        const date = me.date || DateHelper.clearTime(new Date());
        date.setMonth(month);
        month = {
          date
        };
      }

      month = new Month(ObjectHelper.assign({
        weekStartDay: me.weekStartDay,
        nonWorkingDays: me.nonWorkingDays,
        hideNonWorkingDays: me.hideNonWorkingDays,
        sixWeeks: me.sixWeeks
      }, month));
    }

    month.on({
      dateChange: 'onMonthDateChange',
      thisObj: me
    });
    return month;
  }

  onMonthDateChange({
    source: month,
    newDate,
    oldDate
  }) {
    const me = this; // Ensure we're always in sync with the data our Month holds

    me.year = month.year;

    if (!me.isConfiguring) {
      me.refresh();

      if (oldDate - newDate) {
        /**
         * Fires when the date of this CalendarPanel is set.
         * @event dateChange
         * @param {Date[]} value The new date.
         * @param {Date[]} oldValue The old date.
         */
        me.trigger('dateChange', {
          value: newDate,
          oldValue: oldDate
        });
      }
    }
  }

  updateYear(year) {
    this.month.year = year;
  }

  updateShowWeekNumber(showWeekNumber) {
    this.updateShowWeekColumn(showWeekNumber);
  }

  updateShowWeekColumn(showWeekColumn) {
    const me = this;
    me.element.classList[showWeekColumn ? 'add' : 'remove']('b-show-week-column');

    if (me.floating) {
      // Must realign because content change might change dimensions
      if (!me.isAligning) {
        me.realign();
      }
    }
  }
  /**
   * Refreshes the UI after changing a config that would affect the UI.
   */

  refresh() {
    // This method may be overridden by subclasses to add things like refresh scheduling.
    this.doRefresh();
  }
  /**
   * Implementation of the UI refresh.
   * @private
   */

  doRefresh() {
    // Ensure sub elements are all present
    this.getConfig('element');
    const me = this,
          today = DateHelper.clearTime(new Date()),
          {
      cellElements,
      weekElements,
      weekDayElements,
      date,
      month,
      dayCellCls,
      disabledCls,
      otherMonthCls,
      weekendCls,
      todayCls,
      nonWorkingDayCls,
      canonicalDayNumbers
    } = me; // If we have not been initialized with a current date, use today

    if (!date) {
      me.date = today;
    } // Make sure we've calculated our shortDayNames

    me.getConfig('dayNameFormat'); // Reset all cell classNames.
    // The lastDomConfig property must not be preserved if we are directly manipulating the DOM
    // and other code paths in the app use DomSync.

    for (let i = 0, len = cellElements.length; i < len; i++) {
      cellElements[i].lastDomConfig = null;
      cellElements[i].className = dayCellCls;
    }

    for (let columnIndex = 0; columnIndex < 7; columnIndex++) {
      const cell = me.weekdayCells[columnIndex],
            cellDay = me.canonicalDayNumbers[columnIndex],
            cellClassList = cell.classList;
      cell.className = cell.innerHTML = '';

      if (me.headerRenderer) {
        me.headerRenderer(cell, columnIndex, cellDay);
      } else {
        cell.innerHTML = me.shortDayNames[cellDay];
      }

      cellClassList.add('b-calendar-day-header');

      if (cellDay === 0 || cellDay === 6) {
        cellClassList.add(weekendCls);
      }

      if (me.nonWorkingDays[cellDay]) {
        cellClassList.add(nonWorkingDayCls);
      }

      cell.dataset.columnIndex = columnIndex;
      cell.dataset.cellDay = cellDay;
    } // Create cell content

    let weekIndex = 0,
        cellIndex = 0,
        lastWorkingColumn = 6; // Which column is the last working day so it can be tagged with an identifying class

    for (let columnIndex = 6; columnIndex >= 0; columnIndex--) {
      if (!me.nonWorkingDays[canonicalDayNumbers[columnIndex]]) {
        lastWorkingColumn = columnIndex;
        break;
      }
    }

    month.eachWeek((week, dates) => {
      const weekDayElement = weekDayElements[weekIndex],
            weekCells = [weekDayElement.previousSibling, ...weekDayElement.children]; // Stamp week into week row's dataset

      weekElements[weekIndex].dataset.week = `${week[0]},${week[1]}`;
      weekCells[0].className = 'b-week-number-cell';

      if (me.weekRenderer) {
        me.weekRenderer(weekCells[0], week);
      } else {
        weekCells[0].innerHTML = week[1];
      }

      for (let columnIndex = 0; columnIndex < 7; columnIndex++) {
        const date = dates[columnIndex],
              day = date.getDay(),
              cell = weekCells[columnIndex + 1],
              cellClassList = cell.classList;

        if (me.isDisabledDate(date)) {
          cellClassList.add(disabledCls);
        }

        if (date.getMonth() !== month.month) {
          cellClassList.add(otherMonthCls);
        }

        if (day === 0 || day === 6) {
          cellClassList.add(weekendCls);
        }

        if (date.getTime() === today.getTime()) {
          cellClassList.add(todayCls);
        }

        if (me.nonWorkingDays[day]) {
          cellClassList.add(nonWorkingDayCls);
        } // Tag the last working day in a week with a class

        cellClassList[columnIndex === lastWorkingColumn ? 'add' : 'remove']('b-last-working-day');
        cell.dataset.date = DateHelper.makeKey(date);
        cell.dataset.cellIndex = cellIndex;
        cell.dataset.columnIndex = columnIndex;

        if (me.cellRenderer) {
          me.cellRenderer({
            cell,
            date,
            day,
            cellIndex,
            columnIndex,
            visibleColumnIndex: me.visibleDayColumnIndex[day],
            week
          });
        } else {
          cell.innerHTML = date.getDate();
        }

        cellIndex++;
      }

      weekIndex++;
    });

    if (me.floating) {
      // Must realign because content change might change dimensions
      if (!me.isAligning) {
        me.realign();
      }
    }

    me.refreshCount = (me.refreshCount || 0) + 1;
    /**
     * Fires when this CalendarPanel refreshes.
     * @event refresh
     */

    me.trigger('refresh');
  }

  isDisabledDate(date) {
    const day = date.getDay(),
          disabledDates = this.disabledDates;

    if (this.disableWeekends && (day === 0 || day === 6)) {
      return true;
    }

    if (disabledDates) {
      if (typeof disabledDates === 'function') {
        return disabledDates(date);
      }

      if (Array.isArray(disabledDates)) {
        date = DateHelper.clearTime(date, true);
        return disabledDates.some(d => !(DateHelper.clearTime(d, true) - date));
      }
    }
  }

  get bodyConfig() {
    const result = super.bodyConfig,
          weeksContainerChildren = [];
    result.children = [{
      tag: 'div',
      className: 'b-calendar-row b-calendar-weekdays',
      reference: 'weekdaysHeader',
      children: this.dayNameCells
    }, {
      // `notranslate` prevents google translate messing up the DOM, https://github.com/facebook/react/issues/11538
      className: 'b-weeks-container notranslate',
      reference: 'weeksElement',
      children: weeksContainerChildren
    }];

    for (let i = 0; i < 6; i++) {
      const weekRow = {
        className: 'b-calendar-row b-calendar-week',
        children: [{// the week number cell
        }, {
          className: 'b-calendar-days',
          children: []
        }]
      }; // Generate cells seven dys

      for (let j = 0; j < 7; j++) {
        weekRow.children[1].children.push({});
      }

      weeksContainerChildren.push(weekRow);
    }

    return result;
  }

  get dayNameCells() {
    const me = this,
          weekNumberHeader = document.createElement('div'),
          result = [weekNumberHeader];
    me.weekdayCells = [];
    weekNumberHeader.className = 'b-week-number-cell';

    for (let i = 0; i < 7; i++) {
      const cell = document.createElement('div');
      result.push(cell);
      me.weekdayCells.push(cell);
    }

    return result;
  }

  get firstVisibleDate() {
    for (const date = this.month.startDate;; date.setDate(date.getDate() + 1)) {
      if (!this.hideNonWorkingDays || !this.nonWorkingDays[date.getDay()]) {
        return date;
      }
    }
  }

  getCell(date) {
    if (!(typeof date === 'string')) {
      date = DateHelper.makeKey(date);
    }

    return this.weeksElement.querySelector(`[data-date="${date}"]`);
  }

  onTipOverCell({
    source: tip,
    target
  }) {
    tip.date = DateHelper.parseKey(target.dataset.date);
  }

  updateLocalization() {
    this.updateDayNameFormat();
    this.updateWeekStartDay(this.weekStartDay);
    super.updateLocalization();
  }

} // Register this widget type with its Factory

CalendarPanel.initClass();
CalendarPanel._$name = 'CalendarPanel';

/**
 * @module Core/widget/Checkbox
 */

/**
 * Checkbox field, wraps <code>&lt;input type="checkbox"&gt;</code>.
 * Color can be specified and you can optionally configure {@link #config-text}
 * to display in a label to the right of the checkbox in addition to a standard
 * field {@link #config-label}.
 *
 * {@inlineexample widget/Checkbox.js vertical}
 *
 * This field can be used as an {@link Grid.column.Column#config-editor} for the {@link Grid.column.Column}.
 *
 * @extends Core/widget/Field
 * @classType checkbox
 */

class Checkbox extends Field {
  //region Config
  static get $name() {
    return 'Checkbox';
  } // Factoryable type name

  static get type() {
    return 'checkbox';
  } // Factoryable type alias

  static get alias() {
    return 'check';
  }

  static get configurable() {
    return {
      inputType: 'checkbox',

      /**
       * Get/set label
       * @member {String} name
       */

      /**
       * Text to display on checkbox label
       * @config {String}
       */
      text: '',

      /**
       * The checked state. The same as `value`.
       * @config {Boolean} checked
       */

      /**
       * Checkbox color, must have match in css
       * @config {String}
       */
      color: null,

      /**
       * Get/set value
       * @member {String} value
       */

      /**
       * Sets input fields value attribute
       * @config {String}
       */
      value: '',
      toggleGroup: null,
      defaultBindProperty: 'value',
      localizableProperties: ['label', 'text']
    };
  } //endregion
  //region Init

  construct(config) {
    // Convert checked to value so that initializing getter can read it if requested prior to trying to set it.
    if ('checked' in config) {
      config = ObjectHelper.assign({}, config); // copy inherited properties unlike Object.assign()

      config.value = config.checked;
      delete config.checked;
    }

    super.construct(config);
    this.syncHasText();
  } // Implementation needed at this level because it has two inner elements in its inputWrap

  get innerElements() {
    return [this.inputElement, {
      tag: 'label',
      class: 'b-checkbox-label',
      for: `${this.id}-input`,
      reference: 'textLabel',
      html: this.text || ''
    }];
  }

  get inputElement() {
    const me = this,
          config = super.inputElement;

    if (me.toggleGroup) {
      config.dataset = {
        group: me.toggleGroup
      };
    }

    return config;
  } //endregion
  //region Toggle

  /**
   * Get/set checked state. Equivalent to `value` config.
   * @property {Boolean}
   */

  get checked() {
    return this.value;
  }

  set checked(value) {
    this.value = value;
  }

  syncHasText() {
    this.element.classList[this.text ? 'add' : 'remove']('b-text');
  }

  updateText(value) {
    if (this.textLabel) {
      this.syncHasText();
      this.textLabel.innerHTML = value;
    }
  }

  changeValue(value) {
    return value === 'false' ? false : Boolean(value);
  }

  updateValue(value) {
    const me = this; // Only do action if change needed.

    if (!me.inputting) {
      me.inputting = true;
      me.input.checked = value;
      me.inputting = false;
      me.uncheckToggleGroupMembers(); // The change event does not fire on programmatic change of input.

      if (!me.isConfiguring) {
        me.triggerChange(false);
      }
    }
  }

  get inputValueAttr() {
    return 'checked';
  }

  updateColor(value, was) {
    const classes = this.element.classList;

    if (was) {
      classes.remove(was);
    }

    if (value) {
      classes.add(value);
    }
  }

  getToggleGroupMembers() {
    const me = this,
          {
      checked,
      toggleGroup,
      input: checkedElement
    } = me,
          result = [];

    if (checked && toggleGroup) {
      DomHelper.forEachSelector(`input[type=checkbox][data-group=${toggleGroup}]`, inputEl => {
        if (inputEl !== checkedElement) {
          const partnerCheckbox = Widget.fromElement(inputEl);
          partnerCheckbox && result.push(partnerCheckbox);
        }
      });
    }

    return result;
  }

  uncheckToggleGroupMembers() {
    if (this.checked && this.toggleGroup) {
      this.getToggleGroupMembers().forEach(widget => widget.checked = false);
    }
  }

  updateReadOnly(readOnly) {
    this.input.disabled = readOnly; // Field and Widget have a say too. Widget adds the class and fires the event

    super.updateReadOnly(readOnly);
  }
  /**
   * Check the box
   */

  check() {
    this.checked = true;
  }
  /**
   * Uncheck the box
   */

  uncheck() {
    this.checked = false;
  }
  /**
   * Toggle checked state. If you want to force a certain state, assign to {@link #property-checked} instead.
   */

  toggle() {
    this.checked = !this.checked;
  } //endregion
  //region Events

  /**
   * Triggers events when user toggles the checkbox
   * @fires beforeChange
   * @fires change
   * @fires action
   * @private
   */

  internalOnChange(event) {
    const me = this;

    if (!me.inputting) {
      me.inputting = true;
      me.value = me.input.checked;
      me.triggerChange(true);
      me.inputting = false;
    }
  }
  /**
   * Triggers events when checked state is changed
   * @fires beforeChange
   * @fires change
   * @fires action
   * @private
   */

  triggerChange(userAction) {
    const me = this,
          {
      checked
    } = me.input;
    /**
     * Fired before checkbox is toggled. Returning false from a listener prevents the checkbox from being toggled.
     * @event beforeChange
     * @preventable
     * @param {Core.widget.Checkbox} source Checkbox
     * @param {Boolean} checked Checked or not
     */

    /**
     * Fired when checkbox is toggled
     * @event change
     * @param {Core.widget.Checkbox} source Checkbox
     * @param {Boolean} checked Checked or not
     */
    // Prevent uncheck if this checkbox is part of a toggleGroup (radio-button mode) ..also ensure the group has
    // visible active members

    const prevented = !checked && userAction && me.toggleGroup && me.getToggleGroupMembers().filter(widget => widget.isVisible && !widget.disabled).length || // Since Widget has Events mixed in configured with 'callOnFunctions' this will also call onBeforeChange,
    // onChange and onAction
    !me.callPreventable('change', {
      checked,
      value: checked,
      userAction,
      valid: true
    }, eventObject => {
      me.triggerFieldChange(eventObject, false);

      if (userAction) {
        me.uncheckToggleGroupMembers();
      }
      /**
      * User performed the default action (toggled the checkbox)
      * @event action
      * @param {Core.widget.Checkbox} source Checkbox
      * @param {Boolean} checked Checked or not
      */

      me.trigger('action', eventObject);
      return true;
    }); // If prevented need to rollback the checkbox input

    if (prevented) {
      // Input change is not preventable, so need to revert the changes
      // The change event does not fire on programmatic change of input, so no need to suspend
      me.input.checked = me._value = !checked;
    }
  } //endregion

} // Register this widget type with its Factory

Checkbox.initClass();
Checkbox._$name = 'Checkbox';

/**
 * @module Core/widget/layout/Fit
 */

/**
 * A helper class for containers which must manage a single child widget which must fit the container's
 * {@link Core.widget.Widget#property-contentElement contentElement}.
 */

class Fit extends Layout {
  static get $name() {
    return 'Fit';
  }

  static get configurable() {
    return {
      containerCls: 'b-fit-container',
      itemCls: 'b-fit-item'
    };
  }

} // Layouts must register themselves so that the static layout instantiation
// in Layout knows what to do with layout type names

Fit._$name = 'Fit';
Layout.registerLayout(Fit);

/**
 * @module Core/widget/Editor
 */

/**
 * Displays an input field, optionally editing a field of a record at a particular position.
 *
 * Offers events to signal edit completion upon `ENTER` or focus loss (if configured to do so),
 * or edit cancellation on `ESC`, or focus loss if configured that way.
 * @extends Core/widget/Container
 *
 * @classType Editor
 */

class Editor extends Container {
  //region Config
  static get $name() {
    return 'Editor';
  } // Factoryable type name

  static get type() {
    return 'editor';
  }

  static get configurable() {
    return {
      positioned: true,
      hidden: true,
      layout: 'fit',
      align: {
        align: 't0-t0',
        offset: [0, 0]
      },

      /**
       * A config object, or the `type` string of the widget (usually a {@link Core.widget.Field Field} subclass,
       * i.e. {@link Core.widget.TextField TextField}) which this editor will encapsulate.
       * @config {Object|String}
       * @default
       */
      inputField: 'textfield',

      /**
       * What action should be taken when focus moves out of the editor, either by `TAB` or clicking outside.
       * May be `'complete'` or `'cancel`'. Any other value results in no action being taken upon focus leaving the editor
       * leaving the application to listen for the {@link #event-focusOut focusOut} event.
       * @config {String}
       * @default
       */
      blurAction: 'complete',

      /**
       * The name of the `key` which completes the edit.
       *
       * See https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent/key/Key_Values for key names.
       * @config {String}
       * @default
       */
      completeKey: 'Enter',

      /**
       * The name of the `key` which cancels the edit.
       *
       * See https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent/key/Key_Values for key names.
       * @config {String}
       * @default
       */
      cancelKey: 'Escape',

      /**
       * How to handle a request to complete the edit if the field is invalid. There are three choices:
       *  - `block` The default. The edit is not exited, the field remains focused.
       *  - `allow` Allow the edit to be completed.
       *  - `revert` The field value is reverted and the edit is completed.
       * @config {String}
       * @default
       */
      invalidAction: 'block',

      /**
       * Configure as `true` to have editing complete as soon as the field fires its `change` event.
       * @config {Boolean}
       * @default false
       */
      completeOnChange: null
    };
  } //endregion
  //region Events

  /**
   * Fired before the editor is shown to start an edit operation. Returning `false` from a handler vetoes the edit operation.
   * @event beforeStart
   * @param {Object} value - The value to be edited.
   * @preventable
   */

  /**
   * Fired when an edit operation has begun.
   * @event start
   * @param {Object} value - The starting value of the field.
   * @param {Core.widget.Editor} source - The Editor that triggered the event.
   */

  /**
   * Fired when an edit completion has been requested, either by `ENTER`, or focus loss (if configured to complete on blur).
   * The completion may be vetoed, in which case, focus is moved back into the editor.
   * @event beforeComplete
   * @param {Object} oldValue - The original value.
   * @param {Object} value - The new value.
   * @param {Core.widget.Editor} source - The Editor that triggered the event.
   * @param {Function} [finalize] An async function may be injected into this property
   * which performs asynchronous finalization tasks such as complex validation of confirmation. The
   * value `true` or `false` must be returned.
   * @param {Object} [finalize.context] An object describing the editing context upon requested completion of the edit.
   * @preventable
   */

  /**
   * Edit has been completed, and any associated record or element has been updated.
   * @event complete
   * @param {Object} oldValue - The original value.
   * @param {Object} value - The new value.
   * @param {Core.widget.Editor} source - The Editor that triggered the event.
   */

  /**
   * Fired when cancellation has been requested, either by `ESC`, or focus loss (if configured to cancel on blur).
   * The cancellation may be vetoed, in which case, focus is moved back into the editor.
   * @event beforeCancel
   * @param {Object} oldValue - The original value.
   * @param {Object} value - The new value.
   * @param {Core.widget.Editor} source - The Editor that triggered the event.
   * @preventable
   */

  /**
   * Edit has been canceled without updating the associated record or element.
   * @event cancel
   * @param {Object} oldValue - The original value.
   * @param {Object} value - The value of the field.
   * @param {Core.widget.Editor} source - The Editor that triggered the event.
   */

  /**
   * Fire to relay a `keypress` event from the field.
   * @event keypress
   * @param {Event} event - The key event.
   */
  //endregion

  afterConfigure() {
    const me = this;
    super.afterConfigure();
    me.onTargetSizeChange = me.onTargetSizeChange.bind(me);

    if (me.completeKey || me.cancelKey) {
      EventHelper.on({
        element: me.element,
        keydown: 'onKeyDown',
        thisObj: me
      });
    }

    me.on({
      hide: 'afterEditorHide',
      thisObj: me
    });
    LocaleManagerSingleton.on({
      locale: 'onLocaleChange',
      thisObj: me
    });
  }

  onLocaleChange() {
    const {
      inputField
    } = this;

    if (inputField && !inputField.isDestroyed) {
      // All Field subclasses have this method, but if `inputField` is a custom widget,
      // `syncInputFieldValue` has to be implemented
      if (inputField.syncInputFieldValue) {
        inputField.syncInputFieldValue(true);
      }
    }
  }
  /**
   * Start editing
   * @param {Object} editObject An object containing details about what to edit.
   * @param {HTMLElement|Core.helper.util.Rectangle} editObject.target the element or Rectangle to align to.
   * @param {String} [editObject.align=t0-t0] How to align to the target.
   * @param {Boolean} [editObject.matchSize=true] Match editor size to target size.
   * @param {Boolean} [editObject.matchFont=true] Match editor's font-size size to target's font-size.
   * @param {Core.data.Model} [editObject.record] The record to edit.
   * @param {String} [editObject.field] The field name in the record to edit. This defaults to the `name` of the {@link #config-inputField}.
   * Also if record has method set + capitalized field, method will be called, e.g. if record has method named
   * `setFoobar` and this config is `foobar`, then instead of `record.foobar = value`, `record.setFoobar(value)` will be called.
   * @param {Object} [editObject.value] The value to edit.
   * @param {Boolean} [editObject.focus=true] Focus the field.
   * @param {Boolean} [editObject.fitTargetContent] Pass `true` to allow the Editor to expand beyond the
   * width of its target element if its content overflows horizontally. This is useful if the editor has
   * triggers to display, such as a combo.
   */

  startEdit({
    target,
    align = this.align,
    hideTarget = false,
    matchSize = true,
    matchFont = true,
    fitTargetContent = false,
    value,
    record,
    field = this.inputField.name,
    focus = true
  }) {
    const me = this,
          {
      inputField
    } = me,
          targetFontSize = DomHelper.getStyleValue(target, 'font-size'),
          targetFontFamily = DomHelper.getStyleValue(target, 'font-family');

    if (me.trigger('beforeStart', {
      value
    }) !== false) {
      var _inputField$value$;

      if (record && field && value === undefined) {
        me.record = record;
        me.dataField = field;
        value = record[field];
      }

      if (matchSize) {
        if (target instanceof HTMLElement) {
          me.updateSize(target);
        } // If we are editing and sizing based on an element,
        // we need to update editor size if grid cell size changes
        // Removed in 'hide' listener

        if (target instanceof HTMLElement) {
          ResizeMonitor.addResizeListener(target, me.onTargetSizeChange);
        }
      }

      if (inputField.input) {
        if (matchFont) {
          inputField.input.style.fontSize = targetFontSize;
          inputField.input.style.fontFamily = targetFontFamily;
        } else {
          inputField.input.style.fontSize = inputField.input.style.fontFamily = '';
        }
      }

      me._isSettingValues = true; // In case our finalize code set it to invalid, start it clear of errors.

      if (inputField.clearError) {
        inputField.clearError();
      }

      inputField.value = value;
      me._isSettingValues = false;
      me.showBy({
        target,
        ...align
      });

      if (fitTargetContent) {
        // Input doesn't fit, so widen it
        const overflow = inputField.input.scrollWidth - inputField.input.clientWidth;

        if (overflow > 0) {
          me.width += overflow + DomHelper.scrollBarWidth;
        }
      }

      if (focus && me.inputField.focus) {
        me.inputField.focus();
      }

      if (target.nodeType === 1) {
        target.classList.add('b-editing');

        if (hideTarget) {
          target.classList.add('b-hide-visibility');
        }
      }

      me.editing = true; // Passed value may have been '10/06/2019', send the live field value to startedit

      me.trigger('start', {
        value: inputField.value
      });

      if (Array.isArray(inputField.value) && inputField.editingRecords && (_inputField$value$ = inputField.value[0]) !== null && _inputField$value$ !== void 0 && _inputField$value$.isModel) {
        // If this editor is editing model instances, save a cloned copy in case fields
        // are changed
        me.oldValue = inputField.value.map(record => record.copy(record.id));
      } else {
        me.oldValue = inputField.value; // If the value from the value getter is an array, we must clone it because
        // if it's the same *instance*, the ObjectHelper.isEqual test in completeEdit
        // will find that there are no changes.

        if (Array.isArray(me.oldValue)) {
          me.oldValue = me.oldValue.slice();
        }
      } // The initialValue is what the revertOnEscape uses by preference before it uses its valueOnFocus.
      // In an Editor, it can focus in and out but still need that correct initial value.

      inputField.initialValue = me.oldValue;
      return true;
    }

    return false;
  }

  refreshEdit() {
    if (this.isVisible) {
      const {
        record,
        dataField,
        inputField
      } = this;

      if (record && dataField) {
        const value = record[dataField]; // Only update the field if the value has changed

        if (!ObjectHelper.isEqual(inputField.value, value)) {
          inputField.value = value;
        }
      }
    }
  }

  finishEdit() {
    const {
      target
    } = this.lastAlignSpec;

    if (target.nodeType === 1) {
      target.classList.remove('b-editing');
      target.classList.remove('b-hide-visibility');
    }

    this.editing = false;
  }

  onKeyDown(event) {
    const me = this;

    switch (event.key) {
      case me.completeKey:
        me.completeEdit();
        event.stopImmediatePropagation();
        break;

      case me.cancelKey:
        me.cancelEdit();
        event.stopImmediatePropagation();
        break;
    }

    me.trigger('keydown', {
      event
    });
  }

  onFocusOut(event) {
    super.onFocusOut(event);

    if (!this.isFinishing && this.editing) {
      const method = this[`${this.blurAction}Edit`];

      if (method) {
        method.call(this);
      }
    }
  }
  /**
   * Complete the edit, and, if associated with a record, update the record if possible.
   * If editing is completed, the editor is hidden.
   *
   * If the field is invalid, the `{@link #config-invalidAction}` config is used to decide
   * upon the course of action.
   *
   * If a {@link #event-beforeComplete} handler returns `false` then editing is not completed.
   *
   * If the field's valus has not been changed, then editing is terminated through {@link #function-cancelEdit}.
   *
   * @returns `true` if editing ceased, `false` if the editor is still active.
   * @async
   */

  async completeEdit(finalize) {
    const me = this,
          {
      inputField,
      oldValue,
      record
    } = me,
          invalidAction = inputField.invalidAction || (inputField.allowInvalid === false ? 'block' : me.invalidAction),
          {
      value
    } = inputField; // If we are not editing, we should manipulated the field or not fire any events

    if (!me.isVisible) {
      return;
    } // If we're configured not to allow invalid values, refocus the field in case complete was triggered by focusout.

    if (!inputField.isValid) {
      if (invalidAction === 'block') {
        inputField.focus && inputField.focus();
        return false;
      } else if (invalidAction === 'revert') {
        me.cancelEdit();
        return true;
      }
    } // No change means a cancel.
    else if (ObjectHelper.isEqual(value, oldValue)) {
        me.cancelEdit();
        return true;
      } // Allow veto of the completion
      else {
          const context = {
            inputField,
            record,
            value,
            oldValue
          };

          if (me.trigger('beforeComplete', context) === false) {
            inputField.focus && inputField.focus();
          } else {
            // CellEdit#onEditorBeforeComplete injects editorContext into the basic context
            if (!finalize) {
              finalize = context.finalize || context.editorContext && context.editorContext.finalize;
            } // Allow async finalization of the editing, implementer may want to show a confirmation popup etc

            if (finalize) {
              let result = await finalize(context);

              if (result === true) {
                me.onEditComplete();
              } else {
                if (inputField.setError) {
                  const error = result || inputField.invalidValueError,
                        clearError = () => {
                    listeners();
                    inputField.clearError(error);
                  },
                        listeners = inputField.on({
                    change: clearError,
                    input: clearError
                  }); // Mark as invalid. Because this is decided upon without the knowledge
                  // of the field, this state will be rescinded upon the next change of
                  // input field.

                  inputField.setError(error);
                }

                if (invalidAction === 'block') {
                  inputField.focus && inputField.focus();
                } else if (invalidAction === 'revert') {
                  inputField.value = oldValue;
                  result = true;
                }

                result = false;
              }

              return result;
            } // Successful completion
            else {
                me.onEditComplete();
                return true;
              }
          }
        }

    return false;
  }
  /**
   * Cancel the edit and hide the editor.
   */

  cancelEdit() {
    const me = this,
          {
      inputField,
      oldValue
    } = me,
          {
      value
    } = inputField;

    if (me.isVisible && !me.isFinishing && me.trigger('beforeCancel', {
      value,
      oldValue
    }) !== false) {
      // Hiding must not trigger our blurAction
      me.isFinishing = true;
      me.hide();
      me.trigger('cancel', {
        value,
        oldValue
      });
      me.finishEdit();
      me.isFinishing = false;
    }
  } // Handle updating what needs to be updated.

  onEditComplete() {
    const me = this,
          {
      record,
      dataField,
      inputField,
      oldValue
    } = me,
          {
      value
    } = inputField;

    if (!me.isFinishing) {
      // Hiding must not trigger our blurAction
      me.isFinishing = true;
      me.hide();

      if (record) {
        const setterName = `set${StringHelper.capitalize(dataField)}`;

        if (record[setterName]) {
          record[setterName](value);
        } else {
          record[dataField] = value;
        }
      }

      me.trigger('complete', {
        value,
        oldValue
      });
      me.finishEdit();
      me.isFinishing = false;
    }
  }

  doDestroy() {
    if (this.createdInputField) {
      this.inputField.destroy();
    }

    super.doDestroy();
  }

  set owner(owner) {
    this._owner = owner;
  } // This is a positioned widget appended to a Widget's contentElement. It may have no owner link.
  // Grab the owner by finding what widget it is inside.

  get owner() {
    return this._owner || Widget.fromElement(this.element.parentNode);
  }

  changeInputField(inputField, oldInputField) {
    const me = this;

    if (oldInputField) {
      oldInputField.destroy();
    }

    if (typeof inputField === 'string') {
      inputField = {
        type: inputField
      };
    }

    if (!(inputField instanceof Widget)) {
      inputField = Widget.create(inputField);
      me.createdInputField = true; // So we know we can destroy it
    }

    if (me.completeOnChange) {
      inputField.on({
        change: 'onInputFieldChange',
        thisObj: me
      });
    }

    inputField.parent = me;
    return inputField;
  }

  updateInputField(inputField) {
    this.removeAll();
    this.add(inputField);
  }

  get inputField() {
    return this.items[0];
  }

  onInputFieldChange() {
    if (this.containsFocus) {
      this.completeEdit();
    }
  }

  onTargetSizeChange(resizedElement, oldRect, newRect) {
    if (oldRect && newRect) {
      this.updateSize(resizedElement);
    }
  }

  updateSize(targetEl) {
    const rect = targetEl.getBoundingClientRect();
    this.width = rect.width - this.align.offset[0];
    this.height = rect.height;
  }

  afterEditorHide() {
    ResizeMonitor.removeResizeListener(this.lastAlignSpec.target, this.onTargetSizeChange);
  }

} // Register this widget type with its Factory

Editor.initClass();
Editor._$name = 'Editor';

/**
 * @module Core/widget/DatePicker
 */

/**
 * A Panel which can display a month of date cells, which navigates between the cells,
 * fires events upon user selection actions, optionally navigates to other months
 * in response to UI gestures, and optionally displays information about each date cell.
 *
 * This class is used by the {@link Core.widget.DateField} class.
 *
 * @externalexample widget/DatePicker.js
 * @classtype datepicker
 * @extends Core/widget/CalendarPanel
 */

class DatePicker extends CalendarPanel {
  static get $name() {
    return 'DatePicker';
  } // Factoryable type name

  static get type() {
    return 'datepicker';
  }

  static get delayable() {
    return {
      refresh: 'raf'
    };
  }

  static get configurable() {
    return {
      /**
       * The date that the user has navigated to using the UI *prior* to setting the widget's
       * value by selecting.
       *
       * This may be changed using keyboard navigation. The {@link Core.widget.CalendarPanel#property-date} is set
       * by pressing `ENTER` when the desired date is reached.
       *
       * Programatically setting the {@link Core.widget.CalendarPanel#config-date}, or using the UI to select the date
       * by clicking it also sets the `activeDate`
       * @config {Date}
       */
      activeDate: {
        $config: {
          equal: 'date'
        },
        value: null
      }
    };
  }

  static get defaultConfig() {
    return {
      focusable: true,
      textContent: false,
      tools: {
        prevMonth: {
          align: 'start',
          cls: 'b-icon b-icon-angle-left',
          handler: 'gotoPrevMonth'
        },
        nextMonth: {
          align: 'end',
          cls: 'b-icon b-icon-angle-right',
          handler: 'gotoNextMonth'
        }
      },
      header: {
        title: '<div class="b-editable b-datepicker-month" data-reference="monthElement"></div> <div class="b-editable b-datepicker-year" data-reference="yearElement"></div>',
        titleAlign: 'center'
      },

      /**
       * The minimum selectable date. Selection of and navigtion to dates prior
       * to this date will not be possible.
       * @config {Date}
       */
      minDate: null,

      /**
       * The maximum selectable date. Selection of and navigtion to dates after
       * this date will not be possible.
       * @config {Date}
       */
      maxDate: null,

      /**
       * The class name to add to the calendar cell whose date which is outside of the
       * {@link #config-minDate}/{@link #config-maxDate} range.
       * @config {String}
       * @private
       */
      outOfRangeCls: 'b-out-of-range',

      /**
       * The class name to add to the currently focused calendar cell.
       * @config {String}
       * @private
       */
      activeCls: 'b-active-date',

      /**
       * The class name to add to selected calendar cells.
       * @config {String}
       * @private
       */
      selectedCls: 'b-selected-date',

      /**
       * By default, disabled dates cannot be navigated to, and they are skipped over
       * during keyboard navigation. Configure this as `true` to enable navigation to
       * disabled dates.
       * @config {Boolean}
       * @default
       */
      focusDisabledDates: null,

      /**
       * Configure as `true` to enable selecting a single date range by selecting a
       * start and end date. Hold "SHIFT" button to select date range.
       * @config {Boolean}
       * @default
       */
      multiSelect: false,

      /**
       * By default, the month and year show their editor on hover instead of click.
       * On a touch platform, they both show the editor on tap.
       * @config {Boolean}
       * @default
       */
      editOnHover: true,

      /**
       * By default, the month and year are editable. Configure this as `false` to prevent that.
       * @config {Boolean}
       * @default
       */
      editMonth: true
    };
  }
  /**
   * Fires when a date is selected. If {@link #config-multiSelect} is specified, this
   * will fire upon deselection and selection of dates.
   * @event selectionChange
   * @param {Date[]} selection The selected date. If {@link #config-multiSelect} is specified
   * this may be a two element array specifying start and end dates.
   * @param {Boolean} userAction This will be `true` if the change was caused by user interaction
   * as opposed to programmatic setting.
   */

  construct(config) {
    const me = this;
    me.selection = [];
    super.construct(config);
    me.element.setAttribute('aria-activedescendant', `${me.id}-active-day`);
    EventHelper.on({
      element: me.element,
      mouseover: 'onPickerMouseover',
      click: 'onPickerClick',
      thisObj: me
    });
  }

  doDestroy() {
    if (this._yearEditor) {
      this._yearEditor.destroy();
    }

    if (this._monthEditor) {
      this._monthEditor.destroy();
    }

    super.doDestroy();
  }

  get focusElement() {
    return this.element;
  }

  get childItems() {
    const result = super.childItems;

    if (this._yearEditor) {
      result.unshift(this._yearEditor);
    }

    if (this._monthEditor) {
      result.unshift(this._monthEditor);
    }

    return result;
  }

  refresh() {
    const me = this,
          sbw = DomHelper.scrollBarWidth;
    super.refresh();
    me.monthElement.innerHTML = DateHelper.format(me.month.date, 'MMMM');
    me.yearElement.innerHTML = DateHelper.format(me.month.date, 'YYYY');

    if (me.editMonth) {
      me.monthElement.style.minWidth = `calc(${me.maxMonthLength + 1}ch + ${sbw}px)`;
      me.yearElement.style.minWidth = sbw ? `calc(3ch + ${sbw}px` : '7ch';
    }
  }

  cellRenderer({
    cell,
    date
  }) {
    const me = this,
          {
      activeCls,
      selectedCls
    } = me,
          cellClassList = cell.classList;
    cell.innerHTML = date.getDate();
    cell.setAttribute('aria-label', DateHelper.format(date, 'MMMM D, YYYY'));

    if (me.isActiveDate(date)) {
      cellClassList.add(activeCls);
      cell.id = `${me.id}-active-day`;
    } else {
      cell.removeAttribute('id');
    }

    if (me.isSelectedDate(date)) {
      cellClassList.add(selectedCls);
    }

    if (me.minDate && date < me.minDate) {
      cellClassList.add(me.outOfRangeCls);
    } else if (me.maxDate && date > me.maxDate) {
      cellClassList.add(me.outOfRangeCls);
    }
  }

  onPickerMouseover(event) {
    if (this.editOnHover) {
      const editable = this.editMonth && DomHelper.up(event.target, '.b-editable');

      if (editable) {
        return this.onEditGesture(event);
      }
    }
  }

  onPickerClick(event) {
    const me = this,
          {
      target
    } = event,
          cell = DomHelper.up(target, `.${me.dayCellCls}:not(.${me.disabledCls}):not(.${me.outOfRangeCls})`),
          editable = me.editMonth && DomHelper.up(target, '.b-editable');

    if (cell) {
      return me.onCellClick(event);
    }

    if ((!me.editOnHover || DomHelper.isTouchEvent) && editable) {
      return me.onEditGesture(event);
    }

    if (me._monthEditor && !me._monthEditor.owns(event)) {
      me._monthEditor.cancelEdit();
    }

    if (me._yearEditor && !me._yearEditor.owns(event)) {
      me._yearEditor.cancelEdit();
    }
  }

  onCellClick(event) {
    this.onUIDateSelect(DateHelper.parseKey(event.target.dataset.date), event);
  }

  onEditGesture(event) {
    const me = this,
          {
      month
    } = me,
          {
      target
    } = event;

    if (target === me.monthElement) {
      me.monthEditor.startEdit({
        target,
        value: month.month,
        fitTargetContent: false,
        hideTarget: true
      });
    } else if (target === me.yearElement) {
      me.yearEditor.minWidth = `${DomHelper.scrollBarWidth + 50}px)`;
      me.yearEditor.startEdit({
        target,
        value: month.year,
        fitTargetContent: true,
        hideTarget: true
      });
    }
  }
  /**
   * Called when the user uses the UI to select the current activeDate. So ENTER when focused
   * or clicking a date cell.
   * @param {Date} date The active date to select
   * @param {Event} event the intigating event, either a `click` event or a `keydown` event.
   * @internal
   */

  onUIDateSelect(date, event) {
    const me = this,
          {
      lastClickedDate,
      selection
    } = me;
    me.activeDate = date;
    me.lastClickedDate = date;

    if (!me.isDisabledDate(date)) {
      me.activatingEvent = event; // Handle multi selecting.
      // * single contiguous date range, eg: an event start and end
      // * multiple discontiguous ranges

      if (me.multiSelect) {
        if (me.multiRange) ; else if (!lastClickedDate || date.getTime() !== lastClickedDate.getTime()) {
          if (lastClickedDate && event.shiftKey) {
            selection[1] = date;
            selection.sort();
          } else {
            selection.length = 0;
            selection[0] = date;
          }

          me.trigger('selectionChange', {
            selection,
            userAction: Boolean(event)
          });
        }
      } else {
        if (!me.value || me.value.getTime() !== date.getTime()) {
          me.value = date;
        } else if (me.floating) {
          me.hide();
        }
      }

      me.activatingEvent = null;
    } // Ensure the b-selected-date class is on the correct cell(s).

    me.refresh();
  }

  onInternalKeyDown(keyEvent) {
    const me = this,
          keyName = keyEvent.key.trim() || keyEvent.code,
          activeDate = me.activeDate,
          newDate = new Date(activeDate);

    if (keyName === 'Escape' && me.floating) {
      return me.hide();
    } // Only navigate if not focused on one of our child widgets.
    // We have a prevMonth and nextMonth tool and possibly month and year pickers.

    if (activeDate && keyEvent.target === me.focusElement) {
      do {
        switch (keyName) {
          case 'ArrowLeft':
            // Disable browser use of this key.
            // Ctrl+ArrowLeft navigates back.
            // ArrowLeft scrolls if there is horizontal scroll.
            keyEvent.preventDefault();

            if (keyEvent.ctrlKey) {
              newDate.setMonth(newDate.getMonth() - 1);
            } else {
              newDate.setDate(newDate.getDate() - 1);
            }

            break;

          case 'ArrowUp':
            // Disable browser use of this key.
            // ArrowUp scrolls if there is vertical scroll.
            keyEvent.preventDefault();
            newDate.setDate(newDate.getDate() - 7);
            break;

          case 'ArrowRight':
            // Disable browser use of this key.
            // Ctrl+ArrowRight navigates forwards.
            // ArrowRight scrolls if there is horizontal scroll.
            keyEvent.preventDefault();

            if (keyEvent.ctrlKey) {
              newDate.setMonth(newDate.getMonth() + 1);
            } else {
              newDate.setDate(newDate.getDate() + 1);
            }

            break;

          case 'ArrowDown':
            // Disable browser use of this key.
            // ArrowDown scrolls if there is vertical scroll.
            keyEvent.preventDefault();
            newDate.setDate(newDate.getDate() + 7);
            break;

          case 'Enter':
            return me.onUIDateSelect(activeDate, keyEvent);
        }
      } while (me.isDisabledDate(newDate) && !me.focusDisabledDates); // Don't allow navigation to outside of date bounds.

      if (me.minDate && newDate < me.minDate) {
        return;
      }

      if (me.maxDate && newDate > me.maxDate) {
        return;
      }

      me.activeDate = newDate;
    }
  }

  set minDate(minDate) {
    this._minDate = minDate ? this.changeDate(minDate) : null;
    this.refresh();
  }

  get minDate() {
    return this._minDate;
  }

  set maxDate(maxDate) {
    this._maxDate = maxDate ? this.changeDate(maxDate) : null;
    this.refresh();
  }

  get maxDate() {
    return this._maxDate;
  }

  updateDate(date) {
    super.updateDate(date);
    this.activeDate = date;
  }

  changeActiveDate(activeDate) {
    activeDate = activeDate ? this.changeDate(activeDate) : this.date || (this.date = DateHelper.clearTime(new Date()));

    if (isNaN(activeDate)) {
      throw new Error('DatePicker date ingestion must be passed a Date, or a YYYY-MM-DD date string');
    }

    return activeDate;
  }

  updateActiveDate(activeDate) {
    // Month's date setter protects it from non-changes.
    this.month.date = activeDate; // Must refresh so that the active cell gets a refresh.

    this.refresh();
  }

  set value(value) {
    const me = this,
          {
      selection
    } = me;
    let changed;

    if (value) {
      value = me.changeDate(value, me.value); // Undefined return value means no change

      if (value === undefined) {
        return;
      }

      if (!me.value || value.getTime() !== me.value.getTime()) {
        selection.length = 0;
        selection[0] = value;
        changed = true;
      }

      me.date = value;
    } else {
      changed = selection.length;
      selection.length = 0; // Clearing the value - go to today's calendar

      me.date = new Date();
    }

    if (changed) {
      me.trigger('selectionChange', {
        selection,
        userAction: Boolean(me.activatingEvent)
      });
    }
  }

  get value() {
    return this.selection[this.selection.length - 1];
  }

  gotoPrevMonth() {
    const {
      activeDate
    } = this,
          // Navigate from the activeDate if the activeDate is in the UI.
    baseDate = activeDate && this.getCell(activeDate) ? activeDate : this.date,
          newDate = new Date(baseDate);
    newDate.setMonth(newDate.getMonth() - 1);
    this.date = newDate;
  }

  gotoNextMonth() {
    const {
      activeDate
    } = this,
          // Navigate from the activeDate if the activeDate is in the UI.
    baseDate = activeDate && this.getCell(activeDate) ? activeDate : this.date,
          newDate = new Date(baseDate);
    newDate.setMonth(newDate.getMonth() + 1);
    this.date = newDate;
  }

  isActiveDate(date) {
    return this.activeDate && this.changeDate(date).getTime() === this.activeDate.getTime();
  }

  isSelectedDate(date) {
    return this.selection.some(d => d.getTime() === date.getTime());
  }

  get monthEditor() {
    const me = this;

    if (!me._monthEditor) {
      me._monthEditor = new Editor({
        owner: me,
        appendTo: me.element,
        inputField: me.monthInput = new Combo({
          editable: false,
          autoExpand: !me.editOnHover,
          items: me.monthItems,
          highlightExternalChange: false,
          picker: {
            align: {
              align: 't0-b0'
            },
            cls: 'b-month-picker-list',
            scrollable: {
              overflowX: false
            }
          }
        }),
        completeOnChange: true,
        listeners: {
          complete: 'onMonthPicked',
          beforeCancel: 'onBeforeEditorCancel',
          thisObj: me
        }
      });
    }

    return me._monthEditor;
  }

  onMonthPicked({
    value,
    oldValue
  }) {
    this.activeDate = DateHelper.add(this.activeDate, value - oldValue, 'month');
  }

  get yearEditor() {
    const me = this;

    if (!me._yearEditor) {
      me._yearEditor = new Editor({
        owner: me,
        appendTo: me.element,
        inputField: me.yearInput = new Combo({
          editable: false,
          autoExpand: !me.editOnHover,
          items: me.yearItems,
          highlightExternalChange: false,
          picker: {
            cls: 'b-year-picker-list',
            scrollable: {
              overflowX: false
            }
          }
        }),
        completeOnChange: true,
        listeners: {
          complete: 'onYearPicked',
          beforeCancel: 'onBeforeEditorCancel',
          thisObj: me
        }
      });
    }

    return me._yearEditor;
  }

  onYearPicked({
    value
  }) {
    const newDate = new Date(this.activeDate);
    newDate.setFullYear(value);
    this.activeDate = newDate;
  }

  onBeforeEditorCancel({
    source
  }) {
    // Always move focus to this to cancel the edit so that it doesn't try to find a revert target.
    if (source.containsFocus) {
      this.element.focus();
      return false;
    }
  }

  get monthItems() {
    return DateHelper.getMonthNames().map((m, i) => [i, m]);
  }

  get yearItems() {
    const result = [],
          middle = new Date().getFullYear();

    for (let y = middle - 20; y < middle + 21; y++) {
      result.push(y);
    }

    return result;
  }

  get maxMonthLength() {
    if (!this._maxMonthLength) {
      this._maxMonthLength = 0;

      for (let i = 0, months = this.monthItems; i < 12; i++) {
        this._maxMonthLength = Math.max(this._maxMonthLength, months[i][1].length);
      }
    }

    return this._maxMonthLength;
  }

  updateLocalization() {
    if (this._monthEditor) {
      this._monthEditor.doDestroy();

      this._monthEditor = null;
    }

    if (this._yearEditor) {
      this._yearEditor.doDestroy();

      this._yearEditor = null;
    }

    this._maxMonthLength = 0;
    super.updateLocalization();
  }

} // Register this widget type with its Factory

DatePicker.initClass();
DatePicker._$name = 'DatePicker';

//TODO: picker icon (calendar) should show day number
/**
 * @module Core/widget/DateField
 */

/**
 * Date field widget (text field + date picker).
 *
 * This field can be used as an {@link Grid.column.Column#config-editor editor} for the {@link Grid.column.Column Column}.
 * It is used as the default editor for the {@link Grid.column.DateColumn DateColumn}.
 *
 * @extends Core/widget/PickerField
 *
 * @example
 * // minimal DateField config with date format specified
 * let dateField = new DateField({
 *   format: 'YYMMDD'
 * });
 *
 * @classType datefield
 * @externalexample widget/DateField.js
 */

class DateField extends PickerField {
  //region Config
  static get $name() {
    return 'DateField';
  } // Factoryable type name

  static get type() {
    return 'datefield';
  } // Factoryable type alias

  static get alias() {
    return 'date';
  }

  static get configurable() {
    return {
      /**
       * Get/Set format for date displayed in field (see {@link Core.helper.DateHelper#function-format-static}
       * for formatting options).
       * @property {String}
       * @name format
       */

      /**
       * Format for date displayed in field. Defaults to using long date format, as defined by current locale (`L`)
       * @config {String}
       * @default
       */
      format: 'L',
      // same for all languages
      fallbackFormat: 'YYYY-MM-DD',
      timeFormat: 'HH:mm:ss:SSS',

      /**
       * A flag which indicates what time should be used for selected date.
       * `false` by default which means time is reset to midnight.
       *
       * Possible options are:
       * - `false` to reset time to midnight
       * - `true` to keep original time value
       * - `'17:00'` a string which is parsed automatically
       * - `new Date(2020, 0, 1, 17)` a date object to copy time from
       * - `'entered'` to keep time value entered by user (in case {@link #config-format} includes time info)
       *
       * @config {Boolean|Date|String}
       * @default
       */
      keepTime: false,

      /**
       * Format for date in the {@link #config-picker}. Uses localized format per default
       * @config {String}
       */
      pickerFormat: null,
      triggers: {
        expand: {
          cls: 'b-icon-calendar',
          handler: 'onTriggerClick',
          weight: 200
        },
        back: {
          cls: 'b-icon b-icon-angle-left b-step-trigger',
          handler: 'onBackClick',
          align: 'start',
          weight: 100
        },
        forward: {
          cls: 'b-icon b-icon-angle-right b-step-trigger',
          handler: 'onForwardClick',
          align: 'end',
          weight: 100
        }
      },
      // An optional extra CSS class to add to the picker container element
      calendarContainerCls: '',

      /**
       * Get/set min value, which can be a Date or a string. If a string is specified, it will be converted using
       * the specified {@link #config-format}.
       * @property {String|Date}
       * @name min
       */

      /**
       * Min value
       * @config {String|Date}
       */
      min: null,

      /**
       * Get/set max value, which can be a Date or a string. If a string is specified, it will be converted using
       * the specified {@link #config-format}.
       * @property {String|Date}
       * @name max
       */

      /**
       * Max value
       * @config {String|Date}
       */
      max: null,

      /**
       * The `step` property may be set in object form specifying two properties, `magnitude`, a Number, and
       * `unit`, a String.
       *
       * If a Number is passed, the steps's current unit is used (or `day` if no current step set) and just the
       * magnitude is changed.
       *
       * If a String is passed, it is parsed by {@link Core.helper.DateHelper#function-parseDuration-static}, for
       * example `'1d'`, `'1 d'`, `'1 day'`, or `'1 day'`.
       *
       * Upon read, the value is always returned in object form containing `magnitude` and `unit`.
       * @property {String|Number|Object}
       * @name step
       */

      /**
       * Time increment duration value. If specified, `forward` and `back` triggers are displayed.
       * The value is taken to be a string consisting of the numeric magnitude and the units.
       * The units may be a recognised unit abbreviation of this locale or the full local unit name.
       * For example `'1d'` or `'1w'` or `'1 week'`. This may be specified as an object containing
       * two properties: `magnitude`, a Number, and `unit`, a String
       * @config {String|Number|Object}
       */
      step: false,
      stepTriggers: null,

      /**
       * The week start day in the {@link #config-picker}, 0 meaning Sunday, 6 meaning Saturday.
       * Uses localized value per default.
       * @config {Number}
       */
      weekStartDay: null,

      /**
       * A config object used to configure the {@link Core.widget.DatePicker datePicker}.
       * ```javascript
       * dateField = new DateField({
       *      picker    : {
       *          multiSelect : true
       *      }
       *  });
       * ```
       * @config {Object}
       */
      picker: {
        type: 'datepicker',
        floating: true,
        scrollAction: 'realign',
        align: {
          align: 't0-b0',
          axisLock: true
        }
      },

      /**
       * Get/set value, which can be a Date or a string. If a string is specified, it will be converted using the
       * specified {@link #config-format}
       * @property {String|Date}
       * @name value
       */

      /**
       * Value, which can be a Date or a string. If a string is specified, it will be converted using the
       * specified {@link #config-format}
       * @config {String|Date}
       */
      value: null
    };
  } //endregion
  //region Init & destroy

  /**
   * Creates default picker widget
   *
   * @internal
   */

  changePicker(picker, oldPicker) {
    const me = this,
          defaults = {
      owner: me,
      forElement: me[me.pickerAlignElement],
      minDate: me.min,
      maxDate: me.max,
      weekStartDay: me._weekStartDay,
      // need to pass the raw value to let the component to use its default value
      align: {
        anchor: me.overlayAnchor,
        target: me[me.pickerAlignElement]
      },
      onSelectionChange: ({
        selection,
        source: picker
      }) => {
        // We only care about what DatePicker does if it has been opened
        if (picker.isVisible) {
          me._isUserAction = true;
          me._isPickerInput = true;
          me.value = selection[0];
          me._isPickerInput = false;
          me._isUserAction = false;
          picker.hide();
        }
      }
    };

    if (me.calendarContainerCls) {
      defaults.cls = me.calendarContainerCls;
    }

    if (me.value) {
      defaults.value = me.value;
    } else {
      defaults.date = new Date();
    }

    return DatePicker.reconfigure(oldPicker, picker, {
      owner: me,
      defaults
    });
  } //endregion
  //region Click listeners

  get backShiftDate() {
    return DateHelper.add(this.value, -1 * this._step.magnitude, this._step.unit);
  }

  onBackClick() {
    const me = this,
          {
      min
    } = me;

    if (!me.readOnly && me.value) {
      const newValue = me.backShiftDate;

      if (!min || min.getTime() <= newValue) {
        me._isUserAction = true;
        me.value = newValue;
        me._isUserAction = false;
      }
    }
  }

  get forwardShiftDate() {
    return DateHelper.add(this.value, this._step.magnitude, this._step.unit);
  }

  onForwardClick() {
    const me = this,
          {
      max
    } = me;

    if (!me.readOnly && me.value) {
      const newValue = me.forwardShiftDate;

      if (!max || max.getTime() >= newValue) {
        me._isUserAction = true;
        me.value = newValue;
        me._isUserAction = false;
      }
    }
  } //endregion
  //region Toggle picker

  showPicker(focusPicker) {
    if (this.readOnly) {
      return;
    }

    const {
      _picker
    } = this; // If it's already instanced, move it.
    // It will be initialized correctly if not.

    _picker && (_picker.value = this.value);
    super.showPicker(focusPicker);
  }

  focusPicker() {
    this.picker.focus();
  } //endregion
  // region Validation

  get isValid() {
    const me = this,
          min = me.min,
          max = me.max;
    me.clearError('L{Field.minimumValueViolation}', true);
    me.clearError('L{Field.maximumValueViolation}', true);
    let value = me.value;

    if (value) {
      value = value.getTime();

      if (min && min.getTime() > value) {
        me.setError('L{Field.minimumValueViolation}', true);
        return false;
      }

      if (max && max.getTime() < value) {
        me.setError('L{Field.maximumValueViolation}', true);
        return false;
      }
    }

    return super.isValid;
  } //endregion
  //region Getters/setters

  transformDateValue(value) {
    if (value != null) {
      if (!DateHelper.isDate(value)) {
        if (typeof value === 'string') {
          // If date cannot be parsed with set format, try fallback - the more general one
          value = DateHelper.parse(value, this.format) || DateHelper.parse(value, this.fallbackFormat);
        } else {
          value = new Date(value);
        }
      } // We insist on a *valid* Date as the value

      if (DateHelper.isValidDate(value)) {
        return this.transformTimeValue(value);
      }
    }

    return null;
  }

  transformTimeValue(value) {
    const me = this,
          {
      keepTime
    } = me;
    value = DateHelper.clone(value);

    if (!keepTime) {
      DateHelper.clearTime(value, false);
    } // change time if keepTime !== 'entered'
    else if (keepTime !== 'entered') {
        const timeValue = DateHelper.parse(keepTime, me.timeFormat); // if this.keepTime is a valid date or a string describing valid time copy from it

        if (DateHelper.isValidDate(timeValue)) {
          DateHelper.copyTimeValues(value, timeValue);
        } // otherwise try to copy from the current value
        else if (DateHelper.isValidDate(me.value)) {
            DateHelper.copyTimeValues(value, me.value);
          }
      } // if keepTime === 'entered' and picker is used apply current value time
      else if (me._isPickerInput && DateHelper.isValidDate(me.value)) {
          DateHelper.copyTimeValues(value, me.value);
        } // else don't change time

    return value;
  }

  changeMin(value) {
    return this.transformDateValue(value);
  }

  updateMin(min) {
    const {
      input,
      _picker
    } = this;

    if (input) {
      if (min == null) {
        input.removeAttribute('min');
      } else {
        input.min = min;
      }
    } // See if our lazy config has been realized...

    if (_picker) {
      _picker.minDate = min;
    }

    this.syncInvalid();
  }

  changeMax(value) {
    return this.transformDateValue(value);
  }

  updateMax(max) {
    const {
      input,
      _picker
    } = this;

    if (input) {
      if (max == null) {
        input.removeAttribute('max');
      } else {
        input.max = max;
      }
    }

    if (_picker) {
      _picker.maxDate = max;
    }

    this.syncInvalid();
  }

  get weekStartDay() {
    // This trick allows our weekStartDay to float w/the locale even if the locale changes
    return typeof this._weekStartDay === 'number' ? this._weekStartDay : DateHelper.weekStartDay;
  }

  updateWeekStartDay(weekStartDay) {
    if (this._picker) {
      this._picker.weekStartDay = weekStartDay;
    }
  }

  changeValue(value, oldValue) {
    const me = this,
          newValue = me.transformDateValue(value); // A value we could not parse

    if (value && !newValue) {
      // setError uses localization
      me.setError('L{invalidDate}');
      return;
    }

    me.clearError('L{invalidDate}'); // Reject non-change

    if (me.hasChanged(oldValue, newValue)) {
      return super.changeValue(newValue, oldValue);
    } // But we must fix up the display in case it was an unparseable string
    // and the value therefore did not change.

    if (!me.inputting) {
      me.syncInputFieldValue();
    }
  }

  updateValue(value, oldValue) {
    const picker = this._picker;

    if (picker && !this.inputting) {
      picker.value = value;
    }

    super.updateValue(value, oldValue);
  }

  changeStep(value, was) {
    const type = typeof value;

    if (!value) {
      return null;
    }

    if (type === 'number') {
      value = {
        magnitude: Math.abs(value),
        unit: was ? was.unit : 'day'
      };
    } else if (type === 'string') {
      value = DateHelper.parseDuration(value);
    }

    if (value && value.unit && value.magnitude) {
      if (value.magnitude < 0) {
        value = {
          magnitude: -value.magnitude,
          // Math.abs
          unit: value.unit
        };
      }

      return value;
    }
  }

  updateStep(value) {
    // If a step is configured, show the steppers
    this.element.classList[value ? 'remove' : 'add']('b-no-steppers');
    this.syncInvalid();
  }

  hasChanged(oldValue, newValue) {
    // if both dates are provided and the field does not has time info in its format
    if (oldValue !== null && oldValue !== void 0 && oldValue.getTime && newValue !== null && newValue !== void 0 && newValue.getTime && this.keepTime !== 'entered') {
      // Only compare date part
      return !DateHelper.isEqual(DateHelper.clearTime(oldValue), DateHelper.clearTime(newValue));
    }

    return super.hasChanged(oldValue, newValue);
  }

  get inputValue() {
    // Do not use the _value property. If called during configuration, this
    // will import the configured value from the config object.
    const date = this.value;
    return date ? DateHelper.format(date, this.format) : '';
  }

  updateFormat() {
    this.syncInputFieldValue(true);
  } //endregion
  //region Localization

  updateLocalization() {
    super.updateLocalization();
    this.syncInputFieldValue(true);
  } //endregion
  //region Other

  internalOnKeyEvent(event) {
    super.internalOnKeyEvent(event);

    if (event.key === 'Enter' && this.isValid) {
      this.picker.hide();
    }
  } //endregion

} // Register this widget type with its Factory

DateField.initClass();
DateField._$name = 'DateField';

/**
 * @module Core/widget/TimePicker
 */

/**
 * A Popup which displays hour and minute number fields and AM/PM switcher buttons for 12 hour time format.
 * Fires timeChange event upon user changes time.
 *
 * This class is not intended for use in applications. It is used internally by the
 * {@link Core.widget.TimeField} class.
 *
 * @classType timepicker
 * @private
 */

class TimePicker extends Popup {
  //region Config
  static get $name() {
    return 'TimePicker';
  } // Factoryable type name

  static get type() {
    return 'timepicker';
  }

  static get defaultConfig() {
    return {
      items: [{
        type: 'number',
        ref: 'fieldHours',
        min: 0,
        max: 23,
        highlightExternalChange: false,
        format: '2>9'
      }, {
        html: '<label>:</label>'
      }, {
        type: 'number',
        ref: 'fieldMinutes',
        min: 0,
        max: 59,
        highlightExternalChange: false,
        format: '2>9'
      }, {
        type: 'button',
        text: 'AM',
        toggleGroup: 'am-pm',
        ref: 'buttonAM',
        cls: 'b-blue'
      }, {
        type: 'button',
        text: 'PM',
        toggleGroup: 'am-pm',
        ref: 'buttonPM',
        cls: 'b-blue'
      }],
      float: '1',
      autoShow: false,
      trapFocus: true,

      /**
       * Default time value
       * @config {Date}
       */
      value: DateHelper.getTime(0),

      /**
       * Time format. Used to set appropriate 12/24 hour format to display.
       * See Core.helper.DateHelper#format for formatting options.
       * @config {String}
       */
      format: null
    };
  } //endregion
  //region Init

  /**
   * Fires when a time is changed.
   * @event timeChange
   * @param {Date} time The selected time.
   */

  construct(config) {
    const me = this;
    super.construct(config);
    me._pm = false;
    me.fieldHours.on('change', me.onFieldChange, me);
    me.fieldMinutes.on('change', me.onFieldChange, me);
    me.buttonAM.on('click', me.onButtonAMClick, me);
    me.buttonPM.on('click', me.onButtonPMClick, me);
    EventHelper.on({
      element: me.element,
      keydown: 'onPickerKeyDown',
      thisObj: me
    });
    EventHelper.on({
      element: me.fieldHours.element,
      keydown: 'onPickerKeyDown',
      thisObj: me
    });
    EventHelper.on({
      element: me.fieldMinutes.element,
      keydown: 'onPickerKeyDown',
      thisObj: me
    });
    me.refresh();
  } //endregion
  //region Event listeners

  onFieldChange() {
    const me = this;

    if (me._time) {
      me.value = me.pickerToTime();
    }
  }

  onButtonAMClick() {
    const me = this;
    me._pm = false;

    if (me._time) {
      me.value = me.pickerToTime();
    }
  }

  onButtonPMClick() {
    const me = this;
    me._pm = true;

    if (me._time) {
      me.value = me.pickerToTime();
    }
  }

  onPickerKeyDown(keyEvent) {
    const me = this,
          keyName = keyEvent.key && keyEvent.key.trim() || keyEvent.code;

    switch (keyName) {
      case 'Escape':
        // Support for undefined initial time
        me.triggerTimeChange(me._initialValue);
        me.hide();
        keyEvent.preventDefault();
        break;

      case 'Enter':
        me.value = me.pickerToTime();
        me.hide();
        keyEvent.preventDefault();
        break;
    }
  } //endregion
  //region Internal functions

  pickerToTime() {
    const me = this,
          pm = me._pm;
    me.fieldHours.format = me._is24Hour ? '2>9' : null;
    let hours = me.fieldHours.value,
        newValue = new Date(me._time);

    if (!me._is24Hour) {
      if (pm && hours < 12) hours = hours + 12;
      if (!pm && hours === 12) hours = 0;
    }

    newValue.setHours(hours);
    newValue.setMinutes(me.fieldMinutes.value);

    if (me._min) {
      newValue = DateHelper.max(me._min, newValue);
    }

    if (me._max) {
      newValue = DateHelper.min(me._max, newValue);
    }

    return newValue;
  }

  triggerTimeChange(time) {
    this.trigger('timeChange', {
      time
    });
  } //endregion
  //region Getters / Setters

  /**
   * Get/set value, which can be a Date or a string. If a string is specified, it will be converted using the
   * specified {@link #config-format}
   * @property {Date|String}
   */

  set value(newTime) {
    const me = this;
    let changed = false;

    if (!newTime || !me._time) {
      me._time = TimePicker.defaultConfig.value;
      changed = true;
    } else if (newTime.getTime() !== me._time.getTime()) {
      me._time = newTime;
      changed = true;
    }

    if (changed) {
      if (me.isVisible) {
        me.triggerTimeChange(me.value);
      }

      me.refresh();
    }
  }

  get value() {
    return this._time;
  }
  /**
   * Get/Set format for time displayed in field (see Core.helper.DateHelper#format for formatting options)
   * @property {String}
   */

  set format(value) {
    const me = this;
    me._format = value;
    me._is24Hour = DateHelper.is24HourFormat(me._format);
    me.refresh();
  }

  get format() {
    return this._format;
  }
  /**
   * Get/set max value, which can be a Date or a string. If a string is specified, it will be converted using the
   * specified {@link #config-format}
   * @property {Date|String}
   */

  set min(value) {
    this._min = value;
  }

  get min() {
    return this._min;
  }
  /**
   * Get/set min value, which can be a Date or a string. If a string is specified, it will be converted using the
   * specified {@link #config-format}
   * @property {Date|String}
   */

  set max(value) {
    this._max = value;
  }

  get max() {
    return this._max;
  }
  /**
   * Get/set initial value and value, which can be a Date or a string. If a string is specified,
   * it will be converted using the specified {@link #config-format}. Initial value is restored on Escape click
   * @property {Date|String}
   */

  set initialValue(value) {
    this.value = value;
    this._initialValue = value;
  }

  get initialValue() {
    return this._initialValue;
  } //endregion
  //region Internal widgets getters

  get buttonAM() {
    return this.widgetMap.buttonAM;
  }

  get buttonPM() {
    return this.widgetMap.buttonPM;
  }

  get fieldHours() {
    return this.widgetMap.fieldHours;
  }

  get fieldMinutes() {
    return this.widgetMap.fieldMinutes;
  } //endregion
  //region Display

  refresh() {
    const me = this;

    if (!me.isConfiguring) {
      const time = me._time,
            is24 = me._is24Hour,
            hours = time.getHours(),
            pm = me._pm = hours >= 12,
            fieldHours = me.fieldHours;
      me.element.classList[is24 ? 'add' : 'remove']('b-24h');
      fieldHours.min = is24 ? 0 : 1;
      fieldHours.max = is24 ? 23 : 12;
      fieldHours.value = is24 ? hours : hours % 12 || 12;
      me.fieldMinutes.value = time.getMinutes();
      me.buttonAM.pressed = !pm;
      me.buttonPM.pressed = pm;
      me.buttonAM.hidden = me.buttonPM.hidden = is24;
    }
  } //endregion

} // Register this widget type with its Factory

TimePicker.initClass();
TimePicker._$name = 'TimePicker';

//TODO: picker icon (clock) should be clock that shows actual time
/**
 * @module Core/widget/TimeField
 */

/**
 * Time field widget (text field + time picker).
 *
 * This field can be used as an {@link Grid.column.Column#config-editor editor} for the {@link Grid.column.Column Column}.
 * It is used as the default editor for the {@link Grid.column.TimeColumn TimeColumn}.
 *
 * @extends Core/widget/PickerField
 *
 * @example
 * let field = new TimeField({
 *   format: 'HH'
 * });
 *
 * @classType timefield
 * @externalexample widget/TimeField.js
 */

class TimeField extends PickerField {
  //region Config
  static get $name() {
    return 'TimeField';
  } // Factoryable type name

  static get type() {
    return 'timefield';
  } // Factoryable type alias

  static get alias() {
    return 'time';
  }

  static get configurable() {
    return {
      picker: {
        type: 'timepicker',
        floating: true,
        align: {
          align: 't0-b0',
          axisLock: true
        }
      },

      /**
       * Get/Set format for time displayed in field (see {@link Core.helper.DateHelper#function-format-static}
       * for formatting options).
       * @property {String}
       * @name format
       */

      /**
       * Format for date displayed in field (see Core.helper.DateHelper#function-format-static for formatting
       * options).
       * @config {String}
       * @default
       */
      format: 'LT',
      triggers: {
        expand: {
          template: () => `<div class="b-align-${this.align || 'end'}"><div class="b-icon-clock-live"></div></div>`,
          handler: 'onTriggerClick',
          align: 'end'
        },
        back: {
          cls: 'b-icon b-icon-angle-left b-step-trigger',
          handler: 'onBackClick',
          align: 'start'
        },
        forward: {
          cls: 'b-icon b-icon-angle-right b-step-trigger',
          handler: 'onForwardClick',
          align: 'end'
        }
      },

      /**
       * Get/set min value, which can be a Date or a string. If a string is specified, it will be converted using
       * the specified {@link #config-format}.
       * @property {String|Date}
       * @name min
       */

      /**
       * Min time value
       * @config {String|Date}
       */
      min: null,

      /**
       * Get/set max value, which can be a Date or a string. If a string is specified, it will be converted using
       * the specified {@link #config-format}.
       * @property {String|Date}
       * @name max
       */

      /**
       * Max time value
       * @config {String|Date}
       */
      max: null,

      /**
       * The `step` property may be set in Object form specifying two properties, `magnitude`, a Number, and
       * `unit`, a String.
       *
       * If a Number is passed, the steps's current unit is used and just the magnitude is changed.
       *
       * If a String is passed, it is parsed by {@link Core.helper.DateHelper#function-parseDuration-static}, for
       * example `'5m'`, `'5 m'`, `'5 min'`, `'5 minutes'`.
       *
       * Upon read, the value is always returned in object form containing `magnitude` and `unit`.
       * @property {String|Number|Object}
       * @name step
       */

      /**
       * Time increment duration value. Defaults to 5 minutes.
       * The value is taken to be a string consisting of the numeric magnitude and the units.
       * The units may be a recognised unit abbreviation of this locale or the full local unit name.
       * For example `"10m"` or `"5min"` or `"2 hours"`
        * @config {String}
       */
      step: '5m',
      stepTriggers: null,

      /**
       * Get/set value, which can be a Date or a string. If a string is specified, it will be converted using the
       * specified {@link #config-format}.
       * @property {String|Date}
       */

      /**
       * Value, which can be a Date or a string. If a string is specified, it will be converted using the
       * specified {@link #config-format}
       * @config {String|Date}
       */
      value: null
    };
  } //endregion
  //region Init & destroy

  changePicker(picker, oldPicker) {
    const me = this;
    return TimePicker.reconfigure(oldPicker, picker, {
      owner: me,
      defaults: {
        forElement: me[me.pickerAlignElement],
        owner: me,
        align: {
          anchor: me.overlayAnchor,
          target: me[me.pickerAlignElement]
        },

        onTimeChange({
          time
        }) {
          me._isUserAction = true;
          me.value = time;
          me._isUserAction = false;
        }

      }
    });
  } //endregion
  //region Click listeners

  onBackClick() {
    const me = this,
          {
      min
    } = me;

    if (!me.readOnly && me.value) {
      const newValue = DateHelper.add(me.value, -1 * me.step.magnitude, me.step.unit);

      if (!min || min.getTime() <= newValue) {
        me._isUserAction = true;
        me.value = newValue;
        me._isUserAction = false;
      }
    }
  }

  onForwardClick() {
    const me = this,
          {
      max
    } = me;

    if (!me.readOnly && me.value) {
      const newValue = DateHelper.add(me.value, me.step.magnitude, me.step.unit);

      if (!max || max.getTime() >= newValue) {
        me._isUserAction = true;
        me.value = newValue;
        me._isUserAction = false;
      }
    }
  } //endregion
  // region Validation

  get isValid() {
    const me = this;
    me.clearError('L{Field.minimumValueViolation}', true);
    me.clearError('L{Field.maximumValueViolation}', true);
    let value = me.value;

    if (value) {
      value = value.getTime();

      if (me._min && me._min.getTime() > value) {
        me.setError('L{Field.minimumValueViolation}', true);
        return false;
      }

      if (me._max && me._max.getTime() < value) {
        me.setError('L{Field.maximumValueViolation}', true);
        return false;
      }
    }

    return super.isValid;
  }

  hasChanged(oldValue, newValue) {
    if (oldValue && oldValue.getTime && newValue && newValue.getTime) {
      // Only care about the time part
      return oldValue.getHours() !== newValue.getHours() || oldValue.getMinutes() !== newValue.getMinutes() || oldValue.getSeconds() !== newValue.getSeconds() || oldValue.getMilliseconds() !== newValue.getMilliseconds();
    }

    return super.hasChanged(oldValue, newValue);
  } //endregion
  //region Toggle picker

  /**
   * Show picker
   */

  showPicker(focusPicker) {
    const me = this,
          {
      picker
    } = me;

    if (me.readOnly) {
      return;
    }

    picker.initialValue = me.value;
    picker.format = me.format;
    picker.maxTime = me.max;
    picker.minTime = me.min; // Show valid time from picker while editor has undefined value

    me.value = picker.value;
    super.showPicker(focusPicker);
  }

  onPickerShow() {
    super.onPickerShow(); // Remove PickerField key listener

    this.pickerKeyDownRemover && this.pickerKeyDownRemover();
  }
  /**
   * Focus time picker
   */

  focusPicker() {
    this.picker.focus();
  } //endregion
  //region Getters/setters

  transformTimeValue(value) {
    if (value != null) {
      if (!DateHelper.isDate(value)) {
        if (typeof value === 'string') {
          value = DateHelper.parse(value, this.format);
        } else {
          value = new Date(value);
        }
      } // We insist on a *valid* Time as the value

      if (DateHelper.isValidDate(value)) {
        // Clear date part back to zero so that all we have is the time part of the epoch.
        return DateHelper.getTime(value);
      }
    }

    return null;
  }

  changeMin(value) {
    return this.transformTimeValue(value);
  }

  updateMin(value) {
    const {
      input
    } = this;

    if (input) {
      if (value == null) {
        input.removeAttribute('min');
      } else {
        input.min = value;
      }
    }

    this.syncInvalid();
  }

  changeMax(value) {
    return this.transformTimeValue(value);
  }

  updateMax(value) {
    const {
      input
    } = this;

    if (input) {
      if (value == null) {
        input.removeAttribute('max');
      } else {
        input.max = value;
      }
    }

    this.syncInvalid();
  }

  changeValue(value, was) {
    const me = this,
          newValue = me.transformTimeValue(value); // A value we could not parse

    if (value && !newValue || me.isRequired && value === '') {
      // setError uses localization
      me.setError('L{invalidTime}');
      return;
    }

    me.clearError('L{invalidTime}'); // Reject non-change

    if (me.hasChanged(was, newValue)) {
      return super.changeValue(newValue, was);
    } // But we must fix up the display in case it was an unparseable string
    // and the value therefore did not change.

    if (!me.inputting) {
      me.syncInputFieldValue(true);
    }
  }

  updateValue(value, was) {
    const {
      expand
    } = this.triggers; // This makes to clock icon show correct time

    if (expand && value) {
      expand.element.firstElementChild.style.animationDelay = -((value.getHours() * 60 + value.getMinutes()) / 10) + 's';
    }

    super.updateValue(value, was);
  }

  changeStep(value, was) {
    const type = typeof value;

    if (!value) {
      return null;
    }

    if (type === 'number') {
      value = {
        magnitude: Math.abs(value),
        unit: was ? was.unit : 'day'
      };
    } else if (type === 'string') {
      value = DateHelper.parseDuration(value);
    }

    if (value && value.unit && value.magnitude) {
      if (value.magnitude < 0) {
        value = {
          magnitude: -value.magnitude,
          // Math.abs
          unit: value.unit
        };
      }

      return value;
    }
  }

  updateStep(value) {
    // If a step is configured, show the steppers
    this.element.classList[value ? 'remove' : 'add']('b-no-steppers');
    this.syncInvalid();
  }

  updateFormat() {
    this.syncInputFieldValue(true);
  }

  get inputValue() {
    return DateHelper.format(this.value, this.format);
  } //endregion
  //region Localization

  updateLocalization() {
    super.updateLocalization();
    this.syncInputFieldValue(true);
  } //endregion

} // Register this widget type with its Factory

TimeField.initClass();
TimeField._$name = 'TimeField';

/**
 * @module Core/widget/DateTimeField
 */

/**
 * A field combining a {@link Core.widget.DateField} and a {@link Core.widget.TimeField}.
 *
 * @extends Core/widget/Field
 * @classtype datetimefield
 * @externalexample widget/DateTimeField.js
 */

class DateTimeField extends Field {
  static get configurable() {
    return {
      /**
       * Configuration for {@link Core.widget.TimeField}
       *
       * @config {Object}
       */
      timeField: {
        flex: '0 0 45%'
      },

      /**
       * Configuration for {@link Core.widget.DateField}
       *
       * @config {Object}
       */
      dateField: {
        // To be able to use transformDateValue for parsing without loosing time, a bit of a hack
        keepTime: true,
        flex: 1,
        step: '1 d'
      },

      /**
       * The week start day in the {@link Core.widget.DateField#config-picker}, 0 meaning Sunday, 6 meaning Saturday.
       * Uses localized value per default.
       *
       * @config {Number}
       */
      weekStartDay: null,
      inputTemplate: () => ''
    };
  }

  static get $name() {
    return 'DateTimeField';
  }

  static get type() {
    return 'datetimefield';
  } // Factoryable type alias

  static get alias() {
    return 'datetime';
  } // Implementation needed at this level because it has two inner elements in its inputWrap

  get innerElements() {
    return [this.dateField.element, this.timeField.element];
  } // Converts the timeField config into a TimeField

  changeTimeField(config) {
    const me = this,
          result = new TimeField(ObjectHelper.assign({
      syncInvalid(...args) {
        const updatingInvalid = me.updatingInvalid;
        TimeField.prototype.syncInvalid.apply(this, args);
        me.timeField && !updatingInvalid && me.syncInvalid();
      }

    }, config));
    EventHelper.on({
      element: result.element,
      keydown: 'onTimeFieldKeyDown',
      thisObj: me
    }); // Must set *after* construction, otherwise it becomes the default state
    // to reset readOnly back to

    if (me.readOnly) {
      result.readOnly = true;
    }

    return result;
  } // Set up change listener when TimeField is available. Not in timeField config to enable users to supply their own
  // listeners block there

  updateTimeField(timeField) {
    const me = this;
    timeField.on({
      change({
        userAction,
        value
      }) {
        if (userAction && !me.$settingValue) {
          const dateAndTime = me.dateField.value;
          me._isUserAction = true;
          me.value = dateAndTime ? DateHelper.copyTimeValues(dateAndTime, value) : null;
          me._isUserAction = false;
        }
      },

      thisObj: me
    });
  } // Converts the dateField config into a class based on { type : "..." } provided (DateField by default)

  changeDateField(config) {
    const me = this,
          type = (config === null || config === void 0 ? void 0 : config.type) || 'datefield',
          cls = Widget.resolveType(config.type || 'datefield'),
          result = Widget.create(ObjectHelper.assign({
      type,

      syncInvalid(...args) {
        const updatingInvalid = me.updatingInvalid;
        cls.prototype.syncInvalid.apply(this, args);
        me.dateField && !updatingInvalid && me.syncInvalid();
      }

    }, config));
    EventHelper.on({
      element: result.element,
      keydown: 'onDateFieldKeyDown',
      thisObj: me
    }); // Must set *after* construction, otherwise it becomes the default state
    // to reset readOnly back to

    if (me.readOnly) {
      result.readOnly = true;
    }

    return result;
  }

  get childItems() {
    return [this.dateField, this.timeField];
  } // Set up change listener when DateField is available. Not in dateField config to enable users to supply their own
  // listeners block there

  updateDateField(dateField) {
    const me = this;
    dateField.on({
      change({
        userAction,
        value
      }) {
        if (userAction && !me.$isInternalChange) {
          me._isUserAction = true;
          me.value = value;
          me._isUserAction = false;
        }
      },

      thisObj: me
    });
  }

  updateWeekStartDay(weekStartDay) {
    if (this.dateField) {
      this.dateField.weekStartDay = weekStartDay;
    }
  }

  changeWeekStartDay(value) {
    var _this$dateField$weekS, _this$dateField;

    return typeof value === 'number' ? value : (_this$dateField$weekS = (_this$dateField = this.dateField) === null || _this$dateField === void 0 ? void 0 : _this$dateField.weekStartDay) !== null && _this$dateField$weekS !== void 0 ? _this$dateField$weekS : DateHelper.weekStartDay;
  } // Apply our value to our underlying fields

  syncInputFieldValue(skipHighlight = this.isConfiguring) {
    super.syncInputFieldValue(true);

    if (!skipHighlight && !this.highlightExternalChange) {
      skipHighlight = true;
    }

    const me = this,
          highlightDate = me.dateField.highlightExternalChange,
          highlightTime = me.timeField.highlightExternalChange;
    me.$isInternalChange = true;
    me.dateField.highlightExternalChange = false; // Prevent dateField from keeping its time value
    // TODO: Should be doable without this hack

    me.dateField.value = null;
    me.dateField.highlightExternalChange = highlightDate;

    if (skipHighlight) {
      me.timeField.highlightExternalChange = me.dateField.highlightExternalChange = false;
    }

    me.timeField.value = me.dateField.value = me.inputValue;
    me.dateField.highlightExternalChange = highlightDate;
    me.timeField.highlightExternalChange = highlightTime;
    me.$isInternalChange = false; // Must evaluate after child fields have been updated since our validity state depends on theirs.

    me.syncInvalid();
  }

  onTimeFieldKeyDown(e) {
    const me = this; // we need to handle keydown for composed field manually and before it's done by cellEdit feature

    if (e.key === 'Enter' || e.key === 'Tab') {
      const dateAndTime = me.dateField.value;
      me._isUserAction = true;
      me.value = dateAndTime ? DateHelper.copyTimeValues(dateAndTime, me.timeField.value) : null;
      me._isUserAction = false;
    }
  }

  onDateFieldKeyDown(e) {
    const me = this;

    if (e.key === 'Tab') {
      e.stopPropagation();
      e.preventDefault();
      me.timeField.focus();
    } // we need to handle keydown for composed field manually and before it's done by cellEdit feature

    if (e.key === 'Enter') {
      me.value = me.dateField.value;
    }
  } // Make us and our underlying fields required

  updateRequired(required, was) {
    this.timeField.required = this.dateField.required = required;
  }

  updateReadOnly(readOnly, was) {
    super.updateReadOnly(readOnly, was);

    if (!this.isConfiguring) {
      this.timeField.readOnly = this.dateField.readOnly = readOnly;
    }
  } // Make us and our underlying fields disabled

  onDisabled(value) {
    this.timeField.disabled = this.dateField.disabled = value;
  }

  focus() {
    this.dateField.focus();
  }

  hasChanged(oldValue, newValue) {
    return !DateHelper.isEqual(oldValue, newValue);
  }

  get isValid() {
    return this.timeField.isValid && this.dateField.isValid;
  }

  setError(error, silent) {
    [this.dateField, this.timeField].forEach(f => f.setError(error, silent));
  }

  getErrors() {
    const errors = [...(this.dateField.getErrors() || []), ...(this.timeField.getErrors() || [])];
    return errors.length ? errors : null;
  }

  clearError(error, silent) {
    [this.dateField, this.timeField].forEach(f => f.clearError(error, silent));
  }

  updateInvalid() {
    // use this flag in this level to avoid looping
    this.updatingInvalid = true;
    [this.dateField, this.timeField].forEach(f => f.updateInvalid());
    this.updatingInvalid = false;
  }

}
DateTimeField.initClass();
DateTimeField._$name = 'DateTimeField';

/**
 * @module Core/widget/DisplayField
 */

/**
 * DisplayField widget used to show a read only value
 *
 * @extends Core/widget/Field
 *
 * @example
 * let displayField = new DisplayField({
 *   label: 'name',
 *   value : 'John Doe'
 * });
 *
 * @classType DisplayField
 * @externalexample widget/DisplayField.js
 */

class DisplayField extends TextField {
  static get $name() {
    return 'DisplayField';
  } // Factoryable type name

  static get type() {
    return 'displayfield';
  } // Factoryable type alias

  static get alias() {
    return 'display';
  }

  static get configurable() {
    return {
      readOnly: true,
      editable: false,
      cls: 'b-display-field'
    };
  }

  get focusElement() {// we're not focusable.
  }

  changeReadOnly() {
    return true;
  }

  changeEditable() {
    return false;
  }

} // Register this widget type with its Factory

DisplayField.initClass();
DisplayField._$name = 'DisplayField';

/**
 * @module Core/widget/DurationField
 */

/**
 * A specialized field allowing a user to also specify duration unit when editing the duration value.
 *
 * This field can be used as an {@link Grid.column.Column#config-editor editor} for the {@link Grid.column.Column Column}.
 * It is used as the default editor for the `DurationColumn`.
 *
 * @extends Core/widget/TextField
 * @classType durationfield
 * @externalexample widget/DurationField.js
 */

class DurationField extends TextField {
  static get $name() {
    return 'DurationField';
  } // Factoryable type name

  static get type() {
    return 'durationfield';
  } // Factoryable type alias

  static get alias() {
    return 'duration';
  }

  static get defaultConfig() {
    return {
      /**
       * The `value` config may be set in Object form specifying two properties,
       * `magnitude`, a Number, and `unit`, a String.
       *
       * If a String is passed, it is parsed in accordance with current locale rules.
       * The string is taken to be the numeric magnitude, followed by whitespace, then an abbreviation, or name of
       * the unit.
       * @config {Object|String}
       */
      value: null,

      /**
       * Step size for spin button clicks.
       * @config {Number}
       * @default
       */
      step: 1,

      /**
       * The duration unit to use with the current magnitude value.
       * @config {String}
       */
      unit: null,
      defaultUnit: 'day',

      /**
       * The duration magnitude to use with the current unit value.
       * @config {Number}
       */
      magnitude: null,

      /**
       * When set to `true` the field will use short names of unit durations
       * (as returned by {@link Core.helper.DateHelper#function-getShortNameOfUnit-static}) when creating the
       * input field's display value.
       * @config {Boolean}
       */
      useAbbreviation: false,

      /**
       * Set to `true` to allow negative duration
       * @config {Boolean}
       */
      allowNegative: false,

      /**
       * The number of decimal places to allow. Defaults to no constraint.
       * @config {Number}
       * @default
       */
      decimalPrecision: null,
      triggers: {
        spin: {
          type: 'spintrigger'
        }
      },
      nullValue: null
    };
  }
  /**
   * Fired when this field's value changes.
   * @event change
   * @param {Core.data.Duration} value - This field's value
   * @param {Core.data.Duration} oldValue - This field's previous value
   * @param {Boolean} valid - True if this field is in a valid state.
   * @param {Event} [event] - The triggering DOM event if any.
   * @param {Boolean} userAction - Triggered by user taking an action (`true`) or by setting a value (`false`)
   * @param {Core.widget.DurationField} source - This field
   */

  /**
   * User performed default action (typed into this field or hit the triggers).
   * @event action
   * @param {Core.data.Duration} value - This field's value
   * @param {Core.data.Duration} oldValue - This field's previous value
   * @param {Boolean} valid - True if this field is in a valid state.
   * @param {Event} [event] - The triggering DOM event if any.
   * @param {Boolean} userAction - Triggered by user taking an action (`true`) or by setting a value (`false`)
   * @param {Core.widget.DurationField} source - This field
   */

  static get configurable() {
    return {
      /**
       * Minimum duration value (e.g. 1d)
       * @config {String}
       */
      min: null,

      /**
       * Max duration value (e.g. 10d)
       * @config {String}
       */
      max: null,

      /**
       * Comma-separated list of units to allow in this field, e.g. "day,hour,year". Leave blank to allow all
       * valid units (the default)
       * @config {String}
       */
      allowedUnits: null
    };
  }

  changeMin(value) {
    return typeof value === 'string' ? new Duration(value) : value;
  }

  changeMax(value) {
    return typeof value === 'string' ? new Duration(value) : value;
  }

  changeAllowedUnits(units) {
    if (typeof units === 'string') {
      units = units.split(',');
    }

    return units;
  }

  updateAllowedUnits(units) {
    this.allowedUnitsRe = new RegExp(`(${units.join('|')})`, 'i');
  }

  get inputValue() {
    // Do not use the _value property. If called during configuration, this
    // will import the configured value from the config object.
    return this.value == null ? '' : this.value.toString(this.useAbbreviation);
  }
  /**
   * Get/Set duration unit to use with the current magnitude value.
   * Valid values are:
   * - "millisecond" - Milliseconds
   * - "second" - Seconds
   * - "minute" - Minutes
   * - "hour" - Hours
   * - "day" - Days
   * - "week" - Weeks
   * - "month" - Months
   * - "quarter" - Quarters
   * - "year"- Years
   *
   * @property {String}
   */

  set unit(unit) {
    this._unit = unit;
    this.value = this.calcValue();
  }

  get unit() {
    return this._unit;
  }
  /**
   * Get/Set numeric magnitude `value` to use with the current unit value.
   * @property {Number}
   */

  set magnitude(magnitude) {
    this.clearError('L{invalidUnit}');
    this._magnitude = this.roundMagnitude(magnitude);
    super.value = this.calcValue();
  }

  get magnitude() {
    return this._magnitude;
  }

  roundMagnitude(value) {
    return value && this.decimalPrecision != null ? ObjectHelper.round(value, this.decimalPrecision) : value;
  }

  get allowDecimals() {
    return this.decimalPrecision !== 0;
  }

  get isValid() {
    const me = this,
          isEmpty = me.value == null || me.value && me.value.magnitude == null;
    return super.isValid && (isEmpty && !me.required || !isEmpty && (me.allowNegative || me.value.magnitude >= 0));
  }

  internalOnChange(event) {
    const me = this,
          value = me.value,
          oldVal = me._lastValue;

    if (me.hasChanged(oldVal, value)) {
      me._lastValue = value;
      me.triggerFieldChange({
        value,
        event,
        userAction: true,
        valid: me.isValid
      });
    }
  }

  onFocusOut(e) {
    this.syncInputFieldValue(true);
    return super.onFocusOut(e);
  }
  /**
   * The `value` property may be set in Object form specifying two properties, `magnitude`, a Number, and `unit`, a
   * String.
   *
   * If a Number is passed, the field's current unit is used and just the magnitude is changed.
   *
   * If a String is passed, it is parsed in accordance with current locale rules. The string is taken to be the
   * numeric magnitude, followed by whitespace, then an abbreviation, or name of the unit.
   *
   * Upon read, the value is always a {@link Core.data.Duration Duration} object containing `magnitude` and `unit`.
   * @property {String|Number|Object|Core.data.Duration}
   */

  set value(value) {
    const me = this;
    let newMagnitude, newUnit;
    this.clearError('L{invalidUnit}');

    if (typeof value === 'number') {
      // A number means preserving existing unit value
      newMagnitude = me.roundMagnitude(value);
      newUnit = me._unit;
    } else if (typeof value === 'string') {
      // Parse as a string
      const parsedDuration = DateHelper.parseDuration(value, me.allowDecimals, me._unit || DurationField.defaultConfig.defaultUnit);

      if (parsedDuration) {
        if (!me.allowedUnitsRe || me.allowedUnitsRe.test(parsedDuration.unit)) {
          newUnit = parsedDuration.unit;
          newMagnitude = me.roundMagnitude(parsedDuration.magnitude);
        } else {
          me.setError('L{invalidUnit}');
        }
      }
    } else {
      // Using value object with unit and magnitude
      if (value && 'unit' in value && 'magnitude' in value) {
        newUnit = value.unit;
        newMagnitude = me.roundMagnitude(value.magnitude);
      } else {
        newUnit = null;
        newMagnitude = null;
      }
    }

    if (me._magnitude !== newMagnitude || me._unit !== newUnit) {
      me._magnitude = newMagnitude;
      me._unit = newUnit;
      super.value = me.calcValue();
    }
  }

  okMax(value) {
    return this.max == null || value <= this.max;
  }

  okMin(value) {
    return this.min == null || value >= this.min;
  }

  get validity() {
    const value = this.value,
          validity = {}; // Assert range for non-empty fields, empty fields will turn invalid if `required: true`

    if (value != null) {
      validity.rangeUnderflow = !this.okMin(value);
      validity.rangeOverflow = !this.okMax(value);
    }

    validity.valid = !validity.rangeUnderflow && !validity.rangeOverflow;
    return validity;
  }

  get value() {
    return super.value;
  }

  calcValue() {
    const me = this;

    if ((!me._unit || me._magnitude == null) && me.clearable) {
      return null;
    } else {
      return new Duration(me._magnitude, me._unit || DurationField.defaultConfig.defaultUnit);
    }
  }

  hasChanged(oldValue, newValue) {
    return newValue && !oldValue || !newValue && oldValue || newValue && oldValue && !oldValue.isEqual(newValue);
  }
  /**
   * The `milliseconds` property is a read only property which returns the number of milliseconds in this field's
   * value
   * @property {Number}
   */

  get milliseconds() {
    return this.value ? this.value.milliseconds : 0;
  }

  onInternalKeyDown(keyEvent) {
    if (keyEvent.key === 'ArrowUp') {
      this.doSpinUp();
    } else if (keyEvent.key === 'ArrowDown') {
      this.doSpinDown();
    }
  }

  doSpinUp() {
    const me = this;
    let newValue = (me.magnitude || 0) + me.step;
    me._isUserAction = true;

    if (!me.okMin(newValue)) {
      newValue = me.min;
    }

    if (me.okMax(newValue)) {
      me.value = newValue;
    }

    me._isUserAction = false;
  }

  doSpinDown() {
    const me = this;
    let newValue = (me.magnitude || 0) - me.step;

    if (!me.okMax(newValue)) {
      newValue = me.max;
    }

    if (me.okMin(newValue) && me.allowNegative || (me.magnitude || 0) > 0) {
      me._isUserAction = true;
      me.value = newValue;
      me._isUserAction = false;
    }
  }

} // Register this widget type with its Factory

DurationField.initClass();
DurationField._$name = 'DurationField';

/**
 * @module Core/widget/FileField
 */

/**
 * Filefield widget. Wraps native &lt;input type="file"&gt;.
 *
 * {@inlineexample widget/FileField.js vertical}
 *
 * There is a nicer styled wrapper for this field, see {@link Core/widget/FilePicker}
 *
 * @extends Core/widget/Field
 * @classType filefield
 */

class FileField extends Field {
  static get $name() {
    return 'FileField';
  } // Factoryable type name

  static get type() {
    return 'filefield';
  }

  internalOnChange(event) {
    // Event order is not consistent across browsers:
    //
    //  Chrome/Firefox:             IE11:                       Edge:
    //      internalOnInput             internalOnChange            internalOnChange
    //      internalOnChange            internalOnInput
    //
    // The problem this creates is that the onChange logic expects value != lastValue which is ensured by the
    // onInput handler. In the IE11/Edge sequence, the first change event is ignored, but with a file input that
    // is the only one you get and so the component's change event does not fire. The user has to make a second
    // file selection to get the event to fire.
    //
    // Fortunately, the event sequence for a file input is simple, so we can just force that order here:
    super.internalOnInput(event);
    super.internalOnChange(event);
  }

  internalOnInput() {// ignore -- see internalOnChange above
  }

  static get configurable() {
    return {
      /**
       * Set to true to allow picking multiple files. Note that when set to a truthy value,
       * the field is set to accept multiple files, but the value returned will be
       * an empty string since this is what is rendered into the HTML.
       * @config {Boolean}
       * @default
       */
      multiple: null,

      /**
       * Comma-separated list of file extensions or MIME type to to accept. E.g.
       * ".jpg,.png,.doc" or "image/*". Null by default, allowing all files.
       * @config {String}
       */
      accept: null,
      inputType: 'file',
      attributes: ['multiple', 'accept']
    };
  }
  /**
   * Returns list of selected files
   * @property {FileList}
   * @readonly
   */

  get files() {
    return this.input.files;
  }
  /**
   * Opens browser file picker
   * @internal
   */

  pickFile() {
    this.input.click();
  }

  get multiple() {
    return this._multiple ? '' : null;
  }
  /**
   * Clears field value
   */

  clear() {
    this.input.value = null;
  }

  triggerChange(event) {
    this.triggerFieldChange({
      event,
      value: this.input.value,
      oldValue: this._lastValue,
      userAction: true,
      valid: true
    });
  }

} // Register this widget type with its Factory

FileField.initClass();
FileField._$name = 'FileField';

/**
 * @module Core/widget/FilePicker
 */

/**
 * File input field wrapped into {@link Core/widget/Button button}. Clicking button opens browser file picker window.
 * When files are chosen, badge appears showing amount of files. Hovering the button shows tip with file names.
 *
 * By default only single file allowed.
 *
 * @extends Core/widget/Container
 * @example
 *
 * let fileField = new FilePicker({
 *   fileFieldConfig : {
 *      multiple : true,
 *      accept   : "image/*"
 *   },
 *   buttonConfig : {
 *       text : 'Pick file...'
 *   }
 * });
 *
 * @classType filepicker
 * @externalexample widget/FilePicker.js
 */

class FilePicker extends Container {
  static get $name() {
    return 'FilePicker';
  } // Factoryable type name

  static get type() {
    return 'filepicker';
  }

  static get defaultConfig() {
    return {
      /**
       * The name of the property to set when a single value is to be applied to this FilePicker. Such as when used
       * in a grid WidgetColumn, this is the property to which the column's `field` is applied.
       * @config {String}
       * @default
       * @category Misc
       */
      defaultBindProperty: 'value',

      /**
       * Fires after user closes file picker dialog.
       * @event change
       * @param {FileList} files List of picked files
       */

      /**
       * Fires when field is cleared with {@link #function-clear} method
       * @event clear
       */

      /**
       * Wrapper button config object. See {@link Core/widget/Button} for list of available configs.
       * @config {Object}
       */
      buttonConfig: null,

      /**
       * Underlying field config object. See {@link Core/widget/FileField} for list of available configs.
       * @config {Object}
       */
      fileFieldConfig: null
    };
  }

  construct(config = {}) {
    const me = this;
    config.items = [Object.assign({
      type: 'button',
      ref: 'fileButton',
      text: 'L{FilePicker.file}',
      localeClass: this
    }, config.buttonConfig), Object.assign({
      type: 'filefield',
      ref: 'fileField',
      style: 'display: none'
    }, config.fileFieldConfig)].concat(config.items || []);
    super.construct(config);
    me.button.on({
      click: me.onButtonClick,
      thisObj: me
    });
    me.fileField.on({
      change: me.onFileFieldChange,
      thisObj: me
    });

    me._thisIsAUsedExpression(me.fileTip);
  }

  get button() {
    return this.widgetMap.fileButton;
  }

  get fileField() {
    return this.widgetMap.fileField;
  }
  /**
   * List of selected files
   * @property {FileList}
   * @readonly
   */

  get files() {
    return this.fileField.files;
  }

  get fileTip() {
    const me = this;
    return me._fileTip || (me._fileTip = new Tooltip({
      cls: 'b-file-tip',
      forElement: me.button.element,
      showOnHover: true,
      align: 'b-t',
      scrollAction: 'realign',
      listeners: {
        beforeshow() {
          const tip = this,
                files = me.files;

          if (files && files.length) {
            tip.html = `${Array.from(files).map(file => file.name).join('<br>')}`;
            return true;
          } // Veto show

          return false;
        }

      }
    }));
  }
  /**
   * Clears field
   */

  clear() {
    const me = this;
    me.fileField.clear();
    me.button.badge = '';
    me.trigger('clear');
  }

  onButtonClick({
    event
  }) {
    const me = this; // forward click to the file input to open browser file picker
    // me.fileField.input.click();

    me.fileField.pickFile();
    event.preventDefault();
  }

  onFileFieldChange({
    valid
  }) {
    const me = this;
    me.button.badge = me.files.length || '';
    me.triggerFieldChange({
      files: me.files,
      valid
    });
  }

} // Register this widget type with its Factory

FilePicker.initClass();
FilePicker._$name = 'FilePicker';

/**
 * @module Core/widget/FilterField
 */

/**
 * A simple text field for filtering a store.
 *
 * Allows filtering by {@link #config-field field}:
 *
 * ```javascript
 * const filterField = new FilterField({
 *    store : eventStore,
 *    field : 'name'
 * });
 * ```
 *
 * Or by using a {@link #config-filterFunction filter function} for greater control/custom logic:
 *
 * ```javascript
 * const filterField = new FilterField({
 *    store          : eventStore,
 *    filterFunction : (record, value) => record.name.includes(value)
 * });
 * ```
 *
 * @extends Core/widget/TextField
 * @classType filterfield
 */

class FilterField extends TextField {
  static get $name() {
    return 'FilterField';
  } // Factoryable type name

  static get type() {
    return 'filterfield';
  }

  static get configurable() {
    return {
      /**
       * The model field name to filter by. Can optionally be replaced by {@link #config-filterFunction}
       * @config {String}
       * @category Filtering
       */
      field: null,

      /**
       * The store to filter.
       * @config {Core.data.Store}
       * @category Filtering
       */
      store: null,

      /**
       * Optional filter function to be called with record and value as parameters for store filtering.
       * ```javascript
       * {
       *     type           : 'filterfield',
       *     store          : myStore,
       *     filterFunction : (record, value)  => {
       *        return record.text.includes(value);
       *     }
       * }
       * ```
       * @param {Core.data.Model} record Record for comparison
       * @param {String} value Value to compare with
       * @return {Boolean} Return true if record matches comparison requirements
       * @config {Function}
       * @category Filtering
       */
      filterFunction: null,
      clearable: true,
      keyStrokeChangeDelay: 100,

      onChange({
        value
      }) {
        const {
          store,
          field,
          filterFunction
        } = this;

        if (store) {
          const filterId = `${field || this.id}-Filter`;

          if (value.length === 0) {
            store.removeFilter(filterId);
          } else {
            let filterBy;

            if (filterFunction) {
              filterBy = record => filterFunction(record, value);
            } else {
              // We filter using a RegExp, so quote significant characters
              value = value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

              filterBy = record => record.get(field).match(new RegExp(value, 'i'));
            } // A filter with an id replaces any previous filter with that id.
            // Leave any other filters which may be in use in place.

            store.filter({
              id: filterId,
              filterBy
            });
          }
        }
      }

    };
  }

}
FilterField.initClass();
FilterField._$name = 'FilterField';

/**
 * @module Core/widget/MenuItem
 */

const bIcon$1 = /^b-icon-/,
      bFa$1 = /^b-fa-/;
/**
 * A widget representing a single menu item in a {@link Core.widget.Menu}. May be configured with a
 * {@link #config-checked} state which creates a checkbox which may be toggled. Can also be
 * {@link Core.widget.Widget#config-disabled}, which affects item appearance and blocks interactions.
 *
 * Fires events when activated which bubble up through the parent hierarchy
 * and may be listened for on an ancestor. See {@link Core.widget.Menu Menu}
 * for more details on usage.
 *
 * @extends Core/widget/Widget
 * @classType menuitem
 */

class MenuItem extends Widget {
  //region Config
  static get $name() {
    return 'MenuItem';
  } // Factoryable type name

  static get type() {
    return 'menuitem';
  }

  static get configurable() {
    return {
      /**
       * If configured with a `Boolean` value, a checkbox is displayed
       * as the start icon, and the {@link #event-toggle} event is fired
       * when the checked state changes.
       * @config {Boolean}
       */
      checked: null
    };
  }

  static get defaultConfig() {
    return {
      /**
       * A submenu configuration object, or an array of MenuItem configuration
       * objects from which to create a submenu.
       *
       * Note that this does not have to be a Menu. The `type` config can be used
       * to specify any widget as the submenu.
       * @config {Object|Object[]}
       */
      menu: null,

      /**
       * Item icon class.
       *
       * All [Font Awesome](https://fontawesome.com/cheatsheet) icons may also be specified as `'b-fa-' + iconName`.
       *
       * Otherwise this is a developer-defined CSS class string which results in the desired icon.
       * @config {String}
       */
      icon: null,

      /**
       * The text to be displayed in the item
       * @config {String}
       */
      text: null,

      /**
       * By default, upon activate, non-checkbox menu items will collapse
       * the owning menu hierarchy.
       *
       * Configure this as `false` to cause the menu to persist after
       * activating an item
       * @config {Boolean}
       */
      closeParent: null,

      /**
       * If provided, turns the menu item into a link
       * @config {String}
       */
      href: null,

      /**
       * The `target` attribute for the {@link #config-href} config
       * @config {String}
       */
      target: null,
      localizableProperties: ['text']
    };
  }
  /**
   * Actions this item. Fires the {@link #event-item} event, and if this
   * if a {@link #config-checked} item, toggles the checked state, firing
   * the {@link #event-toggle} event.
   */

  doAction(event) {
    const item = this,
          menu = this.parent,
          itemEvent = {
      menu,
      item,
      element: item.element,
      bubbles: true
    };

    if (typeof item.checked === 'boolean') {
      item.checked = !item.checked;
    } // Give internal handlers a chance to inject extra information before
    // user-supplied "item" handlers see the event.
    // Grid's CellMenu feature, HeaderMenu feature and other context menu features do this.

    item.trigger('beforeItem', itemEvent);
    /**
     * This menu item has been activated.
     *
     * Note that this event bubbles up through parents and can be
     * listened for on a top level {@link Core.widget.Menu Menu} for convenience.
     * @event item
     * @param {Core.widget.MenuItem} item - The menu item which is being actioned.
     * @param {Core.widget.Menu} menu - Menu containing the menu item
     */

    item.trigger('item', itemEvent); // Collapse the owning menu hierarchy if configured to do so

    if (item.closeParent && menu) {
      menu.rootMenu.close(); // Don't prevent links doing their thing

      if (event && !item.href) {
        event.preventDefault();
      }
    }
  }

  doDestroy() {
    const menu = this._menu;

    if (menu instanceof Widget) {
      menu.destroy();
    }

    super.doDestroy();
  }

  template(me) {
    const icon = me.icon || (typeof me.checked === 'boolean' ? `b-fw-icon b-icon-${me.checked ? '' : 'un'}checked` : ''),
          iconExtraCls = bIcon$1.test(icon) ? ' b-icon' : bFa$1.test(icon) ? ' b-fa' : '',
          tag = me.href ? 'a' : 'div';
    return TemplateHelper.tpl`
            <${tag} ${tag === 'a' ? `href="${me.href}"` : ''} ${tag === 'a' && me.target ? `target="${me.target}"` : ''} class="${me.hasMenu ? 'b-has-submenu' : ''}" ${me.name ? `data-name="${me.name}"` : ''} tabIndex="-1">
                ${icon ? `<i class="b-menuitem-icon ${icon}${iconExtraCls}" data-reference="iconElement"></i>` : ''}
                <span class="b-menu-text" data-reference="textElement">${me.text}</span>
                ${me.hasMenu ? '<i class="b-fw-icon b-icon-sub-menu" data-reference="subMenuIcon"></i>' : ''}
            </${tag}>`;
  }

  get focusElement() {
    return this.element;
  }

  get contentElement() {
    return this.textElement;
  }

  get isFocusable() {
    const focusElement = this.focusElement; // We are only focusable if the focusEl is deeply visible, that means
    // it must have layout - an offsetParent. Body does not have offsetParent.
    // Disabled menu items are focusable but cannot be activated.
    // https://www.w3.org/TR/wai-aria-practices/#h-note-17

    return focusElement && this.isVisible && (focusElement === document.body || focusElement.offsetParent);
  }

  onFocusIn(e) {
    super.onFocusIn(e);

    if (!this.disabled && this.menu) {
      this.openMenu();
    }
  }

  onFocusOut(e) {
    super.onFocusOut(e);
    this.closeMenu();
  }

  openMenu(andFocus) {
    const menu = this.menu;

    if (!this.disabled && menu) {
      menu.focusOnToFront = andFocus;
      menu.show();
      this.owner.currentSubMenu = menu;
    }
  }

  closeMenu() {
    if (this._menu instanceof Widget) {
      this.menu.close();
    }
  }
  /**
   * Get/sets the checked state of this `MenuItem` and fires the {@link #event-toggle}
   * event upon change.
   *
   * Note that this must be configured as a `Boolean` to enable the checkbox UI.
   * @member {Boolean} checked
   */

  changeChecked(checked, old) {
    if (!this.isConfiguring && !(typeof old === 'boolean')) {
      return;
    }

    return Boolean(checked);
  }

  updateChecked(checked) {
    const me = this;

    if (!me.isConfiguring) {
      me.iconElement.classList[checked ? 'add' : 'remove']('b-icon-checked');
      me.iconElement.classList[checked ? 'remove' : 'add']('b-icon-unchecked');
      /**
       * The checked state of this menu item has changed.
       *
       * Note that this event bubbles up through parents and can be listened for on a top level
       * {@link Core.widget.Menu Menu} for convenience.
       * @event toggle
       * @param {Core.widget.MenuItem} item - The menu item whose checked state changed.
       * @param {Core.widget.Menu} menu - Menu containing the menu item
       * @param {Boolean} checked - The _new_ checked state.
       */

      me.trigger('toggle', {
        menu: me.owner,
        item: me,
        checked,
        element: me.element,
        bubbles: true
      });
    }
  }

  set menu(menu) {
    this._menu = menu;
  }

  get text() {
    return this.html;
  }

  set text(text) {
    this.html = text;
  }
  /**
   * Returns the instantiated menu widget as configured by {@link #config-menu}.
   * @property {Core.widget.Widget}
   * @readonly
   */

  get menu() {
    const me = this;
    let result = me._menu; // Asking for the menu after it's been destroyed means we no longer have a menu

    if (result && result.isDestroyed) {
      result = me._menu = null;
    }

    if (result && !(result instanceof Widget)) {
      // This covers both Array and Object which are valid items config formats.
      // menu could be { itemRef : { text : 'sub item 1 } }. But if it has
      // child items or html property in it, it's the main config
      if (typeof result === 'object' && !('items' in result || 'widgets' in result || 'html' in result)) {
        result = {
          lazyItems: result
        };
      }

      result = me.menu = Widget.create(Object.assign({
        type: 'menu',
        autoShow: false,
        autoClose: true,
        scrollAction: me.owner.scrollAction,
        constrainTo: me.owner.constrainTo,
        forElement: me.element,
        align: 'l0-r0',
        anchor: true,
        owner: me,
        cls: 'b-sub-menu' // Makes the anchor hoverable to avoid mouseleave

      }, result));
    }

    return result;
  }

  set closeParent(closeParent) {
    this._closeParent = closeParent;
  }

  get closeParent() {
    const result = typeof this.checked === 'boolean' ? this._closeParent : this._closeParent !== false;
    return result && !this.hasMenu;
  }

  get hasMenu() {
    const menu = this.isConfiguring ? this.initialConfig.menu : this._menu;
    return menu && (menu instanceof Widget || typeof menu === 'object' || menu.length > 0);
  }

  get childItems() {
    const {
      menu
    } = this;
    return menu ? [menu] : [];
  }

} // Register this widget type with its Factory

MenuItem.initClass();
MenuItem._$name = 'MenuItem';

const validKeys = {
  ArrowUp: 1,
  ArrowDown: 1,
  ArrowRight: 1,
  ArrowLeft: 1,
  Enter: 1,
  Escape: 1
};
/**
 * @module Core/widget/Menu
 */

/**
 * Menu widget, displays a list of items which the user can select from using mouse or keyboard. Can have submenus.
 *
 * @example
 * let menu = new Menu({
 *     forElement : btn.element,
 *     items      : [
 *         {
 *             icon : 'b-icon b-icon-add',
 *             text : 'Add'
 *         },
 *         {
 *             icon : 'b-icon b-icon-trash',
 *             text : 'Remove'
 *         },
 *         {
 *             icon     : 'b-icon b-icon-lock',
 *             disabled : true,
 *             text     : 'I am disabled'
 *         },
 *         {
 *             text : 'Sub menu',
 *             menu : [{
 *                 icon : 'b-icon b-fa-play',
 *                 text : 'Play'
 *             }]
 *         }
 *     ],
 *     // Method is called for all ancestor levels
 *     onItem({ item }) {
 *         Toast.show('You clicked ' + item.text);
 *     }
 * });
 *
 * @classType menu
 * @externalexample widget/Menu.js
 *
 * @extends Core/widget/Popup
 */

class Menu extends Popup {
  //region Config
  static get $name() {
    return 'Menu';
  } // Factoryable type name

  static get type() {
    return 'menu';
  }

  static get configurable() {
    return {
      focusable: true,
      align: 't-b',
      scrollAction: 'hide',

      /**
       * Specify false to prevent the menu from getting focus when hovering items
       * @default
       * @config {Boolean}
       */
      focusOnHover: null,
      // We do need a Scroller so that we can use its API to scroll around.
      // But the overflow flags default to false.
      scrollable: false,
      defaultType: 'menuitem',
      tools: {
        // To get rid of the close tool from Popup
        close: false
      }
    };
  } //endregion

  /**
   * A descendant menu item has been activated.
   *
   * Note that this event bubbles up through parents and can be
   * listened for on a top level {@link Core.widget.Menu Menu} for convenience.
   * @event item
   * @param {Core.widget.MenuItem} item - The menu item which is being actioned.
   * @param {Core.widget.Menu} menu - Menu containing the menu item
   */

  /**
   * The checked state of a descendant menu item has changed.
   *
   * Note that this event bubbles up through parents and can be
   * listened for on a top level {@link Core.widget.Menu Menu} for convenience.
   * @event toggle
   * @param {Core.widget.MenuItem} item - The menu item whose checked state changed.
   * @param {Core.widget.Menu} menu - Menu containing the menu item
   * @param {Boolean} checked - The _new_ checked state.
   */
  //region Init

  construct(config) {
    if (Array.isArray(config)) {
      config = {
        lazyItems: config
      };
    }

    super.construct(config);
    EventHelper.on({
      element: this.element,
      click: 'onMouseClick',
      mouseover: 'onMouseOver',
      mouseleave: 'onMouseLeave',
      thisObj: this
    });
  }

  afterShow(resolveFn) {
    // Don't instantiate all our items' subMenus right now.
    // Use our private _menu property which will still be a config item.
    const hasSubmenu = this.items.some(item => Boolean(item._menu)); // afterShow is called before alignment, so this is the correct time
    // to mutate things which will change this Widget's size.

    if (hasSubmenu) {
      this.element.classList.add('b-menu-with-submenu');
    } // Add CSS class to menu if any item has an icon, to allow aligning icon-less items

    const hasIcon = this.items.some(item => item.icon);

    if (hasIcon) {
      this.element.classList.add('b-menu-with-icon');
    }

    super.afterShow(resolveFn);
  }

  createWidget(item) {
    if (typeof item === 'string') {
      item = {
        text: item
      };
    }

    return super.createWidget(item);
  }

  get focusElement() {
    var _this$parentMenu;

    const fromParentMenu = (_this$parentMenu = this.parentMenu) === null || _this$parentMenu === void 0 ? void 0 : _this$parentMenu.element.contains(DomHelper.activeElement),
          firstWidget = this.items[0];

    if (fromParentMenu || !(firstWidget instanceof MenuItem)) {
      return super.focusElement;
    }

    return this.element;
  } //endregion

  onDocumentMouseDown(event) {
    // It's not a click outside if its a click on our owner Menu
    if (!this.parentMenu || !this.parentMenu.owns(event.event.target)) {
      super.onDocumentMouseDown(event);
    }
  } //region Show

  hide(animate) {
    // We need to be _hidden when any focused descendants try to revertFocus
    // so that they continue to fall back through the getFocusRevertTarget upward chain.
    super.hide(animate);

    if (this.currentSubMenu && !this.currentSubMenu.isDestroyed) {
      this.currentSubMenu.hide(animate);
    }

    if (this.parentMenu) {
      this.parentMenu.currentSubMenu = null;
    }
  } //endregion
  //region Events

  /**
   * Activates a menu item if user clicks on it
   * @private
   */

  onMouseClick(event) {
    const me = this,
          menuItem = event.target.closest('.b-menuitem');

    if (menuItem) {
      me.triggerElement(menuItem, event); // IE / Edge still triggers event listeners that were removed in a listener - prevent this

      event.stopImmediatePropagation();
    }
  }
  /**
   * Activates menu items on hover. On real mouse hover, not on a touchstart.
   * @private
   */

  onMouseOver(event) {
    const me = this;

    if (this.focusOnHover !== false) {
      const fromItemElement = DomHelper.up(event.relatedTarget, '.b-widget'),
            toItemElement = DomHelper.up(event.target, '.b-widget'),
            overItem = Widget.fromElement(toItemElement); // Activate soon in case they're moving fast over items.

      if (!DomHelper.isTouchEvent && toItemElement && toItemElement !== fromItemElement && overItem.parent === this) {
        me.setTimeout({
          fn: 'handleMouseOver',
          delay: 30,
          args: [overItem],
          cancelOutstanding: true
        });
      }
    }
  }

  handleMouseOver(overItem) {
    overItem.focus();
  } // unselect any menu item if mouse leaves the menu element (unless it enters a child menu)

  onMouseLeave(event) {
    const me = this,
          {
      relatedTarget
    } = event,
          leavingToChild = relatedTarget && me.owns(relatedTarget);
    let targetCmp = relatedTarget && relatedTarget instanceof HTMLElement && Widget.fromElement(relatedTarget),
        shouldHideMenu = !leavingToChild;

    if (targetCmp) {
      while (targetCmp.ownerCmp) {
        targetCmp = targetCmp.ownerCmp;
      } // Or was found and does not belong to current menu DOM tree
      // This condition will not allow possibly existing picker to hide
      // Covered by Menu.t.js

      shouldHideMenu &= !DomHelper.getAncestor(targetCmp.element, [event.target]);
    }

    if (!leavingToChild && shouldHideMenu) {
      me.currentSubMenu && me.currentSubMenu.hide();
      me.currentSubMenu = me.selectedElement = null; // Deactivate currently active *menu items* on mouseleave

      if (me.element.contains(DomHelper.activeElement) && DomHelper.activeElement.matches('.b-menuitem')) {
        me.focusElement.focus();
      }
    }
  }
  /**
   * Keyboard navigation. Up/down, close with esc, activate with enter
   * @private
   */

  onInternalKeyDown(event) {
    const sourceWidget = Widget.fromElement(event),
          isFromWidget = sourceWidget && sourceWidget !== this && !(sourceWidget instanceof MenuItem);

    if (event.key === 'Escape') {
      // Only close this menu if the ESC was in a child input Widget
      (isFromWidget ? this : this.rootMenu).close();
      return;
    }

    super.onInternalKeyDown(event); // Do not process keys from certain elemens

    if (isFromWidget) {
      return;
    }

    if (validKeys[event.key]) {
      event.preventDefault();
    }

    const active = DomHelper.activeElement,
          el = this.element;
    this.navigateFrom(active !== el && el.contains(active) ? active : null, event.key, event);
  }

  navigateFrom(active, key, event) {
    const me = this,
          treeWalker = me.treeWalker,
          item = active && me.getItem(active);
    let toActivate;

    switch (key) {
      case 'ArrowUp':
        treeWalker.currentNode = active || (active = me.bottomFocusTrap);
        treeWalker.previousNode();
        toActivate = treeWalker.currentNode;
        break;

      case 'ArrowDown':
        treeWalker.currentNode = active || (active = me.topFocusTrap);
        treeWalker.nextNode();
        toActivate = treeWalker.currentNode;
        break;

      case ' ':
        if (active && !active.classList.contains('b-disabled')) {
          if (item && item.menu) {
            me.openSubMenu(active, item);
          } else {
            me.triggerElement(active, event);
          }
        }

        break;

      case 'ArrowRight':
        if (active && item && item.menu && !active.classList.contains('b-disabled')) {
          // opening with arrow keys highlights first item (as in menus on mac)
          const openedMenu = me.openSubMenu(active, item); // If show hs not been vetoed, ask it to focus.
          // Container will delegate focus inward if possible.

          if (openedMenu) {
            openedMenu.focus();
          }
        } else {
          treeWalker.currentNode = active || (active = me.topFocusTrap);
          treeWalker.nextNode();
          toActivate = treeWalker.currentNode;
        }

        break;

      case 'ArrowLeft':
        if (me.isSubMenu) {
          me.hide();
        } else if (!active) {
          treeWalker.currentNode = active || (active = me.topFocusTrap);
          treeWalker.nextNode();
          toActivate = treeWalker.currentNode;
        }

        break;

      case 'Enter':
        if (active && !active.classList.contains('b-disabled')) {
          me.triggerElement(active, event);
        }

        break;
    } // Move focus to wherever we have calculated

    if (toActivate) {
      // Previous moved to encapsulating element; wrap from end
      if (toActivate === me.element) {
        me.navigateFrom(me.bottomFocusTrap, 'ArrowUp', event);
      } // Next could not move because we're at the end; wrap from top
      else if (toActivate === active) {
          me.navigateFrom(me.topFocusTrap, 'ArrowDown', event);
        } else {
          toActivate.focus();
        }
    }
  } //endregion
  //region Activate menu item

  getItem(item) {
    // Cannot use truthiness test because index zero may be passed.
    if (item != null) {
      // Access by index
      if (typeof item === 'number') {
        return this.items[item];
      } // Access by element

      if (item.nodeType === 1) {
        return Widget.fromElement(item, 'menuitem', this.contentElement);
      } // Access by id
      else {
          return this.items.find(c => c.id == item);
        }
    }
  }
  /**
   * Activate a menu item (from its element)
   * @private
   * @fires item
   * @param menuItemElement
   */

  triggerElement(menuItemElement, event) {
    const menu = this,
          item = menu.getItem(menuItemElement); // If the trigger gesture happened on a non-MenuItem
    // item will be undefined. Do not action on a non-MenuItem
    // or a disabled MenuItem

    if (item && !item.disabled) {
      item.doAction(event);
    }
  }
  /**
   * Returns true if this menu is a sub menu.
   * To find out which menu is the parent, check {@link #property-parentMenu}.
   * @type {boolean}
   * @readonly
   */

  get isSubMenu() {
    return this.owner && this === this.owner.menu;
  }
  /**
   * Opens a submenu anchored to a menu item
   * @private
   * @param element
   * @param item
   */

  openSubMenu(element, item) {
    const me = this,
          subMenu = item.menu;

    if (subMenu) {
      if (!subMenu.isVisible) {
        const event = {
          item,
          element
        };

        if (me.trigger('beforeSubMenu', event) === false) {
          return;
        }

        if (item.onBeforeSubMenu && item.onBeforeSubMenu(event) === false) {
          return;
        }

        subMenu.show();
      }
      /**
       * Currently open sub menu, if any
       * @member {Core.widget.Menu} currentSubMenu
       * @readonly
       */

      return me.currentSubMenu = subMenu;
    }
  }
  /**
   * Get/set focused menu item.
   * Shows submenu if newly focused item has a menu and is not disabled.
   * @property {HTMLElement}
   */

  set selectedElement(element) {
    const me = this,
          lastSelected = me._selectedElement;

    if (lastSelected) {
      const lastItem = me.getItem(lastSelected),
            lastItemMenu = lastItem && lastItem.menu;

      if (lastItemMenu) {
        lastItemMenu.hide();
      }

      lastSelected.classList.remove('b-active');
    }

    me._selectedElement = element; // might set to null to deselect

    if (element) {
      const doFocus = DomHelper.isFocusable(element);
      element.classList.add('b-active');
      me.scrollable.scrollIntoView(element, {
        animate: !doFocus,
        focus: doFocus
      });
    }
  }

  get selectedElement() {
    return this._selectedElement;
  }

  selectFirst() {
    const treeWalker = this.treeWalker;
    treeWalker.currentNode = this.topFocusTrap;
    treeWalker.nextNode(); // If we are under keyboard control, this must happen in the next
    // animation frame so that the keydown event doesn't fire on the
    // newly focused node.

    this.requestAnimationFrame(() => treeWalker.currentNode.focus());
  } //endregion
  //region Close

  /**
   * Gets the parent Menu if this Menu is a submenu, or `undefined`.
   * @property {Core.widget.Menu}
   */

  get parentMenu() {
    let result = this.owner;

    if (result instanceof MenuItem) {
      result = result.owner;
    }

    if (result instanceof Menu) {
      return result;
    }
  }
  /**
   * Gets this menus root menu, the very first menu shown in a sub menu hierarchy
   * @property {Core.widget.Menu}
   * @private
   */

  get rootMenu() {
    let menu = this;

    while (menu.parentMenu && menu.parentMenu instanceof this.constructor) {
      menu = menu.parentMenu;
    }

    return menu;
  } //endregion

} // Register this widget type with its Factory

Menu.initClass();
Menu._$name = 'Menu';

/**
 * A singleton modal dialog box which can be used to ask the user to confirm or reject actions.
 *
 * @extends Core/widget/Popup
 * @externalexample widget/MessageDialog.js
 * @singleton
 */

class MessageDialog extends Popup {
  static get $name() {
    return 'MessageDialog';
  } // Factoryable type name

  static get type() {
    return 'messagedialog';
  }

  static get configurable() {
    return {
      id: 'bryntum-msgdialog',
      centered: true,
      modal: true,
      hidden: true,
      autoShow: false,
      closeAction: 'hide',
      title: '\xa0',
      lazyItems: {
        $config: ['lazy'],
        value: [{
          type: 'widget',
          id: 'bryntum-msgdialog-message',
          cls: 'b-msgdialog-message',
          ref: 'message'
        }, {
          type: 'textfield',
          id: 'bryntum-msgdialog-input',
          cls: 'b-msgdialog-input',
          ref: 'input'
        }]
      },
      bbar: {
        id: 'bryntum-msgdialog-bbar',
        items: [{
          ref: 'yesButton',
          id: 'bryntum-msgdialog-yesbutton',
          cls: 'b-msgdialog-yesbutton b-green',
          text: 'L{Object.Yes}',
          onClick: 'up.onYesClick'
        }, {
          ref: 'noButton',
          id: 'bryntum-msgdialog-nobutton',
          cls: 'b-msgdialog-nobutton b-gray',
          text: 'L{Object.No}',
          onClick: 'up.onNoClick'
        }, {
          ref: 'cancelButton',
          id: 'bryntum-msgdialog-cancelbutton',
          cls: 'b-msgdialog-cancelbutton b-gray',
          text: 'L{Object.Cancel}',
          onClick: 'up.onCancelClick'
        }]
      }
    };
  }

  construct() {
    /**
     * The enum value for the no button
     * @property {Number} noButton
     * @type {Number}
     * @readOnly
     */
    this.noButton = 0;
    /**
     * The enum value for the yes button
     * @property {Number} yesButton
     * @type {Number}
     * @readOnly
     */

    this.yesButton = 1;
    /**
     * The enum value for the cancel button
     * @property {Number} cancelButton
     * @type {Number}
     * @readOnly
     */

    this.cancelButton = 3;
    super.construct(...arguments);
  }
  /**
   * Shows a confirm dialog with "Yes" and "No" buttons. The returned promise resolves passing `true`
   * if the "yes" button is pressed, and `false` if the "No" button is pressed. Typing `ESC` rejects.
   * @async
   * @param {Object} options An options object for what to show.
   * @param {String} [options.title] The title to show in the dialog header.
   * @param {String} [options.message] The message to show in the dialog body.
   * @returns {Promise} A promise which is resolved when the dialog is shown
   */

  async confirm({
    message,
    title = '\xa0'
  }) {
    const me = this; // Ensure our child items are instanced

    me.getConfig('lazyItems');
    me.title = me.optionalL(title);
    me.element.classList.remove(me.showClass);

    if (message) {
      me.showClass = 'b-show-message-yes-no';
      me.widgetMap.message.html = me.optionalL(message);
    } else {
      me.showClass = 'b-show-yes-no';
    }

    me.element.classList.add(me.showClass);
    me.show();
    return me.promise = new Promise(resolve => {
      me.resolve = resolve;
    });
  }

  doResolve(value) {
    const me = this,
          {
      resolve
    } = me;

    if (resolve) {
      me.resolve = me.reject = me.promise = null;
      resolve(value);
      me.hide();
    }
  }

  onInternalKeyDown(event) {
    // Cancel on escape key
    if (event.key === 'Escape') {
      event.stopImmediatePropagation();

      if (this.widgetMap.cancelButton.isVisible) {
        this.onCancelClick();
      } else {
        this.onNoClick();
      }
    }

    super.onInternalKeyDown(event);
  }

  onYesClick() {
    this.doResolve(MessageDialogSingleton.yesButton);
  }

  onNoClick() {
    this.doResolve(MessageDialogSingleton.noButton);
  }

  onCancelClick() {
    this.doResolve(MessageDialogSingleton.cancelButton);
  }

} // Register this widget type with its Factory

MessageDialog.initClass();
const MessageDialogSingleton = new MessageDialog();

/**
 * @module Core/widget/NumberField
 */

/**
 * Number field widget. Similar to native `<input type="number">`, but implemented as `<input type="text">` to support
 * formatting.
 *
 * This field can be used as an {@link Grid.column.Column#config-editor editor} for the {@link Grid.column.Column Column}.
 * It is used as the default editor for the {@link Grid.column.NumberColumn NumberColumn},
 * {@link Grid.column.PercentColumn PercentColumn}, {@link Grid.column.AggregateColumn AggregateColumn}.
 *
 * ```
 * const number = new NumberField({
 *   min   : 1,
 *   max   : 5,
 *   value : 3
 * });
 * ```
 *
 * @extends Core/widget/Field
 * @classType numberfield
 * @externalexample widget/NumberField.js
 */

class NumberField extends Field {
  //region Config
  static get $name() {
    return 'NumberField';
  } // Factoryable type name

  static get type() {
    return 'numberfield';
  } // Factoryable type alias

  static get alias() {
    return 'number';
  }

  static get configurable() {
    return {
      /**
       * Min value
       * @config {Number}
       */
      min: null,

      /**
       * Max value
       * @config {Number}
       */
      max: null,

      /**
        * Step size for spin button clicks.
       * @property {Number}
       * @name step
       */

      /**
       * Step size for spin button clicks. Also used when pressing up/down keys in the field.
       * @config {Number}
       * @default
       */
      step: 1,

      /**
       * Large step size, defaults to 10 * `step`. Applied when pressing SHIFT and stepping either by click or
       * using keyboard.
       * @config {Number}
       * @default 10
       */
      largeStep: 0,

      /**
       * Initial value
       * @config {Number}
       */
      value: null,

      /**
       * The format to use for rendering numbers.
       *
       * For example:
       * ```
       *  format: '9,999.00##'
       * ```
       * The above enables digit grouping and will display at least 2 (but no more than 4) fractional digits.
       * @config {String|Object|Core.helper.util.NumberFormat}
       * @default
       */
      format: '',

      /**
       * The number of decimal places to allow. Defaults to no constraint.
       *
       * This config has been replaced by {@link #config-format}. Instead of this:
       *```
       *  decimalPrecision : 3
       *```
       * Use `format`:
       *```
       *  format : '9.###'
       *```
       * To set both `decimalPrecision` and `leadingZeroes` (say to `3`), do this:
       *```
       *  format : '3>9.###'
       *```
       * @config {Number}
       * @default
       * @deprecated 3.1 Use {@link #config-format} instead.
       */
      decimalPrecision: null,

      /**
       * The maximum number of leading zeroes to show. Defaults to no constraint.
       *
       * This config has been replaced by {@link #config-format}. Instead of this:
       *```
       *  leadingZeros : 3
       *```
       * Use `format`:
       *```
       *  format : '3>9'
       *```
       * To set both `leadingZeroes` and `decimalPrecision` (say to `2`), do this:
       *```
       *  format : '3>9.##'
       *```
       * @config {Number}
       * @default
       * @deprecated 3.1 Use {@link #config-format} instead.
       */
      leadingZeroes: null,
      triggers: {
        spin: {
          type: 'spintrigger'
        }
      },

      /**
       * Controls how change events are triggered when stepping the value up or down using either spinners or
       * arrow keys.
       *
       * Configure with:
       * * `true` to trigger a change event per step
       * * `false` to not trigger change while stepping. Will trigger on blur/Enter
       * * A number of milliseconds to buffer the change event, triggering when no steps are performed during that
       *   period of time.
       *
       * @config {Boolean|Number}
       * @default
       */
      changeOnSpin: true,
      // NOTE: using type="number" has several trade-offs:
      //
      // Negatives:
      //   - No access to caretPos/textSelection. This causes anomalies when replacing
      //     the input value with a formatted version of that value (the caret moves to
      //     the end of the input el on each character typed).
      //   - The above also prevents Siesta/synthetic events from mimicking typing.
      //   - Thousand separators cannot be displayed (input.value = '1,000' throws an
      //     exception).
      // Positives:
      //   - On mobile, the virtual keyboard only shows digits et al.
      //   - validity property on DOM node that handles min/max checks.
      //
      // The above may not be exhaustive, but there is not a compelling reason to
      // use type="number" except on mobile.

      /**
       * This can be set to `'number'` to enable the numeric virtual keyboard on
       * mobile devices. Doing so limits this component's ability to handle keystrokes
       * and format properly as the user types, so this is not recommended for
       * desktop applications. This will also limit similar features of automated
       * testing tools that mimic user input.
       * @config {String}
       * @default text
       */
      inputType: null
    };
  } //endregion
  //region Init

  construct(config) {
    super.construct(config);
    const me = this; // Support for selecting all by double click in empty input area
    // Browsers work differently at this case

    me.input.addEventListener('dblclick', () => {
      me.select();
    });

    if (typeof me.changeOnSpin === 'number') {
      me.bufferedSpinChange = me.buffer(me.triggerChange, me.changeOnSpin);
    }
  } //endregion
  //region Internal functions

  acceptValue(value, rawValue) {
    let accept = !isNaN(value); // https://github.com/bryntum/support/issues/1349
    // Pass through if there is a text selection in the field. This fixes the case when
    // valid value is typed already and we are replacing it by selecting complete string and typing over it.
    // Cannot be tested in siesta, see ticket for more info.

    if (accept && !this.hasTextSelection) {
      accept = false;
      const raw = this.input.value,
            current = parseFloat(raw);

      if (raw !== rawValue) {
        // The new value is out of range, but we accept it if the current value
        // is also problematic. Consider the case where the input is empty and the
        // minimum value is 100. The user must first type "1" and we must accept it
        // if they are to get the opportunity to type the "0"s.
        accept = !this.acceptValue(current, raw); // Also, if we are checking the current value, be sure not to infinitely
        // recurse here.
      }
    }

    return accept;
  }

  okMax(value) {
    return isNaN(this.max) || value <= this.max;
  }

  okMin(value) {
    return isNaN(this.min) || value >= this.min;
  }

  internalOnKeyEvent(e) {
    if (e.type === 'keydown') {
      const me = this,
            key = e.key;
      let block; // Native arrow key spin behaviour differs between browsers, so we replace
      // the native spinners w/our own triggers and handle arrows keys as well.

      if (key === 'ArrowUp') {
        me.doSpinUp(e.shiftKey);
        block = true;
      } else if (key === 'ArrowDown') {
        me.doSpinDown(e.shiftKey);
        block = true;
      } else if (!e.altKey && !e.ctrlKey && key && key.length === 1) {
        // The key property contains the character or key name... so ignore
        // keys that aren't a single character.
        const after = me.getAfterValue(key),
              afterValue = me.formatter.parseStrict(after);
        block = !me.acceptValue(afterValue, after);
      }

      if (key === 'Enter' && me._changedBySilentSpin) {
        me.triggerChange(e, true); // reset the flag

        me._changedBySilentSpin = false;
      }

      if (block) {
        e.preventDefault();
      }
    }

    super.internalOnKeyEvent(e);
  }

  doSpinUp(largeStep = false) {
    const me = this;
    let newValue = (me.value || 0) + (largeStep ? me.largeStep : me.step);

    if (!me.okMin(newValue)) {
      newValue = me.min;
    }

    if (me.okMax(newValue)) {
      me.applySpinChange(newValue);
    }
  }

  doSpinDown(largeStep = false) {
    const me = this;
    let newValue = (me.value || 0) - (largeStep ? me.largeStep : me.step);

    if (!me.okMax(newValue)) {
      newValue = me.max;
    }

    if (me.okMin(newValue)) {
      me.applySpinChange(newValue);
    }
  }

  applySpinChange(newValue) {
    const me = this;
    me._isUserAction = true; // Should not trigger change immediately?

    if (me.changeOnSpin !== true) {
      me._changedBySilentSpin = true; // Silence the change

      me.silenceChange = true; // Optionally buffer the change

      me.bufferedSpinChange && me.bufferedSpinChange(null, true);
    }

    me.value = newValue;
    me._isUserAction = false;
    me.silenceChange = false;
  }

  triggerChange() {
    if (!this.silenceChange) {
      super.triggerChange(...arguments);
    }
  }

  onFocusOut(e) {
    super.onFocusOut(...arguments);
    const me = this,
          {
      input
    } = me,
          raw = input.value,
          value = me.formatter.truncate(raw),
          formatted = isNaN(value) ? raw : me.formatValue(value);

    if (raw !== formatted) {
      input.value = formatted;
    }

    if (me._changedBySilentSpin) {
      me.triggerChange(e, true); // reset the flag

      me._changedBySilentSpin = false;
    }
  }

  internalOnInput(event) {
    const me = this,
          {
      formatter,
      input
    } = me,
          {
      parser
    } = formatter,
          raw = input.value,
          decimals = parser.decimalPlaces(raw);

    if (formatter.truncator && decimals) {
      let value = raw;
      const trunc = formatter.truncate(raw);

      if (!isNaN(trunc)) {
        value = me.formatValue(trunc);

        if (parser.decimalPlaces(value) < decimals) {
          // If typing has caused truncation or rounding, reset. To best preserve
          // the caret pos (which is reset by assigning input.value), we grab and
          // restore the distance from the end. This allows special things to format
          // into the string (such as thousands separators) since they always go to
          // the front of the input.
          const pos = raw.length - me.caretPos;
          input.value = value;
          me.caretPos = value.length - pos;
        }
      }
    }

    super.internalOnInput(event);
  }

  formatValue(value) {
    return this.formatter.format(value);
  }

  changeFormat(format) {
    const me = this;

    if (format === '') {
      const {
        leadingZeroes,
        decimalPrecision
      } = me;
      format = leadingZeroes ? `${leadingZeroes}>9` : null;

      if (decimalPrecision != null) {
        format = `${format || ''}9.${'#'.repeat(decimalPrecision)}`;
      } else if (format) {
        // When we only have leadingZeroes, we'll have a format like "4>9", but
        // that will default to 3 decimal digits. Prior behavior implied no limit
        // on decimal digits unless decimalPrecision was specified.
        format += '.*';
      }
    }

    return format;
  }

  get formatter() {
    const me = this,
          format = me.format;
    let formatter = me._formatter;

    if (!formatter || me._lastFormat !== format) {
      formatter = NumberFormat.get(me._lastFormat = format); // TODO use this.formatter.is.from.currency/percent visually...
      // if (!formatter.is.decimal) {
      //     formatter = formatter.as('decimal');
      // }

      me._formatter = formatter;
    }

    return formatter;
  } //endregion
  //region Getters/Setters

  updateStep(step) {
    this.element.classList[step ? 'remove' : 'add']('b-hide-spinner');
    this._step = step;
  }

  changeLargeStep(largeStep) {
    return largeStep || this.step * 10;
  }

  get validity() {
    const value = this.value,
          validity = {}; // Assert range for non-empty fields, empty fields will turn invalid if `required: true`

    if (value != null) {
      validity.rangeUnderflow = !this.okMin(value);
      validity.rangeOverflow = !this.okMax(value);
    }

    validity.valid = !validity.rangeUnderflow && !validity.rangeOverflow;
    return validity;
  }
  /**
   * Get/set the NumberField's value, or `undefined` if the input field is empty
   * @property {Number}
   */

  changeValue(value, was) {
    const me = this;

    if (value || value === 0) {
      let valueIsNaN; // We insist on a number as the value

      if (typeof value !== 'number') {
        value = typeof value === 'string' ? me.formatter.parse(value) : Number(value);
        valueIsNaN = isNaN(value);

        if (valueIsNaN) {
          value = '';
        }
      }

      if (!valueIsNaN && me.format) {
        value = me.formatter.round(value);
      }
    } else {
      value = undefined;
    }

    return super.changeValue(value, was);
  }

  get inputValue() {
    let value = this.value;

    if (value != null && this.format) {
      value = this.formatValue(value);
    }

    return value;
  } //endregion

} // Register this widget type with its Factory

NumberField.initClass();
NumberField._$name = 'NumberField';

/**
 * @module Core/widget/PagingToolbar
 */

/**
 * A special Toolbar class, which, when attached to an {@link Core.data.AjaxStore AjaxStore}, which has been configured
 * to be {@link Core.data.AjaxStore#property-isPaged paged}, controls the loading of that store to page through the data
 * set.
 *
 * ```javascript
 * new Grid({
 *      bbar : {
 *          type  : 'pagingtoolbar'
 *      }
 * });
 * ```
 *
 * @extends Core/widget/Toolbar
 * @classType toolbar
 */

class PagingToolbar extends Toolbar {
  static get $name() {
    return 'PagingToolbar';
  } // Factoryable type name

  static get type() {
    return 'pagingtoolbar';
  }

  static get defaultConfig() {
    return {
      /**
       * The {@link Core.data.AjaxStore AjaxStore} that this PagingToolbar is to control.
       * @config {Core.data.AjaxStore}
       */
      store: null,
      defaults: {
        localeClass: this
      },
      items: {
        firstPageButton: {
          onClick: 'up.onFirstPageClick',
          icon: 'b-fa-angle-double-left'
        },
        previousPageButton: {
          onClick: 'up.onPreviousPageClick',
          icon: 'b-fa-angle-left'
        },
        pageNumber: {
          type: 'numberfield',
          label: 'L{page}',
          min: 1,
          max: 1,
          triggers: null,
          onChange: 'up.onPageNumberChange',
          highlightExternalChange: false
        },
        pageCount: {
          type: 'widget',
          cls: 'b-pagecount b-toolbar-text'
        },
        nextPageButton: {
          onClick: 'up.onNextPageClick',
          icon: 'b-fa-angle-right'
        },
        lastPageButton: {
          onClick: 'up.onLastPageClick',
          icon: 'b-fa-angle-double-right'
        },
        separator: {
          type: 'widget',
          cls: 'b-toolbar-separator'
        },
        reloadButton: {
          onClick: 'up.onReloadClick',
          icon: 'b-fa-redo'
        },
        spacer: {
          type: 'widget',
          cls: 'b-toolbar-fill'
        },
        dataSummary: {
          type: 'widget',
          cls: 'b-toolbar-text'
        }
      }
    };
  } // Retrieve store from grid when "assigned" to it

  set parent(parent) {
    super.parent = parent;

    if (!this.store) {
      this.store = parent.store;
    }
  }

  get parent() {
    return super.parent;
  }

  set store(store) {
    const me = this,
          listener = {
      beforerequest: 'onStoreBeforeRequest',
      afterrequest: 'onStoreChange',
      change: 'onStoreChange',
      thisObj: me
    };

    if (me.store) {
      me.store.un(listener);
    }

    me._store = store;

    if (store) {
      store.on(listener);

      if (store.isLoading) {
        me.onStoreBeforeRequest();
      }
    }
  }

  get store() {
    return this._store;
  }

  onStoreBeforeRequest() {
    this.eachWidget(w => w.disable());
  }

  updateLocalization() {
    const me = this,
          {
      reloadButton,
      firstPageButton,
      previousPageButton,
      nextPageButton,
      lastPageButton
    } = me.widgetMap;
    firstPageButton.tooltip = me.L('L{firstPage}');
    previousPageButton.tooltip = me.L('L{prevPage}');
    nextPageButton.tooltip = me.L('L{nextPage}');
    lastPageButton.tooltip = me.L('L{lastPage}');
    reloadButton.tooltip = me.L('L{reload}');
    me.updateSummary();
    super.updateLocalization();
  }

  updateSummary() {
    const me = this,
          {
      pageCount,
      dataSummary
    } = me.widgetMap;
    let count, lastPage, start, end, allCount;
    count = lastPage = start = end = allCount = 0;

    if (me.store) {
      const {
        store
      } = me,
            {
        pageSize,
        currentPage
      } = store;
      count = store.count;
      lastPage = store.lastPage;
      allCount = store.allCount;
      start = Math.max(0, (currentPage - 1) * pageSize + 1);
      end = Math.min(allCount, start + pageSize - 1);
    }

    pageCount.html = me.L('L{pageCountTemplate}')({
      lastPage
    });
    dataSummary.html = count ? me.L('L{summaryTemplate}')({
      start,
      end,
      allCount
    }) : me.L('L{noRecords}');
  }

  onStoreChange() {
    const me = this,
          {
      widgetMap,
      store
    } = me,
          {
      count,
      lastPage,
      currentPage
    } = store,
          {
      pageNumber,
      pageCount,
      firstPageButton,
      previousPageButton,
      nextPageButton,
      lastPageButton,
      dataSummary
    } = widgetMap;
    me.eachWidget(w => w.enable());
    pageNumber.value = currentPage;
    pageNumber.max = lastPage;
    dataSummary.disabled = pageNumber.disabled = pageCount.disabled = !count;
    firstPageButton.disabled = previousPageButton.disabled = currentPage <= 1 || !count;
    nextPageButton.disabled = lastPageButton.disabled = currentPage >= lastPage || !count;
    me.updateSummary();
  }

  onPageNumberChange({
    value
  }) {
    if (this.store.currentPage !== value) {
      this.store.loadPage(value);
    }
  }

  onFirstPageClick() {
    this.store.loadPage(1);
  }

  onPreviousPageClick() {
    this.store.previousPage();
  }

  onNextPageClick() {
    this.store.nextPage();
  }

  onLastPageClick() {
    this.store.loadPage(this.store.lastPage);
  }

  onReloadClick() {
    this.store.loadPage(this.store.currentPage);
  }

} // Register this widget type with its Factory

PagingToolbar.initClass();
PagingToolbar._$name = 'PagingToolbar';

/**
 * @module Core/widget/Slider
 */

/**
 * Wraps native &lt;input type="range"&gt;
 *
 * @extends Core/widget/Widget
 *
 * @example
 * let slider = new Slider({
 *   text: 'Choose value'
 * });
 *
 * @classType slider
 * @externalexample widget/Slider.js
 */

class Slider extends Widget {
  //region Config
  static get $name() {
    return 'Slider';
  } // Factoryable type name

  static get type() {
    return 'slider';
  }

  static get defaultConfig() {
    return {
      /**
       * Slider label text
       * @config {String}
       */
      text: '',

      /**
       * Show value in label (appends in () if text is set)
       * @config {Boolean}
       * @default
       */
      showValue: true,

      /**
       * Show value in tooltip
       * @config {Boolean}
       * @default
       */
      showTooltip: false,

      /**
       * Minimum value
       * @config {Number}
       * @default
       */
      min: 0,

      /**
       * Maximum value
       * @config {Number}
       * @default
       */
      max: 100,

      /**
       * Step size
       * @config {Number}
       * @default
       */
      step: 1,

      /**
       * Initial value
       * @config {Number}
       */
      value: null,
      // The value is set in the Light theme. The Material theme will have different value.
      thumbSize: 20,
      localizableProperties: ['text']
    };
  } //endregion
  //region Init

  construct(config) {
    const me = this;
    super.construct(config);
    me.updateLabel();

    if (me.showTooltip) {
      me.tip = new Tooltip({
        forElement: me.input,
        anchor: false,
        // No anchor displayed since thumbSize is different for different themes
        align: 'b-t',
        axisLock: true
      });
    }

    me.input.addEventListener('input', me.onInternalInput.bind(me));
    me.input.addEventListener('change', me.onInternalChange.bind(me));
    me.input.addEventListener('mouseover', me.onInternalMouseOver.bind(me));
    me.input.addEventListener('mouseout', me.onInternalMouseOut.bind(me));
  }

  template(widget) {
    return TemplateHelper.tpl`
            <div class="${widget.text ? 'b-text b-has-label' : ''}">
                <input type="range"
                   data-reference="input"
                   id="${widget.id}-input"
                   min="${widget.min}"
                   max="${widget.max}"
                   step="${widget.step}"
                   value="${widget.value}">
                <label data-reference="label" for="${widget.id}-input">${widget.text}</label>
            </div>
        `;
  }

  get focusElement() {
    return this.input;
  } //endregion
  //region Events

  /**
   * Fired while slider thumb is being dragged
   * @event input
   * @param {Core.widget.Slider} source - The slider
   * @param {String} value - The value
   */

  /**
   * Fired after the slider value changes (on mouse up following slider interaction)
   * @event change
   * @param {String} value - Value
   * @param {Boolean} userAction - Triggered by user taking an action (`true`) or by setting a value (`false`)
   * @param {Core.widget.Slider} source - The slider
   */

  onInternalInput(event) {
    const me = this;
    me._value = parseInt(me.input.value);
    me.updateUI();
    me.trigger('input', {
      value: me.value
    });
  }

  onInternalChange(event) {
    const me = this;
    me.updateUI();
    me.triggerChange(true);
    me.trigger('action', {
      value: me.value
    });
  }

  onInternalMouseOver(event) {
    const me = this;
    me.updateLabel();

    if (me.tip) {
      me.tip.showBy({
        target: me.calcThumbPosition(),
        offset: 5
      });
    }
  }

  onInternalMouseOut(event) {
    if (this.tip) {
      this.tip.hide();
    }
  }

  triggerChange(userAction) {
    this.triggerFieldChange({
      value: this.value,
      valid: true,
      userAction
    });
  } //endregion
  //region Getters/setters

  /**
   * Get/Set text. Appends value if Slider.showValue is true
   * @property {String}
   */

  get text() {
    return this._text;
  }

  set text(text) {
    const me = this;
    me._text = text;

    if (me.label) {
      if (me.showValue) {
        text = text ? `${text} (${me.value})` : me.value;
      }

      me.label.innerHTML = text;
    }

    if (me.tip) {
      me.tip.html = me.value;
    }
  }
  /**
   * Get/set value
   * @property {Number}
   */

  get value() {
    return this.input ? parseInt(this.input.value) : this._value;
  }

  set value(value) {
    const me = this;

    if (me._value !== value) {
      if (me.input) {
        me.input.value = value;
        me.updateUI();
        me.triggerChange(false);
      }

      me._value = value;
    }
  }
  /**
   * Get/set min value
   * @property {Number}
   */

  get min() {
    return this.input ? this.input.min : this._min;
  }

  set min(min) {
    const me = this;

    if (me.input) {
      me.input.min = min;

      if (me._value < min) {
        me.value = min;
        me.trigger('input', {
          value: me.value
        });
      }
    }

    me._min = min;
  }
  /**
   * Get/set max value
   * @property {Number}
   */

  get max() {
    return this.input ? this.input.max : this._max;
  }

  set max(max) {
    const me = this;

    if (me.input) {
      me.input.max = max;

      if (me._value > max) {
        me.value = max;
        me.trigger('input', {
          value: me.value
        });
      }
    }

    me._max = max;
  }
  /**
   * Get/set step size
   * @property {Number}
   */

  get step() {
    return this.input ? this.input.step : this._step;
  }

  set step(step) {
    if (this.input) this.input.step = step;
    this._step = step;
  } //endregion
  //region Util

  /**
   * Refresh label text
   * @private
   */

  updateLabel() {
    this.text = this._text;
  }
  /**
   * Refresh tooltip position
   * @private
   */

  updateTooltipPosition() {
    if (this.tip) {
      this.tip.alignTo({
        target: this.calcThumbPosition(),
        offset: 5
      });
    }
  }
  /**
   * Refresh slider UI
   * @private
   */

  updateUI() {
    this.updateLabel();
    this.updateTooltipPosition();
  }

  calcThumbPosition() {
    const me = this,
          inputRect = Rectangle.from(me.input),
          offset = (inputRect.width - me.thumbSize) * me.calcPercentProgress() / 100;
    return new Rectangle(inputRect.x + offset, inputRect.y + inputRect.height / 2 - me.thumbSize / 2, me.thumbSize, me.thumbSize);
  }

  calcPercentProgress() {
    return (this.value - this.min) / (this.max - this.min) * 100;
  } //endregion

} // Register this widget type with its Factory

Slider.initClass();
Slider._$name = 'Slider';

/**
 * @module Core/widget/SlideToggle
 */

/**
 * SlideToggle field is a variation of {@link Core.widget.Checkbox} with a sliding toggle instead of box with check mark.
 * It wraps <code>&lt;input type="checkbox"&gt;</code>.
 * Color can be specified and you can optionally configure {@link #config-text} to display in a label to the right of
 * the toggle in addition to a standard field {@link #config-label}.
 *
 * {@inlineexample widget/SlideToggle.js vertical}
 *
 * This field can be used as an {@link Grid.column.Column#config-editor} for the {@link Grid.column.Column}.
 *
 * @extends Core/widget/Checkbox
 * @classType slidetoggle
 */

class SlideToggle extends Checkbox {
  static get $name() {
    return 'SlideToggle';
  }

  static get type() {
    return 'slidetoggle';
  }

  static get properties() {
    return {
      toggledCls: 'b-slidetoggle-checked'
    };
  }

  construct(config) {
    if (config !== null && config !== void 0 && config.checked) {
      config.cls = (config.cls || '') + ' ' + this.constructor.properties.toggledCls;
    }

    super.construct(config);
  }

  get innerElements() {
    return [this.inputElement, this.toggleElement, {
      tag: 'label',
      class: 'b-slidetoggle-label',
      for: `${this.id}-input`,
      reference: 'textLabel',
      html: this.text || ''
    }];
  }

  get toggleElement() {
    return {
      class: 'b-slidetoggle-toggle',
      reference: 'slideToggle',
      children: [{
        class: 'b-slidetoggle-thumb',
        reference: 'slideThumb'
      }]
    };
  }

  internalOnChange() {
    super.internalOnChange();
    this.element.classList[this.value ? 'add' : 'remove'](this.toggledCls);
  }

}
SlideToggle.initClass();
SlideToggle._$name = 'SlideToggle';

/**
 * @module Core/widget/Splitter
 */

const hasFlex = el => parseInt(DomHelper.getStyleValue(el, 'flex-basis'), 10) || parseInt(DomHelper.getStyleValue(el, 'flex-grow'), 10);
/**
 * A simple splitter widget that resizes the elements next to it or above/below it depending on orientation.
 *
 * @extends Core/widget/Widget
 * @classType splitter
 * @externalexample widget/Splitter.js
 */

class Splitter extends Widget {
  //region Config
  // Factoryable type name
  static get type() {
    return 'splitter';
  }

  static get defaultConfig() {
    return {
      /**
       * The splitters orientation, configurable with 'auto', 'horizontal' or 'vertical'.
       *
       * 'auto' tries to determine the orientation by either checking the `flex-direction` of the parent element
       * or by comparing the positions of the closest sibling elements to the splitter. If they are above and
       * below 'horizontal' is used, if not it uses 'vertical'.
       *
       * ```
       * new Splitter({
       *    orientation : 'horizontal'
       * });
       * ```
       *
       * To receive the actully used orienatation when configured with 'auto', see
       * {@link #property-currentOrientation}.
       *
       * @config {String}
       * @default
       */
      orientation: 'auto'
    };
  } //endregion
  //region Init & destroy

  doDestroy() {
    this.mouseDetacher && this.mouseDetacher();
    super.doDestroy();
  } //endregion
  //region Template & element

  template() {
    return '<div class="b-splitter"></div>';
  }

  get element() {
    return super.element;
  }

  set element(element) {
    super.element = element;
    const events = {
      element: this.element,
      pointerdown: 'onMouseDown',
      thisObj: this
    };

    if (!BrowserHelper.supportsPointerEvents) {
      events.mousedown = 'onMouseDown';
      events.touchstart = 'onMouseDown';
    }

    EventHelper.on(events);
  } //endregion
  //region Orientation

  /**
   * Get actually used orientation, which is either the configured value for `orientation` or if configured with
   * 'auto' the currently used orientation.
   * @property {String}
   * @readonly
   */

  get currentOrientation() {
    return this._currentOrientation;
  }
  /**
   * Splitter orientation, see {@link #config-orientation}. When set to 'auto' then actually used orientation can be
   * retrieved using {@link #property-currentOrientation}.
   * @property {String}
   * @readonly
   */

  get orientation() {
    return this._orientation;
  }

  set orientation(orientation) {
    this._orientation = orientation;

    if (orientation === 'auto') {
      this._currentOrientation = null;
    } else {
      this._currentOrientation = orientation;
    }

    this.updateOrientation();
  } // Determines current orientation if configured with 'auto'. Adds orientation cls

  updateOrientation() {
    const me = this,
          {
      element
    } = this;

    if (me._prevOrientation) {
      element.classList.remove(me._prevOrientation);
    } // Orientation auto and already rendered, determine orientation to use

    if (!me._currentOrientation && me.rendered && element.offsetParent) {
      const flexDirection = DomHelper.getStyleValue(element.parentElement, 'flex-direction'); // If used in a flex layout, determine orientation from flex-direction

      if (flexDirection) {
        me._currentOrientation = flexDirection.startsWith('column') ? 'horizontal' : 'vertical';
      } // If used in some other layout, try to determine form sibling elements position
      else {
          const previous = element.previousElementSibling,
                next = element.nextElementSibling;

          if (!previous || !next) {
            // To early in rendering, next sibling not rendered yet
            return;
          }

          const prevRect = previous.getBoundingClientRect(),
                nextRect = next.getBoundingClientRect(),
                topMost = prevRect.top < nextRect.top ? prevRect : nextRect,
                bottomMost = topMost === nextRect ? prevRect : nextRect;
          me._currentOrientation = topMost.top !== bottomMost.top ? 'horizontal' : 'vertical';
        }
    }

    if (me._currentOrientation) {
      element.classList.add(`b-${me._currentOrientation}`);
    }

    me._prevOrientation = me._currentOrientation;
  } //endregion
  //region Events

  onMouseDown(event) {
    event.preventDefault();

    if (event.touches) {
      event = event.touches[0];
    }

    const me = this,
          {
      element
    } = me,
          prev = element.previousElementSibling,
          next = element.nextElementSibling,
          prevHasFlex = hasFlex(prev),
          nextHasFlex = hasFlex(next),
          flexed = []; // First stop any ongoing drag operation, since we cannot trust that we always get the mouseup event

    me.mouseDetacher && me.mouseDetacher(); // Remember flexed children, to enable maintaining their proportions on resize

    for (const child of element.parentElement.children) {
      if (hasFlex(child) && child !== element) {
        flexed.push({
          element: child,
          width: child.offsetWidth,
          height: child.offsetHeight
        });
      }
    }

    me.context = {
      startX: event.pageX,
      startY: event.pageY,
      prevWidth: prev.offsetWidth,
      prevHeight: prev.offsetHeight,
      nextWidth: next.offsetWidth,
      nextHeight: next.offsetHeight,
      prevHasFlex,
      nextHasFlex,
      flexed,
      prev,
      next
    };
    const events = {
      element: document,
      pointermove: 'onMouseMove',
      pointerup: 'onMouseUp',
      thisObj: me
    };

    if (!BrowserHelper.supportsPointerEvents) {
      events.mousemove = 'onMouseMove';
      events.touchmove = 'onMouseMove';
      events.mouseup = events.touchend = 'onMouseUp';
    }

    me.mouseDetacher = EventHelper.on(events);
  }

  onMouseMove(event) {
    const me = this,
          {
      context
    } = me,
          deltaX = event.pageX - context.startX,
          deltaY = event.pageY - context.startY;
    event.preventDefault();
    Object.assign(context, {
      deltaX,
      deltaY
    });

    if (!context.started) {
      context.started = true;
      me.trigger('start', {
        context,
        event
      }); // Convert heights/widths to flex for flexed elements to maintain proportions
      // 100px high -> flex-grow 100

      context.flexed.forEach(flexed => {
        if (me.currentOrientation === 'vertical') {
          flexed.element.style.flexGrow = flexed.width;
        } else {
          flexed.element.style.flexGrow = flexed.height;
        } //Remove flex-basis, since it interferes with resizing

        flexed.element.style.flexBasis = '0';
      });
    } // Adjust flex-grow or width/height for splitters closest siblings

    if (me.currentOrientation === 'vertical') {
      const newPrevWidth = context.prevWidth + deltaX,
            newNextWidth = context.nextWidth - deltaX;

      if (context.prevHasFlex) {
        context.prev.style.flexGrow = newPrevWidth;
      } else {
        context.prev.style.width = `${newPrevWidth}px`;
      }

      if (context.nextHasFlex) {
        context.next.style.flexGrow = newNextWidth;
      } else {
        context.next.style.width = `${newNextWidth}px`;
      }
    } else {
      const newPrevHeight = context.prevHeight + deltaY,
            newNextHeight = context.nextHeight - deltaY;

      if (context.prevHasFlex) {
        context.prev.style.flexGrow = newPrevHeight;
      } else {
        context.prev.style.height = `${newPrevHeight}px`;
      }

      if (context.nextHasFlex) {
        context.next.style.flexGrow = newNextHeight;
      } else {
        context.next.style.height = `${newNextHeight}px`;
      }
    }

    me.trigger('move', {
      context,
      event
    });
  }

  onMouseUp(event) {
    const me = this;
    me.mouseDetacher && me.mouseDetacher();
    me.mouseDetacher = null;

    if (me.context.started) {
      me.trigger('end', {
        context: me.context,
        event
      });
    }

    me.context = null;
  } //endregion

  render() {
    super.render(...arguments);
    const me = this;

    if (!me._currentOrientation) {
      // Determine current orientation
      me.updateOrientation();

      if (!me._currentOrientation) {
        // Determine current orientation from closest siblings, needs to happen on new frame since the splitter
        // is rendered before its next sibling
        me.requestAnimationFrame(() => me.updateOrientation());
      }
    }
  }

} // Register this widget type with its Factory

Splitter.initClass();
Splitter._$name = 'Splitter';

/**
 * @module Core/widget/layout/Card
 */

const animationClasses = ['b-slide-out-left', 'b-slide-out-right', 'b-slide-in-left', 'b-slide-in-right'];
/**
 * A helper class for containers which must manage multiple child widgets, of which only one may be visible at once such
 * as a {@link Core.widget.TabPanel}. This class offers an active widget switching API, and optional slide-in,
 * slide-out animations from child to child.
 */

class Card extends Layout {
  static get $name() {
    return 'Card';
  }

  static get configurable() {
    return {
      containerCls: 'b-card-container',
      itemCls: 'b-card-item',
      hideChildHeaderCls: 'b-hide-child-headers',

      /**
       * Specifies whether to slide tabs in and out of visibility.
       * @config {Boolean}
       * @default
       */
      animateCardChange: true,

      /**
       * The active child item.
       * @config {Core.widget.Widget}
       */
      activeItem: null,

      /**
       * The active child index.
       * @config {Number}
       */
      activeIndex: null
    };
  }

  onChildAdd(item) {
    super.onChildAdd(item);
    item.on({
      beforeHide: 'onBeforeChildHide',
      beforeShow: 'onBeforeChildShow',
      thisObj: this
    });
    const {
      activeItem,
      owner
    } = this,
          activeIndex = owner.activeIndex != null ? owner.activeIndex : this.activeIndex || 0,
          itemIndex = owner.items.indexOf(item),
          isActive = activeItem != null ? item === activeItem : itemIndex === activeIndex; // Ensure inactive child items start hidden, and the active one starts shown.
    // Sync our active indicators with reality ready for render.

    if (isActive) {
      item.show();
      this._activeIndex = itemIndex;
      this._activeItem = item;
    } else {
      item.hide();
    }
  }

  onChildRemove(item) {
    super.onChildRemove(item); // Active child has been removed without setting another child to be active.
    // Choose an immediate sibling to be the new active item

    if (this._activeItem === item) {
      this.activateSiblingOf(item);
    }

    item.un({
      beforeHide: 'onBeforeChildHide',
      beforeShow: 'onBeforeChildShow',
      thisObj: this
    });
  }
  /**
   * Detect external code showing a child. We veto that show and activate it through the API.
   * @internal
   */

  onBeforeChildShow({
    source: showingChild
  }) {
    // Some outside code is showing a child.
    // We must control this, so veto it and activate it in the standard way.
    if (!this.owner.isConfiguring && !showingChild.$isActivating) {
      this.activeItem = showingChild;
      return false;
    }
  }
  /**
   * Detect external code hiding a child. We veto that show and activate an immediate sibling through the API.
   * @internal
   */

  onBeforeChildHide({
    source: hidingChild
  }) {
    // Some outside code is hiding a child.
    // We must control this, so veto it and activate a sibling in the standard way.
    if (!this.owner.isConfiguring && !hidingChild.$isDeactivating) {
      this.activateSiblingOf(hidingChild);
      return false;
    }
  }

  activateSiblingOf(item) {
    const {
      owner
    } = this,
          items = owner.items.slice(),
          removeAt = items.indexOf(item);
    items.splice(removeAt, 1);
    this.activeIndex = Math.min(removeAt, items.length - 1);
  }
  /**
   * Get/set active item, using index or the Widget to activate
   * @param {Core.widget.Widget|Number} activeItem
   * @returns {Object} An object describing the card change containing the following properties:
   *
   *  - `prevActiveIndex` The previously active index.
   *  - `prevActiveItem ` The previously active child item.
   *  - `activeIndex    ` The newly active index.
   *  - `activeItem     ` The newly active child item.
   *  - `promise        ` A promise which completes when the slide-in animation finishes and the child item contains
   * focus if it is focusable.
   * @internal
   */

  setActiveItem(activeIndex, prevActiveIndex = this.activeIndex) {
    const me = this,
          {
      owner
    } = me,
          {
      items
    } = owner,
          widgetPassed = activeIndex instanceof Widget,
          prevActiveItem = items[prevActiveIndex],
          newActiveItem = owner.items[activeIndex = widgetPassed ? activeIndex = items.indexOf(activeIndex) : parseInt(activeIndex)],
          event = {
      prevActiveIndex,
      prevActiveItem
    }; // There's a child widget at that index to activate and we're not already activating it.

    if (newActiveItem && !newActiveItem.$isActivating && newActiveItem !== prevActiveItem) {
      const prevItemElement = prevActiveItem && prevActiveItem.element,
            newActiveElement = newActiveItem && newActiveItem.element;
      event.activeIndex = activeIndex;
      event.activeItem = newActiveItem;

      if (owner.trigger('beforeActiveItemChange', event) === false) {
        return null;
      } // A previous card change is in progress, abort it and clean the items it was operating upon

      if (me.animateDetacher) {
        const abortedEvent = me.animateDetacher.event;
        me.animateDetacher();
        DomHelper.removeClasses(abortedEvent.prevActiveItem.element, animationClasses);
        DomHelper.removeClasses(abortedEvent.activeItem.element, animationClasses);
        me.animateDetacher = null;
      }

      event.promise = new Promise((resolve, reject) => {
        // If there's something to slide out, slide it out, and slide the new item in
        if (prevItemElement && owner.isVisible && me.animateCardChange) {
          // During the card sliding trick, we don't want resize notifications.
          // The outgoing card should be as inert as if it were hidden.
          const wasMonitoringSize = prevActiveItem.monitorResize;
          prevActiveItem.monitorResize = false; // The outgoing card must report its isVisible property as false from now on
          // even before we officially hide it.

          prevActiveItem._hidden = true; // Show the item so that it can be slid in.
          // Events will ensue, UIs can react to the show event.
          // The flag is so that our onBeforeChildShow listener can
          // tell if it's part of our orderly activate operation.

          newActiveItem.$isActivating = true;
          newActiveItem.show();
          newActiveItem.$isActivating = false;
          prevItemElement.classList.add(activeIndex > prevActiveIndex ? 'b-slide-out-left' : 'b-slide-out-right');
          newActiveElement.classList.add(activeIndex < prevActiveIndex ? 'b-slide-in-left' : 'b-slide-in-right');
          owner.isAnimating = true; // When the new widget is in place, clean up

          me.animateDetacher = EventHelper.onTransitionEnd({
            mode: 'animation',
            element: newActiveElement,
            // onTransitionEnd protects us from being called
            // after the thisObj is destroyed.
            thisObj: prevActiveItem,
            handler: function () {
              me.animateDetacher = null;
              owner.isAnimating = false; // Clean incoming widget's animation classes

              DomHelper.removeClasses(newActiveElement, animationClasses); // If there's an outgoing item, clean its animation classes and hide it

              if (prevItemElement) {
                DomHelper.removeClasses(prevItemElement, animationClasses); // The flag is so that our onBeforeChildHide listener can
                // tell if it's part of our orderly activate operation.

                prevActiveItem.$isDeactivating = true;
                prevActiveItem._hidden = false;
                prevActiveItem.hide();
                prevActiveItem.monitorResize = wasMonitoringSize;
                prevActiveItem.$isDeactivating = false;
              }

              me.onActiveItemChange(event, resolve);
            },
            once: true
          });
          me.animateDetacher.reject = reject;
          me.animateDetacher.event = event;
        } // Nothing to slide out or we are not animating.
        else {
            // Show the new active items first, so that the hide listener doesn't
            // automatically set a new active item based on active item being hidden.
            // The flag is so that our onBeforeChildShow listener can
            // tell if it's part of our orderly activate operation.
            newActiveItem.$isActivating = true;
            newActiveItem.show();
            newActiveItem.$isActivating = false;

            if (prevActiveItem) {
              // The flag is so that our onBeforeChildHide listener can
              // tell if it's part of our orderly activate operation.
              prevActiveItem.$isDeactivating = true;
              prevActiveItem.hide();
              prevActiveItem.$isDeactivating = false;
            }

            me.onActiveItemChange(event, resolve);
          }
      });
    }

    return event;
  }

  onActiveItemChange(event, resolve) {
    this._activeItem = event.activeItem;
    this._activeIndex = event.activeIndex;
    this.owner.trigger('activeItemChange', event); // Note that we have to call focus *after* the element is in its new position
    // because focus({preventScroll:true}) is not supported everywhere
    // and crazy browser scrolling behaviour on focus breaks the animation.

    event.activeItem.focus();
    resolve(event);
  }

  renderChildren() {
    const {
      owner
    } = this; // The usual; not working on IE11

    if (BrowserHelper.isIE11) {
      this.animateCardChange = false;
    }

    owner.contentElement.classList[owner.suppressChildHeaders ? 'add' : 'remove'](this.hideChildHeaderCls);
    super.renderChildren();
  }

  changeActiveIndex(activeIndex) {
    const {
      owner
    } = this; // Sanitize it if possible

    return owner.isConfiguring && !owner._items ? activeIndex : Math.min(activeIndex, owner.items.length - 1);
  }

  updateActiveIndex(activeIndex, oldActiveIndex) {
    if (!this.owner.isConfiguring) {
      this.setActiveItem(activeIndex, oldActiveIndex);
    }
  }

  updateActiveItem(activeItem) {
    if (!this.owner.isConfiguring) {
      this.setActiveItem(activeItem, this.activeIndex);
    }
  }

} // Layouts must register themselves so that the static layout instantiation
// in Layout knows what to do with layout type names

Card._$name = 'Card';
Layout.registerLayout(Card);

/**
 * @module Core/widget/TabPanel
 */

/**
 * Tab panel widget, displays a collection of tabs which each can contain other widgets. Layout is handled using css
 *
 * @extends Core/widget/Container
 * @example
 * let tabPanel = new TabPanel({
 *  items: [
 *      {
 *          title: 'First',
 *          items: [
 *              { type: 'textfield', label: 'Name' },
 *              ...
 *          ]
 *      }, {
 *          title: 'Last',
 *          items: [
 *              ...
 *          ]
 *      }
 *  ]
 * });
 *
 * @classType tabpanel
 * @externalexample widget/TabPanel.js
 */

class TabPanel extends Container {
  //region Config
  static get $name() {
    return 'TabPanel';
  } // Factoryable type name

  static get type() {
    return 'tabpanel';
  } // Factoryable type alias

  static get alias() {
    return 'tabs';
  }

  static get configurable() {
    return {
      // Prevent child panels from displaying a header unless explicitly configured with one
      suppressChildHeaders: true,

      /**
       * The index of the initially active tab.
       * @config {Number}
       * @default
       */
      activeTab: 0,

      /**
       * Min width of a tab title. 0 means no minimum width. This is default.
       * @config {Number}
       * @default
       */
      tabMinWidth: null,

      /**
       * Max width of a tab title. 0 means no maximum width. This is default.
       * @config {Number}
       * @default
       */
      tabMaxWidth: null,

      /**
       * Specifies whether to slide tabs in and out of visibility.
       * @config {Boolean}
       * @default
       */
      animateTabChange: true
    };
  }
  /**
   * The index of the initially active tab.
   * @property {Number}
   * @name activeTab
   * @member
   */

  static get defaultConfig() {
    return {
      itemCls: 'b-tabpanel-item',
      defaultType: 'container',
      layout: 'card'
    };
  } //endregion
  //region Init

  construct(config) {
    const me = this;
    super.construct(config); // assign elements to titleElement, so they can get updated automatically when title is changed

    me.items.forEach((tab, i) => {
      tab.titleElement = me.element.querySelector(`div.b-tabpanel-tab[data-index="${i}"] span`);
    });
    EventHelper.on({
      element: me.tabStrip,
      delegate: '.b-tabpanel-tab',
      click: 'onTabElementClick',
      thisObj: this
    });
  }

  changeElement() {
    const me = this;
    return super.changeElement({
      children: [{
        className: 'b-tabpanel-tabs',
        reference: 'tabStrip',
        children: me.items.map((childItem, i) => {
          return {
            className: {
              'b-tabpanel-tab': 1,
              'b-active': i === me.activeTab,
              [childItem.cls]: childItem.cls,
              'b-hidden': childItem.$hideTab,
              'b-disabled': childItem.$disableTab
            },
            style: {
              minWidth: DomHelper.setLength(me.tabMinWidth),
              maxWidth: DomHelper.setLength(me.tabMaxWidth)
            },
            tabindex: -1,
            dataset: {
              index: i
            },
            children: [{
              tag: 'span',
              className: 'b-tabpanel-tab-title',
              html: childItem.title
            }]
          };
        })
      }, {
        className: {
          'b-tabpanel-body': 1
        },
        reference: 'tabPanelBody'
      }]
    });
  }

  onChildAdd(child) {
    // If the items come in as hidden, assume they are to be not available.
    // This flag causes the tab selectors to be rendered hidden.
    if (child.hidden) {
      child.$hideTab = true;
    } // same for disabled

    if (child.disabled) {
      child.$disableTab = true;
    } // The layout will hide inactive new items.
    // The $hideTab flag is for items which were preconfigured as hidden.
    // And we must add our beforeHide listener *after* call super.

    super.onChildAdd(child);
    child.on({
      beforeHide: 'onBeforeChildHide',
      beforeChangeHidden: 'onBeforeChildChangeHidden',
      beforeUpdateDisabled: 'onBeforeChildUpdateDisabled',
      thisObj: this,
      prio: 1000 // We must know before the layout intercepts and activates a sibling

    });
  }

  onChildRemove(child) {
    super.onChildRemove(child);
    child.un({
      beforeHide: 'onBeforeChildHide',
      beforeChangeHidden: 'onBeforeChildChangeHidden',
      beforeUpdateDisabled: 'onBeforeChildUpdateDisabled',
      thisObj: this
    });
  } //endregion

  updateTabMinWidth(tabMinWidth) {
    this.element && DomHelper.forEachSelector(this.element, '.b-tabpanel-tab', tab => {
      DomHelper.setLength(tab, 'minWidth', tabMinWidth || null);
    });
  }

  updateTabMaxWidth(tabMaxWidth) {
    this.element && DomHelper.forEachSelector(this.element, '.b-tabpanel-tab', tab => {
      DomHelper.setLength(tab, 'maxWidth', tabMaxWidth || null);
    });
  }

  changeLayout(layout) {
    layout = super.changeLayout(layout);
    layout.activeIndex = this.activeTab;
    layout.animateCardChange = this.animateTabChange;
    return layout;
  } //region Tabs

  get contentElement() {
    return this.tabPanelBody;
  }

  get focusElement() {
    const me = this,
          activeTab = me.items[me.activeTab || 0],
          tabFocusElement = activeTab && activeTab.focusElement;
    return tabFocusElement || me.tabStrip.children[me.activeTab];
  }

  getTabElement(item) {
    return this.element.querySelector(`div.b-tabpanel-tab[data-index="${this.items.indexOf(item)}"]`);
  }

  changeActiveTab(activeTab) {
    return parseInt(activeTab, 10);
  }

  updateActiveTab(activeTab) {
    const me = this;

    if (typeof activeTab === 'number') {
      if (activeTab < 0 || activeTab >= me.items.length) {
        throw new Error('Invalid tab index: ' + activeTab);
      }
    } // Must be a child widget, so add if it's not already in our items.
    else if (this.items.indexOf(activeTab) === -1) {
        activeTab = me.add(activeTab);
      }

    if (!me.isConfiguring) {
      me.layout.activeItem = activeTab;
    }
  } // Auto called because Card layout triggers the activeItemChange on its owner

  onActiveItemChange(activeItemChangeEvent) {
    const me = this,
          {
      tabStrip
    } = me,
          {
      prevActiveIndex,
      activeIndex
    } = activeItemChangeEvent,
          prevTabElement = tabStrip.children[prevActiveIndex]; // Our UI changes immediately, our state must be accurate

    me.activeTab = activeIndex; // Deactivate previous active tab

    if (prevTabElement) {
      prevTabElement.classList.remove('b-active');
    } // Activate the new tab

    tabStrip.children[activeIndex].classList.add('b-active');
    tabStrip.children[activeIndex].classList.remove('b-hidden');
    /**
     * The active tab has changed.
     * @event tabChange
     * @param {Core.widget.Widget} prevActiveItem - The previous active child widget.
     * @param {Number} prevActiveIndex - The previous active index.
     * @param {Core.widget.Widget} activeItem - The new active child widget.
     * @param {Number} activeIndex - The new active index.
     */

    me.trigger('tabChange', activeItemChangeEvent);
  }
  /**
   * The active tab index. Setting must be done through {@link #property-activeTab}
   * @property {Number}
   * @readonly
   */

  get activeIndex() {
    return this.layout.activeIndex;
  }
  /**
   * The active child widget. Setting must be done through {@link #property-activeTab}
   * @property {Core.widget.Widget}
   * @readonly
   */

  get activeItem() {
    return this.layout.activeItem;
  } //endregion
  //region Events

  onTabElementClick(event) {
    if (this.items[parseInt(event.currentTarget.dataset.index)].disabled) {
      return;
    }

    this.activeTab = event.currentTarget.dataset.index;
  } // Hide the tab bar and content when method called is hide()

  onBeforeChildHide({
    source: hidingChild
  }) {
    // If it's a hide that is not part of the layout's deactivating, we must hide the tab selector
    if (!hidingChild.$isDeactivating) {
      const me = this,
            tabBarEl = me.getTabElement(hidingChild);
      tabBarEl.classList.add('b-hidden');
    }
  } // Hide the tab bar when 'tabItem.hidden = true|false;' is executed

  onBeforeChildChangeHidden({
    source: hidingChild,
    hidden
  }) {
    // If it's a hide/show that is not part of the layout's deactivating/activating, we must hide/show the tab selector
    if (!hidingChild.$isDeactivating && !hidingChild.$isActivating) {
      const me = this,
            tabBarEl = me.getTabElement(hidingChild),
            method = hidden ? 'add' : 'remove';
      tabBarEl.classList[method]('b-hidden');
      hidingChild.element.classList[method]('b-hidden'); // if tab to hide is active, let's active previous visible and enabled tab

      if (hidingChild === me.activeItem) {
        me.activateAvailableTab();
      }
    }
  }

  onBeforeChildUpdateDisabled({
    source: disablingChild,
    disabled
  }) {
    const me = this,
          tabBarEl = me.getTabElement(disablingChild);
    tabBarEl.classList[disabled ? 'add' : 'remove']('b-disabled'); // if tab to disable is active, let's active previous visible and enabled tab

    if (disablingChild === me.activeItem) {
      me.activateAvailableTab();
    }
  }

  activateAvailableTab() {
    const availableTabs = DomHelper.children(this.tabStrip, '> .b-tabpanel-tab:not(.b-disabled):not(.b-hidden)'),
          tabToActivate = [...availableTabs].reverse().find(tab => parseInt(tab.dataset.index) < this.activeTab) || availableTabs.find(tab => parseInt(tab.dataset.index) > this.activeTab);
    this.activeTab = tabToActivate === null || tabToActivate === void 0 ? void 0 : tabToActivate.dataset.index;
  } //endregion

} // Register this widget type with its Factory

TabPanel.initClass();
TabPanel._$name = 'TabPanel';

/**
 * @module Core/widget/TextAreaField
 */

/**
 * TextAreaField widget for multiline text input. Wraps a native &lt;textarea&gt;
 *
 * This field can be used as an {@link Grid.column.Column#config-editor editor} for the {@link Grid.column.Column Column}.
 *
 * @extends Core/widget/Field
 *
 * @example
 * let textAreaField = new TextAreaField({
 *   placeholder: 'Enter some text'
 * });
 *
 * @classType textareafield
 * @externalexample widget/TextAreaField.js
 */

class TextAreaField extends PickerField {
  static get $name() {
    return 'TextAreaField';
  } // Factoryable type name

  static get type() {
    return 'textareafield';
  } // Factoryable type alias

  static get alias() {
    return 'textarea';
  }

  static get configurable() {
    return {
      picker: {
        type: 'widget',
        cls: 'b-textareafield-picker',
        floating: true,
        scrollAction: 'realign',
        align: {
          align: 't-b',
          axisLock: true
        },
        autoShow: false
      },
      triggers: null,
      // Override PickerField. We don't have a trigger by default

      /**
       * Configure as `false` to have the field render as a non-editable picker field which
       * shows a `<textarea>` input when expanded.
       * @config {Boolean}
       * @default
       */
      inline: true,

      /**
       * The resize style to apply to the `<textarea>` element.
       * @config {String}
       * @default
       */
      resize: 'none',
      inputType: null
    };
  }

  startConfigure(config) {
    // Read the inline config which will force evaluation of triggers.
    this.getConfig('inline');
    super.startConfigure(config);
  }

  get inputElement() {
    const result = super.inputElement;

    if (this.inline) {
      result.tag = 'textarea';
      result.style = (result.style || '') + `;resize:${this.resize}`;
    } else {
      result.readOnly = 'readonly';
      result.reference = 'displayElement';
    }

    return result;
  }

  get focusElement() {
    return this.inline || this._picker && this._picker.isVisible ? this.input : this.displayElement;
  }

  showPicker() {
    // Block picker if inline
    if (this.inline) {
      return;
    }

    const {
      picker
    } = this;
    picker.width = this.pickerWidth || this[this.pickerAlignElement].offsetWidth; // Always focus the picker.

    super.showPicker(true);
  }

  focusPicker() {
    this.input.focus();
  }

  onPickerKeyDown(keyEvent) {
    const me = this,
          realInput = me.input;

    switch (keyEvent.key.trim() || keyEvent.code) {
      case 'Escape':
        // TODO: revert value?
        me.picker.hide();
        return;

      case 'Enter':
        if (keyEvent.ctrlKey) {
          me.syncInputFieldValue();
          me.picker.hide();
        }

        break;
    } // Super's onPickerKeyDown fires through this.input, so avoid infinite recursion
    // by redirecting it through the displayElement.

    me.input = me.displayElement;
    const result = super.onPickerKeyDown(keyEvent);
    me.input = realInput;
    return result;
  }

  syncInputFieldValue(skipHighlight) {
    if (this.displayElement) {
      this.displayElement.value = this.inputValue;
    }

    super.syncInputFieldValue(skipHighlight);
  }

  changeValue(value) {
    return value == null ? '' : value;
  }

  updateInline(inline) {
    if (!inline && !this.triggers) {
      this.triggers = {};
    }
  }

  changeTriggers(triggers, was) {
    if (!this.inline) {
      (triggers || (triggers = {})).expand = {
        cls: 'b-icon-picker',
        handler: 'onTriggerClick'
      };
    }

    return super.changeTriggers(triggers, was);
  }

  changePicker(picker, oldPicker) {
    var _picker;

    const me = this,
          pickerWidth = me.pickerWidth || ((_picker = picker) === null || _picker === void 0 ? void 0 : _picker.width);
    picker = TextAreaField.reconfigure(oldPicker, picker ? Objects.merge({
      owner: me,
      forElement: me[me.pickerAlignElement],
      align: {
        matchSize: pickerWidth == null,
        anchor: me.overlayAnchor,
        target: me[me.pickerAlignElement]
      },
      html: `<textarea id="${me.id + '-input'}" style="resize:${me.resize}">${me.value}</textarea>`
    }, picker) : null, me); // May have been set to null (destroyed)

    if (picker) {
      const input = me.input = picker.element.querySelector(`#${me.id}-input`);
      me.inputListenerRemover = EventHelper.on({
        element: input,
        thisObj: me,
        focus: 'internalOnInputFocus',
        change: 'internalOnChange',
        input: 'internalOnInput',
        keydown: 'internalOnKeyEvent',
        keypress: 'internalOnKeyEvent',
        keyup: 'internalOnKeyEvent'
      });
    }

    return picker;
  }

} // Register this widget type with its Factory

TextAreaField.initClass();
TextAreaField._$name = 'TextAreaField';

/**
 * @module Grid/column/Column
 */

const validWidth = value => typeof value === 'number' || (value === null || value === void 0 ? void 0 : value.endsWith('px'));
/**
 * Base class for other column types, used if no type is specified on a column.
 *
 * Default editor is a {@link Core.widget.TextField TextField}.
 *
 * ```javascript
 * const grid = new Grid({
 *   columns : [{
 *     field : 'name',
 *     text  : 'Name'
 *   }, {
 *     text  : 'Hobby',
 *     field : 'others.hobby', // reading nested field data
 *   }, {
 *     type  : 'number', // Will use NumberColumn
 *     field : 'age',
 *     text  : 'Age'
 *   }]
 * });
 * ```
 *
 * ## Column types
 *
 * Grid ships with multiple different column types. Which type to use for a column is specified by the `type` config. The built in types are:
 *
 * * {@link Grid.column.ActionColumn action} - displays actions (clickable icons) in the cell.
 * * {@link Grid.column.AggregateColumn aggregate} - a column, which, when used as part of a Tree, aggregates the values of
 *   this column's descendants using a configured function which defaults to `sum`.
 * * {@link Grid.column.CheckColumn check} - displays a checkbox in the cell.
 * * {@link Grid.column.DateColumn date} - displays a date in the specified format.
 * * {@link Grid.column.NumberColumn number} - a column for showing/editing numbers.
 * * {@link Grid.column.PercentColumn percent} - displays a basic progress bar.
 * * {@link Grid.column.RatingColumn rating} - displays a star rating.
 * * {@link Grid.column.RowNumberColumn rownumber} - displays the row number in each cell.
 * * {@link Grid.column.TemplateColumn template} - uses a template for cell content.
 * * {@link Grid.column.TimeColumn time} - displays a time in the specified format.
 * * {@link Grid.column.TreeColumn tree} - displays a tree structure when using the {@link Grid.feature.Tree tree} feature.
 * * {@link Grid.column.WidgetColumn widget} - displays widgets in the cells.
 *
 * ## Cell renderers
 *
 * You can affect the contents and styling of cells in a column using a {@link Grid.column.Column#config-renderer renderer()} function.
 *
 * ```javascript
 * const grid = new Grid({
 *   columns : [
 *   ...
 *     {
 *       field      : 'approved',
 *       text       : 'Approved',
 *       htmlEncode : false, // allow to use HTML code
 *       renderer({ value }) {
 *         return value === true ? '<b>Yes</b>' : '<i>No</i>';
 *       }
 *     }
 *     ...
 *     ]
 * });
 * ```
 *
 * ## Menus
 *
 * You can add custom items to the context menu for a columns header and for its cells, using {@link Grid.column.Column#config-headerMenuItems headerMenuItems} and
 * {@link Grid.column.Column#config-cellMenuItems cellMenuItems}. Here is an example:
 *
 * ```javascript
 * const grid = new Grid({
 *   columns : [
 *     ...
 *     {
 *       type  : 'number',
 *       field : 'age',
 *       text  : 'Age',
 *       headerMenuItems: [{
 *           text : 'My unique header item',
 *           icon : 'b-fa b-fa-paw',
 *           onItem() { console.log('item clicked'); }
 *       }],
 *       cellMenuItems: [{
 *           text : 'My unique cell item',
 *           icon : 'b-fa b-fa-plus',
 *           onItem() { console.log('item clicked'); }
 *       }]
 *     }
 *   ...
 *   ]
 * });
 * ```
 *
 * @extends Core/data/Model
 * @classType column
 * @mixes Core/mixin/Events
 * @mixes Core/localization/Localizable
 */

class Column extends Events(Localizable(Model)) {
  //region Config
  static get fields() {
    return [//region Common

    /**
     * Get/set header text
     * @member {String} text
     */

    /**
     * Text to display in the header
     * @config {String} text
     * @category Common
     */
    'text',
    /**
     * The {@link Core.data.Model Model} field name to read data from
     * @config {String} field
     * @category Common
     */
    'field',
    /**
     * Renderer function, used to format and style the content displayed in the cell. Return the cell text you
     * want to display. Can also affect other aspects of the cell, such as styling.
     *
     * **NOTE:** If you mutate cellElement and you want to prevent cell content to be reset during the rendering,
     * please return `undefined` from the renderer or just omit the `return` statement. If you return a value and
     * the value can be undefined, please make sure you return an empty string to update the cell content. For example:
     *
     * ```javascript
     * new Grid({
     *     columns : [
     *         // Returns an empty string if status field value is undefined
     *         { text : 'Status', renderer : ({ record }) => record.status ?? '' },
     *     ]
     * });
     * ```
     *
     * @param {Object} renderData Object containing renderer parameters
     * @param {HTMLElement} [renderData.cellElement] Cell element, for adding CSS classes, styling etc.
     *        Can be `null` in case of export
     * @param {*} renderData.value Value to be displayed in the cell
     * @param {Core.data.Model} renderData.record Record for the row
     * @param {Grid.column.Column} renderData.column This column
     * @param {Grid.view.Grid} renderData.grid This grid
     * @param {Grid.row.Row} [renderData.row] Row object. Can be null in case of export. Use the
     * {@link Grid.row.Row#function-assignCls row's API} to manipulate CSS class names.
     * @param {Object} [renderData.size] Set `size.height` to specify the desired row height for the current row.
     *        Largest specified height is used, falling back to configured {@link Grid/view/Grid#config-rowHeight}
     *        in case none is specified. Can be null in case of export
     * @param {Number} [renderData.size.height] Set this to request a certain row height
     * @param {Number} [renderData.size.configuredHeight] Row height that will be used if none is requested
     * @param {Boolean} [renderData.isExport] True if record is being exported to allow special handling during export
     * @param {Boolean} [renderData.isMeasuring] True if the column is being measured for a `resizeToFitContent`
     *        call. In which case an advanced renderer might need to take different actions.
     * @config {Function} renderer
     * @category Common
     */
    'renderer', //'reactiveRenderer',

    /**
     * Column width. If value is Number then width is in pixels
     * @config {Number|String} width
     * @category Common
     */
    'width',
    /**
     * Gets or sets the column flex weight
     * @member {String} flex
     */

    /**
     * Column width as a flex weight. All columns with flex specified divide the available space (after
     * subtracting fixed widths) between them according to the flex value. Columns that have flex 2 will be
     * twice as wide as those with flex 1 (and so on)
     * @config {Number} flex
     * @category Common
     */
    'flex',
    /**
     * This config sizes a column to fits its content. It is used instead of `width` or `flex`.
     *
     * This config requires the {@link Grid.feature.ColumnAutoWidth} feature which responds to changes in the
     * grid's store and synchronizes the widths' of all `autoWidth` columns.
     *
     * If this config is not a Boolean value, it is passed as the only argument to the `resizeToFitContent`
     * method to constrain the column's width.
     *
     * @config {Boolean|Number|Number[]} autoWidth
     * @category Common
     */
    'autoWidth',
    /**
     * Mode to use when measuring the contents of this column in calls to {@link #function-resizeToFitContent}.
     * Available modes are:
     *
     * * 'exact'       - Most precise, renders and measures all cells (Default, slowest)
     * * 'textContent' - Renders all cells but only measures the one with the longest `textContent`
     * * 'value'       - Renders and measures only the cell with the longest data (Fastest)
     * * 'none'/falsy  - Resize to fit content not allowed, a call does nothing
     *
     * @config {String} fitMode
     * @default 'exact'
     * @category Common
     */
    {
      name: 'fitMode',
      defaultValue: 'exact'
    }, //endregion
    //region Interaction

    /**
     * Specify if this column should be editable, and define which editor to use for editing cells in the
     * column (if {@link Grid/feature/CellEdit CellEdit} feature is enabled). Editor refers to {@link #config-field}
     * for a data source.
     *
     * All subclasses of {@link Core.widget.Field Field} can be used as editors. The most popular are:
     * - {@link Core.widget.TextField TextField}
     * - {@link Core.widget.NumberField NumberField}
     * - {@link Core.widget.DateField DateField}
     * - {@link Core.widget.TimeField TimeField}
     * - {@link Core.widget.Combo Combo}
     *
     * If record has method set + capitalized field, method will be called, e.g. if record has method named
     * `setFoobar` and this config is `foobar`, then instead of `record.foobar = value`,
     * `record.setFoobar(value)` will be called.
     *
     * @config {String|Object|Boolean} editor
     * @category Interaction
     */
    {
      name: 'editor',
      defaultValue: {}
    },
    /**
     * A function which is called when a cell edit is requested to finish.
     *
     * This may be an `async` function which performs complex validation. The edit will not
     * complete until it returns `false` to mean the edit cannot be finished, or `true` to go
     * ahead and complete.
     *
     * @param {Object} context An object describing the state of the edit at completion request time.
     * @param {Core.widget.Field} context.inputField The field configured as the column's `editor`.
     * @param {Core.data.Model} context.record The record being edited.
     * @param {*} context.oldValue The old value of the cell.
     * @param {*} context.value The new value of the cell.
     * @param {Grid.view.Grid} context.grid The host grid.
     * @param {Object} context.editorContext The {@link Grid.feature.CellEdit CellEdit} context object.
     * @param {Grid.column.Column} context.editorContext.column The column being edited.
     * @param {Core.data.Model} context.editorContext.record The record being edited.
     * @param {HTMLElement} context.editorContext.cell The cell element hosting the editor.
     * @param {Core.widget.Editor} context.editorContext.editor The floating Editor widget which is hosting the input field.
     * @config {Function} finalizeCellEdit
     * @category Interaction
     */
    'finalizeCellEdit',
    /**
     * Setting this option means that pressing the `ESCAPE` key after editing the field will
     * revert the field to the value it had when the edit began. If the value is _not_ changed
     * from when the edit started, the input field's {@link Core.widget.Field#config-clearable}
     * behaviour will be activated. Finally, the edit will be canceled.
     * @config {Boolean} revertOnEscape
     * @default true
     * @category Interaction
     */
    {
      name: 'revertOnEscape',
      defaultValue: true
    },
    /**
     * How to handle a request to complete a cell edit in this column if the field is invalid.
     * There are three choices:
     *  - `block` The default. The edit is not exited, the field remains focused.
     *  - `allow` Allow the edit to be completed.
     *  - `revert` The field value is reverted and the edit is completed.
     * @config {String} invalidAction
     * @default 'block'
     * @category Interaction
     */
    {
      name: 'invalidAction',
      defaultValue: 'block'
    },
    /**
     * Allow sorting of data in the column. You can pass true/false to enable/disable sorting, or provide a
     * custom sorting function, or a config object for a {@link Core.util.CollectionSorter}
     *
     * ```javascript
     * const grid = new Grid({
     *     columns : [
     *          {
     *              // Disable sorting for this column
     *              sortable : false
     *          },
     *          {
     *              field : 'name',
     *              // Custom sorting for this column
     *              sortable(user1, user2) {
     *                  return user1.name < user2.name ? -1 : 1;
     *              }
     *          },
     *          {
     *              // A config object for a Core.util.CollectionSorter
     *              sortable : {
     *                  property         : 'someField',
     *                  direction        : 'DESC',
     *                  useLocaleCompare : 'sv-SE'
     *              }
     *          }
     *     ]
     * });
     * ```
     * When providing a custom sorting function, if the sort feature is configured with
     * `prioritizeColumns : true` that function will also be used for programmatic sorting of the store:
     *
     * ```javascript
     * const grid = new Grid({
     *     features : {
     *       sort : {
     *           prioritizeColumns : true
     *       }
     *     },
     *
     *     columns : [
     *          {
     *              field : 'name',
     *              // Custom sorting for this column
     *              sortable(user1, user2) {
     *                  return user1.name < user2.name ? -1 : 1;
     *              }
     *          }
     *     ]
     * });
     *
     * // Will use sortable() from the column definition above
     * grid.store.sort('name');
     * ```
     *
     * @config {Boolean|Function|Object} sortable
     * @default true
     * @category Interaction
     */
    {
      name: 'sortable',
      defaultValue: true,

      // Normalize function/object forms
      convert(value, column) {
        if (!value) {
          return false;
        }

        const sorter = {};

        if (typeof value === 'function') {
          // Scope for sortable() expected to be the column
          sorter.sortFn = value.bind(column);
        } else if (typeof value === 'object') {
          Object.assign(sorter, value);

          if (sorter.fn) {
            sorter.sortFn = sorter.fn;
            delete sorter.fn;
          }
        }

        return sorter;
      }

    },
    /**
     * Allow searching in the column (respected by QuickFind and Search features)
     * @config {Boolean} searchable
     * @default true
     * @category Interaction
     */
    {
      name: 'searchable',
      defaultValue: true
    },
    /**
     * Allow filtering data in the column (if {@link Grid.feature.Filter} or {@link Grid.feature.FilterBar}
     * feature is enabled).
     *
     * Also allows passing a custom filtering function that will be called for each record with a single
     * argument of format `{ value, record, [operator] }`. Returning `true` from the function includes the
     * record in the filtered set.
     *
     * Configuration object may be used for {@link Grid.feature.FilterBar} feature to specify `filterField`. See
     * an example in the code snippet below or check {@link Grid.feature.FilterBar} page for more details.
     *
     * ```
     * const grid = new Grid({
     *     columns : [
     *          {
     *              field : 'name',
     *              // Disable filtering for this column
     *              filterable : false
     *          },
     *          {
     *              field : 'age',
     *              // Custom filtering for this column
     *              filterable: ({ value, record }) => Math.abs(record.age - value) < 10
     *          },
     *          {
     *              field : 'city',
     *              // Filtering for a value out of a list of values
     *              filterable: {
     *                  filterField : {
     *                      type  : 'combo',
     *                      value : '',
     *                      items : [
     *                          'Paris',
     *                          'Dubai',
     *                          'Moscow',
     *                          'London',
     *                          'New York'
     *                      ]
     *                  }
     *              }
     *          }
     *     ]
     * });
     * ```
     *
     * When providing a custom filtering function, if the filter feature is configured with
     * `prioritizeColumns : true` that function will also be used for programmatic filtering of the store:
     *
     * ```javascript
     * const grid = new Grid({
     *     features : {
     *         filter : {
     *             prioritizeColumns : true
     *         }
     *     },
     *
     *     columns : [
     *          {
     *              field : 'age',
     *              // Custom filtering for this column
     *              filterable: ({ value, record }) => Math.abs(record.age - value) < 10
     *          }
     *     ]
     * });
     *
     * // Will use filterable() from the column definition above
     * grid.store.filter({
     *     property : 'age',
     *     value    : 50
     * });
     * ```
     *
     * @config {Boolean|Function|Object} filterable
     * @default true
     * @category Interaction
     */
    {
      name: 'filterable',
      defaultValue: true,

      // Normalize function/object forms
      convert(value) {
        if (!value) {
          return false;
        }

        const filter = {
          columnOwned: true
        };

        if (typeof value === 'function') {
          filter.filterFn = value;
        } else if (typeof value === 'object') {
          Object.assign(filter, value);
        }

        return filter;
      }

    },
    /**
     * Allow column visibility to be toggled through UI
     * @config {Boolean} hideable
     * @default true
     * @category Interaction
     */
    {
      name: 'hideable',
      defaultValue: true
    },
    /**
     * Set to false to prevent this column header from being dragged
     * @config {Boolean} draggable
     * @category Interaction
     */
    {
      name: 'draggable',
      defaultValue: true
    },
    /**
     * Set to false to prevent grouping by this column
     * @config {Boolean} groupable
     * @category Interaction
     */
    {
      name: 'groupable',
      defaultValue: true
    },
    /**
     * Set to `false` to prevent the column from being drag-resized when the ColumnResize plugin is enabled.
     * @config {Boolean} resizable
     * @default true
     * @category Interaction
     */
    {
      name: 'resizable',
      defaultValue: true
    }, //endregion
    //region Rendering

    /**
     * Renderer function for group headers (when using Group feature).
     * @param {Object} renderData
     * @param {HTMLElement} renderData.cellElement Cell element, for adding CSS classes, styling etc.
     * @param {*} renderData.groupRowFor Current group value
     * @param {Core.data.Model} renderData.record Record for the row
     * @param {Core.data.Model[]} renderData.groupRecords Records in the group
     * @param {Grid.column.Column} renderData.column Current rendering column
     * @param {Grid.column.Column} renderData.groupColumn Column that the grid is grouped by
     * @param {Number} renderData.count Number of records in the group
     * @param {Grid.view.Grid} renderData.grid This grid
     * @config {Function} groupRenderer
     * @returns {String} The header grouping text
     * @category Rendering
     */
    'groupRenderer',
    /**
     * Renderer function for the column header.
     * @param {Object} renderData
     * @param {Grid.column.Column} renderData.column This column
     * @param {HTMLElement} renderData.headerElement The header element
     * @config {Function} headerRenderer
     * @category Rendering
     */
    'headerRenderer',
    /**
     * Renderer function for cell tooltips header (used with CellTooltip feature). Specify false to prevent
     * tooltip for that column.
     * @param {HTMLElement} cellElement Cell element
     * @param {Core.data.Model} record Record for cell row
     * @param {Grid.column.Column} column Cell column
     * @param {Grid.feature.CellTooltip} cellTooltip Feature instance, used to set tooltip content async
     * @param {MouseEvent} event The event that triggered the tooltip
     * @config {Function} tooltipRenderer
     * @category Rendering
     */
    'tooltipRenderer',
    /**
     * CSS class added to each cell in this column
     * @config {String} cellCls
     * @category Rendering
     */
    'cellCls',
    /**
     * CSS class added to the header of this column
     * @config {String} cls
     * @category Rendering
     */
    'cls',
    /**
     * Get/set header icon class
     * @member {String} icon
     */

    /**
     * Icon to display in header. Specifying an icon will render a `<i>` element with the icon as value for the
     * class attribute
     * @config {String} icon
     * @category Rendering
     */
    'icon', //endregion
    //region Layout

    /**
     * Text align (left, center, right)
     * @config {String} align
     * @category Layout
     */
    'align',
    /**
     * Column minimal width. If value is Number then minimal width is in pixels
     * @config {Number|String} minWidth
     * @default 60
     * @category Layout
     */
    {
      name: 'minWidth',
      defaultValue: 60
    },
    /**
     * Get/set columns hidden state. Specify `true` to hide the column, `false` to show it.
     * @member {Boolean} hidden
     */

    /**
     * Hide the column from start
     * @config {Boolean} hidden
     * @category Layout
     */
    {
      name: 'hidden',
      defaultValue: false
    },
    /**
     * Convenient way of putting a column in the "locked" region. Same effect as specifying region: 'locked'.
     * If you have defined your own regions (using {@link Grid.view.Grid#config-subGridConfigs}) you should use
     * {@link #config-region} instead of this one.
     * @config {Boolean} locked
     * @default false
     * @category Layout
     */
    {
      name: 'locked'
    },
    /**
     * Region (part of the grid, it can be configured with multiple) where to display the column. Defaults to
     * {@link Grid.view.Grid#config-defaultRegion}.
     * @config {String} region
     * @category Layout
     */
    {
      name: 'region'
    }, //endregion
    // region Menu

    /**
     * Show column picker for the column
     * @config {Boolean} showColumnPicker
     * @default true
     * @category Menu
     */
    {
      name: 'showColumnPicker',
      defaultValue: true
    },
    /**
     * false to prevent showing a context menu on the column header element
     * @config {Boolean} enableHeaderContextMenu
     * @default true
     * @category Menu
     */
    {
      name: 'enableHeaderContextMenu',
      defaultValue: true
    },
    /**
     * Set to `false` to prevent showing a context menu on the cell elements in this column
     * @config {Boolean} enableCellContextMenu
     * @default true
     * @category Menu
     */
    {
      name: 'enableCellContextMenu',
      defaultValue: true
    },
    /**
     * Extra items to show in the header context menu for this column.
     *
     * **Deprecated** to be an array:
     *
     * ```javascript
     * headerMenuItems : [
     *     { text : 'Custom item' }
     * ]
     * ```
     *
     * Now it has to be a named object:
     *
     * ```javascript
     * headerMenuItems : {
     *     customItem : { text : 'Custom item' }
     * }
     * ```
     *
     * @config {Object} headerMenuItems
     * @category Menu
     */
    'headerMenuItems',
    /**
     * Extra items to show in the cell context menu for this column
     *
     * **Deprecated** to be an array:
     *
     * ```javascript
     * cellMenuItems : [
     *     { text : 'Custom item' }
     * ]
     * ```
     *
     * Now it has to be a named object:
     *
     * ```javascript
     * cellMenuItems : {
     *     customItem : { text : 'Custom item' }
     * }
     * ```
     *
     * @config {Object} cellMenuItems
     * @category Menu
     */
    'cellMenuItems', //endregion
    //region Summary

    /**
     * Summary type (when using Summary feature). Valid types are:
     * <dl class="wide">
     * <dt>sum <dd>Sum of all values in the column
     * <dt>add <dd>Alias for sum
     * <dt>count <dd>Number of rows
     * <dt>countNotEmpty <dd>Number of rows containing a value
     * <dt>average <dd>Average of all values in the column
     * <dt>function <dd>A custom function, used with store.reduce. Should take arguments (sum, record)
     * </dl>
     * @config {String} sum
     * @category Summary
     */
    'sum',
    /**
     * Summary configs, use if you need multiple summaries per column. Replaces {@link #config-sum} and
     * {@link #config-summaryRenderer} configs. Accepts an array of objects with the following fields:
     * * sum - Matching {@link #config-sum}
     * * renderer - Matching {@link #config-summaryRenderer}
     * * seed - Initial value when using a function as `sum`
     * @config {Object[]} summaries
     * @category Summary
     */
    'summaries',
    /**
     * Renderer function for summary (when using Summary feature). The renderer is called with the calculated
     * summary as only argument.
     * @config {Function} summaryRenderer
     * @category Summary
     */
    'summaryRenderer', //endregion
    //region Misc

    /**
     * Column settings at different responsive levels, see responsive demo under examples/
     * @config {Object} responsiveLevels
     * @category Misc
     */
    'responsiveLevels',
    /**
     * Tags, may be used by ColumnPicker feature for grouping columns by tag in the menu
     * @config {String[]} tags
     * @category Misc
     */
    'tags',
    /**
     * Column config to apply to normal config if viewed on a touch device
     * @config {Object} touchConfig
     * @category Misc
     */
    'touchConfig',
    /**
     * When using the tree feature, exactly one column should specify { tree: true }
     * @config {Boolean} tree
     * @category Misc
     */
    'tree',
    /**
     * Determines which type of filtering to use for the column. Usually determined by the column type used,
     * but may be overridden by setting this field.
     * @config {String} filterType
     * @category Misc
     */
    'filterType',
    /**
     * By default, any rendered column text content is HTML-encoded. Set this flag to `false` disable this and allow rendering html elements
     * @config {Boolean} htmlEncode
     * @default true
     * @category Misc
     */
    {
      name: 'htmlEncode',
      defaultValue: true
    },
    /**
     * Set to `true`to automatically call DomHelper.sync for html returned from a renderer. Should in most cases
     * be more performant than replacing entire innerHTML of cell and also allows CSS transitions to work. Has
     * no effect unless {@link #config-htmlEncode} is disabled. Returned html must contain a single root element (that can have
     * multiple children). See PercentColumn for example usage.
     * @config {Boolean} autoSyncHtml
     * @default false
     * @category Misc
     */
    {
      name: 'autoSyncHtml',
      defaultValue: false
    }, 'type',
    /**
     * Set to `true` to have the {@link Grid.feature.CellEdit CellEdit} feature update the record being
     * edited live upon field edit instead of when editing is finished by using `TAB` or `ENTER`
     * @config {Boolean} instantUpdate
     * @category Misc
     */
    {
      name: 'instantUpdate',
      defaultValue: false
    }, {
      name: 'repaintOnResize',
      defaultValue: false
    },
    /**
     * An optional query selector to select a sub element within the cell being
     * edited to align a cell editor's `X` position and `width` to.
     * @config {String} editTargetSelector
     * @category Misc
     */
    'editTargetSelector', //endregion
    //region Export

    /**
     * Used by the Export feature. Set to `false` to omit a column from an exported dataset
     * @config {Boolean} exportable
     * @default true
     * @category Export
     */
    {
      name: 'exportable',
      defaultValue: true
    },
    /**
     * Column type which will be used by {@link Grid.util.TableExporter}. See list of available types in TableExporter
     * doc. Returns undefined by default, which means column type should be read from the record field.
     * @config {String} exportedType
     * @category Export
     */
    {
      name: 'exportedType'
    } //endregion
    ];
  } // prevent undefined fields from being exposed, to simplify spotting errors

  static get autoExposeFields() {
    return false;
  } //endregion
  //region Init

  construct(data, store) {
    const me = this;
    me.masterStore = store; // Store might be an array

    if (store) {
      me._grid = Array.isArray(store) ? store[0].grid : store.grid;
    }

    me.localizableProperties = data.localizableProperties || ['text'];

    if (data.localeClass) {
      me.localeClass = data.localeClass;
    }

    super.construct(data, store, null, false); // Default value for region is assigned by the ColumnStore in createRecord(), same for `locked`
    // Allow field : null if the column does not rely on a record field.
    // For example the CheckColumn when used by GridSelection.

    if (!('field' in me.data)) {
      me.field = '_' + (me.type || '') + ++Column.emptyCount;
      me.noFieldSpecified = true;
    } // If our field is a dot separated path, we must use ObjectHelper.getPath to extract our value

    me.hasComplexMapping = me.field && me.field.includes('.');

    if (!me.width && !me.flex && !me.children) {
      // Set the width silently because we're in construction.
      me.set({
        width: Column.defaultWidth,
        flex: null
      }, null, true);
    }
  }

  remove() {
    const {
      subGrid,
      grid
    } = this,
          focusedCell = subGrid && grid && grid.focusedCell; // Prevent errors when removing the column that the owning grid has registered as focused.

    if (focusedCell && focusedCell.columnId === this.id) {
      // Focus is in the grid, navigate before column is removed
      if (DomHelper.activeElement === grid) {
        grid.navigateRight();
      } // Focus not in the grid, bump the focused cell pointer to the ext visible column.
      else {
          focusedCell.columnId = subGrid.columns.getAdjacentVisibleLeafColumn(this.id, true, true).id;
        }
    }

    super.remove();
  }
  /**
   * Extracts the value from the record specified by this Column's {@link #config-field} specification.
   *
   * This will work if the field is a dot-separated path to access fields in associated records, eg
   *
   * ```javascript
   *  field : 'resource.calendar.name'
   * ```
   *
   * **Note:** This is the raw field value, not the value returned by the {@link #config-renderer}.
   * @param {Core.data.Model} record The record from which to extract the field value.
   * @returns {*} The value of the referenced field if any.
   */

  getRawValue(record) {
    const me = this;

    if (me.hasComplexMapping) {
      return ObjectHelper.getPath(record, me.field);
    } // Engine can change field value to null, in which case cell will render previous record value, before project commit

    return record[me.field];
  } // Create an ownership hierarchy which links columns up to their SubGrid if no owner injected.

  get owner() {
    return this._owner || this.subGrid;
  }

  set owner(owner) {
    this._owner = owner;
  }

  get nextVisibleSibling() {
    // During move from one region to another, nextSibling might not be wired up to the new next sibling in region.
    // (Because the order in master store did not change)
    const region = this.region;
    let next = this.nextSibling;

    while (next && (next.hidden || next.region !== region)) {
      next = next.nextSibling;
    }

    return next;
  }

  get isLastInSubGrid() {
    return !this.nextVisibleSibling && (!this.parent || this.parent.isLastInSubGrid);
  }
  /**
   * The header element for this Column. *Only available after the grid has been rendered*.
   *
   * **Note that column headers are rerendered upon mutation of Column values, so this
   * value is volatile and should not be cached, but should be read whenever needed.**
   * @property {HTMLElement}
   * @readonly
   */

  get element() {
    return this.grid.getHeaderElement(this);
  }
  /**
   * The text wrapping element for this Column. *Only available after the grid has been rendered*.
   *
   * This is the full-width element which *contains* the text-bearing element and any icons.
   *
   * **Note that column headers are rerendered upon mutation of Column values, so this
   * value is volatile and should not be cached, but should be read whenever needed.**
   * @property {HTMLElement}
   * @readonly
   */

  get textWrapper() {
    return DomHelper.getChild(this.element, '.b-grid-header-text');
  }
  /**
   * The text containing element for this Column. *Only available after the grid has been rendered*.
   *
   * **Note that column headers are rerendered upon mutation of Column values, so this
   * value is volatile and should not be cached, but should be read whenever needed.**
   * @property {HTMLElement}
   * @readonly
   */

  get textElement() {
    return DomHelper.down(this.element, '.b-grid-header-text-content');
  }
  /**
   * The child element into which content should be placed. This means where any
   * contained widgets such as filter input fields should be rendered. *Only available after the grid has been rendered*.
   *
   * **Note that column headers are rerendered upon mutation of Column values, so this
   * value is volatile and should not be cached, but should be read whenever needed.**
   * @property {HTMLElement}
   * @readonly
   */

  get contentElement() {
    return DomHelper.down(this.element, '.b-grid-header-children');
  }
  /**
   * The Field to use as editor for this column
   * @private
   * @readonly
   */

  get editor() {
    let editor = this.data.editor;

    if (editor && !(editor instanceof Widget)) {
      // Give frameworks a shot at injecting their own editor, wrapped as a widget
      const result = this.grid.processCellEditor({
        editor,
        field: this.field
      });

      if (result) {
        // Use framework editor
        editor = this.data.editor = result.editor;
      } else {
        if (typeof editor === 'string') {
          editor = {
            type: editor
          };
        } // The two configs, default and configured must be deep merged.

        editor = this.data.editor = Widget.create(ObjectHelper.merge(this.defaultEditor, editor, {
          owner: this.grid
        }));
      }
    }

    return editor;
  }

  set editor(editor) {
    this.data.editor = editor;
  }
  /**
   * A config object specifying the editor to use to edit this column.
   * @private
   * @readonly
   */

  get defaultEditor() {
    return {
      type: 'textfield',
      name: this.field
    };
  }
  /**
   * Default settings for the column, applied in constructor. None by default, override in subclass.
   * @member {Object} defaults
   * @returns {Object}
   * @readonly
   */
  //get defaults() {
  //    return {};
  //}
  //endregion
  //region Properties

  get isFocusable() {
    return this.isLeaf;
  }

  static get type() {
    return 'column';
  }

  static get text() {
    return this.$meta.fields.defaults.text;
  }

  get grid() {
    return this._grid || this.parent && this.parent.grid;
  }

  get locked() {
    return this.data.region === 'locked';
  }

  set locked(locked) {
    this.region = locked ? 'locked' : 'normal';
  } // parent headers cannot be sorted by

  get sortable() {
    return this.isLeaf && this.data.sortable;
  }

  set sortable(sortable) {
    this.set('sortable', sortable);
  } // parent headers cannot be grouped by

  get groupable() {
    return this.isLeaf && this.data.groupable;
  }

  set groupable(groupable) {
    this.set('groupable', groupable);
  } //endregion
  //region Show/hide

  /**
   * Hides this column.
   */

  hide(silent = false) {
    const me = this,
          parent = me.parent; // Reject non-change

    if (!me.hidden) {
      me.hidden = true;

      if (parent && !parent.isRoot) {
        // check if all sub columns are hidden, if so hide parent
        const anyVisible = parent.children.some(child => child.hidden !== true);

        if (!anyVisible && !parent.hidden) {
          silent = true; // hiding parent will trigger event

          parent.hide();
        }
      }

      if (me.children) {
        me.children.forEach(child => child.hide(true));
      }

      if (!silent) {
        me.stores.forEach(store => store.trigger('columnHide', {
          column: me
        }));
      }
    }
  }
  /**
   * Shows this column.
   */

  show(silent = false) {
    const me = this,
          parent = me.parent; // Reject non-change

    if (me.hidden) {
      me.hidden = false;

      if (parent && parent.hidden) {
        parent.show();
      }

      if (me.children) {
        me.children.forEach(child => child.show(true));
      } // event is triggered on chained stores

      if (!silent) {
        me.stores.forEach(store => store.trigger('columnShow', {
          column: me
        }));
      }
    }
  }
  /**
   * Toggles the column visibility.
   * @param {Boolean} force Set to true (visible) or false (hidden) to force a certain state
   */

  toggle(force = null) {
    if (this.hidden && force === undefined || force === true) return this.show();
    if (!this.hidden && force === undefined || force === false) return this.hide();
  } //endregion
  //region Index & id

  /**
   * Generates an id for the column when none is set. Generated ids are 'col1', 'col2' and so on. If a field is
   * specified (as it should be in most cases) the field name is used instead: 'name1', 'age2' ...
   * @private
   * @returns {String}
   */

  generateId() {
    if (!Column.generatedIdIndex) Column.generatedIdIndex = 0;
    return (this.field ? this.field.replace(/\./g, '-') : 'col') + ++Column.generatedIdIndex;
  }
  /**
   * Index among all flattened columns
   * @property {Number}
   * @readOnly
   * @internal
   */

  get allIndex() {
    return this.masterStore.indexOf(this);
  } //endregion
  //region Width

  /**
   * Returns minimal width in pixels for applying to style according to the current `width` and `minWidth`. Used for IE11.
   * @internal
   */

  get calcMinWidth() {
    const {
      width,
      minWidth
    } = this.data;

    if (validWidth(width) && validWidth(minWidth)) {
      return Math.max(parseInt(width) || 0, parseInt(minWidth) || 0);
    } else {
      return width;
    }
  }
  /**
   * Get/set columns width in px. If column uses flex, width will be undefined.
   * Setting a width on a flex column cancels out flex.
   *
   * **NOTE:** Grid might be configured to always stretch the last column, in which case the columns actual width
   * might deviate from the configured width.
   *
   * ```javascript
   * let grid = new Grid({
   *     appendTo : 'container',
   *     height   : 200,
   *     width    : 400,
   *     columns  : [{
   *         text  : 'First column',
   *         width : 100
   *     }, {
   *         text  : 'Last column',
   *         width : 100 // last column in the grid is always stretched to fill the free space
   *     }]
   * });
   *
   * grid.columns.last.element.offsetWidth; // 300 -> this points to the real element width
   * ```
   * @property {Number|String}
   */

  get width() {
    return this.data.width;
  }

  set width(width) {
    const data = {
      width
    };

    if (width && 'flex' in this.data) {
      data.flex = null; // remove flex when setting width to enable resizing flex columns
    }

    this.set(data);
  }

  set flex(flex) {
    const data = {
      flex
    };

    if (flex && 'width' in this.data) {
      data.width = null; // remove width when setting flex
    }

    this.set(data);
  }

  get flex() {
    return this.data.flex;
  } // Private, only used in tests where standalone Headers are created with no grid
  // from which to lookup the associate SubGrid.

  set subGrid(subGrid) {
    this._subGrid = subGrid;
  }
  /**
   * Get the SubGrid to which this column belongs
   * @property {Grid.view.SubGrid}
   * @readonly
   */

  get subGrid() {
    return this._subGrid || (this.grid ? this.grid.getSubGridFromColumn(this) : undefined);
  }
  /**
   * Get the element for the SubGrid to which this column belongs
   * @property {HTMLElement}
   * @readonly
   * @private
   */

  get subGridElement() {
    return this.subGrid.element;
  } // Returns size in pixels for measured value

  measureSize(value) {
    return DomHelper.measureSize(value, this.subGrid ? this.subGrid.element : undefined);
  } // This method is used to calculate minimum row width for edge and safari
  // It calculates minimum width of the row taking column hierarchy into account

  calculateMinWidth() {
    const me = this,
          width = me.measureSize(me.width),
          minWidth = me.measureSize(me.minWidth);
    let minChildWidth = 0;

    if (me.children) {
      minChildWidth = me.children.reduce((result, column) => {
        return result + column.calculateMinWidth();
      }, 0);
    }

    return Math.max(width, minWidth, minChildWidth);
  }
  /**
   * Resizes the column to match the widest string in it. By default it also measures the column header, this
   * behaviour can be configured by setting {@link Grid.view.Grid#config-resizeToFitIncludesHeader}.
   *
   * Called internally when you double click the edge between
   * column headers, but can also be called programmatically. For performance reasons it is limited to checking 1000
   * rows surrounding the current viewport.
   *
   * @param {Number|Number[]} widthMin Minimum allowed width. If content width is less than this, this width is used
   * instead. If this parameter is an array, the first element is `widthMin` and the seconds is `widthMax`.
   * @param {Number} widthMax Maximum allowed width. If the content width is greater than this number, this width
   * is used instead.
   */

  resizeToFitContent(widthMin, widthMax, batch = false) {
    const me = this,
          {
      grid,
      element,
      fitMode
    } = me,
          {
      rowManager,
      store
    } = grid,
          {
      count
    } = store;

    if (count <= 0 || me.fitMode === 'none' || !me.fitMode) {
      return;
    }

    const [row] = rowManager.rows,
          {
      rowElement,
      cellElement
    } = grid.beginGridMeasuring();
    let maxWidth = 0,
        start,
        end,
        i,
        record,
        value,
        length,
        longest = {
      length: 0,
      record: null
    }; // Fake element data to be able to use Row#renderCell()

    cellElement._domData = {
      columnId: me.id,
      row,
      rowElement
    }; // Clear cellElement, since it is being reused between columns

    cellElement.innerHTML = ''; // Measure header unless configured not to

    if (grid.resizeToFitIncludesHeader) {
      // Cache the padding
      if (!grid.$headerPadding) {
        const style = window.getComputedStyle(element);
        grid.$headerPadding = parseInt(style.paddingLeft);
      } // Grab the header text content element

      const headerText = element.querySelector('.b-grid-header-text-content'); // Restyle it to shrinkwrap its text, measure and then restore

      headerText.style.cssText = 'flex: none; width: auto';
      maxWidth = headerText.offsetWidth + grid.$headerPadding * 2 + 2; // +2 to avoid overflow ellipsis

      headerText.style.cssText = '';
    } // If it's a very large dataset, measure the maxWidth of the field in the 1000 rows
    // surrounding the rendered block.

    if (count > 1000) {
      start = Math.max(Math.min(rowManager.topIndex + Math.round(rowManager.rowCount / 2) - 500, count - 1000), 0);
      end = start + 1000;
    } else {
      start = 0;
      end = count;
    }

    for (i = start; i < end; i++) {
      record = store.getAt(i);
      value = me.getRawValue(record); // In value mode we determine the record with the longest value, no rendering involved

      if (fitMode === 'value') {
        length = String(value).length;
      } // In exact and textContent modes we have to render the records
      else {
          row.renderCell({
            cellElement,
            record,
            updatingSingleRow: true,
            isMeasuring: true
          }); // Reading textContent is "cheap", it does not require a layout

          if (fitMode === 'textContent') {
            length = cellElement.textContent.length;
          } // Using exact mode, measure the cell = expensive
          else {
              const width = cellElement.offsetWidth;

              if (width > maxWidth) {
                maxWidth = width;
              }
            }
        }

      if (length > longest.length) {
        longest = {
          record,
          length
        };
      }
    } // value mode and textContent mode both required us to render and measure the record determined to be the
    // longest above

    if (fitMode === 'value' || fitMode === 'textContent') {
      row.renderCell({
        cellElement,
        record: longest.record,
        updatingSingleRow: true,
        isMeasuring: true
      });
      maxWidth = Math.max(maxWidth, cellElement.offsetWidth);
    }

    if (Array.isArray(widthMin)) {
      [widthMin, widthMax] = widthMin;
    }

    maxWidth = Math.max(maxWidth, widthMin || 0);
    maxWidth = Math.min(maxWidth, widthMax || 1e6); // 1 million px default max
    // Batch mode saves a little time by not removing the measuring elements between columns

    if (!batch) {
      grid.endGridMeasuring();
    }

    me.width = maxWidth;
    return maxWidth;
  } //endregion
  //region State

  /**
   * Get column state, used by State mixin
   * @private
   */

  getState() {
    const me = this,
          state = {
      id: me.id,
      // State should only store column attributes which user can modify via UI (except column index).
      // User can hide column, resize or move it to neighbor region
      [me.flex ? 'flex' : 'width']: me.flex ? me.flex : me.width,
      hidden: me.hidden,
      region: me.region,
      locked: me.locked
    };

    if (me.children) {
      state.children = me.children.map(child => child.getState());
    }

    return state;
  }
  /**
   * Apply state to column, used by State mixin
   * @private
   */

  applyState(state) {
    const me = this;
    me.beginBatch();

    if ('locked' in state) {
      me.locked = state.locked;
    }

    if ('width' in state) {
      me.width = state.width;
    }

    if ('flex' in state) {
      me.flex = state.flex;
    }

    if ('width' in state && me.flex) {
      me.flex = undefined;
    } else if ('flex' in state && me.width) {
      me.width = undefined;
    }

    if ('region' in state) {
      me.region = state.region;
    }

    me.endBatch();

    if ('hidden' in state) {
      me.toggle(state.hidden !== true);
    }
  } //endregion
  //region Other

  /**
   * Refresh the cell for supplied record in this column, if that cell is rendered.
   * @param {Core.data.Model} record Record used to get row to update the cell in
   */

  refreshCell(record) {
    this.grid.rowManager.refreshCell(record, this.id);
  }
  /**
   * Clear cell contents. Base implementation which just sets innerHTML to blank string.
   * Should be overridden in subclasses to clean up for examples widgets.
   * @param {HTMLElement} cellElement
   * @internal
   */

  clearCell(cellElement) {
    cellElement.innerHTML = '';
  }
  /**
   * Override in subclasses to allow/prevent editing of certain rows.
   * @param {Core.data.Model} record
   * @internal
   */

  canEdit(record) {
    // the record can decide which column is editable
    if (record.isEditable) {
      const isEditable = record.isEditable(this.field); // returns undefined for unknown field

      if (isEditable !== undefined) {
        return isEditable;
      }
    }

    return true;
  } //endregion

} // Registered in ColumnStore as we can't have this in Column due to circular dependencies
// ColumnStore.registerColumnType(Column);

Column.emptyCount = 0;
Column.defaultWidth = 100;
Column.exposeProperties();
Column._$name = 'Column';

/**
 * @module Grid/data/ColumnStore
 */

/**
 * A store specialized in handling columns. Used by the Grid to hold its columns and used as a chained store by each SubGrid
 * to hold theirs. Should not be instanced directly, instead access it through `grid.columns` or `subGrid.columns`
 *
 * ```
 * // resize first column
 * grid.columns.first.width = 200;
 *
 * // remove city column
 * grid.columns.get('city').remove();
 *
 * // add new column
 * grid.columns.add({text : 'New column'});
 *
 * // add new column to specific region (SubGrid)
 * grid.columns.add({text : 'New column', region : 'locked'});
 *
 * // add new column to 'locked' region (SubGrid)
 * grid.columns.add({text : 'New column', locked : true});
 * ```
 *
 * @extends Core/data/Store
 */

class ColumnStore extends Localizable(Store) {
  //region events

  /**
   * Fires when a column is shown.
   * @event columnShow
   * @param {Grid.data.ColumnStore} source The store which triggered the event.
   * @param {Grid.column.Column} column The column which status has been changed.
   */

  /**
   * Fires when a column has been hidden.
   * @event columnHide
   * @param {Grid.data.ColumnStore} source The store which triggered the event.
   * @param {Grid.column.Column} column The column which status has been changed.
   */
  //endregion
  static get defaultConfig() {
    return {
      modelClass: Column,
      tree: true,

      /**
       * Automatically adds a field definition to the store used by the Grid when adding a new Column displaying a
       * non-existing field.
       *
       * To enable this behaviour:
       *
       * ```javascript
       * const grid = new Grid({
       *     columns : {
       *         autoAddField : true,
       *         data         : [
       *             // Column definitions here
       *         ]
       *     }
       * }
       *
       * @config {Boolean}
       * @default
       */
      autoAddField: false
    };
  }

  construct(config) {
    const me = this; // Consequences of ColumnStore construction can cause reading of grid.columns
    // so set the property early.

    if (config.grid) {
      config.grid._columnStore = me;
      me.id = `${config.grid.id}-columns`; // Visible columns must be invalidated on expand/collapse

      config.grid.on({
        subGridCollapse: 'clearSubGridCaches',
        subGridExpand: 'clearSubGridCaches',
        thisObj: me
      });
    }

    super.construct(config); // So that we can invalidate cached collections which take computing so that we compute them
    // only when necessary. For example when asking for the visible leaf columns, we do not want
    // to compute that each time.

    me.on({
      change: me.clearCaches,
      thisObj: me,
      prio: 1
    });
  }

  get modelClass() {
    return this._modelClass;
  }

  set modelClass(ClassDef) {
    this._modelClass = ClassDef;
  }

  doDestroy() {
    const allColumns = [];
    this.traverse(column => allColumns.push(column));
    super.doDestroy(); // Store's destroy unjoins all records. Destroy all columns *after* that.

    allColumns.forEach(column => column.destroy());
  } // Overridden because the flat collection only contains top level columns,
  // not leaves - group columns are *not* expanded.

  getById(id) {
    return super.getById(id) || this.idRegister[id];
  }

  forEach(fn, thisObj = this) {
    // Override to omit root
    this.traverseWhile((n, i) => fn.call(thisObj, n, i), true);
  }

  get totalFixedWidth() {
    let result = 0;

    for (const col of this) {
      if (!col.hidden) {
        // if column has children (grouped header) use they to width increment
        if (col.children) {
          col.children.forEach(childCol => result += this.calculateFixedWidth(childCol));
        } else {
          result += this.calculateFixedWidth(col);
        }
      }
    }

    return result;
  }

  calculateFixedWidth(column) {
    if (column.flex) {
      return column.measureSize(Column.defaultWidth);
    } else {
      return Math.max(column.measureSize(column.width), column.measureSize(column.minWidth));
    }
  }
  /**
   * Returns the top level columns. If using grouped columns, this is the top level columns. If no grouped
   * columns are being used, this is the leaf columns.
   * @property {Grid.column.Column[]}
   * @readonly
   */

  get topColumns() {
    return this.isChained ? this.masterStore.rootNode.children.filter(this.chainedFilterFn) : this.rootNode.children;
  }
  /**
   * Returns the visible leaf headers which drive the rows' cell content.
   * @property {Grid.column.Column[]}
   * @readonly
   */

  get visibleColumns() {
    const me = this;

    if (!me._visibleColumns) {
      me._visibleColumns = me.leaves.filter(column => !column.hidden && (!column.subGrid || !column.subGrid.collapsed));
    }

    return me._visibleColumns;
  }

  clearSubGridCaches(subGrid) {
    subGrid.columns.clearCaches();
    this.clearCaches();
  }

  clearCaches() {
    this._visibleColumns = null;
  }

  onMasterDataChanged(event) {
    super.onMasterDataChanged(event); // If master store has changes we also need to clear cached columns, in case a column was hidden

    this.clearCaches();
  }

  getAdjacentVisibleLeafColumn(columnOrId, next = true, wrap = false) {
    const columns = this.visibleColumns,
          column = columnOrId instanceof Column ? columnOrId : this.getById(columnOrId);
    let idx = columns.indexOf(column) + (next ? 1 : -1); // If we walked off either end, wrap if directed to do so,
    // otherwise, return null;

    if (!columns[idx]) {
      if (wrap) {
        idx = next ? 0 : columns.length - 1;
      } else {
        return null;
      }
    }

    return columns[idx];
  }
  /**
   * Bottom columns are the ones displayed in the bottom row of a grouped header, or all columns if not using a grouped
   * header. They are the columns that actually display any data.
   * @property {Grid.column.Column[]}
   * @readonly
   */

  get bottomColumns() {
    return this.leaves;
  }
  /**
   * Get column by field. To be sure that you are getting exactly the intended column, use {@link Core.data.Store#function-getById Store#getById()} with the
   * columns id instead.
   * @param {String} field Field name
   * @returns {Grid.column.Column}
   */

  get(field) {
    return this.findRecord('field', field, true);
  }
  /**
   * Used internally to create a new record in the store. Creates a column of the correct type by looking up the
   * specified type among registered columns.
   * @private
   */

  createRecord(data) {
    const {
      grid = {}
    } = this,
          // Some ColumnStore tests lacks Grid
    {
      store
    } = grid;
    let columnClass = this.modelClass;

    if (data.type) {
      columnClass = ColumnStore.getColumnClass(data.type);

      if (!columnClass) {
        throw new Error(`Column type '${data.type}' not registered`);
      }
    }

    if (data.locked) {
      data.region = 'locked';
      delete data.locked;
    }

    const column = new columnClass(data, this); // Doing this after construction, in case the columnClass has a default value for region (Schedulers
    // TimeAxisColumn has)

    if (!column.data.region) {
      column.data.region = grid.defaultRegion || 'normal';
    } // Add missing fields to Grids stores model

    if (this.autoAddField && !column.noFieldSpecified && store && !store.modelClass.getFieldDefinition(column.field)) {
      let fieldDefinition = column.field; // Some columns define the type to use for new fields (date, number etc)

      if (column.constructor.fieldType) {
        fieldDefinition = {
          name: column.field,
          type: column.constructor.fieldType
        };
      }

      store.modelClass.addField(fieldDefinition);
    }

    return column;
  }
  /**
   * indexOf extended to also accept a columns field, for backward compatibility.
   * ```
   * grid.columns.indexOf('name');
   * ```
   * @param {Core.data.Model|String} recordOrId
   * @returns {Number}
   */

  indexOf(recordOrId) {
    // TODO: build the need for field away
    const index = super.indexOf(recordOrId);
    if (index > -1) return index; // no record found by id, find by field since old code relies on that instead of id
    // TODO: replace such cases with columns id

    return this.records.findIndex(r => r.field === recordOrId);
  } //region Column types

  /**
   * Call from custom column to register it with ColumnStore. Required to be able to specify type in column config.
   * @param {Function} columnClass The {@link Grid.column.Column} subclass to register.
   * @param {Boolean} simpleRenderer Pass `true` if its default renderer does *not* use other fields from the passed
   * record than its configured {@link Grid.column.Column#config-field}. This enables more granular cell updating
   * upon record mutation.
   * @example
   * // create and register custom column
   * class CustomColumn {
   *  static get type() {
   *      return 'custom';
   *  }
   * }
   * ColumnStore.registerColumnType(CustomColumn, true);
   * // now possible to specify in column config
   * let grid = new Grid({
   *   columns: [
   *     { type: 'custom', field: 'id' }
   *   ]
   * });
   */

  static registerColumnType(columnClass, simpleRenderer = false) {
    if (!ColumnStore.columnTypes) ColumnStore.columnTypes = {};
    columnClass.simpleRenderer = simpleRenderer;
    ColumnStore.columnTypes[columnClass.type] = columnClass;
  }
  /**
   * Returns registered column class for specified type.
   * @param type Type name
   * @returns {Grid.column.Column}
   * @internal
   */

  static getColumnClass(type) {
    return ColumnStore.columnTypes && ColumnStore.columnTypes[type];
  } //endregion

}
/**
 * Custom {@link Grid.data.ColumnStore} event which triggers when a column is resized, i.e. its width has been changed
 *
 * @param {Function} handler
 * @param {Object} [thisObj]
 */

const columnResizeEvent = (handler, thisObj) => ({
  update: ({
    store,
    record,
    changes
  }) => {
    let result = true;

    if ('width' in changes || 'minWidth' in changes || 'flex' in changes) {
      result = handler.call(thisObj, {
        store,
        record,
        changes
      });
    }

    return result;
  }
}); // Can't have this in Column due to circular dependencies

ColumnStore.registerColumnType(Column, true);
ColumnStore._$name = 'ColumnStore';

/**
 * @module Grid/column/ActionColumn
 */

/**
 * A column that displays actions (clickable icons) in the cell.
 *
 * ```javascript
 * new TreeGrid({
 *     appendTo : document.body,
 *     columns  : [{
 *         type    : 'action',
 *         text    : 'Increase amount',
 *         actions : [{
 *             cls      : 'b-fa b-fa-plus',
 *             renderer : ({ action, record }) => `<i class="b-action-item ${action.cls} b-${record.enabled ? "green" : "red"}-class"></i>`,
 *             visible  : ({ record }) => record.canAdd,
 *             tooltip  : ({ record }) => `<p class="b-nicer-than-default">Add to ${record.name}</p>`,
 *             onClick  : ({ record }) => console.log(`Adding ${record.name}`)
 *         }, {
 *             cls     : 'b-fa b-fa-pencil',
 *             tooltip : 'Edit note',
 *             onClick : ({ record }) => console.log(`Editing ${record.name}`)
 *         }]
 *     }]
 * });
 * ```
 *
 * Actions may be placed in {@link Grid/feature/Group} headers, by setting `actions.showForGroup` to `true`. Those actions will not be shown on normal rows.
 *
 * @extends Grid/column/Column
 *
 * @classType action
 * @externalexample column/ActionColumn.js
 */

class ActionColumn extends Column {
  static get type() {
    return 'action';
  }

  static get fields() {
    return [
    /**
     * An array of action config objects
     * @property {String} actions.cls CSS Class for action icon
     * @property {Function|String} actions.tooltip Tooltip text
     * @property {Core.data.Model} actions.tooltip.record The model instance
     * @property {Function|Boolean} actions.visible Boolean to define the action icon visibility or a callback function to change it dynamically
     * @property {Core.data.Model} actions.visible.record The model instance
     * @property {Function} actions.onClick Callback to handle click action item event
     * @property {Core.data.Model} actions.onClick.record The model instance
     * @property {Boolean} actions.showForGroup Set to true to have action icon visible in group headers only when using the `group` feature
     * @property {Function} actions.renderer A render function used to define the action element. Expected to return a HTML string or a DOM config object.
     * **Note**: when specified, the `cls` action config is ignored. Make sure you add an action icon manually, for example:
     * ```javascript
     * {
     *      type    : 'action',
     *      text    : 'Increase amount',
     *      actions : [{
     *          cls      : 'b-fa b-fa-plus', // this line will be ignored
     *          renderer : ({ record }) => '<i class="b-action-item b-fa b-fa-plus"></i> ' + record.name,
     *          onClick  : ({ record }) => {}
     *      }]
     * }
     * ```
     * @property {Core.data.Model} actions.renderer.record The model instance
     * @config {Object[]} actions List of action configs
     * @category Common
     */
    'actions',
    /**
     * Set true to hide disable actions in this column if the grid is {@link Core.widget.Widget#config-readOnly}
     * @config {Boolean} disableIfGridReadOnly
     * @default
     * @category Common
     */
    {
      name: 'disableIfGridReadOnly',
      defaultValue: false
    }];
  }

  static get defaults() {
    return {
      /**
       * Filtering by action column is not supported by default, because it has a custom renderer and uses HTML with icons as content.
       * @config {Boolean} filterable
       * @default false
       * @category Interaction
       */
      filterable: false,

      /**
       * Grouping by action column is not supported by default, because it has a custom renderer and uses HTML with icons as content.
       * @config {Boolean} groupable
       * @default false
       * @category Interaction
       */
      groupable: false,

      /**
       * Sorting by action column is not supported by default, because it has a custom renderer and uses HTML with icons as content.
       * @config {Boolean} sortable
       * @default false
       * @category Interaction
       */
      sortable: false,

      /**
       * Editor for action column is not supported by default, because it has a custom renderer and uses HTML with icons as content.
       * @config {Boolean} editor
       * @default false
       * @category Interaction
       */
      editor: false,

      /**
       * Searching by action column is not supported by default, because it has a custom renderer and uses HTML with icons as content.
       * @config {Boolean} searchable
       * @default false
       * @category Interaction
       */
      searchable: false,

      /**
       * By default, for action column this flag is switched to `true`, because the content of this column is always HTML.
       * @config {Boolean} htmlEncode
       * @default false
       * @category Misc
       */
      htmlEncode: false,

      /**
       * Set to `true` to allow the column to being drag-resized when the ColumnResize plugin is enabled.
       * @config {Boolean} resizable
       * @default false
       * @category Interaction
       */
      resizable: false,

      /**
       * Column minimal width. If value is Number then minimal width is in pixels.
       * @config {Number|String} minWidth
       * @default 30
       * @category Layout
       */
      minWidth: 30
    };
  }

  construct(config, store) {
    const me = this;
    me.internalCellCls = 'b-action-cell';
    super.construct(...arguments); // use auto-size only as default behaviour

    if (!config.width && !config.flex) {
      me.grid.on('paint', me.updateAutoWidth, me);
    } // https://github.com/bryntum/support/issues/1754
    // Create exclusive method to be independent if features like cellEdit are disabled

    me.grid.on({
      cellClick: 'onGridCellClick',
      thisObj: me
    });

    if (me.disableIfGridReadOnly) {
      me.grid.element.classList.add('b-actioncolumn-readonly');
    }
  }
  /**
   * Renderer that displays action icon(s) in the cell.
   * @private
   */

  renderer({
    column,
    record
  }) {
    const inGroupTitle = record && 'groupRowFor' in record.meta;
    return {
      className: {
        'b-action-ct': 1
      },
      children: column.actions.map((actionConfig, index) => {
        if ('visible' in actionConfig) {
          if (typeof actionConfig.visible === 'function' && actionConfig.visible({
            record
          }) === false) {
            return '';
          }

          if (actionConfig.visible === false) {
            return '';
          }
        } // check if an action allowed to be shown in case of using groupping

        if (inGroupTitle && !actionConfig.showForGroup || !inGroupTitle && actionConfig.showForGroup) {
          return '';
        }

        let btip;

        if (typeof actionConfig.tooltip === 'function') {
          btip = actionConfig.tooltip({
            record
          });
        } else {
          btip = actionConfig.tooltip || '';
        } // handle custom renderer if it is specified

        if (actionConfig.renderer && typeof actionConfig.renderer === 'function') {
          const customRendererData = actionConfig.renderer({
            index,
            record,
            column,
            tooltip: btip,
            action: actionConfig
          }); // take of set data-index to make onClick handler work stable

          if (typeof customRendererData === 'string') {
            return {
              tag: 'span',
              dataset: {
                index
              },
              html: customRendererData
            };
          } else {
            customRendererData.dataset = customRendererData.dataset || {};
            customRendererData.dataset.index = index;
            return customRendererData;
          }
        } else {
          return {
            tag: 'i',
            dataset: {
              index,
              btip
            },
            className: {
              'b-action-item': 1,
              [actionConfig.cls]: actionConfig.cls
            }
          };
        }
      })
    };
  }
  /**
   * Handle icon click and call action handler.
   * @private
   */

  onGridCellClick({
    grid,
    column,
    record,
    target
  }) {
    if (column !== this || !target.classList.contains('b-action-item')) {
      return;
    }

    let actionIndex = target.dataset.index; // index may be set in a parent node if user used an html string in his custom renderer
    // and we take care to set this property to support onClick handler

    if (!actionIndex) {
      actionIndex = target.parentElement.dataset && target.parentElement.dataset.index;
    }

    const action = column.actions[actionIndex],
          actionHandler = action && action.onClick;

    if (actionHandler) {
      this.callback(actionHandler, column, [{
        record,
        action
      }]);
    }
  }
  /**
   * Update width for actions column to fit content.
   * @private
   */

  updateAutoWidth() {
    const me = this,
          groupActions = []; // header may be disabled, in that case we won't be able to calculate the width properly

    if (!me.element) {
      return;
    }

    let actions = []; // collect group and non group actions to check length later

    me.actions.forEach(actionOriginal => {
      const action = { ...actionOriginal
      }; // remove possible visibility condition to make sure an action will exists in test HTML

      delete action.visible; // group actions shows in different row and never together with non group

      if (action.showForGroup) {
        delete action.showForGroup;
        groupActions.push(action);
      } else {
        actions.push(action);
      }
    }); // use longest actions length to calculate column width

    if (groupActions.length > actions.length) {
      actions = groupActions;
    }

    const actionsHtml = DomHelper.createElement(me.renderer({
      column: {
        actions
      }
    })).outerHTML;
    me.width = DomHelper.measureText(actionsHtml, me.element, true, me.element.parentElement);
  }

}
ColumnStore.registerColumnType(ActionColumn);
ActionColumn.exposeProperties();
ActionColumn._$name = 'ActionColumn';

/**
 * @module Grid/column/AggregateColumn
 */

/**
 * A column, which, when used as part of a Tree, aggregates the values of this column's descendants using
 * a configured function which defaults to `sum`.
 *
 * Default editor depends on the data field type. If it is a number, default editor is a {@link Core.widget.NumberField NumberField}.
 * Otherwise Default editor is a {@link Core.widget.TextField TextField}.
 *
 * ```
 * const grid = new TreeGrid({
 *   // Custom aggregation handler.
 *   // For test purposes, this just does "sum"
 *   myAggregator(...values) {
 *       let result = 0;
 *
 *       for (let i = 0, { length } = args; i < length; i++) {
 *           result += parseInt(args[i], 10);
 *       }
 *       return result;
 *   },
 *   columns : [
 *      { field : 'name', text : 'Name' },
 *
 *      // Will sum the ages of leaf nodes. This is the default.
 *      { type : 'aggregate', field : 'age', text : 'Age', renderer : ({ value }) => `<b>${value}<b>` },
 *
 *      // Will use AggregateColumn's built-in avg of scores of leaf nodes
 *      { type : 'aggregate', field : 'score', text : 'Score', function : 'avg' },
 *
 *      // Will use the grid's myAggregator function
 *      { type : 'aggregate', field : 'revenue', text : 'Revenue', function : 'up.myAggregator' },
 *   ]
 * });
 * ```
 *
 * @extends Grid/column/Column
 * @classType aggregate
 */

class AggregateColumn extends Column {
  //region Config
  static get type() {
    return 'aggregate';
  }

  static get fields() {
    return ['function'];
  }

  static get defaults() {
    return {
      /**
       * Math Function name, or function name prepended by `"up."` that is resolveable in an
       * ancestor component (such as the owning Grid, or a hight Container), or a function to
       * use to aggregate child record values for this column, or a function.
       *
       * This Column is provided with a `sum` and `avg` function. The default function is `sum`
       * which is used for the aggregation.
       * @config {Function|String}
       * @category Common
       */
      function: 'sum'
    };
  }

  construct(data, columnStore) {
    this.configuredAlign = 'align' in data;
    this.configuredEditor = 'editor' in data;
    super.construct(...arguments);
    const {
      grid
    } = columnStore;

    if (grid) {
      this.owner = grid;
      grid.on({
        paint: 'onGridPaint',
        thisObj: this,
        prio: 1000
      });
    }
  }

  onGridPaint({
    source: grid
  }) {
    this.store = grid.store;
  }

  set store(store) {
    const me = this,
          storeListeners = {
      update: 'onRecordUpdate',
      thisObj: me,
      prio: 1000
    },
          oldStore = me._store;

    if (store !== oldStore) {
      if (oldStore) {
        oldStore.un(storeListeners);
      }

      me._store = store;
      const {
        modelClass
      } = store,
            field = modelClass.fieldMap[me.field]; // It's *likely*, but not certain that this will be used for a numeric field.
      // Use numeric defaults unless configured otherwise if so.

      if (field && field.type === 'number') {
        if (!me.configuredAlign) {
          me.align = 'end';
        }

        if (!me.configuredEditor) {
          me.editor = 'number';
        }
      }

      store.on(storeListeners);
    }
  }

  canEdit(record) {
    return record.isLeaf;
  }

  get store() {
    return this._store;
  }

  sum(...args) {
    let result = 0;

    for (let i = 0, {
      length
    } = args; i < length; i++) {
      result += parseInt(args[i] || 0, 10);
    }

    return result;
  }

  avg(...args) {
    let result = 0;
    const {
      length
    } = args;

    for (let i = 0; i < length; i++) {
      result += parseInt(args[i] || 0, 10);
    }

    return result / length;
  }

  onRecordUpdate({
    record,
    changes
  }) {
    const me = this,
          {
      rowManager
    } = me.grid;

    if (me.field in changes) {
      if (record.isLeaf) {
        record.bubble(rec => {
          const row = rowManager.getRowFor(rec);

          if (row) {
            const cellElement = row.getCell(me.field);

            if (cellElement) {
              row.renderCell({
                cellElement,
                record: rec
              });
            }
          }
        }, true);
      }
    }
  }

  getRawValue(record) {
    let value = 0;

    if (record.children) {
      const me = this,
            fn = me.function,
            isMathFn = typeof fn === 'string' && typeof Math[fn] === 'function',
            {
        handler,
        thisObj
      } = isMathFn ? {
        handler: Math[fn],
        thisObj: Math
      } : me.resolveCallback(fn);

      for (let i = 0, {
        length
      } = record.children; i < length; i++) {
        value = handler.call(thisObj, value, me.getRawValue(record.children[i]));
      }

      record.setData(me.field, value);
    } else {
      value = record[this.field];
    }

    return value;
  }

}
ColumnStore.registerColumnType(AggregateColumn, true);
AggregateColumn.exposeProperties();
AggregateColumn._$name = 'AggregateColumn';

//TODO: Currently widgets reuse elements already in cell, but performance would improve if entire widget was reused
/**
 * @module Grid/column/WidgetColumn
 */

/**
 * A column that displays widgets in the cells.
 *
 * There is no `editor` provided. It is the configured widget's responsibility to provide editing if needed.
 *
 * @extends Grid/column/Column
 *
 * @example
 * new Grid({
 *     appendTo : document.body,
 *
 *     columns : [
 *         { type: 'widget', text: 'Increase age', widgets: [{ type: 'button', icon: 'add' }], data: 'age' }
 *     ]
 * });
 *
 * @classType widget
 * @externalexample column/WidgetColumn.js
 */

class WidgetColumn extends Column {
  //region Config
  static get type() {
    return 'widget';
  }

  static get fields() {
    return [
    /**
     * An array of widget config objects
     * @config {Object[]} widgets
     * @category Common
     */
    'widgets'];
  }

  static get defaults() {
    return {
      filterable: false,
      sortable: false,
      editor: false,
      searchable: false,
      fitMode: false
    };
  } //endregion
  //region Init / Destroy

  construct(config, store) {
    this.widgetMap = {};
    this.internalCellCls = 'b-widget-cell';
    super.construct(...arguments);
  }

  doDestroy() {
    // Destroy all the widgets we created.
    for (const widget of Object.values(this.widgetMap)) {
      widget.destroy && widget.destroy();
    }

    super.doDestroy();
  } //endregion
  //region Render

  /**
   * Renderer that displays a widget in the cell.
   * @param {Object} event Render event
   * @param {Grid.column.Column} event.column Rendered column
   * @param {Core.data.Model} event.record Rendered record
   * @private
   */

  renderer(event) {
    const me = this,
          {
      cellElement,
      column,
      value,
      record,
      isExport
    } = event,
          widgets = column.widgets; // This renderer might be called from subclasses by accident
    // This condition saves us from investigating bug reports

    if (!isExport && widgets) {
      // If there is no widgets yet and we're going to add them,
      // need to make sure there is no content left in the cell after its previous usage
      // by grid features such as grouping feature or so.
      if (!cellElement.widgets) {
        // Reset cell content
        me.clearCell(cellElement);
      }

      cellElement.widgets = widgets.map((widgetCfg, i) => {
        let widget, widgetNextSibling; // If cell element already has widgets, check if we need to destroy/remove one

        if (cellElement.widgets) {
          // Current widget
          widget = cellElement.widgets[i]; // Store next element sibling to insert widget to correct position later

          widgetNextSibling = widget.element.nextElementSibling; // If we are not syncing content for present widget, remove it from cell and render again later

          if (widgetCfg.recreate && widget) {
            // destroy widget and remove reference to it
            delete me.widgetMap[widget.id];
            widget.destroy();
            cellElement.widgets[i] = null;
          }
        } // Ensure widget is created if first time through

        if (!widget) {
          me.onBeforeWidgetCreate(widgetCfg, event);
          widget = WidgetHelper.append(widgetCfg, widgetNextSibling ? {
            insertBefore: widgetNextSibling
          } : cellElement)[0];
          me.widgetMap[widget.id] = widget;
          me.onAfterWidgetCreate(widget, event);
        }

        widget.cellInfo = {
          cellElement,
          value,
          record,
          column
        };

        if (me.grid) {
          widget.readOnly = me.grid.readOnly;
        }

        if (me.onBeforeWidgetSetValue(widget, event) !== false) {
          if (!widgetCfg.noValueOnRender) {
            if (widgetCfg.valueProperty) {
              widget[widgetCfg.valueProperty] = value;
            } else if (widget.defaultBindProperty) {
              widget[widget.defaultBindProperty] = value;
            } else {
              widget.text = widget.value = value;
            }
          }
        }

        me.onAfterWidgetSetValue(widget, event);
        return widget;
      });
    }

    if (isExport) {
      return null;
    }
  } //endregion
  //region Other

  /**
   * Called before widget is created on rendering
   * @param {Object} widgetCfg Widget config
   * @param {Object} event Render event
   * @private
   */

  onBeforeWidgetCreate(widgetCfg, event) {}
  /**
   * Called after widget is created on rendering
   * @param {Core.widget.Widget} widget Created widget
   * @param {Object} event Render event
   * @private
   */

  onAfterWidgetCreate(widget, event) {}
  /**
   * Called before widget gets value on rendering. Pass `false` to skip value setting while rendering
   * @preventable
   * @param {Core.widget.Widget} widget Created widget
   * @param {Object} event Render event
   * @param {Grid.column.Column} event.column Rendered column
   * @param {Core.data.Model} event.record Rendered record
   */

  onBeforeWidgetSetValue(widget, event) {}
  /**
   * Called after widget gets value on rendering.
   * @param {Core.widget.Widget} widget Created widget
   * @param {Object} event Render event
   * @param {Grid.column.Column} event.column Rendered column
   * @param {Core.data.Model} event.record Rendered record
   */

  onAfterWidgetSetValue(widget, event) {} // Overrides base implementation to cleanup widgets, for example when a cell is reused as part of group header

  clearCell(cellElement) {
    if (cellElement.widgets) {
      cellElement.widgets.forEach(widget => {
        // Destroy widget and remove reference to it
        delete this.widgetMap[widget.id];
        widget.destroy();
      });
      cellElement.widgets = null;
    } // Even if there is no widgets need to make sure there is no content left, for example after a cell has been reused as part of group header

    super.clearCell(cellElement);
  } // Null implementation because there is no way of ascertaining whether the widgets get their width from
  // the column, or the column shrinkwraps the Widget.
  // Remember that the widget could have a width from a CSS rule which we cannot read.
  // It might have width: 100%, or a flex which would mean it is sized by us, but we cannot read that -
  // getComputedStyle would return the numeric width.

  resizeToFitContent() {} //endregion

}
ColumnStore.registerColumnType(WidgetColumn);
WidgetColumn.exposeProperties();
WidgetColumn._$name = 'WidgetColumn';

//TODO: Reuse checkbox instead of creating a new one
/**
 * @module Grid/column/CheckColumn
 */

/**
 * A column that displays a checkbox in the cell. The value of the backing field is toggled by the checkbox.
 *
 * This column uses a {@link Core.widget.Checkbox checkbox} as its editor, and it is not intended to be changed.
 *
 * @extends Grid/column/WidgetColumn
 *
 * @example
 * new Grid({
 *     appendTo : document.body,
 *
 *     columns : [
 *         { type: 'check', field: 'allow' }
 *     ]
 * });
 *
 * @classType check
 * @externalexample column/CheckColumn.js
 */

class CheckColumn extends WidgetColumn {
  //region Config
  static get type() {
    return 'check';
  }

  static get fields() {
    return ['checkCls', 'showCheckAll'];
  }

  static get defaults() {
    return {
      align: 'center',

      /**
       * CSS class name to add to checkbox
       * @config {String}
       * @category Rendering
       */
      checkCls: null,

      /**
       * True to show a checkbox in the column header to be able to select/deselect all rows
       * @config {Boolean}
       */
      showCheckAll: false,
      widgets: [{
        type: 'checkbox',
        valueProperty: 'checked'
      }]
    };
  }

  constructor(config, store) {
    var _me$grid;

    super(...arguments);
    const me = this;
    me.externalHeaderRenderer = me.headerRenderer;
    me.headerRenderer = me.internalHeaderRenderer;
    me.internalCellCls = 'b-check-cell';
    (_me$grid = me.grid) === null || _me$grid === void 0 ? void 0 : _me$grid.on({
      destroy: () => {
        var _me$headerCheckbox;

        return (_me$headerCheckbox = me.headerCheckbox) === null || _me$headerCheckbox === void 0 ? void 0 : _me$headerCheckbox.destroy();
      },
      thisObj: me
    });
  }

  internalHeaderRenderer({
    headerElement,
    column
  }) {
    var _column$externalHeade;

    let returnValue;
    headerElement.classList.add('b-check-header');

    if (column.showCheckAll) {
      headerElement.classList.add('b-check-header-with-checkbox');

      if (column.headerCheckbox) {
        column.headerCheckbox.destroy();
      }

      column.headerCheckbox = new Checkbox({
        appendTo: headerElement,
        owner: this.grid,
        listeners: {
          change: ({
            checked
          }) => {
            /**
             * Fired when the header checkbox is clicked to toggle its checked status.
             * @event toggleAll
             * @param {Grid.column.Column} source This Column
             * @param {Boolean} checked The checked status of the header checkbox.
             */
            column.trigger('toggleAll', {
              checked
            });
          }
        }
      });
    } else {
      returnValue = column.text;
    }

    returnValue = (_column$externalHeade = column.externalHeaderRenderer) === null || _column$externalHeade === void 0 ? void 0 : _column$externalHeade.call(this, ...arguments);
    return column.showCheckAll ? undefined : returnValue;
  } //endregion

  renderer({
    value,
    isExport
  }) {
    if (isExport) {
      return value == null ? '' : value;
    } else {
      super.renderer(...arguments);
    }
  } //region Widget rendering

  onBeforeWidgetCreate(widgetCfg, event) {
    widgetCfg.cls = this.checkCls;
  }

  onAfterWidgetCreate(widget, event) {
    event.cellElement.widget = widget;
    widget.on({
      beforeChange: 'onBeforeCheckboxChange',
      change: 'onCheckboxChange',
      thisObj: this
    });
  }

  onBeforeWidgetSetValue(widget) {
    widget.record = widget.cellInfo.record;
    this.isInitialSet = true;
  }

  onAfterWidgetSetValue(widget) {
    this.isInitialSet = false;
  } //endregion
  //region Events

  onBeforeCheckboxChange({
    source,
    checked
  }) {
    if (!this.isInitialSet) {
      /**
       * Fired when a cell is clicked to toggle its checked status. Returning `false` will prevent status change.
       * @event beforeToggle
       * @param {Grid.column.Column} source This Column
       * @param {Core.data.Model} record The record for the row containing the cell.
       * @param {Boolean} checked The new checked status of the cell.
       */
      return this.trigger('beforeToggle', {
        record: source.cellInfo.record,
        checked
      });
    }
  }

  onCheckboxChange({
    source,
    checked
  }) {
    if (!this.isInitialSet) {
      const record = source.cellInfo.record,
            field = this.field;

      if (field) {
        const setterName = `set${StringHelper.capitalize(field)}`;

        if (record[setterName]) {
          record[setterName](checked);
        } else {
          record.set(field, checked);
        }
      }
      /**
       * Fired when a cell is clicked to toggle its checked status.
       * @event toggle
       * @param {Grid.column.Column} source This Column
       * @param {Core.data.Model} record The record for the row containing the cell.
       * @param {Boolean} checked The new checked status of the cell.
       */

      this.trigger('toggle', {
        record,
        checked
      });
    }
  } //endregion

  onCellKeyDown({
    event,
    cellElement
  }) {
    // SPACE key toggles the checkbox
    if (event.key === ' ') {
      const checkbox = cellElement.widget;
      checkbox === null || checkbox === void 0 ? void 0 : checkbox.toggle(); // Prevent native browser scrolling

      event.preventDefault();
    }
  }

}
ColumnStore.registerColumnType(CheckColumn, true);
CheckColumn._$name = 'CheckColumn';

/**
 * @module Grid/column/DateColumn
 */

/**
 * A column that displays a date in the specified format (see {@link Core.helper.DateHelper#function-format-static} for
 * formatting options).
 *
 * Default editor is a {@link Core.widget.DateField DateField}.
 *
 * If no `format` is specified, {@link Core.helper.DateHelper#property-defaultFormat-static DateHelper.defaultFormat} is
 * used. Please note that by default the `L` format is used.
 *
 * @extends Grid/column/Column
 *
 * @example
 * new Grid({
 *     appendTo : document.body,
 *
 *     columns : [
 *          { type: 'date', text: 'Start date', format: 'YYYY-MM-DD', data: 'start' }
 *     ]
 * });
 *
 * @classType date
 * @externalexample column/DateColumn.js
 */

class DateColumn extends Column {
  static get $name() {
    return 'DateColumn';
  } //region Config

  static get type() {
    return 'date';
  } // Type to use when auto adding field

  static get fieldType() {
    return 'date';
  }

  static get fields() {
    return ['format', 'pickerFormat', 'step'];
  }

  static get defaults() {
    return {
      /**
       * Date format
       * @config {String}
       * @default
       * @category Common
       */
      format: 'L',

      /**
       * Time increment duration value to apply when clicking the left / right trigger icons. See {@link Core.widget.DateField#config-step} for more information
       * Set to `null` to hide the step triggers.
       * @config {String|Number|Object}
       * @default
       * @category Common
       */
      step: 1,
      minWidth: 85,
      filterType: 'date'
    };
  } //endregion
  //region Init

  constructor(config, store) {
    super(...arguments);
    this.internalCellCls = 'b-date-cell';
  } //endregion
  //region Display

  /**
   * Renderer that displays the date with the specified format. Also adds cls 'date-cell' to the cell.
   * @private
   */

  defaultRenderer({
    value
  }) {
    return value ? this.formatValue(value) : '';
  }
  /**
   * Group renderer that displays the date with the specified format.
   * @private
   */

  groupRenderer({
    cellElement,
    groupRowFor
  }) {
    cellElement.innerHTML = this.formatValue(groupRowFor);
  } //endregion
  //region Formatter

  /**
   * Used by both renderer and groupRenderer to do the actual formatting of the date
   * @private
   * @param value
   * @returns {String}
   */

  formatValue(value) {
    // Ideally we should be served a date, but if not make it easier for the user by parsing
    if (typeof value === 'string') {
      value = DateHelper.parse(value, this.format || undefined); // null does not use default format
    }

    return DateHelper.format(value, this.format || undefined);
  } //endregion
  //region Getters/setters

  /**
   * Get/Set format for date displayed in cell and editor (see {@link Core.helper.DateHelper#function-format-static} for formatting options)
   * @property {String}
   */

  set format(value) {
    const me = this,
          editor = me.editor;
    me.set('format', value);

    if (editor) {
      editor.format = me.format;
    }
  }

  get format() {
    return this.get('format');
  }

  get defaultEditor() {
    const me = this;
    return {
      name: me.field,
      type: 'date',
      calendarContainerCls: 'b-grid-cell-editor-related',
      weekStartDay: me.grid.weekStartDay,
      format: me.format,
      step: me.step
    };
  } //endregion

}
ColumnStore.registerColumnType(DateColumn, true);
DateColumn.exposeProperties();
DateColumn._$name = 'DateColumn';

/**
 * @module Grid/column/PercentColumn
 */

/**
 * A column that display a basic progress bar.
 *
 * Default editor is a {@link Core.widget.NumberField NumberField}.
 *
 * @extends Grid/column/Column
 *
 * @example
 * new Grid({
 *     appendTo : document.body,
 *
 *     columns : [
 *         { type: 'percent', text: 'Progress', data: 'progress' }
 *     ]
 * });
 *
 * @classType percent
 * @externalexample column/PercentColumn.js
 */

class PercentColumn extends Column {
  static get type() {
    return 'percent';
  } // Type to use when auto adding field

  static get fieldType() {
    return 'number';
  }

  static get fields() {
    return ['lowThreshold'];
  }

  static get defaults() {
    return {
      /**
       * PercentColumn uses a {@link Core.widget.NumberField} configured with an allowed interval 0 - 100 as
       * its default editor.
       * @config {Object|String}
       * @default Core.widget.NumberField
       * @category Misc
       */
      editor: {
        type: 'number',
        min: 0,
        max: 100
      },

      /**
       * Set to false to not render the number value
       * @config {Boolean}
       * @category Rendering
       */
      showValue: true,

      /**
       * When below this percentage the bar will have `b-low` CSS class added. By default it turns the bar red.
       * @config {Number}
       * @category Rendering
       */
      lowThreshold: 20,
      filterType: 'number',
      htmlEncode: false,
      searchable: false,
      summaryRenderer: sum => `${sum}%`,
      fitMode: false
    };
  }

  constructor(config, store) {
    super(...arguments);
    this.internalCellCls = 'b-percent-bar-cell';
  }
  /**
   * Renderer that displays a progress bar in the cell.
   * @private
   */

  defaultRenderer({
    value
  }) {
    value = value || 0;
    return {
      className: 'b-percent-bar-outer',
      children: [{
        tag: 'div',
        className: {
          'b-percent-bar': 1,
          'b-zero': value === 0,
          'b-low': value < this.lowThreshold
        },
        style: {
          width: value + '%'
        },
        text: this.showValue ? value + '%' : undefined
      }]
    };
  } // Null implementation because the column width drives the width of its content.
  // So the concept of sizing to content is invalid here.

  resizeToFitContent() {}

}
PercentColumn.sum = 'average';
ColumnStore.registerColumnType(PercentColumn, true);
PercentColumn._$name = 'PercentColumn';

/**
 * @module Grid/column/NumberColumn
 */

/**
 * A column for showing/editing numbers.
 *
 * Default editor is a {@link Core.widget.NumberField NumberField}.
 *
 * @extends Grid/column/Column
 * @example
 * new Grid({
 *     appendTo : document.body,
 *
 *     columns : [
 *         { type: 'number', min: 0, max : 100, field: 'score' }
 *     ]
 * });
 *
 * @classType number
 * @externalexample column/NumberColumn.js
 */

class NumberColumn extends Column {
  //region Config
  static get type() {
    return 'number';
  } // Type to use when auto adding field

  static get fieldType() {
    return 'number';
  }

  static get fields() {
    return ['format',
    /**
     * The minimum value for the field used during editing.
     * @config {Number} min
     * @category Common
     */
    'min',
    /**
     * The maximum value for the field used during editing.
     * @config {Number} max
     * @category Common
     */
    'max',
    /**
     * Step size for the field used during editing.
     * @config {Number} step
     * @category Common
     */
    'step',
    /**
     * Large step size for the field used during editing. In effect for `SHIFT + click/arrows`
     * @config {Number} largeStep
     * @category Common
     */
    'largeStep',
    /**
     * Unit to append to displayed value.
     * @config {String} unit
     * @category Common
     */
    'unit'];
  }

  static get defaults() {
    return {
      filterType: 'number',

      /**
       * The format to use for rendering numbers.
       *
       * By default, the locale's default number formatter is used. For `en-US`, the
       * locale default is a maximum of 3 decimal digits, using thousands-based grouping.
       * This would render the number `1234567.98765` as `'1,234,567.988'`.
       *
       * @config {String|Object|Core.helper.util.NumberFormat}
       */
      format: ''
    };
  }

  constructor(config, store) {
    super(...arguments);
    this.internalCellCls = 'b-number-cell';
  } //endregion
  //region Init

  get defaultEditor() {
    const {
      format,
      name,
      max,
      min,
      step,
      largeStep,
      align
    } = this; // Remove any undefined configs, to allow config system to use default values instead

    return ObjectHelper.cleanupProperties({
      type: 'numberfield',
      format,
      name,
      max,
      min,
      step,
      largeStep,
      textAlign: align
    });
  }

  get formatter() {
    const me = this,
          {
      format
    } = me;
    let formatter = me._formatter;

    if (!formatter || me._lastFormat !== format) {
      me._formatter = formatter = NumberFormat.get(me._lastFormat = format);
    }

    return formatter;
  }
  /**
   * Renderer that displays value + optional unit in the cell
   * @private
   */

  defaultRenderer({
    value
  }) {
    if (value != null) {
      value = this.formatter.format(value);

      if (this.unit) {
        value = `${value}${this.unit}`;
      }
    }

    return value;
  }

}
ColumnStore.registerColumnType(NumberColumn, true);
NumberColumn.exposeProperties();
NumberColumn._$name = 'NumberColumn';

/**
 * @module Grid/column/RatingColumn
 */

/**
 * A column that displays a star rating. Click a start to set a value, shift+click to unset a single start from the end.
 * Clicking the first and only star toggles it.
 *
 * This column uses a custom widget as its editor, and it is not intended to be changed.
 *
 * @extends Grid/column/NumberColumn
 *
 * @example
 * new Grid({
 *     appendTo : document.body,
 *
 *     columns : [
 *         { type: 'rating', max : 10, field: 'rating' }
 *     ]
 * });
 *
 * @classType percent
 * @externalexample column/RatingColumn.js
 */

class RatingColumn extends NumberColumn {
  static get type() {
    return 'rating';
  } // Type to use when auto adding field

  static get fieldType() {
    return 'number';
  }

  static get fields() {
    return ['emptyIcon', 'filledIcon', 'editable'];
  }

  static get defaults() {
    return {
      min: 0,
      max: 5,

      /**
       * The empty rating icon to show
       * @config {String}
       * @category Rendering
       */
      emptyIcon: 'b-icon b-icon-star',

      /**
       * The filled rating icon to show
       * @config {String}
       * @category Rendering
       */
      filledIcon: 'b-icon b-icon-star',

      /**
       * Allow user to click an icon to change the value
       * @config {Boolean}
       * @category Interaction
       */
      editable: true,
      filterType: 'number',
      searchable: false,
      width: '11.2em',
      htmlEncode: false,
      minWidth: '11.2em',
      editor: false,
      fitMode: 'value'
    };
  }

  constructor(config, store) {
    super(...arguments);
    this.internalCellCls = 'b-rating-cell';
  }
  /**
   * Renderer that displays a number of stars in the cell. Also adds CSS class 'b-rating-cell' to the cell.
   * @private
   */

  renderer({
    value
  }) {
    return {
      className: {
        'b-rating-cell-inner': 1,
        'b-not-editable': !this.editable
      },
      children: ArrayHelper.populate(this.max, i => {
        const filled = i < value;
        return {
          tag: 'i',
          className: {
            'b-rating-icon': true,
            'b-filled': filled,
            'b-empty': !filled,
            [filled ? this.filledIcon : this.emptyIcon]: true
          }
        };
      })
    };
  }

  onCellClick({
    grid,
    column,
    record,
    target,
    event
  }) {
    if (target.classList.contains('b-rating-icon') && !grid.readOnly && column.editable) {
      let starIndex = [].indexOf.call(target.parentNode.childNodes, target);

      if (target.classList.contains('b-filled') && (event.metaKey || event.shiftKey)) {
        starIndex = starIndex - 1;
      } // Clicking first star when it is only one removes it

      if (record.get(column.field) === 1 && starIndex === 0) {
        starIndex = -1;
      }

      record.set(column.field, starIndex + 1);
    }
  }

}
ColumnStore.registerColumnType(RatingColumn, true);
RatingColumn.exposeProperties();
RatingColumn._$name = 'RatingColumn';

/**
 * @module Grid/column/RowNumberColumn
 */

/**
 * A column that displays the row number in each cell.
 *
 * There is no `editor`, since value is read-only.
 *
 * ```javascript
 * const grid = new Grid({
 *   appendTo : targetElement,
 *   width    : 300,
 *   columns  : [
 *     { type : 'rownumber' }
 *   ]
 * });
 *
 * @extends Grid/column/Column
 *
 * @classType rownumber
 * @externalexample column/RowNumberColumn.js
 */

class RowNumberColumn extends Column {
  static get defaults() {
    return {
      /**
       * @config {Boolean} groupable
       * @hide
       */
      groupable: false,

      /**
       * @config {Boolean} sortable
       * @hide
       */
      sortable: false,

      /**
       * @config {Boolean} filterable
       * @hide
       */
      filterable: false,

      /**
       * @config {Boolean} searchable
       * @hide
       */
      searchable: false,

      /**
       * @config {Boolean} resizable
       * @hide
       */
      resizable: false,
      minWidth: 50,
      width: 50,
      align: 'right',
      text: '#',
      editor: false
    };
  }

  construct(config, store) {
    const me = this;
    super.construct(...arguments);
    const {
      grid
    } = me; // Update our width when the store mutates (tests test Columns in isolation with no grid, so we must handle that!)

    if (grid) {
      grid.store.on({
        [`change${grid.asyncEventSuffix}`]: 'onStoreChange',
        thisObj: me
      });

      if (grid.store.count) {
        grid.on({
          render: 'resizeToFitContent',
          thisObj: me,
          once: true
        });
      }
    }

    me.internalCellCls = 'b-row-number-cell';
  }

  static get type() {
    return 'rownumber';
  }

  onStoreChange({
    action
  }) {
    if (action === 'dataset' || action === 'add' || action === 'remove' || action === 'removeall') {
      this.resizeToFitContent();
    }
  }
  /**
   * Renderer that displays the row number in the cell.
   * @private
   */

  renderer({
    record,
    grid
  }) {
    return record.isSpecialRow ? '' : grid.store.indexOf(record, true) + 1;
  }
  /**
   * Resizes the column to match the widest string in it. Called when you double click the edge between column
   * headers
   */

  resizeToFitContent() {
    const {
      grid
    } = this,
          {
      store
    } = grid,
          {
      count
    } = store;

    if (count && !this.hidden) {
      const cellElement = grid.element.querySelector(`.b-grid-cell[data-column-id="${this.id}"]`); // cellElement might not exist, e.g. when trial is expired

      if (cellElement) {
        const cellPadding = parseInt(DomHelper.getStyleValue(cellElement, 'padding-left')),
              maxWidth = DomHelper.measureText(count, cellElement);
        this.width = maxWidth + 2 * cellPadding;
      }
    }
  }

  set flex(f) {}

}
ColumnStore.registerColumnType(RowNumberColumn, true);
RowNumberColumn._$name = 'RowNumberColumn';

/**
 * @module Grid/column/TemplateColumn
 */

/**
 * A column that uses a template for cell content. Any function can be used as template, and the function is passed { value, record, field } properties.
 * It should return a string which will be rendered in the cell.
 *
 * Default editor is a {@link Core.widget.TextField TextField}.
 *
 * @extends Grid/column/Column
 *
 * @example
 * new Grid({
 *     appendTo : document.body,
 *
 *     columns : [
 *         { type: 'template', field: 'age', template: value => `${value} years old` }
 *     ]
 * });
 *
 * @classType template
 * @externalexample column/TemplateColumn.js
 */

class TemplateColumn extends Column {
  static get fields() {
    return [
    /**
     * Template function used to generate a value displayed in the cell. Called with arguments { value, record, field }
     * @config {Function} template
     * @category Common
     */
    'template'];
  }

  static get defaults() {
    return {
      htmlEncode: false
    };
  }

  constructor(config, store) {
    super(...arguments);
    const me = this;

    if (!me.template) {
      throw new Error('TemplateColumn needs a template');
    }

    if (typeof me.template !== 'function') {
      throw new Error('TemplateColumn.template must be a function');
    }
  }

  static get type() {
    return 'template';
  }
  /**
   * Renderer that uses a template for cell content.
   * @private
   */

  renderer(renderData) {
    // If it's a special row, such as a group row, we can't use the user's template
    if (!renderData.record.isSpecialRow) {
      return this.template({
        value: renderData.value,
        record: renderData.record,
        field: renderData.column.field
      });
    }
  }

}
ColumnStore.registerColumnType(TemplateColumn, true);
TemplateColumn.exposeProperties();
TemplateColumn._$name = 'TemplateColumn';

/**
 * @module Grid/column/TimeColumn
 */

/**
 * A column that displays a time in the specified format (see {@link Core.helper.DateHelper#function-format-static} for formatting options).
 *
 * Default editor is a {@link Core.widget.TimeField TimeField}.
 *
 * @extends Grid/column/Column
 *
 * @example
 * new Grid({
 *     appendTo : document.body,
 *
 *     columns : [
 *          { type: 'time', text: 'Start time', format: 'HH:mm:ss', data: 'start' }
 *     ]
 * });
 *
 * @classType time
 * @externalexample column/TimeColumn.js
 */

class TimeColumn extends Column {
  //region Config
  static get type() {
    return 'time';
  } // Type to use when auto adding field

  static get fieldType() {
    return 'date';
  }

  static get fields() {
    return ['format'];
  }

  static get defaults() {
    return {
      /**
       * Time format
       * @config {String}
       * @category Common
       */
      format: 'LT',
      minWidth: 140,
      filterType: 'time'
    };
  } //endregion
  //region Init

  constructor(config, store) {
    super(...arguments);
    this.internalCellCls = 'b-time-cell';
  } //endregion
  //region Display

  /**
   * Renderer that displays the time with the specified format. Also adds cls 'b-time-cell' to the cell.
   * @private
   */

  defaultRenderer({
    value
  }) {
    return value ? this.formatValue(value) : '';
  }
  /**
   * Group renderer that displays the time with the specified format.
   * @private
   */

  groupRenderer({
    cellElement,
    groupRowFor
  }) {
    cellElement.innerHTML = this.formatValue(groupRowFor);
  } //endregion
  //region Formatter

  /**
   * Used by both renderer and groupRenderer to do the actual formatting of the time
   * @private
   * @param value
   * @returns {String}
   */

  formatValue(value) {
    const me = this; // Ideally we should be served a time, but if not make it easier for the user by parsing

    if (typeof value === 'string') {
      value = DateHelper.parse(value, me.format);
    }

    return DateHelper.format(value, me.format);
  } //endregion
  //region Getters/Setters

  /**
   * Get/Set format for time displayed in cell and editor (see {@link Core.helper.DateHelper#function-format-static} for formatting options)
   * @property {String}
   */

  set format(value) {
    const me = this,
          editor = me.editor;
    me.set('format', value);

    if (editor) {
      editor.format = me.format;
    }
  }

  get format() {
    return this.get('format');
  }

  get defaultEditor() {
    return {
      name: this.field,
      type: 'time',
      format: this.format
    };
  } //endregion

}
ColumnStore.registerColumnType(TimeColumn, true);
TimeColumn.exposeProperties();
TimeColumn._$name = 'TimeColumn';

/**
 * @module Grid/column/TreeColumn
 */

let currentParentHasIcon = false;
/**
 * A column that displays a tree structure when using the {@link Grid.feature.Tree tree} feature.
 *
 * Default editor is a {@link Core.widget.TextField TextField}.
 *
 * TreeColumn provides configs to define icons for {@link #config-expandIconCls expanded} / {@link #config-collapseIconCls collapsed} nodes,
 * {@link #config-expandedFolderIconCls expanded folder} / {@link #config-collapsedFolderIconCls collapsed folder} nodes and
 * {@link #config-leafIconCls leaf} nodes.
 *
 * When the TreeColumn renders its cells, it will look for two special fields {@link Grid.data.GridRowModel#field-href}
 * and {@link Grid.data.GridRowModel#field-target}. Specifying `href` will produce a link for the TreeNode,
 * and `target` will have the same meaning as in an A tag:
 *
 * ```javascript
 * {
 *    id        : 1,
 *    name      : 'Some external link'
 *    href      : '//www.website.com",
 *    target    : '_blank"
 * }
 * ```
 *
 * @example
 * new TreeGrid({
 *     appendTo : document.body,
 *
 *     columns : [
 *          { type: 'tree', field: 'name' }
 *     ]
 * });
 *
 * @classType tree
 * @extends Grid/column/Column
 * @externalexample column/TreeColumn.js
 */

class TreeColumn extends Column {
  static get defaults() {
    return {
      tree: true,
      hideable: false,
      minWidth: 150
    };
  }

  static get fields() {
    return [
    /**
     * The icon to use for the collapse icon in collapsed state
     * @config {String} expandIconCls
     */
    {
      name: 'expandIconCls',
      defaultValue: 'b-icon b-icon-tree-expand'
    },
    /**
     * The icon to use for the collapse icon in expanded state
     * @config {String} collapseIconCls
     */
    {
      name: 'collapseIconCls',
      defaultValue: 'b-icon b-icon-tree-collapse'
    },
    /**
     * The icon to use for the collapse icon in expanded state
     * @config {String} collapsedFolderIconCls
     */
    {
      name: 'collapsedFolderIconCls'
    },
    /**
     * The icon to use for the collapse icon in expanded state
     * @config {String} expandedFolderIconCls
     */
    {
      name: 'expandedFolderIconCls'
    },
    /**
     * Size of the child indent in em. Resulting indent is indentSize multiplied by child level.
     * @config {Number} indentSize
     * @default 1.7
     */
    {
      name: 'indentSize',
      defaultValue: 1.7
    },
    /**
     * The icon to use for the leaf nodes in the tree
     * @config {String} leafIconCls
     */
    {
      name: 'leafIconCls',
      defaultValue: 'b-icon b-icon-tree-leaf'
    }, {
      name: 'editTargetSelector',
      defaultValue: '.b-tree-cell-value'
    }];
  }

  static get type() {
    return 'tree';
  }

  constructor(config, store) {
    super(...arguments);
    const me = this;
    me.internalCellCls = 'b-tree-cell'; // We handle htmlEncoding in this class rather than relying on the generic Row DOM manipulation
    // since this class requires quite a lot of DOM infrastructure around the actual rendered content

    me.shouldHtmlEncode = me.htmlEncode;
    me.setData('htmlEncode', false); // add tree renderer (which calls original renderer internally)

    if (me.renderer) {
      me.originalRenderer = me.renderer;
    }

    me.renderer = me.treeRenderer.bind(me);
  }
  /**
   * A column renderer that is automatically added to the column with { tree: true }. It adds padding and node icons
   * to the cell to make the grid appear to be a tree. The original renderer is called in the process.
   * @private
   */

  treeRenderer(renderData) {
    var _value;

    const me = this,
          {
      cellElement,
      row,
      record,
      isExport
    } = renderData,
          gridMeta = record.instanceMeta(renderData.grid.store),
          innerConfig = {
      className: 'b-tree-cell-value'
    },
          children = [innerConfig],
          result = {
      className: 'b-tree-cell-inner',
      tag: record.href ? 'a' : 'div',
      href: record.href,
      target: record.target,
      children
    },
          rowClasses = {
      'b-tree-parent-row': 0,
      'b-tree-collapsed': 0,
      'b-tree-expanded': 0,
      'b-loading-children': 0
    };
    let iconCls,
        {
      value
    } = renderData;

    if (me.originalRenderer) {
      const rendererHtml = me.originalRenderer(renderData);
      value = rendererHtml === false ? cellElement.innerHTML : rendererHtml;
    }

    value = String((_value = value) !== null && _value !== void 0 ? _value : '');

    if (isExport) {
      return value;
    }

    if (!record.isLeaf) {
      const isCollapsed = gridMeta.collapsed,
            expanderIconCls = isCollapsed ? me.expandIconCls : me.collapseIconCls,
            folderIconCls = isCollapsed ? me.collapsedFolderIconCls : me.expandedFolderIconCls;
      rowClasses['b-tree-parent-row'] = 1;
      rowClasses['b-tree-collapsed'] = isCollapsed;
      rowClasses['b-tree-expanded'] = !isCollapsed;
      rowClasses['b-loading-children'] = gridMeta.isLoadingChildren;
      cellElement.classList.add('b-tree-parent-cell');
      children.unshift({
        className: 'b-tree-expander',
        children: expanderIconCls ? [{
          tag: 'i',
          className: {
            [expanderIconCls]: 1
          }
        }] : null
      }); // Allow user to customize tree icon or opt out entirely

      currentParentHasIcon = iconCls = renderData.iconCls || record.iconCls || folderIconCls;
    } else {
      // TODO: Cleanup for reusing dom nodes should be done elsewhere, also cleanup selection
      cellElement.classList.add('b-tree-leaf-cell'); // Allow user to customize tree icon or opt out entirely

      iconCls = renderData.iconCls || record.iconCls || me.leafIconCls;
    } // Row can be just a dummy object for example when the renderer is called from Column#resizeToFitContent.
    // Add/remove the various tree node classes.

    if (row.isRow) {
      row.assignCls(rowClasses);
    } // If we are encoding HTML, or there's no raw HTML, we can use the children property
    // with the raw value as a child, and DomSync will create a TextNode from that.

    if (me.shouldHtmlEncode || !value.includes('<')) {
      innerConfig.children = [iconCls ? {
        tag: 'i',
        className: {
          'b-tree-icon': 1,
          [iconCls]: 1
        }
      } : null, value];
    } // If we are accepting HTML without encoding it, and there is HTML we must use html property
    else {
        innerConfig.html = `${iconCls ? `<i class="b-tree-icon ${iconCls}"></i>` : ''}${value}`;
      }

    const padding = record.childLevel * me.indentSize + (record.isLeaf ? currentParentHasIcon ? 2.0 : iconCls ? 0.5 : 0.4 : 0);
    result.style = `padding-left:${padding}em`;
    return result;
  }

}
ColumnStore.registerColumnType(TreeColumn, true);
TreeColumn.exposeProperties();
TreeColumn._$name = 'TreeColumn';

/**
 * @module Grid/data/GridRowModel
 */

/**
 * Model extended with some fields related to grid rendering. Used as default model type in the grids store if nothing
 * else is specified.
 *
 * Using this model is optional. If you use a custom model instead and need the functionality of any of the fields
 * below, you just have to remember to add fields with the same name to your model.
 *
 * @extends Core/data/Model
 */

class GridRowModel extends Model {
  static get fields() {
    return [
    /**
     * Icon for row (used automatically in tree, feel free to use it in renderer in other cases)
     * @field {String} iconCls
     * @category Styling
     */
    'iconCls',
    /**
     * Start expanded or not (only valid for tree data)
     * @field {Boolean} expanded
     * @category Tree
     */
    'expanded',
    /**
     * CSS class (or several classes divided by space) to append to row elements
     * @field {String} cls
     * @category Styling
     */
    'cls',
    /**
     * Used by the default implementation of {@link Grid.view.GridBase#config-getRowHeight} to determine row
     * height. Set it to use another height than the default for a the records row.
     * @field {Number} rowHeight
     * @category Styling
     */
    'rowHeight',
    /**
     * A link to use for this record when rendered into a {@link Grid.column.TreeColumn}.
     * @field {String} href
     * @category Tree
     */
    'href',
    /**
     * The target to use if this tree node provides a value for the {@link #field-href} field.
     * @field {String} target
     * @category Tree
     */
    'target'];
  }

}
GridRowModel.exposeProperties();
GridRowModel._$name = 'GridRowModel';

/**
 * @module Grid/feature/GridFeatureManager
 */

const consumerToFeatureMap = new Map(),
      consumerToDefaultFeatureMap = new Map(),
      DEFAULT_FOR_TYPE = 'Grid',
      remapToBase = {
  Grid: 'GridBase',
  Scheduler: 'SchedulerBase',
  Gantt: 'GanttBase'
},
      classNameFix = /\$\d+$/;
/**
 * Static class intended to register and query grid features
 *
 * @class
 */

class GridFeatureManager {
  /**
   * Register a feature class with the Grid. Enables it to be created and configured using config Grid#features.
   * @param {Core.mixin.InstancePlugin} featureClass Feature to register
   * @param {Boolean} [onByDefault] Specify true to have the feature enabled per default
   * @param {String|String[]} [forType] Specify a type to let the class applying the feature to determine if it should use it
   */
  static registerFeature(featureClass, onByDefault = false, forType = null, as = null) {
    // Our built in features should all define $name to survive minification/obfuscation, but user defined features might not
    as = StringHelper.uncapitalize(as || Object.prototype.hasOwnProperty.call(featureClass, '$name') && featureClass.$$name || featureClass.name); // Remove webpack's disambiguation suffix.
    // For example ExcelExporter in Scheduler will be called ExcelExporter$1
    // It must be found as ExcelExporter in the Scheduler's feature Map, so correct the name.

    as = as.replace(classNameFix, '');

    if (!Array.isArray(forType)) {
      forType = [forType || DEFAULT_FOR_TYPE];
    }

    forType.forEach(forType => {
      const type = remapToBase[forType] || forType,
            consumerFeaturesMap = consumerToFeatureMap.get(type) || new Map(),
            consumerDefaultFeaturesMap = consumerToDefaultFeatureMap.get(type) || new Map();
      consumerFeaturesMap.set(as, featureClass);
      consumerDefaultFeaturesMap.set(featureClass, onByDefault);
      consumerToFeatureMap.set(type, consumerFeaturesMap);
      consumerToDefaultFeatureMap.set(type, consumerDefaultFeaturesMap);
    });
  }
  /**
   * Get all the features registered for the given type name in an object where keys are feature names and values are feature constructors.
   *
   * @param {String} [forType]
   * @return {Object}
   */

  static getTypeNameFeatures(forType = DEFAULT_FOR_TYPE) {
    const type = remapToBase[forType] || forType,
          consumerFeaturesMap = consumerToFeatureMap.get(type),
          features = {};

    if (consumerFeaturesMap) {
      consumerFeaturesMap.forEach((featureClass, as) => features[as] = featureClass);
    }

    return features;
  }
  /**
   * Get all the default features registered for the given type name in an object where keys are feature names and values are feature constructors.
   *
   * @param {String} [forType]
   * @return {Object}
   */

  static getTypeNameDefaultFeatures(forType = DEFAULT_FOR_TYPE) {
    const type = remapToBase[forType] || forType,
          consumerFeaturesMap = consumerToFeatureMap.get(type),
          consumerDefaultFeaturesMap = consumerToDefaultFeatureMap.get(type);
    const features = {};

    if (consumerFeaturesMap && consumerDefaultFeaturesMap) {
      consumerFeaturesMap.forEach((featureClass, as) => {
        if (consumerDefaultFeaturesMap.get(featureClass)) {
          features[as] = featureClass;
        }
      });
    }

    return features;
  }
  /**
   * Gets all the features registered for the given instance type name chain. First builds the type name chain then quaries for features
   * for each type name and combines them into one object, see {@link #function-getTypeNameFeatures-static}() for returned object description.
   * If feature is registered for both parent and child type name then feature for child overrides feature for parent.
   *
   * @param {Object} instance
   * @return {Object}
   */

  static getInstanceFeatures(instance) {
    return instance.$meta.names.reduce((features, typeName) => Object.assign(features, this.getTypeNameFeatures(typeName)), {});
  }
  /**
   * Gets all the *defualt* features registered for the given instance type name chain. First builds the type name chain then quaries for features
   * for each type name and combines them into one object, see {@link #function-getTypeNameFeatures-static}() for returned object description.
   * If feature is registered for both parent and child type name then feature for child overrides feature for parent.
   *
   * @param {Object} instance
   * @return {Object}
   */

  static getInstanceDefaultFeatures(instance) {
    return instance.$meta.names.reduce((features, typeName) => Object.entries(this.getTypeNameFeatures(typeName)).reduce((features, [as, featureClass]) => {
      if (this.isDefaultFeatureForTypeName(featureClass, typeName)) {
        features[as] = featureClass;
      } else {
        delete features[as];
      }

      return features;
    }, features), {});
  }
  /**
   * Checks if the given feature class is default for the type name
   *
   * @param {Core.mixin.InstancePlugin} featureClass Feature to check
   * @param {String} [forType]
   * @return {Boolean}
   */

  static isDefaultFeatureForTypeName(featureClass, forType = DEFAULT_FOR_TYPE) {
    const type = remapToBase[forType] || forType,
          consumerDefaultFeaturesMap = consumerToDefaultFeatureMap.get(type);
    return consumerDefaultFeaturesMap && consumerDefaultFeaturesMap.get(featureClass) || false;
  }
  /**
   * Checks if the given feature class is default for the given instance type name chain. If the feature is not default for the
   * parent type name but it is for the child type name, then the child setting overrides the parent one.
   *
   * @param {Core.mixin.InstancePlugin} featureClass Feature to check
   * @param {String} [forType]
   * @return {Boolean}
   */

  static isDefaultFeatureForInstance(featureClass, instance) {
    //const typeChain = ObjectHelper.getTypeNameChain(instance);
    const typeChain = instance.$meta.names.slice().reverse();
    let result = null;

    for (let i = 0, len = typeChain.length; i < len && result === null; ++i) {
      const consumerDefaultFeaturesMap = consumerToDefaultFeatureMap.get(typeChain[i]);

      if (consumerDefaultFeaturesMap && consumerDefaultFeaturesMap.has(featureClass)) {
        result = consumerDefaultFeaturesMap.get(featureClass);
      }
    }

    return result || false;
  }
  /**
   * Resets feature registration date, used in tests to reset state after test
   *
   * @internal
   */

  static reset() {
    consumerToFeatureMap.clear();
    consumerToDefaultFeatureMap.clear();
  }

}
GridFeatureManager._$name = 'GridFeatureManager';

//TODO: Maybe some more way to stop editing in touch mode (in case grid fills entire page...)
const validNonEditingKeys = {
  Enter: 1,
  F2: 1
},
      validEditingKeys = {
  ArrowUp: 1,
  ArrowDown: 1,
  ArrowLeft: 1,
  ArrowRight: 1
};
/**
 * @module Grid/feature/CellEdit
 */

/**
 * Adding this feature to the grid and other Bryntum products which are based on the Grid (i.e. Scheduler, SchedulerPro, and Gantt)
 * enables cell editing. Any subclass of {@link Core.widget.Field Field} can be used
 * as editor for the {@link Grid.column.Column Column}. The most popular are:
 *
 * - {@link Core.widget.TextField TextField}
 * - {@link Core.widget.NumberField NumberField}
 * - {@link Core.widget.DateField DateField}
 * - {@link Core.widget.TimeField TimeField}
 * - {@link Core.widget.Combo Combo}
 *
 * Usage instructions:
 * ## Start editing
 * * Double click on a cell
 * * Press [ENTER] or [F2] with a cell selected
 * * It is also possible to change double click to single click to start editing, using the {@link #config-triggerEvent} config
 *
 * ```javascript
 * new Grid({
 *    features : {
 *        cellEdit : {
 *            triggerEvent : 'cellclick'
 *        }
 *    }
 * });
 * ```
 *
 * ## While editing
 * * [ENTER] Finish editing and start editing the same cell in next row
 * * [SHIFT] + [ENTER] Same as above put with previous row
 * * [F2] Finish editing
 * * [CMD/CTRL] + [ENTER] Finish editing
 * * [ESC] By default, first reverts the value back to its original value, next press cancels editing
 * * [TAB] Finish editing and start editing the next cell
 * * [SHIFT] + [TAB] Finish editing and start editing the previous cell
 *
 * Columns specify editor in their configuration. Editor can also by set by using a column type. Columns
 * may also contain these three configurations which affect how their cells are edited:
 * * {@link Grid.column.Column#config-invalidAction}
 * * {@link Grid.column.Column#config-revertOnEscape}
 * * {@link Grid.column.Column#config-finalizeCellEdit}
 *
 * ## Preventing editing of certain cells
 * You can prevent editing on a column by setting `editor` to false:
 *
 * ```javascript
 * new Grid({
 *    columns : [
 *       {
 *          type   : 'number',
 *          text   : 'Age',
 *          field  : 'age',
 *          editor : false
 *       }
 *    ]
 * });
 * ```
 * To prevent editing in a specific cell, listen to the {@link #event-beforeCellEditStart} and return false:
 *
 * ```javascript
 * grid.on('beforeCellEditStart', ({ editorContext }) => {
 *     return editorContext.column.field !== 'id';
 * });
 * ```
 *
 * To use an alternative input field to edit a cell, listen to the {@link #event-beforeCellEditStart} and
 * set the `editor` property of the context to the input field you want to use:
 *
 * ```javascript
 * grid.on('beforeCellEditStart', ({ editorContext }) => {
 *     return editorContext.editor = myDateField;
 * });
 * ```
 *
 * This feature is **enabled** by default.
 *
 * @extends Core/mixin/InstancePlugin
 *
 * @demo Grid/celledit
 * @classtype cellEdit
 * @externalexample feature/CellEdit.js
 */

class CellEdit extends Delayable(InstancePlugin) {
  //region Config
  static get $name() {
    return 'CellEdit';
  } // Default configuration

  static get defaultConfig() {
    return {
      /**
       * Set to true to select the field text when editing starts
       * @config {Boolean}
       * @default
       */
      autoSelect: true,

      /**
       * What action should be taken when focus moves leaves the cell editor, for example when clicking outside.
       * May be `'complete'` or `'cancel`'.
       * @config {String}
       * @default
       */
      blurAction: 'complete',

      /**
       * Set to true to have TAB on the last cell in the data set create a new record
       * and begin editing it at its first editable cell.
       *
       * If this is configured as an object, it is used as the default data value set for each new record.
       * @config {Boolean|Object}
       * @default
       */
      addNewAtEnd: null,

      /**
       * Set to true to start editing when user starts typing text on a focused cell (as in Excel)
       * @config {Boolean}
       * @default
       */
      autoEdit: false,

      /**
       * Class to use as an editor. Default value: {@link Core.widget.Editor}
       * @config {Core.widget.Widget}
       * @internal
       */
      editorClass: Editor,

      /**
       * The name of the grid event that will trigger cell editing. Defaults to
       * {@link Grid.view.mixin.GridElementEvents#event-cellDblClick celldblclick} but can be changed to any other event,
       * such as {@link Grid.view.mixin.GridElementEvents#event-cellClick cellclick}.
       *
       * ```javascript
       * features : {
       *     cellEdit : {
       *         triggerEvent : 'cellclick'
       *     }
       * }
       * ```
       *
       * @config {String}
       * @default
       */
      triggerEvent: 'celldblclick',
      focusCellAnimationDuration: false
    };
  } // Plugin configuration. This plugin chains some of the functions in Grid.

  static get pluginConfig() {
    return {
      assign: ['startEditing'],
      before: ['onElementKeyDown', 'onElementMouseDown'],
      chain: ['onElementClick', 'bindStore']
    };
  } //endregion
  //region Init

  construct(grid, config) {
    super.construct(grid, config);
    const me = this,
          gridListeners = {
      renderRows: 'onGridRefreshed',
      cellClick: 'onCellClick',
      thisObj: me
    };
    me.grid = grid;

    if (me.triggerEvent !== 'cellclick') {
      gridListeners[me.triggerEvent] = 'onTriggerEditEvent';
    }

    grid.on(gridListeners);
    grid.rowManager.on({
      changeTotalHeight: 'onGridRefreshed',
      thisObj: me
    });
    me.bindStore(grid.store);
  }

  bindStore(store) {
    this.detachListeners('store');
    store.on({
      name: 'store',
      update: 'onStoreUpdate',
      thisObj: this
    });
  }
  /**
   * Displays a YES/NO confirmation dialog box owned by the current Editor. This is intended to be
   * used by {@link Grid.column.Column#config-finalizeCellEdit} implementations. The returned promise resolves passing `true`
   * if the "yes" button is pressed, and `false` if the "No" button is pressed. Typing `ESC` rejects.
   * @param {Object} options An options object for what to show.
   * @param {String} [options.title] The title to show in the dialog header.
   * @param {String} [options.message] The message to show in the dialog body.
   * @async
   */

  async confirm(options) {
    let result = true;

    if (this.editorContext) {
      // The input field must not lose containment of focus during this confirmation
      // so temporarily make the MessageDialog a descendant widget.
      MessageDialogSingleton.owner = this.editorContext.editor.inputField;
      result = await MessageDialogSingleton.confirm(options);
      MessageDialogSingleton.owner = null;
    }

    return result === MessageDialogSingleton.yesButton;
  }

  doDestroy() {
    this.cancelEditing(true); // To kill timeouts

    this.grid.columns.allRecords.forEach(column => {
      if (column._cellEditor) {
        column._cellEditor.destroy();
      }
    });
    super.doDestroy();
  }

  doDisable(disable) {
    if (disable) {
      this.cancelEditing(true);
    }

    super.doDisable(disable);
  }

  set disabled(disabled) {
    super.disabled = disabled;
  }

  get disabled() {
    const {
      grid
    } = this;
    return Boolean(super.disabled || grid.disabled || grid.readOnly);
  } //endregion
  //region Editing

  /**
   * Is any cell currently being edited?
   * @readonly
   * @property {Boolean}
   */

  get isEditing() {
    return Boolean(this.editorContext);
  }
  /**
   * Returns the record currently being edited, or `null`
   * @readonly
   * @property {Core.data.Model}
   */

  get activeRecord() {
    var _this$editorContext;

    return ((_this$editorContext = this.editorContext) === null || _this$editorContext === void 0 ? void 0 : _this$editorContext.record) || null;
  }
  /**
   * Internal function to create or get existing editor for specified cell.
   * @private
   * @param cellContext Cell to get or create editor for
   * @returns {Core.widget.Editor} An Editor container which displays the input field.
   * @category Internal
   */

  getEditorForCell({
    cell,
    column,
    selector,
    editor
  }) {
    const me = this,
          {
      grid
    } = me; // Reuse the Editor by caching it on the column.

    let cellEditor = column._cellEditor,
        leftOffset = 0; // Only applicable for tree cells to show editor right of the icons etc
    // Help Editor match size and position

    if (column.editTargetSelector) {
      const editorTarget = cell.querySelector(column.editTargetSelector);
      leftOffset = editorTarget.offsetLeft;
    }

    editor.autoSelect = me.autoSelect;

    if (cellEditor) {
      // Already got the positioned Editor container which carries the input field.
      // just check if the actual field has been changed in a beforeCellEditStart handler.
      // If so, switch it out.
      if (cellEditor.inputField !== editor) {
        cellEditor.remove(cellEditor.items[0]);
        cellEditor.add(editor);
      }

      cellEditor.align.offset[0] = leftOffset;
    } else {
      cellEditor = column._cellEditor = new me.editorClass({
        constrainTo: null,
        cls: 'b-cell-editor',
        inputField: editor,
        blurAction: 'none',
        invalidAction: column.invalidAction,
        completeKey: false,
        cancelKey: false,
        owner: grid,
        align: {
          align: 't0-t0',
          offset: [leftOffset, 0]
        },
        listeners: me.getEditorListeners()
      });
    } // Keep the record synced with the value

    if (column.instantUpdate && !editor.cellEditValueSetter) {
      ObjectHelper.wrapProperty(editor, 'value', null, value => {
        const {
          editorContext
        } = me; // Only tickle the record if the value has changed.

        if (editorContext !== null && editorContext !== void 0 && editorContext.editor.isValid && !ObjectHelper.isEqual(editorContext.record[editorContext.column.field], value)) {
          editorContext.record[editorContext.column.field] = value;
        }
      });
      editor.cellEditValueSetter = true;
    }

    Object.assign(cellEditor.element.dataset, {
      rowId: selector.id,
      columnId: selector.columnId,
      field: column.field
    }); // First ESC press reverts

    cellEditor.inputField.revertOnEscape = column.revertOnEscape;
    return me.editor = cellEditor;
  } // Turned into function to allow overriding in Gantt, and make more configurable in general

  getEditorListeners() {
    return {
      focusout: 'onEditorFocusOut',
      focusin: 'onEditorFocusIn',
      start: 'onEditorStart',
      beforecomplete: 'onEditorBeforeComplete',
      complete: 'onEditorComplete',
      cancel: 'onEditorCancel',
      thisObj: this
    };
  }

  onEditorStart({
    source: editor
  }) {
    const me = this,
          editorContext = me.editorContext = editor.cellEditorContext;

    if (editorContext) {
      const {
        grid
      } = me,
            {
        cell
      } = editorContext;
      cell.classList.add('b-editing'); // Should move editing to new cell on click, unless click is configured to start editing - in which case it
      // will move anyway

      if (me.triggerEvent !== 'cellclick') {
        grid.on({
          cellclick: 'onCellClickWhileEditing'
        }, me);
      } // Handle tapping outside of the grid element. Use GlobalEvents
      // because it uses a capture:true listener before any other handlers
      // might stop propagation.
      // Cannot use delegate here. A tapped cell will match :not(#body-container)

      me.removeEditingListeners = GlobalEvents.addListener({
        globaltap: 'onTapOut',
        thisObj: me
      });
      /**
       * Fires on the owning Grid when editing starts
       * @event startCellEdit
       * @param {Grid.view.Grid} source Owner grid
       * @param {Object} editorContext Editing context
       * @param {Core.widget.Editor} editorContext.editor The Editor being used.
       * Will contain an `inputField` property which is the field being used to perform the editing.
       * @param {Grid.column.Column} editorContext.column Target column
       * @param {Core.data.Model} editorContext.record Target record
       * @param {HTMLElement} editorContext.cell Target cell
       * @param {*} editorContext.value Cell value
       */

      grid.trigger('startCellEdit', {
        grid,
        editorContext
      });
    }
  }

  onEditorBeforeComplete(context) {
    const {
      grid
    } = this,
          editor = context.source,
          editorContext = editor.cellEditorContext;
    context.grid = grid;
    context.editorContext = editorContext;
    /**
     * Fires on the owning Grid before the cell editing is finished, return false to signal that the value is invalid and editing should not be finalized.
     * @event beforeFinishCellEdit
     * @param {Grid.view.Grid} grid Target grid
     * @param {Object} editorContext Editing context
     * @param {Core.widget.Editor} editorContext.editor The Editor being used.
     * Will contain an `inputField` property which is the field being used to perform the editing.
     * @param {Grid.column.Column} editorContext.column Target column
     * @param {Core.data.Model} editorContext.record Target record
     * @param {HTMLElement} editorContext.cell Target cell
     * @param {*} editorContext.value Cell value
     */

    return grid.trigger('beforeFinishCellEdit', context);
  }

  onEditorComplete({
    source: editor
  }) {
    const {
      grid
    } = this,
          editorContext = editor.cellEditorContext; // Ensure the docs below are accurate!

    editorContext.value = editor.inputField.value;
    /**
     * Fires on the owning Grid when cell editing is finished
     * @event finishCellEdit
     * @param {Grid.view.Grid} grid Target grid
     * @param {Object} editorContext Editing context
     * @param {Core.widget.Editor} editorContext.editor The Editor being used.
     * Will contain an `inputField` property which is the field being used to perform the editing.
     * @param {Grid.column.Column} editorContext.column Target column
     * @param {Core.data.Model} editorContext.record Target record
     * @param {HTMLElement} editorContext.cell Target cell
     * @param {*} editorContext.value Cell value
     */

    grid.trigger('finishCellEdit', {
      grid,
      editorContext
    });
    this.cleanupAfterEdit(editorContext);
  }

  onEditorCancel() {
    const {
      editorContext,
      muteEvents,
      grid
    } = this;

    if (editorContext) {
      this.cleanupAfterEdit(editorContext);
    }

    if (!muteEvents) {
      /**
       * Fires on the owning Grid when editing is cancelled
       * @event cancelCellEdit
       * @param {Grid.view.Grid} source Owner grid
       */
      grid.trigger('cancelCellEdit', {
        grid
      });
    }
  }

  cleanupAfterEdit(editorContext) {
    const me = this,
          {
      editor
    } = editorContext;
    editorContext.cell.classList.remove('b-editing');
    editor.cellEditorContext = me.editorContext = null;
    me.grid.un({
      cellclick: 'onCellClickWhileEditing',
      viewportResize: 'onViewportResizeWhileEditing'
    }, me);
    me.removeEditingListeners(); // MS Edge workaround.
    // At this moment active element is grid.element, but removing editor element still triggers focusout event
    // which is processed by the GlobalEvents, which decides that focus goes to body element. That, in turn, triggers
    // clearFocus on grid navigation, removing focused cell from cache etc, eventually focus actually goes to body.
    // Suspending listener to seamlessly remove element keeping focus where it belongs.
    // NOTE: not reproducible in IFrame, so our tests cannot catch this

    GlobalEvents.suspendFocusEvents();
    editor.element.remove();
    GlobalEvents.resumeFocusEvents();
  }
  /**
   * Find the next succeeding or preceding cell which is editable (column.editor != false)
   * @param {Object} cellInfo
   * @param {Boolean} isForward
   * @returns {Object}
   * @private
   * @category Internal
   */

  getAdjacentEditableCell(cellInfo, isForward) {
    const {
      grid
    } = this,
          {
      store,
      columns
    } = grid;
    let rowId = cellInfo.id,
        columnId = cellInfo.columnId,
        column = columns.getAdjacentLeaf(columnId, isForward);

    while (rowId) {
      const record = store.getById(rowId);

      if (column) {
        columnId = column.id;

        if (!column.hidden && column.editor && column.canEdit(record)) {
          return {
            id: rowId,
            columnId: column.id
          };
        }

        column = columns.getAdjacentLeaf(columnId, isForward);
      } else {
        const editRec = store.getAdjacent(cellInfo.id, isForward, false, true);
        rowId = editRec === null || editRec === void 0 ? void 0 : editRec.id;

        if (editRec) {
          column = isForward ? columns.first : columns.leaves[columns.leaves.length - 1];
        }
      }
    }

    return null;
  }
  /**
   * Adds a new, empty record at the end of the TaskStore with the initial
   * data specified by the {@link Grid.feature.CellEdit#config-addNewAtEnd} setting.
   *
   * @private
   * @returns {Core.data.Model} Newly added record
   */

  doAddNewAtEnd() {
    const newRecordConfig = typeof this.addNewAtEnd === 'object' ? ObjectHelper.clone(this.addNewAtEnd) : {},
          {
      grid
    } = this,
          record = grid.store.add(newRecordConfig)[0]; // If the new record was not added due to it being off the end of the rendered block
    // ensure we force it to be there before we attempt to edit it.

    if (!grid.rowManager.getRowFor(record)) {
      grid.rowManager.displayRecordAtBottom();
    }

    return record;
  }
  /**
   * Creates an editing context object for the passed cell context (target cell must be in the DOM).
   *
   * If the referenced cell is editable, an object returned will
   * be returned containing the following properties:
   *
   *     - column
   *     - record
   *     - cell
   *     - value
   *     - selector
   *
   * If the references cell is _not_ editable, `false` will be returned.
   * @param {Object} cellContext an object which encapsulates a cell.
   * @param {String} cellContext.id The record id of the row to edit
   * @param {String} cellContext.columnId The column id of the column to edit
   * @private
   */

  getEditingContext(cellContext) {
    cellContext = this.grid.normalizeCellContext(cellContext);
    const me = this,
          {
      grid
    } = me,
          column = grid.columns.getById(cellContext.columnId),
          record = grid.store.getById(cellContext.id); // Cell must be in the DOM to edit.
    // Cannot edit hidden columns and columns without an editor.
    // Cannot edit special rows (groups etc).

    if (column && !column.hidden && column.editor && record && !record.isSpecialRow && column.canEdit(record)) {
      const value = record === null || record === void 0 ? void 0 : record[column.field];
      return {
        column,
        record,
        value: value === undefined ? null : value,
        selector: cellContext,
        editor: column.editor
      };
    } else {
      return false;
    }
  }
  /**
   * Start editing specified cell. If no cellContext is given it starts with the first cell in the first row.
   * This function is exposed on Grid and can thus be called as `grid.startEditing(...)`
   * @param {Object} cellContext Cell specified in format { id: 'x', columnId/column/field: 'xxx' }. See {@link Grid.view.Grid#function-getCell} for details.
   * @fires startCellEdit
   * @returns {Boolean} editingStarted
   * @category Editing
   */

  startEditing(cellContext = {}) {
    const me = this; // If already editing, or grid is readonly, no can do.

    if (!(me.editorContext || me.disabled)) {
      const {
        grid
      } = me,
            normalizedContext = grid.normalizeCellContext(cellContext),
            editorContext = me.getEditingContext(normalizedContext); // Cannot edit hidden columns and columns without an editor
      // Cannot edit special rows (groups etc).

      if (!editorContext) {
        return false;
      } // Now that we know we can edit this cell, scroll the record into view and register it as last focusedCell

      grid.focusCell(cellContext);
      editorContext.cell = grid.getCell(cellContext);
      /**
       * Fires on the owning Grid before editing starts, return `false` to prevent editing
       * @event beforeCellEditStart
       * @preventable
       * @param {Grid.view.Grid} source Owner grid
       * @param {Object} editorContext Editing context
       * @param {Grid.column.Column} editorContext.column Target column
       * @param {Core.data.Model} editorContext.record Target record
       * @param {HTMLElement} editorContext.cell Target cell
       * @param {Core.widget.Field} editorContext.editor The input field that the column is configured
       * with (see {@link Grid.column.Column#config-field}). This property mey be replaced
       * to be a different {@link Core.widget.Field field} in the handler, to take effect
       * just for the impending edit.
       * @param {Function} [editorContext.finalize] An async function may be injected into this property
       * which performs asynchronous finalization tasks such as complex validation of confirmation. The
       * value `true` or `false` must be returned.
       * @param {Object} [editorContext.finalize.context] An object describing the editing context upon requested completion of the edit.
       * @param {*} editorContext.value Cell value
       */

      if (grid.trigger('beforeCellEditStart', {
        grid,
        editorContext
      }) === false) {
        return false;
      } // Focus grid element to preserve focus inside once editing is started
      // https://app.assembla.com/spaces/bryntum/tickets/8155-grid-cell-not-properly-focused-in-advanced-demo

      DomHelper.focusWithoutScrolling(grid.focusElement);
      const editor = editorContext.editor = me.getEditorForCell(editorContext),
            {
        cell,
        record,
        column
      } = editorContext; // Prevent highlight when setting the value in the editor

      editor.inputField.highlightExternalChange = false;
      editor.cellEditorContext = editorContext;
      editor.render(me.grid.getSubGridFromColumn(column).element); // Attempt to start edit.
      // We will set up our context in onEditorStart *if* the start was successful.

      editor.startEdit({
        target: cell,
        record,
        field: editor.inputField.name || editorContext.column.field
      });
      return true;
    }

    return false;
  }
  /**
   * Cancel editing, destroys the editor
   * @param {Boolean} silent Pass true to prevent method from firing event
   * @fires cancelCellEdit
   * @category Editing
   */

  cancelEditing(silent = false) {
    const me = this,
          {
      editorContext,
      editor,
      grid
    } = me;

    if (editorContext) {
      // If cancel was not called from onEditorFocusOut, then refocus the grid.
      if (editor.containsFocus) {
        // Kill editorContext before we destroy the editor so that we know we are not editing
        // in ensuing focusout event handling
        me.editorContext = null; // Control focus reversion if we own focus

        if (editor.inputField.owns(DomHelper.activeElement)) {
          DomHelper.focusWithoutScrolling(grid.focusElement);
        }

        me.editorContext = editorContext;
      }

      me.muteEvents = silent;
      editor.cancelEdit();
      me.muteEvents = false;
    }
  }
  /**
   * Finish editing, update the underlying record and destroy the editor
   * @fires finishCellEdit
   * @category Editing
   * @returns `false` if the edit could not be finished due to the value being invalid or the
   * Editor's `complete` event was vetoed.
   * @async
   */

  async finishEditing() {
    const me = this,
          {
      editorContext
    } = me;
    let result = false;

    if (editorContext) {
      var _me$grid$waitForTrans, _me$grid;

      const {
        column
      } = editorContext; // If completeEdit finds that the editor context has a finalize method in it,
      // it will *await* the completion of that method before completing the edit
      // so we must await completeEdit.
      // We can override that finalize method by passing the column's own finalizeCellEdit.
      // Set a flag (promise) indicating that we are in the middle of editing finalization

      me.finishEditingPromise = editorContext.editor.completeEdit(column.bindCallback(column.finalizeCellEdit));
      result = await me.finishEditingPromise; // If grid is animating, wait for it to finish to not start a follow up edit when things are moving
      // (only applies to Scheduler for now, tested in Schedulers CellEdit.t.js)

      await ((_me$grid$waitForTrans = (_me$grid = me.grid).waitForTransition) === null || _me$grid$waitForTrans === void 0 ? void 0 : _me$grid$waitForTrans.call(_me$grid)); // reset the flag

      me.finishEditingPromise = null;
    }

    return result;
  } //endregion
  //region Events

  /**
   * Event handler added when editing is active called when user clicks a cell in the grid during editing.
   * It finishes editing and moves editor to the selected cell instead.
   * @private
   * @category Internal event handling
   */

  async onCellClickWhileEditing({
    event,
    cellSelector
  }) {
    const me = this; // Ignore clicks if async finalization is running

    if (me.finishEditingPromise) {
      return;
    } // Ignore clicks in the editor.

    if (me.editorContext && !me.editorContext.editor.owns(event.target)) {
      if (me.getEditingContext(cellSelector)) {
        // Attempt to finish the current edit.
        // Will return false if the field is invalid.
        if (await me.finishEditing()) {
          me.startEditing(cellSelector);
        } // Previous edit was invalid, return to it.
        else {
            me.grid.focusCell(me.editorContext.selector);
            me.editor.inputField.focus();
          }
      } else {
        me.finishEditing();
      }
    }
  }
  /**
   * Starts editing if user taps selected cell again on touch device. Chained function called when user clicks a cell.
   * @private
   * @category Internal event handling
   */

  onCellClick({
    source: grid,
    record,
    cellSelector,
    cellElement,
    target,
    event
  }) {
    const selected = grid.focusedCell || {},
          column = grid.columns.getById(cellSelector.columnId); // Columns may provide their own handling of cell editing

    if (column.onCellClick) {
      column.onCellClick({
        grid,
        column,
        record,
        cellSelector,
        cellElement,
        target,
        event
      });
    } else if (target.closest('.b-tree-expander')) {
      this.cancelEditing();
      return false;
    } else if (DomHelper.isTouchEvent && cellSelector.id == selected.id && cellSelector.columnId == selected.columnId) {
      this.startEditing(cellSelector);
    } else if (this.triggerEvent === 'cellclick') {
      this.onTriggerEditEvent({
        cellSelector
      });
    }
  }
  /**
   * Called when the user triggers the edit action in {@link #config-triggerEvent} config. Starts editing.
   * @private
   * @category Internal event handling
   */

  onTriggerEditEvent({
    cellSelector
  }) {
    if (this.editorContext && !this.finishEditing()) {
      return;
    }

    this.startEditing(cellSelector);
  }
  /**
   * Update the input field if underlying data changes during edit.
   * @private
   * @category Internal event handling
   */

  onStoreUpdate({
    changes,
    record
  }) {
    const {
      editorContext
    } = this;

    if (editorContext !== null && editorContext !== void 0 && editorContext.editor.isVisible) {
      if (record === editorContext.record && editorContext.editor.dataField in changes) {
        editorContext.editor.refreshEdit();
      }
    }
  }
  /**
   * Realign editor if grid renders rows while editing is ongoing (as a result to autoCommit or WebSocket data received).
   * @private
   * @category Internal event handling
   */

  onGridRefreshed() {
    const me = this,
          {
      grid
    } = me;

    if (me.editorContext && grid.focusedCell) {
      const cell = grid.getCell(grid.focusedCell);

      if (cell) {
        me.editorContext.editor.showBy(cell);
      } else {
        me.cancelEditing();
      }
    }
  }
  /**
   * Chained function called on key down. [enter] or [f2] starts editing. [enter] also finishes editing and starts
   * editing next row, [f2] also finishes editing without moving to the next row. [esc] cancels editing. [tab]
   * edits next column, [shift] + [tab] edits previous.
   * @param event
   * @private
   * @category Internal event handling
   */

  async onElementKeyDown(event) {
    const me = this; // flagging event with handled = true used to signal that other features should probably not care about it

    if (event.handled || !me.grid.focusElement.contains(event.target)) {
      return;
    }

    if (!me.editorContext) {
      const {
        key
      } = event,
            backspace = key === 'Backspace',
            autoEdit = me.autoEdit && (key.length === 1 || backspace); // enter or f2 to edit, or any character key if autoEdit is enabled

      if ((autoEdit || validNonEditingKeys[key]) && me.grid.focusedCell) {
        if (me.startEditing(me.grid.focusedCell)) {
          const {
            inputField
          } = me.editor,
                {
            input
          } = inputField; // if editing started with a keypress and the editor has an input field, set its value

          if (autoEdit && input) {
            // Simulate a keydown in an input field by setting input value
            // plus running our internal processing of that event
            inputField.internalOnKeyEvent(event);

            if (!event.defaultPrevented) {
              input.value = backspace ? '' : key;
              inputField.internalOnInput(event); // IE11 + Edge put caret at 0 when focusing

              inputField.moveCaretToEnd();
            }
          }
        }

        event.preventDefault();
      }
    } else {
      switch (event.key) {
        case 'Enter':
          me.onEnterKeyPress(event);
          break;

        case 'F2':
          event.preventDefault();
          me.finishEditing();
          break;

        case 'Escape':
          event.stopPropagation();
          event.preventDefault();
          me.cancelEditing();
          break;

        case 'Tab':
          me.onTabKeyPress(event);
          break;
      } // prevent arrow keys from moving editor

      if (validEditingKeys[event.key]) {
        event.handled = true;
      }
    }
  }

  async onEnterKeyPress(event) {
    const me = this,
          {
      grid
    } = me;
    event.preventDefault();
    event.stopPropagation();

    if (await me.finishEditing()) {
      // Finalizing might have been blocked by an invalid value
      if (!me.isEditing) {
        if (event.ctrlKey || event.metaKey || event.altKey || me.grid.touch) {
          // Enter in combination with special keys finishes editing
          // On touch Enter always finishes editing. Feels more natural since no tab-key etc.
          return;
        } // Edit previous

        if (event.shiftKey) {
          if (grid.internalNextPrevRow(false, true, event, false)) {
            me.startEditing(grid.focusedCell);
          }
        } // Edit next
        else {
            // If we are at the last editable cell, optionally add a new row
            if (me.addNewAtEnd) {
              const record = grid.store.getById(grid.focusedCell.id);

              if (record === grid.store.last) {
                await me.doAddNewAtEnd();
              }
            }

            if (grid.internalNextPrevRow(true, true)) {
              me.startEditing(grid.focusedCell);
            }
          }
      }
    }
  }

  async onTabKeyPress(event) {
    event.preventDefault();
    const me = this,
          {
      focusedCell
    } = me.grid;

    if (focusedCell) {
      const isForward = !event.shiftKey;
      let cellInfo = me.getAdjacentEditableCell(focusedCell, isForward); // If we are at the last editable cell, optionally add a new row

      if (!cellInfo && isForward && me.addNewAtEnd) {
        const currentEditableFinalizationResult = await me.finishEditing();

        if (currentEditableFinalizationResult === true) {
          await this.doAddNewAtEnd(); // Re-grab the next editable cell

          cellInfo = me.getAdjacentEditableCell(focusedCell, isForward);
        }
      }

      if (cellInfo) {
        let finalizationResult = true;

        if (me.isEditing) {
          finalizationResult = await me.finishEditing();
        }

        if (finalizationResult) {
          me.grid.focusCell(cellInfo, {
            animate: me.focusCellAnimationDuration
          });
          me.startEditing(cellInfo);
        }
      }
    }
  }

  onElementMouseDown(event) {
    // If it's a contextmenu mousedown during cell edit, prevent default
    // because the contextmenu handler will move focus directly to the context menu.
    // If we allow it to go through the grid, the edit will not terminate because
    // that usually means begin editing somewhere else in the grid.
    // TODO: This won't be necessary when cells are the focusable DOM unit.
    if (event.button === 2 && this.editorContext) {
      event.preventDefault();
    }
  }
  /**
   * Cancel editing on widget focusout
   * @private
   */

  onEditorFocusOut(event) {
    const me = this,
          {
      grid
    } = me; // If the editor is not losing focus as a result of its tidying up process
    // And focus is moving to outside of the grid, or back to the initiating cell
    // (which indicates a click on empty space below rows), then explicitly terminate.

    if (me.editorContext && !me.editor.isFinishing && me.editor.inputField.owns(event._target) && (event.toWidget !== grid || grid.isLocationEqual(grid.focusedCell, me.editorContext.selector))) {
      if (me.blurAction === 'cancel') {
        me.cancelEditing();
      } // if not already in the middle of editing finalization (that could be async)
      else if (!me.finishEditingPromise) {
          me.finishEditing();
        }
    }
  }

  onEditorFocusIn(event) {
    const widget = event.toWidget;

    if (widget === this.editor.inputField) {
      if (this.autoSelect && widget.selectAll && !widget.readOnly && !widget.disabled) {
        widget.selectAll();
      }
    }
  }
  /**
   * Cancel edit on touch outside of grid for mobile Safari (focusout not triggering unless you touch something focusable)
   * @private
   */

  onTapOut({
    event
  }) {
    const me = this;

    if (!me.grid.bodyContainer.contains(event.target)) {
      if (!me.editor.owns(event.target)) {
        if (me.blurAction === 'cancel') {
          me.cancelEditing();
        } else {
          me.finishEditing();
        }
      }
    }
  }
  /**
   * Finish editing if clicking below rows (only applies when grid is higher than rows).
   * @private
   * @category Internal event handling
   */

  onElementClick(event) {
    if (event.target.classList.contains('b-grid-body-container') && this.editorContext) {
      this.finishEditing();
    }
  } //endregion

}
CellEdit._$name = 'CellEdit';
GridFeatureManager.registerFeature(CellEdit, true);

/**
 * @module Grid/feature/base/ContextMenuBase
 */

/**
 * Abstract base class used by other context menu features.
 * @extends Core/mixin/InstancePlugin
 * @abstract
 */

class ContextMenuBase extends InstancePlugin {
  static get $name() {
    return 'ContextMenuBase';
  } //region Config

  static get configurable() {
    return {
      /**
       * This is a type of the context menu used to generate correct names for methods and events.
       * Should be in camel case. Required to be set in subclass.
       * @config {String}
       * @readonly
       */
      type: null,

      /**
       * Gets the Menu instance that this feature is using.
       * @member {Core.widget.Menu} menu
       * @readonly
       */

      /**
       * A config which will be applied when creating the Menu component.
       * @config {Object}
       */
      menu: {
        $config: ['lazy', 'nullify'],
        value: {
          type: 'menu',
          autoShow: false,
          closeAction: 'hide',
          scrollAction: 'hide',
          constrainTo: window
        }
      },

      /**
       * A config which will be applied when creating the Menu component.
       * @config {Object}
       * @deprecated 4.0.5 Use {@link #config-menu} instead.
       */
      menuConfig: null,

      /**
       * {@link Core.widget.Menu Menu} items object containing named child menu items to apply to the feature's
       * provided context menu.
       *
       * This may add extra items as below, but may also remove any of the default items by configuring the name
       * of the item as `false`
       *
       * ```javascript
       * features : {
       *     cellMenu : {
       *         // This object is applied to the Feature's predefined default items
       *         items : {
       *             switchToDog : {
       *                 text : 'Dog',
       *                 icon : 'b-fa b-fa-fw b-fa-dog',
       *                 onItem({contextRecord}) {
       *                     contextRecord.dog = true;
       *                     contextRecord.cat = false;
       *                 },
       *                 weight : 500     // Make this second from end
       *             },
       *             switchToCat : {
       *                 text : 'Cat',
       *                 icon : 'b-fa b-fa-fw b-fa-cat',
       *                 onItem({contextRecord}) {
       *                     contextRecord.dog = false;
       *                     contextRecord.cat = true;
       *                 },
       *                 weight : 510     // Make this sink to end
       *             },
       *             add : false // We do not want the "Add" submenu to be available
       *         }
       *     }
       * }
       * ```
       *
       * @config {Object}
       */
      items: {},

      /**
       * Event which is used to show context menu.
       * Available options are: 'contextmenu', 'click', 'dblclick'.
       * Default value is used from {@link Grid/view/GridBase#config-contextMenuTriggerEvent}
       * @config {String}
       */
      triggerEvent: false
    };
  } // Plugin configuration. This plugin chains some of the functions in Grid.
  // The contextmenu event is emulated from a taphold gesture on touch platforms.

  static get pluginConfig() {
    return {
      assign: ['showContextMenu'],
      chain: ['onElementContextMenu', 'onElementClick', 'onElementDblClick', 'onElementKeyDown']
    };
  } //endregion
  //region Init

  construct(...args) {
    var _this$type;

    const config = args[args.length === 1 ? 0 : 1]; // Copy deprecated config into its correct place.

    if (config && 'menuConfig' in config) {
      VersionHelper.deprecate('Grid', '5.0.0', '`menuConfig` attribute deprecated, in favour of `menu`');
      config.menu = config.menuConfig;
    }

    super.construct(...args);

    if (!((_this$type = this.type) !== null && _this$type !== void 0 && _this$type.length)) {
      throw new Error(`Config 'type' is required to be specified for context menu`);
    }
  } //endregion
  //region Events

  /**
   * Fired from grid when an item is selected in the context menu.
   * @event contextMenuItem
   * @param {Grid.view.Grid} source The grid
   * @param {Core.widget.Menu} menu The menu
   * @param {Object} item Selected menu item
   */

  /**
   * Fired from grid when a check item is toggled in the context menu.
   * @event contextMenuToggleItem
   * @param {Grid.view.Grid} source The grid
   * @param {Core.widget.Menu} menu The menu
   * @param {Object} item Selected menu item
   * @param {Boolean} checked Checked or not
   */

  onElementContextMenu(event) {
    this.triggerEvent === 'contextmenu' && this.internalShowContextMenu(event);
  }

  onElementClick(event) {
    this.triggerEvent === 'click' && this.internalShowContextMenu(event);
  }

  onElementDblClick(event) {
    this.triggerEvent === 'dblclick' && this.internalShowContextMenu(event);
  }

  onElementKeyDown(event) {} //endregion
  //region Menu handlers

  internalShowContextMenu(event) {
    const me = this;

    if (me.disabled) {
      return;
    }

    const data = me.getDataFromEvent(event);

    if (me.shouldShowMenu(data)) {
      me.showContextMenu(data);
    }
  }

  getDataFromEvent(event) {
    return {
      event,
      targetElement: this.getTargetElementFromEvent(event)
    };
  }

  getTargetElementFromEvent(event) {
    return event.target;
  }
  /**
   * Shows the context menu.
   * @param {Object} menuContext An informational object containing contextual information about the last activation
   * of the context menu.
   * @param {Event} menuContext.event The initiating event.
   * @param {HTMLElement} menuContext.targetElement The target to which the menu is being applied.
   * @internal
   */

  showContextMenu(menuContext) {
    var _me$_menu;

    const me = this; // If our menu was visible from last invocation, hide it.
    // Apps may need the ${type}MenuShow event which is triggered in onShow.

    (_me$_menu = me._menu) === null || _me$_menu === void 0 ? void 0 : _me$_menu.hide();

    if (me.disabled) {
      return;
    }
    /**
     * @member {Object} menuContext
     * An informational object containing contextual information about the last activation
     * of the context menu. The base properties are listed below. Some subclasses may add extra
     * contextual information such as `eventRecord` and `resourceRecord` to the block.
     * @property {Event} menuContext.event The initiating event.
     * @property {Number[]} menuContext.point The client `X` and `Y` position of the initiating event.
     * @property {HTMLElement} menuContext.targetElement The target to which the menu is being applied.
     * @property {Object} menuContext.items The context menu **configuration** items.
     * @property {Core.data.Model[]} menuContext.selection The record selection in the client (Grid, Scheduler, Gantt or Calendar).
     * @readonly
     */

    me.menuContext = menuContext;
    const {
      type,
      client,
      processItems
    } = me,
          {
      event
    } = menuContext,
          elCenter = event !== null && event !== void 0 ? event : Rectangle.from(menuContext.targetElement).center;
    Objects.assign(menuContext, {
      point: event ? [event.clientX + 1, event.clientY + 1] : [elCenter.x, elCenter.y],
      items: {},
      selection: me.client.selectedRecords
    }); // Call the chainable method which other features use to add or remove their own menu items.

    me.callChainablePopulateMenuMethod(menuContext); // Merge with user defined items

    Objects.merge(menuContext.items, me.baseItems); // Allow user a chance at processing the items and preventing the menu from showing

    if ((!processItems || processItems(menuContext) !== false) && me.hasActiveMenuItems(menuContext)) {
      me.populateItemsWithData(menuContext);
      me.preventDefaultEvent(menuContext); // beforeContextMenuShow is a lifecycle method which may be implemented in subclasses to preprocess the event.

      if (me.beforeContextMenuShow(menuContext) !== false) {
        // Trigger event that allows preventing menu or manipulating its items
        if (client.trigger(`${type}MenuBeforeShow`, menuContext) !== false && // TODO: cellContextMenuBeforeShow and headerContextMenuBeforeShow events are deprecated in ContextMenu feature
        client.trigger(`${type}ContextMenuBeforeShow`, menuContext) !== false) {
          me.menu.items = menuContext.items;
          me.menu.showByPoint(menuContext.point);
        }
      }
    }
  }
  /**
   * Returns the base, configured-in menu items set from the configured items, taking into
   * account the namedItems the feature offers.
   * @property {Object[]}
   * @readonly
   * @internal
   */

  get baseItems() {
    if (!this._baseItems) {
      const me = this,
            {
        namedItems
      } = me,
            baseItems = me._baseItems = Objects.assign({}, me.items); // Substitute any named items into any of our items that reference them.

      for (const ref in baseItems) {
        const item = baseItems[ref];

        if (item) {
          // If this class or instance has a "namedItems" object
          // named by this ref, then use it as the basis for the item
          if (namedItems && ref in namedItems) {
            baseItems[ref] = typeof item === 'object' ? Objects.merge(Objects.clone(namedItems[ref]), item) : namedItems[ref];
          } else if (item === true) {
            delete baseItems[ref];
          }
        }
      }
    }

    return this._baseItems;
  }
  /**
   * Hides the context menu
   * @internal
   */

  hideContextMenu(animate) {
    var _this$menu;

    (_this$menu = this.menu) === null || _this$menu === void 0 ? void 0 : _this$menu.hide(animate);
  }

  callChainablePopulateMenuMethod(eventParams) {
    var _this$client$, _this$client;

    // For example `populateCellMenu`
    (_this$client$ = (_this$client = this.client)[`populate${StringHelper.capitalize(this.type)}Menu`]) === null || _this$client$ === void 0 ? void 0 : _this$client$.call(_this$client, eventParams);
  }

  createContextMenuEventForElement(targetElement) {
    const targetPoint = Rectangle.from(targetElement).center,
          contextmenuEvent = new MouseEvent(this.triggerEvent, {
      clientX: targetPoint.x,
      clientY: targetPoint.y
    });
    Object.defineProperty(contextmenuEvent, 'target', {
      get() {
        return targetElement;
      }

    });
    return contextmenuEvent;
  }

  hasActiveMenuItems(eventParams) {
    return Object.values(eventParams.items).some(item => item);
  }
  /**
   * Override this function and return `false` to prevent the context menu from being shown. Returns `true` by default.
   * @return {Boolean}
   * @internal
   */

  shouldShowMenu() {
    return true;
  }

  beforeContextMenuShow(eventParams) {}

  populateItemsWithData(eventParams) {}

  preventDefaultEvent(eventParams) {
    var _eventParams$event;

    (_eventParams$event = eventParams.event) === null || _eventParams$event === void 0 ? void 0 : _eventParams$event.preventDefault();
  } //endregion
  //region Configurables

  changeTriggerEvent(triggerEvent) {
    return triggerEvent || this.client.contextMenuTriggerEvent;
  }

  changeMenu(menu, oldMenu) {
    const me = this,
          {
      client,
      type
    } = me;

    if (menu) {
      return Menu.reconfigure(oldMenu, menu ? Menu.mergeConfigs({
        owner: client,

        onItem(itemEvent) {
          client.trigger(`${type}MenuItem`, itemEvent); // TODO: contextMenuItem event is deprecated in ContextMenu feature

          client.trigger('contextMenuItem', itemEvent); // TODO: eventContextMenuItem event and schedulerContextMenuItem event are deprecated

          client.trigger(`${type}ContextMenuItem`, itemEvent);
        },

        onToggle(itemEvent) {
          client.trigger(`${type}MenuToggleItem`, itemEvent); // TODO: contextMenuToggleItem event is deprecated in ContextMenu feature

          client.trigger('contextMenuToggleItem', itemEvent);
        },

        onDestroy() {
          me.menu = null;
        },

        // Load up the item event with the contextual info
        onBeforeItem(itemEvent) {
          Object.assign(itemEvent, me.menuContext);
        },

        onShow({
          source: menu
        }) {
          me.menuContext.menu = menu;
          client.trigger(`${type}MenuShow`, me.menuContext); // TODO: cellContextMenuShow and headerContextMenuShow events are deprecated in ContextMenu feature

          client.trigger(`${type}ContextMenuShow`, me.menuContext);
        }

      }, menu) : null, me);
    } else if (oldMenu !== null && oldMenu !== void 0 && oldMenu.isWidget) {
      oldMenu.destroy();
    }
  } //endregion

}
ContextMenuBase._$name = 'ContextMenuBase';

/**
 * @module Grid/feature/CellMenu
 */

/**
 * Right click to display context menu for cells.
 *
 * ### Default cell menu items
 *
 * The Cell menu feature provides only one item by default and all the other items are populated by the other features:
 *
 * | Reference              | Text             | Weight | Feature                            | Description                                           |
 * |------------------------|------------------|--------|------------------------------------|-------------------------------------------------------|
 * | `removeRow`            | Delete record    | 100    | *This feature*                     | Remove record from the store                          |
 * | `search`               | Search for value | 200    | {@link Grid.feature.Search Search} | Search for the selected cell text                     |
 * | `filterDateEquals`     | On               | 300    | {@link Grid.feature.Filter Filter} | Filters by the column field, equal to the cell value  |
 * | `filterDateBefore`     | Before           | 310    | {@link Grid.feature.Filter Filter} | Filters by the column field, less than the cell value |
 * | `filterDateAfter`      | After            | 320    | {@link Grid.feature.Filter Filter} | Filters by the column field, more than the cell value |
 * | `filterNumberEquals`   | Equals           | 300    | {@link Grid.feature.Filter Filter} | Filters by the column field, equal to the cell value  |
 * | `filterNumberLess`     | Less than        | 310    | {@link Grid.feature.Filter Filter} | Filters by the column field, less than the cell value |
 * | `filterNumberMore`     | More than        | 320    | {@link Grid.feature.Filter Filter} | Filters by the column field, more than the cell value |
 * | `filterDurationEquals` | Equals           | 300    | {@link Grid.feature.Filter Filter} | Filters by the column field, equal to the cell value  |
 * | `filterDurationLess`   | Less than        | 310    | {@link Grid.feature.Filter Filter} | Filters by the column field, less than the cell value |
 * | `filterDurationMore`   | More than        | 320    | {@link Grid.feature.Filter Filter} | Filters by the column field, more than the cell value |
 * | `filterStringEquals`   | Equals           | 300    | {@link Grid.feature.Filter Filter} | Filters by the column field, equal to the cell value  |
 * | `filterRemove`         | Remove filter    | 400    | {@link Grid.feature.Filter Filter} | Stops filtering by selected column field              |
 *
 * ### Customizing the menu items
 *
 * The menu items in the Cell menu can be customized, existing items can be changed or removed,
 * and new items can be added. This is handled using the `items` config of the feature.
 *
 * Add extra items for all columns:
 *
 * ```javascript
 * const grid = new Grid({
 *   features : {
 *     cellMenu : {
 *       items : {
 *          extraItem : { text: 'My cell item', icon: 'fa fa-bus', weight: 200, onItem : () => ... }
 *       }
 *     }
 *   }
 * });
 * ```
 *
 * It is also possible to add items using columns config. See examples below.
 *
 * Add extra items for a single column:
 *
 * ```javascript
 * const grid = new Grid({
 *   columns: [
 *     {
 *       field: 'city',
 *       text: 'City',
 *       cellMenuItems: {
 *         columnItem : { text: 'My unique cell item', icon: 'fa fa-beer', onItem : () => ... }
 *       }
 *     }
 *   ]
 * });
 * ```
 *
 * Remove existing item:
 *
 * ```javascript
 * const scheduler = new Scheduler({
 *     features : {
 *         cellMenu : {
 *             items : {
 *                 removeRow : false
 *             }
 *         }
 *     }
 * });
 * ```
 *
 * Customize existing item:
 *
 * ```javascript
 * const scheduler = new Scheduler({
 *     features : {
 *         cellMenu : {
 *             items : {
 *                 removeRow : {
 *                     text : 'Throw away',
 *                     icon : 'b-fa b-fa-dumpster'
 *                 }
 *             }
 *         }
 *     }
 * });
 * ```
 *
 * It is also possible to manipulate the default items and add new items in the processing function:
 *
 * ```javascript
 * const grid = new Grid({
 *   features : {
 *     cellMenu : {
 *       processItems({items, record}) {
 *           if (record.cost > 5000) {
 *              items.myItem = { text : 'Split cost' };
 *           }
 *       }
 *     }
 *   }
 * });
 * ```
 *
 * Full information of the menu customization can be found in the ["Customizing the Cell menu and the Header menu"](#guides/customization/contextmenu.md)
 * guide.
 *
 * This feature is **enabled** by default.
 *
 * @extends Grid/feature/base/ContextMenuBase
 * @demo Grid/filtering
 * @classtype cellMenu
 * @externalexample feature/CellMenu.js
 */

class CellMenu extends ContextMenuBase {
  //region Config
  static get $name() {
    return 'CellMenu';
  }

  static get defaultConfig() {
    return {
      /**
       * A function called before displaying the menu that allows manipulations of its items.
       * Returning `false` from this function prevents the menu being shown.
       *
       * ```javascript
       *   features         : {
       *      cellMenu : {
       *           processItems({ items, record, column }) {
       *               // Add or hide existing items here as needed
       *               items.myAction = {
       *                   text   : 'Cool action',
       *                   icon   : 'b-fa b-fa-fw b-fa-ban',
       *                   onItem : () => console.log(`Clicked ${record.name}`),
       *                   weight : 1000 // Move to end
       *               };
       *
       *               if (!record.allowDelete) {
       *                   items.removeRow.hidden = true;
       *               }
       *           }
       *       }
       *   },
       * ```
       * @param {Object} context An object with information about the menu being shown
       * @param {Core.data.Model} context.record The record representing the current row
       * @param {Grid.column.Column} context.column The current column
       * @param {Object} context.items An object containing the {@link Core.widget.MenuItem menu item} configs keyed by their id
       * @param {Event} context.event The DOM event object that triggered the show
       * @config {Function}
       * @preventable
       */
      processItems: null,

      /**
       * {@link Core.widget.Menu Menu} items object containing named child menu items to apply to the feature's
       * provided context menu.
       *
       * This may add extra items as below, but you can also configure, or remove any of the default items by
       * configuring the name of the item as `false`
       *
       * ```javascript
       * features : {
       *     cellMenu : {
       *         // This object is applied to the Feature's predefined default items
       *         items : {
       *             switchToDog : {
       *                 text : 'Dog',
       *                 icon : 'b-fa b-fa-fw b-fa-dog',
       *                 onItem({contextRecord}) {
       *                     contextRecord.dog = true;
       *                     contextRecord.cat = false;
       *                 },
       *                 weight : 500     // Make this second from end
       *             },
       *             switchToCat : {
       *                 text : 'Cat',
       *                 icon : 'b-fa b-fa-fw b-fa-cat',
       *                 onItem({contextRecord}) {
       *                     contextRecord.dog = false;
       *                     contextRecord.cat = true;
       *                 },
       *                 weight : 510     // Make this sink to end
       *             },
       *             removeRow : {
       *                 // Change icon for the delete item
       *                 icon : 'b-fa b-fa-times'
       *             }
       *         }
       *     }
       * }
       * ```
       *
       * @config {Object}
       */
      items: null,
      type: 'cell'
    };
  }

  static get pluginConfig() {
    const config = super.pluginConfig;
    config.chain.push('populateCellMenu');
    return config;
  } //endregion
  //region Events

  /**
   * Fired from grid before the context menu is shown for a cell.
   * Allows manipulation of the items to show in the same way as in the {@link #config-processItems}.
   *
   * Returning `false` from a listener prevents the menu from being shown.
   *
   * @event cellMenuBeforeShow
   * @preventable
   * @param {Grid.view.Grid} source The grid
   * @param {Core.widget.Menu} menu The menu
   * @param {Object} items Menu item configs
   * @param {Grid.column.Column} column Column
   * @param {Core.data.Model} record Record
   */

  /**
   * Fired from grid after the context menu is shown for a cell.
   * @event cellMenuShow
   * @param {Grid.view.Grid} source The grid
   * @param {Core.widget.Menu} menu The menu
   * @param {Object} items Menu item configs
   * @param {Grid.column.Column} column Column
   * @param {Core.data.Model} record Record
   */

  /**
   * Fired from grid when an item is selected in the cell context menu.
   * @event cellMenuItem
   * @param {Grid.view.Grid} source The grid
   * @param {Core.widget.Menu} menu The menu
   * @param {Object} item Selected menu item
   * @param {Grid.column.Column} column Column
   * @param {Core.data.Model} record Record
   */

  /**
   * Fired from grid when a check item is toggled in the cell context menu.
   * @event cellMenuToggleItem
   * @param {Grid.view.Grid} source The grid
   * @param {Core.widget.Menu} menu The menu
   * @param {Object} item Selected menu item
   * @param {Grid.column.Column} column Column
   * @param {Core.data.Model} record Record
   * @param {Boolean} checked Checked or not
   */
  //endregion
  //region Menu handlers

  showContextMenu(eventParams) {
    const me = this,
          {
      cellSelector,
      id,
      event
    } = eventParams; // Process the gesture as navigation so that the use may select/multiselect
    // the items to include in their context menu operation.
    // Also select if not already selected.

    me.client.focusCell(cellSelector, {
      doSelect: !me.client.isSelected(id),
      event
    });
    super.showContextMenu(eventParams);
  }

  shouldShowMenu({
    column
  }) {
    return column && column.enableCellContextMenu !== false;
  }

  getDataFromEvent(event) {
    return ObjectHelper.assign(super.getDataFromEvent(event), this.client.getCellDataFromEvent(event));
  }

  beforeContextMenuShow(eventParams) {
    if (!eventParams.record || eventParams.record.isSpecialRow) {
      eventParams.items.removeRow = false;
    }
  } //endregion
  //region Getters/Setters

  populateCellMenu({
    items,
    column
  }) {
    const {
      client
    } = this;

    if (column !== null && column !== void 0 && column.cellMenuItems) {
      if (Array.isArray(column.cellMenuItems)) {
        VersionHelper.deprecate('Grid', '5.0.0', '`cellMenuItems` column config specified as an array is deprecated, need to specify the config as a named object. Please see https://bryntum.com/docs/grid/#guides/upgrades/4.0.0.md for more information.');
      } // Array works smoothly since number index turns into a key for named object

      ObjectHelper.merge(items, column.cellMenuItems);
    }

    if (!client.readOnly) {
      items.removeRow = {
        text: client.selectedRecords.length > 1 ? 'L{removeRows}' : 'L{removeRow}',
        localeClass: this,
        icon: 'b-fw-icon b-icon-trash',
        name: 'removeRow',
        cls: 'b-separator',
        weight: 100,
        onItem: () => client.store.remove(client.selectedRecords)
      };

      if (!client.showRemoveRowInContextMenu) {
        VersionHelper.deprecate('Grid', '5.0.0', '`showRemoveRowInContextMenu` config is deprecated, in favor of `CellMenu` feature configuration. Please see https://bryntum.com/docs/grid/#guides/upgrades/4.0.0.md for more information.');
        items.removeRow = false;
      }
    }
  }

  get showMenu() {
    return true;
  } //endregion

}
CellMenu.featureClass = '';
CellMenu._$name = 'CellMenu';
GridFeatureManager.registerFeature(CellMenu, true, ['Grid', 'Scheduler']);
GridFeatureManager.registerFeature(CellMenu, false, ['Gantt']);

/**
 * @module Grid/feature/CellTooltip
 */

/**
 * Displays a tooltip when hovering cells. Contents can be customized by specifying a global `tooltipRenderer` function
 * for the feature and/or on a per column basis.
 *
 * Configuration properties passed into this feature are used to configure the {@link Core.widget.Tooltip} instance
 * used.
 *
 * This feature is <strong>disabled</strong> by default.
 *
 * ## Showing async content
 * Showing remotely loaded content is super easy using the {@link #config-tooltipRenderer}:
 *
 * ```javascript
 * // Async tooltip with some custom settings
 * let grid = new Grid({
 *   features: {
 *     cellTooltip: {
 *       // Time that mouse needs to be over cell before tooltip is shown
 *       hoverDelay : 4000,
 *       // Time after mouse out to hide the tooltip, 0 = instantly
 *       hideDelay  : 0,
 *       // Async tooltip renderer, return a Promise which yields the text content
 *       tooltipRenderer({ record, tip }) => AjaxHelper.get(`tooltip.php?id=${record.id}`).then(response => response.text())
 *     }
 *   }
 * });
 * ```
 *
 * @extends Core/mixin/InstancePlugin
 *
 * @example
 * // Column with its own tooltip renderer
 * {
 *   text            : 'Name',
 *   field           : 'name',
 *   tooltipRenderer : ({ record }) => `My name is\xa0<b>${record.name}</b>`
 * }
 *
 * @demo Grid/celltooltip
 * @classtype cellTooltip
 * @externalexample feature/CellTooltip.js
 */

class CellTooltip extends InstancePlugin {
  //region Config
  static get $name() {
    return 'CellTooltip';
  }

  static get defaultConfig() {
    return {
      /**
       * Function called to generate the HTML content for the cell tooltip.
       * The function should return a string (your HTML), or a Promise yielding a string (for remotely loaded content)
       * @config {Function}
       * @param {Object} context
       * @param {HTMLElement} context.cellElement The cell element
       * @param {Core.data.Model} context.record The row record
       * @param {Grid.column.Column} context.column The column
       * @param {Core.widget.Tooltip} context.tip The Tooltip instance
       * @param {Grid.feature.CellTooltip} context.cellTooltip The feature
       * @param {Event} context.event The raw DOM event
       * @return {String|Promise}
       */
      tooltipRenderer: null
    };
  } //endregion
  // region Init

  construct(grid, config) {
    const me = this;
    super.construct(grid, me.processConfig(config));
  }

  initTip() {
    const me = this;
    me.tip = new Tooltip(Object.assign({
      forElement: me.client.element,
      forSelector: '.b-grid-row:not(.b-group-row) .b-grid-cell',
      hoverDelay: 1000,
      trackMouse: false,
      cls: 'b-celltooltip-tip',
      getHtml: me.getTooltipContent.bind(me),
      listeners: {
        pointerOver: 'onPointerOver',
        thisObj: me
      }
    }, me.initialConfig));
    me.relayEvents(me.tip, ['beforeshow', 'show']);
  }

  onPointerOver({
    target
  }) {
    const column = this.client.getColumnFromElement(target); // Veto onPointerOver if column's tooltipRenderer is false

    return column.tooltipRenderer !== false;
  } // CellTooltip feature handles special config cases, where user can supply a function to use as tooltipRenderer
  // instead of a normal config object

  processConfig(config) {
    if (typeof config === 'function') {
      return {
        tooltipRenderer: config
      };
    }

    return config;
  } // override setConfig to process config before applying it (used mainly from ReactGrid)

  setConfig(config) {
    super.setConfig(this.processConfig(config));
  }

  doDestroy() {
    this.tip && this.tip.destroy();
    super.doDestroy();
  }

  doDisable(disable) {
    if (!disable) {
      this.initTip();
    } else if (this.tip) {
      this.tip.destroy();
      this.tip = null;
    }

    super.doDisable(disable);
  } //endregion
  //region Content

  /**
   * Called from Tooltip to populate it with html.
   * @private
   */

  getTooltipContent({
    tip,
    activeTarget: cellElement,
    event
  }) {
    const me = this,
          record = me.client.getRecordFromElement(cellElement),
          column = me.client.getColumnFromElement(cellElement);
    let result; // If we have not changed context, we should not change content, unless we have a custom target selector (element within the cell)

    if (!me.forSelector && record === me.lastRecord && column === me.lastColumn) {
      return me.tip._html;
    }

    me.lastRecord = record;
    me.lastColumn = column; // first, use columns tooltipRenderer if any

    if (column.tooltipRenderer) {
      result = column.tooltipRenderer({
        cellElement,
        record,
        column,
        event,
        tip,
        cellTooltip: me
      });

      if (result === false) {
        VersionHelper.deprecate('Grid', '5.0.0', 'Returning false to indicate loading is deprecated. Please see https://bryntum.com/docs/grid/#guides/upgrades/3.0.1.md for more information');
      }
    } // secondly, try features renderer (but specifying column.tooltipRenderer as false prevents tooltip in that column)
    else if (me.tooltipRenderer && column.tooltipRenderer !== false) {
        result = me.tooltipRenderer({
          cellElement,
          record,
          column,
          event,
          tip,
          cellTooltip: me
        });

        if (result === false) {
          VersionHelper.deprecate('Grid', '5.0.0', 'Returning false to indicate loading is deprecated. Please see https://bryntum.com/docs/grid/#guides/upgrades/3.0.1.md for more information');
        }
      }

    return result;
  } //endregion

}
CellTooltip._$name = 'CellTooltip';
GridFeatureManager.registerFeature(CellTooltip);

const storeListenerName = 'store';
/**
 * @module Grid/feature/ColumnAutoWidth
 */

/**
 * Enables the {@link Grid.column.Column#config-autoWidth} config for a grid's columns.
 *
 * This feature is <strong>enabled</strong> by default.
 *
 * @extends Core/mixin/InstancePlugin
 * @mixes Core/mixin/Delayable
 *
 * @classtype columnAutoWidth
 */

class ColumnAutoWidth extends Delayable(InstancePlugin) {
  static get $name() {
    return 'ColumnAutoWidth';
  }

  construct(config) {
    var _this$client;

    super.construct(config);
    const store = (_this$client = this.client) === null || _this$client === void 0 ? void 0 : _this$client.store; // The initial bindStore can come super early such that our hooks won't catch it:

    if (store) {
      this.bindStore(store);
    }
  } //region Config

  static get defaultConfig() {
    return {
      /**
       * The default `autoWidth` option for columns with `autoWidth: true`. This can
       * be a single number for the minimum column width, or an array of two numbers
       * for the `[minWidth, maxWidth]`.
       * @config {Number|Number[]}
       */
      default: null,

      /**
       * The amount of time (in milliseconds) to delay after a store modification
       * before synchronizing `autoWidth` columns.
       * @config {Number}
       * @default
       */
      delay: 0
    };
  } //endregion
  //region Internals

  static get pluginConfig() {
    return {
      after: {
        bindStore: 'bindStore',
        unbindStore: 'unbindStore',
        renderRows: 'syncAutoWidthColumns'
      },
      assign: ['columnAutoWidthPending', 'syncAutoWidthColumns']
    };
  }

  doDestroy() {
    this.unbindStore();
    super.doDestroy();
  }

  bindStore(store) {
    this.lastSync = null;
    store.on({
      name: storeListenerName,
      [`change${this.client.asyncEventSuffix}`]: 'onStoreChange'
    }, this);
  }

  unbindStore() {
    this.detachListeners(storeListenerName);
  }

  get columnAutoWidthPending() {
    return this.lastSync === null || this.hasTimeout('syncAutoWidthColumns');
  }

  onStoreChange({
    action
  }) {
    if (action !== 'move') {
      ++this.storeGeneration;

      if (!this.hasTimeout('syncAutoWidthColumns')) {
        this.setTimeout('syncAutoWidthColumns', this.delay);
      }
    }
  }

  syncAutoWidthColumns() {
    const me = this,
          grid = me.client,
          storeGeneration = me.storeGeneration;

    if (me.lastSync !== storeGeneration) {
      me.lastSync = storeGeneration;
      let autoWidth, resizingColumns;

      for (const column of grid.columns.visibleColumns) {
        autoWidth = column.autoWidth;

        if (autoWidth) {
          if (autoWidth === true) {
            autoWidth = me.default;
          }

          grid.resizingColumns = resizingColumns = true;
          column.resizeToFitContent(autoWidth);
        }
      }

      if (resizingColumns) {
        grid.resizingColumns = false;
        grid.afterColumnsResized();
      }
    }

    if (me.hasTimeout('syncAutoWidthColumns')) {
      me.clearTimeout('syncAutoWidthColumns');
    }
  } //endregion

}
ColumnAutoWidth.prototype.storeGeneration = 0;
ColumnAutoWidth._$name = 'ColumnAutoWidth';
GridFeatureManager.registerFeature(ColumnAutoWidth, true);

/**
 * @module Grid/feature/ColumnDragToolbar
 */

/**
 * Displays a toolbar while dragging column headers. Drop on a button in the toolbar to activate a certain function,
 * for example to group by that column. This feature simplifies certain operations on touch devices.
 *
 * This feature is <strong>disabled</strong> by default, but turned on automatically on touch devices.
 *
 * @extends Core/mixin/InstancePlugin
 *
 * @classtype columnDragToolbar
 * @externalexample feature/ColumnDragToolbar.js
 * @demo Grid/columndragtoolbar
 */

class ColumnDragToolbar extends Delayable(InstancePlugin) {
  //region Config
  static get $name() {
    return 'ColumnDragToolbar';
  } // Plugin configuration. This plugin chains some of the functions in Grid

  static get pluginConfig() {
    return {
      after: ['render']
    };
  } //endregion
  //region Init

  construct(grid, config) {
    if (grid.features.columnReorder) {
      grid.features.columnReorder.on('beforedestroy', this.onColumnReorderBeforeDestroy, this);
    }

    this.grid = grid;
    super.construct(grid, config);
  }

  doDestroy() {
    const me = this;

    if (me.grid.features.columnReorder && !me.grid.features.columnReorder.isDestroyed) {
      me.detachFromColumnReorder();
    }

    me.element && me.element.remove();
    me.element = null;
    super.doDestroy();
  }

  doDisable(disable) {
    if (this.initialized) {
      if (disable) {
        this.detachFromColumnReorder();
      } else {
        this.init();
      }
    }

    super.doDisable(disable);
  }

  init() {
    const me = this,
          grid = me.grid;

    if (!grid.features.columnReorder) {
      return;
    }

    me.reorderDetacher = grid.features.columnReorder.on({
      gridheaderdragstart({
        context
      }) {
        const column = grid.columns.getById(context.element.dataset.columnId);
        me.showToolbar(column);
      },

      gridheaderdrag: ({
        context
      }) => me.onDrag(context),
      gridheaderabort: () => {
        me.hideToolbar();
      },
      gridheaderdrop: ({
        context
      }) => {
        if (context.valid) {
          me.hideToolbar();
        } else {
          me.onDrop(context);
        }
      },
      thisObj: me
    });
    me.initialized = true;
  }

  onColumnReorderBeforeDestroy() {
    this.detachFromColumnReorder();
  }

  detachFromColumnReorder() {
    const me = this;
    me.grid.features.columnReorder.un('beforedestroy', me.onColumnReorderBeforeDestroy, me);
    me.reorderDetacher && me.reorderDetacher();
    me.reorderDetacher = null;
  }
  /**
   * Initializes this feature on grid render.
   * @private
   */

  render() {
    if (!this.initialized) {
      this.init();
    }
  } //endregion
  //region Toolbar

  showToolbar(column) {
    const me = this,
          buttons = me.grid.getColumnDragToolbarItems(column, []),
          groups = [];
    me.clearTimeout(me.buttonHideTimer);
    me.clearTimeout(me.toolbarHideTimer);
    buttons.forEach(button => {
      button.text = button.localeClass.L(button.text);
      let group = groups.find(group => group.text === button.group);

      if (!group) {
        group = {
          text: button.localeClass.L(button.group),
          buttons: []
        };
        groups.push(group);
      }

      group.buttons.push(button);
    });
    me.element = DomHelper.append(me.grid.element, me.template(groups));
    me.groups = groups;
    me.buttons = buttons;
    me.column = column;
  }

  hideToolbar() {
    const me = this;
    return new Promise(resolve => {
      if (me.element && !me.toolbarHideTimer) {
        me.element.classList.add('b-remove'); // TODO: use AnimationHelper when available

        me.toolbarHideTimer = me.setTimeout(() => {
          me.toolbarHideTimer = null;
          me.element && me.element.remove();
          me.element = null;
          resolve();
        }, 200);
      }
    });
  } //endregion
  //region Events

  onDrag(info) {
    const me = this;

    if (info.dragProxy.getBoundingClientRect().top - me.grid.element.getBoundingClientRect().top > 100) {
      me.element.classList.add('b-closer');
    } else {
      me.element.classList.remove('b-closer');
    }

    if (me.hoveringButton) {
      me.hoveringButton.classList.remove('b-hover');
      me.hoveringButton = null;
    }

    if (info.targetElement && info.targetElement.closest('.b-columndragtoolbar')) {
      me.element.classList.add('b-hover');
      const button = info.targetElement.closest('.b-columndragtoolbar  .b-target-button:not([data-disabled=true])');

      if (button) {
        button.classList.add('b-hover');
        me.hoveringButton = button;
      }
    } else {
      me.element.classList.remove('b-hover');
    }
  }

  onDrop(info) {
    const me = this;

    if (info.targetElement && info.targetElement.matches('.b-columndragtoolbar .b-target-button:not([data-disabled=true])')) {
      const buttonEl = info.targetElement,
            button = me.buttons.find(button => button.name === buttonEl.dataset.name);

      if (button) {
        buttonEl.classList.add('b-activate');
        me.buttonHideTimer = me.setTimeout(() => {
          me.hideToolbar();
          button.onDrop({
            column: me.column
          });
        }, 100);
      }
    } else {
      me.hideToolbar();
    }
  } //endregion

  template(groups) {
    return TemplateHelper.tpl`
            <div class="b-columndragtoolbar">     
            <div class="b-title"></div>          
            ${groups.map(group => TemplateHelper.tpl`
                <div class="b-group">
                    <div class="b-buttons">
                    ${group.buttons.map(btn => TemplateHelper.tpl`
                        <div class="b-target-button" data-name="${btn.name}" data-disabled="${btn.disabled}">
                            <i class="${btn.icon}"></i>
                            ${btn.text}
                        </div>
                    `)}
                    </div>
                    <div class="b-title">${group.text}</div>
                </div>
            `)}
            </div>`;
  }

}
ColumnDragToolbar.featureClass = 'b-hascolumndragtoolbar'; // used by default on touch devices, can be enabled otherwise

ColumnDragToolbar._$name = 'ColumnDragToolbar';
GridFeatureManager.registerFeature(ColumnDragToolbar, BrowserHelper.isTouchDevice);

/**
 * @module Grid/feature/ColumnPicker
 */

/**
 * Displays a column picker (to show/hide columns) in the header context menu. Columns can be displayed in sub menus
 * by region or tag. Grouped headers are displayed as menu hierarchies.
 *
 * This feature is <strong>enabled</strong> by default.
 *
 * @extends Core/mixin/InstancePlugin
 *
 * @demo Grid/columns
 * @classtype columnPicker
 * @externalexample feature/ColumnPicker.js
 */

class ColumnPicker extends InstancePlugin {
  //region Config
  static get $name() {
    return 'ColumnPicker';
  }

  static get defaultConfig() {
    return {
      /**
       * Groups columns in the picker by region (each region gets its own sub menu)
       * @config {Boolean}
       * @default
       */
      groupByRegion: false,

      /**
       * Groups columns in the picker by tag, each column may be shown under multiple tags. See
       * {@link Grid.column.Column#config-tags}
       * @config {Boolean}
       * @default
       */
      groupByTag: false,
      menuCls: 'b-column-picker-menu b-sub-menu'
    };
  } // Plugin configuration. This plugin chains some of the functions in Grid.

  static get pluginConfig() {
    return {
      chain: ['populateHeaderMenu', 'getColumnDragToolbarItems']
    };
  } //endregion
  //region Init

  construct(grid, config) {
    this.grid = grid;
    super.construct(grid, config);
  } //endregion
  //region Context menu

  /**
   * Get menu items, either a straight list of columns or sub menus per subgrid
   * @private
   * @param columnStore Column store to traverse
   * @returns {Object[]} Menu item configs
   */

  getColumnPickerItems(columnStore) {
    const me = this;

    if (me.groupByRegion) {
      // submenus for grids regions
      return me.grid.regions.map(region => {
        const columns = me.grid.getSubGrid(region).columns.topColumns;
        return {
          text: StringHelper.capitalize(region),
          menu: me.buildColumnMenu(columns),
          disabled: columns.length === 0,
          region: region
        };
      });
    } else if (me.groupByTag) {
      // submenus for column tags
      const tags = {};
      columnStore.topColumns.forEach(column => {
        column.tags && column.hideable && column.tags.forEach(tag => {
          if (!tags[tag]) {
            tags[tag] = 1;
          }
        });
      }); // TODO: as checkitems, but how to handle toggling? hide a column only when all tags for it are unchecked?

      return Object.keys(tags).sort().map(tag => ({
        text: StringHelper.capitalize(tag),
        menu: me.buildColumnMenu(me.getColumnsForTag(tag)),
        tag: tag,
        onBeforeSubMenu: ({
          item,
          itemEl
        }) => {
          me.refreshTagMenu(item, itemEl);
        }
      }));
    } else {
      // all columns in same menu
      return me.buildColumnMenu(columnStore.topColumns);
    }
  }
  /**
   * Get all columns that has the specified tag
   * TODO: if tags are useful from somewhere else, move to ColumnStore
   * @private
   * @param tag
   * @returns {Grid.column.Column[]}
   */

  getColumnsForTag(tag) {
    // TODO: if tags are usefull from somewhere else, move to ColumnStore
    return this.grid.columns.records.filter(column => column.tags && column.tags.includes(tag) && column.hideable !== false);
  }
  /**
   * Refreshes checked status for a tag menu. Needed since columns can appear under multiple tags.
   * @private
   */

  refreshTagMenu(item, itemEl) {
    const columns = this.getColumnsForTag(item.tag);
    columns.forEach(column => {
      const subItem = item.items.find(subItem => subItem.column === column);
      if (subItem) subItem.checked = column.hidden !== true;
    });
  }
  /**
   * Traverses columns to build menu items for the column picker.
   * @private
   */

  buildColumnMenu(columns) {
    let currentRegion = columns.length > 0 && columns[0].region;
    const {
      grid
    } = this,
          items = columns.reduce((items, column) => {
      const visibleInRegion = grid.columns.visibleColumns.filter(col => col.region === column.region);

      if (column.hideable !== false) {
        const itemConfig = {
          grid,
          text: column.text,
          column: column,
          name: column.id,
          checked: column.hidden !== true,
          disabled: column.hidden !== true && visibleInRegion.length === 1,
          cls: column.region !== currentRegion ? 'b-separator' : ''
        };
        currentRegion = column.region;

        if (column.children) {
          itemConfig.menu = this.buildColumnMenu(column.children);
        }

        items.push(itemConfig);
      }

      return items;
    }, []);
    return {
      cls: this.menuCls,
      items
    };
  }
  /**
   * Populates the header context menu items.
   * @param {Object} options Contains menu items and extra data retrieved from the menu target.
   * @param {Grid.column.Column} options.column Column for which the menu will be shown
   * @param {Object} options.items A named object to describe menu items
   * @internal
   */

  populateHeaderMenu({
    column,
    items
  }) {
    const me = this,
          {
      columns
    } = me.grid;

    if (column.showColumnPicker !== false && columns.some(col => col.hideable)) {
      // column picker
      items.columnPicker = {
        text: 'L{columnsMenu}',
        localeClass: me,
        name: 'columnPicker',
        icon: 'b-fw-icon b-icon-columns',
        cls: 'b-separator',
        weight: 200,
        menu: me.getColumnPickerItems(columns),
        onToggle: me.onColumnToggle,
        disabled: me.disabled
      };
    } // menu item for hiding this column

    if (column.hideable !== false) {
      const visibleInRegion = columns.visibleColumns.filter(col => col.region === column.region);
      items.hideColumn = {
        text: 'L{hideColumn}',
        localeClass: me,
        icon: 'b-fw-icon b-icon-hide-column',
        weight: 210,
        name: 'hideColumn',
        disabled: visibleInRegion.length === 1 || me.disabled,
        onItem: () => column.hide()
      };
    }
  }
  /**
   * Handler for column hide/show menu checkitems.
   * @private
   * @param {Object} event The {@link Core.widget.MenuItem#event-toggle} event.
   */

  onColumnToggle({
    menu,
    item,
    checked
  }) {
    if (!!item.column.hidden !== !checked) {
      item.column[checked ? 'show' : 'hide']();
      const {
        grid,
        column
      } = item,
            {
        columns,
        features
      } = grid,
            // Sibling items, needed to disable other item if it is the last one in region
      siblingItems = menu.items,
            // Columns left visible in same region as this items column
      visibleInRegion = columns.visibleColumns.filter(col => col.region === item.column.region),
            // Needed to access "hide-column" item outside of column picker
      // TODO: 'contextMenu' is deprecated. Please see https://bryntum.com/docs/grid/#guides/upgrades/4.0.0.md for more information.
      {
        contextMenu,
        headerMenu
      } = features,
            isOldFeatureUsed = contextMenu && !contextMenu.disabled,
            contextMenuFeature = isOldFeatureUsed ? contextMenu : headerMenu,
            mainMenu = contextMenuFeature && contextMenuFeature[isOldFeatureUsed ? 'currentMenu' : 'menu'],
            hideItem = isOldFeatureUsed ? mainMenu.items.find(item => item.name === 'hideColumn') : mainMenu.widgetMap.hideColumn; // Do not allow user to hide the last column in any region

      if (visibleInRegion.length === 1) {
        const lastVisibleItem = siblingItems.find(i => i.name === visibleInRegion[0].id);

        if (lastVisibleItem) {
          lastVisibleItem.disabled = true;
        } // Also disable "Hide column" item if only one column left in this region

        if (hideItem && column.region === item.column.region) {
          hideItem.disabled = true;
        }
      } // Multiple columns visible, enable "hide-column" and all items for that region
      else {
          visibleInRegion.forEach(col => {
            const siblingItem = siblingItems.find(sibling => sibling.column === col);

            if (siblingItem) {
              siblingItem.disabled = false;
            }
          });

          if (hideItem && column.region === item.column.region) {
            hideItem.disabled = false;
          }
        }

      if (item.menu) {
        // Reflect status in submenu.
        // Cannot use short form () => foo because eachWidget aborts on return of false
        item.menu.eachWidget(subItem => {
          subItem.checked = checked;
        });
      }

      const parentItem = menu.owner;

      if (parentItem && parentItem.column === column.parent) {
        parentItem.checked = siblingItems.some(subItem => subItem.checked === true);
      }
    }
  }
  /**
   * Supply items to ColumnDragToolbar
   * @private
   */

  getColumnDragToolbarItems(column, items) {
    const visibleInRegion = this.grid.columns.visibleColumns.filter(col => col.region === column.region);

    if (column.hideable !== false && visibleInRegion.length > 1) {
      items.push({
        text: 'L{hideColumnShort}',
        group: 'L{column}',
        localeClass: this,
        icon: 'b-fw-icon b-icon-hide-column',
        weight: 101,
        name: 'hideColumn',
        onDrop: ({
          column
        }) => column.hide()
      });
    }

    return items;
  } //endregion

}
ColumnPicker._$name = 'ColumnPicker';
GridFeatureManager.registerFeature(ColumnPicker, true);

/**
 * @module Grid/feature/ColumnReorder
 */

/**
 * Allows user to reorder columns by dragging headers. To get notified about column reorder listen to `change` event
 * on {@link Grid.data.ColumnStore columns} store.
 *
 * This feature is <strong>enabled</strong> by default.
 *
 * @extends Core/mixin/InstancePlugin
 *
 * @demo Grid/columns
 * @classtype columnReorder
 * @externalexample feature/ColumnReorder.js
 */

class ColumnReorder extends Delayable(InstancePlugin) {
  //region Init
  static get $name() {
    return 'ColumnReorder';
  }

  construct(grid, config) {
    this.ignoreSelectors = ['.b-grid-header-resize-handle', '.b-field'];
    this.grid = grid;
    super.construct(grid, config);
  }

  doDestroy() {
    this.dragHelper && this.dragHelper.destroy();
    super.doDestroy();
  }
  /**
   * Initialize drag & drop (called from render)
   * @private
   */

  init() {
    const me = this,
          {
      grid
    } = me,
          gridEl = grid.element,
          containers = DomHelper.children(gridEl, '.b-grid-headers');
    containers.push(...DomHelper.children(gridEl, '.b-grid-header-children'));

    if (me.dragHelper) {
      // update the dragHelper with the new set of containers it should operate upon
      me.dragHelper.containers = containers;
    } else {
      me.dragHelper = new DragHelper({
        name: 'columnReorder',
        mode: 'container',
        dragThreshold: 10,
        targetSelector: '.b-grid-header',
        outerElement: gridEl.querySelector('header.b-grid-header-container'),
        containers,

        isElementDraggable(element) {
          const abort = Boolean(DomHelper.up(element, me.ignoreSelectors.join(',')));

          if (abort || me.disabled) {
            return false;
          }

          const columnEl = DomHelper.up(element, this.targetSelector),
                column = columnEl && grid.columns.getById(columnEl.dataset.columnId),
                isLast = column && column.childLevel === 0 && grid.subGrids[column.region].columns.count === 1; // TODO: If we want to prevent dragging last column out of group we can use the code below...

          /*isLast = column.level !== 0
                  // In grouped header, do not allow dragging last remaining child
                  ? column.parent.children.length === 1
                  // Not in a grouped header, do not allow dragging last remaining column
                  : grid.subGrids[column.region].columns.count === 1;*/

          return Boolean(column) && column.draggable !== false && !isLast;
        },

        ignoreSelector: '.b-filter-icon,.b-grid-header-resize-handle',
        listeners: {
          dragstart: me.onDragStart,
          drag: me.onDrag,
          drop: me.onDrop,
          thisObj: me
        }
      });
      me.relayEvents(me.dragHelper, ['dragStart', 'drag', 'drop', 'abort'], 'gridHeader');
    }
  } //endregion
  //region Plugin config
  // Plugin configuration. This plugin chains some of the functions in Grid

  static get pluginConfig() {
    return {
      after: ['render', 'renderContents']
    };
  } //endregion
  //region Events (drop)

  onDrag({
    context,
    event
  }) {
    const me = this,
          targetHeader = Widget.fromElement(event.target, 'gridheader'); // If SubGrid is configured with a sealed column set, do not allow moving into it

    if (targetHeader && targetHeader.subGrid.sealedColumns) {
      context.valid = false;
      return;
    } // Require that we drag inside grid header while dragging if we don't have a drag toolbar

    if (!me.grid.features.columnDragToolbar) {
      context.valid = Boolean(event.target.closest('.b-grid-headers'));
    }
  }

  onDragStart() {
    const me = this;

    if (!me.grid.features.columnDragToolbar) {
      const headerContainerBox = me.grid.element.querySelector('.b-grid-header-container').getBoundingClientRect();
      me.dragHelper.minY = headerContainerBox.top;
      me.dragHelper.maxY = headerContainerBox.bottom;
    }

    this.grid.headerContainer.classList.add('b-dragging-header');
  }
  /**
   * Handle drop
   * @private
   */

  onDrop({
    context
  }) {
    if (!context.valid) {
      return this.onInvalidDrop({
        context
      });
    }

    const me = this,
          grid = me.grid,
          element = context.dragging,
          onHeader = DomHelper.up(context.target, '.b-grid-header'),
          onColumn = grid.columns.get(onHeader.dataset.column),
          toRegion = context.draggedTo.dataset.region || onColumn.region,
          sibling = context.insertBefore,
          column = grid.columns.getById(element.dataset.columnId),
          insertBefore = sibling ? grid.columns.getById(sibling.dataset.columnId) : grid.subGrids[toRegion].columns.last.nextSibling,
          newParent = insertBefore ? insertBefore.parent : grid.columns.rootNode;
    grid.headerContainer.classList.remove('b-dragging-header'); // Dropped into its current position in the same SubGrid - abort

    if (toRegion === column.region && (onColumn === column.previousSibling || insertBefore === column.nextSibling)) {
      me.dragHelper.abort();
      return;
    } // Check if we should remove last child

    const emptyParent = column.parent && column.parent.children.length === 1 && column.parent;

    if (emptyParent) {
      emptyParent.parent.removeChild(emptyParent);
    } // Clean up element used during drag drop as it will not be removed by Grid when it refreshes its header elements

    element.remove();
    column.region = toRegion; // Insert the column into its new place

    newParent.insertChild(column, insertBefore);
  }
  /**
   * Handle invalid drop
   * @private
   */

  onInvalidDrop() {
    this.grid.headerContainer.classList.remove('b-dragging-header');
  } //endregion
  //region Render

  /**
   * Updates DragHelper with updated headers when grid contents is rerendered
   * @private
   */

  renderContents() {
    // columns shown, hidden or reordered
    this.init();
  }
  /**
   * Initializes this feature on grid render.
   * @private
   */

  render() {
    // always reinit on render
    this.init();
  } //endregion

}
ColumnReorder.featureClass = 'b-column-reorder';
ColumnReorder._$name = 'ColumnReorder';
GridFeatureManager.registerFeature(ColumnReorder, true);

/**
 * @module Grid/feature/ColumnResize
 */

/**
 * Enables user to resize columns by dragging a handle on the righthand side of the header. To get notified about column
 * resize listen to `change` event on {@link Grid.data.ColumnStore columns} store.
 *
 * This feature is <strong>enabled</strong> by default.
 *
 * @extends Core/mixin/InstancePlugin
 *
 * @demo Grid/columns
 * @classtype columnResize
 * @externalexample feature/ColumnResize.js
 */

class ColumnResize extends InstancePlugin {
  static get $name() {
    return 'ColumnResize';
  }

  static get defaultConfig() {
    return {
      /**
       * Resize all cells below a resizing header during dragging.
       * `'auto'` means `true` on non-mobile platforms.
       * @config {String|Boolean}
       * @default
       */
      liveResize: 'auto'
    };
  } //region Init

  construct(grid, config) {
    const me = this;
    me.grid = grid;
    super.construct(grid, config);
    me.resizer = new ResizeHelper({
      name: 'columnResize',
      targetSelector: '.b-grid-header',
      handleSelector: '.b-grid-header-resize-handle',
      outerElement: grid.element,
      listeners: {
        beforeresizestart: me.onBeforeResizeStart,
        resizestart: me.onResizeStart,
        resizing: me.onResizing,
        resize: me.onResize,
        thisObj: me
      }
    });
  } //endregion
  // This plugin needs no functions chaining into the client grid

  static get pluginConfig() {
    return [];
  }

  set liveResize(liveResize) {
    if (liveResize === 'auto') {
      liveResize = !BrowserHelper.isMobileSafari;
    }

    this._liveResize = liveResize;
  }

  get liveResize() {
    return this._liveResize;
  }

  doDestroy() {
    this.resizer && this.resizer.destroy();
    super.doDestroy();
  } //region Events

  onBeforeResizeStart() {
    return !this.disabled;
  }

  onResizeStart({
    context
  }) {
    const {
      grid,
      resizer
    } = this,
          column = context.column = grid.columns.getById(context.element.dataset.columnId);
    resizer.minWidth = column.minWidth; // remove minWidth value as it's used as a rendering workaround for IE flexbox bugs

    context.element.style.minWidth = '';
    grid.element.classList.add('b-column-resizing');
  }
  /**
   * Handle drag event - resize the column live unless it's a touch gesture
   * @private
   */

  onResizing({
    context
  }) {
    if (context.valid && this.liveResize) {
      this.grid.resizingColumns = true;
      context.column.width = context.newWidth;
    }
  }
  /**
   * Handle drop event (only used for touch)
   * @private
   */

  onResize({
    context
  }) {
    const {
      grid
    } = this,
          {
      column
    } = context;
    grid.element.classList.remove('b-column-resizing');

    if (context.valid) {
      if (this.liveResize) {
        grid.resizingColumns = false;
        grid.afterColumnsResized();
      } else {
        column.width = context.newWidth;
      } // In case of IE 11 we should calculate flex basis to fix header width
      // covered by ColumnResize.t

      if (BrowserHelper.isIE11 && column.parent && column.parent.flex) {
        const parent = column.parent,
              headerEl = grid.getHeaderElement(parent.id);
        headerEl.style.flexBasis = parent.children.reduce((result, column) => {
          return result + grid.getHeaderElement(column.id).offsetWidth;
        }, 0) + 'px';
      }
    }
  } //endregion

}
ColumnResize._$name = 'ColumnResize';
GridFeatureManager.registerFeature(ColumnResize, true);

/**
 * @module Grid/feature/ContextMenu
 */

/**
 * **DEPRECATED: This class is deprecated since v4.0.0,
 * use {@link Grid.feature.CellMenu CellMenu} feature for cell context menu and
 * {@link Grid.feature.HeaderMenu HeaderMenu} feature for header context menu instead.**
 *
 * Right click to display context menu for headers and cells.
 *
 * This feature is **off** by default in favor of `CellMenu` and `HeaderMenu`.
 * For info on enabling it, see {@link Grid.view.mixin.GridFeatures}.
 *
 * @extends Core/mixin/InstancePlugin
 * @classtype contextMenu
 * @deprecated 4.0.0
 */

class ContextMenu extends InstancePlugin {
  //region Config
  static get $name() {
    return 'ContextMenu';
  }

  static get defaultConfig() {
    return {
      /**
       * Extra items to add to the header context menu. See {@link Core.widget.MenuItem} for more info.
       *
       * ```javascript
       * features : {
       *     contextMenu : {
       *         headerItems : [
       *             { text : 'Header item', onItem : () => ... }
       *         ]
       *     }
       * }
       * ```
       *
       * @config {Object[]}
       */
      headerItems: [],

      /**
       * A function called before displaying the header menu that allows manipulations of its items. Called with a
       * single parameter with format { column, items }. Returning `false` from this function prevents
       * the menu from being shown.
       *
       * ```javascript
       * features : {
       *     contextMenu : {
       *         processHeaderItems({record, items}) {
       *             // Add or remove items here as needed
       *             if (column.field === 'age') {
       *                 items.push({ text: 'Hide youngsters', icon : 'b-fa b-fa-fw b-fa-baby' })
       *             }
       *         }
       *     }
       * }
       * ```
       *
       * @config {Function}
       */
      processHeaderItems: null,

      /**
       * Extra items to add to the cell context menu. See {@link Core.widget.MenuItem} for more info.
       *
       * ```javascript
       * features : {
       *     contextMenu : {
       *         cellItems : [
       *             { text : 'Cell item', onItem : () => ... }
       *         ]
       *     }
       * }
       * ```
       *
       * @config {Object[]}
       */
      cellItems: [],

      /**
       * A function called before displaying the cell menu that allows manipulations of its items. Called with a
       * single parameter with format { record, items }. Returning `false` from this function prevents
       * the menu from being shown.
       *
       * ```javascript
       * features : {
       *     contextMenu : {
       *         processCellItems({record, items}) {
       *             // Add or remove items here as needed
       *             if (record.age > 50) {
       *                 items.push({ text: 'Add extra vacation', icon : 'b-fa b-fa-fw b-fa-umbrella-beach' })
       *             }
       *         }
       *     }
       * }
       * ```
       *
       * @config {Function}
       */
      processCellItems: null,

      /**
       * Event which is used to show context menu.
       * Available options are: 'contextmenu', 'click', 'dblclick'.
       * Default value is used from {@link Grid/view/Grid#config-contextMenuTriggerEvent}
       * @config {String}
       */
      triggerEvent: null,
      // Private config to disable context menu for cells, raised when
      // taskContextMenu is enabled
      disableCellContextMenu: null
    };
  } //endregion
  //region Events

  /**
   * Fired from grid before the context menu is shown for a header. Allows manipulation of the items
   * to show in the same way as in `processHeaderItems`. Returning false from a listener prevents the
   * menu from being shown.
   * @event headerContextMenuBeforeShow
   * @deprecated 4.0.0 Renamed to headerMenuBeforeShow
   * @preventable
   * @param {Grid.view.Grid} source
   * @param {Object} items Menu item configs
   * @param {Grid.column.Column} column Column
   */

  /**
   * Fired from grid after showing the context menu for a header
   * @event headerContextMenuShow
   * @deprecated 4.0.0 Renamed to headerMenuShow
   * @preventable
   * @param {Grid.view.Grid} source
   * @param {Core.widget.Menu} menu The menu
   * @param {Grid.column.Column} column Column
   */

  /**
   * Fired from grid before the context menu is shown for a cell. Allows manipulation of the items
   * to show in the same way as in `processCellItems`. Returning false from a listener prevents the
   * menu from being shown.
   * @event cellContextMenuBeforeShow
   * @deprecated 4.0.0 Renamed to cellMenuBeforeShow
   * @preventable
   * @param {Grid.view.Grid} source
   * @param {Object} items Menu item configs
   * @param {Grid.column.Column} column Column
   * @param {Core.data.Model} record Record
   */

  /**
   * Fired from grid after showing the context menu for a cell
   * @event cellContextMenuShow
   * @deprecated 4.0.0 Renamed to cellMenuShow
   * @preventable
   * @param {Grid.view.Grid} source
   * @param {Core.widget.Menu} menu The menu
   * @param {Grid.column.Column} column Column
   * @param {Core.data.Model} record Record
   */

  /**
   * Fired when an item is selected in the context menu.
   * @event contextMenuItem
   * @deprecated 4.0.0 Renamed to cellMenuItem and headerMenuItem
   * @param {Grid.view.Grid} grid The grid
   * @param {Object} item Selected menu item
   * @param {Grid.column.Column} column Column
   * @param {HTMLElement} itemEl Menu item element
   */

  /**
   * Fired when a check item is toggled in the context menu.
   * @event contextMenuToggleItem
   * @deprecated 4.0.0 Renamed to cellMenuToggleItem and headerMenuToggleItem
   * @param {Grid.view.Grid} grid The grid
   * @param {Object} item Selected menu item
   * @param {Grid.column.Column} column Column
   * @param {Boolean} checked Checked or not
   * @param {HTMLElement} itemEl Menu item element
   */
  //endregion
  //region Init

  construct(grid, config) {
    super.construct(grid, config);
    this.grid = grid;
    VersionHelper.deprecate('Grid', '5.0.0', '`ContextMenu` feature is deprecated, in favor of `CellMenu` and `HeaderMenu` features. Please see https://bryntum.com/docs/grid/#guides/upgrades/3.1.0.md for more information.'); // If this feature is enabled, need to disable CellMenu and HeaderMenu. They are mutually exclusive.

    if (grid.features.cellMenu) {
      grid.features.cellMenu.disabled = true;
    }

    if (grid.features.headerMenu) {
      grid.features.headerMenu.disabled = true;
    }
  }

  doDestroy() {
    if (this.currentMenu) {
      this.currentMenu.destroy();
    }

    super.doDestroy();
  } //endregion
  //region Plugin config
  // Plugin configuration. This plugin chains some of the functions in Grid.
  // The contextmenu event is emulated from a taphold gesture on touch platforms.

  static get pluginConfig() {
    return {
      assign: ['showContextMenu'],
      chain: ['onElementContextMenu', 'onElementClick', 'onElementDblClick', 'onElementKeyDown']
    };
  } //endregion
  //region Events

  onElementContextMenu(event) {
    this.triggerEvent === 'contextmenu' && this.showContextMenu(event);
  }

  onElementClick(event) {
    this.triggerEvent === 'click' && this.showContextMenu(event);
  }

  onElementDblClick(event) {
    this.triggerEvent === 'dblclick' && this.showContextMenu(event);
  }

  onElementKeyDown(event) {
    if (!event.handled && event.target.matches('.b-grid-header.b-depth-0')) {
      switch (event.key) {
        case ' ':
        case 'ArrowDown':
          this.showContextMenu(event);
          break;
      }
    }
  }
  /**
   * Show context menu.
   * @param event
   * @private
   */

  showContextMenu(event) {
    if (!this.disabled) {
      const header = DomHelper.up(event.target, '.b-grid-header'),
            cellData = this.grid.getCellDataFromEvent(event);

      if (header) {
        this.handleHeaderContextMenu(header, event);
      } else if (cellData && !this.disableCellContextMenu) {
        this.handleCellContextMenu(cellData, event);
      }
    }
  }

  handleHeaderContextMenu(header, event) {
    if (header.dataset.column) {
      const me = this,
            grid = me.grid,
            column = grid.columns.getById(header.dataset.columnId);

      if (column.enableHeaderContextMenu !== false) {
        const arrayOfItems = [...me.headerItems, ...(column.headerMenuItems || [])],
              namedItems = ObjectHelper.transformArrayToNamedObject(arrayOfItems),
              menu = {
          owner: me.client,
          constrainTo: document,
          cls: 'b-context-menu',
          scrollAction: 'hide',

          onItem({
            item,
            element
          }) {
            grid.trigger('contextMenuItem', {
              source: grid,
              item,
              column,
              element
            });
          },

          onToggle({
            item,
            checked,
            element
          }) {
            grid.trigger('contextMenuToggleItem', {
              source: grid,
              item,
              column,
              checked,
              element
            });
          },

          onDestroy() {
            // If menu is destroyed by WidgetHelper, make sure we don't keep a reference to it anymore
            me.currentMenu = null;
          },

          listeners: {
            show({
              source: menu
            }) {
              eventParams.menu = menu;
              grid.trigger('headerContextMenuShow', eventParams);
            }

          }
        }; // `populateHeaderMenu` is chained by mixins, thus each feature can supply items

        grid.populateHeaderMenu({
          column,
          items: namedItems,
          menu
        });
        const items = ObjectHelper.transformNamedObjectToArray(namedItems),
              {
          processHeaderItems
        } = me,
              eventParams = {
          items,
          column,
          event,
          element: header
        };
        ObjectHelper.merge(menu, {
          items
        });

        if ((!processHeaderItems || processHeaderItems(eventParams) !== false) && items.length > 0) {
          event.preventDefault();
          items.sort((a, b) => (a.weight || 150) - (b.weight || 150)); // Trigger event that allows preventing menu or manipulating its items

          if (grid.trigger('headerContextMenuBeforeShow', eventParams) !== false) {
            // Align to header element when using arrow down key
            me.currentMenu = WidgetHelper.showContextMenu(event.type === 'keydown' ? header : [event.clientX + 1, event.clientY + 1], menu); // Propagate the operating column down to all MenuItem levels
            // Will not override if they are preconfigured with a column.

            me.currentMenu.eachWidget(item => {
              if (!item.column && item instanceof MenuItem) {
                item.column = column;
              }
            }, true);
          }
        }
      }
    }
  }

  handleCellContextMenu(cellData, event) {
    const me = this,
          grid = me.grid,
          column = grid.columns.getById(cellData.columnId);

    if (column.enableCellContextMenu !== false) {
      // Process the gesture as navigation so that the use may select/multiselect
      // the items to include in their context menu operation.
      // Also select if not already selected.
      grid.focusCell(cellData.cellSelector, {
        doSelect: !grid.isSelected(cellData.id),
        event
      });
      const record = cellData.record,
            arrayOfItems = [...(me.cellItems || []), ...(column.cellMenuItems || [])],
            namedItems = ObjectHelper.transformArrayToNamedObject(arrayOfItems); // `populateCellMenu` is chained by mixins, thus each feature can supply items

      grid.populateCellMenu({
        column,
        record,
        items: namedItems
      });
      const items = ObjectHelper.transformNamedObjectToArray(namedItems),
            {
        processCellItems
      } = me,
            cell = cellData.cellElement,
            eventParams = {
        items,
        column,
        event,
        record,
        element: cell
      },
            menu = {
        owner: me.client,
        items: items,
        // Load up the item event with the contextual info
        onBeforeItem: itemEvent => {
          Object.assign(itemEvent, eventParams);
        },

        onItem({
          item
        }) {
          grid.trigger('contextMenuItem', {
            source: grid,
            item,
            column,
            record,
            cell
          });
        },

        onClose({
          reason
        }) {
          // return focus to grid when context menu is closed, if not cause by clicking outside of grid
          if (reason !== 'outside') {
            grid.focus();
          }
        },

        onDestroy() {
          // If menu is destroyed by WidgetHelper, make sure we don't keep a reference to it anymore
          me.currentMenu = null;
        },

        listeners: {
          show({
            source: menu
          }) {
            eventParams.menu = menu;
            grid.trigger('cellContextMenuShow', eventParams);
          }

        }
      };

      if ((!processCellItems || processCellItems(eventParams) !== false) && items.length > 0) {
        items.forEach(item => {
          item.column = column;
          item.record = record;
        });
        event.preventDefault();
        items.sort((a, b) => (a.weight || 150) - (b.weight || 150)); // Trigger event that allows preventing menu or manipulating its items

        if (grid.trigger('cellContextMenuBeforeShow', eventParams) !== false) {
          me.currentMenu = WidgetHelper.showContextMenu([event.clientX + 1, event.clientY + 1], menu);
        }
      }
    }
  }
  /**
   * Hides the context menu
   * @internal
   */

  hideContextMenu(animate) {
    this.currentMenu && this.currentMenu.hide(animate);
  } //endregion
  //region Getters/Setters

  get triggerEvent() {
    return this._triggerEvent || this.client.contextMenuTriggerEvent;
  }

  set triggerEvent(value) {
    this._triggerEvent = value;
  } //endregion

}
ContextMenu.featureClass = '';
ContextMenu._$name = 'ContextMenu';
GridFeatureManager.registerFeature(ContextMenu);

//TODO: Format value in header filter tooltip (see date)
/**
 * @module Grid/feature/Filter
 */

const fieldTypeMap = {
  date: 'date',
  integer: 'number',
  number: 'number',
  string: 'text',
  duration: 'duration'
};
/**
 * Feature that allows filtering of the grid by settings filters on columns. The actual filtering is done by the store.
 * For info on programmatically handling filters, see {@link Core.data.mixin.StoreFilter StoreFilter}.
 *
 * {@inlineexample feature/Filter.js}
 *
 * ```javascript
 * // Filtering turned on but no default filter
 * const grid = new Grid({
 *   features : {
 *     filter : true
 *   }
 * });
 *
 * // Using default filter
 * const grid = new Grid({
 *   features : {
 *     filter : { property : 'city', value : 'Gavle' }
 *   }
 * });
 * ```
 *
 * A column can supply a custom filtering function as its {@link Grid.column.Column#config-filterable} config. When
 * filtering by that column using the UI that function will be used to determine which records to include. See
 * {@link Grid.column.Column#config-filterable Column#filterable} for more information.
 *
 * ```javascript
 * // Custom filtering function for a column
 * const grid = new Grid({
 *    features : {
 *        filter : true
 *    },
 *
 *    columns: [
 *        {
 *          field      : 'age',
 *          text       : 'Age',
 *          type       : 'number',
 *          // Custom filtering function that checks "greater than" no matter
 *          // which field user filled in :)
 *          filterable : ({ record, value, operator }) => record.age > value
 *        }
 *    ]
 * });
 * ```
 *
 * If this feature is configured with `prioritizeColumns : true`, those functions will also be used when filtering
 * programmatically:
 *
 * ```javascript
 * const grid = new Grid({
 *    features : {
 *        filter : {
 *            prioritizeColumns : true
 *        }
 *    },
 *
 *    columns: [
 *        {
 *          field      : 'age',
 *          text       : 'Age',
 *          type       : 'number',
 *          filterable : ({ record, value, operator }) => record.age > value
 *        }
 *    ]
 * });
 *
 * // Because of the prioritizeColumns config above, any custom filterable function
 * // on a column will be used when programmatically filtering by that columns field
 * grid.store.filter({
 *     property : 'age',
 *     value    : 41
 * });
 * ```
 *
 * You can supply a field config to use for the filtering field displayed for string type columns:
 *
 * ```javascript
 * // For string-type columns you can also replace the filter UI with a custom field:
 * columns: [
 *     {
 *         field : 'city',
 *         // Filtering for a value out of a list of values
 *         filterable: {
 *             filterField : {
 *                 type  : 'combo',
 *                 items : [
 *                     'Paris',
 *                     'Dubai',
 *                     'Moscow',
 *                     'London',
 *                     'New York'
 *                 ]
 *             }
 *         }
 *     }
 * ]
 * ```
 *
 * This feature is <strong>disabled</strong> by default.
 *
 * **Note:** This feature cannot be used together with {@link Grid.feature.FilterBar FilterBar} feature, they are
 * mutually exclusive.
 *
 * @extends Core/mixin/InstancePlugin
 * @demo Grid/filtering
 * @classtype filter
 */

class Filter extends InstancePlugin {
  //region Init
  static get $name() {
    return 'Filter';
  }

  static get configurable() {
    return {
      /**
       * Use custom filtering functions defined on columns also when programmatically filtering by the columns
       * field.
       *
       * ```javascript
       * const grid = new Grid({
       *     columns : [
       *         {
       *             field : 'age',
       *             text : 'Age',
       *             filterable({ record, value }) {
       *               // Custom filtering, return true/false
       *             }
       *         }
       *     ],
       *
       *     features : {
       *         filter : {
       *             prioritizeColumns : true // <--
       *         }
       *     }
       * });
       *
       * // Because of the prioritizeColumns config above, any custom
       * // filterable function on a column will be used when
       * // programmatically filtering by that columns field
       * grid.store.filter({
       *     property : 'age',
       *     value    : 30
       * });
       * ```
       *
       * @config {Boolean}
       * @default
       * @category Common
       */
      prioritizeColumns: false
    };
  }

  construct(grid, config) {
    if (grid.features.filterBar) {
      throw new Error('Grid.feature.Filter feature may not be used together with Grid.feature.FilterBar. These features are mutually exclusive.');
    }

    const me = this;
    me.grid = grid;
    me.closeFilterEditor = me.closeFilterEditor.bind(me);
    super.construct(grid, config);
    me.bindStore(grid.store);

    if (config && typeof config === 'object') {
      const clone = ObjectHelper.clone(config); // Feature accepts a filter config object, need to remove this config

      delete clone.prioritizeColumns;

      if (!ObjectHelper.isEmpty(clone)) {
        grid.store.filter(clone, null, grid.isConfiguring);
      }
    }
  }

  doDestroy() {
    var _this$filterTip, _this$filterEditorPop;

    (_this$filterTip = this.filterTip) === null || _this$filterTip === void 0 ? void 0 : _this$filterTip.destroy();
    (_this$filterEditorPop = this.filterEditorPopup) === null || _this$filterEditorPop === void 0 ? void 0 : _this$filterEditorPop.destroy();
    super.doDestroy();
  }

  get store() {
    return this.grid.store;
  }

  bindStore(store) {
    this.detachListeners('store');
    store.on({
      name: 'store',
      beforeFilter: 'onStoreBeforeFilter',
      filter: 'onStoreFilter',
      thisObj: this
    });
  } //endregion
  //region Plugin config
  // Plugin configuration. This plugin chains some of the functions in Grid.

  static get pluginConfig() {
    return {
      chain: ['renderHeader', 'populateCellMenu', 'populateHeaderMenu', 'onElementClick', 'bindStore']
    };
  } //endregion
  //region Refresh headers

  /**
   * Update headers to match stores filters. Called on store load and grid header render.
   * @param reRenderRows Also refresh rows?
   * @private
   */

  refreshHeaders(reRenderRows) {
    const me = this,
          grid = me.grid,
          element = grid.headerContainer;

    if (element) {
      // remove .latest from all filters, will be applied to actual latest
      DomHelper.children(element, '.b-filter-icon.b-latest').forEach(iconElement => iconElement.classList.remove('b-latest'));

      if (!me.filterTip) {
        me.filterTip = new Tooltip({
          forElement: element,
          forSelector: '.b-filter-icon',

          getHtml({
            activeTarget
          }) {
            return activeTarget.dataset.filterText;
          }

        });
      }

      if (!grid.store.isFiltered) {
        me.filterTip.hide();
      }

      for (const column of grid.columns) {
        if (column.filterable !== false) {
          const filter = me.store.filters.getBy('property', column.field),
                headerEl = column.element;

          if (headerEl) {
            const textEl = column.textWrapper;
            let filterIconEl = textEl === null || textEl === void 0 ? void 0 : textEl.querySelector('.b-filter-icon'),
                filterText;

            if (filter) {
              let value = filter.displayValue || filter.value || '';

              if (column.formatValue) {
                value = column.formatValue(value);
              }

              filterText = me.L('L{filter}') + ': ' + (typeof filter === 'string' ? filter : `${filter.operator} ${value}`);
            } else {
              filterText = me.L('L{applyFilter}');
            }

            if (!filterIconEl) {
              // putting icon in header text to have more options for positioning it
              filterIconEl = DomHelper.createElement({
                parent: textEl,
                tag: 'div',
                className: 'b-filter-icon',
                dataset: {
                  filterText: filterText
                }
              });
              headerEl.classList.add('b-filterable');
            } else {
              filterIconEl.dataset.filterText = filterText;
            } // latest applied filter distinguished with class to enable highlighting etc.

            if (column.field === me.store.latestFilterField) filterIconEl.classList.add('b-latest');
            headerEl.classList[filter ? 'add' : 'remove']('b-filter'); // When IE11 support is dropped
            // headerEl.classList.toggle('b-filter', !!filter);
          }

          column.meta.isFiltered = !!filter;
        }
      }

      if (reRenderRows) {
        grid.refreshRows();
      }
    }
  } //endregion
  //region Filter

  applyFilter(column, config) {
    this.store.filter({ ...column.filterable,
      ...config,
      property: column.field
    });
  }

  removeFilter(column) {
    this.store.removeFilter(column.field);
  } // TODO: break out as own views, registering with Filter the same way columns register with ColumnManager

  getPopupDateItems(column, fieldType, filter, initialValue, store, changeCallback, closeCallback, filterField) {
    const me = this,
          onClose = changeCallback;

    function onClear() {
      me.removeFilter(column);
    }

    function onKeydown({
      event
    }) {
      if (event.key === 'Enter') {
        changeCallback();
      }
    }

    function onChange({
      source,
      value
    }) {
      if (value == null) {
        onClear();
      } else {
        me.clearSiblingsFields(source);
        me.applyFilter(column, {
          operator: source.operator,
          value,
          displayValue: source._value,
          type: 'date'
        });
      }
    }

    return [ObjectHelper.assign({
      type: 'date',
      ref: 'on',
      placeholder: 'L{on}',
      localeClass: me,
      clearable: true,
      label: '<i class="b-fw-icon b-icon-filter-equal"></i>',
      value: (filter === null || filter === void 0 ? void 0 : filter.operator) === '=' ? filter.value : initialValue,
      operator: '=',
      onKeydown,
      onChange,
      onClose,
      onClear
    }, filterField), ObjectHelper.assign({
      type: 'date',
      ref: 'before',
      placeholder: 'L{before}',
      localeClass: me,
      clearable: true,
      label: '<i class="b-fw-icon b-icon-filter-before"></i>',
      value: (filter === null || filter === void 0 ? void 0 : filter.operator) === '<' ? filter.value : null,
      operator: '<',
      onKeydown,
      onChange,
      onClose,
      onClear
    }, filterField), ObjectHelper.assign({
      type: 'date',
      ref: 'after',
      cls: 'b-last-row',
      placeholder: 'L{after}',
      localeClass: me,
      clearable: true,
      label: '<i class="b-fw-icon b-icon-filter-after"></i>',
      value: (filter === null || filter === void 0 ? void 0 : filter.operator) === '>' ? filter.value : null,
      operator: '>',
      onKeydown,
      onChange,
      onClose,
      onClear
    }, filterField)];
  }

  getPopupNumberItems(column, fieldType, filter, initialValue, store, changeCallback, closeCallback, filterField) {
    const me = this,
          onEsc = changeCallback;

    function onClear() {
      me.removeFilter(column);
    }

    function onKeydown({
      event
    }) {
      if (event.key === 'Enter') {
        changeCallback();
      }
    }

    function onChange({
      source,
      value
    }) {
      if (value == null) {
        onClear();
      } else {
        me.clearSiblingsFields(source);
        me.applyFilter(column, {
          operator: source.operator,
          value
        });
      }
    }

    return [ObjectHelper.assign({
      type: 'number',
      placeholder: 'L{Filter.equals}',
      localeClass: me,
      clearable: true,
      label: '<i class="b-fw-icon b-icon-filter-equal"></i>',
      value: (filter === null || filter === void 0 ? void 0 : filter.operator) === '=' ? filter.value : initialValue,
      operator: '=',
      onKeydown,
      onChange,
      onEsc,
      onClear
    }, filterField), ObjectHelper.assign({
      type: 'number',
      placeholder: 'L{lessThan}',
      localeClass: me,
      clearable: true,
      label: '<i class="b-fw-icon b-icon-filter-less"></i>',
      value: (filter === null || filter === void 0 ? void 0 : filter.operator) === '<' ? filter.value : null,
      operator: '<',
      onKeydown,
      onChange,
      onEsc,
      onClear
    }, filterField), ObjectHelper.assign({
      type: 'number',
      cls: 'b-last-row',
      placeholder: 'L{moreThan}',
      localeClass: me,
      clearable: true,
      label: '<i class="b-fw-icon b-icon-filter-more"></i>',
      value: (filter === null || filter === void 0 ? void 0 : filter.operator) === '>' ? filter.value : null,
      operator: '>',
      onKeydown,
      onChange,
      onEsc,
      onClear
    }, filterField)];
  }

  clearSiblingsFields(sourceField) {
    var _this$filterEditorPop2;

    // TODO: Store filtering allows multiple filters per field (for example age > 50 and age < 80),
    // but the Filter feature only handles a single filter per field.
    // For now, trying to add filter by age > and then for age <,
    // it should clear the previous field since that filter is replaced
    (_this$filterEditorPop2 = this.filterEditorPopup) === null || _this$filterEditorPop2 === void 0 ? void 0 : _this$filterEditorPop2.items.forEach(field => {
      field !== sourceField && (field === null || field === void 0 ? void 0 : field.clear());
    });
  }

  getPopupDurationItems(column, fieldType, filter, initialValue, store, changeCallback, closeCallback, filterField) {
    const me = this,
          onEsc = changeCallback,
          onClear = closeCallback;

    function onChange({
      source,
      value
    }) {
      if (value == null) {
        closeCallback();
      } else {
        me.applyFilter(column, {
          operator: source.operator,
          value: value
        });
        changeCallback();
      }
    }

    return [ObjectHelper.assign({
      type: 'duration',
      placeholder: 'L{Filter.equals}',
      localeClass: me,
      clearable: true,
      label: '<i class="b-fw-icon b-icon-filter-equal"></i>',
      value: (filter === null || filter === void 0 ? void 0 : filter.operator) === '=' ? filter.value : initialValue,
      operator: '=',
      onChange,
      onEsc,
      onClear
    }, filterField), ObjectHelper.assign({
      type: 'duration',
      placeholder: 'L{lessThan}',
      localeClass: me,
      clearable: true,
      label: '<i class="b-fw-icon b-icon-filter-less"></i>',
      value: (filter === null || filter === void 0 ? void 0 : filter.operator) === '<' ? filter.value : null,
      operator: '<',
      onChange,
      onEsc,
      onClear
    }, filterField), ObjectHelper.assign({
      type: 'duration',
      cls: 'b-last-row',
      placeholder: 'L{moreThan}',
      localeClass: me,
      clearable: true,
      label: '<i class="b-fw-icon b-icon-filter-more"></i>',
      value: (filter === null || filter === void 0 ? void 0 : filter.operator) === '>' ? filter.value : null,
      operator: '>',
      onChange,
      onEsc,
      onClear
    }, filterField)];
  }

  getPopupStringItems(column, fieldType, filter, initialValue, store, changeCallback, closeCallback, filterField) {
    const me = this;
    return [ObjectHelper.assign({
      type: fieldType,
      cls: 'b-last-row',
      placeholder: 'L{filter}',
      localeClass: me,
      clearable: true,
      label: '<i class="b-fw-icon b-icon-filter-equal"></i>',
      value: filter ? filter.value || filter : initialValue,
      operator: '*',

      onChange({
        source,
        value
      }) {
        if (value === '') {
          closeCallback();
        } else {
          me.applyFilter(column, {
            operator: source.operator,
            value
          });
          changeCallback();
        }
      },

      onClose: changeCallback,
      onClear: closeCallback
    }, filterField)];
  }
  /**
   * Get fields to display in filter popup.
   * @param {Grid.column.Column} column Column
   * @param fieldType Type of field, number, date etc.
   * @param filter Current filter filter
   * @param initialValue
   * @param store Grid store
   * @param changeCallback Callback for when filter has changed
   * @param closeCallback Callback for when editor should be closed
   * @param filterField filter field
   * @returns {*}
   * @private
   */

  getPopupItems(column, fieldType, filter, initialValue, store, changeCallback, closeCallback, filterField) {
    switch (fieldType) {
      case 'date':
        return this.getPopupDateItems(...arguments);

      case 'number':
        return this.getPopupNumberItems(...arguments);

      case 'duration':
        return this.getPopupDurationItems(...arguments);

      default:
        return this.getPopupStringItems(...arguments);
    }
  }
  /**
   * Shows a popup where a filter can be edited.
   * @param {Grid.column.Column|String} column Column to show filter editor for
   * @param {*} [value] The initial value of the filter field
   */

  showFilterEditor(column, value) {
    const me = this,
          {
      store
    } = me,
          col = typeof column === 'string' ? me.grid.columns.getById(column) : column,
          headerEl = col.element,
          field = store.modelClass.fieldMap[col.field],
          filter = store.filters.getBy('property', col.field),
          type = col.filterType,
          fieldType = type ? fieldTypeMap[type] : fieldTypeMap[col.type] || field && fieldTypeMap[field.type] || 'text';

    if (col.filterable === false) {
      return;
    } // Destroy previous filter popup

    me.closeFilterEditor();
    const items = me.getPopupItems(col, fieldType, filter, value, store, me.closeFilterEditor, () => {
      me.removeFilter(col);
      me.closeFilterEditor();
    }, col.filterable.filterField); // Localize placeholders

    items.forEach(item => item.placeholder = this.L(item.placeholder));
    me.filterEditorPopup = WidgetHelper.openPopup(headerEl, {
      owner: me.grid,
      width: '16em',
      cls: 'b-filter-popup',
      scrollAction: 'realign',
      items
    });
  }
  /**
   * Close the filter editor.
   */

  closeFilterEditor() {
    var _this$filterEditorPop3;

    // Must defer the destroy because it may be closed by an event like a "change" event where
    // there may be plenty of code left to execute which must not execute on destroyed objects.
    (_this$filterEditorPop3 = this.filterEditorPopup) === null || _this$filterEditorPop3 === void 0 ? void 0 : _this$filterEditorPop3.setTimeout(this.filterEditorPopup.destroy);
    this.filterEditorPopup = null;
  } //endregion
  //region Context menu
  //TODO: break out together with getPopupXXItems() (see comment above)

  populateCellMenuWithDateItems({
    column,
    record,
    items
  }) {
    const property = column.field,
          field = record.getFieldDefinition(property),
          type = column.filterType || (field === null || field === void 0 ? void 0 : field.type) || column.type;

    if (type === 'date') {
      const me = this,
            value = record[property],
            filter = operator => {
        me.applyFilter(column, {
          operator,
          value,
          displayValue: column.formatValue ? column.formatValue(value) : value,
          type: 'date'
        });
      };

      items.filterDateEquals = {
        text: 'L{on}',
        localeClass: me,
        icon: 'b-fw-icon b-icon-filter-equal',
        cls: 'b-separator',
        name: 'filterDateEquals',
        weight: 300,
        disabled: me.disabled,
        onItem: () => filter('=')
      };
      items.filterDateBefore = {
        text: 'L{before}',
        localeClass: me,
        icon: 'b-fw-icon b-icon-filter-before',
        name: 'filterDateBefore',
        weight: 310,
        disabled: me.disabled,
        onItem: () => filter('<')
      };
      items.filterDateAfter = {
        text: 'L{after}',
        localeClass: me,
        icon: 'b-fw-icon b-icon-filter-after',
        name: 'filterDateAfter',
        weight: 320,
        disabled: me.disabled,
        onItem: () => filter('>')
      };
    }
  }

  populateCellMenuWithNumberItems({
    column,
    record,
    items
  }) {
    const property = column.field,
          field = record.getFieldDefinition(property),
          type = column.filterType || column.type || (field === null || field === void 0 ? void 0 : field.type);

    if (type === 'number') {
      const me = this,
            value = record[property],
            filter = operator => {
        me.applyFilter(column, {
          operator,
          value
        });
      };

      items.filterNumberEquals = {
        text: 'L{equals}',
        localeClass: me,
        icon: 'b-fw-icon b-icon-filter-equal',
        cls: 'b-separator',
        name: 'filterNumberEquals',
        weight: 300,
        disabled: me.disabled,
        onItem: () => filter('=')
      };
      items.filterNumberLess = {
        text: 'L{lessThan}',
        localeClass: me,
        icon: 'b-fw-icon b-icon-filter-less',
        name: 'filterNumberLess',
        weight: 310,
        disabled: me.disabled,
        onItem: () => filter('<')
      };
      items.filterNumberMore = {
        text: 'L{moreThan}',
        localeClass: me,
        icon: 'b-fw-icon b-icon-filter-more',
        name: 'filterNumberMore',
        weight: 320,
        disabled: me.disabled,
        onItem: () => filter('>')
      };
    }
  }

  populateCellMenuWithDurationItems({
    column,
    record,
    items
  }) {
    const property = column.field,
          field = record.getFieldDefinition(property),
          type = column.filterType || (field === null || field === void 0 ? void 0 : field.type) || column.type;

    if (type === 'duration') {
      const me = this,
            value = record[property],
            filter = operator => {
        me.applyFilter(column, {
          operator,
          value
        });
      };

      items.filterDurationEquals = {
        text: 'L{equals}',
        localeClass: me,
        icon: 'b-fw-icon b-icon-filter-equal',
        cls: 'b-separator',
        name: 'filterDurationEquals',
        weight: 300,
        disabled: me.disabled,
        onItem: () => filter('=')
      };
      items.filterDurationLess = {
        text: 'L{lessThan}',
        localeClass: me,
        icon: 'b-fw-icon b-icon-filter-less',
        name: 'filterDurationLess',
        weight: 310,
        disabled: me.disabled,
        onItem: () => filter('<')
      };
      items.filterDurationMore = {
        text: 'L{moreThan}',
        localeClass: me,
        icon: 'b-fw-icon b-icon-filter-more',
        name: 'filterDurationMore',
        weight: 320,
        disabled: me.disabled,
        onItem: () => filter('>')
      };
    }
  }

  populateCellMenuWithStringItems({
    column,
    record,
    items
  }) {
    const property = column.field,
          field = record.getFieldDefinition(property),
          type = column.filterType || (field === null || field === void 0 ? void 0 : field.type) || column.type;

    if (!/(date|number|duration)/.test(type)) {
      var _column$filterable$fi, _column$filterable$fi2;

      const me = this,
            value = record[property],
            operator = (_column$filterable$fi = (_column$filterable$fi2 = column.filterable.filterField) === null || _column$filterable$fi2 === void 0 ? void 0 : _column$filterable$fi2.operator) !== null && _column$filterable$fi !== void 0 ? _column$filterable$fi : '*';
      items.filterStringEquals = {
        text: 'L{equals}',
        localeClass: me,
        icon: 'b-fw-icon b-icon-filter-equal',
        cls: 'b-separator',
        name: 'filterStringEquals',
        weight: 300,
        disabled: me.disabled,
        onItem: () => me.applyFilter(column, {
          value,
          operator
        })
      };
    }
  }
  /**
   * Add menu items for filtering.
   * @param {Object} options Contains menu items and extra data retrieved from the menu target.
   * @param {Grid.column.Column} options.column Column for which the menu will be shown
   * @param {Core.data.Model} options.record Record for which the menu will be shown
   * @param {Object} options.items A named object to describe menu items
   * @internal
   */

  populateCellMenu({
    column,
    record,
    items
  }) {
    const me = this;

    if (column.filterable !== false) {
      me.populateCellMenuWithDateItems(...arguments);
      me.populateCellMenuWithNumberItems(...arguments);
      me.populateCellMenuWithDurationItems(...arguments);
      me.populateCellMenuWithStringItems(...arguments);

      if (column.meta.isFiltered) {
        items.filterRemove = {
          text: 'L{removeFilter}',
          localeClass: me,
          icon: 'b-fw-icon b-icon-clear',
          cls: 'b-separator',
          weight: 400,
          name: 'filterRemove',
          disabled: me.disabled,
          onItem: () => me.removeFilter(column)
        };
      }
    }
  }
  /**
   * Add menu item for removing filter if column is filtered.
   * @param {Object} options Contains menu items and extra data retrieved from the menu target.
   * @param {Grid.column.Column} options.column Column for which the menu will be shown
   * @param {Object} options.items A named object to describe menu items
   * @returns {Object} items Menu items config
   * @internal
   */

  populateHeaderMenu({
    column,
    items
  }) {
    const me = this;

    if (column.meta.isFiltered) {
      items.editFilter = {
        text: 'L{editFilter}',
        localeClass: me,
        name: 'editFilter',
        weight: 100,
        icon: 'b-fw-icon b-icon-filter',
        cls: 'b-separator',
        disabled: me.disabled,
        onItem: () => me.showFilterEditor(column)
      };
      items.removeFilter = {
        text: 'L{removeFilter}',
        localeClass: me,
        name: 'removeFilter',
        weight: 110,
        icon: 'b-fw-icon b-icon-remove',
        disabled: me.disabled,
        onItem: () => me.removeFilter(column)
      };
    } else if (column.filterable !== false) {
      items.filter = {
        text: 'L{filter}',
        localeClass: me,
        name: 'filter',
        weight: 100,
        icon: 'b-fw-icon b-icon-filter',
        cls: 'b-separator',
        disabled: me.disabled,
        onItem: () => me.showFilterEditor(column)
      };
    }
  } //endregion
  //region Events
  // Intercept filtering by a column that has a custom filtering fn, and inject that fn

  onStoreBeforeFilter({
    filters
  }) {
    const {
      columns
    } = this.client;

    for (let i = 0; i < filters.count; i++) {
      var _column$filterable;

      const filter = filters.getAt(i),
            column = (filter.columnOwned || this.prioritizeColumns) && columns.get(filter.property);

      if (column !== null && column !== void 0 && (_column$filterable = column.filterable) !== null && _column$filterable !== void 0 && _column$filterable.filterFn) {
        // Cache CollectionFilter on the column to not have to recreate on each filter operation
        if (!column.$filter) {
          column.$filter = new CollectionFilter({
            columnOwned: true,
            property: filter.property,
            operator: filter.operator,

            filterBy(record) {
              return column.filterable.filterFn({
                value: this.value,
                record,
                operator: this.operator,
                property: this.property
              });
            }

          });
        } // Update value used by filters filtering fn

        column.$filter.value = filter.value;
        filters.splice(i, 1, column.$filter);
      }
    }
  }
  /**
   * Store filtered; refresh headers.
   * @private
   */

  onStoreFilter() {
    // Pass false to not refresh rows.
    // Store's refresh event will refresh the rows.
    this.refreshHeaders(false);
  }
  /**
   * Called after headers are rendered, make headers match stores initial sorters
   * @private
   */

  renderHeader() {
    this.refreshHeaders(false);
  }
  /**
   * Called when user clicks on the grid. Only care about clicks on the filter icon.
   * @param {MouseEvent} event
   * @private
   */

  onElementClick(event) {
    const target = event.target;

    if (this.filterEditorPopup) {
      this.closeFilterEditor();
    } // Checks if click is on node expander icon, then toggles expand/collapse

    if (target.classList.contains('b-filter-icon')) {
      const headerEl = DomHelper.up(target, '.b-grid-header');
      this.showFilterEditor(headerEl.dataset.columnId);
      return false;
    }
  } //endregion

}
Filter._$name = 'Filter';
GridFeatureManager.registerFeature(Filter);

/* eslint-disable no-unused-expressions */
/**
 * @module Grid/feature/FilterBar
 */

/**
 * Feature that allows filtering of the grid by entering filters on column headers.
 * The actual filtering is done by the store.
 * For info on programmatically handling filters, see {@link Core.data.mixin.StoreFilter StoreFilter}.
 *
 * {@inlineexample feature/FilterBar.js}
 *
 * ```javascript
 * // filtering turned on but no initial filter
 * const grid = new Grid({
 *   features: {
 *     filterBar : true
 *   }
 * });
 *
 * // using initial filter
 * const grid = new Grid({
 *   features : {
 *     filterBar : { filter: { property : 'city', value : 'Gavle' } }
 *   }
 * });
 * ```
 *
 * The individual filterability of columns is defined by a `filterable` property on the column which defaults to `true`.
 * If `false`, that column is not filterable.
 *
 * The property value may also be a custom filter function.
 *
 * The property value may also be an object which may contain the following two properties:
 *  - **filterFn** : `Function` A custom filtering function
 *  - **filterField** : `Object` A config object for the filter value input field.
 *
 * ```javascript
 * // Custom filtering function for a column
 * const grid = new Grid({
 *   features : {
 *     filterBar : true
 *   },
 *
 *   columns: [
 *      {
 *        field      : 'age',
 *        text       : 'Age',
 *        type       : 'number',
 *        // Custom filtering function that checks "greater than"
 *        filterable : ({ record, value }) => record.age > value
 *      },
 *      {
 *        field : 'name',
 *        // Filterable may specify a filterFn and a config for the filtering input field
 *        filterable : {
 *          filterFn : ({ record, value }) => record.name.toLowerCase().indexof(value.toLowerCase()) !== -1,
 *          filterField : {
 *            emptyText : 'Filter name'
 *          }
 *        }
 *      },
 *      {
 *        field : 'city',
 *        text : 'Visited',
 *        flex : 1,
 *        // Filterable with multiselect combo to pick several items to filter
 *        filterable : {
 *          filterField : {
 *            type        : 'combo',
 *            multiSelect : true,
 *            items       : ['Barcelona', 'Moscow', 'Stockholm']
 *          },
 *          filterFn    : ({ record, value }) => !value.length || value.includes(record.city)
 *        }
 *      }
 *   ]
 * });
 * ```
 *
 * If this feature is configured with `prioritizeColumns : true`, those functions will also be used when filtering
 * programmatically:
 *
 * ```javascript
 * const grid = new Grid({
 *    features : {
 *        filterBar : {
 *            prioritizeColumns : true
 *        }
 *    },
 *
 *    columns: [
 *        {
 *          field      : 'age',
 *          text       : 'Age',
 *          type       : 'number',
 *          // Custom filtering function that checks "greater than" no matter
 *          // which field user filled in :)
 *          filterable : ({ record, value, operator }) => record.age > value
 *        }
 *    ]
 * });
 *
 * // Will be used when filtering programmatically or using the UI
 * grid.store.filter({
 *     property : 'age',
 *     value    : 41
 * });
 * ```
 *
 *
 * This feature is <strong>disabled</strong> by default.
 *
 * **Note:** This feature cannot be used together with {@link Grid.feature.Filter filter} feature, they are mutually
 * exclusive.
 *
 * @extends Core/mixin/InstancePlugin
 * @demo Grid/filterbar
 * @classtype filterBar
 */

class FilterBar extends InstancePlugin {
  //region Config
  static get $name() {
    return 'FilterBar';
  }

  static get configurable() {
    return {
      /**
       * Use custom filtering functions defined on columns also when programmatically filtering by the columns
       * field.
       *
       * ```javascript
       * const grid = new Grid({
       *     columns : [
       *         {
       *             field : 'age',
       *             text : 'Age',
       *             filterable({ record, value }) {
       *               // Custom filtering, return true/false
       *             }
       *         }
       *     ],
       *
       *     features : {
       *         filterBar : {
       *             prioritizeColumns : true // <--
       *         }
       *     }
       * });
       *
       * // Because of the prioritizeColumns config above, any custom
       * // filterable function on a column will be used when
       * // programmatically filtering by that columns field
       * grid.store.filter({
       *     property : 'age',
       *     value    : 30
       * });
       * ```
       *
       * @config {Boolean}
       * @default
       * @category Common
       */
      prioritizeColumns: false,

      /**
       * The delay in milliseconds to wait after the last keystroke before applying filters.
       * Set to 0 to not trigger filtering from keystrokes, requires pressing ENTER instead
       * @config {Number}
       * @default
       */
      keyStrokeFilterDelay: 300,
      // Destroying data level filters when we hiding UI is supposed to be optional someday. So far this flag is private
      clearStoreFiltersOnHide: true
    };
  }

  static get properties() {
    return {
      filterFieldCls: 'b-filter-bar-field',
      filterFieldInputCls: 'b-filter-bar-field-input',
      filterableColumnCls: 'b-filter-bar-enabled',
      filterFieldInputSelector: '.b-filter-bar-field-input',
      filterableColumnSelector: '.b-filter-bar-enabled',
      filterParseRegExp: /^\s*([<>=*])?(.*)$/,
      storeTrackingSuspended: 0
    };
  } //endregion
  //region Init

  construct(grid, config) {
    if (grid.features.filter) {
      throw new Error('Grid.feature.FilterBar feature may not be used together with Grid.feature.Filter, These features are mutually exclusive.');
    }

    const me = this;
    me.grid = grid;
    me.onColumnFilterFieldChange = me.onColumnFilterFieldChange.bind(me);
    super.construct(grid, Array.isArray(config) ? {
      filter: config
    } : config);
    me.bindStore(grid.store);

    if (me.filter) {
      grid.store.filter(me.filter);
    }

    me.gridDetacher = grid.on('beforeelementclick', me.onBeforeElementClick, me);
  }

  bindStore(store) {
    this.detachListeners('store');
    store.on({
      name: 'store',
      beforeFilter: 'onStoreBeforeFilter',
      filter: 'onStoreFilter',
      thisObj: this
    });
  }

  doDestroy() {
    var _this$gridDetacher;

    this.destroyFilterBar();
    (_this$gridDetacher = this.gridDetacher) === null || _this$gridDetacher === void 0 ? void 0 : _this$gridDetacher.call(this);
    super.doDestroy();
  }

  doDisable(disable) {
    const {
      columns
    } = this.grid; // hide the fields, each silently - no updating of the store's filtered state until the end

    columns === null || columns === void 0 ? void 0 : columns.forEach(column => {
      const widget = this.getColumnFilterField(column);

      if (widget) {
        widget.disabled = disable;
      }
    });
    super.doDisable(disable);
  }

  static get pluginConfig() {
    return {
      before: ['onElementKeyDown'],
      chain: ['renderHeader', 'populateHeaderMenu', 'bindStore']
    };
  } //endregion
  //region FilterBar

  destroyFilterBar() {
    var _this$grid$columns;

    (_this$grid$columns = this.grid.columns) === null || _this$grid$columns === void 0 ? void 0 : _this$grid$columns.forEach(this.destroyColumnFilterField, this);
  }
  /**
   * Hides the filtering fields.
   */

  hideFilterBar() {
    var _me$grid$columns;

    const me = this; // we don't want to hear back store "filter" event while we're resetting store filters

    me.clearStoreFiltersOnHide && me.suspendStoreTracking(); // hide the fields, each silently - no updating of the store's filtered state until the end

    (_me$grid$columns = me.grid.columns) === null || _me$grid$columns === void 0 ? void 0 : _me$grid$columns.forEach(col => me.hideColumnFilterField(col, true)); // Now update the filtered state

    me.grid.store.filter();
    me.clearStoreFiltersOnHide && me.resumeStoreTracking();
    me.hidden = true;
  }
  /**
   * Shows the filtering fields.
   */

  showFilterBar() {
    this.renderFilterBar();
    this.hidden = false;
  }
  /**
   * Toggles the filtering fields visibility.
   */

  toggleFilterBar() {
    if (this.hidden) {
      this.showFilterBar();
    } else {
      this.hideFilterBar();
    }
  }
  /**
   * Renders the filtering fields for filterable columns.
   * @private
   */

  renderFilterBar() {
    this.grid.columns.visibleColumns.forEach(column => this.renderColumnFilterField(column));
    this.rendered = true;
  } //endregion
  //region FilterBar fields

  /**
   * Renders text field filter in the provided column header.
   * @param {Grid.column.Column} column Column to render text field filter for.
   * @private
   */

  renderColumnFilterField(column) {
    const me = this,
          grid = me.grid,
          filterable = me.getColumnFilterable(column); // we render fields for filterable columns only

    if (filterable && !column.hidden) {
      const headerEl = column.element,
            filter = grid.store.filters.getBy('property', column.field);
      let widget = me.getColumnFilterField(column); // if we don't haven't created a field yet
      // we build it from scratch

      if (!widget) {
        const type = `${column.filterType || 'text'}field`;
        widget = WidgetHelper.append(Object.assign({
          type,
          owner: me.grid,
          clearable: true,
          column: column,
          name: column.field,
          value: filter && !filter._filterBy && me.buildFilterString(filter),
          cls: me.filterFieldCls,
          inputCls: me.filterFieldInputCls,
          keyStrokeChangeDelay: me.keyStrokeFilterDelay,
          onChange: me.onColumnFilterFieldChange,
          onClear: me.onColumnFilterFieldChange,
          disabled: me.disabled
        }, filterable.filterField), headerEl)[0];
        me.setColumnFilterField(column, widget);
      } // if we have one..
      else {
          // re-apply widget filter
          me.onColumnFilterFieldChange({
            source: widget,
            value: widget.value
          }); // re-append the widget to its parent node (in case the column header was redrawn (happens when resizing columns))

          widget.render(headerEl); // show widget in case it was hidden

          widget.show();
        }

      headerEl.classList.add(me.filterableColumnCls);
    }
  }
  /**
   * Fills in column filter fields with values from the grid store filters.
   * @private
   */

  updateColumnFilterFields() {
    const {
      columns,
      store
    } = this.grid;
    let field, filter;

    for (const column of columns) {
      field = this.getColumnFilterField(column);

      if (field) {
        filter = store.filters.getBy('property', column.field);

        if (filter) {
          // For filtering functions we keep what user typed into the field, we cannot construct a filter
          // string from them
          if (!filter._filterBy) {
            field.value = this.buildFilterString(filter);
          } else {
            field.value = filter.value;
          }
        } // No filter, clear field
        else {
            field.value = '';
          }
      }
    }
  }

  getColumnFilterable(column) {
    if (!column.isRoot && column.filterable !== false && column.field) {
      if (typeof column.filterable === 'function') {
        column.filterable = {
          filterFn: column.filterable
        };
      }

      return column.filterable;
    }
  }

  destroyColumnFilterField(column) {
    const widget = this.getColumnFilterField(column);

    if (widget) {
      this.hideColumnFilterField(column); // destroy filter UI field

      widget.destroy(); // remember there is no field bound anymore

      this.setColumnFilterField(column, undefined);
    }
  }

  hideColumnFilterField(column, silent) {
    const me = this,
          {
      store
    } = me.grid,
          columnEl = column.element,
          widget = me.getColumnFilterField(column);

    if (widget) {
      // hide field
      widget.hide();

      if (me.clearStoreFiltersOnHide && column.field) {
        store.removeFilter(column.field, silent);
      }

      columnEl === null || columnEl === void 0 ? void 0 : columnEl.classList.remove(me.filterableColumnCls);
    }
  }

  getColumnFilterField(column) {
    var _this$_columnFilters;

    return (_this$_columnFilters = this._columnFilters) === null || _this$_columnFilters === void 0 ? void 0 : _this$_columnFilters[column.data.id];
  }

  setColumnFilterField(column, widget) {
    this._columnFilters = this._columnFilters || {};
    this._columnFilters[column.data.id] = widget;
  } //endregion
  //region Filters

  parseFilterValue(value) {
    if (Array.isArray(value)) {
      return {
        value
      };
    }

    const match = String(value).match(this.filterParseRegExp);
    return {
      operator: match[1] || '*',
      value: match[2]
    };
  }

  buildFilterString(filter) {
    return (filter.operator === '*' ? '' : filter.operator) + filter.value;
  } //endregion
  // region Events
  // Intercept filtering by a column that has a custom filtering fn, and inject that fn

  onStoreBeforeFilter({
    filters
  }) {
    const {
      columns
    } = this.client;

    for (let i = 0; i < filters.count; i++) {
      var _column$filterable;

      const filter = filters.getAt(i),
            column = (filter.columnOwned || this.prioritizeColumns) && columns.get(filter.property);

      if ((_column$filterable = column.filterable) !== null && _column$filterable !== void 0 && _column$filterable.filterFn) {
        // Cache CollectionFilter on the column to not have to recreate on each filter operation
        if (!column.$filter) {
          column.$filter = new CollectionFilter({
            columnOwned: true,
            property: filter.property,

            filterBy(record) {
              return column.filterable.filterFn({
                value: this.value,
                record,
                property: this.property
              });
            }

          });
        } // Update value used by filters filtering fn

        column.$filter.value = filter.value;
        filters.splice(i, 1, column.$filter);
      }
    }
  }
  /**
   * Fires when store gets filtered. Refreshes field values in column headers.
   * @private
   */

  onStoreFilter() {
    if (!this.storeTrackingSuspended && this.rendered) {
      this.updateColumnFilterFields();
    }
  }

  suspendStoreTracking() {
    this.storeTrackingSuspended++;
  }

  resumeStoreTracking() {
    this.storeTrackingSuspended--;
  }
  /**
   * Called after headers are rendered, make headers match stores initial sorters
   * @private
   */

  renderHeader() {
    if (!this.hidden) {
      this.renderFilterBar();
    }
  }

  onElementKeyDown(event) {
    // flagging event with handled = true used to signal that other features should probably not care about it
    if (event.handled) {
      return;
    } // if we are pressing left/right arrow keys while being in a filter editor
    // we set event.handled flag (otherwise other features prevent the event)

    if (event.target.matches(this.filterFieldInputSelector)) {
      switch (event.key) {
        case 'ArrowLeft':
        case 'ArrowRight':
          event.handled = true;
      }
    }
  }

  onBeforeElementClick({
    event
  }) {
    // prevent other features reacting when clicking a filter field (or any element inside it)
    if (event.target.closest(`.${this.filterFieldCls}`)) {
      return false;
    }
  }
  /**
   * Called when a column text filter field value is changed by user.
   * @param  {Core.widget.TextField} field Filter text field.
   * @param  {String} value New filtering value.
   * @private
   */

  onColumnFilterFieldChange({
    source: field,
    value
  }) {
    const me = this,
          {
      store
    } = me.grid; // we don't want to hear back store "filter" event
    // so we suspend store tracking

    me.suspendStoreTracking();

    if (value == null || value === '' || Array.isArray(value) && value.length === 0) {
      // remove filter if setting to empty
      store.removeFilter(field.name);
    } else {
      store.filter({
        columnOwned: true,
        property: field.name,
        ...me.parseFilterValue(value)
      });
    }

    me.resumeStoreTracking();
  } //endregion
  //region Menu items

  /**
   * Adds a menu item to toggle filter bar visibility.
   * @param {Object} options Contains menu items and extra data retrieved from the menu target.
   * @param {Grid.column.Column} options.column Column for which the menu will be shown
   * @param {Object} options.items A named object to describe menu items
   * @internal
   */

  populateHeaderMenu({
    items
  }) {
    items.toggleFilterBar = {
      text: this.hidden ? 'L{enableFilterBar}' : 'L{disableFilterBar}',
      localeClass: this,
      name: 'toggleFilterBar',
      weight: 120,
      icon: 'b-fw-icon b-icon-filter',
      cls: 'b-separator',
      onItem: () => this.toggleFilterBar()
    };
  } //endregion

}
FilterBar.featureClass = 'b-filter-bar';
FilterBar._$name = 'FilterBar';
GridFeatureManager.registerFeature(FilterBar);

//TODO: Expand function?
/**
 * @module Grid/feature/Group
 */

/**
 * Enables rendering and handling of row groups. The actual grouping is done in the store, but triggered by [shift] +
 * clicking headers or by using two finger tap (one on header, one anywhere on grid). Groups can be expanded/collapsed
 * by clicking on the group row or pressing [space] when group row is selected.
 * The actual grouping is done by the store, see {@link Core.data.mixin.StoreGroup#function-group}.
 *
 * Grouping by a field performs sorting by the field automatically. It's not possible to prevent sorting.
 * If you group, the records have to be sorted so that records in a group stick together. You can either control sorting
 * direction, or provide a custom sorting function called `fn` to your grouper.
 *
 * For info on programmatically handling grouping, see {@link Core.data.mixin.StoreGroup StoreGroup}.
 *
 * Currently grouping is not supported when using pagination, the underlying store cannot group data that is split into pages.
 *
 * This feature is **enabled** by default.
 *
 * @example
 * // grouping feature is enabled, no default value though
 * let grid = new Grid({
 *     features : {
 *         group : true
 *     }
 * });
 *
 * // use initial grouping
 * let grid = new Grid({
 *     features : {
 *         group : 'city'
 *     }
 * });
 *
 * // default grouper and custom renderer, which will be applied to each cell except the "group" cell
 * let grid = new Grid({
 *   features : {
 *     group : {
 *       field : 'city',
 *       ascending : false,
 *       renderer : ({ isFirstColumn, count, groupRowFor, record }) => isFirstColumn ? `${groupRowFor} (${count})` : ''
 *     }
 *   }
 * });
 *
 * // can also be specified on the store
 * let grid = new Grid({
 *     store : {
 *         groupers : [
 *             { field : 'city', ascending : false }
 *         ]
 *     }
 * });
 *
 * // custom sorting function can also be specified on the store
 * let grid = new Grid({
 *     store : {
 *         groupers : [{
 *             field : 'city',
 *             fn : (recordA, recordB) => {
 *                 // apply custom logic, for example:
 *                 return recordA.city.length < recordB.city.length ? -1 : 1;
 *             }
 *         }]
 *     }
 * });
 *
 * @extends Core/mixin/InstancePlugin
 *
 * @demo Grid/grouping
 * @classtype group
 * @externalexample feature/Group.js
 */

class Group extends InstancePlugin {
  static get $name() {
    return 'Group';
  }

  static get defaultConfig() {
    return {
      /**
       * The name of the record field to group by.
       * @config {String}
       * @default
       */
      field: null,

      /**
       * A function which produces the HTML for a group header.
       * The function is called in the context of this Group feature object.
       * Default group renderer displays the `groupRowFor` and `count`.
       *
       * @config {Function}
       * @property {String} groupRowFor The value of the `field` for the group.
       * @property {Core.data.Model} record The group record representing the group.
       * @property {Object} record.meta Meta data with additional info about the grouping.
       * @property {Array} record.groupChildren The group child records.
       * @property {Number} count Number of records in the group.
       * @property {Grid.column.Column} column The column the renderer runs for.
       * @property {Boolean} isFirstColumn True, if `column` is the first column.
       * If `RowNumberColumn` is the real first column, it's not taken into account.
       * @property {Grid.column.Column} [groupColumn] The column under which the `field` is shown.
       * @property {Object} size Sizing information for the group header row, only `height` is relevant.
       * @property {Number} size.height The height of the row, set this if you want a custom height for the group header row
       * That is UI part, so do not rely on its existence.
       * @default
       */
      renderer: null
    };
  } //region Init

  construct(grid, config) {
    const me = this;

    if (grid.features.tree) {
      return;
    } // groupSummary feature needs to be initialized first, if it is used

    me._thisIsAUsedExpression(grid.features.groupSummary); // process initial config into an actual config object

    config = me.processConfig(config);
    me.grid = grid;
    me.rowManager = grid.rowManager;
    super.construct(grid, config);
    me.bindStore(grid.store);
    me.rowManager.on({
      beforeRenderRow: 'onBeforeRenderRow',
      renderCell: 'renderCell',
      // The feature gets to see cells being rendered before the GroupSummary feature
      // because this injects header content into group header rows and adds rendering
      // info to the cells renderData which GroupSummary must comply with.
      prio: 1100,
      thisObj: me
    });
  } // Group feature handles special config cases, where user can supply a string or a group config object
  // instead of a normal config object

  processConfig(config) {
    if (typeof config === 'string') {
      return {
        field: config,
        ascending: null
      };
    }

    return config;
  } // override setConfig to process config before applying it (used mainly from ReactGrid)

  setConfig(config) {
    if (config === null) {
      this.store.clearGroupers();
    } else {
      super.setConfig(this.processConfig(config));
    }
  }

  bindStore(store) {
    this.detachListeners('store');
    store.on({
      name: 'store',
      group: 'onStoreGroup',
      thisObj: this
    });
  }

  set renderer(renderer) {
    this.groupRenderer = renderer;
  }

  set field(field) {
    this._field = field;
    this.store.group(field, this.ascending);
  }

  get field() {
    return this._field;
  }

  doDestroy() {
    super.doDestroy();
  }

  doDisable(disable) {
    const {
      store
    } = this; // Grouping mostly happens in store, need to clear groupers there to remove headers.
    // Use configured groupers as first sorters to somewhat maintain the order

    if (disable && store.isGrouped) {
      const {
        sorters
      } = store;
      sorters.unshift(...store.groupers);
      store.clearGroupers();
      store.sort(sorters);
    }

    super.doDisable(disable);
  }

  get store() {
    return this.grid.store;
  } //endregion
  //region Plugin config
  // Plugin configuration. This plugin chains some of the functions in Grid.

  static get pluginConfig() {
    return {
      assign: ['collapseAll', 'expandAll'],
      chain: ['renderHeader', 'populateHeaderMenu', 'getColumnDragToolbarItems', 'onElementTouchStart', 'onElementClick', 'onElementKeyDown', 'bindStore']
    };
  } //endregion
  //region Expand/collapse

  /**
   * Collapses or expands a group depending on its current state
   * @param {Core.data.Model|String} recordOrId Record or records id for a group row to collapse or expand
   * @param {Boolean} collapse Force collapse (true) or expand (true)
   * @fires togglegroup
   */

  toggleCollapse(recordOrId, collapse) {
    this.internalToggleCollapse(recordOrId, collapse);
  }
  /**
   * Collapses or expands a group depending on its current state
   * @param {Core.data.Model|String} recordOrId Record or records id for a group row to collapse or expand
   * @param {Boolean} collapse Force collapse (true) or expand (true)
   * @param {Boolean} skipRender True to not render rows
   * @internal
   * @fires togglegroup
   */

  internalToggleCollapse(recordOrId, collapse, skipRender = false) {
    const me = this,
          store = me.store,
          groupRecord = store.getById(recordOrId),
          meta = groupRecord.meta;
    if (meta.groupRowFor === undefined) return;
    collapse = collapse === undefined ? !meta.collapsed : collapse;

    if (collapse) {
      store.excludeGroupRecords(groupRecord);
    } else {
      store.includeGroupRecords(groupRecord);
    } // note to self, all this could be done in collapsed setter on GroupModel if desired...

    meta.collapsed = collapse;
    /**
     * Group expanded or collapsed
     * @event toggleGroup
     * @param groupRecord Group record
     * @param {Boolean} collapse Collapsed (true) or expanded (false)
     */

    me.grid.trigger('toggleGroup', {
      groupRecord,
      collapse
    });

    if (!skipRender) {
      // render from grouprecord and down, no need to touch those above
      me.rowManager.renderFromRecord(groupRecord);
    }
  }
  /**
   * Collapse all groups. This function is exposed on Grid and can thus be called as `grid.collapseAll()`
   */

  collapseAll() {
    const me = this;

    if (me.store.isGrouped && !me.disabled) {
      me.store.groupRecords.forEach(r => me.internalToggleCollapse(r, true, true));
      me.grid.refreshRows(true);
    }
  }
  /**
   * Expand all groups. This function is exposed on Grid and can thus be called as `grid.expandAll()`
   */

  expandAll() {
    const me = this;

    if (me.store.isGrouped && !me.disabled) {
      me.store.groupRecords.forEach(r => me.internalToggleCollapse(r, false, true));
      me.grid.refreshRows();
    }
  } //endregion
  //region Rendering

  /**
   * Called before rendering row contents, used to reset rows no longer used as group rows
   * @private
   */

  onBeforeRenderRow({
    row,
    record
  }) {
    // row.id contains previous record id on before render
    const oldRecord = row.grid.store.getById(row.id); // force update of inner html if this row used for group data

    row.forceInnerHTML = row.forceInnerHTML || oldRecord && 'groupRowFor' in oldRecord.meta;
  }
  /**
   * Called when a cell is rendered, styles the group rows first cell.
   * @private
   */

  renderCell(renderData) {
    const me = this,
          {
      grid
    } = me,
          {
      cellElement,
      row,
      column
    } = renderData,
          meta = renderData.record.meta,
          firstColumn = grid.columns.visibleColumns[0],
          firstSubGridColumns = grid.subGrids[grid.regions[0]].columns.visibleColumns,
          rowClasses = {
      'b-group-row': 0,
      'b-grid-group-collapsed': 0
    };

    if (!me.disabled && me.store.isGrouped) {
      if ('groupRowFor' in meta) {
        // do nothing with action column to make possible using actions for groups
        if (column.type === 'action') {
          return;
        } // let column clear the cell, in case it needs to do some cleanup

        column.clearCell(cellElement); // this is a group row, add css classes

        rowClasses['b-grid-group-collapsed'] = meta.collapsed;
        rowClasses['b-group-row'] = 1;

        if (firstColumn.type === 'rownumber' && column === firstSubGridColumns[1] || firstColumn.type !== 'rownumber' && column === firstSubGridColumns[0]) {
          cellElement.classList.add('b-group-title');
        }

        me.buildGroupHeader(renderData);
      } else {
        cellElement.classList.remove('b-group-title');
      }
    } // Still need to sync row classes is disabled or not grouped.
    // Previous b-group-row and b-grid-group-collapsed classes must be removed.

    row.assignCls(rowClasses);
  } // renderData.cellElement is required

  buildGroupHeader(renderData) {
    const me = this,
          {
      record,
      cellElement,
      column,
      persist
    } = renderData,
          grid = me.grid,
          meta = record.meta,
          // Need to adjust count if group summary is used
    // TODO remove this when grouping has been refactored to not store group headers/footers in the Store
    count = meta.childCount - (grid.features.groupSummary ? 1 : 0),
          firstVisibleColumn = grid.columns.visibleColumns[0],
          firstSubGridColumns = column && grid.subGrids[grid.regions[0]].columns.visibleColumns,
          isFirstColumn = renderData.isFirstColumn = firstVisibleColumn.type === 'rownumber' && column === firstSubGridColumns[1] || firstVisibleColumn.type !== 'rownumber' && column === firstSubGridColumns[0];
    let html = null,
        applyDefault = true;

    if (persist || column) {
      const groupColumn = grid.columns.get(meta.groupField); // First try using columns groupRenderer (might not even have a column if grouping programmatically)

      if (groupColumn !== null && groupColumn !== void 0 && groupColumn.groupRenderer) {
        if (isFirstColumn) {
          // groupRenderer could return nothing and just apply changes directly to DOM element
          html = groupColumn.groupRenderer(Object.assign({}, renderData, {
            groupRowFor: meta.groupRowFor,
            groupRecords: record.groupChildren,
            groupColumn,
            count
          }));
          applyDefault = false;
        }
      } // Secondly use features groupRenderer, if configured with one
      else if (me.groupRenderer) {
          // groupRenderer could return nothing and just apply changes directly to DOM element
          html = me.groupRenderer(Object.assign({}, renderData, {
            groupRowFor: meta.groupRowFor,
            groupRecords: record.groupChildren,
            groupColumn,
            count,
            isFirstColumn
          }));
        } // Third, just display unformatted value and child count (also applied for features groupRenderer that do
      // not output any html of their own)

      if (isFirstColumn && html == null && applyDefault && cellElement.childElementCount === 0) {
        html = `${meta.groupRowFor === '__novalue__' ? '' : meta.groupRowFor} (${count})`;
      }
    } else if (me.groupRenderer) {
      // groupRenderer could return nothing and just apply changes directly to DOM element
      html = me.groupRenderer(renderData);
    } // Renderers could return nothing and just apply changes directly to DOM element

    if (typeof html === 'string') {
      cellElement.innerHTML = html;
    } else if (typeof html === 'object') {
      DomSync.sync({
        targetElement: cellElement,
        domConfig: {
          onlyChildren: true,
          children: Array.isArray(html) ? html : [html]
        }
      });
    } // If groupRenderer added elements to the cell, we need to remember that to clear it on re-usage as a normal cell

    if (cellElement.childElementCount > 0) {
      cellElement._hasHtml = true;
    }

    return cellElement.innerHTML;
  }
  /**
   * Called when an header is rendered, adds grouping icon if grouped by that column.
   * @private
   * @param headerContainerElement
   */

  renderHeader(headerContainerElement) {
    const {
      store,
      grid
    } = this;

    if (store.isGrouped) {
      // Sorted from start, reflect in rendering
      for (const groupInfo of store.groupers) {
        // Might be grouping by field without column, which is valid
        const column = grid.columns.get(groupInfo.field),
              header = column && grid.getHeaderElement(column.id); // IE11 doesnt support this
        //header && header.classList.add('b-group', groupInfo.ascending ? 'b-asc' : 'b-desc');

        if (header) {
          header.classList.add('b-group');
          header.classList.add(groupInfo.ascending ? 'b-asc' : 'b-desc');
        }
      }
    }
  } //endregion
  //region Context menu

  /**
   * Supply items for headers context menu.
   * @param {Object} options Contains menu items and extra data retrieved from the menu target.
   * @param {Grid.column.Column} options.column Column for which the menu will be shown
   * @param {Object} options.items A named object to describe menu items
   * @internal
   */

  populateHeaderMenu({
    column,
    items
  }) {
    const me = this;

    if (column.groupable !== false) {
      items.groupAsc = {
        text: 'L{groupAscending}',
        localeClass: me,
        icon: 'b-fw-icon b-icon-group-asc',
        name: 'groupAsc',
        cls: 'b-separator',
        weight: 400,
        disabled: me.disabled,
        onItem: () => me.store.group(column.field, true)
      };
      items.groupDesc = {
        text: 'L{groupDescending}',
        localeClass: me,
        icon: 'b-fw-icon b-icon-group-desc',
        name: 'groupDesc',
        weight: 410,
        disabled: me.disabled,
        onItem: () => me.store.group(column.field, false)
      };
    }

    if (me.store.isGrouped) {
      items.groupRemove = {
        text: 'L{stopGrouping}',
        localeClass: me,
        icon: 'b-fw-icon b-icon-clear',
        name: 'groupRemove',
        cls: column.groupable ? '' : 'b-separator',
        weight: 420,
        disabled: me.disabled,
        onItem: () => me.store.clearGroupers()
      };
    }
  }
  /**
   * Supply items to ColumnDragToolbar
   * @private
   */

  getColumnDragToolbarItems(column, items) {
    var _store$groupers;

    const me = this,
          store = me.store;
    items.push({
      text: 'L{groupAscendingShort}',
      group: 'L{group}',
      localeClass: me,
      icon: 'b-icon b-icon-group-asc',
      name: 'groupAsc',
      cls: 'b-separator',
      weight: 110,
      disabled: me.disabled,
      onDrop: ({
        column
      }) => store.group(column.field, true)
    });
    items.push({
      text: 'L{groupDescendingShort}',
      group: 'L{group}',
      localeClass: me,
      icon: 'b-icon b-icon-group-desc',
      name: 'groupDesc',
      weight: 110,
      disabled: me.disabled,
      onDrop: ({
        column
      }) => store.group(column.field, false)
    });
    const grouped = ((_store$groupers = store.groupers) === null || _store$groupers === void 0 ? void 0 : _store$groupers.some(col => col.field === column.field)) && !me.disabled;
    items.push({
      text: 'L{stopGroupingShort}',
      group: 'L{group}',
      localeClass: me,
      icon: 'b-icon b-icon-clear',
      name: 'groupRemove',
      disabled: !grouped,
      weight: 110,
      onDrop: ({
        column
      }) => store.removeGrouper(column.field)
    });
    return items;
  } //endregion
  //region Events - Store

  /**
   * Called when store grouping changes. Reflects on header and rerenders rows.
   * @private
   */

  onStoreGroup({
    groupers
  }) {
    const {
      grid
    } = this,
          {
      element
    } = grid,
          curGroupHeaders = element && DomHelper.children(element, '.b-grid-header.b-group');

    if (element) {
      for (const header of curGroupHeaders) {
        DomHelper.removeClasses(header, ['b-group', 'b-asc', 'b-desc']);
      }

      if (groupers) {
        for (const groupInfo of groupers) {
          const header = grid.getHeaderElementByField(groupInfo.field);

          if (header) {
            DomHelper.addClasses(header, ['b-group', groupInfo.ascending ? 'b-asc' : 'b-desc']);
          }
        }
      }
    }
  } //endregion
  //region Events - Grid

  /**
   * Store touches when user touches header, used in onElementTouchEnd.
   * @private
   */

  onElementTouchStart(event) {
    const me = this,
          target = event.target,
          header = DomHelper.up(target, '.b-grid-header'),
          column = header && me.grid.getColumnFromElement(header); // If it's a multi touch, group.

    if (event.touches.length > 1 && column && column.groupable !== false && !me.disabled) {
      me.store.group(column.field);
    }
  }
  /**
   * React to click on headers (to group by that column if [alt] is pressed) and on group rows (expand/collapse).
   * @private
   * @param event
   * @returns {Boolean}
   */

  onElementClick(event) {
    const me = this,
          {
      store
    } = me,
          {
      target
    } = event,
          row = DomHelper.up(target, '.b-group-row'),
          header = DomHelper.up(target, '.b-grid-header'),
          field = header === null || header === void 0 ? void 0 : header.dataset.column; // prevent expand/collapse if disabled or clicked on item with own handler

    if (target.classList.contains('b-resizer') || me.disabled || target.classList.contains('b-action-item')) {
      return;
    } // Header

    if (header && field) {
      var _store$groupers2;

      const columnGrouper = (_store$groupers2 = store.groupers) === null || _store$groupers2 === void 0 ? void 0 : _store$groupers2.find(g => g.field === field); // Store has a grouper for this column's field; flip grouper order

      if (columnGrouper && !event.shiftKey) {
        store.group(field, !columnGrouper.ascending);
        return false;
      } // Group or ungroup
      else if (event.shiftKey) {
          const column = me.grid.columns.get(field);

          if (column.groupable !== false) {
            if (event.altKey) {
              store.removeGrouper(field);
            } else {
              store.group(field);
            }
          }
        }

      event.preventDefault();
    } // Anywhere on group-row

    if (row) {
      me.internalToggleCollapse(DomDataStore.get(row).id);
      return false;
    }
  }
  /**
   * Toggle groups with [space].
   * @private
   * @param event
   * @returns {*}
   */

  onElementKeyDown(event) {
    const me = this; // only catch space on grid element, not in editors etc...

    if (!me.disabled && me.store.isGrouped && event.target === me.grid.focusElement && event.key === ' ' && me.grid.focusedCell) {
      event.preventDefault();
      me.internalToggleCollapse(me.grid.focusedCell.id);
    }
  } //endregion

}
Group._$name = 'Group';
GridFeatureManager.registerFeature(Group, true, ['Grid', 'Scheduler']);

/**
 * @module Grid/feature/HeaderMenu
 */

/**
 * Right click column header or focus it and press SPACE key to show the context menu for headers.
 *
 * ### Default header menu items
 *
 * The Header menu has no default items provided by the `HeaderMenu` feature, but there are other features
 * that populate the header menu with the following items:
 *
 * | Reference         | Text                              | Weight | Feature                                        | Description                                      |
 * |-------------------|-----------------------------------|--------|------------------------------------------------|--------------------------------------------------|
 * | `filter`          | Filter                            | 100    | {@link Grid.feature.Filter Filter}             | Shows the filter popup to add a filter           |
 * | `editFilter`      | Edit filter                       | 100    | {@link Grid.feature.Filter Filter}             | Shows the filter popup to change/remove a filter |
 * | `removeFilter`    | Remove filter                     | 110    | {@link Grid.feature.Filter Filter}             | Stops filtering by selected column field         |
 * | `toggleFilterBar` | Hide filter bar / Show filter bar | 120    | {@link Grid.feature.FilterBar FilterBar}       | Toggles filter bar visibility                    |
 * | `columnPicker`    | Columns                           | 200    | {@link Grid.feature.ColumnPicker ColumnPicker} | Shows a submenu to control columns visibility    |
 * | \>column.id*      | column.text*                      |        | {@link Grid.feature.ColumnPicker ColumnPicker} | Check item to hide/show corresponding column     |
 * | `hideColumn`      | Hide column                       | 210    | {@link Grid.feature.ColumnPicker ColumnPicker} | Hides selected column                            |
 * | `sortAsc`         | Sort ascending                    | 300    | {@link Grid.feature.Sort Sort}                 | Sort by the column field in ascending order      |
 * | `sortDesc`        | Sort descending                   | 310    | {@link Grid.feature.Sort Sort}                 | Sortby the column field in descending order      |
 * | `multiSort`       | Multi sort                        | 320    | {@link Grid.feature.Sort Sort}                 | Shows a submenu to control multi-sorting         |
 * | \>`addSortAsc`    | Add ascending sorting             | 330    | {@link Grid.feature.Sort Sort}                 | Adds ascending sorter using the column field     |
 * | \>`addSortDesc`   | Add descending sorting            | 340    | {@link Grid.feature.Sort Sort}                 | Adds descending sorter using the column field    |
 * | \>`removeSorter`  | Remove sorter                     | 350    | {@link Grid.feature.Sort Sort}                 | Stops sorting by selected column field           |
 * | `groupAsc`        | Group ascending                   | 400    | {@link Grid.feature.Group Group}               | Group by the column field in ascending order     |
 * | `groupDesc`       | Group descending                  | 410    | {@link Grid.feature.Group Group}               | Groupby the column field in descending order     |
 * | `groupRemove`     | Stop grouping                     | 420    | {@link Grid.feature.Group Group}               | Stops grouping                                   |
 *
 * \* - items that are generated dynamically
 *
 * \> - first level of submenu
 *
 * ### Customizing the menu items
 *
 * The menu items in the Header menu can be customized, existing items can be changed or removed,
 * and new items can be added. This is handled using the `items` config of the feature.
 *
 * Add extra items for all columns:
 *
 * ```javascript
 * const grid = new Grid({
 *   features : {
 *     headerMenu : {
 *       items : {
 *         extraItem : { text: 'My header item', icon: 'fa fa-car', weight: 200, onItem : () => ... }
 *       }
 *     }
 *   }
 * });
 * ```
 *
 * It is also possible to add items using columns config. See examples below.
 *
 * Add extra items for a single column:
 *
 * ```javascript
 * const grid = new Grid({
 *   columns: [
 *     {
 *       field: 'name',
 *       text: 'Name',
 *       headerMenuItems: {
 *         columnItem : { text: 'My unique header item', icon: 'fa fa-flask', onItem : () => ... }
 *       }
 *     }
 *   ]
 * });
 * ```
 *
 * Remove built in item:
 *
 * ```javascript
 * const grid = new Grid({
 *   features : {
 *     headerMenu : {
 *       items : {
 *          // Hide 'Stop grouping'
 *          groupRemove : false
 *       }
 *     }
 *   }
 * });
 * ```
 *
 * Customize built in item:
 *
 * ```javascript
 * const grid = new Grid({
 *   features : {
 *     headerMenu : {
 *       items : {
 *          hideColumn : {
 *              text : 'Bye bye column'
 *          }
 *       }
 *     }
 *   }
 * });
 * ```
 *
 * It is also possible to manipulate the default items and add new items in the processing function:
 *
 * ```javascript
 * const grid = new Grid({
 *   features : {
 *     headerMenu : {
 *       processItems({items, record}) {
 *           if (record.cost > 5000) {
 *              items.myItem = { text : 'Split cost' };
 *           }
 *       }
 *     }
 *   }
 * });
 * ```
 *
 * Full information of the menu customization can be found in the "Customizing the Cell menu and the Header menu" guide.
 *
 * This feature is <strong>enabled</strong> by default.
 *
 * @extends Grid/feature/base/ContextMenuBase
 * @demo Grid/filtering
 * @classtype headerMenu
 * @externalexample feature/HeaderMenu.js
 */

class HeaderMenu extends ContextMenuBase {
  //region Config
  static get $name() {
    return 'HeaderMenu';
  }

  static get defaultConfig() {
    return {
      /**
       * A function called before displaying the menu that allows manipulations of its items.
       * Returning `false` from this function prevents the menu being shown.
       *
       * ```javascript
       *   features         : {
       *       headerMenu : {
       *           processItems({ column, items }) {
       *               // Add or hide existing items here as needed
       *               items.myAction = {
       *                   text   : 'Cool action',
       *                   icon   : 'b-fa b-fa-fw b-fa-ban',
       *                   onItem : () => console.log('Some coolness'),
       *                   weight : 300 // Move to end
       *               };
       *
       *               // Hide column picker
       *               items.columnPicker.hidden = true;
       *           }
       *       }
       *   },
       * ```
       * @param {Object} context An object with information about the menu being shown
       * @param {Grid.column.Column} context.column The current column
       * @param {Object} context.items An object containing the {@link Core.widget.MenuItem menu item} configs keyed by their id
       * @param {Event} context.event The DOM event object that triggered the show
       * @config {Function}
       * @preventable
       */
      processItems: null,

      /**
       * This is a preconfigured set of items used to create the default context menu.
       *
       * The `items` provided by this feature are listed in the intro section of this class. You can
       * configure existing items by passing a configuration object to the keyed items.
       *
       * To remove existing
       * items, set corresponding keys to `false`
       *
       * ```javascript
       * const scheduler = new Scheduler({
       *     features : {
       *         headerMenu : {
       *             items : {
       *                 filter        : false,
       *                 columnPicker  : false
       *             }
       *         }
       *     }
       * });
       * ```
       *
       * See the feature config in the above example for details.
       *
       * @config {Object} items
       */
      items: null,
      type: 'header',
      // private "hack" to provide backward compatibility for deprecated HeaderContextMenu feature in Scheduler
      _showForTimeAxis: false
    };
  }

  static get pluginConfig() {
    const config = super.pluginConfig;
    config.chain.push('populateHeaderMenu');
    return config;
  } //endregion
  //region Events

  /**
   * Fired from grid before the context menu is shown for a header.
   * Allows manipulation of the items to show in the same way as in the {@link #config-processItems}.
   *
   * Returning `false` from a listener prevents the menu from being shown.
   *
   * @event headerMenuBeforeShow
   * @preventable
   * @param {Grid.view.Grid} source The grid
   * @param {Core.widget.Menu} menu The menu
   * @param {Object} items Menu item configs
   * @param {Grid.column.Column} column Column
   */

  /**
   * Fired from grid after the context menu is shown for a header
   * @event headerMenuShow
   * @param {Grid.view.Grid} source The grid
   * @param {Core.widget.Menu} menu The menu
   * @param {Object} items Menu item configs
   * @param {Grid.column.Column} column Column
   */

  /**
   * Fired from grid when an item is selected in the header context menu.
   * @event headerMenuItem
   * @param {Grid.view.Grid} source The grid
   * @param {Core.widget.Menu} menu The menu
   * @param {Object} item Selected menu item
   * @param {Grid.column.Column} column Column
   */

  /**
   * Fired from grid when a check item is toggled in the header context menu.
   * @event headerMenuToggleItem
   * @param {Grid.view.Grid} source The grid
   * @param {Core.widget.Menu} menu The menu
   * @param {Object} item Selected menu item
   * @param {Grid.column.Column} column Column
   * @param {Boolean} checked Checked or not
   */
  //endregion

  onElementKeyDown(event) {
    if (!event.handled && event.target.matches('.b-grid-header.b-depth-0')) {
      switch (event.key) {
        case ' ':
          this.internalShowContextMenu(this.createContextMenuEventForElement(event.target));
          break;
      }
    }
  } //region Menu handlers

  shouldShowMenu(eventParams) {
    const {
      column
    } = eventParams; // TODO: remove "this._showForTimeAxis || " when support for HeaderContextMenu feature in Scheduler is dropped.

    return column && column.enableHeaderContextMenu !== false && (this._showForTimeAxis || column !== this.client.timeAxisColumn);
  } // TODO: remove "showContextMenu" override completely when support for HeaderContextMenu feature in Scheduler is dropped.

  showContextMenu({
    column
  }) {
    super.showContextMenu(...arguments);

    if (column === this.client.timeAxisColumn && this.menu) {
      // the TimeAxis's context menu probably will cause scrolls because it manipulates the dates.
      // The menu should not hide on scroll when for a TimeAxisColumn
      this.menu.scrollAction = 'realign';
    }
  }

  getDataFromEvent(event) {
    return ObjectHelper.assign(super.getDataFromEvent(event), this.client.getHeaderDataFromEvent(event));
  }

  populateHeaderMenu({
    items,
    column
  }) {
    if (column !== null && column !== void 0 && column.headerMenuItems) {
      if (Array.isArray(column.headerMenuItems)) {
        VersionHelper.deprecate('Grid', '5.0.0', '`headerMenuItems` column config specified as an array is deprecated, need to specify the config as a named object. Please see https://bryntum.com/docs/grid/#guides/upgrades/4.0.0.md for more information.');
      } // Array works smoothly since number index turns into a key for named object

      ObjectHelper.merge(items, column.headerMenuItems);
    }

    return items;
  }

}
HeaderMenu.featureClass = '';
HeaderMenu._$name = 'HeaderMenu';
GridFeatureManager.registerFeature(HeaderMenu, true);

//TODO: Handle date columns
/**
 * @module Grid/feature/QuickFind
 */

/**
 * Feature that allows the user to search in a column by focusing a cell and typing. Navigate between hits using the
 * keyboard, [f3] or [ctrl]/[cmd] + [g] moves to next, also pressing [shift] moves to previous.
 *
 * This feature is <strong>disabled</strong> by default.
 *
 * @extends Core/mixin/InstancePlugin
 *
 * @example
 * // enable QuickFind
 * let grid = new Grid({
 *   features: {
 *     quickFind: true
 *   }
 * });
 *
 * // navigate to next hit programmatically
 * grid.features.quickFind.gotoNextHit();
 *
 * @demo Grid/quickfind
 * @classtype quickFind
 * @externalexample feature/QuickFind.js
 */

class QuickFind extends InstancePlugin {
  //region Config
  static get $name() {
    return 'QuickFind';
  }

  static get defaultConfig() {
    return {
      mode: 'header',
      find: ''
    };
  } // Plugin configuration. This plugin chains some of the functions in Grid.

  static get pluginConfig() {
    return {
      chain: ['onElementKeyDown', 'onElementKeyPress', 'onCellNavigate']
    };
  } //endregion
  //region Init

  construct(grid, config) {
    this.grid = grid;
    super.construct(grid, config);
  }

  doDisable(disable) {
    if (disable) {
      this.clear();
    }

    super.doDisable(disable);
  }

  get store() {
    return this.grid.store;
  } //endregion
  //region Show/hide QuickFind

  /**
   * Shows a "searchfield" in the header. Triggered automatically when you have a cell focused and start typing.
   * @private
   */

  showQuickFind() {
    const me = this,
          header = me.grid.getHeaderElement(me.columnId);

    if (header) {
      if (!me.headerField) {
        const [element, field, badge] = DomHelper.createElement({
          tag: 'div',
          className: 'b-quick-hit-header',
          children: [{
            tag: 'div',
            className: 'b-quick-hit-field'
          }, {
            tag: 'div',
            className: 'b-quick-hit-badge'
          }]
        }, true);

        if (me.mode === 'header') {
          header.appendChild(element);
        } else {
          element.className += ' b-quick-hit-mode-grid';
          me.grid.element.appendChild(element);
        }

        me.headerField = {
          header: element,
          field: field,
          badge: badge,
          colHeader: header
        };
      }

      me.headerField.field.innerHTML = me.find;
      me.headerField.badge.innerHTML = me.found.length;
      header.classList.add('b-quick-find-header');

      if (!me.renderListenerInitialized) {
        me.grid.rowManager.on({
          rendercell: me.renderCell,
          thisObj: me
        });
        me.renderListenerInitialized = true;
      }
    }
  }
  /**
   * Hide the "searchfield" and remove highlighted hits. Called automatically when pressing [esc] or backspacing away
   * the keywords.
   * @private
   */

  hideQuickFind() {
    const me = this,
          {
      grid,
      headerField
    } = me; // rerender cells to remove quick-find markup

    for (const hit of me.prevFound || me.found) {
      const row = grid.getRowById(hit.id);

      if (row) {
        const cellElement = row.getCell(me.columnId);
        cellElement._content = null;
        row.renderCell({
          cellElement,
          record: hit.data
        });
      }
    }

    if (headerField) {
      headerField.header.parentNode.removeChild(headerField.header);
      headerField.colHeader.classList.remove('b-quick-find-header');
      me.headerField = null;
    }

    if (me.renderListenerInitialized) {
      grid.rowManager.un({
        rendercell: me.renderCell
      }, me);
      me.renderListenerInitialized = false;
    }

    grid.trigger('hideQuickFind');
  } //endregion
  //region Search

  /**
   * Performs a search and highlights hits. If find is empty, QuickFind is closed.
   * @param {String} find Text to search for
   * @param {String} columnFieldOrId Column to search
   */

  search(find, columnFieldOrId = this.columnId) {
    const me = this,
          {
      grid,
      currentColumn
    } = me,
          column = grid.columns.getById(columnFieldOrId) || grid.columns.get(columnFieldOrId),
          found = me.store.findByField(column.field, find);
    let i = 1;
    Object.assign(me, {
      foundMap: {},
      prevFound: me.found,
      found: found,
      find: find,
      columnId: column.id
    });

    if (find) {
      me.showQuickFind();
    } else {
      me.hideQuickFind();
    } // reset column to use its normal settings for htmlEncoding

    if (currentColumn && currentColumn !== column) currentColumn.disableHtmlEncode = false; // clear old hits

    for (const cellElement of DomHelper.children(grid.element, '.b-quick-hit')) {
      //IE11 doesnt support this
      //cell.classList.remove('b-quick-hit', 'b-quick-hit-cell');
      cellElement.classList.remove('b-quick-hit');
      cellElement.classList.remove('b-quick-hit-cell'); // rerender cell to remove quick-hit-text

      const row = DomDataStore.get(cellElement).row; // Cell caches its content for faster comparison, need to clear cache to have it rerendered

      cellElement._content = cellElement.lastDomConfig = null; // We need to clear the real content because if DomSync is being used
      // It will not work with QuickFind's extra, injected content.

      cellElement.innerHTML = '';
      row.renderCell({
        cellElement
      });
    } // want to set innerHTML each time for cell decoration to work

    column.disableHtmlEncode = true;
    me.currentColumn = column;
    if (!found) return;

    if (found.length > 0) {
      me.gotoClosestHit(grid.focusedCell, found);
    } // highlight hits for visible cells

    for (const hit of found) {
      me.foundMap[hit.id] = i++;
      const row = grid.getRowById(hit.data.id);
      row === null || row === void 0 ? void 0 : row.renderCell({
        cellElement: row.getCell(column.id)
      }); // limit highlighted hits

      if (i > 1000) break;
    }

    grid.trigger('quickFind', {
      find,
      found
    });
  }
  /**
   * Clears and closes QuickFind.
   */

  clear() {
    var _this$found;

    if ((_this$found = this.found) !== null && _this$found !== void 0 && _this$found.length) {
      this.search('');
    }
  }
  /**
   * Number of results found
   * @type {Number}
   * @readonly
   */

  get foundCount() {
    var _this$found$length, _this$found2;

    return (_this$found$length = (_this$found2 = this.found) === null || _this$found2 === void 0 ? void 0 : _this$found2.length) !== null && _this$found$length !== void 0 ? _this$found$length : 0;
  }
  /**
   * Found results (as returned by Store#findByField), an array in format { index: x, data: record }
   * @member {Object[]} found
   * @readonly
   */
  //endregion
  //region Navigation

  /**
   * Go to specified hit.
   * @param {Number} index
   */

  gotoHit(index) {
    const nextHit = this.found[index];

    if (nextHit) {
      this.grid.focusCell({
        columnId: this.columnId,
        id: nextHit.id
      });
    }

    return !!nextHit;
  }

  gotoClosestHit(focusedCell, found) {
    const focusedIndex = focusedCell ? this.grid.store.indexOf(focusedCell.id) : 0,
          foundSorted = found.slice().sort((a, b) => Math.abs(a.index - focusedIndex) - Math.abs(b.index - focusedIndex));
    this.gotoHit(found.indexOf(foundSorted[0]));
  }
  /**
   * Go to the first hit.
   */

  gotoFirstHit() {
    this.gotoHit(0);
  }
  /**
   * Go to the last hit.
   */

  gotoLastHit() {
    this.gotoHit(this.found.length - 1);
  }
  /**
   * Select the next hit, scrolling it into view. Triggered with [f3] or [ctrl]/[cmd] + [g].
   */

  gotoNextHit() {
    var _grid$lastFocusedCell;

    const me = this,
          {
      grid
    } = me,
          // start from focused cell, or if focus has left grid use lastFocusedCell
    currentId = grid._focusedCell ? grid._focusedCell.id : (_grid$lastFocusedCell = grid.lastFocusedCell) === null || _grid$lastFocusedCell === void 0 ? void 0 : _grid$lastFocusedCell.id,
          currentIndex = grid.store.indexOf(currentId) || 0,
          nextHit = me.found.find(hit => hit.index > currentIndex);

    if (nextHit) {
      grid.focusCell({
        columnId: me.columnId,
        id: nextHit.id
      });
    } else {
      me.gotoFirstHit();
    }
  }
  /**
   * Select the previous hit, scrolling it into view. Triggered with [shift] + [f3] or [shift] + [ctrl]/[cmd] + [g].
   */

  gotoPrevHit() {
    var _grid$lastFocusedCell2;

    const me = this,
          {
      grid,
      found
    } = me,
          currentId = grid._focusedCell ? grid._focusedCell.id : (_grid$lastFocusedCell2 = grid.lastFocusedCell) === null || _grid$lastFocusedCell2 === void 0 ? void 0 : _grid$lastFocusedCell2.id,
          currentIndex = grid.store.indexOf(currentId) || 0;
    let prevHit;

    if (!found.length) {
      return;
    }

    for (let i = found.length - 1; i--; i >= 0) {
      if (found[i].index < currentIndex) {
        prevHit = found[i];
        break;
      }
    }

    if (prevHit) {
      grid.focusCell({
        columnId: me.columnId,
        id: prevHit.id
      });
    } else {
      me.gotoLastHit();
    }
  } //endregion
  //region Render

  /**
   * Called from SubGrid when a cell is rendered.
   * @private
   */

  renderCell(renderData) {
    var _me$foundMap;

    const me = this,
          cellElement = renderData.cellElement,
          foundMap = me.columnId === renderData.column.id && ((_me$foundMap = me.foundMap) === null || _me$foundMap === void 0 ? void 0 : _me$foundMap[renderData.record.id]);

    if (foundMap) {
      // Check also TreeColumn's special internal `shouldHtmlEncode` flag
      const htmlEncoded = renderData.column.htmlEncode || renderData.column.shouldHtmlEncode; // highlight cell

      cellElement.classList.add('b-quick-hit'); // Special treatment of columns outputting HTML, just highlight whole cell

      if (!htmlEncoded) {
        cellElement.classList.add('b-quick-hit-cell');
      } else {
        var _html;

        // if features have added other stuff to the cell, value is in div.b-grid-cell-value
        // highlight in cell if found in innerHTML
        let inner = DomHelper.down(cellElement, '.b-grid-cell-value,.b-tree-cell-value') || cellElement,
            html = inner.innerText,
            where = (_html = html) === null || _html === void 0 ? void 0 : _html.toLowerCase().indexOf(me.find.toLowerCase());

        if (where > -1) {
          const end = where + me.find.length,
                casedFind = html.slice(where, end);
          let spaceChar = ''; // Insert a space if matching char preceded by whitespace

          if (html[where - 1] === ' ') {
            spaceChar = '&nbsp;';
          }

          html = html.slice(0, where) + `<span class="b-quick-hit-text">${spaceChar}${casedFind}</span>` + html.slice(end);
          inner.innerHTML = html + `<div class="b-quick-hit-cell-badge">${foundMap}</div>`;
        } else {
          cellElement.classList.add('b-quick-hit-cell');
        }
      }
    }
  } //endregion
  //region Events

  /**
   * Chained function called on grids keydown event. Handles backspace, escape, f3 and ctrl/cmd + g keys.
   * @private
   * @param event KeyboardEvent
   */

  onElementKeyDown(event) {
    const me = this,
          filterFeature = me.grid.features.filter; // Only react to keystrokes on grid cell elements. IE11 gets event.target wrong compared to other browsers...

    if (me.disabled || DomHelper.up(event.target, BrowserHelper.isIE11 ? '.b-widget:not(.b-grid-subgrid):not(.b-grid)' : '.b-widget:not(.b-grid)')) {
      return;
    }

    if (me.find.length > 0) {
      // backspace
      if (event.key === 'Backspace') {
        event.preventDefault();
        me.find = me.find.substr(0, me.find.length - 1);
        me.search(me.find);
      } // escape
      else if (event.key === 'Escape') {
          event.preventDefault();
          me.find = '';
          me.search(me.find);
        } // F3 or CTRL+g
        else if (event.key === 'F3' || event.key.toLowerCase() === 'g' && (event.ctrlKey || event.metaKey)) {
            event.preventDefault();

            if (event.shiftKey) {
              me.gotoPrevHit();
            } else {
              me.gotoNextHit();
            }
          } // Ctrl+Shift+F
          else if (filterFeature && me.columnId && me.foundCount && event.ctrlKey && event.shiftKey && event.key === 'F') {
              me.clear();
              filterFeature.showFilterEditor(me.grid.columns.getById(me.columnId), me.find);
            }
    }
  }
  /**
   * Chained function called on grids keypress event. Handles input for "searchfield".
   * @private
   * @param event
   */

  onElementKeyPress(event) {
    const me = this,
          {
      grid
    } = me; // Only react to keystrokes on grid cell elements

    if (me.disabled || DomHelper.up(event.target, BrowserHelper.isIE11 ? '.b-widget:not(.b-grid-subgrid):not(.b-grid)' : '.b-widget:not(.b-grid)') || event.key === 'Enter') {
      return;
    }

    if (grid._focusedCell) {
      const column = grid.columns.getById(grid._focusedCell.columnId); // if trying to search in invalid column, it's a hard failure

      if (column && column.searchable !== false) {
        var _event$key;

        me.columnId = grid._focusedCell.columnId;

        if (((_event$key = event.key) === null || _event$key === void 0 ? void 0 : _event$key.length) === 1) {
          me.find += event.key;
          me.search(me.find);
        }
      }
    }
  }

  onCellNavigate(grid, fromCellSelector, toCellSelector) {
    const me = this,
          found = me.prevFound || me.found;

    if (found && (!toCellSelector || toCellSelector.columnId !== me.columnId)) {
      me.clear();
    }
  } //endregion

}
QuickFind._$name = 'QuickFind';
GridFeatureManager.registerFeature(QuickFind);

/**
 * @module Grid/feature/RegionResize
 */

/**
 * Makes the splitter between grid section draggable so you can resize grid sections.
 *
 * This feature is <strong>disabled</strong> by default.
 *
 * @extends Core/mixin/InstancePlugin
 *
 * @example
 * // enable RegionResize
 * let grid = new Grid({
 *   features: {
 *     regionResize: true
 *   }
 * });
 *
 * @demo Grid/features
 * @classtype regionResize
 * @externalexample feature/RegionResize.js
 */

class RegionResize extends InstancePlugin {
  // region Init
  static get $name() {
    return 'RegionResize';
  }

  construct(grid, config) {
    this.grid = grid;
    super.construct(grid, config);
  }

  doDestroy() {
    // TODO: Cleanup
    super.doDestroy();
  } //endregion
  //region Plugin config
  // Plugin configuration. This plugin chains some of the functions in Grid.

  static get pluginConfig() {
    return {
      chain: ['onElementTouchStart', 'onElementTouchMove', 'onElementTouchEnd', 'onElementMouseDown', 'onElementMouseMove', 'onElementDblClick', 'onElementMouseUp', 'onSubGridCollapse', 'onSubGridExpand', 'render']
    };
  } //endregion

  onElementDblClick(event) {
    const me = this,
          grid = me.grid,
          splitterEl = DomHelper.up(event.target, '.b-grid-splitter-collapsed'); // If collapsed splitter is dblclicked and region is not expanding
    // It is unlikely that user might dblclick splitter twice and even if he does, nothing should happen.
    // But just in case lets not expand twice.

    if (splitterEl && !me.expanding) {
      me.expanding = true;
      let region = splitterEl.dataset.region,
          subGrid = grid.getSubGrid(region); // Usually collapsed splitter means corresponding region is collapsed. But in case of last two regions one
      // splitter can be collapsed in two directions. So, if corresponding region is expanded then last one is collapsed

      if (!subGrid.collapsed) {
        region = grid.getLastRegions()[1];
        subGrid = grid.getSubGrid(region);
      }

      subGrid.expand().then(() => me.expanding = false);
    }
  } //region Move splitter

  /**
   * Begin moving splitter.
   * @private
   * @param splitterElement Splitter element
   * @param clientX Initial x position from which new width will be calculated on move
   */

  startMove(splitterElement, clientX) {
    const me = this,
          {
      grid
    } = me,
          region = splitterElement.dataset.region,
          gridEl = grid.element,
          nextRegion = grid.regions[grid.regions.indexOf(region) + 1],
          nextSubGrid = grid.getSubGrid(nextRegion),
          splitterSubGrid = grid.getSubGrid(region);
    let subGrid = splitterSubGrid,
        flip = 1;

    if (subGrid.flex != null) {
      // If subgrid has flex, check if next one does not
      if (nextSubGrid.flex == null) {
        subGrid = nextSubGrid;
        flip = -1;
      }
    }

    if (splitterElement.classList.contains('b-grid-splitter-collapsed')) {
      return;
    }

    const availableWidth = subGrid.element.offsetWidth + nextSubGrid.element.offsetWidth;
    me.dragContext = {
      element: splitterElement,
      headerEl: subGrid.header.element,
      subGridEl: subGrid.element,
      subGrid,
      splitterSubGrid,
      originalWidth: subGrid.element.offsetWidth,
      originalX: clientX,
      minWidth: subGrid.minWidth || 0,
      maxWidth: Math.min(availableWidth, subGrid.maxWidth || availableWidth),
      flip
    };
    gridEl.classList.add('b-moving-splitter');
    splitterSubGrid.toggleSplitterCls('b-moving');
  }
  /**
   * Stop moving splitter.
   * @private
   */

  endMove() {
    const dragContext = this.dragContext;

    if (dragContext) {
      this.grid.element.classList.remove('b-moving-splitter');
      dragContext.splitterSubGrid.toggleSplitterCls('b-moving', false);
      this.dragContext = null;
    }
  }

  onCollapseClick(subGrid, splitterEl) {
    const me = this,
          grid = me.grid,
          region = splitterEl.dataset.region,
          regions = grid.getLastRegions(); // Last splitter in the grid is responsible for collapsing/expanding last 2 regions and is always related to the
    // left one. Check if we are working with last splitter

    if (regions[0] === region) {
      const lastSubGrid = grid.getSubGrid(regions[1]);

      if (lastSubGrid.collapsed) {
        lastSubGrid.expand();
        return;
      }
    }

    subGrid.collapse();
  }

  onExpandClick(subGrid, splitterEl) {
    const me = this,
          grid = me.grid,
          region = splitterEl.dataset.region,
          regions = grid.getLastRegions(); // Last splitter in the grid is responsible for collapsing/expanding last 2 regions and is always related to the
    // left one. Check if we are working with last splitter

    if (regions[0] === region) {
      if (!subGrid.collapsed) {
        const lastSubGrid = grid.getSubGrid(regions[1]);
        lastSubGrid.collapse();
        return;
      }
    }

    subGrid.expand();
  }
  /**
   * Update splitter position.
   * @private
   * @param newClientX
   */

  updateMove(newClientX) {
    const {
      dragContext
    } = this;

    if (dragContext) {
      const difX = newClientX - dragContext.originalX,
            newWidth = Math.min(dragContext.maxWidth, dragContext.originalWidth + difX * dragContext.flip); // SubGrids monitor their own size and keep any splitters synced

      dragContext.subGrid.width = Math.max(newWidth, dragContext.minWidth);
    }
  } //endregion
  //region Events

  /**
   * Start moving splitter on touch start.
   * @private
   * @param event
   */

  onElementTouchStart(event) {
    const target = event.target.closest('.b-grid-splitter');

    if (target) {
      this.startMove(target, event.touches[0].clientX);
    }
  }
  /**
   * Move splitter on touch move.
   * @private
   * @param event
   */

  onElementTouchMove(event) {
    if (this.dragContext) {
      this.updateMove(event.touches[0].clientX);
      event.preventDefault();
    }
  }
  /**
   * Stop moving splitter on touch end.
   * @private
   * @param event
   */

  onElementTouchEnd(event) {
    if (this.dragContext) {
      this.endMove();
      event.preventDefault();
    }
  }
  /**
   * Start moving splitter on mouse down (on splitter).
   * @private
   * @param event
   */

  onElementMouseDown(event) {
    const me = this,
          target = event.target,
          splitter = target.closest(':not(.b-row-reordering):not(.b-dragging-event):not(.b-dragging-task):not(.b-dragging-header):not(.b-dragselecting) .b-grid-splitter'),
          subGrid = splitter && me.grid.getSubGrid(splitter.dataset.region); // Only care about left clicks, avoids a bug found by monkeys

    if (event.button === 0) {
      // In case of touch screen inner splitter has 100% height and we should handle
      // it as target too
      if (target.classList.contains('b-grid-splitter') || target.classList.contains('b-grid-splitter-inner')) {
        me.startMove(splitter, event.clientX);
      } else if (target.classList.contains('b-icon-collapse-gridregion')) {
        me.onCollapseClick(subGrid, splitter);
      } else if (target.classList.contains('b-icon-expand-gridregion')) {
        me.onExpandClick(subGrid, splitter);
      }
    }
  }
  /**
   * Move splitter on mouse move.
   * @private
   * @param event
   */

  onElementMouseMove(event) {
    if (this.dragContext) {
      this.updateMove(event.clientX);
      event.preventDefault();
    }
  }
  /**
   * Stop moving splitter on mouse up.
   * @private
   * @param event
   */

  onElementMouseUp(event) {
    if (this.dragContext) {
      this.endMove();
      event.preventDefault();
    }
  }

  onSubGridCollapse(subGrid) {
    const splitterEl = this.grid.resolveSplitter(subGrid),
          regions = this.grid.getLastRegions(); // if last region was collapsed

    if (regions[1] === subGrid.region) {
      splitterEl.classList.add('b-grid-splitter-allow-collapse');
    }
  }

  onSubGridExpand(subGrid) {
    const splitterEl = this.grid.resolveSplitter(subGrid);
    splitterEl.classList.remove('b-grid-splitter-allow-collapse');
  } //endregion

  render() {
    const {
      regions,
      subGrids
    } = this.grid; // Multiple regions, only allow collapsing to the edges by hiding buttons

    if (regions.length > 2) {
      // Only works in a 3 subgrid scenario. To support more subgrids we have to merge splitters or something
      // on collapse. Not going down that path currently...
      subGrids[regions[0]].splitterElement.classList.add('b-left-only');
      subGrids[regions[1]].splitterElement.classList.add('b-right-only');
    }
  }

}
RegionResize.featureClass = 'b-split';
RegionResize._$name = 'RegionResize';
GridFeatureManager.registerFeature(RegionResize);

/**
 * @module Grid/feature/RowReorder
 */
/**
 * Allows user to reorder rows by dragging them. To get notified about row reorder listen to `change` event
 * on the grid {@link Core.data.Store store}.
 *
 * This feature is **off** by default. For info on enabling it, see {@link Grid.view.mixin.GridFeatures}.
 * This feature is **enabled** by default for Gantt.
 *
 * If the grid is set to {@link Grid.view.Grid#config-readOnly}, reordering is disabled. Inside all event listeners you have access a `context` object which has a `record` property (the dragged record).
 *
 * You can validate the drag drop flow by listening to the `gridrowdrag` event. Inside this listener you have access the `index` property which is the target drop position.
 * For trees you get access to the `parent` record and `index`, where index means the child index inside the parent.
 *
 * ```
 * features : {
 *     rowReorder : {
 *         listeners : {
 *             gridRowDrag : ({ context }) => {
 *                // Here you have access to context.insertBefore, and additionally context.parent for trees
 *
 *                context.valid = false;
 *             }
 *         }
 *     }
 * }
 * ```
 *
 * Note, that this feature uses the concept of "insert before" when choosing a drop point in the data.
 *
 * So the dropped record's position is *before the visual next record's poition*.
 *
 * This may look like a pointless distinction, but consider the case when a Store is filtered.
 *
 * The record *above* may he several filtered out records below it. When unfiltered, the dropped
 * record will be *below* these because of the "insert before" behaviour.
 *
 * @extends Core/mixin/InstancePlugin
 * @demo Grid/rowreorder
 * @classtype rowReorder
 */

class RowReorder extends Delayable(InstancePlugin) {
  //region Events

  /**
   * Fired before dragging starts, return false to prevent the drag operation.
   * @preventable
   * @event gridRowBeforeDragStart
   * @param {Core.helper.DragHelper} source
   * @param {Object} context
   * @param {Core.data.Model} context.record [DEPRECATED] The dragged row record
   * @param {Core.data.Model[]} context.records The dragged row records
   * @param {MouseEvent|TouchEvent} event
   */

  /**
   * Fired when dragging starts.
   * @event gridRowDragStart
   * @param {Core.helper.DragHelper} source
   * @param {Object} context
   * @param {Core.data.Model} context.record [DEPRECATED] The dragged row record
   * @param {Core.data.Model[]} context.records The dragged row records
   * @param {MouseEvent|TouchEvent} event
   */

  /**
   * Fired while the row is being dragged, in the listener function you have access to `context.insertBefore` a grid / tree record, and additionally `context.parent` (a TreeNode) for trees. You can
   * signal that the drop position is valid or invalid by setting `context.valid = false;`
   * @event gridRowDrag
   * @param {Core.helper.DragHelper} source
   * @param {Object} context
   * @param {Boolean} context.valid Set this to true or false to indicate whether the drop position is valid.
   * @param {Core.data.Model} context.insertBefore The record to insert before (`null` if inserting at last position of a parent node)
   * @param {Core.data.Model} context.parent The parent record of the current drop position (only applicable for trees)
   * @param {Core.data.Model} context.record [DEPRECATED] The dragged row record
   * @param {Core.data.Model[]} context.records The dragged row records
   * @param {MouseEvent} event
   */

  /**
   * Fired before the row drop operation is finalized.
   * @event gridRowBeforeDropFinalize
   * @param {Core.helper.DragHelper} source
   * @param {Object} context
   * @param {Boolean} context.valid Set this to true or false to indicate whether the drop position is valid
   * @param {Core.data.Model} context.insertBefore The record to insert before (`null` if inserting at last position of a parent node)
   * @param {Core.data.Model} context.parent The parent record of the current drop position (only applicable for trees)
   * @param {Core.data.Model} context.record [DEPRECATED] The dragged row record
   * @param {Core.data.Model[]} context.records The dragged row records
   * @param {MouseEvent} event
   */

  /**
   * Fired after the row drop operation has completed, regardless of validity
   * @event gridRowDrop
   * @param {Core.helper.DragHelper} source
   * @param {Object} context
   * @param {Boolean} context.valid true or false depending on whether the drop position was valid
   * @param {Core.data.Model} context.insertBefore The record to insert before (`null` if inserting at last position of a parent node)
   * @param {Core.data.Model} context.parent The parent record of the current drop position (only applicable for trees)
   * @param {Core.data.Model} context.record [DEPRECATED] The dragged row record
   * @param {Core.data.Model[]} context.records The dragged row records
   * @param {MouseEvent} event
   */

  /**
   * Fired when a row drag operation is aborted
   * @event gridRowAbort
   * @param {Core.helper.DragHelper} source
   * @param {Object} context
   * @param {MouseEvent} event
   */
  //endregion
  //region Init
  static get $name() {
    return 'RowReorder';
  }

  static get defaultConfig() {
    return {
      /**
       * If hovering over a parent node for this period of a time in a tree, the node will expand
       * @config {Number}
       */
      hoverExpandTimeout: 1000,
      finalizeDelay: 300
    };
  }

  construct(grid, config) {
    this.grid = grid;
    super.construct(...arguments);
  }

  doDestroy() {
    var _this$dragHelper;

    (_this$dragHelper = this.dragHelper) === null || _this$dragHelper === void 0 ? void 0 : _this$dragHelper.destroy();
    super.doDestroy();
  }
  /**
   * Initialize drag & drop (called from render)
   * @private
   */

  init() {
    const me = this,
          {
      grid
    } = me;
    me.dragHelper = new DragHelper({
      name: 'rowReorder',
      mode: 'translateXY',
      cloneTarget: true,
      dragThreshold: 10,
      targetSelector: '.b-grid-row',
      lockX: true,
      transitionDuration: grid.transitionDuration,
      scrollManager: grid.scrollManager,
      dragWithin: grid.verticalScroller,
      outerElement: grid.verticalScroller,
      // Since parent nodes can expand after hovering, meaning original drag start position now refers to a different point in the tree
      ignoreSamePositionDrop: false,

      createProxy(element) {
        const clone = element.cloneNode(true),
              container = document.createElement('div');
        clone.removeAttribute('id'); // The containing element will be positioned instead

        clone.style.transform = '';
        container.appendChild(clone);

        if (grid.selectedRecords.length > 1) {
          const clone2 = clone.cloneNode(true);
          clone2.classList.add('b-row-dragging-multiple');
          container.appendChild(clone2);
        }

        DomHelper.removeClsGlobally(container, 'b-selected', 'b-hover', 'b-focused');
        return container;
      },

      listeners: {
        beforedragstart: me.onBeforeDragStart,
        dragstart: me.onDragStart,
        drag: me.onDrag,
        drop: me.onDrop,
        reset: me.onReset,
        prio: 10000,
        // To ensure our listener is run before the relayed listeners (for the outside world)
        thisObj: me
      }
    });
    me.dropIndicator = DomHelper.createElement({
      parent: grid.bodyContainer,
      className: 'b-row-drop-indicator'
    });
    me.relayEvents(me.dragHelper, ['beforeDragStart', 'dragStart', 'drag', 'abort'], 'gridRow');
  } //endregion
  //region Plugin config

  static get pluginConfig() {
    return {
      after: ['render']
    };
  } //endregion
  //region Events (drop)

  onBeforeDragStart({
    context
  }) {
    const me = this,
          grid = me.grid,
          targetSubGrid = grid.regions[0],
          subGridEl = grid.subGrids[targetSubGrid].element; // Only dragging enabled in the leftmost grid section

    if (me.disabled || grid.readOnly || !subGridEl.contains(context.element)) {
      return false;
    }

    const records = context.records = grid.selectedRecords.slice().sort((r1, r2) => grid.store.indexOf(r1) - grid.store.indexOf(r2));
    context.startRecord = grid.getRecordFromElement(context.element); // Backwards compat, @deprecated - remove in 5.0

    context.record = context.startRecord;
    return records.length > 0 && !records.some(rec => rec.isSpecialRow);
  }

  onDragStart({
    context
  }) {
    var _contextMenu$hideCont, _cellMenu$hideContext, _headerMenu$hideConte;

    const me = this,
          {
      cellEdit,
      contextMenu,
      cellMenu,
      headerMenu
    } = me.grid.features;

    if (cellEdit) {
      me.cellEditDisabledState = cellEdit.disabled;
      cellEdit.disabled = true; // prevent editing from being started through keystroke during row reordering
    } // TODO: 'contextMenu' is deprecated. Please see https://bryntum.com/docs/grid/#guides/upgrades/4.0.0.md for more information.

    contextMenu === null || contextMenu === void 0 ? void 0 : (_contextMenu$hideCont = contextMenu.hideContextMenu) === null || _contextMenu$hideCont === void 0 ? void 0 : _contextMenu$hideCont.call(contextMenu, false);
    cellMenu === null || cellMenu === void 0 ? void 0 : (_cellMenu$hideContext = cellMenu.hideContextMenu) === null || _cellMenu$hideContext === void 0 ? void 0 : _cellMenu$hideContext.call(cellMenu, false);
    headerMenu === null || headerMenu === void 0 ? void 0 : (_headerMenu$hideConte = headerMenu.hideContextMenu) === null || _headerMenu$hideConte === void 0 ? void 0 : _headerMenu$hideConte.call(headerMenu, false);
    me.grid.element.classList.add('b-row-reordering');
    const focusedCell = context.element.querySelector('.b-focused');
    focusedCell === null || focusedCell === void 0 ? void 0 : focusedCell.classList.remove('b-focused');
    DomHelper.removeClasses(context.element.firstElementChild, ['b-selected', 'b-hover']);
  }

  onDrag({
    context,
    event
  }) {
    const me = this,
          {
      grid
    } = me,
          {
      store,
      rowManager
    } = grid,
          {
      outerElement
    } = me.dragHelper; // Ignore if user drags outside grid area

    if (!outerElement.contains(event.target) || !event.target.closest('.b-grid-subgrid')) {
      context.valid = false;
      return;
    } // Chrome 85 has a bug, event.targets top is sometimes below event.pageX
    // https://bugs.chromium.org/p/chromium/issues/detail?id=1121915

    if (BrowserHelper.isChrome && !Rectangle.from(outerElement).contains(EventHelper.getPagePoint(event))) {
      return;
    }

    let valid = context.valid,
        row = grid.rowManager.getRowAt(event.clientY),
        overRecord,
        dataIndex,
        after,
        insertBefore;

    if (row) {
      const rowTop = row.top + grid._bodyRectangle.y - grid.scrollable.y,
            middleY = rowTop + row.height / 2;
      dataIndex = row.dataIndex;
      overRecord = row && store.getAt(dataIndex); // Drop after row below if mouse is in bottom half of hovered row

      after = event.clientY > middleY;
    } // Below the rows. Drop after last row
    else {
        dataIndex = store.count - 1;
        overRecord = store.last;
        row = grid.rowManager.getRow(dataIndex);
        after = true;
      } // Hovering the dragged record. This is a no-op.
    // But still gather the contextual data.

    if (overRecord === context.startRecord) {
      valid = false;
    }

    if (store.tree) {
      DomHelper.removeClsGlobally(grid.element, 'b-row-reordering-target-parent');
      insertBefore = after ? overRecord.nextSibling : overRecord; // For trees, prevent moving a parent into its own hierarchy

      if (context.records.some(rec => rec.contains(overRecord))) {
        valid = false;
      }

      context.parent = overRecord.parent;

      if (!context.parent.isRoot) {
        const parentRow = rowManager.getRowById(context.parent);

        if (parentRow) {
          parentRow.addCls('b-row-reordering-target-parent');
        }
      }

      me.clearTimeout(me.hoverTimer);

      if (overRecord && overRecord.isParent && !overRecord.isExpanded(store)) {
        me.hoverTimer = me.setTimeout(() => grid.expand(overRecord), me.hoverExpandTimeout);
      }
    } else {
      // Public property used for validation
      insertBefore = after ? store.getAt(dataIndex + 1) : overRecord;
    } // Provide visual clue to user of the drop position

    DomHelper.setTranslateY(me.dropIndicator, row.top + (after ? row.height : 0));
    context.insertBefore = insertBefore;
    context.valid = valid;
  }
  /**
   * Handle drop
   * @private
   */

  async onDrop(event) {
    const me = this,
          context = event.context;
    me.trigger('gridRowBeforeDropFinalize', event);

    if (context.valid) {
      context.asyncCleanup = context.async = true;
      context.element.classList.add('b-dropping');
      me.setTimeout(() => me.finalizeReorder(context).then(() => me.trigger('gridRowDrop', event)), me.finalizeDelay);
    } else {
      me.trigger('gridRowDrop', event);
    }
  }

  async finalizeReorder(context) {
    const me = this,
          {
      grid
    } = me,
          {
      store
    } = grid;
    let records = context.records;
    context.valid = context.valid && !records.some(rec => !store.includes(rec));

    if (context.valid) {
      let result;

      if (store.tree) {
        // Remove any selected child records of parent nodes
        records = records.filter(record => !record.parent || !records.includes(record.parent));
        result = await context.parent.tryInsertChild(records, context.insertBefore);
        context.valid = result !== false;
      } else {
        store.move(records, context.insertBefore);
      }

      store.clearSorters();
    }

    context.finalize(context.valid);
    grid.element.classList.remove('b-row-reordering');
  }
  /**
   * Clean up on reset
   * @private
   */

  onReset() {
    const me = this,
          cellEdit = me.grid.features.cellEdit;
    me.grid.element.classList.remove('b-row-reordering');

    if (cellEdit) {
      cellEdit.disabled = me.cellEditDisabledState;
    }

    DomHelper.removeClsGlobally(me.grid.element, 'b-row-reordering-target-parent');
  } //endregion
  //region Render

  /**
   * Updates DragHelper with updated headers when grid contents is rerendered
   * @private
   */

  render() {
    // columns shown, hidden or reordered
    this.init();
  } //endregion

}
RowReorder.featureClass = '';
RowReorder._$name = 'RowReorder';
GridFeatureManager.registerFeature(RowReorder, false);
GridFeatureManager.registerFeature(RowReorder, true, 'Gantt');

//TODO: Should listen for store search also, to work the other way around
/**
 * @module Grid/feature/Search
 */

/**
 * Feature that allows the user to search the entire grid. Navigate between hits using the
 * keyboard, [f3] or [ctrl]/[cmd] + [g] moves to next, also pressing [shift] moves to previous.
 *
 * Note that this feature does not include a UI, please build your own and call appropriate methods in the feature. For
 * a demo implementation, see
 * <a href="../examples/search" target="_blank">Search example</a>.
 *
 * This feature is <strong>disabled</strong> by default.
 *
 * @extends Core/mixin/InstancePlugin
 *
 * @example
 * // enable Search
 * let grid = new Grid({
 *   features: {
 *     search: true
 *   }
 * });
 *
 * // perform search
 * grid.features.search.search('steve');
 *
 * @demo Grid/search
 * @classtype search
 * @externalexample feature/Search.js
 */

class Search extends InstancePlugin {
  //region Init
  static get $name() {
    return 'Search';
  }

  construct(grid, config) {
    super.construct(grid, config);
    Object.assign(this, {
      grid,
      find: '',
      hitEls: []
    });
  }

  doDestroy() {
    this.clear(true);
    super.doDestroy();
  }

  doDisable(disable) {
    if (disable) {
      this.clear();
    }

    super.doDisable(disable);
  }

  get store() {
    return this.grid.store;
  } //endregion
  //region Plugin config
  // Plugin configuration. This plugin chains some of the functions in Grid.

  static get pluginConfig() {
    return {
      chain: ['populateCellMenu', 'onElementKeyDown']
    };
  } //endregion
  //region Search

  /**
   * Performs a search and highlights hits.
   * @param {String} find Text to search for
   * @param {Boolean} gotoHit Go to first hit after search
   * @param {Boolean} reapply Pass true to force search
   */

  search(find, gotoHit = true, reapply = false) {
    var _me$hitColumns;

    const me = this; // empty search considered a clear

    if (!find) {
      return me.clear();
    } // searching for same thing again, do nothing

    if (!reapply && find === me.find || me.disabled) {
      return;
    }

    const {
      grid,
      store
    } = me,
          // Only search columns in use
    columns = grid.columns.visibleColumns.filter(col => col.searchable !== false),
          fields = columns.map(col => col.field),
          found = store.search(find, fields);
    let i = 1;
    Object.assign(me, {
      foundMap: {},
      prevFound: me.found,
      found,
      find
    });
    me.clearHits();

    if (!found) {
      return;
    } // columns from previous search, reset htmlEncode

    (_me$hitColumns = me.hitColumns) === null || _me$hitColumns === void 0 ? void 0 : _me$hitColumns.forEach(col => col.disableHtmlEncode = false);
    me.hitColumns = []; // highlight hits for visible cells

    for (const hit of found) {
      me.foundMap[hit.field + '-' + hit.id] = i++; // disable htmlEncode for columns with hits

      const column = grid.columns.get(hit.field);

      if (column) {
        column.disableHtmlEncode = true;
        me.hitColumns.push(column);
      } // limit hits

      if (i > 1000) break;
    }

    if (!me.listenersInitialized) {
      grid.rowManager.on({
        rendercell: me.renderCell
      }, me);
      store.on({
        [`refresh${grid.asyncEventSuffix}`]: me.onStoreRefresh
      }, me);
      me.listenersInitialized = true;
    }

    grid.refreshRows();
    grid.trigger('search', {
      grid,
      find,
      found
    });

    if (gotoHit && !me.isHitFocused) {
      me.gotoNextHit(true);
    }

    return found;
  }

  clearHits() {
    // clear old hits
    for (const cellElement of DomHelper.children(this.grid.element, '.b-search-hit')) {
      // IE11 doesnt support this
      //cell.classList.remove('b-search-hit', 'b-search-hit-cell');
      cellElement.classList.remove('b-search-hit');
      cellElement.classList.remove('b-search-hit-cell'); // rerender cell to remove search-hit-text

      const row = DomDataStore.get(cellElement).row;
      cellElement._content = null;
      row.renderCell({
        cellElement
      });
    }
  }
  /**
   * Clears search results.
   */

  clear(silent = false) {
    const me = this,
          {
      grid
    } = me;

    if (me.foundMap) {
      delete me.foundMap;
    }

    delete me.find;
    me.clearHits();

    if (me.listenersInitialized) {
      grid.rowManager.un({
        rendercell: me.renderCell
      }, me);
      me.store.un({
        refresh: me.onStoreRefresh
      }, me);
      me.listenersInitialized = false;
    }

    if (!silent) {
      grid.trigger('clearSearch', {
        grid
      });
    }
  }
  /**
   * Number of results found
   * @readonly
   * @property {Number}
   */

  get foundCount() {
    var _this$found$length, _this$found;

    return (_this$found$length = (_this$found = this.found) === null || _this$found === void 0 ? void 0 : _this$found.length) !== null && _this$found$length !== void 0 ? _this$found$length : 0;
  } //endregion
  //region Navigation

  /**
   * Returns true if focused row is a hit
   * @property {Boolean}
   * @readonly
   */

  get isHitFocused() {
    const me = this,
          {
      grid
    } = me,
          currentIndex = grid.focusedCell ? grid.store.indexOf(grid.focusedCell.id) : -1,
          currentColumn = grid.focusedCell ? grid.columns.getById(grid.focusedCell.columnId) : null;
    return currentIndex !== -1 && me.found.some(hit => hit.index === currentIndex && currentColumn && hit.field === currentColumn.field);
  }
  /**
   * Select the next hit, scrolling it into view. Triggered with [f3] or [ctrl]/[cmd] + [g].
   */

  gotoNextHit(fromStart = false) {
    var _me$found;

    const me = this;
    if (!((_me$found = me.found) !== null && _me$found !== void 0 && _me$found.length)) return;
    const {
      grid
    } = me,
          fromCell = grid.focusedCell || grid.lastFocusedCell,
          currentIndex = fromCell && !fromStart ? grid.store.indexOf(fromCell.id) : -1,
          nextHit = me.found.findIndex(hit => hit.index > currentIndex);

    if (nextHit !== -1) {
      me.gotoHit(nextHit);
    }
  }
  /**
   * Select the previous hit, scrolling it into view. Triggered with [shift] + [f3] or [shift] + [ctrl]/[cmd] + [g].
   */

  gotoPrevHit() {
    var _me$found2;

    const me = this;
    if (!((_me$found2 = me.found) !== null && _me$found2 !== void 0 && _me$found2.length)) return;
    const {
      grid,
      found
    } = me,
          fromCell = grid.focusedCell || grid.lastFocusedCell,
          currentIndex = fromCell ? grid.store.indexOf(fromCell.id) : 0;

    for (let i = found.length - 1; i--; i >= 0) {
      const hit = found[i];

      if (hit.index < currentIndex) {
        me.gotoHit(i);
        break;
      }
    }
  }
  /**
   * Go to specified hit.
   * @param {Number} index
   */

  gotoHit(index) {
    const {
      grid
    } = this,
          nextHit = this.found[index];

    if (nextHit) {
      grid.focusCell({
        field: nextHit.field,
        id: nextHit.id
      });
    }

    return Boolean(nextHit);
  }
  /**
   * Go to the first hit.
   */

  gotoFirstHit() {
    this.gotoHit(0);
  }
  /**
   * Go to the last hit.
   */

  gotoLastHit() {
    this.gotoHit(this.found.length - 1);
  } //endregion
  //region Render

  /**
   * Called from SubGrid when a cell is rendered. Highlights search hits.
   * @private
   */

  renderCell({
    cellElement,
    column,
    record,
    value,
    cellContent
  }) {
    var _me$foundMap;

    const me = this,
          hitIndex = (_me$foundMap = me.foundMap) === null || _me$foundMap === void 0 ? void 0 : _me$foundMap[column.field + '-' + record.id];

    if (hitIndex) {
      // highlight cell
      cellElement.classList.add('b-search-hit'); // highlight in cell if found in innerHTML

      const inner = DomHelper.down(cellElement, '.b-grid-cell-value') || cellElement,
            find = String(me.find).toLowerCase();

      if (String(value).toLowerCase() === find) {
        inner.innerHTML = `<span class="b-search-hit-text">${cellContent}</span><div class="b-search-hit-cell-badge">${hitIndex}</div>`;
      } else {
        const find = String(me.find).toLowerCase(),
              where = cellContent && cellContent.toLowerCase().indexOf(find);

        if (where > -1) {
          const end = where + find.length,
                casedFind = cellContent.slice(where, end);
          inner.innerHTML = `${cellContent.slice(0, where)}<span class="b-search-hit-text">${casedFind}</span>${cellContent.slice(end)}<div class="b-search-hit-cell-badge">${hitIndex}</div>`;
        } else {
          cellElement.classList.add('b-search-hit-cell');
        }
      }

      me.hitEls.push(cellElement);
    }
  } //endregion
  //region Context menu

  /**
   * Add search menu item to cell context menu.
   * @param {Object} options Contains menu items and extra data retrieved from the menu target.
   * @param {Grid.column.Column} options.column Column for which the menu will be shown
   * @param {Core.data.Model} options.record Record for which the menu will be shown
   * @param {Object} options.items A named object to describe menu items
   * @internal
   */

  populateCellMenu({
    column,
    record,
    items
  }) {
    const me = this;

    if (column.searchable) {
      items.search = {
        text: 'L{searchForValue}',
        localeClass: me,
        icon: 'b-fw-icon b-icon-search',
        cls: 'b-separator',
        name: 'search',
        weight: 200,
        disabled: me.disabled,
        onItem: () => {
          // TODO: Only extract selection from current cell instead? Lazy way for now
          let sel = window.getSelection().toString();

          if (!sel) {
            sel = record[column.field];
          }

          me.search(sel);
        }
      };
    }
  } //endregion
  //region Events

  /**
   * Chained function called on grids keydown event. Handles backspace, escape, f3 and ctrl/cmd + g keys.
   * @private
   * @param event KeyboardEvent
   */

  onElementKeyDown(event) {
    const me = this;

    if (me.find && me.find !== '') {
      if (event.key === 'F3' || event.key.toLowerCase() === 'g' && (event.ctrlKey || event.metaKey)) {
        event.preventDefault();

        if (event.shiftKey) {
          me.gotoPrevHit();
        } else {
          me.gotoNextHit();
        }
      }
    }
  }

  onStoreRefresh() {
    this.search(this.find, false, true);
  } //endregion

}
Search.featureClass = 'b-search';
Search._$name = 'Search';
GridFeatureManager.registerFeature(Search);

//TODO: Allow multisort using multitouch?
/**
 * @module Grid/feature/Sort
 */

/**
 * Allows sorting of grid by clicking (or tapping) headers, also displays which columns grid is sorted by (numbered if
 * using multisort). Use modifier keys for multisorting: ctrl + click to add sorter, ctrl + alt + click to remove sorter.
 * The actual sorting is done by the store, see {@link Core.data.mixin.StoreSort#function-sort Store#sort()}.
 *
 * {@inlineexample feature/Sort.js}
 *
 * ```javascript
 * // sorting feature is enabled, no default value though
 * const grid = new Grid({
 *     features : {
 *         sort : true
 *     }
 * });
 *
 * // use initial sorting
 * const grid = new Grid({
 *     features : {
 *         sort : 'name'
 *     }
 * });
 *
 * // can also be specified on the store
 * const grid = new Grid({
 *     store : {
 *         sorters : [
 *             { field : 'name', ascending : false }
 *         ]
 *     }
 * });
 *
 * // custom sorting function can also be specified on the store
 * const grid = new Grid({
 *     store : {
 *         sorters : [{
 *             fn : (recordA, recordB) => {
 *                 // apply custom logic, for example:
 *                 return recordA.name.length < recordB.name.length ? -1 : 1;
 *             }
 *         }]
 *     }
 * });
 * ```
 *
 * For info on programmatically handling sorting, see {@link Core.data.mixin.StoreSort StoreSort}:
 *
 * ```javascript
 * const grid = new Grid({ });
 * // Programmatic sorting of the store, Grids rows and UI will be updated
 * grid.store.sort('age');
 * ```
 *
 * Grid columns can define custom sorting functions (see {@link Grid.column.Column#config-sortable Column#sortable}).
 * If this feature is configured with `prioritizeColumns: true`, those functions will also be used when sorting
 * programmatically:
 *
 * ```javascript
 * const grid = new Grid({
 *     columns : [
 *         {
 *             field : 'age',
 *             text : 'Age',
 *             sortable(lhs, rhs) {
 *               // Custom sorting, see Array#sort
 *             }
 *         }
 *     ],
 *
 *     features : {
 *         sort : {
 *             prioritizeColumns : true
 *         }
 *     }
 * });
 *
 * // Sortable fn will also be used when sorting programmatically
 * grid.store.sort('age');
 * ```
 *
 * This feature is **enabled** by default.
 *
 * @extends Core/mixin/InstancePlugin
 * @demo Grid/sorting
 * @classtype sort
 */

class Sort extends InstancePlugin {
  //region Config
  static get $name() {
    return 'Sort';
  }

  static get configurable() {
    return {
      /**
       * Enable multi sort
       * @config {Boolean}
       * @default
       */
      multiSort: true,

      /**
       * Use custom sorting functions defined on columns also when programmatically sorting by the columns field.
       *
       * ```javascript
       * const grid = new Grid({
       *     columns : [
       *         {
       *             field : 'age',
       *             text : 'Age',
       *             sortable(lhs, rhs) {
       *               // Custom sorting, see Array#sort
       *             }
       *         }
       *     ],
       *
       *     features : {
       *         sort : {
       *             prioritizeColumns : true
       *         }
       *     }
       * });
       *
       * grid.store.sort('age');
       * ```
       *
       * @config {Boolean}
       * @default
       */
      prioritizeColumns: false
    };
  }

  static get properties() {
    return {
      ignoreRe: new RegExp([// Stop this feature from having to know the internals of two other optional features.
      'b-grid-header-resize-handle', 'b-filter-icon'].join('|')),
      sortableCls: 'b-sortable',
      sortedCls: 'b-sort',
      sortedAscCls: 'b-asc',
      sortedDescCls: 'b-desc'
    };
  } //endregion
  //region Init

  construct(grid, config) {
    // process initial config into an actual config object
    config = this.processConfig(config);
    this.grid = grid;
    this.bindStore(grid.store);
    super.construct(grid, config);
  } // Sort feature handles special config cases, where user can supply a string or an array of sorters
  // instead of a normal config object

  processConfig(config) {
    if (typeof config === 'string' || Array.isArray(config)) {
      return {
        field: config,
        ascending: null
      };
    }

    return config;
  } // override setConfig to process config before applying it

  setConfig(config) {
    super.setConfig(this.processConfig(config));
  }

  bindStore(store) {
    this.detachListeners('store');
    store.on({
      name: 'store',
      beforeSort: 'onStoreBeforeSort',
      sort: 'syncHeaderSortState',
      thisObj: this
    });
  }

  set field(field) {
    // Use columns sortable config for initial sorting if it is specified
    const column = this.grid.columns.get(field);

    if (column && typeof column.sortable === 'object') {
      // Normalization of Store & CollectionSorter differences
      column.sortable.field = column.sortable.property || field;
      field = column.sortable;
    }

    this.store.sort(field, this.ascending);
  }

  doDestroy() {
    super.doDestroy();
  } // Avoid caching store, it might change

  get store() {
    return this.grid.store;
  } //endregion
  //region Plugin config
  // Plugin configuration. This plugin chains some of the functions in Grid.

  static get pluginConfig() {
    return {
      chain: ['onElementClick', 'populateHeaderMenu', 'getColumnDragToolbarItems', 'renderHeader', 'bindStore']
    };
  } //endregion
  //region Headers

  /**
   * Update headers to match stores sorters (displays sort icon in correct direction on them)
   * @private
   */

  syncHeaderSortState() {
    const me = this;

    if (!me.grid.isConfiguring) {
      const storeSorters = me.store.sorters,
            sorterCount = storeSorters.length,
            classList = new DomClassList();
      let sorter; // Sync the sortable, sorted, and sortIndex state of each leaf header element

      for (const leafColumn of me.grid.columns.bottomColumns) {
        const leafHeader = leafColumn.element;

        if (leafHeader) {
          var _leafColumn$textWrapp;

          // TimeAxisColumn in Scheduler has no textWrapper, since it has custom rendering,
          // but since it cannot be sorted by anyway lets just ignore it
          const dataset = (_leafColumn$textWrapp = leafColumn.textWrapper) === null || _leafColumn$textWrapp === void 0 ? void 0 : _leafColumn$textWrapp.dataset; // data-sortIndex is 1-based, and only set if there is > 1 sorter.
          // iOS Safari throws a JS error if the requested delete property is not present.

          (dataset === null || dataset === void 0 ? void 0 : dataset.sortIndex) && delete dataset.sortIndex;
          classList.value = leafHeader.classList;

          if (leafColumn.sortable !== false) {
            classList.add(me.sortableCls);
            sorter = storeSorters.find(sort => sort.field === leafColumn.field || sort.sortFn && sort.sortFn === leafColumn.sortable.sortFn);

            if (sorter) {
              if (sorterCount > 1 && dataset) {
                dataset.sortIndex = storeSorters.indexOf(sorter) + 1;
              }

              classList.add(me.sortedCls);

              if (sorter.ascending) {
                classList.add(me.sortedAscCls);
                classList.remove(me.sortedDescCls);
              } else {
                classList.add(me.sortedDescCls);
                classList.remove(me.sortedAscCls);
              }
            } else {
              classList.remove(me.sortedCls); // Not optimal, but easiest way to make sure sort feature does not remove needed classes.
              // Better solution would be to use different names for sorting and grouping

              if (!classList['b-group']) {
                classList.remove(me.sortedAscCls);
                classList.remove(me.sortedDescCls);
              }
            }
          } else {
            classList.remove(me.sortableCls);
          } // Update the element's classList

          DomHelper.syncClassList(leafHeader, classList);
        }
      }
    }
  } //endregion
  //region Context menu

  /**
   * Adds sort menu items to header context menu.
   * @param {Object} options Contains menu items and extra data retrieved from the menu target.
   * @param {Grid.column.Column} options.column Column for which the menu will be shown
   * @param {Object} options.items A named object to describe menu items
   * @internal
   */

  populateHeaderMenu({
    column,
    items
  }) {
    const me = this,
          {
      store
    } = me,
          sortBy = { ...column.sortable,
      field: column.field,
      columnOwned: true
    };

    if (column.sortable !== false) {
      items.sortAsc = {
        text: 'L{sortAscending}',
        localeClass: me,
        icon: 'b-fw-icon b-icon-sort-asc',
        name: 'sortAsc',
        cls: 'b-separator',
        weight: 300,
        disabled: me.disabled,
        onItem: () => store.sort(sortBy, true)
      };
      items.sortDesc = {
        text: 'L{sortDescending}',
        localeClass: me,
        icon: 'b-fw-icon b-icon-sort-desc',
        name: 'sortDesc',
        weight: 310,
        disabled: me.disabled,
        onItem: () => store.sort(sortBy, false)
      };

      if (me.multiSort && me.grid.columns.records.some(col => col.sortable)) {
        const sorter = store.sorters.find(s => s.field === column.field || column.sortable.sortFn === s.sortFn);
        items.multiSort = {
          text: 'L{multiSort}',
          localeClass: me,
          icon: 'b-fw-icon b-icon-sort',
          name: 'multiSort',
          weight: 320,
          disabled: me.disabled,
          menu: {
            addSortAsc: {
              text: sorter ? 'L{toggleSortAscending}' : 'L{addSortAscending}',
              localeClass: me,
              icon: 'b-fw-icon b-icon-sort-asc',
              name: 'addSortAsc',
              disabled: sorter && (sorter === null || sorter === void 0 ? void 0 : sorter.ascending),
              weight: 330,
              onItem: () => store.addSorter(sortBy, true)
            },
            addSortDesc: {
              text: sorter ? 'L{toggleSortDescending}' : 'L{addSortDescending}',
              localeClass: me,
              icon: 'b-fw-icon b-icon-sort-desc',
              name: 'addSortDesc',
              disabled: sorter && !sorter.ascending,
              weight: 340,
              onItem: () => store.addSorter(sortBy, false)
            },
            removeSorter: {
              text: 'L{removeSorter}',
              localeClass: me,
              icon: 'b-fw-icon b-icon-remove',
              name: 'removeSorter',
              weight: 350,
              disabled: !sorter,
              onItem: () => {
                store.removeSorter(sortBy.field);
              }
            }
          }
        };
      }
    }
  }
  /**
   * Supply items to ColumnDragToolbar
   * @private
   */

  getColumnDragToolbarItems(column, items) {
    const me = this,
          {
      store,
      disabled
    } = me;

    if (column.sortable !== false) {
      items.push({
        text: 'L{sortAscendingShort}',
        group: 'L{sort}',
        localeClass: me,
        icon: 'b-icon b-icon-sort-asc',
        name: 'sortAsc',
        cls: 'b-separator',
        weight: 105,
        disabled,
        onDrop: ({
          column
        }) => store.sort(column.field, true)
      });
      items.push({
        text: 'L{sortDescendingShort}',
        group: 'L{sort}',
        localeClass: me,
        icon: 'b-icon b-icon-sort-desc',
        name: 'sortDesc',
        weight: 105,
        disabled,
        onDrop: ({
          column
        }) => store.sort(column.field, false)
      });
      const sorter = store.sorters.find(s => s.field === column.field);
      Array.prototype.push.apply(items, [{
        text: 'L{addSortAscendingShort}',
        group: 'L{multiSort}',
        localeClass: me,
        icon: 'b-icon b-icon-sort-asc',
        name: 'multisortAddAsc',
        disabled: disabled || sorter && sorter.ascending,
        weight: 105,
        onDrop: ({
          column
        }) => store.addSorter(column.field, true)
      }, {
        text: 'L{addSortDescendingShort}',
        group: 'L{multiSort}',
        localeClass: me,
        icon: 'b-icon b-icon-sort-desc',
        name: 'multisortAddDesc',
        disabled: disabled || sorter && !sorter.ascending,
        weight: 105,
        onDrop: ({
          column
        }) => store.addSorter(column.field, false)
      }, {
        text: 'L{removeSorterShort}',
        group: 'L{multiSort}',
        localeClass: me,
        icon: 'b-icon b-icon-remove',
        name: 'multisortRemove',
        weight: 105,
        disabled: disabled || !sorter,
        onDrop: ({
          column
        }) => store.removeSorter(column.field)
      }]);
    }

    return items;
  } //endregion
  //region Events
  // Intercept sorting by a column that has a custom sorting fn, and inject that fn

  onStoreBeforeSort({
    sorters
  }) {
    const {
      columns
    } = this.client;

    for (let i = 0; i < sorters.length; i++) {
      var _column$sortable;

      const sorter = sorters[i],
            column = (sorter.columnOwned || this.prioritizeColumns) && columns.get(sorter.field);

      if (column !== null && column !== void 0 && (_column$sortable = column.sortable) !== null && _column$sortable !== void 0 && _column$sortable.sortFn) {
        sorters[i] = { ...sorter,
          ...column.sortable,
          columnOwned: true
        };
      }
    }
  }
  /**
   * Clicked on header, sort Store.
   * @private
   */

  onElementClick(event) {
    const me = this,
          store = me.store,
          target = event.target,
          header = DomHelper.up(target, '.b-grid-header.b-sortable'),
          field = header === null || header === void 0 ? void 0 : header.dataset.column;

    if (me.ignoreRe.test(target.className) || me.disabled) {
      return;
    } //Header

    if (header && field) {
      const column = me.grid.columns.getById(header.dataset.columnId),
            columnGrouper = store.isGrouped && store.groupers.find(g => g.field === field); // The Group feature will handle the change of the grouper's direction

      if (columnGrouper && !event.shiftKey) {
        return;
      }

      if (column.sortable && !event.shiftKey) {
        if (event.ctrlKey && event.altKey) {
          store.removeSorter(column.field);
        } else {
          const sortBy = {
            columnOwned: true,
            field: column.field
          }; // sortable as a function is handled by onStoreBeforeSort() above

          if (typeof column.sortable === 'object') {
            ObjectHelper.assign(sortBy, column.sortable);
          }

          store.sort(sortBy, null, event.ctrlKey);
        }
      }
    }
  }
  /**
   * Called when grid headers are rendered, make headers match current sorters.
   * @private
   */

  renderHeader() {
    this.syncHeaderSortState();
  } //endregion

}
Sort.featureClass = 'b-sort';
Sort._$name = 'Sort';
GridFeatureManager.registerFeature(Sort, true);

/**
 * @module Grid/feature/Stripe
 */

/**
 * Stripes rows, by adding even (b-even) or odd (b-odd) css classes to row elements.
 *
 * This feature is <strong>disabled</strong> by default.
 *
 * @extends Core/mixin/InstancePlugin
 *
 * @example
 * let grid = new Grid({
 *   features: {
 *     stripe: true
 *   }
 * });
 *
 * @demo Grid/columns
 * @classtype stripe
 * @externalexample feature/Stripe.js
 */

class Stripe extends InstancePlugin {
  static get $name() {
    return 'Stripe';
  }

  construct(grid, config) {
    super.construct(grid, config);
    grid.rowManager.on({
      renderrow: 'onRenderRow',
      thisObj: this
    });
  }

  doDisable(disable) {
    if (!this.isConfiguring) {
      // Refresh rows to add/remove even/odd classes
      this.client.refreshRows();
    }

    super.doDisable(disable);
  }
  /**
   * Applies even/odd CSS when row is rendered
   * @param {Grid.row.Row} rowModel
   * @private
   */

  onRenderRow({
    row
  }) {
    const {
      disabled
    } = this,
          even = row.dataIndex % 2 === 0;
    row.assignCls({
      'b-even': !disabled && even,
      'b-odd': !disabled && !even
    });
  }

}
Stripe._$name = 'Stripe';
GridFeatureManager.registerFeature(Stripe);

//TODO: Handle paths? Like expandPath('Parent A/Sub B/Leaf')
/**
 * @module Grid/feature/Tree
 */

const immediatePromise$4 = Promise.resolve();
/**
 * Feature that makes the grid work more like a tree. Included by default in {@link Grid.view.TreeGrid}. Requires
 * exactly one {@link Grid.column.TreeColumn} among grids columns. That column will have its renderer replaced with a
 * tree renderer that adds padding and icon to give the appearance of a tree. The original renderer is preserved and
 * also called.
 *
 * This feature is <strong>disabled</strong> by default. When enabled, the feature cannot be disabled during runtime.
 *
 * @extends Core/mixin/InstancePlugin
 *
 * @demo Grid/tree
 * @classtype tree
 * @externalexample feature/Tree.js
 */

class Tree extends InstancePlugin.mixin(Delayable) {
  //region Config
  static get $name() {
    return 'Tree';
  }

  static get defaultConfig() {
    return {
      /**
       * Expand parent nodes when clicking on their cell
       * @config {Boolean}
       * @default
       */
      expandOnCellClick: false
    };
  } // Plugin configuration. This plugin chains some of the functions in Grid.

  static get pluginConfig() {
    return {
      assign: ['collapseAll', 'expandAll', 'collapse', 'expand', 'expandTo', 'toggleCollapse'],
      before: ['navigateRight', 'navigateLeft'],
      chain: ['onElementClick', 'onElementKeyDown', 'bindStore']
    };
  } //endregion
  //region Init

  construct(grid, config) {
    super.construct(grid, config); // find column

    const me = this,
          treeColumn = grid.columns.find(col => col instanceof TreeColumn);
    Object.assign(me, {
      grid: grid,
      rowManager: grid.rowManager,
      treeColumn: treeColumn
    });
    this.bindStore(grid.store);
  }

  doDisable(disable) {}

  get store() {
    return this.grid.store;
  }

  bindStore(store) {
    this.detachListeners('store');
    store.on({
      name: 'store',
      beforeLoadChildren: 'onBeforeLoadChildren',
      loadChildren: 'onLoadChildren',
      loadChildrenException: 'onLoadChildrenException',
      beforeToggleNode: 'onBeforeToggleNode',
      thisObj: this
    });
  } //endregion
  //region Expand & collapse

  /**
   * Collapse an expanded node or expand a collapsed. Optionally forcing a certain state.
   * @param {String|Number|Core.data.Model} idOrRecord Record (the node itself) or id of a node to toggle
   * @param {Boolean} [collapse] Force collapse (true) or expand (false)
   * @param {Boolean} [skipRefresh] Set to true to not refresh rows (if calling in batch)
   * @returns {Promise}
   * @async
   */

  async toggleCollapse(idOrRecord, collapse, skipRefresh = false) {
    const me = this,
          {
      store,
      grid
    } = me,
          {
      rowManager
    } = grid,
          record = store.getById(idOrRecord),
          meta = record.instanceMeta(store);

    if (await store.toggleCollapse(record, collapse)) {
      const row = rowManager.getRowFor(record);

      if (row) {
        const cellElement = row.getCell(me.treeColumn.id); // Toggle cell's expanded/collapsed state

        cellElement && row.renderCell({
          cellElement,
          record
        });
      } // Add a temporary cls, used by Scheduler & Gantt to prevent transitions on events/tasks
      // Block multiple applications in the case of a recursive collapseAll operation

      if (!me.isTogglingNode) {
        grid.element.classList.add('b-toggling-node');
        me.isTogglingNode = true;
        me.requestAnimationFrame(() => {
          grid.element.classList.remove('b-toggling-node');
          me.isTogglingNode = false;
        });
      }

      grid.trigger(meta.collapsed ? 'collapseNode' : 'expandNode', {
        source: grid,
        record
      });
      grid.trigger('toggleNode', {
        source: grid,
        record,
        collapse
      });
    }
  }
  /**
   * Collapse a single node. This function is exposed on Grid and can thus be called as `grid.collapse()`
   * @param {String|Number|Core.data.Model} idOrRecord Record (the node itself) or id of a node to collapse
   * @returns {Promise}
   */

  async collapse(idOrRecord) {
    return this.toggleCollapse(idOrRecord, true);
  }
  /**
   * Expand a single node. This function is exposed on Grid and can thus be called as `grid.expand()`
   * @param {String|Number|Core.data.Model} idOrRecord Record (the node itself) or id of a node to expand
   * @returns {Promise}
   */

  async expand(idOrRecord) {
    return this.toggleCollapse(idOrRecord, false);
  }

  onBeforeToggleNode({
    record,
    collapse
  }) {
    this.grid.trigger('beforeToggleNode', {
      record,
      collapse
    });
  }

  onBeforeLoadChildren({
    source: store,
    params
  }) {
    const parent = store.getById(params[store.modelClass.idField]),
          row = this.grid.rowManager.getRowFor(parent);

    if (row) {
      row.addCls('b-loading-children');
    }
  }

  onLoadChildren({
    source: store,
    params
  }) {
    const parent = store.getById(params[store.modelClass.idField]),
          row = this.grid.rowManager.getRowFor(parent);

    if (row) {
      row.removeCls('b-loading-children');
    }
  }

  onLoadChildrenException({
    record
  }) {
    const row = this.grid.rowManager.getRowFor(record);

    if (row) {
      row.removeCls('b-loading-children');
    }
  }
  /**
   * Expand or collapse all nodes, as specified by param, starting at the passed node (which defaults to the root node)
   * @param {Boolean} [collapse] Set to true to collapse, false to expand (defaults to true)
   * @param {Core.data.Model} [topNode] The topmost node from which to cascade a collapse.
   * Defaults to the {@link Core.data.Store#property-rootNode}. Not included in the cascade if
   * the root node is being used.
   * @returns {Promise}
   */

  expandOrCollapseAll(collapse = true, topNode = this.store.rootNode) {
    // TODO: Some logic here and some in the store, keep in same place maybe
    const {
      grid,
      store
    } = this,
          promises = [],
          childRecords = [];
    grid.trigger('beforeToggleAllNodes', {
      source: grid,
      collapse
    }); // Each collapse/expand will trigger events on store, avoid that by suspending

    store.suspendEvents();
    store.traverse(record => {
      const gridMeta = record.instanceMeta(store);

      if (!record.isLeaf) {
        if (collapse && !gridMeta.collapsed) {
          this.toggleCollapse(record, true, true);
          childRecords.push(...record.children);
        } else if (!collapse && gridMeta.collapsed) {
          if (Array.isArray(record.children)) {
            childRecords.push(...record.children);
          }

          promises.push(this.toggleCollapse(record, false, true));
        }
      }
    }, topNode, topNode === store.rootNode);
    store.resumeEvents();
    return (collapse ? immediatePromise$4 : Promise.all(promises)).then(() => {
      // Return to top when collapsing all
      grid.refreshRows(collapse);

      if (childRecords.length) {
        if (collapse) {
          store.trigger('remove', {
            records: childRecords,
            isCollapse: true,
            isCollapseAll: true
          });
        } else {
          store.trigger('add', {
            records: childRecords,
            isExpand: true,
            isExpandAll: true
          });
        }
      }

      grid.trigger('toggleAllNodes', {
        source: grid,
        collapse
      });
    });
  }
  /**
   * Collapse all nodes. This function is exposed on Grid and can thus be called as `grid.collapseAll()`
   * @returns {Promise}
   */

  async collapseAll() {
    return this.expandOrCollapseAll(true);
  }
  /**
   * Expand all nodes. This function is exposed on Grid and can thus be called as `grid.expandAll()`
   * @returns {Promise}
   */

  async expandAll() {
    return this.expandOrCollapseAll(false);
  }
  /**
   * Expands parent nodes to make this node "visible". This function is exposed on Grid and can thus be called as
   * `grid.expandTo()`
   * @param {String|Number|Core.data.Model} idOrRecord Record (the node itself) or id of a node
   * @returns {Promise}
   */

  async expandTo(idOrRecord) {
    const me = this,
          record = me.store.getById(idOrRecord),
          recordMeta = record.instanceMeta(me.store); // Already visible? abort

    if (recordMeta.hiddenByCollapse === false) return; // Expand each parent

    let refreshNeeded = false,
        parent = record.parent;
    const parents = []; // To expand path correctly we need to do it from top
    // so parent record for each record will be in the store already

    while (parent && !parent.isRoot) {
      parents.push(parent);
      parent = parent.parent;
    }

    await parents.reverse().forEach(async parent => {
      if (parent.instanceMeta(me.store).collapsed) {
        refreshNeeded = true;
      }

      await me.toggleCollapse(parent, false, true);
    });

    if (refreshNeeded) {
      me.grid.refreshRows();
    }

    await me.grid.scrollRowIntoView(record);
  } //endregion
  //region Renderer
  //endregion
  //region Events

  /**
   * Called when user clicks somewhere in the grid. Expand/collapse node on icon click.
   * @private
   */

  onElementClick(event) {
    const me = this,
          target = event.target,
          cellData = me.grid.getCellDataFromEvent(event),
          clickedExpander = target.closest('.b-tree-expander'); // Checks if click is on node expander icon, then toggles expand/collapse. Also toggles on entire cell if expandOnCellClick is true

    if (clickedExpander || me.expandOnCellClick && cellData && cellData.record.isParent) {
      clickedExpander && event.preventDefault();
      me.toggleCollapse(cellData.record);
    }
  }
  /**
   * Called on key down in grid. Expand/collapse node on [space]
   * @private
   */

  onElementKeyDown(event) {
    // only catch space on grid element, not in editors etc...
    if (event.target === this.client.focusElement && event.key === ' ') {
      event.preventDefault();
      this.toggleCollapse(this.grid.focusedCell.id);
    }
  } //endregion

  navigateRight(event) {
    const me = this,
          grid = me.grid,
          cellSelector = grid.focusedCell;

    if (cellSelector) {
      const record = grid.store.getById(cellSelector.id); // shift triggers tree navigation behaviour, also used by default for single column which is tree

      if (grid.columns.getById(cellSelector.columnId).tree && (event.shiftKey || grid.columns.count === 1)) {
        // on collapsed parent, expand
        if (record.isParent && record.instanceMeta(grid.store).collapsed) {
          me.expand(record);
        } // otherwise go down
        else {
            grid.navigateDown(event);
          }

        return false;
      }
    }
  }

  navigateLeft(event) {
    const me = this,
          grid = me.grid,
          cellSelector = grid.focusedCell;

    if (cellSelector) {
      const record = grid.store.getById(cellSelector.id);

      if (grid.columns.getById(cellSelector.columnId).tree && (event.shiftKey || grid.columns.count === 1)) {
        // on expanded parent, collapse
        if (record.isParent && !record.instanceMeta(grid.store).collapsed) {
          me.collapse(record);
        } // otherwise go to parent
        else if (record.parent) {
            grid.focusCell({
              id: record.parent.id,
              columnId: cellSelector.columnId
            });
          }

        return false;
      }
    }
  }

}
Tree.featureClass = 'b-tree';
Tree._$name = 'Tree';
GridFeatureManager.registerFeature(Tree, false, 'Grid');
GridFeatureManager.registerFeature(Tree, true, 'TreeGrid');

const RowsRange = {
  all: 'all',
  visible: 'visible'
};
const PaperFormat = {
  A6: {
    width: 4.13,
    height: 5.81
  },
  A5: {
    width: 5.81,
    height: 8.25
  },
  A4: {
    width: 8.25,
    height: 11.69
  },
  A3: {
    width: 11.69,
    height: 16.49
  },
  Legal: {
    width: 8.5,
    height: 14
  },
  Letter: {
    width: 8.5,
    height: 11
  }
};
const Orientation = {
  portrait: 'portrait',
  landscape: 'landscape'
};
const FileFormat = {
  pdf: 'pdf',
  png: 'png'
};

/**
 * @module Core/widget/mixin/LocalizableComboItems
 */

/**
 * A mixin that regenerates a combobox items on locale change.
 * @private
 * @mixin
 */

var LocalizableComboItems = (Target => class LocalizableComboItems extends (Target || Combo) {
  static get $name() {
    return 'LocalizableComboItems';
  }

  static get defaultConfig() {
    return {
      items: true
    };
  }

  set items(items) {
    if (items === true) {
      items = this.buildLocalizedItems();
    }

    super.items = items;
  }

  get items() {
    return super.items;
  }

  construct(...args) {
    // set a special flag to skip unneeded store translation on construction step
    this.inConstruct = true;
    super.construct(...args);
    this.inConstruct = false;
  }

  buildLocalizedItems() {
    return [];
  }

  updateLocalizedItems() {
    const me = this;

    if (me.store && !me.inConstruct) {
      const {
        value
      } = me; // TODO: just updating new data is not enough ..selected value text is shown wrong :(
      // it seems caused by selected record cache
      // review this code after #9387 is fixed

      me.store.data = me.buildLocalizedItems();
      me.value = null;
      me.value = value;
      me.syncInputFieldValue(true);
    }
  }

  updateLocalization() {
    this.updateLocalizedItems();
    super.updateLocalization();
  }

});

class LocalizableCombo extends LocalizableComboItems(Combo) {
  static get $name() {
    return 'LocalizableCombo';
  } // Factoryable type name

  static get type() {
    return 'localizablecombo';
  }

} // Register this widget type with its Factory

LocalizableCombo.initClass();
LocalizableCombo._$name = 'LocalizableCombo';

class ExportRowsCombo extends LocalizableCombo {
  static get $name() {
    return 'ExportRowsCombo';
  } // Factoryable type name

  static get type() {
    return 'ExportRowsCombo';
  }

  static get defaultConfig() {
    return {
      editable: false
    };
  }

  buildLocalizedItems() {
    const me = this;
    return [{
      id: RowsRange.all,
      text: me.L('L{all}')
    }, {
      id: RowsRange.visible,
      text: me.L('L{visible}')
    }];
  }

} // Register this widget type with its Factory

ExportRowsCombo.initClass();
ExportRowsCombo._$name = 'ExportRowsCombo';

class ExportOrientationCombo extends LocalizableCombo {
  static get $name() {
    return 'ExportOrientationCombo';
  } // Factoryable type name

  static get type() {
    return 'ExportOrientationCombo';
  }

  static get defaultConfig() {
    return {
      editable: false
    };
  }

  buildLocalizedItems() {
    const me = this;
    return [{
      id: Orientation.portrait,
      text: me.L('L{portrait}')
    }, {
      id: Orientation.landscape,
      text: me.L('L{landscape}')
    }];
  }

} // Register this widget type with its Factory

ExportOrientationCombo.initClass();
ExportOrientationCombo._$name = 'ExportOrientationCombo';

/**
 * @module Grid/view/export/ExportDialog
 */

/**
 * Dialog window which allows to pick export options.
 *
 * ```
 * grid = new Grid({
 *     features : {
 *         pdfExport : { exportServer : '...' }
 *     }
 * });
 *
 * grid.features.pdfExport.showExportDialog();
 * ```
 *
 * @extends Core/widget/Popup
 */

class ExportDialog extends Popup {
  static get $name() {
    return 'ExportDialog';
  }

  static get defaultConfig() {
    return {
      autoShow: false,
      autoClose: false,
      closable: true,
      centered: true,

      /**
       * Grid instance to build export dialog for
       * @config {Grid.view.Grid}
       */
      client: null,

      /**
       * Set to `false` to allow using PNG + Multipage config in export dialog
       * @config {Boolean}
       */
      hidePNGMultipageOption: true,
      exporterType: 'singlepage',
      orientation: 'portrait',
      paperFormat: 'A4',
      fileFormat: 'pdf',
      alignRows: false,
      rowsRange: 'all',
      title: 'L{exportSettings}',
      defaults: {
        localeClass: this
      },
      bbar: {
        defaults: {
          type: 'button',
          localeClass: this
        },
        items: {
          exportButton: {
            color: 'b-green',
            text: 'L{ExportDialog.export}',
            weight: 100
          },
          cancelButton: {
            color: 'b-gray',
            text: 'L{ExportDialog.cancel}',
            weight: 200
          }
        }
      }
    };
  }

  construct(config = {}) {
    const me = this,
          {
      client
    } = config;

    if (!client) {
      throw new Error('`client` config is required');
    }

    const items = me.buildDialogItems(config);

    if (config.items) {
      if (Array.isArray(config.items)) {
        config.items.push(...items);
      } else {
        items.forEach(item => {
          config.items[item.ref] = item;
          delete item.ref;
        });
      }
    } else {
      config.items = items;
    }

    config.width = config.width || me.L('L{width}');
    super.construct(config);
    me.widgetMap.exportButton.on('click', me.onExportClick, me);
    me.widgetMap.cancelButton.on('click', me.onCancelClick, me);
    LocaleManagerSingleton.on({
      locale: 'onLocaleChange',
      prio: -1,
      thisObj: me
    });
  }

  get fieldProperties() {
    return ['exporterType', 'orientation', 'fileFormat', 'paperFormat', 'alignRows', 'rowsRange', 'repeatHeader'];
  }

  getDefaultFieldValues(config) {
    const result = ObjectHelper.copyProperties({}, config, this.fieldProperties);
    return ObjectHelper.copyPropertiesIf(result, this.getDefaultConfiguration(), this.fieldProperties);
  }

  buildDialogItems(config) {
    const me = this,
          {
      exporters,
      client
    } = config,
          {
      exporterType,
      orientation,
      paperFormat,
      fileFormat,
      alignRows,
      rowsRange,
      repeatHeader
    } = me.getDefaultFieldValues(config),
          labelWidth = me.L('L{ExportDialog.labelWidth}');
    me.columnsStore = client.columns.chain(record => record.isLeaf && record.exportable);

    function buildComboItems(obj, fn = x => x) {
      return Object.keys(obj).map(key => ({
        id: key,
        text: fn(key)
      }));
    }

    return [{
      labelWidth,
      type: 'combo',
      ref: 'columnsField',
      label: 'L{ExportDialog.columns}',
      store: me.columnsStore,
      value: me.columnsStore.allRecords,
      valueField: 'id',
      displayField: 'text',
      multiSelect: true
    }, {
      labelWidth,
      type: 'exportrowscombo',
      ref: 'rowsRangeField',
      label: 'L{ExportDialog.rows}',
      value: rowsRange
    }, {
      labelWidth,
      type: 'localizablecombo',
      ref: 'exporterTypeField',
      label: 'L{ExportDialog.exporterType}',
      editable: false,
      value: exporterType,
      buildLocalizedItems: () => exporters.map(exporter => ({
        id: exporter.type,
        text: me.optionalL(exporter.title, this)
      })),

      onChange({
        value
      }) {
        this.owner.widgetMap.alignRowsField.hidden = value === 'singlepage';
        this.owner.widgetMap.repeatHeaderField.hidden = value !== 'multipagevertical';
      }

    }, {
      labelWidth,
      type: 'checkbox',
      ref: 'alignRowsField',
      label: 'L{ExportDialog.alignRows}',
      checked: alignRows,
      hidden: exporterType === 'singlepage'
    }, {
      labelWidth,
      type: 'checkbox',
      ref: 'repeatHeaderField',
      label: 'L{ExportDialog.repeatHeader}',
      localeClass: this,
      value: repeatHeader,
      hidden: exporterType !== 'multipagevertical'
    }, {
      labelWidth,
      type: 'combo',
      ref: 'fileFormatField',
      label: 'L{ExportDialog.fileFormat}',
      localeClass: this,
      editable: false,
      value: fileFormat,
      items: buildComboItems(FileFormat, value => value.toUpperCase()),

      onChange({
        value,
        oldValue
      }) {
        if (me.hidePNGMultipageOption) {
          const exporterField = me.widgetMap.exporterTypeField,
                exporter = exporterField.store.find(r => r.id === 'singlepage');

          if (value === FileFormat.png && exporter) {
            this._previousDisabled = exporterField.disabled;
            exporterField.disabled = true;
            this._previousValue = exporterField.value;
            exporterField.value = 'singlepage';
          } else if (oldValue === FileFormat.png && this._previousValue) {
            exporterField.disabled = this._previousDisabled;
            exporterField.value = this._previousValue;
          }
        }
      }

    }, {
      labelWidth,
      type: 'combo',
      ref: 'paperFormatField',
      label: 'L{ExportDialog.paperFormat}',
      editable: false,
      value: paperFormat,
      items: buildComboItems(PaperFormat)
    }, {
      labelWidth,
      type: 'exportorientationcombo',
      ref: 'orientationField',
      label: 'L{ExportDialog.orientation}',
      value: orientation
    }];
  }

  onLocaleChange() {
    const labelWidth = this.L('L{labelWidth}');
    this.width = this.L('L{width}');
    this.eachWidget(widget => {
      if (widget instanceof Field) {
        widget.labelWidth = labelWidth;
      }
    });
  }

  onExportClick() {
    const values = this.values;
    /**
     * Fires when export button is clicked
     * @event export
     * @param {Object} values Object containing config for {@link Grid.feature.export.PdfExport#function-export export()} method
     * @group Export
     */

    this.trigger('export', {
      values
    });
  }

  onCancelClick() {
    /**
     * Fires when cancel button is clicked. Popup will hide itself.
     * @event cancel
     * @group Export
     */
    this.trigger('cancel');
    this.hide();
  }

  get values() {
    const fieldRe = /field/i,
          result = {};
    this.eachWidget(widget => {
      if (fieldRe.test(widget.ref)) {
        result[widget.ref.replace(fieldRe, '')] = widget instanceof Checkbox ? widget.checked : widget.value;
      }
    });
    return result;
  }

}
ExportDialog._$name = 'ExportDialog';

function _AwaitValue(value) {
  this.wrapped = value;
}

function _AsyncGenerator(gen) {
  var front, back;

  function send(key, arg) {
    return new Promise(function (resolve, reject) {
      var request = {
        key: key,
        arg: arg,
        resolve: resolve,
        reject: reject,
        next: null
      };

      if (back) {
        back = back.next = request;
      } else {
        front = back = request;
        resume(key, arg);
      }
    });
  }

  function resume(key, arg) {
    try {
      var result = gen[key](arg);
      var value = result.value;
      var wrappedAwait = value instanceof _AwaitValue;
      Promise.resolve(wrappedAwait ? value.wrapped : value).then(function (arg) {
        if (wrappedAwait) {
          resume(key === "return" ? "return" : "next", arg);
          return;
        }

        settle(result.done ? "return" : "normal", arg);
      }, function (err) {
        resume("throw", err);
      });
    } catch (err) {
      settle("throw", err);
    }
  }

  function settle(type, value) {
    switch (type) {
      case "return":
        front.resolve({
          value: value,
          done: true
        });
        break;

      case "throw":
        front.reject(value);
        break;

      default:
        front.resolve({
          value: value,
          done: false
        });
        break;
    }

    front = front.next;

    if (front) {
      resume(front.key, front.arg);
    } else {
      back = null;
    }
  }

  this._invoke = send;

  if (typeof gen.return !== "function") {
    this.return = undefined;
  }
}

if (typeof Symbol === "function" && Symbol.asyncIterator) {
  _AsyncGenerator.prototype[Symbol.asyncIterator] = function () {
    return this;
  };
}

_AsyncGenerator.prototype.next = function (arg) {
  return this._invoke("next", arg);
};

_AsyncGenerator.prototype.throw = function (arg) {
  return this._invoke("throw", arg);
};

_AsyncGenerator.prototype.return = function (arg) {
  return this._invoke("return", arg);
};

function _wrapAsyncGenerator(fn) {
  return function () {
    return new _AsyncGenerator(fn.apply(this, arguments));
  };
}

function _awaitAsyncGenerator(value) {
  return new _AwaitValue(value);
}

/**
 * @module Grid/feature/export/exporter/Exporter
 */

/**
 * Base class for all exporters
 * @mixes Core/localization/Localizable
 * @mixes Core/mixin/Events
 */

class Exporter extends Delayable(Events(Localizable(Base))) {
  static get defaultConfig() {
    return {
      /**
       * `True` to replace all linked CSS files URLs to absolute before passing HTML to the server.
       * When passing a string the current origin of the CSS files URLS will be replaced by the passed origin.
       *
       * For example: css files pointing to /app.css will be translated from current origin to {translateURLsToAbsolute}/app.css
       * @config {Boolean|String}
       * @default
       */
      translateURLsToAbsolute: true,

      /**
       * When true links are converted to absolute by combining current window location (with replaced origin) with
       * resource link.
       * When false links are converted by combining new origin with resource link (for angular)
       * @config {Boolean}
       * @default
       */
      keepPathName: true
    };
  }
  /**
   * Template of an extracted page.
   * @param {Object} data Data for the page template
   * @returns {String}
   */

  pageTpl(data) {
    const {
      title,
      header,
      footer,
      styles,
      htmlClasses,
      bodyStyle,
      bodyClasses = [],
      paperHeight,
      paperWidth,
      html
    } = data;
    bodyClasses.push(`b-${this.constructor.type}`); // Other classes needed on outer element.
    // TODO: use the new static Widget.outerCls property when merged with master

    if (DomHelper.scrollBarWidth) {
      bodyClasses.push('b-visible-scrollbar');
    } else {
      bodyClasses.push('b-overlay-scrollbar');
    }

    if (BrowserHelper.isChrome) {
      bodyClasses.push('b-chrome');
    } else if (BrowserHelper.isSafari) {
      bodyClasses.push('b-safari');
    } else if (BrowserHelper.isFirefox) {
      bodyClasses.push('b-firefox');
    } else if (BrowserHelper.isIE11) {
      bodyClasses.push('b-ie');
    } else if (BrowserHelper.isEdge) {
      bodyClasses.push('b-edge');
    }

    return TemplateHelper.tpl`
            <!DOCTYPE html>
            <html class="${htmlClasses}" style="width: ${paperWidth}in; height: ${paperHeight}in;">
                <head>
                    <meta content="text/html; charset=UTF-8" http-equiv="Content-Type" />
                    <title>${title}</title>
                    ${styles.join('')}
                </head>
                <body class="b-export ${bodyClasses.join(' ')}" style="width: ${paperWidth}in; height: ${paperHeight}in; ${bodyStyle}">
                    <div class="b-export-content">
                        ${header && `<div class="b-export-header" style="width: 100%">${header}</div>`}
                        <div class="b-export-body"><div class="b-export-viewport">${html}</div></div>
                        ${footer && `<div class="b-export-footer" style="width: 100%">${footer}</div>`}
                    </div>
                </body>
            </html>`;
  }
  /**
   * Returns all style-related tags: `<style>` and `<link rel="stylesheet">`
   * @property {String[]}
   * @readonly
   */

  get stylesheets() {
    const me = this;

    if (me._stylesheets) {
      return me._stylesheets;
    }

    const translate = me.translateURLsToAbsolute,
          origin = window.origin,
          styleSheetNodes = Array.from(document.querySelectorAll('link[rel="stylesheet"], style')),
          styles = [];
    styleSheetNodes.forEach(node => {
      node = node.cloneNode(true); // put absolute URL to node `href` attribute

      if (translate && node.href) {
        let result;

        if (translate === true) {
          result = node.href;
        } // translate is truthy if we are here, which means it is string now
        else {
            if (this.keepPathName) {
              result = node.href.replace(origin, translate);
            } else {
              result = new URL(node.getAttribute('href'), translate);
            }
          }

        node.setAttribute('href', result);
      }

      let styleText; // Empty style tag will be copied in IE, so we need to use cssText

      if (BrowserHelper.isIE11 && node.styleSheet && /style/i.test(node.tagName)) {
        styleText = `<style type="text/css">${node.styleSheet.cssText}</style>`;
      } else {
        styleText = node.outerHTML;
      }

      if (translate && /style/i.test(node.tagName)) {
        const converter = me.getStyleTagURLConverter(translate);
        styleText = styleText.replace(/url\(['"]?(.+?)['"]?\)/g, converter);
      }

      styles.push(styleText);
    });
    styles.push(`<style>
                body, html {
                    overflow: auto;
                }

                body {
                    position: relative;
                    margin: 0;
                }
            </style>`);
    return me._stylesheets = styles;
  }

  set stylesheets(value) {
    this._stylesheets = value;
  }

  getStyleTagURLConverter(translate) {
    return function (match, url) {
      let result;

      try {
        let base; // Filter out local references, e.g. to svg marker

        if (/^#/.test(url)) {
          result = match;
        } else {
          if (translate === true) {
            base = window.location.href;
          } // translate is truthy if we are here, which means it is string now
          else {
              if (this.keepPathName) {
                base = window.location.href.replace(window.location.origin, translate);
              } else {
                base = translate;
              }
            }

          result = `url('${new URL(url, base).href}')`;
        }
      } catch (e) {
        result = match;
      }

      return result;
    }.bind(this);
  }

  saveState({
    client
  }) {
    this.state = client.state;
    const {
      tbar,
      bbar
    } = client;

    if (tbar) {
      this.state.tbar_hidden = tbar.hidden;
      tbar.hide(false);
    }

    if (bbar) {
      this.state.bbar_hidden = bbar.hidden;
      bbar.hide(false);
    }
  }

  async restoreState({
    client
  }) {
    const promises = [],
          detachers = new Set(),
          {
      tbar,
      bbar
    } = client;
    detachers.add(client.scrollable.on({
      scroll() {
        promises.push(client.scrollable.await('scrollEnd'));
      }

    }));
    client.eachSubGrid(({
      header,
      scrollable
    }) => {
      detachers.add(scrollable.on({
        scroll() {
          promises.push(scrollable.await('scrollEnd'));
        }

      }));
      detachers.add(header.scrollable.on({
        scroll() {
          promises.push(header.scrollable.await('scrollEnd'));
        }

      }));
    });

    if (tbar) {
      tbar.hidden = this.state.tbar_hidden;
      delete this.state.tbar_hidden;
    }

    if (bbar) {
      bbar.hidden = this.state.bbar_hidden;
      delete this.state.bbar_hidden;
    }

    client.state = this.state;
    await Promise.all(promises);
    detachers.forEach(fn => fn());
  }

  beforeExport() {
    // Into this element we will put HTML for export
    this.element = document.createElement('div');
  } //region DOM helpers

  cloneElement(element, target = this.element, clear = true) {
    if (clear) {
      target.innerHTML = '';
    }

    if (BrowserHelper.isChrome) {
      target.appendChild(element.cloneNode(true));
    } else {
      target.innerHTML = element.outerHTML;
    }

    DomHelper.removeEachSelector(target, '.b-grid-row');
    const maskEl = target.querySelector('.b-gridbase > .b-mask');

    if (maskEl) {
      maskEl.remove();
    }
  }
  /**
   * Appends generated header/footer element to the document body to measure their height
   * @param html
   * @returns {number}
   * @private
   */

  measureElement(html = '') {
    if (html instanceof HTMLElement) {
      html = html.outerHTML;
    }

    const target = DomHelper.createElement({
      parent: document.body,
      style: {
        visibility: 'hidden',
        position: 'absolute'
      },
      // Add html to measure to a div between two other divs to take margin into account
      html: `<div style="height: 1px"></div>${html}<div style="height: 1px"></div>`
    });
    const result = target.offsetHeight - 2;
    target.remove();
    return result;
  } // Converts local urls to absolute

  prepareHTML(html) {
    if (html instanceof HTMLElement) {
      html = html.outerHTML;
    }

    const target = DomHelper.createElement({
      parent: document.body,
      style: {
        visibility: 'hidden',
        position: 'absolute'
      },
      html
    });
    const elements = target.querySelectorAll('img');

    for (let i = 0, l = elements.length; i < l; i++) {
      elements[i].setAttribute('src', elements[i].src);
    }

    const result = target.innerHTML;
    target.remove();
    return result;
  }

  createPlaceholder(el, clear = true) {
    if (clear) {
      el.innerHTML = '';
    }

    return DomHelper.createElement({
      parent: el,
      id: IdHelper.generateId('export')
    });
  }

  getVirtualScrollerHeight(client) {
    let result = 0; // If overlay scroll is enabled, this will return 0
    // when disabled, it will report proper virutalScrollers element height

    client.eachSubGrid(subGrid => {
      if (subGrid.overflowingHorizontally) {
        result = DomHelper.scrollBarWidth;
      }
    }); // If there's a visible scrollbar, need to also take border height into account

    return result === 0 ? result : result + 1;
  } //endregion

  inchToPx(value) {
    // 1in = 96px for screens
    // https://developer.mozilla.org/en-US/docs/Web/CSS/length#Absolute_length_units
    return value * 96;
  }

  getScaleValue(base, value) {
    return Math.floor(base * 10000 / value) / 10000;
  }

  getVisibleRowsCount(client) {
    const rowManager = client.rowManager,
          // We cannot rely on `visibleRowCount` property, it is a mere estimate
    firstVisibleIndex = rowManager.rows.indexOf(rowManager.firstVisibleRow),
          lastVisibleIndex = rowManager.rows.indexOf(rowManager.lastVisibleRow);
    let result; // first index could be negative only if there are no rendered rows, in which case we return store count

    if (firstVisibleIndex !== -1) {
      // last visible index could be negative if last actually visible row doesn't reach view bottom, i.e.
      // view is not completely filled with rows
      if (lastVisibleIndex === -1) {
        result = client.store.count - firstVisibleIndex;
      } else {
        // for a zero-based index we need to add 1 to the result
        result = lastVisibleIndex - firstVisibleIndex + 1;
      }
    } else {
      result = client.store.count;
    }

    return result;
  }

  async export(config) {
    const me = this;
    let pages;
    me.beforeExport();
    me.saveState(config);
    await me.prepareComponent(config);

    try {
      pages = await me.getPages(config);
    } finally {
      await me.restoreComponent(config);
      me.stylesheets = null; // https://app.assembla.com/spaces/bryntum/tickets/9400-scrollrowintoview-promise-is-not-reliable/details
      // Restoring scroll might trigger rows repaint on next animation frame. We are
      // waiting for next animation frame in such case. Covered in SinglePage.t.js

      await new Promise(resolve => me.requestAnimationFrame(resolve));
      await me.restoreState(config);
    }

    return pages;
  }

  async getPages(config) {
    const generator = this.pagesExtractor(config),
          pages = [];
    let step;

    while ((step = await generator.next()) && !step.done) {
      pages.push(step.value);
    }

    return pages;
  } // Row buffer has to be adjusted to render complete row set per exported page. See virtual scrolling section in README
  // for more details

  adjustRowBuffer(client) {
    const {
      contentHeight
    } = this.exportMeta,
          {
      rowManager
    } = client;
    this.oldRowManagerConfig = {
      prependRowBuffer: rowManager.prependRowBuffer,
      appendRowBuffer: rowManager.appendRowBuffer
    }; // render 3 times more rows to get enough to fill exported page

    const adjustedRowBuffer = Math.ceil(contentHeight / rowManager.rowOffsetHeight);
    rowManager.prependRowBuffer = adjustedRowBuffer;
    rowManager.appendRowBuffer = adjustedRowBuffer; // Pass false to skip scrolling

    client.renderRows(false); // In case we are at the bottom of the view and we are increasing buffer, rowmanager might return to the top row
    // In this case we need to force it to render rows at the current scroll position

    client.rowManager.jumpToPosition(client.scrollable.y);
  }

  restoreRowBuffer(client) {
    client.rowManager.prependRowBuffer = this.oldRowManagerConfig.prependRowBuffer;
    client.rowManager.appendRowBuffer = this.oldRowManagerConfig.appendRowBuffer; // There is no need to force rendering rows here, those would be updated during applyState call on a grid.
    // More precisely - during setRowHeight method.
  }

  async prepareComponent(config) {
    const me = this,
          {
      client,
      columns,
      rowsRange,
      keepRegionSizes
    } = config,
          exportMeta = me.exportMeta = {
      totalWidth: 0,
      totalHeight: 0 - me.getVirtualScrollerHeight(client),
      subGrids: {}
    };
    client.columns.forEach(column => {
      if (columns.includes(column.id)) {
        column.show();
      } else {
        column.hide();
      }
    });

    if (client.rowManager.rowCount > 0) {
      if (rowsRange === RowsRange.all) {
        exportMeta.firstVisibleDataIndex = client.rowManager.rows[0].dataIndex;
        await client.scrollRowIntoView(client.store.first, {
          block: 'start'
        });
      } else {
        const firstVisibleDataIndex = exportMeta.firstVisibleDataIndex = client.rowManager.rows.find(row => row.bottom > client.scrollable.y).dataIndex;
        await client.scrollRowIntoView(client.store.getAt(firstVisibleDataIndex), {
          block: 'start'
        });
        config.alignRows = true;
      }
    } // clone whole grid element to the detached container

    this.cloneElement(client.element);
    const {
      element
    } = me;
    client.eachSubGrid(subGrid => {
      var _subGrid$splitterElem;

      const placeHolder = me.createPlaceholder(element.querySelector(`[id="${subGrid.id}"]`), false);
      let width;

      if (keepRegionSizes !== null && keepRegionSizes !== void 0 && keepRegionSizes[subGrid.region]) {
        width = subGrid.element.offsetWidth;
      } else {
        width = subGrid.columns.visibleColumns.reduce((result, column) => {
          if (typeof column.width === 'number') {
            result += column.width;
          } else {
            result += client.getHeaderElement(column.id).offsetWidth;
          }

          return result;
        }, 0);
      }

      exportMeta.totalWidth += width;
      const splitterWidth = ((_subGrid$splitterElem = subGrid.splitterElement) === null || _subGrid$splitterElem === void 0 ? void 0 : _subGrid$splitterElem.offsetWidth) || 0;
      exportMeta.totalWidth += splitterWidth;
      exportMeta.subGrids[subGrid.region] = {
        id: subGrid.id,
        headerId: subGrid.header && subGrid.header.id || null,
        footerId: subGrid.footer && subGrid.footer.id || null,
        rows: [],
        splitterWidth,
        placeHolder,
        width
      };
    });
  }

  prepareExportElement() {
    const me = this,
          {
      element,
      exportMeta
    } = me; // Exporters may change subGrid width, e.g. when specific date range is exported

    Object.values(exportMeta.subGrids).forEach(({
      width,
      id,
      headerId,
      footerId
    }) => {
      [id, headerId, footerId].forEach(id => {
        if (id) {
          const childElement = element.querySelector(`[id="${id}"]`); // SubGrid now always have header & footer, but not always in DOM

          if (childElement) {
            childElement.style.width = `${width}px`;
            childElement.style.flex = '';
          }
        }
      });
    });
    return element.innerHTML;
  }

  async restoreComponent(config) {}

  async scrollRowIntoView(client, index) {
    await client.scrollRowIntoView(client.store.getAt(index), {
      block: 'start'
    }); // #9400 - scrollRowIntoView promise is not reliable

    await new Promise(resolve => this.requestAnimationFrame(resolve));
  }

} // HACK: terser/obfuscator doesn't yet support async generators, when processing code it converts async generator to regular async
// function.

/**
 * Pages generator. Value should be string with exported HTML
 * @param {Object} config
 * @returns {AsyncIterableIterator<{value: String, done: Boolean}>}
 * @private
 */

Exporter.prototype.pagesExtractor = /*#__PURE__*/function () {
  var _pagesExtractor = _wrapAsyncGenerator(function* () {
    yield '';
  });

  function pagesExtractor() {
    return _pagesExtractor.apply(this, arguments);
  }

  return pagesExtractor;
}();

Exporter._$name = 'Exporter';

class MultiPageExporter extends Exporter {
  static get $name() {
    return 'MultiPageExporter';
  }

  static get type() {
    return 'multipage';
  }

  static get title() {
    // In case locale is missing exporter is still distinguishable
    return this.L('L{multipage}');
  }

  static get exportingPageText() {
    return 'L{exportingPage}';
  } //region State management

  async stateNextPage({
    client
  }) {
    const {
      exportMeta
    } = this;
    ++exportMeta.currentPage;
    ++exportMeta.verticalPosition;
    delete exportMeta.lastExportedRowBottom; // If current vertical position is greater than max vertical pages, switch to next column

    if (exportMeta.verticalPosition >= exportMeta.verticalPages) {
      exportMeta.verticalPosition = exportMeta.currentPageTopMargin = 0;
      ++exportMeta.horizontalPosition;
      await this.scrollRowIntoView(client, exportMeta.firstVisibleDataIndex, {
        block: 'start'
      });
    }
  } //endregion
  //region Preparation

  async prepareComponent(config) {
    await super.prepareComponent(config);
    const me = this,
          {
      exportMeta
    } = me,
          {
      client,
      headerTpl,
      footerTpl,
      alignRows,
      rowsRange
    } = config,
          paperFormat = PaperFormat[config.paperFormat],
          isPortrait = config.orientation === Orientation.portrait,
          paperWidth = isPortrait ? paperFormat.width : paperFormat.height,
          paperHeight = isPortrait ? paperFormat.height : paperFormat.width,
          pageWidth = me.inchToPx(paperWidth),
          pageHeight = me.inchToPx(paperHeight),
          horizontalPages = Math.ceil(exportMeta.totalWidth / pageWidth); // To estimate amount of pages correctly we need to know height of the header/footer on every page

    let totalHeight = exportMeta.totalHeight + client.height - client.bodyHeight + client.scrollable.scrollHeight,
        contentHeight = pageHeight;

    if (headerTpl) {
      contentHeight -= me.measureElement(headerTpl({
        totalWidth: exportMeta.totalWidth,
        totalPages: -1,
        currentPage: -1
      }));
    }

    if (footerTpl) {
      contentHeight -= me.measureElement(footerTpl({
        totalWidth: exportMeta.totalWidth,
        totalPages: -1,
        currentPage: -1
      }));
    }

    let verticalPages,
        totalRows = client.store.count;

    if (rowsRange === RowsRange.visible) {
      totalRows = me.getVisibleRowsCount(client);
      const lastRowBottom = client.rowManager.rows[totalRows - 1].bottom;
      totalHeight = totalHeight - client.scrollable.scrollHeight + lastRowBottom;
    } // alignRows config specifies if rows should be always fully visible. E.g. if row doesn't fit on the page, it goes
    // to the top of the next page

    if (alignRows && rowsRange !== RowsRange.visible) {
      // we need to estimate amount of vertical pages for case when we only put row on the page if it fits
      // first we need to know how much rows would fit one page, keeping in mind first page also contains header
      // This estimation is loose, because row height might differ much between pages
      const rowHeight = client.rowManager.rowOffsetHeight,
            rowsOnFirstPage = Math.floor((contentHeight - client.headerHeight) / rowHeight),
            rowsPerPage = Math.floor(contentHeight / rowHeight),
            remainingRows = totalRows - rowsOnFirstPage;
      verticalPages = 1 + Math.ceil(remainingRows / rowsPerPage);
    } else {
      verticalPages = Math.ceil(totalHeight / contentHeight);
    }

    Object.assign(exportMeta, {
      paperWidth,
      paperHeight,
      pageWidth,
      pageHeight,
      horizontalPages,
      verticalPages,
      totalHeight,
      contentHeight,
      totalRows,
      totalPages: horizontalPages * verticalPages,
      currentPage: 0,
      verticalPosition: 0,
      horizontalPosition: 0,
      currentPageTopMargin: 0
    });
    this.adjustRowBuffer(client);
  }

  async restoreComponent(config) {
    await super.restoreComponent(config);
    this.restoreRowBuffer(config.client);
  } //endregion

  async buildPage(config) {
    const me = this,
          {
      exportMeta
    } = me,
          {
      client,
      headerTpl,
      footerTpl,
      alignRows
    } = config,
          {
      totalWidth,
      totalPages,
      currentPage,
      subGrids,
      currentPageTopMargin,
      verticalPosition,
      totalRows
    } = exportMeta,
          {
      rowManager
    } = client,
          {
      rows
    } = rowManager; // Rows are stored in shared state object, need to clean it before exporting next page

    Object.values(subGrids).forEach(subGrid => subGrid.rows = []); // With variable row height total height might change after scroll, update it
    // to show content completely on the last page

    if (config.rowsRange === RowsRange.all) {
      exportMeta.totalHeight = client.height - client.bodyHeight + client.scrollable.scrollHeight - me.getVirtualScrollerHeight(client);
    }

    let headerHeight = 0,
        footerHeight = 0,
        index = config.rowsRange === RowsRange.visible ? rows.findIndex(r => r.bottom > client.scrollable.y) : rows.findIndex(r => r.bottom + currentPageTopMargin + client.headerHeight > 0),
        remainingHeight,
        header,
        footer;
    const firstRowIndex = index,
          // This is a portion of the row which is not visible, which means it shouldn't affect remaining height
    // Don't calculate for the first page
    overflowingHeight = verticalPosition === 0 ? 0 : rows[index].top + currentPageTopMargin + client.headerHeight; // Measure header and footer height

    if (headerTpl) {
      header = me.prepareHTML(headerTpl({
        totalWidth,
        totalPages,
        currentPage
      }));
      headerHeight = me.measureElement(header);
    }

    if (footerTpl) {
      footer = me.prepareHTML(footerTpl({
        totalWidth,
        totalPages,
        currentPage
      }));
      footerHeight = me.measureElement(footer);
    } // Calculate remaining height to fill with rows
    // remainingHeight is height of the page content region to fill. When next row is exported, this heights gets
    // reduced. Since top rows may be partially visible, it would lead to increasing error and eventually to incorrect
    // exported rows for the page

    remainingHeight = exportMeta.pageHeight - headerHeight - footerHeight - overflowingHeight; // first exported page container header

    if (verticalPosition === 0) {
      remainingHeight -= client.headerHeight;
    } // data index of the last collected row

    let lastDataIndex,
        offset = 0;

    while (remainingHeight > 0) {
      const row = rows[index];

      if (alignRows && remainingHeight < row.offsetHeight) {
        offset = -remainingHeight;
        remainingHeight = 0; // If we skip a row save its bottom to meta data in order to align canvases height
        // properly

        me.exportMeta.lastExportedRowBottom = rows[index - 1].bottom;
      } else {
        me.collectRow(row);
        remainingHeight -= row.offsetHeight;
        lastDataIndex = row.dataIndex; // Last row is processed, still need to fill the view

        if (++index === rows.length && remainingHeight > 0) {
          remainingHeight = 0;
        } else if (config.rowsRange === RowsRange.visible && index - firstRowIndex === totalRows) {
          remainingHeight = 0;
        }
      }
    }

    const lastRow = rows[index - 1];

    if (lastRow) {
      // Calculate exact grid height according to the the last exported row
      exportMeta.exactGridHeight = lastRow.bottom + client.footerContainer.offsetHeight + client.headerContainer.offsetHeight;
    }

    await me.onRowsCollected(rows.slice(firstRowIndex, index), config); // No scrolling required if we are only exporting currently visible rows

    if (config.rowsRange === RowsRange.visible) {
      exportMeta.exactGridHeight -= exportMeta.scrollableTopMargin = client.scrollable.y;
    } else {
      // With variable row height row manager might relayout rows to fix position, moving them up or down.
      const detacher = rowManager.on('offsetrows', ({
        offset: value
      }) => offset += value);
      await me.scrollRowIntoView(client, lastDataIndex + 1);
      detacher();
    }

    const html = me.buildPageHtml();
    return {
      html,
      header,
      footer,
      offset
    };
  }

  async onRowsCollected() {}

  collectRow(row) {
    const subGrids = this.exportMeta.subGrids;
    Object.entries(row.elements).forEach(([key, value]) => {
      subGrids[key].rows.push(value.outerHTML);
    });
  }

  buildPageHtml() {
    const me = this,
          {
      subGrids
    } = me.exportMeta; // Now when rows are collected, we need to add them to exported grid

    let html = me.prepareExportElement();
    Object.values(subGrids).forEach(({
      placeHolder,
      rows
    }) => {
      const placeHolderText = placeHolder.outerHTML;
      html = html.replace(placeHolderText, rows.join(''));
    });
    return html;
  }

  prepareExportElement() {
    const me = this,
          {
      element,
      exportMeta
    } = me;

    if (exportMeta.scrollableTopMargin) {
      element.querySelector('.b-grid-vertical-scroller').style.marginTop = `-${exportMeta.scrollableTopMargin}px`;
    }

    return super.prepareExportElement();
  }

} // HACK: terser/obfuscator doesn't yet support async generators, when processing code it converts async generator to regular async
// function.

MultiPageExporter.prototype.pagesExtractor = /*#__PURE__*/function () {
  var _pagesExtractor = _wrapAsyncGenerator(function* (config) {
    const me = this,
          {
      exportMeta,
      stylesheets
    } = me,
          {
      totalWidth,
      totalPages,
      paperWidth,
      paperHeight,
      contentHeight
    } = exportMeta;
    let currentPage;

    while ((currentPage = exportMeta.currentPage) < totalPages) {
      me.trigger('exportStep', {
        text: me.L(MultiPageExporter.exportingPageText, {
          currentPage,
          totalPages
        }),
        progress: Math.round((currentPage + 1) / totalPages * 90)
      });
      const {
        html,
        header,
        footer,
        offset
      } = yield _awaitAsyncGenerator(me.buildPage(config)); // TotalHeight might change in case of variable row heights
      // Move exported content in the visible frame

      const styles = [...stylesheets, `
                <style>
                    #${config.client.id} {
                        height: ${exportMeta.exactGridHeight}px !important;
                        width: ${totalWidth}px !important;
                    }
                    
                    .b-export-body .b-export-viewport {
                        margin-left : ${-paperWidth * exportMeta.horizontalPosition}in;
                        margin-top  : ${exportMeta.currentPageTopMargin}px;
                    }
                </style>
            `]; // when aligning rows, offset gets accumulated, so we need to take it into account

      exportMeta.currentPageTopMargin -= contentHeight + offset;
      yield _awaitAsyncGenerator(me.stateNextPage(config));
      yield {
        html: me.pageTpl({
          html,
          header,
          footer,
          styles,
          paperWidth,
          paperHeight
        })
      };
    }
  });

  function pagesExtractor(_x) {
    return _pagesExtractor.apply(this, arguments);
  }

  return pagesExtractor;
}();

MultiPageExporter._$name = 'MultiPageExporter';

class MultiPageVerticalExporter extends Exporter {
  static get $name() {
    return 'MultiPageVerticalExporter';
  }

  static get type() {
    return 'multipagevertical';
  }

  static get title() {
    // In case locale is missing exporter is still distinguishable
    return this.L('L{multipagevertical}');
  }

  static get exportingPageText() {
    return 'L{exportingPage}';
  } //region State management

  async stateNextPage({
    client
  }) {
    const {
      exportMeta
    } = this,
          {
      totalRows,
      processedRows,
      totalPages
    } = exportMeta;
    ++exportMeta.currentPage;
    ++exportMeta.verticalPosition; // With variable row heights it is possible that initial pages estimation is wrong. If we're out but there are
    // more rows to process - continue exporting

    if (exportMeta.currentPage === totalPages && processedRows.size !== totalRows) {
      ++exportMeta.totalPages;
      ++exportMeta.verticalPages;
    }
  } //endregion

  estimateTotalPages(config) {
    const me = this,
          {
      exportMeta
    } = me,
          {
      client,
      headerTpl,
      footerTpl,
      alignRows,
      rowsRange,
      repeatHeader
    } = config,
          {
      pageWidth,
      pageHeight,
      totalWidth
    } = exportMeta,
          scale = me.getScaleValue(pageWidth, totalWidth); // To estimate amount of pages correctly we need to know height of the header/footer on every page

    let totalHeight = 0 - me.getVirtualScrollerHeight(client) + client.height - client.bodyHeight + client.scrollable.scrollHeight,
        // We will be scaling content horizontally, need to adjust content height accordingly
    contentHeight = pageHeight / scale,
        totalRows = client.store.count,
        initialScroll = 0,
        verticalPages;

    if (headerTpl) {
      contentHeight -= me.measureElement(headerTpl({
        totalWidth,
        totalPages: -1,
        currentPage: -1
      }));
    }

    if (footerTpl) {
      contentHeight -= me.measureElement(footerTpl({
        totalWidth,
        totalPages: -1,
        currentPage: -1
      }));
    } // If we are repeating header on every page we have smaller contentHeight

    if (repeatHeader) {
      contentHeight -= client.headerHeight;
      totalHeight -= client.headerHeight;
    }

    if (rowsRange === RowsRange.visible) {
      const rowManager = client.rowManager,
            firstRow = rowManager.firstVisibleRow,
            lastRow = rowManager.lastVisibleRow;
      initialScroll = firstRow.top;
      totalRows = me.getVisibleRowsCount(client);
      totalHeight = totalHeight - client.scrollable.scrollHeight + lastRow.bottom - firstRow.top;
    } // alignRows config specifies if rows should be always fully visible. E.g. if row doesn't fit on the page, it goes
    // to the top of the next page

    if (alignRows && !repeatHeader && rowsRange !== RowsRange.visible) {
      // we need to estimate amount of vertical pages for case when we only put row on the page if it fits
      // first we need to know how much rows would fit one page, keeping in mind first page also contains header
      // This estimation is loose, because row height might differ much between pages
      const rowHeight = client.rowManager.rowOffsetHeight,
            rowsOnFirstPage = Math.floor((contentHeight - client.headerHeight) / rowHeight),
            rowsPerPage = Math.floor(contentHeight / rowHeight),
            remainingRows = totalRows - rowsOnFirstPage;
      verticalPages = 1 + Math.ceil(remainingRows / rowsPerPage);
    } else {
      verticalPages = Math.ceil(totalHeight / contentHeight);
    }

    Object.assign(exportMeta, {
      scale,
      contentHeight,
      totalRows,
      totalHeight,
      verticalPages,
      initialScroll,
      horizontalPages: 1,
      totalPages: verticalPages
    });
  }

  async prepareComponent(config) {
    await super.prepareComponent(config);
    const me = this,
          {
      exportMeta
    } = me,
          {
      client
    } = config,
          paperFormat = PaperFormat[config.paperFormat],
          isPortrait = config.orientation === Orientation.portrait,
          paperWidth = isPortrait ? paperFormat.width : paperFormat.height,
          paperHeight = isPortrait ? paperFormat.height : paperFormat.width,
          pageWidth = me.inchToPx(paperWidth),
          pageHeight = me.inchToPx(paperHeight),
          horizontalPages = 1;
    Object.assign(exportMeta, {
      paperWidth,
      paperHeight,
      pageWidth,
      pageHeight,
      horizontalPages,
      currentPage: 0,
      verticalPosition: 0,
      horizontalPosition: 0,
      currentPageTopMargin: 0,
      processedRows: new Set()
    });
    me.estimateTotalPages(config);
    me.adjustRowBuffer(client);
  }

  async restoreComponent(config) {
    await super.restoreComponent(config);
    this.restoreRowBuffer(config.client);
  }

  async buildPage(config) {
    const me = this,
          {
      exportMeta
    } = me,
          {
      client,
      headerTpl,
      footerTpl,
      alignRows,
      repeatHeader
    } = config,
          {
      totalWidth,
      totalPages,
      currentPage,
      subGrids,
      currentPageTopMargin,
      verticalPosition,
      totalRows,
      contentHeight
    } = exportMeta,
          // If we are repeating header we've already taked header height into account when setting content height
    clientHeaderHeight = repeatHeader ? 0 : client.headerHeight,
          {
      rowManager
    } = client,
          {
      rows
    } = rowManager; // Rows are stored in shared state object, need to clean it before exporting next page

    Object.values(subGrids).forEach(subGrid => subGrid.rows = []); // With variable row height total height might change after scroll, update it
    // to show content completely on the last page

    if (config.rowsRange === RowsRange.all) {
      exportMeta.totalHeight = client.height - client.bodyHeight + client.scrollable.scrollHeight - me.getVirtualScrollerHeight(client);
    }

    let index = config.rowsRange === RowsRange.visible ? rows.findIndex(r => r.bottom > client.scrollable.y) : rows.findIndex(r => r.bottom + currentPageTopMargin + clientHeaderHeight > 0),
        remainingHeight,
        header,
        footer;
    const firstRowIndex = index,
          // This is a portion of the row which is not visible, which means it shouldn't affect remaining height
    // Don't calculate for the first page
    overflowingHeight = verticalPosition === 0 ? 0 : rows[index].top + currentPageTopMargin + clientHeaderHeight; // Measure header and footer height

    if (headerTpl) {
      header = me.prepareHTML(headerTpl({
        totalWidth,
        totalPages,
        currentPage
      }));
    }

    if (footerTpl) {
      footer = me.prepareHTML(footerTpl({
        totalWidth,
        totalPages,
        currentPage
      }));
    } // Calculate remaining height to fill with rows
    // remainingHeight is height of the page content region to fill. When next row is exported, this heights gets
    // reduced. Since top rows may be partially visible, it would lead to increasing error and eventually to incorrect
    // exported rows for the page

    remainingHeight = contentHeight - overflowingHeight; // first exported page container header

    if (verticalPosition === 0) {
      remainingHeight -= clientHeaderHeight;
    } // data index of the last collected row

    let lastDataIndex,
        offset = 0;

    while (remainingHeight > 0) {
      const row = rows[index];

      if (alignRows && remainingHeight < row.offsetHeight) {
        offset = -remainingHeight;
        remainingHeight = 0;
      } else {
        me.collectRow(row);
        remainingHeight -= row.offsetHeight; // only mark row as processed if it fitted without overflow

        if (remainingHeight > 0) {
          // We cannot use simple counter here because some rows appear on 2 pages. Need to track unique identifier
          exportMeta.processedRows.add(row.dataIndex);
        }

        lastDataIndex = row.dataIndex; // Last row is processed, still need to fill the view

        if (++index === rows.length && remainingHeight > 0) {
          remainingHeight = 0;
        } else if (config.rowsRange === RowsRange.visible && index - firstRowIndex === totalRows) {
          remainingHeight = 0;
        }
      }
    }

    const lastRow = rows[index - 1];

    if (lastRow) {
      // Calculate exact grid height according to the the last exported row
      exportMeta.exactGridHeight = lastRow.bottom + client.footerContainer.offsetHeight + client.headerContainer.offsetHeight;
    }

    await me.onRowsCollected(rows.slice(firstRowIndex, index), config); // No scrolling required if we are only exporting currently visible rows

    if (config.rowsRange === RowsRange.visible) {
      exportMeta.scrollableTopMargin = client.scrollable.y;
    } else {
      // With variable row height row manager migh relayout rows to fix position, moving them up or down.
      const detacher = rowManager.on('offsetrows', ({
        offset: value
      }) => offset += value);
      await me.scrollRowIntoView(client, lastDataIndex + 1);
      detacher();
    }

    const html = me.buildPageHtml();
    return {
      html,
      header,
      footer,
      offset
    };
  }

  async onRowsCollected() {}

  collectRow(row) {
    const subGrids = this.exportMeta.subGrids;
    Object.entries(row.elements).forEach(([key, value]) => {
      subGrids[key].rows.push(value.outerHTML);
    });
  }

  buildPageHtml() {
    const me = this,
          {
      subGrids
    } = me.exportMeta; // Now when rows are collected, we need to add them to exported grid

    let html = me.prepareExportElement();
    Object.values(subGrids).forEach(({
      placeHolder,
      rows
    }) => {
      const placeHolderText = placeHolder.outerHTML;
      html = html.replace(placeHolderText, rows.join(''));
    });
    return html;
  }

} // HACK: terser/obfuscator doesn't yet support async generators, when processing code it converts async generator to regular async
// function.

MultiPageVerticalExporter.prototype.pagesExtractor = /*#__PURE__*/function () {
  var _pagesExtractor = _wrapAsyncGenerator(function* (config) {
    const me = this,
          {
      exportMeta,
      stylesheets
    } = me,
          {
      totalWidth,
      paperWidth,
      paperHeight,
      contentHeight,
      scale,
      initialScroll
    } = exportMeta;
    let {
      totalPages
    } = exportMeta,
        currentPage;

    while ((currentPage = exportMeta.currentPage) < totalPages) {
      me.trigger('exportStep', {
        text: me.L(MultiPageVerticalExporter.exportingPageText, {
          currentPage,
          totalPages
        }),
        progress: Math.round((currentPage + 1) / totalPages * 90)
      });
      const {
        html,
        header,
        footer,
        offset
      } = yield _awaitAsyncGenerator(me.buildPage(config)); // TotalHeight might change in case of variable row heights
      // Move exported content in the visible frame

      const styles = [...stylesheets, `
                <style>
                    #${config.client.id} {
                        width: ${totalWidth}px !important;
                    }
                    
                    .b-export .b-export-content {
                        transform: scale(${scale});
                        transform-origin: top left;
                        height: auto;
                    }
                </style>
            `];

      if (config.repeatHeader) {
        const gridHeight = exportMeta.exactGridHeight ? `${exportMeta.exactGridHeight + exportMeta.currentPageTopMargin}px` : '100%';
        styles.push(`
                <style>
                    #${config.client.id} {
                        height: ${gridHeight} !important;
                    }
                    
                    .b-export .b-export-content {
                        height: ${100 / scale}%;
                    }
                    
                    .b-export-body {
                        height: 100%;
                        display: flex;
                    }
                
                    .b-export-viewport {
                        height: 100%;
                    }
                    
                    .b-grid-vertical-scroller {
                        margin-top: ${exportMeta.currentPageTopMargin - initialScroll}px;
                    }
                </style>
                `);
      } else {
        const gridHeight = exportMeta.exactGridHeight || contentHeight - exportMeta.currentPageTopMargin;
        styles.push(`
                <style>
                    #${config.client.id} {
                        height: ${gridHeight}px !important;
                    }
                    
                    .b-export-body {
                        overflow: hidden;
                    }
                    
                    .b-export .b-export-content {
                        height: ${100 / scale}%;
                    }
                    
                    .b-export-body .b-export-viewport {
                        margin-top: ${exportMeta.currentPageTopMargin}px;
                    }
                    
                    .b-grid-vertical-scroller {
                        margin-top: -${initialScroll}px;
                    }
                </style>
                `);
      } // when aligning rows, offset gets accumulated, so we need to take it into account

      exportMeta.currentPageTopMargin -= contentHeight + offset;
      yield _awaitAsyncGenerator(me.stateNextPage(config));
      ({
        totalPages
      } = exportMeta);
      yield {
        html: me.pageTpl({
          html,
          header,
          footer,
          styles,
          paperWidth,
          paperHeight
        })
      };
    }
  });

  function pagesExtractor(_x) {
    return _pagesExtractor.apply(this, arguments);
  }

  return pagesExtractor;
}();

MultiPageVerticalExporter._$name = 'MultiPageVerticalExporter';

/**
 * @module Grid/feature/export/exporter/SinglePageExporter
 *
 * A single page export
 *
 * @classType singlepage
 * @extends Grid/feature/export/exporter/Exporter
 */

class SinglePageExporter extends Exporter {
  static get $name() {
    return 'SinglePageExporter';
  }

  static get type() {
    return 'singlepage';
  }

  static get title() {
    // In case locale is missing exporter is still distinguishable
    return this.localize('L{singlepage}');
  }

  static get defaultConfig() {
    return {
      /**
       * Set to true to center content horizontally on the page
       * @config {Boolean}
       */
      centerContentHorizontally: false
    };
  }

  async prepareComponent(config) {
    await super.prepareComponent(config);
    Object.assign(this.exportMeta, {
      verticalPages: 1,
      horizontalPages: 1,
      totalPages: 1,
      currentPage: 0,
      verticalPosition: 0,
      horizontalPosition: 0
    });
  }

  async onRowsCollected() {}

  positionRows(rows) {
    let currentTop = 0; // In case of variable row height row vertical position is not guaranteed to increase
    // monotonously. Position row manually instead

    return rows.map(([html, height]) => {
      const result = html.replace(/translate\(\d+px, \d+px\)/, `translate(0px, ${currentTop}px)`);
      currentTop += height;
      return result;
    });
  }

  collectRow(row) {
    const subGrids = this.exportMeta.subGrids;
    Object.entries(row.elements).forEach(([key, value]) => {
      subGrids[key].rows.push([value.outerHTML, row.offsetHeight]);
    });
  }

  buildPageHtml() {
    const me = this,
          {
      subGrids
    } = me.exportMeta; // Now when rows are collected, we need to add them to exported grid

    let html = me.prepareExportElement();
    Object.values(subGrids).forEach(({
      placeHolder,
      rows
    }) => {
      const placeHolderText = placeHolder.outerHTML;
      html = html.replace(placeHolderText, me.positionRows(rows).join(''));
    });
    return html;
  }

} // HACK: terser/obfuscator doesn't yet support async generators, when processing code it converts async generator to regular async
// function.

SinglePageExporter.prototype.pagesExtractor = /*#__PURE__*/function () {
  var _pagesExtractor = _wrapAsyncGenerator(function* (config) {
    // When we prepared grid we stretched it horizontally, now we need to gather all rows
    // There are two ways:
    // 1. set component height to scrollable.scrollHeight value to render all rows at once (maybe a bit more complex
    // if rows have variable height)
    // 2. iterate over rows, scrolling new portion into view once in a while
    // #1 sounds simpler, but that might require too much rendering, let's scroll rows instead
    const me = this,
          {
      client
    } = config,
          {
      rowManager,
      store
    } = client,
          styles = me.stylesheets,
          portrait = config.orientation === Orientation.portrait,
          paperFormat = PaperFormat[config.paperFormat],
          paperWidth = portrait ? paperFormat.width : paperFormat.height,
          paperHeight = portrait ? paperFormat.height : paperFormat.width,
          totalRows = config.rowsRange === RowsRange.visible && store.count // visibleRowCount is a projection of how much rows will fit the view, which should be
    // maximum amount of exported rows. and there can be less
    ? Math.min(store.count, rowManager.visibleRowCount) : store.count;
    let {
      totalHeight,
      totalWidth
    } = me.exportMeta,
        processedRows = 0,
        lastDataIndex = -1,
        header,
        footer;

    if (rowManager.rows.length > 0) {
      if (config.rowsRange === RowsRange.visible) {
        lastDataIndex = rowManager.rows.find(row => row.bottom > client.scrollable.y).dataIndex - 1;
      } // Collecting rows

      while (processedRows < totalRows) {
        const rows = rowManager.rows,
              lastRow = rows[rows.length - 1],
              lastProcessedRowIndex = processedRows;
        rows.forEach(row => {
          // When we are scrolling rows will be duplicated even with disabled buffers (e.g. when we are trying to
          // scroll last record into view). So we store last processed row dataIndex (which is always growing
          // sequence) and filter all rows with lower/same dataIndex
          if (row.dataIndex > lastDataIndex && processedRows < totalRows) {
            ++processedRows;
            totalHeight += row.offsetHeight;
            me.collectRow(row);
          }
        }); // Calculate new rows processed in this iteration e.g. to collect events

        const firstNewRowIndex = rows.findIndex(r => r.dataIndex === lastDataIndex + 1),
              lastNewRowIndex = firstNewRowIndex + (processedRows - lastProcessedRowIndex);
        yield _awaitAsyncGenerator(me.onRowsCollected(rows.slice(firstNewRowIndex, lastNewRowIndex), config));

        if (processedRows < totalRows) {
          lastDataIndex = lastRow.dataIndex;
          yield _awaitAsyncGenerator(me.scrollRowIntoView(client, lastDataIndex + 1));
        }
      }
    }

    const html = me.buildPageHtml(); // Calculate header height

    totalHeight += client.height - client.bodyHeight;
    const totalClientHeight = totalHeight; // Measure header and footer height

    if (config.headerTpl) {
      header = me.prepareHTML(config.headerTpl({
        totalWidth
      }));
      const height = me.measureElement(header);
      totalHeight += height;
    }

    if (config.footerTpl) {
      footer = me.prepareHTML(config.footerTpl({
        totalWidth
      }));
      const height = me.measureElement(footer);
      totalHeight += height;
    }

    const widthScale = Math.min(1, me.getScaleValue(me.inchToPx(paperWidth), totalWidth)),
          heightScale = Math.min(1, me.getScaleValue(me.inchToPx(paperHeight), totalHeight)),
          scale = Math.min(widthScale, heightScale); // Now add style to stretch grid vertically

    styles.push(`<style>
                #${client.id} {
                    height: ${totalClientHeight}px !important;
                    width: ${totalWidth}px !important;
                }
                
                .b-export-content {
                    ${me.centerContentHorizontally ? 'left: 50%;' : ''}
                    transform: scale(${scale}) ${me.centerContentHorizontally ? 'translateX(-50%)' : ''};
                    transform-origin: top left;
                    height: ${scale === 1 ? 'inherit' : 'auto !important'};
                }
            </style>`);

    if (BrowserHelper.isIE11) {
      styles.push(`<style>
                .b-export-body {
                   min-height: ${totalClientHeight}px !important;
                }
         </style>`);
    } // This is a single page exporter so we only yield one page

    yield {
      html: me.pageTpl({
        html,
        header,
        footer,
        styles,
        paperWidth,
        paperHeight
      })
    };
  });

  function pagesExtractor(_x) {
    return _pagesExtractor.apply(this, arguments);
  }

  return pagesExtractor;
}();

SinglePageExporter._$name = 'SinglePageExporter';

/**
 * @module Grid/feature/export/PdfExport
 */

/**
 * Generates PDF/PNG files from the Grid component.
 *
 *
 * **NOTE:** This feature will make a fetch request to the server, posting
 * the HTML fragments to be exported. The {@link #config-exportServer} URL must be configured.
 *
 * ## Usage
 *
 * ```javascript
 * const grid = new Grid({
 *     features : {
 *         pdfExport : {
 *             exportServer : 'http://localhost:8080' // Required
 *         }
 *     }
 * })
 *
 * // Opens popup allowing to customize export settings
 * grid.features.pdfExport.showExportDialog();
 *
 * // Simple export
 * grid.features.pdfExport.export({
 *     columns : grid.columns.map(c => c.id) // Required, set list of column ids to export
 * }).then(result => {
 *     // Response instance and response content in JSON
 *     let { response } = result;
 * });
 * ```
 *
 * ## Exporters
 *
 * There are three exporters available by default: `singlepage`, `multipage` and `multipagevertical`:
 *  * `singlepage` -  generates single page with content scaled to fit the provided {@link #config-paperFormat}
 *  * `multipage` - generates as many pages as required to fit all requested content, unscaled
 *  * `multipagevertical` - a combination of two above: it scales content horizontally to fit into page width and then
 *  puts overflowing content on vertical pages. Like a scroll.
 *
 * ## Loading resources
 *
 * If you face a problem with loading resources when exporting, the cause might be that the application and the export server are hosted on different servers.
 * This is due to [Cross-Origin Resource Sharing](https://developer.mozilla.org/en-US/docs/Web/HTTP/CORS) (CORS). There are 2 options how to handle this:
 * - Allow cross origin requests from the server where your export is hosted to the server where your application is hosted;
 * - Copy all resources keeping the folder hierarchy from the server where your application is hosted to the server where your export is hosted
 * and setup paths using {@link #config-translateURLsToAbsolute} config and configure the export server to give access to the path:
 *
 * ```javascript
 * const grid = new Grid({
 *     features : {
 *         pdfExport : {
 *             exportServer : 'http://localhost:8080',
 *             translateURLsToAbsolute : 'http://localhost:8080/resources' // '/resources' is hardcoded in WebServer implementation
 *         }
 *     }
 * })
 * ```
 *
 * ```javascript
 * // Following path would be served by this address: http://localhost:8080/resources/
 * node ./src/server.js -h 8080 -r web/application/styles
 * ```
 *
 * where `web/application/styles` is a physical root location of the copied resources, for example:
 *
 * <img src="resources/images/export-server-resources.png" style="max-width : 500px" alt="Export server structure with copied resources" />
 *
 * @extends Core/mixin/InstancePlugin
 *
 * @demo Grid/export
 * @classtype pdfExport
 */

class PdfExport extends InstancePlugin {
  static get $name() {
    return 'PdfExport';
  }

  static get defaultConfig() {
    return {
      /**
       * URL of the print server.
       * @config {String}
       */
      exportServer: undefined,

      /**
       * Name of the exported file.
       * @config {String}
       */
      fileName: null,

      /**
       * Format of the exported file, selectable from `pdf` or `png`. By default plugin exports panel contents to PDF
       * but PNG file format is also available.
       * @config {String}
       * @default
       * @group Export file config
       */
      fileFormat: 'pdf',

      /**
       * Export server will navigate to this url first and then will change page content to whatever client sent.
       * This option is useful with react dev server, which has pretty strict CORS policy.
       * @config {String}
       */
      clientURL: null,

      /**
       * Export paper format. Available options are A1...A5, Legal, Letter.
       * @config {String}
       * @default
       * @group Export file config
       */
      paperFormat: 'A4',

      /**
       * Orientation. Options are `portrait` and `landscape`.
       * @config {String}
       * @default
       * @group Export file config
       */
      orientation: 'portrait',

      /**
       * Specifies which rows to export. `all` for complete set of rows, `visible` for only rows currently visible.
       * @config {String}
       * @group Export file config
       */
      rowsRange: 'all',

      /**
       * Set to true to align row top to the page top on every exported page. Only applied to multipage export.
       * @config {Boolean}
       * @default
       */
      alignRows: false,

      /**
       * Set to true to show column headers on every page. This will also set {@link #config-alignRows} to true.
       * Only applies to MultiPageVertical exporter.
       * @config {Boolean}
       * @default
       */
      repeatHeader: false,

      /**
       * By default subGrid width is changed to fit all exported columns. To keep certain subGrid size specify it
       * in the following form:
       * ```
       * keepRegionSizes : {
       *     locked : true
       * }
       * ```
       * @config {Object}
       * @default
       */
      keepRegionSizes: null,

      /**
       * When exporting large views (hundreds of pages) stringified HTML may exceed browser or server request
       * length limit. This config allows to specify how many pages to send to server in one request.
       * @config {Number}
       * @default
       * @private
       */
      pagesPerRequest: 0,

      /**
       * Config for exporter.
       * @config {Object}
       * @private
       */
      exporterConfig: null,

      /**
       * Type of the exporter to use. Should be one of the configured {@link #config-exporters}
       * @config {String}
       * @default
       */
      exporterType: 'singlepage',

      /**
       * List of exporter classes to use in export feature
       * @config {Grid.feature.export.exporter.Exporter[]}
       * @default
       */
      exporters: [SinglePageExporter, MultiPageExporter, MultiPageVerticalExporter],

      /**
       * `True` to replace all linked CSS files URLs to absolute before passing HTML to the server.
       * When passing a string the current origin of the CSS files URLS will be replaced by the passed origin.
       *
       * For example: css files pointing to /app.css will be translated from current origin to {translateURLsToAbsolute}/app.css
       * @config {Boolean|String}
       * @default
       */
      translateURLsToAbsolute: true,

      /**
       * When true links are converted to absolute by combining current window location (with replaced origin) with
       * resource link.
       * When false links are converted by combining new origin with resource link (for angular)
       * @config {Boolean}
       * @default
       */
      keepPathName: true,

      /**
       * When true, page will attempt to download generated file.
       * @config {Boolean}
       * @default
       */
      openAfterExport: true,

      /**
       * False to open in the current tab, true - in a new tab
       * @config {Boolean}
       * @default
       */
      openInNewTab: false,

      /**
       * A template function used to generate a page header. It is passed an object with ´currentPage´ and `totalPages´ properties.
       *
       * ```javascript
       * let grid = new Grid({
       *     appendTo   : 'container',
       *     features : {
       *         pdfExport : {
       *             exportServer : 'http://localhost:8080/',
       *             headerTpl : ({ currentPage, totalPages }) => `
       *                 <div class="demo-export-header">
       *                     <img src="coolcorp-logo.png"/>
       *                     <dl>
       *                         <dt>Date: ${DateHelper.format(new Date(), 'll LT')}</dt>
       *                         <dd>${totalPages ? `Page: ${currentPage + 1}/${totalPages}` : ''}</dd>
       *                     </dl>
       *                 </div>`
       *          }
       *     }
       * });
       * ```
       * @config {Function}
       */
      headerTpl: null,

      /**
       * A template function used to generate a page footer. It is passed an object with ´currentPage´ and `totalPages´ properties.
       *
       * ```javascript
       * let grid = new Grid({
       *      appendTo   : 'container',
       *      features : {
       *          pdfExport : {
       *              exportServer : 'http://localhost:8080/',
       *              footerTpl    : () => '<div class="demo-export-footer"><h3>© 2020 CoolCorp Inc</h3></div>'
       *          }
       *      }
       * });
       * ```
       * @config {Function}
       */
      footerTpl: null,

      /**
       * An object containing the Fetch options to pass to the export server request. Use this to control if credentials are sent
       * and other options, read more at [MDN](https://developer.mozilla.org/en-US/docs/Web/API/Fetch_API/Using_Fetch).
       * @config {Object}
       */
      fetchOptions: null,

      /**
       * A message to be shown when Export feature is performing export.
       * @config {String}
       * @default "Generating pages..."
       */
      exportMask: 'L{Generating pages}',

      /**
       * A message to be shown when export is almost done.
       * @config {String}
       * @default "Waiting for response from server..."
       */
      exportProgressMask: 'L{Waiting for response from server}',
      localizableProperties: ['exportMask', 'exportProgressMask']
    };
  }

  doDestroy() {
    if (this.exportDialog) {
      this.exportDialog.destroy();
    }

    super.doDestroy();
  }
  /**
   * When export is started from GUI ({@link Grid.view.export.ExportDialog}), export promise can be accessed via
   * this property.
   * @property {Promise}
   */

  get currentExportPromise() {
    return this._currentExportPromise;
  }

  set currentExportPromise(value) {
    this._currentExportPromise = value;
  }

  get exportersMap() {
    return this._exportersMap || (this._exportersMap = new Map());
  }

  getExporter(config = {}) {
    const me = this,
          {
      exportersMap
    } = me,
          {
      type
    } = config;
    let exporter;

    if (exportersMap.has(type)) {
      exporter = exportersMap.get(type);
    } else {
      const exporterClass = this.exporters.find(cls => cls.type === type);

      if (!exporterClass) {
        throw new Error(`Exporter type ${type} is not found. Make sure you've configured it`);
      }

      config = ObjectHelper.clone(config);
      delete config.type;
      exporter = new exporterClass(config);
      exporter.relayAll(me);
      exportersMap.set(type, exporter);
    }

    return exporter;
  }

  buildRequest(pages, config) {
    return {
      html: JSON.stringify(pages),
      fileFormat: config.fileFormat,
      format: config.paperFormat,
      orientation: config.orientation
    };
  }

  buildExportConfig(config = {}) {
    const me = this,
          {
      client,
      exportServer,
      clientURL,
      fileFormat,
      fileName,
      paperFormat,
      rowsRange,
      alignRows,
      repeatHeader,
      keepRegionSizes,
      orientation,
      exporters,
      translateURLsToAbsolute,
      keepPathName,
      headerTpl,
      footerTpl
    } = me;

    if (!config.columns) {
      config.columns = client.columns.visibleColumns.map(c => c.id);
    }

    const result = ObjectHelper.assign({
      client,
      exportServer,
      clientURL,
      fileFormat,
      paperFormat,
      rowsRange,
      alignRows,
      repeatHeader,
      keepRegionSizes,
      orientation,
      translateURLsToAbsolute,
      keepPathName,
      headerTpl,
      footerTpl,
      exporterType: exporters[0].type,
      fileName: fileName || client.$$name
    }, config); // Only vertical exporter is supported

    if (result.exporterType !== 'multipagevertical') {
      result.repeatHeader = false;
    } // Align rows by default

    if (!('alignRows' in config) && config.repeatHeader) {
      result.alignRows = true;
    } // Only change this setting if it is default (false) and not provided directly in config

    if (!('keepRegionSizes' in config) && !result.keepRegionSizes) {
      const collapsed = [],
            keepRegionSizes = {}; // If there's at least one collapsed region - lock other region sizes

      client.eachSubGrid(s => s.collapsed && collapsed.push(s.region));

      if (collapsed.length) {
        client.eachSubGrid(s => {
          if (!collapsed.includes(s.region)) {
            keepRegionSizes[s.region] = true;
          }
        });
        result.keepRegionSizes = keepRegionSizes;
      }
    }

    return result;
  }
  /**
   * Starts the export process. Accepts a config object which overrides any default configs.
   * **NOTE**. Component should not be interacted with when export is in progress
   *
   * @param {Object} config
   * @param {String[]} config.columns (requried) List of column ids to export. E.g.
   * ```grid.features.pdfExport.export({ columns : grid.columns.map(c => c.id) })```
   * @returns {Promise} Object of the following structure
   * ```
   * {
   *     response // Response instance
   * }
   * ```
   * @async
   */

  async export(config = {}) {
    const me = this,
          {
      client,
      pagesPerRequest
    } = me;
    config = me.buildExportConfig(config);
    let result;
    config.exporterConfig = ObjectHelper.assign({
      type: config.exporterType,
      translateURLsToAbsolute: config.translateURLsToAbsolute,
      keepPathName: config.keepPathName
    }, config.exporterConfig || {});
    /**
     * Fires before export started. Return `false` to cancel the export.
     * @event beforeExport
     * @preventable
     * @param {Object} config Export config
     */

    if (client.trigger('beforeExport', config) !== false) {
      // Raise flag on the client to render all suggested dependencies
      client.ignoreViewBox = true; // This mask should be always visible to protect grid from changes even if the mask message is not visible
      // due to the export dialog which is rendered above the grid's mask. The dialog has its own mask which shares the export message.

      client.mask(me.exportMask);

      try {
        const exporter = me.getExporter(config.exporterConfig);

        if (pagesPerRequest === 0) {
          const pages = await exporter.export(config);
          /**
           * Fires when export progress changes
           * @event exportStep
           * @param {Number} progress Current progress, 0-100
           * @param {String} text Optional text to show
           */

          me.trigger('exportStep', {
            progress: 90,
            text: me.exportProgressMask
          });
          const response = await AjaxHelper.post(config.exportServer, {
            html: pages,
            orientation: config.orientation,
            format: config.paperFormat,
            fileFormat: config.fileFormat,
            fileName: config.fileName,
            clientURL: config.clientURL
          }, Object.assign({
            credentials: 'omit',
            parseJson: true
          }, me.fetchOptions));
          result = {
            response
          };

          if (response.ok) {
            const responseJSON = response.parsedJson;

            if (me.openAfterExport) {
              if (responseJSON.success) {
                if (BrowserHelper.isIE11) {
                  window.open(responseJSON.url, 'ExportedPanel');
                } else {
                  const link = document.createElement('a');
                  link.download = config.fileName;
                  link.href = responseJSON.url;

                  if (me.openInNewTab) {
                    link.target = '_blank';
                  }

                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }
              } else {
                WidgetHelper.toast(responseJSON.msg);
              }
            }
          }
        }
      } catch (error) {
        if (error instanceof Response) {
          result = {
            response: error
          };
        } else {
          result = {
            error
          };
        }

        throw error;
      } finally {
        if (me.exportDialog) {
          me.exportDialog.close();
        }

        client.unmask();
        client.ignoreViewBox = false;
        /**
         * Fires when export has finished
         * @event export
         * @param {Response} [response] Optional response, if received
         * @param {Error} [error] Optional error, if exception occurred
         */

        me.trigger('export', result);

        if (result.error) {
          WidgetHelper.toast(me.L('L{Export failed}'));
        } else if (!result.response.ok) {
          WidgetHelper.toast(me.L('L{Server error}'));
        }

        client.trigger('export', result);
      }
    }

    return result;
  }

  get defaultExportDialogConfig() {
    return ObjectHelper.copyProperties({}, this, ['client', 'exporters', 'exporterType', 'orientation', 'fileFormat', 'paperFormat', 'alignRows', 'rowsRange', 'repeatHeader']);
  }
  /**
   * Shows {@link Grid.view.export.ExportDialog export dialog}
   * @returns {Promise}
   */

  showExportDialog() {
    const me = this;

    if (!me.exportDialog) {
      me.exportDialog = new ExportDialog(Object.assign({}, me.defaultExportDialogConfig, {
        listeners: {
          export: me.onExportDialogExport,
          thisObj: me
        }
      }));
    }

    return me.exportDialog.show();
  }

  onExportDialogExport({
    values
  }) {
    const me = this;
    me.mask = new Mask({
      progress: 0,
      maxProgress: 100,
      text: me.exportMask,
      target: me.exportDialog.element
    });
    const detacher = me.on({
      export() {
        me.mask.close();
        detacher();
      },

      exportstep({
        progress,
        text
      }) {
        me.mask.progress = progress;

        if (text != null) {
          me.mask.text = text;
        }
      }

    });
    me.currentExportPromise = me.export(values); // Clear current export promise

    me.currentExportPromise.then(() => me.currentExportPromise = null);
  }

}
PdfExport._$name = 'PdfExport';
GridFeatureManager.registerFeature(PdfExport, false, 'Grid'); // Format expected by export server
// const pageFormat = {
//     html       : '',
//     column     : 1,
//     number     : 1,
//     row        : 1,
//     rowsHeight : 1
// };
//
// const format = {
//     fileFormat  : 'pdf',
//     format      : 'A4',
//     orientation : 'portrait',
//     range       : 'complete',
//     html        : { array : JSON.stringify(pageFormat) }
// };

/**
 * @module Grid/util/TableExporter
 */

/**
 * This class transforms grid component into two arrays: rows and columns. Columns array contains objects with
 * meta information about column: field name, column name, width and type of the rendered value, rows array contains
 * arrays of cell values.
 *
 * ```javascript
 * const exporter = new TableExporter({ target : grid });
 * exporter.export()
 *
 * // Output
 * {
 *     columns : [
 *         { field : 'name',     value : 'First name', type : 'string',  width : 100 },
 *         { field : 'surname',  value : 'Last name',  type : 'string',  width : 100 },
 *         { field : 'age',      value : 'Age',        type : 'number',  width : 50  },
 *         { field : 'married',  value : 'Married',    type : 'boolean', width : 50  },
 *         { field : 'children', value : 'Children',   type : 'object',  width : 100 }
 *     ],
 *     rows : [
 *         ['Michael', 'Scott',   40, false, []],
 *         ['Jim',     'Halpert', 30, true,  [...]]
 *     ]
 * }
 * ```
 *
 * ## How data is exported
 *
 * Exporter iterates over store records and processes each record for each column being exported. Exporter uses same
 * approach to retrieve data as column: reading record field, configured on the column, or calling renderer function
 * if one is provided. This means data can be of any type: primitives or objects. So children array in the above code
 * snippet may contain instances of child record class.
 *
 * ## Column renderers
 *
 * Column renderers are commonly used to style the cell, or even render more HTML into it, like {@link Grid.column.WidgetColumn}
 * does. This is not applicable in case of export. Also, given grid uses virtual rendering (only renders visible rows) and
 * exporter iterates over all records, not just visible ones, we cannot provide all data necessary to the renderer. Some
 * arguments, like cellElement and row, wouldn't exist. Thus renderer is called with as much data we have: value,
 * record, column, grid, other {@link Grid.column.Column#config-renderer documented arguments} would be undefined.
 *
 * Exporter adds one more flag for renderer function: isExport. When renderer receives this flag it knows
 * data is being exported and can skip DOM work to return simpler value. Below snippet shows simplified code of the
 * widget column handling export:
 *
 * ```javascript
 * renderer({ isExport }) {
 *     if (isExport) {
 *         return null;
 *     }
 *     else {
 *         // widget rendering routine
 *         ...
 *     }
 * }
 * ```
 *
 * ## Column types
 *
 * Column types are not actually a complete list of JavaScript types (you can get actual type of the cell using typeof) it
 * is a simple and helpful meta information.
 *
 * Available column types are:
 *  * string
 *  * number
 *  * boolean
 *  * date
 *  * object
 *
 * Everything which is not primitive like string/number/bool (or a date) is considered an object. This includes null, undefined,
 * arrays, classes, functions etc.
 *
 * ## Getting column type
 *
 * If existing grid column is used, column type first would be checked with {@link Grid.column.Column#config-exportedType exportedType}
 * config. If exportedType is undefined or column does not exist in grid, type is read from a record field definition.
 * If the field is not defined, object type is used.
 *
 * Configuring exported type:
 *
 * ```javascript
 * new Grid({
 *     columns : [
 *         {
 *             name         : 'Name',
 *             field        : 'name',
 *             exportedType : 'object',
 *             renderer     : ({ value, isExport }) => {
 *                 if (isExport) {
 *                     return { value }; // return value wrapped into object
 *                 }
 *             }
 *     ]
 * })
 * ```
 *
 * @extends Core/Base
 */

class TableExporter extends Base {
  static get defaultConfig() {
    return {
      /**
       * Target grid instance to export data from
       * @config {Grid.view.Grid} target
       */
      target: null,

      /**
       * Specifies a default column width if no width specified
       * @config {Number} defaultColumnWidth
       * @default
       */
      defaultColumnWidth: 100,

      /**
       * Set to false to export date as it is displayed by Date column formatter
       * @config {Boolean}
       * @default
       */
      exportDateAsInstance: true,

      /**
       * If true and the grid is grouped, shows the grouped value in the first column. True by default.
       * @config {Boolean} showGroupHeader
       * @default
       */
      showGroupHeader: true,

      /**
       * An array of columns configuration used to specify columns width, headers name, and column fields to get the data from.
       * 'field' config is required. If 'text' is missing, it will try to get it retrieved from the grid column or the 'field' config.
       * If 'width' is missing, it will try to get it retrieved from the grid column or {@link #config-defaultColumnWidth} config.
       * If no columns provided the config will be generated from the grid columns.
       *
       * For example:
       * ```javascript
       * columns : [
       *     'firstName', // field
       *     'age', // field
       *     { text : 'Starts', field : 'start', width : 140 },
       *     { text : 'Ends', field : 'finish', width : 140 }
       * ]
       * ```
       *
       * @config {String[]|Object[]} columns
       * @default
       */
      columns: null,

      /**
       * When true and tree is being exported, node names are indented with {@link #config-indentationSymbol}
       * @config {Boolean}
       * @default
       */
      indent: true,

      /**
       * This symbol (four spaces by default) is used to indent node names when {@link #config-indent} is true
       * @config {String}
       * @default
       */
      indentationSymbol: '\u00A0\u00A0\u00A0\u00A0'
    };
  }
  /**
   * Exports grid data according to provided config
   * @param {Object} config
   * @returns {{ rows : Object[][], columns : Object[] }}
   */

  export(config = {}) {
    const me = this;
    config = ObjectHelper.assign({}, me.config, config);
    me.normalizeColumns(config);
    return me.generateExportData(config);
  }

  generateExportData(config) {
    const me = this,
          columns = me.generateColumns(config),
          rows = me.generateRows(config);
    return {
      rows,
      columns
    };
  }

  normalizeColumns(config) {
    // In case columns are provided we need to use normalized config. If those are not provided, we are going
    // to use real columns, possible invoking renderers (we need to pass column instance to the renderer to
    // avoid breaking API too much)
    const columns = config.columns || this.target.columns.visibleColumns.filter(rec => rec.exportable !== false);
    config.columns = columns.map(col => {
      if (typeof col === 'string') {
        return this.target.columns.find(column => column.field === col) || {
          field: col
        };
      } else {
        return col;
      }
    });
  }

  generateColumns(config) {
    return config.columns.map(col => this.processColumn(col, config));
  }

  generateRows(config) {
    const {
      columns
    } = config;

    if (!columns.length) {
      return [];
    }

    const me = this,
          {
      target
    } = me;
    return target.store // although columns are taken from config, it is convenient to provide them as a separate argument
    // becuase that allows to override set of columns to process
    .map(record => me.processRecord(record, columns, config)) // filter out empty rows
    .filter(cells => cells === null || cells === void 0 ? void 0 : cells.length);
  }

  getColumnType(column, store = this.target.store) {
    let result = column.exportedType || 'object';

    if (column.exportedType === undefined) {
      if (column.field) {
        const fieldDefinition = store.modelClass.getFieldDefinition(column.field);

        if (fieldDefinition && fieldDefinition.type !== 'auto') {
          result = fieldDefinition.type;
        }
      }
    }

    return result;
  }
  /**
   * Extracts export data from the column instance
   * @param {Grid.column.Column} column
   * @param {Object} config
   * @private
   * @returns {Object}
   */

  processColumn(column, config) {
    const me = this,
          {
      target
    } = me,
          {
      defaultColumnWidth
    } = config;
    let {
      field,
      text: value,
      width,
      minWidth
    } = column; // If column is not configured with field, field is generated (see Column.js around line 514).
    // In export we want empty string there

    if (!(field in target.store.modelClass.fieldMap)) {
      field = '';
    } // If name or width is missing try to retrieve them from the grid column and the field, or use default values.

    if (!value || !width) {
      const gridColumn = target.columns.find(col => col.field === field);

      if (!value) {
        value = gridColumn && gridColumn.text || field;
      } // null or undefined

      if (width == null) {
        width = gridColumn && gridColumn.width || defaultColumnWidth;
      }
    }

    width = Math.max(width || defaultColumnWidth, minWidth || defaultColumnWidth);
    return {
      field,
      value,
      width,
      type: me.getColumnType(column)
    };
  }
  /**
   * Extracts export data from the record instance reading supplied column configs
   * @param {Core.data.Model|null} record If null is passed, all columns will be filled with empty strings
   * @param {Grid.column.Column[]} columns
   * @param {Object} config
   * @private
   * @returns {Object[]}
   */

  processRecord(record, columns, config) {
    const {
      target
    } = this,
          {
      showGroupHeader,
      indent,
      indentationSymbol
    } = config;
    let cells;

    if (!record) {
      cells = columns.map(() => '');
    } else if (record.isSpecialRow) {
      if (showGroupHeader && record.meta.groupRowFor) {
        cells = columns.map(column => {
          return target.features.group.buildGroupHeader({
            // Create dummy element to get html from
            cellElement: DomHelper.createElement(),
            grid: target,
            record,
            column
          });
        });
      }
    } else {
      cells = columns.map(column => {
        let value = record[column.field],
            useRenderer = column.renderer || column.defaultRenderer;

        if (useRenderer && !(value && column.isDateColumn && config.exportDateAsInstance)) {
          value = useRenderer.call(column, {
            value,
            record,
            column,
            grid: target,
            isExport: true
          });
        }

        if (indent && column.tree) {
          value = `${indentationSymbol.repeat(record.childLevel)}${value}`;
        }

        return value;
      });
    }

    return cells;
  }

}
TableExporter._$name = 'TableExporter';

class BooleanUnicodeSymbol {
  constructor(value) {
    this._value = value;
  }

  get value() {
    return this._value;
  }

  toString() {
    return Boolean(this.value) ? '✓' : '';
  }

}
BooleanUnicodeSymbol._$name = 'BooleanUnicodeSymbol';

/**
 * @module Grid/feature/experimental/ExcelExporter
 */

/**
 * **NOTE**: This class requires a 3rd party library to operate.
 *
 * A feature that allows exporting Grid data to Excel without involving the server. It uses {@link Grid.util.TableExporter}
 * class as data provider, [zipcelx library](https://www.npmjs.com/package/zipcelx)
 * forked and adjusted to support [column width config](https://github.com/bryntum/zipcelx/tree/column-width-build)
 * and [Microsoft XML specification](https://msdn.microsoft.com/en-us/library/office/documentformat.openxml.spreadsheet.aspx).
 * Zipcelx should be either in global scope (window) or can be provided with {@link #config-zipcelx} config.
 *
 * ```
 * // Global scope
 * <script src="zipcelx.js"></script>
 *
 * // importing from package
 * import zipcelx from 'zipcelx';
 *
 * const grid = new Grid({
 *     features : {
 *         excelExporter : {
 *             zipcelx
 *         }
 *     }
 * })
 * ```
 *
 * Here is an example of how to add the feature:
 *
 * ```javascript
 * const grid = new Grid({
 *     features : {
 *         excelExporter : {
 *             // Choose the date format for date fields
 *             dateFormat : 'YYYY-MM-DD HH:mm',
 *
 *             exporterConfig : {
 *                 // Choose the columns to include in the exported file
 *                 columns : ['name', 'role']
 *             }
 *         }
 *     }
 * });
 * ```
 *
 * And how to call it:
 *
 * ```javascript
 * grid.features.excelExporter.export({
 *     filename : 'Export',
 *     exporterConfig : {
 *         columns : [
 *             { text : 'First Name', field : 'firstName', width : 90 },
 *             { text : 'Age', field : 'age', width : 40 },
 *             { text : 'Starts', field : 'start', width : 140 },
 *             { text : 'Ends', field : 'finish', width : 140 }
 *         ]
 *     }
 * })
 * ```
 *
 * @extends Core/mixin/InstancePlugin
 * @demo Grid/exporttoexcel
 */

class ExcelExporter extends InstancePlugin {
  static get $name() {
    return 'ExcelExporter';
  }

  static get defaultConfig() {
    return {
      /**
       * Name of the exported file
       * @config {String} filename
       * @default
       */
      filename: null,

      /**
       * Defines how date in a cell will be formatted
       * @config {String} dateFormat
       * @default
       */
      dateFormat: 'YYYY-MM-DD',

      /**
       * Exporter class to use as a data provider. {@link Grid.util.TableExporter} by default.
       * @config {Grid.util.TableExporter}
       * @default
       */
      exporterClass: TableExporter,

      /**
       * Configuration object for {@link #config-exporterClass exporter class}.
       * @config {Object}
       */
      exporterConfig: null,

      /**
       * Reference to zipcelx library. If not provided, exporter will look in the global scope.
       * @config {Object}
       */
      zipcelx: null,

      /**
       * If this config is true, exporter will convert all empty values to ''. Empty values are:
       * * undefined, null, NaN
       * * Objects/class instances that do not have toString method defined and are stringified to [object Object]
       * * functions
       * @config {Boolean}
       */
      convertEmptyValueToEmptyString: true
    };
  }

  processValue(value) {
    if (value === undefined || value === null || Number.isNaN(value) || typeof value === 'function' || typeof value === 'object' && String(value) === '[object Object]') {
      return '';
    } else {
      return value;
    }
  }

  generateExportData(config) {
    const me = this,
          {
      rows,
      columns
    } = me.exporter.export(config.exporterConfig);
    return {
      rows: rows.map(row => {
        return row.map((value, index) => {
          var _columns$index;

          if (value instanceof Date) {
            value = DateHelper.format(value, config.dateFormat);
          } else if (typeof value === 'boolean') {
            value = new BooleanUnicodeSymbol(value);
          }

          if (me.convertEmptyValueToEmptyString) {
            value = me.processValue(value);
          }

          const type = ((_columns$index = columns[index]) === null || _columns$index === void 0 ? void 0 : _columns$index.type) === 'number' ? 'number' : 'string';
          return {
            value,
            type
          };
        });
      }),
      columns: columns.map(col => {
        let {
          field,
          value,
          width,
          type
        } = col; // when number column is exported with zipcelx, excel warns that sheet is broken and asks for repair
        // repair works, but having error on open doesn't look acceptable
        // type = type === 'number' ? 'number' : 'string';

        type = 'string';
        return {
          field,
          value,
          width,
          type
        };
      })
    };
  }
  /**
   * Generate and download a .xslx file.
   * @param {Object} config Optional configuration object, which overrides initial settings of the feature/exporter.
   */

  export(config = {}) {
    const me = this,
          zipcelx = me.zipcelx || window.zipcelx;

    if (!zipcelx) {
      throw new Error('ExcelExporter: "zipcelx" library is required');
    }

    if (me.disabled) {
      return;
    }

    config = ObjectHelper.assign({}, me.config, config);

    if (!config.filename) {
      config.filename = me.client.$$name;
    }

    const {
      filename
    } = config,
          {
      rows,
      columns
    } = me.generateExportData(config);
    zipcelx({
      filename,
      sheet: {
        data: [columns].concat(rows),
        cols: columns
      }
    });
  }

  construct(grid, config) {
    super.construct(grid, config);

    if (!this.zipcelx) {
      if (typeof zipcelx !== 'undefined') {
        this.zipcelx = window.zipcelx;
      }
    }
  }

  get exporter() {
    return this._exporter || (this._exporter = new this.exporterClass(Object.assign({
      target: this.client
    }, this.exporterConfig)));
  }

}
ExcelExporter._$name = 'ExcelExporter';
GridFeatureManager.registerFeature(ExcelExporter, false, 'Grid');

/**
 * @module Grid/util/ScrollManager
 */

/**
 * Monitors the mouse position over an element and scrolls the element if the cursor is close to edges. This is used by
 * various features to scroll the grid section element, for example dragging rows close to vertical edges.
 * Normally you should not need to interact with this class directly, but you can configure its
 * behavior using the {@link Grid.view.Grid#config-scrollManager} config.
 */

class ScrollManager extends Delayable(Base) {
  //region Default config
  static get defaultConfig() {
    return {
      /**
       * Width in pixels of the area at the edges of an element where scrolling should be triggered
       * @config {Number}
       * @default
       */
      zoneWidth: 50,

      /**
       * Scroll speed, higher number is slower. Calculated as "distance from zone edge / scrollSpeed"
       * @config {Number}
       * @default
       */
      scrollSpeed: 5,

      /**
       * Default element to use for vertical scrolling. Can be overridden in calls to `startMonitoring()`.
       * @internal
       * @config {HTMLElement}
       */
      verticalElement: null,

      /**
       * The direction(s) to scroll ('horizontal', 'vertical' or 'both')
       * @config {String}
       */
      direction: 'both',
      monitoring: [],

      /**
       * Number of milliseconds to wait before scroll starts when the mouse is moved close to an edge monitored by this scroll manager
       * @config {Number}
       * @default
       */
      startScrollDelay: 500,
      testConfig: {
        scrollSpeed: 2,
        startScrollDelay: 0
      }
    };
  } //endregion
  //region Start/stop monitoring

  /**
   * Starts monitoring an element. It will be scrolled if mouse is pressed and within zoneWidth pixels from element edge.
   * @param {HTMLElement|Object} elementOrConfig Element which might be scrolled or config { element, callback, thisObj }
   * @internal
   */

  startMonitoring(elementOrConfig) {
    const me = this,
          config = elementOrConfig instanceof HTMLElement ? {
      element: elementOrConfig
    } : elementOrConfig,
          element = config.element,
          verticalElement = config.verticalElement || me.verticalElement || element; // only interested in elements that exists and are scrollable

    if (element && (element.scrollWidth > element.offsetWidth || verticalElement.scrollHeight > verticalElement.offsetHeight)) {
      // already monitoring, bail out
      if (me.monitoring.find(m => m.element === element)) return;
      config.direction = config.direction || me.direction; // store some stuff needed later

      Object.assign(config, {
        verticalElement,
        scrollManager: me,
        startScrollLeft: element.scrollLeft,
        startScrollTop: verticalElement.scrollTop,
        scrollLeft: element.scrollLeft,
        scrollTop: verticalElement.scrollTop,
        scrollRelativeLeft: 0,
        scrollRelativeTop: 0
      });
      const handler = me.onMouseMove.bind(config); // listen to mousemove to determine if scroll needed or not

      me.mouseMoveDetacher = EventHelper.on({
        mousemove: handler,
        touchmove: handler,
        mouseleave: () => me.clearTimeout(me.scrollTimeout),
        element
      }); // Cache scrollWidth because it may actually change in Edge and allow scrolling to empty space when creating
      // dependency

      config.scrollWidth = element.scrollWidth;
      me.monitoring.push(config);
    }
  }
  /**
   * Stops monitoring an element.
   * @param {HTMLElement} element Element for which monitoring is not desired any more and should stop as soon as possible. Preferably right away, but no later than on next frame :)
   * @internal
   */

  stopMonitoring(element) {
    const me = this,
          config = me.monitoring.find(m => m.element === element); // cant stop nothing...

    if (!config) return;
    me.stopScroll(config);
    me.mouseMoveDetacher && me.mouseMoveDetacher();
    me.monitoring.splice(me.monitoring.indexOf(config), 1);
  } //endregion
  //region Internals

  /**
   * Starts scrolling (see #performScroll). Called from onMouseMove.
   * @private
   * @param {Object} config Config object
   */

  startScroll(config) {
    config.scrolling = true;
    this.performScroll(config);
  }
  /**
   * Stops scrolling. Called from onMouseMove.
   * @private
   * @param {Object} config
   */

  stopScroll(config) {
    const me = this;
    config.scrolling = false;

    if (config.scrollRequested) {
      me.cancelAnimationFrame(config.frameId);
      config.scrollRequested = false;
    }

    me.clearTimeout(me.scrollTimeout);
    me.scrollTimeout = null;
  }
  /**
   * Scrolls by an amount determined by config.scrollDeltaX/Y on each frame. Start/stop by calling #startScroll and
   * #stopScroll.
   * @private
   * @param {Object} config Config object
   */

  performScroll(config) {
    // this function is called repeatedly on each frame for as long as scrolling is needed
    // check that scrolling is needed
    if (config.scrolling && !config.scrollRequested) {
      const element = config.element,
            vertical = config.verticalElement; // scroll the determined amount of pixels

      if (config.scrollDeltaX !== 0) {
        config.scrollLeft += config.scrollDeltaX; // limit to element edges

        config.scrollLeft = Math.max(Math.min(config.scrollLeft, config.scrollWidth - element.clientWidth), 0);
        config.scrollRelativeLeft = config.scrollLeft - config.startScrollLeft;
        element.scrollLeft = config.scrollLeft;
      }

      if (config.scrollDeltaY !== 0) {
        config.scrollTop += config.scrollDeltaY; // limit to element edges

        config.scrollTop = Math.max(Math.min(config.scrollTop, vertical.scrollHeight - vertical.clientHeight), 0);
        config.scrollRelativeTop = config.scrollTop - config.startScrollTop;
        vertical.scrollTop = config.scrollTop;
      } // call callback if scrolled in any direction

      if ((config.scrollRelativeLeft !== 0 || config.scrollRelativeTop !== 0) && config.callback) {
        config.callback.call(config.thisObj || this, config);
      } // scroll some more on next frame

      config.scrollRequested = true;
      config.frameId = this.requestAnimationFrame(() => {
        config.scrollRequested = false;
        this.performScroll(config);
      });
    }
  }
  /**
   * Listener for mouse move on monitored element. Determines if scrolling is needed, and if so how fast to scroll.
   * See #zoneWidth & #scrollSpeed configs.
   * @private
   * @param {MouseEvent} event
   */

  onMouseMove(event) {
    const config = this,
          me = config.scrollManager,
          element = config.element,
          vertical = config.verticalElement,
          box = element.getBoundingClientRect(),
          vbox = vertical.getBoundingClientRect(),
          width = me.zoneWidth,
          speed = me.scrollSpeed; // scroll left or right?

    config.scrollDeltaX = 0;

    if (config.direction !== 'vertical') {
      if (event.clientX > box.right - width) {
        config.scrollDeltaX = Math.round((width - (box.right - event.clientX)) / speed) + 1;
      } else if (event.clientX < box.left + width) {
        config.scrollDeltaX = -Math.round((width + (box.left - event.clientX)) / speed) - 1;
      }
    } // scroll up or down?

    config.scrollDeltaY = 0;

    if (config.direction !== 'horizontal') {
      if (event.clientY > vbox.bottom - width) {
        config.scrollDeltaY = Math.round((width - (vbox.bottom - event.clientY)) / speed) + 1;
      } else if (event.clientY < vbox.top + width) {
        config.scrollDeltaY = -Math.round((width + (vbox.top - event.clientY)) / speed) - 1;
      }
    }

    if (config.scrollDeltaX === 0 && config.scrollDeltaY === 0) {
      me.stopScroll(config);
    } else if (!me.scrollTimeout) {
      me.scrollTimeout = me.setTimeout(() => me.startScroll(config), me.startScrollDelay);
    }
  } //endregion

}
ScrollManager._$name = 'ScrollManager';

/**
 * @module Grid/view/Bar
 */

/**
 * Base class used by Header and Footer. Holds an element for each column. Not intended to be used directly.
 *
 * @extends Core/widget/Widget
 * @internal
 * @abstract
 */

class Bar extends Widget {
  static get $name() {
    return 'Bar';
  } // Factoryable type name

  static get type() {
    return 'gridbar';
  }

  static get defaultConfig() {
    return {
      htmlCls: '',
      scrollable: {} // We need a scroller, but no dimensions scroll by default

    };
  } //region Init

  get columns() {
    return this._columns || this.subGrid.columns;
  } // Only needed for tests which create standalone Headers with no owning SubGrid.

  set columns(columns) {
    this._columns = columns;
  } //endregion

  /**
   * Fix cell widths (flex or fixed width) after rendering.
   * Not a part of template any longer because of CSP
   * @private
   */

  fixCellWidths() {
    const me = this;
    let hasFlex = false,
        flexBasis; // single header "cell"

    me.columns.traverse(column => {
      const cellEl = me.getBarCellElement(column.id),
            domWidth = DomHelper.setLength(column.width),
            domMinWidth = DomHelper.setLength(column.minWidth);

      if (cellEl) {
        // We have to work round the IE11 bug that flex-basis affects the content-box
        // and any padding is added as extra.
        // TODO: Remove  this when IE11 retires.
        if (BrowserHelper.isIE11) {
          flexBasis = `calc(${domWidth} - ${me.getLrPadding(cellEl)}px)`;
        } else {
          flexBasis = domWidth;
        }

        hasFlex = hasFlex || Boolean(column.flex); // Parent column without any specified width and flex should have flex calculated if any child has flex

        if (column.isParent && column.width == null && column.flex == null) {
          const flex = column.children.reduce((result, child) => result += !child.hidden && child.flex || 0, 0); // Do not want to store this flex value on the column since it is always calculated

          cellEl.style.flex = flex > 0 ? `${flex} 0 auto` : '';

          if (flex > 0) {
            // TODO: Figure out a better way of handling this, minWidth on the columns breaks the flexbox
            //  calculation compared to cells, making them misalign
            column.traverse(col => col.data.minWidth = null);
          }
        } // Normal case, set flex, width etc.
        else {
            if (parseInt(column.minWidth) >= 0) {
              cellEl.style.minWidth = domMinWidth;
            } // Clear all the things we might have to set to correct cell widths

            cellEl.style.flex = cellEl.style.flexBasis = cellEl.style.width = '';

            if (column.flex) {
              // If column has children we need to give it
              // flex-shrink: 0, flex-basis: auto so that it always
              // shrinkwraps its children without shrinking
              if (!isNaN(parseInt(column.flex)) && column.children) {
                cellEl.style.flex = `${column.flex} 0 auto`;
              } else {
                cellEl.style.flex = column.flex;
              }
            } else if (parseInt(column.width) >= 0) {
              const parent = column.parent; // Only grid header bar has a notion of group headers
              // Column is a child of an unwidthed group. We have to use width
              // to stretch it.

              if (me.isHeader && !parent.isRoot && !parent.width) {
                cellEl.style.width = domWidth;
              } else {
                // https://app.assembla.com/spaces/bryntum/tickets/8041
                // Column header widths must be set using flex-basis.
                // Using width means that wide widths cause a flexed SubGrid
                // to bust the flex rules.
                // TODO: When IE11 retires, remove calc() hacks to overcome its flexbox bugs.
                // Note that grid in Grid#onColumnsResized and SubGrid#fixCellWidths,
                // cells MUST still be sized using width since rows
                // are absolutely positioned and will not cause the busting out
                // problem, and rows will not stretch to shrinkwrap the cells
                // unless they are widthed with width.
                cellEl.style.flexBasis = flexBasis;
              }
            }
          }

        if (column.height >= 0) {
          cellEl.style.height = DomHelper.setLength(column.height);
        }
      }
    });
    me.element.classList[hasFlex ? 'add' : 'remove']('b-has-flex');
  }

  getLrPadding(cellEl) {
    if (!this.cellLrPadding) {
      const s = cellEl.ownerDocument.defaultView.getComputedStyle(cellEl);
      this.cellLrPadding = parseInt(s.getPropertyValue('padding-left')) + parseInt(s.getPropertyValue('padding-right')) + parseInt(s.getPropertyValue('border-left-width')) + parseInt(s.getPropertyValue('border-right-width'));
    }

    return this.cellLrPadding;
  }
  /**
   * Get the header or footer cell element for the specified column.
   * @param {String} columnId Column id
   * @returns {HTMLElement} Header or footer element, depending on which subclass is in use.
   * @private
   */

  getBarCellElement(columnId) {
    return this.element.querySelector(`[data-column-id="${columnId}"]`);
  }

} // Register this widget type with its Factory

Bar.initClass();
Bar._$name = 'Bar';

/**
 * @module Grid/view/Footer
 */

/**
 * Grid footer, used by Summary feature. You should not need to create instances manually.
 *
 * @extends Grid/view/Bar
 * @internal
 */

class Footer extends Bar {
  static get $name() {
    return 'Footer';
  } // Factoryable type name

  static get type() {
    return 'gridfooter';
  }

  startConfigure(config) {
    config.scrollable.overflowX = 'hidden-scroll';
    super.startConfigure(config);
  }

  get subGrid() {
    return this._subGrid;
  }

  set subGrid(subGrid) {
    this._subGrid = this.owner = subGrid;
  }

  refreshContent() {
    this.element.firstElementChild.innerHTML = this.contentTemplate();
    this.fixFooterWidths();
  }

  onPaint({
    firstPaint
  }) {
    if (firstPaint) {
      this.refreshContent();
    }
  }

  template() {
    const region = this.subGrid.region;
    return TemplateHelper.tpl`
            <div class="b-grid-footer-scroller b-grid-footer-scroller-${region}">
                <div data-reference="footersElement" class="b-grid-footers b-grid-footers-${region}" data-region="${region}"></div>
            </div>
        `;
  }

  get overflowElement() {
    return this.footersElement;
  } //region Getters

  /**
   * Get the footer cell element for the specified column.
   * @param {String} columnId Column id
   * @returns {HTMLElement} Footer cell element
   */

  getFooter(columnId) {
    return this.getBarCellElement(columnId);
  } //endregion

  /**
   * Footer template. Iterates leaf columns to create content.
   * Style not included because of CSP. Widths are fixed up in
   * {@link #function-fixFooterWidths}
   * @private
   */

  contentTemplate() {
    const me = this;
    return me.columns.visibleColumns.map(column => {
      return column.hidden ? '' : TemplateHelper.tpl`
                <div
                    class="b-grid-footer ${column.align ? `b-grid-footer-align-${column.align}` : ''} ${column.cls || ''}"
                    data-column="${column.field || ''}" data-column-id="${column.id}" data-all-index="${column.allIndex}"
                    >
                    ${column.footerText || ''}
                </div>`;
    }).join('');
  }
  /**
   * Fix footer widths (flex or fixed width) after rendering. Not a part of template any longer because of CSP
   * @private
   */

  fixFooterWidths() {
    this.fixCellWidths();
  }

} // Register this widget type with its Factory

Footer.initClass();
Footer._$name = 'Footer';

/**
 * @module Grid/row/Row
 */

/**
 * Represents a single rendered row in the grid. Consists of one row element for each SubGrid in use. The grid only
 * creates as many rows as needed to fill the current viewport (and a buffer). As the grid scrolls
 * the rows are repositioned and reused, there is not a one to one relation between rows and records.
 *
 * For normal use cases you should not have to use this class directly. Rely on using renderers instead.
 * @extends Core/Base
 */

class Row extends Delayable(Base) {
  static get configurable() {
    return {
      /**
       * @member {Core.helper.util.DomClassList|Object} cls
       * When __read__, this a {@link Core.helper.util.DomClassList DomClassList} of class names to be
       * applied to this Row's elements.
       *
       * It can be __set__ using Object notation where each property name with a truthy value is added as
       * a class, or as a regular space-separated string.
       */

      /**
       * The class name to initially add to all row elements
       * @config {String|Core.helper.util.DomClassList|Object}
       */
      cls: {
        $config: {
          equal: (c1, c2) => (c1 === null || c1 === void 0 ? void 0 : c1.isDomClassList) && (c2 === null || c2 === void 0 ? void 0 : c2.isDomClassList) && c1.isEqual(c2)
        },
        value: 'b-grid-row'
      }
    };
  } //region Init

  /**
   * Constructs a Row setting its index.
   * @param {Object} config A configuration object which must contain the following two properties:
   * @param {Grid.view.Grid} config.grid The owning Grid.
   * @param {Grid.row.RowManager} config.rowManager The owning RowManager.
   * @param {Number} index The index of the row within the RowManager's cache.
   * @function constructor
   * @internal
   */

  construct(config) {
    // Set up defaults and properties
    Object.assign(this, {
      _elements: {},
      _elementsArray: [],
      _cells: {},
      _allCells: [],
      _regions: [],
      lastHeight: 0,
      lastTop: -1,
      _dataIndex: 0,
      _top: 0,
      _height: 0,
      _id: null,
      forceInnerHTML: false,
      isGroupFooter: false
    }, config);
    super.construct();
  }

  doDestroy() {
    const me = this; // No need to clean elements up if the entire thing is being destroyed

    if (!me.rowManager.isDestroying) {
      me.removeElements();

      if (me.rowManager.idMap[me.id] === me) {
        delete me.rowManager.idMap[me.id];
      }
    }

    super.doDestroy();
  } //endregion
  //region Data getters/setters

  /**
   * Get index in RowManagers rows array
   * @property {Number}
   * @readonly
   */

  get index() {
    return this._index;
  }

  set index(index) {
    this._index = index;
  }
  /**
   * Get/set this rows current index in grids store
   * @property {Number}
   */

  get dataIndex() {
    return this._dataIndex;
  }

  set dataIndex(dataIndex) {
    if (this._dataIndex !== dataIndex) {
      const elements = this._elementsArray;
      this._dataIndex = dataIndex;

      for (let i = 0; i < elements.length; i++) {
        elements[i].dataset.index = dataIndex;
      }
    }
  }
  /**
   * Get/set id for currently rendered record
   * @property {String|Number}
   */

  get id() {
    return this._id;
  }

  set id(id) {
    const me = this,
          idObj = {
      id
    },
          idMap = me.rowManager.idMap,
          elements = me._elementsArray,
          cells = me._allCells;

    if (me._id != id || idMap[id] !== me) {
      if (idMap[me._id] === me) delete idMap[me._id];
      idMap[id] = me;
      me._id = id;

      for (let i = 0; i < elements.length; i++) {
        DomDataStore.assign(elements[i], idObj);
      }

      for (let i = 0; i < cells.length; i++) {
        DomDataStore.assign(cells[i], idObj);
      }

      for (let i = 0; i < elements.length; i++) {
        elements[i].dataset.id = id;
      }
    }
  } //endregion
  //region Row elements

  /**
   * Add a row element for specified region.
   * @param {String} region Region to add element for
   * @param {HTMLElement} element Element
   * @private
   */

  addElement(region, element) {
    const me = this;
    let cellElement = element.firstElementChild;
    me._elements[region] = element;

    me._elementsArray.push(element);

    me._regions.push(region);

    DomDataStore.assign(element, {
      index: me.index
    });
    me._cells[region] = [];

    while (cellElement) {
      me._cells[region].push(cellElement);

      me._allCells.push(cellElement);

      DomDataStore.set(cellElement, {
        column: cellElement.dataset.column,
        // TODO: dataset is slow, read from columnstore using index instead
        columnId: cellElement.dataset.columnId,
        rowElement: cellElement.parentNode,
        row: me
      });
      cellElement = cellElement.nextElementSibling;
    } // making css selectors simpler, dataset has bad performance but it is only set once and never read

    element.dataset.index = me.index;
  }
  /**
   * Get the element for the specified region.
   * @param {String} region
   * @returns {HTMLElement}
   */

  getElement(region) {
    return this._elements[region];
  }
  /**
   * Execute supplied function for each regions element.
   * @param {Function} fn
   */

  eachElement(fn) {
    for (let i = 0; i < this._elementsArray.length; i++) {
      fn(this._elementsArray[i], i);
    }
  }
  /**
   * Execute supplied function for each cell.
   * @param {Function} fn
   */

  eachCell(fn) {
    this._allCells.forEach(fn);
  }
  /**
   * Row elements (one for each region)
   * @type {HTMLElement[]}
   * @readonly
   */

  get elements() {
    return this._elements;
  } //endregion
  //region Cell elements

  /**
   * Row cell elements
   * @property {HTMLElement[]}
   * @readonly
   */

  get cells() {
    return this._allCells;
  }
  /**
   * Get cell elements for specified region.
   * @param {String} region Region to get elements for
   * @returns {HTMLElement[]} Array of cell elements
   */

  getCells(region) {
    return this._cells[region];
  }
  /**
   * Get the cell element for the specified column.
   * @param {String|Number} columnId Column id
   * @returns {HTMLElement} Cell element
   */

  getCell(columnId) {
    return this._allCells.find(cell => {
      const cellData = DomDataStore.get(cell);
      return cellData.columnId === columnId || cellData.column === columnId;
    });
  }

  removeElements(onlyRelease = false) {
    const me = this; // Triggered before the actual remove to allow cleaning up elements etc.

    me.rowManager.trigger('removeRow', {
      row: me
    });

    if (!onlyRelease) {
      me._elementsArray.forEach(element => element.remove());
    }

    me._elements = {};
    me._cells = {};
    me._elementsArray.length = me._regions.length = me._allCells.length = me.lastHeight = me.height = 0;
    me.lastTop = -1;
  } //endregion
  //region Height

  /**
   * Get/set row height
   * @property {Number}
   */

  get height() {
    return this._height;
  }

  set height(height) {
    this._height = height;
  }
  /**
   * Get row height including border
   * @property {Number}
   */

  get offsetHeight() {
    // me.height is specified height, add border height to it to get cells height to match specified rowHeight
    // border height is measured in Grid#get rowManager
    return this.height + this.grid._rowBorderHeight;
  }
  /**
   * Sync elements height to rows height
   * @private
   */

  updateElementsHeight() {
    const me = this;
    me.rowManager.storeKnownHeight(me.id, me.height); // prevent unnecessary style updates

    if (me.lastHeight !== me.height) {
      const elements = me._elementsArray;

      for (let i = 0; i < elements.length; i++) {
        elements[i].style.height = `${me.offsetHeight}px`;
      }

      me.lastHeight = me.height;
    }
  } //endregion
  //region CSS

  /**
   * Add css classes to each element.
   * @param {...String|Object|Core.helper.util.DomClassList} classes
   */

  addCls(classes) {
    this.updateCls(this.cls.add(classes));
  }
  /**
   * Remove css classes from each element.
   * @param {...String|Object|Core.helper.util.DomClassList} classes
   */

  removeCls(classes) {
    this.updateCls(this.cls.remove(classes));
  }
  /**
   * Adds/removes class names according to the passed object's properties.
   *
   * Properties with truthy values are added.
   * Properties with false values are removed.
   * @param {Object} classList Object containing properties to set/clear
   */

  assignCls(classes) {
    this.updateCls(this.cls.assign(classes));
  }

  changeCls(cls) {
    return cls !== null && cls !== void 0 && cls.isDomClassList ? cls : new DomClassList(cls);
  }

  updateCls(cls) {
    this.eachElement(element => DomHelper.syncClassList(element, cls));
  } //endregion
  //region Position

  /**
   * Is this the very first row?
   * @property {Boolean}
   * @readonly
   */

  get isFirst() {
    return this.dataIndex === 0;
  }
  /**
   * Row top coordinate
   * @property {Number}
   * @readonly
   */

  get top() {
    return this._top;
  }
  /**
   * Row bottom coordinate
   * @property {Number}
   * @readonly
   */

  get bottom() {
    return this._top + this._height + this.grid._rowBorderHeight;
  }
  /**
   * Sets top coordinate, translating elements position.
   * @param {Number} top Top coordinate
   * @param {Boolean} [silent] Specify `true` to not trigger translation event
   * @internal
   */

  setTop(top, silent) {
    if (this._top !== top) {
      this._top = top;
      this.translateElements(silent);
    }
  }
  /**
   * Sets bottom coordinate, translating elements position.
   * @param {Number} bottom Bottom coordinate
   * @param {Boolean} [silent] Specify `true` to not trigger translation event
   * @private
   */

  setBottom(bottom, silent) {
    this.setTop(bottom - this.offsetHeight, silent);
  }
  /**
   * Sets css transform to position elements at correct top position (translateY)
   * @private
   */

  translateElements(silent) {
    const me = this,
          positionMode = me.grid.positionMode;

    if (me.lastTop !== me.top) {
      const elements = me._elementsArray;

      for (let i = 0; i < elements.length; i++) {
        const style = elements[i].style;

        if (positionMode === 'translate') {
          style.transform = `translate(0,${me.top}px)`;
        } else if (positionMode === 'translate3d') {
          style.transform = `translate3d(0,${me.top}px,0)`;
        } else if (positionMode === 'position') {
          style.top = `${me.top}px`;
        }
      }

      if (!silent) {
        me.rowManager.trigger('translateRow', {
          row: me
        });
      }

      me.lastTop = me.top;
    }
  }
  /**
   * Moves all row elements up or down and updates model.
   * @param {Number} offsetTop Pixels to offset the elements
   * @private
   */

  offset(offsetTop) {
    let newTop = this._top + offsetTop; // Not allowed to go below zero (won't be reachable on scroll in that case)

    if (newTop < 0) {
      offsetTop -= newTop;
      newTop = 0;
    }

    this.setTop(newTop);
    return offsetTop;
  } //endregion
  //region Render

  /**
   * Renders a record into this rows elements (trigger event that subgrids catch to do the actual rendering).
   * @param {Number} recordIndex
   * @param {Core.data.Model} record
   * @param {Boolean} [updatingSingleRow]
   * @param {Boolean} [batch]
   * @private
   */

  render(recordIndex, record, updatingSingleRow = true, batch = false) {
    var _record, _record2, _maxRequestedHeight;

    const me = this,
          {
      cls,
      cells,
      grid,
      rowManager,
      height: oldHeight,
      _id: oldId
    } = me,
          rowElData = DomDataStore.get(me._elementsArray[0]),
          rowHeight = rowManager._rowHeight;
    let i = 0,
        cellElement,
        size; // no record specified, try looking up in store (false indicates empty row, don't do lookup

    if (!record && record !== false) {
      record = grid.store.getById(rowElData.id);
      recordIndex = grid.store.indexOf(record);
    } // Now we have acquired a record, see what classes it requires on the

    const rCls = (_record = record) === null || _record === void 0 ? void 0 : _record.cls,
          recordCls = rCls ? rCls.isDomClassList ? rCls : new DomClassList(rCls) : null;
    cls.assign({
      'b-grid-row-updating': updatingSingleRow && grid.transitionDuration,
      'b-selected': grid.isSelected((_record2 = record) === null || _record2 === void 0 ? void 0 : _record2.id)
    }); // These are DomClassLists, so they have to have their properties processed by add/remove

    if (me.lastRecordCls) {
      cls.remove(me.lastRecordCls);
    } // Assign our record's cls to the row, and cache the value so it can be removed next time round

    if (recordCls) {
      cls.add(recordCls);
      me.lastRecordCls = Object.assign({}, recordCls);
    } else {
      me.lastRecordCls = null;
    } // Flush any changes to our DomClassList to the Row's DOM

    me.updateCls(cls); // used by GroupSummary feature to clear row before

    rowManager.trigger('beforeRenderRow', {
      row: me,
      record,
      recordIndex,
      oldId
    });

    if (updatingSingleRow && grid.transitionDuration) {
      me.setTimeout(() => {
        cls.remove('b-grid-row-updating');
        me.updateCls(cls);
      }, grid.transitionDuration);
    }

    me.id = record.id;
    me.dataIndex = recordIndex; // Configured height, used as row height if renderers do not specify otherwise

    const height = !grid.fixedRowHeight && grid.getRowHeight(record) || rowHeight; // Max height returned by renderers

    let maxRequestedHeight = me.maxRequestedHeight = null;

    for (i = 0; i < cells.length; i++) {
      cellElement = cells[i];
      size = me.renderCell({
        cellElement,
        record,
        height,
        maxRequestedHeight,
        updatingSingleRow
      });

      if (!rowManager.fixedRowHeight) {
        // We want to make row in all regions as high as the highest cell
        if (size.height != null) {
          me.maxRequestedHeight = maxRequestedHeight = Math.max(maxRequestedHeight, size.height);
        }
      }
    }

    me.height = (_maxRequestedHeight = maxRequestedHeight) !== null && _maxRequestedHeight !== void 0 ? _maxRequestedHeight : height; // Height gets set during render, reflect on elements

    me.updateElementsHeight(); // Rerendering a row might change its height, which forces translation of all following rows

    if (updatingSingleRow) {
      if (oldHeight !== me.height) {
        rowManager.translateFromRow(me, batch);
      }

      rowManager.trigger('updateRow', {
        row: me,
        record,
        recordIndex,
        oldId
      });
      rowManager.trigger('renderDone');
    }

    rowManager.trigger('renderRow', {
      row: me,
      record,
      recordIndex,
      oldId
    });
    me.forceInnerHTML = false;
  }
  /**
   * Renders a single cell, calling features to allow them to hook.
   * @param {Object} options Rendering options
   * @param {HTMLElement} options.cellElement Cell element to render
   * @param {Core.data.Model} options.record Record, fetched from store if undefined
   * @param {Number} [options.height] Configured row height
   * @param {Number} [options.maxRequestedHeight] Maximum proposed row height from renderers
   * @param {Boolean} [options.updatingSingleRow] Rendered as part of updating a single row
   * @param {Boolean} [options.isMeasuring] Rendered as part of a measuring operation
   * @private
   */

  renderCell({
    cellElement,
    record,
    height,
    maxRequestedHeight,
    updatingSingleRow = true,
    isMeasuring = false
  }) {
    const me = this,
          {
      grid
    } = me,
          cellElementData = DomDataStore.get(cellElement),
          column = grid.columns.getById(cellElementData.columnId),
          rowElement = cellElementData.rowElement,
          rowElementData = DomDataStore.get(rowElement),
          {
      // Avoid two calls to col's getters by gathering these fields.
      internalCellCls,
      cellCls,
      align,
      renderer,
      defaultRenderer,
      id: columnId
    } = column,
          cellContext = {
      columnId,
      id: rowElementData.id
    },
          useRenderer = renderer || defaultRenderer;

    if (!record) {
      // Clear out row without record
      // Edge case, only happens if groups/tree is collapsed leading to fewer records than row elements
      if (rowElementData.id === null) {
        // setting to ' ' (space) makes it not remove firstChild, thus not breaking
        // when doing real render the next time
        // NOTE: have opted to not clear cell. might be confusing in DOM but simplifies for cells reusing
        // elements internally. Another option would be to have a derenderer per column which is called
        // cell.innerHTML = ' ';
        cellElement.className = 'b-grid-cell';
        return;
      }

      record = grid.store.getById(rowElementData.id);

      if (!record) {
        return;
      }
    } // Using new record for this row, consider it repurposed and inform Grid that cell is now visible
    // if (record.id !== oldId && col.reactiveRenderer) {
    //     const oldRecord = me.grid.store.getById(oldId);
    //     me.grid.cellBecameInvisible({ oldRecord : record, row : cellElementData.row, cell : cellElement });
    //     me.grid.cellBecameVisible({ record, row : cellElementData.row, cell : cellElement });
    // }
    // if (column.reactiveRenderer) {
    //     return null;
    // }

    let cellContent = column.getRawValue(record),
        shouldSetContent = true;
    const dataField = record.fieldMap[column.field],
          size = {
      configuredHeight: height,
      height: null,
      maxRequestedHeight
    },
          rendererData = {
      cellElement,
      dataField,
      rowElement,
      value: cellContent,
      record,
      column,
      size,
      grid,
      row: cellElementData.row,
      updatingSingleRow,
      isMeasuring
    },
          newCellClass = {
      'b-grid-cell': 1,
      [internalCellCls]: internalCellCls,
      // Check cell CSS should not be applied to group header rows
      [cellCls]: record.isSpecialRow && column.internalCellCls === 'b-check-cell' ? undefined : cellCls,
      'b-cell-dirty': record.isFieldModified(column.field),
      [`b-grid-cell-align-${align}`]: align,
      'b-selected': grid.isSelected(cellContext),
      'b-focused': grid.isFocused(cellContext)
    };
    DomHelper.syncClassList(cellElement, newCellClass); // Call renderer or set innerHTML directly if none
    // if renderer returns a value, set it in innerHTML below. render is also free to do it itself

    if (useRenderer) {
      cellContent = useRenderer.call(column, rendererData);

      if (cellContent === undefined) {
        shouldSetContent = false;
      }
    } else if (dataField) {
      cellContent = dataField.print(cellContent);
    } // Allow wrappers to do their own processing of cell contents, for example react wrapper allows JSX

    if (grid.processCellContent) {
      shouldSetContent = grid.processCellContent({
        cellContent,
        cellElement,
        record,
        cellElementData,
        rendererData
      });
    } // Listeners might need to know what string went into the DOM
    // Maintainer: the == null test below ensures that values of both null and undefined
    // result in an empty cell.

    const text = rendererData.cellContent = cellContent == null ? '' : String(cellContent);

    if (shouldSetContent) {
      // row might be flagged by GroupSummary to require full "redraw"
      if (me.forceInnerHTML) {
        // To allow minimal updates below, we must remove custom markup inserted by the GroupSummary feature
        cellElement.innerHTML = ''; // Delete cached content value

        delete cellElement._content;
        cellElement.lastDomConfig = null;
      } // display cell contents as text or use actual html?
      // (disableHtmlEncode set by features that decorate cell contents)

      if (column.htmlEncode && !column.disableHtmlEncode) {
        // Set innerText if cell currently has html content (from for example group renderer),
        // always do it on Linux, it does not have firstChild.data
        if (BrowserHelper.isLinux || cellElement._hasHtml) {
          cellElement.innerText = text;
          cellElement._hasHtml = false;
        } else {
          DomHelper.setInnerText(cellElement, text);
        }
      } else {
        const hasStringContent = typeof cellContent === 'string',
              hasObjectContent = typeof cellContent === 'object';

        if (column.autoSyncHtml && (!hasStringContent || cellElement.childElementCount)) {
          // String content in html column is handled as a html template string
          if (hasStringContent) {
            // update cell with only changed attributes etc.
            DomHelper.sync(text, cellElement.firstElementChild);
          } // Other content is considered to be a DomHelper config object
          else {
              DomSync.sync({
                domConfig: cellContent,
                targetElement: cellElement
              });
            }
        } // Consider all returned plain objects to be DomHelper configs for cell content
        else if (hasObjectContent) {
            DomSync.sync({
              targetElement: cellElement,
              domConfig: {
                onlyChildren: true,
                children: Array.isArray(cellContent) ? cellContent : [cellContent]
              }
            });
          } // Apply text as innerHTML only if it has changed
          else if (cellElement._content !== text) {
              cellElement.innerHTML = cellElement._content = text;
            }
      }
    }

    if (!isMeasuring) {
      // Allow others to affect rendering
      me.rowManager.trigger('renderCell', rendererData);
    }

    return size;
  } //endregion

}
Row._$name = 'Row';

//TODO: Handle vertical resize, add/remove row elements?
/**
 * @module Grid/row/RowManager
 * @private
 */

/**
 * Virtual representation of the grid, using {@link Grid.row.Row} to represent rows. Plugs into {@link Grid.view.Grid}
 * and exposes the following functions on grid itself:
 * * {@link #function-getRecordCoords()}
 * * {@link #function-getRowById()}
 * * {@link #function-getRow()}
 * * {@link #function-getRowFor()}
 * * {@link #function-getRowFromElement()}
 *
 * @example
 * let row = grid.getRowById(1);
 * @private
 */

class RowManager extends InstancePlugin {
  //region Config
  // Plugin configuration.
  static get pluginConfig() {
    return {
      chain: ['getRowById', 'getRecordCoords', 'getRow', 'getRowFor', 'getRowFromElement', 'destroy'],
      assign: ['rowHeight', 'topRow', 'bottomRow', 'firstVisibleRow', 'lastVisibleRow', 'firstFullyVisibleRow']
    };
  }

  static get defaultConfig() {
    return {
      /**
       * Number of rows to render above current viewport
       * @config {Number}
       * @default
       */
      prependRowBuffer: 5,

      /**
       * Number of rows to render below current viewport
       * @config {Number}
       * @default
       */
      appendRowBuffer: 5,

      /**
       * Default row height, assigned from Grid at construction (either from config
       * {@link Grid.view.Grid#config-rowHeight} or CSS). Can be set from renderers
       * @config {Number}
       * @default
       */
      rowHeight: null,

      /**
       * Set to `true` to get a small performance boost in applications that uses fixed row height
       * @config {Boolean}
       */
      fixedRowHeight: null,
      autoHeight: false
    };
  }

  static get properties() {
    return {
      idMap: {},
      // TODO: investigate if topIndex can to built away, since topRow is always first in array and has dataIndex??
      topIndex: 0,
      lastScrollTop: 0,
      _rows: [],
      // Record id -> row height mapping
      heightMap: new Map(),
      // Sum of entries in heightMap
      totalKnownHeight: 0,
      // Will be calculated in `estimateTotalHeight()`, as totalKnownHeight + an estimate for unknown rows
      _totalHeight: 0,
      // Average of the known heights, kept up to date when entries in the heightMap are updated
      averageRowHeight: 0,
      scrollTargetRecordId: null,
      refreshDetails: {
        topRowIndex: 0,
        topRowTop: 0
      }
    };
  } //endregion
  //region Init

  construct(config) {
    config.grid._rowManager = this;
    super.construct(config.grid, config);
  } // Chained to grids doDestroy

  doDestroy() {
    // To remove timeouts
    this._rows.forEach(row => row.destroy());

    super.doDestroy();
  }
  /**
   * Initializes the RowManager with Rows to fit specified height.
   * @param {Number} height
   * @param {Boolean} [isRendering]
   * @private
   * @category Init
   */

  initWithHeight(height, isRendering = false) {
    const me = this; // no valid height, make room for all rows

    if (me.autoHeight) {
      height = me.store.allCount * me.preciseRowOffsetHeight;
    }

    me.viewHeight = height;
    me.calculateRowCount(isRendering);
    return height;
  }
  /**
   * Releases all elements (not from dom), calculates how many are needed, creates those and renders
   */

  reinitialize(returnToTop = false) {
    const me = this; // Calculate and correct the amount of rows needed (without triggering render)
    // Rows which are found to be surplus are destroyed.

    me.calculateRowCount(false, true, true); // If our row range is outside of the store's range, force a return to top

    if (me.topIndex + me.rowCount - 1 > me.store.count) {
      returnToTop = true;
    }

    const top = me.topRow && !returnToTop ? me.topRow.top : 0;
    me.scrollTargetRecordId = null;

    if (returnToTop) {
      me.topIndex = me.lastScrollTop = 0;
    }

    const {
      topRow
    } = me;

    if (topRow) {
      // Ensure rendering from the topRow starts at the correct position
      topRow.dataIndex = me.topIndex;
      topRow.setTop(top, true);
    } // Need to estimate height in case we have Grid using autoHeight

    me.estimateTotalHeight();
    me.renderFromRow(topRow);
  } //endregion
  //region Rows

  /**
   * Add or remove rows to fit row count
   * @private
   * @category Rows
   */

  matchRowCount(skipRender = false) {
    const me = this,
          {
      rows,
      grid
    } = me,
          numRows = rows.length,
          delta = numRows - me.rowCount;

    if (delta) {
      if (delta < 0) {
        const newRows = []; // add rows

        for (let index = numRows, dataIndex = numRows ? rows[numRows - 1].dataIndex + 1 : 0; index < me.rowCount; index++, dataIndex++) {
          newRows.push(new Row({
            rowManager: me,
            grid,
            index,
            dataIndex
          }));
        }

        rows.push.apply(rows, newRows); // and elements (by triggering event used by SubGrid to add elements)

        me.trigger('addRows', {
          rows: newRows
        });

        if (!skipRender) {
          // render
          me.renderFromRow(rows[Math.max(0, numRows - 1)]);
        }
      } else {
        // remove rows from bottom
        const removedRows = rows.splice(numRows - delta, delta); // trigger event in case some feature needs to cleanup when removing (widget column might be interested)

        me.trigger('removeRows', {
          rows: removedRows
        });
        removedRows.forEach(row => row.destroy()); // no need to rerender or such when removing from bottom. all is good :)
      }
    }
  }
  /**
   * Calculates how many rows fit in the available height (view height)
   * @private
   * @category Rows
   */

  calculateRowCount(skipMatchRowCount = false, allowRowCountShrink = true, skipRender = false) {
    // TODO: replace prependRowBuffer, appendXX with bufferSize
    const me = this,
          {
      store
    } = me,
          visibleRowCount = Math.ceil(me.viewHeight / me.minRowOffsetHeight),
          // Want whole rows
    maxRenderRowCount = visibleRowCount + me.prependRowBuffer + me.appendRowBuffer; // If RowManager is reinitialized in a hidden state the view might not have a height

    if (!me.grid.columns.count || isNaN(visibleRowCount)) {
      me.rowCount = 0;
      return 0;
    } // when for example jumping we do not want to remove excess rows,
    // since we know they are needed at other scroll locations

    if (maxRenderRowCount < me.rowCount && !allowRowCountShrink) {
      return me.rowCount;
    }

    me.visibleRowCount = visibleRowCount;
    me.rowCount = Math.min(store.count, maxRenderRowCount); // No need for more rows than data
    // If the row count doesn't match the calculated, ensure it matches,

    if (!skipMatchRowCount) {
      if (me.rows && me.rowCount !== me.rows.length) {
        me.matchRowCount(skipRender);
      } else if (!me.rowCount) {
        me.trigger('changeTotalHeight', {
          totalHeight: me.totalHeight
        });
      }

      me.grid.toggleEmptyText();
    }

    return me.rowCount;
  }

  removeAllRows() {
    // remove rows from bottom
    const me = this,
          {
      topRow
    } = me,
          result = topRow ? me.refreshDetails = {
      topRowIndex: topRow.dataIndex,
      topRowTop: topRow.top
    } : me.refreshDetails,
          removedRows = me.rows.slice(); // trigger event in case some feature needs to cleanup when removing (widget column might be interested)

    me.trigger('removeRows', {
      rows: removedRows
    });
    me.rows.forEach(row => row.destroy());
    me.rows.length = 0;
    me.idMap = {}; // We return a descriptor of the last rendered block before the remove.
    // This is primarily for a full GridBase#renderContents to be able to perform a correct refresh.

    return result;
  }

  setPosition(refreshDetails) {
    // Sets up the rendering position for the next call to reinitialize
    const {
      topRow
    } = this,
          {
      topRowIndex,
      topRowTop
    } = refreshDetails;
    topRow.setTop(topRowTop);
    topRow.dataIndex = topRowIndex;
  } //endregion
  //region Rows - Getters

  get store() {
    return this.client.store;
  }
  /**
   * Get all Rows
   * @property {Grid.row.Row[]}
   * @readonly
   * @category Rows
   */

  get rows() {
    return this._rows;
  }
  /**
   * Get the Row at specified index. Returns `undefined` if the row index is not rendered.
   * @param {Number} index
   * @returns {Grid.row.Row}
   * @category Rows
   */

  getRow(index) {
    return this.rowCount && this.rows[index - this.topIndex];
  }
  /**
   * Get Row for specified record id
   * @param {Core.data.Model|String|Number} recordOrId Record id (or a record)
   * @returns {Grid.row.Row} Found Row or null if record not rendered
   * @category Rows
   */

  getRowById(recordOrId) {
    if (recordOrId && recordOrId.isModel) {
      recordOrId = recordOrId.id;
    }

    return this.idMap[recordOrId];
  }
  /**
   * Get a Row from an HTMLElement
   * @param {HTMLElement} element
   * @returns {Grid.row.Row} Found Row or null if record not rendered
   * @category Rows
   */

  getRowFromElement(element) {
    element = element.closest('.b-grid-row');
    return element && this.getRow(element.dataset.index);
  }
  /**
   * Get the row at the specified Y coordinate, which is by default viewport-based.
   * @param {Number} y The `Y` coordinate to find the Row for.
   * @param {Boolean} [local=false] Pass `true` if the `Y` coordinate is local to the SubGrid's element.
   * @returns {Grid.row.Row} Found Row or null if no row is rendered at that point.
   */

  getRowAt(y, local = false) {
    // Make it local.
    if (!local) {
      // Because this is used with event Y positions which are integers, we must
      // round the Rectangle to the closest integer.
      y -= Rectangle.from(this.grid.bodyContainer, null, true).roundPx(1).top; // Adjust for scrolling

      y += this.grid.scrollable.y;
    }

    y = DomHelper.roundPx(y);
    return this.rows.find(r => y >= r.top && y < r.bottom);
  }
  /**
   * Get a Row for either a record, a record id or an HTMLElement
   * @param {HTMLElement|Core.data.Model|String|Number} recordOrId Record or record id or HTMLElement
   * @returns {Grid.row.Row} Found Row or null if record not rendered
   * @category Rows
   */

  getRowFor(recordOrId) {
    if (recordOrId instanceof HTMLElement) {
      return this.getRowFromElement(recordOrId);
    }

    return this.getRowById(recordOrId);
  }
  /**
   * Gets the Row following the specified Row (by index or object). Wraps around the end.
   * @param {Number|Grid.row.Row} indexOrRow index or Row
   * @returns {Grid.row.Row}
   * @category Rows
   */

  getNextRow(indexOrRow) {
    const index = typeof indexOrRow === 'number' ? indexOrRow : indexOrRow.index;
    return this.getRow((index + 1) % this.rowCount);
  }
  /**
   * Get the Row that is currently displayed at top.
   * @property {Grid.row.Row}
   * @readonly
   * @category Rows
   */

  get topRow() {
    return this.rows[0];
  }
  /**
   * Get the Row currently displayed furthest down.
   * @property {Grid.row.Row}
   * @readonly
   * @category Rows
   */

  get bottomRow() {
    // TODO: remove when ticket on making sure rowCount is always up to date is fixed
    const rowCount = Math.min(this.rowCount, this.store.count);
    return this.rows[rowCount - 1];
  }
  /**
   * Get the topmost visible Row
   * @property {Grid.row.Row}
   * @readonly
   * @category Rows
   */

  get firstVisibleRow() {
    return this.rows.find(r => r.bottom > this.grid.scrollable.y);
  }

  get firstFullyVisibleRow() {
    return this.rows.find(r => r.top >= this.grid.scrollable.y);
  }
  /**
   * Get the last visible Row
   * @property {Grid.row.Row}
   * @readonly
   * @category Rows
   */

  get lastVisibleRow() {
    const grid = this.grid; // We need the last row who's top is inside the scrolling viewport

    return ArrayHelper.findLast(this.rows, r => r.top < grid.scrollable.y + grid.bodyHeight);
  }
  /**
   * Calls offset() for each Row passing along offset parameter
   * @param {Number} offset Pixels to translate Row elements.
   * @private
   * @category Rows
   */

  offsetRows(offset) {
    if (offset !== 0) {
      const {
        rows
      } = this,
            {
        length
      } = rows;

      for (let i = 0; i < length; i++) {
        rows[i].offset(offset);
      }
    }

    this.trigger('offsetRows', {
      offset
    });
  } //endregion
  //region Row height
  // TODO: should support setting rowHeight in em and then convert internally to pixels. 1em = font-size. Not needed for 1.0

  /**
   * Set a fixed row height (can still be overridden by renderers) or get configured row height. Setting refreshes all rows
   * @type {Number}
   * @category Rows
   */

  get rowHeight() {
    return this._rowHeight;
  }

  set rowHeight(height) {
    const me = this,
          {
      grid,
      fixedRowHeight
    } = me,
          oldRowHeight = me.rowHeight;
    ObjectHelper.assertNumber(height, 'rowHeight');

    if (height < 10) {
      height = 10;
    }

    me.trigger('beforeRowHeight', {
      height
    });
    me.minRowHeight = me._rowHeight = height;
    me.prependBufferHeight = me.prependRowBuffer * me.rowOffsetHeight;
    me.appendBufferHeight = me.appendRowBuffer * me.rowOffsetHeight;

    if (fixedRowHeight) {
      me.averageRowHeight = height;
    }

    if (me.rows.length) {
      const oldY = grid.scrollable.y,
            topRow = me.getRowAt(oldY, true),
            // When changing rowHeight in a scrolled grid, there might no longer be a row at oldY
      edgeOffset = topRow ? topRow.top - oldY : 0;
      let average, oldAverage; // When using fixedRowHeight there is no need to update an average

      if (fixedRowHeight) {
        average = height;
        oldAverage = oldRowHeight;
      } else {
        oldAverage = average = me.averageRowHeight;
        me.clearKnownHeights(); // Scale the average height in proportion to the row height change

        average *= height / oldRowHeight;
      } // Adjust number of rows, since it is only allowed to shrink in refresh()

      me.calculateRowCount(false, true, true); // Reposition the top row since it is used to position the rest

      me.topRow.setTop(me.topRow.dataIndex * (average + grid._rowBorderHeight), true);
      me.refresh();
      const newY = oldY * (average / oldAverage); // Scroll top row to the same position.

      if (newY !== oldY) {
        grid.scrollRowIntoView(topRow.id, {
          block: 'start',
          edgeOffset
        });
      }
    }

    me.trigger('rowHeight', {
      height
    });
  }
  /**
   * Get actually used row height, which includes any border and might be an average if using variable row height.
   * @property {Number}
   */

  get rowOffsetHeight() {
    return Math.floor(this.preciseRowOffsetHeight);
  }

  get preciseRowOffsetHeight() {
    return (this.averageRowHeight || this._rowHeight) + this.grid._rowBorderHeight;
  }

  get minRowOffsetHeight() {
    return (this.minRowHeight || this._rowHeight) + this.grid._rowBorderHeight;
  }
  /*
  * How store CRUD affects the height map:
  *
  * | Operation | Result                            |
  * |-----------|-----------------------------------|
  * | add       | No. Appears on render             |
  * | insert    | No. Appears on render             |
  * | remove    | Remove entry                      |
  * | removeAll | Clear                             |
  * | update    | No                                |
  * | replace   | Height might differ, remove entry |
  * | move      | No                                |
  * | filter    | No                                |
  * | sort      | No                                |
  * | group     | No                                |
  * | dataset   | Clear                             |
  *
  * The above is handled in GridBase
  */

  /**
   * Returns `true` if all rows have a known height. They do if all rows are visitied, or if RowManager is configured
   * with `fixedRowHeight`. If so, all tops can be calculated exactly, no guessing needed
   * @property {Boolean}
   * @private
   */

  get allHeightsKnown() {
    return this.fixedRowHeight || this.heightMap.size >= this.store.count;
  }
  /**
   * Store supplied `height` using `id` as key in the height map. Called by `Row` when it gets its height.
   * Keeps `avarageRowHeight` and `totalKnownHeight` up to date. Ignored when configured with `fixedRowHeight`
   * @param {String|Number} id
   * @param {Number} height
   * @internal
   */

  storeKnownHeight(id, height) {
    const me = this,
          {
      heightMap
    } = me;

    if (!me.fixedRowHeight) {
      // Decrease know height with old value
      if (heightMap.has(id)) {
        me.totalKnownHeight -= heightMap.get(id);
      } // Height here is "clientHeight"

      heightMap.set(id, height); // And increase with new

      me.totalKnownHeight += height;

      if (height < me.minRowHeight) {
        me.minRowHeight = height;
      }

      me.averageRowHeight = me.totalKnownHeight / heightMap.size;
    }
  }
  /**
   * Get the known or estimated offset height for the specified record id
   * @param {Core.data.Model} record
   * @returns {Number}
   * @private
   */

  getOffsetHeight(record) {
    const me = this;
    return (me.heightMap.get(record.id) || me.grid.getRowHeight(record) || me.averageRowHeight || me.rowHeight) + me.grid._rowBorderHeight;
  }
  /**
   * Invalidate cached height for a record. Removing it from `totalKnownHeight` and factoring it out of
   * `avarageRowHeight`.
   * @param {Core.data.Model|Core.data.Model[]} records
   */

  invalidateKnownHeight(records) {
    const me = this;

    if (!me.fixedRowHeight) {
      const {
        heightMap
      } = me;

      if (!Array.isArray(records)) {
        records = [records];
      }

      records.forEach(record => {
        if (record) {
          if (heightMap.has(record.id)) {
            // Known height decreases when invalidating
            me.totalKnownHeight -= heightMap.get(record.id);
            heightMap.delete(record.id);
          }
        }
      });
      me.averageRowHeight = me.totalKnownHeight / heightMap.size;
    }
  }
  /**
   * Invalidates all cached height and resets `avarageRowHeight` and `totalKnownHeight`
   */

  clearKnownHeights() {
    this.heightMap.clear();
    this.averageRowHeight = this.totalKnownHeight = 0;
  }
  /**
   * Calculates a row top from its data index. Uses known values from the height map, unknown are substituted with
   * the average row height. When configured with `fixedRowHeight`, it will always calculate a correct value
   * @param {Number} index Index in store
   * @private
   */

  calculateTop(index) {
    // When using fixed row height, life is easy
    if (this.fixedRowHeight) {
      return index * this.rowOffsetHeight;
    }

    const {
      store
    } = this;
    let top = 0; // When not using fixed row height, we make an educated guess at the top. The more rows have been visited, the
    // more correct the guess is (fully correct if all rows visited)

    for (let i = 0; i < index; i++) {
      const record = store.getAt(i);
      top += this.getOffsetHeight(record);
    }

    return Math.floor(top);
  } //endregion
  //region Calculations

  /**
   * Returns top and bottom for rendered row or estimated coordinates for unrendered.
   * @param {Core.data.Model|string|Number} recordOrId Record or record id
   * @param {Boolean} [local] Pass true to get relative record coordinates
   * @param {Boolean} [roughly] Pass true to allow a less exact but cheaper estimate
   * @returns {Core.helper.util.Rectangle} Record bounds with format { x, y, width, height, bottom, right }
   * @category Calculations
   */

  getRecordCoords(recordOrId, local = false, roughly = false) {
    const me = this,
          id = typeof recordOrId === 'string' || typeof recordOrId === 'number' ? recordOrId : recordOrId.id,
          row = me.getRowById(recordOrId);
    let scrollingViewport = me.client._bodyRectangle; // _bodyRectangle is not updated on page/containing element scroll etc. Need to make sure it is correct in case
    // that has happend. This if-statement should be removed when fixing
    // https://app.assembla.com/spaces/bryntum/tickets/6587-cached-_bodyrectangle-should-be-updated-on--quot-external-quot--scroll/details

    if (!local) {
      scrollingViewport = me.client._bodyRectangle = Rectangle.client(me.client.bodyContainer);
    } // Rendered? Then we know position for certain

    if (row) {
      return new Rectangle(scrollingViewport.x, local ? Math.round(row.top) : Math.round(row.top + scrollingViewport.y - me.client.scrollable.y), scrollingViewport.width, row.offsetHeight);
    }

    return me.getRecordCoordsByIndex(me.store.indexOf(id), local, roughly);
  }
  /**
   * Returns estimated top and bottom coordinates for specified row.
   * @param {Number} recordIndex Record index
   * @param {Boolean} [local]
   * @returns {Core.helper.util.Rectangle} Estimated record bounds with format { x, y, width, height, bottom, right }
   * @category Calculations
   */

  getRecordCoordsByIndex(recordIndex, local = false, roughly = false) {
    const me = this,
          {
      topRow,
      bottomRow
    } = me,
          scrollingViewport = me.client._bodyRectangle,
          {
      id
    } = me.store.getAt(recordIndex),
          // Not using rowOffsetHeight since it floors the value and that rounding might give big errors far down
    height = me.preciseRowOffsetHeight,
          currentTopIndex = topRow.dataIndex,
          currentBottomIndex = bottomRow.dataIndex,
          // Instead of estimating top from the very top, use closest known coordinate. Makes sure a coordinate is not
    // estimated on wrong side of rendered rows, needed to correctly draw dependencies where one event is located
    // on a unrendered row
    calculateFrom = // bottomRow is closest, calculate from it
    recordIndex > currentBottomIndex ? {
      index: recordIndex - currentBottomIndex - 1,
      y: bottomRow.bottom,
      from: 'bottomRow'
    } //  closer to topRow than 0, use topRow
    : recordIndex > currentTopIndex / 2 ? {
      index: recordIndex - currentTopIndex,
      y: topRow.top,
      from: 'topRow'
    } // closer to the very top, use it
    : {
      index: recordIndex,
      y: 0,
      from: 'top'
    },
          top = me.allHeightsKnown && !roughly // All heights are known (all rows visited or fixed row height), get actual top coord
    ? me.calculateTop(recordIndex) // Otherwise estimate
    : Math.floor(calculateFrom.y + calculateFrom.index * height),
          result = new Rectangle(scrollingViewport.x, local ? top : top + scrollingViewport.y - me.client.scrollable.y, scrollingViewport.width, // Either known height or average
    Math.floor(me.heightMap.get(id) || height)); // Signal that it's not based on an element, so is only approximate.
    // Grid.scrollRowIntoView will have to go round again using the block options below to ensure it's correct.

    result.virtual = true; // When the block becomes visible, scroll it to the logical position using the scrollIntoView's block
    // option. If it's above, use block: 'start', if below, use block: 'end'.

    result.block = result.bottom < scrollingViewport.y ? 'start' : result.y > scrollingViewport.bottom ? 'end' : 'nearest';
    return result;
  }
  /**
   * Total estimated grid height (used for scroller)
   * @property {Number}
   * @readonly
   * @category Calculations
   */

  get totalHeight() {
    return this._totalHeight;
  } //endregion
  //region Iteration etc.

  /**
   * Calls a function for each Row
   * @param {Function} fn Function that will be called with Row as first parameter
   * @category Iteration
   */

  forEach(fn) {
    this.rows.forEach(fn);
  }
  /**
   * Iterator that allows you to do for (let row of rowManager)
   * @category Iteration
   */

  [Symbol.iterator]() {
    return this.rows[Symbol.iterator]();
  } //endregion
  //region Scrolling & rendering

  /**
   * Refresh a single cell.
   * @param {Core.data.Model} record Record for row holding the cell that should be updated
   * @param {String|Number} columnId Column id to identify the cell within the row
   * @returns {Boolean} Returns `true` if cell was found and refreshed, `false` if not
   */

  refreshCell(record, columnId) {
    const row = this.getRowFor(record);

    if (row) {
      const cellElement = row.getCell(columnId);

      if (cellElement) {
        row.renderCell({
          cellElement
        });
        return true;
      }
    }

    return false;
  }
  /**
   * Renders from the top of the grid, also resetting scroll to top. Used for example when collapsing all groups.
   * @category Scrolling & rendering
   */

  returnToTop() {
    const me = this;
    me.topIndex = 0;
    me.lastScrollTop = 0;

    if (me.topRow) {
      me.topRow.dataIndex = 0; // Force the top row to the top of the scroll range

      me.topRow.setTop(0, true);
    }

    me.refresh(); // Rows rendered from top, make sure grid is scrolled to top also

    me.grid.scrollable.y = 0;
  }
  /**
   * Renders from specified records row and down (used for example when collapsing a group, does not affect rows above).
   * @param {Core.data.Model} record Record of first row to render
   * @category Scrolling & rendering
   */

  renderFromRecord(record) {
    const row = this.getRowById(record.id);

    if (row) {
      this.renderFromRow(row);
    }
  }
  /**
   * Renders from specified row and down (used for example when collapsing a group, does not affect rows above).
   * @param {Grid.row.Row} fromRow First row to render
   * @category Scrolling & rendering
   */

  renderFromRow(fromRow = null) {
    const me = this,
          {
      rows,
      store
    } = me,
          storeCount = store.count; // Calculate row count, adding rows if needed, but do not rerender - we are going to do that below.
    // Bail out if no rows. Allow removing rows if we have more than store have rows

    if (me.calculateRowCount(false, storeCount < rows.length, true) === 0) {
      return;
    }

    let // render from this row
    fromRowIndex = fromRow ? rows.indexOf(fromRow) : 0,
        // starting either from its specified dataIndex or from its index (happens on first render, no dataIndex yet)
    dataIndex = fromRow ? fromRow.dataIndex : rows[0].dataIndex,
        // amount of records after this one in store
    recordsAfter = storeCount - dataIndex - 1,
        // render to this row, either the last row or the row which will hold the last record available
    toRowIndex = Math.min(rows.length - 1, fromRowIndex + recordsAfter),
        // amount of rows which wont be rendered below last record (if we have fewer records than topRow + row count)
    leftOverCount = rows.length - toRowIndex - 1,
        // Start with top correctly just below the previous row's bottom
    top = fromRowIndex > 0 ? rows[fromRowIndex - 1].bottom : rows[fromRowIndex].top,
        row; // _rows array is ordered in display order, just iterate to the end

    for (let i = fromRowIndex; i <= toRowIndex; i++) {
      row = rows[i]; // Needed in scheduler when translating events, happens before render

      row.dataIndex = dataIndex; // Silent translation, render will update contents anyway

      row.setTop(top, true);
      row.render(dataIndex, store.getAt(dataIndex++), false);
      top += row.offsetHeight;
    } // if number for records to display has decreased, for example by collapsing a node, we might get unused rows
    // below bottom. move those to top to not have unused rows laying around

    while (leftOverCount-- > 0) {
      me.displayRecordAtTop();
    } // Reestimate total height

    me.estimateTotalHeight(true);
    me.trigger('renderDone');
  }
  /**
   * Renders the passed array (or [Set](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Set)) of {@link Grid.row.Row rows}
   * @param {Grid.row.Row[]|Set} rows The rows to render
   * @category Scrolling & rendering
   */

  renderRows(rows) {
    let oldHeight,
        heightChanged = false;
    rows = Array.from(rows); // Sort topmost row first

    rows.sort((a, b) => a.dataIndex - b.dataIndex); // Render the requested rows.

    for (const row of rows) {
      oldHeight = row.height; // Pass updatingSingleRow as false, so that it does not shuffle following
      // rows downwards on each render. We do that once here after the rows are all refreshed.

      row.render(null, null, false);
      heightChanged |= row.height !== oldHeight;
    } // If this caused a height change, shuffle following rows.

    if (heightChanged) {
      this.translateFromRow(rows[0]);
    }

    this.trigger('renderDone');
  }
  /**
   * Translates all rows after the specified row. Used when a single rows height is changed and the others should
   * rearrange. (Called from Row#render)
   * @param {Grid.row.Row} fromRow
   * @private
   * @category Scrolling & rendering
   */

  translateFromRow(fromRow, batch = false) {
    const me = this;
    let top = fromRow.bottom,
        row,
        index;

    for (index = fromRow.dataIndex + 1, row = me.getRow(index); row; row = me.getRow(++index)) {
      row.setTop(top);
      top += row.offsetHeight;
    } // Reestimate total height

    if (!batch) {
      me.estimateTotalHeight(true);
    }
  }
  /**
   * Rerender all rows
   * @category Scrolling & rendering
   */

  refresh() {
    const me = this,
          {
      topRow
    } = me; // too early

    if (!topRow) {
      return;
    }

    me.idMap = {};
    me.renderFromRow(topRow);
    me.trigger('refresh');
  }
  /**
   * Makes sure that specified record is displayed in view
   * @param newScrollTop Top of visible section
   * @param [forceRecordIndex] Index of record to display at center
   * @private
   * @category Scrolling & rendering
   */

  jumpToPosition(newScrollTop, forceRecordIndex) {
    // There are two very different requirements here.
    // If there is a forceRecordIndex, that takes precedence to get it into the center of the
    // viewport, and wherever we render the calculated row block, we may then *adjust the scrollTop*
    // to get that row to the center.
    //
    // If there's no forceRecordIndex, then the scroll position is the primary objective and
    // we must render what we calculate to be correct at that viewport position.
    const me = this,
          {
      store,
      heightMap
    } = me,
          storeCount = store.count;

    if (me.allHeightsKnown && !me.fixedRowHeight) {
      const top = newScrollTop - me.prependBufferHeight,
            border = me.grid._rowBorderHeight;
      let accumulated = 0,
          targetIndex = 0;

      while (accumulated < top) {
        const record = store.getAt(targetIndex);
        accumulated += heightMap.get(record.id) + border;
        targetIndex++;
      }

      const startIndex = Math.max(Math.min(targetIndex, storeCount - me.rowCount), 0);
      me.lastScrollTop = newScrollTop;
      me.topRow.dataIndex = me.topIndex = startIndex;
      me.topRow.setTop(me.calculateTop(startIndex), false); // render entire buffer

      me.refresh();
    } else {
      const rowHeight = me.preciseRowOffsetHeight,
            // Calculate index of the top of the rendered block.
      // If we are targeting the scrollTop, this will be the top index at the scrollTop minus prepend count.
      // If we are targeting a recordIndex, this will attempt to place that in the center of the rendered block.
      targetIndex = forceRecordIndex == null ? Math.floor(newScrollTop / rowHeight) - me.prependRowBuffer : forceRecordIndex - Math.floor(me.rowCount / 2),
            startIndex = Math.max(Math.min(targetIndex, storeCount - me.rowCount), 0),
            viewportTop = me.client.scrollable.y,
            viewportBottom = Math.min(me.client._bodyRectangle.height + viewportTop + me.appendBufferHeight, me.totalHeight);
      me.lastScrollTop = newScrollTop;
      me.topRow.dataIndex = me.topIndex = startIndex;
      me.topRow.setTop(Math.floor(startIndex * rowHeight), false); // render entire buffer

      me.refresh(); // TODO: It is likely the approach below will be needed for scrolling in opposite direction also, although no
      //   problem encountered yet
      // Not filled all the way down?

      if (me.bottomRow.bottom < viewportBottom) {
        // Might have jumped into a section of low heights. Needs to be done after the refresh, since heights
        // are not known before it
        me.calculateRowCount(false, false, false); // Fill with available rows (might be available above buffer because of var row height), stop if we run out of records :)

        while (me.bottomRow.bottom < viewportBottom && me._rows[me.prependRowBuffer].top < viewportTop && me.bottomRow.dataIndex < storeCount - 1) {
          me.displayRecordAtBottom();
        } // TODO: Block below was not needed for current tests, but if row height in one block is enough smaller
        //  than average row height then we will need to add more rows
        // Still not filled all the way down? Need more rows
        // if (me.bottomRow.bottom < viewportBottom) {
        //     //const localAverage = blockHeight / me.rowCount;
        //     while (me.bottomRow.bottom < viewportBottom) {
        //        me.addRecordAtBottom();
        //     }
        // }

      }

      me.estimateTotalHeight();
    } // If the row index is our priority, then scroll it into the center

    if (forceRecordIndex != null) {
      const {
        scrollable
      } = me.grid,
            targetRow = me.getRow(forceRecordIndex),
            // When coming from a block of high rowHeights to one with much lower we might still miss the target...
      // TODO: Jump again in these cases?
      rowCenter = targetRow && Rectangle.from(targetRow._elementsArray[0]).center.y,
            viewportCenter = scrollable.viewport.center.y; // Scroll the targetRow into the center of the viewport

      if (targetRow) {
        scrollable.y = newScrollTop = Math.floor(scrollable.y + (rowCenter - viewportCenter));
      }
    }

    return newScrollTop;
  }
  /**
   * Jumps to a position if it is far enough from current position. Otherwise does nothing.
   * @private
   * @category Scrolling & rendering
   */

  warpIfNeeded(newScrollTop) {
    const me = this,
          result = {
      newScrollTop,
      deltaTop: newScrollTop - me.lastScrollTop
    }; // if gap to fill is large enough, better to jump there than to fill row by row

    if (Math.abs(result.deltaTop) > me.rowCount * me.rowOffsetHeight * 3) {
      // no specific record targeted
      let index; // Specific record specified as target of scroll?

      if (me.scrollTargetRecordId) {
        index = me.store.indexOf(me.scrollTargetRecordId); // since scroll is happening async record might have been removed after requesting scroll,
        // in that case we rely on calculated index (as when scrolling without target)
      } // perform the jump and return results

      result.newScrollTop = me.jumpToPosition(newScrollTop, index);
      result.deltaTop = 0; // no extra filling needed
    }

    return result;
  }
  /**
   * Handles virtual rendering (only visible rows + buffer are in dom) for rows
   * @param {Number} newScrollTop The `Y` scroll position for which to render rows.
   * @param {Boolean} [force=false] Pass `true` to update the rendered row block even if the scroll position has not changed.
   * @return {Number} Adjusted height required to fit rows
   * @private
   * @category Scrolling & rendering
   */

  updateRenderedRows(newScrollTop, force, ignoreError = false) {
    const me = this,
          clientRect = me.client._bodyRectangle; // Might be triggered after removing all records, should not crash

    if (me.rowCount === 0) {
      return 0;
    }

    let result = me.totalHeight;

    if (force || // Only react if we have scrolled by one row or more
    Math.abs(newScrollTop - me.lastScrollTop) >= me.rowOffsetHeight || // or if we have a gap at top/bottom (#9375)
    me.topRow.top > newScrollTop || me.bottomRow.bottom < newScrollTop + clientRect.height) {
      // If scrolled by a large amount, jump instead of rendering each row
      const posInfo = me.warpIfNeeded(newScrollTop);
      me.scrollTargetRecordId = null; // Cache the last correct render scrollTop before fill.
      // it can be adjusted to hide row position corrections.

      me.lastScrollTop = posInfo.newScrollTop;

      if (posInfo.deltaTop > 0) {
        // Scrolling down
        me.fillBelow(posInfo.newScrollTop);
      } else if (posInfo.deltaTop < 0) {
        // Scrolling up
        me.fillAbove(posInfo.newScrollTop);
      }

      if (!me.fixedRowHeight && !ignoreError) {
        me.correctError(posInfo, clientRect, newScrollTop);
      } // Calculate the new height based on new content

      result = me.estimateTotalHeight();
    }

    return result;
  }

  correctError(posInfo, clientRect, newScrollTop) {
    const me = this;
    let error = 0; // TODO: Merge with else, does the same calculation
    // When we transition from not knowing all heights to doing so, the old estimate will likely have positioned
    // rows a bit off. Compensate for that here.

    if (me.allHeightsKnown) {
      error = me.topRow.top - me.calculateTop(me.topRow.dataIndex);
    } // If it's a temporary scroll, we can be told to ignore the drift.
    // Apart from that, we must correct keep the rendered block position correct.
    // Otherwise, when rolling upwards after a teleport, we may not be able to reach
    // the top. Some rows may end up at -ve positions.
    else {
        // Only correct the rendered block position if we are in danger of running out of scroll space.
        // That is if we are getting towards the top or bottom of the scroll range.
        if ( // Scrolling up within top zone
        posInfo.deltaTop < 0 && newScrollTop < clientRect.height * 2 || // Scrolling down within bottom zone
        posInfo.deltaTop > 0 && newScrollTop > me.totalHeight - clientRect.height * 2 - 3) {
          // TODO: Calc could be eased more, using distance left to have less effect the further away from top/bottom
          error = me.topRow.top - me.calculateTop(me.topRow.dataIndex); //me.topIndex * me.rowOffsetHeight;
        }
      }

    if (error) {
      // Correct the rendered block position if it's not at the calculated position.
      // Keep the visual position correct by adjusting the scrollTop by the same amount.
      // When variable row heights are used, this will keep the rendered block top correct.
      me.offsetRows(-error);
      me.grid.scrollable.y = me.lastScrollTop = me.grid.scrollable.y - error;
    }
  }
  /**
   * Moves as many rows from the bottom to the top that are needed to fill to current scroll pos.
   * @param newTop Scroll position
   * @private
   * @category Scrolling & rendering
   */

  fillAbove(newTop) {
    const me = this,
          fillHeight = newTop - me.topRow.top - me.prependBufferHeight;
    let accumulatedHeight = 0;

    while (accumulatedHeight > fillHeight && me.topIndex > 0) {
      // We want to show prev record at top of rows
      accumulatedHeight -= me.displayRecordAtTop();
    }

    me.trigger('renderDone');
  }
  /**
   * Moves as many rows from the top to the bottom that are needed to fill to current scroll pos.
   * @param newTop Scroll position
   * @private
   * @category Scrolling & rendering
   */

  fillBelow(newTop) {
    const me = this,
          fillHeight = newTop - me.topRow.top - me.prependBufferHeight,
          recordCount = me.store.count,
          rowCount = me.rowCount;
    let accumulatedHeight = 0; // Repeat until we have filled empty height

    while (accumulatedHeight < fillHeight && // fill empty height
    me.topIndex + rowCount < recordCount && // as long as we have records left
    me.topRow.top + me.topRow.offsetHeight < newTop // and do not move top row fully into view (can happen with var row height)
    ) {
      // We want to show next record at bottom of rows
      accumulatedHeight += me.displayRecordAtBottom();
    }

    me.trigger('renderDone');
  }
  /**
   * Estimates height needed to fit all rows, based on average row height. Also offsets rows if needed to not be above
   * the reachable area of the view.
   * @param {Boolean} [immediate] Specify true to pass the `immediate` flag on to any listeners (probably only Grid
   * cares. Used to bypass buffered element resize)
   * @returns {Number}
   * @private
   * @category Scrolling & rendering
   */

  estimateTotalHeight(immediate = false) {
    const me = this;

    if (me.grid.renderingRows) {
      return;
    }

    const recordCount = me.store.count,
          unknownCount = recordCount - me.heightMap.size,
          {
      bottomRow
    } = me;
    let estimate; // No need to estimate when using fixed row height

    if (me.fixedRowHeight) {
      estimate = recordCount * me.rowOffsetHeight;
    } else {
      estimate = // Known height, from entries in heightMap
      me.totalKnownHeight + // Those heights are "clientHeights", estimate needs to include borders
      me.heightMap.size * me.grid._rowBorderHeight + // Add estimate for rows with unknown height
      unknownCount * me.preciseRowOffsetHeight; // No bottomRow yet if estimating initial height in autoHeight grid

      if (bottomRow && unknownCount) {
        const bottom = bottomRow.bottom; // To low estimate or reached the end with scroll left, adjust to fit current bottom

        if (bottom > estimate || me.topIndex + me.rowCount >= recordCount && estimate > bottom) {
          estimate = bottom; // estimate all the way down

          if (bottomRow.dataIndex < recordCount - 1) {
            estimate += (recordCount - 1 - bottomRow.dataIndex) * me.preciseRowOffsetHeight;
          }
        }
      }

      estimate = Math.floor(estimate);
    }

    if (estimate !== me.totalHeight) {
      if (me.trigger('changeTotalHeight', {
        totalHeight: estimate,
        immediate
      }) !== false) {
        me._totalHeight = estimate;
      }
    }

    return estimate;
  }
  /**
   * Moves a row from bottom to top and renders the corresponding record to it.
   * @returns {Number} New row height
   * @private
   * @category Scrolling & rendering
   */

  displayRecordAtTop() {
    const me = this,
          recordIndex = me.topIndex - 1,
          record = me.store.getAt(recordIndex),
          // Row currently rendered at the bottom, the row we want to move
    bottomRow = me.bottomRow,
          bottomRowTop = bottomRow.top;
    me.trigger('beforeTranslateRow', {
      row: bottomRow,
      newRecord: record
    }); // estimated top, for rendering that depends on having top

    bottomRow._top = me.topRow.top - me.getOffsetHeight(record); // if configured with fixed row height, it will be the correct value

    bottomRow.estimatedTop = !me.fixedRowHeight; // Render row

    bottomRow.render(recordIndex, record, false); // Move it to top. Restore top so that the setter won't reject non-change
    // if the estimate happened to be correct.

    bottomRow._top = bottomRowTop;
    bottomRow.setBottom(me.topRow.top);
    bottomRow.estimatedTop = false; // Prev row is now at top

    me.topIndex--; // move to start of array (bottomRow becomes topRow)

    me._rows.unshift(me._rows.pop());

    return bottomRow.offsetHeight;
  }
  /**
   * Moves a row from top to bottom and renders the corresponding record to it.
   * @returns {Number} New row height
   * @private
   * @category Scrolling & rendering
   */

  displayRecordAtBottom() {
    const me = this,
          recordIndex = me.topIndex + me.rowCount,
          record = me.store.getAt(recordIndex),
          // Row currently rendered on the top, the row we want to move
    topRow = me.topRow;
    me.trigger('beforeTranslateRow', {
      row: topRow,
      newRecord: record
    });
    topRow.dataIndex = recordIndex; // Move it to bottom

    topRow.setTop(me.bottomRow.bottom); // Render row

    topRow.render(recordIndex, record, false); // Next row is now at top

    me.topIndex++; // move to end of array (topRow becomes bottomRow)

    me._rows.push(me._rows.shift());

    return topRow.offsetHeight;
  } //endregion

  get widgetClass() {}

}
RowManager._$name = 'RowManager';

/**
 * @module Grid/util/GridScroller
 */

const xAxis$1 = {
  x: 1
};
/**
 * A Scroller subclass which handles scrolling in a grid.
 *
 * If the grid has no parallel scrolling grids (No locked columns), then this functions
 * transparently as a Scroller.
 *
 * If there are locked columns, then scrolling to an _element_ will invoke the scroller
 * of the subgrid which contains that element.
 * @internal
 */

class GridScroller extends Scroller {
  addScroller(scroller) {
    (this.xScrollers || (this.xScrollers = [])).push(scroller);
  }

  addPartner(otherScroller, axes = xAxis$1) {
    if (typeof axes === 'string') {
      axes = {
        [axes]: 1
      };
    } // Link up all our X scrollers

    if (axes.x) {
      this.xScrollers.forEach((scroller, i) => scroller.addPartner(otherScroller.xScrollers[i], 'x'));
    } // We are the only Y scroller

    if (axes.y) {
      super.addPartner(otherScroller, 'y');
    }
  }

  removePartner(otherScroller) {
    this.xScrollers.forEach((scroller, i) => {
      if (!scroller.isDestroyed) {
        scroller.removePartner(otherScroller.xScrollers[i]);
      }
    });
    super.removePartner(otherScroller);
  }

  updateOverflowX(overflowX) {
    this.xScrollers && this.xScrollers.forEach(s => s.overflowX = overflowX);
    this.widget.virtualScrollers.classList[overflowX === false ? 'add' : 'remove']('b-hide-display');
  }

  scrollIntoView(element, options) {
    // If we are after an element, we have to ask the scroller of the SubGrid
    // that the element is in. It will do the X scrolling and delegate the Y
    // scrolling up to this GridScroller.
    if (element.nodeType === 1) {
      for (const subGridScroller of this.xScrollers) {
        if (subGridScroller.element.contains(element)) {
          return subGridScroller.scrollIntoView(element, options);
        }
      }
    } else {
      return super.scrollIntoView(element, options);
    }
  }

  set x(x) {
    if (this.xScrollers) {
      this.xScrollers[0].x = x;
    }
  }

  get x() {
    // when trying to scroll grid with no columns xScrollers do not exist
    return this.xScrollers ? this.xScrollers[0].x : 0;
  }

}
GridScroller._$name = 'GridScroller';

/**
 * @module Grid/view/Header
 */

/**
 * The Grid header, which contains simple columns but also allows grouped columns. One instance is created and used per SubGrid
 * automatically, you should not need to instantiate this class manually. See {@link Grid.column.Column} for information about
 * column configuration.
 *
 * @extends Grid/view/Bar
 * @internal
 *
 * @externalexample grid/Header.js
 */

class Header extends Bar {
  static get $name() {
    return 'Header';
  } // Factoryable type name

  static get type() {
    return 'gridheader';
  }

  startConfigure(config) {
    config.scrollable.overflowX = 'hidden-scroll';
    super.startConfigure(config);
  }

  get subGrid() {
    return this._subGrid;
  }

  set subGrid(subGrid) {
    this._subGrid = subGrid;
    this.id = subGrid.id + '-header';
  }

  get region() {
    return this.subGrid && this.subGrid.region;
  }

  template() {
    const me = this,
          region = me.region;
    return TemplateHelper.tpl`
            <div class="b-grid-header-scroller b-grid-header-scroller-${region}">
                <div data-reference="headersElement" class="b-grid-headers b-grid-headers-${region}" data-region="${region}" data-max-depth="${me.maxDepth}" data-reference="headersElement"></div>
            </div>
        `;
  }

  get overflowElement() {
    return this.headersElement;
  }
  /**
   * Header template, recursive template for column headers.
   * Style not included because of CSP. Widths are fixed up in
   * {@link #function-fixHeaderWidths}
   * @private
   */

  contentTemplate(column) {
    const me = this;

    if (column.hidden) {
      return '';
    } else {
      /* eslint-disable */
      return TemplateHelper.tpl`
                <div class="b-grid-header" data-column="${column.field || ''}" data-column-id="${column.id}" ${column.isFocusable ? 'tabindex="0"' : ''}>
                    <div class="b-grid-header-text">
                        <div class="b-grid-header-text-content"></div>
                    </div>
                    ${column.children ? `
                    <div class="b-grid-header-children">
                            ${column.children.map(child => me.contentTemplate(child)).join('')}
                    </div>
                    ` : ''}
                    <div class="b-grid-header-resize-handle"></div>
                </div>
            `;
      /* eslint-enable */
    }
  } // used by safari to fix flex when rows width shrink below this value

  calculateMinWidthForSafari() {
    let minWidth = 0,
        columns = this.columns.visibleColumns;
    columns.forEach(column => {
      minWidth += column.calculateMinWidth();
    });
    return minWidth;
  }
  /**
   * Fix header widths (flex or fixed width) after rendering. Not a part of template any longer because of CSP
   * @private
   */

  fixHeaderWidths() {
    this.fixCellWidths();
  }

  refreshHeaders() {
    const me = this; // run renderers, not done from template to work more like cell rendering

    me.columns.traverse(column => {
      const headerElement = me.getBarCellElement(column.id);

      if (headerElement) {
        const classList = new DomClassList({
          'b-grid-header': 1,
          'b-grid-header-parent': column.isParent,
          [`b-level-${column.childLevel}`]: 1,
          [`b-depth-${column.meta.depth}`]: 1,
          [`b-grid-header-align-${column.align}`]: column.align,
          'b-grid-header-resizable': column.resizable && column.isLeaf,
          [column.cls]: column.cls,
          'b-last-parent': column.isParent && column.isLastInSubGrid,
          'b-last-leaf': column.isLeaf && column.isLastInSubGrid
        });
        let html = column.text;
        headerElement.className = classList;

        if (column.headerRenderer) {
          html = column.headerRenderer.call(column.thisObj || me, {
            column,
            headerElement
          });
        }

        if (column.icon) {
          html = `<i class="${column.icon}"></i>` + (html || '');
        }

        const innerEl = headerElement.querySelector('.b-grid-header-text-content');

        if (innerEl) {
          innerEl.innerHTML = html || '';
        }
      }
    });
    me.fixHeaderWidths();
  }

  get columns() {
    const me = this,
          result = super.columns;

    if (!me.columnsDetacher) {
      // columns is a chained store, it will be repopulated from master when columns change.
      // That action always triggers change with action dataset.
      me.columnsDetacher = result.on({
        change() {
          me.initDepths();
        },

        thisObj: me
      });
      me.initDepths();
    }

    return result;
  }

  set columns(columns) {
    super.columns = columns;
  }
  /**
   * Depths are used for styling of grouped headers. Sets them on meta.
   * @private
   */

  initDepths(columns = this.columns.topColumns, parent = null) {
    let me = this,
        maxDepth = 0;
    if (parent && parent.meta) parent.meta.depth++;

    for (const column of columns) {
      // TODO: this should maybe move
      column.meta.depth = 0;

      if (column.children) {
        me.initDepths(column.children.filter(me.columns.chainedFilterFn), column);
        if (column.meta.depth && parent) parent.meta.depth += column.meta.depth;
      }

      if (column.meta.depth > maxDepth) maxDepth = column.meta.depth;
    }

    if (!parent) {
      me.maxDepth = maxDepth;
    }

    return maxDepth;
  } //endregion
  //region Getters

  /**
   * Get the header cell element for the specified column.
   * @param {String} columnId Column id
   * @returns {HTMLElement} Header cell element
   */

  getHeader(columnId) {
    return this.getBarCellElement(columnId);
  } //endregion

  get contentElement() {
    return this.element.firstElementChild;
  }

  refreshContent() {
    const me = this;
    me.content = me.columns.topColumns.map(col => me.contentTemplate(col)).join('');
    me.refreshHeaders();
  }

  onPaint({
    firstPaint
  }) {
    if (firstPaint) {
      this.refreshContent();
    }
  }

} // Register this widget type with its Factory

Header.initClass();
Header._$name = 'Header';

// We declare consts inside case blocks in this file.
const gridElementEventHandlers = {
  touchstart: 'onElementTouchStart',
  touchmove: 'onElementTouchMove',
  touchend: 'onElementTouchEnd',
  mouseover: 'onElementMouseOver',
  mouseout: 'onElementMouseOut',
  mousedown: 'onElementMouseDown',
  mousemove: 'onElementMouseMove',
  mouseup: 'onElementMouseUp',
  click: 'onHandleElementClick',
  dblclick: 'onElementDblClick',
  keydown: 'onElementKeyDown',
  keyup: 'onElementKeyUp',
  keypress: 'onElementKeyPress',
  contextmenu: 'onElementContextMenu'
};
/**
 * @module Grid/view/mixin/GridElementEvents
 */

/**
 * Mixin for Grid that handles dom events. Some listeners fire own events but all can be chained by features. None of
 * the functions in this class are indented to be called directly.
 *
 * @mixin
 */

var GridElementEvents = (Target => class GridElementEvents extends (Target || Base) {
  static get $name() {
    return 'GridElementEvents';
  } //region Config

  static get defaultConfig() {
    return {
      /**
       * Time in ms until a longpress is triggered
       * @config {Number}
       * @default
       * @category Events
       */
      longPressTime: 400
    };
  } //endregion
  //region Events

  /**
   * User clicked in a grid cell
   * @event cellClick
   * @param {Grid.view.Grid} grid
   * @param {Core.data.Model} record
   * @param {Grid.column.Column} column
   * @param {Object} cellSelector
   * @param {HTMLElement} cellElement
   * @param {HTMLElement} target
   * @param {Event} event
   */

  /**
   * User double clicked in a grid cell
   * @event cellDblClick
   * @param {Grid.view.Grid} grid
   * @param {Core.data.Model} record
   * @param {Grid.column.Column} column
   * @param {Object} cellSelector
   * @param {HTMLElement} cellElement
   * @param {HTMLElement} target
   * @param {Event} event
   */

  /**
   * User activated contextmenu in a grid cell
   * @event cellContextMenu
   * @param {Grid.view.Grid} grid
   * @param {Core.data.Model} record
   * @param {Grid.column.Column} column
   * @param {Object} cellSelector
   * @param {HTMLElement} cellElement
   * @param {HTMLElement} target
   * @param {Event} event
   */

  /**
   * User moved moused to a grid cell
   * @event cellMouseOver
   * @param {Grid.view.Grid} grid
   * @param {Core.data.Model} record
   * @param {Grid.column.Column} column
   * @param {Object} cellSelector
   * @param {HTMLElement} cellElement
   * @param {HTMLElement} target
   * @param {Event} event
   */

  /**
   * User moved moused out of a grid cell
   * @event cellMouseOut
   * @param {Grid.view.Grid} grid
   * @param {Core.data.Model} record
   * @param {Grid.column.Column} column
   * @param {Object} cellSelector
   * @param {HTMLElement} cellElement
   * @param {HTMLElement} target
   * @param {Event} event
   */
  //endregion
  //region Event handling

  /**
   * Init listeners for a bunch of dom events. All events are handled by handleEvent().
   * @private
   * @category Events
   */

  initInternalEvents() {
    const handledEvents = Object.keys(gridElementEventHandlers),
          len = handledEvents.length,
          listeners = {
      element: this.element,
      thisObj: this
    }; // Route all events through handleEvent, so that we can capture this.event
    // before we route to the handlers

    for (let i = 0; i < len; i++) {
      listeners[handledEvents[i]] = 'handleEvent';
    }

    EventHelper.on(listeners);
    EventHelper.on({
      focus: 'onGridElementFocus',
      element: this.focusElement,
      thisObj: this
    });
  }
  /**
   * This method finds the cell location of the passed event. It returns an object describing the cell.
   * @param {Event} event A Mouse, Pointer or Touch event targeted at part of the grid.
   * @returns {Object} An object containing the following properties:
   * - `cellElement` - The cell element clicked on.
   * - `column` - The {@link Grid.column.Column column} clicked under.
   * - `columnId` - The `id` of the {@link Grid.column.Column column} clicked under.
   * - `record` - The {@link Core.data.Model record} clicked on.
   * - `id` - The `id` of the {@link Core.data.Model record} clicked on.
   * @internal
   * @category Events
   */

  getCellDataFromEvent(event) {
    const me = this,
          cellElement = DomHelper.up(event.target, '.b-grid-cell'); // There is a cell

    if (cellElement) {
      const cellData = DomDataStore.get(cellElement),
            {
        id,
        columnId
      } = cellData,
            record = me.store.getById(id),
            column = me.columns.getById(columnId); // Row might not have a record, since we transition record removal
      // https://app.assembla.com/spaces/bryntum/tickets/6805

      return record ? {
        cellElement,
        cellData,
        columnId,
        id,
        record,
        column,
        cellSelector: {
          id,
          columnId
        }
      } : null;
    }
  }
  /**
   * This method finds the header location of the passed event. It returns an object describing the header.
   * @param {Event} event A Mouse, Pointer or Touch event targeted at part of the grid.
   * @returns {Object} An object containing the following properties:
   * - `headerElement` - The header element clicked on.
   * - `column` - The {@link Grid.column.Column column} clicked under.
   * - `columnId` - The `id` of the {@link Grid.column.Column column} clicked under.
   * @internal
   * @category Events
   */

  getHeaderDataFromEvent(event) {
    const headerElement = DomHelper.up(event.target, '.b-grid-header'); // There is a header

    if (headerElement) {
      const headerData = ObjectHelper.assign({}, headerElement.dataset),
            {
        columnId
      } = headerData,
            column = this.columns.getById(columnId);
      return column ? {
        headerElement,
        headerData,
        columnId,
        column
      } : null;
    }
  }
  /**
   * Handles all dom events, routing them to correct functions (touchstart -> onElementTouchStart)
   * @param event
   * @private
   * @category Events
   */

  handleEvent(event) {
    if (!this.disabled) {
      this.event = event;

      if (gridElementEventHandlers[event.type]) {
        this[gridElementEventHandlers[event.type]](event);
      }
    }
  } //endregion
  //region Touch events

  /**
   * Touch start, chain this function in features to handle the event.
   * @param event
   * @category Touch events
   * @internal
   */

  onElementTouchStart(event) {
    const me = this,
          cellData = me.getCellDataFromEvent(event);
    DomHelper.isTouchEvent = true;

    if (event.touches.length === 1) {
      me.longPressTimeout = me.setTimeout(() => {
        me.onElementLongPress(event);
        event.preventDefault();
        me.longPressPerformed = true;
      }, me.longPressTime);
    }

    if (cellData && !event.defaultPrevented) {
      me.onFocusGesture(cellData, event);
    }
  }
  /**
   * Touch move, chain this function in features to handle the event.
   * @param event
   * @category Touch events
   * @internal
   */

  onElementTouchMove(event) {
    const me = this;

    if (me.longPressTimeout) {
      me.clearTimeout(me.longPressTimeout);
      me.longPressTimeout = null;
    }
  }
  /**
   * Touch end, chain this function in features to handle the event.
   * @param event
   * @category Touch events
   * @internal
   */

  onElementTouchEnd(event) {
    const me = this;

    if (me.longPressPerformed) {
      if (event.cancelable) {
        event.preventDefault();
      }

      me.longPressPerformed = false;
    }

    if (me.longPressTimeout) {
      me.clearTimeout(me.longPressTimeout);
      me.longPressTimeout = null;
    }
  }

  onElementLongPress(event) {} //endregion
  //region Mouse events
  // Trigger events in same style when clicking, dblclicking and for contextmenu

  triggerCellMouseEvent(name, event) {
    const me = this,
          cellData = me.getCellDataFromEvent(event); // There is a cell

    if (cellData) {
      const column = me.columns.getById(cellData.columnId);
      me.trigger('cell' + StringHelper.capitalize(name), {
        grid: this,
        record: cellData.record,
        column,
        cellSelector: cellData.cellSelector,
        cellElement: cellData.cellElement,
        target: event.target,
        event
      });
    }
  }
  /**
   * Mouse down, chain this function in features to handle the event.
   * @param event
   * @category Mouse events
   * @internal
   */

  onElementMouseDown(event) {
    const me = this,
          cellData = me.getCellDataFromEvent(event);
    me.skipFocusSelection = true;
    me.triggerCellMouseEvent('mousedown', event); // Browser event unification fires a mousedown on touch tap prior to focus.

    if (cellData && !event.defaultPrevented) {
      me.onFocusGesture(cellData, event);
    }
  }
  /**
   * Mouse move, chain this function in features to handle the event.
   * @param event
   * @category Mouse events
   * @internal
   */

  onElementMouseMove(event) {
    // Keep track of the last mouse position in case, due to OSX sloppy focusing,
    // focus is moved into the browser before a mousedown is delivered.
    // The cached mousemove event will provide the correct target in
    // GridNavigation#onGridElementFocus.
    this.mouseMoveEvent = event;
  }
  /**
   * Mouse up, chain this function in features to handle the event.
   * @param event
   * @category Mouse events
   * @internal
   */

  onElementMouseUp(event) {}
  /**
   * Called before {@link #function-onElementClick}.
   * Fires 'beforeElementClick' event which can return false to cancel further onElementClick actions.
   * @param event
   * @fires beforeElementClick
   * @category Mouse events
   * @internal
   */

  onHandleElementClick(event) {
    if (this.trigger('beforeElementClick', {
      event
    }) !== false) {
      this.onElementClick(event);
    }
  }
  /**
   * Click, select cell on click and also fire 'cellClick' event.
   * Chain this function in features to handle the dom event.
   * @param event
   * @fires cellClick
   * @category Mouse events
   * @internal
   */

  onElementClick(event) {
    const me = this,
          cellData = me.getCellDataFromEvent(event); // There is a cell

    if (cellData) {
      me.triggerCellMouseEvent('click', event); // Clear hover styling when clicking in a row to avoid having it stick around if you keyboard navigate
      // away from it
      // https://app.assembla.com/spaces/bryntum/tickets/5848

      DomDataStore.get(cellData.cellElement).row.removeCls('b-hover');
    }
  }

  onFocusGesture(cellData, event) {
    //TODO: should be able to cancel focusCell from listeners
    if (cellData) {
      this.focusCell(cellData.cellSelector, {
        scroll: false,
        doSelect: true,
        event
      });
    }
  }
  /**
   * Double click, fires 'cellDblClick' event.
   * Chain this function in features to handle the dom event.
   * @param {Event} event
   * @fires cellDblClick
   * @category Mouse events
   * @internal
   */

  onElementDblClick(event) {
    const me = this,
          target = event.target;
    me.triggerCellMouseEvent('dblClick', event);

    if (target.classList.contains('b-grid-header-resize-handle')) {
      const header = DomHelper.up(target, '.b-grid-header'),
            column = me.columns.getById(header.dataset.columnId);
      column.resizeToFitContent();
    }
  }
  /**
   * Mouse over, adds 'hover' class to elements.
   * @param event
   * @fires mouseOver
   * @category Mouse events
   * @internal
   */

  onElementMouseOver(event) {
    // bail out early if scrolling
    if (!this.scrolling) {
      const cellElement = DomHelper.up(event.target, '.b-grid-cell');

      if (cellElement) {
        const row = DomDataStore.get(cellElement).row; // No hover effect needed if a mouse button is pressed (like when resizing window, region, or resizing something etc).
        // NOTE: 'buttons' not supported in Safari

        if (row && (typeof event.buttons !== 'number' || event.buttons === 0)) {
          this.hoveredRow = row;
        }

        this.triggerCellMouseEvent('mouseOver', event);
      }
      /**
       * Mouse moved in over element in grid
       * @event mouseOver
       * @param {Event} event
       */

      this.trigger('mouseOver', {
        event
      });
    }
  }
  /**
   * Mouse out, removes 'hover' class from elements.
   * @param event
   * @fires mouseOut
   * @category Mouse events
   * @internal
   */

  onElementMouseOut(event) {
    this.hoveredRow = null; // bail out early if scrolling

    if (!this.scrolling) {
      const cellElement = DomHelper.up(event.target, '.b-grid-cell');

      if (cellElement) {
        this.triggerCellMouseEvent('mouseOut', event);
      }
      /**
       * Mouse moved out from element in grid
       * @event mouseOut
       * @param {Event} event
       */

      this.trigger('mouseOut', {
        event
      });
    }
  }

  set hoveredRow(row) {
    const me = this; // Unhover

    if (me._hoveredRow && !me._hoveredRow.isDestroyed) {
      me._hoveredRow.removeCls('b-hover');

      me._hoveredRow = null;
    } // Hover

    if (row && !me.scrolling) {
      me._hoveredRow = row;
      row.addCls('b-hover');
    }
  } //endregion
  //region Keyboard events

  /**
   * Key down, handles arrow keys for selection.
   * Chain this function in features to handle the dom event.
   * @param event
   * @category Keyboard events
   * @internal
   */

  onElementKeyDown(event) {
    const me = this; // flagging event with handled = true used to signal that other features should probably not care about it.
    // for this to work you should specify overrides for onElementKeyDown to be run before this function
    // (see for example CellEdit feature)

    if (event.handled) return; // Read this to refresh cached reference in case this keystroke lead to the removal of current row

    const focusedCell = me.focusedCell;

    if (event.target.matches('.b-grid-header.b-depth-0')) {
      me.handleHeaderKeyDown(event);
    } else if (event.target === me.focusElement || BrowserHelper.isIE11 && event.currentTarget === me.focusElement) {
      // IE11 Browser check is not placed in EventHelper to maintain built-in delegated functionality
      me.handleViewKeyDown(event); // If a cell is focused and column is interested - call special callback

      if (focusedCell) {
        var _column$onCellKeyDown;

        const cellElement = focusedCell.element,
              column = me.columns.getById(cellElement.dataset.columnId);
        (_column$onCellKeyDown = column.onCellKeyDown) === null || _column$onCellKeyDown === void 0 ? void 0 : _column$onCellKeyDown.call(column, {
          event,
          cellElement
        });
      }
    } // If focus is *within* a cell (eg WidgetColumn or CheckColumn), jump up to focus the cell.
    else if (event.key === 'Escape' && me.isActionableLocation) {
        const focusedCellClone = ObjectHelper.clone(focusedCell);
        focusedCellClone.element = null;
        me.focusCell(focusedCellClone);
        DomHelper.focusWithoutScrolling(me.element);
      }
  }

  handleViewKeyDown(event) {
    const me = this;

    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        return me.navigateLeft(event);

      case 'ArrowRight':
        event.preventDefault();
        return me.navigateRight(event);

      case 'ArrowUp':
        event.preventDefault();
        return me.navigateUp(event);

      case 'ArrowDown':
        event.preventDefault();
        return me.navigateDown(event);
    }
  }

  handleHeaderKeyDown(event) {
    const me = this,
          column = me.columns.getById(event.target.dataset.columnId);
    column.onKeyDown && column.onKeyDown(event);

    switch (event.key) {
      case 'ArrowLeft':
        me.onHeaderLeftKey(column);
        break;

      case 'ArrowRight':
        me.onHeaderRightKey(column);
        break;

      case 'ArrowDown':
        event.preventDefault();
        me.onHeaderDownKey(column);
        break;

      case 'Enter':
        me.onHeaderEnterKey(column);
        break;
    }
  }

  onHeaderLeftKey(column) {
    const prev = this.columns.getAdjacentVisibleLeafColumn(column, false);

    if (prev) {
      this.getHeaderElement(prev.id).focus();
    }
  }

  onHeaderRightKey(column) {
    const next = this.columns.getAdjacentVisibleLeafColumn(column, true);

    if (next) {
      this.getHeaderElement(next.id).focus();
    }
  }

  onHeaderDownKey(column) {
    const row = this.firstFullyVisibleRow;

    if (row) {
      this.focusCell({
        columnId: column.id,
        id: row.id
      });
    }
  }

  onHeaderEnterKey(column) {
    this.getHeaderElement(column.id).click();
  }
  /**
   * Key press, chain this function in features to handle the dom event.
   * @param event
   * @category Keyboard events
   * @internal
   */

  onElementKeyPress(event) {}
  /**
   * Key up, chain this function in features to handle the dom event.
   * @param event
   * @category Keyboard events
   * @internal
   */

  onElementKeyUp(event) {} //endregion
  //region Other events

  /**
   * Context menu, chain this function in features to handle the dom event.
   * In most cases, include ContextMenu feature instead.
   * @param event
   * @category Other events
   * @internal
   */

  onElementContextMenu(event) {
    const me = this,
          cellData = me.getCellDataFromEvent(event); // There is a cell

    if (cellData) {
      me.triggerCellMouseEvent('contextMenu', event); // Focus on tap for touch events.
      // Selection follows from focus.

      if (DomHelper.isTouchEvent) {
        me.onFocusGesture(cellData, event);
      }
    }
  }
  /**
   * Overrides empty base function in View, called when view is resized.
   * @fires resize
   * @param element
   * @param width
   * @param height
   * @param oldWidth
   * @param oldHeight
   * @category Other events
   * @internal
   */

  onInternalResize(element, width, height, oldWidth, oldHeight) {
    const me = this;

    if (me._devicePixelRatio && me._devicePixelRatio !== window.devicePixelRatio) {
      // Pixel ratio changed, likely because of browser zoom. This affects the relative scrollbar width also
      DomHelper.resetScrollBarWidth();
    }

    me._devicePixelRatio = window.devicePixelRatio; // cache to avoid recalculations in the middle of rendering code (RowManger#getRecordCoords())

    me._bodyRectangle = Rectangle.client(me.bodyContainer);
    super.onInternalResize(...arguments);

    if (height !== oldHeight) {
      me._bodyHeight = me.bodyContainer.offsetHeight;

      if (me.isPainted) {
        // initial height will be set from render(),
        // it reaches onInternalResize too early when rendering, headers/footers are not sized yet
        me.rowManager.initWithHeight(me._bodyHeight);
      }
    }

    me.refreshVirtualScrollbars();

    if (width !== oldWidth) {
      // Slightly delay to avoid resize loops.
      me.setTimeout(() => {
        if (!me.isDestroyed) {
          me.updateResponsive(width, oldWidth);
        }
      }, 0);
    }
  } //endregion
  // This does not need a className on Widgets.
  // Each *Class* which doesn't need 'b-' + constructor.name.toLowerCase() automatically adding
  // to the Widget it's mixed in to should implement thus.

  get widgetClass() {}

});

/**
 * @module Grid/view/mixin/GridFeatures
 */

const validConfigTypes = {
  string: 1,
  object: 1,
  function: 1 // used by CellTooltip

};
/**
 * Mixin for Grid that handles features. Features are plugins that add functionality to the grid. Feature classes should
 * register with Grid by calling {@link Grid.feature.GridFeatureManager#function-registerFeature-static registerFeature}. This
 * enables features to be specified and configured in grid
 * config.
 *
 * Define which features to use:
 *
 * ```javascript
 * // specify which features to use (note that some features are used by default)
 * const grid = new Grid({
 *   features: {
 *      sort: 'name',
 *      search: true
 *   }
 * });
 * ```
 *
 * Access a feature in use:
 *
 * ```javascript
 * grid.features.search.search('cat');
 * ```
 *
 * Basic example of implementing a feature:
 *
 * ```javascript
 * class MyFeature extends InstancePlugin {
 *
 * }
 *
 * GridFeatures.registerFeature(MyFeature);
 *
 * // using the feature
 * const grid = new Grid({
 *   features: {
 *     myFeature: true
 *   }
 * });
 * ```
 *
 * ## Enable and disable features at runtime
 *
 * Each feature is either "enabled" (included by default), or "off" (excluded completely). You can always check the docs
 * of a specific feature to find out how it is configured by default.
 *
 * Features which are "off" completely are not available and cannot be enabled at runtime.
 *
 * For a feature that is **off** by default that you want to enable later during runtime,
 * configure it with `disabled : true`:
 * ```javascript
 * const grid = new Grid({
 *      featureName : {
 *          disabled : true // on and disabled, can be enabled later
 *      }
 * });
 *
 * // enable the feature
 * grid.featureName.disabled = false;
 * ```
 *
 * If the feature is **off** by default, and you want to include and enable the feature, configure it as `true`:
 * ```javascript
 * const grid = new Grid({
 *      featureName : true // on and enabled, can be disabled later
 * });
 *
 * // disable the feature
 * grid.featureName.disabled = true;
 * ```
 *
 * If the feature is **on** by default, but you want to turn it **off**, configure it as `false`:
 * ```javascript
 * const grid = new Grid({
 *      featureName : false // turned off, not included at all
 * });
 * ```
 *
 * If the feature is **enabled** by default and you have no need of reconfiguring it,
 * you can omit the feature configuration.
 *
 * @mixin
 */

var GridFeatures = (Target => class GridFeatures extends (Target || Base) {
  static get $name() {
    return 'GridFeatures';
  } //region Init

  /**
   * Specify which features to use on the grid. Most features accepts a boolean, some also accepts a config object.
   * Please note that if you are not using the bundles you might need to import the features you want to use.
   *
   * ```javascript
   * const grid = new Grid({
   *     features : {
   *         stripe : true,   // Enable stripe feature
   *         sort   : 'name', // Configure sort feature
   *         group  : false   // Disable group feature
   *     }
   * }
   * ```
   *
   * @config {Object} features
   * @category Common
   */

  /**
   * Map of the features available on the grid. Use it to access them on your grid object
   *
   * ```javascript
   * grid.features.group.expandAll();
   * ```
   *
   * @readonly
   * @member {Object} features
   * @category Common
   * @typings any
   */

  set features(features) {
    const me = this,
          defaultFeatures = GridFeatureManager.getInstanceDefaultFeatures(this);
    features = me._features = ObjectHelper.assign({}, features); // default features, enabled unless otherwise specified

    if (defaultFeatures) {
      Object.keys(defaultFeatures).forEach(feature => {
        if (!(feature in features)) {
          features[feature] = true;
        }
      });
    } // We *prime* the features so that if any configuration code accesses a feature, it
    // will self initialize, but if not, they will remain in a primed state until afterConfigure.

    const registeredInstanceFeatures = GridFeatureManager.getInstanceFeatures(this);

    for (const featureName of Object.keys(features)) {
      const config = features[featureName]; // Create feature initialization property if config is truthy.
      // Config must be a valid configuration value for the feature class.

      if (config) {
        // Feature configs name must start with lowercase letter to be valid
        if (StringHelper.uncapitalize(featureName) !== featureName) {
          throw new Error(`Invalid feature name '${featureName}', must start with a lowercase letter`);
        }

        const featureClass = registeredInstanceFeatures[featureName];

        if (!featureClass) {
          throw new Error(`Feature '${featureName}' not available, make sure you have imported it`);
        } // Create a self initializing property on the features object named by the feature name.
        // when accessed, it will create and return the real feature.
        // Now, if some Feature initialization code attempt to access a feature which has not yet been initialized
        // it will be initialized just in time.

        Reflect.defineProperty(features, featureName, me.createFeatureInitializer(features, featureName, featureClass, config));
      }
    }
  }

  get features() {
    return this._features;
  }

  createFeatureInitializer(features, featureName, featureClass, config) {
    const constructorArgs = [this],
          construct = featureClass.prototype.construct; // Only pass config if there is one.
    // The constructor(config = {}) only works for undefined config

    if (validConfigTypes[typeof config]) {
      constructorArgs[1] = config;
    }

    return {
      configurable: true,

      get() {
        // Delete this defined property and replace it with the Feature instance.
        delete features[featureName]; // Ensure the feature is injected into the features object before initialization
        // so that it is available from call chains from its initialization.

        featureClass.prototype.construct = function (...args) {
          features[featureName] = this;
          construct.apply(this, args);
          featureClass.prototype.construct = construct;
        }; // Return the Feature instance

        return new featureClass(...constructorArgs);
      }

    };
  } //endregion
  //region Other stuff

  /**
   * Check if a feature is included
   * @param {String} name Feature name, as registered with `GridFeatureManager.registerFeature()`
   * @returns {Boolean}
   * @category Misc
   */

  hasFeature(name) {
    return !!(this.features && this.features[name]);
  } //endregion
  // This does not need a className on Widgets.
  // Each *Class* which doesn't need 'b-' + constructor.name.toLowerCase() automatically adding
  // to the Widget it's mixed in to should implement thus.

  get widgetClass() {}

});

/**
 * @module Grid/view/mixin/GridNavigation
 */

const defaultFocusOptions = Object.freeze({
  doSelect: true
}),
      disableScrolling = Object.freeze({
  x: false,
  y: false
});
/**
 * Mixin for Grid that handles cell to cell navigation.
 *
 * @mixin
 */

var GridNavigation = (Target => class GridNavigation extends (Target || Base) {
  static get $name() {
    return 'GridNavigation';
  } //region Cell

  /**
   * User navigated to a grid cell
   * @event navigate
   * @param {Grid.view.Grid} grid
   * @param {Object} last focused location
   * @param {Object} location focus moved to
   * @param {Event} [event] The UI event which caused navigation.
   */

  /**
   * Cell selector for focused cell, set to focus a cell or use {@link #function-focusCell}.
   * @property {Object}
   */

  get focusedCell() {
    const result = this._focusedCell;

    if (result && this.getCell(result)) {
      return result;
    }

    this.clearFocus();
  }
  /**
   * This property is `true` if an element _within_ a cell is focused.
   * @property {Boolean}
   * @readonly
   */

  get isActionableLocation() {
    const focusedCell = this._focusedCell;
    return Boolean(focusedCell && focusedCell.element && this.getCell(focusedCell).contains(focusedCell.element));
  }

  set focusedCell(cellSelector) {
    this.focusCell(cellSelector, {
      doSelect: false
    });
  }

  get focusedRecord() {
    if (this._focusedCell) {
      return this.store.getById(this._focusedCell.id);
    }
  }
  /**
   * CSS selector for currently focused cell. Format is "[data-index=index] [data-column-id=columnId]".
   * @property {String}
   * @readonly
   */

  get cellCSSSelector() {
    const me = this,
          cell = me._focusedCell,
          row = cell && me.getRowById(cell.id);
    if (!cell || !row) return '';
    return `[data-index=${row.index}] [data-column-id=${cell.columnId}]`;
  }
  /**
   * Grid element focus, selects first row and cell when grid is focused if none is selected (otherwise user cannot
   * see that grid is focused).
   * Chain this function it features to handle the dom event.
   * @param event
   * @category Other events
   * @internal
   */

  onGridElementFocus(event) {
    // Select first row if none is selected when grid is focused (to show that it has focus)
    const me = this,
          focusOptions = {
      doSelect: false,
      event
    };

    if (me._initialFocusedCellContext) {
      const {
        cellSelector,
        options
      } = me._initialFocusedCellContext;
      me._focusedCell = null;
      me.focusCell(cellSelector, options);
      me._initialFocusedCellContext = null;
    } else if (me._focusedCell) {
      me.focusCell(me._focusedCell, focusOptions);
    } else {
      // If they just clicked in blank space, do not refocus the last focused cell.
      // We focus cell 0, 0 on that gesture.
      const targetContext = me.element.contains(event.relatedTarget) ? null : me.lastFocusedCell;

      if (targetContext) {
        const cell = me.getCell(targetContext); // If we're using lastFocusedCell and the cell is no longer in existence, or not visible
        // or the previously focused record has since been removed.
        // Fallback to reverting to where focus entered the grid from.
        // TODO: CellContext should store the record index
        // to fall back to if the actual record is no longer present.

        if (!cell || !DomHelper.isVisible(cell) || !me.store.getById(targetContext.id)) {
          me.revertFocus();
          return;
        } // flag to not affect selection

        me.returningFocus = true;
        me.focusCell(targetContext, focusOptions);
        me.returningFocus = false;
      } else if (!me.scrolling && !me.skipFocusSelection && me.store.first) {
        const {
          mouseMoveEvent: evt,
          rowManager: rm,
          _bodyRectangle: r
        } = me,
              defaultRow = evt && rm.getRowAt(Math.min(Math.max(evt.clientY, r.y), r.bottom)) || me.firstFullyVisibleRow,
              focusContext = evt && me.getCellDataFromEvent(evt) || {
          id: defaultRow.id,
          columnId: me.columns.visibleColumns[0].id
        };

        if (me.getCell(focusContext)) {
          me.focusCell(focusContext, focusOptions);
        }
      }
    }

    me.skipFocusSelection = false;
  }

  onFocusOut(event) {
    super.onFocusOut(event);
    this.clearFocus();
  }

  afterHide() {
    super.afterHide(...arguments); // Do not scroll back to the last focused cell/last moused over cell upon reshow

    this.lastFocusedCell = this.mouseMoveEvent = null;
  }
  /**
   * Checks whether or not a cell is focused.
   * @param {Object|string|Number} cellSelector Cell selector { id: x, columnId: xx } or row id
   * @returns {Boolean} true if cell or row is focused, otherwise false
   */

  isFocused(cellSelector) {
    return Boolean(this._focusedCell) && this.isLocationEqual(cellSelector, this._focusedCell);
  }
  /**
   * Navigates to a cell and/or its row (depending on selectionMode)
   * @param {Object} cellSelector { id: rowId, columnId: 'columnId' }
   * @param {Object} options Modifier options for how to deal with focusing the cell. These
   * are used as the {@link Core.helper.util.Scroller#function-scrollTo} options.
   * @param {Boolean} [options.doSelect=true] Optionally pass `false` to not continue to selection.
   * @param {Event} [options.event] Optionally pass the UI event which instigated the focus request.
   * @param {Object|boolean} [options.scroll=true] Pass `false` to not scroll the cell into view, or a
   * scroll options object to affact the scroll.
   * @returns {Object} Cell selector
   * @fires navigate
   */

  focusCell(cellSelector, options = defaultFocusOptions) {
    var _options, _options$event, _Widget$fromElement;

    // Mac OS specific behaviour: when you right click a non-active window, the window does not receive focus, but the context menu is shown.
    // So for Mac OS we treat the right click as a non-focusable action, if window is not active
    if (!document.hasFocus() && BrowserHelper.isMac && ((_options = options) === null || _options === void 0 ? void 0 : (_options$event = _options.event) === null || _options$event === void 0 ? void 0 : _options$event.button) === 2) {
      return;
    }

    const me = this,
          {
      activeElement
    } = DomHelper; // If we're being passed a context object (as opposed to a record), clone in case we were
    // passed the _focusedCell, and after refresh isLocationEqual needs to detect a difference.

    cellSelector = me.normalizeCellContext(cellSelector === Object ? Object.assign({}, cellSelector) : cellSelector);
    const doSelect = options.doSelect !== false,
          {
      event,
      scroll
    } = options,
          lastFocusedCell = me.lastFocusedCell = me._focusedCell,
          isNotMove = me.isLocationEqual(cellSelector, lastFocusedCell),
          lastCell = lastFocusedCell && me.getCell(lastFocusedCell),
          subGrid = me.getSubGridFromColumn(cellSelector.columnId);
    let cell = me.getCell(cellSelector); // If we're focusing due to a mousedown on a focusable element *within* a cell
    // then we do not "navigate" to the cell - we allow the element to be focused.
    // For example a CheckColumn or WidgetColumn.

    if (event && event.type === 'mousedown') {
      for (let target = event.target; target !== cell; target = target.parentElement) {
        if (DomHelper.isFocusable(target)) {
          cellSelector.element = target;
          return;
        }
      }
    } // No navigation takes place, but the selection still needs to know if a UI event happened.

    if (isNotMove) {
      if (event) {
        me.onCellNavigate && me.onCellNavigate(me, null, me._focusedCell, event, doSelect);
      }

      return cellSelector;
    }

    if (lastCell) {
      lastCell.classList.remove('b-focused');
    } // DomHelper.focusWithoutScrolling means "Don't try to scroll this element into view"
    // because browsers try to move the element into view.
    // But the grid itself needs to scroll the cell into view.

    const testCell = cell || me.getCell({
      row: me.rowManager.topIndex,
      columnId: cellSelector.columnId
    }),
          subGridRect = Rectangle.from(subGrid.element),
          bodyRect = Rectangle.from(me.bodyContainer),
          cellRect = Rectangle.from(testCell).moveTo(null, subGridRect.y);

    if (scroll === false) {
      options = Object.assign({}, options, disableScrolling);
    } else {
      options = Object.assign({}, options, scroll); // If the test cell is larger than the subGrid, in any dimension, disable scrolling

      if (cellRect.width > subGridRect.width || cellRect.height > bodyRect.height) {
        options.x = options.y = false;
      } // Else ask for the column to be scrolled into view
      else {
          options.column = cellSelector.columnId;
        }

      me.scrollRowIntoView(cellSelector.id, options);
    } // If active element is not a part of the grid body,
    // and the grid does not own the widget which is represented by the active element
    // (for example header element), focus the cell without scrolling

    if (activeElement !== me.focusElement && !me.focusElement.contains(activeElement) && ((_Widget$fromElement = Widget.fromElement(activeElement)) === null || _Widget$fromElement === void 0 ? void 0 : _Widget$fromElement.owner) !== me) {
      me._initialFocusedCellContext = {
        cellSelector,
        options
      };
      DomHelper.focusWithoutScrolling(me.focusElement); // If focusing didn't work right away, clear the cached value, so onGridElementFocus is not affected by the value.
      // Does not work in IE11 because focus in IE asynchronous. Just ignore it.

      if (!me.isLocationEqual(cellSelector, me._focusedCell) && !BrowserHelper.isIE11) {
        me._initialFocusedCellContext = null;
      }

      return;
    } // Get the newly visible cell *after* it has been scrolled into view.
    // With buffered rendering, it may not have existed before the scroll.

    cell = me.getCell(cellSelector);

    if (cell) {
      cellSelector.element = cell;
      cell.classList.add('b-focused');
    } //Remember

    me._focusedCell = cellSelector;
    me.onCellNavigate && me.onCellNavigate(me, lastFocusedCell, me._focusedCell, event, doSelect);
    me.trigger('navigate', {
      lastFocusedCell,
      focusedCell: me._focusedCell,
      event
    }); //TODO: should be able to cancel selectcell from listeners

    return cellSelector;
  }

  isLocationEqual(cellSelector, otherCellSelector) {
    return cellSelector && otherCellSelector && cellSelector.id === otherCellSelector.id && cellSelector.columnId === otherCellSelector.columnId && ( // Normalized cellSelectors are not required to have an element, only compare elements if both selectors
    // have it specified
    !cellSelector.element || !otherCellSelector.element || cellSelector.element === otherCellSelector.element);
  }

  blurCell(cellSelector) {
    const me = this,
          cell = me.getCell(cellSelector);

    if (cell) {
      cell.classList.remove('b-focused');
    }
  }

  clearFocus() {
    const me = this;

    if (me._focusedCell) {
      // set last to have focus return to previous cell when alt tabbing
      me.lastFocusedCell = me._focusedCell;
      me.blurCell(me._focusedCell);
      me._focusedCell = null;
    }
  }
  /**
   * Selects the cell before or after currently focused cell.
   * @private
   * @param next Specify true to select the next cell, false to select the previous
   * @param {Event} [event] Optionally, the UI event which caused navigation.
   * @returns {Object} Used cell selector
   */

  internalNextPrevCell(next = true, event) {
    const me = this,
          cellSelector = me._focusedCell;

    if (cellSelector) {
      return me.focusCell({
        id: cellSelector.id,
        columnId: me.columns.getAdjacentVisibleLeafColumn(cellSelector.columnId, next, true).id
      }, {
        doSelect: true,
        event
      });
    }

    return null;
  }
  /**
   * Select the cell after the currently focused one.
   * @param {Event} [event] Optionally, the UI event which caused navigation.
   * @returns {Object} Cell selector
   */

  navigateRight(event) {
    return this.internalNextPrevCell(true, event);
  }
  /**
   * Select the cell before the currently focused one.
   * @param {Event} [event] Optionally, the UI event which caused navigation.
   * @returns {Object} Cell selector
   */

  navigateLeft(event) {
    return this.internalNextPrevCell(false, event);
  } //endregion
  //region Row

  /**
   * Selects the next or previous record in relation to the current selection. Scrolls into view if outside.
   * @private
   * @param next Next record (true) or previous (false)
   * @param {Boolean} skipSpecialRows True to not return specialRows like headers
   * @param {Event} [event] Optionally, the UI event which caused navigation.
   * @returns {Object|Boolean} Selection context for the focused row (& cell) or false if no selection was made
   */

  internalNextPrevRow(next, skipSpecialRows = true, event, moveToHeader = true) {
    const me = this,
          cell = me._focusedCell;
    if (!cell) return false;
    const record = me.store[`get${next ? 'Next' : 'Prev'}`](cell.id, false, skipSpecialRows);

    if (record) {
      return me.focusCell({
        id: record.id,
        columnId: cell.columnId,
        scroll: {
          x: false
        }
      }, {
        doSelect: true,
        event
      });
    } else if (!next && moveToHeader) {
      this.clearFocus();
      return this.getHeaderElement(cell.columnId).focus();
    }
  }
  /**
   * Navigates to the cell below the currently focused cell
   * @param {Event} [event] Optionally, the UI event which caused navigation.
   * @returns {Object} Selector for focused row (& cell)
   */

  navigateDown(event) {
    return this.internalNextPrevRow(true, false, event);
  }
  /**
   * Navigates to the cell above the currently focused cell
   * @param {Event} [event] Optionally, the UI event which caused navigation.
   * @returns {Object} Selector for focused row (& cell)
   */

  navigateUp(event) {
    return this.internalNextPrevRow(false, false, event);
  } //endregion
  // This does not need a className on Widgets.
  // Each *Class* which doesn't need 'b-' + constructor.name.toLowerCase() automatically adding
  // to the Widget it's mixed in to should implement thus.

  get widgetClass() {}

});

/**
 * @module Grid/view/mixin/GridResponsive
 */

/**
 * Simplifies making grid responsive. Supply levels as {@link #config-responsiveLevels} config, default levels are:
 * <dl>
 * <dt>small <dd>< 400px,
 * <dt>medium <dd>< 600px
 * <dt>large <dd>> 600px
 * </dl>
 *
 * Columns can define configs per level to be resized etc:
 *
 * ```
 * let grid = new Grid({
 *   responsiveLevels: {
 *     small: 300,
 *     medium: 400,
 *     large: '*' // everything above 400
 *   },
 *
 *   columns: [
 *     {
 *       field: 'name',
 *       text: 'Name',
 *       responsiveLevels: {
 *         small: { hidden: true },
 *         '*': { hidden: false } // all other levels
 *       }
 *     },
 *     { field: 'xx', ... }
 *   ]
 * });
 * ```
 *
 * It is also possible to give a [Grid state](#Grid/view/mixin/GridState) object instead of a level width, but in that
 * case the object must contain a `levelWidth` property:
 *
 * ```
 * let grid = new Grid({
 *   responsiveLevels: {
 *     small: {
 *       // Width is required
 *       levelWidth : 400,
 *       // Other configs are optional, see GridState for available options
 *       rowHeight  : 30
 *     },
 *     medium : {
 *       levelWidth : 600,
 *       rowHeight  : 40
 *     },
 *     large: {
 *       levelWidth : '*', // everything above 300
 *       rowHeight  : 45
 *     }
 *   }
 * });
 * ```
 *
 * @demo Grid/responsive
 * @externalexample grid/Responsive.js
 * @mixin
 */

var GridResponsive = (Target => class GridResponsive extends (Target || Base) {
  static get $name() {
    return 'GridResponsive';
  }

  static get defaultConfig() {
    return {
      /**
       * "Break points" for which responsive config to use for columns and css.
       * @config {Object}
       * @category Misc
       * @default { small : 400, medium : 600, large : '*' }
       */
      responsiveLevels: Object.freeze({
        small: 400,
        medium: 600,
        large: '*'
      })
    };
  }
  /**
   * Find closes bigger level, aka level we want to use.
   * @private
   * @category Misc
   */

  getClosestBiggerLevel(width) {
    let me = this,
        levels = Object.keys(me.responsiveLevels),
        useLevel = null,
        minDelta = 99995,
        biggestLevel = null;
    levels.forEach(level => {
      let levelSize = me.responsiveLevels[level]; // responsiveLevels can contains config objects, in which case we should use width from it

      if (!['number', 'string'].includes(typeof levelSize)) {
        levelSize = levelSize.levelWidth;
      }

      if (levelSize === '*') {
        biggestLevel = level;
      } else if (width < levelSize) {
        const delta = levelSize - width;

        if (delta < minDelta) {
          minDelta = delta;
          useLevel = level;
        }
      }
    });
    return useLevel || biggestLevel;
  }
  /**
   * Get currently used responsive level (as string)
   * @property {String}
   * @readonly
   * @category Misc
   */

  get responsiveLevel() {
    return this.getClosestBiggerLevel(this.width);
  }
  /**
   * Check if resize lead to a new responsive level and take appropriate actions
   * @private
   * @fires responsive
   * @param width
   * @param oldWidth
   * @category Misc
   */

  updateResponsive(width, oldWidth) {
    const me = this,
          oldLevel = me.getClosestBiggerLevel(oldWidth),
          level = me.getClosestBiggerLevel(width); // On first render oldWidth is 0, in such case we need to apply level anyway

    if (oldWidth === 0 || oldLevel !== level) {
      // Level might be a state object
      const levelConfig = me.responsiveLevels[level];

      if (!['number', 'string'].includes(typeof levelConfig)) {
        me.applyState(levelConfig);
      } // check columns for responsive config

      me.columns.forEach(column => {
        const levels = column.responsiveLevels;

        if (levels) {
          if (levels[level]) {
            // using state to apply responsive config, since it already does what we want...
            column.applyState(levels[level]);
          } else if (levels['*']) {
            column.applyState(levels['*']);
          }
        }
      });
      me.element.classList.remove('b-responsive-' + oldLevel);
      me.element.classList.add('b-responsive-' + level);
      /**
       * Grid resize lead to a new responsive level being applied
       * @event responsive
       * @param {Grid.view.Grid} grid Grid that was resized
       * @param {String} level New responsive level (small, large, etc)
       * @param {Number} width New width in px
       * @param {String} oldLevel Old responsive level
       * @param {Number} oldWidth Old width in px
       */

      me.trigger('responsive', {
        level,
        width,
        oldLevel,
        oldWidth
      });
    }
  } // This does not need a className on Widgets.
  // Each *Class* which doesn't need 'b-' + constructor.name.toLowerCase() automatically adding
  // to the Widget it's mixed in to should implement thus.

  get widgetClass() {}

});

const validIdTypes = {
  string: 1,
  number: 1
},
      isSelectAction = {
  dataset: 1,
  batch: 1
};
/**
 * @module Grid/view/mixin/GridSelection
 */

/**
 * A mixin for Grid that handles row and cell selection. See {@link #config-selectionMode} for details on how to control what should be selected (rows or cells)
 *
 * @example
 * // select a row
 * grid.selectedRow = 7;
 *
 * // select a cell
 * grid.selectedCell = { id: 5, columnId: 'column1' }
 *
 * // select a record
 * grid.selectedRecord = grid.store.last;
 *
 * // select multiple records by ids
 * grid.selectedRecords = [1, 2, 4, 6]
 *
 * @mixin
 */

var GridSelection = (Target => class GridSelection extends (Target || Base) {
  static get $name() {
    return 'GridSelection';
  }

  static get defaultConfig() {
    return {
      /**
       * The selection settings, where you can set these boolean flags to control what is selected. Options below:
       * @config {Object} selectionMode
       * @param {Boolean} selectionMode.row select rows
       * @param {Boolean} selectionMode.cell select cells
       * @param {Boolean} selectionMode.rowCheckboxSelection select rows only when clicking in the checkbox column
       * @param {Boolean} selectionMode.multiSelect Allow multiple selection
       * @param {Boolean} selectionMode.checkbox true to add a checkbox selection column to the grid
       * @param {Boolean} selectionMode.showCheckAll  true to add a checkbox to the selection column header to select/deselect all rows
       * @param {Boolean} selectionMode.deselectFilteredOutRecords true to deselect rows that are filtered out
       * @default
       * @category Selection
       */
      selectionMode: {
        row: true,
        cell: true,
        rowCheckboxSelection: false,
        multiSelect: true,
        checkbox: false,
        showCheckAll: false,
        deselectFilteredOutRecords: false
      },
      selectedRecordCollection: {}
    };
  } //region Init

  afterConfigure() {
    const me = this; // Inject our CheckColumn into the ColumnStore

    if (me.selectionMode.checkbox) {
      const checkColumnClass = ColumnStore.getColumnClass('check');

      if (!checkColumnClass) {
        throw new Error('CheckColumn must be imported for checkbox selection mode to work');
      }

      const col = me.checkboxColumn = new checkColumnClass({
        id: `${me.id}-selection-column`,
        width: '4em',
        minWidth: '4em',
        // Misaligned on IE11 without, since 4em is below columns default minWidth
        field: null,
        cellCls: 'b-checkbox-selection',
        // Always put the checkcolumn in the first region
        region: me.items[0].region,
        showCheckAll: me.selectionMode.showCheckAll
      }, me.columns);
      col.meta.depth = 0; // This is assigned in Column.js for normal columns

      col._grid = me; // Override renderer to inject the rendered record's selected status into the value

      const checkboxRenderer = col.renderer;

      col.renderer = renderData => {
        renderData.value = me.isSelected(renderData.record);
        checkboxRenderer.call(col, renderData);
      };

      col.on({
        toggle: 'onCheckChange',
        toggleAll: 'onCheckAllChange',
        thisObj: me
      }); // Insert the checkbox after any rownumber column. If not there, -1 means in at 0.

      const insertIndex = me.columns.indexOf(me.columns.findRecord('type', 'rownumber')) + 1;
      me.columns.insert(insertIndex, col);
    }

    me.store.on({
      filter: 'onStoreFilter',
      thisObj: me
    });
    super.afterConfigure();
  } //endregion
  //region Events

  onStoreFilter({
    records
  }) {
    if (this.selectionMode.deselectFilteredOutRecords) {
      const {
        filtersFunction
      } = this.store,
            filteredOutRecords = this.selectedRecordCollection.values.filter(rec => !filtersFunction(rec));
      this.selectedRecordCollection.remove(filteredOutRecords);
    }
  }
  /**
   * Triggered from Grid view when the id of a record has changed.
   * Update the collection indices.
   * @private
   * @category Selection
   */

  onStoreRecordIdChange({
    record,
    oldValue
  }) {
    // If the next mixin up the inheritance chain has an implementation, call it
    super.onStoreRecordIdChange && super.onStoreRecordIdChange(...arguments);
    const item = this.selectedRecordCollection.get(oldValue); // having the record registered by the oldValue means we need to rebuild indices

    if (item === record) {
      this.selectedRecordCollection.rebuildIndices();
    }
  }
  /**
   * The selection has been changed.
   * @event selectionChange
   * @param {String} action `'select'`/`'deselect'`
   * @param {String} mode `'row'`/`'cell'`
   * @param {Grid.view.Grid} source
   * @param {Core.data.Model[]|Object} deselected The records or cells (depending on the `mode`) deselected in this operation.
   * @param {Core.data.Model[]|Object} selected The records or cells (depending on the `mode`) selected in this operation.
   * @param {Core.data.Model[]|Object} selection  The records or cells (depending on the `mode`) in the new selection.
   */

  /**
   * Responds to mutations of the underlying storage Collection
   * @param {Object} event
   * @private
   */

  onSelectedRecordCollectionChange({
    source: selectedRecordCollection,
    action,
    added = [],
    removed
  }) {
    const me = this;

    if (me._selectedCell && !me.isSelectable(me._selectedCell)) {
      me.deselectCell(me._selectedCell);
    } // Filter out unselectable rows

    added = added.filter(row => me.isSelectable(row));
    me.triggerChangeEvent({
      mode: 'row',
      action: added.length ? 'select' : 'deselect',
      selection: me.selectedRecords,
      selected: added,
      deselected: removed
    }, me.silent);
  }

  onCheckChange({
    source: column,
    checked,
    record
  }) {
    if (checked) {
      this.selectRow({
        record,
        column,
        addToSelection: this.selectionMode.multiSelect !== false,
        scrollIntoView: false
      });

      if (column.headerCheckbox && this.selectedRecords.length === this.store.count - (this.store.groupRecords ? this.store.groupRecords.length : 0)) {
        column.suspendEvents();
        column.headerCheckbox.checked = true;
        column.resumeEvents();
      }
    } else {
      this.deselectRow(record);

      if (column.headerCheckbox) {
        column.suspendEvents();
        column.headerCheckbox.checked = false;
        column.resumeEvents();
      }
    }
  }

  onCheckAllChange({
    checked
  }) {
    this[checked ? 'selectAll' : 'deselectAll']();
  } //endregion
  //region Selection collection

  set selectedRecordCollection(selectedRecordCollection) {
    if (!(selectedRecordCollection instanceof Collection)) {
      selectedRecordCollection = new Collection(selectedRecordCollection);
    }

    this._selectedRecordCollection = selectedRecordCollection; // Fire row change events from onSelectedRecordCollectionChange

    selectedRecordCollection.on({
      change: 'onSelectedRecordCollectionChange',
      thisObj: this
    });
  }

  get selectedRecordCollection() {
    return this._selectedRecordCollection;
  }
  /**
   * Removes and adds records to/from the selection at the same time. Analogous
   * to the `Array` `splice` method.
   *
   * Note that if items that are specified for removal are also in the `toAdd` array,
   * then those items are *not* removed then appended. They remain in the same position
   * relative to all remaining items.
   *
   * @param {Number} index Index at which to remove a block of items. Only valid if the
   * second, `toRemove` argument is a number.
   * @param {Object[]|Number} toRemove Either the number of items to remove starting
   * at the passed `index`, or an array of items to remove (If an array is passed, the `index` is ignored).
   * @param  {Object[]|Object} toAdd An item, or an array of items to add.
   */

  spliceSelectedRecords(index, toRemove, toAdd) {
    this._selectedRecordCollection.splice(index, toRemove, toAdd);
  } //endregion
  //region Cell & row

  /**
   * Checks whether or not a cell or row is selected.
   * @param {Object|string|Number|Core.data.Model} cellSelectorOrId Cell selector { id: x, column: xx } or row id, or record
   * @returns {Boolean} true if cell or row is selected, otherwise false
   * @category Selection
   */

  isSelected(cellSelectorOrId) {
    var _cellSelectorOrId;

    const me = this; // A record passed
    // HACK: replaces instanceof check with this because it makes GridSelection test in Edge more stable

    if ((_cellSelectorOrId = cellSelectorOrId) !== null && _cellSelectorOrId !== void 0 && _cellSelectorOrId.isModel) {
      cellSelectorOrId = cellSelectorOrId.id;
    }

    if (validIdTypes[typeof cellSelectorOrId]) {
      return me.selectedRecordCollection.includes(cellSelectorOrId);
    } else {
      return me._selectedCell && me._selectedCell.id == cellSelectorOrId.id && me._selectedCell.columnId === cellSelectorOrId.columnId;
    }
  }
  /**
   * Checks whether or not a cell or row can be selected.
   * @param {Core.data.Model|Object|String|Number} recordCellOrId Record or cell or record id
   * @returns {Boolean} true if cell or row can be selected, otherwise false
   * @category Selection
   */

  isSelectable(recordCellOrId) {
    // Selection disabled for undefined record, special row (group header / footer) and
    // record which was already removed from store
    if (!recordCellOrId) {
      return false;
    }

    const row = this.store.getById(recordCellOrId.id || recordCellOrId);
    return row && !(row.meta && row.isSpecialRow);
  }
  /**
   * Cell selector for selected cell, set to select a cell or use {@link #function-selectCell()}.
   * @property {Object}
   * @category Selection
   */

  get selectedCell() {
    return this._selectedCell;
  }

  set selectedCell(cellSelector) {
    this.selectCell(cellSelector);
  }

  set selectionMode(mode) {
    if (mode && mode.rowCheckboxSelection) {
      mode.row = mode.checkbox = true;
      mode.cell = false;
    }

    this._selectionMode = mode;
  }

  get selectionMode() {
    return this._selectionMode;
  }
  /**
   * The last selected record. Set to select a row or use Grid#selectRow. Set to null to
   * deselect all
   * @property {Core.data.Model}
   * @category Selection
   */

  get selectedRecord() {
    return this.selectedRecordCollection.last || null;
  }

  set selectedRecord(record) {
    this.selectRow({
      record
    });
  }
  /**
   * Selected records.
   * Can be set as array of ids.
   * ```
   * grid.selectedRecords = [1, 2, 4, 6]
   * ```
   *
   * @property {Core.data.Model[]|Number[]}
   * @category Selection
   */

  get selectedRecords() {
    return this.selectedRecordCollection.values;
  }

  set selectedRecords(selectedRecords) {
    const {
      selectedRecordCollection,
      store
    } = this,
          toSelect = [];

    if (selectedRecords) {
      for (let record of selectedRecords) {
        record = store.getById(record);

        if (record) {
          toSelect.push(record);
        }
      }
    } // Replace the entire selected collection with the new record set

    selectedRecordCollection.splice(0, selectedRecordCollection.count, toSelect);
  }
  /**
   * CSS selector for the currently selected cell. Format is "[data-index=index] [data-column-id=column]".
   * @type {String}
   * @category Selection
   * @readonly
   */

  get selectedCellCSSSelector() {
    const me = this,
          cell = me._selectedCell,
          row = cell && me.getRowById(cell.id);
    if (!cell || !row) return '';
    return `[data-index=${row.dataIndex}] [data-column-id=${cell.columnId}]`;
  }
  /**
   * Selects a row (without selecting a cell).
   * @param {Object|Core.data.Model} options A record to select or an config object describing the selection
   * @param {Core.data.Model|String|Number} options.record Record or record id, specifying null will deselect all
   * @param {Grid.column.Column} options.column The column to scroll into view if `scrollIntoView` is not specified as `false`. Defaults to the grid's first column.
   * @param {Boolean} [options.scrollIntoView] Specify `false` to prevent row from being scrolled into view
   * @param {Boolean} [options.addToSelection] Specify `true` to add to selection, defaults to `false` which replaces
   * @fires selectionChange
   * @category Selection
   */

  selectRow({
    record,
    column = this.columns.bottomColumns[0],
    scrollIntoView = true,
    addToSelection = false
  }) {
    const me = this;

    if (arguments[0].isModel) {
      record = arguments[0];
    } else {
      record = me.store.getById(record);
    }

    if (record) {
      me.selectCell({
        id: record.id,
        column
      }, scrollIntoView, addToSelection);
    } else {
      me.deselectAll();
    }
  }
  /**
   * Selects a cell and/or its row (depending on {@link #config-selectionMode})
   * @param {Object} cellSelector { id: rowId, columnId: 'columnId' }
   * @param {Boolean} scrollIntoView Specify `false` to prevent row from being scrolled into view
   * @param {Boolean} addToSelection Specify `true` to add to selection, defaults to `false` which replaces
   * @param {Boolean} silent Specify `true` to not trigger any events when selecting the cell
   * @returns {Object} Cell selector
   * @fires selectionChange
   * @category Selection
   */

  selectCell(cellSelector, scrollIntoView = false, addToSelection = false, silent = false) {
    const me = this,
          {
      selectedRecordCollection,
      selectionMode
    } = me,
          selector = me.normalizeCellContext(cellSelector),
          record = selector.record || me.store.getById(selector.id); // Clear selection if row is not selectable

    if (!me.isSelectable(record)) {
      this.deselectAll();
      return;
    }

    if (scrollIntoView) {
      me.scrollRowIntoView(selector.id, {
        column: selector.columnId
      });
    } // Row selection (both sides if locked columns)

    if (selectionMode.row) {
      if (silent) {
        me.silent = (me.silent || 0) + 1;
      }

      if (addToSelection) {
        selectedRecordCollection.add(record);
      } // Clear all others
      else {
          selectedRecordCollection.splice(0, selectedRecordCollection.count, record);
        }

      if (silent) {
        me.silent--;
      } // When starting a selection, register the start cell

      if (me.selectedRecordCollection.count === 1) {
        me.startCell = selector;
        me.lastRange = null;
      }
    } // Cell selection

    if (selectionMode.cell && (selector.columnId || selector.column) && !me.isSelected(selector)) {
      const deselected = me._selectedCell ? [me._selectedCell] : []; //Remember

      me._selectedCell = selector;
      me.triggerChangeEvent({
        mode: 'cell',
        action: 'select',
        selected: [selector],
        deselected,
        selection: [selector]
      }, silent);
    }

    return selector;
  }
  /**
   * Selects all rows. If store is filtered, this will merge the selection of all visible rows with any selection made prior to filtering
   * @category Selection
   */

  selectAll() {
    const me = this;

    if (me.store.isFiltered && !me.selectionMode.deselectFilteredOutRecords) {
      me.selectedRecordCollection.add(...me.store.records.filter(r => !r.isSpecialRow));
    } else {
      me.selectedRecords = me.store.records.filter(r => !r.isSpecialRow);
    }
  }
  /**
   * Deselects all selected rows and cells. If store is filtered, this will unselect all visible rows only. Any selections made prior to filtering remains.
   * @param {Boolean} [keepFilteredOutSelection] Pass `false` to clear all selected records, and `true` to only
   * clear selected records in the current filtered set of records
   * @category Selection
   */

  deselectAll(keepFilteredOutSelection = false) {
    const me = this;

    if (keepFilteredOutSelection) {
      me.selectedRecordCollection.remove(...me.store.records);
    } else {
      me.selectedRecordCollection.clear();
    }

    if (me._selectedCell) {
      me.deselectCell(me._selectedCell);
    }
  }
  /**
   * Deselect a row
   * @param {Core.data.Model|String|Number} recordOrId Record or an id for a record
   * @category Selection
   */

  deselectRow(record) {
    this.deselectRows(record);
  }
  /**
   * Deselect one or more rows
   * @param {Core.data.Model|String|Number|Core.data.Model[]|String[]|Number[]} recordOrIds An array of records or ids for a record
   * @category Selection
   */

  deselectRows(recordsOrIds) {
    recordsOrIds = Array.isArray(recordsOrIds) ? recordsOrIds : [recordsOrIds];
    const records = recordsOrIds.map(recordOrId => this.store.getById(recordOrId));
    this.selectedRecordCollection.remove(records);
  }
  /**
   * Deselect a cell/row, depending on settings in Grid#selectionMode
   * @param {Object} cellSelector
   * @returns {Object} Normalized cell selector
   * @category Selection
   */

  deselectCell(cellSelector) {
    const me = this,
          selector = me.normalizeCellContext(cellSelector),
          selMode = me.selectionMode,
          record = selector.record || me.store.getById(selector.id),
          selectedCell = me._selectedCell; // Row selection (both sides if locked columns)

    if (selMode.row) {
      me.selectedRecordCollection.remove(record);
    } // Cell selection

    if (selMode.cell && selector.columnId && selectedCell) {
      if (selectedCell.id === selector.id && selectedCell.columnId === selector.columnId) {
        me._selectedCell = null;
        me.triggerChangeEvent({
          mode: 'cell',
          action: 'deselect',
          selected: [],
          deselected: [selector],
          selection: []
        });
      }
    }

    return selector;
  } //endregion
  //region Record

  /**
   * Selects rows corresponding to a range of records (from fromId to toId)
   * @param {String|Number} fromId
   * @param {String|Number} toId
   * @category Selection
   */

  selectRange(fromId, toId) {
    const {
      store,
      selectedRecordCollection
    } = this,
          fromIndex = store.indexOf(fromId),
          toIndex = store.indexOf(toId),
          startIndex = Math.min(fromIndex, toIndex),
          endIndex = Math.max(fromIndex, toIndex);

    if (startIndex === -1 || endIndex === -1) {
      throw new Error('Record not found in selectRange');
    }

    selectedRecordCollection.splice(0, selectedRecordCollection.count, store.getRange(startIndex, endIndex + 1, false));
  }
  /**
   * Triggered from Grid view when records get removed from the store.
   * Deselects all records which have been removed.
   * @private
   * @category Selection
   */

  onStoreRemove(event) {
    // If the next mixin up the inheritance chain has an implementation, call it
    super.onStoreRemove && super.onStoreRemove(event);
    this.selectedRecordCollection.remove(event.records);
  }
  /**
   * Triggered from Grid view when the store changes. This might happen
   * if store events are batched and then resumed.
   * Deselects all records which have been removed.
   * @private
   * @category Selection
   */

  onStoreDataChange({
    action,
    source: store
  }) {
    // If the next mixin up the inheritance chain has an implementation, call it
    super.onStoreDataChange && super.onStoreDataChange(...arguments);

    if (isSelectAction[action]) {
      const selectedRecords = this.selectedRecordCollection,
            toRemove = []; // Update selected records collection

      selectedRecords.forEach(record => {
        // If record still exists after reload, update selectedRecordCollection with a reference to the new task version
        if (store.includes(record)) {
          const index = selectedRecords.indexOf(record);
          selectedRecords.splice(index, 1, store.getById(record.id));
        } else {
          toRemove.push(record);
        }
      }); // Remove in one go to fire a single selectionChange event

      selectedRecords.remove(toRemove);
    }
  }
  /**
   * Triggered from Grid view when all records get removed from the store.
   * Deselects all records.
   * @private
   * @category Selection
   */

  onStoreRemoveAll() {
    // If the next mixin up the inheritance chain has an implementation, call it
    super.onStoreRemoveAll && super.onStoreRemoveAll();
    this.deselectAll();
  } //endregion
  //region Handle multiSelect

  /**
   * Handles multi selection using the mouse. Called from GridElementEvents on mousedown in a cell and
   * simultaneously pressing a modifier key.
   * @param cellData
   * @param event
   * @private
   * @category Selection
   */

  handleMouseMultiSelect(cellData, event) {
    const me = this,
          id = cellData.id;

    function mergeRange(fromId, toId) {
      const {
        store,
        selectedRecordCollection
      } = me,
            fromIndex = store.indexOf(fromId),
            toIndex = store.indexOf(toId),
            startIndex = Math.min(fromIndex, toIndex),
            endIndex = Math.max(fromIndex, toIndex);

      if (startIndex === -1 || endIndex === -1) {
        throw new Error('Record not found in selectRange');
      }

      const newRange = store.getRange(startIndex, endIndex + 1, false).filter(row => me.isSelectable(row));
      selectedRecordCollection.splice(0, me.lastRange || 0, newRange);
      me.lastRange = newRange;
    }

    if ((event.metaKey || event.ctrlKey) && me.isSelected(id)) {
      // ctrl/cmd deselects row if selected
      me.deselectRow(id);
    } else if (me.selectionMode.multiSelect) {
      if (event.shiftKey && me.startCell) {
        // shift appends selected range (if we have previously focused cell)
        mergeRange(me.startCell.id, id);
      } else if (event.ctrlKey || event.metaKey) {
        // ctrl/cmd adds to selection if using multiselect (and not selected)
        me.selectRow({
          record: id,
          scrollIntoView: false,
          addToSelection: true
        });
      }
    }
  } //endregion
  //region Navigation

  /**
   * Triggered from GridNavigation when focus is moved to another cell within the grid. Selects the cell unless
   * modifier keys are pressed, in which case it has already been handled
   * @private
   * @category Selection
   */

  onCellNavigate(me, fromCellSelector, toCellSelector, event, doSelect = true) {
    // CheckColumn events are handled by the CheckColumn itself.
    if (me.columns.getById(toCellSelector.columnId) === me.checkboxColumn || me.selectionMode.rowCheckboxSelection) {
      return;
    } // 1.do not affect selection if focus is returning to the grid from some widget
    // 2. don't select when clicking expander icon in a tree

    if (!doSelect || me.returningFocus || event && event.target.closest('.b-tree-expander')) {
      return;
    }

    const isSameRecord = fromCellSelector && toCellSelector.id === fromCellSelector.id,
          isMouse = event && event.type === 'mousedown',
          isMouseCtrl = isMouse && event.ctrlKey; // SHIFT for keyboard / mouse and CTRL for mouse events indicate multiselect

    if (event && (!event.button || event.button === 2) && (event.shiftKey || isMouseCtrl)) {
      me.handleMouseMultiSelect(toCellSelector, event);
    } else {
      const clickedSameRecordWithModifierKey = isSameRecord && (!event || event.shiftKey || event.ctrlKey),
            clickedAlreadySelectedRecord = event && me.isSelected(toCellSelector.id),
            clickedWithModifierKey = isMouseCtrl; // We intentionally do not clear existing selection here if clicking a selected record,
      // it's done in onCellClick where we know current interaction is not a drag drop operation

      me.selectCell(toCellSelector, false, clickedSameRecordWithModifierKey || clickedWithModifierKey || clickedAlreadySelectedRecord);
    } // Remember last cell with ctrl pressed

    if (!me.startCell || isMouseCtrl) {
      me.startCell = toCellSelector;
      me.lastRange = null;
    }
  }
  /**
   * Keeps the UI synced with the selectionChange event before firing it out.
   * Event is not fired if the `silent` parameter is truthy.
   * @param {Object} selectionChangeEvent The change event to sync the UI to, and to possibly fire.
   * @param {Boolean} silent Specify `true` to not trigger any the passed.
   * @private
   * @category Selection
   */

  triggerChangeEvent(selectionChangeEvent, silent) {
    const me = this,
          {
      mode,
      selected,
      deselected
    } = selectionChangeEvent;
    let i, len, row, cell; // Keep the UI up to date with the triggered changes.
    // A mode: 'row' change selects and/or deselects records.

    if (mode === 'row') {
      for (i = 0, len = selected.length; i < len; i++) {
        row = me.getRowFor(selected[i]);

        if (row) {
          row.addCls('b-selected');

          if (me.checkboxColumn && !me.checkboxColumn.hidden && !selected[i].isSpecialRow) {
            row.getCell(me.checkboxColumn.id).widget.checked = true;
          }
        }
      }

      for (i = 0, len = deselected.length; i < len; i++) {
        row = me.getRowFor(deselected[i]);

        if (row) {
          row.removeCls('b-selected');

          if (me.checkboxColumn && !me.checkboxColumn.hidden && !deselected[i].isSpecialRow) {
            row.getCell(me.checkboxColumn.id).widget.checked = false;
          }
        }
      }
    } // A mode: 'cell' change selects and/or deselects *one* cell right now.
    // But we always use an array for future-proofing.
    else if (mode === 'cell') {
        for (i = 0, len = selected.length; i < len; i++) {
          cell = me.getCell(selected[i]);

          if (cell) {
            cell.classList.add('b-selected');
          }
        }

        for (i = 0, len = deselected.length; i < len; i++) {
          cell = me.getCell(deselected[i]);

          if (cell) {
            cell.classList.remove('b-selected');
          }
        }
      }

    if (!silent) {
      me.trigger('selectionChange', selectionChangeEvent);
    }
  }

  onCellClick({
    event,
    record
  }) {
    const me = this;

    if (!me.selectionMode.checkbox && me.selectionMode.multiSelect && !event.shiftKey && !event.metaKey && !event.ctrlKey) {
      me.deselectRows(me.selectedRecords.filter(rec => rec !== record));
    }
  } //endregion
  //region Getters/setters
  // This does not need a className on Widgets.
  // Each *Class* which doesn't need 'b-' + constructor.name.toLowerCase() automatically adding
  // to the Widget it's mixed in to should implement thus.

  get widgetClass() {} //endregion

});

/**
 * @module Grid/view/mixin/GridState
 */

const suspendStoreEvents = subGrid => subGrid.columns.suspendEvents(),
      resumeStoreEvents = subGrid => subGrid.columns.resumeEvents(),
      fillSubGridColumns = subGrid => {
  subGrid.columns.clearCaches();
  subGrid.columns.fillFromMaster();
},
      compareStateSortIndex = (a, b) => a.stateSortIndex - b.stateSortIndex;
/**
 * Mixin for Grid that handles state. It serializes the following grid properties:
 *
 * * rowHeight
 * * readOnly
 * * selectedCell
 * * selectedRecords
 * * columns (order, widths, visibility)
 * * store (sorters, groupers, filters)
 * * scroll position
 *
 * See {@link Core.mixin.State} for more information on state.
 *
 * @demo Grid/state
 * @externalexample grid/State.js
 * @mixin
 */

var GridState = (Target => class GridState extends (Target || Base) {
  static get $name() {
    return 'GridState';
  }
  /**
   * Gets or sets grid's state. Check out {@link Grid.view.mixin.GridState GridState} mixin for details.
   * @member {Object} state
   */

  /**
   * Get grid's current state for serialization. State includes rowHeight, headerHeight, readOnly, selectedCell,
   * selectedRecordId, column states and store state etc.
   * @returns {Object} State object to be serialized
   * @private
   */

  getState() {
    const me = this,
          style = me.element.style.cssText,
          state = {
      rowHeight: me.rowHeight,
      readOnly: me.readOnly
    };

    if (style) {
      state.style = style;
    }

    if (me.selectedCell) {
      // TODO: Create wrapper class to avoid JSON.stringify recursion in state.selectedCell.
      const {
        id,
        columnId
      } = me.selectedCell;
      state.selectedCell = {
        id,
        columnId
      };
    }

    state.selectedRecords = me.selectedRecords.map(entry => entry.id);
    state.columns = me.columns.map(column => column.getState());
    state.store = me.store.state;
    state.scroll = me.storeScroll();
    state.subGrids = {};
    me.eachSubGrid(subGrid => {
      var _subGrid$collapsed;

      const config = state.subGrids[subGrid.region] = state.subGrids[subGrid.region] || {};

      if (subGrid.flex == null) {
        config.width = subGrid.width;
      }

      config.collapsed = (_subGrid$collapsed = subGrid.collapsed) !== null && _subGrid$collapsed !== void 0 ? _subGrid$collapsed : false;
    });
    return state;
  }
  /**
   * Apply previously stored state.
   * @param {Object} state
   * @private
   */

  applyState(state) {
    const me = this; // Do this first since it might perform full rendering of contents, recreating filterbar header fields

    if ('columns' in state) {
      let columnsChanged = false,
          needSort = false; // We're going to renderContents anyway, so stop the ColumnStores from updating the UI

      me.columns.suspendEvents();
      me.eachSubGrid(suspendStoreEvents); // each column triggers rerender at least once...

      state.columns.forEach((columnState, index) => {
        const column = me.columns.getById(columnState.id);

        if (column) {
          const columnGeneration = column.generation;
          column.applyState(columnState);
          columnsChanged = columnsChanged || column.generation !== columnGeneration; // In case a sort is needed, stamp in the ordinal position.

          column.stateSortIndex = index; // If we find one out of order, only then do we need to sort

          if (column.allIndex !== index) {
            needSort = columnsChanged = true;
          }
        }
      });

      if (columnsChanged) {
        me.eachSubGrid(fillSubGridColumns);
      }

      if (needSort) {
        me.eachSubGrid(subGrid => {
          subGrid.columns.records.sort(compareStateSortIndex);
          subGrid.columns.allRecords.sort(compareStateSortIndex);
        });
        me.columns.sort({
          fn: compareStateSortIndex,
          // always sort ascending
          ascending: true
        });
      } // If we have been painted, and column restoration changed the column layout, refresh contents

      if (me.isPainted && columnsChanged) {
        me.renderContents();
      } // Allow ColumnStores to update the UI again

      me.columns.resumeEvents();
      me.eachSubGrid(resumeStoreEvents);
    }

    if ('subGrids' in state) {
      me.eachSubGrid(subGrid => {
        if (subGrid.region in state.subGrids) {
          const subGridState = state.subGrids[subGrid.region];

          if ('width' in subGridState) {
            subGrid.width = subGridState.width;
          }

          if ('collapsed' in subGridState) {
            subGrid.collapsed = subGridState.collapsed;
          }
        }
      });
    }

    if ('readOnly' in state) {
      me.readOnly = state.readOnly;
    }

    if ('rowHeight' in state) {
      me.rowHeight = state.rowHeight;
    }

    if ('style' in state) {
      me.style = state.style;
    }

    if ('selectedCell' in state) {
      me.selectedCell = state.selectedCell;
    }

    if ('store' in state) {
      me.store.state = state.store;
    }

    if ('selectedRecords' in state) {
      me.selectedRecords = state.selectedRecords;
    }

    if ('scroll' in state) {
      me.restoreScroll(state.scroll);
    }
  } // This does not need a className on Widgets.
  // Each *Class* which doesn't need 'b-' + constructor.name.toLowerCase() automatically adding
  // to the Widget it's mixed in to should implement thus.

  get widgetClass() {}

});

/**
 * @module Grid/util/SubGridScroller
 */
const immediatePromise$5 = Promise.resolve(),
      defaultScrollOptions$1 = {
  block: 'nearest'
};
/**
 * A Scroller subclass which handles scrolling in a SubGrid. Needs special treatment since the SubGrid itself only
 * allows horizontal scrolling, while the vertical scrolling is done by an outer element containing all subgrids.
 *
 * @internal
 */

class SubGridScroller extends Scroller {
  scrollIntoView(element, options = defaultScrollOptions$1) {
    const me = this,
          {
      xDelta,
      yDelta
    } = me.getDeltaTo(element, options),
          result = xDelta || yDelta ? me.scrollBy(xDelta, yDelta, options) : immediatePromise$5;

    if (options.highlight || options.focus) {
      result.then(() => {
        if (options.highlight) {
          if (element instanceof Rectangle) {
            element.translate(-xDelta, -yDelta).highlight();
          } else {
            DomHelper.highlight(element);
          }
        }

        options.focus && element.focus && element.focus();
      });
    }

    return result;
  }

  scrollBy(xDelta, yDelta, options) {
    const yPromise = yDelta && this.yScroller.scrollBy(0, yDelta, options),
          xPromise = xDelta && super.scrollBy(xDelta, 0, options);

    if (xPromise && xPromise.cancel && yPromise && yPromise.cancel) {
      const cancelX = xPromise.cancel,
            cancelY = yPromise.cancel; // Set up cross canceling

      xPromise.cancel = yPromise.cancel = () => {
        cancelX();
        cancelY();
      };

      return Promise.all([xPromise, yPromise]);
    }

    return xPromise || yPromise || immediatePromise$5;
  }

  scrollTo(toX, toY, options) {
    const yPromise = toY != null && this.yScroller.scrollTo(null, toY, options),
          xPromise = toX != null && super.scrollTo(toX, null, options); // Keep parters in sync immediately unless we are going to animate our position.
    // There are potentially three: The header, the footer and the docked fake horizontal scroller.
    // It will be more efficient and maintain correct state doing it now.

    if (!(options && options.animate)) {
      this.syncPartners();
    }

    if (xPromise && xPromise.cancel && yPromise && yPromise.cancel) {
      const cancelX = xPromise.cancel,
            cancelY = yPromise.cancel; // Set up cross canceling

      xPromise.cancel = yPromise.cancel = () => {
        cancelX();
        cancelY();
      };

      return Promise.all([xPromise, yPromise]);
    }

    return xPromise || yPromise || immediatePromise$5;
  }

  get viewport() {
    const elementBounds = Rectangle.from(this.element),
          viewport = elementBounds.intersect(Rectangle.from(this.yScroller.element)); // For 0 height subgrids, viewport will be `false` but we still expect a Rectangle to be returned

    return viewport || new Rectangle(elementBounds.x, elementBounds.y, elementBounds.width, 0);
  }

  set y(y) {
    if (this.yScroller) {
      this.yScroller.y = y;
    }
  }

  get y() {
    return this.yScroller ? this.yScroller.y : 0;
  }

  get maxY() {
    return this.yScroller ? this.yScroller.maxY : 0;
  }

  get scrollHeight() {
    return this.yScroller ? this.yScroller.scrollHeight : 0;
  }

  get clientHeight() {
    return this.yScroller ? this.yScroller.clientHeight : 0;
  }

}
SubGridScroller._$name = 'SubGridScroller';

/**
 * @module Grid/view/SubGrid
 */

/**
 * A SubGrid is a part of the grid (it has at least one and normally no more than two, called locked and normal). It
 * has its own header, which holds the columns to display rows for in the SubGrid. SubGrids are created by Grid, you
 * should not need to create instances directly.
 *
 * If not configured with a width or flex, the SubGrid will be sized to fit its columns. In this case, if all columns
 * have a fixed width (not using flex) then toggling columns will also affect the width of the SubGrid.
 *
 * @extends Core/widget/Widget
 */

class SubGrid extends Widget {
  //region Config
  static get $name() {
    return 'SubGrid';
  } // Factoryable type name

  static get type() {
    return 'subgrid';
  }
  /**
   * Region (name) for this SubGrid
   * @config {String} region
   */

  /**
   * Column store, a store containing the columns for this SubGrid
   * @config {Grid.data.ColumnStore} columns
   */

  static get defaultConfig() {
    return {
      localizableProperties: ['emptyText'],
      insertRowsBefore: null,
      appendTo: null,
      monitorResize: true,
      headerClass: null,
      footerClass: null,

      /**
       * Set `true` to start subgrid collapsed. To operate collapsed state on subgrid use
       * {@link #function-collapse}/{@link #function-expand} methods.
       * @config {Boolean}
       * @default false
       */
      collapsed: null,
      scrollable: {
        // Each SubGrid only handles scrolling in the X axis.
        // The owning Grid handles the Y axis.
        overflowX: true
      },
      scrollerClass: SubGridScroller,
      // Will be set to true by GridSubGrids if it calculates the subgrids width based on its columns.
      // Used to determine if hiding a column should affect subgrids width
      hasCalculatedWidth: null,

      /**
       * Set `true` to disable moving columns into or out of this SubGrid.
       * @config {Boolean}
       * @default false
       * @private
       */
      sealedColumns: null,
      emptyText: null
    };
  }

  static get configurable() {
    return {
      element: true,
      header: {},
      footer: {},
      virtualScrollerElement: true,
      splitterElement: true,
      headerSplitter: true,
      scrollerSplitter: true,
      footerSplitter: true
    };
  } //endregion
  //region Init

  /**
   * SubGrid constructor
   * @param config
   * @private
   */

  construct(config) {
    super.construct(config);
    this.rowManager.on('addrows', this.onAddRow, this);
  }

  doDestroy() {
    var _me$fakeScroller;

    const me = this;
    me.header.destroy();
    me.footer.destroy();
    (_me$fakeScroller = me.fakeScroller) === null || _me$fakeScroller === void 0 ? void 0 : _me$fakeScroller.destroy();
    me.virtualScrollerElement.remove();
    me.splitterElement.remove();
    me.headerSplitter.remove();
    me.scrollerSplitter.remove();
    me.footerSplitter.remove();
    super.doDestroy();
  }

  get barConfig() {
    const me = this,
          {
      width,
      flex
    } = me.element.style,
          config = {
      subGrid: me,
      parent: me // Contained widgets need to know their parents

    }; // If we have been configured with sizing, construct the Bar in sync.

    if (flex) {
      config.flex = flex;
    } else if (width) {
      config.width = width;
    }

    return config;
  }

  changeHeader(header) {
    return new this.headerClass(Objects.assign({}, header, this.barConfig));
  }

  changeFooter(footer) {
    return new this.footerClass(Objects.assign({}, footer, this.barConfig));
  } //endregion
  //region Splitters

  /**
   * Toggle (add/remove) class for splitters
   * @param {String} cls class name
   * @param {Boolean} [add] actions. Set to `true` to add class, `false` to remove
   * @private
   */

  toggleSplitterCls(cls, add = true) {
    const me = this,
          splitters = [me.splitterElement, me.headerSplitter, me.footerSplitter, me.scrollerSplitter];
    splitters.forEach(el => el === null || el === void 0 ? void 0 : el.classList[add ? 'add' : 'remove'](cls));
  } //endregion
  //region Template

  changeElement(element) {
    const {
      region
    } = this;
    super.changeElement({
      className: {
        'b-grid-subgrid': 1,
        [`b-grid-subgrid-${region}`]: 1,
        'b-grid-horizontal-scroller': 1,
        'b-grid-subgrid-collapsed': this.collapsed
      },
      dataset: {
        region
      }
    });
  }

  get rowElementConfig() {
    return {
      className: 'b-grid-row',
      children: this.columns.visibleColumns.map(column => ({
        className: 'b-grid-cell',
        dataset: {
          column: column.field || '',
          columnId: column.id
        }
      }))
    };
  } // Added to DOM in  Grid `get bodyConfig`

  changeVirtualScrollerElement() {
    const references = DomHelper.createElement({
      reference: 'virtualScrollerElement',
      className: 'b-virtual-scroller',
      dataset: {
        region: this.region
      },
      children: [{
        reference: 'virtualScrollerWidth',
        className: 'b-virtual-width'
      }]
    });
    this.virtualScrollerWidth = references.virtualScrollerWidth;
    return references.virtualScrollerElement;
  }

  changeSplitterElement() {
    const references = DomHelper.createElement({
      reference: 'splitterElement',
      className: {
        'b-grid-splitter': 1,
        'b-grid-splitter-collapsed': this.collapsed,
        'b-hide-display': 1 // GridSubGrids determines visibility

      },
      dataset: {
        region: this.region
      },
      children: [{
        className: 'b-grid-splitter-inner b-grid-splitter-main',
        children: [{
          className: 'b-grid-splitter-buttons',
          reference: 'splitterButtons',
          children: [{
            tag: 'i',
            className: 'b-icon b-icon-collapse-gridregion'
          }, {
            tag: 'i',
            className: 'b-icon b-icon-expand-gridregion'
          }]
        }]
      }]
    });
    this.splitterButtons = references.splitterButtons;
    return references.splitterElement;
  }

  get splitterConfig() {
    return {
      className: this.splitterElement.className.trim(),
      children: [{
        className: 'b-grid-splitter-inner'
      }],
      dataset: {
        region: this.region
      }
    };
  }

  changeHeaderSplitter() {
    return DomHelper.createElement(this.splitterConfig);
  }

  changeScrollerSplitter() {
    return DomHelper.createElement(this.splitterConfig);
  }

  changeFooterSplitter() {
    return DomHelper.createElement(this.splitterConfig);
  }

  hideSplitter() {
    this.splitterElement.classList.add('b-hide-display');
    this.headerSplitter.classList.add('b-hide-display');
    this.scrollerSplitter.classList.add('b-hide-display');
    this.footerSplitter.classList.add('b-hide-display');
    this.$showingSplitter = false;
  }

  showSplitter() {
    this.splitterElement.classList.remove('b-hide-display');
    this.headerSplitter.classList.remove('b-hide-display');
    this.scrollerSplitter.classList.remove('b-hide-display');
    this.footerSplitter.classList.remove('b-hide-display');
    this.$showingSplitter = true;
  } //endregion
  //region Render

  render(...args) {
    const me = this;
    super.render(...args); // Unit tests create naked SubGrids so we have to do this.

    if (me.grid) {
      me.updateHasFlex();
      me.element.parentNode.insertBefore(me.splitterElement, me.element.nextElementSibling);
      EventHelper.on({
        element: me.grid.element,
        delegate: `.b-grid-splitter[data-region=${me.region}]`,
        mouseover: 'onSplitterMouseOver',
        mouseout: 'onSplitterMouseOut',
        thisObj: me
      });
      me._collapsed && me.collapse();
    }
  }

  refreshHeader() {
    this.header.refreshContent();
  }

  refreshFooter() {
    var _this$footer;

    (_this$footer = this.footer) === null || _this$footer === void 0 ? void 0 : _this$footer.refreshContent();
  } // Override to iterate header and footer.

  eachWidget(fn, deep = true) {
    const me = this,
          widgets = [me.header, me.footer];

    for (let i = 0; i < widgets.length; i++) {
      const widget = widgets[i];

      if (fn(widget) === false) {
        return;
      }

      if (deep && widget.eachWidget) {
        widget.eachWidget(fn, deep);
      }
    }
  } //endregion
  //region Size & resize

  /**
   * Sets cell widths. Cannot be done in template because of CSP
   * @private
   */

  fixCellWidths(rowElement, visibleColumns = null) {
    if (!visibleColumns) visibleColumns = this.columns.bottomColumns.filter(col => !col.hidden); // fix cell widths, no longer allowed in template because of CSP

    let cell = rowElement.firstElementChild,
        i = 0;

    while (cell) {
      const column = visibleColumns[i];

      if (column.minWidth) {
        cell.style.minWidth = DomHelper.setLength(column.minWidth);
      } // either flex or width, flex has precedence

      if (column.flex) {
        cell.style.flex = column.flex;
        cell.style.width = '';
      } else if (column.width) {
        // https://app.assembla.com/spaces/bryntum/tickets/8041
        // Although header and footer elements must be sized using flex-basis to avoid the busting out problem,
        // grid cells MUST be sized using width since rows are absolutely positioned and will not cause the
        // busting out problem, and rows will not stretch to shrinkwrap the cells unless they are widthed with
        // width.
        cell.style.width = DomHelper.setLength(column.width); // IE11 calculates flexbox container width based on min-width rather than actual width. When column
        // has width defined greater than minWidth, row may have incorrect width

        if (BrowserHelper.isIE11) {
          cell.style.flex = '';
          cell.style.minWidth = DomHelper.setLength(column.calcMinWidth);
        }
      } else {
        cell.style.flex = cell.style.width = cell.style.minWidth = '';
      }

      cell = cell.nextElementSibling;
      i++;
    }
  }

  get totalFixedWidth() {
    return this.columns.totalFixedWidth;
  }
  /**
   * Sets header width and scroller width (if needed, depending on if using flex). Might also change the subgrids
   * width, if it uses a width calculated from its columns.
   * @private
   */

  fixWidths() {
    const me = this,
          {
      element,
      header,
      footer
    } = me;

    if (!me.collapsed) {
      if (me.flex) {
        header.flex = me.flex;

        if (footer) {
          footer.flex = me.flex;
        }

        element.style.flex = me.flex;
      } else {
        // If width is calculated and no column is using flex, check if total width is less than width. If so,
        // recalculate width and bail out of further processing (since setting width will trigger again)
        if (me.hasCalculatedWidth && !me.columns.some(col => !col.hidden && col.flex) && me.totalFixedWidth !== me.width) {
          me.width = me.totalFixedWidth; // Setting width above clears the hasCalculatedWidth flag, but we want to keep it set to react
          // correctly next time

          me.hasCalculatedWidth = true;
          return;
        }

        let totalWidth = me.width;

        if (!totalWidth) {
          totalWidth = 0; // summarize column widths, needed as container width when not using flex widths and for correct
          // overflow check in Edge

          for (const col of me.columns) {
            if (!col.flex && !col.hidden) totalWidth += col.width;
          }
        } // rows are absolutely positioned, meaning that their width won't affect container width
        // hence we must set it, if not using flex

        element.style.width = `${totalWidth}px`;
        header.width = totalWidth;

        if (footer) {
          footer.width = totalWidth;
        }
      }

      me.syncScrollingPartners(false);
    }
  } // Safari/Edge do not shrink cells the same way as chrome & ff does without having a width set on the row

  fixRowWidthsInSafariEdge() {
    if (BrowserHelper.isSafari || BrowserHelper.isEdge) {
      const me = this,
            {
        region,
        header
      } = me,
            minWidth = header.calculateMinWidthForSafari(); // fix row widths for safari, it does not size flex cells correctly at small widths otherwise.
      // there should be a css solution, but I have failed to find it

      me.rowManager.forEach(row => {
        // This function runs on resize and rendering a SubGrid triggers a resize. When adding a new SubGrid
        // on the fly elements wont exists for it yet, so ignore...
        const element = row.getElement(region); // it is worth noting that setting a width does not prevent the row from growing beyond that with
        // when making view wider, it is used in flex calculation more like a min-width

        if (element) {
          element.style.width = `${minWidth}px`;
        }
      });
      header.headersElement.style.width = `${minWidth}px`;
    }
  }
  /**
   * Get/set SubGrid width, which also sets header and footer width (if available).
   * @property {Number}
   */

  set width(width) {
    const me = this; // Width explicitly set, remember that

    me.hasCalculatedWidth = false;
    super.width = width;
    me.header.width = width;
    me.footer.width = width; // When we're live, we can't wait until the  throttled resize occurs - it looks bad.

    if (me.isPainted) {
      me.onElementResize();
    }
  }

  get width() {
    return super.width;
  }
  /**
   * Get/set SubGrid flex, which also sets header and footer flex (if available).
   * @property {Number|String}
   */

  set flex(flex) {
    const me = this; // Width explicitly set, remember that

    me.hasCalculatedWidth = false;
    me.header.flex = flex;
    me.footer.flex = flex;
    super.flex = flex; // When we're live, we can't wait until the  throttled resize occurs - it looks bad.

    if (me.isPainted) {
      me.onElementResize();
    }
  }

  get flex() {
    return super.flex;
  }
  /**
   * Called when grid changes size. SubGrid determines if it has changed size and triggers scroll (for virtual
   * rendering in cells to work when resizing etc.)
   * @private
   */

  onInternalResize(element, newWidth, newHeight, oldWidth, oldHeight) {
    const me = this,
          {
      grid
    } = me; // Widget caches dimensions

    super.onInternalResize(element, newWidth, newHeight, oldWidth, oldHeight); // Unit tests create naked SubGrids so we have to do this.

    if (grid) {
      me.syncSplitterButtonPosition();

      if (newWidth !== oldWidth) {
        // trigger scroll, in case anything is done on scroll it needs to be done now also
        grid.trigger('horizontalScroll', {
          subGrid: me,
          grid,
          scrollLeft: me.scrollable.x
        });
        me.refreshFakeScroll();
        me.fixRowWidthsInSafariEdge();
      }

      me.trigger('afterInternalResize', me);
    }
  }
  /**
   * Keeps the parallel splitters in the header, footer and fake scroller synced in terms
   * of being collapsed or not.
   * @private
   */

  syncParallelSplitters(collapsed) {
    const me = this,
          {
      grid
    } = me;

    if (me.splitterElement && me.$showingSplitter) {
      me.toggleSplitterCls('b-grid-splitter-collapsed', collapsed);
    } else {
      // If we're the last, we don't own a splitter, we use the previous region's splitter
      const prevGrid = grid.getSubGrid(grid.getPreviousRegion(me.region)); // If there's a splitter before us, sync it with our state.

      if (prevGrid && prevGrid.splitterElement) {
        prevGrid.syncParallelSplitters(collapsed);
      }
    }
  }

  onSplitterMouseOver() {
    const me = this,
          {
      nextSibling
    } = me; // No hover effect when collapsed

    if (!me.collapsed && (!nextSibling || !nextSibling.collapsed)) {
      me.toggleSplitterCls('b-hover');
    }

    me.startSplitterButtonSyncing();
  }

  onSplitterMouseOut() {
    const me = this,
          {
      nextSibling
    } = me;
    me.toggleSplitterCls('b-hover', false);

    if (!me.collapsed && (!nextSibling || !nextSibling.collapsed)) {
      me.stopSplitterButtonSyncing();
    }
  }

  startSplitterButtonSyncing() {
    const me = this;

    if (me.splitterElement) {
      me.syncSplitterButtonPosition();

      if (!me.splitterSyncScrollListener) {
        me.splitterSyncScrollListener = me.grid.scrollable.on({
          scroll: 'syncSplitterButtonPosition',
          thisObj: me
        });
      }
    }
  }

  stopSplitterButtonSyncing() {
    if (this.splitterSyncScrollListener) {
      this.splitterSyncScrollListener();
      this.splitterSyncScrollListener = null;
    }
  }

  syncSplitterButtonPosition() {
    const me = this;
    me.splitterButtons.style.transform = `translateY(${me.grid.scrollable.y + me.grid.bodyHeight / 2 - (me.headerSplitter ? me.grid.headerHeight : 0)}px)`;
  }
  /**
   * Get the "viewport" for the SubGrid as a Rectangle
   * @property {Core.helper.util.Rectangle}
   * @readonly
   */

  get viewRectangle() {
    const me = this;
    return new Rectangle(me.scrollable.x, me.scrollable.y, me._width || 0, me.rowManager.viewHeight);
  }
  /**
   * Called when updating column widths to apply 'b-has-flex' which is used when fillLastColumn is configured.
   * @internal
   */

  updateHasFlex() {
    const hasFlex = this.columns.visibleColumns.some(column => column.flex);
    DomHelper.toggleClasses(this.element, ['b-has-flex'], hasFlex);
  }
  /**
   * Resize all columns in the SubGrid to fit their width, according to their configured
   * {@link Grid.column.Column#config-fitMode}
   */

  resizeColumnsToFitContent() {
    this.grid.beginGridMeasuring();
    this.columns.bottomColumns.forEach(column => {
      column.resizeToFitContent(null, null, true);
    });
    this.grid.endGridMeasuring();
  } //endregion
  //region Scroll

  get overflowingHorizontally() {
    const {
      scrollable
    } = this; // This is if *this* subGrid has horizontal overflow
    // +1 is for Edge, it messes up otherwise

    return scrollable.scrollWidth > scrollable.clientWidth + (BrowserHelper.isEdge ? 1 : 0);
  }
  /**
   * Fixes widths of fake scrollers
   * @private
   */

  refreshFakeScroll() {
    const me = this,
          {
      element,
      virtualScrollerElement,
      virtualScrollerWidth,
      totalFixedWidth,
      store,
      header,
      footer,
      scrollable
    } = me; // Use a fixed scroll width if grid is empty, to make it scrollable without rows
    // https://app.assembla.com/spaces/bryntum/tickets/7184

    scrollable.scrollWidth = store !== null && store !== void 0 && store.count ? null : totalFixedWidth; // Scroller lays out in the same way as subgrid.
    // If we are flexed, the scroller is flexed etc.

    virtualScrollerElement.style.width = element.style.width;
    virtualScrollerElement.style.flex = element.style.flex;

    if (!me.collapsed) {
      if (me.overflowingHorizontally) {
        virtualScrollerWidth.style.width = `${(scrollable === null || scrollable === void 0 ? void 0 : scrollable.scrollWidth) || 0}px`;
        header.element.classList.add('b-overflowing');
        footer.element.classList.add('b-overflowing'); // If *any* SubGrids have horizontal overflow, the main grid
        // has to show its virtual horizontal scollbar.

        me.grid.virtualScrollers.classList.remove('b-hide-display');
      } else {
        virtualScrollerWidth.style.width = 0;
        header.element.classList.remove('b-overflowing');
        footer.element.classList.remove('b-overflowing');
      }
    }
  }
  /**
   * Init scroll syncing for header and footer (if available).
   * @private
   */

  initScroll() {
    const me = this,
          {
      scrollable,
      virtualScrollerElement
    } = me;
    me.syncPartnersOnFrame = me.createOnFrame(me.syncScrollingPartners);

    if (BrowserHelper.isFirefox) {
      scrollable.element.addEventListener('wheel', event => {
        if (event.deltaX) {
          scrollable.x += event.deltaX;
          event.preventDefault();
        }
      });
    }

    scrollable.yScroller = me.grid.scrollable; // Add our Scroller to the controlling GridScroller

    scrollable.yScroller.addScroller(scrollable); // Create a Scroller for the fake horizontal scrollbar so that it can partner

    me.fakeScroller = new Scroller({
      element: virtualScrollerElement,
      overflowX: true
    }); // Sync scrolling partners (header, footer) when our xScroller reports a scroll.
    // Also fires horizontalscroll

    scrollable.on({
      scroll: 'onSubGridScroll',
      scrollend: 'onSubGridScrollEnd',
      thisObj: me
    });
    scrollable.addPartner(me.fakeScroller, 'x');
    scrollable.addPartner(me.header.scrollable, 'x');
    scrollable.addPartner(me.footer.scrollable, 'x');
  }

  onSubGridScrollEnd() {
    // If we do not have the direct update flag set which would ensure a sync in each scroll event
    // then ensure syncing happens on scroll end. This is for animated scrolls where the scroll
    // impulses come through animation frames.
    if (!this.forceScrollUpdate) {
      this.syncScrollingPartners();
    }

    this.scrolling = false;
  }

  onSubGridScroll() {
    // Force direct update, without waiting for next animation frame
    // TODO: Only used in Scheduler, could perhaps live in Scheduler specific SubGrid in the future
    if (this.forceScrollUpdate) {
      this.syncScrollingPartners();
      this.forceScrollUpdate = false;
    } else {
      this.syncPartnersOnFrame();
    }
  }

  set scrolling(scrolling) {
    this._scrolling = scrolling;
  }

  get scrolling() {
    return this._scrolling;
  }
  /**
   * This syncs the horizontal scroll position of the header and the footer with
   * the horizontal scroll position of the grid. Usually, this will be called automatically
   * when the grid scrolls. In some cases, such as a refresh caused by column changes
   * it will need to be called from elsewhere.
   * @internal
   */

  syncScrollingPartners(addCls = true) {
    const subGrid = this,
          {
      grid
    } = subGrid;

    if (!subGrid.scrolling && addCls) {
      subGrid.scrolling = true;
    }

    grid.trigger('horizontalScroll', {
      subGrid,
      grid,
      scrollLeft: subGrid.scrollable.x
    });
  }
  /**
   * Scrolls a column into view (if it is not already). Called by Grid#scrollColumnIntoView, use it instead to not
   * have to care about which SubGrid contains a column.
   * @param {Grid.column.Column|String|Number} column Column name (data) or column index or actual column object.
   * @param {Object} [options] How to scroll.
   * @param {String} [options.block] How far to scroll the element: `start/end/center/nearest`.
   * @param {Number} [options.edgeOffset] edgeOffset A margin around the element or rectangle to bring into view.
   * @param {Object|Boolean|Number} [options.animate] Set to `true` to animate the scroll by 300ms,
   * or the number of milliseconds to animate over, or an animation config object.
   * @param {Number} [options.animate.duration] The number of milliseconds to animate over.
   * @param {String} [options.animate.easing] The name of an easing function.
   * @param {Boolean} [options.highlight] Set to `true` to highlight the element when it is in view.
   * @param {Boolean} [options.focus] Set to `true` to focus the element when it is in view.
   * @returns {Promise} If the column exists, a promise which is resolved when the column header element has been scrolled into view.
   */

  scrollColumnIntoView(column, options) {
    const me = this,
          scroller = me.header.scrollable; // Allow column,column id,or column index to be passed

    column = column instanceof Column ? column : me.columns.get(column) || me.columns.getById(column) || me.columns.getAt(column);

    if (column) {
      // Get the current column header element.
      const columnHeaderElement = me.header.getHeader(column.id);

      if (columnHeaderElement) {
        return scroller.scrollIntoView(Rectangle.from(columnHeaderElement, null, true), options);
      }
    }
  } //endregion
  //region Rows

  /**
   * Creates elements for the new rows when RowManager has determined that more rows are needed
   * @private
   */

  onAddRow({
    rows
  }) {
    const me = this,
          config = me.rowElementConfig,
          visibleColumns = me.columns.bottomColumns.filter(col => !col.hidden),
          frag = document.createDocumentFragment();
    rows.forEach(row => {
      const rowElement = DomHelper.createElement(config);
      frag.appendChild(rowElement);
      row.addElement(me.region, rowElement); // TODO: Stamp the correct width into the cells on creation

      me.fixCellWidths(rowElement, visibleColumns);
    });
    me.fixRowWidthsInSafariEdge(); // Put the row elements into the SubGrid en masse.
    // If 2nd param is null, insertBefore appends.

    me.element.insertBefore(frag, me.insertRowsBefore);
  }
  /**
   * Get all row elements for this SubGrid.
   * @property {HTMLElement[]}
   * @readonly
   */

  get rowElements() {
    return this.fromCache('.b-grid-row', true);
  }
  /**
   * Removes all row elements from the subgrids body and empties cache
   * @private
   */

  clearRows() {
    this.emptyCache();
    const all = this.element.querySelectorAll('.b-grid-row'),
          range = document.createRange();

    if (all.length) {
      range.setStartBefore(all[0]);
      range.setEndAfter(all[all.length - 1]);
      range.deleteContents();
    }
  } // only called when RowManager.rowScrollMode = 'dom', which is not intended to be used

  addNewRowElement() {
    const rowElement = DomHelper.append(this.element, this.rowElementConfig);
    this.fixCellWidths(rowElement);
    return rowElement;
  }

  get emptyText() {
    return this._emptyText;
  }

  set emptyText(text) {
    this._emptyText = text;
    this.element.dataset.emptyText = text;
  } //endregion
  // region Expand/collapse
  // All usages are commented, uncomment when this is resolved: https://app.assembla.com/spaces/bryntum/tickets/5472

  toggleTransitionClasses(doRemove = false) {
    const me = this,
          grid = me.grid,
          nextRegion = grid.getSubGrid(grid.getNextRegion(me.region)),
          splitter = grid.resolveSplitter(nextRegion);
    nextRegion.element.classList[doRemove ? 'remove' : 'add']('b-grid-subgrid-animate-collapse');
    nextRegion.header.element.classList[doRemove ? 'remove' : 'add']('b-grid-subgrid-animate-collapse');
    me.element.classList[doRemove ? 'remove' : 'add']('b-grid-subgrid-animate-collapse');
    me.header.element.classList[doRemove ? 'remove' : 'add']('b-grid-subgrid-animate-collapse');
    splitter.classList[doRemove ? 'remove' : 'add']('b-grid-splitter-animate');
  }
  /**
   * Get/set collapsed state
   * @property {Boolean}
   */

  get collapsed() {
    return this._collapsed;
  }

  set collapsed(collapsed) {
    if (this.isConfiguring) {
      this._collapsed = collapsed;
    } else {
      if (collapsed) {
        this.collapse();
      } else {
        this.expand();
      }
    }
  }
  /**
   * Collapses subgrid. If collapsing subgrid is the only one expanded, next subgrid to the right (or previous) will
   * be expanded.
   *
   * @example
   * let locked = grid.getSubGrid('locked');
   * locked.collapse().then(() => {
   *     console.log(locked.collapsed); // Logs 'True'
   * });
   *
   * let normal = grid.getSubGrid('normal');
   * normal.collapse().then(() => {
   *     console.log(locked.collapsed); // Logs 'False'
   *     console.log(normal.collapsed); // Logs 'True'
   * });
   *
   * @async
   * @returns {Promise} A Promise which resolves when this SubGrid is fully collapsed.
   */

  async collapse() {
    const me = this,
          grid = me.grid,
          nextRegion = grid.getSubGrid(grid.getNextRegion(me.region)),
          splitterOwner = me.splitterElement ? me : me.previousSibling; // Count all expanded regions. Grid must have always have at least one expanded

    let expandedRegions = 0;
    grid.eachSubGrid(subGrid => {
      subGrid !== me && !subGrid._collapsed && ++expandedRegions;
    }); // Current region is the only one expanded, expand next region

    if (expandedRegions === 0) {
      // expandPromise = nextRegion.expand();
      await nextRegion.expand();
    }

    return new Promise(resolve => {
      if (!me._beforeCollapseState) {
        me._beforeCollapseState = {};
        let widthChanged = false; // If current width is zero, the resize event will not be fired. In such case we want to trigger callback immediately

        if (me.width) {
          widthChanged = true; // Toggle transition classes here, we will actually change width below
          // me.toggleTransitionClasses();
          // afterinternalresize event is buffered, it will be fired only once after animation is finished
          // and element size is final

          me.on({
            afterinternalresize: () => {
              // me.toggleTransitionClasses(true);
              resolve(me);
            },
            thisObj: me,
            once: true
          });
        } // When trying to collapse region we need its partner to occupy free space. Considering multiple
        // regions, several cases are possible:
        // 1) Both left and right regions have fixed width
        // 2) Left region has fixed width, right region is flexed
        // 3) Left region is flexed, right region has fixed width
        // 4) Both regions are flexed
        //
        // To collapse flexed region we need to remove flex style, remember it somehow and set fixed width.
        // If another region is flexed, it will fill the space. If it has fixed width, we need to increase
        // its width by collapsing region width. Same logic should be appliead to headers.
        //
        // Save region width first

        me._beforeCollapseState.width = me.width;
        me._beforeCollapseState.elementWidth = me.element.style.width; // Next region is not flexed, need to make it fill the space

        if (nextRegion.element.style.flex === '') {
          me._beforeCollapseState.nextRegionWidth = nextRegion.width;
          nextRegion.width += me._beforeCollapseState.width;
        } // Current region is flexed, store style to restore on expand

        if (me.element.style.flex !== '') {
          me._beforeCollapseState.flex = me.element.style.flex; // remove flex state to reduce width later

          me.header.element.style.flex = me.element.style.flex = '';
        } // Sets the grid to its collapsed width as defined in SASS: zero

        me.element.classList.add('b-grid-subgrid-collapsed'); // The parallel elements which must be in sync width-wise must know about collapsing

        me.virtualScrollerElement.classList.add('b-collapsed');
        me.header.element.classList.add('b-collapsed');
        me.footer.element.classList.add('b-collapsed');
        me.width = '';
        me._collapsed = true;

        if (!widthChanged) {
          // sync splitters in case subGrid was collapsed by state (https://github.com/bryntum/support/issues/1857)
          me.syncParallelSplitters(true);
          resolve(false);
        }
      }
    }).then(value => {
      if (!me.isDestroyed && value !== false) {
        me.syncParallelSplitters(true);
        me.grid.trigger('subGridCollapse', me); // Our splitter is permanently visible when collapsed, so keep splitter button set
        // synced in the vertical centre of the view just in time for paint.
        // Uses translateY so will not cause a further layout.

        splitterOwner.startSplitterButtonSyncing && splitterOwner.startSplitterButtonSyncing();
      }
    });
  }
  /**
   * Expands subgrid.
   *
   * @example
   * grid.getSubGrid('locked').expand().then(() => console.log('locked grid expanded'));
   *
   * @async
   * @returns {Promise} A Promise which resolves when this SubGrid is fully expanded.
   */

  async expand() {
    const me = this,
          grid = me.grid,
          nextRegion = grid.getSubGrid(grid.getNextRegion(me.region)),
          splitterOwner = me.splitterElement ? me : me.previousSibling;
    return new Promise(resolve => {
      if (me._beforeCollapseState != null) {
        // If current width matches width expected after expand resize event will not be fired. In such case
        // we want to trigger callback immediately
        let widthChanged = false; // See similar clause in collapse method above

        if (me.width !== me._beforeCollapseState.elementWidth) {
          widthChanged = true; // Toggle transition classes here, we will actually change width below
          // me.toggleTransitionClasses();

          me.on({
            afterinternalresize: () => {
              // me.toggleTransitionClasses(true);
              // Delay the resolve to avoid "ResizeObserver loop limit exceeded" errors
              // collapsing the only expanded region and it has to expand its nextRegion
              // before it can collapse.
              me.setTimeout(() => resolve(me), 10);
            },
            thisObj: me,
            once: true
          });
        } // previous region is not flexed, reduce its width as it was increased in collapse

        if (nextRegion.element.style.flex === '') {
          nextRegion.width = me._beforeCollapseState.nextRegionWidth;
        }

        me.element.classList.remove('b-grid-subgrid-collapsed');
        me._collapsed = false; // The parallel elements which must be in sync width-wise must know about collapsing

        me.virtualScrollerElement.classList.remove('b-collapsed');
        me.header.element.classList.remove('b-collapsed');
        me.footer.element.classList.remove('b-collapsed'); // This region used to be flex, let's restore it

        if (me._beforeCollapseState.flex) {
          // Always restore width, restoring flex wont trigger resize otherwise
          me.width = me._beforeCollapseState.width; // Widget flex setting clears style width

          me.header.flex = me.flex = me._beforeCollapseState.flex;
          me.footer.flex = me._beforeCollapseState.flex;
          me._width = null;
        } else {
          me.width = me._beforeCollapseState.elementWidth;
        }

        me.element.classList.remove('b-grid-subgrid-collapsed');
        me._collapsed = false;

        if (!widthChanged) {
          resolve(false);
        }

        delete me._beforeCollapseState;
      }
    }).then(value => {
      if (value !== false) {
        // Our splitter is hidden when expanded, so we no longer need to keep splitter button set
        // synced in the vertical centre of the view.
        splitterOwner.stopSplitterButtonSyncing();
        me.syncParallelSplitters(false);
        me.grid.trigger('subGridExpand', me);
      }
    });
  } //endregion

} // Register this widget type with its Factory

SubGrid.initClass();
SubGrid._$name = 'SubGrid';

/**
 * @module Grid/view/mixin/GridSubGrids
 */

/**
 * Mixin for grid that handles SubGrids. Each SubGrid is scrollable horizontally separately from the other SubGrids.
 * Having two SubGrids allows you to achieve what is usually called locked or frozen columns.
 *
 * By default a Grid has two SubGrids, one named 'locked' and one 'normal'. The `locked` region has fixed width, while
 * the `normal` region grows to fill all available width (flex).
 *
 * Which SubGrid a column belongs to is determined using its {@link Grid.column.Column#config-region} config. For
 * example to put a column into the locked region, specify `{ region: 'locked' }`. For convenience, a column can be put
 * in the locked region using `{ locked: true }`.
 *
 * ```javascript
 * new Grid({
 *   columns : [
 *     // These two columns both end up in the "locked" region
 *     { field: 'name', text: 'Name', locked: true }
 *     { field: 'age', text: 'Age', region: 'locked' }
 *   ]
 * });
 * ```
 *
 * To customize the SubGrids, use {@link Grid.view.Grid#config-subGridConfigs}:
 *
 * ```javascript
 * // change the predefined subgrids
 * new Grid({
 *   subGridConfigs : {
 *       locked : { flex : 1 } ,
 *       normal : { flex : 3 }
 *   }
 * })
 *
 * // or define your own entirely
 * new Grid({
 *   subGridConfigs : {
 *       a : { width : 150 } ,
 *       b : { flex  : 1 },
 *       c : { width : 150 }
 *   },
 *
 *   columns : [
 *       { field : 'name', text : 'Name', region : 'a' },
 *       ...
 *   ]
 * })
 * ```
 *
 * @demo Grid/lockedcolumns
 * @mixin
 */

var GridSubGrids = (Target => class GridSubGrids extends (Target || Base) {
  static get $name() {
    return 'GridSubGrids';
  }

  static get properties() {
    return {
      /**
       * An object containing the {@link Grid.view.SubGrid} region instances, indexed by subGrid id ('locked', normal'...)
       * @property {Object} subGrids
       * @type {Object}
       * @readonly
       * @category Common
       */
      subGrids: {}
    };
  } //region Init

  changeSubGridConfigs(configs) {
    const me = this,
          usedRegions = new Set();

    for (const column of me.columns) {
      // Allow specifying regions for undefined subgrids
      if (column.region && !configs[column.region]) {
        configs[column.region] = {};
      }

      usedRegions.add(column.region);
    } // Implementer has provided configs for other subGrids but not normal, put defaults in place

    if (configs.normal && ObjectHelper.isEmpty(configs.normal)) {
      configs.normal = GridBase.defaultConfig.subGridConfigs.normal;
    }

    for (const region of usedRegions) {
      me.createSubGrid(region, configs[region]);
    } // Add them to Grid

    me.items = me.subGrids;
    return configs;
  }

  createSubGrid(region, config = null) {
    const me = this,
          subGridColumns = me.columns.makeChained(column => column.region === region, ['region']),
          subGridConfig = ObjectHelper.assign({
      grid: me,
      store: me.store,
      rowManager: me.rowManager,
      region: region,
      headerClass: me.headerClass,
      footerClass: me.footerClass,
      columns: subGridColumns,
      // Sort by region unless weight is explicitly defined
      weight: region
    }, config || me.subGridConfigs[region]);
    let hasCalculatedWidth = false;

    if (!subGridConfig.flex && !subGridConfig.width) {
      subGridConfig.width = subGridColumns.totalFixedWidth;
      hasCalculatedWidth = true;
    }

    const subGrid = me.subGrids[region] = new SubGrid(subGridConfig); // Must be set after creation, otherwise reset in SubGrid#set width

    subGrid.hasCalculatedWidth = hasCalculatedWidth;

    if (region === me.regions[0]) {
      // Have already done lookups for this in a couple of places, might as well store it...
      subGrid.isFirstRegion = true;
    }

    return subGrid;
  } // A SubGrid is added to Grid, add its header etc too

  onChildAdd(subGrid) {
    if (subGrid.isSubGrid) {
      const me = this,
            {
        items,
        headerContainer,
        virtualScrollers,
        footerContainer
      } = me,
            // 2 elements per index, actual element + splitter
      index = items.indexOf(subGrid) * 2;

      if (!me.hideHeaders) {
        DomHelper.insertAt(headerContainer, subGrid.headerSplitter, index);
        DomHelper.insertAt(headerContainer, subGrid.header.element, index);
      }

      DomHelper.insertAt(virtualScrollers, subGrid.scrollerSplitter, index);
      DomHelper.insertAt(virtualScrollers, subGrid.virtualScrollerElement, index);
      DomHelper.insertAt(footerContainer, subGrid.footerSplitter, index);
      DomHelper.insertAt(footerContainer, subGrid.footer.element, index); // Show splitter for all except last (new might not sort last, depending on weight)

      items.forEach((subGrid, i) => {
        if (i < items.length - 1) {
          subGrid.showSplitter();
        }
      });
    }

    return super.onChildAdd(subGrid);
  } // A SubGrid is remove from grid, remove its header etc too

  onChildRemove(subGrid) {
    super.onChildRemove(subGrid);
    const {
      items
    } = this;
    delete this.subGrids[subGrid.region];
    ArrayHelper.remove(this.regions, subGrid.region);
    subGrid.destroy(); // Make sure the new last splitter is hidden

    if (items.length) {
      items[items.length - 1].hideSplitter();
    }
  }

  doDestroy() {
    this.eachSubGrid(subGrid => subGrid.destroy());
    super.doDestroy();
  } //endregion
  //region Iteration & calling

  /**
   * Iterate over all subGrids, calling the supplied function for each.
   * @param {Function} fn Function to call for each instance
   * @param {Object} thisObj `this` reference to call the function in, defaults to the subGrid itself
   * @category SubGrid
   * @internal
   */

  eachSubGrid(fn, thisObj = null) {
    this.items.forEach((subGrid, i) => {
      fn.call(thisObj || subGrid, subGrid, i++);
    });
  }

  eachWidget(fn, deep = true) {
    this.items.forEach(widget => {
      if (fn(widget) === false) {
        return;
      }

      if (deep && widget.eachWidget) {
        widget.eachWidget(fn, deep);
      }
    });
  }
  /**
   * Call a function by name for all subGrids (that have the function).
   * @param {String} fnName Name of function to call, uses the subGrid itself as `this` reference
   * @param params Parameters to call the function with
   * @return {*} Return value from first SubGrid is returned
   * @category SubGrid
   * @internal
   */

  callEachSubGrid(fnName, ...params) {
    // TODO: make object { normal: retval, locked: retval } to return? or store. revisit when needed
    let returnValue = null;
    this.items.forEach((subGrid, i) => {
      if (subGrid[fnName]) {
        const partialReturnValue = subGrid[fnName](...params);
        if (i === 0) returnValue = partialReturnValue;
      }
    });
    return returnValue;
  } //endregion
  //region Getters

  get regions() {
    return this.items.map(item => item.region);
  }
  /**
   * This method should return names of the two last regions in the grid as they are visible in the UI. In case
   * `regions` property cannot be trusted, use different approach. Used by SubGrid and RegionResize to figure out
   * which region should collapse or expand.
   * @returns {String[]}
   * @private
   * @category SubGrid
   */

  getLastRegions() {
    const result = this.regions.slice(-2); // ALWAYS return array of length 2 in order to avoid extra conditions. Normally should not be called with 1 region

    return result.length === 2 ? result : [result[0], result[0]];
  }
  /**
   * This method should return right neighbour for passed region, or left neighbour in case last visible region is passed.
   * This method is used to decide which subgrid should take space of the collapsed one.
   * @param {String} region
   * @returns {String}
   * @private
   * @category SubGrid
   */

  getNextRegion(region) {
    const regions = this.regions; // return next region or next to last

    return regions[regions.indexOf(region) + 1] || regions[regions.length - 2];
  }

  getPreviousRegion(region) {
    return this.regions[this.regions.indexOf(region) - 1];
  }
  /**
   * Returns the subGrid for the specified region.
   * @param {String} region Region, eg. locked or normal (per default)
   * @returns {Grid.view.SubGrid} A subGrid
   * @category SubGrid
   */

  getSubGrid(region) {
    return this.subGrids[region];
  }
  /**
   * Get the SubGrid that contains specified column
   * @param {String|Grid.column.Column} column Column "name" or column object
   * @returns {Grid.view.SubGrid}
   * @category SubGrid
   */

  getSubGridFromColumn(column) {
    column = column instanceof Column ? column : this.columns.get(column) || this.columns.getById(column);
    return this.getSubGrid(column.region);
  } //endregion

  /**
   * Returns splitter element for subgrid
   * @param {Grid.view.SubGrid|String} subGrid
   * @returns {HTMLElement}
   * @private
   * @category SubGrid
   */

  resolveSplitter(subGrid) {
    const regions = this.getLastRegions();
    let region = subGrid instanceof SubGrid ? subGrid.region : subGrid;

    if (regions[1] === region) {
      region = regions[0];
    }

    return this.subGrids[region].splitterElement;
  } // This does not need a className on Widgets.
  // Each *Class* which doesn't need 'b-' + constructor.name.toLowerCase() automatically adding
  // to the Widget it's mixed in to should implement thus.

  get widgetClass() {}

});

const locale$2 = LocaleHelper.mergeLocales(locale$1, {
  //region Features
  ColumnPicker: {
    column: 'Column',
    columnsMenu: 'Columns',
    hideColumn: 'Hide column',
    hideColumnShort: 'Hide'
  },
  Filter: {
    applyFilter: 'Apply filter',
    filter: 'Filter',
    editFilter: 'Edit filter',
    on: 'On',
    before: 'Before',
    after: 'After',
    equals: 'Equals',
    lessThan: 'Less than',
    moreThan: 'More than',
    removeFilter: 'Remove filter'
  },
  FilterBar: {
    enableFilterBar: 'Show filter bar',
    disableFilterBar: 'Hide filter bar'
  },
  Group: {
    group: 'Group',
    groupAscending: 'Group ascending',
    groupDescending: 'Group descending',
    groupAscendingShort: 'Ascending',
    groupDescendingShort: 'Descending',
    stopGrouping: 'Stop grouping',
    stopGroupingShort: 'Stop'
  },
  Search: {
    searchForValue: 'Search for value'
  },
  Sort: {
    sort: 'Sort',
    sortAscending: 'Sort ascending',
    sortDescending: 'Sort descending',
    multiSort: 'Multi sort',
    removeSorter: 'Remove sorter',
    addSortAscending: 'Add ascending sorter',
    addSortDescending: 'Add descending sorter',
    toggleSortAscending: 'Change to ascending',
    toggleSortDescending: 'Change to descending',
    sortAscendingShort: 'Ascending',
    sortDescendingShort: 'Descending',
    removeSorterShort: 'Remove',
    addSortAscendingShort: '+ Ascending',
    addSortDescendingShort: '+ Descending'
  },
  //endregion
  //region Grid
  GridBase: {
    loadFailedMessage: 'Data loading failed!',
    syncFailedMessage: 'Data synchronization failed!',
    unspecifiedFailure: 'Unspecified failure',
    networkFailure: 'Network error',
    parseFailure: 'Failed to parse server response',
    loadMask: 'Loading...',
    syncMask: 'Saving changes, please wait...',
    noRows: 'No records to display',
    moveColumnLeft: 'Move to left section',
    moveColumnRight: 'Move to right section',
    removeRow: 'Delete record',
    removeRows: 'Delete records',
    moveColumnTo: region => `Move column to ${region}`
  },
  CellMenu: {
    removeRow: 'Delete record',
    removeRows: 'Delete records'
  },
  //endregion
  //region Export
  PdfExport: {
    'Waiting for response from server': 'Waiting for response from server...',
    'Export failed': 'Export failed',
    'Server error': 'Server error',
    'Generating pages': 'Generating pages...'
  },
  ExportDialog: {
    width: '40em',
    labelWidth: '12em',
    exportSettings: 'Export settings',
    export: 'Export',
    exporterType: 'Control pagination',
    cancel: 'Cancel',
    fileFormat: 'File format',
    rows: 'Rows',
    alignRows: 'Align rows',
    columns: 'Columns',
    paperFormat: 'Paper format',
    orientation: 'Orientation',
    repeatHeader: 'Repeat header'
  },
  ExportRowsCombo: {
    all: 'All rows',
    visible: 'Visible rows'
  },
  ExportOrientationCombo: {
    portrait: 'Portrait',
    landscape: 'Landscape'
  },
  SinglePageExporter: {
    singlepage: 'Single page'
  },
  MultiPageExporter: {
    multipage: 'Multiple pages',
    exportingPage: ({
      currentPage,
      totalPages
    }) => `Exporting page ${currentPage}/${totalPages}`
  },
  MultiPageVerticalExporter: {
    multipagevertical: 'Multiple pages (vertical)',
    exportingPage: ({
      currentPage,
      totalPages
    }) => `Exporting page ${currentPage}/${totalPages}`
  } //endregion

});

LocaleManagerSingleton.registerLocale('En', {
  desc: 'English',
  locale: locale$2
});

//region Import

/**
 * @module Grid/view/GridBase
 */

const resolvedPromise = new Promise(resolve => resolve()),
      storeListenerName$1 = 'GridBase:store',
      defaultScrollOptions$2 = {
  block: 'nearest',
  inline: 'nearest'
},
      preserveScroll = {
  dataset: 1,
  filter: 1
};
/**
 * A thin base class for {@link Grid.view.Grid}. Does not include any features by default, allowing smaller custom built bundles if used
 * in place of {@link Grid.view.Grid}.
 *
 * **NOTE:** In most scenarios you probably want to use Grid instead of GridBase.

 * @extends Core/widget/Panel
 * @mixes Core/mixin/Events
 * @mixes Core/mixin/Pluggable
 * @mixes Core/mixin/State
 * @mixes Grid/view/mixin/GridElementEvents
 * @mixes Grid/view/mixin/GridFeatures
 * @mixes Grid/view/mixin/GridResponsive
 * @mixes Grid/view/mixin/GridSelection
 * @mixes Grid/view/mixin/GridState
 * @mixes Grid/view/mixin/GridSubGrids
 */

class GridBase extends Panel.mixin(Pluggable, State, GridElementEvents, GridFeatures, GridNavigation, GridResponsive, GridSelection, GridState, GridSubGrids) {
  //region Config
  static get $name() {
    return 'GridBase';
  } // Factoryable type name

  static get type() {
    return 'gridbase';
  }

  static get delayable() {
    return {
      onGridScroll: {
        type: 'raf'
      },
      bufferedAfterColumnsResized: 250
    };
  }

  static get configurable() {
    return {
      //region Hidden configs

      /**
       * @hideconfigs autoUpdateRecord, defaults, hideWhenEmpty, itemCls, items, layout, layoutStyle, lazyItems, namedItems, record, textContent, defaultAction, html, htmlCls, tag, textAlign, trapFocus, content, defaultBindProperty, ripple
       */

      /**
       * @hideproperties html, isSettingValues, isValid, items, record, values, content, layoutStyle
       */

      /**
       * @hidefunctions attachTooltip, add, getWidgetById, insert, processWidgetConfig, remove, removeAll
       */
      //endregion

      /**
       * Configure this as `true` to allow elements within cells to be styled as `position: sticky`.
       *
       * Columns which contain sticky content will need to be configured with
       *
       * ```javascript
       *    cellCls : 'b-sticky-cell',
       * ```
       *
       * Or a custom renderer can add the class to the passed cell element.
       *
       * It is up to the application author how to style the cell content. It is recommended that
       * a custom renderer create content with CSS class names which the application author
       * will use to apply the `position`, and matching `margin-top` and `top` styles to keep the
       * content stuck at the grid's top.
       *
       * Note that not all browsers support this CSS feature. A cross browser alternative
       * is to use the {link Grid.feature.StickyCells StickyCells} Feature.
       * @config {Boolean}
       * @category Misc
       */
      enableSticky: null,

      /**
       * An object containing sub grid configuration objects keyed by a `region` property.
       * By default, grid has a 'locked' region (if configured with locked columns) and a 'normal' region.
       * The 'normal' region defaults to use `flex: 1`.
       *
       * This config can be used to reconfigure the "built in" sub grids or to define your own.
       * ```
       * // Redefining the "built in" regions
       * new Grid({
       *   subGridConfigs : {
       *     locked : { flex : 1 },
       *     normal : { width : 100 }
       *   }
       * });
       *
       * // Defining your own multi region sub grids
       * new Grid({
       *   subGridConfigs : {
       *     left   : { width : 100 },
       *     middle : { flex : 1 },
       *     right  : { width  : 100 }
       *   },
       *
       *   columns : {
       *     { field : 'manufacturer', text: 'Manufacturer', region : 'left' },
       *     { field : 'model', text: 'Model', region : 'middle' },
       *     { field : 'year', text: 'Year', region : 'middle' },
       *     { field : 'sales', text: 'Sales', region : 'right' }
       *   }
       * });
       * ```
       * @config {Object}
       * @category Misc
       */
      subGridConfigs: {
        normal: {
          flex: 1
        }
      },

      /**
       * Store that holds records to display in the grid, or a store config object. If the configuration contains
       * a `readUrl`, an `AjaxStore` will be created.
       *
       * A store will be created if none is specified.
       * @config {Core.data.Store|Object}
       * @category Common
       */
      store: {
        value: {},
        $config: 'nullify'
      },
      hideFooters: true
    };
  } // Default settings, applied in grids constructor.

  static get defaultConfig() {
    return {
      /**
       * Row height in pixels. When set to null, an empty row will be measured and its height will be used as
       * default row height, enabling it to be controlled using CSS
       * @config {Number}
       * @category Common
       */
      rowHeight: null,

      /**
       * Use fixed row height. Setting this to `true` will configure the underlying RowManager to use fixed row
       * height, which sacrifices the ability to use rows with variable height to gain a fraction better
       * performance.
       *
       * Using this setting also ignores the {@link Grid.view.GridBase#config-getRowHeight} function, and thus any
       * row height set in data. Only Grids configured {@link Grid.view.GridBase#config-rowHeight} is used.
       *
       * @config {Boolean}
       * @category Layout
       */
      fixedRowHeight: null,

      /**
       * A function called for each row to determine its height. It is passed a {@link Core.data.Model record} and
       * expected to return the desired height of that records row. If the function returns a falsy value, Grids
       * configured {@link Grid.view.GridBase#config-rowHeight} is used.
       *
       * The default implementation of this function returns the row height from the records
       * {@link Grid.data.GridRowModel#field-rowHeight rowHeight field}.
       *
       * Override this function to take control over how row heights are determined:
       *
       * ```javascript
       * new Grid({
       *    getRowHeight(record) {
       *        if (record.low) {
       *            return 20;
       *        }
       *        else if (record.high) {
       *            return 60;
       *        }
       *
       *        // Will use grids configured rowHeight
       *        return null;
       *    }
       * });
       * ```
       *
       * NOTE: Height set in a Column renderer takes precedence over the height returned by this function.
       *
       * @config {Function} getRowHeight
       * @param {Core.data.Model} getRowHeight.record Record to determine row height for
       * @returns {Number} Desired row height
       * @category Layout
       */
      // used if no rowHeight specified and none found in CSS. not public since our themes have row height
      // specified and this is more of an internal failsafe
      defaultRowHeight: 45,

      /**
       * Text to display when there is no data to display in the grid
       * @config {String}
       * @default
       * @category Common
       */
      emptyText: 'L{noRows}',

      /**
       * Refresh entire row when a record changes (`true`) or, if possible, only the cells affected (`false`).
       *
       * When this is set to `false`, then if a column uses a renderer, cells in that column will still
       * be updated because it is impossible to know whether the cells value will be affected.
       *
       * If a standard, provided Column class is used with no custom renderer, its cells will only be updated
       * if the column's {@link Grid.column.Column#config-field} is changed.
       * @config {Boolean}
       * @default
       * @category Misc
       */
      fullRowRefresh: true,

      /**
       * True to not create any grid column headers
       * @config {Boolean}
       * @default false
       * @category Misc
       */
      hideHeaders: null,

      /**
       * Show "Remove row" item in context menu (if enabled and grid not read only).
       *
       * **DEPRECATED:** Use cellMenu feature configuration instead:
       *
       * ```javascript
       * const scheduler = new Scheduler({
       *     features : {
       *         cellMenu : {
       *             items : {
       *                 removeRow : false
       *             }
       *         }
       *     }
       * });
       *
       * Please see https://bryntum.com/docs/grid/#guides/upgrades/4.0.0.md for more information.
       *
       * @deprecated 4.0.0
       * @config {Boolean}
       * @default
       * @category Misc
       */
      showRemoveRowInContextMenu: true,

      /**
       * Automatically set grids height to fit all rows (no scrolling in the grid). In general you should avoid
       * using `autoHeight: true`, since it will bypass Grids virtual rendering and render all rows at once, which
       * in a larger grid is really bad for performance.
       * @config {Boolean}
       * @default false
       * @category Layout
       */
      autoHeight: null,

      /**
       * Data to set in grids store (a Store will be created if none is specified)
       * @config {Object[]}
       * @category Common
       */
      data: null,

      /**
       * Column definitions for the grid, will be used to create Column instances that are added to a ColumnStore:
       *
       * ```
       * new Grid({
       *   columns : [
       *     { text : 'Alias', field : 'alias' },
       *     { text : 'Superpower', field : 'power' }
       *   ]
       * });
       * ```
       *
       * Also accepts a store config object:
       *
       * ```
       * new Grid({
       *   columns : {
       *     data : [
       *       { text : 'Alias', field : 'alias' },
       *       { text : 'Superpower', field : 'power' }
       *     ],
       *     listeners : {
       *       update() {
       *         // Some update happened
       *       }
       *     }
       *   }
       * });
       * ```
       *
       * This store can be accessed using {@link #property-columns}:
       *
       * ```
       * grid.columns.add({ field : 'column', text : 'New column' });
       * ```
       * @config {Object[]|Object}
       * @category Common
       */
      columns: [],

      /**
       * Region to which columns are added when they have none specified
       * @config {String}
       * @default
       * @category Misc
       */
      defaultRegion: 'normal',

      /**
       * true to destroy the store when the grid is destroyed
       * @config {Boolean}
       * @default false
       * @category Misc
       */
      destroyStore: null,

      /**
       * Set to true to allow text selection in the grid cells
       * @config {Boolean}
       * @default false
       * @category Selection
       */
      enableTextSelection: null,

      /**
       * A message to be shown when a store or Crud Manager is performing a remote operation. Set to `null` to disable default load mask.
       *
       * To create a custom load mask need to subscribe to the grid's store events and show {@link Core.widget.Mask Mask}
       * on {@link Core.data.AjaxStore#event-beforeRequest beforeRequest} and hide it on {@link Core.data.AjaxStore#event-afterRequest afterRequest}.
       * Note, for Crud Manager events are different: show Mask on `beforeSend` and hide it on `requestDone` and `requestFail`.
       *
       * ```javascript
       * const grid = new Grid({
       *     loadMask : null,
       *     syncMask : null
       * });
       *
       * grid.store.on({
       *     beforeRequest : () => {
       *         Mask.mask({
       *             text : 'Data is loading...',
       *             mode : 'dark-blur'
       *         }, document.body);
       *     }
       *     afterRequest : () => {
       *         Mask.unmask(document.body);
       *     }
       * });
       *
       * store.load();
       * ```
       *
       * @config {String|null}
       * @default "Loading..."
       * @category Misc
       */
      loadMask: 'L{loadMask}',

      /**
       * A {@link Core.widget.Mask} config object to adjust the {@link Core.widget.Widget#config-maskDefaults}
       * when data is loading. The message and optional configuration from the {@link #config-loadMask} config
       * take priority over these options, just as they do for `maskDefaults`, respectively.
       *
       * The final mask configuration for a load mask is as if the following were applied:
       *
       * ```
       *  Object.assign({},
       *      grid.maskDefaults,
       *      grid.loadMaskDefaults,
       *      grid.loadMask);
       * ```
       * @config {Object|Core.widget.Mask}
       * @category Misc
       */
      loadMaskDefaults: {
        useTransition: true,
        showDelay: 1000
      },

      /**
       * A {@link Core.widget.Mask} config object to adjust the {@link Core.widget.Widget#config-maskDefaults}
       * when an error occurs loading data.
       *
       * The final mask configuration for an error mask is as if the following were applied:
       *
       * ```
       *  Object.assign({},
       *      grid.maskDefaults,
       *      grid.loadMaskDefaults,
       *      grid.loadMaskError,
       *      errorMessage);
       * ```
       * @config {Object|Core.widget.Mask}
       * @category Misc
       */
      loadMaskError: {
        icon: 'b-icon b-icon-warning',
        autoClose: 3000,
        showDelay: 0
      },

      /**
       * Grids change the `maskDefaults` to cover only their `body` element.
       * @config {Object|Core.widget.Mask}
       * @category Misc
       */
      maskDefaults: {
        cover: 'body',
        target: 'element'
      },

      /**
       * A message to be shown when Crud Manager is persisting changes on the server. Set to `null` to disable default sync mask.
       * This config is similar to {@link #config-loadMask} but designed for saving data.
       *
       * To create a custom sync mask need to subscribe to the Crud Manager events and show {@link Core.widget.Mask Mask}
       * on `beforeSend` and hide it on `requestDone` and `requestFail`.
       *
       * @config {String|null}
       * @default "Saving changes, please wait..."
       * @category Misc
       */
      syncMask: 'L{syncMask}',

      /**
       * Set to `false` to inhibit column lines
       * @config {Boolean}
       * @default
       * @category Misc
       */
      columnLines: true,

      /**
       * Set to `true` to stretch the last column in a grid with all fixed width columns
       * to fill extra available space if the grid's width is wider than the sum of all
       * configured column widths.
       * @config {Boolean}
       * @default
       * @category Layout
       */
      fillLastColumn: true,

      /**
       * Set to `false` to only measure cell contents when double clicking the edge between column headers.
       * @config {Boolean}
       * @default
       * @category Layout
       */
      resizeToFitIncludesHeader: true,

      /**
       * Set to `false` to prevent remove row animation and remove the delay related to that.
       * @config {Boolean}
       * @default
       * @category Misc
       */
      animateRemovingRows: !BrowserHelper.isIE11,
      // IE11 doesn't have reliable firing of transitionend

      /**
       * Set to `true` to not get a warning when using another base class than GridRowModel for your grid data. If
       * you do, and would like to use the full feature set of the grid then include the fields from GridRowModel
       * in your model definition.
       * @config {Boolean}
       * @default false
       * @category Misc
       */
      disableGridRowModelWarning: null,
      headerClass: Header,
      footerClass: Footer,
      testPerformance: false,
      // TODO: break out as strategies
      positionMode: 'translate',
      // translate, translate3d, position
      rowScrollMode: 'move',
      // move, dom, all

      /**
       * Grid monitors window resize by default.
       * @config {Boolean}
       * @default true
       * @category Misc
       */
      monitorResize: true,

      /**
       * An object containing Feature configuration objects (or `true` if no configuration is required)
       * keyed by the Feature class name in all lowercase.
       * @config {Object}
       * @category Common
       * @typings any
       */
      features: true,

      /**
       * Configures whether the grid is scrollable in the `Y` axis. This is used to configure a {@link Grid.util.GridScroller}.
       * See the {@link #config-scrollerClass} config option.
       * @config {Boolean|Object|Core.helper.util.Scroller}
       * @category Scrolling
       */
      scrollable: {
        // Just Y for now until we implement a special grid.view.Scroller subclass
        // Which handles the X scrolling of subgrids.
        overflowY: true
      },

      /**
       * The class to instantiate to use as the {@link #config-scrollable}. Defaults to {@link Grid.util.GridScroller}.
       * @config {Core.helper.util.Scroller}
       * @category Scrolling
       */
      scrollerClass: GridScroller,

      /**
       * Configuration values for the {@link Grid.util.ScrollManager} class.
       * @config {Object}
       * @category Scrolling
       */
      scrollManager: null,

      /**
       * Configure as `true` to have the grid show a red "changed" tag in cells who's
       * field value has changed and not yet been committed.
       * @config {Boolean}
       * @default false
       * @category Misc
       */
      showDirty: null,
      refreshSuspended: 0,

      /**
       * Animation transition duration in milliseconds.
       * @config {Number}
       * @default
       * @category Misc
       */
      transitionDuration: 500,

      /**
       * Event which is used to show context menus.
       * Available options are: 'contextmenu', 'click', 'dblclick'.
       * Default value is 'contextmenu'
       * @config {String}
       * @category Misc
       */
      contextMenuTriggerEvent: 'contextmenu',
      localizableProperties: ['emptyText', 'loadMask', 'syncMask'],
      asyncEventSuffix: ''
    };
  }
  /**
   * Animation transition duration in milliseconds.
   * @property {number}
   * @name transitionDuration
   * @category Misc
   */

  static getLKey() {
    return '01fcef2c-355f-11eb-b269-d094663d5c88';
  }

  static get properties() {
    return {
      _selectedRecords: [],
      _verticalScrollHeight: 0,
      virtualScrollHeight: 0,
      _scrollTop: null
    };
  }

  static get deprecatedEvents() {
    return {
      cellContextMenuBeforeShow: {
        product: 'Grid',
        invalidAsOfVersion: '5.0.0',
        message: '`cellContextMenuBeforeShow` event is deprecated, in favor of `cellMenuBeforeShow` event. Please see https://bryntum.com/docs/grid/#guides/upgrades/4.0.0.md for more information.'
      },
      cellContextMenuShow: {
        product: 'Grid',
        invalidAsOfVersion: '5.0.0',
        message: '`cellContextMenuShow` event is deprecated, in favor of `cellMenuShow` event. Please see https://bryntum.com/docs/grid/#guides/upgrades/4.0.0.md for more information.'
      },
      headerContextMenuBeforeShow: {
        product: 'Grid',
        invalidAsOfVersion: '5.0.0',
        message: '`headerContextMenuBeforeShow` event is deprecated, in favor of `headerMenuBeforeShow` event. Please see https://bryntum.com/docs/grid/#guides/upgrades/4.0.0.md for more information.'
      },
      headerContextMenuShow: {
        product: 'Grid',
        invalidAsOfVersion: '5.0.0',
        message: '`headerContextMenuShow` event is deprecated, in favor of `headerMenuShow` event. Please see https://bryntum.com/docs/grid/#guides/upgrades/4.0.0.md for more information.'
      },
      contextMenuItem: {
        product: 'Grid',
        invalidAsOfVersion: '5.0.0',
        message: '`contextMenuItem` event is deprecated, in favor of `cellMenuItem` and `headerMenuItem` events. Please see https://bryntum.com/docs/grid/#guides/upgrades/4.0.0.md for more information.'
      },
      contextMenuToggleItem: {
        product: 'Grid',
        invalidAsOfVersion: '5.0.0',
        message: '`contextMenuToggleItem` event is deprecated, in favor of `cellMenuToggleItem` and `headerMenuToggleItem` events. Please see https://bryntum.com/docs/grid/#guides/upgrades/4.0.0.md for more information.'
      }
    };
  } //endregion
  //region Properties

  /**
   * Get/set the store used by this Grid. The setter accepts Store or a configuration object for a store.
   * If the configuration contains a `readUrl`, an AjaxStore will be created.
   * @property {Core.data.Store|Object}
   * @name store
   * @category Common
   */
  //endregion
  //region Init-destroy

  finishConfigure(config) {
    const me = this;
    super.finishConfigure(config);
    me.scrollManager = new ScrollManager(ObjectHelper.assign({
      grid: me,
      // Horizontal scrolling might happen on different subgrids, but vertical is always on same element
      verticalElement: me.scrollable.element
    }, me.scrollManager)); // When locale is applied columns react and change, which triggers `change` event on columns store for each
    // changed column, and every change normally triggers rendering view. This overhead becomes noticeable with
    // larger amount of columns. So we set two listeners to locale events: prioritized listener to be executed first
    // and suspend renderContents method and unprioritized one to resume method and call it immediately.

    LocaleManagerSingleton.on({
      locale: 'onBeforeLocaleChange',
      prio: 1,
      thisObj: me
    });
    LocaleManagerSingleton.on({
      locale: 'onLocaleChange',
      prio: -1,
      thisObj: me
    });
    GlobalEvents.on({
      theme: 'onThemeChange',
      thisObj: me
    });
    me.on({
      subGridExpand: 'onSubGridExpand',
      prio: -1,
      thisObj: me
    }); // Buffered for scrolling, to be called

    me.bufferedFixElementHeights = me.buffer('fixElementHeights', 350, me); // Add the extra grid classes to the element

    me.setGridClassList(me.element.classList);
  }

  updateEnableSticky(enableSticky) {
    this.positionMode = enableSticky ? 'position' : 'translate';
    this.element.classList[enableSticky ? 'add' : 'remove']('b-enable-sticky');
  }

  onSubGridExpand() {
    // Need to rerender all rows, because if the rows were rerendered (by adding a new column to another region for example)
    // while the region was collapsed, cells in the region will be empty.
    this.renderContents();
  }

  onBeforeLocaleChange() {
    this._suspendRenderContentsOnColumnsChanged = true;
  }

  onLocaleChange() {
    this._suspendRenderContentsOnColumnsChanged = false;

    if (this.isPainted) {
      this.renderContents();
    }
  }

  finalizeInit() {
    super.finalizeInit();

    if (this.store.isLoading) {
      // Maybe show loadmask if store is already loading when grid is constructed
      this.onStoreBeforeRequest();
    }
  }
  /**
   * Cleanup
   * @private
   */

  doDestroy() {
    const me = this;
    me.detachListeners(storeListenerName$1);

    for (const feature of Object.values(me.features)) {
      var _feature$destroy;

      (_feature$destroy = feature.destroy) === null || _feature$destroy === void 0 ? void 0 : _feature$destroy.call(feature);
    }

    if (me.columns) {
      me.columns = null;
    }

    super.doDestroy();
  }
  /**
   * Adds extra classes to the Grid element after it's been configured.
   * Also iterates through features, thus ensuring they have been initialized.
   * @private
   */

  setGridClassList(classList) {
    const me = this;
    classList.add(`b-grid-${me.positionMode}`); // TODO: enableTextSelection should be a setter, enabling toggling at any time

    if (!me.enableTextSelection) {
      classList.add('b-grid-notextselection');
    }

    if (me.autoHeight) {
      classList.add('b-autoheight');
    }

    if (me.fillLastColumn) {
      classList.add('b-fill-last-column');
    }

    if (me.showDirty) {
      classList.add('b-show-dirty');
    }

    Object.values(me.features).forEach(feature => {
      if (feature.disabled) {
        return;
      }

      let featureClass;

      if (Object.prototype.hasOwnProperty.call(feature.constructor, 'featureClass')) {
        featureClass = feature.constructor.featureClass;
      } else {
        featureClass = `b-${feature instanceof Base ? feature.$$name : feature.constructor.name}`;
      }

      if (featureClass) {
        classList.add(featureClass.toLowerCase());
      }
    });
  } //endregion
  //region Functions & events injected by features
  // For documentation & typings purposes
  //region Feature events

  /**
   * *Only when the {@link Grid.feature.Tree} feature is enabled*.
   * <p>Fired before a record toggles its collapsed state.
   * @event beforeToggleNode
   * @param {Core.data.Model} record The record being toggled.
   * @param {Boolean} collapse `true` if the node is being collapsed.
   */

  /**
   * *Only when the {@link Grid.feature.Tree} feature is enabled*.
   * <p>Fired after a record has been collapsed.
   * @event collapseNode
   * @param {Core.data.Model} record The record which has been collapsed.
   */

  /**
   * *Only when the {@link Grid.feature.Tree} feature is enabled*.
   * <p>Fired after a record has been expanded.
   * @event expandNode
   * @param {Core.data.Model} record The record which has been expanded.
   */

  /**
   * *Only when the {@link Grid.feature.Tree} feature is enabled*.
   * <p>Fired after a record toggles its collapsed state.
   * @event toggleNode
   * @param {Core.data.Model} record The record being toggled.
   * @param {Boolean} collapse `true` if the node is being collapsed.
   */
  //endregion

  /**
   * Collapse all groups/parent nodes.
   *
   * *NOTE: Only available when the {@link Grid/feature/Group Group} or the {@link Grid/feature/Tree Tree} feature is enabled.*
   *
   * @function collapseAll
   * @category Feature shortcuts
   */

  /**
   * Expand all groups/parent nodes.
   *
   * *NOTE: Only available when the {@link Grid/feature/Group Group} or the {@link Grid/feature/Tree Tree} feature is enabled.*
   *
   * @function expandAll
   * @category Feature shortcuts
   */

  /**
   * Start editing specified cell. If no cellContext is given it starts with the first cell in the first row.
   *
   * *NOTE: Only available when the {@link Grid/feature/CellEdit CellEdit} feature is enabled.*
   *
   * @function startEditing
   * @param {Object} cellContext Cell specified in format { id: 'x', columnId/column/field: 'xxx' }. See {@link Grid.view.Grid#function-getCell} for details.
   * @returns {Boolean}
   * @category Feature shortcuts
   */

  /**
   * Collapse an expanded node or expand a collapsed. Optionally forcing a certain state.
   *
   * *NOTE: Only available when the {@link Grid/feature/Tree Tree} feature is enabled.*
   *
   * @function toggleCollapse
   * @param {String|Number|Core.data.Model} idOrRecord Record (the node itself) or id of a node to toggle
   * @param {Boolean} [collapse] Force collapse (true) or expand (false)
   * @param {Boolean} [skipRefresh] Set to true to not refresh rows (if calling in batch)
   * @returns {Promise}
   * @category Feature shortcuts
   */

  /**
   * Collapse a single node.
   *
   * *NOTE: Only available when the {@link Grid/feature/Tree Tree} feature is enabled.*
   *
   * @function collapse
   * @param {String|Number|Core.data.Model} idOrRecord Record (the node itself) or id of a node to collapse
   * @returns {Promise}
   * @category Feature shortcuts
   */

  /**
   * Expand a single node.
   *
   * *NOTE: Only available when the {@link Grid/feature/Tree Tree} feature is enabled.*
   *
   * @function expand
   * @param {String|Number|Core.data.Model} idOrRecord Record (the node itself) or id of a node to expand
   * @returns {Promise}
   * @category Feature shortcuts
   */

  /**
   * Expands parent nodes to make this node "visible".
   *
   * *NOTE: Only available when the {@link Grid/feature/Tree Tree} feature is enabled.*
   *
   * @function expandTo
   * @param {String|Number|Core.data.Model} idOrRecord Record (the node itself) or id of a node
   * @returns {Promise}
   * @category Feature shortcuts
   */
  //endregion
  //region Grid template & elements

  get bodyConfig() {
    return {
      reference: 'bodyElement',
      className: 'b-grid-panel-body',
      children: [{
        tag: 'header',
        reference: 'headerContainer',
        className: {
          'b-grid-header-container': 1,
          'b-hidden': this.hideHeaders
        }
      }, {
        reference: 'bodyContainer',
        className: 'b-grid-body-container',
        tabIndex: -1,
        children: [{
          reference: 'verticalScroller',
          className: 'b-grid-vertical-scroller'
        }]
      }, {
        reference: 'virtualScrollers',
        className: 'b-virtual-scrollers b-hide-display',
        style: BrowserHelper.isFirefox ? {
          height: `${DomHelper.scrollBarWidth}px`
        } : undefined
      }, {
        tag: 'footer',
        reference: 'footerContainer',
        className: {
          'b-grid-footer-container': 1,
          'b-hidden': this.hideFooters
        }
      }]
    };
  }

  get contentElement() {
    return this.verticalScroller;
  }

  get overflowElement() {
    return this.bodyContainer;
  }

  get focusElement() {
    return this.bodyContainer;
  }

  updateHideFooters(hide) {
    var _this$footerContainer;

    (_this$footerContainer = this.footerContainer) === null || _this$footerContainer === void 0 ? void 0 : _this$footerContainer.classList[hide ? 'add' : 'remove']('b-hidden');
  } //endregion
  //region Columns

  set columns(columns) {
    const me = this;

    if (me._columnStore) {
      if (columns) {
        // TODO: @johan: reconfiguring, ie changing whole column set should work.
        // That could mean a total recalculation of subGrids.
        // That's not possible right now, so
        //throw new Error('Cannot reconfigure column set');
        // me._columnStore.clear();
        me._columnStore.data = columns;
      } else {
        me._columnStore.destroy();
      }
    } else {
      // ColumnStore instance
      if (columns instanceof ColumnStore) {
        if (!columns.grid) {
          columns.grid = me;
        }

        me._columnStore = columns;
      } // Array of columns
      else if (Array.isArray(columns)) {
          me._columnStore = new ColumnStore({
            grid: me,
            data: columns
          });
        } // Store config object
        else if (columns) {
            me._columnStore = new ColumnStore(Object.assign({
              grid: me
            }, columns));
          } // changes might be triggered when applying state, before grid is rendered
      // TODO: have this run a lighter weight, non-destructive response.
      // onColumnsChanged is a start, but lots of machinery is hooked to render.

      me._columnStore.on('change', me.onColumnsChanged, me);

      me._columnStore.on(columnResizeEvent(me.onColumnsResized, me)); // Add touch class for touch devices

      if (BrowserHelper.isTouchDevice) {
        me.touch = true; // apply touchConfig for columns that defines it

        me._columnStore.forEach(column => {
          const touchConfig = column.touchConfig;

          if (touchConfig) {
            column.applyState(touchConfig);
          }
        });
      }
    }
  }
  /**
   * Get the {@link Grid.data.ColumnStore ColumnStore} used by this Grid.
   *
   * @property {Grid.data.ColumnStore}
   * @category Common
   * @readonly
   */

  get columns() {
    return this._columnStore;
  }

  onColumnsChanged({
    action,
    changes,
    record: column
  }) {
    const me = this; // this.onPaint will handle changes caused by updateResponsive

    if (!me.isPainted) {
      return;
    }

    if (action === 'update') {
      // Just updating width is already handled in a minimal way.
      if ('width' in changes || 'minWidth' in changes || 'flex' in changes) {
        // Update any leaf columns that want to be repainted on size change
        const region = column.region;
        me.columns.visibleColumns.forEach(col => {
          if (col.region === region && col.repaintOnResize) {
            me.refreshColumn(col);
          }
        });
        return;
      } // Column toggled, need to recheck if any visible column has flex

      if ('hidden' in changes) {
        const subGrid = me.getSubGridFromColumn(column.id);
        subGrid.header.fixHeaderWidths();
        subGrid.footer.fixFooterWidths();
        subGrid.updateHasFlex();
      }
    } // Might have to add or remove subgrids when assigning a new set of columns or when changing region

    if (action === 'dataset' || action === 'update' && 'region' in changes) {
      const regions = me.columns.getDistinctValues('region'),
            {
        toRemove,
        toAdd
      } = ArrayHelper.delta(regions, me.regions, true);
      me.remove(toRemove.map(region => me.getSubGrid(region)));
      me.add(toAdd.map(region => me.createSubGrid(region)));
    }

    if (!me._suspendRenderContentsOnColumnsChanged) {
      me.renderContents();
    }
  }

  onColumnsResized({
    changes,
    record: column
  }) {
    const me = this;

    if (this.isConfiguring) {
      return;
    }

    const domWidth = DomHelper.setLength(column.width),
          domMinWidth = DomHelper.setLength(column.minWidth),
          subGrid = me.getSubGridFromColumn(column.id); // Let header and footer fix their own widths

    subGrid.header.fixHeaderWidths();
    subGrid.footer.fixFooterWidths();
    subGrid.updateHasFlex();

    if (!me.cellEls || column !== me.lastColumnResized) {
      me.cellEls = DomHelper.children(me.element, `.b-grid-cell[data-column-id="${column.id}"]`);
      me.lastColumnResized = column;
    }

    for (const cell of me.cellEls) {
      if ('width' in changes) {
        // https://app.assembla.com/spaces/bryntum/tickets/8041
        // Although header and footer elements must be sized
        // using flex-basis to avoid the busting out problem,
        // grid cells MUST be sized using width since rows are absolutely
        // positioned and will not cause the busting out problem,
        // and rows will not stretch to shrinkwrap the cells
        // unless they are widthed with width.
        cell.style.width = domWidth; // IE11 calculates flexbox container width based on min-width rather than actual width. When column
        // has width defined greater than minWidth, row may have incorrect width

        if (BrowserHelper.isIE11) {
          cell.style.flex = '';
          cell.style.minWidth = DomHelper.setLength(column.calcMinWidth);
        }
      }

      if ('minWidth' in changes) {
        cell.style.minWidth = domMinWidth;
      }

      if ('flex' in changes) {
        var _column$flex;

        cell.style.flex = (_column$flex = column.flex) !== null && _column$flex !== void 0 ? _column$flex : null;
      }
    } // If we're being driven by the ColumnResizer or other bulk column resizer (like
    // ColumnAutoWidth), they will finish up with a call to afterColumnsResized.

    if (!me.resizingColumns) {
      me.afterColumnsResized();
    }
  }

  afterColumnsResized() {
    const me = this;
    me.eachSubGrid(subGrid => {
      if (!subGrid.collapsed) {
        subGrid.fixWidths();
        subGrid.fixRowWidthsInSafariEdge();
      }
    });
    me.lastColumnResized = me.cellEls = null; // Buffer some expensive operations, like updating the fake scrollers

    me.bufferedAfterColumnsResized(); // Must happen immediately, not inside the bufferedAfterColumnsResized

    me.onHeightChange();
  }

  bufferedAfterColumnsResized() {
    this.refreshVirtualScrollbars();
    this.eachSubGrid(subGrid => {
      if (!subGrid.collapsed) {
        subGrid.refreshFakeScroll();
      }
    });
  } // Hook that can be overridden to prepare custom editors, can be used by framework wrappers

  processCellEditor(editorConfig) {} //endregion
  //region Rows

  /**
   * Get the topmost visible grid row
   * @property {Grid.row.Row}
   * @readonly
   * @name firstVisibleRow
   * @category Rows
   */

  /**
   * Get the last visible grid row
   * @property {Grid.row.Row}
   * @readonly
   * @name lastVisibleRow
   * @category Rows
   */

  /**
   * Get the Row that is currently displayed at top.
   * @member {Grid.row.Row} topRow
   * @readonly
   * @category Rows
   * @private
   */

  /**
   * Get the Row currently displayed furthest down.
   * @member {Grid.row.Row} bottomRow
   * @readonly
   * @category Rows
   * @private
   */

  /**
   * Get Row for specified record id.
   * @function getRowById
   * @param {Core.data.Model|String|Number} recordOrId Record id (or a record)
   * @returns {Grid.row.Row} Found Row or null if record not rendered
   * @category Rows
   * @private
   */

  /**
   * Returns top and bottom for rendered row or estimated coordinates for unrendered.
   * @function getRecordCoords
   * @param {Core.data.Model|string|Number} recordOrId Record or record id
   * @returns {Object} Record bounds with format { top, height, bottom }
   * @category Calculations
   * @private
   */

  /**
   * Get the Row at specified index. "Wraps" index if larger than available rows.
   * @function getRow
   * @param {Number} index
   * @returns {Grid.row.Row}
   * @category Rows
   * @private
   */

  /**
   * Get a Row for either a record, a record id or an HTMLElement
   * @function getRowFor
   * @param {HTMLElement|Core.data.Model|String|Number} recordOrId Record or record id or HTMLElement
   * @returns {Grid.row.Row} Found Row or null if record not rendered
   * @category Rows
   * @private
   */

  /**
   * Get a Row from an HTMLElement
   * @function getRowFromElement
   * @param {HTMLElement} element
   * @returns {Grid.row.Row} Found Row or null if record not rendered
   * @category Rows
   * @private
   */

  get rowManager() {
    const me = this; // Use row height from CSS if not specified in config. Did not want to turn this into a getter/setter for
    // rowHeight since RowManager will plug its implementation into Grid when created below, and after initial
    // configuration that is what should be used

    if (!me._isRowMeasured) {
      me.measureRowHeight();
    } // RowManager is a plugin, it is configured with its grid as its "client".
    // It uses client.store as its record source.

    return me._rowManager || (me._rowManager = new RowManager({
      grid: me,
      rowHeight: me.rowHeight,
      rowScrollMode: me.rowScrollMode || 'move',
      autoHeight: me.autoHeight,
      fixedRowHeight: me.fixedRowHeight,
      listeners: {
        changeTotalHeight: me.onRowManagerChangeTotalHeight,
        requestScrollChange: me.onRowManagerRequestScrollChange,
        thisObj: me
      }
    }));
  } // Default implementation, documented in `defaultConfig`

  getRowHeight(record) {
    return record.rowHeight;
  } //endregion
  //region Store

  /**
   * Hooks up data store listeners
   * @private
   * @category Store
   */

  bindStore(store) {
    const suffix = this.asyncEventSuffix;
    store.on({
      name: storeListenerName$1,
      [`refresh${suffix}`]: 'onStoreDataChange',
      [`add${suffix}`]: 'onStoreAdd',
      [`remove${suffix}`]: 'onStoreRemove',
      [`move${suffix}`]: 'onStoreMove',
      [`replace${suffix}`]: 'onStoreReplace',
      [`removeAll${suffix}`]: 'onStoreRemoveAll',
      idChange: 'onStoreRecordIdChange',
      update: 'onStoreUpdateRecord',
      beforeRequest: 'onStoreBeforeRequest',
      afterRequest: 'onStoreAfterRequest',
      clearChanges: 'onStoreDataChange',
      exception: 'onStoreException',
      commit: 'onStoreCommit'
    }, this);
  }

  unbindStore(oldStore) {
    this.detachListeners(storeListenerName$1);

    if (this.destroyStore) {
      oldStore.destroy();
    }
  }

  changeStore(store) {
    if (store == null) {
      return null;
    }

    if (typeof store === 'string') {
      store = Store.getStore(store);
    }

    if (!(store instanceof Store)) {
      var _this$initialConfig$f;

      store = ObjectHelper.assign({
        data: this.data,
        tree: Boolean((_this$initialConfig$f = this.initialConfig.features) === null || _this$initialConfig$f === void 0 ? void 0 : _this$initialConfig$f.tree)
      }, store);

      if (!store.data) {
        delete store.data;
      } // extend GridRowModel to not pollute it with custom fields (if we have multiple grids on page)

      if (!store.modelClass) {
        store.modelClass = class extends GridRowModel {};
      }

      store = new (store.readUrl ? AjaxStore : Store)(store);
    }

    return store;
  }

  updateStore(store, was) {
    const me = this;

    if (was) {
      me.unbindStore(was);
    }

    if (store) {
      me.bindStore(store);
    } // Changing store when painted -> refresh rows to reflect new data

    if (!me.isDestroying && me.isPainted && !me.refreshSuspended) {
      var _me$_rowManager;

      (_me$_rowManager = me._rowManager) === null || _me$_rowManager === void 0 ? void 0 : _me$_rowManager.reinitialize();
    }
  }
  /**
   * Rerenders a cell if a record is updated in the store
   * @private
   * @category Store
   */

  onStoreUpdateRecord({
    source: store,
    record,
    changes
  }) {
    const me = this;

    if (me.forceFullRefresh) {
      // flagged to need full refresh (probably from using GroupSummary)
      me.rowManager.refresh();
      me.forceFullRefresh = false;
    } else {
      let row; // Search for old row if id was changed

      if (record.isFieldModified('id')) {
        row = me.getRowFor(record.meta.modified.id);
      }

      row = row || me.getRowFor(record); // not rendered, bail out

      if (!row) return; // We must refresh the full row if it's a special row which has signalled
      // an update because it has no cells.

      if (me.fullRowRefresh || record.isSpecialRow) {
        const index = store.indexOf(record);

        if (index !== -1) {
          row.render(index, record);
        }
      } else {
        me.columns.visibleColumns.forEach(column => {
          const field = column.field,
                isSafe = column.constructor.simpleRenderer && !Object.prototype.hasOwnProperty.call(column.data, 'renderer'); // If there's a  non-safe renderer, that is a renderer which draws values from elsewhere
          // than just its configured field, that column must be refreshed on every record update.
          // Obviously, if the column's configured field is changed that also means it's refreshed.

          if (!isSafe || changes[field]) {
            const cellElement = row.getCell(field);

            if (cellElement) {
              row.renderCell({
                cellElement,
                record
              });
            }
          }
        });
      }
    }
  }

  refreshFromRowOnStoreAdd(row, context) {
    const me = this,
          {
      rowManager
    } = me;
    rowManager.renderFromRow(row);
    rowManager.trigger('changeTotalHeight', {
      totalHeight: rowManager.totalHeight
    }); // First record? Also update fake scrollers
    // TODO: Consider making empty grid scrollable to not have to do this

    if (me.store.count === 1) {
      me.callEachSubGrid('refreshFakeScroll');
    }
  }

  onMaskAutoClose(mask) {
    super.onMaskAutoClose(mask);
    this.toggleEmptyText();
  }
  /**
   * Refreshes rows when data is added to the store
   * @private
   * @category Store
   */

  onStoreAdd({
    source: store,
    records,
    index,
    oldIndex,
    isChild,
    oldParent,
    isMove,
    isExpandAll
  }) {
    // Do not react if the content has not been rendered
    if (!this.isPainted || isExpandAll) {
      return;
    } // If it's the addition of a child to a collapsed zone, the UI does not change.

    if (isChild && !records[0].ancestorsExpanded(store)) {
      return;
    }

    this.rowManager.calculateRowCount(false, true, true);
    const me = this,
          {
      rowManager
    } = me,
          {
      topIndex,
      rows,
      rowCount
    } = rowManager,
          bottomIndex = rowManager.topIndex + rowManager.rowCount - 1,
          dataStart = index,
          dataEnd = index + records.length - 1,
          atEnd = bottomIndex >= store.count - records.length - 1; // When moving a node within a tree we might need the redraw to include its old parent and its children. Not worth
    // the complexity of trying to do a partial render for this, rerender all rows to be safe.
    // Moving records within a flat store is handled elsewhere, in onStoreMove
    // TODO: Moving within a tree should also trigger 'move' (https://app.assembla.com/spaces/bryntum/tickets/7270)

    if (oldParent || oldIndex > -1 || isChild && isMove) {
      rowManager.refresh();
    } // Added block starts in our visible block. Render from there downwards.
    else if (dataStart >= topIndex && dataStart < topIndex + rowCount) {
        me.refreshFromRowOnStoreAdd(rows[dataStart - topIndex], ...arguments);
      } // Added block ends in our visible block, render block
      else if (dataEnd >= topIndex && dataEnd < topIndex + rowCount) {
          rowManager.refresh();
        } // If added block is outside of the visible area, no visible change
        // but potentially a change in total dataset height.
        else {
            const {
              totalHeight
            } = rowManager.totalHeight; // If we are against the end of the dataset, and have appended records
            // ensure they are rendered below

            if (atEnd && index > bottomIndex) {
              rowManager.fillBelow(me._scrollTop || 0);
            }

            if (totalHeight !== rowManager.totalHeight) {
              rowManager.trigger('changeTotalHeight', {
                totalHeight: rowManager.totalHeight
              });
            }
          }
  }
  /**
   * Responds to exceptions signalled by the store
   * @private
   * @category Store
   */

  onStoreException({
    action,
    type,
    response,
    exceptionType,
    error
  }) {
    const me = this;
    let message;

    switch (type) {
      case 'server':
        message = response.message || me.L('L{unspecifiedFailure}');
        break;

      case 'exception':
        message = exceptionType === 'network' ? me.L('L{networkFailure}') : error && error.message || me.L('L{parseFailure}');
        break;
    }

    if (me.masked) {
      // eslint-disable-next-line
      const messageHTML = `<div class="b-grid-load-failure">
                    <div class="b-grid-load-fail">${me.L(action === 'read' ? 'L{loadFailedMessage}' : 'L{syncFailedMessage}')}</div>
                    <div class="b-grid-load-fail">${response && response.url ? response.url + ' responded with' : ''}</div>
                    <div class="b-grid-load-fail">${message}</div>
                </div>`;
      ObjectHelper.assign(me.masked.errorDefaults, me.loadMaskError);
      me.masked.error = messageHTML;
    }
  }
  /**
   * Refreshes rows when data is changed in the store
   * @private
   * @category Store
   */

  onStoreDataChange({
    action,
    changes,
    source: store
  }) {
    if (this.suspendRendering) {
      return;
    } // If the next mixin up the inheritance chain has an implementation, call it

    super.onStoreDataChange && super.onStoreDataChange(...arguments);
    const me = this,
          isGroupFieldChange = store.isGrouped && changes && store.groupers.some(grouper => grouper.field in changes); // If it's new data, the old calculation is invalidated.

    if (action === 'dataset') {
      me.rowManager.clearKnownHeights();
    } // No need to rerender if it's a change of the value of the group field which
    // will be responded to by StoreGroup

    if (me.isPainted && !isGroupFieldChange) {
      // Return to top if setting new data or is filtering
      me.renderRows(null, action in preserveScroll);
    }

    me.toggleEmptyText();
  }
  /**
   * The hook is called when the id of a record has changed.
   * @private
   * @category Store
   */

  onStoreRecordIdChange() {
    // If the next mixin up the inheritance chain has an implementation, call it
    super.onStoreRecordIdChange && super.onStoreRecordIdChange(...arguments);
  }
  /**
   * Shows a load mask while the connected store is loading
   * @private
   * @category Store
   */

  onStoreBeforeRequest() {
    const loadMask = this.loadMask;

    if (loadMask) {
      this.masked = Mask.mergeConfigs(this.loadMaskDefaults, loadMask);
    }
  }
  /**
   * Hides load mask after a load request ends either in success or failure
   * @private
   * @category Store
   */

  onStoreAfterRequest(event) {
    if (this.loadMask && !event.exception) {
      this.masked = null;
      this.toggleEmptyText();
    }
  }

  needsFullRefreshOnStoreRemove() {
    const features = this._features;
    return (features === null || features === void 0 ? void 0 : features.group) && !features.group.disabled || (features === null || features === void 0 ? void 0 : features.groupSummary) && !features.groupSummary.disabled;
  }
  /**
   * Animates removal of record.
   * @private
   * @category Store
   */

  onStoreRemove({
    records,
    isCollapse,
    isChild,
    isMove,
    isCollapseAll
  }) {
    // Do not react if the content has not been rendered,
    // or if it is a move, which will be handled by onStoreAdd
    if (!this.isPainted || isMove || isCollapseAll) {
      return;
    } // GridSelection mixin does its job on records removing

    super.onStoreRemove && super.onStoreRemove(...arguments);
    let topRowIndex = 2 ** 53 - 1;
    const me = this,
          {
      rowManager
    } = me,
          // Gather all visible rows which need to be removed.
    rowsToRemove = records.reduce((result, record) => {
      const row = rowManager.getRowById(record.id);

      if (row) {
        result.push(row); // Rows are repositioned in the array, it matches visual order. Need to find actual index in it

        topRowIndex = Math.min(topRowIndex, rowManager.rows.indexOf(row));
      }

      return result;
    }, []); // Remove cached heights

    rowManager.invalidateKnownHeight(records);

    if (me.animateRemovingRows && rowsToRemove.length && !isCollapse && !isChild) {
      const topRow = rowsToRemove[0];
      me.isAnimating = true; // As soon as first row has disappeared, rerender the view

      EventHelper.onTransitionEnd({
        element: topRow._elementsArray[0],
        property: 'left',
        // Detach listener after timeout even if event wasn't fired
        duration: me.transitionDuration,
        thisObj: me,

        handler() {
          me.isAnimating = false;
          rowsToRemove.forEach(row => !row.isDestroyed && row.removeCls('b-removing'));
          rowManager.refresh(); // undocumented internal event for scheduler

          me.trigger('rowRemove');
        }

      });
      rowsToRemove.forEach(row => row.addCls('b-removing'));
    } // Cannot do an update from the affected row and down here. Since group headers might be affected by
    // removing rows we need a full refresh
    else if (me.needsFullRefreshOnStoreRemove(...arguments)) {
        rowManager.refresh();
      } else {
        // Potentially remove rows and change dataset height
        rowManager.calculateRowCount(false, true, true); // If there were rows below which have moved up into place
        // then repurpose them with their new records

        if (rowManager.rows[topRowIndex]) {
          rowManager.renderFromRow(rowManager.rows[topRowIndex]);
        } // If nothing to render below, just update dataset height
        else {
            rowManager.trigger('changeTotalHeight', {
              totalHeight: rowManager.totalHeight
            });
          }

        me.trigger('rowRemove', {
          isCollapse
        });
      }
  }

  onStoreMove({
    from,
    to
  }) {
    const {
      rowManager
    } = this,
          {
      topIndex,
      rowCount
    } = rowManager,
          [dataStart, dataEnd] = [from, to].sort(); // Changed block starts in our visible block. Render from there downwards.

    if (dataStart >= topIndex && dataStart < topIndex + rowCount) {
      rowManager.renderFromRow(rowManager.rows[dataStart - topIndex]);
    } // Changed block ends in our visible block, render block
    else if (dataEnd >= topIndex && dataEnd < topIndex + rowCount) {
        rowManager.refresh();
      } // If changed block is outside of the visible area, this is a no-op

  }

  onStoreReplace({
    records,
    all
  }) {
    const {
      rowManager
    } = this;

    if (all) {
      rowManager.clearKnownHeights();
      rowManager.refresh();
    } else {
      const rows = records.reduce((rows, [, record]) => {
        const row = this.getRowFor(record);

        if (row) {
          rows.push(row);
        }

        return rows;
      }, []); // Heights will be stored on render, but some records might be out of view -> have to invalidate separately

      rowManager.invalidateKnownHeight(records);
      rowManager.renderRows(rows);
    }
  }
  /**
   * Rerenders grid when all records have been removed
   * @private
   * @category Store
   */

  onStoreRemoveAll() {
    // GridSelection mixin does its job on records removing
    super.onStoreRemoveAll && super.onStoreRemoveAll(...arguments);

    if (this.isPainted) {
      this.rowManager.clearKnownHeights();
      this.renderRows();
      this.toggleEmptyText();
    }
  } // Refresh dirty cells on commit

  onStoreCommit({
    changes
  }) {
    if (this.showDirty && changes.modified.length) {
      const rows = [];
      changes.modified.forEach(record => {
        const row = this.rowManager.getRowFor(record);
        row && rows.push(row);
      });
      this.rowManager.renderRows(rows);
    }
  }
  /**
   * Convenience functions for getting/setting data in related store
   * @property {Object[]}
   * @category Common
   */

  get data() {
    if (this._store) {
      return this._store.records;
    } else {
      return this._data;
    }
  }

  set data(data) {
    if (this._store) {
      this._store.data = data;
    } else {
      this._data = data;
    }
  }

  get emptyText() {
    return this._emptyText;
  }

  set emptyText(text) {
    this._emptyText = text;
    this.eachSubGrid(subGrid => subGrid.emptyText = text);
  } //endregion
  //region Context menu items

  /**
   * Populates the header context menu. Chained in features to add menu items.
   * @param {Object} options Contains menu items and extra data retrieved from the menu target.
   * @param {Grid.column.Column} options.column Column for which the menu will be shown
   * @param {Object} options.items A named object to describe menu items
   * @internal
   */

  populateHeaderMenu({
    column,
    items
  }) {
    const me = this,
          {
      subGrids,
      regions
    } = me;
    let first = true;
    Object.entries(subGrids).forEach(([region, subGrid]) => {
      // If SubGrid is configured with a sealed column set, do not allow moving into it
      if (subGrid.sealedColumns) {
        return;
      }

      if (column.draggable && region !== column.region && (!column.parent && subGrids[column.region].columns.count > 1 || column.parent && column.parent.children.length > 1)) {
        const moveRight = subGrid.element.compareDocumentPosition(subGrids[column.region].element) === document.DOCUMENT_POSITION_PRECEDING,
              // With 2 regions, use Move left, Move right. With multiple, include region name
        text = regions.length > 2 ? me.L('L{moveColumnTo}', me.optionalL(region)) : me.L(moveRight ? 'L{moveColumnRight}' : 'L{moveColumnLeft}');
        items[`${region}Region`] = {
          targetSubGrid: region,
          text,
          icon: 'b-fw-icon ' + (moveRight ? 'b-icon-column-move-right' : 'b-icon-column-move-left'),
          name: 'moveColumn',
          cls: first ? 'b-separator' : '',
          onItem: ({
            item
          }) => {
            column.traverse(col => col.region = region); // Changing region will move the column to the correct SubGrid, but we want it to go last

            me.columns.insert(me.columns.indexOf(subGrids[item.targetSubGrid].columns.last) + 1, column);
            me.scrollColumnIntoView(column);
          }
        };
        first = false;
      }
    });
  }
  /**
   * Populates the cell context menu. Chained in features to add menu items.
   * @param {Object} options Contains menu items and extra data retrieved from the menu target.
   * @param {Grid.column.Column} options.column Column for which the menu will be shown
   * @param {Core.data.Model} options.record Record for which the menu will be shown
   * @param {Object} options.items A named object to describe menu items
   * @internal
   */

  populateCellMenu({
    record,
    items
  }) {
    var _me$features;

    const me = this,
          // TODO: 'contextMenu' is deprecated. Please see https://bryntum.com/docs/grid/#guides/upgrades/4.0.0.md for more information.
    isDeprecatedFeatureUsed = ((_me$features = me.features) === null || _me$features === void 0 ? void 0 : _me$features.contextMenu) && !me.features.contextMenu.disabled;

    if (isDeprecatedFeatureUsed && me.showRemoveRowInContextMenu && !me.readOnly && record && !record.isSpecialRow) {
      const manyItems = me.selectedRecords.length > 1;
      items.removeRow = {
        text: manyItems ? 'L{removeRows}' : 'L{removeRow}',
        localeClass: this,
        icon: 'b-fw-icon b-icon-trash',
        name: manyItems ? 'removeRows' : 'removeRow',
        onItem: () => me.store.remove(manyItems ? me.selectedRecords : record)
      };
    }
  }

  getColumnDragToolbarItems(column, items) {
    return items;
  } //endregion
  //region Getters

  normalizeCellContext(cellContext) {
    const {
      columns,
      store
    } = this; // TODO: should clone instead of modify?
    // TODO: The answer is to use the Grid/util/Location class to robustly encapsulate a record/column intersection
    // And have them immutable, so that to change is to clone, as explained by MaximGB,
    // we want to use columnId for precision, but allow user to specify column name for ease of use...
    // modify cellContext to include columnId in those cases

    if (cellContext instanceof store.modelClass) {
      return {
        record: cellContext,
        id: cellContext.id,
        columnId: columns.bottomColumns[0].id
      };
    }

    if (!('columnId' in cellContext)) {
      if ('field' in cellContext) {
        const column = columns.get(cellContext.field);
        cellContext.columnId = column && column.id;
      } else if ('column' in cellContext) {
        const column = typeof cellContext.column === 'number' ? columns.bottomColumns[cellContext.column] : cellContext.column;
        cellContext.columnId = column && column.id;
      } // Fall back to first leaf column

      if (!('columnId' in cellContext)) {
        cellContext.columnId = columns.bottomColumns[0].id;
      }
    }

    if ('id' in cellContext) {
      // If the context is for an element, but it's stale (for a removed record)
      // then fix it up to refer to the record id at the same index.
      if (cellContext.element && !store.getById(cellContext.id)) {
        // This uses the data-index property to get the row at that index.
        const record = this.getRecordFromElement(cellContext.element); // We have a record at the same index.

        if (record) {
          cellContext.id = record.id;
        }
      }
    } else {
      const record = 'row' in cellContext ? store.getAt(cellContext.row) : cellContext.record;

      if (record) {
        cellContext.id = record.id;
      }
    }

    return cellContext;
  } // TODO: move to RowManager? Or create a CellManager?

  /**
   * Returns a cell if rendered.
   * @param {Object} cellContext { id: rowId, columnId: columnId [,column: column number, field: column field] }
   * @param {Number} [cellContext.row] The row index of the row to access. Exclusive with `id` and 'record'.
   * @param {String|Number} [cellContext.id] The record id of the row to access. Exclusive with `row` and 'record'.
   * @param {Core.data.Model} [cellContext.record] The record of the row to access. Exclusive with `id` and 'row'.
   * @param {Number} [cellContext.column] The column instance or the index of the cell to access.  Exclusive with `columnId`.
   * @param {String|Number} [cellContext.columnId] The column id of the column to access. Exclusive with `column`.
   * @param {String} [cellContext.field] The field of the column to access. Exclusive with `column`.
   * @returns {HTMLElement}
   * @category Getters
   */

  getCell(cellContext) {
    let row,
        result = null;
    cellContext = this.normalizeCellContext(cellContext);

    if ('id' in cellContext) {
      row = this.getRowById(cellContext.id);
    }

    if (row && 'columnId' in cellContext) {
      result = row.getCell(cellContext.columnId);
    }

    return result;
  } //TODO: Should move to ColumnManager? Or Header?

  /**
   * Returns the header element for the column
   * @param {String|Number|Grid.column.Column} columnId or Column instance
   * @returns {HTMLElement} Header element
   * @category Getters
   */

  getHeaderElement(columnId) {
    if (typeof columnId !== 'string') {
      columnId = columnId.id;
    }

    return this.fromCache(`.b-grid-header[data-column-id="${columnId}"]`);
  }

  getHeaderElementByField(field) {
    const column = this.columns.get(field);
    return column ? this.getHeaderElement(column) : null;
  }
  /**
   * Body height
   * @property {Number}
   * @readonly
   * @category Layout
   */

  get bodyHeight() {
    return this._bodyHeight;
  }
  /**
   * Header height
   * @property {Number}
   * @readonly
   * @category Layout
   */

  get headerHeight() {
    const me = this; // measure header if rendered and not stored

    if (me.isPainted && !me._headerHeight) {
      me._headerHeight = me.headerContainer.offsetHeight;
    }

    return me._headerHeight;
  }
  /**
   * Searches up from the specified element for a grid row and returns the record associated with that row.
   * @param {HTMLElement} element Element somewhere within a row or the row container element
   * @returns {Core.data.Model} Record for the row
   * @category Getters
   */

  getRecordFromElement(element) {
    const el = element.closest('.b-grid-row');
    if (!el) return null;
    return this.store.getAt(el.dataset.index);
  }
  /**
   * Searches up from specified element for a grid cell or an header and returns the column which the cell belongs to
   * @param {HTMLElement} element Element somewhere in a cell
   * @returns {Grid.column.Column} Column to which the cell belongs
   * @category Getters
   */

  getColumnFromElement(element) {
    const cell = DomHelper.up(element, '.b-grid-cell, .b-grid-header');
    if (!cell) return null;

    if (cell.matches('.b-grid-header')) {
      return this.columns.getById(cell.dataset.columnId);
    }

    const cellData = DomDataStore.get(cell);
    return this.columns.getById(cellData.columnId);
  } // Getter and setter for autoHeight only added for type checking, since it seems common to get it wrong in react/angular

  get autoHeight() {
    return this._autoHeight;
  }

  set autoHeight(autoHeight) {
    ObjectHelper.assertBoolean(autoHeight, 'autoHeight');
    this._autoHeight = autoHeight;
  }
  /**
   * Toggle column line visibility. End result might be overruled by/differ between themes.
   * @property {Boolean}
   * @category Misc
   */

  get columnLines() {
    return this._columnLines;
  }

  set columnLines(columnLines) {
    ObjectHelper.assertBoolean(columnLines, 'columnLines');
    DomHelper.toggleClasses(this.element, 'b-no-column-lines', !columnLines);
    this._columnLines = columnLines;
  } //endregion
  //region Fix width & height

  /**
   * Sets widths and heights for headers, rows and other parts of the grid as needed
   * @private
   * @category Width & height
   */

  fixSizes() {
    // subGrid width
    this.callEachSubGrid('fixWidths');
  }

  onRowManagerChangeTotalHeight({
    totalHeight,
    immediate
  }) {
    return this.refreshTotalHeight(totalHeight, immediate);
  }
  /**
   * Makes height of vertical scroller match estimated total height of grid. Called when scrolling vertically and
   * when showing/hiding rows.
   * @param {Number} [height] Total height supplied by RowManager
   * @param {Boolean} [immediate] Flag indicating if buffered element sizing should be bypassed
   * @private
   * @category Width & height
   */

  refreshTotalHeight(height = this.rowManager.totalHeight, immediate = false) {
    const me = this; // Veto change of estimated total height while rendering rows or if triggered while in a hidden state

    if (me.renderingRows || !me.isVisible) {
      return false;
    } // TODO: Needed??

    if (me.rowManager.bottomRow) {
      height = Math.max(height, me.rowManager.bottomRow.bottom);
    }

    const scroller = me.scrollable,
          delta = Math.abs(me.virtualScrollHeight - height),
          clientHeight = me._bodyRectangle.height,
          newMaxY = height - clientHeight;

    if (delta) {
      const // We must update immediately if we are nearing the end of the scroll range.
      isCritical = newMaxY - me._scrollTop < clientHeight * 2 || // Or if we have scrolled pass visual height
      me._verticalScrollHeight && me._verticalScrollHeight - clientHeight < me._scrollTop; // Update the true scroll range using the scroller. This will not cause a repaint.

      scroller.scrollHeight = me.virtualScrollHeight = height; // If we are scrolling, put this off because it causes
      // a full document layout and paint.
      // Do not buffer calls for not yet painted grid

      if (me.isPainted && (me.scrolling && !isCritical || delta < 100) && !immediate) {
        me.bufferedFixElementHeights();
      } else {
        me.virtualScrollHeightDirty && me.virtualScrollHeightDirty();
        me.bufferedFixElementHeights.cancel();
        me.fixElementHeights();
      }
    }
  }

  fixElementHeights() {
    const me = this,
          height = me.virtualScrollHeight,
          heightInPx = `${height}px`;
    me._verticalScrollHeight = height;
    me.verticalScroller.style.height = heightInPx;
    me.virtualScrollHeightDirty = false;

    if (me.autoHeight) {
      me.bodyContainer.style.height = heightInPx;
      me._bodyHeight = height;
      me._bodyRectangle = Rectangle.client(me.bodyContainer);
    }

    me.refreshVirtualScrollbars();
  } //endregion
  //region Scroll & virtual rendering

  set scrolling(scrolling) {
    this._scrolling = scrolling;
  }

  get scrolling() {
    return this._scrolling;
  }
  /**
   * Activates automatic scrolling of a subGrid when mouse is moved closed to the edges
   * @param {Grid.view.SubGrid|String} subGrid A subGrid instance or its region name
   */

  enableScrollingCloseToEdges(subGrid) {
    if (typeof subGrid === 'string') {
      subGrid = this.subGrids[subGrid];
    }

    this.scrollManager.startMonitoring({
      element: subGrid.element
    });
  }
  /**
   * Deactivates automatic scrolling of a subGrid when mouse is moved closed to the edges
   * @param {Grid.view.SubGrid|String} subGrid A subGrid instance or its region name
   */

  disableScrollingCloseToEdges(subGrid) {
    if (typeof subGrid === 'string') {
      subGrid = this.subGrids[subGrid];
    }

    this.scrollManager.stopMonitoring(subGrid.element);
  }
  /**
   * Responds to request from RowManager to adjust scroll position. Happens when jumping to a scroll position with
   * variable row height.
   * @param {Number} bottomMostRowY
   * @private
   * @category Scrolling
   */

  onRowManagerRequestScrollChange({
    bottom
  }) {
    this.scrollable.y = bottom - this.bodyHeight;
  }
  /**
   * Scroll syncing for normal headers & grid + triggers virtual rendering for vertical scroll
   * @private
   * @fires scroll
   * @category Scrolling
   */

  initScroll() {
    const me = this; // This method may be called early, before render calls it, so ensure that it's
    // only executed once.

    if (!me.scrollInitialized) {
      let scrollTop;
      const onScroll = me.createOnFrame(() => {
        scrollTop = me.scrollable.y; // Was getting scroll events in FF where scrollTop was unchanged, ignore those

        if (scrollTop !== me._scrollTop) {
          me._scrollTop = scrollTop;

          if (!me.scrolling) {
            me.scrolling = true;
            me.eachSubGrid(s => s.suspendResizeMonitor = true);
          }

          me.rowManager.updateRenderedRows(scrollTop);
          /**
           * Grid has scrolled vertically
           * @event scroll
           * @param {Grid.view.Grid} grid
           * @param {Number} scrollTop
           */

          me.trigger('scroll', {
            scrollTop
          });
        }
      });
      me.scrollInitialized = true;
      me.scrollable.on({
        scroll: onScroll,

        scrollend() {
          me.scrolling = false;
          me.eachSubGrid(s => s.suspendResizeMonitor = false);
        }

      });
      me.callEachSubGrid('initScroll');
    }
  } // TODO: rename to scrollRecordIntoView? Or have an alias?

  /**
   * Scrolls a row into view. If row isn't rendered it tries to calculate position
   * @param {Core.data.Model|String|Number} recordOrId Record or record id
   * @param {Object} [options] How to scroll.
   * @param {String} [options.column] Field name or ID of the column, or the Column instance to scroll to.
   * @param {String} [options.block] How far to scroll the element: `start/end/center/nearest`.
   * @param {Number} [options.edgeOffset] edgeOffset A margin around the element or rectangle to bring into view.
   * @param {Boolean|Number} [options.animate] Set to `true` to animate the scroll, or the number of milliseconds to animate over.
   * @param {Boolean} [options.highlight] Set to `true` to highlight the element when it is in view.
   * @category Scrolling
   * @returns {Promise} A promise which resolves when the specified row has been scrolled into view.
   */

  scrollRowIntoView(recordOrId, options = defaultScrollOptions$2) {
    const me = this,
          blockPosition = options.block || 'nearest',
          {
      rowManager
    } = me;
    recordOrId = me.store.getById(recordOrId);

    if (recordOrId) {
      // check that record is "displayable", not filtered out or hidden by collapse
      if (me.store.indexOf(recordOrId) === -1) {
        return resolvedPromise;
      }

      let scroller = me.scrollable,
          recordRect = me.getRecordCoords(recordOrId);
      const scrollerRect = Rectangle.from(scroller.element); // If it was calculated from the index, update the rendered rowScrollMode
      // and scroll to the actual element. Note that this should only be necessary
      // for variableRowHeight.
      // But to "make the tests green", this is a workaround for a buffered rendering
      // bug when teleporting scroll. It does not render the rows at their correct
      // positions. Please do not try to "fix" this. I will do it. NGW

      if (recordRect.virtual) {
        const virtualBlock = recordRect.block; // Scroll the calculated position to the center of the scrollingViewport
        // and then update the rendered block while asking the RowManager to
        // display the required recordOrId.

        scroller.scrollIntoView(recordRect, {
          block: 'center'
        });
        rowManager.scrollTargetRecordId = recordOrId;
        rowManager.updateRenderedRows(scroller.y, true);
        recordRect = me.getRecordCoords(recordOrId);
        rowManager.lastScrollTop = scroller.y;

        if (recordRect.virtual) {
          // bail out to not get caught in infinite loop, since code above is cut out of bundle
          // eslint-disable-next-line no-useless-return,no-unreachable
          return resolvedPromise;
        }

        const innerOptions = blockPosition !== 'nearest' ? options : {
          block: virtualBlock
        }; // Scroll the target just less than append/prepend buffer height out of view so that the animation looks good

        if (options.animate) {
          // Do not fire scroll events during this scroll sequence - it's a purely cosmetic operation.
          // We are scrolling the desired row out of view merely to *animate scroll* it to the requested position.
          scroller.suspendEvents(); // Scroll to its final position

          if (blockPosition === 'end' || blockPosition === 'nearest' && virtualBlock === 'end') {
            scroller.y -= scrollerRect.bottom - recordRect.bottom;
          } else if (blockPosition === 'start' || blockPosition === 'nearest' && virtualBlock === 'start') {
            scroller.y += recordRect.y - scrollerRect.y;
          } // Ensure rendered block is correct at that position

          rowManager.updateRenderedRows(scroller.y, false, true); // Scroll away from final position to enable a cosmetic scroll to final position

          if (virtualBlock === 'end') {
            scroller.y -= rowManager.appendRowBuffer * rowManager.rowHeight - 1;
          } else {
            scroller.y += rowManager.prependRowBuffer * rowManager.rowHeight - 1;
          } // The row will still be rendered, so scroll it using the scroller directly

          const result = scroller.scrollIntoView(me.getRecordCoords(recordOrId), Object.assign({}, options, innerOptions)); // Now we're at the required position, resume events

          return result.then(() => scroller.resumeEvents());
        } else {
          return me.scrollRowIntoView(recordOrId, Object.assign({}, options, innerOptions));
        }
      } else {
        let {
          column
        } = options;

        if (column) {
          if (typeof column === 'string') {
            column = me.columns.getById(column) || me.columns.get(column);
          } // If we are targetting a column, we must use the scroller of that column's SubGrid

          if (column) {
            scroller = me.getSubGridFromColumn(column).scrollable;
            const cellRect = Rectangle.from(rowManager.getRowFor(recordOrId).getCell(column.id));
            recordRect.x = cellRect.x;
            recordRect.width = cellRect.width;
          }
        } // No column, then tell the scroller not to scroll in the X axis
        else {
            options.x = false;
          }

        return scroller.scrollIntoView(recordRect, options);
      }
    }
  }
  /**
   * Scrolls a column into view (if it is not already)
   * @param {Grid.column.Column|String|Number} column Column name (data) or column index or actual column object.
   * @param {Object} [options] How to scroll.
   * @param {String} [options.block] How far to scroll the element: `start/end/center/nearest`.
   * @param {Number} [options.edgeOffset] edgeOffset A margin around the element or rectangle to bring into view.
   * @param {Object|Boolean|Number} [options.animate] Set to `true` to animate the scroll by 300ms,
   * or the number of milliseconds to animate over, or an animation config object.
   * @param {Number} [options.animate.duration] The number of milliseconds to animate over.
   * @param {String} [options.animate.easing] The name of an easing function.
   * @param {Boolean} [options.highlight] Set to `true` to highlight the element when it is in view.
   * @param {Boolean} [options.focus] Set to `true` to focus the element when it is in view.
   * @returns {Promise} If the column exists, a promise which is resolved when the column header element has been scrolled into view.
   * @category Scrolling
   */

  scrollColumnIntoView(column, options) {
    column = column instanceof Column ? column : this.columns.get(column) || this.columns.getById(column) || this.columns.getAt(column);
    return this.getSubGridFromColumn(column).scrollColumnIntoView(column, options);
  } // TODO The API { id: recordId, column: 'columnName' } is not clear: id has to be renamed to `record` or `recordId` to be self-explanatory;

  /**
   * Scrolls a cell into view (if it is not already)
   * @param {Object} cellContext Cell selector { id: recordId, column: 'columnName' }
   * @category Scrolling
   */

  scrollCellIntoView(cellContext, options) {
    return this.scrollRowIntoView(cellContext.id, Object.assign({
      column: cellContext.columnId
    }, typeof options === 'boolean' ? {
      animate: options
    } : options));
  }
  /**
   * Scroll all the way down
   * @returns {Promise} A promise which resolves when the bottom is reached.
   * @category Scrolling
   */

  scrollToBottom(options) {
    // triggers scroll to last record. not using current scroller height because we do not know if it is correct
    return this.scrollRowIntoView(this.store.last, options);
  }
  /**
   * Scroll all the way up
   * @returns {Promise} A promise which resolves when the top is reached.
   * @category Scrolling
   */

  scrollToTop(options) {
    return this.scrollable.scrollBy(0, -this.scrollable.y, options);
  }
  /**
   * Stores the scroll state. Returns an objects with a `scrollTop` number value for the entire grid and a `scrollLeft`
   * object containing a left position scroll value per sub grid.
   * @returns {Object}
   * @category Scrolling
   */

  storeScroll() {
    const me = this,
          state = me.storedScrollState = {
      scrollTop: me.scrollable.y,
      scrollLeft: {}
    }; // TODO: Implement special multi-element Scroller subclass for Grids which
    // encapsulates the x axis only Scrollers of all its SubGrids.

    me.eachSubGrid(subGrid => {
      state.scrollLeft[subGrid.region] = subGrid.scrollable.x;
    });
    return state;
  }
  /**
   * Restore scroll state. If state is not specified, restores the last stored state.
   * @param {Object} [state] Scroll state, optional
   * @category Scrolling
   */

  restoreScroll(state = this.storedScrollState) {
    const me = this; // TODO: Implement special multi-element Scroller subclass for Grids which
    // encapsulates the x axis only Scrollers of all its SubGrids.

    me.eachSubGrid(subGrid => {
      var _subGrid$fakeScroller;

      const x = state.scrollLeft[subGrid.region]; // Force scrollable to set its position to the underlying element in case it was removed and added back to
      // the DOM prior to restoring state

      subGrid.scrollable.updateX(x);
      subGrid.header.scrollable.updateX(x);
      subGrid.footer.scrollable.updateX(x);
      (_subGrid$fakeScroller = subGrid.fakeScroller) === null || _subGrid$fakeScroller === void 0 ? void 0 : _subGrid$fakeScroller.updateX(x);
    });
    me.scrollable.updateY(state.scrollTop);
  } //endregion
  //region Theme & measuring

  beginGridMeasuring() {
    const me = this;

    if (!me.$measureCellElements) {
      me.$measureCellElements = DomHelper.createElement({
        // For row height measuring, features are not yet there. Work around that for the stripe feature,
        // which removes borders
        className: 'b-grid-subgrid ' + (!me._isRowMeasured && me.hasFeature('stripe') ? 'b-stripe' : ''),
        reference: 'subGridElement',
        style: {
          position: 'absolute',
          top: '-10000px',
          left: '-100000px',
          visibility: 'hidden',
          contain: 'strict'
        },
        children: [{
          className: 'b-grid-row',
          reference: 'rowElement',
          children: [{
            className: 'b-grid-cell',
            reference: 'cellElement',
            style: {
              width: 'auto',
              contain: BrowserHelper.isFirefox ? 'layout paint' : 'layout style paint'
            }
          }]
        }]
      });
    } // Bring element into life if we get here early, to be able to access verticalScroller below

    me.getConfig('element'); // Temporarily add to where subgrids live, to get have all css classes in play

    me.verticalScroller.appendChild(me.$measureCellElements.subGridElement); // Not yet on page, which prevents us from getting style values. Add it to body temporarily

    if (!me.rendered && !(me.adopt && document.body.contains(me.element))) {
      document.body.appendChild(me.element);
      me.$removeAfterMeasuring = true;
    }

    return me.$measureCellElements;
  }

  endGridMeasuring() {
    // Remove grid from DOM if it was added for measuring
    if (this.$removeAfterMeasuring) {
      this.element.remove();
      this.$removeAfterMeasuring = false;
    } // Remove measuring elements from grid

    this.$measureCellElements.subGridElement.remove();
  }
  /**
   * Creates a fake subgrid with one row and mesaures its height. Result is used as rowHeight.
   * @private
   */

  measureRowHeight() {
    const me = this,
          // Create a fake subgrid with one row, since styling for row is specified on .b-grid-subgrid .b-grid-row
    {
      rowElement
    } = me.beginGridMeasuring(),
          // Use style height or default height from config.
    // Not using clientHeight since it will have some value even if no height specified in CSS
    styles = DomHelper.getStyleValue(rowElement, ['height', 'border-top-width', 'border-bottom-width']),
          styleHeight = parseInt(styles.height),
          borderTop = parseInt(styles['border-top-width']),
          borderBottom = parseInt(styles['border-bottom-width']); // Change rowHeight if specified in styling, also remember that value to replace later if theme changes and
    // user has not explicitly set some other height

    if (me.rowHeight == null || me.rowHeight === me._rowHeightFromStyle) {
      me.rowHeight = !isNaN(styleHeight) && styleHeight ? styleHeight : me.defaultRowHeight;
      me._rowHeightFromStyle = me.rowHeight;
    } // this measurement will be added to rowHeight during rendering, to get correct cell height

    me._rowBorderHeight = borderTop + borderBottom;
    me._isRowMeasured = true;
    me.endGridMeasuring(); // There is a ticket about measuring the actual first row instead:
    // https://app.assembla.com/spaces/bryntum/tickets/5735-measure-first-real-rendered-row-for-rowheight/details
  }
  /**
   * Handler for global theme change event (triggered by shared.js). Remeasures row height.
   * @private
   */

  onThemeChange({
    theme
  }) {
    this.measureRowHeight();
    this.trigger('theme', {
      theme
    });
  } //endregion
  //region Rendering of rows

  /**
   * Triggers a render of records to all row elements. Call after changing order, grouping etc to reflect changes
   * visually. Preserves scroll.
   * @category Rendering
   */

  refreshRows(returnToTop = false) {
    const {
      element,
      rowManager
    } = this;
    element.classList.add('b-notransition');

    if (returnToTop) {
      rowManager.returnToTop();
    } else {
      rowManager.refresh();
    }

    element.classList.remove('b-notransition');
  }
  /**
   * Triggers a render of all the cells in a column.
   * @param {Grid.column.Column} column
   * @category Rendering
   */

  refreshColumn(column) {
    if (!column.hidden) {
      const field = column.field;
      this.rowManager.forEach(row => {
        const cellElement = row.getCell(field);
        row.renderCell({
          cellElement
        });
      });
    }
  } //endregion
  //region Render the grid

  /**
   * Recalculates virtual scrollbars widths and scrollWidth
   * @private
   */

  refreshVirtualScrollbars() {
    // NOTE: This was at some point changed to only run on platforms with width-occupying scrollbars, but it needs
    // to run with overlayed scrollbars also to make them show/hide as they should.
    const me = this,
          {
      headerContainer,
      footerContainer,
      virtualScrollers,
      hasVerticalOverflow
    } = me,
          {
      classList
    } = virtualScrollers,
          hadHorizontalOverflow = !classList.contains('b-hide-display'),
          // We need to ask each subGrid if it has horizontal overflow.
    // If any do, we show the virtual scroller, otherwise we hide it.
    hasHorizontalOverflow = Object.values(me.subGrids).some(subGrid => subGrid.overflowingHorizontally),
          horizontalOverflowChanged = hasHorizontalOverflow !== hadHorizontalOverflow,
          method = hasVerticalOverflow ? 'add' : 'remove'; // If horizontal overflow state changed, the docked horizontal scrollbar's visibility
    //  must be synced to match, and this may cause a height change;

    if (horizontalOverflowChanged) {
      virtualScrollers.classList[hasHorizontalOverflow ? 'remove' : 'add']('b-hide-display');
    } // Auto-widthed padding element at end hides or shows to create matching margin.

    if (DomHelper.scrollBarWidth) {
      headerContainer.classList[method]('b-show-yscroll-padding');
      footerContainer.classList[method]('b-show-yscroll-padding');
      virtualScrollers.classList[method]('b-show-yscroll-padding'); // Do any measuring necessitated by show/hide of the docked horizontal scrollbar
      /// *after* mutating DOM classnames.

      if (horizontalOverflowChanged) {
        // If any subgrids reported they have horiontal overflow, then we have to ask them
        // to sync the widths of the scroll elements inside the docked horizontal scrollbar
        // so that it takes up the required scrollbar width at the bottom of our body element.
        if (hasHorizontalOverflow) {
          me.callEachSubGrid('refreshFakeScroll');
        }

        me.onHeightChange();
      }
    }
  }

  get hasVerticalOverflow() {
    const {
      scrollable
    } = this; // IE11 and Edge report 1px scroll when no scroll actually exist, which breaks header margin
    // caught by FilterBar test

    return BrowserHelper.isEdge || BrowserHelper.isIE11 ? Math.abs(scrollable.scrollHeight - scrollable.clientHeight) > 1 : scrollable.scrollHeight > scrollable.clientHeight;
  }
  /**
   * Returns content height calculated from row manager
   * @private
   */

  get contentHeight() {
    const rowManager = this.rowManager;
    return Math.max(rowManager.totalHeight, rowManager.bottomRow ? rowManager.bottomRow.bottom : 0);
  }

  onContentChange() {
    const me = this,
          rowManager = me.rowManager;

    if (me.isVisible) {
      rowManager.estimateTotalHeight();
      me.paintListener = null;
      me.refreshTotalHeight(me.contentHeight);
      me.callEachSubGrid('refreshFakeScroll');
      me.onHeightChange();
    } // If not visible, this operation MUST be done when we become visible.
    // This is announced by the paint event which is triggered when a Widget
    // really gains visibility, ie is shown or rendered, or it's not hidden,
    // and a hidden/non-rendered ancestor is shown or rendered.
    // See Widget#triggerPaint.
    else if (!me.paintListener) {
        me.paintListener = me.on({
          paint: 'onContentChange',
          once: true,
          thisObj: me
        });
      }
  }

  onHeightChange() {
    const me = this; // cache to avoid recalculations in the middle of rendering code (RowManger#getRecordCoords())

    me._bodyRectangle = Rectangle.client(me.bodyContainer);
    me._bodyHeight = me.autoHeight ? me.contentHeight : me.bodyContainer.offsetHeight;
  }
  /**
   * Called after headers have been rendered to the headerContainer.
   * This does not do anything, it's just for Features to hook in to.
   * @param {HTMLElement} headerContainer DOM element which contains the headers.
   * @param {HTMLElement} element Grid element
   * @private
   * @category Rendering
   */

  renderHeader(headerContainer, element) {}
  /**
   * Called after footers have been rendered to the footerContainer.
   * This does not do anything, it's just for Features to hook in to.
   * @param {HTMLElement} footerContainer DOM element which contains the footers.
   * @param {HTMLElement} element Grid element
   * @private
   * @category Rendering
   */

  renderFooter(footerContainer, element) {}

  suspendRefresh() {
    this.refreshSuspended++;
  }

  resumeRefresh(trigger) {
    if (this.refreshSuspended && ! --this.refreshSuspended) {
      if (trigger) {
        this.refreshRows();
      }

      this.trigger('resumeRefresh');
    }
  }
  /**
   * Rerenders all grid rows, completely replacing all row elements with new ones
   * @category Rendering
   */

  renderRows(keepScroll = true, returnToTop = false) {
    const me = this,
          scrollState = keepScroll && me.storeScroll();

    if (me.refreshSuspended) {
      return;
    }
    /**
     * Grid rows are about to be rendered
     * @event beforeRenderRows
     * @param {Grid.view.Grid} source This grid.
     */

    me.trigger('beforeRenderRows');
    me.renderingRows = true; // This allows us to do things like disable animations on a refresh

    me.element.classList.add('b-grid-refreshing');

    if (returnToTop) {
      me.scrollable.y = me._scrollTop = 0;
    }

    me.rowManager.reinitialize(returnToTop);
    /**
     * Grid rows have been rendered
     * @event renderRows
     * @param {Grid.view.Grid} source This grid.
     */

    me.trigger('renderRows');
    me.renderingRows = false;
    me.onContentChange();

    if (keepScroll) {
      me.restoreScroll(scrollState);
    }

    me.element.classList.remove('b-grid-refreshing');
  }
  /**
   * Rerenders the grids rows, headers and footers, completely replacing all row elements with new ones
   * @category Rendering
   */

  renderContents() {
    const me = this,
          {
      element,
      headerContainer,
      footerContainer,
      rowManager
    } = me;
    me.emptyCache(); // columns will be "drawn" on render anyway, bail out

    if (me.isPainted) {
      // reset measured header height, to make next call to get headerHeight measure it
      me._headerHeight = null;
      me.callEachSubGrid('refreshHeader', headerContainer);
      me.callEachSubGrid('refreshFooter', footerContainer); // Note that these are hook methods for features to plug in to. They do not do anything.

      me.renderHeader(headerContainer, element);
      me.renderFooter(footerContainer, element);
      me.fixSizes(); // any elements currently used for rows should be released.
      // actual removal of elements is done in SubGrid#clearRows

      const refreshContext = rowManager.removeAllRows();
      rowManager.calculateRowCount(false, true, true);

      if (rowManager.rowCount) {
        // Sets up the RowManager's position for when renderRows calls RowManager#reinitialize
        // so that it renders the correct data block at the correct position.
        rowManager.setPosition(refreshContext);
        me.renderRows();
      }
    }
  } // Render rows etc. on first paint, to make sure Grids element has been laid out

  onPaint() {
    const me = this,
          {
      rowManager,
      store,
      element,
      headerContainer,
      bodyContainer,
      footerContainer
    } = me,
          scrollPad = DomHelper.scrollBarPadElement;

    if (me.isPainted) {
      return;
    }

    let columnsChanged,
        maxDepth = 0; // See if updateResponsive changed any columns.

    me.columns.on({
      change: () => columnsChanged = true,
      once: true
    }); // Apply any responsive configs before rendering rows.

    me.updateResponsive(me.width, 0); // If there were any column changes, apply them

    if (columnsChanged) {
      me.callEachSubGrid('refreshHeader', headerContainer);
      me.callEachSubGrid('refreshFooter', footerContainer);
    } // Note that these are hook methods for features to plug in to. They do not do anything.
    // SubGrids take care of their own rendering.

    me.renderHeader(headerContainer, element);
    me.renderFooter(footerContainer, element); // These padding elements are only visible on scrollbar showing platforms.
    // And then, only when the owning element as the b-show-yscroll-padding class added.
    // See refreshVirtualScrollbars where this is synced on the header, footer and scroller elements.

    DomHelper.append(headerContainer, scrollPad);
    DomHelper.append(footerContainer, scrollPad);
    DomHelper.append(me.virtualScrollers, scrollPad); // Cached, updated on resize. Used by RowManager and by the subgrids upon their render.
    // Measure after header and footer have been rendered and taken their height share.

    me._bodyRectangle = Rectangle.client(me.bodyContainer);
    const bodyOffsetHeight = me.bodyContainer.offsetHeight;

    if (me.autoHeight) {
      me._bodyHeight = rowManager.initWithHeight(element.offsetHeight - headerContainer.offsetHeight - footerContainer.offsetHeight, true);
      bodyContainer.style.height = me.bodyHeight + 'px';
    } else {
      me._bodyHeight = bodyOffsetHeight;
      rowManager.initWithHeight(me._bodyHeight, true);
    }

    me.eachSubGrid(subGrid => {
      if (subGrid.header.maxDepth > maxDepth) {
        maxDepth = subGrid.header.maxDepth;
      }
    });
    headerContainer.dataset.maxDepth = maxDepth;
    me.fixSizes();

    if (store.count || !store.isLoading) {
      me.renderRows(false, false);
    }

    me.initScroll();
    me.initInternalEvents();
  }

  render() {
    const me = this; // When displayed inside one of our containers, require a size to be considered visible. Ensures it is painted
    // on display when for example in a tab

    me.requireSize = Boolean(me.owner); // Render as a container. This renders the child SubGrids

    super.render(...arguments); // Sanity check that main element has been given some sizing styles, unless autoHeight is used in which case
    // it will be sized programmatically instead

    if (!me.autoHeight && me.headerContainer.offsetHeight && !me.bodyContainer.offsetHeight) {
      console.warn('Grid element not sized correctly, please check your CSS styles and review how you size the widget');
    }
  } //endregion
  //region Masking and Appearance

  /**
   * Show a load mask with a spinner and the specified message. When using an AjaxStore masking and unmasking is
   * handled automatically, but if you are loading data in other ways you can call this function manually when your
   * load starts.
   * ```
   * myLoadFunction() {
   *   // Show mask before initiating loading
   *   grid.maskBody('Loading data');
   *   // Your custom loading code
   *   load.then(() => {
   *      // Hide the mask when loading is finished
   *      grid.unmaskBody();
   *   });
   * }
   * ```
   * @param {String} loadMask Message to show next to the spinner
   * @returns {Core.widget.Mask}
   */

  maskBody(loadMask) {
    const me = this;

    if (!me.bodyContainer) {
      return;
    } // remove any existing mask

    me.unmaskBody();
    const {
      maskElement
    } = me.activeMask = Mask.mask(loadMask, me.element);
    maskElement.style.marginTop = `${me.bodyContainer.offsetTop}px`;
    maskElement.style.height = `${me.virtualScrollers.offsetTop + me.virtualScrollers.offsetHeight - me.bodyContainer.offsetTop}px`;
    return me.activeMask;
  }
  /**
   * Hide the load mask.
   */

  unmaskBody() {
    const me = this;
    me.loadmaskHideTimer && me.clearTimeout(me.loadmaskHideTimer);
    me.loadmaskHideTimer = null;
    me.activeMask && me.activeMask.destroy();
    me.activeMask = null;
  }

  toggleEmptyText() {
    if (this.bodyContainer) {
      DomHelper.toggleClasses(this.bodyContainer, 'b-grid-empty', !(this.rowManager.rowCount > 0 || this.store.isLoading || this.store.isCommitting));
    }
  } //endregion

} // Register this widget type with its Factory

GridBase.initClass();
VersionHelper.setVersion('grid', '4.0.7');
GridBase._$name = 'GridBase';

//region Import

/**
 * @module Grid/view/Grid
 */

/**
 * The Grid component is a very powerful and performant UI component that shows tabular data (or tree data using the {@link Grid.view.TreeGrid}).
 *
 * <h2>Intro</h2>
 * The Grid widget has a wide range of features and a large API to allow users to work with data efficiently in the browser. The two
 * most important configs are {@link #config-store} and {@link #config-columns}. With the store config, you decide which data to load into the grid.
 * You can work with both in-memory arrays or load data using ajax. See the {@link Core.data.Store} class to learn more about loading data into stores.
 *
 * The columns config accepts an array of {@link Grid.column.Column Column} descriptors defining which fields that will be displayed in the grid.
 * The {@link Grid.column.Column#config-field} property in the column descriptor maps to a field in your dataset. The simplest grid configured with inline data and two columns would
 * look like this:
 *
 *      let grid = new Grid({
 *          appendTo : document.body,
 *
 *          columns: [
 *              { field: 'name', text: 'Name' },
 *              { field: 'job', text: 'Job', renderer: ({value}) => value || 'Unemployed' }
 *          ],
 *
 *          data: [
 *              { name: 'Bill', job: 'Retired' },
 *              { name: 'Elon', job: 'Visionary' },
 *              { name: 'Me' }
 *          ]
 *      });
 *
 * {@inlineexample grid/Grid.js}
 * <h2>Features</h2>
 * To avoid the Grid core being bloated, its main features are implemented in separate ´feature´ classes. These can be turned on and off based
 * on your requirements. To configure (or disable) a feature, use the {@link #config-features} object to provide your desired configuration for the features
 * you want to use. Each feature has an ´id´ that you use as a key in the features object:
 *
 *      let grid = new Grid({
 *          appendTo : document.body,
 *
 *          features : {
 *              cellEdit     : false,
 *              regionResize : true,
 *              cellTooltip  : {
 *                  tooltipRenderer : (data) => {
 *                  }
 *              },
 *              ...
 *          }
 *      });
 *
 * {@region Column configuration options}
 * A grid contains a number of columns that control how your data is rendered. The simplest option is to simply point a Column to a field in your dataset, or define a custom {@link Grid.column.Column#config-renderer}.
 * The renderer function receives one object parameter containing rendering data for the current cell being rendered.
 *
 *      let grid = new Grid({
 *          appendTo : document.body,
 *
 *          columns: [
 *              {
 *                  field: 'task',
 *                  text: 'Task',
 *                  renderer: (renderData) => {
 *                      const record = renderData.record;
 *
 *                      if (record.percentDone === 100) {
 *                          renderData.cellElement.classList.add('taskDone');
 *                          renderData.cellElement.style.background = 'green';
 *                      }
 *
 *                      return renderData.value;
 *                  }
 *              }
 *          ]
 *      });
 *
 * {@endregion}
 * {@region Grid sections (aka "locked" or "frozen" columns)}
 * The grid can be divided horizontally into individually scrollable sections. This is great if you have lots of columns that
 * don't fit the available width of the screen. To enable this feature, simply mark the columns you want to `lock`.
 * Locked columns are then displayed in their own section to the left of the other columns:
 *
 *      let grid = new Grid({
 *          appendTo : document.body,
 *          width    : 500,
 *          subGridConfigs : {
 *              // set a fixed locked section width if desired
 *              locked : { width: 300 }
 *          },
 *          columns : [
 *              { field : 'name', text : 'Name', width : 200, locked : true },
 *              { field : 'firstName', text : 'First name', width : 100, locked : true },
 *              { field : 'surName', text : 'Last name', width : 100, locked : true },
 *              { field : 'city', text : 'City', width : 100 },
 *              { type : 'number', field : 'age', text : 'Age', width : 200 },
 *              { field : 'food', text : 'Food', width : 200 }
 *          ]
 *      });
 *
 * {@inlineexample grid/LockedGrid.js}
 * You can also move columns between sections by using drag and drop, or use the built-in header context menu. If you want to be able to resize the
 * locked grid section, enable the {@link Grid.feature.RegionResize regionResize} feature.
 * {@endregion}
 * {@region Filtering}
 * One important requirement of a good Grid component is the ability to filter large datasets to quickly find what you're looking for. To
 * enable filtering (through the context menu), add the {@link Grid.feature.Filter filter} feature:
 *
 *      let grid = new Grid({
 *          features: {
 *              filter: true
 *          }
 *      });
 *
 * Or activate a default filter at initial rendering:
 *
 *      let grid = new Grid({
 *          features: {
 *              filter: { property : 'city', value : 'New York' }
 *          }
 *      });
 *
 * {@inlineexample feature/Filter.js}
 * {@endregion}
 * {@region Tooltips}
 * If you have a data models with many fields, and you want to show
 * additional data when hovering over a cell, use the {@link Grid.feature.CellTooltip cellTooltip} feature. To show a tooltip for all cells:
 *
 *      let grid = new Grid({
 *          features: {
 *              cellTooltip: ({value}) => value
 *          }
 *      });
 *
 * {@inlineexample feature/CellTooltip.js}
 * {@endregion}
 * {@region Inline Editing (default <strong>on</strong>)}
 * To enable inline cell editing in the grid, simply add the {@link Grid.feature.CellEdit cellEdit} feature:
 *
 *      let grid = new Grid({
 *          appendTo : document.body,
 *
 *          features : {
 *              cellEdit : true
 *          },
 *          columns: [
 *              {
 *                  field: 'task',
 *                  text: 'Task'
 *              }
 *          ]
 *      });
 *
 * {@inlineexample feature/CellEdit.js}
 * {@endregion}
 * {@region Context Menu}
 * Use {@link Grid.feature.CellMenu CellMenu} and {@link Grid.feature.HeaderMenu HeaderMenu}
 * features if you want your users to be able to interact with the data through the context menu:
 *
 *      let grid = new Grid({
 *          features : {
 *              headerMenu : {
 *                  items : {
 *                      showInfo : {
 *                          text   : 'Show info',
 *                          icon   : 'fa fa-info-circle',
 *                          weight : 200,
 *                          onItem : ({ item }) => console.log(item.text)
 *                      }
 *                  }
 *              },
 *              cellMenu :  {
 *                  items : {
 *                      showOptions : {
 *                          text   : 'Show options',
 *                          icon   : 'fa fa-gear',
 *                          weight : 200
 *                      }
 *                  }
 *              }
 *          }
 *      });
 *
 * {@inlineexample feature/CellMenu.js}
 * {@endregion}
 * {@region Grouping}
 * To group rows by a field in your dataset, use the {@link Grid.feature.Group group} feature.
 * {@inlineexample feature/Group.js}
 * {@endregion}
 * {@region Searching}
 * When working with lots of data, a quick alternative to filtering is the {@link Grid.feature.Search search} feature. It highlights
 * matching values in the grid as you type.
 * {@inlineexample feature/Search.js}
 * {@endregion}
 * {@region Loading and saving data}
 * The grid keeps all its data in a {@link Core.data.Store}, which is essentially an Array of {@link Core.data.Model Model} items.
 * You define your own Model representing your data entities and use the Model API to get and set values.
 *
 *      class Person extends Model {}
 *
 *      let person = new Person({
 *          name: 'Steve',
 *          age: 38
 *      });
 *
 *      person.name = 'Linda'; // person object is now `dirty`
 *
 *      let store = new Store({
 *          data : [
 *              { name : 'Don', age : 40 }
 *          ]
 *      });
 *
 *      store.add(person);
 *
 *      console.log(store.count()); // === 2
 *
 *      store.remove(person); // Remove from store
 *
 * When you update a record in a store, it's considered dirty, until you call {@link Core.data.mixin.StoreCRUD#function-commit commit} on the containing Store. You can also configure your Store to commit automatically (like Google docs).
 * If you use an AjaxStore, it will send changes to your server when commit is called.
 * Any changes you make to the Store or its records are immediately reflected in the Grid, so there is no need to tell it to refresh manually.
 *
 * To learn more about loading and saving data, please refer to [this guide](#guides/data/displayingdata.md).
 * {@endregion}
 * {@region Default configs}
 * There is a myriad of configs and features available for Grid, some of them on by default and some of them requiring
 * extra configuration. The code below tries to illustrate the major things that are used by default:
 *
 * ```javascript
 * let grid = new Grid({
 *    // The following features are enabled by default:
 *    features : {
 *        cellEdit      : true,
 *        columnPicker  : true,
 *        columnReorder : true,
 *        columnResize  : true,
 *        cellMenu      : true,
 *        headerMenu    : true,
 *        group         : true,
 *        sort          : true
 *    },
 *
 *    animateRemovingRows       : true,  // Rows will slide out on removal
 *    autoHeight                : false, // Grid needs to have a height supplied through CSS (strongly recommended) or by specifying `height`
 *    columnLines               : true,  // Themes might override it to hide lines anyway
 *    emptyText                 : 'No rows to display',
 *    enableTextSelection       : false, // Not allowed to select text in cells by default,
 *    fillLastColumn            : true,  // By default the last column is stretched to fill the grid
 *    fullRowRefresh            : true,  // Refreshes entire row when a cell value changes
 *    loadMask                  : 'Loading...',
 *    resizeToFitIncludesHeader : true,  // Also measure header when auto resizing columns
 *    responsiveLevels : {
 *      small : 400,
 *      medium : 600,
 *      large : '*'
 *    },
 *    rowHeight                  : null,  // Determined using CSS, it will measure rowHeight
 *    showDirty                  : false, // No indicator for changed cells
 * });
 * ```
 * {@endregion}
 * {@region Performance}
 * In general the Grid widget has very good performance and you can try loading any amount of data in the <a target="_blank" href="../examples/bigdataset">bigdataset</a> demo.
 * The overall rendering performance is naturally affected by many other things than
 * the data volume. Other important factors that can impact performance: number of columns, complex cell renderers, locked columns, the number of features enabled
 * and of course the browser (Chrome fastest, IE slowest).
 * {@endregion}
 *
 * @extends Grid/view/GridBase
 * @classType grid
 */

class Grid extends GridBase {
  static get $name() {
    return 'Grid';
  } // Factoryable type name

  static get type() {
    return 'grid';
  }

} // Register this widget type with its Factory

Grid.initClass();
Grid._$name = 'Grid';

/**
 * @module Grid/view/TreeGrid
 */

/**
 * A TreeGrid, a Tree combined with a Grid. Must be configured with exactly one {@link Grid.column.TreeColumn} (`type: tree`), but can also have an
 * arbitrary number of other columns. Most features that can be used with Grid also works with TreeGrid, with the
 * exception of the Group feature.
 * @extends Grid/view/Grid
 *
 * @classtype treegrid
 * @externalexample grid/TreeGrid.js
 */

class TreeGrid extends Grid {
  static get $name() {
    return 'TreeGrid';
  } // Factoryable type name

  static get type() {
    return 'treegrid';
  }

  static get configurable() {
    return {
      /**
       * The store instance or config object that holds the records to be displayed by this TreeGrid. If assigning
       * a store instance, it must be configured with `tree: true`.
       *
       * A store will be created if none is specified.
       * @config {Core.data.Store|Object} store
       * @category Common
       */
      store: {
        tree: true
      }
    };
  } //region Plugged in functions / inherited configs

  /**
   * Collapse an expanded node or expand a collapsed. Optionally forcing a certain state.
   *
   * @function toggleCollapse
   * @param {String|Number|Core.data.Model} idOrRecord Record (the node itself) or id of a node to toggle
   * @param {Boolean} [collapse] Force collapse (true) or expand (false)
   * @param {Boolean} [skipRefresh] Set to true to not refresh rows (if calling in batch)
   * @returns {Promise}
   * @category Feature shortcuts
   */

  /**
   * Collapse a single node.
   *
   * @function collapse
   * @param {String|Number|Core.data.Model} idOrRecord Record (the node itself) or id of a node to collapse
   * @returns {Promise}
   * @category Feature shortcuts
   */

  /**
   * Expand a single node.
   *
   * @function expand
   * @param {String|Number|Core.data.Model} idOrRecord Record (the node itself) or id of a node to expand
   * @returns {Promise}
   * @category Feature shortcuts
   */

  /**
   * Expands parent nodes to make this node "visible".
   *
   * @function expandTo
   * @param {String|Number|Core.data.Model} idOrRecord Record (the node itself) or id of a node
   * @returns {Promise}
   * @category Feature shortcuts
   */
  //endregion

  /* disconnect doc comment */
  //region Store

  updateStore(store, was) {
    if (store && !store.tree) {
      throw new Error('TreeGrid requires a Store configured with tree : true');
    }

    super.updateStore(store, was);
  } //endregion

} // Register this widget type with its Factory

TreeGrid.initClass();
TreeGrid._$name = 'TreeGrid';

/**
 * @module Scheduler/crud/AbstractCrudManagerMixin
 */

const storeSortFn = function (lhs, rhs, sortProperty) {
  // TODO: get rid of these StoreDescriptors. Just use Stores.
  if (lhs.store) {
    lhs = lhs.store;
  }

  if (rhs.store) {
    rhs = rhs.store;
  }

  lhs = lhs[sortProperty] || 0;
  rhs = rhs[sortProperty] || 0;
  return lhs < rhs ? -1 : lhs > rhs ? 1 : 0;
},
      // Sorter function to keep stores in loadPriority order
storeLoadSortFn = function (lhs, rhs) {
  return storeSortFn(lhs, rhs, 'loadPriority');
},
      // Sorter function to keep stores in syncPriority order
storeSyncSortFn = function (lhs, rhs) {
  return storeSortFn(lhs, rhs, 'syncPriority');
},
      nullFn = () => {};
/**
 * An abstract mixin that supplies most of the CrudManager functionality.
 * It implements basic mechanisms of collecting stores to organize batch communication with a server.
 * Yet it does not contain methods related to _data transfer_ nor _encoding_.
 * These methods are to be provided in sub-classes.
 * Out of the box there are mixins implementing {@link Scheduler.crud.transport.AjaxTransport support of AJAX for data transferring}
 * and {@link Scheduler.crud.encoder.JsonEncoder JSON for data encoding system}.
 * For example this is how we make a model that will implement CrudManager protocol and use AJAX/JSON to pass the dada to the server:
 *
 * ```javascript
 * class SystemSettings extends JsonEncode(AjaxTransport(AbstractCrudManagerMixin(Model))) {
 *     ...
 * }
 * ```
 *
 * ## Data transfer and encoding methods
 *
 * These are methods that must be provided by subclasses of this class:
 *
 * - {@link #function-sendRequest sendRequest}
 * - {@link #function-cancelRequest cancelRequest}
 * - {@link #function-encode encode}
 * - {@link #function-decode decode}
 *
 * @mixin
 * @mixes Core/mixin/Delayable
 * @mixes Core/mixin/Events
 * @abstract
 */

var AbstractCrudManagerMixin = (Target => {
  // Trigger $meta calculation to get up-to-date is "isXXX" flags
  Target.$meta;
  const mixins = []; // These two mixins are mixed in the Scheduling Engine code ..but in its own way
  // so that Base.mixin() cannot understand that they are already there and applies them 2nd time

  if (!Target.isEvents) {
    mixins.push(Events);
  }

  if (!Target.isDelayable) {
    mixins.push(Delayable);
  }

  return class AbstractCrudManagerMixin extends (Target || Base).mixin(...mixins) {
    /**
     * Fires before server response gets applied to the stores. Return `false` to prevent data applying.
     * This event can be used for server data preprocessing. To achieve it user can modify the `response` object.
     * @event beforeResponseApply
     * @param {Scheduler.crud.AbstractCrudManager} source The CRUD manager.
     * @param {String} requestType The request type (`sync` or `load`).
     * @param {Object} response The decoded server response object.
     */

    /**
     * Fires before loaded data get applied to the stores. Return `false` to prevent data applying.
     * This event can be used for server data preprocessing. To achieve it user can modify the `response` object.
     * @event beforeLoadApply
     * @param {Scheduler.crud.AbstractCrudManager} source The CRUD manager.
     * @param {Object} response The decoded server response object.
     * @param {Object} options Options provided to the {@link #function-load} method.
     */

    /**
     * Fires before sync response data get applied to the stores. Return `false` to prevent data applying.
     * This event can be used for server data preprocessing. To achieve it user can modify the `response` object.
     * @event beforeSyncApply
     * @param {Scheduler.crud.AbstractCrudManager} source The CRUD manager.
     * @param {Object} response The decoded server response object.
     */
    static get $name() {
      return 'AbstractCrudManagerMixin';
    } //region Default config

    static get defaultConfig() {
      return {
        // overrideCrudStoreLoad : false,

        /**
         * The server revision stamp.
         * The _revision stamp_ is a number which should be incremented after each server-side change.
         * This property reflects the current version of the data retrieved from the server and gets updated after each {@link #function-load} and {@link #function-sync} call.
         * @property {Number}
         * @readonly
         */
        crudRevision: null,

        /**
         * A list of registered stores whose server communication will be collected into a single batch.
         * Each store is represented by a _store descriptor_, an object having following structure:
         * @member {Object[]} crudStores
         * @property {String} crudStores.storeId Unique store identifier.
         * @property {Core.data.Store} crudStores.store Store itself.
         * @property {String} [crudStores.phantomIdField] Set this if store model has a predefined field to keep phantom record identifier.
         * @property {String} [crudStores.idField] id field name, if it's not specified then class will try to get it from a store model.
         */

        /**
         * Sets the list of stores controlled by the CRUD manager.
         *
         * When adding a store to the CrudManager, make sure the server response format is correct for `load` and `sync` requests.
         * Learn more in the [Working with data](#guides/data/crud_manager.md#loading-data) guide.
         *
         * Store can be provided by itself, its storeId or an object having the following structure:
         * @property {String} stores.storeId Unique store identifier. Under this name the store related requests/responses will be sent.
         * @property {Core.data.Store} stores.store The store itself.
         * @property {String} [stores.phantomIdField] Set this if the store model has a predefined field to keep phantom record identifier.
         * @property {String} [stores.idField] id field name, if it's not specified then class will try to get it from a store model.
         * @config {Core.data.Store[]|String[]|Object[]}
         */
        crudStores: [],

        /**
         * Name of a store property to retrieve store identifiers from.
         * Store identifier is used as a container name holding corresponding store data while transferring them to/from the server.
         * By default `storeId` property is used. And in case a container identifier has to differ this config can be used:
         *
         * ```javascript
         * class CatStore extends Store {
         *     // storeId is "meow" but for sending/receiving store data
         *     // we want have "cats" container in JSON, so we create a new property "storeIdForCrud"
         *     storeId          : 'meow',
         *     storeIdForCrud   : 'cats'
         * });
         *
         * class MyCrudManager extends CrudManager {
         *     ...
         *     crudStores           : ['meow'],
         *     // crud manager will get store identifier from "storeIdForCrud" property
         *     storeIdProperty  : 'storeIdForCrud'
         * });
         * ```
         * The `storeIdProperty` property can also be specified directly on a store:
         *
         * ```javascript
         * class CatStore extends Store {
         *     // storeId is "meow" but for sending/receiving store data
         *     // we want have "cats" container in JSON
         *     storeId          : 'meow',
         *     // so we create a new property "storeIdForCrud"..
         *     storeIdForCrud  : 'cats',
         *     // and point CrudManager to use it as the store identifier source
         *     storeIdProperty  : 'storeIdForCrud'
         * });
         *
         * class DogStore extends Store {
         *     // storeId is "dogs" and it will be used as a container name for the store data
         *     storeId          : 'dogs'
         * });
         *
         * class MyCrudManager extends CrudManager {
         *     ...
         *     crudStores           : ['meow', 'dogs']
         * });
         * ```
         * @config {String}
         */
        storeIdProperty: 'storeId',
        // TODO: no support for remote filtering yet
        // /**
        //  * The name of the 'filter' parameter to send in a load request.
        //  * @config {String}
        //  * @default
        //  */
        crudFilterParam: 'filter',

        /**
         * Sends request to the server.
         * @function sendRequest
         * @param {Object} request The request to send. An object having following properties:
         * @param {String} request.data {@link #function-encode Encoded} request.
         * @param {String} request.type Request type, can be either `load` or `sync`
         * @param {Function} request.success Callback to be started on successful request transferring
         * @param {Function} request.failure Callback to be started on request transfer failure
         * @param {Object} request.thisObj `this` reference for the above `success` and `failure` callbacks
         * @return {Promise} The request promise.
         * @abstract
         */

        /**
         * Cancels request to the server.
         * @function cancelRequest
         * @param {Promise} promise The request promise to cancel (a value returned by corresponding {@link #function-sendRequest} call).
         * @param {Function} reject Reject handle of the corresponding promise
         * @abstract
         */

        /**
         * Encodes request to the server.
         * @function encode
         * @param {Object} request The request to encode.
         * @returns {String} The encoded request.
         * @abstract
         */

        /**
         * Decodes response from the server.
         * @function decode
         * @param {String} response The response to decode.
         * @returns {Object} The decoded response.
         * @abstract
         */
        transport: {},

        /**
         * When `true` forces the CRUD manager to process responses depending on their `type` attribute.
         * So `load` request may be responded with `sync` response for example.
         * Can be used for smart server logic allowing the server to decide when it's better to respond with a complete data set (`load` response)
         * or it's enough to return just a delta (`sync` response).
         * @config {Boolean}
         * @default
         */
        trackResponseType: false,

        /**
         * When `true` the Crud Manager does not require
         * all updated and removed records to be mentioned in the *sync* response.
         * In this case response should include only server side changes.
         *
         * **Please note that added records should still be mentioned in response
         * to provide real identifier instead of the phantom one.**
         * @config {Boolean}
         * @default
         */
        supportShortSyncResponse: true,

        /**
         * Field name to be used to transfer a phantom record identifier.
         * @config {String}
         * @default
         */
        phantomIdField: '$PhantomId',

        /**
         * Field name to be used to transfer a phantom parent record identifier.
         * @config {String}
         * @default
         */
        phantomParentIdField: '$PhantomParentId',

        /**
         * `true` to automatically call {@link #function-load} method after creation.
         * @config {Boolean}
         * @default
         */
        autoLoad: false,

        /**
         * The timeout in milliseconds to wait before persisting changes to the server.
         * Used when {@link #config-autoSync} is set to `true`.
         * @config {Number}
         * @default
         */
        autoSyncTimeout: 100,

        /**
         * `true` to automatically persist store changes after edits are made in any of the stores monitored.
         * Please note that sync request will not be invoked immediately but only after {@link #config-autoSyncTimeout} interval.
         * @config {Boolean}
         * @default
         */
        autoSync: false,

        /**
         * `True` to reset identifiers (defined by `idField` config) of phantom records before submitting them to the server.
         * @config {Boolean}
         * @default
         */
        resetIdsBeforeSync: true,

        /**
         * @member {Object[]} syncApplySequence
         * An array of stores presenting an alternative sync responses apply order.
         * Each store is represented by a _store descriptor_, an object having following structure:
         * @property {String} syncApplySequence.storeId Unique store identifier.
         * @property {Core.data.Store} syncApplySequence.store Store itself.
         * @property {String} [syncApplySequence.phantomIdField] Set this if store model has a predefined field to keep phantom record identifier.
         * @property {String} [syncApplySequence.idField] id field name, if it's not specified then class will try to get it from a store model.
         */

        /**
         * An array of store identifiers sets an alternative sync responses apply order.
         * By default the order in which sync responses are applied to the stores is the same as they registered in.
         * But in case of some tricky dependencies between stores this order can be changed:
         *
         *```javascript
        * class MyCrudManager extends CrudManager {
        *     // register stores (they will be loaded in the same order: 'store1' then 'store2' and finally 'store3')
        *     crudStores : ['store1', 'store2', 'store3'],
        *     // but we apply changes from server to them in an opposite order
        *     syncApplySequence : ['store3', 'store2', 'store1']
        * });
        *```
        * @config {String[]}
        */
        syncApplySequence: [],
        orderedCrudStores: [],

        /**
         * true to write all fields from the record to the server. If set to false it will only send the fields that
         * were modified. Note that any fields that have `persist` set to false will still be ignored while those
         * with `critical` set to true will be included.
         * @config {Boolean}
         * @default
         */
        writeAllFields: false,
        crudIgnoreUpdates: 0,
        autoSyncSuspendCounter: 0,
        // Flag that shows if crud manager performed successful load request
        crudLoaded: false,
        createMissingRecords: false,
        autoSyncTimerId: null,
        applyingLoadResponse: false,
        applyingSyncResponse: false,
        callOnFunctions: true
      };
    }

    get isCrudManager() {
      return true;
    } //endregion
    //region Init

    construct(config = {}) {
      this._requestId = 0;
      this.activeRequests = {};
      this.crudStoresIndex = {};
      super.construct(config);
    }

    afterConstruct() {
      super.afterConstruct();

      if (this.autoLoad) {
        this.doAutoLoad();
      }
    } //endregion
    //region Load

    doAutoLoad() {
      return this.load().catch(response => {
        if (!response || !this.isValidResponse(response.rawResponse, response.response)) {
          throw response;
        }
      });
    } //endregion
    //region Store descriptors & index

    /**
     * Returns a registered store descriptor.
     * @param {String|Core.data.Store} storeId The store identifier or registered store instance.
     * @returns {Object} The descriptor of the store.
     */

    getStoreDescriptor(storeId) {
      if (!storeId) return null;
      if (storeId instanceof Store) return this.crudStores.find(storeDesc => storeDesc.store === storeId);
      if (typeof storeId === 'object') return this.crudStoresIndex[storeId.storeId];
      return this.crudStoresIndex[storeId] || this.getStoreDescriptor(Store.getStore(storeId));
    }

    fillStoreDescriptor(descriptor) {
      const {
        store
      } = descriptor,
            {
        storeIdProperty = this.storeIdProperty,
        modelClass
      } = store;

      if (!descriptor.storeId) {
        descriptor.storeId = store[storeIdProperty];
      }

      if (!descriptor.idField) {
        descriptor.idField = modelClass.idField;
      }

      if (!descriptor.phantomIdField) {
        descriptor.phantomIdField = modelClass.phantomIdField;
      }

      if (!descriptor.phantomParentIdField) {
        descriptor.phantomParentIdField = modelClass.phantomParentIdField;
      }

      if (!('writeAllFields' in descriptor)) {
        descriptor.writeAllFields = store.writeAllFields;
      }

      return descriptor;
    }

    updateCrudStoreIndex() {
      const crudStoresIndex = this.crudStoresIndex = {};
      this.crudStores.forEach(store => store.storeId && (crudStoresIndex[store.storeId] = store));
    } //endregion
    //region Store collection (add, remove, get & iterate)

    /**
     * Returns a registered store.
     * @param {String} storeId Store identifier.
     * @returns {Core.data.Store} Found store instance.
     */

    getCrudStore(storeId) {
      const storeDescriptor = this.getStoreDescriptor(storeId);
      return storeDescriptor === null || storeDescriptor === void 0 ? void 0 : storeDescriptor.store;
    }

    forEachCrudStore(fn, thisObj = this) {
      if (!fn) throw new Error('Iterator function must be provided');
      this.crudStores.every(store => fn.call(thisObj, store.store, store.storeId, store) !== false);
    }

    set crudStores(stores) {
      this._crudStores = [];
      this.addCrudStore(stores); // Ensure preconfigured stores stay stable at the start of the array when
      // addPrioritizedStore attempts to insert in order. Only featured gantt/scheduler stores
      // must participate in the ordering. If they were configured in, they must not move.

      for (const store of this._crudStores) {
        store.loadPriority = store.syncPriority = 0;
      }
    }

    get crudStores() {
      return this._crudStores;
    }

    get orderedCrudStores() {
      return this._orderedCrudStores;
    }

    set orderedCrudStores(stores) {
      return this._orderedCrudStores = stores;
    }

    set syncApplySequence(stores) {
      this._syncApplySequence = [];
      this.addStoreToApplySequence(stores);
    }

    get syncApplySequence() {
      return this._syncApplySequence;
    }

    internalAddCrudStore(store) {
      const me = this;
      let storeInfo; // if store instance provided

      if (store instanceof Store) {
        storeInfo = {
          store
        };
      } else if (typeof store === 'object') {
        // normalize sub-stores (if any)
        if (store.stores) {
          if (!Array.isArray(store.stores)) {
            store.stores = [store.stores];
          }

          store.stores.forEach((subStore, j) => {
            let subStoreInfo = subStore;

            if (typeof subStore === 'string') {
              subStoreInfo = {
                storeId: subStore
              };
            } // keep reference to the "master" store descriptor

            subStoreInfo.masterStoreInfo = store;
            store.stores[j] = subStoreInfo;
          });
        } else if (!store.store) {
          // not a store descriptor, assume it is a store config
          store = {
            storeId: store.id,
            store: new Store(store)
          };
        }

        storeInfo = store;
      } // if it's a store identifier
      else {
          storeInfo = {
            store: Store.getStore(store)
          };
        }

      me.fillStoreDescriptor(storeInfo); // store instance

      store = storeInfo.store; // if the store has "setCrudManager" hook - use it

      if (store.setCrudManager) {
        store.setCrudManager(me);
      } // otherwise decorate the store w/ "crudManager" property
      else {
          store.crudManager = me;
        } // Stores have a defaultConfig for pageSize. CrudManager does not support that.
      // TODO: PORT currently no support for paging.

      store.pageSize = null; // Prevent AjaxStores from performing their own CRUD operations

      if (me.overrideCrudStoreLoad && store.load) {
        store.load = store.commit = () => {};
      } // listen to store changes

      me.bindCrudStoreListeners(store);
      return storeInfo;
    }
    /**
     * Adds a store to the collection.
     *
     *```javascript
    * // append stores to the end of collection
    * crudManager.addCrudStore([
    *     store1,
    *     // storeId
    *     'bar',
    *     // store descriptor
    *     {
    *         storeId : 'foo',
    *         store   : store3
    *     },
    *     {
    *         storeId         : 'bar',
    *         store           : store4,
    *         // to write all fields of modified records
    *         writeAllFields  : true
    *     }
    * ]);
    *```
    *
    * **Note:** Order in which stores are kept in the collection is very essential sometimes.
    * Exactly in this order the loaded data will be put into each store.
     *
     * When adding a store to the CrudManager, make sure the server response format is correct for `load` and `sync` requests.
     * Learn more in the [Working with data](#guides/data/crud_manager.md#loading-data) guide.
     *
    * @param {Core.data.Store|String|Object|Core.data.Store[]|String[]|Object[]} store
    * A store or list of stores. Each store might be specified by its instance, `storeId` or _descriptor_.
    * The _store descriptor_ is an object having following properties:
    * @param {String} store.storeId The store identifier that will be used as a key in requests.
    * @param {Core.data.Store} store.store The store itself.
    * @param {String} [store.idField] The idField of the store. If not specified will be taken from the store model.
    * @param {String} [store.phantomIdField] The field holding unique Ids of phantom records (if store has such model).
    * @param {Boolean} [store.writeAllFields] Set to true to write all fields from modified records
    * @param {Number} [position] The relative position of the store. If `fromStore` is specified the this position will be taken relative to it.
    * If not specified then store(s) will be appended to the end of collection.
    * Otherwise it will be just a position in stores collection.
    *
    * ```javascript
    * // insert stores store4, store5 to the start of collection
    * crudManager.addCrudStore([ store4, store5 ], 0);
    * ```
    *
    * @param {String|Core.data.Store|Object} [fromStore] The store relative to which position should be calculated. Can be defined as a store identifier, instance or descriptor (the result of {@link #function-getStoreDescriptor} call).
    *
    * ```javascript
    * // insert store6 just before a store having storeId equal to 'foo'
    * crudManager.addCrudStore(store6, 0, 'foo');
    *
    * // insert store7 just after store3 store
    * crudManager.addCrudStore(store7, 1, store3);
    * ```
    */

    addCrudStore(store, position, fromStore) {
      if (!store) return;
      if (!Array.isArray(store)) store = [store];
      if (!store.length) return;
      const me = this,
            stores = store.map(me.internalAddCrudStore, me); // if no position specified then append stores to the end

      if (typeof position === 'undefined') {
        me.crudStores.push(...stores);
      } // if position specified
      else {
          let pos = position; // if specified the store relative to which we should insert new one(-s)

          if (fromStore) {
            if (fromStore instanceof Store || typeof fromStore !== 'object') fromStore = me.getStoreDescriptor(fromStore); // get its position

            pos += me.crudStores.indexOf(fromStore);
          } // insert new store(-s)
          //me.crudStores.splice.apply(me.crudStores, [].concat([pos, 0], stores));

          me.crudStores.splice(pos, 0, ...stores);
        }

      me.orderedCrudStores.push(...stores);
      me.updateCrudStoreIndex();
    } // Adds configured scheduler stores to the store collection ensuring correct order
    // unless they're already registered.

    addPrioritizedStore(store) {
      const me = this;

      if (!me.hasCrudStore(store)) {
        this.addCrudStore(store, ArrayHelper.findInsertionIndex(store, me.crudStores, storeLoadSortFn));
      }

      if (!me.hasApplySequenceStore(store)) {
        this.addStoreToApplySequence(store, ArrayHelper.findInsertionIndex(store, me.syncApplySequence, storeSyncSortFn));
      }
    }

    hasCrudStore(store) {
      var _this$crudStores;

      return (_this$crudStores = this.crudStores) === null || _this$crudStores === void 0 ? void 0 : _this$crudStores.some(s => s === store || s.store === store || s.storeId === store);
    }
    /**
     * Removes a store from collection. If the store was registered in alternative sync sequence list
     * it will be removed from there as well.
     *
     * ```javascript
     *    // remove store having storeId equal to "foo"
     *    crudManager.removeCrudStore("foo");
     *
     *    // remove store3
     *    crudManager.removeCrudStore(store3);
     * ```
     *
     * @param {Object|String|Core.data.Store} store The store to remove. Either the store descriptor, store identifier or store itself.
     */

    removeCrudStore(store) {
      const me = this,
            stores = me.crudStores,
            foundStore = stores.find(s => s === store || s.store === store || s.storeId === store);

      if (foundStore) {
        // unbind store listeners
        me.unbindCrudStoreListeners(foundStore.store);
        delete me.crudStoresIndex[foundStore.storeId];
        ArrayHelper.remove(stores, foundStore);

        if (me.syncApplySequence) {
          me.removeStoreFromApplySequence(store);
        }
      } else {
        throw new Error('Store not found in stores collection');
      }
    } //endregion
    //region Store listeners

    bindCrudStoreListeners(store) {
      store.on({
        name: store.id,
        change: 'onCrudStoreChange',
        destroy: 'onCrudStoreDestroy',
        thisObj: this
      });
    }

    unbindCrudStoreListeners(store) {
      this.detachListeners(store.id);
    } //endregion
    //region Apply sequence

    /**
     * Adds a store to the alternative sync responses apply sequence.
     * By default the order in which sync responses are applied to the stores is the same as they registered in.
     * But this order can be changes either on construction step using {@link #config-syncApplySequence} option
     * or but calling this method.
     *
     * **Please note**, that if the sequence was not initialized before this method call then
     * you will have to do it yourself like this for example:
     *
     *    ```javascript
     *    // alternative sequence was not set for this crud manager
     *    // so let's fill it with existing stores keeping the same order
     *    crudManager.addStoreToApplySequence(crudManager.crudStores);
     *
     *    // and now we can add our new store
     *
     *    // we will load its data last
     *    crudManager.addCrudStore(someNewStore);
     *    // but changes to it will be applied first
     *    crudManager.addStoreToApplySequence(someNewStore, 0);
     *    ```
     * add registered stores to the sequence along with the store(s) you want to add
     *
     * @param {Core.data.Store|Object|Core.data.Store[]|Object[]} store The store to add or its _descriptor_ (or array of stores or descriptors).
     * Where _store descriptor_ is an object having following properties:
     * @param {String} store.storeId The store identifier that will be used as a key in requests.
     * @param {Core.data.Store} store.store The store itself.
     * @param {String} [store.idField] The idField of the store. If not specified will be taken from the store model.
     * @param {String} [store.phantomIdField] The field holding unique Ids of phantom records (if store has such model).
     * @param {Number} [position] The relative position of the store. If `fromStore` is specified the this position will be taken relative to it.
    * If not specified then store(s) will be appended to the end of collection.
    * Otherwise it will be just a position in stores collection.
    *
    *    ```javascript
    *    // insert stores store4, store5 to the start of sequence
    *    crudManager.addStoreToApplySequence([ store4, store5 ], 0);
    *    ```
    * @param {String|Core.data.Store|object} [fromStore] The store relative to which position should be calculated. Can be defined as a store identifier, instance or its descriptor (the result of {@link #function-getStoreDescriptor} call).
    *
    *    ```javascript
    *    // insert store6 just before a store having storeId equal to 'foo'
    *    crudManager.addStoreToApplySequence(store6, 0, 'foo');
    *
    *    // insert store7 just after store3 store
    *    crudManager.addStoreToApplySequence(store7, 1, store3);
    *    ```
    */

    addStoreToApplySequence(store, position, fromStore) {
      if (!store) return;
      if (!Array.isArray(store)) store = [store];
      const me = this,
            // loop over list of stores to add
      data = store.reduce((collection, store) => {
        const s = me.getStoreDescriptor(store);
        if (s) collection.push(s);
        return collection;
      }, []); // if no position specified then append stores to the end

      if (typeof position === 'undefined') {
        me.syncApplySequence.push(...data); // if position specified
      } else {
        let pos = position; // if specified the store relative to which we should insert new one(-s)

        if (fromStore) {
          if (fromStore instanceof Store || typeof fromStore !== 'object') fromStore = me.getStoreDescriptor(fromStore); // get its position

          pos += me.syncApplySequence.indexOf(fromStore);
        } // insert new store(-s)
        //me.syncApplySequence.splice.apply(me.syncApplySequence, [].concat([pos, 0], data));

        me.syncApplySequence.splice(pos, 0, ...data);
      }

      const sequenceKeys = me.syncApplySequence.map(desc => desc.storeId);
      me.orderedCrudStores = [...me.syncApplySequence];
      me.crudStores.forEach(desc => {
        if (!sequenceKeys.includes(desc.storeId)) {
          me.orderedCrudStores.push(desc);
        }
      });
    }
    /**
     * Removes a store from the alternative sync sequence.
     *
     * ```javascript
     * // remove store having storeId equal to "foo"
     * crudManager.removeStoreFromApplySequence("foo");
     * ```
     *
     * @param {Object|String|Core.data.Store} store The store to remove. Either the store descriptor, store identifier or store itself.
     */

    removeStoreFromApplySequence(store) {
      const index = this.syncApplySequence.findIndex(s => s === store || s.store === store || s.storeId === store);

      if (index > -1) {
        this.syncApplySequence.splice(index, 1); // ordered crud stores list starts with syncApplySequence, we can use same index

        this.orderedCrudStores.splice(index, 1);
      }
    }

    hasApplySequenceStore(store) {
      return this.syncApplySequence.some(s => s === store || s.store === store || s.storeId === store);
    } //endregion
    //region Events
    // onNodeRemove(oldParent) {
    //     var treeStore = oldParent && oldParent.getTreeStore();
    //     // "noderemove" event is fired too early and getRemovedRecords() don't not have the removed node yet
    //     // so we wait till tree store "endupdate" event and only then invoke "onCrudStoreChange" method
    //     treeStore && treeStore.on('endupdate', this.onCrudStoreChange, this, { once : true });
    // }
    // onStoreUpdate(store, record, operation, fields) {
    //     if ((!store.isTreeStore || record !== store.getRoot())) {
    //         // If only a single field was changed, make sure it's a persistable field to avoid full scan of the store
    //         // Collapsing/expanding a tree node will trigger this behavior otherwise
    //         var isSingleNonPersistField = fields && fields.length === 1 && record.getField(fields[0]) && !record.getField(fields[0]).persist;
    //
    //         if (!isSingleNonPersistField) {
    //             this.onCrudStoreChange();
    //         }
    //     }
    // }
    // onTreeStoreInsertOrAppend(parent, child) {
    //     if (!child.isRoot()) {
    //         this.onCrudStoreChange();
    //     }
    // }
    // Remove stores that are destroyed, to not try and apply response changes etc to them

    onCrudStoreDestroy({
      source: store
    }) {
      this.removeCrudStore(store);
    }

    onCrudStoreChange() {
      const me = this;

      if (me.crudIgnoreUpdates) {
        return;
      }
      /**
       * Fires when data in any of the registered data stores is changed.
       * ```javascript
       *     crudManager.on('hasChanges', function (crud) {
       *         // enable persist changes button when some store gets changed
       *         saveButton.enable();
       *     });
       * ```
       * @event hasChanges
       * @param {Scheduler.crud.AbstractCrudManager} source The CRUD manager.
       */

      if (me.crudStoreHasChanges()) {
        me.trigger('hasChanges');

        if (me.autoSync) {
          me.scheduleAutoSync();
        }
      } else {
        me.trigger('noChanges');
      }
    }
    /**
     * Suspends automatic sync upon store changes. Can be called multiple times (it uses an internal counter).
     */

    suspendAutoSync() {
      this.autoSyncSuspendCounter++;
      this.forEachCrudStore(store => store.suspendAutoCommit());
    }
    /**
     * Resumes automatic sync upon store changes. Will trigger commit if the internal counter is 0.
     */

    resumeAutoSync(doSync = true) {
      const me = this;
      me.autoSyncSuspendCounter--;

      if (me.autoSyncSuspendCounter <= 0) {
        me.autoSyncSuspendCounter = 0;

        if (me.autoSync && doSync) {
          me.forEachCrudStore(store => store.resumeAutoCommit(doSync));
          me.sync();
        }
      }
    }

    scheduleAutoSync() {
      const me = this; // add deferred call if it's not scheduled yet

      if (!me.autoSyncTimerId && !me.autoSyncSuspendCounter) {
        me.autoSyncTimerId = me.setTimeout(() => {
          me.autoSyncTimerId = null;
          me.sync().catch(error => {
            if (!error || !me.isValidResponse(error.rawResponse, error.response)) {
              throw error;
            }
          });
        }, me.autoSyncTimeout);
      }
    }

    async internalOnResponse(requestType, responseText, fetchOptions, options) {
      // reset last requested package ID
      const me = this,
            response = responseText ? me.decode(responseText) : null;

      if (!response || !response.success) {
        /**
         * Fires when a request fails.
         * @event requestFail
         * @param {Scheduler.crud.AbstractCrudManager} source The CRUD manager instance.
         * @param {String} requestType The request type (`sync` or `load`).
         * @param {Object} response The decoded server response object.
         * @param {String} responseText The raw server response text
         * @param {Object} responseOptions The response options.
         */
        me.trigger('requestFail', {
          requestType,
          response,
          responseText,
          responseOptions: fetchOptions
        });
        /**
         * Fires when a {@link #function-load load request} fails.
         * @event loadFail
         * @param {Scheduler.crud.AbstractCrudManager} source The CRUD manager instance.
         * @param {Object} response The decoded server response object.
         * @param {String} responseText The raw server response text
         * @param {Object} responseOptions The response options.
         * @params {Object} options Options provided to the {@link #function-load} method.
         */

        /**
         * Fires when a {@link #function-sync sync request} fails.
         * @event syncFail
         * @param {Scheduler.crud.AbstractCrudManager} source The CRUD manager instance.
         * @param {Object} response The decoded server response object.
         * @param {String} responseText The raw server response text
         * @param {Object} responseOptions The response options.
         */

        me.trigger(requestType + 'Fail', {
          response,
          responseOptions: fetchOptions,
          responseText,
          options
        });
      } else if (me.trigger('beforeResponseApply', {
        requestType,
        response
      }) !== false && me.trigger('before' + StringHelper.capitalize(requestType) + 'Apply', {
        response,
        options
      }) !== false) {
        me.crudRevision = response.revision;
        await me.applyResponse(requestType, response, options);
        /**
         * Fires on successful request completion after data gets applied to the stores.
         * @event requestDone
         * @param {Scheduler.crud.AbstractCrudManager} source The CRUD manager.
         * @param {String} requestType The request type (`sync` or `load`).
         * @param {Object} response The decoded server response object.
         * @param {Object} responseOptions The server response options.
         */

        me.trigger('requestDone', {
          requestType,
          response,
          responseOptions: fetchOptions
        });
        /**
         * Fires on successful {@link #function-load load request} completion after data gets loaded to the stores.
         * @event load
         * @param {Scheduler.crud.AbstractCrudManager} source The CRUD manager.
         * @param {Object} response The decoded server response object.
         * @param {Object} responseOptions The server response options.
         * @params {Object} options Options provided to the {@link #load} method.
         */

        /**
         * Fires on successful {@link #function-sync sync request} completion.
         * @event sync
         * @param {Scheduler.crud.AbstractCrudManager} source The CRUD manager.
         * @param {Object} response The decoded server response object.
         * @param {Object} responseOptions The server response options.
         */

        me.trigger(requestType, {
          response,
          responseOptions: fetchOptions,
          options
        });

        if (requestType === 'load' || !me.crudStoreHasChanges()) {
          /**
           * Fires when registered stores get into state when they don't have any
           * not persisted change. This happens after {@link #function-load load} or {@link #function-sync sync} request
           * completion. Or this may happen after a record update which turns its fields back to their original state.
           *
           * ```javascript
           *     crudManager.on('nochanges', function (crud) {
           *         // disable persist changes button when there is no changes
           *         saveButton.disable();
           *     });
           * ```
           *
           * @event noChanges
           * @param {Scheduler.crud.AbstractCrudManager} source The CRUD manager.
           */
          me.trigger('noChanges');
        }
      }

      me.activeRequests[requestType] = null;
      return response;
    }

    async internalOnLoad(responseText, fetchOptions, options) {
      // Successful load request, mark crud manager as loaded
      this.crudLoaded = true;
      return this.internalOnResponse('load', responseText, fetchOptions, options);
    }

    async internalOnSync(responseText, fetchOptions, options) {
      return this.internalOnResponse('sync', responseText, fetchOptions, options);
    } //endregion
    //region Changes tracking

    suspendChangesTracking() {
      this.crudIgnoreUpdates++;
    }

    resumeChangesTracking(skipChangeCheck) {
      if (this.crudIgnoreUpdates && ! --this.crudIgnoreUpdates && !skipChangeCheck) {
        this.onCrudStoreChange();
      }
    }

    get isBatchingChanges() {
      return this.crudIgnoreUpdates > 0;
    }
    /**
     * Returns `true` if any of registered stores (or some particular store) has non persisted changes.
     *
     *    ```javascript
     *    // if we have any unsaved changes
     *    if (crudManager.crudStoreHasChanges()) {
     *        // persist them
     *        crudManager.sync();
     *    // otherwise
     *    } else {
     *        alert("There are no unsaved changes...");
     *    }
     *    ```
     *
     * @param {String|Core.data.Store} [storeId] The store identifier or store instance to check changes for.
     * If not specified then will check changes for all of the registered stores.
     * @returns {Boolean} `true` if there are not persisted changes.
     */

    crudStoreHasChanges(storeId) {
      return storeId ? this.isCrudStoreDirty(this.getCrudStore(storeId)) : this.crudStores.some(this.isCrudStoreDirty);
    }

    isCrudStoreDirty(store) {
      return Boolean(store === null || store === void 0 ? void 0 : store.store.changes);
    } //endregion
    //region Load

    emitCrudStoreEvents(stores, eventName, eventParams) {
      const event = {
        action: 'read' + eventName,
        ...eventParams
      };

      for (const store of this.crudStores) {
        if (stores.includes(store.storeId)) {
          store.store.trigger(eventName, event);
        }
      }
    }

    getLoadPackage(options) {
      const pack = {
        type: 'load',
        requestId: this.requestId
      },
            stores = this.crudStores,
            optionsCopy = Object.assign({}, options); // This is a special option which does not apply to a store.
      // It's used as options to the AjaxTransport#sendRequest method

      delete optionsCopy.request;
      pack.stores = stores.map(store => {
        var _store$store;

        const opts = optionsCopy === null || optionsCopy === void 0 ? void 0 : optionsCopy[store.storeId],
              pageSize = store.pageSize || ((_store$store = store.store) === null || _store$store === void 0 ? void 0 : _store$store.pageSize); // TODO: PORT currently no support for remote filters
        // if the store uses remote filtering
        // if (store.store.remoteFilter && filterParam) {
        //
        //     opts = opts || {};
        //
        //     var filters = [];
        //
        //     store.store.getFilters().each(function(f) {
        //         filters.push(f.serialize());
        //     });
        //
        //     // put filters info into the package
        //     opts[filterParam] = filters;
        // }
        // TODO: PORT currently no support for paging

        if (opts || pageSize) {
          const params = Object.assign({
            storeId: store.storeId,
            page: 1
          }, opts);

          if (pageSize) {
            params.pageSize = pageSize;
          }

          store.currentPage = params.page; // Remove from common request options

          if (opts) {
            delete optionsCopy[store.storeId];
          }

          return params;
        }

        return store.storeId;
      }); // Apply common request options

      Object.assign(pack, optionsCopy);
      return pack;
    }

    loadCrudStore(store, data, options) {
      const rows = data === null || data === void 0 ? void 0 : data.rows;

      if (options !== null && options !== void 0 && options.append || data !== null && data !== void 0 && data.append) {
        store.add(rows);
      } else {
        store.data = rows;
      }

      store.trigger('load', {
        data: rows
      });
    }

    loadDataToCrudStore(storeDesc, data, options) {
      const me = this,
            store = storeDesc.store,
            // nested stores list
      subStores = storeDesc.stores,
            idField = storeDesc.idField || 'id',
            //model && model.meta.idField || 'id',
      isTree = store.tree,
            rows = data === null || data === void 0 ? void 0 : data.rows;
      store.__loading = true;

      if (rows) {
        let subData;

        if (subStores) {
          subData = me.getSubStoresData(rows, subStores, idField, isTree);
        }

        me.loadCrudStore(store, data, options, storeDesc);

        if (subData) {
          // load sub-stores as well (if we have them)
          subData.forEach(sub => {
            me.loadDataToCrudStore(Object.assign({
              store: store.getById(sub.id).get(sub.storeDesc.storeId) // TODO: PORT have to check what this does

            }, sub.storeDesc), sub.data);
          });
        }
      }

      store.__loading = false;
    }
    /**
     * Loads data to the Crud Manager
     * @param {Object} response A simple object representing the data.
     * The object structure matches the decoded `load` response structure:
     *
     * // load static data into crudManager
     * crudManager.loadCrudManagerData({
     *     success   : true,
     *     resources : {
     *         rows : [
     *             { id : 1, name : 'John' },
     *             { id : 2, name : 'Abby' }
     *         ]
     *     }
     * });
     * @param {Object} [options] Extra data loading options.
     */

    loadCrudManagerData(response, options = {}) {
      // we don't want to react to store changes during loading of them
      this.suspendChangesTracking(); // we load data to the stores in the order they're kept in this.stores array

      this.crudStores.forEach(storeDesc => {
        const storeId = storeDesc.storeId,
              data = response[storeId];

        if (data) {
          this.loadDataToCrudStore(storeDesc, data, options[storeId]);
        }
      });
      this.resumeChangesTracking(true);
    }
    /**
     * Returns true if the crud manager is currently loading data
     * @property {Boolean}
     * @readonly
     * @category CRUD
     */

    get isCrudManagerLoading() {
      return Boolean(this.activeRequests.load || this.applyingLoadResponse);
    }

    get isCrudManagerSyncing() {
      return Boolean(this.activeRequests.sync || this.applyingSyncResponse);
    }

    get isLoadingOrSyncing() {
      return Boolean(this.activeRequests.load || this.activeRequests.sync);
    }
    /**
     * Loads data to the stores registered in the crud manager. For example:
     *
     * ```javascript
     * crudManager.load(
     *     // here are request parameters
     *     {
     *         store1 : { append : true, page : 3, smth : 'foo' },
     *         store2 : { page : 2, bar : '!!!' }
     *     }
     * ).then(
     *     () => alert('OMG! It works!'),
     *     ({ response, cancelled }) => console.log(`Error: ${cancelled ? 'Cancelled' : response.message}`)
     * );
     * ```
     *
     * ** Note: ** If there is an incomplete load request in progress then system will try to cancel it by calling {@link #function-cancelRequest}.
     * @param {Object} [options] The request parameters. This argument can be omitted like this:
     *
     * ```javascript
     * crudManager.load().then(
     *     () => alert('OMG! It works!'),
     *     ({ response, cancelled }) => console.log(`Error: ${cancelled ? 'Cancelled' : response.message}`)
     * );
     * ```
     *
     * When presented it should be an object where keys are store Ids and values are, in turn, objects
     * of parameters related to the corresponding store. These parameters will be transferred in each
     * store's entry in the `stores` property of the POST data.
     *
     * A special property, `request` may reference an object which contains options to merge
     * into the options which are passed to {@link Scheduler.crud.transport.AjaxTransport#function-sendRequest}.
     *
     * ```javascript
     * {
     *     store1 : { page : 3, append : true, smth : 'foo' },
     *     store2 : { page : 2, bar : '!!!' },
     *     request : {
     *         params : {
     *             startDate : '2021-01-01'
     *         }
     *     }
     * },
     * ```
     *
     * Additionally for flat stores `append: true` can be specified to add loaded records to the existing records, default is to remove corresponding store's existing records first.
     * **Please note** that for delta loading you can also use an {@link #config-trackResponseType alternative approach}.
     * @returns {Promise} Promise, which is resolved if request was successful.
     * Both the resolve and reject functions are passed a `state` object. State object has following structure:
     *
     *     {
     *         cancelled       : Boolean, // **optional** flag, which is present when promise was rejected
     *         rawResponse     : String,  // raw response from ajax request, either response xml or text
     *         rawResponseText : String,  // raw response text as String from ajax request
     *         response        : Object,  // processed response in form of object
     *         options         : Object   // options, passed to load request
     *     }
     *
     * If promise was rejected by {@link #event-beforeLoad} event, `state` object will have structure:
     *
     *     {
     *         cancelled : true
     *     }
     *
     */

    load(options) {
      const me = this,
            pack = me.getLoadPackage(options);
      return new Promise((resolve, reject) => {
        /**
         * Fires before {@link #function-load load request} is sent. Return `false` to cancel load request.
         * @event beforeLoad
         * @param {Scheduler.crud.AbstractCrudManager} source The CRUD manager.
         * @param {Object} pack The data package which contains data for all stores managed by the crud manager.
         */
        if (me.trigger('beforeLoad', {
          pack
        }) !== false) {
          // if another load request is in progress let's cancel it
          const {
            load
          } = me.activeRequests;

          if (load) {
            me.cancelRequest(load.desc, load.reject);
            me.trigger('loadCanceled', {
              pack
            });
          } // TODO: refactor this

          const request = Objects.assign({
            data: me.encode(pack),
            type: 'load',
            success: me.onLoadSuccess,
            failure: me.onLoadFailure,
            thisObj: me
          }, options === null || options === void 0 ? void 0 : options.request);
          me.activeRequests.load = {
            options,
            pack,
            resolve,

            reject(...args) {
              // sendRequest will start a fetch promise, which we cannot reject from here. In fact what we
              // need to do, is to make fetch.then() to not call any real handlers. Which is what we do here.
              request.success = request.failure = null;
              reject(...args);
            },

            id: pack.requestId,
            desc: me.sendRequest(request)
          };
          me.emitCrudStoreEvents(pack.stores, 'loadStart');
          me.trigger('loadStart', {
            pack
          });
        } else {
          /**
           * Fired after {@link #function-load load request} was canceled by some {@link #event-beforeLoad}
           * listener or due to incomplete prior load request.
           * @event loadCanceled
           * @param {Scheduler.crud.AbstractCrudManager} source The CRUD manager.
           * @param {Object} pack The data package which contains data for all stores managed by the crud manager.
           */
          me.trigger('loadCanceled', {
            pack
          });
          me.processPromise(reject, null, {
            action: 'load',
            cancelled: true
          });
        }
      });
    }

    async onLoadSuccess(rawResponse, fetchOptions) {
      if (this.isDestroyed) return;
      let responseText = '';
      const request = this.activeRequests.load,
            {
        options
      } = request;
      await rawResponse.text().then(value => responseText = value).catch(nullFn); // since we break the method w/ promises chain ..need to check if the instance is not destroyed in the meantime

      if (this.isDestroyed) return;
      const response = await this.internalOnLoad(responseText, fetchOptions, options); // since we break the method w/ promises chain ..need to check if the instance is not destroyed in the meantime

      if (this.isDestroyed) return;
      this.emitCrudStoreEvents(request.pack.stores, 'afterRequest');

      if (response !== null && response !== void 0 && response.success) {
        request.resolve({
          response,
          rawResponse,
          responseText,
          options
        });
      } else {
        this.processPromise(request.reject, request.resolve, {
          action: 'load',
          cancelled: false,
          response,
          rawResponse,
          responseText,
          options
        });
      }
    }

    async onLoadFailure(rawResponse, fetchOptions = {}) {
      var _fetchOptions$abortCo;

      if (this.isDestroyed) return;
      const request = this.activeRequests.load,
            {
        options
      } = request,
            signal = (_fetchOptions$abortCo = fetchOptions.abortController) === null || _fetchOptions$abortCo === void 0 ? void 0 : _fetchOptions$abortCo.signal,
            wasAborted = Boolean(signal === null || signal === void 0 ? void 0 : signal.aborted);

      if (!wasAborted) {
        let responseText = '';

        if (rawResponse) {
          await rawResponse.text().then(value => responseText = value).catch(nullFn);
        } // since we break the method w/ promises chain ..need to check if the instance is not destroyed in the meantime

        if (this.isDestroyed) return;
        const response = await this.internalOnLoad(responseText, fetchOptions, options); // since we break the method w/ promises chain ..need to check if the instance is not destroyed in the meantime

        if (this.isDestroyed) return;
        this.emitCrudStoreEvents(request.pack.stores, 'afterRequest', {
          exception: true
        });
        this.processPromise(request.reject, null, {
          action: 'load',
          cancelled: wasAborted,
          response,
          rawResponse,
          responseText,
          options,
          request
        });
      }
    }

    getSubStoresData(rows, subStores, idField, isTree) {
      if (!rows) return;
      const result = [];

      function processRow(row, subStores) {
        subStores.forEach(subStore => {
          const storeId = subStore.storeId; // if row contains data for this sub-store

          if (row[storeId]) {
            // keep them for the later loading
            result.push({
              id: row[idField],
              storeDesc: subStore,
              data: row[storeId]
            }); // and remove reference from the row

            delete row[storeId];
          }
        });
      } // if it's a TreeStore

      if (isTree) {
        // loop over nodes
        rows.forEach(row => {
          processRow(row, subStores); // also let's grab sub-stores from node children

          const childrenSubData = this.getSubStoresData(row.children, subStores, idField, true);

          if (childrenSubData) {
            result.push(...childrenSubData);
          }
        }); // if it's a "flat" store
      } else {
        rows.forEach(row => processRow(row, subStores));
      }

      return result;
    } //endregion
    //region Changes (prepare, process, get)

    prepareAdded(list, storeInfo) {
      const {
        store,
        stores
      } = storeInfo,
            {
        isTree
      } = store,
            phantomIdField = storeInfo.phantomIdField || this.phantomIdField,
            phantomParentIdField = storeInfo.phantomParentIdField || this.phantomParentIdField;
      return list.filter(record => record.isValid).map(record => {
        const cls = record.constructor,
              data = Object.assign(record.persistableData, {
          [phantomIdField]: record.id
        });

        if (isTree) {
          const {
            parent
          } = record;

          if (parent && !parent.isRoot && parent.isPhantom) {
            data[phantomParentIdField] = parent.id;
          }
        }

        if (this.resetIdsBeforeSync) delete ObjectHelper.deletePath(data, cls.getFieldDataSource(cls.idField)); // if the store has embedded ones

        if (stores) {
          this.processSubStores(record, data, stores);
        }

        return data;
      });
    }

    prepareUpdated(list, storeInfo) {
      const {
        store,
        stores
      } = storeInfo,
            {
        isTree
      } = store,
            writeAllFields = storeInfo.writeAllFields || storeInfo.writeAllFields !== false && this.writeAllFields,
            phantomParentIdField = storeInfo.phantomParentIdField || this.phantomParentIdField; // TODO: root node included into store.modified
      // need to get rid of it since we don't persist it

      if (storeInfo.store.tree) {
        const rootNode = storeInfo.store.rootNode;
        list = list.filter(record => record !== rootNode);
      }

      return list.filter(record => record.isValid).reduce((data, record) => {
        let recordData; // write all fields

        if (writeAllFields) {
          recordData = record.persistableData;
        } else {
          recordData = record.modificationData; // TODO: PORT critical fields
          // critical fields should always be presented
          // const criticalFields = record.getCriticalFields();
          //
          // for (var j = 0; j < criticalFields.length; j++) {
          //     field = criticalFields[j];
          //
          //     if (field.serialize) {
          //         data[field.getName()] = field.serialize(record.get(field.getName()), record);
          //     } else {
          //         data[field.getName()] = record.get(field.getName());
          //     }
          // }
        }

        if (isTree) {
          const {
            parent
          } = record;

          if (parent && !parent.isRoot && parent.isPhantom) {
            recordData[phantomParentIdField] = parent.id;
          }
        } // if the store has embedded ones

        if (stores) {
          this.processSubStores(record, recordData, stores);
        } // recordData can be null

        if (!ObjectHelper.isEmpty(recordData)) {
          data.push(recordData);
        }

        return data;
      }, []);
    }

    prepareRemoved(list) {
      return list.map(record => {
        const cls = record.constructor;
        return ObjectHelper.setPath({}, cls.getFieldDataSource(cls.idField), record.id);
      });
    }

    processSubStores(record, data, stores) {
      stores.forEach(store => {
        const id = store.storeId,
              subStore = record.get(id); // if embedded store is assigned to the record

        if (subStore) {
          // let's collect its changes as well
          const changes = this.getCrudStoreChanges(Object.assign({
            store: subStore
          }, store));

          if (changes) {
            data[id] = Object.assign(changes, {
              $store: true
            });
          } else {
            delete data[id];
          }
        } else {
          delete data[id];
        }
      });
    }

    getCrudStoreChanges(storeDescriptor) {
      const {
        store
      } = storeDescriptor;
      let {
        added = [],
        modified: updated = [],
        removed = []
      } = store.changes || {},
          // sub-stores
      result;
      if (added.length) added = this.prepareAdded(added, storeDescriptor);
      if (updated.length) updated = this.prepareUpdated(updated, storeDescriptor);
      if (removed.length) removed = this.prepareRemoved(removed); // if this store has changes

      if (added.length || updated.length || removed.length) {
        result = {};
        if (added.length) result.added = added;
        if (updated.length) result.updated = updated;
        if (removed.length) result.removed = removed;
      }

      return result;
    }

    getChangeSetPackage() {
      const pack = {
        type: 'sync',
        requestId: this.requestId,
        revision: this.crudRevision
      };
      let found = 0;
      this.crudStores.forEach(store => {
        const changes = this.getCrudStoreChanges(store);

        if (changes) {
          found++;
          pack[store.storeId] = changes;
        }
      });
      return found ? pack : null;
    } //endregion
    //region Apply

    applyChangesToRecord(record, rawChanges, stores) {
      const me = this,
            modelClass = record.constructor,
            {
        fieldDataSourceMap
      } = modelClass,
            recProto = modelClass.prototype,
            changes = {},
            data = record.data,
            done = {
        [me.phantomIdField]: true
      };
      let hasChanges; // if this store has sub-stores assigned to some fields

      if (stores) {
        // then first we apply changes to that stores
        stores.forEach(store => {
          const name = store.storeId;

          if (Object.prototype.hasOwnProperty.call(rawChanges, name)) {
            // remember that we processed this field
            done[name] = true;
            const subStore = record.get(name);

            if (subStore) {
              me.applyChangesToStore(Object.assign({
                store: subStore
              }, store), rawChanges[name]);
            } else {
              console.log('Can\'t find store for the response sub-package');
            }
          }
        });
      }

      const rawChangesFiltered = {};

      for (const key in rawChanges) {
        if (Object.prototype.hasOwnProperty.call(rawChanges, key) && !done[key]) {
          rawChangesFiltered[key] = rawChanges[key];
        }
      }

      const rowChangesSimplePaths = ObjectHelper.pathifyKeys(rawChangesFiltered); // Collect the changes into a change set for field names.

      for (const dataSource in rowChangesSimplePaths) {
        const field = fieldDataSourceMap[dataSource],
              propName = field ? field.name : dataSource,
              value = modelClass.processField(propName, rowChangesSimplePaths[dataSource]),
              oldValue = dataSource in recProto ? record[propName] : ObjectHelper.getPath(data, dataSource);

        if (!(field !== null && field !== void 0 && field.isEqual ? field.isEqual(oldValue, value) : ObjectHelper.isEqual(oldValue, value))) {
          hasChanges = true;
          changes[propName] = value;
        }
      }

      if (hasChanges) {
        me.suspendChangesTracking(); // Set each field separately until https://app.assembla.com/spaces/bryntum/tickets/9123 is fixed.

        for (const fieldName in changes) {
          record[fieldName] = changes[fieldName];
        } // TODO: Re-enable record.set when https://app.assembla.com/spaces/bryntum/tickets/9123 is fixed.
        // Set fields one go
        // record.set(changes);

        me.resumeChangesTracking(true);
      } // Clear changes only for the passed record,
      // not descendant nodes.
      // TODO: they *might* also be genuinely new
      // so might have to stay.

      record.clearChanges(true, false);
    }

    applyRemovals(store, removedEntries, context) {
      const {
        removed: removedStash,
        modelClass
      } = store,
            dataSource = modelClass.getFieldDataSource(modelClass.idField);
      let applied = 0;
      removedEntries === null || removedEntries === void 0 ? void 0 : removedEntries.forEach(removedEntry => {
        const id = removedEntry[dataSource];
        let done = false; // just remove the record from the removed stash

        if (removedStash.includes(id)) {
          removedStash.remove(id);
          done = true; // number of removals applied

          applied++;
        } // if responded removed record isn`t found in store.removed
        // probably don't removed on the client side yet (server driven removal)

        if (!done) {
          const record = store.getById(id);

          if (record) {
            this.suspendChangesTracking();
            record.remove();
            removedStash.remove(record); // number of removals applied

            applied++;
            this.resumeChangesTracking(true);
          } else {
            console.log('Can\'t find record to remove from the response package');
          }
        }
      });
      return applied;
    }

    getRowsToApplyChangesTo({
      store,
      storeId
    }, storeResponse) {
      var _me$activeRequests$sy, _rows, _removed;

      const me = this,
            {
        modelClass
      } = store,
            idDataSource = modelClass.getFieldDataSource(modelClass.idField),
            // request data
      {
        updated: requestUpdated,
        removed: requestRemoved
      } = ((_me$activeRequests$sy = me.activeRequests.sync) === null || _me$activeRequests$sy === void 0 ? void 0 : _me$activeRequests$sy.pack[storeId]) || {};
      let rows, removed; // If the response contains the store section

      if (storeResponse) {
        const respondedIds = {}; // responded record changes/removals

        rows = storeResponse.rows || [];
        removed = storeResponse.removed || []; // Collect hash w/ identifiers of responded records

        [...rows, ...removed].forEach(responseRecord => {
          const id = ObjectHelper.getPath(responseRecord, idDataSource);
          respondedIds[id] = true;
        }); // If it's told to support providing server changes only in response
        // CrudManager should collect other records to commit from current requested data

        if (me.supportShortSyncResponse) {
          // append records requested to update (if not there already)
          requestUpdated === null || requestUpdated === void 0 ? void 0 : requestUpdated.forEach(data => {
            const id = ObjectHelper.getPath(data, idDataSource); // if response doesn't include

            if (!respondedIds[id]) {
              rows.push({
                [idDataSource]: id
              });
            }
          }); // append records requested to remove (if not there already)

          requestRemoved === null || requestRemoved === void 0 ? void 0 : requestRemoved.forEach(data => {
            const id = ObjectHelper.getPath(data, idDataSource); // if response doesn't include

            if (!respondedIds[id]) {
              removed.push({
                [idDataSource]: id
              });
            }
          });
        }
      } // If there is no this store section we use records mentioned in the current request
      else if (requestUpdated || requestRemoved) {
          rows = requestUpdated;
          removed = requestRemoved;
        } // return nullish "rows"/"removed" if no entries

      rows = (_rows = rows) !== null && _rows !== void 0 && _rows.length ? rows : null;
      removed = (_removed = removed) !== null && _removed !== void 0 && _removed.length ? removed : null;
      return {
        rows,
        removed
      };
    }

    applyChangesToStore(storeDesc, storeResponse) {
      const me = this,
            phantomIdField = storeDesc.phantomIdField || me.phantomIdField,
            {
        store,
        stores
      } = storeDesc,
            {
        modelClass
      } = store,
            idDataSource = modelClass.getFieldDataSource(modelClass.idField),
            // collect records we need to process
      {
        rows,
        removed
      } = me.getRowsToApplyChangesTo(storeDesc, storeResponse); // process added/updated records

      rows === null || rows === void 0 ? void 0 : rows.forEach(data => {
        const phantomId = data[phantomIdField],
              id = ObjectHelper.getPath(data, idDataSource);
        let record = null; // if phantomId is provided then we will use it to find added record

        if (phantomId != null) {
          record = store.getById(phantomId);
        } // if id is provided then we will use it to find updated record
        else if (id != null) {
            record = store.getById(id);
          }

        if (record) {
          me.applyChangesToRecord(record, data, stores, store);
        } else {
          me.suspendChangesTracking(); // create new record in the store

          record = store.add(data)[0];
          me.resumeChangesTracking(true);
          record.clearChanges();
        }
      }); // process removed records

      if (removed && me.applyRemovals(store, removed)) {
        store.trigger('dataChanged', {
          source: store
        });
      }
    }

    applySyncResponse(response) {
      const me = this,
            stores = me.orderedCrudStores;
      me.applyingSyncResponse = true;

      for (const store of stores) {
        me.applyChangesToStore(store, response[store.storeId]);
      }

      me.applyingSyncResponse = false;
    }

    applyLoadResponse(response, options) {
      this.applyingLoadResponse = true;
      this.loadCrudManagerData(response, options);
      this.applyingLoadResponse = false;
    }

    async applyResponse(requestType, response, options) {
      // in trackResponseType we check response type before deciding how to react on the response
      if (this.trackResponseType) {
        requestType = response.type || requestType;
      }

      switch (requestType) {
        case 'load':
          this.applyLoadResponse(response, options);
          break;

        case 'sync':
          this.applySyncResponse(response);
          break;
      }
    } //endregion

    /**
     * Generates unique request identifier.
     * @internal
     * @template
     * @return {Number} The request identifier.
     */

    get requestId() {
      return Number.parseInt(`${Date.now()}${this._requestId++}`);
    }
    /**
     * Persists changes made on the registered stores to the server. Usage:
     *
     * ```javascript
     * // persist and run a callback on request completion
     * crud.sync().then(
     *     () => console.log("Changes saved..."),
     *     ({ response, cancelled }) => console.log(`Error: ${cancelled ? 'Cancelled' : response.message}`)
     * );
     * ```
     *
     * ** Note: ** If there is an incomplete sync request in progress then system will queue the call and delay it until previous request completion.
     * In this case {@link #event-syncDelayed} event will be fired.
     *
     * ** Note: ** Please take a look at {@link #config-autoSync} config. This option allows to persist changes automatically after any data modification.
     * @returns {Promise} Promise, which is resolved if request was successful.
     * Both the resolve and reject functions are passed a `state` object. State object has following structure:
     *
     *     {
     *         cancelled       : Boolean, // **optional** flag, which is present when promise was rejected
     *         rawResponse     : String,  // raw response from ajax request, either response xml or text
     *         rawResponseText : String,  // raw response text as String from ajax request
     *         response        : Object,  // processed response in form of object
     *     }
     *
     * If promise was rejected by {@link #event-beforeSync} event, `state` object will have structure:
     *
     *     {
     *         cancelled : true
     *     }
     *
     */

    sync() {
      const me = this;

      if (me.activeRequests.sync) {
        // let's delay this call and start it only after server response

        /**
         * Fires after {@link #function-sync sync request} was delayed due to incomplete previous one.
         * @event syncDelayed
         * @param {Scheduler.crud.AbstractCrudManager} source The CRUD manager.
         * @param {Object} arguments The arguments of {@link #function-sync} call.
         */
        me.trigger('syncDelayed'); // Queue sync request after current one

        return me.activeSyncPromise = me.activeSyncPromise.finally(() => me.sync());
      } // Store current request promise. While this one is pending, all following sync requests will create chain
      // of sequential promises

      return me.activeSyncPromise = new Promise((resolve, reject) => {
        // get current changes set package
        const pack = me.getChangeSetPackage(); // if no data to persist we resolve immediately

        if (!pack) {
          resolve(null);
          return;
        }
        /**
         * Fires before {@link #function-sync sync request} is sent. Return `false` to cancel sync request.
         *
         * ```javascript
         *     crudManager.on('beforesync', function() {
         *        // cannot persist changes before at least one record is added
         *        // to the `someStore` store
         *        if (!someStore.getCount()) return false;
         *     });
         * ```
         * @event beforeSync
         * @param {Scheduler.crud.AbstractCrudManager} source The CRUD manager.
         * @param {Object} pack The data package which contains data for all stores managed by the crud manager.
         */

        if (me.trigger('beforeSync', {
          pack
        }) !== false) {
          me.trigger('syncStart', {
            pack
          }); // keep active request details

          me.activeRequests.sync = {
            pack,
            resolve,
            reject,
            id: pack.requestId,
            desc: me.sendRequest({
              data: me.encode(pack),
              type: 'sync',
              success: me.onSyncSuccess,
              failure: me.onSyncFailure,
              thisObj: me
            })
          };
        } else {
          // if this sync was canceled let's fire event about it

          /**
           * Fires after {@link #function-sync sync request} was canceled by some {@link #event-beforeSync} listener.
           * @event syncCanceled
           * @param {Scheduler.crud.AbstractCrudManager} source The CRUD manager.
           * @param {Object} pack The data package which contains data for all stores managed by the crud manager.
           */
          me.trigger('syncCanceled', {
            pack
          });
          this.processPromise(reject, null, {
            action: 'sync',
            cancelled: true
          });
        }
      }).catch(error => {
        // If the request was not cancelled in beforeSync listener, forward the error so the user's `catch` handler can catch it
        if (error && !error.cancelled) {
          throw error;
        } // Pass the error object as a param to the next `then` chain

        return error;
      });
    }

    async onSyncSuccess(rawResponse, fetchOptions) {
      if (this.isDestroyed) return;
      let responseText = '';
      await rawResponse.text().then(value => responseText = value).catch(nullFn); // since we break the method w/ promises chain ..need to check if the instance is not destroyed in the meantime

      if (this.isDestroyed) return;
      const request = this.activeRequests.sync,
            response = await this.internalOnSync(responseText, fetchOptions); // since we break the method w/ promises chain ..need to check if the instance is not destroyed in the meantime

      if (this.isDestroyed) return;

      if (response !== null && response !== void 0 && response.success) {
        request.resolve({
          response,
          rawResponse,
          responseText,
          request
        });
      } else {
        this.processPromise(request.reject, request.resolve, {
          action: 'sync',
          cancelled: false,
          response,
          rawResponse,
          responseText,
          request
        });
      }
    }

    async onSyncFailure(rawResponse, fetchOptions = {}) {
      var _fetchOptions$abortCo2;

      if (this.isDestroyed) return;
      const request = this.activeRequests.sync,
            {
        options
      } = request,
            signal = (_fetchOptions$abortCo2 = fetchOptions.abortController) === null || _fetchOptions$abortCo2 === void 0 ? void 0 : _fetchOptions$abortCo2.signal,
            wasAborted = Boolean(signal === null || signal === void 0 ? void 0 : signal.aborted);

      if (!wasAborted) {
        let responseText = '';

        if (rawResponse) {
          await rawResponse.text().then(value => responseText = value).catch(nullFn);
        } // since we break the method w/ promises chain ..need to check if the instance is not destroyed in the meantime

        if (this.isDestroyed) return;
        const response = await this.internalOnSync(responseText, fetchOptions, options); // since we break the method w/ promises chain ..need to check if the instance is not destroyed in the meantime

        if (this.isDestroyed) return;
        this.processPromise(request.reject, null, {
          action: 'sync',
          cancelled: wasAborted,
          response,
          rawResponse,
          responseText,
          options,
          request
        });
      }
    }
    /**
     * Commits all records changes of all the registered stores.
     */

    commitCrudStores() {
      this.crudStores.forEach(store => store.store.commit());
    }
    /**
     * Rejects all records changes on all stores and re-insert any records that were removed locally. Any phantom records will be removed.
     */

    rejectCrudStores() {
      this.orderedCrudStores.forEach(store => store.store.clearChanges());
    }
    /**
     * Removes all stores and cancels active requests.
     */

    doDestroy() {
      const me = this,
            {
        load,
        sync
      } = me.activeRequests;
      load && me.cancelRequest(load.desc, load.reject);
      sync && me.cancelRequest(sync.desc, sync.reject);

      while (me.crudStores.length > 0) {
        me.removeCrudStore(me.crudStores[0]);
      }

      super.doDestroy && super.doDestroy();
    } // set crudRevision(value) {
    //     debugger
    //     this._crudRevision = value;
    // }
    // get crudRevision() {
    //     return this._crudRevision;
    // }

    /**
     * Check if response is valid
     * @param {Response} rawResponse raw
     * @param {Object} response parsed response
     * @return {Boolean} truthy when response is `ok` or `status` is 500 or 404 and it is successfully parsed
     * @private
     */

    isValidResponse(rawResponse, response) {
      return rawResponse && (rawResponse.ok && response || [500, 404].includes(rawResponse.status));
    }

    processPromise(reject, resolve, context) {
      const {
        cancelled,
        action,
        rawResponse,
        response
      } = context; // Call `resolve()` instead of `reject()` for valid response. These errors are notified by API events

      if (resolve && this.isValidResponse(rawResponse, response)) {
        resolve(context);
      } else {
        const message = `${StringHelper.capitalize(action)} ${cancelled ? 'canceled' : 'failed'}`,
              error = new Error(message);
        Object.assign(error, context);
        reject(error);
      }
    }

  };
});

/**
 * @module Scheduler/crud/AbstractCrudManager
 */

/**
 * This is an abstract class serving as the base for the {@link Scheduler.data.CrudManager} class.
 * It implements basic mechanisms to organize batch communication with a server.
 * Yet it does not contain methods related to _data transfer_ nor _encoding_.
 * These methods are to be provided in sub-classes by consuming the appropriate mixins.
 *
 * For example, this is how the class can be used to implement an JSON encoding system:
 *
 * ```javascript
 * // let's make new CrudManager using AJAX as a transport system and JSON for encoding
 * class MyCrudManager extends JsonEncode(AjaxTransport(AbstractCrudManager)) {
 *
 * }
 * ```
 *
 * ## Data transfer and encoding methods
 *
 * These are methods that must be provided by subclasses of this class:
 *
 * - [#sendRequest](#Scheduler/crud/AbstractCrudManagerMixin#function-sendRequest)
 * - [#cancelRequest](#Scheduler/crud/AbstractCrudManagerMixin#function-cancelRequest)
 * - [#encode](#Scheduler/crud/AbstractCrudManagerMixin#function-encode)
 * - [#decode](#Scheduler/crud/AbstractCrudManagerMixin#function-decode)
 *
 * @extends Core/Base
 * @mixes Scheduler/crud/AbstractCrudManagerMixin
 * @abstract
 */

class AbstractCrudManager extends Base.mixin(AbstractCrudManagerMixin) {
  //region Default config

  /**
   * The server revision stamp.
   * The _revision stamp_ is a number which should be incremented after each server-side change.
   * This property reflects the current version of the data retrieved from the server and gets updated after each {@link Scheduler/crud/AbstractCrudManagerMixin#function-load} and {@link Scheduler/crud/AbstractCrudManagerMixin#function-sync} call.
   * @property {Number}
   * @readonly
   */
  get revision() {
    return this.crudRevision;
  }

  set revision(value) {
    this.crudRevision = value;
  }

  static get defaultConfig() {
    return {
      overrideCrudStoreLoad: true,

      /**
       * Sets the list of stores controlled by the CRUD manager.
       *
       * When adding a store to the CrudManager, make sure the server response format is correct for `load` and `sync` requests.
       * Learn more in the [Working with data](#guides/data/crud_manager.md#loading-data) guide.
       *
       * Store can be provided by itself, its storeId or an object having the following structure:
       * @property {String} stores.storeId Unique store identifier. Under this name the store related requests/responses will be sent.
       * @property {Core.data.Store} stores.store The store itself.
       * @property {String} [stores.phantomIdField] Set this if the store model has a predefined field to keep phantom record identifier.
       * @property {String} [stores.idField] id field name, if it's not specified then class will try to get it from a store model.
       * @config {Core.data.Store[]|String[]|Object[]}
       */
      stores: null
      /**
       * Encodes request to the server.
       * @function encode
       * @param {Object} request The request to encode.
       * @returns {String} The encoded request.
       * @abstract
       */

      /**
       * Decodes response from the server.
       * @function decode
       * @param {String} response The response to decode.
       * @returns {Object} The decoded response.
       * @abstract
       */

    };
  } //endregion
  //region Init

  construct(config = {}) {
    if (config.stores) {
      config.crudStores = config.stores;
      delete config.stores;
    }

    super.construct(config);
  } //endregion
  //region Store collection (add, remove, get & iterate)

  set stores(stores) {
    if (stores !== this.crudStores) {
      this.crudStores = stores;
    }
  }
  /**
   * A list of registered stores whose server communication will be collected into a single batch.
   * Each store is represented by a _store descriptor_, an object having following structure:
   * @member {Object[]} stores
   * @property {String} stores.storeId Unique store identifier.
   * @property {Core.data.Store} stores.store Store itself.
   * @property {String} [stores.phantomIdField] Set this if store model has a predefined field to keep phantom record identifier.
   * @property {String} [stores.idField] id field name, if it's not specified then class will try to get it from a store model.
   */

  get stores() {
    return this.crudStores;
  } //endregion

  /**
   * Returns true if the crud manager is currently loading data
   * @property {Boolean}
   * @readonly
   * @category CRUD
   */

  get isLoading() {
    return this.isCrudManagerLoading;
  }
  /**
   * Commits all records changes of all the registered stores.
   */

  commit() {
    this.commitCrudStores();
  }
  /**
   * Rejects all records changes on all stores and re-insert any records that were removed locally. Any phantom records will be removed.
   */

  reject() {
    this.rejectCrudStores();
  }
  /**
   * Adds a store to the collection.
   *
   *```javascript
   * // append stores to the end of collection
   * crudManager.addStore([
   *     store1,
   *     // storeId
   *     'bar',
   *     // store descriptor
   *     {
   *         storeId : 'foo',
   *         store   : store3
   *     },
   *     {
   *         storeId         : 'bar',
   *         store           : store4,
   *         // to write all fields of modified records
   *         writeAllFields  : true
   *     }
   * ]);
   *```
   *
   * **Note:** Order in which stores are kept in the collection is very essential sometimes.
   * Exactly in this order the loaded data will be put into each store.
   *
   * When adding a store to the CrudManager, make sure the server response format is correct for `load` and `sync` requests.
   * Learn more in the [Working with data](#guides/data/crud_manager.md#loading-data) guide.
   *
   * @param {Core.data.Store|String|Object|Core.data.Store[]|String[]|Object[]} store
   * A store or list of stores. Each store might be specified by its instance, `storeId` or _descriptor_.
   * The _store descriptor_ is an object having following properties:
   * @param {String} store.storeId The store identifier that will be used as a key in requests.
   * @param {Core.data.Store} store.store The store itself.
   * @param {String} [store.idField] The idField of the store. If not specified will be taken from the store model.
   * @param {String} [store.phantomIdField] The field holding unique Ids of phantom records (if store has such model).
   * @param {Boolean} [store.writeAllFields] Set to true to write all fields from modified records
   * @param {Number} [position] The relative position of the store. If `fromStore` is specified the this position will be taken relative to it.
   * If not specified then store(s) will be appended to the end of collection.
   * Otherwise it will be just a position in stores collection.
   *
   * ```javascript
   * // insert stores store4, store5 to the start of collection
   * crudManager.addStore([ store4, store5 ], 0);
   * ```
   *
   * @param {String|Core.data.Store|Object} [fromStore] The store relative to which position should be calculated. Can be defined as a store identifier, instance or descriptor (the result of {@link Scheduler/crud/AbstractCrudManagerMixin#function-getStoreDescriptor} call).
   *
   * ```javascript
   * // insert store6 just before a store having storeId equal to 'foo'
   * crudManager.addStore(store6, 0, 'foo');
   *
   * // insert store7 just after store3 store
   * crudManager.addStore(store7, 1, store3);
   * ```
   */

  addStore(...args) {
    return this.addCrudStore(...args);
  }

  removeStore(...args) {
    return this.removeCrudStore(...args);
  }

  getStore(...args) {
    return this.getCrudStore(...args);
  }

  hasChanges(...args) {
    return this.crudStoreHasChanges(...args);
  }

  loadData(...args) {
    return this.loadCrudManagerData(...args);
  }

}
AbstractCrudManager._$name = 'AbstractCrudManager';

/**
 * @module Scheduler/crud/transport/AjaxTransport
 */

/**
 * Implements data transferring functional that can be used for {@link Scheduler.crud.AbstractCrudManager} super classing.
 * Uses the fetch API for transport, https://developer.mozilla.org/en-US/docs/Web/API/Fetch_API
 *
 * @example
 * // create a new CrudManager using AJAX as a transport system and JSON for encoding
 * class MyCrudManager extends AjaxTransport(JsonEncode(AbstractCrudManager)) {}
 *
 * @abstract
 * @mixin
 */

var AjaxTransport = (Target => class AjaxTransport extends (Target || Base) {
  static get $name() {
    return 'AjaxTransport';
  }
  /**
   * Configuration of the AJAX requests used by __Crud Manager__ to communicate with a server-side.
   *
   * ```javascript
   * transport : {
   *     load : {
   *         url       : 'http://mycool-server.com/load.php',
   *         // HTTP request parameter used to pass serialized "load"-requests
   *         paramName : 'data',
   *         // pass extra HTTP request parameter
   *         params    : {
   *             foo : 'bar'
   *         }
   *     },
   *     sync : {
   *         url     : 'http://mycool-server.com/sync.php',
   *         // specify Content-Type for requests
   *         headers : {
   *             'Content-Type' : 'application/json'
   *         }
   *     }
   * }
   *```
   * Since the class uses Fetch API you can use
   * any its [Request interface](https://developer.mozilla.org/en-US/docs/Web/API/Request) options:
   *
   * ```javascript
   * transport : {
   *     load : {
   *         url         : 'http://mycool-server.com/load.php',
   *         // HTTP request parameter used to pass serialized "load"-requests
   *         paramName   : 'data',
   *         // pass few Fetch API options
   *         method      : 'GET',
   *         credentials : 'include',
   *         cache       : 'no-cache'
   *     },
   *     sync : {
   *         url         : 'http://mycool-server.com/sync.php',
   *         // specify Content-Type for requests
   *         headers     : {
   *             'Content-Type' : 'application/json'
   *         },
   *         credentials : 'include'
   *     }
   * }
   *```
   *
   * An object where you can set the following possible properties:
   * @config {Object} transport
   * @property {Object} transport.load Load requests configuration:
   * @property {String} transport.load.url URL to request for data loading.
   * @property {String} [transport.load.method='GET'] HTTP method to be used for load requests.
   * @property {String} [transport.load.paramName='data'] Name of the parameter that will contain a serialized `load` request.
   * The value is mandatory for requests using `GET` method (default for `load`) so if the value is not provided `data` string is used as default.
   * This value is optional for HTTP methods like `POST` and `PUT`, the request body will be used for data transferring in these cases.
   * @property {Object} [transport.load.params] An object containing extra HTTP parameters to pass to the server when sending a `load` request.
   *
   * ```javascript
   * transport : {
   *     load : {
   *         url       : 'http://mycool-server.com/load.php',
   *         // HTTP request parameter used to pass serialized "load"-requests
   *         paramName : 'data',
   *         // pass extra HTTP request parameter
   *         // so resulting URL will look like: http://mycool-server.com/load.php?userId=123456&data=...
   *         params    : {
   *             userId : '123456'
   *         }
   *     },
   *     ...
   * }
   * ```
   * @property {Object} [transport.load.headers] An object containing headers to pass to each server request.
   *
   * ```javascript
   * transport : {
   *     load : {
   *         url       : 'http://mycool-server.com/load.php',
   *         // HTTP request parameter used to pass serialized "load"-requests
   *         paramName : 'data',
   *         // specify Content-Type for "load" requests
   *         headers   : {
   *             'Content-Type' : 'application/json'
   *         }
   *     },
   *     ...
   * }
   * ```
   * @property {Object} [transport.load.fetchOptions] **DEPRECATED:** Any Fetch API options can be simply defined on the upper configuration level:
   * ```javascript
   * transport : {
   *     load : {
   *         url          : 'http://mycool-server.com/load.php',
   *         // HTTP request parameter used to pass serialized "load"-requests
   *         paramName    : 'data',
   *         // Fetch API options
   *         method       : 'GET',
   *         credentials  : 'include'
   *     },
   *     ...
   * }
   * ```
   * @property {Object} [transport.load.requestConfig] **DEPRECATED:** The config options can be defined on the upper configuration level.
   * @property {Object} transport.sync Sync requests (`sync` in further text) configuration:
   * @property {String} transport.sync.url URL to request for `sync`.
   * @property {String} [transport.sync.method='POST'] HTTP request method to be used for `sync`.
   * @property {String} [transport.sync.paramName=undefined] Name of the parameter in which `sync` data will be transferred.
   * This value is optional for requests using methods like `POST` and `PUT`, the request body will be used for data transferring in this case (default for `sync`).
   * And the value is mandatory for requests using `GET` method (if the value is not provided `data` string will be used as fallback).
   * @property {Object} [transport.sync.params] HTTP headers to pass with an HTTP request handling `sync`.
   *
   * ```javascript
   * transport : {
   *     sync : {
   *         url    : 'http://mycool-server.com/sync.php',
   *         // extra HTTP request parameter
   *         params : {
   *             userId : '123456'
   *         }
   *     },
   *     ...
   * }
   * ```
   * @property {Object} [transport.sync.headers] HTTP headers to pass with an HTTP request handling `sync`.
   *
   * ```javascript
   * transport : {
   *     sync : {
   *         url     : 'http://mycool-server.com/sync.php',
   *         // specify Content-Type for "sync" requests
   *         headers : {
   *             'Content-Type' : 'application/json'
   *         }
   *     },
   *     ...
   * }
   * ```
   * @property {Object} [transport.sync.fetchOptions] **DEPRECATED:** Any Fetch API options can be simply defined on the upper configuration level:
   * ```javascript
   * transport : {
   *     sync : {
   *         url         : 'http://mycool-server.com/sync.php',
   *         credentials : 'include'
   *     },
   *     ...
   * }
   * ```
   * @property {Object} [transport.sync.requestConfig] **DEPRECATED:** The config options can be defined on the upper configuration level.
   */

  static get defaultMethod() {
    return {
      load: 'GET',
      sync: 'POST'
    };
  }
  /**
   * Cancels a sent request.
   * @param {Promise} requestPromise The Promise object wrapping the Request to be cancelled.
   * The _requestPromise_ is the value returned from the corresponding {@link #function-sendRequest} call.
   * @param {Function} reject The reject handle of the requestPromise
   */

  cancelRequest(requestPromise, reject) {
    reject();
  }

  shouldUseBodyForRequestData(packCfg, method, paramName) {
    return !(method === 'HEAD' || method === 'GET') && !paramName;
  }
  /**
   * Sends a __Crud Manager__ request to the server.
   * @param {Object}   request The request configuration object having following properties:
   * @param {String}   request.type The request type. Either `load` or `sync`.
   * @param {String}   request.data The encoded __Crud Manager__ request data.
   * @param {Object}   request.params An object specifying extra HTTP params to send with the request.
   * @param {Function} request.success A function to be started on successful request transferring.
   * @param {String}   request.success.rawResponse `Response` object returned by the [fetch api](https://developer.mozilla.org/en-US/docs/Web/API/Fetch_API).
   * @param {Function} request.failure A function to be started on request transfer failure.
   * @param {String}   request.failure.rawResponse `Response` object returned by the [fetch api](https://developer.mozilla.org/en-US/docs/Web/API/Fetch_API).
   * @param {Object}   request.thisObj `this` reference for the above `success` and `failure` functions.
   * @return {Promise} The fetch Promise object.
   * @fires beforesend
   */

  sendRequest(request) {
    const me = this,
          {
      data
    } = request,
          transportConfig = me.transport[request.type] || {},
          // clone parameters defined for this type of request
    requestConfig = Objects.assign({}, transportConfig, transportConfig.requestConfig);
    requestConfig.method = requestConfig.method || AjaxTransport.defaultMethod[request.type];
    requestConfig.params = Objects.assign(requestConfig.params || {}, request.params);
    let {
      paramName
    } = requestConfig; // transfer package in the request body for some types of HTTP requests

    if (me.shouldUseBodyForRequestData(transportConfig, requestConfig.method, paramName)) {
      requestConfig.body = data; // for requests having body we set Content-Type to 'application/json' by default

      requestConfig.headers = requestConfig.headers || {};
      requestConfig.headers['Content-Type'] = requestConfig.headers['Content-Type'] || 'application/json';
    } else {
      // when we don't use body paramName is mandatory so fallback to 'data' as name
      paramName = paramName || 'data';
      requestConfig.params[paramName] = data;
    }

    if (!requestConfig.url) {
      throw new Error('Trying to request without URL specified');
    } // sanitize request config

    delete requestConfig.requestConfig;
    delete requestConfig.paramName;
    /**
     * Fires before a request is sent to the server.
     *
     * ```javascript
     * crudManager.on('beforeSend', function ({ params, type }) {
     *     // let's set "sync" request parameters
     *     if (type == 'sync') {
     *         // dynamically depending on "flag" value
     *         if (flag) {
     *             params.foo = 'bar';
     *         }
     *         else {
     *             params.foo = 'smth';
     *         }
     *     }
     * });
     * ```
     * @event beforeSend
     * @param {Scheduler.crud.AbstractCrudManager} crudManager The CRUD manager.
     * @param {Object} params HTTP request params to be passed in the request URL.
     * @param {String} type CrudManager request type (`load`/`sync`)
     * @param {Object} requestConfig Configuration object for Ajax request call
     */

    me.trigger('beforeSend', {
      params: requestConfig.params,
      type: request.type,
      requestConfig,
      config: request
    }); // AjaxHelper.fetch call it "queryParams"

    requestConfig.queryParams = requestConfig.params;
    delete requestConfig.params;
    let responsePromise;
    const fetchOptions = Objects.assign({}, requestConfig, requestConfig.fetchOptions),
          ajaxPromise = AjaxHelper.fetch(requestConfig.url, fetchOptions);
    ajaxPromise.catch(error => {
      const signal = fetchOptions.abortController && fetchOptions.abortController.signal;

      if (signal && !signal.aborted) {
        console.warn(error);
      }
    }).then(response => {
      if (response && response.ok) {
        var _request$success;

        responsePromise = (_request$success = request.success) === null || _request$success === void 0 ? void 0 : _request$success.call(request.thisObj || me, response, fetchOptions);
      } else {
        var _request$failure;

        responsePromise = (_request$failure = request.failure) === null || _request$failure === void 0 ? void 0 : _request$failure.call(request.thisObj || me, response, fetchOptions);
      }
    });
    return Promise.all([ajaxPromise, responsePromise]);
  }

});

/* eslint-disable no-unused-expressions */
/**
 * This mixin class adds Crud Manager functionality to the class it is mixed in to.
 *
 * The class mixing it in should draw its `eventStore` from the instantiated CrudManager's eventStore.
 *
 * It adds {@link #config-crudManager} config allowing to provide a Crud Manager either as a class instance or as a configuration object
 * (for later the mixin provides {@link #config-crudManagerClass} config defining which class should be instantiated).
 *
 * The mixin also tracks Crud Manager requests to the server and masks the view during them. For masking it
 * uses the grid {@link Grid.view.GridBase#config-loadMask} and {@link Grid.view.GridBase#config-syncMask} properties.
 *
 * @mixin
 */

var CrudManagerView = (Target => class CrudManagerView extends Target {
  //region Config
  static get $name() {
    return 'CrudManagerView';
  }

  static get configurable() {
    return {
      /**
       * Class that should be used to instantiate a CrudManager in case it's provided as a simple object to
       * {@link #config-crudManager} config.
       * @config {Scheduler.crud.AbstractCrudManagerMixin}
       * @category Data
       */
      crudManagerClass: null,

      /**
       * Supply a {@link Scheduler.data.CrudManager CrudManager} instance or a config object if you want to use
       * CrudManager for handling data.
       * @config {Object|Scheduler.crud.AbstractCrudManagerMixin}
       * @category Data
       */
      crudManager: null
    };
  } //endregion

  /**
   * Get/set the CrudManager instance
   * @member {Scheduler.data.CrudManager} crudManager
   * @category Data
   */
  //region Init

  afterConstruct() {
    const me = this,
          {
      crudManager
    } = me;
    super.afterConstruct();

    if (crudManager && me.loadMask && crudManager.isCrudManagerLoading) {
      // Show loadMask if crud manager is already loading
      me.onCrudManagerLoadStart();
    }
  } //endregion

  /**
   * Hooks up crud manager listeners
   * @private
   * @category Store
   */

  bindCrudManager(crudManager) {
    this.detachListeners('crudManager');
    crudManager === null || crudManager === void 0 ? void 0 : crudManager.on({
      name: 'crudManager',
      loadStart: 'onCrudManagerLoadStart',
      load: 'onCrudManagerLoad',
      loadCanceled: 'onCrudManagerLoadCanceled',
      syncStart: 'onCrudManagerSyncStart',
      sync: 'onCrudManagerSync',
      syncCanceled: 'onCrudManagerSyncCanceled',
      requestFail: 'onCrudManagerRequestFail',
      thisObj: this
    });
  }

  onCrudManagerLoadStart() {
    // Show loadMask before crud manager starts loading
    const me = this,
          {
      loadMask
    } = me;

    if (loadMask) {
      me.masked = Mask.mergeConfigs(me.loadMaskDefaults, loadMask);
    }

    me.toggleEmptyText();
  }

  onCrudManagerSyncStart() {
    const me = this,
          {
      syncMask
    } = me;

    if (syncMask) {
      me.masked = Mask.mergeConfigs(me.loadMaskDefaults, syncMask);
    }
  }

  onCrudManagerRequestFinalize(successful = true, requestType, response) {
    const me = this;

    if (successful) {
      me.masked = null;
      me.toggleEmptyText();
    } else {
      // Do not remove. Assertion strings for Localization sanity check.
      // 'L{GridBase.loadFailedMessage}'
      // 'L{GridBase.syncFailedMessage}'
      const messageHTML = `<div class="b-grid-load-failure">
                <div class="b-grid-load-fail">${me.L(`L{GridBase.${requestType}FailedMessage}`)}</div>
                ${response && response.message ? `<div class="b-grid-load-fail">${me.L('L{CrudManagerView.serverResponseLabel}')} ${response.message}</div>` : ''}
            </div>`;
      me.masked = Mask.mergeConfigs(me.loadMaskDefaults, me.loadMaskError, messageHTML);
    }
  }

  onCrudManagerLoadCanceled() {
    this.onCrudManagerRequestFinalize();
  }

  onCrudManagerSyncCanceled() {
    this.onCrudManagerRequestFinalize();
  }

  onCrudManagerLoad() {
    this.onCrudManagerRequestFinalize();
  }

  onCrudManagerSync() {
    this.onCrudManagerRequestFinalize();
  }

  onCrudManagerRequestFail({
    requestType,
    response
  }) {
    this.onCrudManagerRequestFinalize(false, requestType, response);
  }

  changeCrudManager(crudManager) {
    if (crudManager && !crudManager.isCrudManager) {
      // CrudManager injects itself into is Scheduler's _crudManager property
      // because code it triggers needs to access it through its getter.
      crudManager = new this.crudManagerClass(Object.assign({
        scheduler: this
      }, crudManager));
    } // config setter will veto because of above described behaviour
    // of setting the property early on creation

    this._crudManager = crudManager;
    this.bindCrudManager(crudManager);
  }

  get widgetClass() {}

});

/**
 * @module Scheduler/crud/encoder/JsonEncoder
 */

/**
 * Implements data encoding functional that should be mixed to a {@link Scheduler.crud.AbstractCrudManager} sub-class.
 * Uses _JSON_ as an encoding system.
 *
 * @example
 * // create a new CrudManager using AJAX as a transport system and JSON for encoding
 * class MyCrudManager extends JsonEncode(AjaxTransport(AbstractCrudManager)) {}
 *
 * @mixin
 */

var JsonEncoder = (Target => class JsonEncoder extends (Target || Base) {
  static get $name() {
    return 'JsonEncoder';
  } //format  : 'json',

  /**
   * Encodes a request object to _JSON_ encoded string. If encoding fails (due to circular structure), it returns null.
   * Supposed to be overridden in case data provided by the CRUD manager has to be transformed into format requested by server.
   * @param {Object} request The request to encode.
   * @returns {String} The encoded request.
   */

  encode(requestConfig) {
    return StringHelper.safeJsonStringify(requestConfig);
  }
  /**
   * Decodes (parses) a _JSON_ response string to an object. If parsing fails, it returns null.
   * Supposed to be overridden in case data provided by server has to be transformed into format requested by the CRUD manager.
   * @param {String} responseText The response text to decode.
   * @returns {Object} The decoded response.
   */

  decode(responseText) {
    return StringHelper.safeJsonParse(responseText);
  }

});

/**
 * @module Scheduler/data/util/recurrence/AbstractRecurrenceIterator
 * @private
 */

const frequencyToUnitRe = /ly$/i,
      frequencyToUnit = frequency => {
  const result = frequency.replace(frequencyToUnitRe, '');
  return result === 'DAI' ? 'DAY' : result;
},
      fn = (date, counter, isFirst, timeSpan) => timeSpan.buildOccurrence(date, isFirst),
      captureLastOccurrence = date => lastOccurrenceDate = date;

let lastOccurrenceDate;
class AbstractRecurrenceIterator extends Base {
  static processIterationConfig(config) {
    const {
      recurrence
    } = config,
          {
      frequency,
      interval,
      timeSpan,
      endDate: until,
      count
    } = recurrence; // Force the correction of the event to be in sync with its recurrence rule
    // before performing iteration. For example, if the event's configured startDate
    // is 1st January 2020, and the rule dictates that the event will take place
    // monthly, every Monday, then the first event is not until Monday 6th January, 2020.

    if (!config.syncingStartDate && !timeSpan.meta.isSyncedWithRule) {
      // Do not generate occurrences outside of the specified range
      const intervalEndDate = DateHelper.add(timeSpan.startDate, interval, frequencyToUnit(frequency)),
            endDate = DateHelper.min(intervalEndDate, config.endDate || intervalEndDate);
      this.forEachDate({
        syncingStartDate: true,
        startDate: timeSpan.startDate,
        endDate,
        recurrence,
        fn
      });
    } // Capture the start after its been synced with its recurrence rule

    const timeSpanStart = timeSpan.startDate; // Extract the endDate from the config, defaulting to the recurrence UNTIL date

    let {
      startDate = timeSpanStart,
      endDate = until
    } = config; // No point in starting the iteration before the event starts

    if (startDate < timeSpanStart) {
      startDate = timeSpanStart;
    } // The recurrence's stop date overrides the configured endDate.

    if (until) {
      if (!endDate || endDate > until) {
        endDate = until;
      }
    } // If we are limiting using count and we are not starting from the
    // first occurrence, then we have to calculate a stop date.
    // This is because for date ranges in the future we cannot calculate how many
    // preceding occurrences there may have been.
    else if (count && startDate > timeSpanStart) {
        // Iterate the occurrences from the start to capture the last one
        this.forEachDate({
          recurrence,
          fn: captureLastOccurrence
        }); // The date of the last occurrence in the count sequence overrides the configured endDate.

        if (!endDate || endDate > lastOccurrenceDate) {
          endDate = lastOccurrenceDate;
        }
      } // Preserve the requested start of requested visits.

    const earliestVisibleDate = startDate; // Unless we are only asked for events which *start* within the time range. we must make
    // a best attempt to include any occurrences which span the start date.
    // So if we are asking for events from the 1st of the month, and there's an event
    // which runs every 2 months from the 15th to the 5th, we must include it. Start the
    // iteration <interval> full frequency quanta before the requested start.
    // This will only cause <interval> extra iterations.
    // We cannot step back to before the event's startihg date though.

    if (!config.startOnly) {
      startDate = new Date(DateHelper.max(DateHelper.add(startDate, -interval, frequencyToUnit(frequency)), timeSpanStart));
    }

    return Object.assign({
      extraArgs: [],
      // Only check start date for time spans with 0 duration
      startOnly: !Boolean(timeSpan.durationMS)
    }, config, {
      startDate,
      endDate,
      timeSpan,
      timeSpanStart,
      earliestVisibleDate,
      durationMS: timeSpan.durationMS,
      spansStart: startDate <= timeSpanStart && endDate > timeSpanStart
    });
  }

  static get frequency() {
    return 'NONE';
  }

  static get MAX_OCCURRENCES_COUNT() {
    return 1000000;
  }

  static getOccurrenceIndex(event) {
    if (event.isOccurrence) {
      return DateHelper.diff(event.recurringTimeSpan.startDate, event.startDate, frequencyToUnit(event.recurringTimeSpan.recurrence.frequency));
    }
  }
  /**
   * Returns Nth occurrence of a week day in the provided period of time.
   * @param  {Date} startDate Period start date.
   * @param  {Date} endDate   Period end date.
   * @param  {Integer} day    Week day (0 - Sunday, 1 - Monday, 2 - Tuesday, etc.)
   * @param  {Integer} index  Index to find.
   * @return {Date}           Returns the found date or null if there is no `index`th entry.
   * @private
   */

  static getNthDayInPeriod(startDate, endDate, day, index) {
    let result, sign, borderDate;

    if (index) {
      const dayDurationInMs = 86400000,
            weekDurationInMs = 604800000;

      if (index > 0) {
        sign = 1;
        borderDate = startDate;
      } else {
        sign = -1;
        borderDate = endDate;
      } // delta between requested day and border day

      const delta = day - borderDate.getDay(); // if the requested day goes after (before, depending on borderDate used (start/end))
      // we adjust index +/-1

      if (sign * delta < 0) {
        index += sign;
      } // measure "index" weeks forward (or backward) ..take delta into account

      result = new Date(borderDate.getTime() + (index - sign) * weekDurationInMs + delta * dayDurationInMs); // if resulting date is outside of the provided range there is no "index"-th entry
      // of the day

      if (result < startDate || result > endDate) {
        result = null;
      }
    }

    return result;
  }

  static buildDate(year, month, date) {
    const dt = new Date(year, month, date);

    if (dt.getFullYear() === year && dt.getMonth() === month && dt.getDate() === date) {
      return dt;
    }
  }

  static isValidPosition(position) {
    return Boolean(position);
  }

  static forEachDateAtPositions(dates, positions, fn, scope) {
    const datesLength = dates.length,
          processed = {};

    for (let i = 0; i < positions.length; i++) {
      const index = positions[i];

      if (this.isValidPosition(index)) {
        const date = index > 0 ? dates[index - 1] : dates[datesLength + index];

        if (date && !processed[date.getTime()]) {
          // remember that we've returned the date
          processed[date.getTime()] = true; // return false if it's time to stop recurring

          if (fn.call(scope, date) === false) {
            return false;
          }
        }
      }
    }
  }

  static isInView(startOnly, occurrenceDate, earliestVisibleDate, durationMS, timeSpan) {
    return (startOnly ? occurrenceDate >= earliestVisibleDate : occurrenceDate.valueOf() + durationMS > earliestVisibleDate) && !timeSpan.hasException(occurrenceDate);
  }

}
AbstractRecurrenceIterator._$name = 'AbstractRecurrenceIterator';

/**
 * @module Scheduler/data/mixin/PartOfProject
 */

/**
 * This is a mixin, included in all models and stores of the Scheduler project. It provides a common API for accessing
 * all stores of the project.
 *
 * @mixin
 */

var PartOfProject = (Target => class PartOfProject extends (Target || Base) {
  /**
   * Returns the project this entity belongs to.
   *
   * @member {Scheduler.model.ProjectModel} project
   * @readonly
   * @category Project
   */

  /**
   * Returns the event store of the project this entity belongs to.
   *
   * @member {Scheduler.data.EventStore} eventStore
   * @readonly
   * @category Project
   */

  /**
   * Returns the dependency store of the project this entity belongs to.
   *
   * @member {Scheduler.data.DependencyStore} dependencyStore
   * @readonly
   * @category Project
   */

  /**
   * Returns the assignment store of the project this entity belongs to.
   *
   * @member {Scheduler.data.AssignmentStore} assignmentStore
   * @readonly
   * @category Project
   */

  /**
   * Returns the resource store of the project this entity belongs to.
   *
   * @member {Scheduler.data.ResourceStore} resourceStore
   * @readonly
   * @category Project
   */
  static get $name() {
    return 'PartOfProject';
  }

});

/**
 * @module Scheduler/model/mixin/AssignmentModelMixin
 */

/**
 * Mixin that holds configuration shared between assignments in Scheduler and Scheduler Pro.
 * @mixin
 */
var AssignmentModelMixin = (Target => class AssignmentModelMixin extends Target {
  static get $name() {
    return 'AssignmentModelMixin';
  }
  /**
   * Set value for the specified field(s), triggering engine calculations immediately. See
   * {@link Core.data.Model#function-set Model#set()} for arguments.
   *
   * ```javascript
   * assignment.set('resourceId', 2);
   * // assignment.resource is not yet resolved
   *
   * await assignment.setAsync('resourceId', 2);
   * // assignment.resource is resolved
   * ```
   *
   * @param {String|Object} field The field to set value for, or an object with multiple values to set in one call
   * @param {*} value Value to set
   * @param {Boolean} [silent=false] Set to true to not trigger events
   * automatically.
   * @function setAsync
   * @category Editing
   * @async
   */
  //region Fields

  static get fields() {
    return [
    /**
     * Id for the resource to assign to
     * @field {String|Number} resourceId
     * @category Common
     */
    'resourceId',
    /**
     * Id for the event to assign
     * @field {String|Number} eventId
     * @category Common
     */
    'eventId',
    /**
     * Specify `false` to opt out of drawing dependencies from/to this assignment
     * @field {Boolean} drawDependencies
     * @category Common
     */
    {
      name: 'drawDependencies',
      type: 'boolean'
    },
    /**
     * Id for event to assign. Can be used as an alternative to `eventId`, but please note that after
     * load it will be populated with the actual event and not its id. This field is not persistable.
     * @field {String|Number} event
     * @category Common
     */
    'event',
    /**
     * Id for resource to assign to. Can be used as an alternative to `resourceId`, but please note that after
     * load it will be populated with the actual resource and not its id. This field is not persistable.
     * @field {String|Number} resource
     * @category Common
     */
    'resource'];
  } //endregion

  construct(data, ...args) {
    data = data || {}; // Engine expects event and resource, not eventId and resourceId. We need to support both

    if (data.eventId != null) {
      data.event = data.eventId;
    }

    if (data.resourceId != null) {
      data.resource = data.resourceId;
    }

    super.construct(data, ...args);
  } //region Event & resource

  /**
   * A key made up from the event id and the id of the resource assigned to.
   * @property eventResourceKey
   * @readonly
   * @internal
   */

  get eventResourceKey() {
    const me = this;
    return `${me.event ? me.eventId : me.getData('eventId') || me.internalId}-${me.resource ? me.resourceId : me.getData('resourceId') || me.internalId}`;
  }

  set(field, value, ...args) {
    const toSet = this.fieldToKeys(field, value); // Resource was set, store its id as resourceId and announce it

    if ('resource' in toSet) {
      var _toSet$resource;

      toSet.resourceId = (_toSet$resource = toSet.resource) === null || _toSet$resource === void 0 ? void 0 : _toSet$resource.id;
    } // Same for event

    if ('event' in toSet) {
      var _toSet$event;

      toSet.eventId = (_toSet$event = toSet.event) === null || _toSet$event === void 0 ? void 0 : _toSet$event.id;
    }

    super.set(toSet, null, ...args);
  } // Settings resourceId relays to `resource`. Underlying data will be updated in `afterChange()` above

  set resourceId(value) {
    const {
      resource
    } = this; // When assigning a new id to a resource, it will update the resourceId of the assignment. But the assignments
    // resource is still the same so we need to announce here

    if (resource !== null && resource !== void 0 && resource.isModel && resource.id === value) {
      this.set('resourceId', value);
    } else {
      this.resource = value;
    }
  }

  get resourceId() {
    var _this$resource;

    // If assigned using `resource` and not `resourceId` there will be no resourceId
    return this.get('resourceId') || ((_this$resource = this.resource) === null || _this$resource === void 0 ? void 0 : _this$resource.id);
  } // Same for event as for resourceId

  set eventId(value) {
    const {
      event
    } = this; // When assigning a new id to an event, it will update the eventId of the assignment. But the assignments
    // event is still the same so we need to announce here

    if (event !== null && event !== void 0 && event.isModel && event.id === value) {
      this.set('eventId', value);
    } else {
      this.event = value;
    }
  }

  get eventId() {
    var _this$event;

    // If assigned using `event` and not `eventId` there will be no eventId
    return this.get('eventId') || ((_this$event = this.event) === null || _this$event === void 0 ? void 0 : _this$event.id);
  }
  /**
   * Convenience property to get the name of the associated event.
   * @property {String}
   * @readonly
   */

  get eventName() {
    var _this$event2;

    return (_this$event2 = this.event) === null || _this$event2 === void 0 ? void 0 : _this$event2.name;
  }
  /**
   * Convenience property to get the name of the associated resource.
   * @property {String}
   * @readonly
   */

  get resourceName() {
    var _this$resource2;

    return (_this$resource2 = this.resource) === null || _this$resource2 === void 0 ? void 0 : _this$resource2.name;
  } // TODO : Deprecate in favor of `get resource`

  /**
   * Returns the resource associated with this assignment.
   *
   * @return {Scheduler.model.ResourceModel} Instance of resource
   */

  getResource() {
    return this.resource;
  } //endregion
  // Convenience getter to not have to check `instanceof AssignmentModel`

  get isAssignment() {
    return true;
  }
  /**
   * Returns true if the Assignment can be persisted (e.g. task and resource are not 'phantoms')
   *
   * @property {Boolean}
   */

  get isPersistable() {
    const {
      event,
      resource,
      unjoinedStores,
      assignmentStore
    } = this,
          crudManager = assignmentStore === null || assignmentStore === void 0 ? void 0 : assignmentStore.crudManager;
    let result;

    if (assignmentStore) {
      // if crud manager is used it can deal with phantom event/resource since it persists all records in one batch
      // if no crud manager used we have to wait till event/resource are persisted
      result = this.isValid && (crudManager || !event.hasGeneratedId && !resource.hasGeneratedId);
    } // if we remove the record
    else {
        result = Boolean(unjoinedStores[0]);
      }

    return result;
  }

  get isValid() {
    return this.resource != null && this.event != null;
  }
  /**
   * Returns a textual representation of this assignment (e.g. Mike 50%).
   * @return {String}
   */

  toString() {
    if (this.resourceName) {
      return `${this.resourceName} ${this.units}%`;
    }

    return '';
  }

});

function split(iterable) {
  const gen1Pending = [];
  const gen2Pending = [];
  let iterator;

  const gen1 = function* () {
    if (!iterator) iterator = iterable[Symbol.iterator]();

    while (true) {
      if (gen1Pending.length) {
        yield* gen1Pending;
        gen1Pending.length = 0;
      }

      if (!iterator) break;
      const {
        value,
        done
      } = iterator.next();

      if (done) {
        iterator = null;
        iterable = null;
        break;
      }

      gen2Pending.push(value);
      yield value;
    }
  };

  const gen2 = function* () {
    if (!iterator) iterator = iterable[Symbol.iterator]();

    while (true) {
      if (gen2Pending.length) {
        yield* gen2Pending;
        gen2Pending.length = 0;
      }

      if (!iterator) break;
      const {
        value,
        done
      } = iterator.next();

      if (done) {
        iterator = null;
        iterable = null;
        break;
      }

      gen1Pending.push(value);
      yield value;
    }
  };

  return [gen1(), gen2()];
}
function* inBatchesBySize(iterator, batchSize) {
  if (batchSize < 0) throw new Error("Batch size needs to a natural number");
  batchSize = batchSize | 0;
  const runningBatch = [];

  for (const el of iterator) {
    if (runningBatch.length === batchSize) {
      yield runningBatch;
      runningBatch.length = 0;
    }

    runningBatch.push(el);
  }

  if (runningBatch.length > 0) yield runningBatch;
}
function* filter(iterator, func) {
  let i = 0;

  for (const el of iterator) {
    if (func(el, i++)) yield el;
  }
}
function* drop(iterator, howMany) {
  let i = 0;

  for (const el of iterator) {
    if (++i > howMany) yield el;
  }
}
function every(iterator, func) {
  let i = 0;

  for (const el of iterator) {
    if (!func(el, i++)) return false;
  }

  return true;
}
function some(iterator, func) {
  let i = 0;

  for (const el of iterator) {
    if (func(el, i++)) return true;
  }

  return false;
}
function* map(iterator, func) {
  let i = 0;

  for (const el of iterator) yield func(el, i++);
}
function reduce(iterator, func, initialAcc) {
  let i = 0;
  let acc = initialAcc;

  for (const el of iterator) {
    acc = func(acc, el, i++);
  }

  return acc;
}
function* uniqueOnly(iterator) {
  const seen = new Set();

  for (const el of iterator) {
    if (!seen.has(el)) {
      seen.add(el);
      yield el;
    }
  }
}
function* uniqueOnlyBy(iterator, func) {
  const seen = new Set();

  for (const el of iterator) {
    const uniqueBy = func(el);

    if (!seen.has(uniqueBy)) {
      seen.add(uniqueBy);
      yield el;
    }
  }
}
function* takeWhile(iterator, func) {
  let i = 0;

  for (const el of iterator) {
    if (func(el, i++)) yield el;else return;
  }
}
function* concat(...iterators) {
  for (let i = 0; i < iterators.length; i++) yield* iterators[i];
}
function* concatIterable(iteratorsProducer) {
  for (const iterator of iteratorsProducer) yield* iterator;
}
class ChainedIteratorClass {
  constructor(iterable) {
    this.iterable = undefined;
    if (!iterable) throw new Error("Require an iterable instance for chaining");
    this.iterable = iterable;
  }

  derive(iterable) {
    this.iterable = undefined;
    return new ChainedIteratorClass(iterable);
  }

  copy() {
    const [iter1, iter2] = split(this.iterable);
    this.iterable = iter2;
    return new ChainedIteratorClass(iter1);
  }

  split() {
    const [iter1, iter2] = split(this.iterable);
    return [new ChainedIteratorClass(iter1), this.derive(iter2)];
  }

  inBatchesBySize(batchSize) {
    return this.derive(inBatchesBySize(this.iterable, batchSize));
  }

  filter(func) {
    return this.derive(filter(this.iterable, func));
  }

  drop(howMany) {
    return this.derive(drop(this.iterable, howMany));
  }

  map(func) {
    return this.derive(map(this.iterable, func));
  }

  reduce(func, initialAcc) {
    return reduce(this, func, initialAcc);
  }

  concat() {
    return this.derive(concatIterable(this.iterable));
  }

  uniqueOnly() {
    return this.derive(uniqueOnly(this.iterable));
  }

  uniqueOnlyBy(func) {
    return this.derive(uniqueOnlyBy(this.iterable, func));
  }

  every(func) {
    return every(this, func);
  }

  some(func) {
    return some(this, func);
  }

  takeWhile(func) {
    return this.derive(takeWhile(this.iterable, func));
  }

  *[Symbol.iterator]() {
    let iterable = this.iterable;
    if (!iterable) throw new Error("Chained iterator already exhausted or used to derive the new one");
    this.iterable = undefined;
    yield* iterable;
    iterable = undefined;
  }

  toArray() {
    return Array.from(this);
  }

  sort(order) {
    return Array.from(this).sort(order);
  }

  toSet() {
    return new Set(this);
  }

  toMap() {
    return new Map(this);
  }

  flush() {
    for (const element of this) {}
  }

  memoize() {
    return new MemoizedIteratorClass(this);
  }

}
const ChainedIterator = iterator => new ChainedIteratorClass(iterator);
const CI = ChainedIterator;
class MemoizedIteratorClass extends ChainedIteratorClass {
  constructor() {
    super(...arguments);
    this.elements = [];
    this.$iterator = undefined;
  }

  set iterable(iterable) {
    this.$iterable = iterable;
  }

  get iterable() {
    return this;
  }

  derive(iterable) {
    return new ChainedIteratorClass(iterable);
  }

  *[Symbol.iterator]() {
    const elements = this.elements;

    if (this.$iterable) {
      if (!this.$iterator) this.$iterator = this.$iterable[Symbol.iterator]();
      let iterator = this.$iterator;
      let alreadyConsumed = elements.length;
      if (alreadyConsumed > 0) yield* elements;

      while (true) {
        if (elements.length > alreadyConsumed) {
          for (let i = alreadyConsumed; i < elements.length; i++) yield elements[i];

          alreadyConsumed = elements.length;
        }

        if (!iterator) break;
        const {
          value,
          done
        } = iterator.next();

        if (done) {
          iterator = this.$iterator = null;
          this.$iterable = null;
        } else {
          elements.push(value);
          alreadyConsumed++;
          yield value;
        }
      }
    } else {
      yield* elements;
    }
  }

}
const MemoizedIterator = iterator => new MemoizedIteratorClass(iterator);
const MI = MemoizedIterator;

const MixinInstanceOfProperty = Symbol('MixinIdentity');
const MixinStateProperty = Symbol('MixinStateProperty');

class MixinWalkDepthState {
  constructor() {
    this.baseEl = undefined;
    this.sourceEl = undefined;
    this.$elementsByTopoLevel = undefined;
    this.$topoLevels = undefined;
    this.linearizedByTopoLevelsSource = MI(this.linearizedByTopoLevels());
  }

  static new(props) {
    const me = new this();
    props && Object.assign(me, props);
    return me;
  }

  get topoLevels() {
    if (this.$topoLevels !== undefined) return this.$topoLevels;
    return this.$topoLevels = this.buildTopoLevels();
  }

  buildTopoLevels() {
    return Array.from(this.elementsByTopoLevel.keys()).sort((level1, level2) => level1 - level2);
  }

  get elementsByTopoLevel() {
    if (this.$elementsByTopoLevel !== undefined) return this.$elementsByTopoLevel;
    return this.$elementsByTopoLevel = this.buildElementsByTopoLevel();
  }

  getOrCreateLevel(map, topoLevel) {
    let elementsAtLevel = map.get(topoLevel);

    if (!elementsAtLevel) {
      elementsAtLevel = [];
      map.set(topoLevel, elementsAtLevel);
    }

    return elementsAtLevel;
  }

  buildElementsByTopoLevel() {
    let maxTopoLevel = 0;
    const baseElements = this.baseEl ? CI(this.baseEl.walkDepthState.elementsByTopoLevel.values()).concat().toSet() : new Set();
    const map = CI(this.sourceEl.requirements).map(mixin => mixin.walkDepthState.elementsByTopoLevel).concat().reduce((elementsByTopoLevel, [topoLevel, mixins]) => {
      if (topoLevel > maxTopoLevel) maxTopoLevel = topoLevel;
      this.getOrCreateLevel(elementsByTopoLevel, topoLevel).push(mixins);
      return elementsByTopoLevel;
    }, new Map());
    this.getOrCreateLevel(map, maxTopoLevel + 1).push([this.sourceEl]);
    return CI(map).map(([level, elements]) => {
      return [level, CI(elements).concat().uniqueOnly().filter(mixin => !baseElements.has(mixin)).sort((mixin1, mixin2) => mixin1.id - mixin2.id)];
    }).toMap();
  }

  *linearizedByTopoLevels() {
    yield* CI(this.topoLevels).map(level => this.elementsByTopoLevel.get(level)).concat();
  }

}

let MIXIN_ID = 1;
const identity = a => class extends a {};
class ZeroBaseClass {}

class MixinState {
  constructor() {
    this.id = MIXIN_ID++;
    this.requirements = [];
    this.baseClass = ZeroBaseClass;
    this.identitySymbol = undefined;
    this.mixinLambda = identity;
    this.walkDepthState = undefined;
    this.$minimalClass = undefined;
    this.name = '';
  }

  static new(props) {
    const me = new this();
    props && Object.assign(me, props);
    me.walkDepthState = MixinWalkDepthState.new({
      sourceEl: me,
      baseEl: getMixinState(me.baseClass)
    });
    const mixinLambda = me.mixinLambda;
    const symbol = me.identitySymbol = Symbol(mixinLambda.name);
    const mixinLambdaWrapper = Object.assign(function (base) {
      const extendedClass = mixinLambda(base);
      extendedClass.prototype[symbol] = true;
      return extendedClass;
    }, {
      [MixinInstanceOfProperty]: symbol,
      [MixinStateProperty]: me
    });
    Object.defineProperty(mixinLambdaWrapper, Symbol.hasInstance, {
      value: isInstanceOfStatic
    });
    me.mixinLambda = mixinLambdaWrapper;
    return me;
  }

  get minimalClass() {
    if (this.$minimalClass !== undefined) return this.$minimalClass;
    return this.$minimalClass = this.buildMinimalClass();
  }

  getBaseClassMixinId(baseClass) {
    const constructor = this.constructor;
    const mixinId = constructor.baseClassesIds.get(baseClass);
    if (mixinId !== undefined) return mixinId;
    const newId = MIXIN_ID++;
    constructor.baseClassesIds.set(baseClass, newId);
    return newId;
  }

  buildMinimalClass() {
    const self = this.constructor;
    let baseCls = this.baseClass;
    const minimalClassConstructor = this.walkDepthState.linearizedByTopoLevelsSource.reduce((acc, mixin) => {
      const {
        cls,
        hash
      } = acc;
      const nextHash = hash + String.fromCharCode(mixin.id);
      let wrapperCls = self.minimalClassesByLinearHash.get(nextHash);

      if (!wrapperCls) {
        wrapperCls = mixin.mixinLambda(cls);
        mixin.name = wrapperCls.name;
        self.minimalClassesByLinearHash.set(nextHash, wrapperCls);
      }

      acc.cls = wrapperCls;
      acc.hash = nextHash;
      return acc;
    }, {
      cls: baseCls,
      hash: String.fromCharCode(this.getBaseClassMixinId(baseCls))
    }).cls;
    const minimalClass = Object.assign(minimalClassConstructor, {
      [MixinInstanceOfProperty]: this.identitySymbol,
      [MixinStateProperty]: this,
      mix: this.mixinLambda,
      derive: base => Mixin([minimalClass, base], base => class extends base {}),
      $: this,
      toString: this.toString.bind(this)
    });
    Object.defineProperty(minimalClass, Symbol.hasInstance, {
      value: isInstanceOfStatic
    });
    return minimalClass;
  }

  toString() {
    return this.walkDepthState.linearizedByTopoLevelsSource.reduce((acc, mixin) => `${mixin.name}(${acc})`, this.baseClass.name);
  }

}

MixinState.minimalClassesByLinearHash = new Map();
MixinState.baseClassesIds = new Map();

const isMixinClass = func => {
  return Object.getPrototypeOf(func.prototype).constructor.hasOwnProperty(MixinStateProperty);
};

const getMixinState = func => {
  return Object.getPrototypeOf(func.prototype).constructor[MixinStateProperty];
};

const mixin = (required, mixinLambda) => {
  let baseClass;

  if (required.length > 0) {
    const lastRequirement = required[required.length - 1];
    if (!isMixinClass(lastRequirement) && lastRequirement !== ZeroBaseClass) baseClass = lastRequirement;
  }

  const requirements = [];
  required.forEach((requirement, index) => {
    const mixinState = requirement[MixinStateProperty];

    if (mixinState !== undefined) {
      const currentBaseClass = mixinState.baseClass;

      if (currentBaseClass !== ZeroBaseClass) {
        if (baseClass) {
          if (baseClass !== currentBaseClass) {
            const currentIsSub = currentBaseClass.prototype.isPrototypeOf(baseClass.prototype);
            const currentIsSuper = baseClass.prototype.isPrototypeOf(currentBaseClass.prototype);
            if (!currentIsSub && !currentIsSuper) throw new Error("Base class mismatch");
            baseClass = currentIsSuper ? currentBaseClass : baseClass;
          }
        } else baseClass = currentBaseClass;
      }

      requirements.push(mixinState);
    } else {
      if (index !== required.length - 1) throw new Error("Base class should be provided as the last element of the requirements array");
    }
  });
  const mixinState = MixinState.new({
    requirements,
    mixinLambda: mixinLambda,
    baseClass: baseClass || ZeroBaseClass
  });
  return mixinState.minimalClass;
};

const isInstanceOfStatic = function (instance) {
  return Boolean(instance && instance[this[MixinInstanceOfProperty]]);
};

const isInstanceOf = (instance, func) => {
  return Boolean(instance && instance[func[MixinInstanceOfProperty]]);
};
const Mixin = mixin;
const MixinAny = mixin;

class Base$1 {
  initialize(props) {
    props && Object.assign(this, props);
  }

  static new(props) {
    const instance = new this();
    instance.initialize(props);
    return instance;
  }

}

const MIN_SMI = -Math.pow(2, 30);
const MAX_SMI = Math.pow(2, 30) - 1;
const uppercaseFirst = str => str.slice(0, 1).toUpperCase() + str.slice(1);
const defineProperty$6 = (target, property, value) => {
  Object.defineProperty(target, property, {
    value,
    enumerable: true,
    configurable: true
  });
  return value;
};
const prototypeValue = value => {
  return function (target, propertyKey) {
    target[propertyKey] = value;
  };
};
const copySetInto = (sourceSet, targetSet) => {
  for (const value of sourceSet) targetSet.add(value);

  return targetSet;
};
const delay = timeout => new Promise(resolve => setTimeout(resolve, timeout));
let isRegeneratorRuntime = null;
const isGeneratorFunction = function (func) {
  if (isRegeneratorRuntime === null) isRegeneratorRuntime = typeof regeneratorRuntime !== 'undefined';

  if (isRegeneratorRuntime === true) {
    return regeneratorRuntime.isGeneratorFunction(func);
  } else {
    return func.constructor.name === 'GeneratorFunction';
  }
};

const emptyFn$2 = (...args) => undefined;

const DEBUG_ONLY = func =>  emptyFn$2;

var __decorate = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const BreakCurrentStackExecution = Symbol('BreakCurrentStackExecution');
class Effect extends Base$1 {}

__decorate([prototypeValue(true)], Effect.prototype, "sync", void 0);

__decorate([prototypeValue(true)], Effect.prototype, "pure", void 0);

const ProposedOrPreviousSymbol = Symbol('ProposedOrPreviousSymbol');
const ProposedOrPrevious = Effect.new({
  handler: ProposedOrPreviousSymbol
});
const RejectSymbol = Symbol('RejectSymbol');
class RejectEffect extends Effect {
  constructor() {
    super(...arguments);
    this.handler = RejectSymbol;
  }

}

__decorate([prototypeValue(false)], RejectEffect.prototype, "pure", void 0);

const Reject = reason => RejectEffect.new({
  reason
});
const TransactionSymbol = Symbol('TransactionSymbol');
const GetTransaction = Effect.new({
  handler: TransactionSymbol
});
const OwnQuarkSymbol = Symbol('OwnQuarkSymbol');
const OwnQuark = Effect.new({
  handler: OwnQuarkSymbol
});
const OwnIdentifierSymbol = Symbol('OwnIdentifierSymbol');
const OwnIdentifier = Effect.new({
  handler: OwnIdentifierSymbol
});
const WriteSymbol = Symbol('WriteSymbol');
class WriteEffect extends Effect {
  constructor() {
    super(...arguments);
    this.handler = WriteSymbol;
  }

}

__decorate([prototypeValue(false)], WriteEffect.prototype, "pure", void 0);

const Write = (identifier, proposedValue, ...proposedArgs) => WriteEffect.new({
  identifier,
  proposedArgs: [proposedValue, ...proposedArgs]
});
const WriteSeveralSymbol = Symbol('WriteSeveralSymbol');
class WriteSeveralEffect extends Effect {
  constructor() {
    super(...arguments);
    this.handler = WriteSeveralSymbol;
  }

}

__decorate([prototypeValue(false)], WriteSeveralEffect.prototype, "pure", void 0);
const PreviousValueOfSymbol = Symbol('PreviousValueOfSymbol');
class PreviousValueOfEffect extends Effect {
  constructor() {
    super(...arguments);
    this.handler = PreviousValueOfSymbol;
  }

}
const PreviousValueOf = identifier => PreviousValueOfEffect.new({
  identifier
});
const ProposedValueOfSymbol = Symbol('ProposedValueOfSymbol');
const HasProposedValueSymbol = Symbol('HasProposedValueSymbol');
class HasProposedValueEffect extends Effect {
  constructor() {
    super(...arguments);
    this.handler = HasProposedValueSymbol;
  }

}
const HasProposedValue = identifier => HasProposedValueEffect.new({
  identifier
});
const ProposedOrPreviousValueOfSymbol = Symbol('ProposedOrPreviousValueOfSymbol');
class ProposedOrPreviousValueOfEffect extends Effect {
  constructor() {
    super(...arguments);
    this.handler = ProposedOrPreviousValueOfSymbol;
  }

}
const ProposedOrPreviousValueOf = identifier => ProposedOrPreviousValueOfEffect.new({
  identifier
});
const ProposedArgumentsOfSymbol = Symbol('ProposedArgumentsOfSymbol');
class ProposedArgumentsOfEffect extends Effect {
  constructor() {
    super(...arguments);
    this.handler = ProposedArgumentsOfSymbol;
  }

}
const ProposedArgumentsOf = identifier => ProposedArgumentsOfEffect.new({
  identifier
});
const UnsafeProposedOrPreviousValueOfSymbol = Symbol('UnsafeProposedOrPreviousValueOfSymbol');
const UnsafePreviousValueOfSymbol = Symbol('UnsafePreviousValueOfSymbol');

class CalculationGen extends Mixin([], base => class CalculationGen extends base {
  constructor() {
    super(...arguments);
    this.iterator = undefined;
    this.iterationResult = undefined;
  }

  isCalculationStarted() {
    return Boolean(this.iterator || this.iterationResult);
  }

  isCalculationCompleted() {
    return Boolean(this.iterationResult && this.iterationResult.done);
  }

  get result() {
    return this.iterationResult && this.iterationResult.done ? this.iterationResult.value : undefined;
  }

  startCalculation(onEffect, ...args) {
    const iterator = this.iterator = this.calculation.call(this.context || this, onEffect, ...args);
    return this.iterationResult = iterator.next();
  }

  continueCalculation(value) {
    return this.iterationResult = this.iterator.next(value);
  }

  cleanupCalculation() {
    this.iterationResult = undefined;
    this.iterator = undefined;
  }

  *calculation(onEffect, ...args) {
    throw new Error("Abstract method `calculation` called");
  }

  runSyncWithEffect(onEffect, ...args) {
    this.startCalculation(onEffect, ...args);

    while (!this.isCalculationCompleted()) {
      this.continueCalculation(onEffect(this.iterationResult.value));
    }

    this.iterator = undefined;
    return this.result;
  }

  async runAsyncWithEffect(onEffect, ...args) {
    this.startCalculation(onEffect, ...args);

    while (!this.isCalculationCompleted()) {
      this.continueCalculation(await onEffect(this.iterationResult.value));
    }

    this.iterator = undefined;
    return this.result;
  }

}) {}
const SynchronousCalculationStarted = Symbol('SynchronousCalculationStarted');
const calculationStartedConstant = {
  value: SynchronousCalculationStarted
};
class CalculationSync extends Mixin([], base => class CalculationSync extends base {
  constructor() {
    super(...arguments);
    this.iterationResult = undefined;
  }

  isCalculationStarted() {
    return Boolean(this.iterationResult);
  }

  isCalculationCompleted() {
    return Boolean(this.iterationResult && this.iterationResult.done);
  }

  get result() {
    return this.iterationResult && this.iterationResult.done ? this.iterationResult.value : undefined;
  }

  startCalculation(onEffect, ...args) {
    this.iterationResult = calculationStartedConstant;
    return this.iterationResult = {
      done: true,
      value: this.calculation.call(this.context || this, onEffect, ...args)
    };
  }

  continueCalculation(value) {
    throw new Error("Can not continue synchronous calculation");
  }

  cleanupCalculation() {
    this.iterationResult = undefined;
  }

  calculation(onEffect, ...args) {
    throw new Error("Abstract method `calculation` called");
  }

  runSyncWithEffect(onEffect, ...args) {
    this.startCalculation(onEffect, ...args);
    return this.result;
  }

  async runAsyncWithEffect(onEffect, ...args) {
    throw new Error('Can not run synchronous calculation asynchronously');
  }

}) {}
function runGeneratorSyncWithEffect(effect, func, args, scope) {
  const gen = func.apply(scope || null, args);
  let iteration = gen.next();

  while (!iteration.done) {
    iteration = gen.next(effect(iteration.value));
  }

  return iteration.value;
}
async function runGeneratorAsyncWithEffect(effect, func, args, scope) {
  const gen = func.apply(scope || null, args);
  let iteration = gen.next();

  while (!iteration.done) {
    const effectResolution = effect(iteration.value);
    if (effectResolution instanceof Promise) iteration = gen.next(await effectResolution);else iteration = gen.next(effectResolution);
  }

  return iteration.value;
}

var OnCycleAction;

(function (OnCycleAction) {
  OnCycleAction["Cancel"] = "Cancel";
  OnCycleAction["Resume"] = "Resume";
})(OnCycleAction || (OnCycleAction = {}));

const WalkSource = Symbol('WalkSource');
const NOT_VISITED = -1;
const VISITED_TOPOLOGICALLY = -2;
class WalkContext extends Base$1 {
  constructor() {
    super(...arguments);
    this.visited = new Map();
    this.toVisit = [];
    this.currentEpoch = 0;
  }

  startFrom(sourceNodes) {
    this.continueFrom(sourceNodes);
  }

  continueFrom(sourceNodes) {
    this.toVisit.push.apply(this.toVisit, sourceNodes.map(node => {
      return {
        node: node,
        from: WalkSource,
        label: undefined
      };
    }));
    this.walkDepth();
  }

  onNode(node, walkStep) {}

  onTopologicalNode(node) {}

  onCycle(node, stack) {
    return OnCycleAction.Cancel;
  }

  forEachNext(node, func) {
    throw new Error("Abstract method called");
  }

  collectNext(node, toVisit, visitInfo) {
    throw new Error("Abstract method called");
  }

  getVisitedInfo(node) {
    return this.visited.get(node);
  }

  setVisitedInfo(node, visitedAt, info) {
    if (!info) {
      info = {
        visitedAt,
        visitEpoch: this.currentEpoch
      };
      this.visited.set(node, info);
    } else {
      info.visitedAt = visitedAt;
      info.visitEpoch = this.currentEpoch;
    }

    return info;
  }

  walkDepth() {
    const visited = this.visited;
    const toVisit = this.toVisit;
    let depth;

    while (depth = toVisit.length) {
      const node = toVisit[depth - 1].node;
      const visitedInfo = this.getVisitedInfo(node);

      if (visitedInfo && visitedInfo.visitedAt === VISITED_TOPOLOGICALLY && visitedInfo.visitEpoch === this.currentEpoch) {
        toVisit.pop();
        continue;
      }

      if (visitedInfo && visitedInfo.visitEpoch === this.currentEpoch && visitedInfo.visitedAt !== NOT_VISITED) {
        if (visitedInfo.visitedAt < depth) {
          if (this.onCycle(node, toVisit) !== OnCycleAction.Resume) break;
        } else {
          visitedInfo.visitedAt = VISITED_TOPOLOGICALLY;
          this.onTopologicalNode(node);
        }

        toVisit.pop();
      } else {
        if (this.onNode(node, toVisit[depth - 1]) === false) break;
        const visitedInfo2 = this.setVisitedInfo(node, depth, visitedInfo);
        const lengthBefore = toVisit.length;
        this.collectNext(node, toVisit, visitedInfo2);

        if (toVisit.length === lengthBefore) {
          visitedInfo2.visitedAt = VISITED_TOPOLOGICALLY;
          this.onTopologicalNode(node);
          toVisit.pop();
        }
      }
    }
  }

}
function cycleInfo(stack) {
  const length = stack.length;
  if (length === 0) return [];
  const cycleSource = stack[length - 1].node;
  const cycle = [cycleSource];
  let current = length - 1;
  let cursor = current;

  while (current >= 0 && stack[current].from !== cycleSource) {
    while (current >= 0 && stack[current].from === stack[cursor].from) current--;

    if (current >= 0) {
      cycle.push(stack[current].node);
      cursor = current;
    }
  }

  if (current < 0) return [];
  cycle.push(cycleSource);
  return cycle.reverse();
}

var EdgeType;

(function (EdgeType) {
  EdgeType[EdgeType["Normal"] = 1] = "Normal";
  EdgeType[EdgeType["Past"] = 2] = "Past";
})(EdgeType || (EdgeType = {}));

let ORIGIN_ID = 0;
class Quark extends MixinAny([Map], base => class Quark extends base {
  constructor() {
    super(...arguments);
    this.createdAt = undefined;
    this.identifier = undefined;
    this.value = undefined;
    this.proposedValue = undefined;
    this.proposedArguments = undefined;
    this.usedProposedOrPrevious = false;
    this.previous = undefined;
    this.origin = undefined;
    this.originId = MIN_SMI;
    this.needToBuildProposedValue = false;
    this.edgesFlow = 0;
    this.visitedAt = NOT_VISITED;
    this.visitEpoch = 0;
    this.promise = undefined;
    this.$outgoingPast = undefined;
  }

  static new(props) {
    const instance = new this();
    props && Object.assign(instance, props);
    return instance;
  }

  get level() {
    return this.identifier.level;
  }

  get calculation() {
    return this.identifier.calculation;
  }

  get context() {
    return this.identifier.context || this.identifier;
  }

  forceCalculation() {
    this.edgesFlow = MAX_SMI;
  }

  cleanup() {
    this.cleanupCalculation();
  }

  isShadow() {
    return Boolean(this.origin && this.origin !== this);
  }

  resetToEpoch(epoch) {
    this.visitEpoch = epoch;
    this.visitedAt = NOT_VISITED;
    if (this.edgesFlow < 0) this.edgesFlow = 0;
    this.usedProposedOrPrevious = false;
    this.cleanupCalculation();
    if (this.value !== undefined) this.clearOutgoing();
    this.promise = undefined;

    if (this.origin && this.origin === this) {
      this.proposedArguments = undefined;

      if (this.value !== undefined) {
        this.proposedValue = this.value;
      }

      this.value = undefined;
    } else {
      this.origin = undefined;
      this.value = undefined;
    }

    if (this.identifier.proposedValueIsBuilt && this.proposedValue !== TombStone) {
      this.needToBuildProposedValue = true;
      this.proposedValue = undefined;
    }
  }

  copyFrom(origin) {
    this.value = origin.value;
    this.proposedValue = origin.proposedValue;
    this.proposedArguments = origin.proposedArguments;
    this.usedProposedOrPrevious = origin.usedProposedOrPrevious;
  }

  clearProperties() {
    this.value = undefined;
    this.proposedValue = undefined;
    this.proposedArguments = undefined;
  }

  mergePreviousOrigin(latestScope) {
    const origin = this.origin;
    if (origin !== this.previous) throw new Error("Invalid state");
    this.copyFrom(origin);
    const outgoing = this.getOutgoing();

    for (const [identifier, quark] of origin.getOutgoing()) {
      const ownOutgoing = outgoing.get(identifier);

      if (!ownOutgoing) {
        const latest = latestScope.get(identifier);
        if (!latest || latest.originId === quark.originId) outgoing.set(identifier, latest || quark);
      }
    }

    if (origin.$outgoingPast !== undefined) {
      const outgoingPast = this.getOutgoingPast();

      for (const [identifier, quark] of origin.getOutgoingPast()) {
        const ownOutgoing = outgoingPast.get(identifier);

        if (!ownOutgoing) {
          const latest = latestScope.get(identifier);
          if (!latest || latest.originId === quark.originId) outgoingPast.set(identifier, latest || quark);
        }
      }
    }

    this.origin = this;
    origin.clearProperties();
    origin.clear();
  }

  setOrigin(origin) {
    this.origin = origin;
    this.originId = origin.originId;
  }

  getOrigin() {
    if (this.origin) return this.origin;
    return this.startOrigin();
  }

  startOrigin() {
    this.originId = ORIGIN_ID++;
    return this.origin = this;
  }

  getOutgoing() {
    return this;
  }

  getOutgoingPast() {
    if (this.$outgoingPast !== undefined) return this.$outgoingPast;
    return this.$outgoingPast = new Map();
  }

  addOutgoingTo(toQuark, type) {
    const outgoing = type === EdgeType.Normal ? this : this.getOutgoingPast();
    outgoing.set(toQuark.identifier, toQuark);
  }

  clearOutgoing() {
    this.clear();
    if (this.$outgoingPast !== undefined) this.$outgoingPast.clear();
  }

  getValue() {
    return this.origin ? this.origin.value : undefined;
  }

  setValue(value) {
    if (this.origin && this.origin !== this) throw new Error('Can not set value to the shadow entry');
    this.getOrigin().value = value;
  }

  hasValue() {
    return this.getValue() !== undefined;
  }

  hasProposedValue() {
    if (this.isShadow()) return false;
    return this.hasProposedValueInner();
  }

  hasProposedValueInner() {
    return this.proposedValue !== undefined;
  }

  getProposedValue(transaction) {
    if (this.needToBuildProposedValue) {
      this.needToBuildProposedValue = false;
      this.proposedValue = this.identifier.buildProposedValue.call(this.identifier.context || this.identifier, this.identifier, this, transaction);
    }

    return this.proposedValue;
  }

  outgoingInTheFutureCb(revision, forEach) {
    let current = this;

    while (current) {
      for (const outgoing of current.getOutgoing().values()) {
        if (outgoing.originId === revision.getLatestEntryFor(outgoing.identifier).originId) forEach(outgoing);
      }

      if (current.isShadow()) current = current.previous;else current = null;
    }
  }

  outgoingInTheFutureAndPastCb(revision, forEach) {
    let current = this;

    while (current) {
      for (const outgoing of current.getOutgoing().values()) {
        const latestEntry = revision.getLatestEntryFor(outgoing.identifier);
        if (latestEntry && outgoing.originId === latestEntry.originId) forEach(outgoing);
      }

      if (current.$outgoingPast !== undefined) {
        for (const outgoing of current.$outgoingPast.values()) {
          const latestEntry = revision.getLatestEntryFor(outgoing.identifier);
          if (latestEntry && outgoing.originId === latestEntry.originId) forEach(outgoing);
        }
      }

      if (current.isShadow()) current = current.previous;else current = null;
    }
  }

  outgoingInTheFutureAndPastTransactionCb(transaction, forEach) {
    let current = this;

    while (current) {
      for (const outgoing of current.getOutgoing().values()) {
        const latestEntry = transaction.getLatestStableEntryFor(outgoing.identifier);
        if (latestEntry && outgoing.originId === latestEntry.originId) forEach(outgoing);
      }

      if (current.$outgoingPast !== undefined) {
        for (const outgoing of current.$outgoingPast.values()) {
          const latestEntry = transaction.getLatestStableEntryFor(outgoing.identifier);
          if (latestEntry && outgoing.originId === latestEntry.originId) forEach(outgoing);
        }
      }

      if (current.isShadow()) current = current.previous;else current = null;
    }
  }

  outgoingInTheFutureTransactionCb(transaction, forEach) {
    let current = this;

    while (current) {
      for (const outgoing of current.getOutgoing().values()) {
        const latestEntry = transaction.getLatestEntryFor(outgoing.identifier);
        if (latestEntry && outgoing.originId === latestEntry.originId) forEach(outgoing);
      }

      if (current.isShadow()) current = current.previous;else current = null;
    }
  }

}) {}
const TombStone = Symbol('Tombstone');

var __decorate$1 = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var Levels;

(function (Levels) {
  Levels[Levels["UserInput"] = 0] = "UserInput";
  Levels[Levels["DependsOnlyOnUserInput"] = 1] = "DependsOnlyOnUserInput";
  Levels[Levels["DependsOnlyOnDependsOnlyOnUserInput"] = 2] = "DependsOnlyOnDependsOnlyOnUserInput";
  Levels[Levels["DependsOnSelfKind"] = 3] = "DependsOnSelfKind";
})(Levels || (Levels = {}));

class Meta extends Base$1 {
  constructor() {
    super(...arguments);
    this.name = undefined;
    this.lazy = false;
    this.total = true;
    this.pure = true;
    this.proposedValueIsBuilt = false;
  }

  calculation(Y) {
    throw new Error("Abstract method `calculation` called");
  }

  equality(v1, v2) {
    return v1 === v2;
  }

}

__decorate$1([prototypeValue(Levels.DependsOnSelfKind)], Meta.prototype, "level", void 0);

__decorate$1([prototypeValue(true)], Meta.prototype, "sync", void 0);

class Identifier extends Meta {
  constructor() {
    super(...arguments);
    this.context = undefined;
  }

  newQuark(createdAt) {
    const newQuark = this.quarkClass.new();
    newQuark.createdAt = createdAt;
    newQuark.identifier = this;
    newQuark.needToBuildProposedValue = this.proposedValueIsBuilt;
    return newQuark;
  }

  write(me, transaction, quark, proposedValue, ...args) {
    quark = quark || transaction.getWriteTarget(me);
    quark.proposedValue = proposedValue;
    quark.proposedArguments = args.length > 0 ? args : undefined;
  }

  writeToTransaction(transaction, proposedValue, ...args) {
    transaction.write(this, proposedValue, ...args);
  }

  writeToGraph(graph, proposedValue, ...args) {
    graph.write(this, proposedValue, ...args);
  }

  readFromGraphAsync(graph) {
    return graph.readAsync(this);
  }

  readFromGraph(graph) {
    return graph.read(this);
  }

  readFromTransaction(transaction) {
    return transaction.read(this);
  }

  readFromTransactionAsync(transaction) {
    return transaction.readAsync(this);
  }

  buildProposedValue(me, quark, transaction) {
    return undefined;
  }

  enterGraph(graph) {}

  leaveGraph(graph) {}

}
const QuarkSync = Quark.mix(CalculationSync.mix(Map));
const QuarkGen = Quark.mix(CalculationGen.mix(Map));
class Variable extends Identifier {
  calculation(YIELD) {
    throw new Error("The 'calculation' method of the variables will never be called. Instead the value will be set directly to quark");
  }

  write(me, transaction, quark, proposedValue, ...args) {
    quark = quark || transaction.getWriteTarget(me);
    quark.value = proposedValue;
    quark.proposedArguments = args.length > 0 ? args : undefined;
  }

}

__decorate$1([prototypeValue(Levels.UserInput)], Variable.prototype, "level", void 0);

__decorate$1([prototypeValue(QuarkSync)], Variable.prototype, "quarkClass", void 0);

function VariableC(...args) {
  return Variable.new(...args);
}
class CalculatedValueSync extends Identifier {
  calculation(YIELD) {
    return YIELD(ProposedOrPrevious);
  }

}

__decorate$1([prototypeValue(QuarkSync)], CalculatedValueSync.prototype, "quarkClass", void 0);

function CalculatedValueSyncC(...args) {
  return CalculatedValueSync.new(...args);
}
class CalculatedValueGen extends Identifier {
  *calculation(YIELD) {
    return yield ProposedOrPrevious;
  }

}

__decorate$1([prototypeValue(QuarkGen)], CalculatedValueGen.prototype, "quarkClass", void 0);

function CalculatedValueGenC(...args) {
  return CalculatedValueGen.new(...args);
}
const throwUnknownIdentifier = identifier => {
  throw new Error(`Unknown identifier ${identifier}`);
};

let CLOCK = 0;
class Revision extends Base$1 {
  constructor() {
    super(...arguments);
    this.createdAt = CLOCK++;
    this.name = 'revision-' + this.createdAt;
    this.previous = undefined;
    this.scope = new Map();
    this.reachableCount = 0;
    this.referenceCount = 0;
    this.selfDependent = new Set();
  }

  getLatestEntryFor(identifier) {
    let revision = this;

    while (revision) {
      const entry = revision.scope.get(identifier);
      if (entry) return entry;
      revision = revision.previous;
    }

    return null;
  }

  hasIdentifier(identifier) {
    const latestEntry = this.getLatestEntryFor(identifier);
    return Boolean(latestEntry && latestEntry.getValue() !== TombStone);
  }

  *previousAxis() {
    let revision = this;

    while (revision) {
      yield revision;
      revision = revision.previous;
    }
  }

}

class LeveledQueue {
  constructor() {
    this.length = 0;
    this.levels = [];
    this.lowestLevel = MAX_SMI;
  }

  getLowestLevel() {
    for (let i = this.lowestLevel !== MAX_SMI ? this.lowestLevel : 0; i < this.levels.length; i++) {
      if (this.levels[i]) return this.lowestLevel = i;
    }

    return this.lowestLevel = MAX_SMI;
  }

  takeLowestLevel() {
    for (let i = this.lowestLevel !== MAX_SMI ? this.lowestLevel : 0; i < this.levels.length; i++) {
      const level = this.levels[i];

      if (level) {
        this.length -= level.length;
        this.levels[i] = null;
        this.lowestLevel = i + 1;
        return level;
      }
    }
  }

  pop() {
    for (let i = this.lowestLevel !== MAX_SMI ? this.lowestLevel : 0; i < this.levels.length; i++) {
      const level = this.levels[i];
      this.lowestLevel = i;

      if (level && level.length > 0) {
        this.length--;
        return level.pop();
      }
    }

    this.lowestLevel = MAX_SMI;
  }

  push(el) {
    const elLevel = el.level;
    let level = this.levels[elLevel];

    if (!level) {
      for (let i = this.levels.length; i < elLevel; i++) this.levels[i] = null;

      level = this.levels[elLevel] = [];
    }

    level.push(el);
    this.length++;
    if (elLevel < this.lowestLevel) this.lowestLevel = elLevel;
  }

  *[Symbol.iterator]() {
    for (let i = 0; i < this.levels.length; i++) {
      const level = this.levels[i];
      if (level) yield* level;
    }
  }

}

class ComputationCycle extends Base$1 {
  toString() {
    const cycleIdentifiers = [];
    const cycleEvents = [];
    this.cycle.forEach(({
      name,
      context
    }) => {
      cycleIdentifiers.push(name);
      if (cycleEvents[cycleEvents.length - 1] !== context) cycleEvents.push(context);
    });
    return 'events: \n' + cycleEvents.map(event => '#' + event.id).join(' => ') + '\n\nidentifiers: \n' + cycleIdentifiers.join('\n');
  }

}
class TransactionCycleDetectionWalkContext extends WalkContext {
  constructor() {
    super(...arguments);
    this.transaction = undefined;
  }

  onCycle(node, stack) {
    return OnCycleAction.Cancel;
  }

  doCollectNext(from, to, toVisit) {
    let visit = this.visited.get(to);

    if (!visit) {
      visit = {
        visitedAt: NOT_VISITED,
        visitEpoch: this.currentEpoch
      };
      this.visited.set(to, visit);
    }

    toVisit.push({
      node: to,
      from,
      label: undefined
    });
  }

  collectNext(from, toVisit) {
    const latestEntry = this.transaction.getLatestEntryFor(from);

    if (latestEntry) {
      latestEntry.outgoingInTheFutureTransactionCb(this.transaction, outgoingEntry => {
        this.doCollectNext(from, outgoingEntry.identifier, toVisit);
      });
    }
  }

}

class TransactionWalkDepth extends Base$1 {
  constructor() {
    super(...arguments);
    this.visited = new Map();
    this.transaction = undefined;
    this.baseRevision = undefined;
    this.pushTo = undefined;
    this.toVisit = [];
    this.currentEpoch = 0;
  }

  startFrom(sourceNodes) {
    this.continueFrom(sourceNodes);
  }

  continueFrom(sourceNodes) {
    this.toVisit.push.apply(this.toVisit, sourceNodes);
    this.walkDepth();
  }

  startNewEpoch() {
    if (this.toVisit.length) throw new Error("Can not start new walk epoch in the middle of the walk");
    this.currentEpoch++;
  }

  onTopologicalNode(identifier, visitInfo) {
    if (!identifier.lazy && identifier.level !== Levels.UserInput) this.pushTo.push(visitInfo);
  }

  onCycle(node, stack) {
    return OnCycleAction.Resume;
  }

  doCollectNext(from, to, toVisit) {
    let quark = this.visited.get(to);

    if (!quark) {
      quark = to.newQuark(this.baseRevision);
      quark.visitEpoch = this.currentEpoch;
      this.visited.set(to, quark);
    }

    toVisit.push(to);
  }

  collectNext(from, toVisit, visitInfo) {
    const latestEntry = this.baseRevision.getLatestEntryFor(from);

    if (latestEntry) {
      visitInfo.previous = latestEntry;
      latestEntry.outgoingInTheFutureAndPastTransactionCb(this.transaction, outgoingEntry => {
        this.doCollectNext(from, outgoingEntry.identifier, toVisit);
      });
    }

    for (const outgoingIdentifier of visitInfo.getOutgoing().keys()) {
      this.doCollectNext(from, outgoingIdentifier, toVisit);
    }

    if (visitInfo.$outgoingPast !== undefined) for (const outgoingIdentifier of visitInfo.getOutgoingPast().keys()) {
      this.doCollectNext(from, outgoingIdentifier, toVisit);
    }
  }

  walkDepth() {
    const visited = this.visited;
    const toVisit = this.toVisit;
    let depth;

    while (depth = toVisit.length) {
      const node = toVisit[depth - 1];
      let visitInfo = visited.get(node);

      if (visitInfo && visitInfo.visitedAt === VISITED_TOPOLOGICALLY && visitInfo.visitEpoch === this.currentEpoch) {
        visitInfo.edgesFlow++;
        toVisit.pop();
        continue;
      }

      if (visitInfo && visitInfo.visitEpoch === this.currentEpoch && visitInfo.visitedAt !== NOT_VISITED) {
        if (visitInfo.visitedAt < depth) {
          if (this.onCycle(node, toVisit) !== OnCycleAction.Resume) break;
          visitInfo.edgesFlow++;
        } else {
          visitInfo.visitedAt = VISITED_TOPOLOGICALLY;
          this.onTopologicalNode(node, visitInfo);
        }

        toVisit.pop();
      } else {
        const lengthBefore = toVisit.length;

        if (!visitInfo) {
          visitInfo = node.newQuark(this.baseRevision);
          visitInfo.visitEpoch = this.currentEpoch;
          visited.set(node, visitInfo);
        }

        this.collectNext(node, toVisit, visitInfo);

        if (visitInfo.visitEpoch < this.currentEpoch) {
          visitInfo.resetToEpoch(this.currentEpoch);
        }

        visitInfo.visitedAt = depth;
        visitInfo.edgesFlow++;

        if (toVisit.length === lengthBefore) {
          visitInfo.visitedAt = VISITED_TOPOLOGICALLY;
          this.onTopologicalNode(node, visitInfo);
          toVisit.pop();
        }
      }
    }
  }

}

const EdgeTypeNormal = EdgeType.Normal;
const EdgeTypePast = EdgeType.Past;
class Transaction$1 extends Base$1 {
  constructor() {
    super(...arguments);
    this.baseRevision = undefined;
    this.candidateClass = Revision;
    this.candidate = undefined;
    this.graph = undefined;
    this.isClosed = false;
    this.walkContext = undefined;
    this.entries = new Map();
    this.stackGen = new LeveledQueue();
    this.activeStack = [];
    this.onEffectSync = undefined;
    this.onEffectAsync = undefined;
    this.propagationStartDate = 0;
    this.lastProgressNotificationDate = 0;
    this.startProgressNotificationsAfterMs = 500;
    this.emitProgressNotificationsEveryMs = 200;
    this.emitProgressNotificationsEveryCalculations = 100;
    this.plannedTotalIdentifiersToCalculate = 0;
    this.ongoing = Promise.resolve();
    this.selfDependedMarked = false;
    this.rejectedWith = undefined;
  }

  initialize(...args) {
    super.initialize(...args);
    this.walkContext = TransactionWalkDepth.new({
      visited: this.entries,
      transaction: this,
      baseRevision: this.baseRevision,
      pushTo: this.stackGen
    });
    if (!this.candidate) this.candidate = this.candidateClass.new({
      previous: this.baseRevision
    });
    this.onEffectSync = this.read.bind(this);
    this.onEffectAsync = this.readAsync.bind(this);
  }

  get dirty() {
    return this.entries.size > 0;
  }

  markSelfDependent() {
    if (this.selfDependedMarked) return;
    this.selfDependedMarked = true;

    for (const selfDependentIden of this.baseRevision.selfDependent) {
      const existing = this.entries.get(selfDependentIden);
      if (existing && existing.getValue() === TombStone) continue;
      this.touch(selfDependentIden);
    }
  }

  getActiveEntry() {
    return this.activeStack[this.activeStack.length - 1];
  }

  yieldAsync(effect) {
    if (effect instanceof Promise) return effect;
    return this.graph[effect.handler](effect, this);
  }

  yieldSync(effect) {
    if (effect instanceof Promise) {
      throw new Error("Can not yield a promise in the synchronous context");
    }

    return this.graph[effect.handler](effect, this);
  }

  readAsync(identifier) {
    if (!(identifier instanceof Identifier)) return this.yieldAsync(identifier);
    let entry;
    const activeEntry = this.getActiveEntry();

    if (activeEntry) {
      entry = this.addEdge(identifier, activeEntry, EdgeTypeNormal);
    } else {
      entry = this.entries.get(identifier);

      if (!entry) {
        const previousEntry = this.baseRevision.getLatestEntryFor(identifier);
        if (!previousEntry) throwUnknownIdentifier(identifier);
        entry = previousEntry.hasValue() ? previousEntry : this.touch(identifier);
      }
    }

    if (entry.hasValue()) return entry.getValue();
    if (entry.promise) return entry.promise;
    if (!entry.previous || !entry.previous.hasValue()) entry.forceCalculation();
    return this.ongoing = entry.promise = this.ongoing.then(() => {
      return (async () => {
        while (this.stackGen.lowestLevel < identifier.level) {
          await runGeneratorAsyncWithEffect(this.onEffectAsync, this.calculateTransitionsStackGen, [this.onEffectAsync, this.stackGen.takeLowestLevel()], this);
        }

        this.markSelfDependent();
        if (entry.getValue() === undefined) return runGeneratorAsyncWithEffect(this.onEffectAsync, this.calculateTransitionsStackGen, [this.onEffectAsync, [entry]], this);
      })();
    }).then(() => {
      if (this.rejectedWith) throw new Error(`Transaction rejected: ${String(this.rejectedWith.reason)}`);
      if (!entry.hasValue()) throw new Error('Computation cycle. Sync');
      return entry.getValue();
    });
  }

  get(identifier) {
    if (!(identifier instanceof Identifier)) return this.yieldSync(identifier);
    let entry;
    const activeEntry = this.getActiveEntry();

    if (activeEntry) {
      entry = this.addEdge(identifier, activeEntry, EdgeTypeNormal);
    } else {
      entry = this.entries.get(identifier);

      if (!entry) {
        const previousEntry = this.baseRevision.getLatestEntryFor(identifier);
        if (!previousEntry) throwUnknownIdentifier(identifier);
        entry = previousEntry.hasValue() ? previousEntry : this.touch(identifier);
      }
    }

    const value1 = entry.getValue();
    if (value1 === TombStone) throwUnknownIdentifier(identifier);
    if (value1 !== undefined && entry.hasValue()) return value1;
    if (entry.promise) return entry.promise;
    if (!entry.previous || !entry.previous.hasValue()) entry.forceCalculation();

    while (this.stackGen.getLowestLevel() < identifier.level) {
      this.calculateTransitionsStackSync(this.onEffectSync, this.stackGen.takeLowestLevel());
    }

    this.markSelfDependent();

    if (identifier.sync) {
      this.calculateTransitionsStackSync(this.onEffectSync, [entry]);
      const value = entry.getValue();
      if (value === undefined) throw new Error('Cycle during synchronous computation');
      if (value === TombStone) throwUnknownIdentifier(identifier);
      return value;
    } else {
      const promise = this.ongoing = entry.promise = this.ongoing.then(() => {
        if (entry.getValue() === undefined) return runGeneratorAsyncWithEffect(this.onEffectAsync, this.calculateTransitionsStackGen, [this.onEffectAsync, [entry]], this);
      }).then(() => {
        if (this.rejectedWith) throw new Error(`Transaction rejected: ${String(this.rejectedWith.reason)}`);
        const value = entry.getValue();
        if (value === undefined) throw new Error('Computation cycle. Async get');
        if (value === TombStone) throwUnknownIdentifier(identifier);
        return value;
      });

      return promise;
    }
  }

  read(identifier) {
    if (!(identifier instanceof Identifier)) return this.yieldSync(identifier);
    let entry;
    const activeEntry = this.getActiveEntry();

    if (activeEntry) {
      entry = this.addEdge(identifier, activeEntry, EdgeTypeNormal);
    } else {
      entry = this.entries.get(identifier);

      if (!entry) {
        const previousEntry = this.baseRevision.getLatestEntryFor(identifier);
        if (!previousEntry) throwUnknownIdentifier(identifier);
        entry = previousEntry.hasValue() ? previousEntry : this.touch(identifier);
      }
    }

    const value1 = entry.getValue();
    if (value1 === TombStone) throwUnknownIdentifier(identifier);
    if (value1 !== undefined) return value1;
    if (!entry.previous || !entry.previous.hasValue()) entry.forceCalculation();

    while (this.stackGen.getLowestLevel() < identifier.level) {
      this.calculateTransitionsStackSync(this.onEffectSync, this.stackGen.takeLowestLevel());
    }

    this.markSelfDependent();
    this.calculateTransitionsStackSync(this.onEffectSync, [entry]);
    const value = entry.getValue();
    if (value === undefined) throw new Error('Cycle during synchronous computation');
    if (value === TombStone) throwUnknownIdentifier(identifier);
    return value;
  }

  readCurrentOrProposedOrPrevious(identifier) {
    const dirtyQuark = this.entries.get(identifier);

    if (dirtyQuark) {
      const value = dirtyQuark.getValue();
      if (value !== undefined) return value;
      if (dirtyQuark.proposedValue !== undefined) return dirtyQuark.proposedValue;
    }

    return this.readPrevious(identifier);
  }

  readCurrentOrProposedOrPreviousAsync(identifier) {
    const dirtyQuark = this.entries.get(identifier);

    if (dirtyQuark) {
      const value = dirtyQuark.getValue();
      if (value !== undefined) return value;
      if (dirtyQuark.proposedValue !== undefined) return dirtyQuark.proposedValue;
    }

    return this.readPreviousAsync(identifier);
  }

  readPrevious(identifier) {
    const previousEntry = this.baseRevision.getLatestEntryFor(identifier);
    if (!previousEntry) return undefined;
    const value = previousEntry.getValue();
    return value !== TombStone ? value === undefined && identifier.lazy ? this.read(identifier) : value : undefined;
  }

  readPreviousAsync(identifier) {
    const previousEntry = this.baseRevision.getLatestEntryFor(identifier);
    if (!previousEntry) return undefined;
    const value = previousEntry.getValue();
    return value !== TombStone ? value !== undefined ? value : this.readAsync(identifier) : undefined;
  }

  readProposedOrPrevious(identifier) {
    const dirtyQuark = this.entries.get(identifier);

    if (dirtyQuark && dirtyQuark.proposedValue !== undefined) {
      return dirtyQuark.proposedValue;
    } else {
      return this.readPrevious(identifier);
    }
  }

  readProposedOrPreviousAsync(identifier) {
    const dirtyQuark = this.entries.get(identifier);

    if (dirtyQuark && dirtyQuark.proposedValue !== undefined) {
      return dirtyQuark.proposedValue;
    } else {
      return this.readPreviousAsync(identifier);
    }
  }

  write(identifier, proposedValue, ...args) {
    if (proposedValue === undefined) proposedValue = null;
    identifier.write.call(identifier.context || identifier, identifier, this, null, proposedValue, ...args);
  }

  getWriteTarget(identifier) {
    return this.touch(identifier).startOrigin();
  }

  acquireQuarkIfExists(identifier) {
    const entry = this.entries.get(identifier);
    return entry && entry.origin === entry ? entry.origin : undefined;
  }

  touch(identifier) {
    const existingEntry = this.entries.get(identifier);
    if (!existingEntry || existingEntry.visitEpoch < this.walkContext.currentEpoch) this.walkContext.continueFrom([identifier]);
    const entry = existingEntry || this.entries.get(identifier);
    entry.forceCalculation();
    return entry;
  }

  hasIdentifier(identifier) {
    const activeEntry = this.entries.get(identifier);
    if (activeEntry && activeEntry.getValue() === TombStone) return false;
    return Boolean(activeEntry || this.baseRevision.getLatestEntryFor(identifier));
  }

  addIdentifier(identifier, proposedValue, ...args) {
    let entry = this.entries.get(identifier);
    const isVariable = identifier.level === Levels.UserInput;

    if (!entry) {
      entry = identifier.newQuark(this.baseRevision);
      entry.previous = this.baseRevision.getLatestEntryFor(identifier);
      entry.forceCalculation();
      this.entries.set(identifier, entry);
      if (!identifier.lazy && !isVariable) this.stackGen.push(entry);
    }

    if (proposedValue !== undefined || isVariable) {
      entry.startOrigin();
      identifier.write.call(identifier.context || identifier, identifier, this, entry, proposedValue === undefined && isVariable ? null : proposedValue, ...args);
    }

    if (entry.getValue() === TombStone) entry.value = undefined;
    identifier.enterGraph(this.graph);
    return entry;
  }

  removeIdentifier(identifier) {
    identifier.leaveGraph(this.graph);
    const entry = this.touch(identifier).startOrigin();
    entry.setValue(TombStone);
  }

  populateCandidateScopeFromTransitions(candidate, scope) {
    if (candidate.scope.size === 0) {
      candidate.scope = scope;
    } else {
      for (const [identifier, quark] of scope) {
        if (quark.isShadow()) {
          const latestEntry = candidate.getLatestEntryFor(identifier);
          quark.getOutgoing().forEach((toQuark, toIdentifier) => latestEntry.getOutgoing().set(toIdentifier, toQuark));
        } else {
          candidate.scope.set(identifier, quark);
        }
      }
    }
  }

  preCommit(args) {
    if (this.isClosed) throw new Error('Can not propagate closed revision');
    this.markSelfDependent();
    this.isClosed = true;
    this.propagationStartDate = Date.now();
    this.plannedTotalIdentifiersToCalculate = this.stackGen.length;
  }

  postCommit() {
    this.populateCandidateScopeFromTransitions(this.candidate, this.entries);
    const entries = this.entries;
    this.walkContext = undefined;
    return {
      revision: this.candidate,
      entries,
      transaction: this
    };
  }

  commit(args) {
    this.preCommit(args);
    this.calculateTransitionsSync(this.onEffectSync);
    return this.postCommit();
  }

  reject(rejection = RejectEffect.new()) {
    this.rejectedWith = rejection;

    for (const quark of this.entries.values()) {
      quark.cleanup();
    }

    this.entries.clear();
    this.walkContext = undefined;
  }

  async commitAsync(args) {
    this.preCommit(args);
    return this.ongoing = this.ongoing.then(() => {
      return runGeneratorAsyncWithEffect(this.onEffectAsync, this.calculateTransitions, [this.onEffectAsync], this);
    }).then(() => {
      return this.postCommit();
    });
  }

  getLatestEntryFor(identifier) {
    let entry = this.entries.get(identifier) || this.baseRevision.getLatestEntryFor(identifier);
    if (entry.getValue() === TombStone) return undefined;
    return entry;
  }

  getLatestStableEntryFor(identifier) {
    let entry = this.entries.get(identifier);

    if (entry) {
      const value = entry.getValue();
      if (value === TombStone) return undefined;
      return value === undefined ? this.baseRevision.getLatestEntryFor(identifier) : entry;
    } else {
      return this.baseRevision.getLatestEntryFor(identifier);
    }
  }

  addEdge(identifierRead, activeEntry, type) {
    const identifier = activeEntry.identifier;
    if (identifier.level < identifierRead.level) throw new Error('Identifier can not read from higher level identifier');
    let entry = this.entries.get(identifierRead);

    if (!entry) {
      const previousEntry = this.baseRevision.getLatestEntryFor(identifierRead);
      if (!previousEntry) throwUnknownIdentifier(identifierRead);
      entry = identifierRead.newQuark(this.baseRevision);
      previousEntry.origin && entry.setOrigin(previousEntry.origin);
      entry.previous = previousEntry;
      this.entries.set(identifierRead, entry);
    }

    entry.addOutgoingTo(activeEntry, type);
    return entry;
  }

  onQuarkCalculationCompleted(entry, value) {
    entry.cleanup();
    const identifier = entry.identifier;
    const previousEntry = entry.previous;
    const sameAsPrevious = Boolean(previousEntry && previousEntry.hasValue() && identifier.equality(value, previousEntry.getValue()));

    if (sameAsPrevious) {
      previousEntry.outgoingInTheFutureAndPastTransactionCb(this, previousOutgoingEntry => {
        const outgoingEntry = this.entries.get(previousOutgoingEntry.identifier);
        if (outgoingEntry) outgoingEntry.edgesFlow--;
      });
      entry.edgesFlow = MAX_SMI;
      entry.setOrigin(previousEntry.origin);
      entry.value = value;
    } else {
      entry.startOrigin();
      entry.setValue(value);
    }

    let ignoreSelfDependency = false;

    if (entry.usedProposedOrPrevious) {
      if (entry.proposedValue !== undefined) {
        if (identifier.equality(value, entry.proposedValue)) ignoreSelfDependency = true;
      } else {
        if (sameAsPrevious || !previousEntry && value === null) ignoreSelfDependency = true;
      }

      if (!ignoreSelfDependency) this.candidate.selfDependent.add(identifier);
    }
  }

  onReadIdentifier(identifierRead, activeEntry, stack) {
    const requestedEntry = this.addEdge(identifierRead, activeEntry, EdgeTypeNormal);

    if (requestedEntry.hasValue() || requestedEntry.value !== undefined) {
      const value = requestedEntry.getValue();
      if (value === TombStone) throwUnknownIdentifier(identifierRead);
      return activeEntry.continueCalculation(value);
    } else if (requestedEntry.isShadow()) {
      requestedEntry.startOrigin();
      requestedEntry.forceCalculation();
      stack.push(requestedEntry);
      return undefined;
    } else {
      if (!requestedEntry.isCalculationStarted()) {
        stack.push(requestedEntry);
        if (!requestedEntry.previous || !requestedEntry.previous.hasValue()) requestedEntry.forceCalculation();
        return undefined;
      } else {
        let cycle;
        const walkContext = TransactionCycleDetectionWalkContext.new({
          transaction: this,

          onCycle(node, stack) {
            cycle = ComputationCycle.new({
              cycle: cycleInfo(stack)
            });
            return OnCycleAction.Cancel;
          }

        });
        walkContext.startFrom([requestedEntry.identifier]);
        this.graph.onComputationCycleHandler(activeEntry, requestedEntry, cycle);
      }
    }
  }

  *calculateTransitions(context) {
    const queue = this.stackGen;

    while (queue.length) {
      yield* this.calculateTransitionsStackGen(context, queue.takeLowestLevel());
    }
  }

  calculateTransitionsSync(context) {
    const queue = this.stackGen;

    while (queue.length) {
      this.calculateTransitionsStackSync(context, queue.takeLowestLevel());
    }
  }

  *calculateTransitionsStackGen(context, stack) {
    if (this.rejectedWith) return;
    this.walkContext.startNewEpoch();
    const entries = this.entries;
    const propagationStartDate = this.propagationStartDate;
    const enableProgressNotifications = this.graph ? this.graph.enableProgressNotifications : false;
    let counter = 0;
    const prevActiveStack = this.activeStack;
    this.activeStack = stack;

    while (stack.length && !this.rejectedWith) {
      if (enableProgressNotifications && !(counter++ % this.emitProgressNotificationsEveryCalculations)) {
        const now = Date.now();
        const elapsed = now - propagationStartDate;

        if (elapsed > this.startProgressNotificationsAfterMs) {
          const lastProgressNotificationDate = this.lastProgressNotificationDate;

          if (!lastProgressNotificationDate || now - lastProgressNotificationDate > this.emitProgressNotificationsEveryMs) {
            this.lastProgressNotificationDate = now;
            this.graph.onPropagationProgressNotification({
              total: this.plannedTotalIdentifiersToCalculate,
              remaining: stack.length,
              phase: 'propagating'
            });
            yield delay(0);
          }
        }
      }

      const entry = stack[stack.length - 1];
      const identifier = entry.identifier;
      const ownEntry = entries.get(identifier);

      if (ownEntry !== entry) {
        entry.cleanup();
        stack.pop();
        continue;
      }

      if (entry.edgesFlow == 0) {
        entry.edgesFlow--;
        const previousEntry = entry.previous;
        previousEntry && previousEntry.outgoingInTheFutureAndPastTransactionCb(this, outgoing => {
          const outgoingEntry = entries.get(outgoing.identifier);
          if (outgoingEntry) outgoingEntry.edgesFlow--;
        });
      }

      if (entry.edgesFlow < 0 && entry.previous && entry.previous.origin) {
        entry.setOrigin(entry.previous.origin);

        if (entry.size === 0) {
          entries.delete(identifier);
        }

        entry.cleanup();
        stack.pop();
        continue;
      }

      if (entry.hasValue() || entry.proposedValue === TombStone) {
        entry.cleanup();
        stack.pop();
        continue;
      }

      const startedAtEpoch = entry.visitEpoch;
      let iterationResult = entry.isCalculationStarted() ? entry.iterationResult : entry.startCalculation(this.onEffectSync);

      while (iterationResult) {
        const value = iterationResult.value === undefined ? null : iterationResult.value;

        if (entry.isCalculationCompleted()) {
          if (entry.visitEpoch == startedAtEpoch) {
            this.onQuarkCalculationCompleted(entry, value);
          }

          stack.pop();
          break;
        } else if (value instanceof Identifier) {
          iterationResult = this.onReadIdentifier(value, entry, stack);
        } else if (value === SynchronousCalculationStarted) {
          stack.pop();
          break;
        } else {
          const effectResult = yield value;
          if (effectResult === BreakCurrentStackExecution) break;

          if (entry.visitEpoch === startedAtEpoch) {
            iterationResult = entry.continueCalculation(effectResult);
          } else {
            stack.pop();
            break;
          }
        }
      }
    }

    this.activeStack = prevActiveStack;
  }

  calculateTransitionsStackSync(context, stack) {
    if (this.rejectedWith) return;
    this.walkContext.startNewEpoch();
    const entries = this.entries;
    const prevActiveStack = this.activeStack;
    this.activeStack = stack;

    while (stack.length && !this.rejectedWith) {
      const entry = stack[stack.length - 1];
      const identifier = entry.identifier;
      const ownEntry = entries.get(identifier);

      if (ownEntry !== entry) {
        entry.cleanup();
        stack.pop();
        continue;
      }

      if (entry.edgesFlow == 0) {
        entry.edgesFlow--;
        const previousEntry = entry.previous;
        previousEntry && previousEntry.outgoingInTheFutureAndPastTransactionCb(this, outgoing => {
          const outgoingEntry = entries.get(outgoing.identifier);
          if (outgoingEntry) outgoingEntry.edgesFlow--;
        });
      }

      if (entry.edgesFlow < 0 && entry.previous && entry.previous.origin) {
        entry.setOrigin(entry.previous.origin);

        if (entry.size === 0) {
          entries.delete(identifier);
        }

        entry.cleanup();
        stack.pop();
        continue;
      }

      if (entry.hasValue() || entry.proposedValue === TombStone) {
        entry.cleanup();
        stack.pop();
        continue;
      }

      const startedAtEpoch = entry.visitEpoch;
      let iterationResult = entry.isCalculationStarted() ? entry.iterationResult : entry.startCalculation(this.onEffectSync);

      while (iterationResult) {
        const value = iterationResult.value === undefined ? null : iterationResult.value;

        if (entry.isCalculationCompleted()) {
          if (entry.visitEpoch == startedAtEpoch) {
            this.onQuarkCalculationCompleted(entry, value);
          }

          stack.pop();
          break;
        } else if (value instanceof Identifier) {
          iterationResult = this.onReadIdentifier(value, entry, stack);
        } else if (value === SynchronousCalculationStarted) {
          stack.pop();
          break;
        } else {
          const effectResult = context(value);
          if (effectResult instanceof Promise) throw new Error("Effect resolved to promise in the synchronous context, check that you marked the asynchronous calculations accordingly");
          if (effectResult === BreakCurrentStackExecution) break;

          if (entry.visitEpoch === startedAtEpoch) {
            iterationResult = entry.continueCalculation(effectResult);
          } else {
            stack.pop();
            break;
          }
        }
      }
    }

    this.activeStack = prevActiveStack;
  }

}

const CommitZero = {
  rejectedWith: null
};
class Listener extends Base$1 {
  constructor() {
    super(...arguments);
    this.handlers = [];
  }

  trigger(value) {
    for (let i = 0; i < this.handlers.length; i++) this.handlers[i](value);
  }

}
class ChronoGraph extends Base$1 {
  constructor() {
    super(...arguments);
    this.baseRevisionStable = undefined;
    this.baseRevisionTentative = undefined;
    this.baseRevision = Revision.new();
    this.topRevision = undefined;
    this.historyLimit = 0;
    this.listeners = new Map();
    this.$activeTransaction = undefined;
    this.isCommitting = false;
    this.enableProgressNotifications = false;
    this.ongoing = Promise.resolve();
    this._isInitialCommit = true;
    this.autoCommitTimeoutId = null;
    this.autoCommit = false;
    this.autoCommitMode = 'sync';
    this.autoCommitHandler = null;
    this.onWriteDuringCommit = 'throw';
    this.onComputationCycle = 'throw';
    this.transactionClass = Transaction$1;
    this.$followingRevision = undefined;
  }

  initialize(...args) {
    super.initialize(...args);
    if (!this.topRevision) this.topRevision = this.baseRevision;

    if (this.autoCommit) {
      this.autoCommitHandler = this.autoCommitMode === 'sync' ? arg => this.commit(arg) : async arg => this.commitAsync(arg);
    }

    this.markAndSweep();
  }

  hasPendingAutoCommit() {
    return this.autoCommitTimeoutId !== null;
  }

  get dirty() {
    return this.activeTransaction.dirty;
  }

  clear() {
    this.reject();
    this.unScheduleAutoCommit();
    this.baseRevision.scope && this.baseRevision.scope.clear();
    this.baseRevision.previous = null;
    this.listeners.clear();
    this.topRevision = this.baseRevision;
    this.$followingRevision = undefined;
    this.$activeTransaction = undefined;
    this.markAndSweep();
  }

  *eachReachableRevision() {
    let isBetweenTopBottom = true;
    let counter = 0;

    for (const revision of this.topRevision.previousAxis()) {
      yield [revision, isBetweenTopBottom || counter < this.historyLimit];

      if (revision === this.baseRevision) {
        isBetweenTopBottom = false;
      } else {
        if (!isBetweenTopBottom) counter++;
      }
    }
  }

  get isInitialCommit() {
    return this._isInitialCommit;
  }

  set isInitialCommit(value) {
    this._isInitialCommit = value;
  }

  markAndSweep() {
    let lastReferencedRevision;
    const unreachableRevisions = [];

    for (const [revision, isReachable] of this.eachReachableRevision()) {
      if (isReachable) {
        revision.reachableCount++;
        lastReferencedRevision = revision;
      } else unreachableRevisions.push(revision);

      revision.referenceCount++;
    }

    unreachableRevisions.unshift(lastReferencedRevision);

    for (let i = unreachableRevisions.length - 1; i >= 1 && unreachableRevisions[i].reachableCount === 0; i--) {
      this.compactRevisions(unreachableRevisions[i - 1], unreachableRevisions[i]);
    }
  }

  compactRevisions(newRev, prevRev) {
    if (prevRev.reachableCount > 0 || newRev.previous !== prevRev) throw new Error("Invalid compact operation");

    if (prevRev.referenceCount <= 1) {
      for (const [identifier, entry] of newRev.scope) {
        if (entry.getValue() === TombStone) {
          prevRev.scope.delete(identifier);
        } else {
          const prevQuark = prevRev.scope.get(identifier);

          if (entry.origin === entry) {
            if (prevQuark) {
              prevQuark.clear();
              prevQuark.clearProperties();
            }
          } else if (prevQuark && entry.origin === prevQuark) {
            entry.mergePreviousOrigin(newRev.scope);
          } else if (identifier.lazy && !entry.origin && prevQuark && prevQuark.origin) {
            entry.startOrigin().proposedValue = prevQuark.origin.value !== undefined ? prevQuark.origin.value : prevQuark.origin.proposedValue;
          }

          entry.previous = undefined;
          prevRev.scope.set(identifier, entry);
        }
      }

      copySetInto(newRev.selfDependent, prevRev.selfDependent);
      newRev.scope = prevRev.scope;
      prevRev.scope = null;
    } else {
      newRev.scope = new Map(concat(prevRev.scope, newRev.scope));
      newRev.selfDependent = new Set(concat(prevRev.selfDependent, newRev.selfDependent));
      prevRev.referenceCount--;
    }

    newRev.previous = null;
  }

  get followingRevision() {
    if (this.$followingRevision !== undefined) return this.$followingRevision;
    const revisions = Array.from(this.topRevision.previousAxis());
    const entries = [];

    for (let i = revisions.length - 1; i > 0; i--) entries.push([revisions[i], revisions[i - 1]]);

    return this.$followingRevision = new Map(entries);
  }

  get activeTransaction() {
    if (this.$activeTransaction) return this.$activeTransaction;
    return this.$activeTransaction = this.transactionClass.new({
      baseRevision: this.baseRevisionTentative || this.baseRevision,
      graph: this
    });
  }

  branch(config) {
    const Constructor = this.constructor;
    return Constructor.new(Object.assign({}, config, {
      baseRevision: this.baseRevision
    }));
  }

  propagate(args) {
    return this.commit(args);
  }

  reject(reason) {
    this.activeTransaction.reject(RejectEffect.new({
      reason
    }));
    this.ongoing = Promise.resolve();
    this.$activeTransaction = undefined;
    this.baseRevisionTentative = undefined;

    if (this.baseRevisionStable) {
      this.baseRevision = this.baseRevisionStable;
      this.baseRevisionStable = undefined;
    }
  }

  commit(args) {
    this.unScheduleAutoCommit();
    this.baseRevisionStable = this.baseRevision;
    const activeTransaction = this.activeTransaction;
    const transactionCommitResult = activeTransaction.commit(args);
    this.$activeTransaction = undefined;
    const result = this.finalizeCommit(transactionCommitResult);
    this.baseRevisionStable = undefined;
    this.isInitialCommit = false;
    return result;
  }

  async propagateAsync(args) {
    return this.commitAsync(args);
  }

  async commitAsync(args) {
    if (this.isCommitting) return this.ongoing;
    this.isCommitting = true;
    this.baseRevisionStable = this.baseRevision;
    return this.ongoing = this.ongoing.then(() => {
      return this.doCommitAsync(args);
    }).then(res => {
      return res;
    }).finally(() => {
      this.baseRevisionStable = undefined;
      this.baseRevisionTentative = undefined;
      this.isInitialCommit = false;
      this.isCommitting = false;
    });
  }

  async doCommitAsync(args) {
    this.unScheduleAutoCommit();
    const activeTransaction = this.activeTransaction;
    const transactionResult = await activeTransaction.commitAsync(args);
    this.baseRevisionTentative = activeTransaction.candidate;
    this.$activeTransaction = undefined;
    const result = this.finalizeCommit(transactionResult);
    await this.finalizeCommitAsync(transactionResult);

    if (this.activeTransaction.dirty) {
      return await this.doCommitAsync(args);
    } else {
      return result;
    }
  }

  finalizeCommit(transactionResult) {
    const {
      revision,
      entries,
      transaction
    } = transactionResult;

    if (!transaction.rejectedWith) {
      if (revision.previous !== this.baseRevision) throw new Error('Invalid revisions chain');

      for (const [revision, isReachable] of this.eachReachableRevision()) {
        if (isReachable) revision.reachableCount--;
        revision.referenceCount--;
      }

      this.baseRevision = this.topRevision = revision;

      for (const [identifier, quarkEntry] of entries) {
        quarkEntry.cleanup();
        if (quarkEntry.isShadow() || !quarkEntry.hasValue()) continue;
        const listener = this.listeners.get(identifier);
        if (listener) listener.trigger(quarkEntry.getValue());
      }

      this.$followingRevision = undefined;
      this.markAndSweep();
    } else {
      if (this.baseRevisionStable) this.baseRevision = this.baseRevisionStable;
      this.baseRevisionStable = undefined;
      this.baseRevisionTentative = undefined;
    }

    return {
      rejectedWith: transaction.rejectedWith
    };
  }

  async finalizeCommitAsync(transactionResult) {}

  onComputationCycleHandler(activeEntry, requestedEntry, cycle) {
    const exception = new Error("Computation cycle:\n" + cycle);
    exception.cycle = cycle;

    switch (this.onComputationCycle) {
      case 'ignore':
        console.log(exception.message);
        return activeEntry.continueCalculation(requestedEntry.proposedValue !== undefined ? requestedEntry.proposedValue : requestedEntry.value);

      case 'throw':
        throw exception;

      case 'reject':
        this.reject(exception);
        break;
    }
  }

  scheduleAutoCommit() {
    if (this.autoCommitTimeoutId === null) {
      this.autoCommitTimeoutId = setTimeout(this.autoCommitHandler, 10);
    }
  }

  unScheduleAutoCommit() {
    if (this.autoCommitTimeoutId !== null) {
      clearTimeout(this.autoCommitTimeoutId);
      this.autoCommitTimeoutId = null;
    }
  }

  variable(value) {
    const variable = VariableC();
    return this.addIdentifier(variable, value === undefined ? null : value);
  }

  variableNamed(name, value) {
    const variable = VariableC({
      name
    });
    return this.addIdentifier(variable, value === undefined ? null : value);
  }

  identifier(calculation, context) {
    const identifier = isGeneratorFunction(calculation) ? CalculatedValueGenC({
      calculation,
      context
    }) : CalculatedValueSyncC({
      calculation,
      context
    });
    return this.addIdentifier(identifier);
  }

  identifierNamed(name, calculation, context) {
    const identifier = calculation.constructor.name === 'GeneratorFunction' ? CalculatedValueGenC({
      name,
      calculation,
      context
    }) : CalculatedValueSyncC({
      name,
      calculation,
      context
    });
    return this.addIdentifier(identifier);
  }

  addIdentifier(identifier, proposedValue, ...args) {
    if (this.isCommitting) {
      if (this.onWriteDuringCommit === 'throw') throw new Error('Adding identifier during commit');else if (this.onWriteDuringCommit === 'warn') ;
    }

    this.activeTransaction.addIdentifier(identifier, proposedValue, ...args);
    if (this.autoCommit) this.scheduleAutoCommit();
    return identifier;
  }

  removeIdentifier(identifier) {
    if (this.isCommitting) {
      if (this.onWriteDuringCommit === 'throw') throw new Error('Removing identifier during commit');else if (this.onWriteDuringCommit === 'warn') ;
    }

    this.activeTransaction.removeIdentifier(identifier);
    this.listeners.delete(identifier);
    if (this.autoCommit) this.scheduleAutoCommit();
  }

  hasIdentifier(identifier) {
    return this.activeTransaction.hasIdentifier(identifier);
  }

  write(identifier, proposedValue, ...args) {
    if (this.isCommitting) {
      if (this.onWriteDuringCommit === 'throw') throw new Error('Write during commit');else if (this.onWriteDuringCommit === 'warn') ;
    }

    this.activeTransaction.write(identifier, proposedValue, ...args);
    if (this.autoCommit) this.scheduleAutoCommit();
  }

  readPrevious(identifier) {
    return this.activeTransaction.readPrevious(identifier);
  }

  readPreviousAsync(identifier) {
    return this.activeTransaction.readPreviousAsync(identifier);
  }

  read(identifier) {
    return this.activeTransaction.read(identifier);
  }

  readAsync(identifier) {
    return this.activeTransaction.readAsync(identifier);
  }

  get(identifier) {
    return this.activeTransaction.get(identifier);
  }

  observe(observerFunc, onUpdated) {
    const identifier = this.addIdentifier(CalculatedValueGen.new({
      lazy: false,
      calculation: observerFunc
    }));
    this.addListener(identifier, onUpdated);
    return identifier;
  }

  observeContext(observerFunc, context, onUpdated) {
    const identifier = this.addIdentifier(CalculatedValueGen.new({
      lazy: false,
      calculation: observerFunc,
      context: context
    }));
    this.addListener(identifier, onUpdated);
    return identifier;
  }

  addListener(identifier, onUpdated) {
    let listener = this.listeners.get(identifier);

    if (!listener) {
      listener = Listener.new();
      this.listeners.set(identifier, listener);
    }

    listener.handlers.push(onUpdated);
  }

  undo() {
    const baseRevision = this.baseRevision;
    const previous = baseRevision.previous;
    if (!previous) return false;
    this.baseRevision = previous;
    this.$activeTransaction = undefined;
    return true;
  }

  redo() {
    const baseRevision = this.baseRevision;
    if (baseRevision === this.topRevision) return false;
    const nextRevision = this.followingRevision.get(baseRevision);
    this.baseRevision = nextRevision;
    this.$activeTransaction = undefined;
    return true;
  }

  onPropagationProgressNotification(notification) {}

  [ProposedOrPreviousSymbol](effect, transaction) {
    const activeEntry = transaction.getActiveEntry();
    activeEntry.usedProposedOrPrevious = true;
    const proposedValue = activeEntry.getProposedValue(transaction);
    if (proposedValue !== undefined) return proposedValue;
    if (!activeEntry.previous) return undefined;
    const identifier = activeEntry.identifier;

    if (identifier.lazy) {
      if (activeEntry.previous.hasValue()) return activeEntry.previous.getValue();
      if (activeEntry.previous.hasProposedValue()) return activeEntry.previous.getProposedValue(transaction);
      return null;
    }

    return transaction.readPrevious(activeEntry.identifier);
  }

  [RejectSymbol](effect, transaction) {
    this.reject(effect.reason);
    return BreakCurrentStackExecution;
  }

  [TransactionSymbol](effect, transaction) {
    return transaction;
  }

  [OwnQuarkSymbol](effect, transaction) {
    return transaction.getActiveEntry();
  }

  [OwnIdentifierSymbol](effect, transaction) {
    const activeEntry = transaction.getActiveEntry();
    return activeEntry.identifier;
  }

  [WriteSymbol](effect, transaction) {
    const activeEntry = transaction.getActiveEntry();
    if (activeEntry.identifier.lazy) throw new Error('Lazy identifiers can not use `Write` effect');
    const writeToHigherLevel = effect.identifier.level > activeEntry.identifier.level;
    if (!writeToHigherLevel) transaction.walkContext.startNewEpoch();
    transaction.write(effect.identifier, ...effect.proposedArgs);
    return writeToHigherLevel ? undefined : BreakCurrentStackExecution;
  }

  [WriteSeveralSymbol](effect, transaction) {
    const activeEntry = transaction.getActiveEntry();
    if (activeEntry.identifier.lazy) throw new Error('Lazy identifiers can not use `Write` effect');
    let writeToHigherLevel = true;
    effect.writes.forEach(writeInfo => {
      if (writeInfo.identifier.level <= activeEntry.identifier.level && writeToHigherLevel) {
        transaction.walkContext.startNewEpoch();
        writeToHigherLevel = false;
      }

      transaction.write(writeInfo.identifier, ...writeInfo.proposedArgs);
    });
    return writeToHigherLevel ? undefined : BreakCurrentStackExecution;
  }

  [PreviousValueOfSymbol](effect, transaction) {
    const activeEntry = transaction.getActiveEntry();
    const source = effect.identifier;
    transaction.addEdge(source, activeEntry, EdgeTypePast);
    return transaction.readPrevious(source);
  }

  [ProposedValueOfSymbol](effect, transaction) {
    const activeEntry = transaction.getActiveEntry();
    const source = effect.identifier;
    transaction.addEdge(source, activeEntry, EdgeTypePast);
    const quark = transaction.entries.get(source);
    const proposedValue = quark && !quark.isShadow() ? quark.getProposedValue(transaction) : undefined;
    return proposedValue;
  }

  [HasProposedValueSymbol](effect, transaction) {
    const activeEntry = transaction.getActiveEntry();
    const source = effect.identifier;
    transaction.addEdge(source, activeEntry, EdgeTypePast);
    const quark = transaction.entries.get(source);
    return quark ? quark.hasProposedValue() : false;
  }

  [ProposedOrPreviousValueOfSymbol](effect, transaction) {
    const activeEntry = transaction.getActiveEntry();
    const source = effect.identifier;
    transaction.addEdge(source, activeEntry, EdgeTypePast);
    return transaction.readProposedOrPrevious(source);
  }

  [UnsafeProposedOrPreviousValueOfSymbol](effect, transaction) {
    return transaction.readProposedOrPrevious(effect.identifier);
  }

  [UnsafePreviousValueOfSymbol](effect, transaction) {
    return transaction.readPrevious(effect.identifier);
  }

  [ProposedArgumentsOfSymbol](effect, transaction) {
    const activeEntry = transaction.getActiveEntry();
    const source = effect.identifier;
    transaction.addEdge(source, activeEntry, EdgeTypePast);
    const quark = transaction.entries.get(source);
    return quark && !quark.isShadow() ? quark.proposedArguments : undefined;
  }

}

class EntityMeta extends Base$1 {
  constructor() {
    super(...arguments);
    this.name = undefined;
    this.ownFields = new Map();
    this.schema = undefined;
    this.$skeleton = {};
    this.$allFields = undefined;
  }

  hasField(name) {
    return this.getField(name) !== undefined;
  }

  getField(name) {
    return this.allFields.get(name);
  }

  addField(field) {
    const name = field.name;
    if (!name) throw new Error(`Field must have a name`);
    if (this.ownFields.has(name)) throw new Error(`Field with name [${name}] already exists`);
    field.entity = this;
    this.ownFields.set(name, field);
    return field;
  }

  forEachParent(func) {
    let entity = this;

    while (entity) {
      func(entity);
      entity = entity.parentEntity;
    }
  }

  get allFields() {
    if (this.$allFields !== undefined) return this.$allFields;
    const allFields = new Map();
    const visited = new Set();
    this.forEachParent(entity => {
      entity.ownFields.forEach((field, name) => {
        if (!visited.has(name)) {
          visited.add(name);
          allFields.set(name, field);
        }
      });
    });
    return this.$allFields = allFields;
  }

  forEachField(func) {
    this.allFields.forEach(func);
  }

}

var ReadMode;

(function (ReadMode) {
  ReadMode[ReadMode["Current"] = 0] = "Current";
  ReadMode[ReadMode["Previous"] = 1] = "Previous";
  ReadMode[ReadMode["ProposedOrPrevious"] = 2] = "ProposedOrPrevious";
  ReadMode[ReadMode["CurrentOrProposedOrPrevious"] = 3] = "CurrentOrProposedOrPrevious";
})(ReadMode || (ReadMode = {}));

class Replica extends Mixin([ChronoGraph], base => class Replica extends base {
  constructor() {
    super(...arguments);
    this.autoCommit = true;
    this.readMode = ReadMode.Current;
  }

  addEntity(entity) {
    entity.enterGraph(this);
  }

  addEntities(entities) {
    entities.forEach(entity => this.addEntity(entity));
  }

  removeEntity(entity) {
    entity.leaveGraph(this);
  }

  removeEntities(entities) {
    entities.forEach(entity => this.removeEntity(entity));
  }

}) {}

class FieldIdentifier extends Mixin([Identifier], base => class FieldIdentifier extends base {
  constructor() {
    super(...arguments);
    this.field = undefined;
    this.self = undefined;
    this.DATA = undefined;
  }

  getFromGraph(graph) {
    if (graph) {
      if (graph.readMode === ReadMode.Current) return graph.get(this);
      if (graph.readMode === ReadMode.Previous) return graph.activeTransaction.readPrevious(this);
      if (graph.readMode === ReadMode.ProposedOrPrevious) graph.activeTransaction.readProposedOrPrevious(this);
      return graph.activeTransaction.readCurrentOrProposedOrPrevious(this);
    } else return this.DATA;
  }

  readFromGraph(graph) {
    if (graph) return graph.read(this);else return this.DATA;
  }

  writeToGraph(graph, proposedValue, ...args) {
    if (graph) graph.write(this, proposedValue, ...args);else this.DATA = proposedValue;
  }

  leaveGraph(graph) {
    const entry = graph.activeTransaction.getLatestStableEntryFor(this);
    if (entry) this.DATA = entry.getValue();
    super.leaveGraph(graph);
  }

  toString() {
    return this.name;
  }

}) {}
class MinimalFieldIdentifierSync extends FieldIdentifier.mix(CalculatedValueSync) {}
class MinimalFieldIdentifierGen extends FieldIdentifier.mix(CalculatedValueGen) {}
class MinimalFieldVariable extends FieldIdentifier.mix(Variable) {}
class EntityIdentifier extends Mixin([Identifier], base => class EntityIdentifier extends base {
  constructor() {
    super(...arguments);
    this.entity = undefined;
    this.self = undefined;
  }

  equality() {
    return false;
  }

  toString() {
    return `Entity identifier [${this.self}]`;
  }

}) {}
class MinimalEntityIdentifier extends EntityIdentifier.mix(CalculatedValueGen) {}

class Field$1 extends Meta {
  constructor() {
    super(...arguments);
    this.persistent = true;
  }

  getIdentifierClass(calculationFunction) {
    if (this.identifierCls) return this.identifierCls;
    if (!calculationFunction) return MinimalFieldVariable;
    return isGeneratorFunction(calculationFunction) ? MinimalFieldIdentifierGen : MinimalFieldIdentifierSync;
  }

}

const isEntityMarker = Symbol('isEntity');
class Entity extends Mixin([], base => {
  class Entity extends base {
    [isEntityMarker]() {}

    get $entity() {
      return createEntityOnPrototype(this.constructor.prototype);
    }

    get $() {
      const $ = {};
      this.$entity.forEachField((field, name) => {
        $[name] = this.createFieldIdentifier(field);
      });

      {
        return defineProperty$6(this, '$', $);
      }
    }

    get $$() {
      return defineProperty$6(this, '$$', MinimalEntityIdentifier.new({
        name: this.$entityName,
        entity: this.$entity,
        calculation: this.calculateSelf,
        context: this,
        self: this
      }));
    }

    get $entityName() {
      return this.constructor.name || this.$entity.name;
    }

    *calculateSelf() {
      return this;
    }

    createFieldIdentifier(field) {
      const name = field.name;
      const entity = this.$entity;
      const constructor = this.constructor;
      const skeleton = entity.$skeleton;
      if (!skeleton[name]) skeleton[name] = constructor.getIdentifierTemplateClass(this, field);
      const identifier = new skeleton[name]();
      identifier.context = this;
      identifier.self = this;
      identifier.name = `${this.$$.name}.$.${field.name}`;
      return identifier;
    }

    forEachFieldIdentifier(func) {
      this.$entity.forEachField((field, name) => func(this.$[name], name));
    }

    enterGraph(replica) {
      if (this.graph) throw new Error('Already entered replica');
      this.graph = replica;
      replica.addIdentifier(this.$$);
      this.$entity.forEachField((field, name) => {
        const identifier = this.$[name];
        replica.addIdentifier(identifier, identifier.DATA);
        identifier.DATA = undefined;
      });
    }

    leaveGraph(graph) {
      const ownGraph = this.graph;
      const removeFrom = graph || ownGraph;
      if (!removeFrom) return;
      this.$entity.forEachField((field, name) => removeFrom.removeIdentifier(this.$[name]));
      removeFrom.removeIdentifier(this.$$);
      if (removeFrom === ownGraph) this.graph = undefined;
    }

    propagate(arg) {
      return this.commit(arg);
    }

    commit(arg) {
      const graph = this.graph;
      if (!graph) return CommitZero;
      return graph.commit(arg);
    }

    async propagateAsync() {
      return this.commitAsync();
    }

    async commitAsync(arg) {
      const graph = this.graph;
      if (!graph) return Promise.resolve(CommitZero);
      return graph.commitAsync(arg);
    }

    static get $entity() {
      return ensureEntityOnPrototype(this.prototype);
    }

    static getIdentifierTemplateClass(me, field) {
      const name = field.name;
      const config = {
        name: `${me.$$.name}.$.${name}`,
        field: field
      };
      if (field.hasOwnProperty('sync')) config.sync = field.sync;
      if (field.hasOwnProperty('lazy')) config.lazy = field.lazy;
      if (field.hasOwnProperty('equality')) config.equality = field.equality;
      const calculationFunction = me.$calculations && me[me.$calculations[name]];
      if (calculationFunction) config.calculation = calculationFunction;
      const writeFunction = me.$writes && me[me.$writes[name]];
      if (writeFunction) config.write = writeFunction;
      const buildProposedFunction = me.$buildProposed && me[me.$buildProposed[name]];

      if (buildProposedFunction) {
        config.buildProposedValue = buildProposedFunction;
        config.proposedValueIsBuilt = true;
      }

      const template = field.getIdentifierClass(calculationFunction).new(config);

      const TemplateClass = function () {};

      TemplateClass.prototype = template;
      return TemplateClass;
    }

    run(methodName, ...args) {
      const onEffect = effect => {
        if (effect instanceof Identifier) return this.graph.read(effect);
        throw new Error("Helper methods can not yield effects during computation");
      };

      return runGeneratorSyncWithEffect(onEffect, this[methodName], args, this);
    }

    static createPropertyAccessorsFor(fieldName) {
      const propertyKey = fieldName;
      const target = this.prototype;
      Object.defineProperty(target, propertyKey, {
        get: function () {
          return this.$[propertyKey].getFromGraph(this.graph);
        },
        set: function (value) {
          this.$[propertyKey].writeToGraph(this.graph, value);
        }
      });
    }

    static createMethodAccessorsFor(fieldName) {
      const propertyKey = fieldName;
      const target = this.prototype;
      const getterFnName = `get${uppercaseFirst(propertyKey)}`;
      const setterFnName = `set${uppercaseFirst(propertyKey)}`;
      const putterFnName = `put${uppercaseFirst(propertyKey)}`;

      if (!(getterFnName in target)) {
        target[getterFnName] = function () {
          return this.$[propertyKey].getFromGraph(this.graph);
        };
      }

      if (!(setterFnName in target)) {
        target[setterFnName] = function (value, ...args) {
          this.$[propertyKey].writeToGraph(this.graph, value, ...args);
          return this.graph ? this.graph.autoCommitMode === 'sync' ? this.graph.commit() : this.graph.commitAsync() : Promise.resolve(CommitZero);
        };
      }

      if (!(putterFnName in target)) {
        target[putterFnName] = function (value, ...args) {
          this.$[propertyKey].writeToGraph(this.graph, value, ...args);
        };
      }
    }

  }

  return Entity;
}) {}
const createEntityOnPrototype = proto => {
  let parent = Object.getPrototypeOf(proto);
  return defineProperty$6(proto, '$entity', EntityMeta.new({
    parentEntity: parent.hasOwnProperty(isEntityMarker) ? null : parent.$entity,
    name: proto.constructor.name
  }));
};
const ensureEntityOnPrototype = proto => {
  if (!proto.hasOwnProperty('$entity')) createEntityOnPrototype(proto);
  return proto.$entity;
};
const generic_field = (fieldConfig, fieldCls = Field$1) => {
  return function (target, fieldName) {
    const entity = ensureEntityOnPrototype(target);
    const field = entity.addField(fieldCls.new(Object.assign(fieldConfig || {}, {
      name: fieldName
    })));
    const cons = target.constructor;
    cons.createPropertyAccessorsFor(fieldName);
    cons.createMethodAccessorsFor(fieldName);
  };
};
const field = generic_field;
const calculate = function (fieldName) {
  return function (target, propertyKey, _descriptor) {
    ensureEntityOnPrototype(target);
    let calculations;

    if (!target.$calculations) {
      calculations = target.$calculations = {};
    } else {
      if (!target.hasOwnProperty('$calculations')) {
        calculations = target.$calculations = Object.create(target.$calculations);
      } else calculations = target.$calculations;
    }

    calculations[fieldName] = propertyKey;
  };
};
const write = function (fieldName) {
  return function (target, propertyKey, _descriptor) {
    ensureEntityOnPrototype(target);
    let writes;

    if (!target.$writes) {
      writes = target.$writes = {};
    } else {
      if (!target.hasOwnProperty('$writes')) {
        writes = target.$writes = Object.create(target.$writes);
      } else writes = target.$writes;
    }

    writes[fieldName] = propertyKey;
  };
};
const build_proposed = function (fieldName) {
  return function (target, propertyKey, _descriptor) {
    ensureEntityOnPrototype(target);
    let buildProposed;

    if (!target.$buildProposed) {
      buildProposed = target.$buildProposed = {};
    } else {
      if (!target.hasOwnProperty('$buildProposed')) {
        buildProposed = target.$buildProposed = Object.create(target.$buildProposed);
      } else buildProposed = target.$buildProposed;
    }

    buildProposed[fieldName] = propertyKey;
  };
};

var __decorate$2 = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};
class ReferenceField extends Mixin([Field$1], base => class ReferenceField extends base {
  constructor() {
    super(...arguments);
    this.identifierCls = MinimalReferenceIdentifier;
  }

}) {}
class ReferenceIdentifier extends Mixin([FieldIdentifier], base => {
  class ReferenceIdentifier extends base {
    constructor() {
      super(...arguments);
      this.field = undefined;
      this.proposedValueIsBuilt = true;
    }

    hasBucket() {
      return Boolean(this.field.bucket);
    }

    getBucket(entity) {
      return entity.$[this.field.bucket];
    }

    buildProposedValue(me, quark, transaction) {
      const proposedValue = quark.proposedValue;
      if (proposedValue === null) return null;
      const value = isInstanceOf(proposedValue, Entity) ? proposedValue : me.resolve(proposedValue);

      if (value && me.hasBucket()) {
        me.getBucket(value).addToBucket(transaction, me.self);
      }

      return value;
    }

    resolve(locator) {
      const resolver = this.field.resolver;
      return resolver ? resolver.call(this.self, locator) : null;
    }

    enterGraph(graph) {
      if (this.hasBucket()) {
        const value = graph.activeTransaction.readProposedOrPrevious(this);

        if (value instanceof Entity) {
          this.getBucket(value).addToBucket(graph.activeTransaction, this.self);
        }
      }

      super.enterGraph(graph);
    }

    leaveGraph(graph) {
      if (this.hasBucket()) {
        const value = graph.activeTransaction.readProposedOrPrevious(this);

        if (value instanceof Entity) {
          this.getBucket(value).removeFromBucket(graph.activeTransaction, this.self);
        }
      }

      super.leaveGraph(graph);
    }

    write(me, transaction, q, proposedValue) {
      const quark = q || transaction.acquireQuarkIfExists(me);

      if (me.hasBucket()) {
        if (quark) {
          const prevValue = quark.getValue();

          if (prevValue instanceof Entity) {
            me.getBucket(prevValue).removeFromBucket(transaction, me.self);
          }
        } else if (transaction.baseRevision.hasIdentifier(me)) {
          const value = transaction.readPrevious(me);

          if (value instanceof Entity) {
            me.getBucket(value).removeFromBucket(transaction, me.self);
          }
        }
      }

      super.write(me, transaction, q, proposedValue);
    }

  }

  __decorate$2([prototypeValue(Levels.DependsOnlyOnUserInput)], ReferenceIdentifier.prototype, "level", void 0);

  __decorate$2([prototypeValue(QuarkSync)], ReferenceIdentifier.prototype, "quarkClass", void 0);

  return ReferenceIdentifier;
}) {}
class MinimalReferenceIdentifier extends ReferenceIdentifier.mix(FieldIdentifier.mix(CalculatedValueSync)) {}

var __decorate$3 = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};
class ReferenceBucketField extends Mixin([Field$1], base => class ReferenceBucketField extends base {
  constructor() {
    super(...arguments);
    this.persistent = false;
    this.identifierCls = MinimalReferenceBucketIdentifier;
  }

}) {}
var BucketMutationType;

(function (BucketMutationType) {
  BucketMutationType["Add"] = "Add";
  BucketMutationType["Remove"] = "Remove";
})(BucketMutationType || (BucketMutationType = {}));

class ReferenceBucketQuark extends Mixin([Quark], base => class ReferenceBucketQuark extends base {
  constructor() {
    super(...arguments);
    this.mutations = [];
    this.previousValue = undefined;
  }

  hasProposedValueInner() {
    return this.mutations.length > 0;
  }

}) {}
const MinimalReferenceBucketQuark = ReferenceBucketQuark.mix(QuarkSync);
class ReferenceBucketIdentifier extends Mixin([FieldIdentifier], base => {
  class ReferenceBucketIdentifier extends base {
    constructor() {
      super(...arguments);
      this.proposedValueIsBuilt = true;
    }

    addToBucket(transaction, entity) {
      const quark = transaction.getWriteTarget(this);
      quark.mutations.push({
        type: BucketMutationType.Add,
        entity
      });
      const baseRevision = transaction.baseRevision;
      if (!quark.previousValue && baseRevision.hasIdentifier(this)) quark.previousValue = transaction.readPrevious(this);
    }

    removeFromBucket(transaction, entity) {
      const preQuark = transaction.entries.get(this);
      if (preQuark && preQuark.getValue() === TombStone) return;
      const quark = transaction.getWriteTarget(this);
      quark.mutations.push({
        type: BucketMutationType.Remove,
        entity
      });
      const baseRevision = transaction.baseRevision;
      if (!quark.previousValue && baseRevision.hasIdentifier(this)) quark.previousValue = transaction.readPrevious(this);
    }

    buildProposedValue(me, quarkArg, transaction) {
      const quark = quarkArg;
      const newValue = new Set(quark.previousValue);

      for (let i = 0; i < quark.mutations.length; i++) {
        const {
          type,
          entity
        } = quark.mutations[i];

        if (type === BucketMutationType.Remove) {
          newValue.delete(entity);
        } else if (type === BucketMutationType.Add) {
          newValue.add(entity);
        }
      }

      return newValue;
    }

    leaveGraph(graph) {
      super.leaveGraph(graph);
      this.DATA = undefined;
    }

  }

  __decorate$3([prototypeValue(Levels.DependsOnlyOnDependsOnlyOnUserInput)], ReferenceBucketIdentifier.prototype, "level", void 0);

  __decorate$3([prototypeValue(MinimalReferenceBucketQuark)], ReferenceBucketIdentifier.prototype, "quarkClass", void 0);

  return ReferenceBucketIdentifier;
}) {}
class MinimalReferenceBucketIdentifier extends ReferenceBucketIdentifier.mix(FieldIdentifier.mix(CalculatedValueSync)) {}

var __decorate$4 = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const isSerializableEqual = function (oldValue, newValue) {
  return oldValue === newValue || ((newValue === null || newValue === void 0 ? void 0 : newValue.isBase) ? this.serialize(newValue) : newValue) == ((oldValue === null || oldValue === void 0 ? void 0 : oldValue.isBase) ? this.serialize(oldValue) : oldValue);
};
class ModelField extends Field$1 {
  constructor() {
    super(...arguments);
    this.modelFieldConfig = {};
  }

  getIdentifierClass(calculationFunction) {
    if (this.identifierCls) return this.identifierCls;
    if (!calculationFunction) return MinimalChronoModelFieldVariable;
    return isGeneratorFunction(calculationFunction) ? MinimalChronoModelFieldIdentifierGen : MinimalChronoModelFieldIdentifierSync;
  }

}
class ModelReferenceField extends ReferenceField.mix(ModelField) {
  constructor() {
    super(...arguments);
    this.identifierCls = ChronoModelReferenceFieldIdentifier;
  }

}
class ModelBucketField extends ReferenceBucketField.mix(Field$1) {
  constructor() {
    super(...arguments);
    this.identifierCls = ChronoModelReferenceBucketFieldIdentifier;
  }

}
const IsChronoModelSymbol = Symbol('IsChronoModelSymbol');
class ChronoModelFieldIdentifier extends Mixin([FieldIdentifier], base => {
  const superProto = base.prototype;

  class ChronoModelFieldIdentifier extends base {
    [IsChronoModelSymbol]() {}

    getFromGraph(graph) {
      if (graph) {
        if (graph.readMode === ReadMode.CurrentOrProposedOrPrevious) {
          if (this.sync) return graph.get(this);else return graph.activeTransaction.readCurrentOrProposedOrPrevious(this);
        }

        return superProto.getFromGraph.call(this, graph);
      } else return this.DATA;
    }

    writeToGraph(graph, proposedValue, ...args) {
      if (graph) {
        superProto.writeToGraph.call(this, graph, proposedValue, ...args);
      } else {
        this.DATA = proposedValue;
        this.self.set(this.field.name, proposedValue, false, false, true);
      }
    }

    write(me, transaction, quark, proposedValue, ...args) {
      proposedValue = me.convert(proposedValue);
      superProto.write.call(this, me, transaction, quark, proposedValue, ...args);
    }

    convert(value) {
      const field = this.field;
      const fieldDefinition = this.self.getFieldDefinition(field.name);

      if (fieldDefinition === null || fieldDefinition === void 0 ? void 0 : fieldDefinition.convert) {
        value = fieldDefinition.convert(value);
      } else if (field.converter) {
        value = field.converter(value, field);
      }

      return value;
    }

    equality(v1, v2) {
      if (v1 instanceof Date && v2 instanceof Date) return v1.getTime() === v2.getTime();
      return v1 === v2;
    }

  }

  __decorate$4([prototypeValue(false)], ChronoModelFieldIdentifier.prototype, "sync", void 0);

  return ChronoModelFieldIdentifier;
}) {}
class ChronoModelReferenceFieldQuark extends Mixin([ReferenceBucketQuark], base => {
  const superProto = base.prototype;

  class ChronoModelReferenceFieldQuark extends base {
    setValue(value) {
      superProto.setValue.call(this, value);
      if (value !== TombStone) this.identifier.DATA = value;
    }

  }

  return ChronoModelReferenceFieldQuark;
}) {}
const MinimalChronoModelReferenceFieldQuark = ChronoModelReferenceFieldQuark.mix(MinimalReferenceBucketQuark);
class ChronoModelReferenceFieldIdentifier extends ReferenceIdentifier.mix(ChronoModelFieldIdentifier.mix(MinimalFieldIdentifierSync)) {
  buildProposedValue(me, q, transaction) {
    const quark = q;
    const proposedValue = quark.proposedValue;

    if (proposedValue === null) {
      transaction.candidate.failedResolutionReferences.delete(quark.identifier);
      return null;
    }

    if (isInstanceOf(proposedValue, Entity)) {
      if (me.hasBucket()) me.getBucket(proposedValue).addToBucket(transaction, me.self);
      transaction.candidate.failedResolutionReferences.delete(quark.identifier);
      return proposedValue;
    }

    const resolved = me.resolve(proposedValue);

    if (isInstanceOf(resolved, Entity)) {
      if (me.hasBucket()) me.getBucket(resolved).addToBucket(transaction, me.self);
      transaction.candidate.failedResolutionReferences.delete(quark.identifier);
      return resolved;
    } else {
      transaction.candidate.failedResolutionReferences.set(quark.identifier, proposedValue);
      return null;
    }
  }

}

__decorate$4([prototypeValue(true)], ChronoModelReferenceFieldIdentifier.prototype, "sync", void 0);

__decorate$4([prototypeValue(MinimalChronoModelReferenceFieldQuark)], ChronoModelReferenceFieldIdentifier.prototype, "quarkClass", void 0);

class ChronoModelReferenceBucketFieldIdentifier extends ReferenceBucketIdentifier.mix(ChronoModelFieldIdentifier.mix(MinimalFieldIdentifierSync)) {}

__decorate$4([prototypeValue(true)], ChronoModelReferenceBucketFieldIdentifier.prototype, "sync", void 0);

class MinimalChronoModelFieldIdentifierSync extends ChronoModelFieldIdentifier.mix(MinimalFieldIdentifierSync) {}
class MinimalChronoModelFieldIdentifierGen extends ChronoModelFieldIdentifier.mix(MinimalFieldIdentifierGen) {}
class MinimalChronoModelFieldVariable extends ChronoModelFieldIdentifier.mix(MinimalFieldVariable) {}
const model_field = function (modelFieldConfig = {}, chronoFieldConfig = {}, chronoFieldClass = ModelField) {
  return function (target, propertyKey) {
    const decoratorFn = generic_field(Object.assign({
      modelFieldConfig
    }, chronoFieldConfig), chronoFieldClass);
    decoratorFn(target, propertyKey);
    injectStaticFieldsProperty(target.constructor);
  };
};
const injectStaticFieldsProperty = prototype => {
  if (!prototype.hasOwnProperty('fields')) {
    Object.defineProperty(prototype, 'fields', {
      get: function () {
        return getDecoratedModelFields(this);
      }
    });
  }
};
const getDecoratedModelFields = constr => {
  const proto = constr.prototype;
  const result = [];

  if (proto.hasOwnProperty('$entity')) {
    proto.$entity.ownFields.forEach(field => {
      if (field instanceof ModelField) {
        const config = field.modelFieldConfig || {};

        if (!config.convert && field.converter && field.converter !== dateConverter) {
          config.convert = field.converter;
        }

        result.push(Object.assign(config, {
          $chrono: field,
          name: field.name
        }));
      }
    });
  }

  return result;
};
const dateConverter = (date, field) => {
  var _a, _b;

  if (date === null) {
    return null;
  }

  if (!(date instanceof Date)) {
    date = DateHelper.parse(date, ((_a = field.modelFieldConfig) === null || _a === void 0 ? void 0 : _a.format) || ((_b = field.modelFieldConfig) === null || _b === void 0 ? void 0 : _b.dateFormat) || DateHelper.defaultParseFormat);
  }

  return date || undefined;
};

class ChronoModelMixin extends Mixin([Entity, Model], base => {
  const superProto = base.prototype;

  class ChronoModelMixin extends base {
    construct(config, ...args) {
      this.constructor.exposeProperties();
      this.originalData = config = config || {};
      superProto.construct.call(this, config, ...args);
    }

    copy(newId = null, deep = null) {
      const copy = superProto.copy.call(this, newId, deep);

      if (ObjectHelper.isObject(deep) && !deep.skipFieldIdentifiers || !ObjectHelper.isObject(deep)) {
        this.forEachFieldIdentifier((identifier, fieldName) => {
          copy[fieldName] = this[fieldName];
        });
      }

      return copy;
    }

    applyValue(useProp, key, value, skipAccessors, field) {
      const chronoField = this.$entity.getField(key);
      if (chronoField) useProp = true;

      if (skipAccessors) {
        useProp = false;
      }

      superProto.applyValue.call(this, useProp, key, value, skipAccessors, field);
    }

    afterChange(toSet, wasSet, silent, fromRelationUpdate, skipAccessors) {
      if (!skipAccessors && Object.keys(wasSet).some(key => this.$entity.getField(key))) {
        superProto.afterChange.call(this, toSet, wasSet, true, fromRelationUpdate, skipAccessors);
      } else {
        superProto.afterChange.apply(this, arguments);
      }
    }

    get data() {
      return this._data;
    }

    set data(data) {
      this._data = data;
      const {
        fields
      } = this;

      for (let i = 0; i < fields.length; i++) {
        const field = fields[i];
        const chronoField = this.$entity.getField(field.name);

        if (chronoField) {
          const value = field.complexMapping ? ObjectHelper.getPath(data, field.dataSource) : data[field.dataSource];
          this[field.name] = value;
        }
      }
    }

    get $entityName() {
      const className = this.constructor.name || this.$entity.name;
      const id = this.id;
      return `${className}${id != null ? '-' + String(id) : ''}`;
    }

  }

  return ChronoModelMixin;
}) {}

class AbstractPartOfProjectGenericMixin extends Mixin([], base => {
  const superProto = base.prototype;

  class AbstractPartOfProjectGenericMixin extends base {
    async commitAsync() {
      return this.project.commitAsync();
    }

    set project(project) {
      this.$project = project;
    }

    get project() {
      return this.$project;
    }

    calculateProject() {
      throw new Error("Implement me");
    }

    setProject(project) {
      return this.project = project;
    }

    getProject() {
      if (this.project) return this.project;
      return this.setProject(this.calculateProject());
    }

    getAssignmentStore() {
      const project = this.getProject();
      return project === null || project === void 0 ? void 0 : project.assignmentStore;
    }

    getDependencyStore() {
      const project = this.getProject();
      return project === null || project === void 0 ? void 0 : project.dependencyStore;
    }

    getEventStore() {
      const project = this.getProject();
      return project === null || project === void 0 ? void 0 : project.eventStore;
    }

    getResourceStore() {
      const project = this.getProject();
      return project === null || project === void 0 ? void 0 : project.resourceStore;
    }

    getCalendarManagerStore() {
      const project = this.getProject();
      return project === null || project === void 0 ? void 0 : project.calendarManagerStore;
    }

  }

  return AbstractPartOfProjectGenericMixin;
}) {}

class ChronoPartOfProjectGenericMixin extends Mixin([AbstractPartOfProjectGenericMixin], base => {
  const superProto = base.prototype;

  class ChronoPartOfProjectGenericMixin extends base {
    getGraph() {
      const project = this.getProject();
      return project === null || project === void 0 ? void 0 : project.getGraph();
    }

    getEventById(id) {
      var _a;

      return (_a = this.getEventStore()) === null || _a === void 0 ? void 0 : _a.getById(id);
    }

    getDependencyById(id) {
      var _a;

      return (_a = this.getDependencyStore()) === null || _a === void 0 ? void 0 : _a.getById(id);
    }

    getResourceById(id) {
      var _a;

      return (_a = this.getResourceStore()) === null || _a === void 0 ? void 0 : _a.getById(id);
    }

    getAssignmentById(id) {
      var _a;

      return (_a = this.getAssignmentStore()) === null || _a === void 0 ? void 0 : _a.getById(id);
    }

    getCalendarById(id) {
      var _a;

      return (_a = this.getCalendarManagerStore()) === null || _a === void 0 ? void 0 : _a.getById(id);
    }

  }

  return ChronoPartOfProjectGenericMixin;
}) {}

class ChronoStoreMixin extends Mixin([Store], base => class ChronoStoreMixin extends base {}) {}

class AbstractPartOfProjectStoreMixin extends Mixin([AbstractPartOfProjectGenericMixin, Store], base => {
  const superProto = base.prototype;

  class AbstractPartOfProjectStoreMixin extends base {
    constructor() {
      super(...arguments);
      this.isLoadingData = false;
    }

    construct(config = {}) {
      config.asyncEvents = {
        add: true,
        remove: true,
        removeAll: true,
        change: true,
        refresh: true,
        replace: true,
        move: true,
        update: true
      };
      return superProto.construct.call(this, config);
    }

    trigger(eventName, param) {
      const me = this,
            {
        asyncEvents,
        project
      } = me,
            asyncEvent = asyncEvents === null || asyncEvents === void 0 ? void 0 : asyncEvents[eventName],
            asyncAction = asyncEvent && (asyncEvent === true || asyncEvent[param.action]);

      if (!asyncAction) {
        return superProto.trigger.call(me, eventName, param);
      }

      superProto.trigger.call(me, `${eventName}PreCommit`, Object.assign({}, param));

      if (!project || project.isEngineReady() && !project.isWritingData) {
        superProto.trigger.call(me, eventName, param);
      } else {
        project === null || project === void 0 ? void 0 : project.on({
          dataReady() {
            superProto.trigger.call(me, eventName, param);
          },

          once: true,
          thisObj: this
        });
      }

      return true;
    }

    calculateProject() {
      return this.project;
    }

    setStoreData(data) {
      var _a;

      if (this.project) {
        this.project.hasLoadedDataToCommit = true;
      }

      this.isLoadingData = true;
      superProto.setStoreData.call(this, data);
      this.isLoadingData = false;
      (_a = this.project) === null || _a === void 0 ? void 0 : _a.trigger('storeRefresh', {
        store: this
      });
    }

    async doAutoCommit() {
      if (this.suspendCount <= 0 && !this.project.isEngineReady()) {
        await this.project.commitAsync();
      }

      superProto.doAutoCommit.call(this);
    }

    async addAsync(records, silent) {
      const result = this.add(records, silent);
      await this.project.commitAsync();
      return result;
    }

    async insertAsync(index, records, silent) {
      const result = this.insert(index, records, silent);
      await this.project.commitAsync();
      return result;
    }

    async loadDataAsync(data) {
      this.data = data;
      await this.project.commitAsync();
    }

  }

  return AbstractPartOfProjectStoreMixin;
}) {}

class ChronoPartOfProjectStoreMixin extends Mixin([AbstractPartOfProjectStoreMixin, ChronoPartOfProjectGenericMixin, ChronoStoreMixin], base => {
  const superProto = base.prototype;

  class ChronoPartOfProjectStoreMixin extends base {
    setStoreData(data) {
      var _a;

      (_a = this.project) === null || _a === void 0 ? void 0 : _a.repopulateStore(this);
      superProto.setStoreData.call(this, data);
    }

  }

  return ChronoPartOfProjectStoreMixin;
}) {}

class AbstractPartOfProjectModelMixin extends Mixin([AbstractPartOfProjectGenericMixin, Model], base => {
  const superProto = base.prototype;

  class AbstractPartOfProjectModelMixin extends base {
    joinStore(store) {
      let joinedProject = false;

      if (isInstanceOf(store, AbstractPartOfProjectStoreMixin)) {
        const project = store.getProject();

        if (project && !project.isRepopulatingStores && !this.getProject()) {
          this.setProject(project);
          joinedProject = true;
        }
      }

      superProto.joinStore.call(this, store);
      if (joinedProject) this.joinProject();
    }

    unJoinStore(store, isReplacing = false) {
      superProto.unJoinStore.call(this, store, isReplacing);
      const project = this.getProject();

      if (project && !project.isDestroying && !project.isRepopulatingStores && isInstanceOf(store, AbstractPartOfProjectStoreMixin) && project === store.getProject()) {
        this.leaveProject(isReplacing);
        this.setProject(null);
      }
    }

    joinProject() {}

    leaveProject(isReplacing = false) {}

    calculateProject() {
      const store = this.stores.find(s => isInstanceOf(s, AbstractPartOfProjectStoreMixin) && !!s.getProject());
      return store === null || store === void 0 ? void 0 : store.getProject();
    }

    async setAsync(fieldName, value, silent) {
      var _a;

      const result = this.set(fieldName, value, silent);
      await ((_a = this.project) === null || _a === void 0 ? void 0 : _a.commitAsync());
      return result;
    }

    async getAsync(fieldName) {
      var _a;

      await ((_a = this.project) === null || _a === void 0 ? void 0 : _a.commitAsync());
      return this.get(fieldName);
    }

  }

  return AbstractPartOfProjectModelMixin;
}) {}

class ChronoPartOfProjectModelMixin extends Mixin([AbstractPartOfProjectModelMixin, ChronoPartOfProjectGenericMixin, ChronoModelMixin], base => {
  const superProto = base.prototype;

  class ChronoPartOfProjectModelMixin extends base {
    joinProject() {
      this.getGraph().addEntity(this);
    }

    leaveProject(isReplacing = false) {
      superProto.leaveProject.call(this, isReplacing);
      const replica = this.getGraph();
      replica.removeEntity(this);
    }

    getProject() {
      return superProto.getProject.call(this);
    }

    calculateProject() {
      const store = this.stores.find(s => isInstanceOf(s, ChronoPartOfProjectStoreMixin) && !!s.getProject());
      return store === null || store === void 0 ? void 0 : store.getProject();
    }

  }

  return ChronoPartOfProjectModelMixin;
}) {}

var __decorate$5 = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};
class BaseAssignmentMixin extends Mixin([ChronoPartOfProjectModelMixin], base => {
  const superProto = base.prototype;

  class BaseAssignmentMixin extends base {}

  __decorate$5([generic_field({
    bucket: 'assigned',
    resolver: function (id) {
      return this.getEventById(id);
    },
    modelFieldConfig: {
      serialize: event => event === null || event === void 0 ? void 0 : event.id,
      isEqual: isSerializableEqual,
      persist: false
    }
  }, ModelReferenceField)], BaseAssignmentMixin.prototype, "event", void 0);

  __decorate$5([generic_field({
    bucket: 'assigned',
    resolver: function (id) {
      return this.getResourceById(id);
    },
    modelFieldConfig: {
      serialize: resource => resource === null || resource === void 0 ? void 0 : resource.id,
      isEqual: isSerializableEqual,
      persist: false
    }
  }, ModelReferenceField)], BaseAssignmentMixin.prototype, "resource", void 0);

  injectStaticFieldsProperty(BaseAssignmentMixin);
  return BaseAssignmentMixin;
}) {}

class CorePartOfProjectGenericMixin extends Mixin([AbstractPartOfProjectGenericMixin], base => {
  const superProto = base.prototype;

  class CorePartOfProjectGenericMixin extends base {
    get eventStore() {
      var _a;

      return (_a = this.project) === null || _a === void 0 ? void 0 : _a.eventStore;
    }

    get resourceStore() {
      var _a;

      return (_a = this.project) === null || _a === void 0 ? void 0 : _a.resourceStore;
    }

    get assignmentStore() {
      var _a;

      return (_a = this.project) === null || _a === void 0 ? void 0 : _a.assignmentStore;
    }

    get dependencyStore() {
      var _a;

      return (_a = this.project) === null || _a === void 0 ? void 0 : _a.dependencyStore;
    }

    get calendarManagerStore() {
      var _a;

      return (_a = this.project) === null || _a === void 0 ? void 0 : _a.calendarManagerStore;
    }

    getEventById(id) {
      var _a;

      return (_a = this.eventStore) === null || _a === void 0 ? void 0 : _a.getById(id);
    }

    getDependencyById(id) {
      var _a;

      return (_a = this.dependencyStore) === null || _a === void 0 ? void 0 : _a.getById(id);
    }

    getResourceById(id) {
      var _a;

      return (_a = this.resourceStore) === null || _a === void 0 ? void 0 : _a.getById(id);
    }

    getAssignmentById(id) {
      var _a;

      return (_a = this.assignmentStore) === null || _a === void 0 ? void 0 : _a.getById(id);
    }

    getCalendarById(id) {
      var _a;

      return (_a = this.calendarManagerStore) === null || _a === void 0 ? void 0 : _a.getById(id);
    }

  }

  return CorePartOfProjectGenericMixin;
}) {}

class CorePartOfProjectModelMixin extends Mixin([AbstractPartOfProjectModelMixin, CorePartOfProjectGenericMixin, Model], base => {
  const superProto = base.prototype;

  class CorePartOfProjectModelMixin extends base {
    constructor() {
      super(...arguments);
      this.$isCalculating = false;
      this.$changed = {};
      this.$beforeChange = {};
    }

    joinProject() {
      this.invalidate();
    }

    leaveProject(isReplacing = false) {
      var _a;

      superProto.leaveProject.call(this, isReplacing);
      (_a = this.project) === null || _a === void 0 ? void 0 : _a.bufferedCommitAsync();
    }

    invalidate() {
      var _a;

      (_a = this.project) === null || _a === void 0 ? void 0 : _a.invalidate(this);
    }

    getCurrentOrProposed(fieldName) {
      var _a;

      if (fieldName in this.$changed) {
        return this.$changed[fieldName];
      }

      return (_a = this.get(fieldName)) !== null && _a !== void 0 ? _a : null;
    }

    hasCurrentOrProposed(fieldName) {
      return this.$changed[fieldName] != null || this.get(fieldName) != null;
    }

    propose(changes) {
      Object.keys(changes).forEach(field => {
        this.$changed[field] = changes[field];
      });
      this.invalidate();
    }

    setChanged(field, value, invalidate = true, setData = false) {
      const me = this;
      me.$changed[field] = value;

      if (setData) {
        if (!(field in me.$beforeChange)) {
          me.$beforeChange[field] = me.get(field);
        }

        me.setData(field, value);
      }

      invalidate && me.invalidate();
    }

    calculateInvalidated() {}

    finalizeInvalidated(silent = false) {
      const me = this;
      me.$isCalculating = true;

      if (me.$changed.__REFADR__) {
        me.$changed = Object.assign({}, me.$changed);
      }

      Object.keys(me.$beforeChange).forEach(fieldName => {
        me.setData(fieldName, me.$beforeChange[fieldName]);
      });
      me.set(me.$changed, null, silent);
      me.$changed = {};
      me.$beforeChange = {};
      me.$isCalculating = false;
    }

  }

  return CorePartOfProjectModelMixin;
}) {}

class CoreEventMixin extends Mixin([CorePartOfProjectModelMixin], base => {
  const superProto = base.prototype;

  class CoreEventMixin extends base {
    static get fields() {
      return [{
        name: 'startDate',
        type: 'date'
      }, {
        name: 'endDate',
        type: 'date'
      }, {
        name: 'duration',
        type: 'number'
      }, {
        name: 'durationUnit',
        type: 'string',
        defaultValue: 'day'
      }];
    }

    get startDate() {
      return this.getCurrentOrProposed('startDate');
    }

    get endDate() {
      return this.getCurrentOrProposed('endDate');
    }

    get duration() {
      return this.getCurrentOrProposed('duration');
    }

    set startDate(value) {
      this.proposeStartDate(value);
    }

    set endDate(value) {
      this.proposeEndDate(value);
    }

    set duration(value) {
      this.proposeDuration(value);
    }

    getStartDate() {
      return this.startDate;
    }

    proposeStartDate(startDate, keepDuration = true) {
      this.propose({
        startDate,
        keepDuration
      });
    }

    async setStartDate(startDate, keepDuration = true) {
      var _a;

      this.proposeStartDate(startDate, keepDuration);
      return (_a = this.project) === null || _a === void 0 ? void 0 : _a.commitAsync();
    }

    getEndDate() {
      return this.endDate;
    }

    proposeEndDate(endDate, keepDuration = false) {
      this.propose({
        endDate,
        keepDuration
      });
    }

    async setEndDate(endDate, keepDuration = false) {
      var _a;

      this.proposeEndDate(endDate, keepDuration);
      return (_a = this.project) === null || _a === void 0 ? void 0 : _a.commitAsync();
    }

    getDuration() {
      return this.duration;
    }

    proposeDuration(duration, unit, keepStart = true) {
      this.propose({
        duration,
        keepStart
      });
      if (unit) this.propose({
        durationUnit: unit
      });
    }

    async setDuration(duration, unit, keepStart = true) {
      var _a;

      this.proposeDuration(duration, unit, keepStart);
      return (_a = this.project) === null || _a === void 0 ? void 0 : _a.commitAsync();
    }

    getDurationUnit() {
      return this.durationUnit;
    }

    joinProject() {
      var _a;

      const changed = this.$changed;
      if (this.hasCurrentOrProposed('startDate')) changed.startDate = this.getCurrentOrProposed('startDate');
      if (this.hasCurrentOrProposed('endDate')) changed.endDate = this.getCurrentOrProposed('endDate');
      if (this.hasCurrentOrProposed('duration')) changed.duration = this.getCurrentOrProposed('duration');

      if (this.eventStore && !this.eventStore.isLoadingData) {
        const unresolved = (_a = this.assignmentStore) === null || _a === void 0 ? void 0 : _a.storage.findItem('event', null);

        if (unresolved) {
          for (const assignment of unresolved) {
            if (assignment.getCurrentOrProposed('event') === this.id) {
              assignment.setChanged('event', this);
            }
          }
        }
      }

      superProto.joinProject.call(this);
    }

    applyValue(useProp, key, value, skipAccessors, field) {
      if (key === 'startDate' || key == 'duration' || key === 'endDate') {
        useProp = true;
      }

      if (skipAccessors) {
        useProp = false;
      }

      superProto.applyValue.call(this, useProp, key, value, skipAccessors, field);
    }

    afterChange(toSet, wasSet, silent, fromRelationUpdate, skipAccessors) {
      if (!this.$isCalculating && !skipAccessors) {
        this.setData(this.$changed);
      }

      superProto.afterChange.call(this, toSet, wasSet, silent, fromRelationUpdate, skipAccessors);
    }

    calculateInvalidated() {
      const changed = this.$changed;
      const changedStart = ('startDate' in changed);
      const changedEnd = ('endDate' in changed);
      const changedDuration = ('duration' in changed);
      const {
        startDate,
        endDate,
        duration,
        keepDuration,
        keepStart
      } = changed;
      let calculate = null;

      if (changedStart && !changedEnd && !changedDuration) {
        if (startDate === null) {
          changed.endDate = null;
        } else if (this.hasCurrentOrProposed('endDate') && startDate > this.getCurrentOrProposed('endDate') && !keepDuration) {
          changed.endDate = startDate;
          changed.duration = 0;
        } else if (this.hasCurrentOrProposed('duration') && (keepDuration || !this.hasCurrentOrProposed('endDate'))) {
          calculate = 'endDate';
        } else if (this.hasCurrentOrProposed('endDate')) {
          calculate = 'duration';
        }
      } else if (!changedStart && changedEnd && !changedDuration) {
        if (endDate === null) {
          changed.startDate = null;
        } else if (this.hasCurrentOrProposed('startDate') && endDate < this.getCurrentOrProposed('startDate') && !keepDuration) {
          changed.startDate = endDate;
          changed.duration = 0;
        } else if (this.hasCurrentOrProposed('duration') && (keepDuration || !this.hasCurrentOrProposed('startDate'))) {
          calculate = 'startDate';
        } else if (this.hasCurrentOrProposed('startDate')) {
          calculate = 'duration';
        }
      } else if (!changedStart && !changedEnd && changedDuration) {
        if (duration === null) {
          changed.endDate = null;
        } else if (this.hasCurrentOrProposed('startDate') && (keepStart || !this.hasCurrentOrProposed('endDate'))) {
          if (keepStart && changed.duration < 0) {
            changed.duration = 0;
          }

          calculate = 'endDate';
        } else if (this.hasCurrentOrProposed('endDate')) {
          calculate = 'startDate';
        }
      } else if (changedStart && changedEnd && !changedDuration) {
        if (startDate === null && endDate === null) {
          changed.duration = null;
        } else {
          calculate = 'duration';
        }
      } else if (changedStart && !changedEnd && changedDuration) {
        calculate = 'endDate';
      } else if (!changedStart && changedEnd && changedDuration) {
        calculate = 'startDate';
      } else if (changedStart && changedEnd && changedDuration) {
        if (duration == null) {
          calculate = 'duration';
        } else if (startDate == null) {
          calculate = 'startDate';
        } else {
          calculate = 'endDate';
        }
      }

      switch (calculate) {
        case 'startDate':
          changed.startDate = DateHelper.add(this.getCurrentOrProposed('endDate'), -this.getCurrentOrProposed('duration'), this.getCurrentOrProposed('durationUnit'));
          break;

        case 'endDate':
          changed.endDate = DateHelper.add(this.getCurrentOrProposed('startDate'), this.getCurrentOrProposed('duration'), this.getCurrentOrProposed('durationUnit'));
          break;

        case 'duration':
          changed.duration = DateHelper.diff(this.getCurrentOrProposed('startDate'), this.getCurrentOrProposed('endDate'), this.getCurrentOrProposed('durationUnit'));
          break;
      }

      delete changed.keepDuration;
      delete changed.keepStart;
    }

  }

  return CoreEventMixin;
}) {}

class CoreResourceMixin extends Mixin([CorePartOfProjectModelMixin], base => {
  const superProto = base.prototype;

  class CoreResourceMixin extends base {
    get assigned() {
      var _a;

      return (_a = this.project) === null || _a === void 0 ? void 0 : _a.assignmentStore.getResourcesAssignments(this);
    }

    joinProject() {
      var _a;

      if (this.resourceStore && !this.resourceStore.isLoadingData) {
        (_a = this.assignmentStore) === null || _a === void 0 ? void 0 : _a.query(a => a.get('resource') === this.id).forEach(unresolved => unresolved.setChanged('resource', this));
      }

      superProto.joinProject.call(this);
    }

    leaveProject(isReplacing = false) {
      if (this.assigned && !isReplacing) {
        const resourceStore = this.resourceStore;
        this.assigned.forEach(assignment => resourceStore.assignmentsForRemoval.add(assignment));
      }

      superProto.leaveProject.call(this);
    }

    applyValue(useProp, key, value, skipAccessor, field) {
      if (field.name === 'id') {
        this.assigned.forEach(assignment => {
          assignment.set('resourceId', value);
        });
      }

      superProto.applyValue.call(this, useProp, key, value, skipAccessor, field);
    }

  }

  return CoreResourceMixin;
}) {}

class CoreAssignmentMixin extends Mixin([CorePartOfProjectModelMixin], base => {
  const superProto = base.prototype;

  class CoreAssignmentMixin extends base {
    static get fields() {
      return [{
        name: 'resource',
        isEqual: (a, b) => a === b,
        persist: false
      }, {
        name: 'event',
        isEqual: (a, b) => a === b,
        persist: false
      }];
    }

    setChanged(field, value, invalidate) {
      const {
        assignmentStore,
        eventStore,
        resourceStore,
        project
      } = this;
      let update = false;

      if (field === 'event') {
        const event = isInstanceOf(value, CoreEventMixin) ? value : eventStore === null || eventStore === void 0 ? void 0 : eventStore.getById(value);
        if (event) update = true;
        value = event || value;
      }

      if (field === 'resource') {
        const resource = isInstanceOf(value, CoreResourceMixin) ? value : resourceStore === null || resourceStore === void 0 ? void 0 : resourceStore.getById(value);
        if (resource) update = true;
        value = resource || value;
      }

      superProto.setChanged.call(this, field, value, invalidate, true);

      if (assignmentStore && update && !project.isPerformingCommit && !assignmentStore.isLoadingData && !assignmentStore.isBatchAssigning) {
        assignmentStore.updateIndices();
      }
    }

    joinProject() {
      superProto.joinProject.call(this);
      this.setChanged('event', this.get('event'));
      this.setChanged('resource', this.get('resource'));
    }

    calculateInvalidated() {
      var _a, _b;

      let {
        event = this.event,
        resource = this.resource
      } = this.$changed;

      if (event !== null && !isInstanceOf(event, CoreEventMixin)) {
        const resolved = (_a = this.eventStore) === null || _a === void 0 ? void 0 : _a.getById(event);
        if (resolved) this.setChanged('event', resolved, false);
      }

      if (resource !== null && !isInstanceOf(resource, CoreResourceMixin)) {
        const resolved = (_b = this.resourceStore) === null || _b === void 0 ? void 0 : _b.getById(resource);
        if (resolved) this.setChanged('resource', resolved, false);
      }
    }

    set event(event) {
      this.setChanged('event', event);
    }

    get event() {
      const event = this.get('event');
      return isInstanceOf(event, CoreEventMixin) ? event : null;
    }

    set resource(resource) {
      this.setChanged('resource', resource);
    }

    get resource() {
      const resource = this.get('resource');
      return isInstanceOf(resource, CoreResourceMixin) ? resource : null;
    }

  }

  return CoreAssignmentMixin;
}) {}

var _window$bryntum;
const EngineMixin =
/*  */
CoreAssignmentMixin;
/**
 * @module Scheduler/model/AssignmentModel
 */

/**
 * This model represent a single assignment of a resource to an event in scheduler, usually added to a
 * {@link Scheduler.data.AssignmentStore}.
 *
 * It is a subclass of {@link Core.data.Model} class. Please refer to the documentation for that class to become
 * familiar with the base interface of this class.
 *
 * ## Fields and references
 *
 * An Assignment has the following fields:
 * - `id` - The id of the assignment
 * - `resourceId` - The id of the resource assigned (optionally replaced with `resource` for load)
 * - `eventId` - The id of the event to which the resource is assigned (optionally replaced with `event` for load)
 *
 * The data source for these fields can be customized by subclassing this class:
 *
 * ```javascript
 * class MyAssignment extends AssignmentModel {
 *   static get fields() {
 *       return [
 *          { name : 'resourceId', dataSource : 'linkedResource' }
 *       ];
 *   }
 * }
 * ```
 *
 * After load and project normalization, these references are accessible (assuming their respective stores are loaded):
 * - `event` - The linked event record
 * - `resource` - The linked resource record
 *
 * ## Async resolving of references
 *
 * As described above, an assignment links an event to a resource. It holds references to an event record and a resource
 * record. These references are populated async, using the calculation engine of the project that the assignment via
 * its store is a part of. Because of this asyncness, references cannot be used immediately after modifications:
 *
 * ```javascript
 * assignment.resourceId = 2;
 * // assignment.resource is not yet available
 * ```
 *
 * To make sure references are updated, wait for calculations to finish:
 *
 * ```javascript
 * assignment.resourceId = 2;
 * await assignment.project.commitAsync();
 * // assignment.resource is available
 * ```
 *
 * As an alternative, you can also use `setAsync()` to trigger calculations directly after the change:
 *
 * ```javascript
 * await assignment.setAsync({ resourceId : 2});
 * // assignment.resource is available
 * ```
 *
 * @extends Core/data/Model
 * @mixes Scheduler/model/mixin/AssignmentModelMixin
 * @uninherit Core/data/mixin/TreeNode
 */

class AssignmentModel extends AssignmentModelMixin(PartOfProject(EngineMixin.derive(Model))) {}
AssignmentModel.exposeProperties();
AssignmentModel._$name = 'AssignmentModel';

// Private class that leverages the differences between a core and a basic project
var PartOfBaseProject = (Target => class PartOfBaseProject extends Target {
  static get $name() {
    return 'PartOfBaseProject';
  }

  get assignmentStore() {
    return this.project.assignmentStore;
  }

  get calendarManagerStore() {
    return this.project.calendarManagerStore;
  }

  get dependencyStore() {
    return this.project.dependencyStore;
  }

  get eventStore() {
    return this.project.eventStore;
  }

  get resourceStore() {
    return this.project.resourceStore;
  }

});

/**
 * @module Scheduler/data/mixin/AssignmentStoreMixin
 */

/**
 * This is a mixin, containing functionality related to managing assignments.
 *
 * It is consumed by the regular {@link Scheduler.data.AssignmentStore} class and Scheduler Pros counterpart.
 *
 * @mixin
 */

var AssignmentStoreMixin = (Target => class AssignmentStoreMixin extends Target {
  static get $name() {
    return 'AssignmentStoreMixin';
  }
  /**
   * Add assignments to the store.
   *
   * NOTE: References (event, resource) on the assignments are determined async by a calculation engine. Thus they
   * cannot be directly accessed after using this function.
   *
   * For example:
   *
   * ```javascript
   * const [assignment] = assignmentStore.add({ eventId, resourceId });
   * // assignment.event is not yet available
   * ```
   *
   * To guarantee references are set up, wait for calculations for finish:
   *
   * ```javascript
   * const [assignment] = assignmentStore.add({ eventId, resourceId });
   * await assignmentStore.project.commitAsync();
   * // assignment.event is available (assuming EventStore is loaded and so on)
   * ```
   *
   * Alternatively use `addAsync()` instead:
   *
   * ```javascript
   * const [assignment] = await assignmentStore.addAsync({ eventId, resourceId });
   * // assignment.event is available (assuming EventStore is loaded and so on)
   * ```
   *
   * @param {Scheduler.model.AssignmentModel|Scheduler.model.AssignmentModel[]|Object|Object[]} records
   * Array of records/data or a single record/data to add to store
   * @param {Boolean} [silent] Specify `true` to suppress events
   * @returns {Scheduler.model.AssignmentModel[]} Added records
   * @function add
   * @category CRUD
   */

  /**
   * Add assignments to the store and triggers calculations directly after. Await this function to have up to date
   * references on the added assignments.
   *
   * ```javascript
   * const [assignment] = await assignmentStore.addAsync({ eventId, resourceId });
   * // assignment.event is available (assuming EventStore is loaded and so on)
   * ```
   *
   * @param {Scheduler.model.AssignmentModel|Scheduler.model.AssignmentModel[]|Object|Object[]} records
   * Array of records/data or a single record/data to add to store
   * @param {Boolean} [silent] Specify `true` to suppress events
   * @returns {Scheduler.model.AssignmentModel[]} Added records
   * @function addAsync
   * @category CRUD
   * @async
   */

  /**
   * Applies a new dataset to the AssignmentStore. Use it to plug externally fetched data into the store.
   *
   * NOTE: References (assignments, resources) on the assignments are determined async by a calculation engine. Thus
   * they cannot be directly accessed after assigning the new dataset.
   *
   * For example:
   *
   * ```javascript
   * assignmentStore.data = [{ eventId, resourceId }];
   * // assignmentStore.first.event is not yet available
   * ```
   *
   * To guarantee references are available, wait for calculations for finish:
   *
   * ```javascript
   * assignmentStore.data = [{ eventId, resourceId  }];
   * await assignmentStore.project.commitAsync();
   * // assignmentStore.first.event is available
   * ```
   *
   * Alternatively use `loadDataAsync()` instead:
   *
   * ```javascript
   * await assignmentStore.loadDataAsync([{ eventId, resourceId }]);
   * // assignmentStore.first.event is available
   * ```
   *
   * @member {Object[]} data
   * @category Records
   */

  /**
   * Applies a new dataset to the AssignmentStore and triggers calculations directly after. Use it to plug externally
   * fetched data into the store.
   *
   * ```javascript
   * await assignmentStore.loadDataAsync([{ eventId, resourceId }]);
   * // assignmentStore.first.event is available
   * ```
   *
   * @param {Object[]} data Array of AssignmentModel data objects
   * @function loadDataAsync
   * @category CRUD
   * @async
   */

  static get defaultConfig() {
    return {
      /**
       * CrudManager must load stores in the correct order. Lowest first.
       * @private
       */
      loadPriority: 300,

      /**
       * CrudManager must sync stores in the correct order. Lowest first.
       * @private
       */
      syncPriority: 300,
      storeId: 'assignments'
    };
  }

  add(newAssignments) {
    if (!Array.isArray(newAssignments)) {
      newAssignments = [newAssignments];
    }

    for (let i = 0; i < newAssignments.length; i++) {
      let a = newAssignments[i];

      if (!(a instanceof Model)) {
        newAssignments[i] = a = this.createRecord(a);
      }

      if (this.storage.findIndex('eventResourceKey', a.eventResourceKey, true) !== -1) {
        throw new Error(`Duplicate assignment Event: ${a.eventId} to resource: ${a.resourceId}`);
      }
    }

    return super.add(newAssignments);
  }

  includesAssignment(eventId, resourceId) {
    return this.storage.findIndex('eventResourceKey', `${eventId}-${resourceId}`, true) !== -1;
  }

  setStoreData(data) {
    super.setStoreData(data);
  } //region Init & destroy

  set storage(storage) {
    super.storage = storage; // This allows a map based, fast lookup of assignments by their eventResourceKey.
    // This is so that the test for duplicate assignment adding is fast.

    this.storage.addIndex('eventResourceKey');
  }

  get storage() {
    return super.storage;
  } //endregion
  //region Stores
  // To not have to do instanceof checks

  get isAssignmentStore() {
    return true;
  } //endregion
  //region Recurrence

  /**
   * Returns a "fake" assignment used to identify a certain occurrence of a recurring event.
   * If passed the original event, it returns `originalAssignment`.
   * @param {Scheduler.model.AssignmentModel} originalAssignment
   * @param {Scheduler.model.EventModel} occurrence
   * @returns {Object} Temporary assignment
   * @internal
   */

  getOccurrence(originalAssignment, occurrence) {
    // Pass along the original assignment for non occurrence related calls
    if (!originalAssignment || !(occurrence !== null && occurrence !== void 0 && occurrence.isOccurrence)) {
      return originalAssignment;
    } // Not for saving chars, needed in fn below

    const me = this;
    return {
      id: `${occurrence.id}:a${originalAssignment.id}`,
      event: occurrence,
      resource: originalAssignment.resource,
      eventId: occurrence.id,
      resourceId: originalAssignment.resource.id,
      isAssignment: true,
      // This field is required to distinguish this fake assignment when event is being removed from UI
      isOccurrenceAssignment: true,

      // Not being an actual record, instanceMeta is stored on the store instead
      instanceMeta(instanceOrId) {
        return me.occurrenceInstanceMeta(this, instanceOrId);
      }

    };
  } // Per fake assignment instance meta, stored on store since fakes are always generated on demand

  occurrenceInstanceMeta(occurrenceAssignment, instanceOrId) {
    const me = this,
          instanceId = instanceOrId.id || instanceOrId,
          {
      id
    } = occurrenceAssignment;
    let {
      occurrenceMeta
    } = me;

    if (!occurrenceMeta) {
      occurrenceMeta = me.occurrenceMeta = {};
    }

    if (!occurrenceMeta[id]) {
      occurrenceMeta[id] = {};
    }

    return occurrenceMeta[id][instanceId] || (occurrenceMeta[id][instanceId] = {});
  } //endregion
  //region Mapping

  /**
   * Maps over event assignments.
   *
   * @param {Scheduler.model.EventModel} event
   * @param {Function} [fn]
   * @param {Function} [filterFn]
   * @return {Scheduler.model.EventModel[]|Array}
   * @category Assignments
   */

  mapAssignmentsForEvent(event, fn, filterFn) {
    event = this.eventStore.getById(event);
    const fnSet = Boolean(fn),
          filterFnSet = Boolean(filterFn);

    if (fnSet || filterFnSet) {
      return event.assignments.reduce((result, assignment) => {
        const mapResult = fnSet ? fn(assignment) : assignment;

        if (!filterFnSet || filterFn(mapResult)) {
          result.push(mapResult);
        }

        return result;
      }, []);
    }

    return event.assignments;
  }
  /**
   * Maps over resource assignments.
   *
   * @param {Scheduler.model.ResourceModel|Number|String} resource
   * @param {Function} [fn]
   * @param {Function} [filterFn]
   * @return {Scheduler.model.ResourceModel[]|Array}
   * @category Assignments
   */

  mapAssignmentsForResource(resource, fn, filterFn) {
    resource = this.resourceStore.getById(resource);
    const fnSet = Boolean(fn),
          filterFnSet = Boolean(filterFn);

    if (fnSet || filterFnSet) {
      return resource.assignments.reduce((result, assignment) => {
        const mapResult = fnSet ? fn(assignment) : assignment;

        if (!filterFnSet || filterFn(mapResult)) {
          result.push(mapResult);
        }

        return result;
      }, []);
    }

    return resource.assignments;
  }
  /**
   * Returns all assignments for a given event.
   *
   * @param {Scheduler.model.TimeSpan} event
   * @return {Scheduler.model.AssignmentModel[]}
   * @category Assignments
   */

  getAssignmentsForEvent(event) {
    return event.assignments;
  }
  /**
   * Removes all assignments for given event
   *
   * @param {Scheduler.model.TimeSpan} event
   * @category Assignments
   */

  removeAssignmentsForEvent(event) {
    return this.remove(event.assignments);
  }
  /**
   * Returns all assignments for a given resource.
   *
   * @param {Scheduler.model.ResourceModel} resource
   * @return {Scheduler.model.AssignmentModel[]}
   * @category Assignments
   */

  getAssignmentsForResource(resource) {
    resource = this.resourceStore.getById(resource);
    return resource.assignments;
  }
  /**
   * Removes all assignments for given resource
   *
   * @param {Scheduler.model.ResourceModel|*} resource
   * @category Assignments
   */

  removeAssignmentsForResource(resource) {
    this.remove(this.getAssignmentsForResource(resource));
  }
  /**
   * Returns all resources assigned to an event.
   *
   * @param {Scheduler.model.EventModel} event
   * @return {Scheduler.model.ResourceModel[]}
   * @category Assignments
   */

  getResourcesForEvent(event) {
    return event.resources;
  }
  /**
   * Returns all events assigned to a resource
   *
   * @param {Scheduler.model.ResourceModel|String|Number} resource
   * @return {Scheduler.model.TimeSpan[]}
   * @category Assignments
   */

  getEventsForResource(resource) {
    resource = this.resourceStore.getById(resource);
    return resource && resource.events;
  }
  /**
   * Creates and adds assignment record(s) for a given event and resource(s).
   *
   * @param {Scheduler.model.TimeSpan} event
   * @param {Scheduler.model.ResourceModel|Scheduler.model.ResourceModel[]} resources The resource(s) to assign to the event
   * @param {Function} [assignmentSetupFn] A hook function which takes an assignment as its argument and must return an assignment.
   * @param {Boolean} [removeExistingAssignments] `true` to remove assignments for other resources
   * @return {Scheduler.model.AssignmentModel[]} An array with the created assignment(s)
   * @category Assign
   */

  assignEventToResource(event, resources, assignmentSetupFn = o => o, removeExistingAssignments = false) {
    const me = this,
          toRemove = removeExistingAssignments ? new Set(event.assignments) : null;
    resources = Array.isArray(resources) ? resources : [resources]; // Use same code path as other single assignments if already assigned

    if (me.eventStore && me.eventStore.usesSingleAssignment && event.assignments.length) {
      if (!me.isEventAssignedToResource(event, resources[0])) {
        event.resource = resources[0];
      }

      return [];
    }

    let newAssignments = [];
    me.suspendAutoCommit(); // Assign

    resources.forEach(resource => {
      const assignment = me.getAssignmentForEventAndResource(event, resource);

      if (!assignment) {
        let assignment = {
          eventId: Model.asId(event),
          resourceId: Model.asId(resource)
        };
        assignment = assignmentSetupFn(assignment);
        newAssignments.push(assignment);
      } else if (removeExistingAssignments) {
        toRemove.delete(assignment);
      }
    });
    newAssignments = me.add(newAssignments);

    if (removeExistingAssignments) {
      me.remove(Array.from(toRemove));
    } // If true, will trigger a commit

    me.resumeAutoCommit();
    return newAssignments;
  }
  /**
   * Removes assignment record for a given event and resource.
   *
   * @param {Scheduler.model.TimeSpan|String|Number} event
   * @param {Scheduler.model.ResourceModel|String|Number} [resources] The resource to unassign the event from. If omitted, all resources of the events will be unassigned
   * @return {Scheduler.model.AssignmentModel|Scheduler.model.AssignmentModel[]}
   * @category Assign
   */

  unassignEventFromResource(event, resources) {
    const me = this,
          assignmentsToRemove = [];

    if (!resources) {
      return me.removeAssignmentsForEvent(event);
    }

    if (!Array.isArray(resources)) {
      resources = [resources];
    }

    for (let i = 0; i < resources.length; i++) {
      if (me.isEventAssignedToResource(event, resources[i])) {
        assignmentsToRemove.push(me.getAssignmentForEventAndResource(event, resources[i]));
      }
    }

    return me.remove(assignmentsToRemove);
  }
  /**
   * Checks whether an event is assigned to a resource.
   *
   * @param {Scheduler.model.EventModel|String|Number} event Event record or id
   * @param {Scheduler.model.ResourceModel|String|Number} resource Resource record or id
   * @return {Boolean}
   * @category Assignments
   */

  isEventAssignedToResource(event, resource) {
    return Boolean(this.getAssignmentForEventAndResource(event, resource));
  }
  /**
   * Returns an assignment record for a given event and resource
   *
   * @param {Scheduler.model.EventModel|String|Number} event The event or its id
   * @param {Scheduler.model.ResourceModel|String|Number} resource The resource or its id
   * @return {Scheduler.model.AssignmentModel}
   * @category Assignments
   */

  getAssignmentForEventAndResource(event, resource) {
    event = this.eventStore.getById(event);
    resource = this.resourceStore.getById(resource);

    if (!event || !resource) {
      return null;
    }

    return this.getOccurrence(event.assignments.find(a => a.resource === resource), event);
  } //endregion

});

const isNotNumber = value => Number(value) !== value;
const CIFromSetOrArrayOrValue = value => {
  if (value instanceof Set || value instanceof Array) return CI(value);
  return CI([value]);
};
const delay$1 = value => new Promise(resolve => setTimeout(resolve, value));

class AbstractAssignmentStoreMixin extends Mixin([AbstractPartOfProjectStoreMixin], base => {
  const superProto = base.prototype;

  class AbstractAssignmentStoreMixin extends base {
    constructor() {
      super(...arguments);
      this.assignmentsForRemoval = new Set();
      this.allAssignmentsForRemoval = false;
    }

    remove(records, silent) {
      this.assignmentsForRemoval = CIFromSetOrArrayOrValue(records).toSet();
      const res = superProto.remove.call(this, records);
      this.assignmentsForRemoval.clear();
      return res;
    }

    removeAll(silent) {
      this.allAssignmentsForRemoval = true;
      const res = superProto.removeAll.call(this, silent);
      this.allAssignmentsForRemoval = false;
      return res;
    }

  }

  return AbstractAssignmentStoreMixin;
}) {}

class ChronoAssignmentStoreMixin extends Mixin([AbstractAssignmentStoreMixin, ChronoPartOfProjectStoreMixin], base => {
  const superProto = base.prototype;

  class ChronoAssignmentStoreMixin extends base {
    static get defaultConfig() {
      return {
        modelClass: BaseAssignmentMixin
      };
    }

    set data(value) {
      this.allAssignmentsForRemoval = true;
      super.data = value;
      this.allAssignmentsForRemoval = false;
    }

  }

  return ChronoAssignmentStoreMixin;
}) {}

class CorePartOfProjectStoreMixin extends Mixin([AbstractPartOfProjectStoreMixin, CorePartOfProjectGenericMixin, Store], base => {
  const superProto = base.prototype;

  class CorePartOfProjectStoreMixin extends base {
    setProject(project) {
      const result = superProto.setProject.call(this, project);
      if (project) this.joinProject(project);
      return result;
    }

    joinProject(project) {}

    onCommitAsync() {}

  }

  return CorePartOfProjectStoreMixin;
}) {}

const emptySet = new Set();
class CoreAssignmentStoreMixin extends Mixin([AbstractAssignmentStoreMixin, CorePartOfProjectStoreMixin], base => {
  const superProto = base.prototype;

  class CoreAssignmentStoreMixin extends base {
    static get defaultConfig() {
      return {
        modelClass: CoreAssignmentMixin,
        storage: {
          extraKeys: [{
            property: 'event',
            unique: false
          }, {
            property: 'resource',
            unique: false
          }]
        }
      };
    }

    set data(value) {
      this.allAssignmentsForRemoval = true;
      super.data = value;
      this.allAssignmentsForRemoval = false;
    }

    getEventsAssignments(event) {
      return this.storage.findItem('event', event) || emptySet;
    }

    getResourcesAssignments(resource) {
      return this.storage.findItem('resource', resource) || emptySet;
    }

    updateIndices() {
      this.storage.rebuildIndices();
    }

    linkAssignments(store, modelName) {
      const unresolved = this.storage.findItem(modelName, null);

      if (unresolved) {
        this.isBatchAssigning = true;

        for (const assignment of unresolved) {
          const record = store.getById(assignment.getCurrentOrProposed(modelName));
          if (record) assignment.setChanged(modelName, record);
        }

        this.isBatchAssigning = false;
        this.updateIndices();
      }
    }

    onCommitAsync() {
      this.updateIndices();
    }

  }

  return CoreAssignmentStoreMixin;
}) {}

var _window$bryntum$1;
const EngineMixin$1 =
/*  */
PartOfProject(CoreAssignmentStoreMixin.derive(AjaxStore));
/**
 * @module Scheduler/data/AssignmentStore
 */

/**
 * A store representing a collection of assignments between events in the {@link Scheduler.data.EventStore} and resources
 * in the {@link Scheduler.data.ResourceStore}.
 *
 * This store only accepts a model class inheriting from {@link Scheduler.model.AssignmentModel}.
 *
 * An AssignmentStore is usually connected to a project, which binds it to other related stores (EventStore,
 * ResourceStore and DependencyStore). The project also handles references (event, resource) to related records for the
 * records in the store.
 *
 * Resolving the references happens async, records are not guaranteed to have up to date references until calculations
 * are finished. To be certain that references are resolved, call `await project.commitAsync()` after store actions. Or
 * use one of the `xxAsync` functions, such as `loadDataAsync()`.
 *
 * Using `commitAsync()`:
 *
 * ```javascript
 * assignmentStore.data = [{ eventId, resourceId }, ...];
 *
 * // references (event, resource) not resolved yet
 *
 * await assignmentStore.project.commitAsync();
 *
 * // now they are
 * ```
 *
 * Using `loadDataAsync()`:
 *
 * ```javascript
 * await assignmentStore.loadDataAsync([{ eventId, resourceId }, ...]);
 *
 * // references (event, resource) are resolved
 * ```
 *
 * @mixes Scheduler/data/mixin/AssignmentStoreMixin
 * @mixes Scheduler/data/mixin/PartOfProject
 * @extends Core/data/AjaxStore
 */

class AssignmentStore extends AssignmentStoreMixin(EngineMixin$1) {
  static get defaultConfig() {
    return {
      modelClass: AssignmentModel
    };
  }

}
AssignmentStore._$name = 'AssignmentStore';

/**
 * @module Scheduler/data/mixin/ResourceStoreMixin
 */

/**
 * This is a mixin for the ResourceStore functionality. It is consumed by the {@link Scheduler.data.ResourceStore}.
 *
 * @mixin
 */

var ResourceStoreMixin = (Target => class ResourceStoreMixin extends (Target || Base) {
  static get $name() {
    return 'ResourceStoreMixin';
  }

  get isResourceStore() {
    return true;
  }
  /**
   * Add resources to the store.
   *
   * NOTE: References (events, assignments) on the resources are determined async by a calculation engine. Thus they
   * cannot be directly accessed after using this function.
   *
   * For example:
   *
   * ```javascript
   * const [resource] = resourceStore.add({ id });
   * // resource.events is not yet available
   * ```
   *
   * To guarantee references are set up, wait for calculations for finish:
   *
   * ```javascript
   * const [resource] = resourceStore.add({ id });
   * await resourceStore.project.commitAsync();
   * // resource.events is available (assuming EventStore is loaded and so on)
   * ```
   *
   * Alternatively use `addAsync()` instead:
   *
   * ```javascript
   * const [resource] = await resourceStore.addAsync({ id });
   * // resource.events is available (assuming EventStore is loaded and so on)
   * ```
   *
   * @param {Scheduler.model.ResourceModel|Scheduler.model.ResourceModel[]|Object|Object[]} records
   * Array of records/data or a single record/data to add to store
   * @param {Boolean} [silent] Specify `true` to suppress events
   * @returns {Scheduler.model.ResourceModel[]} Added records
   * @function add
   * @category CRUD
   */

  /**
   * Add resources to the store and triggers calculations directly after. Await this function to have up to date
   * references on the added resources.
   *
   * ```javascript
   * const [resource] = await resourceStore.addAsync({ id });
   * // resource.events is available (assuming EventStore is loaded and so on)
   * ```
   *
   * @param {Scheduler.model.ResourceModel|Scheduler.model.ResourceModel[]|Object|Object[]} records
   * Array of records/data or a single record/data to add to store
   * @param {Boolean} [silent] Specify `true` to suppress events
   * @returns {Scheduler.model.ResourceModel[]} Added records
   * @function addAsync
   * @category CRUD
   * @async
   */

  /**
   * Applies a new dataset to the ResourceStore. Use it to plug externally fetched data into the store.
   *
   * NOTE: References (events, assignments) on the resources are determined async by a calculation engine. Thus
   * they cannot be directly accessed after assigning the new dataset.
   *
   * For example:
   *
   * ```javascript
   * resourceStore.data = [{ id }];
   * // resourceStore.first.events is not yet available
   * ```
   *
   * To guarantee references are available, wait for calculations for finish:
   *
   * ```javascript
   * resourceStore.data = [{ id }];
   * await resourceStore.project.commitAsync();
   * // resourceStore.first.events is available
   * ```
   *
   * Alternatively use `loadDataAsync()` instead:
   *
   * ```javascript
   * await resourceStore.loadDataAsync([{ id }]);
   * // resourceStore.first.events is available
   * ```
   *
   * @member {Object[]} data
   * @category Records
   */

  /**
   * Applies a new dataset to the ResourceStore and triggers calculations directly after. Use it to plug externally
   * fetched data into the store.
   *
   * ```javascript
   * await resourceStore.loadDataAsync([{ id }]);
   * // resourceStore.first.events is available
   * ```
   *
   * @param {Object[]} data Array of ResourceModel data objects
   * @function loadDataAsync
   * @category CRUD
   * @async
   */

  static get defaultConfig() {
    return {
      /**
       * CrudManager must load stores in the correct order. Lowest first.
       * @private
       */
      loadPriority: 200,

      /**
       * CrudManager must sync stores in the correct order. Lowest first.
       * @private
       */
      syncPriority: 100,
      storeId: 'resources',
      autoTree: true
    };
  }

  construct(config) {
    super.construct(config);

    if (!this.modelClass.isResourceModel) {
      throw new Error('Model for ResourceStore must subclass ResourceModel');
    }
  }

  removeAll() {
    const result = super.removeAll(...arguments); // Removing all resources removes all assignments

    result && this.assignmentStore.removeAll();
    return result;
  } // Apply id changes also to assignments (used to be handled automatically by relations earlier, but engine does not
  // care about ids so needed now)
  // problems:
  // 1. orientation/HorizontalRendering listens to assignment store changes and is trying to refresh view
  // When we update resource id on assignment, listener will be invoken and view will try to refresh. And it will
  // fail, because row is not updated yet. Flag is raised on resource store to make HorizontalRendering to skip
  // refreshing view in this particular case of resource id changing

  onRecordIdChange({
    record,
    oldValue,
    value
  }) {
    super.onRecordIdChange({
      record,
      oldValue,
      value
    });

    if (record.isFieldModified('id')) {
      this.isChangingId = true;
      record.updateAssignmentResourceIds();
      this.isChangingId = false;
    }
  }

});

/**
 * @module Scheduler/model/mixin/ResourceModelMixin
 */

/**
 * Mixin that holds configuration shared between resources in Scheduler and Scheduler Pro.
 * @mixin
 */
var ResourceModelMixin = (Target => class ResourceModelMixin extends Target {
  static get $name() {
    return 'ResourceModelMixin';
  } // Flag checked by ResourceStore to make sure it uses a valid subclass

  static get isResourceModel() {
    return true;
  }
  /**
   * Set value for the specified field(s), triggering engine calculations immediately. See
   * {@link Core.data.Model#function-set Model#set()} for arguments.
   *
   * This does not matter much on the resource itself, but is of importance when manipulating its references:
   *
   * ```javascript
   * assignment.set('resourceId', 2);
   * // resource.assignments is not yet up to date
   *
   * await assignment.setAsync('resourceId', 2);
   * // resource.assignments is up to date
   * ```
   *
   * @param {String|Object} field The field to set value for, or an object with multiple values to set in one call
   * @param {*} value Value to set
   * @param {Boolean} [silent=false] Set to true to not trigger events
   * automatically.
   * @function setAsync
   * @category Editing
   * @async
   */
  //region Fields

  static get fields() {
    return [
    /**
     * Unique identifier
     * @field {String|Number} id
     * @category Common
     */

    /**
     * Get or set resource name
     * @field {String} name
     * @category Common
     */
    {
      name: 'name',
      type: 'string',
      persist: true
    },
    /**
     * Controls the primary color used for events assigned to this resource. Can be overridden per event using
     * EventModels {@link Scheduler/model/mixin/EventModelMixin#field-eventColor eventColor config}. See Schedulers
     * {@link Scheduler.view.mixin.TimelineEventRendering#config-eventColor eventColor config} for available
     * colors.
     * @field {String} eventColor
     * @category Styling
     */
    'eventColor',
    /**
     * Controls the style used for events assigned to this resource. Can be overridden per event using
     * EventModels {@link Scheduler/model/mixin/EventModelMixin#field-eventStyle eventStyle config}. See Schedulers
     * {@link Scheduler.view.mixin.TimelineEventRendering#config-eventStyle eventStyle config} for available
     * options.
     * @field {String} eventStyle
     * @category Styling
     */
    'eventStyle',
    /**
     * Fully qualified image URL, used by `ResourceInfoColumn` and vertical modes `ResourceHeader` to display a miniature image
     * for the resource.
     * @field {String} imageUrl
     * @category Styling
     */
    'imageUrl',
    /**
     * Image name relative to {@link Scheduler/view/mixin/SchedulerEventRendering#config-resourceImagePath},
     * used by `ResourceInfoColumn` and vertical modes `ResourceHeader` to display a miniature image
     * for the resource.
     * @field {String} image
     * @category Styling
     */
    'image',
    /**
     * Margin from rows edge to topmost event bar for this resource, in px.
     * @field {Number} resourceMargin
     * @category Layout
     */
    {
      name: 'resourceMargin',
      type: 'number'
    },
    /**
     * Margin between stacked event bars for this resource, in px.
     * @field {Number} barMargin
     * @category Layout
     */
    {
      name: 'barMargin',
      type: 'number'
    },
    /**
     * Base height of this resource, in px. When unset, Schedulers configured rowHeight is used.
     *
     * This value is used in horizontal mode to determine row height. When stacking, it is used as input for
     * calculating the actual row height:
     *
     * ```javascript
     * row.height = (resource.rowHeight - resourceMargin * 2) * overlap count - barMargin * (overlap count - 1)
     * ```
     *
     * When packing or overlapping, it is used as the actual row height.
     *
     * @field {Number} rowHeight
     * @category Layout
     */

    /**
     * Specify this to use a resource specific event layout in horizontal mode, see
     * {@link Scheduler.view.mixin.SchedulerEventRendering#config-eventLayout} for options.
     *
     * When unset (the default) Schedulers setting is used.
     *
     * @field {String} eventLayout
     * @category Layout
     */
    'eventLayout'];
  } //endregion
  //region Id change

  updateAssignmentResourceIds() {
    this.assigned.forEach(assignment => {
      assignment.resourceId = this.id;
    });
  }

  syncId(value) {
    super.syncId(value);
    this.updateAssignmentResourceIds();
  } //endregion
  //region Getters

  /**
   * Get associated events
   *
   * @property {Scheduler.model.EventModel[]}
   * @readonly
   */

  get events() {
    return this.assignments.reduce((events, assignment) => {
      if (assignment.event) {
        events.push(assignment.event);
      }

      return events;
    }, []);
  }
  /**
   * Returns all assignments for the resource
   *
   * @property {Scheduler.model.AssignmentModel[]}
   */

  get assignments() {
    return this.assigned ? [...this.assigned] : [];
  }

  set assignments(assignments) {
    // Engine does not allow assigning to `assigned`, handle it here
    assignments.forEach(assignment => {
      assignment.resource = this;
    });
  }
  /**
   * Returns an array of events, associated with this resource
   *
   * @return {Scheduler.model.EventModel[]}
   */

  getEvents() {
    // TODO: Deprecate in favor of .events
    return this.events;
  }
  /**
   * Returns true if the Resource can be persisted.
   * In a flat store resource is always considered to be persistable, in a tree store resource is considered to
   * be persistable if it's parent node is persistable.
   *
   * @property {Boolean}
   * @readonly
   */

  get isPersistable() {
    return !this.parent || this.parent.isPersistable;
  } //endregion

  /**
   * Unassigns this Resource from all its Events
   */

  unassignAll() {
    this.assignments && this.assignmentStore.remove(this.assignments);
  }

});

const stripDuplicates = array => Array.from(new Set(array));

var TimeUnit;

(function (TimeUnit) {
  TimeUnit["Millisecond"] = "millisecond";
  TimeUnit["Second"] = "second";
  TimeUnit["Minute"] = "minute";
  TimeUnit["Hour"] = "hour";
  TimeUnit["Day"] = "day";
  TimeUnit["Week"] = "week";
  TimeUnit["Month"] = "month";
  TimeUnit["Quarter"] = "quarter";
  TimeUnit["Year"] = "year";
})(TimeUnit || (TimeUnit = {}));

var ConstraintType;

(function (ConstraintType) {
  ConstraintType["MustStartOn"] = "muststarton";
  ConstraintType["MustFinishOn"] = "mustfinishon";
  ConstraintType["StartNoEarlierThan"] = "startnoearlierthan";
  ConstraintType["StartNoLaterThan"] = "startnolaterthan";
  ConstraintType["FinishNoEarlierThan"] = "finishnoearlierthan";
  ConstraintType["FinishNoLaterThan"] = "finishnolaterthan";
})(ConstraintType || (ConstraintType = {}));

var SchedulingMode;

(function (SchedulingMode) {
  SchedulingMode["Normal"] = "Normal";
  SchedulingMode["FixedDuration"] = "FixedDuration";
  SchedulingMode["FixedEffort"] = "FixedEffort";
  SchedulingMode["FixedUnits"] = "FixedUnits";
})(SchedulingMode || (SchedulingMode = {}));

var DependencyValidationResult;

(function (DependencyValidationResult) {
  DependencyValidationResult[DependencyValidationResult["NoError"] = 0] = "NoError";
  DependencyValidationResult[DependencyValidationResult["CyclicDependency"] = 1] = "CyclicDependency";
  DependencyValidationResult[DependencyValidationResult["DuplicatingDependency"] = 2] = "DuplicatingDependency";
})(DependencyValidationResult || (DependencyValidationResult = {}));

var DependencyType;

(function (DependencyType) {
  DependencyType[DependencyType["StartToStart"] = 0] = "StartToStart";
  DependencyType[DependencyType["StartToEnd"] = 1] = "StartToEnd";
  DependencyType[DependencyType["EndToStart"] = 2] = "EndToStart";
  DependencyType[DependencyType["EndToEnd"] = 3] = "EndToEnd";
})(DependencyType || (DependencyType = {}));

var DependenciesCalendar;

(function (DependenciesCalendar) {
  DependenciesCalendar["Project"] = "Project";
  DependenciesCalendar["FromEvent"] = "FromEvent";
  DependenciesCalendar["ToEvent"] = "ToEvent";
})(DependenciesCalendar || (DependenciesCalendar = {}));

var ProjectType;

(function (ProjectType) {
  ProjectType[ProjectType["SchedulerBasic"] = 1] = "SchedulerBasic";
  ProjectType[ProjectType["SchedulerPro"] = 2] = "SchedulerPro";
  ProjectType[ProjectType["Gantt"] = 3] = "Gantt";
})(ProjectType || (ProjectType = {}));

var Direction;

(function (Direction) {
  Direction["Forward"] = "Forward";
  Direction["Backward"] = "Backward";
  Direction["None"] = "None";
})(Direction || (Direction = {}));

const MIN_DATE = new Date(-8640000000000000);
const MAX_DATE = new Date(8640000000000000);

var EdgeInclusion;

(function (EdgeInclusion) {
  EdgeInclusion[EdgeInclusion["Left"] = 0] = "Left";
  EdgeInclusion[EdgeInclusion["Right"] = 1] = "Right";
})(EdgeInclusion || (EdgeInclusion = {}));

var CalendarIteratorResult;

(function (CalendarIteratorResult) {
  CalendarIteratorResult[CalendarIteratorResult["FullRangeIterated"] = 0] = "FullRangeIterated";
  CalendarIteratorResult[CalendarIteratorResult["StoppedByIterator"] = 1] = "StoppedByIterator";
  CalendarIteratorResult[CalendarIteratorResult["MaxCacheExtendCyclesReached"] = 2] = "MaxCacheExtendCyclesReached";
  CalendarIteratorResult[CalendarIteratorResult["MaxRangeReached"] = 3] = "MaxRangeReached";
})(CalendarIteratorResult || (CalendarIteratorResult = {}));

class CalendarCache {
  constructor(config) {
    this.cacheFilledStartDate = MAX_DATE;
    this.cacheFilledEndDate = MIN_DATE;
    this.intervalsCachingChunkDuration = 30;
    this.intervalsCachingChunkUnit = TimeUnit.Day;
    this.maxCacheExtendCycles = 100;
    this.maxRange = 5 * 12 * 30 * 24 * 60 * 60 * 1000;
    config && Object.assign(this, config);
  }

  includeWrappingRangeFrom(cache, startDate, endDate) {
    cache.ensureCacheFilledForInterval(startDate, endDate);
    this.intervalCache.includeWrappingRange(cache.intervalCache, startDate, endDate);
  }

  ensureCacheFilledForInterval(startDate, endDate) {
    const cacheFilledStartDateN = this.cacheFilledStartDate.getTime();
    const cacheFilledEndDateN = this.cacheFilledEndDate.getTime();

    if (cacheFilledStartDateN !== MAX_DATE.getTime()) {
      const startDateN = startDate.getTime();
      const endDateN = endDate.getTime();
      if (cacheFilledStartDateN <= startDateN && endDateN <= cacheFilledEndDateN) return;

      if (endDateN <= cacheFilledStartDateN) {
        endDate = new Date(cacheFilledStartDateN - 1);
      } else if (startDateN >= cacheFilledEndDateN) {
        startDate = new Date(cacheFilledEndDateN + 1);
      } else if (cacheFilledStartDateN <= startDateN && startDateN <= cacheFilledEndDateN) {
        startDate = new Date(cacheFilledEndDateN + 1);
      } else if (cacheFilledStartDateN <= endDateN && endDateN <= cacheFilledEndDateN) {
        endDate = new Date(cacheFilledStartDateN - 1);
      } else {
        this.ensureCacheFilledForInterval(startDate, new Date(cacheFilledStartDateN - 1));
        this.ensureCacheFilledForInterval(new Date(cacheFilledEndDateN + 1), endDate);
        return;
      }
    }

    if (cacheFilledStartDateN === MAX_DATE.getTime() || startDate.getTime() < cacheFilledEndDateN) {
      this.cacheFilledStartDate = startDate;
    }

    if (cacheFilledEndDateN === MIN_DATE.getTime() || cacheFilledEndDateN < endDate.getTime()) {
      this.cacheFilledEndDate = endDate;
    }

    this.fillCache(startDate, endDate);
  }

  fillCache(_1, _2) {
    throw new Error("Abstract method");
  }

  clear() {
    this.cacheFilledStartDate = MAX_DATE;
    this.cacheFilledEndDate = MIN_DATE;
    this.intervalCache.clear();
  }

  forEachAvailabilityInterval(options, func, scope) {
    var _a;

    scope = scope || this;
    const startDate = options.startDate;
    const endDate = options.endDate;
    const startDateN = startDate && startDate.getTime();
    const endDateN = endDate && endDate.getTime();
    const maxRange = (_a = options.maxRange) !== null && _a !== void 0 ? _a : this.maxRange;
    const isForward = options.isForward !== false;

    if (isForward ? !startDate : !endDate) {
      throw new Error("At least `startDate` or `endDate` is required, depending from the `isForward` option");
    }

    const intervalCache = this.intervalCache;
    let cacheCursorDate = isForward ? startDate : endDate;
    let cursorDate = isForward ? startDate : endDate;
    const rangeStart = cursorDate.getTime();

    for (let cycle = 1; cycle < this.maxCacheExtendCycles; cycle++) {
      if (isForward) {
        this.ensureCacheFilledForInterval(cacheCursorDate, endDate || DateHelper.add(cacheCursorDate, this.intervalsCachingChunkDuration, this.intervalsCachingChunkUnit));
      } else {
        this.ensureCacheFilledForInterval(startDate || DateHelper.add(cacheCursorDate, -this.intervalsCachingChunkDuration, this.intervalsCachingChunkUnit), cacheCursorDate);
      }

      let interval = intervalCache.getIntervalOf(cursorDate, isForward ? EdgeInclusion.Left : EdgeInclusion.Right);

      while (interval) {
        const intervalStartDate = interval.startDate;
        const intervalEndDate = interval.endDate;

        if (isForward && endDateN && intervalStartDate.getTime() >= endDateN || !isForward && startDateN && intervalEndDate.getTime() <= startDateN) {
          return CalendarIteratorResult.FullRangeIterated;
        }

        if (isForward && intervalStartDate.getTime() - rangeStart >= maxRange || !isForward && rangeStart - intervalEndDate.getTime() >= maxRange) {
          return CalendarIteratorResult.MaxRangeReached;
        }

        if (isForward && intervalStartDate.getTime() > this.cacheFilledEndDate.getTime() || !isForward && intervalEndDate.getTime() < this.cacheFilledStartDate.getTime()) {
          break;
        }

        cursorDate = isForward ? intervalEndDate : intervalStartDate;
        const countFrom = startDateN && intervalStartDate.getTime() < startDateN ? startDate : intervalStartDate;
        const countTill = endDateN && intervalEndDate.getTime() > endDateN ? endDate : intervalEndDate;

        if (func.call(scope, countFrom, countTill, interval.cacheInterval) === false) {
          return CalendarIteratorResult.StoppedByIterator;
        }

        interval = isForward ? intervalCache.getNextInterval(interval) : intervalCache.getPrevInterval(interval);
      }

      if (isForward && cursorDate.getTime() === MAX_DATE.getTime() || !isForward && cursorDate.getTime() === MIN_DATE.getTime()) {
        return CalendarIteratorResult.FullRangeIterated;
      }

      cacheCursorDate = isForward ? this.cacheFilledEndDate : this.cacheFilledStartDate;
    }

    return CalendarIteratorResult.MaxCacheExtendCyclesReached;
  }

}

class CalendarCacheIntervalMultiple {
  constructor(config) {
    this.intervalGroups = [];
    config && Object.assign(this, config);
  }

  combineWith(interval) {
    const copy = this.intervalGroups.slice();
    copy.push([interval.calendar, interval]);
    return new CalendarCacheIntervalMultiple({
      intervalGroups: copy
    });
  }

  getIsWorkingForEvery() {
    if (this.isWorkingForEvery != null) return this.isWorkingForEvery;

    for (let [_calendar, intervals] of this.getGroups()) {
      if (!intervals[0].isWorking) return this.isWorkingForEvery = false;
    }

    return this.isWorkingForEvery = true;
  }

  getIsWorkingForSome() {
    if (this.isWorkingForSome != null) return this.isWorkingForSome;

    for (let [_calendar, intervals] of this.getGroups()) {
      if (intervals[0].isWorking) return this.isWorkingForSome = true;
    }

    return this.isWorkingForSome = false;
  }

  getCalendars() {
    this.getGroups();
    return this.calendars;
  }

  isCalendarWorking(calendar) {
    return this.getCalendarsWorkStatus().get(calendar);
  }

  getCalendarsWorkStatus() {
    if (this.calendarsWorkStatus) return this.calendarsWorkStatus;
    const res = new Map();

    for (let [calendar, intervals] of this.getGroups()) {
      res.set(calendar, intervals[0].isWorking);
    }

    return this.calendarsWorkStatus = res;
  }

  getCalendarsWorking() {
    if (this.calendarsWorking) return this.calendarsWorking;
    const calendars = [];

    for (let [calendar, intervals] of this.getGroups()) {
      if (intervals[0].isWorking) calendars.push(calendar);
    }

    return this.calendarsWorking = calendars;
  }

  getCalendarsNonWorking() {
    if (this.calendarsNonWorking) return this.calendarsNonWorking;
    const calendars = [];

    for (let [calendar, intervals] of this.getGroups()) {
      if (!intervals[0].isWorking) calendars.push(calendar);
    }

    return this.calendarsNonWorking = calendars;
  }

  getGroups() {
    if (this.intervalsByCalendar) return this.intervalsByCalendar;
    const calendars = this.calendars = [];
    const intervalsByCalendar = new Map();
    this.intervalGroups.forEach(([calendar, interval]) => {
      let data = intervalsByCalendar.get(calendar);

      if (!data) {
        calendars.push(calendar);
        data = [];
        intervalsByCalendar.set(calendar, data);
      }

      data.push.apply(data, interval.intervals);
    });
    intervalsByCalendar.forEach((intervals, calendar) => {
      const unique = stripDuplicates(intervals);
      unique.sort((interval1, interval2) => interval2.getPriorityField() - interval1.getPriorityField());
      intervalsByCalendar.set(calendar, unique);
    });
    return this.intervalsByCalendar = intervalsByCalendar;
  }

}

const binarySearch = (value, array, comparator = (a, b) => a - b) => {
  let left = 0;
  let right = array.length;

  while (left < right) {
    const mid = (left + right) / 2 | 0;
    const compare = comparator(value, array[mid]);
    if (compare === 0) return {
      found: true,
      index: mid
    };else if (compare < 0) right = mid;else left = mid + 1;
  }

  return {
    found: false,
    index: right
  };
};

var IndexPosition;

(function (IndexPosition) {
  IndexPosition[IndexPosition["Exact"] = 0] = "Exact";
  IndexPosition[IndexPosition["Next"] = 1] = "Next";
})(IndexPosition || (IndexPosition = {}));

class SortedMap {
  constructor(comparator) {
    this.keys = [];
    this.values = [];

    this.comparator = comparator || ((a, b) => a - b);
  }

  set(key, value) {
    const search = binarySearch(key, this.keys, this.comparator);

    if (search.found) {
      this.values[search.index] = value;
    } else {
      this.keys.splice(search.index, 0, key);
      this.values.splice(search.index, 0, value);
    }

    return search.index;
  }

  insertAt(index, key, value) {
    this.keys.splice(index, 0, key);
    this.values.splice(index, 0, value);
  }

  setValueAt(index, value) {
    this.values[index] = value;
  }

  get(key) {
    const search = binarySearch(key, this.keys, this.comparator);
    return search.found ? this.values[search.index] : undefined;
  }

  getEntryAt(index) {
    return index < this.keys.length ? {
      key: this.keys[index],
      value: this.values[index]
    } : undefined;
  }

  getKeyAt(index) {
    return this.keys[index];
  }

  getValueAt(index) {
    return this.values[index];
  }

  delete(key) {
    const search = binarySearch(key, this.keys, this.comparator);
    if (search.found) this.deleteAt(search.index);
  }

  size() {
    return this.keys.length;
  }

  deleteAt(index) {
    this.keys.splice(index, 1);
    this.values.splice(index, 1);
  }

  indexOfKey(key) {
    const search = binarySearch(key, this.keys, this.comparator);
    return {
      found: search.found ? IndexPosition.Exact : IndexPosition.Next,
      index: search.index
    };
  }

  map(func) {
    const keys = this.keys;
    const values = this.values;
    const result = [];

    for (let i = 0; i < keys.length; i++) result.push(func(values[i], keys[i], i));

    return result;
  }

  getAllEntries() {
    return this.map((value, key) => {
      return {
        value,
        key
      };
    });
  }

  clear() {
    this.keys.length = 0;
    this.values.length = 0;
  }

}

class IntervalCache {
  constructor(config) {
    this.points = new SortedMap((a, b) => a.getTime() - b.getTime());
    this.leftInfinityKey = MIN_DATE;
    this.rightInfinityKey = MAX_DATE;
    Object.assign(this, config);
    if (this.emptyInterval === undefined || !this.combineIntervalsFn) throw new Error("All of `emptyPoint`, `combineIntervalsFn` are required");
    this.points.set(this.leftInfinityKey, this.emptyInterval);
  }

  size() {
    return this.points.size();
  }

  indexOf(date) {
    return this.points.indexOfKey(date);
  }

  getDateAt(index) {
    return this.points.getKeyAt(index);
  }

  getPointAt(index) {
    return this.points.getValueAt(index);
  }

  getIntervalOf(date, edgeInclusion = EdgeInclusion.Left) {
    let {
      found,
      index
    } = this.indexOf(date);
    let startDateIndex;

    if (edgeInclusion === EdgeInclusion.Left) {
      startDateIndex = found === IndexPosition.Exact ? index : index - 1;
    } else {
      startDateIndex = index - 1;
    }

    return this.getIntervalWithStartDateIndex(startDateIndex);
  }

  getPrevInterval(interval) {
    if (interval.startDateIndex === 0) return null;
    return this.getIntervalWithStartDateIndex(interval.startDateIndex - 1);
  }

  getNextInterval(interval) {
    if (interval.startDateIndex >= this.size() - 1) return null;
    return this.getIntervalWithStartDateIndex(interval.startDateIndex + 1);
  }

  getIntervalWithStartDateIndex(startDateIndex) {
    return {
      startDateIndex: startDateIndex,
      startDate: this.getDateAt(startDateIndex),
      endDate: startDateIndex + 1 < this.size() ? this.getDateAt(startDateIndex + 1) : this.rightInfinityKey,
      cacheInterval: this.getPointAt(startDateIndex)
    };
  }

  addInterval(startDate, endDate, extendInterval) {
    const points = this.points;
    const {
      found,
      index
    } = points.indexOfKey(startDate);
    let curIndex;
    let lastUpdatedPoint;

    if (found == IndexPosition.Exact) {
      const inclusion = extendInterval(lastUpdatedPoint = points.getValueAt(index));
      points.setValueAt(index, inclusion);
      curIndex = index + 1;
    } else {
      const inclusion = extendInterval(lastUpdatedPoint = points.getValueAt(index - 1));
      points.insertAt(index, startDate, inclusion);
      curIndex = index + 1;
    }

    while (curIndex < points.size()) {
      const curDate = points.getKeyAt(curIndex);
      if (curDate.getTime() >= endDate.getTime()) break;
      const inclusion = extendInterval(lastUpdatedPoint = points.getValueAt(curIndex));
      points.setValueAt(curIndex, inclusion);
      curIndex++;
    }

    if (curIndex === points.size()) {
      points.insertAt(points.size(), endDate, this.emptyInterval);
    } else {
      const curDate = points.getKeyAt(curIndex);

      if (curDate.getTime() === endDate.getTime()) ; else {
        points.insertAt(curIndex, endDate, lastUpdatedPoint);
      }
    }
  }

  includeWrappingRange(intervalCache, startDate, endDate) {
    let interval = intervalCache.getIntervalOf(startDate);

    while (interval) {
      this.addInterval(interval.startDate, interval.endDate, existingInterval => this.combineIntervalsFn(existingInterval, interval.cacheInterval));
      if (interval.endDate.getTime() > endDate.getTime()) break;
      interval = intervalCache.getNextInterval(interval);
    }
  }

  getSummary() {
    return this.points.map((label, date) => {
      return {
        label,
        date
      };
    });
  }

  clear() {
    this.points.clear();
    this.points.set(this.leftInfinityKey, this.emptyInterval);
  }

}

class CalendarCacheMultiple extends CalendarCache {
  constructor(config) {
    super(config);
    this.calendarCaches = stripDuplicates(this.calendarCaches);
    this.intervalCache = new IntervalCache({
      emptyInterval: new CalendarCacheIntervalMultiple(),
      combineIntervalsFn: (interval1, interval2) => {
        return interval1.combineWith(interval2);
      }
    });
  }

  fillCache(startDate, endDate) {
    this.calendarCaches.forEach(calendarCache => {
      calendarCache.fillCache(startDate, endDate);
      this.includeWrappingRangeFrom(calendarCache, startDate, endDate);
    });
  }

}

/**
 * The code just copy/pasted from pre-built later.js file and made exported
 *
 * @private
 */
// @ts-ignore
const later = function () {

  var later = {
    // @ts-ignore
    version: "1.2.0" // @ts-ignore

  }; // @ts-ignore

  if (!Array.prototype.indexOf) {
    // @ts-ignore
    Array.prototype.indexOf = function (searchElement) {

      if (this == null) {
        // @ts-ignore
        throw new TypeError(); // @ts-ignore
      } // @ts-ignore

      var t = Object(this); // @ts-ignore

      var len = t.length >>> 0; // @ts-ignore

      if (len === 0) {
        // @ts-ignore
        return -1; // @ts-ignore
      } // @ts-ignore

      var n = 0; // @ts-ignore

      if (arguments.length > 1) {
        // @ts-ignore
        n = Number(arguments[1]); // @ts-ignore

        if (n != n) {
          // @ts-ignore
          n = 0; // @ts-ignore
        } else if (n != 0 && n != Infinity && n != -Infinity) {
          // @ts-ignore
          n = (n > 0 || -1) * Math.floor(Math.abs(n)); // @ts-ignore
        } // @ts-ignore

      } // @ts-ignore

      if (n >= len) {
        // @ts-ignore
        return -1; // @ts-ignore
      } // @ts-ignore

      var k = n >= 0 ? n : Math.max(len - Math.abs(n), 0); // @ts-ignore

      for (; k < len; k++) {
        // @ts-ignore
        if (k in t && t[k] === searchElement) {
          // @ts-ignore
          return k; // @ts-ignore
        } // @ts-ignore

      } // @ts-ignore

      return -1; // @ts-ignore
    }; // @ts-ignore

  } // @ts-ignore

  if (!String.prototype.trim) {
    // @ts-ignore
    String.prototype.trim = function () {
      // @ts-ignore
      return this.replace(/^\s+|\s+$/g, ""); // @ts-ignore
    }; // @ts-ignore

  } // @ts-ignore

  later.array = {}; // @ts-ignore

  later.array.sort = function (arr, zeroIsLast) {
    // @ts-ignore
    arr.sort(function (a, b) {
      // @ts-ignore
      return +a - +b; // @ts-ignore
    }); // @ts-ignore

    if (zeroIsLast && arr[0] === 0) {
      // @ts-ignore
      arr.push(arr.shift()); // @ts-ignore
    } // @ts-ignore

  }; // @ts-ignore

  later.array.next = function (val, values, extent) {
    // @ts-ignore
    var cur,
        zeroIsLargest = extent[0] !== 0,
        nextIdx = 0; // @ts-ignore

    for (var i = values.length - 1; i > -1; --i) {
      // @ts-ignore
      cur = values[i]; // @ts-ignore

      if (cur === val) {
        // @ts-ignore
        return cur; // @ts-ignore
      } // @ts-ignore

      if (cur > val || cur === 0 && zeroIsLargest && extent[1] > val) {
        // @ts-ignore
        nextIdx = i; // @ts-ignore

        continue; // @ts-ignore
      } // @ts-ignore

      break; // @ts-ignore
    } // @ts-ignore

    return values[nextIdx]; // @ts-ignore
  }; // @ts-ignore

  later.array.nextInvalid = function (val, values, extent) {
    // @ts-ignore
    var min = extent[0],
        max = extent[1],
        len = values.length,
        zeroVal = values[len - 1] === 0 && min !== 0 ? max : 0,
        next = val,
        i = values.indexOf(val),
        start = next; // @ts-ignore

    while (next === (values[i] || zeroVal)) {
      // @ts-ignore
      next++; // @ts-ignore

      if (next > max) {
        // @ts-ignore
        next = min; // @ts-ignore
      } // @ts-ignore

      i++; // @ts-ignore

      if (i === len) {
        // @ts-ignore
        i = 0; // @ts-ignore
      } // @ts-ignore

      if (next === start) {
        // @ts-ignore
        return undefined; // @ts-ignore
      } // @ts-ignore

    } // @ts-ignore

    return next; // @ts-ignore
  }; // @ts-ignore

  later.array.prev = function (val, values, extent) {
    // @ts-ignore
    var cur,
        len = values.length,
        zeroIsLargest = extent[0] !== 0,
        prevIdx = len - 1; // @ts-ignore

    for (var i = 0; i < len; i++) {
      // @ts-ignore
      cur = values[i]; // @ts-ignore

      if (cur === val) {
        // @ts-ignore
        return cur; // @ts-ignore
      } // @ts-ignore

      if (cur < val || cur === 0 && zeroIsLargest && extent[1] < val) {
        // @ts-ignore
        prevIdx = i; // @ts-ignore

        continue; // @ts-ignore
      } // @ts-ignore

      break; // @ts-ignore
    } // @ts-ignore

    return values[prevIdx]; // @ts-ignore
  }; // @ts-ignore

  later.array.prevInvalid = function (val, values, extent) {
    // @ts-ignore
    var min = extent[0],
        max = extent[1],
        len = values.length,
        zeroVal = values[len - 1] === 0 && min !== 0 ? max : 0,
        next = val,
        i = values.indexOf(val),
        start = next; // @ts-ignore

    while (next === (values[i] || zeroVal)) {
      // @ts-ignore
      next--; // @ts-ignore

      if (next < min) {
        // @ts-ignore
        next = max; // @ts-ignore
      } // @ts-ignore

      i--; // @ts-ignore

      if (i === -1) {
        // @ts-ignore
        i = len - 1; // @ts-ignore
      } // @ts-ignore

      if (next === start) {
        // @ts-ignore
        return undefined; // @ts-ignore
      } // @ts-ignore

    } // @ts-ignore

    return next; // @ts-ignore
  }; // @ts-ignore

  later.day = later.D = {
    // @ts-ignore
    name: "day",
    // @ts-ignore
    range: 86400,
    // @ts-ignore
    val: function (d) {
      // @ts-ignore
      return d.D || (d.D = later.date.getDate.call(d)); // @ts-ignore
    },
    // @ts-ignore
    isValid: function (d, val) {
      // @ts-ignore
      return later.D.val(d) === (val || later.D.extent(d)[1]); // @ts-ignore
    },
    // @ts-ignore
    extent: function (d) {
      // @ts-ignore
      if (d.DExtent) return d.DExtent; // @ts-ignore

      var month = later.M.val(d),
          max = later.DAYS_IN_MONTH[month - 1]; // @ts-ignore

      if (month === 2 && later.dy.extent(d)[1] === 366) {
        // @ts-ignore
        max = max + 1; // @ts-ignore
      } // @ts-ignore

      return d.DExtent = [1, max]; // @ts-ignore
    },
    // @ts-ignore
    start: function (d) {
      // @ts-ignore
      return d.DStart || (d.DStart = later.date.next(later.Y.val(d), later.M.val(d), later.D.val(d))); // @ts-ignore
    },
    // @ts-ignore
    end: function (d) {
      // @ts-ignore
      return d.DEnd || (d.DEnd = later.date.prev(later.Y.val(d), later.M.val(d), later.D.val(d))); // @ts-ignore
    },
    // @ts-ignore
    next: function (d, val) {
      // @ts-ignore
      val = val > later.D.extent(d)[1] ? 1 : val; // @ts-ignore

      var month = later.date.nextRollover(d, val, later.D, later.M),
          DMax = later.D.extent(month)[1]; // @ts-ignore

      val = val > DMax ? 1 : val || DMax; // @ts-ignore

      return later.date.next(later.Y.val(month), later.M.val(month), val); // @ts-ignore
    },
    // @ts-ignore
    prev: function (d, val) {
      // @ts-ignore
      var month = later.date.prevRollover(d, val, later.D, later.M),
          DMax = later.D.extent(month)[1]; // @ts-ignore

      return later.date.prev(later.Y.val(month), later.M.val(month), val > DMax ? DMax : val || DMax); // @ts-ignore
    } // @ts-ignore

  }; // @ts-ignore

  later.dayOfWeekCount = later.dc = {
    // @ts-ignore
    name: "day of week count",
    // @ts-ignore
    range: 604800,
    // @ts-ignore
    val: function (d) {
      // @ts-ignore
      return d.dc || (d.dc = Math.floor((later.D.val(d) - 1) / 7) + 1); // @ts-ignore
    },
    // @ts-ignore
    isValid: function (d, val) {
      // @ts-ignore
      return later.dc.val(d) === val || val === 0 && later.D.val(d) > later.D.extent(d)[1] - 7; // @ts-ignore
    },
    // @ts-ignore
    extent: function (d) {
      // @ts-ignore
      return d.dcExtent || (d.dcExtent = [1, Math.ceil(later.D.extent(d)[1] / 7)]); // @ts-ignore
    },
    // @ts-ignore
    start: function (d) {
      // @ts-ignore
      return d.dcStart || (d.dcStart = later.date.next(later.Y.val(d), later.M.val(d), Math.max(1, (later.dc.val(d) - 1) * 7 + 1 || 1))); // @ts-ignore
    },
    // @ts-ignore
    end: function (d) {
      // @ts-ignore
      return d.dcEnd || (d.dcEnd = later.date.prev(later.Y.val(d), later.M.val(d), Math.min(later.dc.val(d) * 7, later.D.extent(d)[1]))); // @ts-ignore
    },
    // @ts-ignore
    next: function (d, val) {
      // @ts-ignore
      val = val > later.dc.extent(d)[1] ? 1 : val; // @ts-ignore

      var month = later.date.nextRollover(d, val, later.dc, later.M),
          dcMax = later.dc.extent(month)[1]; // @ts-ignore

      val = val > dcMax ? 1 : val; // @ts-ignore

      var next = later.date.next(later.Y.val(month), later.M.val(month), val === 0 ? later.D.extent(month)[1] - 6 : 1 + 7 * (val - 1)); // @ts-ignore

      if (next.getTime() <= d.getTime()) {
        // @ts-ignore
        month = later.M.next(d, later.M.val(d) + 1); // @ts-ignore

        return later.date.next(later.Y.val(month), later.M.val(month), val === 0 ? later.D.extent(month)[1] - 6 : 1 + 7 * (val - 1)); // @ts-ignore
      } // @ts-ignore

      return next; // @ts-ignore
    },
    // @ts-ignore
    prev: function (d, val) {
      // @ts-ignore
      var month = later.date.prevRollover(d, val, later.dc, later.M),
          dcMax = later.dc.extent(month)[1]; // @ts-ignore

      val = val > dcMax ? dcMax : val || dcMax; // @ts-ignore

      return later.dc.end(later.date.prev(later.Y.val(month), later.M.val(month), 1 + 7 * (val - 1))); // @ts-ignore
    } // @ts-ignore

  }; // @ts-ignore

  later.dayOfWeek = later.dw = later.d = {
    // @ts-ignore
    name: "day of week",
    // @ts-ignore
    range: 86400,
    // @ts-ignore
    val: function (d) {
      // @ts-ignore
      return d.dw || (d.dw = later.date.getDay.call(d) + 1); // @ts-ignore
    },
    // @ts-ignore
    isValid: function (d, val) {
      // @ts-ignore
      return later.dw.val(d) === (val || 7); // @ts-ignore
    },
    // @ts-ignore
    extent: function () {
      // @ts-ignore
      return [1, 7]; // @ts-ignore
    },
    // @ts-ignore
    start: function (d) {
      // @ts-ignore
      return later.D.start(d); // @ts-ignore
    },
    // @ts-ignore
    end: function (d) {
      // @ts-ignore
      return later.D.end(d); // @ts-ignore
    },
    // @ts-ignore
    next: function (d, val) {
      // @ts-ignore
      val = val > 7 ? 1 : val || 7; // @ts-ignore

      return later.date.next(later.Y.val(d), later.M.val(d), later.D.val(d) + (val - later.dw.val(d)) + (val <= later.dw.val(d) ? 7 : 0)); // @ts-ignore
    },
    // @ts-ignore
    prev: function (d, val) {
      // @ts-ignore
      val = val > 7 ? 7 : val || 7; // @ts-ignore

      return later.date.prev(later.Y.val(d), later.M.val(d), later.D.val(d) + (val - later.dw.val(d)) + (val >= later.dw.val(d) ? -7 : 0)); // @ts-ignore
    } // @ts-ignore

  }; // @ts-ignore

  later.dayOfYear = later.dy = {
    // @ts-ignore
    name: "day of year",
    // @ts-ignore
    range: 86400,
    // @ts-ignore
    val: function (d) {
      // @ts-ignore
      return d.dy || (d.dy = Math.ceil(1 + (later.D.start(d).getTime() - later.Y.start(d).getTime()) / later.DAY)); // @ts-ignore
    },
    // @ts-ignore
    isValid: function (d, val) {
      // @ts-ignore
      return later.dy.val(d) === (val || later.dy.extent(d)[1]); // @ts-ignore
    },
    // @ts-ignore
    extent: function (d) {
      // @ts-ignore
      var year = later.Y.val(d); // @ts-ignore

      return d.dyExtent || (d.dyExtent = [1, year % 4 ? 365 : 366]); // @ts-ignore
    },
    // @ts-ignore
    start: function (d) {
      // @ts-ignore
      return later.D.start(d); // @ts-ignore
    },
    // @ts-ignore
    end: function (d) {
      // @ts-ignore
      return later.D.end(d); // @ts-ignore
    },
    // @ts-ignore
    next: function (d, val) {
      // @ts-ignore
      val = val > later.dy.extent(d)[1] ? 1 : val; // @ts-ignore

      var year = later.date.nextRollover(d, val, later.dy, later.Y),
          dyMax = later.dy.extent(year)[1]; // @ts-ignore

      val = val > dyMax ? 1 : val || dyMax; // @ts-ignore

      return later.date.next(later.Y.val(year), later.M.val(year), val); // @ts-ignore
    },
    // @ts-ignore
    prev: function (d, val) {
      // @ts-ignore
      var year = later.date.prevRollover(d, val, later.dy, later.Y),
          dyMax = later.dy.extent(year)[1]; // @ts-ignore

      val = val > dyMax ? dyMax : val || dyMax; // @ts-ignore

      return later.date.prev(later.Y.val(year), later.M.val(year), val); // @ts-ignore
    } // @ts-ignore

  }; // @ts-ignore

  later.hour = later.h = {
    // @ts-ignore
    name: "hour",
    // @ts-ignore
    range: 3600,
    // @ts-ignore
    val: function (d) {
      // @ts-ignore
      return d.h || (d.h = later.date.getHour.call(d)); // @ts-ignore
    },
    // @ts-ignore
    isValid: function (d, val) {
      // @ts-ignore
      return later.h.val(d) === val; // @ts-ignore
    },
    // @ts-ignore
    extent: function () {
      // @ts-ignore
      return [0, 23]; // @ts-ignore
    },
    // @ts-ignore
    start: function (d) {
      // @ts-ignore
      return d.hStart || (d.hStart = later.date.next(later.Y.val(d), later.M.val(d), later.D.val(d), later.h.val(d))); // @ts-ignore
    },
    // @ts-ignore
    end: function (d) {
      // @ts-ignore
      return d.hEnd || (d.hEnd = later.date.prev(later.Y.val(d), later.M.val(d), later.D.val(d), later.h.val(d))); // @ts-ignore
    },
    // @ts-ignore
    next: function (d, val) {
      // @ts-ignore
      val = val > 23 ? 0 : val; // @ts-ignore

      var next = later.date.next(later.Y.val(d), later.M.val(d), later.D.val(d) + (val <= later.h.val(d) ? 1 : 0), val); // @ts-ignore

      if (!later.date.isUTC && next.getTime() <= d.getTime()) {
        // @ts-ignore
        next = later.date.next(later.Y.val(next), later.M.val(next), later.D.val(next), val + 1); // @ts-ignore
      } // @ts-ignore

      return next; // @ts-ignore
    },
    // @ts-ignore
    prev: function (d, val) {
      // @ts-ignore
      val = val > 23 ? 23 : val; // @ts-ignore

      return later.date.prev(later.Y.val(d), later.M.val(d), later.D.val(d) + (val >= later.h.val(d) ? -1 : 0), val); // @ts-ignore
    } // @ts-ignore

  }; // @ts-ignore

  later.minute = later.m = {
    // @ts-ignore
    name: "minute",
    // @ts-ignore
    range: 60,
    // @ts-ignore
    val: function (d) {
      // @ts-ignore
      return d.m || (d.m = later.date.getMin.call(d)); // @ts-ignore
    },
    // @ts-ignore
    isValid: function (d, val) {
      // @ts-ignore
      return later.m.val(d) === val; // @ts-ignore
    },
    // @ts-ignore
    extent: function (d) {
      // @ts-ignore
      return [0, 59]; // @ts-ignore
    },
    // @ts-ignore
    start: function (d) {
      // @ts-ignore
      return d.mStart || (d.mStart = later.date.next(later.Y.val(d), later.M.val(d), later.D.val(d), later.h.val(d), later.m.val(d))); // @ts-ignore
    },
    // @ts-ignore
    end: function (d) {
      // @ts-ignore
      return d.mEnd || (d.mEnd = later.date.prev(later.Y.val(d), later.M.val(d), later.D.val(d), later.h.val(d), later.m.val(d))); // @ts-ignore
    },
    // @ts-ignore
    next: function (d, val) {
      // @ts-ignore
      var m = later.m.val(d),
          s = later.s.val(d),
          inc = val > 59 ? 60 - m : val <= m ? 60 - m + val : val - m,
          next = new Date(d.getTime() + inc * later.MIN - s * later.SEC); // @ts-ignore

      if (!later.date.isUTC && next.getTime() <= d.getTime()) {
        // @ts-ignore
        next = new Date(d.getTime() + (inc + 120) * later.MIN - s * later.SEC); // @ts-ignore
      } // @ts-ignore

      return next; // @ts-ignore
    },
    // @ts-ignore
    prev: function (d, val) {
      // @ts-ignore
      val = val > 59 ? 59 : val; // @ts-ignore

      return later.date.prev(later.Y.val(d), later.M.val(d), later.D.val(d), later.h.val(d) + (val >= later.m.val(d) ? -1 : 0), val); // @ts-ignore
    } // @ts-ignore

  }; // @ts-ignore

  later.month = later.M = {
    // @ts-ignore
    name: "month",
    // @ts-ignore
    range: 2629740,
    // @ts-ignore
    val: function (d) {
      // @ts-ignore
      return d.M || (d.M = later.date.getMonth.call(d) + 1); // @ts-ignore
    },
    // @ts-ignore
    isValid: function (d, val) {
      // @ts-ignore
      return later.M.val(d) === (val || 12); // @ts-ignore
    },
    // @ts-ignore
    extent: function () {
      // @ts-ignore
      return [1, 12]; // @ts-ignore
    },
    // @ts-ignore
    start: function (d) {
      // @ts-ignore
      return d.MStart || (d.MStart = later.date.next(later.Y.val(d), later.M.val(d))); // @ts-ignore
    },
    // @ts-ignore
    end: function (d) {
      // @ts-ignore
      return d.MEnd || (d.MEnd = later.date.prev(later.Y.val(d), later.M.val(d))); // @ts-ignore
    },
    // @ts-ignore
    next: function (d, val) {
      // @ts-ignore
      val = val > 12 ? 1 : val || 12; // @ts-ignore

      return later.date.next(later.Y.val(d) + (val > later.M.val(d) ? 0 : 1), val); // @ts-ignore
    },
    // @ts-ignore
    prev: function (d, val) {
      // @ts-ignore
      val = val > 12 ? 12 : val || 12; // @ts-ignore

      return later.date.prev(later.Y.val(d) - (val >= later.M.val(d) ? 1 : 0), val); // @ts-ignore
    } // @ts-ignore

  }; // @ts-ignore

  later.second = later.s = {
    // @ts-ignore
    name: "second",
    // @ts-ignore
    range: 1,
    // @ts-ignore
    val: function (d) {
      // @ts-ignore
      return d.s || (d.s = later.date.getSec.call(d)); // @ts-ignore
    },
    // @ts-ignore
    isValid: function (d, val) {
      // @ts-ignore
      return later.s.val(d) === val; // @ts-ignore
    },
    // @ts-ignore
    extent: function () {
      // @ts-ignore
      return [0, 59]; // @ts-ignore
    },
    // @ts-ignore
    start: function (d) {
      // @ts-ignore
      return d; // @ts-ignore
    },
    // @ts-ignore
    end: function (d) {
      // @ts-ignore
      return d; // @ts-ignore
    },
    // @ts-ignore
    next: function (d, val) {
      // @ts-ignore
      var s = later.s.val(d),
          inc = val > 59 ? 60 - s : val <= s ? 60 - s + val : val - s,
          next = new Date(d.getTime() + inc * later.SEC); // @ts-ignore

      if (!later.date.isUTC && next.getTime() <= d.getTime()) {
        // @ts-ignore
        next = new Date(d.getTime() + (inc + 7200) * later.SEC); // @ts-ignore
      } // @ts-ignore

      return next; // @ts-ignore
    },
    // @ts-ignore
    prev: function (d, val, cache) {
      // @ts-ignore
      val = val > 59 ? 59 : val; // @ts-ignore

      return later.date.prev(later.Y.val(d), later.M.val(d), later.D.val(d), later.h.val(d), later.m.val(d) + (val >= later.s.val(d) ? -1 : 0), val); // @ts-ignore
    } // @ts-ignore

  }; // @ts-ignore

  later.time = later.t = {
    // @ts-ignore
    name: "time",
    // @ts-ignore
    range: 1,
    // @ts-ignore
    val: function (d) {
      // @ts-ignore
      return d.t || (d.t = later.h.val(d) * 3600 + later.m.val(d) * 60 + later.s.val(d)); // @ts-ignore
    },
    // @ts-ignore
    isValid: function (d, val) {
      // @ts-ignore
      return later.t.val(d) === val; // @ts-ignore
    },
    // @ts-ignore
    extent: function () {
      // @ts-ignore
      return [0, 86399]; // @ts-ignore
    },
    // @ts-ignore
    start: function (d) {
      // @ts-ignore
      return d; // @ts-ignore
    },
    // @ts-ignore
    end: function (d) {
      // @ts-ignore
      return d; // @ts-ignore
    },
    // @ts-ignore
    next: function (d, val) {
      // @ts-ignore
      val = val > 86399 ? 0 : val; // @ts-ignore

      var next = later.date.next(later.Y.val(d), later.M.val(d), later.D.val(d) + (val <= later.t.val(d) ? 1 : 0), 0, 0, val); // @ts-ignore

      if (!later.date.isUTC && next.getTime() < d.getTime()) {
        // @ts-ignore
        next = later.date.next(later.Y.val(next), later.M.val(next), later.D.val(next), later.h.val(next), later.m.val(next), val + 7200); // @ts-ignore
      } // @ts-ignore

      return next; // @ts-ignore
    },
    // @ts-ignore
    prev: function (d, val) {
      // @ts-ignore
      val = val > 86399 ? 86399 : val; // @ts-ignore

      return later.date.next(later.Y.val(d), later.M.val(d), later.D.val(d) + (val >= later.t.val(d) ? -1 : 0), 0, 0, val); // @ts-ignore
    } // @ts-ignore

  }; // @ts-ignore

  later.weekOfMonth = later.wm = {
    // @ts-ignore
    name: "week of month",
    // @ts-ignore
    range: 604800,
    // @ts-ignore
    val: function (d) {
      // @ts-ignore
      return d.wm || (d.wm = (later.D.val(d) + (later.dw.val(later.M.start(d)) - 1) + (7 - later.dw.val(d))) / 7); // @ts-ignore
    },
    // @ts-ignore
    isValid: function (d, val) {
      // @ts-ignore
      return later.wm.val(d) === (val || later.wm.extent(d)[1]); // @ts-ignore
    },
    // @ts-ignore
    extent: function (d) {
      // @ts-ignore
      return d.wmExtent || (d.wmExtent = [1, (later.D.extent(d)[1] + (later.dw.val(later.M.start(d)) - 1) + (7 - later.dw.val(later.M.end(d)))) / 7]); // @ts-ignore
    },
    // @ts-ignore
    start: function (d) {
      // @ts-ignore
      return d.wmStart || (d.wmStart = later.date.next(later.Y.val(d), later.M.val(d), Math.max(later.D.val(d) - later.dw.val(d) + 1, 1))); // @ts-ignore
    },
    // @ts-ignore
    end: function (d) {
      // @ts-ignore
      return d.wmEnd || (d.wmEnd = later.date.prev(later.Y.val(d), later.M.val(d), Math.min(later.D.val(d) + (7 - later.dw.val(d)), later.D.extent(d)[1]))); // @ts-ignore
    },
    // @ts-ignore
    next: function (d, val) {
      // @ts-ignore
      val = val > later.wm.extent(d)[1] ? 1 : val; // @ts-ignore

      var month = later.date.nextRollover(d, val, later.wm, later.M),
          wmMax = later.wm.extent(month)[1]; // @ts-ignore

      val = val > wmMax ? 1 : val || wmMax; // @ts-ignore

      return later.date.next(later.Y.val(month), later.M.val(month), Math.max(1, (val - 1) * 7 - (later.dw.val(month) - 2))); // @ts-ignore
    },
    // @ts-ignore
    prev: function (d, val) {
      // @ts-ignore
      var month = later.date.prevRollover(d, val, later.wm, later.M),
          wmMax = later.wm.extent(month)[1]; // @ts-ignore

      val = val > wmMax ? wmMax : val || wmMax; // @ts-ignore

      return later.wm.end(later.date.next(later.Y.val(month), later.M.val(month), Math.max(1, (val - 1) * 7 - (later.dw.val(month) - 2)))); // @ts-ignore
    } // @ts-ignore

  }; // @ts-ignore

  later.weekOfYear = later.wy = {
    // @ts-ignore
    name: "week of year (ISO)",
    // @ts-ignore
    range: 604800,
    // @ts-ignore
    val: function (d) {
      // @ts-ignore
      if (d.wy) return d.wy; // @ts-ignore

      var wThur = later.dw.next(later.wy.start(d), 5),
          YThur = later.dw.next(later.Y.prev(wThur, later.Y.val(wThur) - 1), 5); // @ts-ignore

      return d.wy = 1 + Math.ceil((wThur.getTime() - YThur.getTime()) / later.WEEK); // @ts-ignore
    },
    // @ts-ignore
    isValid: function (d, val) {
      // @ts-ignore
      return later.wy.val(d) === (val || later.wy.extent(d)[1]); // @ts-ignore
    },
    // @ts-ignore
    extent: function (d) {
      // @ts-ignore
      if (d.wyExtent) return d.wyExtent; // @ts-ignore

      var year = later.dw.next(later.wy.start(d), 5),
          dwFirst = later.dw.val(later.Y.start(year)),
          dwLast = later.dw.val(later.Y.end(year)); // @ts-ignore

      return d.wyExtent = [1, dwFirst === 5 || dwLast === 5 ? 53 : 52]; // @ts-ignore
    },
    // @ts-ignore
    start: function (d) {
      // @ts-ignore
      return d.wyStart || (d.wyStart = later.date.next(later.Y.val(d), later.M.val(d), later.D.val(d) - (later.dw.val(d) > 1 ? later.dw.val(d) - 2 : 6))); // @ts-ignore
    },
    // @ts-ignore
    end: function (d) {
      // @ts-ignore
      return d.wyEnd || (d.wyEnd = later.date.prev(later.Y.val(d), later.M.val(d), later.D.val(d) + (later.dw.val(d) > 1 ? 8 - later.dw.val(d) : 0))); // @ts-ignore
    },
    // @ts-ignore
    next: function (d, val) {
      // @ts-ignore
      val = val > later.wy.extent(d)[1] ? 1 : val; // @ts-ignore

      var wyThur = later.dw.next(later.wy.start(d), 5),
          year = later.date.nextRollover(wyThur, val, later.wy, later.Y); // @ts-ignore

      if (later.wy.val(year) !== 1) {
        // @ts-ignore
        year = later.dw.next(year, 2); // @ts-ignore
      } // @ts-ignore

      var wyMax = later.wy.extent(year)[1],
          wyStart = later.wy.start(year); // @ts-ignore

      val = val > wyMax ? 1 : val || wyMax; // @ts-ignore

      return later.date.next(later.Y.val(wyStart), later.M.val(wyStart), later.D.val(wyStart) + 7 * (val - 1)); // @ts-ignore
    },
    // @ts-ignore
    prev: function (d, val) {
      // @ts-ignore
      var wyThur = later.dw.next(later.wy.start(d), 5),
          year = later.date.prevRollover(wyThur, val, later.wy, later.Y); // @ts-ignore

      if (later.wy.val(year) !== 1) {
        // @ts-ignore
        year = later.dw.next(year, 2); // @ts-ignore
      } // @ts-ignore

      var wyMax = later.wy.extent(year)[1],
          wyEnd = later.wy.end(year); // @ts-ignore

      val = val > wyMax ? wyMax : val || wyMax; // @ts-ignore

      return later.wy.end(later.date.next(later.Y.val(wyEnd), later.M.val(wyEnd), later.D.val(wyEnd) + 7 * (val - 1))); // @ts-ignore
    } // @ts-ignore

  }; // @ts-ignore

  later.year = later.Y = {
    // @ts-ignore
    name: "year",
    // @ts-ignore
    range: 31556900,
    // @ts-ignore
    val: function (d) {
      // @ts-ignore
      return d.Y || (d.Y = later.date.getYear.call(d)); // @ts-ignore
    },
    // @ts-ignore
    isValid: function (d, val) {
      // @ts-ignore
      return later.Y.val(d) === val; // @ts-ignore
    },
    // @ts-ignore
    extent: function () {
      // @ts-ignore
      return [1970, 2099]; // @ts-ignore
    },
    // @ts-ignore
    start: function (d) {
      // @ts-ignore
      return d.YStart || (d.YStart = later.date.next(later.Y.val(d))); // @ts-ignore
    },
    // @ts-ignore
    end: function (d) {
      // @ts-ignore
      return d.YEnd || (d.YEnd = later.date.prev(later.Y.val(d))); // @ts-ignore
    },
    // @ts-ignore
    next: function (d, val) {
      // @ts-ignore
      return val > later.Y.val(d) && val <= later.Y.extent()[1] ? later.date.next(val) : later.NEVER; // @ts-ignore
    },
    // @ts-ignore
    prev: function (d, val) {
      // @ts-ignore
      return val < later.Y.val(d) && val >= later.Y.extent()[0] ? later.date.prev(val) : later.NEVER; // @ts-ignore
    } // @ts-ignore

  }; // @ts-ignore

  later.fullDate = later.fd = {
    // @ts-ignore
    name: "full date",
    // @ts-ignore
    range: 1,
    // @ts-ignore
    val: function (d) {
      // @ts-ignore
      return d.fd || (d.fd = d.getTime()); // @ts-ignore
    },
    // @ts-ignore
    isValid: function (d, val) {
      // @ts-ignore
      return later.fd.val(d) === val; // @ts-ignore
    },
    // @ts-ignore
    extent: function () {
      // @ts-ignore
      return [0, 3250368e7]; // @ts-ignore
    },
    // @ts-ignore
    start: function (d) {
      // @ts-ignore
      return d; // @ts-ignore
    },
    // @ts-ignore
    end: function (d) {
      // @ts-ignore
      return d; // @ts-ignore
    },
    // @ts-ignore
    next: function (d, val) {
      // @ts-ignore
      return later.fd.val(d) < val ? new Date(val) : later.NEVER; // @ts-ignore
    },
    // @ts-ignore
    prev: function (d, val) {
      // @ts-ignore
      return later.fd.val(d) > val ? new Date(val) : later.NEVER; // @ts-ignore
    } // @ts-ignore

  }; // @ts-ignore

  later.modifier = {}; // @ts-ignore

  later.modifier.after = later.modifier.a = function (constraint, values) {
    // @ts-ignore
    var value = values[0]; // @ts-ignore

    return {
      // @ts-ignore
      name: "after " + constraint.name,
      // @ts-ignore
      range: (constraint.extent(new Date())[1] - value) * constraint.range,
      // @ts-ignore
      val: constraint.val,
      // @ts-ignore
      isValid: function (d, val) {
        // @ts-ignore
        return this.val(d) >= value; // @ts-ignore
      },
      // @ts-ignore
      extent: constraint.extent,
      // @ts-ignore
      start: constraint.start,
      // @ts-ignore
      end: constraint.end,
      // @ts-ignore
      next: function (startDate, val) {
        // @ts-ignore
        if (val != value) val = constraint.extent(startDate)[0]; // @ts-ignore

        return constraint.next(startDate, val); // @ts-ignore
      },
      // @ts-ignore
      prev: function (startDate, val) {
        // @ts-ignore
        val = val === value ? constraint.extent(startDate)[1] : value - 1; // @ts-ignore

        return constraint.prev(startDate, val); // @ts-ignore
      } // @ts-ignore

    }; // @ts-ignore
  }; // @ts-ignore

  later.modifier.before = later.modifier.b = function (constraint, values) {
    // @ts-ignore
    var value = values[values.length - 1]; // @ts-ignore

    return {
      // @ts-ignore
      name: "before " + constraint.name,
      // @ts-ignore
      range: constraint.range * (value - 1),
      // @ts-ignore
      val: constraint.val,
      // @ts-ignore
      isValid: function (d, val) {
        // @ts-ignore
        return this.val(d) < value; // @ts-ignore
      },
      // @ts-ignore
      extent: constraint.extent,
      // @ts-ignore
      start: constraint.start,
      // @ts-ignore
      end: constraint.end,
      // @ts-ignore
      next: function (startDate, val) {
        // @ts-ignore
        val = val === value ? constraint.extent(startDate)[0] : value; // @ts-ignore

        return constraint.next(startDate, val); // @ts-ignore
      },
      // @ts-ignore
      prev: function (startDate, val) {
        // @ts-ignore
        val = val === value ? value - 1 : constraint.extent(startDate)[1]; // @ts-ignore

        return constraint.prev(startDate, val); // @ts-ignore
      } // @ts-ignore

    }; // @ts-ignore
  }; // @ts-ignore

  later.compile = function (schedDef) {
    // @ts-ignore
    var constraints = [],
        constraintsLen = 0,
        tickConstraint; // @ts-ignore

    for (var key in schedDef) {
      // @ts-ignore
      var nameParts = key.split("_"),
          name = nameParts[0],
          mod = nameParts[1],
          vals = schedDef[key],
          constraint = mod ? later.modifier[mod](later[name], vals) : later[name]; // @ts-ignore

      constraints.push({
        // @ts-ignore
        constraint: constraint,
        // @ts-ignore
        vals: vals // @ts-ignore

      }); // @ts-ignore

      constraintsLen++; // @ts-ignore
    } // @ts-ignore

    constraints.sort(function (a, b) {
      // @ts-ignore
      var ra = a.constraint.range,
          rb = b.constraint.range; // @ts-ignore

      return rb < ra ? -1 : rb > ra ? 1 : 0; // @ts-ignore
    }); // @ts-ignore

    tickConstraint = constraints[constraintsLen - 1].constraint; // @ts-ignore

    function compareFn(dir) {
      // @ts-ignore
      return dir === "next" ? function (a, b) {
        // @ts-ignore
        return a.getTime() > b.getTime(); // @ts-ignore
      } : function (a, b) {
        // @ts-ignore
        return b.getTime() > a.getTime(); // @ts-ignore
      }; // @ts-ignore
    } // @ts-ignore

    return {
      // @ts-ignore
      start: function (dir, startDate) {
        // @ts-ignore
        var next = startDate,
            nextVal = later.array[dir],
            maxAttempts = 1e3,
            done; // @ts-ignore

        while (maxAttempts-- && !done && next) {
          // @ts-ignore
          done = true; // @ts-ignore

          for (var i = 0; i < constraintsLen; i++) {
            // @ts-ignore
            var constraint = constraints[i].constraint,
                curVal = constraint.val(next),
                extent = constraint.extent(next),
                newVal = nextVal(curVal, constraints[i].vals, extent); // @ts-ignore

            if (!constraint.isValid(next, newVal)) {
              // @ts-ignore
              next = constraint[dir](next, newVal); // @ts-ignore

              done = false; // @ts-ignore

              break; // @ts-ignore
            } // @ts-ignore

          } // @ts-ignore

        } // @ts-ignore

        if (next !== later.NEVER) {
          // @ts-ignore
          next = dir === "next" ? tickConstraint.start(next) : tickConstraint.end(next); // @ts-ignore
        } // @ts-ignore

        return next; // @ts-ignore
      },
      // @ts-ignore
      end: function (dir, startDate) {
        // @ts-ignore
        var result,
            nextVal = later.array[dir + "Invalid"],
            compare = compareFn(dir); // @ts-ignore

        for (var i = constraintsLen - 1; i >= 0; i--) {
          // @ts-ignore
          var constraint = constraints[i].constraint,
              curVal = constraint.val(startDate),
              extent = constraint.extent(startDate),
              newVal = nextVal(curVal, constraints[i].vals, extent),
              next; // @ts-ignore

          if (newVal !== undefined) {
            // @ts-ignore
            next = constraint[dir](startDate, newVal); // @ts-ignore

            if (next && (!result || compare(result, next))) {
              // @ts-ignore
              result = next; // @ts-ignore
            } // @ts-ignore

          } // @ts-ignore

        } // @ts-ignore

        return result; // @ts-ignore
      },
      // @ts-ignore
      tick: function (dir, date) {
        // @ts-ignore
        return new Date(dir === "next" ? tickConstraint.end(date).getTime() + later.SEC : tickConstraint.start(date).getTime() - later.SEC); // @ts-ignore
      },
      // @ts-ignore
      tickStart: function (date) {
        // @ts-ignore
        return tickConstraint.start(date); // @ts-ignore
      } // @ts-ignore

    }; // @ts-ignore
  }; // @ts-ignore

  later.schedule = function (sched) {
    // @ts-ignore
    if (!sched) throw new Error("Missing schedule definition."); // @ts-ignore

    if (!sched.schedules) throw new Error("Definition must include at least one schedule."); // @ts-ignore

    var schedules = [],
        schedulesLen = sched.schedules.length,
        exceptions = [],
        exceptionsLen = sched.exceptions ? sched.exceptions.length : 0; // @ts-ignore

    for (var i = 0; i < schedulesLen; i++) {
      // @ts-ignore
      schedules.push(later.compile(sched.schedules[i])); // @ts-ignore
    } // @ts-ignore

    for (var j = 0; j < exceptionsLen; j++) {
      // @ts-ignore
      exceptions.push(later.compile(sched.exceptions[j])); // @ts-ignore
    } // @ts-ignore

    function getInstances(dir, count, startDate, endDate, isRange) {
      // @ts-ignore
      var compare = compareFn(dir),
          loopCount = count,
          maxAttempts = 1e3,
          schedStarts = [],
          exceptStarts = [],
          next,
          end,
          results = [],
          isForward = dir === "next",
          lastResult,
          rStart = isForward ? 0 : 1,
          rEnd = isForward ? 1 : 0; // @ts-ignore

      startDate = startDate ? new Date(startDate) : new Date(); // @ts-ignore

      if (!startDate || !startDate.getTime()) throw new Error("Invalid start date."); // @ts-ignore

      setNextStarts(dir, schedules, schedStarts, startDate); // @ts-ignore

      setRangeStarts(dir, exceptions, exceptStarts, startDate); // @ts-ignore

      while (maxAttempts-- && loopCount && (next = findNext(schedStarts, compare))) {
        // @ts-ignore
        if (endDate && compare(next, endDate)) {
          // @ts-ignore
          break; // @ts-ignore
        } // @ts-ignore

        if (exceptionsLen) {
          // @ts-ignore
          updateRangeStarts(dir, exceptions, exceptStarts, next); // @ts-ignore

          if (end = calcRangeOverlap(dir, exceptStarts, next)) {
            // @ts-ignore
            updateNextStarts(dir, schedules, schedStarts, end); // @ts-ignore

            continue; // @ts-ignore
          } // @ts-ignore

        } // @ts-ignore

        if (isRange) {
          // @ts-ignore
          var maxEndDate = calcMaxEndDate(exceptStarts, compare); // @ts-ignore

          end = calcEnd(dir, schedules, schedStarts, next, maxEndDate); // @ts-ignore

          var r = isForward ? [new Date(Math.max(startDate, next)), end ? new Date(endDate ? Math.min(end, endDate) : end) : undefined] : [end ? new Date(endDate ? Math.max(endDate, end.getTime() + later.SEC) : end.getTime() + later.SEC) : undefined, new Date(Math.min(startDate, next.getTime() + later.SEC))]; // @ts-ignore

          if (lastResult && r[rStart].getTime() === lastResult[rEnd].getTime()) {
            // @ts-ignore
            lastResult[rEnd] = r[rEnd]; // @ts-ignore

            loopCount++; // @ts-ignore
          } else {
            // @ts-ignore
            lastResult = r; // @ts-ignore

            results.push(lastResult); // @ts-ignore
          } // @ts-ignore

          if (!end) break; // @ts-ignore

          updateNextStarts(dir, schedules, schedStarts, end); // @ts-ignore
        } else {
          // @ts-ignore
          results.push(isForward ? new Date(Math.max(startDate, next)) : getStart(schedules, schedStarts, next, endDate)); // @ts-ignore

          tickStarts(dir, schedules, schedStarts, next); // @ts-ignore
        } // @ts-ignore

        loopCount--; // @ts-ignore
      } // @ts-ignore

      for (var i = 0, len = results.length; i < len; i++) {
        // @ts-ignore
        var result = results[i]; // @ts-ignore

        results[i] = Object.prototype.toString.call(result) === "[object Array]" ? [cleanDate(result[0]), cleanDate(result[1])] : cleanDate(result); // @ts-ignore
      } // @ts-ignore

      return results.length === 0 ? later.NEVER : count === 1 ? results[0] : results; // @ts-ignore
    } // @ts-ignore

    function cleanDate(d) {
      // @ts-ignore
      if (d instanceof Date && !isNaN(d.valueOf())) {
        // @ts-ignore
        return new Date(d); // @ts-ignore
      } // @ts-ignore

      return undefined; // @ts-ignore
    } // @ts-ignore

    function setNextStarts(dir, schedArr, startsArr, startDate) {
      // @ts-ignore
      for (var i = 0, len = schedArr.length; i < len; i++) {
        // @ts-ignore
        startsArr[i] = schedArr[i].start(dir, startDate); // @ts-ignore
      } // @ts-ignore

    } // @ts-ignore

    function updateNextStarts(dir, schedArr, startsArr, startDate) {
      // @ts-ignore
      var compare = compareFn(dir); // @ts-ignore

      for (var i = 0, len = schedArr.length; i < len; i++) {
        // @ts-ignore
        if (startsArr[i] && !compare(startsArr[i], startDate)) {
          // @ts-ignore
          startsArr[i] = schedArr[i].start(dir, startDate); // @ts-ignore
        } // @ts-ignore

      } // @ts-ignore

    } // @ts-ignore

    function setRangeStarts(dir, schedArr, rangesArr, startDate) {

      for (var i = 0, len = schedArr.length; i < len; i++) {
        // @ts-ignore
        var nextStart = schedArr[i].start(dir, startDate); // @ts-ignore

        if (!nextStart) {
          // @ts-ignore
          rangesArr[i] = later.NEVER; // @ts-ignore
        } else {
          // @ts-ignore
          rangesArr[i] = [nextStart, schedArr[i].end(dir, nextStart)]; // @ts-ignore
        } // @ts-ignore

      } // @ts-ignore

    } // @ts-ignore

    function updateRangeStarts(dir, schedArr, rangesArr, startDate) {
      // @ts-ignore
      var compare = compareFn(dir); // @ts-ignore

      for (var i = 0, len = schedArr.length; i < len; i++) {
        // @ts-ignore
        if (rangesArr[i] && !compare(rangesArr[i][0], startDate)) {
          // @ts-ignore
          var nextStart = schedArr[i].start(dir, startDate); // @ts-ignore

          if (!nextStart) {
            // @ts-ignore
            rangesArr[i] = later.NEVER; // @ts-ignore
          } else {
            // @ts-ignore
            rangesArr[i] = [nextStart, schedArr[i].end(dir, nextStart)]; // @ts-ignore
          } // @ts-ignore

        } // @ts-ignore

      } // @ts-ignore

    } // @ts-ignore

    function tickStarts(dir, schedArr, startsArr, startDate) {
      // @ts-ignore
      for (var i = 0, len = schedArr.length; i < len; i++) {
        // @ts-ignore
        if (startsArr[i] && startsArr[i].getTime() === startDate.getTime()) {
          // @ts-ignore
          startsArr[i] = schedArr[i].start(dir, schedArr[i].tick(dir, startDate)); // @ts-ignore
        } // @ts-ignore

      } // @ts-ignore

    } // @ts-ignore

    function getStart(schedArr, startsArr, startDate, minEndDate) {
      // @ts-ignore
      var result; // @ts-ignore

      for (var i = 0, len = startsArr.length; i < len; i++) {
        // @ts-ignore
        if (startsArr[i] && startsArr[i].getTime() === startDate.getTime()) {
          // @ts-ignore
          var start = schedArr[i].tickStart(startDate); // @ts-ignore

          if (minEndDate && start < minEndDate) {
            // @ts-ignore
            return minEndDate; // @ts-ignore
          } // @ts-ignore

          if (!result || start > result) {
            // @ts-ignore
            result = start; // @ts-ignore
          } // @ts-ignore

        } // @ts-ignore

      } // @ts-ignore

      return result; // @ts-ignore
    } // @ts-ignore

    function calcRangeOverlap(dir, rangesArr, startDate) {
      // @ts-ignore
      var compare = compareFn(dir),
          result; // @ts-ignore

      for (var i = 0, len = rangesArr.length; i < len; i++) {
        // @ts-ignore
        var range = rangesArr[i]; // @ts-ignore

        if (range && !compare(range[0], startDate) && (!range[1] || compare(range[1], startDate))) {
          // @ts-ignore
          if (!result || compare(range[1], result)) {
            // @ts-ignore
            result = range[1]; // @ts-ignore
          } // @ts-ignore

        } // @ts-ignore

      } // @ts-ignore

      return result; // @ts-ignore
    } // @ts-ignore

    function calcMaxEndDate(exceptsArr, compare) {
      // @ts-ignore
      var result; // @ts-ignore

      for (var i = 0, len = exceptsArr.length; i < len; i++) {
        // @ts-ignore
        if (exceptsArr[i] && (!result || compare(result, exceptsArr[i][0]))) {
          // @ts-ignore
          result = exceptsArr[i][0]; // @ts-ignore
        } // @ts-ignore

      } // @ts-ignore

      return result; // @ts-ignore
    } // @ts-ignore

    function calcEnd(dir, schedArr, startsArr, startDate, maxEndDate) {
      // @ts-ignore
      var compare = compareFn(dir),
          result; // @ts-ignore

      for (var i = 0, len = schedArr.length; i < len; i++) {
        // @ts-ignore
        var start = startsArr[i]; // @ts-ignore

        if (start && start.getTime() === startDate.getTime()) {
          // @ts-ignore
          var end = schedArr[i].end(dir, start); // @ts-ignore

          if (maxEndDate && (!end || compare(end, maxEndDate))) {
            // @ts-ignore
            return maxEndDate; // @ts-ignore
          } // @ts-ignore

          if (!result || compare(end, result)) {
            // @ts-ignore
            result = end; // @ts-ignore
          } // @ts-ignore

        } // @ts-ignore

      } // @ts-ignore

      return result; // @ts-ignore
    } // @ts-ignore

    function compareFn(dir) {
      // @ts-ignore
      return dir === "next" ? function (a, b) {
        // @ts-ignore
        return !b || a.getTime() > b.getTime(); // @ts-ignore
      } : function (a, b) {
        // @ts-ignore
        return !a || b.getTime() > a.getTime(); // @ts-ignore
      }; // @ts-ignore
    } // @ts-ignore

    function findNext(arr, compare) {
      // @ts-ignore
      var next = arr[0]; // @ts-ignore

      for (var i = 1, len = arr.length; i < len; i++) {
        // @ts-ignore
        if (arr[i] && compare(next, arr[i])) {
          // @ts-ignore
          next = arr[i]; // @ts-ignore
        } // @ts-ignore

      } // @ts-ignore

      return next; // @ts-ignore
    } // @ts-ignore

    return {
      // @ts-ignore
      isValid: function (d) {
        // @ts-ignore
        return getInstances("next", 1, d, d) !== later.NEVER; // @ts-ignore
      },
      // @ts-ignore
      next: function (count, startDate, endDate) {
        // @ts-ignore
        return getInstances("next", count || 1, startDate, endDate); // @ts-ignore
      },
      // @ts-ignore
      prev: function (count, startDate, endDate) {
        // @ts-ignore
        return getInstances("prev", count || 1, startDate, endDate); // @ts-ignore
      },
      // @ts-ignore
      nextRange: function (count, startDate, endDate) {
        // @ts-ignore
        return getInstances("next", count || 1, startDate, endDate, true); // @ts-ignore
      },
      // @ts-ignore
      prevRange: function (count, startDate, endDate) {
        // @ts-ignore
        return getInstances("prev", count || 1, startDate, endDate, true); // @ts-ignore
      } // @ts-ignore

    }; // @ts-ignore
  }; // @ts-ignore

  later.setTimeout = function (fn, sched) {
    // @ts-ignore
    var s = later.schedule(sched),
        t; // @ts-ignore

    if (fn) {
      // @ts-ignore
      scheduleTimeout(); // @ts-ignore
    } // @ts-ignore

    function scheduleTimeout() {
      // @ts-ignore
      var now = Date.now(),
          next = s.next(2, now); // @ts-ignore

      if (!next[0]) {
        // @ts-ignore
        t = undefined; // @ts-ignore

        return; // @ts-ignore
      } // @ts-ignore

      var diff = next[0].getTime() - now; // @ts-ignore

      if (diff < 1e3) {
        // @ts-ignore
        diff = next[1] ? next[1].getTime() - now : 1e3; // @ts-ignore
      } // @ts-ignore

      if (diff < 2147483647) {
        // @ts-ignore
        t = setTimeout(fn, diff); // @ts-ignore
      } else {
        // @ts-ignore
        t = setTimeout(scheduleTimeout, 2147483647); // @ts-ignore
      } // @ts-ignore

    } // @ts-ignore

    return {
      // @ts-ignore
      isDone: function () {
        // @ts-ignore
        return !t; // @ts-ignore
      },
      // @ts-ignore
      clear: function () {
        // @ts-ignore
        clearTimeout(t); // @ts-ignore
      } // @ts-ignore

    }; // @ts-ignore
  }; // @ts-ignore

  later.setInterval = function (fn, sched) {
    // @ts-ignore
    if (!fn) {
      // @ts-ignore
      return; // @ts-ignore
    } // @ts-ignore

    var t = later.setTimeout(scheduleTimeout, sched),
        done = t.isDone(); // @ts-ignore

    function scheduleTimeout() {
      // @ts-ignore
      if (!done) {
        // @ts-ignore
        fn(); // @ts-ignore

        t = later.setTimeout(scheduleTimeout, sched); // @ts-ignore
      } // @ts-ignore

    } // @ts-ignore

    return {
      // @ts-ignore
      isDone: function () {
        // @ts-ignore
        return t.isDone(); // @ts-ignore
      },
      // @ts-ignore
      clear: function () {
        // @ts-ignore
        done = true; // @ts-ignore

        t.clear(); // @ts-ignore
      } // @ts-ignore

    }; // @ts-ignore
  }; // @ts-ignore

  later.date = {}; // @ts-ignore

  later.date.timezone = function (useLocalTime) {
    // @ts-ignore
    later.date.build = useLocalTime ? function (Y, M, D, h, m, s) {
      // @ts-ignore
      return new Date(Y, M, D, h, m, s); // @ts-ignore
    } : function (Y, M, D, h, m, s) {
      // @ts-ignore
      return new Date(Date.UTC(Y, M, D, h, m, s)); // @ts-ignore
    }; // @ts-ignore

    var get = useLocalTime ? "get" : "getUTC",
        d = Date.prototype; // @ts-ignore

    later.date.getYear = d[get + "FullYear"]; // @ts-ignore

    later.date.getMonth = d[get + "Month"]; // @ts-ignore

    later.date.getDate = d[get + "Date"]; // @ts-ignore

    later.date.getDay = d[get + "Day"]; // @ts-ignore

    later.date.getHour = d[get + "Hours"]; // @ts-ignore

    later.date.getMin = d[get + "Minutes"]; // @ts-ignore

    later.date.getSec = d[get + "Seconds"]; // @ts-ignore

    later.date.isUTC = !useLocalTime; // @ts-ignore
  }; // @ts-ignore

  later.date.UTC = function () {
    // @ts-ignore
    later.date.timezone(false); // @ts-ignore
  }; // @ts-ignore

  later.date.localTime = function () {
    // @ts-ignore
    later.date.timezone(true); // @ts-ignore
  }; // @ts-ignore

  later.date.UTC(); // @ts-ignore

  later.SEC = 1e3; // @ts-ignore

  later.MIN = later.SEC * 60; // @ts-ignore

  later.HOUR = later.MIN * 60; // @ts-ignore

  later.DAY = later.HOUR * 24; // @ts-ignore

  later.WEEK = later.DAY * 7; // @ts-ignore

  later.DAYS_IN_MONTH = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31]; // @ts-ignore

  later.NEVER = 0; // @ts-ignore

  later.date.next = function (Y, M, D, h, m, s) {
    // @ts-ignore
    return later.date.build(Y, M !== undefined ? M - 1 : 0, D !== undefined ? D : 1, h || 0, m || 0, s || 0); // @ts-ignore
  }; // @ts-ignore

  later.date.nextRollover = function (d, val, constraint, period) {
    // @ts-ignore
    var cur = constraint.val(d),
        max = constraint.extent(d)[1]; // @ts-ignore

    return (val || max) <= cur || val > max ? new Date(period.end(d).getTime() + later.SEC) : period.start(d); // @ts-ignore
  }; // @ts-ignore

  later.date.prev = function (Y, M, D, h, m, s) {
    // @ts-ignore
    var len = arguments.length; // @ts-ignore

    M = len < 2 ? 11 : M - 1; // @ts-ignore

    D = len < 3 ? later.D.extent(later.date.next(Y, M + 1))[1] : D; // @ts-ignore

    h = len < 4 ? 23 : h; // @ts-ignore

    m = len < 5 ? 59 : m; // @ts-ignore

    s = len < 6 ? 59 : s; // @ts-ignore

    return later.date.build(Y, M, D, h, m, s); // @ts-ignore
  }; // @ts-ignore

  later.date.prevRollover = function (d, val, constraint, period) {
    // @ts-ignore
    var cur = constraint.val(d); // @ts-ignore

    return val >= cur || !val ? period.start(period.prev(d, period.val(d) - 1)) : period.start(d); // @ts-ignore
  }; // @ts-ignore

  later.parse = {}; // @ts-ignore

  later.parse.cron = function (expr, hasSeconds) {
    // @ts-ignore
    var NAMES = {
      // @ts-ignore
      JAN: 1,
      // @ts-ignore
      FEB: 2,
      // @ts-ignore
      MAR: 3,
      // @ts-ignore
      APR: 4,
      // @ts-ignore
      MAY: 5,
      // @ts-ignore
      JUN: 6,
      // @ts-ignore
      JUL: 7,
      // @ts-ignore
      AUG: 8,
      // @ts-ignore
      SEP: 9,
      // @ts-ignore
      OCT: 10,
      // @ts-ignore
      NOV: 11,
      // @ts-ignore
      DEC: 12,
      // @ts-ignore
      SUN: 1,
      // @ts-ignore
      MON: 2,
      // @ts-ignore
      TUE: 3,
      // @ts-ignore
      WED: 4,
      // @ts-ignore
      THU: 5,
      // @ts-ignore
      FRI: 6,
      // @ts-ignore
      SAT: 7 // @ts-ignore

    }; // @ts-ignore

    var REPLACEMENTS = {
      // @ts-ignore
      "* * * * * *": "0/1 * * * * *",
      // @ts-ignore
      "@YEARLY": "0 0 1 1 *",
      // @ts-ignore
      "@ANNUALLY": "0 0 1 1 *",
      // @ts-ignore
      "@MONTHLY": "0 0 1 * *",
      // @ts-ignore
      "@WEEKLY": "0 0 * * 0",
      // @ts-ignore
      "@DAILY": "0 0 * * *",
      // @ts-ignore
      "@HOURLY": "0 * * * *" // @ts-ignore

    }; // @ts-ignore

    var FIELDS = {
      // @ts-ignore
      s: [0, 0, 59],
      // @ts-ignore
      m: [1, 0, 59],
      // @ts-ignore
      h: [2, 0, 23],
      // @ts-ignore
      D: [3, 1, 31],
      // @ts-ignore
      M: [4, 1, 12],
      // @ts-ignore
      Y: [6, 1970, 2099],
      // @ts-ignore
      d: [5, 1, 7, 1] // @ts-ignore

    }; // @ts-ignore

    function getValue(value, offset, max) {
      // @ts-ignore
      return isNaN(value) ? NAMES[value] || null : Math.min(+value + (offset || 0), max || 9999); // @ts-ignore
    } // @ts-ignore

    function cloneSchedule(sched) {
      // @ts-ignore
      var clone = {},
          field; // @ts-ignore

      for (field in sched) {
        // @ts-ignore
        if (field !== "dc" && field !== "d") {
          // @ts-ignore
          clone[field] = sched[field].slice(0); // @ts-ignore
        } // @ts-ignore

      } // @ts-ignore

      return clone; // @ts-ignore
    } // @ts-ignore

    function add(sched, name, min, max, inc) {
      // @ts-ignore
      var i = min; // @ts-ignore

      if (!sched[name]) {
        // @ts-ignore
        sched[name] = []; // @ts-ignore
      } // @ts-ignore

      while (i <= max) {
        // @ts-ignore
        if (sched[name].indexOf(i) < 0) {
          // @ts-ignore
          sched[name].push(i); // @ts-ignore
        } // @ts-ignore

        i += inc || 1; // @ts-ignore
      } // @ts-ignore

      sched[name].sort(function (a, b) {
        // @ts-ignore
        return a - b; // @ts-ignore
      }); // @ts-ignore
    } // @ts-ignore

    function addHash(schedules, curSched, value, hash) {
      // @ts-ignore
      if (curSched.d && !curSched.dc || curSched.dc && curSched.dc.indexOf(hash) < 0) {
        // @ts-ignore
        schedules.push(cloneSchedule(curSched)); // @ts-ignore

        curSched = schedules[schedules.length - 1]; // @ts-ignore
      } // @ts-ignore

      add(curSched, "d", value, value); // @ts-ignore

      add(curSched, "dc", hash, hash); // @ts-ignore
    } // @ts-ignore

    function addWeekday(s, curSched, value) {
      // @ts-ignore
      var except1 = {},
          except2 = {}; // @ts-ignore

      if (value === 1) {
        // @ts-ignore
        add(curSched, "D", 1, 3); // @ts-ignore

        add(curSched, "d", NAMES.MON, NAMES.FRI); // @ts-ignore

        add(except1, "D", 2, 2); // @ts-ignore

        add(except1, "d", NAMES.TUE, NAMES.FRI); // @ts-ignore

        add(except2, "D", 3, 3); // @ts-ignore

        add(except2, "d", NAMES.TUE, NAMES.FRI); // @ts-ignore
      } else {
        // @ts-ignore
        add(curSched, "D", value - 1, value + 1); // @ts-ignore

        add(curSched, "d", NAMES.MON, NAMES.FRI); // @ts-ignore

        add(except1, "D", value - 1, value - 1); // @ts-ignore

        add(except1, "d", NAMES.MON, NAMES.THU); // @ts-ignore

        add(except2, "D", value + 1, value + 1); // @ts-ignore

        add(except2, "d", NAMES.TUE, NAMES.FRI); // @ts-ignore
      } // @ts-ignore

      s.exceptions.push(except1); // @ts-ignore

      s.exceptions.push(except2); // @ts-ignore
    } // @ts-ignore

    function addRange(item, curSched, name, min, max, offset) {
      // @ts-ignore
      var incSplit = item.split("/"),
          inc = +incSplit[1],
          range = incSplit[0]; // @ts-ignore

      if (range !== "*" && range !== "0") {
        // @ts-ignore
        var rangeSplit = range.split("-"); // @ts-ignore

        min = getValue(rangeSplit[0], offset, max); // @ts-ignore

        max = getValue(rangeSplit[1], offset, max) || max; // @ts-ignore
      } // @ts-ignore

      add(curSched, name, min, max, inc); // @ts-ignore
    } // @ts-ignore

    function parse(item, s, name, min, max, offset) {
      // @ts-ignore
      var value,
          split,
          schedules = s.schedules,
          curSched = schedules[schedules.length - 1]; // @ts-ignore

      if (item === "L") {
        // @ts-ignore
        item = min - 1; // @ts-ignore
      } // @ts-ignore

      if ((value = getValue(item, offset, max)) !== null) {
        // @ts-ignore
        add(curSched, name, value, value); // @ts-ignore
      } else if ((value = getValue(item.replace("W", ""), offset, max)) !== null) {
        // @ts-ignore
        addWeekday(s, curSched, value); // @ts-ignore
      } else if ((value = getValue(item.replace("L", ""), offset, max)) !== null) {
        // @ts-ignore
        addHash(schedules, curSched, value, min - 1); // @ts-ignore
      } else if ((split = item.split("#")).length === 2) {
        // @ts-ignore
        value = getValue(split[0], offset, max); // @ts-ignore

        addHash(schedules, curSched, value, getValue(split[1])); // @ts-ignore
      } else {
        // @ts-ignore
        addRange(item, curSched, name, min, max, offset); // @ts-ignore
      } // @ts-ignore

    } // @ts-ignore

    function isHash(item) {
      // @ts-ignore
      return item.indexOf("#") > -1 || item.indexOf("L") > 0; // @ts-ignore
    } // @ts-ignore

    function itemSorter(a, b) {
      // @ts-ignore
      return isHash(a) && !isHash(b) ? 1 : a - b; // @ts-ignore
    } // @ts-ignore

    function parseExpr(expr) {
      // @ts-ignore
      var schedule = {
        // @ts-ignore
        schedules: [{}],
        // @ts-ignore
        exceptions: [] // @ts-ignore

      },
          components = expr.replace(/(\s)+/g, " ").split(" "),
          field,
          f,
          component,
          items; // @ts-ignore

      for (field in FIELDS) {
        // @ts-ignore
        f = FIELDS[field]; // @ts-ignore

        component = components[f[0]]; // @ts-ignore

        if (component && component !== "*" && component !== "?") {
          // @ts-ignore
          items = component.split(",").sort(itemSorter); // @ts-ignore

          var i,
              length = items.length; // @ts-ignore

          for (i = 0; i < length; i++) {
            // @ts-ignore
            parse(items[i], schedule, field, f[1], f[2], f[3]); // @ts-ignore
          } // @ts-ignore

        } // @ts-ignore

      } // @ts-ignore

      return schedule; // @ts-ignore
    } // @ts-ignore

    function prepareExpr(expr) {
      // @ts-ignore
      var prepared = expr.toUpperCase(); // @ts-ignore

      return REPLACEMENTS[prepared] || prepared; // @ts-ignore
    } // @ts-ignore

    var e = prepareExpr(expr); // @ts-ignore

    return parseExpr(hasSeconds ? e : "0 " + e); // @ts-ignore
  }; // @ts-ignore

  later.parse.recur = function () {
    // @ts-ignore
    var schedules = [],
        exceptions = [],
        cur,
        curArr = schedules,
        curName,
        values,
        every,
        modifier,
        applyMin,
        applyMax,
        i,
        last; // @ts-ignore

    function add(name, min, max) {
      // @ts-ignore
      name = modifier ? name + "_" + modifier : name; // @ts-ignore

      if (!cur) {
        // @ts-ignore
        curArr.push({}); // @ts-ignore

        cur = curArr[0]; // @ts-ignore
      } // @ts-ignore

      if (!cur[name]) {
        // @ts-ignore
        cur[name] = []; // @ts-ignore
      } // @ts-ignore

      curName = cur[name]; // @ts-ignore

      if (every) {
        // @ts-ignore
        values = []; // @ts-ignore

        for (i = min; i <= max; i += every) {
          // @ts-ignore
          values.push(i); // @ts-ignore
        } // @ts-ignore

        last = {
          // @ts-ignore
          n: name,
          // @ts-ignore
          x: every,
          // @ts-ignore
          c: curName.length,
          // @ts-ignore
          m: max // @ts-ignore

        }; // @ts-ignore
      } // @ts-ignore

      values = applyMin ? [min] : applyMax ? [max] : values; // @ts-ignore

      var length = values.length; // @ts-ignore

      for (i = 0; i < length; i += 1) {
        // @ts-ignore
        var val = values[i]; // @ts-ignore

        if (curName.indexOf(val) < 0) {
          // @ts-ignore
          curName.push(val); // @ts-ignore
        } // @ts-ignore

      } // @ts-ignore

      values = every = modifier = applyMin = applyMax = 0; // @ts-ignore
    } // @ts-ignore

    return {
      // @ts-ignore
      schedules: schedules,
      // @ts-ignore
      exceptions: exceptions,
      // @ts-ignore
      on: function () {
        // @ts-ignore
        values = arguments[0] instanceof Array ? arguments[0] : arguments; // @ts-ignore

        return this; // @ts-ignore
      },
      // @ts-ignore
      every: function (x) {
        // @ts-ignore
        every = x || 1; // @ts-ignore

        return this; // @ts-ignore
      },
      // @ts-ignore
      after: function (x) {
        // @ts-ignore
        modifier = "a"; // @ts-ignore

        values = [x]; // @ts-ignore

        return this; // @ts-ignore
      },
      // @ts-ignore
      before: function (x) {
        // @ts-ignore
        modifier = "b"; // @ts-ignore

        values = [x]; // @ts-ignore

        return this; // @ts-ignore
      },
      // @ts-ignore
      first: function () {
        // @ts-ignore
        applyMin = 1; // @ts-ignore

        return this; // @ts-ignore
      },
      // @ts-ignore
      last: function () {
        // @ts-ignore
        applyMax = 1; // @ts-ignore

        return this; // @ts-ignore
      },
      // @ts-ignore
      time: function () {
        // @ts-ignore
        for (var i = 0, len = values.length; i < len; i++) {
          // @ts-ignore
          var split = values[i].split(":"); // @ts-ignore

          if (split.length < 3) split.push(0); // @ts-ignore

          values[i] = +split[0] * 3600 + +split[1] * 60 + +split[2]; // @ts-ignore
        } // @ts-ignore

        add("t"); // @ts-ignore

        return this; // @ts-ignore
      },
      // @ts-ignore
      second: function () {
        // @ts-ignore
        add("s", 0, 59); // @ts-ignore

        return this; // @ts-ignore
      },
      // @ts-ignore
      minute: function () {
        // @ts-ignore
        add("m", 0, 59); // @ts-ignore

        return this; // @ts-ignore
      },
      // @ts-ignore
      hour: function () {
        // @ts-ignore
        add("h", 0, 23); // @ts-ignore

        return this; // @ts-ignore
      },
      // @ts-ignore
      dayOfMonth: function () {
        // @ts-ignore
        add("D", 1, applyMax ? 0 : 31); // @ts-ignore

        return this; // @ts-ignore
      },
      // @ts-ignore
      dayOfWeek: function () {
        // @ts-ignore
        add("d", 1, 7); // @ts-ignore

        return this; // @ts-ignore
      },
      // @ts-ignore
      onWeekend: function () {
        // @ts-ignore
        values = [1, 7]; // @ts-ignore

        return this.dayOfWeek(); // @ts-ignore
      },
      // @ts-ignore
      onWeekday: function () {
        // @ts-ignore
        values = [2, 3, 4, 5, 6]; // @ts-ignore

        return this.dayOfWeek(); // @ts-ignore
      },
      // @ts-ignore
      dayOfWeekCount: function () {
        // @ts-ignore
        add("dc", 1, applyMax ? 0 : 5); // @ts-ignore

        return this; // @ts-ignore
      },
      // @ts-ignore
      dayOfYear: function () {
        // @ts-ignore
        add("dy", 1, applyMax ? 0 : 366); // @ts-ignore

        return this; // @ts-ignore
      },
      // @ts-ignore
      weekOfMonth: function () {
        // @ts-ignore
        add("wm", 1, applyMax ? 0 : 5); // @ts-ignore

        return this; // @ts-ignore
      },
      // @ts-ignore
      weekOfYear: function () {
        // @ts-ignore
        add("wy", 1, applyMax ? 0 : 53); // @ts-ignore

        return this; // @ts-ignore
      },
      // @ts-ignore
      month: function () {
        // @ts-ignore
        add("M", 1, 12); // @ts-ignore

        return this; // @ts-ignore
      },
      // @ts-ignore
      year: function () {
        // @ts-ignore
        add("Y", 1970, 2450); // @ts-ignore

        return this; // @ts-ignore
      },
      // @ts-ignore
      fullDate: function () {
        // @ts-ignore
        for (var i = 0, len = values.length; i < len; i++) {
          // @ts-ignore
          values[i] = values[i].getTime(); // @ts-ignore
        } // @ts-ignore

        add("fd"); // @ts-ignore

        return this; // @ts-ignore
      },
      // @ts-ignore
      customModifier: function (id, vals) {
        // @ts-ignore
        var custom = later.modifier[id]; // @ts-ignore

        if (!custom) throw new Error("Custom modifier " + id + " not recognized!"); // @ts-ignore

        modifier = id; // @ts-ignore

        values = arguments[1] instanceof Array ? arguments[1] : [arguments[1]]; // @ts-ignore

        return this; // @ts-ignore
      },
      // @ts-ignore
      customPeriod: function (id) {
        // @ts-ignore
        var custom = later[id]; // @ts-ignore

        if (!custom) throw new Error("Custom time period " + id + " not recognized!"); // @ts-ignore

        add(id, custom.extent(new Date())[0], custom.extent(new Date())[1]); // @ts-ignore

        return this; // @ts-ignore
      },
      // @ts-ignore
      startingOn: function (start) {
        // @ts-ignore
        return this.between(start, last.m); // @ts-ignore
      },
      // @ts-ignore
      between: function (start, end) {
        // @ts-ignore
        cur[last.n] = cur[last.n].splice(0, last.c); // @ts-ignore

        every = last.x; // @ts-ignore

        add(last.n, start, end); // @ts-ignore

        return this; // @ts-ignore
      },
      // @ts-ignore
      and: function () {
        // @ts-ignore
        cur = curArr[curArr.push({}) - 1]; // @ts-ignore

        return this; // @ts-ignore
      },
      // @ts-ignore
      except: function () {
        // @ts-ignore
        curArr = exceptions; // @ts-ignore

        cur = null; // @ts-ignore

        return this; // @ts-ignore
      } // @ts-ignore

    }; // @ts-ignore
  }; // @ts-ignore

  later.parse.text = function (str) {
    // @ts-ignore
    var recur = later.parse.recur,
        pos = 0,
        input = "",
        error; // @ts-ignore

    var TOKENTYPES = {
      // @ts-ignore
      eof: /^$/,
      // @ts-ignore
      rank: /^((\d\d\d\d)|([2-5]?1(st)?|[2-5]?2(nd)?|[2-5]?3(rd)?|(0|[1-5]?[4-9]|[1-5]0|1[1-3])(th)?))\b/,
      // @ts-ignore
      time: /^((([0]?[1-9]|1[0-2]):[0-5]\d(\s)?(am|pm))|(([0]?\d|1\d|2[0-3]):[0-5]\d))\b/,
      // @ts-ignore
      dayName: /^((sun|mon|tue(s)?|wed(nes)?|thu(r(s)?)?|fri|sat(ur)?)(day)?)\b/,
      // @ts-ignore
      monthName: /^(jan(uary)?|feb(ruary)?|ma((r(ch)?)?|y)|apr(il)?|ju(ly|ne)|aug(ust)?|oct(ober)?|(sept|nov|dec)(ember)?)\b/,
      // @ts-ignore
      yearIndex: /^(\d\d\d\d)\b/,
      // @ts-ignore
      every: /^every\b/,
      // @ts-ignore
      after: /^after\b/,
      // @ts-ignore
      before: /^before\b/,
      // @ts-ignore
      second: /^(s|sec(ond)?(s)?)\b/,
      // @ts-ignore
      minute: /^(m|min(ute)?(s)?)\b/,
      // @ts-ignore
      hour: /^(h|hour(s)?)\b/,
      // @ts-ignore
      day: /^(day(s)?( of the month)?)\b/,
      // @ts-ignore
      dayInstance: /^day instance\b/,
      // @ts-ignore
      dayOfWeek: /^day(s)? of the week\b/,
      // @ts-ignore
      dayOfYear: /^day(s)? of the year\b/,
      // @ts-ignore
      weekOfYear: /^week(s)?( of the year)?\b/,
      // @ts-ignore
      weekOfMonth: /^week(s)? of the month\b/,
      // @ts-ignore
      weekday: /^weekday\b/,
      // @ts-ignore
      weekend: /^weekend\b/,
      // @ts-ignore
      month: /^month(s)?\b/,
      // @ts-ignore
      year: /^year(s)?\b/,
      // @ts-ignore
      between: /^between (the)?\b/,
      // @ts-ignore
      start: /^(start(ing)? (at|on( the)?)?)\b/,
      // @ts-ignore
      at: /^(at|@)\b/,
      // @ts-ignore
      and: /^(,|and\b)/,
      // @ts-ignore
      except: /^(except\b)/,
      // @ts-ignore
      also: /(also)\b/,
      // @ts-ignore
      first: /^(first)\b/,
      // @ts-ignore
      last: /^last\b/,
      // @ts-ignore
      "in": /^in\b/,
      // @ts-ignore
      of: /^of\b/,
      // @ts-ignore
      onthe: /^on the\b/,
      // @ts-ignore
      on: /^on\b/,
      // @ts-ignore
      through: /(-|^(to|through)\b)/ // @ts-ignore

    }; // @ts-ignore

    var NAMES = {
      // @ts-ignore
      jan: 1,
      // @ts-ignore
      feb: 2,
      // @ts-ignore
      mar: 3,
      // @ts-ignore
      apr: 4,
      // @ts-ignore
      may: 5,
      // @ts-ignore
      jun: 6,
      // @ts-ignore
      jul: 7,
      // @ts-ignore
      aug: 8,
      // @ts-ignore
      sep: 9,
      // @ts-ignore
      oct: 10,
      // @ts-ignore
      nov: 11,
      // @ts-ignore
      dec: 12,
      // @ts-ignore
      sun: 1,
      // @ts-ignore
      mon: 2,
      // @ts-ignore
      tue: 3,
      // @ts-ignore
      wed: 4,
      // @ts-ignore
      thu: 5,
      // @ts-ignore
      fri: 6,
      // @ts-ignore
      sat: 7,
      // @ts-ignore
      "1st": 1,
      // @ts-ignore
      fir: 1,
      // @ts-ignore
      "2nd": 2,
      // @ts-ignore
      sec: 2,
      // @ts-ignore
      "3rd": 3,
      // @ts-ignore
      thi: 3,
      // @ts-ignore
      "4th": 4,
      // @ts-ignore
      "for": 4 // @ts-ignore

    }; // @ts-ignore

    function t(start, end, text, type) {
      // @ts-ignore
      return {
        // @ts-ignore
        startPos: start,
        // @ts-ignore
        endPos: end,
        // @ts-ignore
        text: text,
        // @ts-ignore
        type: type // @ts-ignore

      }; // @ts-ignore
    } // @ts-ignore

    function peek(expected) {
      // @ts-ignore
      var scanTokens = expected instanceof Array ? expected : [expected],
          whiteSpace = /\s+/,
          token,
          curInput,
          m,
          scanToken,
          start,
          len; // @ts-ignore

      scanTokens.push(whiteSpace); // @ts-ignore

      start = pos; // @ts-ignore

      while (!token || token.type === whiteSpace) {
        // @ts-ignore
        len = -1; // @ts-ignore

        curInput = input.substring(start); // @ts-ignore

        token = t(start, start, input.split(whiteSpace)[0]); // @ts-ignore

        var i,
            length = scanTokens.length; // @ts-ignore

        for (i = 0; i < length; i++) {
          // @ts-ignore
          scanToken = scanTokens[i]; // @ts-ignore

          m = scanToken.exec(curInput); // @ts-ignore

          if (m && m.index === 0 && m[0].length > len) {
            // @ts-ignore
            len = m[0].length; // @ts-ignore

            token = t(start, start + len, curInput.substring(0, len), scanToken); // @ts-ignore
          } // @ts-ignore

        } // @ts-ignore

        if (token.type === whiteSpace) {
          // @ts-ignore
          start = token.endPos; // @ts-ignore
        } // @ts-ignore

      } // @ts-ignore

      return token; // @ts-ignore
    } // @ts-ignore

    function scan(expectedToken) {
      // @ts-ignore
      var token = peek(expectedToken); // @ts-ignore

      pos = token.endPos; // @ts-ignore

      return token; // @ts-ignore
    } // @ts-ignore

    function parseThroughExpr(tokenType) {
      // @ts-ignore
      var start = +parseTokenValue(tokenType),
          end = checkAndParse(TOKENTYPES.through) ? +parseTokenValue(tokenType) : start,
          nums = []; // @ts-ignore

      for (var i = start; i <= end; i++) {
        // @ts-ignore
        nums.push(i); // @ts-ignore
      } // @ts-ignore

      return nums; // @ts-ignore
    } // @ts-ignore

    function parseRanges(tokenType) {
      // @ts-ignore
      var nums = parseThroughExpr(tokenType); // @ts-ignore

      while (checkAndParse(TOKENTYPES.and)) {
        // @ts-ignore
        nums = nums.concat(parseThroughExpr(tokenType)); // @ts-ignore
      } // @ts-ignore

      return nums; // @ts-ignore
    } // @ts-ignore

    function parseEvery(r) {
      // @ts-ignore
      var num, period, start, end; // @ts-ignore

      if (checkAndParse(TOKENTYPES.weekend)) {
        // @ts-ignore
        r.on(NAMES.sun, NAMES.sat).dayOfWeek(); // @ts-ignore
      } else if (checkAndParse(TOKENTYPES.weekday)) {
        // @ts-ignore
        r.on(NAMES.mon, NAMES.tue, NAMES.wed, NAMES.thu, NAMES.fri).dayOfWeek(); // @ts-ignore
      } else {
        // @ts-ignore
        num = parseTokenValue(TOKENTYPES.rank); // @ts-ignore

        r.every(num); // @ts-ignore

        period = parseTimePeriod(r); // @ts-ignore

        if (checkAndParse(TOKENTYPES.start)) {
          // @ts-ignore
          num = parseTokenValue(TOKENTYPES.rank); // @ts-ignore

          r.startingOn(num); // @ts-ignore

          parseToken(period.type); // @ts-ignore
        } else if (checkAndParse(TOKENTYPES.between)) {
          // @ts-ignore
          start = parseTokenValue(TOKENTYPES.rank); // @ts-ignore

          if (checkAndParse(TOKENTYPES.and)) {
            // @ts-ignore
            end = parseTokenValue(TOKENTYPES.rank); // @ts-ignore

            r.between(start, end); // @ts-ignore
          } // @ts-ignore

        } // @ts-ignore

      } // @ts-ignore

    } // @ts-ignore

    function parseOnThe(r) {
      // @ts-ignore
      if (checkAndParse(TOKENTYPES.first)) {
        // @ts-ignore
        r.first(); // @ts-ignore
      } else if (checkAndParse(TOKENTYPES.last)) {
        // @ts-ignore
        r.last(); // @ts-ignore
      } else {
        // @ts-ignore
        r.on(parseRanges(TOKENTYPES.rank)); // @ts-ignore
      } // @ts-ignore

      parseTimePeriod(r); // @ts-ignore
    } // @ts-ignore

    function parseScheduleExpr(str) {
      // @ts-ignore
      pos = 0; // @ts-ignore

      input = str; // @ts-ignore

      error = -1; // @ts-ignore

      var r = recur(); // @ts-ignore

      while (pos < input.length && error < 0) {
        // @ts-ignore
        var token = parseToken([TOKENTYPES.every, TOKENTYPES.after, TOKENTYPES.before, TOKENTYPES.onthe, TOKENTYPES.on, TOKENTYPES.of, TOKENTYPES["in"], TOKENTYPES.at, TOKENTYPES.and, TOKENTYPES.except, TOKENTYPES.also]); // @ts-ignore

        switch (token.type) {
          // @ts-ignore
          case TOKENTYPES.every:
            // @ts-ignore
            parseEvery(r); // @ts-ignore

            break;
          // @ts-ignore
          // @ts-ignore

          case TOKENTYPES.after:
            // @ts-ignore
            if (peek(TOKENTYPES.time).type !== undefined) {
              // @ts-ignore
              r.after(parseTokenValue(TOKENTYPES.time)); // @ts-ignore

              r.time(); // @ts-ignore
            } else {
              // @ts-ignore
              r.after(parseTokenValue(TOKENTYPES.rank)); // @ts-ignore

              parseTimePeriod(r); // @ts-ignore
            } // @ts-ignore

            break;
          // @ts-ignore
          // @ts-ignore

          case TOKENTYPES.before:
            // @ts-ignore
            if (peek(TOKENTYPES.time).type !== undefined) {
              // @ts-ignore
              r.before(parseTokenValue(TOKENTYPES.time)); // @ts-ignore

              r.time(); // @ts-ignore
            } else {
              // @ts-ignore
              r.before(parseTokenValue(TOKENTYPES.rank)); // @ts-ignore

              parseTimePeriod(r); // @ts-ignore
            } // @ts-ignore

            break;
          // @ts-ignore
          // @ts-ignore

          case TOKENTYPES.onthe:
            // @ts-ignore
            parseOnThe(r); // @ts-ignore

            break;
          // @ts-ignore
          // @ts-ignore

          case TOKENTYPES.on:
            // @ts-ignore
            r.on(parseRanges(TOKENTYPES.dayName)).dayOfWeek(); // @ts-ignore

            break;
          // @ts-ignore
          // @ts-ignore

          case TOKENTYPES.of:
            // @ts-ignore
            r.on(parseRanges(TOKENTYPES.monthName)).month(); // @ts-ignore

            break;
          // @ts-ignore
          // @ts-ignore

          case TOKENTYPES["in"]:
            // @ts-ignore
            r.on(parseRanges(TOKENTYPES.yearIndex)).year(); // @ts-ignore

            break;
          // @ts-ignore
          // @ts-ignore

          case TOKENTYPES.at:
            // @ts-ignore
            r.on(parseTokenValue(TOKENTYPES.time)).time(); // @ts-ignore

            while (checkAndParse(TOKENTYPES.and)) {
              // @ts-ignore
              r.on(parseTokenValue(TOKENTYPES.time)).time(); // @ts-ignore
            } // @ts-ignore

            break;
          // @ts-ignore

          case TOKENTYPES.and:
            // @ts-ignore
            break;
          // @ts-ignore

          case TOKENTYPES.also:
            // @ts-ignore
            r.and(); // @ts-ignore

            break;
          // @ts-ignore

          case TOKENTYPES.except:
            // @ts-ignore
            r.except(); // @ts-ignore

            break;
          // @ts-ignore

          default:
            // @ts-ignore
            error = pos;
          // @ts-ignore
        } // @ts-ignore

      } // @ts-ignore

      return {
        // @ts-ignore
        schedules: r.schedules,
        // @ts-ignore
        exceptions: r.exceptions,
        // @ts-ignore
        error: error // @ts-ignore

      }; // @ts-ignore
    } // @ts-ignore

    function parseTimePeriod(r) {
      // @ts-ignore
      var timePeriod = parseToken([TOKENTYPES.second, TOKENTYPES.minute, TOKENTYPES.hour, TOKENTYPES.dayOfYear, TOKENTYPES.dayOfWeek, TOKENTYPES.dayInstance, TOKENTYPES.day, TOKENTYPES.month, TOKENTYPES.year, TOKENTYPES.weekOfMonth, TOKENTYPES.weekOfYear]); // @ts-ignore

      switch (timePeriod.type) {
        // @ts-ignore
        case TOKENTYPES.second:
          // @ts-ignore
          r.second(); // @ts-ignore

          break;
        // @ts-ignore
        // @ts-ignore

        case TOKENTYPES.minute:
          // @ts-ignore
          r.minute(); // @ts-ignore

          break;
        // @ts-ignore
        // @ts-ignore

        case TOKENTYPES.hour:
          // @ts-ignore
          r.hour(); // @ts-ignore

          break;
        // @ts-ignore
        // @ts-ignore

        case TOKENTYPES.dayOfYear:
          // @ts-ignore
          r.dayOfYear(); // @ts-ignore

          break;
        // @ts-ignore
        // @ts-ignore

        case TOKENTYPES.dayOfWeek:
          // @ts-ignore
          r.dayOfWeek(); // @ts-ignore

          break;
        // @ts-ignore
        // @ts-ignore

        case TOKENTYPES.dayInstance:
          // @ts-ignore
          r.dayOfWeekCount(); // @ts-ignore

          break;
        // @ts-ignore
        // @ts-ignore

        case TOKENTYPES.day:
          // @ts-ignore
          r.dayOfMonth(); // @ts-ignore

          break;
        // @ts-ignore
        // @ts-ignore

        case TOKENTYPES.weekOfMonth:
          // @ts-ignore
          r.weekOfMonth(); // @ts-ignore

          break;
        // @ts-ignore
        // @ts-ignore

        case TOKENTYPES.weekOfYear:
          // @ts-ignore
          r.weekOfYear(); // @ts-ignore

          break;
        // @ts-ignore
        // @ts-ignore

        case TOKENTYPES.month:
          // @ts-ignore
          r.month(); // @ts-ignore

          break;
        // @ts-ignore
        // @ts-ignore

        case TOKENTYPES.year:
          // @ts-ignore
          r.year(); // @ts-ignore

          break;
        // @ts-ignore
        // @ts-ignore

        default:
          // @ts-ignore
          error = pos;
        // @ts-ignore
      } // @ts-ignore

      return timePeriod; // @ts-ignore
    } // @ts-ignore

    function checkAndParse(tokenType) {
      // @ts-ignore
      var found = peek(tokenType).type === tokenType; // @ts-ignore

      if (found) {
        // @ts-ignore
        scan(tokenType); // @ts-ignore
      } // @ts-ignore

      return found; // @ts-ignore
    } // @ts-ignore

    function parseToken(tokenType) {
      // @ts-ignore
      var t = scan(tokenType); // @ts-ignore

      if (t.type) {
        // @ts-ignore
        t.text = convertString(t.text, tokenType); // @ts-ignore
      } else {
        // @ts-ignore
        error = pos; // @ts-ignore
      } // @ts-ignore

      return t; // @ts-ignore
    } // @ts-ignore

    function parseTokenValue(tokenType) {
      // @ts-ignore
      return parseToken(tokenType).text; // @ts-ignore
    } // @ts-ignore

    function convertString(str, tokenType) {
      // @ts-ignore
      var output = str; // @ts-ignore

      switch (tokenType) {
        // @ts-ignore
        case TOKENTYPES.time:
          // @ts-ignore
          var parts = str.split(/(:|am|pm)/),
              hour = parts[3] === "pm" && parts[0] < 12 ? parseInt(parts[0], 10) + 12 : parts[0],
              min = parts[2].trim(); // @ts-ignore

          output = (hour.length === 1 ? "0" : "") + hour + ":" + min; // @ts-ignore

          break;
        // @ts-ignore
        // @ts-ignore

        case TOKENTYPES.rank:
          // @ts-ignore
          output = parseInt(/^\d+/.exec(str)[0], 10); // @ts-ignore

          break;
        // @ts-ignore
        // @ts-ignore

        case TOKENTYPES.monthName: // @ts-ignore

        case TOKENTYPES.dayName:
          // @ts-ignore
          output = NAMES[str.substring(0, 3)]; // @ts-ignore

          break;
        // @ts-ignore
      } // @ts-ignore

      return output; // @ts-ignore
    } // @ts-ignore

    return parseScheduleExpr(str.toLowerCase()); // @ts-ignore
  }; // @ts-ignore

  return later; // @ts-ignore
}(); // Set the local time mode for "later" library
// @ts-ignore

later.date.localTime(); // @ts-ignore

class CalendarIntervalMixin extends Mixin([AbstractPartOfProjectModelMixin], base => {
  const superProto = base.prototype;

  class CalendarIntervalMixin extends base {
    static get fields() {
      return ['name', {
        name: 'startDate',
        type: 'date',
        format: 'YYYY-MM-DDTHH:mm:ssZ'
      }, {
        name: 'endDate',
        type: 'date',
        format: 'YYYY-MM-DDTHH:mm:ssZ'
      }, 'recurrentStartDate', 'recurrentEndDate', {
        name: 'isWorking',
        type: 'boolean',
        defaultValue: false
      }, {
        name: 'priority',
        type: 'number'
      }];
    }

    getCalendar() {
      return this.stores[0].calendar;
    }

    resetPriority() {
      this.priorityField = null;
      this.getCalendar().getDepth();
    }

    getPriorityField() {
      if (this.priorityField != null) return this.priorityField;
      let base = 10000 + this.getCalendar().getDepth() * 100;
      let priority = this.priority;

      if (priority == null) {
        priority = this.isRecurrent() ? 20 : 30;
      }

      return this.priorityField = base + priority;
    }

    isRecurrent() {
      return Boolean(this.recurrentStartDate && this.recurrentEndDate && this.getStartDateSchedule() && this.getEndDateSchedule());
    }

    isStatic() {
      return Boolean(this.startDate && this.endDate);
    }

    parseDateSchedule(schedule) {
      if (schedule && schedule !== Object(schedule)) {
        schedule = later.parse.text(schedule);

        if (schedule !== Object(schedule) || schedule.error > 0) {
          try {
            schedule = JSON.parse(schedule);
          } catch (e) {
            return null;
          }
        }
      }

      return schedule;
    }

    getStartDateSchedule() {
      if (this.startDateSchedule) return this.startDateSchedule;
      const schedule = this.parseDateSchedule(this.recurrentStartDate);
      return this.startDateSchedule = later.schedule(schedule);
    }

    getEndDateSchedule() {
      if (this.endDateSchedule) return this.endDateSchedule;
      const schedule = this.parseDateSchedule(this.recurrentEndDate);
      return this.endDateSchedule = later.schedule(schedule);
    }

  }

  return CalendarIntervalMixin;
}) {}

class CalendarIntervalStore extends Mixin([AbstractPartOfProjectStoreMixin], base => {
  const superProto = base.prototype;

  class CalendarIntervalStore extends base {
    static get defaultConfig() {
      return {
        modelClass: CalendarIntervalMixin
      };
    }

  }

  return CalendarIntervalStore;
}) {}

class CalendarCacheInterval {
  constructor(config) {
    this.intervals = [];
    config && Object.assign(this, config);
    if (!this.calendar) throw new Error("Required attribute `calendar` is missing");
  }

  includeInterval(interval) {
    if (this.intervals.indexOf(interval) == -1) {
      const copy = this.intervals.slice();
      copy.push(interval);
      return new CalendarCacheInterval({
        intervals: copy,
        calendar: this.calendar
      });
    } else return this;
  }

  combineWith(interval) {
    return new CalendarCacheInterval({
      intervals: this.intervals.concat(interval.intervals),
      calendar: this.calendar
    });
  }

  getIsWorking() {
    if (this.isWorking != null) return this.isWorking;
    const intervals = this.intervals = this.normalizeIntervals(this.intervals);
    return this.isWorking = intervals[0].isWorking;
  }

  normalizeIntervals(intervals) {
    const filtered = stripDuplicates(intervals);
    filtered.sort((interval1, interval2) => interval2.getPriorityField() - interval1.getPriorityField());
    return filtered;
  }

}

class CalendarCacheSingle extends CalendarCache {
  constructor(config) {
    super(config);
    this.staticIntervalsCached = false;
    if (!this.unspecifiedTimeInterval) throw new Error("Required attribute `unspecifiedTimeInterval` is missing");
    this.intervalCache = new IntervalCache({
      emptyInterval: new CalendarCacheInterval({
        intervals: [this.unspecifiedTimeInterval],
        calendar: this.calendar
      }),
      combineIntervalsFn: (interval1, interval2) => {
        return interval1.combineWith(interval2);
      }
    });
  }

  fillCache(startDate, endDate) {
    if (!this.staticIntervalsCached) {
      this.cacheStaticIntervals();
      this.staticIntervalsCached = true;
    }

    if (this.parentCache) this.includeWrappingRangeFrom(this.parentCache, startDate, endDate);
    const startDateN = startDate.getTime();
    const endDateN = endDate.getTime();
    if (startDateN > endDateN) throw new Error("Invalid cache fill interval");
    this.forEachRecurrentInterval(interval => {
      const startSchedule = interval.getStartDateSchedule();
      const endSchedule = interval.getEndDateSchedule();
      let wrappingStartDate = startSchedule.prev(1, startDate);
      let wrappingEndDate = endSchedule.next(1, endDate);

      if (wrappingStartDate !== later.NEVER && wrappingStartDate.getTime() === startDateN) {
        const wrappingStartDates = startSchedule.prev(2, startDate);
        if (wrappingStartDates !== later.NEVER && wrappingStartDates.length === 2) wrappingStartDate = wrappingStartDates[1];
      }

      if (wrappingEndDate !== later.NEVER && wrappingEndDate.getTime() === endDateN) {
        const wrappingEndDates = endSchedule.next(2, endDate);
        if (wrappingEndDates !== later.NEVER && wrappingEndDates.length === 2) wrappingEndDate = wrappingEndDates[1];
      }

      const startDates = startSchedule.next(Infinity, wrappingStartDate !== later.NEVER ? wrappingStartDate : startDate, wrappingEndDate !== later.NEVER ? new Date(wrappingEndDate.getTime() - 1) : endDate);
      if (startDates === later.NEVER) return;
      const endDates = endSchedule.next(Infinity, new Date(startDates[0].getTime() + 1), wrappingEndDate !== later.NEVER ? wrappingEndDate : endDate);

      if (endDates.length > startDates.length) {
        endDates.length = startDates.length;
      } else if (endDates.length < startDates.length) {
        startDates.length = endDates.length;
      }

      startDates.forEach((startDate, index) => {
        const recStartDate = startDate;
        const recEndDate = endDates[index];
        this.intervalCache.addInterval(recStartDate, recEndDate, existingCacheInterval => existingCacheInterval.includeInterval(interval));
      });
    });
  }

  clear() {
    this.staticIntervalsCached = false;
    super.clear();
  }

  cacheStaticIntervals() {
    this.forEachStaticInterval(interval => {
      this.intervalCache.addInterval(interval.startDate, interval.endDate, existingCacheInterval => existingCacheInterval.includeInterval(interval));
    });
  }

  forEachStaticInterval(func) {
    this.intervalStore.forEach(interval => {
      if (interval.isStatic()) func(interval);
    });
  }

  forEachRecurrentInterval(func) {
    this.intervalStore.forEach(interval => {
      if (interval.isRecurrent()) func(interval);
    });
  }

}

class UnspecifiedTimeIntervalModel extends Mixin([CalendarIntervalMixin], base => {
  const superProto = base.prototype;

  class UnspecifiedTimeIntervalModel extends base {
    getCalendar() {
      return this.calendar;
    }

    getPriorityField() {
      if (this.priorityField != null) return this.priorityField;
      return this.priorityField = this.getCalendar().getDepth();
    }

  }

  return UnspecifiedTimeIntervalModel;
}) {}

class AbstractCalendarMixin extends Mixin([AbstractPartOfProjectModelMixin], base => {
  const superProto = base.prototype;

  class CalendarMixin extends base {
    constructor() {
      super(...arguments);
      this.version = 1;
    }

    static get fields() {
      return [{
        name: 'version',
        type: 'number'
      }, 'name', {
        name: 'unspecifiedTimeIsWorking',
        type: 'boolean',
        defaultValue: true
      }, 'intervals'];
    }

    get intervalStoreClass() {
      return CalendarIntervalStore;
    }

    afterConstruct() {
      superProto.afterConstruct.call(this);
      const modelClass = this.getDefaultConfiguration().calendarIntervalModelClass || this.intervalStoreClass.defaultConfig.modelClass;
      this.intervalStore = new this.intervalStoreClass({
        calendar: this,
        modelClass
      });

      if (this.intervals && this.intervals.length) {
        this.addIntervals(this.intervals);
      }
    }

    isDefault() {
      const project = this.getProject();

      if (project) {
        return this === project.defaultCalendar;
      }

      return false;
    }

    getDepth() {
      return this.childLevel + 1;
    }

    forEachAvailabilityInterval(options, func, scope) {
      return this.calendarCache.forEachAvailabilityInterval(options, func, scope);
    }

    accumulateWorkingTime(date, durationMs, isForward) {
      if (durationMs === 0) return {
        finalDate: new Date(date),
        remainingDurationInMs: 0
      };
      if (isNaN(durationMs)) throw new Error("Invalid duration");
      let finalDate = date;
      this.forEachAvailabilityInterval(isForward ? {
        startDate: date,
        isForward: true
      } : {
        endDate: date,
        isForward: false
      }, (intervalStartDate, intervalEndDate, calendarCacheInterval) => {
        let result = true;

        if (calendarCacheInterval.getIsWorking()) {
          const dstDiff = intervalStartDate.getTimezoneOffset() - intervalEndDate.getTimezoneOffset();
          const diff = intervalEndDate.getTime() - intervalStartDate.getTime() + dstDiff * 60 * 1000;

          if (durationMs <= diff) {
            finalDate = isForward ? new Date(intervalStartDate.getTime() + durationMs) : new Date(intervalEndDate.getTime() - durationMs);
            durationMs = 0;
            result = false;
          } else {
            finalDate = isForward ? intervalEndDate : intervalStartDate;
            durationMs -= diff;
          }
        }

        return result;
      });
      return {
        finalDate: new Date(finalDate),
        remainingDurationInMs: durationMs
      };
    }

    calculateDurationMs(startDate, endDate) {
      let duration = 0;
      this.forEachAvailabilityInterval({
        startDate: startDate,
        endDate: endDate
      }, (intervalStartDate, intervalEndDate, calendarCacheInterval) => {
        if (calendarCacheInterval.getIsWorking()) {
          const dstDiff = intervalStartDate.getTimezoneOffset() - intervalEndDate.getTimezoneOffset();
          duration += intervalEndDate.getTime() - intervalStartDate.getTime() + dstDiff * 60 * 1000;
        }
      });
      return duration;
    }

    calculateEndDate(startDate, durationMs) {
      const isForward = durationMs >= 0;
      const res = this.accumulateWorkingTime(startDate, Math.abs(durationMs), isForward);
      return res.remainingDurationInMs === 0 ? res.finalDate : null;
    }

    calculateStartDate(endDate, durationMs) {
      const isForward = durationMs <= 0;
      const res = this.accumulateWorkingTime(endDate, Math.abs(durationMs), isForward);
      return res.remainingDurationInMs === 0 ? res.finalDate : null;
    }

    skipNonWorkingTime(date, isForward = true) {
      let workingDate;
      const res = this.forEachAvailabilityInterval(isForward ? {
        startDate: date,
        isForward: true
      } : {
        endDate: date,
        isForward: false
      }, (intervalStartDate, intervalEndDate, calendarCacheInterval) => {
        if (calendarCacheInterval.getIsWorking()) {
          workingDate = isForward ? intervalStartDate : intervalEndDate;
          return false;
        }
      });
      if (res === CalendarIteratorResult.MaxRangeReached || res === CalendarIteratorResult.FullRangeIterated) return 'empty_calendar';
      return workingDate ? new Date(workingDate) : new Date(date);
    }

    addInterval(interval) {
      return this.addIntervals([interval]);
    }

    addIntervals(intervals) {
      this.bumpVersion();
      return this.intervalStore.add(intervals);
    }

    bumpVersion() {
      this.clearCache();
      this.version++;
    }

    get calendarCache() {
      if (this.$calendarCache !== undefined) return this.$calendarCache;
      const unspecifiedTimeInterval = new UnspecifiedTimeIntervalModel({
        isWorking: this.unspecifiedTimeIsWorking
      });
      unspecifiedTimeInterval.calendar = this;
      return this.$calendarCache = new CalendarCacheSingle({
        calendar: this,
        unspecifiedTimeInterval: unspecifiedTimeInterval,
        intervalStore: this.intervalStore,
        parentCache: this.parent && !this.parent.isRoot ? this.parent.calendarCache : null
      });
    }

    clearCache() {
      this.$calendarCache && this.$calendarCache.clear();
      this.$calendarCache = undefined;
    }

    resetPriorityOfAllIntervals() {
      this.traverse(calendar => {
        calendar.intervalStore.forEach(interval => interval.resetPriority());
      });
    }

    insertChild(child, before) {
      let res = superProto.insertChild.call(this, child, before);

      if (!Array.isArray(res)) {
        res = [res];
      }

      res.forEach(r => {
        r.bumpVersion();
        r.resetPriorityOfAllIntervals();
      });
      return res;
    }

    joinProject() {
      superProto.joinProject.call(this);
      this.intervalStore.setProject(this.getProject());
    }

    leaveProject() {
      superProto.leaveProject.call(this);
      this.intervalStore.setProject(null);
      this.intervalStore.destroy();
      this.clearCache();
    }

    isDayHoliday(day) {
      const startDate = DateHelper.clearTime(day),
            endDate = DateHelper.getNext(day, TimeUnit.Day);
      let hasWorkingTime = false;
      this.forEachAvailabilityInterval({
        startDate,
        endDate,
        isForward: true
      }, (_intervalStartDate, _intervalEndDate, calendarCacheInterval) => {
        hasWorkingTime = calendarCacheInterval.getIsWorking();
        return !hasWorkingTime;
      });
      return !hasWorkingTime;
    }

    getDailyHolidaysRanges(startDate, endDate) {
      const result = [];
      startDate = DateHelper.clearTime(startDate);

      while (startDate < endDate) {
        if (this.isDayHoliday(startDate)) {
          result.push({
            startDate,
            endDate: DateHelper.getStartOfNextDay(startDate, true, true)
          });
        }

        startDate = DateHelper.getNext(startDate, TimeUnit.Day);
      }

      return result;
    }

    getNonWorkingTimeRanges(startDate, endDate) {
      const result = [];
      this.forEachAvailabilityInterval({
        startDate,
        endDate,
        isForward: true
      }, (intervalStartDate, intervalEndDate, calendarCacheInterval) => {
        if (!calendarCacheInterval.getIsWorking()) {
          const entry = calendarCacheInterval.intervals[0];
          result.push({
            name: entry.name,
            startDate: intervalStartDate,
            endDate: intervalEndDate
          });
        }
      });
      return result;
    }

    isWorkingTime(startDate, endDate) {
      const workingTimeStart = this.skipNonWorkingTime(startDate);
      return workingTimeStart && workingTimeStart !== 'empty_calendar' ? workingTimeStart < endDate : false;
    }

  }

  return CalendarMixin;
}) {}

var __decorate$6 = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const hasMixin = Symbol('CalendarMixin');
class BaseCalendarMixin extends Mixin([AbstractCalendarMixin, ChronoPartOfProjectModelMixin], base => {
  const superProto = base.prototype;

  class BaseCalendarMixin extends base {
    constructor() {
      super(...arguments);
      this.version = 1;
    }

    [hasMixin]() {}

  }

  __decorate$6([field({
    persistent: false
  })], BaseCalendarMixin.prototype, "version", void 0);

  __decorate$6([model_field({
    type: 'string'
  })], BaseCalendarMixin.prototype, "name", void 0);

  __decorate$6([model_field({
    type: 'boolean',
    defaultValue: true
  })], BaseCalendarMixin.prototype, "unspecifiedTimeIsWorking", void 0);

  __decorate$6([model_field()], BaseCalendarMixin.prototype, "intervals", void 0);

  return BaseCalendarMixin;
}) {}

var __decorate$7 = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};
class HasCalendarMixin extends Mixin([ChronoPartOfProjectModelMixin], base => {
  const superProto = base.prototype;

  class HasCalendarMixin extends base {
    writeCalendar(me, transaction, quark, calendar) {
      const calendarManagerStore = this.getCalendarManagerStore();
      const cal = calendar;

      if (calendar && calendarManagerStore && calendar instanceof BaseCalendarMixin && !calendarManagerStore.includes(cal)) {
        calendarManagerStore.add(calendar);
      }

      me.constructor.prototype.write.call(this, me, transaction, quark, calendar);
    }

    resolveCalendar(locator) {
      var _a;

      return (_a = this.getCalendarManagerStore()) === null || _a === void 0 ? void 0 : _a.getById(locator);
    }

    *calculateEffectiveCalendar() {
      let calendar = yield this.$.calendar;

      if (!calendar) {
        const project = this.getProject();
        calendar = yield project.$.effectiveCalendar;
      }

      yield calendar.$.version;
      return calendar;
    }

  }

  __decorate$7([field()], HasCalendarMixin.prototype, "effectiveCalendar", void 0);

  __decorate$7([generic_field({
    modelFieldConfig: {
      persist: true,
      serialize: calendar => calendar === null || calendar === void 0 ? void 0 : calendar.id,
      isEqual: isSerializableEqual
    },
    resolver: function (locator) {
      return this.resolveCalendar(locator);
    },
    sync: true
  }, ModelReferenceField)], HasCalendarMixin.prototype, "calendar", void 0);

  __decorate$7([write('calendar')], HasCalendarMixin.prototype, "writeCalendar", null);

  __decorate$7([calculate('effectiveCalendar')], HasCalendarMixin.prototype, "calculateEffectiveCalendar", null);

  injectStaticFieldsProperty(HasCalendarMixin);
  return HasCalendarMixin;
}) {}
class CanCombineCalendarsMixin extends Mixin([], base => {
  const superProto = base.prototype;

  class CanCombineCalendars extends base {
    constructor() {
      super(...arguments);
      this.combinedcalendarscache = new Map();
    }

    combineCalendars(calendars) {
      const uniqueOnly = stripDuplicates(calendars);
      if (uniqueOnly.length === 0) throw new Error("No calendars to combine");
      uniqueOnly.sort((calendar1, calendar2) => {
        if (calendar1.internalId < calendar2.internalId) return -1;else return 1;
      });
      const hash = uniqueOnly.map(calendar => calendar.internalId + '/').join('');
      const versionsHash = uniqueOnly.map(calendar => calendar.version + '/').join('');
      const cached = this.combinedcalendarscache.get(hash);
      let res;
      if (cached && cached.versionsHash === versionsHash) res = cached.cache;else {
        res = new CalendarCacheMultiple({
          calendarCaches: uniqueOnly.map(calendar => calendar.calendarCache)
        });
        this.combinedcalendarscache.set(hash, {
          versionsHash: versionsHash,
          cache: res
        });
      }
      return res;
    }

  }

  return CanCombineCalendars;
}) {}

var __decorate$8 = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};
class BaseResourceMixin extends Mixin([HasCalendarMixin, ChronoPartOfProjectModelMixin], base => {
  const superProto = base.prototype;

  class BaseResourceMixin extends base {
    get assignments() {
      return [...this.assigned];
    }

    leaveProject(isReplacing = false) {
      if (this.assigned && !isReplacing) {
        const resourceStore = this.getResourceStore();
        this.assigned.forEach(assignment => resourceStore.assignmentsForRemoval.add(assignment));
      }

      superProto.leaveProject.call(this);
    }

  }

  __decorate$8([generic_field({}, ModelBucketField)], BaseResourceMixin.prototype, "assigned", void 0);

  return BaseResourceMixin;
}) {}

var _window$bryntum$2;
const EngineMixin$2 =
/*  */
CoreResourceMixin;
/**
 * @module Scheduler/model/ResourceModel
 */

/**
 * This class represent a single Resource in scheduler, usually added to a {@link Scheduler.data.ResourceStore}.
 *
 * It is a subclass of  {@link Core.data.Model}. Please refer to the documentation for that class to become familiar
 * with the base interface of the resource.
 *
 * ## Fields and references
 *
 * A resource has a few predefined fields, see Fields below. If you want to add more fields with meta data describing
 * your resources then you should subclass this class:
 *
 * ```javascript
 * class MyResource extends ResourceModel {
 *   static get fields() {
 *     return [
 *       // "id" and "name" fields are already provided by the superclass
 *       { name: 'company', type : 'string' }
 *     ];
 *   }
 * });
 * ```
 *
 * If you want to use other names in your data for the id and name fields you can configure them as seen below:
 *
 * ```javascript
 * class MyResource extends ResourceModel {
 *   static get fields() {
 *     return [
 *        { name: 'name', dataSource: 'userName' }
 *     ];
 *   },
 * });
 * ```
 *
 * After load and project normalization, these references are accessible (assuming their respective stores are loaded):
 * - `assignments` - The linked assignment records
 * - `events` - The linked (through assignments) event records
 *
 * ## Async resolving of references
 *
 * As described above, a resource has links to assignments and events. These references are populated async, using the
 * calculation engine of the project that the resource via its store is a part of. Because of this asyncness, references
 * cannot be used immediately after assignment modifications:
 *
 * ```javascript
 * assignment.resourceId = 2;
 * // resource.assignments is not yet up to date
 * ```
 *
 * To make sure references are updated, wait for calculations to finish:
 *
 * ```javascript
 * assignment.resourceId = 2;
 * await assignment.project.commitAsync();
 * // resource.assignments is up to date
 * ```
 *
 * As an alternative, you can also use `setAsync()` to trigger calculations directly after the change:
 *
 * ```javascript
 * await assignment.setAsync({ resourceId : 2});
 * // resource.assignments is up to date
 * ```
 *
 * @extends Grid/data/GridRowModel
 * @mixes Scheduler/model/mixin/ResourceModelMixin
 */

class ResourceModel extends ResourceModelMixin(PartOfProject(EngineMixin$2.derive(GridRowModel))) {}
ResourceModel.exposeProperties();
ResourceModel._$name = 'ResourceModel';

class AbstractResourceStoreMixin extends Mixin([AbstractPartOfProjectStoreMixin], base => {
  const superProto = base.prototype;

  class AbstractResourceStoreMixin extends base {
    constructor() {
      super(...arguments);
      this.assignmentsForRemoval = new Set();
    }

    onDataChange(event) {
      var _a;

      super.onDataChange(event);
      if ((_a = event.removed) === null || _a === void 0 ? void 0 : _a.length) this.afterResourceRemoval();
    }

    remove(records, silent) {
      const res = superProto.remove.call(this, records);
      this.afterResourceRemoval();
      return res;
    }

    removeAll(silent) {
      const res = superProto.removeAll.call(this, silent);
      this.afterResourceRemoval();
      return res;
    }

    afterResourceRemoval() {
      const assignmentStore = this.getAssignmentStore();

      if (assignmentStore && !assignmentStore.allAssignmentsForRemoval) {
        const assignmentsForRemoval = [...this.assignmentsForRemoval].filter(assignment => !assignmentStore.assignmentsForRemoval.has(assignment));
        assignmentsForRemoval.length > 0 && assignmentStore.remove(assignmentsForRemoval);
      }

      this.assignmentsForRemoval.clear();
    }

    processRecord(resourceRecord, isDataset = false) {
      const existingRecord = this.getById(resourceRecord.id);
      const isReplacing = existingRecord && existingRecord !== resourceRecord;

      if (isReplacing) {
        for (const assignment of existingRecord.assigned) {
          assignment.resource = resourceRecord;
        }
      }

      return resourceRecord;
    }

  }

  return AbstractResourceStoreMixin;
}) {}

class ChronoResourceStoreMixin extends Mixin([AbstractResourceStoreMixin, ChronoPartOfProjectStoreMixin], base => {
  const superProto = base.prototype;

  class ChronoResourceStoreMixin extends base {
    static get defaultConfig() {
      return {
        modelClass: BaseResourceMixin
      };
    }

  }

  return ChronoResourceStoreMixin;
}) {}

class CoreResourceStoreMixin extends Mixin([AbstractResourceStoreMixin, CorePartOfProjectStoreMixin], base => {
  const superProto = base.prototype;

  class CoreResourceStoreMixin extends base {
    static get defaultConfig() {
      return {
        modelClass: CoreResourceMixin
      };
    }

    joinProject() {
      var _a;

      (_a = this.assignmentStore) === null || _a === void 0 ? void 0 : _a.linkAssignments(this, 'resource');
    }

    afterLoadData() {
      var _a;

      (_a = this.assignmentStore) === null || _a === void 0 ? void 0 : _a.linkAssignments(this, 'resource');
    }

  }

  return CoreResourceStoreMixin;
}) {}

var _window$bryntum$3;
const EngineMixin$3 =
/*  */
PartOfProject(CoreResourceStoreMixin.derive(AjaxStore));
/**
 * @module Scheduler/data/ResourceStore
 */

/**
 * A store holding all the {@link Scheduler.model.ResourceModel resources} to be rendered into a
 * {@link Scheduler.view.Scheduler Scheduler}.
 *
 * This store only accepts a model class inheriting from {@link Scheduler.model.ResourceModel}.
 *
 * A ResourceStore is usually connected to a project, which binds it to other related stores (EventStore,
 * AssignmentStore and DependencyStore). The project also handles references (assignments, events) to related records
 * for the records in the store.
 *
 * Resolving the references happens async, records are not guaranteed to have up to date references until calculations
 * are finished. To be certain that references are resolved, call `await project.commitAsync()` after store actions. Or
 * use one of the `xxAsync` functions, such as `loadDataAsync()`.
 *
 * Using `commitAsync()`:
 *
 * ```javascript
 * resourceStore.data = [{ id }, ...];
 *
 * // references (assignments, events) not resolved yet
 *
 * await resourceStore.project.commitAsync();
 *
 * // now they are
 * ```
 *
 * Using `loadDataAsync()`:
 *
 * ```javascript
 * await resourceStore.loadDataAsync([{ id }, ...]);
 *
 * // references (assignments, events) are resolved
 * ```
 *
 * @mixes Scheduler/data/mixin/PartOfProject
 * @mixes Scheduler/data/mixin/ResourceStoreMixin
 * @extends Core/data/AjaxStore
 */

class ResourceStore extends ResourceStoreMixin(EngineMixin$3) {
  static get defaultConfig() {
    return {
      modelClass: ResourceModel
    };
  }

}
ResourceStore._$name = 'ResourceStore';

/**
 * @module Scheduler/data/mixin/EventStoreMixin
 */

const returnTrue$1 = () => true,
      emptyArray$6 = Object.freeze([]),
      emptyIndex = Object.freeze({}),
      MS_PER_DAY = 864e5,
      notRecurring = event => !event.isRecurring;
/**
 * This is a mixin, containing functionality related to managing events.
 *
 * It is consumed by the regular {@link Scheduler.data.EventStore} class and the Gantt `TaskStore` classes
 * to allow data sharing between a Gantt chart and a Scheduler.
 *
 * @mixin
 */

var EventStoreMixin = (Target => class EventStoreMixin extends (Target || Base) {
  static get $name() {
    return 'EventStoreMixin';
  } //region Init & destroy

  construct(config) {
    super.construct(config);
    Object.assign(this, {
      autoTree: true
    });
  } //endregion
  //region Indices
  // Override to syncIndices on initial load

  afterLoadData() {
    this.syncIndices('splice', this.storage.allValues); // Babel cannot transpile ?. version of this correctly for some reason

    super.afterLoadData && super.afterLoadData();
  }
  /**
   * Responds to mutations of the underlying storage Collection.
   *
   * Maintain indices for fast finding of events by date.
   * @param {Object} event
   * @private
   */

  onDataChange({
    action,
    added,
    removed,
    replaced
  }) {
    // Indices must be synced before responding to change
    this.syncIndices(action, added, removed, replaced);
    super.onDataChange(...arguments);
  }

  onDataReplaced(action, data) {
    // Indices must be synced before responding to change
    this.syncIndices('clear');
    this.syncIndices('splice', this.storage.values);
    super.onDataReplaced(action, data);
  } // TODO: Improve Collection indices to handle this
  // Keeps date & startDate indices up to date, used by Calendar and recurrence
  // The indices are initialized lazily on first access, and then kept up to date on changes

  syncIndices(action, added = emptyArray$6, removed = emptyArray$6, replaced, wasSet) {
    var _added, _removed, _DateHelper$clearTime3, _wasSet$startDate, _getTime, _ref, _wasSet$endDate;

    const me = this,
          addedCount = (_added = added) === null || _added === void 0 ? void 0 : _added.length,
          removedCount = (_removed = removed) === null || _removed === void 0 ? void 0 : _removed.length,
          replacedCount = replaced === null || replaced === void 0 ? void 0 : replaced.length;
    let dateMS, endDateMS, i, newEvent, outgoingEvent; // Only create indices if they have been requested

    if (me.dateIndicesRequested) {
      switch (action) {
        case 'clear':
          me._startDateIndex = {};
          me._dateIndex = {};
          break;
        // Add and remove

        case 'splice':
          // Handle replacement of records by instances with same ID
          if (replacedCount) {
            added = added.slice();
            removed = removed.slice();

            for (i = 0; i < replacedCount; i++) {
              removed.push(replaced[i][0]);
              added.push(replaced[i][1]);
            }
          } // Remove entries from indices

          if (removedCount) {
            for (i = 0; i < removedCount; i++) {
              var _DateHelper$clearTime, _outgoingEvent$endDat, _outgoingEvent$endDat2;

              outgoingEvent = removed[i];
              dateMS = (_DateHelper$clearTime = DateHelper.clearTime(outgoingEvent.startDate)) === null || _DateHelper$clearTime === void 0 ? void 0 : _DateHelper$clearTime.getTime();
              endDateMS = (_outgoingEvent$endDat = (_outgoingEvent$endDat2 = outgoingEvent.endDate) === null || _outgoingEvent$endDat2 === void 0 ? void 0 : _outgoingEvent$endDat2.getTime()) !== null && _outgoingEvent$endDat !== void 0 ? _outgoingEvent$endDat : dateMS; // Must handle unscheduled events

              if (dateMS) {
                me.removeIndexEntry('startDate', outgoingEvent, DateHelper.makeKey(dateMS));

                do {
                  me.removeIndexEntry('date', outgoingEvent, DateHelper.makeKey(dateMS));
                  dateMS += MS_PER_DAY;
                } while (dateMS < endDateMS);
              }
            }
          } // Add entries to indices

          if (addedCount) {
            for (i = 0; i < addedCount; i++) {
              newEvent = added[i]; // Can only be date-indexed if it's scheduled

              if (newEvent.isScheduled) {
                var _DateHelper$clearTime2, _newEvent$endDate$get, _newEvent$endDate;

                dateMS = (_DateHelper$clearTime2 = DateHelper.clearTime(newEvent.startDate)) === null || _DateHelper$clearTime2 === void 0 ? void 0 : _DateHelper$clearTime2.getTime();
                endDateMS = (_newEvent$endDate$get = (_newEvent$endDate = newEvent.endDate) === null || _newEvent$endDate === void 0 ? void 0 : _newEvent$endDate.getTime()) !== null && _newEvent$endDate$get !== void 0 ? _newEvent$endDate$get : dateMS; // Must handle unscheduled events

                if (dateMS) {
                  me.addIndexEntry('startDate', newEvent, DateHelper.makeKey(dateMS));

                  do {
                    me.addIndexEntry('date', newEvent, DateHelper.makeKey(dateMS));
                    dateMS += MS_PER_DAY;
                  } while (dateMS < endDateMS);
                }
              }
            }
          }

          break;
        // invoked when the start or end changes so that the event can be re-indexed.

        case 'reschedule':
          outgoingEvent = added[0];
          dateMS = (_DateHelper$clearTime3 = DateHelper.clearTime(((_wasSet$startDate = wasSet.startDate) === null || _wasSet$startDate === void 0 ? void 0 : _wasSet$startDate.oldValue) || outgoingEvent.startDate)) === null || _DateHelper$clearTime3 === void 0 ? void 0 : _DateHelper$clearTime3.getTime();
          endDateMS = (_getTime = (_ref = ((_wasSet$endDate = wasSet.endDate) === null || _wasSet$endDate === void 0 ? void 0 : _wasSet$endDate.oldValue) || outgoingEvent.endDate) === null || _ref === void 0 ? void 0 : _ref.getTime()) !== null && _getTime !== void 0 ? _getTime : dateMS; // Must handle unscheduled events

          if (dateMS) {
            // Remove index entries for the outgoing date
            me.removeIndexEntry('startDate', outgoingEvent, DateHelper.makeKey(dateMS));

            do {
              me.removeIndexEntry('date', outgoingEvent, DateHelper.makeKey(dateMS));
              dateMS += MS_PER_DAY;
            } while (dateMS < endDateMS);
          } // Now process as as splice with an add and no removes.

          me.syncIndices('splice', added);
          break;
      }
    }
  }

  onModelChange(record, toSet, wasSet, silent, fromRelationUpdate) {
    // Ensure by-date indices are up to date.
    if ('startDate' in wasSet || 'endDate' in wasSet) {
      this.syncIndices('reschedule', [record], null, null, wasSet);
    }

    super.onModelChange(...arguments);
  }

  addIndexEntry(indexName, value, key = value[indexName]) {
    const indexPropName = `_${indexName}Index`,
          index = this[indexPropName] || (this[indexPropName] = {}),
          entry = index[key] || (index[key] = new Set());
    entry.add(value);
  }

  removeIndexEntry(indexName, value, key = value[indexName]) {
    const indexPropName = `_${indexName}Index`,
          index = this[indexPropName] || (this[indexPropName] = {}),
          entry = index[key];

    if (entry) {
      entry.delete(value);
    }
  } // Retrieve the date or startDate index, building them on first request

  getDateIndex(indexName) {
    const me = this; // Date indices are created on first usage and after that kept up to date on changes

    if (!me.dateIndicesRequested) {
      me.dateIndicesRequested = true;
      me.syncIndices('splice', me.storage.allValues);
    }

    return me[`_${indexName}Index`] || emptyIndex;
  } //endregion
  //region Events records, iteration etc.

  /**
   * Returns events between the supplied start and end date
   *
   * @param {Date} startDate The start date
   * @param {Date} endDate The end date
   * @param {Boolean} allowPartial false to only include events that start and end inside of the span
   * @param {Boolean} onlyAssigned true to only include events that are assigned to a resource
   * @return {Scheduler.model.EventModel[]} the events
   * @category Events
   * @deprecated 4.0.0 Use {@link #function-getEvents}
   */

  getEventsInTimeSpan(startDate, endDate, allowPartial = true, onlyAssigned = false) {
    VersionHelper.deprecate('Scheduler', '5.0.0', '`getEventsInTimeSpan` method deprecated, in favor of `getEvents`');
    return this.getEvents({
      startDate,
      endDate,
      allowPartial,
      onlyAssigned
    });
  }
  /**
   * Returns all events that starts on the specified day.
   *
   * @param {Date} startDate Start date
   * @returns {Scheduler.model.EventModel[]} Events starting on specified day
   * @category Events
   * @deprecated 4.0.0 Use {@link #function-getEvents}
   */

  getEventsByStartDate(startDate) {
    VersionHelper.deprecate('Scheduler', '5.0.0', '`getEventsByStartDate` method deprecated, in favor of `getEvents`');
    return this.getEvents({
      startDate,
      startOnly: true
    });
  }
  /**
   * Returns an array of events for the date range specified by the `startDate` and `endDate` options.
   *
   * By default, for any date, this includes any event which *intersects* that date.
   *
   * To only include events that are fully contained *within* the date range, pass the `allowPartial`
   * option as `false`
   *
   * By default, any occurrences of recurring events are included in the resulting array. If that is not
   * required, pass the `includeOccurrences` option as `false`. **Note that if
   * `includeOccurrences` is `true`, the start date and end date options are mandatory. The method
   * must know what range of ocurrences needs to be generated and returned.**
   *
   * example:
   *
   * ```javascript
   *  visibleEvents = eventStore.getEvents({
   *      resourceRecord : myResource,
   *      startDate      : scheduler.timeAxis.startDate,
   *      endDate        : scheduler.timeAxis.endDate
   *  });
   * ```
   *
   * @param {Date} [options.date] If only one date is required, pass this option instead of the
   * `startDate` and `endDate` options.
   * @param {Date} options.startDate The start date for the range of events to include.
   * @param {Date} [options.endDate] The end date for the range of events to include.
   * @param {Scheduler.model.ResourceModel} options.resourceRecord Pass a resource to only return events assigned to
   *   this resource. Not supported when using the `dateMap` option (see below)
   * @param {Function} [options.filter] A function to filter out events which are not required.
   * @param {Boolean} [options.includeOccurrences=true] Occurrences of recurring events are included by default.
   * @param {Boolean} [options.allowPartial=true] Events which start before or after the range, but *intersect* the
   *   range are included by default.
   * @param {Boolean} [options.startOnly] Pass `true` to only include events which *start on* each date in the range.
   * @param {Boolean} [options.onlyAssigned] Pass `true` to only include events that are assigned to a resource
   * @param {Boolean|Map} [options.dateMap] Populates the passed `Map`, or if passed as `true`, creates and
   * returns a new `Map`. The keys are `YYYY-MM-DD` date strings and the entries are arrays of {@link Scheduler.model.EventModel EventModel}s.
   * @returns {Scheduler.model.EventModel[]|Map} Events which match the passed configuration.
   * @category Events
   */

  getEvents({
    filter,
    date,
    startDate,
    // Events which intersect the startDate/endDate
    endDate,
    // will be returned
    startOnly,
    // Only events which start on each date will be returned
    includeOccurrences,
    // Interpolate occurrences into the returned event set
    allowPartial,
    // Include events which *intersect* the date range
    onlyAssigned = false,
    // Only include events that are assigned to a resource
    dateMap = false,
    // Return a Map keyed by date each value being an array of events
    // Private option. Select which date index to look up events in depending on the date
    // we are examining in the date iteration process. Some callers may want to use
    // different indices depending on the stage through the date iteration.
    // See Calendar package for usage.
    getDateIndex
  }) {
    const me = this,
          options = arguments[0],
          {
      lastDateRange,
      added,
      filtersFunction
    } = me,
          passedFilter = filter; // Add filtering for only assigned events if requested.

    if (onlyAssigned) {
      options.filter = passedFilter ? e => passedFilter(e) && e.resources.length : e => e.resources.length;
    } // Note that we cannot use defaulting in the argument block because we pass
    // the incoming options object down into implementations.

    if (!('startDate' in options)) {
      startDate = options.startDate = date;
    }

    if (!('includeOccurrences' in options)) {
      includeOccurrences = options.includeOccurrences = true;
    }

    if (!('allowPartial' in options)) {
      allowPartial = options.allowPartial = !startOnly;
    } // We can't use me.filtersFunction if reapplyFilterOnAdd is false because there may be newly
    // added events which may not be subject to the filter. Records which are still in
    // the added bag must be tested for presence using indexOf so as to be always in sync
    // with the store being refiltered. Parens help readability.
    // eslint-disable-next-line no-extra-parens

    options.storeFilterFn = me.isFiltered ? me.reapplyFilterOnAdd ? filtersFunction : eventRecord => added.includes(eventRecord) ? me.indexOf(eventRecord) > -1 : filtersFunction(eventRecord) : null; // Default to a one day range if only startDate passed

    if (!endDate) {
      if (startDate) {
        endDate = options.endDate = DateHelper.clearTime(startDate);
        endDate.setDate(endDate.getDate() + 1);
      } // If no dates passed, the dateFilter will include all.
      else {
          // We need to know what occurrences to generate.
          if (includeOccurrences) {
            throw new Error('getEvents MUST be passed startDate and endDate if recurring occurrences are requested');
          }

          options.dateFilter = returnTrue$1;
        }
    }

    if (!options.dateFilter) {
      // Must start in the date range
      if (startOnly) {
        options.dateFilter = e => e.startDate && !(DateHelper.clearTime(e.startDate) - startDate);
      } // Any intersection with our date range
      else if (allowPartial) {
          options.dateFilter = e => e.startDate && DateHelper.intersectSpans(e.startDate, e.endDate || e.startDate, startDate, endDate);
        } // Must be wholly contained with the our range
        else {
            options.dateFilter = e => e.startDate && e.startDate >= startDate && (e.endDate || e.startDate) <= endDate;
          }
    }

    const newDateRange = {
      startDate,
      endDate
    },
          dateRangeChange = !lastDateRange || lastDateRange.startDate - newDateRange.startDate || lastDateRange.endDate - newDateRange.endDate;

    if (dateRangeChange) {
      // Ensure the listeners are present
      me.processConfiguredListeners();
      /**
       * Fired when a range of events is requested from the {@link #function-getEvents} method.
       * @event loadDateRange
       * @param {Scheduler.data.EventStore} source This EventStore
       * @param {Object} old The old date range
       * @param {Date} old.startDate the old start date.
       * @param {Date} old.endDate the old end date.
       * @param {Object} new The new date range
       * @param {Date} new.startDate the new start date.
       * @param {Date} new.endDate the new end date.
       */

      me.trigger('loadDateRange', {
        old: lastDateRange || {},
        new: Object.assign({}, newDateRange)
      });
      me.lastDateRange = newDateRange;
    }

    return dateMap ? me.getEventsAsMap(options) : me.getEventsAsArray(options);
  }
  /**
   * Internal implementation for {@link #function-getEvents} to use when not using dateMap.
   * @private
   */

  getEventsAsArray({
    filter,
    date,
    resourceRecord,
    startDate = date,
    // Events which intersect the startDate/endDate
    endDate,
    // will be returned
    startOnly,
    // Only events which start on each date will be returned
    includeOccurrences = true,
    // Interpolate occurrences into the returned event set
    // Injected by the getEvents master method
    dateFilter,
    storeFilterFn,
    // Private option. Select which date index to look up events in depending on the date
    // we are examining in the date iteration process. Some callers may want to use
    // different indices depending on the stage through the date iteration.
    // See Calendar package for usage.
    getDateIndex
  }) {
    const me = this,
          events = [];

    if (me.count) {
      let candidateEvents = resourceRecord ? me.getEventsForResource(resourceRecord) : new Set(); // If there *was* a resourceRecord, candidateEvents will already be set up using me.getEventsForResource.
      // If no resourceRecord specified, we are gathering by date, so use the indices.

      if (!resourceRecord) {
        const eventSet = new Set(),
              index = me.getDateIndex(startOnly ? 'startDate' : 'date'); // Add all recurring events which started on or before our date range.

        me.recurringEvents.forEach(e => {
          if (DateHelper.clearTime(e.startDate) <= startDate) {
            eventSet.add(e);
          }
        }); // Iterate the date range, using the indices to find qualifed events.

        for (const date = new Date(startDate); date < endDate; date.setDate(date.getDate() + 1)) {
          const coincidingEvents = (getDateIndex ? me.getDateIndex(getDateIndex(date)) : index)[DateHelper.makeKey(date)];

          if (coincidingEvents) {
            coincidingEvents.forEach(e => eventSet.add(e));
          }
        } // We gathered all events which *coincide* with each date.
        // We also added in all recurring events which started on or before our date range.
        // All these were made unique by the Set.
        // Return it to array form.

        candidateEvents = [...eventSet];
      } // Events found from the date indices won't be filtered.
      // On the other side, when using getEventForResource we will get all events for
      // the resource even if the EventStore is filtered, handle this by excluding "invisible" events here

      if (storeFilterFn) {
        candidateEvents = candidateEvents.filter(storeFilterFn);
      } // Go through candidates.
      // For a recurring event, and we are including recurrences, add date-qualifying occurrences.
      // For a non-recurring event, add it if it's date-qualified.

      for (let i = 0, {
        length
      } = candidateEvents; i < length; i++) {
        const e = candidateEvents[i]; // For recurring events, add date-qualifying occurrences, not the base

        if (e.isRecurring && includeOccurrences) {
          events.push.apply(events, e.getOccurrencesForDateRange(startDate, endDate).filter(dateFilter));
        } // For ordinary events, add if it's date-qualified
        else {
            if (dateFilter(e)) {
              events.push(e);
            }
          }
      }
    }

    return filter ? events.filter(filter) : events;
  }
  /**
   * Internal implementation for {@link #function-getEvents} to use when using dateMap.
   * @private
   */

  getEventsAsMap({
    filter: passedFilter,
    date,
    resourceRecord,
    // Not supported yet. Will add if ever requested.
    startDate = date,
    // Events which intersect the startDate/endDate
    endDate,
    // will be returned
    startOnly,
    // Only events which start on each date will be returned
    includeOccurrences = true,
    // Interpolate occurrences into the returned event set
    dateMap,
    // Return a Map keyed by date each value being an array of events
    storeFilterFn,
    // Private option. Select which date index to look up events in depending on the date
    // we are examining in the date iteration process. Some callers may want to use
    // different indices depending on the stage through the date iteration.
    // See Calendar package for usage.
    getDateIndex
  }) {
    const me = this; // Convert `true` to a Map.

    if (dateMap.clear) {
      dateMap.clear();
    } else {
      dateMap = new Map();
    }

    if (me.count) {
      const index = me.getDateIndex(startOnly ? 'startDate' : 'date'),
            recurringEvents = [],
            filter = e => (!passedFilter || passedFilter(e)) && (!storeFilterFn || storeFilterFn(e)); // We can't yet do this for just a resource.

      if (resourceRecord) {
        throw new Error('Querying for events for a resource and returning a date-keyed Map is not supported');
      } else {
        // Add all recurring events which started before the end of our date range.
        me.recurringEvents.forEach(e => {
          if (DateHelper.clearTime(e.startDate) < endDate) {
            recurringEvents.push(e);
          }
        }); // Iterate the date range, using the indices to find qualifed events.

        for (const date = new Date(startDate); date < endDate; date.setDate(date.getDate() + 1)) {
          const key = DateHelper.makeKey(date);
          let coincidingEvents = (getDateIndex ? me.getDateIndex(getDateIndex(date)) : index)[DateHelper.makeKey(date)];

          if (coincidingEvents) {
            // Convert Set which index holds into an Array.
            // A recurring event doesn't go into the Map, its occurrences do.
            // Then filter by the passed filter and this Store's filter function
            // because events found from the date indices won't be filtered.
            coincidingEvents = [...coincidingEvents].filter(notRecurring).filter(filter);
            (dateMap.get(key) || dateMap.set(key, []).get(key)).push(...coincidingEvents);
          }
        }
      } // Go through matching recurring events.

      for (let i = 0, {
        length
      } = recurringEvents; i < length; i++) {
        const e = recurringEvents[i]; // For each recurring event, add occurrences if we are including occurrences else, add the base.
        // Then filter by the passed filter and this Store's filter function
        // because events found from the date indices won't be filtered.

        const occurrences = (includeOccurrences ? e.getOccurrencesForDateRange(startDate, endDate) : [e]).filter(filter); // Add occurrences to dateMap

        for (let i = 0, {
          length
        } = occurrences; i < length; i++) {
          const occurrence = occurrences[i],
                date = DateHelper.clearTime(occurrence.startDate),
                indexName = getDateIndex ? getDateIndex(date) : startOnly ? 'startDate' : 'date',
                lastInteresctingDate = indexName === 'startDate' ? DateHelper.endOf(date) : occurrence.endDate || DateHelper.add(occurrence.startDate, occurrence.duration, occurrence.durationUnit); // Loop through covered dates, adding to dateMap if required

          for (; date < lastInteresctingDate; date.setDate(date.getDate() + 1)) {
            const key = DateHelper.makeKey(date);
            (dateMap.get(key) || dateMap.set(key, []).get(key)).push(occurrence);
          }
        }
      }
    }

    return dateMap;
  }
  /**
   * Calls the supplied iterator function once for every scheduled event, providing these arguments
   * - event : the event record
   * - startDate : the event start date
   * - endDate : the event end date
   *
   * Returning false cancels the iteration.
   *
   * @param {Function} fn iterator function
   * @param {Object} thisObj `this` reference for the function
   * @category Events
   */

  forEachScheduledEvent(fn, thisObj = this) {
    this.forEach(event => {
      const {
        startDate,
        endDate
      } = event;

      if (startDate && endDate) {
        return fn.call(thisObj, event, startDate, endDate);
      }
    });
  }
  /**
   * Returns an object defining the earliest start date and the latest end date of all the events in the store.
   *
   * @return {Object} An object with 'start' and 'end' Date properties (or null values if data is missing).
   * @category Events
   */

  getTotalTimeSpan() {
    let earliest = new Date(9999, 0, 1),
        latest = new Date(0);
    this.forEach(event => {
      if (event.startDate) {
        earliest = DateHelper.min(event.startDate, earliest);
      }

      if (event.endDate) {
        latest = DateHelper.max(event.endDate, latest);
      }
    }); // TODO: this will fail in programs designed to work with events in the past (after Jan 1, 1970)

    earliest = earliest < new Date(9999, 0, 1) ? earliest : null;
    latest = latest > new Date(0) ? latest : null; // keep last calculated value to be able to track total timespan changes

    return this.lastTotalTimeSpan = {
      startDate: earliest || null,
      endDate: latest || earliest || null
    };
  }
  /**
   * Checks if given event record is persistable. By default it always is, override EventModels `isPersistable` if you
   * need custom logic.
   *
   * @param {Scheduler.model.EventModel} event
   * @return {Boolean}
   * @category Events
   */

  isEventPersistable(event) {
    return event.isPersistable;
  } //endregion
  //region Resource

  /**
   * Checks if a date range is allocated or not for a given resource.
   * @param {Date} start The start date
   * @param {Date} end The end date
   * @param {Scheduler.model.EventModel|null} excludeEvent An event to exclude from the check (or null)
   * @param {Scheduler.model.ResourceModel} resource The resource
   * @return {Boolean} True if the timespan is available for the resource
   * @category Resource
   */

  isDateRangeAvailable(start, end, excludeEvent, resource) {
    // NOTE: Also exists in TaskStore.js
    // This should be a collection of unique event records
    const allEvents = new Set(this.getEventsForResource(resource)); // In private mode we can pass an AssignmentModel. In this case, we assume that multi-assignment is used.
    // So we need to make sure that other resources are available for this time too.
    // No matter if the event retrieved from the assignment belongs to the target resource or not.
    // We gather all events from from the resources the event is assigned to except of the one from the assignment record.
    // Note, events from the target resource are added above.

    if (excludeEvent !== null && excludeEvent !== void 0 && excludeEvent.isAssignment) {
      const currentEvent = excludeEvent.event,
            resources = currentEvent.resources;
      resources.forEach(resource => {
        // Ignore events for the resource which is passed as an AssignmentModel to excludeEvent
        if (resource.id !== excludeEvent.resourceId) {
          this.getEventsForResource(resource).forEach(event => allEvents.add(event));
        }
      });
    }

    if (excludeEvent) {
      const eventToRemove = excludeEvent.isAssignment ? excludeEvent.event : excludeEvent;
      allEvents.delete(eventToRemove);
    }

    return !Array.from(allEvents).some(event => event.isScheduled && DateHelper.intersectSpans(start, end, event.startDate, event.endDate));
  }
  /**
   * Filters the events associated with a resource, based on the function provided. An array will be returned for those
   * events where the passed function returns true.
   * @param {Scheduler.model.ResourceModel} resource
   * @param {Function} fn The function
   * @param {Object} [thisObj] `this` reference for the function
   * @return {Scheduler.model.EventModel[]} the events in the time span
   * @private
   * @category Resource
   */

  filterEventsForResource(resource, fn, thisObj = this) {
    return resource.getEvents(this).filter(fn.bind(thisObj));
  }
  /**
   * Returns all resources assigned to an event.
   *
   * @param {Scheduler.model.EventModel|String|Number} event
   * @return {Scheduler.model.ResourceModel[]}
   * @category Resource
   */

  getResourcesForEvent(event) {
    // If we are sent an occurrence, use its parent
    if (event.isOccurrence) {
      event = event.recurringTimeSpan;
    }

    return this.assignmentStore.getResourcesForEvent(event);
  }
  /**
   * Returns all events assigned to a resource.
   *
   * @param {Scheduler.model.ResourceModel|String|Number} resource Resource or resource id.
   *   *NOTE:* this does not include occurrences of recurring events. Use the {@link #function-getEvents} API
   *   to include occurrences of recurring events.
   * @return {Scheduler.model.EventModel[]}
   * @category Resource
   */

  getEventsForResource(resource) {
    return this.assignmentStore.getEventsForResource(resource);
  } //endregion
  //region Assignment

  /**
   * Returns all assignments for a given event.
   *
   * @param {Scheduler.model.EventModel|String|Number} event
   * @return {Scheduler.model.AssignmentModel[]}
   * @category Assignment
   */

  getAssignmentsForEvent(event) {
    return this.assignmentStore.getAssignmentsForEvent(event) || [];
  }
  /**
   * Returns all assignments for a given resource.
   *
   * @param {Scheduler.model.ResourceModel|String|Number} resource
   * @return {Scheduler.model.AssignmentModel[]}
   * @category Assignment
   */

  getAssignmentsForResource(resource) {
    return this.assignmentStore.getAssignmentsForResource(resource) || [];
  }
  /**
   * Creates and adds assignment record for a given event and a resource.
   *
   * @param {Scheduler.model.EventModel|String|Number} event
   * @param {Scheduler.model.ResourceModel|String|Number|Scheduler.model.ResourceModel[]|String[]|number[]} resource The resource(s) to assign to the event
   * @param {Boolean} [removeExistingAssignments] `true` to first remove existing assignments
   * @return {Scheduler.model.AssignmentModel[]} An array with the created assignment(s)
   * @category Assignment
   */

  assignEventToResource(event, resource, removeExistingAssignments = false) {
    return this.assignmentStore.assignEventToResource(event, resource, undefined, removeExistingAssignments);
  }
  /**
   * Removes assignment record for a given event and a resource.
   *
   * @param {Scheduler.model.EventModel|String|Number} event
   * @param {Scheduler.model.ResourceModel|String|Number} resource
   * @category Assignment
   */

  unassignEventFromResource(event, resource) {
    this.assignmentStore.unassignEventFromResource(event, resource);
  }
  /**
   * Reassigns an event from an old resource to a new resource
   *
   * @param {Scheduler.model.EventModel}    event    An event or id of the event to reassign
   * @param {Scheduler.model.ResourceModel|Scheduler.model.ResourceModel[]} oldResource A resource or id to unassign from
   * @param {Scheduler.model.ResourceModel|Scheduler.model.ResourceModel[]} newResource A resource or id to assign to
   * @category Assignment
   */

  reassignEventFromResourceToResource(event, oldResource, newResource) {
    const me = this,
          newResourceId = Model.asId(newResource),
          assignment = me.assignmentStore.getAssignmentForEventAndResource(event, oldResource);

    if (assignment) {
      assignment.resourceId = newResourceId;
    } else {
      me.assignmentStore.assignEventToResource(event, newResource);
    }
  }
  /**
   * Checks whether an event is assigned to a resource.
   *
   * @param {Scheduler.model.EventModel|String|Number} event
   * @param {Scheduler.model.ResourceModel|String|Number} resource
   * @return {Boolean}
   * @category Assignment
   */

  isEventAssignedToResource(event, resource) {
    return this.assignmentStore.isEventAssignedToResource(event, resource);
  }
  /**
   * Removes all assignments for given event
   *
   * @param {Scheduler.model.EventModel|String|Number} event
   * @category Assignment
   */

  removeAssignmentsForEvent(event) {
    this.assignmentStore.removeAssignmentsForEvent(event);
  }
  /**
   * Removes all assignments for given resource
   *
   * @param {Scheduler.model.ResourceModel|String|Number} resource
   * @category Assignment
   */

  removeAssignmentsForResource(resource) {
    this.assignmentStore.removeAssignmentsForResource(resource);
  } //endregion

});

/* eslint-disable no-unused-expressions */

/**
 * @module Scheduler/data/mixin/SharedEventStoreMixin
 */

/**
 * This is a mixin, containing functionality related to managing events.
 *
 * It is consumed by the regular {@link Scheduler.data.EventStore} class and Scheduler Pros counterpart.
 *
 * @mixin
 */
var SharedEventStoreMixin = (Target => class SharedEventStoreMixin extends Target {
  static get $name() {
    return 'SharedEventStoreMixin';
  }
  /**
   * Add events to the store.
   *
   * NOTE: Dates, durations and references (assignments, resources) on the events are determined async by a calculation
   * engine. Thus they cannot be directly accessed after using this function.
   *
   * For example:
   *
   * ```javascript
   * eventStore.add({ startDate, duration });
   * // endDate is not yet calculated
   * ```
   *
   * To guarantee data is in a calculated state, wait for calculations for finish:
   *
   * ```javascript
   * eventStore.add({ startDate, duration });
   * await eventStore.project.commitAsync();
   * // endDate is calculated
   * ```
   *
   * Alternatively use `addAsync()` instead:
   *
   * ```javascript
   * await eventStore.addAsync({ startDate, duration });
   * // endDate is calculated
   * ```
   *
   * @param {Scheduler.model.EventModel|Scheduler.model.EventModel[]|Object|Object[]} records
   * Array of records/data or a single record/data to add to store
   * @param {Boolean} [silent] Specify `true` to suppress events
   * @returns {Scheduler.model.EventModel[]} Added records
   * @function add
   * @category CRUD
   */

  /**
   * Add events to the store and triggers calculations directly after. Await this function to have up to date data on
   * the added events.
   *
   * ```javascript
   * await eventStore.addAsync({ startDate, duration });
   * // endDate is calculated
   * ```
   *
   * @param {Scheduler.model.EventModel|Scheduler.model.EventModel[]|Object|Object[]} records
   * Array of records/data or a single record/data to add to store
   * @param {Boolean} [silent] Specify `true` to suppress events
   * @returns {Scheduler.model.EventModel[]} Added records
   * @function addAsync
   * @category CRUD
   * @async
   */

  /**
   * Applies a new dataset to the EventStore. Use it to plug externally fetched data into the store.
   *
   * NOTE: Dates, durations and relations (assignments, resources) on the events are determined async by a calculation
   * engine. Thus they cannot be directly accessed after assigning the new dataset.
   *
   * For example:
   *
   * ```javascript
   * eventStore.data = [{ startDate, duration }];
   * // eventStore.first.endDate is not yet calculated
   * ```
   *
   * To guarantee data is in a calculated state, wait for calculations for finish:
   *
   * ```javascript
   * eventStore.data = [{ startDate, duration }];
   * await eventStore.project.commitAsync();
   * // eventStore.first.endDate is calculated
   * ```
   *
   * Alternatively use `loadDataAsync()` instead:
   *
   * ```javascript
   * await eventStore.loadDataAsync([{ startDate, duration }]);
   * // eventStore.first.endDate is calculated
   * ```
   *
   * @member {Object[]} data
   * @category Records
   */

  /**
   * Applies a new dataset to the EventStore and triggers calculations directly after. Use it to plug externally
   * fetched data into the store.
   *
   * ```javascript
   * await eventStore.loadDataAsync([{ startDate, duration }]);
   * // eventStore.first.endDate is calculated
   * ```
   *
   * @param {Object[]} data Array of EventModel data objects
   * @function loadDataAsync
   * @category CRUD
   * @async
   */

  static get defaultConfig() {
    return {
      /**
       * CrudManager must load stores in the correct order. Lowest first.
       * @private
       */
      loadPriority: 100,

      /**
       * CrudManager must sync stores in the correct order. Lowest first.
       * @private
       */
      syncPriority: 200,
      storeId: 'events',

      /**
       * Configure with `true` to also remove the event when removing the last assignment from the linked
       * AssignmentStore. This config has not effect when using EventStore in legacy `resourceId`-mode.
       * @config {Boolean}
       * @default
       * @category Common
       */
      removeUnassignedEvent: true
    };
  }
  /**
   * Class used to represent records. Defaults to class EventModel.
   * @property {Scheduler.model.EventModel}
   * @category Records
   * @typings { new(data: object): Model }
   * @name modelClass
   */

  construct(config) {
    super.construct(config, true);

    if (!this.modelClass.isEventModel) {
      throw new Error('The model for the EventStore must subclass EventModel');
    }
  }
  /**
   * Appends a new record to the store
   * @param {Scheduler.model.EventModel} record The record to append to the store
   */

  append(record) {
    return this.add(record);
  } //region Project

  get project() {
    return super.project;
  }

  set project(project) {
    super.project = project;
    this.detachListeners('project');

    if (project) {
      project.on({
        name: 'project',
        assignmentStoreChange: 'onProjectAssignmentStoreChange',
        thisObj: this,
        prio: 200 // Before UI updates

      }); // Project already has AssignmentStore instance? Attach to it

      if (project.assignmentStore && project.assignmentStore.isAssignmentStore) {
        this.attachToAssignmentStore(project.assignmentStore);
      }
    }
  } //endregion
  //region Single assignment

  processRecords(eventRecords) {
    const {
      assignmentStore
    } = this,
          assignmentsToAdd = [];
    eventRecords = super.processRecords(eventRecords, assignmentStore && (eventRecord => {
      // AssignmentStore found, add an assignment to it if this is not a dataset operation
      const resourceId = eventRecord.get('resourceId');

      if (!eventRecord.reassignedFromReplace && resourceId != null) {
        // Check if the event is already assigned to the resource, though it's not in the event store.
        // It could happen when you remove an event, so both event and assignment records are removed,
        // then you "undo" the action and the assignment is restored before the event is restored.
        if (!assignmentStore.includesAssignment(eventRecord.id, resourceId)) {
          // Cannot use `event.assign(resourceId)` since event is not part of store yet
          // Using a bit shorter generated id to not look so ugly in DOM
          assignmentsToAdd.push({
            id: assignmentStore.modelClass.generateId(''),
            resourceId,
            eventId: eventRecord.id
          });
        }
      } // clear flag

      eventRecord.reassignedFromReplace = false;
    }));
    assignmentStore === null || assignmentStore === void 0 ? void 0 : assignmentStore.add(assignmentsToAdd);
    return eventRecords;
  }

  processRecord(eventRecord, isDataset = false) {
    eventRecord = super.processRecord(eventRecord, isDataset);
    const resourceId = eventRecord.get('resourceId');

    if (resourceId != null) {
      const me = this,
            {
        assignmentStore
      } = me,
            existingRecord = me.getById(eventRecord.id),
            isReplacing = existingRecord && existingRecord !== eventRecord && !isDataset; // Replacing an existing event, repoint the resource of its assignment
      // (already repointed to the new event by engine in EventStoreMixin)

      if (isReplacing) {
        // Have to look assignment up on store, removed by engine in super call above
        const assignment = assignmentStore.find(e => e.eventId === eventRecord.id);

        if (assignment) {
          assignment.resource = resourceId;
          eventRecord.reassignedFromReplace = true;
        }
      } // No AssignmentStore assigned yet, need to process when that happens. Or if it is a dataset operation,
      // processing will happen at the end of it to not add individual assignment (bad for performance)
      else {
          me.$processResourceIds = true;
        } // Flag that we have been loaded using resourceId, checked by CrudManager to exclude the internal
      // AssignmentStore from sync

      me.usesSingleAssignment = true;
    }

    return eventRecord;
  }

  processResourceIds() {
    const {
      assignmentStore
    } = this;

    if (this.$processResourceIds && assignmentStore !== null && assignmentStore !== void 0 && assignmentStore.isAssignmentStore) {
      const assignments = []; // resourceIds used during initialization, convert into assignments

      this.forEach(eventRecord => {
        const {
          resourceId,
          id: eventId
        } = eventRecord;

        if (resourceId != null) {
          // Using a bit shorter generated id to not look so ugly in DOM
          assignments.push({
            id: assignmentStore.modelClass.generateId(''),
            resourceId,
            eventId
          });
        }
      }); // Disable as much as possible, since we are in full control of this store when using single assignment mode

      assignmentStore.useRawData = {
        disableDefaultValue: true,
        disableDuplicateIdCheck: true,
        disableTypeConversion: true
      }; // Flag that throws in AssignmentStore if data is loaded some other way when using single assignment

      assignmentStore.usesSingleAssignment = false;
      assignmentStore.data = assignments;
      assignmentStore.usesSingleAssignment = true;
      this.$processResourceIds = false;
    }
  }

  loadData() {
    super.loadData(...arguments);
    this.processResourceIds();
  } // Optionally remove unassigned events

  onBeforeRemoveAssignment({
    records
  }) {
    var _me$stm;

    const me = this;

    if (me.removeUnassignedEvent && !me.isRemoving && !me.isSettingData && !((_me$stm = me.stm) !== null && _me$stm !== void 0 && _me$stm.isRestoring) && !me.usesSingleAssignment) {
      const toRemove = new Set(); // Collect all events that are unassigned after the remove

      records.forEach(assignmentRecord => {
        const {
          event
        } = assignmentRecord; // Assignment might not have an event or the event might already be removed

        if (event && !event.isRemoved && event.assignments.every(a => records.includes(a))) {
          toRemove.add(event);
        }
      }); // And remove them

      if (toRemove.size) {
        me.remove([...toRemove]);
      }
    }
  }

  onProjectAssignmentStoreChange({
    store
  }) {
    this.attachToAssignmentStore(store);
  }

  attachToAssignmentStore(assignmentStore) {
    const me = this;
    me.detachListeners('assignmentStore');

    if (assignmentStore) {
      me.processResourceIds();
      assignmentStore.on({
        name: 'assignmentStore',

        // Adding an assignment in single assignment mode should set events resourceId if needed
        addPreCommit({
          records
        }) {
          if (me.usesSingleAssignment && !me.isSettingData && !me.isAssigning) {
            records.forEach(assignment => {
              const {
                event
              } = assignment;

              if (event !== null && event !== void 0 && event.isEvent && event.resourceId !== assignment.resourceId) {
                event.meta.isAssigning = true;
                event.set('resourceId', assignment.resourceId);
                event.meta.isAssigning = false;
              }
            });
          }
        },

        // Called both for remove and removeAll
        beforeRemove: 'onBeforeRemoveAssignment',

        // Removing an assignment in single assignment mode should set events resourceId to null
        removePreCommit({
          records
        }) {
          if (me.usesSingleAssignment) {
            records.forEach(assignment => {
              var _me$getById;

              // With engine link to event is already broken when we get here, hence the lookup
              (_me$getById = me.getById(assignment.eventId)) === null || _me$getById === void 0 ? void 0 : _me$getById.set('resourceId', null);
            });
          }
        },

        removeAllPreCommit() {
          if (me.usesSingleAssignment && !me.isSettingData) {
            me.allRecords.forEach(eventRecord => eventRecord.set('resourceId', null));
          }
        },

        // Keep events resourceId in sync with assignment on changes in single assignment mode
        update({
          record,
          changes
        }) {
          if (me.usesSingleAssignment && 'resourceId' in changes) {
            const {
              event
            } = record;
            event.meta.isAssigning = true;
            event.set('resourceId', changes.resourceId.value);
            event.meta.isAssigning = false;
          }
        },

        thisObj: me
      });
    }
  }

  set data(data) {
    this.isSettingData = true; // // When using single assignment, remove all assignments when loading a new set of events

    if (this.usesSingleAssignment && !this.syncDataOnLoad) {
      this.assignmentStore.removeAll(true);
    }

    super.data = data;
    this.isSettingData = false;
  } // Override trigger to decorate update/change events with a flag if resourceId was the only thing changed, in which
  // case the change most likely can be ignored since the assignment will also change

  trigger(eventName, params) {
    const {
      changes
    } = params || {};

    if (changes && 'resourceId' in changes && Object.keys(changes).length === 1) {
      params.isAssign = true;
    }

    return super.trigger(...arguments);
  } //endregion

});

/**
 * @module Scheduler/data/mixin/RecurringTimeSpansMixin
 */

const emptyArray$7 = Object.freeze([]);
/**
 * This mixin class provides recurring timespans functionality to a store of {@link Scheduler.model.TimeSpan TimeSpan} models.
 * @mixin
 */

var RecurringTimeSpansMixin = (Target => class RecurringTimeSpansMixin extends (Target || Base) {
  static get $name() {
    return 'RecurringTimeSpansMixin';
  }

  construct(...args) {
    const me = this; // We store all generated occurrences keyed by `_generated_${recurringTimeSpan.id}:${occurrenceStartDate}`
    // So that when asked to generate an occurrence for a date, an already generated one can be returned.

    me.globalOccurrences = new Map(); // All recurring events added to the store are accessible through this Set. It's used
    // to generate occurrences.

    me.recurringEvents = new Set();
    super.construct(...args);
  } // Override to refreshRecurringEventsCache on initial load

  afterLoadData() {
    // All cached occurrences are now potentially invalid.
    // A store reload might imply any number of changes which invalidate any occurrence.
    this.globalOccurrences.clear(); // Clear and rebuild the recurring events cache

    this.refreshRecurringEventsCache('clear');
    this.refreshRecurringEventsCache('splice', this.storage.allValues);
    super.afterLoadData && super.afterLoadData();
  }
  /**
   * Responds to mutations of the underlying storage Collection.
   *
   * Maintain indices for fast finding of events by date.
   * @param {Object} event
   * @private
   */

  onDataChange({
    action,
    added,
    removed,
    replaced
  }) {
    // Recurring events cache must be refreshed before responding to change
    this.refreshRecurringEventsCache(action, added, removed, replaced);
    super.onDataChange(...arguments);
  }

  refreshRecurringEventsCache(action, added = emptyArray$7, removed = emptyArray$7, replaced) {
    const me = this,
          {
      recurringEvents
    } = me,
          replacedCount = replaced === null || replaced === void 0 ? void 0 : replaced.length;

    switch (action) {
      case 'clear':
        recurringEvents.clear();
        break;
      // Add and remove

      case 'splice':
        // Handle replacement of records by instances with same ID
        if (replacedCount) {
          added = added.slice();
          removed = removed.slice();

          for (let i = 0; i < replacedCount; i++) {
            removed.push(replaced[i][0]);
            added.push(replaced[i][1]);
          }
        }

        const addedCount = added.length,
              removedCount = removed.length; // Track the recurring events we contain

        if (removedCount && recurringEvents.size) {
          for (let i = 0; i < removedCount; i++) {
            const outgoingEvent = removed[i];

            if (outgoingEvent.isRecurring) {
              recurringEvents.delete(outgoingEvent);
            }
          }
        } // Track the recurring events we contain

        if (addedCount) {
          for (let i = 0; i < addedCount; i++) {
            const newEvent = added[i]; // Allow easy access to recurring events

            if (newEvent.isRecurring) {
              recurringEvents.add(newEvent);
            }
          }
        }

        break;
    }
  }

  add(record) {
    // StoreCRUD#add always converts to array anyway, so do it first here
    // so that the occurrence check can be reliable.
    const records = Array.isArray(record) ? record : [record]; // Occurrences can never be in the store.
    // When an occurrence is edited, it becomes an exception
    // and is promoted to be a full event and THEN added.

    if (records.some(r => r.isOccurrence)) {
      throw new Error('Occurrences must not be added to the event store');
    } else {
      return super.add(records);
    }
  }

  getById(id) {
    let result = super.getById(id); // If the id is not found in the Store, then it could be one of our generated occurrences

    if (!result) {
      result = this.globalOccurrences.get(this.modelClass.asId(id));
    }

    return result;
  }

  onModelChange(record, toSet, wasSet, silent, fromRelationUpdate) {
    const isRecurrenceRelatedFieldChange = this.isRecurrenceRelatedFieldChange(record, wasSet); // If this is the base of a recurring sequence, then any reactors to events from
    // the super call must regenerate occurrences, so must be done at top.
    // If silent is true, occurrences won't be recalculated. Do not remove occurrences from cache in such case.

    if (isRecurrenceRelatedFieldChange && !silent) {
      record.removeOccurrences();
    }

    super.onModelChange(...arguments); // If this is the base of a recurring sequence, then the EventStore must
    // trigger a refresh event so that UIs refresh themselves.
    // This could be at the tail end of the creation of an exception
    // or a new recurring base.

    if (isRecurrenceRelatedFieldChange && !silent) {
      const event = {
        action: 'batch',
        records: this.storage.values
      };
      this.trigger('refresh', event);
      this.trigger('change', event);
    }
  }
  /**
   * The method restricts which field modifications should trigger timespan occurrences building.
   * By default any field change of a recurring timespan causes the rebuilding.
   * @param  {Scheduler.model.TimeSpan} timeSpan The modified timespan.
   * @param  {Object} wasSet Object containing the change set.
   * @return {Boolean} `True` if the fields modification should trigger the timespan occurrences rebuilding.
   * @internal
   */

  isRecurrenceRelatedFieldChange(timeSpan, wasSet) {
    return timeSpan.isRecurring || 'recurrenceRule' in wasSet;
  }
  /**
   * Builds occurrences for the provided timespan across the provided date range.
   * @private
   */

  getOccurrencesForTimeSpan(timeSpan, startDate, endDate) {
    const result = [];

    if (timeSpan.isRecurring) {
      timeSpan.recurrence.forEachOccurrence(startDate, endDate, r => result.push(r));
    }

    return result;
  }

  set data(data) {
    // All cached occurrences are now invalid with a new dataset
    this.globalOccurrences.clear();
    super.data = data;
  }
  /**
   * Returns all the recurring timespans.
   * @return {Scheduler.model.TimeSpan[]} Array of recurring events.
   */

  getRecurringTimeSpans() {
    return [...this.recurringEvents];
  }

});

/**
 * @module Scheduler/data/mixin/RecurringEventsMixin
 */

/**
 * This mixin class provides recurring events functionality to the {@link Scheduler.data.EventStore event store}.
 * @extends Scheduler/data/mixin/RecurringTimeSpansMixin
 * @mixin
 */

var RecurringEventsMixin = (Target => class RecurringEventsMixin extends RecurringTimeSpansMixin(Target || Base) {
  static get $name() {
    return 'RecurringEventsMixin';
  }
  /**
   * Returns all the recurring events.
   *
   * **An alias for ** {@link Scheduler.data.mixin.RecurringTimeSpansMixin#function-getRecurringTimeSpans} method.
   *
   * @return {Scheduler.model.EventModel[]} Array of recurring events.
   */

  getRecurringEvents() {
    return this.getRecurringTimeSpans();
  }

  isEventPersistable(event) {
    // occurrences are not persistable
    return super.isEventPersistable(event) && (!event.supportsRecurring || !event.isOccurrence);
  }

});

/**
 * @module Scheduler/model/TimeSpan
 */

/**
 * This class represent a simple date range. It is being used in various subclasses and plugins which operate on date ranges.
 *
 * Its a subclass of  {@link Core.data.Model}.
 * Please refer to documentation of those classes to become familiar with the base interface of this class.
 *
 * A TimeSpan has the following fields:
 *
 * - {@link #field-startDate}    - start date of the task in the ISO 8601 format
 * - {@link #field-endDate}      - end date of the task in the ISO 8601 format (not inclusive)
 * - {@link #field-duration}     - duration, time between start date and end date
 * - {@link #field-durationUnit} - unit used to express the duration
 * - {@link #field-name}         - an optional name of the range
 * - {@link #field-cls}          - an optional CSS class to be associated with the range.
 *
 * The data source of any field can be customized in the subclass. Please refer to {@link Core.data.Model} for details. To specify
 * another date format:
 *
 * ```javascript
 * class MyTimeSpan extends TimeSpan {
 *   static get fields() {
 *      { name: 'startDate', type: 'date', dateFormat: 'DD/MM/YY' }
 *   }
 * }
 * ```
 *
 * @extends Core/data/Model
 */

class TimeSpan extends Model {
  //region Field definitions
  static get fields() {
    return [
    /**
     * The start date of a time span (or Event / Task).
     *
     * Uses {@link Core/helper/DateHelper#property-defaultFormat-static DateHelper.defaultFormat} to convert a
     * supplied string to a Date. To specify another format, either change that setting or subclass TimeSpan and
     * change the dateFormat for this field.
     *
     * @field {String|Date} startDate
     * @category Scheduling
     */
    {
      name: 'startDate',
      type: 'date'
    },
    /**
     * The end date of a time span (or Event / Task).
     *
     * Uses {@link Core/helper/DateHelper#property-defaultFormat-static DateHelper.defaultFormat} to convert a
     * supplied string to a Date. To specify another format, either change that setting or subclass TimeSpan and
     * change the dateFormat for this field.
     *
     * @field {String|Date} endDate
     * @category Scheduling
     */
    {
      name: 'endDate',
      type: 'date'
    },
    /**
     * The numeric part of the timespan's duration (the number of units).
     * @field {Number} duration
     * @category Scheduling
     */
    {
      name: 'duration',
      type: 'number',
      allowNull: true
    },
    /**
     * The unit part of the TimeSpan duration, defaults to "d" (days). Valid values are:
     *
     * - "ms" (milliseconds)
     * - "s" (seconds)
     * - "m" (minutes)
     * - "h" (hours)
     * - "d" (days)
     * - "w" (weeks)
     * - "M" (months)
     * - "y" (years)
     *
     * This field is readonly after creation, to change durationUnit use #setDuration().
     * @field {String} durationUnit
     * @category Scheduling
     */
    {
      name: 'durationUnit',
      type: 'string',
      defaultValue: 'd'
    }, {
      name: 'fullDuration',
      persist: false
    },
    /**
     * An encapsulation of the CSS classes to add to the rendered time span element.
     * @field {Core.helper.util.DomClassList|String} cls
     *
     * This may be accessed as a string, but for granular control of adding and
     * removing individual classes, it is recommended to use the
     * {@link Core.helper.util.DomClassList DomClassList} API.
     * @category Styling
     */
    {
      name: 'cls',
      defaultValue: ''
    },
    /**
     * CSS class specifying an icon to apply to the rendered time span element.
     * @field {String} iconCls
     * @category Styling
     */
    'iconCls',
    /**
     * A CSS style string (applied to `style.cssText`) or object (applied to `style`)
     * ```
     * record.style = 'color: red;font-weight: 800';
     * ```
     *
     * @field {String} style
     * @category Styling
     */
    {
      name: 'style',
      type: 'object'
    },
    /**
     * The name of the time span (or Event / Task)
     * @field {String} name
     * @category Common
     */
    {
      name: 'name',
      type: 'string',
      defaultValue: ''
    }];
  } //endregion
  //region Init

  afterConstruct() {
    super.afterConstruct(); // This should probably be a property setter of some mandatory config, then we would not need an afterConfigure implementation.

    this.normalize();
  }
  /**
   * Returns the event store this event is part of.
   *
   * @property {Scheduler.data.EventStore}
   * @readonly
   */

  get eventStore() {
    const me = this; // If we are an occurrence, return our base recurring event's store

    if (me.isOccurrence) {
      return me.recurringTimeSpan.eventStore;
    }

    if (!me._eventStore) {
      var _me$stores;

      me._eventStore = (_me$stores = me.stores) === null || _me$stores === void 0 ? void 0 : _me$stores.find(s => s.isEventStore);
    }

    return me._eventStore;
  }

  normalize() {
    const me = this,
          {
      startDate,
      endDate,
      duration,
      durationUnit
    } = me,
          hasDuration = duration != null; // need to calculate duration (checking first since seemed most likely to happen)

    if (startDate && endDate && !hasDuration) {
      me.setData('duration', DateHelper.diff(startDate, endDate, durationUnit, true));
    } // need to calculate endDate?
    else if (startDate && !endDate && hasDuration) {
        me.setData('endDate', DateHelper.add(startDate, duration, durationUnit));
      } // need to calculate startDate
      else if (!startDate && endDate && hasDuration) {
          me.setData('startDate', DateHelper.add(endDate, -duration, durationUnit));
        }
  } //endregion
  //region Getters & Setters

  get cls() {
    if (!this._cls) {
      this._cls = new DomClassList(super.get('cls'));
    }

    return this._cls;
  }

  set cls(cls) {
    const me = this;

    if (me._cls) {
      me._cls.value = cls;
    } else {
      me._cls = new DomClassList(cls);
    }

    me.set('cls', me._cls.value);
  }

  get startDate() {
    return this.get('startDate');
  }

  set startDate(date) {
    this.setStartDate(date);
  }

  get endDate() {
    return this.get('endDate');
  }

  set endDate(date) {
    this.setEndDate(date);
  }

  get duration() {
    return this.get('duration');
  }

  set duration(duration) {
    this.setDuration(duration, this.durationUnit);
  }

  get durationUnit() {
    return this.get('durationUnit');
  }
  /**
   * Sets duration and durationUnit in one go. Only allowed way to change durationUnit, the durationUnit field is
   * readonly after creation
   * @param {Number} duration Duration value
   * @param {String} durationUnit Unit for specified duration value, see {@link #field-durationUnit} for valid values
   */

  setDuration(duration, durationUnit = this.durationUnit) {
    // Must be a number
    duration = parseFloat(duration);
    const toSet = {
      duration,
      durationUnit
    };

    if (this.startDate) {
      toSet.endDate = DateHelper.add(this.startDate, duration, durationUnit);
    } else if (this.endDate) {
      toSet.startDate = DateHelper.add(this.endDate, -duration, durationUnit);
    }

    this.set(toSet);
  }
  /**
   * Property which encapsulates the duration's magnitude and units.
   * @property {Core.data.Duration}
   */

  get fullDuration() {
    // Used for formatting during export
    return new Duration({
      unit: this.durationUnit,
      magnitude: this.duration
    });
  }

  set fullDuration(duration) {
    if (typeof duration === 'string') {
      duration = DateHelper.parseDuration(duration, true, this.durationUnit);
    }

    this.setDuration(duration.magnitude, duration.unit);
  }
  /**
   * Sets the range start date
   *
   * @param {Date} date The new start date
   * @param {Boolean} keepDuration Pass `true` to keep the duration of the task ("move" the event), `false` to change the duration ("resize" the event).
   * Defaults to `true`
   */

  setStartDate(date, keepDuration = true) {
    const me = this,
          toSet = {
      startDate: date
    };

    if (date) {
      let calcEndDate;

      if (keepDuration) {
        calcEndDate = me.duration != null;
      } else {
        if (me.endDate) {
          toSet.duration = DateHelper.diff(date, me.endDate, me.durationUnit, true);
          if (toSet.duration < 0) throw new Error('Negative duration');
        } else {
          calcEndDate = this.duration != null;
        }
      }

      if (calcEndDate) {
        toSet.endDate = DateHelper.add(date, me.duration, me.durationUnit);
      }
    } else {
      toSet.duration = null;
    }

    me.set(toSet);
  }
  /**
   * Sets the range end date
   *
   * @param {Date} date The new end date
   * @param {Boolean} keepDuration Pass `true` to keep the duration of the task ("move" the event), `false` to change the duration ("resize" the event).
   * Defaults to `false`
   */

  setEndDate(date, keepDuration = false) {
    const me = this,
          toSet = {
      endDate: date
    };

    if (date) {
      let calcStartDate;

      if (keepDuration === true) {
        calcStartDate = me.duration != null;
      } else {
        if (me.startDate) {
          toSet.duration = DateHelper.diff(me.startDate, date, me.durationUnit, true);
          if (toSet.duration < 0) throw new Error('Negative duration');
        } else {
          calcStartDate = this.duration != null;
        }
      }

      if (calcStartDate) {
        toSet.startDate = DateHelper.add(date, -me.duration, me.durationUnit);
      }
    }

    me.set(toSet);
  }
  /**
   * Sets the event start and end dates
   *
   * @param {Date} start The new start date
   * @param {Date} end The new end date
   * @param {Boolean} [silent] Pass `true` to not trigger events
   */

  setStartEndDate(start, end, silent) {
    this.set({
      startDate: start,
      endDate: end
    }, null, silent);
  }
  /**
   * Returns an array of dates in this range. If the range starts/ends not at the beginning of day, the whole day will be included.
   * @readonly
   * @property {Date[]}
   */

  get dates() {
    const dates = [],
          startDate = DateHelper.startOf(this.startDate, 'day'),
          endDate = this.endDate;

    for (let date = startDate; date < endDate; date = DateHelper.add(date, 1, 'day')) {
      dates.push(date);
    }

    return dates;
  }

  get startDateMS() {
    var _this$startDate;

    return (_this$startDate = this.startDate) === null || _this$startDate === void 0 ? void 0 : _this$startDate.getTime();
  }

  get endDateMS() {
    var _this$endDate;

    return (_this$endDate = this.endDate) === null || _this$endDate === void 0 ? void 0 : _this$endDate.getTime();
  }
  /**
   * Returns the duration of this Event in milliseconds.
   * @private
   */

  get durationMS() {
    const me = this;

    if (me.endDate && me.startDate) {
      return me.endDateMS - me.startDateMS;
    } else {
      return DateHelper.asMilliseconds(me.duration || 0, me.durationUnit);
    }
  }

  get isMilestone() {
    return this.durationMS === 0;
  }

  inSetNormalize(field) {
    if (typeof field !== 'string') {
      // If user is updating multiple properties in one go using an object, we help out
      // by filling out missing schedule related data
      let {
        startDate,
        endDate,
        duration,
        durationUnit
      } = field; // Conversion is usually handled in inSet, but we are normalizing prior to that and have to handle it here

      if (typeof startDate === 'string') {
        startDate = this.getFieldDefinition('startDate').convert(startDate);
      }

      if (typeof endDate === 'string') {
        endDate = this.getFieldDefinition('endDate').convert(endDate);
      }

      if ('duration' in field) {
        if (startDate && !endDate) {
          endDate = DateHelper.add(startDate, duration, durationUnit || this.durationUnit, true, true);
        }

        if (!startDate && endDate) {
          startDate = DateHelper.add(endDate, -duration, durationUnit || this.durationUnit, true, true);
        }
      } else if (startDate && endDate) {
        duration = DateHelper.diff(startDate, endDate, durationUnit || this.durationUnit, true);
      }

      startDate && (field.startDate = startDate);
      endDate && (field.endDate = endDate);
      duration != null && (field.duration = duration);
      return field;
    }
  }

  inSet(field, value, silent, fromRelationUpdate, skipAccessors) {
    if (!skipAccessors) {
      field = this.inSetNormalize(field) || field;
    }

    return super.inSet(field, value, silent, fromRelationUpdate, skipAccessors);
  } //endregion
  //region Iteration

  /**
   * Iterates over the {@link #property-dates}
   * @param {Function} func The function to call for each date
   * @param {Object} thisObj `this` reference for the function
   */

  forEachDate(func, thisObj) {
    return this.dates.forEach(func.bind(thisObj));
  } //endregion

  /**
   * Checks if the range record has both start and end dates set and start <= end
   *
   * @property {Boolean}
   */

  get isScheduled() {
    const me = this;
    return Boolean(me.startDate && me.endDate && me.hasValidDates);
  } // Simple check if end date is greater than start date

  get isValid() {
    let result = true; //super.isValid(),

    if (result) {
      const {
        startDate,
        endDate
      } = this;
      result = !startDate || !endDate || endDate - startDate >= 0;
    }

    return result;
  } // Simple check if just end date is greater than start date

  get hasValidDates() {
    const {
      startDateMS,
      endDateMS
    } = this;
    return !startDateMS || !endDateMS || endDateMS - startDateMS >= 0;
  }
  /**
   * Shift the dates for the date range by the passed amount and unit
   * @param {String} unit The unit to shift by, see {@link Core.helper.DateHelper} for more information on valid formats.
   * @param {Number} amount The amount to shift
   */

  shift(amount, unit = this.durationUnit) {
    // TODO REMOVE FOR 2.0
    if (typeof amount === 'string') {
      const u = amount;
      amount = unit;
      unit = u;
    }

    return this.setStartDate(DateHelper.add(this.startDate, amount, unit, true), true);
  }
  /**
   * Returns the WBS code of this model (e.g '2.1.3'). Only relevant when part of a tree store, as in the Gantt chart.
   * @property {String}
   */

  get wbsCode() {
    return this._wbsCode || this.indexPath.join('.');
  }

  set wbsCode(value) {
    // wbsCode needs to be writable to interop w/TaskModel and Baselines which copy this field value
    this._wbsCode = value;
  }

  fullCopy() {
    //NOT PORTED
    return this.copy.apply(this, arguments);
  }

  intersects(timeSpan) {
    return this.intersectsRange(timeSpan.startDate, timeSpan.endDate);
  }

  intersectsRange(start, end) {
    const myStart = this.startDate,
          myEnd = this.endDate;
    return myStart && myEnd && DateHelper.intersectSpans(myStart, myEnd, start, end);
  }
  /**
   * Splits this event into two pieces at the desired position.
   *
   * @param {Number} splitPoint A number greater than 0 and less than 1, indicating how this event will be split. 0.5 means cut it in half
   * @return {Scheduler.model.TimeSpan} The newly created split section of the timespan
   */

  split(splitPoint = 0.5) {
    const me = this,
          clone = me.copy(),
          {
      eventStore,
      assignmentStore
    } = me,
          ownNewDuration = me.duration * splitPoint,
          cloneDuration = me.duration - ownNewDuration;

    if (splitPoint <= 0 || splitPoint >= 1) {
      throw new Error('Split point must be > 0 and < 1');
    }

    clone.startDate = DateHelper.add(me.startDate, ownNewDuration, me.durationUnit);
    clone.duration = cloneDuration;
    me.duration = ownNewDuration;

    if (eventStore) {
      eventStore.add(clone);

      if (assignmentStore && !eventStore.usesSingleAssignment) {
        assignmentStore.add(me.assignments.map(assignment => {
          const clonedData = Object.assign({}, assignment.data, {
            eventId: clone.id,
            // From engine
            event: null,
            resource: null
          });
          delete clonedData.id;
          return clonedData;
        }));
      }
    }

    return clone;
  }

  toICSString(icsEventConfig = {}) {
    if (!this.isScheduled) {
      return '';
    }

    const nowAsUTC = DateHelper.toUTC(new Date()),
          startAsUTC = DateHelper.toUTC(this.startDate),
          endAsUTC = DateHelper.toUTC(this.endDate),
          fullDateFormat = 'YYYYMMDDTHHmmss',
          // To allow testing using a fixed timestamp value
    timestamp = icsEventConfig.DTSTAMP || DateHelper.format(nowAsUTC, fullDateFormat) + 'Z';
    delete icsEventConfig.DTSTAMP;
    let startEnd = {};

    if (this.allDay) {
      const dateFormat = 'YYYYMMDD';
      startEnd = {
        'DTSTART;VALUE=DATE': DateHelper.format(startAsUTC, dateFormat),
        'DTEND;VALUE=DATE': DateHelper.format(endAsUTC, dateFormat)
      };
    } else {
      startEnd = {
        DTSTART: DateHelper.format(startAsUTC, fullDateFormat) + 'Z',
        DTEND: DateHelper.format(endAsUTC, fullDateFormat) + 'Z'
      };
    }

    const version = VersionHelper.scheduler && VersionHelper.getVersion('scheduler') || VersionHelper.calendar && VersionHelper.getVersion('calendar') || '',
          icsWrapConfig = {
      BEGIN: 'VCALENDAR',
      VERSION: '2.0',
      CALSCALE: 'GREGORIAN',
      PRODID: `-//Bryntum AB//Bryntum Scheduler ${version} //EN`,
      END: 'VCALENDAR'
    },
          eventConfig = {
      BEGIN: 'VEVENT',
      UID: this.id + '@bryntum.com',
      CLASS: 'PUBLIC',
      SUMMARY: this.name,
      DTSTAMP: timestamp,
      ...startEnd,
      ...(this.recurrenceRule ? {
        RRULE: this.recurrenceRule
      } : {}),
      ...icsEventConfig,
      END: 'VEVENT'
    },
          icsItems = Object.keys(icsWrapConfig).map(key => `${key}:${icsWrapConfig[key]}`),
          eventItems = Object.keys(eventConfig).map(key => `${key}:${eventConfig[key]}`); // Inject event details before the closing VCALENDAR entry

    icsItems.splice(icsItems.length - 1, 0, ...eventItems);
    return icsItems.join('\n');
  }
  /**
   * Triggers a download of this time span in ICS format (for import in Outlook etc.)
   *
   * ```javascript
   * timeSpan.downloadAsICS({
   *      LOCATION : timeSpan.location
   *  });
   * ```
   * @param {Object} [icsEventConfig] A config object with properties to be added in to `BEGIN:VEVENT` section of the
   * exported event.
   */

  exportToICS(icsEventConfig) {
    if (this.isScheduled) {
      const blob = new Blob([this.toICSString(icsEventConfig)], {
        type: 'text/calendar'
      });
      BrowserHelper.downloadBlob(blob, (this.name || 'Event') + '.ics');
    }
  }
  /**
   * Defines if the given event field should be manually editable in UI.
   * You can override this method to provide your own logic.
   *
   * By default the method defines all the event fields as editable.
   *
   * @param {String} fieldName Name of the field
   * @returns {Boolean} Returns `true` if the field is editable, `false` if it is not and `undefined` if the model has no such field.
   */

  isEditable(fieldName) {
    // return undefined for unknown fields
    return this.getFieldDefinition(fieldName) ? true : undefined;
  }

}
TimeSpan._$name = 'TimeSpan';

const dayParseRegExp = /^([+-]?[0-9])?(SU|MO|TU|WE|TH|FR|SA)$/;
const days = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];
class RecurrenceDayRuleEncoder extends Base {
  static decodeDay(rawDay) {
    let parsedDay, result;

    if (parsedDay = dayParseRegExp.exec(rawDay)) {
      result = [days.indexOf(parsedDay[2])]; // optional position number

      if (parsedDay[1]) {
        parsedDay[1] = parseInt(parsedDay[1], 10);
        result.push(parsedDay[1]);
      }
    }

    return result;
  }

  static encodeDay(day) {
    let position; // support decodeDay() result format

    if (Array.isArray(day)) {
      [day, position] = day;
    }

    return (position ? position.toString() : '') + days[day];
  } // Turns days values provided as an array of strings (like [`-1MO`, `SU`, `+3FR`])
  // into an array of [ dayIndex, position ] elements, where:
  //
  // - `dayIndex` - zero-based week day index value (0 - Sunday, 1 - Monday, 2 - Tuesday, etc.)
  // - `position` - (optional) 1-based position of the day (integer value (can be both positive and negative))

  static decode(rawDays) {
    let result = [],
        parsedDay;

    if (rawDays) {
      for (let i = 0; i < rawDays.length; i++) {
        if (parsedDay = this.decodeDay(rawDays[i])) {
          result.push(parsedDay);
        }
      }
    }

    return result;
  }

  static encode(days) {
    let result = [],
        day;

    if (days) {
      for (let i = 0; i < days.length; i++) {
        if (day = this.encodeDay(days[i])) {
          result.push(day);
        }
      }
    }

    return result;
  }

}
RecurrenceDayRuleEncoder._$name = 'RecurrenceDayRuleEncoder';

/**
 * @module Scheduler/data/util/recurrence/DailyRecurrenceIterator
 */
/**
 * A class which provides iteration to call a function for dates specified by a
 * {@link Scheduler.model.RecurrenceModel RecurrenceModel} over a specified date range.
 * @private
 */

class DailyRecurrenceIterator extends AbstractRecurrenceIterator {
  static get frequency() {
    return 'DAILY';
  }
  /**
   * Iterates over the passed date range, calling the passed callback on each date on which
   * starts a recurring event which matches the passed recurrence rule and overlaps the start and end dates
   * and is not an {@link Scheduler.model.mixin.RecurringTimeSpan#field-exceptionDates exceptionDate}
   * in the recurring event.
   * @param {Object} config An object which describes how to iterate.
   * @param {Date} config.startDate The point in time to begin iteration.
   * @param {Date} config.endDate The point in time to end iteration.
   * @param {Boolean} [config.startOnly] By default, all occurrences which intersect the date range
   * will be visited. Pass `true` to only visit occurrences which *start* in the date range.
   * @param {Scheduler.model.RecurrenceModel} config.recurrence The point in time to end iteration.
   * @param {Function} config.fn The function to call for each date which matches the recurrence in the date range.
   * @param {Date} config.fn.date The occurrence date.
   * @param {Number} config.fn.counter A counter of how many dates have been visited in this iteration.
   * @param {Boolean} config.fn.isFirst A flag which is `true` if the date is the first occurrence in the specified recurrence rule.
   * @param {Array} [config.extraArgs] Extra arguments to pass to the callback after the `isFirst` argument.
   */

  static forEachDate(config) {
    const {
      startOnly,
      startDate,
      endDate,
      timeSpan,
      timeSpanStart,
      earliestVisibleDate,
      durationMS,
      spansStart,
      recurrence,
      fn,
      extraArgs,
      scope = this
    } = this.processIterationConfig(config),
          {
      interval
    } = recurrence,
          delay = startDate - timeSpanStart,
          // recurrence interval duration in ms (86400000 is a single day duration in ms)
    intervalDuration = interval * 86400000,
          delayInIntervals = Math.floor(delay / intervalDuration);
    let count = recurrence.count,
        counter = 0,
        occurrenceDate = DateHelper.add(timeSpanStart, delayInIntervals * interval, 'day');

    if (!endDate && !count) {
      count = this.MAX_OCCURRENCES_COUNT;
    }

    while (!endDate || occurrenceDate <= endDate) {
      const inView = this.isInView(startOnly, occurrenceDate, earliestVisibleDate, durationMS, timeSpan);
      counter++;

      if (inView && (endDate && occurrenceDate > endDate || fn.apply(scope, [occurrenceDate, counter, counter === 1 && spansStart, timeSpan, ...extraArgs]) === false || count && counter >= count)) {
        break;
      } // shift to the next day

      occurrenceDate = DateHelper.add(occurrenceDate, interval, 'day');
    }
  }

}
DailyRecurrenceIterator._$name = 'DailyRecurrenceIterator';

/**
 * @module Scheduler/data/util/recurrence/WeeklyRecurrenceIterator
 */
/**
 * A class which provides iteration to call a function for dates specified by a
 * {@link Scheduler.model.RecurrenceModel RecurrenceModel} over a specified date range.
 * @private
 */

class WeeklyRecurrenceIterator extends AbstractRecurrenceIterator {
  static get frequency() {
    return 'WEEKLY';
  }
  /**
   * Iterates over the passed date range, calling the passed callback on each date on which
   * starts an event which matches the passed recurrence rule and overlaps the start and end dates.
   * @param {Object} config An object which describes how to iterate.
   * @param {Date} config.startDate The point in time to begin iteration.
   * @param {Date} config.endDate The point in time to end iteration.
   * @param {Boolean} [config.startOnly] By default, all occurrences which intersect the date range
   * will be visited. Pass `true` to only visit occurrences which *start* in the date range.
   * @param {Scheduler.model.RecurrenceModel} config.recurrence The point in time to end iteration.
   * @param {Function} config.fn The function to call for each date which matches the recurrence in the date range.
   * @param {Date} config.fn.date The occurrence date.
   * @param {Number} config.fn.counter A counter of how many dates have been visited in this iteration.
   * @param {Boolean} config.fn.isFirst A flag which is `true` if the date is the first occurrence in the specified recurrence rule.
   * @param {Array} [config.extraArgs] Extra arguments to pass to the callback after the `isFirst` argument.
   */

  static forEachDate(config) {
    const {
      startOnly,
      startDate,
      endDate,
      timeSpan,
      timeSpanStart,
      earliestVisibleDate,
      durationMS,
      spansStart,
      recurrence,
      fn,
      extraArgs,
      scope = this
    } = this.processIterationConfig(config),
          {
      interval,
      days
    } = recurrence;
    let counter = 0,
        {
      count
    } = recurrence,
        weekDays = RecurrenceDayRuleEncoder.decode(days),
        weekStartDate,
        occurrenceDate; // days could be provided in any order so it's important to sort them

    if (weekDays && weekDays.length) {
      weekDays.sort((a, b) => a[0] - b[0]);
    } // "Days" might be skipped then we use the event start day
    else {
        weekDays = [[timeSpanStart.getDay()]];
      } // if the recurrence is limited w/ "Count"
    // we need to 1st count passed occurrences so we always start iteration from the event start date

    weekStartDate = DateHelper.getNext(count ? timeSpanStart : startDate, 'week', 0, 0);

    if (!endDate && !count) {
      count = this.MAX_OCCURRENCES_COUNT;
    }

    while (!endDate || weekStartDate <= endDate) {
      for (let i = 0; i < weekDays.length; i++) {
        occurrenceDate = DateHelper.copyTimeValues(DateHelper.add(weekStartDate, weekDays[i][0], 'day'), timeSpanStart);

        if (occurrenceDate >= timeSpanStart) {
          const inView = this.isInView(startOnly, occurrenceDate, earliestVisibleDate, durationMS, timeSpan);
          counter++;

          if (inView && (endDate && occurrenceDate > endDate || fn.apply(scope, [occurrenceDate, counter, counter === 1 && spansStart, timeSpan, ...extraArgs]) === false || count && counter >= count)) {
            return;
          }
        }
      } // get next week start

      weekStartDate = DateHelper.getNext(weekStartDate, 'week', interval, 0);
    }
  }

}
WeeklyRecurrenceIterator._$name = 'WeeklyRecurrenceIterator';

/**
 * @module Scheduler/data/util/recurrence/MonthlyRecurrenceIterator
 */
/**
 * A class which provides iteration to call a function for dates specified by a
 * {@link Scheduler.model.RecurrenceModel RecurrenceModel} over a specified date range.
 * @private
 */

class MonthlyRecurrenceIterator extends AbstractRecurrenceIterator {
  static get frequency() {
    return 'MONTHLY';
  }

  static getNthDayOfMonth(date, dayNum) {
    const daysInMonth = DateHelper.daysInMonth(date);
    let result = null;

    if (dayNum && Math.abs(dayNum) <= daysInMonth) {
      result = new Date(date.getFullYear(), date.getMonth(), dayNum < 0 ? daysInMonth + dayNum + 1 : dayNum);
    }

    return result;
  }

  static isValidPosition(position) {
    return position && Math.abs(position) > 0 && Math.abs(position) <= 31;
  }
  /**
   * Iterates over the passed date range, calling the passed callback on each date on which
   * starts an event which matches the passed recurrence rule and overlaps the start and end dates.
   * @param {Object} config An object which describes how to iterate.
   * @param {Date} config.startDate The point in time to begin iteration.
   * @param {Date} config.endDate The point in time to end iteration.
   * @param {Boolean} [config.startOnly] By default, all occurrences which intersect the date range
   * will be visited. Pass `true` to only visit occurrences which *start* in the date range.
   * @param {Scheduler.model.RecurrenceModel} config.recurrence The point in time to end iteration.
   * @param {Function} config.fn The function to call for each date which matches the recurrence in the date range.
   * @param {Date} config.fn.date The occurrence date.
   * @param {Number} config.fn.counter A counter of how many dates have been visited in this iteration.
   * @param {Boolean} config.fn.isFirst A flag which is `true` if the date is the first occurrence in the specified recurrence rule.
   * @param {Array} [config.extraArgs] Extra arguments to pass to the callback after the `isFirst` argument.
   */

  static forEachDate(config) {
    const {
      startOnly,
      startDate,
      endDate,
      timeSpan,
      timeSpanStart,
      earliestVisibleDate,
      durationMS,
      spansStart,
      recurrence,
      fn,
      extraArgs,
      scope = this
    } = this.processIterationConfig(config),
          {
      interval,
      days,
      count,
      positions
    } = recurrence,
          weekDays = RecurrenceDayRuleEncoder.decode(days),
          hasPositions = positions && positions.length,
          processedDate = {};
    let {
      monthDays
    } = recurrence,
        counter = 0,
        weekDayPosition,
        monthStartDate,
        monthEndDate,
        dates,
        occurrenceDate,
        i; // If the recurrence is limited w/ "Count", we need to 1st count passed
    // occurrences so we always start iteration from the event start date

    monthStartDate = DateHelper.startOf(count ? timeSpanStart : startDate, 'month');
    monthEndDate = new Date(DateHelper.getNext(monthStartDate, 'month', 1) - 1); // If no month days nor week days are provided let's use event start date month day

    if (!(monthDays && monthDays.length) && !(weekDays && weekDays.length)) {
      monthDays = [timeSpanStart.getDate()];
    }

    if (weekDays && weekDays.length) {
      // Collect hash of positions indexed by week days
      weekDays.forEach(day => {
        if (day[1]) {
          weekDayPosition = weekDayPosition || {};
          weekDayPosition[day[0]] = day[1];
        }
      });
    }

    while ((!endDate || endDate >= monthStartDate) && (!count || counter < count)) {
      dates = [];

      if (weekDays && weekDays.length) {
        weekDays.forEach(day => {
          const weekDay = day[0];
          let from = 1,
              till = 53; // if position provided

          if (day[1]) {
            from = till = day[1];
          }

          for (i = from; i <= till; i++) {
            if (occurrenceDate = this.getNthDayInPeriod(monthStartDate, monthEndDate, weekDay, i)) {
              occurrenceDate = DateHelper.copyTimeValues(occurrenceDate, timeSpanStart);

              if (!processedDate[occurrenceDate.getTime()]) {
                // remember we processed the date
                processedDate[occurrenceDate.getTime()] = true;
                dates.push(occurrenceDate);
              }
            }
          }
        });
        dates.sort((a, b) => a - b);

        if (!hasPositions) {
          for (i = 0; i < dates.length; i++) {
            occurrenceDate = dates[i];

            if (occurrenceDate >= timeSpanStart) {
              const inView = this.isInView(startOnly, occurrenceDate, earliestVisibleDate, durationMS, timeSpan);
              counter++;

              if (inView && (endDate && occurrenceDate > endDate || fn.apply(scope, [occurrenceDate, counter, counter === 1 && spansStart, timeSpan, ...extraArgs]) === false || count && counter >= count)) {
                return false;
              }
            }
          }
        }
      } else {
        const sortedMonthDates = [];

        for (i = 0; i < monthDays.length; i++) {
          // check if the date wasn't iterated over yet
          if ((occurrenceDate = this.getNthDayOfMonth(monthStartDate, monthDays[i])) && !processedDate[occurrenceDate.getTime()]) {
            processedDate[occurrenceDate.getTime()] = true;
            sortedMonthDates.push(occurrenceDate);
          }
        } // it's important to sort the dates to iterate over them in the proper order

        sortedMonthDates.sort((a, b) => a - b);

        for (i = 0; i < sortedMonthDates.length; i++) {
          occurrenceDate = DateHelper.copyTimeValues(sortedMonthDates[i], timeSpanStart);

          if (hasPositions) {
            dates.push(occurrenceDate);
          } else if (occurrenceDate >= timeSpanStart) {
            const inView = this.isInView(startOnly, occurrenceDate, earliestVisibleDate, durationMS, timeSpan);
            counter++;

            if (inView && ( // eslint-disable-next-line no-labels
            endDate && occurrenceDate > endDate || fn.apply(scope, [occurrenceDate, counter, counter === 1 && spansStart, timeSpan, ...extraArgs]) === false || count && counter >= count)) {
              return;
            }
          }
        }
      }

      if (hasPositions && dates.length) {
        this.forEachDateAtPositions(dates, positions, occurrenceDate => {
          if (occurrenceDate >= timeSpanStart) {
            const inView = startOnly ? occurrenceDate >= earliestVisibleDate : occurrenceDate.valueOf() + durationMS > earliestVisibleDate && !timeSpan.hasException(occurrenceDate);
            counter++; // Ignore dates outside of the [startDate, endDate] range

            if (inView && (!endDate || occurrenceDate <= endDate) && ( // return false if it's time to stop recurring
            fn.apply(scope, [occurrenceDate, counter, counter === 1 && spansStart, timeSpan, ...extraArgs]) === false || count && counter >= count)) {
              return false;
            }
          }
        });
      } // get next month start

      monthStartDate = DateHelper.getNext(monthStartDate, 'month', interval);
      monthEndDate = new Date(DateHelper.getNext(monthStartDate, 'month', 1) - 1);
    }
  }

}
MonthlyRecurrenceIterator._$name = 'MonthlyRecurrenceIterator';

/**
 * @module Scheduler/data/util/recurrence/YearlyRecurrenceIterator
 */
/**
 * A class which provides iteration to call a function for dates specified by a
 * {@link Scheduler.model.RecurrenceModel RecurrenceModel} over a specified date range.
 * @private
 */

class YearlyRecurrenceIterator extends AbstractRecurrenceIterator {
  static get frequency() {
    return 'YEARLY';
  }
  /**
   * Iterates over the passed date range, calling the passed callback on each date on which
   * starts an event which matches the passed recurrence rule and overlaps the start and end dates.
   * @param {Object} config An object which describes how to iterate.
   * @param {Date} config.startDate The point in time to begin iteration.
   * @param {Date} config.endDate The point in time to end iteration.
   * @param {Boolean} [config.startOnly] By default, all occurrences which intersect the date range
   * will be visited. Pass `true` to only visit occurrences which *start* in the date range.
   * @param {Scheduler.model.RecurrenceModel} config.recurrence The point in time to end iteration.
   * @param {Function} config.fn The function to call for each date which matches the recurrence in the date range.
   * @param {Date} config.fn.date The occurrence date.
   * @param {Number} config.fn.counter A counter of how many dates have been visited in this iteration.
   * @param {Boolean} config.fn.isFirst A flag which is `true` if the date is the first occurrence in the specified recurrence rule.
   * @param {Array} [config.extraArgs] Extra arguments to pass to the callback after the `isFirst` argument.
   */

  static forEachDate(config) {
    const {
      startOnly,
      startDate,
      endDate,
      timeSpan,
      timeSpanStart,
      earliestVisibleDate,
      durationMS,
      spansStart,
      recurrence,
      fn,
      extraArgs,
      scope = this
    } = this.processIterationConfig(config),
          {
      interval,
      days,
      count,
      positions
    } = recurrence,
          weekDays = RecurrenceDayRuleEncoder.decode(days),
          hasPositions = positions && positions.length,
          processedDate = {};
    let {
      months
    } = recurrence,
        counter = 0,
        i,
        occurrenceDate,
        dates,
        yearStartDate,
        yearEndDate,
        weekDayPosition; // if the recurrence is limited w/ "Count"
    // we need to 1st count passed occurrences so we always start iteration from the event start date

    yearStartDate = DateHelper.startOf(count ? timeSpanStart : startDate, 'year');
    yearEndDate = new Date(DateHelper.getNext(yearStartDate, 'year', 1) - 1);
    months && months.sort((a, b) => a - b); // if no months provided let's use the event month

    if (!(months && months.length) && !(weekDays && weekDays.length)) {
      months = [timeSpanStart.getMonth() + 1];
    }

    if (weekDays && weekDays.length) {
      // Collect hash of positions indexed by week days
      weekDays.forEach(day => {
        if (day[1]) {
          weekDayPosition = weekDayPosition || {};
          weekDayPosition[day[0]] = day[1];
        }
      });
    }

    while ((!endDate || endDate >= yearStartDate) && (!count || counter < count)) {
      dates = [];

      if (weekDays && weekDays.length) {
        weekDays.forEach(day => {
          let weekDay = day[0],
              from = 1,
              till = 53; // if position provided

          if (day[1]) {
            from = till = day[1];
          }

          for (i = from; i <= till; i++) {
            if (occurrenceDate = this.getNthDayInPeriod(yearStartDate, yearEndDate, weekDay, i)) {
              occurrenceDate = DateHelper.copyTimeValues(occurrenceDate, timeSpanStart);

              if (!processedDate[occurrenceDate.getTime()]) {
                // remember we processed the date
                processedDate[occurrenceDate.getTime()] = true;
                dates.push(occurrenceDate);
              }
            }
          }
        });
        dates.sort((a, b) => a - b);

        if (!hasPositions) {
          for (i = 0; i < dates.length; i++) {
            occurrenceDate = dates[i];

            if (occurrenceDate >= timeSpanStart) {
              const inView = this.isInView(startOnly, occurrenceDate, earliestVisibleDate, durationMS, timeSpan);
              counter++;

              if (inView && (endDate && occurrenceDate > endDate || fn.apply(scope, [occurrenceDate, counter, counter === 1 && spansStart, timeSpan, ...extraArgs]) === false || count && counter >= count)) {
                return;
              }
            }
          }
        }
      } else {
        for (i = 0; i < months.length; i++) {
          if (occurrenceDate = this.buildDate(yearStartDate.getFullYear(), months[i] - 1, timeSpanStart.getDate())) {
            occurrenceDate = DateHelper.copyTimeValues(occurrenceDate, timeSpanStart); // check if the date wasn't iterated over yet

            if (!processedDate[occurrenceDate.getTime()]) {
              processedDate[occurrenceDate.getTime()] = true;

              if (hasPositions) {
                dates.push(occurrenceDate);
              } else if (occurrenceDate >= timeSpanStart) {
                const inView = startOnly ? occurrenceDate >= earliestVisibleDate : occurrenceDate.valueOf() + durationMS > earliestVisibleDate && !timeSpan.hasException(occurrenceDate);
                counter++;

                if (inView && (endDate && occurrenceDate > endDate || fn.apply(scope, [occurrenceDate, counter, counter === 1 && spansStart, timeSpan, ...extraArgs]) === false || count && counter >= count)) {
                  return;
                }
              }
            }
          }
        }
      }

      if (hasPositions && dates.length) {
        this.forEachDateAtPositions(dates, positions, occurrenceDate => {
          if (occurrenceDate >= timeSpanStart) {
            const inView = startOnly ? occurrenceDate >= earliestVisibleDate : occurrenceDate.valueOf() + durationMS > earliestVisibleDate && !timeSpan.hasException(occurrenceDate);
            counter++; // Ignore dates outside of the [startDate, endDate] range

            if (inView && (!endDate || occurrenceDate <= endDate)) {
              // return false if it's time to stop recurring
              if (fn.apply(scope, [occurrenceDate, counter, counter === 1 && spansStart, timeSpan, ...extraArgs]) === false || count && counter >= count) {
                return false;
              }
            }
          }
        });
      } // get next month start

      yearStartDate = DateHelper.getNext(yearStartDate, 'year', interval);
      yearEndDate = new Date(DateHelper.getNext(yearStartDate, 'year', 1) - 1);
    }
  }

}
YearlyRecurrenceIterator._$name = 'YearlyRecurrenceIterator';

/**
 * @module Scheduler/model/RecurrenceModel
 */

const recurrenceIterators = {};
[DailyRecurrenceIterator, WeeklyRecurrenceIterator, MonthlyRecurrenceIterator, YearlyRecurrenceIterator].forEach(it => {
  recurrenceIterators[it.frequency] = it;
});

function convertStringOfIntegerItemsValue(value) {
  if (value) {
    if (typeof value == 'string') {
      value = value.split(',').map(item => parseInt(item, 10));
    }
  } else {
    value = null;
  }

  return value;
}

function convertStringOfItemsValue(value) {
  if (value) {
    if (typeof value == 'string') {
      value = value.split(',');
    }
  } else {
    value = null;
  }

  return value;
}

function isEqualAsString(value1, value2) {
  return String(value1) === String(value2);
}

function convertInteger(value) {
  if (this.defaultValue && value === undefined) {
    return this.defaultValue;
  }

  if (this.allowNull && value == null) {
    return null;
  }

  value = parseInt(value);
  return isNaN(value) ? undefined : value;
}
/**
 * This class represents a timespan recurrence settings.
 * It is a subclass of {@link Core.data.Model} class.
 * Please refer to the documentation for that class to become familiar with the base interface of this class.
 *
 * The data source for these fields can be customized by subclassing this class.
 *
 * @extends Core/data/Model
 */

class RecurrenceModel extends Model {
  /**
   * Indicates that this is a `RecurrenceModel` class instance
   * (allows to avoid using `instanceof`).
   * @property {Boolean}
   * @readonly
   */
  get isRecurrenceModel() {
    return true;
  } //region Fields

  static get fields() {
    return [
    /**
     * Field defines the recurrence frequency. Supported values are: `DAILY`, `WEEKLY`, `MONTHLY`, `YEARLY`.
     * @field {String} frequency
     */
    {
      name: 'frequency',
      defaultValue: 'DAILY'
    },
    /**
     * Field defines how often the recurrence repeats.
     * For example, if the recurrence is weekly its interval is 2, then the timespan repeats every two weeks.
     * @field {number} interval
     */
    {
      name: 'interval',
      defaultValue: 1,
      convert: convertInteger
    },
    /**
     * End date of the recurrence. Specifies when the recurrence ends.
     * The value is optional, the recurrence can as well be stopped using {@link #field-count} field value.
     * @field {Date} endDate
     */
    {
      name: 'endDate',
      type: 'date'
    },
    /**
     * Specifies the number of occurrences after which the recurrence ends.
     * The value includes the associated timespan itself so values less than 2 make no sense.
     * The field is optional, the recurrence as well can be stopped using {@link #field-endDate} field value.
     * @field {number} count
     */
    {
      name: 'count',
      allowNull: true,
      convert: convertInteger
    },
    /**
     * Specifies days of the week on which the timespan should occur.
     * An array of string values `SU`, `MO`, `TU`, `WE`, `TH`, `FR`, `SA`
     * corresponding to Sunday, Monday, Tuesday, Wednesday, Thursday, Friday, and Saturday days of the week.
     * Each value can also be preceded by a positive (+n) or negative (-n) integer.
     * If present, this indicates the nth occurrence of a specific day within the monthly or yearly recurrence.
     *
     * **Not applicable** for daily {@link #field-frequency}.
     * @field {String[]} days
     */
    {
      name: 'days',
      convert: convertStringOfItemsValue,
      isEqual: isEqualAsString
    },
    /**
     * Specifies days of the month on which the timespan should occur.
     * An array of integer values (-31..-1 - +1..+31, negative values mean counting backwards from the month end).
     * **Applicable only** for monthly {@link #field-frequency}.
     * @field {number[]} monthDays
     */
    {
      name: 'monthDays',
      convert: convertStringOfIntegerItemsValue,
      isEqual: isEqualAsString
    },
    /**
     * Specifies months of the year on which the timespan should occur.
     * An array of integer values (1 - 12).
     * **Applicable only** for yearly {@link #field-frequency}.
     * @field {number[]} months
     */
    {
      name: 'months',
      convert: convertStringOfIntegerItemsValue,
      isEqual: isEqualAsString
    },
    /**
     * The positions to include in the recurrence. The values operate on a set of recurrence instances **in one interval** of the recurrence rule.
     * An array of integer values (valid values are 1 to 366 or -366 to -1, negative values mean counting backwards from the end of the built list of occurrences).
     * **Not applicable** for daily {@link #field-frequency}.
     * @field {number} positions
     */
    {
      name: 'positions',
      convert: convertStringOfIntegerItemsValue,
      isEqual: isEqualAsString
    }];
  }

  get dateFormat() {
    return this._dateFormat || 'YYYYMMDDTHHmmss';
  }

  set dateFormat(format) {
    this._dateFormat = format;
  }

  get recurrenceIterator() {
    return recurrenceIterators[this.frequency];
  }
  /**
   * The timespan this recurrence is associated with.
   * @property {Scheduler.model.TimeSpan}
   */

  get timeSpan() {
    return this._timeSpan;
  }

  set timeSpan(value) {
    this._timeSpan = value;
  }
  /**
   * The recurrence rule. A string in [RFC-5545](https://tools.ietf.org/html/rfc5545#section-3.3.10) described format ("RRULE" expression).
   * @property {String}
   */

  get rule() {
    const me = this,
          result = [];

    if (me.frequency) {
      result.push(`FREQ=${me.frequency}`);

      if (me.interval > 1) {
        result.push(`INTERVAL=${me.interval}`);
      }

      if (me.days && me.days.length) {
        result.push('BYDAY=' + me.days.join(','));
      }

      if (me.monthDays && me.monthDays.length) {
        result.push('BYMONTHDAY=' + me.monthDays.join(','));
      }

      if (me.months && me.months.length) {
        result.push('BYMONTH=' + me.months.join(','));
      }

      if (me.count) {
        result.push(`COUNT=${me.count}`);
      }

      if (me.endDate) {
        result.push('UNTIL=' + DateHelper.format(me.endDate, me.dateFormat));
      }

      if (me.positions && me.positions.length) {
        result.push('BYSETPOS=' + me.positions.join(','));
      }
    }

    return result.join(';');
  }

  set rule(rule) {
    const me = this;
    me.beginBatch();

    if (rule) {
      const parts = rule.split(';');

      for (let i = 0, len = parts.length; i < len; i++) {
        const part = parts[i].split('='),
              value = part[1];

        switch (part[0]) {
          case 'FREQ':
            me.frequency = value;
            break;

          case 'INTERVAL':
            me.interval = value;
            break;

          case 'COUNT':
            me.count = value;
            me.until = null;
            break;

          case 'UNTIL':
            if (value) {
              me.endDate = DateHelper.parse(value, me.dateFormat);
            } else {
              me.endDate = null;
            }

            me.count = null;
            break;

          case 'BYDAY':
            me.days = value;
            break;

          case 'BYMONTHDAY':
            me.monthDays = value;
            break;

          case 'BYMONTH':
            me.months = value;
            break;

          case 'BYSETPOS':
            me.positions = value;
            break;
        }
      }

      me.sanitize();
    } else {
      me.set({
        frequency: null,
        interval: null,
        count: null,
        endDate: null,
        days: null,
        monthDays: null,
        months: null,
        positions: null
      });
    }

    me.endBatch();
  }

  construct(data = {}) {
    const me = this,
          {
      rule,
      timeSpan
    } = data;
    me._suspendedTimeSpanNotifying = 0;
    delete data.timeSpan;
    delete data.rule;
    super.construct(...arguments);

    if (rule) {
      me.suspendTimeSpanNotifying();
      me.rule = rule;
      me.resumeTimeSpanNotifying();
    }

    me.timeSpan = timeSpan;
  }
  /**
   * Iterate occurrences for the owning timespan across the specified date range. This method can be called even
   * if the timespan is not yet a member of a store, however, the occurrences returned will not be cached across
   * subsequent calls to this method.
   * @param {Date} startDate The start date of the iteration.
   * @param {Date} endDate The end date of the iteration.
   * @param {Function} fn The function to call for each occurrence.
   * @param {Scheduler.model.TimeSpan} fn.occurrence The occurrence.
   * @param {Boolean} fn.first A flag which is `true` for the first occurrence of this recurrence.
   * @param {Number} fn.counter A counter of how many dates have been visited in this iteration.
   * @param {Date} fn.date The occurrence date.
   * @internal
   */

  forEachOccurrence(startDate, endDate, fn) {
    if (this.timeSpan.startDate) {
      this.recurrenceIterator.forEachDate({
        recurrence: this,
        startDate,
        endDate,

        fn(date, counter, first, timeSpan) {
          fn(timeSpan.buildOccurrence(date, first), first, counter, date);
        }

      });
    }
  }
  /**
   * Cleans up fields that do not makes sense for the current {@link #field-frequency} value.
   * @private
   */

  sanitize() {
    const me = this,
          timeSpan = me.timeSpan,
          timeSpanStartDate = timeSpan && timeSpan.startDate;
    me.beginBatch();
    me.isSanitizing = true;

    switch (me.frequency) {
      case 'DAILY':
        me.positions = null;
        me.days = null;
        me.monthDays = null;
        me.months = null;
        break;

      case 'WEEKLY':
        me.positions = null;
        me.monthDays = null;
        me.months = null;
        const days = me.days;

        if (timeSpanStartDate && days && days.length == 1 && days[0] == RecurrenceDayRuleEncoder.encodeDay(timeSpanStartDate.getDay())) {
          me.days = null;
        }

        break;

      case 'MONTHLY':
        if (me.monthDays && me.monthDays.length) {
          me.positions = null;
          me.days = null;
        }

        me.months = null;
        const monthDays = me.monthDays;

        if (timeSpanStartDate && monthDays && monthDays.length == 1 && monthDays[0] == timeSpanStartDate.getDate()) {
          me.monthDays = null;
        }

        break;

      case 'YEARLY':
        me.monthDays = null;
        const months = me.months;

        if (timeSpanStartDate && months && months.length == 1 && months[0] == timeSpanStartDate.getMonth() + 1) {
          me.months = null;
        }

        break;
    }

    me.isSanitizing = false;
    me.endBatch();
  }

  copy(...args) {
    const result = super.copy(...args);
    result.dateFormat = this.dateFormat;
    result.timeSpan = this.timeSpan;
    return result;
  }

  afterChange(toSet, wasSet, silent) {
    const result = super.afterChange(toSet, wasSet, silent),
          {
      timeSpan
    } = this;

    if (!this.isSanitizing) {
      // cleanup data to match the chosen frequency
      this.sanitize();
    }

    if (timeSpan) {
      // Remove all exceptionsDates that our owning timeSpan had that are
      // now after our end date and therefore redundant.
      if ('endDate' in wasSet) {
        const endDate = DateHelper.clearTime(wasSet.endDate.value),
              {
          exceptionDates
        } = timeSpan; // Clear any now-invalid cached occurrences

        timeSpan.removeOccurrencesFrom(endDate); // If we had any exceptions on or after this date, remove them.

        if (exceptionDates) {
          for (const dateKey in exceptionDates) {
            const exceptionDate = DateHelper.parseKey(dateKey);

            if (exceptionDate >= endDate) {
              delete exceptionDates[dateKey];
            }
          }
        }
      }

      if (!this.isTimeSpanNotifyingSuspended) {
        timeSpan.onRecurrenceChanged();
      }
    }

    return result;
  }

  get count() {
    return this.get('count');
  }

  set count(value) {
    if (value) this.endDate = null;
    this.set('count', value);
  }

  get endDate() {
    return this.get('endDate');
  }

  set endDate(value) {
    if (value) this.count = null;
    this.set('endDate', value);
  }

  get isTimeSpanNotifyingSuspended() {
    return Boolean(this._suspendedTimeSpanNotifying);
  }

  suspendTimeSpanNotifying() {
    this._suspendedTimeSpanNotifying++;
  }

  resumeTimeSpanNotifying() {
    if (this._suspendedTimeSpanNotifying) this._suspendedTimeSpanNotifying--;
  }

}
RecurrenceModel._$name = 'RecurrenceModel';

function convertExceptionDatesValue(value) {
  const result = {},
        {
    dateFormat
  } = this;

  if (value) {
    value = typeof value == 'string' ? value.split(',') : Array.isArray(value) ? value : [value];
    value.forEach(item => {
      if (typeof item == 'string') {
        item = DateHelper.parse(item, dateFormat);
      } // If we've got a valid date out of the incoming item, add an exception key

      if (!isNaN(item)) {
        result[DateHelper.makeKey(item)] = 1;
      }
    });
  }

  return result;
}

function serializeExceptionDatesValue(value) {
  const result = [],
        {
    dateFormat
  } = this;

  for (const date in value) {
    if (value[date]) {
      result.push(DateHelper.format(DateHelper.parseKey(date), dateFormat));
    }
  }

  return result;
}

const emptyArray$8 = [];
/**
 * @module Scheduler/model/mixin/RecurringTimeSpan
 */

/**
 * This mixin class provides recurrence related fields and methods to a {@link Scheduler.model.TimeSpan timespan model}.
 *
 * The mixin introduces two types of timespans: __recurring timespan__ and its __occurrences__.
 * __Recurring timespan__ is a timespan having {@link #field-recurrenceRule recurrence rule} specified and its __occurrences__ are "fake" dynamically generated timespans.
 * Their set depends on the scheduler visible timespan and changes upon the timespan change.
 *
 * There are few methods allowing to distinguish a recurring event and an occurrence: {@link #property-isRecurring}, {@link #property-isOccurrence}
 * and {@link #property-recurringTimeSpan} (returns the event this record is an occurrence of).
 *
 * The {@link #field-recurrenceRule recurrence rule} defined for the event is parsed and
 * represented with {@link Scheduler.model.RecurrenceModel RecurrenceModel} class (can be changed by setting {@link #property-recurrenceModel} property) instance.
 * See: {@link #property-recurrence} property.
 * @mixin
 */

var RecurringTimeSpan = (Target => class RecurringTimeSpan extends (Target || TimeSpan) {
  static get $name() {
    return 'RecurringTimeSpan';
  }
  /**
   * Returns `true` if this timespan supports recurring.
   * @property {Boolean}
   */

  get supportsRecurring() {
    return true;
  }

  static get fields() {
    return [
    /**
     * The timespan recurrence rule. A string in [RFC-5545](https://tools.ietf.org/html/rfc5545#section-3.3.10) described format ("RRULE" expression).
     * @field {String} recurrenceRule
     * @category Scheduling
     */
    {
      name: 'recurrenceRule'
    },
    /**
     * The timespan exception dates. The dates that must be skipped when generating occurrences for a repeating timespan.
     * This is used to modify only individual occurrences of the timespan so the further regenerations
     * won't create another copy of this occurrence again.
     * Use {@link #function-addExceptionDate} method to add an individual entry to the dates array:
     *
     * ```javascript
     * // Break the link between the occurrence and its base.
     * // This also adds the occurrence date as an exception date
     * // so that the base timespan knows that this date should be skipped when regenerating its occurrences.
     * occurrence.recurringTimeSpan = null;
     *
     * // now the occurrence is an individual record that can be changed & persisted freely
     * occurrence.setStartEndDate(new Date(2018, 6, 2), new Date(2018, 6, 3));
     * ```
     * **Note:** The dates in this field get automatically removed when the event changes its {@link Scheduler.model.TimeSpan#field-startDate start date}.
     *
     * @field {Object} exceptionDates
     * @category Scheduling
     */
    {
      name: 'exceptionDates',
      convert: convertExceptionDatesValue,
      serialize: serializeExceptionDatesValue
    }];
  }
  /**
   * Override of {@link Core.data.Model Model}'s method. If an {@link #property-isOccurrence}
   * is passed, it is detached from its parent recurring event. If it still has a recurrence
   * then the recurring event is changed to stop at the occurrence date. If it has no recurrence
   * an exception is added at the occurrence date.
   */

  remove() {
    if (this.isOccurrence) {
      this.cancelBatch();
      this.detachFromRecurringEvent();
    } else {
      return super.remove(...arguments);
    }
  }

  get eventStore() {
    var _this$firstStore;

    let result = this.isOccurrence ? this.recurringEvent.eventStore : super.eventStore; // Recurrence methods are called on `eventStore`, but in case we have a regular
    // store with recurrence mixin, we won't event store and should return own
    // store instead

    if (!result && (_this$firstStore = this.firstStore) !== null && _this$firstStore !== void 0 && _this$firstStore.isRecurringTimeSpansMixin) {
      result = this.firstStore;
    }

    return result;
  }
  /**
   * Name of the class representing the recurrence model, defaults to {@link Scheduler.model.RecurrenceModel}
   * @property {String}
   */

  get recurrenceModel() {
    return this._recurrenceModel || RecurrenceModel;
  }

  set recurrenceModel(model) {
    this._recurrenceModel = model;
  }
  /**
   * Sets a recurrence for the timespan with a given frequency, interval, and end.
   * @param {Object|String|Scheduler.model.RecurrenceModel} recurrence A data object for an instance of {@link Scheduler.model.RecurrenceModel RecurrenceModel}.
   * May also be the frequency string: `DAILY`, `WEEKLY`, `MONTHLY`, or `YEARLY`.
   *
   * ```javascript
   * // repeat the event every other week till Jan 2 2039
   * event.setRecurrence({
   *     frequency : "WEEKLY",
   *     interval  : 2,
   *     endDate   : new Date(2039, 0, 2)
   * });
   * ```
   *
   * Also a {@link Scheduler.model.RecurrenceModel recurrence model} can be provided as the only argument for this method:
   *
   * ```javascript
   * const recurrence = new RecurrenceModel({ frequency : 'DAILY', interval : 5 });
   *
   * event.setRecurrence(recurrence);
   * ```
   *
   * or
   *
   * ```javascript
   * event.setRecurrence("WEEKLY", 2, new Date(2039, 0, 2));
   * ```
   * @param {Number} [interval] The interval between occurrences (instances of this recurrence). For example, a daily recurrence with an interval of 2 occurs every other day. Must be greater than 0.
   * @param {Number|Date} [recurrenceEnd] The end of the recurrence. The value can be specified by a date or by a maximum count of occurrences (has to greater than 1, since 1 means the event itself).
   */

  setRecurrence(recurrence, interval, recurrenceEnd) {
    const me = this;
    let recurrenceRule;

    if (recurrence) {
      if (!recurrence.isRecurrenceModel) {
        if (typeof recurrence === 'string') {
          recurrence = {
            frequency: recurrence
          };

          if (interval) {
            recurrence.interval = interval;
          } // If the recurrence is limited

          if (recurrenceEnd) {
            if (recurrenceEnd instanceof Date) {
              recurrence.endDate = recurrenceEnd;
            } else {
              recurrence.count = recurrenceEnd;
            }
          }
        }

        recurrence = new me.recurrenceModel(recurrence);
      } // The RecurrenceModel has a reference to its owning recurring event.
      // It uses this to remove the owning event's exceptions after its new endDate
      // whenever its endDate is changed.

      recurrence.timeSpan = me;
      recurrenceRule = recurrence.rule;
    }

    me.recurrence = recurrence;
    me.recurrenceRule = recurrenceRule;
  }
  /**
   * The recurrence model used for the timespan.
   * @property {Scheduler.model.RecurrenceModel}
   */

  get recurrence() {
    const me = this,
          rule = me.recurrenceRule;

    if (!me._recurrence && rule) {
      me._recurrence = new me.recurrenceModel({
        rule,
        timeSpan: me,
        id: `${me.id}-recurrence`
      });
    }

    return me._recurrence;
  }

  set recurrence(recurrence) {
    const me = this;
    me._recurrence = recurrence;

    if (recurrence) {
      // bind recurrence instance to the model
      recurrence.timeSpan = me;
      me.recurrenceRule = recurrence.rule;
    } else {
      // If this is being done to an occurrence, it's a signal that we are being
      // made an exception. Apply the change immediately, directly to the data.
      if (me.isOccurrence) {
        me.setData('recurrenceRule', null);
      } else {
        me.recurrenceRule = null;
      }
    }
  }
  /**
   * Indicates if the timespan is recurring.
   * @property {Boolean}
   * @readonly
   */

  get isRecurring() {
    // MUST evaluate in this order so that if it is an occurrence,
    // the recurrence getter does not refresh the rule
    return Boolean(!this.isOccurrence && this.recurrence);
  }
  /**
   * Indicates if the timespan is an occurrence of another recurring timespan.
   * @property {Boolean}
   * @readonly
   */

  get isOccurrence() {
    return Boolean(this.recurringTimeSpan);
  }
  /**
   * The "main" timespan this model is an occurrence of. For non-occurrences returns `null`.
   * @property {Scheduler.model.TimeSpan}
   * @readonly
   * @internal
   */

  get recurringTimeSpan() {
    return this._recurringTimeSpan;
  }

  get isPersistable() {
    return !this.supportsRecurring || !this.isOccurrence;
  }

  set recurringTimeSpan(recurringTimeSpan) {
    this._recurringTimeSpan = recurringTimeSpan;
  }
  /**
   * Returns the occurrences of this event over the specified time range. If the first
   * occurrence is in the time range `*this*` record is included in that position.
   * @param {Date} startDate The start date of the range for which to include occurrences.
   * @param {Date} [endDate] The end date of the range for which to include occurrences.
   * Defaults to the startDate.
   * @returns {Scheduler.model.TimeSpan[]} The array of occurrences which occur over the specified range.
   */

  getOccurrencesForDateRange(startDate, endDate = startDate) {
    return this.eventStore.getOccurrencesForTimeSpan(this, startDate, endDate);
  }
  /**
   * Array of this recurring timespan's cached occurrences. __Not including the owning recurring
   * event__.
   *
   * Empty if the timespan is not recurring.
   *
   * __Note that this is an internal accessor and is cleared whenever changes are made to the
   * owning recurring event__.
   * @property {Scheduler.model.TimeSpan[]}
   * @readonly
   * @internal
   */

  get occurrences() {
    if (this.isRecurring) {
      const result = []; // The occurrencesMap contains entries for each occurrence date.

      this.occurrenceMap.forEach(occurrence => {
        if (occurrence !== this) {
          result.push(occurrence);
        }
      });
      return result;
    }

    return emptyArray$8;
  }
  /**
   * A Map, keyed by each date an occurrence intersects, of occurrences of this event.
   * @property {Map}
   * @readonly
   * @internal
   */

  get occurrenceMap() {
    return this._occurrencesMap || (this._occurrencesMap = new Map());
  }
  /**
   * Removes an occurrence from this recurring timespan's cached occurrences.
   * @param dateOrTimeSpan occurrence date or occurrence TimeSpan
   * @internal
   */

  removeOccurrence(dateOrTimeSpan) {
    var _this$eventStore;

    const date = dateOrTimeSpan.isTimeSpan ? dateOrTimeSpan.occurrenceDate : dateOrTimeSpan; // Clear the occurrences *is* we are in an EventStore.

    (_this$eventStore = this.eventStore) === null || _this$eventStore === void 0 ? void 0 : _this$eventStore.globalOccurrences.delete(this.createRecurrenceKey(date)); // Remove occurrence from its by-startDate cache

    this.occurrenceMap.delete(DateHelper.makeKey(date));
  }
  /**
   * Removes all cached occurrences on or after the passed date from this recurring timespan's cached occurrences.
   * @internal
   */

  removeOccurrencesFrom(date) {
    this.occurrenceMap.forEach((occurrence, dateKey) => {
      if (DateHelper.parseKey(dateKey) >= date) {
        this.removeOccurrence(occurrence);
      }
    });
  }
  /**
   * Removes this recurring timespan's cached occurrences.
   * @internal
   */

  removeOccurrences() {
    // This recurring event must also be removed from the occurrenceMap if it's there
    // So insert it as the first element. Can also be found from the store's global occurrence
    // Map using [...this.eventStore.globalOccurrences.keys()].filter(e => e.startsWith(`_generated:${this.id}`))
    [this, ...this.occurrences].forEach(occurrence => this.removeOccurrence(occurrence));
  }
  /**
   * The method is triggered when the timespan recurrence settings get changed.
   * It updates the {@link #field-recurrenceRule} field in this case.
   * @internal
   */

  onRecurrenceChanged() {
    var _this$recurrence;

    this.recurrenceRule = ((_this$recurrence = this.recurrence) === null || _this$recurrence === void 0 ? void 0 : _this$recurrence.rule) || null;
  }
  /**
   * The original {@lScheduler.model.TimeSpan#field-startDate startDate} of this event before any modifications
   * took place. Used by {@link #function-removeOccurrence} and {@link #function-detachFromRecurringEvent}
   * @internal
   * @readonly
   */

  get occurrenceDate() {
    var _this$meta$modified;

    return ((_this$meta$modified = this.meta.modified) === null || _this$meta$modified === void 0 ? void 0 : _this$meta$modified.startDate) || this.startDate;
  }
  /**
   * If this event is an {@link #property-isOccurrence ocurrence} of a recurring event, then this
   * property yields its zero-based occurrence index in the sequence.
   * @property {Number}
   * @readonly
   */

  get occurrenceIndex() {
    return AbstractRecurrenceIterator.getOccurrenceIndex(this);
  }
  /**
   * Builds an occurrence of this recurring event by cloning the timespan data.
   * The method is used internally by the __RecurringTimeSpans__ mixin.
   * Override it if you need to customize the generated occurrences.
   *
   * If the date requested is the start date of the event sequence, `this`
   * record is returned. All runs of recurring events begin with the base record.
   * @param  {Date} occurrenceDate The occurrence start date.
   * @param  {Boolean} isFirst `true` if this is the first occurrence.
   * @return {Scheduler.model.TimeSpan} The occurrence.
   * @internal
   */

  buildOccurrence(occurrenceDate, isFirst) {
    var _me$eventStore;

    const me = this,
          {
      occurrenceMap,
      recurrence,
      meta
    } = me,
          globalOccurrences = (_me$eventStore = me.eventStore) === null || _me$eventStore === void 0 ? void 0 : _me$eventStore.globalOccurrences,
          id = me.createRecurrenceKey(occurrenceDate),
          onStartDate = !(occurrenceDate - me.startDate),
          {
      fieldMap
    } = me.constructor; // An occurrence has a unique ID which identifies it by its base recurring event and its time.

    let occurrence = globalOccurrences === null || globalOccurrences === void 0 ? void 0 : globalOccurrences.get(id),
        {
      duration
    } = me; // If there's no duration, or it's an all day event (which makes the event ceil and floor its
    // start and end dates, but does *NOT* as of 27/5/2020 adjust its duration) then
    // we calculate the duration of the occurrence.

    if (me.endDate && (me.allDay || !duration)) {
      duration = DateHelper.as(me.durationUnit, me.endDate.getTime() - me.startDate.getTime());
    } // Don't let NaN in record data

    const occurrenceEndDate = duration !== undefined ? DateHelper.add(occurrenceDate, duration, me.durationUnit) : undefined;

    if (!occurrence) {
      // If this is the first occurrence (start times may not match), or it's right on the start
      // then this recurring event *IS* the occurrence
      if (isFirst || onStartDate) {
        occurrence = me; // We are the first occurrence.
        // But if our start time is not as the rule requires, adjust ourself *silently*

        if (!onStartDate) {
          me.setStartEndDate(occurrenceDate, occurrenceEndDate, true); // Since we've changed the event start date the recurrence "Days"/"MonthDays"/"Months"
          // might get redundant in case the event start date matches the fields values
          // Calling recurrence sanitize() will clean the fields in this case.

          recurrence.suspendTimeSpanNotifying();
          recurrence.sanitize();
          recurrence.resumeTimeSpanNotifying();
        } // Either way, because of adjustment above, or initial correctness, we are in sync
        // with our recurrence rule. A RecurrenceIterator is now able to calculate a correct
        // UNTIL date from a COUNT value. See AbstractRecurrenceIterator#processIterationConfig

        meta.isSyncedWithRule = true;
      } // Generate an occurrence which references this as its parent
      else {
          occurrence = me.copy({
            [fieldMap.id.dataSource]: id,
            [fieldMap.startDate.dataSource]: occurrenceDate,
            [fieldMap.endDate.dataSource]: occurrenceEndDate,
            [fieldMap.duration.dataSource]: duration
          });
          occurrence.recurringTimeSpan = me;
        }

      globalOccurrences === null || globalOccurrences === void 0 ? void 0 : globalOccurrences.set(id, occurrence); // A recurring timespan keeps a by-startDate index of occurrences.
      // And itself will be among those.

      occurrenceMap.set(DateHelper.makeKey(occurrenceDate), occurrence);
    }

    return occurrence;
  }

  createRecurrenceKey(date = this.startDate) {
    return `_generated:${this.id}:${DateHelper.format(date, 'YYYYMMDD')}`;
  }

  afterChange(toSet, wasSet, silent, ...args) {
    const me = this,
          {
      eventStore
    } = me; // reset cached recurrence instance in case "recurrenceRule" is changed

    if ('recurrenceRule' in wasSet) {
      me._recurrence = null; // If we are a recurring event, we must be in the recurringEvents cache.
      // If we are *not* a recurring event, we must *not* be in there.
      // An event not yet in a store (eg dragging to create) won't have an eventStore.
      // eslint-disable-next-line no-unused-expressions

      eventStore === null || eventStore === void 0 ? void 0 : eventStore.recurringEvents[wasSet.recurrenceRule.value ? 'add' : 'delete'](me);
    } // Any change to an occurrence adds it to an event store, at which point
    // it ceases to be an occurrence.
    //
    // If it has a recurrenceRule it becomes the start of a new recurring event series,
    // and the old owning recurring event stops on the day before.
    //
    // If it has no recurrenceRule, it becomes an exception to its owning recurring event.

    if (me.isOccurrence) {
      var _wasSet$startDate, _wasSet$resourceRecor;

      const {
        recurringTimeSpan,
        resource,
        occurrenceIndex,
        recurrence
      } = me,
            startDate = ((_wasSet$startDate = wasSet.startDate) === null || _wasSet$startDate === void 0 ? void 0 : _wasSet$startDate.value) || me.startDate,
            count = recurrence && recurringTimeSpan.recurrence.count,
            // resourceRecords is a temporary property of occurrence events to handle cases
      // if only resources has been updated. (change only resources won't mark record as dirty)
      newResource = ((_wasSet$resourceRecor = wasSet.resourceRecords) === null || _wasSet$resourceRecor === void 0 ? void 0 : _wasSet$resourceRecor.value) || me.data.newResource;
      recurringTimeSpan.beginBatch();
      me.detachFromRecurringEvent();
      me.clearChanges(); // Must silently set our own ID, not be the key generated from our parent id and occurrence date.
      // Must not result in the id field being in the modified state.

      me.setData('id', me.generateId(recurringTimeSpan.eventStore));

      if (newResource) {
        // clear resourceId to avoid auto-adding to assignmentStore, it handled manually bellow
        delete me.data.resourceId;
      } // Add to the store silently. We do not want a UI update now.
      // The impending changes to the former parent recurring event trigger a full refresh.

      recurringTimeSpan.eventStore.add(me, true);
      me.startDate = startDate; // Ensure that the original count is honoured.
      // If we are the 8th occurrence of 10, OUR repeat count must be 3.

      if (count) {
        me.recurrence.count = count - occurrenceIndex;
      }

      me.assign(newResource || resource); // remove data afer apply

      delete me.data.resourceRecords;
      delete wasSet.resourceRecords; // Any change to a recurring events triggers a store refresh event.

      recurringTimeSpan.endBatch();
    } else {
      // Setting a newException date must mark the exceptionDates as modified
      if ('newExceptionDate' in wasSet) {
        me.meta.modified.exceptionDates = true;
        delete me.meta.modified.newExceptionDate; // Remove any occurrence on that date from our by-startDate cache
        // and from the global occurrences cache

        me.removeOccurrence(wasSet.newExceptionDate.value);
      }
    }

    return super.afterChange(toSet, wasSet, silent, ...args);
  }
  /**
   * Detaches an occurrence from its owning recurring event so that it can be added to the eventStore
   * either as an exception, or as the start of a new recurring sequence.
   * @internal
   */

  detachFromRecurringEvent() {
    const me = this,
          // For access further down, breaking the link involves engine if trying to get the occurrenceDate later,
    // resulting in the wrong date
    {
      recurringTimeSpan,
      occurrenceDate,
      startDate
    } = me; // Break the link

    me.recurringTimeSpan = null; // The occurrenceDate is injected into the data when an occurrence is created.
    // the recurringTimeSpan's afterChange will remove any cache occurrence
    // for this date; see above

    recurringTimeSpan.addExceptionDate(occurrenceDate); // If we still have a recurrenceRule, we're being promoted to be a new recurring event.
    // The recurrence setter applies the rule immediately to occurrences, so this will
    // always be correct.

    if (me.recurrenceRule) {
      // The RecurrenceModel removes occurrences and exceptions after this date
      recurringTimeSpan.recurrence.endDate = DateHelper.add(startDate, -1, 'minute');
    }
  }
  /**
   * The setter used by Model#inSet when {@link #function-addExceptionDate} is called.
   * Adding an exception must trigger change processing in a recurring event, so it must
   * be changed through a {@link Core.data.Model#function-set} call. Also, the change must be batchable
   * with other changes.
   * @private
   * @readonly
   */

  set newExceptionDate(date) {
    if (date) {
      const exceptionDates = this.exceptionDates || (this.exceptionDates = {});
      exceptionDates[DateHelper.makeKey(date)] = 1;
    }
  }
  /**
   * Adds an exception date that should be skipped when generating occurrences for the timespan.
   * The methods adds an entry to the array kept in {@link #field-exceptionDates} field.
   * @param {Date} date Exception date.
   * @internal
   */

  addExceptionDate(newExceptionDate) {
    return this.set({
      newExceptionDate
    });
  }
  /**
   * Does this recurring event have an exception on the passed date.
   * @param {Date} date The date to find an exception for.
   * @returns {Boolean} `true` if the event has an exception starting on the passed date.
   */

  hasException(date) {
    var _this$exceptionDates;

    return (_this$exceptionDates = this.exceptionDates) === null || _this$exceptionDates === void 0 ? void 0 : _this$exceptionDates[DateHelper.makeKey(date)];
  }

});

const oneDayMS = 1000 * 60 * 60 * 24;
/**
 * @module Scheduler/model/mixin/EventModelMixin
 */

/**
 * Mixin that holds configuration shared between events in Scheduler and Scheduler Pro.
 * @mixin
 */

var EventModelMixin = (Target => class EventModelMixin extends Target {
  static get $name() {
    return 'EventModelMixin';
  } // Flag checked by EventStore to make sure it uses a valid subclass

  static get isEventModel() {
    return true;
  }
  /**
   * Set value for the specified field(s), triggering engine calculations immediately. See
   * {@link Core.data.Model#function-set Model#set()} for arguments.
   *
   * ```javascript
   * eventRecord.set('duration', 4);
   * // eventRecord.endDate is not yet calculated
   *
   * await eventRecord.setAsync('duration', 4);
   * // eventRecord.endDate is calculated
   * ```
   *
   * @param {String|Object} field The field to set value for, or an object with multiple values to set in one call
   * @param {*} value Value to set
   * @param {Boolean} [silent=false] Set to true to not trigger events. If event is recurring, occurrences won't be updated
   * automatically.
   * @function setAsync
   * @category Editing
   * @async
   */
  //region Fields

  static get fields() {
    return [// TODO: below startDate/endDate/duration fields docs copypaste should be cleaned up after supporing @localdoc & @inheritdoc combination

    /**
     * The start date of a time span (or Event / Task).
     *
     * Uses {@link Core/helper/DateHelper#property-defaultFormat-static DateHelper.defaultFormat} to convert a
     * supplied string to a Date. To specify another format, either change that setting or subclass TimeSpan and
     * change the dateFormat for this field.
     *
     * UI fields representing this data field are disabled for summary tasks. See {@link #function-isEditable} for details.
     *
     * @field {String|Date} startDate
     * @category Scheduling
     */

    /**
     * The end date of a time span (or Event / Task).
     *
     * Uses {@link Core/helper/DateHelper#property-defaultFormat-static DateHelper.defaultFormat} to convert a
     * supplied string to a Date. To specify another format, either change that setting or subclass TimeSpan and
     * change the dateFormat for this field.
     *
     * UI fields representing this data field are disabled for summary tasks. See {@link #function-isEditable} for details.
     *
     * @field {String|Date} endDate
     * @category Scheduling
     */

    /**
     * The numeric part of the timespan's duration (the number of units).
     *
     * UI fields representing this data field are disabled for summary tasks. See {@link #function-isEditable} for details.
     *
     * @field {Number} duration
     * @category Scheduling
     */

    /**
     * Property which encapsulates the duration's magnitude and units.
     * @property {Core.data.Duration}
     * @name fullDuration
     */

    /**
     * The unique identifier of a task (mandatory)
     * @field {String|Number} id
     * @category Common
     */

    /**
     * Id of the resource this event is associated with (only usable for single assignments). We recommend
     * using assignments in an AssignmentStore over this approach. Internally any Event using `resourceId`
     * will have an assignment in AssignmentStore generated.
     * @field {String|Number} resourceId
     * @category Common
     */
    'resourceId',
    /**
     * Specify false to prevent the event from being dragged (if EventDrag feature is used)
     * @field {Boolean} draggable
     * @default true
     * @category Interaction
     */
    {
      name: 'draggable',
      type: 'boolean',
      persist: false,
      defaultValue: true
    }, // true or false

    /**
     * Specify false to prevent the event from being resized (if EventResize feature is used). You can also
     * specify 'start' or 'end' to only allow resizing in one direction
     * @field {boolean|String} resizable
     * @default true
     * @category Interaction
     */
    {
      name: 'resizable',
      persist: false,
      defaultValue: true
    }, // true, false, 'start' or 'end'

    /**
     * A field marking event as all day(s) spanning event.
     * For example, a holiday day may be represented by a `startDate`, and the `allDay` flag.
     * @field {Boolean} allDay
     * @category Scheduling
     */
    {
      name: 'allDay',
      defaultValue: false
    },
    /**
     * Controls this events appearance, see Schedulers
     * {@link Scheduler.view.mixin.TimelineEventRendering#config-eventStyle eventStyle config} for
     * available options.
     * @field {String} eventStyle
     * @category Styling
     */
    'eventStyle',
    /**
     * Controls the primary color of the event, see Schedulers
     * {@link Scheduler.view.mixin.TimelineEventRendering#config-eventColor eventColor config} for
     * available colors.
     * @field {String} eventColor
     * @category Styling
     */
    'eventColor',
    /**
     * Width (in px) to use for this milestone when using Scheduler#milestoneLayoutMode 'data'.
     * @field {Number} milestoneWidth
     * @category Styling
     */
    'milestoneWidth',
    /**
     * Set this field to false to opt out of {@link Scheduler.feature.StickyEvents sticky event content}
     * (keeping event text in view while scrolling).
     * @field {Boolean}
     * @category Styling
     */
    'stickyContents'];
  } //endregion
  //region Resources

  /**
   * Returns all resources assigned to an event.
   *
   * @property {Scheduler.model.ResourceModel[]}
   * @readonly
   */

  get resources() {
    // Only include valid resources, to not have nulls in the result
    return this.assignments.reduce((resources, assignment) => {
      assignment.resource && resources.push(assignment.resource);
      return resources;
    }, []);
  }

  set resources(resources) {
    if (!Array.isArray(resources)) {
      resources = [resources];
    }

    const me = this,
          newResourceIds = resources.map(me.constructor.asId),
          existingResourceIds = me.assignments.map(a => a.resource.id),
          {
      onlyInA: toAdd,
      onlyInB: toRemove
    } = ArrayHelper.delta(newResourceIds, existingResourceIds); // Add first, remove after. Otherwise event might get removed with its last assignment

    me.assignmentStore.add(toAdd.map(resourceId => ({
      resource: resourceId,
      event: me
    })));
    me.assignmentStore.remove(toRemove.map(resourceId => me.assignments.find(a => a.resource.id === resourceId)));
  }
  /**
   * Iterate over all associated resources
   * @private
   */

  forEachResource(fn, thisObj = this) {
    for (const resource of this.resources) {
      if (fn.call(thisObj, resource) === false) return;
    }
  }
  /**
   * Returns either the resource associated with this event (when called w/o `resourceId`) or resource
   * with specified id.
   *
   * @param {String} [resourceId] To retrieve a specific resource
   * @return {Scheduler.model.ResourceModel}
   */

  getResource(resourceId) {
    if (resourceId == null) {
      return this.resource;
    }

    return this.resourceStore ? this.resourceStore.getById(resourceId) : null;
  } //endregion
  //region Dates

  get startDate() {
    let dt = super.startDate;

    if (this.isOccurrence) {
      dt = this.get('startDate');
    }

    if (this.allDay) {
      dt = this.constructor.getAllDayStartDate(dt);
    }

    return dt;
  }

  set startDate(startDate) {
    if (this.batching) {
      this.set({
        startDate
      });
    } else {
      super.startDate = startDate;
    }
  }

  get endDate() {
    let dt = super.endDate;

    if (this.isOccurrence) {
      dt = this.get('endDate');
    }

    if (this.allDay) {
      dt = this.constructor.getAllDayEndDate(dt);
    }

    return dt;
  }

  set endDate(endDate) {
    if (this.batching) {
      this.set({
        endDate
      });
    } else {
      super.endDate = endDate;
    }
  }
  /**
   * Shift the dates for the date range by the passed amount and unit
   * @param {String} unit The unit to shift by, see {@link Core.helper.DateHelper} for more information on valid formats.
   * @param {Number} amount The amount to shift
   * @returns {Promise} A promise which is resolved when shift calculations are done
   * @async
   * @method shift
   */
  //endregion
  //region Is
  // Used internally to differentiate between Event and ResourceTimeRange

  get isEvent() {
    return true;
  }
  /**
   * Returns true if event can be drag and dropped
   * @property {Boolean}
   */

  get isDraggable() {
    return this.draggable;
  }
  /**
   * Returns true if event can be resized, but can additionally return 'start' or 'end' indicating how this event can be resized.
   * @property {Boolean|String}
   * @readonly
   */

  get isResizable() {
    return !this.isMilestone && this.resizable;
  }
  /**
   * Returns false if the event is not persistable. By default it always is, override this getter if you need
   * custom logic.
   *
   * @property {Boolean}
   * @readonly
   */

  get isPersistable() {
    return super.isPersistable;
  } //endregion
  //region Single assignment compatibility

  get usesSingleAssignment() {
    return !this.eventStore || this.eventStore.usesSingleAssignment;
  }
  /**
   * Override persistable getter to prevent sending resourceId when using multiple resource assignment mode
   * https://github.com/bryntum/support/issues/1345
   * @private
   */

  get persistableData() {
    const data = super.persistableData;

    if (!this.usesSingleAssignment) {
      delete data.resourceId;
    }

    return data;
  }
  /**
   * Returns the first assigned resource, or assigns a resource
   * @member {Scheduler.model.ResourceModel} resource
   */

  get resource() {
    const {
      resources
    } = this;
    return resources.length ? resources[0] : null;
  }

  set resource(resourceRecord) {
    // Use the resourceId setter for single assignment
    this.resourceId = this.constructor.asId(resourceRecord);
  }

  get resourceId() {
    var _this$resource;

    return this.usesSingleAssignment ? this.get('resourceId') : (_this$resource = this.resource) === null || _this$resource === void 0 ? void 0 : _this$resource.id;
  }

  set resourceId(resourceId) {
    this.applyResourceId(resourceId);
  }

  applyResourceId(resourceId, fromApplyValue = false) {
    const me = this,
          {
      assignments,
      assignmentStore,
      eventStore
    } = me; // When part of an EventStore, resourceIds are changed to be AssignmentModels

    if (eventStore) {
      if (resourceId != null) {
        if (!me.skipEnforcingSingleAssignment) {
          eventStore.usesSingleAssignment = true;
        } // Reassign if already assigned, only single assignment allowed

        if (assignments !== null && assignments !== void 0 && assignments.length && resourceId !== assignments[0].resourceId) {
          //assignments[0].set('resourceId', resourceId, Boolean(me.eventStore.eventsSuspended));
          // Silent reassign if events are suspended on event store, wont be expecting UI update then
          const eventsSuspended = Boolean(eventStore.eventsSuspended);
          eventsSuspended && assignmentStore.suspendEvents();
          assignments[0].resource = resourceId;
          eventsSuspended && assignmentStore.resumeEvents();
        } // Otherwise assign
        else {
            assignmentStore.assignEventToResource(me, resourceId);
          }
      } else {
        // Setting resourceId to null removes all assignments
        assignmentStore.remove(me.assignments);
      }
    } // Not part of an EventStore, edge case. Set to data unless we are in such operation already
    else if (!fromApplyValue) {
        me.set({
          resourceId
        });
      }
  } // Special handling of setting resourceId, creates assignment

  applyValue(useProp, mapping, value, skipAccessors, field) {
    if (field && field.name === 'resourceId' && !this.meta.isAssigning) {
      const {
        eventStore
      } = this;
      eventStore && (eventStore.isAssigning = true);
      this.applyResourceId(value, true);
      eventStore && (eventStore.isAssigning = false);
    }

    super.applyValue(useProp, mapping, value, skipAccessors, field);
  } //endregion
  //region Assignment

  /**
   * Returns all assignments for the event. Event must be part of the store for this method to work.
   * @property {Scheduler.model.AssignmentModel[]}
   * @readonly
   */

  get assignments() {
    return [...(this.assigned || [])];
  }
  /**
   * Assigns this event to the specified resource.
   *
   * *Note:* The event must be part of an EventStore for this to work. If the EventStore uses single assignment
   * (loaded using resourceId) existing assignments will always be removed.
   *
   * @param {Scheduler.model.ResourceModel|String|Number} resource A new resource for this event, either as a full
   *        Resource record or an id (or an array of such).
   * @param {Boolean} [removeExistingAssignments] `true` to first remove existing assignments
   */

  assign(resource, removeExistingAssignments = false) {
    const {
      eventStore
    } = this;

    if (eventStore && !eventStore.usesSingleAssignment) {
      eventStore.assignEventToResource(this, resource, removeExistingAssignments);
    } else {
      // Remember what resource to assign,  directly in single assignment mode or for later when we are joined to
      // an EventStore
      this.resourceId = this.constructor.asId(resource);

      if (!eventStore) {
        // Prevent flagging EventStore as using single assignment when that happens, we cannot know that here
        this.meta.skipEnforcingSingleAssignment = true;
      }
    }
  }
  /**
   * Unassigns this event from the specified resource
   *
   * @param {Scheduler.model.ResourceModel|String|Number} [resource] The resource to unassign from.
   */

  unassign(resource, removingResource = false) {
    var _me$eventStore;

    const me = this;
    resource = me.constructor.asId(resource); // If unassigned is caused by removing the resource the UI should be able to find out to not do extra redraws etc.

    me.meta.removingResource = removingResource;
    (_me$eventStore = me.eventStore) === null || _me$eventStore === void 0 ? void 0 : _me$eventStore.unassignEventFromResource(me, resource);
    me.meta.removingResource = null;
  }
  /**
   * Reassigns an event from an old resource to a new resource
   *
   * @param {Scheduler.model.ResourceModel|String|Number} oldResourceId A resource to unassign from or its id
   * @param {Scheduler.model.ResourceModel|String|Number} newResourceId A resource to assign to or its id
   */

  reassign(oldResourceId, newResourceId) {
    this.eventStore && this.eventStore.reassignEventFromResourceToResource(this, oldResourceId, newResourceId);
  }
  /**
   * Returns true if this event is assigned to a certain resource.
   *
   * @param {Scheduler.model.ResourceModel|String|Number} resource The resource to query for
   * @return {Boolean}
   */

  isAssignedTo(resource) {
    const resourceId = this.constructor.asId(resource);
    return this.assignments.some(assignment => assignment.resourceId === resourceId);
  } //endregion
  //region Dependencies

  /**
   * Returns all predecessor dependencies of this event
   *
   * @readonly
   * @property {Scheduler.model.DependencyBaseModel[]}
   */

  get predecessors() {
    return [...this.incomingDeps];
  }
  /**
   * Returns all successor dependencies of this event
   *
   * @readonly
   * @property {Scheduler.model.DependencyBaseModel[]}
   */

  get successors() {
    return [...this.outgoingDeps];
  }

  get dependencies() {
    return [...this.incomingDeps, ...this.outgoingDeps];
  } //endregion

  normalize() {// Normalization handled by SchedulingEngine
  }

  inSetNormalize() {// Normalization handled by SchedulingEngine
  }
  /**
   * The "main" event this model is an occurrence of.
   * Returns `null` for non-occurrences.
   * @property {Scheduler.model.EventModel}
   * @alias #Scheduler.model.mixin.RecurringTimeSpan#property-recurringTimeSpan
   * @readonly
   */

  get recurringEvent() {
    return this.recurringTimeSpan;
  }
  /**
   * Flag which indicates that this event is an inter day event. This means that it spans
   * an entire day or multiple days.
   *
   * This is essentially used by the Calendar package to determine if an event should
   * go into the all day zone of a DayView.
   *
   * @property {Boolean}
   * @readonly
   */

  get isInterDay() {
    const {
      durationMS
    } = this; // A full day (86400000 or more) marks as it as interDay,
    // which means it belongs in the all day row of a Calendar DayView

    if (durationMS >= oneDayMS) {
      return true;
    } // Working out whether it crosses midnight is a little more difficult

    const {
      endDate,
      startDate
    } = this,
          eventStartMidnight = DateHelper.clearTime(startDate); // If either is null or NaN, we have to answer falsy

    if (startDate && endDate) {
      eventStartMidnight.setDate(eventStartMidnight.getDate() + 1); // If the endDate is past midnight, it's interDay and goes in the all day row of a Calendar DayView

      return (endDate || DateHelper.add(startDate, durationMS)) > eventStartMidnight;
    }
  } //region All day statics

  static getAllDayStartDate(dt) {
    if (dt && dt.isEvent) {
      dt = dt.get('startDate');
    }

    if (dt) {
      dt = DateHelper.clearTime(dt, true);
    }

    return dt;
  }

  static getAllDayEndDate(dt) {
    if (dt && dt.isEvent) {
      dt = dt.get('endDate');
    }

    if (dt && (dt.getHours() > 0 || dt.getMinutes() > 0 || dt.getSeconds() > 0 || dt.getMilliseconds() > 0)) {
      dt = DateHelper.getNext(dt, 'd', 1);
    }

    return dt;
  }

  static getAllDayDisplayStartDate(dt) {
    if (dt && dt.isEvent) {
      dt = dt.get('startDate');
    }

    return DateHelper.clearTime(dt, true);
  }

  static getAllDayDisplayEndDate(startDate, endDate) {
    if (startDate && startDate.isEvent) {
      endDate = startDate.get('endDate');
      startDate = startDate.get('startDate');
    }

    if (endDate) {
      startDate = this.constructor.getAllDayDisplayStartDate(startDate); // If date falls on start of the day - subtract one day to show end date correctly
      // e.g. event starts on 2017-01-01 00:00 and ends on 2017-01-02 00:00, editor should show
      // 2017-01-01 for both start and end

      if (DateHelper.clearTime(endDate, true).valueOf() === endDate.valueOf()) {
        endDate = DateHelper.add(endDate, DateHelper.DAY, -1);
      } else if (startDate.valueOf() !== endDate.valueOf()) {
        endDate = DateHelper.clearTime(endDate, true);
      }
    }

    return endDate;
  }
  /**
   * Defines if the given event field should be manually editable in UI.
   * You can override this method to provide your own logic.
   *
   * By default the method defines {@link #field-endDate}, {@link #field-duration} and {@link #property-fullDuration} fields
   * editable for leaf events only (in case the event is part of a tree store) and all other fields as editable.
   *
   * @param {String} fieldName Name of the field
   * @returns {Boolean} Returns `true` if the field is editable, `false` if it is not and `undefined` if the event has no such field.
   */

  isEditable(fieldName) {
    switch (fieldName) {
      // end/duration is allowed to edit for leafs
      case 'endDate':
      case 'duration':
      case 'fullDuration':
        return this.isLeaf;
    }

    return super.isEditable(fieldName);
  } //endregion

});

const required = DEBUG_ONLY();

var __decorate$9 = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let FORMULA_ID = 0;
const CalculateProposed = FORMULA_ID++;
class Formula extends Base$1 {
  constructor() {
    super(...arguments);
    this.formulaId = FORMULA_ID++;
    this.inputs = new Set();
  }

}
class VariableWalkContext extends WalkContext {
  collectNext(sourceNode, toVisit) {
    if (sourceNode instanceof Formula) {
      toVisit.push({
        node: sourceNode.output,
        from: sourceNode,
        label: undefined
      });
    } else {
      const formulas = this.cache.formulasByInput.get(sourceNode);
      formulas && formulas.forEach(formula => toVisit.push({
        node: formula,
        from: sourceNode,
        label: undefined
      }));
    }
  }

}
class FormulasCache extends Mixin([Base$1], base => class FormulasCache extends base {
  constructor() {
    super(...arguments);
    this.variables = new Set();
    this.formulas = new Set();
    this.$formulasByInput = undefined;
    this.$formulasByOutput = undefined;
  }

  get formulasByInput() {
    if (this.$formulasByInput !== undefined) return this.$formulasByInput;
    this.fillCache();
    return this.$formulasByInput;
  }

  get formulasByOutput() {
    if (this.$formulasByOutput !== undefined) return this.$formulasByOutput;
    this.fillCache();
    return this.$formulasByOutput;
  }

  add(formula) {
    this.$formulasByInput = undefined;
    this.$formulasByOutput = undefined;
    this.formulas.add(formula);
  }

  has(formula) {
    return this.formulas.has(formula);
  }

  fillCache() {
    this.$formulasByInput = new Map();
    this.$formulasByOutput = new Map();
    this.formulas.forEach(formula => {
      let formulasByOutput = this.$formulasByOutput.get(formula.output);

      if (!formulasByOutput) {
        formulasByOutput = new Set();
        this.$formulasByOutput.set(formula.output, formulasByOutput);
      }

      formulasByOutput.add(formula);
      formula.inputs.forEach(input => {
        let formulasByInput = this.$formulasByInput.get(input);

        if (!formulasByInput) {
          formulasByInput = new Set();
          this.$formulasByInput.set(input, formulasByInput);
        }

        formulasByInput.add(formula);
      });
    });
  }

  allInputVariables() {
    return uniqueOnly(concatIterable(map(this.formulas, formula => formula.inputs.values())));
  }

  isCyclic() {
    let isCyclic = false;
    const walkContext = VariableWalkContext.new({
      cache: this,
      onCycle: () => {
        isCyclic = true;
        return OnCycleAction.Cancel;
      }
    });
    walkContext.startFrom(Array.from(this.allInputVariables()));
    return isCyclic;
  }

}) {}
class CycleDescription extends FormulasCache {}
class CycleResolution extends Base$1 {
  constructor() {
    super(...arguments);
    this.description = undefined;
    this.defaultResolutionFormulas = new Set();
    this.resolutionsByInputHash = new Map();
  }

  clear() {
    this.resolutionsByInputHash.clear();
  }

  resolve(input) {
    const cached = this.resolutionsByInputHash.get(input.hash);
    if (cached !== undefined) return cached;
    const resolution = this.buildResolution(input);
    this.resolutionsByInputHash.set(input.hash, resolution);
    return resolution;
  }

  buildResolution(input) {
    const walkContext = WalkState.new({
      context: this,
      input
    });
    const allResolutions = Array.from(walkContext.next()).map(state => {
      return {
        resolution: state.asResolution(),
        nbrOfDefaultFormulas: Array.from(state.activatedFormulas.formulas).reduce((count, formula) => state.formulaIsDefault(formula) ? count + 1 : count, 0),
        unCoveredInputWeight: state.unCoveredInputWeight()
      };
    });
    allResolutions.sort((res1, res2) => {
      if (res1.unCoveredInputWeight < res2.unCoveredInputWeight) return -1;
      if (res1.unCoveredInputWeight > res2.unCoveredInputWeight) return 1;
      return res2.nbrOfDefaultFormulas - res1.nbrOfDefaultFormulas;
    });
    if (allResolutions.length > 0) return allResolutions[0].resolution;else debugger;
  }

}
var VariableInputState;

(function (VariableInputState) {
  VariableInputState[VariableInputState["NoInput"] = 0] = "NoInput";
  VariableInputState[VariableInputState["HasPreviousValue"] = 1] = "HasPreviousValue";
  VariableInputState[VariableInputState["HasProposedValue"] = 2] = "HasProposedValue";
  VariableInputState[VariableInputState["KeepIfPossible"] = 4] = "KeepIfPossible";
})(VariableInputState || (VariableInputState = {}));

class CycleResolutionInput extends Base$1 {
  constructor() {
    super(...arguments);
    this.context = undefined;
    this.input = undefined;
    this.$hash = '';
  }

  get hash() {
    if (this.$hash !== '') return this.$hash;
    return this.$hash = this.buildHash();
  }

  get description() {
    return this.context.description;
  }

  get resolution() {
    return this.context.resolve(this);
  }

  initialize(...args) {
    super.initialize(...args);
    this.input = new Map(CI(this.description.variables).map(variable => [variable, VariableInputState.NoInput]));
  }

  buildHash() {
    return String.fromCharCode(...CI(this.description.variables).inBatchesBySize(5).map(batch => this.batchToCharCode(batch)));
  }

  batchToCharCode(batch) {
    return batch.reduceRight((charCode, variable, index) => charCode | this.input.get(variable) << index * 3, 0);
  }

  addProposedValueFlag(variable) {

    const input = this.input.get(variable);
    this.input.set(variable, input | VariableInputState.HasProposedValue);
  }

  hasProposedValue(variable) {
    return Boolean(this.input.get(variable) & VariableInputState.HasProposedValue);
  }

  hasProposedValueVars() {
    return CI(this.description.variables).filter(variable => this.hasProposedValue(variable));
  }

  addPreviousValueFlag(variable) {

    const input = this.input.get(variable);
    this.input.set(variable, input | VariableInputState.HasPreviousValue);
  }

  hasPreviousValue(variable) {
    return Boolean(this.input.get(variable) & VariableInputState.HasPreviousValue);
  }

  hasPreviousValueVars() {
    return CI(this.description.variables).filter(variable => this.hasPreviousValue(variable));
  }

  addKeepIfPossibleFlag(variable) {

    const input = this.input.get(variable);
    this.input.set(variable, input | VariableInputState.KeepIfPossible);
  }

  keepIfPossible(variable) {
    return Boolean(this.input.get(variable) & VariableInputState.KeepIfPossible);
  }

  keepIfPossibleVars() {
    return CI(this.description.variables).filter(variable => this.keepIfPossible(variable));
  }

}

__decorate$9([required], CycleResolutionInput.prototype, "context", void 0);

class WalkState extends Base$1 {
  constructor() {
    super(...arguments);
    this.context = undefined;
    this.input = undefined;
    this.previous = undefined;
    this.activatedFormula = undefined;
    this.$activatedFormulas = undefined;
  }

  get activatedFormulas() {
    if (this.$activatedFormulas !== undefined) return this.$activatedFormulas;
    const cache = FormulasCache.new({
      variables: this.description.variables,
      formulas: CI(this.thisAndPreviousStates()).map(state => state.activatedFormula).toSet()
    });
    return this.$activatedFormulas = cache;
  }

  get description() {
    return this.context.description;
  }

  *thisAndPreviousStates() {
    let current = this;

    while (current && current.activatedFormula) {
      yield current;
      current = current.previous;
    }
  }

  formulaHasProposedValueInInput(formula) {
    return Array.from(formula.inputs).some(variable => this.input.hasProposedValue(variable));
  }

  unCoveredInputWeight() {
    const proposedVars = map(this.input.hasProposedValueVars(), variable => {
      return {
        variable,
        isProposed: true
      };
    });
    const keepIfPossibleVars = map(this.input.keepIfPossibleVars(), variable => {
      return {
        variable,
        isProposed: false
      };
    });
    const allInputVars = CI([proposedVars, keepIfPossibleVars]).concat().uniqueOnlyBy(el => el.variable);
    return allInputVars.reduce((totalWeight, {
      variable,
      isProposed
    }) => {
      let weight = 0;
      const isOverwrittenByFormulas = this.activatedFormulas.formulasByOutput.get(variable);

      if (isOverwrittenByFormulas) {
        const formula = isOverwrittenByFormulas.size === 1 ? Array.from(isOverwrittenByFormulas)[0] : null;

        if (formula && this.formulaIsDefault(formula) && this.formulaHasProposedValueInInput(formula)) {
          if (isProposed) weight += 1e6;else weight += 1e4;
        } else {
          if (isProposed) weight += 1e7;else weight += 1e5;
        }
      }

      const usedInFormulas = this.activatedFormulas.formulasByInput.get(variable);

      if (!(usedInFormulas && usedInFormulas.size > 0)) {
        if (isProposed) weight += 1e3;else weight += 1e2;
      }

      return totalWeight + weight;
    }, 0);
  }

  preferFormula(formula1, formula2) {
    const allInputsHasProposed1 = this.formulaAllInputsHasProposed(formula1);
    const allInputsHasProposed2 = this.formulaAllInputsHasProposed(formula2);
    if (allInputsHasProposed1 && !allInputsHasProposed2) return -1;
    if (allInputsHasProposed2 && !allInputsHasProposed1) return 1;
    const countInputsWithProposedOrKeep1 = this.formulaCountInputsWithProposedOrKeep(formula1);
    const countInputsWithProposedOrKeep2 = this.formulaCountInputsWithProposedOrKeep(formula2);
    if (countInputsWithProposedOrKeep1 > countInputsWithProposedOrKeep2) return -1;
    if (countInputsWithProposedOrKeep1 < countInputsWithProposedOrKeep2) return 1;
    if (this.formulaIsDefault(formula1) && !this.formulaIsDefault(formula2)) return -1;
    if (this.formulaIsDefault(formula2) && !this.formulaIsDefault(formula1)) return 1;
    return 0;
  }

  formulaIsDefault(formula) {
    return this.context.defaultResolutionFormulas.has(formula);
  }

  formulaCountInputsWithProposedOrKeep(formula) {
    let count = 0;
    Array.from(formula.inputs).forEach(variable => {
      if (this.input.hasProposedValue(variable) || this.input.keepIfPossible(variable)) count++;
    });
    return count;
  }

  formulaAllInputsHasProposedOrKeep(formula) {
    return Array.from(formula.inputs).every(variable => this.input.hasProposedValue(variable) || this.input.keepIfPossible(variable));
  }

  formulaAllInputsHasProposed(formula) {
    return Array.from(formula.inputs).every(variable => this.input.hasProposedValue(variable));
  }

  formulaIsApplicable(formula) {
    const everyFormulaInputHasValue = Array.from(formula.inputs).every(variable => this.input.hasProposedValue(variable) || this.input.hasPreviousValue(variable) || this.activatedFormulas.formulasByOutput.has(variable));
    const cache = FormulasCache.new({
      formulas: new Set(this.activatedFormulas.formulas)
    });
    cache.add(formula);
    return everyFormulaInputHasValue && !cache.isCyclic();
  }

  formulaIsInsignificant(formula) {
    const outputVariableAlreadyCalculated = this.activatedFormulas.formulasByOutput.has(formula.output);
    const outputVariableHasPreviousValue = this.input.hasPreviousValue(formula.output);
    return outputVariableAlreadyCalculated || outputVariableHasPreviousValue && Array.from(formula.inputs).some(variable => !this.input.hasPreviousValue(variable) && !this.input.hasProposedValue(variable));
  }

  unvisitedFormulas() {
    return Array.from(this.description.formulas).filter(formula => !this.activatedFormulas.has(formula));
  }

  *next() {
    const unvisitedFormulas = this.unvisitedFormulas();
    unvisitedFormulas.sort(this.preferFormula.bind(this));
    let isFinal = true;

    for (const formula of unvisitedFormulas) {
      if (!this.formulaIsApplicable(formula) || this.formulaIsInsignificant(formula)) continue;
      const nextState = WalkState.new({
        previous: this,
        context: this.context,
        input: this.input,
        activatedFormula: formula
      });
      yield* nextState.next();
      isFinal = false;
    }

    if (isFinal) yield this;
  }

  asResolution() {
    return new Map(CI(this.description.variables).map(variable => {
      const formulas = this.activatedFormulas.formulasByOutput.get(variable);

      if (formulas) {
        for (const firstFormula of formulas) {
          return [variable, firstFormula.formulaId];
        }
      }

      return [variable, CalculateProposed];
    }));
  }

}

class CycleResolutionInputChrono extends CycleResolutionInput {
  collectInfo(Y, identifier, symbol) {
    if (Y(PreviousValueOf(identifier)) != null) this.addPreviousValueFlag(symbol);
    if (Y(HasProposedValue(identifier))) this.addProposedValueFlag(symbol);
  }

}

var Instruction;

(function (Instruction) {
  Instruction["KeepDuration"] = "KeepDuration";
  Instruction["KeepStartDate"] = "KeepStartDate";
  Instruction["KeepEndDate"] = "KeepEndDate";
})(Instruction || (Instruction = {}));

const StartDateVar = Symbol('StartDate');
const EndDateVar = Symbol('EndDate');
const DurationVar = Symbol('Duration');
const startDateFormula = Formula.new({
  output: StartDateVar,
  inputs: new Set([DurationVar, EndDateVar])
});
const endDateFormula = Formula.new({
  output: EndDateVar,
  inputs: new Set([DurationVar, StartDateVar])
});
const durationFormula = Formula.new({
  output: DurationVar,
  inputs: new Set([StartDateVar, EndDateVar])
});
const SEDGraphDescription = CycleDescription.new({
  variables: new Set([StartDateVar, EndDateVar, DurationVar]),
  formulas: new Set([startDateFormula, endDateFormula, durationFormula])
});
const SEDForwardCycleResolutionContext = CycleResolution.new({
  description: SEDGraphDescription,
  defaultResolutionFormulas: new Set([endDateFormula])
});
const SEDBackwardCycleResolutionContext = CycleResolution.new({
  description: SEDGraphDescription,
  defaultResolutionFormulas: new Set([startDateFormula])
});
class SEDDispatcher extends CycleResolutionInputChrono {
  addInstruction(instruction) {
    if (instruction === Instruction.KeepStartDate) this.addKeepIfPossibleFlag(StartDateVar);
    if (instruction === Instruction.KeepEndDate) this.addKeepIfPossibleFlag(EndDateVar);
    if (instruction === Instruction.KeepDuration) this.addKeepIfPossibleFlag(DurationVar);
  }

}
class SEDDispatcherIdentifier extends FieldIdentifier.mix(CalculatedValueGen) {
  equality(v1, v2) {
    const resolution1 = v1.resolution;
    const resolution2 = v2.resolution;
    return resolution1.get(StartDateVar) === resolution2.get(StartDateVar) && resolution1.get(EndDateVar) === resolution2.get(EndDateVar) && resolution1.get(DurationVar) === resolution2.get(DurationVar);
  }

}

var __decorate$a = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};
class BaseEventMixin extends Mixin([HasCalendarMixin], base => {
  const superProto = base.prototype;

  class BaseEventMixin extends base {
    *calculateDispatcher(YIELD) {
      const proposed = yield ProposedOrPrevious;
      const cycleDispatcher = yield* this.prepareDispatcher(YIELD);
      const startDateProposedArgs = yield ProposedArgumentsOf(this.$.startDate);
      const startInstruction = startDateProposedArgs ? startDateProposedArgs[0] ? Instruction.KeepDuration : Instruction.KeepEndDate : undefined;
      if (startInstruction) cycleDispatcher.addInstruction(startInstruction);
      const endDateProposedArgs = yield ProposedArgumentsOf(this.$.endDate);
      const endInstruction = endDateProposedArgs ? endDateProposedArgs[0] ? Instruction.KeepDuration : Instruction.KeepStartDate : undefined;
      if (endInstruction) cycleDispatcher.addInstruction(endInstruction);
      const directionValue = yield this.$.direction;
      const durationProposedArgs = yield ProposedArgumentsOf(this.$.duration);
      let durationInstruction;

      if (durationProposedArgs) {
        switch (durationProposedArgs[0]) {
          case true:
            durationInstruction = Instruction.KeepStartDate;
            break;

          case false:
            durationInstruction = Instruction.KeepEndDate;
            break;
        }
      }

      if (!durationInstruction && cycleDispatcher.hasProposedValue(DurationVar)) {
        durationInstruction = directionValue === Direction.Forward || directionValue === Direction.None ? Instruction.KeepStartDate : Instruction.KeepEndDate;
      }

      if (durationInstruction) cycleDispatcher.addInstruction(durationInstruction);
      return cycleDispatcher;
    }

    *prepareDispatcher(Y) {
      const dispatcherClass = this.dispatcherClass(Y);
      const cycleDispatcher = dispatcherClass.new({
        context: this.cycleResolutionContext(Y)
      });
      cycleDispatcher.collectInfo(Y, this.$.startDate, StartDateVar);
      cycleDispatcher.collectInfo(Y, this.$.endDate, EndDateVar);
      cycleDispatcher.collectInfo(Y, this.$.duration, DurationVar);
      return cycleDispatcher;
    }

    cycleResolutionContext(Y) {
      const direction = Y(this.$.direction);
      return direction === Direction.Forward || direction === Direction.None ? SEDForwardCycleResolutionContext : SEDBackwardCycleResolutionContext;
    }

    dispatcherClass(Y) {
      return SEDDispatcher;
    }

    buildProposedDispatcher(me, quark, transaction) {
      const dispatcher = this.dispatcherClass(transaction.onEffectSync).new({
        context: this.cycleResolutionContext(transaction.onEffectSync)
      });
      dispatcher.addPreviousValueFlag(StartDateVar);
      dispatcher.addPreviousValueFlag(EndDateVar);
      dispatcher.addPreviousValueFlag(DurationVar);
      return dispatcher;
    }

    *skipNonWorkingTime(date, isForward) {
      const calendar = yield this.$.effectiveCalendar;
      if (!date) return null;
      const skippingRes = calendar.skipNonWorkingTime(date, isForward);

      if (skippingRes instanceof Date) {
        return skippingRes;
      } else {
        yield Reject("Empty calendar");
      }
    }

    *skipWorkingTime(date, isForward, duration, unit) {
      const durationUnit = yield this.$.durationUnit;

      if (unit && unit !== durationUnit) {
        duration = yield* this.getProject().$convertDuration(duration, unit, durationUnit);
      }

      return yield* this.calculateProjectedXDateWithDuration(date, isForward, duration);
    }

    setStartDate(date, keepDuration = true) {
      if (this.graph) {
        this.graph.write(this.$.startDate, date, keepDuration);
        return this.graph.commitAsync();
      } else {
        this.$.startDate.DATA = date;
      }
    }

    writeStartDate(me, transaction, quark, date, keepDuration = true) {
      if (!transaction.baseRevision.hasIdentifier(me) && date == null) return;
      const instruction = keepDuration ? Instruction.KeepDuration : Instruction.KeepEndDate;
      me.constructor.prototype.write.call(this, me, transaction, quark, date, keepDuration);
    }

    *calculateStartDate() {
      const dispatch = yield this.$.dispatcher;
      const formulaId = dispatch.resolution.get(StartDateVar);

      if (formulaId === CalculateProposed) {
        return yield* this.calculateStartDateProposed();
      } else if (formulaId === startDateFormula.formulaId) {
        return yield* this.calculateStartDatePure();
      } else {
        throw new Error("Unknown formula for `startDate`");
      }
    }

    *calculateStartDatePure() {
      return yield* this.calculateProjectedXDateWithDuration(yield this.$.endDate, false, yield this.$.duration);
    }

    *calculateStartDateProposed() {
      const startDate = yield ProposedOrPrevious;
      return yield* this.skipNonWorkingTime(startDate, true);
    }

    *calculateProjectedXDateWithDuration(baseDate, isForward, duration) {
      const durationUnit = yield this.$.durationUnit;
      const calendar = yield this.$.effectiveCalendar;
      const project = this.getProject();
      if (!baseDate || isNotNumber(duration)) return null;
      isForward = isForward === undefined ? true : isForward;

      if (isForward) {
        return calendar.calculateEndDate(baseDate, yield* project.$convertDuration(duration, durationUnit, TimeUnit.Millisecond));
      } else {
        return calendar.calculateStartDate(baseDate, yield* project.$convertDuration(duration, durationUnit, TimeUnit.Millisecond));
      }
    }

    setEndDate(date, keepDuration = false) {
      if (this.graph) {
        this.graph.write(this.$.endDate, date, keepDuration);
        return this.graph.commitAsync();
      } else {
        this.$.endDate.DATA = date;
      }
    }

    writeEndDate(me, transaction, quark, date, keepDuration = false) {
      if (!transaction.baseRevision.hasIdentifier(me) && date == null) return;
      me.constructor.prototype.write.call(this, me, transaction, quark, date, keepDuration);
    }

    *calculateEndDate() {
      const dispatch = yield this.$.dispatcher;
      const formulaId = dispatch.resolution.get(EndDateVar);

      if (formulaId === CalculateProposed) {
        return yield* this.calculateEndDateProposed();
      } else if (formulaId === endDateFormula.formulaId) {
        return yield* this.calculateEndDatePure();
      } else {
        throw new Error("Unknown formula for `endDate`");
      }
    }

    *calculateEndDatePure() {
      return yield* this.calculateProjectedXDateWithDuration(yield this.$.startDate, true, yield this.$.duration);
    }

    *calculateEndDateProposed() {
      const endDate = yield ProposedOrPrevious;
      return yield* this.skipNonWorkingTime(endDate, false);
    }

    getDuration(unit) {
      const duration = this.duration;
      return unit !== undefined ? this.getProject().convertDuration(duration, this.durationUnit, unit) : duration;
    }

    setDuration(duration, unit, keepStart) {
      if (this.graph) {
        if (duration !== undefined) {
          this.graph.write(this.$.duration, duration, unit, keepStart);
          return this.graph.commitAsync();
        }
      } else {
        this.$.duration.DATA = duration;
        if (unit != null) this.$.durationUnit.DATA = unit;
      }
    }

    setDurationUnit(_value) {
      throw new Error("Use `setDuration` instead");
    }

    writeDuration(me, transaction, quark, duration, unit, keepStart = undefined) {
      if (duration < 0) duration = 0;
      if (!transaction.baseRevision.hasIdentifier(me) && duration == null) return;
      me.constructor.prototype.write.call(this, me, transaction, quark, duration, keepStart);
      if (unit != null) transaction.write(this.$.durationUnit, unit);
    }

    *calculateDuration() {
      const dispatch = yield this.$.dispatcher;
      const formulaId = dispatch.resolution.get(DurationVar);

      if (formulaId === CalculateProposed) {
        return yield* this.calculateDurationProposed();
      } else if (formulaId === durationFormula.formulaId) {
        return yield* this.calculateDurationPure();
      } else {
        throw new Error("Unknown formula for `duration`");
      }
    }

    *calculateDurationPure() {
      const startDate = yield this.$.startDate;
      const endDate = yield this.$.endDate;
      if (!startDate || !endDate) return null;

      if (startDate > endDate) {
        yield Write(this.$.duration, 0, null);
      } else {
        return yield* this.calculateProjectedDuration(startDate, endDate);
      }
    }

    *calculateDurationProposed() {
      return yield ProposedOrPrevious;
    }

    *calculateProjectedDuration(startDate, endDate, durationUnit) {
      if (!startDate || !endDate) return null;
      if (!durationUnit) durationUnit = yield this.$.durationUnit;
      const calendar = yield this.$.effectiveCalendar;
      const project = this.getProject();
      return yield* project.$convertDuration(calendar.calculateDurationMs(startDate, endDate), TimeUnit.Millisecond, durationUnit);
    }

    *calculateEffectiveDuration() {
      const dispatch = yield this.$.dispatcher;
      let effectiveDurationToUse;
      const durationResolution = dispatch.resolution.get(DurationVar);

      if (durationResolution === CalculateProposed) {
        effectiveDurationToUse = yield this.$.duration;
      } else if (durationResolution === durationFormula.formulaId) {
        effectiveDurationToUse = yield* this.calculateProjectedDuration(yield ProposedOrPreviousValueOf(this.$.startDate), yield ProposedOrPreviousValueOf(this.$.endDate));
      }

      return effectiveDurationToUse;
    }

  }

  __decorate$a([model_field({
    type: 'date'
  }, {
    converter: dateConverter
  })], BaseEventMixin.prototype, "startDate", void 0);

  __decorate$a([model_field({
    type: 'date'
  }, {
    converter: dateConverter
  })], BaseEventMixin.prototype, "endDate", void 0);

  __decorate$a([model_field({
    type: 'number',
    allowNull: true
  })], BaseEventMixin.prototype, "duration", void 0);

  __decorate$a([model_field({
    type: 'string',
    defaultValue: TimeUnit.Day
  }, {
    converter: DateHelper.normalizeUnit
  })], BaseEventMixin.prototype, "durationUnit", void 0);

  __decorate$a([model_field({
    type: 'string',
    defaultValue: Direction.Forward
  }, {
    sync: true
  })], BaseEventMixin.prototype, "direction", void 0);

  __decorate$a([field({
    identifierCls: SEDDispatcherIdentifier
  })], BaseEventMixin.prototype, "dispatcher", void 0);

  __decorate$a([model_field({
    type: 'boolean',
    defaultValue: false
  })], BaseEventMixin.prototype, "manuallyScheduled", void 0);

  __decorate$a([calculate('dispatcher')], BaseEventMixin.prototype, "calculateDispatcher", null);

  __decorate$a([build_proposed('dispatcher')], BaseEventMixin.prototype, "buildProposedDispatcher", null);

  __decorate$a([write('startDate')], BaseEventMixin.prototype, "writeStartDate", null);

  __decorate$a([calculate('startDate')], BaseEventMixin.prototype, "calculateStartDate", null);

  __decorate$a([write('endDate')], BaseEventMixin.prototype, "writeEndDate", null);

  __decorate$a([calculate('endDate')], BaseEventMixin.prototype, "calculateEndDate", null);

  __decorate$a([write('duration')], BaseEventMixin.prototype, "writeDuration", null);

  __decorate$a([calculate('duration')], BaseEventMixin.prototype, "calculateDuration", null);

  return BaseEventMixin;
}) {}

class AbstractHasAssignmentsMixin extends Mixin([AbstractPartOfProjectModelMixin], base => {
  const superProto = base.prototype;

  class HasAssignmentsMixin extends base {
    getAssignmentFor(resource) {
      for (const assignment of this.assigned) {
        if (assignment.resource === resource) return assignment;
      }

      return null;
    }

    isAssignedTo(resource) {
      return Boolean(this.getAssignmentFor(resource));
    }

    async assign(resource) {
      if (this.getAssignmentFor(resource)) throw new Error('Resource can\'t be assigned twice to the same task');
      const assignmentCls = this.project.assignmentStore.modelClass;
      this.addAssignment(new assignmentCls({
        event: this,
        resource: resource
      }));
      return this.commitAsync();
    }

    async unassign(resource) {
      const assignment = this.getAssignmentFor(resource);
      if (!assignment) throw new Error(`Can't unassign resource \`${resource}\` from task \`${this}\` - resource is not assigned to the task!`);
      this.removeAssignment(assignment);
      return this.commitAsync();
    }

    leaveProject() {
      if (this.assigned) {
        const eventStore = this.getEventStore();
        this.assigned.forEach(assignment => eventStore.assignmentsForRemoval.add(assignment));
      }

      superProto.leaveProject.call(this, ...arguments);
    }

    remove() {
      if (this.parent) {
        const eventStore = this.getEventStore();
        superProto.remove.call(this);
        eventStore && eventStore.afterEventRemoval();
      } else {
        return superProto.remove.call(this);
      }
    }

    addAssignment(assignment) {
      this.getProject().assignmentStore.add(assignment);
      return assignment;
    }

    removeAssignment(assignment) {
      this.getProject().assignmentStore.remove(assignment);
      return assignment;
    }

  }

  return HasAssignmentsMixin;
}) {}

var __decorate$b = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};
class BaseHasAssignmentsMixin extends Mixin([BaseEventMixin, AbstractHasAssignmentsMixin], base => {
  const superProto = base.prototype;

  class BaseHasAssignmentsMixin extends base {
    get assignments() {
      return this.assigned ? [...this.assigned] : [];
    }

  }

  __decorate$b([generic_field({}, ModelBucketField)], BaseHasAssignmentsMixin.prototype, "assigned", void 0);

  return BaseHasAssignmentsMixin;
}) {}

var __decorate$c = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};
class HasDependenciesMixin extends Mixin([BaseEventMixin], base => {
  const superProto = base.prototype;

  class HasDependenciesMixin extends base {
    leaveProject() {
      const eventStore = this.getEventStore();

      if (this.outgoingDeps) {
        this.outgoingDeps.forEach(dependency => eventStore.dependenciesForRemoval.add(dependency));
      }

      if (this.incomingDeps) {
        this.incomingDeps.forEach(dependency => eventStore.dependenciesForRemoval.add(dependency));
      }

      superProto.leaveProject.call(this);
    }

  }

  __decorate$c([generic_field({}, ModelBucketField)], HasDependenciesMixin.prototype, "outgoingDeps", void 0);

  __decorate$c([generic_field({}, ModelBucketField)], HasDependenciesMixin.prototype, "incomingDeps", void 0);

  return HasDependenciesMixin;
}) {}

class SchedulerBasicEvent extends Mixin([BaseEventMixin, BaseHasAssignmentsMixin, HasDependenciesMixin], base => {
  const superProto = base.prototype;

  class SchedulerBasicEvent extends base {}

  return SchedulerBasicEvent;
}) {}

class CoreHasAssignmentsMixin extends Mixin([CoreEventMixin, AbstractHasAssignmentsMixin], base => {
  const superProto = base.prototype;

  class CoreHasAssignmentsMixin extends base {
    get assigned() {
      var _a;

      const assignments = (_a = this.project) === null || _a === void 0 ? void 0 : _a.assignmentStore.getEventsAssignments(this);

      if (!assignments) {
        return this.$cachedAssignments;
      }

      return this.$cachedAssignments = assignments;
    }

    applyValue(useProp, key, value, skipAccessor, field) {
      var _a;

      if (key === 'id') {
        (_a = this.assigned) === null || _a === void 0 ? void 0 : _a.forEach(assignment => assignment.set('eventId', value));
      }

      superProto.applyValue.call(this, useProp, key, value, skipAccessor, field);
    }

    copy(newId = null, deep = null) {
      const copy = superProto.copy.call(this, newId, deep);

      if (ObjectHelper.isObject(deep) && !deep.skipFieldIdentifiers || !ObjectHelper.isObject(deep)) {
        copy.$cachedAssignments = this.assigned;
      }

      return copy;
    }

  }

  return CoreHasAssignmentsMixin;
}) {}

class CoreHasDependenciesMixin extends Mixin([CoreEventMixin], base => {
  const superProto = base.prototype;

  class CoreHasDependenciesMixin extends base {
    get outgoingDeps() {
      return this.project.dependencyStore.getOutgoingDepsForEvent(this);
    }

    get incomingDeps() {
      return this.project.dependencyStore.getIncomingDepsForEvent(this);
    }

    leaveProject() {
      const eventStore = this.eventStore;

      if (this.outgoingDeps) {
        this.outgoingDeps.forEach(dependency => eventStore.dependenciesForRemoval.add(dependency));
      }

      if (this.incomingDeps) {
        this.incomingDeps.forEach(dependency => eventStore.dependenciesForRemoval.add(dependency));
      }

      superProto.leaveProject.call(this);
    }

  }

  return CoreHasDependenciesMixin;
}) {}

class SchedulerCoreEvent extends Mixin([CoreEventMixin, CoreHasAssignmentsMixin, CoreHasDependenciesMixin], base => {
  const superProto = base.prototype;

  class SchedulerCoreEvent extends base {}

  return SchedulerCoreEvent;
}) {}

var _window$bryntum$4;
const EngineMixin$4 =
/*  */
SchedulerCoreEvent;
/**
 * @module Scheduler/model/EventModel
 */

/**
 * This class represent a single event in your schedule, usually added to a {@link Scheduler.data.EventStore}.
 *
 * It is a subclass of the {@link Scheduler.model.TimeSpan}, which is in turn subclass of {@link Core.data.Model}.
 * Please refer to documentation of that class to become familiar with the base interface of the event.
 *
 * ## Async date calculations
 *
 * A record created from an {@link Scheduler/model/EventModel EventModel} is normally part of an {@link Scheduler.data.EventStore EventStore},
 * which in turn is part of a project. When dates or the duration of an event is changed, the project performs async calculations
 * to normalize the other fields.
 * For example if {@field-duration} is changed, it will calculate {@field-endDate}.
 *
 * As a result of this being an async operation, the values of other fields are not guaranteed to be up to date
 * immediately after a change. To ensure data is up to date, await the calculations to finish.
 *
 * For example, {@field-endDate} is not up to date after this operation:
 *
 * ```javascript
 * eventRecord.duration = 5;
 * // endDate not yet calculated
 * ```
 *
 * But if calculations are awaited it is up to date:
 *
 * ```javascript
 * eventRecord.duration = 5;
 * await eventRecord.project.commitAsync();
 * // endDate is calculated
 * ```
 *
 * As an alternative, you can also use `setAsync()` to trigger calculations directly after the change:
 *
 * ```javascript
 * await eventRecord.setAsync({ duration : 5});
 * // endDate is calculated
 * ```
 *
 * ## Subclassing the Event model class
 * The Event model has a few predefined fields as seen below. If you want to add new fields or change the options for the existing fields,
 * you can do that by subclassing this class (see example below).
 *
 * ```
 * class MyEvent extends EventModel {
 *
 *     static get fields() {
 *         return [
 *            // Add new field
 *            { name: 'myField', type : 'number', defaultValue : 0 }
 *         ];
 *     },
 *
 *     myCheckMethod() {
 *         return this.myField > 0
 *     },
 *
 *     ...
 * });
 * ```
 * If you in your data want to use other names for the {@field-startDate}, {@field-endDate}, {@field-resourceId} and name fields you can configure
 * them as seen below:
 * ```
 * class MyEvent extends EventModel {
 *
 *     static get fields() {
 *         return [
 *            { name: 'startDate', dataSource 'taskStart' },
 *            { name: 'endDate', dataSource 'taskEnd', format: 'YYYY-MM-DD' },
 *            { name: 'resourceId', dataSource 'userId' },
 *            { name: 'name', dataSource 'taskTitle' },
 *         ];
 *     },
 *     ...
 * });
 * ```
 *
 * Please refer to {@link Core.data.Model} for additional details.
 *
 * @extends Scheduler/model/TimeSpan
 * @mixes Scheduler/model/mixin/RecurringTimeSpan
 * @mixes Scheduler/model/mixin/EventModelMixin
 */

class EventModel extends EngineMixin$4.derive(TimeSpan).mixin(RecurringTimeSpan, PartOfProject, EventModelMixin) {}
EventModel.exposeProperties();
EventModel._$name = 'EventModel';

class AbstractEventStoreMixin extends Mixin([AbstractPartOfProjectStoreMixin], base => {
  const superProto = base.prototype;

  class AbstractEventStoreMixin extends base {
    constructor() {
      super(...arguments);
      this.assignmentsForRemoval = new Set();
      this.dependenciesForRemoval = new Set();
    }

    onDataChange(event) {
      var _a;

      super.onDataChange(event);
      if ((_a = event.removed) === null || _a === void 0 ? void 0 : _a.length) this.afterEventRemoval();
    }

    remove(records, silent) {
      const res = superProto.remove.call(this, records);
      this.afterEventRemoval();
      return res;
    }

    removeAll(silent) {
      const res = superProto.removeAll.call(this, silent);
      this.afterEventRemoval();
      return res;
    }

    afterEventRemoval() {
      const {
        assignmentsForRemoval,
        dependenciesForRemoval
      } = this;
      if (!assignmentsForRemoval) return;
      const assignmentStore = this.getAssignmentStore();

      if (assignmentStore && !assignmentStore.allAssignmentsForRemoval && assignmentsForRemoval.size) {
        const toRemove = [...assignmentsForRemoval].filter(assignment => !assignmentStore.assignmentsForRemoval.has(assignment));
        toRemove.length > 0 && assignmentStore.remove(toRemove);
      }

      assignmentsForRemoval.clear();
      const dependencyStore = this.getDependencyStore();

      if (dependencyStore && !dependencyStore.allDependenciesForRemoval && dependenciesForRemoval.size) {
        const toRemove = [...dependenciesForRemoval].filter(dependency => !dependencyStore.dependenciesForRemoval.has(dependency));
        toRemove.length > 0 && dependencyStore.remove(toRemove);
      }

      dependenciesForRemoval.clear();
    }

    processRecord(eventRecord, isDataset = false) {
      var _a;

      if (!((_a = this.project) === null || _a === void 0 ? void 0 : _a.isRepopulatingStores)) {
        const existingRecord = this.getById(eventRecord.id);
        const isReplacing = existingRecord && existingRecord !== eventRecord;

        if (isReplacing) {
          for (const assignment of existingRecord.assigned) {
            assignment.event = eventRecord;
          }
        }
      }

      return eventRecord;
    }

  }

  return AbstractEventStoreMixin;
}) {}

class ChronoEventStoreMixin extends Mixin([AbstractEventStoreMixin, ChronoPartOfProjectStoreMixin], base => {
  const superProto = base.prototype;

  class ChronoEventStoreMixin extends base {
    static get defaultConfig() {
      return {
        modelClass: SchedulerBasicEvent
      };
    }

    set data(value) {
      super.data = value;
      this.afterEventRemoval();
    }

  }

  return ChronoEventStoreMixin;
}) {}
class ChronoEventTreeStoreMixin extends Mixin([ChronoEventStoreMixin], base => {
  const superProto = base.prototype;

  class ChronoEventTreeStoreMixin extends base {
    buildRootNode() {
      return this.getProject() || {};
    }

    static get defaultConfig() {
      return {
        tree: true
      };
    }

  }

  return ChronoEventTreeStoreMixin;
}) {}

class CoreEventStoreMixin extends Mixin([AbstractEventStoreMixin, CorePartOfProjectStoreMixin], base => {
  const superProto = base.prototype;

  class CoreEventStoreMixin extends base {
    static get defaultConfig() {
      return {
        modelClass: SchedulerCoreEvent
      };
    }

    joinProject() {
      var _a;

      (_a = this.assignmentStore) === null || _a === void 0 ? void 0 : _a.linkAssignments(this, 'event');
    }

    afterLoadData() {
      var _a;

      this.afterEventRemoval();
      (_a = this.assignmentStore) === null || _a === void 0 ? void 0 : _a.linkAssignments(this, 'event');
    }

  }

  return CoreEventStoreMixin;
}) {}

var _window$bryntum$5;
const EngineMixin$5 =
/*  */
PartOfProject(CoreEventStoreMixin.derive(AjaxStore));
/**
 * @module Scheduler/data/EventStore
 */

/**
 * A store holding all the {@link Scheduler.model.EventModel events} to be rendered into a {@link Scheduler.view.Scheduler Scheduler}.
 *
 * This store only accepts a model class inheriting from {@link Scheduler.model.EventModel}.
 *
 * An EventStore is usually connected to a project, which binds it to other related stores (AssignmentStore,
 * ResourceStore and DependencyStore). The project also handles normalization/calculation of the data on the records in
 * the store. For example if a record is added with a `startDate` and an `endDate`, it will calculate the `duration`.
 *
 * The calculations happens async, records are not guaranteed to have up to date data until they are finished. To be
 * certain that calculations have finished, call `await project.commitAsync()` after store actions. Or use one of the
 * `xxAsync` functions, such as `loadDataAsync()`.
 *
 * Using `commitAsync()`:
 *
 * ```javascript
 * eventStore.data = [{ startDate, endDate }, ...];
 *
 * // duration of the record is not yet calculated
 *
 * await eventStore.project.commitAsync();
 *
 * // now it is
 * ```
 *
 * Using `loadDataAsync()`:
 *
 * ```javascript
 * await eventStore.loadDataAsync([{ startDate, endDate }, ...]);
 *
 * // duration is calculated
 * ```
 *
 * @mixes Scheduler/data/mixin/PartOfProject
 * @mixes Scheduler/data/mixin/SharedEventStoreMixin
 * @mixes Scheduler/data/mixin/EventStoreMixin
 * @mixes Scheduler/data/mixin/RecurringEventsMixin
 * @extends Core/data/AjaxStore
 */

class EventStore extends EngineMixin$5.mixin(SharedEventStoreMixin, RecurringEventsMixin, EventStoreMixin) {
  static get defaultConfig() {
    return {
      /**
       * Class used to represent records
       * @config {Scheduler.model.EventModel}
       * @default
       * @category Common
       * @typings { new(data: object): Model }
       */
      modelClass: EventModel
    };
  }

}
EventStore._$name = 'EventStore';

/**
 * @module Scheduler/model/DependencyBaseModel
 */

const canonicalDependencyTypes = ['SS', 'SF', 'FS', 'FF'];
/**
 * Base class used for both Scheduler and Gantt. Not intended to be used directly
 *
 * @extends Core/data/Model
 */

class DependencyBaseModel extends Model {
  /**
   * Set value for the specified field(s), triggering engine calculations immediately. See
   * {@link Core.data.Model#function-set Model#set()} for arguments.
   **
   * ```javascript
   * dependency.set('from', 2);
   * // dependency.fromEvent is not yet up to date
   *
   * await dependency.setAsync('from', 2);
   * // dependency.fromEvent is up to date
   * ```
   *
   * @param {String|Object} field The field to set value for, or an object with multiple values to set in one call
   * @param {*} value Value to set
   * @param {Boolean} [silent=false] Set to true to not trigger events
   * automatically.
   * @function setAsync
   * @category Editing
   * @async
   */
  //region Fields

  /**
   * An enumerable object, containing names for the dependency types integer constants.
   * - 0 StartToStart
   * - 1 StartToEnd
   * - 2 EndToStart
   * - 3 EndToEnd
   * @property {Object}
   * @readonly
   * @category Dependency
   */
  static get Type() {
    return {
      StartToStart: 0,
      StartToEnd: 1,
      EndToStart: 2,
      EndToEnd: 3
    };
  }

  static get fields() {
    return [// 3 mandatory fields

    /**
     * From event, id of source event
     * @field {String|number} from
     * @category Dependency
     */
    {
      name: 'from'
    },
    /**
     * To event, id of target event
     * @field {String|number} to
     * @category Dependency
     */
    {
      name: 'to'
    },
    /**
     * Dependency type, see static property Type
     * @field {Number} type
     * @default 2
     * @category Dependency
     */
    {
      name: 'type',
      type: 'int',
      defaultValue: 2
    },
    /**
     * CSS class to apply to lines drawn for the dependency
     * @field {String} cls
     * @category Styling
     */
    {
      name: 'cls',
      defaultValue: ''
    },
    /**
     * Bidirectional, drawn with arrows in both directions
     * @field {Boolean} bidirectional
     * @category Dependency
     */
    {
      name: 'bidirectional',
      type: 'boolean'
    },
    /**
     * Start side on source (top, left, bottom, right)
     * @field {String} fromSide
     * @category Dependency
     */
    {
      name: 'fromSide',
      type: 'string'
    },
    /**
     * End side on target (top, left, bottom, right)
     * @field {String} toSide
     * @category Dependency
     */
    {
      name: 'toSide',
      type: 'string'
    },
    /**
     * The magnitude of this dependency's lag (the number of units).
     * @field {Number} lag
     * @category Dependency
     */
    {
      name: 'lag',
      type: 'number',
      allowNull: true,
      defaultValue: 0
    },
    /**
     * The units of this dependency's lag, defaults to "d" (days). Valid values are:
     *
     * - "ms" (milliseconds)
     * - "s" (seconds)
     * - "m" (minutes)
     * - "h" (hours)
     * - "d" (days)
     * - "w" (weeks)
     * - "M" (months)
     * - "y" (years)
     *
     * This field is readonly after creation, to change `lagUnit` use {@link #function-setLag setLag()}.
     * @field {String} lagUnit
     * @category Dependency
     * @readonly
     */
    {
      name: 'lagUnit',
      type: 'string',
      defaultValue: 'd'
    }];
  } // fromEvent/toEvent defined in CoreDependencyMixin in engine

  /**
   * Gets/sets the source event of the dependency
   * @member {Scheduler.model.EventModel} fromEvent
   * @category Dependency
   */

  /**
   * Gets/sets the target event of the dependency
   * @member {Scheduler.model.EventModel} toEvent
   * @category Dependency
   */

  /**
   * Gets/sets the source event of the dependency
   *
   * @property {Scheduler.model.EventModel}
   * @deprecated 4.0.0 Deprecated in favor of fromEvent
   * @category Dependency
   */

  get sourceEvent() {
    VersionHelper.deprecate('scheduler', '5.0.0', 'Deprecated in favor of fromEvent');
    return this.fromEvent;
  }

  set sourceEvent(event) {
    this.fromEvent = event;
  }
  /**
   * Gets/sets the target event of the dependency
   *
   * @property {Scheduler.model.EventModel}
   * @deprecated 4.0.0 Deprecated in favor of toEvent
   * @category Dependency
   */

  get targetEvent() {
    VersionHelper.deprecate('scheduler', '5.0.0', 'Deprecated in favor of toEvent');
    return this.toEvent;
  }

  set targetEvent(event) {
    this.targetEvent = event;
  } //endregion
  //region Init

  construct(data) {
    // Engine expects fromEvent and toEvent, not from and to. We need to support both
    if (data.from != null) {
      data.fromEvent = data.from;
    }

    if (data.to != null) {
      data.toEvent = data.to;
    }

    super.construct(...arguments);
  } //endregion

  get eventStore() {
    var _this$unjoinedStores$;

    return this.eventStore || ((_this$unjoinedStores$ = this.unjoinedStores[0]) === null || _this$unjoinedStores$ === void 0 ? void 0 : _this$unjoinedStores$.eventStore);
  }

  set from(value) {
    const {
      fromEvent
    } = this; // When assigning a new id to an event, it will update the eventId of the assignment. But the assignments
    // event is still the same so we need to announce here

    if (fromEvent !== null && fromEvent !== void 0 && fromEvent.isModel && fromEvent.id === value) {
      this.set('from', value);
    } else {
      this.fromEvent = value;
    }
  }

  get from() {
    return this.get('from');
  }

  set to(value) {
    const {
      toEvent
    } = this; // When assigning a new id to an event, it will update the eventId of the assignment. But the assignments
    // event is still the same so we need to announce here

    if (toEvent !== null && toEvent !== void 0 && toEvent.isModel && toEvent.id === value) {
      this.set('to', value);
    } else {
      this.toEvent = value;
    }
  }

  get to() {
    return this.get('to');
  }
  /**
   * Alias to dependency type, but when set resets {@link #field-fromSide} & {@link #field-toSide} to null as well.
   *
   * @property {Number}
   * @category Dependency
   */

  get hardType() {
    return this.getHardType();
  }

  set hardType(type) {
    this.setHardType(type);
  }
  /**
   * Returns dependency hard type, see {@link #property-hardType}.
   *
   * @return {Number}
   * @category Dependency
   */

  getHardType() {
    return this.get('type');
  }
  /**
   * Sets dependency {@link #field-type} and resets {@link #field-fromSide} and {@link #field-toSide} to null.
   *
   * @param {Number} type
   * @category Dependency
   */

  setHardType(type) {
    let result;

    if (type !== this.hardType) {
      result = this.set({
        type,
        fromSide: null,
        toSide: null
      });
    }

    return result;
  }

  get lag() {
    return this.get('lag');
  }

  set lag(lag) {
    if (typeof lag === 'number') {
      this.set({
        lag
      });
    } else {
      this.setLag(lag);
    }
  }
  /**
   * Sets lag and lagUnit in one go. Only allowed way to change lagUnit, the lagUnit field is readonly after creation
   * @param {Number|String|Object} lag The lag value. May be just a numeric magnitude, or a full string descriptor eg '1d'
   * @param {String} [lagUnit] Unit for numeric lag value, see {@link #field-lagUnit} for valid values
   * @category Dependency
   */

  setLag(lag, lagUnit) {
    // Either they're only setting the magnitude
    // or, if it's a string, parse the full duration.
    if (arguments.length === 1) {
      if (typeof lag === 'number') {
        this.lag = lag;
      } else {
        lag = DateHelper.parseDuration(lag);
        this.set({
          lag: lag.magnitude,
          lagUnit: lag.unit
        });
      }

      return;
    } // Must be a number

    lag = parseFloat(lag);
    this.set({
      lag,
      lagUnit
    });
  }

  getLag() {
    if (this.lag) {
      return `${this.lag < 0 ? '-' : '+'}${Math.abs(this.lag)}${DateHelper.getShortNameOfUnit(this.lagUnit)}`;
    }

    return '';
  }
  /**
   * Property which encapsulates the lag's magnitude and units. An object which contains two properties:
   * @property {Core.data.Duration}
   * @property {Number} fullLag.magnitude The magnitude of the duration
   * @property {String} fullLag.unit The unit in which the duration is measured, eg `'d'` for days
   * @category Dependency
   */

  get fullLag() {
    return new Duration({
      unit: this.lagUnit,
      magnitude: this.lag
    });
  }

  set fullLag(lag) {
    if (typeof lag === 'string') {
      this.setLag(lag);
    } else {
      this.setLag(lag.magnitude, lag.unit);
    }
  }
  /**
   * Returns true if the linked events have been persisted (e.g. neither of them are 'phantoms')
   *
   * @property {Boolean}
   * @readonly
   * @category Editing
   */

  get isPersistable() {
    const me = this,
          {
      stores,
      unjoinedStores
    } = me,
          store = stores[0];
    let result;

    if (store) {
      const {
        fromEvent,
        toEvent
      } = me,
            crudManager = store.crudManager; // if crud manager is used it can deal with phantom source/target since it persists all records in one batch
      // if no crud manager used we have to wait till source/target are persisted

      result = fromEvent && (crudManager || !fromEvent.hasGeneratedId) && toEvent && (crudManager || !toEvent.hasGeneratedId);
    } else {
      result = Boolean(unjoinedStores[0]);
    }

    return result;
  }
  /**
   * Returns the source event of the dependency
   *
   * @return {Scheduler.model.EventModel} The source event of this dependency
   * @category Dependency
   * @deprecated 5.0 Deprecated in favor of `toEvent`
   */

  getSourceEvent() {
    VersionHelper.deprecate('scheduler', '5.0.0', 'Deprecated in favor of fromEvent');
    return this.fromEvent;
  }
  /**
   * Gets/sets the dependency type
   *
   * @property {Number}
   */

  /**
   * Gets/sets the name of field holding the CSS class for each rendered dependency element
   *
   * @property {String} cls
   */

  /**
   * Returns the target event of the dependency
   *
   * @return {Scheduler.model.EventModel} The target event of this dependency
   * @category Dependency
   * @deprecated 5.0 Deprecated in favor of `toEvent`
   */

  getTargetEvent() {
    VersionHelper.deprecate('scheduler', '5.0.0', 'Deprecated in favor of toEvent');
    return this.toEvent;
  }

  getDateRange(doNotNormalize = false) {
    const {
      fromEvent,
      toEvent
    } = this;

    if (fromEvent && toEvent && fromEvent.isScheduled && toEvent.isScheduled) {
      const Type = DependencyBaseModel.Type;
      let sourceDate, targetDate;

      switch (this.type) {
        case Type.StartToStart:
          sourceDate = fromEvent.startDate;
          targetDate = toEvent.startDate;
          break;

        case Type.StartToEnd:
          sourceDate = fromEvent.startDate;
          targetDate = toEvent.endDate;
          break;

        case Type.EndToEnd:
          sourceDate = fromEvent.endDate;
          targetDate = toEvent.endDate;
          break;

        case Type.EndToStart:
          sourceDate = fromEvent.endDate;
          targetDate = toEvent.startDate;
          break;
      }

      return {
        start: doNotNormalize ? sourceDate : DateHelper.min(sourceDate, targetDate),
        end: doNotNormalize ? targetDate : DateHelper.max(sourceDate, targetDate)
      };
    }

    return null;
  }
  /**
   * Applies given CSS class to dependency, the value doesn't persist
   *
   * @param {String} cls
   * @category Dependency
   */

  highlight(cls) {
    const h = this.highlighted ? this.highlighted.split(' ') : [];
    if (!h.includes(cls)) this.highlighted = h.concat(cls).join(' ');
  }
  /**
   * Removes given CSS class from dependency if applied, the value doesn't persist
   *
   * @param {String} cls
   * @category Dependency
   */

  unhighlight(cls) {
    const {
      highlighted
    } = this;

    if (highlighted) {
      const h = highlighted.split(' '),
            idx = h.findIndex(i => i === cls);

      if (idx >= 0) {
        h.splice(idx, 1);
        this.highlighted = h.join(' ');
      }
    }
  }
  /**
   * Checks if the given CSS class is applied to dependency.
   *
   * @param {String} cls
   * @return {Boolean}
   * @category Dependency
   */

  isHighlightedWith(cls) {
    return this.highlighted && this.highlighted.split(' ').includes(cls);
  }

  getConnectorString(raw) {
    const rawValue = canonicalDependencyTypes[this.type];

    if (raw) {
      return rawValue;
    } // FS => empty string; it's the default

    if (this.type === DependencyBaseModel.Type.EndToStart) {
      return '';
    }

    return rawValue;
  } // getConnectorStringFromType(type, raw) {
  //     const rawValue = canonicalDependencyTypes[type];
  //
  //     if (raw) {
  //         return rawValue;
  //     }
  //
  //     // FS => empty string; it's the default
  //     if (type === DependencyBaseModel.Type.EndToStart) {
  //         return '';
  //     }
  //
  //     const locale = LocaleManager.locale;
  //
  //     // See if there is a local version of SS, SF or FF
  //     if (locale) {
  //         const localized = locale.Scheduler && locale.Scheduler[rawValue];
  //         if (localized) {
  //             return localized;
  //         }
  //     }
  //
  //     return rawValue;
  // }
  // getConnectorString(raw) {
  //     return this.getConnectorStringFromType(this.type);
  // }
  // * getConnectorStringGenerator(raw) {
  //     return this.getConnectorStringFromType(yield this.$.type);
  // }

  toString() {
    return `${this.from}${this.getConnectorString()}${this.getLag()}`;
  }
  /**
   * Returns `true` if the dependency is valid. Has valid type and both source and target ids set and not links to itself.
   *
   * @property {Boolean}
   * @typings ignore
   * @category Editing
   */

  get isValid() {
    const {
      from,
      to,
      type
    } = this;
    return typeof type === 'number' && from && from !== '' && to != null && to !== '' && from !== to;
  }

  get fromEventName() {
    var _this$fromEvent;

    return ((_this$fromEvent = this.fromEvent) === null || _this$fromEvent === void 0 ? void 0 : _this$fromEvent.name) || '';
  }

  get toEventName() {
    var _this$toEvent;

    return ((_this$toEvent = this.toEvent) === null || _this$toEvent === void 0 ? void 0 : _this$toEvent.name) || '';
  }

}
DependencyBaseModel.exposeProperties();
DependencyBaseModel._$name = 'DependencyBaseModel';

var __decorate$d = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};
class BaseDependencyMixin extends Mixin([ChronoPartOfProjectModelMixin], base => {
  const superProto = base.prototype;

  class BaseDependencyMixin extends base {}

  __decorate$d([generic_field({
    bucket: 'outgoingDeps',
    resolver: function (id) {
      return this.getEventById(id);
    },
    modelFieldConfig: {
      persist: true,
      serialize: event => event === null || event === void 0 ? void 0 : event.id,
      isEqual: isSerializableEqual
    }
  }, ModelReferenceField)], BaseDependencyMixin.prototype, "fromEvent", void 0);

  __decorate$d([generic_field({
    bucket: 'incomingDeps',
    resolver: function (id) {
      return this.getEventById(id);
    },
    modelFieldConfig: {
      persist: true,
      serialize: event => event === null || event === void 0 ? void 0 : event.id,
      isEqual: isSerializableEqual
    }
  }, ModelReferenceField)], BaseDependencyMixin.prototype, "toEvent", void 0);

  __decorate$d([model_field({
    type: 'int',
    defaultValue: DependencyType.EndToStart
  })], BaseDependencyMixin.prototype, "type", void 0);

  return BaseDependencyMixin;
}) {}

class CoreDependencyMixin extends Mixin([CorePartOfProjectModelMixin], base => {
  const superProto = base.prototype;

  class CoreDependencyMixin extends base {
    static get fields() {
      return [{
        name: 'fromEvent',
        isEqual: (a, b) => a === b,
        persist: false
      }, {
        name: 'toEvent',
        isEqual: (a, b) => a === b,
        persist: false
      }];
    }

    setChanged(field, value, invalidate) {
      var _a, _b, _c;

      let update = false;

      if (field === 'fromEvent' || field === 'toEvent') {
        const event = isInstanceOf(value, CoreEventMixin) ? value : (_a = this.eventStore) === null || _a === void 0 ? void 0 : _a.getById(value);
        if (event) update = true;
        value = event || value;
      }

      superProto.setChanged.call(this, field, value, invalidate, true);

      if (update && !this.project.isPerformingCommit && !((_b = this.dependencyStore) === null || _b === void 0 ? void 0 : _b.isLoadingData)) {
        (_c = this.dependencyStore) === null || _c === void 0 ? void 0 : _c.updateIndices();
      }
    }

    joinProject() {
      superProto.joinProject.call(this);
      this.setChanged('fromEvent', this.get('fromEvent'));
      this.setChanged('toEvent', this.get('toEvent'));
    }

    calculateInvalidated() {
      var _a, _b;

      let {
        fromEvent,
        toEvent
      } = this.$changed;

      if (fromEvent !== null && !isInstanceOf(fromEvent, CoreEventMixin)) {
        const resolved = (_a = this.eventStore) === null || _a === void 0 ? void 0 : _a.getById(fromEvent);
        if (resolved) this.$changed.fromEvent = resolved;
      }

      if (toEvent !== null && !isInstanceOf(toEvent, CoreEventMixin)) {
        const resolved = (_b = this.eventStore) === null || _b === void 0 ? void 0 : _b.getById(toEvent);
        if (resolved) this.$changed.toEvent = resolved;
      }
    }

    set fromEvent(fromEvent) {
      this.setChanged('fromEvent', fromEvent);
    }

    get fromEvent() {
      const fromEvent = this.get('fromEvent');
      return isInstanceOf(fromEvent, CoreEventMixin) ? fromEvent : null;
    }

    set toEvent(toEvent) {
      this.setChanged('toEvent', toEvent);
    }

    get toEvent() {
      const toEvent = this.get('toEvent');
      return isInstanceOf(toEvent, CoreEventMixin) ? toEvent : null;
    }

  }

  return CoreDependencyMixin;
}) {}

var _window$bryntum$6;
const EngineMixin$6 =
/*  */
CoreDependencyMixin;
/**
 * @module Scheduler/model/DependencyModel
 */

/**
 * This model represents a dependency between two events, usually added to a {@link Scheduler.data.DependencyStore}.
 *
 * It is a subclass of the {@link Scheduler.model.DependencyBaseModel} class, which in its turn subclasses
 * {@link Core.data.Model}. Please refer to documentation of those classes to become familiar with the base interface of
 * this class.
 *
 * ## Fields and references
 *
 * A Dependency has a few predefined fields, see Fields below.  The name of any fields data source can be customized in
 * the subclass, see the example below. Please also refer to {@link Core.data.Model} for details.
 *
 * ```javascript
 * class MyDependency extends DependencyModel {
 *   static get fields() {
 *     return [
 *       { name: 'to', dataSource: 'targetId' },
 *       { name: 'from', dataSource: 'sourceId' }
 *     ]);
 *   }
 * }
 * ```
 *
 * After load and project normalization, these references are accessible (assuming their respective stores are loaded):
 * - `fromEvent` - The event on the start side of the dependency
 * - `toEvent` - The event on the end side of the dependency
 *
 * ## Async resolving of references
 *
 * As described above, a dependency has links to events. These references are populated async, using the calculation
 * engine of the project that the resource via its store is a part of. Because of this asyncness, references cannot be
 * used immediately after modifications:
 *
 * ```javascript
 * dependency.from = 2;
 * // dependency.fromEvent is not yet up to date
 * ```
 *
 * To make sure references are updated, wait for calculations to finish:
 *
 * ```javascript
 * dependency.from = 2;
 * await dependency.project.commitAsync();
 * // dependency.fromEvent is up to date
 * ```
 *
 * As an alternative, you can also use `setAsync()` to trigger calculations directly after the change:
 *
 * ```javascript
 * await dependency.setAsync({ from : 2});
 * // dependency.fromEvent is up to date
 * ```
 *
 * @extends Scheduler/model/DependencyBaseModel
 * @uninherit Core/data/mixin/TreeNode
 */

class DependencyModel extends PartOfProject(EngineMixin$6.derive(DependencyBaseModel)) {
  // Determines the type of dependency based on fromSide and toSide
  getTypeFromSides(fromSide, toSide, rtl) {
    const types = DependencyBaseModel.Type,
          startSide = rtl ? 'right' : 'left',
          endSide = rtl ? 'left' : 'right';

    if (fromSide === startSide) {
      return toSide === startSide ? types.StartToStart : types.StartToEnd;
    }

    return toSide === endSide ? types.EndToEnd : types.EndToStart;
  }

}
DependencyModel.exposeProperties();
DependencyModel._$name = 'DependencyModel';

/**
 * @module Scheduler/data/mixin/DependencyStoreMixin
 */

/**
 * This is a mixin, containing functionality related to managing dependencies.
 *
 * It is consumed by the regular {@link Scheduler.data.DependencyStore} class and Scheduler Pros counterpart.
 *
 * @mixin
 */

var DependencyStoreMixin = (Target => class DependencyStoreMixin extends Target {
  static get $name() {
    return 'DependencyStoreMixin';
  }
  /**
   * Add dependencies to the store.
   *
   * NOTE: References (fromEvent, toEvent) on the dependencies are determined async by a calculation engine. Thus they
   * cannot be directly accessed after using this function.
   *
   * For example:
   *
   * ```javascript
   * const [dependency] = dependencyStore.add({ from, to });
   * // dependency.fromEvent is not yet available
   * ```
   *
   * To guarantee references are set up, wait for calculations for finish:
   *
   * ```javascript
   * const [dependency] = dependencyStore.add({ from, to });
   * await dependencyStore.project.commitAsync();
   * // dependency.fromEvent is available (assuming EventStore is loaded and so on)
   * ```
   *
   * Alternatively use `addAsync()` instead:
   *
   * ```javascript
   * const [dependency] = await dependencyStore.addAsync({ from, to });
   * // dependency.fromEvent is available (assuming EventStore is loaded and so on)
   * ```
   *
   * @param {Scheduler.model.DependencyModel|Scheduler.model.DependencyModel[]|Object|Object[]} records
   * Array of records/data or a single record/data to add to store
   * @param {Boolean} [silent] Specify `true` to suppress events
   * @returns {Scheduler.model.DependencyModel[]} Added records
   * @function add
   * @category CRUD
   */

  /**
   * Add dependencies to the store and triggers calculations directly after. Await this function to have up to date
   * references on the added dependencies.
   *
   * ```javascript
   * const [dependency] = await dependencyStore.addAsync({ from, to });
   * // dependency.fromEvent is available (assuming EventStore is loaded and so on)
   * ```
   *
   * @param {Scheduler.model.DependencyModel|Scheduler.model.DependencyModel[]|Object|Object[]} records
   * Array of records/data or a single record/data to add to store
   * @param {Boolean} [silent] Specify `true` to suppress events
   * @returns {Scheduler.model.DependencyModel[]} Added records
   * @function addAsync
   * @category CRUD
   * @async
   */

  /**
   * Applies a new dataset to the DependencyStore. Use it to plug externally fetched data into the store.
   *
   * NOTE: References (fromEvent, toEvent) on the dependencies are determined async by a calculation engine. Thus
   * they cannot be directly accessed after assigning the new dataset.
   *
   * For example:
   *
   * ```javascript
   * dependencyStore.data = [{ from, to }];
   * // dependencyStore.first.fromEvent is not yet available
   * ```
   *
   * To guarantee references are available, wait for calculations for finish:
   *
   * ```javascript
   * dependencyStore.data = [{ from, to }];
   * await dependencyStore.project.commitAsync();
   * // dependencyStore.first.fromEvent is available
   * ```
   *
   * Alternatively use `loadDataAsync()` instead:
   *
   * ```javascript
   * await dependencyStore.loadDataAsync([{ from, to }]);
   * // dependencyStore.first.fromEvent is available
   * ```
   *
   * @member {Object[]} data
   * @category Records
   */

  /**
   * Applies a new dataset to the DependencyStore and triggers calculations directly after. Use it to plug externally
   * fetched data into the store.
   *
   * ```javascript
   * await dependencyStore.loadDataAsync([{ from, to }]);
   * // dependencyStore.first.fromEvent is available
   * ```
   *
   * @param {Object[]} data Array of DependencyModel data objects
   * @function loadDataAsync
   * @category CRUD
   * @async
   */

  static get defaultConfig() {
    return {
      /**
       * CrudManager must load stores in the correct order. Lowest first.
       * @private
       */
      loadPriority: 400,

      /**
       * CrudManager must sync stores in the correct order. Lowest first.
       * @private
       */
      syncPriority: 400,
      storeId: 'dependencies'
    };
  } // TODO: document

  reduceEventDependencies(event, reduceFn, result, flat = true, depsGetterFn) {
    depsGetterFn = depsGetterFn || (event => this.getEventDependencies(event));

    event = Array.isArray(event) ? event : [event];
    event.reduce((result, event) => {
      if (event.children && !flat) {
        event.traverse(evt => {
          result = depsGetterFn(evt).reduce(reduceFn, result);
        });
      } else {
        result = depsGetterFn(event).reduce(reduceFn, result);
      }
    }, result);
    return result;
  } // TODO: document

  mapEventDependencies(event, fn, filterFn, flat, depsGetterFn) {
    return this.reduceEventDependencies(event, (result, dependency) => {
      filterFn(dependency) && result.push(dependency);
      return result;
    }, [], flat, depsGetterFn);
  } // TODO: document

  mapEventPredecessors(event, fn, filterFn, flat) {
    return this.reduceEventPredecessors(event, (result, dependency) => {
      filterFn(dependency) && result.push(dependency);
      return result;
    }, [], flat);
  } // TODO: document

  mapEventSuccessors(event, fn, filterFn, flat) {
    return this.reduceEventSuccessors(event, (result, dependency) => {
      filterFn(dependency) && result.push(dependency);
      return result;
    }, [], flat);
  }
  /**
   * Returns all dependencies for a certain event (both incoming and outgoing)
   *
   * @param {Scheduler.model.EventModel} event
   * @return {Scheduler.model.DependencyModel[]}
   */

  getEventDependencies(event) {
    return [].concat(event.predecessors || [], event.successors || []);
  }
  /**
   * Returns all incoming dependencies of the given event
   *
   * @param {Scheduler.model.EventModel} event
   * @return {Scheduler.model.DependencyModel[]}
   * @deprecated 5.0.0
   */

  getEventPredecessors(event) {
    VersionHelper.deprecate('scheduler', '5.0.0', 'Superfluous, will be removed in favour of `event.predecessors`');
    return event.predecessors;
  }
  /**
   * Returns all outgoing dependencies of a event
   *
   * @param {Scheduler.model.EventModel} event
   * @return {Scheduler.model.DependencyModel[]}
   * @deprecated 5.0.0
   */

  getEventSuccessors(event) {
    VersionHelper.deprecate('scheduler', '5.0.0', 'Superfluous, will be removed in favour of `event.successors`');
    return event.successors;
  } // TODO: document

  removeEventDependencies(event) {
    this.remove(this.getEventDependencies(event));
  } // TODO: document

  removeEventPredecessors(event) {
    this.remove(event.predecessors);
  } // TODO: document

  removeEventSuccessors(event, flat) {
    this.remove(event.successors);
  }

  getBySourceTargetId(key) {
    //TODO: in original code this uses a keymap
    return this.records.find(r => key == this.constructor.makeDependencySourceTargetCompositeKey(r.from, r.to));
  }
  /**
   * Returns dependency model instance linking tasks with given ids. The dependency can be forward (from 1st
   * task to 2nd) or backward (from 2nd to 1st).
   *
   * @param {Scheduler.model.EventModel|String} sourceEvent 1st event
   * @param {Scheduler.model.EventModel|String} targetEvent 2nd event
   * @return {Scheduler.model.DependencyModel}
   */

  getDependencyForSourceAndTargetEvents(sourceEvent, targetEvent) {
    sourceEvent = Model.asId(sourceEvent);
    targetEvent = Model.asId(targetEvent);
    return this.getBySourceTargetId(this.constructor.makeDependencySourceTargetCompositeKey(sourceEvent, targetEvent));
  }
  /**
   * Returns a dependency model instance linking given events if such dependency exists in the store.
   * The dependency can be forward (from 1st event to 2nd) or backward (from 2nd to 1st).
   *
   * @param {Scheduler.model.EventModel|String} sourceEvent
   * @param {Scheduler.model.EventModel|String} targetEvent
   * @return {Scheduler.model.DependencyModel}
   */

  getEventsLinkingDependency(sourceEvent, targetEvent) {
    return this.getDependencyForSourceAndTargetEvents(sourceEvent, targetEvent) || this.getDependencyForSourceAndTargetEvents(targetEvent, sourceEvent);
  }
  /**
   * Validation method used to validate a dependency. Override and return `true` to indicate that an
   * existing dependency between two tasks is valid. For a new dependency being created please see
   * {@link #function-isValidDependencyToCreate}.
   *
   * @param {Scheduler.model.DependencyModel|Number|String} dependencyOrFromId The dependency model or from event id
   * @param {Number|String} [toId] To event id if the first parameter is not a dependency model instance
   * @param {Number} [type] Dependency {@link Scheduler.model.DependencyBaseModel#property-Type-static}  if the first parameter is not a dependency model instance.
   * @return {Boolean}
   */

  isValidDependency(dependencyOrFromId, toId, type) {
    if (arguments.length === 1) {
      toId = dependencyOrFromId.to || dependencyOrFromId.toEvent;
      dependencyOrFromId = dependencyOrFromId.from || dependencyOrFromId.fromEvent;
    } // This condition is supposed to map all model instances to be validated by project. Lowest common ancestor
    // for scheduler event, scheduler pro event and gantt task is TimeSpan

    if (dependencyOrFromId instanceof TimeSpan && toId instanceof TimeSpan) {
      // Not asserting dependency type here. Default value should normally suffice.
      return this.project.isValidDependency(dependencyOrFromId, toId, type);
    }

    return dependencyOrFromId != null && toId != null && dependencyOrFromId !== toId;
  }
  /**
   * Validation method used to validate a dependency while creating. Override and return `true` to indicate that
   * a new dependency is valid to be created.
   *
   * @param {Number|String} fromId `From` event id
   * @param {Number|String} toId `To` event id
   * @param {Number} type Dependency {@link Scheduler.model.DependencyBaseModel#property-Type-static}
   * @return {Boolean}
   */

  isValidDependencyToCreate(fromId, toId, type) {
    return this.isValidDependency(fromId, toId, type);
  }
  /**
   * Returns all dependencies highlighted with the given CSS class
   *
   * @param {String} cls
   * @return {Scheduler.model.DependencyBaseModel[]}
   */

  getHighlightedDependencies(cls) {
    return this.records.reduce((result, dep) => {
      if (dep.isHighlightedWith(cls)) result.push(dep);
      return result;
    }, []);
  }

  static makeDependencySourceTargetCompositeKey(from, to) {
    return `source(${from})-target(${to})`;
  } //region Product neutral

  getTimeSpanDependencies(record) {
    return this.getEventDependencies(record);
  } //endregion

});

class AbstractDependencyStoreMixin extends Mixin([AbstractPartOfProjectStoreMixin], base => {
  const superProto = base.prototype;

  class AbstractDependencyStoreMixin extends base {
    constructor() {
      super(...arguments);
      this.dependenciesForRemoval = new Set();
      this.allDependenciesForRemoval = false;
    }

    remove(records, silent) {
      this.dependenciesForRemoval = CIFromSetOrArrayOrValue(records).toSet();
      const res = superProto.remove.call(this, records);
      this.dependenciesForRemoval.clear();
      return res;
    }

    removeAll(silent) {
      this.allDependenciesForRemoval = true;
      const res = superProto.removeAll.call(this, silent);
      this.allDependenciesForRemoval = false;
      return res;
    }

  }

  return AbstractDependencyStoreMixin;
}) {}

class ChronoDependencyStoreMixin extends Mixin([AbstractDependencyStoreMixin, ChronoPartOfProjectStoreMixin], base => {
  const superProto = base.prototype;

  class ChronoDependencyStoreMixin extends base {
    set data(value) {
      this.allDependenciesForRemoval = true;
      super.data = value;
      this.allDependenciesForRemoval = false;
    }

  }

  return ChronoDependencyStoreMixin;
}) {}

const emptySet$1 = new Set();
class CoreDependencyStoreMixin extends Mixin([AbstractDependencyStoreMixin, CorePartOfProjectStoreMixin], base => {
  const superProto = base.prototype;

  class CoreDependencyStoreMixin extends base {
    constructor() {
      super(...arguments);
      this.dependenciesForRemoval = new Set();
      this.allDependenciesForRemoval = false;
    }

    static get defaultConfig() {
      return {
        modelClass: CoreDependencyMixin,
        storage: {
          extraKeys: [{
            property: 'fromEvent',
            unique: false
          }, {
            property: 'toEvent',
            unique: false
          }]
        }
      };
    }

    getIncomingDepsForEvent(event) {
      return this.storage.findItem('toEvent', event) || emptySet$1;
    }

    getOutgoingDepsForEvent(event) {
      return this.storage.findItem('fromEvent', event) || emptySet$1;
    }

    set data(value) {
      this.allDependenciesForRemoval = true;
      super.data = value;
      this.allDependenciesForRemoval = false;
    }

    updateIndices() {
      this.storage.rebuildIndices();
    }

    onCommitAsync() {
      this.updateIndices();
    }

  }

  return CoreDependencyStoreMixin;
}) {}

var _window$bryntum$7;
const EngineMixin$7 =
/*  */
PartOfProject(CoreDependencyStoreMixin.derive(AjaxStore));
/**
 * @module Scheduler/data/DependencyStore
 */

/**
 * A store representing a collection of dependencies between events in the {@link Scheduler.data.EventStore}.
 *
 * This store only accepts a model class inheriting from {@link Scheduler.model.DependencyModel}.
 *
 * A DependencyStore is usually connected to a project, which binds it to other related stores (EventStore,
 * AssignmentStore and ResourceStore). The project also handles references (fromEvent, toEvent) to related records
 * for the records in the store.
 *
 * Resolving the references happens async, records are not guaranteed to have up to date references until calculations
 * are finished. To be certain that references are resolved, call `await project.commitAsync()` after store actions. Or
 * use one of the `xxAsync` functions, such as `loadDataAsync()`.
 *
 * Using `commitAsync()`:
 *
 * ```javascript
 * dependencyStore.data = [{ from, to }, ...];
 *
 * // references (fromEvent, toEvent) not resolved yet
 *
 * await dependencyStore.project.commitAsync();
 *
 * // now they are
 * ```
 *
 * Using `loadDataAsync()`:
 *
 * ```javascript
 * await dependencyStore.loadDataAsync([{ from, to }, ...]);
 *
 * // references (fromEvent, toEvent) are resolved
 * ```
 *
 * @mixes Scheduler/data/mixin/PartOfProject
 * @mixes Scheduler/data/mixin/DependencyStoreMixin
 * @extends Core/data/AjaxStore
 */

class DependencyStore extends DependencyStoreMixin(EngineMixin$7.derive(AjaxStore)) {
  static get defaultConfig() {
    return {
      modelClass: DependencyModel
    };
  }

}
DependencyStore._$name = 'DependencyStore';

/**
 * @module Scheduler/data/mixin/ProjectCrudManager
 */

/**
 * This mixin provides Crud Manager functionality supporting loading of scheduling engine projects.
 *
 * @mixin
 * @mixes Scheduler/crud/AbstractCrudManagerMixin
 * @mixes Scheduler/crud/transport/AjaxTransport
 * @mixes Scheduler/crud/encoder/JsonEncoder
 */

var ProjectCrudManager = (Target => class ProjectCrudManager extends (Target || Base).mixin(AbstractCrudManagerMixin, AjaxTransport, JsonEncoder) {
  //region Config
  static get defaultConfig() {
    return {
      /**
       * A project that holds and links stores
       * @config {Scheduler.model.ProjectModel}
       */
      project: null
    };
  }

  startConfigure(config) {
    // process the project first which ingests any configured data sources,
    this.getConfig('project');
    super.startConfigure(config);
  }

  async doAutoLoad() {
    const {
      project
    } = this; // Delay autoLoad to after projects initial commit if configured with a project

    if (project) {
      await project.commitAsync();
    }

    return super.doAutoLoad();
  }

  loadCrudManagerData(response, options = {}) {
    const me = this,
          {
      project
    } = me; // we don't want reacting on store changes during loading of them

    me.suspendChangesTracking();
    super.loadCrudManagerData(...arguments); // if there is the project data provided

    if (response !== null && response !== void 0 && response.project) {
      me.applyingProjectResponse = true;
      Object.assign(project, response.project);
      me.applyingProjectResponse = false;
    }

    me.resumeChangesTracking();
  }

  async sync() {
    const {
      project
    } = this; // Suspend Crud Manager autoSync to not react on changes during sync() call

    this.suspendAutoSync(); // Make sure data is in a calculated state before syncing

    if (project) {
      await project.commitAsync();
    }

    let result;

    try {
      result = await super.sync();
    } finally {
      this.resumeAutoSync(false);
    }

    return result;
  }

  async applyResponse(requestType, response, options) {
    var _me$project;

    const me = this;

    if (me.isDestroyed || (_me$project = me.project) !== null && _me$project !== void 0 && _me$project.isDestroyed) {
      return;
    }

    me.trigger('startApplyResponse'); // clear "added"/"modified" collections on the stores
    // TODO: need to snapshot their state to be able to revert in case of an exception

    me.clearCrudStoresChanges({
      clearRemovedCollection: false
    });
    await super.applyResponse(requestType, response, options); // clear "removed" collection on the stores

    me.clearCrudStoresChanges({
      removeAddedRecords: false,
      clearAddedCollection: false,
      clearModifiedCollection: false,
      clearRemovedCollection: true
    }); // if there is the project data provided

    if (response !== null && response !== void 0 && response.project) {
      me.applyingProjectResponse = true;
      Object.assign(me, response.project);
      me.applyingProjectResponse = false;
    } // if we have a project

    if (me.project) {
      // response can force its type
      if (me.trackResponseType) {
        requestType = response.type || requestType;
      } // Make a boolean flag indicating what has triggered the propagation ("propagatingLoadChanges" or  "propagatingSyncChanges")

      const propagationFlag = `propagating${StringHelper.capitalize(requestType)}Changes`;
      me[propagationFlag] = true; // Wait till calculation gets done

      await me.project.commitAsync();
      me[propagationFlag] = false;
    }
  }

  clearCrudStoresChanges(flags = {
    removeAddedRecords: true,
    clearAddedCollection: true,
    clearModifiedCollection: true,
    clearRemovedCollection: true
  }) {
    const {
      removeAddedRecords,
      clearAddedCollection,
      clearModifiedCollection,
      clearRemovedCollection
    } = flags; // TODO: Change when https://app.assembla.com/spaces/bryntum/tickets/8975 is fixed
    // this.crudStores.forEach(store => store.store.clearChanges());

    this.forEachCrudStore(store => {
      if (removeAddedRecords) {
        // remove phantom records
        store.remove(this.added, true);
      }

      if (clearModifiedCollection) {
        store.modified.forEach(r => r.clearChanges(false));
        store.modified.clear();
      }

      if (clearAddedCollection) {
        store.added.clear();
      }

      if (clearRemovedCollection) {
        store.removed.clear();
      }
    });
  }

});

/**
 * @module Scheduler/model/ResourceTimeRangeModel
 */

/**
 * This class represent a single resource time range in your schedule.
 * To style the rendered elements, use {@link Scheduler.model.TimeSpan#field-cls cls} or {@link #field-timeRangeColor} field.
 * The class is used by the {@link Scheduler.feature.ResourceTimeRanges ResourceTimeRanges} feature.
 *
 * ## Recurring ranges support
 *
 * By default the class doesn't support recurrence.
 * In order to add its support please use {@link Scheduler/model/mixin/RecurringTimeSpan RecurringTimeSpan} mixin
 * (the store containing the model should in turn be mixed with {@link Scheduler.data.mixin.RecurringTimeSpansMixin RecurringTimeSpansMixin} class):
 *
 * ```js
 * // Mix RecurringTimeSpan (which adds recurrence support) to ResourceTimeRangeModel
 * class MyResourceTimeRange extends RecurringTimeSpan(ResourceTimeRangeModel) {};
 *
 * // Mix RecurringTimeSpansMixin (which adds recurrence support) to ResourceTimeRangeStore
 * class MyResourceTimeRangeStore extends RecurringTimeSpansMixin(ResourceTimeRangeStore) {
 *     static get defaultConfig() {
 *         return {
 *             // use our new MyResourceTimeRange model
 *             modelClass : MyResourceTimeRange
 *         };
 *     }
 * };
 *
 * // Make new store that supports time ranges recurrence
 * const store = new MyResourceTimeRangeStore({
 *     data : [{        {
 *         id             : 1,
 *         resourceId     : 'r1',
 *         startDate      : '2019-01-01T11:00',
 *         endDate        : '2019-01-01T13:00',
 *         name           : 'Coffee break',
 *         // this time range should repeat every day
 *         recurrenceRule : 'FREQ=DAILY'
 *     }]
 * });
 * ```
 *
 * @extends Scheduler/model/TimeSpan
 */

class ResourceTimeRangeModel extends TimeSpan {
  //region Fields
  static get fields() {
    return [
    /**
     * Id of the resource this time range is associated with
     * @field {String|Number} resourceId
     */
    'resourceId',
    /**
     * Controls this time ranges primary color, defaults to using current themes default time range color.
     * @field {String} timeRangeColor
     */
    'timeRangeColor'];
  }

  static get relationConfig() {
    return [
    /**
     * The associated resource, retrieved using a relation to a ResourceStore determined by the value assigned
     * to `resourceId`. The relation also lets you access all time ranges on a resource through
     * `ResourceModel#timeRanges`.
     * @property {Scheduler.model.ResourceModel} resource
     */
    {
      relationName: 'resource',
      fieldName: 'resourceId',
      store: 'resourceStore',
      collectionName: 'timeRanges',
      nullFieldOnRemove: true
    }];
  } //endregion
  // Used internally to differentiate between Event and ResourceTimeRange

  get isResourceTimeRange() {
    return true;
  } // To match EventModel API

  get resources() {
    return this.resource ? [this.resource] : [];
  }

}
ResourceTimeRangeModel._$name = 'ResourceTimeRangeModel';

/**
 * @module Scheduler/data/ResourceTimeRangeStore
 */

/**
 * A class representing a collection of resource time ranges.
 * Contains a collection of {@link Scheduler.model.ResourceTimeRangeModel ResourceTimeRangeModel} records.
 * The class is used by the {@link Scheduler.feature.ResourceTimeRanges ResourceTimeRanges} feature.
 *
 * ## Recurring ranges support
 *
 * By default the class doesn't support recurrence. In order to add its support
 * please use {@link Scheduler.data.mixin.RecurringTimeSpansMixin RecurringTimeSpansMixin} mixin
 * (the store model in turn should be mixed with {@link Scheduler/model/mixin/RecurringTimeSpan RecurringTimeSpan} class):
 *
 * ```js
 * // Mix RecurringTimeSpan (which adds recurrence support) to ResourceTimeRangeModel
 * class MyResourceTimeRange extends RecurringTimeSpan(ResourceTimeRangeModel) {};
 *
 * // Mix RecurringTimeSpansMixin (which adds recurrence support) to ResourceTimeRangeStore
 * class MyResourceTimeRangeStore extends RecurringTimeSpansMixin(ResourceTimeRangeStore) {
 *     static get defaultConfig() {
 *         return {
 *             // use our new MyResourceTimeRange model
 *             modelClass : MyResourceTimeRange
 *         };
 *     }
 * };
 *
 * // Make new store that supports time ranges recurrence
 * const store = new MyResourceTimeRangeStore({
 *     data : [{        {
 *         id             : 1,
 *         resourceId     : 'r1',
 *         startDate      : '2019-01-01T11:00',
 *         endDate        : '2019-01-01T13:00',
 *         name           : 'Coffee break',
 *         // this time range should repeat every day
 *         recurrenceRule : 'FREQ=DAILY'
 *     }]
 * });
 * ```
 *
 * @extends Core/data/AjaxStore
 */

class ResourceTimeRangeStore extends AjaxStore {
  static get defaultConfig() {
    return {
      /**
       * CrudManager must load stores in the correct order. Lowest first.
       * @private
       */
      loadPriority: 500,

      /**
       * CrudManager must sync stores in the correct order. Lowest first.
       * @private
       */
      syncPriority: 500,

      /**
       * This store should be linked to a ResourceStore to link the time ranges to resources
       * @config {Scheduler.data.ResourceStore}
       */
      resourceStore: null,
      modelClass: ResourceTimeRangeModel,
      storeId: 'resourceTimeRanges'
    };
  }

  set resourceStore(store) {
    this._resourceStore = store; // If store is assigned after configuration we need to init relations

    if (!this.isConfiguring) {
      this.initRelations(true);
    }
  }

  get resourceStore() {
    return this._resourceStore;
  } // Matching signature in EventStore to allow reusage of SchedulerStores#onInternalEventStoreChange()

  getResourcesForEvent(resourceTimeRange) {
    return [resourceTimeRange.resource];
  }

}
ResourceTimeRangeStore._$name = 'ResourceTimeRangeStore';

/**
 * @module Scheduler/model/mixin/ProjectModelMixin
 */

/**
 * Mixin that holds configuration shared between projects in Scheduler and Scheduler Pro.
 * @mixin
 */

var ProjectModelMixin = (Target => class ProjectModelMixin extends (Target || Model) {
  static get $name() {
    return 'ProjectModelMixin';
  } //region Config

  static get defaultConfig() {
    return {
      /**
       * Configuration options to provide to the STM manager
       *
       * @config {Object|Core.data.stm.StateTrackingManager}
       * @category Advanced
       */
      stm: {},

      /**
       * The constructor of the event model class, to be used in the project. Will be set as the
       * {@link Core.data.Store#config-modelClass modelClass} property of the {@link #property-eventStore}
       *
       * @config {Scheduler.model.EventModel}
       * @category Models & Stores
       */
      eventModelClass: EventModel,

      /**
       * The constructor of the dependency model class, to be used in the project. Will be set as the
       * {@link Core.data.Store#config-modelClass modelClass} property of the {@link #property-dependencyStore}
       *
       * @config {Scheduler.model.DependencyModel}
       * @category Models & Stores
       */
      dependencyModelClass: DependencyModel,
      timeRangeModelClass: TimeSpan,
      resourceTimeRangeModelClass: ResourceTimeRangeModel,

      /**
       * The constructor of the resource model class, to be used in the project. Will be set as the
       * {@link Core.data.Store#config-modelClass modelClass} property of the {@link #property-resourceStore}
       *
       * @config {Scheduler.model.ResourceModel}
       * @category Models & Stores
       */
      resourceModelClass: ResourceModel,

      /**
       * The constructor of the assignment model class, to be used in the project. Will be set as the
       * {@link Core.data.Store#config-modelClass modelClass} property of the {@link #property-assignmentStore}
       *
       * @config {Scheduler.model.AssignmentModel}
       * @category Models & Stores
       */
      assignmentModelClass: AssignmentModel,

      /**
       * The constructor to create an event store instance with. Should be a class, subclassing the
       * {@link Scheduler.data.EventStore}
       * @config {Scheduler.data.EventStore|Object}
       * @category Models & Stores
       */
      eventStoreClass: EventStore,

      /**
       * The constructor to create a dependency store instance with. Should be a class, subclassing the
       * {@link Scheduler.data.DependencyStore}
       * @config {Scheduler.data.DependencyStore|Object}
       * @category Models & Stores
       */
      dependencyStoreClass: DependencyStore,

      /**
       * The constructor to create a resource store instance with. Should be a class, subclassing the
       * {@link Scheduler.data.ResourceStore}
       * @config {Scheduler.data.ResourceStore|Object}
       * @category Models & Stores
       */
      resourceStoreClass: ResourceStore,

      /**
       * The constructor to create an assignment store instance with. Should be a class, subclassing the
       * {@link Scheduler.data.AssignmentStore}
       * @config {Scheduler.data.AssignmentStore|Object}
       * @category Models & Stores
       */
      assignmentStoreClass: AssignmentStore,

      /**
       * The constructor to create a time range store instance with. Should be a class subclassing the {@link Core.data.Store}
       * @config {Core.data.Store|Object}
       * @category Models & Stores
       */
      timeRangeStoreClass: Store,

      /**
       * The constructor to create a resource time range store instance with. Should be a class subclassing the {@link Scheduler.data.ResourceTimeRangeStore}
       * @config {Scheduler.data.ResourceTimeRangeStore|Object}
       * @category Models & Stores
       */
      resourceTimeRangeStoreClass: ResourceTimeRangeStore,

      /**
       * The initial data, to fill the {@link #property-eventStore eventStore} with.
       * Should be an array of {@link Scheduler.model.EventModel EventModels} or it's configuration objects.
       *
       * @config {Scheduler.model.EventModel[]} eventsData
       * @category Inline data
       */

      /**
       * The initial data, to fill the {@link #property-dependencyStore dependencyStore} with.
       * Should be an array of {@link Scheduler.model.DependencyModel DependencyModels} or it's configuration
       * objects.
       *
       * @config {Scheduler.model.DependencyModel[]} [dependenciesData]
       * @category Inline data
       */

      /**
       * The initial data, to fill the {@link #property-resourceStore resourceStore} with.
       * Should be an array of {@link Scheduler.model.ResourceModel ResourceModels} or it's configuration objects.
       *
       * @config {Scheduler.model.ResourceModel[]} [resourcesData]
       * @category Inline data
       */

      /**
       * The initial data, to fill the {@link #property-assignmentStore assignmentStore} with.
       * Should be an array of {@link Scheduler.model.AssignmentModel AssignmentModels} or it's configuration objects.
       *
       * @config {Scheduler.model.AssignmentModel[]} [assignmentsData]
       * @category Inline data
       */

      /**
       * The initial data, to fill the {@link #property-timeRangeStore timeRangeStore} with.
       * Should be an array of {@link Scheduler.model.TimeSpan TimeSpan} or it's configuration objects.
       *
       * @config {Scheduler.model.TimeSpan[]} [timeRangesData]
       * @category Inline data
       */

      /**
       * The initial data, to fill the {@link #property-resourceTimeRangeStore resourceTimeRangeStore} with.
       * Should be an array of {@link Scheduler.model.ResourceTimeRangeModel ResourceTimeRangeModel} or it's configuration objects.
       *
       * @config {Scheduler.model.ResourceTimeRangeModel[]} [resourceTimeRangesData]
       * @category Inline data
       */
      eventStore: {},
      assignmentStore: {},
      dependencyStore: {},
      resourceStore: {},
      timeRangeStore: null,
      resourceTimeRangeStore: null
    };
  } //endregion
  //region Properties

  /**
   * State tracking manager instance the project relies on
   * @member {Core.data.stm.StateTrackingManager} stm
   */

  /**
   * The {@link Scheduler.data.EventStore store} holding the events information.
   *
   * See also {@link Scheduler.model.EventModel}
   *
   * @member {Scheduler.data.EventStore} eventStore
   * @category Models & Stores
   */

  /**
   * An {@link Scheduler.data.EventStore} instance or a config object.
   * @config {Scheduler.data.EventStore|Object} eventStore
   * @category Models & Stores
   */

  /**
   * The {@link Scheduler.data.DependencyStore store} holding the dependencies information.
   *
   * See also {@link Scheduler.model.DependencyModel}
   *
   * @member {Scheduler.data.DependencyStore} dependencyStore
   * @category Models & Stores
   */

  /**
   * A {@link Scheduler.data.DependencyStore} instance or a config object.
   * @config {Scheduler.data.DependencyStore|Object} dependencyStore
   * @category Models & Stores
   */

  /**
   * The {@link Scheduler.data.ResourceStore store} holding the resources that can be assigned to the events in the event store.
   *
   * See also {@link Scheduler.model.ResourceModel}
   *
   * @member {Scheduler.data.ResourceStore} resourceStore
   * @category Models & Stores
   */

  /**
   * A {@link Scheduler.data.ResourceStore} instance or a config object.
   * @config {Scheduler.data.ResourceStore|Object} resourceStore
   * @category Models & Stores
   */

  /**
   * The {@link Scheduler.data.AssignmentStore store} holding the assignments information.
   *
   * See also {@link Scheduler.model.AssignmentModel}
   *
   * @member {Scheduler.data.AssignmentStore} assignmentStore
   * @category Models & Stores
   */

  /**
   * A {@link Scheduler.data.AssignmentStore} instance or a config object.
   * @config {Scheduler.data.AssignmentStore|Object} assignmentStore
   * @category Models & Stores
   */

  /**
   * The {@link Core.data.Store store} holding the time ranges information.
   *
   * See also {@link Scheduler.model.TimeSpan}
   *
   * @member {Core.data.Store} timeRangeStore
   * @category Models & Stores
   */

  /**
   * A {@link Core.data.Store} instance or a config object.
   * @config {Core.data.Store|Object} timeRangeStore
   * @category Models & Stores
   */

  /**
   * The {@link Scheduler.data.ResourceTimeRangeStore store} holding the resource time ranges information.
   *
   * See also {@link Scheduler.model.ResourceTimeRangeModel}
   *
   * @member {Scheduler.data.ResourceTimeRangeStore} resourceTimeRangeStore
   * @category Models & Stores
   */

  /**
   * A {@link Scheduler.data.ResourceTimeRangeStore} instance or a config object.
   * @config {Scheduler.data.ResourceTimeRangeStore|Object} resourceTimeRangeStore
   * @category Models & Stores
   */
  //endregion
  //region Functions

  /**
   * Accepts a "data package" consisting of data for the projects stores, which is then loaded into the stores.
   *
   * The package can hold data for EventStore, AssignmentStore, ResourceStore and DependencyStore. It uses the same
   * format as when creating a project with inline data:
   *
   * ```javascript
   * await project.loadInlineData({
   *     eventsData       : [...],
   *     resourcesData    : [...],
   *     assignmentsData  : [...],
   *     dependenciesData : [...]
   * });
   * ```
   *
   * After populating the stores it commits the project, starting its calculations. By awaiting `loadInlineData()` you
   * can be sure that project calculations are finished.
   *
   * @function loadInlineData
   * @param {Object} dataPackage A data package as described above
   * @fires load
   * @async
   */

  /**
   * Project changes (CRUD operations to records in its stores) are automatically committed on a buffer to the
   * underlying graph based calculation engine. The engine performs it calculations async.
   *
   * By calling this function, the commit happens right away. And by awaiting it you are sure that project
   * calculations are finished and that references between records are up to date.
   *
   * The returned promise is resolved with an object. If that object has `rejectedWith` set, there has been a conflict and the calculation failed.
   *
   * ```javascript
   * // Move an event in time
   * eventStore.first.shift(1);
   *
   * // Trigger calculations directly and wait for them to finish
   * const result = await project.commitAsync();
   *
   * if (result.rejectedWith) {
   *     // there was a conflict during the scheduling
   * }
   * ```
   *
   * @async
   * @returns {Promise}
   * @function commitAsync
   */
  //endregion
  //region Init

  construct(config = {}) {
    super.construct(...arguments); // These stores are not handled by engine, but still held on project

    const me = this;

    if (!me.timeRangeStore) {
      me.timeRangeStore = new me.timeRangeStoreClass({
        modelClass: me.timeRangeModelClass,
        storeId: 'timeRanges'
      });
    }

    if (!me.resourceTimeRangeStore) {
      me.resourceTimeRangeStore = new me.resourceTimeRangeStoreClass({
        modelClass: me.resourceTimeRangeModelClass
      });
    }

    if (me.resourceTimeRangesData) {
      me.resourceTimeRangeStore.data = me.resourceTimeRangesData;
    }

    if (me.timeRangesData) {
      me.timeRangeStore.data = me.timeRangesData;
    }
  } //endregion
  //region JSON

  /**
   * Returns the data from the records of the projects stores, in a format that can be consumed by `loadInlineData()`.
   *
   * Used by JSON.stringify to correctly convert this record to json.
   *
   *
   * ```javascript
   * const project = new ProjectModel({
   *     eventsData       : [...],
   *     resourcesData    : [...],
   *     assignmentsData  : [...],
   *     dependenciesData : [...]
   * });
   *
   * const json = project.toJSON();
   *
   * // json:
   * {
   *     eventsData : [...],
   *     resourcesData : [...],
   *     dependenciesData : [...],
   *     assignmentsData : [...]
   * }
   * ```
   *
   * Output can be consumed by `loadInlineData()`:
   *
   * ```javascript
   * const json = project.toJSON();
   *
   * // Plug it back in later
   * project.loadInlineData(json);
   * ```
   *
   * @returns {Object}
   * @category JSON
   */

  toJSON() {
    const me = this,
          result = {
      eventsData: me.eventStore.toJSON(),
      resourcesData: me.resourceStore.toJSON(),
      dependenciesData: me.dependencyStore.toJSON()
    };

    if (!me.eventStore.usesSingleAssignment) {
      result.assignmentsData = me.assignmentStore.toJSON();
    }

    return result;
  }
  /**
   * Get or set project data (records from its stores) as a JSON string.
   *
   * Get a JSON string:
   *
   * ```javascript
   * const project = new ProjectModel({
   *     eventsData       : [...],
   *     resourcesData    : [...],
   *     assignmentsData  : [...],
   *     dependenciesData : [...]
   * });
   *
   * const jsonString = project.json;
   *
   * // jsonString:
   * '{"eventsData":[...],"resourcesData":[...],...}'
   * ```
   *
   * Set a JSON string (to populate the project stores):
   *
   * ```javascript
   * project.json = '{"eventsData":[...],"resourcesData":[...],...}'
   * ```
   *
   * @property {String}
   */

  get json() {
    return super.json;
  }

  set json(json) {
    if (typeof json === 'string') {
      json = StringHelper.safeJsonParse(json);
    }

    this.loadInlineData(json);
  } //endregion

  afterChange(toSet, wasSet) {
    super.afterChange(...arguments);

    if (wasSet.calendar) {
      this.trigger('calendarChange');
    }
  }

});

/**
 * @module Scheduler/data/util/ModelPersistencyManager
 */

/**
 * This class manages model persistency, it listens to model stores' beforesync event and removes all non persistable
 * records from sync operation. The logic has meaning only for CRUD-less sync operations.
 *
 * @private
 */

class ModelPersistencyManager extends Base {
  // region Event attachers
  set eventStore(newEventStore) {
    const me = this;
    me.eventStoreDetacher && me.eventStoreDetacher();
    me._eventStore = newEventStore;

    if (newEventStore && newEventStore.autoCommit) {
      me.eventStoreDetacher = newEventStore.on({
        beforecommit: me.onEventStoreBeforeSync,
        thisObj: me,
        detachable: true,
        // Just in case
        prio: 100
      });
    }
  }

  get eventStore() {
    return this._eventStore;
  }

  set resourceStore(newResourceStore) {
    const me = this;
    me.resourceStoreDetacher && me.resourceStoreDetacher();
    me._resourceStore = newResourceStore;

    if (newResourceStore && newResourceStore.autoCommit) {
      me.resourceStoreDetacher = newResourceStore.on({
        beforecommit: me.onResourceStoreBeforeSync,
        thisObj: me,
        detachable: true,
        // Just in case
        prio: 100
      });
    }
  }

  get resourceStore() {
    return this._resourceStore;
  }

  set assignmentStore(newAssignmentStore) {
    const me = this;
    me.assignmentStoreDetacher && me.assignmentStoreDetacher();
    me._assignmentStore = newAssignmentStore;

    if (newAssignmentStore && newAssignmentStore.autoSync) {
      me.assignmentStoreDetacher = newAssignmentStore.on({
        beforecommit: me.onAssignmentStoreBeforeSync,
        thisObj: me,
        detachable: true,
        // Just in case
        prio: 100
      });
    }
  }

  get assignmentStore() {
    return this._assignmentStore;
  }

  set dependencyStore(newDependencyStore) {
    const me = this;
    me.dependencyStoreDetacher && me.dependencyStoreDetacher();
    me._dependencyStore = newDependencyStore;

    if (newDependencyStore && newDependencyStore.autoSync) {
      me.dependencyStoreDetacher = newDependencyStore.on({
        beforecommit: me.onDependencyStoreBeforeSync,
        thisObj: me,
        detachable: true,
        // Just in case
        prio: 100
      });
    }
  }

  get dependencyStore() {
    return this._dependencyStore;
  } // endregion
  // region Event handlers

  onEventStoreBeforeSync({
    changes
  }) {
    const me = this;
    me.removeNonPersistableRecordsToCreate(changes);
    return me.shallContinueSync(changes);
  }

  onResourceStoreBeforeSync({
    changes
  }) {
    const me = this;
    me.removeNonPersistableRecordsToCreate(changes);
    return me.shallContinueSync(changes);
  }

  onAssignmentStoreBeforeSync({
    changes
  }) {
    const me = this;
    me.removeNonPersistableRecordsToCreate(changes);
    return me.shallContinueSync(changes);
  }

  onDependencyStoreBeforeSync({
    changes
  }) {
    const me = this;
    me.removeNonPersistableRecordsToCreate(changes);
    return me.shallContinueSync(changes);
  } // endregion
  // region Management rules

  removeNonPersistableRecordsToCreate(changes) {
    let recordsToCreate = changes.added || [],
        r,
        i; // We remove from the array we iterate thus we iterate from end to start

    for (i = recordsToCreate.length - 1; i >= 0; --i) {
      r = recordsToCreate[i];

      if (!r.isPersistable) {
        recordsToCreate.splice(recordsToCreate.indexOf(r), 1);
      }
    } // Prevent empty create request

    if (recordsToCreate.length === 0) {
      changes.added.length = 0;
    }
  }

  shallContinueSync(options) {
    return Boolean(options.added && options.added.length > 0 || options.modified && options.modified.length > 0 || options.removed && options.removed.length > 0);
  } // endregion

}
ModelPersistencyManager._$name = 'ModelPersistencyManager';

class Schema extends Base$1 {
  constructor() {
    super(...arguments);
    this.entities = new Map();
  }

  hasEntity(name) {
    return this.entities.has(name);
  }

  getEntity(name) {
    return this.entities.get(name);
  }

  addEntity(entity) {
    const name = entity.name;
    if (!name) throw new Error(`Entity must have a name`);
    if (this.hasEntity(name)) throw new Error(`Entity with name [${String(name)}] already exists`);
    entity.schema = this;
    this.entities.set(name, entity);
    return entity;
  }

  getEntityDecorator() {
    return target => {
      const entity = entityDecoratorBody(target);
      this.addEntity(entity);
      return target;
    };
  }

}
const entityDecoratorBody = target => {
  const name = target.name;
  if (!name) throw new Error(`Can't add entity - the target class has no name`);
  return ensureEntityOnPrototype(target.prototype);
};

var __decorate$e = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const ConflictSymbol = Symbol('ConflictSymbol');
var ConflictResolutionResult;

(function (ConflictResolutionResult) {
  ConflictResolutionResult["Cancel"] = "Cancel";
  ConflictResolutionResult["Resume"] = "Resume";
})(ConflictResolutionResult || (ConflictResolutionResult = {}));

class ConflictEffect extends Effect {
  constructor() {
    super(...arguments);
    this.handler = ConflictSymbol;
  }

}

__decorate$e([prototypeValue(false)], ConflictEffect.prototype, "sync", void 0);

class EngineRevision extends Revision {
  constructor() {
    super(...arguments);
    this.failedResolutionReferences = new Map();
  }

}
class EngineTransaction extends Transaction$1 {
  constructor() {
    super(...arguments);
    this.candidateClass = EngineRevision;
  }

  initialize(...args) {
    super.initialize(...args);
    this.candidate.failedResolutionReferences = new Map(this.baseRevision.failedResolutionReferences);
  }

  addIdentifier(identifier, proposedValue, ...args) {
    this.markFailedResolutionReferences();
    return super.addIdentifier(identifier, proposedValue, ...args);
  }

  markFailedResolutionReferences() {
    this.candidate.failedResolutionReferences.forEach((failedResolutionValue, identifier) => {
      this.write(identifier, failedResolutionValue);
    });
    this.candidate.failedResolutionReferences.clear();
  }

}
class EngineReplica extends Mixin([Replica], base => {
  const superProto = base.prototype;

  class EngineReplica extends base {
    constructor() {
      super(...arguments);
      this.baseRevision = EngineRevision.new();
      this.transactionClass = EngineTransaction;
      this.autoCommitMode = 'async';
      this.onComputationCycle = 'reject';
      this.silenceInitialCommit = true;
      this.ignoreInitialCommitComputationCycles = true;
    }

    onPropagationProgressNotification(notification) {
      if (this.enableProgressNotifications && this.project) this.project.trigger('progress', notification);
    }

    propagate(args) {
      return this.commitAsync(args);
    }

    async commitAsync(args) {
      this.project.trigger('beforeCommit');

      if (this.isInitialCommit && this.ignoreInitialCommitComputationCycles) {
        this._onComputationCycle = this._onComputationCycle || this.onComputationCycle;
        this.onComputationCycle = 'ignore';
      }

      const replacedReplicaResult = this.project.beforeCommitAsync();
      if (replacedReplicaResult) return replacedReplicaResult;
      return superProto.commitAsync.call(this, args);
    }

    get isInitialCommit() {
      return this.project.isInitialCommit || super.isInitialCommit;
    }

    set isInitialCommit(value) {
      super.isInitialCommit = value;
    }

    async finalizeCommitAsync(transactionResult) {
      const {
        project
      } = this;
      if (!project || project.isDestroyed) return;
      const {
        entries
      } = transactionResult;
      const autoCommitStores = new Set();
      if (BrowserHelper.global.DEBUG) console.timeEnd('Time to visible');
      const {
        isInitialCommit,
        silenceInitialCommit
      } = this;
      const silenceCommit = isInitialCommit && silenceInitialCommit;

      if (isInitialCommit) {
        project.isInitialCommitPerformed = true;
        if (this.ignoreInitialCommitComputationCycles) this.onComputationCycle = this._onComputationCycle;
      }

      project.isWritingData = true;
      project.hasLoadedDataToCommit = false;
      project.trigger('refresh', {
        isInitialCommit
      });
      await new Promise(resolve => {
        setTimeout(() => {
          var _a, _b;

          if (!project.isDestroyed && !transactionResult.transaction.rejectedWith) {
            (_a = project.suspendChangesTracking) === null || _a === void 0 ? void 0 : _a.call(project);
            if (BrowserHelper.global.DEBUG) console.time('Finalize propagation');
            const records = new Set();

            for (const quark of entries.values()) {
              const identifier = quark.identifier;
              const quarkValue = quark.getValue();
              const {
                field
              } = identifier;
              if (quark.isShadow() || !identifier[IsChronoModelSymbol] || quarkValue === TombStone || field instanceof ModelBucketField) continue;
              const record = identifier.self;
              const store = record.firstStore;

              if (!records.has(record)) {
                record.beginBatch();
                records.add(record);
              }

              if ((store === null || store === void 0 ? void 0 : store.autoCommit) && !autoCommitStores.has(store)) {
                store.suspendAutoCommit();
                autoCommitStores.add(store);
              }

              record.set(field.name, quarkValue);
            }

            for (const record of records) {
              record.ignoreBag = silenceCommit;
              record.endBatch(silenceCommit, true);
              record.ignoreBag = false;
            }

            if (BrowserHelper.global.DEBUG) console.timeEnd('Finalize propagation');
            project.trigger('dataReady', {
              records,
              isInitialCommit
            });
            (_b = project.resumeChangesTracking) === null || _b === void 0 ? void 0 : _b.call(project, silenceCommit);
            autoCommitStores.forEach(store => store.resumeAutoCommit());

            if (silenceCommit) {
              [project.eventStore, project.dependencyStore, project.resourceStore, project.assignmentStore, project.calendarManagerStore].forEach(store => store.acceptChanges());
            }
          }

          project.isWritingData = false;
          resolve();
        }, 0);
      });
    }

    async [ConflictSymbol](effect, transaction) {
      return this.project.onSchedulingConflict(effect, transaction);
    }

    [RejectSymbol](effect, transaction) {
      return super[RejectSymbol](effect, transaction);
    }

  }

  return EngineReplica;
}) {}

var __decorate$f = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};
class DurationConverterMixin extends Mixin([ChronoModelMixin], base => {
  const superProto = base.prototype;

  class DurationConverterMixin extends base {
    *calculateUnitsInMs() {
      const hoursPerDay = yield this.$.hoursPerDay;
      const daysPerWeek = yield this.$.daysPerWeek;
      const daysPerMonth = yield this.$.daysPerMonth;
      return {
        millisecond: 1,
        second: 1000,
        minute: 60 * 1000,
        hour: 60 * 60 * 1000,
        day: hoursPerDay * 60 * 60 * 1000,
        week: daysPerWeek * hoursPerDay * 60 * 60 * 1000,
        month: daysPerMonth * hoursPerDay * 60 * 60 * 1000,
        quarter: 3 * daysPerMonth * hoursPerDay * 60 * 60 * 1000,
        year: 4 * 3 * daysPerMonth * hoursPerDay * 60 * 60 * 1000
      };
    }

    convertDuration(duration, fromUnit, toUnit) {
      let result = duration;

      if (fromUnit !== toUnit) {
        result = duration * this.unitsInMs[fromUnit] / this.unitsInMs[toUnit];
      }

      return result;
    }

    *$convertDuration(duration, fromUnit, toUnit) {
      if (!fromUnit || !toUnit) throw new Error("Conversion unit not provided");
      const unitsInMs = yield this.$.unitsInMs;
      let result = duration;

      if (fromUnit !== toUnit) {
        result = duration * unitsInMs[fromUnit] / unitsInMs[toUnit];
      }

      return result;
    }

  }

  __decorate$f([field()], DurationConverterMixin.prototype, "unitsInMs", void 0);

  __decorate$f([model_field({
    type: 'number',
    defaultValue: 24
  })], DurationConverterMixin.prototype, "hoursPerDay", void 0);

  __decorate$f([model_field({
    type: 'number',
    defaultValue: 7
  })], DurationConverterMixin.prototype, "daysPerWeek", void 0);

  __decorate$f([model_field({
    type: 'number',
    defaultValue: 30
  })], DurationConverterMixin.prototype, "daysPerMonth", void 0);

  __decorate$f([calculate('unitsInMs')], DurationConverterMixin.prototype, "calculateUnitsInMs", null);

  return DurationConverterMixin;
}) {}

class AbstractCalendarManagerStoreMixin extends Mixin([AbstractPartOfProjectStoreMixin], base => {
  const superProto = base.prototype;

  class AbstractCalendarManagerStoreMixin extends base {}

  return AbstractCalendarManagerStoreMixin;
}) {}

class ChronoCalendarManagerStoreMixin extends Mixin([AbstractCalendarManagerStoreMixin, ChronoPartOfProjectStoreMixin], base => {
  const superProto = base.prototype;

  class ChronoCalendarManagerStoreMixin extends base {
    static get defaultConfig() {
      return {
        tree: true,
        modelClass: BaseCalendarMixin
      };
    }

  }

  return ChronoCalendarManagerStoreMixin;
}) {}

class EventsWrapper extends Mixin([], Events) {}
class DelayableWrapper extends Mixin([], Delayable) {}
class AbstractProjectMixin extends Mixin([EventsWrapper, DelayableWrapper, Model], base => {
  const superProto = base.prototype;

  class AbstractProjectMixin extends base {
    get isRepopulatingStores() {
      return false;
    }

    get isInitialCommit() {
      return !this.isInitialCommitPerformed || this.hasLoadedDataToCommit;
    }

    construct(config = {}) {
      this.isInitialCommitPerformed = false;
      this.isLoadingInlineData = false;
      this.isWritingData = false;
      this.hasLoadedDataToCommit = false;
      superProto.construct.call(this, config);
      this.silenceInitialCommit = 'silenceInitialCommit' in config ? config.silenceInitialCommit : true;
    }

    repopulateStore(store) {}

    repopulateReplica() {}

    async commitAsync() {
      throw new Error("Abstract method called");
    }

    isEngineReady() {
      throw new Error("Abstract method called");
    }

  }

  return AbstractProjectMixin;
}) {}

class ChronoAbstractProjectMixin extends Mixin([ChronoModelMixin, AbstractProjectMixin], base => {
  const superProto = base.prototype;

  class ChronoAbstractProjectMixin extends base {
    getGraph() {
      return this.replica;
    }

    beforeCommitAsync() {
      return null;
    }

    async commitAsync() {
      return this.replica.commitAsync();
    }

    async onSchedulingConflict(conflict, transaction) {
      if (this.hasListener('schedulingConflict')) {
        return new Promise((resolve, reject) => {
          this.trigger('schedulingConflict', {
            continueWithResolutionResult: resolve,
            conflict
          });
        });
      }

      return ConflictResolutionResult.Cancel;
    }

  }

  return ChronoAbstractProjectMixin;
}) {}

class HasSubEventsMixin extends Mixin([BaseEventMixin], base => {
  const superProto = base.prototype;

  class HasSubEventsMixin extends base {
    *hasSubEvents() {
      throw new Error("Abstract method `hasSubEvents` has been called");
    }

    *subEventsIterable() {
      throw new Error("Abstract method `subEventsIterable` has been called");
    }

    *calculateStartDatePure() {
      const manuallyScheduled = yield this.$.manuallyScheduled;
      const hasSubEvents = yield* this.hasSubEvents();

      if (!manuallyScheduled && hasSubEvents) {
        return yield* this.calculateMinChildrenStartDate();
      } else {
        return yield* superProto.calculateStartDatePure.call(this);
      }
    }

    *calculateEndDatePure() {
      const manuallyScheduled = yield this.$.manuallyScheduled;
      const hasSubEvents = yield* this.hasSubEvents();

      if (!manuallyScheduled && hasSubEvents) {
        return yield* this.calculateMaxChildrenEndDate();
      } else {
        return yield* superProto.calculateEndDatePure.call(this);
      }
    }

    *calculateStartDateProposed() {
      const manuallyScheduled = yield this.$.manuallyScheduled;
      const hasSubEvents = yield* this.hasSubEvents();

      if (!manuallyScheduled && hasSubEvents) {
        return yield* this.calculateStartDatePure();
      } else {
        return yield* superProto.calculateStartDateProposed.call(this);
      }
    }

    *calculateEndDateProposed() {
      const manuallyScheduled = yield this.$.manuallyScheduled;
      const hasSubEvents = yield* this.hasSubEvents();

      if (!manuallyScheduled && hasSubEvents) {
        return yield* this.calculateEndDatePure();
      } else {
        return yield* superProto.calculateEndDateProposed.call(this);
      }
    }

    *calculateDurationProposed() {
      const manuallyScheduled = yield this.$.manuallyScheduled;
      const hasSubEvents = yield* this.hasSubEvents();

      if (!manuallyScheduled && hasSubEvents) {
        return yield* this.calculateDurationPure();
      } else {
        return yield* superProto.calculateDurationProposed.call(this);
      }
    }

    *calculateMinChildrenStartDate() {
      const subEvents = yield* this.subEventsIterable();
      const subStartDates = [];

      for (const subEvent of subEvents) {
        subStartDates.push(yield subEvent.$.startDate);
      }

      let timestamp = subStartDates.reduce((acc, subStartDate) => subStartDate ? Math.min(acc, subStartDate.getTime()) : acc, MAX_DATE.getTime());
      if (timestamp === MIN_DATE.getTime() || timestamp === MAX_DATE.getTime()) return null;
      return new Date(timestamp);
    }

    *calculateMaxChildrenEndDate() {
      const subEvents = yield* this.subEventsIterable();
      const subEndDates = [];

      for (const subEvent of subEvents) {
        subEndDates.push(yield subEvent.$.endDate);
      }

      let timestamp = subEndDates.reduce((acc, subEndDate) => subEndDate ? Math.max(acc, subEndDate.getTime()) : acc, MIN_DATE.getTime());
      if (timestamp === MIN_DATE.getTime() || timestamp === MAX_DATE.getTime()) return null;
      return new Date(timestamp);
    }

  }

  return HasSubEventsMixin;
}) {}

var __decorate$g = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};
class SchedulerBasicProjectMixin extends MixinAny([ChronoAbstractProjectMixin, BaseEventMixin, HasSubEventsMixin, HasCalendarMixin, DurationConverterMixin, CanCombineCalendarsMixin], base => {
  const superProto = base.prototype;

  class SchedulerBasicProjectMixin extends base {
    construct(config = {}) {
      this.enableProgressNotifications = config.enableProgressNotifications;

      if (!('expanded' in config)) {
        config.expanded = true;
      }

      superProto.construct.call(this, config);
      this.ignoreInitialCommitComputationCycles = 'ignoreInitialCommitComputationCycles' in config ? config.ignoreInitialCommitComputationCycles : true;
      if (this.repopulateOnDataset !== false) this.repopulateOnDataset = true;
      if (!this.eventModelClass) this.eventModelClass = this.getDefaultEventModelClass();
      if (!this.eventStoreClass) this.eventStoreClass = this.getDefaultEventStoreClass();
      if (!this.dependencyModelClass) this.dependencyModelClass = this.getDefaultDependencyModelClass();
      if (!this.dependencyStoreClass) this.dependencyStoreClass = this.getDefaultDependencyStoreClass();
      if (!this.resourceModelClass) this.resourceModelClass = this.getDefaultResourceModelClass();
      if (!this.resourceStoreClass) this.resourceStoreClass = this.getDefaultResourceStoreClass();
      if (!this.assignmentModelClass) this.assignmentModelClass = this.getDefaultAssignmentModelClass();
      if (!this.assignmentStoreClass) this.assignmentStoreClass = this.getDefaultAssignmentStoreClass();
      if (!this.calendarModelClass) this.calendarModelClass = this.getDefaultCalendarModelClass();
      if (!this.calendarManagerStoreClass) this.calendarManagerStoreClass = this.getDefaultCalendarManagerStoreClass();
      this.replica = this.createReplica();
      this.replica.addEntity(this);
      this.defaultCalendar = new this.calendarModelClass({
        unspecifiedTimeIsWorking: this.unspecifiedTimeIsWorking
      });
      this.defaultCalendar.project = this;
      this.replica.addEntity(this.defaultCalendar);
      const stm = this.stm = new StateTrackingManager(Object.assign({
        disabled: true
      }, this.stm));
      this.stm.on({
        restoringStop: async () => {
          this.stm.disable();
          await this.commitAsync();

          if (!this.isDestroyed) {
            this.stm.enable();
            this.trigger('stateRestoringDone');
          }
        }
      });
      let reEnableAutoRecord;
      this.on({
        beforeCommit() {
          if (stm.isRecording && stm.autoRecord) {
            reEnableAutoRecord = true;
            stm.autoRecord = false;
          }
        },

        dataReady() {
          if (reEnableAutoRecord) {
            stm.autoRecord = true;
            reEnableAutoRecord = false;
          }
        }

      });
      this.setCalendarManagerStore(this.calendarManagerStore);
      this.setEventStore(this.eventStore);
      this.setDependencyStore(this.dependencyStore);
      this.setResourceStore(this.resourceStore);
      this.setAssignmentStore(this.assignmentStore);
      const hasInlineData = Boolean(this.calendarsData || this.eventsData || this.dependenciesData || this.resourcesData || this.assignmentsData);

      if (hasInlineData) {
        this.loadInlineData({
          calendarsData: this.calendarsData,
          eventsData: this.eventsData,
          dependenciesData: this.dependenciesData,
          resourcesData: this.resourcesData,
          assignmentsData: this.assignmentsData
        });
        delete this.calendarsData;
        delete this.eventsData;
        delete this.dependenciesData;
        delete this.resourcesData;
        delete this.assignmentsData;
      }
    }

    doDestroy() {
      var _a, _b, _c, _d, _e, _f;

      const me = this;
      (_a = me.eventStore) === null || _a === void 0 ? void 0 : _a.destroy();
      (_b = me.dependencyStore) === null || _b === void 0 ? void 0 : _b.destroy();
      (_c = me.assignmentStore) === null || _c === void 0 ? void 0 : _c.destroy();
      (_d = me.resourceStore) === null || _d === void 0 ? void 0 : _d.destroy();
      (_e = me.calendarManagerStore) === null || _e === void 0 ? void 0 : _e.destroy();
      me.replica.clear();
      (_f = me.stm) === null || _f === void 0 ? void 0 : _f.destroy();
      superProto.doDestroy.call(this);
    }

    createReplica() {
      return EngineReplica.mix(Replica).new({
        project: this,
        schema: Schema.new(),
        enableProgressNotifications: this.enableProgressNotifications,
        silenceInitialCommit: this.silenceInitialCommit,
        ignoreInitialCommitComputationCycles: this.ignoreInitialCommitComputationCycles,
        onWriteDuringCommit: 'ignore',
        readMode: ReadMode.CurrentOrProposedOrPrevious
      });
    }

    *hasSubEvents() {
      return this.getEventStore().count > 0;
    }

    *subEventsIterable() {
      return this.getEventStore().getRange();
    }

    getType() {
      return ProjectType.SchedulerBasic;
    }

    get enableProgressNotifications() {
      return this._enableProgressNotifications;
    }

    set enableProgressNotifications(value) {
      this._enableProgressNotifications = value;
      if (this.replica) this.replica.enableProgressNotifications = value;
    }

    getDefaultEventModelClass() {
      return SchedulerBasicEvent;
    }

    getDefaultEventStoreClass() {
      return ChronoEventStoreMixin;
    }

    getDefaultDependencyModelClass() {
      return BaseDependencyMixin;
    }

    getDefaultDependencyStoreClass() {
      return ChronoDependencyStoreMixin;
    }

    getDefaultResourceModelClass() {
      return BaseResourceMixin;
    }

    getDefaultResourceStoreClass() {
      return ChronoResourceStoreMixin;
    }

    getDefaultAssignmentModelClass() {
      return BaseAssignmentMixin;
    }

    getDefaultAssignmentStoreClass() {
      return ChronoAssignmentStoreMixin;
    }

    getDefaultCalendarModelClass() {
      return BaseCalendarMixin;
    }

    getDefaultCalendarManagerStoreClass() {
      return ChronoCalendarManagerStoreMixin;
    }

    async loadInlineData(data) {
      const {
        calendarManagerStore,
        eventStore,
        dependencyStore,
        assignmentStore,
        resourceStore
      } = this;
      this.replica.unScheduleAutoCommit();

      if (this.replica.enableProgressNotifications) {
        await delay(0);
        this.replica.onPropagationProgressNotification({
          total: 0,
          remaining: 0,
          phase: 'storePopulation'
        });
        await delay(50);
      }

      this.isLoadingInlineData = true;

      if (BrowserHelper.global.DEBUG) {
        console.log(`%cInitializing project`, 'font-weight:bold;color:darkgreen;text-transform:uppercase;margin-top: 2em');
        console.time('Time to visible');
        console.time('Populating project');
      }

      if (data.calendarsData) {
        this.repopulateStore(calendarManagerStore);
        calendarManagerStore.data = data.calendarsData;
      }

      if (data.eventsData || data.tasksData) {
        this.repopulateStore(eventStore);
        eventStore.data = data.eventsData || data.tasksData;
      }

      if (data.dependenciesData) {
        this.repopulateStore(dependencyStore);
        dependencyStore.data = data.dependenciesData;
      }

      if (data.resourcesData) {
        this.repopulateStore(resourceStore);
        resourceStore.data = data.resourcesData;
      }

      if (data.assignmentsData) {
        this.repopulateStore(assignmentStore);
        assignmentStore.data = data.assignmentsData;
      }

      if (BrowserHelper.global.DEBUG) console.timeEnd('Populating project');
      await this.commitLoad();
      this.isLoadingInlineData = false;
      return;
    }

    async commitLoad() {
      await this.commitAsync();
      if (!this.isDestroyed) this.trigger('load');
    }

    get isRepopulatingStores() {
      var _a;

      return Boolean((_a = this.repopulateStores) === null || _a === void 0 ? void 0 : _a.size);
    }

    repopulateStore(store) {
      if (this.repopulateOnDataset && store.count) {
        if (!this.repopulateStores) this.repopulateStores = new Set();
        this.repopulateStores.add(store);
        this.repopulateReplica();
      }
    }

    repopulateReplica() {
      const {
        calendarManagerStore,
        eventStore,
        dependencyStore,
        assignmentStore,
        resourceStore,
        replica: oldReplica
      } = this;
      this.unlinkStoreRecords(calendarManagerStore, eventStore, dependencyStore, resourceStore, assignmentStore);
      this.unlinkRecord(this);
      this.unlinkRecord(this.defaultCalendar);
      oldReplica.clear();
      const replica = this.replica = this.createReplica();
      replica.addEntity(this);
      replica.addEntity(this.defaultCalendar);
      this.joinStoreRecords(calendarManagerStore, true);
      this.joinStoreRecords(eventStore, true);
      this.joinStoreRecords(dependencyStore, true);
      this.joinStoreRecords(resourceStore, true);
      this.joinStoreRecords(assignmentStore, true);
      this.repopulateStores.clear();
    }

    beforeCommitAsync() {
      if (this.repopulateReplica.isPending) {
        this.repopulateReplica.now();
        return this.replica.commitAsync();
      }

      return null;
    }

    unlinkRecord(record) {
      var _a;

      const {
        activeTransaction
      } = this.replica;
      const {
        $
      } = record;
      const keys = Object.keys($);

      for (let i = 0; i < keys.length; i++) {
        const key = keys[i];
        const identifier = $[key];

        if (identifier.field instanceof ModelReferenceField) {
          const value = record[key];
          identifier.DATA = (_a = value === null || value === void 0 ? void 0 : value.id) !== null && _a !== void 0 ? _a : value;
        } else {
          const entry = activeTransaction.getLatestStableEntryFor(identifier);
          if (entry) identifier.DATA = entry.getValue();
        }
      }

      record.graph = null;
    }

    unlinkStoreRecords(...stores) {
      stores.forEach(store => store.traverse(record => {
        if (!this.repopulateStores.has(store)) {
          this.unlinkRecord(record);
        }
      }, false));
    }

    getGraph() {
      return this.replica;
    }

    async addEvents(events) {
      this.eventStore.add(events);
      return this.graph.commitAsync();
    }

    async addEvent(event) {
      this.eventStore.add(event);
      return this.graph.commitAsync();
    }

    includeEvent(event) {
      this.eventStore.add(event);
    }

    async removeEvents(events) {
      this.eventStore.remove(events);
      return this.graph.commitAsync();
    }

    excludeEvent(event) {
      this.eventStore.remove(event);
    }

    async removeEvent(event) {
      this.eventStore.remove(event);
      return this.graph.commitAsync();
    }

    getStm() {
      return this.stm;
    }

    calculateProject() {
      return this;
    }

    resolveCalendar(locator) {
      return super.resolveCalendar(locator) || this.defaultCalendar;
    }

    joinStoreRecords(store, skipRoot = false) {
      const fn = record => {
        record.setProject(this);
        record.joinProject();
      };

      if (store.rootNode) {
        store.rootNode.traverse(fn, skipRoot);
      } else {
        store.forEach(fn);
      }
    }

    unJoinStoreRecords(store) {
      const fn = record => {
        record.leaveProject();
        record.setProject(this);
      };

      if (store.rootNode) {
        store.rootNode.traverse(node => {
          if (node !== store.rootNode) fn(node);
        });
      } else {
        store.forEach(fn);
      }
    }

    setEventStore(store) {
      const oldEventStore = this.eventStore;

      if (oldEventStore && this.stm.hasStore(oldEventStore)) {
        this.stm.removeStore(oldEventStore);
        this.unJoinStoreRecords(oldEventStore);
        const assignmentsForRemoval = oldEventStore.assignmentsForRemoval;
        assignmentsForRemoval.forEach(assignment => {
          const oldEvent = assignment.event;

          if (oldEvent) {
            const newEvent = store.getById(oldEvent.id);

            if (newEvent) {
              assignment.event = newEvent;
              assignmentsForRemoval.delete(assignment);
            }
          }
        });
        oldEventStore.afterEventRemoval();
      }

      if (!store || !(store instanceof Store)) {
        const storeClass = (store === null || store === void 0 ? void 0 : store.storeClass) || this.eventStoreClass;
        this.eventStore = new storeClass(Object.assign({
          modelClass: this.eventModelClass,
          project: this,
          stm: this.stm
        }, store || {}));
      } else {
        this.eventStore = store;
        store.setProject(this);
        this.stm.addStore(store);

        if (store.tree && store.rootNode !== this) {
          this.appendChild(store.rootNode.children || []);
          store.rootNode = this;
        } else {
          this.joinStoreRecords(store);
        }
      }

      this.trigger('eventStoreChange', {
        store: this.eventStore
      });
    }

    setDependencyStore(store) {
      const oldDependencyStore = this.dependencyStore;

      if (oldDependencyStore && this.stm.hasStore(oldDependencyStore)) {
        this.stm.removeStore(oldDependencyStore);
      }

      if (!store || !(store instanceof Store)) {
        const storeClass = (store === null || store === void 0 ? void 0 : store.storeClass) || this.dependencyStoreClass;
        this.dependencyStore = new storeClass(Object.assign({
          modelClass: this.dependencyModelClass,
          project: this,
          stm: this.stm
        }, store || {}));
      } else {
        this.dependencyStore = store;
        store.setProject(this);
        this.stm.addStore(store);
        this.joinStoreRecords(store);
      }

      this.trigger('dependencyStoreChange', {
        store: this.dependencyStore
      });
    }

    setResourceStore(store) {
      const oldResourceStore = this.resourceStore;

      if (oldResourceStore && this.stm.hasStore(oldResourceStore)) {
        this.stm.removeStore(oldResourceStore);
        this.unJoinStoreRecords(oldResourceStore);
        const assignmentsForRemoval = oldResourceStore.assignmentsForRemoval;
        assignmentsForRemoval.forEach(assignment => {
          const oldResource = assignment.resource;

          if (oldResource) {
            const newResource = store.getById(oldResource.id);

            if (newResource) {
              assignment.resource = newResource;
              assignmentsForRemoval.delete(assignment);
            }
          }
        });
        oldResourceStore.afterResourceRemoval();
      }

      if (!store || !(store instanceof Store)) {
        const storeClass = (store === null || store === void 0 ? void 0 : store.storeClass) || this.resourceStoreClass;
        this.resourceStore = new storeClass(Object.assign({
          modelClass: this.resourceModelClass,
          project: this,
          stm: this.stm
        }, store || {}));
      } else {
        this.resourceStore = store;
        store.setProject(this);
        this.stm.addStore(store);
        this.joinStoreRecords(store);
      }

      this.trigger('resourceStoreChange', {
        store: this.resourceStore
      });
    }

    setAssignmentStore(store) {
      const oldAssignmentStore = this.assignmentStore;

      if (oldAssignmentStore && this.stm.hasStore(oldAssignmentStore)) {
        this.stm.removeStore(oldAssignmentStore);
        this.unJoinStoreRecords(oldAssignmentStore);
      }

      if (!store || !(store instanceof Store)) {
        const storeClass = (store === null || store === void 0 ? void 0 : store.storeClass) || this.assignmentStoreClass;
        this.assignmentStore = new storeClass(Object.assign({
          modelClass: this.assignmentModelClass,
          project: this,
          stm: this.stm
        }, store || {}));
      } else {
        this.assignmentStore = store;
        store.setProject(this);
        this.stm.addStore(store);
        this.joinStoreRecords(store);
      }

      this.trigger('assignmentStoreChange', {
        store: this.assignmentStore
      });
    }

    setCalendarManagerStore(store) {
      const oldCalendarManagerStore = this.calendarManagerStore;

      if (oldCalendarManagerStore && this.stm.hasStore(oldCalendarManagerStore)) {
        this.stm.removeStore(oldCalendarManagerStore);
      }

      if (!store || !(store instanceof Store)) {
        const storeClass = (store === null || store === void 0 ? void 0 : store.storeClass) || this.calendarManagerStoreClass;
        this.calendarManagerStore = new storeClass(Object.assign({
          modelClass: this.calendarModelClass,
          project: this,
          stm: this.stm
        }, store || {}));
      } else {
        this.calendarManagerStore = store;

        if (store) {
          store.setProject(this);
          this.stm.addStore(store);
          this.joinStoreRecords(store);
        }
      }

      this.trigger('calendarManagerStoreChange', {
        store: this.calendarManagerStore
      });
    }

    async isValidDependency(...args) {
      return true;
    }

    async tryPropagateWithChanges(changerFn) {
      const stm = this.stm;
      let stmInitiallyDisabled, stmInitiallyAutoRecord;

      const captureStm = () => {
        stmInitiallyDisabled = stm.disabled;
        stmInitiallyAutoRecord = stm.autoRecord;

        if (stmInitiallyDisabled) {
          stm.enable();
        } else {
          if (stmInitiallyAutoRecord) {
            stm.autoRecord = false;
          }

          if (stm.isRecording) {
            stm.stopTransaction();
          }
        }
      };

      const commitStmTransaction = () => {
        stm.stopTransaction();

        if (stmInitiallyDisabled) {
          stm.resetQueue();
        }
      };

      const rejectStmTransaction = () => {
        if (stm.transaction.length) {
          stm.forEachStore(s => s.beginBatch());
          stm.rejectTransaction();
          stm.forEachStore(s => s.endBatch());
        } else {
          stm.stopTransaction();
        }
      };

      const freeStm = () => {
        stm.disabled = stmInitiallyDisabled;
        stm.autoRecord = stmInitiallyAutoRecord;
      };

      captureStm();
      stm.startTransaction();
      changerFn();
      let result = true;

      try {
        const commitResult = await this.commitAsync();
        result = !commitResult.rejectedWith;
      } catch (e) {
        if (!/cycle/i.test(e)) throw e;
        result = false;
      }

      if (result) {
        commitStmTransaction();
      } else {
        this.replica.reject();
        rejectStmTransaction();
      }

      freeStm();
      return result;
    }

    isEngineReady() {
      const {
        replica
      } = this;
      return !(replica.dirty && (replica.hasPendingAutoCommit() || replica.isCommitting));
    }

    static get defaultConfig() {
      return {
        assignmentsData: null,
        calendarsData: null,
        dependenciesData: null,
        eventsData: null,
        resourcesData: null,
        eventStore: null,
        resourceStore: null,
        assignmentStore: null,
        dependencyStore: null,
        calendarManagerStore: null,
        eventModelClass: null,
        resourceModelClass: null,
        assignmentModelClass: null,
        dependencyModelClass: null,
        calendarModelClass: null,
        repopulateOnDataSet: true
      };
    }

    static get delayable() {
      return {
        repopulateReplica: 10
      };
    }

  }

  SchedulerBasicProjectMixin.applyConfigs = true;

  __decorate$g([model_field({
    type: 'boolean',
    defaultValue: true
  })], SchedulerBasicProjectMixin.prototype, "unspecifiedTimeIsWorking", void 0);

  return SchedulerBasicProjectMixin;
}) {}

class CoreCalendarMixin extends Mixin([AbstractCalendarMixin, CorePartOfProjectModelMixin], base => {
  const superProto = base.prototype;

  class CoreCalendarMixin extends base {}

  return CoreCalendarMixin;
}) {}

class CoreCalendarManagerStoreMixin extends Mixin([AbstractCalendarManagerStoreMixin, CorePartOfProjectStoreMixin], base => {
  const superProto = base.prototype;

  class CoreCalendarManagerStoreMixin extends base {
    static get defaultConfig() {
      return {
        tree: true,
        modelClass: CoreCalendarMixin
      };
    }

  }

  return CoreCalendarManagerStoreMixin;
}) {}

class EventsWrapper$1 extends Mixin([], Events) {}
class DelayableWrapper$1 extends Mixin([], Delayable) {}
class SchedulerCoreProjectMixin extends Mixin([AbstractProjectMixin, CorePartOfProjectGenericMixin, EventsWrapper$1, DelayableWrapper$1, Model], base => {
  const superProto = base.prototype;

  class SchedulerCoreProjectMixin extends base {
    static get defaultConfig() {
      return {
        stm: {},
        eventStore: {},
        assignmentStore: {},
        resourceStore: {},
        dependencyStore: {},
        calendarManagerStore: {},
        eventModelClass: SchedulerCoreEvent,
        assignmentModelClass: CoreAssignmentMixin,
        resourceModelClass: CoreResourceMixin,
        dependencyModelClass: CoreDependencyMixin,
        calendarModelClass: CoreCalendarMixin,
        eventStoreClass: CoreEventStoreMixin,
        assignmentStoreClass: CoreAssignmentStoreMixin,
        resourceStoreClass: CoreResourceStoreMixin,
        dependencyStoreClass: CoreDependencyStoreMixin,
        calendarManagerStoreClass: CoreCalendarManagerStoreMixin,
        assignmentsData: null,
        calendarsData: null,
        dependenciesData: null,
        eventsData: null,
        resourcesData: null
      };
    }

    construct(config = {}) {
      this.$invalidated = new Set();
      this.isPerformingCommit = false;
      this.silenceInitialCommit = true;
      this.ongoing = Promise.resolve();
      superProto.construct.call(this, config);
      this.defaultCalendar = new this.calendarManagerStore.modelClass({
        unspecifiedTimeIsWorking: this.unspecifiedTimeIsWorking
      });
      this.defaultCalendar.project = this;
      const {
        calendarsData,
        eventsData,
        dependenciesData,
        resourcesData,
        assignmentsData
      } = this;
      const hasInlineData = Boolean(calendarsData || eventsData || dependenciesData || resourcesData || assignmentsData);

      if (hasInlineData) {
        this.loadInlineData({
          calendarsData,
          eventsData,
          dependenciesData,
          resourcesData,
          assignmentsData
        });
        delete this.calendarsData;
        delete this.eventsData;
        delete this.dependenciesData;
        delete this.resourcesData;
        delete this.assignmentsData;
      } else {
        this.bufferedCommitAsync();
      }
    }

    doDestroy() {
      var _a, _b, _c, _d, _e, _f;

      const me = this;
      (_a = me.eventStore) === null || _a === void 0 ? void 0 : _a.destroy();
      (_b = me.dependencyStore) === null || _b === void 0 ? void 0 : _b.destroy();
      (_c = me.assignmentStore) === null || _c === void 0 ? void 0 : _c.destroy();
      (_d = me.resourceStore) === null || _d === void 0 ? void 0 : _d.destroy();
      (_e = me.calendarManagerStore) === null || _e === void 0 ? void 0 : _e.destroy();
      (_f = me.stm) === null || _f === void 0 ? void 0 : _f.destroy();
      superProto.doDestroy.call(this);
    }

    async loadInlineData(data) {
      this.isLoadingInlineData = true;

      if (data.calendarsData) {
        this.calendarManagerStore.data = data.calendarsData;
      }

      if (data.resourcesData) {
        this.resourceStore.data = data.resourcesData;
      }

      if (data.assignmentsData) {
        this.assignmentStore.data = data.assignmentsData;
      }

      if (data.eventsData) {
        this.eventStore.data = data.eventsData;
      }

      if (data.dependenciesData) {
        this.dependencyStore.data = data.dependenciesData;
      }

      await this.commitLoad();
      this.isLoadingInlineData = false;
      return;
    }

    async commitLoad() {
      await this.commitAsync();
      if (!this.isDestroyed) this.trigger('load');
    }

    joinStoreRecords(store) {
      const fn = record => {
        record.setProject(this);
        record.joinProject();
      };

      if (store.rootNode) {
        store.rootNode.traverse(fn);
      } else {
        store.forEach(fn);
      }
    }

    unJoinStoreRecords(store) {
      const fn = record => {
        record.leaveProject();
        record.setProject(this);
      };

      if (store.rootNode) {
        store.rootNode.traverse(node => {
          if (node !== store.rootNode) fn(node);
        });
      } else {
        store.forEach(fn);
      }
    }

    resolveStoreAndModelClass(name, config) {
      const storeClass = (config === null || config === void 0 ? void 0 : config.storeClass) || this[`${name}StoreClass`];
      let modelClass = config === null || config === void 0 ? void 0 : config.modelClass;

      if (!modelClass) {
        if (this.getDefaultConfiguration()[`${name}ModelClass`] !== storeClass.defaultConfig.modelClass) {
          modelClass = storeClass.defaultConfig.modelClass;
        } else {
          modelClass = this[`${name}ModelClass`];
        }
      }

      return {
        storeClass,
        modelClass
      };
    }

    get eventStore() {
      return this.$eventStore;
    }

    setEventStore(eventStore) {
      this.eventStore = eventStore;
    }

    set eventStore(eventStore) {
      const me = this;
      const {
        stm
      } = me;
      const oldStore = me.$eventStore;

      if (!(eventStore instanceof Store)) {
        const {
          storeClass,
          modelClass
        } = me.resolveStoreAndModelClass('event', eventStore);
        eventStore = new storeClass(Object.assign({
          modelClass,
          project: me,
          stm
        }, eventStore));
      } else {
        eventStore.project = me;
        stm.addStore(eventStore);
        me.joinStoreRecords(eventStore);
      }

      if (oldStore && stm.hasStore(oldStore)) {
        stm.removeStore(oldStore);
        me.unJoinStoreRecords(oldStore);
        const {
          assignmentsForRemoval
        } = oldStore;
        assignmentsForRemoval.forEach(assignment => {
          const oldEvent = assignment.event;

          if (oldEvent) {
            const newEvent = eventStore.getById(oldEvent.id);

            if (newEvent) {
              assignment.event = newEvent;
              assignmentsForRemoval.delete(assignment);
            }
          }
        });
        oldStore.afterEventRemoval();
      }

      eventStore.setProject(me);
      me.$eventStore = eventStore;
      me.trigger('eventStoreChange', {
        store: eventStore
      });
    }

    get assignmentStore() {
      return this.$assignmentStore;
    }

    setAssignmentStore(assignmentStore) {
      this.assignmentStore = assignmentStore;
    }

    set assignmentStore(assignmentStore) {
      const me = this;
      const {
        stm
      } = me;
      const oldStore = me.$assignmentStore;

      if (oldStore && stm.hasStore(oldStore)) {
        stm.removeStore(oldStore);
        me.unJoinStoreRecords(oldStore);
      }

      if (!(assignmentStore instanceof Store)) {
        const {
          storeClass,
          modelClass
        } = me.resolveStoreAndModelClass('assignment', assignmentStore);
        assignmentStore = new storeClass(Object.assign({
          modelClass,
          project: me,
          stm
        }, assignmentStore));
      } else {
        assignmentStore.project = me;
        stm.addStore(assignmentStore);
        me.joinStoreRecords(assignmentStore);
      }

      assignmentStore.setProject(me);
      me.$assignmentStore = assignmentStore;
      me.trigger('assignmentStoreChange', {
        store: assignmentStore
      });
    }

    get resourceStore() {
      return this.$resourceStore;
    }

    setResourceStore(resourceStore) {
      this.resourceStore = resourceStore;
    }

    set resourceStore(resourceStore) {
      const me = this;
      const {
        stm
      } = me;
      const oldStore = me.$resourceStore;

      if (!(resourceStore instanceof Store)) {
        const {
          storeClass,
          modelClass
        } = me.resolveStoreAndModelClass('resource', resourceStore);
        resourceStore = new storeClass(Object.assign({
          modelClass,
          project: me,
          stm
        }, resourceStore));
      } else {
        resourceStore.project = me;
        stm.addStore(resourceStore);
        me.joinStoreRecords(resourceStore);
      }

      if (oldStore && stm.hasStore(oldStore)) {
        stm.removeStore(oldStore);
        me.unJoinStoreRecords(oldStore);
        const {
          assignmentsForRemoval
        } = oldStore;
        assignmentsForRemoval.forEach(assignment => {
          const oldResource = assignment.resource;

          if (oldResource) {
            const newResource = resourceStore.getById(oldResource.id);

            if (newResource) {
              assignment.resource = newResource;
              assignmentsForRemoval.delete(assignment);
            }
          }
        });
        oldStore.afterResourceRemoval();
      }

      resourceStore.setProject(me);
      me.$resourceStore = resourceStore;
      me.trigger('resourceStoreChange', {
        store: resourceStore
      });
    }

    get dependencyStore() {
      return this.$dependencyStore;
    }

    setDependencyStore(dependencyStore) {
      this.dependencyStore = dependencyStore;
    }

    set dependencyStore(dependencyStore) {
      const me = this;

      if (!(dependencyStore instanceof Store)) {
        const {
          storeClass,
          modelClass
        } = me.resolveStoreAndModelClass('dependency', dependencyStore);
        dependencyStore = new storeClass(Object.assign({
          modelClass,
          project: me,
          stm: me.stm
        }, dependencyStore));
      } else {
        dependencyStore.project = me;
        me.stm.addStore(dependencyStore);
        me.joinStoreRecords(dependencyStore);
      }

      me.$dependencyStore = dependencyStore;
      me.trigger('dependencyStoreChange', {
        store: dependencyStore
      });
    }

    get calendarManagerStore() {
      return this.$calendarManagerStore;
    }

    setCalendarManagerStore(calendarManagerStore) {
      this.calendarManagerStore = calendarManagerStore;
    }

    set calendarManagerStore(calendarManagerStore) {
      const me = this;

      if (!(calendarManagerStore instanceof Store)) {
        const storeClass = (calendarManagerStore === null || calendarManagerStore === void 0 ? void 0 : calendarManagerStore.storeClass) || me.calendarManagerStoreClass;
        const modelClass = (calendarManagerStore === null || calendarManagerStore === void 0 ? void 0 : calendarManagerStore.modelClass) || storeClass.defaultConfig.modelClass || me.calendarModelClass;
        calendarManagerStore = new storeClass(Object.assign({
          modelClass,
          project: me,
          stm: me.stm
        }, calendarManagerStore));
      } else {
        me.stm.addStore(calendarManagerStore);
      }

      calendarManagerStore.setProject(me);
      me.$calendarManagerStore = calendarManagerStore;
      me.trigger('calendarManagerStoreChange', {
        store: calendarManagerStore
      });
    }

    get calendar() {
      return this.$calendar || this.defaultCalendar;
    }

    set calendar(calendar) {
      this.$calendar = calendar;
    }

    get effectiveCalendar() {
      return this.calendar;
    }

    async addEvent(event) {
      this.eventStore.add(event);
      return this.commitAsync();
    }

    async addAssignment(assignment) {
      this.assignmentStore.add(assignment);
      return this.commitAsync();
    }

    async addResource(resource) {
      this.resourceStore.add(resource);
      return this.commitAsync();
    }

    async addDependency(dependency) {
      this.dependencyStore.add(dependency);
      return this.commitAsync();
    }

    bufferedCommitAsync() {
      if (!this.hasPendingAutoCommit) {
        this.setTimeout({
          fn: 'commitAsync',
          delay: 10
        });
      }
    }

    get hasPendingAutoCommit() {
      return this.hasTimeout('commitAsync');
    }

    unScheduleAutoCommit() {
      this.clearTimeout('commitAsync');
    }

    async commitAsync() {
      if (this.isPerformingCommit) return this.ongoing;
      return this.ongoing = this.doCommitAsync();
    }

    async doCommitAsync() {
      this.isPerformingCommit = true;
      this.unScheduleAutoCommit();
      await delay$1(0);

      if (!this.isDestroyed) {
        for (const record of this.$invalidated) {
          record.calculateInvalidated();
        }

        const {
          isInitialCommit,
          silenceInitialCommit
        } = this;
        const silenceCommit = isInitialCommit && silenceInitialCommit;
        this.assignmentStore.onCommitAsync();
        this.dependencyStore.onCommitAsync();
        this.isInitialCommitPerformed = true;
        this.hasLoadedDataToCommit = false;
        this.isPerformingCommit = false;
        const stores = [this.assignmentStore, this.dependencyStore, this.eventStore, this.resourceStore, this.calendarManagerStore];
        stores.forEach(store => {
          if (!store.isDestroyed) store.suspendAutoCommit();
        });
        this.isWritingData = true;
        this.trigger('refresh', {
          isInitialCommit
        });

        for (const record of this.$invalidated) {
          record.beginBatch();
          record.finalizeInvalidated(isInitialCommit);
        }

        for (const record of this.$invalidated) {
          record.ignoreBag = silenceCommit;
          record.endBatch(silenceCommit, true);
          record.ignoreBag = false;
        }

        this.isWritingData = false;
        this.$invalidated.clear();
        this.trigger('dataReady');
        stores.forEach(store => {
          if (!store.isDestroyed) store.resumeAutoCommit();
        });

        if (silenceCommit) {
          [this.eventStore, this.dependencyStore, this.resourceStore, this.assignmentStore, this.calendarManagerStore].forEach(store => {
            if (!store.isDestroyed) store.acceptChanges();
          });
        }

        return true;
      }
    }

    async propagateAsync() {
      return this.commitAsync();
    }

    invalidate(record) {
      this.$invalidated.add(record);
      this.bufferedCommitAsync();
    }

    async isValidDependency(...args) {
      return true;
    }

    getStm() {
      return this.stm;
    }

    set stm(stm) {
      stm = this.$stm = new StateTrackingManager(Object.assign({
        disabled: true
      }, stm));
      stm.on({
        restoringStop: async () => {
          stm.disable();
          await this.commitAsync();

          if (!this.isDestroyed) {
            stm.enable();
            this.trigger('stateRestoringDone');
          }
        }
      });
    }

    get stm() {
      return this.$stm;
    }

    isEngineReady() {
      return !this.hasPendingAutoCommit && !this.isPerformingCommit && this.isInitialCommitPerformed;
    }

  }

  SchedulerCoreProjectMixin.applyConfigs = true;
  return SchedulerCoreProjectMixin;
}) {}

var _window$bryntum$8;
const EngineMixin$8 =
/*  */
SchedulerCoreProjectMixin;
/**
 * @module Scheduler/model/ProjectModel
 */

/**
 * This class represents a global project of your Scheduler - a central place for all data.
 *
 * It holds and links the stores usually used by Scheduler:
 *
 * - {@link Scheduler.data.EventStore}
 * - {@link Scheduler.data.ResourceStore}
 * - {@link Scheduler.data.AssignmentStore}
 * - {@link Scheduler.data.DependencyStore}
 * - {@link Core.data.Store TimeRangeStore}
 * - {@link #config-timeRangeStore}
 * - {@link Scheduler.data.ResourceTimeRangeStore}
 *
 * The project uses a calculation engine to normalize dates and durations. It is also responsible for
 * handling references between models, for example to link an event via an assignment to a resource. These operations
 * are asynchronous, a fact that is hidden when working in the Scheduler UI but which you must know about when performing
 * more advanced operations on the data level.
 *
 * When there is a change to data that requires something else to be recalculated, the project schedules a calculation (a
 * commit) which happens moments later. It is also possible to trigger these calculations directly. This snippet illustrate
 * the process:
 *
 1. Something changes which requires the project to recalculate, for example adding a new task:
 *
 * ```javascript
 * const [event] = project.eventStore.add({ startDate, endDate });
 * ```
 *
 * 2. A recalculation is scheduled, thus:
 *
 * ```javascript
 * event.duration; // <- Not yet calculated
 * ```
 *
 * 3. Calculate now instead of waiting for the scheduled calculation
 *
 * ```javascript
 * await project.commitAsync();
 *
 * event.duration; // <- Now available
 * ```
 *
 * ## Built in StateTrackingManager
 *
 * The project also has a built in {@link Core.data.stm.StateTrackingManager StateTrackingManager} (STM for short), that
 * handles undo/redo for the project stores (additional stores can also be added). You can enable it to track all
 * project store changes:
 *
 * ```javascript
 * // Turn on auto recording when you create your Scheduler:
 * const scheduler = new Scheduler({
 *    project : {
 *        stm : {
 *            autoRecord : true
 *        }
 *    }
 * });
 *
 * // Undo a transaction
 * project.stm.undo();
 *
 * // Redo
 * project.stm.redo();
 * ```
 *
 * Check out the `undoredo` demo to see it in action.
 *
 * @extends Core/data/Model
 * @mixes Scheduler/model/mixin/ProjectModelMixin
 * @uninherit Core/data/mixin/TreeNode
 */

class ProjectModel extends ProjectModelMixin(EngineMixin$8) {
  /**
   * Silences propagations caused by the project loading.
   *
   * Applying the loaded data to the project occurs in two basic stages:
   *
   * 1. Data gets into the engine graph which triggers changes propagation
   * 2. The changes caused by the propagation get written to related stores
   *
   * Setting this flag to `true` makes the component perform step 2 silently without triggering events causing reactions on those changes
   * (like sending changes back to the server if `autoSync` is enabled) and keeping stores in unmodified state.
   *
   * This is safe if the loaded data is consistent so propagation doesn't really do any adjustments.
   * By default the system treats the data as consistent so this option is `true`.
   *
   * ```js
   * new Scheduler({
   *     project : {
   *         // We want scheduling engine to recalculate the data properly
   *         // so then we could save it back to the server
   *         silenceInitialCommit : false
   *     }
   *     ...
   * })
   * ```
   *
   * @config {Boolean} silenceInitialCommit
   * @default true
   */
  construct(...args) {
    super.construct(...args);

    if (VersionHelper.isTestEnv) {
      window.bryntum.testProject = this;
    } // Moved here from EventStore, since project is now the container instead of it

    this.modelPersistencyManager = this.createModelPersistencyManager();
  }
  /**
   * Creates and returns model persistency manager
   *
   * @return {Scheduler.data.util.ModelPersistencyManager}
   * @internal
   */

  createModelPersistencyManager() {
    return new ModelPersistencyManager({
      eventStore: this,
      resourceStore: this.resourceStore,
      assignmentStore: this.assignmentStore,
      dependencyStore: this.dependencyStore
    });
  }

}
ProjectModel.applyConfigs = true;
ProjectModel._$name = 'ProjectModel';

/**
 * @module Scheduler/data/CrudManager
 */

/**
 * The Crud Manager (or "CM") is a class implementing centralized loading and saving of data in multiple stores.
 * Loading the stores and saving all changes is done using a single AJAX request.
 * This class uses JSON as the data encoding format.
 *
 * ## Scheduler stores
 *
 * The class supports Scheduler specific stores (namely: resource, event, assignment and dependency stores).
 * For these stores, the CM has separate configs ({@link #config-resourceStore}, {@link #config-eventStore},
 * {@link #config-assignmentStore}) to register them.
 *
 * ```javascript
 * const crudManager = new CrudManager({
 *     autoLoad        : true,
 *     resourceStore   : resourceStore,
 *     eventStore      : eventStore,
 *     assignmentStore : assignmentStore,
 *     transport       : {
 *         load : {
 *             url : 'php/read.php'
 *         },
 *         sync : {
 *             url : 'php/save.php'
 *         }
 *     }
 * });
 * ```
 *
 * ## AJAX request configuration
 *
 * To configure AJAX request parameters please take a look at the
 * {@link Scheduler.crud.transport.AjaxTransport AjaxTransport} docs.
 *
 * ```javascript
 * const crudManager = new CrudManager({
 *     autoLoad        : true,
 *     resourceStore,
 *     eventStore,
 *     assignmentStore,
 *     transport       : {
 *         load    : {
 *             url         : 'php/read.php',
 *             // use GET request
 *             method      : 'GET',
 *             // pass request JSON in "rq" parameter
 *             paramName   : 'rq',
 *             // extra HTTP request parameters
 *             params      : {
 *                 foo     : 'bar'
 *             },
 *             // pass some extra Fetch API option
 *             credentials : 'include'
 *         },
 *         sync : {
 *             url : 'php/save.php'
 *         }
 *     }
 * });
 * ```
 *
 * ## Load order
 *
 * The CM is aware of the proper load order for Scheduler specific stores so you don't need to worry about it.
 * If you provide any extra stores (using {@link Scheduler.crud.AbstractCrudManager#config-stores} config) they will be
 * added to the start of collection before the Scheduler specific stores.
 * If you need a different loading order, you should use {@link Scheduler.crud.AbstractCrudManager#function-addStore} method to
 * register your store:
 *
 * ```javascript
 * const crudManager = new CrudManager({
 *     resourceStore   : resourceStore,
 *     eventStore      : eventStore,
 *     assignmentStore : assignmentStore,
 *     // extra user defined stores will get to the start of collection
 *     // so they will be loaded first
 *     stores          : [ store1, store2 ],
 *     transport       : {
 *         load : {
 *             url : 'php/read.php'
 *         },
 *         sync : {
 *             url : 'php/save.php'
 *         }
 *     }
 * });
 *
 * // append store3 to the end so it will be loaded last
 * crudManager.addStore(store3);
 *
 * // now when we registered all the stores let's load them
 * crudManager.load();
 * ```
 *
 * ## Assignment store
 *
 * CrudManager is designed to use AssignmentStore for assigning events to one or multiple resources.
 * However if server provides `resourceId` for any of the `events` then CrudManager enables backward compatible mode when an event could have a single assignment only. This also disables multiple assignments in Scheduler UI.
 * In order to use multiple assignments server backend should be able to receive/send `assignments` for `load` and `sync` requests.
 *
 * ## Features
 *
 * The Crud Manager can automatically add Scheduler feature stores to the tracked collection.
 * For example, it tracks TimeRanges {@link Scheduler.feature.TimeRanges#config-store}.
 * You can receive a tracked store by its id:
 * ```javascript
 * const timeRangesStore = crudManager.getStore('timeRanges');
 * ```
 *
 * @mixes Scheduler/data/mixin/ProjectCrudManager
 * @mixes Scheduler/crud/encoder/JsonEncoder
 * @mixes Scheduler/crud/transport/AjaxTransport
 * @extends Scheduler/crud/AbstractCrudManager
 */

class CrudManager extends AbstractCrudManager.mixin(ProjectCrudManager, AjaxTransport, JsonEncoder) {
  //region Config
  static get defaultConfig() {
    return {
      projectClass: ProjectModel,
      resourceStoreClass: ResourceStore,
      eventStoreClass: EventStore,
      assignmentStoreClass: AssignmentStore,
      dependencyStoreClass: DependencyStore,

      /**
       * A store with resources (or its descriptor).
       * @config {Scheduler.data.ResourceStore|Object}
       */
      resourceStore: {},

      /**
       * A store with events (or its descriptor).
       *
       * ```
       * crudManager : {
       *      eventStore {
       *          storeClass : MyEventStore
       *      }
       * }
       * ```
       * @config {Scheduler.data.EventStore|Object}
       */
      eventStore: {},

      /**
       * A store with assignments (or its descriptor).
       * @config {Scheduler.data.AssignmentStore|Object}
       */
      assignmentStore: {},

      /**
       * A store with dependencies (or its descriptor).
       * @config {Scheduler.data.DependencyStore|Object}
       */
      dependencyStore: {},

      /**
       * A project that holds and links stores
       * @config {Scheduler.model.ProjectModel}
       */
      project: null,
      scheduler: null
    };
  } //endregion

  startConfigure(config) {
    if (config.scheduler) {
      // The effects of this class's initialization are so wide ranging that they may need to
      // access the Scheduler's CrudManager, so ensure it's available immediately.
      config.scheduler._crudManager = this;
    }

    super.startConfigure(config);
  }

  afterConstruct() {
    const me = this,
          {
      scheduler
    } = me; // Created by being configured on a scheduler

    if (scheduler) {
      // Features self initialize if not already initialized.
      // This must be done after the _crudManager is assigned because it may access this.crudManager
      // to get its eventStore.
      const {
        timeRanges
      } = scheduler.features; // Must pull resourceTimeRanges in here. TODO: Refactor this flow, it is weird

      if (timeRanges) {
        me.addCrudStore(timeRanges.store);
        me._timeRangesStore = timeRanges.store;
      }
    } // Created as a standalone instance, create stores to load if none supplied (and plug them into scheduler later)
    // (Not using {} as default value on the configs to not create unneeded stores when running the code above)
    else if (!me.project) {
        me.project = null;
      }

    super.afterConstruct();
  }

  buildProject() {
    return new this.projectClass(this.buildProjectConfig());
  }

  buildProjectConfig() {
    return ObjectHelper.cleanupProperties({
      eventStore: this.eventStore,
      resourceStore: this.resourceStore,
      assignmentStore: this.assignmentStore,
      dependencyStore: this.dependencyStore,
      resourceTimeRangeStore: this.resourceTimeRangeStore
    });
  } //region Stores

  set project(project) {
    const me = this;
    me._project = project;

    if (project) {
      me.eventStore = project.eventStore;
      me.resourceStore = project.resourceStore;
      me.assignmentStore = project.assignmentStore;
      me.dependencyStore = project.dependencyStore;
      me.resourceTimeRangeStore = project.resourceTimeRangeStore;
    }

    if (!me.eventStore) {
      me.eventStore = {};
    }

    if (!me.resourceStore) {
      me.resourceStore = {};
    }

    if (!me.assignmentStore) {
      me.assignmentStore = {};
    }

    if (!me.dependencyStore) {
      me.dependencyStore = {};
    }
  }

  get project() {
    return this._project;
  }
  /**
   * Returns store associated with timeRanges feature, if feature is enabled.
   * @property {Core.data.Store}
   * @readonly
   */

  get timeRangesStore() {
    return this._timeRangesStore;
  }
  /**
   * Get/set the resource store bound to the CRUD manager.
   * @property {Scheduler.data.ResourceStore}
   */

  get resourceStore() {
    var _this$_resourceStore;

    return (_this$_resourceStore = this._resourceStore) === null || _this$_resourceStore === void 0 ? void 0 : _this$_resourceStore.store;
  }

  set resourceStore(store) {
    const me = this;
    me.setFeaturedStore('_resourceStore', store, me.resourceStoreClass);
  }
  /**
   * Get/set the event store bound to the CRUD manager.
   * @property {Scheduler.data.EventStore}
   */

  get eventStore() {
    var _this$_eventStore;

    return (_this$_eventStore = this._eventStore) === null || _this$_eventStore === void 0 ? void 0 : _this$_eventStore.store;
  }

  set eventStore(store) {
    const me = this;
    me.setFeaturedStore('_eventStore', store, me.eventStoreClass);
  }
  /**
   * Get/set the assignment store bound to the CRUD manager.
   * @property {Scheduler.data.AssignmentStore}
   */

  get assignmentStore() {
    var _this$_assignmentStor;

    return (_this$_assignmentStor = this._assignmentStore) === null || _this$_assignmentStor === void 0 ? void 0 : _this$_assignmentStor.store;
  }

  set assignmentStore(store) {
    this.setFeaturedStore('_assignmentStore', store, this.assignmentStoreClass);
  }
  /**
   * Get/set the dependency store bound to the CRUD manager.
   * @property {Scheduler.data.DependencyStore}
   */

  get dependencyStore() {
    var _this$_dependencyStor;

    return (_this$_dependencyStor = this._dependencyStore) === null || _this$_dependencyStor === void 0 ? void 0 : _this$_dependencyStor.store;
  }

  set dependencyStore(store) {
    this.setFeaturedStore('_dependencyStore', store, this.dependencyStoreClass);
  }

  set resourceTimeRangeStore(store) {
    this._resourceTimeRangesStore = store;
    this.addCrudStore(store);
  }

  get resourceTimeRangeStore() {
    return this._resourceTimeRangesStore;
  }

  setFeaturedStore(property, store, storeClass) {
    var _me$property;

    const me = this,
          oldStore = (_me$property = me[property]) === null || _me$property === void 0 ? void 0 : _me$property.store; // if not the same store

    if (oldStore !== store) {
      var _store;

      // normalize store value (turn it into a storeClass instance if needed)
      store = Store.getStore(store, ((_store = store) === null || _store === void 0 ? void 0 : _store.storeClass) || storeClass);

      if (oldStore) {
        me.removeStore(oldStore);
      }

      me[property] = store && {
        store
      } || null; // Adds configured scheduler stores to the store collection ensuring correct order
      // unless they're already registered.

      me.addPrioritizedStore(me[property]);
    }

    return me[property];
  }

  getChangeSetPackage() {
    const pack = super.getChangeSetPackage(); // Remove assignments from changes if using single assignment mode (resourceId)

    if (pack && this.eventStore.usesSingleAssignment) {
      delete pack[this.assignmentStore.storeId]; // No other changes?

      if (!this.crudStores.some(storeInfo => pack[storeInfo.storeId])) {
        return null;
      }
    }

    return pack;
  } //endregion

}
CrudManager._$name = 'CrudManager';

/**
 * @module Scheduler/data/util/recurrence/RecurrenceLegend
 */

/**
 * A static class allowing to get a human readable description of the provided recurrence.
 *
 * ```javascript
 * const event = new EventModel({ startDate : new Date(2018, 6, 3), endDate : new Date(2018, 6, 4) });
 * const recurrence = new RecurrenceModel({ frequency : 'WEEKLY', days : ['MO', 'TU', 'WE'] });
 * event.recurrence = recurrence;
 * // "Weekly on  Mon, Tue and Wed"
 * RecurrenceLegend.getLegend(recurrence);
 * ```
 * @mixes Core/localization/Localizable
 */

class RecurrenceLegend extends Localizable() {
  static get $name() {
    return 'RecurrenceLegend';
  }

  static get allDaysValue() {
    return 'SU,MO,TU,WE,TH,FR,SA';
  }

  static get workingDaysValue() {
    return 'MO,TU,WE,TH,FR';
  }

  static get nonWorkingDaysValue() {
    return 'SU,SA';
  }
  /**
   * Returns the provided recurrence description. The recurrence might be assigned to a timespan model,
   * in this case the timespan start date should be provided in the second argument.
   * @param  {Scheduler.model.RecurrenceModel} recurrence         Recurrence model.
   * @param  {Date}                           [timeSpanStartDate] The recurring timespan start date. Can be omitted if the recurrence is assigned to a timespan model
   *                                                              (and the timespan has {@link Scheduler.model.TimeSpan#field-startDate} filled).
   *                                                              Then start date will be retrieved from the model.
   * @return {String}                                             The recurrence description.
   */

  static getLegend(recurrence, timeSpanStartDate) {
    const me = this,
          {
      timeSpan,
      interval,
      days,
      monthDays,
      months,
      positions
    } = recurrence,
          startDate = timeSpanStartDate || timeSpan.startDate,
          tplData = {
      interval
    };
    let fn;

    switch (recurrence.frequency) {
      case 'DAILY':
        return interval === 1 ? me.L('L{Daily}') : me.L('L{Every {0} days}', tplData);

      case 'WEEKLY':
        if (days && days.length) {
          tplData.days = me.getDaysLegend(days);
        } else if (startDate) {
          tplData.days = DateHelper.getDayName(startDate.getDay());
        }

        return me.L(interval === 1 ? 'L{Weekly on {1}}' : 'L{Every {0} weeks on {1}}', tplData);

      case 'MONTHLY':
        if (days && days.length && positions && positions.length) {
          tplData.days = me.getDaysLegend(days, positions);
        } else if (monthDays && monthDays.length) {
          // sort dates to output in a proper order
          monthDays.sort((a, b) => a - b);
          tplData.days = me.arrayToText(monthDays);
        } else if (startDate) {
          tplData.days = startDate.getDate();
        }

        return me.L(interval === 1 ? 'L{Monthly on {1}}' : 'L{Every {0} months on {1}}', tplData);

      case 'YEARLY':
        if (days && days.length && positions && positions.length) {
          tplData.days = me.getDaysLegend(days, positions);
        } else {
          tplData.days = startDate.getDate();
        }

        if (months && months.length) {
          // sort months to output in a proper order
          months.sort((a, b) => a - b);

          if (months.length > 2) {
            fn = month => DateHelper.getMonthShortName(month - 1);
          } else {
            fn = month => DateHelper.getMonthName(month - 1);
          }

          tplData.months = me.arrayToText(months, fn);
        } else {
          tplData.months = DateHelper.getMonthName(startDate.getMonth());
        }

        return me.L(interval === 1 ? 'L{Yearly on {1} of {2}}' : 'L{Every {0} years on {1} of {2}}', tplData);
    }
  }

  static getDaysLegend(days, positions) {
    const me = this,
          tplData = {
      position: ''
    };
    let fn;

    if (positions && positions.length) {
      // Do not remove. Assertion strings for Localization sanity check.
      // 'L{position1}'
      // 'L{position2}'
      // 'L{position3}'
      // 'L{position4}'
      // 'L{position5}'
      // 'L{position-1}'
      tplData.position = me.arrayToText(positions, position => me.L(`L{position${position}}`));
    }

    if (days.length) {
      days.sort((a, b) => RecurrenceDayRuleEncoder.decodeDay(a)[0] - RecurrenceDayRuleEncoder.decodeDay(b)[0]);

      switch (days.join(',')) {
        case me.allDaysValue:
          tplData.days = me.L('L{day}');
          break;

        case me.workingDaysValue:
          tplData.days = me.L('L{weekday}');
          break;

        case me.nonWorkingDaysValue:
          tplData.days = me.L('L{weekend day}');
          break;

        default:
          if (days.length > 2) {
            fn = day => DateHelper.getDayShortName(RecurrenceDayRuleEncoder.decodeDay(day)[0]);
          } else {
            fn = day => DateHelper.getDayName(RecurrenceDayRuleEncoder.decodeDay(day)[0]);
          }

          tplData.days = me.arrayToText(days, fn);
      }
    }

    return me.L('L{daysFormat}', tplData);
  } // Converts array of items to a human readable list.
  // For example: [1,2,3,4]
  // to: "1, 2, 3 and 4"

  static arrayToText(array, fn) {
    if (fn) {
      array = array.map(fn);
    }

    return array.join(', ').replace(/,(?=[^,]*$)/, this.L('L{ and }'));
  }

}
RecurrenceLegend._$name = 'RecurrenceLegend';

/**
 * @module Scheduler/data/mixin/ProjectConsumer
 */

/**
 * Creates a Project using any configured stores, and sets the stores configured into the project into
 * the host object.
 *
 * @mixin
 */

var ProjectConsumer = (Target => class ProjectConsumer extends (Target || Base) {
  static get $name() {
    return 'ProjectConsumer';
  } //region Default config

  static get declarable() {
    return ['projectStores'];
  }

  static get configurable() {
    return {
      projectModelClass: ProjectModel,
      // TODO: move to SchedulerProBase and SchedulerBase to specify different types

      /**
       * The {@link Scheduler.model.ProjectModel} instance, containing the data visualized by the Scheduler.
       *
       * **Note:** In SchedulerPro the project is instance of SchedulerPro.model.ProjectModel class.
       * @member {Scheduler.model.ProjectModel} project
       */

      /**
       * A {@link Scheduler.model.ProjectModel} instance or a config object. The project holds all Scheduler data.
       * Can be omitted in favor of individual store configs or {@link Scheduler.crud.mixin.CrudManagerView#config-crudManager} config.
       *
       * **Note:** This config is **mandatory** in SchedulerPro. See SchedulerPro.model.ProjectModel class.
       * @config {Scheduler.model.ProjectModel|Object} project
       */
      project: {},

      /**
       * Configure as `true` to destroy the Project and stores when `this` is destroyed.
       * @config {Boolean}
       */
      destroyStores: null,
      // Will be populated by AttachToProjectMixin which features mix in
      projectSubscribers: []
    };
  } //endregion

  startConfigure(config) {
    // process the project first which ingests any configured data sources,
    this.getConfig('project');
    super.startConfigure(config);
  } //region Project
  // This is where all the ingestion hapens.
  // At config time, the changers inject incoming values into the project config object
  // that we are building. At the end we instantiate the project with all incoming
  // config values filled in.

  changeProject(project, oldProject) {
    const me = this,
          {
      projectStoreNames,
      projectDataNames
    } = me.constructor;
    me.projectCallbacks = new Set();

    if (project) {
      // Flag for changes to know what stage we are at
      me.buildingProjectConfig = true;

      if (!project.isModel) {
        // When configuring, prio order:
        // 1. If using an already existing CrudManager, it is assumed to already have the stores we should use,
        //    adopt them as ours.
        // 2. If a supplied store already has a project, it is assumed to be shared with another scheduler and
        //    that project is adopted as ours.
        // 3. Use stores from a supplied project config.
        // 4. Use stores configured on scheduler.
        // + Pass on inline data (events, resources, dependencies, assignments -> xxData on the project config)
        //
        // What happens during project initialization is this:
        // this._project is the project *config* object.
        // changeXxxx methods put incoming values directly into it through this.project
        // to be used as its configuration.
        // So when it is instantiated, it has had all configs injected.
        if (me.isConfiguring) {
          // Set property for changers to put incoming values into
          me._project = project; // crudManager will be a clone of the raw config if it is a raw config.

          const {
            crudManager
          } = me; // Pull in stores from the crudManager config first

          if (crudManager) {
            const {
              isCrudManager
            } = crudManager;

            for (const storeName of projectStoreNames) {
              if (crudManager[storeName]) {
                // We configure the project with the stores, and *not* the CrudManger.
                // The CrudManager ends up having its project set and thereby adopting ours.
                me[storeName] = crudManager[storeName]; // If it's just a config, take the stores out.
                // We will *configure* it with this project and it will ingest
                // its stores from there.

                if (!isCrudManager) {
                  delete crudManager[storeName];
                }
              }
            }
          } // Pull in all our configured stores into the project config object.
          // That also extracts any project into this._sharedProject

          me.getConfig('projectStores'); // Referencing these data configs causes them to be pulled into
          // the _project.xxxData config property if they are present.

          for (const dataName of projectDataNames) {
            me.getConfig(dataName);
          }
        }

        const {
          eventStore
        } = project; // Delay autoLoading until listeners are set up, to be able to inject params

        if (eventStore && !eventStore.isEventStoreMixin && eventStore.autoLoad && !eventStore.count) {
          eventStore.autoLoad = false;
          me.delayAutoLoad = true;
        } // Use sharedProject if found, else instantiate our config.

        project = me._sharedProject || new me.projectModelClass(project); // Clear the property so that the updater is called.

        delete me._project;
      } // In the updater, configs are live

      me.buildingProjectConfig = false;
    }

    return project;
  }
  /**
   * Implement in subclass to take action when project is replaced.
   *
   * __`super.updateProject(...arguments)` must be called first.__
   *
   * @param {Scheduler.model.ProjectModel} project
   */

  updateProject(project, oldProject) {
    const me = this,
          {
      projectListeners,
      crudManager
    } = me;
    me.detachListeners('projectConsumer'); // When we set the crudManager now, it will go through to the CrudManagerVIew

    delete me._crudManager;

    if (project) {
      projectListeners.thisObj = me;
      project.on(projectListeners); // If the project is a CrudManager, use it as such.

      if (project.isCrudManager) {
        me.crudManager = project;
      } // Apply the project to CrudManager, making sure the same stores are used there and here
      else if (crudManager) {
          crudManager.project = project; // CrudManager goes through the changer as usual and is initialized
          // from the Project, not any stores it was originally configured with.

          me.crudManager = crudManager;
        } // Notifies classes that mix AttachToProjectMixin that we have a new project

      me.projectSubscribers.forEach(subscriber => subscriber.attachToProject(project)); // Sets the project's stores into the host object

      for (const storeName of me.constructor.projectStoreNames) {
        me[storeName] = project[storeName];
      } // Listeners are set up, if EventStore was configured with autoLoad now is the time to load

      if (me.delayAutoLoad) {
        // Restore the flag, not needed but to look good on inspection
        project.eventStore.autoLoad = true;
        project.eventStore.load();
      }
    }

    me.trigger('projectChange', {
      project
    });
  } // Implementation here because we need to get first look at it to adopt its stores

  changeCrudManager(crudManager) {
    // Set the property to be scanned for incoming stores.
    // If it's a config, it will be stripped of those stores prior to construction.
    if (this.buildingProjectConfig) {
      this._crudManager = crudManager.isCrudManager ? crudManager : Object.assign({}, crudManager);
    } else {
      return super.changeCrudManager(crudManager);
    }
  } // Called when project changes are committed, after data is written back to records

  onProjectDataReady() {
    if (this.projectCallbacks.size) {
      this.projectCallbacks.forEach(callback => callback());
      this.projectCallbacks.clear();
    }
  }
  /**
   * Accepts a callback that will be called when the underlying project is ready (no commit pending and current commit
   * finalized)
   * @param {Function} callback
   */

  whenProjectReady(callback) {
    // Might already be ready, call directly
    if (this.isEngineReady) {
      callback();
    } else {
      this.projectCallbacks.add(callback);
    }
  }
  /**
   * Returns `true` if engine is in a stable calculated state, `false` otherwise.
   * @property {Boolean}
   */

  get isEngineReady() {
    var _this$project$isEngin, _this$project;

    // NonWorkingTime calls this during destruction, hence the ?.
    return Boolean((_this$project$isEngin = (_this$project = this.project).isEngineReady) === null || _this$project$isEngin === void 0 ? void 0 : _this$project$isEngin.call(_this$project));
  } //endregion
  //region Destroy
  // Cleanup, destroys stores if this.destroyStores is true.

  doDestroy() {
    super.doDestroy();

    if (this.destroyStores) {
      // Shared project might already be destroyed
      !this.project.isDestroyed && this.project.destroy();
    }
  } //endregion

  get projectStores() {
    const {
      projectStoreNames
    } = this.constructor;
    return projectStoreNames.map(storeName => this[storeName]);
  }

  static get projectStoreNames() {
    return Object.keys(this.projectStores);
  }

  static get projectDataNames() {
    return this.projectStoreNames.reduce((result, storeName) => {
      const {
        dataName
      } = this.projectStores[storeName];

      if (dataName) {
        result.push(dataName);
      }

      return result;
    }, []);
  }

  static setupProjectStores(cls, meta) {
    const {
      projectStores
    } = cls;

    if (projectStores) {
      const projectListeners = {
        name: 'projectConsumer',
        dataReady: 'onProjectDataReady'
      },
            storeConfigs = {
        projectListeners
      }; // Create property for dataName and change and updater for stores

      for (const storeName in projectStores) {
        const {
          dataName
        } = projectStores[storeName]; // Define "eventStore" config

        storeConfigs[storeName] = null; // Define up the "events" property

        if (dataName) {
          // Getter to return store data
          // Setter to update project config or live project
          Object.defineProperty(meta.class.prototype, dataName, {
            get() {
              var _this$project$storeNa;

              // get events() { return this.project.eventStore.records; }
              return (_this$project$storeNa = this.project[storeName]) === null || _this$project$storeNa === void 0 ? void 0 : _this$project$storeNa.records;
            },

            set(data) {
              const {
                project
              } = this;

              if (this.buildingProjectConfig) {
                // Set the property in the project config object.
                // eg project.eventsData = [...]
                project[`${dataName}Data`] = data;
              } else {
                // Live update the project when in use.
                project[storeName].data = data;
              }
            }

          });
        }

        this.createStoreDescriptor(meta, storeName, projectStores[storeName], projectListeners);
      } // Create the projectListeners config.

      this.setupConfigs(meta, storeConfigs);
    }
  }

  static createStoreDescriptor(meta, storeName, {
    listeners
  }, projectListeners) {
    const {
      prototype: clsProto
    } = meta.class,
          storeNameCap = StringHelper.capitalize(storeName); // Set up onProjectEventStoreChange to set this.eventStore

    projectListeners[`${storeName}Change`] = function ({
      store
    }) {
      this[storeName] = store;
    }; // create changeEventStore

    clsProto[`change${storeNameCap}`] = function (store, oldStore) {
      var _store;

      const me = this,
            {
        project
      } = me,
            storeProject = (_store = store) === null || _store === void 0 ? void 0 : _store.project;

      if (me.buildingProjectConfig) {
        // Capture any project found at project config time
        // to use as our shared project
        if (storeProject && storeProject.isProjectModel) {
          me._sharedProject = storeProject;
        } // Set the property in the project config object.
        // Must not go through the updater. It's too early to
        // inform host of store change.

        project[storeName] = store;
        return;
      } // Live update the project when in use.

      if (!me.initializingProject) {
        if (project[storeName] !== store) {
          project[`set${storeNameCap}`](store);
          store = project[storeName];
        }
      } // Implement processing here instead of creating a separate updater.
      // Subclasses can implement updaters.

      if (store !== oldStore) {
        if (listeners) {
          listeners.thisObj = me;
          listeners.name = `${storeName}Listeners`;
          me.detachListeners(listeners.name);
          store.on(listeners);
        } // Notifies classes that mix AttachToProjectMixin that we have a new XxxxxStore

        me.projectSubscribers.forEach(subscriber => {
          if (subscriber[storeName] !== store && subscriber[`attachTo${storeNameCap}`]) {
            subscriber[`attachTo${storeNameCap}`](store);
          }
        });
      }

      return store;
    };
  } //region WidgetClass
  // This does not need a className on Widgets.
  // Each *Class* which doesn't need 'b-' + constructor.name.toLowerCase() automatically adding
  // to the Widget it's mixed in to should implement thus.

  get widgetClass() {} //endregion

});

/**
 * @module Scheduler/preset/ViewPreset
 */

/**
 * A ViewPreset is a record of {@link Scheduler.preset.PresetStore PresetStore} which describes the granularity
 * of the timeline view of a {@link Scheduler.view.Scheduler Scheduler} and the layout and subdivisions of the timeline header.
 *
 * You can create a new instance by specifying all fields:
 * ```javascript
 * const myViewPreset = new ViewPreset({
 *     id   : 'myPreset',              // Unique id value provided to recognize your view preset. Not required, but having it you can simply set new view preset by id: scheduler.viewPreset = 'myPreset'
 *
 *     name : 'My view preset',        // A human-readable name provided to be used in GUI, e.i. preset picker, etc.
 *
 *     tickWidth  : 24,                // Time column width in horizontal mode
 *     tickHeight : 50,                // Time column height in vertical mode
 *     displayDateFormat : 'HH:mm',    // Controls how dates will be displayed in tooltips etc
 *
 *     shiftIncrement : 1,             // Controls how much time to skip when calling shiftNext and shiftPrevious.
 *     shiftUnit      : 'day',         // Valid values are 'millisecond', 'second', 'minute', 'hour', 'day', 'week', 'month', 'quarter', 'year'.
 *     defaultSpan    : 12,            // By default, if no end date is supplied to a view it will show 12 hours
 *
 *     timeResolution : {              // Dates will be snapped to this resolution
 *         unit      : 'minute',       // Valid values are 'millisecond', 'second', 'minute', 'hour', 'day', 'week', 'month', 'quarter', 'year'.
 *         increment : 15
 *     },
 *
 *     headers : [                     // This defines your header rows from top to bottom
 *         {                           // For each row you can define 'unit', 'increment', 'dateFormat', 'renderer', 'align', and 'thisObj'
 *             unit       : 'day',
 *             dateFormat : 'ddd DD/MM'
 *         },
 *         {
 *             unit       : 'hour',
 *             dateFormat : 'HH:mm'
 *         }
 *     ],
 *
 *     columnLinesFor : 1              // Defines header level column lines will be drawn for. Defaults to the last level.
 * });
 * ```
 *
 * Or you can extend one of view presets registered in {@link Scheduler.preset.PresetManager PresetManager}:
 *
 * ```javascript
 * const myViewPreset2 = new ViewPreset({
 *     id   : 'myPreset',                  // Unique id value provided to recognize your view preset. Not required, but having it you can simply set new view preset by id: scheduler.viewPreset = 'myPreset'
 *     name : 'My view preset',            // A human-readable name provided to be used in GUI, e.i. preset picker, etc.
 *     base : 'hourAndDay',                // Extends 'hourAndDay' view preset provided by PresetManager. You can pick out any of PresetManager's view presets: PresetManager.records
 *
 *     timeResolution : {                  // Override time resolution
 *         unit      : 'minute',
 *         increment : 15                  // Make it increment every 15 mins
 *     },
 *
 *     headers : [                         // Override headers
 *         {
 *             unit       : 'day',
 *             dateFormat : 'DD.MM.YYYY'   // Use different date format for top header 01.10.2020
 *         },
 *         {
 *             unit       : 'hour',
 *             dateFormat : 'LT'
 *         }
 *     ]
 * });
 * ```
 *
 * See {@link Scheduler.preset.PresetManager PresetManager} for the list of base presets. You may add your own
 * presets to this global list:
 *
 * ```javascript
 * PresetManager.add(myViewPreset);     // Adds new preset to the global scope. All newly created scheduler instances will have it too.
 *
 * const scheduler = new Scheduler({
 *     viewPreset : 'myPreset'
 *     // other configs...
 * });
 * ```
 *
 * Or add them on an individual basis to Scheduler instances:
 *
 * ```javascript
 * const scheduler = new Scheduler({...});
 *
 * scheduler.presets.add(myViewPreset); // Adds new preset to the scheduler instance only. All newly created scheduler instances will **not** have it.
 *
 * scheduler.viewPreset = 'myPreset';
 * ```
 *
 * This live demo shows a custom ViewPreset with AM/PM time format:
 * @externalexample scheduler/viewpresets/amPmPreset.js
 * @extends Core/data/Model
 */

class ViewPreset extends Model {
  static get fields() {
    return [
    /**
     * The name of the view preset
     * @field {String} name
     */
    {
      name: 'name',
      type: 'string'
    },
    /**
     * The height of the row in horizontal orientation
     * @field {Number} rowHeight
     * @default
     */
    {
      name: 'rowHeight',
      defaultValue: 24
    },
    /**
     * The width of the time tick column in horizontal orientation
     * @field {Number} tickWidth
     * @default
     */
    {
      name: 'tickWidth',
      defaultValue: 50
    },
    /**
     * The height of the time tick column in vertical orientation
     * @field {Number} tickHeight
     * @default
     */
    {
      name: 'tickHeight',
      defaultValue: 50
    },
    /**
     * Defines how dates will be formatted in tooltips etc
     * @field {String} displayDateFormat
     * @default
     */
    {
      name: 'displayDateFormat',
      defaultValue: 'HH:mm'
    },
    /**
     * The unit to shift when calling shiftNext/shiftPrevious to navigate in the chart.
     * Valid values are "millisecond", "second", "minute", "hour", "day", "week", "month", "quarter", "year".
     * @field {String} shiftUnit
     * @default
     */
    {
      name: 'shiftUnit',
      defaultValue: 'hour'
    },
    /**
     * The amount to shift (in shiftUnits)
     * @field {Number} shiftIncrement
     * @default
     */
    {
      name: 'shiftIncrement',
      defaultValue: 1
    },
    /**
     * The amount of time to show by default in a view (in the unit defined by the middle header)
     * @field {Number} defaultSpan
     * @default
     */
    {
      name: 'defaultSpan',
      defaultValue: 12
    },
    /**
     * An object containing a unit identifier and an increment variable. This value means minimal task duration you can create using UI.
     * For example when you drag create a task or drag & drop a task, if increment is 5 and unit is 'minute'
     * that means that you can create a 5 min long task, or move it 5 min forward/backward. This config maps to
     * scheduler's {@link Scheduler.view.mixin.TimelineDateMapper#property-timeResolution} config.
     *
     * ```javascript
     * timeResolution : {
     *   unit      : 'minute',  //Valid values are "millisecond", "second", "minute", "hour", "day", "week", "month", "quarter", "year".
     *   increment : 5
     * }
     * ```
     *
     * @field {Object} timeResolution
     */
    'timeResolution',
    /**
     * An array containing one or more {@link Scheduler.preset.ViewPresetHeaderRow} config objects, each of which defines a level of headers for the scheduler.
     * The `main` unit will be the last header's unit, but this can be changed using the {@link #field-mainHeaderLevel} field.
     * @field {Object[]} headers
     */
    'headers',
    /**
     * Index of the {@link #field-headers} array to define which header level is the `main` header.
     * Defaults to the bottom header.
     * @field {Number} mainHeaderLevel
     */
    'mainHeaderLevel',
    /**
     * Index of a header level in the {@link #field-headers} array for which column lines are drawn. See {@link Scheduler.feature.ColumnLines}.
     * Defaults to the bottom header.
     * @field {Number} columnLinesFor
     */
    'columnLinesFor'];
  }

  construct(data) {
    super.construct(...arguments);
    this.normalizeUnits();
  }

  generateId(owner) {
    const me = this,
          {
      headers
    } = me,
          parts = []; // If we were subclassed from a base, use that id as the basis of oure.

    let result = Object.getPrototypeOf(me.data).id;

    if (!result) {
      for (let {
        length
      } = headers, i = length - 1; i >= 0; i--) {
        parts.push(i ? headers[i].unit : StringHelper.capitalize(headers[i].unit));
      } // Use upwards header units at so eg "monthAndYear"

      result = parts.join('And');
    } // If duplicate, make it "hourAndDay-50by80"

    if (owner.includes(result)) {
      result += `-${me.tickWidth}by${me.tickHeight || me.tickWidth}`; // If still duplicate use increment

      if (owner.includes(result)) {
        result += `-${me.bottomHeader.increment}`; // And if STILL duplicate, make it unique with a suffix

        if (owner.includes(result)) {
          result = IdHelper.generateId(`${result}-`);
        }
      }
    }

    return result;
  }

  normalizeUnits() {
    const me = this,
          {
      timeResolution,
      headers
    } = me; // Make sure date "unit" constant specified in the preset are resolved

    for (let i = 0, {
      length
    } = headers; i < length; i++) {
      const header = headers[i];
      header.unit = DateHelper.normalizeUnit(header.unit);

      if (header.splitUnit) {
        header.splitUnit = DateHelper.normalizeUnit(header.splitUnit);
      }

      if (!('increment' in header)) {
        headers[i] = Object.assign({
          increment: 1
        }, header);
      }
    }

    if (timeResolution) {
      timeResolution.unit = DateHelper.normalizeUnit(timeResolution.unit);
    }

    if (me.shiftUnit) {
      me.shiftUnit = DateHelper.normalizeUnit(me.shiftUnit);
    }
  } // Process legacy columnLines config into a headers array.

  static normalizeHeaderConfig(data) {
    const {
      headerConfig,
      columnLinesFor,
      mainHeaderLevel
    } = data,
          headers = data.headers = [];

    if (headerConfig.top) {
      if (columnLinesFor == 'top') {
        data.columnLinesFor = 0;
      }

      if (mainHeaderLevel == 'top') {
        data.mainHeaderLevel = 0;
      }

      headers[0] = headerConfig.top;
    }

    if (headerConfig.middle) {
      if (columnLinesFor == 'middle') {
        data.columnLinesFor = headers.length;
      }

      if (mainHeaderLevel == 'middle') {
        data.mainHeaderLevel = headers.length;
      }

      headers.push(headerConfig.middle);
    } else {
      throw new Error('ViewPreset.headerConfig must be configured with a middle');
    }

    if (headerConfig.bottom) {
      // Main level is middle when using headerConfig object.
      data.mainHeaderLevel = headers.length - 1; // There *must* be a middle above this bottom header
      // so that is the columnLines one by default.

      if (columnLinesFor == null) {
        data.columnLinesFor = headers.length - 1;
      } else if (columnLinesFor == 'bottom') {
        data.columnLinesFor = headers.length;
      } // There *must* be a middle above this bottom header
      // so that is the main one by default.

      if (mainHeaderLevel == null) {
        data.mainHeaderLevel = headers.length - 1;
      }

      if (mainHeaderLevel == 'bottom') {
        data.mainHeaderLevel = headers.length;
      }

      headers.push(headerConfig.bottom);
    }
  } // These are read-only once configured.

  set() {}

  inSet() {}

  get columnLinesFor() {
    return 'columnLinesFor' in this.data ? this.data.columnLinesFor : this.headers.length - 1;
  }

  get tickSize() {
    return this._tickSize || this.tickWidth;
  }

  get tickWidth() {
    return 'tickWidth' in this.data ? this.data.tickWidth : 50;
  }

  get tickHeight() {
    return 'tickHeight' in this.data ? this.data.tickHeight : 50;
  }

  get headerConfig() {
    // Configured in the legacy manner, just return the configured value.
    if (this.data.headerConfig) {
      return this.data.headerConfig;
    } // Rebuild the object based upon the configured headers array.

    const result = {},
          {
      headers
    } = this,
          {
      length
    } = headers;

    switch (length) {
      case 1:
        result.middle = headers[0];
        break;

      case 2:
        if (this.mainHeaderLevel === 0) {
          result.middle = headers[0];
          result.bottom = headers[1];
        } else {
          result.top = headers[0];
          result.middle = headers[1];
        }

        break;

      case 3:
        result.top = headers[0];
        result.middle = headers[1];
        result.bottom = headers[2];
        break;

      default:
        throw new Error('headerConfig object not supported for >3 header levels');
    }

    return result;
  }

  set mainHeaderLevel(mainHeaderLevel) {
    this.data.mainHeaderLevel = mainHeaderLevel;
  }

  get mainHeaderLevel() {
    if ('mainHeaderLevel' in this.data) {
      return this.data.mainHeaderLevel;
    } // 3 headers, then it's the middle

    if (this.data.headers.length === 3) {
      return 1;
    }
    // If it's middle, top, use mainHeaderLevel : 0

    return this.headers.length - 1;
  }

  get mainHeader() {
    return this.headers[this.mainHeaderLevel];
  }

  get bottomHeader() {
    return this.headers[this.headers.length - 1];
  }

  get leafUnit() {
    return this.bottomHeader.unit;
  }

  get mainUnit() {
    return this.mainHeader;
  }

  get msPerPixel() {
    const {
      bottomHeader
    } = this;
    return Math.round(DateHelper.asMilliseconds(bottomHeader.increment || 1, bottomHeader.unit) / this.tickWidth);
  }

  get isValid() {
    const me = this;
    let valid = true; // Make sure all date "unit" constants are valid

    for (const header of me.headers) {
      valid = valid && Boolean(DateHelper.normalizeUnit(header.unit));
    }

    if (me.timeResolution) {
      valid = valid && DateHelper.normalizeUnit(me.timeResolution.unit);
    }

    if (me.shiftUnit) {
      valid = valid && DateHelper.normalizeUnit(me.shiftUnit);
    }

    return valid;
  }

}
ViewPreset._$name = 'ViewPreset';

/**
 * @module Scheduler/preset/PresetStore
 */

/**
 * A special Store subclass which holds {@link Scheduler.preset.ViewPreset ViewPresets}.
 * Each ViewPreset in this store represents a zoom level. The store data is sorted in special
 * zoom order. That is zoomed out to zoomed in. The first Preset will produce the narrowest event bars
 * the last one will produce the widest event bars.
 *
 * To specify view presets (zoom levels) please provide set of view presets to the scheduler:
 *
 * ```javascript
 * const myScheduler = new Scheduler({
 *     presets : [
 *         {
 *             base : 'hourAndDay',
 *             id   : 'MyHourAndDay',
 *             // other preset configs....
 *         },
 *         {
 *             base : 'weekAndMonth',
 *             id   : 'MyWeekAndMonth',
 *             // other preset configs....
 *         }
 *     ],
 *     viewPreset : 'MyHourAndDay',
 *     // other scheduler configs....
 *     });
 * ```
 *
 * @extends Core/data/Store
 */

class PresetStore extends Localizable(Store) {
  static get defaultConfig() {
    return {
      useRawData: true,
      modelClass: ViewPreset,

      /**
       * Specifies the sort order of the presets in the store.
       * By default they are in zoomed out to zoomed in order. That is
       * presets which will create widest event bars to presets
       * which will produce narrowest event bars.
       *
       * Configure this as `-1` to reverse this order.
       * @config {Number}
       * @default
       */
      zoomOrder: 1
    };
  }

  set storage(storage) {
    super.storage = storage; // Maintained in order automatically while adding.

    this.storage.addSorter((lhs, rhs) => {
      const leftBottomHeader = lhs.bottomHeader,
            rightBottomHeader = rhs.bottomHeader; // Sort order:
      //  Milliseconds per pixel.
      //  Tick size.
      //  Unit magnitude.
      //  Increment size.

      const order = rhs.msPerPixel - lhs.msPerPixel || unitMagnitudes[leftBottomHeader.unit] - unitMagnitudes[rightBottomHeader.unit] || leftBottomHeader.increment - rightBottomHeader.increment;
      return order * this.zoomOrder;
    });
  }

  get storage() {
    return super.storage;
  }

  createRecord(data, ...args) {
    if (data.base) {
      const base = this.getById(data.base) || pm.getById(data.base); // Flatten to set prototype for the full config below, since config system forks etc

      data = ObjectHelper.assign({}, data);
      Object.setPrototypeOf(data, base.data);
    }

    return super.createRecord(data, ...args);
  }

  updateLocalization() {
    super.updateLocalization();
    const me = this; // Collect presets from store...

    let presets = me.allRecords; // and basePresets if we are the PresetManager

    if (me.isPresetManager) {
      presets = new Set(presets.concat(Object.values(me.basePresets)));
    }

    presets.forEach(preset => {
      let locale = me.optionalL(preset.id); // Default presets generated from base presets use localization of base if they have no own

      if (typeof locale === 'string' && preset.baseId) {
        locale = me.optionalL(preset.baseId);
      } // Apply any custom format defined in locale, or the original format if none exists

      if (locale && typeof locale === 'object') {
        if (!preset.originalDisplayDateFormat) {
          preset.originalDisplayDateFormat = preset.displayDateFormat;
        } // TODO: work around to work topDateFormat for weekAndDay viewPreset localization.
        // it must be fixed on: https://github.com/bryntum/support/issues/1775
        // it there is a topDateFormat but preset.mainHeaderLevel is 0, means the middle header is the top header actually, so convert property to middle (if middle doesn't exists) to localization understand (topDateFormat for weekAndDay for example)
        // topDateFormat doesn't work when mainHeaderLevel is 0 because it doesn't have top config but has top header visually (Check on get headerConfig method in ViewPreset class)

        if (preset.mainHeaderLevel === 0 && locale.topDateFormat) {
          locale.middleDateFormat = locale.middleDateFormat || locale.topDateFormat;
        }

        preset.setData('displayDateFormat', locale.displayDateFormat || preset.originalDisplayDateFormat);
        ['top', 'middle', 'bottom'].forEach(level => {
          const levelConfig = preset.headerConfig[level],
                localeLevelDateFormat = locale[level + 'DateFormat'];

          if (levelConfig) {
            if (!levelConfig.originalDateFormat) {
              levelConfig.originalDateFormat = levelConfig.dateFormat;
            } // if there was defined topDateFormat on locale file for example, use it instead of renderer from basePresets (https://github.com/bryntum/support/issues/1307)

            if (localeLevelDateFormat && levelConfig.renderer) {
              levelConfig.renderer = null;
            }

            levelConfig.dateFormat = localeLevelDateFormat || levelConfig.originalDateFormat;
          }
        });
      }
    });
  }

}
PresetStore._$name = 'PresetStore';

/**
 * ## Intro
 * This is a global Store of {@link Scheduler.preset.ViewPreset ViewPresets}, required to supply initial data to
 * Scheduler's {@link Scheduler.view.mixin.TimelineViewPresets#config-presets presets}.
 *
 * You can provide new view presets globally or for a specific scheduler.
 *
 * **NOTE:** You **cannot** modify existing records in the PresetManager store. You can either remove
 * preset records from the store or add new records to the store.
 * Also please keep in mind, all changes provided to the PresetManager store are not reflected to the
 * {@link Scheduler.view.mixin.TimelineViewPresets#config-presets presets} of schedulers that already exist!
 *
 * If you want to have just a few presets (also known as zoom levels) in your Scheduler, you can slice corresponding records
 * from the `PresetManager` and apply them to the Scheduler `presets` config.
 * ```javascript
 * const newPresets = PresetManager.records.slice(10, 12);
 *
 * const scheduler = new Scheduler({
 *     presets    : newPresets, // Only 2 zoom levels are available
 *     viewPreset : newPresets[0].id
 * });
 * ```
 *
 * If you want to adjust all default presets and assign to a specific scheduler you are going to create,
 * you can extend them and pass as an array to the Scheduler `presets` config.
 * Here is an example of how to set the same `timeResolution` to all `ViewPresets`.
 * ```javascript
 * const newPresets = PresetManager.map(preset => {
 *     return {
 *         id             : 'my_' + preset.id,
 *         base           : preset.id, // Based on an existing preset
 *         timeResolution : {
 *             unit      : 'day',
 *             increment : 1
 *         }
 *     };
 * });
 *
 * const scheduler = new Scheduler({
 *     presets     : newPresets,
 *     viewPreset : 'my_hourAndDay'
 * });
 * ```
 *
 * If you want to do the same for **all** schedulers which will be created next, you can register new presets in a loop.
 * ```javascript
 * PresetManager.records.forEach(preset => {
 *     // Pass the same ID, so when a new preset is added to the store, it will replace the current one.
 *     PresetManager.registerPreset(preset.id, {
 *        id             : preset.id,
 *        base           : preset.id,
 *        timeResolution : {
 *            unit      : 'day',
 *            increment : 1
 *        }
 *    });
 * });
 * ```
 *
 * Here is an example of how to add a new `ViewPreset` to the global `PresetManager` store and to the already created scheduler `presets`.
 * ```javascript
 * const scheduler = new Scheduler({...});
 *
 * const newGlobalPresets = PresetManager.add({
 *     id              : 'myNewPreset',
 *     base            : 'hourAndDay', // Based on an existing preset
 *     columnLinesFor  : 0,
 *     headers : [                         // Override headers
 *         {
 *             unit       : 'day',
 *             dateFormat : 'DD.MM.YYYY'   // Use different date format for top header 01.10.2020
 *         },
 *         {
 *             unit       : 'hour',
 *             dateFormat : 'LT'
 *         }
 *     ]
 * });
 *
 * scheduler.presets.add(newGlobalPresets); // Add new presets to the scheduler that has been created before changes to PresetManager are applied
 * ```
 *
 * Predefined presets are:
 *
 * - `secondAndMinute` - creates 2 level header - minute and seconds within it:
 * {@inlineexample scheduler/viewpresets/secondAndMinute.js}
 * - `minuteAndHour` - creates 2 level header - hour and minutes within it:
 * {@inlineexample scheduler/viewpresets/minuteAndHour.js}
 * - `hourAndDay` - creates 2 level header - day and hours within it:
 * {@inlineexample scheduler/viewpresets/hourAndDay.js}
 * - `dayAndWeek` - creates 2 level header - week and days within it:
 * {@inlineexample scheduler/viewpresets/dayAndWeek.js}
 * - `weekAndDay` - just like `dayAndWeek` but with different formatting:
 * {@inlineexample scheduler/viewpresets/weekAndDay.js}
 * - `weekAndDayLetter` - creates 2 level header - with weeks and day letters within it:
 * {@inlineexample scheduler/viewpresets/weekAndDayLetter.js}
 * - `weekAndMonth` - creates 2 level header - month and weeks within it:
 * {@inlineexample scheduler/viewpresets/weekAndMonth.js}
 * - `weekDateAndMonth` - creates 2 level header - month and weeks within it (weeks shown by first day only):
 * {@inlineexample scheduler/viewpresets/weekDateAndMonth.js}
 * - `monthAndYear` - creates 2 level header - year and months within it:
 * {@inlineexample scheduler/viewpresets/monthAndYear.js}
 * - `year` - creates 2 level header - year and quarters within it:
 * {@inlineexample scheduler/viewpresets/year.js}
 * - `manyYears` - creates 2 level header - 5-years and year within it:
 * {@inlineexample scheduler/viewpresets/manyYears.js}
 *
 * See the {@link Scheduler.preset.ViewPreset} and {@link Scheduler.preset.ViewPresetHeaderRow} classes for a description of the view preset properties.
 *
 * {@region Localizing View Presets}
 * Bryntum Scheduler uses locales for translations including date formats for view presets.
 *
 * To translate date format for view presets just define the date format for the specified region
 * for your locale file, like this:
 * ```javascript
 * const locale = {
 *
 *     // ... Other translations here ...
 *
 *     PresetManager : {
 *         // Translation for the "weekAndDay" ViewPreset
 *         weekAndDay : {
 *             // Change the date format for the top and middle levels
 *             topDateFormat    : 'MMM',
 *             middleDateFormat : 'D'
 *         },
 *         // Translation for the "dayAndWeek" ViewPreset
 *         dayAndWeek : {
 *             // Change the date format for the top level
 *             topDateFormat : 'MMMM YYYY'
 *         }
 *     }
 * }
 *
 * LocaleManager.extendLocale('En', locale);
 * ```
 *
 * Check the <a target="_blank" href="../examples/localization">localization demo</a> and [this guide](#guides/customization/localization.md) for more details.
 * {@endregion}
 *
 * @singleton
 * @extends Scheduler/preset/PresetStore
 */

class PresetManager extends PresetStore {
  static get defaultConfig() {
    return {
      // To not break CSP demo
      preventSubClassingModel: true,
      basePresets: {
        // Do not remove. Assertion strings for Localization sanity check.
        // 'L{secondAndMinute}'
        // 'L{minuteAndHour}'
        // 'L{hourAndDay}'
        // 'L{dayAndWeek}'
        // 'L{weekAndDay}'
        secondAndMinute: {
          name: 'Seconds',
          tickWidth: 30,
          // Time column width
          tickHeight: 40,
          displayDateFormat: 'll LTS',
          // Controls how dates will be displayed in tooltips etc
          shiftIncrement: 10,
          // Controls how much time to skip when calling shiftNext and shiftPrevious.
          shiftUnit: 'minute',
          // Valid values are "millisecond", "second", "minute", "hour", "day", "week", "month", "quarter", "year".
          defaultSpan: 24,
          // By default, if no end date is supplied to a view it will show 24 hours
          timeResolution: {
            // Dates will be snapped to this resolution
            unit: 'second',
            // Valid values are "millisecond", "second", "minute", "hour", "day", "week", "month", "quarter", "year".
            increment: 5
          },
          // This defines your header rows.
          // For each row you can define "unit", "increment", "dateFormat", "renderer", "align", and "thisObj"
          headers: [{
            unit: 'minute',
            dateFormat: 'llll'
          }, {
            unit: 'second',
            increment: 10,
            dateFormat: 'ss'
          }]
        },
        minuteAndHour: {
          name: 'Minutes',
          tickWidth: 60,
          // Time column width
          tickHeight: 60,
          displayDateFormat: 'll LT',
          // Controls how dates will be displayed in tooltips etc
          shiftIncrement: 1,
          // Controls how much time to skip when calling shiftNext and shiftPrevious.
          shiftUnit: 'hour',
          // Valid values are "MILLI", "SECOND", "minute", "HOUR", "DAY", "WEEK", "MONTH", "QUARTER", "YEAR".
          defaultSpan: 24,
          // By default, if no end date is supplied to a view it will show 24 hours
          timeResolution: {
            // Dates will be snapped to this resolution
            unit: 'minute',
            // Valid values are "MILLI", "SECOND", "minute", "HOUR", "DAY", "WEEK", "MONTH", "QUARTER", "YEAR".
            increment: 30
          },
          headers: [{
            unit: 'hour',
            dateFormat: 'ddd MM/DD, hA'
          }, {
            unit: 'minute',
            increment: 30,
            dateFormat: 'mm'
          }]
        },
        hourAndDay: {
          name: 'Hours',
          tickWidth: 70,
          tickHeight: 40,
          displayDateFormat: 'll LT',
          shiftIncrement: 1,
          shiftUnit: 'day',
          defaultSpan: 24,
          timeResolution: {
            unit: 'minute',
            increment: 30
          },
          headers: [{
            unit: 'day',
            dateFormat: 'ddd DD/MM' //Mon 01/10

          }, {
            unit: 'hour',
            dateFormat: 'LT'
          }]
        },
        day: {
          name: 'Hours 2',
          displayDateFormat: 'LT',
          shiftIncrement: 1,
          shiftUnit: 'day',
          defaultSpan: 1,
          timeResolution: {
            unit: 'minute',
            increment: 30
          },
          mainHeaderLevel: 0,
          headers: [{
            unit: 'day',
            dateFormat: 'ddd DD/MM',
            // Mon 01/02
            splitUnit: 'day'
          }, {
            unit: 'hour',

            renderer(value) {
              return `
                                    <div class="b-sch-calendarcolumn-ct"><span class="b-sch-calendarcolumn-hours">${DateHelper.format(value, 'HH')}</span>
                                    <span class="b-sch-calendarcolumn-minutes">${DateHelper.format(value, 'mm')}</span></div>
                                `;
            }

          }]
        },
        week: {
          name: 'Week/hours',
          displayDateFormat: 'LT',
          shiftIncrement: 1,
          shiftUnit: 'week',
          defaultSpan: 24,
          timeResolution: {
            unit: 'minute',
            increment: 30
          },
          mainHeaderLevel: 0,
          headers: [{
            unit: 'week',
            dateFormat: 'D d',
            splitUnit: 'day'
          }, {
            unit: 'hour',
            dateFormat: 'LT',

            // will be overridden by renderer
            renderer(value) {
              return `
                                    <div class="sch-calendarcolumn-ct">
                                    <span class="sch-calendarcolumn-hours">${DateHelper.format(value, 'HH')}</span>
                                    <span class="sch-calendarcolumn-minutes">${DateHelper.format(value, 'mm')}</span>
                                    </div>
                                `;
            }

          }]
        },
        dayAndWeek: {
          name: 'Days 2',
          tickWidth: 100,
          tickHeight: 80,
          displayDateFormat: 'll LT',
          shiftUnit: 'day',
          shiftIncrement: 1,
          defaultSpan: 5,
          timeResolution: {
            unit: 'hour',
            increment: 1
          },
          headers: [{
            unit: 'week',

            renderer(start) {
              return DateHelper.getShortNameOfUnit('week') + '.' + DateHelper.format(start, 'WW MMM YYYY');
            }

          }, {
            unit: 'day',
            dateFormat: 'dd DD'
          }]
        },
        weekAndDay: {
          name: 'Days',
          tickWidth: 100,
          tickHeight: 80,
          displayDateFormat: 'll hh:mm A',
          shiftUnit: 'week',
          shiftIncrement: 1,
          defaultSpan: 1,
          timeResolution: {
            unit: 'day',
            increment: 1
          },
          mainHeaderLevel: 0,
          headers: [{
            unit: 'week',
            dateFormat: 'YYYY MMMM DD' // 2017 January 01

          }, {
            unit: 'day',
            increment: 1,
            dateFormat: 'DD MMM'
          }]
        },
        weekAndMonth: {
          name: 'Weeks',
          tickWidth: 100,
          tickHeight: 105,
          displayDateFormat: 'll',
          shiftUnit: 'week',
          shiftIncrement: 5,
          defaultSpan: 6,
          timeResolution: {
            unit: 'day',
            increment: 1
          },
          headers: [{
            unit: 'month',
            dateFormat: 'MMM YYYY' //Jan 2017

          }, {
            unit: 'week',
            dateFormat: 'DD MMM'
          }]
        },
        weekAndDayLetter: {
          name: 'Weeks 2',
          tickWidth: 20,
          tickHeight: 50,
          displayDateFormat: 'll',
          shiftUnit: 'week',
          shiftIncrement: 1,
          defaultSpan: 10,
          timeResolution: {
            unit: 'day',
            increment: 1
          },
          mainHeaderLevel: 0,
          headers: [{
            unit: 'week',
            dateFormat: 'ddd DD MMM YYYY',
            verticalColumnWidth: 115
          }, {
            unit: 'day',
            dateFormat: 'd1',
            verticalColumnWidth: 25
          }]
        },
        weekDateAndMonth: {
          name: 'Weeks 3',
          tickWidth: 30,
          tickHeight: 40,
          displayDateFormat: 'll',
          shiftUnit: 'week',
          shiftIncrement: 1,
          defaultSpan: 10,
          timeResolution: {
            unit: 'day',
            increment: 1
          },
          headers: [{
            unit: 'month',
            dateFormat: 'YYYY MMMM'
          }, {
            unit: 'week',
            dateFormat: 'DD'
          }]
        },
        monthAndYear: {
          name: 'Months',
          tickWidth: 110,
          tickHeight: 110,
          displayDateFormat: 'll',
          shiftIncrement: 3,
          shiftUnit: 'month',
          defaultSpan: 12,
          timeResolution: {
            unit: 'day',
            increment: 1
          },
          headers: [{
            unit: 'year',
            dateFormat: 'YYYY' //2017

          }, {
            unit: 'month',
            dateFormat: 'MMM YYYY' //Jan 2017

          }]
        },
        year: {
          name: 'Years',
          tickWidth: 100,
          tickHeight: 100,
          resourceColumnWidth: 100,
          displayDateFormat: 'll',
          shiftUnit: 'year',
          shiftIncrement: 1,
          defaultSpan: 1,
          timeResolution: {
            unit: 'month',
            increment: 1
          },
          headers: [{
            unit: 'year',
            dateFormat: 'YYYY'
          }, {
            unit: 'quarter',

            renderer(start, end, cfg) {
              return DateHelper.getShortNameOfUnit('quarter').toUpperCase() + (Math.floor(start.getMonth() / 3) + 1);
            }

          }]
        },
        manyYears: {
          name: 'Years 2',
          tickWidth: 40,
          tickHeight: 50,
          displayDateFormat: 'll',
          shiftUnit: 'year',
          shiftIncrement: 1,
          defaultSpan: 1,
          timeResolution: {
            unit: 'year',
            increment: 1
          },
          mainHeaderLevel: 0,
          headers: [{
            unit: 'year',
            dateFormat: 'YYYY',
            increment: 5
          }, {
            unit: 'year',
            dateFormat: 'YY',
            increment: 1
          }]
        }
      },
      // This is a list of bryntum-supplied preset adjustments used to create the Scheduler's
      // default initial set of ViewPresets.
      defaultPresets: [// Years over years
      'manyYears', {
        width: 80,
        increment: 1,
        resolution: 1,
        preset: 'manyYears',
        resolutionUnit: 'YEAR'
      }, // Years over quarters
      'year', {
        width: 30,
        increment: 1,
        resolution: 1,
        preset: 'year',
        resolutionUnit: 'MONTH'
      }, {
        width: 50,
        increment: 1,
        resolution: 1,
        preset: 'year',
        resolutionUnit: 'MONTH'
      }, {
        width: 200,
        increment: 1,
        resolution: 1,
        preset: 'year',
        resolutionUnit: 'MONTH'
      }, // Years over months
      'monthAndYear', // Months over weeks
      'weekDateAndMonth', // Months over weeks
      'weekAndMonth', // Months over weeks
      'weekAndDayLetter', // Weeks over days
      'weekAndDay', {
        width: 54,
        increment: 1,
        resolution: 1,
        preset: 'weekAndDay',
        resolutionUnit: 'HOUR'
      }, // Days over hours
      'hourAndDay', {
        width: 64,
        increment: 6,
        resolution: 30,
        preset: 'hourAndDay',
        resolutionUnit: 'MINUTE'
      }, {
        width: 100,
        increment: 6,
        resolution: 30,
        preset: 'hourAndDay',
        resolutionUnit: 'MINUTE'
      }, {
        width: 64,
        increment: 2,
        resolution: 30,
        preset: 'hourAndDay',
        resolutionUnit: 'MINUTE'
      }, // Hours over minutes
      'minuteAndHour', {
        width: 30,
        increment: 15,
        resolution: 5,
        preset: 'minuteAndHour'
      }, {
        width: 130,
        increment: 15,
        resolution: 5,
        preset: 'minuteAndHour'
      }, {
        width: 60,
        increment: 5,
        resolution: 5,
        preset: 'minuteAndHour'
      }, {
        width: 100,
        increment: 5,
        resolution: 5,
        preset: 'minuteAndHour'
      }, // Minutes over seconds
      'secondAndMinute', {
        width: 60,
        increment: 10,
        resolution: 5,
        preset: 'secondAndMinute'
      }, {
        width: 130,
        increment: 5,
        resolution: 5,
        preset: 'secondAndMinute'
      }],
      listeners: {
        locale: 'updateLocalization'
      }
    };
  }

  set basePresets(basePresets) {
    const presetCache = this._basePresets = {};

    for (const id in basePresets) {
      basePresets[id].id = id;
      presetCache[id] = this.createRecord(basePresets[id]);
    }
  }

  get basePresets() {
    return this._basePresets;
  }

  set defaultPresets(defaultPresets) {
    for (let i = 0, {
      length
    } = defaultPresets; i < length; i++) {
      const presetAdjustment = defaultPresets[i],
            isBase = typeof presetAdjustment === 'string',
            baseType = isBase ? presetAdjustment : presetAdjustment.preset;
      let preset; // The default was just a string, so it's an unmodified instance of a base type.

      if (isBase) {
        preset = this.basePresets[baseType];
      } // If it's an object, it's an adjustment to a base tyope
      else {
          const config = Object.setPrototypeOf(ObjectHelper.clone(this.basePresets[baseType].data), {
            id: baseType
          }),
                {
            timeResolution
          } = config,
                bottomHeader = config.headers[config.headers.length - 1];
          config.id = undefined;

          if ('width' in presetAdjustment) {
            config.tickWidth = presetAdjustment.width;
          }

          if ('height' in presetAdjustment) {
            config.tickHeight = presetAdjustment.height;
          }

          if ('increment' in presetAdjustment) {
            bottomHeader.increment = presetAdjustment.increment;
          }

          if ('resolution' in presetAdjustment) {
            timeResolution.increment = presetAdjustment.resolution;
          }

          if ('resolutionUnit' in presetAdjustment) {
            timeResolution.unit = DateHelper.getUnitByName(presetAdjustment.resolutionUnit);
          }

          preset = this.createRecord(config); // Keep id of original preset around, used with localization in PresetStore

          preset.baseId = baseType;
        }

      this.add(preset);
    }
  }

  getById(id) {
    // Look first in the default set, and if it's one of the base types that is not imported into the
    // default set, then look at the bases.
    return super.getById(id) || this.basePresets[id];
  }
  /**
   * Registers a new view preset base to be used by any scheduler grid or tree on the page.
   * @param {String} id The unique identifier for this preset
   * @param {Object} config The configuration properties of the view preset (see {@link Scheduler.preset.ViewPreset} for more information)
   * @returns {Scheduler.preset.ViewPreset} A new ViewPreset based upon the passed configuration.
   */

  registerPreset(id, config) {
    const preset = this.createRecord(Object.assign({
      id
    }, config)),
          existingDuplicate = this.find(p => p.equals(preset));

    if (existingDuplicate) {
      return existingDuplicate;
    }

    if (preset.isValid) {
      this.add(preset);
    } else {
      throw new Error('Invalid preset, please check your configuration');
    }

    return preset;
  }

  getPreset(preset) {
    if (typeof preset === 'number') {
      preset = this.getAt(preset);
    }

    if (typeof preset === 'string') {
      preset = this.getById(preset);
    } else if (!(preset instanceof ViewPreset)) {
      preset = this.createRecord(preset);
    }

    return preset;
  }
  /**
   * Applies preset customizations or fetches a preset view preset using its name.
   * @param {String|Object} presetOrId Id of a predefined preset or a preset config object
   * @returns {Scheduler.preset.ViewPreset} Resulting ViewPreset instance
   */

  normalizePreset(preset) {
    const me = this;

    if (!(preset instanceof ViewPreset)) {
      if (typeof preset === 'string') {
        preset = me.getPreset(preset);

        if (!preset) {
          throw new Error('You must define a valid view preset. See PresetManager for reference');
        }
      } else if (typeof preset === 'object') {
        // Look up any existing ViewPreset that it is based upon.
        if (preset.base) {
          const base = this.getById(preset.base);

          if (!base) {
            throw new Error(`ViewPreset base '${preset.base}' does not exist`);
          } // The config is based upon the base's data with the new config object merged in.

          preset = ObjectHelper.merge(ObjectHelper.clone(base.data), preset);
        } // Ensure the new ViewPreset has a legible, logical id which does not already
        // exxist in our store.

        if (preset.id) {
          preset = me.createRecord(preset);
        } else {
          preset = me.createRecord(ObjectHelper.assign({}, preset));
          preset.id = preset.generateId(preset);
        }
      }
    }

    return preset;
  }
  /**
   * Deletes a view preset
   * @param {String} id The id of the preset, or the preset instance.
   */

  deletePreset(presetOrId) {
    if (typeof presetOrId === 'string') {
      presetOrId = this.getById(presetOrId);
    } else if (typeof presetOrId === 'number') {
      presetOrId = this.getAt(presetOrId);
    }

    if (presetOrId) {
      this.remove(presetOrId); // ALso remove it from our base list

      delete this.basePresets[presetOrId.id];
    }
  }

}

const pm = new PresetManager();

/**
 * @module Scheduler/data/TimeAxis
 */

/**
 * A class representing the time axis of the scheduler. The scheduler timescale is based on the ticks generated by this class.
 * This is a pure "data" (model) representation of the time axis and has no UI elements.
 *
 * The time axis can be {@link #config-continuous} or not. In continuous mode, each timespan starts where the previous ended, and in non-continuous mode
 * there can be gaps between the ticks.
 * A non-continuous time axis can be used when want to filter out certain periods of time (like weekends) from the time axis.
 *
 * To create a non-continuos time axis you have 2 options. First, you can create a time axis containing only the time spans of interest.
 * To do that, subclass this class and override the {@link #function-generateTicks} method.
 *
 * The other alternative is to call the {@link #function-filterBy} method, passing a function to it which should return `false` if the time tick should be filtered out.
 * Calling {@link Core.data.mixin.StoreFilter#function-clearFilters} will return you to a full time axis.
 *
 * @extends Core/data/Store
 */

class TimeAxis extends Store {
  //region Events

  /**
   * Fires before the timeaxis is about to be reconfigured (e.g. new start/end date or unit/increment). Return false to abort the operation.
   * @event beforereconfigure
   * @param {Scheduler.data.TimeAxis} source The time axis instance
   * @param {Date} startDate The new time axis start date
   * @param {Date} endDate The new time axis end date
   */

  /**
   * Event that is triggered when we end reconfiguring and everything UI-related should be done
   * @event endreconfigure
   * @private
   */

  /**
   * Fires when the timeaxis has been reconfigured (e.g. new start/end date or unit/increment)
   * @event reconfigure
   * @param {Scheduler.data.TimeAxis} source The time axis instance
   */

  /**
   * Fires if all the ticks in the timeaxis are filtered out. After firing the filter is cleared to return the time
   * axis to a valid state.
   * @event invalidFilter
   * @param {Scheduler.data.TimeAxis} source The time axis instance
   */
  //endregion
  //region Default config
  static get defaultConfig() {
    return {
      modelClass: TimeSpan,

      /**
       * Set to false if the timeline is not continuous, e.g. the next timespan does not start where the previous ended (for example skipping weekends etc).
       * @config {Boolean}
       * @default
       */
      continuous: true,
      originalContinuous: null,

      /**
       * Include only certain hours or days in the time axis (makes it `continuous : false`). Accepts and object
       * with `day` and `hour` properties:
       * ```
       * const scheduler = new Scheduler({
       *     timeAxis : {
       *         include : {
       *              // Do not display hours after 17 or before 9 (only display 9 - 17). The `to´ value is not
       *              // included in the time axis
       *              hour : {
       *                  from : 9,
       *                  to   : 17
       *              },
       *              // Do not display sunday or saturday
       *              day : [0, 6]
       *         }
       *     }
       * }
       * ```
       * In most cases we recommend that you use Scheduler's workingTime config instead. It is easier to use and
       * makes sure all parts of the Scheduler gets updated.
       * @config {Object}
       */
      include: null,

      /**
       * Automatically adjust the timespan when generating ticks with {@link #function-generateTicks} according to
       * the `viewPreset` configuration. Setting this to false may lead to shifting time/date of ticks.
       * @config {Boolean}
       * @default
       */
      autoAdjust: true,
      unit: null,
      increment: null,
      resolutionUnit: null,
      resolutionIncrement: null,
      weekStartDay: null,
      mainUnit: null,
      shiftUnit: null,
      shiftIncrement: 1,
      //isConfigured : false,
      // in case of `autoAdjust : false`, the 1st and last ticks can be truncated, containing only part of the normal tick
      // these dates will contain adjusted start/end (like if the tick has not been truncated)
      adjustedStart: null,
      adjustedEnd: null,
      // the visible position in the first tick, can actually be > 1 because the adjustment is done by the `mainUnit`
      visibleTickStart: null,
      // the visible position in the first tick, is always ticks count - 1 < value <= ticks count, in case of autoAdjust, always = ticks count
      visibleTickEnd: null,
      defaultSpan: 1,
      tickCache: {},
      viewPreset: null
    };
  } //endregion
  //region Init
  // private

  construct(config) {
    const me = this; // TODO: maybe not needed

    me.generateTicksValidatorFn = () => true;

    super.construct(config);
    me.originalContinuous = me.continuous;
    me.on({
      change: ({
        action
      }) => {
        // If the change was due to filtering, there will be a refresh event
        // arriving next, so do not reconfigure
        if (action !== 'filter') {
          me.trigger('reconfigure', {
            supressRefresh: false
          });
        }
      },
      refresh: () => me.trigger('reconfigure', {
        supressRefresh: false
      }),
      endreconfigure: event => me.trigger('reconfigure', event)
    });

    if (me.startDate) {
      me.internalOnReconfigure();
      me.trigger('reconfigure');
    } else if (me.viewPreset) {
      const range = me.getAdjustedDates(new Date());
      me.startDate = range.startDate;
      me.endDate = range.endDate;
    }
  }

  get isTimeAxis() {
    return true;
  } //endregion
  //region Configuration (reconfigure & consumePreset)

  /**
   * Reconfigures the time axis based on the config object supplied and generates the new 'ticks'.
   * @param {Object} config
   * @param {Boolean} [suppressRefresh]
   * @private
   */

  reconfigure(config, suppressRefresh = false, preventThrow = false) {
    const me = this,
          normalized = me.getAdjustedDates(config.startDate, config.endDate);

    if (me.trigger('beforeReconfigure', {
      startDate: normalized.startDate,
      endDate: normalized.endDate,
      config
    }) !== false) {
      me.trigger('beginReconfigure');
      me._configuredStartDate = config.startDate;
      me._configuredEndDate = config.endDate;
      Object.assign(me, config);

      if (me.internalOnReconfigure(preventThrow) === false) {
        return false;
      }

      me.trigger('endReconfigure', {
        suppressRefresh,
        config
      });
    }
  }

  internalOnReconfigure(preventThrow = false) {
    const me = this;
    me.isConfigured = true;
    const adjusted = me.getAdjustedDates(me.startDate, me.endDate, true),
          normalized = me.getAdjustedDates(me.startDate, me.endDate),
          start = normalized.startDate,
          end = normalized.endDate;

    if (start >= end) {
      throw new Error(`Invalid start/end dates. Start date must less than end date. Start date: ${start}. End date: ${end}.`);
    }

    const unit = me.unit,
          increment = me.increment || 1,
          ticks = me.generateTicks(start, end, unit, increment); // Suspending to be able to detect an invalid filter

    me.suspendEvents();
    me.data = ticks;
    const {
      count
    } = me;

    if (count === 0) {
      if (preventThrow) {
        me.resumeEvents();
        return false;
      }

      throw new Error('Invalid time axis configuration or filter, please check your input data.');
    } // start date is cached, update it to fill after generated ticks

    me.startDate = me.first.startDate;
    me.resumeEvents();
    const checkEnd = me.last.endDate;

    if (me.isContinuous) {
      me.adjustedStart = adjusted.startDate;
      me.adjustedEnd = DateHelper.getNext(count > 1 ? ticks[count - 1].startDate : adjusted.startDate, unit, increment, me.weekStartDay);
    } else {
      me.adjustedStart = me.startDate;
      me.adjustedEnd = checkEnd;
    } // if visibleTickStart > 1 this means some tick is fully outside of the view - we are not interested in it and want to
    // drop it and adjust "adjustedStart" accordingly

    do {
      // TODO this has to use more sophisticated formula to take into account that months for example can be expressed in ms consistenly
      me.visibleTickStart = (me.startDate - me.adjustedStart) / (DateHelper.asMilliseconds(unit) * increment); // TODO: Changed from round to floor which seems to work, but this is not needed in ExtScheduler. Need to step and see what is different

      if (me.autoAdjust) me.visibleTickStart = Math.floor(me.visibleTickStart);
      if (me.visibleTickStart >= 1) me.adjustedStart = DateHelper.getNext(me.adjustedStart, unit, increment, me.weekStartDay);
    } while (me.visibleTickStart >= 1);

    do {
      me.visibleTickEnd = count - (me.adjustedEnd - checkEnd) / (DateHelper.asMilliseconds(unit) * increment);
      if (count - me.visibleTickEnd >= 1) me.adjustedEnd = DateHelper.getNext(me.adjustedEnd, unit, -1, me.weekStartDay);
    } while (count - me.visibleTickEnd >= 1);

    me.updateTickCache(true);
  }
  /**
   * Get/set currently used preset
   * @property {Scheduler.preset.ViewPreset}
   */

  get viewPreset() {
    return this._viewPreset;
  }

  set viewPreset(preset) {
    const me = this;
    preset = pm.getPreset(preset);

    if (!(preset instanceof ViewPreset)) {
      throw new Error('TimeAxis must be configured with the ViewPreset instance that the Scheduler is using');
    }

    me._viewPreset = preset;
    Object.assign(me, {
      unit: preset.bottomHeader.unit,
      increment: preset.bottomHeader.increment || 1,
      resolutionUnit: preset.timeResolution.unit,
      resolutionIncrement: preset.timeResolution.increment,
      mainUnit: preset.mainHeader.unit,
      shiftUnit: preset.shiftUnit,
      shiftIncrement: preset.shiftIncrement || 1,
      defaultSpan: preset.defaultSpan || 1,
      presetName: preset.id,
      // Weekview columns are updated upon 'datachanged' event on this object.
      // We have to pass headers in order to render them correctly (timeAxisViewModel is incorrect in required time)
      headers: preset.headers
    });
  } //endregion
  //region Getters & setters

  set weekStartDay(day) {
    this._weekStartDay = day;
  }

  get weekStartDay() {
    if (this._weekStartDay == null) {
      return DateHelper.weekStartDay;
    }

    return this._weekStartDay;
  } // private

  get resolution() {
    return {
      unit: this.resolutionUnit,
      increment: this.resolutionIncrement
    };
  } // private

  set resolution(resolution) {
    this.resolutionUnit = resolution.unit;
    this.resolutionIncrement = resolution.increment;
  }

  get resolutionUnit() {
    return this._resolutionUnit;
  }

  set resolutionUnit(resolutionUnit) {
    this._resolutionUnit = resolutionUnit;
  }

  get resolutionIncrement() {
    return this._resolutionIncrement;
  }

  set resolutionIncrement(resolutionIncrement) {
    this._resolutionIncrement = resolutionIncrement || 1;
  }

  set mainUnit(mainUnit) {
    this._mainUnit = mainUnit;
  }

  get mainUnit() {
    return this._mainUnit;
  }

  set shiftUnit(shiftUnit) {
    this._shiftUnit = shiftUnit;
  } // private

  get shiftUnit() {
    return this._shiftUnit || this._mainUnit;
  }

  set shiftIncrement(shiftIncrement) {
    this._shiftIncrement = shiftIncrement;
  } // private

  get shiftIncrement() {
    return this._shiftIncrement || 1;
  }

  set unit(unit) {
    this._unit = unit;
  } // private

  get unit() {
    return this._unit;
  }

  set increment(increment) {
    this._increment = increment;
  } // private

  get increment() {
    return this._increment;
  }

  get defaultSpan() {
    return this._defaultSpan;
  }

  set defaultSpan(defaultSpan) {
    this._defaultSpan = defaultSpan;
  } //endregion
  //region Timespan & resolution

  /**
   * Changes the time axis timespan to the supplied start and end dates.
   * @param {Date} newStartDate The new start date
   * @param {Date} newEndDate The new end date
   */

  setTimeSpan(newStartDate, newEndDate, preventThrow = false) {
    const me = this,
          {
      startDate,
      endDate
    } = me.getAdjustedDates(newStartDate, newEndDate);

    if (me.startDate - startDate !== 0 || me.endDate - endDate !== 0) {
      return me.reconfigure({
        startDate,
        endDate
      }, false, preventThrow);
    }
  }
  /**
   * Moves the time axis by the passed amount and unit.
   *
   * NOTE: When using a filtered TimeAxis the result of `shift()` cannot be guaranteed, it might shift into a
   * filtered out span. It tries to be smart about it by shifting from unfiltered start and end dates.
   * If that solution does not work for your filtering setup, please call {@link #function-setTimeSpan} directly
   * instead.
   *
   * @param {Number} amount The number of units to jump
   * @param {String} [unit] The unit (Day, Week etc)
   */

  shift(amount, unit = this.shiftUnit) {
    const me = this;
    let {
      startDate,
      endDate
    } = me; // Use unfiltered start and end dates when shifting a filtered time axis, to lessen risk of messing it up.
    // Still not guaranteed to work though

    if (me.isFiltered) {
      startDate = me.allRecords[0].startDate;
      endDate = me.allRecords[me.allCount - 1].endDate;
    } // Hack for filtered time axis, for example if weekend is filtered out and you shiftPrev() day from monday

    let tries = 0;

    do {
      startDate = DateHelper.add(startDate, amount, unit);
      endDate = DateHelper.add(endDate, amount, unit);
    } while (tries++ < 100 && me.setTimeSpan(startDate, endDate, true) === false);
  }
  /**
   * Moves the time axis forward in time in units specified by the view preset `shiftUnit`, and by the amount specified by the `shiftIncrement`
   * config of the current view preset.
   *
   * NOTE: When using a filtered TimeAxis the result of `shiftNext()` cannot be guaranteed, it might shift into a
   * filtered out span. It tries to be smart about it by shifting from unfiltered start and end dates.
   * If that solution does not work for your filtering setup, please call {@link #function-setTimeSpan} directly
   * instead.
    *
   * @param {Number} [amount] The number of units to jump forward
   */

  shiftNext(amount = this.shiftIncrement) {
    this.shift(amount);
  }
  /**
   * Moves the time axis backward in time in units specified by the view preset `shiftUnit`, and by the amount specified by the `shiftIncrement` config of the current view preset.
   *
   * NOTE: When using a filtered TimeAxis the result of `shiftPrev()` cannot be guaranteed, it might shift into a
   * filtered out span. It tries to be smart about it by shifting from unfiltered start and end dates.
   * If that solution does not work for your filtering setup, please call {@link #function-setTimeSpan} directly
   * instead.
    *
   * @param {Number} [amount] The number of units to jump backward
   */

  shiftPrevious(amount = this.shiftIncrement) {
    this.shift(-amount);
  } //endregion
  //region Filter & continous

  /**
   * Filter the time axis by a function. The passed function will be called with each tick in time axis.
   * If the function returns true, the 'tick' is included otherwise it is filtered. If all ticks are filtered out
   * the time axis is considered invalid, triggering `invalidFilter` and then removing the filter.
   * @param {Function} fn The function to be called, it will receive an object with startDate/endDate properties, and 'index' of the tick.
   * @param {Object} [thisObj] `this` reference for the function
   */

  filterBy(fn, thisObj = this) {
    const me = this;
    me.filters.clear();
    super.filterBy((tick, index) => fn.call(thisObj, tick.data, index));

    if (me.count === 0) {
      me.trigger('invalidFilter');
      me.clearFilters();
    }
  }

  triggerFilterEvent(event) {
    const me = this;

    if (!event.filters.count) {
      me.continuous = me.originalContinuous;
    } else {
      me.continuous = false;
    } // Filters has been applied (or cleared) but listeners are not informed yet, update tick cache to have start and
    // end dates correct when later redrawing events & header

    me.updateTickCache();
    super.triggerFilterEvent(event);
  }
  /**
   * Returns `true` if the time axis is continuous (will return `false` when filtered)
   * @property {Boolean}
   */

  get isContinuous() {
    return this.continuous !== false && !this.filtered;
  } //endregion
  //region Dates

  getAdjustedDates(startDate, endDate, forceAdjust = false) {
    const me = this;
    startDate = startDate || me.startDate;
    endDate = endDate || DateHelper.add(startDate, me.defaultSpan, me.mainUnit);
    return me.autoAdjust || forceAdjust ? {
      startDate: me.floorDate(startDate, false, me.autoAdjust ? me.mainUnit : me.unit, 1),
      endDate: me.ceilDate(endDate, false, me.autoAdjust ? me.mainUnit : me.unit, 1)
    } : {
      startDate: startDate,
      endDate: endDate
    };
  }
  /**
   * Method to get the current start date of the time axis.
   * @property {Date}
   */

  get startDate() {
    // TODO: added _start as caching, might mess something up when reconfiguring? change here if tests fail
    return this._start || (this.first ? new Date(this.first.startDate) : null);
  }

  set startDate(start) {
    this._start = DateHelper.parse(start);
  }
  /**
   * Method to get a the current end date of the time axis
   * @property {Date}
   */

  get endDate() {
    return this._end || (this.last ? new Date(this.last.endDate) : null);
  }

  set endDate(end) {
    if (end) this._end = DateHelper.parse(end);
  } // used in performance critical code for comparisons

  get startMS() {
    return this._startMS;
  } // used in performance critical code for comparisons

  get endMS() {
    return this._endMS;
  } // Floors a date and optionally snaps it to one of the following resolutions:
  // 1. 'resolutionUnit'. If param 'resolutionUnit' is passed, the date will simply be floored to this unit.
  // 2. If resolutionUnit is not passed: If date should be snapped relative to the timeaxis start date,
  // the resolutionUnit of the timeAxis will be used, or the timeAxis 'mainUnit' will be used to snap the date
  //
  // returns a copy of the original date
  // private

  floorDate(date, relativeToStart, resolutionUnit, incr) {
    relativeToStart = relativeToStart !== false;

    const me = this,
          relativeTo = relativeToStart ? DateHelper.clone(me.startDate) : null,
          increment = incr || me.resolutionIncrement,
          unit = resolutionUnit || (relativeToStart ? me.resolutionUnit : me.mainUnit),
          snap = (value, increment) => Math.floor(value / increment) * increment;

    if (relativeToStart) {
      const snappedDuration = snap(DateHelper.diff(relativeTo, date, unit), increment); // TODO: used to be small unit multipled with factor (minute = seconds, minutes * 60)

      return DateHelper.add(relativeTo, snappedDuration, unit);
    }

    let dt = DateHelper.clone(date);

    if (unit === 'week') {
      const day = dt.getDay() || 7,
            startDay = me.weekStartDay || 7;
      dt = DateHelper.add(DateHelper.startOf(dt, 'day'), day >= startDay ? startDay - day : -(7 - startDay + day), 'day'); // Watch out for Brazil DST craziness (see test 028_timeaxis_dst.t.js)

      if (dt.getDay() !== startDay && dt.getHours() === 23) {
        dt = DateHelper.add(dt, 1, 'hour');
      }
    } else {
      // removes "smaller" units from date (for example minutes; removes seconds and milliseconds)
      dt = DateHelper.startOf(dt, unit); // day and year are 1-based so need to make additional adjustments

      const modifier = ['day', 'year'].includes(unit) ? 1 : 0,
            useUnit = unit === 'day' ? 'date' : unit,
            snappedValue = snap(DateHelper.get(dt, useUnit) - modifier, increment) + modifier;
      dt = DateHelper.set(dt, useUnit, snappedValue);
    }

    return dt;
  }
  /**
   * Rounds the date to nearest unit increment
   * @private
   */

  roundDate(date, relativeTo) {
    const me = this,
          dt = DateHelper.clone(date),
          increment = me.resolutionIncrement || 1;
    relativeTo = DateHelper.clone(relativeTo || me.startDate);

    switch (me.resolutionUnit) {
      case 'week':
        DateHelper.startOf(dt, 'day');
        let distanceToWeekStartDay = dt.getDay() - me.weekStartDay,
            toAdd;

        if (distanceToWeekStartDay < 0) {
          distanceToWeekStartDay = 7 + distanceToWeekStartDay;
        }

        if (Math.round(distanceToWeekStartDay / 7) === 1) {
          toAdd = 7 - distanceToWeekStartDay;
        } else {
          toAdd = -distanceToWeekStartDay;
        }

        return DateHelper.add(dt, toAdd, 'day');

      case 'month':
        const nbrMonths = DateHelper.diff(relativeTo, dt, 'month') + DateHelper.as('month', dt.getDay() / DateHelper.daysInMonth(dt)),
              //*/DH.as('month', DH.diff(relativeTo, dt)) + (dt.getDay() / DH.daysInMonth(dt)),
        snappedMonths = Math.round(nbrMonths / increment) * increment;
        return DateHelper.add(relativeTo, snappedMonths, 'month');

      case 'quarter':
        DateHelper.startOf(dt, 'month');
        return DateHelper.add(dt, 'month', 3 - dt.getMonth() % 3);

      default:
        const duration = DateHelper.as(me.resolutionUnit, DateHelper.diff(relativeTo, dt)),
              // Need to find the difference of timezone offsets between relativeTo and original dates. 0 if timezone offsets are the same.
        offset = DateHelper.as(me.resolutionUnit, relativeTo.getTimezoneOffset() - dt.getTimezoneOffset(), 'minute'),
              // Need to add the offset to the whole duration, so the divided value will take DST into account
        snappedDuration = Math.round((duration + offset) / increment) * increment; // TODO: used to add one res unit lower * factor, minutes = add seconds, minutes * 60
        // Now when the round is done, we need to subtract the offset, so the result also will take DST into account

        return DateHelper.add(relativeTo, snappedDuration - offset, me.resolutionUnit);
    }
  } // private

  ceilDate(date, relativeToStart, resolutionUnit, increment) {
    const me = this;
    relativeToStart = relativeToStart !== false;
    increment = increment || (relativeToStart ? this.resolutionIncrement : 1);
    let dt = DateHelper.clone(date),
        doCall = false,
        unit = resolutionUnit || (relativeToStart ? me.resolutionUnit : me.mainUnit);

    switch (unit) {
      case 'minute':
        doCall = !DateHelper.isStartOf(dt, 'minute');
        break;

      case 'hour':
        doCall = !DateHelper.isStartOf(dt, 'hour');
        break;

      case 'day':
      case 'date':
        doCall = !DateHelper.isStartOf(dt, 'day');
        break;

      case 'week':
        dt = DateHelper.startOf(dt, 'day');
        doCall = dt.getDay() !== me.weekStartDay || !DateHelper.isEqual(dt, date);
        break;

      case 'month':
        dt = DateHelper.startOf(dt, 'day');
        doCall = dt.getDate() !== 1 || !DateHelper.isEqual(dt, date);
        break;

      case 'quarter':
        dt = DateHelper.startOf(dt, 'day');
        doCall = dt.getMonth() % 3 !== 0 || dt.getDate() !== 1 || !DateHelper.isEqual(dt, date);
        break;

      case 'year':
        dt = DateHelper.startOf(dt, 'day');
        doCall = dt.getMonth() !== 0 || dt.getDate() !== 1 || !DateHelper.isEqual(dt, date);
        break;
    }

    if (doCall) return DateHelper.getNext(dt, unit, increment, me.weekStartDay);
    return dt;
  } //endregion
  //region Ticks

  get include() {
    return this._include;
  }

  set include(include) {
    const me = this;
    me._include = include;
    me.continuous = !include;

    if (!me.isConfiguring) {
      me.startDate = me._configuredStartDate;
      me.endDate = me._configuredEndDate;
      me.internalOnReconfigure();
      me.trigger('includeChange');
    }
  } // Check if a certain date is included based on timeAxis.include rules

  processExclusion(startDate, endDate, unit) {
    const {
      include
    } = this;

    if (include) {
      return Object.entries(include).some(([includeUnit, rule]) => {
        if (!rule) {
          return false;
        }

        const {
          from,
          to
        } = rule; // Including the closest smaller unit with a { from, to} rule should affect start & end of the
        // generated tick. Currently only works for days or smaller.

        if (DateHelper.compareUnits('day', unit) >= 0 && DateHelper.getLargerUnit(includeUnit) === unit) {
          if (from) {
            DateHelper.set(startDate, includeUnit, from);
          }

          if (to) {
            let stepUnit = unit; // Stepping back base on date, not day

            if (unit === 'day') {
              stepUnit = 'date';
            } // Since endDate is not inclusive it points to the next day etc.
            // Turns for example 2019-01-10T00:00 -> 2019-01-09T18:00

            DateHelper.set(endDate, {
              [stepUnit]: DateHelper.get(endDate, stepUnit) - 1,
              [includeUnit]: to
            });
          }
        } // "Greater" unit being included? Then we need to care about it
        // (for example excluding day will also affect hour, minute etc)

        if (DateHelper.compareUnits(includeUnit, unit) >= 0) {
          const datePart = includeUnit === 'day' ? startDate.getDay() : DateHelper.get(startDate, includeUnit);

          if (from && datePart < from || to && datePart >= to) {
            return true;
          }
        }
      });
    }

    return false;
  } // Calculate constants used for exclusion when scaling within larger ticks

  initExclusion() {
    Object.entries(this.include).forEach(([unit, rule]) => {
      if (rule) {
        const {
          from,
          to
        } = rule; // For example for hour:
        // 1. Get the next bigger unit -> day, get ratio -> 24
        // 2. to 20 - from 8 = 12 hours visible each day. lengthFactor 24 / 12 = 2 means that each hour used
        // needs to represent 2 hours when drawn (to stretch)
        // |    ████    | -> |  ████████  |

        rule.lengthFactor = DateHelper.getUnitToBaseUnitRatio(unit, DateHelper.getLargerUnit(unit)) / (to - from); // TODO: Since `to` is exclusive this should be the correct one... but cannot get it to work throughout

        rule.lengthFactorExcl = DateHelper.getUnitToBaseUnitRatio(unit, DateHelper.getLargerUnit(unit)) / (to - from - 1); // Calculate weighted center to stretch around |   ██x█ |

        rule.center = from + from / (rule.lengthFactor - 1);
      }
    });
  }
  /**
   * Method generating the ticks for this time axis. Should return an array of ticks. Each tick is an object of the following structure:
   * ```
   * {
   *    startDate : ..., // start date
   *    endDate   : ...  // end date
   * }
   * ```
   * Take notice, that this function either has to be called with `start`/`end` parameters, or create those variables.
   *
   * To see it in action please check out our [TimeAxis](https://bryntum.com/examples/scheduler/timeaxis/) example and navigate to "Compressed non-working time" tab.
   *
   * @param {Date} axisStartDate The start date of the interval
   * @param {Date} axisEndDate The end date of the interval
   * @param {String} unit The unit of the time axis
   * @param {Number} increment The increment for the unit specified.
   * @return {Array} ticks The ticks representing the time axis
   */

  generateTicks(axisStartDate, axisEndDate, unit = this.unit, increment = this.increment) {
    const me = this,
          ticks = [],
          usesExclusion = Boolean(me.include);
    let intervalEnd,
        tickEnd,
        isExcluded,
        dstDiff = 0,
        {
      startDate,
      endDate
    } = me.getAdjustedDates(axisStartDate, axisEndDate);
    me.tickCache = {};

    if (usesExclusion) {
      me.initExclusion();
    }

    while (startDate < endDate) {
      intervalEnd = DateHelper.getNext(startDate, unit, increment, me.weekStartDay);

      if (!me.autoAdjust && intervalEnd > endDate) {
        intervalEnd = endDate;
      } // Handle hourly increments crossing DST boundaries to keep the timescale looking correct
      // Only do this for HOUR resolution currently, and only handle it once per tick generation.

      if (unit === 'hour' && increment > 1 && ticks.length > 0 && dstDiff === 0) {
        const prev = ticks[ticks.length - 1];
        dstDiff = (prev.startDate.getHours() + increment) % 24 - prev.endDate.getHours();

        if (dstDiff !== 0) {
          // A DST boundary was crossed in previous tick, adjust this tick to keep timeaxis "symmetric".
          intervalEnd = DateHelper.add(intervalEnd, dstDiff, 'hour');
        }
      }

      isExcluded = false;

      if (usesExclusion) {
        tickEnd = new Date(intervalEnd.getTime());
        isExcluded = me.processExclusion(startDate, intervalEnd, unit);
      } else {
        tickEnd = intervalEnd;
      }

      if (me.generateTicksValidatorFn(startDate) && !isExcluded) {
        ticks.push({
          id: ticks.length + 1,
          startDate,
          endDate: intervalEnd
        });
        me.tickCache[startDate.getTime()] = ticks.length - 1;
      }

      startDate = tickEnd;
    }

    return ticks;
  }

  get visibleTickTimeSpan() {
    const me = this;
    return me.isContinuous ? me.visibleTickEnd - me.visibleTickStart : me.count;
  }
  /**
   * Gets a tick "coordinate" representing the date position on the time scale. Returns -1 if the date is not part of the time axis.
   * @param {Date} date the date
   * @return {Number} the tick position on the scale or -1 if the date is not part of the time axis
   */

  getTickFromDate(date) {
    const me = this,
          ticks = me.records,
          dateMS = date.getTime();
    let begin = 0,
        end = ticks.length - 1,
        middle,
        tick,
        tickStart,
        tickEnd; // Quickly eliminate out of range dates or if we have not been set up with a time range yet

    if (!ticks.length || date < ticks[0].startDate || date > ticks[end].endDate) {
      return -1;
    }

    if (me.isContinuous) {
      // TODO: This is the code from ExtScheduler, it is a calculation without iteration so it should perform better,
      //  we should consider using it...
      // if (date - ticks[0].startDate === 0) return this.visibleTickStart;
      // if (date - ticks[end].endDate === 0) return this.visibleTickEnd;
      //
      // const { adjustedStart, adjustedEnd }     = this;
      //
      // let tickIndex       = Math.floor(ticks.length * (date - adjustedStart) / (adjustedEnd - adjustedStart));
      //
      // // for the date == adjustedEnd case
      // if (tickIndex > end) {
      //     tickIndex = end;
      // }
      //
      // const tickStart           = tickIndex === 0 ? adjustedStart : ticks[tickIndex].startDate;
      // const tickEnd             = tickIndex === end ? adjustedEnd : ticks[tickIndex].endDate;
      //
      // tick                = tickIndex + (date - tickStart) / (tickEnd - tickStart);
      //
      // // in case of `autoAdjust : false` the actual visible timespan starts not from 0 tick coordinate, but
      // // from `visibleTickStart` coordinate, this check generally repeats the "quick bailout" check in the begining of the method,
      // // but still
      // if (tick < this.visibleTickStart || tick > this.visibleTickEnd) {
      //     return -1;
      // }
      //
      // return tick;
      // Chop tick cache in half until we find a match
      while (begin < end) {
        middle = begin + end + 1 >> 1;

        if (dateMS > ticks[middle].endDate.getTime()) {
          begin = middle + 1;
        } else if (dateMS < ticks[middle].startDate.getTime()) {
          end = middle - 1;
        } else {
          begin = middle;
        }
      }

      tick = ticks[begin];
      tickStart = tick.startDate.getTime(); // Part way though, calculate the fraction

      if (dateMS > tickStart) {
        tickEnd = tick.endDate.getTime();
        begin += (dateMS - tickStart) / (tickEnd - tickStart);
      }

      return Math.min(Math.max(begin, me.visibleTickStart), me.visibleTickEnd);
    } else {
      for (let i = 0; i <= end; i++) {
        tickEnd = ticks[i].endDate;

        if (date <= tickEnd) {
          tickStart = ticks[i].startDate; // date < tickStart can occur in filtered case

          tick = i + (date > tickStart ? (date - tickStart) / (tickEnd - tickStart) : 0);
          return tick;
        }
      }
    }
  }
  /**
   * Gets the time represented by a tick "coordinate".
   * @param {Number} tick the tick "coordinate"
   * @param {String} [roundingMethod] The rounding method to use
   * @return {Date} The date to represented by the tick "coordinate", or null if invalid.
   */

  getDateFromTick(tick, roundingMethod) {
    const me = this;

    if (tick === me.visibleTickEnd) {
      return me.endDate;
    }

    const wholeTick = Math.floor(tick),
          fraction = tick - wholeTick,
          t = me.getAt(wholeTick);

    if (!t) {
      return null;
    }

    let start = wholeTick === 0 ? me.adjustedStart : t.startDate,
        // if we've filtered timeaxis using filterBy, then we cannot trust to adjustedEnd property and should use tick end
    end = wholeTick === me.count - 1 && me.isContinuous ? me.adjustedEnd : t.endDate,
        date = DateHelper.add(start, fraction * (end - start), 'millisecond');

    if (roundingMethod) {
      date = me[roundingMethod + 'Date'](date);
    }

    return date;
  }
  /**
   * Returns the ticks of the timeaxis in an array of objects with a "start" and "end" date.
   * @property {Object[]}
   */

  get ticks() {
    return this.records;
  }
  /**
   * Caches ticks and start/end dates for faster processing during rendering of events.
   * @private
   */

  updateTickCache(onlyStartEnd = false) {
    const me = this;

    if (me.count) {
      me._start = me.first.startDate;
      me._end = me.last.endDate;
      me._startMS = me.startDate.getTime();
      me._endMS = me.endDate.getTime();
    } else {
      me._start = me._end = me._startMs = me._endMS = null;
    } // onlyStartEnd is true prior to clearing filters, to get start and end dates correctly during that process.
    // No point in filling tickCache yet in that case, it will be done after the filters are cleared

    if (!onlyStartEnd) {
      me.tickCache = {};
      me.forEach((tick, i) => me.tickCache[tick.startDate.getTime()] = i);
    }
  } //endregion
  //region Axis

  /**
   * Returns true if the passed date is inside the span of the current time axis.
   * @param {Date} date The date to query for
   * @return {Boolean} true if the date is part of the timeaxis
   */

  dateInAxis(date, inclusiveEnd = false) {
    const me = this,
          axisStart = me.startDate,
          axisEnd = me.endDate; // Date is between axis start/end and axis is not continuous - need to perform better lookup

    if (me.isContinuous) {
      return inclusiveEnd ? DateHelper.betweenLesserEqual(date, axisStart, axisEnd) : DateHelper.betweenLesser(date, axisStart, axisEnd);
    } else {
      let length = me.getCount(),
          tickStart,
          tickEnd,
          tick;

      for (let i = 0; i < length; i++) {
        tick = me.getAt(i);
        tickStart = tick.startDate;
        tickEnd = tick.endDate;

        if (inclusiveEnd && date <= tickEnd || !inclusiveEnd && date < tickEnd) {
          return date >= tickStart;
        }
      }
    }

    return false;
  }
  /**
   * Returns true if the passed timespan is part of the current time axis (in whole or partially).
   * @param {Date} start The start date
   * @param {Date} end The end date
   * @return {Boolean} true if the timespan is part of the timeaxis
   */

  timeSpanInAxis(start, end) {
    const me = this;

    if (!end || end.getTime() === start.getTime()) {
      return this.dateInAxis(start, true);
    }

    if (me.isContinuous) {
      return DateHelper.intersectSpans(start, end, me.startDate, me.endDate);
    }

    return start < me.startDate && end > me.endDate || me.getTickFromDate(start) !== me.getTickFromDate(end);
  } // Accepts a TimeSpan model (uses its cached MS values to be a bit faster during rendering)

  isTimeSpanInAxis(timeSpan) {
    const me = this,
          {
      startMS,
      endMS
    } = me,
          {
      startDateMS,
      endDateMS
    } = timeSpan; // only consider fully scheduled ranges

    if (!startDateMS || !endDateMS) return false;

    if (endDateMS === startDateMS) {
      return me.dateInAxis(timeSpan.startDate, true);
    }

    if (me.isContinuous) {
      return endDateMS > startMS && startDateMS < endMS;
    }

    const startTick = me.getTickFromDate(timeSpan.startDate),
          endTick = me.getTickFromDate(timeSpan.endDate); // endDate is not inclusive
    // TODO: Handle in getTickFromDate() somehow?

    if (startTick === me.count && DateHelper.isEqual(timeSpan.startDate, me.last.endDate) || endTick === 0 && DateHelper.isEqual(timeSpan.endDate, me.first.startDate)) {
      return false;
    }

    return (// Spanning entire axis
      startDateMS < startMS && endDateMS > endMS || // Unintentionally 0 wide (ticks excluded or outside)
      startTick !== endTick
    );
  } //endregion
  //region Iteration

  /**
   * Calls the supplied iterator function once per interval. The function will be called with three parameters, start date and end date and an index.
   * @internal
   * @param {String} unit The unit to use when iterating over the timespan
   * @param {Number} increment The increment to use when iterating over the timespan
   * @param {Function} iteratorFn The function to call
   * @param {Object} [thisObj] `this` reference for the function
   */

  forEachAuxInterval(unit, increment = 1, iteratorFn, thisObj = this) {
    let end = this.endDate,
        dt = this.startDate,
        i = 0,
        intervalEnd;
    if (dt > end) throw new Error('Invalid time axis configuration');

    while (dt < end) {
      intervalEnd = DateHelper.min(DateHelper.getNext(dt, unit, increment, this.weekStartDay), end);
      iteratorFn.call(thisObj, dt, intervalEnd, i);
      dt = intervalEnd;
      i++;
    }
  } //endregion

}
TimeAxis._$name = 'TimeAxis';

/**
 * @module Scheduler/feature/ColumnLines
 */

/**
 * Displays column lines for ticks, with a different styling for major ticks (by default they are darker). If this
 * feature is disabled, no lines are shown. If it's enabled, line are shown for the tick level which is set in current
 * ViewPreset. Please see {@link Scheduler.preset.ViewPreset#field-columnLinesFor} config for details.
 *
 * The lines are drawn as divs, with only visible lines available in DOM. The color and style of the lines are
 * determined the css rules for `.b-column-line` and `.b-column-line-major`.
 *
 * For vertical mode, this features also draws vertical resource column lines if scheduler is configured with
 * `columnLines : true` (which is the default, see {@link Grid.view.GridBase#config-columnLines}).
 *
 * This feature is **enabled** by default
 *
 * @extends Core/mixin/InstancePlugin
 * @demo Scheduler/basic
 * @externalexample scheduler/ColumnLines.js
 * @classtype columnLines
 */

class ColumnLines extends InstancePlugin {
  //region Config
  static get $name() {
    return 'ColumnLines';
  } // Plugin configuration. This plugin chains some of the functions in Grid.

  static get pluginConfig() {
    return {
      after: ['render', 'updateCanvasSize', 'onVisibleDateRangeChange', 'onVisibleResourceRangeChange']
    };
  } //endregion
  //region Init & destroy

  construct(client, config) {
    client.useBackgroundCanvas = true;
    super.construct(client, config);
  }

  doDisable(disable) {
    super.doDisable(disable);

    if (!this.isConfiguring) {
      this.refresh();
    }
  } //endregion
  //region Draw

  /**
   * Draw lines when scheduler/gantt is rendered.
   * @private
   */

  render() {
    this.refresh();
  }
  /**
   * Draw column lines that are in view
   * @private
   */

  refresh() {
    const me = this,
          {
      client
    } = me,
          {
      timeAxis,
      timeAxisViewModel,
      isHorizontal
    } = client,
          axisStart = timeAxis.startDate; // Early bailout for timeaxis without start date

    if (!axisStart || !me.startDate) {
      return;
    }

    const linesForLevel = timeAxisViewModel.columnLinesFor,
          majorLinesForLevel = Math.max(linesForLevel - 1, 0),
          start = me.startDate.getTime(),
          end = me.endDate.getTime(),
          domConfigs = [],
          dates = new Set();

    if (!me.element) {
      me.element = DomHelper.createElement({
        parent: client.backgroundCanvas,
        className: 'b-column-lines-canvas'
      });
    }

    if (!me.disabled) {
      const addLineConfig = (tick, isMajor) => {
        const tickEnd = tick.end.getTime(); // Only end of tick matters, not a typo :)

        if (tickEnd > start && tickEnd < end && !dates.has(tickEnd)) {
          dates.add(tickEnd);
          domConfigs.push({
            className: isMajor ? 'b-column-line-major' : 'b-column-line',
            style: {
              //End date is exclusive, thus -1
              [isHorizontal ? 'left' : 'top']: tick.coord + tick.width - 1
            },
            dataset: {
              line: isMajor ? `major-${tick.index}` : `line-${tick.index}`
            }
          });
        }
      }; // Collect configs for major lines

      if (linesForLevel !== majorLinesForLevel) {
        timeAxisViewModel.columnConfig[majorLinesForLevel].forEach(tick => addLineConfig(tick, true));
      } // And normal lines, skipping dates already occupied by major lines

      timeAxisViewModel.columnConfig[linesForLevel].forEach(tick => addLineConfig(tick, false)); // Add vertical resource column lines, if grid is configured to show column lines

      if (!isHorizontal && client.columnLines) {
        const {
          columnWidth,
          firstResource,
          lastResource
        } = client.resourceColumns;

        for (let i = firstResource; i < lastResource + 1; i++) {
          domConfigs.push({
            className: 'b-column-line b-resource-column-line',
            style: {
              left: (i + 1) * columnWidth - 1
            },
            dataset: {
              line: `resource-${i}`
            }
          });
        }
      }
    }

    DomSync.sync({
      targetElement: me.element,
      onlyChildren: true,
      domConfig: {
        children: domConfigs,
        syncOptions: {
          // When zooming in and out we risk getting a lot of released lines if we do not limit it
          releaseThreshold: 4
        }
      },
      syncIdField: 'line'
    });
  } //endregion
  //region Events
  // Called when visible date range changes, for example from zooming, scrolling, resizing

  onVisibleDateRangeChange({
    startDate,
    endDate
  }) {
    this.startDate = startDate;
    this.endDate = endDate;
    this.refresh();
  } // Called when visible resource range changes, for example on scroll and resize

  onVisibleResourceRangeChange({
    firstResource,
    lastResource
  }) {
    this.refresh();
  }

  updateCanvasSize() {
    this.refresh();
  } //endregion

}
ColumnLines._$name = 'ColumnLines';
GridFeatureManager.registerFeature(ColumnLines, true, ['Scheduler', 'Gantt']);

/**
 * @module Scheduler/feature/mixin/DependencyCreation
 */
// TODO: refactor this class using StateChart utility to be implemented in Core/util/StateChart.js or XState library if allowed to be used

/**
 * Mixin for Dependencies feature that handles dependency creation (drag & drop from terminals which are shown on hover).
 * Requires {@link Core.mixin.Delayable} to be mixed in alongside.
 *
 * @mixin
 */

var DependencyCreation = (Target => class DependencyCreation extends (Target || Base) {
  static get $name() {
    return 'DependencyCreation';
  } //region Config

  static get defaultConfig() {
    return {
      /**
       * `false` to not show a tooltip while creating a dependency
       * @config {Boolean}
       * @default
       */
      showCreationTooltip: true,

      /**
       * A tooltip config object that will be applied to the dependency creation tooltip
       * @config {Object}
       */
      creationTooltip: null,

      /**
       * CSS class used for terminals
       * @config {String}
       * @default
       */
      terminalCls: 'b-sch-terminal',

      /**
       * Where (at events borders) to display terminals
       * @config {String[]}
       * @default
       */
      terminalSides: ['left', 'top', 'right', 'bottom'],

      /**
       * Set to `false` to not allow creating dependencies
       * @config {Boolean}
       * @default
       */
      allowCreate: true
    };
  } //endregion
  //region Init & destroy

  construct(view, config) {
    super.construct(view, config);
    const me = this;
    me.view = view;
    me.eventName = view.scheduledEventName;
    view.on('readOnly', () => me.updateCreateListeners());
    me.updateCreateListeners();
  }

  doDestroy() {
    var _me$mouseDetacher, _me$creationTooltip;

    const me = this;
    me.detachListeners('view');
    me.creationData = null;
    (_me$mouseDetacher = me.mouseDetacher) === null || _me$mouseDetacher === void 0 ? void 0 : _me$mouseDetacher.call(me);
    (_me$creationTooltip = me.creationTooltip) === null || _me$creationTooltip === void 0 ? void 0 : _me$creationTooltip.destroy();
    super.doDestroy();
  }

  updateCreateListeners() {
    const me = this;

    if (!me.view) {
      return;
    }

    me.detachListeners('view');

    if (me.isCreateAllowed) {
      me.view.on({
        name: 'view',
        [`${me.eventName}mouseenter`]: 'onTimeSpanMouseEnter',
        [`${me.eventName}mouseleave`]: 'onTimeSpanMouseLeave',
        thisObj: me
      });
    }
  }

  set allowCreate(value) {
    this._allowCreate = value;
    this.updateCreateListeners();
  }

  get allowCreate() {
    return this._allowCreate;
  }

  get isCreateAllowed() {
    return this.allowCreate && !this.view.readOnly && !this.disabled;
  }

  get listenersTarget() {
    return document.body;
  } //endregion
  //region Events

  /**
   * Show terminals when mouse enters event/task element
   * @private
   */

  onTimeSpanMouseEnter(event) {
    const record = event[`${this.eventName}Record`],
          element = event[`${this.eventName}Element`];
    this.showTerminals(record, DomHelper.down(element, event.source.eventInnerSelector));
  }
  /**
   * Hide terminals when mouse leaves event/task element
   * @private
   */

  onTimeSpanMouseLeave(event) {
    const me = this,
          element = event[`${me.eventName}Element`];

    if (!me.creationData || !DomHelper.isDescendant(element, me.creationData.sourceTerminal.parentElement)) {
      me.hideTerminals(element);
    }
  }
  /**
   * Remove hover styling when mouse leaves terminal. Also hides terminals when mouse leaves one it and not creating a
   * dependency.
   * @private
   */

  onTerminalMouseOut(event) {
    const me = this,
          el = DomHelper.up(event.target, me.view.eventSelector);

    if (el && !DomHelper.isDescendant(el, me.showingTerminalsFor) && (!me.creationData || el !== me.creationData.sourceTerminal.parentElement)) {
      me.hideTerminals(el);
      me.view.unhover(event);
    }
  }
  /**
   * Start creating a dependency when mouse is pressed over terminal
   * @private
   */

  onTerminalMouseDown(event) {
    const me = this; // ignore non-left button clicks

    if (event.button === 0 && !me.creationData) {
      var _view$resolveResource;

      const view = me.view,
            timeAxisSubGridElement = view.timeAxisSubGridElement,
            terminal = event.target,
            element = terminal.parentElement.closest(view.eventSelector),
            viewBounds = Rectangle.from(view.element, document.body);
      event.preventDefault();
      event.stopPropagation();
      me.creationData = {
        source: view.resolveTimeSpanRecord(element),
        sourceTerminal: terminal,
        startPoint: Rectangle.from(terminal, timeAxisSubGridElement).center,
        startX: event.pageX - viewBounds.x + view.scrollLeft,
        startY: event.pageY - viewBounds.y + view.scrollTop,
        valid: false,
        sourceResource: (_view$resolveResource = view.resolveResourceRecord) === null || _view$resolveResource === void 0 ? void 0 : _view$resolveResource.call(view, element)
      };
      me.mouseDetacher = EventHelper.on({
        mouseup: {
          element: me.listenersTarget,
          handler: 'onMouseUp'
        },
        mousemove: {
          element: timeAxisSubGridElement,
          handler: 'onMouseMove'
        },
        thisObj: me
      });
    }
  }
  /**
   * Update connector line showing dependency between source and target when mouse moves. Also check if mouse is over
   * a valid target terminal
   * @private
   */

  onMouseMove(event) {
    const me = this,
          {
      view,
      creationData: data
    } = me,
          viewBounds = Rectangle.from(view.element, document.body),
          deltaX = event.pageX - viewBounds.x + view.scrollLeft - data.startX,
          deltaY = event.pageY - viewBounds.y + view.scrollTop - data.startY,
          length = Math.round(Math.sqrt(deltaX * deltaX + deltaY * deltaY)) - 3,
          angle = Math.atan2(deltaY, deltaX);
    let {
      connector
    } = me;

    if (!connector) {
      if (me.onRequestDragCreate() === false) {
        return;
      }

      connector = me.connector;
    }

    connector.style.width = `${length}px`;
    connector.style.transform = `rotate(${angle}rad)`;
    me.lastMouseMoveEvent = event;
  }

  onRequestDragCreate() {
    const me = this,
          {
      view,
      creationData: data
    } = me;
    /**
     * Fired on the owning Scheduler/Gantt before a dependency creation drag operation starts. Return `false to
     * prevent it
     * @event beforeDependencyCreateDrag
     * @param {Object} data
     * @param {Scheduler.model.TimeSpan} data.source The source task
     */

    if (view.trigger('beforeDependencyCreateDrag', {
      data
    }) === false) {
      me.abort();
      return false;
    }

    view.element.classList.add('b-creating-dependency');
    me.connector = me.createConnector(data.startPoint.x, data.startPoint.y);
    /**
     * Fired on the owning Scheduler/Gantt when a dependency creation drag operation starts
     * @event dependencyCreateDragStart
     * @param {Object} data
     */

    view.trigger('dependencyCreateDragStart', {
      data
    });

    if (me.showCreationTooltip) {
      me.creationTooltip = me.creationTooltip || me.createDragTooltip();
      me.creationTooltip.disabled = false;
      me.creationTooltip.show();
    }

    view.scrollManager.startMonitoring({
      element: view.timeAxisSubGridElement,
      callback: () => me.lastMouseMoveEvent && me.onMouseMove(me.lastMouseMoveEvent),
      thisObj: me
    });
  }

  onOverTargetTerminal(event) {
    const me = this,
          {
      target
    } = event,
          {
      view,
      creationData: data
    } = me;
    const {
      connector
    } = me;

    if (!ObjectHelper.isPromise(data.valid)) {
      if (target !== data.sourceTerminal) {
        if (target !== data.targetTerminal) {
          data.targetTerminal = target;
          data.target = view.resolveTimeSpanRecord(target);

          if (view.resolveResourceRecord) {
            data.targetResource = view.resolveResourceRecord(target);
          }

          const fromSide = data.sourceTerminal.dataset.side,
                toSide = data.targetTerminal.dataset.side,
                updateValidity = valid => {
            if (!me.isDestroyed) {
              data.valid = valid;
              target.classList.add(valid ? 'b-valid' : 'b-invalid');
              connector.classList.add(valid ? 'b-valid' : 'b-invalid');
              /**
               * Fired on the owning Scheduler/Gantt when asynchronous dependency validation completes
               * @event dependencyValidationComplete
               * @param {Object} data
               */

              view.trigger('dependencyValidationComplete', {
                data
              });
            }
          };

          let type; // NOTE: Top/Bottom sides are not taken into account due to
          //       scheduler doesn't check for type value anyway, whereas
          //       gantt will reject any other dependency types undefined in
          //       DependencyBaseModel.Type enumeration.

          switch (true) {
            case fromSide === 'left' && toSide === 'left':
              type = DependencyBaseModel.Type.StartToStart;
              break;

            case fromSide === 'left' && toSide === 'right':
              type = DependencyBaseModel.Type.StartToEnd;
              break;

            case fromSide === 'right' && toSide === 'left':
              type = DependencyBaseModel.Type.EndToStart;
              break;

            case fromSide === 'right' && toSide === 'right':
              type = DependencyBaseModel.Type.EndToEnd;
              break;
          }
          /**
           * Fired on the owning Scheduler/Gantt when asynchronous dependency validation starts
           * @event dependencyValidationStart
           * @param {Object} data
           */

          view.trigger('dependencyValidationStart', {
            data
          });
          data.valid = me.dependencyStore.isValidDependency(data.source, data.target, type); // Promise is returned when using the engine

          if (ObjectHelper.isPromise(data.valid)) {
            data.valid.then(updateValidity);
          } else {
            updateValidity(data.valid);
          }
        }
      } else {
        connector.classList.remove('b-valid');
        connector.classList.remove('b-invalid');
      }
    }
  }
  /**
   * Create a new dependency if mouse release over valid terminal. Hides connector
   * @private
   */

  onMouseUp(event) {
    const me = this,
          data = me.creationData,
          target = event.target;
    me.abort();

    const doDependencyDrop = async () => {
      data.targetTerminal = event.target;
      const result = me.createDependency(data);

      if (ObjectHelper.isPromise(result)) {
        await result;
      } //data.valid = true; // TODO: remove this line if tests are fine

      /**
       * Fired on the owning Scheduler/Gantt when a dependency drag creation operation succeeds
       * @event dependencyCreateDrop
       * @param {Object} data
       */

      me.view.trigger('dependencyCreateDrop', {
        data
      });
    };

    const doAfterDependencyDrop = data => {
      /**
       * Fired on the owning Scheduler/Gantt after a dependency drag creation operation finished, no matter to outcome
       * @event afterDependencyCreateDrop
       * @param {Object} data
       */
      me.view.trigger('afterDependencyCreateDrop', {
        data
      });
    }; // TODO: should call finalize and allow user to hook it (as in EventDrag, EventResize)

    if (data.valid && target.matches(`.${me.terminalCls}`)) {
      if (ObjectHelper.isPromise(data.valid)) {
        data.valid.then(valid => {
          data.valid = valid;

          if (valid) {
            doDependencyDrop().then(() => doAfterDependencyDrop(data));
          } else {
            doAfterDependencyDrop(data);
          }
        });
      } else {
        doDependencyDrop().then(() => doAfterDependencyDrop(data));
      }
    } else {
      data.valid = false;
      doAfterDependencyDrop(data);
    }
  }
  /**
   * Aborts dependency creation, removes proxy and cleans up listeners
   */

  abort() {
    var _me$mouseDetacher2;

    const me = this,
          {
      view,
      creationData
    } = me; // Remove terminals from source and target events.

    if (creationData) {
      const {
        source,
        sourceResource,
        target,
        targetResource
      } = creationData;

      if (source) {
        const el = view.getElementFromEventRecord(source, sourceResource);

        if (el) {
          me.hideTerminals(el);
        }
      }

      if (target) {
        const el = view.getElementFromEventRecord(target, targetResource);

        if (el) {
          me.hideTerminals(el);
        }
      }
    }

    if (me.creationTooltip) {
      me.creationTooltip.disabled = true;
    }

    me.creationData = me.lastMouseMoveEvent = null;
    (_me$mouseDetacher2 = me.mouseDetacher) === null || _me$mouseDetacher2 === void 0 ? void 0 : _me$mouseDetacher2.call(me);
    me.removeConnector();
  } //endregion
  //region Connector

  /**
   * Creates a connector line that visualizes dependency source & target
   * @private
   */

  createConnector(x, y) {
    const me = this,
          view = me.view;
    me.connector = DomHelper.createElement({
      parent: view.timeAxisSubGridElement,
      className: `${me.baseCls}-connector`,
      style: `left:${x}px;top:${y}px`
    });
    view.element.classList.add('b-creating-dependency');
    return me.connector;
  }

  createDragTooltip() {
    const me = this,
          view = me.view;
    return me.creationTooltip = new Tooltip(Object.assign({
      id: `${view.id}-dependency-drag-tip`,
      cls: 'b-sch-dependency-creation-tooltip',
      loadingMsg: '',
      autoShow: true,
      anchorToTarget: false,
      // Keep tip visible until drag drop operation is finalized
      forSelector: '.b-sch-terminal',
      forElement: view.timeAxisSubGridElement,
      trackMouse: true,
      // Do not constrain at all, want it to be able to go outside of the viewport to not get in the way
      constrainTo: null,
      getHtml: ({
        tip,
        event
      }) => {
        if (me.creationData) {
          event && me.onOverTargetTerminal(event);
          return me.generateTooltipContent(tip, me.creationData);
        }
      },
      header: {
        dock: 'right'
      },
      listeners: {
        pointerout: ({
          source: tip
        }) => {
          const data = me.creationData;
          data.target = data.targetTerminal = null;
          data.valid = false;
          tip.html = tip.getHtml({
            tip
          });
          return false;
        },
        beforeShow: ({
          source: tip
        }) => {
          // Show initial content immediately
          tip.html = tip.getHtml({
            tip
          });
        }
      }
    }, me.creationTooltip || {}));
  }
  /**
   * Remove connector
   * @private
   */

  removeConnector(callback) {
    const me = this,
          {
      connector,
      view
    } = me;

    if (connector) {
      connector.classList.add('b-removing');
      connector.style.width = '0';
      me.setTimeout(() => {
        connector.remove();
        me.connector = null;

        if (callback) {
          callback.call(me);
        }
      }, 200);
    }

    view.element.classList.remove('b-creating-dependency');
    me.creationTooltip && me.creationTooltip.hide();
    view.scrollManager.stopMonitoring(view.timeAxisSubGridElement);
  } //endregion
  //region Terminals

  /**
   * Show terminals for specified event at sides defined in #terminalSides.
   * @param {Scheduler.model.TimeSpan} timeSpanRecord Event/task to show terminals for
   * @param {HTMLElement} element Event/task element
   */

  showTerminals(timeSpanRecord, element) {
    const me = this;

    if (!me.isCreateAllowed) {
      return;
    }

    const cls = me.terminalCls,
          terminalsVisibleCls = `${cls}s-visible`; // We operate on the event bar, not the wrap

    element = DomHelper.down(element, me.view.eventInnerSelector); // bail out if terminals already shown or if view is readonly
    // do not draw new terminals if we are resizing event

    if (!element.classList.contains(terminalsVisibleCls) && !this.view.element.classList.contains('b-resizing-event') && !me.view.readOnly) {
      // create terminals for desired sides
      me.terminalSides.forEach(side => {
        const terminal = DomHelper.createElement({
          parent: element,
          className: `${cls} ${cls}-${side}`,
          dataset: {
            side,
            feature: true
          }
        });
        terminal.detacher = EventHelper.on({
          element: terminal,
          mouseout: 'onTerminalMouseOut',
          mousedown: {
            handler: 'onTerminalMouseDown',
            capture: true
          },
          thisObj: me
        });
      });
      element.classList.add(terminalsVisibleCls);
      timeSpanRecord.cls.add(terminalsVisibleCls);
      me.showingTerminalsFor = element;
    }
  }
  /**
   * Hide terminals for specified event
   * @param {HTMLElement} eventElement Event element
   */

  hideTerminals(eventElement) {
    // remove all terminals
    const me = this,
          eventParams = me.client.getTimeSpanMouseEventParams(eventElement),
          timeSpanRecord = eventParams === null || eventParams === void 0 ? void 0 : eventParams[`${me.eventName}Record`],
          terminalsVisibleCls = `${me.terminalCls}s-visible`;
    DomHelper.forEachSelector(eventElement, `.${me.terminalCls}`, terminal => {
      terminal.detacher && terminal.detacher();
      terminal.remove();
    });
    DomHelper.down(eventElement, me.view.eventInnerSelector).classList.remove(terminalsVisibleCls);
    timeSpanRecord.cls.remove(terminalsVisibleCls);
    me.showingTerminalsFor = null;
  } //endregion
  //region Dependency creation

  /**
   * Create a new dependency from source terminal to target terminal
   * @internal
   */

  createDependency(data) {
    const {
      source,
      target
    } = data,
          fromSide = data.sourceTerminal.dataset.side,
          toSide = data.targetTerminal.dataset.side,
          type = (fromSide === 'left' ? 0 : 2) + (toSide === 'right' ? 1 : 0);
    return this.dependencyStore.add({
      from: source.id,
      to: target.id,
      type,
      fromSide,
      toSide
    })[0];
  } //endregion
  //region Tooltip

  /**
   * Update dependency creation tooltip
   * @private
   */

  generateTooltipContent(tip, data) {
    var _data$target$name, _data$target, _data$targetTerminal$, _data$targetTerminal;

    const me = this,
          {
      valid
    } = data,
          {
      classList
    } = tip.element;
    Object.assign(data, {
      fromText: data.source.name,
      toText: (_data$target$name = (_data$target = data.target) === null || _data$target === void 0 ? void 0 : _data$target.name) !== null && _data$target$name !== void 0 ? _data$target$name : '',
      fromSide: data.sourceTerminal.dataset.side,
      toSide: (_data$targetTerminal$ = (_data$targetTerminal = data.targetTerminal) === null || _data$targetTerminal === void 0 ? void 0 : _data$targetTerminal.dataset.side) !== null && _data$targetTerminal$ !== void 0 ? _data$targetTerminal$ : ''
    });
    let tipTitleIconClsSuffix, tipTitleText; // Promise, when using engine

    if (ObjectHelper.isPromise(valid)) {
      classList.remove('b-invalid');
      classList.add('b-checking');
      return new Promise(resolve => valid.then(valid => {
        data.valid = valid;

        if (!tip.isDestroyed) {
          resolve(me.generateTooltipContent(tip, data));
        }
      }));
    } // Valid
    else if (valid === true) {
        classList.remove('b-invalid');
        classList.remove('b-checking');
        tipTitleIconClsSuffix = 'valid';
        tipTitleText = me.L('L{Dependencies.valid}');
      } // Invalid
      else {
          classList.remove('b-checking');
          classList.add('b-invalid');
          tipTitleIconClsSuffix = 'invalid';
          tipTitleText = me.L('L{Dependencies.invalid}');
        }

    tip.title = `<i class="b-icon b-icon-${tipTitleIconClsSuffix}"></i>${tipTitleText}`;
    return `<table class="b-sch-dependency-creation-tooltip">
                        <tr><td>${me.L('L{Dependencies.from}')}: </td><td>${data.fromText}</td><td><div class="b-sch-box b-${data.fromSide}"></div></td></tr>
                        <tr><td>${me.L('L{Dependencies.to}')}: </td><td>${data.toText}</td><td><div class="b-sch-box b-${data.toSide}"></div></td></tr>
                    </table>`;
  } //endregion

});

/**
 * @module Scheduler/util/RectangularPathFinder
 */

/**
 * Class which finds rectangular path, i.e. path with 90 degrees turns, between two boxes.
 * @private
 */

class RectangularPathFinder extends Base {
  static get defaultConfig() {
    return {
      /**
       * Default start connection side: 'left', 'right', 'top', 'bottom'
       * @config {String|Boolean}
       * @default
       */
      startSide: 'right',

      /**
       * Default start arrow size in pixels
       * @config {Number}
       * @default
       */
      startArrowSize: 0,

      /**
       * Default start arrow staff size in pixels
       * @config {Number}
       * @default
       */
      startArrowMargin: 12,

      /**
       * Default starting connection point shift from box's arrow pointing side middle point
       * @config {Number}
       * @default
       */
      startShift: 0,

      /**
       * Default end arrow pointing direction, possible values are: 'left', 'right', 'top', 'bottom'
       * @config {String|Boolean}
       * @default
       */
      endSide: 'left',

      /**
       * Default end arrow size in pixels
       * @config {Number}
       * @default
       */
      endArrowSize: 0,

      /**
       * Default end arrow staff size in pixels
       * @config {Number}
       * @default
       */
      endArrowMargin: 12,

      /**
       * Default ending connection point shift from box's arrow pointing side middle point
       * @config {Number}
       * @default
       */
      endShift: 0,

      /**
       * Start / End box vertical margin, the amount of pixels from top and bottom line of a box where drawing
       * is prohibited
       * @config {Number}
       * @default
       */
      verticalMargin: 2,

      /**
       * Start / End box horizontal margin, the amount of pixels from left and right line of a box where drawing
       * @config {Number}
       * @default
       */
      horizontalMargin: 5,

      /**
       * Other rectangular areas (obstacles) to search path through
       * @config {Object[]}
       * @default
       */
      otherBoxes: null
    };
  }
  /**
   * Returns list of horizontal and vertical segments connecting two boxes
   * <pre>
   *    |    | |  |    |       |
   *  --+----+----+----*-------*---
   *  --+=>Start  +----*-------*--
   *  --+----+----+----*-------*--
   *    |    | |  |    |       |
   *    |    | |  |    |       |
   *  --*----*-+-------+-------+--
   *  --*----*-+         End <=+--
   *  --*----*-+-------+-------+--
   *    |    | |  |    |       |
   * </pre>
   * Path goes by lines (-=) and turns at intersections (+), boxes depicted are adjusted by horizontal/vertical
   * margin and arrow margin, original boxes are smaller (path can't go at original box borders). Algorithm finds
   * the shortest path with minimum amount of turns. In short it's mix of "Lee" and "Dijkstra pathfinding"
   * with turns amount taken into account for distance calculation.
   *
   * The algorithm is not very performant though, it's O(N^2), where N is amount of
   * points in the grid, but since the maximum amount of points in the grid might be up to 34 (not 36 since
   * two box middle points are not permitted) that might be ok for now.
   *
   * @param {Object} lineDef An object containing any of the class configuration option overrides as well
   *                         as `startBox`, `endBox`, `startHorizontalMargin`, `startVerticalMargin`,
   *                         `endHorizontalMargin`, `endVerticalMargin` properties
   * @param {Object} lineDef.startBox An object containing `start`, `end`, `top`, `bottom` properties
   * @param {Object} lineDef.endBox   An object containing `start`, `end`, `top`, `bottom` properties
   * @param {Number} lineDef.startHorizontalMargin Horizontal margin override for start box
   * @param {Number} lineDef.startVerticalMargin   Vertical margin override for start box
   * @param {Number} lineDef.endHorizontalMargin   Horizontal margin override for end box
   * @param {Number} lineDef.endVerticalMargin     Vertical margin override for end box
   *
   *
   * @return {Object[]|Boolean} Array of line segments or false if path cannot be found
   * @return {Number} return.x1
   * @return {Number} return.y1
   * @return {Number} return.x2
   * @return {Number} return.y2
   */
  //
  //@ignore
  //@privateparam {Function[]|Function} noPathFallbackFn
  //     A function or array of functions which will be tried in case a path can't be found
  //     Each function will be given a line definition it might try to adjust somehow and return.
  //     The new line definition returned will be tried to find a path.
  //     If a function returns false, then next function will be called if any.
  //

  findPath(lineDef, noPathFallbackFn) {
    let me = this,
        lineDefFull,
        startBox,
        endBox,
        startShift,
        endShift,
        startSide,
        endSide,
        startArrowSize,
        endArrowSize,
        startArrowMargin,
        endArrowMargin,
        horizontalMargin,
        verticalMargin,
        startHorizontalMargin,
        startVerticalMargin,
        endHorizontalMargin,
        endVerticalMargin,
        otherHorizontalMargin,
        otherVerticalMargin,
        otherBoxes,
        connStartPoint,
        connEndPoint,
        pathStartPoint,
        pathEndPoint,
        gridStartPoint,
        gridEndPoint,
        startGridBox,
        endGridBox,
        grid,
        path,
        tryNum;

    if (noPathFallbackFn && !Array.isArray(noPathFallbackFn)) {
      noPathFallbackFn = [noPathFallbackFn];
    }

    for (tryNum = 0; lineDef && !path;) {
      lineDefFull = Object.assign(me.config, lineDef);
      startBox = lineDefFull.startBox;
      endBox = lineDefFull.endBox;
      startShift = lineDefFull.startShift;
      endShift = lineDefFull.endShift;
      startSide = lineDefFull.startSide;
      endSide = lineDefFull.endSide;
      startArrowSize = lineDefFull.startArrowSize;
      endArrowSize = lineDefFull.endArrowSize;
      startArrowMargin = lineDefFull.startArrowMargin;
      endArrowMargin = lineDefFull.endArrowMargin;
      horizontalMargin = lineDefFull.horizontalMargin;
      verticalMargin = lineDefFull.verticalMargin;
      startHorizontalMargin = lineDefFull.hasOwnProperty('startHorizontalMargin') ? lineDefFull.startHorizontalMargin : horizontalMargin;
      startVerticalMargin = lineDefFull.hasOwnProperty('startVerticalMargin') ? lineDefFull.startVerticalMargin : verticalMargin;
      endHorizontalMargin = lineDefFull.hasOwnProperty('endHorizontalMargin') ? lineDefFull.endHorizontalMargin : horizontalMargin;
      endVerticalMargin = lineDefFull.hasOwnProperty('endVerticalMargin') ? lineDefFull.endVerticalMargin : verticalMargin;
      otherHorizontalMargin = lineDefFull.hasOwnProperty('otherHorizontalMargin') ? lineDefFull.otherHorizontalMargin : horizontalMargin;
      otherVerticalMargin = lineDefFull.hasOwnProperty('otherVerticalMargin') ? lineDefFull.otherVerticalMargin : verticalMargin;
      otherBoxes = lineDefFull.otherBoxes;
      startSide = me.normalizeSide(startSide);
      endSide = me.normalizeSide(endSide);
      connStartPoint = me.getConnectionCoordinatesFromBoxSideShift(startBox, startSide, startShift);
      connEndPoint = me.getConnectionCoordinatesFromBoxSideShift(endBox, endSide, endShift);
      startGridBox = me.calcGridBaseBoxFromBoxAndDrawParams(startBox, startSide, startArrowSize, startArrowMargin, startHorizontalMargin, startVerticalMargin);
      endGridBox = me.calcGridBaseBoxFromBoxAndDrawParams(endBox, endSide, endArrowSize, endArrowMargin, endHorizontalMargin, endVerticalMargin);
      otherBoxes = otherBoxes && otherBoxes.map(box => me.calcGridBaseBoxFromBoxAndDrawParams(box, false, 0, 0, otherHorizontalMargin, otherVerticalMargin));
      pathStartPoint = me.getConnectionCoordinatesFromBoxSideShift(startGridBox, startSide, startShift);
      pathEndPoint = me.getConnectionCoordinatesFromBoxSideShift(endGridBox, endSide, endShift);
      grid = me.buildPathGrid(startGridBox, endGridBox, pathStartPoint, pathEndPoint, startSide, endSide, otherBoxes);
      gridStartPoint = me.convertDecartPointToGridPoint(grid, pathStartPoint);
      gridEndPoint = me.convertDecartPointToGridPoint(grid, pathEndPoint);
      path = me.findPathOnGrid(grid, gridStartPoint, gridEndPoint, startSide, endSide); // Loop if
      // - path is still not found
      // - have no next line definition (which should be obtained from call to one of the functions from noPathFallbackFn array
      // - have noPathFallBackFn array
      // - current try number is less then noPathFallBackFn array length

      for (lineDef = false; !path && !lineDef && noPathFallbackFn && tryNum < noPathFallbackFn.length; tryNum++) {
        lineDef = noPathFallbackFn[tryNum](lineDefFull);
      }
    }

    if (path) {
      path = me.prependPathWithArrowStaffSegment(path, connStartPoint, startArrowSize, startSide);
      path = me.appendPathWithArrowStaffSegment(path, connEndPoint, endArrowSize, endSide);
      path = me.optimizePath(path);
    }

    return path;
  }

  getConnectionCoordinatesFromBoxSideShift(box, side, shift) {
    let coords;

    switch (side) {
      case 'left':
        coords = {
          x: box.start,
          y: (box.top + box.bottom) / 2 + shift
        };
        break;

      case 'right':
        coords = {
          x: box.end,
          y: (box.top + box.bottom) / 2 + shift
        };
        break;

      case 'top':
        coords = {
          x: (box.start + box.end) / 2 + shift,
          y: box.top
        };
        break;

      case 'bottom':
        coords = {
          x: (box.start + box.end) / 2 + shift,
          y: box.bottom
        };
        break;
    }

    return coords;
  }

  calcGridBaseBoxFromBoxAndDrawParams(box, side, arrowSize, arrowMargin, horizontalMargin, verticalMargin) {
    let gridBox;

    switch (side) {
      case 'left':
        gridBox = {
          start: box.start - Math.max(arrowSize + arrowMargin, horizontalMargin),
          end: box.end + horizontalMargin,
          top: box.top - verticalMargin,
          bottom: box.bottom + verticalMargin
        };
        break;

      case 'right':
        gridBox = {
          start: box.start - horizontalMargin,
          end: box.end + Math.max(arrowSize + arrowMargin, horizontalMargin),
          top: box.top - verticalMargin,
          bottom: box.bottom + verticalMargin
        };
        break;

      case 'top':
        gridBox = {
          start: box.start - horizontalMargin,
          end: box.end + horizontalMargin,
          top: box.top - Math.max(arrowSize + arrowMargin, verticalMargin),
          bottom: box.bottom + verticalMargin
        };
        break;

      case 'bottom':
        gridBox = {
          start: box.start - horizontalMargin,
          end: box.end + horizontalMargin,
          top: box.top - verticalMargin,
          bottom: box.bottom + Math.max(arrowSize + arrowMargin, verticalMargin)
        };
        break;

      default:
        gridBox = {
          start: box.start - horizontalMargin,
          end: box.end + horizontalMargin,
          top: box.top - verticalMargin,
          bottom: box.bottom + verticalMargin
        };
    }

    return gridBox;
  }

  buildPathGrid(startGridBox, endGridBox, pathStartPoint, pathEndPoint, startSide, endSide, otherGridBoxes) {
    let xs,
        ys,
        y,
        x,
        ix,
        iy,
        xslen,
        yslen,
        ib,
        blen,
        box,
        permitted,
        point,
        points = {},
        linearPoints = [];
    xs = [startGridBox.start, startSide === 'left' || startSide === 'right' ? (startGridBox.start + startGridBox.end) / 2 : pathStartPoint.x, startGridBox.end, endGridBox.start, endSide === 'left' || endSide === 'right' ? (endGridBox.start + endGridBox.end) / 2 : pathEndPoint.x, endGridBox.end];
    ys = [startGridBox.top, startSide === 'top' || startSide === 'bottom' ? (startGridBox.top + startGridBox.bottom) / 2 : pathStartPoint.y, startGridBox.bottom, endGridBox.top, endSide === 'top' || endSide === 'bottom' ? (endGridBox.top + endGridBox.bottom) / 2 : pathEndPoint.y, endGridBox.bottom];

    if (otherGridBoxes) {
      otherGridBoxes.forEach(box => {
        xs.push(box.start, (box.start + box.end) / 2, box.end);
        ys.push(box.top, (box.top + box.bottom) / 2, box.bottom);
      });
    }

    xs = [...new Set(xs.sort((a, b) => a - b))];
    ys = [...new Set(ys.sort((a, b) => a - b))]; // TODO: fastest way to make unique, Set is slower
    // for ( let i = 0, I = array.length; i < I; i++ ) {
    //     if ( ~array.indexOf( array[ i ], i + 1 ) ) {
    //         array.splice( i, 1 );
    //         i--;
    //         I--;
    //     }
    // }

    for (iy = 0, yslen = ys.length; iy < yslen; ++iy) {
      points[iy] = points[iy] || {};
      y = ys[iy];

      for (ix = 0, xslen = xs.length; ix < xslen; ++ix) {
        x = xs[ix];
        permitted = (x <= startGridBox.start || x >= startGridBox.end || y <= startGridBox.top || y >= startGridBox.bottom) && (x <= endGridBox.start || x >= endGridBox.end || y <= endGridBox.top || y >= endGridBox.bottom);

        if (otherGridBoxes) {
          for (ib = 0, blen = otherGridBoxes.length; permitted && ib < blen; ++ib) {
            box = otherGridBoxes[ib];
            permitted = x <= box.start || x >= box.end || y <= box.top || y >= box.bottom || // Allow point if it is a path start/end even if point is inside any box
            x === pathStartPoint.x && y === pathStartPoint.y || x === pathEndPoint.x && y === pathEndPoint.y;
          }
        }

        point = {
          distance: Math.pow(2, 53) - 1,
          // Number.MAX_SAFE_INTEGER (not supported in Opera/IE)
          permitted: permitted,
          x: x,
          y: y,
          ix: ix,
          iy: iy
        };
        points[iy][ix] = point;
        linearPoints.push(point);
      }
    }

    return {
      width: xs.length,
      height: ys.length,
      xs: xs,
      ys: ys,
      points: points,
      linearPoints: linearPoints
    };
  }

  convertDecartPointToGridPoint(grid, point) {
    let x = grid.xs.indexOf(point.x),
        y = grid.ys.indexOf(point.y);
    return grid.points[y][x];
  }

  findPathOnGrid(grid, gridStartPoint, gridEndPoint, startSide, endSide) {
    let me = this,
        path = false;

    if (gridStartPoint.permitted && gridEndPoint.permitted) {
      grid = me.waveForward(grid, gridStartPoint, 0);
      path = me.collectPath(grid, gridEndPoint, endSide);
    }

    return path;
  } // Returns neighbors from Von Neiman ambit (see Lee pathfinding algorithm description)

  getGridPointNeighbors(grid, gridPoint, predicateFn) {
    let ix = gridPoint.ix,
        iy = gridPoint.iy,
        result = [],
        neighbor; // NOTE:
    // It's important to push bottom neighbors first since this method is used
    // in collectPath(), which reversively collects path from end to start node
    // and if bottom neighbors are pushed first in result array then collectPath()
    // will produce a line which is more suitable (pleasant looking) for our purposes.

    if (iy < grid.height - 1) {
      neighbor = grid.points[iy + 1][ix];
      (!predicateFn || predicateFn(neighbor)) && result.push(neighbor);
    }

    if (iy > 0) {
      neighbor = grid.points[iy - 1][ix];
      (!predicateFn || predicateFn(neighbor)) && result.push(neighbor);
    }

    if (ix < grid.width - 1) {
      neighbor = grid.points[iy][ix + 1];
      (!predicateFn || predicateFn(neighbor)) && result.push(neighbor);
    }

    if (ix > 0) {
      neighbor = grid.points[iy][ix - 1];
      (!predicateFn || predicateFn(neighbor)) && result.push(neighbor);
    }

    return result;
  }

  waveForward(grid, gridStartPoint, distance) {
    const me = this; // I use the WalkHelper here because a point on a grid and it's neighbors might be considered as a hierarchy.
    // The point is the parent node, and it's neighbors are the children nodes. Thus the grid here is hierarchical
    // data structure which can be walked. WalkHelper walks non-recursivelly which is exactly what I need as well.

    WalkHelper.preWalkUnordered( // Walk starting point - a node is a grid point and it's distance from the starting point
    [gridStartPoint, distance], // Children query function
    // NOTE: It's important to fix neighbor distance first, before waving to a neighbor, otherwise waving might
    //       get through a neighbor point setting it's distance to a value more then (distance + 1) whereas we,
    //       at the children quering moment in time, already know that the possibly optimal distance is (distance + 1)
    ([point, distance]) => me.getGridPointNeighbors(grid, point, neighborPoint => neighborPoint.permitted && neighborPoint.distance > distance + 1).map(neighborPoint => [neighborPoint, distance + 1] // Neighbor distance fixation
    ), // Walk step iterator function
    ([point, distance]) => point.distance = distance // Neighbor distance applying
    );
    return grid;
  }

  collectPath(grid, gridEndPoint, endSide) {
    let me = this,
        pathFound = true,
        neighbors,
        lowestDistanceNeighbor,
        xDiff,
        yDiff,
        path = [];

    while (pathFound && gridEndPoint.distance) {
      neighbors = me.getGridPointNeighbors(grid, gridEndPoint, point => point.permitted && point.distance == gridEndPoint.distance - 1);
      pathFound = neighbors.length > 0;

      if (pathFound) {
        // Prefer turnless neighbors first
        neighbors = neighbors.sort((a, b) => {
          let xDiff, yDiff;
          xDiff = a.ix - gridEndPoint.ix;
          yDiff = a.iy - gridEndPoint.iy;
          let resultA = (endSide === 'left' || endSide === 'right') && yDiff === 0 || (endSide === 'top' || endSide === 'bottom') && xDiff === 0 ? -1 : 1;
          xDiff = b.ix - gridEndPoint.ix;
          yDiff = b.iy - gridEndPoint.iy;
          let resultB = (endSide === 'left' || endSide === 'right') && yDiff === 0 || (endSide === 'top' || endSide === 'bottom') && xDiff === 0 ? -1 : 1;
          if (resultA > resultB) return 1;
          if (resultA < resultB) return -1; // apply additional sorting to be sure to pick bottom path in IE

          if (resultA === resultB) return a.y > b.y ? -1 : 1;
        });
        lowestDistanceNeighbor = neighbors[0];
        path.push({
          x1: lowestDistanceNeighbor.x,
          y1: lowestDistanceNeighbor.y,
          x2: gridEndPoint.x,
          y2: gridEndPoint.y
        }); // Detecting new side, either xDiff or yDiff must be 0 (but not both)

        xDiff = lowestDistanceNeighbor.ix - gridEndPoint.ix;
        yDiff = lowestDistanceNeighbor.iy - gridEndPoint.iy;

        switch (true) {
          case !yDiff && xDiff > 0:
            endSide = 'left';
            break;

          case !yDiff && xDiff < 0:
            endSide = 'right';
            break;

          case !xDiff && yDiff > 0:
            endSide = 'top';
            break;

          case !xDiff && yDiff < 0:
            endSide = 'bottom';
            break;
        }

        gridEndPoint = lowestDistanceNeighbor;
      }
    }

    return pathFound && path.reverse() || false;
  }

  prependPathWithArrowStaffSegment(path, connStartPoint, startArrowSize, startSide) {
    let prependSegment, firstSegment;

    if (path.length > 0) {
      firstSegment = path[0];
      prependSegment = {
        x2: firstSegment.x1,
        y2: firstSegment.y1
      };

      switch (startSide) {
        case 'left':
          prependSegment.x1 = connStartPoint.x - startArrowSize;
          prependSegment.y1 = firstSegment.y1;
          break;

        case 'right':
          prependSegment.x1 = connStartPoint.x + startArrowSize;
          prependSegment.y1 = firstSegment.y1;
          break;

        case 'top':
          prependSegment.x1 = firstSegment.x1;
          prependSegment.y1 = connStartPoint.y - startArrowSize;
          break;

        case 'bottom':
          prependSegment.x1 = firstSegment.x1;
          prependSegment.y1 = connStartPoint.y + startArrowSize;
          break;
      }

      path.unshift(prependSegment);
    }

    return path;
  }

  appendPathWithArrowStaffSegment(path, connEndPoint, endArrowSize, endSide) {
    let appendSegment, lastSegment;

    if (path.length > 0) {
      lastSegment = path[path.length - 1];
      appendSegment = {
        x1: lastSegment.x2,
        y1: lastSegment.y2
      };

      switch (endSide) {
        case 'left':
          appendSegment.x2 = connEndPoint.x - endArrowSize;
          appendSegment.y2 = lastSegment.y2;
          break;

        case 'right':
          appendSegment.x2 = connEndPoint.x + endArrowSize;
          appendSegment.y2 = lastSegment.y2;
          break;

        case 'top':
          appendSegment.x2 = lastSegment.x2;
          appendSegment.y2 = connEndPoint.y - endArrowSize;
          break;

        case 'bottom':
          appendSegment.x2 = lastSegment.x2;
          appendSegment.y2 = connEndPoint.y + endArrowSize;
          break;
      }

      path.push(appendSegment);
    }

    return path;
  }

  optimizePath(path) {
    let optPath = [],
        prevSegment,
        curSegment;

    if (path.length > 0) {
      prevSegment = path.shift();
      optPath.push(prevSegment);

      while (path.length > 0) {
        curSegment = path.shift(); // both segments are equal

        if (prevSegment.x1 == curSegment.x1 && prevSegment.y1 == curSegment.y1 && prevSegment.x2 == curSegment.x2 && prevSegment.y2 == curSegment.y2) {
          prevSegment = curSegment;
        } // both segments are horizontal
        else if (prevSegment.y1 - prevSegment.y2 === 0 && curSegment.y1 - curSegment.y2 === 0) {
            prevSegment.x2 = curSegment.x2;
          } // both segments are vertical
          else if (prevSegment.x1 - prevSegment.x2 === 0 && curSegment.x1 - curSegment.x2 === 0) {
              prevSegment.y2 = curSegment.y2;
            } // segments has different orientation (path turn)
            else {
                optPath.push(curSegment);
                prevSegment = curSegment;
              }
      }
    }

    return optPath;
  }

  normalizeSide(side) {
    return RectangularPathFinder.sideToSide[side] || side;
  }

  static get sideToSide() {
    return {
      l: 'left',
      r: 'right',
      t: 'top',
      b: 'bottom'
    };
  }

}
RectangularPathFinder._$name = 'RectangularPathFinder';

/**
 * @module Scheduler/data/mixin/AttachToProjectMixin
 */

/**
 * Mixin that calls the target class `attachToProject()` function when a new project is assigned to Scheduler/Gantt.
 *
 * @mixin
 */
var AttachToProjectMixin = (Target => class AttachToProjectMixin extends Target {
  static get $name() {
    return 'AttachToProjectMixin';
  }

  async afterConstruct() {
    super.afterConstruct();
    const me = this,
          projectHolder = me.client || me.grid,
          {
      project
    } = projectHolder;
    projectHolder.projectSubscribers.push(me); // Attach to already existing stores

    if (project) {
      me.attachToProject(project);
      me.attachToResourceStore(project.resourceStore);
      me.attachToEventStore(project.eventStore);
      me.attachToAssignmentStore(project.assignmentStore);
      me.attachToDependencyStore(project.dependencyStore);
      me.attachToCalendarManagerStore(project.calendarManagerStore);
    }
  }
  /**
   * Override to take action when the project instance is replaced.
   *
   * @param {Scheduler.model.ProjectModel} project
   */

  attachToProject(project) {
    this.detachListeners('project');
    this._project = project;
  }
  /**
   * Override to take action when the EventStore instance is replaced, either from being replaced on the project or
   * from assigning a new project.
   *
   * @param {Scheduler.data.EventStore} store
   */

  attachToEventStore(store) {
    this.detachListeners('eventStore');
    this.eventStore = store;
  }
  /**
   * Override to take action when the ResourceStore instance is replaced, either from being replaced on the project
   * or from assigning a new project.
   *
   * @param {Scheduler.data.ResourceStore} store
   */

  attachToResourceStore(store) {
    this.detachListeners('resourceStore');
    this.resourceStore = store;
  }
  /**
   * Override to take action when the AssignmentStore instance is replaced, either from being replaced on the project
   * or from assigning a new project.
   *
   * @param {Scheduler.data.AssignmentStore} store
   */

  attachToAssignmentStore(store) {
    this.detachListeners('assignmentStore');
    this.assignmentStore = store;
  }
  /**
   * Override to take action when the DependencyStore instance is replaced, either from being replaced on the project
   * or from assigning a new project.
   *
   * @param {Scheduler.data.DependencyStore} store
   */

  attachToDependencyStore(store) {
    this.detachListeners('dependencyStore');
    this.dependencyStore = store;
  } // TODO: Move attachToCalendarMangerStore to Pro somehow

  /**
   * Override to take action when the CalendarManagerStore instance is replaced, either from being replaced on the
   * project or from assigning a new project.
   *
   * @param {Core.data.Store} store
   */

  attachToCalendarManagerStore(store) {
    this.detachListeners('calendarManagerStore');
    this.calendarManagerStore = store;
  }

  get project() {
    return this._project;
  }

});

/**
 * @module Scheduler/feature/Dependencies
 */

const resourceRefreshAllActions = {
  add: 1,
  dataset: 1,
  remove: 1,
  removeAll: 1
},
      fromBoxSide = ['left', 'left', 'right', 'right'],
      toBoxSide = ['left', 'right', 'left', 'right'];
/**
 * Feature that draws dependencies between events Uses a {@link Scheduler.data.DependencyStore DependencyStore} to
 * determine which dependencies to draw, if none is defined one will be created automatically. Dependencies can also be
 * specified as Scheduler#dependencies, see example below.
 *
 * To customize the dependency tooltip, you can provide the {@link #config-tooltip} config and specify a {@link Core.widget.Tooltip#config-getHtml} function.
 * For example:
 *
 * ```javascript
 * const scheduler = new Scheduler({
 *     features : {
 *         dependencies : {
 *             tooltip : {
 *                 getHtml({ activeTarget }) {
 *                     const dependencyModel = scheduler.resolveDependencyRecord(activeTarget);
 *
 *                     if (!dependencyModel) return null;
 *
 *                     const { fromEvent, toEvent } = dependencyModel;
 *
 *                     return `${fromEvent.name} (${fromEvent.id}) -> ${toEvent.name} (${toEvent.id})`;
 *                 }
 *             }
 *         }
 *     }
 * }
 * ```
 *
 * This feature is **off** by default. It is **not** supported in vertical mode.
 * For info on enabling it, see {@link Grid.view.mixin.GridFeatures}.
 *
 * @mixes Core/mixin/Delayable
 * @mixes Scheduler/feature/mixin/DependencyCreation
 *
 * @extends Core/mixin/InstancePlugin
 * @demo Scheduler/dependencies
 * @externalexample scheduler/Dependencies.js
 * @classtype dependencies
 */

class Dependencies extends InstancePlugin.mixin(DependencyCreation, AttachToProjectMixin, Delayable) {
  /**
   * Fired when dependencies are rendered
   * @event dependenciesDrawn
   * @param {Boolean} [partial] Optional event parameter. `true` when subset of dependencies is repainted, omitted
   * when all lines were repainted.
   */
  //region Config
  static get $name() {
    return 'Dependencies';
  }

  static get defaultConfig() {
    return {
      /**
       * Path finder instance configuration
       * @config {Object}
       */
      pathFinderConfig: null,

      /**
       * The CSS class to add to a dependency line when hovering over it
       * @config {String}
       * @default
       * @private
       */
      overCls: 'b-sch-dependency-over',

      /**
       * The CSS class applied to dependency lines
       * @config {String}
       * @default
       * @private
       */
      baseCls: 'b-sch-dependency',
      highlightDependenciesOnEventHover: false,

      /**
       * Set to true to show a tooltip when hovering a dependency line
       * @config {Boolean}
       */
      showTooltip: true,

      /**
       * A tooltip config object that will be applied to the dependency hover tooltip. Can be used to for example
       * customize delay
       * @config {Object}
       */
      tooltip: null,
      bufferSize: 50,
      // Used to determine the size of the cells in the virtual grid used to cache intersecting dependencies
      cacheGridSize: {
        // the width of an intersection cell expressed in timeaxis ticks
        ticks: 25,
        // the height of an intersection cell express in amount of rows
        // (could be named rows, but that wont match if we add support for vertical mode)
        index: 25
      }
    };
  }

  static get properties() {
    return {
      toDrawOnProjectRefresh: new Set(),
      dependenciesToRefresh: new Map(),
      drawnDependencies: [],
      drawnLines: []
    };
  } // Plugin configuration. This plugin chains some of the functions in Grid.

  static get pluginConfig() {
    return {
      chain: ['render', 'renderContents', 'onElementClick', 'onElementDblClick', 'onElementMouseOver', 'onElementMouseOut', 'onProjectRefresh'],
      assign: ['getElementForDependency', 'getElementsForDependency', 'resolveDependencyRecord']
    };
  } //endregion
  //region Init & destroy

  construct(scheduler, config = {}) {
    const me = this;

    if (scheduler.isVertical) {
      throw new Error('Dependencies feature is not supported in vertical mode');
    } // Many things may schedule a draw. Ensure it only happens once, on the next frame.
    // And Ensure it really is on the *next* frame after invocation by passing
    // the cancelOutstanding flag.

    me.doScheduleDraw = me.createOnFrame('draw', [], me, true);
    super.construct(scheduler, config);
    me.pathFinder = new RectangularPathFinder(me.pathFinderConfig);
    delete me.pathFinderConfig;
    me.lineDefAdjusters = me.createLineDefAdjusters();
    scheduler.rowManager.on({
      refresh: 'onRefresh',
      thisObj: me
    });
  }

  doDestroy() {
    var _this$tooltip;

    (_this$tooltip = this.tooltip) === null || _this$tooltip === void 0 ? void 0 : _this$tooltip.destroy();
    super.doDestroy();
  }

  doDisable(disable) {
    const me = this,
          {
      project
    } = me;
    me.updateCreateListeners(); // detach/attach store listeners

    if (project || disable) {
      me.attachToResourceStore(disable ? null : project.resourceStore);
      me.attachToEventStore(disable ? null : project.eventStore);
      me.attachToAssignmentStore(disable ? null : project.assignmentStore);
      me.attachToDependencyStore(disable ? null : project.dependencyStore);
    }

    if (me.client.isPainted) {
      // draw() needs "me.dependencyStore" to be provided
      me.dependencyStore = me.dependencyStore || project.dependencyStore;
      me.draw();
    }

    super.doDisable(disable);
  } //endregion
  //region Dependency types

  static getLocalizedDependencyType(type) {
    // Do not remove. Assertion strings for Localization sanity check.
    // 'L{DependencyType.SS}'
    // 'L{DependencyType.SF}'
    // 'L{DependencyType.FS}'
    // 'L{DependencyType.FF}'
    // 'L{DependencyType.StartToStart}'
    // 'L{DependencyType.StartToEnd}'
    // 'L{DependencyType.EndToStart}'
    // 'L{DependencyType.EndToEnd}'
    // 'L{DependencyType.long}'
    // 'L{DependencyType.short}'
    return type ? this.L(`L{DependencyType.${type}}`) : '';
  }

  findDependencyIndex(dependencies = [], dependency, assignmentData) {
    return dependencies.findIndex(item => item.dependency === dependency && (item.assignmentData === assignmentData || item.assignmentData && assignmentData && item.assignmentData.from === assignmentData.from && item.assignmentData.to === assignmentData.to));
  }

  includesDependency() {
    return this.findDependencyIndex(...arguments) >= 0;
  } //endregion
  //region Stores

  attachToResourceStore(store) {
    super.attachToResourceStore(store);

    if (store && !this.disabled) {
      store.on({
        name: 'resourceStore',
        changePreCommit: 'onResourceStoreChange',
        refreshPreCommit: 'onResourceStoreRefresh',
        thisObj: this
      });
    }
  }

  attachToDependencyStore(store) {
    const me = this;
    super.attachToDependencyStore(store); // When assigning a new store there is no point in keeping anything cached

    me.resetGridCache();
    me.resetBoundsCache();

    if (store && !me.disabled) {
      // TODO: This is rather doubtfull code, investigate it
      // used to store meta per scheduler on models, in case they are used in multiple schedulers
      store.metaMapId = me.client.id;
      store.on({
        name: 'dependencyStore',
        changePreCommit: 'onDependencyChange',
        thisObj: me
      });
    }
  }

  attachToAssignmentStore(store) {
    super.attachToAssignmentStore(store);

    if (store && !this.disabled) {
      store.on({
        name: 'assignmentStore',
        changePreCommit: 'onAssignmentChange',
        refreshPreCommit: 'onAssignmentRefresh',
        thisObj: this
      });
    }
  }

  attachToEventStore(store) {
    super.attachToEventStore(store);

    if (store && !this.disabled) {
      store.on({
        name: 'eventStore',
        changePreCommit: 'onEventChange',
        thisObj: this
      });
    }
  } //endregion
  //region Markers

  createMarkers() {
    const me = this,
          svg = me.client.svgCanvas,
          endMarker = me.endMarker = me.initMarkerElement('arrowEnd', '8', 'M0,0 L0,6 L9,3 z'); // Edge and IE11 do not support required svg 2.0 orient value

    if (BrowserHelper.isEdge || BrowserHelper.isIE11) {
      const startMarker = me.startMarker = me.initMarkerElement('arrowStart', '1', 'M0,3 L9,6 L9,0 z');
      svg.appendChild(startMarker);
    } else {
      endMarker.setAttribute('orient', 'auto-start-reverse');
    }

    svg.appendChild(endMarker);
  }
  /**
   * Creates SVG marker element (arrow) which is used for all dependency lines
   * @private
   * @param {String} id Id of the marker element
   * @param {String} refX
   * @param {String} arrowPath Path defining arrow
   */

  initMarkerElement(id, refX, arrowPath) {
    return DomHelper.createElement({
      id,
      tag: 'marker',
      ns: 'http://www.w3.org/2000/svg',
      markerHeight: 9,
      markerWidth: 9,
      refX,
      refY: 3,
      viewBox: '0 0 9 6',
      orient: 'auto',
      markerUnits: 'userSpaceOnUse',
      children: [{
        tag: 'path',
        ns: 'http://www.w3.org/2000/svg',
        d: arrowPath
      }]
    });
  }
  /**
   * Returns an array of functions used to alter path config when no path found.
   * It first tries to shrink arrow margins and secondly hides arrows entirely
   * @private
   * @returns {Function[]}
   */

  createLineDefAdjusters() {
    const scheduler = this.client;

    function shrinkArrowMargins(lineDef) {
      let adjusted = false;

      if (lineDef.startArrowMargin > scheduler.barMargin || lineDef.endArrowMargin > scheduler.barMargin) {
        lineDef.startArrowMargin = lineDef.endArrowMargin = scheduler.barMargin;
        adjusted = true;
      }

      return adjusted ? lineDef : adjusted;
    }

    function resetArrowMargins(lineDef) {
      let adjusted = false;

      if (lineDef.startArrowMargin > 0 || lineDef.endArrowMargin > 0) {
        lineDef.startArrowMargin = lineDef.endArrowMargin = 0;
        adjusted = true;
      }

      return adjusted ? lineDef : adjusted;
    }

    return [shrinkArrowMargins, resetArrowMargins];
  } //endregion
  //region Elements

  getElementForDependency(dependency, assignmentData = null) {
    return this.getElementsForDependency(dependency, assignmentData)[0];
  }

  getElementsForDependency(dependency, assignmentData = null) {
    // Selector targeting all instances of a dependency
    let selector = `[depId="${dependency.id}"]`; // Optionally narrow it down to a single instance (assignment)

    if (assignmentData) {
      selector += `[fromId="${assignmentData.from.id}"][toId="${assignmentData.to.id}"]`;
    }

    return Array.from(this.client.svgCanvas.querySelectorAll(selector));
  }
  /**
   * Returns the dependency record for a DOM element
   * @param {HTMLElement} element The dependency line element
   * @return {Scheduler.model.DependencyModel} The dependency record
   */

  resolveDependencyRecord(element) {
    // getAttribute return a string or null
    const id = typeof element === 'string' ? element : element.getAttribute('depId');
    return id ? this.dependencyStore.getById(id) : null;
  } //endregion
  //region Events
  //region Events that triggers redraw
  // Refresh dependencies when grid is refreshed

  onRefresh() {
    if (this.client.isEngineReady) {
      this.onProjectRefresh();
    }
  }

  onProjectRefresh() {
    const me = this;

    if (!me.client.refreshSuspended) {
      // Mainly for operations involving rows, adding or removing invalidates all resources
      if (me.refreshAllWhenReady) {
        me.resetGridCache();
        me.scheduleDraw(true);
        me.refreshAllWhenReady = false;
      } else if (me.toDrawOnProjectRefresh.size) {
        // Check if there is a cache exists. If it does - use it,
        // if not - create one after all records are drawn, this will cache all existing records
        const cache = me._dependencyGridCache;
        cache && me.toDrawOnProjectRefresh.forEach(dependency => {
          // Previously this code would draw added dependencies here, no matter if in view or not. To not have
          // to have the logic for determining whats in view or not here also, simply do a full draw below.
          // Old approach was especially costly when using CrudManager, which adds on load
          me.addToGridCache(dependency);
        });

        if (!cache) {
          me._thisIsAUsedExpression(me.dependencyGridCache);
        }

        me.toDrawOnProjectRefresh.clear();
        me.scheduleDraw();
      }
    }
  }

  onToggleNode() {
    // Need to repopulate grid cache
    this.resetGridCache(); // node toggled in tree, can affect resources both above and below, need to redraw all.

    this.scheduleDraw(true);
  }

  onViewportResize() {
    // More or fewer dependencies might be in view, but their locations has not changed so no need to invalidate
    // grid cache
    this.scheduleDraw(true);
  }
  /**
   * Flags for redrawing if a rows height has changed
   * @private
   */

  onTranslateRow({
    row
  }) {
    // a changetotalheight event is fired after translations, if a rowHeight change is detected here it will redraw
    // all dependencies
    if (row.lastTop >= 0 && row.top !== row.lastTop) {
      this.scheduleDraw(true);
    }
  }
  /**
   * Redraws all dependencies if a rows height changed, as detected in onTranslateRow
   * @private
   */

  onChangeTotalHeight() {
    if (this.client.isEngineReady) {
      // redraw all dependencies if the height changes. Could be caused by resource add/remove.
      // in reality not all deps needs to be redrawn, those fully above the row which changed height could be left
      // as is, but determining that would likely require more processing than redrawing
      this.scheduleDraw(true);
    }
  }
  /**
   * Draws dependencies on horizontal scroll
   * @private
   */

  onHorizontalScroll({
    subGrid
  }) {
    // ResizeMonitor triggers scroll during render, make sure we have been drawn some other way before redrawing
    if (this.isDrawn && subGrid === this.client.timeAxisSubGrid) {
      this.scheduleDraw(false);
    }
  }
  /**
   * Draws dependencies on vertical scroll
   * @private
   */

  onVerticalScroll() {
    // ResizeMonitor triggers scroll during render, make sure render is done
    if (this.isDrawn) {
      // Do not invalidate on scroll, if height changes it will be invalidated anyway
      this.scheduleDraw(false);
    }
  }

  onRowsRefresh() {
    this.scheduleDraw(true);
  }

  onResourceStoreChange({
    action
  }) {
    // bail out in case we are finalizing the propagation caused by project loading
    // we've already scheduled dependencies draw in the project "refresh" listener
    if (this.client.project.propagatingLoadChanges) {
      return;
    } // Adding/removing resources affects all dependencies that intersect or are fully below the resources.
    // Instead of determining which those are, do a full redraw when project have been recalculated

    if (resourceRefreshAllActions[action]) {
      this.refreshAllWhenReady = true;
    }
  }
  /**
   * Redraws dependencies when a row has changed
   * @private
   */

  onResourceStoreRefresh({
    action
  }) {
    // bail out in case we are finalizing the propagation caused by project loading
    // we've already scheduled dependencies draw in the project "refresh" listener
    if (this.client.project.propagatingLoadChanges) {
      return;
    }

    switch (action) {
      case 'sort':
      case 'filter':
      case 'batch':
        // Will need to recreate grid cache after sort, filter, and any unspecified
        // set of operations encapsulated by a batch, and redraw everything
        this.resetGridCache();
        this.scheduleDraw(true);
    }
  }
  /**
   * Redraws dependencies when a dependency has changed
   * @private
   */

  async onDependencyChange({
    action,
    record,
    records
  }) {
    const me = this,
          {
      client
    } = me; // bail out in case:
    // - client is not painted yet
    // - client has rendering suspended
    // - or we are finalizing the propagation caused by project loading
    // we've already scheduled dependencies draw in the project "refresh" listener

    if (!client.isPainted || client.suspendRendering || this.client.project.propagatingLoadChanges) {
      return;
    }

    switch (action) {
      case 'dataset':
        me.dependencyGridCache = {};
        me.toDrawOnProjectRefresh = new Set(records);
        return;

      case 'add':
        // Dependency added, draw it on project refresh. Cannot draw it earlier since adding it will have
        // invalidated the graph, making it impossible to determine coords
        records.forEach(record => me.toDrawOnProjectRefresh.add(record));
        return;

      case 'update':
        {
          // Not drawn, either out of view or not properly configured earlier
          if (!me.drawnDependencies.some(d => d.dependency === record)) {
            me.addToGridCache(record);
            me.scheduleDraw();
          } else {
            // Dependency updated. Might have changed source or target, redraw it completely
            return me.scheduleRefreshDependency(record);
          }

          break;
        }

      case 'remove':
        // dependencies removed, release elements and remove from cache
        records.forEach(dependency => {
          me.releaseDependency(dependency, true);
          me.removeFromCache(dependency); // Dependency might got scheduled to be refreshed before it is removed

          me.dependenciesToRefresh.delete(dependency);
        });
        client.trigger('dependenciesDrawn');
        return;
      // Removing all or filtering -> full redraw

      case 'removeall':
      case 'filter':
        me.resetGridCache(); // continue to schedule draw

        break;
    } // other changes (removeall, filter) trigger full redraw

    me.scheduleDraw(true);
  }
  /**
   * Redraws dependencies when an event has changed
   * @private
   */

  onEventChange({
    action,
    record,
    changes
  }) {
    // TODO : In Gantt this will be the same as the row store, only need one
    const me = this; // bail out in case we are finalizing the propagation caused by project loading
    // we've already scheduled dependencies draw in the project "refresh" listener

    if (me.client.project.propagatingLoadChanges) {
      return;
    }

    switch (action) {
      case 'filter':
        // filtering events, need to redraw all dependencies
        me.scheduleDraw(true);
        break;

      case 'update':
        // Ignore normalization of single field
        if (Object.keys(changes).length === 1 && (changes.startDate || changes.endDate || changes.duration)) {
          return;
        }

        if (!record.isEvent) {
          me.drawForTimeSpan(record, true);
        } else {
          // event updated, need to redraw dependencies for all events in its row since it might have changed
          // vertical position (from changing startDate, duration, name or anything if using custom sorter)
          const eventRecords = [record]; // Collect all events for all resources which have this event assigned... they might all be affected

          record.resources.forEach(resourceRecord => eventRecords.push(...resourceRecord.events));
          new Set(eventRecords).forEach(eventRecord => me.drawForTimeSpan(eventRecord, true));
        }

        break;

      case 'removeall':
        me.resetGridCache();
        me.scheduleDraw(true);
        break;
    } // adding event has no effect on dependencies, unless it changes row height. in which case it will be handled
    // by onTranslateRow().
    // updating an event might also change row height, handled the same way.
    // removing events will also remove dependencies, thus handled in onDependencyChange

  }

  onAssignmentRefresh({
    action
  }) {
    // bail out in case we are finalizing the propagation caused by project loading
    // we've already scheduled dependencies draw in the project "refresh" listener
    if (this.client.project.propagatingLoadChanges) {
      return;
    }

    if (action === 'dataset') {
      // Assigning using EventEdit replaces all assignments. Taking the easy way out, throwing cache away
      this.resetGridCache();
      this.scheduleDraw(true);
    }
  }

  onAssignmentChange({
    action,
    record,
    records,
    changes
  }) {
    const me = this; // bail out in case we are finalizing the propagation caused by project loading
    // we've already scheduled dependencies draw in the project "refresh" listener

    if (me.client.project.propagatingLoadChanges) {
      return;
    }

    if (record) {
      records = [record];
    } // Unusual case probably, not worth figuring out which was filtered out

    if (action === 'filter') {
      me.resetGridCache();
      me.scheduleDraw(true);
    }

    if (action === 'add' || action === 'remove' || action === 'update') {
      // Ignore normalization of event & resource
      if (action === 'update' && // Only care about the normalization case, where changes are exactly 2 = event + resource
      Object.keys(changes).length === 2 && // And those changed from not being models
      changes.event && changes.resource && !changes.event.isModel && !changes.resource.isModel) {
        return;
      }

      records.forEach(assignment => {
        const {
          assignmentStore,
          eventStore
        } = me.client,
              // Engine clears assignment.event on remove, have to look it up
        event = eventStore.getById(assignment.eventId); // Event might have been removed with the assignment, if not all its deps might need refreshing

        event && event.dependencies.forEach(dependency => {
          // New assignment added by other means than EventEdit
          if (action === 'add') {
            me.toDrawOnProjectRefresh.add(dependency); //me.scheduleRefreshDependency(dependency);
          } // Event unassigned, remove dep line (directly, nothing is redrawn so engine state can be ignored)
          else if (action === 'remove') {
              let assignments; // Removed source?

              if (dependency.fromEvent === event) {
                // Might point to a multi assigned event, need to remove all lines
                assignments = dependency.toEvent.assignments.map(to => ({
                  from: assignment,
                  to
                }));
              } // Nope, target
              else {
                  // Might point to a multi assigned event, need to remove all lines
                  assignments = dependency.fromEvent.assignments.map(from => ({
                    from,
                    to: assignment
                  }));
                }

              assignments.forEach(assignmentData => {
                me.releaseDependency(dependency, assignmentData);
                me.removeFromCache(dependency, assignmentData);
              });
            } // Single assignment updated, redraw lines for it
            else if (action === 'update') {
                me.getDependencyAssignmentsAsFromToArray({
                  dependency,
                  assignmentStore
                }).filter(a => a.from === record || a.to === record).forEach(assignmentData => {
                  me.scheduleRefreshDependency(dependency, assignmentData);
                });
              }
        });
      });
    }
  } // TODO: this function is called often, use cache to avoid searching in store

  getDependencyAssignmentsAsFromToArray({
    dependency,
    assignmentStore
  }) {
    const {
      fromEvent,
      toEvent
    } = dependency,
          assignments = [];

    if (fromEvent && toEvent) {
      const fromAssignments = this.getEventAssignments(fromEvent, assignmentStore),
            toAssignments = this.getEventAssignments(toEvent, assignmentStore);
      fromAssignments.forEach(from => {
        toAssignments.forEach(to => {
          assignments.push({
            from,
            to
          });
        });
      });
    }

    return assignments;
  }

  getEventAssignments(event, assignmentStore) {
    if (!event.isRemoved) {
      return event.assignments;
    } // If event is removed assignments might be either removed or planned to be removed
    else {
        const assignments = assignmentStore.removed.values.filter(assignment => assignment.event === event);

        for (const assignment of assignmentStore.assignmentsForRemoval) {
          if (assignment.event === event) {
            assignments.push(assignment);
          }
        }

        return assignments;
      }
  } //endregion

  onElementClick(event) {
    const me = this;

    if (event.target.matches('.' + me.baseCls)) {
      const dependency = DomDataStore.get(event.target).dependency,
            eventName = event.type === 'click' ? 'Click' : 'DblClick';
      /**
       * Fires on the owning Scheduler/Gantt when a click is registered on a dependency line.
       * @event dependencyClick
       * @param {Scheduler.view.Scheduler} source The scheduler
       * @param {Scheduler.model.DependencyModel} dependency
       * @param {MouseEvent} event
       */

      /**
       * Fires on the owning Scheduler/Gantt when a click is registered on a dependency line.
       * @event dependencyDblClick
       * @param {Scheduler.view.Scheduler} source The scheduler
       * @param {Scheduler.model.DependencyModel} dependency
       * @param {MouseEvent} event
       */

      me.client.trigger(`dependency${eventName}`, {
        dependency,
        event
      });
    }
  }

  onElementDblClick(event) {
    return this.onElementClick(event);
  }

  onElementMouseOver(event) {
    const me = this;

    if (event.target.matches('.' + me.baseCls)) {
      const dependency = DomDataStore.get(event.target).dependency;
      /**
       * Fires on the owning Scheduler/Gantt when the mouse moves over a dependency line.
       * @event dependencyMouseOver
       * @param {Scheduler.view.Scheduler} source The scheduler
       * @param {Scheduler.model.DependencyModel} dependency
       * @param {MouseEvent} event
       */

      me.client.trigger('dependencyMouseOver', {
        dependency,
        event
      });

      if (me.overCls) {
        me.highlight(dependency, me.overCls);
      }
    }
  }

  onElementMouseOut(event) {
    const me = this;

    if (event.target.matches('.' + me.baseCls)) {
      const dependency = DomDataStore.get(event.target).dependency;
      /**
       * Fires on the owning Scheduler/Gantt when the mouse moves out of a dependency line.
       * @event dependencyMouseOut
       * @param {Scheduler.view.Scheduler} source The scheduler
       * @param {Scheduler.model.DependencyModel} dependency
       * @param {MouseEvent} event
       */

      me.client.trigger('dependencyMouseOut', {
        dependency,
        event
      });
      if (me.overCls && !dependency.isRemoved) me.unhighlight(dependency);
    }
  } //endregion
  //region Highlight

  highlight(dependency, cls = this.overCls) {
    const elements = this.getElementsForDependency(dependency);
    elements.forEach(element => {
      element.classList.add(cls);
    });
    dependency.highlight(cls);
  }

  unhighlight(dependency, cls = this.overCls) {
    const elements = this.getElementsForDependency(dependency);
    elements.forEach(element => {
      element.classList.remove(cls);
    });
    dependency.unhighlight(cls);
  }

  highlightEventDependencies(timespan) {
    timespan.dependencies.forEach(dep => this.highlight(dep));
  }

  unhighlightEventDependencies(timespan) {
    timespan.dependencies.forEach(dep => this.unhighlight(dep));
  } //endregion
  //region Determining dependencies to draw
  // Neither resource can be hidden for a dependency to be considered visible

  isDependencyVisible(dependency, assignmentData = null) {
    const me = this,
          {
      assignmentStore,
      resourceStore,
      eventStore
    } = me.client,
          {
      fromEvent,
      toEvent
    } = dependency; // Bail out early in case source or target doesn't exist

    if (!(fromEvent && toEvent)) {
      return false;
    }

    let fromResource, toResource; // Using multi-assignment, resource obtained from assignment

    if (assignmentData) {
      const {
        from,
        to
      } = assignmentData;
      fromResource = from.resource;
      toResource = to.resource; // EventStore or AssignmentStore might be filtered, or assignment might opt out of dependency drawing

      if (from.drawDependencies === false || to.drawDependencies === false || eventStore.isFiltered && (!eventStore.includes(fromEvent) || !eventStore.includes(toEvent)) || assignmentStore.isFiltered && (!assignmentStore.includes(from) || !assignmentStore.includes(to))) {
        return false;
      }
    } // Not using assignments, resource obtained from event
    else {
        fromResource = fromEvent.resource;
        toResource = toEvent.resource;
      } // Verify these are real existing Resources and collapsed away (resource not existing in resource store)

    if (!resourceStore.isAvailable(fromResource) || !resourceStore.isAvailable(toResource)) {
      return false;
    }

    return fromEvent.isModel && !fromResource.instanceMeta(resourceStore).hidden && !toResource.instanceMeta(resourceStore).hidden;
  } // Get the bounding box for either the source or the target event

  getBox(dependency, source, assignmentData = null, roughly = false) {
    const eventRecord = this.getTimeSpanRecordFromDependency(dependency, source);
    let resourceRecord; // Multi-assignment, get resource from assignment

    if (assignmentData) {
      resourceRecord = assignmentData[source ? 'from' : 'to'].resource;
    } // Single, get resource from event
    else {
        // resourceRecord = this.dataApi.getEventResource({ event : eventRecord, resourceStore : this.resourceStore });
        resourceRecord = eventRecord.resource;
      }

    return this.client.getResourceEventBox(eventRecord, resourceRecord, true, roughly);
  } // Get source or target events resource

  getRowRecordFromDependency(dependency, source, assignmentData) {
    let rowRecord; // Multi-assigned, use assignments resource

    if (assignmentData) {
      rowRecord = assignmentData[source ? 'from' : 'to'].resource;
    } // Not multi-assigned, get events resource
    else {
        rowRecord = this.getTimeSpanRecordFromDependency(dependency, source).resource;
      }

    return rowRecord;
  } // Get from or to event

  getTimeSpanRecordFromDependency(dependency, from = true) {
    return dependency[`${from ? 'from' : 'to'}Event`];
  }

  getMetaId(assignmentData = null) {
    return assignmentData ? `${this.client.id}-ass${assignmentData.from.id}-ass${assignmentData.to.id}` : this.client.id;
  } // Gets the source and target events bounds and unions them to determine the dependency bounds

  getDependencyBounds(dependency, assignmentData, roughly = false) {
    const me = this,
          scheduler = me.client,
          ddr = dependency.getDateRange(); // quick bailout for hidden rows

    if (!scheduler.rowManager.rowCount || !me.isDependencyVisible(dependency, assignmentData)) {
      return null;
    } // quick bailout in case dependency dates and view dates do not intersect

    if (!(ddr && scheduler.timeAxis.count && DateHelper.intersectSpans(ddr.start, ddr.end, scheduler.startDate, scheduler.endDate))) {
      return null;
    }

    const metaId = me.getMetaId(assignmentData),
          instanceMeta = dependency.instanceMeta(metaId),
          startRectangle = me.getBox(dependency, true, assignmentData, roughly),
          endRectangle = me.getBox(dependency, false, assignmentData, roughly); // Can't draw dependency if either start or end is in collapsed row

    if (!startRectangle || !endRectangle) {
      return null;
    } // If we are forcing recalculation of dep bounds, or there are no calculated bounds for this dependency
    // or the calculated bounds were based on a "best guess", then recalculate the bounds.

    if (me._resetBoundsCache || !instanceMeta.bounds || !instanceMeta.bounds.layout) {
      const from = me.getTimeSpanRecordFromDependency(dependency, true),
            to = me.getTimeSpanRecordFromDependency(dependency, false),
            bounds = Rectangle.union(startRectangle, endRectangle);

      if (!scheduler.isGantt) {
        [[from, startRectangle, assignmentData.from], [to, endRectangle, assignmentData.to]].map(([record, rectangle, assignment]) => {
          // When using other milestoneLayoutMode than default milestones should be treated as normal events.
          // Milestones are zero width by default, so we must measure the milestone el's height
          // (or icon el width) and use that as the width. We cannot use the event's calculated height because
          // if there are labels, the milestone diamond will be smaller.
          // If the event doesn't have an element, then it's outside of the rendered block and the exact
          // width doesn't matter.
          if (scheduler.milestoneLayoutMode === 'default' && record.isMilestone) {
            if (!scheduler.milestoneWidth) {
              const milestoneElement = scheduler.getElementFromAssignmentRecord(assignment);

              if (milestoneElement) {
                scheduler.milestoneWidth = milestoneElement.offsetWidth; // The line above used to have this, but that does not allow mixin icons with non-icons.
                // With the new milestone styling, we seem fine without it anyway
                //record.iconCls ? milestoneElement.offsetWidth : parseInt(window.getComputedStyle(milestoneElement, ':before').fontSize);
              }
            } // If it could not be measured due to the event being outside of the rendered block
            // we have to use the calculated height.

            const milestoneWidth = scheduler.milestoneWidth || rectangle.height;
            rectangle.left -= milestoneWidth / 2;
            rectangle.right += milestoneWidth / 2;
          }
        });
      }

      instanceMeta.bounds = {
        bounds,
        startRectangle,
        endRectangle,
        // Cache whether both rectangles are based on the true layout
        // or a best guess approximation to be recalculated
        // next time through.
        layout: startRectangle.layout || endRectangle.layout
      };
    }

    return dependency.instanceMeta(metaId).bounds;
  } // Grid cache is a virtual grid holding info on which dependencies intersects its virtual cells.
  // Used to determine which dependencies should be considered for drawing, iterating over all dependencies each update
  // gets too costly when count increases (>10000).
  //
  // Illustration shows entire schedule area, dddd is a dependency line, vvv is viewport, xxx virtual cell border:
  //
  // ----------------------------------
  // |     vvvvvxvvvvv                |
  // |     v    x    v                |
  // |     v d  x    v                |
  // |     v d  x    v                |
  // |xxxxxvxdxxx    v                |
  // |     vvdvvxvvvvv                |
  // |       d  x                     |
  // |       d  x                     |
  // |       d  x                     |
  // |xxxxxxxdxxxxxxx                 |
  // |       d  x                     |
  // |       d  x                     |
  // |          x                     |
  // |          x                     |
  // |xxxxxxxxxxxxxxx                 |
  // ----------------------------------
  //
  // The dependency crosses three virtual grid cells [0,0], [0,1] and [0,2]. Stored in a map in cache:
  // {
  //    0 : {
  //      0 : [ d, ... ],
  //      1 : [ d, ... ],
  //      2 : [ d, ... ]
  //    }
  // }
  //
  // Viewport crosses four virtual grid cells [0,0], [1,0], [0,1], [1,1]. Those cells are checked in the cached map to
  // find out which rows should be considered for drawing.
  //
  // This approach minimizes the amount of iteration needed

  get dependencyGridCache() {
    const me = this;

    if (!me._dependencyGridCache) {
      me._dependencyGridCache = {};
      me.dependencyStore.forEach(dependency => me.addToGridCache(dependency));
    }

    return me._dependencyGridCache;
  }

  getIteratableDependencyAssignments(dependency) {
    return this.assignmentStore ? this.getDependencyAssignmentsAsFromToArray({
      dependency,
      assignmentStore: this.assignmentStore
    }) : [null]; // On purpose, to be iterable
  }

  addToGridCache(dependency) {
    const me = this,
          {
      client
    } = me,
          {
      store
    } = client,
          {
      dependencyGridCache,
      cacheGridSize
    } = me,
          assignmentDataArray = me.getIteratableDependencyAssignments(dependency),
          axisStartMS = client.startDate.getTime(),
          axisEndMS = client.endDate.getTime(),
          cacheGridSizeTicksMS = DateHelper.asMilliseconds(client.timeAxis.unit) * cacheGridSize.ticks;
    assignmentDataArray.forEach(assignmentData => {
      const metaId = me.getMetaId(assignmentData),
            meta = dependency.instanceMeta(metaId),
            metaGridCache = meta.gridCache = [],
            // Using index vertically rather than y for reliability with variable row height
      fromIndex = store.indexOf(me.getRowRecordFromDependency(dependency, true, assignmentData)),
            toIndex = store.indexOf(me.getRowRecordFromDependency(dependency, false, assignmentData)),
            // Using dates horizontally rather than x for performance reasons
      dates = dependency.getDateRange(),
            startDateMS = dates === null || dates === void 0 ? void 0 : dates.start.getTime(),
            endDateMS = dates === null || dates === void 0 ? void 0 : dates.end.getTime();

      if (fromIndex !== -1 && toIndex !== -1 && dates && endDateMS > axisStartMS && startDateMS < axisEndMS) {
        const topIndex = Math.min(fromIndex, toIndex),
              bottomIndex = Math.max(fromIndex, toIndex),
              boxTop = Math.floor(topIndex / cacheGridSize.index),
              boxBottom = Math.floor(bottomIndex / cacheGridSize.index),
              boxLeft = Math.floor(Math.max(startDateMS - axisStartMS, 0) / cacheGridSizeTicksMS),
              boxRight = Math.floor(Math.max(endDateMS - axisStartMS, 0) / cacheGridSizeTicksMS),
              key = `${dependency.id}.-.${metaId}`;
        let cacheX, cacheY, cacheYMap, x, y; // Store the dependency in the virtual cells which it intersects

        for (x = boxLeft; x <= boxRight; x++) {
          cacheX = dependencyGridCache[x] || (dependencyGridCache[x] = {});

          for (y = boxTop; y <= boxBottom; y++) {
            cacheY = cacheX[y] || (cacheX[y] = []);
            cacheYMap = cacheY.cached || (cacheY.cached = {}); // Only push to the cache if it is not already there

            if (!cacheYMap[key]) {
              cacheYMap[key] = true;
              cacheY.push({
                dependency,
                assignmentData,
                metaId
              });
            }

            metaGridCache.push([x, y]);
          }
        }
      }
    });
  }

  removeFromCache(dependency, assignmentData = null) {
    const me = this,
          {
      _dependencyGridCache
    } = me;
    let assignments; // Some short-cut case

    if (assignmentData) {
      assignments = [assignmentData];
    } else {
      assignments = this.getIteratableDependencyAssignments(dependency);
    }

    assignments.forEach(assignmentData => {
      var _meta$gridCache;

      const metaId = me.getMetaId(assignmentData),
            meta = dependency.instanceMeta(metaId),
            key = `${dependency.id}.-.${metaId}`;
      _dependencyGridCache && ((_meta$gridCache = meta.gridCache) === null || _meta$gridCache === void 0 ? void 0 : _meta$gridCache.forEach(([x, y]) => {
        var _dependencyGridCache$;

        if ((_dependencyGridCache$ = _dependencyGridCache[x]) !== null && _dependencyGridCache$ !== void 0 && _dependencyGridCache$.hasOwnProperty(y)) {
          const entries = me._dependencyGridCache[x][y];

          if (entries !== null && entries !== void 0 && entries.cached[key]) {
            const index = me.findDependencyIndex(entries, dependency, assignmentData);
            delete entries.cached[key]; // Cannot use ArrayHelper#remove since it cannot compare deeply

            if (index > -1) {
              entries.splice(index, 1);
            }
          }
        }
      }));
      meta.bounds = null;
      meta.gridCache = null;
    });
  }

  set dependencyGridCache(cache) {
    this._dependencyGridCache = cache;
  } // Reset cached bounds, not grid cache since it is expensive to create. It is so coarse anyway so should be fine
  // with most changes, except for sorting and similar. Reset on demand instead

  resetBoundsCache() {
    // Not actually resetting here, would just be costly to iterate and reset per dependency, instead flagging to
    // force cached value to be updated
    this._resetBoundsCache = true;
  } // In some cases we do need to reset cache, like when time axis is reconfigured

  resetGridCache() {
    this.dependencyGridCache = null;
  } //endregion
  //region Draw & render
  //region Lines

  /**
   * Generates `otherBoxes` config for rectangular path finder, which push dependency line to the row boundary.
   * It should be enough to return single box with top/bottom taken from row top/bottom and left/right taken from source
   * box, extended by start arrow margin to both sides.
   * @param {Core.helper.util.Rectangle} box Box for which other boxes are requried
   * @param {String} side Side of the box where line starts
   * @returns {Object[]}
   * @private
   */

  generateBoundaryBoxes(box, side) {
    const boxes = []; // We need two boxes for the bottom edge, because otherwise path cannot be found. Ideally that shouldn't be
    // necessary. Other solution would be to adjust bottom by -1px, but that would make some dependency lines to take
    // 1px different path on a row boundary, which doesn't look nice (but slightly more performant)

    if (side === 'bottom') {
      boxes.push({
        start: box.left,
        end: box.left + box.width / 2,
        top: box.rowTop,
        bottom: box.rowBottom
      }, {
        start: box.left + box.width / 2,
        end: box.right,
        top: box.rowTop,
        bottom: box.rowBottom
      });
    } else {
      boxes.push({
        start: box.left - this.pathFinder.startArrowMargin,
        end: box.right + this.pathFinder.startArrowMargin,
        top: box.rowTop,
        bottom: box.rowBottom
      });
    }

    return boxes;
  }

  getDependencyStartSide(dependency) {
    const me = this,
          source = me.getTimeSpanRecordFromDependency(dependency, true),
          type = dependency.type;
    let startSide = dependency.fromSide;

    if (!startSide) {
      switch (true) {
        case type === DependencyModel.Type.StartToEnd:
          startSide = me.getConnectorStartSide(source);
          break;

        case type === DependencyModel.Type.StartToStart:
          startSide = me.getConnectorStartSide(source);
          break;

        case type === DependencyModel.Type.EndToStart:
          startSide = me.getConnectorEndSide(source);
          break;

        case type === DependencyModel.Type.EndToEnd:
          startSide = me.getConnectorEndSide(source);
          break;

        default:
          throw new Error('Invalid dependency type: ' + type);
      }
    }

    return startSide;
  }

  getDependencyEndSide(dependency) {
    const me = this,
          target = me.getTimeSpanRecordFromDependency(dependency, false),
          type = dependency.type;
    let endSide = dependency.toSide; // Fallback to view trait if dependency end side is not given /*or can be obtained from type*/

    if (!endSide) {
      switch (true) {
        case type === DependencyModel.Type.StartToEnd:
          endSide = me.getConnectorEndSide(target);
          break;

        case type === DependencyModel.Type.StartToStart:
          endSide = me.getConnectorStartSide(target);
          break;

        case type === DependencyModel.Type.EndToStart:
          endSide = me.getConnectorStartSide(target);
          break;

        case type === DependencyModel.Type.EndToEnd:
          endSide = me.getConnectorEndSide(target);
          break;

        default:
          throw new Error('Invalid dependency type: ' + type);
      }
    }

    return endSide;
  }

  prepareLineDef(dependency, dependencyDrawData, assignmentData = null) {
    const me = this,
          startSide = me.getDependencyStartSide(dependency),
          endSide = me.getDependencyEndSide(dependency),
          {
      startRectangle,
      endRectangle
    } = dependencyDrawData,
          intraRowDependency = startRectangle.rowTop != null && startRectangle.rowTop !== endRectangle.rowTop,
          otherBoxes = []; // Only add otherBoxes if there is a rowTop and those are not equal - which means assignments are is different
    // resources

    if (intraRowDependency) {
      otherBoxes.push(...me.generateBoundaryBoxes(startRectangle, startSide));
    }

    let {
      startArrowMargin,
      verticalMargin
    } = me.pathFinder; // Do not change start arrow margin in case dependency is bidirectional

    if (!dependency.bidirectional) {
      if (/(top|bottom)/.test(startSide)) {
        startArrowMargin = me.client.barMargin / 2;
      }

      verticalMargin = me.client.barMargin / 2;
    }

    return {
      startBox: {
        start: startRectangle.x,
        end: startRectangle.right,
        top: startRectangle.y,
        bottom: startRectangle.bottom
      },
      endBox: {
        start: endRectangle.x,
        end: endRectangle.right,
        top: endRectangle.y,
        bottom: endRectangle.bottom
      },
      otherBoxes,
      startArrowMargin,
      verticalMargin,
      otherVerticalMargin: 0,
      otherHorizontalMargin: 0,
      startSide: startSide,
      endSide: endSide
    };
  } // Draws a single SVG line that represents the dependency

  drawLine(canvas, dependency, points, assignmentData = null, cache = true) {
    const scheduler = this.client,
          metaId = this.getMetaId(assignmentData); // Reuse existing element if possible

    let line = dependency.instanceMeta(metaId).lineElement;

    if (!line || !cache) {
      line = document.createElementNS('http://www.w3.org/2000/svg', 'polyline');

      if (cache) {
        dependency.instanceMeta(metaId).lineElement = line;
      }

      line.setAttribute('depId', dependency.id);

      if (assignmentData) {
        line.setAttribute('fromId', assignmentData.from.id);
        line.setAttribute('toId', assignmentData.to.id);
      }

      canvas.appendChild(line);
    } // TODO: Use DomHelper.syncClassList
    // className is SVGAnimatedString for svg elements, reading attribute instead

    line.classList.length && line.classList.remove.apply(line.classList, line.getAttribute('class').split(' '));
    line.classList.add(this.baseCls);

    if (dependency.cls) {
      line.classList.add(dependency.cls);
    }

    if (dependency.bidirectional) {
      line.classList.add('b-sch-bidirectional-line');
    }

    if (dependency.highlighted) {
      line.classList.add(...dependency.highlighted.split(' '));
    }

    if (BrowserHelper.isIE11) {
      const ddr = dependency.getDateRange(true),
            viewStart = scheduler.startDate;

      if (ddr.start < viewStart) {
        line.classList.add('b-no-start-marker');
      }

      if (ddr.end < viewStart) {
        line.classList.add('b-no-end-marker');
      }
    }

    line.setAttribute('points', !points ? '' : points.map((p, i) => i !== points.length - 1 ? `${p.x1},${p.y1}` : `${p.x1},${p.y1} ${p.x2},${p.y2}`).join(' '));
    DomDataStore.set(line, {
      dependency
    });
    return line;
  } //endregion

  /**
   * Re-caches and redraws a dependency, for all assignments.
   * @param {Scheduler.model.DependencyModel} dependency Dependency to refresh
   */

  refreshDependency(dependency) {
    const me = this,
          assignments = me.getIteratableDependencyAssignments(dependency); // Release dependency element, for all assignments if using AssignmentStore

    me.releaseDependency(dependency, assignments[0] !== null); // Remove it from grid & bounds cache

    me.removeFromCache(dependency); // Re-add it to grid cache

    me.addToGridCache(dependency); // Draw all assignments

    assignments.forEach(assignmentData => me.drawDependency(dependency, null, assignmentData));
  }
  /**
   * Re-caches and redraws a dependency for given assignment.
   * @param {Scheduler.model.DependencyModel} dependency Dependency to refresh
   * @param {Object} assignmentData
   * @param {Scheduler.model.AssignmentModel} assignmentData.from Source assignment
   * @param {Scheduler.model.AssignmentModel} assignmentData.to Target assignment
   * @private
   */

  refreshDependencyAssignment(dependency, assignmentData) {
    const me = this; // In case it was assigned to something not in view/timeline, release the line

    me.releaseDependency(dependency, assignmentData); // Update cache to only contain whats left of it

    me.removeFromCache(dependency, assignmentData);
    me.addToGridCache(dependency); // Draw lines

    me.drawDependency(dependency, null, assignmentData);
  }
  /**
   * Stores all dependencies/assignments that were requested to refresh and schedules repaint on next animation frame
   * @param {Scheduler.model.DependencyModel} dependency Dependency model to refresh
   * @param {Object} [assignmentData] Assignment data
   * @param {Scheduler.model.AssignmentModel} [assignmentData.from] Source assignment
   * @param {Scheduler.model.AssignmentModel} [assignmentData.to] Target assignment
   * @private
   */

  scheduleRefreshDependency(dependency, assignmentData = null) {
    const me = this,
          map = me.dependenciesToRefresh; // Ignore undrawn dependencies, they cannot be refreshed

    if (!me.drawnDependencies.find(d => d.dependency === dependency)) {
      // Nevertheless update the cache, we might land here from method which updates dependency for modified event
      me.removeFromCache(dependency);
      me.addToGridCache(dependency);
      return;
    } // If this method was called once without assignment data - all lines related should be repainted

    if (!assignmentData) {
      map.set(dependency, true);
    } else if (map.has(dependency)) {
      if (map.get(dependency) !== true) {
        map.get(dependency).add(assignmentData);
      }
    } else {
      map.set(dependency, new Set([assignmentData]));
    }

    if (map.size === 1) {
      me.requestAnimationFrame(() => me.refreshDependencyOnFrame());
    }
  }
  /**
   * Implement on subclass when a certain sort order is needed
   * @returns {Map} Dependencies
   * @private
   */

  getSortedDependenciesToRefresh() {
    return this.dependenciesToRefresh;
  }
  /**
   * Repaints scheduled dependencies/assignments
   * @private
   */

  refreshDependencyOnFrame() {
    const me = this,
          map = me.getSortedDependenciesToRefresh();

    if (me.client.isPainted) {
      // First clear cache and release dependencies. This will modify DOM
      map.forEach((assignments, dependency) => {
        if (assignments === true) {
          const assignments = me.getIteratableDependencyAssignments(dependency); // Release dependency element, for all assignments if using AssignmentStore

          me.releaseDependency(dependency, assignments[0] !== null); // Remove it from grid & bounds cache

          me.removeFromCache(dependency);
        } else {
          assignments.forEach(assignment => {
            // In case it was assigned to something not in view/timeline, release the line
            me.releaseDependency(dependency, assignment); // Update cache to only contain whats left of it

            me.removeFromCache(dependency, assignment);
          });
        }
      }); // Then fill cache before drawing dependencies. This will read the DOM forcing reflow

      map.forEach((assignments, dependency) => {
        // Re-add it to grid cache
        me.addToGridCache(dependency);
      }); // Finally append elements to the DOM

      map.forEach((assignments, dependency) => {
        if (assignments === true) {
          assignments = me.getIteratableDependencyAssignments(dependency);
        }

        assignments.forEach(assignmentData => {
          me.drawDependency(dependency, null, assignmentData);
        });
      }); // Cannot clear `map`, since it is a sorted copy of the actual map.

      me.dependenciesToRefresh.clear();
      me.client.trigger('dependenciesDrawn', {
        partial: true
      });
    }
  }
  /**
   * Draws a single dependency (for a single assignment if using multiple), if in view.
   * @param {Scheduler.model.DependencyModel} dependency Dependency to draw
   */

  drawDependency(dependency, drawData = null, assignmentData = null, canvas = this.client.svgCanvas, cache = true) {
    const me = this,
          {
      drawnDependencies,
      oldDrawnDependencies
    } = me,
          // Determines if a dependency should be draw, and if so returns the coordinates of its events
    dependencyDrawData = drawData || me.getDependencyBounds(dependency, assignmentData);

    if (!me.disabled && dependencyDrawData) {
      // Build line defs
      const lineDef = me.prepareLineDef(dependency, dependencyDrawData),
            lines = me.pathFinder.findPath(lineDef, me.lineDefAdjusters);
      me.drawLine(canvas, dependency, lines, assignmentData, cache); // Do not push dependency to drawn deps if this is a temporary render
      // Cannot use ArrayHelper#include since object wont be the same, only its contents

      if (cache && !this.includesDependency(drawnDependencies, dependency, assignmentData)) {
        drawnDependencies.push({
          dependency,
          assignmentData
        });
      }
    } // Remove from oldDrawnDeps, to not have element removed. Cannot use ArrayHelper#remove as stated above

    const oldIndex = this.findDependencyIndex(oldDrawnDependencies, dependency, assignmentData);

    if (oldIndex >= 0) {
      oldDrawnDependencies.splice(oldIndex, 1);
    }
  }
  /**
   * Draws multiple dependencies, called from drawForEvent() or drawFromTask().
   * @private
   */

  drawForTimeSpan(timeSpanRecord, async = false) {
    const me = this; // If the client is doing an animated update, we must update at end.
    // That will be asynchronous relative to now, so do not pass on the async flag.

    if (me.client.isAnimating) {
      me.client.on({
        transitionend() {
          // Because of asyncness record might be removed during the transition
          !timeSpanRecord.isRemoved && me.drawForTimeSpan(timeSpanRecord, true);
        },

        once: true
      });
    } // Otherwise, schedule the draw for the next frame.
    else {
        me.dependencyStore.getTimeSpanDependencies(timeSpanRecord).forEach(dependency => {
          if (async) {
            me.scheduleRefreshDependency(dependency);
          } else {
            me.refreshDependency(dependency);
          }
        });
      }
  }
  /**
   * Draws all dependencies for the specified event.
   */

  drawForEvent(eventRecord) {
    this.drawForTimeSpan(eventRecord);
  } // Redraw all dependencies for a particular eventRecord, using its current element instead of calculating a box
  // Used to do live redraw while resizing or dragging events

  updateDependenciesForTimeSpan(timeSpanRecord, element, newResource = null) {
    const me = this,
          eventRecord = timeSpanRecord.isAssignment ? timeSpanRecord.event : timeSpanRecord,
          deps = me.dependencyStore.getTimeSpanDependencies(eventRecord),
          metaId = me.getMetaId(),
          scheduler = me.client,
          originalTaskRect = Rectangle.from(element, scheduler.timeAxisSubGridElement),
          row = newResource && scheduler.getRowFor(newResource);
    let bounds;
    deps.forEach(dep => {
      const assignments = this.getIteratableDependencyAssignments(dep);
      assignments.forEach(assignmentData => {
        const taskRect = originalTaskRect.clone();
        let startRectangle, endRectangle;

        if (row) {
          taskRect.rowTop = row.top;
          taskRect.rowBottom = row.bottom;
        } // If dragging one multi assigned event the others wont move until it is dropped. Prevent their dep
        // lines from updating by bailing out

        if (assignmentData && assignmentData.from !== timeSpanRecord && assignmentData.to !== timeSpanRecord) {
          return;
        } // Bail out if dependency is not visible (other end might be collapsed)

        if (!me.isDependencyVisible(dep, assignmentData)) {
          return;
        }

        if (me.getTimeSpanRecordFromDependency(dep, true) === eventRecord) {
          startRectangle = taskRect; // try to look into dependency cache first

          if (bounds = dep.instanceMeta(metaId).bounds) {
            endRectangle = bounds.endRectangle;
          } else {
            endRectangle = me.getBox(dep, false, assignmentData);
          }
        } else {
          // try to look into dependency cache first
          if (bounds = dep.instanceMeta(metaId).bounds) {
            startRectangle = bounds.startRectangle;
          } else {
            startRectangle = me.getBox(dep, true, assignmentData);
          }

          endRectangle = taskRect;
        }

        if (startRectangle && endRectangle) {
          me.drawDependency(dep, {
            startRectangle,
            endRectangle
          }, assignmentData);
        }
      });
    });
  }

  scheduleDraw(relayout = false) {
    const me = this;

    if (me.disabled) {
      return;
    } // There may be number of concurrent calls to this method, we need to reset cache if at least
    // once it was called with relayout = true

    if (relayout) {
      me.resetBoundsCache();
    } // If the scheduler/gantt is doing an animated update, schedule the draw
    // for when that's done so that we get correct element boxes.

    if (me.client.isAnimating) {
      if (!me.clientTransitionRemover) {
        me.clientTransitionRemover = me.client.on({
          transitionend() {
            me.clientTransitionRemover();
            me.clientTransitionRemover = null;
            me.draw();
          },

          once: true
        });
      }
    } // Otherwise, schedule the draw for the next frame.
    else {
        me.doScheduleDraw();
      }
  }
  /**
   * Draws all dependencies that overlap the current viewport
   */

  draw(reLayout = false) {
    const me = this,
          scheduler = me.client; // Early bailout if we get here before we have any deps or rows rendered

    if (!me.oldDrawnDependencies && !me.dependencyStore.count || !scheduler.isPainted) {
      return;
    } // if animation is in progress, schedule drawing and skip current one

    if (scheduler.isAnimating) {
      scheduler.on({
        transitionend() {
          me.scheduleDraw(true);
        },

        once: true
      });
      return;
    } // viewBox is the bounds of the current viewport, used to determine which dependencies to draw

    const viewBox = scheduler.timeAxisSubGrid.viewRectangle;

    if (reLayout) {
      me.resetBoundsCache();
    } // If there is no visible area for dependencies to display, we should stop redrawing and keep info about current dependencies.
    // Next time when visible area appears all dependencies will be redrawn.

    if (!viewBox.width || !viewBox.height) {
      return;
    }

    me.oldDrawnDependencies = me.drawnDependencies;
    me.drawnDependencies = []; // expand viewBox with buffer size

    viewBox.inflate(me.bufferSize);
    const {
      startDate,
      visibleDateRange
    } = scheduler; // Do not draw if no rows or no ticks

    if (!me.disabled && !scheduler.suspendRendering && scheduler.store.count && scheduler.rowManager.rowCount && startDate && visibleDateRange.startDate && visibleDateRange.endDate) {
      // dependencies might start rendering before the 1st propagation which ends up having an empty dependencies cache
      // and thus never rendered dependencies
      if (!scheduler.project.isInitialCommitPerformed) {
        // Restore drawn dependencies, otherwise the unused ones will not be released next time we draw
        me.drawnDependencies = me.oldDrawnDependencies;
        me.refreshAllWhenReady = true;
        return;
      }

      const consideredDependencies = {},
            {
        dependencyGridCache,
        cacheGridSize
      } = me,
            cacheGridSizeTicksMS = DateHelper.asMilliseconds(scheduler.timeAxis.unit) * cacheGridSize.ticks,
            visibleStartMS = visibleDateRange.startDate.getTime() - startDate.getTime(),
            visibleEndMS = visibleDateRange.endDate.getTime() - startDate.getTime(),
            viewLeft = Math.floor(visibleStartMS / cacheGridSizeTicksMS),
            viewRight = Math.floor(visibleEndMS / cacheGridSizeTicksMS),
            topIndex = Math.floor(scheduler.rowManager.topRow.dataIndex / cacheGridSize.index),
            bottomIndex = Math.floor(scheduler.rowManager.bottomRow.dataIndex / cacheGridSize.index),
            dependenciesToDraw = [];
      let x, rowIndex, i; // Iterate over virtual dependency grid cells, pushing each dependency that intersects that cell

      for (x = viewLeft; x <= viewRight; x++) {
        for (rowIndex = topIndex; rowIndex <= bottomIndex; rowIndex++) {
          const cacheX = dependencyGridCache[x],
                deps = cacheX === null || cacheX === void 0 ? void 0 : cacheX[rowIndex];

          for (i = 0; deps && i < deps.length; i++) {
            const {
              dependency,
              assignmentData,
              metaId
            } = deps[i],
                  // Unique id for dependency combined with assignment
            flagId = dependency.id + '-' + metaId;

            if (!consideredDependencies[flagId]) {
              // Only draw those actually in view
              const bounds = me.getDependencyBounds(dependency, assignmentData); // TODO: get rid of this export-specific flag

              if (bounds !== null && bounds !== void 0 && bounds.bounds.intersect(viewBox, true, true) || scheduler.ignoreViewBox) {
                dependenciesToDraw.push([dependency, bounds, assignmentData]);
              }

              consideredDependencies[flagId] = true;
            }
          }
        }
      } // Append dependencies to the DOM only after all have been calculated

      dependenciesToDraw.forEach(([dependency, bounds, assignmentData]) => {
        me.drawDependency(dependency, bounds, assignmentData);
      });
    } // Stop forcing recalculation of bounds

    me._resetBoundsCache = false; // Release elements for any dependencies that wasn't drawn

    me.oldDrawnDependencies.forEach(data => me.releaseDependency(data.dependency, data.assignmentData));
    scheduler.trigger('dependenciesDrawn');
    me.isDrawn = true;
  }
  /**
   * Release a dependency that is determined to be no longer visible
   * @param {Scheduler.model.DependencyModel} dependency
   */

  releaseDependency(dependency, assignmentData = null) {
    // Remove for all assignments (related to this client, store might be shared)
    if (assignmentData === true) {
      const clientDataKeys = Object.keys(dependency.meta.map || {}).filter(key => key.startsWith(this.client.id));
      clientDataKeys.forEach(key => {
        const data = dependency.meta.map[key];

        if (data.lineElement) {
          data.lineElement.remove();
          data.lineElement = null;
        }
      });
    } // Remove specific
    else {
        const metaId = this.getMetaId(assignmentData),
              lineElement = dependency.instanceMeta(metaId).lineElement;

        if (lineElement) {
          dependency.instanceMeta(metaId).lineElement = null; // Not reusing elements for other lines currently

          lineElement.remove();
        }
      }
  }

  render() {
    const me = this,
          scheduler = me.client;

    if (me.showTooltip) {
      me.tooltip = me.createTooltip();
    }

    scheduler.timeAxis.on({
      endreconfigure: 'resetGridCache',
      thisObj: me
    });
    scheduler.rowManager.on({
      refresh: 'onRowsRefresh',
      // redraws dependencies after zoom
      changetotalheight: 'onChangeTotalHeight',
      // redrawn dependencies after group collapse
      thisObj: me
    }); // dependencies are drawn on scroll, both horizontal and vertical

    scheduler.on({
      horizontalscroll: 'onHorizontalScroll',
      svgcanvascreated: 'createMarkers',
      togglenode: 'onToggleNode',
      scroll: 'onVerticalScroll',
      timelineviewportresize: 'onViewportResize',
      thisObj: me
    });

    if (me.highlightDependenciesOnEventHover) {
      scheduler.on(scheduler.scheduledEventName + 'MouseEnter', params => me.highlightEventDependencies(params.eventRecord || params.taskRecord));
      scheduler.on(scheduler.scheduledEventName + 'MouseLeave', params => me.unhighlightEventDependencies(params.eventRecord || params.taskRecord));
    }
  }

  renderContents() {
    this.draw();
  } //endregion
  //region Connector sides

  /**
   * Gets displaying item start side
   *
   * @param {Scheduler.model.TimeSpan} timeSpanRecord
   * @return {String} 'left' / 'right' / 'top' / 'bottom'
   */

  getConnectorStartSide(timeSpanRecord) {
    return this.client.currentOrientation.getConnectorStartSide(timeSpanRecord);
  }
  /**
   * Gets displaying item end side
   *
   * @param {Scheduler.model.TimeSpan} timeSpanRecord
   * @return {String} 'left' / 'right' / 'top' / 'bottom'
   */

  getConnectorEndSide(timeSpanRecord) {
    return this.client.currentOrientation.getConnectorEndSide(timeSpanRecord);
  } //endregion
  //region Tooltip

  createTooltip() {
    const me = this,
          scheduler = me.client;
    return new Tooltip(ObjectHelper.assign({
      align: 'b-t',
      id: `${scheduler.id}-dependency-tip`,
      //TODO: need some way better to specify this. maybe each feature should be queried?
      forSelector: `.b-timelinebase:not(.b-eventeditor-editing):not(.b-resizing-event):not(.b-dragcreating):not(.b-dragging-event):not(.b-creating-dependency) .${me.baseCls}`,
      clippedBy: [scheduler.timeAxisSubGridElement, scheduler.bodyContainer],
      forElement: scheduler.timeAxisSubGridElement,
      showOnHover: true,
      hoverDelay: 0,
      hideDelay: 0,
      anchorToTarget: false,
      textContent: false,
      // Skip max-width setting
      trackMouse: false,
      getHtml: me.getHoverTipHtml.bind(me)
    }, me.tooltip || {}));
  }
  /**
   * Generates html for the tooltip shown when hovering a dependency
   * @param {Object} tooltipConfig
   * @returns {string} Html to display in the tooltip
   * @private
   */

  getHoverTipHtml({
    activeTarget
  }) {
    const me = this,
          dependency = me.resolveDependencyRecord(activeTarget),
          {
      fromEvent,
      toEvent
    } = dependency;
    return TemplateHelper.tpl`
             <table class="b-sch-dependency-tooltip">
                <tr>
                    <td>${me.L('L{from}')}: </td>
                    <td>${fromEvent.name}</td>
                    <td>
                        <div class="b-sch-box b-${fromBoxSide[dependency.type]}"></div>
                    </td>
                </tr>
                <tr>
                    <td>${me.L('L{to}')}: </td>
                    <td>${toEvent.name}</td>
                    <td><div class="b-sch-box b-${toBoxSide[dependency.type]}"></div></td>
                </tr>
            </table>
        `;
  } //endregion

}
Dependencies._$name = 'Dependencies';
GridFeatureManager.registerFeature(Dependencies, false, 'Scheduler');
GridFeatureManager.registerFeature(Dependencies, true, 'SchedulerPro'); // region polyfills
// from https://github.com/eligrey/classList.js

if (document.createElementNS && !('classList' in document.createElementNS('http://www.w3.org/2000/svg', 'g'))) {
  (function (view) {
    if (!('Element' in view)) return;

    var classListProp = 'classList',
        protoProp = 'prototype',
        elemCtrProto = view.Element[protoProp],
        objCtr = Object,
        strTrim = String[protoProp].trim || function () {
      return this.replace(/^\s+|\s+$/g, '');
    },
        arrIndexOf = Array[protoProp].indexOf || function (item) {
      for (var i = 0, len = this.length; i < len; i++) {
        if (i in this && this[i] === item) {
          return i;
        }
      }

      return -1;
    },
        // Vendors: please allow content code to instantiate DOMExceptions
    DOMEx = function (type, message) {
      this.name = type;
      this.code = DOMException[type]; // eslint-disable-line no-undef

      this.message = message;
    },
        checkTokenAndGetIndex = function (classList, token) {
      if (token === '') {
        throw new DOMEx('SYNTAX_ERR', 'The token must not be empty.');
      }

      if (/\s/.test(token)) {
        throw new DOMEx('INVALID_CHARACTER_ERR', 'The token must not contain space characters.');
      }

      return arrIndexOf.call(classList, token);
    },
        ClassList = function (elem) {
      var trimmedClasses = strTrim.call(elem.getAttribute('class') || ''),
          classes = trimmedClasses ? trimmedClasses.split(/\s+/) : [];

      for (var i = 0, len = classes.length; i < len; i++) {
        this.push(classes[i]);
      }

      this._updateClassName = function () {
        elem.setAttribute('class', this.toString());
      };
    },
        classListProto = ClassList[protoProp] = [],
        classListGetter = function () {
      return new ClassList(this);
    }; // Most DOMException implementations don't allow calling DOMException's toString()
    // on non-DOMExceptions. Error's toString() is sufficient here.

    DOMEx[protoProp] = Error[protoProp];

    classListProto.item = function (i) {
      return this[i] || null;
    };

    classListProto.contains = function (token) {
      return ~checkTokenAndGetIndex(this, token + '');
    };

    classListProto.add = function () {
      var tokens = arguments,
          i = 0,
          l = tokens.length,
          token,
          updated = false;

      do {
        token = tokens[i] + '';

        if (!~checkTokenAndGetIndex(this, token)) {
          this.push(token);
          updated = true;
        }
      } while (++i < l);

      if (updated) {
        this._updateClassName();
      }
    };

    classListProto.remove = function () {
      var tokens = arguments,
          i = 0,
          l = tokens.length,
          token,
          updated = false,
          index;

      do {
        token = tokens[i] + '';
        index = checkTokenAndGetIndex(this, token);

        while (~index) {
          this.splice(index, 1);
          updated = true;
          index = checkTokenAndGetIndex(this, token);
        }
      } while (++i < l);

      if (updated) {
        this._updateClassName();
      }
    };

    classListProto.toggle = function (token, force) {
      var result = this.contains(token),
          method = result ? force !== true && 'remove' : force !== false && 'add';

      if (method) {
        this[method](token);
      }

      if (force === true || force === false) {
        return force;
      } else {
        return !result;
      }
    };

    classListProto.replace = function (token, replacementToken) {
      var index = checkTokenAndGetIndex(token + '');

      if (~index) {
        this.splice(index, 1, replacementToken);

        this._updateClassName();
      }
    };

    classListProto.toString = function () {
      return this.join(' ');
    };

    if (objCtr.defineProperty) {
      var classListPropDesc = {
        get: classListGetter,
        enumerable: true,
        configurable: true
      };

      try {
        objCtr.defineProperty(elemCtrProto, classListProp, classListPropDesc);
      } catch (ex) {
        // IE 8 doesn't support enumerable:true
        // adding undefined to fight this issue https://github.com/eligrey/classList.js/issues/36
        // modernie IE8-MSW7 machine has IE8 8.0.6001.18702 and is affected
        if (ex.number === undefined || ex.number === -0x7FF5EC54) {
          classListPropDesc.enumerable = false;
          objCtr.defineProperty(elemCtrProto, classListProp, classListPropDesc);
        }
      }
    } else if (objCtr[protoProp].__defineGetter__) {
      elemCtrProto.__defineGetter__(classListProp, classListGetter);
    }
  })(window);
} // There is full or partial native classList support, so just check if we need
// to normalize the add/remove and toggle APIs.

(function () {
  var testElement = document.createElement('_');
  testElement.classList.add('c1', 'c2'); // Polyfill for IE 10/11 and Firefox <26, where classList.add and
  // classList.remove exist but support only one argument at a time.

  if (!testElement.classList.contains('c2')) {
    var createMethod = function (method) {
      var original = DOMTokenList.prototype[method]; // eslint-disable-line no-undef

      DOMTokenList.prototype[method] = function (token) {
        // eslint-disable-line no-undef
        for (var i = 0, len = arguments.length; i < len; i++) {
          token = arguments[i];
          original.call(this, token);
        }
      };
    };

    createMethod('add');
    createMethod('remove');
  }

  testElement.classList.toggle('c3', false); // Polyfill for IE 10 and Firefox <24, where classList.toggle does not
  // support the second argument.

  if (testElement.classList.contains('c3')) {
    var _toggle = DOMTokenList.prototype.toggle; // eslint-disable-line no-undef

    DOMTokenList.prototype.toggle = function (token, force) {
      // eslint-disable-line no-undef
      if (1 in arguments && !this.contains(token) === !force) {
        return force;
      } else {
        return _toggle.call(this, token);
      }
    };
  } // replace() polyfill

  if (!('replace' in document.createElement('_').classList)) {
    DOMTokenList.prototype.replace = function (token, replacementToken) {
      // eslint-disable-line no-undef
      var tokens = this.toString().split(' '),
          index = tokens.indexOf(token + '');

      if (~index) {
        tokens = tokens.slice(index);
        this.remove.apply(this, tokens);
        this.add(replacementToken);
        this.add.apply(this, tokens.slice(1));
      }
    };
  }

  testElement = null;
})(); // endregion

/**
 * @module Scheduler/view/DependencyEditor
 */

/**
 * A dependency editor popup.
 *
 * @extends Core/widget/Popup
 * @private
 */

class DependencyEditor extends Popup {
  static get $name() {
    return 'DependencyEditor';
  }

  static get defaultConfig() {
    return {
      items: [],
      draggable: {
        handleSelector: ':not(button,.b-field-inner)' // blacklist buttons and field inners

      },
      axisLock: 'flexible'
    };
  }

  processWidgetConfig(widget) {
    const dependencyEditFeature = this.dependencyEditFeature;
    let fieldConfig = {};

    if (widget.ref === 'lagField' && !dependencyEditFeature.showLagField) {
      return false;
    }

    if (widget.ref === 'deleteButton' && !dependencyEditFeature.showDeleteButton) {
      return false;
    }

    Object.assign(widget, fieldConfig);
    return super.processWidgetConfig(widget);
  }

  afterShow(...args) {
    const deleteButton = this.widgetMap.deleteButton; // Only show delete button if the dependency record belongs to a store

    if (deleteButton) {
      deleteButton.hidden = !this.record.isPartOfStore();
    }

    super.afterShow(...args);
  }

  onInternalKeyDown(event) {
    this.trigger('keyDown', {
      event
    });
    super.onInternalKeyDown(event);
  }

}
DependencyEditor._$name = 'DependencyEditor';

/* eslint-disable no-unused-expressions */
/**
 * @module Scheduler/feature/DependencyEdit
 */

/**
 * Feature that displays a popup containing fields for editing a dependency. Requires the {@link Scheduler.feature.Dependencies Dependencies} feature to be enabled.
 *
 * This feature is **off** by default. It does **not** support vertical mode.
 * For info on enabling it, see {@link Grid.view.mixin.GridFeatures}.
 *
 * @extends Core/mixin/InstancePlugin
 * @externalexample scheduler/Dependencies.js
 * @demo Scheduler/dependencies
 * @classtype dependencyEdit
 */

class DependencyEdit extends InstancePlugin {
  //region Config
  static get $name() {
    return 'DependencyEdit';
  }

  static get defaultConfig() {
    return {
      /**
       * True to hide this editor if a click is detected outside it (defaults to true)
       * @config {Boolean}
       * @default
       * @category Editor
       */
      autoClose: true,

      /**
       * True to save and close this panel if ENTER is pressed in one of the input fields inside the panel.
       * @config {Boolean}
       * @default
       * @category Editor
       */
      saveAndCloseOnEnter: true,

      /**
       * True to show a delete button in the form.
       * @config {Boolean}
       * @default
       * @category Editor widgets
       */
      showDeleteButton: true,

      /**
       * The event that shall trigger showing the editor. Defaults to `dependencydblclick`, set to empty string or `null` to disable editing of dependencies.
       * @config {String}
       * @default
       * @category Editor
       */
      triggerEvent: 'dependencydblclick',

      /**
       * True to show the lag field for the dependency
       * @config {Boolean}
       * @default
       * @category Editor widgets
       */
      showLagField: false,
      dependencyRecord: null,

      /**
       * Default editor configuration, used to configure the Popup.
       * @config {Object}
       * @category Editor
       */
      editorConfig: {
        title: 'L{Edit dependency}',
        localeClass: this,
        closable: true,
        defaults: {
          localeClass: this
        },
        items: {
          /**
           * Reference to the from name
           * @member {Core.widget.DisplayField} fromNameField
           * @readonly
           */
          fromNameField: {
            type: 'display',
            label: 'L{From}',
            editable: false
          },

          /**
           * Reference to the to name field
           * @member {Core.widget.DisplayField} toNameField
           * @readonly
           */
          toNameField: {
            type: 'display',
            label: 'L{To}'
          },

          /**
           * Reference to the type field
           * @member {Core.widget.Combo} typeField
           * @readonly
           */
          typeField: {
            type: 'combo',
            label: 'L{Type}',
            name: 'type',
            editable: false,
            valueField: 'id',
            displayField: 'name',
            items: Object.keys(DependencyModel.Type).map(type => {
              // Do not remove. Assertion strings for Localization sanity check.
              // 'L{StartToStart}'
              // 'L{StartToEnd}'
              // 'L{EndToStart}'
              // 'L{EndToEnd}'
              return {
                id: DependencyModel.Type[type],
                name: this.L(type)
              };
            })
          },

          /**
           * Reference to the lag field
           * @member {Core.widget.DurationField} lagField
           * @readonly
           */
          lagField: {
            type: 'duration',
            label: 'L{Lag}',
            name: 'lag',
            allowNegative: true
          }
        },
        bbar: {
          defaults: {
            localeClass: this
          },
          items: {
            foo: {
              type: 'widget',
              cls: 'b-label-filler'
            },

            /**
             * Reference to the save button, if used
             * @member {Core.widget.Button} saveButton
             * @readonly
             */
            saveButton: {
              color: 'b-green',
              text: 'L{Save}'
            },

            /**
             * Reference to the delete button, if used
             * @member {Core.widget.Button} deleteButton
             * @readonly
             */
            deleteButton: {
              color: 'b-gray',
              text: 'L{Delete}'
            },

            /**
             * Reference to the cancel button, if used
             * @member {Core.widget.Button} cancelButton
             * @readonly
             */
            cancelButton: {
              color: 'b-gray',
              text: 'L{Object.Cancel}'
            }
          }
        }
      }
    };
  } //endregion
  //region Init & destroy

  construct(client, config) {
    const me = this;
    client.dependencyEdit = me;
    me.dependencyStore = client.dependencyStore;
    super.construct(client, config);

    if (!client.features.dependencies) {
      throw new Error('Dependencies feature required when using DependencyEdit');
    }

    me.clientListenersDetacher = client.on({
      [me.triggerEvent]: me.onActivateEditor,
      thisObj: me
    });
  }

  doDestroy() {
    var _this$editor;

    this.clientListenersDetacher();
    (_this$editor = this.editor) === null || _this$editor === void 0 ? void 0 : _this$editor.destroy();
    super.doDestroy();
  } //endregion
  //region Editing

  get editorConfig() {
    return this._editorConfig;
  }

  set editorConfig(editorConfig) {
    const me = this,
          defaultEditorConfig = me.getDefaultConfiguration().editorConfig; // Apply editorConfig to the default editorConfig, allowing users to manipulate for example only bbar

    editorConfig = ObjectHelper.assign({}, defaultEditorConfig, editorConfig);
    editorConfig.items = editorConfig.items || [];
    me._editorConfig = editorConfig;
  } //endregion
  //region Save

  get isValid() {
    return Object.values(this.editor.widgetMap).every(field => {
      if (!field.name || field.hidden) {
        return true;
      }

      return field.isValid !== false;
    });
  }

  get values() {
    const values = {};
    this.editor.eachWidget(widget => {
      if (!widget.name || widget.hidden) return;
      values[widget.name] = widget.value;
    }, true);
    return values;
  }
  /**
       * Template method, intended to be overridden. Called before the dependency record has been updated.
       * @param {Scheduler.model.DependencyModel} dependencyRecord The dependency record
       *
       **/

  onBeforeSave(dependencyRecord) {}
  /**
       * Template method, intended to be overridden. Called after the dependency record has been updated.
       * @param {Scheduler.model.DependencyModel} dependencyRecord The dependency record
       *
       **/

  onAfterSave(dependencyRecord) {}
  /**
       * Updates record being edited with values from the editor
       * @private
       */

  updateRecord(dependencyRecord) {
    const {
      values
    } = this; // Engine does not understand { magnitude, unit } syntax

    if (values.lag) {
      values.lagUnit = values.lag.unit;
      values.lag = values.lag.magnitude;
    } // Type replaces fromSide/toSide, if they are used

    if ('type' in values) {
      dependencyRecord.fromSide != null && (values.fromSide = null);
      dependencyRecord.toSide != null && (values.toSide = null);
    } // Chronograph doesn't filter out undefined fields, it nullifies them instead
    // https://github.com/bryntum/chronograph/issues/11

    ObjectHelper.cleanupProperties(values, true);
    dependencyRecord.set(values);
  } //endregion
  //region Events

  onPopupKeyDown({
    event
  }) {
    if (event.key === 'Enter' && this.saveAndCloseOnEnter && event.target.tagName.toLowerCase() === 'input') {
      // Need to prevent this key events from being fired on whatever receives focus after the editor is hidden
      event.preventDefault();
      this.onSaveClick();
    }
  }

  onSaveClick() {
    if (this.save()) {
      this.editor.hide();
    }
  }

  onDeleteClick() {
    this.deleteDependency();
    this.editor.hide();
  }

  onCancelClick() {
    this.editor.hide();
  } //region Editing
  // Called from editDependency() to actually show the editor

  internalShowEditor(dependencyRecord) {
    const me = this,
          scheduler = me.client;
    let showPoint = me.lastPointerDownCoordinate;
    /**
     * Fires on the owning Scheduler before an dependency is displayed in the editor.
     * This may be listened for to allow an application to take over dependency editing duties. Returning `false`
     * stops the default editing UI from being shown.
     * @event beforeDependencyEdit
     * @param {Scheduler.view.Scheduler} source The scheduler
     * @param {Scheduler.feature.DependencyEdit} dependencyEdit The dependencyEdit feature
     * @param {Scheduler.model.DependencyModel} dependencyRecord The record about to be shown in the editor.
     * @preventable
     */

    if (scheduler.trigger('beforeDependencyEdit', {
      dependencyEdit: me,
      dependencyRecord
    }) === false) {
      return;
    }

    const editor = me.getEditor(dependencyRecord);
    me.loadRecord(dependencyRecord);
    /**
     * Fires on the owning Scheduler when the editor for a dependency is available but before it is shown. Allows
     * manipulating fields before the widget is shown.
     * @event beforeDependencyEditShow
     * @param {Scheduler.view.Scheduler} source The scheduler
     * @param {Scheduler.feature.DependencyEdit} dependencyEdit The dependencyEdit feature
     * @param {Scheduler.model.DependencyModel} dependencyRecord The record about to be shown in the editor.
     * @param {Core.widget.Popup} editor The editor popup
     */

    scheduler.trigger('beforeDependencyEditShow', {
      dependencyEdit: me,
      dependencyRecord,
      editor
    });

    if (!showPoint) {
      const center = Rectangle.from(me.client.element).center;
      showPoint = [center.x - editor.width / 2, center.y - editor.height / 2];
    }

    editor.showByPoint(showPoint);
  }
  /**
   * Opens a popup to edit the passed dependency.
   * @param {Scheduler.model.DependencyModel} dependencyRecord The dependency to edit
   */

  editDependency(dependencyRecord) {
    if (this.client.readOnly) return;
    this.internalShowEditor(dependencyRecord);
  } //endregion
  //region Save

  /**
   * Gets an editor instance. Creates on first call, reuses on consecutive
   * @internal
   * @returns {Scheduler.view.DependencyEditor} Editor popup
   */

  getEditor() {
    var _me$saveButton, _me$deleteButton, _me$cancelButton;

    const me = this;
    let {
      editor
    } = me;

    if (editor) {
      return editor;
    }

    editor = me.editor = new DependencyEditor(ObjectHelper.assign({
      dependencyEditFeature: me,
      autoShow: false,
      anchor: true,
      scrollAction: 'realign',
      clippedBy: [me.client.timeAxisSubGridElement, me.client.bodyContainer],
      constrainTo: window,
      autoClose: me.autoClose,
      cls: me.cls,
      listeners: {
        keydown: me.onPopupKeyDown,
        thisObj: me
      }
    }, me.editorConfig));

    if (editor.items.length === 0) {
      console.warn('Editor configured without any `items`');
    } // assign widget refs

    editor.eachWidget(widget => {
      const ref = widget.ref || widget.id; // don't overwrite if already defined

      if (ref && !me[ref]) {
        me[ref] = widget;
      }
    });
    (_me$saveButton = me.saveButton) === null || _me$saveButton === void 0 ? void 0 : _me$saveButton.on('click', me.onSaveClick, me);
    (_me$deleteButton = me.deleteButton) === null || _me$deleteButton === void 0 ? void 0 : _me$deleteButton.on('click', me.onDeleteClick, me);
    (_me$cancelButton = me.cancelButton) === null || _me$cancelButton === void 0 ? void 0 : _me$cancelButton.on('click', me.onCancelClick, me);
    return me.editor;
  } //endregion
  //region Delete

  /**
   * Sets fields values from record being edited
   * @private
   */

  loadRecord(dependency) {
    const me = this;
    me.fromNameField.value = dependency.fromEvent.name;
    me.toNameField.value = dependency.toEvent.name;

    if (me.lagField) {
      me.lagField.unit = dependency.lagUnit;
    }

    me.editor.record = me.dependencyRecord = dependency;
  } //endregion
  //region Stores

  /**
   * Saves the changes (applies them to record if valid, if invalid editor stays open)
   * @private
   * @fires beforeDependencySave
   * @fires beforeDependencyAdd
   * @fires afterDependencySave
   * @returns {*}
   */

  async save() {
    const me = this,
          {
      client,
      dependencyRecord
    } = me;

    if (!dependencyRecord || !me.isValid) {
      return;
    }

    const {
      dependencyStore,
      values
    } = me;
    /**
     * Fires on the owning Scheduler before a dependency is saved
     * @event beforeDependencySave
     * @param {Scheduler.view.Scheduler} source The scheduler instance
     * @param {Scheduler.model.DependencyModel} dependencyRecord The dependency about to be saved
     * @param {Object} values The new values
     * @preventable
     */

    if (client.trigger('beforeDependencySave', {
      dependencyRecord,
      values
    }) !== false) {
      var _client$project;

      me.onBeforeSave(dependencyRecord);
      me.updateRecord(dependencyRecord); // Check if this is a new record

      if (dependencyStore && !dependencyRecord.stores.length) {
        /**
         * Fires on the owning Scheduler before a dependency is added
         * @event beforeDependencyAdd
         * @param {Scheduler.view.Scheduler} source The scheduler
         * @param {Scheduler.feature.DependencyEdit} dependencyEdit The dependency edit feature
         * @param {Scheduler.model.DependencyModel} dependencyRecord The dependency about to be added
         * @preventable
         */
        if (client.trigger('beforeDependencyAdd', {
          dependencyRecord,
          dependencyEdit: me
        }) === false) {
          return;
        }

        dependencyStore.add(dependencyRecord);
      }

      await ((_client$project = client.project) === null || _client$project === void 0 ? void 0 : _client$project.commitAsync());
      /**
       * Fires on the owning Scheduler after a dependency is successfully saved
       * @event afterDependencySave
       * @param {Scheduler.view.Scheduler} source The scheduler instance
       * @param {Scheduler.model.DependencyModel} dependencyRecord The dependency about to be saved
       */

      client.trigger('afterDependencySave', {
        dependencyRecord
      });
      me.onAfterSave(dependencyRecord);
    }

    return dependencyRecord;
  }
  /**
   * Delete dependency being edited
   * @private
   * @fires beforeDependencyDelete
   */

  async deleteDependency() {
    const {
      client,
      editor,
      dependencyRecord
    } = this;
    /**
     * Fires on the owning Scheduler before a dependency is deleted
     * @event beforeDependencyDelete
     * @param {Scheduler.view.Scheduler} source The scheduler instance
     * @param {Scheduler.model.DependencyModel} dependencyRecord The dependency record about to be deleted
     * @preventable
     */

    if (client.trigger('beforeDependencyDelete', {
      dependencyRecord
    }) !== false) {
      var _client$project2;

      if (editor.containsFocus) {
        editor.revertFocus();
      }

      client.dependencyStore.remove(dependencyRecord);
      await ((_client$project2 = client.project) === null || _client$project2 === void 0 ? void 0 : _client$project2.commitAsync());
      return true;
    }

    return false;
  } //endregion
  //region Events

  onActivateEditor({
    dependency,
    event
  }) {
    if (!this.disabled) {
      this.lastPointerDownCoordinate = [event.clientX, event.clientY];
      this.editDependency(dependency);
    }
  } //endregion

}
DependencyEdit._$name = 'DependencyEdit';
GridFeatureManager.registerFeature(DependencyEdit, false);

/**
 * @module Scheduler/tooltip/ClockTemplate
 */

/**
 * A template showing a clock, it consumes an object containing a date and a text
 * @private
 */

class ClockTemplate extends Base {
  static get defaultConfig() {
    return {
      minuteHeight: 8,
      minuteTop: 2,
      hourHeight: 8,
      hourTop: 2,
      handLeft: 10,
      div: document.createElement('div'),
      scheduler: null,

      // may be passed to the constructor if needed
      // `b-sch-clock-day` for calendar icon
      // `b-sch-clock-hour` for clock icon
      template(data) {
        return `<div class="b-sch-clockwrap b-sch-clock-${data.mode || this.mode} ${data.cls || ''}">
                    <div class="b-sch-clock">
                        <div class="b-sch-hour-indicator">${DateHelper.format(data.date, 'MMM')}</div>
                        <div class="b-sch-minute-indicator">${DateHelper.format(data.date, 'D')}</div>
                        <div class="b-sch-clock-dot"></div>
                    </div>
                    <span class="b-sch-clock-text">${data.text}</span>
                </div>`;
      }

    };
  }

  generateContent(data) {
    const me = this,
          date = data.date,
          html = me.template(data),
          div = me.div;
    div.innerHTML = html;
    me.updateDateIndicator(div, date);
    return div.innerHTML;
  }

  updateDateIndicator(el, date) {
    const hourIndicatorEl = el.querySelector('.b-sch-hour-indicator'),
          minuteIndicatorEl = el.querySelector('.b-sch-minute-indicator');

    if (date && hourIndicatorEl && minuteIndicatorEl) {
      if (this.mode === 'hour') {
        hourIndicatorEl.style.transform = `rotate(${date.getHours() % 12 * 30}deg)`;
        minuteIndicatorEl.style.transform = `rotate(${date.getMinutes() * 6}deg)`;
      } else {
        hourIndicatorEl.style.transform = 'none';
        minuteIndicatorEl.style.transform = 'none';
      }
    }
  }

  set mode(mode) {
    this._mode = mode;
  } // `day` mode for calendar icon
  // `hour` mode for clock icon

  get mode() {
    if (this._mode) {
      return this._mode;
    }

    const unitLessThanDay = DateHelper.compareUnits(this.scheduler.timeAxisViewModel.timeResolution.unit, 'day') < 0,
          formatContainsHourInfo = DateHelper.formatContainsHourInfo(this.scheduler.displayDateFormat);
    return unitLessThanDay && formatContainsHourInfo ? 'hour' : 'day';
  }

  set template(template) {
    this._template = template;
  }
  /**
   * Get the clock template, which accepts an object of format { date, text }
   * @property {function(*): string}
   */

  get template() {
    return this._template;
  }

}
ClockTemplate._$name = 'ClockTemplate';

/* eslint-disable no-unused-expressions */
/**
 * @module Scheduler/feature/base/DragBase
 */
//TODO: shift to copy
//TODO: dragging of event that starts & ends outside of view

/**
 * Base class for EventDrag (Scheduler) and TaskDrag (Gantt) features. Contains shared code. Not to be used directly.
 *
 * @extends Core/mixin/InstancePlugin
 * @abstract
 */

class DragBase extends InstancePlugin {
  //region Config
  static get defaultConfig() {
    return {
      // documented on Schedulers EventDrag feature and Gantts TaskDrag
      dragTipTemplate: data => `
                <div class="b-sch-tip-${data.valid ? 'valid' : 'invalid'}">
                    ${data.startClockHtml}
                    ${data.endClockHtml}
                    <div class="b-sch-tip-message">${data.message}</div>
                </div>
            `,
      // documented on Schedulers EventDrag feature, not used for Gantt
      constrainDragToResource: true,

      /**
       * Specifies whether or not to show tooltip while dragging event
       * @config {Boolean}
       * @default
       */
      showTooltip: true,

      /**
       * When enabled, the event being dragged always "snaps" to the exact start date that it will have after drop.
       * @config {Boolean}
       * @default
       */
      showExactDropPosition: false,

      /**
       * Set to `false` to allow dragging tasks outside of the client Scheduler.
       * Useful when you want to drag tasks between multiple Scheduler instances
       * @config {Boolean}
       * @default
       */
      constrainDragToTimeline: true,

      /*
       * The store from which the dragged items are mapped to the UI.
       * In Scheduler's implementation of this base class, this will be
       * an EventStore, in Gantt's implementations, this will be a TaskStore.
       * Because both derive from this base, we must refer to it as this.store.
       * @private
       */
      store: null,

      /**
       * An object used to configure the internal {@link Core.helper.DragHelper} class
       * @config {Object}
       * @default
       */
      dragHelperConfig: null,
      tooltipCls: null
    };
  } // Plugin configuration. This plugin chains some of the functions in Grid.

  static get pluginConfig() {
    return {
      chain: ['onPaint']
    };
  } //endregion
  //region Init

  /**
   * Called when scheduler is rendered. Sets up drag and drop and hover tooltip.
   * @private
   */

  onPaint({
    firstPaint
  }) {
    var _me$drag;

    const me = this,
          {
      client,
      constrainDragToTimeline
    } = me;
    (_me$drag = me.drag) === null || _me$drag === void 0 ? void 0 : _me$drag.destroy();
    me.drag = new DragHelper(Object.assign({
      name: me.constructor.name,
      // useful when debugging with multiple draggers
      mode: 'translateXY',
      [client.isHorizontal ? 'lockY' : 'lockX']: me.constrainDragToResource,
      [client.isHorizontal ? 'lockX' : 'lockY']: me.constrainDragToTimeSlot,
      minX: true,
      // Allows dropping with start before time axis
      maxX: true,
      // Allows dropping with end after time axis
      constrain: false,
      cloneTarget: !constrainDragToTimeline,
      dragWithin: constrainDragToTimeline ? null : document.body,
      hideOriginalElement: true,
      outerElement: client.timeAxisSubGridElement,
      targetSelector: client.eventSelector,
      isElementDraggable: (el, event) => {
        return !client.readOnly && (!me.dragHelperConfig || !me.dragHelperConfig.isElementDraggable || me.dragHelperConfig.isElementDraggable(el, event)) && me.isElementDraggable(el, event);
      },
      scrollManager: constrainDragToTimeline ? client.scrollManager : null,
      transitionDuration: client.transitionDuration,
      listeners: {
        beforedragstart: 'onBeforeDragStart',
        dragstart: 'onDragStart',
        drag: 'onDrag',
        drop: 'onDrop',
        abort: 'onDragAbort',
        reset: 'onDragReset',
        thisObj: me
      }
    }, me.dragHelperConfig));

    if (firstPaint) {
      client.rowManager.on({
        changeTotalHeight: 'updateYConstraint',
        thisObj: me
      });
    }

    if (me.showTooltip) {
      me.clockTemplate = new ClockTemplate({
        scheduler: client
      });
    }
  }

  doDestroy() {
    var _this$drag, _this$clockTemplate, _this$tip;

    (_this$drag = this.drag) === null || _this$drag === void 0 ? void 0 : _this$drag.destroy();
    (_this$clockTemplate = this.clockTemplate) === null || _this$clockTemplate === void 0 ? void 0 : _this$clockTemplate.destroy();
    (_this$tip = this.tip) === null || _this$tip === void 0 ? void 0 : _this$tip.destroy();
    super.doDestroy();
  } //endregion
  //region Drag events

  onBeforeDragStart({
    context
  }) {
    const me = this,
          {
      client
    } = me,
          name = client.scheduledEventName,
          eventRecord = client.resolveEventRecord(context.element);

    if (me.disabled || !eventRecord || eventRecord.isDraggable === false) {
      return false;
    } // Cache the date corresponding to the drag start point so that on drag, we can always
    // perform the same calculation to then find the time delta without having to calculate
    // the new start end end times from the position that the element is.

    context.pointerStartDate = client.getDateFromXY([context.startClientX, context.startPageY], null, false);
    const result = client.trigger(`before${name}Drag`, {
      eventRecord,
      context: me.dragData
    }) !== false;

    if (result !== false) {
      me.updateYConstraint(eventRecord);
    }

    return result;
  } // Constrain to time slot means lockX if we're horizontal, otherwise lockY

  set constrainDragToTimeSlot(value) {
    const axis = this.client.isHorizontal ? 'lockX' : 'lockY';
    this._constrainDragToTimeSlot = value;

    if (this.drag) {
      this.drag[axis] = value;
    }
  }

  get constrainDragToTimeSlot() {
    return this._constrainDragToTimeSlot;
  } // Constrain to resource means lockY if we're horizontal, otherwise lockX

  set constrainDragToResource(value) {
    const axis = this.client.isHorizontal ? 'lockY' : 'lockX';
    this._constrainDragToResource = value;

    if (this.drag) {
      this.drag[axis] = value;
    }
  }

  get constrainDragToResource() {
    return this._constrainDragToResource;
  }
  /**
   * Triggered when dragging of an event starts. Initializes drag data associated with the event being dragged.
   * @private
   */

  onDragStart({
    context,
    event
  }) {
    const me = this,
          {
      client
    } = me;
    me.currentOverClient = client;
    me.scrollClients = {};
    me.onMouseOverNewTimeline(client);
    const dragData = me.dragData = me.getDragData(context, event); // Do not let DomSync reuse the element

    context.element.retainElement = true;

    if (me.showTooltip) {
      const tipTarget = dragData.context.dragProxy ? dragData.context.dragProxy.firstChild : context.element;

      if (!me.tip) {
        me.tip = new Tooltip({
          id: `${client.id}-event-drag-tip`,
          align: 'b-t',
          autoShow: true,
          updateContentOnMouseMove: true,
          clippedBy: me.constrainDragToTimeline ? [client.timeAxisSubGridElement, client.bodyContainer] : null,
          forElement: tipTarget,
          getHtml: me.getTipHtml.bind(me),
          // During drag, it must be impossible for the mouse to be over the tip.
          style: 'pointer-events:none',
          cls: me.tooltipCls
        });
        me.tip.on('innerhtmlupdate', me.updateDateIndicator, me);
      } else {
        me.tip.showBy(tipTarget);
      }
    } // me.copyKeyPressed = me.isCopyKeyPressed();
    //
    // if (me.copyKeyPressed) {
    //     dragData.refElements.addCls('sch-event-copy');
    //     dragData.originalHidden = true;
    // }

    me.triggerDragStart(dragData);
  }

  updateDateIndicator() {
    const {
      startDate,
      endDate
    } = this.dragData,
          {
      tip
    } = this,
          endDateElement = tip.element.querySelector('.b-sch-tooltip-enddate');
    this.clockTemplate.updateDateIndicator(tip.element, startDate);
    endDateElement && this.clockTemplate.updateDateIndicator(endDateElement, endDate);
  }
  /**
   * Triggered while dragging an event. Updates drag data, validation etc.
   * @private
   */

  onDrag({
    context,
    event
  }) {
    const me = this,
          dd = me.dragData,
          start = dd.startDate;
    let client;

    if (me.constrainDragToTimeline) {
      client = me.client;
    } else {
      let target = event.target; // Can't detect target under a touch event

      if (/^touch/.test(event.type)) {
        const center = Rectangle.from(dd.context.element, null, true).center;
        target = DomHelper.elementFromPoint(center.x, center.y);
      }

      client = Widget.fromElement(target, 'timelinebase');
    }

    const depFeature = me.client.features.dependencies,
          x = context.newX,
          y = context.newY;

    if (!client) {
      if (depFeature) {
        depFeature.updateDependenciesForTimeSpan(dd.draggedEntities[0], dd.context.element);
      }

      return;
    }

    if (client !== me.currentOverClient) {
      me.onMouseOverNewTimeline(client);
    } //this.checkShiftChange();

    me.updateDragContext(context, event); // Snapping not supported when dragging outside a scheduler

    if (me.constrainDragToTimeline && !me.constrainDragToTimeSlot && (me.showExactDropPosition || me.client.timeAxisViewModel.snap)) {
      const newDate = client.getDateFromCoordinate(me.getCoordinate(dd.draggedEntities[0], context.element, [x, y])),
            timeDiff = newDate - dd.sourceDate,
            realStart = new Date(dd.origStart - 0 + timeDiff),
            offset = client.timeAxisViewModel.getDistanceBetweenDates(realStart, dd.startDate);

      if (dd.startDate >= client.timeAxis.startDate && offset != null) {
        if (me.client.isVertical) {
          DomHelper.addTranslateY(context.element, offset);
        } else {
          DomHelper.addTranslateX(context.element, offset);
        }
      }
    } // Let product specific implementations trigger drag event (eventDrag, taskDrag)

    me.triggerEventDrag(dd, start);
    let valid = me.checkDragValidity(dd, event);

    if (valid && typeof valid !== 'boolean') {
      context.message = valid.message || '';
      valid = valid.valid;
    }

    context.valid = valid !== false;

    if (me.showTooltip) {
      me.tip.realign();
    }

    if (depFeature) {
      depFeature.updateDependenciesForTimeSpan(dd.draggedEntities[0], dd.context.element.querySelector(client.eventInnerSelector), dd.newResource);
    }
  }

  onMouseOverNewTimeline(newTimeline) {
    const me = this,
          scrollClients = me.scrollClients || {};
    me.currentOverClient.element.classList.remove('b-dragging-' + me.currentOverClient.scheduledEventName);
    newTimeline.element.classList.add('b-dragging-' + newTimeline.scheduledEventName);

    if (!(newTimeline.id in scrollClients)) {
      const scrollManager = newTimeline.scrollManager;
      scrollManager.startMonitoring({
        element: newTimeline.timeAxisSubGridElement
      });
      scrollClients[newTimeline.id] = newTimeline;
    }

    me.currentOverClient = newTimeline;
  }
  /**
   * Triggered when dropping an event. Finalizes the operation.
   * @private
   */

  onDrop({
    context,
    event
  }) {
    var _me$tip;

    const me = this,
          {
      currentOverClient,
      dragData
    } = me;
    let modified = false;
    me.updateDragContext(context, event);
    (_me$tip = me.tip) === null || _me$tip === void 0 ? void 0 : _me$tip.hide();

    if (context.valid && dragData.startDate && dragData.endDate) {
      dragData.finalize = (...params) => {
        me.finalize(...params);
        context.finalize(...params);
      }; // Allow implementer to take control of the flow, by returning false from this listener,
      // to show a confirmation popup etc. This event is documented in EventDrag and TaskDrag

      currentOverClient.trigger(`before${currentOverClient.capitalizedEventName}DropFinalize`, {
        context: dragData,
        event
      });
      context.async = dragData.async; // Internal validation, making sure all dragged records fit inside the view

      if (!context.async && me.isValidDrop(dragData)) {
        modified = dragData.startDate - dragData.origStart !== 0 || dragData.newResource !== dragData.resourceRecord;
      }
    }

    if (!context.async) {
      me.finalize(dragData.valid && context.valid && modified);
    }
  }

  onDragAbort({
    context
  }) {
    var _me$tip2;

    const me = this;
    me.client.currentOrientation.onDragAbort({
      context,
      dragData: me.dragData
    }); // otherwise the event disappears on next refresh (#62)

    me.resumeElementRedrawing(me.dragData.record);
    (_me$tip2 = me.tip) === null || _me$tip2 === void 0 ? void 0 : _me$tip2.hide(); // Trigger eventDragAbort / taskDragAbort depending on product

    me.triggerDragAbort(me.dragData);
  } // For the drag across multiple schedulers, tell all involved scroll managers to stop monitoring

  onDragReset({
    source: dragHelper
  }) {
    var _me$currentOverClient, _me$dragData;

    const me = this,
          scrollClients = me.scrollClients || {};

    for (const managerId in scrollClients) {
      scrollClients[managerId].scrollManager.stopMonitoring(scrollClients[managerId].timeAxisSubGridElement);
    }

    if (dragHelper.context && dragHelper.context.started) {
      const {
        eventBarEls
      } = me.dragData;
      eventBarEls[0].classList.remove('b-drag-main');
    }

    me.scrollClients = null;
    (_me$currentOverClient = me.currentOverClient) === null || _me$currentOverClient === void 0 ? void 0 : _me$currentOverClient.element.classList.remove('b-dragging-' + me.currentOverClient.scheduledEventName); // Dependencies are updated dynamically during drag, so ensure they are redrawn
    // if the event snaps back with no change after abort or an invalid drop.

    if (((_me$dragData = me.dragData) === null || _me$dragData === void 0 ? void 0 : _me$dragData.context.valid) === false && me.currentOverClient.features.dependencies) {
      me.currentOverClient.features.dependencies.scheduleDraw(true);
    } // TODO we should clean this up, requires review of the flow first
    // me.dragData = null;

  }
  /**
   * Triggered internally on invalid drop.
   * @private
   */

  onInternalInvalidDrop() {
    var _this$tip2;

    (_this$tip2 = this.tip) === null || _this$tip2 === void 0 ? void 0 : _this$tip2.hide();
    this.drag.abort();
    this.triggerAfterDrop(this.dragData, false);
  } //endregion
  //region Finalization & validation

  /**
   * Called on drop to update the record of the event being dropped.
   * @private
   * @param {Boolean} updateRecords Specify true to update the record, false to treat as invalid
   */

  async finalize(updateRecords) {
    const me = this,
          {
      dragData
    } = me,
          {
      context,
      draggedEntities
    } = dragData;
    let result;
    draggedEntities.forEach((record, i) => {
      me.resumeElementRedrawing(record);
      dragData.eventBarEls[i].classList.remove(me.drag.draggingCls);
      dragData.eventBarEls[i].retainElement = false;
    });

    if (updateRecords) {
      // updateRecords may or may not be async.
      // We see if it returns a Promise.
      result = me.updateRecords(dragData); // If updateRecords is async, the calling DragHelper must know this and
      // go into a awaitingFinalization state.

      if (ObjectHelper.isPromise(result)) {
        context.async = true;
        await result;
      } // If the finalize handler decided to change the dragData's validity...

      if (!dragData.valid) {
        me.onInternalInvalidDrop();
      } else {
        me.drag.reset();
        me.triggerAfterDrop(dragData, true);
      }
    } else {
      me.onInternalInvalidDrop();
    }

    return result;
  } //endregion
  //region Drag data

  /**
   * Updates drag datas dates and validity (calls #validatorFn if specified)
   * @private
   */

  updateDragContext(info, event) {
    const me = this,
          {
      drag
    } = me,
          dd = me.dragData,
          client = me.currentOverClient,
          {
      isHorizontal
    } = client,
          element = info.element,
          proxyRegion = Rectangle.from(element, client.timeAxisSubGridElement),
          record = dd.draggedEntities[0],
          eventRecord = record.isAssignment ? record.event : record,
          constrainToTimeSlot = me.constrainDragToTimeSlot || (isHorizontal ? drag.lockX : drag.lockY);
    dd.browserEvent = event;

    if (constrainToTimeSlot) {
      dd.timeDiff = 0;
    } else {
      if (client.timeAxis.isContinuous) {
        const {
          dateConstraints
        } = dd,
              {
          timeAxisSubGrid
        } = client,
              {
          scrollable
        } = timeAxisSubGrid,
              timeAxisRegion = scrollable.viewport,
              timeAxisPosition = client.isHorizontal ? info.pageX - timeAxisRegion.x + scrollable.x : info.pageY - timeAxisRegion.y + scrollable.y,
              // Use the localized coordinates to ask the TimeAxisViewModel what date the mouse is at.
        // Pass allowOutOfRange as true in case we have dragged out of either side of the timeline viewport.
        pointerDate = client.timeAxisViewModel.getDateFromPosition(timeAxisPosition, null, true),
              timeDiff = dd.timeDiff = pointerDate - info.pointerStartDate; // calculate and round new startDate based on actual dd.timeDiff

        dd.startDate = me.adjustStartDate(dd.origStart, timeDiff);

        if (dateConstraints) {
          dd.startDate = DateHelper.constrain(dd.startDate, dateConstraints.start, new Date(dateConstraints.end - eventRecord.durationMS));
        }

        dd.endDate = new Date(dd.startDate - 0 + dd.duration);
      } else {
        const range = me.resolveStartEndDates(proxyRegion); // if dragging is out of timeAxis rect bounds, we will not be able to get dates

        dd.valid = Boolean(range.startDate && range.endDate);

        if (dd.valid) {
          dd.startDate = range.startDate;
          dd.endDate = range.endDate;
        }
      }

      if (dd.valid) {
        dd.timeDiff = dd.startDate - dd.origStart;
      }
    } // getProductDragContext may switch valid flag, need to keep it here

    Object.assign(dd, me.getProductDragContext(dd));

    if (dd.valid) {
      // If it's fully outside, we don't allow them to drop it - the event would disappear from their control.
      if (dd.endDate <= client.timeAxis.startDate || dd.startDate >= client.timeAxis.endDate) {
        dd.context.valid = false;
        dd.context.message = me.L('L{EventDrag.noDropOutsideTimeline}');
      } else {
        const result = !event || me.checkDragValidity(dd, event);

        if (!result || typeof result === 'boolean') {
          dd.context.valid = result !== false;
          dd.context.message = '';
        } else {
          dd.context.valid = result.valid !== false;
          dd.context.message = result.message;
        }
      }
    } else {
      dd.context.valid = false;
    }
  }

  suspendElementRedrawing(record, suspend = true) {
    const element = this.getRecordElement(record);

    if (element) {
      element.retainElement = suspend;
    }

    record.instanceMeta(this.client).retainElement = suspend;
  }

  resumeElementRedrawing(record) {
    this.suspendElementRedrawing(record, false);
  }
  /**
   * Initializes drag data (dates, constraints, dragged events etc). Called when drag starts.
   * @private
   * @param info
   * @param event
   * @returns {*}
   */

  getDragData(info, event) {
    const me = this,
          {
      client,
      drag
    } = me,
          {
      record,
      dateConstraints,
      eventBarEls,
      draggedEntities
    } = me.setupProductDragData(info),
          timespan = record.isAssignment ? record.event : record,
          origStart = timespan.startDate,
          origEnd = timespan.endDate,
          timeAxis = client.timeAxis,
          startsOutsideView = origStart < timeAxis.startDate,
          endsOutsideView = origEnd > timeAxis.endDate,
          coordinate = me.getCoordinate(timespan, info.element, [info.elementStartX, info.elementStartY]),
          clientCoordinate = me.getCoordinate(timespan, info.element, [info.startClientX, info.startClientY]); // prevent elements from being released when out of view

    draggedEntities.forEach(record => me.suspendElementRedrawing(record)); // Select current without deselecting other events if Ctrl key is pressed

    if (drag.startEvent.ctrlKey) {
      if (record.isAssignment) {
        client.selectAssignment(record, true);
      } else {
        client.selectEvent(record, true);
      }
    }

    const dragData = {
      context: info,
      dateConstraints,
      eventBarEls,
      record,
      draggedEntities,
      sourceDate: startsOutsideView ? origStart : client.getDateFromCoordinate(coordinate),
      screenSourceDate: client.getDateFromCoordinate(clientCoordinate, null, false),
      startDate: origStart,
      endDate: origEnd,
      timeDiff: 0,
      origStart,
      origEnd,
      startsOutsideView,
      endsOutsideView,
      duration: origEnd - origStart,
      browserEvent: event // So we can know if SHIFT/CTRL was pressed

    };
    eventBarEls.forEach(el => {
      el.classList.add(drag.draggingCls);
      el.classList.remove('b-sch-event-hover');
      el.classList.remove('b-active');
    });

    if (eventBarEls.length > 1) {
      // RelatedElements are secondary elements moved by the same delta as the grabbed element
      if (!me.constrainDragToTimeline) {
        // Will be dragging a clone, need to do the same for all selected elements
        // TODO: This should be handled by DragHelper! It is basically a copy of code there for the main element
        info.relatedElements = eventBarEls.slice(1).map(eventBar => {
          // TODO: Should be able to simplify this to only use DomSync.addChild
          const offsetX = DomHelper.getOffsetX(eventBar, drag.dragWithin),
                offsetY = DomHelper.getOffsetY(eventBar, drag.dragWithin),
                offsetWidth = eventBar.offsetWidth,
                offsetHeight = eventBar.offsetHeight,
                element = drag.createProxy(eventBar); // Match the grabbed element's size and position.

          DomHelper.setTranslateXY(element, offsetX, offsetY);
          element.style.width = `${offsetWidth}px`;
          element.style.height = `${offsetHeight}px`; //element.classList.add(drag.dragProxyCls);

          drag.dragWithin.appendChild(element);
          eventBar.classList.add('b-drag-original');

          if (drag.hideOriginalElement) {
            eventBar.classList.add('b-hidden');
          }

          return element;
        });
      } else {
        info.relatedElements = eventBarEls.slice(1);
      }

      info.relatedElStartPos = [];
      info.relatedElDragFromPos = []; // Move the selected events into a unified cascade.

      if (me.unifiedDrag) {
        // EventBarEls should animate into the cascade
        me.client.isAnimating = true;
        EventHelper.onTransitionEnd({
          element: eventBarEls[1],
          property: 'transform',

          handler() {
            me.client.isAnimating = false;
          },

          once: true
        }); // Main dragged element should not look different. The relatedElements do.

        eventBarEls[0].classList.add('b-drag-main');
        let [x, y] = DomHelper.getTranslateXY(info.element);
        info.relatedElements.forEach((el, i) => {
          // Cache the start pos for reversion in case of invalid drag
          info.relatedElStartPos[i] = DomHelper.getTranslateXY(el); // Move into cascade and cache the dragFrom pos

          x += 10;
          y += 10;
          DomHelper.setTranslateXY(el, x, y);
          info.relatedElDragFromPos[i] = [x, y];
        });
      } else {
        // Start pos and dragFrom pos are the same for non-unified
        info.relatedElements.forEach((el, i) => {
          info.relatedElStartPos[i] = info.relatedElDragFromPos[i] = DomHelper.getTranslateXY(el);
        });
      }
    }

    return dragData;
  } // Provide your custom implementation of this to allow additional selected records to be dragged together with the original one.

  getRelatedRecords(record) {
    return [];
  } //endregion
  //region Constraints
  // private

  setupConstraints(constrainRegion, elRegion, tickSize, constrained) {
    const me = this,
          xTickSize = !me.showExactDropPosition && tickSize > 1 ? tickSize : 0,
          yTickSize = 0; // If `constrained` is false then we haven't specified getDateConstraint method and should constrain mouse position to scheduling area
    // else we have specified date constraints and so we should limit mouse position to smaller region inside of constrained region using offsets and width.

    if (constrained) {
      me.setXConstraint(constrainRegion.left, constrainRegion.right - elRegion.width, xTickSize);
    } // And if not constrained, release any constraints from the previous drag.
    else {
        // minX being true means allow the start to be before the time axis.
        // maxX being true means allow the end to be after the time axis.
        me.setXConstraint(true, true, xTickSize);
      }

    me.setYConstraint(constrainRegion.top, constrainRegion.bottom - elRegion.height, yTickSize);
  }

  updateYConstraint(eventRecord) {
    const me = this,
          {
      client
    } = me,
          {
      context
    } = me.drag,
          tickSize = client.timeAxisViewModel.snapPixelAmount; // If we're dragging when the vertical size is recalculated by the host grid,
    // we must update our Y constraint unless we are locked in the Y axis.

    if (context && !me.drag.lockY) {
      let constrainRegion; // This calculates a relative region which the DragHelper uses within its outerElement

      if (me.constrainDragToTimeline) {
        constrainRegion = client.getScheduleRegion(null, eventRecord);
      } // Not constraining to timeline.
      // Unusual configuration, but this must mean no Y constraining.
      else {
          me.setYConstraint(null, null, tickSize);
          return;
        }

      me.setYConstraint(constrainRegion.top, constrainRegion.bottom - context.element.offsetHeight, tickSize);
    } else {
      me.setYConstraint(null, null, tickSize);
    }
  }

  setXConstraint(iLeft, iRight, iTickSize) {
    const {
      drag
    } = this;
    drag.leftConstraint = iLeft;
    drag.rightConstraint = iRight;
    drag.minX = iLeft;
    drag.maxX = iRight;
  }

  setYConstraint(iUp, iDown, iTickSize) {
    const {
      drag
    } = this;
    drag.topConstraint = iUp;
    drag.bottomConstraint = iDown;
    drag.minY = iUp;
    drag.maxY = iDown;
  } //endregion
  //region Other stuff

  adjustStartDate(startDate, timeDiff) {
    return this.client.timeAxis.roundDate(new Date(startDate - 0 + timeDiff), this.client.snapRelativeToEventStartDate ? startDate : false);
  }

  resolveStartEndDates(proxyRect) {
    const {
      start,
      end
    } = this.client.getStartEndDatesFromRectangle(proxyRect, 'round', this.dragData.duration);
    return {
      startDate: start,
      endDate: end
    };
  } //endregion
  //region Dragtip

  /**
   * Gets html to display in tooltip while dragging event. Uses clockTemplate to display start & end dates.
   */

  getTipHtml() {
    const me = this,
          {
      dragData,
      client
    } = me,
          {
      startDate,
      endDate,
      draggedEntities
    } = dragData,
          startText = client.getFormattedDate(startDate),
          endText = client.getFormattedEndDate(endDate, startDate),
          {
      valid,
      message
    } = dragData.context,
          dragged = draggedEntities[0],
          // Scheduler always drags assignments
    timeSpanRecord = dragged.isTask ? dragged : dragged.event;
    return me.dragTipTemplate({
      valid,
      startDate,
      endDate,
      startText,
      endText,
      dragData,
      message: message || '',
      [client.scheduledEventName + 'Record']: timeSpanRecord,
      startClockHtml: me.clockTemplate.template({
        date: startDate,
        text: startText,
        cls: 'b-sch-tooltip-startdate'
      }),
      endClockHtml: timeSpanRecord.isMilestone ? '' : me.clockTemplate.template({
        date: endDate,
        text: endText,
        cls: 'b-sch-tooltip-enddate'
      })
    });
  } //endregion
  //region Product specific, implemented in subclasses
  // Check if element can be dropped at desired location

  isValidDrop(dragData) {
    throw new Error('Implement in subclass');
  } // Similar to the fn above but also calls validatorFn

  checkDragValidity(dragData) {
    throw new Error('Implement in subclass');
  } // Update records being dragged

  updateRecords(context) {
    throw new Error('Implement in subclass');
  } // Determine if an element can be dragged

  isElementDraggable(el, event) {
    throw new Error('Implement in subclass');
  } // Get coordinate for correct axis

  getCoordinate(record, element, coord) {
    throw new Error('Implement in subclass');
  } // Product specific drag data

  setupProductDragData(info) {
    throw new Error('Implement in subclass');
  } // Product specific data in drag context

  getProductDragContext(dd) {
    throw new Error('Implement in subclass');
  }

  getRecordElement(record) {
    throw new Error('Implement in subclass');
  } //endregion

}
DragBase._$name = 'DragBase';

/**
 * @module Scheduler/feature/base/TimeSpanMenuBase
 */

/**
 * Abstract base class used by other context menu features which show the context menu for TimeAxis.
 * Using this class you can make sure the menu expects the target to disappear,
 * since it can be scroll out of the scheduling zone.
 *
 * Features that extend this class are:
 *  * {@link Scheduler/feature/EventMenu EventMenu};
 *  * {@link Scheduler/feature/ScheduleMenu ScheduleMenu};
 *  * {@link Scheduler/feature/TimeAxisHeaderMenu TimeAxisHeaderMenu};
 *
 * @extends Grid/feature/base/ContextMenuBase
 * @abstract
 */

class TimeSpanMenuBase extends ContextMenuBase {
  changeMenu(menu, oldMenu) {
    if (menu) {
      menu = Objects.assign({}, menu, {
        // It's a rectangle outside which the target of alignment may disappear.
        // It has to be like that because when scrolling in a scheduler, there are two scrolling elements.
        // The main Scheduler body for up/down and the subgrid for left and right
        clippedBy: [this.client.timeAxisSubGridElement, this.client.bodyContainer]
      });
    }

    return super.changeMenu(menu, oldMenu);
  }

}
TimeSpanMenuBase._$name = 'TimeSpanMenuBase';

/**
 * @module Scheduler/feature/EventMenu
 */

/**
 * Displays a context menu for events. Items are populated by other features and/or application code.
 *
 * ### Default event menu items
 *
 * Here is the list of menu items provided by the feature and populated by the other features:
 *
 * | Reference       | Text           | Weight | Feature                                       | Description                                                             |
 * |-----------------|----------------|--------|-----------------------------------------------|-------------------------------------------------------------------------|
 * | `editEvent`     | Edit event     | 100    | {@link Scheduler.feature.EventEdit EventEdit} | Open the event editor. Hidden for read-only Scheduler                   |
 * | `deleteEvent`   | Delete event   | 200    | *This feature*                                | Remove the event. Hidden for read-only Scheduler                        |
 * | `unassignEvent` | Unassign event | 300    | *This feature*                                | Unassign the event. Shown when using multi-assignment and not read-only |
 *
 * ### Customizing the menu items
 *
 * The menu items in the Event menu can be customized, existing items can be changed or removed,
 * and new items can be added. This is handled using the `items` config of the feature.
 *
 * Add extra items for all events:
 *
 * ```javascript
 * const scheduler = new Scheduler({
 *     features : {
 *         eventMenu : {
 *             items : {
 *                 extraItem : {
 *                     text : 'Extra',
 *                     icon : 'b-fa b-fa-fw b-fa-flag',
 *                     onItem({eventRecord}) {
 *                         eventRecord.flagged = true;
 *                     }
 *                 }
 *             }
 *         }
 *     }
 * });
 * ```
 *
 * Remove existing items:
 *
 * ```javascript
 * const scheduler = new Scheduler({
 *     features : {
 *         eventMenu : {
 *             items : {
 *                 deleteEvent   : false,
 *                 unassignEvent : false
 *             }
 *         }
 *     }
 * });
 * ```
 *
 * Customize existing item:
 *
 * ```javascript
 * const scheduler = new Scheduler({
 *     features : {
 *         eventMenu : {
 *             items : {
 *                 deleteEvent : {
 *                     text : 'Delete booking'
 *                 }
 *             }
 *         }
 *     }
 * });
 * ```
 *
 * Manipulate existing items for all events or specific events:
 *
 * ```javascript
 * const scheduler = new Scheduler({
 *     features : {
 *         eventMenu : {
 *             // Process items before menu is shown
 *             processItems({eventRecord, items}) {
 *                  // Push an extra item for conferences
 *                  if (eventRecord.type === 'conference') {
 *                      items.showSessionItem = {
 *                          text : 'Show sessions',
 *                          onItem({eventRecord}) {
 *                              // ...
 *                          }
 *                      };
 *                  }
 *
 *                  // Do not show menu for secret events
 *                  if (eventRecord.type === 'secret') {
 *                      return false;
 *                  }
 *             }
 *         }
 *     }
 * });
 * ```
 *
 * Full information of the menu customization can be found in the "Customizing the Event menu, the Schedule menu, and the TimeAxisHeader menu" guide.
 *
 * This feature is **enabled** by default
 *
 * @extends Scheduler/feature/base/TimeSpanMenuBase
 * @demo Scheduler/eventmenu
 * @classtype eventMenu
 */

class EventMenu extends TimeSpanMenuBase {
  //region Config
  static get $name() {
    return 'EventMenu';
  }

  static get configurable() {
    return {
      /**
       * A function called before displaying the menu that allows manipulations of its items.
       * Returning `false` from this function prevents the menu being shown.
       *
       * ```javascript
       * features         : {
       *    eventMenu : {
       *         processItems({ items, eventRecord, assignmentRecord, resourceRecord }) {
       *             // Add or hide existing items here as needed
       *             items.myAction = {
       *                 text   : 'Cool action',
       *                 icon   : 'b-fa b-fa-fw b-fa-ban',
       *                 onItem : () => console.log(`Clicked ${eventRecord.name}`),
       *                 weight : 1000 // Move to end
       *             };
       *
       *            if (!eventRecord.allowDelete) {
       *                 items.deleteEvent.hidden = true;
       *             }
       *         }
       *     }
       * },
       * ```
       * @param {Object} context An object with information about the menu being shown
       * @param {Scheduler.model.EventModel} context.eventRecord The record representing the current event
       * @param {Scheduler.model.ResourceModel} context.resourceRecord The record representing the current resource
       * @param {Scheduler.model.AssignmentModel} context.assignmentRecord The assignment record
       * @param {Object} context.items An object containing the {@link Core.widget.MenuItem menu item} configs keyed by their id
       * @param {Event} context.event The DOM event object that triggered the show
       * @config {Function}
       * @preventable
       */
      processItems: null,
      type: 'event'
      /**
       * This is a preconfigured set of items used to create the default context menu.
       *
       * The `items` provided by this feature are listed below. These are the property names which you may
       * configure:
       *
       * - `deleteEvent` Deletes the context event.
       * - `unassignEvent` Unassigns the context event from the current resource (only added when multi assignment is used).
       *
       * To remove existing items, set corresponding keys to `false`
       *
       * ```javascript
       * const scheduler = new Scheduler({
       *     features : {
       *         eventMenu : {
       *             items : {
       *                 deleteEvent   : false,
       *                 unassignEvent : false
       *             }
       *         }
       *     }
       * });
       * ```
       *
       * See the feature config in the above example for details.
       *
       * @config {Object} items
       */

    };
  }

  static get pluginConfig() {
    const config = super.pluginConfig;
    config.chain.push('populateEventMenu');
    return config;
  } //endregion
  //region Events

  /**
   * Fired from scheduler before the context menu is shown for an event. Allows manipulation of the items
   * to show in the same way as in `processItems`. Returning `false` from a listener prevents the menu from
   * being shown.
   * @event eventMenuBeforeShow
   * @preventable
   * @param {Scheduler.view.Scheduler} source
   * @param {Object} items Menu item configs
   * @param {Scheduler.model.EventModel} eventRecord Event record for which the menu was triggered
   * @param {Scheduler.model.ResourceModel} resourceRecord Resource record
   * @param {Scheduler.model.AssignmentModel} assignmentRecord Assignment record, if assignments are used
   * @param {HTMLElement} eventElement
   */

  /**
   * Fired from scheduler when an item is selected in the context menu.
   * @event eventMenuItem
   * @param {Scheduler.view.Scheduler} source
   * @param {Core.widget.MenuItem} item
   * @param {Scheduler.model.EventModel} eventRecord
   * @param {Scheduler.model.ResourceModel} resourceRecord
   * @param {Scheduler.model.AssignmentModel} assignmentRecord Assignment record, if assignments are used
   * @param {HTMLElement} eventElement
   */

  /**
   * Fired from scheduler after showing the context menu for an event
   * @event eventMenuShow
   * @param {Scheduler.view.Scheduler} source
   * @param {Core.widget.Menu} menu The menu
   * @param {Scheduler.model.EventModel} eventRecord Event record for which the menu was triggered
   * @param {Scheduler.model.ResourceModel} resourceRecord Resource record
   * @param {Scheduler.model.AssignmentModel} assignmentRecord Assignment record, if assignments are used
   * @param {HTMLElement} eventElement
   */
  //endregion

  onElementKeyDown(event) {
    if (!event.handled && event.target.matches(this.client.eventSelector)) {
      if (event.key === ' ') {
        this.internalShowContextMenu(this.createContextMenuEventForElement(event.target));
      }
    }
  }

  getDataFromEvent(event) {
    const data = super.getDataFromEvent(event),
          eventElement = data.targetElement,
          {
      client
    } = this,
          eventRecord = client.resolveEventRecord(eventElement),
          // For vertical mode the resource must be resolved from the event
    resourceRecord = eventRecord && (client.resolveResourceRecord(eventElement) || client.resourceStore.last),
          assignmentRecord = eventRecord && client.resolveAssignmentRecord(eventElement);
    return Object.assign(data, {
      eventElement,
      eventRecord,
      resourceRecord,
      assignmentRecord
    });
  }

  getTargetElementFromEvent({
    target
  }) {
    return DomHelper.up(target, this.client.eventSelector) || target;
  }

  shouldShowMenu(eventParams) {
    return eventParams.eventRecord;
  }
  /**
   * Shows context menu for the provided event. If record is not rendered (outside of time span/filtered)
   * menu won't appear.
   * @param {Scheduler.model.EventModel} eventRecord Event record to show menu for.
   * @param {Object} [options]
   * @param {HTMLElement} options.targetElement Element to align context menu to.
   * @param {MouseEvent} options.event Browser event.
   * If provided menu will be aligned according to clientX/clientY coordinates.
   * If omitted, context menu will be centered to event element.
   */

  showContextMenuFor(eventRecord, {
    targetElement,
    event
  } = {}) {
    if (this.disabled) {
      return;
    }

    if (!targetElement) {
      targetElement = this.getElementFromRecord(eventRecord); // If record is not rendered, do nothing

      if (!targetElement) {
        return;
      }
    }

    event = event || this.createContextMenuEventForElement(targetElement);
    this.internalShowContextMenu(event);
  }

  getElementFromRecord(record) {
    return this.client.getElementsFromEventRecord(record)[0];
  }

  populateEventMenu({
    items
  }) {
    const {
      client
    } = this;
    items.deleteEvent = {
      hidden: client.readOnly
    };
    items.unassignEvent = {
      hidden: client.readOnly || client.eventStore.usesSingleAssignment
    };
  } // This generates the fixed, unchanging part of the items and is only called once
  // to generate the baseItems of the feature.
  // The dynamic parts which are set by populateEventMenu have this merged into them.

  changeItems(items) {
    const {
      client
    } = this;
    return Objects.assign({
      deleteEvent: {
        text: 'L{SchedulerBase.Delete event}',
        icon: 'b-icon b-icon-trash',
        weight: 200,

        onItem({
          menu,
          eventRecord
        }) {
          var _menu$focusInEvent;

          // We must synchronously push focus back into the menu's triggering
          // event so that the our beforeRemove handlers can move focus onwards
          // to the closest remaining event.
          // Otherwise, the menu's default hide processing on hide will attempt
          // to move focus back to the menu's triggering event which will
          // by then have been deleted.
          const revertTarget = (_menu$focusInEvent = menu.focusInEvent) === null || _menu$focusInEvent === void 0 ? void 0 : _menu$focusInEvent.relatedTarget;

          if (revertTarget) {
            revertTarget.focus();
            client.navigator.activeItem = revertTarget;
          }

          client.removeRecords([eventRecord]);
        }

      },
      unassignEvent: {
        text: 'L{SchedulerBase.Unassign event}',
        icon: 'b-icon b-icon-unassign',
        weight: 300,
        name: 'unassignEvent',

        onItem({
          menu,
          eventRecord,
          resourceRecord
        }) {
          var _menu$focusInEvent2;

          // We must synchronously push focus back into the menu's triggering
          // event so that the our beforeRemove handlers can move focus onwards
          // to the closest remaining event.
          // Otherwise, the menu's default hide processing on hide will attempt
          // to move focus back to the menu's triggering event which will
          // by then have been deleted.
          const revertTarget = (_menu$focusInEvent2 = menu.focusInEvent) === null || _menu$focusInEvent2 === void 0 ? void 0 : _menu$focusInEvent2.relatedTarget;

          if (revertTarget) {
            revertTarget.focus();
            client.navigator.activeItem = revertTarget;
          }

          eventRecord.unassign(resourceRecord);
        }

      }
    }, items);
  }

}
EventMenu.featureClass = '';
EventMenu._$name = 'EventMenu';
GridFeatureManager.registerFeature(EventMenu, true, 'Scheduler');

/**
 * @module Scheduler/feature/EventContextMenu
 */

/**
 * **DEPRECATED: This class is deprecated since v4.0.0,
 * use {@link Scheduler.feature.EventMenu EventMenu} feature instead.**
 *
 * Displays a context menu for events. Items are populated by other features and/or application code.
 *
 * This feature is **off** by default in favor of `EventMenu`.
 * For info on enabling it, see {@link Grid.view.mixin.GridFeatures}.
 *
 * @deprecated 4.0.0
 * @extends Scheduler/feature/EventMenu
 * @classtype eventContextMenu
 */

class EventContextMenu extends EventMenu {
  //region Config
  static get $name() {
    return 'EventContextMenu';
  }

  construct(scheduler, config) {
    super.construct(...arguments);
    this.scheduler = scheduler;
    VersionHelper.deprecate('Scheduler', '5.0.0', '`EventContextMenu` feature is deprecated, in favor of `EventMenu` feature. Please see https://bryntum.com/docs/scheduler/#guides/upgrades/3.1.0.md for more information.'); // If this feature is enabled, need to disable EventMenu. They are mutually exclusive.

    if (scheduler.features.eventMenu) {
      scheduler.features.eventMenu.disabled = true;
    }
  } //endregion
  //region Events

  /**
   * Fired from scheduler before the context menu is shown for an event. Allows manipulation of the items
   * to show in the same way as in `processItems`. Returning `false` from a listener prevents the menu from
   * being shown.
   * @event eventContextMenuBeforeShow
   * @deprecated 4.0.0 Renamed to eventMenuBeforeShow
   * @preventable
   * @param {Scheduler.view.Scheduler} source
   * @param {Object} items Menu item configs
   * @param {Scheduler.model.EventModel} eventRecord Event record for which the menu was triggered
   * @param {Scheduler.model.ResourceModel} resourceRecord Resource record
   * @param {Scheduler.model.AssignmentModel} assignmentRecord Assignment record, if assignments are used
   * @param {HTMLElement} eventElement
   */

  /**
   * Fired from scheduler when an item is selected in the context menu.
   * @event eventContextMenuItem
   * @deprecated 4.0.0 Renamed to eventMenuItem
   * @param {Scheduler.view.Scheduler} source
   * @param {Core.widget.MenuItem} item
   * @param {Scheduler.model.EventModel} eventRecord
   * @param {Scheduler.model.ResourceModel} resourceRecord
   * @param {Scheduler.model.AssignmentModel} assignmentRecord Assignment record, if assignments are used
   * @param {HTMLElement} eventElement
   */

  /**
   * Fired from scheduler after showing the context menu for an event
   * @event eventContextMenuShow
   * @deprecated 4.0.0 Renamed to eventMenuShow
   * @param {Scheduler.view.Scheduler} source
   * @param {Core.widget.Menu} menu The menu
   * @param {Scheduler.model.EventModel} eventRecord Event record for which the menu was triggered
   * @param {Scheduler.model.ResourceModel} resourceRecord Resource record
   * @param {Scheduler.model.AssignmentModel} assignmentRecord Assignment record, if assignments are used
   * @param {HTMLElement} eventElement
   */
  //endregion

}
EventContextMenu.featureClass = '';
EventContextMenu._$name = 'EventContextMenu';
GridFeatureManager.registerFeature(EventContextMenu);

/* eslint-disable no-unused-expressions */
/**
 * @module Scheduler/feature/EventDrag
 */
//TODO: relay events as in Dependencies. (drag -> eventdrag etc)
//TODO: shift to copy
//TODO: dragging of event that starts & ends outside of view

/**
 * Allows user to drag and drop events within the scheduler, to change startDate or resource assignment.
 *
 * This feature is **enabled** by default
 *
 * Note that changing record data during drag is not supported.
 *
 * @example
 * // constrain drag to current resource
 * let scheduler = new Scheduler({
 *   features: {
 *     eventDrag: {
 *       constrainDragToResource: true
 *     }
 *   }
 * });
 *
 * @extends Scheduler/feature/base/DragBase
 * @demo Scheduler/basic
 * @externalexample scheduler/EventDrag.js
 * @classtype eventDrag
 */

class EventDrag extends DragBase {
  //region Config
  static get $name() {
    return 'EventDrag';
  }

  static get defaultConfig() {
    return {
      /**
       * Template used to generate drag tooltip contents.
       * ```
       * const scheduler = new Scheduler({
       *   features : {
       *     eventDrag : {
       *       dragTipTemplate({eventRecord, startText}) {
       *         return `${eventRecord.name}: ${startText}`
       *       }
       *     }
       *   }
       * });
       * ```
       * @config {Function} dragTipTemplate
       * @param {Object} data Tooltip data
       * @param {Scheduler.model.EventModel} data.eventRecord
       * @param {Boolean} data.valid Currently over a valid drop target or not
       * @param {Date} data.startDate New start date
       * @param {Date} data.endDate New end date
       * @param {String} data.startText Formatted new start date
       * @param {String} data.endText Formatted new end date
       * @param {String} data.startClockHtml Pre-generated HTML to display startDate as clock/calendar
       * @param {String} data.endClockHtml Pre-generated HTML to display endDate as clock/calendar
       * @param {Object} data.dragData Detailed drag context
       * @returns {String}
       */

      /**
       * Set to true to only allow dragging events within the same resource.
       * @config {Boolean}
       * @default
       */
      constrainDragToResource: false,

      /**
       * Set to true to only allow dragging events to different resources, and disallow rescheduling by dragging.
       * @config {Boolean}
       * @default
       */
      constrainDragToTimeSlot: false,

      /**
       * An empty function by default, but provided so that you can perform custom validation on
       * the item being dragged. This function is called during the drag and drop process and also after the drop is made.
       * Return true if the new position is valid, false to prevent the drag.
       * @param {Object} context A drag drop context object
       * @param {Date} context.startDate New start date
       * @param {Date} context.endDate New end date
       * @param {Scheduler.model.AssignmentModel[]} context.assignmentRecords Assignment records which were dragged
       * @param {Scheduler.model.EventModel[]} context.draggedRecords  **Deprecated** in 4.0. Use `context.eventRecords` instead.
       * @param {Scheduler.model.EventModel[]} context.eventRecords Event records which were dragged
       * @param {Scheduler.model.ResourceModel} context.newResource New resource record
       * @param {Event} event The event object
       * @return {Boolean} `true` if this validation passes
       * @config {Function}
       */
      validatorFn: () => {},

      /**
       * The `this` reference for the validatorFn
       * @config {Object}
       */
      validatorFnThisObj: null,

      /**
       * When the host Scheduler is `{@link Scheduler.view.mixin.EventSelection#config-multiEventSelect multiEventSelect}: true`
       * then, there are two modes of dragging *within the same Scheduler*.
       *
       * Non unified means that all selected events are dragged by the same number of resource rows.
       *
       * Unified means that all selected events are collected together and dragged as one, and are all dropped
       * on the same targeted resource row at the same targeted time.
       * @config {Boolean}
       * @default false
       */
      unifiedDrag: null
    };
  } //endregion
  //region Events

  /**
   * Fired on the owning Scheduler to allow implementer to prevent immediate finalization by setting `data.context.async = true`
   * in the listener, to show a confirmation popup etc
   * ```
   *  scheduler.on('beforeeventdropfinalize', ({context}) => {
   *      context.async = true;
   *      setTimeout(() => {
   *          // async code don't forget to call finalize
   *          context.finalize();
   *      }, 1000);
   *  })
   * ```
   * Changing record data during drag is not supported.
   *
   * @event beforeEventDropFinalize
   * @param {Scheduler.view.Scheduler} source Scheduler instance
   * @param {Object} context
   * @param {Boolean} context.async Set true to handle dragdrop asynchronously (e.g. to wait for user
   * @param {Scheduler.model.EventModel} context.targetEventRecord Event record for drop target
   * @param {Scheduler.model.ResourceModel} context.newResource Resource record for drop target
   * confirmation)
   * @param {Function} context.finalize Call this method to finalize dragdrop. This method accepts one
   * argument: pass true to update records, or false, to ignore changes
   */

  /**
   * Fired on the owning Scheduler after event drop
   * @event afterEventDrop
   * @param {Scheduler.view.Scheduler} source
   * @param {Scheduler.model.AssignmentModel[]} assignmentRecords
   * @param {Scheduler.model.EventModel[]} eventRecords
   * @param {Boolean} valid
   * @param {Object} context
   */

  /**
   * Fired on the owning Scheduler when an event is dropped
   * @event eventDrop
   * @param {Scheduler.view.Scheduler} source
   * @param {Scheduler.model.EventModel[]} eventRecords
   * @param {Scheduler.model.AssignmentModel[]} assignmentRecords
   * @param {Boolean} isCopy
   * @param {Object} context
   * @param {Scheduler.model.EventModel} context.targetEventRecord Event record for drop target
   * @param {Scheduler.model.ResourceModel} context.newResource Resource record for drop target
   */

  /**
   * Fired on the owning Scheduler before event dragging starts. Return false to prevent the action
   * @event beforeEventDrag
   * @param {Scheduler.view.Scheduler} source
   * @param {Scheduler.model.EventModel} eventRecord
   * @param {Object} context
   */

  /**
   * Fired on the owning Scheduler when event dragging starts
   * @event eventDragStart
   * @param {Scheduler.view.Scheduler} source
   * @param {Scheduler.model.EventModel[]} eventRecords
   * @param {Scheduler.model.AssignmentModel[]} assignmentRecords
   * @param {Object} context
   */

  /**
   * Fired on the owning Scheduler when event is dragged
   * @event eventDrag
   * @param {Scheduler.view.Scheduler} source
   * @param {Scheduler.model.AssignmentModel[]} assignmentRecords
   * @param {Scheduler.model.EventModel[]} eventRecords
   * @param {Date} startDate
   * @param {Date} endDate
   * @param {Scheduler.model.ResourceModel} newResource
   * @param {Object} context
   */

  /**
   * Fired on the owning Scheduler after an event drag operation has been aborted
   * @event eventDragAbort
   * @param {Scheduler.view.Scheduler} source
   * @param {Scheduler.model.AssignmentModel[]} assignmentRecords
   * @param {Scheduler.model.EventModel[]} eventRecords
   * @param {Object} context
   */
  //endregion
  //region Data layer
  // Deprecated. Use this.client instead

  get scheduler() {
    return this.client;
  } //endregion
  //region Drag events

  isElementDraggable(el, event) {
    const {
      scheduler
    } = this,
          eventElement = DomHelper.up(el, scheduler.eventSelector),
          {
      eventResize
    } = scheduler.features;

    if (!eventElement || this.disabled) {
      return false;
    } // displaying something resizable within the event?

    if (el.matches('[class$="-handle"]')) {
      return false;
    }

    const eventRecord = scheduler.resolveEventRecord(eventElement); // using EventResize and over a virtual handle?
    // Milestones cannot be resized

    return !(eventResize && eventRecord.resizable && !eventRecord.isMilestone && eventResize.resize.overAnyHandle(event, eventElement));
  }

  getTriggerParams(dragData) {
    const {
      assignmentRecords,
      eventRecords
    } = dragData;
    return {
      context: dragData,
      // `draggedRecords` is deprecated
      draggedRecords: eventRecords,
      eventRecords,
      assignmentRecords
    };
  }

  triggerEventDrag(dragData, start) {
    // If there has been a change...
    if (dragData.startDate - start !== 0 || dragData.newResource !== dragData.resourceRecord) {
      this.scheduler.trigger('eventDrag', Object.assign(this.getTriggerParams(dragData), {
        startDate: dragData.startDate,
        endDate: dragData.endDate,
        newResource: dragData.newResource
      }));
    }
  }

  triggerDragStart(dragData) {
    this.scheduler.trigger('eventDragStart', this.getTriggerParams(dragData));
  }

  triggerDragAbort(dragData) {
    this.scheduler.trigger('eventDragAbort', this.getTriggerParams(dragData));
  }

  triggerAfterDrop(dragData, valid) {
    this.currentOverClient.trigger('afterEventDrop', Object.assign(this.getTriggerParams(dragData), {
      valid
    }));
  }

  onDragStart({
    context,
    event
  }) {
    const {
      eventMenu,
      eventContextMenu // TODO: 'eventContextMenu' is deprecated. Please see https://bryntum.com/docs/scheduler/#guides/upgrades/4.0.0.md for more information.

    } = this.client.features,
          eventContextMenuFeature = eventMenu && !eventMenu.disabled ? eventMenu : eventContextMenu;
    super.onDragStart({
      context,
      event
    }); // If this is a touch action, hide the context menu which may have shown

    eventContextMenuFeature === null || eventContextMenuFeature === void 0 ? void 0 : eventContextMenuFeature.hideContextMenu(false);
  }

  onDragAbort() {
    // Edge case: If this drag was a no-op, and underlying data was changed while drag was ongoing (e.g. web socket
    // push), we need to manually force a view refresh to ensure a correct render state
    if (this.dragData.initialAssignmentsState.find(({
      resource
    }, i) => resource.id !== this.dragData.assignmentRecords[i].resourceId)) {
      this.client.refresh();
    }

    super.onDragAbort(...arguments);
  } //endregion
  //region Finalization & validation

  /**
   * Checks if an event can be dropped on the specified resource.
   * @private
   * @returns {Boolean} Valid (true) or invalid (false)
   */

  isValidDrop(dragData) {
    const {
      newResource,
      resourceRecord
    } = dragData,
          sourceRecord = dragData.draggedEntities[0]; // Not allowed to drop an event to group header

    if (!newResource || newResource.isSpecialRow) {
      return false;
    } // Not allowed to assign an event twice to the same resource

    if (resourceRecord !== newResource) {
      return !sourceRecord.event.resources.includes(newResource);
    }

    return true;
  }

  checkDragValidity(dragData, event) {
    const me = this,
          scheduler = me.currentOverClient;
    let result = me.dragData.context.valid;

    if (result) {
      // First make sure DragHelper thinks it's a valid drag, then scheduler domain checks
      if (!scheduler.allowOverlap && !scheduler.isDateRangeAvailable(dragData.startDate, dragData.endDate, dragData.draggedEntities[0], dragData.newResource)) {
        result = {
          valid: false,
          message: me.L('L{eventOverlapsExisting}')
        };
      } else {
        result = me.validatorFn.call(me.validatorFnThisObj || me, dragData, event);
      }
    }

    return result;
  } //endregion
  //region Update records

  /**
   * Update events being dragged.
   * @private
   * @param context Drag data.
   * @async
   */

  async updateRecords(context) {
    const me = this,
          fromScheduler = me.scheduler,
          toScheduler = me.currentOverClient,
          copyKeyPressed = false;
    fromScheduler.eventStore.suspendAutoCommit();
    toScheduler.eventStore.suspendAutoCommit();
    const result = await me.updateAssignments(fromScheduler, toScheduler, context, copyKeyPressed);
    fromScheduler.eventStore.resumeAutoCommit();
    toScheduler.eventStore.resumeAutoCommit(); // Tell the world there was a successful drop

    toScheduler.trigger('eventDrop', Object.assign(me.getTriggerParams(context), {
      isCopy: copyKeyPressed,
      event: context.browserEvent,
      targetEventRecord: context.targetEventRecord,
      targetResourceRecord: context.newResource
    }));
    return result;
  }
  /**
   * Update assignments being dragged
   * @private
   * @async
   */

  async updateAssignments(fromScheduler, toScheduler, context, copy) {
    // The code is written to emit as few store events as possible
    const me = this,
          isCrossScheduler = fromScheduler !== toScheduler,
          isVertical = toScheduler.mode === 'vertical',
          {
      assignmentStore: fromAssignmentStore,
      eventStore: fromEventStore,
      resourceStore
    } = fromScheduler,
          {
      assignmentStore: toAssignmentStore,
      eventStore: toEventStore,
      resourceStore: toResourceStore
    } = toScheduler,
          {
      eventRecords,
      assignmentRecords,
      timeDiff,
      initialAssignmentsState,
      resourceRecord: fromResource,
      newResource: toResource
    } = context,
          unifiedDrag = me.unifiedDrag,
          event1Date = me.adjustStartDate(assignmentRecords[0].event.startDate, timeDiff),
          eventsToAdd = [],
          eventsToRemove = [],
          assignmentsToAdd = [],
          assignmentsToRemove = [],
          eventsToCheck = [],
          resourcesToRefresh = new Set(),
          eventsToBatch = new Set();
    fromScheduler.suspendRefresh();
    toScheduler.suspendRefresh();
    let updated = false,
        updatedEvent = false,
        indexDiff; // By how many resource rows has the drag moved.

    if (isCrossScheduler) {
      // the difference in indexes via first dragged event will help us to find resources for all the rest events accordingly
      indexDiff = toResourceStore.indexOf(toResource) - resourceStore.indexOf(fromResource);
    } else {
      indexDiff = me.constrainDragToResource ? 0 : resourceStore.indexOf(fromResource) - resourceStore.indexOf(toResource);
    }

    if (isVertical) {
      // TODO : Broken after merge, figure it out
      eventRecords.forEach((draggedEvent, i) => {
        const eventBar = context.eventBarEls[i];
        delete draggedEvent.instanceMeta(fromScheduler).hasTemporaryDragElement; // If it was created by a call to scheduler.currentOrientation.addTemporaryDragElement
        // then release it back to be available to DomSync next time the rendered event block
        // is synced.

        if (eventBar.dataset.transient) {
          eventBar.remove();
        }
      });
    } // Using for to support await inside

    for (let i = 0; i < assignmentRecords.length; i++) {
      // Reassigned when dropped on other scheduler, thus not const
      let draggedAssignment = assignmentRecords[i],
          draggedEvent = draggedAssignment.event;
      const initialState = initialAssignmentsState[i],
            eventBar = context.eventBarEls[i],
            originalEventRecord = draggedEvent,
            originalStartDate = initialState.startDate,
            // grabbing resource early, since after ".copy()" the record won't belong to any store
      // and ".getResources()" won't work. If it's a move to another scheduler, ensure the
      // array still has a length. The process function will do an assign as opposed
      // to a reassign
      originalResourceRecord = initialState.resource,
            // Calculate new startDate (and round it) based on timeDiff up here, might be added to another
      // eventstore below in which case it is invalidated. But this is anyway the target date
      newStartDate = this.constrainDragToTimeSlot ? originalStartDate : unifiedDrag ? event1Date : me.adjustStartDate(originalStartDate, timeDiff);
      resourcesToRefresh.add(originalResourceRecord); // If changing resource, the element wont be found in SchedulerStores#onEventBeforeCommit and thus the
      // committing cls wont be applied. Apply it here, in case we are using a backend. If we are not, it will be
      // replaced anyway on the immediate redraw

      eventBar.querySelector(fromScheduler.eventInnerSelector).classList.add(fromScheduler.committingCls);

      if (copy) {
        draggedAssignment = draggedAssignment.copy(null);
        assignmentsToAdd.push(draggedAssignment);
      } // Dropped on another scheduler, not sharing assignmentStore
      else if (fromAssignmentStore !== toAssignmentStore) {
          const // Single assignment from a multi assigned event dragged over, event needs to be copied over
          keepEvent = originalEventRecord.assignments.length > 1,
                newAssignment = draggedAssignment.copy(); // Remove assignment from source scheduler

          assignmentsToRemove.push(draggedAssignment); // If it was the last assignment, the event should also be removed

          if (!keepEvent) {
            eventsToRemove.push(originalEventRecord);
          } // If event does not already exist in target scheduler a copy is added

          if (!toEventStore.getById(originalEventRecord.id)) {
            // Clone keeping the same id
            const clonedData = Object.assign({}, originalEventRecord.data, {
              id: originalEventRecord.id,
              // Engine gets mad if not nulled
              calendar: null
            });
            draggedEvent = clonedData;
            eventsToAdd.push(clonedData);
          } // And add it to the target scheduler
          // TODO: Should be handled on the data layer ideally

          if (!toEventStore.usesSingleAssignment) {
            assignmentsToAdd.push(newAssignment);
          }

          draggedAssignment = newAssignment;
        }

      let newResource = toResource;

      if (!unifiedDrag) {
        if (!isCrossScheduler) {
          // If not dragging events as a unified block, distribute each to a new resource
          // using the same offset as the dragged event.
          if (indexDiff !== 0) {
            const newIndex = Math.max(Math.min(resourceStore.indexOf(originalResourceRecord) - indexDiff, resourceStore.count - 1), 0);
            newResource = resourceStore.getAt(newIndex);
          } else {
            newResource = originalResourceRecord;
          }
        } // we have a resource for first dragged event in toResource
        else if (i > 0) {
            const draggedEventResourceIndex = originalResourceRecord.resourceStore.indexOf(originalResourceRecord);
            newResource = newResource.resourceStore.getAt(draggedEventResourceIndex + indexDiff) || newResource;
          }
      }

      resourcesToRefresh.add(newResource); // Cannot rely on assignment generation to detect update, since it might be a new assignment

      if (draggedAssignment.resource !== newResource) {
        draggedAssignment.resource = newResource; // Actual events should be batched, not data for new events when dragging between

        draggedEvent.isEvent && eventsToBatch.add(draggedEvent);
        updated = true; // When dragging an occurrence, the assignment is only temporary. We have to tag the newResource along
        // to be picked up by the occurrence -> event conversion
        // TODO: A hack, figure a better way out

        if (draggedEvent.isOccurrence) {
          draggedEvent.set('newResource', newResource);
        } // // TODO: Should be handled on the datalayer somehow, but it is kind of edge casey

        if (isCrossScheduler && toEventStore.usesSingleAssignment) {
          // In single assignment mode, when dragged to another scheduler it will not copy the assignment
          // over but instead set the resourceId of the event. To better match expected behaviour
          draggedEvent.resourceId = newResource.id;
        }
      } // Same for event

      if (!eventsToCheck.find(ev => ev.draggedEvent === draggedEvent) && !DateHelper.isEqual(draggedEvent.startDate, newStartDate)) {
        var _draggedEvent$resourc;

        draggedEvent.startDate = newStartDate;
        eventsToCheck.push({
          draggedEvent,
          originalStartDate
        });
        draggedEvent.isEvent && eventsToBatch.add(draggedEvent);
        updatedEvent = true; // When changing date, all assignments are affected so might need to redraw more resources
        // (might be a clone, no resources then)

        (_draggedEvent$resourc = draggedEvent.resources) === null || _draggedEvent$resourc === void 0 ? void 0 : _draggedEvent$resourc.forEach(resource => resourcesToRefresh.add(resource));
      }
    }

    fromAssignmentStore.remove(assignmentsToRemove);
    fromEventStore.remove(eventsToRemove);
    toAssignmentStore.add(assignmentsToAdd);
    const addedEvents = toEventStore.add(eventsToAdd); // When not constrained to timeline we are dragging a clone and need to manually do some cleanup if
    // dropped in view

    if (!me.constrainDragToTimeline) {
      // go through assignmentRecords again after events has been added to toEventStore (if any)
      // now we have updated assignment ids and can properly reuse event HTML elements
      for (let i = 0; i < assignmentRecords.length; i++) {
        const originalDraggedEvent = assignmentRecords[i].event,
              // try to get dragged event from addedEvents array, it will be there with updated ids
        // if toScheduler is different
        draggedEvent = (addedEvents === null || addedEvents === void 0 ? void 0 : addedEvents.find(r => r.id === originalDraggedEvent.id)) || originalDraggedEvent,
              eventBar = context.eventBarEls[i],
              element = i === 0 ? context.context.element : context.context.relatedElements[i - 1],
              // Determine if in time axis here also, since the records date might be invalidated further below
        inTimeAxis = toScheduler.isInTimeAxis(draggedEvent); // Remove original element properly

        DomSync.removeChild(fromScheduler.foregroundCanvas, eventBar);

        if (draggedEvent.resource && toScheduler.rowManager.getRowFor(draggedEvent.resource) && inTimeAxis) {
          const elRect = Rectangle.from(element, toScheduler.foregroundCanvas, true); // Ensure that after inserting the dragged element clone into the toScheduler's foregroundCanvas
          // it's at the same visual position that it was dragged to.

          DomHelper.setTranslateXY(element, elRect.x, elRect.y);
          element.classList.remove('b-sch-event-hover');
          element.classList.remove('b-active');
          element.classList.remove('b-drag-proxy');
          element.classList.remove('b-dragging');
          element.retainElement = false; // Add element properly, so that DomSync will reuse it on next update

          DomSync.addChild(toScheduler.foregroundCanvas, element, draggedEvent.assignments[0].id);
        }
      }
    }

    addedEvents === null || addedEvents === void 0 ? void 0 : addedEvents.forEach(added => eventsToBatch.add(added)); // addedEvents order is the same with [context.element, ..context.relatedElements]
    // Any added or removed events or assignments => something changed

    if (assignmentsToRemove.length || eventsToRemove.length || assignmentsToAdd.length || eventsToAdd.length) {
      updated = true;
    } // Commit changes to affected projects

    if (updated || updatedEvent) {
      // By batching event changes when using single assignment we avoid two updates, without it there will be one
      // for date change and one when changed assignment updates resourceId on the event
      toEventStore.usesSingleAssignment && eventsToBatch.forEach(eventRecord => eventRecord.beginBatch());
      await Promise.all([toScheduler.project !== fromScheduler.project ? toScheduler.project.commitAsync() : null, fromScheduler.project.commitAsync()]); // End batch in engine friendly way, avoiding to have `set()` trigger another round of calculations

      toEventStore.usesSingleAssignment && eventsToBatch.forEach(eventRecord => eventRecord.endBatch(false, true));
    }

    if (!updated) {
      // Engine might have reverted the date change, in which case this should be considered an invalid op
      updated = eventsToCheck.some(({
        draggedEvent,
        originalStartDate
      }) => !DateHelper.isEqual(draggedEvent.startDate, originalStartDate));
    } // Resumes self twice if not cross scheduler, but was suspended twice above also so all good

    toScheduler.resumeRefresh();
    fromScheduler.resumeRefresh();

    if (!updated) {
      context.valid = false;
    } else {
      // Not doing full refresh above, to allow for animations
      toScheduler.refreshWithTransition();

      if (isCrossScheduler) {
        fromScheduler.refreshWithTransition();
      }
    }
  } //endregion
  //region Drag data

  getProductDragContext(dragData) {
    const targetEventRecord = this.scheduler.resolveEventRecord(dragData.browserEvent.target);
    let newResource;

    if (this.constrainDragToResource) {
      newResource = dragData.resourceRecord;
    } else if (!this.constrainDragToTimeline) {
      // If we're dragging freely on the page, require to drag onto a resource row always
      newResource = this.resolveResource();
    } else {
      newResource = this.resolveResource() || dragData.newResource || dragData.resourceRecord;
    }

    const {
      assignmentRecords,
      eventRecords
    } = dragData;
    return {
      valid: Boolean(newResource),
      // `draggedRecords` is deprecated
      draggedRecords: eventRecords,
      eventRecords,
      assignmentRecords,
      newResource,
      targetEventRecord
    };
  }

  setupProductDragData(info) {
    const me = this,
          {
      scheduler
    } = me,
          element = info.grabbed,
          eventRecord = scheduler.resolveEventRecord(element),
          resourceRecord = scheduler.resolveResourceRecord(element),
          assignmentRecord = scheduler.resolveAssignmentRecord(element),
          eventRegion = Rectangle.from(element),
          draggedEntities = [assignmentRecord],
          eventBarEls = [];

    if (me.constrainDragToResource && !resourceRecord) {
      throw new Error('Resource could not be resolved for event: ' + eventRecord.id);
    }

    const dateConstraints = scheduler.getDateConstraints(me.constrainDragToResource ? resourceRecord : null, eventRecord);

    if (me.constrainDragToTimeline) {
      me.setupConstraints(scheduler.getScheduleRegion(me.constrainDragToResource ? resourceRecord : null, eventRecord), eventRegion, scheduler.timeAxisViewModel.snapPixelAmount, Boolean(dateConstraints));
    } // We multi drag other selected events if multiEventSelect is set and
    // (the dragged event is already selected, or the ctrl key is pressed)

    if (scheduler.multiEventSelect && (scheduler.isAssignmentSelected(draggedEntities[0]) || me.drag.startEvent.ctrlKey)) {
      draggedEntities.push.apply(draggedEntities, me.getRelatedRecords(assignmentRecord));
    } // Collecting all elements to drag

    draggedEntities.forEach(assignment => {
      let eventBarEl = scheduler.getElementFromAssignmentRecord(assignment);

      if (!eventBarEl) {
        eventBarEl = scheduler.currentOrientation.addTemporaryDragElement(assignment.event, assignment.resource);
        eventBarEl = eventBarEl.innerElement;
      }

      eventBarEls.push(eventBarEl);
    }); // What is dragged is the wrapper.

    eventBarEls.forEach((el, i) => eventBarEls[i] = el.parentNode);
    return {
      record: assignmentRecord,
      dateConstraints,
      eventBarEls,
      draggedEntities
    };
  }
  /**
   * Initializes drag data (dates, constraints, dragged events etc). Called when drag starts.
   * @private
   * @param info
   * @param event
   * @returns {*}
   */

  getDragData(info, event) {
    const dragData = super.getDragData(info, event),
          assignmentRecords = dragData.draggedEntities,
          eventRecords = [...new Set(assignmentRecords.map(assignment => assignment.event))];
    return ObjectHelper.assign(dragData, {
      resourceRecord: this.scheduler.resolveResourceRecord(info.grabbed),
      initialAssignmentsState: assignmentRecords.map(as => ({
        startDate: as.event.startDate,
        resource: as.resource
      })),
      eventRecords,
      assignmentRecords
    });
  }
  /**
   * Provide your custom implementation of this to allow additional selected records to be dragged together with the original one.
   * @param {Scheduler.model.AssignmentModel} assignmentRecord The assignment about to be dragged
   * @return {Scheduler.model.AssignmentModel[]} An array of assignment records to drag together with the original
   */

  getRelatedRecords(assignmentRecord) {
    return this.scheduler.selectedAssignments.filter(selectedRecord => selectedRecord !== assignmentRecord && selectedRecord.event.isDraggable);
  }
  /**
   * Get correct axis coordinate depending on schedulers mode (horizontal -> x, vertical -> y). Also takes milestone
   * layout into account.
   * @private
   * @param {Scheduler.model.EventModel} eventRecord Record being dragged
   * @param {HTMLElement} element Element being dragged
   * @param {Number[]} coord XY coordinates
   * @returns {Number|Number[]} X,Y or XY
   */

  getCoordinate(eventRecord, element, coord) {
    const scheduler = this.currentOverClient;

    if (scheduler.isHorizontal) {
      let x = coord[0]; // Adjust coordinate for milestones if using a layout mode, since they are aligned differently than events

      if (scheduler.milestoneLayoutMode !== 'default' && eventRecord.isMilestone) {
        switch (scheduler.milestoneAlign) {
          case 'center':
            x += element.offsetWidth / 2;
            break;

          case 'end':
            x += element.offsetWidth;
            break;
        }
      }

      return x;
    } else {
      let y = coord[1]; // Adjust coordinate for milestones if using a layout mode, since they are aligned differently than events

      if (scheduler.milestoneLayoutMode !== 'default' && eventRecord.isMilestone) {
        switch (scheduler.milestoneAlign) {
          case 'center':
            y += element.offsetHeight / 2;
            break;

          case 'end':
            y += element.offsetHeight;
            break;
        }
      }

      return y;
    }
  }
  /**
   * Get resource record occluded by the drag proxy.
   * @private
   * @returns {Scheduler.model.ResourceModel}
   */

  resolveResource() {
    const element = this.dragData.dragProxy || this.dragData.context.element,
          client = this.currentOverClient,
          {
      isHorizontal
    } = client,
          {
      context
    } = this.dragData,
          // Page coords for elementFromPoint
    pageRect = Rectangle.from(element, null, true),
          y = client.isVertical || this.unifiedDrag ? context.clientY : pageRect.center.y,
          // Local coords to resolve resource in vertical
    localRect = Rectangle.from(element, client.timeAxisSubGridElement, true),
          {
      x: lx,
      y: ly
    } = localRect.center;
    let resource; // This is benchmarked as the fastest way to find a Grid Row from a viewport Y coordinate
    // so use it in preference to elementFromPoint (which causes a forced synchronous layout) in horisontal mode.

    if (isHorizontal) {
      const row = client.rowManager.getRowAt(y);
      resource = row && client.resourceStore.getAt(row.dataIndex);
    } else {
      // In vertical mode, just use the X coordinate to find out which resource we are under.
      // The method requires that a .b-sch-timeaxis-cell element be passed.
      // There is only one in vertical mode, so use that.
      resource = client.resolveResourceRecord(client.timeAxisSubGridElement.querySelector('.b-sch-timeaxis-cell'), [lx, ly]);
    }

    return resource;
  } //endregion
  //region Other stuff

  adjustStartDate(startDate, timeDiff) {
    const scheduler = this.currentOverClient;
    return scheduler.timeAxis.roundDate(new Date(startDate - 0 + timeDiff), scheduler.snapRelativeToEventStartDate ? startDate : false);
  }

  resolveStartEndDates(proxyRect) {
    const scheduler = this.currentOverClient,
          {
      timeAxis
    } = scheduler,
          dd = this.dragData; // Non continuous time axis will return null instead of date for a rectangle outside of the view unless
    // told to estimate date

    let {
      start: startDate,
      end: endDate
    } = scheduler.getStartEndDatesFromRectangle(proxyRect, 'round', dd.duration, !timeAxis.isContinuous); // if dragging is out of timeAxis rect bounds, we will not be able to get dates

    if (startDate && endDate) {
      if (!dd.startsOutsideView) {
        // Make sure we didn't target a start date that is filtered out, if we target last hour cell (e.g. 21:00) of
        // the time axis, and the next tick is 08:00 following day. Trying to drop at end of 21:00 cell should target start of next cell
        if (startDate && !scheduler.timeAxis.dateInAxis(startDate, false)) {
          const tick = scheduler.timeAxis.getTickFromDate(startDate);

          if (tick >= 0) {
            startDate = scheduler.timeAxis.getDateFromTick(tick);
          }
        }

        endDate = startDate && DateHelper.add(startDate, dd.duration, 'ms');
      } else if (!dd.endsOutsideView) {
        startDate = endDate && DateHelper.add(endDate, -dd.duration, 'ms');
      }
    }

    return {
      startDate,
      endDate
    };
  }

  getRecordElement(assignmentRecord) {
    return this.scheduler.getElementFromAssignmentRecord(assignmentRecord, true);
  } //endregion

}
EventDrag._$name = 'EventDrag';
GridFeatureManager.registerFeature(EventDrag, true, 'Scheduler');

/**
 * @module Scheduler/feature/base/DragCreateBase
 */
// TODO: add hovertip if EvenDrag feature not used
// TODO: some of the code here could be shared with EventResize. make common base class?
// TODO: touch support
// TODO: mouse up outside not working as it should

/**
 * Base class for EventDragCreate (Scheduler) and TaskDragCreate (Gantt) features. Contains shared code. Not to be used directly.
 *
 * @extends Core/mixin/InstancePlugin
 */

class DragCreateBase extends InstancePlugin {
  //region Config
  static get defaultConfig() {
    return {
      /**
       * true to show a time tooltip when dragging to create a new event
       * @config {Boolean}
       * @default
       */
      showTooltip: true,

      /**
       * Number of pixels the drag target must be moved before dragging is considered to have started. Defaults to 2.
       * @config {Number}
       * @default
       */
      dragTolerance: 2,
      // used by gantt to only allow one task per row
      preventMultiple: false,
      validatorFn: () => {},

      /**
       * `this` reference for the validatorFn
       * @config {Object}
       */
      validatorFnThisObj: null,

      /**
       * CSS class to add to proxy used when creating a new event
       * @config {String}
       * @default
       * @private
       */
      proxyCls: 'b-sch-dragcreator-proxy',
      tipTemplate: data => `
                <div class="b-sch-tip-${data.valid ? 'valid' : 'invalid'}">
                    ${data.startClockHtml}
                    ${data.endClockHtml}
                    <div class="b-sch-tip-message">${data.message}</div>
                </div>
            `
    };
  } // Plugin configuration. This plugin chains some of the functions in Grid.

  static get pluginConfig() {
    return {
      chain: ['onElementMouseDown', 'onElementMouseMove'],
      before: ['onElementContextMenu']
    };
  }

  doDestroy() {
    var _this$tip, _this$resize;

    (_this$tip = this.tip) === null || _this$tip === void 0 ? void 0 : _this$tip.destroy();
    (_this$resize = this.resize) === null || _this$resize === void 0 ? void 0 : _this$resize.destroy();
    super.doDestroy();
  } //endregion
  //region Proxy element & resizing

  /**
   * Adds a proxy element to illustrate the timespan that might be created
   * @param {Object} config
   * @param {Core.data.Model} config.rowRecord  The row for which an event proxy element is being created.
   * @param {Number} config.startX the position along the time axis at which to display the proxy element.
   * @param {Number} [config.width] the width of the proxy element.
   * @private
   */

  addProxy(config) {
    const me = this,
          {
      client
    } = me,
          {
      barMargin
    } = client,
          region = client.currentOrientation.getRowRegion(config.rowRecord);
    let width, height, proxyX, proxyY;

    if (client.isHorizontal) {
      proxyX = 'currentX' in config ? Math.min(config.startX, config.currentX + 1) : config.startX;
      proxyY = region.y + barMargin;
      width = config.width || me.dragTolerance;
      height = region.height - barMargin * 2;
    } else {
      proxyX = region.x + barMargin;
      proxyY = 'currentY' in config ? Math.min(config.startY, config.currentY + 1) : config.startY;
      width = region.width - barMargin * 2;
      height = config.height || me.dragTolerance;
    } // This CSS class is to block further drag creates when one is in progress (like awaiting async finalization)

    client.element.classList.add('b-dragcreating'); // This CSS class is to block hover for other events during actually dragging the proxy

    client.element.classList.add('b-dragcreating-proxy-sizing'); // We are dragging to size element from nothing. Its size on drag is calculated by its *initial size*, plus the
    // mouse movement delta, so it MUST start at zero for that to yield the correct result.

    return me.proxy = DomHelper.createElement({
      parent: client.foregroundCanvas,
      className: me.proxyCls,
      style: `transform:translate(${proxyX}px, ${proxyY}px);width:${width}px;height:${height}px`,
      // Prevent element from being recycled by DomHelper.sync()
      retainElement: true
    });
  }
  /**
   * Remove proxy element and clean up related stuff
   * @private
   */

  removeProxy() {
    const me = this;

    if (me.proxy) {
      var _me$tip;

      me.proxy.remove();
      me.resize.destroy();
      me.proxy = me.resize = null; // This CSS class is to block further drag creates when one is in progress (like awaiting async finalization)

      me.client.element.classList.remove('b-dragcreating'); // This CSS class is to block hover for other events during actually dragging the proxy

      me.client.element.classList.remove('b-dragcreating-proxy-sizing');
      (_me$tip = me.tip) === null || _me$tip === void 0 ? void 0 : _me$tip.hide();
    }
  }
  /**
   * Creates an instance of ResizeHelper used to resize the proxy element
   * @param {Event} event
   * @param {Object} data
   * @private
   */

  initResizer(event, data) {
    var _me$resize;

    const me = this,
          {
      client
    } = me;
    let edge;

    if (client.isHorizontal) {
      edge = data.currentX > data.startX ? 'right' : 'left';
    } else {
      edge = data.currentY > data.startY ? 'bottom' : 'top';
    }

    (_me$resize = me.resize) === null || _me$resize === void 0 ? void 0 : _me$resize.destroy();
    me.resize = new ResizeHelper({
      name: me.constructor.name,
      // For debugging
      direction: client.isVertical ? 'vertical' : 'horizontal',
      isTouch: event.type.startsWith('touch'),
      dragThreshold: me.dragTolerance,
      grab: {
        element: me.proxy,
        edge,
        event: data.startEvent
      },
      allowEdgeSwitch: true,
      // Means that they can switch edges; the mouse can cross the zero point and drag the other way
      outerElement: client.timeAxisSubGridElement,
      // Constrain resize to view
      scrollManager: client.scrollManager,
      scroller: client.timeAxisSubGrid.scrollable,
      // Scroll in both directions
      listeners: {
        resizing: 'onResizing',
        resize: 'onResize',
        cancel: 'onCancel',
        thisObj: me
      }
    });
    me.resize.context.resource = data.resource;
  } //endregion
  //region Tooltip

  /**
   * Creates a tooltip that displays start & end dates. Anchored to the proxy element
   * @private
   */

  initTooltip() {
    const me = this,
          {
      client
    } = me;

    if (me.showTooltip) {
      if (me.tip) {
        me.tip.showBy(me.getTooltipTarget());
      } else {
        me.clockTemplate = new ClockTemplate({
          scheduler: client
        });
        me.tip = new Tooltip({
          id: `${client.id}-drag-create-tip`,
          autoShow: true,
          trackMouse: false,
          updateContentOnMouseMove: true,
          getHtml: me.getTipHtml.bind(me),
          align: client.isVertical ? 't-b' : 'b100-t100',
          hideDelay: 0,
          axisLock: true // Don't want it flipping to the side where we are dragging

        });
        me.tip.on('innerhtmlupdate', me.updateDateIndicator, me);
      }
    }
  }

  updateDateIndicator() {
    const me = this,
          {
      startDate,
      endDate
    } = me.createContext,
          {
      tip
    } = me,
          endDateElement = tip.element.querySelector('.b-sch-tooltip-enddate');

    if (startDate && endDate) {
      me.clockTemplate.updateDateIndicator(tip.element, startDate);
      endDateElement && me.clockTemplate.updateDateIndicator(endDateElement, endDate);
    }
  }
  /**
   * Updates the tooltip contents
   * @returns {*}
   */

  getTipHtml() {
    if (!this.resize.context) {
      return;
    } // keeping this on separate row to not mess up look of other declarations

    const me = this,
          {
      client
    } = me,
          {
      edge,
      newX,
      newY,
      elementStartX,
      elementStartY,
      elementWidth,
      elementHeight,
      newWidth,
      newHeight,
      valid,
      message
    } = me.resize.context;
    let start, end;

    if (client.isHorizontal) {
      const x = edge === 'left' ? newX : elementStartX,
            // Coordinates are 0 based, widths are not so subtracting 1 from the widths
      endX = Math.min(Math.max(x + (newWidth || elementWidth) - 1, 0), client.timeAxisViewModel.totalSize - 1);
      start = client.getDateFromCoordinate(x, 'round', true);
      end = client.getDateFromCoordinate(endX, 'round', true);
    } else {
      const y = edge === 'top' ? newY : elementStartY,
            // Coordinates are 0 based, widths are not so subtracting 1 from the widths
      endY = Math.min(Math.max(y + (newHeight || elementHeight) - 1, 0), client.timeAxisViewModel.totalSize - 1);
      start = client.getDateFromCoordinate(y, 'round', true);
      end = client.getDateFromCoordinate(endY, 'round', true);
    }

    const first = DateHelper.min(start, end),
          last = DateHelper.max(start, end),
          startText = first && client.getFormattedDate(first),
          endText = last && end && client.getFormattedEndDate(last, first);
    return me.tipTemplate({
      startDate: first,
      endDate: last,
      startClockHtml: me.clockTemplate.template({
        date: first,
        text: startText,
        cls: 'b-sch-tooltip-startdate'
      }),
      endClockHtml: me.clockTemplate.template({
        // actual end date and display date may differ
        date: client.getDisplayEndDate(last, first),
        text: endText,
        cls: 'b-sch-tooltip-enddate'
      }),
      valid,
      startText,
      endText,
      message
    });
  } //endregion
  //region Finalize (create EventModel)

  finalize(doCreate) {
    const me = this;

    const resetFinalization = () => {
      me.proxy.retainElement = false;
      me.reset();
    };

    const completeFinalization = () => {
      me.client.trigger('afterDragCreate', {
        proxyElement: me.proxy
      });
      resetFinalization();
    };

    if (doCreate) {
      // Call product specific implementation
      const result = me.finalizeDragCreate(me.createContext);

      if (ObjectHelper.isPromise(result)) {
        result.then(completeFinalization, resetFinalization);
      } else {
        completeFinalization();
      }
    } else {
      completeFinalization();
    }
  } //endregion
  //region Events

  onElementMouseDown(event) {
    const me = this,
          {
      client
    } = me; // detect mouse down directly in scheduler cell

    if (event.button === 0 && event.target.matches('.b-sch-timeaxis-cell') && !me.disabled && !client.readOnly) {
      const rowRecord = client.isVertical ? client.resolveResourceRecord(event) : client.getRecordFromElement(event.target); // no drag creation in group headers etc.

      if (rowRecord.isSpecialRow) {
        return;
      }

      if (me.preventMultiple && !me.isRowEmpty(rowRecord)) {
        return;
      }

      me.monitorMove = true;
      me.createContext = {
        startEvent: event,
        startScreenX: event.screenX,
        startScreenY: event.screenY,
        startX: event.offsetX,
        startY: event.offsetY,
        cellElement: event.target,
        rowRecord
      };
      me.mouseUpDetacher = EventHelper.on({
        element: document.body,
        mouseup: 'onMouseUp',
        once: true,
        thisObj: this
      });
    }
  }

  onElementMouseMove(event) {
    const me = this;

    if (me.monitorMove) {
      const {
        createContext,
        client
      } = me,
            delta = client.isVertical ? createContext.startScreenY - event.screenY : createContext.startScreenX - event.screenX;

      if (Math.abs(delta) > me.dragTolerance) {
        const dateTime = client.getDateFromDomEvent(event),
              region = Rectangle.from(client.timeAxisSubGridElement, null, true);

        if (me.handleBeforeDragCreate(dateTime, event) === false) {
          me.monitorMove = false;
          return;
        } // Math.max with 0 because if the mousedown is within dragTolerance of
        // the left edge and then drag goes left, dragging can begin with
        // the X in negative territory.

        createContext.currentX = Math.max(event.clientX - region.x + client.timeAxisSubGrid.scrollable.x, 0);
        createContext.currentY = Math.max(event.clientY - region.y + client.timeAxisSubGrid.scrollable.y, 0);
        me.addProxy(me.createContext);
        me.initResizer(event, me.createContext);
        me.initTooltip();
        me.monitorMove = false;
        client.trigger('dragCreateStart', {
          proxyElement: me.proxy
        });
      }
    } // If dragcreate is async, resizer was already reset and we should not
    // align tooltip anymore

    if (me.proxy && me.showTooltip && me.resize.context) {
      me.tip.alignTo(me.getTooltipTarget());
    }
  }

  getTooltipTarget() {
    const me = this,
          target = Rectangle.from(me.proxy, null, true);

    if (me.client.isVertical) {
      // Align to the dragged edge of the proxy, and then bump bottom so that the anchor aligns perfectly.
      if (me.resize.context.edge === 'bottom') {
        target.y = target.bottom - 1;
      }

      target.height = me.tip.anchorSize[1] / 2;
    } else {
      // Align to the dragged edge of the proxy, and then bump right so that the anchor aligns perfectly.
      if (me.resize.context.edge === 'right') {
        target.x = target.right - 1;
      }

      target.width = me.tip.anchorSize[0] / 2;
    }

    return {
      target
    };
  }

  onMouseUp() {
    const me = this;
    me.client.element.classList.remove('b-dragcreating-proxy-sizing');
    me.monitorMove = false;
  }
  /**
   * Prevent right click when drag creating
   * @returns {Boolean}
   * @private
   */

  onElementContextMenu() {
    if (this.proxy) {
      return false;
    }
  }

  onResizing({
    context,
    event
  }) {
    const me = this,
          {
      client
    } = me,
          x = context.edge === 'left' ? context.newX : context.elementStartX,
          y = context.edge === 'top' ? context.newY : context.elementStartY,
          // Coordinates are 0 based, widths are not so subtracting 1 from the widths
    endX = Math.min(x + Math.max((context.newWidth || context.elementWidth) - 1, 0), client.timeAxisViewModel.totalSize - 1),
          endY = Math.min(y + Math.max((context.newHeight || context.elementHeight) - 1, 0), client.timeAxisViewModel.totalSize - 1),
          startCoord = client.isVertical ? y : x,
          endCoord = client.isVertical ? endY : endX,
          first = client.getDateFromCoordinate(startCoord, 'round', true),
          last = client.getDateFromCoordinate(endCoord, 'round', true),
          dc = me.dateConstraints;
    let start = DateHelper.min(first, last),
        end = DateHelper.max(first, last);

    if (dc) {
      end = DateHelper.constrain(end, dc.start, dc.end);
      start = DateHelper.constrain(start, dc.start, dc.end);
    }

    Object.assign(me.createContext, {
      startDate: start,
      endDate: end
    });
    context.valid = me.checkValidity(me.createContext, event);
    context.message = '';

    if (context.valid && typeof context.valid !== 'boolean') {
      context.message = context.valid.message;
      context.valid = context.valid.valid;
    } // If users returns nothing, that's interpreted as valid

    context.valid = context.valid !== false;
  }

  onResize({
    context: {
      valid
    },
    event
  }) {
    const me = this,
          {
      startDate,
      endDate
    } = me.createContext;

    if (!startDate || !endDate || endDate - startDate <= 0) {
      valid = false;
    }

    me.createContext = me.prepareCreateContextForFinalization(me.createContext, event, me.finalize.bind(me));

    if (valid) {
      me.client.trigger('beforeDragCreateFinalize', {
        context: me.createContext,
        event,
        proxyElement: me.proxy
      });
    } // Drag create could be finalized immediately

    if (me.createContext) {
      // OMG, how not to confuse those contexts?
      me.resize.context.async = me.createContext.async;

      if (!me.createContext.async) {
        me.finalize(valid);
      } else {
        // We do not want to remove resizer yet, because it will also remove context and proxy element (or resize to 0 width)
        // Instead we blindfold resize helper to prevent event resize from starting parallel drag create
        me.resize.removeListeners();
      }
    }
  }

  prepareCreateContextForFinalization(createContext, event, finalize, async = false) {
    return Object.assign({}, createContext, {
      async,
      event,
      finalize
    });
  }

  get dragging() {
    return this.resize;
  }

  onCancel() {
    this.cancel();
  }

  cancel() {
    this.reset();
    this.mouseUpDetacher && this.mouseUpDetacher();
  }

  reset() {
    const me = this;
    me.removeProxy();
    me.createContext = null;
    me.monitorMove = false;
  } //endregion
  //region Product specific, implemented in subclasses

  checkValidity(context, event) {
    throw new Error('Implement in subclass');
  }

  triggerDragCreateEnd(newRecord, context) {
    throw new Error('Implement in subclass');
  }

  handleBeforeDragCreate(dateTime, event) {
    throw new Error('Implement in subclass');
  }

  isRowEmpty(rowRecord) {
    throw new Error('Implement in subclass');
  } //endregion

}
DragCreateBase._$name = 'DragCreateBase';

/**
 * @module Scheduler/feature/EventDragCreate
 */

/**
 * Feature that allows the user to create new events by dragging in empty parts of the scheduler rows.
 *
 * This feature is **enabled** by default
 *
 * **NOTE:** Incompatible with the {@link Scheduler.feature.EventDragSelect EventDragSelect} and the {@link Scheduler.feature.Pan Pan} features.
 *
 * @extends Scheduler/feature/base/DragCreateBase
 * @demo Scheduler/basic
 * @externalexample scheduler/EventDragCreate.js
 * @classtype eventDragCreate
 */

class EventDragCreate extends DragCreateBase {
  //region Config
  static get $name() {
    return 'EventDragCreate';
  }

  static get defaultConfig() {
    return {
      /**
       * An empty function by default, but provided so that you can perform custom validation on the event being created.
       * Return true if the new event is valid, false to prevent an event being created.
       * @param {Object} context A drag create context
       * @param {Date} context.startDate Event start date
       * @param {Date} context.endDate Event end date
       * @param {Scheduler.model.EventModel} context.record Event record
       * @param {Scheduler.model.ResourceModel} context.resourceRecord Resource record
       * @param {Event} event The event object
       * @return {Boolean} `true` if this validation passes
       * @config {function}
       */
      validatorFn: () => {}
    };
  } //endregion
  //region Events

  /**
   * Fires on the owning Scheduler after the new event has been created.
   * @event dragCreateEnd
   * @param {Scheduler.view.Scheduler} source
   * @param {Scheduler.model.EventModel} newEventRecord
   * @param {Scheduler.model.ResourceModel} resourceRecord
   * @param {MouseEvent} event The ending mouseup event.
   * @param {HTMLElement} proxyElement The proxy element showing the drag creation zone.
   */

  /**
   * Fires on the owning Scheduler at the beginning of the drag gesture
   * @event beforeDragCreate
   * @param {Scheduler.view.Scheduler} source
   * @param {Scheduler.model.ResourceModel} resourceRecord
   * @param {Date} date The datetime associated with the drag start point.
   */

  /**
   * Fires on the owning Scheduler after the drag start has created a proxy element.
   * @event dragCreateStart
   * @param {Scheduler.view.Scheduler} source
   * @param {HTMLElement} proxyElement The proxy representing the new event.
   */

  /**
   * Fired on the owning Scheduler to allow implementer to prevent immediate finalization by setting `data.context.async = true`
   * in the listener, to show a confirmation popup etc
   * ```
   *  scheduler.on('beforedragcreatefinalize', ({context}) => {
   *      context.async = true;
   *      setTimeout(() => {
   *          // async code don't forget to call finalize
   *          context.finalize();
   *      }, 1000);
   *  })
   * ```
   * @event beforeDragCreateFinalize
   * @param {Scheduler.view.Scheduler} source Scheduler instance
   * @param {HTMLElement} proxyElement Proxy element, representing future event
   * @param {Object} context
   * @param {Boolean} context.async Set true to handle drag create asynchronously (e.g. to wait for user
   * confirmation)
   * @param {Function} context.finalize Call this method to finalize drag create. This method accepts one
   * argument: pass true to update records, or false, to ignore changes
   */

  /**
   * Fires on the owning Scheduler at the end of the drag create gesture whether or not
   * a new event was created by the gesture.
   * @event afterDragCreate
   * @param {Scheduler.view.Scheduler} source
   * @param {HTMLElement} proxyElement The proxy element showing the drag creation zone.
   */
  //endregion
  //region Init

  construct(scheduler, config) {
    this.scheduler = scheduler;
    super.construct(scheduler, config);
  }

  get store() {
    return this.scheduler.eventStore;
  } //endregion
  //region Scheduler specific implementation

  finalizeDragCreate(context) {
    const newEventRecord = new this.store.modelClass({
      startDate: context.startDate,
      // Needed to force engine to calculate duration even if there is a defaultValue specified
      duration: null,
      endDate: context.endDate
    });
    this.proxy.dataset.eventId = newEventRecord.id;
    this.scheduler.trigger('dragCreateEnd', {
      newEventRecord,
      resourceRecord: context.rowRecord,
      event: context.event,
      proxyElement: this.proxy
    });
  }

  handleBeforeDragCreate(dateTime, event) {
    const me = this,
          result = me.scheduler.trigger('beforeDragCreate', {
      resourceRecord: me.createContext.rowRecord,
      date: dateTime,
      event
    });

    if (result) {
      var _tipFeature$hoverTip;

      // Tooltip will not be activated while drag is in progress,
      // but we need to hide it deliberately on drag start
      const tipFeature = me.scheduler.features.scheduleTooltip;
      tipFeature === null || tipFeature === void 0 ? void 0 : (_tipFeature$hoverTip = tipFeature.hoverTip) === null || _tipFeature$hoverTip === void 0 ? void 0 : _tipFeature$hoverTip.hide();
    } // Save date constraints

    me.dateConstraints = me.scheduler.getDateConstraints(me.createContext.rowRecord, dateTime);
    return result;
  }

  checkValidity(context, event) {
    const me = this,
          {
      client
    } = me; // Nicer for users of validatorFn

    context.resourceRecord = context.rowRecord;
    return (client.allowOverlap || client.isDateRangeAvailable(context.startDate, context.endDate, null, context.resourceRecord)) && me.validatorFn.call(me.validatorFnThisObj || me, context, event);
  } // Determine if resource already has events or not

  isRowEmpty(resourceRecord) {
    const events = this.store.getEventsForResource(resourceRecord);
    return !events || !events.length;
  } //endregion

}
EventDragCreate._$name = 'EventDragCreate';
GridFeatureManager.registerFeature(EventDragCreate, true, 'Scheduler');

/**
 * @module Scheduler/feature/EventDragSelect
 */

/**
 * Enables users to click and drag to select events inside the Scheduler's timeline.
 *
 * This feature is **off** by default. For info on enabling it, see {@link Grid.view.mixin.GridFeatures}.
 *
 * **NOTE:** Incompatible with the {@link Scheduler.feature.EventDragCreate EventDragCreate} and the {@link Scheduler.feature.Pan Pan} features.
 *
 * @extends Core/mixin/InstancePlugin
 * @mixes Core/mixin/Delayable
 *
 * @example
 * let scheduler = new Scheduler({
 *   features : {
 *     eventDragSelect      : true,
 *     eventDragCreate      : false
 *   }
 * });
 *
 * @demo Scheduler/dragselection
 * @classtype eventDragSelect
 */

class EventDragSelect extends Delayable(InstancePlugin) {
  // region Init
  static get $name() {
    return 'EventDragSelect';
  }

  construct(timeline, config) {
    this.timeline = timeline;
    timeline.multiEventSelect = true;
    const targetSelectors = ['.b-timeline-subgrid .b-grid-cell', '.b-timeline-subgrid'];
    this.targetSelector = targetSelectors.join(',');
    super.construct(timeline, config);
  } //endregion
  //region Plugin config
  // Plugin configuration. This plugin chains some of the functions in Scheduler.

  static get pluginConfig() {
    return {
      chain: ['onElementMouseDown', 'onElementMouseMove', 'onElementMouseUp']
    };
  } //endregion

  onElementMouseDown(event) {
    const me = this,
          scheduler = me.client; // only react to mouse input, and left button

    if (event.touches || event.button !== 0 || me.disabled) {
      return;
    } // only react to mousedown directly on grid cell or subgrid element

    if (event.target.matches(me.targetSelector)) {
      me.startX = event.clientX;
      me.startY = event.clientY;
      me.element = DomHelper.createElement({
        tag: 'div',
        className: 'b-dragselect-rect'
      }, true)[0];
      Widget.floatRoot.appendChild(me.element);
      scheduler.element.classList.add('b-dragselecting'); // Since the dragselect element is in the floatRoot, we want to use viewport-based coordinates, so we pass
      // ignorePageScroll=true when calling Rectangle.from():

      me.eventRectangles = Array.from(scheduler.element.querySelectorAll(scheduler.eventSelector)).map(el => ({
        rectangle: Rectangle.from(el,
        /* ignorePageScroll = */
        true),
        record: scheduler.resolveEventRecord(el)
      }));
      scheduler.clearEventSelection();
      me.subGridElementRect = Rectangle.from(scheduler.timeAxisSubGrid.element,
      /* ignorePageScroll = */
      true); // No key processing during drag selection

      scheduler.navigator.disabled = true;
    }
  }

  onElementMouseMove(event) {
    const me = this; // Synthetic mousemove event has no button for IE11
    // Detect if a mouseup happened outside our element (or browser window for that matter). Note 'buttons' is not supported by Safari

    if (event.buttons === 0 && !BrowserHelper.isSafari && (!BrowserHelper.isIE11 || event.isTrusted)) {
      me.onElementMouseUp(event);
      return;
    }

    if (typeof me.startX === 'number') {
      const x = Math.max(event.clientX, me.subGridElementRect.left),
            y = Math.max(event.clientY, me.subGridElementRect.top),
            left = Math.min(me.startX, x),
            top = Math.min(me.startY, y),
            width = Math.abs(me.startX - x),
            height = Math.abs(me.startY - y),
            rect = new Rectangle(left, top, width, height).constrainTo(me.subGridElementRect);
      DomHelper.setTranslateXY(me.element, rect.left, rect.top);
      me.element.style.width = rect.width + 'px';
      me.element.style.height = rect.height + 'px';
      me.rectangle = rect;
      me.updateSelection();
    }
  }

  onElementMouseUp() {
    const me = this;

    if (typeof me.startX === 'number') {
      me.element && me.element.remove();
      me.client.element.classList.remove('b-dragselecting');
      me.startX = me.startY = null; // Navigator will react to the 'click' event which clears selection, bypass this with a short timeout

      me.setTimeout(() => me.client.navigator.disabled = false, 100);
    }
  }

  updateSelection() {
    const me = this,
          renderedEventRects = me.eventRectangles,
          rectangle = me.rectangle;

    for (let i = 0, len = renderedEventRects.length; i < len; i++) {
      const eventData = renderedEventRects[i],
            shouldSelect = rectangle.intersect(eventData.rectangle, true);

      if (shouldSelect && !eventData.selected) {
        eventData.selected = true;
        me.client.selectEvent(eventData.record, true);
      } else if (!shouldSelect && eventData.selected) {
        eventData.selected = false;
        me.client.deselectEvent(eventData.record);
      }
    }
  }

}
EventDragSelect._$name = 'EventDragSelect';
GridFeatureManager.registerFeature(EventDragSelect, false, 'Scheduler');

/**
 * @module Scheduler/feature/base/EditBase
 */

const DH$2 = DateHelper,
      makeDate = fields => {
  // single field, update record directly
  if (fields.length === 1) return fields[0].value; // two fields, date + time
  else if (fields.length === 2) {
      const [date, time] = fields[0] instanceof DateField ? fields : fields.reverse(),
            dateValue = DH$2.parse(date.value);

      if (dateValue) {
        dateValue.setHours(time.value.getHours(), time.value.getMinutes(), time.value.getSeconds(), time.value.getMilliseconds());
      } // Clone to not end up sharing dates

      return dateValue ? DateHelper.clone(dateValue) : null;
    } // shouldn't happen...

  return null;
},
      copyTime = (dateTo, dateFrom) => {
  const d = new Date(dateTo.getTime());
  d.setHours(dateFrom.getHours(), dateFrom.getMinutes());
  return d;
},
      adjustEndDate = (startDate, startTime, me) => {
  // The end datetime just moves in response to the changed start datetime, keeping the same duration.
  if (startDate && startTime) {
    const newEndDate = DH$2.add(copyTime(me.startDateField.value, me.startTimeField.value), me.eventRecord.durationMS, 'milliseconds');
    me.endDateField.value = newEndDate;
    me.endTimeField.value = DH$2.clone(newEndDate);
  }
};
/**
 * Base class for EventEdit (Scheduler) and TaskEdit (Gantt) features. Contains shared code. Not to be used directly.
 *
 * @extends Core/mixin/InstancePlugin
 */

class EditBase extends InstancePlugin {
  //region Config
  static get configurable() {
    return {
      /**
       * Deprecated. Use the `editorConfig` config:
       * ```javascript
       * const scheduler = new Scheduler({
       *     features : {
       *         eventEdit  : {
       *             editorConfig : {
       *                 autoClose : false,
       *                 modal     : true
       *             }
       *         }
       *     }
       * });
       * ```
       * @config {Boolean}
       * @default
       * @category Editor
       * @deprecated 4.0.0 Configure the {@link #config-editorConfig}:
       */
      autoClose: true,

      /**
       * True to save and close this panel if ENTER is pressed in one of the input fields inside the panel.
       * @config {Boolean}
       * @default
       * @category Editor
       */
      saveAndCloseOnEnter: true,
      triggerEvent: null,

      /**
       * Deprecated. Use the `items` config:
       * ```javascript
       * const scheduler = new Scheduler({
       *     features : {
       *         eventEdit : {
       *             editorConfig : {
       *                 bbar : {
       *                     items : {
       *                         deleteButton : false
       *                     }
       *                 }
       *             }
       *         }
       *     }
       * });
       * ```
       * @config {Boolean}
       * @default
       * @category Editor widgets
       * @deprecated 4.0.0 Configure the {@link #config-editorConfig}:
       */
      showDeleteButton: true,

      /**
       * Deprecated. Use the `items` config:
       * ```javascript
       * const scheduler = new Scheduler({
       *     features : {
       *         eventEdit : {
       *             items : {
       *                 nameField : {
       *                     hidden : true
       *                 }
       *             }
       *         }
       *     }
       * });
       * ```
       * @config {Boolean}
       * @default
       * @category Editor widgets
       * @deprecated 4.0.0 Configure the {@link #config-items}:
       */
      showNameField: true,

      /**
       * Deprecated. Use the `items` config:
       * ```javascript
       * const scheduler = new Scheduler({
       *     features : {
       *         eventEdit : {
       *             timeFormat : 'HH:mm',
       *             items      : {
       *                 startTimeField : {
       *                     // We use 24 hour clock: use less space
       *                     flex   : '1 0 30%'
       *                 },
       *                 endTimeField : {
       *                     flex   : '1 0 30%'
       *                 }
       *             }
       *         }
       *     }
       * });
       * ```
       * @config {Object}
       * @category Editor widgets
       * @deprecated 4.0.0 Configure the {@link #config-editorConfig}:
       */
      startTimeConfig: null,

      /**
       * Deprecated. Use the `items` config:
       * ```javascript
       * const scheduler = new Scheduler({
       *     features : {
       *         eventEdit : {
       *             items : {
       *                 startDateField : {
       *                     // Allow click back and forward by 1 day
       *                     step: '1d'
       *                 },
       *                 endDateField : {
       *                     step: '1d'
       *                 }
       *             }
       *         }
       *     }
       * });
       * ```
       * @config {Object}
       * @category Editor widgets
       * @deprecated 4.0.0 Configure the {@link #config-editorConfig}:
       */
      startDateConfig: null,

      /**
       * Deprecated. Use the `items` config:
       * ```javascript
       * const scheduler = new Scheduler({
       *     features : {
       *         eventEdit  : {
       *             timeFormat : 'HH:mm',
       *             items : {
       *                 startTimeField : {
       *                     // We use 24 hour clock: use less space
       *                     flex   : '1 0 30%'
       *                 },
       *                 endTimeField : {
       *                     flex   : '1 0 30%'
       *                 }
       *             }
       *         }
       *     }
       * });
       * ```
       * @config {Object}
       * @category Editor widgets
       * @deprecated 4.0.0 Configure the {@link #config-editorConfig}:
       */
      endTimeConfig: null,

      /**
       * Deprecated. Use the `items` config:
       * ```javascript
       * const scheduler = new Scheduler({
       *     features : {
       *         eventEdit  : {
       *             items : {
       *                 startDateField : {
       *                     // Allow click back and forward by 1 day
       *                     step: '1d'
       *                 },
       *                 endDateField : {
       *                     step: '1d'
       *                 }
       *             }
       *         }
       *     }
       * });
       * ```
       * @config {Object}
       * @category Editor widgets
       * @deprecated 4.0.0 Configure the {@link #config-editorConfig}:
       */
      endDateConfig: null,

      /**
       * This config parameter is passed to the `startDateField` and `endDateField` constructor.
       * @config {String}
       * @default
       * @category Editor widgets
       */
      dateFormat: 'L',
      // date format that uses browser locale

      /**
       * This config parameter is passed to the `startTimeField` and `endTimeField` constructor.
       * @config {String}
       * @default
       * @category Editor widgets
       */
      timeFormat: 'LT',
      // date format that uses browser locale

      /**
       * Default editor configuration, which widgets it shows etc.
       *
       * This is the entry point into configuring any aspect of the editor.
       *
       * The {@link Core.widget.Container#config-items} configuration of a Container
       * is *deeply merged* with its default `items` value. This means that you can specify
       * an `editorConfig` object which configures the editor, or widgets inside the editor:
       * ```javascript
       * const scheduler = new Scheduler({
       *     features : {
       *         eventEdit  : {
       *             editorConfig : {
       *                 autoClose : false,
       *                 modal     : true,
       *                 cls       : 'editor-widget-cls',
       *                 items : {
       *                     resourceField : {
       *                         hidden : true
       *                     },
       *                     // Add our own event owner field at the top of the form.
       *                     // Weight -100 will make it sort top the top.
       *                     ownerField : {
       *                         weight : -100,
       *                         type   : 'usercombo',
       *                         name   : 'owner',
       *                         label  : 'Owner'
       *                     }
       *                 },
       *                 bbar : {
       *                     items : {
       *                         deleteButton : false
       *                     }
       *                 }
       *             }
       *         }
       *     }
       * });
       * ```
       * @config {Object}
       * @category Editor
       */
      editorConfig: null,

      /**
       * Deprecated. Use the `items` config:
       * ```javascript
       * const scheduler = new Scheduler({
       *     features : {
       *         eventEdit  : {
       *             items : {
       *                 owner : {
       *                     weight : -100, // Will sort above system-supplied fields which are weight 0
       *                     type   : 'usercombo',
       *                     name   : 'owner',
       *                     label  : 'Owner'
       *                 }
       *             }
       *         }
       *     }
       * });
       * ```
       * @config {String|Object[]}
       * @category Editor widgets
       * @deprecated 4.0.0 Use the {@link #config-items} config
       */
      extraItems: null,

      /**
       * An object to merge with the provided items config of the editor to override the
       * configuration of provided fields, or add new fields.
       *
       * Example:
       * ```javascript
       * const scheduler = new Scheduler({
       *     features : {
       *         eventEdit  : {
       *             items : {
       *                 // Merged with provided config of the resource field
       *                 resourceField : {
       *                     label : 'Calendar'
       *                 },
       *                 owner : {
       *                     weight : -100, // Will sort above system-supplied fields which are weight 0
       *                     type   : 'usercombo',
       *                     name   : 'owner',
       *                     label  : 'Owner'
       *                 }
       *             }
       *         }
       *     }
       * });
       *```
       *
       * The provided fields are called
       *  - `nameField`
       *  - `resourceField`
       *  - `startDateField`
       *  - `startTimeField`
       *  - `endDateField`
       *  - `endTimeField`
       *  - `recurrenceCombo`
       *  - `editRecurrenceButton`
       * @config {Object}
       * @category Editor widgets
       */
      items: null,

      /**
       * The week start day used in all date fields of the feature editor form by default.
       * 0 means Sunday, 6 means Saturday.
       * Defaults to the locale's week start day.
       * @config {Number}
       */
      weekStartDay: null
    };
  } //endregion
  //region Init & destroy

  construct(client, config) {
    const me = this;
    client.eventEdit = me;
    super.construct(client, ObjectHelper.assign({
      weekStartDay: client.weekStartDay
    }, config));
    me.clientListenersDetacher = client.on({
      [me.triggerEvent]: 'onActivateEditor',
      dragCreatEend: 'onDragCreateEnd',
      // Not fired at the Scheduler level.
      // Calendar, which inherits this, implements this event.
      eventAutoCreated: 'onEventAutoCreated',
      thisObj: me
    });
  }

  doDestroy() {
    var _this$editor;

    this.clientListenersDetacher();
    (_this$editor = this.editor) === null || _this$editor === void 0 ? void 0 : _this$editor.destroy();
    super.doDestroy();
  } //endregion
  //region Editing

  changeEditorConfig(editorConfig) {
    // extraItems is deprecated
    const {
      items,
      extraItems
    } = this; // Merge extraItems in either format into a copy of the default editorConfig.

    if (extraItems) {
      editorConfig = Objects.clone(editorConfig);
      editorConfig = this.insertExtraWidgetsIntoDefaultWidgets(editorConfig);
    } // Merge items which is an Object with the default editorConfig's items
    else if (items) {
        editorConfig = Objects.clone(editorConfig);
        editorConfig.items = Config.merge(items, editorConfig.items);
      }

    return editorConfig;
  }
  /**
   * Insert extra fields into default Editor fields according to specific rules:
   * - If no index provided insert them into `extraItems` placeholder (or at the end if not);
   * - If index provided sort ASC and insert one by one, but only after no-index fields are inserted;
   * - If `extraItems` placeholder exists, don't take widgets that go after it into account;
   * @private
   */

  insertExtraWidgetsIntoDefaultWidgets(editorConfig) {
    VersionHelper.deprecate('Scheduler', '5.0.0', 'extraItems replaced by items in EditFeature.');
    const me = this,
          {
      extraItems
    } = me,
          {
      items
    } = editorConfig,
          extraItemsAsArray = Array.isArray(extraItems) ? extraItems : extraItems && Object.values(extraItems);

    if (!extraItems || !Object.keys(extraItemsAsArray).length) {
      return editorConfig;
    } // If they still use index instead of weight, we honour it for now.

    const hasIndex = Object.values(extraItemsAsArray).some(i => 'index' in i);

    if (hasIndex) {
      const extraItemsAsArray = Array.isArray(extraItems) ? extraItems : extraItems && ObjectHelper.transformNamedObjectToArray(extraItems);
      VersionHelper.deprecate('Scheduler', '5.0.0', 'index in extraItems replaced by weight to sort editor items'); // items needs to be an array to have extra items inserted at an index.

      let itemsAsArray = ObjectHelper.transformNamedObjectToArray(items),
          lastItem = itemsAsArray[itemsAsArray.length - 1]; // Find default extra widgets position

      let index = itemsAsArray.findIndex(widget => widget.type === 'extraItems'),
          tail; // If extra widgets placeholder exists

      if (index > -1) {
        // Remove extra widgets placeholder from its position
        itemsAsArray.splice(index, 1); // Backup everything that goes after extra widgets placeholder, like Save/Delete/Cancel buttons

        tail = itemsAsArray.splice(index);
      } // Split extra widgets on 2 parts: those which have index and those which haven't

      const withIndex = extraItemsAsArray.filter(widget => widget.index >= 0),
            withoutIndex = extraItemsAsArray.filter(widget => !(widget.index >= 0)); // Add those without index to the end of the default widgets

      withoutIndex.forEach((item, index) => item.weight = lastItem.weight + index * 100);
      itemsAsArray = itemsAsArray.concat(withoutIndex); // Sort those which have index in ASC order, so we insert fields in series

      withIndex.sort((widgetA, widgetB) => widgetA.index - widgetB.index); // And now insert extra widgets at their individually specified index

      withIndex.forEach(widget => {
        const previousWidget = itemsAsArray[Math.min(widget.index, itemsAsArray.length) - 1];
        widget.weight = previousWidget ? previousWidget.weight + 1 : -100;
        itemsAsArray.splice(widget.index, 0, widget);
      });

      if (tail && tail.length) {
        // Return backuped fields to the end of the widgets
        itemsAsArray = itemsAsArray.concat(tail);
      } // Convert it back to object form.

      editorConfig.items = ObjectHelper.transformArrayToNamedObject(itemsAsArray);
    } // Merge object form of extraItems into editorConfig's items
    else {
        // Default is to append so make high weight
        let weight = 900;

        for (const item of Array.isArray(extraItems) ? extraItems : Object.values(extraItems)) {
          if (!item.ref) {
            item.ref = item.id;
          }

          item.weight = weight += 100;
        }

        editorConfig.items = { ...items,
          ...(Array.isArray(extraItems) ? ObjectHelper.transformArrayToNamedObject(extraItems) : extraItems)
        };
      }

    return editorConfig;
  }

  onDatesChange(params) {
    const me = this,
          field = params.source,
          value = params.value;

    switch (field.ref) {
      case 'startDateField':
        me.startTimeField && adjustEndDate(value, me.startTimeField.value, me);
        break;

      case 'startTimeField':
        me.startDateField && adjustEndDate(me.startDateField.value, value, me);
        break;
    }

    if (me.endTimeField) {
      // If the event starts and ends on the same day, the time fields need
      // to have their min and max set against each other.
      if (DH$2.isEqual(DH$2.clearTime(me.startDateField.value), DH$2.clearTime(me.endDateField.value))) {
        me.endTimeField.min = me.startTimeField.value;
      } else {
        me.endTimeField.min = null;
      }
    }
  } //endregion
  //region Save

  async save() {
    throw new Error('Implement in subclass');
  }

  get isValid() {
    const me = this;
    return Object.values(me.editor.widgetMap).every(field => {
      if (!field.name || field.hidden) {
        return true;
      }

      return field.isValid !== false;
    });
  }

  get values() {
    const me = this,
          {
      editor
    } = me,
          startFields = [],
          endFields = [],
          values = {};
    editor.eachWidget(widget => {
      var _editor$widgetMap$rec;

      const name = widget.name; // If the widget is part of the recurrence editor, we don't gather it.

      if (!name || widget.hidden || widget.up(w => w === me.recurrenceEditor)) {
        return;
      }

      switch (name) {
        case 'startDate':
          startFields.push(widget);
          break;

        case 'endDate':
          endFields.push(widget);
          break;

        case 'resource':
          values[name] = widget.record;
          break;

        case 'recurrenceRule':
          // If recurrence set to null, completely clear the recurrenceRule.
          // Otherwise it will still be perceived as recurring with the rule 'FREQ=none'
          values[name] = ((_editor$widgetMap$rec = editor.widgetMap.recurrenceCombo) === null || _editor$widgetMap$rec === void 0 ? void 0 : _editor$widgetMap$rec.value) === 'none' ? '' : widget.value;
          break;

        default:
          values[name] = widget.value;
      }
    }, true); // Handle fields being configured away

    if (startFields.length) {
      values.startDate = makeDate(startFields);
    }

    if (endFields.length) {
      values.endDate = makeDate(endFields);
    } // Since there is no duration field in the editor,
    // we don't need to recalc duration value on each date change.
    // It's enough to return correct duration value in `values`,
    // so the record will get updated with the correct data.

    if ('startDate' in values && 'endDate' in values) {
      values.duration = DH$2.diff(values.startDate, values.endDate, me.editor.record.durationUnit, true);
    }

    return values;
  }
  /**
   * Template method, intended to be overridden. Called before the event record has been updated.
   * @param {Scheduler.model.EventModel} eventRecord The event record
   *
   **/

  onBeforeSave(eventRecord) {}
  /**
   * Template method, intended to be overridden. Called after the event record has been updated.
   * @param {Scheduler.model.EventModel} eventRecord The event record
   *
   **/

  onAfterSave(eventRecord) {}
  /**
   * Updates record being edited with values from the editor
   * @private
   */

  updateRecord(record) {
    const {
      values
    } = this; // Clean resourceId / resources out of values when using assignment store, it will handle the assignment

    if (this.assignmentStore) {
      delete values.resource;
    }

    return record.set(values);
  } //endregion
  //region Events

  resetEditingContext() {
    const me = this;
    me.targetEventElement = null;
    me.client.element.classList.remove('b-eventeditor-editing');
    me.phantomEventElement && me.phantomEventElement.remove();
    me.phantomEventElement = null;
  }

  onPopupKeyDown({
    event
  }) {
    if (!this.readOnly && event.key === 'Enter' && this.saveAndCloseOnEnter && event.target.tagName.toLowerCase() === 'input') {
      // Need to prevent this key events from being fired on whatever receives focus after the editor is hidden
      event.preventDefault(); // If enter key was hit in an input element of a start field, need to adjust end date fields (the same way as if #onDatesChange handler was called)

      if (event.target.name === 'startDate') {
        adjustEndDate(this.startDateField.value, this.startTimeField.value, this);
      }

      this.onSaveClick();
    }
  }

  async onSaveClick() {
    const saved = await this.save();

    if (saved) {
      this.editor.close();
    }
  }

  async onDeleteClick() {
    const removed = await this.deleteEvent();

    if (removed) {
      // We expect deleteEvent will trigger close if autoClose is true and focus has moved out,
      // otherwise need to call it manually
      if (!this.editor.autoClose || this.editor.containsFocus) {
        this.editor.close();
      }
    }
  }

  onCancelClick() {
    this.editor.close();
  } //endregion

}
EditBase._$name = 'EditBase';

/**
 * @module Scheduler/view/EventEditor
 */

/**
 * Provided event editor dialog.
 *
 * @extends Core/widget/Popup
 * @private
 */

class EventEditor extends Popup {
  // Factoryable type name
  static get type() {
    return 'eventeditor';
  }

  static get $name() {
    return 'EventEditor';
  }

  static get configurable() {
    return {
      items: [],
      draggable: {
        handleSelector: ':not(button,.b-field-inner)' // Ignore buttons and field inners

      },
      axisLock: 'flexible',
      scrollable: {
        // In case editor is very tall or window is small, make it scrollable
        overflowY: true
      },
      readOnly: null,

      /**
       * A Function (or *name* of a function) which produces a customized Panel header based upon the event being edited.
       * @config {Function|String}
       * @param {Scheduler.model.EventModel} eventRecord The record being edited
       * @returns {String} The Panel title.
       */
      titleRenderer: null
    };
  }

  updateLocalization() {
    super.updateLocalization(...arguments); // Use this if there's no titleRenderer

    this.initialTitle = this.title || '';
  }

  processWidgetConfig(widget) {
    var _widget$type;

    const me = this,
          eventEditFeature = me.eventEditFeature;

    if ((_widget$type = widget.type) !== null && _widget$type !== void 0 && _widget$type.includes('date') && widget.weekStartDay == null) {
      widget.weekStartDay = me.weekStartDay;
    }

    if (widget.type === 'extraItems') {
      return false;
    }

    let fieldConfig = {};

    if (widget.ref === 'resourceField') {
      if (!eventEditFeature.showResourceField) {
        return false;
      } // Can't use store directly since it may be grouped and then contains irrelevant group records

      me.resourceStore = widget.store = eventEditFeature.resourceStore.makeChained(record => !record.isSpecialRow, null, {
        // Need to show all records in the combo. Required in case resource store is a tree.
        excludeCollapsedRecords: false
      }); // When events are loaded with resourceId, we should only support single select

      widget.multiSelect = !eventEditFeature.eventStore.usesSingleAssignment;

      if (eventEditFeature.resourceFieldConfig) {
        fieldConfig = eventEditFeature.resourceFieldConfig;
      }
    }

    if (widget.ref === 'nameField' && !eventEditFeature.showNameField) {
      return false;
    }

    if (widget.ref === 'deleteButton' && !eventEditFeature.showDeleteButton) {
      return false;
    }

    if ((widget.name === 'startDate' || widget.name === 'endDate') && widget.type === 'date') {
      fieldConfig.format = eventEditFeature.dateFormat;
    }

    if ((widget.name === 'startDate' || widget.name === 'endDate') && widget.type === 'time') {
      fieldConfig.format = eventEditFeature.timeFormat;
    }

    if (eventEditFeature.startDateConfig && widget.name === 'startDate' && widget.type === 'date') {
      fieldConfig = eventEditFeature.startDateConfig;
    }

    if (eventEditFeature.startTimeConfig && widget.name === 'startDate' && widget.type === 'time') {
      fieldConfig = eventEditFeature.startTimeConfig;
    }

    if (eventEditFeature.endDateConfig && widget.name === 'endDate' && widget.type === 'date') {
      fieldConfig = eventEditFeature.endDateConfig;
    }

    if (eventEditFeature.endTimeConfig && widget.name === 'endDate' && widget.type === 'time') {
      fieldConfig = eventEditFeature.endTimeConfig;
    }

    Object.assign(widget, fieldConfig);
    return super.processWidgetConfig(widget);
  }

  show(...args) {
    // Updated chained store. It is not done automatically for grouping/trees.
    if (this.resourceStore) {
      this.resourceStore.fillFromMaster();
    }

    super.show(...args);
  }

  onBeforeShow(...args) {
    const me = this,
          {
      record,
      titleRenderer
    } = me,
          deleteButton = me.widgetMap.deleteButton; // Hide delete button if we are readOnly or the event does not belong to a store

    if (deleteButton) {
      deleteButton.hidden = me.readOnly || !record.stores.length && !record.isOccurrence;
    }

    if (titleRenderer) {
      me.title = me.callback(titleRenderer, me, [record]);
    } else {
      me.title = me.initialTitle;
    }

    super.onBeforeShow && super.onBeforeShow(...args);
  }

  onInternalKeyDown(event) {
    this.trigger('keyDown', {
      event
    });
    super.onInternalKeyDown(event);
  }

  updateReadOnly(readOnly) {
    const {
      deleteButton,
      saveButton,
      cancelButton
    } = this.widgetMap;
    super.updateReadOnly(readOnly);

    if (deleteButton) {
      deleteButton.hidden = readOnly;
    }

    if (saveButton) {
      saveButton.hidden = readOnly;
    }

    if (cancelButton) {
      cancelButton.hidden = readOnly;
    }
  }

} // Register this widget type with its Factory

EventEditor.initClass();
EventEditor._$name = 'EventEditor';

/**
 * @module Scheduler/view/recurrence/RecurrenceConfirmationPopup
 */

/**
 * Confirmation dialog showing up before modifying a recurring event or some of its occurrences.
 * For recurring events the dialog notifies user that the event change/removal will cause all its occurrences
 * change/removal and asks to confirm the action.
 *
 * And for occurrences the dialog allows to choose if user wants to affect all further occurrences, this occurrence only or cancel the change.
 *
 * Usage example:
 *
 * ```javascript
 * const confirmation = new RecurrenceConfirmationPopup();
 *
 * confirmation.confirm({
 *     eventRecord : recurringEvent,
 *     actionType  : "delete",
 *     changerFn   : () => recurringEvent.remove(event)
 * });
 * ```
 *
 * @classType recurrenceconfirmation
 * @extends Core/widget/Popup
 */

class RecurrenceConfirmationPopup extends Popup {
  static get $name() {
    return 'RecurrenceConfirmationPopup';
  } // Factoryable type name

  static get type() {
    return 'recurrenceconfirmation';
  }

  static get defaultConfig() {
    return {
      localizableProperties: [],
      align: 'b-t',
      autoShow: false,
      autoClose: false,
      closeAction: 'onRecurrenceClose',
      modal: true,
      centered: true,
      scrollAction: 'realign',
      constrainTo: window,
      draggable: true,
      closable: true,
      floating: true,
      eventRecord: null,
      cls: 'b-sch-recurrenceconfirmation',
      bbar: {
        defaults: {
          localeClass: this
        },
        items: {
          changeMultipleButton: {
            weight: 100,
            color: 'b-green',
            text: 'L{Object.Yes}',
            onClick: 'up.onChangeMultipleButtonClick'
          },
          changeSingleButton: {
            weight: 200,
            color: 'b-gray',
            text: 'L{update-only-this-btn-text}',
            onClick: 'up.onChangeSingleButtonClick'
          },
          cancelButton: {
            weight: 300,
            color: 'b-gray',
            text: 'L{Object.Cancel}',
            onClick: 'up.onCancelButtonClick'
          }
        }
      }
    };
  }
  /**
   * Reference to the "Apply changes to multiple occurrences" button, if used
   * @property {Core.widget.Button}
   * @readonly
   */

  get changeMultipleButton() {
    return this.widgetMap.changeMultipleButton;
  }
  /**
   * Reference to the button that causes changing of the event itself only, if used
   * @property {Core.widget.Button}
   * @readonly
   */

  get changeSingleButton() {
    return this.widgetMap.changeSingleButton;
  }
  /**
   * Reference to the cancel button, if used
   * @property {Core.widget.Button}
   * @readonly
   */

  get cancelButton() {
    return this.widgetMap.cancelButton;
  }
  /**
   * Handler for "Apply changes to multiple occurrences" {@link #property-changeMultipleButton button}.
   * It calls {@link #function-processMultipleRecords} and then hides the dialog.
   */

  onChangeMultipleButtonClick() {
    this.processMultipleRecords();
    this.hide();
  }
  /**
   * Handler for the {@link #property-changeSingleButton button} that causes changing of the event itself only.
   * It calls {@link #function-processSingleRecord} and then hides the dialog.
   */

  onChangeSingleButtonClick() {
    this.processSingleRecord();
    this.hide();
  }
  /**
   * Handler for {@link #property-cancelButton cancel button}.
   * It calls `cancelFn` provided to {@link #function-confirm} call and then hides the dialog.
   */

  onCancelButtonClick() {
    this.cancelFn && this.cancelFn.call(this.thisObj);
    this.hide();
  }

  onRecurrenceClose() {
    if (this.cancelFn) {
      this.cancelFn.call(this.thisObj);
    }

    this.hide();
  }
  /**
   * Displays the confirmation.
   * Usage example:
   *
   * ```javascript
   * const popup = new RecurrenceConfirmationPopup();
   *
   * popup.confirm({
   *     eventRecord,
   *     actionType : "delete",
   *     changerFn  : () => eventStore.remove(record)
   * });
   * ```
   *
   * @param {Object}                     config               The following config options are supported:
   * @param {Scheduler.model.EventModel} config.eventRecord   Event being modified.
   * @param {String}                     config.actionType    Type of modification to be applied to the event. Can be either "update" or "delete".
   * @param {Function}                   config.changerFn     A function that should be called to apply the change to the event upon user choice.
   * @param {Function}                   [config.thisObj]     `changerFn` and `cancelFn` functions scope.
   * @param {Function}                   [config.cancelFn]    Function called on `Cancel` button click.
   */

  confirm(config = {}) {
    const me = this;
    ['actionType', 'eventRecord', 'title', 'html', 'changerFn', 'cancelFn', 'thisObj'].forEach(prop => {
      if (prop in config) me[prop] = config[prop];
    });
    me.updatePopupContent();
    return super.show(config);
  }

  updatePopupContent() {
    const me = this,
          {
      changeMultipleButton,
      changeSingleButton,
      cancelButton
    } = me.widgetMap,
          {
      eventRecord,
      actionType = 'update'
    } = me,
          isMaster = eventRecord && eventRecord.isRecurring; // Do not remove. Assertion strings for Localization sanity check.
    // 'L{delete-further-message}'
    // 'L{update-further-message}'
    // 'L{delete-all-message}'
    // 'L{update-all-message}'
    // 'L{delete-further-btn-text}'
    // 'L{update-further-btn-text}'
    // 'L{delete-only-this-btn-text}'
    // 'L{update-only-this-btn-text}'

    if (isMaster) {
      changeMultipleButton.text = me.L('L{Object.Yes}');
      me.html = me.L(`${actionType}-all-message`);
    } else {
      changeMultipleButton.text = me.L(`${actionType}-further-btn-text`);
      me.html = me.L(`${actionType}-further-message`);
    }

    changeSingleButton.text = me.L(`${actionType}-only-this-btn-text`);
    cancelButton.text = me.L('L{Object.Cancel}'); // TODO: so far we hide 'Only this event' option for a recurring event itself until this case is supported

    if (isMaster) {
      changeSingleButton.hide();
    } else {
      changeSingleButton.show();
    }

    me.width = me.L('L{width}'); // the following lines are added to satisfy the 904_unused localization test
    // to let it know that these locales are used:
    // this.L('L{delete-title}') not found
    // this.L('L{update-title}') not found

    me.title = me.L(`${actionType}-title`);
  }
  /**
   * Applies changes to multiple occurrences as reaction on "Apply changes to multiple occurrences"
   * {@link #property-changeMultipleButton button} click.
   */

  processMultipleRecords() {
    const {
      eventRecord,
      changerFn,
      thisObj
    } = this;
    eventRecord.beginBatch(); // Apply changes to the occurrence.
    // It is not joined to any stores, so this has no consequence.

    this.callback(changerFn, thisObj, [eventRecord]); // afterChange will promote it to being an new recurring base because there's still recurrence

    eventRecord.endBatch();
  }
  /**
   * Applies changes to a single record by making it a "real" event and adding an exception to the recurrence.
   * The method is called as reaction on clicking the {@link #property-changeSingleButton button} that causes changing of the event itself only.
   */

  processSingleRecord() {
    const {
      eventRecord,
      changerFn,
      thisObj
    } = this;
    eventRecord.beginBatch(); // When the changes apply, because there's no recurrence, it will become an exception

    eventRecord.recurrence = null; // Apply changes to the occurrence.
    // It is not joined to any stores, so this has no consequence.

    this.callback(changerFn, thisObj, [eventRecord]); // Must also change after the callback in case the callback sets the rule.
    // This will update the batch update data block to prevent it being set back to recurring.

    eventRecord.recurrenceRule = null; // afterChange will promote it to being an exception because there's no recurrence

    eventRecord.endBatch();
  }

  updateLocalization() {
    this.updatePopupContent();
    super.updateLocalization();
  }

}

RecurrenceConfirmationPopup.initClass();
RecurrenceConfirmationPopup._$name = 'RecurrenceConfirmationPopup';

/**
 * @module Scheduler/view/recurrence/field/RecurrenceFrequencyCombo
 */

/**
 * A combobox field allowing to pick frequency in the {@link Scheduler.view.recurrence.RecurrenceEditor recurrence dialog}.
 *
 * @extends Core/widget/Combo
 * @classType recurrencefrequencycombo
 */

class RecurrenceFrequencyCombo extends LocalizableComboItems(Combo) {
  static get $name() {
    return 'RecurrenceFrequencyCombo';
  } // Factoryable type name

  static get type() {
    return 'recurrencefrequencycombo';
  }

  static get defaultConfig() {
    return {
      editable: false,
      displayField: 'text',
      valueField: 'value'
    };
  }

  buildLocalizedItems() {
    return [{
      value: 'DAILY',
      text: this.L('L{Daily}')
    }, {
      value: 'WEEKLY',
      text: this.L('L{Weekly}')
    }, {
      value: 'MONTHLY',
      text: this.L('L{Monthly}')
    }, {
      value: 'YEARLY',
      text: this.L('L{Yearly}')
    }];
  }

}

RecurrenceFrequencyCombo.initClass();
RecurrenceFrequencyCombo._$name = 'RecurrenceFrequencyCombo';

/**
 * @module Scheduler/view/recurrence/field/RecurrenceCombo
 */

/**
 * A combobox field displaying the recurrence by either mode: `Daily`, `Weekly`, `Monthly` or `Yearly` if the recurrence
 * has no other non-default settings, or `Custom...` if the recurrence has custom setting applied.
 *
 * @extends Scheduler/view/recurrence/field/RecurrenceFrequencyCombo
 * @classType recurrencecombo
 */

class RecurrenceCombo extends RecurrenceFrequencyCombo {
  static get $name() {
    return 'RecurrenceCombo';
  } // Factoryable type name

  static get type() {
    return 'recurrencecombo';
  }

  static get defaultConfig() {
    return {
      customValue: 'custom',
      placeholder: 'None',
      // TODO: draw a splitting line
      splitCls: 'b-recurrencecombo-split',
      items: true,
      highlightExternalChange: false
    };
  }

  buildLocalizedItems() {
    const me = this;
    return [{
      value: 'none',
      text: me.L('L{None}')
    }, ...super.buildLocalizedItems(), {
      value: me.customValue,
      text: me.L('L{Custom}'),
      cls: me.splitCls
    }];
  }

  set value(value) {
    // Use 'none' instead of falsy value
    value = value || 'none';
    super.value = value;
  }

  get value() {
    return super.value;
  }

  set recurrence(recurrence) {
    const me = this;

    if (recurrence) {
      me.value = me.isCustomRecurrence(recurrence) ? me.customValue : recurrence.frequency;
    } else {
      me.value = null;
    }
  }

  isCustomRecurrence(recurrence) {
    const {
      interval,
      days,
      monthDays,
      months
    } = recurrence;
    return Boolean(interval > 1 || days && days.length || monthDays && monthDays.length || months && months.length);
  }

}

RecurrenceCombo.initClass();
RecurrenceCombo._$name = 'RecurrenceCombo';

/**
 * @module Scheduler/view/recurrence/RecurrenceLegendButton
 */

/**
 * Class implementing a button which text displays the associated recurrence info in a human readable form.
 * @extends Core/widget/Button
 * @classType recurrencelegendbutton
 */

class RecurrenceLegendButton extends Button {
  static get $name() {
    return 'RecurrenceLegendButton';
  } // Factoryable type name

  static get type() {
    return 'recurrencelegendbutton';
  }

  static get defaultConfig() {
    return {
      localizableProperties: [],
      recurrence: null
    };
  }
  /**
   * Sets / gets the recurrence to display description for.
   * @property {Scheduler.model.RecurrenceModel}
   */

  set recurrence(recurrence) {
    this._recurrence = recurrence;
    this.updateLegend();
  }

  get recurrence() {
    return this._recurrence;
  }

  set eventStartDate(eventStartDate) {
    this._eventStartDate = eventStartDate;
    this.updateLegend();
  }

  get eventStartDate() {
    return this._eventStartDate;
  }

  updateLegend() {
    const me = this,
          {
      recurrence
    } = me;
    me.text = recurrence ? RecurrenceLegend.getLegend(recurrence, me.eventStartDate) : '';
  }

  onLocaleChange() {
    // on locale switch we update the button text to use proper language
    this.updateLegend();
  }

  updateLocalization() {
    this.onLocaleChange();
    super.updateLocalization();
  }

} // Register this widget type with its Factory

RecurrenceLegendButton.initClass();
RecurrenceLegendButton._$name = 'RecurrenceLegendButton';

/**
 * @module Scheduler/view/recurrence/field/RecurrenceDaysCombo
 */

/**
 * A combobox field allowing to pick days for the `Monthly` and `Yearly` mode in the {@link Scheduler.view.recurrence.RecurrenceEditor recurrence dialog}.
 *
 * @extends Core/widget/Combo
 * @classType recurrencedayscombo
 */

class RecurrenceDaysCombo extends LocalizableComboItems(Combo) {
  static get $name() {
    return 'RecurrenceDaysCombo';
  } // Factoryable type name

  static get type() {
    return 'recurrencedayscombo';
  }

  static get defaultConfig() {
    const allDaysValue = 'SU,MO,TU,WE,TH,FR,SA';
    return {
      allDaysValue,
      editable: false,
      defaultValue: allDaysValue,
      workingDaysValue: 'MO,TU,WE,TH,FR',
      nonWorkingDaysValue: 'SU,SA',
      splitCls: 'b-recurrencedays-split',
      displayField: 'text',
      valueField: 'value'
    };
  }

  buildLocalizedItems() {
    const me = this;
    me._weekDays = null;
    return me.weekDays.concat([{
      value: me.allDaysValue,
      text: me.L('L{day}'),
      cls: me.splitCls
    }, {
      value: me.workingDaysValue,
      text: me.L('L{weekday}')
    }, {
      value: me.nonWorkingDaysValue,
      text: me.L('L{weekend day}')
    }]);
  }

  get weekDays() {
    const me = this;

    if (!me._weekDays) {
      const weekStartDay = DateHelper.weekStartDay;
      const dayNames = DateHelper.getDayNames().map((text, index) => ({
        text,
        value: RecurrenceDayRuleEncoder.encodeDay(index)
      })); // we should start week w/ weekStartDay

      me._weekDays = dayNames.slice(weekStartDay).concat(dayNames.slice(0, weekStartDay));
    }

    return me._weekDays;
  }

  set value(value) {
    const me = this;

    if (value && Array.isArray(value)) {
      value = value.join(',');
    } // if the value has no matching option in the store we need to use default value

    if (!value || !me.store.findRecord('value', value)) {
      value = me.defaultValue;
    }

    super.value = value;
  }

  get value() {
    let value = super.value;

    if (value && Array.isArray(value)) {
      value = value.join(',');
    }

    return value;
  }

} // Register this widget type with its Factory

RecurrenceDaysCombo.initClass();
RecurrenceDaysCombo._$name = 'RecurrenceDaysCombo';

/**
 * @module Scheduler/view/recurrence/field/RecurrenceDaysButtonGroup
 */

/**
 * A segmented button field allowing to pick days for the "Weekly" mode in the {@link Scheduler.view.recurrence.RecurrenceEditor recurrence dialog}.
 *
 * @extends Core/widget/ButtonGroup
 */

class RecurrenceDaysButtonGroup extends ButtonGroup {
  static get $name() {
    return 'RecurrenceDaysButtonGroup';
  } // Factoryable type name

  static get type() {
    return 'recurrencedaysbuttongroup';
  }

  static get defaultConfig() {
    return {
      defaults: {
        cls: 'b-raised',
        toggleable: true
      }
    };
  }

  construct(config = {}) {
    const me = this;
    config.columns = 7;
    config.items = me.buildItems();
    super.construct(config);
  }

  updateItemText(item) {
    const day = RecurrenceDayRuleEncoder.decodeDay(item.value)[0];
    item.text = DateHelper.getDayName(day).substring(0, 3);
  }

  buildItems() {
    const me = this;

    if (!me.__items) {
      const weekStartDay = DateHelper.weekStartDay;
      const dayNames = DateHelper.getDayNames().map((text, index) => ({
        text: text.substring(0, 3),
        value: RecurrenceDayRuleEncoder.encodeDay(index)
      })); // we should start week w/ weekStartDay

      me.__items = dayNames.slice(weekStartDay).concat(dayNames.slice(0, weekStartDay));
    }

    return me.__items;
  }

  set value(value) {
    if (value && Array.isArray(value)) {
      value = value.join(',');
    }

    super.value = value;
  }

  get value() {
    let value = super.value;

    if (value && Array.isArray(value)) {
      value = value.join(',');
    }

    return value; // return value ? value.split(',') : [];
  }

  onLocaleChange() {
    // update button texts on locale switch
    this.items.forEach(this.updateItemText, this);
  }

  updateLocalization() {
    this.onLocaleChange();
    super.updateLocalization();
  }

  get widgetClassList() {
    const classList = super.widgetClassList; // to look more like a real field

    classList.push('b-field');
    return classList;
  }

}

RecurrenceDaysButtonGroup.initClass();
RecurrenceDaysButtonGroup._$name = 'RecurrenceDaysButtonGroup';

/**
 * A segmented button field allowing to pick month days for the `Monthly` mode in the {@link Scheduler.view.recurrence.RecurrenceEditor recurrence dialog}.
 *
 * @extends Core/widget/ButtonGroup
 */

class RecurrenceMonthDaysButtonGroup extends ButtonGroup {
  static get $name() {
    return 'RecurrenceMonthDaysButtonGroup';
  } // Factoryable type name

  static get type() {
    return 'recurrencemonthdaysbuttongroup';
  }

  static get defaultConfig() {
    return {
      defaults: {
        toggleable: true,
        cls: 'b-raised'
      }
    };
  }

  get minValue() {
    return 1;
  }

  get maxValue() {
    return 31;
  }

  construct(config = {}) {
    const me = this;
    config.columns = 7;
    config.items = me.buildItems();
    super.construct(config);
  }

  buildItems() {
    const me = this,
          items = [];

    for (let value = me.minValue; value <= me.maxValue; value++) {
      // button config
      items.push({
        text: value + '',
        value
      });
    }

    return items;
  }

  get widgetClassList() {
    const classList = super.widgetClassList; // to look more like a real field

    classList.push('b-field');
    return classList;
  }

}

RecurrenceMonthDaysButtonGroup.initClass();
RecurrenceMonthDaysButtonGroup._$name = 'RecurrenceMonthDaysButtonGroup';

/**
 * A segmented button field allowing to pick months for the `Yearly` mode in the {@link Scheduler.view.recurrence.RecurrenceEditor recurrence dialog}.
 *
 * @extends Core/widget/ButtonGroup
 */

class RecurrenceMonthsButtonGroup extends ButtonGroup {
  static get $name() {
    return 'RecurrenceMonthsButtonGroup';
  } // Factoryable type name

  static get type() {
    return 'recurrencemonthsbuttongroup';
  }

  static get defaultConfig() {
    return {
      defaults: {
        toggleable: true,
        cls: 'b-raised'
      }
    };
  }

  construct(config = {}) {
    const me = this;
    config.columns = 4;
    config.items = me.buildItems();
    super.construct(config);
  }

  buildItems() {
    return DateHelper.getMonthNames().map((item, index) => ({
      text: item.substring(0, 3),
      value: index + 1 // 1-based

    }));
  }

  updateItemText(item) {
    item.text = DateHelper.getMonthName(item.value - 1).substring(0, 3);
  }

  onLocaleChange() {
    // update button texts on locale switch
    this.items.forEach(this.updateItemText, this);
  }

  updateLocalization() {
    this.onLocaleChange();
    super.updateLocalization();
  }

  get widgetClassList() {
    const classList = super.widgetClassList; // to look more like a real field

    classList.push('b-field');
    return classList;
  }

}

RecurrenceMonthsButtonGroup.initClass();
RecurrenceMonthsButtonGroup._$name = 'RecurrenceMonthsButtonGroup';

/**
 * @module Scheduler/view/recurrence/field/RecurrenceStopConditionCombo
 */

/**
 * A combobox field allowing to choose stop condition for the recurrence in the {@link Scheduler.view.recurrence.RecurrenceEditor recurrence dialog}.
 *
 * @extends Core/widget/Combo
 * @classType recurrencestopconditioncombo
 */

class RecurrenceStopConditionCombo extends LocalizableComboItems(Combo) {
  static get $name() {
    return 'RecurrenceStopConditionCombo';
  } // Factoryable type name

  static get type() {
    return 'recurrencestopconditioncombo';
  }

  static get defaultConfig() {
    return {
      editable: false,
      placeholder: 'Never',
      displayField: 'text',
      valueField: 'value'
    };
  }

  buildLocalizedItems() {
    return [{
      value: 'never',
      text: this.L('L{Never}')
    }, {
      value: 'count',
      text: this.L('L{After}')
    }, {
      value: 'date',
      text: this.L('L{On date}')
    }];
  }

  set value(value) {
    // Use 'never' instead of falsy value
    value = value || 'never';
    super.value = value;
  }

  get value() {
    return super.value;
  }

  get recurrence() {
    return this._recurrence;
  }

  set recurrence(recurrence) {
    let value = null;

    if (recurrence.endDate) {
      value = 'date';
    } else if (recurrence.count) {
      value = 'count';
    }

    this._recurrence = recurrence;
    this.value = value;
  }

}

RecurrenceStopConditionCombo.initClass();
RecurrenceStopConditionCombo._$name = 'RecurrenceStopConditionCombo';

/**
 * @module Scheduler/view/recurrence/field/RecurrencePositionsCombo
 */

/**
 * A combobox field allowing to specify day positions in the {@link Scheduler.view.recurrence.RecurrenceEditor recurrence editor}.
 *
 * @extends Core/widget/Combo
 * @classType recurrencepositionscombo
 */

class RecurrencePositionsCombo extends LocalizableComboItems(Combo) {
  static get $name() {
    return 'RecurrencePositionsCombo';
  } // Factoryable type name

  static get type() {
    return 'recurrencepositionscombo';
  }

  static get defaultConfig() {
    return {
      editable: false,
      splitCls: 'b-sch-recurrencepositions-split',
      displayField: 'text',
      valueField: 'value',
      defaultValue: 1,
      maxPosition: 5
    };
  }

  buildLocalizedItems() {
    const me = this;
    return me.buildDayNumbers().concat([// the following lines are added to satisfy the 904_unused localization test
    // to let it know that these locales are used:
    // this.L('L{position-1}')
    {
      value: '-1',
      text: me.L('L{position-1}'),
      cls: me.splitCls
    }]);
  }

  buildDayNumbers() {
    const me = this; // the following lines are added to satisfy the 904_unused localization test
    // to let it know that these locales are used:
    // this.L('L{position1}')
    // this.L('L{position2}')
    // this.L('L{position3}')
    // this.L('L{position4}')
    // this.L('L{position5}')

    return ArrayHelper.populate(me.maxPosition, i => ({
      value: i,
      text: me.L(`position${i + 1}`)
    }));
  }

  set value(value) {
    const me = this;

    if (value && Array.isArray(value)) {
      value = value.join(',');
    } // if the value has no matching option in the store we need to use default value

    if (!value || !me.store.findRecord('value', value)) {
      value = me.defaultValue;
    }

    super.value = value;
  }

  get value() {
    const value = super.value;
    return value ? `${value}`.split(',').map(item => parseInt(item, 10)) : [];
  }

}

RecurrencePositionsCombo.initClass();
RecurrencePositionsCombo._$name = 'RecurrencePositionsCombo';

/**
 * @module Scheduler/view/recurrence/RecurrenceEditor
 */

/**
 * Class implementing a dialog to edit {@link Scheduler.model.RecurrenceModel Recurrence model}.
 * The class is used by the {@link Scheduler.view.mixin.RecurringEvents recurring events} feature and you don't need to instantiate it normally.
 * Before showing the dialog need to use {@link Core.widget.Container#property-record} to load a {@link Scheduler.model.RecurrenceModel Recurrence model}
 * data into the editor fields. For example:
 *
 * ```javascript
 * // make the editor instance
 * const editor = new RecurrenceEditor();
 * // load recurrnce model into it
 * editor.record = new RecurrenceModel({ frequency : "WEEKLY" });
 * // display the editor
 * editor.show();
 * ```
 *
 * @extends Core/widget/Popup
 * @classType recurrenceeditor
 */

class RecurrenceEditor extends Popup {
  static get $name() {
    return 'RecurrenceEditor';
  } // Factoryable type name

  static get type() {
    return 'recurrenceeditor';
  }

  static get configurable() {
    return {
      draggable: true,
      closable: true,
      floating: true,
      cls: 'b-recurrenceeditor',
      title: 'L{Repeat event}',
      autoClose: true,
      width: 400,
      defaults: {
        localeClass: this
      },
      items: {
        frequencyField: {
          type: 'recurrencefrequencycombo',
          name: 'frequency',
          label: 'L{Frequency}',
          weight: 10,
          onChange: 'up.onFrequencyFieldChange'
        },
        intervalField: {
          type: 'numberfield',
          weight: 15,
          name: 'interval',
          label: 'L{Every}',
          min: 1,
          required: true
        },
        daysButtonField: {
          type: 'recurrencedaysbuttongroup',
          weight: 20,
          name: 'days',
          forFrequency: 'WEEKLY'
        },
        // the radio button enabling "monthDaysButtonField" in MONTHLY mode
        monthDaysRadioField: {
          type: 'checkbox',
          weight: 30,
          toggleGroup: 'radio',
          forFrequency: 'MONTHLY',
          label: 'L{Each}',
          checked: true,
          onChange: 'up.onMonthDaysRadioFieldChange'
        },
        monthDaysButtonField: {
          type: 'recurrencemonthdaysbuttongroup',
          weight: 40,
          name: 'monthDays',
          forFrequency: 'MONTHLY'
        },
        monthsButtonField: {
          type: 'recurrencemonthsbuttongroup',
          weight: 50,
          name: 'months',
          forFrequency: 'YEARLY'
        },
        // the radio button enabling positions & days combos in MONTLY & YEARLY modes
        positionAndDayRadioField: {
          type: 'checkbox',
          weight: 60,
          toggleGroup: 'radio',
          forFrequency: 'MONTHLY|YEARLY',
          label: 'L{On the}',
          onChange: 'up.onPositionAndDayRadioFieldChange'
        },
        positionsCombo: {
          type: 'recurrencepositionscombo',
          weight: 80,
          name: 'positions',
          forFrequency: 'MONTHLY|YEARLY'
        },
        daysCombo: {
          type: 'recurrencedayscombo',
          weight: 90,
          name: 'days',
          forFrequency: 'MONTHLY|YEARLY',
          flex: 1
        },
        stopRecurrenceField: {
          type: 'recurrencestopconditioncombo',
          weight: 100,
          label: 'L{End repeat}',
          onChange: 'up.onStopRecurrenceFieldChange'
        },
        countField: {
          type: 'numberfield',
          weight: 110,
          name: 'count',
          min: 2,
          required: true,
          disabled: true,
          label: ' '
        },
        endDateField: {
          type: 'datefield',
          weight: 120,
          name: 'endDate',
          hidden: true,
          disabled: true,
          label: ' ',
          required: true
        }
      },
      bbar: {
        defaults: {
          localeClass: this
        },
        items: {
          foo: {
            type: 'widget',
            cls: 'b-label-filler',
            weight: 100
          },
          saveButton: {
            color: 'b-green',
            text: 'L{Save}',
            onClick: 'up.onSaveClick',
            weight: 200
          },
          cancelButton: {
            color: 'b-gray',
            text: 'L{Object.Cancel}',
            onClick: 'up.onCancelClick',
            weight: 300
          }
        }
      }
    };
  }

  updateReadOnly(readOnly) {
    super.updateReadOnly(readOnly); // No save or cancel buttons. It's purely for information display when in readOnly mode

    this.bbar.hidden = readOnly;
  }

  updateRecord(record) {
    super.updateRecord(record);
    const me = this,
          event = record.timeSpan,
          startDate = event && event.startDate,
          {
      daysButtonField,
      monthDaysButtonField,
      monthsButtonField,
      monthDaysRadioField,
      positionAndDayRadioField,
      stopRecurrenceField
    } = me.widgetMap; // some fields default values are calculated based on event "startDate" value

    if (startDate) {
      // if no "days" value provided
      if (!record.days || !record.days.length) {
        daysButtonField.value = [RecurrenceDayRuleEncoder.encodeDay(startDate.getDay())];
      } // if no "monthDays" value provided

      if (!record.monthDays || !record.monthDays.length) {
        monthDaysButtonField.value = startDate.getDate();
      } // if no "months" value provided

      if (!record.months || !record.months.length) {
        monthsButtonField.value = startDate.getMonth() + 1;
      }
    } // if the record has both "days" & "positions" fields set check "On the" checkbox

    if (record.days && record.positions) {
      positionAndDayRadioField.check(); // TODO: if toggleGroup members are not painted automatic unchecking doesn't work

      if (!me.isPainted) {
        monthDaysRadioField.uncheck();
      }
    } else {
      monthDaysRadioField.check(); // TODO: if toggleGroup members are not painted automatic unchecking doesn't work

      if (!me.isPainted) {
        positionAndDayRadioField.uncheck();
      }
    }

    stopRecurrenceField.recurrence = record;
  }

  onSaveClick() {
    const me = this;

    if (me.saveHandler) {
      me.saveHandler.call(me.thisObj || me, me, me.record);
    } else {
      me.syncEventRecord();
      me.close();
    }
  }

  onCancelClick() {
    const me = this;

    if (me.cancelHandler) {
      me.cancelHandler.call(me.thisObj || me, me, me.record);
    } else {
      me.close();
    }
  }
  /**
   * Updates the provided recurrence model with the contained form data.
   * If recurrence model is not provided updates the last loaded recurrence model.
   */

  syncEventRecord(recurrence) {
    // get values relevant to the RecurrenceModel (from enabled fields only)
    const values = this.getValues(w => w.name in recurrence && !w.disabled);
    recurrence.set(values);
  }

  toggleStopFields() {
    const me = this,
          {
      countField,
      endDateField
    } = me.widgetMap;

    switch (me.widgetMap.stopRecurrenceField.value) {
      case 'count':
        countField.show();
        countField.enable();
        endDateField.hide();
        endDateField.disable();
        break;

      case 'date':
        countField.hide();
        countField.disable();
        endDateField.show();
        endDateField.enable();
        break;

      default:
        countField.hide();
        endDateField.hide();
        countField.disable();
        endDateField.disable();
    }
  }

  onMonthDaysRadioFieldChange({
    checked
  }) {
    const {
      monthDaysButtonField
    } = this.widgetMap;
    monthDaysButtonField.disabled = !checked || !this.isWidgetAvailableForFrequency(monthDaysButtonField);
  }

  onPositionAndDayRadioFieldChange({
    checked
  }) {
    const {
      daysCombo,
      positionsCombo
    } = this.widgetMap; // toggle day & positions combos

    daysCombo.disabled = positionsCombo.disabled = !checked || !this.isWidgetAvailableForFrequency(daysCombo);
  }

  onStopRecurrenceFieldChange() {
    this.toggleStopFields();
  }

  isWidgetAvailableForFrequency(widget, frequency = this.widgetMap.frequencyField.value) {
    return !widget.forFrequency || widget.forFrequency.indexOf(frequency) > -1;
  }

  onFrequencyFieldChange({
    value,
    valid
  }) {
    const me = this,
          items = me.queryAll(w => 'forFrequency' in w);

    if (valid && value) {
      for (let i = 0; i < items.length; i++) {
        const item = items[i];

        if (me.isWidgetAvailableForFrequency(item, value)) {
          item.show();
          item.enable();
        } else {
          item.hide();
          item.disable();
        }
      } // The following lines are added to satisfy the 904_unused localization test
      // to let it know that these locales are used:
      // this.L('L{DAILYintervalUnit}')
      // this.L('L{WEEKLYintervalUnit}')
      // this.L('L{MONTHLYintervalUnit}')
      // this.L('L{YEARLYintervalUnit}')

      me.widgetMap.intervalField.hint = me.L(`${value}intervalUnit`);
      me.toggleFieldsState();
    }
  }

  toggleFieldsState() {
    const me = this,
          {
      widgetMap
    } = this;
    me.onMonthDaysRadioFieldChange({
      checked: widgetMap.monthDaysRadioField.checked
    });
    me.onPositionAndDayRadioFieldChange({
      checked: widgetMap.positionAndDayRadioField.checked
    });
    me.onStopRecurrenceFieldChange();
  }

  updateLocalization() {
    // do extra labels translation (not auto-translated yet)
    const {
      countField,
      intervalField,
      frequencyField
    } = this.widgetMap;
    countField.hint = this.L('L{time(s)}');

    if (frequencyField.value) {
      intervalField.hint = this.L(`${frequencyField.value}intervalUnit`);
    }

    super.updateLocalization();
  }

} // Register this widget type with its Factory

RecurrenceEditor.initClass();
RecurrenceEditor._$name = 'RecurrenceEditor';

/**
 * @module Scheduler/feature/mixin/RecurringEventEdit
 */

/**
 * This mixin class provides recurring events functionality to the {@link Scheduler.feature.EventEdit event editor}.
 * @mixin
 */

var RecurringEventEdit = (Target => class RecurringEventEdit extends (Target || Base) {
  static get $name() {
    return 'RecurringEventEdit';
  }

  static get configurable() {
    return {
      recurringEventsItems: {
        /**
         * Reference to the `Repeat` event field, if used
         * @member {Scheduler.view.recurrence.field.RecurrenceCombo} recurrenceCombo
         * @readonly
         */
        recurrenceCombo: {
          type: 'recurrencecombo',
          label: 'L{EventEdit.Repeat}',
          ref: 'recurrenceCombo',
          weight: 700
        },

        /**
         * Reference to the button that opens the event repeat settings dialog, if used
         * @member {Scheduler.view.recurrence.RecurrenceLegendButton} editRecurrenceButton
         * @readonly
         */
        editRecurrenceButton: {
          type: 'recurrencelegendbutton',
          ref: 'editRecurrenceButton',
          name: 'recurrenceRule',
          color: 'b-gray',
          flex: '1',
          weight: 800
        }
      },

      /**
       * Set to `false` to hide recurring fields in event editor,
       * even if the {@link Scheduler.view.mixin.RecurringEvents#config-enableRecurringEvents Recurring Events} is `true`
       * and a recurring event is being edited.
       * @config {Boolean}
       * @category Recurring
       */
      showRecurringUI: null
    };
  }

  changeEditorConfig(editorConfig) {
    editorConfig.items = { ...editorConfig.items,
      ...this.recurringEventsItems
    }; // EditBase inserts extraItems *after* all default items are in

    editorConfig = super.changeEditorConfig(editorConfig);
    return editorConfig;
  }

  construct(scheduler, config) {
    const me = this;
    super.construct(scheduler, config);
    me.scheduler.on({
      beforeEventDelete: me.onRecurrableEventBeforeDelete,
      beforeEventSave: me.onRecurrableEventBeforeSave,
      thisObj: me
    });
  }

  doDestroy() {
    this._recurrenceConfirmation && this.recurrenceConfirmation.destroy();
    this._recurrenceEditor && this.recurrenceEditor.destroy();
    super.doDestroy();
  }

  onEditorConstructed(editor) {
    const me = this;
    editor.on('hide', me.onRecurringEventEditorHide, me);
    editor.widgetMap.editRecurrenceButton.menu = me.recurrenceEditor;
    me.recurrenceCombo.on('change', me.onRecurrenceComboChange.bind(me));
  }

  internalShowEditor() {
    this.toggleRecurringFieldsVisibility(this.client.enableRecurringEvents && this.showRecurringUI !== false);
  }

  toggleRecurringFieldsVisibility(show = true) {
    const methodName = show ? 'show' : 'hide';
    this.editRecurrenceButton && this.editRecurrenceButton[methodName]();
    this.recurrenceCombo && this.recurrenceCombo[methodName]();
  }

  onRecurringEventEditorHide({
    source
  }) {
    const {
      recurrenceEditor,
      recurrenceConfirmation
    } = this;

    if (recurrenceEditor && recurrenceEditor.isVisible) {
      recurrenceEditor.hide();
    }

    if (recurrenceConfirmation && recurrenceConfirmation.isVisible) {
      recurrenceConfirmation.hide();
    }
  } // Builds RecurrenceModel to load into the recurrenceEditor
  // It builds the model based on either:
  // - recurrence rule string (if provided)
  // - or the event being edited recurrence (if the event is repeating)
  // - or simply make a recurrence model w/ default state (by default means: Frequency=Daily, Interval=1)

  makeRecurrence(rule) {
    const event = this.eventRecord,
          eventCopy = event.copy();
    let recurrence = event.recurrence;

    if (!rule && recurrence) {
      recurrence = recurrence.copy();
    } else {
      recurrence = new event.recurrenceModel(rule ? {
        rule
      } : {});
    } // bind cloned recurrence to the cloned event

    recurrence.timeSpan = eventCopy; // update cloned event w/ start date from the UI field

    eventCopy.setStartDate(this.values.startDate);
    recurrence.suspendTimeSpanNotifying();
    return recurrence;
  }

  onRecurrableEventBeforeSave({
    eventRecord,
    context
  }) {
    const me = this;

    if (eventRecord.supportsRecurring && (eventRecord.isRecurring || eventRecord.isOccurrence)) {
      me.recurrenceConfirmation.confirm({
        actionType: 'update',
        eventRecord,

        changerFn() {
          context.finalize(true);
        },

        cancelFn() {
          context.finalize(false);
        }

      }); // signalizes that we plan to decide save or not asynchronously

      context.async = true;
      return false;
    }
  }

  showDisplayConfirmationOnEventDelete(eventRecord) {
    return this.isEditing && this.eventRecord === eventRecord && eventRecord.supportsRecurring && (eventRecord.isRecurring || eventRecord.isOccurrence);
  }

  onRecurrableEventBeforeDelete({
    eventRecords,
    context
  }) {
    const [eventRecord] = eventRecords;

    if (this.showDisplayConfirmationOnEventDelete(eventRecord)) {
      this.recurrenceConfirmation.confirm({
        actionType: 'delete',
        eventRecord,

        changerFn() {
          context.finalize(true);
        },

        cancelFn() {
          context.finalize(false);
        }

      });
      return false;
    }
  }

  set recurrenceConfirmation(recurrenceConfirmation) {
    this._recurrenceConfirmation = recurrenceConfirmation;
  }

  get recurrenceConfirmation() {
    const me = this;
    let recurrenceConfirmation = me._recurrenceConfirmation;

    if (!recurrenceConfirmation || !recurrenceConfirmation.$$name) {
      recurrenceConfirmation = Widget.create(Object.assign({
        type: 'recurrenceconfirmation',
        owner: me.getEditor()
      }, recurrenceConfirmation));
      me._recurrenceConfirmation = recurrenceConfirmation;
    }

    return recurrenceConfirmation;
  }

  set recurrenceEditor(recurrenceEditor) {
    this._recurrenceEditor = recurrenceEditor;
  }

  get recurrenceEditor() {
    const me = this;
    let recurrenceEditor = me._recurrenceEditor; // Recurrence editor is centered and modal.

    if (!recurrenceEditor || !recurrenceEditor.$$name) {
      recurrenceEditor = Widget.create(Object.assign({
        type: 'recurrenceeditor',
        autoShow: false,
        clippedBy: [me.scheduler.timeAxisSubGridElement, me.scheduler.bodyContainer],
        centered: true,
        modal: true,
        constrainTo: window,
        anchor: false,
        saveHandler: me.recurrenceEditorSaveHandler,
        onBeforeShow: me.onBeforeShowRecurrenceEditor.bind(me),
        thisObj: me
      }, recurrenceEditor));
      me._recurrenceEditor = recurrenceEditor; // Must set *after* construction, otherwise it becomes the default state
      // to reset readOnly back to.  Must use direct property access because
      // getter consults state of editor.

      recurrenceEditor.readOnly = me._readOnly;
    }

    return recurrenceEditor;
  }

  onBeforeShowRecurrenceEditor() {
    const me = this,
          {
      recurrenceEditor,
      eventRecord
    } = me;

    if (recurrenceEditor && eventRecord && eventRecord.supportsRecurring) {
      // if the event has no recurrence yet ..initialize it before showing recurrence editor
      if (!me.recurrence) {
        me.recurrence = me.makeRecurrence();
      } // update the cloned recurrence w/ up to date start date value

      me.recurrence.timeSpan.setStartDate(me.values.startDate); // load RecurrenceModel record into the recurrence editor

      recurrenceEditor.record = me.recurrence; // In case they drag it. Centered falls off if the widget has position set.

      recurrenceEditor.centered = true;
    }
  }

  loadRecurrenceData(recurrence) {
    this.recurrence = recurrence;
    this.updateRecurrenceFields(recurrence);
  }

  updateRecurrenceFields(recurrence) {
    const me = this,
          {
      editRecurrenceButton
    } = me;

    if (me.recurrenceCombo) {
      me.recurrenceCombo.recurrence = recurrence;
    } // update the recurrence legend

    if (editRecurrenceButton) {
      editRecurrenceButton.recurrence = recurrence;
      editRecurrenceButton.value = recurrence ? recurrence.rule : null;

      if (recurrence && me.client.enableRecurringEvents && me.showRecurringUI !== false) {
        editRecurrenceButton.show();
      } else {
        editRecurrenceButton.hide();
      }
    }
  }

  onRecurrenceComboChange({
    source,
    value,
    userAction
  }) {
    const me = this,
          {
      recurrenceEditor
    } = me;

    if (value == source.customValue) {
      // if user picked "Custom" - show recurrence editor
      if (userAction) {
        // This will recurse through the change event into the opposite side
        // of the value test which will call updateRecurrenceFields, where the
        // assignment to the value of the recurrenceCombo will be a non-change.
        // That will sync the state of the recurrenceButton.
        me.recurrenceCombo.recurrence = me.makeRecurrence();

        if (recurrenceEditor.centered) {
          recurrenceEditor.show();
        } else {
          recurrenceEditor.show((me.editRecurrenceButton || source).element);
        }
      }
    } // user has picked some frequency -> make a new recurrence based on it
    else {
        me.loadRecurrenceData(value ? me.makeRecurrence(`FREQ=${value}`) : null);
      }
  }

  recurrenceEditorSaveHandler(editor, recurrence) {
    // apply changes to the kept recurrence
    editor.syncEventRecord(recurrence); // update the recurrence related UI

    this.updateRecurrenceFields(recurrence);
    editor.close();
  }

  onDatesChange(...args) {
    super.onDatesChange(...args);
    const me = this,
          {
      editRecurrenceButton
    } = me;

    if (!me.loadingRecord && editRecurrenceButton) {
      const {
        startDate
      } = me.values;
      if (startDate) editRecurrenceButton.eventStartDate = startDate;
    }
  }

  internalLoadRecord(eventRecord, resourceRecord) {
    if (this.recurrenceCombo && eventRecord && eventRecord.supportsRecurring) {
      this.loadRecurrenceData(eventRecord.recurrence ? this.makeRecurrence() : null);
    }
  }

});

/**
 * @module Scheduler/widget/ResourceCombo
 */

/**
 * Combo subclass which selects resources, highlighting the `eventColor` of each resource in
 * the picker and in the input area.
 *
 * @extends Core/widget/List
 * @classType resourceCombo
 */

class ResourceCombo extends Combo {
  static get $name() {
    return 'ResourceCombo';
  } // Factoryable type name

  static get type() {
    return 'resourcecombo';
  }

  static get configurable() {
    return {
      showEventColor: false,
      picker: {
        cls: 'b-resourcecombo-picker',

        itemIconTpl(record, i) {
          const {
            eventColor
          } = record,
                isColor = eventColor === null || eventColor === void 0 ? void 0 : eventColor.startsWith('#'),
                style = eventColor ? isColor ? ` style="color:${eventColor}"` : '' : ' style="display:none"',
                colorClass = !eventColor || isColor ? '' : ` b-sch-foreground-${eventColor}`;
          return `<div class="b-icon b-icon-square${colorClass}"${style}></div>`;
        }

      }
    };
  }

  changeShowEventColor(showEventColor) {
    return Boolean(showEventColor);
  }

  updateShowEventColor(showEventColor) {
    const {
      _picker
    } = this,
          methodName = showEventColor ? 'add' : 'remove';
    this.element.classList[methodName]('b-show-event-color');
    _picker === null || _picker === void 0 ? void 0 : _picker.element.classList[methodName]('b-show-event-color');
  }

  changePicker(picker, oldPicker) {
    var _picker2;

    picker = super.changePicker(picker, oldPicker);
    (_picker2 = picker) === null || _picker2 === void 0 ? void 0 : _picker2.element.classList[this.showEventColor ? 'add' : 'remove']('b-show-event-color');
    return picker;
  } // Implementation needed at this level because it has two inner elements in its inputWrap

  get innerElements() {
    return [{
      class: 'b-icon b-resource-icon b-icon-square b-hide-display',
      reference: 'resourceIcon'
    }, this.inputElement];
  }

  syncInputFieldValue() {
    var _me$selected$eventCol, _me$selected;

    const me = this,
          {
      resourceIcon,
      lastResourceIconCls
    } = me,
          {
      classList
    } = resourceIcon,
          eventColor = (_me$selected$eventCol = (_me$selected = me.selected) === null || _me$selected === void 0 ? void 0 : _me$selected.eventColor) !== null && _me$selected$eventCol !== void 0 ? _me$selected$eventCol : '';
    super.syncInputFieldValue(); // Remove last colour whichever way it was done

    resourceIcon.style.color = '';
    lastResourceIconCls && classList.remove(lastResourceIconCls);
    me.lastResourceIconCls = null;

    if (eventColor) {
      if (eventColor.startsWith('#')) {
        resourceIcon.style.color = eventColor;
      } else {
        me.lastResourceIconCls = `b-sch-foreground-${eventColor}`;
        classList.add(me.lastResourceIconCls);
      }

      classList.remove('b-hide-display');
    } else {
      classList.add('b-hide-display');
    }
  }

} // Register this widget type with its Factory

ResourceCombo.initClass();
ResourceCombo._$name = 'ResourceCombo';

/**
 * @module Scheduler/feature/EventEdit
 */

/**
 * Feature that displays a popup containing widgets for editing event data.
 *
 *  {@inlineexample scheduler/EventEdit.js}
 *
 * To customize its contents you can:
 *
 * * Reconfigure built in widgets by providing override configs in the {@link Scheduler.feature.base.EditBase#config-items items} config.
 * * Change the date format of the date & time fields: {@link Scheduler.feature.base.EditBase#config-dateFormat dateFormat} and {@link Scheduler.feature.base.EditBase#config-timeFormat timeFormat}
 * * Configure provided widgets in the editor and add your own in the {@link Scheduler.feature.base.EditBase#config-items items} config.
 * * Advanced: Reconfigure the whole editor widget using {@link #config-editorConfig}
 *
 * ## Built in widgets
 *
 * The built in widgets are:
 *
 * | Widget ref             | Type                                                                            | Weight | Description                                                    |
 * |------------------------|---------------------------------------------------------------------------------|--------|----------------------------------------------------------------|
 * | `nameField`            | {@link Core.widget.TextField TextField}                                         | 100    | Edit name                                                      |
 * | `resourceField`        | {@link Scheduler.widget.ResourceCombo ResourceCombo}                            | 200    | Pick resource(s)                                               |
 * | `startDateField`       | {@link Core.widget.DateField DateField}                                         | 300    | Edit startDate (date part)                                     |
 * | `startTimeField`       | {@link Core.widget.TimeField TimeField}                                         | 400    | Edit startDate (time part)                                     |
 * | `endDateField`         | {@link Core.widget.DateField DateField}                                         | 500    | Edit endDate (date part)                                       |
 * | `endTimeField`         | {@link Core.widget.TimeField TimeField}                                         | 600    | Edit endDate (time part)                                       |
 * | `recurrenceCombo`      | {@link Scheduler.view.recurrence.field.RecurrenceCombo RecurrenceCombo}         | 700    | Select recurrence rule (only visible if recurrence is used)    |
 * | `editRecurrenceButton` | {@link Scheduler.view.recurrence.RecurrenceLegendButton RecurrenceLegendButton} | 800    | Edit the recurrence rule  (only visible if recurrence is used) |
 *
 * The built in buttons are:
 *
 * | Widget ref             | Type                                                                            | Weight | Description                                                    |
 * |------------------------|---------------------------------------------------------------------------------|--------|----------------------------------------------------------------|
 * | `saveButton`           | {@link Core.widget.Button Button}                                               | 100    | Save event button on the bbar                                  |
 * | `deleteButton`         | {@link Core.widget.Button Button}                                               | 200    | Delete event button on the bbar                                |
 * | `cancelButton`         | {@link Core.widget.Button Button}                                               | 300    | Cancel event editing button on the bbar                        |
 *
 * ## Removing a built in widgets
 *
 * To remove a built in widget, specify its `ref` as `false` in the `items` config:
 *
 * ```javascript
 * const scheduler = new Scheduler({
 *     features : {
 *         eventEdit : {
 *             items : {
 *                 // Remove the start time field
 *                 startTimeField : false
 *             }
 *         }
 *     }
 * })
 * ```
 *
 * Bottom buttons may be hidden using `bbar` config passed to `editorConfig`:
 * ```javascript
 * const scheduler = new Scheduler({
 *     features : {
 *         eventEdit : {
 *             editorConfig : {
 *                 bbar : {
 *                     items : {
 *                         deleteButton : false
 *                     }
 *                 }
 *             }
 *         }
 *     }
 * })
 * ```
 *
 * ## Customizing a built in widget
 *
 * To customize a built in widget, use its `ref` as the key in the `items` config and specify the configs you want
 * to change (they will merge with the widgets default configs):
 *
 * ```javascript
 * const scheduler = new Scheduler({
 *     features : {
 *         eventEdit : {
 *             items : {
 *                 // ref for an existing field
 *                 nameField : {
 *                     // Change its label
 *                     label : 'Description'
 *                 }
 *             }
 *         }
 *     }
 * })
 * ```
 *
 * ## Adding custom widgets
 *
 * To add a custom widget, add an entry to the `items` config. The `name` property links the input field to a field in
 * the loaded event record:
 *
 * ```javascript
 * const scheduler = new Scheduler({
 *     features : {
 *         eventEdit : {
 *             items : {
 *                 // Key to use as fields ref (for easier retrieval later)
 *                 color : {
 *                     type  : 'combo',
 *                     label : 'Color',
 *                     items : ['red', 'green', 'blue'],
 *                     // name will be used to link to a field in the event record when loading and saving in the editor
 *                     name  : 'eventColor'
 *                 }
 *             }
 *         }
 *     }
 * })
 * ```
 *
 * For more info on customizing the event editor, please see Guides/Customization/Customize event editor
 *
 * This feature is **enabled** by default
 *
 * @mixes Scheduler/feature/mixin/RecurringEventEdit
 * @extends Scheduler/feature/base/EditBase
 * @demo Scheduler/eventeditor
 * @classtype eventEdit
 */

class EventEdit extends EditBase.mixin(RecurringEventEdit, Delayable) {
  //region Config
  static get $name() {
    return 'EventEdit';
  }

  static get configurable() {
    return {
      /**
       * The event that shall trigger showing the editor. Defaults to `eventdblclick`, set to `` or null to disable editing of existing events.
       * @config {String}
       * @default
       * @category Editor
       */
      triggerEvent: 'eventdblclick',

      /**
       * True to show a combo for picking resource:
       * ```javascript
       * const scheduler = new Scheduler({
       *    features : {
       *       eventEdit : {
       *           // Merged with the provided items
       *           // So we can override any config of any provided field.
       *          items : {
       *              resourceField : {
       *                  hidden : true
       *              }
       *           }
       *        }
       *     }
       * });
       * ```
       * @config {Boolean}
       * @default
       * @category Editor widgets
       * @deprecated 4.0.0 Configure using the `resourceField` property in the `items`
       * of {@link #config-editorConfig}
       */
      showResourceField: true,

      /**
       * Config for the resourceField constructor:
       * ```javascript
       * const scheduler = new Scheduler({
       *    features : {
       *       eventEdit : {
       *           // Merged with the provided items
       *           // So we can override any config of any provided field.
       *          items : {
       *              resourceField : {
       *                  label : 'Calendar
       *              }
       *           }
       *        }
       *     }
       * });
       * ```
       * @config {Object}
       * @category Editor widgets
       * @deprecated 4.0.0 Configure using the `resourceField` property in the `items`
       * of {@link #config-editorConfig}
       */
      resourceFieldConfig: null,

      /**
       * The data field in the model that defines the eventType.
       * Applied as class (b-eventtype-xx) to the editors element, to allow showing/hiding fields depending on
       * eventType. Dynamic toggling of fields in the editor is activated by adding an `eventTypeField` field to
       * your widget:
       *
       * ```javascript
       * const scheduler = new Scheduler({
       *    features : {
       *       eventEdit : {
       *           items : {
       *               eventTypeField : {
       *                  type  : 'combo',
       *                  name  : 'eventType',
       *                  label : 'Type',
       *                  items : ['Appointment', 'Internal', 'Meeting']
       *               }
       *           }
       *        }
       *     }
       * });
       * ```
       * Note, your event model class also must declare this field:
       * ```javascript
       *  class MyEvent extends EventModel {
       *      static get fields() {
       *          return [
       *              { name : 'eventType' }
       *          ];
       *      }
       *  }
       * ```
       * @config {String}
       * @default
       * @category Editor
       */
      typeField: 'eventType',

      /**
       * The current {@link Scheduler.model.EventModel} record, which is being edited by the event editor.
       * @property {Scheduler.model.EventModel}
       * @readonly
       */
      eventRecord: null,

      /**
       * Specify `true` to put the editor in read only mode.
       * @config {Boolean}
       * @default false
       */
      readOnly: null,

      /**
       * The configuration for the internal editor widget. With this config you can control the *type*
       * of editor (defaults to `Popup`) and which widgets to show,
       * change the items in the `bbar`, or change whether the popup should be modal etc.
       *
       * ```javascript
       * const scheduler = new Scheduler({
       *     features : {
       *         eventEdit  : {
       *             editorConfig : {
       *                 modal  : true,
       *                 cls    : 'my-editor' // A CSS class,
       *                 items  : {
       *                     owner : {
       *                         weight : -100, // Will sort above system-supplied fields which are weight 100 to 800
       *                         type   : 'usercombo',
       *                         name   : 'owner',
       *                         label  : 'Owner'
       *                     },
       *                     agreement : {
       *                         weight : 1000, // Will sort below system-supplied fields which are weight 100 to 800
       *                         type   : 'checkbox',
       *                         name   : 'agreement',
       *                         label  : 'Agree to terms'
       *                     }
       *                 },
       *                 bbar : {
       *                     items : {
       *                         deleteButton : {
       *                             hidden : true
       *                         }
       *                     }
       *                 }
       *             }
       *         }
       *     }
       * });
       * ```
       *
       * Or to use your own custom editor:
       *
       * ```javascript
       * const scheduler = new Scheduler({
       *     features : {
       *         eventEdit  : {
       *             editorConfig : {
       *                 type : 'myCustomEditorType'
       *             }
       *         }
       *     }
       * });
       * ```
       * @config {Object}
       * @category Editor
       */
      editorConfig: {
        type: 'eventeditor',
        title: 'L{EventEdit.Edit event}',
        closable: true,
        localeClass: this,
        defaults: {
          localeClass: this
        },
        items: {
          /**
           * Reference to the name field, if used
           * @member {Core.widget.TextField} nameField
           * @readonly
           */
          nameField: {
            type: 'text',
            label: 'L{Name}',
            clearable: true,
            name: 'name',
            weight: 100,
            required: true
          },

          /**
           * Reference to the resource field, if used
           * @member {Core.widget.Combo} resourceField
           * @readonly
           */
          resourceField: {
            type: 'resourcecombo',
            label: 'L{Resource}',
            name: 'resource',
            editable: true,
            valueField: 'id',
            displayField: 'name',
            highlightExternalChange: false,
            weight: 200
          },

          /**
           * Reference to the start date field, if used
           * @member {Core.widget.DateField} startDateField
           * @readonly
           */
          startDateField: {
            type: 'date',
            cls: 'b-inline',
            clearable: false,
            required: true,
            label: 'L{Start}',
            name: 'startDate',
            flex: '1 0 60%',
            weight: 300
          },

          /**
           * Reference to the start time field, if used
           * @member {Core.widget.TimeField} startTimeField
           * @readonly
           */
          startTimeField: {
            type: 'time',
            clearable: false,
            required: true,
            name: 'startDate',
            cls: 'b-match-label',
            flex: '1 0 40%',
            weight: 400
          },

          /**
           * Reference to the end date field, if used
           * @member {Core.widget.DateField} endDateField
           * @readonly
           */
          endDateField: {
            type: 'date',
            cls: 'b-inline',
            clearable: false,
            required: true,
            label: 'L{End}',
            name: 'endDate',
            flex: '1 0 60%',
            weight: 500
          },

          /**
           * Reference to the end time field, if used
           * @member {Core.widget.TimeField} endTimeField
           * @readonly
           */
          endTimeField: {
            type: 'time',
            clearable: false,
            required: true,
            name: 'endDate',
            cls: 'b-match-label',
            flex: '1 0 40%',
            weight: 600
          }
        },
        bbar: {
          // When readOnly, child buttons are hidden
          hideWhenEmpty: true,
          defaults: {
            localeClass: this
          },
          items: {
            /**
             * Reference to the save button, if used
             * @member {Core.widget.Button} saveButton
             * @readonly
             */
            saveButton: {
              color: 'b-green',
              text: 'L{Save}',
              weight: 100
            },

            /**
             * Reference to the delete button, if used
             * @member {Core.widget.Button} deleteButton
             * @readonly
             */
            deleteButton: {
              color: 'b-gray',
              text: 'L{Delete}',
              weight: 200
            },

            /**
             * Reference to the cancel button, if used
             * @member {Core.widget.Button} cancelButton
             * @readonly
             */
            cancelButton: {
              color: 'b-gray',
              text: 'L{Object.Cancel}',
              weight: 300
            }
          }
        }
      },
      targetEventElement: null
    };
  }

  static get pluginConfig() {
    return {
      chain: ['populateEventMenu', 'onEventEnterKey'],
      assign: ['editEvent']
    };
  } //endregion
  //region Init & destroy

  construct(scheduler, config) {
    this.scheduler = scheduler; // Default to the scheduler's state, but configs may override

    this.readOnly = scheduler.readOnly;
    super.construct(scheduler, config);
    scheduler.on({
      projectChange: 'onChangeProject',
      readOnly: 'onClientReadOnlyToggle',
      thisObj: this
    });
  } //endregion
  //region Editing

  /**
   * Get/set readonly state
   * @property {Boolean}
   */

  get readOnly() {
    return this.editor ? this.editor.readOnly : this._readOnly;
  }

  updateReadOnly(readOnly) {
    if (this.editor) {
      this.editor.readOnly = readOnly;
    }
  }

  onClientReadOnlyToggle({
    readOnly
  }) {
    this.readOnly = readOnly;
  }
  /**
   * Gets an editor instance. Creates on first call, reuses on consecutive
   * @internal
   * @returns {Core.widget.Popup} Editor popup
   */

  getEditor() {
    var _me$onEditorConstruct, _me$eventTypeField, _me$saveButton, _me$deleteButton, _me$cancelButton;

    const me = this;
    let {
      editor
    } = me;

    if (editor) {
      return editor;
    }

    editor = me.editor = Widget.create(me.getEditorConfig()); // Must set *after* construction, otherwise it becomes the default state
    // to reset readOnly back to. Must use direct property access because
    // getter consults state of editor.

    editor.readOnly = me._readOnly;

    if (editor.items.length === 0) {
      console.warn('Event Editor configured without any `items`');
    } // add listeners programmatically so users cannot override them accidentally

    editor.on({
      beforehide: 'resetEditingContext',
      beforeshow: 'onBeforeEditorShow',
      keydown: 'onPopupKeyDown',
      thisObj: me
    });
    /**
     * Fired before the editor will load the event record data into its input fields. This is useful if you
     * want to modify the fields before data is loaded (e.g. set some input field to be readonly)
     * @event eventEditBeforeSetRecord
     * @param {Core.widget.Container} source The editor widget
     * @param {Scheduler.model.EventModel} record The record
     */

    this.scheduler.relayEvents(editor, ['beforeSetRecord'], 'eventEdit'); // assign widget variables, using widget name: startDate -> me.startDateField
    // widgets with id set use that instead, id -> me.idField

    Object.values(editor.widgetMap).forEach(widget => {
      const ref = widget.ref || widget.id; // don't overwrite if already defined

      if (ref && !me[ref]) {
        me[ref] = widget;

        switch (widget.name) {
          case 'startDate':
          case 'endDate':
            widget.on('change', me.onDatesChange, me);
            break;
        }
      }
    }); // launch onEditorConstructed hook if provided

    (_me$onEditorConstruct = me.onEditorConstructed) === null || _me$onEditorConstruct === void 0 ? void 0 : _me$onEditorConstruct.call(me, editor);
    (_me$eventTypeField = me.eventTypeField) === null || _me$eventTypeField === void 0 ? void 0 : _me$eventTypeField.on('change', me.onEventTypeChange, me);
    (_me$saveButton = me.saveButton) === null || _me$saveButton === void 0 ? void 0 : _me$saveButton.on('click', me.onSaveClick, me);
    (_me$deleteButton = me.deleteButton) === null || _me$deleteButton === void 0 ? void 0 : _me$deleteButton.on('click', me.onDeleteClick, me);
    (_me$cancelButton = me.cancelButton) === null || _me$cancelButton === void 0 ? void 0 : _me$cancelButton.on('click', me.onCancelClick, me);
    return me.editor;
  }

  getEditorConfig() {
    const me = this,
          {
      autoClose,
      cls,
      scheduler
    } = me;
    return ObjectHelper.assign({
      owner: scheduler,
      eventEditFeature: me,
      weekStartDay: me.weekStartDay,
      align: 'b-t',
      id: `${scheduler.id}-event-editor`,
      autoShow: false,
      anchor: true,
      scrollAction: 'realign',
      clippedBy: [scheduler.timeAxisSubGridElement, scheduler.bodyContainer],
      constrainTo: window,
      autoClose,
      cls
    }, me.editorConfig);
  } // Called from editEvent() to actually show the editor

  internalShowEditor(eventRecord, resourceRecord, align = null) {
    var _align, _align$target, _align2;

    const me = this,
          scheduler = me.scheduler,
          // Align to the element (b-sch-event) and not the wrapper
    eventElement = ((_align = align) === null || _align === void 0 ? void 0 : (_align$target = _align.target) === null || _align$target === void 0 ? void 0 : _align$target.nodeType) === 1 ? align.target : DomHelper.down(scheduler.getElementFromEventRecord(eventRecord, resourceRecord), scheduler.eventInnerSelector);
    align = (_align2 = align) !== null && _align2 !== void 0 ? _align2 : {
      // Align to the element (b-sch-event) and not the wrapper
      target: eventElement,
      anchor: true
    }; // Event not in current TimeAxis - cannot be edited without extending the TimeAxis.
    // If there's no event element and the eventRecord is not in the store, we still
    // edit centered on the Scheduler - we're adding a new event

    if (align.target || !eventRecord.isPartOfStore(scheduler.eventStore)) {
      /**
       * Fires on the owning Scheduler before an event is displayed in an editor.
       * This may be listened for to allow an application to take over event editing duties. Returning `false`
       * stops the default editing UI from being shown.
       * @event beforeEventEdit
       * @param {Scheduler.view.Scheduler} source The scheduler
       * @param {Scheduler.feature.EventEdit} eventEdit The eventEdit feature
       * @param {Scheduler.model.EventModel} eventRecord The record about to be shown in the event editor.
       * @param {Scheduler.model.ResourceModel} resourceRecord The Resource record for the event. If the event
       * is being created, it will not contain a resource, so this parameter specifies the resource the
       * event is being created for.
       * @param {HTMLElement} eventElement The element which represents the event in the scheduler display.
       * @preventable
       */
      if (scheduler.trigger('beforeEventEdit', {
        eventEdit: me,
        eventRecord,
        resourceRecord,
        eventElement
      }) === false) {
        var _me$phantomEventEleme;

        scheduler.element.classList.remove('b-eventeditor-editing');
        (_me$phantomEventEleme = me.phantomEventElement) === null || _me$phantomEventEleme === void 0 ? void 0 : _me$phantomEventEleme.remove();
        me.phantomEventElement = null;
        return;
      }

      me.resourceRecord = resourceRecord;
      const editor = me.getEditor(eventRecord);
      me.editingContext = {
        eventRecord,
        resourceRecord,
        eventElement,
        editor
      };
      super.internalShowEditor && super.internalShowEditor(eventRecord, resourceRecord, align);

      if (me.typeField) {
        me.toggleEventType(eventRecord.get(me.typeField));
      }

      me.loadRecord(eventRecord, resourceRecord); // Honour alignment settings "anchor" and "centered" which may be injected from editorConfig.

      if (editor.centered || !editor.anchor) {
        editor.show();
      } else if (eventElement) {
        me.targetEventElement = eventElement;
        editor.showBy(align);
      } // We are adding an unrendered event. Display the editor centered
      else {
          editor.show(); // Must be done after show because show always reverts to its configured centered setting.

          editor.updateCentered(true);
        } // Adjust time field step increment based on timeAxis resolution

      const timeResolution = scheduler.timeAxisViewModel.timeResolution;

      if (timeResolution.unit === 'hour' || timeResolution.unit === 'minute') {
        const step = `${timeResolution.increment}${timeResolution.unit}`;

        if (me.startTimeField) {
          me.startTimeField.step = step;
        }

        if (me.endTimeField) {
          me.endTimeField.step = step;
        }
      }
    }
  } // Fired in a listener so that it's after the auto-called onBeforeShow listeners so that
  // subscribers to the beforeEventEditShow are called at exactly the correct lifecycle point.

  onBeforeEditorShow() {
    /**
     * Fires on the owning Scheduler when the editor for an event is available but before it is populated with
     * data and shown. Allows manipulating fields etc.
     * @event beforeEventEditShow
     * @param {Scheduler.view.Scheduler} source The scheduler
     * @param {Scheduler.feature.EventEdit} eventEdit The eventEdit feature
     * @param {Scheduler.model.EventModel} eventRecord The record about to be shown in the event editor.
     * @param {Scheduler.model.ResourceModel} resourceRecord The Resource record for the event. If the event
     * is being created, it will not contain a resource, so this parameter specifies the resource the
     * event is being created for.
     * @param {HTMLElement} eventElement The element which represents the event in the scheduler display.
     * @param {Core.widget.Popup} editor The editor
     */
    this.scheduler.trigger('beforeEventEditShow', Object.assign({
      eventEdit: this
    }, this.editingContext));
  }

  updateTargetEventElement(targetEventElement, oldTargetEventElement) {
    if (targetEventElement) {
      targetEventElement.classList.add('b-editing');
    }

    if (oldTargetEventElement) {
      oldTargetEventElement.classList.remove('b-editing');
    }
  }
  /**
   * Opens an editor for the passed event. This function is exposed on Scheduler and can be called as
   * `scheduler.editEvent()`.
   * @param {Scheduler.model.EventModel} eventRecord Event to edit
   * @param {Scheduler.model.ResourceModel} [resourceRecord] The Resource record for the event.
   * This parameter is needed if the event is newly created for a resource and has not been assigned, or when using
   * multi assignment.
   * @param {HTMLElement} [element] Element to anchor editor to (defaults to events element)
   */

  editEvent(eventRecord, resourceRecord, element = null) {
    const me = this;

    if (me.isEditing) {
      // old editing flow already running, clean it up
      me.resetEditingContext();
    }

    if (me.disabled) {
      return;
    } // The Promise being async allows a mouseover to trigger the event tip
    // unless we add the editing class immediately.

    me.scheduler.element.classList.add('b-eventeditor-editing'); // Implementations may be async, so the return value must always be propagated.

    return me.doEditEvent(...arguments);
  }
  /**
   * Returns true if the editor is currently active
   * @readonly
   * @property {Boolean}
   */

  get isEditing() {
    var _this$editor;

    return Boolean((_this$editor = this.editor) === null || _this$editor === void 0 ? void 0 : _this$editor.isVisible);
  } // editEvent is the single entry point in the base class.
  // Subclass implementations of the action may differ, so are implemented in doEditEvent

  doEditEvent(eventRecord, resourceRecord, element = null) {
    const me = this,
          scheduler = me.scheduler,
          isNewRecord = !eventRecord.isOccurrence && !eventRecord.isPartOfStore(scheduler.eventStore);

    if (!resourceRecord) {
      // Need to handle resourceId for edge case when creating an event with resourceId and editing it before
      // adding it to the EventStore
      resourceRecord = eventRecord.resource || me.resourceStore.getById(eventRecord.resourceId);
    }

    if (isNewRecord) {
      // Assume ownership of the phantom element
      this.phantomEventElement = element; // Ensure temporal data fields are ready when the editor is shown

      TimeSpan.prototype.normalize.call(eventRecord);
    } // If element is specified (call triggered by EventDragCreate)
    // Then we can align to that, and no scrolling is necessary.
    // If we are simply being asked to edit a new event which is not
    // yet added, the editor is centered, and no scroll is necessary

    if (element || isNewRecord) {
      me.internalShowEditor(eventRecord, resourceRecord, {
        target: element
      });
    } else {
      // Ensure event is in view before showing the editor.
      // Note that we first need to extend the time axis to include
      // currently out of range events.
      scheduler.scrollResourceEventIntoView(resourceRecord, eventRecord, null, {
        animate: true,
        edgeOffset: 0,
        extendTimeAxis: false
      }).then(() => me.internalShowEditor(eventRecord, resourceRecord), () => scheduler.element.classList.remove('b-eventeditor-editing'));
    }
  }
  /**
   * Sets fields values from record being edited
   * @private
   */

  loadRecord(eventRecord, resourceRecord) {
    this.loadingRecord = true;
    this.internalLoadRecord(eventRecord, resourceRecord);
    this.loadingRecord = false;
  }

  internalLoadRecord(eventRecord, resourceRecord) {
    const me = this,
          {
      eventStore
    } = me.client,
          {
      editor
    } = me;
    me.eventRecord = eventRecord;
    me.resourceRecord = resourceRecord;
    editor.record = eventRecord;

    if (me.resourceField) {
      const resources = eventStore.getResourcesForEvent(eventRecord); // Flag on parent Container to indicate that initially blank fields are valid

      editor._isSettingValues = true; // If this is an unassigned event, select the resource we've been provided

      if (!eventStore.storage.includes(eventRecord, true) && me.resourceRecord) {
        me.resourceField.value = me.resourceRecord[me.resourceField.valueField];
      } else if (me.assignmentStore) {
        me.resourceField.value = resources.map(resource => resource[me.resourceField.valueField]);
      }

      editor._isSettingValues = false;
    }

    super.internalLoadRecord(eventRecord, resourceRecord);
  }

  toggleEventType(eventType) {
    // expose eventType in dataset, for querying and styling
    this.editor.element.dataset.eventType = eventType || '';
    this.editor.eachWidget(widget => {
      var _widget$dataset;

      // need {}'s here so we don't return false and end iteration
      ((_widget$dataset = widget.dataset) === null || _widget$dataset === void 0 ? void 0 : _widget$dataset.eventType) && (widget.hidden = widget.dataset.eventType !== eventType);
    });
  } //endregion
  //region Save

  async finalizeEventSave(eventRecord, resourceRecords, resolve, reject) {
    const me = this,
          {
      scheduler,
      eventStore,
      assignmentStore,
      phantomEventElement: element
    } = me;
    let aborted = false; // Prevent multiple commits from this flow

    assignmentStore.suspendAutoCommit(); // Avoid multiple redraws, from event changes + assignment changes

    scheduler.suspendRefresh();
    me.onBeforeSave(eventRecord);
    eventRecord.beginBatch();
    me.updateRecord(eventRecord);
    eventRecord.endBatch();

    if (!eventRecord.isOccurrence) {
      if (eventStore && !eventRecord.stores.length) {
        /**
         * Fires on the owning Scheduler before an event is added
         * @event beforeEventAdd
         * @param {Scheduler.view.Scheduler} source The Scheduler instance.
         * @param {Scheduler.model.EventModel} eventRecord The record about to be added
         * @param {Scheduler.model.ResourceModel[]} resources **Deprecated** Use `resourceRecords` instead
         * @param {Scheduler.model.ResourceModel[]} resourceRecords Resources that the record is assigned to
         * @preventable
         */
        if (scheduler.trigger('beforeEventAdd', {
          eventRecord,
          resourceRecords,
          resources: resourceRecords
        }) !== false) {
          //  Add to eventStore first, then assign the resource. Order is necessary since assigning might
          // involve an AssignmentStore
          eventStore.add(eventRecord);
          const [assignmentRecord] = eventStore.assignEventToResource(eventRecord, resourceRecords);

          if (element) {
            // If a filter was reapplied and filtered out the newly added event we need to clean up the drag proxy...
            if (!me.eventStore.includes(eventRecord)) {
              // Feels a bit strange having that responsibility here, but since it is already handled
              element.remove();
            } else {
              // Hand it over to DomSync (to make sure it does not use any other previously released element,
              // which would break the animation when transitioning from dragproxy -> event element)
              DomSync.addChild(scheduler.foregroundCanvas, element, assignmentRecord.id);
            }

            me.phantomEventElement = null;
          }
        } else {
          aborted = true;
        }
      } else {
        eventStore.assignEventToResource(eventRecord, resourceRecords, true);
      }
    } // An occurrence event record may have changed only resources value. In that case we'll never get into afterChange() method that
    // apply changed data and make an event "real", because resources is not a field and a record won't be marked as dirty.
    // We used temporary field to save updated resources list and get into afterChange() method.
    else if (resourceRecords) {
        eventRecord.set('resourceRecords', resourceRecords);
      }

    if (!aborted) {
      await scheduler.project.commitAsync();
    }

    assignmentStore.resumeAutoCommit(); // Redraw once

    scheduler.resumeRefresh(true);

    if (!aborted) {
      /**
       * Fires on the owning Scheduler after an event is successfully saved
       * @event afterEventSave
       * @param {Scheduler.view.Scheduler} source The scheduler instance
       * @param {Scheduler.model.EventModel} eventRecord The record about to be saved
       */
      scheduler.trigger('afterEventSave', {
        eventRecord
      });
      me.onAfterSave(eventRecord);
    }

    resolve(aborted ? false : eventRecord);
  }
  /**
   * Saves the changes (applies them to record if valid, if invalid editor stays open)
   * @private
   * @fires beforeEventSave
   * @fires beforeEventAdd
   * @fires afterEventSave
   * @returns {Promise}
   * @async
   */

  save() {
    return new Promise((resolve, reject) => {
      var _me$resourceField;

      const me = this,
            {
        scheduler,
        eventRecord
      } = me;

      if (!eventRecord || !me.isValid) {
        resolve(false);
        return;
      }

      const {
        eventStore,
        values
      } = me,
            resourceRecords = ((_me$resourceField = me.resourceField) === null || _me$resourceField === void 0 ? void 0 : _me$resourceField.records) || (me.resourceRecord ? [me.resourceRecord] : []),
            resourceRecord = resourceRecords[0]; // Check for potential overlap scenarios before saving. TODO needs to be indicated in the UI

      if (!me.scheduler.allowOverlap && eventStore) {
        const abort = resourceRecords.some(resource => {
          return !eventStore.isDateRangeAvailable(values.startDate, values.endDate, eventRecord, resource);
        });

        if (abort) {
          resolve(false);
          return;
        }
      }

      const context = {
        finalize(saveEvent) {
          try {
            if (saveEvent !== false) {
              me.finalizeEventSave(eventRecord, resourceRecords, resolve, reject);
            } else {
              resolve(false);
            }
          } catch (e) {
            reject(e);
          }
        }

      };
      /**
       * Fires on the owning Scheduler before an event is saved
       * @event beforeEventSave
       * @param {Scheduler.view.Scheduler} source The scheduler instance
       * @param {Scheduler.model.EventModel} eventRecord The record about to be saved
       * @param {Scheduler.model.ResourceModel} resourceRecord [DEPRECATED IN FAVOR OF `resourceRecords`] The resource to which the event is assigned
       * @param {Scheduler.model.ResourceModel[]} resourceRecords The resources to which the event is assigned
       * @param {Object} values The new values
       * @param {Object} context Extended save context:
       * @param {Boolean} [context.async] Set this to `true` in a listener to indicate that the listener will asynchronously decide to prevent or not the event save.
       * @param {Function} context.finalize Function to call to finalize the save. Used when `async` is `true`. Provide `false` to the function to prevent the save.
       * @preventable
       */

      if (scheduler.trigger('beforeEventSave', {
        eventRecord,
        resourceRecords,
        resourceRecord,
        values,
        context
      }) !== false) {
        context.finalize();
      } // truthy context.async means than a listener will decide to approve saving asynchronously
      else if (!context.async) {
          resolve();
        }
    });
  } //endregion
  //region Delete

  /**
   * Delete event being edited
   * @returns {Promise}
   * @fires beforeEventDelete
   * @private
   * @async
   */

  deleteEvent() {
    return new Promise((resolve, reject) => {
      const me = this,
            {
        eventRecord,
        editor
      } = me;
      me.scheduler.removeRecords([eventRecord], removeRecord => {
        // The reason it does it here is to move focus *before* it gets deleted,
        // and then there's code in the delete to see that it's deleting the focused one,
        // and jump forwards or backwards to move to the next or previous event
        // See 'Should allow key activation' test in tests/view/mixins/EventNavigation.t.js
        if (removeRecord && editor.containsFocus) {
          editor.revertFocus();
        }

        resolve(removeRecord);
      });
    });
  } //endregion
  //region Stores

  onChangeProject({
    project
  }) {
    if (this.resourceField) {
      this.resourceField.store = project.resourceStore;
    }
  }

  set resourceStore(store) {
    this._resourceStore = store;
  }

  get eventStore() {
    return this.scheduler.project.eventStore;
  }

  get resourceStore() {
    return this.scheduler.project.resourceStore;
  }

  get assignmentStore() {
    return this.scheduler.project.assignmentStore;
  } //endregion
  //endregion
  //region Events

  onActivateEditor({
    eventRecord,
    resourceRecord,
    eventElement
  }) {
    this.editEvent(eventRecord, resourceRecord, eventElement);
  }

  onDragCreateEnd({
    newEventRecord,
    resourceRecord,
    proxyElement
  }) {
    const me = this;

    if (!me.disabled) {
      // Call scheduler template method
      me.scheduler.onEventCreated(newEventRecord); // Clone proxy after showing editor so it's not deleted

      const phantomEventElement = proxyElement.cloneNode(true);
      phantomEventElement.removeAttribute('id');
      proxyElement.parentElement.appendChild(phantomEventElement);
      me.editEvent(newEventRecord, resourceRecord, phantomEventElement);
    }
  } // chained from EventNavigation

  onEventEnterKey({
    assignmentRecord,
    eventRecord,
    target
  }) {
    const element = target.closest(this.client.eventInnerSelector);

    if (assignmentRecord) {
      this.editEvent(eventRecord, assignmentRecord.resource, element);
    } else if (eventRecord) {
      this.editEvent(eventRecord, eventRecord.resource, element);
    }
  } // Toggle fields visibility when changing eventType

  onEventTypeChange({
    value
  }) {
    this.toggleEventType(value);
  } //endregion
  //region Context menu

  populateEventMenu({
    eventRecord,
    resourceRecord,
    items
  }) {
    if (!this.scheduler.readOnly) {
      items.editEvent = {
        text: 'L{EventEdit.Edit event}',
        localeClass: this,
        icon: 'b-icon b-icon-edit',
        weight: 100,
        onItem: () => {
          this.editEvent(eventRecord, resourceRecord);
        }
      };
    }
  } //endregion

}
EventEdit._$name = 'EventEdit';
GridFeatureManager.registerFeature(EventEdit, true, 'Scheduler');
GridFeatureManager.registerFeature(EventEdit, false, 'SchedulerPro');
EventEdit.initClass();

/**
 * @module Scheduler/feature/EventFilter
 */

/**
 * Adds event filter menu items to the timeline header context menu.
 *
 * This feature is **enabled** by default
 *
 * @extends Core/mixin/InstancePlugin
 *
 * @example
 * let scheduler = new Scheduler({
 *   features : {
 *     eventFilter : true // `true` by default, set to `false` to disable the feature and remove the menu item from the timeline header
 *   }
 * });
 *
 * @classtype eventFilter
 * @externalexample scheduler/EventFilter.js
 */

class EventFilter extends InstancePlugin {
  // Plugin configuration. This plugin chains some of the functions in Grid.
  static get $name() {
    return 'EventFilter';
  }

  static get pluginConfig() {
    return {
      chain: ['populateHeaderMenu', // TODO: 'headerContextMenu' is deprecated. Please see https://bryntum.com/docs/scheduler/#guides/upgrades/4.0.0.md for more information.
      'populateTimeAxisHeaderMenu']
    };
  }

  construct(scheduler, config) {
    super.construct(scheduler, config);
    this.scheduler = scheduler;
  } // TODO: 'headerContextMenu' is deprecated. Please see https://bryntum.com/docs/scheduler/#guides/upgrades/4.0.0.md for more information.

  /**
   * Populates the header context menu items.
   * @param {Object} options Contains menu items and extra data retrieved from the menu target.
   * @param {Grid.column.Column} options.column Column for which the menu will be shown
   * @param {Object} options.items A named object to describe menu items
   * @internal
   * @deprecated 4.0.0 Use `populateTimeAxisHeaderMenu` instead
   */

  populateHeaderMenu({
    column,
    items
  }) {
    if (column.type !== 'timeAxis') {
      return;
    }

    this.populateTimeAxisHeaderMenu(...arguments);
  }
  /**
   * Populates the header context menu items.
   * @param {Object} options Contains menu items and extra data retrieved from the menu target.
   * @param {Grid.column.Column} options.column Column for which the menu will be shown
   * @param {Object} options.items A named object to describe menu items
   * @internal
   */

  populateTimeAxisHeaderMenu({
    column,
    items
  }) {
    const me = this;
    items.eventsFilter = {
      text: 'L{filterEvents}',
      icon: 'b-fw-icon b-icon-filter',
      disabled: me.disabled,
      localeClass: this,
      weight: 100,
      menu: {
        type: 'popup',
        localeClass: this,
        items: {
          nameFilter: {
            weight: 110,
            type: 'textfield',
            cls: 'b-eventfilter b-last-row',
            clearable: true,
            keyStrokeChangeDelay: 300,
            label: 'L{byName}',
            localeClass: this,
            width: 200,
            listeners: {
              change: me.onEventFilterChange,
              thisObj: me
            }
          }
        },

        onBeforeShow({
          source: menu
        }) {
          const [filterByName] = menu.items,
                filter = me.scheduler.eventStore.filters.getBy('property', 'name');
          filterByName.value = filter && filter.value || '';
        }

      }
    };
  }

  onEventFilterChange({
    value
  }) {
    const me = this;

    if (value !== '') {
      me.scheduler.eventStore.filter('name', value);
    } else {
      me.scheduler.eventStore.removeFilter('name');
    }
  }

}
EventFilter.featureClass = 'b-event-filter';
EventFilter._$name = 'EventFilter';
GridFeatureManager.registerFeature(EventFilter, true, ['Scheduler', 'Gantt']);

//TODO: Prevent right click while resizing (do it in ResizeHelper?)
/**
 * @module Scheduler/feature/base/ResizeBase
 */

const tipAlign = {
  top: 'b-t',
  right: 'b100-t100',
  bottom: 't-b',
  left: 'b0-t0'
};
/**
 * Base class for EventResize (Scheduler) and TaskResize (Gantt) features. Contains shared code. Not to be used directly.
 *
 * @extends Core/mixin/InstancePlugin
 * @abstract
 */

class ResizeBase extends InstancePlugin {
  //region Config
  static get defaultConfig() {
    return {
      /**
       * `false` to not show a tooltip while resizing
       * @config {Boolean}
       * @default
       */
      showTooltip: true,

      /**
       * true to see exact event length during resizing
       * @config {Boolean}
       * @default
       */
      showExactResizePosition: false,

      /**
       * An empty function by default, but provided so that you can perform custom validation on
       * the item being resized. Return true if the new duration is valid, false to signal that it is not.
       * @param {Object} context The resize context, contains the record & dates.
       * @param {Scheduler.model.TimeSpan} context.record The record being resized.
       * @param {Date} context.startDate The new start date.
       * @param {Date} context.endDate The new start date.
       * @param {Event} event The browser Event object
       * @return {Boolean}
       * @config {Function}
       */
      validatorFn: () => {},

      /**
       * `this` reference for the validatorFn
       * @config {Object}
       */
      validatorFnThisObj: null,

      /**
       * The tooltip instance to show while resizing an event or a configuration object for the {@link Core.widget.Tooltip}.
       * @config {Core.widget.Tooltip|Object}
       */
      tip: null,
      tipTemplate: data => `
                <div class="b-sch-tip-${data.valid ? 'valid' : 'invalid'}">
                    ${data.startClockHtml}
                    ${data.endClockHtml}
                    <div class="b-sch-tip-message">${data.message}</div>
                </div>
            `
    };
  }

  static get pluginConfig() {
    return {
      chain: ['render']
    };
  } //endregion
  //region Init & destroy

  render() {
    const me = this;
    me.resize && me.resize.destroy();
    me.resize = me.createResizeHelper();

    if (me.showTooltip) {
      me.clockTemplate = new ClockTemplate({
        scheduler: me.client
      });
    }
  }

  doDestroy() {
    const me = this;
    me.tip && me.tip.destroy();
    me.clockTemplate && me.clockTemplate.destroy();
    me.resize && me.resize.destroy();
    super.doDestroy();
  } //endregion
  //region Events

  isElementResizable(element, event) {
    const {
      client,
      resize
    } = this,
          timespanRecord = client.resolveTimeSpanRecord(element);

    if (client.readOnly) {
      return false;
    }

    let resizable = timespanRecord && timespanRecord.isResizable; // go up from "handle" to resizable element

    element = DomHelper.up(event.target, client.eventSelector); // Not resizable if the mousedown is on a resizing handle of
    // a percent bar.

    const handleHoldingElement = element ? element.firstElementChild : element,
          handleEl = event.target.closest('[class$="-handle"]');

    if (!resizable || handleEl && handleEl !== handleHoldingElement) {
      return false;
    }

    const startsOutside = element.classList.contains('b-sch-event-startsoutside'),
          endsOutside = element.classList.contains('b-sch-event-endsoutside');

    if (resizable === true) {
      if (startsOutside && endsOutside) {
        return false;
      } else if (startsOutside) {
        resizable = 'end';
      } else if (endsOutside) {
        resizable = 'start';
      } else {
        return resize.overStartHandle(event, element) || resize.overEndHandle(event, element);
      }
    }

    if (startsOutside && resizable === 'start' || endsOutside && resizable === 'end') {
      return false;
    }

    if (resize.overStartHandle(event, element) && resizable === 'start' || resize.overEndHandle(event, element) && resizable === 'end') {
      return true;
    }

    return false;
  }

  onBeforeResizeStart({
    element,
    event
  }) {
    const {
      client
    } = this,
          name = client.scheduledEventName,
          timespanRecord = client.resolveTimeSpanRecord(element);

    if (this.disabled) {
      return false;
    } // trigger beforeEventResize or beforeTaskResize depending on product

    return client.trigger(`before${client.capitalizedEventName}Resize`, {
      [name + 'Record']: timespanRecord,
      event
    }) !== false;
  }

  onResizeStart({
    context,
    event
  }) {
    const me = this,
          client = me.client,
          timespanRecord = client.resolveTimeSpanRecord(context.element),
          name = client.scheduledEventName;
    client.element.classList.add('b-resizing-event'); // Let products to their specific stuff

    me.setupProductResizeContext(context, event);

    if (me.showTooltip) {
      if (me.tip instanceof Tooltip) {
        me.tip.align = tipAlign[context.edge];
        me.tip.updateContentOnMouseMove = true; // Tip needs to be shown first for getTooltipTarget to be able to measure anchor size

        me.tip.show();
        me.tip.showBy(me.getTooltipTarget());
      } else {
        me.tip = new Tooltip(ObjectHelper.assign({
          id: `${client.id}-event-resize-tip`,
          autoShow: true,
          axisLock: true,
          trackMouse: false,
          updateContentOnMouseMove: true,
          getHtml: me.getTipHtml.bind(me),
          align: tipAlign[context.edge],
          hideDelay: 0
        }, me.tip));
        me.tip.on('innerhtmlupdate', me.updateDateIndicator, me);
      }
    } // flag to not allow release of element when scrolling

    timespanRecord.instanceMeta(client).retainElement = true; // Trigger eventResizeStart or taskResizeStart depending on product

    client.trigger(`${name}ResizeStart`, {
      [`${name}Record`]: timespanRecord,
      event
    });
  }

  updateDateIndicator() {
    const {
      edge,
      startDate,
      endDate
    } = this.resize.context,
          {
      element
    } = this.tip;

    if (startDate || endDate) {
      if (edge === 'right' || edge === 'bottom') {
        this.clockTemplate.updateDateIndicator(element.querySelector('.b-sch-tooltip-enddate'), endDate);
      } else {
        this.clockTemplate.updateDateIndicator(element, startDate);
      }
    }
  }

  getTooltipTarget() {
    const me = this,
          target = Rectangle.from(me.resize.context.element, null, true);

    if (me.resize.direction === 'horizontal') {
      // Align to the dragged edge of the proxy, and then bump right so that the anchor aligns perfectly.
      if (me.resize.context.edge === 'right') {
        target.x = target.right - 1;
      }

      target.width = me.tip.anchorSize[0] / 2;
    } else {
      // Align to the dragged edge of the proxy, and then bump bottom so that the anchor aligns perfectly.
      if (me.resize.context.edge === 'bottom') {
        target.y = target.bottom - 1;
      }

      target.height = me.tip.anchorSize[1] / 2;
    }

    return {
      target
    };
  }

  onResizing({
    context,
    event
  }) {
    const me = this,
          {
      client
    } = me,
          depFeature = client.features.dependencies,
          timespanRecord = me.getTimespanRecord(context),
          name = client.scheduledEventName,
          {
      element,
      edge
    } = context,
          xy = DomHelper.getTranslateXY(element);
    let start, end;

    if (edge === 'top' || edge === 'left') {
      end = timespanRecord.endDate;

      if (client.snapRelativeToEventStartDate) {
        start = client.getDateFromXY(xy, null, true);
        start = client.timeAxis.roundDate(start, timespanRecord.startDate);
      } else {
        start = client.getDateFromXY(xy, 'round', true);
      }
    } // bottom || right
    else {
        xy[0] += element.offsetWidth;
        xy[1] += element.offsetHeight;
        start = timespanRecord && timespanRecord.startDate;

        if (client.snapRelativeToEventStartDate) {
          end = client.getDateFromXY(xy, null, true);
          end = client.timeAxis.roundDate(end, timespanRecord.endDate);
        } else {
          end = client.getDateFromXY(xy, 'round', true);
        }
      }

    start = start || context.startDate;
    end = end || context.endDate;

    if (context.dateConstraints) {
      start = DateHelper.constrain(start, context.dateConstraints.start, context.dateConstraints.end);
      end = DateHelper.constrain(end, context.dateConstraints.start, context.dateConstraints.end);
    }

    if (me.showExactResizePosition || client.timeAxisViewModel.snap) {
      const exactSize = edge === 'top' || edge === 'left' ? client.timeAxisViewModel.getDistanceBetweenDates(start, timespanRecord.endDate) : client.timeAxisViewModel.getDistanceBetweenDates(timespanRecord.startDate, end);

      switch (edge) {
        case 'top':
          DomHelper.setTranslateY(element, context.elementStartY + context.elementWidth - exactSize);
          element.style.height = exactSize + 'px';
          break;

        case 'right':
          element.style.width = exactSize + 'px';
          break;

        case 'bottom':
          element.style.height = exactSize + 'px';
          break;

        case 'left':
          DomHelper.setTranslateX(element, context.elementStartX + context.elementWidth - exactSize);
          element.style.width = exactSize + 'px';
          break;
      }
    }

    const dateChanged = context.endDate - end !== 0 || context.startDate - start !== 0;
    context.endDate = end;
    context.startDate = start; // No need to query on every pixel of mouse move

    if (dateChanged) {
      context.valid = me.checkValidity(context, event);
    } // Trigger eventPartialResize or taskPartialResize depending on product

    client.trigger(`${name}PartialResize`, {
      [`${name}Record`]: timespanRecord,
      startDate: start,
      endDate: end,
      element,
      context
    });

    if (depFeature) {
      depFeature.updateDependenciesForTimeSpan(timespanRecord, element);
    }

    if (me.showTooltip) {
      me.tip.alignTo(me.getTooltipTarget());
    }
  }

  checkValidity(context, event) {
    let valid = context.startDate && context.endDate > context.startDate && this.validatorFn.call(this.validatorFnThisObj || this, context, event);

    if (valid && typeof valid !== 'boolean') {
      context.message = valid.message || '';
      valid = valid.valid;
    }

    return valid !== false;
  }

  onFinishResize({
    source: drag,
    context,
    event
  }) {
    const me = this,
          timespanRecord = me.getTimespanRecord(context),
          oldStart = timespanRecord.startDate,
          oldEnd = timespanRecord.endDate,
          start = context.startDate || oldStart,
          end = context.endDate || oldEnd,
          client = me.client;
    let modified = false;

    if (me.tip) {
      me.tip.hide();
    } // allow release of element again

    timespanRecord.instanceMeta(client).retainElement = false;
    context.oldFinalize = context.finalize;

    context.finalize = (...params) => {
      // We are overriding context of the resize helper. It is finalized automatically on sync resize. Which means,
      // we should only call finalize if context is async.
      context.async && me.finalize(...params);
    };

    context.valid = start && end && end - start > 0 && ( // Input sanity check
    start - oldStart !== 0 || end - oldEnd !== 0) && // Make sure start OR end changed
    context.valid !== false;

    if (context.valid) {
      // Seems to be a valid resize operation, ask outside world if anyone wants to take control over the finalizing,
      // to show a confirm dialog prior to applying the new values. Triggers beforeEventResizeFinalize or
      // beforeTaskResizeFinalize depending on product
      client.trigger(`before${client.scheduledEventName}ResizeFinalize`, {
        context,
        event
      });
      modified = true;
    }

    if (!context.async) {
      me.finalize(modified);
    }
  }

  onCancelResize({
    context
  }) {
    const timespanRecord = this.getTimespanRecord(context); // resizing may not have started at all (just clicking a resize handle)

    if (timespanRecord) {
      timespanRecord.instanceMeta(this.client).retainElement = false;
    }

    this.finalize(false);
  }

  async finalize(updateRecord) {
    const me = this,
          {
      client
    } = me,
          context = me.resize.context,
          timespanRecord = me.getTimespanRecord(context),
          name = client.scheduledEventName;
    let wasChanged = false;

    if (me.tip) {
      me.tip.hide();
    }

    if (context.started) {
      if (updateRecord) {
        // Scheduler and gantt updates the record differently
        wasChanged = await me.internalUpdateRecord(context, timespanRecord);
      }

      if (!updateRecord || !wasChanged) {
        const dependencies = client.features.dependencies;
        me.resize.abortResize(null, true); // Dependencies are updated dynamically during resize, so ensure they are redrawn
        // if the event snaps back with no change.

        if (dependencies) {
          dependencies.scheduleDraw(true);
        }
      } else {
        context.oldFinalize();
      }
    }

    client.element.classList.remove('b-resizing-event'); // Triggers eventResizeEnd or taskResizeEnd depending on product

    client.trigger(`${name}ResizeEnd`, {
      changed: wasChanged,
      [`${name}Record`]: timespanRecord || client.resolveEventRecord(context.element)
    });
  } //endregion
  //region Tooltip

  getTipHtml({
    tip
  }) {
    const me = this;
    let {
      startDate,
      endDate,
      valid,
      message
    } = me.resize.context; // Empty string hides the tip - we get called before the Resizer, so first call will be empty

    if (!startDate || !endDate) {
      return tip.html;
    }

    if (message === undefined) message = '';
    endDate = me.client.getDisplayEndDate(endDate, startDate);
    const startText = me.client.getFormattedDate(startDate),
          endText = me.client.getFormattedDate(endDate);
    return me.tipTemplate({
      valid,
      startDate,
      endDate,
      startText,
      endText,
      message,
      startClockHtml: me.clockTemplate.template({
        date: startDate,
        text: startText,
        cls: 'b-sch-tooltip-startdate'
      }),
      endClockHtml: me.clockTemplate.template({
        date: endDate,
        text: endText,
        cls: 'b-sch-tooltip-enddate'
      })
    });
  } //endregion
  //region Product specific, implemented in subclasses

  getRowRecord(context) {
    throw new Error('Implement in subclass');
  }

  getTimespanRecord(context) {
    throw new Error('Implement in subclass');
  }

  setupProductResizeContext(context, event) {
    throw new Error('Implement in subclass');
  } // Store containing the timespan record being resized

  get store() {
    throw new Error('Implement in subclass');
  } //endregion

}
ResizeBase._$name = 'ResizeBase';

//TODO: Prevent right click while resizing (do it in ResizeHelper?)
/**
 * @module Scheduler/feature/EventResize
 */

/**
 * Feature that allows resizing an event by dragging its end.
 *
 * By default it displays a tooltip with the new start and end dates, formatted using
 * {@link Scheduler/view/mixin/TimelineViewPresets#config-displayDateFormat}.
 *
 * This feature is **enabled** by default
 *
 * @extends Scheduler/feature/base/ResizeBase
 * @demo Scheduler/basic
 * @externalexample scheduler/EventResize.js
 * @classtype eventResize
 */

class EventResize extends ResizeBase {
  //region Events

  /**
   * Fired on the owning Scheduler before resizing starts. Return false to prevent operation
   * @event beforeEventResize
   * @param {Scheduler.model.EventModel} eventRecord
   * @param {Event} event
   */

  /**
   * Fires on the owning Scheduler when event resizing starts
   * @event eventResizeStart
   * @param {Scheduler.model.EventModel} eventRecord
   * @param {Event} event
   */

  /**
   * Fires on the owning Scheduler on each resize move event
   * @event eventPartialResize
   * @param {Scheduler.model.EventModel} eventRecord
   * @param {Date} startDate
   * @param {Date} endDate
   * @param {HTMLElement} element
   */

  /**
   * Fired on the owning Scheduler to allow implementer to prevent immediate finalization by setting `data.context.async = true`
   * in the listener, to show a confirmation popup etc
   * ```
   *  scheduler.on('beforeeventresizefinalize', ({context}) => {
   *      context.async = true;
   *      setTimeout(() => {
   *          // async code don't forget to call finalize
   *          context.finalize();
   *      }, 1000);
   *  })
   * ```
   * @event beforeEventResizeFinalize
   * @param {Object} data
   * @param {Scheduler.view.Scheduler} data.source Scheduler instance
   * @param {Object} data.context
   * @param {Boolean} data.context.async Set true to handle resize asynchronously (e.g. to wait for user
   * confirmation)
   * @param {Function} data.context.finalize Call this method to finalize resize. This method accepts one
   * argument: pass true to update records, or false, to ignore changes
   */

  /**
   * Fires on the owning Scheduler after the resizing gesture has finished.
   * @event eventResizeEnd
   * @param {Boolean} wasChanged
   * @param {Scheduler.model.EventModel} eventRecord
   */
  //endregion
  //region Scheduler specifics
  static get $name() {
    return 'EventResize';
  }

  createResizeHelper() {
    const me = this,
          client = me.client;
    return new ResizeHelper({
      direction: client.isVertical ? 'vertical' : 'horizontal',
      name: me.constructor.$$name,
      // for debugging
      isElementResizable: (el, event) => me.isElementResizable(el, event),
      targetSelector: client.eventSelector,
      handleContainerSelector: client.eventInnerSelector,
      resizingCls: 'b-sch-event-wrap-resizing',
      allowResize: me.isElementResizable.bind(me),
      outerElement: client.timeAxisSubGridElement,
      // constrain resize to view
      scrollManager: client.scrollManager,
      dragThreshold: 0,
      dynamicHandleSize: true,
      reservedSpace: 5,
      scroller: client.timeAxisSubGrid.scrollable,
      // allow reading correct x & y scroll
      listeners: {
        beforeresizestart: me.onBeforeResizeStart,
        resizestart: me.onResizeStart,
        resizing: me.onResizing,
        resize: me.onFinishResize,
        cancel: me.onCancelResize,
        thisObj: me
      }
    });
  } // Store used by ResizeBase to detect updates on dropped record

  get store() {
    return this.client.eventStore;
  } // Used by ResizeBase to get an eventRecord from the drag context

  getTimespanRecord(context) {
    return context.eventRecord;
  }

  getRowRecord(context) {
    return context.resourceRecord;
  } // Injects Scheduler specific data into the drag context

  setupProductResizeContext(context, event) {
    const scheduler = this.client,
          eventRecord = scheduler.resolveEventRecord(context.element),
          resourceRecord = scheduler.resolveResourceRecord(context.element);
    Object.assign(context, {
      eventRecord,
      resourceRecord,
      dateConstraints: scheduler.getDateConstraints(resourceRecord, eventRecord)
    });
  }

  onResizeStart({
    context,
    event
  }) {
    super.onResizeStart({
      context,
      event
    }); // Hide terminals when resizing starts

    if (this.client.features.dependencies) {
      this.client.features.dependencies.hideTerminals(context.element);
    } // Add resizing cls to inner element, as expected by styling

    context.element.querySelector('.b-sch-event').classList.add('b-sch-event-resizing');
  }

  checkValidity(context, event) {
    return (this.client.allowOverlap || this.client.isDateRangeAvailable(context.startDate, context.endDate, context.eventRecord, context.resourceRecord)) && super.checkValidity(context, event);
  }

  onFinishResize({
    source,
    context,
    event
  }) {
    super.onFinishResize({
      source,
      context,
      event
    }); // When resizing is done successfully, mouse should be over element, so we show terminals

    if (this.client.features.dependencies) {
      this.client.features.dependencies.showTerminals(context.eventRecord, context.element);
    }
  }

  async internalUpdateRecord(context, timespanRecord) {
    const {
      generation
    } = timespanRecord; // Special handling of occurrences, they need normalization since that is not handled by engine at the moment

    if (timespanRecord.isOccurrence) {
      timespanRecord.set(TimeSpan.prototype.inSetNormalize.call(timespanRecord, {
        startDate: context.startDate,
        endDate: context.endDate
      }));
    } else if (context.edge === 'left' || context.edge === 'top') {
      await timespanRecord.setStartDate(context.startDate, false);
    } else {
      await timespanRecord.setEndDate(context.endDate, false);
    } // The record has been changed

    if (timespanRecord.generation !== generation) {
      return true;
    } // The record has not been changed

    return false;
  }

  finalize(updateRecord) {
    this.resize.context.element.querySelector('.b-sch-event').classList.remove('b-sch-event-resizing');
    super.finalize(updateRecord);
  } //endregion

}
EventResize._$name = 'EventResize';
GridFeatureManager.registerFeature(EventResize, true, 'Scheduler');

/**
 * @module Scheduler/feature/base/TooltipBase
 */

/**
 * Base class for EventTooltip (Scheduler) and TaskTooltip (Gantt) features. Contains shared code. Not to be used directly.
 *
 * @extends Core/mixin/InstancePlugin
 */

class TooltipBase extends InstancePlugin {
  //region Config
  static get defaultConfig() {
    return {
      /**
       * Specify true to have tooltip updated when mouse moves, if you for example want to display date at mouse
       * position.
       * @config {Boolean}
       * @default
       * @category Misc
       */
      autoUpdate: false,
      template: null,
      cls: null,
      align: {
        align: 'b-t'
      },
      clockTemplate: null,
      // Set to true to update tooltip contents if record changes while tip is open
      monitorRecordUpdate: null
    };
  } // Plugin configuration. This plugin chains some of the functions in Grid.

  static get pluginConfig() {
    return {
      chain: ['render']
    };
  } //endregion
  //region Init

  construct(client, config) {
    const me = this; // process initial config into an actual config object

    config = me.processConfig(config);
    super.construct(client, config); // Default triggering selector is the client's inner element selector

    if (!me.forSelector) {
      me.forSelector = `${client.eventInnerSelector}:not(.b-dragproxy)`;
    }

    me.clockTemplate = new ClockTemplate({
      scheduler: client
    });
    client.on({
      [`before${client.scheduledEventName}drag`]: () => {
        var _me$tooltip;

        return (_me$tooltip = me.tooltip) === null || _me$tooltip === void 0 ? void 0 : _me$tooltip.hide();
      }
    });
  } // TooltipBase feature handles special config cases, where user can supply a function to use as template
  // instead of a normal config object

  processConfig(config) {
    if (typeof config === 'function') {
      return {
        template: config
      };
    }

    return config;
  } // override setConfig to process config before applying it (used mainly from ReactScheduler)

  setConfig(config) {
    super.setConfig(this.processConfig(config));
  }

  doDestroy() {
    var _this$clockTemplate, _this$tooltip;

    (_this$clockTemplate = this.clockTemplate) === null || _this$clockTemplate === void 0 ? void 0 : _this$clockTemplate.destroy();
    (_this$tooltip = this.tooltip) === null || _this$tooltip === void 0 ? void 0 : _this$tooltip.destroy();
    super.doDestroy();
  }

  doDisable(disable) {
    if (this.tooltip) {
      this.tooltip.disabled = disable;
    }

    super.doDisable(disable);
  } //endregion

  render() {
    const me = this,
          client = me.client,
          ignoreSelector = ['.b-dragselecting', '.b-eventeditor-editing', '.b-taskeditor-editing', '.b-resizing-event', '.b-dragcreating', `.b-dragging-${client.scheduledEventName}`, '.b-creating-dependency', '.b-dragproxy'].map(cls => `:not(${cls})`).join('');

    if (me.tooltip) {
      me.tooltip.destroy();
    }
    /**
     * A reference to the tooltip instance, which will have a special `eventRecord` property that
     * you can use to get data from the contextual event record to which this tooltip is related.
     * @member {Core.widget.Tooltip} tooltip
     * @readonly
     * @category Misc
     */

    me.tooltip = new Tooltip(Object.assign({
      axisLock: 'flexible',
      id: me.tipId || `${me.client.id}-event-tip`,
      cls: me.tipCls,
      forSelector: `.b-timelinebase${ignoreSelector} .b-grid-body-container:not(.b-scrolling) ${me.forSelector}`,
      scrollAction: 'realign',
      clippedBy: [client.timeAxisSubGridElement, client.bodyContainer],
      forElement: client.timeAxisSubGridElement,
      showOnHover: true,
      hoverDelay: 0,
      hideDelay: 100,
      anchorToTarget: true,
      allowOver: Boolean(me.config.items || me.config.tools),
      getHtml: me.getTipHtml.bind(me),
      disabled: me.disabled
    }, me.config));
    me.tooltip.on({
      innerhtmlupdate: 'updateDateIndicator',
      overtarget: 'onOverNewTarget',
      show: 'onTipShow',
      hide: 'onTipHide',
      thisObj: me
    });
  }

  updateDateIndicator() {
    const me = this,
          tip = me.tooltip,
          endDateElement = tip.element.querySelector('.b-sch-tooltip-enddate');

    if (!me.record) {
      return;
    }

    me.clockTemplate.updateDateIndicator(tip.element, me.record.startDate);
    endDateElement && me.clockTemplate.updateDateIndicator(endDateElement, me.record.endDate);
  }

  resolveTimeSpanRecord(forElement) {
    return this.client.resolveTimeSpanRecord(forElement);
  }

  getTipHtml({
    tip,
    activeTarget
  }) {
    const me = this,
          {
      client
    } = me,
          recordProp = me.recordType || `${client.scheduledEventName}Record`,
          timeSpanRecord = me.resolveTimeSpanRecord(activeTarget); // If user has mouseovered a fading away element of a deleted event,
    // an event record will not be found. In this case the tip must hide.
    // Instance of check is to not display while propagating

    if ((timeSpanRecord === null || timeSpanRecord === void 0 ? void 0 : timeSpanRecord.startDate) instanceof Date) {
      const {
        startDate,
        endDate
      } = timeSpanRecord,
            startText = client.getFormattedDate(startDate),
            endDateValue = client.getDisplayEndDate(endDate, startDate),
            endText = client.getFormattedDate(endDateValue);
      tip.eventRecord = timeSpanRecord;
      return me.template({
        tip,
        // eventRecord for Scheduler, taskRecord for Gantt
        [`${recordProp}`]: timeSpanRecord,
        startDate,
        endDate,
        startText,
        endText,
        startClockHtml: me.clockTemplate.template({
          date: startDate,
          text: startText,
          cls: 'b-sch-tooltip-startdate'
        }),
        endClockHtml: timeSpanRecord.isMilestone ? '' : me.clockTemplate.template({
          date: endDateValue,
          text: endText,
          cls: 'b-sch-tooltip-enddate'
        })
      });
    } else {
      tip.hide();
      return '';
    }
  }

  get record() {
    const me = this;

    if (!me._record) {
      me._record = me.tooltip.activeTarget && me.resolveTimeSpanRecord(me.tooltip.activeTarget);
    }

    return me._record;
  }

  onTipShow({
    source: tooltip
  }) {
    const me = this;

    if (me.monitorRecordUpdate && !me.updateListener) {
      me.updateListener = me.client.eventStore.on({
        update: me.onRecordUpdate,
        thisObj: me
      });
    }
  }

  onTipHide() {
    var _this$updateListener;

    this._record = null;
    (_this$updateListener = this.updateListener) === null || _this$updateListener === void 0 ? void 0 : _this$updateListener.call(this);
    this.updateListener = null;
  }

  onOverNewTarget({
    newTarget
  }) {
    this._record = null;
  }

  onRecordUpdate({
    record
  }) {
    const me = this; // make sure the record we are showing the tip for is still relevant

    if (record === me.record) {
      // Stop aligning at this point
      me.tooltip.alignTo();
      me.tooltip.updateContent();
    }
  }

}
TooltipBase._$name = 'TooltipBase';

/**
 * @module Scheduler/feature/EventTooltip
 */

/**
 * Displays a tooltip when hovering events. The template used to render the tooltip can be customized, see {@link #config-template}.
 * Config options are also applied to the tooltip shown, see {@link Core.widget.Tooltip} for available options.
 *
 * ## Showing local data
 * To show a basic "local" tooltip (with data available in the Event record) upon hover:
 * ```javascript
 * new Scheduler({
 *   features : {
 *     eventTooltip : {
 *         // Tooltip configs can be used here
 *         align : 'l-r' // Align left to right,
 *         // A custom HTML template
 *         template : data => `<dl>
 *           <dt>Assigned to:</dt>
 *              <dt>Time:</dt>
 *              <dd>
 *                  ${DateHelper.format(data.eventRecord.startDate, 'LT')} - ${DateHelper.format(data.eventRecord.endDate, 'LT')}
 *              </dd>
 *              ${data.eventRecord.get('note') ? `<dt>Note:</dt><dd>${data.eventRecord.note}</dd>` : ''}
 *
 *              ${data.eventRecord.get('image') ? `<dt>Image:</dt><dd><img class="image" src="${data.eventRecord.get('image')}"/></dd>` : ''}
 *          </dl>
 *     }
 *   }
 * });
 * ```
 *
 * ## Showing remotely loaded data
 * Loading remote data into the event tooltip is easy. Simply use the {@link #config-template} and return a Promise which yields the content to show.
 * ```javascript
 * new Scheduler({
 *   features : {
 *     eventTooltip : {
 *        template : ({ eventRecord }) => AjaxHelper.get(`./fakeServer?name=${eventRecord.name}`).then(response => response.text())
 *     }
 *   }
 * });
 * ```
 * This feature is **enabled** by default
 *
 * By default, the tooltip {@link Core.widget.Widget#config-scrollAction realigns on scroll}
 * meaning that it will stay aligned with its target should a scroll interaction make the target move.
 *
 * If this is causing performance issues in a Scheduler, such as if there are many dozens of events
 * visible, you can configure this feature with `scrollAction: 'hide'`. This feature's configuration is
 * applied to the tooltip, so that will mean that the tooltip will hide if its target is moved by a
 * scroll interaction.
 *
 * @extends Scheduler/feature/base/TooltipBase
 * @demo Scheduler/basic
 * @externalexample scheduler/EventTooltip.js
 * @classtype eventTooltip
 */

class EventTooltip extends TooltipBase {
  //region Config
  static get $name() {
    return 'EventTooltip';
  }

  static get defaultConfig() {
    return {
      /**
       * A function which receives data about the event and returns a string,
       * or a Promise yielding a string (for async tooltips), to be displayed in the tooltip.
       * This method will be called with an object containing the fields below
       * @param {Object} data
       * @param {Scheduler.model.EventModel} data.eventRecord
       * @param {Date} data.startDate
       * @param {Date} data.endDate
       * @param {String} data.startText
       * @param {String} data.endText
       * @config {Function} template
       */
      template: data => `
                ${data.eventRecord.name ? StringHelper.xss`<div class="b-sch-event-title">${data.eventRecord.name}</div>` : ''}
                ${data.startClockHtml}
                ${data.endClockHtml}`,
      cls: 'b-sch-event-tooltip',
      monitorRecordUpdate: true
    };
  } //endregion

  construct(client, config) {
    const me = this;
    super.construct(client, config);

    if (typeof me.align === 'string') {
      me.align = {
        align: me.align
      };
    }
  }

  render() {
    const me = this,
          dependencies = me.client.features.dependencies;
    super.render();

    if (dependencies) {
      const configuredOffset = me.align.offset;
      me.tooltip.on('beforeshow', ({
        source: tooltip
      }) => {
        // Don't let dependency terminal interfere with this tooltip
        if (!dependencies.disabled) {
          tooltip.align.offset = [0, 10];
        } else {
          tooltip.align.offset = configuredOffset;
        }
      });
    }
  }

}
EventTooltip._$name = 'EventTooltip';
GridFeatureManager.registerFeature(EventTooltip, true, 'Scheduler');

/**
 * @module Grid/feature/mixin/SummaryFormatter
 */

/**
 * Mixin for Summary and GroupSummary that handles formatting sums.
 * @mixin
 * @private
 */

var SummaryFormatter = (Target => class SummaryFormatter extends (Target || Base) {
  static get $name() {
    return 'SummaryFormatter';
  }
  /**
   * Calculates sums and returns as a html table
   * @param {Grid.column.Column} column Column to calculate sum for
   * @param {Core.data.Model[]} records Records to include in calculation
   * @param {String} cls CSS class to apply to summary table
   * @param {Core.data.Model} groupRecord current group row record
   * @param {String} groupField Current groups field name
   * @param {String} groupValue Current groups value
   * @returns {String} html content
   */

  generateHtml(column, records, cls, groupRecord, groupField, groupValue) {
    const store = this.store,
          summaries = column.summaries || (column.sum ? [{
      sum: column.sum,
      renderer: column.summaryRenderer
    }] : []);
    let html = `<table class="${cls}">`;
    summaries.forEach(config => {
      let type = config.sum,
          sum = null;
      if (type === true) type = 'sum';

      switch (type) {
        case 'sum':
        case 'add':
          sum = store.sum(column.field, records);
          break;

        case 'max':
          sum = store.max(column.field, records);
          break;

        case 'min':
          sum = store.min(column.field, records);
          break;

        case 'average':
        case 'avg':
          sum = store.average(column.field, records);
          break;

        case 'count':
          sum = records.length;
          break;

        case 'countNotEmpty':
          sum = records.reduce((sum, record) => {
            const value = record[column.field];
            return sum + (value != null ? 1 : 0);
          }, 0);
          break;
      }

      if (typeof type === 'function') {
        sum = records.reduce(type, 'seed' in config ? config.seed : 0);
      }

      if (sum !== null) {
        const valueCls = 'b-grid-summary-value',
              // optional label
        labelHtml = config.label ? `<td class="b-grid-summary-label">${config.label}</td>` : ''; // value to display, either using renderer or as is

        let valueHtml = config.renderer ? config.renderer({
          config,
          sum
        }) : sum,
            summaryHtml;

        if (valueHtml == null) {
          valueHtml = '';
        } // no <td>s in html, wrap it (always the case when not using renderer)

        if (!String(valueHtml).includes('<td>')) {
          summaryHtml = labelHtml // has label, use returned html as value cell
          ? `${labelHtml}<td class="${valueCls}">${valueHtml}</td>` // no label, span entire table
          : `<td colspan="2" class="${valueCls}">${valueHtml}</td>`;
        } // user is in charge of giving correct formatting
        else {
            summaryHtml = valueHtml;
          }

        html += `<tr>${summaryHtml}</tr>`;
      }
    });
    return html + '</table>';
  }

});

/**
 * @module Grid/feature/GroupSummary
 */

/**
 * Displays a summary row as a group footer in a grouped grid. Uses same configuration options on columns as
 * {@link Grid.feature.Summary}.
 *
 * This feature is <strong>disabled</strong> by default.
 *
 * ```
 * features : {
 *     group        : 'city',
 *     groupSummary : true
 * }
 * ```
 *
 * @extends Core/mixin/InstancePlugin
 *
 * @demo Grid/groupsummary
 * @classtype groupSummary
 * @externalexample feature/GroupSummary.js
 */

class GroupSummary extends SummaryFormatter(InstancePlugin) {
  //region Init
  static get $name() {
    return 'GroupSummary';
  }

  static get configurable() {
    return {
      /**
       * Configure as true to have group summaries rendered in the group header when a group is collapsed.
       * @config {Boolean}
       */
      collapseToHeader: null
    };
  }

  construct(grid, config) {
    this.grid = grid;
    super.construct(grid, config);

    if (!grid.features.group) {
      throw new Error('Requires Group feature to work, please enable');
    }

    this.bindStore(grid.store);
    grid.rowManager.on({
      beforeRenderRow: 'onBeforeRenderRow',
      renderCell: 'renderCell',
      // The feature gets to see cells being rendered after the Group feature
      // because the Group feature injects header content into group header rows
      // and adds rendering info to the cells renderData which we must comply with.
      // In particular, it calculates the isFirstColumn flag which it adds to
      // the cell renderData which we interrogate.
      prio: 1000,
      thisObj: this
    });
  }

  bindStore(store) {
    this.detachListeners('store');
    store.on({
      name: 'store',
      update: 'onStoreUpdate',
      // need to run before grids listener, to flag for full refresh
      prio: 1,
      thisObj: this
    });
  }

  get store() {
    return this.grid.store;
  }

  doDisable(disable) {
    // Flag that will make the Store insert rows for group footers
    this.store.useGroupFooters = !disable; // Refresh groups to show/hide footers

    if (!this.isConfiguring) {
      this.store.group();
    }

    super.doDisable(disable);
  } //endregion
  //region Plugin config
  // Plugin configuration. This plugin chains some of the functions in Grid.

  static get pluginConfig() {
    return {
      chain: ['bindStore']
    };
  } //endregion
  //region Render

  /**
   * Called before rendering row contents, used to reset rows no longer used as group summary rows
   * @private
   */

  onBeforeRenderRow({
    row,
    record
  }) {
    if (row.isGroupFooter && !('groupFooterFor' in record.meta)) {
      // not a group row.
      row.isGroupFooter = false; // force full "redraw" when rendering cells

      row.forceInnerHTML = true;
    } else if (row.isGroupHeader && !record.meta.collapsed) {
      // remove any summary elements
      row.eachElement(this.removeSummaryElements);
    }
  }

  removeSummaryElements(rowEl) {
    const summaryElement = rowEl.querySelector('.b-timeaxis-group-summary');
    summaryElement === null || summaryElement === void 0 ? void 0 : summaryElement.remove();
  }
  /**
   * Called when a cell is rendered, styles the group rows first cell.
   * @private
   */

  renderCell({
    column,
    cellElement,
    row,
    record,
    size,
    isFirstColumn
  }) {
    const me = this,
          {
      meta
    } = record,
          isGroupHeader = ('groupRowFor' in meta),
          isGroupFooter = ('groupFooterFor' in meta),
          rowClasses = {
      'b-group-footer': 0,
      'b-header-summary': 0
    }; // Should we generate summary content in this cell?
    // We should if this is a group footer row.
    // We also should if it's a collapsed group header and we are configured to put
    // the summary in the header on collapse, *and* if it's not the first column
    // as defined by the Group feature.

    if (me.store.isGrouped && (isGroupFooter || me.collapseToHeader && isGroupHeader && meta.collapsed && !isFirstColumn)) {
      const groupRecord = isGroupHeader ? record : meta.groupRecord;
      row.isGroupFooter = isGroupFooter;
      row.isGroupHeader = isGroupHeader; // This is a group footer row, add css

      if (isGroupFooter) {
        rowClasses['b-group-footer'] = 1;
      } // This is a header row which is being used during collapsed state
      // to contain summary data because of the collapseToHeader config.
      else {
          rowClasses['b-header-summary'] = 1;
        } // returns height config or count. config format is { height, count }. where `height is in px and should be
      // added to value calculated from `count

      const heightSetting = me.updateSummaryHtml(cellElement, column, groupRecord);
      const count = typeof heightSetting === 'number' ? heightSetting : heightSetting.count; // number of summaries returned, use to calculate cell height

      if (count > 1) {
        size.height = me.grid.rowHeight + count * me.grid.rowHeight * 0.1;
      } // height config with height specified, added to cell height

      if (heightSetting.height) {
        size.height += heightSetting.height;
      }
    } // Sync row's classes with its status as a group header or footer.

    row.assignCls(rowClasses);
  }

  updateSummaryHtml(cellElement, column, groupRecord) {
    const records = groupRecord.groupChildren.slice();
    records.pop(); // last record is group footer, remove

    const html = this.generateHtml(column, records, 'b-grid-group-summary', groupRecord, groupRecord.meta.groupField, groupRecord.meta.groupRowFor); // First time, set table

    if (!cellElement.children.length) {
      cellElement.innerHTML = html;
    } // Following times, sync changes
    else {
        DomHelper.sync(html, cellElement.firstElementChild);
      } // return summary "count", used to set row height

    return column.summaries ? column.summaries.length : column.sum ? 1 : 0;
  } //endregion
  //region Events

  /**
   * Updates summaries on store changes (except record update, handled below)
   * @private
   */

  onStoreUpdate({
    source: store,
    changes
  }) {
    if (!this.disabled && store.isGrouped) {
      // If a grouping field is among the changes, StoreGroup#onDataChanged will
      // take care of the update by re-sorting.
      if (changes && store.groupers.find(grouper => grouper.field in changes)) {
        return;
      } // only update summary when a field that affects summary is changed
      // TODO: this should maybe be removed, another column might depend on the value for its summary?

      const shouldUpdate = Object.keys(changes).some(field => {
        const colField = this.grid.columns.get(field); // check existence, since a field not used in a column might have changed

        return Boolean(colField) && (Boolean(colField.sum) || Boolean(colField.summaries));
      });

      if (shouldUpdate) {
        this.grid.forceFullRefresh = true;
      }
    }
  } //endregion

}
GroupSummary.featureClass = 'b-group-summary';
GroupSummary._$name = 'GroupSummary';
GridFeatureManager.registerFeature(GroupSummary);

/**
 * @module Scheduler/view/Header
 */

/**
 * Custom header subclass which handles the existence of the special TimeAxisColumn
 *
 * @extends Grid/view/Header
 * @private
 */

class Header$1 extends Header {
  static get $name() {
    return 'SchedulerHeader';
  }

  refreshContent() {
    // Only render contents into the header once as it contains the special rendering of the TimeAxisColumn
    if (this.headersElement && this.headersElement.childNodes.length === 0) {
      super.refreshContent();
    }
  }

}
Header$1._$name = 'Header';

/**
 * @module Scheduler/view/model/TimeAxisViewModel
 */

/**
 * This class is an internal view model class, describing the visual representation of a {@link Scheduler.data.TimeAxis}.
 * The config for the header rows is described in the {@link Scheduler.preset.ViewPreset#field-headers headers}.
 * To calculate the size of each cell in the time axis, this class requires:
 *
 * - availableSpace  - The total width or height available for the rendering
 * - tickSize       - The fixed width or height of each cell in the lowest header row. This value is normally read from the
 * {@link Scheduler.preset.ViewPreset viewPreset} but this can also be updated programmatically using the {@link #property-tickSize} setter
 *
 * Normally you should not interact with this class directly.
 *
 * @extends Core/mixin/Events
 */

class TimeAxisViewModel extends Events() {
  //region Default config
  // TODO: replace _availableSpace etc with availableSpace in config and make setters work during config phase
  static get defaultConfig() {
    return {
      /**
       * The time axis providing the underlying data to be visualized
       * @config {Scheduler.data.TimeAxis}
       * @internal
       */
      timeAxis: null,

      /**
       * The available width/height, this is normally not known by the consuming UI component using this model
       * class until it has been fully rendered. The consumer of this model should set
       * {@link #property-availableSpace} when its width has changed.
       * @config {Number}
       * @internal
       */
      availableSpace: null,

      /**
       * The "tick width" for horizontal mode or "tick height" for vertical mode, to use for the cells in the
       * bottom most header row.
       * This value is normally read from the {@link Scheduler.preset.ViewPreset viewPreset}
       * @config {Number}
       * @default
       * @internal
       */
      tickSize: 100,

      /**
       * true if there is a requirement to be able to snap events to a certain view resolution.
       * This has implications of the {@link #config-tickSize} that can be used, since all widths must be in even pixels.
       * @config {Boolean}
       * @default
       * @internal
       */
      snap: false,

      /**
       * true if cells in the bottom-most row should be fitted to the {@link #property-availableSpace available space}.
       * @config {Boolean}
       * @default
       * @internal
       */
      forceFit: false,
      headers: null,
      calendar: null,
      mode: 'horizontal',
      // or 'vertical'
      //used for Exporting. Make sure the tick columns are not recalculated when resizing.
      suppressFit: false,
      // cache of the config currently used.
      columnConfig: [],
      // the view preset name to apply initially
      viewPreset: null,
      // The default header level to draw column lines for
      columnLinesFor: null,
      // To give a link to the eventStore inside the header renderer if provided
      eventStore: null,
      originalTickSize: null,
      headersDatesCache: []
    };
  } //endregion
  //region Init & destroy

  construct(config) {
    const me = this; // getSingleUnitInPixels results are memoized because of frequent calls during rendering.

    me.unitToPixelsCache = {};
    super.construct(config);
    const viewPreset = me.timeAxis.viewPreset || me.viewPreset;

    if (viewPreset) {
      if (viewPreset instanceof ViewPreset) {
        me.consumeViewPreset(viewPreset);
      } else {
        const preset = pm.getPreset(viewPreset);
        preset && me.consumeViewPreset(preset);
      }
    } // When time axis is changed, reconfigure the model

    me.timeAxis.on('reconfigure', me.onTimeAxisReconfigure, me);
    me.configured = true;
  }

  doDestroy() {
    this.timeAxis.un('reconfigure', this.onTimeAxisReconfigure, this);
    super.doDestroy();
  }
  /**
   * Returns an array representing the headers of the current timeAxis. Each element is an array representing the cells for that level in the header.
   * @return {Object[]} An array of headers, each element being an array representing each cell (with start date and end date) in the timeline representation.
   * @internal
   */

  get columnConfig() {
    return this._columnConfig;
  }

  set columnConfig(config) {
    this._columnConfig = config;
  }

  get headers() {
    return this._headers;
  }

  set headers(headers) {
    if (headers && headers.length && headers[headers.length - 1].cellGenerator) {
      throw new Error('`cellGenerator` cannot be used for the bottom level of your headers. Use TimeAxis#generateTicks() instead.');
    }

    this._headers = headers;
  }

  get isTimeAxisViewModel() {
    return true;
  } //endregion
  //region Events

  /**
   * Fires after the model has been updated.
   * @event update
   * @param {Scheduler.view.model.TimeAxisViewModel} source The model instance
   */

  /**
   * Fires after the model has been reconfigured.
   * @event reconfigure
   * @param {Scheduler.view.model.TimeAxisViewModel} source The model instance
   */
  //endregion
  //region Mode

  /**
   * Using horizontal mode?
   * @returns {Boolean}
   * @readonly
   * @internal
   */

  get isHorizontal() {
    return this.mode !== 'vertical';
  }
  /**
   * Using vertical mode?
   * @returns {Boolean}
   * @readonly
   * @internal
   */

  get isVertical() {
    return this.mode === 'vertical';
  }
  /**
   * Gets/sets the forceFit value for the model. Setting it will cause it to update its contents and fire the
   * {@link #event-update} event.
   * @property {Boolean}
   * @internal
   */

  set forceFit(value) {
    if (value !== this._forceFit) {
      this._forceFit = value;
      this.update();
    }
  } //endregion
  //region Reconfigure & update

  reconfigure(config) {
    // clear the cached headers
    const me = this;
    me.headers = null; // Ensure correct ordering

    me.setConfig(config);
    me.trigger('reconfigure');
  }

  onTimeAxisReconfigure({
    source: timeAxis,
    suppressRefresh
  }) {
    if (this.viewPreset !== timeAxis.viewPreset) {
      this.consumeViewPreset(timeAxis.viewPreset);
    }

    if (!suppressRefresh) {
      this.update();
    }
  }
  /**
   * Updates the view model current timeAxis configuration and available space.
   * @param {Number} [availableSpace] The available space for the rendering of the axis (used in forceFit mode)
   * @param {Boolean} [silent] Pass `true` to suppress the firing of the `update` event.
   * @param {Boolean} [forceUpdate] Pass `true` to fire the `update` event even if the size has not changed.
   * @internal
   */

  update(availableSpace, silent = false, forceUpdate = false) {
    const me = this,
          {
      timeAxis,
      headers
    } = me; // We're in configuration, or no change, quit

    if (me.isConfiguring || me._availableSpace === availableSpace) {
      if (forceUpdate) {
        me.trigger('update');
      }

      return;
    }

    me._availableSpace = Math.max(availableSpace || me.availableSpace || 0, 0);

    if (typeof me.availableSpace !== 'number') {
      throw new Error('Invalid available space provided to TimeAxisModel');
    }

    me.columnConfig = []; // The "column width" is considered to be the width of each tick in the lowest header row and this width
    // has to be same for all cells in the lowest row.

    const tickSize = me._tickSize = me.calculateTickSize(me.originalTickSize);

    if (typeof tickSize !== 'number' || tickSize <= 0) {
      throw new Error('Invalid timeAxis tick size');
    } // getSingleUnitInPixels results are memoized because of frequent calls during rendering.

    me.unitToPixelsCache = {}; // totalSize is cached because of frequent calls which calculate it.

    me._totalSize = null; // Generate the underlying date ranges for each header row, which will provide input to the cell rendering

    for (let pos = 0, {
      length
    } = headers; pos < length; pos++) {
      const header = headers[pos];

      if (header.cellGenerator) {
        const headerCells = header.cellGenerator.call(me, timeAxis.startDate, timeAxis.endDate);
        me.columnConfig[pos] = me.createHeaderRow(pos, header, headerCells);
      } else {
        me.columnConfig[pos] = me.createHeaderRow(pos, header);
      }
    }

    if (!silent) {
      me.trigger('update');
    }
  } //endregion
  //region CalendarColumn
  // /**
  //  * Will update columns start/end dates to perform date/coordinate lookups
  //  * @param {Object[]} columns Array of column configs including start/end dates
  //  * @private
  //  */
  // updateCalendarColumnDates(columns) {
  //     this.calendarColumnDates = [];
  //
  //     for (let i = 0; i < columns.length; i++) {
  //         let obj = columns[i];
  //         this.calendarColumnDates.push([obj.start, obj.end]);
  //     }
  // }
  // // TODO: make getter
  // /**
  //  * Returns current column start/end dates array
  //  * @returns {Date[]}
  //  * @private
  //  */
  // getCalendarColumnDates() {
  //     return this.calendarColumnDates;
  // }
  //endregion
  //region Date / position mapping

  /**
   * Returns the distance in pixels for a timespan with the given start and end date.
   * @param {Date} start start date
   * @param {Date} end end date
   * @return {Number} The length of the time span
   * @category Date mapping
   */

  getDistanceBetweenDates(start, end) {
    return Math.round(this.getPositionFromDate(end) - this.getPositionFromDate(start));
  }
  /**
   * Returns the distance in pixels for a time span
   * @param {Number} durationMS Time span duration in ms
   * @return {Number} The length of the time span
   * @category Date mapping
   */

  getDistanceForDuration(durationMs) {
    const me = this,
          tick = this.timeAxis.getAt(0),
          pxPerMs = me.tickSize / (tick.endDate - tick.startDate);
    return pxPerMs * durationMs;
  }
  /**
   * Gets the position of a date on the projected time axis or -1 if the date is not in the timeAxis.
   * @param {Date} date the date to query for.
   * @returns {Number} the coordinate representing the date
   * @category Date mapping
   */

  getPositionFromDate(date, options = {}) {
    const me = this,
          tick = me.getScaledTick(date, options);

    if (tick === -1) {
      return -1;
    }

    return Math.round(me.tickSize * (tick - me.timeAxis.visibleTickStart));
  } // Translates a tick along the time axis to facilitate scaling events when excluding certain days or hours

  getScaledTick(date, {
    respectExclusion,
    snapToNextIncluded,
    isEnd,
    min,
    max
  }) {
    const {
      timeAxis
    } = this,
          {
      include,
      unit
    } = timeAxis;
    let tick = timeAxis.getTickFromDate(date);

    if (tick !== -1 && respectExclusion && include) {
      let tickChanged = false; // Stretch if we are using a larger unit than 'hour', except if it is 'day'. If so, it is already handled
      // by a cheaper reconfiguration of the ticks in `generateTicks`

      if (include.hour && DateHelper.compareUnits(unit, 'hour') > 0 && unit !== 'day') {
        const {
          from,
          to,
          lengthFactor,
          center
        } = include.hour,
              // Original hours
        originalHours = date.getHours(),
              // Crop to included hours
        croppedHours = Math.min(Math.max(originalHours, from), to); // If we are not asked to snap (when other part of span is not included) any cropped away hour
        // should be considered excluded

        if (!snapToNextIncluded && croppedHours !== originalHours) {
          return -1;
        }

        const // Should scale hour and smaller units (seconds will hardly affect visible result...)
        fractionalHours = croppedHours + date.getMinutes() / 60,
              // Number of hours from the center    |xxxx|123c----|xxx|
        hoursFromCenter = center - fractionalHours,
              // Step from center to stretch event  |x|112233c----|xxx|
        newHours = center - hoursFromCenter * lengthFactor; // Adding instead of setting to get a clone of the date, to not affect the original

        date = DateHelper.add(date, newHours - originalHours, 'h');
        tickChanged = true;
      }

      if (include.day && DateHelper.compareUnits(unit, 'day') > 0) {
        const {
          from,
          to,
          lengthFactor,
          center
        } = include.day; //region Crop

        let checkDay = date.getDay(); // End date is exclusive, check the day before if at 00:00

        if (isEnd && date.getHours() === 0 && date.getMinutes() === 0 && date.getSeconds() === 0 && date.getMilliseconds() === 0) {
          if (--checkDay < 0) {
            checkDay = 6;
          }
        }

        let addDays = 0;

        if (checkDay < from || checkDay >= to) {
          // If end date is in view but start date is excluded, snap to next included day
          if (snapToNextIncluded) {
            // Step back to "to-1" (not inclusive) for end date
            if (isEnd) {
              addDays = (to - checkDay - 8) % 7;
            } // Step forward to "from" for start date
            else {
                addDays = (from - checkDay + 7) % 7;
              }

            date = DateHelper.add(date, addDays, 'd');
            date = DateHelper.startOf(date, 'd', false); // Keep end after start and vice versa

            if (max && date.getTime() >= max || min && date.getTime() <= min) {
              return -1;
            }
          } else {
            // day excluded at not snapping to next
            return -1;
          }
        } //endregion

        const {
          weekStartDay
        } = timeAxis,
              // Center to stretch around, for some reason pre-calculated cannot be used for sundays :)
        fixedCenter = date.getDay() === 0 ? 0 : center,
              // Should scale day and smaller units (minutes will hardly affect visible result...)
        fractionalDay = date.getDay() + date.getHours() / 24,
              //+ dateClone.getMinutes() / (24 * 1440),
        // Number of days from the calculated center
        daysFromCenter = fixedCenter - fractionalDay,
              // Step from center to stretch event
        newDay = fixedCenter - daysFromCenter * lengthFactor; // Adding instead of setting to get a clone of the date, to not affect the original

        date = DateHelper.add(date, newDay - fractionalDay + weekStartDay, 'd');
        tickChanged = true;
      } // Now the date might start somewhere else (fraction of ticks)

      if (tickChanged) {
        // When stretching date might end up outside of time axis, making it invalid to use. Clip it to time axis
        // to circumvent this
        date = DateHelper.constrain(date, timeAxis.startDate, timeAxis.endDate); // Get a new tick based on the "scaled" date

        tick = timeAxis.getTickFromDate(date);
      }
    }

    return tick;
  }
  /**
   * Gets the date for a position on the time axis
   * @param {Number} position The page X or Y coordinate
   * @param {String} [roundingMethod] The rounding method to use
   * @param {Boolean} [allowOutOfRange=false] By default, this returns `null` if the position is outside
   * of the time axis. Pass `true` to attempt to calculate a date outside of the time axis.
   * @returns {Date} the Date corresponding to the xy coordinate
   * @category Date mapping
   */

  getDateFromPosition(position, roundingMethod, allowOutOfRange = false) {
    const me = this,
          timeAxis = me.timeAxis,
          tick = me.getScaledPosition(position) / me.tickSize + timeAxis.visibleTickStart;

    if (tick < 0 || tick > timeAxis.count) {
      if (allowOutOfRange) {
        // Subtract the correct number of tick units from the start date
        if (tick < 0) {
          return DateHelper.add(timeAxis.startDate, tick, timeAxis.unit);
        } // Add the correct number of tick units to the end date

        return DateHelper.add(timeAxis.endDate, tick - timeAxis.count, timeAxis.unit);
      }

      return null;
    }

    return timeAxis.getDateFromTick(tick, roundingMethod);
  } // Translates a position along the time axis to facilitate scaling events when excluding certain days or hours

  getScaledPosition(position) {
    const {
      include,
      unit,
      weekStartDay
    } = this.timeAxis; // Calculations are

    if (include) {
      const dayWidth = this.getSingleUnitInPixels('day'); // Have to calculate day before hour to get end result correct

      if (include.day && DateHelper.compareUnits(unit, 'day') > 0) {
        const {
          from,
          lengthFactor
        } = include.day,
              // Scaling happens within a week, determine position within it
        positionInWeek = position % (dayWidth * 7),
              // Store were the week starts to be able to re-add it after scale
        weekStartPosition = position - positionInWeek; // Scale position using calculated length per day factor, adding the width of excluded days

        position = positionInWeek / lengthFactor + (from - weekStartDay) * dayWidth + weekStartPosition;
      } // Hours are not taken into account when viewing days, since the day ticks are reconfigured in
      // `generateTicks` instead

      if (include.hour && DateHelper.compareUnits(unit, 'hour') > 0 && unit !== 'day') {
        const {
          from,
          lengthFactorExcl
        } = include.hour,
              hourWidth = this.getSingleUnitInPixels('hour'),
              // Scaling happens within a day, determine position within it
        positionInDay = position % dayWidth,
              // Store were the day starts to be able to re-add it after scale
        dayStartPosition = position - positionInDay; // Scale position using calculated length per day factor, adding the width of excluded hours

        position = positionInDay / lengthFactorExcl + from * hourWidth + dayStartPosition;
      }
    }

    return position;
  }
  /**
   * Returns the amount of pixels for a single unit
   * @internal
   * @return {Number} The unit in pixel
   */

  getSingleUnitInPixels(unit) {
    const me = this;
    return me.unitToPixelsCache[unit] || (me.unitToPixelsCache[unit] = DateHelper.getUnitToBaseUnitRatio(me.timeAxis.unit, unit, true) * me.tickSize / me.timeAxis.increment);
  }
  /**
   * [Experimental] Returns the pixel increment for the current view resolution.
   * @internal
   * @return {Number} The increment
   */

  get snapPixelAmount() {
    if (this.snap) {
      const {
        resolution
      } = this.timeAxis;
      return (resolution.increment || 1) * this.getSingleUnitInPixels(resolution.unit);
    } else {
      return 1;
    }
  } //endregion
  //region Sizes

  /**
   * Get/set the current time column size (the width or height of a cell in the bottom-most time axis header row,
   * depending on mode)
   * @internal
   * @property {Number}
   */

  get tickSize() {
    return this._tickSize;
  }

  set tickSize(size) {
    this.setTickSize(size, false);
  }

  setTickSize(size, suppressEvent) {
    this._tickSize = this.originalTickSize = size;
    this.update(undefined, suppressEvent);
  }

  get timeResolution() {
    return this.timeAxis.resolution;
  } // Calculates the time column width/height based on the value defined viewPreset "tickWidth/tickHeight". It also
  // checks for the forceFit view option and the snap, both of which impose constraints on the time column width
  // configuration.

  calculateTickSize(proposedSize) {
    const me = this,
          {
      forceFit,
      timeAxis,
      suppressFit
    } = me,
          timelineUnit = timeAxis.unit;
    let size = 0,
        ratio = 1; //Number.MAX_VALUE;

    if (me.snap) {
      const resolution = timeAxis.resolution;
      ratio = DateHelper.getUnitToBaseUnitRatio(timelineUnit, resolution.unit) * resolution.increment;
    }

    if (!suppressFit) {
      const ticks = timeAxis.visibleTickTimeSpan,
            // TODO: change this to always floor if we want to prevent scheduler scroll when not really needed
      fittingSize = Math[forceFit ? 'floor' : 'round'](me.availableSpace / ticks);
      size = forceFit || proposedSize < fittingSize ? fittingSize : proposedSize;

      if (ratio > 0 && (!forceFit || ratio < 1)) {
        // For touch, make sure we always fill the available space with forceFit (to not show edges of side-time-pickers)
        const method = forceFit ? 'floor' : 'round';
        size = Math.round(Math.max(1, Math[method](ratio * size)) / ratio);
      }
    } else {
      size = proposedSize;
    }

    return size;
  }
  /**
   * Returns the total width/height of the time axis representation, depending on mode.
   * @return {Number} The width or height
   * @internal
   * @readonly
   */

  get totalSize() {
    return this._totalSize || (this._totalSize = Math.round(this.tickSize * this.timeAxis.visibleTickTimeSpan));
  }
  /**
   * Get/set the available space for the time axis representation. If size changes it will cause it to update its
   * contents and fire the {@link #event-update} event.
   * @internal
   * @property {Number}
   */

  get availableSpace() {
    return this._availableSpace;
  }

  set availableSpace(space) {
    const me = this; // We should only need to repaint fully if the tick width has changed (which will happen if forceFit is set, or if the full size of the time axis doesn't
    // occupy the available space - and gets stretched

    me._availableSpace = Math.max(0, space);

    if (me._availableSpace > 0) {
      const newTickSize = me.calculateTickSize(me.originalTickSize);

      if (newTickSize > 0 && newTickSize !== me.tickSize) {
        me.update();
      }
    }
  } //endregion
  //region Fitting & snapping

  /**
   * Returns start dates for ticks at the specified level in format { date, isMajor }.
   * @param {Number} level Level in headers array, `0` meaning the topmost...
   * @param {Boolean} useLowestHeader Use lowest level
   * @param getEnd
   * @returns {Array}
   * @internal
   */

  getDates(level = this.columnLinesFor, useLowestHeader = false, getEnd = false) {
    const me = this,
          ticks = [],
          linesForLevel = useLowestHeader ? me.lowestHeader : level,
          majorLevel = me.majorHeaderLevel,
          levelUnit = me.headers && me.headers[level].unit,
          majorUnit = majorLevel != null && me.headers && me.headers[majorLevel].unit,
          validMajor = majorLevel != null && DateHelper.doesUnitsAlign(majorUnit, levelUnit),
          hasGenerator = !!(me.headers && me.headers[linesForLevel].cellGenerator);

    if (hasGenerator) {
      const cells = me.columnConfig[linesForLevel];

      for (let i = 1, l = cells.length; i < l; i++) {
        ticks.push({
          date: cells[i].startDate
        });
      }
    } else {
      me.forEachInterval(linesForLevel, (start, end) => {
        ticks.push({
          date: getEnd ? end : start,
          // do not want to consider tick to be major tick, hence the check for majorHeaderLevel
          isMajor: majorLevel !== level && validMajor && me.isMajorTick(getEnd ? end : start)
        });
      });
    }

    return ticks;
  }

  get forceFit() {
    return this._forceFit;
  }
  /**
   * This function fits the time columns into the available space in the time axis column.
   * @param {Boolean} suppressEvent `true` to skip firing the 'update' event.
   * @internal
   */

  fitToAvailableSpace(suppressEvent) {
    const proposedSize = Math.floor(this.availableSpace / this.timeAxis.visibleTickTimeSpan);
    this.setTickSize(proposedSize, suppressEvent);
  }

  get snap() {
    return this._snap;
  }
  /**
   * Gets/sets the snap value for the model. Setting it will cause it to update its contents and fire the
   * {@link #event-update} event.
   * @property {Boolean}
   * @internal
   */

  set snap(value) {
    if (value !== this._snap) {
      this._snap = value;
      if (this.configured) this.update();
    }
  } //endregion
  //region Headers
  // private

  createHeaderRow(position, headerRowConfig, headerCells) {
    const me = this,
          cells = [],
          align = headerRowConfig.align,
          today = DateHelper.clearTime(new Date()),
          headerCellCls = headerRowConfig.headerCellCls || '',
          createCellContext = (start, end, i, data) => {
      let value = DateHelper.format(start, headerRowConfig.dateFormat);
      const cellData = {
        align,
        start,
        end,
        value: data ? data.header : value,
        headerCellCls,
        width: this.getDistanceBetweenDates(start, end),
        index: i
      };

      if (cellData.width === 0) {
        return;
      } // Vertical mode uses absolute positioning for header cells

      cellData.coord = size;
      size += cellData.width;
      me.headersDatesCache[position][start.getTime()] = 1;

      if (headerRowConfig.renderer) {
        value = headerRowConfig.renderer.call(headerRowConfig.thisObj || me, start, end, cellData, i, me.eventStore);
        cellData.value = value == null ? '' : value;
      } // To be able to style individual day cells, weekends or other important days

      if (headerRowConfig.unit === 'day' && (!headerRowConfig.increment || headerRowConfig.increment === 1)) {
        cellData.headerCellCls += ' b-sch-dayheadercell-' + start.getDay();

        if (me.calendar && me.calendar.isWeekend(start)) {
          cellData.headerCellCls += ' b-sch-dayheadercell-nonworking';
        }

        if (DateHelper.clearTime(start, true) - today === 0) {
          cellData.headerCellCls += ' b-sch-dayheadercell-today';
        }
      }

      cells.push(cellData);
    };

    let size = 0;
    me.headersDatesCache[position] = {};

    if (headerCells) {
      headerCells.forEach((cellData, i) => createCellContext(cellData.start, cellData.end, i, cellData));
    } else {
      me.forEachInterval(position, createCellContext);
    }

    return cells;
  }

  get mainHeader() {
    return 'mainHeaderLevel' in this ? this.headers[this.mainHeaderLevel] : this.bottomHeader;
  }

  get bottomHeader() {
    return this.headers[this.headers.length - 1];
  }

  get lowestHeader() {
    return this.headers.length - 1;
  }
  /**
   * This method is meant to return the level of the header which 2nd lowest.
   * It is used for {@link #function-isMajorTick} method
   * @return {String}
   * @private
   */

  get majorHeaderLevel() {
    const {
      headers
    } = this;

    if (headers) {
      return Math.max(headers.length - 2, 0);
    }

    return null;
  } //endregion
  //region Ticks

  /**
   * For vertical view (and column lines plugin) we sometimes want to know if current tick starts along with the
   * upper header level.
   * @param {Date} date
   * @return {Boolean}
   * @private
   */

  isMajorTick(date) {
    const nextLevel = this.majorHeaderLevel; // if forceFit is used headersDatesCache won´t have been generated yet on the first call here,
    // since no size is set yet

    return nextLevel != null && this.headersDatesCache[nextLevel] && this.headersDatesCache[nextLevel][date.getTime()] || false;
  }
  /**
   * Calls the supplied iterator function once per interval. The function will be called with three parameters, start date and end date and an index.
   * Return false to break the iteration.
   * @param {Number} position The index of the header in the headers array.
   * @param {Function} iteratorFn The function to call, will be called with start date, end date and "tick index"
   * @param {Object} [thisObj] `this` reference for the function
   * @internal
   */

  forEachInterval(position, iteratorFn, thisObj = this) {
    const {
      headers
    } = this;

    if (headers) {
      // This is the lowest header row, which should be fed the data in the tickStore (or a row above using same unit)
      if (position === headers.length - 1) {
        this.timeAxis.forEach((r, index) => iteratorFn.call(thisObj, r.startDate, r.endDate, index));
      } // All other rows
      else {
          const header = headers[position];
          this.timeAxis.forEachAuxInterval(header.unit, header.increment, iteratorFn, thisObj);
        }
    }
  }
  /**
   * Calls the supplied iterator function once per interval. The function will be called with three parameters, start date and end date and an index.
   * Return false to break the iteration.
   * @internal
   * @param {Function} iteratorFn The function to call
   * @param {Object} [thisObj] `this` reference for the function
   */

  forEachMainInterval(iteratorFn, thisObj) {
    this.forEachInterval(this.mainHeaderLevel, iteratorFn, thisObj);
  } //endregion
  //region ViewPreset

  consumeViewPreset(preset) {
    const me = this; // clear the cached headers

    me.headers = null;

    me._thisIsAUsedExpression(me.tickSize); // Since we are bypassing the tickSize setter below, ensure that
    // the config initial setter has been removed by referencing the property.
    // We only do this to avoid multiple updates from this. TODO: Fix this issue,
    // and do not bypass setters.

    me.viewPreset = preset;
    Object.assign(me, {
      headers: preset.headers,
      columnLinesFor: preset.columnLinesFor,
      mainHeaderLevel: preset.mainHeaderLevel,
      _tickSize: me.isHorizontal ? preset.tickWidth : preset.tickHeight
    });
    me.originalTickSize = me.tickSize;
  } //endregion
  // //region Calendar
  //
  // setCalendar(cal) {
  //     if (cal !== this.calendar) {
  //         this.calendar = cal;
  //
  //         if (cal) {
  //             this.update();
  //         }
  //     }
  // }
  //
  // //endregion

}
TimeAxisViewModel._$name = 'TimeAxisViewModel';

const tempDate = new Date();
/**
 * @module Scheduler/view/mixin/TimelineDateMapper
 */

/**
 * Mixin that contains functionality to convert between coordinates and dates etc.
 *
 * @mixin
 */

var TimelineDateMapper = (Target => class TimelineDateMapper extends (Target || Base) {
  static get $name() {
    return 'TimelineDateMapper';
  } //region Coordinate <-> Date

  /**
   * Gets the date for an X or Y coordinate, either local to the view element or the page based on the 3rd argument.
   * If the coordinate is not in the currently rendered view, null will be returned unless the `allowOutOfRange`
   * parameter is passed a `true`.
   * @param {Number} coordinate The X or Y coordinate
   * @param {String} [roundingMethod] The rounding method to use
   * @param {Boolean} [local] true if the coordinate is local to the scheduler view element
   * @param {Boolean} [allowOutOfRange] By default, this returns `null` if the position is outside
   * of the time axis. Pass `true` to attempt to calculate a date outside of the time axis.
   * @returns {Date} The Date corresponding to the X or Y coordinate
   * @category Dates
   */

  getDateFromCoordinate(coordinate, roundingMethod, local = true, allowOutOfRange = false) {
    if (!local) {
      coordinate = this.currentOrientation.translateToScheduleCoordinate(coordinate);
    }

    return this.timeAxisViewModel.getDateFromPosition(coordinate, roundingMethod, allowOutOfRange);
  }
  /**
   * Gets the date for an XY coordinate regardless of the orientation of the time axis.
   * @param {Array} xy The page X and Y coordinates
   * @param {String} [roundingMethod] Optional, 'floor' to floor the value or 'round' to round the value to nearest increment
   * @param {Boolean} [local] true if the coordinate is local to the scheduler element
   * @param {Boolean} [allowOutOfRange] By default, this returns `null` if the position is outside
   * of the time axis. Pass `true` to attempt to calculate a date outside of the time axis.
   * @returns {Date} the Date corresponding to the xy coordinate
   * @category Dates
   */

  getDateFromXY(xy, roundingMethod, local = true, allowOutOfRange = false) {
    return this.currentOrientation.getDateFromXY(xy, roundingMethod, local, allowOutOfRange);
  }
  /**
   * Gets the time for a DOM event such as 'mousemove' or 'click' regardless of the orientation of the time axis.
   * @param {Event} e the Event instance
   * @param {String} [roundingMethod] Optional, 'floor' to floor the value or 'round' to round the value to nearest increment
   * @param {Boolean} [allowOutOfRange] By default, this returns `null` if the position is outside
   * of the time axis. Pass `true` to attempt to calculate a date outside of the time axis.
   * @returns {Date} The date corresponding to the EventObject's position along the orientation of the time axis.
   * @category Dates
   */

  getDateFromDomEvent(e, roundingMethod, allowOutOfRange = false) {
    return this.getDateFromXY([e.pageX, e.pageY], roundingMethod, false, allowOutOfRange);
  }
  /**
   * Gets the start and end dates for an element Region
   * @param {Core.helper.util.Rectangle} rect The rectangle to map to start and end dates
   * @param {String} roundingMethod The rounding method to use
   * @param {Number} duration The duration in MS of the underlying event
   * @returns {Object} an object containing start/end properties
   */

  getStartEndDatesFromRectangle(rect, roundingMethod, duration, allowOutOfRange = false) {
    const me = this,
          {
      isHorizontal
    } = me,
          timeSpanEnd = isHorizontal ? me.timeAxisSubGrid.width : me.timeAxisSubGrid.height,
          startPos = isHorizontal ? rect.x : rect.top,
          endPos = isHorizontal ? rect.right : rect.bottom;
    let start, end; // Element within bounds

    if (startPos >= 0 && endPos < timeSpanEnd) {
      start = me.getDateFromCoordinate(startPos, null, true);
      end = me.getDateFromCoordinate(endPos, null, true);
    } // Starts before, start is worked backwards from end
    else if (startPos < 0) {
        end = me.getDateFromCoordinate(endPos, roundingMethod, true, allowOutOfRange);
        start = end && DateHelper.add(end, -duration, 'ms');
      } // Ends after, end is calculated from the start
      else {
          start = me.getDateFromCoordinate(startPos, roundingMethod, true, allowOutOfRange);
          end = start && DateHelper.add(start, duration, 'ms');
        }

    return {
      start,
      end
    };
  } //endregion
  //region Date display

  /**
   * Get/set format to use when displaying dates. Usually set by specifying a view preset
   * @property {String}
   * @category Dates
   */

  get displayDateFormat() {
    return this._displayDateFormat;
  }

  set displayDateFormat(format) {
    this._displayDateFormat = format; // Start/EndDateColumn listens for this to change their format to match

    this.trigger('displayDateFormatChange', {
      format
    });
  }
  /**
   * Method to get a formatted display date
   * @private
   * @param {Date} date The date
   * @return {String} The formatted date
   */

  getFormattedDate(date) {
    return DateHelper.format(date, this.displayDateFormat);
  }
  /**
   * Method to get a displayed end date value, see {@link #function-getFormattedEndDate} for more info.
   * @private
   * @param {Date} endDate The date to format
   * @param {Date} startDate The start date
   * @return {Date} The date value to display
   */

  getDisplayEndDate(endDate, startDate) {
    if ( // If time is midnight,
    endDate.getHours() === 0 && endDate.getMinutes() === 0 && ( // and end date is greater then start date
    !startDate || !(endDate.getYear() === startDate.getYear() && endDate.getMonth() === startDate.getMonth() && endDate.getDate() === startDate.getDate())) && // and UI display format doesn't contain hour info (in this case we'll just display the exact date)
    !DateHelper.formatContainsHourInfo(this.displayDateFormat)) {
      // format the date inclusively as 'the whole previous day'.
      endDate = DateHelper.add(endDate, -1, 'day');
    }

    return endDate;
  }
  /**
   * Method to get a formatted end date for a scheduled event, the grid uses the "displayDateFormat" property defined in the current view preset.
   * End dates are formatted as 'inclusive', meaning when an end date falls on midnight and the date format doesn't involve any hour/minute information,
   * 1ms will be subtracted (e.g. 2010-01-08T00:00:00 will first be modified to 2010-01-07 before being formatted).
   * @private
   * @param {Date} endDate The date to format
   * @param {Date} startDate The start date
   * @return {String} The formatted date
   */

  getFormattedEndDate(endDate, startDate) {
    return this.getFormattedDate(this.getDisplayEndDate(endDate, startDate));
  } //endregion
  //region Other date functions

  /**
   * Gets the x or y coordinate relative to the scheduler element, or page coordinate (based on the 'local' flag)
   * If the coordinate is not in the currently rendered view, -1 will be returned.
   * @param {Date|Number} date the date to query for (or a date as ms)
   * @param {Boolean|Object} options true to return a coordinate local to the scheduler view element (defaults to true),
   * also accepts a config object like { local : true }.
   * @returns {Number} the x or y position representing the date on the time axis
   * @category Dates
   */

  getCoordinateFromDate(date, options = true) {
    const me = this,
          {
      timeAxisViewModel
    } = me,
          {
      isContinuous,
      startMS,
      endMS,
      startDate,
      unit
    } = me.timeAxis,
          dateMS = date.valueOf(); // Avoiding to break the API while allowing passing options through to getPositionFromDate()

    if (options === true) {
      options = {
        local: true
      };
    } else if (!options) {
      options = {
        local: false
      };
    } else if (!('local' in options)) {
      options.local = true;
    }

    let pos; // TODO for 2.0 try to normalize and just use dates as input for this method,
    // then this if-statement would not be needed

    if (!(date instanceof Date)) {
      tempDate.setTime(date);
      date = tempDate;
    } // Shortcut for continuous time axis that is using a unit that can be reliably translated to days (or smaller)

    if (isContinuous && date.getTimezoneOffset() === startDate.getTimezoneOffset() && DateHelper.getUnitToBaseUnitRatio(unit, 'day') !== -1) {
      if (dateMS < startMS || dateMS > endMS) {
        return -1;
      }

      pos = Math.round((dateMS - startMS) / (endMS - startMS) * timeAxisViewModel.totalSize);
    } // Non-continuous or using for example months (vary in length)
    else {
        pos = timeAxisViewModel.getPositionFromDate(date, options);
      }

    if (!options.local) {
      pos = me.currentOrientation.translateToPageCoordinate(pos);
    }

    return pos;
  }
  /**
   * Returns the distance in pixels for the time span in the view.
   * @param {Date} startDate The start date of the span
   * @param {Date} endDate The end date of the span
   * @return {Number} The distance in pixels
   * @category Dates
   */

  getTimeSpanDistance(startDate, endDate) {
    return this.timeAxisViewModel.getDistanceBetweenDates(startDate, endDate);
  }
  /**
   * Returns the center date of the currently visible timespan of scheduler.
   *
   * @property {Date}
   * @readonly
   * @category Dates
   */

  get viewportCenterDate() {
    const me = this,
          {
      isHorizontal,
      timeAxis
    } = me,
          scroller = isHorizontal ? me.timeAxisSubGrid.scrollable : me.scrollable,
          centerX = scroller.x + scroller.clientWidth / 2,
          centerY = scroller.y + scroller.clientHeight / 2;

    if (timeAxis.isContinuous) {
      // Calculate center pixel in the viewport.
      // Then Calculate how far through the axis range that is.
      const scrollCenter = isHorizontal ? centerX / scroller.scrollWidth : centerY / scroller.scrollHeight,
            centerMilli = timeAxis.startMS + (timeAxis.endMS - timeAxis.startMS) * scrollCenter;
      return new Date(centerMilli);
    } else {
      const xy = isHorizontal ? [centerX, 0] : [0, centerY];
      return me.getDateFromXY(xy, null, true);
    }
  }

  get viewportCenterDateCached() {
    return this.cachedCenterDate || (this.cachedCenterDate = this.viewportCenterDate);
  } //endregion
  //region TimeAxis getters/setters

  /**
   * Gets/sets the current time resolution object, which contains a unit identifier and an increment count { unit, increment }.
   * This value means minimal task duration you can create using UI.
   * For example when you drag create a task or drag & drop a task, if increment is 5 and unit is 'minute'
   * that means that you can create a 5 min long task, or move it 5 min forward/backward.
   * This value is taken from viewPreset {@link Scheduler.preset.ViewPreset#field-timeResolution timeResolution} config by default.
   * When supplying a `Number` to the setter only the `increment` is changed and the `unit` value remains untouched.
   *
   * ```javascript
   * timeResolution : {
   *   unit      : 'minute',  //Valid values are "millisecond", "second", "minute", "hour", "day", "week", "month", "quarter", "year".
   *   increment : 5
   * }
   * ```
   *
   * @property {Object|Number}
   * @category Dates
   */

  get timeResolution() {
    return this.timeAxis.resolution;
  }

  set timeResolution(resolution) {
    this.timeAxis.resolution = typeof resolution === 'number' ? {
      increment: resolution,
      unit: this.timeAxis.resolution.unit
    } : resolution;
  } //endregion
  //region Snap

  /**
   * Controls whether the scheduler should snap to the resolution when interacting with it
   * @property {Boolean}
   * @category Dates
   */

  set snap(enabled) {
    // timeAxisViewModel is not created yet during configuration
    if (!this.isConfiguring) {
      this.timeAxisViewModel.snap = enabled;
    } else {
      this._snap = enabled;
    }
  }

  get snap() {
    // timeAxisViewModel is not created yet during configuration
    if (this.isConfiguring) {
      return this._snap;
    }

    return this.timeAxisViewModel.snap;
  } //endregion

  onSchedulerHorizontalScroll({
    subGrid,
    scrollLeft
  }) {
    // Invalidate cached center date unless we are scrolling to center on it.
    if (!this.scrollingToCenter) {
      this.cachedCenterDate = null;
    }
  } // This does not need a className on Widgets.
  // Each *Class* which doesn't need 'b-' + constructor.name.toLowerCase() automatically adding
  // to the Widget it's mixed in to should implement thus.

  get widgetClass() {}

});

/**
 * @module Scheduler/view/mixin/TimelineDomEvents
 */

const eventNameMap = {
  mousedown: 'MouseDown',
  mouseup: 'MouseUp',
  click: 'Click',
  dblclick: 'DblClick',
  contextmenu: 'ContextMenu',
  mouseover: 'MouseOver',
  mouseout: 'MouseOut'
};
/**
 * Mixin that handles dom events (click etc) for scheduler and rendered events.
 *
 * @mixin
 */

var TimelineDomEvents = (Target => class TimelineDomEvents extends (Target || Base) {
  static get $name() {
    return 'TimelineDomEvents';
  } //region Default config

  static get defaultConfig() {
    return {
      // TODO: PORT longpress missing
      scheduledBarEvents: {
        mousedown: 'handleScheduledBarEvent',
        mouseup: 'handleScheduledBarEvent',
        click: 'handleScheduledBarEvent',
        dblclick: 'handleScheduledBarEvent',
        contextmenu: 'handleScheduledBarEvent',
        mouseover: 'handleScheduledBarEvent',
        mouseout: 'handleScheduledBarEvent'
      },
      // TODO: PORT pinch, pinchstart, pinchend missing
      schedulerEvents: {
        click: 'handleScheduleEvent',
        dblclick: 'handleScheduleEvent',
        contextmenu: 'handleScheduleEvent',
        mousemove: 'handleScheduleEvent'
      }
    };
  } //endregion
  //region Init

  /**
   * Adds listeners for DOM events for the scheduler and its events.
   * Which events is specified in Scheduler#scheduledBarEvents and Scheduler#schedulerEvents.
   * @private
   */

  initDomEvents() {
    const me = this; // Set thisObj and element of the configured listener specs.

    me.scheduledBarEvents.element = me.schedulerEvents.element = me.timeAxisSubGridElement;
    me.scheduledBarEvents.thisObj = me.schedulerEvents.thisObj = me; // same listener used for different events

    EventHelper.on(me.scheduledBarEvents);
    EventHelper.on(me.schedulerEvents);
  } //endregion
  //region Event handling

  getTimeSpanMouseEventParams(eventElement, event) {
    throw new Error('Implement in subclass');
  }

  getScheduleMouseEventParams(cellData, event) {
    throw new Error('Implement in subclass');
  }
  /**
   * Wraps dom Events for rendered scheduler EventModels and fires prefixed as our events.
   * For example click -> eventclick
   * @private
   * @param event
   */

  handleScheduledBarEvent(event) {
    const me = this,
          eventElement = DomHelper.up(event.target, me.eventInnerSelector),
          eventName = eventNameMap[event.type] || StringHelper.capitalize(event.type);

    if (eventElement) {
      me.trigger(me.scheduledEventName + eventName, me.getTimeSpanMouseEventParams(eventElement, event));
    }
  }
  /**
   * Wraps dom Events for the scheduler and fires as our events.
   * For example click -> scheduleClick
   * @private
   * @param event
   */

  handleScheduleEvent(event) {
    const me = this,
          eventElement = DomHelper.up(event.target, me.eventSelector),
          cellElement = !eventElement && DomHelper.up(event.target, '.' + me.timeCellCls),
          eventName = eventNameMap[event.type] || StringHelper.capitalize(event.type);

    if (cellElement) {
      const clickedDate = me.getDateFromDomEvent(event, 'floor');

      if (!clickedDate) {
        return;
      }

      const cellData = DomDataStore.get(cellElement),
            index = cellData.row.dataIndex,
            tickIndex = me.timeAxis.getTickFromDate(clickedDate),
            tick = me.timeAxis.getAt(Math.floor(tickIndex));

      if (tick) {
        me.trigger('schedule' + eventName, Object.assign({
          date: clickedDate,
          tickStartDate: tick.startDate,
          tickEndDate: tick.endDate,
          row: cellData.row,
          index,
          event
        }, me.getScheduleMouseEventParams(cellData, event)));
      }
    }
  }
  /**
   * Relays mouseover events as eventmouseenter if over rendered event.
   * Also adds Scheduler#overScheduledEventClass to the hovered element.
   * @private
   */

  onElementMouseOver(event) {
    super.onElementMouseOver(event);
    const me = this,
          target = event.target; // We must be over the event bar

    if (target.closest(me.eventInnerSelector)) {
      const eventElement = target.closest(me.eventSelector);
      eventElement.classList.add(me.overScheduledEventClass);

      if (eventElement !== me.hoveredEventNode && !me.preventOverCls) {
        me.hoveredEventNode = eventElement;
        const params = me.getTimeSpanMouseEventParams(eventElement, event);

        if (params) {
          // do not fire this event if model cannot be found
          // this can be the case for "b-sch-dragcreator-proxy" elements for example
          me.trigger(me.scheduledEventName + 'MouseEnter', params);
        }
      }
    } else {
      me.hoveredEventNode = null;
    }
  }
  /**
   * Relays mouseout events as eventmouseleave if out from rendered event.
   * Also removes Scheduler#overScheduledEventClass from the hovered element.
   * @private
   */

  onElementMouseOut(event) {
    super.onElementMouseOut(event);
    const me = this; // We must be over the event bar

    if (event.target.closest(me.eventInnerSelector) && me.resolveTimeSpanRecord(event.target) && me.hoveredEventNode) {
      // out to child shouldn't count...
      if (event.relatedTarget && DomHelper.isDescendant(event.target.closest(me.eventInnerSelector), event.relatedTarget)) return;
      me.unhover(event);
    }
  }

  unhover(event) {
    const me = this,
          eventElement = me.hoveredEventNode;

    if (eventElement) {
      eventElement.classList.remove(me.overScheduledEventClass);
      me.trigger(me.scheduledEventName + 'MouseLeave', me.getTimeSpanMouseEventParams(eventElement, event));
      me.hoveredEventNode = null;
    }
  } //endregion
  // This does not need a className on Widgets.
  // Each *Class* which doesn't need 'b-' + constructor.name.toLowerCase() automatically adding
  // to the Widget it's mixed in to should implement thus.

  get widgetClass() {}

});

/**
 * @module Scheduler/view/mixin/TimelineViewPresets
 */

const datesDiffer = (d1 = 0, d2 = 0) => d2 - d1;
/**
 * View preset handling.
 *
 * A Scheduler's {@link #config-presets} are loaded with a default set of {@link Scheduler.preset.ViewPreset ViewPresets}
 * which are defined by the system in the {@link Scheduler.preset.PresetManager PresetManager}.
 *
 * The zooming feature works by reconfiguring the Scheduler with a new {@link Scheduler.preset.ViewPreset ViewPreset} selected
 * from the {@link #config-presets} store.
 *
 * {@link Scheduler.preset.ViewPreset ViewPresets} can be added and removed from the store to change the amount of available steps.
 * Range of zooming in/out can be also modified with {@link Scheduler.view.mixin.TimelineZoomable#config-maxZoomLevel} / {@link Scheduler.view.mixin.TimelineZoomable#config-minZoomLevel} properties.
 *
 * This mixin adds additional methods to the column : {@link Scheduler.view.mixin.TimelineZoomable#property-maxZoomLevel}, {@link Scheduler.view.mixin.TimelineZoomable#property-minZoomLevel}, {@link Scheduler.view.mixin.TimelineZoomable#function-zoomToLevel}, {@link Scheduler.view.mixin.TimelineZoomable#function-zoomIn},
 * {@link Scheduler.view.mixin.TimelineZoomable#function-zoomOut}, {@link Scheduler.view.mixin.TimelineZoomable#function-zoomInFull}, {@link Scheduler.view.mixin.TimelineZoomable#function-zoomOutFull}.
 *
 * **Notice**: Zooming is not supported when `forceFit` option is set to true for the Scheduler or for filtered timeaxis.
 *
 * @mixin
 */

var TimelineViewPresets = (Target => class TimelineViewPresets extends (Target || Base) {
  static get $name() {
    return 'TimelineViewPresets';
  } //region Default config

  static get configurable() {
    return {
      /**
       * A string key used to lookup a predefined {@link Scheduler.preset.ViewPreset} (e.g. 'weekAndDay', 'hourAndDay'),
       * managed by {@link Scheduler.preset.PresetManager}. See {@link Scheduler.preset.PresetManager} for more information.
       * Or a config object for a viewPreset.
       *
       * Options:
       * - 'secondAndMinute'
       * - 'minuteAndHour'
       * - 'hourAndDay'
       * - 'dayAndWeek'
       * - 'weekAndDay'
       * - 'weekAndMonth',
       * - 'monthAndYear'
       * - 'year'
       * - 'manyYears'
       * - 'weekAndDayLetter'
       * - 'weekDateAndMonth'
       * - 'day'
       * - 'week'
       *
       * If passed as a config object, the settings from the viewPreset with the provided `base` property will be used along
       * with any overridden values in your object.
       *
       * To override:
       * ```javascript
       * viewPreset : {
       *   base    : 'hourAndDay',
       *   id      : 'myHourAndDayPreset',
       *   headers : [
       *       {
       *           unit      : "hour",
       *           increment : 12,
       *           renderer  : (startDate, endDate, headerConfig, cellIdx) => {
       *               return "";
       *           }
       *       }
       *   ]
       * }
       * ```
       * or set a new valid preset config if the preset is not registered in the {@link Scheduler.preset.PresetManager}.
       *
       * When you use scheduler in weekview mode, this config is used to pick view preset. If passed view preset is not
       * supported by weekview (only 2 supported by default - 'day' and 'week') default preset will be used - 'week'.
       * @config {String|Object}
       * @default
       * @category Common
       */
      viewPreset: 'weekAndDayLetter'
    };
  }

  static get defaultConfig() {
    return {
      /**
       * Defines how dates will be formatted in tooltips etc. This config has priority over similar config on the
       * view preset. For allowed values see {@link Core.helper.DateHelper#function-format-static}.
       * @config {String}
       * @category Scheduled events
       */
      displayDateFormat: null,

      /**
       * An array of {@link Scheduler.preset.ViewPreset ViewPreset} config objects
       * which describes the available timeline layouts for this scheduler.
       *
       * By default, a predefined set is loaded from the {@link Scheduler.preset.PresetManager PresetManager}.
       *
       * A {@link Scheduler.preset.ViewPreset ViewPreset} describes the granularity of the
       * timeline view and the layout and subdivisions of the timeline header.
       * @config {Object[]} presets
       *
       * @category Common
       */
      presets: true
    };
  } //endregion

  /**
   * Get/set the current view preset
   * @member {Scheduler.preset.ViewPreset|String} viewPreset
   * @param [viewPreset.options]
   * @param {Date} [viewPreset.options.startDate] A new start date for the time axis
   * @param {Date} [viewPreset.options.endDate] A new end date for the time axis
   * @param {Date} [viewPreset.options.centerDate] Where to center the new time axis
   * @category Common
  */
  //region Get/set

  /**
   * Get the {@link Scheduler.preset.PresetStore PresetStore} created for the Scheduler,
   * or set an array of {@link Scheduler.preset.ViewPreset ViewPreset} config objects.
   * @property {Scheduler.preset.PresetStore|Object[]}
   * @category Common
   */

  set presets(presets) {
    if (presets === true) {
      presets = pm.allRecords;
    }

    this._presets = new PresetStore({
      data: presets
    });
  }

  get presets() {
    return this._presets;
  }

  normalizePreset(preset) {
    const me = this,
          input = preset,
          {
      presets
    } = me; // They may have passed a string id, an number index, or ViewPreset config object.

    if (!(preset instanceof ViewPreset)) {
      // A config of a preset, instantiate it based upon any base that may be requested.
      // This may be one that's supplied by Bryntum in the PresetManager, or one that has been
      // previously added to this Scheduler's preset store.
      if (typeof preset === 'object') {
        // Look up any existing ViewPreset that it is based upon.
        if (preset.base) {
          // Look locally first, then in the PresetManager
          const base = presets.getById(preset.base) || pm.getById(preset.base);

          if (!base) {
            throw new Error(`ViewPreset base '${preset.base}' does not exist`);
          } // The config is based upon the base's data with the new Preset type's normalized data object merged in.
          // MUST run the incoming data block through the constructor in order to apply the backward compatibility
          // processing to the data block to be merged with the base.

          preset = ObjectHelper.merge(ObjectHelper.clone(base.data), pm.createRecord(preset).data);
        } // Ensure the new ViewPreset has a legible, logical id which does not already
        // exist in our store.

        if (preset.id) {
          preset = presets.createRecord(preset);
        } else {
          preset = presets.createRecord(ObjectHelper.assign({}, preset));
          preset.id = preset.generateId(presets);
        }
      } // Must be an index or id
      else {
          if (typeof preset === 'number') {
            preset = presets.getAt(preset);
          } else {
            preset = presets.getById(preset) || pm.getById(preset);
          }
        }

      if (!preset) {
        throw new Error(`Invalid ViewPreset requested: ${input}`);
      }
    } // An instance.
    // If an existing id is used, this will replace it.

    return presets.add(preset)[0];
  }

  changeViewPreset(viewPreset, oldViewPreset) {
    const me = this; // Throw on use of expired deprecated usage.

    if (!(viewPreset instanceof ViewPreset) && viewPreset.name && !viewPreset.base) {
      throw new Error('ViewPreset name config replaced by base config. See https://www.bryntum.com/docs/scheduler/#guides/upgrades/3.0.0.md');
    }

    viewPreset = me.normalizePreset(viewPreset);
    const lastOpts = me.lastViewPresetOptions || {},
          options = viewPreset.options || (viewPreset.options = {}),
          event = options.event = {
      startDate: options.startDate,
      endDate: options.endDate,
      from: oldViewPreset,
      to: viewPreset,
      preset: viewPreset
    },
          presetChanged = !me._viewPreset || !me._viewPreset.equals(viewPreset),
          optionsChanged = datesDiffer(options.startDate, lastOpts.startDate) || datesDiffer(options.endDate, lastOpts.endDate) || datesDiffer(options.centerDate, lastOpts.centerDate) || options.startDate && datesDiffer(options.startDate, me.startDate) || options.endDate && datesDiffer(options.endDate, me.endDate); // Only return the value for onward processing if there's a change

    if (presetChanged || optionsChanged) {
      // Bypass the no-change check if the viewPreset is the same and we only got in here
      // because different options were asked for.
      if (!presetChanged) {
        me._viewPreset = null;
      }
      /**
       * Fired before the {@link #config-viewPreset} is changed.
       * @event beforePresetChange
       * @param {Scheduler.view.Scheduler} source This Scheduler instance.
       * @param {Date} startDate The new start date of the timeline.
       * @param {Date} endDate The new end date of the timeline.
       * @param {Scheduler.preset.ViewPreset} from The outgoing ViewPreset.
       * @param {Scheduler.preset.ViewPreset} to The ViewPreset being switched to.
       * @preventable
       */
      // Do not trigger events for the initial preset

      if (me.isConfiguring || me.trigger('beforePresetChange', event) !== false) {
        return viewPreset;
      }
    }
  }

  updateViewPreset(preset) {
    const me = this,
          {
      options
    } = preset,
          {
      event,
      startDate,
      endDate
    } = options,
          {
      isHorizontal,
      _timeAxis: timeAxis // Do not tickle the getter, we are just peeking to see if it's there yet.

    } = me;
    let {
      centerDate
    } = options; // Raise flag to prevent partner from changing view preset if one is in progress

    me._viewPresetChanging = true; // prefer to use displayDateFormat configured on the panel

    me.displayDateFormat = me.config.displayDateFormat || preset.displayDateFormat;

    if (timeAxis && !me.isConfiguring) {
      // Cache options only when they are applied so that non-change vetoing in changeViewPreset is accurate
      me.lastViewPresetOptions = options; // Timeaxis may already be configured (in case of sharing with the timeline partner), no need to reconfigure it

      if (timeAxis.isConfigured) {
        // None of this reconfiguring should cause a refresh
        me.suspendRefresh();
        const timeAxisCfg = {
          weekStartDay: me.weekStartDay,
          startTime: me.startTime,
          endTime: me.endTime
        }; // if startDate is provided we use it and the provided endDate

        if (startDate) {
          timeAxisCfg.startDate = startDate;
          timeAxisCfg.endDate = endDate; // if both dates are provided we can calculate centerDate for the viewport

          if (!centerDate && endDate) {
            // TODO: PORT infitieScroll stuff
            // if (me.infiniteScroll && view.cachedScrollDate && view.cachedScrollDateIsCentered) {
            //     centerDate = view.cachedScrollDate;
            // } else {
            centerDate = new Date((startDate.getTime() + endDate.getTime()) / 2); //}
          } // when no start/end dates are provided we use the current timespan

        } else {
          timeAxisCfg.startDate = timeAxis.startDate;
          timeAxisCfg.endDate = endDate || timeAxis.endDate;

          if (!centerDate) {
            // TODO: PORT inifiteScroll stuff
            // if (me.infiniteScroll && view.cachedScrollDate && view.cachedScrollDateIsCentered) {
            //     centerDate = view.cachedScrollDate;
            // } else {
            centerDate = me.viewportCenterDate;
          }
        }

        timeAxis.isConfigured = false;
        timeAxis.viewPreset = preset;
        timeAxis.reconfigure(timeAxisCfg, true);
        me.timeAxisViewModel.reconfigure({
          viewPreset: me.viewPreset,
          headers: preset.headers,
          // This was hardcoded to 'middle' prior to the Preset refactor.
          // In the old code, the default headers were 'top' and 'middle', which
          // meant that 'middle' meant the lowest header.
          // So this is now length - 1.
          columnLinesFor: preset.columnLinesFor != null ? preset.columnLinesFor : preset.headers.length - 1,
          // TODO: PORT rowHeightHorizontal??
          // rowHeightHorizontal : me.readRowHeightFromPreset ? preset.rowHeight : me.rowHeight,
          tickSize: isHorizontal ? preset.tickWidth : preset.tickHeight || preset.tickWidth || 60
        }); // Allow refresh to run after the reconfiguring

        me.resumeRefresh();
      }

      me.refresh(); // if view is rendered and scroll is not disabled by "notScroll" option

      if (!options.notScroll && me.isPainted) {
        // and we have centerDate to scroll to
        if (centerDate) {
          // remember the central date we scroll to (it gets reset after user scroll)
          me.cachedCenterDate = centerDate; // setViewPreset method on a partner panels should be executed with same arguments.
          // if one partner was provided with a centerDate, other one has to be too to generate exact
          // header and set same scroll

          event.centerDate = centerDate;
          const viewSize = me.isHorizontal ? me.timeAxisSubGrid.width : me.timeAxisSubGrid.height,
                coord = Math.max(Math.floor(me.getCoordinateFromDate(centerDate, true) - viewSize / 2), 0);
          me.viewPresetActiveScroll = me.isHorizontal ? {
            left: coord
          } : {
            top: coord
          }; // The horizontal scroll handler must not invalidate the cached center
          // when this scroll event rolls round on the next frame.

          me.scrollingToCenter = true;

          if (me.isHorizontal) {
            me.scrollHorizontallyTo(coord, false);
          } else {
            me.scrollVerticallyTo(coord, false);
          } // Release the lock on scrolling invalidating the cached center.

          me.setTimeout(() => {
            me.scrollingToCenter = false;
          }, 100); // if we don't have a central date to scroll at we reset scroll (this is bw compatible behavior)
        } else {
          if (me.isHorizontal) {
            me.scrollHorizontallyTo(0, false);
          } else {
            me.scrollVerticallyTo(0, false);
          }
        }
      }
    }
    /**
     * Fired after the {@link #config-viewPreset} has changed.
     * @event presetChange
     * @param {Scheduler.view.Scheduler} source This Scheduler instance.
     * @param {Date} startDate The new start date of the timeline.
     * @param {Date} centerDate The new center date of the timeline.
     * @param {Date} endDate The new end date of the timeline.
     * @param {Scheduler.preset.ViewPreset} from The outgoing ViewPreset.
     * @param {Scheduler.preset.ViewPreset} to The ViewPreset being switched to.
     * @preventable
     */

    me.trigger('presetChange', event);
    me._viewPresetChanging = false;
  } //endregion
  // This does not need a className on Widgets.
  // Each *Class* which doesn't need 'b-' + constructor.name.toLowerCase() automatically adding
  // to the Widget it's mixed in to should implement thus.

  get widgetClass() {}

});

/**
 * @module Scheduler/view/mixin/TimelineZoomable
 */

/**
 * Mixin providing "zooming" functionality.
 *
 * The zoom levels are stored as instances of {@link Scheduler.preset.ViewPreset ViewPreset}s, and are
 * cached centrally in the {@link Scheduler.preset.PresetManager PresetManager}.
 *
 * The default presets are loaded into the {@link Scheduler.view.mixin.TimelineViewPresets#config-presets}
 * store upon Scheduler instantiation. Preset selection is covered in the
 * {@link Scheduler.view.mixin.TimelineViewPresets TimelineViewPresets} mixin.
 *
 * To specify custom zoom levels please provide a set of view presets to the global PresetManager store **before** scheduler creation,
 * or provide a set of view presets to a specific scheduler only:
 *
 * ```javascript
 * const myScheduler = new Scheduler({
 *     presets : [
 *         {
 *             base : 'hourAndDay',
 *             id   : 'MyHourAndDay',
 *             // other preset configs....
 *         },
 *         {
 *             base : 'weekAndMonth',
 *             id   : 'MyWeekAndMonth',
 *             // other preset configs....
 *         }
 *     ],
 *     viewPreset : 'MyHourAndDay',
 *     // other scheduler configs....
 *     });
 * ```
 *
 * @mixin
 */

var TimelineZoomable = (Target => class TimelineZoomable extends (Target || Base) {
  static get $name() {
    return 'TimelineZoomable';
  }

  static get defaultConfig() {
    return {
      /**
       * If true, you can zoom in and out on the the time axis using CTRL-key + mouse wheel.
       * @config {Boolean}
       * @default
       * @category Zoom
       */
      zoomOnMouseWheel: true,

      /**
       * True to zoom to time span when double clicking a time axis cell.
       * @config {Boolean}
       * @default
       * @category Zoom
       */
      zoomOnTimeAxisDoubleClick: true,
      preventScrollZoom: null,

      /**
       * Minimal zoom level to which {@link #function-zoomOut} will work
       * @config {Number}
       * @category Zoom
       */
      minZoomLevel: null,

      /**
       * Maximal zoom level to which {@link #function-zoomIn} will work
       * @config {Number}
       * @category Zoom
       */
      maxZoomLevel: null,

      /**
       * Integer number indicating the size of timespan during zooming. When zooming, the timespan is adjusted to make the scrolling area `visibleZoomFactor` times
       * wider than the timeline area itself. Used in {@link #function-zoomToSpan} and {@link #function-zoomToLevel} functions.
       * @config {Number}
       * @default
       * @category Zoom
       */
      visibleZoomFactor: 5,

      /**
       * Whether the originally rendered timespan should be preserved while zooming. By default it is set to `false`,
       * meaning the timeline panel will adjust the currently rendered timespan to limit the amount of HTML content to render. When setting this option
       * to `true`, be careful not to allow to zoom a big timespan in seconds resolution for example. That will cause **a lot** of HTML content
       * to be rendered and affect performance. You can use {@link #config-minZoomLevel} and {@link #config-maxZoomLevel} config options for that.
       * @config {Boolean}
       * @default
       * @category Zoom
       */
      zoomKeepsOriginalTimespan: null
    };
  }

  construct(config) {
    const me = this;
    super.construct(config);

    if (me.zoomOnMouseWheel) {
      EventHelper.on({
        element: me.timeAxisSubGridElement,
        wheel: 'onWheel',
        // Throttle zooming with the wheel a bit to have greater control of it
        throttled: {
          buffer: 100,

          // Prevent events from slipping through the throttle, causing scroll
          alt(event) {
            if (event.ctrlKey) {
              event.preventDefault();
            }
          }

        },
        thisObj: me,
        capture: true,
        passive: false
      });
    }

    if (me.zoomOnTimeAxisDoubleClick) {
      me.on('timeaxisheaderdblclick', ({
        startDate,
        endDate
      }) => {
        if (!me.isVertical) {
          me.zoomToSpan({
            startDate,
            endDate
          });
        }
      });
    }
  }

  get maxZoomLevel() {
    return this._maxZoomLevel || this.presets.count - 1;
  }
  /**
   * Get/set the {@link #config-maxZoomLevel} value
   * @property {Number}
   * @category Zoom
   */

  set maxZoomLevel(level) {
    if (typeof level !== 'number') {
      level = this.presets.count - 1;
    }

    if (level < 0 || level >= this.presets.count) {
      throw new Error('Invalid range for `maxZoomLevel`');
    }

    this._maxZoomLevel = level;
  }

  get minZoomLevel() {
    return this._minZoomLevel || 0;
  }
  /**
   * Sets the {@link #config-minZoomLevel} value
   * @property {Number}
   * @category Zoom
   */

  set minZoomLevel(level) {
    if (typeof level !== 'number') {
      level = 0;
    }

    if (level < 0 || level >= this.presets.count) {
      throw new Error('Invalid range for `minZoomLevel`');
    }

    this._minZoomLevel = level;
  }
  /**
   * Current zoom level, which is equal to the {@link Scheduler.preset.ViewPreset ViewPreset} index
   * in the provided array of {@link Scheduler.view.mixin.TimelineViewPresets#config-presets zoom levels}.
   * @property {Number}
   * @category Zoom
   */

  get zoomLevel() {
    return this.presets.indexOf(this.viewPreset);
  } // noinspection JSAnnotator

  set zoomLevel(level) {
    this.zoomToLevel(level);
  }
  /*
   * @private
   * Returns number of milliseconds per pixel.
   * @param {Object} level Element from array of {@link Scheduler.view.mixin.TimelineViewPresets#config-presets}.
   * @param {Boolean} ignoreActualWidth If true, then density will be calculated using default zoom level settings.
   * Otherwise density will be calculated for actual tick width.
   * @return {Number} Return number of milliseconds per pixel.
   */

  getMilliSecondsPerPixelForZoomLevel(preset, ignoreActualWidth) {
    const {
      bottomHeader
    } = preset,
          width = this.isHorizontal ? preset.tickWidth : preset.tickHeight; // trying to convert the unit + increment to a number of milliseconds
    // this number is not fixed (month can be 28, 30 or 31 day), but at least this conversion
    // will be consistent (should be no DST changes at year 1)

    return Math.round((DateHelper.add(new Date(1, 0, 1), bottomHeader.increment || 1, bottomHeader.unit) - new Date(1, 0, 1)) / ( // `actualWidth` is a column width after view adjustments applied to it (see `calculateTickWidth`)
    // we use it if available to return the precise index value from `getCurrentZoomLevelIndex`
    ignoreActualWidth ? width : preset.actualWidth || width));
  }
  /**
   * Zooms to passed view preset, saving center date. Method accepts config object as a first argument, which can be
   * reduced to primitive type (string,number) when no additional options required. e.g.:
   * ```
   * // zooming to preset
   * scheduler.zoomTo({ preset : 'hourAndDay' })
   * // shorthand
   * scheduler.zoomTo('hourAndDay')
   *
   * // zooming to level
   * scheduler.zoomTo({ level : 0 })
   * // shorthand
   * scheduler.zoomTo(0)
   * ```
   *
   * It is also possible to zoom to a time span by omitting `preset` and `level` configs, in which case scheduler sets
   * the time frame to a specified range and applies zoom level which allows to fit all columns to this range. The
   * given time span will be centered in the scheduling view (unless `centerDate` config provided). In the same time,
   * the start/end date of the whole time axis will be extended to allow scrolling for user.
   * ```
   * // zooming to time span
   * scheduler.zoomTo({ startDate : new Date(..), endDate : new Date(...) })
   *
   * ```
   *
   * @param {Object|String|Number} config Config object, preset name or zoom level index.
   * @param {String} config.preset Preset name to zoom to. Ignores level config in this case
   * @param {Number} config.level Zoom level to zoom to. Is ignored, if preset config is provided
   * @param {Date} config.startDate New time frame start. If provided along with end, view will be centered in this time
   * interval (unless `centerDate` is present)
   * @param {Date} config.endDate New time frame end
   * @param {Date} config.centerDate Date that should be kept in the center. Has priority over start and end params
   * @param {Number} config.width Lowest tick width. Might be increased automatically
   * @param {Number} [config.leftMargin] Amount of pixels to extend span start on (used, when zooming to span)
   * @param {Number} [config.rightMargin] Amount of pixels to extend span end on (used, when zooming to span)
   * @param {Number} [config.adjustStart] Amount of units to extend span start on (used, when zooming to span)
   * @param {Number} [config.adjustEnd] Amount of units to extend span end on (used, when zooming to span)
   * @category Zoom
   */

  zoomTo(config) {
    const me = this;

    if (typeof config === 'object') {
      if (config.preset) {
        me.zoomToLevel(config.preset, config);
      } else if (config.level != null) {
        me.zoomToLevel(config.level, config);
      } else {
        me.zoomToSpan(config);
      }
    } else {
      me.zoomToLevel(config);
    }
  }
  /**
   * Allows zooming to certain level of {@link Scheduler.view.mixin.TimelineViewPresets#config-presets} array. Automatically limits zooming between {@link #config-maxZoomLevel}
   * and {@link #config-minZoomLevel}. Can also set time axis timespan to the supplied start and end dates.
   *
   * @param {Number} preset Level to zoom to.
   * @param {Object} [options] Object, containing options for this method
   * @param {Date} options.startDate New time frame start. If provided along with end, view will be centered in this time
   * interval, ignoring centerDate config.
   * @param {Date} options.endDate New time frame end.
   * @param {Date} options.centerDate Date that should be kept in center. Is ignored when start and end are provided.
   * @param {Number} options.width Lowest tick width. Might be increased automatically
   * @return {Number} level Current zoom level or null if it hasn't changed.
   * @category Zoom
   */

  zoomToLevel(preset, options = {}) {
    // Sanitize numeric zooming.
    if (typeof preset === 'number') {
      preset = Math.min(Math.max(preset, this.minZoomLevel), this.maxZoomLevel);
    }

    const me = this,
          tickSizeProp = me.isVertical ? 'tickHeight' : 'tickWidth',
          newPreset = me.normalizePreset(preset),
          configuredTickSize = newPreset[tickSizeProp],
          startDate = options.startDate ? new Date(options.startDate) : null,
          endDate = options.endDate ? new Date(options.endDate) : null;
    let span = startDate && endDate ? {
      startDate,
      endDate
    } : null;
    const centerDate = options.centerDate || (span ? new Date((startDate.getTime() + endDate.getTime()) / 2) : me.viewportCenterDateCached),
          timeAxisSubGridWidth = me.timeAxisSubGrid.width;

    if (timeAxisSubGridWidth === 0) {
      return null;
    } // Always calculate an optimal date range for the new zoom level

    if (!span) {
      span = me.calculateOptimalDateRange(centerDate, timeAxisSubGridWidth, newPreset);
    } // Temporarily override tick size while reconfiguring the TimeAxisViewModel

    if ('width' in options) {
      newPreset.setData(tickSizeProp, options.width);
    }

    me.isZooming = true; // Passed through to the viewPreset changing method

    newPreset.options = {
      startDate: span.startDate || me.startDate,
      endDate: span.endDate || me.endDate,
      centerDate
    };
    me.viewPreset = newPreset; // after switching the view preset the `width` config of the zoom level may change, because of adjustments
    // we will save the real value in the `actualWidth` property, so that `getCurrentZoomLevelIndex` method
    // will return the exact level index after zooming

    newPreset.actualWidth = me.timeAxisViewModel.tickSize;
    me.isZooming = false; // Restore the tick size because the default presets are shared.

    newPreset.setData(tickSizeProp, configuredTickSize);
    return me.zoomLevel;
  }
  /**
   * Changes the range of the scheduling chart to fit all the events in its event store.
   * @param {Object} [options] Options object for the zooming operation.
   * @param {Number} [options.leftMargin] Defines margin in pixel between the first event start date and first visible date
   * @param {Number} [options.rightMargin] Defines margin in pixel between the last event end date and last visible date
   */

  zoomToFit(options) {
    const eventStore = this.eventStore,
          span = eventStore.getTotalTimeSpan();
    options = Object.assign({
      leftMargin: 0,
      rightMargin: 0
    }, options, span); // Make sure we received a time span, event store might be empty

    if (options.startDate && options.endDate) {
      this.zoomToSpan(options);
    }
  }
  /**
   * Sets time frame to specified range and applies zoom level which allows to fit all columns to this range.
   *
   * The given time span will be centered in the scheduling view, in the same time, the start/end date of the whole time axis
   * will be extended in the same way as {@link #function-zoomToLevel} method does, to allow scrolling for user.
   *
   * @param {Object} config The time frame.
   * @param {Date} config.startDate The time frame start.
   * @param {Date} config.endDate The time frame end.
   * @param {Date} [config.centerDate] Date that should be kept in the center. Has priority over start and end params
   * @param {Number} [config.leftMargin] Amount of pixels to extend span start on
   * @param {Number} [config.rightMargin] Amount of pixels to extend span end on
   * @param {Number} [config.adjustStart] Amount of units to extend span start on
   * @param {Number} [config.adjustEnd] Amount of units to extend span end on
   *
   * @return {Number} level Current zoom level or null if it hasn't changed.
   * @category Zoom
   */

  zoomToSpan(config = {}) {
    if (config.leftMargin || config.rightMargin) {
      config.adjustStart = 0;
      config.adjustEnd = 0;
    }

    if (!config.leftMargin) config.leftMargin = 0;
    if (!config.rightMargin) config.rightMargin = 0;
    if (!config.startDate || !config.endDate) throw new Error('zoomToSpan: must provide startDate + endDate dates');
    const me = this,
          // this config enables old zoomToSpan behavior which we want to use for zoomToFit in Gantt
    needToAdjust = config.adjustStart >= 0 || config.adjustEnd >= 0;
    let {
      startDate,
      endDate
    } = config;

    if (needToAdjust) {
      startDate = DateHelper.add(startDate, -config.adjustStart, me.timeAxis.mainUnit);
      endDate = DateHelper.add(endDate, config.adjustEnd, me.timeAxis.mainUnit);
    }

    if (startDate <= endDate) {
      // get scheduling view width
      const {
        availableSpace
      } = me.timeAxisViewModel,
            presets = me.presets.allRecords,
            diffMS = endDate - startDate || 1; // if potential width of col is less than col width provided by zoom level
      //   - we'll zoom out panel until col width fit into width from zoom level
      // and if width of column is more than width from zoom level
      //   - we'll zoom in until col width fit won't fit into width from zoom level

      let currLevel = me.zoomLevel,
          inc,
          range; // if we zoomed out even more than the highest zoom level - limit it to the highest zoom level

      if (currLevel === -1) currLevel = 0;
      let msPerPixel = me.getMilliSecondsPerPixelForZoomLevel(presets[currLevel], true),
          // increment to get next zoom level:
      // -1 means that given timespan won't fit the available width in the current zoom level, we need to zoom out,
      // so that more content will "fit" into 1 px
      //
      // +1 mean that given timespan will already fit into available width in the current zoom level, but,
      // perhaps if we'll zoom in a bit more, the fitting will be better
      candidateLevel = currLevel + (inc = diffMS / msPerPixel + config.leftMargin + config.rightMargin > availableSpace ? -1 : 1),
          zoomLevel,
          levelToZoom = null; // loop over zoom levels

      while (candidateLevel >= 0 && candidateLevel <= presets.length - 1) {
        // get zoom level
        zoomLevel = presets[candidateLevel];
        msPerPixel = me.getMilliSecondsPerPixelForZoomLevel(zoomLevel, true);
        const spanWidth = diffMS / msPerPixel + config.leftMargin + config.rightMargin; // if zooming out

        if (inc === -1) {
          // if columns fit into available space, then all is fine, we've found appropriate zoom level
          if (spanWidth <= availableSpace) {
            levelToZoom = candidateLevel; // stop searching

            break;
          } // if zooming in

        } else {
          // if columns still fits into available space, we need to remember the candidate zoom level as a potential
          // resulting zoom level, the indication that we've found correct zoom level will be that timespan won't fit
          // into available view
          if (spanWidth <= availableSpace) {
            // if it's not currently active level
            if (currLevel !== candidateLevel - inc) {
              // remember this level as applicable
              levelToZoom = candidateLevel;
            }
          } else {
            // Sanity check to find the following case:
            // If we're already zoomed in at the appropriate level, but the current zoomLevel is "too small" to fit and had to be expanded,
            // there is an edge case where we should actually just stop and use the currently selected zoomLevel
            break;
          }
        }

        candidateLevel += inc;
      } // If we didn't find a large/small enough zoom level, use the lowest/highest level

      levelToZoom = levelToZoom != null ? levelToZoom : candidateLevel - inc; // presets is the array of all ViewPresets this Scheduler is using

      zoomLevel = presets[levelToZoom];
      const unitToZoom = zoomLevel.bottomHeader.unit;

      if (config.leftMargin || config.rightMargin) {
        // time axis doesn't yet know about new view preset (zoom level) so it cannot round/ceil date correctly
        startDate = new Date(startDate.getTime() - msPerPixel * config.leftMargin);
        endDate = new Date(endDate.getTime() + msPerPixel * config.rightMargin);
      }

      const tickCount = DateHelper.getDurationInUnit(startDate, endDate, unitToZoom, true) / zoomLevel.bottomHeader.increment;

      if (tickCount === 0) {
        return null;
      }

      const customWidth = Math.floor(availableSpace / tickCount),
            centerDate = config.centerDate || new Date((startDate.getTime() + endDate.getTime()) / 2);

      if (needToAdjust) {
        range = {
          startDate,
          endDate
        };
      } else {
        range = me.calculateOptimalDateRange(centerDate, availableSpace, zoomLevel);
      }

      return me.zoomToLevel(levelToZoom, Object.assign(range, {
        width: customWidth,
        centerDate
      }));
    }

    return null;
  }
  /**
   * Zooms in the timeline according to the array of zoom levels. If the amount of levels to zoom is given, the view will zoom in by this value.
   * Otherwise a value of `1` will be used.
   *
   * @param {Number} levels (optional) amount of levels to zoom in
   *
   * @return {Number} currentLevel New zoom level of the panel or null if level hasn't changed.
   * @category Zoom
   */

  zoomIn(levels = 1) {
    const currentZoomLevelIndex = this.zoomLevel;
    if (currentZoomLevelIndex >= this.maxZoomLevel) return null;
    return this.zoomToLevel(currentZoomLevelIndex + levels);
  }
  /**
   * Zooms out the timeline according to the array of zoom levels. If the amount of levels to zoom is given, the view will zoom out by this value.
   * Otherwise a value of `1` will be used.
   *
   * @param {Number} levels (optional) amount of levels to zoom out
   *
   * @return {Number} currentLevel New zoom level of the panel or null if level hasn't changed.
   * @category Zoom
   */

  zoomOut(levels = 1) {
    const currentZoomLevelIndex = this.zoomLevel;
    if (currentZoomLevelIndex <= this.minZoomLevel) return null;
    return this.zoomToLevel(currentZoomLevelIndex - levels);
  }
  /**
   * Zooms in the timeline to the {@link #config-maxZoomLevel} according to the array of zoom levels.
   *
   * @return {Number} currentLevel New zoom level of the panel or null if level hasn't changed.
   * @category Zoom
   */

  zoomInFull() {
    return this.zoomToLevel(this.maxZoomLevel);
  }
  /**
   * Zooms out the timeline to the {@link #config-minZoomLevel} according to the array of zoom levels.
   *
   * @return {Number} currentLevel New zoom level of the panel or null if level hasn't changed.
   * @category Zoom
   */

  zoomOutFull() {
    return this.zoomToLevel(this.minZoomLevel);
  }
  /*
   * Adjusts the timespan of the panel to the new zoom level. Used for performance reasons,
   * as rendering too many columns takes noticeable amount of time so their number is limited.
   * @category Zoom
   * @private
   */

  calculateOptimalDateRange(centerDate, panelSize, viewPreset, userProvidedSpan) {
    // this line allows us to always use the `calculateOptimalDateRange` method when calculating date range for zooming
    // (even in case when user has provided own interval)
    // other methods may override/hook into `calculateOptimalDateRange` to insert own processing
    // (infinite scrolling feature does)
    if (userProvidedSpan) return userProvidedSpan;
    const me = this,
          {
      timeAxis
    } = me,
          {
      bottomHeader
    } = viewPreset,
          tickWidth = me.isHorizontal ? viewPreset.tickWidth : viewPreset.tickHeight;

    if (me.zoomKeepsOriginalTimespan) {
      return {
        startDate: timeAxis.startDate,
        endDate: timeAxis.endDate
      };
    }

    const unit = bottomHeader.unit,
          difference = Math.ceil(panelSize / tickWidth * bottomHeader.increment * me.visibleZoomFactor / 2),
          startDate = DateHelper.add(centerDate, -difference, unit),
          endDate = DateHelper.add(centerDate, difference, unit);
    return {
      startDate: timeAxis.floorDate(startDate, false, unit, bottomHeader.increment),
      endDate: timeAxis.ceilDate(endDate, false, unit, bottomHeader.increment)
    };
  }

  onWheel(event) {
    const me = this;

    if (event.ctrlKey) {
      event.preventDefault();

      if (!me.preventScrollZoom) {
        if (event.deltaY > 0) {
          me.zoomOut();
        } else if (event.deltaY < 0) {
          me.zoomIn();
        }

        me.preventScrollZoom = true;
        me.setTimeout(() => me.preventScrollZoom = false, 30);
      }
    }
  }
  /**
   * Changes the time axis timespan to the supplied start and end dates.
   * @param {Date} startDate The new start date
   * @param {Date} endDate The new end date. If not supplied, the {@link Scheduler.preset.ViewPreset#field-defaultSpan} property of the current view preset will be used to calculate the new end date.
   */

  setTimeSpan(startDate, endDate) {
    this.timeAxis.setTimeSpan(startDate, endDate);
  }
  /**
   * Moves the time axis by the passed amount and unit.
   *
   * NOTE: If using a filtered time axis, see {@link Scheduler.data.TimeAxis#function-shift} for more information.
   *
   * @param {Number} amount The number of units to jump
   * @param {String} [unit] The unit (Day, Week etc)
   */

  shift(amount, unit) {
    this.timeAxis.shift(amount, unit);
  }
  /**
   * Moves the time axis forward in time in units specified by the view preset `shiftUnit`, and by the amount specified by the `shiftIncrement`
   * config of the current view preset.
   *
   * NOTE: If using a filtered time axis, see {@link Scheduler.data.TimeAxis#function-shiftNext} for more information.
   *
   * @param {Number} [amount] The number of units to jump forward
   */

  shiftNext(amount) {
    this.timeAxis.shiftNext(amount);
  }
  /**
   * Moves the time axis backward in time in units specified by the view preset `shiftUnit`, and by the amount specified by the `shiftIncrement` config of the current view preset.
   *
   * NOTE: If using a filtered time axis, see {@link Scheduler.data.TimeAxis#function-shiftPrevious} for more information.
   *
   * @param {Number} [amount] The number of units to jump backward
   */

  shiftPrevious(amount) {
    this.timeAxis.shiftPrevious(amount);
  } // This does not need a className on Widgets.
  // Each *Class* which doesn't need 'b-' + constructor.name.toLowerCase() automatically adding
  // to the Widget it's mixed in to should implement thus.

  get widgetClass() {}

});

/**
 * @module Scheduler/view/mixin/RecurringEvents
 */

/**
 * A mixin that adds recurring events functionality to the Scheduler.
 *
 * The main purpose of the code in here is displaying  a {@link Scheduler.view.recurrence.RecurrenceConfirmationPopup special confirmation}
 * on user mouse dragging/resizing/deleting recurring events and their occurrences.
 *
 * @mixin
 */

var RecurringEvents = (Target => class RecurringEvents extends (Target || Base) {
  static get $name() {
    return 'RecurringEvents';
  }

  static get configurable() {
    return {
      /**
       * Enables showing occurrences of recurring events across the scheduler's time axis.
       *
       * Enables extra recurrence UI fields in the system-provided event editor.
       * @config {Boolean}
       * @default false
       * @category Scheduled events
       */
      enableRecurringEvents: false,
      recurrenceConfirmationPopup: {
        $config: ['lazy'],
        value: {
          type: 'recurrenceconfirmation'
        }
      }
    };
  }

  construct(config) {
    super.construct(config);
    this.on({
      beforeeventdropfinalize: 'onBeforeEventDropFinalize',
      beforeeventresizefinalize: 'onBeforeEventResizeFinalize',
      beforeEventDelete: 'onRecurrableEventBeforeDelete',
      beforeAssignmentDelete: 'onRecurrableAssignmentBeforeDelete',
      thisObj: this
    });
  }

  changeRecurrenceConfirmationPopup(recurrenceConfirmationPopup, oldRecurrenceConfirmationPopup) {
    // Widget.reconfigure reither reconfigures an existing instance, or creates a new one, or,
    // if the configuration is null, destroys the existing instance.
    const result = this.constructor.reconfigure(oldRecurrenceConfirmationPopup, recurrenceConfirmationPopup, 'recurrenceconfirmation');
    result.owner = this;
    return result;
  }

  showDisplayConfirmationOnEventDelete(eventRecord) {
    const {
      eventEdit
    } = this.features; // show confirmation if we deal with a recurring event (or its occurrence)
    // and if the record is not being edited by event editor (since event editor has its own confirmation)

    return eventRecord.supportsRecurring && (eventRecord.isRecurring || eventRecord.isOccurrence) && (!eventEdit || !eventEdit.isEditing || eventEdit.eventRecord !== eventRecord);
  }

  onRecurrableEventBeforeDelete({
    eventRecords,
    context
  }) {
    const [eventRecord] = eventRecords;

    if (this.enableRecurringEvents && this.showDisplayConfirmationOnEventDelete(eventRecord)) {
      this.recurrenceConfirmationPopup.confirm({
        actionType: 'delete',
        eventRecord,

        changerFn() {
          context.finalize(true);
        },

        cancelFn() {
          context.finalize(false);
        }

      });
      return false;
    }
  }

  onRecurrableAssignmentBeforeDelete({
    assignmentRecords,
    context
  }) {
    const eventRecord = assignmentRecords[0].event;

    if (this.enableRecurringEvents && this.showDisplayConfirmationOnEventDelete(eventRecord)) {
      this.recurrenceConfirmationPopup.confirm({
        actionType: 'delete',
        eventRecord,

        changerFn() {
          context.finalize(true);
        },

        cancelFn() {
          context.finalize(false);
        }

      });
      return false;
    }
  }

  onBeforeEventDropFinalize({
    context
  }) {
    if (this.enableRecurringEvents) {
      const {
        eventRecords
      } = context,
            recurringEvents = eventRecords.filter(eventRecord => eventRecord.supportsRecurring && (eventRecord.isRecurring || eventRecord.isOccurrence));

      if (recurringEvents.length) {
        context.async = true;
        this.recurrenceConfirmationPopup.confirm({
          actionType: 'update',
          eventRecord: recurringEvents[0],

          changerFn() {
            context.finalize(true);
          },

          cancelFn() {
            context.finalize(false);
          }

        });
      }
    }
  }

  onBeforeEventResizeFinalize({
    context
  }) {
    if (this.enableRecurringEvents) {
      const {
        eventRecord
      } = context,
            isRecurring = eventRecord.supportsRecurring && (eventRecord.isRecurring || eventRecord.isOccurrence);

      if (isRecurring) {
        context.async = true;
        this.recurrenceConfirmationPopup.confirm({
          actionType: 'update',
          eventRecord,

          changerFn() {
            context.finalize(true);
          },

          cancelFn() {
            context.finalize(false);
          }

        });
      }
    }
  }
  /**
   * Returns occurrences of the provided recurring event across the date range of this Scheduler.
   * @param  {Scheduler.model.TimeSpan} recurringEvent Recurring event for which occurrences should be retrieved.
   * @return {Scheduler.model.TimeSpan[]} Array of the provided timespans occurrences.
   *
   * __Empty if the passed event is not recurring, or has no occurrences in the date range.__
   *
   * __If the date range encompasses the start point, the recurring event itself will be the first entry.__
   */

  getOccurrencesFor(recurringEvent) {
    return this.eventStore.getOccurrencesForTimeSpan(recurringEvent, this.timeAxis.startDate, this.timeAxis.endDate);
  } // This does not need a className on Widgets.
  // Each *Class* which doesn't need 'b-' + constructor.name.toLowerCase() automatically adding
  // to the Widget it's mixed in to should implement thus.

  get widgetClass() {}

});

/**
 * @module Scheduler/view/mixin/TimelineEventRendering
 */

/**
 * Functions to handle event rendering (EventModel -> dom elements).
 *
 * @mixin
 */

var TimelineEventRendering = (Target => class TimelineEventRendering extends (Target || Base) {
  static get $name() {
    return 'TimelineEventRendering';
  } //region Default config

  static get defaultConfig() {
    return {
      /**
       * Controls how much space to leave between stacked event bars in px
       * @config {Number}
       * @default
       * @category Scheduled events
       */
      barMargin: 10,
      resourceMargin: null,

      /**
       * True to size events based on the rowHeight and barMargin settings. Set this to false if you want to
       * control height and top properties via CSS instead.
       * @config {Boolean}
       * @default
       * @category Scheduled events
       */
      managedEventSizing: true,

      /**
       * The CSS class added to an event/assignment when it is newly created
       * in the UI and unsynced with the server.
       * @config {String}
       * @default
       * @private
       * @category CSS
       */
      generatedIdCls: 'b-sch-dirty-new',

      /**
       * The CSS class added to an event when it has unsaved modifications
       * @config {String}
       * @default
       * @private
       * @category CSS
       */
      dirtyCls: 'b-sch-dirty',

      /**
       * The CSS class added to an event when it is currently committing changes
       * @config {String}
       * @default
       * @private
       * @category CSS
       */
      committingCls: 'b-sch-committing',

      /**
       * The CSS class added to an event/assignment when it ends outside of the visible time range.
       * @config {String}
       * @default
       * @private
       * @category CSS
       */
      endsOutsideViewCls: 'b-sch-event-endsoutside',

      /**
       * The CSS class added to an event/assignment when it starts outside of the visible time range.
       * @config {String}
       * @default
       * @private
       * @category CSS
       */
      startsOutsideViewCls: 'b-sch-event-startsoutside',

      /**
       * The CSS class added to an event/assignment when it is not draggable.
       * @config {String}
       * @default
       * @private
       * @category CSS
       */
      fixedEventCls: 'b-sch-event-fixed',

      /**
       * Event style used by default. Events and resources can specify their own style, with priority order being:
       * Event -> Resource -> Scheduler default. Determines the appearance of the event by assigning a CSS class
       * to it. Available styles are:
       * * plain (default), flat look
       * * border, has border in darker shade of events color
       * * colored, has colored text and wide left border in same color
       * * hollow, only border + text until hovered
       * * line, as a line with the text below it
       * * dashed, as a dashed line with the text below it
       * * minimal, as a thin line with small text above it
       * * rounded, minimalistic style with rounded corners
       *
       * Specify `null` to not apply a default style and take control using custom CSS (easily overridden basic
       * styling will be used).
       *
       * @config {String}
       * @default
       * @category Scheduled events
       */
      eventStyle: 'plain',

      /**
       * Event color used by default. Events and resources can specify their own color, with priority order being:
       * Event -> Resource -> Scheduler default. Available colors are:
       * * red
       * * pink
       * * purple
       * * violet
       * * indigo
       * * blue
       * * cyan
       * * teal
       * * green
       * * lime
       * * yellow
       * * orange
       * * deep-orange
       * * gray
       * * gantt-green (Useful when you want to match the color to the default color in Gantt)
       *
       * Specify `null` to not apply a default color and take control using custom CSS (an easily overridden color
       * will be used to make sure events are still visible).
       *
       * @config {String}
       * @default
       * @category Scheduled events
       */
      eventColor: 'green',

      /**
       * The width/height (depending on vertical / horizontal mode) of all the time columns.
       * @config {Number}
       * @category Scheduled events
       */
      tickSize: null
    };
  } //endregion
  //region Settings

  /**
   * Control how much space to leave between stacked event bars in px. Value will be constrained by half the row height.
   * @property {Number}
   * @category Scheduled events
   */

  get barMargin() {
    return this._barMargin;
  }

  set barMargin(margin) {
    const me = this;
    ObjectHelper.assertNumber(margin, 'barMargin'); // bar margin should not exceed half of the row height

    if (me.isHorizontal && me.rowHeight) {
      margin = Math.min(Math.ceil(me.rowHeight / 2), margin);
    }

    if (me._barMargin !== margin) {
      me._barMargin = margin;

      if (me.rendered) {
        me.currentOrientation.onBeforeRowHeightChange();
        me.refreshWithTransition();
      }
    }
  } // Documented in SchedulerEventRendering to not show up in Gantt docs

  get resourceMargin() {
    return this._resourceMargin == null ? this.barMargin : this._resourceMargin;
  }

  set resourceMargin(margin) {
    const me = this;
    ObjectHelper.assertNumber(margin, 'resourceMargin'); // bar margin should not exceed half of the row height

    if (me.isHorizontal && me.rowHeight) {
      margin = Math.min(Math.ceil(me.rowHeight / 2), margin);
    }

    if (me._resourceMargin !== margin) {
      me._resourceMargin = margin;

      if (me.rendered) {
        me.currentOrientation.onBeforeRowHeightChange();
        me.refreshWithTransition();
      }
    }
  } // /**
  //  * Distance between bars when using eventLayout pack
  //  * @property {Number}
  //  */
  // get barPackMargin() {
  //     return this._barPackMargin;
  // }
  //
  // set barPackMargin(margin) {
  //     if (this._barPackMargin !== margin) {
  //         this._barPackMargin = margin;
  //         this.refresh();
  //     }
  // }

  /**
   * Get/set the width/height (depending on mode) of all the time columns to the supplied value.
   * There is a limit for the tick size value. Its minimal allowed value is calculated so ticks would fit the available space.
   * Only applicable when {@link Scheduler.view.TimelineBase#config-forceFit} is set to `false`.
   * To set `tickSize` freely skipping that limitation please set {@link Scheduler.view.TimelineBase#config-suppressFit} to `true`.
   * @property {Number}
   * @category Scheduled events
   */

  set tickSize(width) {
    ObjectHelper.assertNumber(width, 'tickSize');
    this.timeAxisViewModel.tickSize = width;
  }

  get tickSize() {
    return this.timeAxisViewModel.tickSize;
  }
  /**
   * Predefined event colors, useful in combos etc.
   * @type {String}
   * @category Scheduled events
   */

  static get eventColors() {
    return ['red', 'pink', 'purple', 'violet', 'indigo', 'blue', 'cyan', 'teal', 'green', 'lime', 'yellow', 'orange', 'deep-orange', 'gray'];
  }
  /**
   * Predefined event styles , useful in combos etc.
   * @type {String}
   * @category Scheduled events
   */

  static get eventStyles() {
    return ['plain', 'border', 'hollow', 'colored', 'line', 'dashed', 'minimal', 'rounded'];
  } //endregion
  // This does not need a className on Widgets.
  // Each *Class* which doesn't need 'b-' + constructor.name.toLowerCase() automatically adding
  // to the Widget it's mixed in to should implement thus.

  get widgetClass() {}

});

/**
 * @module Scheduler/view/mixin/TimelineScroll
 */

/**
 * Functions for scrolling to events, dates etc.
 *
 * @mixin
 */

var TimelineScroll = (Target => class TimelineScroll extends (Target || Base) {
  static get $name() {
    return 'TimelineScroll';
  } //region Scroll to date

  /**
   * Scrolls the time line "tick" encapsulating the passed `Date` into view according to the passed options.
   * @param {Date} date The date to which to scroll the time line
   * @param {Object} [options] How to scroll.
   * @param {String} [options.block=nearest] How far to scroll the tick: `start/end/center/nearest`.
   * @param {Number} [options.edgeOffset=20] edgeOffset A margin *in pixels* around the tick to bring into view.
   * @param {Boolean|Number} [options.animate] Set to `true` to animate the scroll, or the number of milliseconds to animate over.
   * @returns {Promise} A Promise which resolves when the scrolling is complete.
   */

  scrollToDate(date, options = {}) {
    const me = this,
          scroller = me.timeAxisSubGrid.scrollable,
          scrollerViewport = scroller.viewport,
          localCoordinate = me.getCoordinateFromDate(date, true),
          target = me.isHorizontal ? new Rectangle(me.getCoordinateFromDate(date, false), scrollerViewport.y, me.timeAxisViewModel.tickSize, scrollerViewport.height) : new Rectangle(scrollerViewport.x, me.getCoordinateFromDate(date, false), scrollerViewport.width, me.timeAxisViewModel.tickSize);
    return me.scrollToCoordinate(localCoordinate, target, date, options);
  }
  /**
   * Scrolls to current time.
   * @param {Object} [options] How to scroll.
   * @param {String} [options.block=nearest] How far to scroll the tick: `start/end/center/nearest`.
   * @param {Number} [options.edgeOffset=20] edgeOffset A margin *in pixels* around the tick to bring into view.
   * @param {Boolean|Number} [options.animate] Set to `true` to animate the scroll, or the number of milliseconds to animate over.
   * @returns {Promise} A Promise which resolves when the scrolling is complete.
   */

  scrollToNow(options = {}) {
    return this.scrollToDate(new Date(), options);
  }
  /**
   * Used by {@link #function-scrollToDate} to scroll to correct coordinate.
   * @param {Number} localCoordinate Coordinate to scroll to
   * @param {Date} date Date to scroll to, used for reconfiguring the time axis
   * @param {Object} [options] How to scroll.
   * @param {String} [options.block=nearest] How far to scroll the tick: `start/end/center/nearest`.
   * @param {Number} [options.edgeOffset] edgeOffset A margin *in pixels* around the tick to bring into view.
   * @param {Boolean|Number} [options.animate] Set to `true` to animate the scroll, or the number of milliseconds to animate over.
   * @returns {Promise} A Promise which resolves when the scrolling is complete.
   * @private
   */

  async scrollToCoordinate(localCoordinate, target, date, options = {}) {
    const me = this; // Not currently have this date in a timeaxis. Ignore negative scroll in weekview, it can be just 'filtered' with
    // startTime/endTime config

    if (localCoordinate < 0) {
      // adjust the timeaxis first
      const halfVisibleSpan = (me.timeAxis.endDate - me.timeAxis.startDate) / 2,
            {
        unit,
        increment
      } = me.timeAxis,
            newStartDate = DateHelper.snap('floor', new Date(date.getTime() - halfVisibleSpan), increment + ' ' + unit),
            newEndDate = DateHelper.snap('ceil', new Date(date.getTime() + halfVisibleSpan), increment + ' ' + unit); // We're trying to reconfigure time span to current dates, which means we are as close to center as it
      // could be. Do nothing then.
      // covered by 1102_panel_api

      if (newStartDate - me.startDate !== 0 && newEndDate - me.endDate !== 0) {
        me.setTimeSpan(newStartDate, newEndDate);
        return me.scrollToDate(date, options);
      }

      return;
    }

    await me.timeAxisSubGrid.scrollable.scrollIntoView(target, options); // Horizontal scroll is triggered on next frame in SubGrid.js, view is not up to date yet. Resolve on next frame

    return me.nextAnimationFrame();
  } //endregion
  //region Relative scrolling
  // These methods are important to users because although they are mixed into the top level Grid/Scheduler,
  // for X scrolling the explicitly target the SubGrid that holds the scheduler.

  /**
   * Get/set horizontal scroll. Applies to the SubGrid that holds the scheduler
   * @property {Number}
   * @category Scrolling
   */

  set scrollLeft(left) {
    this.timeAxisSubGrid.scrollable.x = left;
  }
  /**
   * Get/set vertical scroll
   * @property {Number}
   * @category Scrolling
   */

  set scrollTop(top) {
    this.scrollable.y = top;
  }

  get scrollLeft() {
    return this.timeAxisSubGrid.scrollable.x;
  }

  get scrollTop() {
    return this.scrollable.y;
  }
  /**
   * Horizontal scrolling. Applies to the SubGrid that holds the scheduler
   * @param {Number} x
   * @param {Object|Boolean} [options] How to scroll. May be passed as `true` to animate.
   * @param {Object|Boolean|Number} [options.animate] Set to `true` to animate the scroll by 300ms,
   * or the number of milliseconds to animate over, or an animation config object.
   * @param {Number} [options.animate.duration] The number of milliseconds to animate over.
   * @param {String} [options.animate.easing] The name of an easing function.
   * @returns {Promise} A promise which is resolved when the scrolling has finished.
   */

  scrollHorizontallyTo(coordinate, options = true) {
    return this.timeAxisSubGrid.scrollable.scrollTo(coordinate, null, options);
  }
  /**
   * Vertical scrolling
   * @param {Number} y
   * @param {Object|Boolean} [options] How to scroll. May be passed as `true` to animate.
   * @param {Object|Boolean|Number} [options.animate] Set to `true` to animate the scroll by 300ms,
   * or the number of milliseconds to animate over, or an animation config object.
   * @param {Number} [options.animate.duration] The number of milliseconds to animate over.
   * @param {String} [options.animate.easing] The name of an easing function.
   * @returns {Promise} A promise which is resolved when the scrolling has finished.
   */

  scrollVerticallyTo(y, options = true) {
    return this.scrollable.scrollTo(null, y, options);
  }
  /**
   * Scrolls the subgrid that contains the scheduler
   * @param {Number} x
   * @param {Object|Boolean} [options] How to scroll. May be passed as `true` to animate.
   * @param {Object|Boolean|Number} [options.animate] Set to `true` to animate the scroll by 300ms,
   * or the number of milliseconds to animate over, or an animation config object.
   * @param {Number} [options.animate.duration] The number of milliseconds to animate over.
   * @param {String} [options.animate.easing] The name of an easing function.
   * @returns {Promise} A promise which is resolved when the scrolling has finished.
   */

  scrollTo(x, options = true) {
    return this.timeAxisSubGrid.scrollable.scrollTo(x, null, options);
  } //endregion
  // This does not need a className on Widgets.
  // Each *Class* which doesn't need 'b-' + constructor.name.toLowerCase() automatically adding
  // to the Widget it's mixed in to should implement thus.

  get widgetClass() {}

});

const timeAxisColumnConfigs = ['viewPreset', 'eventBarTextField', 'eventRenderer', 'eventRendererThisObj', 'eventBodyTemplate'],
      exitTransition = {
  fn: 'exitTransition',
  delay: 0,
  cancelOutstanding: true
};
/**
 * @module Scheduler/view/TimelineBase
 */

/**
 * Abstract base class used by timeline based components such as Scheduler and Gantt. Based on Grid, supplies a "locked"
 * region for columns and a "normal" for rendering of events etc.
 * @abstract
 *
 * @mixes Scheduler/view/mixin/TimelineDateMapper
 * @mixes Scheduler/view/mixin/TimelineDomEvents
 * @mixes Scheduler/view/mixin/TimelineEventRendering
 * @mixes Scheduler/view/mixin/TimelineScroll
 * @mixes Scheduler/view/mixin/TimelineViewPresets
 * @mixes Scheduler/view/mixin/TimelineZoomable
 * @mixes Scheduler/view/mixin/RecurringEvents
 *
 * @extends Grid/view/Grid
 */

class TimelineBase extends GridBase.mixin(TimelineDateMapper, TimelineDomEvents, TimelineEventRendering, TimelineScroll, TimelineViewPresets, TimelineZoomable, RecurringEvents) {
  //region Config
  static get $name() {
    return 'TimelineBase';
  } // Factoryable type name

  static get type() {
    return 'timelinebase';
  }

  static get configurable() {
    return {
      partnerSharedConfigs: {
        value: ['timeAxisViewModel', 'timeAxis', 'viewPreset'],
        $config: {
          merge: 'distinct'
        }
      },

      /**
       * Get/set startDate. Defaults to current date if none specified.
       *
       * **Note:** If you need to set start and end date at the same time, use {@link Scheduler.view.mixin.TimelineZoomable#function-setTimeSpan} method.
       * @member {Date} startDate
       * @category Common
       */

      /**
       * The start date of the timeline. If omitted, and a TimeAxis has been set, the start date of the provided
       * {@link Scheduler.data.TimeAxis} will be used. If no TimeAxis has been configured, it'll use the start/end
       * dates of the loaded event dataset. If no date information exists in the event data set, it defaults to
       * the current date and time.
       *
       * If a string is supplied, it will be parsed using
       * {@link Core/helper/DateHelper#property-defaultFormat-static DateHelper.defaultFormat}
       *
       * **Note:** If you need to set start and end date at the same time, use
       * {@link Scheduler.view.mixin.TimelineZoomable#function-setTimeSpan} method.
       * @config {Date|String}
       * @category Common
       */
      startDate: {
        $config: {
          equal: 'date'
        },
        value: null
      },

      /**
       * Get/set endDate. Defaults to startDate + default span of the used ViewPreset.
       *
       * **Note:** If you need to set start and end date at the same time, use {@link Scheduler.view.mixin.TimelineZoomable#function-setTimeSpan} method.
       * @member {Date} endDate
       * @category Common
       */

      /**
       * The end date of the timeline. If omitted, it will be calculated based on the {@link #config-startDate}
       * setting and the 'defaultSpan' property of the current
       * {@link Scheduler.view.mixin.TimelineViewPresets#config-viewPreset}.
       *
       * If a string is supplied, it will be parsed using
       * {@link Core/helper/DateHelper#property-defaultFormat-static DateHelper.defaultFormat}
       *
       * **Note:** If you need to set start and end date at the same time, use
       * {@link Scheduler.view.mixin.TimelineZoomable#function-setTimeSpan} method.
       * @config {Date|String}
       * @category Common
       */
      endDate: {
        $config: {
          equal: 'date'
        },
        value: null
      },

      /**
       * When set, the text in the major time axis header sticks in the scrolling viewport as long as possible.
       * @config {Boolean}
       * @default
       */
      stickyHeaders: true
    };
  }

  static get defaultConfig() {
    return {
      /**
       * A valid JS day index between 0-6 (0: Sunday, 1: Monday etc.) to be considered the start day of the week.
       * When omitted, the week start day is retrieved from the active locale class.
       * @config {Number} weekStartDay
       * @category Time axis
       */

      /**
       * An object with format `{ fromDay, toDay, fromHour, toHour }` that describes the working days and hours.
       * This object will be used to populate TimeAxis {@link Scheduler.data.TimeAxis#config-include} property.
       *
       * Using it results in a non-continuous time axis. Any ticks not covered by the working days and hours will
       * be excluded. Events within larger ticks (for example if using week as the unit for ticks) will be
       * stretched to fill the gap otherwise left by the non working hours.
       *
       * As with end dates, `toDay` and `toHour` are exclusive. Thus `toDay : 6` means that day 6 (saturday) will
       * not be included.
       *
       *
       * **NOTE:** When this feature is enabled {@link Scheduler.view.mixin.TimelineZoomable Zooming feature} is
       * not supported. It's recommended to disable zooming controls:
       *
       * ```javascript
       * new Scheduler({
       *     zoomOnMouseWheel          : false,
       *     zoomOnTimeAxisDoubleClick : false,
       *     ...
       * });
       * ```
       *
       * @config {Object}
       * @category Time axis
       */
      workingTime: null,

      /**
       * A backing data store of 'ticks' providing the input date data for the time axis of timeline panel.
       * @member {Scheduler.data.TimeAxis} timeAxis
       * @readonly
       * @category Time axis
       */

      /**
       * A {@link Scheduler.data.TimeAxis} config object or instance, used to create a backing data store of
       * 'ticks' providing the input date data for the time axis of timeline panel. Created automatically if none
       * supplied.
       * @config {Object|Scheduler.data.TimeAxis}
       * @category Time axis
       */
      timeAxis: null,

      /**
       * The time axis column config for vertical {@link Scheduler.view.SchedulerBase#config-mode}.
       *
       * Object with {@link Scheduler.column.VerticalTimeAxisColumn} configuration.
       *
       * This object will be used to configure the vertical time axis column instance.
       *
       * The config allows configuring the `VerticalTimeAxisColumn` instance used in vertical mode with any Column options that apply to it.
       *
       * Example:
       *
       * ```javascript
       * new Scheduler({
       *     mode     : 'vertical',
       *     features : {
       *         filterBar : true
       *     },
       *     verticalTimeAxisColumn : {
       *         text  : 'Filter by event name',
       *         width : 180,
       *         filterable : {
       *             // add a filter field to the vertical column access header
       *             filterField : {
       *                 type        : 'text',
       *                 placeholder : 'Type to search',
       *                 onChange    : ({ value }) => {
       *                     // filter event by name converting to lowerCase to be equal comparison
       *                     scheduler.eventStore.filter({
       *                         filters : event => event.name.toLowerCase().includes(value.toLowerCase()),
       *                         replace : true
       *                     });
       *                 }
       *             }
       *         }
       *     },
       *     ...
       * });
       * ```
       *
       * @config {Object}
       * @category Time axis
       */
      verticalTimeAxisColumn: {},

      /**
       * The backing view model for the visual representation of the time axis.
       * Either a real instance or a simple config object.
       * @private
       * @config {Scheduler.view.model.TimeAxisViewModel|Object}
       * @category Time axis
       */
      timeAxisViewModel: null,

      /**
       * You can set this option to `false` to make the timeline panel start and end on the exact provided
       * {@link #config-startDate}/{@link #config-endDate} w/o adjusting them.
       * @config {Boolean}
       * @default
       * @category Time axis
       */
      autoAdjustTimeAxis: true,

      /**
       * A date to bring into view initially on the scrollable timeline. Either a date or an object describing
       * the scroll action, see {Scheduler.view.mixin.TimelineScroll#function-scrollToDate} for reference.
       * @config {Date|Object}
       * @category Common
       */
      visibleDate: null,

      /**
       * true to snap to resolution increment while interacting with scheduled events.
       * @config {Boolean}
       * @default
       * @category Scheduled events
       */
      snap: false,

      /**
       * Affects drag drop and resizing of events when {@link #config-snap} is enabled. If set to `true`, dates
       * will be snapped relative to event start. e.g. for a zoom level with timeResolution = { unit: "s",
       * increment: "20" }, an event that starts at 10:00:03 and is dragged would snap its start date to 10:00:23,
       * 10:00:43 etc. When set to `false`, dates will be snapped relative to the timeAxis startDate (tick start)
       * - 10:00:03, 10:00:20, 10:00:40 etc.
       * @config {Boolean}
       * @default
       * @category Scheduled events
       */
      snapRelativeToEventStartDate: false,

      /**
       * Set to `true` to force the time columns to fit to the available space (horizontal or vertical depends on mode).
       * Note that setting {@link Scheduler.view.TimelineBase#config-suppressFit} on `true`, will disable `forceFit` functionality.
       * @config {Boolean}
       * @default
       * @category Time axis
       */
      forceFit: false,

      /**
       * Set to `true` to prevent auto calculating of a minimal {@link Scheduler.view.mixin.TimelineEventRendering#property-tickSize}
       * to always fit the content to the screen size. Setting this property on `true` will disable {@link Scheduler.view.TimelineBase#config-forceFit} behaviour.
       * @config {Boolean}
       * @default false
       * @category Time axis
       */
      suppressFit: false,

      /**
       * CSS class to add to rendered events
       * @config {String}
       * @category CSS
       * @private
       */
      eventCls: null,

      /**
       * Returns dates that will constrain resize and drag operations. The method will be called with the
       * Resource, and for operations on existing events - the event. For drag create operation, the mousedown
       * date will be passed as the second parameter
       * @return {Object} Constraining object containing `start` and `end` constraints. Omitting either
       * will mean that end is not constrained. So you can prevent a resize or move from moving *before*
       * a certain time while not constraininhg the end date.
       * @return {Date} [return.start] Start date
       * @return {Date} [return.end] End date
       * @config {Function}
       * @category Scheduled events
       */
      getDateConstraints: null,

      /**
       * CSS class to add to cells in the timeaxis column
       * @config {String}
       * @category CSS
       * @private
       */
      timeCellCls: null,
      timeCellSelector: null,
      scheduledEventName: null,

      /**
       * Create event on double click if scheduler is not in read only mode.
       * Set to false to turn creating off.
       * @config {Boolean}
       * @default
       * @category Scheduled events
       */
      createEventOnDblClick: true,
      //dblClickTime : 200,

      /**
       * A CSS class to apply to each event in the view on mouseover.
       * @config {String}
       * @category CSS
       * @private
       */
      overScheduledEventClass: null,
      // allow the panel to prevent adding the hover CSS class in some cases - during drag drop operations
      preventOverCls: false,
      // The last hovered over event bar HTML node
      hoveredEventNode: null,
      // This setting is set to true by features that need it
      useBackgroundCanvas: false,

      /**
       * Set to `false` if you don't want event bar DOM updates to animate. Animations are disabled in IE11 by default.
       * @config {Boolean}
       * @default true
       * @category Scheduled events
       */
      enableEventAnimations: !BrowserHelper.isIE11,
      disableGridRowModelWarning: true,
      // does not look good with locked columns and also interferes with event animations
      animateRemovingRows: false,

      /**
       * Partners this Timeline panel with another Timeline in order to sync their region sizes (sub-grids like locked, normal will get the same width),
       * start and end dates, view preset, zoom level and scrolling position. All these values will be synced with the timeline defined as the `partner`.
       * @config {Scheduler.view.TimelineBase}
       * @category Time axis
       */
      partner: null,
      schedulerRegion: 'normal',
      transitionDuration: 200,
      // internal timer id reference
      animationTimeout: null,

      /**
       * How to handle milestones during event layout. Options are:
       * * default - Milestones do not affect event layout
       * * estimate - Milestone width is estimated by multiplying text length with Scheduler#milestoneCharWidth
       * * data - Milestone width is determined by checking EventModel#milestoneWidth
       * * measure - Milestone width is determined by measuring label width
       * Please note that currently text width is always determined using EventModel#name.
       * Also note that only 'default' is supported by eventStyles line, dashed and minimal.
       * @config {String}
       * @default
       * @category Scheduled events
       */
      milestoneLayoutMode: 'default',

      /**
       * Region to which columns are added when they have none specified
       * @config {string}
       * @default
       * @category Misc
       */
      defaultRegion: 'locked',

      /**
       * Decimal precision used when displaying durations, used by tooltips and DurationColumn.
       * Specify `false` to use raw value
       * @config {Number|Boolean}
       * @default
       * @category Common
       */
      durationDisplayPrecision: 1,
      asyncEventSuffix: 'PreCommit'
    };
  } //endregion
  //region Init

  construct(config = {}) {
    const me = this;
    super.construct(config);
    me.$firstVerticalOverflow = true; // Buffer resize, since resize does full redraw it is costly

    me.onSchedulerViewportResize = me.throttle(me.onSchedulerViewportResize, 250);
    me.initDomEvents();
    me.currentOrientation.init();
    me.rowManager.on('refresh', () => {
      me.forceLayout = false;
    });
  } // Override from Grid.view.GridSubGrids

  createSubGrid(region, config = {}) {
    const me = this,
          {
      stickyHeaders
    } = me; // We are creating the TimeAxisSubGrid

    if (region === (me.schedulerRegion || 'normal')) {
      if (me.isHorizontal) {
        config.header = {
          cls: {
            'b-sticky-headers': stickyHeaders
          }
        };
      } // Use Scheduler's header

      config.headerClass = Header$1; // If user have not specified a width or flex for scheduler region, default to flex=1

      if (!('flex' in config || 'width' in config)) {
        config.flex = 1;
      }
    } // The assumption is that if we are in vertical mode, the locked SubGrid
    // is used to house the verticalTimeAxis, and so it must all be oberflow:visible
    else if (region === 'locked' && stickyHeaders && me.isVertical) {
        config.scrollable = {
          overflowX: 'visible',
          overflowY: 'visible'
        }; // It's the child of the overflowElement

        me.bodyContainer.classList.add('b-sticky-headers');
      }

    return super.createSubGrid(region, config);
  }

  doDestroy() {
    const me = this,
          {
      currentOrientation
    } = this;

    if (currentOrientation) {
      currentOrientation.destroy();
    } // Break links between this TimeLine and any partners.

    if (me.partneredWith) {
      me.partneredWith.forEach(p => {
        me.removePartner(p);
      });
      me.partneredWith.destroy();
    }

    super.doDestroy();
  }

  startConfigure(config) {
    super.startConfigure(config); // partner needs to be initialized first so that the various shared
    // configs are assigned first before we default them in.

    this.getConfig('partner');
  }

  changeStartDate(startDate) {
    if (typeof startDate === 'string') {
      startDate = DateHelper.parse(startDate);
    }

    return startDate;
  }

  refreshVirtualScrollbars() {
    super.refreshVirtualScrollbars();

    if (!this.hasVerticalOverflow) {
      const {
        scrollable
      } = this;

      if (this.partners.some(p => p.hasVerticalOverflow)) {
        scrollable.overflowY = 'scroll';
      } else {
        scrollable.overflowY = true;
      }
    }
  }

  onPaint({
    firstPaint
  }) {
    // Upon first paint we need to pass the forceUpdate flag in case we are sharing the TimAxisViewModel
    // with another Timeline which will already have done this.
    if (firstPaint) {
      // subGrid in IE11 doesn't have height at this point, but container element does so we take it from there
      // TODO: make flex element inherit height from parent (min-height: 100% doesn't work)
      const {
        timeAxisSubGrid
      } = this,
            availableSpace = this.isVertical ? this.bodyContainer.offsetHeight : timeAxisSubGrid.width;
      this.timeAxisViewModel.update(availableSpace, false, true);
    }

    super.onPaint(...arguments);
  }

  changeSubGridConfigs(configs) {
    // Avoid in-flight column width reconfiguration which would end up with DOM updates.
    // Must be done here, because super.changeSubGridConfigs references columns
    // which will invoke our set columns before these configs have been converted
    // to SubGrids.
    if (this.isVertical && configs.locked) {
      this.verticalTimeAxisColumn.width = configs.locked.width;
    }

    super.changeSubGridConfigs(configs);
    const me = this,
          timeAxisSubGrid = me.timeAxisSubGrid = me.subGrids[me.timeAxisColumn.region]; // Scheduler SubGrid doesn't accept external columns moving in

    timeAxisSubGrid.sealedColumns = true;
    timeAxisSubGrid.on({
      resize: me.onTimeAxisSubGridResize,
      thisObj: me
    });
  }

  onSchedulerHorizontalScroll(subGrid, scrollLeft) {
    // rerender cells in scheduler column on horizontal scroll to display events in view
    this.currentOrientation.updateFromHorizontalScroll(scrollLeft);
    super.onSchedulerHorizontalScroll(subGrid, scrollLeft);
  }
  /**
   * Overrides initScroll from Grid, listens for horizontal scroll to do virtual event rendering
   * @private
   */

  initScroll() {
    const me = this;
    let frameCount = 0;
    super.initScroll(); // Fixes scroll freezing bug on iPad by putting scheduler scroller in its own layer

    if (BrowserHelper.isMobileSafari) {
      me.scrollable.element.style.transform = 'translate3d(0, 0, 0)';
    }

    me.on('horizontalscroll', ({
      subGrid,
      scrollLeft
    }) => {
      if (me.isPainted && subGrid === me.timeAxisSubGrid && !me.isDestroying && !me.refreshSuspended) {
        me.onSchedulerHorizontalScroll(subGrid, scrollLeft);
      }

      frameCount++;
    });

    if (me.testPerformance === 'horizontal') {
      me.setTimeout(() => {
        let start = performance.now(),
            // eslint-disable-line no-undef
        scrollSpeed = 5,
            direction = 1;
        const scrollInterval = me.setInterval(() => {
          scrollSpeed = scrollSpeed + 5;
          me.scrollLeft += (10 + Math.floor(scrollSpeed)) * direction;

          if (direction === 1 && me.scrollLeft > 5500) {
            direction = -1;
            scrollSpeed = 5;
          }

          if (direction === -1 && me.scrollLeft <= 0) {
            const done = performance.now(),
                  // eslint-disable-line no-undef
            elapsed = done - start;
            let timePerFrame = elapsed / frameCount,
                fps = 1000 / timePerFrame;
            fps = Math.round(fps * 10) / 10;
            clearInterval(scrollInterval);
            console.log(me.eventPositionMode, me.eventScrollMode, fps + 'fps');
          }
        }, 0);
      }, 500);
    }
  } //endregion

  /**
   * Calls the specified function (returning its return value) and preserves the timeline center
   * point. This is a useful way of retaining the user's visual context while making updates
   * and changes to the view which require major changes or a full refresh.
   * @param {Function} fn The function to call.
   * @param {Object} thisObj The `this` context for the function.
   * @param {...*} args Parameters to the function.
   */

  preserveViewCenter(fn, thisObj = this, ...args) {
    const me = this,
          centerDate = me.viewportCenterDate,
          result = fn.apply(thisObj, args);
    const {
      isVertical
    } = me,
          scroller = isVertical ? me.scrollable : me.timeAxisSubGrid.scrollable,
          clientSize = scroller[isVertical ? 'clientHeight' : 'clientWidth'],
          scrollStart = Math.max(Math.floor(me.getCoordinateFromDate(centerDate, true) - clientSize / 2), 0);
    me.scrollingToCenter = true;
    me[isVertical ? 'scrollVerticallyTo' : 'scrollHorizontallyTo'](scrollStart, false).then(() => me.scrollingToCenter = false);
    return result;
  } //region Config getters/setters

  /**
   * Returns `true` if any of the events/tasks or feature injected elements (such as ResourceTimeRanges) are within
   * the {@link #config-timeAxis}
   * @property {Boolean}
   * @readonly
   */

  get hasVisibleEvents() {
    return !this.noFeatureElementsInAxis() || this.eventStore.storage.values.some(t => this.timeAxis.isTimeSpanInAxis(t));
  } // Template function to be chained in features to determine if any elements are in time axis (needed since we cannot
  // currently chain getters). Negated to not break chain. First feature that has elements visible returns false,
  // which prevents other features from being queried.

  noFeatureElementsInAxis() {} // Private getter used to piece togheter event names such as beforeEventDrag / beforeTaskDrag. Could also be used
  // in templates.

  get capitalizedEventName() {
    if (!this._capitalizedEventName) {
      this._capitalizedEventName = StringHelper.capitalize(this.scheduledEventName);
    }

    return this._capitalizedEventName;
  }

  set partner(partner) {
    this._partner = partner;
    this.addPartner(partner);
  }
  /**
   * Partners this Timeline with the passed timeline in order to sync the horizontal scrolling position and zoom level
   * @param {Scheduler.view.TimelineBase} otherTimeline The timeline to partner with
   */

  addPartner(partner) {
    const me = this;

    if (!me.isPartneredWith(partner)) {
      const partneredWith = me.partneredWith || (me.partneredWith = new Collection()); // Each must know about the other so that they can sync others upon region resize

      partneredWith.add(partner);
      (partner.partneredWith || (partner.partneredWith = new Collection())).add(me); // Flush through viewPreset initGetter so that the setup in setConfig doesn't
      // take them to be the class's defined getters.

      me.getConfig('viewPreset');
      partner.on({
        presetchange: 'onPartnerPresetChange',
        thisObj: me
      }); // collect configs that are meant to be shared between partners

      const partnerSharedConfig = me.partnerSharedConfigs.reduce((config, configName) => {
        config[configName] = partner[configName];
        return config;
      }, {});
      me.setConfig(partnerSharedConfig);
      me.on({
        presetchange: 'onPartnerPresetChange',
        thisObj: partner
      });

      if (me.isPainted) {
        me.scrollable.addPartner(partner.scrollable, 'x');
      } else {
        // When initScroll comes round, make sure it syncs with the partner
        me.initScroll = FunctionHelper.createSequence(me.initScroll, () => {
          me.scrollable.addPartner(partner.scrollable, 'x');
        }, me);
      }
    }
  }
  /**
   * Breaks the link between this Timeline and the passed Timeline set up by the
   * {@link #function-addPartner} method.
   * @param {Scheduler.view.TimelineBase} otherTimeline The timeline to unlink from
   */

  removePartner(partner) {
    const me = this,
          {
      partneredWith
    } = me;

    if (me.isPartneredWith(partner)) {
      partneredWith.remove(partner);
      me.scrollable.removePartner(partner.scrollable);
      me.un({
        presetchange: 'onPartnerPresetChange',
        thisObj: partner
      });
      partner.removePartner(me);
    }
  }

  isPartneredWith(partner) {
    var _this$partneredWith;

    return Boolean((_this$partneredWith = this.partneredWith) === null || _this$partneredWith === void 0 ? void 0 : _this$partneredWith.includes(partner));
  }

  onPartnerPresetChange({
    preset,
    startDate,
    endDate,
    centerDate
  }) {
    if (!this._viewPresetChanging && this.viewPreset !== preset) {
      // Passed through to the viewPreset changing method
      preset.options = {
        startDate,
        endDate,
        centerDate
      };
      this.viewPreset = preset;
    }
  }

  get partner() {
    return this._partner;
  }

  get partners() {
    const partners = this.partner ? [this.partner] : [];

    if (this.partneredWith) {
      partners.push.apply(partners, this.partneredWith.all);
    }

    return partners;
  }

  get timeAxisColumn() {
    return this.columns && this._timeAxisColumn;
  }

  get columns() {
    // Maintainer. If we do not implement a getter to go along with our setter, this
    // property becomes unreadable at this class level.
    return super.columns;
  }

  set columns(columns) {
    const me = this;
    let timeAxisColumnIndex, timeAxisColumnConfig, timeAxisColumn, value; // No columns means destroy

    if (columns) {
      const isArray = Array.isArray(columns);
      let cols = columns;

      if (!isArray) {
        cols = columns.data;
      }

      timeAxisColumnIndex = cols && cols.length;
      cols.some((col, index) => {
        if (col.type === 'timeAxis') {
          timeAxisColumnIndex = index;
          timeAxisColumnConfig = ObjectHelper.assign(col, me.timeAxisColumn);
          return true;
        }
      }); // TODO : This is scheduler specific, override `set columns` instead
      // No additional columns allowed in vertical mode, but store the specified set in case we toggle mode later

      if (me.isVertical) {
        me._horizontalColumns = columns;
        cols = [ObjectHelper.assign({
          type: 'verticalTimeAxis',
          locked: true,
          timeline: me
        }, me.verticalTimeAxisColumn), // Make space for a regular TimeAxisColumn after the VerticalTimeAxisColumn
        cols[timeAxisColumnIndex]];
        timeAxisColumnIndex = 1;
      } else {
        // We're going to mutate this array which we do not own, so copy it first.
        cols = cols.slice();
      } // Fix up the timeAxisColumn config in place

      timeAxisColumnConfig = cols[timeAxisColumnIndex] = Object.assign({
        type: 'timeAxis',
        locked: false,
        timeline: me,
        cellCls: me.timeCellCls,
        mode: me.mode
      }, timeAxisColumnConfig); // Pass these configs into the timeAxisColumnConfig

      timeAxisColumnConfigs.forEach(configName => {
        value = me[configName];

        if (value != null) {
          timeAxisColumnConfig[configName] = value;
        }
      });

      if (isArray) {
        columns = cols;
      } else {
        columns.data = cols;
      }
    } // Invoke Grid's setter. Will create a ColumnStore which is returned by the columns setter.

    super.columns = columns;

    if (columns) {
      timeAxisColumn = me._timeAxisColumn = me.columns.getAt(timeAxisColumnIndex);

      if (me.isVertical) {
        me.verticalTimeAxisColumn = me.columns.getAt(timeAxisColumnIndex - 1);
      } // Set up event relaying early

      timeAxisColumn.relayAll(me);
    }
  }

  get timeView() {
    const me = this; // Maintainer, we need to ensure that the columns property is initialized
    // if this getter is called at configuration time before columns have been ingested.

    return me.columns && me.isVertical ? me.verticalTimeAxisColumn && me.verticalTimeAxisColumn.view : me.timeAxisColumn && me.timeAxisColumn.timeAxisView;
  }

  set eventCls(eventCls) {
    const me = this;
    me._eventCls = eventCls;

    if (!me.eventSelector) {
      me.eventSelector = `.${eventCls}-wrap`; // No difference with new rendering, released have 'b-released' only

      me.unreleasedEventSelector = `${me.eventSelector}`;
    }

    if (!me.eventInnerSelector) {
      me.eventInnerSelector = '.' + eventCls;
    }
  }

  get eventCls() {
    return this._eventCls;
  }

  set timeAxisViewModel(timeAxisViewModel) {
    var _me$timeAxisColumn;

    const me = this,
          currentModel = me._timeAxisViewModel,
          tavmListeners = {
      name: 'timeAxisViewModel',
      update: 'onTimeAxisViewModelUpdate',
      prio: 100,
      thisObj: me
    };

    if (me.partner && !timeAxisViewModel || currentModel && currentModel === timeAxisViewModel) {
      return;
    }

    if (currentModel) {
      if (currentModel.owner === me) {
        // We created this model, destroy it
        currentModel.destroy();
      }
    }

    me.detachListeners('timeAxisViewModel'); // Getting rid of instanceof check to allow using code from different bundles

    if (timeAxisViewModel && timeAxisViewModel.isTimeAxisViewModel) {
      timeAxisViewModel.on(tavmListeners);
    } else {
      timeAxisViewModel = Object.assign({
        mode: me._mode,
        snap: me.snap,
        forceFit: me.forceFit,
        timeAxis: me.timeAxis,
        suppressFit: me.suppressFit,
        listeners: tavmListeners,
        owner: me
      }, timeAxisViewModel);
      timeAxisViewModel = new TimeAxisViewModel(timeAxisViewModel);
    } // Replace in dependent classes relying on the model

    if (!me.isConfiguring && (_me$timeAxisColumn = me.timeAxisColumn) !== null && _me$timeAxisColumn !== void 0 && _me$timeAxisColumn.timeAxisView) {
      me.timeAxisColumn.timeAxisView.model = timeAxisViewModel;
    }

    me._timeAxisViewModel = timeAxisViewModel;
  }
  /**
   * The internal view model, describing the visual representation of the time axis.
   * @property {Scheduler.view.model.TimeAxisViewModel}
   * @readonly
   * @category Time axis
   */

  get timeAxisViewModel() {
    if (!this._timeAxisViewModel) {
      this.timeAxisViewModel = null;
    }

    return this._timeAxisViewModel;
  }

  get suppressFit() {
    var _this$_timeAxisViewMo, _this$_timeAxisViewMo2;

    return (_this$_timeAxisViewMo = (_this$_timeAxisViewMo2 = this._timeAxisViewModel) === null || _this$_timeAxisViewMo2 === void 0 ? void 0 : _this$_timeAxisViewMo2.suppressFit) !== null && _this$_timeAxisViewMo !== void 0 ? _this$_timeAxisViewMo : this._suppressFit;
  }

  set suppressFit(value) {
    if (this._timeAxisViewModel) {
      this.timeAxisViewModel.suppressFit = value;
    } else {
      this._suppressFit = value;
    }
  }

  set timeAxis(timeAxis) {
    const me = this,
          currentTimeAxis = me._timeAxis,
          timeAxisListeners = {
      name: 'timeAxis',

      reconfigure({
        config
      }) {
        /**
         * Fired when the timeaxis has changed, for example by zooming or configuring a new time span.
         * @event timeAxisChange
         * @param {Scheduler.view.Scheduler} source - This Scheduler
         * @param {Object} config Config object used to reconfigure the time axis.
         * @param {Date} config.start New start date (if supplied)
         * @param {Date} config.end New end date (if supplied)
         */
        me.trigger('timeAxisChange', {
          config
        });
      },

      thisObj: me
    };

    if (me.partner && !timeAxis || currentTimeAxis && currentTimeAxis === timeAxis) {
      return;
    }

    if (currentTimeAxis) {
      if (currentTimeAxis.owner === me) {
        // We created this model, destroy it
        currentTimeAxis.destroy();
      }
    }

    me.detachListeners('timeAxis'); // Getting rid of instanceof check to allow using code from different bundles

    if (!(timeAxis && timeAxis.isTimeAxis)) {
      timeAxis = ObjectHelper.assign({
        owner: me,
        viewPreset: me.viewPreset,
        autoAdjust: me.autoAdjustTimeAxis,
        mode: 'plain',
        weekStartDay: me.weekStartDay
      }, timeAxis);

      if (me.startDate) {
        timeAxis.startDate = me.startDate;
      }

      if (me.endDate) {
        timeAxis.endDate = me.endDate;
      }

      if (me.workingTime) {
        me.applyWorkingTime(timeAxis);
      }

      timeAxis = new TimeAxis(timeAxis);
    } // Inform about reconfiguring the timeaxis, to allow users to react to start & end date changes

    timeAxis.on(timeAxisListeners);
    me._timeAxis = timeAxis;
  }

  get timeAxis() {
    if (!this._timeAxis) {
      this.timeAxis = null;
    }

    return this._timeAxis;
  }
  /**
   * Get/set working time. Assign `null` to stop using working time. See {@link #config-workingTime} config for details.
   * @property {Object}
   */

  set workingTime(config) {
    this._workingTime = config;

    if (!this.isConfiguring) {
      this.applyWorkingTime(this.timeAxis);
    }
  }

  get workingTime() {
    return this._workingTime;
  } // Translates the workingTime configs into TimeAxis#include rules, applies them and then refreshes the header and
  // redraws the events

  applyWorkingTime(timeAxis) {
    const me = this,
          config = me._workingTime;

    if (config) {
      let hour = null; // Only use valid values

      if (config.fromHour >= 0 && config.fromHour < 24 && config.toHour > config.fromHour && config.toHour <= 24 && config.toHour - config.fromHour < 24) {
        hour = {
          from: config.fromHour,
          to: config.toHour
        };
      }

      let day = null; // Only use valid values

      if (config.fromDay >= 0 && config.fromDay < 7 && config.toDay > config.fromDay && config.toDay <= 7 && config.toDay - config.fromDay < 7) {
        day = {
          from: config.fromDay,
          to: config.toDay
        };
      }

      if (hour || day) {
        timeAxis.include = {
          hour,
          day
        };
      } else {
        // No valid rules, restore timeAxis
        timeAxis.include = null;
      }
    } else {
      // No rules, restore timeAxis
      timeAxis.include = null;
    }

    if (me.isPainted) {
      var _me$features$columnLi;

      // Refreshing header, which also recalculate tickSize and header data
      me.timeAxisColumn.refreshHeader(); // Update column lines

      (_me$features$columnLi = me.features.columnLines) === null || _me$features$columnLi === void 0 ? void 0 : _me$features$columnLi.refresh(); // Animate event changes

      me.refreshWithTransition();
    }
  }

  updateStartDate(date) {
    this.setStartDate(date);
  }
  /**
   * Sets the timeline start date.
   *
   * **Note:**
   * - If you need to set start and end date at the same time, use {@link Scheduler.view.mixin.TimelineZoomable#function-setTimeSpan} method.
   * - If keepDuration is false and new start date is greater than end date, it will throw an exception.
   *
   * @param {Date} date The new start date
   * @param {Boolean} keepDuration Pass `true` to keep the duration of the timeline ("move" the timeline),
   * `false` to change the duration ("resize" the timeline). Defaults to `true`.
   */

  setStartDate(date, keepDuration = true) {
    const me = this,
          ta = me._timeAxis || {},
          {
      startDate,
      endDate,
      mainUnit
    } = ta;

    if (typeof date === 'string') {
      date = DateHelper.parse(date);
    }

    if (me._timeAxis && endDate) {
      if (date) {
        let calcEndDate = endDate;

        if (keepDuration && startDate) {
          const diff = DateHelper.diff(startDate, endDate, mainUnit, true);
          calcEndDate = DateHelper.add(date, diff, mainUnit);
        }

        ta.setTimeSpan(date, calcEndDate);
      }
    } else {
      me._tempStartDate = date;
    }
  }

  get startDate() {
    const me = this;

    if (me._timeAxis) {
      return me._timeAxis.startDate;
    }

    return me._tempStartDate || new Date();
  }

  changeEndDate(date) {
    if (typeof date === 'string') {
      date = DateHelper.parse(date);
    }

    this.setEndDate(date);
  }
  /**
   * Sets the timeline end date
   *
   * **Note:**
   * - If you need to set start and end date at the same time, use {@link Scheduler.view.mixin.TimelineZoomable#function-setTimeSpan} method.
   * - If keepDuration is false and new end date is less than start date, it will throw an exception.
   *
   * @param {Date} date The new end date
   * @param {Boolean} keepDuration Pass `true` to keep the duration of the timeline ("move" the timeline),
   * `false` to change the duration ("resize" the timeline). Defaults to `false`.
   */

  setEndDate(date, keepDuration = false) {
    const me = this,
          ta = me._timeAxis || {},
          {
      startDate,
      endDate,
      mainUnit
    } = ta;

    if (typeof date === 'string') {
      date = DateHelper.parse(date);
    }

    if (me._timeAxis && startDate) {
      if (date) {
        let calcStartDate = startDate;

        if (keepDuration && endDate) {
          const diff = DateHelper.diff(startDate, endDate, mainUnit, true);
          calcStartDate = DateHelper.add(date, -diff, mainUnit);
        }

        ta.setTimeSpan(calcStartDate, date);
      }
    } else {
      me._tempEndDate = date;
    }
  }

  get endDate() {
    const me = this;

    if (me._timeAxis) {
      return me._timeAxis.endDate;
    }

    return me._tempEndDate || DateHelper.add(me.startDate, me.viewPreset.defaultSpan, me.viewPreset.mainHeader.unit);
  }

  get features() {
    return super.features;
  } // add region resize by default

  set features(features) {
    features = features === true ? {} : features;

    if (!('regionResize' in features)) {
      features.regionResize = true;
    }

    super.features = features;
  }

  get eventStyle() {
    return this._eventStyle;
  }

  set eventStyle(style) {
    const me = this;
    me._eventStyle = style;

    if (me.isPainted) {
      me.refreshWithTransition();
    }
  }

  get eventColor() {
    return this._eventColor;
  }

  set eventColor(color) {
    const me = this;
    me._eventColor = color;

    if (me.isPainted) {
      me.refreshWithTransition();
    }
  } //endregion
  //region Event handlers

  onLocaleChange() {
    // Clear events in case they use date as part of displayed info
    //        this.currentOrientation.cache.clear();
    super.onLocaleChange();
    this.timeAxisColumn.refreshHeader();
  }
  /**
   * This is a template method called by Widget when the encapsulating Scheduler SubGrid size.
   * Width changes *might* affect the Scheduler SubGrid, and *that* will trigger its
   * handler below, if that is the case. Here, we are only interested in the height changing because the
   * encapsulating grid is what dictates the scrolling viewport height.
   * @param {HTMLElement} element
   * @param {Number} width
   * @param {Number} height
   * @param {Number} oldWidth
   * @param {Number} oldHeight
   * @private
   */

  onInternalResize(element, width, height, oldWidth, oldHeight) {
    // Cache before its updated by super call.
    const oldSchedulerBodyRect = this._bodyRectangle;
    super.onInternalResize(element, width, height, oldWidth, oldHeight); // The Scheduler (The Grid) dictates the viewport height.

    if (oldSchedulerBodyRect && (height !== oldHeight || width !== oldWidth)) {
      this.onSchedulerViewportResize(width, height, oldSchedulerBodyRect.width, oldSchedulerBodyRect.height);
    }
  }
  /**
   * This is an event handler triggered when the Scheduler SubGrid changes size.
   * Its height changes when content height changes, and that is not what we are
   * interested in here. If the *width* changes, that means the visible viewport
   * has changed size.
   * @param {HTMLElement} element
   * @param {Number} width
   * @param {Number} height
   * @param {Number} oldWidth
   * @param {Number} oldHeight
   * @private
   */

  onTimeAxisSubGridResize({
    width,
    height,
    oldWidth,
    oldHeight
  }) {
    const me = this; // The timeAxisSubGrid dictates the viewport width.

    if (me.isPainted && width !== oldWidth) {
      const schedulerBodyRect = me._bodyRectangle; // For the first vertical overflow (likely when rows are rendered the first time), we want to update size
      // immediately to reduce initial "jumpiness" a bit

      if (me.hasVerticalOverflow && me.$firstVerticalOverflow) {
        me.onSchedulerViewportResize.now(width, schedulerBodyRect.height, oldWidth, schedulerBodyRect.height);
        me.$firstVerticalOverflow = false;
      } else {
        me.onSchedulerViewportResize(width, schedulerBodyRect.height, oldWidth, schedulerBodyRect.height);
      }
    }
  } // Note: This function is throttled in construct(), since it will do a full redraw per call

  onSchedulerViewportResize(width, height, oldWidth, oldHeight) {
    if (this.isPainted) {
      const me = this,
            subGrid = me.timeAxisSubGrid;
      me.currentOrientation.onViewportResize(width, height, oldWidth, oldHeight); // Ignore resize caused by toggling vertical scrollbar visibility, since those might otherwise lead to an
      // infinite loop when the difference between on/off causes horizontal scrolling to also toggle

      if (me._lastOverflow == null || me._lastOverflow === subGrid.overflowingHorizontally) {
        // Responding by triggering a layout in the same frame seems to trigger an infinite resize
        // loop when using ResizeObserver, so push the response out into the next frame.
        me.setTimeout(() => {
          me.timeAxisViewModel.availableSpace = me.isHorizontal ? subGrid.width : me._bodyRectangle.height;
        }, 0);
        me.partneredWith && !me.isSyncingFromPartner && me.partneredWith.forEach(p => {
          if (!p.isSyncingFromPartner) {
            p.isSyncingFromPartner = true;
            me.eachSubGrid(subGrid => {
              const partnerSubGrid = p.subGrids[subGrid.region]; // If there is a difference, sync the partner SubGrid state

              if (partnerSubGrid.width !== subGrid.width) {
                if (subGrid.collapsed) {
                  partnerSubGrid.collapse();
                } else {
                  if (partnerSubGrid.collapsed) {
                    partnerSubGrid.expand();
                  } // When using flexed subgrid, make sure flex values has prio over width

                  if (subGrid.flex) {
                    // If flex values match, resize should be fine without changing anything
                    if (subGrid.flex !== partnerSubGrid.flex) {
                      partnerSubGrid.flex = subGrid.flex;
                    }
                  } else {
                    partnerSubGrid.width = subGrid.width;
                  }
                }
              }
            });
            p.isSyncingFromPartner = false;
          }
        });
      } // NOTE: Moved out of the if-statement above since that prevented event from being triggered in all
      // scenarios where size has changed

      /**
       * Fired when the *scheduler* viewport (not the overall Scheduler element) changes size.
       * This happens when the grid changes height, or when the subgrid which encapsulates the
       * scheduler column changes width.
       * @event timelineViewportResize
       * @param {Core.widget.Widget} source - This Scheduler
       * @param {Number} width The new width
       * @param {Number} height The new height
       * @param {Number} oldWidth The old width
       * @param {Number} oldHeight The old height
       */

      me.trigger('timelineViewportResize', {
        width,
        height,
        oldWidth,
        oldHeight
      });
      me._lastOverflow = subGrid.overflowingHorizontally;
    }
  }

  onTimeAxisViewModelUpdate() {
    this.updateCanvasSize();
    this.currentOrientation.onTimeAxisViewModelUpdate();
  } //endregion
  //region Mode

  get currentOrientation() {
    throw new Error('Implement in subclass');
  } // Horizontal is the default, overridden in scheduler

  get isHorizontal() {
    return true;
  } //endregion
  //region Canvases and elements

  get backgroundCanvas() {
    return this._backgroundCanvas;
  }

  get foregroundCanvas() {
    return this._foregroundCanvas;
  }

  get svgCanvas() {
    const me = this;

    if (!me._svgCanvas) {
      const svg = me._svgCanvas = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('id', IdHelper.generateId('svg')); // To not be recycled by DomSync

      svg.retainElement = true;
      me.foregroundCanvas.appendChild(svg);
      me.trigger('svgCanvasCreated');
    }

    return me._svgCanvas;
  }
  /**
   * Returns the subGrid containing the time axis
   * @property {Grid.view.SubGrid}
   * @readonly
   */

  get timeAxisSubGridElement() {
    // We need the TimeAxisSubGrid to exist, so regions must be inited
    this.getConfig('regions');
    return this.timeAxisColumn && this.timeAxisColumn.subGridElement;
  }

  updateCanvasSize() {
    const me = this,
          width = me.isHorizontal ? me.timeAxisViewModel.totalSize : me.timeAxisColumn.width;
    let result = false; // Canvas might need a height in vertical mode, if ticks does not fill height (suppressFit : true)

    if (me.isVertical && me.suppressFit) {
      DomHelper.setLength(me.foregroundCanvas, 'height', me.timeAxisViewModel.totalSize);
      result = true;
    }

    if (width !== me.$canvasWidth) {
      if (me.backgroundCanvas) {
        DomHelper.setLength(me.backgroundCanvas, 'width', width);
      }

      DomHelper.setLength(me.foregroundCanvas, 'width', width);
      me.$canvasWidth = me.timeAxisViewModel.totalSize;
      result = true;
    }

    return result;
  }
  /**
   * A chainable function which Features may hook to add their own content to the timeaxis header.
   * @param {Array} configs An array of domConfigs, append to it to have the config applied to the header
   */

  getHeaderDomConfigs(configs) {}
  /**
   * A chainable function which Features may hook to add their own content to the foreground canvas
   * @param {Array} configs An array of domConfigs, append to it to have the config applied to the foreground canvas
   */

  getForegroundDomConfigs(configs) {} //endregion
  //region Grid overrides

  refresh(forceLayout = true) {
    const me = this;

    if (me.isPainted && !me.refreshSuspended) {
      if (me.hasVisibleEvents) {
        if (me.isEngineReady) {
          me.refreshRows(false, forceLayout);
        } else {
          me.refreshAfterProjectRefresh = true;
          me.currentOrientation.refreshAllWhenReady = true;
        }
      } // Even if there are no events in our timeline, Features
      // assume there will be a refresh event from the RowManager
      // after a refresh request so fire it here.
      else {
          me.rowManager.trigger('refresh');
        }
    }
  }

  render() {
    const me = this,
          schedulerEl = me.timeAxisSubGridElement;

    if (me.useBackgroundCanvas) {
      me._backgroundCanvas = DomHelper.createElement({
        className: 'b-sch-background-canvas',
        parent: schedulerEl,
        nextSibling: schedulerEl.firstElementChild
      });
    } // The font-size trick is no longer used by scheduler, since it allows per resource margins

    const fgCanvas = me._foregroundCanvas = DomHelper.createElement({
      className: 'b-sch-foreground-canvas',
      style: `font-size:${me.rowHeight - me.resourceMargin * 2}px`,
      parent: schedulerEl
    });
    me.timeAxisSubGrid.insertRowsBefore = fgCanvas; // Size correctly in case ticks does not fill height

    if (me.isVertical && me.suppressFit) {
      me.updateCanvasSize();
    }

    super.render(...arguments);

    if (me.visibleDate) {
      if (me.visibleDate instanceof Date) {
        me.visibleDate = {
          date: me.visibleDate
        };
      }

      me.scrollToDate(me.visibleDate.date, me.visibleDate);
    }
  } // TODO: refreshRows -> refresh in grid?

  refreshRows(returnToTop = false, reLayoutEvents = true) {
    const me = this;

    if (me.isConfiguring) {
      return;
    }

    me.currentOrientation.refreshRows(reLayoutEvents);
    super.refreshRows(returnToTop);
  } //endregion
  //region Other

  getDateConstraints() {} // duration = false prevents transition

  runWithTransition(fn, duration) {
    const me = this; // Do not attempt to enter animating state if we are not visible

    if (me.isVisible) {
      // Allow calling with true/false to keep code simpler in other places
      if (duration == null || duration === true) {
        duration = me.transitionDuration;
      } // Ask Grid superclass to enter the animated state if requested and enabled.

      if (duration && me.enableEventAnimations) {
        me.isAnimating = true; // Exit animating state in duration milliseconds.
        // Cancel any previous outstanding exit timer.

        exitTransition.delay = duration;
        me.setTimeout(exitTransition);
      }
    }

    fn();
  }

  exitTransition() {
    this.isAnimating = false;
    this.trigger('transitionend');
  }

  async waitForTransition() {
    if (this.isAnimating) {
      return this.await('transitionend');
    }
  }
  /**
   * Refreshes the grid with transitions enabled.
   */

  refreshWithTransition(forceLayout, duration) {
    this.runWithTransition(() => this.refresh(forceLayout), duration);
  }
  /**
   * Returns an object representing the visible date range, with `startDate` and `endDate` properties.
   * @deprecated 4.0.0 Deprecated in favor of `get visibleDateRange`
   * @return {Object} The date range
   * @return {Date} return.startDate Start date
   * @return {Date} return.endDate End date
   */

  getVisibleDateRange() {
    VersionHelper.deprecate('scheduler', '5.0.0', 'Deprecated in favor of `get visibleDateRange`');
    return this.visibleDateRange;
  }
  /**
   * Returns an object representing the visible date range, with `startDate` and `endDate` properties
   * @property {Object}
   */

  get visibleDateRange() {
    return this.currentOrientation.visibleDateRange;
  } //endregion

  /**
   * Returns a rounded duration value to be displayed in UI (tooltips, labels etc)
   * @param {Number} The raw duration value
   * @param {Number} [nbrDecimals] The number of decimals, defaults to {@link #config-durationDisplayPrecision}
   * @return {Number} The rounded duration
   */

  formatDuration(duration, nbrDecimals = this.durationDisplayPrecision) {
    const multiplier = Math.pow(10, nbrDecimals);
    return Math.round(duration * multiplier) / multiplier;
  }

} // Register this widget type with its Factory

TimelineBase.initClass(); // Has to be here because Gantt extends TimelineBase

VersionHelper.setVersion('scheduler', '4.0.8');
TimelineBase._$name = 'TimelineBase';

/* eslint-disable quote-props */
const locale$3 = LocaleHelper.mergeLocales(locale$2, {
  ResourceInfoColumn: {
    eventCountText: function (data) {
      return data + ' event' + (data !== 1 ? 's' : '');
    }
  },
  Dependencies: {
    from: 'From',
    to: 'To',
    valid: 'Valid',
    invalid: 'Invalid'
  },
  DependencyType: {
    SS: 'SS',
    SF: 'SF',
    FS: 'FS',
    FF: 'FF',
    StartToStart: 'Start-to-Start',
    StartToEnd: 'Start-to-End',
    EndToStart: 'End-to-Start',
    EndToEnd: 'End-to-End',
    short: ['SS', 'SF', 'FS', 'FF'],
    long: ['Start-to-Start', 'Start-to-End', 'End-to-Start', 'End-to-End']
  },
  DependencyEdit: {
    From: 'From',
    To: 'To',
    Type: 'Type',
    Lag: 'Lag',
    'Edit dependency': 'Edit dependency',
    Save: 'Save',
    Delete: 'Delete',
    Cancel: 'Cancel',
    StartToStart: 'Start to Start',
    StartToEnd: 'Start to End',
    EndToStart: 'End to Start',
    EndToEnd: 'End to End'
  },
  EventEdit: {
    Name: 'Name',
    Resource: 'Resource',
    Start: 'Start',
    End: 'End',
    Save: 'Save',
    Delete: 'Delete',
    Cancel: 'Cancel',
    'Edit event': 'Edit event',
    Repeat: 'Repeat'
  },
  EventDrag: {
    eventOverlapsExisting: 'Event overlaps existing event for this resource',
    noDropOutsideTimeline: 'Event may not be dropped completely outside the timeline'
  },
  SchedulerBase: {
    'Add event': 'Add event',
    'Delete event': 'Delete event',
    'Unassign event': 'Unassign event'
  },
  // TODO: 'headerContextMenu' is deprecated. Please see https://bryntum.com/docs/scheduler/#guides/upgrades/4.0.0.md for more information.
  HeaderContextMenu: {
    pickZoomLevel: 'Zoom',
    activeDateRange: 'Date range',
    startText: 'Start date',
    endText: 'End date',
    todayText: 'Today'
  },
  TimeAxisHeaderMenu: {
    pickZoomLevel: 'Zoom',
    activeDateRange: 'Date range',
    startText: 'Start date',
    endText: 'End date',
    todayText: 'Today'
  },
  UndoRedo: {
    UndoLastAction: 'Undo last action',
    RedoLastAction: 'Redo last undone action'
  },
  EventFilter: {
    filterEvents: 'Filter tasks',
    byName: 'By name'
  },
  TimeRanges: {
    showCurrentTimeLine: 'Show current timeline'
  },
  PresetManager: {
    secondAndMinute: {
      displayDateFormat: 'll LTS'
    },
    minuteAndHour: {
      topDateFormat: 'ddd MM/DD, hA',
      displayDateFormat: 'll LST'
    },
    hourAndDay: {
      topDateFormat: 'ddd MM/DD',
      middleDateFormat: 'LST',
      displayDateFormat: 'll LST'
    },
    weekAndDay: {
      displayDateFormat: 'll LST'
    },
    dayAndWeek: {
      displayDateFormat: 'll LST'
    }
  },
  RecurrenceConfirmationPopup: {
    'delete-title': 'You are deleting an event',
    'delete-all-message': 'Do you want to delete all occurrences of this event?',
    'delete-further-message': 'Do you want to delete this and all future occurrences of this event, or only the selected occurrence?',
    'delete-further-btn-text': 'Delete All Future Events',
    'delete-only-this-btn-text': 'Delete Only This Event',
    'update-title': 'You are changing a repeating event',
    'update-all-message': 'Do you want to change all occurrences of this event?',
    'update-further-message': 'Do you want to change only this occurrence of the event, or this and all future occurrences?',
    'update-further-btn-text': 'All Future Events',
    'update-only-this-btn-text': 'Only This Event',
    Yes: 'Yes',
    Cancel: 'Cancel',
    width: 600
  },
  RecurrenceLegend: {
    ' and ': ' and ',
    // frequency patterns
    Daily: 'Daily',
    // Weekly on Sunday
    // Weekly on Sun, Mon and Tue
    'Weekly on {1}': ({
      days
    }) => `Weekly on ${days}`,
    // Monthly on 16
    // Monthly on the last weekday
    'Monthly on {1}': ({
      days
    }) => `Monthly on ${days}`,
    // Yearly on 16 of January
    // Yearly on the last weekday of January and February
    'Yearly on {1} of {2}': ({
      days,
      months
    }) => `Yearly on ${days} of ${months}`,
    // Every 11 days
    'Every {0} days': ({
      interval
    }) => `Every ${interval} days`,
    // Every 2 weeks on Sunday
    // Every 2 weeks on Sun, Mon and Tue
    'Every {0} weeks on {1}': ({
      interval,
      days
    }) => `Every ${interval} weeks on ${days}`,
    // Every 2 months on 16
    // Every 2 months on the last weekday
    'Every {0} months on {1}': ({
      interval,
      days
    }) => `Every ${interval} months on ${days}`,
    // Every 2 years on 16 of January
    // Every 2 years on the last weekday of January and February
    'Every {0} years on {1} of {2}': ({
      interval,
      days,
      months
    }) => `Every ${interval} years on ${days} of ${months}`,
    // day position translations
    position1: 'the first',
    position2: 'the second',
    position3: 'the third',
    position4: 'the fourth',
    position5: 'the fifth',
    'position-1': 'the last',
    // day options
    day: 'day',
    weekday: 'weekday',
    'weekend day': 'weekend day',
    // {0} - day position info ("the last"/"the first"/...)
    // {1} - day info ("Sunday"/"Monday"/.../"day"/"weekday"/"weekend day")
    // For example:
    //  "the last Sunday"
    //  "the first weekday"
    //  "the second weekend day"
    daysFormat: ({
      position,
      days
    }) => `${position} ${days}`
  },
  RecurrenceEditor: {
    'Repeat event': 'Repeat event',
    Cancel: 'Cancel',
    Save: 'Save',
    Frequency: 'Frequency',
    Every: 'Every',
    DAILYintervalUnit: 'day(s)',
    WEEKLYintervalUnit: 'week(s)',
    MONTHLYintervalUnit: 'month(s)',
    YEARLYintervalUnit: 'year(s)',
    Each: 'Each',
    'On the': 'On the',
    'End repeat': 'End repeat',
    'time(s)': 'time(s)'
  },
  RecurrenceDaysCombo: {
    day: 'day',
    weekday: 'weekday',
    'weekend day': 'weekend day'
  },
  RecurrencePositionsCombo: {
    position1: 'first',
    position2: 'second',
    position3: 'third',
    position4: 'fourth',
    position5: 'fifth',
    'position-1': 'last'
  },
  RecurrenceStopConditionCombo: {
    Never: 'Never',
    After: 'After',
    'On date': 'On date'
  },
  RecurrenceFrequencyCombo: {
    Daily: 'Daily',
    Weekly: 'Weekly',
    Monthly: 'Monthly',
    Yearly: 'Yearly'
  },
  RecurrenceCombo: {
    None: 'None',
    Custom: 'Custom...'
  },
  //region Features
  Summary: {
    'Summary for': date => `Summary for ${date}`
  },
  //endregion
  //region Export
  ScheduleRangeCombo: {
    completeview: 'Complete schedule',
    currentview: 'Visible schedule',
    daterange: 'Date range',
    completedata: 'Complete schedule (for all events)'
  },
  SchedulerExportDialog: {
    'Schedule range': 'Schedule range',
    'Export from': 'From',
    'Export to': 'To'
  },
  ExcelExporter: {
    'No resource assigned': 'No resource assigned'
  },
  //endregion
  CrudManagerView: {
    serverResponseLabel: 'Server response:'
  }
});

LocaleManagerSingleton.registerLocale('En', {
  desc: 'English',
  locale: locale$3
});

/**
 * @module Scheduler/view/mixin/SchedulerDom
 */

/**
 * Mixin with EventModel and ResourceModel <-> HTMLElement mapping functions
 *
 * @mixin
 */

var SchedulerDom = (Target => class SchedulerDom extends (Target || Base) {
  static get $name() {
    return 'SchedulerDom';
  } //region Get

  /**
   * Returns a single HTMLElement representing an event record assigned to a specific resource.
   * @param {Scheduler.model.AssignmentModel} assignmentRecord An assignment record
   * @return {HTMLElement} The element representing the event record
   */

  getElementFromAssignmentRecord(assignmentRecord, returnWrapper = false) {
    return this.currentOrientation.getElementFromAssignmentRecord(assignmentRecord, returnWrapper);
  }
  /**
   * Returns a single HTMLElement representing an event record assigned to a specific resource.
   * @param {Scheduler.model.EventModel} eventRecord An event record
   * @param {Scheduler.model.ResourceModel} resourceRecord A resource record
   * @return {HTMLElement} The element representing the event record
   */

  getElementFromEventRecord(eventRecord, resourceRecord, returnWrapper = false) {
    return this.currentOrientation.getElementFromEventRecord(eventRecord, resourceRecord, returnWrapper);
  }
  /**
   * Returns all the HTMLElements representing an event record.
   *
   * @param {Scheduler.model.EventModel} eventRecord An event record
   * @param {Scheduler.model.ResourceModel} [resourceRecord] A resource record
   *
   * @return {HTMLElement[]} The element(s) representing the event record
   */

  getElementsFromEventRecord(eventRecord, resourceRecord, returnWrapper = false) {
    return this.currentOrientation.getElementsFromEventRecord(eventRecord, resourceRecord, returnWrapper);
  } //endregion
  //region Resolve

  /**
   * Resolves the resource based on a dom element or event. In vertical mode, if resolving from an element higher up in
   * the hierarchy than event elements, then it is required to supply an coordinates since resources are virtual
   * columns.
   * @param {HTMLElement|Event} elementOrEvent The HTML element or DOM event to resolve a resource from
   * @param {Number[]} [xy] X and Y coordinates, required in some cases in vertical mode, disregarded in horizontal
   * @return {Scheduler.model.ResourceModel} The resource corresponding to the element, or null if not found.
   */

  resolveResourceRecord(elementOrEvent, xy) {
    return this.currentOrientation.resolveRowRecord(elementOrEvent, xy);
  }
  /**
   * Returns the event record for a DOM element
   * @param {HTMLElement} element The DOM node to lookup
   * @return {Scheduler.model.EventModel} The event record
   */

  resolveEventRecord(element) {
    element = DomHelper.up(element, this.eventSelector);

    if (element) {
      if (element.dataset.eventId) {
        return this.eventStore.getById(element.dataset.eventId);
      }

      if (element.dataset.assignmentId) {
        return this.assignmentStore.getById(element.dataset.assignmentId).event;
      }
    }

    return null;
  } // Used by shared features to resolve an event or task

  resolveTimeSpanRecord(element) {
    return this.resolveEventRecord(element);
  }
  /**
   * Returns an assignment record for a DOM element
   * @param {HTMLElement} element The DOM node to lookup
   * @return {Scheduler.model.AssignmentModel} The assignment record
   */

  resolveAssignmentRecord(element) {
    const eventElement = DomHelper.up(element, this.eventSelector),
          assignmentRecord = eventElement && this.assignmentStore.getById(eventElement.dataset.assignmentId),
          eventRecord = eventElement && this.eventStore.getById(eventElement.dataset.eventId); // When resolving a recurring event, we might be resolving an occurrence

    return this.assignmentStore.getOccurrence(assignmentRecord, eventRecord);
  } //endregion
  // Decide if a record is inside a collapsed tree node, or inside a collapsed group (using grouping feature)

  isRowVisible(resourceRecord) {
    // records in collapsed groups/brances etc are removed from processedRecords
    return this.store.indexOf(resourceRecord) >= 0;
  } // This does not need a className on Widgets.
  // Each *Class* which doesn't need 'b-' + constructor.name.toLowerCase() automatically adding
  // to the Widget it's mixed in to should implement thus.

  get widgetClass() {}

});

/**
 * @module Scheduler/view/mixin/SchedulerDomEvents
 */

/**
 * Mixin that handles dom events (click etc) for scheduler and rendered events.
 *
 * @mixin
 */

var SchedulerDomEvents = (Target => class SchedulerDomEvents extends (Target || Base) {
  static get $name() {
    return 'SchedulerDomEvents';
  } //region Events

  /**
   * Triggered when user moves mouse over an empty area in the schedule.
   * @event scheduleMouseMove
   * @param {Scheduler.view.TimelineBase} source This TimelineBase
   * @param {Date} date Date at mouse position
   * @param {Date} tickStartDate The start date of the current time axis tick
   * @param {Date} tickEndDate The end date of the current time axis tick
   * @param {Grid.row.Row} row Clicked row (in horizontal mode only)
   * @param {Number} index Index of clicked resource
   * @param {Scheduler.model.ResourceModel} resourceRecord Resource record
   * @param {MouseEvent} event Browser event
   */

  /**
   * Triggered when user clicks an empty area in the schedule.
   * @event scheduleClick
   * @param {Scheduler.view.TimelineBase} source This TimelineBase
   * @param {Date} date Date at mouse position
   * @param {Date} tickStartDate The start date of the current time axis tick
   * @param {Date} tickEndDate The end date of the current time axis tick
   * @param {Grid.row.Row} row Clicked row (in horizontal mode only)
   * @param {Number} index Index of clicked resource
   * @param {Scheduler.model.ResourceModel} resourceRecord Resource record
   * @param {MouseEvent} event Browser event
   */

  /**
   * Triggered when user clicks an empty area in the schedule.
   * @event scheduleDblClick
   * @param {Scheduler.view.TimelineBase} source This TimelineBase
   * @param {Date} date Date at mouse position
   * @param {Date} tickStartDate The start date of the current time axis tick
   * @param {Date} tickEndDate The end date of the current time axis tick
   * @param {Grid.row.Row} row Double clicked row (in horizontal mode only)
   * @param {Number} index Index of double clicked resource
   * @param {Scheduler.model.ResourceModel} resourceRecord Resource record
   * @param {MouseEvent} event Browser event
   */

  /**
   * Triggered when user right clicks an empty area in the schedule.
   * @event scheduleContextMenu
   * @param {Scheduler.view.TimelineBase} source This TimelineBase
   * @param {Date} date Date at mouse position
   * @param {Date} tickStartDate The start date of the current time axis tick
   * @param {Date} tickEndDate The end date of the current time axis tick
   * @param {Grid.row.Row} row Clicked row (in horizontal mode only)
   * @param {Number} index Index of clicked resource
   * @param {Scheduler.model.ResourceModel} resourceRecord Resource record
   * @param {MouseEvent} event Browser event
   */

  /**
   * Triggered for mouse down on an event.
   * @event eventMouseDown
   * @param {Scheduler.view.Scheduler} source This Scheduler
   * @param {Scheduler.model.EventModel} eventRecord Event record
   * @param {Scheduler.model.AssignmentModel} assignmentRecord Assignment record
   * @param {MouseEvent} event Browser event
   */

  /**
   * Triggered for mouse up on an event.
   * @event eventMouseUp
   * @param {Scheduler.view.Scheduler} source This Scheduler
   * @param {Scheduler.model.EventModel} eventRecord Event record
   * @param {Scheduler.model.AssignmentModel} assignmentRecord Assignment record
   * @param {MouseEvent} event Browser event
   */

  /**
   * Triggered for click on an event.
   * @event eventClick
   * @param {Scheduler.view.Scheduler} source This Scheduler
   * @param {Scheduler.model.EventModel} eventRecord Event record
   * @param {Scheduler.model.AssignmentModel} assignmentRecord Assignment record
   * @param {MouseEvent} event Browser event
   */

  /**
   * Triggered for double click on an event.
   * @event eventDblClick
   * @param {Scheduler.view.Scheduler} source This Scheduler
   * @param {Scheduler.model.EventModel} eventRecord Event record
   * @param {Scheduler.model.AssignmentModel} assignmentRecord Assignment record
   * @param {MouseEvent} event Browser event
   */

  /**
   * Triggered for right click on an event.
   * @event eventContextMenu
   * @param {Scheduler.view.Scheduler} source This Scheduler
   * @param {Scheduler.model.EventModel} eventRecord Event record
   * @param {Scheduler.model.AssignmentModel} assignmentRecord Assignment record
   * @param {MouseEvent} event Browser event
   */

  /**
   * Triggered for mouse over on an event.
   * @event eventMouseOver
   * @param {Scheduler.view.Scheduler} source This Scheduler
   * @param {Scheduler.model.EventModel} eventRecord Event record
   * @param {Scheduler.model.AssignmentModel} assignmentRecord Assignment record
   * @param {MouseEvent} event Browser event
   */

  /**
   * Triggered for mouse out from an event.
   * @event eventMouseOut
   * @param {Scheduler.view.Scheduler} source This Scheduler
   * @param {Scheduler.model.EventModel} eventRecord Event record
   * @param {Scheduler.model.AssignmentModel} assignmentRecord Assignment record
   * @param {MouseEvent} event Browser event
   */
  //endregion
  //region Event handling

  getTimeSpanMouseEventParams(eventElement, event) {
    // May have hovered a record being removed / faded out
    const eventRecord = this.resolveEventRecord(eventElement);
    return eventRecord && {
      eventRecord,
      resourceRecord: this.resolveResourceRecord(eventElement),
      assignmentRecord: this.resolveAssignmentRecord(eventElement),
      eventElement,
      event
    };
  }

  getScheduleMouseEventParams(cellData, event) {
    const resourceRecord = this.isVertical ? this.resolveResourceRecord(event) : this.store.getById(cellData.id);
    return {
      resourceRecord
    };
  }
  /**
   * Relays keydown events as eventkeydown if we have a selected task.
   * @private
   */

  onElementKeyDown(event) {
    super.onElementKeyDown(event);
    const me = this;

    if (me.selectedEvents.length) {
      me.trigger(me.scheduledEventName + 'KeyDown', {
        eventRecord: me.selectedEvents,
        assignmentRecord: me.selectedAssignments
      });
    }
  }
  /**
   * Relays keyup events as eventkeyup if we have a selected task.
   * @private
   */

  onElementKeyUp(event) {
    super.onElementKeyUp(event);
    const me = this;

    if (me.selectedEvents.length) {
      me.trigger(me.scheduledEventName + 'KeyUp', {
        eventRecord: me.selectedEvents,
        assignmentRecord: me.selectedAssignments
      });
    }
  } //endregion
  // This does not need a className on Widgets.
  // Each *Class* which doesn't need 'b-' + constructor.name.toLowerCase() automatically adding
  // to the Widget it's mixed in to should implement thus.

  get widgetClass() {}

});

/**
 * @module Scheduler/view/mixin/SchedulerDragResize
 */

/**
 * Mixin for triggering event editor on drag creation etc.
 * @private
 * @mixin
 */

var SchedulerDragResize = (Target => class SchedulerDragResize extends (Target || Base) {
  static get $name() {
    return 'SchedulerDragResize';
  }

  construct(config) {
    super.construct(config);

    if (this.hasFeature('eventDragCreate')) {
      this.on({
        dragCreateEnd: 'internalOnDragCreateEnd',
        thisObj: this
      });
    }
  }

  internalOnDragCreateEnd({
    newEventRecord: eventRecord,
    resourceRecord
  }) {
    const me = this; // If an event editor is defined and enabled, it has to manage how/if/when the event is added to the event store

    if ( // Scheduler or Scheduler Pro using `eventEdit`
    (!me.hasFeature('simpleEventEdit') || me.features.simpleEventEdit.disabled) && (!me.hasFeature('eventEdit') || me.features.eventEdit.disabled) && ( // Scheduler Pro using `taskEdit`
    !me.hasFeature('taskEdit') || me.features.taskEdit.disabled)) {
      const resources = resourceRecord ? [resourceRecord] : []; // resources **Deprecated 4.0** Use `resourceRecords` instead

      if (me.trigger('beforeEventAdd', {
        eventRecord,
        resources,
        resourceRecords: resources
      }) !== false) {
        me.onEventCreated(eventRecord);
        me.eventStore.add(eventRecord);
        eventRecord.assign(resourceRecord);
      }
    }
  }

  get eventEditor() {
    return this._eventEditor;
  }

  set eventEditor(editor) {
    this._eventEditor = editor;
  } // This does not need a className on Widgets.
  // Each *Class* which doesn't need 'b-' + constructor.name.toLowerCase() automatically adding
  // to the Widget it's mixed in to should implement thus.

  get widgetClass() {}

});

/**
 * @module Scheduler/eventlayout/HorizontalLayout
 */

/**
 * Base class for HorizontalLayoutPack & HorizontalLayoutStack. Should not be used directly, instead specify
 * {@link Scheduler.view.mixin.SchedulerEventRendering#config-eventLayout} in Scheduler config (stack, pack or none):
 * @example
 * let scheduler = new Scheduler({
 *   eventLayout: 'stack'
 * });
 * @abstract
 * @private
 */

class HorizontalLayout extends Base {
  static get defaultConfig() {
    return {
      nbrOfBandsByResource: {},
      bandIndexToPxConvertFn: null,
      bandIndexToPxConvertThisObj: null
    };
  }

  clearCache(resource) {
    if (resource) {
      delete this.nbrOfBandsByResource[resource.id];
    } else {
      this.nbrOfBandsByResource = {};
    }
  } // Input:
  // 1. Resource record
  // 2. Array of Event models, or a function to call to receive such event records lazily

  getNumberOfBands(resource, resourceEventsOrFn) {
    const nbrOfBandsByResource = this.nbrOfBandsByResource;

    if (nbrOfBandsByResource.hasOwnProperty(resource.id)) {
      return nbrOfBandsByResource[resource.id];
    }

    const resourceEvents = typeof resourceEventsOrFn === 'function' ? resourceEventsOrFn() : resourceEventsOrFn,
          eventsData = resourceEvents.map(event => ({
      start: event.startDate,
      end: event.endDate,
      event: event
    }));
    return this.applyLayout(eventsData, resource);
  } // TODO DOC

  applyLayout(events, resource) {
    const rowEvents = events.slice(),
          // return a number of bands required
    newNbrBands = this.layoutEventsInBands(rowEvents);
    return this.nbrOfBandsByResource[resource.id] = newNbrBands;
  }

}
HorizontalLayout._$name = 'HorizontalLayout';

/**
 * @module Scheduler/eventlayout/HorizontalLayoutStack
 */

/**
 * Handles layout of events within a row (resource) in horizontal mode. Stacks events, increasing row height when to fit
 * all overlapping events.
 *
 * This layout is used by default in horizontal mode.
 *
 * @extends Scheduler/eventlayout/HorizontalLayout
 * @private
 */

class HorizontalLayoutStack extends HorizontalLayout {
  // Input: Array of event layout data
  layoutEventsInBands(events) {
    let verticalPosition = 0;

    do {
      let eventIndex = 0,
          event = events[0];

      while (event) {
        // Apply band height to the event cfg
        event.top = this.bandIndexToPxConvertFn.call(this.bandIndexToPxConvertThisObj || this, verticalPosition, event.eventRecord, event.resourceRecord); // Remove it from the array and continue searching

        events.splice(eventIndex, 1);
        eventIndex = this.findClosestSuccessor(event, events);
        event = events[eventIndex];
      }

      verticalPosition++;
    } while (events.length > 0); // Done!

    return verticalPosition;
  } // TODO: optimize this for better performance with many events per resource

  findClosestSuccessor(eventRenderData, events) {
    const eventEnd = eventRenderData.endMS,
          isMilestone = eventRenderData.eventRecord && eventRenderData.eventRecord.duration === 0;
    let minGap = Infinity,
        closest,
        gap,
        event;

    for (let i = 0, l = events.length; i < l; i++) {
      event = events[i];
      gap = event.startMS - eventEnd;

      if (gap >= 0 && gap < minGap && ( // Two milestones should not overlap
      gap > 0 || event.endMS - event.startMS > 0 || !isMilestone)) {
        closest = i;
        minGap = gap;
      }
    }

    return closest;
  }

}
HorizontalLayoutStack._$name = 'HorizontalLayoutStack';

/**
 * @module Scheduler/eventlayout/PackMixin
 */

/**
 * Mixin holding functionality shared between HorizontalLayoutPack and VerticalLayout.
 *
 * @mixin
 * @private
 */

var PackMixin = (Target => class PackMixin extends (Target || Base) {
  static get $name() {
    return 'PackMixin';
  }

  static get defaultConfig() {
    return {
      coordProp: 'top',
      sizeProp: 'height'
    };
  } // Packs the events to consume as little space as possible

  applyLayout(events, applyClusterFn) {
    const me = this,
          {
      coordProp,
      sizeProp
    } = me;
    let slot, firstInCluster, cluster, j;

    for (let i = 0, l = events.length; i < l; i++) {
      firstInCluster = events[i];
      slot = me.findStartSlot(events, firstInCluster);
      cluster = me.getCluster(events, i);

      if (cluster.length > 1) {
        firstInCluster[coordProp] = slot.start;
        firstInCluster[sizeProp] = slot.end - slot.start; // If there are multiple slots and events in the cluster have multiple start dates, group all same-start events into first slot

        j = 1;

        while (j < cluster.length - 1 && cluster[j + 1].start - firstInCluster.start === 0) {
          j++;
        } // See if there's more than 1 slot available for this cluster, if so - first group in cluster consumes the entire first slot

        const nextSlot = me.findStartSlot(events, cluster[j]);

        if (nextSlot && nextSlot.start < 0.8) {
          cluster.length = j;
        }
      }

      const clusterSize = cluster.length,
            slotSize = (slot.end - slot.start) / clusterSize; // Apply fraction values

      for (j = 0; j < clusterSize; j++) {
        applyClusterFn(cluster[j], j, slot, slotSize);
      }

      i += clusterSize - 1;
    }
  }

  findStartSlot(events, event) {
    const {
      sizeProp,
      coordProp
    } = this,
          priorOverlappers = this.getPriorOverlappingEvents(events, event);
    let i;

    if (priorOverlappers.length === 0) {
      return {
        start: 0,
        end: 1
      };
    }

    for (i = 0; i < priorOverlappers.length; i++) {
      if (i === 0 && priorOverlappers[0][coordProp] > 0) {
        return {
          start: 0,
          end: priorOverlappers[0][coordProp]
        };
      } else if (priorOverlappers[i][coordProp] + priorOverlappers[i][sizeProp] < (i < priorOverlappers.length - 1 ? priorOverlappers[i + 1][coordProp] : 1)) {
        return {
          start: priorOverlappers[i][coordProp] + priorOverlappers[i][sizeProp],
          end: i < priorOverlappers.length - 1 ? priorOverlappers[i + 1][coordProp] : 1
        };
      }
    }

    return false;
  }

  getPriorOverlappingEvents(events, event) {
    const start = event.start,
          end = event.end,
          overlappers = [];

    for (let i = 0, l = events.indexOf(event); i < l; i++) {
      if (DateHelper.intersectSpans(start, end, events[i].start, events[i].end)) {
        overlappers.push(events[i]);
      }
    }

    overlappers.sort(this.sortOverlappers.bind(this));
    return overlappers;
  }

  sortOverlappers(e1, e2) {
    return e1[this.coordProp] < e2[this.coordProp] ? -1 : 1;
  }

  getCluster(events, startIndex) {
    const startEvent = events[startIndex];

    if (startIndex >= events.length - 1) {
      return [startEvent];
    }

    const evts = [startEvent],
          l = events.length;
    let {
      start,
      end
    } = startEvent,
        i = startIndex + 1;

    while (i < l && DateHelper.intersectSpans(start, end, events[i].start, events[i].end)) {
      evts.push(events[i]);
      start = DateHelper.max(start, events[i].start);
      end = DateHelper.min(events[i].end, end);
      i++;
    }

    return evts;
  }

});

/**
 * @module Scheduler/eventlayout/HorizontalLayoutPack
 */

/**
 * Handles layout of events within a row (resource) in horizontal mode. Packs events (adjusts their height) to fit
 * available row height
 *
 * @extends Scheduler/eventlayout/HorizontalLayout
 * @mixes Scheduler/eventlayout/PackMixin
 * @private
 */

class HorizontalLayoutPack extends HorizontalLayout.mixin(PackMixin) {
  // Packs the events to consume as little space as possible
  applyLayout(events) {
    super.applyLayout(events, (event, j, slot, slotSize) => {
      event.height = slotSize;
      event.top = slot.start + j * slotSize;
    });
    events.forEach(event => {
      Object.assign(event, this.bandIndexToPxConvertFn.call(this.bandIndexToPxConvertThisObj || this, event.top, event.height, event.eventRecord, event.resourceRecord));
    });
  }

}
HorizontalLayoutPack._$name = 'HorizontalLayoutPack';

/**
 * @module Scheduler/view/mixin/SchedulerEventRendering
 */

/**
 * Functions to handle event rendering (EventModel -> dom elements).
 *
 * @mixin
 */

var SchedulerEventRendering = (Target => class SchedulerEventRendering extends (Target || Base) {
  static get $name() {
    return 'SchedulerEventRendering';
  } //region Default config

  static get configurable() {
    return {
      /**
       * Get/set overlap mode. See {#config-eventLayout} config, valid values are stack (horizontal), pack, mixed
       * (vertical) and none
       * @member {String} eventLayout
       * @category Scheduled events
       */

      /**
       * How to handle overlapping events. Valid values are:
       * - stack, adjusts row height (only horizontal)
       * - pack, adjusts event height
       * - mixed, allows two events to overlap, more packs (only vertical)
       * - none, allows events to overlap
       * @config {String}
       * @default
       * @category Scheduled events
       */
      eventLayout: 'stack'
    };
  }

  static get defaultConfig() {
    return {
      /**
       * An empty function by default, but provided so that you can override it. This function is called each time
       * an event is rendered into the schedule to render the contents of the event. It's called with the event,
       * its resource and a `renderData` object which allows you to populate data placeholders inside the event
       * template. **IMPORTANT** You should never modify any data on the EventModel inside this method.
       *
       * By default, the DOM markup of an event bar includes placeholders for 'cls' and 'style'. The cls property
       * is a {@link Core.helper.util.DomClassList} which will be added to the event element. The style property
       * is an inline style declaration for the event element.
       *
       * IMPORTANT: When returning content, be sure to consider how that content should be encoded to avoid XSS
       * (Cross-Site Scripting) attacks. This is especially important when including user-controlled data such as
       * the event's `name`. The function {@link Core.helper.StringHelper#function-encodeHtml-static} as well as
       * {@link Core.helper.StringHelper#function-xss-static} can be helpful in these cases.
       *
       * ```javascript
       *  eventRenderer({ eventRecord, resourceRecord, renderData }) {
       *      renderData.style = 'color:white';                 // You can use inline styles too.
       *
       *      // Property names with truthy values are added to the resulting elements CSS class.
       *      renderData.cls.isImportant = this.isImportant(eventRecord);
       *      renderData.cls.isModified = eventRecord.isModified;
       *
       *      // Remove a class name by setting the property to false
       *      renderData.cls[scheduler.generatedIdCls] = false;
       *
       *      // Or, you can treat it as a string, but this is less efficient, especially
       *      // if your renderer wants to *remove* classes that may be there.
       *      renderData.cls += ' extra-class';
       *
       *      return StringHelper.xss`${DateHelper.format(eventRecord.startDate, 'YYYY-MM-DD')}: ${eventRecord.name}`;
       *  }
       * ```
       *
       * @param {Object} detail An object containing the information needed to render an Event.
       * @param {Scheduler.model.EventModel} detail.eventRecord The event record.
       * @param {Scheduler.model.ResourceModel} detail.resourceRecord The resource record.
       * @param {Scheduler.model.AssignmentModel} detail.assignmentRecord The assignment record.
       * @param {Object} detail.renderData An object containing details about the event rendering.
       * @param {Scheduler.model.EventModel} detail.renderData.event The event record.
       * @param {Core.helper.util.DomClassList|String} detail.renderData.cls An object whose property names
       * represent the CSS class names to be added to the event bar element. Set a property's value to truthy or
       * falsy to add or remove the class name based on the property name. Using this technique, you do not have
       * to know whether the class is already there, or deal with concatenation.
       * @param {Core.helper.util.DomClassList|String} detail.renderData.wrapperCls An object whose property names
       * represent the CSS class names to be added to the event wrapper element. Set a property's value to truthy
       * or falsy to add or remove the class name based on the property name. Using this technique, you do not
       * have to know whether the class is already there, or deal with concatenation.
       * @param {Core.helper.util.DomClassList|String} detail.renderData.iconCls An object whose property names
       * represent the CSS class names to be added to an event icon element.
       *
       * Note that an element carrying this icon class is injected into the event element *after*
       * the renderer completes, *before* the renderer's created content.
       *
       * To disable this if the renderer takes full control and creates content using the iconCls,
       * you can set `renderData.iconCls = null`.
       * @param {Number} detail.renderData.left Vertical offset position (in pixels) on the time axis.
       * @param {Number} detail.renderData.width Width in pixels of the event element.
       * @param {Number} detail.renderData.height Height in pixels of the event element.
       * @param {String} detail.renderData.eventStyle The `eventStyle` of the event. Use this to apply custom
       * styles to the event DOM element
       * @param {String} detail.renderData.eventColor The `eventColor` of the event. Use this to set a custom
       * color for the rendered event
       * @param {Object[]} detail.renderData.children An array of DOM configs used as children to the
       * `b-sch-event` element. Can be populated with additional DOM configs to have more control over contents.
       * @returns {String|Object} A simple string, or a custom object which will be applied to the
       * {@link #config-eventBodyTemplate}, creating the actual HTML
       * @config {function}
       * @category Scheduled events
       */
      eventRenderer: null,

      /**
       * How to align milestones in relation to their startDate. Only applies when using a `milestoneLayoutMode`
       * other than `default`. Valid values are:
       * * start
       * * center (default)
       * * end
       * @config {String}
       * @default
       */
      milestoneAlign: 'center',

      /**
       * Factor representing the average char width in pixels used to determine milestone width when configured
       * with `milestoneLayoutMode: 'estimate'`.
       * @config {Number}
       * @default
       */
      milestoneCharWidth: 10,
      // This is determined by styling, in the future it should be measured
      milestoneMinWidth: 40,

      /**
       * `this` reference for the {@link #config-eventRenderer} function
       * @config {Object}
       * @category Scheduled events
       */
      eventRendererThisObj: null,

      /**
       * The class responsible for the packing horizontal event layout process.
       * Override this to take control over the layout process.
       * @config {Scheduler.eventlayout.HorizontalLayout}
       * @default
       * @private
       * @category Misc
       */
      horizontalLayoutPackClass: HorizontalLayoutPack,

      /**
       * The class name responsible for the stacking horizontal event layout process.
       * Override this to take control over the layout process.
       * @config {Scheduler.eventlayout.HorizontalLayout}
       * @default
       * @private
       * @category Misc
       */
      horizontalLayoutStackClass: HorizontalLayoutStack,

      /**
       * Override this method to provide a custom sort function to sort any overlapping events. By default,
       * overlapping events are laid out based on the start date. If the start date is equal, events with earlier
       * end date go first.
       *
       * Here's a sample sort function, sorting on start- and end date. If this function returns -1, then event a
       * is placed above event b.
       * ```javascript
       * horizontalEventSorterFn(a, b) {
       *
       *   const startA = a.startDate, endA = a.endDate;
       *   const startB = b.startDate, endB = b.endDate;
       *
       *   const sameStart = (startA - startB === 0);
       *
       *   if (sameStart) {
       *     return endA > endB ? -1 : 1;
       *   } else {
       *     return (startA < startB) ? -1 : 1;
       *   }
       * }
       * ```
       * @param  {Scheduler.model.EventModel} a First event
       * @param  {Scheduler.model.EventModel} b Second event
       * @return {Number} Return -1 to display a above b, 1 for b above a
       * @config {function}
       * @category Misc
       */
      horizontalEventSorterFn: null,

      /**
       * Field from EventModel displayed as text in the bar when rendering
       * @config {String}
       * @default
       * @category Scheduled events
       */
      eventBarTextField: 'name',

      /**
       * The template used to generate the markup of your events in the scheduler. To 'populate' the
       * eventBodyTemplate with data, use the {@link #config-eventRenderer} method
       * @config {Function}
       * @category Scheduled events
       */
      eventBodyTemplate: null,
      eventPositionMode: 'translate',
      eventScrollMode: 'move',

      /**
       * Specify `true` to force rendered events to fill entire ticks. This only affects rendering, events retain
       * their set start and end dates on the data level. When enabling this config you should probably also
       * disable EventDrag and EventResize, otherwise their behaviour will not be what a user expects.
       * @config {Boolean}
       * @default
       * @category Scheduled events
       */
      fillTicks: false,

      /**
       * By default scheduler fades events in on load. Specify `false` to prevent this animation or specify one
       * of the available animation types to use it (`true` equals `'fade-in'`):
       * * fade-in (default)
       * * slide-from-left
       * * slide-from-top
       * ```
       * // Slide events in from the left on load
       * scheduler = new Scheduler({
       *     useInitialAnimation : 'slide-from-left'
       * });
       * ```
       * @config {Boolean|String}
       * @default
       * @category Misc
       */
      useInitialAnimation: true,

      /**
       * A config object used to configure the resource columns in vertical mode.
       * See {@link Scheduler.view.ResourceHeader} for more details on available properties.
       *
       * ```
       * new Scheduler({
       *   resourceColumns : {
       *     columnWidth    : 100,
       *     headerRenderer : ({ resourceRecord }) => `${resourceRecord.id} - ${resourceRecord.name}`
       *   }
       * })
       * ```
       * @config {Object}
       * @category Resources
       */
      resourceColumns: null,

      /**
       * Path to load resource images from. Used by the resource header in vertical mode and the
       * {@link Scheduler.column.ResourceInfoColumn} in horizontal mode. Set this to display miniature
       * images for each resource using their `image` or `imageUrl` fields.
       *
       * * `image` represents image name inside the specified `resourceImagePath`,
       * * `imageUrl` represents fully qualified image URL.
       *
       *  If set and a resource has no `imageUrl` or `image` specified it will try show miniature using
       *  the resource's name with {@link #config-resourceImageExtension} appended.
       *
       * **NOTE**: The path should end with a `/`:
       *
       * ```
       * new Scheduler({
       *   resourceImagePath : 'images/resources/'
       * });
       * ```
       * @config {String}
       * @category Resources
       */
      resourceImagePath: null,

      /**
       * Generic resource image, used when provided `imageUrl` or `image` fields or path calculated from resource name are all invalid.
       * @default
       * @config {String}
       * @category Resources
       */
      defaultResourceImageName: 'none.png',

      /**
       * Resource image extension, used when creating image path from resource name.
       * @default
       * @config {String}
       * @category Resources
       */
      resourceImageExtension: '.jpg',

      /**
       * Controls how much space to leave between stacked event bars in px.
       *
       * Can be configured per resource by setting {@link Scheduler.model.mixin.ResourceModelMixin#field-barMargin resource.barMargin}.
       *
       * @config {Number} barMargin
       * @default
       * @category Scheduled events
       */

      /**
       * Control how much space to leave between the first event/last event and the resources edge (top/bottom
       * margin within the resource row in horizontal mode, left/right margin within the resource column in
       * vertical mode), in px. Defaults to the value of {@link Scheduler.view.mixin.TimelineEventRendering#config-barMargin}.
       *
       * Can be configured per resource by setting {@link Scheduler.model.mixin.ResourceModelMixin#field-resourceMargin resource.resourceMargin}.
       *
       * @config {Number}
       * @category Scheduled events
       */
      resourceMargin: null,
      // Used to animate events on first render
      isFirstRender: true,
      initialAnimationDuration: 2000
    };
  } //endregion
  //region Properties

  /**
   * Property for {@link #config-useInitialAnimation useInitialAnimation}
   * @property {Boolean|String}
   * @name useInitialAnimation
   */
  //endregion
  //region Init

  get eventPrefix() {
    return this._eventPrefix;
  }

  set eventPrefix(eventPrefix) {
    this._eventPrefix = eventPrefix || this.id + '-';
  } //endregion
  //region Settings

  updateEventLayout(eventLayout, oldEventLayout) {
    const me = this;

    if (oldEventLayout) {
      me.element.classList.remove(`b-eventlayout-${oldEventLayout}`);
    }

    me.element.classList.add(`b-eventlayout-${eventLayout}`);

    if (!me.isConfiguring) {
      me.refreshWithTransition();
    }
  }
  /**
   * Get event layout handler. The handler decides the vertical placement of events within a resource.
   * Returns null if no eventLayout is used (if {@link #config-eventLayout} is set to "none")
   * @internal
   * @returns {Scheduler.eventlayout.HorizontalLayout}
   * @readonly
   * @category Scheduled events
   */

  getEventLayoutHandler(eventLayout) {
    const me = this,
          {
      timeAxisViewModel,
      horizontal
    } = me;

    if (!me.isHorizontal) {
      return null;
    }

    if (!me.layouts) {
      me.layouts = {};
    }

    switch (eventLayout) {
      // stack, adjust row height to fit all events
      case 'stack':
        {
          if (me.horizontalLayoutStackClass) {
            me.layouts.horizontalStack = new me.horizontalLayoutStackClass({
              scheduler: me,
              timeAxisViewModel,
              bandIndexToPxConvertFn: horizontal.layoutEventVerticallyStack,
              bandIndexToPxConvertThisObj: horizontal
            });
          }

          return me.layouts.horizontalStack;
        }
      // pack, fit all events in available height by adjusting their height

      case 'pack':
        {
          if (me.horizontalLayoutPackClass) {
            me.layouts.horizontalPack = new me.horizontalLayoutPackClass({
              scheduler: me,
              timeAxisViewModel,
              bandIndexToPxConvertFn: horizontal.layoutEventVerticallyPack,
              bandIndexToPxConvertThisObj: horizontal
            });
          }

          return me.layouts.horizontalPack;
        }

      default:
        return null;
    }
  }
  /**
   * Get/set fillTicks setting. If set to true it forces the rendered events to fill entire ticks.
   * @property {String}
   * @category Scheduled events
   */

  get fillTicks() {
    return this._fillTicks;
  }

  set fillTicks(fill) {
    if (fill !== this._fillTicks) {
      this._fillTicks = fill;

      if (!this.isConfiguring) {
        this.refreshWithTransition();
      }
    }
  }
  /**
   * Control how much space to leave between the first event/last event and the resources edge (top/bottom margin
   * within the resource row in horizontal mode, left/right margin within the resource column in vertical mode),
   * in px. Defaults to the value of {@link Scheduler.view.mixin.TimelineEventRendering#config-barMargin}.
   *
   * Can be configured per resource by setting {@link Scheduler.model.mixin.ResourceModelMixin#field-resourceMargin resource.resourceMargin}.
   *
   * @member {Number} resourceMargin
   * @category Scheduled events
   */

  get useInitialAnimation() {
    return this._useInitialAnimation;
  }

  set useInitialAnimation(name) {
    const me = this;

    if (me._useInitialAnimation) {
      me.element.classList.remove(`b-initial-${me._useInitialAnimation}`);
    }

    me._useInitialAnimation = name === true ? 'fade-in' : name;

    if (name) {
      me.element.classList.add(`b-initial-${me._useInitialAnimation}`); // Transition block for FF, to not interfere with animations

      if (BrowserHelper.isFirefox) {
        me.element.classList.add('b-prevent-event-transitions');
      }
    }
  }

  set horizontalEventSorterFn(fn) {
    this._horizontalEventSorterFn = fn;

    if (this.rendered) {
      this.refreshWithTransition();
    }
  }

  get horizontalEventSorterFn() {
    return this._horizontalEventSorterFn;
  } //endregion
  //region Resource header/columns
  // NOTE: The configs below are initially applied to the resource header in `TimeAxisColumn#set mode`

  set resourceColumns(config) {
    this._resourceColumns = config;
  }
  /**
   * Use it to manipulate resource column properties at runtime.
   * @property {Scheduler.view.ResourceHeader}
   * @readonly
   */

  get resourceColumns() {
    var _this$timeAxisColumn;

    return ((_this$timeAxisColumn = this.timeAxisColumn) === null || _this$timeAxisColumn === void 0 ? void 0 : _this$timeAxisColumn.resourceColumns) || this._resourceColumns;
  }
  /**
   * Get resource column width. Only applies to vertical mode. To set it, assign to
   * `scheduler.resourceColumns.columnWidth`.
   * @property {Number}
   * @readonly
   */

  get resourceColumnWidth() {
    return this.resourceColumns ? this.resourceColumns.columnWidth : null;
  } //endregion
  //region Event rendering
  // Chainable function called with the events to render for a specific resource. Allows features to add/remove.
  // Chained by ResourceTimeRanges

  getEventsToRender(resource, events) {
    return events;
  }
  /**
   * Rerenders events for specified resource (by rerendering the entire row).
   * @param {Scheduler.model.ResourceModel} resourceRecord
   */

  repaintEventsForResource(resourceRecord) {
    this.currentOrientation.repaintEventsForResource(resourceRecord);
  }
  /**
   * Rerenders the events for all resources connected to the specified event
   * @param {Scheduler.model.EventModel} eventRecord
   * @private
   */

  repaintEvent(eventRecord) {
    const resources = this.eventStore.getResourcesForEvent(eventRecord);
    resources.forEach(resourceRecord => this.repaintEventsForResource(resourceRecord));
  } // Returns a resource specific resourceMargin, falling back to Schedulers setting
  // This fn could be made public to allow hooking it as an alternative to only setting this in data

  getResourceMargin(resourceRecord) {
    var _resourceRecord$resou;

    return (_resourceRecord$resou = resourceRecord === null || resourceRecord === void 0 ? void 0 : resourceRecord.resourceMargin) !== null && _resourceRecord$resou !== void 0 ? _resourceRecord$resou : this.resourceMargin;
  } // Returns a resource specific barMargin, falling back to Schedulers setting
  // This fn could be made public to allow hooking it as an alternative to only setting this in data

  getBarMargin(resourceRecord) {
    var _resourceRecord$barMa;

    return (_resourceRecord$barMa = resourceRecord === null || resourceRecord === void 0 ? void 0 : resourceRecord.barMargin) !== null && _resourceRecord$barMa !== void 0 ? _resourceRecord$barMa : this.barMargin;
  } // Returns a resource specific rowHeight, falling back to Schedulers setting
  // Prio order: Height requested by renderer, height from record, configured height
  // This fn could be made public to allow hooking it as an alternative to only setting this in data

  getResourceHeight(resourceRecord) {
    var _ref, _row$maxRequestedHeig;

    const row = this.getRowById(resourceRecord);
    return (_ref = (_row$maxRequestedHeig = row === null || row === void 0 ? void 0 : row.maxRequestedHeight) !== null && _row$maxRequestedHeig !== void 0 ? _row$maxRequestedHeig : resourceRecord.rowHeight) !== null && _ref !== void 0 ? _ref : this.rowHeight;
  } // Combined convenience getter for destructuring on calling side

  getResourceLayoutSettings(resourceRecord) {
    return {
      resourceMargin: this.getResourceMargin(resourceRecord),
      barMargin: this.getBarMargin(resourceRecord),
      rowHeight: this.getResourceHeight(resourceRecord)
    };
  } //endregion
  //region Template

  /**
   * Generates data used in the template when rendering an event. For example which css classes to use. Also applies
   * #eventBodyTemplate and calls the {@link #config-eventRenderer}.
   * @private
   * @param {Scheduler.model.EventModel} eventRecord Event to generate data for
   * @param {Scheduler.model.ResourceModel} resourceRecord Events resource
   * @param {Boolean|Object} includeOutside Specify true to get boxes for timespans outside of the rendered zone in both
   * dimensions. This option is used when calculating dependency lines, and we need to include routes from timespans
   * which may be outside the rendered zone.
   * @param {Boolean} includeOutside.timeAxis Pass as `true` to include timespans outside of the TimeAxis's bounds
   * @param {Boolean} includeOutside.viewport Pass as `true` to include timespans outside of the vertical timespan viewport's bounds.
   * @returns {Object} Data to use in event template, or `undefined` if the event is outside of the rendered zone.
   */

  generateRenderData(eventRecord, resourceRecord, includeOutside = {
    viewport: true
  }) {
    // TODO: Change this fn to accept an assignment instead of event + resource
    const me = this,
          // generateRenderData calculates layout for events which are outside of the vertical viewport
    // because the RowManager needs to know a row height.
    renderData = me.currentOrientation.getTimeSpanRenderData(eventRecord, resourceRecord, includeOutside),
          {
      isEvent,
      isMilestone
    } = eventRecord,
          assignmentRecord = isEvent && eventRecord.assignments.find(a => a.resourceId === resourceRecord.id),
          // Events inner element, will be populated by renderer and/or eventBodyTemplate
    eventContent = {
      className: 'b-sch-event-content',
      dataset: {
        taskBarFeature: 'content'
      }
    };

    if (renderData) {
      var _renderData$iconCls2;

      let resizable = eventRecord.isResizable;

      if (renderData.startsOutsideView) {
        if (resizable === true) resizable = 'end';else if (resizable === 'start') resizable = false;
      }

      if (renderData.endsOutsideView) {
        if (resizable === true) resizable = 'start';else if (resizable === 'end') resizable = false;
      } // Event record cls properties are now DomClassList instances, so clone them
      // so that they can be manipulated here and by renderers.
      // Truthy value means the key will be added as a class name.
      // ResourceTimeRanges applies custom cls to wrapper.

      const clsList = eventRecord.isResourceTimeRange ? new DomClassList() : eventRecord.cls.clone(),
            wrapperClsList = eventRecord.isResourceTimeRange ? eventRecord.cls.clone() : new DomClassList();
      Object.assign(clsList, {
        [resourceRecord.cls]: resourceRecord.cls,
        [me.generatedIdCls]: eventRecord.hasGeneratedId,
        // Boolean needed here, otherwise DomSync will dig into comparing the modifications
        [me.dirtyCls]: Boolean(eventRecord.hasPersistableChanges || assignmentRecord && assignmentRecord.hasPersistableChanges),
        [me.committingCls]: eventRecord.isCommitting,
        [me.endsOutsideViewCls]: renderData.endsOutsideView,
        [me.startsOutsideViewCls]: renderData.startsOutsideView,
        'b-clipped-start': renderData.clippedStart,
        'b-clipped-end': renderData.clippedEnd
      });
      Object.assign(wrapperClsList, {
        [`${me.eventCls}-parent`]: resourceRecord.isParent
      });
      renderData.wrapperStyle = ''; // Event specifics, things that do not apply to ResourceTimeRanges

      if (isEvent) {
        const selected = assignmentRecord && me.isAssignmentSelected(assignmentRecord);
        Object.assign(clsList, {
          [me.eventCls]: 1,
          'b-milestone': isMilestone,
          'b-sch-event-narrow': renderData.width < 10,
          [me.fixedEventCls]: eventRecord.isDraggable === false,
          [`b-sch-event-resizable-${resizable}`]: Boolean(me.features.eventResize),
          [me.eventSelectedCls]: selected,
          [me.eventAssignHighlightCls]: me.isEventSelected(eventRecord) && !selected,
          'b-recurring': eventRecord.isRecurring,
          'b-occurrence': eventRecord.isOccurrence
        });
        renderData.eventId = eventRecord.id;
        const eventStyle = eventRecord.eventStyle || resourceRecord.eventStyle || me.eventStyle,
              eventColor = eventRecord.eventColor || resourceRecord.eventColor || me.eventColor,
              hasAnimation = me.isFirstRender && me.useInitialAnimation;
        Object.assign(wrapperClsList, {
          [`${me.eventCls}-wrap`]: 1,
          'b-milestone-wrap': isMilestone
        });

        if (hasAnimation) {
          const index = renderData.row ? renderData.row.index : renderData.top / me.tickSize;
          renderData.wrapperStyle = `animation-delay: ${index / 20}s;`; // Further initial animations are prevented when the first one has finished

          if (!me.initialAnimationDetacher) {
            me.initialAnimationDetacher = EventHelper.on({
              element: me.foregroundCanvas,
              delegate: me.eventSelector,
              animationend: 'stopInitialAnimation',
              // Fallback in case animation is interrupted
              expires: {
                alt: 'stopInitialAnimation',
                delay: me.initialAnimationDuration
              },
              thisObj: me
            });
          }
        }

        renderData.eventColor = eventColor;
        renderData.eventStyle = eventStyle; // TODO: Deprecate assignment, use assignmentRecord

        renderData.assignmentRecord = renderData.assignment = assignmentRecord;
      } // If not using a wrapping div, this cls will be added to event div for correct rendering

      renderData.wrapperCls = wrapperClsList;
      renderData.cls = clsList;
      renderData.iconCls = new DomClassList(eventRecord.get(me.eventBarIconClsField) || eventRecord.iconCls); // ResourceTimeRanges applies custom style to the wrapper

      if (eventRecord.isResourceTimeRange) {
        renderData.style = '';
        renderData.wrapperStyle += eventRecord.style || '';
      } // Others to inner
      else {
          renderData.style = eventRecord.style || '';
        } // TODO: Deprecate resource in favor of resourceRecord

      renderData.resource = renderData.resourceRecord = resourceRecord;
      renderData.resourceId = renderData.rowId;

      if (isEvent) {
        let childContent = null,
            milestoneLabelConfig = null,
            value;

        if (me.eventRenderer) {
          // User has specified a renderer fn, either to return a simple string, or an object intended for the eventBodyTemplate
          const rendererValue = me.eventRenderer.call(me.eventRendererThisObj || me, {
            eventRecord,
            resourceRecord,
            assignmentRecord: renderData.assignmentRecord,
            tplData: renderData,
            renderData
          }); // If the user's renderer coerced it into a string, recreate a DomClassList.

          if (typeof renderData.cls === 'string') {
            renderData.cls = new DomClassList(renderData.cls);
          }

          if (typeof renderData.wrapperCls === 'string') {
            renderData.wrapperCls = new DomClassList(renderData.wrapperCls);
          } // Same goes for iconCls

          if (typeof renderData.iconCls === 'string') {
            renderData.iconCls = new DomClassList(renderData.iconCls);
          }

          if (me.eventBodyTemplate) {
            value = me.eventBodyTemplate(rendererValue);
          } else {
            value = rendererValue;
          }
        } else if (me.eventBodyTemplate) {
          // User has specified an eventBodyTemplate, but no renderer - just apply the entire event record data.
          value = me.eventBodyTemplate(eventRecord);
        } else if (me.eventBarTextField) {
          // User has specified a field in the data model to read from
          value = StringHelper.encodeHtml(eventRecord[me.eventBarTextField] || '');
        }

        if (!me.eventBodyTemplate) {
          var _renderData$iconCls;

          eventContent.children = []; // Give milestone a dedicated label element so we can use padding

          if (isMilestone && me.milestoneLayoutMode === 'default' && value != null && value !== '') {
            eventContent.children.unshift(milestoneLabelConfig = {
              tag: 'label',
              children: []
            });
          }

          if ((_renderData$iconCls = renderData.iconCls) !== null && _renderData$iconCls !== void 0 && _renderData$iconCls.length) {
            eventContent.children.unshift({
              tag: 'i',
              className: renderData.iconCls
            });
          } // Array, assumed to contain DOM configs for eventContent children (or milestone label)

          if (Array.isArray(value)) {
            (milestoneLabelConfig || eventContent).children.push(...value);
          } // Likely HTML content
          else if (StringHelper.isHtml(value)) {
              childContent = {
                tag: 'span',
                // div causes wrapping
                html: value
              };
            } // DOM config or plain string can be used as is
            else if (typeof value === 'string' || typeof value === 'object') {
                childContent = value;
              } // Other, use string
              else if (value != null) {
                  childContent = String(value);
                } // Must allow empty string as valid content

          if (childContent != null) {
            // Milestones have content in their label, other events in their "body"
            (milestoneLabelConfig || eventContent).children.push(childContent);
            renderData.cls.add('b-has-content');
          }

          if (eventContent.children.length) {
            renderData.children.push(eventContent);
          }
        } else {
          eventContent.html = value;
          renderData.children.push(eventContent);
        }

        const {
          eventStyle,
          eventColor
        } = renderData; // Renderers have last say on style & color

        renderData.wrapperCls[`b-sch-style-${eventStyle}`] = eventStyle;

        if (eventColor !== null && eventColor !== void 0 && eventColor.startsWith('#')) {
          const colorProp = eventStyle ? 'color' : 'background-color';
          renderData.style = `${colorProp}:${eventColor};` + renderData.style;
          renderData.wrapperCls['b-sch-custom-color'] = 1;
        } // Normally the color is applied as a class to the wrapper
        else {
            renderData.wrapperCls[`b-sch-color-${eventColor}`] = eventColor;
          } // Milestones has to apply styling to b-sch-event-content

        if (renderData.style && isMilestone && eventContent) {
          eventContent.style = renderData.style;
          delete renderData.style;
        }
      } // If there are any iconCls entries...

      renderData.cls['b-sch-event-withicon'] = (_renderData$iconCls2 = renderData.iconCls) === null || _renderData$iconCls2 === void 0 ? void 0 : _renderData$iconCls2.length; // For comparison in sync, cheaper than comparing DocumentFragments

      renderData.eventContent = eventContent;
      renderData.wrapperChildren = []; // Method which features may chain in to

      me.onEventDataGenerated(renderData);
    }

    return renderData;
  }
  /**
   * A method which may be chained by features. It is called when an event's render
   * data is calculated so that features may update the style, class list or body.
   * @param {Object} eventData
   */

  onEventDataGenerated(eventData) {} //endregion
  //region Animation

  /**
   * Restarts initial events animation with new value {@link #config-useInitialAnimation}.
   * @param {Boolean|String} initialAnimation new initial animation value
   */

  restartInitialAnimation(initialAnimation) {
    var _me$initialAnimationD;

    const me = this;
    (_me$initialAnimationD = me.initialAnimationDetacher) === null || _me$initialAnimationD === void 0 ? void 0 : _me$initialAnimationD.call(me);
    me.initialAnimationDetacher = null;
    me.useInitialAnimation = initialAnimation;
    me.isFirstRender = true;
    me.refresh();
  }

  stopInitialAnimation() {
    const me = this;
    me.initialAnimationDetacher();
    me.isFirstRender = false; // Prevent any further initial animations

    me.useInitialAnimation = false; // Remove transition block for FF a bit later, to not interfere with animations

    if (BrowserHelper.isFirefox) {
      me.setTimeout(() => me.element.classList.remove('b-prevent-event-transitions'), 100);
    }
  } //endregion
  // region milestones

  /**
   * Determines width of a milestones label. How width is determined is decided by configuring Scheduler#milestoneLayoutMode.
   * Please note that currently text width is always determined using EventModel#name.
   * @param {Scheduler.model.EventModel} eventRecord
   * @returns {Number}
   */

  getMilestoneLabelWidth(eventRecord) {
    const me = this,
          mode = me.milestoneLayoutMode;

    if (mode === 'measure') {
      const element = me.milestoneMeasureElement || (me.milestoneMeasureElement = DomHelper.createElement({
        className: 'b-sch-event-wrap b-milestone-wrap b-measure',
        children: [{
          className: 'b-sch-event b-milestone',
          children: [{
            className: 'b-sch-event-content'
          }]
        }],
        parent: me.foregroundCanvas
      })); // DomSync should not touch

      element.retainElement = true;
      element.firstElementChild.firstElementChild.innerHTML = eventRecord.name;
      return element.offsetWidth;
    }

    if (mode === 'estimate') {
      return Math.max(eventRecord.name.length * me.milestoneCharWidth, me.milestoneMinWidth);
    }

    if (mode === 'data') {
      return Math.max(eventRecord.milestoneWidth, me.milestoneMinWidth);
    }

    return 0;
  }

  set milestoneLayoutMode(mode) {
    this._milestoneLayoutMode = mode;
    this.element.classList[mode !== 'default' ? 'add' : 'remove']('b-sch-layout-milestones');

    if (!this.isConfiguring) {
      this.refreshWithTransition();
    }
  }

  get milestoneLayoutMode() {
    return this._milestoneLayoutMode;
  }
  /**
   * Get/set how to align milestones in relation to their startDate. Only applies when using a `milestoneLayoutMode`
   * other than `default`. Valid values are:
   * * start
   * * center (default)
   * * end
   * @property {String}
   */

  set milestoneAlign(align) {
    this._milestoneAlign = align;

    if (!this.isConfiguring) {
      this.refreshWithTransition();
    }
  }

  get milestoneAlign() {
    return this._milestoneAlign;
  }

  set milestoneCharWidth(width) {
    this._milestoneCharWidth = width;

    if (!this.isConfiguring) {
      this.refreshWithTransition();
    }
  }

  get milestoneCharWidth() {
    return this._milestoneCharWidth;
  } // endregion
  // This does not need a className on Widgets.
  // Each *Class* which doesn't need 'b-' + constructor.name.toLowerCase() automatically adding
  // to the Widget it's mixed in to should implement thus.

  get widgetClass() {}

});

/**
 * @module Scheduler/view/mixin/SchedulerStores
 */

/**
 * Functions for store assignment and store event listeners.
 *
 * @mixin
 * @extends Scheduler/data/mixin/ProjectConsumer
 */

var SchedulerStores = (Target => class SchedulerStores extends ProjectConsumer(Target || Base) {
  static get $name() {
    return 'SchedulerStores';
  } //region Default config
  // This is the static definition of the Stores we consume from the project, and
  // which we must provide *TO* the project if we or our CrudManager is configured
  // with them.
  // The property name is the store name, and within that there is the dataname which
  // is the property which provides static data definition. And there is a listeners
  // definition which specifies the listeners *on this object* for each store.
  //
  // To process incoming stores, implement an updateXxxxxStore method such
  // as `updateEventStore(eventStore)`.
  //
  // To process an incoming Project implement `updateProject`. __Note that
  // `super.updateProject(...arguments)` must be called first.__

  static get projectStores() {
    return {
      resourceStore: {
        dataName: 'resources'
      },
      eventStore: {
        dataName: 'events',
        listeners: {
          changePreCommit: 'onInternalEventStoreChange',
          clearChanges: 'onEventClearChanges',
          beforeCommit: 'onEventBeforeCommit',
          commit: 'onEventCommit',
          exception: 'onEventException',
          idchange: 'onEventIdChange',
          beforeLoad: 'applyStartEndParameters'
        }
      },
      assignmentStore: {
        dataName: 'assignments',
        listeners: {
          changePreCommit: 'onAssignmentChange',
          // In EventSelection.js
          beforeRemove: {
            fn: 'onAssignmentBeforeRemove',
            // We must go last in case an app vetoes a remove
            // by returning false from a handler.
            prio: -1000
          }
        }
      },
      dependencyStore: {
        dataName: 'dependencies'
      },
      calendarManagerStore: {},
      resourceTimeRangeStore: {}
    };
  }

  static get configurable() {
    return {
      /**
       * Inline events, will be loaded into an internally created EventStore.
       * @config {Scheduler.model.EventModel[]|Object[]} events
       * @category Data events
       */

      /**
       * The {@link Scheduler.data.EventStore} holding the events to be rendered into the scheduler (required).
       * @config {Scheduler.data.EventStore|Object} eventStore
       * @category Data
       */

      /**
       * Inline resources, will be loaded into an internally created ResourceStore.
       * @config {Scheduler.model.ResourceModel[]|Object[]} resources
       * @category Data
       */

      /**
       * The {@link Scheduler.data.ResourceStore} holding the resources to be rendered into the scheduler (required).
       * @config {Scheduler.data.ResourceStore|Object} resourceStore
       * @category Data
       */

      /**
       * Inline assignments, will be loaded into an internally created AssignmentStore.
       * @config {Scheduler.model.AssignmentModel[]|Object[]} assignments
       * @category Data
       */

      /**
       * The optional {@link Scheduler.data.AssignmentStore}, holding assignments between resources and events.
       * Required for multi assignments.
       * @config {Scheduler.data.AssignmentStore|Object} assignmentStore
       * @category Data
       */

      /**
       * Inline dependencies, will be loaded into an internally created DependencyStore.
       * @config {Scheduler.model.DependencyModel[]|Object[]} dependencies
       * @category Data
       */

      /**
       * The optional {@link Scheduler.data.DependencyStore}.
       * @config {Scheduler.data.DependencyStore|Object} dependencyStore
       * @category Data
       */

      /**
       * Inline time ranges, will be loaded into an internally created store if {@link Scheduler.feature.TimeRanges TimeRanges}
       * is enabled.
       * @config {Scheduler.model.TimeSpan[]|Object[]}
       * @category Data
       */
      timeRanges: null,

      /**
       * Inline resource time ranges, will be loaded into an internally created store if
       * {@link Scheduler.feature.ResourceTimeRanges ResourceTimeRanges} is enabled.
       * @config {Scheduler.model.ResourceTimeRangeModel[]|Object[]}
       * @category Data
       */
      resourceTimeRanges: null,

      /**
       * Overridden to *not* auto create a store at the Scheduler level.
       * The store is the {@link #config-resourceStore} of the backing project
       * @config {Core.data.Store}
       * @private
       */
      store: null,

      /**
       * The name of the start date parameter that will be passed to in every `eventStore` load request.
       * @config {String}
       * @category Data
       */
      startParamName: 'startDate',

      /**
       * The name of the end date parameter that will be passed to in every `eventStore` load request.
       * @config {String}
       * @category Data
       */
      endParamName: 'endDate',

      /**
       * true to apply start and end dates of the current view to any `eventStore` load requests.
       * @config {Boolean}
       * @category Data
       */
      passStartEndParameters: false,

      /**
       * Configure with `true` to also remove the event when removing the last assignment.
       * @config {Boolean}
       * @default
       * @category Misc
       */
      removeUnassignedEvent: true
    };
  } //endregion
  //region Project

  updateProject(project, oldProject) {
    super.updateProject(project, oldProject);
    this.detachListeners('schedulerStores');
    project.on({
      name: 'schedulerStores',
      refresh: 'onProjectRefresh',
      thisObj: this
    });
  } // Called when project changes are committed, before data is written back to records (but still ready to render
  // since data is fetched from engine)

  onProjectRefresh({
    isInitialCommit
  }) {
    const me = this;

    if (isInitialCommit) {
      if (me.isVertical) {
        me.refreshAfterProjectRefresh = false;
        me.refreshWithTransition();
      }
    }

    if (me.navigateToAfterRefresh) {
      me.navigateTo(me.navigateToAfterRefresh);
      me.navigateToAfterRefresh = null;
    }

    if (me.refreshAfterProjectRefresh) {
      me.refreshWithTransition();
      me.refreshAfterProjectRefresh = false;
    }
  } //endregion
  //region Row store

  get store() {
    // Vertical uses a dummy store
    if (!this._store && this.isVertical) {
      // TODO: Make this store readonly, since we are using single cell approach
      this._store = new Store({
        data: [{
          // eslint-disable-next-line quote-props
          'id': 'verticalTimeAxisRow',
          cls: 'b-verticaltimeaxis-row'
        }]
      });
    }

    return super.store;
  }

  set store(store) {
    super.store = store;
  } // Wrap w/ transition refreshFromRowOnStoreAdd() inherited from Grid

  refreshFromRowOnStoreAdd(row, {
    isExpand
  }) {
    const args = arguments;
    this.runWithTransition(() => {
      // Postpone drawing of events for a new resource until the following project refresh. Previously the draw
      // would not happen because engine was not ready, but now when we allow commits and can read values during
      // commit that block is no longer there
      this.currentOrientation.suspended = !isExpand;
      super.refreshFromRowOnStoreAdd(row, ...args);
      this.currentOrientation.suspended = false;
    }, !isExpand);
  }

  onStoreAdd(event) {
    super.onStoreAdd(event);
    this.calculateRowHeights(event.records);
  }

  onStoreUpdateRecord({
    source: store,
    record,
    changes
  }) {
    // Ignore engine changes that do not affect row rendering
    let ignoreCount = 0;

    if ('assigned' in changes) {
      ignoreCount++;
    }

    if ('calendar' in changes) {
      ignoreCount++;
    }

    if (ignoreCount !== Object.keys(changes).length) {
      super.onStoreUpdateRecord(...arguments);
    }
  } //endregion
  //region ResourceStore

  /**
   * Get/set resources, applies to the backing project's ResourceStore.
   * @member {Scheduler.model.ResourceModel[]|Object[]} resources
   * @category Data
   */

  /**
   * Get/set the resource store instance of the backing project
   * @member {Scheduler.data.ResourceStore} resourceStore
   * @category Data
   */

  updateResourceStore(resourceStore) {
    // Reconfigure grid if resourceStore is backing the rows
    if (resourceStore && this.isHorizontal) {
      resourceStore.metaMapId = this.id;
      this.store = resourceStore;
    }
  } //endregion
  //region EventStore

  /**
   * Get/set events, applies to the backing project's EventStore.
   * @member {Scheduler.model.EventModel[]|Object[]} events
   * @category Data
   */

  /**
   * Get/set the event store instance of the backing project.
   * @member {Scheduler.data.EventStore} eventStore
   * @category Data
   */
  //endregion
  //region AssignmentStore

  /**
   * Get/set assignments, applies to the backing project's AssignmentStore.
   * @member {Scheduler.model.AssignmentModel[]|Object[]} assignments
   * @category Data
   */

  /**
   * Get/set the event store instance of the backing project.
   * @member {Scheduler.data.AssignmentStore} assignmentStore
   * @category Data
   */
  //endregion
  //region DependencyStore

  /**
   * Get/set dependencies, applies to the backing projects DependencyStore.
   * @member {Scheduler.model.DependencyModel[]|Object[]} dependencies
   * @category Data
   */

  /**
   * Get/set the dependencies store instance of the backing project.
   * @member {Scheduler.data.DependencyStore} dependencyStore
   * @category Data
   */
  //endregion
  //region CalendarManagerStore
  // TODO: document it when it works. The build process throws with this documented.
  // /**
  //  * Get/set the calendar manager store instance of the backing project
  //  * @member {Engine.store.CalendarManagerStoreMixin} calendarManagerStore
  //  * @category Data
  //  */
  //endregion
  //region Events

  onEventIdChange(params) {
    this.currentOrientation.onEventStoreIdChange && this.currentOrientation.onEventStoreIdChange(params);
  }
  /**
   * Calls appropriate functions for current event layout when the event store is modified.
   * @private
   */
  // Named as Internal to avoid naming collision with wrappers that relay events

  onInternalEventStoreChange(params) {
    // Too early, bail out
    // Also bail out if this is a reassign using resourceId, any updates will be handled by AssignmentStore instead
    if (!this._mode || params.isAssign || this.assignmentStore.isRemovingAssignment) {
      return;
    }

    this.currentOrientation.onEventStoreChange(params);
  }
  /**
   * Refreshes committed events, to remove dirty/committing flag.
   * CSS is added
   * @private
   */

  onEventCommit({
    changes
  }) {
    let resourcesToRepaint = [...changes.added, ...changes.modified].map(eventRecord => this.eventStore.getResourcesForEvent(eventRecord)); // flatten

    resourcesToRepaint = Array.prototype.concat.apply([], resourcesToRepaint); // repaint relevant resource rows

    new Set(resourcesToRepaint).forEach(resourceRecord => this.repaintEventsForResource(resourceRecord));
  }
  /**
   * Adds the committing flag to changed events before commit.
   * @private
   */

  onEventBeforeCommit({
    changes
  }) {
    const {
      currentOrientation,
      committingCls
    } = this; // Committing sets a flag in meta that during event rendering applies a CSS class. But to not mess up drag and
    // drop between resources no redraw is performed before committing, so class is never applied to the element(s).
    // Applying here instead

    [...changes.added, ...changes.modified].forEach(eventRecord => eventRecord.assignments.forEach(assignmentRecord => currentOrientation.toggleCls(assignmentRecord, committingCls, true)));
  } // Clear committing flag

  onEventException({
    action
  }) {
    if (action === 'commit') {
      const {
        changes
      } = this.eventStore;
      [...changes.added, ...changes.modified, ...changes.removed].forEach(eventRecord => this.repaintEvent(eventRecord));
    }
  }
  /**
   * Refreshes scheduler when event changes are cleared.
   * @private
   */

  onEventClearChanges() {
    this.refresh();
  }

  onAssignmentBeforeRemove({
    source,
    records,
    removingAll
  }) {
    if (removingAll) {
      return;
    }

    const me = this;
    let moveTo; // Deassigning the active assignment

    if (!me.isConfiguring && me.activeAssignment && records.includes(me.activeAssignment)) {
      // If being done by a keyboard gesture then look for a close target
      // until we find an existing record, not scheduled for removal.
      // Otherwise, per Mats, push focus outside of the Scheduler.
      if (GlobalEvents.lastInteractionType === 'key') {
        // Look for a close target until we find an existing record, not scheduled for removal
        for (let i = 0, l = records.length; i < l && (!moveTo || records.includes(moveTo)); i++) {
          const assignment = records[i];

          if (assignment.resource && assignment.resource.isModel) {
            moveTo = me.getNext(assignment) || me.getPrevious(assignment);
          }
        }
      } // Move focus away from the element which will soon have no
      // backing data.

      if (moveTo) {
        me.navigateTo(moveTo);
        me.navigateToAfterRefresh = moveTo;
      } // Focus must exit the Scheduler's subgrid, otherwise, if a navigation
      // key gesture is delivered before the outgoing event's element has faded
      // out and been removed, navigation will be attempted from a deleted
      // event. Animated hiding is problematic.
      //
      // We cannot just revertFocus() because that might move focus back to an
      // element in a floating EventEditor which is not yet faded out and
      // been removed. Animated hiding is problematic.
      //
      // We cannot focus scheduler.timeAxisColumn.element because the browser
      // would scroll it in some way if we have horizontal overflow.
      //
      // The only thing we can know about to focus here is the Scheduler itself.
      else {
          DomHelper.focusWithoutScrolling(me.focusElement);
        }
    }
  } // /**
  //  * Repaints affected resources when assignments are removed.
  //  * @private
  //  */
  // onAssignmentRemove({ records }) {
  //     if (this.isHorizontal) {
  //         records.forEach(assignment => {
  //             const resource = assignment.resource;
  //             // Removing a resource removes assignments, if resource is already removed no point in repainting here
  //             resource && !resource.isRemoved && this.repaintEventsForResource(resource);
  //         });
  //     }
  // }
  //
  // /**
  //  * Repaints resources when all assignments are removed.
  //  * @private
  //  */
  // onAssignmentRemoveAll() {
  //     if (this.isHorizontal) {
  //         this.currentOrientation.onAssignmentRemoveAll();
  //     }
  // }
  // /**
  //  * Refreshes scheduler when assignment store is filtered.
  //  */
  // onAssignmentFilter() {
  //     if (this.isHorizontal) {
  //         this.refresh();
  //     }
  // }
  //endregion
  //region TimeRangeStore

  /**
   * Get/set the time ranges store instance for {@link Scheduler.feature.TimeRanges TimeRanges} feature.
   * @property {Core.data.Store}
   * @category Data
   * @name timeRangeStore
   */
  //endregion
  //region ResourceTimeRangeStore

  /**
   * Get/set the resource time ranges store instance for {@link Scheduler.feature.ResourceTimeRanges ResourceTimeRanges} feature.
   * @property {Scheduler.data.ResourceTimeRangeStore}
   * @category Data
   * @name resourceTimeRangeStore
   */

  /**
   * Resource time ranges store instance for {@link Scheduler.feature.ResourceTimeRanges ResourceTimeRanges} feature.
   * @config {Scheduler.data.ResourceTimeRangeStore}
   * @category Data
   * @name resourceTimeRangeStore
   */
  //endregion
  //region Other functions

  /**
   * Applies the start and end date to each event store request (formatted in the same way as the start date, defined
   * in the EventStore Model class).
   * @category Data
   */

  applyStartEndParameters({
    source: eventStore,
    params
  }) {
    const me = this,
          field = eventStore.modelClass.fieldMap.startDate;

    if (me.passStartEndParameters) {
      params[me.startParamName] = field.print(me.startDate);
      params[me.endParamName] = field.print(me.endDate);
    }
  } //endregion
  //region WidgetClass
  // This does not need a className on Widgets.
  // Each *Class* which doesn't need 'b-' + constructor.name.toLowerCase() automatically adding
  // to the Widget it's mixed in to should implement thus.

  get widgetClass() {} //endregion

});

/**
 * @module Scheduler/view/mixin/SchedulerScroll
 */

const immediatePromise$6 = Promise.resolve(),
      defaultScrollOptions$3 = {
  block: 'nearest',
  edgeOffset: 20
},
      unrenderedScrollOptions = {
  highlight: false,
  focus: false
};
/**
 * Functions for scrolling to events, dates etc.
 *
 * @mixin
 */

var SchedulerScroll = (Target => class SchedulerScroll extends (Target || Base) {
  static get $name() {
    return 'SchedulerScroll';
  } //region Scroll to event

  /**
   * Scrolls an event record into the viewport.
   * If the resource store is a tree store, this method will also expand all relevant parent nodes to locate the event.
   *
   * @param {Scheduler.model.EventModel} eventRecord the event record to scroll into view
   * @param {Object} [options] How to scroll.
   * @param {String} [options.block=nearest] How far to scroll the event: `start/end/center/nearest`.
   * @param {Number} [options.edgeOffset=20] edgeOffset A margin *in pixels* around the event to bring into view.
   * @param {Boolean|Number} [options.animate] Set to `true` to animate the scroll, or the number of milliseconds to animate over.
   * @param {Boolean} [options.highlight] Set to `true` to highlight the event element when it is in view.
   * @param {Boolean} [options.focus] Set to `true` to focus the event element when it is in view.
   * @returns {Promise} A Promise which resolves when the scrolling is complete.
   *
   * This function is not applicable for events with multiple assignments, please use #scrollResourceEventIntoView instead.
   */

  scrollEventIntoView(eventRecord, options = defaultScrollOptions$3) {
    const me = this,
          resources = eventRecord.resources || [eventRecord];

    if (resources.length > 1) {
      throw new Error('scrollEventIntoView() is not applicable for events with multiple assignments, please use scrollResourceEventIntoView() instead.');
    }

    if (!resources.length) {
      console.warn('You have asked to scroll to an event which is not assigned to a resource');
      return immediatePromise$6;
    }

    return me.scrollResourceEventIntoView(resources[0], eventRecord, null, options);
  }
  /**
   * Scrolls an assignment record into the viewport.
   *
   * If the resource store is a tree store, this method will also expand all relevant parent nodes
   * to locate the event.
   *
   * @param {Scheduler.model.AssignmentModel} assignmentRecord A resource record an event record is assigned to
   * @param {Number} index DOM node index, applicable only for weekview
   * @param {Object} [options] How to scroll.
   * @param {String} [options.block=nearest] How far to scroll the event: `start/end/center/nearest`.
   * @param {Number} [options.edgeOffset=20] edgeOffset A margin *in pixels* around the event to bring into view.
   * @param {Boolean/Number} [options.animate] Set to `true` to animate the scroll, or the number of milliseconds to animate over.
   * @param {Boolean} [options.extendTimeAxis=true] By default, if the requested event is outside the time axis, the time axis is extended.
   * @param {Boolean} [options.highlight] Set to `true` to highlight the event element when it is in view.
   * @param {Boolean} [options.focus] Set to `true` to focus the event element when it is in view.
   * @returns {Promise} A Promise which resolves when the scrolling is complete.
   */

  scrollAssignmentIntoView(assignmentRecord, ...args) {
    return this.scrollResourceEventIntoView(assignmentRecord.resource, assignmentRecord.event, ...args);
  }
  /**
   * Scrolls a resource event record into the viewport.
   *
   * If the resource store is a tree store, this method will also expand all relevant parent nodes
   * to locate the event.
   *
   * @param {Scheduler.model.ResourceModel} resourceRecord A resource record an event record is assigned to
   * @param {Scheduler.model.EventModel} eventRecord An event record to scroll into view
   * @param {Number} index DOM node index, applicable only for weekview
   * @param {Object} [options] How to scroll.
   * @param {String} [options.block=nearest] How far to scroll the event: `start/end/center/nearest`.
   * @param {Number} [options.edgeOffset=20] edgeOffset A margin *in pixels* around the event to bring into view.
   * @param {Boolean|Number} [options.animate] Set to `true` to animate the scroll, or the number of milliseconds to animate over.
   * @param {Boolean} [options.extendTimeAxis=true] By default, if the requested event is outside the time axis, the time axis is extended.
   * @param {Boolean} [options.highlight] Set to `true` to highlight the event element when it is in view.
   * @param {Boolean} [options.focus] Set to `true` to focus the event element when it is in view.
   * @returns {Promise} A Promise which resolves when the scrolling is complete.
   */

  scrollResourceEventIntoView(resourceRecord, eventRecord, index, options = defaultScrollOptions$3) {
    var _me$expandTo;

    const me = this,
          eventStart = eventRecord.startDate,
          eventEnd = eventRecord.endDate,
          eventIsOutside = eventRecord.isScheduled && eventStart < me.timeAxis.startDate | (eventEnd > me.timeAxis.endDate) << 1;
    let el;

    if (options.edgeOffset == null) {
      options.edgeOffset = 20;
    } // Make sure event is within TimeAxis time span unless extendTimeAxis passed as false.
    // The EventEdit feature passes false because it must not mutate the TimeAxis.
    // Bitwise flag:
    //  1 === start is before TimeAxis start.
    //  2 === end is after TimeAxis end.

    if (eventIsOutside && options.extendTimeAxis !== false) {
      let currentTimeSpanRange = me.timeAxis.endDate - me.timeAxis.startDate,
          startAnchorPoint,
          endAnchorPoint; // Event is too wide, expand the range to encompass it.

      if (eventIsOutside === 3) {
        me.timeAxis.setTimeSpan(new Date(eventStart.valueOf() - currentTimeSpanRange / 2), new Date(eventEnd.getTime() + currentTimeSpanRange / 2));
      } // Event is partially or wholly outside but will fit.
      // Move the TimeAxis to include it. Attempt to maintain visual position.
      else {
          startAnchorPoint = eventIsOutside & 1 ? me.getCoordinateFromDate(eventRecord.endDate) : me.getCoordinateFromDate(eventRecord.startDate); // Event starts before

          if (eventIsOutside & 1) {
            me.timeAxis.setTimeSpan(new Date(eventStart), new Date(eventStart.valueOf() + currentTimeSpanRange));
          } // Event ends after
          else {
              me.timeAxis.setTimeSpan(new Date(eventEnd.valueOf() - currentTimeSpanRange), new Date(eventEnd));
            } // Restore view to same relative scroll position.

          endAnchorPoint = eventIsOutside & 1 ? me.getCoordinateFromDate(eventRecord.endDate) : me.getCoordinateFromDate(eventRecord.startDate);
          me.timeAxisSubGrid.scrollable.scrollBy(endAnchorPoint - startAnchorPoint);
        }
    } // If we're a tree, ensure parents are expanded first

    (_me$expandTo = me.expandTo) === null || _me$expandTo === void 0 ? void 0 : _me$expandTo.call(me, resourceRecord); // Establishing element to scroll to

    el = me.getElementFromEventRecord(eventRecord, resourceRecord);

    if (el) {
      // It's usually the event wrapper that holds focus
      if (!DomHelper.isFocusable(el)) {
        el = el.parentNode;
      }

      const scroller = me.timeAxisSubGrid.scrollable; // Force horizontalscroll to be triggered directly on scroll instead of on next frame, to have events
      // already drawn when promise resolves

      me.timeAxisSubGrid.forceScrollUpdate = true; // Scroll into view with animation and highlighting if needed.

      return scroller.scrollIntoView(el, options);
    } else if (eventIsOutside && options.extendTimeAxis === false) {
      console.warn('You have asked to scroll to an event which is outside the current view and extending timeaxis is disabled');
      return immediatePromise$6;
    } else if (!eventRecord.isOccurrence && !me.eventStore.isAvailable(eventRecord)) {
      console.warn('You have asked to scroll to an event which is not available');
      return immediatePromise$6;
    } else if (eventRecord.isScheduled) {
      // Event scheduled but not rendered, scroll to calculated location
      return me.scrollUnrenderedEventIntoView(resourceRecord, eventRecord, options);
    } else {
      // Event not scheduled, just scroll resource row into view
      return me.scrollResourceIntoView(resourceRecord, options);
    }
  }
  /**
   * Scrolls an unrendered event into view. Internal function used from #scrollResourceEventIntoView.
   * @private
   */

  scrollUnrenderedEventIntoView(resourceRec, eventRec, options = defaultScrollOptions$3) {
    // We must only resolve when the event's element has been painted
    // *and* the scroll has fully completed.
    return new Promise(resolve => {
      const me = this,
            // Knock out highlight and focus options. They must be applied after the scroll
      // has fully completed and we have an element. Use a default edgeOffset of 20.
      modifiedOptions = Object.assign({
        edgeOffset: 20
      }, options, unrenderedScrollOptions),
            scroller = me.timeAxisSubGrid.scrollable,
            box = me.getResourceEventBox(eventRec, resourceRec),
            scrollerViewport = scroller.viewport;

      if (!scrollerViewport) {
        resolve();
        return;
      }

      const targetRect = box.translate(scrollerViewport.x - scroller.x, scrollerViewport.y - scroller.y),
            delta = scroller.getDeltaTo(targetRect, modifiedOptions)[me.isHorizontal ? 'xDelta' : 'yDelta'],
            onEventRender = async ({
        eventRecord,
        element,
        targetElement
      }) => {
        if (eventRecord === eventRec) {
          // Vertical's renderEvent is different to eventPaint
          const el = element || targetElement;
          detatcher(); // Don't resolve until the scroll has fully completed.

          await initialScrollPromise; // If we were scrolling rightwards, then because the rectangle was only *estimated*
          // we must now ensure that the scroll position is fully correct for the *element*.
          // TODO: Needed? The position should be correct with the new rendering

          if (delta > 0) {
            await scroller.scrollIntoView(el, {
              edgeOffset: options.edgeOffset
            });
          }

          options.highlight && DomHelper.highlight(el);
          options.focus && el.focus();
          resolve();
        }
      },
            // On either paint or repaint of the event, resolve the scroll promise and detach the listeners.
      detatcher = me.on({
        renderEvent: onEventRender
      }),
            initialScrollPromise = scroller.scrollIntoView(targetRect, modifiedOptions);
    });
  }
  /**
   * Scrolls the specified resource into view, works for both horizontal and vertical modes.
   * @param {Scheduler.model.ResourceModel} resourceRecord A resource record an event record is assigned to
   * @param {Object} [options] How to scroll.
   * @param {String} [options.column] Field name or ID of the column, or the Column instance to scroll to (in horizontal mode).
   * @param {String} [options.block] How far to scroll the element: `start/end/center/nearest`.
   * @param {Number} [options.edgeOffset] edgeOffset A margin around the element or rectangle to bring into view.
   * @param {Boolean|Number} [options.animate] Set to `true` to animate the scroll, or the number of milliseconds to animate over.
   * @param {Boolean} [options.highlight] Set to `true` to highlight the element when it is in view.
   * @returns {Promise} A promise which is resolved when the scrolling has finished.
   */

  scrollResourceIntoView(resourceRecord, options = defaultScrollOptions$3) {
    if (this.isVertical) {
      return this.currentOrientation.scrollResourceIntoView(resourceRecord, options);
    } else {
      return this.scrollRowIntoView(resourceRecord, options);
    }
  } //endregion
  // This does not need a className on Widgets.
  // Each *Class* which doesn't need 'b-' + constructor.name.toLowerCase() automatically adding
  // to the Widget it's mixed in to should implement thus.

  get widgetClass() {}

});

/**
 * @module Scheduler/view/mixin/SchedulerRegions
 */

/**
 * Functions to get regions (bounding boxes) for scheduler, events etc.
 *
 * @mixin
 */

var SchedulerRegions = (Target => class SchedulerRegions extends (Target || Base) {
  static get $name() {
    return 'SchedulerRegions';
  } //region Orientation depended regions

  /**
   * Gets the region represented by the schedule and optionally only for a single resource. The view will ask the
   * scheduler for the resource availability by calling getResourceAvailability. By overriding that method you can
   * constrain events differently for different resources.
   * @param {Scheduler.model.ResourceModel} resourceRecord (optional) The resource record
   * @param {Scheduler.model.EventModel} eventRecord (optional) The event record
   * @return {Core.helper.util.Rectangle} The region of the schedule
   */

  getScheduleRegion(resourceRecord, eventRecord, local = true) {
    return this.currentOrientation.getScheduleRegion(resourceRecord, eventRecord, local);
  }
  /**
   * Gets the region representing the passed resource and optionally just for a certain date interval.
   * @param {Scheduler.model.ResourceModel} resourceRecord The resource record
   * @param {Date} startDate A start date constraining the region
   * @param {Date} endDate An end date constraining the region
   * @return {Core.helper.util.Rectangle} A Rectangle which encapsulates the resource time span
   */

  getResourceRegion(resourceRecord, startDate, endDate) {
    return this.currentOrientation.getRowRegion(resourceRecord, startDate, endDate);
  } //endregion
  //region ResourceEventBox

  getAssignmentEventBox(assignmentRecord, includesOutside) {
    return this.getResourceEventBox(assignmentRecord.event, assignmentRecord.resource, includesOutside);
  }
  /**
   * Get the region for a specified resources specified event.
   * @param {Scheduler.model.EventModel} eventRecord
   * @param {Scheduler.model.ResourceModel} resourceRecord
   * @param {Boolean} includeOutside Specify true to get boxes for events outside of the rendered zone in both
   *   dimensions. This option is used when calculating dependency lines, and we need to include routes from events
   *   which may be outside the rendered zone.
   * @returns {Core.helper.util.Rectangle}
   */

  getResourceEventBox(eventRecord, resourceRecord, includeOutside = false, roughly = false) {
    return this.currentOrientation.getResourceEventBox(eventRecord, resourceRecord, includeOutside, roughly);
  } //endregion
  //region Item box

  /**
   * Gets box for displayed item designated by the record. If several boxes are displayed for the given item
   * then the method returns all of them. Box coordinates are in view coordinate system.
   *
   * Boxes outside scheduling view timeaxis timespan and inside collapsed rows (if row defining store is a tree store)
   * will not be returned. Boxes outside scheduling view vertical visible area (i.e. boxes above currently visible
   * top row or below currently visible bottom row) will be calculated approximately.
   *
   * @param {Scheduler.model.EventModel} event
   * @return {Object|Object[]}
   * @return {Boolean} return.isPainted Whether the box was calculated for the rendered scheduled record or was
   *    approximately calculated for the scheduled record outside of the current vertical view area.
   * @return {Number} return.top
   * @return {Number} return.bottom
   * @return {Number} return.start
   * @return {Number} return.end
   * @return {String} return.relPos if the item is not rendered then provides a view relative position one of 'before', 'after'
   * @internal
   */

  getItemBox(event, includeOutside = false) {
    return event.resources.map(resource => this.getResourceEventBox(event, resource, includeOutside));
  } //endregion
  // This does not need a className on Widgets.
  // Each *Class* which doesn't need 'b-' + constructor.name.toLowerCase() automatically adding
  // to the Widget it's mixed in to should implement thus.

  get widgetClass() {}

});

/**
 * @module Scheduler/view/mixin/SchedulerState
 */

const copyProperties = ['eventLayout', 'barMargin', 'mode', 'eventColor', 'eventStyle', 'tickSize', 'fillTicks'];
/**
 * Mixin for Scheduler that handles state. It serializes the following scheduler properties:
 *
 * * eventLayout
 * * barMargin
 * * mode
 * * tickSize
 * * zoomLevel
 * * eventColor
 * * eventStyle
 *
 * See {@link Grid.view.mixin.GridState} and {@link Core.mixin.State} for more information on state.
 *
 * @mixin
 */

var SchedulerState = (Target => class SchedulerState extends (Target || Base) {
  static get $name() {
    return 'SchedulerState';
  }
  /**
   * Gets or sets scheduler's state. Check out {@link Scheduler.view.mixin.SchedulerState SchedulerState} mixin for details.
   * @member {Object} state
   */

  /**
   * Get scheduler's current state for serialization. State includes rowHeight, headerHeight, readOnly, selectedCell,
   * selectedRecordId, column states and store state etc.
   * @returns {Object} State object to be serialized
   * @private
   */

  getState() {
    const me = this,
          state = ObjectHelper.copyProperties(super.getState(), me, copyProperties);
    state.zoomLevel = me.zoomLevel;
    state.zoomLevelOptions = {
      startDate: me.startDate,
      endDate: me.endDate,
      viewportCenterDate: me.viewportCenterDate,
      width: me.tickSize
    };
    return state;
  }
  /**
   * Apply previously stored state.
   * @param {Object} state
   * @private
   */

  applyState(state) {
    this.suspendRefresh();

    if (state.zoomLevel) {
      this.zoomToLevel(state.zoomLevel, state.zoomLevelOptions);
    }

    ObjectHelper.copyProperties(this, state, copyProperties);
    super.applyState(state);
    this.resumeRefresh(true);
  } // This does not need a className on Widgets.
  // Each *Class* which doesn't need 'b-' + constructor.name.toLowerCase() automatically adding
  // to the Widget it's mixed in to should implement thus.

  get widgetClass() {}

});

/**
 * @module Scheduler/view/mixin/EventSelection
 */

/**
 * Mixin that tracks event or assignment selection by clicking on one or more events in the scheduler.
 * @mixin
 */

var EventSelection = (Target => class EventSelection extends (Target || Base) {
  static get $name() {
    return 'EventSelection';
  } //region Default config

  static get defaultConfig() {
    return {
      /**
       * Configure as `true` to allow `CTRL+click` to select multiple events in the scheduler.
       * @config {Boolean}
       * @category Selection
       */
      multiEventSelect: false,

      /**
       * Configure as `true`, or set property to `true` to disable event selection.
       * @config {Boolean}
       * @default
       * @category Selection
       */
      eventSelectionDisabled: false,

      /**
       * CSS class to add to selected events.
       * @config {String}
       * @default
       * @category CSS
       * @private
       */
      eventSelectedCls: 'b-sch-event-selected',

      /**
       * Configure as `true` to trigger `selectionChange` when removing a selected event/assignment.
       * @config {Boolean}
       * @default
       * @category Selection
       */
      triggerSelectionChangeOnRemove: false,

      /**
       * This flag controls whether Scheduler should maintain its selection of events when loading a new dataset (if selected event ids are included in the newly loaded dataset).
       * @config {Boolean}
       * @default
       * @category Selection
       */
      maintainSelectionOnDatasetChange: true,

      /**
       * CSS class to add to other instances of a selected event, to highlight them.
       * @config {String}
       * @default
       * @category CSS
       * @private
       */
      eventAssignHighlightCls: 'b-sch-event-assign-selected',

      /**
       * Collection to store selection.
       * @config {Core.util.Collection}
       * @private
       */
      selectedCollection: {}
    };
  } //endregion
  //region Events

  /**
   * Fired any time there is a change to the events selected in the Scheduler.
   * @event eventSelectionChange
   * @param {String} action One of the actions 'select', 'deselect', 'update', 'clear'
   * @param {Scheduler.model.EventModel[]} selected An array of the Events added to the selection.
   * @param {Scheduler.model.EventModel[]} deselected An array of the Event removed from the selection.
   * @param {Scheduler.model.EventModel[]} selection The new selection.
   */

  /**
   * Fired any time there is a change to the assignments selected in the Scheduler.
   * @event assignmentSelectionChange
   * @param {String} action One of the actions 'select', 'deselect', 'update', 'clear'
   * @param {Scheduler.model.AssignmentModel[]} selected An array of the Assignments added to the selection.
   * @param {Scheduler.model.AssignmentModel[]} deselected An array of the Assignments removed from the selection.
   * @param {Scheduler.model.AssignmentModel[]} selection The new selection.
   */
  //endregion
  //region Init

  afterConstruct() {
    super.afterConstruct();
    this.navigator.on({
      navigate: 'onEventNavigate',
      thisObj: this
    });
  } //endregion
  //region Selected Collection

  set selectedCollection(selectedCollection) {
    if (!(selectedCollection instanceof Collection)) {
      selectedCollection = new Collection(selectedCollection);
    }

    this._selectedCollection = selectedCollection; // Fire row change events from onSelectedCollectionChange

    selectedCollection.on({
      change: 'onSelectedCollectionChange',
      thisObj: this
    });
  }

  get selectedCollection() {
    return this._selectedCollection;
  } //endregion
  //region Modify selection

  getEventsFromAssignments(assignments) {
    return ArrayHelper.unique(assignments.map(assignment => assignment.event));
  }
  /**
   * The {@link Scheduler.model.EventModel events} which are selected.
   * @property {Scheduler.model.EventModel[]}
   * @category Selection
   */

  get selectedEvents() {
    return this.getEventsFromAssignments(this.selectedCollection.values);
  }

  set selectedEvents(events) {
    // Select all assignments
    const assignments = [];

    if (events && !Array.isArray(events)) {
      events = [events];
    }

    events && events.forEach(event => {
      assignments.push(...event.assignments);
    }); // Replace the entire selected collection with the new record set

    this.selectedCollection.splice(0, this.selectedCollection.count, assignments);
  }
  /**
   * The {@link Scheduler.model.AssignmentModel events} which are selected.
   * @property {Scheduler.model.AssignmentModel[]}
   * @category Selection
   */

  get selectedAssignments() {
    return this.selectedCollection.values;
  }

  set selectedAssignments(assignments) {
    // Replace the entire selected collection with the new record set
    this.selectedCollection.splice(0, this.selectedCollection.count, assignments || []);
  }
  /**
   * Returns `true` if the {@link Scheduler.model.EventModel event} is selected.
   * @param {Scheduler.model.EventModel} event The event
   * @category Selection
   */

  isEventSelected(event) {
    return this.selectedEvents.includes(event);
  }
  /**
   * Returns `true` if the {@link Scheduler.model.AssignmentModel assignment} is selected.
   * @param {Scheduler.model.AssignmentModel} assignment The assignment
   * @category Selection
   */

  isAssignmentSelected(assignment) {
    return this.selectedCollection.includes(assignment);
  }
  /**
   * Selects the passed {@link Scheduler.model.EventModel event} or {@link Scheduler.model.AssignmentModel assignment}
   * *if it is not selected*. Selecting events results in all their assignments being selected.
   * @param {Scheduler.model.EventModel|Scheduler.model.AssignmentModel} eventOrAssignment The event or assignment to select
   * @param {Boolean} [preserveSelection] Pass `true` to preserve any other selected events or assignments
   * @category Selection
   */

  select(eventOrAssignment, preserveSelection = false) {
    if (eventOrAssignment.isAssignment) {
      this.selectAssignment(eventOrAssignment, preserveSelection);
    } else {
      this.selectEvent(eventOrAssignment, preserveSelection);
    }
  }
  /**
   * Selects the passed {@link Scheduler.model.EventModel event} *if it is not selected*. Selecting an event will
   * select all its assignments.
   * @param {Scheduler.model.EventModel} event The event select
   * @param {Boolean} [preserveSelection] Pass `true` to preserve any other selected events
   * @category Selection
   */

  selectEvent(event, preserveSelection = false) {
    // If the event is already selected, this is a no-op.
    // In this case, selection must not be cleared even in the absence of preserveSelection
    if (!this.isEventSelected(event)) {
      preserveSelection ? this.selectedCollection.add(event.assignments) : this.selectedEvents = event;
    }
  }
  /**
   * Selects the passed {@link Scheduler.model.AssignmentModel assignment} *if it is not selected*.
   * @param {Scheduler.model.AssignmentModel} assignment The assignment to select
   * @param {Boolean} [preserveSelection] Pass `true` to preserve any other selected assignments
   * @category Selection
   */

  selectAssignment(assignment, preserveSelection = false) {
    // If the event is already selected, this is a no-op.
    // In this case, selection must not be cleared even in the absence of preserveSelection
    if (!this.isAssignmentSelected(assignment)) {
      preserveSelection ? this.selectedCollection.add(assignment) : this.selectedAssignments = assignment;
    }
  }
  /**
   * Deselects the passed {@link Scheduler.model.EventModel event} or {@link Scheduler.model.AssignmentModel assignment}
   * *if it is selected*.
   * @param {Scheduler.model.EventModel|Scheduler.model.AssignmentModel} eventOrAssignment The event or assignment to deselect.
   * @category Selection
   */

  deselect(eventOrAssignment) {
    if (eventOrAssignment.isAssignment) {
      this.deselectAssignment(eventOrAssignment);
    } else {
      this.deselectEvent(eventOrAssignment);
    }
  }
  /**
   * Deselects the passed {@link Scheduler.model.EventModel event} *if it is selected*.
   * @param {Scheduler.model.EventModel} event The event to deselect.
   * @category Selection
   */

  deselectEvent(event) {
    if (this.isEventSelected(event)) {
      this.selectedCollection.remove(...event.assignments);
    }
  }
  /**
   * Deselects the passed {@link Scheduler.model.AssignmentModel assignment} *if it is selected*.
   * @param {Scheduler.model.AssignmentModel} assignment The assignment to deselect
   * @category Selection
   */

  deselectAssignment(assignment) {
    if (this.isAssignmentSelected(assignment)) {
      this.selectedCollection.remove(assignment);
    }
  }
  /**
   * Adds {@link Scheduler.model.EventModel events} to the selection.
   * @param {Scheduler.model.EventModel[]} events Events to be selected
   * @category Selection
   */

  selectEvents(events) {
    this.selectedCollection.add(events.reduce((assignments, event) => {
      assignments.push(...event.assignments);
      return assignments;
    }, []));
  }
  /**
   * Removes {@link Scheduler.model.EventModel events} from the selection.
   * @param {Scheduler.model.EventModel[]} events Events or assignments  to be deselected
   * @category Selection
   */

  deselectEvents(events) {
    this.selectedCollection.remove(events.reduce((assignments, event) => {
      assignments.push(...event.assignments);
      return assignments;
    }, []));
  }
  /**
   * Adds {@link Scheduler.model.AssignmentModel assignments} to the selection.
   * @param {Scheduler.model.AssignmentModel[]} assignments Assignments to be selected
   * @category Selection
   */

  selectAssignments(assignments) {
    this.selectedCollection.add(assignments);
  }
  /**
   * Removes {@link Scheduler.model.AssignmentModel assignments} from the selection.
   * @param {Scheduler.model.AssignmentModel[]} assignments Assignments  to be deselected
   * @category Selection
   */

  deselectAssignments(assignments) {
    this.selectedCollection.remove(assignments);
  }
  /**
   * Deselects all {@link Scheduler.model.EventModel events} and {@link Scheduler.model.AssignmentModel assignments}.
   * @category Selection
   */

  clearEventSelection() {
    this.selectedAssignments = [];
  } //endregion
  //region Events

  /**
   * Responds to mutations of the underlying selection Collection.
   * Keeps the UI synced, eventSelectionChange and assignmentSelectionChange event is fired when `me.silent` is falsy.
   * @private
   */

  onSelectedCollectionChange({
    added,
    removed
  }) {
    const me = this,
          selection = me.selectedAssignments,
          selected = added || [],
          deselected = removed || [];

    function updateSelection(assignmentRecord, select) {
      const eventRecord = assignmentRecord.event;

      if (eventRecord) {
        const element = me.getElementFromAssignmentRecord(assignmentRecord);
        me.currentOrientation.toggleCls(assignmentRecord, me.eventSelectedCls, select);
        me.getElementsFromEventRecord(eventRecord).forEach(el => {
          if (el !== element) {
            const otherAssignmentRecord = me.resolveAssignmentRecord(el);
            me.currentOrientation.toggleCls(otherAssignmentRecord, me.eventAssignHighlightCls, select);
            el.classList[select ? 'add' : 'remove'](me.eventAssignHighlightCls);
          }
        });
      }
    }

    selected.forEach(record => updateSelection(record, true));
    deselected.forEach(record => updateSelection(record, false)); // To be able to restore selection after reloading resources (which might lead to regenerated assignments in
    // the single assignment scenario, so cannot rely on records or ids)

    me.$selectedAssignments = selection.map(assignment => ({
      eventId: assignment.eventId,
      resourceId: assignment.resourceId
    }));

    if (!me.silent) {
      const action = selection.length > 0 ? selected.length > 0 && deselected.length > 0 ? 'update' : selected.length > 0 ? 'select' : 'deselect' : 'clear';
      me.trigger('assignmentSelectionChange', {
        action,
        selection,
        selected,
        deselected
      });
      me.trigger('eventSelectionChange', {
        action,
        selection: me.selectedEvents,
        selected: me.getEventsFromAssignments(selected),
        deselected: me.getEventsFromAssignments(deselected)
      });
    }
  }
  /**
   * Assignment change listener to remove events from selection which are no longer in the assignments.
   * @private
   */

  onAssignmentChange({
    action,
    records: assignments
  }) {
    const me = this;
    me.silent = !me.triggerSelectionChangeOnRemove;

    if (action === 'remove') {
      me.deselectAssignments(assignments);
    } else if (action === 'removeall' && !me.eventStore.isSettingData) {
      me.clearEventSelection();
    } else if (action === 'dataset' && me.$selectedAssignments) {
      if (!me.maintainSelectionOnDatasetChange) {
        me.clearEventSelection();
      } else {
        const newAssignments = me.$selectedAssignments.map(selector => assignments.find(a => a.eventId === selector.eventId && a.resourceId === selector.resourceId));
        me.selectedAssignments = ArrayHelper.clean(newAssignments);
      }
    }

    me.silent = false;
  }

  onInternalEventStoreChange({
    action,
    records
  }) {
    // Setting empty event dataset cannot be handled in onAssignmentChange above, no assignments might be affected
    if (action === 'dataset' && !records.length) {
      this.clearEventSelection();
    }

    super.onInternalEventStoreChange(...arguments);
  }
  /**
   * Mouse listener to update selection.
   * @private
   */

  onAssignmentSelectionClick(event, clickedRecord) {
    const me = this; // Multi selection: CTRL means preserve selection, just add or remove the event.
    // Single selection: CTRL deselects already selected event

    if (me.isAssignmentSelected(clickedRecord)) {
      event.ctrlKey && me.deselectAssignment(clickedRecord, me.multiEventSelect);
    } else {
      me.selectAssignment(clickedRecord, event.ctrlKey && me.multiEventSelect);
    }
  }
  /**
   * Navigation listener to update selection.
   * @private
   */

  onEventNavigate({
    event,
    item
  }) {
    if (!this.eventSelectionDisabled) {
      const assignment = item && (item.nodeType === 1 ? this.resolveAssignmentRecord(item) : item);

      if (assignment) {
        this.onAssignmentSelectionClick(event, assignment);
      } // Click outside of an event/assignment;
      else {
          this.clearEventSelection();
        }
    }
  } //endregion
  //region Getters/Setters
  // This does not need a className on Widgets.
  // Each *Class* which doesn't need 'b-' + constructor.name.toLowerCase() automatically adding
  // to the Widget it's mixed in to should implement thus.

  get widgetClass() {} //endregion

});

/**
 * @module Scheduler/view/mixin/EventNavigation
 */

const preventDefault = e => e.preventDefault();
/**
 * Mixin that tracks event or assignment selection by clicking on one or more events in the scheduler.
 * @mixin
 */

var EventNavigation = (Target => class EventNavigation extends Delayable(Target || Base) {
  static get $name() {
    return 'EventNavigation';
  } //region Default config

  static get configurable() {
    return {
      /**
       * A config object to use when creating the {@link Core.helper.util.Navigator}
       * to use to perform keyboard navigation in the timeline.
       * @config {Object}
       * @default
       * @category Misc
       * @internal
       */
      navigator: {
        allowCtrlKey: true,
        scrollSilently: true,
        keys: {
          Space: 'onEventSpaceKey',
          Enter: 'onEventEnterKey',
          Delete: 'onDeleteKey',
          Backspace: 'onDeleteKey'
        }
      }
    };
  }

  static get defaultConfig() {
    return {
      /**
       * A CSS class name to add to focused events.
       * @config {String}
       * @default
       * @category CSS
       * @private
       */
      focusCls: 'b-active',

      /**
       * Allow using [Delete] and [Backspace] to remove events/assignments
       * @config {Boolean}
       * @default
       * @category Misc
       */
      enableDeleteKey: true,
      // Number in milliseconds to buffer handlers execution. See `Delayable.throttle` function docs.
      onDeleteKeyBuffer: 500,
      navigatePreviousBuffer: 200,
      navigateNextBuffer: 200,
      testConfig: {
        onDeleteKeyBuffer: 1
      }
    };
  } //endregion
  //region Events

  /**
   * Fired when a user gesture causes the active item to change.
   * @event navigate
   * @param {Event} event The browser event which instigated navigation. May be a click or key or focus event.
   * @param {HTMLElement} item The newly active item, or `null` if focus moved out.
   * @param {HTMLElement} oldItem The previously active item, or `null` if focus is moving in.
   */
  //endregion

  construct(config) {
    const me = this;
    me.isInTimeAxis = me.isInTimeAxis.bind(me);
    me.onDeleteKey = me.throttle(me.onDeleteKey, me.onDeleteKeyBuffer, me);
    super.construct(config);
  }

  changeNavigator(navigator) {
    const me = this;
    me.getConfig('subGridConfigs');
    return new Navigator(me.constructor.mergeConfigs({
      ownerCmp: me,
      target: me.timeAxisSubGridElement,
      processEvent: me.processEvent,
      itemSelector: `.${me.eventCls}-wrap`,
      focusCls: me.focusCls,
      navigatePrevious: me.throttle(me.navigatePrevious, {
        delay: me.navigatePreviousBuffer,
        throttled: preventDefault
      }),
      navigateNext: me.throttle(me.navigateNext, {
        delay: me.navigateNextBuffer,
        throttled: preventDefault
      })
    }, navigator));
  }

  doDestroy() {
    this.navigator.destroy();
    super.doDestroy();
  }

  isInTimeAxis(record) {
    // If event is hidden by workingTime configs, horizontal mapper would raise a flag on instance meta
    // We still need to check if time span is included in axis
    return !record.instanceMeta(this).excluded && this.timeAxis.isTimeSpanInAxis(record);
  }
  /*
   * Override of GridNavigation#focusCell method to handle the TimeAxisColumn.
   * Not needed until we implement full keyboard accessibiliy.
   */

  accessibleFocusCell(cellSelector, options) {
    const me = this;
    cellSelector = me.normalizeCellContext(cellSelector);

    if (cellSelector.columnId === me.timeAxisColumn.id) ; else {
      return super.focusCell(cellSelector, options);
    }
  } // Interface method to extract the navigated to record from a populated 'navigate' event.
  // Gantt, Scheduler and Calendar handle event differently, adding different properties to it.
  // This method is meant to be overridden to return correct target from event

  normalizeTarget(event) {
    return event.assignmentRecord;
  }

  getPrevious(assignmentRecord, isDelete) {
    const me = this,
          {
      resourceStore
    } = me,
          {
      eventSorter
    } = me.currentOrientation,
          // start/end dates are required to limit time span to look at in case recurrence feature is enabled
    {
      startDate,
      endDate
    } = me.timeAxis,
          eventRecord = assignmentRecord.event,
          resourceEvents = me.eventStore.getEvents({
      resourceRecord: assignmentRecord.resource,
      startDate,
      endDate
    }).filter(this.isInTimeAxis).sort(eventSorter);
    let resourceRecord = assignmentRecord.resource,
        previousEvent = resourceEvents[resourceEvents.indexOf(eventRecord) - 1]; // At first event for resource, traverse up the resource store.

    if (!previousEvent) {
      // If we are deleting an event, skip other instances of the event which we may encounter
      // due to multi-assignment.
      for (let rowIdx = resourceStore.indexOf(resourceRecord) - 1; (!previousEvent || isDelete && previousEvent === eventRecord) && rowIdx >= 0; rowIdx--) {
        resourceRecord = resourceStore.getAt(rowIdx);
        const events = me.eventStore.getEvents({
          resourceRecord,
          startDate,
          endDate
        }).filter(me.isInTimeAxis).sort(eventSorter);
        previousEvent = events.length && events[events.length - 1];
      }
    }

    return me.assignmentStore.getAssignmentForEventAndResource(previousEvent, resourceRecord);
  }

  navigatePrevious(keyEvent) {
    const me = this,
          previousAssignment = me.getPrevious(me.normalizeTarget(keyEvent));
    keyEvent.preventDefault();

    if (previousAssignment) {
      if (!keyEvent.ctrlKey) {
        me.clearEventSelection();
      }

      return me.navigateTo(previousAssignment, keyEvent);
    }
  }

  getNext(assignmentRecord, isDelete) {
    const me = this,
          {
      resourceStore
    } = me,
          {
      eventSorter
    } = me.currentOrientation,
          // start/end dates are required to limit time span to look at in case recurrence feature is enabled
    {
      startDate,
      endDate
    } = me.timeAxis,
          eventRecord = assignmentRecord.event,
          resourceEvents = me.eventStore.getEvents({
      resourceRecord: assignmentRecord.resource,
      // start/end are required to limit time
      startDate,
      endDate
    }).filter(this.isInTimeAxis).sort(eventSorter);
    let resourceRecord = assignmentRecord.resource,
        nextEvent = resourceEvents[resourceEvents.indexOf(eventRecord) + 1]; // At last event for resource, traverse down the resource store

    if (!nextEvent) {
      // If we are deleting an event, skip other instances of the event which we may encounter
      // due to multi-assignment.
      for (let rowIdx = resourceStore.indexOf(resourceRecord) + 1; (!nextEvent || isDelete && nextEvent === eventRecord) && rowIdx < resourceStore.count; rowIdx++) {
        resourceRecord = resourceStore.getAt(rowIdx);
        const events = me.eventStore.getEvents({
          resourceRecord,
          startDate,
          endDate
        }).filter(me.isInTimeAxis).sort(eventSorter);
        nextEvent = events[0];
      }
    }

    return me.assignmentStore.getAssignmentForEventAndResource(nextEvent, resourceRecord);
  }

  navigateNext(keyEvent) {
    const me = this,
          nextAssignment = me.getNext(me.normalizeTarget(keyEvent));
    keyEvent.preventDefault();

    if (nextAssignment) {
      if (!keyEvent.ctrlKey) {
        me.clearEventSelection();
      }

      return me.navigateTo(nextAssignment, keyEvent);
    }
  }

  async navigateTo(targetAssignment, uiEvent = {}) {
    const me = this;

    if (targetAssignment) {
      // No key processing during scroll
      me.navigator.disabled = true;
      await me.scrollAssignmentIntoView(targetAssignment, null, {
        animate: 100
      }); // Panel can be destroyed before promise is resolved

      if (!me.isDestroyed) {
        me.navigator.disabled = false;
        me.activeAssignment = targetAssignment;
        me.navigator.trigger('navigate', {
          event: uiEvent,
          item: DomHelper.up(me.getElementFromAssignmentRecord(targetAssignment), me.navigator.itemSelector)
        });
      }
    }
  }

  set activeAssignment(assignmentRecord) {
    const assignmentEl = this.getElementFromAssignmentRecord(assignmentRecord);
    this.navigator.activeItem = assignmentEl.parentNode;
  }

  get activeAssignment() {
    const {
      activeItem
    } = this.navigator;

    if (activeItem) {
      return this.resolveAssignmentRecord(activeItem);
    }
  }

  get previousActiveEvent() {
    const {
      previousActiveItem
    } = this.navigator;

    if (previousActiveItem) {
      return this.resolveEventRecord(previousActiveItem);
    }
  }

  processEvent(keyEvent) {
    const me = this,
          eventElement = DomHelper.up(keyEvent.target, me.eventSelector);

    if (!me.navigator.disabled && eventElement) {
      keyEvent.assignmentRecord = me.resolveAssignmentRecord(eventElement);
      keyEvent.eventRecord = me.resolveEventRecord(eventElement);
      keyEvent.resourceRecord = me.resolveResourceRecord(eventElement);
    }

    return keyEvent;
  }

  onDeleteKey(keyEvent) {
    const record = this.eventStore.usesSingleAssignment ? keyEvent.eventRecord : keyEvent.assignmentRecord;

    if (!this.readOnly && this.enableDeleteKey && record) {
      this.removeRecords([record]);
    }
  }
  /**
   * Internal utility function to remove events. Used when pressing [DELETE] or [BACKSPACE] or when clicking the
   * delete button in the event editor. Triggers a preventable `beforeEventDelete` or `beforeAssignmentDelete` event.
   * @param {Scheduler.model.EventModel[]|Scheduler.model.AssignmentModel[]} records Records to remove
   * @param {Function} [callback] Optional callback executed after triggering the event but before deletion
   * @returns {Boolean} Returns `false` if the operation was prevented, otherwise `true`
   * @internal
   * @fires beforeEventDelete
   * @fires beforeAssignmentDelete
   */

  removeRecords(records, callback = null) {
    if (!this.readOnly && records.length) {
      const context = {
        finalize(removeRecord = true) {
          if (callback) {
            callback(removeRecord);
          }

          if (removeRecord !== false) {
            records.forEach(r => {
              if (r.isAssignment && r.isOccurrenceAssignment) {
                r.event.remove();
              } else {
                r.remove();
              }
            });
          }
        }

      };
      let shouldFinalize;

      if (records[0].isAssignment) {
        /**
         * Fires before an assignment is removed. Can be triggered by user pressing [DELETE] or [BACKSPACE] or
         * by the event editor. Can for example be used to display a custom dialog to confirm deletion, in which
         * case records should be "manually" removed after confirmation:
         *
         * ```javascript
         * scheduler.on({
         *    beforeAssignmentDelete({ assignmentRecords, context }) {
         *        // Show custom confirmation dialog (pseudo code)
         *        confirm.show({
         *            listeners : {
         *                onOk() {
         *                    // Remove the assignments on confirmation
         *                    context.finalize(true);
         *                },
         *                onCancel() {
         *                    // do not remove the assignments if "Cancel" clicked
         *                    context.finalize(false);
         *                }
         *            }
         *        });
         *
         *        // Prevent default behaviour
         *        return false;
         *    }
         * });
         * ```
         *
         * @event beforeAssignmentDelete
         * @param {Scheduler.view.Scheduler} source  The Scheduler instance
         * @param {Scheduler.model.EventModel[]} eventRecords  The records about to be deleted
         * @param {Object} context  Additional removal context:
         * @param {Function} context.finalize  Function to call to finalize the removal.
         *      Used to asynchronously decide to remove the records or not. Provide `false` to the function to
         *      prevent the removal.
         * @param {Boolean} [context.finalize.removeRecords = true]   Provide `false` to the function to prevent
         *      the removal.
         * @preventable
         */
        shouldFinalize = this.trigger('beforeAssignmentDelete', {
          assignmentRecords: records,
          context
        });
      } else {
        /**
         * Fires before an event is removed. Can be triggered by user pressing [DELETE] or [BACKSPACE] or by the
         * event editor. Can for example be used to display a custom dialog to confirm deletion, in which case
         * records should be "manually" removed after confirmation:
         *
         * ```javascript
         * scheduler.on({
         *    beforeEventDelete({ eventRecords, context }) {
         *        // Show custom confirmation dialog (pseudo code)
         *        confirm.show({
         *            listeners : {
         *                onOk() {
         *                    // Remove the events on confirmation
         *                    context.finalize(true);
         *                },
         *                onCancel() {
         *                    // do not remove the events if "Cancel" clicked
         *                    context.finalize(false);
         *                }
         *            }
         *        });
         *
         *        // Prevent default behaviour
         *        return false;
         *    }
         * });
         * ```
         *
         * @event beforeEventDelete
         * @param {Scheduler.view.Scheduler} source  The Scheduler instance
         * @param {Scheduler.model.EventModel[]} eventRecords  The records about to be deleted
         * @param {Object} context  Additional removal context:
         * @param {Function} context.finalize  Function to call to finalize the removal.
         *      Used to asynchronously decide to remove the records or not. Provide `false` to the function to
         *      prevent the removal.
         * @param {Boolean} [context.finalize.removeRecords = true]  Provide `false` to the function to prevent
         *      the removal.
         * @preventable
         */
        shouldFinalize = this.trigger('beforeEventDelete', {
          eventRecords: records,
          context
        });
      }

      if (shouldFinalize !== false) {
        context.finalize();
        return true;
      }
    }

    return false;
  }

  onEventSpaceKey(keyEvent) {// Empty, to be chained by features
  }

  onEventEnterKey(keyEvent) {// Empty, to be chained by features
  }

  get isActionableLocation() {
    // Override from grid if the Navigator's location is an event (or task if we're in Gantt)
    // Being focused on a task/event means that it's *not* actionable. It's not valid to report
    // that we're "inside" the cell in a TimeLine, so ESC must not attempt to focus the cell.
    if (!this.navigator.activeItem) {
      return super.isActionableLocation;
    }
  } // This does not need a className on Widgets.
  // Each *Class* which doesn't need 'b-' + constructor.name.toLowerCase() automatically adding
  // to the Widget it's mixed in to should implement thus.

  get widgetClass() {}

});

var BaseRendering = (Target => class extends (Target || Base) {
  getElementFromAssignmentRecord(assignmentRecord, returnWrapper = false) {
    if (this.scheduler.isPainted && assignmentRecord) {
      var _wrapper$syncIdMap;

      const wrapper = this.scheduler.foregroundCanvas.syncIdMap[assignmentRecord.id]; // Wrapper wont have syncIdMap when saving dragcreated event from editor

      return returnWrapper ? wrapper : wrapper === null || wrapper === void 0 ? void 0 : (_wrapper$syncIdMap = wrapper.syncIdMap) === null || _wrapper$syncIdMap === void 0 ? void 0 : _wrapper$syncIdMap.event;
    }

    return null;
  }

  getElementFromEventRecord(eventRecord, resourceRecord = eventRecord.resource, returnWrapper = false) {
    const assignmentRecord = this.assignmentStore.getAssignmentForEventAndResource(eventRecord, resourceRecord);
    return this.getElementFromAssignmentRecord(assignmentRecord, returnWrapper);
  }

  getElementsFromEventRecord(eventRecord, resourceRecord, returnWrapper = false) {
    const me = this; // Single event instance, as array

    if (resourceRecord) {
      return [me.getElementFromEventRecord(eventRecord, resourceRecord, returnWrapper)];
    } // All instances
    else {
        return eventRecord.resources.reduce((result, resourceRecord) => {
          const el = me.getElementFromEventRecord(eventRecord, resourceRecord, returnWrapper);
          el && result.push(el);
          return result;
        }, []);
      }
  }

});

/* eslint-disable no-unused-expressions */
/**
 * @module Scheduler/view/orientation/HorizontalRendering
 */

const releaseEventActions = {
  releaseElement: 1,
  // Not used at all at the moment
  reuseElement: 1 // Used by some other element

},
      renderEventActions = {
  newElement: 1,
  reuseOwnElement: 1,
  reuseElement: 1
},
      MAX_WIDTH = 9999999,
      heightEventSorter = ({
  startDateMS: lhs
}, {
  startDateMS: rhs
}) => lhs - rhs,
      chronoFields = {
  startDate: 1,
  endDate: 1,
  duration: 1
};

function getStartEnd(view, timeAxis, date, dateMS, useEnd) {
  if (view.fillTicks) {
    let tick = timeAxis.getTickFromDate(date);

    if (tick >= 0) {
      // If date matches a tick start/end, use the earlier tick
      if (useEnd && tick === Math.round(tick) && tick > 0) {
        tick--;
      }

      const tickIndex = Math.floor(tick),
            tickRecord = timeAxis.getAt(tickIndex);
      return tickRecord[useEnd ? 'endDate' : 'startDate'].getTime();
    }
  }

  return dateMS;
}
/**
 * Handles event rendering in Schedulers horizontal mode. Reacts to project/store changes to keep the UI up to date.
 *
 * @internal
 */

class HorizontalRendering extends Base.mixin(BaseRendering, AttachToProjectMixin) {
  //region Config & Init
  static get configurable() {
    return {
      // It's needed to adjust visible date range in Export. Set to 100 to render additional 100px
      // worth of ticks which helps to scroll faster during export and fixes
      // issue when scrollToDate cannot reach panel end date on exceptionally narrow view
      scrollBuffer: 0
    };
  }

  static get properties() {
    return {
      // Map with event DOM configs, keyed by resource id
      resourceMap: new Map(),
      // Map with visible events DOM configs, keyed by row instance
      rowMap: new Map(),
      eventConfigs: [],
      // Flag to avoid transitioning on first refresh
      isFirstRefresh: true
    };
  }

  construct(scheduler) {
    const me = this;
    me.client = me.scheduler = scheduler;
    me.eventSorter = me.eventSorter.bind(scheduler);
    scheduler.rowManager.on({
      name: 'rowManager',
      renderDone: 'onRenderDone',
      removeRows: 'onRemoveRows',
      translateRow: 'onTranslateRow',
      beforeRowHeight: 'onBeforeRowHeightChange',
      thisObj: me
    });
    me.toDrawOnProjectRefresh = new Set();
    me.toDrawOnDataReady = new Set();
    super.construct({});
  }

  init() {} //endregion
  //region Region, dates & coordinates

  get visibleDateRange() {
    return this._visibleDateRange;
  }

  getDateFromXY(xy, roundingMethod, local, allowOutOfRange = false) {
    let coord = xy[0];

    if (!local) {
      coord = this.translateToScheduleCoordinate(coord);
    }

    return this.scheduler.timeAxisViewModel.getDateFromPosition(coord, roundingMethod, allowOutOfRange);
  }

  translateToScheduleCoordinate(x) {
    // Get rid of fractional pixels, to not end up with negative fractional values for pos
    const pos = x - Math.floor(this.scheduler.timeAxisSubGridElement.getBoundingClientRect().left);
    return pos + this.scheduler.scrollLeft;
  }

  translateToPageCoordinate(x) {
    const element = this.scheduler.timeAxisSubGridElement;
    return x + element.getBoundingClientRect().left - element.scrollLeft;
  }
  /**
   * Gets the region, relative to the page, represented by the schedule and optionally only for a single resource.
   * This method will call getDateConstraints to allow for additional resource/event based constraints. By overriding
   * that method you can constrain events differently for different resources.
   * @param {Core.data.Model} [rowRecord] (optional) The row record
   * @param {Scheduler.model.EventModel} [eventRecord] (optional) The event record
   * @return {Core.helper.util.Rectangle} The region of the schedule
   */

  getScheduleRegion(rowRecord, eventRecord, local = true) {
    const me = this,
          {
      scheduler
    } = me,
          {
      timeAxisSubGridElement,
      timeAxis
    } = scheduler,
          resourceMargin = scheduler.getResourceMargin(rowRecord);
    let region;

    if (rowRecord) {
      const eventElement = eventRecord && me.getElementsFromEventRecord(eventRecord, rowRecord)[0];
      region = Rectangle.from(scheduler.getRowById(rowRecord.id).getElement('locked'));

      if (eventElement) {
        const eventRegion = Rectangle.from(eventElement, timeAxisSubGridElement);
        region.y = eventRegion.y;
        region.bottom = eventRegion.bottom;
      } else {
        region.y = region.y + resourceMargin;
        region.bottom = region.bottom - resourceMargin;
      }
    } else {
      // TODO: This is what the bizarre function that was removed here did.
      // The coordinate space needs to be sorted out here!
      region = Rectangle.from(timeAxisSubGridElement).moveTo(null, 0);
      region.width = timeAxisSubGridElement.scrollWidth;
      region.y = region.y + resourceMargin;
      region.bottom = region.bottom - resourceMargin;
    }

    const taStart = timeAxis.startDate,
          taEnd = timeAxis.endDate,
          dateConstraints = scheduler.getDateConstraints(rowRecord, eventRecord) || {
      start: taStart,
      end: taEnd
    };
    let startX = scheduler.getCoordinateFromDate(DateHelper.max(taStart, dateConstraints.start)),
        endX = scheduler.getCoordinateFromDate(DateHelper.min(taEnd, dateConstraints.end));

    if (!local) {
      startX = me.translateToPageCoordinate(startX);
      endX = me.translateToPageCoordinate(endX);
    }

    region.left = Math.min(startX, endX);
    region.right = Math.max(startX, endX);
    return region;
  }
  /**
   * Gets the Region, relative to the timeline view element, representing the passed row and optionally just for a
   * certain date interval.
   * @param {Core.data.Model} rowRecord The row record
   * @param {Date} startDate A start date constraining the region
   * @param {Date} endDate An end date constraining the region
   * @return {Core.helper.util.Rectangle} The Rectangle which encapsulates the row
   */

  getRowRegion(rowRecord, startDate, endDate) {
    const {
      scheduler
    } = this,
          {
      timeAxis
    } = scheduler,
          row = scheduler.getRowById(rowRecord.id); // might not be rendered

    if (!row) {
      return null;
    }

    const taStart = timeAxis.startDate,
          taEnd = timeAxis.endDate,
          start = startDate ? DateHelper.max(taStart, startDate) : taStart,
          end = endDate ? DateHelper.min(taEnd, endDate) : taEnd,
          startX = scheduler.getCoordinateFromDate(start),
          endX = scheduler.getCoordinateFromDate(end, true, true),
          y = row.top,
          x = Math.min(startX, endX),
          bottom = y + row.offsetHeight;
    return new Rectangle(x, y, Math.max(startX, endX) - x, bottom - y);
  }

  getResourceEventBox(eventRecord, resourceRecord, includeOutside, roughly = false) {
    const resourceData = this.resourceMap.get(resourceRecord.id);
    let eventLayout = null,
        approx = false;

    if (resourceData) {
      eventLayout = resourceData.eventsData.find(d => d.eventRecord === eventRecord);
    } // Outside of view, layout now if supposed to be included

    if (!eventLayout) {
      eventLayout = this.getTimeSpanRenderData(eventRecord, resourceRecord, {
        viewport: true,
        timeAxis: includeOutside
      });
      approx = true;
    }

    if (eventLayout) {
      // Event layout is relative to row, need to make to absolute before returning
      const rowBox = this.scheduler.getRecordCoords(resourceRecord, true, roughly),
            absoluteTop = eventLayout.top + rowBox.top,
            box = new Rectangle(eventLayout.left, absoluteTop, eventLayout.width, eventLayout.height); // Flag informing other parts of the code that this box is approximated

      if (approx) {
        box.layout = false;
      }

      box.rowTop = rowBox.top;
      box.rowBottom = rowBox.bottom;
      return box;
    }

    return null;
  } //endregion
  //region Element <-> Record mapping

  resolveRowRecord(elementOrEvent) {
    const me = this,
          {
      scheduler
    } = me,
          element = elementOrEvent instanceof Event ? elementOrEvent.target : elementOrEvent,
          // Fix for FF on Linux having text nodes as event.target
    el = element.nodeType === 3 ? element.parentElement : element,
          eventNode = DomHelper.up(el, scheduler.eventSelector);

    if (eventNode) {
      return me.resourceStore.getById(eventNode.dataset.resourceId);
    }

    return scheduler.getRecordFromElement(el);
  } //endregion
  //region Project

  attachToProject(project) {
    super.attachToProject(project);
    this.refreshAllWhenReady = true;

    if (project) {
      project.on({
        name: 'project',
        refresh: 'onProjectRefresh',
        dataReady: 'onProjectDataReady',
        thisObj: this
      });
    }
  }

  onProjectDataReady() {
    const me = this,
          {
      scheduler,
      toDrawOnDataReady
    } = me;

    if (!scheduler.refreshSuspended) {
      if (toDrawOnDataReady.size) {
        me.clearResources(toDrawOnDataReady);
        me.refreshResources(toDrawOnDataReady);
      }

      toDrawOnDataReady.clear();
    }
  }

  onProjectRefresh() {
    const me = this,
          {
      scheduler,
      toDrawOnProjectRefresh
    } = me;

    if (!scheduler.refreshSuspended) {
      // Either refresh all rows (on for example dataset)
      if (me.refreshAllWhenReady) {
        scheduler.calculateAllRowHeights(true); // Rows rendered? Refresh

        if (scheduler.rowManager.topRow) {
          me.clearAll();
          scheduler.refreshWithTransition(false, !me.isFirstRefresh);
          me.isFirstRefresh = false;
        } // No rows yet, reinitialize (happens if initial project empty and then non empty project assigned)
        else {
            scheduler.rowManager.reinitialize();
          }

        me.refreshAllWhenReady = false;
      } // Or only affected rows (if any)
      else if (toDrawOnProjectRefresh.size) {
          //me.clearResources(toDrawOnProjectRefresh);
          me.refreshResources(toDrawOnProjectRefresh);
        }

      toDrawOnProjectRefresh.clear();
    }
  } //endregion
  //region AssignmentStore

  attachToAssignmentStore(assignmentStore) {
    this.refreshAllWhenReady = true;
    super.attachToAssignmentStore(assignmentStore);

    if (assignmentStore) {
      assignmentStore.on({
        name: 'assignmentStore',
        changePreCommit: 'onAssignmentStoreChange',
        refreshPreCommit: 'onAssignmentStoreRefresh',
        thisObj: this
      });
    }
  }

  onAssignmentStoreChange({
    action,
    records,
    replaced,
    record,
    changes
  }) {
    const me = this,
          assignmentRecords = records || (record ? [record] : []),
          resourceIds = new Set(assignmentRecords.map(assignmentRecord => assignmentRecord.resourceId)); // Ignore assignment changes caused by removing resources, the remove will redraw things anyway
    // Also ignore case when resource id is changed. In this case row will be refreshed by the grid

    if (me.resourceStore.isRemoving || me.resourceStore.isChangingId) {
      return;
    }

    const transition = true;

    switch (action) {
      // These operations will invalidate the graph, need to draw later
      case 'dataset':
        {
          // Ignore dataset when using single assignment mode
          if (!me.eventStore.usesSingleAssignment) {
            me.refreshResourcesWhenReady(resourceIds);
          }

          return;
        }

      case 'add':
      case 'remove':
      case 'updateMultiple':
        // TODO: Dont think updateMultiple is covered by any test...
        me.refreshResourcesWhenReady(resourceIds);
        return;

      case 'removeall':
        me.refreshAllWhenReady = true;
        return;

      case 'replace':
        // Gather resources from both the old record and the new one
        replaced.forEach(([oldAssignment, newAssignment]) => {
          resourceIds.add(oldAssignment.resourceId);
          resourceIds.add(newAssignment.resourceId);
        }); // And refresh them

        me.refreshResourcesWhenReady(resourceIds);
        return;
      // These operations wont invalidate the graph, redraw now

      case 'filter':
        me.clearAll();
        me.scheduler.calculateAllRowHeights(true);
        me.scheduler.refreshWithTransition();
        return;

      case 'update':
        {
          if ('eventId' in changes || 'resourceId' in changes) {
            // When reassigning, clear old resource also
            if ('resourceId' in changes) {
              resourceIds.add(changes.resourceId.oldValue);
            }

            me.refreshResources(resourceIds, transition);
          }
        }
    }
  }

  onAssignmentStoreRefresh({
    action,
    records
  }) {} //endregion
  //region EventStore

  attachToEventStore(eventStore) {
    this.refreshAllWhenReady = true;
    super.attachToEventStore(eventStore);

    if (eventStore) {
      eventStore.on({
        name: 'eventStore',
        refreshPreCommit: 'onEventStoreRefresh',
        thisObj: this
      });
    }
  }

  onEventStoreRefresh({
    action
  }) {
    if (action === 'batch') {
      if (this.scheduler.isEngineReady) {
        this.clearAll();
        this.scheduler.refreshWithTransition();
      } // else {
      //     this.refreshAllWhenReady = true;
      // }

    }
  }

  onEventStoreChange({
    action,
    records,
    record,
    replaced,
    changes
  }) {
    const me = this,
          eventRecords = records || (record ? [record] : []),
          isResourceTimeRange = eventRecords[0] && eventRecords[0].isResourceTimeRange,
          resourceIds = new Set();
    eventRecords.forEach(eventRecord => {
      var _eventRecord$resource;

      // When rendering a Gantt project, the project model also passes through here -> no `resources`
      (_eventRecord$resource = eventRecord.resources) === null || _eventRecord$resource === void 0 ? void 0 : _eventRecord$resource.forEach(resourceRecord => resourceIds.add(resourceRecord.id));
    });

    if (isResourceTimeRange) {
      me.refreshResources(resourceIds);
    } else {
      switch (action) {
        // No-ops
        case 'batch': // Handled elsewhere, dont want it to clear again

        case 'sort': // Order in EventStore does not matter, so these actions are no-ops

        case 'group':
        case 'move':
          return;

        case 'remove':
          // Remove is a no-op since assignment will also be removed
          return;

        case 'clearchanges':
        case 'dataset':
          me.clearAll();
          me.refreshAllWhenReady = true;
          return;

        case 'add':
        case 'updateMultiple':
          // Just refresh below
          break;

        case 'replace':
          // Gather resources from both the old record and the new one
          replaced.forEach(([, newEvent]) => {
            // Old cleared by changed assignment
            newEvent.resources.map(resourceRecord => resourceIds.add(resourceRecord.id));
          });
          break;

        case 'removeall':
        case 'filter':
          // Filter might be caused by add retriggering filters, in which case we need to refresh later
          if (!me.scheduler.isEngineReady) {
            me.refreshAllWhenReady = true;
            return;
          } // Clear all when filtering for simplicity. If that turns out to give bad performance, one would need to
          // figure out which events was filtered out and only clear their resources.

          me.clearAll();
          me.scheduler.calculateAllRowHeights(true);
          me.scheduler.refreshWithTransition();
          return;

        case 'update':
          {
            // Check if changes are graph related or not
            const allChrono = record.$entity ? !Object.keys(changes).some(name => !record.$entity.getField(name)) : !Object.keys(changes).some(name => !chronoFields[name]);
            let dateChanges = 0;
            'startDate' in changes && dateChanges++;
            'endDate' in changes && dateChanges++;
            'duration' in changes && dateChanges++;

            if ('resourceId' in changes) {
              resourceIds.add(changes.resourceId.oldValue);
            } // Always redraw non chrono changes (name etc) and chrono changes that can affect appearance

            if (!allChrono || // skip case when changed "duration" only (w/o start/end affected)
            dateChanges && !('duration' in changes && dateChanges === 1) || 'percentDone' in changes) {
              var _me$project;

              // if we are finalizing data loading let's delay the resources refresh till all the
              // propagation results get into stores
              if ((_me$project = me.project) !== null && _me$project !== void 0 && _me$project.propagatingLoadChanges) {
                resourceIds.forEach(id => me.toDrawOnDataReady.add(id));
              } else {
                me.refreshResources(resourceIds);
              }
            }

            return;
          }
      }

      me.refreshResourcesWhenReady(resourceIds);
    }
  } //endregion
  //region ResourceStore

  attachToResourceStore(resourceStore) {
    this.refreshAllWhenReady = true;
    super.attachToResourceStore(resourceStore);

    if (resourceStore) {
      resourceStore.on({
        name: 'resourceStore',
        changePreCommit: 'onResourceStoreChange',
        thisObj: this
      });
    }
  }

  onResourceStoreChange({
    action,
    isExpand,
    record,
    records,
    changes
  }) {
    const me = this,
          resourceIds = record ? [record.id] : records === null || records === void 0 ? void 0 : records.map(r => r.id);

    switch (action) {
      case 'add':
        // #635 Events disappear when toggling other node
        // If we are expanding project won't fire refresh event
        if (!isExpand) {
          me.refreshResourcesWhenReady(resourceIds);
        }

        return;

      case 'update':
        {
          // Ignore changes from project commit, if they affect events they will be redrawn anyway
          // Also ignore explicit transformation of leaf <-> parent
          if (!me.project.isBatchingChanges && !changes.isLeaf) {
            // Resource changes might affect events, refresh
            me.refreshResources(resourceIds);
          }

          return;
        }

      case 'filter':
        // Bail out on filter action. Map was already updated on `refresh` event triggered before this `change`
        // one. And extra records are removed from rowMap by `onRemoveRows`
        return;

      case 'removeall':
        me.clearAll();
        return;
    }

    me.clearResources(resourceIds);
  } //endregion
  //region RowManager

  onTranslateRow({
    row
  }) {
    // Newly added rows are translated prior to having an id, rule those out since they will be rendered later
    if (row.id != null) {
      // Event layouts are stored relative to the resource, only need to rerender the row to have its absolute
      // position updated to match new translation
      this.refreshRow(row, false);
    }
  } // Used to pre-calculate row heights

  calculateRowHeight(resourceRecord) {
    const {
      scheduler
    } = this,
          rowHeight = scheduler.getResourceHeight(resourceRecord),
          eventLayout = resourceRecord.eventLayout || scheduler.eventLayout;

    if (eventLayout === 'stack' && scheduler.isEngineReady && !resourceRecord.isSpecialRow && resourceRecord.events.length > 1) {
      const {
        assignmentStore,
        eventStore,
        timeAxis
      } = scheduler,
            {
        barMargin,
        resourceMargin
      } = scheduler.getResourceLayoutSettings(resourceRecord),
            heightPerEvent = rowHeight - resourceMargin * 2,
            // When using an AssignmentStore we will get all events for the resource even if the EventStore is
      // filtered
      eventFilter = eventRecord => eventRecord.assignments.some(a => a.resource === resourceRecord && assignmentStore.includes(a)),
            events = eventStore.getEvents({
        resourceRecord,
        includeOccurrences: scheduler.enableRecurringEvents,
        startDate: timeAxis.startDate,
        endDate: timeAxis.endDate,
        filter: eventFilter
      }).sort(heightEventSorter).map(eventRecord => ({
        eventRecord,
        resourceRecord,
        startMS: eventRecord.startDateMS,
        endMS: eventRecord.endDate ? eventRecord.endDateMS : eventRecord.startDateMS
      })),
            nbrOfBandsRequired = scheduler.getEventLayoutHandler(eventLayout).layoutEventsInBands(events);

      return nbrOfBandsRequired * heightPerEvent + (nbrOfBandsRequired - 1) * barMargin + resourceMargin * 2;
    } else {
      return rowHeight;
    }
  } //endregion
  //region TimeAxis

  doUpdateTimeView() {
    this.updateFromHorizontalScroll(this.scheduler.timeAxisSubGrid.scrollable.x);
  }

  onTimeAxisViewModelUpdate() {
    this.clearAll(); // If refresh is suspended, update timeview as soon as refresh gets unsuspended

    if (this.scheduler.refreshSuspended) {
      this.scheduler.on({
        resumeRefresh() {
          // This code will try to refresh rows, but resumeRefresh event doesn't guarantee rowManager rows are
          // in actual state. e.g. if resources were removed during a suspended refresh rowManager won't get a
          // chance to update them until `refresh` event from the project. We can safely update the view only
          // if engine in ready (not committing), otherwise we leave refresh a liability of normal project refresh
          // logic. Covered by SchedulerRendering.t.js
          // https://github.com/bryntum/support/issues/1462
          if (this.scheduler.isEngineReady) {
            this.doUpdateTimeView();
          }
        },

        thisObj: this,
        once: true
      });
    } // Call update anyway. If refresh is suspended this call will only update visible date range and will not redraw rows

    this.doUpdateTimeView();
  } //endregion
  //region Dependency connectors

  /**
   * Gets displaying item start side
   *
   * @param {Scheduler.model.EventModel} eventRecord
   * @return {String} 'left' / 'right' / 'top' / 'bottom'
   */

  getConnectorStartSide(eventRecord) {
    return 'left';
  }
  /**
   * Gets displaying item end side
   *
   * @param {Scheduler.model.EventModel} eventRecord
   * @return {String} 'left' / 'right' / 'top' / 'bottom'
   */

  getConnectorEndSide(eventRecord) {
    return 'right';
  } //endregion
  //region Scheduler hooks

  refreshRows(reLayoutEvents) {
    if (reLayoutEvents) {
      this.clearAll();
    }
  } // Clear events in case they use date as part of displayed info

  onLocaleChange() {
    this.clearAll();
  } // Called when viewport size changes

  onViewportResize() {// TODO: Could do something cheaper
    // Refresh without discarding any available layouts
    //        this.scheduler.refresh(); // Seems not needed? Will be updated by horizontal scroll anyway
  } // Called from EventDrag

  onDragAbort({
    context,
    dragData
  }) {
    // Aborted a drag in a scrolled scheduler, with origin now out of view. Element is no longer needed
    if (this.resourceStore.indexOf(dragData.record.resource) < this.scheduler.topRow.dataIndex) {
      context.element.remove();
    }
  } // Called from EventSelection

  toggleCls(assignmentRecord, cls, add = true, useWrapper = false) {
    const element = this.getElementFromAssignmentRecord(assignmentRecord, useWrapper),
          // TODO: Should be assignmentRecord.resourceId, but breaks engine. Hoping https://github.com/bryntum/bryntum-suite/pull/1252 will fix it
    resourceData = this.resourceMap.get(assignmentRecord.isModel ? assignmentRecord.get('resourceId') : assignmentRecord.resourceId),
          eventData = resourceData === null || resourceData === void 0 ? void 0 : resourceData.eventsData.find(d => d.eventId === assignmentRecord.eventId); // Update cached config

    if (eventData) {
      eventData[useWrapper ? 'wrapperCls' : 'cls'][cls] = add;
    } // Live update element

    if (element) {
      // Update element
      element.classList[add ? 'add' : 'remove'](cls); // And its DOM config

      element.lastDomConfig.className[cls] = add;
    }
  } // React to rows being removed, refreshes view without any relayouting needed since layout is cached relative to row

  onRemoveRows({
    rows
  }) {
    rows.forEach(row => this.rowMap.delete(row));
    this.onRenderDone();
  } // Update header range on horizontal scroll. No need to draw any tasks, Gantt only cares about vertical scroll

  updateFromHorizontalScroll(scrollLeft) {
    const {
      scheduler
    } = this,
          {
      timeAxisSubGrid,
      timeAxis
    } = scheduler,
          width = timeAxisSubGrid.width,
          // If there are few pixels left from the right most position then just render all remaining ticks,
    // there wouldn't be many. It makes end date reachable with more page zoom levels while not having any poor
    // implications.
    // 5px to make TimeViewRangePageZoom test stable in puppeteer.
    returnEnd = timeAxisSubGrid.scrollable.maxX <= Math.round(scrollLeft) + 5,
          startDate = scheduler.getDateFromCoordinate(Math.max(0, scrollLeft - this.scrollBuffer)),
          endDate = returnEnd ? timeAxis.endDate : scheduler.getDateFromCoordinate(scrollLeft + width + this.scrollBuffer) || timeAxis.endDate;

    if (startDate) {
      this._visibleDateRange = {
        startDate,
        endDate,
        startMS: startDate.getTime(),
        endMS: endDate.getTime()
      }; // Update timeaxis header making it display the new dates

      const range = scheduler.timeView.range = {
        startDate,
        endDate
      };
      scheduler.onVisibleDateRangeChange(range);
      scheduler.trigger('visibleRangeChange', range); // If refresh is suspended, someone else is responsible for updating the UI later

      if (!scheduler.refreshSuspended && scheduler.rowManager.rows.length) {
        // Gets here too early in Safari for ResourceHistogram. ResizeObserver triggers a scroll before rows are
        // rendered first time. Could not track down why, bailing out
        if (scheduler.rowManager.rows[0].id === null) {
          return;
        }

        scheduler.rowManager.rows.forEach(row => this.refreshRow(row, false));
        this.onRenderDone();
      }
    }
  } // Called from SchedulerEventRendering

  repaintEventsForResource(resourceRecord) {
    this.refreshResources([resourceRecord.id]);
  }

  onBeforeRowHeightChange() {
    // Row height is cached per resource, all have to be re-laid out
    this.clearAll();
  } //endregion
  //region Refresh resources

  /**
   * Clears resources directly and redraws them on next project refresh
   * @param {Number[]|String[]} resourceIds
   * @private
   */

  refreshResourcesWhenReady(resourceIds) {
    this.clearResources(resourceIds);
    resourceIds.forEach(id => this.toDrawOnProjectRefresh.add(id));
  }
  /**
   * Clears and redraws resources directly. Respects schedulers refresh suspension
   * @param {Number[]|String[]} ids Resource ids
   * @param {Boolean} [transition] Use transition or not
   * @private
   */

  refreshResources(ids, transition = true) {
    const me = this,
          {
      scheduler
    } = me,
          rows = [],
          noRows = [];
    me.clearResources(ids);

    if (!scheduler.refreshSuspended) {
      ids.forEach(id => {
        const row = scheduler.getRowById(id);

        if (row) {
          rows.push(row);
        } else {
          noRows.push(row);
        }
      });
      scheduler.runWithTransition(() => {
        // Rendering rows populates row heights, but not all resources might have a row in view
        scheduler.calculateRowHeights(noRows.map(id => this.resourceStore.getById(id)), true); // Render those that do

        scheduler.rowManager.renderRows(rows);
      }, transition);
    }
  } //endregion
  //region Stack & pack

  layoutEventVerticallyStack(bandIndex, eventRecord, resourceRecord) {
    const {
      barMargin,
      resourceMargin,
      rowHeight
    } = this.scheduler.getResourceLayoutSettings(resourceRecord);
    return bandIndex === 0 ? resourceMargin : resourceMargin + bandIndex * (rowHeight - resourceMargin * 2) + bandIndex * barMargin;
  }

  layoutEventVerticallyPack(topFraction, heightFraction, eventRecord, resourceRecord) {
    const {
      barMargin,
      resourceMargin,
      rowHeight
    } = this.scheduler.getResourceLayoutSettings(resourceRecord),
          // TODO reduce grid row borders when available, https://app.assembla.com/spaces/bryntum/tickets/5840-measure-grid-row-border-at-first-render/details#
    availableHeight = rowHeight - 2 * resourceMargin,
          count = 1 / heightFraction,
          bandIndex = topFraction * count,
          // "y" within row
    height = (availableHeight - (count - 1) * barMargin) * heightFraction,
          top = resourceMargin + bandIndex * height + bandIndex * barMargin;
    return {
      top,
      height
    };
  } //endregion
  //region Render

  /**
   * Used by event drag features to bring into existence event elements that are outside of the rendered block.
   * @param {Scheduler.model.TimeSpan} eventRecord The event to render
   * @param {Scheduler.model.ResourceModel} [resourceRecord] The event to render
   * @private
   */

  addTemporaryDragElement(eventRecord, resourceRecord = eventRecord.resource) {
    const {
      scheduler
    } = this,
          renderData = scheduler.generateRenderData(eventRecord, resourceRecord, {
      timeAxis: true,
      viewport: true
    });
    renderData.absoluteTop = renderData.row ? renderData.top + renderData.row.top : scheduler.getResourceEventBox(eventRecord, resourceRecord, true).top;
    const domConfig = this.renderEvent(renderData),
          {
      dataset
    } = domConfig;
    delete domConfig.tabIndex;
    delete dataset.eventId;
    delete dataset.resourceId;
    delete dataset.syncId;
    dataset.transient = true;
    domConfig.parent = this.scheduler.foregroundCanvas; // So that the regular DomSyncing which may happen during scroll does not
    // sweep up and reuse the temporary element.

    domConfig.retainElement = true;
    const result = DomHelper.createElement(domConfig);
    result.innerElement = result.firstChild;
    eventRecord.instanceMeta(scheduler).hasTemporaryDragElement = true;
    return result;
  } // Earlier start dates are above later tasks
  // If same start date, longer tasks float to top
  // If same start + duration, sort by name
  // Fn can be called with layout date or event records (from EventNavigation)

  eventSorter(a, b) {
    if (this.horizontalEventSorterFn) {
      return this.horizontalEventSorterFn(a.eventRecord || a, b.eventRecord || b);
    }

    const // TODO: Rename startMS -> startDateMS to not have to have isModel check here (and to be consistent)
    startA = a.isModel ? a.startDateMS : a.dataStartMS || a.startMS,
          // dataXX are used if configured with fillTicks
    endA = a.isModel ? a.endDateMS : a.dataEndMS || a.endMS,
          startB = b.isModel ? b.startDateMS : b.dataStartMS || b.startMS,
          endB = b.isModel ? b.endDateMS : b.dataEndMS || b.endMS,
          nameA = a.isModel ? a.name : a.eventRecord.name,
          nameB = b.isModel ? b.name : b.eventRecord.name;
    return startA - startB || endB - endA || (nameA < nameB ? -1 : nameA == nameB ? 0 : 1);
  }
  /**
   * Converts a start/endDate into a MS value used when rendering the event. If scheduler is configured with
   * `fillTicks: true` the value returned will be snapped to tick start/end.
   * @private
   * @param {Scheduler.model.EventModel} eventRecord
   * @returns {Object} Object of format { startMS, endMS, durationMS }
   */

  calculateMS(eventRecord) {
    const me = this,
          {
      scheduler
    } = me,
          {
      timeAxis,
      timeAxisViewModel
    } = scheduler;
    let startMS = getStartEnd(scheduler, timeAxis, eventRecord.startDate, eventRecord.startDateMS, false),
        endMS = getStartEnd(scheduler, timeAxis, eventRecord.endDate, eventRecord.endDateMS, true),
        durationMS = endMS - startMS;

    if (scheduler.milestoneLayoutMode !== 'default' && durationMS === 0) {
      const pxPerMinute = timeAxisViewModel.getSingleUnitInPixels('minute'),
            lengthInPx = scheduler.getMilestoneLabelWidth(eventRecord),
            duration = lengthInPx * (1 / pxPerMinute);
      durationMS = duration * 60 * 1000;

      switch (scheduler.milestoneAlign) {
        case 'start':
        case 'left':
          endMS = startMS + durationMS;
          break;

        case 'end':
        case 'right':
          endMS = startMS;
          startMS = endMS - durationMS;
          break;

        default:
          // using center as default
          endMS = startMS + durationMS / 2;
          startMS = endMS - durationMS;
          break;
      }
    }

    return {
      startMS,
      endMS,
      durationMS
    };
  }
  /**
   * Gets timespan coordinates etc. Relative to containing row. If the timespan is outside of the zone in
   * which timespans are rendered, that is outside of the TimeAxis, or outside of the vertical zone in which timespans
   * are rendered, then `undefined` is returned.
   * @private
   * @param {Scheduler.model.TimeSpan} timeSpan TimeSpan record
   * @param {Core.data.Model} rowRecord Row record
   * @param {Boolean|Object} includeOutside Specify true to get boxes for timespans outside of the rendered zone in both
   * dimensions. This option is used when calculating dependency lines, and we need to include routes from timespans
   * which may be outside the rendered zone.
   * @param {Boolean} includeOutside.timeAxis Pass as `true` to include timespans outside of the TimeAxis's bounds
   * @param {Boolean} includeOutside.viewport Pass as `true` to include timespans outside of the vertical timespan viewport's bounds.
   * @returns {{event/task: *, left: number, width: number, start: (Date), end: (Date), startMS: number, endMS: number, startsOutsideView: boolean, endsOutsideView: boolean}}
   */

  getTimeSpanRenderData(timeSpan, rowRecord, includeOutside = false) {
    const me = this,
          {
      scheduler
    } = me,
          {
      timeAxis,
      timeAxisViewModel
    } = scheduler,
          includeOutsideTimeAxis = includeOutside === true || includeOutside.timeAxis,
          includeOutsideViewport = includeOutside === true || includeOutside.viewport; // If timespan is outside the TimeAxis, give up trying to calculate a layout (Unless we're including timespans
    // outside our zone)

    if (includeOutsideTimeAxis || timeAxis.isTimeSpanInAxis(timeSpan)) {
      const row = scheduler.getRowById(rowRecord);

      if (row || includeOutsideViewport) {
        const pxPerMinute = timeAxisViewModel.getSingleUnitInPixels('minute'),
              timespanStart = timeSpan.startDate,
              timespanEnd = timeSpan.endDate || timespanStart,
              // Allow timespans to be rendered even when they are missing an end date
        viewStartMS = timeAxis.startMS,
              viewEndMS = timeAxis.endMS,
              {
          startMS,
          endMS,
          durationMS
        } = me.calculateMS(timeSpan),
              // These flags have two components because includeOutsideViewport
        // means that we can be calculating data for events either side of
        // the TimeAxis.
        // The start is outside of the view if it's before *or after* the TimeAxis range.
        // 1 set means the start is before the TimeAxis
        // 2 set means the start is after the TimeAxis
        // Either way, a truthy value means that the start is outside of the TimeAxis.
        startsOutsideView = startMS < viewStartMS | (startMS > viewEndMS) << 1,
              // The end is outside of the view if it's before *or after* the TimeAxis range.
        // 1 set means the end is after the TimeAxis
        // 2 set means the end is before the TimeAxis
        // Either way, a truthy value means that the end is outside of the TimeAxis.
        endsOutsideView = endMS > viewEndMS | (endMS <= viewStartMS) << 1,
              durationMinutes = durationMS / (1000 * 60),
              width = endsOutsideView ? pxPerMinute * durationMinutes : null;
        let endX = scheduler.getCoordinateFromDate(endMS, {
          local: true,
          respectExclusion: true,
          isEnd: true
        }),
            startX,
            clippedStart = false,
            clippedEnd = false; // If event starts outside of view, estimate where.

        if (startsOutsideView) {
          startX = (startMS - viewStartMS) / (1000 * 60) * pxPerMinute;
        } // Starts in view, calculate exactly
        else {
            // If end date is included in time axis but start date is not (when using time axis exclusions), snap start date to next included data
            startX = scheduler.getCoordinateFromDate(startMS, {
              local: true,
              respectExclusion: true,
              isEnd: false,
              snapToNextIncluded: endX !== -1
            });
            clippedStart = startX === -1;
          }

        if (endsOutsideView) {
          endX = startX + width;
        } else {
          clippedEnd = endX === -1;
        }

        if (clippedEnd && !clippedStart) {
          // We know where to start but not where to end, snap it (the opposite is already handled by the
          // snapToNextIncluded flag when calculating startX above)
          endX = scheduler.getCoordinateFromDate(endMS, {
            local: true,
            respectExclusion: true,
            isEnd: true,
            snapToNextIncluded: true
          });
        } // If the element is very wide there's no point in displaying it all.
        // Indeed the element may not be displayable at extremely large widths.

        if (width > MAX_WIDTH) {
          // The start is before the TimeAxis start
          if (startsOutsideView === 1) {
            // Both ends outside - spans TimeAxis
            if (endsOutsideView === 1) {
              startX = -100;
              endX = scheduler.timeAxisColumn.width + 100;
            } // End is in view
            else {
                startX = endX - MAX_WIDTH;
              }
          } // The end is after, but the start is in view
          else if (endsOutsideView === 1) {
              endX = startX + MAX_WIDTH;
            }
        }

        if (clippedStart && clippedEnd) {
          // Both ends excluded, but there might be some part in between that should be displayed...
          startX = scheduler.getCoordinateFromDate(startMS, {
            local: true,
            respectExclusion: true,
            isEnd: false,
            snapToNextIncluded: true,
            max: endMS
          });
          endX = scheduler.getCoordinateFromDate(endMS, {
            local: true,
            respectExclusion: true,
            isEnd: true,
            snapToNextIncluded: true,
            min: startMS
          });

          if (startX === endX) {
            // Raise flag on instance meta to avoid duplicating this logic
            timeSpan.instanceMeta(scheduler).excluded = true; // Excluded by time axis exclusion rules, render nothing

            return null;
          }
        }

        const {
          resourceMargin,
          rowHeight
        } = scheduler.getResourceLayoutSettings(rowRecord),
              data = {
          eventRecord: timeSpan,
          taskRecord: timeSpan,
          // Helps with using Gantt projects in Scheduler Pro
          left: Math.min(startX, endX),
          // Use min width 1 for normal events, 0 for milestones (wont have width specified at all in the end)
          width: Math.abs(endX - startX) || (timeSpan.isMilestone ? 0 : 1),
          start: timespanStart,
          end: timespanEnd,
          rowId: rowRecord.id,
          children: [],
          startMS,
          endMS,
          startsOutsideView,
          endsOutsideView,
          clippedStart,
          clippedEnd,
          row
        }; // If filling ticks we need to also keep datas MS values, since they are used for sorting timespans

        if (scheduler.fillTicks) {
          data.dataStartMS = data.start.getTime();
          data.dataEndMS = data.end.getTime();
        }

        data.top = Math.max(0, resourceMargin);

        if (scheduler.managedEventSizing) {
          // Timespan height should be at least 1px
          data.height = Math.max(rowHeight - 2 * resourceMargin, 1);
        }

        return data;
      }
    }
  } // Lay out events within a resource, relative to the resource

  layoutResourceEvents(resourceRecord, includeOutside = false) {
    const me = this,
          {
      scheduler
    } = me,
          {
      eventStore,
      assignmentStore,
      timeAxis
    } = scheduler,
          // Events for this resource
    resourceEvents = eventStore.getEvents({
      includeOccurrences: scheduler.enableRecurringEvents,
      resourceRecord,
      startDate: timeAxis.startDate,
      endDate: timeAxis.endDate,
      filter: eventRecord => eventRecord.assignments.some(a => a.resource === resourceRecord && assignmentStore.includes(a))
    }),
          // Call a chainable template function on scheduler to allow features to add additional "events" to render
    // Currently used by ResourceTimeRanges
    allEvents = scheduler.getEventsToRender(resourceRecord, resourceEvents) || [],
          // Generate layout data
    eventsData = allEvents.reduce((result, eventRecord) => {
      // Only those in time axis (by default)
      if (includeOutside || timeAxis.isTimeSpanInAxis(eventRecord)) {
        const eventBox = scheduler.generateRenderData(eventRecord, resourceRecord, false); // Collect layouts of visible events

        if (eventBox) {
          result.push(eventBox);
        }
      }

      return result;
    }, []); // Ensure the events are rendered in natural order so that navigation works.

    eventsData.sort(me.eventSorter);
    let rowHeight = scheduler.getResourceHeight(resourceRecord);
    const // Only events and tasks should be considered during layout (not resource time ranges if any)
    layoutEventData = eventsData.filter(d => d.eventRecord.isEvent),
          eventLayout = resourceRecord.eventLayout || scheduler.eventLayout,
          layoutHandler = scheduler.getEventLayoutHandler(eventLayout); // Event data is now gathered, calculate layout properties for each event

    if (eventLayout === 'stack') {
      const {
        barMargin,
        resourceMargin
      } = scheduler.getResourceLayoutSettings(resourceRecord),
            nbrOfBandsRequired = layoutHandler.applyLayout(layoutEventData, resourceRecord),
            heightPerEvent = rowHeight - resourceMargin * 2;
      rowHeight = nbrOfBandsRequired * heightPerEvent + (nbrOfBandsRequired - 1) * barMargin + resourceMargin * 2;
    } else if (eventLayout === 'pack') {
      layoutHandler.applyLayout(layoutEventData, resourceRecord);
    }

    return {
      rowHeight,
      eventsData
    };
  } // Generates a DOMConfig for an EventRecord

  renderEvent(data, rowHeight) {
    const {
      scheduler
    } = this,
          {
      resourceRecord,
      assignmentRecord,
      eventRecord
    } = data,
          // Sync using assignment id for events and event id for ResourceTimeRanges. Add eventId for occurrences to make them unique
    syncId = assignmentRecord // Assignment, might be an occurrence
    ? this.assignmentStore.getOccurrence(assignmentRecord, eventRecord).id // Some thine else, probably a ResourceTimeRange
    : data.eventId,
          // Event element config, applied to existing element or used to create a new one below
    elementConfig = {
      className: data.wrapperCls,
      tabIndex: '0',
      children: [{
        className: data.cls,
        style: data.style || '',
        children: data.children,
        dataset: {
          // Each feature putting contents in the event wrap should have this to simplify syncing and
          // element retrieval after sync
          taskFeature: 'event'
        },
        syncOptions: {
          syncIdField: 'taskBarFeature'
        }
      }, ...data.wrapperChildren],
      style: {
        transform: `translate(${data.left}px, ${data.absoluteTop}px)`,
        // ResourceTimeRanges fill row height, cannot be done earlier than this since row height is not
        // known initially
        height: data.fillSize ? rowHeight : data.height,
        // DomHelper appends px to dimensions when using numbers. Ignore width for milestones
        width: data.width > 0 ? data.width : null,
        style: data.wrapperStyle,
        fontSize: data.height + 'px'
      },
      dataset: {
        resourceId: resourceRecord.id,
        eventId: data.eventId,
        // Not using eventRecord.id to distinguish between Event and ResourceTimeRange
        syncId
      },
      // Will not be part of DOM, but attached to the element
      elementData: data,
      // Dragging etc. flags element as retained, to not reuse/release it during that operation. Events
      // always use assignments, but ResourceTimeRanges does not
      retainElement: (assignmentRecord || eventRecord).instanceMeta(scheduler).retainElement,
      // Options for this level of sync, lower levels can have their own
      syncOptions: {
        syncIdField: 'taskFeature',
        // Remove instead of release when a feature is disabled
        releaseThreshold: 0
      }
    }; // Some browsers throw warnings on zIndex = ''

    if (data.zIndex) {
      elementConfig.zIndex = data.zIndex;
    } // Do not want to spam dataset with empty prop when not using assignments (ResourceTimeRanges)

    if (assignmentRecord) {
      elementConfig.dataset.assignmentId = assignmentRecord.id;
    }

    data.elementConfig = elementConfig;
    scheduler.trigger('beforeRenderEvent', {
      renderData: data,
      domConfig: elementConfig
    });
    return elementConfig;
  }
  /**
   * Refresh a row/record, clearing its cache and forcing DOM refresh.
   * @param {Scheduler.model.ResourceModel} recordOrRow Record or row to refresh
   * @param {Boolean} [force] Specify `false` to prevent clearing cache and forcing DOM refresh
   * @internal
   */

  refreshRow(recordOrRow, force = true) {
    let row, record;

    if (recordOrRow.isModel) {
      row = this.scheduler.rowManager.getRowFor(recordOrRow);
      record = recordOrRow;
    } else {
      row = recordOrRow;
      record = this.resourceStore.getById(recordOrRow.id);
    }

    if (force) {
      this.clearResources([recordOrRow]);
    }

    if (row) {
      this.renderer({
        row,
        record
      });

      if (force) {
        this.onRenderDone();
      }
    }
  } // Called per row in "view", collect configs

  renderer({
    row,
    record: resourceRecord,
    size = {}
  }) {
    // Bail out for group headers/footers
    if (resourceRecord.isSpecialRow) {
      // Clear any cached layout for row retooled to special row, and bail out
      this.rowMap.delete(row);
      return;
    }

    const me = this,
          {
      startMS,
      endMS
    } = me.visibleDateRange,
          eventDOMConfigs = []; // Used stored layouts if available

    let resourceLayout = me.resourceMap.get(resourceRecord.id);

    if (!resourceLayout || resourceLayout.invalid) {
      // Previously we would bail out here if engine wasn't ready. Now we instead allow drawing in most cases,
      // since data can be read and written during commit (previously it could not)
      if (me.suspended) {
        return;
      }

      resourceLayout = me.layoutResourceEvents(resourceRecord, false);
      me.resourceMap.set(resourceRecord.id, resourceLayout);
    } // Size row

    size.height = resourceLayout.rowHeight; // Only collect configs for those actually in view

    resourceLayout.eventsData.forEach(layout => {
      if (layout.endMS >= startMS && layout.startMS <= endMS) {
        layout.absoluteTop = layout.top + row.top;
        eventDOMConfigs.push(me.renderEvent(layout, resourceLayout.rowHeight));
      }
    });
    me.rowMap.set(row, eventDOMConfigs);
  } // Called when the current row rendering "pass" is complete, sync collected configs to DOM

  onRenderDone() {
    const {
      scheduler,
      rowMap
    } = this,
          visibleEventDOMConfigs = []; // Concat all rows event configs

    rowMap.forEach(eventDOMConfigs => visibleEventDOMConfigs.push.apply(visibleEventDOMConfigs, eventDOMConfigs));
    DomSync.sync({
      domConfig: {
        onlyChildren: true,
        children: visibleEventDOMConfigs
      },
      targetElement: scheduler.foregroundCanvas,
      syncIdField: 'syncId',

      // Called by DomHelper when it creates, releases or reuses elements
      callback({
        action,
        domConfig,
        lastDomConfig,
        targetElement
      }) {
        var _domConfig$className;

        // If element is a task wrap, trigger appropriate events
        if (action !== 'none' && domConfig !== null && domConfig !== void 0 && (_domConfig$className = domConfig.className) !== null && _domConfig$className !== void 0 && _domConfig$className[`${scheduler.eventCls}-wrap`]) {
          const // Some actions are considered first a release and then a render (reusing another element).
          // This gives clients code a chance to clean up before reusing an element
          isRelease = releaseEventActions[action],
                isRender = renderEventActions[action]; // Trigger release for events (it might be a proxy element, skip those)

          if (isRelease && lastDomConfig !== null && lastDomConfig !== void 0 && lastDomConfig.elementData) {
            const {
              eventRecord,
              resourceRecord,
              assignmentRecord
            } = lastDomConfig.elementData,
                  event = {
              renderData: lastDomConfig.elementData,
              element: targetElement,
              eventRecord,
              resourceRecord,
              assignmentRecord
            }; // Some browsers do not blur on set to display:none, so releasing the active element
            // must *explicitly* move focus outwards to the view.

            if (targetElement === document.activeElement) {
              scheduler.focusElement.focus();
            } // This event is documented on Scheduler

            scheduler.trigger('releaseEvent', event);
          }

          if (isRender) {
            const {
              eventRecord,
              resourceRecord,
              assignmentRecord
            } = domConfig.elementData,
                  event = {
              renderData: domConfig.elementData,
              tplData: domConfig.elementData,
              // DEPRECATED IN 4.0
              element: targetElement,
              isReusingElement: action === 'reuseElement',
              isRepaint: action === 'reuseOwnElement',
              eventRecord,
              resourceRecord,
              assignmentRecord
            }; // This event is documented on Scheduler

            scheduler.trigger('renderEvent', event);
          }
        }
      }

    });
  } //endregion
  //region Cache
  // Clears cached resource layout

  clearResources(recordsOrIds) {
    if (recordsOrIds instanceof Set) {
      recordsOrIds = Array.from(recordsOrIds);
    } else if (!Array.isArray(recordsOrIds)) {
      recordsOrIds = [recordsOrIds];
    }

    const resourceIds = recordsOrIds.map(Model.asId);
    resourceIds.forEach(resourceId => {
      // Invalidate resourceLayout, keeping it around in case we need it before next refresh
      const cached = this.resourceMap.get(resourceId);

      if (cached) {
        cached.invalid = true;
      }

      const row = this.scheduler.getRowById(resourceId);
      row && this.rowMap.delete(row);
    });
  }

  clearAll() {
    this.resourceMap.clear();
    this.rowMap.clear();
  } //endregion

}
HorizontalRendering._$name = 'HorizontalRendering';

/**
 * @module Scheduler/eventlayout/VerticalLayout
 */

/**
 * Assists with event layout in vertical mode, handles `eventLayout: none|pack|mixed`
 * @private
 * @mixes Scheduler/eventlayout/PackMixin
 */

class VerticalLayout extends PackMixin() {
  static get defaultConfig() {
    return {
      coordProp: 'leftFactor',
      sizeProp: 'widthFactor'
    };
  } // Try to pack the events to consume as little space as possible

  applyLayout(events, columnWidth, resourceMargin, barMargin, columnIndex, eventLayout) {
    super.applyLayout(events, (tplData, clusterIndex, slot, slotSize) => {
      // Stretch events to fill available width
      if (eventLayout === 'none') {
        tplData.width = columnWidth - resourceMargin * 2;
        tplData.left += resourceMargin;
      } else {
        // Fractions of resource column
        tplData.widthFactor = slotSize;
        const leftFactor = tplData.leftFactor = slot.start + clusterIndex * slotSize,
              // Number of "columns" in the current slot
        packColumnCount = Math.round(1 / slotSize),
              // Index among those columns for current event
        packColumnIndex = leftFactor / slotSize,
              // Width with all bar margins subtracted
        availableWidth = columnWidth - resourceMargin * 2 - barMargin * (packColumnCount - 1); // Allowing two events to overlap? Slightly offset the second

        if (eventLayout === 'mixed' && packColumnCount === 2) {
          tplData.left += leftFactor * columnWidth / 5 + barMargin;
          tplData.width = columnWidth - leftFactor * columnWidth / 5 - barMargin * 2;
          tplData.zIndex = 5 + packColumnIndex;
        } // Pack by default
        else {
            // Fractional width
            tplData.width = slotSize * availableWidth; // Translate to absolute position

            tplData.left += leftFactor * availableWidth + resourceMargin + barMargin * packColumnIndex;
          }
      }
    });
  }

}
VerticalLayout._$name = 'VerticalLayout';

/**
 * @module Scheduler/view/orientation/VerticalRendering
 */

const releaseEventActions$1 = {
  releaseElement: 1,
  // Not used at all at the moment
  reuseElement: 1 // Used by some other element

},
      renderEventActions$1 = {
  newElement: 1,
  reuseOwnElement: 1,
  reuseElement: 1
},
      chronoFields$1 = {
  startDate: 1,
  endDate: 1,
  duration: 1
},
      emptyObject$3 = Object.freeze({});
/**
 * Handles event rendering in Schedulers vertical mode. Reacts to project/store changes to keep the UI up to date.
 *
 * @internal
 */

class VerticalRendering extends Base.mixin(Delayable, BaseRendering, AttachToProjectMixin) {
  //region Config & Init
  static get properties() {
    return {
      eventMap: new Map(),
      resourceMap: new Map(),
      releasedElements: {},
      toDrawOnProjectRefresh: new Set()
    };
  }

  construct(scheduler) {
    this.client = this.scheduler = scheduler;
    this.timeAxisViewModel = scheduler.timeAxisViewModel;
    this.verticalLayout = new VerticalLayout({
      scheduler
    });
    super.construct({});
  }

  init() {
    const me = this,
          {
      scheduler
    } = me; // Resource header/columns

    me.resourceColumns = scheduler.timeAxisColumn.resourceColumns;
    me.resourceColumns.resourceStore = me.resourceStore;
    scheduler.element.classList.add('b-sch-vertical');
    me.resourceColumns.on({
      name: 'resourceColumns',
      columnWidthChange: 'onResourceColumnWidthChange',
      thisObj: me
    });
    me.initialized = true;

    if (scheduler.isPainted) {
      me.renderer();
    }

    me.resourceColumns.availableWidth = scheduler.timeAxisSubGridElement.offsetWidth;
  } //endregion
  //region Elements <-> Records

  resolveRowRecord(elementOrEvent, xy) {
    const me = this,
          {
      scheduler
    } = me,
          event = elementOrEvent instanceof Event ? elementOrEvent : null,
          element = event ? event.target : elementOrEvent,
          coords = event ? [event.offsetX, event.offsetY] : xy,
          // Fix for FF on Linux having text nodes as event.target
    el = element.nodeType === 3 ? element.parentElement : element,
          eventElement = DomHelper.up(el, scheduler.eventSelector);

    if (eventElement) {
      return scheduler.resourceStore.getById(eventElement.dataset.resourceId);
    } // Need to be inside schedule at least

    if (!DomHelper.up(element, '.b-sch-timeaxis-cell')) {
      return null;
    }

    if (!coords) {
      throw new Error(`Vertical mode needs coordinates to resolve this element. Can also be called with a browser
                event instead of element to extract element and coordinates from`);
    }

    const index = Math.floor(coords[0] / me.resourceColumns.columnWidth);
    return scheduler.resourceStore.getAt(index);
  }

  toggleCls(assignmentRecord, cls, add = true, useWrapper = false) {
    var _this$eventMap$get;

    const eventData = (_this$eventMap$get = this.eventMap.get(assignmentRecord.eventId)) === null || _this$eventMap$get === void 0 ? void 0 : _this$eventMap$get[assignmentRecord.resourceId];

    if (eventData) {
      eventData.renderData[useWrapper ? 'wrapperCls' : 'cls'][cls] = add; // Element from the map cannot be trusted, might be reused in which case map is not updated to reflect that.
      // To be safe, retrieve using `getElementFromAssignmentRecord`

      const element = this.getElementFromAssignmentRecord(assignmentRecord, useWrapper);

      if (element) {
        element.classList[add ? 'add' : 'remove'](cls);
      }
    }
  } //endregion
  //region Coordinate <-> Date

  getDateFromXY(xy, roundingMethod, local, allowOutOfRange = false) {
    let coord = xy[1];

    if (!local) {
      coord = this.translateToScheduleCoordinate(coord);
    }

    return this.timeAxisViewModel.getDateFromPosition(coord, roundingMethod, allowOutOfRange);
  }

  translateToScheduleCoordinate(y) {
    return y - this.scheduler._bodyRectangle.y + this.scheduler.scrollTop;
  }

  translateToPageCoordinate(y) {
    return y + this.scheduler._bodyRectangle.y - this.scheduler.scrollTop;
  } //endregion
  //region Regions

  getResourceEventBox(event, resource) {
    var _this$eventMap$get2;

    const eventId = event.id,
          resourceId = resource.id;
    let {
      renderData
    } = ((_this$eventMap$get2 = this.eventMap.get(eventId)) === null || _this$eventMap$get2 === void 0 ? void 0 : _this$eventMap$get2[resourceId]) || emptyObject$3;

    if (!renderData) {
      var _this$eventMap$get3, _this$eventMap$get3$r;

      // Never been in view, lay it out
      this.layoutResource(this.scheduler.resourceStore.getById(resourceId)); // Have another go at getting the layout data

      renderData = (_this$eventMap$get3 = this.eventMap.get(eventId)) === null || _this$eventMap$get3 === void 0 ? void 0 : (_this$eventMap$get3$r = _this$eventMap$get3[resourceId]) === null || _this$eventMap$get3$r === void 0 ? void 0 : _this$eventMap$get3$r.renderData;
    }

    return renderData ? new Rectangle(renderData.left, renderData.top, renderData.width, renderData.bottom - renderData.top) : null;
  }

  getScheduleRegion(resourceRecord, eventRecord, local) {
    const me = this,
          {
      scheduler
    } = me;
    const region = Rectangle.from(scheduler.timeAxisSubGridElement);

    if (resourceRecord) {
      // TODO: How to account for eventRecord here?
      region.left = me.resourceStore.indexOf(resourceRecord) * scheduler.resourceColumnWidth;
      region.right = region.left + scheduler.resourceColumnWidth;
    }

    const start = scheduler.timeAxis.startDate,
          end = scheduler.timeAxis.endDate,
          dateConstraints = scheduler.getDateConstraints(resourceRecord, eventRecord) || {
      start,
      end
    },
          startY = scheduler.getCoordinateFromDate(DateHelper.max(start, dateConstraints.start)),
          endY = scheduler.getCoordinateFromDate(DateHelper.min(end, dateConstraints.end));

    if (!local) {
      region.top = me.translateToPageCoordinate(startY);
      region.bottom = me.translateToPageCoordinate(endY);
    } else {
      region.top = startY;
      region.bottom = endY;
    }

    return region;
  }

  getRowRegion(resourceRecord, startDate, endDate) {
    const me = this,
          {
      scheduler
    } = me,
          x = me.resourceStore.indexOf(resourceRecord) * scheduler.resourceColumnWidth,
          taStart = scheduler.timeAxis.startDate,
          taEnd = scheduler.timeAxis.endDate,
          start = startDate ? DateHelper.max(taStart, startDate) : taStart,
          end = endDate ? DateHelper.min(taEnd, endDate) : taEnd,
          startY = scheduler.getCoordinateFromDate(start),
          endY = scheduler.getCoordinateFromDate(end, true, true),
          y = Math.min(startY, endY),
          height = Math.abs(startY - endY);
    return new Rectangle(x, y, scheduler.resourceColumnWidth, height);
  }

  get visibleDateRange() {
    const scheduler = this.scheduler,
          scrollPos = scheduler.scrollable.y,
          height = scheduler.scrollable.clientHeight;
    return {
      startDate: scheduler.getDateFromCoordinate(scrollPos),
      endDate: scheduler.getDateFromCoordinate(scrollPos + height) || scheduler.timeAxis.endDate
    };
  } //endregion
  //region Events
  // Column width changed, rerender fully

  onResourceColumnWidthChange({
    width,
    oldWidth
  }) {
    const me = this,
          {
      scheduler
    } = me; // Fix width of column & header

    me.resourceColumns.width = scheduler.timeAxisColumn.width = me.resourceStore.count * width;
    me.clearAll(); // Only transition large changes, otherwise it is janky when dragging slider in demo

    me.refresh(Math.abs(width - oldWidth) > 30); // Not detected by resizeobserver? Need to call this for virtual scrolling to react to update
    //        scheduler.callEachSubGrid('refreshFakeScroll');
    //        scheduler.refreshVirtualScrollbars();
  } //endregion
  //region Project

  attachToProject(project) {
    super.attachToProject(project);

    if (project) {
      project.on({
        name: 'project',
        refresh: 'onProjectRefresh',
        thisObj: this
      });
    }
  }

  onProjectRefresh() {
    const me = this,
          {
      scheduler,
      toDrawOnProjectRefresh
    } = me;

    if (scheduler.rendered && !scheduler.refreshSuspended) {
      // Either refresh all rows (on for example dataset)
      if (me.refreshAllWhenReady) {
        me.clearAll(); //scheduler.refreshWithTransition();

        me.refresh();
        me.refreshAllWhenReady = false;
      } // Or only affected rows (if any)
      else if (toDrawOnProjectRefresh.size) {
          me.refresh();
        }

      toDrawOnProjectRefresh.clear();
    }
  } //endregion
  //region EventStore

  attachToEventStore(eventStore) {
    super.attachToEventStore(eventStore);
    this.refreshAllWhenReady = true;

    if (eventStore) {
      eventStore.on({
        name: 'eventStore',
        refreshPreCommit: 'onEventStoreRefresh',
        thisObj: this
      });
    }
  }

  onEventStoreRefresh({
    action
  }) {
    if (action === 'batch') {
      this.refreshAllWhenReady = true;
    }
  }

  onEventStoreChange({
    action,
    records,
    record,
    replaced,
    changes,
    isAssign
  }) {
    const me = this,
          eventRecords = records || (record ? [record] : []),
          resourceIds = new Set();
    eventRecords.forEach(eventRecord => {
      eventRecord.resources.forEach(resourceRecord => resourceIds.add(resourceRecord.id));
    });

    switch (action) {
      // No-ops
      case 'sort': // Order in EventStore does not matter, so these actions are no-ops

      case 'group':
      case 'move':
      case 'remove':
        // Remove is a no-op since assignment will also be removed
        return;

      case 'dataset':
        me.refreshAllResourcesWhenReady();
        return;

      case 'add':
      case 'updateMultiple':
        // Just refresh below
        break;

      case 'replace':
        // Gather resources from both the old record and the new one
        replaced.forEach(([, newEvent]) => {
          // Old cleared by changed assignment
          newEvent.resources.map(resourceRecord => resourceIds.add(resourceRecord.id));
        }); // And clear them

        me.clearResources(resourceIds);
        break;

      case 'removeall':
      case 'filter':
        // Clear all when filtering for simplicity. If that turns out to give bad performance, one would need to
        // figure out which events was filtered out and only clear their resources.
        me.clearAll();
        me.refresh();
        return;

      case 'update':
        {
          // Check if changes are graph related or not
          const allChrono = record.$entity ? !Object.keys(changes).some(name => !record.$entity.getField(name)) : !Object.keys(changes).some(name => !chronoFields$1[name]); // If more than one of these in changes, it will affect visuals

          let changeCount = 0;
          if ('startDate' in changes) changeCount++;
          if ('endDate' in changes) changeCount++;
          if ('duration' in changes) changeCount++; // Always redraw non chrono changes (name etc)

          if (!allChrono || changeCount > 1) {
            me.clearResources(resourceIds);
            me.refresh();
          }

          return;
        }
    }

    me.refreshResourcesWhenReady(resourceIds);
  } //endregion
  //region ResourceStore

  attachToResourceStore(resourceStore) {
    const me = this;
    super.attachToResourceStore(resourceStore);
    me.refreshAllWhenReady = true;

    if (me.resourceColumns) {
      me.resourceColumns.resourceStore = resourceStore;
    }

    resourceStore.on({
      name: 'resourceStore',
      changePreCommit: 'onResourceStoreChange',
      refreshPreCommit: 'onResourceStoreRefresh',
      // In vertical, resource store is not the row store but should toggle the load mask
      load: () => me.scheduler.unmaskBody(),
      thisObj: me,
      prio: 1 // Call before others to clear cache before redraw

    });

    if (me.initialized) {
      // Invalidate resource range and events
      me.firstResource = me.lastResource = null;
      me.clearAll();
      me.renderer();
    }
  }

  onResourceStoreChange({
    source: resourceStore,
    action,
    records,
    record,
    replaced,
    changes
  }) {
    const me = this,
          // records for add, record for update, replaced [[old, new]] for replace
    resourceRecords = records || (replaced ? replaced.map(r => r[1]) : record ? [record] : []),
          resourceIds = new Set(resourceRecords.map(resourceRecord => resourceRecord.id)); // Invalidate resource range

    me.firstResource = me.lastResource = null; // Operation that did not invalidate engine, refresh directly

    if (me.scheduler.isEngineReady) {
      switch (action) {
        case 'update':
          if (changes !== null && changes !== void 0 && changes.id) {
            me.clearResources([changes.id.oldValue, changes.id.value]);
          } else {
            me.clearResources([record.id]);
          } // Only the invalidation above needed

          break;

        case 'filter':
          // All filtered out resources needs clearing and so does those not filtered out since they might have
          // moved horizontally when others hide
          me.clearAll();
          break;
      }

      me.refresh(true);
    } // Operation that did invalidate project, update on project refresh
    else {
        switch (action) {
          case 'dataset':
          case 'remove': // Cannot tell from which index it was removed

          case 'removeall':
            me.refreshAllResourcesWhenReady();
            return;

          case 'replace':
          case 'add':
            {
              // Make sure all existing events following added resources are offset correctly
              const firstIndex = resourceRecords.reduce((index, record) => Math.min(index, resourceStore.indexOf(record)), resourceStore.count);

              for (let i = firstIndex; i < resourceStore.count; i++) {
                resourceIds.add(resourceStore.getAt(i).id);
              }
            }
        }

        me.refreshResourcesWhenReady(resourceIds);
      }
  }

  onResourceStoreRefresh({
    action
  }) {
    const me = this;

    if (action === 'group') {
      throw new Error('Grouping of resources not supported in vertical mode');
    }

    if (action === 'sort') {
      // Invalidate resource range
      me.firstResource = me.lastResource = null;
      me.clearAll();
      me.refresh();
    }
  } //endregion
  //region AssignmentStore

  attachToAssignmentStore(assignmentStore) {
    super.attachToAssignmentStore(assignmentStore);
    this.refreshAllWhenReady = true;

    if (assignmentStore) {
      assignmentStore.on({
        name: 'assignmentStore',
        changePreCommit: 'onAssignmentStoreChange',
        refreshPreCommit: 'onAssignmentStoreRefresh',
        thisObj: this
      });
    }
  }

  onAssignmentStoreChange({
    action,
    records,
    replaced,
    record,
    changes
  }) {
    const me = this,
          assignmentRecords = records || (record ? [record] : []),
          resourceIds = new Set(assignmentRecords.map(assignmentRecord => assignmentRecord.resourceId)); // Operation that did not invalidate engine, refresh directly

    if (me.scheduler.isEngineReady) {
      switch (action) {
        case 'filter':
          me.clearAll();
          break;

        case 'update':
          {
            // When reassigning, clear old resource also
            if ('resourceId' in changes) {
              resourceIds.add(changes.resourceId.oldValue);
            } // Ignore engine resolving resourceId -> resource, eventId -> event

            if (!Object.keys(changes).filter(field => field !== 'resource' && field !== 'event').length) {
              return;
            }

            me.clearResources(resourceIds);
          }
      }

      me.refresh(true);
    } // Operation that did invalidate project, update on project refresh
    else {
        switch (action) {
          case 'removeall':
            me.refreshAllResourcesWhenReady();
            return;

          case 'replace':
            // Gather resources from both the old record and the new one
            replaced.forEach(([oldAssignment, newAssignment]) => {
              resourceIds.add(oldAssignment.resourceId);
              resourceIds.add(newAssignment.resourceId);
            });
        }

        me.refreshResourcesWhenReady(resourceIds);
      }
  }

  onAssignmentStoreRefresh({
    action,
    records
  }) {
    if (action === 'batch') {
      this.clearResources(records.map(assignment => assignment.resourceId));
      this.renderer();
    }
  } //endregion
  //region View hooks

  refreshRows(reLayoutEvents) {
    if (reLayoutEvents) {
      this.clearAll();
      this.scheduler.refreshFromRerender = false;
    }
  } // Called from SchedulerEventRendering

  repaintEventsForResource(resourceRecord) {
    this.renderResource(resourceRecord);
  }

  updateFromHorizontalScroll(scrollLeft) {
    if (scrollLeft !== this.prevScrollLeft) {
      this.renderer();
      this.prevScrollLeft = scrollLeft;
    }
  }

  updateFromVerticalScroll() {
    this.renderer();
  }

  scrollResourceIntoView(resourceRecord, options) {
    const {
      scheduler
    } = this,
          x = scheduler.resourceStore.indexOf(resourceRecord) * scheduler.resourceColumnWidth;
    return scheduler.scrollHorizontallyTo(x, options);
  } // Called when viewport size changes

  onViewportResize(width) {
    this.resourceColumns.availableWidth = width;
    this.renderer();
  } // Clear events in case they use date as part of displayed info

  onLocaleChange() {
    this.clearAll();
  } // No need to do anything special

  onDragAbort() {}

  onBeforeRowHeightChange() {}

  onTimeAxisViewModelUpdate() {}

  updateElementId() {}

  releaseTimeSpanDiv() {} //endregion
  //region Refresh resources

  /**
   * Clears resources directly and redraws them on next project refresh
   * @param {Number[]|String[]} resourceIds
   * @private
   */

  refreshResourcesWhenReady(resourceIds) {
    this.clearResources(resourceIds);
    resourceIds.forEach(id => this.toDrawOnProjectRefresh.add(id));
  }
  /**
   * Clears all resources directly and redraws them on next project refresh
   * @private
   */

  refreshAllResourcesWhenReady() {
    this.clearAll();
    this.refreshAllWhenReady = true;
  } //region Rendering
  // Resources in view + buffer

  get resourceRange() {
    const {
      scheduler,
      resourceStore
    } = this;

    if (!resourceStore || !resourceStore.count) {
      return {
        firstResource: -1,
        lastResource: -1
      };
    }

    return {
      firstResource: Math.max(Math.floor(scheduler.scrollLeft / scheduler.resourceColumnWidth) - 1, 0),
      lastResource: Math.min(Math.floor((scheduler.scrollLeft + scheduler.timeAxisSubGrid.width) / scheduler.resourceColumnWidth) + 1, resourceStore.count - 1)
    };
  } // Dates in view + buffer

  get dateRange() {
    const {
      scheduler
    } = this;
    let bottomDate = scheduler.getDateFromCoordinate(Math.min(scheduler.scrollTop + scheduler.bodyHeight + scheduler.tickSize - 1, (scheduler.virtualScrollHeight || scheduler.scrollable.scrollHeight) - 1)); // Might end up below time axis (out of ticks)
    // TODO: Change call order on refresh to make sure this is not needed?

    if (!bottomDate) {
      bottomDate = scheduler.timeAxis.last.endDate;
    }

    let topDate = scheduler.getDateFromCoordinate(Math.max(scheduler.scrollTop - scheduler.tickSize, 0)); // Might end up above time axis when reconfiguring (since this happens as part of rendering)

    if (!topDate) {
      topDate = scheduler.timeAxis.first.startDate;
      bottomDate = scheduler.getDateFromCoordinate(scheduler.bodyHeight + scheduler.tickSize - 1);
    }

    return {
      topDate,
      bottomDate
    };
  }

  getTimeSpanRenderData(eventRecord, resourceRecord, includeOutside = false) {
    const me = this,
          {
      scheduler
    } = me,
          {
      startDate,
      endDate
    } = eventRecord,
          resourceMargin = scheduler.getResourceMargin(resourceRecord),
          top = scheduler.getCoordinateFromDate(startDate),
          // Preliminary values for left & width, used for proxy. Will be changed on layout
    left = me.resourceStore.indexOf(resourceRecord) * scheduler.resourceColumnWidth,
          width = scheduler.resourceColumnWidth - resourceMargin * 2,
          startDateMS = startDate.getTime(),
          endDateMS = endDate.getTime();
    let bottom = scheduler.getCoordinateFromDate(endDate),
        height = bottom - top; // Below, estimate height

    if (bottom === -1) {
      height = Math.round((endDateMS - startDateMS) * scheduler.timeAxisViewModel.getSingleUnitInPixels('ms'));
      bottom = top + height;
    }

    return {
      eventRecord,
      resourceRecord,
      left,
      top,
      bottom,
      width,
      height,
      startDate,
      endDate,
      startDateMS,
      endDateMS,
      children: [],
      // to match horizontal, TODO: should change there
      start: startDate,
      end: endDate,
      startMs: startDateMS,
      endMs: endDateMS
    };
  } // Earlier start dates are above later tasks
  // If same start date, longer tasks float to top
  // If same start + duration, sort by name

  eventSorter(a, b) {
    const startA = a.dataStartMs || a.startDateMS,
          // dataXX are used if configured with fillTicks
    endA = a.dataEndMs || a.endDateMS,
          startB = b.dataStartMs || b.startDateMS,
          endB = b.dataEndMs || b.endDateMS,
          nameA = a.isModel ? a.name : a.eventRecord.name,
          nameB = b.isModel ? b.name : b.eventRecord.name;
    return startA - startB || endB - endA || (nameA < nameB ? -1 : nameA == nameB ? 0 : 1);
  } // Calculate the layout for all events assigned to a resource. Since we are never stacking, the layout of one
  // resource will never affect the others

  layoutResource(resourceRecord) {
    const me = this,
          {
      scheduler
    } = me,
          {
      id: resourceId
    } = resourceRecord,
          // Used in loop, reduce access time a wee bit
    {
      assignmentStore,
      eventStore
    } = scheduler,
          // Cache per resource
    cache = me.resourceMap.set(resourceId, {}).get(resourceId),
          // Resource "column"
    resourceIndex = scheduler.resourceStore.indexOf(resourceRecord),
          {
      barMargin,
      resourceMargin
    } = scheduler.getResourceLayoutSettings(resourceRecord); // Events for the resource, minus those that are filtered out by filtering assignments and events

    let events = resourceRecord.assignments.reduce((events, assignmentRecord) => {
      if (assignmentStore.isAvailable(assignmentRecord) && eventStore.isAvailable(assignmentRecord.event)) {
        events.push(assignmentRecord.event);
      }

      return events;
    }, []); // Hook for features to inject additional timespans to render

    events = scheduler.getEventsToRender(resourceRecord, events); // Generate template data for all events, used for rendering and layout

    const layoutData = events.reduce((toLayout, eventRecord) => {
      const renderData = scheduler.generateRenderData(eventRecord, resourceRecord),
            // Elements will be appended to eventData during syncing
      eventData = {
        renderData
      },
            eventResources = ObjectHelper.getMapPath(me.eventMap, renderData.eventId, {}); // Cache per event, { e1 : { r1 : { xxx }, r2 : ... }, e2 : ... }
      // Uses renderData.eventId in favor of eventRecord.id to work with ResourceTimeRanges

      eventResources[resourceId] = eventData; // Cache per resource

      cache[renderData.eventId] = eventData; // Position ResourceTimeRanges directly, they do not affect the layout of others

      if (renderData.fillSize) {
        renderData.left = resourceIndex * scheduler.resourceColumnWidth;
        renderData.width = scheduler.resourceColumnWidth;
      } // Anything not flagged with `fillSize` should take part in layout
      else {
          toLayout.push(renderData);
        }

      return toLayout;
    }, []); // Ensure the events are rendered in natural order so that navigation works.

    layoutData.sort(me.eventSorter); // Apply per resource event layout (pack, overlap or mixed)

    me.verticalLayout.applyLayout(layoutData, scheduler.resourceColumnWidth, resourceMargin, barMargin, resourceIndex, resourceRecord.eventLayout || scheduler.eventLayout);
    return cache;
  }
  /**
   * Used by event drag features to bring into existence event elements that are outside of the rendered block.
   * @param {Scheduler.model.TimeSpan} eventRecord The event to render
   * @private
   */

  addTemporaryDragElement(eventRecord) {
    const {
      scheduler
    } = this,
          renderData = scheduler.generateRenderData(eventRecord, eventRecord.resource, {
      timeAxis: true,
      viewport: true
    });
    renderData.top = renderData.row ? renderData.top + renderData.row.top : scheduler.getResourceEventBox(eventRecord, eventRecord.resource, true).top;
    const domConfig = this.renderEvent({
      renderData
    }),
          {
      dataset
    } = domConfig;
    delete domConfig.tabIndex;
    delete dataset.eventId;
    delete dataset.resourceId;
    delete dataset.syncId;
    dataset.transient = true;
    domConfig.parent = this.scheduler.foregroundCanvas; // So that the regular DomSyncing which may happen during scroll does not
    // sweep up and reuse the temporary element.

    domConfig.retainElement = true;
    const result = DomHelper.createElement(domConfig);
    result.innerElement = result.firstChild;
    eventRecord.instanceMeta(scheduler).hasTemporaryDragElement = true;
    return result;
  } // TODO: Pretty much identical to HorizontalRendering#renderEvent. Make shared base class
  // Render a single event, aborting if already in DOM. To update an event, first release its element and then render
  // it again. The element will be reused and updated. Keeps code simpler

  renderEvent(eventData) {
    // No point in rendering event that already has an element
    const data = eventData.renderData,
          {
      resourceRecord,
      assignmentRecord
    } = data,
          // Event element config, applied to existing element or used to create a new one below
    elementConfig = {
      className: data.wrapperCls,
      tabIndex: '0',
      children: [{
        className: data.cls,
        style: (data.internalStyle || '') + (data.style || ''),
        children: data.children,
        dataset: {
          // Each feature putting contents in the event wrap should have this to simplify syncing and
          // element retrieval after sync
          taskFeature: 'event'
        },
        syncOptions: {
          syncIdField: 'taskBarFeature'
        }
      }, ...data.wrapperChildren],
      style: {
        transform: `translate(${data.left}px, ${data.top}px)`,
        // DomHelper appends px to dimensions when using numbers
        height: data.height,
        width: data.width,
        style: data.wrapperStyle || ''
      },
      dataset: {
        resourceId: resourceRecord.id,
        eventId: data.eventId,
        // Not using eventRecord.id to distinguish between Event and ResourceTimeRange
        // Sync using assignment id for events and event id for ResourceTimeRanges
        syncId: assignmentRecord ? assignmentRecord.id : data.eventId
      },
      // Will not be part of DOM, but attached to the element
      elementData: eventData,
      // Dragging etc. flags element as retained, to not reuse/release it during that operation. Events
      // always use assignments, but ResourceTimeRanges does not
      retainElement: (assignmentRecord || data.eventRecord).instanceMeta(this.scheduler).retainElement,
      // Options for this level of sync, lower levels can have their own
      syncOptions: {
        syncIdField: 'taskFeature',
        // Remove instead of release when a feature is disabled
        releaseThreshold: 0
      }
    }; // Some browsers throw warnings on zIndex = ''

    if (data.zIndex) {
      elementConfig.zIndex = data.zIndex;
    } // Do not want to spam dataset with empty prop when not using assignments (ResourceTimeRanges)

    if (assignmentRecord) {
      elementConfig.dataset.assignmentId = assignmentRecord.id;
    }

    this.scheduler.trigger('beforeRenderEvent', {
      renderData: data,
      domConfig: elementConfig
    }); // Allows access to the used config later, for example to retrieve element

    eventData.elementConfig = elementConfig;
    return elementConfig;
  }

  renderResource(resourceRecord) {
    const me = this,
          // Date at top and bottom for determining which events to include
    {
      topDateMS,
      bottomDateMS
    } = me,
          // Will hold element configs
    syncConfigs = [];
    let resourceEntry = me.resourceMap.get(resourceRecord.id); // Layout all events for the resource unless already done

    if (!resourceEntry) {
      resourceEntry = me.layoutResource(resourceRecord);
    } // Iterate over all events for the resource

    for (const eventId in resourceEntry) {
      const eventData = resourceEntry[eventId],
            {
        endDateMS,
        startDateMS,
        eventRecord
      } = eventData.renderData;

      if ( // Only collect configs for those actually in view
      endDateMS >= topDateMS && startDateMS <= bottomDateMS && // And not being dragged, those has a temporary element already
      !eventRecord.instanceMeta(me.scheduler).hasTemporaryDragElement) {
        syncConfigs.push(me.renderEvent(eventData));
      }
    }

    return syncConfigs;
  }

  isEventElement(domConfig) {
    const className = domConfig && domConfig.className;
    return className && className[this.scheduler.eventCls + '-wrap'];
  } // Single cell so only one call to this renderer, determine which events are in view and draw them.
  // Drawing on scroll is triggered by `updateFromVerticalScroll()` and `updateFromHorizontalScroll()`

  renderer() {
    const me = this,
          {
      scheduler
    } = me,
          {
      resourceStore
    } = scheduler,
          // Determine resource range to draw events for
    {
      firstResource,
      lastResource
    } = me.resourceRange,
          // Date at top and bottom for determining which events to include
    {
      topDate,
      bottomDate
    } = me.dateRange,
          syncConfigs = [],
          featureDomConfigs = [];

    if (!me.initialized || !me.scheduler.isEngineReady) {
      return;
    } // Update current time range, reflecting the change on the vertical time axis header

    if (!DateHelper.isEqual(topDate, me.topDate) || !DateHelper.isEqual(bottomDate, me.bottomDate)) {
      // Calculated values used by `renderResource()`
      me.topDate = topDate;
      me.bottomDate = bottomDate;
      me.topDateMS = topDate.getTime();
      me.bottomDateMS = bottomDate.getTime();
      const range = me.timeView.range = {
        startDate: topDate,
        endDate: bottomDate
      };
      scheduler.onVisibleDateRangeChange(range);
      scheduler.trigger('visibleRangeChange', range);
    }

    if (firstResource !== -1 && lastResource !== -1) {
      // Collect all events for resources in view
      for (let i = firstResource; i <= lastResource; i++) {
        syncConfigs.push.apply(syncConfigs, me.renderResource(resourceStore.getAt(i)));
      }
    }

    scheduler.getForegroundDomConfigs(featureDomConfigs);
    syncConfigs.push.apply(syncConfigs, featureDomConfigs);
    DomSync.sync({
      domConfig: {
        onlyChildren: true,
        children: syncConfigs
      },
      targetElement: scheduler.foregroundCanvas,
      syncIdField: 'syncId',

      // Called by DomHelper when it creates, releases or reuses elements
      callback({
        action,
        domConfig,
        lastDomConfig,
        targetElement
      }) {
        // If element is an event wrap, trigger appropriate events
        if (me.isEventElement(domConfig)) {
          const // Some actions are considered first a release and then a render (reusing another element).
          // This gives clients code a chance to clean up before reusing an element
          isRelease = releaseEventActions$1[action],
                isRender = renderEventActions$1[action]; // If we are reusing an element that was previously released we should not trigger again

          if (isRelease && me.isEventElement(lastDomConfig) && !lastDomConfig.isReleased) {
            const data = lastDomConfig.elementData.renderData,
                  event = {
              renderData: data,
              assignmentRecord: data.assignmentRecord,
              eventRecord: data.eventRecord,
              resourceRecord: data.resourceRecord,
              element: targetElement
            }; // Some browsers do not blur on set to display:none, so releasing the active element
            // must *explicitly* move focus outwards to the view.

            if (targetElement === document.activeElement) {
              scheduler.focusElement.focus();
            } // This event is documented on Scheduler

            scheduler.trigger('releaseEvent', event);
          }

          if (isRender) {
            const data = domConfig.elementData.renderData,
                  event = {
              renderData: data,
              tplData: data,
              // DEPRECATED IN 4.0
              assignmentRecord: data.assignmentRecord,
              eventRecord: data.eventRecord,
              resourceRecord: data.resourceRecord,
              element: targetElement,
              isReusingElement: action === 'reuseElement',
              isRepaint: action === 'reuseOwnElement'
            };
            event.reusingElement = action === 'reuseElement'; // This event is documented on Scheduler

            scheduler.trigger('renderEvent', event);
          }
        }
      }

    }); // Change in displayed resources?

    if (me.firstResource !== firstResource || me.lastResource !== lastResource) {
      // Update header to match
      const range = me.resourceColumns.range = {
        firstResource,
        lastResource
      }; // Store which resources are currently in view

      me.firstResource = firstResource;
      me.lastResource = lastResource;
      scheduler.onVisibleResourceRangeChange(range);
      scheduler.trigger('resourceRangeChange', range);
    }
  }

  refresh(transition) {
    this.scheduler.runWithTransition(() => this.renderer(), transition);
  } // To match horizontals API, used from EventDrag

  refreshResources(resourceIds) {
    this.clearResources(resourceIds);
    this.refresh();
  } //endregion
  //region Other

  get timeView() {
    return this.scheduler.timeView;
  } //endregion
  //region Cache
  // Clears cached resource layout

  clearResources(resourceIds) {
    resourceIds.forEach(resourceId => {
      resourceId = String(resourceId);

      if (this.resourceMap.has(resourceId)) {
        // The *keys* of an Object are strings, so we must iterate the values
        // and use the original eventId to look up in the Map which preserves key type.
        Object.values(this.resourceMap.get(resourceId)).forEach(({
          renderData: {
            eventId
          }
        }) => {
          delete this.eventMap.get(eventId)[resourceId];
        });
        this.resourceMap.delete(resourceId);
      }
    });
  }

  clearAll() {
    this.resourceMap.clear();
    this.eventMap.clear();
  } //endregion

}
VerticalRendering._$name = 'VerticalRendering';

/**
 * @module Scheduler/view/TimeAxisBase
 */

function isLastLevel(level, levels) {
  return level === levels.length - 1;
}

function isLastCell(level, cell) {
  return cell === level.cells[level.cells.length - 1];
}
/**
 * Base class for HorizontalTimeAxis and VerticalTimeAxis. Contains shared functionality to only render ticks in view,
 * should not be used directly.
 *
 * @extends Core/widget/Widget
 * @private
 * @abstract
 */

class TimeAxisBase extends Widget {
  static get $name() {
    return 'TimeAxisBase';
  } //region Config

  static get configurable() {
    return {
      /**
       * The minimum width for a bottom row header cell to be considered 'compact', which adds a special CSS class
       * to the row (for special styling). Copied from Scheduler/Gantt.
       * @config {Number}
       * @default
       */
      compactCellWidthThreshold: 15,
      // TimeAxisViewModel
      model: null,
      cls: null,

      /**
       * Style property to use as cell size. Either width or height depending on orientation
       * @config {String}
       * @private
       */
      sizeProperty: null,

      /**
       * Style property to use as cells position. Either left or top depending on orientation
       * @config {String}
       * @private
       */
      positionProperty: null
    };
  }

  static get properties() {
    return {
      startDate: null,
      endDate: null,
      levels: [],
      size: null
    };
  } // Set visible date range

  set range(range) {
    this.startDate = range.startDate;
    this.endDate = range.endDate;
    this.refresh();
  } //endregion
  //region Html & rendering
  // Generates element configs for all levels defined by the current ViewPreset

  buildCells() {
    var _me$client;

    const me = this,
          {
      sizeProperty
    } = me,
          {
      stickyHeaders,
      isVertical
    } = me.client || {},
          featureHeaderConfigs = [],
          {
      length
    } = me.levels,
          cellConfigs = me.levels.map((level, i) => {
      var _level$cells;

      const stickyHeader = stickyHeaders && (isVertical || i < length - 1);
      return {
        className: {
          'b-sch-header-row': 1,
          [`b-sch-header-row-${level.position}`]: 1,
          'b-sch-header-row-main': i === me.model.viewPreset.mainHeaderLevel,
          'b-lowest': isLastLevel(i, me.levels),
          'b-sticky-header': stickyHeader
        },
        syncOptions: {
          // Keep a maximum of 5 released cells. Might be fine with fewer since ticks are fixed width.
          // Prevents an unnecessary amount of cells from sticking around when switching from narrow to
          // wide tickSizes
          releaseThreshold: 5,
          syncIdField: 'tickIndex'
        },
        dataset: {
          headerFeature: `headerRow${i}`,
          headerPosition: level.position
        },
        // Only include cells in view
        children: (_level$cells = level.cells) === null || _level$cells === void 0 ? void 0 : _level$cells.filter(cell => cell.start < me.endDate && cell.end > me.startDate).map(cell => ({
          className: {
            'b-sch-header-timeaxis-cell': 1,
            [cell.headerCellCls]: cell.headerCellCls,
            [`b-align-${cell.align}`]: cell.align,
            'b-last': isLastCell(level, cell)
          },
          dataset: {
            tickIndex: cell.index,
            // Used in export tests to resolve dates from tick elements
            ...(window.DEBUG && {
              date: cell.start.getTime()
            })
          },
          style: {
            // DomHelper appends px to numeric dimensions
            [me.positionProperty]: cell.coord,
            [sizeProperty]: cell.width,
            [`min-${sizeProperty}`]: cell.width
          },
          children: [{
            tag: 'span',
            className: {
              'b-sch-header-text': 1,
              'b-sticky-header': stickyHeader
            },
            html: cell.value
          }]
        }))
      };
    }); // When tested in isolation there is no client

    (_me$client = me.client) === null || _me$client === void 0 ? void 0 : _me$client.getHeaderDomConfigs(featureHeaderConfigs);
    cellConfigs.push(...featureHeaderConfigs); // noinspection JSSuspiciousNameCombination

    return {
      className: me.widgetClassList,
      syncOptions: {
        // Do not keep entire levels no longer used, for example after switching view preset
        releaseThreshold: 0
      },
      children: cellConfigs
    };
  }

  render(targetElement) {
    super.render(targetElement);
    this.refresh(true);
  }
  /**
   * Refresh the UI
   * @param {Boolean} [rebuild] Specify `true` to force a rebuild of the underlying header level definitions
   */

  refresh(rebuild = !this.levels.length) {
    const me = this,
          {
      columnConfig
    } = me.model,
          {
      levels
    } = me,
          oldLevelsCount = levels.length;

    if (rebuild) {
      levels.length = 0;
      columnConfig.forEach((cells, position) => levels[position] = {
        position,
        cells
      });
      me.size = levels[0].cells.reduce((sum, cell) => sum += cell.width, 0); // TODO clean up when this is fixed: https://app.assembla.com/spaces/bryntum/tickets/8413-horizontaltimeaxis-should-not-completely-overwrite-contents-of-column-el/details#

      const parentEl = me.element.parentElement;

      if (parentEl) {
        parentEl.classList.remove(`b-sch-timeaxiscolumn-levels-${oldLevelsCount}`);
        parentEl.classList.add(`b-sch-timeaxiscolumn-levels-${levels.length}`);
      }
    }

    if (!me.startDate || !me.endDate) {
      return;
    } // Boil down levels to only show what is in view

    DomSync.sync({
      domConfig: me.buildCells(),
      targetElement: me.element,
      syncIdField: 'headerFeature'
    });
    me.trigger('refresh');
  } //endregion
  // Our widget class doesn't include "base".

  get widgetClass() {
    return 'b-timeaxis';
  }

}
TimeAxisBase._$name = 'TimeAxisBase';

/**
 * @module Scheduler/view/HorizontalTimeAxis
 */

/**
 * A visual horizontal representation of the time axis described in the
 * {@link Scheduler.preset.ViewPreset#field-headers headers}.
 * Normally you should not interact with this class directly.
 *
 * @extends Scheduler/view/TimeAxisBase
 * @private
 */

class HorizontalTimeAxis extends TimeAxisBase {
  static get $name() {
    return 'HorizontalTimeAxis';
  }

  static get configurable() {
    return {
      model: null,
      sizeProperty: 'width',
      positionProperty: 'left'
    };
  }

  get width() {
    return this.size;
  }

  onModelUpdate() {
    // Force rebuild when availableSpace has changed, to recalculate width and maybe apply compact styling
    if (this.model.availableSpace !== this.width) {
      this.refresh(true);
    }
  }

  updateModel(timeAxisViewModel) {
    this.detachListeners('tavm');
    timeAxisViewModel === null || timeAxisViewModel === void 0 ? void 0 : timeAxisViewModel.on({
      name: 'tavm',
      update: 'onModelUpdate',
      thisObj: this
    });
  }

}
HorizontalTimeAxis._$name = 'HorizontalTimeAxis';

/**
 * @module Scheduler/view/ResourceHeader
 */

/**
 * Header widget that renders resource column headers and acts as the interaction point for resource columns in vertical
 * mode. Note that it uses virtual rendering and element reusage to gain performance, only headers in view are available
 * in DOM. Because of this you should avoid direct element manipulation, any such changes can be discarded at any time.
 *
 * By default it displays resources `name` and also applies its `iconCls` if any, like this:
 *
 * ```
 * <i class="iconCls">name</i>
 * ```
 *
 * If Scheduler is configured with a {@link Scheduler.view.mixin.SchedulerEventRendering#config-resourceImagePath} the
 * header will render miniatures for the resources, using {@link Scheduler.model.mixin.ResourceModelMixin#field-imageUrl} or {@link Scheduler.model.mixin.ResourceModelMixin#field-image} with
 * fallback to {@link Scheduler.model.mixin.ResourceModelMixin#field-name} + {@link Scheduler.view.mixin.SchedulerEventRendering#config-resourceImageExtension} for unset values.
 *
 * The contents and styling of the resource cells in the header can be customized using {@link #config-headerRenderer}:
 *
 * ```
 * new Scheduler({
 *     mode            : 'vertical',
 *     resourceColumns : {
 *         headerRenderer : ({ resourceRecord }) => `Hello ${resourceRecord.name}`
 *     }
 * }
 *```
 *
 * The width of the resource columns is determined by the {@link #config-columnWidth} config.
 *
 * @extends Core/widget/Widget
 */

class ResourceHeader extends Widget {
  //region Config
  static get $name() {
    return 'ResourceHeader';
  }

  static get defaultConfig() {
    return {
      /**
       * Resource store used to render resource headers. Assigned from Scheduler.
       * @config {Scheduler.data.ResourceStore}
       * @private
       */
      resourceStore: null,
      // TODO: Read this value from CSS as we do with rowHeight?

      /**
       * Width for each resource column
       * @config {Number}
       */
      columnWidth: 150,

      /**
       * Custom header renderer function. Can be used to manipulate the element config used to create the element
       * for the header:
       *
       * ```
       * new Scheduler({
       *   resourceColumns : {
       *     headerRenderer({ elementConfig, resourceRecord }) {
       *       elementConfig.dataset.myExtraData = 'extra';
       *       elementConfig.style.fontWeight = 'bold';
       *     }
       *   }
       * });
       * ```
       *
       * See {@link Core.helper.DomHelper#function-createElement-static DomHelper#createElement()} for more information.
       * Please take care to not break the default configs :)
       *
       * Or as a template by returning HTML from the function:
       *
       * ```
       * new Scheduler({
       *   resourceColumns : {
       *     headerRenderer : ({ resourceRecord }) => `
       *       <div class="my-custom-template">
       *       ${resourceRecord.firstName} {resourceRecord.surname}
       *       </div>
       *     `
       *   }
       * });
       * ```
       *
       * NOTE: When using `headerRenderer` no default internal markup is applied to the resource header cell,
       * `iconCls` and {@link Scheduler.model.mixin.ResourceModelMixin#field-imageUrl} or {@link Scheduler.model.mixin.ResourceModelMixin#field-image}
       * will have no effect unless you supply custom markup for them.
       *
       * @config {Function}
       * @param {Object} params Object containing the params below
       * @param {Scheduler.model.ResourceModel} resourceRecord Resource whose header is being rendered
       * @param {Object} elementConfig A {@link Core.helper.DomHelper#function-createElement-static} config object used to create the element for the resource
       */
      headerRenderer: null,

      /**
       * Automatically resize resource columns to **fill** available width. Set to `false` to always respect the
       * configured `columnWidth`
       * @config {Boolean}
       * @default
       */
      fillWidth: true,

      /**
       * Automatically resize resource columns to always **fit** available width
       * @config {Boolean}
       * @default
       */
      fitWidth: false,
      // Copied from Scheduler#resourceImagePath on creation in TimeAxisColumn.js
      imagePath: null,
      // Copied from Scheduler#resourceImageExtension on creation in TimeAxisColumn.js
      imageExtension: null,
      // Copied from Scheduler#defaultResourceImageName on creation in TimeAxisColumn.js
      defaultImageName: null
    };
  }

  static get properties() {
    return {
      firstResource: -1,
      lastResource: -1
    };
  } //endregion
  //region Init

  construct(config) {
    super.construct(config);
    const me = this;

    if (me.imagePath != null) {
      // Apply default image on load errors
      EventHelper.on({
        element: me.element,
        delegate: '.b-resource-image',
        capture: true,
        error: event => me.setDefaultResourceImage(event.target),
        thisObj: me
      }); // Need to gain some height when displaying images

      me.element.classList.add('b-has-images');
    }

    EventHelper.on({
      element: me.element,
      delegate: '.b-resourceheader-cell',
      capture: true,
      click: 'onResourceMouseEvent',
      dblclick: 'onResourceMouseEvent',
      contextmenu: 'onResourceMouseEvent',
      thisObj: me
    });
  } //endregion
  //region ResourceStore

  set resourceStore(store) {
    const me = this;

    if (store !== me._resourceStore) {
      var _me$resourceStoreDeta;

      (_me$resourceStoreDeta = me.resourceStoreDetacher) === null || _me$resourceStoreDeta === void 0 ? void 0 : _me$resourceStoreDeta.call(me);
      me._resourceStore = store;
      me.resourceStoreDetacher = store.on({
        changePreCommit: 'onResourceStoreDataChange',
        thisObj: me
      }); // Already have data? Update width etc

      if (store.count) {
        me.onResourceStoreDataChange({});
      }
    }
  }

  get resourceStore() {
    return this._resourceStore;
  } // Redraw resource headers on any data change

  onResourceStoreDataChange({
    action
  }) {
    const me = this,
          width = me.resourceStore.count * me.columnWidth;

    if (width !== me.width) {
      me.element.style.width = width + 'px'; // During setup, silently set the width. It will then render correctly. After setup, let the world know...

      me.column.set('width', width, me.column.grid.isConfiguring);
    }

    if (action === 'removeall') {
      // Keep nothing
      me.element.innerHTML = '';
    }

    if (action === 'remove' || action === 'add' || action === 'filter') {
      me.refreshWidths();
    }
  } //endregion
  //region Properties

  get columnWidth() {
    return this._columnWidth;
  }

  set columnWidth(width) {
    const me = this;

    if (width !== me._columnWidth) {
      const oldWidth = me._columnWidth;
      me._columnWidth = width; // Flag set in refreshWidths, do not want to create a loop

      if (!me.refreshingWidths) {
        me._originalColumnWidth = width;
        me.refreshWidths();
      }

      if (!me.isConfiguring) {
        me.refresh(); // Cannot trigger with requested width, might have changed because of fit/fill

        me.trigger('columnWidthChange', {
          width: me._columnWidth,
          oldWidth
        });
      }
    }
  }
  /**
   * Assign to toggle resource columns **fill* mode. `true` means they will stretch (grow) to fill viewport, `false`
   * that they will respect their configured `columnWidth`.
   * @property {Boolean}
   */

  get fillWidth() {
    return this._fillWidth;
  }

  set fillWidth(fill) {
    this._fillWidth = fill;
    this.refreshWidths();
  }
  /**
   * Assign to toggle resource columns **fit* mode. `true` means they will grow or shrink to always fit viewport,
   * `false` that they will respect their configured `columnWidth`.
   * @property {Boolean}
   */

  get fitWidth() {
    return this._fitWidth;
  }

  set fitWidth(fit) {
    this._fitWidth = fit;
    this.refreshWidths();
  }

  setDefaultResourceImage(target) {
    if (this.defaultImageName) {
      const defaultURL = this.getImageURL(this.defaultImageName); // Set image to defaultURL if it is not already set

      if (target.src && !target.src.endsWith(defaultURL.replace(/^[./]*/gm, ''))) {
        target.src = defaultURL;
      }
    }
  }

  getImageURL(imageName) {
    return StringHelper.joinPaths([this.imagePath || '', imageName || '']);
  }

  get imagePath() {
    return this._imagePath;
  }

  set imagePath(path) {
    this._imagePath = path;
    this.refresh();
  } //endregion
  //region Fit to width

  get availableWidth() {
    return this._availableWidth;
  }

  set availableWidth(width) {
    this._availableWidth = width;
    this.refreshWidths();
  } // Updates the column widths according to fill and fit settings

  refreshWidths() {
    const me = this,
          {
      availableWidth,
      _originalColumnWidth
    } = me,
          count = me.resourceStore && me.resourceStore.count; // Bail out if availableWidth not yet set or resource store not assigned/loaded

    if (!availableWidth || !count) {
      return;
    }

    me.refreshingWidths = true;
    const // Fit width if configured to do so or if configured to fill and used width is less than available width
    fit = me.fitWidth || me.fillWidth && _originalColumnWidth * count < availableWidth,
          useWidth = fit ? Math.floor(availableWidth / count) : _originalColumnWidth,
          shouldAnimate = me.column.grid.enableEventAnimations && Math.abs(me._columnWidth - useWidth) > 30;
    DomHelper.addTemporaryClass(me.element, 'b-animating', shouldAnimate ? 300 : 0, me);
    me.columnWidth = useWidth;
    me.refreshingWidths = false;
  } //endregion
  //region Rendering
  // Visual resource range, set by VerticalRendering

  set range({
    firstResource,
    lastResource
  }) {
    this.firstResource = firstResource;
    this.lastResource = lastResource;
    this.refresh();
  }
  /**
   * Refreshes the visible headers
   */

  refresh() {
    const me = this,
          {
      firstResource,
      lastResource
    } = me; // Bail out if we are configuring or have no resources to show

    if (me.column.grid.isConfiguring || firstResource === -1 || lastResource === -1 || lastResource >= me.resourceStore.count) {
      return;
    }

    const configs = []; // Gather element configs for resource headers in view

    for (let i = firstResource; i <= lastResource; i++) {
      const resourceRecord = me.resourceStore.getAt(i),
            elementConfig = {
        // Might look like overkill to use DomClassList here, but can be used in headerRenderer
        className: new DomClassList({
          'b-resourceheader-cell': 1
        }),
        dataset: {
          resourceId: resourceRecord.id
        },
        style: {
          left: i * me.columnWidth,
          width: me.columnWidth
        },
        children: []
      }; // Let a configured headerRenderer have a go at it before applying

      if (me.headerRenderer) {
        const value = me.headerRenderer({
          elementConfig,
          resourceRecord
        });

        if (value != null) {
          elementConfig.html = value;
        }
      } // No headerRenderer, apply default markup
      else {
          let imageUrl;

          if (resourceRecord.imageUrl) {
            imageUrl = resourceRecord.imageUrl;
          } else {
            if (me.imagePath != null) {
              const imageName = resourceRecord.image || resourceRecord.name && resourceRecord.name.toLowerCase() + me.imageExtension || me.defaultImageName;
              imageUrl = me.getImageURL(imageName);
            }
          } // Optionally displaying a miniature image

          if (imageUrl != null) {
            elementConfig.children.push({
              tag: 'img',
              className: 'b-resource-image',
              src: imageUrl
            });
          } // Optionally displaying an icon

          if (resourceRecord.iconCls) {
            elementConfig.children.push({
              tag: 'i',
              className: resourceRecord.iconCls
            });
          } // By default showing resource name

          elementConfig.children.push({
            tag: 'span',
            className: 'b-resource-name',
            html: resourceRecord.name
          });
        }

      configs.push(elementConfig);
    } // Sync changes to the header

    DomSync.sync({
      domConfig: {
        onlyChildren: true,
        children: configs
      },
      targetElement: me.element,
      syncIdField: 'resourceId' // TODO: Add callback here to trigger events when rendering/derendering header cells. Sooner or later
      //  someone is going to ask for a way to render JSX or what not to the header

    });
  } //endregion

  onResourceMouseEvent(event) {
    const resourceCell = event.target.closest('.b-resourceheader-cell'),
          resourceRecord = this.resourceStore.getById(resourceCell.dataset.resourceId);
    this.trigger('resourceHeader' + StringHelper.capitalize(event.type), {
      resourceRecord,
      event
    });
  }

}
ResourceHeader._$name = 'ResourceHeader';

/**
 * @module Scheduler/column/TimeAxisColumn
 */

/**
 * A column containing the timeline "viewport", in which events, dependencies etc are drawn.
 * Normally you do not need to interact with or create this column, it is handled by Scheduler.
 *
 * @extends Grid/column/Column
 */

class TimeAxisColumn extends Events(Column) {
  // region Events

  /**
   * Fires after a click on a time axis cell
   * @event timeAxisHeaderClick
   * @param {Scheduler.column.TimeAxisColumn} column The column object
   * @param {Date} startDate The start date of the header cell
   * @param {Date} endDate The start date of the header cell
   * @param {Event} event The event object
   */

  /**
   * Fires after a double click on a time axis cell
   * @event timeAxisHeaderDblClick
   * @param {Scheduler.column.TimeAxisColumn} column The column object
   * @param {Date} startDate The start date of the header cell
   * @param {Date} endDate The end date of the header cell
   * @param {Event} event The event object
   */

  /**
   * Fires after a right click on a time axis cell
   * @event timeAxisHeaderContextMenu
   * @param {Scheduler.column.TimeAxisColumn} column The column object
   * @param {Date} startDate The start date of the header cell
   * @param {Date} endDate The start date of the header cell
   * @param {Event} event The event object
   */
  //endregion
  //region Init
  construct() {
    const me = this;
    super.construct(...arguments);
    me.initialRender = true;
    me.thisObj = me;
    me.timeAxisViewModel = me.timeline.timeAxisViewModel; // A bit hacky, because mode is a field and not a config
    // eslint-disable-next-line no-self-assign

    me.mode = me.mode;
    me.grid.on({
      paint: 'onTimelinePaint',
      thisObj: me,
      once: true
    });
  }

  static get autoExposeFields() {
    return true;
  } // endregion
  //region Config

  static get fields() {
    return ['mode'];
  }

  static get defaults() {
    return {
      /**
       * Set to false to prevent this column header from being dragged.
       * @config {Boolean} draggable
       * @category Interaction
       * @default false
       */
      draggable: false,

      /**
       * Set to false to prevent grouping by this column.
       * @config {Boolean} groupable
       * @category Interaction
       * @default false
       */
      groupable: false,

      /**
       * Allow column visibility to be toggled through UI.
       * @config {Boolean} hideable
       * @default false
       * @category Interaction
       */
      hideable: false,

      /**
       * Show column picker for the column.
       * @config {Boolean} showColumnPicker
       * @default false
       * @category Menu
       */
      showColumnPicker: false,

      /**
       * Allow filtering data in the column (if Filter feature is enabled)
       * @config {Boolean} filterable
       * @default false
       * @category Interaction
       */
      filterable: false,

      /**
       * Allow sorting of data in the column
       * @config {Boolean} sortable
       * @category Interaction
       * @default false
       */
      sortable: false,

      /**
       * Set to `false` to prevent the column from being drag-resized when the ColumnResize plugin is enabled.
       * @config {Boolean} resizable
       * @default false
       * @category Interaction
       */
      resizable: false,

      /**
       * Allow searching in the column (respected by QuickFind and Search features)
       * @config {Boolean} searchable
       * @default false
       * @category Interaction
       */
      searchable: false,

      /**
       * @config {String} editor
       * @hide
       */
      editor: false,

      /**
       * Set to `true` to show a context menu on the cell elements in this column
       * @config {Boolean} enableCellContextMenu
       * @default false
       * @category Menu
       */
      enableCellContextMenu: false,

      /**
       * @config {Function|Boolean} tooltipRenderer
       * @hide
       */
      tooltipRenderer: false,

      /**
       * CSS class added to the header of this column
       * @config {String} cls
       * @category Rendering
       * @default 'b-sch-timeaxiscolumn'
       */
      cls: 'b-sch-timeaxiscolumn',
      // needs to have width specified, flex-basis messes measurements up
      needWidth: true,
      mode: null,
      region: 'normal',
      exportable: false
    };
  }

  static get type() {
    return 'timeAxis';
  }

  doDestroy() {
    this.resourceColumns && this.resourceColumns.destroy();
    super.doDestroy();
  }

  set mode(mode) {
    const me = this,
          {
      timeline
    } = me;
    me.set('mode', mode); // In horizontal mode this column has a time axis header on top, with timeline ticks

    if (mode === 'horizontal') {
      me.timeAxisView = new HorizontalTimeAxis({
        model: me.timeAxisViewModel,
        compactCellWidthThreshold: me.compactCellWidthThreshold,
        owner: me.grid,
        client: me.grid
      });
    } // In vertical mode, it instead displays resources at top
    else if (mode === 'vertical') {
        // TODO: Most other vertical stuff is handled in VerticalRendering, move there?
        me.resourceColumns = new ResourceHeader(ObjectHelper.assign({
          column: me,
          resourceStore: timeline.resourceStore,
          imagePath: timeline.resourceImagePath,
          imageExtension: timeline.resourceImageExtension,
          defaultImageName: timeline.defaultResourceImageName
        }, timeline.resourceColumns || {}));
        me.relayEvents(me.resourceColumns, ['resourceheaderclick', 'resourceheaderdblclick', 'resourceheadercontextmenu']);
      }
  }

  get mode() {
    return this.get('mode');
  } // TODO: define all configs as fields and set below to false...
  //region Events

  onViewModelUpdate({
    source: viewModel
  }) {
    const me = this;

    if (me.mode === 'horizontal') {
      // render the time axis view into the column header element
      me.refreshHeader(true);
      me.width = viewModel.totalSize;
      me.timeline.refresh(); // When width is set above, that ends up on a columnsResized listener, but the refreshing of the fake
      // scrollers to accommodate the new width is not done in this timeframe, so the upcoming centering related
      // to preset change cannot work. So we have to refresh the fake scrollers now

      me.subGrid.refreshFakeScroll();
    } else if (me.mode === 'vertical') {
      // Refresh to rerender cells, in the process updating the vertical timeaxis to reflect view model changes
      me.timeline.refreshRows();
    }
  } // Called on paint. SubGrid has its width so this is the earliest time to configure the TimeAxisViewModel with
  // correct width

  onTimelinePaint({
    firstPaint
  }) {
    const me = this;

    if (!me.subGrid.insertRowsBefore) {
      return;
    }

    if (firstPaint) {
      me.subGridElement.classList.add('b-timeline-subgrid'); // If the owning Scheduler has hideHeaders: true, element won't be available.

      if (me.element) {
        EventHelper.on({
          element: me.element,
          thisObj: me,
          click: 'onContainerElementClick',
          dblclick: 'onContainerElementClick',
          contextmenu: 'onContainerElementClick'
        });
      }

      if (me.mode === 'vertical') {
        var _me$grid;

        me.refreshHeader(); // The above operation can cause height change.

        (_me$grid = me.grid) === null || _me$grid === void 0 ? void 0 : _me$grid.onHeightChange();
      }
    }
  } //endregion
  //region Rendering

  /**
   * Refreshes the columns header contents (which is either a HorizontalTimeAxis or a ResourceHeader). Useful if you
   * have rendered some extra meta data that depends on external data such as the EventStore or ResourceStore.
   */

  refreshHeader(internal) {
    const me = this,
          {
      element
    } = me;

    if (element) {
      if (me.mode === 'horizontal') {
        // Force timeAxisViewModel to regenerate its column config, which calls header renderers etc.
        !internal && me.timeAxisViewModel.update(null, true);

        if (!me.timeAxisView.rendered) {
          // Do not need the normal header markup
          element.innerHTML = '';
          me.timeAxisView.render(element);
        } else {
          // Force rebuild of cells in case external data has changed (cheap since it still syncs to DOM)
          me.timeAxisView.refresh(true);
        }
      } else if (me.mode === 'vertical') {
        if (!me.resourceColumns.currentElement) {
          // Do not need the normal header markup
          element.innerHTML = '';
          me.resourceColumns.render(element);
        } // Vertical's resourceColumns is redrawn with the events, no need here

      }
    }
  }

  renderer(renderData) {
    // No drawing of events before engines initial commit
    if (this.timeline.project.isInitialCommitPerformed) {
      return this.timeline.currentOrientation.renderer(renderData);
    }
  } //endregion
  // region DOM events

  onContainerElementClick(event) {
    const target = DomHelper.up(event.target, '.b-sch-header-timeaxis-cell');

    if (target) {
      const index = target.dataset.tickIndex,
            position = target.parentElement.dataset.headerPosition,
            columnConfig = this.timeAxisViewModel.columnConfig[position][index]; // Skip same events with Grid context menu triggerEvent

      const contextMenu = this.grid.features.contextMenu;

      if (!contextMenu || event.type !== contextMenu.triggerEvent) {
        this.trigger('timeAxisHeader' + StringHelper.capitalize(event.type), {
          startDate: columnConfig.start,
          endDate: columnConfig.end,
          event
        });
      }
    }
  } // endregion

  get timeAxisViewModel() {
    return this._timeAxisViewModel;
  }

  set timeAxisViewModel(timeAxisViewModel) {
    this.detachListeners('tavm');
    timeAxisViewModel === null || timeAxisViewModel === void 0 ? void 0 : timeAxisViewModel.on({
      name: 'tavm',
      update: 'onViewModelUpdate',
      thisObj: this
    });
    this._timeAxisViewModel = timeAxisViewModel;
  }

}
ColumnStore.registerColumnType(TimeAxisColumn);
TimeAxisColumn._$name = 'TimeAxisColumn';

/**
 * @module Scheduler/view/VerticalTimeAxis
 */

/**
 * Widget that renders a vertical time axis. Only renders ticks in view. Used in vertical mode.
 * @extends Core/widget/Widget
 * @private
 */

class VerticalTimeAxis extends TimeAxisBase {
  static get $name() {
    return 'VerticalTimeAxis';
  }

  static get configurable() {
    return {
      cls: 'b-verticaltimeaxis',
      sizeProperty: 'height',
      positionProperty: 'top',
      wrapText: true
    };
  } // All cells overlayed in the same space.
  // For future use.

  buildHorizontalCells() {
    const me = this,
          {
      client
    } = me,
          stickyHeaders = client === null || client === void 0 ? void 0 : client.stickyHeaders,
          featureHeaderConfigs = [],
          cellConfigs = me.levels.reduce((result, level, i) => {
      if (level.cells) {
        var _level$cells;

        result.push(...((_level$cells = level.cells) === null || _level$cells === void 0 ? void 0 : _level$cells.filter(cell => cell.start < me.endDate && cell.end > me.startDate).map((cell, j, cells) => ({
          className: {
            'b-sch-header-timeaxis-cell': 1,
            [cell.headerCellCls]: cell.headerCellCls,
            [`b-align-${cell.align}`]: cell.align,
            'b-last': j === cells.length - 1,
            'b-lowest': i === me.levels.length - 1
          },
          dataset: {
            tickIndex: cell.index,
            cellId: `${i}-${cell.index}`,
            headerPosition: i,
            // Used in export tests to resolve dates from tick elements
            ...(window.DEBUG && {
              date: cell.start.getTime()
            })
          },
          style: {
            // DomHelper appends px to numeric dimensions
            top: cell.coord,
            height: cell.width,
            minHeight: cell.width
          },
          children: [{
            className: {
              'b-sch-header-text': 1,
              'b-sticky-header': stickyHeaders
            },
            html: cell.value
          }]
        }))));
      }

      return result;
    }, []); // When tested in isolation there is no client

    client === null || client === void 0 ? void 0 : client.getHeaderDomConfigs(featureHeaderConfigs);
    cellConfigs.push(...featureHeaderConfigs); // noinspection JSSuspiciousNameCombination

    return {
      className: me.widgetClassList,
      dataset: {
        headerFeature: `headerRow0`,
        headerPosition: 0
      },
      syncOptions: {
        // Keep a maximum of 5 released cells. Might be fine with fewer since ticks are fixed width.
        // Prevents an unnecessary amount of cells from sticking around when switching from narrow to
        // wide tickSizes
        releaseThreshold: 5,
        syncIdField: 'cellId'
      },
      children: cellConfigs
    };
  }

  get height() {
    return this.size;
  }

}
VerticalTimeAxis._$name = 'VerticalTimeAxis';

/**
 * @module Scheduler/column/VerticalTimeAxisColumn
 */

/**
 * A column containing the time axis labels for vertical mode. Normally you do not need to interact with or
 * create this column, it is handled by Scheduler/Gantt.
 *
 * @extends Grid/column/Column
 */

class VerticalTimeAxisColumn extends Column {
  static get type() {
    return 'verticalTimeAxis';
  }

  static get defaults() {
    return {
      /**
       * Set to false to prevent this column header from being dragged.
       * @config {Boolean} draggable
       * @category Interaction
       * @default false
       * @hide
       */
      draggable: false,

      /**
       * Set to false to prevent grouping by this column.
       * @config {Boolean} groupable
       * @category Interaction
       * @default false
       * @hide
       */
      groupable: false,

      /**
       * Allow column visibility to be toggled through UI.
       * @config {Boolean} hideable
       * @default false
       * @category Interaction
       * @hide
       */
      hideable: false,

      /**
       * Show column picker for the column.
       * @config {Boolean} showColumnPicker
       * @default false
       * @category Menu
       * @hide
       */
      showColumnPicker: false,

      /**
       * Allow filtering data in the column (if Filter feature is enabled)
       * @config {Boolean} filterable
       * @default false
       * @category Interaction
       * @hide
       */
      filterable: false,

      /**
       * Allow sorting of data in the column
       * @config {Boolean} sortable
       * @category Interaction
       * @default false
       * @hide
       */
      sortable: false,
      // /**
      //  * Set to `false` to prevent the column from being drag-resized when the ColumnResize plugin is enabled.
      //  * @config {Boolean} resizable
      //  * @default false
      //  * @category Interaction
      //  * @hide
      //  */
      // resizable : false,

      /**
       * Allow searching in the column (respected by QuickFind and Search features)
       * @config {Boolean} searchable
       * @default false
       * @category Interaction
       * @hide
       */
      searchable: false,

      /**
       * Specifies if this column should be editable, and define which editor to use for editing cells in the column (if CellEdit feature is enabled)
       * @config {String} editor
       * @default false
       * @category Interaction
       * @hide
       */
      editor: false,

      /**
       * Set to `true` to show a context menu on the cell elements in this column
       * @config {Boolean} enableCellContextMenu
       * @default false
       * @category Menu
       * @hide
       */
      enableCellContextMenu: false,

      /**
       * @config {Function} tooltipRenderer
       * @hide
       */
      tooltipRenderer: false,
      cellCls: 'b-verticaltimeaxiscolumn',
      flex: 1
    };
  }

  get isFocusable() {
    return false;
  }

  construct(data) {
    const me = this;
    super.construct(...arguments);
    me.timeAxisViewModel = data.timeline.timeAxisViewModel;
    me.view = new VerticalTimeAxis({
      model: me.timeAxisViewModel,
      client: data.timeline
    });
  }

  renderer({
    cellElement,
    size
  }) {
    this.view.render(cellElement);
    size.height = this.view.height;
  }

}
ColumnStore.registerColumnType(VerticalTimeAxisColumn);
VerticalTimeAxisColumn._$name = 'VerticalTimeAxisColumn';

/**
 * @module Scheduler/view/SchedulerBase
 */

/**
 * A thin base class for {@link Scheduler.view.Scheduler}. Does not include any features by default, allowing smaller
 * custom built bundles if used in place of {@link Scheduler.view.Scheduler}.
 *
 * **NOTE:** In most scenarios you do probably want to use Scheduler instead of SchedulerBase.
 *
 * @mixes Scheduler/view/mixin/EventNavigation
 * @mixes Scheduler/view/mixin/EventSelection
 * @mixes Scheduler/view/mixin/SchedulerDom
 * @mixes Scheduler/view/mixin/SchedulerDomEvents
 * @mixes Scheduler/view/mixin/SchedulerEventRendering
 * @mixes Scheduler/view/mixin/SchedulerRegions
 * @mixes Scheduler/view/mixin/SchedulerScroll
 * @mixes Scheduler/view/mixin/SchedulerState
 * @mixes Scheduler/view/mixin/SchedulerStores
 * @mixes Scheduler/view/mixin/TimelineDateMapper
 * @mixes Scheduler/view/mixin/TimelineDomEvents
 * @mixes Scheduler/view/mixin/TimelineEventRendering
 * @mixes Scheduler/view/mixin/TimelineScroll
 * @mixes Scheduler/view/mixin/TimelineViewPresets
 * @mixes Scheduler/view/mixin/TimelineZoomable
 * @mixes Scheduler/crud/mixin/CrudManagerView
 * @mixes Scheduler/data/mixin/ProjectConsumer
 *
 * @extends Scheduler/view/TimelineBase
 */
// SchedulerDragResize not included as @mixes above on purpose, since it is private

class SchedulerBase extends TimelineBase.mixin(CrudManagerView, SchedulerDom, SchedulerDomEvents, SchedulerDragResize, SchedulerStores, SchedulerScroll, SchedulerState, SchedulerEventRendering, SchedulerRegions, EventSelection, EventNavigation) {
  //region Config
  static get $name() {
    return 'SchedulerBase';
  } // Factoryable type name

  static get type() {
    return 'schedulerbase';
  }

  static get defaultConfig() {
    return {
      /**
       * Scheduler mode. Supported values: horizontal, vertical
       * @config {String} mode
       * @default
       */
      mode: 'horizontal',

      /**
       * CSS class to add to rendered events
       * @config {String}
       * @category CSS
       * @private
       * @default
       */
      eventCls: 'b-sch-event',

      /**
       * CSS class to add to cells in the timeaxis column
       * @config {String}
       * @category CSS
       * @private
       * @default
       */
      timeCellCls: 'b-sch-timeaxis-cell',
      timeCellSelector: '.b-sch-timeaxis-cell',
      scheduledEventName: 'event',

      /**
       * A CSS class to apply to each event in the view on mouseover (defaults to 'b-sch-event-hover').
       * @config {String}
       * @default
       * @category CSS
       * @private
       */
      overScheduledEventClass: 'b-sch-event-hover',

      /**
       * Set to false if you don't want to allow events overlapping times for any one resource (defaults to true).
       * @config {Boolean}
       * @default
       * @category Scheduled events
       */
      allowOverlap: true,

      /**
       * The height in pixels of Scheduler rows.
       * @config {Number}
       * @default
       */
      rowHeight: 60,

      /**
       * Scheduler overrides Grids default implementation of {@link Grid.view.GridBase#config-getRowHeight} to
       * pre-calculate row heights based on events in the rows.
       *
       * The amount of rows that are pre-calculated is limited for performance reasons. The limit is configurable
       * by specifying the {@link Scheduler.view.SchedulerBase#config-preCalculateHeightLimit} config.
       *
       * The results of the calculation are cached internally.
       *
       * @config {Function} getRowHeight
       * @param {Scheduler.model.ResourceModel} getRowHeight.record Resource record to determine row height for
       * @returns {Number} Desired row height
       * @category Layout
       */

      /**
       * Maximum number of resources for which height is pre-calculated. If you have many events per
       * resource you might want to lower this number to gain some initial rendering performance.
       *
       * Specify a falsy value to opt out of row height pre-calculation.
       *
       * @config {Number}
       * @default
       * @category Layout
       */
      preCalculateHeightLimit: 10000,
      crudManagerClass: CrudManager,
      testConfig: {
        loadMaskError: {
          autoClose: 10,
          showDelay: 0
        }
      }
    };
  }

  static get deprecatedEvents() {
    return {
      eventContextMenuBeforeShow: {
        product: 'Scheduler',
        invalidAsOfVersion: '5.0.0',
        message: '`eventContextMenuBeforeShow` event is deprecated, in favor of `eventMenuBeforeShow` event. Please see https://bryntum.com/docs/scheduler/#guides/upgrades/3.1.0.md for more information.'
      },
      eventContextMenuShow: {
        product: 'Scheduler',
        invalidAsOfVersion: '5.0.0',
        message: '`eventContextMenuShow` event is deprecated, in favor of `eventMenuShow` event. Please see https://bryntum.com/docs/scheduler/#guides/upgrades/3.1.0.md for more information.'
      },
      eventContextMenuItem: {
        product: 'Scheduler',
        invalidAsOfVersion: '5.0.0',
        message: '`eventContextMenuItem` event is deprecated, in favor of `eventMenuItem` event. Please see https://bryntum.com/docs/scheduler/#guides/upgrades/3.1.0.md for more information.'
      },
      scheduleContextMenuBeforeShow: {
        product: 'Scheduler',
        invalidAsOfVersion: '5.0.0',
        message: '`scheduleContextMenuBeforeShow` event is deprecated, in favor of `scheduleMenuBeforeShow` event. Please see https://bryntum.com/docs/scheduler/#guides/upgrades/3.1.0.md for more information.'
      },
      scheduleContextMenuShow: {
        product: 'Scheduler',
        invalidAsOfVersion: '5.0.0',
        message: '`scheduleContextMenuShow` event is deprecated, in favor of `scheduleMenuShow` event. Please see https://bryntum.com/docs/scheduler/#guides/upgrades/3.1.0.md for more information.'
      },
      scheduleContextMenuItem: {
        product: 'Scheduler',
        invalidAsOfVersion: '5.0.0',
        message: '`scheduleContextMenuItem` event is deprecated, in favor of `scheduleMenuItem` event. Please see https://bryntum.com/docs/scheduler/#guides/upgrades/3.1.0.md for more information.'
      }
    };
  } //endregion
  //region Events

  /**
   * Fired after rendering an event, when its element is available in DOM.
   * @event renderEvent
   * @param {Scheduler.view.Scheduler} source This Scheduler
   * @param {Scheduler.model.EventModel} eventRecord The event record
   * @param {Scheduler.model.ResourceModel} resourceRecord The resource record
   * @param {Scheduler.model.AssignmentModel} assignmentRecord The assignment record
   * @param {Object} renderData An object containing details about the event rendering, see
   *   {@link Scheduler.view.mixin.SchedulerEventRendering#config-eventRenderer} for details
   * @param {Object} tplData DEPRECATED 4.0: Use renderData instead
   * @param {Boolean} isRepaint `true` if this render is a repaint of the event, updating its existing element
   * @param {Boolean} isReusingElement `true` if this render lead to the event reusing a released events element
   * @param {HTMLElement} element The events element
   */

  /**
   * Fired after releasing an event, useful to cleanup of custom content added on `renderEvent` or in `eventRenderer`.
   * @event releaseEvent
   * @param {Scheduler.view.Scheduler} source This Scheduler
   * @param {Scheduler.model.EventModel} eventRecord The event record
   * @param {Scheduler.model.ResourceModel} resourceRecord The resource record
   * @param {Scheduler.model.AssignmentModel} assignmentRecord The assignment record
   * @param {Object} renderData An object containing details about the event rendering
   * @param {HTMLElement} element The events element
   */

  /**
   * Fired when clicking a resource header cell
   * @event resourceHeaderClick
   * @param {Scheduler.view.Scheduler} source This Scheduler
   * @param {Scheduler.model.ResourceModel} resourceRecord The resource record
   * @param {Event} event The event
   */

  /**
   * Fired when double clicking a resource header cell
   * @event resourceHeaderDblclick
   * @param {Scheduler.view.Scheduler} source This Scheduler
   * @param {Scheduler.model.ResourceModel} resourceRecord The resource record
   * @param {Event} event The event
   */

  /**
   * Fired when activating context menu on a resource header cell
   * @event resourceHeaderContextmenu
   * @param {Scheduler.view.Scheduler} source This Scheduler
   * @param {Scheduler.model.ResourceModel} resourceRecord The resource record
   * @param {Event} event The event
   */
  //endregion
  //region Functions injected by features
  // For documentation & typings purposes

  /**
   * Opens an editor UI to edit the passed event.
   *
   * *NOTE: Only available when the {@link Scheduler/feature/EventEdit EventEdit} feature is enabled.*
   *
   * @function editEvent
   * @param {Scheduler.model.EventModel} eventRecord Event to edit
   * @param {Scheduler.model.ResourceModel} [resourceRecord] The Resource record for the event.
   * This parameter is needed if the event is newly created for a resource and has not been assigned, or when using
   * multi assignment.
   * @param {HTMLElement} [element] Element to anchor editor to (defaults to events element)
   * @category Feature shortcuts
   */

  /**
   * Returns the dependency record for a DOM element
   *
   * *NOTE: Only available when the {@link Scheduler/feature/Dependencies Dependencies} feature is enabled.*
   *
   * @function resolveDependencyRecord
   * @param {HTMLElement} element The dependency line element
   * @return {Scheduler.model.DependencyModel} The dependency record
   * @category Feature shortcuts
   */
  //endregion
  //region Init

  afterConstruct() {
    super.afterConstruct();

    if (this.createEventOnDblClick) {
      this.on('scheduledblclick', this.onSchedulerDblClick);
    }
  } //endregion
  //region Config getters/setters
  // Overrides TimelineBase to supply eventStore as its store (which is only used in passed events)

  set timeAxisViewModel(timeAxisViewModel) {
    super.timeAxisViewModel = timeAxisViewModel;

    if (this.eventStore) {
      this.timeAxisViewModel.eventStore = this.eventStore;
    }
  }

  get timeAxisViewModel() {
    return super.timeAxisViewModel;
  } // Placeholder getter/setter for mixins, please make any changes needed to SchedulerStores#store instead

  get store() {
    return super.store;
  }

  set store(store) {
    super.store = store;
  } //endregion
  //region Event handlers

  onLocaleChange() {
    this.currentOrientation.onLocaleChange();
    super.onLocaleChange();
  }

  onSchedulerDblClick({
    date: startDate,
    resourceRecord,
    row
  }) {
    const me = this;

    if (me.readOnly || resourceRecord.isSpecialRow) {
      return;
    }

    me.internalAddEvent(startDate, resourceRecord, row);
  }

  onColumnsChanged({
    action,
    changes,
    record: column
  }) {
    // TODO: Have ResourceHeader call this directly instead of relying on event?
    if (column === this.timeAxisColumn && 'width' in changes) {
      this.updateCanvasSize();
    }

    super.onColumnsChanged(...arguments);
  } // Only used in vertical mode

  onVerticalScroll({
    scrollTop
  }) {
    this.currentOrientation.updateFromVerticalScroll(scrollTop);
  }
  /**
   * Called when new event is created.
   * Сan be overriden to supply default record values etc.
   * @param {Scheduler.model.EventModel} eventRecord Newly created event
   */

  onEventCreated(eventRecord) {} //endregion
  //region Mode

  /**
   * Checks if scheduler is in horizontal mode
   * @returns {Boolean}
   * @readonly
   * @category Common
   * @private
   */

  get isHorizontal() {
    return this.mode === 'horizontal';
  }
  /**
   * Checks if scheduler is in vertical mode
   * @returns {Boolean}
   * @readonly
   * @category Common
   * @private
   */

  get isVertical() {
    return this.mode === 'vertical';
  }
  /**
   * Get/set mode (horizontal/vertical)
   * @property {String}
   * @private
   * @category Common
   */

  get mode() {
    return this._mode;
  }

  set mode(mode) {
    const me = this;
    me._mode = mode;

    if (!me[mode]) {
      if (mode === 'horizontal') {
        me.horizontal = new HorizontalRendering(me);

        if (me.isPainted) {
          me.horizontal.init();
        }

        me.un('scroll', me.onVerticalScroll, me);
      } else if (mode === 'vertical') {
        // Zooming is not yet supported in vertical mode, disable it
        me.zoomOnTimeAxisDoubleClick = me.zoomOnMouseWheel = false;
        me.vertical = new VerticalRendering(me);

        if (me.rendered) {
          me.vertical.init();
        }

        me.on('scroll', me.onVerticalScroll, me);
      }
    }
  }

  get currentOrientation() {
    return this[this.mode];
  } //endregion
  //region Dom event dummies
  // this is ugly, but needed since super cannot be called from SchedulerDomEvents mixin...

  onElementKeyDown(event) {
    super.onElementKeyDown(event);
  }

  onElementKeyUp(event) {
    super.onElementKeyUp(event);
  }

  onElementMouseOver(event) {
    super.onElementMouseOver(event);
  }

  onElementMouseOut(event) {
    super.onElementMouseOut(event);
  } //endregion
  //region Feature hooks

  /**
   * Populates the event context menu. Chained in features to add menu items.
   * @param {Object} options Contains menu items and extra data retrieved from the menu target.
   * @param {Grid.column.Column} options.column Column for which the menu will be shown.
   * @param {Scheduler.model.EventModel} options.eventRecord The context event.
   * @param {Scheduler.model.ResourceModel} options.resourceRecord The context resource.
   * @param {Scheduler.model.AssignmentModel} options.assignmentRecord The context assignment if any.
   * @param {Object} options.items A named object to describe menu items.
   * @internal
   */

  populateEventMenu() {}
  /**
   * Populates the time axis context menu. Chained in features to add menu items.
   * @param {Object} options Contains menu items and extra data retrieved from the menu target.
   * @param {Grid.column.Column} options.column Column for which the menu will be shown.
   * @param {Scheduler.model.ResourceModel} options.resourceRecord The context resource.
   * @param {Date} options.date The Date corresponding to the mouse position in the time axis.
   * @param {Object} options.items A named object to describe menu items.
   * @internal
   */

  populateScheduleMenu() {} // Called when visible date range changes

  onVisibleDateRangeChange() {} // Called when visible resource range changes in vertical mode

  onVisibleResourceRangeChange() {} //endregion
  //region Scheduler specific date mapping functions

  async internalAddEvent(startDate, resourceRecord, row) {
    const me = this,
          resourceRecords = [resourceRecord],
          eventRecord = new me.eventStore.modelClass({
      startDate,
      endDate: DateHelper.add(startDate, 1, me.timeAxis.unit)
    });
    me.onEventCreated(eventRecord);

    if (me.eventEdit) {
      const eventData = me.currentOrientation.getTimeSpanRenderData(eventRecord, resourceRecord),
            proxyEl = DomHelper.createElement({
        parent: me.foregroundCanvas,
        className: 'b-sch-dragcreator-proxy',
        style: `width:${eventData.width}px;height:${eventData.height}px`
      });
      DomHelper.setTranslateXY(proxyEl, eventData.left, (row && row.top || 0) + eventData.top);
      me.editEvent(eventRecord, resourceRecord, proxyEl);
      return;
    } else if (me.taskEdit) {
      me.editEvent(eventRecord, resourceRecord);
      return;
    }
    /**
     * Fires before an event is added. Can be triggered by schedule double click, drag create action, or by the event editor.
     * @event beforeEventAdd
     * @param {Scheduler.view.Scheduler} source The Scheduler instance
     * @param {Scheduler.model.EventModel} eventRecord The record about to be added
     * @param {Scheduler.model.ResourceModel[]} resources **Deprecated 4.0** Use `resourceRecords` instead
     * @param {Scheduler.model.ResourceModel[]} resourceRecords Resources that the record is assigned to
     * @preventable
     */

    if (me.trigger('beforeEventAdd', {
      eventRecord,
      resourceRecords,
      resources: resourceRecords
    }) !== false) {
      me.eventStore.add(eventRecord);
      me.assignmentStore.assignEventToResource(eventRecord, resourceRecord);

      if (me.taskEdit) {
        await me.project.commitAsync();
        me.editEvent(eventRecord, resourceRecord);
      }
    }
  }
  /**
   * Checks if a date range is allocated or not for a given resource.
   * @param {Date} start The start date
   * @param {Date} end The end date
   * @param {Scheduler.model.EventModel|null} excludeEvent An event to exclude from the check (or null)
   * @param {Scheduler.model.ResourceModel} resource The resource
   * @return {Boolean} True if the timespan is available for the resource
   * @category Dates
   */

  isDateRangeAvailable(start, end, excludeEvent, resource) {
    return this.eventStore.isDateRangeAvailable(start, end, excludeEvent, resource);
  } //endregion

  /**
   * Suspends UI refresh on store operations.
   *
   * Multiple calls to `suspendRefresh` stack up, and will require an equal number of `resumeRefresh` calls to
   * actually resume UI refresh.
   *
   * @function suspendRefresh
   */

  /**
   * Resumes UI refresh on store operations.
   *
   * Multiple calls to `suspendRefresh` stack up, and will require an equal number of `resumeRefresh` calls to
   * actually resume UI refresh.
   *
   * Specify `true` as the first param to trigger a refresh if this call unblocked the refresh suspension.
   * If the underlying project is calculating changes, the refresh will be postponed until it is done.
   *
   * @param {Boolean} trigger `true` to trigger a refresh, if this resume unblocks the suspend
   * @returns {Promise}
   */

  async resumeRefresh(trigger) {
    super.resumeRefresh(false);

    if (!this.refreshSuspended && trigger) {
      // Do not refresh until project is in a valid state
      if (!this.isEngineReady) {
        // Refresh will happen because of the commit, bail out of this one after forcing rendering to consider
        // next one a full refresh
        this.currentOrientation.refreshAllWhenReady = true;
        return this.project.commitAsync();
      } // View could've been destroyed while we waited for engine

      if (!this.isDestroyed) {
        // If it already is, refresh now
        this.refreshWithTransition();
      }
    }
  } //region Appearance
  // Overrides grid to take crudManager loading into account

  toggleEmptyText() {
    const me = this;

    if (me.bodyContainer) {
      DomHelper.toggleClasses(me.bodyContainer, 'b-grid-empty', !(me.rowManager.rowCount || me.crudManager && me.crudManager.isLoading));
    }
  } // Overrides Grids base implementation to return a correctly calculated height for the row. Also stores it in
  // RowManagers height map, which is used to calculate total height etc.

  getRowHeight(resourceRecord) {
    if (this.currentOrientation.calculateRowHeight) {
      const height = this.currentOrientation.calculateRowHeight(resourceRecord);
      this.rowManager.storeKnownHeight(resourceRecord.id, height);
      return height;
    }
  } // Calculates the height for specified rows. Call when changes potentially makes its height invalid

  calculateRowHeights(resourceRecords, silent = false) {
    // Array allowed to have nulls in it for easier code when calling this fn
    resourceRecords.forEach(resourceRecord => resourceRecord && this.getRowHeight(resourceRecord));

    if (!silent) {
      this.rowManager.estimateTotalHeight(true);
    }
  } // Calculate heights for all rows (up to the preCalculateHeightLimit)

  calculateAllRowHeights(silent = false) {
    const {
      store,
      rowManager
    } = this,
          count = Math.min(store.count, this.preCalculateHeightLimit); // Allow opt out by specifying falsy value.

    if (count) {
      rowManager.clearKnownHeights();

      for (let i = 0; i < count; i++) {
        // This will both calculate and store the height
        this.getRowHeight(store.getAt(i));
      } // Make sure height is reflected on scroller etc.

      if (!silent) {
        rowManager.estimateTotalHeight(true);
      }
    }
  } //endregion

} // Register this widget type with its Factory

SchedulerBase.initClass(); // Scheduler version is specified in TimelineBase because Gantt extends it

SchedulerBase._$name = 'SchedulerBase';

const refreshActions = {
  add: 1,
  remove: 1,
  update: 1,
  removeAll: 1,
  filter: 1
};
/**
 * @module Scheduler/feature/GroupSummary
 */
// noinspection JSClosureCompilerSyntax

/**
 * A special version of the Grid GroupSummary feature that enables summaries within scheduler. To use a single summary
 * it is easiest to configure {@link #config-renderer}, for multiple summaries see {@link #config-summaries}.
 *
 * This feature is <strong>disabled</strong> by default. It is **not** supported in vertical mode.
 *
 * @extends Grid/feature/GroupSummary test
 *
 * @classtype groupSummary
 * @externalexample scheduler/GroupSummary.js
 * @demo Scheduler/groupsummary
 * @typings Grid/feature/GroupSummary -> Grid/feature/GridGroupSummary
 */

class GroupSummary$1 extends GroupSummary {
  //region Config
  static get $name() {
    return 'GroupSummary';
  }

  static get defaultConfig() {
    return {
      /**
       * Show tooltip containing summary values and labels
       * @config {Boolean}
       * @default
       */
      showTooltip: true,

      /**
       * Array of summary configs which consists of a label and a {@link #config-renderer} function
       * ```
       *  summaries : [
       *      {
       *         label : 'Label',
       *         renderer : ({ startDate, endDate, eventStore, resourceStore, events, resources, groupRecord, groupField, groupValue }) => {
       *             // return display value
       *             returns '<div>Renderer output</div>';
       *         }
       *      }
       *  ]
       *  ```
       * @config {Object[]}
       */
      summaries: null,

      /**
       * Renderer function for a single time axis tick in a group summary row.
       * Should calculate a sum and return HTML as a result.
       *
       * ```javascript
       * new Scheduler({
       *     features : {
       *         groupSummary : {
       *             renderer : ({ startDate, endDate, eventStore, resourceStore, events, resources, groupRecord, groupField, groupValue }) => {
       *                 // return display value
       *                 returns '<div>Renderer output</div>';
       *             }
       *         }
       *     }
       * });
       * ```
       *
       * @param {Date} startDate Tick start date
       * @param {Date} endDate Tick end date
       * @param {Scheduler.model.EventModel[]} events Events which belong to the group
       * @param {Scheduler.model.ResourceModel[]} resources Resources which belong to the group
       * @param {Scheduler.data.EventStore} eventStore Event store
       * @param {Scheduler.data.ResourceStore} resourceStore Resource store
       * @param {Core.data.Model} groupRecord Current groups header row record
       * @param {String} groupField Current groups field name
       * @param {String} groupValue Current groups value
       * @returns {String} Html content
       * @config {Function}
       */
      renderer: null
    };
  }

  static get pluginConfig() {
    return {
      chain: ['render']
    };
  } //endregion
  //region Init

  construct(scheduler, config) {
    const me = this;

    if (scheduler.isVertical) {
      throw new Error('GroupSummary feature is not supported in vertical mode');
    }

    me.scheduler = scheduler;
    super.construct(scheduler, config);

    if (!me.summaries && me.renderer) {
      me.summaries = [{
        renderer: me.renderer
      }];
    }

    me.isScheduler = scheduler instanceof SchedulerBase;

    if (me.isScheduler) {
      scheduler.eventStore.on({
        change: me.onEventStoreChange,
        thisObj: me
      });
      scheduler.timeAxisViewModel.on({
        update: me.onTimeAxisChange,
        thisObj: me
      });
    }
  }

  doDestroy() {
    if (this._tip) {
      this._tip.destroy();
    }

    super.doDestroy();
  } //endregion
  //region Events

  onTimeAxisChange() {
    this.rerenderGroupSummaries();
  }

  onEventStoreChange({
    action
  }) {
    // Scheduler does minimal update on event changes, it will not rerender the summary rows.
    // Need to handle that here
    if (refreshActions[action]) {
      this.rerenderGroupSummaries();
    }
  }

  rerenderGroupSummaries() {
    // TODO: Sort out the affected rows by checking events resources
    this.scheduler.rowManager.rows.forEach(row => {
      if (row.isGroupFooter) {
        row.render();
      }
    });
  } //endregion
  //region Render

  /**
   * Called before rendering row contents, used to reset rows no longer used as group summary rows
   * @private
   */

  onBeforeRenderRow({
    row,
    record
  }) {
    if (row.isGroupFooter && !record.meta.hasOwnProperty('groupFooterFor')) {
      const timeaxisCell = row.elements.normal.querySelector('.b-sch-timeaxis-cell'); // remove summary cells if exist

      if (timeaxisCell) {
        timeaxisCell.innerHTML = '';
      }
    }

    super.onBeforeRenderRow(...arguments);
  }
  /**
   * Called by parent class to fill timeaxis with summary contents. Generates tick "cells" and populates them with
   * summaries.
   * ```
   * <div class="b-timeaxis-group-summary">
   *     <div class="b-timeaxis-tick">
   *         <div class="b-timeaxix-summary-value">x</div>
   *         ...
   *     </div>
   *     ...
   * </div>
   * ```
   * @private
   */

  generateHtml(column, records, cls, groupRecord, groupField, groupValue) {
    // TODO: Use DOM config object instead of strings
    if (column.type === 'timeAxis') {
      const me = this,
            scheduler = me.scheduler,
            colCfg = scheduler.timeAxisViewModel.columnConfig;
      let html = '';

      if (scheduler.isEngineReady) {
        scheduler.timeAxis.forEach((tick, idx) => {
          const // events for current tick
          events = scheduler.eventStore.getEvents({
            startDate: tick.startDate,
            endDate: tick.endDate,
            allowPartial: true,
            onlyAssigned: true
          }),
                // filter those events to current groups
          groupEvents = events.filter(event => event.resources.some(resource => records.includes(resource))); // TODO: could turn this into a template

          const sumHtml = me.summaries.map(config => {
            // summary renderer used to calculate and format value
            const value = config.renderer({
              startDate: tick.startDate,
              endDate: tick.endDate,
              eventStore: scheduler.eventStore,
              resourceStore: scheduler.resourceStore,
              events: groupEvents,
              resources: records,
              groupRecord,
              groupField,
              groupValue
            });
            return `<div class="b-timeaxis-summary-value">${value}</div>`;
          }).join(''); // get width on column index from the last header config

          html += `<div class="b-timeaxis-tick" style="width: ${colCfg[colCfg.length - 1][idx].width}px">${sumHtml}</div>`;
        });
      }

      return `<div class="b-timeaxis-group-summary">${html}</div>`;
    }

    return super.generateHtml(column, records, cls, groupRecord, groupField, groupValue);
  }
  /**
   * Overrides parents function to return correct summary count, used when sizing row
   * @private
   */

  updateSummaryHtml(cellElement, column, records) {
    const count = super.updateSummaryHtml(cellElement, column, records);

    if (column.type === 'timeAxis') {
      const result = {
        count: 0,
        height: 0
      };
      this.summaries.forEach(config => {
        if (config.height) {
          result.height += config.height;
        } else {
          result.count++;
        }
      });
      return result;
    }

    return count;
  }
  /**
   * Generates tooltip contents for hovered summary tick
   * @private
   */

  getTipHtml({
    activeTarget
  }) {
    const me = this,
          index = Array.from(activeTarget.parentElement.children).indexOf(activeTarget),
          tick = me.scheduler.timeAxis.getAt(index);
    let tipHtml = `<header>${me.L('L{Summary.Summary for}', me.scheduler.getFormattedDate(tick.startDate))}</header>`,
        showTip = false;
    DomHelper.forEachSelector(activeTarget, '.b-timeaxis-summary-value', (element, i) => {
      const label = me._labels[i],
            text = element.innerText.trim();
      tipHtml += `<label>${label || ''}</label><div class="b-timeaxis-summary-value">${text}</div>`;
      if (element.innerHTML) showTip = true;
    });
    return showTip ? tipHtml : null;
  }
  /**
   * Initialize tooltip on render
   * @private
   */

  render() {
    const me = this,
          {
      scheduler
    } = me;

    if (me.isScheduler) {
      // if any sum config has a label, init tooltip
      if (me.summaries && me.summaries.some(config => config.label) && me.showTooltip && !me._tip) {
        me._labels = me.summaries.map(config => config.label || '');
        me._tip = new Tooltip({
          id: `${scheduler.id}-groupsummary-tip`,
          cls: 'b-timeaxis-summary-tip',
          hoverDelay: 0,
          hideDelay: 0,
          forElement: scheduler.timeAxisSubGridElement,
          anchorToTarget: true,
          forSelector: '.b-timeaxis-group-summary .b-timeaxis-tick',
          clippedBy: [scheduler.timeAxisSubGridElement, scheduler.bodyContainer],
          getHtml: me.getTipHtml.bind(me)
        });
      }
    }
  } //endregion

} // Override Grids GroupSummary with this improved version

GroupSummary$1._$name = 'GroupSummary';
GridFeatureManager.registerFeature(GroupSummary$1, false, 'Scheduler');

/**
 * @module Scheduler/feature/HeaderContextMenu
 */

/**
 * **DEPRECATED: This class is deprecated since v4.0.0,
 * use {@link Scheduler.feature.TimeAxisHeaderMenu TimeAxisHeaderMenu} feature instead.**
 *
 * Adds scheduler specific menu items to the timeline header context menu.
 *
 * This feature is **off** by default. For info on enabling it, see {@link Grid.view.mixin.GridFeatures}.
 *
 * @extends Core/mixin/InstancePlugin
 * @classtype headerContextMenu
 * @deprecated 4.0.0
 * @classtype headerContextMenu
 */

class HeaderContextMenu extends InstancePlugin {
  static get $name() {
    return 'HeaderContextMenu';
  }

  static get defaultConfig() {
    return {
      /**
       * An array of additional items to add to the menu
       * @config {Object[]}
       * @default
       */
      extraItems: null,

      /**
       * A function called before displaying the menu that allows manipulations of its items. Called with a
       * single parameter with format { eventRecord, resourceRecord, eventElement, items }.
       *
       * ```javascript
       * features : {
       *     headerContextMenu : {
       *         processItems({ items }) {
       *             // Add or remove items here as needed
       *             items.push({ text: 'Some action', icon : 'b-fa b-fa-fw b-fa-ban' })
       *         }
       *     }
       * }
       * ```
       *
       * @config {Function}
       */
      processItems: null
    };
  } // Plugin configuration. This plugin chains some of the functions in Grid.

  static get pluginConfig() {
    return {
      chain: ['populateHeaderMenu']
    };
  }

  construct(scheduler, config) {
    super.construct(scheduler, config);
    this.scheduler = scheduler;
    VersionHelper.deprecate('Scheduler', '5.0.0', '`HeaderContextMenu` feature is deprecated, in favor of `TimeAxisHeaderMenu` feature. Please see https://bryntum.com/docs/scheduler/#guides/upgrades/3.1.0.md for more information.'); // If this feature is enabled, need to disable TimeAxisHeaderMenu. They are mutually exclusive.

    if (scheduler.features.timeAxisHeaderMenu) {
      scheduler.features.timeAxisHeaderMenu.disabled = true;
    } // By default this feature works based on ContextMenu feature.
    // In case new HeaderMenu feature is used, need to make sure it shows menu for timeAxis too

    if (scheduler.features.headerMenu) {
      scheduler.features.headerMenu._showForTimeAxis = true;
    }
  }
  /**
   * Populates the TimeAxis header context menu items.
   * @param {Object} options Contains menu items and extra data retrieved from the menu target.
   * @param {Grid.column.Column} options.column Column for which the menu will be shown
   * @param {Object} options.items A named object to describe menu items
   * @internal
   */

  populateHeaderMenu({
    column,
    items,
    menu
  }) {
    const me = this;

    if (column.type !== 'timeAxis') {
      return;
    }

    if (menu) {
      // the TimeAxis's context menu probably will cause scrolls because it manipulates the dates.
      // The menu should not hide on scroll when for a TimeAxisColumn
      menu.scrollAction = 'realign';
    }

    ObjectHelper.merge(items, me.defaultItems);
    const arrayOfItems = ObjectHelper.transformNamedObjectToArray(items);

    if (me.extraItems) {
      arrayOfItems.push(...me.extraItems);
    }

    if (me.processItems) {
      me.processItems({
        items: arrayOfItems
      });
    }

    const namedItems = ObjectHelper.transformArrayToNamedObject(arrayOfItems); // replace items in original object with the new items

    ObjectHelper.removeAllProperties(items);
    ObjectHelper.merge(items, namedItems);
  }

  get defaultItems() {
    const me = this,
          {
      scheduler
    } = me,
          {
      timeAxis
    } = scheduler,
          dateStep = {
      magnitude: timeAxis.shiftIncrement,
      unit: timeAxis.shiftUnit
    },
          items = {};
    items.pickZoomLevel = {
      text: 'L{pickZoomLevel}',
      localeClass: this,
      icon: 'b-fw-icon b-icon-search-plus',
      disabled: !scheduler.presets.count || me.disabled,
      menu: {
        type: 'popup',
        items: [{
          type: 'slider',
          showValue: false,
          listeners: {
            input: me.onZoomSliderChange,
            thisObj: me
          },
          // set width for IE11
          minWidth: 130
        }],

        onBeforeShow({
          source: menu
        }) {
          const [zoom] = menu.items;
          zoom.min = scheduler.minZoomLevel;
          zoom.max = scheduler.maxZoomLevel;
          zoom.value = scheduler.zoomLevel;
        }

      }
    };
    me.startDateField = new DateField({
      label: me.L('L{startText}'),
      labelWidth: '6em',
      required: true,
      step: dateStep,
      listeners: {
        change: me.onRangeDateFieldChange,
        thisObj: me
      }
    });
    me.endDateField = new DateField({
      label: me.L('L{endText}'),
      labelWidth: '6em',
      required: true,
      step: dateStep,
      listeners: {
        change: me.onRangeDateFieldChange,
        thisObj: me
      }
    });
    items.activeDateRange = {
      text: 'L{activeDateRange}',
      localeClass: this,
      icon: 'b-fw-icon b-icon-calendar',
      disabled: me.disabled,
      menu: {
        type: 'popup',
        width: '20em',
        items: [me.startDateField, me.endDateField, {
          type: 'button',
          cls: 'b-left-nav-btn',
          icon: 'b-icon b-icon-prev',
          color: 'b-blue b-raised',
          flex: 1,
          margin: 0,
          listeners: {
            click: me.onLeftShiftBtnClick,
            thisObj: me
          }
        }, {
          type: 'button',
          cls: 'b-today-nav-btn',
          color: 'b-blue b-raised',
          text: 'L{todayText}',
          localeClass: this,
          flex: 4,
          margin: '0 8',
          listeners: {
            click: me.onTodayBtnClick,
            thisObj: me
          }
        }, {
          type: 'button',
          cls: 'b-right-nav-btn',
          icon: 'b-icon b-icon-next',
          color: 'b-blue b-raised',
          flex: 1,
          listeners: {
            click: me.onRightShiftBtnClick,
            thisObj: me
          }
        }],
        onBeforeShow: () => me.initDates()
      }
    };
    return items;
  }

  onZoomSliderChange({
    value
  }) {
    this.scheduler.zoomLevel = value;
  }

  initDates() {
    const me = this;
    me.startDateField.suspendEvents();
    me.endDateField.suspendEvents(); // The actual scheduler start dates may include time, but our Date field cannot currently handle
    // a time portion and throws it away, so when we need the value from an unchanged field, we need
    // to use the initialValue set from the timeAxis values.
    // Until our DateField can optionally include a time value, this is the solution.

    me.startDateField.value = me.startDateFieldInitialValue = me.scheduler.startDate;
    me.endDateField.value = me.endDateFieldInitialValue = me.scheduler.endDate;
    me.startDateField.resumeEvents();
    me.endDateField.resumeEvents();
  }

  onRangeDateFieldChange({
    source
  }) {
    const me = this,
          startDateChanged = source === me.startDateField,
          {
      client
    } = me,
          {
      timeAxis
    } = client,
          startDate = me.startDateFieldInitialValue && !startDateChanged ? me.startDateFieldInitialValue : me.startDateField.value;
    let endDate = me.endDateFieldInitialValue && startDateChanged ? me.endDateFieldInitialValue : me.endDateField.value; // When either of the fields is changed, we no longer use its initialValue from the timeAxis start or end
    // so that gets nulled to indicate that it's unavailable and the real field value is to be used.

    if (startDateChanged) {
      me.startDateFieldInitialValue = null;
    } else {
      me.endDateFieldInitialValue = null;
    } // Because the start and end dates are exclusive, avoid a zero
    // length time axis by incrementing the end by one tick unit
    // if they are the same.

    if (!(endDate - startDate)) {
      endDate = DateHelper.add(endDate, timeAxis.shiftIncrement, timeAxis.shiftUnit);
    } // if start date got bigger than end date set end date to start date plus one tick
    else if (endDate < startDate) {
        endDate = DateHelper.add(startDate, timeAxis.shiftIncrement, timeAxis.shiftUnit);
      }

    client.preserveViewCenter(() => client.timeAxis.setTimeSpan(startDate, endDate));
    me.initDates();
  }

  onLeftShiftBtnClick() {
    const me = this;
    me.scheduler.timeAxis.shiftPrevious();
    me.initDates();
  }

  onTodayBtnClick() {
    const me = this,
          today = DateHelper.clearTime(new Date());
    me.scheduler.timeAxis.setTimeSpan(today, DateHelper.add(today, 1, 'day'));
    me.initDates();
  }

  onRightShiftBtnClick() {
    const me = this;
    me.scheduler.timeAxis.shiftNext();
    me.initDates();
  }

}
HeaderContextMenu.featureClass = '';
HeaderContextMenu._$name = 'HeaderContextMenu';
GridFeatureManager.registerFeature(HeaderContextMenu);

/**
 * @module Scheduler/feature/HeaderZoom
 */

/**
 * Enables users to click and drag to zoom to a date range in Scheduler's header time axis.
 *
 * This feature is **off** by default. For info on enabling it, see {@link Grid.view.mixin.GridFeatures}.
 *
 * @extends Core/mixin/InstancePlugin
 *
 * @example
 * let scheduler = new Scheduler({
 *   features : {
 *     headerZoom : true
 *   }
 * });
 *
 * @classtype headerZoom
 * @externalexample scheduler/HeaderZoom.js
 */

class HeaderZoom extends InstancePlugin {
  static get $name() {
    return 'HeaderZoom';
  } // Plugin configuration. This plugin chains some of the functions in Scheduler.

  static get pluginConfig() {
    return {
      chain: ['onElementMouseDown', 'onElementMouseMove', 'onElementMouseUp']
    };
  }

  onElementMouseDown(event) {
    const me = this,
          scheduler = me.client; // only react to mouse input, and left button

    if (event.touches || event.button !== 0 || me.disabled) {
      return;
    } // only react to mousedown directly on timeaxis cell

    if (event.target.closest('.b-sch-header-timeaxis-cell')) {
      const headerEl = scheduler.subGrids.normal.header.headersElement;
      me.startX = event.clientX;
      me.element = DomHelper.createElement({
        parent: headerEl,
        tag: 'div',
        className: 'b-headerzoom-rect'
      });
      me.headerElementRect = Rectangle.from(headerEl);
    }
  }

  onElementMouseMove(event) {
    const me = this; // Synthetic mousemove event has no button for IE11
    // Detect if a mouseup happened outside our element (or browser window for that matter). Note 'buttons' is not supported by Safari

    if (event.buttons === 0 && !BrowserHelper.isSafari && (!BrowserHelper.isIE11 || event.isTrusted)) {
      me.onElementMouseUp(event);
      return;
    }

    if (typeof me.startX === 'number') {
      const x = Math.max(event.clientX, me.headerElementRect.left),
            left = Math.min(me.startX, x),
            width = Math.abs(me.startX - x),
            rect = new Rectangle(left - me.headerElementRect.x + me.client.scrollLeft, 0, width, me.headerElementRect.height);
      DomHelper.setTranslateX(me.element, rect.left);
      me.element.style.width = rect.width + 'px';
    }
  }

  onElementMouseUp(event) {
    const me = this;

    if (typeof me.startX === 'number') {
      const timeline = me.client,
            rect = Rectangle.from(me.element),
            startDate = timeline.getDateFromCoordinate(rect.left, 'round', false),
            endDate = timeline.getDateFromCoordinate(rect.right, 'round', false);
      me.element && me.element.remove();
      me.startX = null;
      me.client.zoomToSpan({
        startDate,
        endDate
      });
    }
  }

}
HeaderZoom._$name = 'HeaderZoom';
GridFeatureManager.registerFeature(HeaderZoom, false, 'Scheduler');

/**
 * @module Scheduler/feature/Labels
 */

const sides = ['top', 'left', 'right', 'bottom'],
      editorAlign = {
  top: 'b-b',
  right: 'l-l',
  bottom: 't-t',
  left: 'r-r'
},
      topBottom = {
  top: 1,
  bottom: 1
};
/**
 * Displays labels at positions {@link #config-top}, {@link #config-right}, {@link #config-bottom} and {@link #config-left}.
 *
 * Text in labels can be set from a field on the {@link Scheduler.model.EventModel EventModel}
 * or the {@link Scheduler.model.ResourceModel ResourceModel} or using a custom renderer.
 *
 * Since `top` and `bottom` labels occupy space that would otherwise be used by the event we recommend using bigger rowHeights
 * (>55px for both labels with default styling) and zero barMargins because `top`/`bottom` labels give space around events anyway.
 *
 * This feature is **off** by default. It is **not** supported in vertical mode.
 * For info on enabling it, see {@link Grid.view.mixin.GridFeatures}.
 *
 * @extends Core/mixin/InstancePlugin
 * @demo Scheduler/labels
 * @externalexample scheduler/Labels.js
 * @classtype labels
 */

class Labels extends InstancePlugin {
  //region Config
  static get $name() {
    return 'Labels';
  }

  static get defaultConfig() {
    return {
      /**
       * CSS class to apply to label elements
       * @config {String}
       * @default
       */
      labelCls: 'b-sch-label',

      /**
       * Top label configuration object. May contain the following properties:
       * @param {String} field The name of a field in one of the associated records,
       *   {@link Scheduler.model.EventModel EventModel} or {@link Scheduler.model.ResourceModel ResourceModel}.
       *   The record from which the field value is drawn will be ascertained by checking for field definitions by
       *   the specified name.
       * @param {Function} renderer A function, which when passed an object containing `eventRecord`,
       *   `resourceRecord`, `assignmentRecord` and `domConfig` properties, returns the HTML to display as the
       *   label
       * @param {Object} thisObj The `this` reference to use in the `renderer`.
       * @param {Object|Core.widget.Field} editor If the label is to be editable, a field configuration object
       *   with a `type` property, or an instantiated Field. **The `field` property is mandatory for editing to
       *   work**.
       * @config {Object}
       * @default
       */
      top: null,

      /**
       * Right label configuration object. May contain the following properties:
       * @param {String} field The name of a field in one of the associated records,
       *   {@link Scheduler.model.EventModel EventModel} or {@link Scheduler.model.ResourceModel ResourceModel}.
       *   The record from which the field value is drawn will be ascertained by checking for field definitions by
       *   the specified name.
       * @param {Function} renderer A function, which when passed an object containing `eventRecord`,
       *   `resourceRecord`, `assignmentRecord` and `domConfig` properties, returns the HTML to display as the
       *   label
       * @param {Object} thisObj The `this` reference to use in the `renderer`.
       * @param {Object|Core.widget.Field} editor If the label is to be editable, a field configuration object
       *   with a `type` property, or an instantiated Field. **The `field` property is mandatory for editing to
       *   work**.
       * @config {Object}
       * @default
       */
      right: null,

      /**
       * Bottom label configuration object. May contain the following properties:
       * @param {String} field The name of a field in one of the associated records,
       *   {@link Scheduler.model.EventModel EventModel} or {@link Scheduler.model.ResourceModel ResourceModel}.
       *   The record from which the field value is drawn will be ascertained by checking for field definitions by
       *   the specified name.
       * @param {Function} renderer A function, which when passed an object containing `eventRecord`,
       *   `resourceRecord`, `assignmentRecord` and `domConfig` properties, returns the HTML to display as the
       *   label
       * @param {Object} thisObj The `this` reference to use in the `renderer`.
       * @param {Object|Core.widget.Field} editor If the label is to be editable, a field configuration object
       *   with a `type` property, or an instantiated Field. **The `field` property is mandatory for editing to
       *   work**.
       * @config {Object}
       * @default
       */
      bottom: null,

      /**
       * Left label configuration object. May contain the following properties:
       * @param {String} field The name of a field in one of the associated records,
       *   {@link Scheduler.model.EventModel EventModel} or {@link Scheduler.model.ResourceModel ResourceModel}.
       *   The record from which the field value is drawn will be ascertained by checking for field definitions by
       *   the specified name.
       * @param {Function} renderer A function, which when passed an object containing `eventRecord`,
       *   `resourceRecord`, `assignmentRecord` and `domConfig` properties, returns the HTML to display as the
       *   label
       * @param {Object} thisObj The `this` reference to use in the `renderer`.
       * @param {Object|Core.widget.Field} editor If the label is to be editable, a field configuration object
       *   with a `type` property, or an instantiated Field. **The `field` property is mandatory for editing to
       *   work**.
       * @config {Object}
       * @default
       */
      left: null,
      thisObj: null,

      /**
       * What action should be taken when focus moves leaves the cell editor, for example when clicking outside.
       * May be `'complete'` or `'cancel`'.
       * @config {String}
       * @default
       */
      blurAction: 'cancel'
    };
  } // Plugin configuration. This plugin chains some of the functions in Grid.

  static get pluginConfig() {
    return {
      chain: ['onEventDataGenerated']
    };
  } //endregion
  //region Init & destroy

  construct(scheduler, config) {
    const me = this;

    if (scheduler.isVertical) {
      throw new Error('Labels feature is not supported in vertical mode');
    }

    me.scheduler = scheduler;
    super.construct(scheduler, config);

    if (me.top || me.bottom || me.left || me.right) {
      me.updateHostClasslist(); // rowHeight warning, not in use
      //const labelCount = !!me.topLabel + !!me.bottomLabel;
      //if (scheduler.rowHeight < 60 - labelCount * 12) console.log('')
    }
  }

  updateHostClasslist() {
    const {
      top,
      bottom
    } = this,
          {
      classList
    } = this.scheduler.element;
    classList.remove('b-labels-topbottom');
    classList.remove('b-labels-top');
    classList.remove('b-labels-bottom'); // OR is correct. This means that there are labels above OR below.

    if (top || bottom) {
      classList.add('b-labels-topbottom');

      if (top) {
        classList.add('b-labels-top');
      }

      if (bottom) {
        classList.add('b-labels-bottom');
      }
    }
  }

  onLabelDblClick(event) {
    const me = this,
          target = event.target;

    if (target && !me.scheduler.readOnly) {
      const {
        side
      } = target.dataset,
            labelConfig = me[side],
            {
        editor,
        field
      } = labelConfig;

      if (editor) {
        const eventRecord = this.scheduler.resolveEventRecord(event.target);

        if (!(editor instanceof Editor)) {
          labelConfig.editor = new Editor({
            appendTo: me.scheduler.element,
            blurAction: me.blurAction,
            inputField: editor,
            scrollAction: 'realign'
          });
        }

        labelConfig.editor.startEdit({
          target,
          align: editorAlign[side],
          matchSize: false,
          record: eventRecord,
          field
        });
        event.stopImmediatePropagation();
        return false;
      }
    }
  }

  set top(top) {
    this._top = this.processLabelSpec(top, 'top');
    this.updateHostClasslist();
  }

  get top() {
    return this._top;
  }

  set right(right) {
    this._right = this.processLabelSpec(right, 'right');
    this.updateHostClasslist();
  }

  get right() {
    return this._right;
  }

  set bottom(bottom) {
    this._bottom = this.processLabelSpec(bottom, 'bottom');
    this.updateHostClasslist();
  }

  get bottom() {
    return this._bottom;
  }

  set left(left) {
    this._left = this.processLabelSpec(left, 'left');
    this.updateHostClasslist();
  }

  get left() {
    return this._left;
  }

  processLabelSpec(labelSpec, side) {
    if (typeof labelSpec === 'function') {
      labelSpec = {
        renderer: labelSpec
      };
    } else if (typeof labelSpec === 'string') {
      labelSpec = {
        field: labelSpec
      };
    } // Allow us to mutate ownProperties in the labelSpec without mutating outside object
    else if (labelSpec) {
        labelSpec = Object.setPrototypeOf({}, labelSpec);
      } else {
        return;
      }

    const {
      scheduler
    } = this,
          {
      eventStore,
      resourceStore,
      taskStore,
      id
    } = scheduler,
          {
      field,
      editor
    } = labelSpec; // If there are milestones, and we are changing the available height
    // either by adding a top/bottom label, or adding a top/bottom label
    // then during the next dependency refresh, milestone width must be recalculated.

    if (topBottom[side]) {
      scheduler.milestoneWidth = null;
    }

    if (eventStore && !taskStore) {
      labelSpec.recordType = 'event';
    } else {
      labelSpec.recordType = 'task';
    } // Find the field definition or property from whichever store and cache the type.

    if (field) {
      let fieldDef;

      if (eventStore && !taskStore) {
        fieldDef = eventStore.modelClass.fieldMap[field];

        if (fieldDef) {
          labelSpec.fieldDef = fieldDef;
          labelSpec.recordType = 'event';
        } // Check if it references a property
        else if (Reflect.has(eventStore.modelClass.prototype, field)) {
            labelSpec.recordType = 'event';
          }
      }

      if (!fieldDef && taskStore) {
        fieldDef = taskStore.modelClass.fieldMap[field];

        if (fieldDef) {
          labelSpec.fieldDef = fieldDef;
          labelSpec.recordType = 'task';
        } // Check if it references a property
        else if (Reflect.has(resourceStore.modelClass.prototype, field)) {
            labelSpec.recordType = 'task';
          }
      }

      if (!fieldDef && resourceStore) {
        fieldDef = resourceStore.modelClass.fieldMap[field];

        if (fieldDef) {
          labelSpec.fieldDef = fieldDef;
          labelSpec.recordType = 'resource';
        } // Check if it references a property
        else if (Reflect.has(resourceStore.modelClass.prototype, field)) {
            labelSpec.recordType = 'resource';
          }
      }

      if (editor) {
        if (typeof editor === 'boolean') {
          scheduler.editor = {
            type: 'textfield'
          };
        } else if (typeof editor === 'string') {
          scheduler.editor = {
            type: editor
          };
        }

        EventHelper.on({
          element: scheduler.timeAxisSubGrid.element,
          delegate: '.b-sch-label',
          dblclick: 'onLabelDblClick',
          thisObj: this
        });
      }
    }

    return labelSpec;
  }

  doDisable(disable) {
    super.doDisable(disable);

    if (this.client.isPainted) {
      this.client.refresh();
    }
  } //endregion

  onEventDataGenerated(data) {
    const me = this;

    if (!me.disabled) {
      // Insert all configured labels
      for (const side of sides) {
        if (me[side]) {
          const {
            field,
            fieldDef,
            recordType,
            renderer,
            thisObj
          } = me[side],
                domConfig = {
            tag: 'label',
            className: {
              [me.labelCls]: 1,
              [`${me.labelCls}-${side}`]: 1
            },
            dataset: {
              side,
              taskFeature: `label-${side}`
            }
          };
          let value;
          const eventRecordProperty = `${recordType}Record`,
                eventRecord = data[eventRecordProperty]; // If there's a renderer, use that by preference

          if (renderer) {
            value = renderer.call(thisObj || me.thisObj || me, {
              [eventRecordProperty]: eventRecord,
              resourceRecord: data.resourceRecord,
              assignmentRecord: data.assignmentRecord,
              domConfig
            });
          } else {
            value = eventRecord[field]; // If it's a date, format it according to the Scheduler's defaults

            if (fieldDef && fieldDef.type === 'date' && !renderer) {
              value = DateHelper.format(value, me.client.displayDateFormat);
            }
          }

          domConfig.html = value || '\xa0';
          data.wrapperChildren.push(domConfig);
        }
      }
    }
  }

} // TODO: Refactor the SASS, so that the auto-generated class name of ''b-' + cls.name.toLowerCase() can be used.

Labels.featureClass = 'b-sch-labels';
Labels._$name = 'Labels';
GridFeatureManager.registerFeature(Labels, false, 'Scheduler');

/**
 * @module Scheduler/feature/AbstractTimeRanges
 */

/**
 * Abstract base class, you should not use this class directly.
 * @abstract
 * @mixes Core/mixin/Delayable
 * @extends Core/mixin/InstancePlugin
 */

class AbstractTimeRanges extends InstancePlugin.mixin(Delayable) {
  //region Config
  static get defaultConfig() {
    return {
      // CSS class to apply to range elements
      rangeCls: 'b-sch-range',
      // CSS class to apply to line elements (0-duration time range)
      lineCls: 'b-sch-line',

      /**
       * Store that holds timeRanges (using the {@link Scheduler.model.TimeSpan} model or subclass thereof).
       * A store will be automatically created if none is specified
       * @config {Object|Core.data.Store}
       */
      store: {
        modelClass: TimeSpan
      },

      /**
       * Set to `true` to enable dragging and resizing of range elements in the header. Only relevant when {@link #config-showHeaderElements} is true.
       * @config {Boolean}
       * @defaultValue
       */
      enableResizing: false,

      /**
       * Specifies whether or not to show tooltip while resizing range elements
       * @config {Boolean}
       * @default
       */
      showTooltip: true,

      /**
       * `true` to render range elements into the time axis header
       * @config {Boolean}
       * @default
       */
      showHeaderElements: true,
      dragTipTemplate: data => `
                <div class="b-sch-tip-${data.valid ? 'valid' : 'invalid'}">
                    <div class="b-sch-tip-name">${data.name || ''}</div>
                    ${data.startClockHtml}
                    ${data.endClockHtml || ''}
                </div>
            `,
      baseCls: 'b-sch-timerange',
      // a unique cls used by subclasses to get custom styling of the elements rendered
      cls: '',
      narrowThreshold: 80
    };
  } // Plugin configuration. This plugin chains some of the functions in Grid.

  static get pluginConfig() {
    return {
      chain: ['onPaint', 'populateHeaderMenu', // TODO: 'headerContextMenu' is deprecated. Please see https://bryntum.com/docs/scheduler/#guides/upgrades/3.1.0.md for more information.
      'populateTimeAxisHeaderMenu']
    };
  } //endregion
  //region Init & destroy

  construct(client, config) {
    const me = this;
    super.construct(client, config); // Add a unique cls used by subclasses to get custom styling of the elements rendered
    // This makes sure that each class only removed its own elements from the DOM

    me.cls = me.cls || `b-timerange-${me.constructor.$$name}`;
    me.baseSelector = `.${me.baseCls}.${me.cls}`; // header elements are required for interaction

    if (me.enableResizing) {
      me.showHeaderElements = true;
    }
  }

  doDestroy() {
    var _me$storeDetacher, _me$clockTemplate, _me$tip, _me$drag, _me$resize;

    const me = this;
    (_me$storeDetacher = me.storeDetacher) === null || _me$storeDetacher === void 0 ? void 0 : _me$storeDetacher.call(me);
    me.detachListeners('timeAxisViewModel');
    me.detachListeners('timeAxis');
    (_me$clockTemplate = me.clockTemplate) === null || _me$clockTemplate === void 0 ? void 0 : _me$clockTemplate.destroy();
    (_me$tip = me.tip) === null || _me$tip === void 0 ? void 0 : _me$tip.destroy();
    (_me$drag = me.drag) === null || _me$drag === void 0 ? void 0 : _me$drag.destroy();
    (_me$resize = me.resize) === null || _me$resize === void 0 ? void 0 : _me$resize.destroy();
    super.doDestroy();
  }

  doDisable(disable) {
    if (this.client.isPainted) {
      this.renderRanges();
    }

    super.doDisable(disable);
  } //endregion
  //region Draw

  onPaint({
    firstPaint
  }) {
    const me = this,
          {
      client
    } = me;

    if (firstPaint) {
      me.timeAxisViewModel = client.timeAxisViewModel;
      me.timeAxisViewModel.on({
        name: 'timeAxisViewModel',
        // TODO seems not needed to listen to both "reconfigure" & "update" ..causes double re-rendering
        // reconfigure : 'onTimeAxisViewModelUpdate',
        update: 'onTimeAxisViewModelUpdate',
        thisObj: me
      });
      client.timeAxis.on({
        name: 'timeAxis',
        includeChange: 'onTimeAxisViewModelUpdate',
        thisObj: me
      });

      if (me.enableResizing && !client.hideHeaders) {
        me.drag = new DragHelper({
          name: 'rangeDrag',
          mode: client.isVertical ? 'translateY' : 'translateX',
          constrain: true,
          outerElement: me.headerContainerElement,
          targetSelector: `${me.baseSelector}`,
          isElementDraggable: (el, event) => !client.readOnly && me.isElementDraggable(el, event),
          transitionDuration: client.transitionDuration,
          listeners: {
            dragstart: 'onDragStart',
            drag: 'onDrag',
            drop: 'onDrop',
            abort: 'onInvalidDrop',
            thisObj: me
          }
        });
        me.resize = new ResizeHelper({
          direction: client.mode,
          targetSelector: `${me.baseSelector}.b-sch-range`,
          outerElement: me.headerContainerElement,
          isElementResizable: el => !el.closest('.b-dragging'),
          listeners: {
            resizestart: 'onResizeStart',
            resizing: 'onResizeDrag',
            resize: 'onResize',
            cancel: 'onInvalidResize',
            thisObj: me
          }
        });
      }

      me.renderRanges();
    }
  }

  renderRanges() {
    const me = this,
          element = me.client.foregroundCanvas; // Scheduler/Gantt might not yet be rendered

    if (element) {
      const {
        headerContainerElement
      } = me; // remove existing timeRanges

      DomHelper.removeEachSelector(element, me.baseSelector); // Partnered Scheduler might not have header container element

      if (headerContainerElement) {
        DomHelper.removeEachSelector(headerContainerElement, me.baseSelector);
      }

      if (!me.disabled) {
        // add timeRanges
        for (const range of me.timeRanges) {
          me.renderRange(range, true);
        }
      }
    }
  }
  /**
   * Returns the TimeRanges in the store.
   * @property {Scheduler.model.TimeSpan[]}
   */

  get timeRanges() {
    return this.store.records;
  }

  shouldRenderRange(range) {
    const {
      timeAxis
    } = this.client;
    return range.duration && timeAxis.timeSpanInAxis(range.startDate, range.endDate) || range.startDate && timeAxis.dateInAxis(range.startDate);
  }

  renderRange(range, injectIntoDom) {
    const me = this,
          client = me.client;

    if (me.shouldRenderRange(range)) {
      const startPos = client.getCoordinateFromDate(DateHelper.max(range.startDate, client.timeAxis.startDate), {
        respectExclusion: true
      }),
            endPos = range.duration && client.getCoordinateFromDate(DateHelper.min(range.endDate, client.timeAxis.endDate), {
        respectExclusion: true,
        isEnd: true
      }),
            size = endPos - startPos,
            labelTpl = `<label>${range.iconCls ? `<i class="${range.iconCls}"></i>` : ''} ${range.name || '&nbsp;'}</label>`,
            config = {
        className: {
          [me.baseCls]: 1,
          [me.cls]: me.cls,
          [me.rangeCls]: endPos,
          [me.lineCls]: !endPos,
          [range.cls]: range.cls,
          'b-narrow-range': endPos && size < me.narrowThreshold
        },
        //`${me.baseCls} ${me.cls} ${endPos ? me.rangeCls : me.lineCls} ${range.cls || ''}`,
        dataset: {
          id: range.id
        },
        style: client.isVertical ? `transform: translateY(${startPos}px); ${endPos ? `height:${size}px` : ''};` : `transform: translateX(${startPos}px); ${endPos ? `width:${size}px` : ''};`,
        retainElement: true // To prevent DomHelper.sync from reusing the element

      },
            bodyElement = DomHelper.createElement(Object.assign({}, config, {
        parent: injectIntoDom && client.foregroundCanvas,
        style: config.style + (range.style || ''),
        html: me.showHeaderElements && !me.showLabelInBody ? '' : labelTpl
      }));
      let headerElement;

      if (me.showHeaderElements) {
        headerElement = DomHelper.createElement(Object.assign({
          parent: injectIntoDom && me.headerContainerElement,
          html: range.name && !me.showLabelInBody ? labelTpl : ''
        }, config));
      }

      const result = {
        bodyElement,
        headerElement
      };

      if (injectIntoDom) {
        // Make the label run vertically if it overflows the width
        me.rotateLabel(range, result);
      }

      return result;
    }
  }

  rotateLabel(range, {
    bodyElement
  }) {
    // Lines have no label. Do not check label content to do not force DOM layout!
    if (!range.iconCls && !range.name) {
      return;
    }

    const label = bodyElement.firstChild; // Check if label is rendered

    if (!label) {
      return;
    } // Remove any rotation so with can be measured.

    label.classList.remove('b-vertical');
    const rotate = this.client.isVertical ? label.offsetHeight < bodyElement.offsetHeight : label.offsetWidth > bodyElement.offsetWidth; // If it overflows, rotate it.

    label.classList[rotate ? 'add' : 'remove']('b-vertical');
  } // returns one body el (+ optionally one header el) that represents a time range, or null if timeRanges is not currently rendered

  getElementsByRecord(idOrRecord) {
    var _me$client$foreground, _me$headerContainerEl;

    const me = this,
          id = typeof idOrRecord !== 'object' ? idOrRecord : idOrRecord.id,
          bodyElement = (_me$client$foreground = me.client.foregroundCanvas) === null || _me$client$foreground === void 0 ? void 0 : _me$client$foreground.querySelector(`${me.baseSelector}[data-id="${id}"]`),
          headerElement = (_me$headerContainerEl = me.headerContainerElement) === null || _me$headerContainerEl === void 0 ? void 0 : _me$headerContainerEl.querySelector(`${me.baseSelector}[data-id="${id}"]`);
    return bodyElement ? {
      bodyElement,
      headerElement
    } : null;
  }

  getBodyElementByRecord(idOrRecord) {
    const id = typeof idOrRecord === 'string' ? idOrRecord : idOrRecord.id;
    return this.client.foregroundCanvas.querySelector(`${this.baseSelector}[data-id="${id}"]`);
  }

  getRecordByElement(el) {
    return this.store.getById(el.dataset.id);
  }

  get headerContainerElement() {
    const {
      isVertical,
      timeView,
      timeAxisColumn
    } = this.client;
    let element = null; // Render into the subGrid´s header element or the vertical timeaxis depending on mode

    if (isVertical && timeView.element) {
      element = timeView.element.parentElement;
    } else if (!isVertical) {
      element = timeAxisColumn.element;
    }

    return element;
  } //endregion
  //region Settings

  /**
   * Get/set if header elements should be rendered
   * @property {boolean}
   */

  get showHeaderElements() {
    return this._showHeaderElements;
  }

  set showHeaderElements(show) {
    this._showHeaderElements = show;
    if (!this.client.isPainted) return;

    if (show) {
      this.client.element.classList.add('b-sch-timeranges-with-headerelements');
    } else {
      this.client.element.classList.remove('b-sch-timeranges-with-headerelements');
    }

    this.renderRanges();
  } //endregion
  //region Menu items
  // TODO: 'headerContextMenu' is deprecated. Please see https://bryntum.com/docs/scheduler/#guides/upgrades/4.0.0.md for more information.

  /**
   * Adds menu items for the context menu.
   * @param {Object} options Contains menu items and extra data retrieved from the menu target.
   * @param {Grid.column.Column} options.column Column for which the menu will be shown
   * @param {Object} options.items A named object to describe menu items
   * @internal
   * @deprecated 4.0.0 Use `populateTimeAxisHeaderMenu` instead.
   */

  populateHeaderMenu({
    column,
    items
  }) {
    if (column.type !== 'timeAxis') {
      return;
    }

    this.populateTimeAxisHeaderMenu(...arguments);
  }
  /**
   * Adds menu items for the context menu, and may mutate the menu configuration.
   * @param {Object} options Contains menu items and extra data retrieved from the menu target.
   * @param {Grid.column.Column} options.column Column for which the menu will be shown
   * @param {Object} options.items A named object to describe menu items
   * @internal
   */

  populateTimeAxisHeaderMenu({
    column,
    items
  }) {} //endregion
  //region Store

  /**
   * Returns the {@link Core.data.Store store} used by this feature
   * @property {Core.data.Store}
   */

  get store() {
    return this._store;
  }

  set store(store) {
    const me = this;
    me._store = Store.getStore(store, Store);
    let renderRanges = false; // if we had some store assigned before we need to detach it

    if (me.storeDetacher) {
      me.storeDetacher(); // then we'll need to render ranges provided by the new store

      renderRanges = true;
    }

    me.storeDetacher = me._store.on({
      change: me.onStoreChanged,
      refresh: me.onStoreChanged,
      thisObj: me
    }); // render ranges if needed

    renderRanges && this.renderRanges();
  } //endregion
  //region Events

  onTimeAxisViewModelUpdate() {
    this.renderRanges();
  }

  onStoreChanged(event) {
    const me = this; // https://github.com/bryntum/support/issues/1398 - checking also if scheduler is visible to change elements

    if (me.disabled || !me.client.isVisible || me.isConfiguring || event.type === 'refresh' && event.action !== 'batch') {
      return;
    } // Only animate changes that happen as a result of a pure data change, i.e. not after a drag drop

    if (event.action === 'update') {
      const range = event.record,
            id = event.changes.hasOwnProperty('id') ? event.changes.id.oldValue : range.id,
            existingElements = me.getElementsByRecord(id),
            redrawnElements = me.renderRange(range, !existingElements);

      if (existingElements && !redrawnElements) {
        existingElements.bodyElement.remove();
        existingElements.headerElement && existingElements.headerElement.remove();
      }

      if (!existingElements || !redrawnElements) return;
      me.client.runWithTransition(() => {
        DomHelper.sync(redrawnElements.bodyElement, existingElements.bodyElement);

        if (me.showHeaderElements) {
          DomHelper.sync(redrawnElements.headerElement, existingElements.headerElement);
        } // Make the label run vertically if it overflows the width

        me.rotateLabel(range, existingElements);
      });
    } else {
      me.renderRanges();
    }
  } //endregion
  //region Drag drop

  showTip(context) {
    const me = this;

    if (me.showTooltip) {
      me.clockTemplate = new ClockTemplate({
        scheduler: me.client
      });
      me.tip = new Tooltip({
        id: `${me.client.id}-time-range-tip`,
        cls: 'b-interaction-tooltip',
        align: 'b-t',
        autoShow: true,
        updateContentOnMouseMove: true,
        forElement: context.element,
        getHtml: () => me.getTipHtml(context.record, context.element)
      });
    }
  }

  isElementDraggable(el) {
    el = el.closest(this.baseSelector + ':not(.b-resizing)');
    return el && !el.classList.contains('b-over-resize-handle');
  }

  onDragStart({
    context
  }) {
    const me = this,
          record = me.getRecordByElement(context.element.closest(me.baseSelector)),
          rangeBodyEl = me.getBodyElementByRecord(record),
          drag = me.drag;
    Object.assign(context, {
      record,
      rangeBodyEl,
      originRangeX: DomHelper.getTranslateX(rangeBodyEl),
      originRangeY: DomHelper.getTranslateY(rangeBodyEl)
    });

    if (me.client.isVertical) {
      drag.minY = 0; // Moving the range, you can drag the start marker down until the end of the range hits the time axis end

      drag.maxY = me.timeAxisViewModel.totalSize - context.rangeBodyEl.offsetHeight; // Setting min/max for X makes drag right of the header valid, but visually still constrained vertically

      drag.minX = 0;
      drag.maxX = Number.MAX_SAFE_INTEGER;
    } else {
      drag.minX = 0; // Moving the range, you can drag the start marker right until the end of the range hits the time axis end

      drag.maxX = me.timeAxisViewModel.totalSize - context.rangeBodyEl.offsetWidth; // Setting min/max for Y makes drag below header valid, but visually still constrained horizontally

      drag.minY = 0;
      drag.maxY = Number.MAX_SAFE_INTEGER;
    }

    me.client.element.classList.add('b-dragging-timerange');
    me.showTip(context);
  }

  onDrag({
    context
  }) {
    // sync body element with header element (x + width)
    if (this.client.isVertical) {
      DomHelper.setTranslateY(context.rangeBodyEl, DomHelper.getTranslateY(context.element));
    } else {
      DomHelper.setTranslateX(context.rangeBodyEl, DomHelper.getTranslateX(context.element));
    }
  }

  onDrop({
    context
  }) {
    if (!context.valid) {
      return this.onInvalidDrop({
        context
      });
    }

    const me = this,
          record = context.record,
          box = context.rangeBodyEl.getBoundingClientRect(),
          newStart = me.client.getDateFromCoordinate(me.client.isVertical ? box.top : box.left, 'round', false),
          wasModified = record.startDate - newStart !== 0;

    if (wasModified) {
      record.setStartDate(newStart);
    } else {
      me.onInvalidDrop();
    }

    if (me.tip) {
      me.tip.destroy();
      me.tip = null;
    }

    me.client.element.classList.remove('b-dragging-timerange');
  }

  onInvalidDrop() {
    const me = this;
    me.drag.reset();
    me.renderRanges();
    me.client.element.classList.remove('b-dragging-timerange');

    if (me.tip) {
      me.tip.destroy();
      me.tip = null;
    }
  } // endregion
  // region Resize

  onResizeStart({
    context
  }) {
    const me = this,
          record = me.getRecordByElement(context.element.closest(me.baseSelector)),
          rangeBodyEl = me.getBodyElementByRecord(record);
    Object.assign(context, {
      record,
      rangeBodyEl
    });
    me.showTip(context);
  }

  onResizeDrag({
    context
  }) {
    const me = this;

    if (me.client.isVertical) {
      if (context.edge === 'top') {
        DomHelper.setTranslateY(context.rangeBodyEl, context.newY);
      }

      context.rangeBodyEl.style.height = context.newHeight + 'px';
    } else {
      if (context.edge === 'left') {
        DomHelper.setTranslateX(context.rangeBodyEl, context.newX);
      }

      context.rangeBodyEl.style.width = context.newWidth + 'px';
    }
  }

  onResize({
    context
  }) {
    if (!context.valid) return this.onInvalidDrop({
      context
    });
    const me = this,
          {
      client
    } = me,
          record = context.record,
          box = context.element.getBoundingClientRect(),
          startPos = client.isVertical ? box.top : box.left,
          endPos = client.isVertical ? box.bottom : box.right,
          newStart = client.getDateFromCoordinate(startPos, 'round', false),
          isStart = context.edge === 'left' || context.edge === 'top',
          newEnd = client.getDateFromCoordinate(endPos, 'round', false),
          wasModified = isStart && record.startDate - newStart !== 0 || newEnd && record.endDate - newEnd !== 0;

    if (wasModified && newEnd > newStart) {
      if (isStart) {
        // could be that the drag operation placed the range with start/end outside the axis
        record.setStartDate(newStart, false);
      } else {
        record.setEndDate(newEnd, false);
      }
    } else {
      me.onInvalidResize();
    }

    if (me.tip) {
      me.tip.destroy();
      me.tip = null;
    }
  }

  onInvalidResize() {
    const me = this;
    me.resize.reset();
    me.renderRanges();

    if (me.tip) {
      me.tip.destroy();
      me.tip = null;
    }
  } //endregion
  //region Tooltip

  /**
   * Generates the html to display in the tooltip during drag drop.
   */

  getTipHtml(record, element) {
    const me = this,
          {
      client
    } = me,
          box = element.getBoundingClientRect(),
          startPos = client.isVertical ? box.top : box.left,
          endPos = client.isVertical ? box.bottom : box.right,
          startDate = client.getDateFromCoordinate(startPos, 'round', false),
          endDate = record.endDate && client.getDateFromCoordinate(endPos, 'round', false),
          startText = client.getFormattedDate(startDate),
          endText = endDate && client.getFormattedEndDate(endDate, startDate);
    return me.dragTipTemplate({
      name: record.name || '',
      startDate: startDate,
      endDate: endDate,
      startText: startText,
      endText: endText,
      startClockHtml: me.clockTemplate.template({
        date: startDate,
        text: startText,
        cls: 'b-sch-tooltip-startdate'
      }),
      endClockHtml: endText && me.clockTemplate.template({
        date: endDate,
        text: endText,
        cls: 'b-sch-tooltip-enddate'
      })
    });
  } //endregion

}
AbstractTimeRanges._$name = 'AbstractTimeRanges';

/**
 * @module Scheduler/feature/NonWorkingTime
 */

/**
 * Feature that allows styling of weekends (and other non working time) by adding timeRanges for those days.
 *
 * By default, Schedulers calendar is empty. When enabling this feature for the basic Scheduler, it injects weekend
 * intervals if no intervals are encountered (saturday and sunday).
 *
 * Please note that the feature does not render ranges smaller than the base unit used by the time axis
 * and for some levels it bails out rendering ranges completely (see {@link #config-maxTimeAxisUnit} for details).
 *
 * This feature is **off** by default for Scheduler, but **enabled** by default for Scheduler Pro.
 * For info on enabling it, see {@link Grid.view.mixin.GridFeatures}.
 *
 * @extends Scheduler/feature/AbstractTimeRanges
 * @demo Scheduler/configuration
 * @externalexample scheduler/NonWorkingTime.js
 * @classtype nonWorkingTime
 */

class NonWorkingTime extends AbstractTimeRanges.mixin(AttachToProjectMixin) {
  //region Default config
  static get $name() {
    return 'NonWorkingTime';
  }

  static get defaultConfig() {
    return {
      /**
       * Set to `true` to highlight non working periods of time
       * @config {Boolean}
       * @default
       */
      highlightWeekends: true,
      showHeaderElements: true,
      showLabelInBody: true,
      cls: 'b-sch-nonworkingtime',

      /**
       * The maximum time axis unit to display non working ranges for.
       * Allows to filter out zoom levels on which the feature should not render.
       * @config {String}
       * @default
       */
      maxTimeAxisUnit: 'week'
    };
  }

  static get pluginConfig() {
    return {
      chain: ['onPaint', 'attachToProject']
    };
  } //endregion
  //region Init & destroy

  doDestroy() {
    this.attachToCalendar(null);
    super.doDestroy();
  } //endregion
  //region Project

  attachToProject(project) {
    super.attachToProject(project);
    this.attachToCalendar(project.effectiveCalendar);
    project.on({
      name: 'project',
      calendarChange: () => this.attachToCalendar(this.project.effectiveCalendar),
      thisObj: this
    });
  } //endregion
  //region TimeAxisViewModel

  onTimeAxisViewModelUpdate() {
    this._timeAxisUnitDurationMs = null;
    return super.onTimeAxisViewModelUpdate();
  } //endregion
  //region Calendar

  attachToCalendar(calendar) {
    const me = this,
          {
      project,
      client
    } = me;
    me.detachListeners('calendar');

    if (calendar) {
      if ( // For basic scheduler...
      !client.isSchedulerPro && !client.isGantt && // ...that uses the default calendar...
      calendar === project.defaultCalendar && // ...and has no defined intervals
      !project.defaultCalendar.intervalStore.count) {
        // Add weekends as non-working time
        calendar.addIntervals([{
          recurrentStartDate: 'on Sat at 0:00',
          recurrentEndDate: 'on Mon at 0:00',
          isWorking: false
        }]);
      }

      calendar.intervalStore.on({
        name: 'calendar',
        change: () => me.setTimeout('renderRanges', 1)
      });
    } // On changing calendar we react to a data level event which is triggered after project refresh.
    // Redraw right away

    if (client.isEngineReady) {
      me.renderRanges();
    } // Initially there is no guarantee we are ready to draw, wait for refresh
    else if (!project.isDestroyed) {
        project.on({
          refresh: me.renderRanges,
          thisObj: me,
          once: true
        });
      }
  }

  get calendar() {
    var _this$project;

    return (_this$project = this.project) === null || _this$project === void 0 ? void 0 : _this$project.effectiveCalendar;
  } //endregion
  //region Draw

  get timeAxisUnitDurationMs() {
    // calculate and cache duration of the timeAxis unit in milliseconds
    if (!this._timeAxisUnitDurationMs) {
      this._timeAxisUnitDurationMs = DateHelper.as('ms', 1, this.client.timeAxis.unit);
    }

    return this._timeAxisUnitDurationMs;
  }

  shouldRenderRange(range) {
    // if the range is longer or equal than one timeAxis unit then render it
    return super.shouldRenderRange(range) && range.durationMS >= this.timeAxisUnitDurationMs;
  }

  renderRanges() {
    const me = this,
          {
      store,
      calendar
    } = me,
          {
      timeAxis,
      foregroundCanvas
    } = me.client; // if not too early (project correctly set up yet etc)

    if (calendar && foregroundCanvas && store && !store.isDestroyed) {
      if (!me.disabled) {
        // checks if we should render ranges for the current zoom level
        const shouldPaint = !me.maxTimeAxisUnit || DateHelper.compareUnits(timeAxis.unit, me.maxTimeAxisUnit) <= 0;
        store.removeAll(true);

        if (calendar && me.highlightWeekends && shouldPaint && timeAxis.count) {
          const timeRanges = calendar.getNonWorkingTimeRanges(timeAxis.startDate, timeAxis.endDate).map((r, i) => ({
            name: r.name,
            cls: 'b-nonworkingtime',
            startDate: r.startDate,
            endDate: r.endDate
          }));
          const timeRangesMerged = [];
          let prevRange; // intervals returned by the calendar are not merged ..let's combine them

          timeRanges.forEach(range => {
            if (prevRange && range.startDate <= prevRange.endDate && range.name === prevRange.name) {
              prevRange.endDate = range.endDate;
            } else {
              timeRangesMerged.push(range);
              range.id = `nonworking-${timeRangesMerged.length}`;
              prevRange = range;
            }
          });
          store.add(timeRangesMerged, true);
        }
      }

      super.renderRanges();
    }
  } //endregion

}
NonWorkingTime._$name = 'NonWorkingTime';
GridFeatureManager.registerFeature(NonWorkingTime, false, 'Scheduler');
GridFeatureManager.registerFeature(NonWorkingTime, true, 'SchedulerPro');
GridFeatureManager.registerFeature(NonWorkingTime, true, 'Gantt');

/**
 * @module Scheduler/feature/Pan
 */

/**
 * Makes the scheduler's timeline pannable by dragging with the mouse.
 *
 * This feature is **off** by default. For info on enabling it, see {@link Grid.view.mixin.GridFeatures}.
 *
 * **NOTE:** Incompatible with the {@link Scheduler.feature.EventDragCreate EventDragCreate} and the {@link Scheduler.feature.EventDragSelect EventDragSelect} features.
 *
 * @extends Core/mixin/InstancePlugin
 *
 * @example
 * // enable Pan
 * let scheduler = new Scheduler({
 *   features : {
 *     pan : true,
 *     eventDragCreate : false
 *   }
 * });
 *
 * @classtype pan
 */

class Pan extends InstancePlugin {
  // region Init
  static get $name() {
    return 'Pan';
  }

  static get defaultConfig() {
    return {
      /**
       * Set to false to only pan horizontally
       * @config {Boolean}
       * @default
       */
      vertical: true
    };
  }

  construct(timeline, config) {
    this.timeline = timeline;
    const targetSelectors = ['.b-grid-cell', '.b-timeline-subgrid'];
    this.targetSelector = targetSelectors.join(',');
    super.construct(timeline, config);
  } //endregion
  //region Plugin config
  // Plugin configuration. This plugin chains some of the functions in Scheduler.

  static get pluginConfig() {
    return {
      chain: ['onElementMouseDown', 'onElementMouseMove', 'onElementMouseUp']
    };
  } //endregion

  onElementMouseDown(event) {
    const me = this,
          timeline = me.timeline,
          dragFeature = Object.values(timeline.features).find(feature => feature instanceof DragBase),
          enablePanOnEvents = timeline.readOnly || !dragFeature || dragFeature.disabled; // only react to mouse input, and left button

    if (event.touches || event.button !== 0 || me.disabled) {
      return;
    } // only react to mousedown directly on grid cell, subgrid element or if drag is disabled - the events too

    if (event.target.matches(me.targetSelector) || enablePanOnEvents && event.target.closest(timeline.eventSelector)) {
      me.mouseX = event.clientX;
      me.mouseY = event.clientY;
    }
  }

  onElementMouseMove(event) {
    const me = this; // Synthetic mousemove event has no button for IE11
    // Detect if a mouseup happened outside our element (or browser window for that matter). Note 'buttons' is not supported by Safari

    if (event.buttons === 0 && !BrowserHelper.isSafari && (!BrowserHelper.isIE11 || event.isTrusted)) {
      me.onElementMouseUp();
      return;
    }

    if (typeof me.mouseX === 'number') {
      const xScroller = me.timeline.subGrids.normal.scrollable,
            yScroller = me.timeline.scrollable,
            x = event.clientX,
            y = event.clientY;
      event.preventDefault();

      if (me.vertical) {
        yScroller.scrollBy(0, me.mouseY - y);
      }

      xScroller.scrollBy(me.mouseX - x);
      me.mouseX = x;
      me.mouseY = y;
    }
  }

  onElementMouseUp(event) {
    this.mouseX = this.mouseY = null;
  } //endregion

}
Pan._$name = 'Pan';
GridFeatureManager.registerFeature(Pan, false, ['Scheduler', 'Gantt']);

/**
 * @module Scheduler/feature/base/ResourceTimeRangesBase
 */

/**
 * Abstract base class for ResourceTimeRanges and ResourceNonWorkingTime features.
 * You should not use this class directly.
 *
 * @extends Core/mixin/InstancePlugin
 * @abstract
 */

class ResourceTimeRangesBase extends InstancePlugin.mixin(AttachToProjectMixin) {
  //region Config
  // Plugin configuration. This plugin chains some of the functions in Grid.
  static get pluginConfig() {
    return {
      chain: ['getEventsToRender', 'onEventDataGenerated', 'noFeatureElementsInAxis']
    };
  } // Let Scheduler know if we have ResourceTimeRanges in view or not

  noFeatureElementsInAxis() {
    const {
      timeAxis
    } = this.scheduler;
    return this.store && !this.store.storage.values.some(t => timeAxis.isTimeSpanInAxis(t));
  } //endregion
  //region Init

  construct(scheduler, config) {
    const me = this;
    me.scheduler = scheduler;
    super.construct(scheduler, config);
  }

  doDisable(disable) {
    if (this.client.isPainted) {
      this.client.refresh();
    }

    super.doDisable(disable);
  } //endregion

  getEventsToRender(resource, events) {
    throw new Error('Implement in subclass');
  } // Called for each event during render, allows manipulation of render data. Adjust any resource time ranges
  // (chained function from Scheduler)

  onEventDataGenerated(renderData) {
    const me = this,
          {
      eventRecord
    } = renderData;

    if (me.shouldInclude(eventRecord)) {
      if (me.scheduler.isVertical) {
        renderData.width = me.scheduler.resourceColumnWidth;
      } else {
        renderData.top = 0;
      } // Flag that we should fill entire row/col

      renderData.fillSize = true; // Add our own cls

      renderData.wrapperCls[me.rangeCls] = 1;
      renderData.wrapperCls[`b-sch-color-${eventRecord.timeRangeColor}`] = eventRecord.timeRangeColor; // Add label

      renderData.children.push(eventRecord.name); // Event data for DOMSync comparison

      renderData.eventId = `${me.idPrefix}-${eventRecord.id}`;
    }
  }

  shouldInclude(eventRecord) {
    throw new Error('Implement in subclass');
  } // Called when a ResourceTimeRangeModel is manipulated, relays to Scheduler#onInternalEventStoreChange which updates to UI

  onStoreChange(event) {
    this.scheduler.onInternalEventStoreChange(event);
  }

} // No feature based styling needed, do not add a cls to Scheduler

ResourceTimeRangesBase.featureClass = '';
ResourceTimeRangesBase._$name = 'ResourceTimeRangesBase';

/**
 * @module Scheduler/feature/ResourceTimeRanges
 */

/**
 * Feature that draws resource time ranges, shaded areas displayed behind events. These zones are similar to events in
 * that they have a start and end date but different in that they do not take part in the event layout and they always
 * occupy full row height.
 *
 * Each time range is represented by an instances of {@link Scheduler.model.ResourceTimeRangeModel ResourceTimeRangeModel}, held in a
 * {@link Scheduler.data.ResourceTimeRangeStore ResourceTimeRangeStore}. Currently the they are readonly UI-wise, but can be manipulated on
 * the data level. To style the rendered elements, use the {@link Scheduler.model.TimeSpan#field-cls cls} field or use the {@link Scheduler.model.ResourceTimeRangeModel#field-timeRangeColor} field.
 *
 * Data can be provided either using the {@link Scheduler.view.Scheduler#config-resourceTimeRanges resourceTimeRanges} config on the Scheduler config object:
 *
 * ```javascript
 * new Scheduler({
 *     ...
 *    features :  {
 *        resourceTimeRanges : true
 *    },
 *
 *    // Data specified directly on the Scheduler instance
 *    resourceTimeRanges : [
 *        // Either specify startDate & endDate or startDate & duration when defining a range
 *        { startDate : new Date(2019,0,1), endDate : new Date(2019,0,3), name : 'Occupied', timeRangeColor : 'red' },
 *        { startDate : new Date(2019,0,3), duration : 2, durationUnit : 'd', name : 'Available' },
 *    ]
 * })
 * ```
 *
 * or the {@link Scheduler.view.Scheduler#config-resourceTimeRangeStore resourceTimeRangeStore} config on the Scheduler config object:
 *
 * ```javascript
 * new Scheduler({
 *     ...
 *     features :  {
 *         resourceTimeRanges : true
 *     },
 *     resourceTimeRangeStore : new ResourceTimeRangeStore({
 *         readUrl : './resourceTimeRanges/'
 *     })
 * })
 * ```
 *
 * This feature is **off** by default. For info on enabling it, see {@link Grid.view.mixin.GridFeatures}.
 *
 * ## Recurring ranges support
 *
 * The feature supports recurring ranges in case the provided store and models
 * have {@link Scheduler.data.mixin.RecurringTimeSpansMixin RecurringTimeSpansMixin} and {@link Scheduler/model/mixin/RecurringTimeSpan RecurringTimeSpan}
 * mixins applied:
 *
 * ```js
 * // We want to use recurring time ranges
 * // so we make a special model extending standard ResourceTimeRangeModel
 * // with RecurringTimeSpan which adds recurrence support
 * class MyResourceTimeRange extends RecurringTimeSpan(ResourceTimeRangeModel) {};
 *
 * // Define a new store extending standard ResourceTimeRangeStore
 * // with RecurringTimeSpansMixin mixin to add recurrence support to the store.
 * // This store will contain time ranges.
 * class MyResourceTimeRangeStore extends RecurringTimeSpansMixin(ResourceTimeRangeStore) {
 *     static get defaultConfig() {
 *         return {
 *             // use our new MyResourceTimeRange model
 *             modelClass : MyResourceTimeRange
 *         };
 *     }
 * };
 *
 * // Instantiate store for resourceTimeRanges using our new classes
 * const resourceTimeRangeStore = new MyResourceTimeRangeStore({
 *     data : [{        {
 *         id             : 1,
 *         resourceId     : 'r1',
 *         startDate      : '2019-01-01T11:00',
 *         endDate        : '2019-01-01T13:00',
 *         name           : 'Lunch',
 *         // this time range should repeat every day
 *         recurrenceRule : 'FREQ=DAILY'
 *     }]
 * });
 *
 * const scheduler = new Scheduler({
 *     ...
 *     features : {
 *         resourceTimeRanges : true
 *     },
 *     // store for "resourceTimeRanges" feature
 *     resourceTimeRangeStore,
 *     ...
 * ```
 *
 * @extends Scheduler/feature/base/ResourceTimeRangesBase
 * @demo Scheduler/resourcetimeranges
 * @externalexample scheduler/ResourceTimeRanges.js
 * @classtype resourceTimeRanges
 */

class ResourceTimeRanges extends ResourceTimeRangesBase {
  //region Config
  static get $name() {
    return 'ResourceTimeRanges';
  }

  static get defaultConfig() {
    return {
      idPrefix: 'resourcetimerange',
      rangeCls: 'b-sch-resourcetimerange',

      /**
       * Store that holds resource time ranges (using ResourceTimeRangeModel or subclass thereof). A store will be
       * automatically created if none is specified
       * @config {Scheduler.data.ResourceTimeRangeStore}
       * @deprecated 4.0 Define `resourceTimeRangeStore` on Scheduler or its project
       */
      store: false,

      /**
       * Time range definitions (data to ResourceTimeRangeModels). Will be added to store. Can also be specified
       * on Scheduler for convenience
       * @config {Scheduler.model.ResourceTimeRangeModel[]|Object[]}
       * @deprecated 4.0 Define `resourceTimeRanges` on Scheduler or `resourceTimeRangesData` on its project
       */
      resourceTimeRanges: null
    };
  } //endregion
  //region Init

  attachToProject(project) {
    const me = this,
          {
      scheduler
    } = me;
    super.attachToProject(project);

    if (!project.resourceTimeRangeStore) {
      project.resourceTimeRangeStore = me._store || scheduler.resourceTimeRangeStore || new ResourceTimeRangeStore();
      const {
        crudManager
      } = scheduler;

      if (crudManager && !crudManager.resourceTimeRangeStore) {
        crudManager.resourceTimeRangeStore = project.resourceTimeRangeStore;
      }
    }

    const store = project.resourceTimeRangeStore;

    if (!me.exposedOnScheduler) {
      // ResourceZones can be set on scheduler or feature, for convenience
      if (scheduler.resourceTimeRanges) {
        store.add(scheduler.resourceTimeRanges);
        delete scheduler.resourceTimeRanges;
      } // expose getter/setter for resourceTimeRanges on scheduler

      Object.defineProperty(scheduler, 'resourceTimeRanges', {
        get: () => store.records,
        set: resourceTimeRanges => store.data = resourceTimeRanges
      });
      Object.defineProperty(scheduler, 'resourceTimeRangeStore', {
        get: () => store
      });
      me.exposedOnScheduler = true;
    } // Link to projects resourceStore if not already linked to one

    if (!store.resourceStore) {
      store.resourceStore = project.resourceStore;
    }

    me.detachListeners('store');
    store.on({
      name: 'store',
      change: me.onStoreChange,
      thisObj: me
    });
  }
  /**
   * Called during construction to do product specific store setup
   * @private
   */

  set store(store) {
    // For backwards compatibility, we now want the store on the project
    this._store = store;
  }

  get store() {
    return this.project.resourceTimeRangeStore;
  } //endregion
  // Called on render of resources events to get events to render. Add any ranges
  // (chained function from Scheduler)

  getEventsToRender(resource, events) {
    const {
      timeRanges
    } = resource; // if we have ranges and the feature is enabled

    if (timeRanges !== null && timeRanges !== void 0 && timeRanges.length && !this.disabled) {
      const {
        startDate,
        endDate
      } = this.client;
      timeRanges.forEach(timeRange => {
        // if this a recurring event let's include its visible occurrences
        if (timeRange.isRecurring) {
          events.push(...timeRange.getOccurrencesForDateRange(startDate, endDate));
        } else {
          events.push(timeRange);
        }
      });
    }

    return events;
  }

  shouldInclude(eventRecord) {
    return eventRecord.isResourceTimeRange && !eventRecord.isNonWorking;
  }

} // No feature based styling needed, do not add a cls to Scheduler

ResourceTimeRanges.featureClass = '';
ResourceTimeRanges._$name = 'ResourceTimeRanges';
GridFeatureManager.registerFeature(ResourceTimeRanges, false, 'Scheduler');

/**
 * @module Scheduler/feature/ScheduleMenu
 */

/**
 * Displays a context menu for empty parts of the schedule. Items are populated in the first place
 * by configurations of this Feature, then by other features and/or application code.
 *
 * ### Default scheduler zone menu items
 *
 * The Scheduler menu feature provides only one item:
 *
 * | Reference      | Text      | Weight | Feature        | Description                                                                                                          |
 * |----------------|-----------|--------|----------------|----------------------------------------------------------------------------------------------------------------------|
 * | `addEvent`     | Add event | 100    | *This feature* | Add a new event for the hovered resource starting at the clicked point in time. Hidden if the Scheduler is read-only |
 *
 * ### Customizing the menu items
 *
 * The menu items in the Scheduler menu can be customized, existing items can be changed or removed,
 * and new items can be added. This is handled using the `items` config of the feature.
 *
 * Add extra item:
 *
 * ```javascript
 * const scheduler = new Scheduler({
 *     features : {
 *         scheduleMenu : {
 *             items : {
 *                 extraItem : {
 *                     text : 'Extra',
 *                     icon : 'b-fa b-fa-fw b-fa-flag',
 *                     onItem({date, resourceRecord, items}) {
 *                         // Custom date based action
 *                     }
 *                 }
 *             }
 *         }
 *     }
 * });
 * ```
 *
 * Remove existing item:
 *
 * ```javascript
 * const scheduler = new Scheduler({
 *     features : {
 *         scheduleMenu : {
 *             items : {
 *                 addEvent : false
 *             }
 *         }
 *     }
 * });
 * ```
 *
 * Customize existing item:
 *
 * ```javascript
 * const scheduler = new Scheduler({
 *     features : {
 *         scheduleMenu : {
 *             items : {
 *                 addEvent : {
 *                     text : 'Create new booking'
 *                 }
 *             }
 *         }
 *     }
 * });
 * ```
 *
 * Manipulate existing items:
 *
 * ```javascript
 * const scheduler = new Scheduler({
 *     features : {
 *         scheduleMenu : {
 *             // Process items before menu is shown
 *             processItems({date, resourceRecord, items}) {
 *                  // Add an extra item for ancient times
 *                  if (date < new Date(2018, 11, 17)) {
 *                      items.modernize = {
 *                          text : 'Modernize',
 *                          ontItem({date}) {
 *                              // Custom date based action
 *                          }
 *                      };
 *                  }
 *
 *                  // Do not show menu for Sundays
 *                  if (date.getDay() === 0) {
 *                      return false;
 *                  }
 *             }
 *         }
 *     }
 * });
 * ```
 *
 * Full information of the menu customization can be found in the "Customizing the Event menu, the Schedule menu, and the TimeAxisHeader menu" guide.
 *
 * This feature is **enabled** by default
 *
 * @demo Scheduler/basic
 * @extends Scheduler/feature/base/TimeSpanMenuBase
 * @classtype scheduleMenu
 */

class ScheduleMenu extends TimeSpanMenuBase {
  //region Config
  static get $name() {
    return 'ScheduleMenu';
  }

  static get defaultConfig() {
    return {
      type: 'schedule',

      /**
       * This is a preconfigured set of items used to create the default context menu.
       *
       * The `items` provided by this feature are listed below. These are the predefined property names which you may
       * configure:
       *
       * - `addEvent` Add an event for at the resource and time indicated by the `contextmenu` event.
       *
       * To remove existing items, set corresponding keys to `false`
       *
       * ```javascript
       * const scheduler = new Scheduler({
       *     features : {
       *         scheduleMenu : {
       *             items : {
       *                 addEvent : false
       *             }
       *         }
       *     }
       * });
       * ```
       *
       * @config {Object} items
       */
      items: null,

      /**
       * A function called before displaying the menu that allows manipulations of its items.
       * Returning `false` from this function prevents the menu being shown.
       *
       * ```javascript
       * features         : {
       *    scheduleMenu : {
       *         processItems({ items, date, resourceRecord }) {
       *            // Add or hide existing items here as needed
       *            items.myAction = {
       *                text   : 'Cool action',
       *                icon   : 'b-fa b-fa-cat',
       *                onItem : () => console.log(`Clicked on ${resourceRecord.name} at ${date}`),
       *                weight : 1000 // Move to end
       *            };
       *
       *            if (!resourceRecord.allowAdd) {
       *                items.addEvent.hidden = true;
       *            }
       *        }
       *    }
       * },
       * ```
       * @param {Object} context An object with information about the menu being shown
       * @param {Scheduler.model.ResourceModel} context.resourceRecord The record representing the current resource
       * @param {Date} context.date The clicked date
       * @param {Object} context.items An object containing the {@link Core.widget.MenuItem menu item} configs keyed by their id
       * @param {Event} context.event The DOM event object that triggered the show
       * @config {Function}
       * @preventable
       */
      processItems: null
    };
  }

  static get pluginConfig() {
    const config = super.pluginConfig;
    config.chain.push('populateScheduleMenu');
    return config;
  } //endregion
  //region Events

  /**
   * Fired from scheduler before the context menu is shown for an event. Allows manipulation of the items
   * to show in the same way as in `processItems`. Returning `false` from a listener prevents the menu from
   * being shown.
   * @event scheduleMenuBeforeShow
   * @preventable
   * @param {Scheduler.view.Scheduler} source
   * @param {Object} items Menu item configs
   * @param {Scheduler.model.EventModel} eventRecord Event record for which the menu was triggered
   * @param {Scheduler.model.ResourceModel} resourceRecord Resource record
   * @param {Scheduler.model.AssignmentModel} assignmentRecord Assignment record, if assignments are used
   * @param {HTMLElement} eventElement
   */

  /**
   * Fired from scheduler when an item is selected in the context menu.
   * @event scheduleMenuItem
   * @param {Scheduler.view.Scheduler} source
   * @param {Core.widget.MenuItem} item
   * @param {Scheduler.model.EventModel} eventRecord
   * @param {Scheduler.model.ResourceModel} resourceRecord
   * @param {Scheduler.model.AssignmentModel} assignmentRecord Assignment record, if assignments are used
   * @param {HTMLElement} eventElement
   */

  /**
   * Fired from scheduler after showing the context menu for an event
   * @event scheduleMenuShow
   * @param {Scheduler.view.Scheduler} source
   * @param {Core.widget.Menu} menu The menu
   * @param {Scheduler.model.EventModel} eventRecord Event record for which the menu was triggered
   * @param {Scheduler.model.ResourceModel} resourceRecord Resource record
   * @param {Scheduler.model.AssignmentModel} assignmentRecord Assignment record, if assignments are used
   * @param {HTMLElement} eventElement
   */
  //endregion

  shouldShowMenu(eventParams) {
    const {
      client
    } = this,
          {
      column,
      targetElement,
      resourceRecord
    } = eventParams,
          isTimeAxisColumn = column ? column === client.timeAxisColumn : client.timeAxisSubGrid.element === targetElement;
    return isTimeAxisColumn && !(resourceRecord && resourceRecord.isSpecialRow);
  }

  getDataFromEvent(event) {
    const {
      client
    } = this,
          cellData = client.getCellDataFromEvent(event),
          date = client.getDateFromDomEvent(event, 'floor'),
          // For vertical mode the resource must be resolved from the event
    resourceRecord = client.resolveResourceRecord(event) || client.resourceStore.last;
    return ObjectHelper.assign(super.getDataFromEvent(event), cellData, {
      date,
      resourceRecord
    });
  }

  populateScheduleMenu({
    items
  }) {
    const {
      client
    } = this;

    if (!client.readOnly) {
      items.addEvent = {
        text: 'L{SchedulerBase.Add event}',
        icon: 'b-icon b-icon-add',
        disabled: client.resourceStore.count === 0,
        weight: 100,

        onItem({
          date,
          resourceRecord
        }) {
          client.internalAddEvent(date, resourceRecord, client.getRowFor(resourceRecord));
        }

      };
    }
  }

}
ScheduleMenu.featureClass = '';
ScheduleMenu._$name = 'ScheduleMenu';
GridFeatureManager.registerFeature(ScheduleMenu, true, 'Scheduler');

/**
 * @module Scheduler/feature/ScheduleContextMenu
 */

/**
 * **DEPRECATED: This class is deprecated since v4.0.0,
 * use {@link Scheduler.feature.ScheduleMenu ScheduleMenu} feature instead.**
 *
 * Displays a context menu for empty parts of the schedule. Items are populated by other features and/or application code.
 *
 * This feature is **off** by default. For info on enabling it, see {@link Grid.view.mixin.GridFeatures}.
 *
 * @deprecated 4.0.0
 * @extends Scheduler/feature/ScheduleMenu
 * @classtype scheduleContextMenu
 */

class ScheduleContextMenu extends ScheduleMenu {
  //region Config
  static get $name() {
    return 'ScheduleContextMenu';
  }

  construct(scheduler, config) {
    super.construct(scheduler, config);
    this.scheduler = scheduler;
    VersionHelper.deprecate('Scheduler', '5.0.0', '`ScheduleContextMenu` feature is deprecated, in favor of `ScheduleMenu` feature. Please see https://bryntum.com/docs/scheduler/#guides/upgrades/3.1.0.md for more information.'); // If this feature is enabled, need to disable ScheduleMenu. They are mutually exclusive.

    if (scheduler.features.scheduleMenu) {
      scheduler.features.scheduleMenu.disabled = true;
    }
  } //endregion
  //region Events

  /**
   * Fired from scheduler before the context menu is shown for an event. Allows manipulation of the items
   * to show in the same way as in `processItems`. Returning `false` from a listener prevents the menu from
   * being shown.
   * @event scheduleContextMenuBeforeShow
   * @deprecated 4.0.0 Renamed to scheduleMenuBeforeShow
   * @preventable
   * @param {Scheduler.view.Scheduler} source
   * @param {Object} items Menu item configs
   * @param {Scheduler.model.EventModel} eventRecord Event record for which the menu was triggered
   * @param {Scheduler.model.ResourceModel} resourceRecord Resource record
   * @param {Scheduler.model.AssignmentModel} assignmentRecord Assignment record, if assignments are used
   * @param {HTMLElement} eventElement
   */

  /**
   * Fired from scheduler when an item is selected in the context menu.
   * @event scheduleContextMenuItem
   * @deprecated 4.0.0 Renamed to scheduleMenuItem
   * @param {Scheduler.view.Scheduler} source
   * @param {Core.widget.MenuItem} item
   * @param {Scheduler.model.EventModel} eventRecord
   * @param {Scheduler.model.ResourceModel} resourceRecord
   * @param {Scheduler.model.AssignmentModel} assignmentRecord Assignment record, if assignments are used
   * @param {HTMLElement} eventElement
   */

  /**
   * Fired from scheduler after showing the context menu for an event
   * @event scheduleContextMenuShow
   * @deprecated 4.0.0 Renamed to eventMenuShow
   * @param {Scheduler.view.Scheduler} source
   * @param {Core.widget.Menu} menu The menu
   * @param {Scheduler.model.EventModel} eventRecord Event record for which the menu was triggered
   * @param {Scheduler.model.ResourceModel} resourceRecord Resource record
   * @param {Scheduler.model.AssignmentModel} assignmentRecord Assignment record, if assignments are used
   * @param {HTMLElement} eventElement
   */
  //endregion

}
ScheduleContextMenu.featureClass = '';
ScheduleContextMenu._$name = 'ScheduleContextMenu';
GridFeatureManager.registerFeature(ScheduleContextMenu);

/**
 * @module Scheduler/feature/ScheduleTooltip
 */

/**
 * Feature that displays a tooltip containing the time at the mouse position when hovering empty parts of the schedule.
 * To hide the schedule tooltip, just disable this feature:
 *
 * ```javascript
 * const scheduler = new Scheduler({
 *     features : {
 *         scheduleTooltip : false
 *     }
 * });
 * ```
 *
 * You can also output a message along with the default time indicator (to indicate resource availability etc)
 *
 * ```javascript
 * const scheduler = new Scheduler({
 *    features : {
 *       scheduleTooltip : {
 *           getText(date, event, resource) {
 *               return 'Hovering ' + resource.name;
 *           }
 *       }
 *   }
 * });
 * ```
 *
 * To take full control over the markup shown in the tooltip you can override the {@link #function-generateTipContent} method:
 * ```
 * const scheduler = new Scheduler({
 *     features : {
 *         scheduleTooltip : {
 *             generateTipContent({ date, event, resourceRecord }) {
 *                 return `
 *                     <dl>
 *                         <dt>Date</dt><dd>${date}</dd>
 *                         <dt>Resource</dt><dd>${resourceRecord.name}</dd>
 *                     </dl>
 *                 `;
 *             }
 *         }
 *     }
 * });
 * @extends Core/mixin/InstancePlugin
 * @demo Scheduler/basic
 * @externalexample scheduler/ScheduleTooltip.js
 * @classtype scheduleTooltip
 */

class ScheduleTooltip extends InstancePlugin {
  //region Config
  static get $name() {
    return 'ScheduleTooltip';
  }

  static get defaultConfig() {
    return {
      messageTemplate: data => `<div class="b-sch-hovertip-msg">${data.message}</div>`
    };
  } // Plugin configuration. This plugin chains some of the functions in Grid.

  static get pluginConfig() {
    return {
      chain: ['render']
    };
  } //endregion
  //region Init

  /**
   * Called when scheduler is rendered. Sets up drag and drop and hover tooltip.
   * @private
   */

  render() {
    const me = this,
          scheduler = me.client; // TODO: render should not ever be called twice.

    if (me.hoverTip) {
      me.hoverTip.destroy();
    }

    let reshowListener;
    const tip = me.hoverTip = new Tooltip({
      id: `${scheduler.id}-schedule-tip`,
      cls: 'b-sch-scheduletip',
      allowOver: true,
      hoverDelay: 0,
      hideDelay: 100,
      showOnHover: true,
      forElement: scheduler.timeAxisSubGridElement,
      anchorToTarget: false,
      trackMouse: true,
      updateContentOnMouseMove: true,
      // disable text content and monitor resize for tooltip, otherwise it doesn't
      // get sized properly on first appearance
      monitorResize: false,
      textContent: false,
      forSelector: '.b-schedulerbase:not(.b-animating):not(.b-dragging-event):not(.b-dragcreating) .b-grid-body-container:not(.b-scrolling) .b-timeline-subgrid:not(.b-scrolling) > :not(.b-sch-foreground-canvas):not(.b-group-footer):not(.b-group-row) *',
      // Do not constrain at all, want it to be able to go outside of the viewport to not get in the way
      constrainTo: null,
      getHtml: me.getHoverTipHtml.bind(me),

      onDocumentMouseDown(event) {
        // Click on the scheduler hides until the very next
        // non-button-pressed mouse move!
        if (tip.forElement.contains(event.event.target)) {
          reshowListener = EventHelper.on({
            element: scheduler.timeAxisSubGridElement,
            mousemove: e => tip.internalOnPointerOver(e),
            capture: true
          });
        }

        const hideAnimation = tip.hideAnimation;
        tip.hideAnimation = false;
        tip.constructor.prototype.onDocumentMouseDown.call(tip, event);
        tip.hideAnimation = hideAnimation;
      },

      listeners: {
        pointerover: ({
          event
        }) => {
          const buttonsPressed = 'buttons' in event ? event.buttons > 0 : event.which > 0; // fallback for Safari which doesn't support 'buttons'
          // This is the non-button-pressed mousemove
          // after the document mousedown

          if (!buttonsPressed && reshowListener) {
            reshowListener();
          } // Never any tooltip while interaction is ongoing and a mouse button is pressed

          return !me.disabled && !buttonsPressed;
        }
      }
    });
    me.clockTemplate = new ClockTemplate({
      scheduler
    });
  }

  doDestroy() {
    this.destroyProperties('clockTemplate', 'hoverTip');
    super.doDestroy();
  } //endregion
  //region Contents

  /**
   * @deprecated Use {@link #function-generateTipContent} instead.
   * Gets html to display in hover tooltip (tooltip displayed on empty parts of scheduler)
   * @private
   */

  getHoverTipHtml({
    tip,
    event
  }) {
    const me = this,
          scheduler = me.client,
          date = event && scheduler.getDateFromDomEvent(event, 'floor', true);
    let html = me.lastHtml;

    if (date) {
      const resourceRecord = scheduler.resolveResourceRecord(event); // resourceRecord might be null if user hover over the tooltip, but we shouldn't hide the tooltip in this case

      if (resourceRecord && (date - me.lastTime !== 0 || resourceRecord !== me.lastResource)) {
        me.lastResource = resourceRecord;
        html = me.lastHtml = me.generateTipContent({
          date,
          event,
          resourceRecord
        });
      }
    } else {
      tip.hide();
      me.lastTime = null;
      me.lastResource = null;
    }

    return html;
  }
  /**
   * Called as mouse pointer is moved over a new resource or time block. You can override this to show
   * custom HTML in the tooltip.
   * @param {Object} context
   * @param {Date} context.date The date of the hovered point
   * @param {Event} context.event The DOM event that triggered this tooltip to show
   * @param {Scheduler.model.ResourceModel} context.resourceRecord The resource record
   */

  generateTipContent({
    date,
    event,
    resourceRecord
  }) {
    const me = this,
          clockHtml = me.clockTemplate.generateContent({
      date: date,
      text: me.client.getFormattedDate(date)
    }),
          messageHtml = me.messageTemplate({
      message: me.getText(date, event, resourceRecord) || ''
    });
    me.lastTime = date;
    return clockHtml + messageHtml;
  }
  /**
   * Override this to render custom text to default hover tip
   * @param {Date} date
   * @param {Event} event Browser event
   * @param {Scheduler.model.ResourceModel} resourceRecord The resource record
   * @return {String}
   */

  getText(date, event, resourceRecord) {} //endregion

} // TODO: Refactor SASS so thet auto-generated class name of 'b-' + cls.name.toLowerCase() can be used.

ScheduleTooltip.featureClass = 'b-scheduletip';
ScheduleTooltip._$name = 'ScheduleTooltip';
GridFeatureManager.registerFeature(ScheduleTooltip, true, 'Scheduler');

/**
 * @module Scheduler/feature/SimpleEventEdit
 */

/**
 * Feature that displays a text field to edit the event name. You can control the flow of this by listening to the events relayed by this class from the underlying {@link Core.widget.Editor}.
 * To use this feature, you also need to disable the built-in default editing feature:
 *
 * ```javascript
 * const scheduler = new Scheduler({
 *     features : {
 *         eventEdit       : false,
 *         simpleEventEdit : true
 *     }
 * });
 * ```
 *
 * This feature is **off** by default. For info on enabling it, see {@link Grid.view.mixin.GridFeatures}.
 *
 * @extends Core/mixin/InstancePlugin
 * @demo Scheduler/simpleeditor
 * @externalexample scheduler/SimpleEventEdit.js
 * @classtype simpleEventEdit
 */

class SimpleEventEdit extends InstancePlugin {
  // region Events

  /**
   * Fired before the editor is shown to start an edit operation. Returning `false` from a handler vetoes the edit operation.
   * @event beforeStart
   * @preventable
   * @property {Object} value - The value to be edited.
   * @property {Core.widget.Editor} source - The Editor that triggered the event.
   */

  /**
   * Fired when an edit operation has begun.
   * @event start
   * @property {Object} value - The starting value of the field.
   * @property {Core.widget.Editor} source - The Editor that triggered the event.
   */

  /**
   * Fired when an edit completion has been requested, either by `ENTER`, or focus loss (if configured to complete on blur).
   * The completion may be vetoed, in which case, focus is moved back into the editor.
   * @event beforeComplete
   * @property {Object} oldValue - The original value.
   * @property {Object} value - The new value.
   * @property {Core.widget.Editor} source - The Editor that triggered the event.
   * @preventable
   */

  /**
   * Edit has been completed, and any associated record or element has been updated.
   * @event complete
   * @property {Object} oldValue - The original value.
   * @property {Object} value - The new value.
   * @property {Core.widget.Editor} source - The Editor that triggered the event.
   */

  /**
   * Fired when cancellation has been requested, either by `ESC`, or focus loss (if configured to cancel on blur).
   * The cancellation may be vetoed, in which case, focus is moved back into the editor.
   * @event beforeCancel
   * @property {Object} oldValue - The original value.
   * @property {Object} value - The new value.
   * @property {Core.widget.Editor} source - The Editor that triggered the event.
   * @preventable
   */

  /**
   * Edit has been canceled without updating the associated record or element.
   * @event cancel
   * @property {Object} oldValue - The original value.
   * @property {Object} value - The value of the field.
   * @property {Core.widget.Editor} source - The Editor that triggered the event.
   */
  // endregion
  //region Config
  static get $name() {
    return 'SimpleEventEdit';
  }

  static get defaultConfig() {
    return {
      /**
       * The event that shall trigger showing the editor. Defaults to `eventdblclick`, set to `` or null to disable editing of existing events.
       * @config {String}
       * @default
       * @category Editor
       */
      triggerEvent: 'eventdblclick',

      /**
       * The current {@link Scheduler.model.EventModel} record, which is being edited by the event editor.
       * @property {Scheduler.model.EventModel}
       * @readonly
       */
      eventRecord: null,

      /**
       * The {@link Scheduler.model.EventModel} field to edit
       * @config {String}
       * @category Editor
       */
      field: 'name',

      /**
       * The editor configuration, where you can control which widget to show
       * @config {Object}
       * @category Editor
       */
      editorConfig: null
    };
  }

  static get pluginConfig() {
    return {
      chain: ['onEventEnterKey'],
      assign: ['editEvent']
    };
  } //endregion
  //region Editing

  construct(scheduler, config) {
    const me = this;
    me.scheduler = scheduler;
    scheduler.eventEdit = me;
    super.construct(scheduler, config);
    me.clientListenersDetacher = scheduler.on({
      [me.triggerEvent]: ({
        eventRecord,
        eventElement
      }) => me.editEvent(eventRecord, eventRecord.resource, eventElement),
      dragcreateend: me.onDragCreateEnd,
      thisObj: me
    });
  }

  doDestroy() {
    var _this$editor;

    this.clientListenersDetacher();
    (_this$editor = this.editor) === null || _this$editor === void 0 ? void 0 : _this$editor.destroy();
    super.doDestroy();
  }

  get eventStore() {
    return this.scheduler.eventStore;
  } // region Editor

  /**
   * Opens an Editor for the passed event. This function is exposed on Scheduler and can be called as
   * `scheduler.editEvent()`.
   * @param {Scheduler.model.EventModel} eventRecord The Event to edit
   * @param {Scheduler.model.ResourceModel} [resourceRecord] The Resource record for the event.
   */

  editEvent(eventRecord, resourceRecord, element) {
    const me = this,
          scheduler = me.scheduler;

    if (scheduler.readOnly || me.disabled) {
      return;
    } // Want to put editor in inner element (b-sch-event) to get correct font size, but when drag creating the proxy
    // has no inner element

    element = DomHelper.down(element, scheduler.eventInnerSelector) || element;
    eventRecord = eventRecord.isAssignment ? eventRecord.event : eventRecord;
    me.resource = resourceRecord;
    me.event = eventRecord;
    me.element = element;
    scheduler.element.classList.add('b-eventeditor-editing');

    if (!me.editor) {
      // Editor is contained in, and owned by the TimeAxisSubGrid to avoid focus flipping out and in.
      // The editor is an owned descendant of the SubGrid.
      me.editor = new Editor(Object.assign({
        owner: me.scheduler.timeAxisSubGrid,
        appendTo: me.scheduler.timeAxisSubGridElement,
        scrollAction: 'realign',
        cls: 'b-simpleeventeditor',
        listeners: {
          complete: me.onEditorComplete,
          cancel: me.onEditorCancel,
          thisObj: me
        }
      }, me.editorConfig));
      me.relayEvents(me.editor, ['beforestart', 'start', 'beforecomplete', 'complete', 'beforecancel', 'cancel']);
    }

    me.editor.startEdit({
      target: element,
      record: eventRecord,
      field: me.field
    }); // No key navigation during editing

    me.scheduler.navigator.disabled = true;
  }

  onEditorComplete() {
    var _me$element;

    const me = this;

    if (me.event.stores.length === 0) {
      me.eventStore.add(me.editor.record);
      me.event.assign(me.resource);
    } // remove element if it's a drag create proxy

    if ((_me$element = me.element) !== null && _me$element !== void 0 && _me$element.classList.contains('b-sch-dragcreator-proxy')) {
      me.element.remove();
    }

    me.reset();
  }

  onEditorCancel() {
    if (this.event.stores.length === 0) {
      this.element.remove();
    }

    this.reset();
  }

  reset() {
    this.scheduler.element.classList.remove('b-eventeditor-editing'); // Restore key navigation after editing

    this.scheduler.navigator.disabled = false;
  } //endregion
  // chained from EventNavigation

  onEventEnterKey({
    assignmentRecord,
    eventRecord
  }) {
    const element = assignmentRecord ? this.scheduler.getElementFromAssignmentRecord(assignmentRecord) : this.scheduler.getElementFromEventRecord(eventRecord),
          resourceRecord = (assignmentRecord || eventRecord).resource;
    this.editEvent(eventRecord, resourceRecord, element);
  } //endregion

  onDragCreateEnd({
    newEventRecord,
    resourceRecord,
    proxyElement
  }) {
    const me = this; // Call scheduler template method

    me.scheduler.onEventCreated(newEventRecord); // Clone proxy after showing editor so it's not deleted

    const phantomEventElement = proxyElement.cloneNode(true);
    phantomEventElement.removeAttribute('id');
    proxyElement.parentElement.appendChild(phantomEventElement);
    me.element = phantomEventElement;
    me.editEvent(newEventRecord, resourceRecord, phantomEventElement);
  }

}
SimpleEventEdit._$name = 'SimpleEventEdit';
GridFeatureManager.registerFeature(SimpleEventEdit, false, 'Scheduler');

/**
 * @module Grid/feature/Summary
 */

/**
 * Displays a summary row in the grid footer. Specify type of summary on columns, available types are:
 * <dl class="wide">
 * <dt>sum <dd>Sum of all values in the column
 * <dt>add <dd>Alias for sum
 * <dt>count <dd>Number of rows
 * <dt>countNotEmpty <dd>Number of rows containing a value
 * <dt>average <dd>Average of all values in the column
 * <dt>function <dd>A custom function, used with store.reduce. Should take arguments (sum, record)
 * </dl>
 * Columns can also specify a summaryRenderer to format the calculated sum.
 *
 * This feature is <strong>disabled</strong> by default.
 *
 * ```
 * { text : 'Score', data : 'score', width : 80, sum : true }
 * { text : 'Rank', data : 'rank', width : 80, sum : 'average', summaryRenderer: sum => return 'Average rank ' + sum }
 * ```
 *
 * Also it is possible to set up multiple summaries as array of summary configs:
 * ```
 * { text : 'Rank', data : 'rank', summaries : [{ sum : 'average', label : 'Average' }, { sum : 'count', label : 'Count' }] }
 * ```
 *
 * @extends Core/mixin/InstancePlugin
 *
 * @demo Grid/features
 * @classtype summary
 * @externalexample feature/Summary.js
 */

class Summary extends SummaryFormatter(InstancePlugin) {
  //region Init
  static get $name() {
    return 'Summary';
  }

  construct(grid, config) {
    this.grid = grid;
    super.construct(grid, config);
    this.bindStore(grid.store);
    grid.hideFooters = false;
  }

  bindStore(store) {
    this.detachListeners('store');
    store.on({
      name: 'store',
      change: 'onStoreChange',
      thisObj: this
    });
  }

  get store() {
    return this.grid.store;
  }

  doDestroy() {
    super.doDestroy();
  }

  doDisable(disable) {
    super.doDisable(disable);

    if (disable) {
      this.grid.element.classList.add('b-summary-disabled');
    } else {
      this.updateSummaries();
      this.grid.element.classList.remove('b-summary-disabled');
    }
  } //endregion
  //region Plugin config
  // Plugin configuration. This plugin chains some of the functions in Grid.

  static get pluginConfig() {
    return {
      chain: ['renderRows']
    };
  } //endregion
  //region Render

  renderRows() {
    this.updateSummaries();
  }
  /**
   * Updates summaries. Summaries are displayed as tables in footer (styling left out to keep brief):
   * ```
   * <table>
   *     <tr><td colspan="2">0</td></tr> // { sum : 'min' } Only a calculation, span entire table
   *     <tr><td>Max</td><td>10</td></tr> // { sum : 'max', label: 'Max' } Label + calculation
   *     <tr><td>Max</td><td>10</td></tr> // { sum : 'sum', label: 'Max' } Label + calculation
   * </table>
   * ```
   * @private
   */

  updateSummaries() {
    const me = this,
          {
      grid,
      store
    } = me,
          cells = DomHelper.children(grid.element, '.b-grid-footer'),
          records = store.storage.values.filter(r => !r.isSpecialRow); // reset seeds, to not have ever increasing sums :)

    grid.columns.forEach(column => {
      column.summaries && column.summaries.forEach(config => {
        if ('seed' in config) {
          if (!('initialSeed' in config)) {
            config.initialSeed = config.seed;
          }

          if (['number', 'string', 'date'].includes(typeof config.initialSeed)) {
            config.seed = config.initialSeed;
          } else {
            // create shallow copy
            config.seed = Object.assign({}, config.initialSeed);
          }
        }
      });
    });
    cells.forEach(cellElement => {
      // Skip for special columns like checkbox selection
      if (!cellElement.dataset.column) {
        return;
      }

      const column = grid.columns.get(cellElement.dataset.column),
            html = me.generateHtml(column, records, 'b-grid-footer-summary');

      if (column.summaries ? column.summaries.length : column.sum ? 1 : 0) {
        // First time, set table
        if (!cellElement.children.length) {
          cellElement.innerHTML = html;
        } // Following times, sync changes
        else {
            DomHelper.sync(html, cellElement.firstElementChild);
          }
      }
    });
  } //endregion
  //region Events

  /**
   * Updates summaries on store changes (except record update, handled below)
   * @private
   */

  onStoreChange({
    action,
    changes
  }) {
    let shouldUpdate = true;

    if (this.disabled) {
      return;
    }

    if (action === 'update') {
      // only update summary when a field that affects summary is changed
      // TODO: this should maybe be removed, another column might depend on the value for its summary?
      shouldUpdate = Object.keys(changes).some(field => {
        const colField = this.grid.columns.get(field); // check existence, since a field not used in a column might have changed

        return Boolean(colField) && (Boolean(colField.sum) || Boolean(colField.summaries));
      });
    }

    if (shouldUpdate) {
      this.updateSummaries();
    }
  } //endregion

}
Summary.featureClass = 'b-summary';
Summary._$name = 'Summary';
GridFeatureManager.registerFeature(Summary);

/**
 * @module Scheduler/feature/Summary
 */
// noinspection JSClosureCompilerSyntax

/**
 * A special version of the Grid Summary feature. This feature displays a summary row in the grid footer.
 * For regular columns in the locked section - specify type of summary on columns, available types are:
 * <dl class="wide">
 * <dt>sum <dd>Sum of all values in the column
 * <dt>add <dd>Alias for sum
 * <dt>count <dd>Number of rows
 * <dt>countNotEmpty <dd>Number of rows containing a value
 * <dt>average <dd>Average of all values in the column
 * <dt>function <dd>A custom function, used with store.reduce. Should take arguments (sum, record)
 * </dl>
 * Columns can also specify a summaryRender to format the calculated sum.
 *
 * To summarize events, either provide a {@link #config-renderer} or use {@link #config-summaries}
 *
 * This feature is <strong>disabled</strong> by default. It is **not** supported in vertical mode.
 *
 * @extends Grid/feature/Summary
 * @classtype summary
 * @externalexample scheduler/Summary.js
 * @demo Scheduler/summary
 * @typings Grid/feature/Summary -> Grid/feature/GridSummary
 */

class Summary$1 extends Summary {
  //region Config
  static get $name() {
    return 'Summary';
  }

  static get defaultConfig() {
    return {
      /**
       * Show tooltip containing summary values and labels
       * @config {Boolean}
       * @default
       */
      showTooltip: true,

      /**
       * Array of summary configs which consists of a label and a {@link #config-renderer} function
       *
       * ```javascript
       * new Scheduler({
       *     features : {
       *         summary : {
       *             summaries : [
       *                 {
       *                     label : 'Label',
       *                     renderer : ({ startDate, endDate, eventStore, resourceStore, events, element }) => {
       *                         // return display value
       *                         returns '<div>Renderer output</div>';
       *                     }
       *                 }
       *             ]
       *         }
       *     }
       * });
       * ```
       *
       * @config {Object[]}
       */
      summaries: null,

      /**
       * Renderer function for a single time axis tick. Should calculate a sum and return HTML as a result.
       *
       * ```javascript
       * new Scheduler({
       *     features : {
       *         summary : {
       *             renderer : ({ startDate, endDate, eventStore, resourceStore, events, element }) => {
       *                 // return display value
       *                 returns '<div>Renderer output</div>';
       *             }
       *         }
       *     }
       * });
       * ```
       *
       * @param {Date} startDate Tick start date
       * @param {Date} endDate Tick end date
       * @param {Scheduler.model.EventModel[]} events Events which belong to the group
       * @param {Scheduler.data.EventStore} eventStore Event store
       * @param {Scheduler.data.ResourceStore} resourceStore Resource store
       * @param {HTMLElement} element Summary tick container
       * @returns {String} Html content
       * @config {Function}
       */
      renderer: null
    };
  } //endregion
  //region Init

  construct(scheduler, config) {
    const me = this;

    if (scheduler.isVertical) {
      throw new Error('Summary feature is not supported in vertical mode');
    }

    me.scheduler = scheduler;
    super.construct(scheduler, config);

    if (!me.summaries) {
      me.summaries = [{
        renderer: me.renderer
      }];
    } // Feature might be run from Grid (in docs), should not crash
    // https://app.assembla.com/spaces/bryntum/tickets/6801/details

    if (scheduler instanceof SchedulerBase) {
      scheduler.eventStore.on({
        filter: 'updateScheduleSummaries',
        update: 'updateScheduleSummaries',
        thisObj: me
      });
      scheduler.resourceStore.on({
        filter: 'updateScheduleSummaries',
        thisObj: me
      });
      scheduler.project.on({
        refresh: 'updateScheduleSummaries',
        thisObj: me
      });
      scheduler.timeAxisViewModel.on({
        update: me.renderRows,
        thisObj: me
      });
    }
  } //endregion
  //region Render

  renderRows() {
    super.renderRows();
    this.render();
  }
  /**
   * Updates summaries.
   * @private
   */

  updateScheduleSummaries() {
    const me = this,
          scheduler = me.scheduler,
          summaryContainer = scheduler.bodyElement.querySelector('.b-grid-footer.b-sch-timeaxiscolumn');

    if (summaryContainer && scheduler.isEngineReady) {
      Array.from(summaryContainer.children).forEach((element, i) => {
        const tick = scheduler.timeAxis.getAt(i),
              events = scheduler.eventStore.getEvents({
          startDate: tick.startDate,
          endDate: tick.endDate,
          allowPartial: true,
          onlyAssigned: true
        });
        let html = '',
            tipHtml = `<header>${me.L('L{Summary for}', scheduler.getFormattedDate(tick.startDate))}</header>`;
        me.summaries.forEach(config => {
          const value = config.renderer({
            startDate: tick.startDate,
            endDate: tick.endDate,
            eventStore: scheduler.eventStore,
            resourceStore: scheduler.resourceStore,
            events,
            element
          }),
                valueHtml = `<div class="b-timeaxis-summary-value">${value}</div>`;
          if (me.summaries.length > 1 || value !== '') html += valueHtml;
          tipHtml += `<label>${config.label || ''}</label>` + valueHtml;
        });
        element.innerHTML = html;
        element._tipHtml = tipHtml;
      });
    }
  }

  render() {
    const me = this,
          scheduler = me.scheduler,
          colCfg = scheduler.timeAxisViewModel.columnConfig,
          summaryEl = scheduler.element.querySelector('.b-grid-footer.b-sch-timeaxiscolumn');

    if (summaryEl) {
      // if any sum config has a label, init tooltip
      if (me.summaries.some(config => config.label) && me.showTooltip && !me._tip) {
        me._tip = new Tooltip({
          id: `${scheduler.id}-summary-tip`,
          cls: 'b-timeaxis-summary-tip',
          hoverDelay: 0,
          hideDelay: 100,
          forElement: summaryEl,
          anchorToTarget: true,
          trackMouse: false,
          forSelector: '.b-timeaxis-tick',
          getHtml: ({
            activeTarget
          }) => activeTarget._tipHtml
        });
      }

      summaryEl.innerHTML = colCfg[colCfg.length - 1].map(col => `<div class="b-timeaxis-tick" style="width: ${col.width}px"></div>`).join('');
      me.updateScheduleSummaries();
    }
  } //endregion

} // Override Grids Summary with this improved version

Summary$1._$name = 'Summary';
GridFeatureManager.registerFeature(Summary$1, false, 'Scheduler');

/**
 * @module Scheduler/feature/TimeAxisHeaderMenu
 */

/**
 * Adds scheduler specific menu items to the timeline header context menu.
 *
 * ## Default timeaxis header menu items
 *
 * Here is the list of menu items provided by this and other features:
 *
 * | Reference          | Text                  | Weight | Feature                                           | Description                  |
 * |--------------------|-----------------------|--------|---------------------------------------------------|------------------------------|
 * | `eventsFilter`     | Filter tasks          | 100    | {@link Scheduler.feature.EventFilter EventFilter} | Submenu for event filtering  |
 * | \>`nameFilter`     | By name               | 110    | {@link Scheduler.feature.EventFilter EventFilter} | Filter by `name`             |
 * | `zoomLevel`        | Zoom                  | 200    | *This feature*                                    | Submenu for timeline zooming |
 * | \>`zoomSlider`     | -                     | 210    | *This feature*                                    | Changes current zoom level   |
 * | `dateRange`        | Date range            | 300    | *This feature*                                    | Submenu for timeline range   |
 * | \>`startDateField` | Start date            | 310    | *This feature*                                    | Start date for the timeline  |
 * | \>`endDateField`   | End date              | 320    | *This feature*                                    | End date for the timeline    |
 * | \>`leftShiftBtn`   | <                     | 330    | *This feature*                                    | Shift backward               |
 * | \>`todayBtn`       | Today                 | 340    | *This feature*                                    | Go to today                  |
 * | \>`rightShiftBtn`  | \>                    | 350    | *This feature*                                    | Shift forward                |
 * | `currentTimeLine`  | Show current timeline | 400    | {@link Scheduler.feature.TimeRanges TimeRanges}   | Show current time line       |
 *
 * \> - first level of submenu
 *
 * ## Customizing the menu items
 *
 * The menu items in the TimeAxis Header menu can be customized, existing items can be changed or removed,
 * and new items can be added. This is handled using the `items` config of the feature.
 *
 * ### Add extra items:
 *
 * ```javascript
 * const scheduler = new Scheduler({
 *     features : {
 *         timeAxisHeaderMenu : {
 *             items : {
 *                 extraItem : {
 *                     text : 'Extra',
 *                     icon : 'b-fa b-fa-fw b-fa-flag',
 *                     onItem() {
 *                         ...
 *                     }
 *                 }
 *             }
 *         }
 *     }
 * });
 * ```
 *
 * ### Remove existing items:
 *
 * ```javascript
 * const scheduler = new Scheduler({
 *     features : {
 *         timeAxisHeaderMenu : {
 *             items : {
 *                 zoomLevel : false
 *             }
 *         }
 *     }
 * });
 * ```
 *
 * ### Customize existing item:
 *
 * ```javascript
 * const scheduler = new Scheduler({
 *     features : {
 *         timeAxisHeaderMenu : {
 *             items : {
 *                 zoomLevel : {
 *                     text : 'Scale'
 *                 }
 *             }
 *         }
 *     }
 * });
 * ```
 * ### Customizing submenu items:
 *
 * ```
 * const scheduler = new Scheduler({
 *      features : {
 *          timeAxisHeaderMenu : {
 *              items : {
 *                  dateRange : {
 *                      menu : {
 *                          items : {
 *                              todayBtn : {
 *                                  text : 'Now'
 *                              }
 *                          }
 *                      }
 *                  }
 *              }
 *          }
 *      }
 * });
 * ```
 *
 * ### Manipulate existing items:
 *
 * ```javascript
 * const scheduler = new Scheduler({
 *     features : {
 *         timeAxisHeaderMenu : {
 *             // Process items before menu is shown
 *             processItems({ items }) {
 *                  // Add an extra item dynamically
 *                 items.coolItem = {
 *                     text : 'Cool action',
 *                     onItem() {
 *                           // ...
 *                     }
 *                 }
 *             }
 *         }
 *     }
 * });
 * ```
 *
 * Full information of the menu customization can be found in the ["Customizing the Event menu, the Schedule menu, and the TimeAxisHeader menu"](#guides/customization/contextmenu.md)
 * guide.
 *
 * This feature is **enabled** by default
 *
 * @extends Grid/feature/HeaderMenu
 * @demo Scheduler/basic
 * @classtype timeAxisHeaderMenu
 * @externalexample scheduler/TimeAxisHeaderMenu.js
 */

class TimeAxisHeaderMenu extends HeaderMenu {
  //region Config
  static get $name() {
    return 'TimeAxisHeaderMenu';
  }

  static get defaultConfig() {
    return {
      /**
       * A function called before displaying the menu that allows manipulations of its items.
       * Returning `false` from this function prevents the menu being shown.
       *
       * ```javascript
       *   features         : {
       *       timeAxisHeaderMenu : {
       *           processItems({ items }) {
       *               // Add or hide existing items here as needed
       *               items.myAction = {
       *                   text   : 'Cool action',
       *                   icon   : 'b-fa b-fa-fw b-fa-ban',
       *                   onItem : () => console.log('Some coolness'),
       *                   weight : 300 // Move to end
       *               };
       *
       *               // Hide zoom slider
       *               items.zoomLevel.hidden = true;
       *           }
       *       }
       *   },
       * ```
       *
       * @param {Object} context An object with information about the menu being shown
       * @param {Object} context.items An object containing the {@link Core.widget.MenuItem menu item} configs keyed by their id
       * @param {Event} context.event The DOM event object that triggered the show
       * @config {Function}
       * @preventable
       */
      processItems: null,

      /**
       * This is a preconfigured set of items used to create the default context menu.
       *
       * The `items` provided by this feature are listed in the intro section of this class. You can
       * configure existing items by passing a configuration object to the keyed items.
       *
       * To remove existing
       * items, set corresponding keys to `false`
       *
       * ```javascript
       * const scheduler = new Scheduler({
       *     features : {
       *         timeAxisHeaderMenu : {
       *             items : {
       *                 eventsFilter        : false
       *             }
       *         }
       *     }
       * });
       * ```
       *
       * See the feature config in the above example for details.
       *
       * @config {Object} items
       */
      items: null,
      type: 'timeAxisHeader'
    };
  }

  static get pluginConfig() {
    const config = super.pluginConfig;
    config.chain.push('populateTimeAxisHeaderMenu');
    return config;
  } //endregion
  //region Events

  /**
   * Fired from Scheduler before the context menu is shown for the time axis header.
   * Allows manipulation of the items to show in the same way as in the {@link #config-processItems}.
   *
   * Returning `false` from a listener prevents the menu from being shown.
   *
   * @event timeAxisHeaderContextMenuBeforeShow
   * @preventable
   * @param {Scheduler.view.Scheduler} source The scheduler
   * @param {Core.widget.Menu} menu The menu
   * @param {Object} items Menu item configs
   * @param {Grid.column.Column} column Time axis column
   */

  /**
   * Fired from grid after the context menu is shown for a header
   * @event timeAxisHeaderContextMenuShow
   * @param {Scheduler.view.Scheduler} source The scheduler
   * @param {Core.widget.Menu} menu The menu
   * @param {Object} items Menu item configs
   * @param {Grid.column.Column} column Time axis column
   */

  /**
   * Fired from grid when an item is selected in the header context menu.
   * @event timeAxisHeaderContextMenuItem
   * @param {Scheduler.view.Scheduler} source The scheduler
   * @param {Core.widget.Menu} menu The menu
   * @param {Object} item Selected menu item
   * @param {Grid.column.Column} column Time axis column
   */
  //endregion

  construct() {
    super.construct(...arguments);

    if (this.triggerEvent.includes('click') && this.client.zoomOnTimeAxisDoubleClick) {
      this.client.zoomOnTimeAxisDoubleClick = false;
    }
  }

  shouldShowMenu(eventParams) {
    const {
      column
    } = eventParams;
    return column && column.enableHeaderContextMenu !== false && column === this.client.timeAxisColumn;
  }

  showContextMenu(eventParams) {
    super.showContextMenu(...arguments);

    if (this.menu) {
      // the TimeAxis's context menu probably will cause scrolls because it manipulates the dates.
      // The menu should not hide on scroll when for a TimeAxisColumn
      this.menu.scrollAction = 'realign';
    }
  }

  populateTimeAxisHeaderMenu({
    items
  }) {
    const me = this,
          {
      client
    } = me,
          dateStep = {
      magnitude: client.timeAxis.shiftIncrement,
      unit: client.timeAxis.shiftUnit
    };
    Object.assign(items, {
      zoomLevel: {
        text: 'L{pickZoomLevel}',
        localeClass: me,
        icon: 'b-fw-icon b-icon-search-plus',
        disabled: !client.presets.count || me.disabled,
        weight: 200,
        menu: {
          type: 'popup',
          items: {
            zoomSlider: {
              weight: 210,
              type: 'slider',
              showValue: false,
              listeners: {
                input: me.onZoomSliderChange,
                thisObj: me
              },
              // set width for IE11
              minWidth: 130
            }
          },

          onBeforeShow({
            source: menu
          }) {
            const [zoom] = menu.items;
            zoom.min = client.minZoomLevel;
            zoom.max = client.maxZoomLevel;
            zoom.value = client.zoomLevel;
          }

        }
      },
      dateRange: {
        text: 'L{activeDateRange}',
        localeClass: me,
        icon: 'b-fw-icon b-icon-calendar',
        weight: 300,
        menu: {
          type: 'popup',
          width: '20em',
          defaults: {
            localeClass: me
          },
          items: {
            startDateField: {
              type: 'datefield',
              label: 'L{startText}',
              weight: 310,
              labelWidth: '6em',
              required: true,
              step: dateStep,
              listeners: {
                change: me.onRangeDateFieldChange,
                thisObj: me
              }
            },
            endDateField: {
              type: 'datefield',
              label: 'L{endText}',
              weight: 320,
              labelWidth: '6em',
              required: true,
              step: dateStep,
              listeners: {
                change: me.onRangeDateFieldChange,
                thisObj: me
              }
            },
            leftShiftBtn: {
              type: 'button',
              weight: 330,
              cls: 'b-left-nav-btn',
              icon: 'b-icon b-icon-prev',
              color: 'b-blue b-raised',
              flex: 1,
              margin: 0,
              listeners: {
                click: me.onLeftShiftBtnClick,
                thisObj: me
              }
            },
            todayBtn: {
              type: 'button',
              weight: 340,
              cls: 'b-today-nav-btn',
              color: 'b-blue b-raised',
              text: 'L{todayText}',
              flex: 4,
              margin: '0 8',
              listeners: {
                click: me.onTodayBtnClick,
                thisObj: me
              }
            },
            rightShiftBtn: {
              type: 'button',
              weight: 350,
              cls: 'b-right-nav-btn',
              icon: 'b-icon b-icon-next',
              color: 'b-blue b-raised',
              flex: 1,
              listeners: {
                click: me.onRightShiftBtnClick,
                thisObj: me
              }
            }
          },
          listeners: {
            paint: me.initDateRangeFields,
            thisObj: me
          }
        }
      }
    });
  }

  onZoomSliderChange({
    value
  }) {
    const me = this; // Zooming maintains timeline center point by scrolling the newly rerendered timeline to the
    // correct point to maintain the visual center. Temporarily inhibit context menu hide on scroll
    // of its context element.

    me.menu.scrollAction = 'realign';
    me.client.zoomLevel = value;
    me.menu.setTimeout({
      fn: () => me.menu.scrollAction = 'hide',
      delay: 100,
      cancelOutstanding: true
    });
  }

  initDateRangeFields({
    source: dateRange,
    firstPaint
  }) {
    if (firstPaint) {
      const {
        widgetMap
      } = dateRange;
      this.startDateField = widgetMap.startDateField;
      this.endDateField = widgetMap.endDateField;
    }

    this.initDates();
  }

  initDates() {
    const me = this;
    me.startDateField.suspendEvents();
    me.endDateField.suspendEvents(); // The actual scheduler start dates may include time, but our Date field cannot currently handle
    // a time portion and throws it away, so when we need the value from an unchanged field, we need
    // to use the initialValue set from the timeAxis values.
    // Until our DateField can optionally include a time value, this is the solution.

    me.startDateField.value = me.startDateFieldInitialValue = me.client.startDate;
    me.endDateField.value = me.endDateFieldInitialValue = me.client.endDate;
    me.startDateField.resumeEvents();
    me.endDateField.resumeEvents();
  }

  onRangeDateFieldChange({
    source
  }) {
    const me = this,
          startDateChanged = source === me.startDateField,
          {
      client
    } = me,
          {
      timeAxis
    } = client,
          startDate = me.startDateFieldInitialValue && !startDateChanged ? me.startDateFieldInitialValue : me.startDateField.value;
    let endDate = me.endDateFieldInitialValue && startDateChanged ? me.endDateFieldInitialValue : me.endDateField.value; // When either of the fields is changed, we no longer use its initialValue from the timeAxis start or end
    // so that gets nulled to indicate that it's unavailable and the real field value is to be used.

    if (startDateChanged) {
      me.startDateFieldInitialValue = null;
    } else {
      me.endDateFieldInitialValue = null;
    } // Because the start and end dates are exclusive, avoid a zero
    // length time axis by incrementing the end by one tick unit
    // if they are the same.

    if (!(endDate - startDate)) {
      endDate = DateHelper.add(endDate, timeAxis.shiftIncrement, timeAxis.shiftUnit);
    } // if start date got bigger than end date set end date to start date plus one tick
    else if (endDate < startDate) {
        endDate = DateHelper.add(startDate, timeAxis.shiftIncrement, timeAxis.shiftUnit);
      }

    client.preserveViewCenter(() => client.timeAxis.setTimeSpan(startDate, endDate));
    me.initDates();
  }

  onLeftShiftBtnClick() {
    this.client.timeAxis.shiftPrevious();
    this.initDates();
  }

  onTodayBtnClick() {
    const today = DateHelper.clearTime(new Date());
    this.client.timeAxis.setTimeSpan(today, DateHelper.add(today, 1, 'day'));
    this.initDates();
  }

  onRightShiftBtnClick() {
    this.client.timeAxis.shiftNext();
    this.initDates();
  }

}
TimeAxisHeaderMenu._$name = 'TimeAxisHeaderMenu';
GridFeatureManager.registerFeature(TimeAxisHeaderMenu, true, ['Scheduler', 'Gantt']);

/**
 * @module Scheduler/feature/TimeRanges
 */

/**
 * Feature that renders global ranges of time in the timeline. Use this feature to visualize a `range` like a 1 hr lunch or some important point in time
 * (a `line`, i.e. a range with 0 duration). This feature can also show a current time indicator if you set {@link #config-showCurrentTimeLine} to true. To style
 * the rendered elements, use the {@link Scheduler.model.TimeSpan#field-cls cls} field of the `TimeSpan` class.
 *
 * Each time range is represented by an instances of {@link Scheduler.model.TimeSpan}, held in a simple {@link Core.data.Store}.
 * If {@link Scheduler.data.CrudManager} is specified for the scheduler, the {@link #config-store} will be added at the end of the CrudManager
 * {@link Scheduler.crud.AbstractCrudManager#property-stores} collection.
 *
 * This feature is **off** by default. For info on enabling it, see {@link Grid.view.mixin.GridFeatures}.
 *
 * @extends Scheduler/feature/AbstractTimeRanges
 * @classtype timeRanges
 * @demo Scheduler/timeranges
 * @externalexample scheduler/TimeRanges.js
 */

class TimeRanges extends AbstractTimeRanges {
  //region Config
  static get $name() {
    return 'TimeRanges';
  }

  static get defaultConfig() {
    return {
      /**
       * Store that holds timeRanges (using the {@link Scheduler.model.TimeSpan} model or subclass thereof).
       * A store will be automatically created if none is specified
       * @config {Object|Core.data.Store}
       */
      store: {
        modelClass: TimeSpan,
        storeId: 'timeRanges'
      },
      updateCurrentTimeLineInterval: 10000,

      /**
       * The date format to show in the header for the current time line, see {@link #config-showCurrentTimeLine}
       * @config {String}
       * @default
       */
      currentDateFormat: 'HH:mm',

      /**
       * Range definitions (data to {@link Scheduler.model.TimeSpan} models). Will be added to store.
       * @config {Scheduler.model.TimeSpan[]|Object[]}
       */
      timeRanges: null,

      /**
       * Show a line indicating current time. Either `true` or `false` or a {@link Scheduler.model.TimeSpan} configuration object to apply to this
       * special time range (allowing you to provide a custom text):
       *
       * ```javascript
       * showCurrentTimeLine : {
       *     name : 'Now'
       * }
       * ```
       *
       * @config {Boolean|Object}
       * @default
       */
      showCurrentTimeLine: false
    };
  } //endregion
  //region Init & destroy

  construct(client, config) {
    const me = this;
    super.construct(client, config);

    if (!client._timeRangesExposed) {
      // expose getter/setter for timeRanges on scheduler/gantt
      Object.defineProperty(client, 'timeRanges', {
        get: () => me.timeRanges,
        set: timeRanges => me.store.data = timeRanges
      });
      client._timeRangesExposed = true;
    } // Pull in the timeRanges config before the store config if it exists.

    me.getConfig('timeRanges');
  }

  startConfigure(config) {
    const {
      client
    } = this; // If the client's project has a timeRangeStore, we must use that
    // unless we've configured feature with a store (e.g. with recurrence mixin)

    if (client.project && !this.initialConfig.store) {
      const store = client.project.timeRangeStore;

      if (store) {
        const data = ObjectHelper.getPath(config, 'store.data');

        if (data) {
          store.data = data;
        }

        config.store = store;
      }
    }
  }

  set timeRanges(timeRanges) {
    const store = this.getConfig('store');

    if (store) {
      store.data = timeRanges;
    } else {
      this._timeRanges = timeRanges;
    }
  }
  /**
   * Returns the TimeRanges which occur within the client Scheduler's time axis.
   * @property {Scheduler.model.TimeSpan[]}
   */

  get timeRanges() {
    const {
      store
    } = this,
          {
      records
    } = store;

    if (store.recurringEvents) {
      const result = [],
            {
        startDate,
        endDate
      } = this.client.timeAxis; // Collect occurrences for the recurring events in the record set

      records.forEach(timeSpan => {
        if (timeSpan.isRecurring) {
          result.push.apply(result, timeSpan.getOccurrencesForDateRange(startDate, endDate));
        } else {
          result.push(timeSpan);
        }
      });
      return result;
    } else {
      return records;
    }
  } //endregion
  //region Current time line

  initCurrentTimeLine() {
    const me = this;

    if (me.currentTimeLine || !me.showCurrentTimeLine) {
      return;
    }

    const data = typeof me.showCurrentTimeLine === 'object' ? me.showCurrentTimeLine : {};
    me.currentTimeLine = new me.store.modelClass(ObjectHelper.assign({
      // eslint-disable-next-line quote-props
      'id': 'currentTime',
      cls: 'b-sch-current-time'
    }, data));
    me.updateCurrentTimeLine = me.updateCurrentTimeLine.bind(me);
    me.currentTimeInterval = me.setInterval(me.updateCurrentTimeLine, me.updateCurrentTimeLineInterval);
    me.updateCurrentTimeLine();

    if (me.client.isPainted) {
      me.renderRanges();
    }
  }

  updateCurrentTimeLine() {
    const me = this,
          currentTimeRecord = me.currentTimeLine;
    currentTimeRecord.startDate = new Date();

    if (!currentTimeRecord.originalData.name) {
      me.currentTimeLine.name = DateHelper.format(me.currentTimeLine.startDate, me.currentDateFormat);
    }

    me.onStoreChanged({
      action: 'update',
      record: me.currentTimeLine,
      changes: {}
    });
  }

  hideCurrentTimeLine() {
    const me = this;

    if (!me.currentTimeLine) {
      return;
    }

    me.clearInterval(me.currentTimeInterval);
    me.currentTimeLine = null;

    if (me.client.isPainted) {
      me.renderRanges();
    }
  }

  renderRanges() {
    const me = this;
    super.renderRanges();

    if (me.showCurrentTimeLine && !me.disabled) {
      me.renderRange(me.currentTimeLine, true);
    }
  }
  /**
   * Get/set the current time line display state
   * @property {boolean}
   */

  get showCurrentTimeLine() {
    return this._showCurrentTimeLine;
  }

  set showCurrentTimeLine(show) {
    this._showCurrentTimeLine = show;

    if (show) {
      this.initCurrentTimeLine();
    } else {
      this.hideCurrentTimeLine();
    }
  } //endregion
  //region Menu items
  // TODO: 'headerContextMenu' is deprecated. Please see https://bryntum.com/docs/scheduler/#guides/upgrades/4.0.0.md for more information.

  /**
   * Adds a menu item to show/hide current time line.
   * @param {Object} options Contains menu items and extra data retrieved from the menu target.
   * @param {Grid.column.Column} options.column Column for which the menu will be shown
   * @param {Object} options.items A named object to describe menu items
   * @deprecated 4.0.0 Use `populateTimeAxisHeaderMenu` instead
   */

  populateHeaderMenu({
    column,
    items
  }) {
    if (column.type !== 'timeAxis') {
      return;
    }

    this.populateTimeAxisHeaderMenu(...arguments);
  }
  /**
   * Adds a menu item to show/hide current time line.
   * @param {Object} options Contains menu items and extra data retrieved from the menu target.
   * @param {Grid.column.Column} options.column Column for which the menu will be shown
   * @param {Object} options.items A named object to describe menu items
   * @internal
   */

  populateTimeAxisHeaderMenu({
    column,
    items
  }) {
    const me = this;
    items.currentTimeLine = {
      weight: 400,
      text: me.L('L{showCurrentTimeLine}'),
      checked: me.showCurrentTimeLine,
      onToggle: ({
        checked
      }) => {
        me.showCurrentTimeLine = checked;
      }
    };
  } //endregion
  //region Disable

  /**
   * Get/set the features disabled state
   * @property {Boolean}
   */

  get disabled() {
    return this._disabled;
  }

  set disabled(disabled) {
    this._disabled = disabled;

    if (this.client.isPainted) {
      this.renderRanges();
    }
  }
  /**
   * Returns the {@link Core.data.Store store} used by this feature
   * @property {Core.data.Store}
   */

  get store() {
    return this._store;
  }

  set store(store) {
    const me = this,
          client = me.client;

    if (!store.storeId) {
      store.storeId = 'timeRanges';
    }

    super.store = store; // timeRanges can be set on scheduler/gantt or feature, for convenience. Should only be processed by the TimeRanges
    // and not any subclasses

    if (client.timeRanges && !client._timeRangesExposed) {
      me._store.add(client.timeRanges);

      delete client.timeRanges;
    }

    if (me.timeRanges) {
      me._store.add(me.timeRanges);

      delete me.timeRanges;
    }
  } //endregion

}
TimeRanges._$name = 'TimeRanges';
GridFeatureManager.registerFeature(TimeRanges, false, ['Scheduler', 'Gantt']);

/**
 * @module Scheduler/feature/base/TimeSpanRecordContextMenuBase
 */
// This is a version of what Containers do, except that we have to apply our namedItems
// all the way down any configured menu hierarchy, and the resulting structure must
// be available *before* menu instantiation for the processItems method to interrogate.

function applyNamedItems(items, namedItems) {
  for (const ref in items) {
    let item = items[ref];

    if (item) {
      if (ref in namedItems) {
        item = items[ref] = typeof item === 'object' ? ObjectHelper.merge(ObjectHelper.clone(namedItems[ref]), item) : namedItems[ref];
      } // Our namedItems must apply all the way down any descendant menus.
      // Extract menu here because it may have been applied by a namedItem.

      const menu = item.menu;

      if (menu) {
        applyNamedItems('items' in menu ? menu.items : menu, namedItems);
      }
    }
  }
}
/**
 * DEPRECATED: This class is deprecated since v4.0.0.
 * It is **not** used by our features anymore and remains here only for backward compatibility.
 * If you already extended this class please re-implement it utilizing our new
 * {@link Scheduler.feature.base.TimeSpanMenuBase } abstract base class.
 *
 * @extends Core/mixin/InstancePlugin
 * @abstract
 * @deprecated 4.0.0
 */

class TimeSpanRecordContextMenuBase extends InstancePlugin {
  //region Config
  static get defaultConfig() {
    return {
      /**
       * This is a preconfigured set of {@link Core.widget.Container#config-namedItems} used to create the default
       * context menu.
       * @config {Object}
       */
      defaultItems: null,

      /**
       * An {@link Core.widget.Menu Menu} items object containing named child menu items
       * to apply to the feature's provided context menu, see {@link #config-defaultItems}.
       *
       * This may add extra items as below, but may also remove any of the {@link #config-defaultItems}
       * by configuring the name of the item as `false`
       *
       * ```javascript
       * features : {
       *     taskMenu : { // use eventMenu in the Scheduler product
       *         // This object is applied to the Feature's predefined defaultItems object
       *         items : {
       *             switchToDog : {
       *                 text : 'Dog',
       *                 icon : 'b-fa b-fa-fw b-fa-dog',
       *                 onItem({contextRecord}) {
       *                     contextRecord.dog = true;
       *                     contextRecord.cat = false;
       *                 },
       *                 weight : 500     // Make this second from end
       *             },
       *             switchToCat : {
       *                 text : 'Cat',
       *                 icon : 'b-fa b-fa-fw b-fa-cat',
       *                 onItem({contextRecord}) {
       *                     contextRecord.dog = false;
       *                     contextRecord.cat = true;
       *                 },
       *                 weight : 510     // Make this sink to end
       *             },
       *             add : false // We do not want the "Add" submenu to be available
       *         }
       *     }
       * }
       * ```
       *
       * @config {Object|Object[]}
       */
      items: null,

      /**
       * A function called before displaying the menu that allows manipulations of its items. Called with a
       * single parameter with format { contextRecord, eventElement, items }. Returning `false`
       * from this function prevents the menu from being shown.
       *
       * ```javascript
       * features : {
       *     taskContextMenu : {
       *         processItems({contextRecord, items}) {
       *             // Add or remove items here as needed
       *             if (contextRecord.type === 'Meeting') {
       *                 items.cancel = {
       *                     text   : 'Cancel',
       *                     icon   : 'b-fa b-fa-fw b-fa-ban',
       *                     weight : 200 // Move to end
       *                 };
       *             }
       *
       *             // Hide delete for parents
       *             items.deleteTask.hidden = contextRecord.isParent;
       *         }
       *     }
       * }
       * ```
       *
       * @config {Function}
       */
      processItems: null,

      /**
       * Event which is used to show context menu.
       * Available options are: 'contextmenu', 'click', 'dblclick'.
       * Default value is used from {@link Grid/view/GridBase#config-contextMenuTriggerEvent}
       * @config {String}
       */
      triggerEvent: null
    };
  } // Plugin configuration. This plugin chains some of the functions in Grid.

  static get pluginConfig() {
    return {
      assign: ['showEventContextMenu'],
      chain: ['onElementContextMenu', 'onElementClick', 'onElementDblClick', 'onEventSpaceKey']
    };
  } //endregion
  //region Init

  construct(grid, config) {
    super.construct(grid, config);
    VersionHelper.deprecate('Scheduler', '5.0.0', '`TimeSpanRecordContextMenuBase` feature is deprecated, in favor of `TimeSpanMenuBase` feature. Please see https://bryntum.com/docs/scheduler/#guides/upgrades/3.1.0.md for more information.');
  }

  doDestroy() {
    if (this.menu) {
      this.menu.destroy();
    }

    super.doDestroy();
  } //endregion
  //region Events

  onElementContextMenu(event) {
    this.triggerEvent === 'contextmenu' && this.showEventContextMenu(event);
  }

  onElementClick(event) {
    this.triggerEvent === 'click' && this.showEventContextMenu(event);
  }

  onElementDblClick(event) {
    this.triggerEvent === 'dblclick' && this.showEventContextMenu(event);
  } // chained from EventNavigation

  onEventSpaceKey(keyEvent) {
    const targetPoint = Rectangle.from(keyEvent.target).center,
          contextmenuEvent = new MouseEvent('contextmenu', Object.assign({
      clientX: targetPoint.x,
      clientY: targetPoint.y
    }, keyEvent));
    Object.defineProperty(contextmenuEvent, 'target', {
      get() {
        return keyEvent.target;
      }

    });
    this.showEventContextMenu(contextmenuEvent);
  } //endregion
  //region Menu handlers

  /**
   * Show event context menu.
   * @param event
   * @internal
   */

  showEventContextMenu(event) {
    const me = this,
          client = me.client,
          target = event.target,
          eventElement = DomHelper.up(target, client.eventSelector),
          targetElement = eventElement || target;

    if (targetElement) {
      const record = me.resolveRecord(targetElement);

      if (record) {
        event.preventDefault();
        me.showContextMenuFor(record, {
          targetElement,
          eventElement,
          event
        });
      }
    }
  }
  /**
   * A method to retrieve record from its DOM element. Must be implemented in subclasses.
   * @abstract
   */

  resolveRecord() {}
  /**
   * Shows context menu for the provided record. If record is not rendered (outside of time span, or collapsed)
   * menu won't appear.
   * @param {Scheduler.model.TimeSpan} record
   * @param {Object} [options]
   * @param {HTMLElement} options.targetElement Element to align context menu to
   * @param {HTMLElement} options.eventElement Event element if target is an event, otherwise `null`
   * @param {Event} options.event Browser event. If provided menu will be aligned according to clientX/clientY coordinates.
   * If omitted, context menu will be centered to targetElement
   */

  showContextMenuFor(record, options) {} // Implement in subclasses to massage options or veto show.

  beforeContextMenuShow() {} //endregion
  //region Show/Hide

  /**
   * @param {Object} eventParams
   * @param {Object[]} items
   * @internal
   */

  showContextMenu(eventParams) {
    if (this.disabled) {
      return;
    }

    const me = this,
          event = eventParams.event,
          menuType = eventParams.menuType.toLowerCase(),
          eventType = StringHelper.uncapitalize(menuType),
          clientGetItemsMethod = `get${StringHelper.capitalize(menuType)}MenuItems`,
          {
      client,
      processItems,
      defaultItems,
      namedItems
    } = me,
          point = event ? [event.clientX + 1, event.clientY + 1] : Rectangle.from(eventParams.targetElement).center,
          items = eventParams.items = ObjectHelper.isEmpty(me.items) ? ObjectHelper.clone(defaultItems) : ObjectHelper.merge(ObjectHelper.clone(defaultItems), me.items);
    eventParams.namedItems = namedItems;
    eventParams.selection = client.selectedRecords; // Apply the named items prior to Container's item processing.
    // Our namedItems must cascade to all descendant Menu levels.
    // And they MUST have all been converted prior to the processItems call.

    applyNamedItems(items, namedItems); // Call the chainable method which other features use to add their own menu items.
    // For example getEventMenuItems

    if (client[clientGetItemsMethod]) {
      client[clientGetItemsMethod](eventParams, items);
    } // Allow user a chance at processing the items and preventing the menu from showing

    if ((!processItems || processItems(eventParams) !== false) && !ObjectHelper.isEmpty(eventParams.items)) {
      // beforeContextMenuShow is a lifecycle method which may be implemented in subclasses to
      // preprocess the event.
      if (me.beforeContextMenuShow(eventParams) !== false) {
        // Trigger event that allows preventing menu or manipulating its items
        if (client.trigger(`${eventType}ContextMenuBeforeShow`, eventParams) !== false) {
          me.menu = WidgetHelper.showContextMenu(point, {
            owner: client,
            scrollAction: 'hide',
            clippedBy: [client.timeAxisSubGridElement, client.bodyContainer],
            constrainTo: window,
            items,

            onDestroy() {
              me.menu = null;
            },

            // Load up the item event with the contextual info
            onBeforeItem: itemEvent => {
              Object.assign(itemEvent, eventParams);
            },
            onItem: itemEvent => client.trigger(`${eventType}ContextMenuItem`, itemEvent),
            listeners: {
              show({
                source: menu
              }) {
                eventParams.menu = menu;
                client.trigger(`${eventType}ContextMenuShow`, eventParams);
              }

            }
          });
        }
      }
    }
  }
  /**
   * Hides the context menu
   * @internal
   */

  hideContextMenu(animate) {
    this.menu && this.menu.hide(animate);
  } //endregion
  //region Getters/Setters

  /**
   * Provides the default configuration of the context menu.
   *
   * Concrete classes must all provide their own defaultItems value in their defaultConfig blocks
   * @private
   */

  set defaultItems(defaultItems) {
    this._defaultItems = defaultItems;
  }

  get defaultItems() {
    const result = ObjectHelper.clone(this._defaultItems); // Read-only client should have no default items enabled

    if (this.client.readOnly) {
      for (const item in result) {
        result[item] = false;
      }
    }

    return result;
  }

  get triggerEvent() {
    return this._triggerEvent || this.client.contextMenuTriggerEvent;
  }

  set triggerEvent(value) {
    this._triggerEvent = value;
  } //endregion

}
TimeSpanRecordContextMenuBase._$name = 'TimeSpanRecordContextMenuBase';

const ScheduleRange = {
  completeview: 'completeview',
  // completedata : 'completedata',
  currentview: 'currentview',
  daterange: 'daterange'
};

const immediatePromise$7 = Promise.resolve();
var SchedulerExporterMixin = (base => class SchedulerExporterMixin extends base {
  async scrollRowIntoView(client, index) {
    const {
      rowManager,
      scrollable
    } = client,
          oldY = scrollable.y; // If it's a valid index to scroll to, then try it.

    if (index < client.store.count) {
      // Scroll the requested row to the viewport top
      scrollable.scrollTo(null, rowManager.calculateTop(index)); // If that initiated a scroll, we need to wait for the row to be rendered, so return
      // a Promise which resolves when that happens.

      if (scrollable.y !== oldY) {
        // GridBase adds listener to vertical scroll to update rows. Rows might be or might not be updated,
        // but at the end of each scroll grid will trigger `scroll` event. So far this is the only scroll event
        // triggered by the grid itself and it is different from `scroll` event on scrollable.
        return new Promise(resolve => {
          const detacher = client.on({
            scroll({
              scrollTop
            }) {
              // future-proof: only react to scroll event with certain argument
              if (scrollTop != null && rowManager.getRow(index)) {
                detacher();
                resolve();
              }
            }

          });
        });
      }
    } // No scroll ocurred. Promise must be resolved immediately

    return immediatePromise$7;
  }

  async scrollToDate(client, date) {
    let scrollFired = false;
    const promises = []; // Time axis is updated on element scroll, which is async event. We need to synchronize this logic.
    // If element horizontal scroll is changed then sync event is fired. We add listener to that one specific event
    // and remove it right after scrollToDate sync code, keeping listeners clean. If scrolling occurred, we need
    // to wait until time header is updated.

    const detacher = client.timeAxisSubGrid.scrollable.on({
      scrollStart({
        x
      }) {
        if (x != null) {
          scrollFired = true;
        }
      }

    }); // added `block: start` to do scrolling faster
    // it moves data to begin of visible area that is longer section for re-render

    promises.push(client.scrollToDate(date, {
      block: 'start'
    }));
    detacher();

    if (scrollFired) {
      promises.push(client.timeView.await('refresh', {
        checkLog: false
      })); // We have to wait for scrollEnd event before moving forward. When exporting large view we might have to scroll
      // extensively and it might occur that requested scroll position would not be reached because concurrent
      // scrollEnd events would move scroll back.
      // Covered by Gantt/tests/feature/export/MultiPageVertical.t.js

      promises.push(client.timeAxisSubGrid.header.scrollable.await('scrollEnd', {
        checkLog: false
      }));
    }

    await Promise.all(promises);
  }

  async prepareComponent(config) {
    const me = this,
          {
      client
    } = config,
          includeTimeline = client.timeAxisSubGrid.width > 0;

    switch (config.scheduleRange) {
      case ScheduleRange.completeview:
        config.rangeStart = client.startDate;
        config.rangeEnd = client.endDate;
        break;

      case ScheduleRange.currentview:
        const {
          startDate,
          endDate
        } = client.visibleDateRange;
        config.rangeStart = startDate;
        config.rangeEnd = endDate;
        break;
    }

    await client.waitForTransition(); // Don't change timespan if time axis subgrid is not visible

    if (includeTimeline) {
      // set new timespan before calling parent to get proper scheduler header/content size
      client.setTimeSpan(config.rangeStart, config.rangeEnd); // Access svgCanvas el to create dependency canvas early
      // eslint-disable-next-line no-unused-expressions

      client.svgCanvas;
    } // Disable event animations during export

    me._oldEnableEventAnimations = client.enableEventAnimations;
    client.enableEventAnimations = false; // Add scroll buffer for the horizontal rendering

    if (client.currentOrientation.isHorizontalRendering) {
      me._oldScrollBuffer = client.currentOrientation.scrollBuffer;
      client.currentOrientation.scrollBuffer = 100;
    }

    await super.prepareComponent(config);
    const {
      exportMeta
    } = me,
          fgCanvasEl = me.element.querySelector('.b-sch-foreground-canvas'),
          timeAxisEl = me.element.querySelector('.b-horizontaltimeaxis');
    exportMeta.includeTimeline = includeTimeline;

    if (includeTimeline && config.scheduleRange !== ScheduleRange.completeview) {
      // If we are exporting subrange of dates we need to change subgrid size accordingly
      exportMeta.totalWidth -= exportMeta.subGrids.normal.width;
      exportMeta.totalWidth += exportMeta.subGrids.normal.width = client.timeAxisViewModel.getDistanceBetweenDates(config.rangeStart, config.rangeEnd);
      const horizontalPages = Math.ceil(exportMeta.totalWidth / exportMeta.pageWidth),
            totalPages = horizontalPages * exportMeta.verticalPages;
      exportMeta.horizontalPages = horizontalPages;
      exportMeta.totalPages = totalPages; // store left scroll to imitate normal grid/header scroll using margin

      exportMeta.subGrids.normal.scrollLeft = client.getCoordinateFromDate(config.rangeStart);
    }

    exportMeta.timeAxisHeaders = [];
    exportMeta.timeAxisPlaceholders = [];
    exportMeta.headersColleted = false;
    DomHelper.forEachSelector(timeAxisEl, '.b-sch-header-row', headerRow => {
      exportMeta.timeAxisPlaceholders.push(me.createPlaceholder(headerRow));
      exportMeta.timeAxisHeaders.push(new Map());
    }); // Add placeholder for events, clear all event elements, but not the entire elements as it contains svg canvas

    exportMeta.subGrids.normal.eventsPlaceholder = me.createPlaceholder(fgCanvasEl, false);
    DomHelper.removeEachSelector(fgCanvasEl, '.b-sch-event-wrap');
    DomHelper.removeEachSelector(me.element, '.b-released');
    exportMeta.eventsBoxes = new Map();
    exportMeta.client = client;

    if (client.features.columnLines && !client.features.columnLines.disabled) {
      const columnLinesCanvas = me.element.querySelector('.b-column-lines-canvas');
      exportMeta.columnLinesPlaceholder = me.createPlaceholder(columnLinesCanvas);
      exportMeta.columnLines = {
        lines: new Map(),
        majorLines: new Map()
      };
    }

    if (client.features.dependencies && !client.features.dependencies.disabled) {
      const svgCanvasEl = me.element.querySelector(`[id="${client.svgCanvas.getAttribute('id')}"]`); // Same as above, clear only dependency lines, because there might be markers added by user

      if (exportMeta.dependenciesPlaceholder = svgCanvasEl) {
        DomHelper.removeEachSelector(svgCanvasEl, '.b-sch-dependency');
      }
    } // We need to scroll component to date to calculate correct start margin

    if (includeTimeline && !DateHelper.betweenLesser(config.rangeStart, client.startDate, client.endDate)) {
      await me.scrollToDate(client, config.rangeStart);
    }
  }

  async restoreState(config) {
    let waitForHorizontalScroll = false;
    const {
      client
    } = config,
          promises = []; // If scroll will be changed during restoring state (and it will likely be), raise a flag that exporter should
    // wait for scrollEnd event before releasing control

    const detacher = client.timeAxisSubGrid.scrollable.on({
      scrollStart({
        x
      }) {
        // HACK: scrollStart might actually fire when scroll is set to existing value
        if (this.element.scrollLeft !== x) {
          waitForHorizontalScroll = true;
        }
      }

    });
    promises.push(super.restoreState(config)); // Scroll start will be fired synchronously

    detacher();

    if (waitForHorizontalScroll) {
      promises.push(client.timeAxisSubGrid.header.scrollable.await('scrollEnd', {
        checkLog: false
      }));
    }

    await Promise.all(promises);
  }

  async restoreComponent(config) {
    const {
      client
    } = config;
    client.enableEventAnimations = this._oldEnableEventAnimations;

    if (client.currentOrientation.isHorizontalRendering) {
      client.currentOrientation.scrollBuffer = this._oldScrollBuffer;
    }

    await super.restoreComponent(config);
  }

  async onRowsCollected(rows, config) {
    const me = this;
    await super.onRowsCollected(rows); // Only collect this data if timeline is visible

    if (me.exportMeta.includeTimeline) {
      const {
        client
      } = config,
            {
        timeView
      } = client,
            {
        pageRangeStart,
        pageRangeEnd
      } = me.getCurrentPageDateRange(config); // Exported page may not contain timeline view, in which case we need to fall through

      if (pageRangeStart) {
        let rangeProcessed = false;
        await me.scrollToDate(client, pageRangeStart); // Time axis and events are only rendered for the visible time span
        // we need to scroll the view and gather events/timeline elements
        // while (timeView.endDate <= config.rangeEnd) {

        while (!rangeProcessed) {
          me.collectLines(config);
          me.collectHeaders(config);
          me.collectEvents(rows, config);

          if (DateHelper.timeSpanContains(timeView.startDate, timeView.endDate, pageRangeStart, pageRangeEnd)) {
            rangeProcessed = true;
          } else if (timeView.endDate.getTime() >= pageRangeEnd.getTime()) {
            rangeProcessed = true;
          } else {
            const endDate = timeView.endDate;
            await me.scrollToDate(client, timeView.endDate); // If timeview end date is same as before scroll it means client is not able to scroll to date
            // and will go into infinite loop unless we stop it

            if (endDate.getTime() === timeView.endDate.getTime()) {
              throw new Error('Could not scroll to date');
            }
          }
        }
      }

      await me.scrollToDate(client, config.rangeStart);
    }
  }

  getCurrentPageDateRange({
    rangeStart,
    rangeEnd,
    client
  }) {
    const me = this,
          {
      exportMeta
    } = me,
          {
      horizontalPages,
      horizontalPosition,
      pageWidth,
      subGrids
    } = exportMeta;
    let pageRangeStart, pageRangeEnd; // when exporting to multiple pages we only need to scroll sub-range within visible time span

    if (horizontalPages > 1) {
      const pageStartX = horizontalPosition * pageWidth,
            pageEndX = (horizontalPosition + 1) * pageWidth,
            // Assuming normal grid is right next to right side of the locked grid
      // There is also a default splitter
      normalGridX = subGrids.locked.width + subGrids.locked.splitterWidth;

      if (pageEndX <= normalGridX) {
        pageRangeEnd = pageRangeStart = null;
      } else {
        pageRangeStart = client.getDateFromCoordinate(Math.max(pageStartX - normalGridX, 0)); // Extend visible schedule by 20% to cover up possible splitter

        pageRangeEnd = client.getDateFromCoordinate((pageEndX - normalGridX) * 1.2) || rangeEnd;
      }
    } else {
      pageRangeStart = rangeStart;
      pageRangeEnd = rangeEnd;
    }

    return {
      pageRangeStart,
      pageRangeEnd
    };
  }

  prepareExportElement() {
    const {
      element,
      exportMeta
    } = this,
          {
      id,
      headerId,
      footerId,
      scrollLeft
    } = exportMeta.subGrids.normal,
          el = element.querySelector(`[id="${id}"]`);
    ['.b-sch-background-canvas', '.b-sch-foreground-canvas'].forEach(selector => {
      const canvasEl = el.querySelector(selector);

      if (canvasEl) {
        // Align canvases to last exported row bottom. If no such property exists - remove inline height
        if (exportMeta.lastExportedRowBottom) {
          canvasEl.style.height = `${exportMeta.lastExportedRowBottom}px`;
        } else {
          canvasEl.style.height = '';
        } // Simulate horizontal scroll

        if (scrollLeft) {
          canvasEl.style.marginLeft = `-${scrollLeft}px`;
        }
      }
    });

    if (scrollLeft) {
      [headerId, footerId].forEach(id => {
        const el = element.querySelector(`[id="${id}"] .b-widget-scroller`);

        if (el) {
          el.style.marginLeft = `-${scrollLeft}px`;
        }
      });
    }

    return super.prepareExportElement();
  }

  collectRow(row) {
    const me = this,
          {
      subGrids
    } = me.exportMeta;
    Object.entries(row.elements).forEach(([key, value]) => {
      const rowConfig = [value.outerHTML, row.top, row.offsetHeight];

      if (key === 'normal') {
        rowConfig.push(new Map());
      }

      subGrids[key].rows.push(rowConfig);
    });
  }

  collectHeaders(config) {
    const me = this,
          {
      client
    } = config,
          {
      exportMeta
    } = me; // We only need to collect headers once, this flag is raised once they are collected along all exported range

    if (!exportMeta.headersCollected) {
      const timeAxisEl = client.timeView.element,
            timeAxisHeaders = exportMeta.timeAxisHeaders;
      DomHelper.forEachSelector(timeAxisEl, '.b-sch-header-row', (headerRow, index, headerRows) => {
        const headersMap = timeAxisHeaders[index];
        DomHelper.forEachSelector(headerRow, '.b-sch-header-timeaxis-cell', el => {
          if (!headersMap.has(el.dataset.tickIndex)) {
            headersMap.set(el.dataset.tickIndex, el.outerHTML);
          }
        });

        if (index === headerRows.length - 1 && headersMap.has(String(client.timeAxis.count - 1))) {
          exportMeta.headersCollected = true;
        }
      });
    }
  }

  collectLines(config) {
    const me = this,
          {
      client
    } = config,
          {
      exportMeta
    } = me,
          {
      columnLines
    } = exportMeta; // We only need to collect headers once, this flag is raised once they are collected along all exported range

    if (!exportMeta.headersCollected && columnLines) {
      const bgCanvas = client.backgroundCanvas;
      DomHelper.forEachSelector(bgCanvas, '.b-column-line, .b-column-line-major', lineEl => {
        if (lineEl.classList.contains('b-column-line')) {
          const lineIndex = Number(lineEl.dataset.line.replace(/line-/, ''));
          columnLines.lines.set(lineIndex, lineEl.outerHTML);
        } else {
          const lineIndex = Number(lineEl.dataset.line.replace(/major-/, ''));
          columnLines.majorLines.set(lineIndex, lineEl.outerHTML);
        }
      });
    }
  }

  collectEvents(rows, config) {
    const me = this,
          addedRows = rows.length,
          {
      client
    } = config,
          normalRows = me.exportMeta.subGrids.normal.rows;
    rows.forEach((row, index) => {
      const rowConfig = normalRows[normalRows.length - addedRows + index],
            resource = client.store.getAt(row.dataIndex),
            eventsMap = rowConfig[3];
      resource.events && resource.events.forEach(event => {
        if (event.isScheduled) {
          let el = client.getElementFromEventRecord(event, resource);

          if (el && (el = el.parentElement) && !eventsMap.has(event.id)) {
            eventsMap.set(event.id, [el.outerHTML, Rectangle.from(el, el.offsetParent)]);
          }
        }
      });
    });
  }

  buildPageHtml() {
    const me = this,
          {
      subGrids,
      timeAxisHeaders,
      timeAxisPlaceholders,
      columnLines,
      columnLinesPlaceholder
    } = me.exportMeta; // Now when rows are collected, we need to add them to exported grid

    let html = me.prepareExportElement();
    Object.values(subGrids).forEach(({
      placeHolder,
      eventsPlaceholder,
      rows
    }) => {
      const placeHolderText = placeHolder.outerHTML,
            // Rows can be repositioned, in which case event related to that row should also be translated
      {
        resources,
        events
      } = me.positionRows(rows);
      html = html.replace(placeHolderText, resources.join(''));

      if (eventsPlaceholder) {
        html = html.replace(eventsPlaceholder.outerHTML, events.join(''));
      }
    });
    timeAxisHeaders.forEach((headers, index) => {
      html = html.replace(timeAxisPlaceholders[index].outerHTML, Array.from(headers.values()).join(''));
    });

    if (columnLines) {
      const lineElements = Array.from(columnLines.lines.values()).concat(Array.from(columnLines.majorLines.values()));
      html = html.replace(columnLinesPlaceholder.outerHTML, lineElements.join(''));
    }

    html = me.buildDependenciesHtml(html);
    return html;
  } // outerHTML is not supported on SVG elements in IE11, using workaround

  getDependenciesOuterHTML() {
    const {
      dependenciesPlaceholder
    } = this.exportMeta;
    let result;

    if (BrowserHelper.isIE11) {
      const wrapper = document.createElement('div'),
            tmpEl = dependenciesPlaceholder.cloneNode(true);
      wrapper.appendChild(tmpEl);
      result = wrapper.innerHTML;
    } else {
      result = dependenciesPlaceholder.outerHTML;
    }

    return result;
  }

  renderDependencies() {
    const me = this,
          {
      client,
      dependenciesPlaceholder,
      eventsBoxes
    } = me.exportMeta,
          {
      dependencies
    } = client,
          dependencyFeature = client.features.dependencies,
          eventsInView = Array.from(eventsBoxes.keys());
    dependencies.filter(r => eventsInView.includes(String(r.from)) || eventsInView.includes(String(r.to))).forEach(dependency => {
      let fromBox = eventsBoxes.get(String(dependency.from)),
          toBox = eventsBoxes.get(String(dependency.to));
      const assignments = dependencyFeature.getIteratableDependencyAssignments(dependency),
            {
        fromEvent,
        toEvent
      } = dependency;
      assignments.forEach(assignmentData => {
        var _fromBox, _toBox;

        // In case of single page export from/to boxes will always be present, also we cannot trust boxes from cache
        // because single page exporter can possible change row/event vertical position
        // In case of multipage we will get box from the dependency feature
        if (!fromBox) {
          if (fromEvent) {
            const box = dependencyFeature.getBox(dependency, true, assignmentData);

            if (box) {
              fromBox = box instanceof Rectangle ? box : new Rectangle(box.start, box.top, box.end - box.start, box.bottom - box.top);
            }
          }
        }

        if (!toBox) {
          if (toEvent) {
            const box = dependencyFeature.getBox(dependency, false, assignmentData);

            if (box) {
              toBox = box instanceof Rectangle ? box : new Rectangle(box.start, box.top, box.end - box.start, box.bottom - box.top);
            }
          }
        } // Gantt dependencies feature modifies box size to point arrow to the top, clone box to keep original size

        const drawData = {
          startRectangle: (_fromBox = fromBox) === null || _fromBox === void 0 ? void 0 : _fromBox.clone(),
          endRectangle: (_toBox = toBox) === null || _toBox === void 0 ? void 0 : _toBox.clone()
        };

        if (fromBox && toBox) {
          dependencyFeature.drawDependency(dependency, drawData, assignmentData, dependenciesPlaceholder, false);
        }
      });
    });
    const result = me.getDependenciesOuterHTML();
    DomHelper.removeEachSelector(dependenciesPlaceholder, '.b-sch-dependency');
    return result;
  }

  buildDependenciesHtml(html) {
    const {
      dependenciesPlaceholder,
      includeTimeline
    } = this.exportMeta;

    if (dependenciesPlaceholder && includeTimeline) {
      const placeholder = this.getDependenciesOuterHTML();
      html = html.replace(placeholder, this.renderDependencies());
    }

    return html;
  }

});

class MultiPageExporter$1 extends SchedulerExporterMixin(MultiPageExporter) {
  static get $name() {
    return 'MultiPageExporter';
  }

  async stateNextPage(config) {
    await super.stateNextPage(config);
    this.exportMeta.eventsBoxes.clear();
  }

  positionRows(rows) {
    const resources = [],
          events = []; // In case of variable row height row vertical position is not guaranteed to increase
    // monotonously. Position row manually instead

    rows.forEach(([html, top, height, eventsHtml]) => {
      resources.push(html);
      eventsHtml && Array.from(eventsHtml.entries()).forEach(([key, [html, box]]) => {
        events.push(html); // Store event box to render dependencies later

        this.exportMeta.eventsBoxes.set(String(key), box);
      });
    });
    return {
      resources,
      events
    };
  }

}
MultiPageExporter$1._$name = 'MultiPageExporter';

class MultiPageVerticalExporter$1 extends SchedulerExporterMixin(MultiPageVerticalExporter) {
  static get $name() {
    return 'MultiPageVerticalExporter';
  }

  async stateNextPage(config) {
    await super.stateNextPage(config);
    this.exportMeta.eventsBoxes.clear();
  }

  async prepareComponent(config) {
    await super.prepareComponent(config); // Scheduler exporter mixin can update totalWidth, so we need to adjust pages and scale here again

    if (config.scheduleRange !== ScheduleRange.completeview) {
      this.estimateTotalPages(config);
    }
  }

  positionRows(rows) {
    const resources = [],
          events = []; // In case of variable row height row vertical position is not guaranteed to increase
    // monotonously. Position row manually instead

    rows.forEach(([html, top, height, eventsHtml]) => {
      resources.push(html);
      eventsHtml && Array.from(eventsHtml.entries()).forEach(([key, [html, box]]) => {
        events.push(html); // Store event box to render dependencies later

        this.exportMeta.eventsBoxes.set(String(key), box);
      });
    });
    return {
      resources,
      events
    };
  }

}
MultiPageVerticalExporter$1._$name = 'MultiPageVerticalExporter';

class ScheduleRangeCombo extends LocalizableCombo {
  static get $name() {
    return 'ScheduleRangeCombo';
  } // Factoryable type name

  static get type() {
    return 'schedulerangecombo';
  }

  static get defaultConfig() {
    return {
      editable: false
    };
  }

  buildLocalizedItems() {
    // Do not remove. Assertion strings for Localization sanity check.
    // 'L{completeview}'
    // 'L{currentview}'
    // 'L{daterange}'
    // 'L{completedata}'
    return Object.entries(ScheduleRange).map(([id, text]) => ({
      id,
      text: this.L(text)
    }));
  }

} // Register this widget type with its Factory

ScheduleRangeCombo.initClass();
ScheduleRangeCombo._$name = 'ScheduleRangeCombo';

/**
 * @module Scheduler/view/export/SchedulerExportDialog
 */

/**
 * Similar to dialog in Grid, but with few extra fields specific to scheduler.
 * @extends Grid/view/export/ExportDialog
 */

class SchedulerExportDialog extends ExportDialog {
  static get $name() {
    return 'SchedulerExportDialog';
  }

  static get defaultConfig() {
    return {
      scheduleRange: 'completeview'
    };
  }

  onLocaleChange() {
    const labelWidth = this.L('labelWidth');
    this.width = this.L('L{width}');
    this.items.forEach(widget => {
      if (widget instanceof Field) {
        widget.labelWidth = labelWidth;
      } else if (widget.ref === 'rangeFieldsContainer') {
        widget.items[0].width = labelWidth;
      }
    });
  }

  get fieldProperties() {
    return ['scheduleRange', ...super.fieldProperties];
  }

  buildDialogItems(config) {
    const me = this,
          {
      client
    } = config,
          {
      scheduleRange
    } = me.getDefaultFieldValues(config),
          items = super.buildDialogItems(config),
          labelWidth = me.L('labelWidth');
    me.columnsStore = client.columns.chain(record => record.isLeaf && record.exportable);
    const columnsField = items.find(item => item.ref === 'columnsField');
    columnsField.store = me.columnsStore;
    columnsField.value = me.columnsStore.allRecords;
    items.splice(1, 0, {
      labelWidth,
      type: 'schedulerangecombo',
      ref: 'scheduleRangeField',
      label: 'L{Schedule range}',
      localeClass: me,
      value: scheduleRange,

      onChange({
        value
      }) {
        const hidden = value !== ScheduleRange.daterange,
              widgetMap = this.owner.widgetMap;
        widgetMap.rangeStartField.hidden = widgetMap.rangeEndField.hidden = hidden;
      }

    }, {
      type: 'container',
      ref: 'rangeFieldsContainer',
      flex: '1 0 100%',
      defaults: {
        localeClass: me
      },
      items: [{
        // Filler widget to align date fields
        type: 'widget',
        width: labelWidth
      }, {
        type: 'datefield',
        ref: 'rangeStartField',
        label: 'L{Export from}',
        labelWidth: '3em',
        hidden: true,
        flex: '1 0 25%',
        localeClass: me,
        value: client.startDate,
        max: DateHelper.max(client.startDate, DateHelper.add(client.endDate, -1, 'd')),

        onChange({
          value
        }) {
          this.owner.widgetMap.rangeEndField.min = DateHelper.add(value, 1, 'd');
        }

      }, {
        // Another filler to move label further from previous field
        type: 'widget',
        width: '0.5em'
      }, {
        type: 'datefield',
        ref: 'rangeEndField',
        label: 'L{Export to}',
        labelWidth: '1em',
        hidden: true,
        flex: '1 0 25%',
        localeClass: me,
        value: client.endDate,
        min: DateHelper.min(client.endDate, DateHelper.add(client.startDate, 1, 'd')),

        onChange({
          value
        }) {
          this.owner.widgetMap.rangeStartField.max = DateHelper.add(value, -1, 'd');
        }

      }]
    });
    return items;
  }

}
SchedulerExportDialog._$name = 'SchedulerExportDialog';

class SinglePageExporter$1 extends SchedulerExporterMixin(SinglePageExporter) {
  static get $name() {
    return 'SinglePageExporter';
  } // We should not collect dependencies per each page, instead we'd render them once

  collectDependencies() {}

  positionRows(rows) {
    let currentTop = 0,
        resources = [],
        events = [],
        translateRe = /translate\((\d+.?\d*)px, (\d+.?\d*)px\)/; // In case of variable row height row vertical position is not guaranteed to increase
    // monotonously. Position row manually instead

    rows.forEach(([html, top, height, eventsHtml]) => {
      resources.push(html.replace(translateRe, `translate($1px, ${currentTop}px)`));
      const rowTopDelta = currentTop - top;
      eventsHtml && Array.from(eventsHtml.entries()).forEach(([key, [html, box]]) => {
        // Fix event vertical position according to the row top
        box.translate(0, rowTopDelta); // Store event box to render dependencies later

        this.exportMeta.eventsBoxes.set(String(key), box);
        events.push(html.replace(translateRe, `translate($1px, ${box.y}px)`));
      });
      currentTop += height;
    });
    return {
      resources,
      events
    };
  }

}
SinglePageExporter$1._$name = 'SinglePageExporter';

/**
 * @module Scheduler/feature/export/PdfExport
 */

/**
 * Generates PDF/PNG files from the Scheduler component.
 *
 * <img src="resources/images/export-dialog.png" style="max-width : 300px" alt="Scheduler Export dialog">
 *
 * **NOTE:** This feature makes a fetch request to the server, posting the HTML fragments to be exported.
 * The {@link Grid.feature.export.PdfExport#config-exportServer} URL must be configured.
 *
 * ## Usage
 *
 * ```javascript
 * const scheduler = new Scheduler({
 *     features : {
 *         pdfExport : {
 *             exportServer : 'http://localhost:8080' // Required
 *         }
 *     }
 * })
 *
 * // Opens popup allowing to customize export settings
 * scheduler.features.pdfExport.showExportDialog();
 *
 * // Simple export
 * scheduler.features.pdfExport.export({
 *     columns : scheduler.columns.map(c => c.id) // Required, set list of column ids to export
 * }).then(result => {
 *     // Response instance and response content in JSON
 *     let { response, responseJSON } = result;
 * });
 * ```
 *
 * Appends configs related to exporting time axis: {@link #config-scheduleRange}, {@link #config-rangeStart},
 * {@link #config-rangeEnd}
 *
 * ## Loading resources
 *
 * If you face a problem with loading resources when exporting, the cause might be that the application and the export server are hosted on different servers.
 * This is due to [Cross-Origin Resource Sharing](https://developer.mozilla.org/en-US/docs/Web/HTTP/CORS) (CORS). There are 2 options how to handle this:
 * - Allow cross origin requests from the server where your export is hosted to the server where your application is hosted;
 * - Copy all resources keeping the folder hierarchy from the server where your application is hosted to the server where your export is hosted
 * and setup paths using {@link Grid.feature.export.PdfExport#config-translateURLsToAbsolute} config and configure the export server to give access to the path:
 *
 * ```javascript
 * const scheduler = new Scheduler({
 *     features : {
 *         pdfExport : {
 *             exportServer : 'http://localhost:8080',
 *             translateURLsToAbsolute : 'http://localhost:8080/resources' // '/resources' is hardcoded in WebServer implementation
 *         }
 *     }
 * })
 * ```
 *
 * ```javascript
 * // Following path would be served by this address: http://localhost:8080/resources/
 * node ./src/server.js -h 8080 -r web/application/styles
 * ```
 *
 * where `web/application/styles` is a physical root location of the copied resources, for example:
 *
 * <img src="resources/images/export-server-resources.png" style="max-width : 500px" alt="Export server structure with copied resources" />
 *
 * @extends Grid/feature/export/PdfExport
 * @typings Grid/feature/export/PdfExport -> Grid/feature/export/GridPdfExport
 */

class PdfExport$1 extends PdfExport {
  static get $name() {
    return 'PdfExport';
  }

  static get defaultConfig() {
    return {
      exporters: [SinglePageExporter$1, MultiPageExporter$1, MultiPageVerticalExporter$1],

      /**
       * Specifies how to export time span.
       *  * completeview - Complete configured time span, from scheduler start date to end date
       *  * currentview  - Currently visible time span
       *  * daterange    - Use specific date range, provided additionally in config. See {@link #config-rangeStart}/
       *  {@link #config-rangeEnd}
       * @config {String}
       * @default
       * @group Export file config
       */
      scheduleRange: 'completeview',

      /**
       * Exported time span range start. Used with `daterange` config of the {@link #config-scheduleRange}
       * @config {Date}
       * @group Export file config
       */
      rangeStart: null,

      /**
       * Exported time span range end. Used with `daterange` config of the {@link #config-scheduleRange}
       * @config {Date}
       * @group Export file config
       */
      rangeEnd: null
    };
  }

  get defaultExportDialogConfig() {
    return ObjectHelper.copyProperties(super.defaultExportDialogConfig, this, ['scheduleRange']);
  }

  showExportDialog() {
    const me = this;

    if (!me.exportDialog) {
      me.exportDialog = new SchedulerExportDialog(Object.assign({}, me.defaultExportDialogConfig, {
        listeners: {
          export: me.onExportDialogExport,
          thisObj: me
        }
      }));
    }

    return me.exportDialog.show();
  }

  buildExportConfig(config) {
    config = super.buildExportConfig(config);
    const {
      scheduleRange,
      rangeStart,
      rangeEnd
    } = this; // Time axis is filtered from UI, need to append it

    if (config.columns && !config.columns.find(col => col.type === 'timeAxis')) {
      config.columns.push(config.client.timeAxisColumn.id);
    }

    return ObjectHelper.assign({
      scheduleRange,
      rangeStart,
      rangeEnd
    }, config);
  }

}
PdfExport$1._$name = 'PdfExport';
GridFeatureManager.registerFeature(PdfExport$1, false, 'Scheduler');

/**
 * @module Scheduler/util/ScheduleTableExporter
 */

/**
 * This class transforms scheduler component into two arrays: rows and columns. Columns array contains objects with
 * meta information about column: field name, column name, width and type of the rendered value, rows array contains
 * arrays of cell values.
 *
 * ```javascript
 * const exporter = new ScheduleTableExporter({ target : scheduler });
 * exporter.export()
 *
 * // Output
 * {
 *     columns : [
 *         { field : 'name',      value : 'First name', type : 'string',  width : 100 },
 *         { field : 'name',      value : 'Task',       type : 'string',  width : 100, eventColumn : true },
 *         { field : 'startDate', value : 'Starts',     type : 'date',    width : 100, eventColumn : true },
 *         { field : 'endDate',   value : 'Ends',       type : 'date',    width : 100, eventColumn : true }
 *     ],
 *     rows : [
 *         ['Michael', 'Hand out dundies',      Date, Date],
 *         ['Michael', 'Buy condo',             Date, Date],
 *         ['Jim',     'Close sale to library', Date, Date]
 *     ]
 * }
 * ```
 *
 * ## How data is exported
 *
 * Data is exported as in the base class with minor addition: every event is exported on a separate row, like
 * demonstrated above.
 *
 * In case there are unassigned events, by default they will be exported as well
 *
 * ```javascript
 * // output
 * {
 *     rows : [
 *         ['Michael', 'Hand out dundies',      Date, Date],
 *         ['Michael', 'Buy condo',             Date, Date],
 *         ['Jim',     'Close sale to library', Date, Date],
 *         ['',        'No resource assigned'],
 *         ['',        'Halloween prep',        Date, Date],
 *         ['',        'New year prep',         Date, Date]
 *     ]
 * }
 * ```
 *
 * @extends Grid/util/TableExporter
 * @mixes Core/localization/Localizable
 */

class ScheduleTableExporter extends Localizable(TableExporter) {
  static get defaultConfig() {
    return {
      /**
       * Set to `false` to not include unassigned events in the export. `true` by default.
       * @config {Boolean} includeUnassigned
       * @default
       */
      includeUnassigned: true,

      /**
       * An array of Event columns configuration used to specify columns width, headers name, and column fields to get the data from.
       * 'field' config is required. If 'text' is missing, the 'field' config will be used instead.
       *
       * For example:
       * ```javascript
       * eventColumns    : [
       *     { text : 'Task', field : 'name' },
       *     { text : 'Starts', field : 'startDate', width : 140 },
       *     { text : 'Ends', field : 'endDate', width : 140 }
       * ]
       * ```
       *
       * @config {String[]|Object[]} eventColumns
       * @default
       */
      eventColumns: [{
        text: 'Task',
        field: 'name'
      }, {
        text: 'Starts',
        field: 'startDate',
        width: 140
      }, {
        text: 'Ends',
        field: 'endDate',
        width: 140
      }],

      /**
       * Function to sort events for each resource. By default events are sorted in the order of appending to the
       * store.
       * For example:
       * ```javascript
       * // Sorting by start date
       * eventSortFn : (a, b) => a.startDate - b.startDate
       * ```
       * @config {Function}
       * @internal
       */
      eventSortFn: null
    };
  }

  construct(config = {}, ...args) {
    super.construct(config, ...args);

    if (!this.eventSortFn) {
      this.eventSortFn = (a, b) => a.internalId - b.internalId;
    }
  }

  normalizeColumns(config) {
    super.normalizeColumns(config);
    config.eventColumns = config.eventColumns.map(col => {
      if (typeof col === 'string') {
        return {
          field: col
        };
      } else {
        return col;
      }
    });
  }

  generateExportData(config) {
    const me = this,
          isScheduler = this.target instanceof SchedulerBase,
          resourceColumns = me.generateColumns(config),
          eventColumns = isScheduler ? me.generateEventColumns(config) : [],
          columns = resourceColumns.concat(eventColumns),
          rows = me.generateRows(config);
    return {
      columns,
      rows
    };
  }

  generateEventColumns(config) {
    return config.eventColumns.map(column => this.processEventColumn(column, config));
  }

  processEventColumn(column, config) {
    const {
      width,
      minWidth
    } = column,
          {
      defaultColumnWidth
    } = config;
    return {
      field: column.field,
      value: column.text,
      width: Math.max(width || defaultColumnWidth, minWidth || defaultColumnWidth),
      eventColumn: true,
      type: this.getColumnType(column, this.target.eventStore)
    };
  }

  generateRows(config) {
    const me = this,
          {
      target
    } = me;
    let result;

    if (!(target instanceof SchedulerBase)) {
      result = super.generateRows(config);
    } else {
      result = []; // forEach skips group records, summary records etc

      target.resourceStore.map(resourceRecord => {
        // Get all events for resource (including assignment store)
        const events = resourceRecord.events || []; // Events order is not guaranteed, sort by internalId, that is guaranteed to grow with every new record

        events.sort(me.eventSortFn); // Set dummy event to have resource info printed without events

        if (!events.length) {
          events.push('');
        }

        events.forEach(eventRecord => result.push(me.getRowData(config, resourceRecord, eventRecord)));
      });

      if (config.includeUnassigned && config.eventColumns.length) {
        const notAssignedEvents = target.eventStore.query(eventRecord => !eventRecord.resources.length);

        if (notAssignedEvents.length) {
          // Use offset to match first event column
          const cells = new Array(config.columns.length).fill('');
          cells.push(me.L('L{ExcelExporter.No resource assigned}'));
          result.push(cells); // Set dummy resource to have event info printed without resource

          notAssignedEvents.forEach(eventRecord => result.push(me.getRowData(config, null, eventRecord)));
        }
      } // filter out empty rows

      result = result.filter(cells => cells.length);
    }

    return result;
  }

  getRowData(config, resource, event) {
    const {
      columns,
      eventColumns
    } = config,
          cells = [],
          resourceCells = this.processRecord(resource, columns, config); // No cells from group footers (or from group headers if configured to not show them)

    resourceCells && cells.push(...resourceCells);

    if (!resource || !resource.isSpecialRow) {
      const eventCells = this.processRecord(event, eventColumns, config); // No cells from group footers (or from group headers if configured to not show them)

      eventCells && cells.push(...eventCells);
    }

    return cells;
  }

}
ScheduleTableExporter._$name = 'ScheduleTableExporter';

/*eslint no-undef: "error"*/
/**
 * @module Scheduler/feature/experimental/ExcelExporter
 */

/**
 * **NOTE**: This class requires a 3rd party library to operate.
 *
 * A plugin that allows exporting Scheduler data to Excel without involving the server. It uses {@link Scheduler.util.ScheduleTableExporter}
 * class as data provider, [zipcelx library](https://www.npmjs.com/package/zipcelx)
 * forked and adjusted to support [column width config](https://github.com/bryntum/zipcelx/tree/column-width-build)
 * and [Microsoft XML specification](https://msdn.microsoft.com/en-us/library/office/documentformat.openxml.spreadsheet.aspx).
 * Zipcelx should be either in global scope (window) or can be provided with {@link #config-zipcelx} config.
 *
 * ```
 * // Global scope
 * <script src="zipcelx.js"></script>
 *
 * // importing from package
 * import zipcelx from 'zipcelx';
 *
 * const grid = new Grid({
 *     features : {
 *         excelExporter : {
 *             zipcelx
 *         }
 *     }
 * })
 * ```
 *
 * Here is an example of how to add the feature:
 *
 * ```javascript
 * const scheduler = new Scheduler({
 *     features : {
 *         excelExporter : {
 *             // Choose the date format for date fields
 *             dateFormat : 'YYYY-MM-DD HH:mm',
 *             exporterConfig : {
 *                 // Choose the Resource fields to include in the exported file
 *                 columns : [{ text : 'Staff', field : 'name' }],
 *
 *                 // Choose the Event fields to include in the exported file
 *                 eventColumns    : [
 *                     { text : 'Task', field : 'name' },
 *                     { text : 'Starts', field : 'startDate', width : 140 },
 *                     { text : 'Ends', field : 'endDate', width : 140 }
 *                 ]
 *             }
 *         }
 *     }
 * });
 * ```
 *
 * And how to call it:
 *
 * ```javascript
 * scheduler.features.excelExporter.export({
 *     filename : 'Export',
 *     exporterConfig : {
 *         columns : [{ text : 'Staff', field : 'name' }]
 *     }
 * })
 * ```
 *
 * @extends Grid/feature/experimental/ExcelExporter
 * @demo Scheduler/exporttoexcel
 * @typings Grid/feature/experimental/ExcelExporter -> Grid/feature/experimental/GridExcelExporter
 */

class ExcelExporter$1 extends ExcelExporter {
  static get $name() {
    return 'ExcelExporter';
  }

  static get defaultConfig() {
    return {
      /**
       * Exporter class. Must subclass {@link Scheduler.util.ScheduleTableExporter}
       * @config {Scheduler.util.ScheduleTableExporter}
       * @default
       */
      exporterClass: ScheduleTableExporter,

      /**
       * Configuration object for {@link #config-exporterClass exporter class}.
       * @config {Object}
       */
      exporterConfig: null
    };
  }

}
ExcelExporter$1._$name = 'ExcelExporter';
GridFeatureManager.registerFeature(ExcelExporter$1, false, 'Scheduler');

/**
 * @module Scheduler/column/ResourceInfoColumn
 */

/**
 * Displays basic resource information. Defaults to showing image + name + event count, it is all configurable.
 * If a resource has no image, you can also provide an icon using `iconCls` in the data (you then need to specify
 * `image === false` in your data).
 * Be sure to specify {@link Scheduler.view.mixin.SchedulerEventRendering#config-resourceImagePath} to instruct the
 * column where to look for the images.
 * @externalexample scheduler/ResourceInfoColumn.js
 * @classType resourceInfo
 * @extends Grid/column/Column
 */

class ResourceInfoColumn extends Column {
  static get $name() {
    return 'ResourceInfoColumn';
  }

  static get type() {
    return 'resourceInfo';
  }

  static get fields() {
    return ['showEventCount', 'showRole', 'showImage', 'validNames', 'autoScaleThreshold'];
  }

  static get defaults() {
    return {
      /**
       * Show image. Looks for image name in fields on the resource in the following order: 'imageUrl', 'image',
       * 'name'. Set `showImage` to a field name to use a custom field. Set `Scheduler.resourceImagePath` to
       * specify where to load images from. If no extension found, defaults to
       * {@link Scheduler.view.mixin.SchedulerEventRendering#config-resourceImageExtension}.
       * @config {Boolean}
       * @default
       */
      showImage: true,

      /**
       * Show number of events assigned to the resource below the name.
       * @config {Boolean}
       * @default
       */
      showEventCount: true,

      /**
       * Show resource role below the name. Specify `true` to display data from the `role` field, or specify a field
       * name to read this value from.
       * @config {Boolean|String}
       * @default
       */
      showRole: false,

      /**
       * Valid image names. Set to `null` to allow all names.
       * @config {String[]}
       * @default
       */
      validNames: ['amit', 'angelo', 'arcady', 'arnold', 'celia', 'chang', 'dan', 'dave', 'emilia', 'george', 'gloria', 'henrik', 'hitomi', 'jong', 'kate', 'lee', 'linda', 'lisa', 'lola', 'macy', 'madison', 'malik', 'mark', 'maxim', 'mike', 'rob', 'steve'],

      /**
       * Specify 0 to prevent the column from adapting its content according to the used row height, or specify a
       * a threshold (row height) at which scaling should start.
       * @config {Number}
       * @default
       */
      autoScaleThreshold: 40,
      field: 'name',
      htmlEncode: false,
      width: 140,
      cellCls: 'b-resourceinfo-cell',
      editor: VersionHelper.isTestEnv ? false : 'text'
    };
  }

  construct() {
    const me = this;
    super.construct(...arguments);

    if (me.grid.isPainted) {
      me.addErrorListener();
    } else {
      me.grid.on({
        paint: me.addErrorListener,
        thisObj: me,
        once: true
      });
    }
  }

  getImageURL(imageName) {
    return StringHelper.joinPaths([this.grid.resourceImagePath || '', imageName || '']);
  }

  addErrorListener() {
    EventHelper.on({
      element: this.grid.element,
      delegate: '.b-resource-image',
      error: event => this.setDefaultResourceImage(event.target),
      capture: true
    });
  }

  setDefaultResourceImage(target) {
    const {
      defaultResourceImageName
    } = this.grid;

    if (defaultResourceImageName) {
      const defaultURL = this.getImageURL(defaultResourceImageName); // Set image to defaultURL if it is not already set

      if (target.src && !target.src.endsWith(defaultURL.replace(/^[./]*/gm, ''))) {
        target.src = defaultURL;
      }
    }
  }

  template(record) {
    const me = this,
          {
      showImage,
      showRole,
      showEventCount,
      grid: scheduler
    } = me,
          {
      timeAxis,
      resourceImageExtension,
      defaultResourceImageName
    } = scheduler,
          roleField = typeof showRole === 'string' ? showRole : 'role',
          count = record.eventStore.getEvents({
      includeOccurrences: scheduler.enableRecurringEvents,
      resourceRecord: record,
      startDate: timeAxis.startDate,
      endDate: timeAxis.endDate
    }).length,
          value = record.get(me.field);
    let imageUrl;

    if (showImage && record.image !== false) {
      if (record.imageUrl) {
        imageUrl = record.imageUrl;
      } else {
        // record.image supposed to be a file name, located at resourceImagePath
        const imageName = typeof showImage === 'string' ? showImage : record.image || value && value.toLowerCase() + resourceImageExtension || defaultResourceImageName;
        imageUrl = me.getImageURL(imageName); // Image name should have an extension

        if (!imageName.includes('.')) {
          // If validNames is specified, check that imageName is valid
          if (!me.validNames || me.validNames.includes(imageName)) {
            imageUrl += resourceImageExtension;
          } // If name is not valid, use generic image
          else if (!record.iconCls) {
              imageUrl = me.getImageURL(defaultResourceImageName);
            }
        }
      }
    }

    return {
      class: 'b-resource-info',
      children: [imageUrl ? {
        tag: 'img',
        draggable: 'false',
        class: 'b-resource-image',
        src: imageUrl
      } : null, record.iconCls ? {
        tag: 'i',
        className: `b-resource-icon ${record.iconCls}`
      } : null, {
        tag: 'dl',
        children: [{
          tag: 'dt',
          html: value
        }, showRole ? {
          tag: 'dd',
          class: 'b-resource-role',
          html: record[roleField]
        } : null, showEventCount ? {
          tag: 'dd',
          class: 'b-resource-events',
          html: me.L('L{eventCountText}', count)
        } : null]
      }]
    };
  }

  defaultRenderer({
    grid,
    record,
    cellElement,
    value,
    isExport
  }) {
    let result;

    if (record.isSpecialRow) {
      result = '';
    } else if (isExport) {
      result = value;
    } else {
      if (this.autoScaleThreshold && grid.rowHeight < this.autoScaleThreshold) {
        cellElement.style.fontSize = grid.rowHeight / 40 + 'em';
      } else {
        cellElement.style.fontSize = '';
      }

      result = this.template(record);
    }

    return result;
  }

}
ColumnStore.registerColumnType(ResourceInfoColumn);
ResourceInfoColumn._$name = 'ResourceInfoColumn';

/**
 * @module Scheduler/feature/StickyEvents
 */

const zeroMargins = {
  width: 0,
  height: 0
};
/**
 * This feature causes the inner text of events to stay in view as long as possible while the event's
 * bar is being moved out of view by sliding the content rightwards (or downwards in vertical mode).
 *
 * This feature may need to be be disabled when using complex
 * {@link Scheduler.view.mixin.SchedulerEventRendering#config-eventBodyTemplate eventBodyTemplates}
 * (See the "Nested events demo) because it relies on controlling the position of the inner element
 * of the event body.
 *
 * This feature is **enabled** by default.
 *
 * @extends Core/mixin/InstancePlugin
 * @demo Scheduler/bigdataset
 * @classtype stickyEvents
 */

class StickyEvents extends InstancePlugin {
  static get $name() {
    return 'StickyEvents';
  }

  static get type() {
    return 'stickyEvents';
  } // Plugin configuration. This plugin chains some of the functions in Scheduler.

  static get pluginConfig() {
    return {
      chain: ['onEventDataGenerated']
    };
  }

  construct(scheduler, config) {
    super.construct(scheduler, config); // Which event we listen for.
    // The scheduler's scroller only fires 'scroll' on vertical scroll.
    // SubGrids are responsible for their own horizontal scrolling.

    const scrollEvent = scheduler.isHorizontal ? 'horizontalScroll' : 'scroll';
    this.toUpdate = new Set(); // When a vertical mode Scheduler fires its scroll event, or a horizontal mode
    // one fires is horizontal horizontalScroll event, we check the resourceMap cache of
    // rendered resources, each of which contains a map of event layouts.
    // If any of those event layouts currently owns an element (is not derendered)
    // and have a start that is less than the scroll position, the eventContent is
    // nudged forward so that it remains in view.

    scheduler.on({
      [scrollEvent]: 'onSchedulerScroll',
      thisObj: this,
      prio: 10000
    });
  }

  onEventDataGenerated(eventData) {
    this.syncEventContentPosition(eventData);
    this.updateStyles();
  }

  onSchedulerScroll() {
    const {
      client
    } = this,
          method = client.isHorizontal ? 'horizontalSyncAllEventsContentPosition' : 'verticalSyncAllEventsContentPosition';
    this[method](client);
  }

  updateStyles() {
    for (const {
      contentEl,
      style
    } of this.toUpdate) {
      DomHelper.applyStyle(contentEl, style);
    }

    this.toUpdate.clear();
  }

  horizontalSyncAllEventsContentPosition(scheduler) {
    const {
      rows
    } = scheduler.rowManager,
          {
      resourceMap
    } = scheduler.currentOrientation;

    if (resourceMap.size) {
      for (let i = 0, {
        length
      } = rows; i < length; i++) {
        var _resourceMap$get;

        const eventsData = (_resourceMap$get = resourceMap.get(rows[i].id)) === null || _resourceMap$get === void 0 ? void 0 : _resourceMap$get.eventsData;

        if (eventsData !== null && eventsData !== void 0 && eventsData.length) {
          for (let j = 0, {
            length
          } = eventsData; j < length; j++) {
            const renderData = eventsData[j],
                  args = [renderData];

            if (renderData.eventRecord.isResourceTimeRange) {
              var _renderData$elementCo;

              args.push((_renderData$elementCo = renderData.elementConfig) === null || _renderData$elementCo === void 0 ? void 0 : _renderData$elementCo.children[0]);
            }

            this.syncEventContentPosition.apply(this, args);
          }
        }
      }
    }

    this.updateStyles();
  }

  verticalSyncAllEventsContentPosition(scheduler) {
    const {
      resourceMap
    } = scheduler.currentOrientation;

    for (const eventsData of resourceMap.values()) {
      for (const {
        renderData,
        elementConfig
      } of Object.values(eventsData)) {
        const args = [renderData];

        if (elementConfig && renderData.eventRecord.isResourceTimeRange) {
          args.push(elementConfig.children[0]);
        }

        this.syncEventContentPosition.apply(this, args);
      }
    }

    this.updateStyles();
  }

  syncEventContentPosition(renderData, eventContent = renderData.eventContent) {
    var _elementConfig$childr, _elementConfig$_eleme;

    // Allow client disable stickiness for certain events
    if (renderData.eventRecord.stickyContents === false) return;
    const {
      client
    } = this,
          {
      isHorizontal
    } = client,
          {
      elementConfig
    } = renderData,
          scrollPosition = isHorizontal ? client.timeAxisSubGrid.scrollable.x : client.scrollable.y,
          firstEventChild = elementConfig === null || elementConfig === void 0 ? void 0 : (_elementConfig$childr = elementConfig.children[0]) === null || _elementConfig$childr === void 0 ? void 0 : _elementConfig$childr.children[0],
          contentEl = (firstEventChild === null || firstEventChild === void 0 ? void 0 : firstEventChild.className) === 'b-sch-event-content' && firstEventChild._element ? firstEventChild._element : eventContent._element,
          eventStart = isHorizontal ? renderData.left : renderData.top,
          eventEnd = eventStart + (isHorizontal ? renderData.width : renderData.height) - 1,
          transformDim = isHorizontal ? 'X' : 'Y'; // If an event is moving back into view, the element it was previously attached to may be under the control
    // of another element now. If that is the case, we must leave it alone, the first DomSync that the
    // rerendering does will take care of it.
    // Event id will be stringified in the element's dataset, so use ==

    if ((elementConfig === null || elementConfig === void 0 ? void 0 : elementConfig.dataset.syncId) == (elementConfig === null || elementConfig === void 0 ? void 0 : (_elementConfig$_eleme = elementConfig._element) === null || _elementConfig$_eleme === void 0 ? void 0 : _elementConfig$_eleme.dataset.syncId)) {
      const style = typeof eventContent.style === 'string' ? eventContent.style = DomHelper.parseStyle(eventContent.style) : eventContent.style || (eventContent.style = {}),
            contentWidth = contentEl === null || contentEl === void 0 ? void 0 : contentEl.offsetWidth; // Only process non-milestone events. Milestones have no width.
      // If there's no offsetWidth, it's still b-released, so we cannot measure it.
      // If the event starts off the left edge, but its right edge is still visible,
      // translate the contentEl to compensate. If not, undo any translation.

      if (!renderData.eventRecord.isMilestone && (!contentEl || contentWidth) && eventStart < scrollPosition && eventEnd >= scrollPosition) {
        const edgeSizes = this.getEventContentMargins(contentEl),
              maxOffset = contentEl ? isHorizontal ? renderData.width - contentWidth - edgeSizes.width : renderData.height - contentEl.offsetHeight - edgeSizes.height : Number.MAX_SAFE_INTEGER,
              offset = Math.min(scrollPosition - eventStart, maxOffset - 2);
        style.transform = offset > 0 ? `translate${transformDim}(${offset}px)` : '';
      } else {
        style.transform = '';
      }

      if (contentEl) {
        this.toUpdate.add({
          contentEl,
          style
        });
      }
    }
  } // Only measure the margins of an event's contentEl once

  getEventContentMargins(contentEl) {
    if (contentEl !== null && contentEl !== void 0 && contentEl.classList.contains('b-sch-event-content')) {
      return DomHelper.getEdgeSize(contentEl, 'margin');
    }

    return zeroMargins;
  }

}
StickyEvents._$name = 'StickyEvents';
GridFeatureManager.registerFeature(StickyEvents, true, 'Scheduler');

/**
 * @module Scheduler/view/Scheduler
 */

/**
 * The Scheduler widget is a very powerful and performant UI component that displays an arbitrary number of "locked"
 * columns with a schedule occupying the remaining space. The schedule has a timeaxis at the top, one row per resource
 * and any number of events per resource.
 *
 * ## Intro
 * The Scheduler widget has a wide range of features and a large API to allow users to work with it efficiently in the
 * browser.
 *
 * The timeaxis displayed at the top of the Scheduler is configured using a {@link Scheduler.view.TimelineBase#config-startDate},
 * {@link Scheduler.view.TimelineBase#config-endDate} and a {@link Scheduler.view.mixin.TimelineViewPresets#config-viewPreset}. The dates
 * determine the outer limits of the range shown in the timeaxis while the {@link Scheduler.preset.ViewPreset} decides
 * the appearance and which dates are actually shown. The Scheduler ships with a selection of predefined view presets,
 * which can be found in {@link Scheduler.preset.PresetManager}. If you want to specify view presets for a specific scheduler only, please
 * see {@link Scheduler.view.mixin.TimelineViewPresets#config-presets} config.
 *
 * The Scheduler uses a {@link Scheduler.data.ResourceStore} to hold resources and an {@link Scheduler.data.EventStore}
 * to hold events. You can use inline data or load data using ajax, see the "Working with data" guides for more
 * information.
 *
 * The simplest schedule configured with inline data would look like this:
 *
 *      let scheduler = new Scheduler({
 *          appendTo : document.body,
 *
 *          startDate  : new Date(2018,4,6),
 *          endDate    : new Date(2018,4,12),
 *          viewPreset : 'dayAndWeek',
 *
 *           columns : [
 *              { field : 'name', text : 'Name', width: 100 }
 *          ],
 *
 *          resources : [
 *              { id : 1, name : 'Bernard' },
 *              { id : 2, name : 'Bianca' }
 *          ],
 *
 *          events : [
 *              { id : 1, resourceId : 1, name : 'Interview', startDate : '2018-05-06', endDate : '2018-05-07' },
 *              { id : 2, resourceId : 1, name : 'Press conference', startDate : '2018-05-08', endDate : '2018-05-09' },
 *              { id : 3, resourceId : 2, name : 'Audition', startDate : '2018-05-06', endDate : '2018-05-08' },
 *              { id : 4, resourceId : 2, name : 'Script deadline', startDate : '2018-05-11', endDate : '2018-05-11' }
 *          ]
 *      });
 *
 * {@inlineexample scheduler/Simplest.js}
 * ## Inheriting from Bryntum Grid
 * Bryntum Scheduler inherits from Bryntum Grid, meaning that most features available for the grid are also available
 * for the scheduler. Common features include columns, cell editing, context menus, row grouping, sorting and more.
 *
 * For more information on configuring columns, filtering, search etc. please see the {@link Grid.view.Grid Grid API docs}.
 * {@region Loading data}
 * As mentioned above Bryntum Scheduler uses an {@link Scheduler.data.EventStore} and a {@link Scheduler.data.ResourceStore}
 * to hold its data. Data is expected to be in JSON format and can be assigned inline (from memory) using the
 * {@link Scheduler.view.mixin.SchedulerStores#config-events} and
 * {@link Scheduler.view.mixin.SchedulerStores#config-resources} shortcuts:
 * ```
 * let scheduler = new Scheduler({
 *    events : myArrayOfEventData,
 *    resources : myArrayOfResourceData
 * });
 * ```
 * If you need to give additional store configuration, you can also specify store configs or instances:
 * ```
 * let resourceStore = new ResourceStore({
 *   // ResourceStore config object
 * })
 *
 * let scheduler = new Scheduler({
 *    // EventStore config object
 *    eventStore : {
 *       ...
 *    },
 *
 *    // Already existing ResourceStore instance
 *    resourceStore
 * });
 * ```
 * To use Ajax to fetch data from a server, specify {@link Core.data.AjaxStore#config-readUrl}:
 * ```
 * let scheduler = new Scheduler({
 *    eventStore : {
 *        readUrl  : 'backend/read_events.php',
 *        autoLoad : true
 *    }
 * });
 * // If you do not specify autoLoad, trigger loading manually:
 * scheduler.eventStore.load();
 * ```
 * For more information, see the "Working with data" guides.
 * {@endregion}
 * {@region Event styling}
 * Bryntum Schedulers appearance can be affected in a few different ways:
 *
 * * Switching themes
 * * Choosing event styles and colors
 * * Using renderer functions
 *
 * ### Switching themes
 * Scheduler ships with four different themes, simply include the css file for the theme you would like to use on your
 * page. The themes are located in the `/build` folder. For example to include the material theme:
 * ```
 * <link rel="stylesheet" href="build/scheduler.material.css" id="bryntum-theme">
 * ```
 * Included themes are (from left to right) classic-light, classic, classic-dark, stockholm and material:
 *
 * <img src="../examples/basic/meta/thumb.classic-light.png" alt="Classic-Light theme" width="300" style="margin-right: .5rem">
 * <img src="../examples/basic/meta/thumb.classic.png" alt="Classic theme" width="300" style="margin-right: .5rem">
 * <img src="../examples/basic/meta/thumb.classic-dark.png" alt="Classic-Dark theme" width="300" style="margin-right: .5rem">
 * <img src="../examples/basic/meta/thumb.stockholm.png" alt="Stockholm theme" width="300" style="margin-right: .5rem">
 * <img src="../examples/basic/meta/thumb.material.png" alt="Material theme" width="300">
 *
 * ### Choosing event styles and colors
 * The style and color of each event can be changed by assigning to the `eventStyle` and `eventColor` configs. These
 * configs are available at 3 different levels:
 *
 * * Scheduler level, affects all events (see {@link Scheduler.view.mixin.TimelineEventRendering#config-eventStyle} and {@link Scheduler.view.mixin.TimelineEventRendering#config-eventColor}).
 * * Resource level, affects all events assigned to that resource (see {@link Scheduler.model.mixin.ResourceModelMixin#field-eventStyle}
 * and {@link Scheduler.model.mixin.ResourceModelMixin#field-eventColor}).
 * * Event level, affects that event (see {@link Scheduler/model/mixin/EventModelMixin#field-eventStyle} and
 * {@link Scheduler/model/mixin/EventModelMixin#field-eventColor}).
 *
 * {@inlineexample scheduler/Styles.js}
 *
 * For available styles, see {@link Scheduler.view.mixin/TimelineEventRendering#config-eventStyle}. For colors,
 * {@link Scheduler.view.mixin/TimelineEventRendering#config-eventColor}. Also take a look at the
 * <a href="../examples/eventstyles" target="_blank">eventstyles demo</a>.
 *
 * ### Using render functions
 * Render function can be used to manipulate the rendering of rows (resources) and events. For information on row
 * renderers, see {@link Grid.column.Column#config-renderer}.
 *
 * Event rendering can be manipulated by specifying an {@link Scheduler.view.mixin/SchedulerEventRendering#config-eventRenderer} function. The function is called
 * for each event before it is rendered to DOM. By using its arguments you can add CSS classes, modify styling and
 * determine the contents of the event:
 * ```
 * let scheduler = new Scheduler({
 *
 *   events    : [...],
 *   resources : [...],
 *
 *   ...,
 *
 *   eventRenderer({resourceRecord, eventRecord, renderData}) {
 *      // add css class to the event
 *      renderData.cls.add('my-css-class');
 *
 *      // use an icon
 *      renderData.iconCls = 'b-fa b-fa-some-nice-icon';
 *
 *      // return value is used as events text
 *      return `${resourceRecord.name}: ${eventRecord.name}`;
 *   }
 * });
 * ```
 * {@endregion}
 * {@region Event manipulation}
 * You can programmatically manipulate the events using data operations, see the "Working with data" guides for more
 * information. Events are reactive, changes reflect on the UI automatically. A small example on manipulating events:
 * ```
 * // change startDate of first event
 * scheduler.eventStore.first.startDate = new Date(2018,5,10);
 *
 * // remove last event
 * scheduler.eventStore.last.remove();
 *
 * // reassign an event
 * scheduler.eventStore.getById(10).resourceId = 2;
 * ```
 *
 * You can also allow your users to manipulate the events using the following features:
 *
 * * {@link Scheduler.feature.EventDrag}, drag and drop events within the schedule
 * * {@link Scheduler.feature.EventDragCreate}, create new events by click-dragging an empty area
 * * {@link Scheduler.feature.EventEdit}, show an event editing form
 * * {@link Scheduler.feature.SimpleEventEdit}, edit the event name easily
 * * {@link Scheduler.feature.EventResize}, resize events by dragging resize handles
 *
 * All of the features mentioned above are enabled by default.
 * {@endregion}
 * {@region Default configs}
 * There is a myriad of configs and features available for Scheduler (browse the API docs to find them), some of them on
 * by default and some of them requiring extra configuration. The code below tries to illustrate the major things that
 * are used by default:
 *
 * ```javascript
 * let scheduler = new Scheduler({
 *    // The following features are enabled by default:
 *    features : {
 *        cellEdit            : true, // Cell editing in the columns part
 *        columnLines         : true, // Column lines in the schedule part
 *        columnPicker        : true, // Header context menu item to toggle visible columns
 *        columnReorder       : true, // Reorder columns in grid part using drag and drop
 *        columnResize        : true, // Resize columns in grid part using the mouse
 *        cellMenu            : true, // Context menu for cells in the grid part
 *        eventMenu           : true, // Context menu for events
 *        eventDrag           : true, // Dragging events
 *        eventDragCreate     : true, // Drag creating events
 *        eventEdit           : true, // Event editor dialog
 *        eventFilter         : true, // Filtering events using header context menu
 *        eventResize         : true, // Resizing events using the mouse
 *        eventTooltip        : true, // Tooltips for events
 *        group               : true, // Row grouping
 *        headerMenu          : true, // Context menu for headers in the grid part
 *        timeAxisHeaderMenu  : true, // Header context menu for schedule part
 *        scheduleMenu        : true, // Context menu for empty parts of the schedule
 *        scheduleTooltip     : true, // Tooltip for empty parts of the schedule
 *        sort                : true  // Row sorting
 *    },
 *
 *    // From Grid
 *    animateRemovingRows       : false, // Rows will not slide out on removal
 *    autoHeight                : false, // Grid needs to have a height supplied through CSS (strongly recommended) or by specifying `height
 *    columnLines               : true,  // Grid part, themes might override it to hide lines anyway
 *    emptyText                 : 'No rows to display',
 *    enableTextSelection       : false, // Not allowed to select text in cells by default,
 *    fillLastColumn            : true,  // By default the last column is stretched to fill the grid
 *    fullRowRefresh            : true,  // Refreshes entire row when a cell value changes
 *    loadMask                  : 'Loading...',
 *    resizeToFitIncludesHeader : true,  // Also measure header when auto resizing columns
 *    responsiveLevels : {
 *      small : 400,
 *      medium : 600,
 *      large : '*'
 *    },
 *    rowHeight                  : 60,    // Scheduler specifies a default rowHeight in pixels
 *    showDirty                  : false, // No indicator for changed cells
 *
 *    // Scheduler specific
 *    autoAdjustTimeAxis             : true,      // startDate & endDate will be adjusted to display a suitable range
 *    allowOverlap                   : true,      // Events are allowed to overlap (overlays, stacks or packs depending on eventLayout)
 *    barMargin                      : 10,        // Space above + below each event
 *    createEventOnDblClick          : true,      // Allow creating new events by double clicking empty space
 *    enableDeleteKey                : true,      // Allow deleting events with delete / backspace keys
 *    enableEventAnimations          : true,      // Animate event changes
 *    eventBarTextField              : 'name',    // Field on EventModel to display in events
 *    eventColor                     : 'green',   // Use green as default color for events
 *    eventLayout                    : 'stack',   // Stack overlapping events by default
 *    eventStyle                     : 'plain',   // Use plain as default style for events
 *    managedEventSizing             : true,      // Calculate event sizes based on rowHeight & barMargin
 *    milestoneCharWidth             : 10,
 *    milestoneLayoutMode            : 'default',
 *    removeUnassignedEvent          : true,      // Remove event when all assignments for it are removed
 *    useInitialAnimation            : true,      // Fade in events initially
 *    viewPreset                     : 'weekAndDayLetter',
 *    zoomOnMouseWheel               : true,
 *    zoomOnTimeAxisDoubleClick      : true
 * });
 * ```
 * {@endregion}
 * {@region Performance}
 * To make scheduler performance as good as possible it only renders the events and resources that are within view (plus
 * an additional buffer). Since adding to and removing from DOM comes with a performance penalty the elements are
 * instead repositioned and reused as you scroll. A side effect of this is that you cannot do direct DOM element
 * manipulation in a reliable way, instead you should use row and event renderer functions to achieve what you want (see
 * the section on event styling above).
 *
 * To put the scheduler to the test, try our <a href="../examples/bigdataset" target="_blank">bigdataset demo</a>.
 * {@endregion}
 *
 * {@region Recurring Events}
 * From 4.0.0, there is no `RecurringEvents` Feature. There is an
 * {@link Scheduler.view.mixin.RecurringEvents#config-enableRecurringEvents enableRecurringEvents}
 * boolean config on the Scheduler. Occurrences of recurring events are provided on a "just in time"
 * basis by a new EventStore API which must now be used when interrogating an EventStore.
 *
 * {@link Scheduler.data.mixin.EventStoreMixin#function-getEvents} is a multipurpose event gathering method
 * which can be asked to return events which match a set of criteria including a date range and a
 * resource. By default, if the requested date range contains occurrences of a recurring event,
 * those occurrences are returned in the result array.
 *
 * ```js
 * myEventStore.getEvents({
 *     resourceRecord : myResourceRecord,
 *     startDate      : myScheduler.startDate,
 *     endDate        : myScheduler.endDate
 * });
 * ```
 *
 * Occurrences are *not* present in the store's data collection.
 *
 * To directly access occurrences of a recurring event which *intersect* a date range, use:
 *
 * ```js
 * recurringEvent.getOccurrencesForDateRange(startDate, endDate);
 * ```
 *
 * The `endDate` argument is optional if the occurrence for one date is required. This method always
 * returns an array. Note that it may be empty if no occurrences intersect the date range.
 *
 * ### Convert an occurrence to an exception
 * To programmatically convert an occurrence to be a single exception to its owner's sequence use:
 *
 * ```
 * myOccurrence.beginBatch();
 * myOccurrence.startDate = DateHelper.add(myOccurrence.startDate, 1, 'day');
 * myOccurrence.name = 'Postponed to next day';
 * myOccurrence.recurrence = null; // This means it does NOT become a new recurring base event.
 * myOccurrence.endBatch();
 * ```
 *
 * That will cause that event to be inserted into the store as a concrete event definition, firing
 * an `add` event as would be expected, and will add an `exceptionDates` to its owning recurring event.
 *
 * When syncing this change back to the server, the `exceptionDates` array for the modified
 * recurring event now contains the exception dates correctly serialized into string form using
 * the `dateFormat` of the field. The system-supplied default value for this is
 * `'YYYY-MM-DDTHH:mm:ssZ'`
 *
 * ### Convert an occurrence to a new recurring event sequence.
 * To programatically convert an occurrence to be the start of a new recurring sequence, use:
 *
 * ```
 * myOccurrence.beginBatch();
 * myOccurrence.startDate = DateHelper.set(myOccurrence.startDate, 'hour', 14);
 * myOccurrence.name = 'Moved to 2pm from here on';
 * myOccurrence.endBatch();
 * ```
 *
 * That will cause that event to be inserted into the store as a concrete *recurring* event
 * definition, firing an `add` event as would be expected, and will terminate the previous
 * recurring owner of that occurrence on the day before the new event.
 * {@endregion}
 *
 * @extends Scheduler/view/SchedulerBase
 */

class Scheduler extends SchedulerBase {
  static get $name() {
    return 'Scheduler';
  } // Factoryable type name

  static get type() {
    return 'scheduler';
  }

} // Register this widget type with its Factory

Scheduler.initClass();
Scheduler._$name = 'Scheduler';

/**
 * @module Scheduler/widget/ResourceFilter
 */

/**
 * A List which allows selection of resources to filter a specified eventStore to only show
 * events for the selected resources.
 *
 * @extends Core/widget/List
 * @classType resourceFilter
 */

class ResourceFilter extends List {
  static get $name() {
    return 'ResourceFilter';
  } // Factoryable type name

  static get type() {
    return 'resourcefilter';
  }

  static get delayable() {
    return {
      applyFilter: 'raf'
    };
  }

  static get configurable() {
    return {
      /**
       * The {@link Scheduler.data.EventStore EventStore} to filter.
       * Events for resources which are deselected in this List will be filtered out.
       * @config {Scheduler.data.EventStore}
       */
      eventStore: null,
      multiSelect: true,
      toggleAllIfCtrlPressed: true,
      itemTpl: record => record.name
    };
  }

  itemIconTpl(record, i) {
    const {
      eventColor
    } = record,
          // Hex color is used as color directly
    style = eventColor !== null && eventColor !== void 0 && eventColor.startsWith('#') ? ` style="color:${eventColor}"` : '',
          // Other colors are applied using CSS
    cls = !style && eventColor ? ` b-sch-foreground-${eventColor}` : '';
    return this.multiSelect ? `<div class="b-selected-icon b-icon${cls}"${style}></div>` : '';
  }

  updateEventStore(eventStore) {
    var _this$store;

    const {
      resourceStore
    } = eventStore,
          // HACK: Temp workaround until List's store is dynamically updateable
    chainedStoreConfig = this.initialConfig.store instanceof Store ? this.initialConfig.store.initialConfig : (_this$store = this.store) === null || _this$store === void 0 ? void 0 : _this$store.config;
    this.store = resourceStore.makeChained(null, null, chainedStoreConfig);

    if (!resourceStore.count) {
      resourceStore.project.on({
        name: 'project',
        refresh: 'initFilter',
        thisObj: this
      });
    } else {
      this.initFilter();
    }
  }

  initFilter() {
    const {
      eventStore
    } = this;

    if (eventStore.count && eventStore.resourceStore.count) {
      this.selected.add(this.store.getRange());
      this.detachListeners('project');
    }
  }

  onSelectionChange({
    source: selected
  }) {
    // Filter disabled if all resources selected
    const me = this,
          disabled = selected.count === me.eventStore.resourceStore.count;
    super.onSelectionChange(...arguments); // If this is the first selection change triggered from the first project refresh
    // in which all the resources are selected, then we don't need to apply the filters.
    // because all resources are selected

    if (!me.resourceFilter) {
      // Our client EventStore is filtered to only show events for our selected resources.
      // Events without an associated resource are filtered into visibility.
      // The addFilter function with silent param adds the filter but don't reevaluate filtering.
      me.resourceFilter = me.eventStore.addFilter({
        id: `${me.id}-filter-instance`,
        filterBy: e => !e.resource || me.selected.includes(e.resources),
        disabled
      }, true);
      return;
    } // Filter disabled if all resources selected

    me.resourceFilter.disabled = disabled; // Have the client EventStore refresh its filtering but after a small delay so the List UI updates immediately.

    me.applyFilter();
  }

  applyFilter() {
    this.eventStore.filter();
  }

} // Register this widget type with its Factory

ResourceFilter.initClass();
ResourceFilter._$name = 'ResourceFilter';

/**
 * @module Scheduler/widget/UndoRedo
 */

const isProjectConsumer = w => w.isProjectConsumer;
/**
 * A widget which encapsulates undo/redo functionality for the {@link Scheduler.model.ProjectModel project}
 * of a scheduling widget (`Scheduler`, `Gantt` or `Calendar`).
 *
 * To make use of this, the project must be configured with a
 * {@link Scheduler.model.mixin.ProjectModelMixin#config-stm State Tracking Manager}.
 *
 * If inserted into a scheduling widget (such as into a `tbar`, or `bbar`, or as an item in a context menu),
 * the project of the encapsulating scheduling widget will be used.
 *
 * If this widget is to be used "standalone" (rendered into the DOM outside of a scheduling widget),
 * this must be configured with a reference to the project, or the scheduling widget which is
 * using the project.
 *
 * There are three child widgets encapsulated which may be referenced through the {@link Core.widget.Container#property-widgetMap}:
 *
 * - `undoBtn` - The button which operates the undo operation
 * - `transactionsCombo` - A combobox into which is pushed the list of transactions,
 * - `redoBtn` - The button which operates the redo operation
 *
 * The transactionsCombo may be configured away if only the buttons are required:
 *
 * ```javascript
 * {
 *     type      : 'undoredo',
 *     items     : {
 *         transactionsCombo : null
 *     }
 * }
 * ```
 * The example below illustrated how to embed an `undoredo` widget in the top toolbar of a Scheduler.
 * @extends Core/widget/Container
 * @classType undoredo
 * @demo Scheduler/undoredo
 * @externalexample scheduler/UndoRedo.js
 */

class UndoRedo extends Container {
  static get $name() {
    return 'UndoRedo';
  }

  static get type() {
    return 'undoredo';
  }

  static get configurable() {
    return {
      /**
       * The Scheduling Widget (or its `id`) whose transaction to track.
       *
       * This may be a `Scheduler`, a `Gantt` or a `Calendar`.
       *
       * ```javascript
       *     {
       *         type      : 'undoredo',
       *         scheduler : myCalendar
       *     }
       * ```
       * @config {Core.widget.Widget|String}
       */
      scheduler: null,

      /**
       * The Scheduling {@link Scheduler.model.ProjectModel project}'s whose
       * transaction to track.
       *
       * ```javascript
       *     {
       *         type    : 'undoredo',
       *         project : sheduler.project
       *     }
       * ```
       * @config {Scheduler.model.ProjectModel}
       */
      project: null,

      /**
       * Configure as `true` to show "Undo" and "Redo" as button texts. The buttons always have a tooltip
       * as a hint to the user as to their purpose.
       * @config {Boolean}
       */
      text: null,

      /**
       * Button color for the undo and redo buttons. See {@link Core.widget.Button#config-color}.
       * @config {String}
       */
      color: null,

      /**
       * Configure as `true` to show "0" badge on the undo and redo buttons when they have no actions
       * left to perform. By default when there are no actions, no badge is displayed.
       * @config {Boolean}
       */
      showZeroActionBadge: null,
      cls: 'b-undo-controls b-toolbar',
      layoutStyle: {
        alignItems: 'stretch',
        flexFlow: 'row nowrap',
        overflow: 'visible'
      },
      items: {
        undoBtn: {
          type: 'button',
          icon: 'b-fa-undo',
          tooltip: 'L{UndoRedo.UndoLastAction}',
          disabled: true,
          onAction: 'up.onUndo' // 'up.' means method is on a parent Widget.

        },
        transactionsCombo: {
          type: 'combo',
          valueField: 'idx',
          editable: false,
          store: {},
          emptyText: 'No items in the undo queue',
          onAction: 'up.onTransactionSelected',
          displayValueRenderer: 'up.transactionsDisplayValueRenderer'
        },
        redoBtn: {
          type: 'button',
          icon: 'b-fa-redo',
          tooltip: 'L{UndoRedo.RedoLastAction}',
          disabled: true,
          onAction: 'up.onRedo'
        }
      }
    };
  }

  construct() {
    super.construct(...arguments); // Look up a Project owner in our ancestors.

    if (!this.stm) {
      this.scheduler = this.up(isProjectConsumer);
    }

    this.stm.on({
      recordingstop: 'updateUndoRedoControls',
      restoringstop: 'updateUndoRedoControls',
      queueReset: 'updateUndoRedoControls',
      disabled: 'updateUndoRedoControls',
      thisObj: this
    });
  }

  changeItems(items) {
    const {
      undoBtn,
      redoBtn
    } = items;

    if (this.color) {
      undoBtn && (undoBtn.color = this.color);
      redoBtn && (redoBtn.color = this.color);
    }

    if (this.text) {
      undoBtn && (undoBtn.text = 'Undo');
      redoBtn && (redoBtn.text = 'Redo');
    }

    return super.changeItems(items);
  }

  changeScheduler(scheduler) {
    return scheduler.isProjectConsumer ? scheduler : bryntum.get(scheduler);
  }

  updateScheduler(scheduler) {
    const {
      crudManager
    } = scheduler;

    if (crudManager) {
      crudManager.on({
        load: 'onCrudManagerLoad',
        thisObj: this
      });
    } // No CrudManager, so it must be inline data, so we can start immediately
    else {
        this.onCrudManagerLoad();
      }

    this.stm = scheduler.project.stm;
  }

  updateProject(project) {
    this.stm = project.stm;
  }

  async onCrudManagerLoad() {
    // Do not want normalization changes to be tracked by STM, wait until they are finished before enabling
    await this.scheduler.project.commitAsync(); // Widget could be destroyed during async project commit

    if (!this.isDestroyed) {
      this.stm.enable();
      this.fillUndoRedoCombo();
    }
  }

  fillUndoRedoCombo() {
    // The transactionsCombo may be configured away if only undo and redo buttons are wanted
    this.widgetMap.transactionsCombo && (this.widgetMap.transactionsCombo.items = this.stm.queue.map((title, idx) => [idx, title || `Transaction ${idx}`]));
  }

  updateUndoRedoControls() {
    const {
      stm,
      showZeroActionBadge
    } = this,
          {
      undoBtn,
      redoBtn
    } = this.widgetMap;
    undoBtn.badge = stm.position || (showZeroActionBadge ? '0' : '');
    redoBtn.badge = stm.length - stm.position || (showZeroActionBadge ? '0' : '');
    undoBtn.disabled = !stm.canUndo;
    redoBtn.disabled = !stm.canRedo;
    this.fillUndoRedoCombo();
  }

  transactionsDisplayValueRenderer(record, combo) {
    var _this$stm;

    const stmPos = ((_this$stm = this.stm) === null || _this$stm === void 0 ? void 0 : _this$stm.position) || 0;
    return `${stmPos} undo actions / ${combo.store.count - stmPos} redo actions`;
  }

  onUndo() {
    this.stm.canUndo && this.stm.undo();
  }

  onRedo() {
    this.stm.canRedo && this.stm.redo();
  }

  onTransactionSelected(combo) {
    const stm = this.stm,
          value = combo.value;

    if (value >= 0) {
      if (stm.canUndo && value < stm.position) {
        stm.undo(stm.position - value);
      } else if (stm.canRedo && value >= stm.position) {
        stm.redo(value - stm.position + 1);
      }
    }
  }

}
UndoRedo.initClass();
UndoRedo._$name = 'UndoRedo';

/**
 * @module Scheduler/customElements/SchedulerTag
 */

/**
 * Import this file to be able to use the tag **&lt;bryntum-scheduler&gt;** to create a scheduler. This is more of a
 * proof of concept than a ready to use class. Dataset from **&lt;data&gt;** and **&lt;bryntum-scheduler&gt;** tags is
 * applied to record and scheduler config, which means, that you can pass any documented config there, not only
 * demonstrated here. Dataset attributes are translated as follows:
 *
 *  * data-view-preset -> viewPreset
 *  * data-start-date -> startDate
 *
 *  etc.
 * ## Example
 * ```html
 * &lt;bryntum-scheduler data-view-preset="weekAndDay" data-start-date="2018-04-02" data-end-date="2018-04-09"&gt;
 *  &lt;column data-field="name"&gt;Name&lt;/column&gt;
 *      &lt;data&gt;
 *          &lt;events&gt;
 *              &lt;data data-id="1" data-resource-id="1" data-start-date="2018-04-03" data-end-date="2018-04-05"&gt;&lt;/data&gt;
 *              &lt;data data-id="2" data-resource-id="2" data-start-date="2018-04-04" data-end-date="2018-04-06"&gt;&lt;/data&gt;
 *              &lt;data data-id="3" data-resource-id="3" data-start-date="2018-04-05" data-end-date="2018-04-07"&gt;&lt;/data&gt;
 *          &lt;/events&gt;
 *          &lt;resources&gt;
 *              &lt;data data-id="1" data-name="Daniel"&gt;&lt;/data&gt;
 *              &lt;data data-id="2" data-name="Steven"&gt;&lt;/data&gt;
 *              &lt;data data-id="3" data-name="Sergei"&gt;&lt;/data&gt;
 *          &lt;/resources&gt;
 *      &lt;/data&gt;
 * &lt;/bryntum-scheduler&gt;
 * ```
 */
// This is required to make trial build function normally

const TagBase = window.customElements ? HTMLElement : Object;
class SchedulerTag extends TagBase {
  constructor() {
    super();
    const me = this,
          columns = [],
          resources = [],
          events = []; // create columns and data

    for (const tag of me.children) {
      if (tag.tagName === 'COLUMN') {
        const width = parseInt(tag.dataset.width),
              flex = parseInt(tag.dataset.flex),
              column = {
          field: tag.dataset.field,
          text: tag.innerHTML
        };
        if (width) column.width = width;else if (flex) column.flex = flex;else column.flex = 1;
        columns.push(column);
      } else if (tag.tagName === 'DATA') {
        for (const storeType of tag.children) {
          for (const record of storeType.children) {
            const row = {};
            Object.assign(row, record.dataset);

            if (storeType.tagName === 'EVENTS') {
              events.push(row);
            } else if (storeType.tagName === 'RESOURCES') {
              resources.push(row);
            }
          }
        }
      }
    }

    const // go over to the dark side
    shadowRoot = this.attachShadow({
      mode: 'open'
    }),
          // include css and target div in shadow dom
    link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = '../../build/scheduler.stockholm.css';

    link.onload = () => {
      const div = document.createElement('div');
      div.id = 'container';
      div.style.width = '100%';
      div.style.height = '100%';
      shadowRoot.appendChild(div); // Listen to focus events on shadow root to handle focus inside the shadow dom

      setupFocusListeners(shadowRoot, EventHelper);
      const config = {
        appendTo: div,
        columns: columns,
        resources,
        events
      };
      Object.assign(config, me.dataset); // render as usual

      const scheduler = new Scheduler(config); // for testing, set first scheduler as global variable

      if (!window.scheduler) window.scheduler = scheduler;
    };

    shadowRoot.appendChild(link);
  }

} // Try-catch to make trial work

try {
  window.customElements && window.customElements.define('bryntum-scheduler', SchedulerTag);
} catch (error) {}

SchedulerTag._$name = 'SchedulerTag';

/* eslint-disable import/first */
if (!window.bryntum) window.bryntum = {};

export { AbstractCrudManager, AbstractRecurrenceIterator, ActionColumn, AggregateColumn, AjaxHelper, AjaxStore, AjaxTransport, ArrayHelper, AssignmentModel, AssignmentStore, AsyncHelper, Badge, Bag, Base, BooleanCombo, BooleanDataField, BrowserHelper, Button, ButtonGroup, CSSHelper, CalendarPanel, CellEdit, CellMenu, CellTooltip, CheckColumn, Checkbox, ChipView, ClickRepeater, Collection, CollectionFilter, CollectionSorter, Column, ColumnAutoWidth, ColumnDragToolbar, ColumnLines, ColumnPicker, ColumnReorder, ColumnResize, ColumnStore, Combo, Config, Container, ContextMenu, CrudManager, CrudManagerView, DailyRecurrenceIterator, DataField, DataGenerator, DateColumn, DateDataField, DateField, DateHelper, DatePicker, DateTimeField, Delayable, DemoBot, Dependencies, DependencyBaseModel, DependencyCreation, DependencyEdit, DependencyModel, DependencyStore, DisplayField, DomClassList, DomDataStore, DomHelper, DomSync, DragBase, DragContext, DragHelper, DragProxy, DragTipProxy, Draggable, Droppable, Duration, DurationField, DynamicObject, Editor, EventContextMenu, EventDrag, EventDragCreate, EventDragSelect, EventEdit, EventFilter, EventHelper, EventMenu, EventModel, EventResize, EventStore, EventTooltip, Events, ExcelExporter$1 as ExcelExporter, ExportOrientationCombo, ExportRowsCombo, Factoryable, Featureable, Field, FileField, FileFormat, FilePicker, Filter, FilterBar, FilterField, Finalizable, Footer, Formatter, Fullscreen, FunctionHelper, GlobalEvents, Grid, GridBase, ExcelExporter as GridExcelExporter, ExportDialog as GridExportDialog, GridFeatureManager, MultiPageExporter as GridMultiPageExporter, MultiPageVerticalExporter as GridMultiPageVerticalExporter, PdfExport as GridPdfExport, GridRowModel, SinglePageExporter as GridSinglePageExporter, Group, GroupSummary$1 as GroupSummary, Header, HeaderContextMenu, HeaderMenu, HeaderZoom, HorizontalTimeAxis, Hoverable, IdHelper, InstancePlugin, IntegerDataField, JsonEncoder, Labels, List, LocaleHelper, LocaleManagerSingleton as LocaleManager, Localizable, LocalizableCombo, Mask, Menu, MenuItem, MessageDialogSingleton as MessageDialog, Model, ModelDataField, Month, MonthlyRecurrenceIterator, MultiPageExporter$1 as MultiPageExporter, MultiPageVerticalExporter$1 as MultiPageVerticalExporter, NonWorkingTime, NumberDataField, NumberField, NumberFormat, ObjectDataField, ObjectHelper, Objects, Orientation, Override, PagingToolbar, Pan, Panel, PaperFormat, Parser, PdfExport$1 as PdfExport, PercentColumn, Pluggable, Point, Popup, pm as PresetManager, PresetStore, ProjectConsumer, ProjectCrudManager, ProjectModel, Promissory, QuickFind, RandomGenerator, RatingColumn, Rectangle, RectangularPathFinder, RecurrenceCombo, RecurrenceConfirmationPopup, RecurrenceDayRuleEncoder, RecurrenceDaysButtonGroup, RecurrenceDaysCombo, RecurrenceEditor, RecurrenceFrequencyCombo, RecurrenceLegend, RecurrenceLegendButton, RecurrenceModel, RecurrenceMonthDaysButtonGroup, RecurrenceMonthsButtonGroup, RecurrencePositionsCombo, RecurrenceStopConditionCombo, RecurringEventEdit, RecurringEventsMixin, RecurringTimeSpan, RecurringTimeSpansMixin, RegionResize, Renderable, ResizeHelper, ResizeMonitor, ResourceCombo, ResourceFilter, ResourceInfoColumn, ResourceModel, ResourceStore, ResourceTimeRangeModel, ResourceTimeRangeStore, ResourceTimeRanges, Ripple, RowManager, RowNumberColumn, RowReorder, RowsRange, ScheduleContextMenu, ScheduleMenu, ScheduleRange, ScheduleRangeCombo, ScheduleTableExporter, ScheduleTooltip, Scheduler, SchedulerBase, SchedulerExportDialog, SchedulerTag, ScrollManager, Scroller, Search, SimpleEventEdit, SinglePageExporter$1 as SinglePageExporter, SlideToggle, Slider, Sort, Splitter, State, StateTrackingManager, Store, StringDataField, StringHelper, Stripe, Summary$1 as Summary, TabPanel, TableExporter, TemplateColumn, TemplateHelper, TextAreaField, TextField, TimeAxis, TimeAxisColumn, TimeAxisHeaderMenu, TimeAxisViewModel, TimeColumn, TimeField, TimePicker, TimeRanges, TimeSpan, TimeSpanMenuBase, TimeSpanRecordContextMenuBase, Toast, Tool, Toolbar, Tooltip, Transaction, Tree, TreeColumn, TreeGrid, UndoRedo, VersionHelper, ViewPreset, WeeklyRecurrenceIterator, Widget, WidgetColumn, WidgetHelper, YearlyRecurrenceIterator };
//# sourceMappingURL=scheduler.module.js.map
